import { ClinicalCondition } from '../types';

/**
 * OmniClinicalBridge V2.0 - Comprehensive Clinical Conditions Database
 * 100 Conditions with WHO & Africa CDC Guidelines
 * Subscription-Worthy Detailed Content
 * Last Updated: January 2026
 * 
 * Color Coding in Red Flags:
 * 🔴 CRITICAL - Immediately life-threatening, act within minutes
 * 🟠 URGENT - Serious, act within 1-2 hours  
 * 🟡 WARNING - Monitor closely, may deteriorate
 */

export const conditions: ClinicalCondition[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // 1. SEVERE MALARIA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '1',
    name: 'Severe Malaria',
    slug: 'severe-malaria',
    specialty: 'Infectious Disease',
    bodySystem: 'Systemic',
    icdCode: 'B50.0',
    resourceLevel: 'BASIC',
    isEmergency: true,
    
    overview: `Severe malaria is a MEDICAL EMERGENCY caused primarily by Plasmodium falciparum with evidence of vital organ dysfunction. It kills over 600,000 people annually, with 95% of deaths in sub-Saharan Africa, predominantly children under 5.


⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS:
• Without treatment, mortality approaches 100%
• Even with optimal treatment, mortality remains 10-20%
• EVERY MINUTE OF DELAY IN TREATMENT INCREASES MORTALITY
• P. falciparum causes >99% of severe malaria in Africa
• Children under 5 account for 80% of deaths
• Cerebral malaria has 15-25% mortality even with treatment
• Hypoglycemia is common, often missed, and rapidly fatal
• IV Artesunate is the treatment of choice (WHO Grade A)

EPIDEMIOLOGY:
• 247 million cases globally (2021)
• 619,000 deaths annually
• 95% of cases in WHO African Region
• 80% of deaths in children under 5
• Incidence: 59 cases per 1,000 population at risk in Africa
• Highest burden: Nigeria (27%), DRC (12%), Uganda, Mozambique
• Artemisinin partial resistance emerging in Rwanda, Uganda`,

    epidemiology: `GLOBAL: 247 million cases, 619,000 deaths annually (2021). 95% in WHO African Region, 80% of deaths in children <5. Incidence in Africa: 59 per 1,000 at risk. RISK FACTORS: Children <5 (highest mortality), pregnant women (especially primigravidae), non-immune travelers, HIV/AIDS patients (2-3x risk), splenectomized individuals, delayed treatment >24 hours.`,

    clinicalPresentation: `SYMPTOMS:
• High fever (often >40°C) with rigors - may be absent in severe cases
• Severe headache
• Altered consciousness/confusion/COMA (cerebral malaria)
• Seizures - especially in children, often multiple
• Severe weakness/PROSTRATION - cannot walk, sit, or feed
• Respiratory distress - deep, labored breathing (acidosis)
• Repeated vomiting - cannot retain oral medications
• Dark or cola-colored urine (blackwater fever/hemoglobinuria)
• Jaundice (yellow eyes and skin)

WHO CRITERIA FOR SEVERE MALARIA - Any ONE of:
🔴 CEREBRAL: GCS <11 in adults or Blantyre Coma Scale <3 in children
🔴 RESPIRATORY: Acidotic breathing, SpO2 <92%
🔴 SHOCK: SBP <80 mmHg (adults), <70 mmHg (children)
🔴 SEIZURES: Multiple convulsions (>2 in 24 hours)
🔴 PROSTRATION: Unable to sit/walk/breastfeed
🔴 SEVERE ANEMIA: Hb <5 g/dL or Hct <15%
🔴 HYPOGLYCEMIA: Glucose <2.2 mmol/L (<40 mg/dL)
🔴 RENAL: Creatinine >265 μmol/L or oliguria
🔴 JAUNDICE: Bilirubin >50 μmol/L with organ dysfunction
🔴 HYPERPARASITEMIA: >10% parasitized RBCs
🔴 BLEEDING: Spontaneous bleeding, DIC`,

    diagnosis: `DIAGNOSTIC APPROACH - DO NOT DELAY TREATMENT FOR LAB CONFIRMATION:

PARASITOLOGICAL CONFIRMATION:
1. Rapid Diagnostic Test (RDT) - Detects HRP2/pLDH, >95% sensitivity
2. Blood Film Microscopy - Thick (detection) + Thin (species, quantification)
   Report as % parasitized RBCs (>10% = hyperparasitemia)

ESSENTIAL LABS:
• Blood glucose - CHECK IMMEDIATELY (hypoglycemia kills)
• Hemoglobin - Severe anemia common
• Blood group and crossmatch - Prepare for transfusion
• Serum creatinine - Renal function
• Lactate - >5 mmol/L indicates severe acidosis

DIFFERENTIAL DIAGNOSIS:
• Bacterial meningitis - Similar coma, do LP if uncertain
• Typhoid encephalopathy - Prolonged fever
• Viral encephalitis - CSF pleocytosis
• DKA - Check glucose, ketones
• Sepsis - May coexist
• Viral hemorrhagic fever - Ebola, Lassa`,

    differentialDiagnosis: [
      'Bacterial meningitis',
      'Typhoid fever with encephalopathy',
      'Viral encephalitis',
      'Diabetic ketoacidosis',
      'Severe sepsis',
      'Viral hemorrhagic fever (Ebola, Lassa)'
    ],

    investigations: [
      { name: 'Rapid Diagnostic Test (RDT)', indication: 'Immediate diagnosis - do not delay treatment', availability: 'BASIC' },
      { name: 'Blood Film (Thick & Thin)', indication: 'Species ID and parasite quantification (>10% = hyperparasitemia)', availability: 'BASIC' },
      { name: 'Blood Glucose', indication: '🔴 CHECK IMMEDIATELY - hypoglycemia is common and fatal', availability: 'BASIC' },
      { name: 'Hemoglobin/Hematocrit', indication: 'Assess anemia severity - transfuse if <5 g/dL', availability: 'BASIC' },
      { name: 'Blood Group & Crossmatch', indication: 'URGENT - prepare for possible transfusion', availability: 'BASIC' },
      { name: 'Serum Creatinine', indication: 'Assess renal function - >265 μmol/L indicates AKI', availability: 'INTERMEDIATE' },
      { name: 'Serum Lactate', indication: '>5 mmol/L indicates severe acidosis, poor prognosis', availability: 'INTERMEDIATE' },
      { name: 'Arterial Blood Gas', indication: 'pH <7.25, base deficit >8 mEq/L', availability: 'ADVANCED' },
      { name: 'Liver Function Tests', indication: 'Bilirubin >50 μmol/L with organ dysfunction = severe', availability: 'INTERMEDIATE' },
      { name: 'Coagulation Profile', indication: 'PT/INR if bleeding or suspected DIC', availability: 'INTERMEDIATE' },
      { name: 'Chest X-ray', indication: 'If respiratory distress - look for pulmonary edema, ARDS', availability: 'BASIC' },
      { name: 'Lumbar Puncture', indication: 'Only if meningitis cannot be excluded clinically', availability: 'INTERMEDIATE' }
    ],

    management: {
      immediate: [
        '⚡ CALL FOR HELP - Activate emergency response team',
        '⚡ CHECK BLOOD GLUCOSE IMMEDIATELY - Treat hypoglycemia (<2.2 mmol/L) with 50% dextrose',
        '⚡ START IV ARTESUNATE - Do NOT wait for lab confirmation if clinical suspicion high',
        '⚡ Establish IV access - Two large bore cannulas (16-18G)',
        '⚡ Give oxygen if SpO2 <94% or any respiratory distress',
        '⚡ Position: Recovery position if unconscious, elevate head 30°',
        '⚡ Insert urinary catheter - Monitor output hourly (target >0.5 mL/kg/hr)',
        '⚡ NG tube if unconscious - Prevent aspiration',
        'Weigh patient accurately - Essential for correct drug dosing',
        'Continuous monitoring: GCS, vitals every 15-30 min until stable'
      ],
      pharmacological: [
        {
          drug: 'Artesunate IV/IM (FIRST-LINE - WHO Grade A)',
          indication: 'All severe malaria - START IMMEDIATELY',
          dose: 'Adults: 2.4 mg/kg IV. Children <20kg: 3 mg/kg IV (higher dose)',
          route: 'IV bolus over 1-2 minutes (IM if IV impossible)',
          duration: 'At 0, 12, 24 hours, then daily. Minimum 24h IV, then complete with 3-day oral ACT',
          contraindications: ['None in severe malaria - benefit always outweighs risk'],
          sideEffects: ['Post-artesunate delayed hemolysis (PADH) - check Hb day 7 and 14'],
          alternatives: ['Artemether IM 3.2 mg/kg loading, then 1.6 mg/kg daily', 'Quinine IV if artemisinins unavailable']
        },
        {
          drug: '50% Dextrose',
          indication: '🔴 HYPOGLYCEMIA - Glucose <2.2 mmol/L (<40 mg/dL)',
          dose: 'Adults: 50 mL IV. Children: 2-5 mL/kg of 10% dextrose IV',
          route: 'IV bolus',
          duration: 'STAT, recheck glucose every 4-6 hours - hypoglycemia is RECURRENT',
          contraindications: [],
          sideEffects: ['Hyperglycemia if excessive'],
          alternatives: ['Oral glucose if conscious and able to swallow']
        },
        {
          drug: 'Blood Transfusion',
          indication: '🔴 Severe anemia: Hb <5 g/dL OR Hb <7 g/dL with respiratory distress/shock',
          dose: 'Packed RBCs 10-20 mL/kg OR Whole blood 20 mL/kg',
          route: 'IV over 3-4 hours (slower if heart failure risk)',
          duration: 'Until Hb >7 g/dL',
          contraindications: [],
          sideEffects: ['Transfusion reactions', 'Infection risk', 'Fluid overload'],
          alternatives: []
        },
        {
          drug: 'Diazepam',
          indication: 'Seizures',
          dose: 'Adults: 10 mg IV slowly. Children: 0.3 mg/kg IV or 0.5 mg/kg rectally',
          route: 'IV over 2 minutes or rectal',
          duration: 'May repeat once after 10 minutes. Max 20 mg adults, 10 mg children',
          contraindications: ['Respiratory depression'],
          sideEffects: ['Respiratory depression - have airway equipment ready'],
          alternatives: ['Lorazepam', 'Phenobarbital']
        },
        {
          drug: 'Paracetamol/Acetaminophen',
          indication: 'Fever >38.5°C',
          dose: '15 mg/kg',
          route: 'Oral/rectal/IV',
          duration: 'Every 6 hours PRN until afebrile',
          contraindications: ['Severe liver disease'],
          sideEffects: ['Hepatotoxicity in overdose'],
          alternatives: ['Tepid sponging for hyperpyrexia']
        }
      ],
      nonPharmacological: [
        '⚠️ AVOID over-hydration - risk of pulmonary edema in severe malaria',
        'Maintain euvolemia - do not give excessive fluids',
        'Tepid sponging for hyperpyrexia (avoid ice water)',
        'Prevent aspiration: NG tube, recovery position if unconscious',
        'Turn unconscious patients every 2 hours - prevent pressure sores',
        'Maintain airway - consider intubation if GCS <8 and available',
        'Strict fluid balance monitoring',
        'Keep NPO until conscious and able to swallow safely'
      ],
      referralCriteria: [
        '🔴 ALL severe malaria cases need hospital admission',
        'Transfer to ICU if: GCS <8, shock, respiratory failure, AKI requiring dialysis',
        'Transfer to higher facility if: Blood transfusion needed but unavailable, mechanical ventilation needed'
      ],
      monitoring: [
        'Parasite count every 12 hours until negative',
        'Blood glucose every 4-6 hours (more if hypoglycemia occurred)',
        'Hemoglobin daily (plus day 7 and 14 for PADH)',
        'GCS/consciousness hourly until improving',
        'Vital signs every 1-2 hours initially',
        'Urine output hourly (target >0.5 mL/kg/hr)',
        'Temperature every 4 hours',
        'Oxygen saturation continuous or every 2 hours'
      ],
      followUp: 'Review at day 7 and day 14 for post-artesunate delayed hemolysis (PADH). Complete malaria treatment. Ensure sleeping under LLIN.'
    },

    redFlags: [
      '🔴 CRITICAL: Coma (GCS <8 or Blantyre <3) - CEREBRAL MALARIA - Protect airway, IV artesunate immediately',
      '🔴 CRITICAL: Respiratory distress/acidotic breathing - Give oxygen, check lactate and glucose urgently',
      '🔴 CRITICAL: Shock (SBP <80 adults, <70 children, weak pulse) - Cautious IV fluids, artesunate, vasopressors if available',
      '🔴 CRITICAL: Hypoglycemia (<2.2 mmol/L) - Give 50% dextrose IMMEDIATELY - this kills if missed',
      '🔴 CRITICAL: Severe anemia (Hb <5 g/dL) - Urgent blood transfusion required',
      '🔴 CRITICAL: Multiple/prolonged seizures - IV diazepam, protect airway, treat underlying malaria',
      '🔴 CRITICAL: Pulmonary edema/ARDS - Restrict fluids, oxygen, consider CPAP/ventilation, furosemide',
      '🟠 URGENT: Hyperparasitemia >10% - Consider exchange transfusion if available, monitor closely',
      '🟠 URGENT: Acute kidney injury (oliguria, rising creatinine) - Careful fluid management, may need dialysis',
      '🟠 URGENT: Hemoglobinuria (black/dark urine) - Aggressive hydration, monitor renal function closely',
      '🟠 URGENT: Abnormal bleeding/DIC - FFP, platelets if available, treat underlying malaria',
      '🟠 URGENT: Pregnant woman with severe malaria - Artesunate safe in all trimesters, fetal monitoring, obstetric alert'
    ],

    complications: [
      'Cerebral malaria - 15-20% mortality even with treatment, 10-25% neurological sequelae in survivors',
      'Severe malarial anemia - May require multiple transfusions',
      'Acute respiratory distress syndrome (ARDS) - High mortality',
      'Acute kidney injury - May require dialysis',
      'Metabolic acidosis - Lactate >5 mmol/L indicates severe disease',
      'Hypoglycemia - Common, recurrent, often fatal if missed',
      'Disseminated intravascular coagulation (DIC)',
      'Pulmonary edema - Often iatrogenic from over-hydration',
      'Secondary bacterial infections',
      'Post-artesunate delayed hemolysis (PADH) - Check Hb day 7 and 14'
    ],

    prognosis: 'WITH OPTIMAL TREATMENT: 10-20% mortality. CEREBRAL MALARIA: 15-25% mortality, neurological sequelae in 10-25% of child survivors. WITHOUT TREATMENT: Near 100% mortality. POOR PROGNOSTIC FACTORS: Deep coma, respiratory distress, shock, hypoglycemia, hyperparasitemia >20%, acidosis, renal failure.',

    prevention: 'Long-lasting insecticidal nets (LLINs) - most effective single intervention. Indoor residual spraying (IRS). IPTp with SP for pregnant women. Seasonal malaria chemoprevention (SMC) for children in Sahel. RTS,S/AS01 (Mosquirix) vaccine - WHO recommended 2021. R21/Matrix-M vaccine - WHO recommended 2023. Prompt treatment of uncomplicated malaria prevents progression.',

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════

Severe malaria is life-threatening but treatable. Complete ALL medication even when feeling better. Return IMMEDIATELY if: fever returns, cannot eat/drink, becomes confused, yellowing eyes. After discharge: sleep under insecticide-treated net EVERY night. Follow-up at day 7 and 14 is ESSENTIAL to check for delayed anemia. Pregnant women: attend all ANC visits for preventive treatment.`,

    references: [
      'WHO Guidelines for Malaria 2023. Geneva: World Health Organization.',
      'Africa CDC Malaria Diagnosis and Treatment Guidelines 2022.',
      'Severe Malaria. Tropical Medicine & International Health. 2014;19 Suppl 1:7-131.',
      'Dondorp AM, et al. Artesunate versus quinine in severe malaria. Lancet. 2010;376:1647-57.',
      'AQUAMAT Trial Group. Artesunate vs quinine in African children. Lancet. 2010;376:1647-57.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. ECLAMPSIA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '2',
    name: 'Eclampsia',
    slug: 'eclampsia',
    specialty: 'Obstetrics',
    bodySystem: 'Cardiovascular',
    icdCode: 'O15',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,
    
    overview: `Eclampsia is the occurrence of SEIZURES in a pregnant or postpartum woman with preeclampsia. It is a LEADING CAUSE OF MATERNAL DEATH in Africa, responsible for 10-15% of maternal deaths.


⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS:
• Can occur antepartum (50%), intrapartum (25%), or postpartum (25%)
• Can occur up to 6 weeks after delivery
• 20% may occur WITHOUT severe hypertension - don't be falsely reassured
• MAGNESIUM SULFATE is the treatment of choice (WHO Grade A)
• DELIVERY is the only definitive treatment
• Without treatment: maternal mortality >10%, fetal mortality >30%

EPIDEMIOLOGY:
• 1.4% of deliveries in sub-Saharan Africa (10x higher than high-income countries)
• 16-69 per 10,000 deliveries in Africa
• Case fatality: 1-2% in well-resourced settings, up to 15% in Africa
• Leading cause of maternal death in many African countries
• Intracranial hemorrhage is the primary cause of death`,

    epidemiology: `1.4% of deliveries in sub-Saharan Africa (10x higher than high-income countries). Case fatality 1-2% developed settings, up to 15% in Africa. RISK FACTORS: Primigravida (3x risk), age <18 or >35, multiple gestation, pre-existing HTN/renal disease/diabetes, obesity BMI >30, previous preeclampsia (15-20% recurrence), limited ANC.`,

    clinicalPresentation: `SEIZURE CHARACTERISTICS:
• Tonic-clonic (grand mal) seizures, typically 60-90 seconds
• May occur without warning or preceded by prodrome

PRODROMAL WARNING SIGNS:
• Severe headache - frontal, throbbing, NOT relieved by paracetamol
• Visual disturbances - blurred vision, scotomata, flashing lights, blindness
• Epigastric/RUQ pain - indicates liver involvement (HELLP)
• Nausea and vomiting
• Sudden severe edema (face, hands)

SIGNS:
• Blood pressure ≥140/90 mmHg (often ≥160/110 - severe)
• Proteinuria ≥2+ on dipstick (may be absent in 20%!)
• Hyperreflexia, clonus (≥3 beats)
• RUQ tenderness (liver capsule distension)
• Facial/periorbital/hand edema
• Reduced GCS post-seizure
• Fetal bradycardia during/after seizure (usually transient)

⚠️ NOTE: 20% of eclampsia occurs WITHOUT severe hypertension or proteinuria!`,

    diagnosis: `DIAGNOSTIC CRITERIA:
• New-onset tonic-clonic seizures in pregnancy or postpartum
• PLUS hypertension (BP ≥140/90) and/or proteinuria
• After excluding other causes of seizures

HELLP SYNDROME (10-20% of eclampsia):
• Hemolysis: LDH >600 U/L, schistocytes on smear
• Elevated Liver enzymes: AST/ALT >70 U/L
• Low Platelets: <100,000/μL
🔴 HELLP = VERY HIGH RISK - urgent delivery

DIFFERENTIAL DIAGNOSIS:
• Epilepsy - history of seizures
• Cerebral malaria - endemic area, parasites
• Meningitis/encephalitis - fever, neck stiffness
• Intracranial hemorrhage - focal signs
• Cerebral venous thrombosis
• Hypoglycemia`,

    differentialDiagnosis: [
      'Epilepsy',
      'Cerebral malaria',
      'Bacterial meningitis',
      'Viral encephalitis',
      'Intracranial hemorrhage',
      'Cerebral venous thrombosis',
      'Hypoglycemia',
      'Posterior reversible encephalopathy syndrome (PRES)'
    ],

    investigations: [
      { name: 'Blood Pressure', indication: 'Every 15 min until stable - target <160/110', availability: 'BASIC' },
      { name: 'Urine Dipstick', indication: 'Proteinuria assessment - may be negative in 20%', availability: 'BASIC' },
      { name: 'Complete Blood Count', indication: 'Platelets <100,000 suggests HELLP', availability: 'BASIC' },
      { name: 'Liver Function Tests', indication: 'AST/ALT >70 U/L indicates HELLP', availability: 'INTERMEDIATE' },
      { name: 'LDH', indication: '>600 U/L indicates hemolysis (HELLP)', availability: 'INTERMEDIATE' },
      { name: 'Renal Function', indication: 'Creatinine >90 μmol/L indicates renal involvement', availability: 'INTERMEDIATE' },
      { name: 'Coagulation Profile', indication: 'If bleeding or HELLP suspected - check for DIC', availability: 'INTERMEDIATE' },
      { name: 'Serum Magnesium', indication: 'Monitor during MgSO4 therapy - therapeutic 4-7 mEq/L', availability: 'INTERMEDIATE' },
      { name: 'Blood Glucose', indication: 'Rule out hypoglycemia as seizure cause', availability: 'BASIC' },
      { name: 'Blood Group & Crossmatch', indication: 'Prepare for possible emergency cesarean/hemorrhage', availability: 'BASIC' },
      { name: 'Fetal Heart Rate Monitoring', indication: 'Continuous CTG if available - assess fetal wellbeing', availability: 'INTERMEDIATE' },
      { name: 'CT/MRI Brain', indication: 'Only if atypical presentation, focal signs, or prolonged coma', availability: 'ADVANCED' }
    ],

    management: {
      immediate: [
        '⚡ PROTECT AIRWAY - Turn on LEFT SIDE (recovery position)',
        '⚡ DO NOT restrain or put anything in mouth during seizure',
        '⚡ GIVE MAGNESIUM SULFATE immediately after seizure stops',
        '⚡ Suction oropharynx if secretions present',
        '⚡ Give oxygen 8-10 L/min by face mask',
        '⚡ Establish IV access',
        '⚡ Monitor fetal heart rate (bradycardia during seizure usually transient)',
        '⚡ CALL FOR HELP - Obstetrician, anesthetist, pediatrician',
        'Check BP immediately after seizure stops',
        'Insert urinary catheter - Monitor output hourly (target >25-30 mL/hr)',
        'Send bloods: CBC, LFTs, renal function, coagulation'
      ],
      pharmacological: [
        {
          drug: 'Magnesium Sulfate - PRITCHARD REGIMEN (WHO First-Line)',
          indication: '🔴 ALL eclampsia - give immediately after seizure',
          dose: 'Loading: 4g IV over 20 min PLUS 10g IM (5g each buttock)',
          route: 'IV + IM',
          duration: 'Maintenance: 5g IM every 4 hours. Continue 24h after last seizure or delivery',
          contraindications: ['Myasthenia gravis'],
          sideEffects: ['Flushing', 'Nausea', 'Muscle weakness', '🔴 Respiratory depression if toxic'],
          alternatives: ['Zuspan regimen (IV only): 4g IV loading, then 1-2g/hr infusion']
        },
        {
          drug: 'Hydralazine',
          indication: 'Severe hypertension (BP ≥160/110) - second-line after MgSO4 started',
          dose: '5-10 mg IV bolus',
          route: 'IV slowly',
          duration: 'Repeat every 20 min as needed. Max 40 mg total',
          contraindications: ['Severe tachycardia', 'Aortic dissection'],
          sideEffects: ['Hypotension', 'Reflex tachycardia', 'Headache'],
          alternatives: ['Labetalol 20-80 mg IV', 'Nifedipine 10 mg oral']
        },
        {
          drug: 'Labetalol',
          indication: 'Severe hypertension - alternative to hydralazine',
          dose: '20 mg IV, then 40 mg, then 80 mg every 10-20 min',
          route: 'IV',
          duration: 'Escalating doses until BP controlled. Max 220 mg total',
          contraindications: ['Asthma', 'Heart block', 'Bradycardia', 'Heart failure'],
          sideEffects: ['Bradycardia', 'Bronchospasm', 'Neonatal bradycardia'],
          alternatives: ['Hydralazine', 'Nifedipine']
        },
        {
          drug: 'Nifedipine',
          indication: 'Severe hypertension when IV access unavailable',
          dose: '10 mg orally (NOT sublingual)',
          route: 'Oral - swallow whole, NOT sublingual',
          duration: 'Repeat after 30 min if needed',
          contraindications: ['Aortic stenosis'],
          sideEffects: ['Headache', 'Flushing', 'Reflex tachycardia'],
          alternatives: ['Hydralazine', 'Labetalol']
        },
        {
          drug: 'Calcium Gluconate 10%',
          indication: '🔴 ANTIDOTE for MgSO4 toxicity (RR <12, absent reflexes)',
          dose: '10 mL (1g) IV over 10 minutes',
          route: 'IV slowly',
          duration: 'Single dose - reverses magnesium effects',
          contraindications: [],
          sideEffects: ['Bradycardia if given too fast'],
          alternatives: []
        }
      ],
      nonPharmacological: [
        'Left lateral position - improves placental perfusion',
        'Oxygen supplementation 8-10 L/min',
        'Dim lights, quiet environment - reduce stimulation',
        'Continuous fetal heart rate monitoring',
        'Strict input/output monitoring',
        'Seizure precautions: padded bed rails, suction available',
        '🔴 DELIVERY is the definitive treatment - deliver once mother stabilized'
      ],
      referralCriteria: [
        '🔴 ALL eclampsia cases need hospital delivery',
        'Transfer to facility with: cesarean capability, blood transfusion, neonatal care',
        'Transfer to higher level if: refractory HTN, HELLP, DIC, renal failure, need for ICU'
      ],
      monitoring: [
        'Blood pressure every 15-30 min until stable, then hourly',
        'Oxygen saturation continuous',
        'Urine output hourly (target >25-30 mL/hr)',
        '⚠️ BEFORE EACH MgSO4 DOSE CHECK: RR >16, reflexes present, urine >25 mL/hr',
        'Fetal heart rate continuous (CTG if available)',
        'Level of consciousness - GCS',
        'Symptoms of impending seizure: headache, visual changes',
        'Signs of HELLP: RUQ pain, nausea, jaundice'
      ],
      followUp: 'Postpartum monitoring for 72 hours minimum. MgSO4 for 24h after delivery. BP monitoring for 6 weeks. Counsel on recurrence risk (15-20%) and importance of ANC in future pregnancies. Low-dose aspirin from 12 weeks in future pregnancies.'
    },

    redFlags: [
      '🔴 CRITICAL: Recurrent seizures despite MgSO4 - Check Mg level, give additional 2g IV, consider intubation',
      '🔴 CRITICAL: Respiratory depression (RR <12) - STOP MgSO4, give calcium gluconate 10% 10mL IV, support ventilation',
      '🔴 CRITICAL: Severe HTN >170/110 not responding - Risk of stroke, urgent BP control',
      '🔴 CRITICAL: HELLP syndrome developing - Urgent delivery, prepare for bleeding',
      '🔴 CRITICAL: Signs of stroke (focal weakness, aphasia) - CT scan, neurosurgery consult',
      '🔴 CRITICAL: Pulmonary edema - Restrict fluids, oxygen, furosemide, urgent delivery',
      '🟠 URGENT: Persistent severe headache/visual changes - May indicate impending seizure or stroke',
      '🟠 URGENT: Oliguria <100 mL in 4 hours - Reduce MgSO4 dose, may indicate renal failure',
      '🟠 URGENT: Fetal bradycardia >15 min after seizure - Prepare for emergency cesarean',
      '🟠 URGENT: Abdominal pain/uterine tenderness - Suspect placental abruption',
      '🟠 URGENT: Postpartum eclampsia (>48h after delivery) - Investigate other causes'
    ],

    complications: [
      'Recurrent seizures / status epilepticus',
      'Aspiration pneumonia',
      'Intracranial hemorrhage (leading cause of death)',
      'Cortical blindness (usually reversible)',
      'HELLP syndrome (20% of eclampsia)',
      'DIC (disseminated intravascular coagulation)',
      'Acute kidney injury',
      'Placental abruption',
      'Pulmonary edema',
      'Maternal death (1-15% depending on setting)',
      'Fetal/neonatal death (10-25%)',
      'Preterm delivery'
    ],

    prognosis: 'WITH MgSO4 AND APPROPRIATE MANAGEMENT: Maternal mortality <1% in well-resourced settings, 2-10% in low-resource settings. WITHOUT TREATMENT: Maternal mortality >10%. Recurrence risk in future pregnancies: 15-20%. Most women have no long-term sequelae if treated promptly.',

    prevention: 'Quality ANC with regular BP and urine checks. Low-dose aspirin 75-150 mg daily from 12 weeks in high-risk women. Calcium supplementation 1.5-2g daily in low-calcium-intake populations. Early detection and treatment of preeclampsia. MgSO4 prophylaxis in severe preeclampsia. Postpartum monitoring for 72 hours.',

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════

Eclampsia is a serious emergency but treatable. The medication (magnesium sulfate) prevents more seizures. Your baby needs to be delivered soon for both your safety. Continue medication for 24 hours after delivery. Future pregnancies: higher risk - early and regular ANC is essential. Return immediately if: severe headache, vision problems, epigastric pain, reduced urine, further convulsions.`,

    references: [
      'WHO Recommendations for Prevention and Treatment of Pre-eclampsia and Eclampsia. 2011.',
      'FIGO Guidelines on Pre-eclampsia and Eclampsia. 2019.',
      'Magpie Trial Collaborative Group. Lancet. 2002;359:1877-90.',
      'ACOG Practice Bulletin 222: Gestational Hypertension and Preeclampsia. 2020.',
      'Duley L. Pre-eclampsia, eclampsia, and hypertension. BMJ Clin Evid. 2011.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. POSTPARTUM HEMORRHAGE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '3',
    name: 'Postpartum Hemorrhage',
    slug: 'postpartum-hemorrhage',
    specialty: 'Obstetrics',
    bodySystem: 'Reproductive',
    icdCode: 'O72',
    resourceLevel: 'BASIC',
    isEmergency: true,
    
    overview: `Postpartum hemorrhage (PPH) is blood loss ≥500 mL after vaginal delivery or ≥1000 mL after cesarean section. It is the LEADING CAUSE OF MATERNAL DEATH globally.


⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS:
• UTERINE ATONY causes 70-80% of PPH - uterus is soft and boggy
• The "4 T's": Tone (atony), Trauma, Tissue (retained), Thrombin (coagulopathy)
• Blood loss is ALWAYS underestimated visually by 30-50%
• A woman can lose 1-2 liters before showing hypotension
• SPEED IS CRITICAL - death can occur within 1-2 hours
• OXYTOCIN is first-line treatment (WHO Grade A)
• 70,000 maternal deaths annually from PPH
• In Africa, PPH accounts for 25-30% of maternal deaths

RISK FACTORS:
Previous PPH (10-15% recurrence), grand multiparity (≥5), multiple pregnancy, polyhydramnios, macrosomia, prolonged/precipitous labor, induced/augmented labor, placenta previa/accreta, preeclampsia/HELLP, chorioamnionitis, anemia (Hb <10), uterine fibroids`,

    epidemiology: `6-10% of deliveries experience PPH; 1-3% severe PPH. 70,000 maternal deaths annually. PPH causes 25-30% of maternal deaths in Africa - leading cause. Case fatality up to 10% in low-resource settings. Higher rates due to anemia, grand multiparity, limited uterotonic access, home delivery.`,

    clinicalPresentation: `SYMPTOMS:
• Heavy vaginal bleeding after delivery
• Continuous trickling of blood (may be missed - look under buttocks!)
• Dizziness, lightheadedness, weakness
• Palpitations, thirst
• Shortness of breath
• Confusion (late sign - indicates severe blood loss)

SIGNS OF PPH:
• Visible heavy bleeding (pooling, clots)
• Blood-soaked pads, sheets, floor
• Soft, boggy uterus on palpation (ATONY - most common)
• Uterus not well-contracted, fundus above umbilicus
• Visible cervical or vaginal laceration

🔴 SIGNS OF HYPOVOLEMIC SHOCK (LATE - don't wait for these!):
• Tachycardia >100 bpm (often FIRST sign)
• Hypotension SBP <90 mmHg (LATE sign)
• Pallor, cold clammy skin
• Weak thready pulse
• Prolonged capillary refill >3 seconds
• Oliguria, altered consciousness

ESTIMATING BLOOD LOSS:
• 500 mL = 1 soaked maternity pad
• 1000 mL = blood pooling on floor, large clots
• 1500 mL = shock signs developing
• Visual estimation underestimates by 30-50% - WEIGH pads!`,

    diagnosis: `DIAGNOSTIC CRITERIA:
• Blood loss ≥500 mL after vaginal delivery
• Blood loss ≥1000 mL after cesarean section
• OR any blood loss causing hemodynamic instability
• OR blood loss requiring transfusion

THE 4 T's - IDENTIFY THE CAUSE:
1. TONE (70-80%): Uterine atony - uterus soft, boggy, not contracted
2. TRAUMA (10-20%): Lacerations of cervix, vagina, perineum; hematomas; uterine rupture
3. TISSUE (5-10%): Retained placenta or placental fragments
4. THROMBIN (<5%): Coagulopathy - DIC, preexisting disorders

EXAMINATION:
1. Palpate uterus - Is it firm and contracted?
2. Inspect perineum and vagina for lacerations
3. If atony controlled but bleeding continues → examine cervix and vagina
4. Explore uterine cavity if retained tissue suspected

BEDSIDE CLOTTING TEST:
• 5 mL blood in glass tube
• No clot at 7 minutes or soft clot that breaks easily = DIC`,

    differentialDiagnosis: [
      'Uterine atony (70-80%)',
      'Cervical/vaginal laceration',
      'Perineal tear',
      'Retained placenta/fragments',
      'Uterine rupture',
      'Uterine inversion',
      'Coagulopathy/DIC',
      'Placenta accreta spectrum'
    ],

    investigations: [
      { name: 'Hemoglobin/Hematocrit', indication: 'Baseline - may be normal initially, drops with fluid resuscitation', availability: 'BASIC' },
      { name: 'Blood Group & Crossmatch', indication: '🔴 URGENT - order immediately when PPH identified', availability: 'BASIC' },
      { name: 'Coagulation Profile', indication: 'PT/aPTT/fibrinogen if ongoing bleeding or DIC suspected', availability: 'INTERMEDIATE' },
      { name: 'Platelet Count', indication: 'Especially if HELLP or DIC suspected', availability: 'BASIC' },
      { name: 'Bedside Clotting Test', indication: '5 mL blood - no clot at 7 min = DIC (no lab needed)', availability: 'BASIC' },
      { name: 'Renal Function', indication: 'If prolonged shock - assess for AKI', availability: 'INTERMEDIATE' },
      { name: 'Ultrasound', indication: 'For retained products of conception if suspected', availability: 'INTERMEDIATE' }
    ],

    management: {
      immediate: [
        '⚡ CALL FOR HELP - Activate massive hemorrhage protocol',
        '⚡ RUB UP THE UTERUS - Bimanual massage to stimulate contraction',
        '⚡ GIVE OXYTOCIN 10 IU IM or slow IV immediately',
        '⚡ Establish 2 large-bore IV lines (16-18G)',
        '⚡ IV fluids rapidly - Crystalloid 1-2 L (NS or Ringer lactate)',
        '⚡ EMPTY BLADDER - Catheterize (full bladder prevents uterine contraction)',
        '⚡ Keep patient WARM - Hypothermia worsens coagulopathy',
        'Type and crossmatch - Order blood URGENTLY',
        'Examine: Identify cause using 4 T\'s (Tone, Trauma, Tissue, Thrombin)',
        'Weigh all blood-soaked materials to estimate loss accurately',
        'Call senior obstetrician, anesthetist, blood bank'
      ],
      pharmacological: [
        {
          drug: 'Oxytocin (FIRST-LINE - WHO Grade A)',
          indication: 'All PPH - give immediately',
          dose: '10 IU IM or 5 IU IV slow bolus',
          route: 'IM or slow IV (over 1-2 min)',
          duration: 'Continue infusion: 40 IU in 1L NS at 125 mL/hr for 4 hours. Max 60 IU/24h',
          contraindications: [],
          sideEffects: ['Hypotension if IV bolus given too fast', 'Water retention'],
          alternatives: ['Ergometrine', 'Misoprostol', 'Carboprost']
        },
        {
          drug: 'Tranexamic Acid (TXA) - WHO RECOMMENDED',
          indication: 'All PPH - give within 3 HOURS of delivery for best effect',
          dose: '1 g IV over 10 minutes',
          route: 'IV',
          duration: 'Repeat 1 g if bleeding continues after 30 min. Max 2 doses',
          contraindications: ['Active thromboembolic disease'],
          sideEffects: ['Nausea', 'Diarrhea', 'Rare: thrombosis'],
          alternatives: []
        },
        {
          drug: 'Ergometrine',
          indication: 'If oxytocin insufficient - second-line',
          dose: '0.5 mg IM or slow IV',
          route: 'IM preferred (IV can cause severe hypertension)',
          duration: 'May repeat once after 15 min. Max 1 mg total',
          contraindications: ['🔴 CONTRAINDICATED: Hypertension, preeclampsia, heart disease'],
          sideEffects: ['Severe hypertension', 'Nausea/vomiting', 'Coronary vasospasm'],
          alternatives: ['Misoprostol']
        },
        {
          drug: 'Misoprostol',
          indication: 'If oxytocin unavailable OR as adjunct',
          dose: '800 mcg sublingual OR 1000 mcg rectally',
          route: 'Sublingual (faster) or rectal',
          duration: 'Single dose',
          contraindications: [],
          sideEffects: ['Shivering', 'Fever', 'Diarrhea'],
          alternatives: ['Oxytocin', 'Ergometrine']
        },
        {
          drug: 'Carboprost (Hemabate)',
          indication: 'Refractory PPH when other uterotonics fail',
          dose: '250 mcg IM or intramyometrial',
          route: 'IM or directly into myometrium',
          duration: 'Every 15-90 min. Max 2 mg (8 doses)',
          contraindications: ['🔴 CONTRAINDICATED: Asthma'],
          sideEffects: ['Diarrhea', 'Fever', 'Hypertension', 'Bronchospasm'],
          alternatives: []
        },
        {
          drug: 'Blood Transfusion',
          indication: 'Ongoing hemorrhage, Hb <7 g/dL, hemodynamic instability',
          dose: 'Packed RBCs until Hb >7-8 g/dL',
          route: 'IV',
          duration: 'As needed until hemodynamically stable',
          contraindications: [],
          sideEffects: ['Transfusion reactions', 'TACO', 'Infection'],
          alternatives: ['O-negative blood if crossmatch unavailable']
        }
      ],
      nonPharmacological: [
        '🔴 BIMANUAL UTERINE COMPRESSION - One hand (fist) in vagina, one on abdomen',
        '🔴 AORTIC COMPRESSION - Fist pressure above umbilicus against spine (buys time)',
        'UTERINE BALLOON TAMPONADE - Condom catheter or Bakri balloon (inflate 300-500 mL saline)',
        'B-LYNCH SUTURE - At laparotomy for atony',
        'UTERINE ARTERY LIGATION - Surgical',
        'INTERNAL ILIAC ARTERY LIGATION - Surgical',
        '🔴 HYSTERECTOMY - Last resort to save life',
        'Non-pneumatic anti-shock garment (NASG) - Stabilizes for transfer'
      ],
      surgical: [
        'Repair of cervical/vaginal lacerations',
        'Manual removal of retained placenta',
        'Uterine exploration and curettage',
        'B-Lynch compression suture',
        'Uterine artery ligation',
        'Internal iliac artery ligation',
        'Hysterectomy (subtotal or total) - life-saving'
      ],
      referralCriteria: [
        '🔴 PPH not responding to uterotonics within 30 minutes',
        '🔴 Estimated blood loss >1000 mL',
        '🔴 Need for blood transfusion',
        'Suspected trauma requiring surgical repair',
        'Retained placenta requiring manual removal',
        'Hemodynamic instability despite resuscitation'
      ],
      monitoring: [
        'Blood loss (weigh pads, measure in containers)',
        'Vital signs every 5-15 minutes until stable',
        'Uterine tone (fundal height and firmness)',
        'Urine output hourly (target >30 mL/hr)',
        'Level of consciousness',
        'Oxygen saturation',
        'Hemoglobin (baseline, post-resuscitation, and q6-12h)',
        'Coagulation studies if ongoing bleeding'
      ],
      followUp: 'Monitor for 24 hours post-PPH. Treat anemia with iron supplementation. Debrief with patient. Counsel on recurrence risk and importance of facility delivery in future.'
    },

    redFlags: [
      '🔴 CRITICAL: Uterus not contracting despite uterotonics - Bimanual compression, balloon, prepare for surgery',
      '🔴 CRITICAL: Blood loss >1500 mL or shock - Massive transfusion protocol, surgical backup',
      '🔴 CRITICAL: DIC (uncontrolled bleeding + coagulopathy) - FFP, cryoprecipitate, platelets',
      '🔴 CRITICAL: Inverted uterus visible at introitus - Manual replacement immediately',
      '🔴 CRITICAL: Hemodynamic instability not responding - Prepare for surgery, may need hysterectomy',
      '🟠 URGENT: Cervical/vaginal laceration with active bleeding - Needs surgical repair',
      '🟠 URGENT: Retained placenta >30 minutes - Manual removal required',
      '🟠 URGENT: Ongoing bleeding with firm uterus - Look for trauma or retained tissue'
    ],

    complications: [
      'Hypovolemic shock',
      'DIC (disseminated intravascular coagulation)',
      'Acute kidney injury',
      'ARDS / pulmonary edema',
      'Sheehan syndrome (pituitary necrosis)',
      'Maternal death',
      'Need for hysterectomy (loss of fertility)',
      'Massive transfusion complications',
      'Infection / sepsis'
    ],

    prognosis: 'WITH PROMPT TREATMENT: Excellent outcome, mortality <1%. WITHOUT TREATMENT or DELAYED: Mortality 10-20%. Most deaths occur within 2-4 hours of delivery. Prognosis worsens with each 30-minute delay in treatment.',

    prevention: 'Active management of third stage of labor (AMTSL) - STANDARD OF CARE. Oxytocin 10 IU IM within 1 minute of delivery. Controlled cord traction after signs of separation. Uterine massage after placental delivery. Correct anemia in pregnancy (target Hb >10). Identify high-risk women for facility delivery. Misoprostol for home deliveries where skilled attendant unavailable.',

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════

Some bleeding after delivery is normal, but heavy bleeding is dangerous. Tell your birth attendant immediately if bleeding seems heavy. Danger signs: soaking more than 1 pad per hour, dizziness, weakness, fast heartbeat. Stay in the health facility for at least 24 hours after delivery. If delivered at home and bleeding heavily - go to health facility IMMEDIATELY. Future pregnancies: deliver in a health facility because of higher PPH risk.`,

    references: [
      'WHO Recommendations for PPH Prevention and Treatment. 2018.',
      'WOMAN Trial Collaborators. Lancet. 2017;389:2105-16.',
      'FIGO/ICM Guidelines for PPH Prevention. 2012.',
      'ACOG Practice Bulletin 183: Postpartum Hemorrhage. 2017.',
      'Escobar MF, et al. Uterine balloon tamponade. Int J Gynaecol Obstet. 2020.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. SEPSIS AND SEPTIC SHOCK
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '4',
    name: 'Sepsis and Septic Shock',
    slug: 'sepsis-septic-shock',
    specialty: 'Emergency Medicine',
    bodySystem: 'Systemic',
    icdCode: 'A41.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,
    overview: `Sepsis is life-threatening organ dysfunction caused by a dysregulated host response to infection. Septic shock is a subset with circulatory, cellular, and metabolic abnormalities associated with higher mortality. It is a MEDICAL EMERGENCY requiring immediate recognition and treatment.


⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS:
• EVERY HOUR of delay in antibiotics increases mortality by 7-8%
• HOUR-1 BUNDLE: Measure lactate, blood cultures, broad-spectrum antibiotics, IV fluids 30 mL/kg, vasopressors if hypotensive
• Sepsis = Infection + Organ Dysfunction (SOFA ≥2)
• Septic Shock = Sepsis + Vasopressors + Lactate >2 despite fluids
• qSOFA (≥2 = high risk): RR ≥22, Altered mentation, SBP ≤100
• Source control is CRITICAL - drain abscesses, remove infected devices
• Norepinephrine is first-line vasopressor
• Target MAP ≥65 mmHg`,
    epidemiology: `GLOBAL: 49 million cases annually, 11 million deaths (20% of all global deaths). Leading cause of hospital deaths worldwide.

AFRICAN CONTEXT: Higher mortality 30-50% vs 15-25% in high-income countries. Limited ICU capacity. High HIV-related sepsis burden. Neonatal sepsis major cause of under-5 mortality.

RISK FACTORS: Age extremes (<1, >65), immunocompromised (HIV, chemo, steroids), chronic disease (diabetes, CKD, cirrhosis), invasive devices, recent surgery/hospitalization, malnutrition, pregnancy/postpartum.

MORTALITY: Sepsis 15-25%, Septic shock 40-60%, Multi-organ failure >70%.`,
    clinicalPresentation: `EARLY SEPSIS (WARM SHOCK):
• Fever >38.3°C or hypothermia <36°C
• Tachycardia >90, Tachypnea >20
• Warm, flushed skin, bounding pulses
• Confusion, weakness, decreased urine output

LATE/SEPTIC SHOCK (COLD SHOCK):
🔴 Severe hypotension SBP <90 or MAP <65
🔴 Cold, clammy, mottled skin
🔴 Weak, thready pulse, cap refill >3 sec
🔴 Oliguria <0.5 mL/kg/hr
🔴 Altered consciousness → coma

ORGAN DYSFUNCTION:
• Brain: Confusion, delirium, coma
• Lungs: Hypoxemia, ARDS
• Heart: Hypotension, elevated troponin
• Kidneys: Oliguria, elevated creatinine
• Liver: Elevated bilirubin
• Coagulation: Thrombocytopenia, DIC

qSOFA (≥2 = High Risk): RR ≥22, Altered mentation, SBP ≤100`,
    diagnosis: `SEPSIS-3 CRITERIA:
• SEPSIS = Infection + SOFA score ≥2
• SEPTIC SHOCK = Sepsis + Vasopressors for MAP ≥65 + Lactate >2 despite fluids

SOFA SCORE (0-24): Respiration (PaO2/FiO2), Coagulation (platelets), Liver (bilirubin), Cardiovascular (MAP/vasopressors), CNS (GCS), Renal (creatinine/urine)

IDENTIFY SOURCE:
• Lungs: Pneumonia (most common)
• Urinary: UTI/pyelonephritis
• Abdomen: Peritonitis, cholangitis
• Skin: Cellulitis, necrotizing fasciitis
• Lines: Central line infection
• CNS: Meningitis
• Heart: Endocarditis

APPROACH: 1) Clinical suspicion 2) qSOFA screening 3) SOFA for organ dysfunction 4) Cultures BEFORE antibiotics 5) Lactate 6) Imaging for source`,
    differentialDiagnosis: [
      'Cardiogenic shock - Elevated JVP, pulmonary edema, poor EF on echo',
      'Hypovolemic shock - Bleeding/fluid loss history, responds to fluids',
      'Anaphylactic shock - Allergen exposure, urticaria, bronchospasm',
      'Neurogenic shock - Spinal injury, bradycardia, warm peripheries',
      'Adrenal crisis - Steroid use history, hypoglycemia, hyponatremia',
      'Severe pancreatitis - Epigastric pain, elevated lipase',
      'Toxic shock syndrome - Rash, mucosal involvement',
      'Severe malaria - Endemic area, parasites on smear'
    ],
    investigations: [
      { name: 'Blood Cultures x2', indication: '🔴 BEFORE antibiotics - do NOT delay Abx >45 min', availability: 'INTERMEDIATE' },
      { name: 'Serum Lactate', indication: '🔴 IMMEDIATE - >2 hypoperfusion, >4 severe', availability: 'INTERMEDIATE' },
      { name: 'CBC', indication: 'WBC >12K or <4K; thrombocytopenia suggests DIC', availability: 'BASIC' },
      { name: 'Creatinine/BUN', indication: 'Renal function - AKI common in sepsis', availability: 'BASIC' },
      { name: 'LFTs', indication: 'Elevated bilirubin = hepatic dysfunction', availability: 'INTERMEDIATE' },
      { name: 'Coagulation (PT/INR, aPTT)', indication: 'Assess for DIC', availability: 'INTERMEDIATE' },
      { name: 'ABG', indication: 'Oxygenation and acid-base status', availability: 'INTERMEDIATE' },
      { name: 'Procalcitonin', indication: 'Bacterial vs viral; >0.5 suggests bacterial', availability: 'ADVANCED' },
      { name: 'Glucose', indication: 'Hyperglycemia common; hypoglycemia = severe', availability: 'BASIC' },
      { name: 'Urinalysis/Culture', indication: 'If urinary source suspected', availability: 'BASIC' },
      { name: 'Chest X-ray', indication: '🔴 All patients - pneumonia, ARDS', availability: 'BASIC' },
      { name: 'CT Abdomen', indication: 'Intra-abdominal source - abscess, perforation', availability: 'ADVANCED' }
    ],
    management: {
      immediate: [
        '⚡ RECOGNIZE - qSOFA: RR ≥22, Altered mentation, SBP ≤100',
        '⚡ MEASURE LACTATE immediately',
        '⚡ BLOOD CULTURES x2 before antibiotics (do NOT delay >45 min)',
        '⚡ IV ANTIBIOTICS within 1 HOUR - every hour delay +7-8% mortality',
        '⚡ IV FLUIDS 30 mL/kg crystalloid in first 3 hours',
        '⚡ VASOPRESSORS if MAP <65 despite fluids - Norepinephrine first-line',
        '⚡ IV access - Two large bore 16-18G or central line',
        '⚡ Oxygen - Target SpO2 ≥94%',
        '⚡ Urinary catheter - Monitor output hourly (target ≥0.5 mL/kg/hr)',
        '⚡ SOURCE CONTROL - Drain abscesses, remove infected devices'
      ],
      pharmacological: [
        { drug: 'Ceftriaxone + Metronidazole', indication: '🔴 Community-acquired sepsis', dose: 'Ceftriaxone 2g IV daily + Metronidazole 500mg IV q8h', route: 'IV', duration: '7-10 days' },
        { drug: 'Piperacillin-Tazobactam', indication: '🔴 Broad-spectrum including Pseudomonas', dose: '4.5g IV q6h (extended infusion preferred)', route: 'IV', duration: '7-10 days' },
        { drug: 'Meropenem', indication: '🔴 Severe/healthcare-associated/ESBL suspected', dose: '1g IV q8h (2g for CNS)', route: 'IV', duration: '7-14 days' },
        { drug: 'Vancomycin', indication: 'Add if MRSA suspected', dose: 'Load 25-30 mg/kg, then 15-20 mg/kg q8-12h', route: 'IV', duration: 'Target trough 15-20' },
        { drug: 'Norepinephrine', indication: '🔴 First-line vasopressor - MAP <65 despite fluids', dose: 'Start 0.1-0.2 mcg/kg/min, titrate to MAP ≥65', route: 'IV infusion', duration: 'Until stable' },
        { drug: 'Vasopressin', indication: 'Add to norepinephrine if MAP not achieved', dose: '0.03-0.04 units/min (fixed, do NOT titrate)', route: 'IV infusion', duration: 'Until NE weaned' },
        { drug: 'Hydrocortisone', indication: '🟠 Refractory shock despite fluids/vasopressors', dose: '200 mg/day (50mg IV q6h or infusion)', route: 'IV', duration: 'Until off vasopressors, taper' },
        { drug: 'Crystalloids (NS or LR)', indication: '🔴 Initial resuscitation', dose: '30 mL/kg in first 3 hours', route: 'IV rapid', duration: 'Until perfusion restored' }
      ],
      referralCriteria: [
        '🔴 ALL septic shock → ICU',
        '🔴 Mechanical ventilation needed → ICU',
        '🔴 Multiple vasopressors → ICU',
        '🔴 Renal replacement needed → Dialysis capability',
        'Transfer if: No ICU, no surgical capability for source control'
      ]
    },
    redFlags: [
      '🔴 CRITICAL: MAP <65 despite 30 mL/kg fluids - Start vasopressors NOW',
      '🔴 CRITICAL: Lactate >4 or rising - Severe hypoperfusion',
      '🔴 CRITICAL: SpO2 <90% - Prepare for intubation',
      '🔴 CRITICAL: GCS <12 - Cerebral hypoperfusion, protect airway',
      '🔴 CRITICAL: Anuria >6 hours - May need dialysis',
      '🔴 CRITICAL: DIC (bleeding + low platelets + high INR)',
      '🔴 CRITICAL: Necrotizing fasciitis (crepitus, spreading) - EMERGENCY surgery',
      '🟠 URGENT: No improvement at 6 hours - Reassess source, resistance',
      '🟠 URGENT: Escalating vasopressors - Add vasopressin, hydrocortisone',
      '🟠 URGENT: New fever on antibiotics - Secondary infection, abscess'
    ],
    complications: [
      'Multi-organ dysfunction (MODS) - Mortality >70% if ≥3 organs',
      'ARDS - PaO2/FiO2 <300, 30-50% mortality',
      'Acute kidney injury - 40-50% of sepsis patients',
      'DIC - Bleeding and thrombosis',
      'Septic cardiomyopathy - Reversible',
      'Encephalopathy - 50-70%, long-term cognitive effects',
      'Critical illness myopathy - Prolonged weakness',
      'Post-sepsis syndrome - 50% of survivors'
    ],
        patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING SEPSIS
═══════════════════════════════════════════════════════════════════════════════

Sepsis is a life-threatening medical emergency that happens when an infection spreads throughout your body and causes your organs to start failing. It requires immediate hospital treatment with antibiotics and supportive care.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK EMERGENCY CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Go to the hospital or call emergency services immediately if you or someone you know has signs of infection AND any of these symptoms:

🔴 Confusion or difficulty waking up
🔴 Severe difficulty breathing
🔴 Skin that is mottled, bluish, or very pale
🔴 Not passing urine for many hours
🔴 Feeling like you might die
🔴 Very fast heartbeat
🔴 Fever with shaking chills OR abnormally low temperature

TIME IS CRITICAL - Every hour of delay in treatment increases the risk of death.

═══════════════════════════════════════════════════════════════════════════════
AFTER HOSPITAL DISCHARGE
═══════════════════════════════════════════════════════════════════════════════

• Complete ALL antibiotics as prescribed, even if you feel better
• Attend all follow-up appointments
• Recovery from sepsis can take weeks to months
• You may feel tired and weak for a long time - this is normal
• Watch for signs of infection returning

RETURN TO HOSPITAL IMMEDIATELY IF:
• Fever returns
• Symptoms worsen
• New confusion or drowsiness
• Difficulty breathing
• Reduced urination

═══════════════════════════════════════════════════════════════════════════════
PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• Keep wounds clean and covered
• Get recommended vaccinations
• Wash hands frequently
• Seek early treatment for infections
• Manage chronic conditions (diabetes, HIV) well

Your healthcare team is here to help you. Do not hesitate to ask questions or seek care when concerned.`,

references: [
      'Evans L, et al. Surviving Sepsis Campaign Guidelines 2021. Crit Care Med. 2021;49(11):e1063-e1143.',
      'Singer M, et al. Sepsis-3 Definitions. JAMA. 2016;315(8):801-810.',
      'Rhodes A, et al. Surviving Sepsis Campaign Guidelines 2016. Crit Care Med. 2017;45(3):486-552.',
      'Kumar A, et al. Antibiotic timing in septic shock. Crit Care Med. 2006;34(6):1589-1596.',
      'Rudd KE, et al. Global sepsis epidemiology. Lancet. 2020;395(10219):200-211.',
      'ARISE Investigators. Goal-directed resuscitation. N Engl J Med. 2014;371(16):1496-1506.',
      'Annane D, et al. APROCCHSS Trial. N Engl J Med. 2018;378(9):809-818.',
      'WHO. Global Report on Sepsis. Geneva: WHO; 2020.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 5. DIABETIC KETOACIDOSIS (DKA)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '5',
    name: 'Diabetic Ketoacidosis (DKA)',
    slug: 'diabetic-ketoacidosis',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E10.10',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Diabetic Ketoacidosis (DKA) is a life-threatening acute metabolic complication of diabetes characterized by the triad of hyperglycemia, ketosis, and metabolic acidosis. It results from absolute or relative insulin deficiency combined with counter-regulatory hormone excess.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• DKA TRIAD: Hyperglycemia (>250 mg/dL) + Ketosis + Metabolic Acidosis (pH <7.3, HCO3 <18)
• FLUID FIRST: Start IV normal saline BEFORE insulin - patients are 5-10L fluid depleted
• POTASSIUM RULES: Check K+ before insulin. If K+ <3.3, give potassium BEFORE insulin
• INSULIN: Start ONLY after K+ ≥3.3 mEq/L. Regular insulin 0.1 units/kg/hr IV infusion
• Monitor glucose hourly - target decrease 50-75 mg/dL per hour
• When glucose reaches 200-250 mg/dL, add dextrose (D5) to IV fluids - continue insulin
• ANION GAP: Calculate and follow until closed (AG = Na - Cl - HCO3; normal 8-12)
• PRECIPITANT: Always search for trigger (infection most common, missed insulin, MI, pancreatitis)
• CEREBRAL EDEMA: Major risk in children - avoid rapid fluid/glucose correction
• Transition to subcutaneous insulin when: eating, AG closed, pH >7.3, HCO3 >18

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

Insulin deficiency + Counter-regulatory hormone excess (glucagon, cortisol, catecholamines):
• Decreased glucose uptake → Hyperglycemia
• Increased gluconeogenesis and glycogenolysis → Worsening hyperglycemia
• Increased lipolysis → Free fatty acids → Hepatic ketogenesis
• Ketone bodies (β-hydroxybutyrate, acetoacetate) → Metabolic acidosis
• Osmotic diuresis → Profound dehydration and electrolyte loss
• Total body potassium depletion (despite normal/high serum K+)`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• Leading cause of death in children/young adults with Type 1 diabetes
• Incidence: 4-8 episodes per 1,000 diabetic patients per year
• Mortality: <1% in specialized centers, 5-10% in resource-limited settings
• DKA at diagnosis of diabetes: 15-70% depending on region

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Higher mortality rates due to delayed presentation and limited ICU access
• DKA often first presentation of previously undiagnosed diabetes
• Infection-precipitated DKA more common (malaria, TB, pneumonia)
• Limited access to insulin contributes to DKA episodes
• Traditional medicine use may delay presentation
• Ketone testing often unavailable - clinical diagnosis important

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

COMMON PRECIPITANTS (The 5 I's):
• Infection (most common - 30-50%) - UTI, pneumonia, sepsis
• Insulin omission/non-compliance (20-25%)
• Infarction - Myocardial infarction, stroke
• Initial presentation of diabetes (new diagnosis)
• Iatrogenic - Steroids, thiazides, SGLT2 inhibitors

OTHER TRIGGERS:
• Alcohol/substance abuse
• Pancreatitis
• Trauma/surgery
• Pregnancy
• Insulin pump failure

════════════════════════════════════════════════════════════════════════════════
HIGH-RISK GROUPS
════════════════════════════════════════════════════════════════════════════════

• Type 1 diabetes (classic DKA)
• Type 2 diabetes with severe stress (ketosis-prone Type 2)
• Adolescents with poor compliance
• Patients on SGLT2 inhibitors (euglycemic DKA)
• Pregnant women with diabetes
• Patients with limited healthcare access`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
SYMPTOMS - Typically develop over 24 hours
════════════════════════════════════════════════════════════════════════════════

CLASSIC SYMPTOMS:
• Polyuria, polydipsia, polyphagia (3 P's)
• Nausea, vomiting (often severe)
• Abdominal pain (can mimic acute abdomen)
• Weakness, fatigue, malaise
• Weight loss
• Altered mental status - confusion to coma

════════════════════════════════════════════════════════════════════════════════
SIGNS
════════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia (dehydration, compensation)
• Hypotension (severe dehydration, shock)
• Tachypnea → Kussmaul respirations (deep, rapid breathing)
• Hypothermia common (may mask infection fever)

EXAMINATION FINDINGS:
• Dehydration signs: Dry mucous membranes, poor skin turgor, sunken eyes
• Fruity/acetone breath odor (ketones)
• Kussmaul respirations (respiratory compensation for acidosis)
• Abdominal tenderness (can mimic surgical abdomen)
• Altered mental status correlates with osmolality
• Signs of precipitating cause (infection, MI)

════════════════════════════════════════════════════════════════════════════════
🔴 SEVERITY CLASSIFICATION
════════════════════════════════════════════════════════════════════════════════

MILD DKA:
• pH 7.25-7.30
• HCO3 15-18 mEq/L
• Alert mental status

MODERATE DKA:
• pH 7.00-7.24
• HCO3 10-14 mEq/L
• Alert/drowsy

SEVERE DKA:
• pH <7.00
• HCO3 <10 mEq/L
• Stupor/coma
• 🔴 Requires ICU admission`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA FOR DKA
════════════════════════════════════════════════════════════════════════════════

ALL THREE REQUIRED:
1. HYPERGLYCEMIA: Blood glucose >250 mg/dL (13.9 mmol/L)
   • Note: Can be lower in euglycemic DKA (SGLT2 inhibitors, pregnancy)

2. KETOSIS: Positive serum/urine ketones OR β-hydroxybutyrate >3 mmol/L

3. METABOLIC ACIDOSIS:
   • Arterial pH <7.3
   • Serum bicarbonate <18 mEq/L
   • Elevated anion gap >12

════════════════════════════════════════════════════════════════════════════════
ANION GAP CALCULATION
════════════════════════════════════════════════════════════════════════════════

Anion Gap = Na+ − (Cl− + HCO3−)

Normal: 8-12 mEq/L
DKA: Usually >20 mEq/L

Use to monitor resolution - AG should close with treatment

════════════════════════════════════════════════════════════════════════════════
EFFECTIVE OSMOLALITY
════════════════════════════════════════════════════════════════════════════════

Effective Osm = 2(Na+) + Glucose/18

Normal: 285-295 mOsm/kg
Correlates with mental status - >320 associated with obtundation

════════════════════════════════════════════════════════════════════════════════
CORRECTED SODIUM
════════════════════════════════════════════════════════════════════════════════

Corrected Na = Measured Na + 1.6 × [(Glucose - 100)/100]

Important for assessing true sodium status

════════════════════════════════════════════════════════════════════════════════
KEY DIAGNOSTIC POINTS
════════════════════════════════════════════════════════════════════════════════

• Serum potassium often normal or HIGH despite total body depletion
• WBC often elevated (stress response) - doesn't always indicate infection
• Serum creatinine may be falsely elevated (interference from ketones)
• Amylase/lipase can be elevated without pancreatitis
• Always look for precipitating cause`,

    differentialDiagnosis: [
      'Hyperosmolar Hyperglycemic State (HHS) - Glucose >600, minimal ketosis, pH >7.3, older patients, higher mortality',
      'Alcoholic ketoacidosis - History of alcohol, low/normal glucose, similar ketosis/acidosis',
      'Starvation ketosis - Mild ketosis, glucose normal/low, minimal acidosis',
      'Lactic acidosis - Normal glucose, elevated lactate, no ketones, often sepsis/hypoxia',
      'Uremic acidosis - Elevated BUN/creatinine, no ketones, chronic kidney disease history',
      'Toxic ingestions - Methanol, ethylene glycol (osmolar gap), salicylates (mixed picture)',
      'Sepsis - May coexist with or precipitate DKA, fever may be absent in DKA',
      'Acute pancreatitis - Can precipitate DKA, elevated lipase (can also be elevated in DKA)',
      'Acute abdomen - DKA abdominal pain can mimic surgical emergency',
      'Euglycemic DKA - SGLT2 inhibitors, pregnancy - ketosis/acidosis with glucose <250'
    ],

    investigations: [
      {
        name: 'Blood Glucose (Point-of-Care)',
        indication: '🔴 IMMEDIATE - Confirms hyperglycemia; repeat hourly during treatment',
        availability: 'BASIC'
      },
      {
        name: 'Serum/Urine Ketones',
        indication: '🔴 Confirms ketosis. Serum β-hydroxybutyrate preferred (>3 mmol/L diagnostic)',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: '🔴 Confirms acidosis - pH, pCO2, HCO3. Assess severity and respiratory compensation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Basic Metabolic Panel (Na, K, Cl, HCO3, BUN, Cr)',
        indication: '🔴 Calculate anion gap; assess renal function; monitor potassium closely',
        availability: 'BASIC'
      },
      {
        name: 'Serum Potassium',
        indication: '🔴 CRITICAL - Check BEFORE starting insulin. If <3.3, replace K+ before insulin',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'WBC elevated due to stress (not always infection); Hct elevated (dehydration)',
        availability: 'BASIC'
      },
      {
        name: 'Serum Osmolality',
        indication: 'Calculated or measured; correlates with mental status; helps differentiate from HHS',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urinalysis',
        indication: 'Ketones (may use nitroprusside - detects acetoacetate, not β-hydroxybutyrate); UTI screen',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: 'Assess chronic glycemic control; helps determine if new diagnosis vs poor control',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: 'If infection suspected as precipitant; pneumonia common trigger',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🔴 Assess for MI as precipitant; monitor for hypo/hyperkalemia changes',
        availability: 'BASIC'
      },
      {
        name: 'Blood/Urine Cultures',
        indication: 'If infection suspected; obtain before antibiotics',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Phosphate and Magnesium',
        indication: 'Often depleted; may need replacement especially if prolonged DKA',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lipase/Amylase',
        indication: 'If pancreatitis suspected as trigger; note: can be elevated in DKA without pancreatitis',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ABC assessment - Secure airway if GCS <8, give oxygen if hypoxic',
        '⚡ Establish 2 large-bore IV access',
        '⚡ Confirm diagnosis: Glucose, ketones, ABG/VBG, electrolytes',
        '⚡ Calculate anion gap: AG = Na - (Cl + HCO3)',
        '⚡ CHECK POTASSIUM before starting insulin',
        '⚡ START IV FLUIDS IMMEDIATELY - NS 1-1.5 L in first hour',
        '⚡ If K+ <3.3 mEq/L: Give 20-40 mEq KCl before starting insulin',
        '⚡ If K+ ≥3.3 mEq/L: Start insulin infusion 0.1 units/kg/hr',
        '⚡ Insert urinary catheter - monitor output hourly',
        '⚡ Place on cardiac monitor (dysrhythmias from K+ shifts)',
        '⚡ NGT if vomiting or altered mental status (aspiration risk)',
        'Search for and treat precipitating cause',
        'Monitor glucose HOURLY - target decrease 50-75 mg/dL/hr',
        'Monitor electrolytes every 2-4 hours',
        'Calculate corrected sodium to guide fluid therapy'
      ],
      pharmacological: [
        {
          drug: 'Normal Saline (0.9% NaCl) - Initial Fluid',
          indication: '🔴 FIRST PRIORITY - Patients are 5-10L fluid depleted',
          dose: '1-1.5 L in first hour, then 500-1000 mL/hr for next 2-4 hours. Total ~4-6L in first 6 hours',
          route: 'IV',
          duration: 'Until hemodynamically stable and able to take orals',
          contraindications: ['Heart failure (use caution, monitor for overload)', 'Severe hypernatremia'],
          sideEffects: ['Fluid overload', 'Hyperchloremic acidosis with large volumes'],
          alternatives: ['Switch to 0.45% NS when corrected Na normal/high; Add D5 when glucose <250']
        },
        {
          drug: 'Regular Insulin IV Infusion',
          indication: '🔴 After confirming K+ ≥3.3 mEq/L - Cornerstone of treatment',
          dose: 'Bolus: 0.1 units/kg IV (optional). Infusion: 0.1 units/kg/hr (or 0.14 units/kg/hr without bolus)',
          route: 'IV continuous infusion',
          duration: 'Until DKA resolved: pH >7.3, HCO3 >18, AG closed, eating',
          contraindications: ['🔴 K+ <3.3 mEq/L - Replace potassium first!'],
          sideEffects: ['Hypokalemia (monitor closely)', 'Hypoglycemia (add dextrose when glucose <250)'],
          alternatives: ['If IV insulin unavailable: Rapid-acting SC insulin q1-2h (less preferred)']
        },
        {
          drug: 'Potassium Chloride (KCl)',
          indication: '🔴 CRITICAL - Total body K+ depleted even if serum K+ normal/high',
          dose: 'K+ <3.3: Hold insulin, give 20-40 mEq/hr until K+ >3.3. K+ 3.3-5.3: Add 20-30 mEq to each liter of IV fluid. K+ >5.3: Hold K+, recheck in 2 hours',
          route: 'IV (central line preferred for high concentrations)',
          duration: 'Throughout DKA treatment; may need oral replacement for days',
          contraindications: ['K+ >5.3 mEq/L', 'Severe renal failure with oliguria'],
          sideEffects: ['Hyperkalemia if over-replaced', 'Cardiac arrhythmias', 'Phlebitis with peripheral IV'],
          alternatives: ['Potassium phosphate can replace some KCl if phosphate also low']
        },
        {
          drug: 'Dextrose 5% (D5) in Fluids',
          indication: '🔴 Add when glucose reaches 200-250 mg/dL - Prevents hypoglycemia while continuing insulin',
          dose: 'Change to D5 0.45% NS at 150-250 mL/hr when glucose 200-250 mg/dL',
          route: 'IV',
          duration: 'Until transitioned to subcutaneous insulin and eating',
          contraindications: ['Glucose still >250 mg/dL'],
          sideEffects: ['May slow glucose decline'],
          alternatives: ['D10W if hypoglycemia occurs despite reducing insulin rate']
        },
        {
          drug: 'Sodium Bicarbonate',
          indication: '🟠 ONLY if pH <6.9 or life-threatening hyperkalemia - Controversial, limited benefit',
          dose: '100 mEq in 400 mL sterile water over 2 hours. Repeat if pH remains <7.0',
          route: 'IV infusion (never bolus)',
          duration: 'Until pH >7.0',
          contraindications: ['pH >7.0 (no benefit, may worsen outcomes)', 'Hypokalemia (worsens K+ shift)'],
          sideEffects: ['Hypokalemia', 'Paradoxical CNS acidosis', 'Delayed ketosis resolution', 'Fluid overload'],
          alternatives: ['Treat underlying cause; adequate fluids and insulin usually correct acidosis']
        },
        {
          drug: 'Phosphate Replacement',
          indication: '🟡 Only if severe hypophosphatemia (<1 mg/dL) or cardiac/respiratory dysfunction',
          dose: '20-30 mEq potassium phosphate in IV fluids (replaces some KCl)',
          route: 'IV',
          duration: 'Until phosphate normalized',
          contraindications: ['Hypocalcemia', 'Renal failure'],
          sideEffects: ['Hypocalcemia', 'Hyperphosphatemia if excessive'],
          alternatives: ['Routine replacement not recommended; oral phosphate when eating']
        },
        {
          drug: 'Subcutaneous Insulin (Transition)',
          indication: 'When DKA resolved: pH >7.3, HCO3 >18, AG <12, glucose <200, patient eating',
          dose: 'Calculate total daily dose. Give 50% as basal, 50% as bolus with meals. Overlap with IV insulin for 1-2 hours',
          route: 'Subcutaneous',
          duration: 'Long-term management',
          contraindications: ['DKA not yet resolved', 'Not tolerating oral intake'],
          sideEffects: ['Hypoglycemia if dose too high', 'Recurrent DKA if transitioned too early'],
          alternatives: ['Resume home regimen if known and adequate; adjust based on HbA1c']
        },
        {
          drug: 'Antibiotics (If Infection)',
          indication: 'If infection identified or strongly suspected as precipitant',
          dose: 'Based on suspected source - e.g., Ceftriaxone 2g IV for pneumonia/UTI',
          route: 'IV',
          duration: 'Per infection guidelines',
          contraindications: ['Known allergy'],
          sideEffects: ['Drug-specific'],
          alternatives: ['Culture-directed therapy when results available']
        }
      ],
      nonPharmacological: [
        'Strict intake/output monitoring - Insert urinary catheter',
        'Cardiac monitoring - Watch for dysrhythmias from potassium shifts',
        'Hourly blood glucose monitoring',
        'Electrolyte monitoring every 2-4 hours initially',
        'Neurological checks every 1-2 hours (cerebral edema risk, especially children)',
        'NPO until mental status clear and not vomiting',
        'DVT prophylaxis when stable',
        'Diabetes education before discharge',
        'Social work/case management for insulin access issues',
        'Sick day management education'
      ],
      surgical: [
        'Rarely needed unless surgical precipitant identified',
        'Delay elective surgery until DKA fully resolved',
        'Emergency surgery may be needed for: Necrotizing fasciitis, perforated viscus, acute abdomen'
      ],
      referralCriteria: [
        '🔴 Severe DKA (pH <7.0) → ICU admission',
        '🔴 Altered mental status or coma → ICU',
        '🔴 Hemodynamic instability despite fluids → ICU',
        '🔴 Severe electrolyte abnormalities → ICU',
        '🔴 Children with DKA → Pediatric ICU (high cerebral edema risk)',
        '🔴 Pregnancy with DKA → High-risk obstetric unit',
        'Endocrinology consult for: New diagnosis diabetes, recurrent DKA, insulin regimen adjustment',
        'Transfer if: ICU unavailable, unable to monitor hourly glucose/electrolytes'
      ],
      monitoring: [
        'Blood glucose: HOURLY until stable, then every 2-4 hours',
        'Electrolytes (Na, K, Cl, HCO3): Every 2-4 hours until stable',
        'Anion gap: Calculate with each electrolyte panel - target closure',
        'Venous blood gas: Every 2-4 hours until pH >7.3',
        'Vital signs: Every 1-2 hours initially',
        'Urine output: Hourly (target >0.5 mL/kg/hr)',
        'Mental status: Every 1-2 hours (cerebral edema watch)',
        'Fluid balance: Strict I/O charting',
        'Insulin infusion rate: Adjust to achieve glucose drop of 50-75 mg/dL/hr',
        'Potassium: Recheck after each intervention'
      ],
      followUp: `DKA RESOLUTION CRITERIA (ALL required):
• Blood glucose <200 mg/dL
• Anion gap <12 (or back to baseline)
• Venous pH >7.3
• Serum bicarbonate ≥18 mEq/L
• Patient able to eat

TRANSITION TO SUBCUTANEOUS INSULIN:
• Calculate basal-bolus regimen
• Give SC long-acting insulin 1-2 hours BEFORE stopping IV insulin
• Overlap IV and SC insulin for at least 1-2 hours

BEFORE DISCHARGE:
• Identify and address precipitating cause
• Diabetes education: Sick day rules, when to seek care
• Ensure insulin access and affordability
• Blood glucose monitoring supplies
• Follow-up appointment within 1 week
• Endocrinology referral if new diagnosis or recurrent DKA

LONG-TERM:
• HbA1c target individualized (usually <7%)
• Regular follow-up with diabetes care team
• Annual screening for complications
• Address psychosocial barriers to adherence`
    },

    redFlags: [
      '🔴 CRITICAL: pH <6.9 - Life-threatening acidosis, consider bicarbonate, ICU admission',
      '🔴 CRITICAL: K+ <3.3 mEq/L - Do NOT give insulin until potassium replaced',
      '🔴 CRITICAL: Altered mental status or GCS <12 - Risk of aspiration, possible cerebral edema',
      '🔴 CRITICAL: Hemodynamic instability (SBP <90) despite fluids - Consider sepsis, cardiogenic cause',
      '🔴 CRITICAL: Signs of cerebral edema (headache, bradycardia, hypertension, altered consciousness) - Give mannitol, reduce fluids, intubate',
      '🔴 CRITICAL: Cardiac arrhythmias - Usually from hypo/hyperkalemia, check K+ and ECG immediately',
      '🔴 CRITICAL: No urine output despite fluids - AKI, may need dialysis',
      '🟠 URGENT: Glucose not dropping 50-75 mg/dL/hr - Increase insulin rate, check IV access',
      '🟠 URGENT: Anion gap not closing despite treatment - Reassess, look for other acid source',
      '🟠 URGENT: Recurrent vomiting - Risk of aspiration, place NGT, check electrolytes',
      '🟠 URGENT: Persistent tachycardia despite fluids - Assess for ongoing fluid loss, sepsis, MI',
      '🟠 URGENT: Fever >38.5°C - High likelihood of infection as precipitant, obtain cultures'
    ],

    complications: [
      'Cerebral edema - Most feared complication, especially in children; mortality 20-25%; presents with headache, deteriorating consciousness, bradycardia, hypertension',
      'Hypokalemia - From insulin-driven intracellular shift; can cause fatal arrhythmias; monitor closely',
      'Hypoglycemia - From overly aggressive insulin; prevented by adding dextrose when glucose <250',
      'Hypophosphatemia - Can cause respiratory muscle weakness, hemolysis; usually self-corrects',
      'Pulmonary edema - From aggressive fluid resuscitation, especially in elderly or renal impairment',
      'Acute respiratory distress syndrome (ARDS) - Rare but serious complication',
      'Venous thromboembolism - Dehydration and immobility increase risk; DVT prophylaxis indicated',
      'Acute kidney injury - From severe dehydration; usually reversible with fluids',
      'Rhabdomyolysis - From severe dehydration and hypophosphatemia',
      'Aspiration pneumonia - Risk with altered mental status and vomiting',
      'Recurrent DKA - Without addressing underlying cause and education; high readmission rates'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY RATES
════════════════════════════════════════════════════════════════════════════════

WITH APPROPRIATE TREATMENT:
• Overall mortality: <1% in specialized centers
• Mortality in resource-limited settings: 5-10%
• Mortality with cerebral edema: 20-25%
• Mortality increases with age and comorbidities

════════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
════════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Severe acidosis (pH <7.0)
• Hyperosmolality >320 mOsm/kg
• Altered mental status at presentation
• Age >65 years
• Comorbid conditions (renal failure, heart failure)
• Delayed treatment
• Serious precipitating illness (MI, sepsis)
• Cerebral edema (especially children)

GOOD PROGNOSIS:
• Young patients without comorbidities
• Mild to moderate DKA
• Early presentation and treatment
• Clear precipitant that is treatable
• Access to appropriate care

════════════════════════════════════════════════════════════════════════════════
RECURRENCE
════════════════════════════════════════════════════════════════════════════════

• 20-30% of patients have recurrent DKA
• Risk factors for recurrence:
  - Insulin non-adherence
  - Lack of diabetes education
  - Mental health issues (depression, eating disorders)
  - Substance abuse
  - Socioeconomic barriers to insulin access
  - Inadequate follow-up care`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

• Diabetes education at diagnosis
• Ensure insulin affordability and access
• Teach self-monitoring of blood glucose
• Sick day management education
• Recognition of DKA warning signs
• Regular follow-up with diabetes care team

════════════════════════════════════════════════════════════════════════════════
SICK DAY RULES - TEACH ALL PATIENTS
════════════════════════════════════════════════════════════════════════════════

• NEVER stop insulin, even if not eating
• Check blood glucose every 2-4 hours
• Check ketones if glucose >250 mg/dL
• Drink plenty of fluids
• Seek medical attention if:
  - Vomiting >2 times
  - Glucose persistently >300 mg/dL
  - Ketones positive
  - Unable to keep fluids down
  - Fever or signs of infection

════════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

• Address precipitant of each DKA episode
• Optimize diabetes regimen
• Screen for depression and eating disorders
• Social work involvement for adherence barriers
• Consider continuous glucose monitoring
• Diabetes support groups
• Home ketone monitoring for high-risk patients`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS DKA?
════════════════════════════════════════════════════════════════════════════════

Diabetic ketoacidosis happens when your body doesn't have enough insulin. Without insulin, your body can't use sugar for energy and starts breaking down fat instead. This produces acids called ketones that build up in your blood and make you very sick. DKA is a medical emergency.

════════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK EMERGENCY CARE
════════════════════════════════════════════════════════════════════════════════

🔴 Go to the hospital immediately if you have:
• Nausea and vomiting that won't stop
• Stomach pain
• Fruity smell on your breath
• Feeling very tired or confused
• Difficulty breathing
• Blood sugar over 300 mg/dL with ketones

════════════════════════════════════════════════════════════════════════════════
SICK DAY RULES
════════════════════════════════════════════════════════════════════════════════

When you're sick (infection, vomiting, fever):
1. NEVER stop your insulin - you may even need more
2. Check your blood sugar every 2-4 hours
3. Check for ketones if blood sugar >250
4. Drink plenty of water and sugar-free fluids
5. Try to eat small amounts of carbs if possible
6. Call your doctor or go to hospital if:
   - You can't keep fluids down
   - Ketones are moderate or high
   - Blood sugar stays >300 despite extra insulin
   - You feel confused or very sleepy

════════════════════════════════════════════════════════════════════════════════
PREVENTION
════════════════════════════════════════════════════════════════════════════════

• Take your insulin every day as prescribed
• Never skip doses, even if you're not eating
• Check your blood sugar regularly
• Keep extra insulin supplies at home
• Know the signs of infection
• Have a sick day plan ready
• Wear medical ID bracelet
• Keep follow-up appointments with your diabetes team

════════════════════════════════════════════════════════════════════════════════
AFTER DKA
════════════════════════════════════════════════════════════════════════════════

• Take all medications as prescribed
• Keep your follow-up appointment
• Talk to your doctor about what caused the DKA
• Ask about resources if you have trouble affording insulin
• Learn your personal warning signs
• Don't be afraid to ask for help`,

    references: [
      'Kitabchi AE, Umpierrez GE, Miles JM, Fisher JN. Hyperglycemic Crises in Adult Patients With Diabetes. Diabetes Care. 2009;32(7):1335-1343.',
      'American Diabetes Association. Standards of Medical Care in Diabetes - 2024. Diabetes Care. 2024;47(Suppl 1):S231-S243.',
      'Dhatariya KK, Glaser NS, Codner E, Umpierrez GE. Diabetic ketoacidosis. Nat Rev Dis Primers. 2020;6(1):40.',
      'Joint British Diabetes Societies. The Management of Diabetic Ketoacidosis in Adults. 2023 Update.',
      'Wolfsdorf JI, Glaser N, Agus M, et al. ISPAD Clinical Practice Consensus Guidelines 2018: Diabetic ketoacidosis and hyperglycemic hyperosmolar state. Pediatr Diabetes. 2018;19(Suppl 27):155-177.',
      'Umpierrez G, Korytkowski M. Diabetic emergencies - ketoacidosis, hyperglycaemic hyperosmolar state and hypoglycaemia. Nat Rev Endocrinol. 2016;12(4):222-232.',
      'Nyenwe EA, Kitabchi AE. The evolution of diabetic ketoacidosis: An update of its etiology, pathogenesis and management. Metabolism. 2016;65(4):507-521.',
      'Savage MW, Dhatariya KK, Kilvert A, et al. Joint British Diabetes Societies guideline for the management of diabetic ketoacidosis. Diabet Med. 2011;28(5):508-515.',
      'Eledrisi MS, Alshanti MS, Shah MF, et al. Overview of the diagnosis and management of diabetic ketoacidosis. Am J Med Sci. 2006;331(5):243-251.',
      'WHO. Global Report on Diabetes. Geneva: World Health Organization; 2016.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 6. ACUTE ASTHMA EXACERBATION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '6',
    name: 'Acute Asthma Exacerbation',
    slug: 'acute-asthma-exacerbation',
    specialty: 'Emergency Medicine',
    bodySystem: 'Respiratory',
    icdCode: 'J45.901',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Acute asthma exacerbation is an acute or subacute worsening of asthma symptoms and lung function compared to the patient's usual status. Severity ranges from mild to life-threatening. Prompt recognition and treatment are essential to prevent respiratory failure and death.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• ASSESS SEVERITY IMMEDIATELY - Mild, Moderate, Severe, or Life-Threatening
• GIVE OXYGEN first if SpO2 <92% - Target 93-95% (not 100%)
• BRONCHODILATORS early and often: Salbutamol (albuterol) nebulized or MDI with spacer
• STEROIDS within first hour - Oral prednisolone as effective as IV in most cases
• IPRATROPIUM adds benefit in severe exacerbations - Add to first 3 nebulizers
• SILENT CHEST is OMINOUS - Indicates severe air trapping, impending respiratory failure
• Peak flow <25% predicted or unable to perform = LIFE-THREATENING
• Normal/Rising pCO2 in acute asthma = DANGER (should be LOW from hyperventilation)
• If not responding to initial treatment → IV magnesium sulfate
• Prepare for intubation if: Exhaustion, confusion, silent chest, rising CO2, SpO2 <92% despite O2

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

Acute exacerbation involves:
• Bronchospasm - Smooth muscle contraction
• Airway inflammation - Mucosal edema
• Mucus hypersecretion - Airway plugging
• Air trapping - Increased residual volume
• V/Q mismatch - Hypoxemia
• Increased work of breathing → Respiratory muscle fatigue
• Progressive hyperinflation → Decreased venous return → Hypotension
• Respiratory failure if untreated`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• 339 million people affected by asthma worldwide
• 461,000 asthma deaths annually (most preventable)
• Emergency visits: 1.8 million annually in US alone
• Hospitalization rate declining with improved management
• Most deaths occur outside hospital - delays in seeking care

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Increasing prevalence, especially in urban areas
• Under-diagnosis and under-treatment common
• Limited access to inhaled corticosteroids for maintenance
• High mortality due to late presentation
• Over-reliance on short-acting bronchodilators (SABA)
• Biomass fuel exposure contributes to poor control
• Medication affordability major barrier

════════════════════════════════════════════════════════════════════════════════
COMMON TRIGGERS
════════════════════════════════════════════════════════════════════════════════

• Respiratory infections (viral most common - rhinovirus, RSV)
• Allergen exposure (dust mites, pollen, mold, pet dander)
• Air pollution and smoke (biomass, tobacco, outdoor pollution)
• Exercise (especially in cold/dry air)
• Medications (NSAIDs, beta-blockers, aspirin)
• Emotional stress
• Weather changes
• Occupational exposures
• GERD
• Non-adherence to controller medications

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS FOR SEVERE/FATAL ASTHMA
════════════════════════════════════════════════════════════════════════════════

🔴 HIGH RISK - These patients need close monitoring:
• Previous near-fatal asthma (intubation/ICU admission)
• Hospitalization or ED visit in past year
• Current or recent oral corticosteroid use
• Not currently using inhaled corticosteroids
• Over-use of SABA (>1 canister/month)
• Poor adherence to medications
• Psychosocial problems (depression, poor perception of symptoms)
• Food allergy in patient with asthma
• Low socioeconomic status, limited healthcare access`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

• Progressive shortness of breath
• Wheezing (may be absent in severe attack - silent chest)
• Chest tightness
• Cough (may be only symptom in cough-variant asthma)
• Difficulty speaking in full sentences
• Anxiety, feeling of doom
• Sleep disturbance due to symptoms

════════════════════════════════════════════════════════════════════════════════
SIGNS BY SEVERITY
════════════════════════════════════════════════════════════════════════════════

MILD-MODERATE:
• Talks in sentences
• Prefers sitting to lying
• Not agitated
• Respiratory rate increased
• Accessory muscle use: None to minimal
• Heart rate: 100-120 bpm
• SpO2 ≥92% on room air
• PEF >50% predicted/personal best

SEVERE EXACERBATION:
• Talks in words only (not sentences)
• Sits hunched forward
• Agitated
• Respiratory rate >30/min
• Accessory muscle use: YES (sternocleidomastoid, intercostals)
• Heart rate >120 bpm
• SpO2 <92% on room air
• PEF 25-50% predicted

🔴 LIFE-THREATENING - EMERGENCY:
• Drowsy, confused, exhausted
• SILENT CHEST (no wheeze - ominous sign)
• Cyanosis
• Bradycardia or arrhythmia
• Hypotension
• Unable to speak
• Poor respiratory effort
• SpO2 <92% despite oxygen
• PEF <25% predicted or unable to perform
• Normal or elevated pCO2 (should be low)

════════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
════════════════════════════════════════════════════════════════════════════════

INSPECTION:
• Respiratory distress, tachypnea
• Use of accessory muscles
• Inability to lie flat (orthopnea)
• Tripod positioning
• Intercostal and supraclavicular retractions
• Cyanosis (late sign)
• Diaphoresis

AUSCULTATION:
• Diffuse expiratory wheezing
• Prolonged expiratory phase
• Decreased air entry
• 🔴 SILENT CHEST = Severe air trapping, impending arrest

PALPATION:
• Tachycardia
• Pulsus paradoxus >12 mmHg (drop in SBP with inspiration)`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
CLINICAL DIAGNOSIS
════════════════════════════════════════════════════════════════════════════════

Diagnosis is primarily CLINICAL based on:
• Known history of asthma
• Acute onset of dyspnea, wheeze, cough, chest tightness
• Response to bronchodilator therapy

════════════════════════════════════════════════════════════════════════════════
SEVERITY CLASSIFICATION (GINA)
════════════════════════════════════════════════════════════════════════════════

MILD-MODERATE:
• Talks in sentences, SpO2 ≥92%, PEF >50%

SEVERE:
• Talks in words, sits hunched, agitated
• RR >30, HR >120, SpO2 <92%, PEF ≤50%
• Accessory muscle use

LIFE-THREATENING:
• Drowsy/confused, silent chest, cyanosis
• Poor respiratory effort, bradycardia
• SpO2 <92% despite O2, PEF <25%
• Normal/high pCO2, low pH

NEAR-FATAL:
• Raised pCO2 requiring mechanical ventilation

════════════════════════════════════════════════════════════════════════════════
PEAK EXPIRATORY FLOW (PEF)
════════════════════════════════════════════════════════════════════════════════

• Objective measure of airflow obstruction
• Compare to predicted or patient's personal best
• >50% = Mild-moderate
• 25-50% = Severe
• <25% = Life-threatening
• Unable to perform = Life-threatening

════════════════════════════════════════════════════════════════════════════════
WHEN TO OBTAIN ABG
════════════════════════════════════════════════════════════════════════════════

Not routinely needed but obtain if:
• SpO2 <92% despite oxygen
• Life-threatening features
• Deteriorating despite treatment
• Considering mechanical ventilation

ABG INTERPRETATION IN ACUTE ASTHMA:
• Early: Respiratory alkalosis (low pCO2) - normal response
• 🔴 DANGER: Normal or rising pCO2 = fatigue, impending failure
• Late: Respiratory acidosis (high pCO2) = respiratory failure`,

    differentialDiagnosis: [
      'COPD exacerbation - Older patient, smoking history, less reversibility, chronic symptoms',
      'Acute heart failure/pulmonary edema - Elevated JVP, crackles (not wheeze), S3 gallop, cardiac history',
      'Pneumonia - Fever, productive cough, focal crackles, consolidation on CXR',
      'Pneumothorax - Sudden onset, unilateral decreased breath sounds, hyperresonance',
      'Pulmonary embolism - Sudden onset, pleuritic pain, risk factors for DVT, clear lungs',
      'Anaphylaxis - Allergen exposure, urticaria, angioedema, hypotension',
      'Upper airway obstruction - Stridor (not wheeze), foreign body history, drooling',
      'Vocal cord dysfunction - Inspiratory stridor, throat tightness, normal SpO2, psychiatric comorbidity',
      'Hyperventilation/Panic attack - Perioral tingling, carpopedal spasm, normal SpO2 and PEF',
      'Bronchiectasis exacerbation - Chronic productive cough, recurrent infections, clubbing'
    ],

    investigations: [
      {
        name: 'Pulse Oximetry (SpO2)',
        indication: '🔴 IMMEDIATE - All patients. Target 93-95%. <92% indicates severe hypoxemia',
        availability: 'BASIC'
      },
      {
        name: 'Peak Expiratory Flow (PEF)',
        indication: '🔴 All patients if able - Objective severity assessment. Repeat after treatment to assess response',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: 'Severe/life-threatening exacerbation, SpO2 <92%, deteriorating. NORMAL pCO2 is concerning!',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: 'Not routine. Obtain if: Suspected pneumonia, pneumothorax, no improvement, first presentation, fever',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: 'Severe exacerbation, older patients, cardiac history - Rule out arrhythmia, ischemia',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'If infection suspected; eosinophilia supports asthma diagnosis; anemia worsens hypoxia',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: 'Severe exacerbation - Check potassium (drops with beta-agonists and steroids)',
        availability: 'BASIC'
      },
      {
        name: 'Serum Magnesium',
        indication: 'If considering IV magnesium; prior to repeat dosing',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Cultures',
        indication: 'Only if sepsis or bacterial infection suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Spirometry',
        indication: 'NOT during acute exacerbation - Use for diagnosis and monitoring between exacerbations',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY immediately - Mild/Moderate vs Severe vs Life-Threatening',
        '⚡ OXYGEN if SpO2 <92% - Target 93-95% (use nasal cannula or mask)',
        '⚡ SALBUTAMOL nebulized 5mg OR MDI 4-10 puffs via spacer - Repeat every 20 min x 3 in first hour',
        '⚡ IPRATROPIUM 500 mcg nebulized - Add to salbutamol for severe exacerbation (first 3 doses)',
        '⚡ CORTICOSTEROIDS within 1 hour - Prednisolone 40-50mg PO or Hydrocortisone 100mg IV',
        '⚡ Position patient upright (sitting forward)',
        '⚡ Obtain IV access',
        '⚡ Monitor: SpO2 continuously, PEF before and after treatment',
        '⚡ REASSESS after each bronchodilator treatment',
        'If SEVERE/LIFE-THREATENING: Call for senior help, prepare for possible intubation',
        'If not responding after 1 hour: IV Magnesium Sulfate 2g over 20 minutes',
        'Consider IV salbutamol if nebulized not effective (250 mcg slow IV)',
        'Avoid sedatives - Respiratory depression risk',
        'Keep patient NPO if severe (aspiration risk, possible intubation)'
      ],
      pharmacological: [
        {
          drug: 'Salbutamol (Albuterol) - Nebulized',
          indication: '🔴 FIRST-LINE - All acute asthma exacerbations',
          dose: '5mg nebulized with oxygen (6-8 L/min). Severe: Continuous nebulization or every 20 min x 3, then every 1-4 hours',
          route: 'Nebulized',
          duration: 'Until significant improvement, then space to every 4-6 hours',
          contraindications: ['Hypersensitivity (rare)'],
          sideEffects: ['Tremor', 'Tachycardia', 'Hypokalemia', 'Hyperglycemia'],
          alternatives: ['MDI with spacer 4-10 puffs equally effective if technique good']
        },
        {
          drug: 'Salbutamol - MDI with Spacer',
          indication: '🔴 Alternative to nebulizer - Equally effective with proper technique',
          dose: '4-10 puffs (100 mcg/puff) via spacer. Repeat every 20 min in severe exacerbation',
          route: 'Inhaled via spacer',
          duration: 'As needed based on response',
          contraindications: ['Unable to coordinate (use nebulizer)', 'Life-threatening (use nebulizer with O2)'],
          sideEffects: ['Tremor', 'Tachycardia', 'Palpitations'],
          alternatives: ['Nebulized salbutamol', 'Terbutaline', 'Levalbuterol']
        },
        {
          drug: 'Ipratropium Bromide',
          indication: '🔴 Add in SEVERE exacerbation - Synergistic with salbutamol',
          dose: '500 mcg nebulized with salbutamol. Give with first 3 nebulizers (every 20 min x 3)',
          route: 'Nebulized',
          duration: 'First hour only (first 3 doses), not needed for maintenance',
          contraindications: ['Hypersensitivity to atropine-like drugs', 'Narrow-angle glaucoma (nebulizer mist to eyes)'],
          sideEffects: ['Dry mouth', 'Urinary retention', 'Blurred vision if in eyes'],
          alternatives: ['Glycopyrrolate']
        },
        {
          drug: 'Prednisolone (Oral Corticosteroid)',
          indication: '🔴 ESSENTIAL - Give within first hour. Oral as effective as IV in most cases',
          dose: 'Adults: 40-50 mg once daily. Children: 1-2 mg/kg (max 40mg)',
          route: 'Oral',
          duration: '5-7 days (no taper needed for short course)',
          contraindications: ['Active untreated infection (relative)', 'Give anyway in acute asthma'],
          sideEffects: ['Hyperglycemia', 'Mood changes', 'Sleep disturbance', 'GI upset'],
          alternatives: ['Hydrocortisone 100mg IV if unable to take PO or vomiting']
        },
        {
          drug: 'Hydrocortisone (IV Corticosteroid)',
          indication: 'Unable to take oral, vomiting, severe/life-threatening, or impaired absorption',
          dose: '100 mg IV every 6 hours',
          route: 'IV',
          duration: 'Until able to take oral, then switch to prednisolone',
          contraindications: ['None absolute in acute severe asthma'],
          sideEffects: ['Hyperglycemia', 'Hypertension', 'Fluid retention'],
          alternatives: ['Methylprednisolone 40-125mg IV']
        },
        {
          drug: 'Magnesium Sulfate IV',
          indication: '🟠 SEVERE exacerbation not responding to initial bronchodilators. Single dose.',
          dose: 'Adults: 2g (8 mmol) IV over 20 minutes. Children: 40 mg/kg (max 2g)',
          route: 'IV infusion over 20 minutes',
          duration: 'Single dose; can repeat once if needed',
          contraindications: ['Renal failure (reduce dose)', 'Heart block', 'Myasthenia gravis'],
          sideEffects: ['Flushing', 'Hypotension', 'Muscle weakness', 'Respiratory depression (rare at this dose)'],
          alternatives: ['Nebulized magnesium (less effective)']
        },
        {
          drug: 'Aminophylline IV',
          indication: '🟡 NOT first-line. Consider only in life-threatening asthma not responding to other treatments',
          dose: 'Loading: 5 mg/kg IV over 20-30 min (omit if on theophylline). Maintenance: 0.5 mg/kg/hr',
          route: 'IV infusion',
          duration: 'Until improvement; switch to oral if continued',
          contraindications: ['Seizure disorder (relative)', 'Cardiac arrhythmias'],
          sideEffects: ['🔴 Narrow therapeutic window - toxicity risk', 'Nausea', 'Tachycardia', 'Seizures', 'Arrhythmias'],
          alternatives: ['Avoid if possible - high side effect profile']
        },
        {
          drug: 'Epinephrine (Adrenaline) IM',
          indication: '🔴 Anaphylaxis with asthma OR Life-threatening asthma not responding to nebulized therapy',
          dose: '0.3-0.5 mg (0.3-0.5 mL of 1:1000) IM into lateral thigh',
          route: 'IM (NOT IV unless cardiac arrest)',
          duration: 'Single dose; can repeat every 5-15 min if needed',
          contraindications: ['None absolute in life-threatening situation'],
          sideEffects: ['Tachycardia', 'Hypertension', 'Anxiety', 'Tremor', 'Arrhythmias'],
          alternatives: ['IV epinephrine 0.1-0.5 mg (1:10,000) ONLY in near-arrest']
        },
        {
          drug: 'Oxygen',
          indication: '🔴 SpO2 <92% or any respiratory distress',
          dose: 'Titrate to SpO2 93-95%. Start with nasal cannula 2-4 L/min or mask 6-10 L/min',
          route: 'Nasal cannula or face mask',
          duration: 'Until SpO2 stable ≥94% on room air',
          contraindications: ['None'],
          sideEffects: ['CO2 retention in some COPD patients (less concern in pure asthma)'],
          alternatives: ['High-flow nasal cannula if available and needed']
        }
      ],
      nonPharmacological: [
        'Position upright - Sitting forward with arms supported optimizes breathing',
        'Calm reassurance - Anxiety worsens bronchospasm and hyperventilation',
        'Remove triggers if identifiable (allergens, smoke)',
        'Ensure good inhaler/spacer technique',
        'Humidified oxygen if available',
        'Chest physiotherapy NOT recommended in acute phase',
        'Heliox (helium-oxygen) - May help in severe cases if available (reduces airway resistance)',
        'Non-invasive ventilation (BiPAP) - Can be tried before intubation in some patients',
        'Hydration - IV fluids if unable to drink, but avoid overhydration'
      ],
      surgical: [
        'Intubation and mechanical ventilation for respiratory failure',
        'Rarely needed if medical management optimized',
        'Indications: Respiratory arrest, deteriorating despite maximal therapy, exhaustion, coma',
        'Use ketamine for induction (bronchodilator properties)',
        'Expect high airway pressures - Use low RR, prolonged expiratory time',
        'ECMO in refractory cases at specialized centers (very rare)'
      ],
      referralCriteria: [
        '🔴 Life-threatening features → ICU immediately',
        '🔴 Not responding to treatment after 1-2 hours → ICU/HDU',
        '🔴 Rising pCO2 or falling pH → ICU, prepare for intubation',
        '🔴 Exhaustion, confusion, or decreasing consciousness → ICU',
        '🔴 Previous near-fatal asthma + current severe attack → ICU',
        'Admit to hospital if: Severe attack, poor response to ED treatment, high-risk patient, social concerns',
        'Can discharge if: Good response (PEF >75%), stable >1 hour, adequate home support, has medications'
      ],
      monitoring: [
        'SpO2: Continuous monitoring until stable',
        'Peak flow: Before and after each bronchodilator treatment',
        'Respiratory rate and work of breathing: Every 15-30 min',
        'Heart rate: Continuous (watch for tachycardia from beta-agonists)',
        'Potassium: Check if prolonged beta-agonist use (causes hypokalemia)',
        'ABG: If severe, if SpO2 not improving, if deteriorating',
        'Level of consciousness: Drowsiness = danger sign',
        'Response to each treatment - Document and adjust'
      ],
      followUp: `BEFORE DISCHARGE - ENSURE ALL OF THE FOLLOWING:

CLINICAL STABILITY:
• PEF >75% predicted or personal best
• Stable on discharge medications for ≥1 hour
• SpO2 ≥94% on room air
• Able to use inhaler correctly

DISCHARGE MEDICATIONS:
• Continue prednisolone 40-50mg daily to complete 5-7 day course
• Reliever: Salbutamol inhaler PRN
• START or OPTIMIZE controller: Inhaled corticosteroid (ICS)
• Provide spacer device

EDUCATION:
• Inhaler technique - Demonstrate and check
• Written asthma action plan
• Recognize worsening symptoms
• When to seek emergency care
• Avoid known triggers
• Importance of controller medication adherence

FOLLOW-UP:
• GP/Primary care within 2-7 days
• Specialist referral if: Near-fatal asthma, recurrent severe exacerbations, poor control
• Review and adjust long-term therapy`
    },

    redFlags: [
      '🔴 CRITICAL: Silent chest - Severe air trapping, impending respiratory arrest, needs immediate ICU',
      '🔴 CRITICAL: Drowsy, confused, or exhausted - Respiratory failure imminent, prepare for intubation',
      '🔴 CRITICAL: Cyanosis - Severe hypoxemia, high-flow oxygen, ICU',
      '🔴 CRITICAL: SpO2 <92% despite high-flow oxygen - Consider intubation',
      '🔴 CRITICAL: Normal or rising pCO2 on ABG - Failing respiratory compensation, ICU',
      '🔴 CRITICAL: Bradycardia or hypotension - Pre-arrest, call resuscitation team',
      '🔴 CRITICAL: Unable to speak or complete sentences - Severe exacerbation, aggressive treatment',
      '🔴 CRITICAL: PEF <25% predicted or unable to perform - Life-threatening',
      '🟠 URGENT: No improvement after 15-20 min of continuous nebulizers - Add ipratropium, magnesium, escalate care',
      '🟠 URGENT: Requiring nebulizers more than every 4 hours - Needs admission, not ready for discharge',
      '🟠 URGENT: Previous near-fatal asthma - Higher threshold for admission and ICU',
      '🟠 URGENT: Peak flow not improving to >50% - Needs continued treatment and likely admission'
    ],

    complications: [
      'Respiratory failure requiring intubation - High mortality once intubated for asthma',
      'Pneumothorax/Pneumomediastinum - From air trapping and high airway pressures',
      'Hypoxic brain injury - From prolonged severe hypoxemia',
      'Cardiac arrhythmias - From hypoxia, beta-agonists, and electrolyte disturbances',
      'Hypokalemia - From beta-agonists and steroids; can cause arrhythmias',
      'Respiratory arrest - Can occur suddenly, even with apparently stable patients',
      'Mucus plugging and atelectasis - May require bronchoscopy in intubated patients',
      'Myopathy - From steroids and prolonged paralysis if ventilated',
      'Death - Most preventable with prompt treatment; highest risk outside hospital'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
OUTCOMES
════════════════════════════════════════════════════════════════════════════════

WITH APPROPRIATE TREATMENT:
• Most patients respond well to bronchodilators and steroids
• ED discharge rate: 50-70% with proper treatment
• Hospital mortality: <1% in well-resourced settings
• Overall asthma mortality declining with ICS use

MORTALITY RISK FACTORS:
• Previous near-fatal asthma
• Intubation required
• Delayed presentation
• Poor access to healthcare
• Under-use of inhaled corticosteroids
• Over-reliance on SABA

════════════════════════════════════════════════════════════════════════════════
LONG-TERM
════════════════════════════════════════════════════════════════════════════════

• Each severe exacerbation increases risk of future exacerbations
• Exacerbations accelerate lung function decline
• Good long-term control reduces exacerbation risk
• Most deaths are PREVENTABLE with:
  - Regular controller medications
  - Written action plans
  - Access to care
  - Patient education`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENT EXACERBATIONS
════════════════════════════════════════════════════════════════════════════════

MEDICATIONS:
• Inhaled corticosteroids (ICS) - Cornerstone of prevention
• ICS-formoterol as reliever (GINA preferred strategy)
• Add-on therapies if needed: LABA, LTRA, tiotropium, biologics
• Adherence is critical - Most patients underuse controllers

WRITTEN ASTHMA ACTION PLAN:
• Recognize worsening symptoms
• When to increase medications
• When to seek medical care
• Personalized to patient's triggers and history

TRIGGER AVOIDANCE:
• Identify and avoid allergens
• Smoking cessation (patient and household)
• Avoid biomass fuel smoke where possible
• Occupational exposures - May need job modification

VACCINATIONS:
• Annual influenza vaccine
• Pneumococcal vaccine
• COVID-19 vaccination

REGULAR REVIEW:
• Assess control at every visit (ACT or ACQ)
• Check inhaler technique regularly
• Step up or down therapy as needed
• Monitor lung function with spirometry

SPECIAL CONSIDERATIONS:
• Pregnancy: Continue controllers - Risk of exacerbation > Medication risk
• Surgery: Optimize control pre-operatively
• Allergic rhinitis: Treat - Upper airway inflammation affects lower`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS AN ASTHMA ATTACK?
════════════════════════════════════════════════════════════════════════════════

During an asthma attack, the airways in your lungs become narrow, making it hard to breathe. This happens because:
• Muscles around the airways tighten (bronchospasm)
• The airway lining swells
• Extra mucus is produced

Asthma attacks can be mild or severe. Severe attacks are medical emergencies and can be life-threatening if not treated quickly.

════════════════════════════════════════════════════════════════════════════════
🔴 EMERGENCY - CALL FOR HELP OR GO TO HOSPITAL IF:
════════════════════════════════════════════════════════════════════════════════

• Your reliever inhaler is not helping
• You cannot speak in full sentences
• You are too breathless to eat or walk
• Your lips or fingernails are turning blue
• You are getting worse despite using your inhaler
• You feel frightened or exhausted

DON'T WAIT - Asthma attacks can get worse very quickly!

════════════════════════════════════════════════════════════════════════════════
WHAT TO DO DURING AN ATTACK
════════════════════════════════════════════════════════════════════════════════

1. Sit upright - Don't lie down
2. Take your reliever inhaler (usually blue)
   - Take 4-10 puffs through a spacer
   - Wait 4 minutes
   - If no better, take 4-10 more puffs
3. If still no improvement - CALL FOR EMERGENCY HELP
4. Keep taking 4 puffs every 4 minutes while waiting for help
5. Stay calm - Panic makes breathing harder

════════════════════════════════════════════════════════════════════════════════
PREVENT ATTACKS
════════════════════════════════════════════════════════════════════════════════

• Take your preventer inhaler EVERY DAY (usually brown/orange)
  - Even when you feel well
  - It takes weeks to work fully
  - Don't stop when you feel better

• Use correct inhaler technique
  - Use a spacer
  - Ask your doctor to check your technique

• Know and avoid your triggers
  - Dust, smoke, pets, pollen, cold air
  - Viral infections

• Follow your asthma action plan
  - Know when to increase medications
  - Know when to seek help

• Get your flu vaccine every year

• See your doctor regularly
  - Even when you feel well
  - Get your asthma checked at least yearly`,

    references: [
      'Global Initiative for Asthma (GINA). Global Strategy for Asthma Management and Prevention. 2024 Update.',
      'British Thoracic Society/Scottish Intercollegiate Guidelines Network. British Guideline on the Management of Asthma. 2019 (Updated 2024).',
      'National Asthma Education and Prevention Program. Expert Panel Report 4: Guidelines for the Diagnosis and Management of Asthma. NHLBI. 2020.',
      'Reddel HK, Bacharier LB, Bateman ED, et al. Global Initiative for Asthma Strategy 2021: Executive Summary and Rationale for Key Changes. Eur Respir J. 2022;59(1):2102730.',
      'Rowe BH, Spooner CH, Ducharme FM, et al. Corticosteroids for preventing relapse following acute exacerbations of asthma. Cochrane Database Syst Rev. 2007;(3):CD000195.',
      'Kew KM, Kirtchuk L, Michell CI. Intravenous magnesium sulfate for treating adults with acute asthma in the emergency department. Cochrane Database Syst Rev. 2014;(5):CD010909.',
      'Rodrigo GJ, Castro-Rodriguez JA. Anticholinergics in the treatment of children and adults with acute asthma: a systematic review with meta-analysis. Thorax. 2005;60(9):740-746.',
      'Cates CJ, Welsh EJ, Rowe BH. Holding chambers (spacers) versus nebulisers for beta-agonist treatment of acute asthma. Cochrane Database Syst Rev. 2013;(9):CD000052.',
      'Brenner BE, Chavda KK, Camargo CA Jr. Randomized trial of inhaled flunisolide versus placebo among asthmatic patients discharged from the emergency department. Ann Emerg Med. 2000;36(5):417-426.',
      'World Health Organization. Global Burden of Disease Study - Asthma. WHO; 2019.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 7. ACUTE MYOCARDIAL INFARCTION (STEMI/NSTEMI)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '7',
    name: 'Acute Myocardial Infarction (STEMI/NSTEMI)',
    slug: 'acute-myocardial-infarction',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I21.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute Myocardial Infarction (AMI) is myocardial necrosis due to acute coronary artery obstruction. STEMI (ST-Elevation MI) requires emergent reperfusion. NSTEMI (Non-ST-Elevation MI) is managed based on risk stratification. TIME IS MUSCLE - Every minute of delay increases myocardial damage and mortality.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• ECG within 10 MINUTES of first medical contact - Identify STEMI immediately
• STEMI = Emergent reperfusion: PCI (goal <90 min) OR Thrombolysis (goal <30 min)
• "TIME IS MUSCLE" - Each 30-min delay increases mortality by 7.5%
• MONA-B: Morphine (if needed), Oxygen (if SpO2 <90%), Nitrates, Aspirin, Beta-blocker
• ASPIRIN 300mg CHEWED immediately - Do not delay for any reason
• Dual Antiplatelet Therapy (DAPT): Aspirin + P2Y12 inhibitor (Clopidogrel/Ticagrelor)
• Anticoagulation: Heparin (UFH or LMWH) for all ACS patients
• High-sensitivity Troponin: Serial measurements at 0 and 3 hours (or 0/1 hour protocol)
• STEMI equivalents: New LBBB, Posterior MI, de Winter T-waves - Treat as STEMI
• Contraindications to thrombolysis: Active bleeding, recent stroke, BP >180/110, known bleeding disorder

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

Acute coronary syndrome results from:
• Atherosclerotic plaque rupture or erosion
• Platelet activation and aggregation
• Thrombus formation (partially or completely occlusive)
• STEMI: Complete occlusion → Transmural ischemia → ST elevation
• NSTEMI: Partial occlusion → Subendocardial ischemia → ST depression/T-wave changes
• Without reperfusion: Irreversible myocyte death begins within 20-40 minutes
• Wavefront of necrosis: Subendocardium → Epicardium over 4-6 hours`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• Leading cause of death worldwide - 17.9 million cardiovascular deaths annually
• ~800,000 AMI events per year in US alone
• Mortality declining in high-income countries (better treatment, prevention)
• Mortality increasing in low/middle-income countries (rising risk factors, limited access)
• In-hospital mortality: 5-6% for STEMI, 3-5% for NSTEMI

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Cardiovascular disease rising rapidly - "Epidemiologic transition"
• Often presents younger due to poorly controlled risk factors
• High prevalence of hypertension (often undiagnosed/untreated)
• Limited access to PCI - Most facilities rely on thrombolysis
• Delayed presentation common due to healthcare access barriers
• Higher mortality due to delays and limited intensive care

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

NON-MODIFIABLE:
• Age (Men >45, Women >55)
• Male sex
• Family history of premature CAD (first-degree relative: men <55, women <65)
• Ethnicity (higher risk in South Asians, African Americans)

MODIFIABLE - Target these:
• Hypertension
• Diabetes mellitus
• Dyslipidemia (high LDL, low HDL)
• Smoking
• Obesity
• Physical inactivity
• Unhealthy diet
• Excessive alcohol
• Psychosocial factors (stress, depression)

════════════════════════════════════════════════════════════════════════════════
HIGH-RISK PRESENTATIONS
════════════════════════════════════════════════════════════════════════════════

• Anterior STEMI (LAD occlusion) - Highest mortality
• Cardiogenic shock at presentation
• Cardiac arrest / VF
• Elderly patients
• Diabetics (may have atypical symptoms)
• Prior MI or heart failure`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
CLASSIC SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

CHEST PAIN - Typical features:
• Substernal/retrosternal location
• Crushing, squeezing, pressure, heaviness ("elephant on chest")
• Radiation to left arm, jaw, neck, back, epigastrium
• Duration >20 minutes (unlike angina which resolves with rest/nitrates)
• Not relieved by position change or breathing
• Associated with sense of doom

ASSOCIATED SYMPTOMS:
• Diaphoresis (sweating) - Very suggestive
• Nausea and vomiting
• Dyspnea
• Palpitations
• Dizziness or syncope
• Fatigue

════════════════════════════════════════════════════════════════════════════════
🔴 ATYPICAL PRESENTATIONS - HIGH INDEX OF SUSPICION
════════════════════════════════════════════════════════════════════════════════

More common in: Elderly, Women, Diabetics, Post-operative patients

• Dyspnea only (no chest pain) - "Anginal equivalent"
• Epigastric pain / "Indigestion"
• Isolated arm, jaw, or neck pain
• Syncope or near-syncope
• Acute confusion (elderly)
• Sudden fatigue or weakness
• Silent MI - No symptoms (diabetics)

════════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
════════════════════════════════════════════════════════════════════════════════

GENERAL:
• Anxious, diaphoretic
• Pale, cool, clammy skin
• Clutching chest (Levine sign)

VITAL SIGNS:
• Tachycardia (or bradycardia in inferior MI)
• Hypertension (pain, sympathetic) or Hypotension (cardiogenic shock)
• Tachypnea

CARDIOVASCULAR:
• S4 gallop (atrial contraction against stiff ventricle) - Common
• S3 gallop (ventricular dysfunction) - Indicates heart failure
• New murmur - Suggests mechanical complication (MR, VSD)
• Elevated JVP - Right ventricular involvement or failure

LUNG:
• Crackles/rales - Pulmonary edema from LV failure
• Clear lungs do not rule out MI

KILLIP CLASSIFICATION (Prognostic):
• Class I: No heart failure signs - Mortality ~6%
• Class II: Mild HF (S3, rales <50% lung fields) - Mortality ~17%
• Class III: Pulmonary edema - Mortality ~38%
• Class IV: Cardiogenic shock - Mortality >80% without intervention`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA FOR AMI (Universal Definition)
════════════════════════════════════════════════════════════════════════════════

ACUTE MYOCARDIAL INFARCTION requires:
1. Rise and/or fall of cardiac troponin with at least one value above 99th percentile
2. PLUS at least one of:
   • Symptoms of ischemia
   • New ischemic ECG changes
   • Development of pathological Q waves
   • Imaging evidence of new loss of viable myocardium
   • Intracoronary thrombus identified by angiography/autopsy

════════════════════════════════════════════════════════════════════════════════
ECG INTERPRETATION - GET WITHIN 10 MINUTES
════════════════════════════════════════════════════════════════════════════════

STEMI CRITERIA:
• New ST elevation at J-point in 2 contiguous leads:
  - ≥2.5 mm in V2-V3 in men <40 years
  - ≥2 mm in V2-V3 in men ≥40 years
  - ≥1.5 mm in V2-V3 in women
  - ≥1 mm in all other leads

STEMI EQUIVALENTS (Treat as STEMI):
• New or presumed new LBBB with ischemic symptoms
• Posterior MI: ST depression V1-V3, tall R waves (get V7-V9)
• de Winter T-waves: Upsloping ST depression with tall T-waves in precordial leads
• Wellens syndrome: Biphasic or deeply inverted T-waves V2-V3 (critical LAD stenosis)

NSTEMI/Unstable Angina ECG findings:
• ST depression ≥0.5 mm
• T-wave inversion ≥1 mm
• Transient ST elevation <20 min
• May be normal (does not rule out ACS)

LOCALIZATION BY ECG:
• Anterior: V1-V4 (LAD)
• Lateral: I, aVL, V5-V6 (LCx)
• Inferior: II, III, aVF (RCA, sometimes LCx)
• Right Ventricular: V4R (RCA) - Get if inferior MI
• Posterior: V7-V9 (RCA or LCx)

════════════════════════════════════════════════════════════════════════════════
CARDIAC BIOMARKERS
════════════════════════════════════════════════════════════════════════════════

HIGH-SENSITIVITY TROPONIN (hs-cTn):
• Gold standard biomarker
• Detectable within 1-3 hours of symptom onset
• Serial measurements: 0 and 3 hours (or 0/1 hour high-sensitivity protocol)
• Rise AND/OR fall pattern indicates acute injury
• Very low baseline with no rise = Rule out MI

Other biomarkers (less used now):
• CK-MB: Rises 4-6 hours, peaks 12-24 hours
• Myoglobin: Early rise but non-specific

════════════════════════════════════════════════════════════════════════════════
RISK STRATIFICATION (NSTEMI)
════════════════════════════════════════════════════════════════════════════════

GRACE Score - Predicts in-hospital and 6-month mortality:
• Age, heart rate, systolic BP
• Creatinine, Killip class
• Cardiac arrest at admission
• ST deviation, elevated troponin

TIMI Risk Score for UA/NSTEMI (0-7 points):
• Age ≥65, ≥3 CAD risk factors
• Known CAD (≥50% stenosis)
• ASA use in past 7 days
• ≥2 anginal episodes in 24h
• ST changes ≥0.5 mm
• Elevated cardiac markers`,

    differentialDiagnosis: [
      'Aortic dissection - Tearing pain radiating to back, unequal BP/pulses, wide mediastinum. 🔴 Anticoagulation contraindicated!',
      'Pulmonary embolism - Sudden dyspnea, pleuritic pain, risk factors for DVT, hypoxia, S1Q3T3',
      'Pericarditis - Sharp pleuritic pain, positional (better leaning forward), diffuse ST elevation with PR depression, friction rub',
      'Myocarditis - Recent viral illness, younger patient, diffuse ECG changes, elevated troponin',
      'Takotsubo (Stress) cardiomyopathy - Emotional/physical stress trigger, apical ballooning, often post-menopausal women',
      'Esophageal spasm/GERD - Burning quality, related to meals, relieved by antacids (but can mimic MI)',
      'Musculoskeletal pain - Reproducible with palpation, positional, no ECG changes',
      'Pneumothorax - Sudden onset, unilateral decreased breath sounds, hyperresonance',
      'Costochondritis - Reproducible chest wall tenderness',
      'Panic attack/Anxiety - Associated psychiatric symptoms, normal ECG and troponin, diagnosis of exclusion'
    ],

    investigations: [
      {
        name: '12-Lead ECG',
        indication: '🔴 WITHIN 10 MINUTES of first medical contact. Repeat every 15-30 min if initial non-diagnostic and ongoing symptoms',
        availability: 'BASIC'
      },
      {
        name: 'High-Sensitivity Troponin (hs-cTn)',
        indication: '🔴 Serial measurements at 0 and 3 hours (or 0/1h protocol). Rise/fall pattern diagnostic of acute MI',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Right-Sided ECG (V4R)',
        indication: '🔴 All inferior STEMI - Detects RV involvement which changes management (avoid nitrates, may need fluids)',
        availability: 'BASIC'
      },
      {
        name: 'Posterior ECG (V7-V9)',
        indication: 'If suspected posterior MI (ST depression V1-V3) - ST elevation in posterior leads confirms',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Baseline; anemia worsens ischemia; leukocytosis common in MI',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: 'Renal function (contrast, medications); electrolytes (arrhythmia risk)',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Profile (PT/INR, aPTT)',
        indication: 'Before anticoagulation; if thrombolysis considered',
        availability: 'BASIC'
      },
      {
        name: 'Lipid Panel',
        indication: 'Baseline for risk factor assessment and statin initiation',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'Assess for pulmonary edema, cardiomegaly, widened mediastinum (aortic dissection)',
        availability: 'BASIC'
      },
      {
        name: 'Echocardiogram',
        indication: 'Assess LV function, wall motion abnormalities, mechanical complications (VSD, MR, free wall rupture)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Coronary Angiography',
        indication: '🔴 STEMI: Immediate for primary PCI. NSTEMI: Within 24-72h based on risk',
        availability: 'ADVANCED'
      },
      {
        name: 'BNP/NT-proBNP',
        indication: 'Prognostic value; elevated levels indicate worse outcomes',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HbA1c and Fasting Glucose',
        indication: 'Screen for diabetes if not known; affects prognosis and management',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CALL FOR HELP - Activate cath lab if STEMI, call cardiology',
        '⚡ ECG within 10 MINUTES - Identify STEMI immediately',
        '⚡ IV access, continuous cardiac monitoring, pulse oximetry',
        '⚡ ASPIRIN 300mg CHEWED immediately (325mg in US) - Do not delay!',
        '⚡ OXYGEN only if SpO2 <90% or respiratory distress (routine O2 not beneficial)',
        '⚡ NITRATES: Sublingual GTN 0.4mg every 5 min x 3 (avoid if: SBP <90, RV infarct, PDE5 inhibitor use)',
        '⚡ MORPHINE 2-4mg IV if pain persists despite nitrates (use cautiously)',
        '⚡ Load P2Y12 inhibitor: Ticagrelor 180mg OR Clopidogrel 600mg (300mg if >75 years or thrombolysis)',
        '⚡ Anticoagulation: UFH or Enoxaparin (see dosing below)',
        '⚡ STEMI: Activate cath lab for Primary PCI (Door-to-Balloon <90 min) OR give Thrombolysis if PCI not available',
        '⚡ Obtain troponin, CBC, metabolic panel, coagulation studies',
        '⚡ Chest X-ray (portable, do not delay reperfusion)',
        '⚡ Beta-blocker: Start early if no contraindications (avoid if: HR <60, SBP <100, signs of HF, PR >0.24s)',
        '⚡ High-intensity statin: Atorvastatin 80mg (start immediately)',
        'Keep patient NPO (potential for cath lab)',
        'Bed rest with continuous monitoring',
        'Prepare for possible complications (arrhythmias, shock)'
      ],
      pharmacological: [
        {
          drug: 'Aspirin',
          indication: '🔴 IMMEDIATE - All ACS patients without true allergy',
          dose: '300mg (325mg US) chewed immediately, then 75-100mg daily indefinitely',
          route: 'Oral (chewed for rapid absorption)',
          duration: 'Lifelong',
          contraindications: ['True aspirin allergy (not GI intolerance)', 'Active major bleeding'],
          sideEffects: ['GI bleeding', 'Dyspepsia', 'Allergic reactions (rare)'],
          alternatives: ['If true allergy: Clopidogrel alone; consider desensitization']
        },
        {
          drug: 'Ticagrelor',
          indication: '🔴 P2Y12 inhibitor - Preferred for most ACS patients',
          dose: 'Loading: 180mg. Maintenance: 90mg twice daily',
          route: 'Oral',
          duration: '12 months post-ACS',
          contraindications: ['History of intracranial hemorrhage', 'Active bleeding', 'Severe hepatic impairment', 'Use with strong CYP3A4 inhibitors'],
          sideEffects: ['Dyspnea (common, usually transient)', 'Bleeding', 'Bradycardia', 'Ventricular pauses'],
          alternatives: ['Clopidogrel 600mg load if ticagrelor unavailable or contraindicated']
        },
        {
          drug: 'Clopidogrel',
          indication: 'P2Y12 inhibitor - Alternative to ticagrelor, especially if thrombolysis given',
          dose: 'Loading: 600mg (300mg if >75 years or with thrombolysis). Maintenance: 75mg daily',
          route: 'Oral',
          duration: '12 months post-ACS',
          contraindications: ['Active bleeding', 'Hypersensitivity'],
          sideEffects: ['Bleeding', 'Rash', 'Diarrhea', 'TTP (rare)'],
          alternatives: ['Ticagrelor', 'Prasugrel (for PCI patients not at high bleeding risk)']
        },
        {
          drug: 'Enoxaparin (LMWH)',
          indication: '🔴 Anticoagulation for ACS - Often preferred over UFH',
          dose: '1 mg/kg SC every 12 hours (reduce to 1 mg/kg daily if CrCl <30). If PCI: Additional IV dose if last dose >8h ago',
          route: 'Subcutaneous',
          duration: 'Until revascularization or hospital discharge (typically 2-8 days)',
          contraindications: ['Active major bleeding', 'HIT history', 'Severe renal impairment (use UFH)'],
          sideEffects: ['Bleeding', 'Thrombocytopenia', 'Injection site hematoma'],
          alternatives: ['UFH', 'Fondaparinux 2.5mg SC daily']
        },
        {
          drug: 'Unfractionated Heparin (UFH)',
          indication: 'Anticoagulation, especially if PCI planned or renal impairment',
          dose: 'Bolus: 60 U/kg (max 4000 U). Infusion: 12 U/kg/hr (max 1000 U/hr). Target aPTT 50-70 sec',
          route: 'IV bolus then infusion',
          duration: 'Until PCI or 48 hours',
          contraindications: ['Active bleeding', 'HIT', 'Severe thrombocytopenia'],
          sideEffects: ['Bleeding', 'HIT', 'Osteoporosis (long-term)'],
          alternatives: ['Enoxaparin', 'Bivalirudin']
        },
        {
          drug: 'Tenecteplase (Thrombolytic)',
          indication: '🔴 STEMI when PCI not available within 120 min - Give within 30 min of arrival',
          dose: 'Weight-based: <60kg: 30mg; 60-70kg: 35mg; 70-80kg: 40mg; 80-90kg: 45mg; >90kg: 50mg. Single IV bolus over 5-10 seconds',
          route: 'IV bolus',
          duration: 'Single dose; assess for reperfusion at 60-90 min',
          contraindications: ['🔴 Absolute: Prior ICH, known cerebral vascular lesion, ischemic stroke <3mo, active bleeding, suspected aortic dissection, significant head trauma <3mo', '🟠 Relative: BP >180/110, current anticoagulation, recent major surgery, prolonged CPR'],
          sideEffects: ['🔴 Intracranial hemorrhage (0.5-1%)', 'Major bleeding', 'Reperfusion arrhythmias', 'Allergic reaction'],
          alternatives: ['Alteplase (tPA)', 'Streptokinase', 'Reteplase']
        },
        {
          drug: 'Nitroglycerin (GTN)',
          indication: 'Chest pain relief, reduce preload',
          dose: 'Sublingual: 0.4mg every 5 min x 3. IV: Start 5-10 mcg/min, titrate to pain relief (max 200 mcg/min)',
          route: 'Sublingual or IV infusion',
          duration: 'Until pain controlled; usually 24-48 hours IV',
          contraindications: ['🔴 SBP <90 mmHg', '🔴 RV infarction (causes severe hypotension)', '🔴 PDE5 inhibitor use within 24-48h (sildenafil, tadalafil)'],
          sideEffects: ['Hypotension', 'Headache', 'Reflex tachycardia', 'Tolerance with prolonged use'],
          alternatives: ['Morphine for pain if nitrates contraindicated']
        },
        {
          drug: 'Morphine Sulfate',
          indication: '🟡 Persistent pain despite nitrates. Use cautiously - may increase mortality',
          dose: '2-4 mg IV every 5-15 min as needed',
          route: 'IV',
          duration: 'PRN for pain',
          contraindications: ['Hypotension', 'Respiratory depression', 'Altered mental status'],
          sideEffects: ['Hypotension', 'Respiratory depression', 'Nausea/vomiting', 'Bradycardia'],
          alternatives: ['Adequate nitrates and anxiolytics; morphine is no longer first-line']
        },
        {
          drug: 'Metoprolol (Beta-blocker)',
          indication: 'Early in all ACS unless contraindicated - Reduces mortality, arrhythmias',
          dose: 'Oral: 25-50mg every 6-12 hours, titrate to HR 50-60. IV: 5mg every 5 min x 3 (if needed for tachycardia/HTN)',
          route: 'Oral preferred; IV if needed',
          duration: 'Continue indefinitely (secondary prevention)',
          contraindications: ['🔴 HR <60', '🔴 SBP <100', '🔴 Signs of heart failure/shock', '🔴 PR interval >0.24s', 'Active bronchospasm', 'Cocaine use (relative)'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue', 'Bronchospasm'],
          alternatives: ['Bisoprolol', 'Carvedilol']
        },
        {
          drug: 'Atorvastatin (High-Intensity Statin)',
          indication: '🔴 All ACS patients - Start immediately regardless of baseline LDL',
          dose: '80 mg once daily',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Active liver disease', 'Pregnancy'],
          sideEffects: ['Myalgias', 'Elevated LFTs', 'Rarely rhabdomyolysis'],
          alternatives: ['Rosuvastatin 20-40mg if atorvastatin not tolerated']
        },
        {
          drug: 'ACE Inhibitor (Ramipril/Lisinopril)',
          indication: 'Start within 24 hours if hemodynamically stable - Especially if anterior MI, HF, EF <40%, diabetes',
          dose: 'Ramipril: Start 2.5mg, titrate to 10mg daily. Lisinopril: Start 2.5-5mg, titrate to 10-20mg daily',
          route: 'Oral',
          duration: 'Lifelong (secondary prevention)',
          contraindications: ['SBP <100', 'Bilateral renal artery stenosis', 'Angioedema history', 'Pregnancy', 'K+ >5.5'],
          sideEffects: ['Cough', 'Hyperkalemia', 'Hypotension', 'Angioedema (rare)', 'AKI'],
          alternatives: ['ARB (valsartan, losartan) if ACE-I intolerant due to cough']
        }
      ],
      nonPharmacological: [
        'Continuous cardiac monitoring - Arrhythmia detection',
        'Bed rest initially, then early mobilization if stable',
        'NPO until cath lab decision made',
        'Strict intake/output monitoring',
        'Daily weight (fluid status)',
        'Cardiac rehabilitation referral',
        'Smoking cessation counseling',
        'Dietary counseling (low sodium, heart-healthy diet)',
        'Diabetes and blood pressure optimization',
        'Psychosocial support - Depression common post-MI'
      ],
      surgical: [
        'Primary PCI - Gold standard for STEMI (Door-to-Balloon <90 min)',
        'Rescue PCI - If thrombolysis fails (persistent ST elevation at 60-90 min)',
        'CABG - If multivessel disease not amenable to PCI, or left main disease',
        'Mechanical complication repair - VSD, papillary muscle rupture, free wall rupture (emergent)',
        'IABP (Intra-aortic balloon pump) - Cardiogenic shock, bridge to intervention',
        'LV assist device or ECMO - Refractory cardiogenic shock',
        'Implantable defibrillator (ICD) - If EF ≤35% at 40 days post-MI'
      ],
      referralCriteria: [
        '🔴 STEMI → Immediate cath lab activation or thrombolysis + transfer',
        '🔴 NSTEMI high-risk → Cardiology consult, invasive strategy within 24h',
        '🔴 Cardiogenic shock → ICU, consider mechanical support',
        '🔴 Mechanical complications → Urgent cardiac surgery',
        '🔴 Cardiac arrest survivor → ICU, cardiology, consider hypothermia protocol',
        'All MI patients should be seen by cardiology before discharge',
        'Transfer to PCI-capable center if: STEMI and no PCI available within 120 min'
      ],
      monitoring: [
        'Continuous ECG monitoring for at least 24-48 hours (arrhythmia detection)',
        'Vital signs: Every 15 min initially, then every 1-4 hours when stable',
        'Telemetry for duration of hospitalization',
        'Serial ECGs: At 60-90 min post-reperfusion, daily, and with any symptom recurrence',
        'Serial troponins: Every 6-8 hours until peak (defines infarct size)',
        'Daily electrolytes (potassium, magnesium - arrhythmia prevention)',
        'Renal function (contrast nephropathy, medication dosing)',
        'Hemoglobin if bleeding concerns or on anticoagulation',
        'Echocardiogram before discharge (assess EF and complications)',
        'Clinical assessment for heart failure, recurrent ischemia'
      ],
      followUp: `BEFORE DISCHARGE:
• Echocardiogram to assess LV function
• Risk factor assessment and optimization
• Complete medication regimen established
• Cardiac rehabilitation referral

DISCHARGE MEDICATIONS (ABCDE):
• A - Antiplatelet therapy (DAPT: Aspirin + P2Y12 inhibitor x 12 months)
• B - Beta-blocker
• C - Cholesterol (high-intensity statin)
• D - Diabetes management / ACE inhibitor or ARB
• E - Exercise (cardiac rehab) / Education

FOLLOW-UP SCHEDULE:
• Cardiology: 1-2 weeks post-discharge
• Primary care: Within 1 week
• Cardiac rehabilitation: Start within 2-4 weeks

LONG-TERM MONITORING:
• Lipid panel: 4-12 weeks post-MI (assess statin response)
• LV function reassessment at 6-12 weeks (especially if EF reduced)
• Consider ICD if EF ≤35% at 40 days post-MI
• Annual cardiology follow-up
• Lifelong risk factor management`
    },

    redFlags: [
      '🔴 CRITICAL: ST elevation on ECG - STEMI, activate cath lab or give thrombolysis immediately',
      '🔴 CRITICAL: Cardiogenic shock (SBP <90, cold/clammy, altered mental status) - ICU, inotropes, IABP, urgent revascularization',
      '🔴 CRITICAL: Ventricular fibrillation or sustained VT - Defibrillate immediately, follow ACLS',
      '🔴 CRITICAL: New systolic murmur (VSD or papillary muscle rupture) - Echo immediately, urgent surgical consult',
      '🔴 CRITICAL: Sudden hemodynamic collapse - Consider free wall rupture, pericardiocentesis if tamponade, emergent surgery',
      '🔴 CRITICAL: Complete heart block or symptomatic bradycardia - Temporary pacing, especially in inferior MI',
      '🟠 URGENT: Ongoing chest pain despite treatment - Consider failed reperfusion, recurrent ischemia, mechanical complication',
      '🟠 URGENT: Hypotension with clear lungs in inferior MI - Suspect RV infarction, give fluids, avoid nitrates',
      '🟠 URGENT: Pulmonary edema (Killip III) - Diuretics, consider NIV, assess for mechanical complication',
      '🟠 URGENT: Recurrent ST elevation after reperfusion - Stent thrombosis, urgent re-catheterization',
      '🟠 URGENT: Rising troponin with new ECG changes - Ongoing or recurrent infarction',
      '🟠 URGENT: Pericarditis within days of MI (friction rub, diffuse ST changes) - Consider Dressler syndrome vs free wall rupture'
    ],

    complications: [
      'Cardiogenic shock - Pump failure, mortality >50% without intervention; requires inotropes, IABP, urgent revascularization',
      'Ventricular arrhythmias (VT/VF) - Most common cause of pre-hospital death; defibrillation, amiodarone',
      'Heart block - Especially inferior MI (AV nodal artery); usually transient; may need temporary pacing',
      'Acute mitral regurgitation - Papillary muscle dysfunction or rupture; new murmur, pulmonary edema; may need urgent surgery',
      'Ventricular septal defect (VSD) - New harsh systolic murmur, step-up in O2 saturation; surgical emergency',
      'Free wall rupture - Sudden hemodynamic collapse, PEA; usually fatal; emergency surgery if diagnosed',
      'Pericarditis - Early (within days) or late (Dressler syndrome at 2-10 weeks); friction rub, diffuse ST elevation',
      'LV thrombus - Especially anterior MI with akinesis; anticoagulation required',
      'Heart failure - Acute or chronic; related to infarct size; optimize medical therapy',
      'LV aneurysm - Late complication; persistent ST elevation; may need surgical repair',
      'Post-MI angina - Recurrent ischemia; may need repeat revascularization',
      'Depression and anxiety - Very common; screen and treat'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY
════════════════════════════════════════════════════════════════════════════════

IN-HOSPITAL MORTALITY (with modern treatment):
• STEMI with primary PCI: 3-5%
• STEMI with thrombolysis: 6-8%
• NSTEMI: 3-5%
• Cardiogenic shock: 40-50% (improved with early revascularization)

PRE-HOSPITAL MORTALITY:
• ~30% of STEMI patients die before reaching hospital
• Most from ventricular fibrillation
• Emphasizes importance of early CPR and defibrillation

════════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
════════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Advanced age
• Anterior MI
• Low ejection fraction (<40%)
• Cardiogenic shock
• Diabetes mellitus
• Renal dysfunction
• Delayed reperfusion
• Prior MI
• Multivessel disease

GOOD PROGNOSIS:
• Young age
• Preserved EF
• Early reperfusion
• Single vessel disease
• Complete revascularization
• Adherence to secondary prevention

════════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
════════════════════════════════════════════════════════════════════════════════

• 1-year mortality: 10-15%
• Recurrent MI: 5-10% per year without secondary prevention
• Heart failure development related to infarct size
• Quality of life generally good with modern therapy
• Cardiac rehabilitation improves outcomes 20-30%
• Adherence to medications reduces recurrent events by 50%`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION (Before First MI)
════════════════════════════════════════════════════════════════════════════════

RISK FACTOR MODIFICATION:
• Blood pressure control: Target <130/80 mmHg
• Lipid management: Statin therapy based on CV risk score
• Diabetes control: HbA1c <7% (individualized)
• Smoking cessation: Single most impactful intervention
• Weight management: BMI <25
• Physical activity: 150 min/week moderate exercise
• Healthy diet: Mediterranean, DASH
• Limit alcohol

MEDICATIONS FOR HIGH-RISK PATIENTS:
• Aspirin: Controversial for primary prevention; individualized
• Statins: Based on CV risk calculation
• ACE inhibitors: If hypertension, diabetes, CKD
• Antihypertensives: Multiple classes as needed

════════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After MI)
════════════════════════════════════════════════════════════════════════════════

MANDATORY MEDICATIONS (unless contraindicated):
• Dual antiplatelet therapy (DAPT) x 12 months
• High-intensity statin
• Beta-blocker
• ACE inhibitor or ARB

LIFESTYLE:
• Cardiac rehabilitation (reduces mortality 20-30%)
• Smoking cessation
• Heart-healthy diet
• Regular exercise (as guided by rehab)
• Weight management

MONITORING:
• Regular follow-up with cardiology
• Lipid panel at 4-12 weeks
• LV function reassessment
• Screen for depression`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS A HEART ATTACK?
════════════════════════════════════════════════════════════════════════════════

A heart attack happens when blood flow to part of your heart muscle is blocked, usually by a blood clot. Without blood flow, that part of the heart starts to die. The faster blood flow is restored, the more heart muscle can be saved.

"TIME IS MUSCLE" - Every minute counts!

════════════════════════════════════════════════════════════════════════════════
🔴 RECOGNIZE WARNING SIGNS - CALL FOR EMERGENCY HELP
════════════════════════════════════════════════════════════════════════════════

• Chest pain or discomfort (pressure, squeezing, fullness)
• Pain spreading to shoulders, neck, jaw, arms, or back
• Shortness of breath
• Cold sweat
• Nausea or vomiting
• Feeling lightheaded or dizzy
• Unusual fatigue

Women and diabetics may have unusual symptoms:
• Indigestion, heartburn
• Extreme tiredness
• Back or jaw pain without chest pain

🔴 DON'T WAIT - Call emergency services immediately
• Don't drive yourself to the hospital
• Chew an aspirin if you have one and aren't allergic
• Time saved = Heart muscle saved

════════════════════════════════════════════════════════════════════════════════
AFTER YOUR HEART ATTACK
════════════════════════════════════════════════════════════════════════════════

TAKE YOUR MEDICATIONS:
• Aspirin - Prevents blood clots
• Second blood thinner (clopidogrel/ticagrelor) - Very important, don't stop!
• Beta-blocker - Protects your heart
• Statin - Lowers cholesterol and stabilizes plaques
• ACE inhibitor - Helps heart heal and prevents remodeling

LIFESTYLE CHANGES:
• Stop smoking - Most important thing you can do
• Heart-healthy diet (less salt, saturated fat; more fruits, vegetables)
• Exercise - As advised by your cardiac rehab team
• Maintain healthy weight
• Limit alcohol
• Manage stress

ATTEND CARDIAC REHABILITATION:
• Structured exercise program
• Education about your heart
• Support and encouragement
• Reduces risk of another heart attack

KNOW WHEN TO SEEK HELP:
• Return of chest pain or pressure
• New or worsening shortness of breath
• Swelling in legs or feet
• Feeling faint or dizzy
• Very fast or irregular heartbeat

FOLLOW-UP APPOINTMENTS:
• Keep all appointments with your cardiologist
• Get regular blood tests as advised
• Know your numbers: Blood pressure, cholesterol, blood sugar`,

    references: [
      'Ibanez B, James S, Agewall S, et al. 2017 ESC Guidelines for the management of acute myocardial infarction in patients presenting with ST-segment elevation. Eur Heart J. 2018;39(2):119-177.',
      'Collet JP, Thiele H, Barbato E, et al. 2020 ESC Guidelines for the management of acute coronary syndromes in patients presenting without persistent ST-segment elevation. Eur Heart J. 2021;42(14):1289-1367.',
      'Amsterdam EA, Wenger NK, Brindis RG, et al. 2014 AHA/ACC Guideline for the Management of Patients With Non-ST-Elevation Acute Coronary Syndromes. Circulation. 2014;130(25):e344-e426.',
      'O\'Gara PT, Kushner FG, Ascheim DD, et al. 2013 ACCF/AHA Guideline for the Management of ST-Elevation Myocardial Infarction. Circulation. 2013;127(4):e362-e425.',
      'Thygesen K, Alpert JS, Jaffe AS, et al. Fourth Universal Definition of Myocardial Infarction (2018). Circulation. 2018;138(20):e618-e651.',
      'Lawton JS, Tamis-Holland JE, Bangalore S, et al. 2021 ACC/AHA/SCAI Guideline for Coronary Artery Revascularization. Circulation. 2022;145(3):e24-e227.',
      'Mehta SR, Wood DA, Storey RF, et al. Complete Revascularization with Multivessel PCI for Myocardial Infarction. N Engl J Med. 2019;381(15):1411-1421.',
      'GUSTO Investigators. An international randomized trial comparing four thrombolytic strategies for acute myocardial infarction. N Engl J Med. 1993;329(10):673-682.',
      'Wallentin L, Becker RC, Budaj A, et al. Ticagrelor versus clopidogrel in patients with acute coronary syndromes. N Engl J Med. 2009;361(11):1045-1057.',
      'World Health Organization. Cardiovascular diseases (CVDs) Fact Sheet. WHO; 2021.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 8. STROKE (ISCHEMIC & HEMORRHAGIC)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '8',
    name: 'Stroke (Ischemic & Hemorrhagic)',
    slug: 'stroke-ischemic-hemorrhagic',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'I63.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Stroke is an acute neurological deficit due to vascular injury to the brain. Ischemic stroke (85%) results from arterial occlusion. Hemorrhagic stroke (15%) results from vessel rupture. TIME IS BRAIN - Rapid diagnosis and treatment are critical.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• "TIME IS BRAIN" - 1.9 million neurons die every minute during stroke
• FAST screening: Face drooping, Arm weakness, Speech difficulty, Time to call emergency
• CT HEAD immediately - Rule out hemorrhage BEFORE any treatment
• IV tPA (Alteplase) window: Within 4.5 HOURS of symptom onset (3 hours if certain exclusions)
• Mechanical thrombectomy: Up to 24 HOURS for large vessel occlusion (if criteria met)
• BP management differs: Ischemic (permissive HTN unless tPA) vs Hemorrhagic (lower aggressively)
• NEVER give anticoagulants or antiplatelets until hemorrhage ruled out by imaging
• Aspirin within 24-48h of ischemic stroke (after hemorrhage excluded, and if no tPA in past 24h)
• Stroke mimics are common - Hypoglycemia, seizure, migraine, conversion disorder
• Dysphagia screen before any oral intake (aspiration risk)

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

ISCHEMIC STROKE (85%):
• Thrombotic: Atherosclerosis of large/small vessels
• Embolic: Cardiac source (AF, valve disease), artery-to-artery embolism
• Lacunar: Small vessel disease from hypertension/diabetes
• Core infarct surrounded by salvageable penumbra
• Penumbra can be saved with timely reperfusion

HEMORRHAGIC STROKE (15%):
• Intracerebral hemorrhage (ICH): Hypertensive, amyloid angiopathy, AVM
• Subarachnoid hemorrhage (SAH): Aneurysm rupture (most common), trauma
• Mass effect and raised intracranial pressure
• Secondary injury from edema and inflammation`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• 2nd leading cause of death worldwide (after heart disease)
• 3rd leading cause of disability
• 13.7 million new strokes per year globally
• 5.5 million deaths annually
• 80% of strokes are preventable through risk factor control

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Stroke incidence rising rapidly
• Higher proportion of hemorrhagic stroke vs high-income countries (due to untreated HTN)
• Younger age at stroke onset (mean age 15 years younger than Western populations)
• High burden of uncontrolled hypertension - Major driver
• Limited access to thrombolysis and thrombectomy
• Higher case fatality rates due to delayed presentation and limited ICU access
• Rheumatic heart disease and AF contribute to cardioembolic stroke

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

NON-MODIFIABLE:
• Age (risk doubles each decade after 55)
• Male sex (slightly higher risk)
• Family history of stroke
• Race/ethnicity (higher in Black, Hispanic populations)
• Prior stroke or TIA

MODIFIABLE - Address these aggressively:
• 🔴 Hypertension - Single most important modifiable risk factor
• Atrial fibrillation - 5-fold increased risk
• Diabetes mellitus
• Dyslipidemia
• Smoking
• Obesity
• Physical inactivity
• Excessive alcohol
• Carotid stenosis
• Heart disease (MI, heart failure, valvular disease)
• Sleep apnea
• Sickle cell disease`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
FAST SCREENING - TEACH TO PUBLIC
════════════════════════════════════════════════════════════════════════════════

F - FACE: Ask to smile. Does one side droop?
A - ARMS: Ask to raise both arms. Does one drift down?
S - SPEECH: Ask to repeat a sentence. Is speech slurred or strange?
T - TIME: If any are positive, call emergency immediately!

════════════════════════════════════════════════════════════════════════════════
ISCHEMIC STROKE - BY VASCULAR TERRITORY
════════════════════════════════════════════════════════════════════════════════

ANTERIOR CIRCULATION (Carotid territory):

Middle Cerebral Artery (MCA) - Most common:
• Contralateral hemiparesis (face and arm > leg)
• Contralateral hemisensory loss
• Homonymous hemianopia
• Dominant hemisphere: Aphasia (expressive/receptive/global)
• Non-dominant hemisphere: Neglect, anosognosia
• Gaze deviation toward lesion

Anterior Cerebral Artery (ACA):
• Contralateral leg weakness > arm
• Personality changes, abulia
• Urinary incontinence

POSTERIOR CIRCULATION (Vertebrobasilar territory):

Posterior Cerebral Artery (PCA):
• Homonymous hemianopia with macular sparing
• Visual agnosia
• Memory impairment

Basilar Artery:
• 🔴 "Locked-in syndrome" - Quadriplegia, anarthria, preserved consciousness
• Cranial nerve palsies
• Vertigo, nystagmus, ataxia
• Coma if complete occlusion

Cerebellar stroke:
• Ataxia, vertigo, nystagmus
• Headache, vomiting
• 🔴 Can herniate rapidly - May need urgent decompression

LACUNAR SYNDROMES (Small vessel):
• Pure motor hemiparesis
• Pure sensory stroke
• Ataxic hemiparesis
• Dysarthria-clumsy hand

════════════════════════════════════════════════════════════════════════════════
HEMORRHAGIC STROKE
════════════════════════════════════════════════════════════════════════════════

INTRACEREBRAL HEMORRHAGE (ICH):
• Sudden severe headache
• Focal neurological deficits (depend on location)
• Rapid deterioration common
• Nausea/vomiting from raised ICP
• Decreased consciousness
• Seizures more common than ischemic stroke

SUBARACHNOID HEMORRHAGE (SAH):
• 🔴 "Thunderclap headache" - Worst headache of life, sudden onset
• Neck stiffness (meningism)
• Photophobia
• Nausea/vomiting
• Loss of consciousness at onset in 50%
• May have focal deficits from associated ICH
• Sentinel headache in 20-50% (warning leak)

════════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
════════════════════════════════════════════════════════════════════════════════

• Blood pressure (often markedly elevated)
• Level of consciousness (GCS)
• Pupil size and reactivity
• Cranial nerve examination
• Motor strength (face, arm, leg)
• Sensory examination
• Coordination and gait (if safe)
• Speech and language assessment
• Visual field testing
• Neck stiffness (SAH)
• Cardiac examination (AF, murmurs)
• Carotid bruits`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
IMMEDIATE DIAGNOSTIC PRIORITIES
════════════════════════════════════════════════════════════════════════════════

1. Is this a stroke? (vs mimic)
2. Is it ischemic or hemorrhagic? (CT will tell)
3. When did symptoms start? (Determines treatment eligibility)
4. Is the patient a candidate for thrombolysis/thrombectomy?

════════════════════════════════════════════════════════════════════════════════
NIHSS - National Institutes of Health Stroke Scale
════════════════════════════════════════════════════════════════════════════════

Standardized assessment of stroke severity (0-42 points):
• Level of consciousness
• Gaze
• Visual fields
• Facial palsy
• Motor arm/leg
• Limb ataxia
• Sensory
• Language
• Dysarthria
• Extinction/inattention

NIHSS Score Interpretation:
• 0-4: Minor stroke
• 5-15: Moderate stroke
• 16-20: Moderate-severe stroke
• 21-42: Severe stroke

Higher NIHSS predicts worse outcomes but also more benefit from intervention

════════════════════════════════════════════════════════════════════════════════
IMAGING
════════════════════════════════════════════════════════════════════════════════

NON-CONTRAST CT HEAD (First-line):
• 🔴 IMMEDIATE - Do not delay
• Primary purpose: RULE OUT HEMORRHAGE
• Ischemic stroke may not show on CT for 6-12 hours
• Hemorrhage appears bright (hyperdense) immediately
• Look for: Hyperdense vessel sign (clot), early ischemic changes

CT ANGIOGRAPHY (CTA):
• Identifies large vessel occlusion (LVO) for thrombectomy
• Images carotid and intracranial vessels
• Should be done if thrombectomy being considered

CT PERFUSION:
• Shows core infarct vs salvageable penumbra
• Helps select patients for late-window thrombectomy

MRI (DWI/PWI):
• More sensitive for early ischemic changes
• DWI positive within minutes
• Useful when CT negative but stroke suspected
• May not be available emergently

FOR SUBARACHNOID HEMORRHAGE:
• Non-contrast CT: Sensitive in first 6 hours (>95%), decreases over time
• Lumbar puncture: If CT negative but SAH suspected (xanthochromia)
• CTA or conventional angiography: Identify aneurysm

════════════════════════════════════════════════════════════════════════════════
STROKE MIMICS - ALWAYS CONSIDER
════════════════════════════════════════════════════════════════════════════════

• Hypoglycemia (check glucose immediately!)
• Seizure with Todd's paralysis
• Complicated migraine
• Conversion disorder/functional neurological disorder
• Brain tumor
• Subdural hematoma
• Encephalitis
• Hypertensive encephalopathy
• Drug toxicity`,

    differentialDiagnosis: [
      'Hypoglycemia - Check glucose immediately! Focal deficits can mimic stroke exactly',
      'Seizure with Todd paralysis - Post-ictal weakness, history of seizure, resolves over hours',
      'Complicated migraine - Headache, visual aura, younger patient, history of migraines, fully reversible',
      'Brain tumor - More gradual onset, may have seizures, mass effect on imaging',
      'Subdural hematoma - History of falls/trauma (may be minor), elderly, anticoagulant use',
      'Hypertensive encephalopathy - Severely elevated BP, encephalopathy, often reversible with BP control',
      'Functional neurological disorder - Non-anatomical deficits, inconsistent exam, Hoover sign',
      'Encephalitis/Meningitis - Fever, altered mental status, CSF abnormalities',
      'Multiple sclerosis - Younger patient, prior episodes, white matter lesions on MRI',
      'Wernicke encephalopathy - Alcoholism, ataxia, ophthalmoplegia, confusion, give thiamine'
    ],

    investigations: [
      {
        name: 'Non-Contrast CT Head',
        indication: '🔴 IMMEDIATE - Rule out hemorrhage before any treatment. Door-to-CT <25 min',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE - Hypoglycemia is a stroke mimic. Must check before tPA',
        availability: 'BASIC'
      },
      {
        name: 'CT Angiography (CTA)',
        indication: '🔴 If thrombectomy candidate - Identifies large vessel occlusion. Do alongside or after CT head',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Perfusion',
        indication: 'Late window (6-24h) thrombectomy evaluation - Shows salvageable penumbra',
        availability: 'ADVANCED'
      },
      {
        name: 'MRI Brain (DWI)',
        indication: 'If CT negative but stroke suspected; more sensitive for small/early infarcts',
        availability: 'ADVANCED'
      },
      {
        name: 'ECG',
        indication: '🔴 All stroke patients - Detect atrial fibrillation (cardioembolic source)',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Anemia, thrombocytopenia (bleeding risk), infection',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation (PT/INR, aPTT)',
        indication: '🔴 Before tPA - Contraindicated if INR >1.7 or on anticoagulants',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: 'Electrolytes, renal function, glucose',
        availability: 'BASIC'
      },
      {
        name: 'Troponin',
        indication: 'Stroke can cause cardiac injury; also rules out concurrent MI',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lipid Panel',
        indication: 'Risk factor assessment for secondary prevention',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: 'Screen for diabetes - Major risk factor',
        availability: 'BASIC'
      },
      {
        name: 'Echocardiogram',
        indication: 'Cardioembolic source evaluation - Especially if young, no risk factors, or AF',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Carotid Doppler Ultrasound',
        indication: 'Anterior circulation stroke - Assess for carotid stenosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture',
        indication: 'If SAH suspected but CT negative - Look for xanthochromia (do >6-12h after onset)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Holter Monitor / Prolonged Cardiac Monitoring',
        indication: 'Detect paroxysmal AF - Especially cryptogenic stroke',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ABCs - Airway, Breathing, Circulation. Intubate if GCS ≤8 or airway compromise',
        '⚡ CHECK GLUCOSE immediately - Treat hypoglycemia, it mimics stroke',
        '⚡ CT HEAD within 25 minutes of arrival - Rule out hemorrhage',
        '⚡ Establish IV access (two large bore)',
        '⚡ Continuous cardiac monitoring (arrhythmia detection)',
        '⚡ Calculate NIHSS score',
        '⚡ Determine time of symptom onset (or "last known well")',
        '⚡ ISCHEMIC: If eligible, give IV tPA within 4.5 hours (see criteria)',
        '⚡ ISCHEMIC with LVO: Activate thrombectomy team if available (up to 24h in select patients)',
        '⚡ HEMORRHAGIC: Reverse anticoagulation, control BP, neurosurgery consult',
        '⚡ BP management: Permissive HTN for ischemic (unless tPA); Lower for hemorrhagic',
        '⚡ NPO until swallow screen (high aspiration risk)',
        '⚡ Oxygen only if SpO2 <94%',
        '⚡ Avoid hyperthermia - Treat fever aggressively',
        'Position head of bed 0-15° (flat if hypotensive; elevate if raised ICP suspected)',
        'DVT prophylaxis (SCDs initially; anticoagulation when stable)',
        'Blood pressure targets (see below)',
        'Admit to stroke unit if available'
      ],
      pharmacological: [
        {
          drug: 'Alteplase (tPA) - IV Thrombolysis',
          indication: '🔴 ISCHEMIC STROKE within 4.5 hours of onset (3h if certain exclusions). CT must exclude hemorrhage',
          dose: '0.9 mg/kg IV (max 90 mg). 10% as bolus over 1 min, remaining 90% infusion over 60 min',
          route: 'IV',
          duration: 'Single treatment',
          contraindications: ['🔴 Hemorrhage on CT', '🔴 BP >185/110 despite treatment', '🔴 INR >1.7 or on anticoagulants', '🔴 Platelets <100,000', '🔴 Recent major surgery/trauma <14 days', '🔴 History of ICH', '🔴 Active bleeding', '🔴 Stroke or head trauma <3 months', 'Glucose <50 or >400'],
          sideEffects: ['🔴 Symptomatic ICH (6%)', 'Angioedema (especially with ACE-I)', 'Systemic bleeding'],
          alternatives: ['Tenecteplase (single bolus, may be preferred in some settings)', 'Mechanical thrombectomy']
        },
        {
          drug: 'Tenecteplase',
          indication: 'Alternative to alteplase - Single IV bolus, easier to administer',
          dose: '0.25 mg/kg IV bolus (max 25 mg)',
          route: 'IV bolus over 5 seconds',
          duration: 'Single dose',
          contraindications: ['Same as alteplase'],
          sideEffects: ['Same as alteplase - ICH risk similar'],
          alternatives: ['Alteplase']
        },
        {
          drug: 'Aspirin',
          indication: '🔴 All ischemic stroke within 24-48h (but NOT within 24h of tPA)',
          dose: '160-325 mg loading dose, then 81-100 mg daily',
          route: 'Oral (or rectal if NPO and no IV form)',
          duration: 'Lifelong for secondary prevention',
          contraindications: ['Hemorrhagic stroke', 'True aspirin allergy', 'Within 24h of tPA'],
          sideEffects: ['GI bleeding', 'Allergic reaction'],
          alternatives: ['Clopidogrel 75mg daily if aspirin-allergic']
        },
        {
          drug: 'Clopidogrel',
          indication: 'Dual antiplatelet for high-risk TIA/minor stroke (NIHSS ≤3) for 21 days',
          dose: 'Loading: 300mg. Maintenance: 75mg daily',
          route: 'Oral',
          duration: '21 days DAPT, then single antiplatelet long-term',
          contraindications: ['Hemorrhagic stroke', 'Active bleeding'],
          sideEffects: ['Bleeding', 'Rash', 'TTP (rare)'],
          alternatives: ['Ticagrelor 90mg twice daily (with aspirin) per THALES trial']
        },
        {
          drug: 'Heparin (DVT Prophylaxis)',
          indication: 'All stroke patients - High VTE risk. Start 24-48h after onset (after hemorrhage excluded)',
          dose: 'Enoxaparin 40mg SC daily OR UFH 5000 units SC every 8-12 hours',
          route: 'Subcutaneous',
          duration: 'Until mobile',
          contraindications: ['Active hemorrhage', 'Hemorrhagic stroke (use SCDs only initially)', 'Very large infarct with hemorrhagic transformation risk'],
          sideEffects: ['Bleeding', 'HIT'],
          alternatives: ['Sequential compression devices (SCDs) until anticoagulation safe']
        },
        {
          drug: 'Labetalol (BP Control - Ischemic if tPA candidate)',
          indication: 'Reduce BP to <185/110 before tPA; maintain <180/105 for 24h after tPA',
          dose: '10-20 mg IV over 1-2 min, may repeat every 10-20 min (max 300 mg). Or infusion 2-8 mg/min',
          route: 'IV',
          duration: 'Until BP controlled, then transition to oral',
          contraindications: ['Asthma/severe COPD', 'Bradycardia', 'Heart block', 'Cardiogenic shock'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Bronchospasm'],
          alternatives: ['Nicardipine 5 mg/hr IV, titrate by 2.5 mg/hr every 5-15 min (max 15 mg/hr)']
        },
        {
          drug: 'Nicardipine (BP Control)',
          indication: 'Alternative IV antihypertensive - Especially if labetalol contraindicated',
          dose: 'Start 5 mg/hr IV, increase by 2.5 mg/hr every 5-15 min (max 15 mg/hr)',
          route: 'IV infusion',
          duration: 'Until BP controlled',
          contraindications: ['Severe aortic stenosis'],
          sideEffects: ['Hypotension', 'Headache', 'Reflex tachycardia'],
          alternatives: ['Labetalol', 'Clevidipine', 'Hydralazine']
        },
        {
          drug: 'Anticoagulation for AF (Warfarin or DOAC)',
          indication: 'Cardioembolic stroke with AF - Start 4-14 days after stroke depending on size/hemorrhagic risk',
          dose: 'Apixaban 5mg BID, Rivaroxaban 20mg daily, or Warfarin (target INR 2-3)',
          route: 'Oral',
          duration: 'Lifelong (CHA2DS2-VASc determines need)',
          contraindications: ['Active bleeding', 'Recent hemorrhagic stroke', 'Large infarct with high transformation risk'],
          sideEffects: ['Bleeding'],
          alternatives: ['DOACs preferred over warfarin in most patients with non-valvular AF']
        },
        {
          drug: 'Statin (High-Intensity)',
          indication: '🔴 All ischemic stroke - Start immediately if not already on statin',
          dose: 'Atorvastatin 40-80mg daily or Rosuvastatin 20-40mg daily',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Active liver disease', 'Pregnancy'],
          sideEffects: ['Myalgias', 'Elevated LFTs', 'Diabetes risk'],
          alternatives: ['Moderate-intensity statin if intolerant to high-intensity']
        },
        {
          drug: 'Mannitol (Cerebral Edema)',
          indication: '🔴 Raised ICP / Herniation signs - Clinical deterioration, pupil changes',
          dose: '0.5-1 g/kg IV over 15-20 min. May repeat 0.25-0.5 g/kg every 6 hours',
          route: 'IV',
          duration: 'Short-term while arranging definitive management',
          contraindications: ['Severe renal failure', 'Pulmonary edema'],
          sideEffects: ['Electrolyte imbalance', 'Rebound ICP elevation', 'Renal injury'],
          alternatives: ['Hypertonic saline 23.4% 30mL or 3% NaCl']
        },
        {
          drug: 'Nimodipine (SAH only)',
          indication: '🔴 Subarachnoid hemorrhage - Prevents vasospasm',
          dose: '60 mg PO/NG every 4 hours',
          route: 'Oral or via NG tube (NOT IV)',
          duration: '21 days',
          contraindications: ['Hypotension (reduce dose or hold)'],
          sideEffects: ['Hypotension', 'Headache'],
          alternatives: ['No good alternative - Nimodipine is standard of care for SAH']
        }
      ],
      nonPharmacological: [
        'Stroke unit admission (reduces mortality by 20%)',
        'Swallow screen before any oral intake - NPO until passed',
        'Head of bed positioning (flat if hypotensive, 30° if raised ICP)',
        'Temperature management - Treat fever aggressively (target normothermia)',
        'Glucose control - Avoid hypo- and hyperglycemia (target 140-180 mg/dL)',
        'DVT prophylaxis with SCDs initially, then pharmacological when safe',
        'Early mobilization when medically stable',
        'Speech and language therapy assessment',
        'Physical therapy and occupational therapy',
        'Nutritional support (NG tube if dysphagia persists)',
        'Skin care and pressure ulcer prevention',
        'Bladder management (avoid indwelling catheter if possible)',
        'Depression screening and treatment',
        'Family education and support'
      ],
      surgical: [
        'Mechanical thrombectomy - LVO within 6-24h (with appropriate imaging selection)',
        'Decompressive craniectomy - Large MCA infarct with malignant edema (age <60, within 48h)',
        'Cerebellar decompression - Posterior fossa stroke with hydrocephalus or compression',
        'EVD (External ventricular drain) - Hydrocephalus from hemorrhage or infarct',
        'Aneurysm clipping or coiling - SAH (secure aneurysm within 24h if possible)',
        'Hematoma evacuation - Consider for large lobar ICH, cerebellar hemorrhage >3cm',
        'Carotid endarterectomy or stenting - Symptomatic carotid stenosis >50% (within 2 weeks)'
      ],
      referralCriteria: [
        '🔴 All stroke patients - Admit to stroke unit',
        '🔴 tPA candidate - Administer immediately, do not wait for specialist',
        '🔴 Large vessel occlusion - Activate thrombectomy team / Transfer to thrombectomy center',
        '🔴 Hemorrhagic stroke - Neurosurgery consult',
        '🔴 SAH - Urgent neurosurgery / Interventional neuroradiology for aneurysm treatment',
        '🔴 Cerebellar stroke with deterioration - Urgent neurosurgery (decompression)',
        '🔴 Young stroke (<50) - Comprehensive workup for unusual causes',
        'Cardiology consult if AF or cardiac source suspected',
        'Vascular surgery if significant carotid stenosis',
        'Rehabilitation medicine for all stroke survivors'
      ],
      monitoring: [
        'Neurological checks (NIHSS): Every 1-2 hours initially, then every 4-6 hours',
        'Vital signs: Every 15 min during tPA, then every 1-2 hours',
        'Blood pressure: Continuous monitoring initially; maintain targets',
        'Cardiac monitoring: Continuous for at least 24-48 hours',
        'Blood glucose: Every 6 hours; treat if <70 or >180 mg/dL',
        'Oxygen saturation: Maintain ≥94%',
        'Temperature: Every 4 hours; treat fever promptly',
        'Neuroimaging: Repeat CT if clinical deterioration (hemorrhagic transformation, edema)',
        'Post-tPA monitoring: Neuro checks every 15 min x 2h, every 30 min x 6h, hourly x 16h',
        'Swallow screen: Before any oral intake',
        'Daily electrolytes and renal function'
      ],
      followUp: `BEFORE DISCHARGE:
• Determine stroke etiology and document (TOAST classification)
• Complete vascular and cardiac workup
• Establish secondary prevention medications
• Functional assessment and rehabilitation needs
• Driving restriction counseling

DISCHARGE MEDICATIONS:
• Antiplatelet (aspirin +/- clopidogrel) or Anticoagulation (if AF)
• High-intensity statin
• Antihypertensive (target <130/80)
• Diabetes management if applicable

REHABILITATION:
• Acute inpatient rehabilitation if significant disability
• Outpatient PT/OT/Speech therapy
• Cognitive rehabilitation if needed

FOLLOW-UP SCHEDULE:
• Neurology: 4-6 weeks
• Primary care: 1-2 weeks
• Repeat imaging if needed
• Long-term cardiac monitoring if cryptogenic stroke

SECONDARY PREVENTION TARGETS:
• BP <130/80 (or <140/90 for some)
• LDL <70 mg/dL
• HbA1c <7% (individualized)
• Smoking cessation
• Regular exercise
• Healthy diet (Mediterranean, DASH)
• Weight optimization`
    },

    redFlags: [
      '🔴 CRITICAL: New or worsening neurological deficits - Repeat CT, may indicate hemorrhagic transformation or edema',
      '🔴 CRITICAL: Decreasing level of consciousness - Herniation risk, consider mannitol/hypertonic saline, urgent CT, neurosurgery',
      '🔴 CRITICAL: Fixed dilated pupil - Herniation, medical emergency, immediate intervention',
      '🔴 CRITICAL: Severe headache during/after tPA - Stop infusion, urgent CT (ICH risk)',
      '🔴 CRITICAL: Sudden severe headache (thunderclap) - SAH until proven otherwise, CT + LP if CT negative',
      '🔴 CRITICAL: Rapidly progressive deficits - Consider basilar artery occlusion, large vessel occlusion',
      '🔴 CRITICAL: Cerebellar signs with deterioration - Risk of brainstem compression, may need urgent surgery',
      '🟠 URGENT: BP >220/120 in ischemic stroke (no tPA) - Needs gradual reduction (lower by 15% in first 24h)',
      '🟠 URGENT: New atrial fibrillation detected - Anticoagulation decision, cardiology input',
      '🟠 URGENT: Failed swallow screen - NPO, NG tube for nutrition and medications',
      '🟠 URGENT: Fever >38°C - Investigate source, treat aggressively (worse outcomes with hyperthermia)',
      '🟠 URGENT: Recurrent stroke or TIA - Urgent vascular imaging, optimize secondary prevention'
    ],

    complications: [
      'Hemorrhagic transformation - Especially after tPA or large infarct; may require reversal agents, surgery',
      'Cerebral edema with herniation - Peak 3-5 days; may need decompressive craniectomy',
      'Seizures - More common with hemorrhagic stroke and cortical involvement; antiepileptics if occur',
      'Aspiration pneumonia - Leading cause of death post-stroke; dysphagia screening essential',
      'Deep vein thrombosis / Pulmonary embolism - Common in immobile patients; prophylaxis critical',
      'Urinary tract infection - Avoid indwelling catheters if possible',
      'Pressure ulcers - Early mobilization and skin care',
      'Depression - Very common (30-50%); screen and treat',
      'Spasticity - Develops over weeks-months; PT, botox, baclofen',
      'Shoulder subluxation and pain - Proper positioning and support',
      'Recurrent stroke - 5-15% per year without secondary prevention',
      'Vasospasm (SAH) - Days 4-14; nimodipine prophylaxis, triple-H therapy if occurs',
      'Hydrocephalus (SAH/ICH) - May need EVD or VP shunt'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY
════════════════════════════════════════════════════════════════════════════════

ISCHEMIC STROKE:
• In-hospital mortality: 5-10%
• 30-day mortality: 10-15%
• 1-year mortality: 20-25%
• With tPA (appropriately selected): Reduced disability, similar mortality

HEMORRHAGIC STROKE:
• ICH 30-day mortality: 30-50%
• SAH 30-day mortality: 30-40%
• Higher mortality than ischemic stroke

════════════════════════════════════════════════════════════════════════════════
FUNCTIONAL OUTCOMES
════════════════════════════════════════════════════════════════════════════════

• 30% independent at 1 year
• 30% dependent, living at home
• 20% require institutional care
• 20% die within first year

Factors affecting recovery:
• Stroke severity (NIHSS)
• Age
• Pre-stroke function
• Stroke location
• Early rehabilitation
• Complications
• Reperfusion treatment

════════════════════════════════════════════════════════════════════════════════
RECURRENCE
════════════════════════════════════════════════════════════════════════════════

• Without secondary prevention: 10-15% per year
• With optimal treatment: 3-5% per year
• Highest risk in first 90 days after index event
• Secondary prevention dramatically reduces recurrence`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

BLOOD PRESSURE CONTROL (Most Important):
• Target <130/80 mmHg for most adults
• Reduces stroke risk by 35-44%

ATRIAL FIBRILLATION MANAGEMENT:
• Anticoagulation based on CHA2DS2-VASc score
• DOACs preferred over warfarin for most
• Reduces stroke risk by 60-70%

LIFESTYLE:
• Smoking cessation
• Mediterranean or DASH diet
• Regular physical activity (150 min/week)
• Limit alcohol (≤2 drinks/day men, ≤1 women)
• Maintain healthy weight

OTHER RISK FACTORS:
• Diabetes control (HbA1c <7%)
• Statin therapy for those with elevated CV risk
• Treat sleep apnea

════════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After Stroke/TIA)
════════════════════════════════════════════════════════════════════════════════

ANTIPLATELET THERAPY:
• Aspirin 75-100mg daily for non-cardioembolic stroke
• Dual antiplatelet (ASA + clopidogrel) for 21 days in high-risk TIA/minor stroke

ANTICOAGULATION:
• For AF, mechanical valves, certain hypercoagulable states
• DOACs preferred for non-valvular AF

STATINS:
• High-intensity statin for all ischemic stroke

BLOOD PRESSURE:
• Target <130/80 mmHg
• Start/intensify therapy before discharge

CAROTID INTERVENTION:
• Endarterectomy or stenting for symptomatic stenosis >50%
• Within 2 weeks of index event for maximum benefit`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS A STROKE?
════════════════════════════════════════════════════════════════════════════════

A stroke happens when blood flow to part of the brain is blocked (ischemic stroke) or when a blood vessel in the brain bursts (hemorrhagic stroke). Without blood flow, brain cells start to die within minutes.

"TIME IS BRAIN" - The faster you get treatment, the more brain can be saved.

════════════════════════════════════════════════════════════════════════════════
🔴 RECOGNIZE STROKE FAST - CALL EMERGENCY IMMEDIATELY
════════════════════════════════════════════════════════════════════════════════

F - FACE: Ask the person to smile. Does one side droop?
A - ARMS: Ask them to raise both arms. Does one drift down?
S - SPEECH: Ask them to repeat a simple sentence. Is it slurred or strange?
T - TIME: If you see ANY of these, call emergency services IMMEDIATELY!

Other warning signs:
• Sudden numbness or weakness (face, arm, or leg - especially one side)
• Sudden confusion or trouble speaking
• Sudden trouble seeing in one or both eyes
• Sudden trouble walking, dizziness, loss of balance
• Sudden severe headache with no known cause

DO NOT WAIT to see if symptoms go away!
DO NOT drive yourself to the hospital!

════════════════════════════════════════════════════════════════════════════════
AFTER YOUR STROKE
════════════════════════════════════════════════════════════════════════════════

TAKE YOUR MEDICATIONS:
• Blood thinner (aspirin or other) - Prevents clots
• Statin - Lowers cholesterol, stabilizes plaques
• Blood pressure pills - VERY important for prevention
• Other medications as prescribed

LIFESTYLE CHANGES:
• Stop smoking - Single most impactful change
• Eat heart-healthy diet (less salt, more fruits and vegetables)
• Exercise regularly (as advised by your doctors)
• Limit alcohol
• Maintain healthy weight
• Manage stress

ATTEND REHABILITATION:
• Physical therapy - Helps regain strength and movement
• Occupational therapy - Helps with daily activities
• Speech therapy - Helps with speaking and swallowing
• Be patient - Recovery takes time

KNOW YOUR NUMBERS:
• Blood pressure goal: Usually <130/80
• Cholesterol: LDL goal usually <70
• Blood sugar: If diabetic, HbA1c usually <7%

════════════════════════════════════════════════════════════════════════════════
WHEN TO SEEK HELP
════════════════════════════════════════════════════════════════════════════════

🔴 Call emergency if you have ANY symptoms of a new stroke (FAST signs)

Call your doctor if:
• Severe headache
• Vision changes
• New weakness or numbness
• Falls
• Problems with medications
• Feeling very sad or hopeless (depression is common after stroke)

FOLLOW-UP APPOINTMENTS:
• Keep all appointments with your doctors
• Bring your medication list
• Write down your questions before visits
• Bring a family member to help remember information`,

    references: [
      'Powers WJ, Rabinstein AA, Ackerson T, et al. Guidelines for the Early Management of Patients With Acute Ischemic Stroke: 2019 Update. Stroke. 2019;50(12):e344-e418.',
      'Greenberg SM, Ziai WC, Cordonnier C, et al. 2022 Guideline for the Management of Patients With Spontaneous Intracerebral Hemorrhage. Stroke. 2022;53(7):e282-e361.',
      'Hemphill JC 3rd, Greenberg SM, Anderson CS, et al. Guidelines for the Management of Spontaneous Intracerebral Hemorrhage. Stroke. 2015;46(7):2032-2060.',
      'Connolly ES Jr, Rabinstein AA, Carhuapoma JR, et al. Guidelines for the Management of Aneurysmal Subarachnoid Hemorrhage. Stroke. 2012;43(6):1711-1737.',
      'Kleindorfer DO, Towfighi A, Chaturvedi S, et al. 2021 Guideline for the Prevention of Stroke in Patients With Stroke and Transient Ischemic Attack. Stroke. 2021;52(7):e364-e467.',
      'Berkhemer OA, Fransen PS, Beumer D, et al. A Randomized Trial of Intraarterial Treatment for Acute Ischemic Stroke (MR CLEAN). N Engl J Med. 2015;372(1):11-20.',
      'Goyal M, Menon BK, van Zwam WH, et al. Endovascular thrombectomy after large-vessel ischaemic stroke: a meta-analysis of individual patient data from five randomised trials (HERMES). Lancet. 2016;387(10029):1723-1731.',
      'NINDS rt-PA Stroke Study Group. Tissue Plasminogen Activator for Acute Ischemic Stroke. N Engl J Med. 1995;333(24):1581-1587.',
      'Johnston SC, Easton JD, Farrant M, et al. Clopidogrel and Aspirin in Acute Ischemic Stroke and High-Risk TIA (POINT). N Engl J Med. 2018;379(3):215-225.',
      'GBD 2019 Stroke Collaborators. Global, regional, and national burden of stroke and its risk factors, 1990-2019. Lancet Neurol. 2021;20(10):795-820.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 9. ACUTE BACTERIAL MENINGITIS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '9',
    name: 'Acute Bacterial Meningitis',
    slug: 'acute-bacterial-meningitis',
    specialty: 'Infectious Disease',
    bodySystem: 'Neurological',
    icdCode: 'G00.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute bacterial meningitis is a life-threatening infection of the meninges and cerebrospinal fluid. Mortality is 15-25% even with treatment, and survivors often have permanent neurological sequelae. ANTIBIOTICS MUST BE GIVEN WITHIN 1 HOUR - Do not delay for any investigation.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• GIVE ANTIBIOTICS WITHIN 1 HOUR - Every hour delay increases mortality and morbidity
• DO NOT delay antibiotics for LP or imaging - Give antibiotics FIRST if any delay anticipated
• Classic triad: Fever + Neck stiffness + Altered mental status (present in <50% of cases)
• Empiric therapy: Ceftriaxone + Vancomycin (+ Ampicillin if >50 years or immunocompromised)
• DEXAMETHASONE: Give 15-20 min BEFORE or WITH first antibiotic dose (not after)
• CT before LP ONLY if: Immunocompromised, history of CNS disease, new seizure, papilledema, focal deficits, GCS <12
• LP contraindicated if: Signs of raised ICP, coagulopathy, infection at LP site
• Prophylaxis for close contacts if N. meningitidis or H. influenzae
• Complications: Cerebral edema, hydrocephalus, stroke, SIADH, seizures, hearing loss
• Notify public health if meningococcal disease suspected

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Bacteria reach meninges via: Hematogenous spread, direct extension (sinusitis, otitis), trauma/surgery
• Bacteria multiply in CSF (limited immune defenses)
• Inflammatory response → Cytokine release → BBB breakdown
• Cerebral edema (vasogenic, cytotoxic, interstitial)
• Increased intracranial pressure
• Decreased cerebral blood flow → Ischemia
• Neuronal injury from inflammation and ischemia`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• 2.5 million cases annually worldwide
• 250,000 deaths per year
• Case fatality: 15-25% with treatment, >70% without treatment
• 25% of survivors have permanent neurological sequelae
• Decreasing incidence in countries with conjugate vaccines

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY - "MENINGITIS BELT"
════════════════════════════════════════════════════════════════════════════════

• Sub-Saharan "Meningitis Belt" (Senegal to Ethiopia) - Epidemic-prone
• Epidemics during dry season (December to June)
• N. meningitidis serogroup A historically most common (now W and C emerging)
• Mass vaccination campaigns have reduced epidemics
• S. pneumoniae major cause of endemic meningitis
• Higher case fatality due to delayed presentation and limited ICU access
• Cryptococcal meningitis common in HIV patients

════════════════════════════════════════════════════════════════════════════════
CAUSATIVE ORGANISMS BY AGE
════════════════════════════════════════════════════════════════════════════════

NEONATES (<1 month):
• Group B Streptococcus (S. agalactiae)
• Escherichia coli (K1 strain)
• Listeria monocytogenes

INFANTS AND CHILDREN (1 month - 5 years):
• Streptococcus pneumoniae
• Neisseria meningitidis
• Haemophilus influenzae type b (where vaccine not available)

OLDER CHILDREN AND ADULTS:
• Streptococcus pneumoniae (most common)
• Neisseria meningitidis

ADULTS >50 YEARS AND IMMUNOCOMPROMISED:
• Streptococcus pneumoniae
• Neisseria meningitidis
• Listeria monocytogenes
• Gram-negative bacilli

POST-NEUROSURGERY/TRAUMA:
• Staphylococcus aureus
• Coagulase-negative staphylococci
• Gram-negative bacilli (including Pseudomonas)

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

• Age extremes (neonates, elderly >65)
• Immunocompromised (HIV, asplenia, complement deficiency, immunosuppressive therapy)
• Crowded living conditions (dormitories, military barracks, prisons)
• Recent respiratory infection
• CSF leak (trauma, surgery)
• Cochlear implants
• No vaccination
• Alcoholism
• Diabetes mellitus
• Chronic illness`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
CLASSIC TRIAD (Present in <50% of cases)
════════════════════════════════════════════════════════════════════════════════

1. Fever
2. Neck stiffness (nuchal rigidity)
3. Altered mental status

🔴 Absence of all three makes bacterial meningitis unlikely, but DO NOT rely on this alone

════════════════════════════════════════════════════════════════════════════════
SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

• Severe headache (worst of life in some cases)
• Fever and chills
• Neck stiffness/pain
• Photophobia
• Nausea and vomiting
• Confusion, irritability, drowsiness
• Seizures (20-30% of cases)

SYMPTOMS IN NEONATES/INFANTS (May be nonspecific):
• Irritability or lethargy
• Poor feeding
• Vomiting
• Bulging fontanelle
• High-pitched cry
• Hypothermia or fever
• Seizures

════════════════════════════════════════════════════════════════════════════════
SIGNS
════════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Fever (may be absent in elderly, immunocompromised, or neonates)
• Tachycardia
• Hypotension (late - septic shock)

MENINGEAL SIGNS:
• Nuchal rigidity - Resistance to passive neck flexion
• Kernig sign - Pain/resistance with knee extension when hip flexed 90°
• Brudzinski sign - Involuntary hip/knee flexion when neck flexed
🔴 Meningeal signs may be absent in elderly, immunocompromised, or early disease

NEUROLOGICAL:
• Altered mental status (GCS <14 in ~80%)
• Cranial nerve palsies (III, VI, VII most common)
• Focal neurological deficits
• Papilledema (if raised ICP)
• Seizures

SIGNS OF SPECIFIC ORGANISMS:

Meningococcal (N. meningitidis):
• 🔴 Petechial/purpuric rash - Non-blanching, can progress rapidly
• Meningococcemia - Shock, DIC, adrenal hemorrhage (Waterhouse-Friderichsen)

Pneumococcal (S. pneumoniae):
• May have concurrent pneumonia, otitis media, sinusitis
• Often more severe presentation

════════════════════════════════════════════════════════════════════════════════
SEVERITY INDICATORS
════════════════════════════════════════════════════════════════════════════════

🔴 POOR PROGNOSTIC SIGNS:
• GCS <10 at presentation
• Seizures
• Hypotension/shock
• Purpuric rash (meningococcemia)
• Age >60 years
• Delay in antibiotics
• S. pneumoniae infection
• High CSF bacterial load (low glucose, high protein)`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
════════════════════════════════════════════════════════════════════════════════

🔴 DO NOT DELAY ANTIBIOTICS FOR DIAGNOSTIC TESTS
If LP will be delayed (CT needed, coagulopathy, etc.), give antibiotics FIRST

ORDER OF PRIORITIES:
1. Clinical suspicion based on symptoms/signs
2. Blood cultures (before antibiotics if possible, but don't delay Abx)
3. Antibiotics + Dexamethasone (within 1 hour)
4. CT head if indicated (see below)
5. Lumbar puncture when safe

════════════════════════════════════════════════════════════════════════════════
WHEN TO CT BEFORE LP
════════════════════════════════════════════════════════════════════════════════

CT head BEFORE LP only if any of:
• Immunocompromised state (HIV, immunosuppressive therapy)
• History of CNS disease (mass, stroke, focal infection)
• New-onset seizure (within 1 week)
• Papilledema on fundoscopy
• Abnormal level of consciousness (GCS <12)
• Focal neurological deficit

🔴 If any above present: Give antibiotics + dexamethasone → CT → LP (if CT safe)

════════════════════════════════════════════════════════════════════════════════
LUMBAR PUNCTURE - CSF ANALYSIS
════════════════════════════════════════════════════════════════════════════════

BACTERIAL MENINGITIS typical CSF findings:
• Opening pressure: Elevated (>20 cm H2O)
• Appearance: Cloudy/turbid/purulent
• WBC: 1,000-5,000/μL (range 100 to >10,000)
• Cell type: >80% Neutrophils (PMNs)
• Protein: Elevated (100-500 mg/dL)
• Glucose: Low (<40 mg/dL or CSF:serum ratio <0.4)
• Gram stain: Positive in 60-90%
• Culture: Gold standard (positive in 70-85% if no prior antibiotics)

Comparison with other causes:
• Viral: WBC 50-1000, lymphocytes, normal glucose, mildly elevated protein
• TB: WBC 50-500, lymphocytes, low glucose, very high protein
• Fungal: WBC 20-500, lymphocytes, low glucose, elevated protein

════════════════════════════════════════════════════════════════════════════════
LP CONTRAINDICATIONS
════════════════════════════════════════════════════════════════════════════════

ABSOLUTE:
• Signs of raised ICP with risk of herniation
• Infection at LP site
• Severe coagulopathy (INR >1.5, platelets <50,000)

RELATIVE:
• Hemodynamic instability (stabilize first)
• Suspected spinal epidural abscess

🔴 If LP contraindicated: Give empiric antibiotics based on clinical suspicion

════════════════════════════════════════════════════════════════════════════════
OTHER DIAGNOSTIC TESTS
════════════════════════════════════════════════════════════════════════════════

• CSF PCR - Especially useful if antibiotics given before LP
• CSF latex agglutination - Rapid detection of bacterial antigens
• Blood cultures - Positive in 50-80% of cases
• Procalcitonin - Elevated in bacterial (helps differentiate from viral)
• Serum glucose - Compare with CSF (CSF:serum ratio)`,

    differentialDiagnosis: [
      'Viral meningitis - Less severe, lymphocytic CSF, normal glucose, self-limiting',
      'Tuberculous meningitis - Subacute onset, lymphocytic CSF, very high protein, low glucose, endemic areas',
      'Fungal meningitis (Cryptococcal) - HIV/immunocompromised, subacute, India ink positive, antigen test',
      'Subarachnoid hemorrhage - Thunderclap headache, xanthochromia, RBCs in CSF',
      'Brain abscess - Focal deficits, seizures, ring-enhancing lesion on imaging',
      'Viral encephalitis (HSV, etc.) - More altered mental status, temporal lobe involvement, PCR positive',
      'Cerebral malaria - Endemic area, parasites on smear, altered consciousness',
      'Neuroleptic malignant syndrome - Antipsychotic use, rigidity, hyperthermia, elevated CK',
      'Drug-induced meningitis (NSAIDs, IVIG, etc.) - History of exposure, sterile CSF',
      'Carcinomatous meningitis - History of malignancy, positive cytology'
    ],

    investigations: [
      {
        name: 'Blood Cultures (2 sets)',
        indication: '🔴 BEFORE antibiotics if possible - But do NOT delay antibiotics for cultures',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture with CSF Analysis',
        indication: '🔴 Diagnostic gold standard - Opening pressure, cell count, glucose, protein, Gram stain, culture',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CSF Gram Stain',
        indication: '🔴 Rapid identification of organism - Positive in 60-90% of untreated cases',
        availability: 'BASIC'
      },
      {
        name: 'CSF Culture',
        indication: '🔴 Definitive diagnosis and antibiotic sensitivities - May be negative if antibiotics given',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CSF PCR (Multiplex)',
        indication: 'Especially useful if antibiotics given before LP - Can identify organism when culture negative',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Head (Non-contrast)',
        indication: 'BEFORE LP only if: Immunocompromised, CNS disease history, seizure, papilledema, focal deficits, GCS<12',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Leukocytosis with left shift typical; thrombocytopenia may indicate DIC',
        availability: 'BASIC'
      },
      {
        name: 'Serum Glucose',
        indication: 'Compare with CSF glucose - CSF:serum ratio <0.4 suggests bacterial',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Profile (PT/INR, aPTT)',
        indication: 'Before LP - Contraindicated if INR >1.5. Also assess for DIC.',
        availability: 'BASIC'
      },
      {
        name: 'Procalcitonin',
        indication: 'Helps differentiate bacterial from viral - Elevated >0.5 ng/mL suggests bacterial',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: 'Elevated in bacterial meningitis; helps monitor response',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: 'Assess renal function, electrolytes - SIADH can cause hyponatremia',
        availability: 'BASIC'
      },
      {
        name: 'Serum Lactate',
        indication: 'If sepsis suspected - Part of sepsis workup',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Test',
        indication: 'All patients with meningitis - Affects differential (cryptococcal) and management',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If pneumococcal suspected - May show concurrent pneumonia',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ANTIBIOTICS WITHIN 1 HOUR - Do NOT delay for LP or imaging',
        '⚡ DEXAMETHASONE - Give 15-20 min BEFORE or WITH first antibiotic dose',
        '⚡ Blood cultures x2 - Before antibiotics if can be done quickly',
        '⚡ Establish IV access (two large bore)',
        '⚡ IV fluids - But avoid overhydration (risk of cerebral edema, SIADH)',
        '⚡ Assess need for CT before LP (see criteria)',
        '⚡ Lumbar puncture when safe - Do not delay antibiotics for LP',
        '⚡ Isolate patient if meningococcal suspected (droplet precautions)',
        '⚡ Notify public health if meningococcal disease suspected',
        '⚡ Contact tracing and prophylaxis for close contacts',
        'Assess and support airway - Intubate if GCS ≤8',
        'Monitor GCS frequently - Watch for deterioration',
        'Control fever - Antipyretics',
        'Seizure precautions - Have anticonvulsants ready',
        'Admit to ICU if: GCS <12, shock, respiratory compromise, status epilepticus'
      ],
      pharmacological: [
        {
          drug: 'Ceftriaxone + Vancomycin (Empiric - Adults)',
          indication: '🔴 EMPIRIC therapy for suspected bacterial meningitis - Give within 1 hour',
          dose: 'Ceftriaxone 2g IV every 12 hours + Vancomycin 15-20 mg/kg IV every 8-12 hours (target trough 15-20)',
          route: 'IV',
          duration: 'Adjust based on organism and sensitivities. Typical: 10-14 days for pneumococcus, 7 days for meningococcus',
          contraindications: ['Severe beta-lactam allergy (use meropenem or chloramphenicol)'],
          sideEffects: ['Vancomycin: Nephrotoxicity, red man syndrome', 'Ceftriaxone: Diarrhea, biliary sludging'],
          alternatives: ['Cefotaxime 2g IV q4-6h can replace ceftriaxone']
        },
        {
          drug: 'Add Ampicillin (If Listeria Risk)',
          indication: '🔴 Add to empiric regimen if: Age >50, immunocompromised, alcoholism, pregnancy',
          dose: '2g IV every 4 hours',
          route: 'IV',
          duration: '21 days or more for Listeria',
          contraindications: ['Penicillin allergy (use TMP-SMX as alternative for Listeria)'],
          sideEffects: ['Rash', 'Diarrhea', 'Allergic reactions'],
          alternatives: ['TMP-SMX 5 mg/kg IV every 6-8 hours for Listeria coverage if penicillin-allergic']
        },
        {
          drug: 'Dexamethasone',
          indication: '🔴 GIVE 15-20 min BEFORE or WITH first antibiotic dose. Reduces mortality and neurological sequelae in pneumococcal meningitis',
          dose: '0.15 mg/kg IV every 6 hours for 4 days (adult: 10mg IV every 6 hours)',
          route: 'IV',
          duration: '4 days; discontinue if non-pneumococcal organism confirmed (some continue regardless)',
          contraindications: ['Do not give if antibiotics already started >1 hour ago (benefit lost)'],
          sideEffects: ['Hyperglycemia', 'GI bleeding', 'May mask fever'],
          alternatives: ['None - Dexamethasone is standard of care']
        },
        {
          drug: 'Ceftriaxone (Directed - S. pneumoniae)',
          indication: 'Confirmed pneumococcal meningitis - Continue if sensitive',
          dose: '2g IV every 12 hours',
          route: 'IV',
          duration: '10-14 days',
          contraindications: ['Resistant organism (use vancomycin + ceftriaxone if MIC >2)'],
          sideEffects: ['Diarrhea', 'Rash', 'Biliary sludging'],
          alternatives: ['If penicillin-sensitive (MIC <0.1): Penicillin G 4 million units IV q4h']
        },
        {
          drug: 'Ceftriaxone (Directed - N. meningitidis)',
          indication: 'Confirmed meningococcal meningitis',
          dose: '2g IV every 12 hours',
          route: 'IV',
          duration: '7 days',
          contraindications: ['Severe cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Rash'],
          alternatives: ['Penicillin G 4 million units IV q4h', 'Chloramphenicol if severe allergy']
        },
        {
          drug: 'Ceftriaxone (Directed - H. influenzae)',
          indication: 'Confirmed H. influenzae meningitis',
          dose: '2g IV every 12 hours',
          route: 'IV',
          duration: '7-10 days',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Rash'],
          alternatives: ['Chloramphenicol', 'Meropenem']
        },
        {
          drug: 'Ampicillin + Gentamicin (Directed - Listeria)',
          indication: 'Confirmed Listeria meningitis',
          dose: 'Ampicillin 2g IV every 4 hours + Gentamicin 2 mg/kg loading, then 1.7 mg/kg every 8 hours',
          route: 'IV',
          duration: '21 days or longer',
          contraindications: ['Penicillin allergy (use TMP-SMX)', 'Renal impairment (adjust gentamicin)'],
          sideEffects: ['Nephrotoxicity and ototoxicity (gentamicin)', 'Rash (ampicillin)'],
          alternatives: ['TMP-SMX 5 mg/kg every 6-8 hours']
        },
        {
          drug: 'Vancomycin + Cefepime/Meropenem (Post-neurosurgery)',
          indication: 'Nosocomial meningitis - Covers Staph and Gram-negatives including Pseudomonas',
          dose: 'Vancomycin 15-20 mg/kg every 8-12h + Cefepime 2g every 8h OR Meropenem 2g every 8h',
          route: 'IV',
          duration: '10-21 days depending on organism',
          contraindications: ['Drug allergies'],
          sideEffects: ['Nephrotoxicity', 'Seizures (cefepime, meropenem at high doses)'],
          alternatives: ['Adjust based on culture results']
        },
        {
          drug: 'Ciprofloxacin (Prophylaxis)',
          indication: '🔴 Close contacts of N. meningitidis case - Give within 24 hours of contact identification',
          dose: 'Adults: 500mg PO single dose. Children: 20mg/kg (max 500mg) PO single dose',
          route: 'Oral',
          duration: 'Single dose',
          contraindications: ['Pregnancy (use ceftriaxone)', 'Children <18 (relative - can use if no alternative)'],
          sideEffects: ['GI upset', 'Tendinopathy'],
          alternatives: ['Rifampicin 600mg PO every 12h x 2 days', 'Ceftriaxone 250mg IM single dose']
        },
        {
          drug: 'Rifampicin (Prophylaxis)',
          indication: 'Alternative prophylaxis for close contacts of N. meningitidis or H. influenzae',
          dose: 'N. meningitidis: 600mg PO every 12h x 2 days. H. influenzae: 20mg/kg (max 600mg) daily x 4 days',
          route: 'Oral',
          duration: '2 days (meningococcus) or 4 days (H. influenzae)',
          contraindications: ['Pregnancy', 'Severe liver disease', 'Concomitant protease inhibitors'],
          sideEffects: ['Orange discoloration of secretions', 'Hepatotoxicity', 'Drug interactions'],
          alternatives: ['Ciprofloxacin', 'Ceftriaxone']
        },
        {
          drug: 'Phenytoin or Levetiracetam (Seizures)',
          indication: 'Seizure prophylaxis controversial; treat if seizures occur',
          dose: 'Levetiracetam 1000-1500mg IV loading, then 500-1000mg every 12h. OR Phenytoin 20mg/kg IV loading',
          route: 'IV',
          duration: 'Until acute phase resolved; consider discontinuing before discharge if no seizures',
          contraindications: ['Drug-specific'],
          sideEffects: ['Phenytoin: Hypotension (infuse slowly), cardiac arrhythmia', 'Levetiracetam: Agitation'],
          alternatives: ['Valproic acid', 'Lacosamide']
        },
        {
          drug: 'Mannitol or Hypertonic Saline (Raised ICP)',
          indication: '🔴 Signs of raised ICP or herniation - Altered consciousness, pupil changes, posturing',
          dose: 'Mannitol 0.5-1 g/kg IV OR 23.4% saline 30mL IV OR 3% saline 250mL IV',
          route: 'IV',
          duration: 'Short-term while arranging definitive management',
          contraindications: ['Mannitol: Severe renal failure, pulmonary edema'],
          sideEffects: ['Electrolyte imbalance', 'Rebound ICP'],
          alternatives: ['Elevate head of bed', 'Hyperventilation (short-term)']
        }
      ],
      nonPharmacological: [
        'ICU admission if: GCS <12, hemodynamic instability, respiratory failure, status epilepticus',
        'Droplet precautions until meningococcal ruled out (first 24h of antibiotics)',
        'Head of bed elevation 30° if raised ICP suspected',
        'Strict intake/output monitoring (risk of SIADH)',
        'Seizure precautions',
        'Frequent neurological assessments (GCS, pupils, focal signs)',
        'Temperature control - Treat fever aggressively',
        'Fluid management - Avoid both dehydration and overhydration',
        'Early rehabilitation referral if deficits present',
        'Hearing assessment before discharge (especially children)',
        'Psychological support for patient and family'
      ],
      surgical: [
        'Rarely needed for uncomplicated meningitis',
        'EVD (External ventricular drain) if hydrocephalus with raised ICP',
        'VP shunt for persistent hydrocephalus',
        'Surgical drainage of parameningeal focus (sinusitis, mastoiditis, epidural abscess)',
        'Craniectomy for severe refractory raised ICP (rare)'
      ],
      referralCriteria: [
        '🔴 All suspected bacterial meningitis - Admit to hospital',
        '🔴 GCS <12 or deteriorating - ICU admission',
        '🔴 Septic shock - ICU, vasopressors',
        '🔴 Status epilepticus - ICU',
        '🔴 Signs of raised ICP - ICU, neurosurgery consult',
        '🔴 Focal deficits or mass lesion - Neurosurgery consult',
        'Infectious disease consultation for complex cases',
        'ENT if parameningeal focus (sinusitis, mastoiditis)',
        'Public health notification for meningococcal disease',
        'Audiology before discharge (especially children)'
      ],
      monitoring: [
        'Neurological status (GCS, pupils, focal signs): Every 1-2 hours initially',
        'Vital signs: Every 1-2 hours; continuous if ICU',
        'Fluid balance: Strict I/O (SIADH risk - may need fluid restriction)',
        'Serum sodium: Every 12-24 hours (SIADH can cause severe hyponatremia)',
        'Blood glucose: Every 6 hours (steroids cause hyperglycemia)',
        'Inflammatory markers (CRP): Daily to assess response',
        'Repeat LP: Consider if no clinical improvement at 48-72 hours',
        'Hearing assessment: Before discharge and at follow-up',
        'Watch for complications: Seizures, stroke, hydrocephalus, subdural empyema'
      ],
      followUp: `BEFORE DISCHARGE:
• Clinical improvement with afebrile for 24-48 hours
• Completing appropriate antibiotic course
• No ongoing complications
• Hearing assessment (especially children)
• Contact tracing and prophylaxis completed

DISCHARGE PLANNING:
• Complete antibiotic course (oral step-down if appropriate)
• Educate on warning signs for recurrence
• Schedule follow-up appointments
• Rehabilitation referral if neurological deficits

FOLLOW-UP SCHEDULE:
• Primary care/Infectious disease: 1-2 weeks post-discharge
• Audiology: 4-6 weeks (hearing loss may be delayed)
• Neurology if: Seizures, focal deficits, cognitive changes

LONG-TERM MONITORING:
• Hearing assessment at 1, 6, and 12 months (especially children)
• Cognitive and developmental assessment (children)
• Address long-term sequelae: Hearing loss, cognitive impairment, focal deficits, epilepsy

VACCINATION:
• Ensure up-to-date with pneumococcal and meningococcal vaccines
• If asplenic or complement-deficient: Lifelong prophylactic antibiotics`
    },

    redFlags: [
      '🔴 CRITICAL: GCS <12 or deteriorating - Raised ICP or herniation risk, consider mannitol, ICU, neurosurgery',
      '🔴 CRITICAL: Signs of herniation (pupil changes, posturing, Cushing triad) - Immediate mannitol/hypertonic saline, intubate, neurosurgery',
      '🔴 CRITICAL: Petechial/purpuric rash spreading rapidly - Meningococcemia, very high mortality, aggressive resuscitation',
      '🔴 CRITICAL: Septic shock (hypotension despite fluids) - ICU, vasopressors, broaden antibiotics',
      '🔴 CRITICAL: Status epilepticus - Benzodiazepines, load antiepileptic, ICU',
      '🔴 CRITICAL: Respiratory failure - Intubate, mechanical ventilation',
      '🟠 URGENT: No clinical improvement at 48-72 hours - Repeat imaging, consider repeat LP, resistant organism, complication',
      '🟠 URGENT: New focal neurological deficit - Stroke, abscess, or subdural empyema, urgent imaging',
      '🟠 URGENT: Persistent fever despite antibiotics - Drug fever, inadequate coverage, abscess, wrong diagnosis',
      '🟠 URGENT: Severe hyponatremia (Na <125) - SIADH, restrict fluids, may need hypertonic saline if symptomatic',
      '🟠 URGENT: Worsening headache with papilledema - Hydrocephalus, may need EVD',
      '🟠 URGENT: CSF leak (rhinorrhea, otorrhea) - Neurosurgery consult for repair'
    ],

    complications: [
      'Cerebral edema and herniation - Most feared acute complication; may require osmotic therapy, EVD, or craniectomy',
      'Seizures - Occur in 20-30%; may require long-term antiepileptics',
      'Stroke/Cerebral infarction - From vasculitis or vasospasm; may cause permanent deficits',
      'Hydrocephalus - Communicating or obstructive; may need EVD or VP shunt',
      'Subdural empyema or effusion - May require surgical drainage',
      'SIADH (Syndrome of Inappropriate ADH) - Causes hyponatremia; fluid restriction',
      'Hearing loss - Most common long-term sequela (up to 30%); especially with pneumococcal',
      'Cognitive impairment - Memory, attention, executive function affected',
      'Cranial nerve palsies - III, VI, VII most common; usually transient',
      'Waterhouse-Friderichsen syndrome - Adrenal hemorrhage in meningococcemia; very high mortality',
      'DIC - Associated with meningococcemia; bleeding and thrombosis',
      'Septic shock - Multi-organ failure',
      'Death - 15-25% despite treatment'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY
════════════════════════════════════════════════════════════════════════════════

WITH APPROPRIATE TREATMENT:
• Overall mortality: 15-25%
• S. pneumoniae: 20-30% (highest among common pathogens)
• N. meningitidis: 10-15%
• H. influenzae: 5-10%
• Listeria: 20-30%
• GBS (neonatal): 10-15%

WITHOUT TREATMENT:
• Approaches 100% mortality

MORTALITY RISK FACTORS:
• Delayed antibiotic therapy
• S. pneumoniae infection
• Age >60 years
• Low GCS at presentation
• Seizures within 24 hours
• Immunocompromised state
• Septic shock

════════════════════════════════════════════════════════════════════════════════
LONG-TERM SEQUELAE
════════════════════════════════════════════════════════════════════════════════

25-30% of survivors have permanent sequelae:
• Hearing loss: 10-30% (most common)
• Cognitive impairment: 10-20%
• Motor deficits: 5-10%
• Epilepsy: 5-10%
• Hydrocephalus requiring shunt: 5%

Children often have:
• Learning disabilities
• Behavioral problems
• Speech and language delays

════════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
════════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Delay in antibiotics
• Low GCS at presentation
• Pneumococcal etiology
• Age extremes
• Immunocompromised
• Seizures
• Focal deficits
• Shock at presentation
• High CSF protein, low glucose

GOOD PROGNOSIS:
• Meningococcal etiology (lower mortality than pneumococcal)
• Early antibiotic administration
• Normal mental status at presentation
• Young adult age`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
VACCINATION - PRIMARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

PNEUMOCOCCAL VACCINES:
• PCV13 (conjugate) - Children, immunocompromised
• PPSV23 (polysaccharide) - Adults ≥65, high-risk groups
• Has significantly reduced pneumococcal meningitis

MENINGOCOCCAL VACCINES:
• MenACWY (conjugate) - Adolescents, college students, travelers to endemic areas
• MenB (serogroup B) - Outbreaks, high-risk groups, some countries routine
• Mass vaccination campaigns in African meningitis belt

HAEMOPHILUS INFLUENZAE TYPE B (Hib):
• Part of routine childhood immunization
• Has dramatically reduced Hib meningitis in vaccinated populations

════════════════════════════════════════════════════════════════════════════════
CHEMOPROPHYLAXIS - SECONDARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

FOR CLOSE CONTACTS OF N. MENINGITIDIS:
• Definition of close contact: Household, daycare, direct exposure to secretions
• Give within 24 hours (ideally), up to 7-10 days
• Options: Ciprofloxacin 500mg PO x1, Rifampicin 600mg PO q12h x 2 days, Ceftriaxone 250mg IM x1

FOR CLOSE CONTACTS OF H. INFLUENZAE:
• Household contacts if any child <4 years incompletely vaccinated
• Rifampicin 20mg/kg (max 600mg) daily x 4 days

INDEX PATIENT PROPHYLAXIS:
• Give before discharge if treated with agents other than ceftriaxone
• Ciprofloxacin or rifampicin to eradicate nasopharyngeal carriage

════════════════════════════════════════════════════════════════════════════════
OTHER PREVENTION MEASURES
════════════════════════════════════════════════════════════════════════════════

• Reduce crowding in high-risk settings (dormitories, military)
• Early treatment of respiratory infections
• CSF leak repair if present
• Prophylactic antibiotics for CSF leak, basilar skull fracture, cochlear implants`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS MENINGITIS?
════════════════════════════════════════════════════════════════════════════════

Meningitis is an infection of the protective membranes (meninges) that surround the brain and spinal cord. Bacterial meningitis is a medical emergency and can be life-threatening. Quick treatment is essential.

════════════════════════════════════════════════════════════════════════════════
🔴 RECOGNIZE THE SYMPTOMS - SEEK EMERGENCY CARE
════════════════════════════════════════════════════════════════════════════════

In Adults and Older Children:
• Severe headache
• High fever
• Stiff neck (pain when bending chin to chest)
• Sensitivity to light
• Nausea and vomiting
• Confusion or difficulty concentrating
• Seizures
• Rash that doesn't fade when pressed (glass test)

In Babies and Young Children:
• High fever with cold hands and feet
• Refusing to feed
• Being irritable or floppy
• High-pitched cry
• Bulging fontanelle (soft spot on head)
• Pale or blotchy skin
• Stiff body or jerking movements

🔴 DON'T WAIT - Meningitis can get worse very quickly. Call emergency services immediately if you suspect meningitis.

════════════════════════════════════════════════════════════════════════════════
THE GLASS TEST (For Rash)
════════════════════════════════════════════════════════════════════════════════

If there is a rash:
• Press a clear glass firmly against the skin
• If the rash does NOT fade and you can still see it through the glass, it could be meningococcal septicemia
• 🔴 This is a medical emergency - Call for help immediately

Note: The rash may not appear or may appear late, so don't wait for a rash to seek help if other symptoms are present.

════════════════════════════════════════════════════════════════════════════════
AFTER MENINGITIS
════════════════════════════════════════════════════════════════════════════════

Recovery can take time. Many people make a full recovery, but some may have lasting effects:
• Hearing problems - Get regular hearing tests
• Memory and concentration difficulties
• Balance and coordination problems
• Epilepsy (seizures)
• Vision problems
• Emotional changes

FOLLOW-UP IS IMPORTANT:
• Attend all follow-up appointments
• Get hearing tested as recommended
• Tell your doctor about any new or worsening symptoms
• Be patient with recovery - It takes time

════════════════════════════════════════════════════════════════════════════════
PREVENTION
════════════════════════════════════════════════════════════════════════════════

• Vaccinations - Keep up to date with meningitis vaccines
• Good hygiene - Wash hands regularly
• Avoid sharing personal items (drinks, utensils, toothbrushes)
• If someone you know has meningitis, you may need preventive antibiotics - Contact your doctor

IF YOUR CLOSE CONTACT HAD MENINGOCOCCAL MENINGITIS:
• You may need preventive antibiotics
• Seek medical advice immediately
• Watch for symptoms in yourself and family members`,

    references: [
      'van de Beek D, Cabellos C, Dzupova O, et al. ESCMID guideline: diagnosis and treatment of acute bacterial meningitis. Clin Microbiol Infect. 2016;22(Suppl 3):S37-S62.',
      'Tunkel AR, Hartman BJ, Kaplan SL, et al. Practice Guidelines for the Management of Bacterial Meningitis. Clin Infect Dis. 2004;39(9):1267-1284.',
      'McGill F, Heyderman RS, Michael BD, et al. The UK joint specialist societies guideline on the diagnosis and management of acute meningitis and meningococcal sepsis in immunocompetent adults. J Infect. 2016;72(4):405-438.',
      'Brouwer MC, McIntyre P, Prasad K, van de Beek D. Corticosteroids for acute bacterial meningitis. Cochrane Database Syst Rev. 2015;(9):CD004405.',
      'de Gans J, van de Beek D; European Dexamethasone in Adulthood Bacterial Meningitis Study Investigators. Dexamethasone in adults with bacterial meningitis. N Engl J Med. 2002;347(20):1549-1556.',
      'van de Beek D, de Gans J, Spanjaard L, et al. Clinical features and prognostic factors in adults with bacterial meningitis. N Engl J Med. 2004;351(18):1849-1859.',
      'Thigpen MC, Whitney CG, Messonnier NE, et al. Bacterial meningitis in the United States, 1998-2007. N Engl J Med. 2011;364(21):2016-2025.',
      'Edmond K, Clark A, Korczak VS, et al. Global and regional risk of disabling sequelae from bacterial meningitis: a systematic review and meta-analysis. Lancet Infect Dis. 2010;10(5):317-328.',
      'Cohn AC, MacNeil JR, Clark TA, et al. Prevention and Control of Meningococcal Disease: Recommendations of the Advisory Committee on Immunization Practices (ACIP). MMWR Recomm Rep. 2013;62(RR-2):1-28.',
      'World Health Organization. Meningococcal meningitis: Fact sheet. WHO; 2023.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════
  // 10. ACUTE KIDNEY INJURY (AKI)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '10',
    name: 'Acute Kidney Injury (AKI)',
    slug: 'acute-kidney-injury',
    specialty: 'Nephrology',
    bodySystem: 'Renal',
    icdCode: 'N17.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute Kidney Injury (AKI) is a rapid decline in kidney function occurring over hours to days, characterized by accumulation of waste products (urea, creatinine) and/or decreased urine output. It is common in hospitalized patients and associated with significant morbidity and mortality.

════════════════════════════════════════════════════════════════════════════════

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


🔴 CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• CLASSIFY AS PRE-RENAL, INTRINSIC, OR POST-RENAL - Guides management
• Pre-renal (most common): Hypovolemia, hypotension - Usually reversible with fluids
• Post-renal: Obstruction - Needs urgent relief (catheter, nephrostomy)
• STOP NEPHROTOXINS: NSAIDs, ACE-I/ARBs, Aminoglycosides, Contrast, Metformin
• Check for URINARY OBSTRUCTION early - Bladder scan, catheterization, ultrasound
• KDIGO staging: Stage 1 (Cr ↑1.5-1.9x), Stage 2 (Cr ↑2-2.9x), Stage 3 (Cr ↑≥3x or ≥4.0 or RRT)
• LIFE-THREATENING complications requiring emergent dialysis: Refractory hyperkalemia, severe acidosis, pulmonary edema, uremic encephalopathy/pericarditis
• Avoid IV contrast if possible; if needed, use low-osmolar and hydrate
• Monitor potassium closely - Hyperkalemia can be fatal
• Most AKI is reversible if cause identified and treated early

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

PRE-RENAL (60-70%):
• Decreased renal perfusion (hypovolemia, heart failure, sepsis, hemorrhage)
• Kidneys structurally normal but underperfused
• GFR falls due to decreased perfusion pressure
• Usually reversible with restoration of perfusion

INTRINSIC RENAL (25-40%):
• Acute Tubular Necrosis (ATN) - Most common (ischemic or nephrotoxic)
• Acute Interstitial Nephritis (AIN) - Drug-induced, infection
• Glomerulonephritis - Immune-mediated
• Vascular - Vasculitis, malignant HTN, TTP/HUS

POST-RENAL (5-10%):
• Obstruction of urinary outflow
• BPH, stones, tumors, strictures, neurogenic bladder
• Bilateral obstruction (or unilateral in single kidney)
• Increased intratubular pressure → Decreased GFR`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• Affects 10-15% of hospitalized patients
• Up to 50% of ICU patients develop AKI
• Associated with 4-fold increased mortality in hospitalized patients
• Hospital mortality: Stage 1: 10%, Stage 2: 20%, Stage 3: 30-40%
• 20-50% of AKI survivors have CKD at 1 year
• Increasing incidence due to aging population and comorbidities

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Higher incidence in low-resource settings due to:
  - Delayed presentation
  - Limited fluid resuscitation
  - Infectious diseases (malaria, diarrheal diseases, sepsis)
  - Traditional/herbal medicine nephrotoxicity
• Community-acquired AKI more common (vs hospital-acquired in high-income)
• Common causes: Sepsis, malaria, obstetric complications (eclampsia, septic abortion)
• Limited access to dialysis - High mortality for dialysis-requiring AKI
• Younger population affected (median age 30-40 vs 60-70 in high-income)

════════════════════════════════════════════════════════════════════════════════
COMMON CAUSES
════════════════════════════════════════════════════════════════════════════════

PRE-RENAL:
• Hypovolemia: Hemorrhage, vomiting, diarrhea, burns, diuretics
• Decreased cardiac output: Heart failure, MI, tamponade
• Systemic vasodilation: Sepsis, anaphylaxis, liver failure
• Renal vasoconstriction: NSAIDs, ACE-I/ARBs, hypercalcemia, hepatorenal syndrome
• Abdominal compartment syndrome

INTRINSIC:
• ATN - Ischemic: Prolonged pre-renal, major surgery, shock
• ATN - Nephrotoxic: Aminoglycosides, contrast, cisplatin, rhabdomyolysis, hemolysis
• AIN: NSAIDs, penicillins, PPIs, allopurinol, infections
• Glomerulonephritis: Post-infectious, RPGN, lupus nephritis
• Vascular: Vasculitis, malignant HTN, TTP/HUS, renal artery occlusion

POST-RENAL:
• BPH (most common in elderly men)
• Ureteric stones/obstruction (bilateral or single kidney)
• Pelvic malignancy (cervical, prostate, bladder)
• Retroperitoneal fibrosis
• Neurogenic bladder

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

• Pre-existing CKD (strongest risk factor)
• Age >65 years
• Diabetes mellitus
• Hypertension
• Heart failure
• Liver disease
• Sepsis
• Major surgery (especially cardiac, vascular)
• Nephrotoxic medications
• Volume depletion
• Contrast exposure
• Rhabdomyolysis`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

AKI may be ASYMPTOMATIC and detected only on labs!

When symptomatic:
• Decreased urine output (oliguria <400 mL/day or anuria <100 mL/day)
• Fatigue, weakness, malaise
• Nausea, vomiting, anorexia
• Confusion, altered mental status (uremic encephalopathy)
• Shortness of breath (pulmonary edema)
• Chest pain (uremic pericarditis)
• Peripheral edema
• Pruritus
• Bleeding tendency

SYMPTOMS OF UNDERLYING CAUSE:
• Pre-renal: Thirst, dizziness, postural symptoms
• Post-renal: Lower abdominal pain, incomplete voiding, history of BPH/stones
• Intrinsic: Flank pain, hematuria, rash, joint pain (depending on cause)

════════════════════════════════════════════════════════════════════════════════
SIGNS
════════════════════════════════════════════════════════════════════════════════

VOLUME STATUS - Critical to assess:

HYPOVOLEMIA (Pre-renal):
• Dry mucous membranes
• Decreased skin turgor
• Sunken eyes
• Tachycardia
• Hypotension (orthostatic or absolute)
• Cool extremities
• Flat neck veins

HYPERVOLEMIA (Fluid overload):
• Elevated JVP
• Peripheral edema
• Pulmonary crackles
• S3 gallop
• Ascites

OTHER SIGNS:
• Palpable bladder (post-renal obstruction)
• Prostatic enlargement (DRE)
• Asterixis (uremic flap)
• Pericardial friction rub (uremic pericarditis)
• Rash (drug-induced AIN, vasculitis)
• Livedo reticularis (atheroembolic disease)

════════════════════════════════════════════════════════════════════════════════
URINE OUTPUT CLASSIFICATION
════════════════════════════════════════════════════════════════════════════════

• Non-oliguric AKI: >400 mL/day - More common, better prognosis
• Oliguric AKI: <400 mL/day (or <0.5 mL/kg/hr)
• Anuric AKI: <100 mL/day - More severe, consider obstruction or cortical necrosis

Note: Normal creatinine can mask AKI in muscle-wasting, elderly, or those with low muscle mass`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
KDIGO DEFINITION AND STAGING OF AKI
════════════════════════════════════════════════════════════════════════════════

AKI is defined as any of:
• Increase in serum creatinine ≥0.3 mg/dL (≥26.5 μmol/L) within 48 hours
• Increase in serum creatinine ≥1.5x baseline within 7 days
• Urine output <0.5 mL/kg/hr for 6 hours

KDIGO STAGING:
┌─────────┬────────────────────────────────────────────┬─────────────────────────┐
│ Stage   │ Serum Creatinine                           │ Urine Output            │
├─────────┼────────────────────────────────────────────┼─────────────────────────┤
│ Stage 1 │ ↑ 1.5-1.9x baseline OR ↑ ≥0.3 mg/dL       │ <0.5 mL/kg/h for 6-12h  │
│ Stage 2 │ ↑ 2.0-2.9x baseline                        │ <0.5 mL/kg/h for ≥12h   │
│ Stage 3 │ ↑ ≥3x baseline OR ≥4.0 mg/dL OR RRT       │ <0.3 mL/kg/h for ≥24h   │
│         │                                            │ OR anuria for ≥12h      │
└─────────┴────────────────────────────────────────────┴─────────────────────────┘

════════════════════════════════════════════════════════════════════════════════
DISTINGUISHING PRE-RENAL FROM INTRINSIC (ATN)
════════════════════════════════════════════════════════════════════════════════

                        PRE-RENAL           ATN (Intrinsic)
─────────────────────────────────────────────────────────────
BUN:Creatinine ratio    >20:1               10-15:1
Urine Sodium            <20 mEq/L           >40 mEq/L
FENa (fractional Na)    <1%                 >2%
Urine Osmolality        >500 mOsm/kg        <350 mOsm/kg
Urine specific gravity  >1.020              ~1.010
Urine sediment          Bland, hyaline      Muddy brown casts
                        casts               (ATN)
Response to fluids      Creatinine improves Creatinine doesn't
                        within 24-72h       improve

FENa = (Urine Na × Serum Cr) / (Serum Na × Urine Cr) × 100

🔴 FENa unreliable if: Diuretics given, contrast, myoglobinuria, CKD

════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
════════════════════════════════════════════════════════════════════════════════

1. CONFIRM AKI - Compare to baseline creatinine
2. ASSESS VOLUME STATUS - Clinical examination
3. RULE OUT OBSTRUCTION - Bladder scan, renal ultrasound
4. REVIEW MEDICATIONS - Stop nephrotoxins
5. URINALYSIS and URINE SEDIMENT - Look for casts, protein, blood
6. CALCULATE FENa if diagnosis unclear
7. Further workup based on suspected cause

════════════════════════════════════════════════════════════════════════════════
URINE SEDIMENT FINDINGS
════════════════════════════════════════════════════════════════════════════════

• Bland sediment, hyaline casts → Pre-renal
• Muddy brown granular casts → ATN
• WBC casts, eosinophils → AIN (drug-induced)
• RBC casts → Glomerulonephritis
• Crystals → Crystal nephropathy (uric acid, calcium oxalate)
• Myoglobin → Rhabdomyolysis`,

    differentialDiagnosis: [
      'Pre-renal azotemia - Hypovolemia, heart failure, sepsis; improves with fluids; FENa <1%',
      'Acute Tubular Necrosis (ATN) - Ischemic or nephrotoxic; muddy brown casts; FENa >2%',
      'Acute Interstitial Nephritis - Drug-induced (NSAIDs, antibiotics); fever, rash, eosinophiluria',
      'Post-renal obstruction - Bilateral ureteric or bladder outlet; hydronephrosis on imaging',
      'Glomerulonephritis - RBC casts, proteinuria; may need biopsy',
      'Atheroembolic disease - Post-procedure, livedo reticularis, eosinophilia',
      'Rhabdomyolysis - Elevated CK (>5x normal), myoglobinuria, trauma/crush/seizures',
      'Contrast-induced nephropathy - Creatinine rises 24-72h post-contrast, peaks 3-5 days',
      'Hepatorenal syndrome - Cirrhosis with ascites, low urine sodium, poor prognosis',
      'Thrombotic microangiopathy (TTP/HUS) - Thrombocytopenia, hemolytic anemia, schistocytes'
    ],

    investigations: [
      {
        name: 'Serum Creatinine (Serial)',
        indication: '🔴 Diagnose and stage AKI - Compare to baseline if available. Monitor every 12-24 hours',
        availability: 'BASIC'
      },
      {
        name: 'Blood Urea Nitrogen (BUN)',
        indication: 'BUN:Cr ratio >20:1 suggests pre-renal; Also indicates degree of uremia',
        availability: 'BASIC'
      },
      {
        name: 'Serum Potassium',
        indication: '🔴 CRITICAL - Hyperkalemia is life-threatening. Monitor frequently (every 4-6 hours if elevated)',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Na, K, Cl, HCO3, Ca, Phos)',
        indication: 'Assess electrolyte derangements, acidosis. Hypocalcemia, hyperphosphatemia common',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'If metabolic acidosis suspected - High anion gap metabolic acidosis in uremia',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Anemia (dilutional or hemolytic), thrombocytopenia (TTP/HUS), leukocytosis (sepsis)',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🔴 Essential - Proteinuria, hematuria, pyuria; Specific gravity for concentration',
        availability: 'BASIC'
      },
      {
        name: 'Urine Sediment Microscopy',
        indication: '🔴 Look for casts: Muddy brown (ATN), WBC (AIN), RBC (GN), Crystals',
        availability: 'BASIC'
      },
      {
        name: 'Urine Sodium and Creatinine (for FENa)',
        indication: 'Calculate FENa to distinguish pre-renal (<1%) from ATN (>2%)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Bladder Scan / Post-Void Residual',
        indication: '🔴 Rule out urinary retention - If >300-400 mL, consider catheterization',
        availability: 'BASIC'
      },
      {
        name: 'Renal Ultrasound',
        indication: '🔴 All AKI patients - Rule out obstruction (hydronephrosis), assess kidney size',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🔴 If potassium elevated - Look for peaked T waves, widened QRS, sine wave',
        availability: 'BASIC'
      },
      {
        name: 'Creatine Kinase (CK)',
        indication: 'If rhabdomyolysis suspected - CK >5x normal diagnostic; often >10,000',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urine Myoglobin',
        indication: 'Rhabdomyolysis - Positive dipstick for blood with few RBCs on microscopy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complement levels (C3, C4)',
        indication: 'If glomerulonephritis suspected - Low in lupus, post-infectious, MPGN',
        availability: 'ADVANCED'
      },
      {
        name: 'ANA, Anti-dsDNA, ANCA',
        indication: 'If autoimmune/vasculitis suspected - Lupus, GPA, MPA',
        availability: 'ADVANCED'
      },
      {
        name: 'Renal Biopsy',
        indication: 'When diagnosis unclear, suspected GN or AIN, prolonged AKI without recovery',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS VOLUME STATUS - Hypovolemic? Euvolemic? Hypervolemic?',
        '⚡ STOP NEPHROTOXIC DRUGS - NSAIDs, ACE-I/ARBs, Aminoglycosides, Metformin, Contrast',
        '⚡ CHECK FOR OBSTRUCTION - Bladder scan, ultrasound; Catheterize if retention',
        '⚡ CHECK POTASSIUM - Treat hyperkalemia urgently if present',
        '⚡ ECG if potassium >6.0 mEq/L - Look for peaked T waves, QRS widening',
        '⚡ IV FLUIDS if pre-renal (hypovolemic) - Crystalloid bolus, reassess',
        '⚡ DIURETICS if hypervolemic - Furosemide for pulmonary edema',
        '⚡ Review indications for EMERGENT DIALYSIS',
        'Adjust medication doses for reduced renal function',
        'Monitor urine output (insert catheter if needed for accurate measurement)',
        'Avoid further nephrotoxic insults (contrast, NSAIDs)',
        'Treat underlying cause (sepsis, heart failure, obstruction)',
        'Daily weights, strict I/O monitoring',
        'Nephrology consult for: Stage 3 AKI, unclear diagnosis, dialysis candidate'
      ],
      pharmacological: [
        {
          drug: 'IV Crystalloid Fluids (Normal Saline or Balanced)',
          indication: '🔴 PRE-RENAL AKI with hypovolemia - First-line treatment',
          dose: 'Bolus 500-1000 mL, then reassess. May need 2-3 L or more depending on deficit',
          route: 'IV',
          duration: 'Until euvolemic and creatinine improving',
          contraindications: ['Volume overload', 'Pulmonary edema', 'Severe heart failure'],
          sideEffects: ['Fluid overload if excessive', 'Hyperchloremic acidosis (with NS)'],
          alternatives: ['Balanced crystalloids (LR, Plasmalyte) may be preferred over NS']
        },
        {
          drug: 'Furosemide (Loop Diuretic)',
          indication: '🔴 HYPERVOLEMIC AKI - Pulmonary edema, volume overload',
          dose: 'IV: 40-80 mg bolus (higher if CKD/diuretic-resistant). May need infusion 5-20 mg/hr',
          route: 'IV (more reliable than PO in AKI)',
          duration: 'Until euvolemic',
          contraindications: ['Hypovolemia', 'Anuria (will not work)'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Ototoxicity (high doses)', 'Hypotension'],
          alternatives: ['Bumetanide 1-2 mg IV', 'Add thiazide for diuretic resistance (metolazone)']
        },
        {
          drug: 'Calcium Gluconate (Hyperkalemia)',
          indication: '🔴 EMERGENCY - K+ >6.5 OR ECG changes (peaked T, widened QRS). Stabilizes cardiac membrane',
          dose: '10 mL of 10% calcium gluconate IV over 2-3 minutes. May repeat in 5 min if ECG still abnormal',
          route: 'IV (preferably central line for calcium chloride)',
          duration: 'Single dose (effect lasts 30-60 min) - Must give other treatments to lower K+',
          contraindications: ['Digoxin toxicity (relative - give slowly over 20-30 min)'],
          sideEffects: ['Bradycardia if given too fast', 'Tissue necrosis if extravasation'],
          alternatives: ['Calcium chloride 10 mL of 10% (3x more calcium, but more caustic - use central line)']
        },
        {
          drug: 'Insulin + Dextrose (Hyperkalemia)',
          indication: '🔴 K+ >6.0 - Shifts potassium into cells',
          dose: 'Regular insulin 10 units IV + Dextrose 50% 50 mL (25g) IV. Check glucose before and after',
          route: 'IV',
          duration: 'Effect lasts 4-6 hours; may need to repeat',
          contraindications: ['Hypoglycemia (give dextrose anyway, monitor closely)'],
          sideEffects: ['Hypoglycemia (30% risk) - Monitor glucose hourly x 4-6 hours'],
          alternatives: ['Increase dextrose if already hyperglycemic']
        },
        {
          drug: 'Sodium Bicarbonate (Hyperkalemia/Acidosis)',
          indication: 'Metabolic acidosis (pH <7.2, HCO3 <15) OR as adjunct for hyperkalemia',
          dose: '50-100 mEq IV over 30-60 min. Or infusion 150 mEq in 1L D5W',
          route: 'IV',
          duration: 'Until acidosis corrected or dialysis',
          contraindications: ['Volume overload (high sodium load)', 'Hypocalcemia (worsens)'],
          sideEffects: ['Volume overload', 'Hypokalemia (shifts K+ into cells)', 'Hypocalcemia', 'Metabolic alkalosis'],
          alternatives: ['Dialysis if severe acidosis']
        },
        {
          drug: 'Salbutamol (Albuterol) Nebulized (Hyperkalemia)',
          indication: 'Adjunct for hyperkalemia - Shifts potassium into cells',
          dose: '10-20 mg nebulized over 10-15 min',
          route: 'Nebulized',
          duration: 'Effect lasts 2-4 hours',
          contraindications: ['Tachyarrhythmias', 'Ischemic heart disease (relative)'],
          sideEffects: ['Tachycardia', 'Tremor', 'Less effective in ESRD'],
          alternatives: ['IV salbutamol 0.5 mg over 15 min']
        },
        {
          drug: 'Sodium Polystyrene Sulfonate (Kayexalate)',
          indication: 'Non-emergent hyperkalemia - Removes potassium from body (slow onset)',
          dose: '15-30 g PO with sorbitol, OR 30-50 g rectal enema',
          route: 'Oral or rectal',
          duration: 'Can repeat every 4-6 hours; effect takes 4-6 hours',
          contraindications: ['Bowel obstruction', 'Post-operative ileus', 'Concurrent sorbitol in some settings (bowel necrosis risk)'],
          sideEffects: ['Constipation', 'Hypokalemia', 'Bowel necrosis (rare but serious)', 'Sodium loading'],
          alternatives: ['Patiromer 8.4 g daily', 'Sodium zirconium cyclosilicate (Lokelma)']
        },
        {
          drug: 'Dopamine (Low-Dose "Renal Dose")',
          indication: '🚫 NOT RECOMMENDED - No evidence of benefit for AKI prevention or treatment',
          dose: 'N/A - Avoid',
          route: 'N/A',
          duration: 'N/A',
          contraindications: ['Should not be used for "renal protection"'],
          sideEffects: ['Tachycardia', 'Arrhythmias', 'Ischemia'],
          alternatives: ['Address underlying cause (fluids for pre-renal, treat sepsis, etc.)']
        },
        {
          drug: 'N-Acetylcysteine (NAC)',
          indication: '🟡 Controversial - Some use for contrast-induced nephropathy prevention',
          dose: '1200 mg PO twice daily on day before and day of contrast',
          route: 'Oral or IV',
          duration: '2 days total',
          contraindications: ['None significant'],
          sideEffects: ['Nausea', 'Vomiting', 'Anaphylactoid reaction (IV)'],
          alternatives: ['Adequate hydration is more important than NAC']
        }
      ],
      nonPharmacological: [
        'Strict intake/output monitoring - Insert Foley catheter if needed',
        'Daily weights - Trend to assess fluid status',
        'Fluid restriction if hypervolemic (e.g., 1-1.5 L/day)',
        'Low potassium diet (<2 g/day) if hyperkalemia',
        'Low protein diet controversial - Avoid in acutely ill patients',
        'Avoid nephrotoxic medications and adjust drug doses',
        'Relieve obstruction: Urinary catheter, ureteric stents, nephrostomy',
        'DVT prophylaxis (mechanical if bleeding risk)',
        'Nutritional support - Adequate calories, protein if not on dialysis',
        'Early nephrology involvement for AKI stage 2-3'
      ],
      surgical: [
        'Urinary catheter placement for bladder outlet obstruction',
        'Ureteric stent placement for ureteric obstruction (stones, tumors)',
        'Percutaneous nephrostomy for obstructive uropathy',
        'Relief of abdominal compartment syndrome',
        'Dialysis access (temporary catheter for acute HD or PD)'
      ],
      referralCriteria: [
        '🔴 EMERGENT DIALYSIS indications (AEIOU): Acidosis (refractory), Electrolytes (hyperkalemia refractory), Intoxication (dialyzable toxins), Overload (pulmonary edema refractory), Uremia (encephalopathy, pericarditis)',
        '🔴 AKI Stage 3 - Nephrology consult',
        '🔴 AKI with unclear etiology - May need biopsy',
        '🔴 AKI not recovering within 48-72 hours despite treatment',
        '🔴 AKI requiring dialysis',
        'Suspected glomerulonephritis or vasculitis',
        'Post-renal obstruction requiring urology intervention',
        'Consider ICU for severe AKI with multiorgan dysfunction'
      ],
      monitoring: [
        'Serum creatinine: Every 12-24 hours until stable',
        'Serum potassium: Every 4-6 hours if elevated; daily when stable',
        'Urine output: Hourly if critically ill, otherwise every 4-6 hours',
        'Fluid balance: Strict I/O charting, daily weights',
        'Vital signs: Assess volume status (BP, HR, JVP)',
        'Electrolytes (Na, K, Ca, Phos, HCO3): Daily',
        'ECG: If potassium >6.0 or symptoms of hyperkalemia',
        'Renal ultrasound: If obstruction suspected or diagnosis unclear'
      ],
      followUp: `AKI RECOVERY MONITORING:
• Most pre-renal AKI recovers within 24-72 hours of fluid resuscitation
• ATN recovery typically 1-3 weeks (may be longer)
• Persistent AKI >7 days without recovery warrants further workup

BEFORE DISCHARGE:
• Ensure creatinine is stable or trending toward baseline
• Identify and document cause of AKI
• Review and stop nephrotoxic medications
• Educate patient on nephrotoxin avoidance (NSAIDs, contrast)
• Arrange follow-up creatinine check

POST-DISCHARGE FOLLOW-UP:
• Repeat creatinine at 1-2 weeks, then 3 months
• 20-50% of AKI survivors develop CKD
• Monitor for proteinuria
• Aggressive BP and diabetes control
• Avoid nephrotoxins long-term
• Nephrology follow-up if: Incomplete recovery, CKD development, required dialysis`
    },

    redFlags: [
      '🔴 CRITICAL: Hyperkalemia >6.5 mEq/L or ECG changes (peaked T, wide QRS) - Calcium gluconate, insulin/dextrose, prepare for dialysis',
      '🔴 CRITICAL: Pulmonary edema not responding to diuretics - Urgent dialysis/ultrafiltration',
      '🔴 CRITICAL: Severe metabolic acidosis (pH <7.1) refractory to bicarbonate - Dialysis indication',
      '🔴 CRITICAL: Uremic encephalopathy (confusion, asterixis, seizures) - Urgent dialysis',
      '🔴 CRITICAL: Uremic pericarditis (friction rub, chest pain) - Urgent dialysis to prevent tamponade',
      '🔴 CRITICAL: Anuria with bilateral hydronephrosis - Urgent decompression (nephrostomy, stents)',
      '🔴 CRITICAL: Rapidly rising creatinine (>0.5 mg/dL per day) - Aggressive workup and treatment',
      '🟠 URGENT: No improvement after 48-72 hours of appropriate treatment - Reassess diagnosis, consider nephrology consult',
      '🟠 URGENT: AKI with active urine sediment (RBC casts) - Possible rapidly progressive GN, urgent workup',
      '🟠 URGENT: AKI with hemolysis and thrombocytopenia - Consider TTP/HUS, avoid platelet transfusion',
      '🟠 URGENT: AKI post-contrast - Hydration, avoid further contrast, monitor for recovery',
      '🟠 URGENT: Suspected rhabdomyolysis (CK >10,000) - Aggressive IV fluids (200-300 mL/hr target), monitor for compartment syndrome'
    ],

    complications: [
      'Hyperkalemia - Most dangerous acute complication; can cause fatal arrhythmias',
      'Metabolic acidosis - High anion gap from retained acids',
      'Fluid overload and pulmonary edema - If oliguric and receiving fluids',
      'Uremic encephalopathy - Confusion, asterixis, seizures, coma',
      'Uremic pericarditis - Can lead to tamponade; indication for dialysis',
      'Uremic bleeding - Platelet dysfunction from uremia',
      'Infection - AKI patients are immunocompromised',
      'Malnutrition - Catabolic state, protein loss',
      'GI bleeding - Stress ulcers, uremic gastritis',
      'Anemia - Decreased erythropoietin, blood loss',
      'CKD progression - 20-50% of AKI survivors develop CKD',
      'Cardiovascular events - Increased MI and stroke risk after AKI',
      'Death - Hospital mortality 20-50% in severe AKI'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY
════════════════════════════════════════════════════════════════════════════════

HOSPITAL MORTALITY BY STAGE:
• Stage 1: ~10%
• Stage 2: ~20%
• Stage 3: ~30-40%
• Dialysis-requiring AKI: ~50-60%

RISK FACTORS FOR DEATH:
• Older age
• Sepsis as cause
• Oliguria/Anuria
• Need for dialysis
• Multi-organ failure
• Pre-existing CKD
• Delayed treatment

════════════════════════════════════════════════════════════════════════════════
RECOVERY
════════════════════════════════════════════════════════════════════════════════

• Pre-renal: Usually reversible within 24-72 hours if treated early
• ATN: Recovery typically 1-3 weeks; may take longer
• Complete recovery: 50-70% of survivors
• Partial recovery with CKD: 20-40%
• Dialysis-dependence: 5-15% of survivors

════════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
════════════════════════════════════════════════════════════════════════════════

After AKI episode:
• Increased risk of CKD: 3-fold higher
• Increased risk of ESRD: 12-fold higher
• Increased cardiovascular events
• Increased mortality for years after AKI
• Need for long-term monitoring even if apparently recovered`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION OF AKI
════════════════════════════════════════════════════════════════════════════════

IDENTIFY HIGH-RISK PATIENTS:
• Pre-existing CKD (eGFR <60)
• Age >65
• Diabetes
• Heart failure
• Liver disease
• Hypovolemia
• Sepsis
• Nephrotoxic medications

GENERAL MEASURES:
• Maintain euvolemia - Avoid both dehydration and overhydration
• Avoid nephrotoxins when possible
• Dose-adjust medications for renal function
• Monitor renal function in high-risk patients

════════════════════════════════════════════════════════════════════════════════
PREVENT CONTRAST-INDUCED NEPHROPATHY
════════════════════════════════════════════════════════════════════════════════

• Use lowest possible contrast volume
• Use iso-osmolar or low-osmolar contrast
• Hydration: NS or NaHCO3 1-1.5 mL/kg/hr for 6-12 hours before and after
• Hold metformin 48 hours before and after contrast
• Consider alternatives to contrast (MRI, ultrasound)
• NAC controversial - May use 1200mg PO BID day before and day of

════════════════════════════════════════════════════════════════════════════════
MEDICATION PRECAUTIONS
════════════════════════════════════════════════════════════════════════════════

HIGH-RISK MEDICATIONS - Use with caution:
• NSAIDs - Avoid if possible in CKD/high-risk
• ACE-I/ARBs - Hold during acute illness, dehydration
• Aminoglycosides - Dose adjust, monitor levels
• Vancomycin - Dose adjust, monitor trough
• Amphotericin B - Consider liposomal formulation
• Lithium - Monitor levels, ensure hydration
• Metformin - Hold with contrast, acute illness`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
WHAT IS ACUTE KIDNEY INJURY?
════════════════════════════════════════════════════════════════════════════════

Acute Kidney Injury (AKI) means your kidneys have suddenly stopped working as well as they should. Your kidneys filter waste and extra fluid from your blood. When they are injured, these wastes build up and can make you very sick.

AKI can often be treated if caught early. Many people recover completely, but some may have lasting kidney damage.

════════════════════════════════════════════════════════════════════════════════
CAUSES OF AKI
════════════════════════════════════════════════════════════════════════════════

• Not drinking enough fluids (dehydration)
• Severe diarrhea or vomiting
• Blood loss
• Infections (sepsis)
• Certain medications (pain killers like ibuprofen, some antibiotics)
• Blockage of urine flow (enlarged prostate, kidney stones)
• Severe illnesses that affect blood flow to kidneys

════════════════════════════════════════════════════════════════════════════════
WARNING SIGNS
════════════════════════════════════════════════════════════════════════════════

Seek medical attention if you have:
• Much less urine than usual, or none at all
• Swelling in your legs, ankles, or feet
• Shortness of breath
• Feeling very tired or confused
• Nausea or vomiting
• Chest pain or pressure

════════════════════════════════════════════════════════════════════════════════
PROTECT YOUR KIDNEYS
════════════════════════════════════════════════════════════════════════════════

DO:
• Drink plenty of water, especially when sick or in hot weather
• Take medications only as prescribed
• Keep follow-up appointments
• Control blood pressure and diabetes
• Tell all your doctors you had kidney injury

DON'T:
• Take NSAIDs (ibuprofen, naproxen, diclofenac) without doctor advice
• Use herbal supplements without checking with your doctor
• Skip medications for blood pressure or diabetes
• Ignore symptoms of dehydration or infection

════════════════════════════════════════════════════════════════════════════════
AFTER AKI
════════════════════════════════════════════════════════════════════════════════

• Your kidneys may have recovered, but you need ongoing monitoring
• You are at higher risk for kidney problems in the future
• Get your kidney function tested regularly
• Avoid medications that can harm kidneys
• Stay well hydrated
• Keep blood pressure and blood sugar under control
• If you need any procedures or imaging, tell them you had AKI`,

    references: [
      'Kellum JA, Lameire N; KDIGO AKI Guideline Work Group. Diagnosis, evaluation, and management of acute kidney injury: a KDIGO summary (Part 1). Crit Care. 2013;17(1):204.',
      'Khwaja A. KDIGO Clinical Practice Guidelines for Acute Kidney Injury. Nephron Clin Pract. 2012;120(4):c179-c184.',
      'Ronco C, Bellomo R, Kellum JA. Acute kidney injury. Lancet. 2019;394(10212):1949-1964.',
      'Mehta RL, Kellum JA, Shah SV, et al. Acute Kidney Injury Network: report of an initiative to improve outcomes in acute kidney injury. Crit Care. 2007;11(2):R31.',
      'Bellomo R, Ronco C, Kellum JA, et al. Acute renal failure - definition, outcome measures, animal models, fluid therapy and information technology needs: the Second International Consensus Conference of the Acute Dialysis Quality Initiative (ADQI) Group. Crit Care. 2004;8(4):R204-R212.',
      'Chawla LS, Bellomo R, Bihorac A, et al. Acute kidney disease and renal recovery: consensus report of the Acute Disease Quality Initiative (ADQI) 16 Workgroup. Nat Rev Nephrol. 2017;13(4):241-257.',
      'Hoste EAJ, Bagshaw SM, Bellomo R, et al. Epidemiology of acute kidney injury in critically ill patients: the multinational AKI-EPI study. Intensive Care Med. 2015;41(8):1411-1423.',
      'Lewington AJP, Cerdá J, Mehta RL. Raising awareness of acute kidney injury: a global perspective of a silent killer. Kidney Int. 2013;84(3):457-467.',
      'Vanmassenhove J, Kielstein J, Jörres A, Biesen WV. Management of patients at risk of acute kidney injury. Lancet. 2017;389(10084):2139-2151.',
      'Perazella MA. Drug-induced acute kidney injury: diverse mechanisms of tubular injury. Curr Opin Crit Care. 2019;25(6):550-557.'
    ],

    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 11. PNEUMONIA (COMMUNITY-ACQUIRED)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '11',
    name: 'Pneumonia (Community-Acquired)',
    slug: 'community-acquired-pneumonia',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'J18.9',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Community-acquired pneumonia (CAP) is an acute infection of the pulmonary parenchyma acquired outside of hospital or healthcare settings, characterized by symptoms of acute lower respiratory tract infection with radiographic evidence of consolidation. CAP remains a leading cause of morbidity and mortality globally, particularly in sub-Saharan Africa where it disproportionately affects children under 5 years, elderly populations, and immunocompromised individuals including those living with HIV/AIDS.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• CURB-65 SCORE determines severity and disposition: Confusion, Urea >7mmol/L, RR ≥30, BP <90/60, Age ≥65 → Score 0-1: outpatient; 2: consider admission; 3-5: severe CAP, consider ICU

• Streptococcus pneumoniae remains the most common bacterial pathogen globally, but atypical pathogens (Mycoplasma, Legionella, Chlamydophila) cause 20-40% of cases

• In Africa, consider TB in any pneumonia not responding to standard antibiotics within 48-72 hours, especially with HIV co-infection, weight loss, or prolonged symptoms

• Empiric antibiotic therapy must cover typical AND atypical pathogens: β-lactam + macrolide OR respiratory fluoroquinolone monotherapy

• HIV-positive patients: ALWAYS consider Pneumocystis jirovecii pneumonia (PCP) if CD4 <200 cells/μL - requires cotrimoxazole, NOT standard CAP antibiotics

• Time to first antibiotic dose impacts mortality - administer within 4 hours of presentation (ideally within 1 hour for severe CAP)

• Chest X-ray is the gold standard for diagnosis - consolidation confirms pneumonia; however, clinical diagnosis may be necessary in resource-limited settings

• Adequate oxygenation is critical: target SpO₂ ≥92% (≥88% in COPD patients at risk of hypercapnia)

• Blood cultures should be obtained BEFORE antibiotics in all hospitalized patients, but NEVER delay antibiotics for cultures

• De-escalation to narrow-spectrum antibiotics based on culture results reduces resistance and adverse effects

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

Community-acquired pneumonia develops through a sequence of events beginning with pathogen entry into the lower respiratory tract. The primary routes include:

1. ASPIRATION of oropharyngeal secretions (most common mechanism)
2. INHALATION of aerosolized droplets containing pathogens
3. HEMATOGENOUS SPREAD from distant infection sites (rare)
4. DIRECT EXTENSION from contiguous infection

STAGES OF LOBAR PNEUMONIA (Classic Pneumococcal Pattern):

Stage 1 - CONGESTION (Days 1-2): Vascular engorgement, alveolar edema, bacterial proliferation. Lung appears heavy, red, and boggy.

Stage 2 - RED HEPATIZATION (Days 2-4): Massive cellular exudation with RBCs, neutrophils, and fibrin filling alveoli. Lung consistency resembles liver.

Stage 3 - GREY HEPATIZATION (Days 4-6): RBC lysis and degradation, continued fibrin and neutrophil accumulation. Greyish-brown appearance.

Stage 4 - RESOLUTION (Day 8+): Enzymatic digestion of exudate, macrophage clearance, restoration of normal architecture. Complete resolution in uncomplicated cases.

BRONCHOPNEUMONIA PATTERN: Patchy consolidation centered on bronchioles, often bilateral and multilobar. More common with S. aureus, Gram-negative organisms, and aspiration.

IMMUNE RESPONSE: Alveolar macrophages provide first-line defense. Failure to contain infection triggers cytokine release (IL-1, IL-6, TNF-α), neutrophil recruitment, and the systemic inflammatory response manifesting as fever, malaise, and elevated inflammatory markers.

GAS EXCHANGE IMPAIRMENT: Consolidation creates ventilation-perfusion mismatch (perfused but unventilated alveoli = shunt physiology), resulting in hypoxemia. Hypercapnia is less common due to compensatory hyperventilation of unaffected lung regions.`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• Lower respiratory infections including pneumonia cause approximately 2.5 million deaths annually worldwide, ranking as the 4th leading cause of death globally

• CAP incidence: 1.5-14 cases per 1,000 person-years in adults, increasing dramatically with age (>65 years: 25-44 per 1,000 person-years)

• Hospitalization rates: 20-35% of CAP cases require hospital admission; 5-10% of hospitalized patients require ICU care

• Case fatality rates: Outpatient CAP <1%; hospitalized non-ICU 5-15%; ICU-admitted severe CAP 25-50%

• Pneumococcal disease causes an estimated 1.6 million deaths annually, with highest burden in children <5 years and adults >65 years

• Economic burden: CAP accounts for over $17 billion in direct healthcare costs annually in the United States alone

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Sub-Saharan Africa bears a disproportionate burden: pneumonia causes approximately 800,000 deaths annually in Africa, with the highest mortality rates globally

• PEDIATRIC BURDEN: Pneumonia is the leading infectious cause of death in African children, causing approximately 490,000 deaths in children under 5 years annually

• HIV CO-INFECTION dramatically increases CAP risk: HIV-positive adults have 5-10 times higher pneumonia incidence than HIV-negative individuals

• Pneumocystis jirovecii pneumonia (PCP) accounts for 20-40% of pneumonia in HIV-positive patients with CD4 <200 cells/μL in Africa

• TUBERCULOSIS consideration is essential: TB presents as pneumonia in 15-25% of HIV-positive patients with respiratory symptoms in high-burden settings

• Streptococcus pneumoniae remains the predominant pathogen (30-50% of bacterial CAP), but Klebsiella pneumoniae is more common in Africa than Western countries

• Antibiotic resistance is a growing concern: pneumococcal penicillin resistance rates of 15-35% reported across African regions

• Access to care challenges: Many patients present late with severe disease due to geographic, financial, and healthcare infrastructure barriers

• Malnutrition increases pneumonia mortality 2-3 fold, particularly relevant in food-insecure regions

• Indoor air pollution from biomass fuel cooking increases pneumonia risk 1.8-fold, affecting over 80% of rural African households

• Vaccine coverage gaps: Pneumococcal conjugate vaccine (PCV) coverage varies widely (20-90%) across African countries

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

HOST FACTORS:
• Age extremes (<5 years, >65 years)
• HIV/AIDS and immunosuppression (CD4 <200 high risk for opportunistic infections)
• Chronic lung disease (COPD, asthma, bronchiectasis)
• Diabetes mellitus (2-3 fold increased risk)
• Chronic kidney disease and liver disease
• Heart failure and cardiovascular disease
• Malnutrition and hypoalbuminemia
• Alcoholism (impaired cough reflex, aspiration risk)
• Smoking (damages mucociliary clearance)
• Prior pneumonia or respiratory infection
• Neurological conditions affecting swallowing (stroke, dementia, Parkinson's)
• Splenic dysfunction or asplenia (encapsulated organism risk)

ENVIRONMENTAL/SOCIAL FACTORS:
• Overcrowding and poor ventilation
• Indoor air pollution (biomass fuels, tobacco smoke)
• Poor access to clean water and sanitation
• Limited healthcare access
• Incomplete vaccination
• Recent viral respiratory infection (influenza, COVID-19)
• Healthcare worker exposure
• Institutionalization (nursing homes, prisons)`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

TYPICAL BACTERIAL PNEUMONIA (Pneumococcal Pattern):
• COUGH: Initially dry, becoming productive with purulent sputum (rusty/bloody in pneumococcal infection)
• FEVER: Often high-grade (>38.5°C), sudden onset with rigors and chills
• DYSPNEA: Progressive shortness of breath, worse with exertion
• PLEURITIC CHEST PAIN: Sharp, localized pain worsening with inspiration and coughing
• MALAISE: General weakness, fatigue, myalgias
• ANOREXIA: Loss of appetite, sometimes with nausea

ATYPICAL PNEUMONIA (Mycoplasma, Chlamydophila, Legionella):
• More gradual onset over days to weeks
• Prominent DRY COUGH (may persist for weeks)
• Lower grade fever
• Prominent EXTRAPULMONARY symptoms: headache, myalgias, pharyngitis
• LEGIONELLA-specific: diarrhea, confusion, hyponatremia, relative bradycardia
• MYCOPLASMA-specific: bullous myringitis, skin rashes, cold agglutinin hemolysis

ELDERLY PATIENTS (Atypical Presentations):
• May lack fever (present in only 50-70%)
• Confusion or altered mental status may be ONLY presenting feature
• Falls, functional decline, decreased oral intake
• Exacerbation of underlying comorbidities
• Tachypnea often the most sensitive sign

HIV-POSITIVE PATIENTS:
• Subacute presentation over weeks suggests PCP or TB
• Dry cough prominent in PCP
• Night sweats, weight loss suggest TB
• Concurrent oral thrush or other AIDS-defining conditions

PEDIATRIC PRESENTATION:
• Tachypnea is the most sensitive sign (WHO criteria)
• Chest indrawing (subcostal retractions)
• Nasal flaring, grunting
• Poor feeding, lethargy
• Fever may be absent in neonates and young infants

════════════════════════════════════════════════════════════════════════════════
SIGNS
════════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachypnea: RR >20 in adults (WHO pediatric criteria: <2 months: ≥60; 2-12 months: ≥50; 1-5 years: ≥40)
• Tachycardia: Often proportional to fever (10 bpm increase per 1°C)
• Fever: Usually >38°C, may be absent in elderly or immunocompromised
• Hypotension: Suggests severe sepsis, poor prognostic sign
• Hypoxemia: SpO₂ <92% indicates significant disease

RESPIRATORY EXAMINATION:
• INSPECTION: Tachypnea, accessory muscle use, asymmetric chest expansion, splinting
• PALPATION: Increased tactile fremitus over consolidation
• PERCUSSION: Dullness to percussion over affected area
• AUSCULTATION: 
  - Bronchial breath sounds over consolidation
  - Crackles (crepitations) - fine, inspiratory
  - Decreased breath sounds (effusion or severe consolidation)
  - Pleural friction rub (pleuritis)
  - Egophony ("E to A" change) over consolidation
  - Whispered pectoriloquy

SIGNS OF SEVERITY:
• Confusion (new onset)
• Cyanosis (central or peripheral)
• Accessory muscle use, nasal flaring
• Inability to complete sentences
• Paradoxical abdominal breathing
• Signs of septic shock (cold peripheries, mottling, prolonged capillary refill)

EXTRAPULMONARY SIGNS:
• Herpes labialis (common with pneumococcal pneumonia)
• Skin rashes (Mycoplasma - erythema multiforme)
• Hepatomegaly (Legionella, Mycoplasma)
• Relative bradycardia with fever (Legionella, psittacosis)`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
════════════════════════════════════════════════════════════════════════════════

CLINICAL DIAGNOSIS requires:
1. Acute symptoms of lower respiratory tract infection:
   • Cough (productive or dry)
   • At least ONE of: fever, pleuritic chest pain, dyspnea
   • At least ONE systemic feature: sweating, fevers, shivers, aches/pains

2. NEW focal chest signs on examination:
   • Bronchial breathing, crackles, dullness to percussion, increased fremitus

3. No other explanation for the illness

RADIOGRAPHIC CONFIRMATION:
• Chest X-ray showing NEW pulmonary infiltrate is the gold standard
• Required for all hospitalized patients
• May be deferred in low-risk outpatients if clinical diagnosis is confident

════════════════════════════════════════════════════════════════════════════════
SEVERITY ASSESSMENT - CURB-65 SCORE
════════════════════════════════════════════════════════════════════════════════

C - Confusion (new disorientation in person, place, or time; AMT ≤8)     [1 point]
U - Urea >7 mmol/L (BUN >20 mg/dL)                                        [1 point]
R - Respiratory rate ≥30 breaths/minute                                   [1 point]
B - Blood pressure <90 systolic OR ≤60 diastolic mmHg                     [1 point]
65 - Age ≥65 years                                                        [1 point]

INTERPRETATION:
• Score 0-1: LOW RISK - Consider outpatient treatment (mortality <3%)
• Score 2: MODERATE RISK - Consider short hospital admission or supervised outpatient (mortality 9%)
• Score 3-5: HIGH RISK - Hospitalize; score 4-5 consider ICU (mortality 15-40%)

CRB-65 (without urea) can be used in primary care/resource-limited settings

════════════════════════════════════════════════════════════════════════════════
PNEUMONIA SEVERITY INDEX (PSI/PORT Score) - Alternative
════════════════════════════════════════════════════════════════════════════════

More complex 20-variable scoring system:
• Class I-II: Outpatient treatment
• Class III: Observation unit or short admission
• Class IV-V: Hospitalization required

════════════════════════════════════════════════════════════════════════════════
CRITERIA FOR SEVERE CAP (IDSA/ATS)
════════════════════════════════════════════════════════════════════════════════

MAJOR CRITERIA (any one = ICU admission):
• Septic shock requiring vasopressors
• Respiratory failure requiring mechanical ventilation

MINOR CRITERIA (≥3 = consider ICU):
• Respiratory rate ≥30/min
• PaO₂/FiO₂ ratio ≤250
• Multilobar infiltrates
• Confusion/disorientation
• Uremia (BUN ≥20 mg/dL)
• Leukopenia (WBC <4,000/mm³)
• Thrombocytopenia (<100,000/mm³)
• Hypothermia (<36°C)
• Hypotension requiring aggressive fluid resuscitation

════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC WORKUP
════════════════════════════════════════════════════════════════════════════════

ALL PATIENTS:
• Pulse oximetry (essential - determines oxygen need)
• Chest X-ray (PA and lateral if possible)

HOSPITALIZED PATIENTS (add):
• Complete blood count with differential
• Basic metabolic panel (urea, creatinine, electrolytes)
• Liver function tests
• C-reactive protein or procalcitonin
• Blood cultures x2 (BEFORE antibiotics)
• Sputum Gram stain and culture (if good quality specimen obtainable)
• Arterial blood gas if SpO₂ <92% or severe disease

SEVERE CAP / ICU (add):
• Legionella urinary antigen
• Pneumococcal urinary antigen
• Procalcitonin (helps distinguish bacterial from viral)
• Lactate level
• Consider bronchoscopy with BAL if not responding

HIV-POSITIVE / IMMUNOCOMPROMISED (add):
• CD4 count
• Induced sputum for PCP (silver stain, PCR)
• TB workup: Sputum AFB smear/culture, GeneXpert MTB/RIF
• Cryptococcal antigen (if CD4 <100)
• Beta-D-glucan (elevated in PCP)

SPECIAL SITUATIONS:
• Pleural effusion >1cm on lateral decubitus: diagnostic thoracentesis
• Non-responding pneumonia: CT chest, bronchoscopy
• Suspected aspiration: swallowing evaluation after acute illness`,

    differentialDiagnosis: [
      'Acute bronchitis - Cough predominant without focal findings, normal CXR, self-limited viral illness',
      'Pulmonary tuberculosis - Chronic cough >2-3 weeks, night sweats, weight loss, apical infiltrates, AFB positive; CRITICAL consideration in Africa',
      'Pneumocystis jirovecii pneumonia (PCP) - HIV with CD4 <200, subacute onset, dry cough, bilateral interstitial infiltrates, elevated LDH',
      'Acute exacerbation of COPD - History of COPD, increased dyspnea/sputum/purulence, may lack consolidation on CXR',
      'Pulmonary embolism - Sudden dyspnea, pleuritic pain, risk factors for DVT, clear CXR or wedge-shaped infiltrate, elevated D-dimer',
      'Lung cancer - Persistent cough, hemoptysis, weight loss, smoking history, mass lesion on imaging',
      'Congestive heart failure - Bilateral infiltrates, cardiomegaly, elevated BNP, peripheral edema, orthopnea',
      'Aspiration pneumonitis - Witnessed aspiration event, dependent lung segments affected, may progress to bacterial superinfection',
      'COVID-19 pneumonia - Bilateral ground-glass opacities, anosmia, contact history, positive PCR/antigen test',
      'Influenza with secondary bacterial pneumonia - Viral prodrome followed by bacterial superinfection, often S. aureus or S. pneumoniae',
      'Lung abscess - Cavitary lesion with air-fluid level, foul-smelling sputum, risk factors for aspiration',
      'Hypersensitivity pneumonitis - Exposure history (birds, mold), recurrent episodes, diffuse infiltrates'
    ],

    investigations: [
      {
        name: 'Pulse Oximetry',
        indication: '🔴 IMMEDIATE - All patients with suspected pneumonia; guides oxygen therapy',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray (PA and lateral)',
        indication: '🔴 Essential - Gold standard for confirming pneumonia; identifies consolidation, effusions, complications',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count (CBC)',
        indication: 'Leukocytosis with left shift suggests bacterial infection; leukopenia indicates severe disease',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Urea, Creatinine, Electrolytes)',
        indication: 'Urea for CURB-65 scoring; assess dehydration and renal function; hyponatremia common in Legionella',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: 'Elevated in bacterial infection; useful for monitoring treatment response (should decrease by 50% at 72h)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures (x2 sets)',
        indication: '🔴 All hospitalized patients BEFORE antibiotics; positive in 5-15% of CAP; identifies pathogen and susceptibilities',
        availability: 'BASIC'
      },
      {
        name: 'Sputum Gram Stain and Culture',
        indication: 'Before antibiotics if good quality specimen obtainable; guides narrow-spectrum therapy',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: '🔴 SpO₂ <92%, severe respiratory distress, or COPD patients; assesses oxygenation and ventilation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Procalcitonin',
        indication: 'Differentiates bacterial from viral pneumonia; guides antibiotic duration; <0.25 μg/L suggests non-bacterial',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pneumococcal Urinary Antigen',
        indication: 'Severe CAP; rapid detection of S. pneumoniae; sensitivity 50-80%; remains positive despite antibiotics',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Legionella Urinary Antigen',
        indication: 'Severe CAP, hyponatremia, diarrhea, confusion; detects serogroup 1 (accounts for 70% of cases)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'GeneXpert MTB/RIF (Xpert)',
        indication: '🔴 HIV-positive patients, non-responding pneumonia, high TB prevalence settings; rapid TB diagnosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Sputum AFB Smear and Culture',
        indication: 'Chronic cough >2 weeks, HIV-positive, TB risk factors; essential in Africa',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🔴 All pneumonia patients in high-prevalence settings; determines PCP/TB risk; guides management',
        availability: 'BASIC'
      },
      {
        name: 'CT Chest',
        indication: 'Non-responding pneumonia, suspected complications, abnormal CXR requiring clarification, suspected PE',
        availability: 'ADVANCED'
      },
      {
        name: 'Bronchoscopy with BAL',
        indication: 'Non-responding pneumonia, immunocompromised host, suspected PCP or unusual pathogens',
        availability: 'ADVANCED'
      },
      {
        name: 'Thoracentesis (Pleural Fluid Analysis)',
        indication: 'Parapneumonic effusion >1cm on lateral decubitus; rule out empyema; pH, glucose, LDH, cell count, Gram stain, culture',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Calculate CURB-65 score immediately to determine disposition (outpatient vs. admission vs. ICU)',
        '⚡ OXYGEN THERAPY: Maintain SpO₂ ≥92% (≥88% in COPD with hypercapnic risk); start with nasal cannula 2-4 L/min, escalate as needed',
        '⚡ ANTIBIOTICS WITHIN 4 HOURS: Administer first dose within 4 hours of diagnosis (within 1 hour for severe CAP); do NOT delay for cultures',
        '⚡ IV ACCESS AND FLUIDS: Establish IV access for hospitalized patients; give crystalloid bolus if hypotensive or dehydrated (30 mL/kg if septic)',
        '⚡ BLOOD CULTURES: Obtain 2 sets from different sites BEFORE antibiotics (but do not delay antibiotics for blood draws)',
        '⚡ SEPSIS SCREENING: If CURB-65 ≥3 or signs of sepsis, initiate Surviving Sepsis Campaign bundle',
        '⚡ RESPIRATORY SUPPORT: Position patient upright (45° head elevation); prepare for escalation to HFNC, NIV, or intubation if deteriorating',
        '⚡ VTE PROPHYLAXIS: Initiate pharmacological prophylaxis (LMWH or UFH) for hospitalized patients unless contraindicated',
        '⚡ GLYCEMIC CONTROL: Monitor blood glucose; target <180 mg/dL in hospitalized patients',
        '⚡ CONSIDER TB AND PCP: In HIV-positive or high-risk patients, send appropriate investigations and consider empiric PCP treatment if clinically indicated'
      ],
      pharmacological: [
        {
          drug: 'Amoxicillin',
          indication: 'First-line outpatient treatment for low-risk CAP (CURB-65 0-1); covers S. pneumoniae',
          dose: '500 mg - 1 g every 8 hours OR 875 mg every 12 hours (high-dose preferred in areas with pneumococcal resistance)',
          route: 'Oral',
          duration: '5 days (minimum); extend to 7 days if slow response',
          contraindications: ['Penicillin allergy (IgE-mediated)', 'History of amoxicillin-associated hepatic dysfunction'],
          sideEffects: ['Diarrhea', 'Nausea', 'Rash', 'Candidiasis', 'Clostridioides difficile infection'],
          alternatives: ['Doxycycline 100 mg BD', 'Azithromycin 500 mg daily (if allergic)', 'Respiratory fluoroquinolone (moxifloxacin/levofloxacin)']
        },
        {
          drug: 'Amoxicillin-Clavulanate (Co-amoxiclav/Augmentin)',
          indication: 'Outpatient CAP with comorbidities (COPD, diabetes, heart failure) or recent antibiotic use',
          dose: '625 mg (500/125) every 8 hours OR 1 g (875/125) every 12 hours',
          route: 'Oral',
          duration: '5-7 days',
          contraindications: ['Penicillin allergy', 'History of co-amoxiclav-associated cholestatic jaundice'],
          sideEffects: ['Diarrhea (more common than amoxicillin)', 'Nausea', 'Hepatotoxicity (rare)', 'Rash'],
          alternatives: ['Respiratory fluoroquinolone monotherapy', 'Ceftriaxone + macrolide']
        },
        {
          drug: 'Azithromycin',
          indication: 'Atypical pathogen coverage; part of combination therapy; monotherapy for penicillin-allergic outpatients',
          dose: '500 mg on day 1, then 250 mg daily for 4 days OR 500 mg daily for 3 days',
          route: 'Oral or IV (500 mg IV daily for hospitalized patients)',
          duration: '3-5 days (excellent tissue penetration allows short course)',
          contraindications: ['Known macrolide hypersensitivity', 'History of QT prolongation', 'Concurrent QT-prolonging drugs', 'Severe hepatic impairment'],
          sideEffects: ['QT prolongation', 'Diarrhea', 'Nausea', 'Abdominal pain', 'Hepatotoxicity'],
          alternatives: ['Doxycycline 100 mg BD', 'Clarithromycin 500 mg BD', 'Levofloxacin 750 mg daily']
        },
        {
          drug: 'Doxycycline',
          indication: 'Alternative to macrolide for atypical coverage; good option for penicillin-allergic patients',
          dose: '100 mg every 12 hours',
          route: 'Oral',
          duration: '5-7 days',
          contraindications: ['Pregnancy (2nd and 3rd trimester)', 'Breastfeeding', 'Children <8 years (dental staining)', 'Severe hepatic impairment'],
          sideEffects: ['Photosensitivity', 'GI upset', 'Esophageal ulceration (take with water, upright)', 'Candidiasis'],
          alternatives: ['Azithromycin', 'Clarithromycin', 'Respiratory fluoroquinolone']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 First-line for hospitalized non-ICU CAP (with macrolide); excellent pneumococcal coverage',
          dose: '1-2 g once daily',
          route: 'IV or IM',
          duration: 'Minimum 5 days; switch to oral when clinically stable for 48 hours',
          contraindications: ['Severe cephalosporin allergy', 'Neonates with hyperbilirubinemia (displaces bilirubin)', 'Concurrent IV calcium in neonates'],
          sideEffects: ['Diarrhea', 'Rash', 'Biliary sludging/pseudolithiasis', 'Clostridioides difficile infection', 'Injection site pain (IM)'],
          alternatives: ['Cefotaxime 1-2 g every 8 hours', 'Ampicillin-sulbactam 1.5-3 g every 6 hours', 'Levofloxacin monotherapy']
        },
        {
          drug: 'Levofloxacin',
          indication: 'Respiratory fluoroquinolone - covers typical AND atypical pathogens; monotherapy for hospitalized CAP; alternative for penicillin allergy',
          dose: '750 mg once daily (standard CAP) OR 500 mg once daily (if CrCl 20-50 mL/min)',
          route: 'Oral or IV (100% bioavailability allows oral treatment)',
          duration: '5 days (750 mg dose) OR 7-14 days (500 mg dose)',
          contraindications: ['History of fluoroquinolone-associated tendinopathy', 'Myasthenia gravis', 'QT prolongation', 'Concurrent Class IA/III antiarrhythmics', 'Pregnancy/breastfeeding'],
          sideEffects: ['Tendinopathy/tendon rupture', 'QT prolongation', 'CNS effects (dizziness, confusion)', 'Photosensitivity', 'Clostridioides difficile infection', 'Aortic aneurysm risk'],
          alternatives: ['Moxifloxacin 400 mg daily', 'Beta-lactam + macrolide combination']
        },
        {
          drug: 'Moxifloxacin',
          indication: 'Respiratory fluoroquinolone with excellent pneumococcal and atypical coverage; monotherapy for CAP',
          dose: '400 mg once daily',
          route: 'Oral or IV',
          duration: '5-7 days',
          contraindications: ['History of fluoroquinolone-associated tendinopathy', 'Myasthenia gravis', 'Significant QT prolongation', 'Hepatic impairment (use with caution)'],
          sideEffects: ['QT prolongation (more than levofloxacin)', 'Hepatotoxicity', 'Tendinopathy', 'Dizziness', 'Photosensitivity'],
          alternatives: ['Levofloxacin 750 mg daily', 'Beta-lactam + macrolide combination']
        },
        {
          drug: 'Ceftriaxone + Azithromycin Combination',
          indication: '🔴 STANDARD OF CARE for hospitalized non-ICU CAP; covers typical and atypical pathogens',
          dose: 'Ceftriaxone 1-2 g IV daily + Azithromycin 500 mg IV/PO daily',
          route: 'IV (can transition azithromycin to oral early)',
          duration: 'Minimum 5 days; step-down to oral when afebrile ≥48 hours, clinically stable, tolerating oral intake',
          contraindications: ['Severe beta-lactam allergy (use respiratory FQ instead)', 'QT prolongation (consider doxycycline instead of azithromycin)'],
          sideEffects: ['Combined side effects of both agents', 'Diarrhea', 'QT prolongation'],
          alternatives: ['Levofloxacin or moxifloxacin monotherapy', 'Ampicillin-sulbactam + azithromycin']
        },
        {
          drug: 'Piperacillin-Tazobactam',
          indication: '🔴 Severe CAP/ICU admission; broad-spectrum coverage including Pseudomonas; aspiration pneumonia',
          dose: '4.5 g every 6 hours (extended infusion over 4 hours preferred)',
          route: 'IV',
          duration: '7-10 days for severe CAP; adjust based on clinical response and cultures',
          contraindications: ['Severe penicillin allergy', 'History of piperacillin-associated hepatotoxicity'],
          sideEffects: ['Diarrhea', 'Rash', 'Thrombocytopenia', 'Electrolyte disturbances (hypokalemia)', 'Clostridioides difficile infection'],
          alternatives: ['Meropenem 1 g every 8 hours', 'Cefepime 2 g every 8 hours + metronidazole (if aspiration)']
        },
        {
          drug: 'Vancomycin',
          indication: '🔴 Add if MRSA suspected (cavitary infiltrate, empyema, concurrent influenza, IV drug use, prior MRSA)',
          dose: '15-20 mg/kg IV every 8-12 hours (target trough 15-20 μg/mL for pneumonia)',
          route: 'IV',
          duration: '7-21 days depending on severity and response',
          contraindications: ['Known vancomycin hypersensitivity'],
          sideEffects: ['Nephrotoxicity', 'Red man syndrome (infuse over ≥60 minutes)', 'Ototoxicity', 'Thrombocytopenia', 'DRESS syndrome'],
          alternatives: ['Linezolid 600 mg every 12 hours (better lung penetration)', 'Ceftaroline 600 mg every 12 hours']
        },
        {
          drug: 'Cotrimoxazole (Trimethoprim-Sulfamethoxazole)',
          indication: '🔴 ESSENTIAL for PCP treatment and prophylaxis in HIV-positive patients with CD4 <200',
          dose: 'TREATMENT: 15-20 mg/kg/day TMP component divided every 6-8 hours; PROPHYLAXIS: 160/800 mg (1 DS tablet) daily or 3x/week',
          route: 'Oral or IV (IV for severe PCP)',
          duration: '21 days for PCP treatment; lifelong prophylaxis until CD4 >200 for 3 months on ART',
          contraindications: ['Sulfonamide allergy', 'Severe renal impairment (CrCl <15 without dialysis)', 'Severe hepatic disease', 'Megaloblastic anemia due to folate deficiency'],
          sideEffects: ['Rash (may be severe - SJS/TEN)', 'Hyperkalemia', 'Myelosuppression', 'Hepatotoxicity', 'Crystalluria (maintain hydration)'],
          alternatives: ['Pentamidine IV (for severe PCP)', 'Dapsone + trimethoprim', 'Atovaquone (mild-moderate PCP)']
        },
        {
          drug: 'Prednisolone/Prednisone (Adjunctive Corticosteroids)',
          indication: '🔴 PCP with hypoxemia (PaO₂ <70 mmHg or A-a gradient >35); consider in severe CAP (controversial)',
          dose: 'PCP: 40 mg PO BD for 5 days, then 40 mg daily for 5 days, then 20 mg daily for 11 days (total 21 days)',
          route: 'Oral (or IV methylprednisolone if unable to take oral)',
          duration: '21 days for PCP; shorter courses (3-7 days) if used for severe CAP',
          contraindications: ['Active untreated TB (relative)', 'Uncontrolled diabetes (use with caution)', 'Active fungal infection'],
          sideEffects: ['Hyperglycemia', 'Immunosuppression', 'GI bleeding risk', 'Psychiatric effects', 'Adrenal suppression'],
          alternatives: ['Methylprednisolone IV 40 mg every 6 hours (if oral not tolerated)', 'Dexamethasone']
        }
      ],
      nonPharmacological: [
        'RESPIRATORY SUPPORT: Supplemental oxygen to maintain SpO₂ ≥92%; consider high-flow nasal cannula (HFNC) for moderate hypoxemia; non-invasive ventilation (NIV) for selected patients; mechanical ventilation for respiratory failure',
        'CHEST PHYSIOTHERAPY: Encourage deep breathing exercises, incentive spirometry; postural drainage and percussion if copious secretions; early mobilization when stable',
        'HYDRATION: Encourage adequate oral fluid intake; IV fluids for those unable to tolerate oral intake or with dehydration; avoid fluid overload in heart failure',
        'NUTRITION: Maintain adequate caloric intake; small frequent meals if appetite reduced; consider nasogastric feeding in prolonged illness with poor intake',
        'POSITIONING: Semi-recumbent position (30-45° head elevation) especially in ventilated patients to reduce aspiration risk; sitting upright improves ventilation',
        'SMOKING CESSATION: Counsel all smokers; provide nicotine replacement if appropriate; refer to cessation programs upon discharge',
        'INFECTION CONTROL: Droplet precautions for bacterial pneumonia; airborne precautions if TB suspected; hand hygiene; appropriate isolation',
        'GLYCEMIC CONTROL: Monitor blood glucose in diabetics and critically ill; target <180 mg/dL; avoid hypoglycemia'
      ],
      surgical: [
        'THORACENTESIS: Diagnostic and therapeutic drainage of parapneumonic effusion; indicated for effusion >1 cm on lateral decubitus or significant respiratory compromise',
        'CHEST TUBE DRAINAGE (Intercostal Drain): Required for empyema (pH <7.2, glucose <40 mg/dL, positive Gram stain/culture, frank pus); complicated parapneumonic effusion; large effusions causing respiratory compromise',
        'VIDEO-ASSISTED THORACOSCOPIC SURGERY (VATS): For loculated empyema not responding to chest tube drainage and fibrinolytics; allows visualization and decortication',
        'OPEN THORACOTOMY AND DECORTICATION: Chronic empyema with lung entrapment; failed VATS; organized pleural peel requiring removal',
        'LUNG ABSCESS DRAINAGE: Percutaneous or bronchoscopic drainage if abscess >4-6 cm not responding to antibiotics after 4-6 weeks',
        'BRONCHOSCOPY: Diagnostic for non-resolving pneumonia, suspected obstruction (tumor, foreign body), or to obtain protected specimens in immunocompromised patients',
        'TRACHEOSTOMY: Prolonged mechanical ventilation (typically >14-21 days); facilitates weaning, pulmonary toilet, and patient comfort'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU/HDU REFERRAL: Septic shock requiring vasopressors; respiratory failure requiring mechanical ventilation; CURB-65 score ≥4',
        '🔴 URGENT HOSPITAL ADMISSION: CURB-65 score 2-3; SpO₂ <92% on room air; multilobar involvement; unable to tolerate oral intake; unstable comorbidities',
        '🔴 PULMONOLOGY/RESPIRATORY REFERRAL: Non-resolving pneumonia (no improvement after 72 hours of appropriate antibiotics); recurrent pneumonia in same location; suspected underlying malignancy or bronchiectasis',
        '🔴 INFECTIOUS DISEASE REFERRAL: Suspected opportunistic infection (PCP, fungal, TB); immunocompromised host; multidrug-resistant organisms; unusual pathogens',
        '🟠 THORACIC SURGERY REFERRAL: Empyema requiring drainage; lung abscess not responding to medical therapy; suspected malignancy',
        '🟠 CARDIOLOGY REFERRAL: New heart failure precipitated by pneumonia; significant arrhythmias; suspected endocarditis',
        '🟠 HIV CLINIC REFERRAL: New HIV diagnosis; uncontrolled HIV with opportunistic infection; ART initiation or modification needed'
      ],
      monitoring: [
        'VITAL SIGNS: Heart rate, blood pressure, respiratory rate, temperature, SpO₂ - every 4-6 hours for ward patients; continuous monitoring in ICU',
        'CLINICAL RESPONSE: Expect improvement in 48-72 hours; fever should resolve within 2-4 days; if no improvement, reassess diagnosis and treatment',
        'OXYGENATION: Daily SpO₂ checks; wean oxygen as tolerated; target SpO₂ ≥92% (≥88% in COPD)',
        'INFLAMMATORY MARKERS: Repeat CRP at 48-72 hours - should decrease by ≥50%; persistent elevation suggests treatment failure or complications',
        'CHEST X-RAY: Not routine for monitoring; repeat if clinical deterioration, suspected complication, or non-resolving symptoms',
        'RENAL FUNCTION: Monitor creatinine especially with nephrotoxic antibiotics (aminoglycosides, vancomycin) or in sepsis',
        'FLUID BALANCE: Daily weights and intake/output for hospitalized patients; avoid both dehydration and fluid overload',
        'ADVERSE DRUG REACTIONS: Monitor for antibiotic side effects (diarrhea, rash, hepatotoxicity); check Clostridioides difficile if diarrhea develops'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
INPATIENT FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

• Daily clinical assessment while hospitalized
• Step-down to oral antibiotics when: afebrile ≥48 hours, clinically improving, tolerating oral intake, hemodynamically stable
• Switch criteria (all must be met): Temperature <37.8°C, HR <100, RR <24, SBP >90, SpO₂ >90% on room air (or baseline O₂), able to take oral medication, normal mental status
• Discharge criteria: Clinically stable on oral antibiotics for ≥24 hours, adequate social support, able to comply with outpatient follow-up

════════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

• Phone or in-person follow-up within 48-72 hours for high-risk outpatients
• Routine follow-up at 4-6 weeks to confirm clinical and radiographic resolution
• CHEST X-RAY at 6-8 weeks: Recommended for all patients >50 years, smokers, or those with persistent symptoms to exclude underlying malignancy or structural lung disease
• Re-evaluate sooner if: symptoms worsen, new symptoms develop, fever returns after initial defervescence

════════════════════════════════════════════════════════════════════════════════
LONG-TERM FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

• Smoking cessation support and follow-up
• Pneumococcal and influenza vaccination if not up-to-date
• Screening for underlying conditions (COPD, bronchiectasis, immunodeficiency) if recurrent pneumonia
• Functional status assessment - recovery may take weeks to months especially in elderly
• Screen for cardiovascular events: CAP is associated with increased risk of MI and stroke for up to 10 years post-infection`
    },

    redFlags: [
      '🔴 CRITICAL: Respiratory rate ≥30/min - indicates severe respiratory distress and high mortality risk',
      '🔴 CRITICAL: SpO₂ <90% on room air - severe hypoxemia requiring immediate oxygen and likely ICU care',
      '🔴 CRITICAL: Systolic BP <90 mmHg - septic shock; requires immediate fluid resuscitation and vasopressors',
      '🔴 CRITICAL: Altered mental status/confusion - indicates severe sepsis or hypoxemia; CURB-65 component',
      '🔴 CRITICAL: Multilobar involvement on CXR - extensive disease with higher mortality',
      '🔴 CRITICAL: Need for mechanical ventilation - defines severe CAP requiring ICU admission',
      '🔴 CRITICAL: Lactate >4 mmol/L - indicates tissue hypoperfusion and high mortality',
      '🟠 URGENT: Temperature <36°C (hypothermia) - paradoxically indicates worse prognosis than fever',
      '🟠 URGENT: Leukopenia (WBC <4,000/mm³) - indicates overwhelming infection or immunocompromise',
      '🟠 URGENT: Thrombocytopenia (<100,000/mm³) - associated with sepsis and DIC',
      '🟠 URGENT: Parapneumonic effusion - may progress to empyema; requires thoracentesis if >1cm',
      '🟠 URGENT: No improvement after 72 hours of appropriate antibiotics - reassess diagnosis, consider resistant organisms, complications, or alternative diagnoses (TB, PCP, malignancy)',
      '🟠 URGENT: HIV-positive patient - increased risk of PCP, TB, and other opportunistic infections',
      '🟠 URGENT: Cavitary lesion on imaging - suggests necrotizing pneumonia, abscess, TB, or malignancy'
    ],

    complications: [
      'PARAPNEUMONIC EFFUSION: Occurs in 20-40% of hospitalized CAP; most resolve with antibiotics; complicated effusions require drainage',
      'EMPYEMA: Infected pleural fluid (pus, positive cultures, pH <7.2); requires chest tube drainage ± fibrinolytics or surgical decortication',
      'LUNG ABSCESS: Cavitary necrosis; more common with aspiration, anaerobes, S. aureus, Klebsiella; requires prolonged antibiotics (4-8 weeks)',
      'ACUTE RESPIRATORY DISTRESS SYNDROME (ARDS): Severe pneumonia can trigger ARDS; high mortality; requires lung-protective ventilation',
      'SEPSIS AND SEPTIC SHOCK: Systemic inflammatory response to infection; requires aggressive resuscitation, antibiotics, and vasopressors',
      'RESPIRATORY FAILURE: May require supplemental oxygen, non-invasive ventilation, or mechanical ventilation',
      'CARDIAC COMPLICATIONS: Arrhythmias (especially atrial fibrillation), myocardial infarction, heart failure exacerbation',
      'ACUTE KIDNEY INJURY: Due to sepsis, hypovolemia, or nephrotoxic antibiotics',
      'ECTOPIC INFECTION FOCI: Meningitis, septic arthritis, endocarditis (especially with S. aureus bacteremia)',
      'POST-PNEUMONIA CARDIOVASCULAR EVENTS: Increased risk of MI and stroke for up to 10 years after CAP',
      'NECROTIZING PNEUMONIA: Tissue destruction and cavitation; associated with S. aureus (PVL-positive), Klebsiella, anaerobes',
      'BRONCHOPLEURAL FISTULA: Abnormal communication between bronchus and pleural space; air leak, empyema',
      'BRONCHIECTASIS: May develop after severe or recurrent pneumonia; predisposes to future infections',
      'POST-INFECTIOUS ORGANIZING PNEUMONIA: Inflammatory lung disease occurring weeks after infection; responds to corticosteroids'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY RATES
════════════════════════════════════════════════════════════════════════════════

• Outpatient CAP (CURB-65 0-1): <1% mortality
• Hospitalized non-ICU CAP (CURB-65 2): 5-15% mortality
• ICU-admitted severe CAP (CURB-65 3-5): 20-50% mortality
• CAP requiring mechanical ventilation: 30-50% mortality
• Pneumococcal bacteremic pneumonia: 15-30% mortality
• Healthcare-associated pneumonia: 20-30% mortality

MORTALITY BY CURB-65 SCORE:
• Score 0: 0.6%
• Score 1: 2.7%
• Score 2: 6.8%
• Score 3: 14.0%
• Score 4: 27.8%
• Score 5: 57.0%

════════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
════════════════════════════════════════════════════════════════════════════════

POOR PROGNOSTIC FACTORS:
• Age >65 years
• Comorbidities (COPD, heart failure, diabetes, CKD, liver disease, malignancy)
• Immunocompromised state (HIV, chemotherapy, steroids)
• Nursing home residence
• Altered mental status
• Multilobar involvement
• Pleural effusion
• Hypotension and septic shock
• Hypoxemia (PaO₂/FiO₂ <250)
• Leukopenia or severe leukocytosis
• Elevated BUN and lactate
• Bacteremia
• Specific pathogens (S. aureus, Pseudomonas, Legionella)

════════════════════════════════════════════════════════════════════════════════
OUTCOMES AND RECOVERY
════════════════════════════════════════════════════════════════════════════════

TIME TO CLINICAL STABILITY:
• Median time to stability: 3 days for hospitalized patients
• Most patients clinically stable by day 5-7
• Early clinical failure (worsening in first 72 hours): 5-15% of hospitalized patients

SYMPTOM RESOLUTION (typical trajectory):
• Fever: Resolves in 2-4 days
• Cough: May persist 2-8 weeks (normal recovery)
• Fatigue: May persist 4-12 weeks
• Chest X-ray: Radiographic resolution lags behind clinical improvement; 2-12 weeks for complete resolution; slower in elderly and those with comorbidities

LONG-TERM OUTCOMES:
• 30-day readmission rate: 15-20%
• 1-year mortality: 25-30% (significantly higher than non-CAP controls)
• Cardiovascular events within 30 days: 10-15%
• Functional impairment at 30 days: 30-50% have not returned to baseline activities
• Quality of life reduction may persist for 6-12 months`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
════════════════════════════════════════════════════════════════════════════════

VACCINATION (Most Important Intervention):

PNEUMOCOCCAL VACCINATION:
• PCV13 (Prevnar 13): Conjugate vaccine covering 13 serotypes; recommended for all children, adults ≥65, and high-risk adults 19-64
• PCV15/PCV20: Newer conjugate vaccines with expanded coverage; preferred in adults ≥65
• PPSV23 (Pneumovax): Polysaccharide vaccine covering 23 serotypes; used alone or after PCV13/15
• HIGH-RISK ADULTS (vaccinate at any age): Immunocompromised, asplenia, cochlear implants, CSF leaks, chronic heart/lung/liver/kidney disease, diabetes, alcoholism, smokers

INFLUENZA VACCINATION:
• Annual vaccination recommended for ALL persons ≥6 months
• Especially important for high-risk groups
• Reduces influenza-related pneumonia and secondary bacterial pneumonia

COVID-19 VACCINATION:
• Reduces severe COVID-19 pneumonia
• Follow current guidelines for primary series and boosters

SMOKING CESSATION:
• Single most important modifiable risk factor
• Counsel all smokers at every healthcare encounter
• Offer pharmacotherapy (NRT, bupropion, varenicline) and behavioral support
• Cessation reduces pneumonia risk within months

OTHER PRIMARY PREVENTION:
• Hand hygiene - reduces transmission of respiratory pathogens
• Avoid close contact with persons who have respiratory infections
• Reduce indoor air pollution (biomass fuel exposure in African context)
• Optimize nutrition and address food insecurity
• Oral hygiene - reduces aspiration of oropharyngeal pathogens
• Limit alcohol consumption - reduces aspiration risk
• HIV testing and treatment - ART dramatically reduces pneumonia incidence
• Manage underlying chronic diseases (diabetes, COPD, heart failure)

════════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Recurrence)
════════════════════════════════════════════════════════════════════════════════

• Update vaccinations during hospitalization or at follow-up
• Smoking cessation counseling and follow-up
• Optimize management of underlying conditions (COPD, heart failure, diabetes)
• Aspiration precautions for those at risk (stroke, neurological disease, GERD):
  - Swallowing evaluation
  - Dietary modifications
  - Postural adjustments during eating
  - PPI for significant GERD
• Consider investigation for structural lung disease (bronchiectasis) or immunodeficiency if recurrent pneumonia
• Nutritional assessment and support
• PCP prophylaxis for eligible HIV-positive patients (CD4 <200)

════════════════════════════════════════════════════════════════════════════════
PUBLIC HEALTH MEASURES (African Context)
════════════════════════════════════════════════════════════════════════════════

• Expand pneumococcal conjugate vaccine (PCV) coverage through national immunization programs
• Improve access to early healthcare to prevent late presentations with severe disease
• Community health worker training in pneumonia recognition and referral
• Reduce indoor air pollution through clean cooking initiatives
• Strengthen tuberculosis screening and treatment programs
• Expand HIV testing, treatment, and PCP prophylaxis
• Address malnutrition through food security programs
• Improve access to pulse oximetry at all healthcare levels`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
UNDERSTANDING YOUR CONDITION (Plain Language)
════════════════════════════════════════════════════════════════════════════════

You have pneumonia, which is an infection in your lungs. The tiny air sacs in your lungs, where oxygen enters your blood, have filled with fluid and pus because of the infection. This is why you may have trouble breathing, feel short of breath, and have a cough.

COMMON SYMPTOMS:
• Cough - often producing thick yellow, green, or rust-colored mucus
• Fever and chills - your body is fighting the infection
• Feeling very tired and weak
• Shortness of breath, especially with activity
• Chest pain when you breathe deeply or cough
• Loss of appetite

WHAT CAUSED THIS:
Pneumonia is usually caused by bacteria, which are tiny germs that can infect your lungs. You likely breathed in these germs from the air or they traveled from your mouth and throat into your lungs. Pneumonia is not always "caught" from someone else - the bacteria often normally live in your throat and only cause problems when your body's defenses are weakened.

════════════════════════════════════════════════════════════════════════════════
YOUR TREATMENT
════════════════════════════════════════════════════════════════════════════════

ANTIBIOTICS - The Most Important Treatment:
• You have been prescribed antibiotics to kill the bacteria causing your infection
• Take ALL the antibiotics exactly as prescribed, even if you start feeling better
• Do NOT stop early - stopping too soon can cause the infection to come back and make the bacteria harder to treat
• Common antibiotics include amoxicillin, azithromycin (Z-pack), doxycycline, or stronger IV antibiotics if you're in hospital

SUPPORTIVE CARE:
• Rest - your body needs energy to fight the infection
• Drink plenty of fluids (water, soup, tea) - staying hydrated helps loosen mucus
• Take paracetamol (acetaminophen) or ibuprofen for fever and body aches
• You may need oxygen through a mask or nasal tubes if your oxygen levels are low

════════════════════════════════════════════════════════════════════════════════
WHAT TO EXPECT DURING RECOVERY
════════════════════════════════════════════════════════════════════════════════

• Fever usually goes away within 2-4 days of starting antibiotics
• You should start feeling better within 3-4 days
• Cough may last 2-6 weeks - this is normal and doesn't mean antibiotics aren't working
• Tiredness and weakness may last several weeks - don't rush back to normal activities
• Chest X-ray changes take weeks to months to clear - don't be alarmed if your doctor orders a follow-up X-ray

RETURNING TO NORMAL ACTIVITIES:
• Return to work/school when fever has been gone for at least 24 hours and you feel up to it
• Avoid strenuous exercise until you feel fully recovered (usually 2-4 weeks)
• Listen to your body - rest when you feel tired

════════════════════════════════════════════════════════════════════════════════
WHEN TO SEEK IMMEDIATE MEDICAL ATTENTION
════════════════════════════════════════════════════════════════════════════════

Go to the emergency room or call for help immediately if you experience:

🔴 Severe difficulty breathing or feeling like you can't get enough air
🔴 Bluish color to lips or fingertips
🔴 Confusion or not thinking clearly
🔴 Fever that returns after it had gone away
🔴 Coughing up blood
🔴 Chest pain that is severe or getting worse
🔴 Unable to keep down fluids or medications

Call your doctor if:
• You're not feeling better after 3-4 days on antibiotics
• Your fever doesn't go down within 2-3 days
• Your symptoms are getting worse instead of better
• You have new symptoms

════════════════════════════════════════════════════════════════════════════════
PREVENTING FUTURE PNEUMONIA
════════════════════════════════════════════════════════════════════════════════

1. GET VACCINATED:
   • Pneumonia vaccine (pneumococcal vaccine) - especially important if you're over 65 or have chronic illness
   • Flu vaccine every year - flu can lead to pneumonia
   • COVID-19 vaccine - COVID can cause serious pneumonia

2. QUIT SMOKING:
   • If you smoke, this is the most important thing you can do
   • Smoking damages your lungs and makes infections more likely
   • Ask your doctor about help to quit

3. PRACTICE GOOD HYGIENE:
   • Wash your hands frequently with soap and water
   • Avoid touching your face
   • Stay away from people who are sick when possible

4. TAKE CARE OF YOUR HEALTH:
   • Manage chronic conditions like diabetes, heart disease, and lung disease
   • Eat a healthy diet
   • Get enough sleep
   • Limit alcohol consumption
   • If you have HIV, take your medications regularly

════════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR HEALTHCARE PROVIDER
════════════════════════════════════════════════════════════════════════════════

• What type of pneumonia do I have?
• How long should I take my antibiotics?
• When should I expect to feel better?
• Do I need a follow-up chest X-ray?
• Should I get a pneumonia vaccine?
• Are there any activities I should avoid?
• When can I return to work/school?`,

    references: [
      'Metlay JP, et al. Diagnosis and Treatment of Adults with Community-acquired Pneumonia: An Official Clinical Practice Guideline of the American Thoracic Society and Infectious Diseases Society of America. Am J Respir Crit Care Med. 2019;200(7):e45-e67. doi:10.1164/rccm.201908-1581ST',
      'Lim WS, et al. BTS guidelines for the management of community acquired pneumonia in adults: update 2009. Thorax. 2009;64 Suppl 3:iii1-55. doi:10.1136/thx.2009.121434',
      'World Health Organization. Revised WHO classification and treatment of pneumonia in children at health facilities: evidence summaries. WHO; 2014.',
      'Musher DM, Thorner AR. Community-acquired pneumonia. N Engl J Med. 2014;371(17):1619-28. doi:10.1056/NEJMra1312885',
      'Mandell LA. Community-acquired pneumonia: An overview. Postgrad Med. 2015;127(6):607-15. doi:10.1080/00325481.2015.1074030',
      'Prina E, Ranzani OT, Torres A. Community-acquired pneumonia. Lancet. 2015;386(9998):1097-108. doi:10.1016/S0140-6736(15)60733-4',
      'Evans SE, et al. Surviving Sepsis Campaign: International Guidelines for Management of Sepsis and Septic Shock 2021. Crit Care Med. 2021;49(11):e1063-e1143.',
      'Feldman C, Anderson R. HIV-associated bacterial pneumonia. Clin Chest Med. 2013;34(2):205-16. doi:10.1016/j.ccm.2013.01.006',
      'Torres A, et al. Pneumonia. Nat Rev Dis Primers. 2021;7(1):25. doi:10.1038/s41572-021-00259-0'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 12. ACUTE HEART FAILURE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '12',
    name: 'Acute Heart Failure',
    slug: 'acute-heart-failure',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I50.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute heart failure (AHF) is the rapid onset or worsening of symptoms and signs of heart failure, requiring urgent evaluation and treatment. It represents one of the most common causes of hospital admission worldwide, with high mortality and rehospitalization rates. AHF can present as new-onset heart failure (de novo) or as acute decompensation of chronic heart failure (ADHF), which accounts for approximately 70% of cases.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

• CLASSIFY BY PERFUSION AND CONGESTION (Forrester/Nohria-Stevenson): Warm-Wet (most common, 67%), Cold-Wet (cardiogenic shock risk), Warm-Dry (hypovolemia/overdiuresis), Cold-Dry (most severe, highest mortality)

• IDENTIFY THE PRECIPITANT (CHAMP mnemonic): Acute Coronary syndrome, Hypertension emergency, Arrhythmia, Mechanical cause (valve rupture, tamponade), Pulmonary embolism - treat the underlying cause!

• IV LOOP DIURETICS are first-line for congestion: Give furosemide IV at 1-2x home oral dose; if no response in 2 hours, double the dose; target urine output >100-150 mL/hour initially

• VASODILATORS for hypertensive AHF (SBP >140): IV nitroglycerin or nitroprusside can rapidly reduce preload and afterload; avoid in hypotension

• INOTROPES for cardiogenic shock (Cold-Wet or Cold-Dry): Dobutamine first-line; add norepinephrine if persistent hypotension despite inotropes

• OXYGEN only if SpO₂ <90% - avoid hyperoxia which may cause coronary vasoconstriction; target SpO₂ 92-96%

• NIV (CPAP/BiPAP) for respiratory distress with pulmonary edema - reduces preload, improves oxygenation, and decreases work of breathing; delays or avoids intubation

• BNP/NT-proBNP aids diagnosis: BNP >100 pg/mL or NT-proBNP >300 pg/mL suggests HF; higher values indicate worse prognosis; use age-adjusted cutoffs

• TROPONIN elevation is common and prognostic, but does NOT always indicate ACS - myocardial injury from supply-demand mismatch is frequent

• EARLY CARDIOLOGY CONSULTATION for new-onset HF, cardiogenic shock, unclear etiology, or need for advanced therapies (IABP, mechanical support, transplant evaluation)

════════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
════════════════════════════════════════════════════════════════════════════════

HEMODYNAMIC ABNORMALITIES:

1. ELEVATED FILLING PRESSURES (Congestion):
   • Left-sided: Elevated LVEDP → Elevated LA pressure → Pulmonary venous congestion → Pulmonary edema, dyspnea
   • Right-sided: Elevated RA pressure → Systemic venous congestion → JVD, peripheral edema, hepatic congestion, ascites

2. REDUCED CARDIAC OUTPUT (Hypoperfusion):
   • Forward failure: Inadequate organ perfusion → Fatigue, altered mentation, oliguria, cool extremities
   • Activation of compensatory mechanisms (initially helpful, later harmful):
     - Sympathetic activation: Tachycardia, vasoconstriction, increased myocardial O₂ demand
     - RAAS activation: Sodium and water retention, vasoconstriction, cardiac remodeling
     - ADH release: Water retention, hyponatremia

CLINICAL PHENOTYPES:

• HYPERTENSIVE AHF (Vascular failure): Acute hypertension → Increased afterload → Flash pulmonary edema with preserved or elevated LVEF; responds well to vasodilators

• NORMOTENSIVE AHF: Gradual fluid accumulation → Congestion without severe hypertension; usually ADHF; responds to diuretics

• HYPOTENSIVE AHF / CARDIOGENIC SHOCK: Severely reduced CO → Hypoperfusion AND congestion; requires inotropes ± vasopressors ± mechanical support; high mortality

PRECIPITATING FACTORS (must identify and treat):
• Acute coronary syndrome (15-25% of AHF)
• Arrhythmias (atrial fibrillation most common)
• Hypertensive crisis
• Medication/dietary non-adherence
• Infection/sepsis
• Valvular dysfunction (acute MR, AR, AS)
• Pulmonary embolism
• Renal failure
• Thyroid dysfunction
• Anemia
• Cardiotoxic drugs (NSAIDs, negative inotropes)`,

    epidemiology: `════════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
════════════════════════════════════════════════════════════════════════════════

• Heart failure affects approximately 64 million people worldwide, with prevalence of 1-2% in developed countries

• AHF accounts for over 1 million hospitalizations annually in the United States and similar numbers in Europe

• Hospital admission for AHF is one of the most common diagnoses in patients >65 years

• In-hospital mortality: 4-10% depending on presentation severity; cardiogenic shock mortality 40-60%

• 30-day mortality: 10-15%; 1-year mortality: 25-35%

• 30-day rehospitalization rate: 20-25%; this is a major quality metric and cost driver

• Heart failure hospitalizations account for 1-2% of total healthcare expenditure in developed countries

• Average length of stay: 5-10 days; shorter with optimal early management

════════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
════════════════════════════════════════════════════════════════════════════════

• Heart failure in Africa is characterized by YOUNGER age at presentation (mean age 52-55 years vs. 75+ years in Western countries)

• DIFFERENT ETIOLOGY PROFILE compared to developed nations:
  - Hypertensive heart disease: 40-50% (most common cause in Africa)
  - Rheumatic heart disease: 15-25% (still prevalent, especially in young adults)
  - Cardiomyopathies: 15-20% (dilated, peripartum, HIV-related)
  - Ischemic heart disease: 5-15% (increasing with urbanization)
  - Peripartum cardiomyopathy: Higher incidence than anywhere in the world

• PERIPARTUM CARDIOMYOPATHY (PPCM) is particularly common in Nigeria, with incidence up to 1:100 pregnancies in some regions; associated with traditional postpartum practices involving heat exposure and high salt intake

• HIV-ASSOCIATED CARDIOMYOPATHY affects 10-30% of HIV patients in Africa; improved with ART but still significant

• ENDOMYOCARDIAL FIBROSIS (EMF) is endemic in equatorial Africa; causes restrictive cardiomyopathy

• Late presentation is common due to healthcare access barriers, leading to more severe decompensation at admission

• Limited access to echocardiography, BNP testing, and cardiac catheterization affects diagnostic accuracy

• Higher in-hospital mortality (10-20%) compared to Western countries due to later presentation and resource limitations

• Medication availability challenges: Loop diuretics usually available; ACE inhibitors/ARBs increasingly available; beta-blockers and aldosterone antagonists may be limited

════════════════════════════════════════════════════════════════════════════════
RISK FACTORS
════════════════════════════════════════════════════════════════════════════════

UNDERLYING CONDITIONS:
• Coronary artery disease / prior MI
• Hypertension (most common in Africa)
• Diabetes mellitus
• Valvular heart disease
• Cardiomyopathies (dilated, hypertrophic, restrictive)
• Congenital heart disease
• Atrial fibrillation
• Chronic kidney disease

PRECIPITATING FACTORS FOR DECOMPENSATION:
• Medication non-adherence (most common)
• Dietary indiscretion (high sodium intake)
• Acute coronary syndrome
• Uncontrolled hypertension
• Arrhythmias (new-onset AF, sustained VT)
• Infection (pneumonia, sepsis)
• Anemia
• Thyroid dysfunction
• Pulmonary embolism
• Pregnancy
• Cardiotoxic medications (NSAIDs, thiazolidinediones, certain chemotherapy)
• Alcohol excess`,

    clinicalPresentation: `════════════════════════════════════════════════════════════════════════════════
SYMPTOMS
════════════════════════════════════════════════════════════════════════════════

CONGESTIVE SYMPTOMS (Backward Failure):
• DYSPNEA: Most common symptom; progressive from exertional to rest; orthopnea (dyspnea when supine); paroxysmal nocturnal dyspnea (PND - sudden awakening with breathlessness)
• PERIPHERAL EDEMA: Bilateral, pitting, dependent (ankles when ambulatory, sacral when bedridden); may progress to anasarca
• ABDOMINAL DISTENSION: Due to ascites and hepatomegaly; early satiety, nausea, right upper quadrant discomfort
• WEIGHT GAIN: Due to fluid retention; important to track daily weights

LOW OUTPUT SYMPTOMS (Forward Failure):
• FATIGUE: Often severe and limiting; due to reduced skeletal muscle perfusion
• WEAKNESS: Progressive exercise intolerance
• ALTERED MENTATION: Confusion, decreased concentration (especially in elderly)
• OLIGURIA: Reduced renal perfusion; may progress to acute kidney injury

SYMPTOMS SUGGESTING CARDIOGENIC SHOCK:
• Severe dyspnea at rest
• Altered consciousness
• Cold, clammy extremities
• Chest pain (if ACS is precipitant)
• Profound weakness

SYMPTOMS OF SPECIFIC PRECIPITANTS:
• Chest pain → Acute coronary syndrome
• Palpitations → Arrhythmia
• Fever → Infection, endocarditis
• Severe headache → Hypertensive crisis
• Pleuritic pain, hemoptysis → Pulmonary embolism

════════════════════════════════════════════════════════════════════════════════
SIGNS
════════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia: Usually present; absence in severe HF may indicate beta-blocker effect or severe decompensation
• Blood pressure: Variable - hypertensive (vascular failure), normotensive (most ADHF), or hypotensive (cardiogenic shock)
• Tachypnea: RR >20; indicates pulmonary congestion or compensation for metabolic acidosis
• Hypoxemia: SpO₂ <90% in severe pulmonary edema
• Narrow pulse pressure: Suggests low cardiac output

JUGULAR VENOUS EXAMINATION:
• ELEVATED JVP: Most specific sign of elevated filling pressures; >8 cm H₂O above sternal angle
• Hepatojugular reflux: Sustained JVP elevation with abdominal pressure
• Kussmaul sign: JVP rise with inspiration (suggests restrictive physiology, tamponade, RV infarct)

CARDIAC EXAMINATION:
• S3 GALLOP: Pathognomonic of heart failure; low-pitched, heard at apex, indicates elevated filling pressures
• S4 gallop: Indicates reduced ventricular compliance (may be normal in elderly)
• Displaced PMI: Laterally and inferiorly displaced indicates cardiomegaly
• Murmurs: May indicate valvular etiology or functional mitral regurgitation
• Irregular rhythm: Suggests atrial fibrillation

PULMONARY EXAMINATION:
• CRACKLES/RALES: Bilateral, basilar, do not clear with cough; indicate pulmonary edema
• Wheezes: "Cardiac asthma" - bronchospasm from pulmonary congestion
• Decreased breath sounds: May indicate pleural effusion
• Dullness to percussion at bases: Pleural effusions common in HF

PERIPHERAL EXAMINATION:
• Peripheral edema: Bilateral, pitting, dependent
• Cool extremities: Indicates low cardiac output and vasoconstriction
• Delayed capillary refill: >3 seconds suggests hypoperfusion
• Hepatomegaly: Often tender; pulsatile in severe TR
• Ascites: Shifting dullness, fluid wave

NOHRIA-STEVENSON CLASSIFICATION (Congestion vs. Perfusion):
• WARM-WET (Profile B): Adequate perfusion, congested - most common (67%)
• COLD-WET (Profile C): Poor perfusion, congested - cardiogenic shock risk
• WARM-DRY (Profile A): Adequate perfusion, not congested - compensated or overdiuresed
• COLD-DRY (Profile L): Poor perfusion, not congested - hypovolemic, worst prognosis`,

    diagnosis: `════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
════════════════════════════════════════════════════════════════════════════════

FRAMINGHAM CRITERIA (Classic - requires 2 major or 1 major + 2 minor):

MAJOR CRITERIA:
• Paroxysmal nocturnal dyspnea or orthopnea
• Neck vein distension (elevated JVP)
• Pulmonary crackles (rales)
• Cardiomegaly on chest X-ray
• Acute pulmonary edema
• S3 gallop
• Hepatojugular reflux
• Weight loss >4.5 kg in 5 days in response to treatment

MINOR CRITERIA:
• Bilateral ankle edema
• Nocturnal cough
• Dyspnea on ordinary exertion
• Hepatomegaly
• Pleural effusion
• Tachycardia (HR >120 bpm)
• Decrease in vital capacity by 1/3 from maximum

════════════════════════════════════════════════════════════════════════════════
CLASSIFICATION OF HEART FAILURE
════════════════════════════════════════════════════════════════════════════════

BY EJECTION FRACTION:
• HFrEF (Heart Failure with Reduced EF): LVEF ≤40%
• HFmrEF (Heart Failure with Mildly Reduced EF): LVEF 41-49%
• HFpEF (Heart Failure with Preserved EF): LVEF ≥50%

NYHA FUNCTIONAL CLASSIFICATION:
• Class I: No limitation; ordinary activity does not cause symptoms
• Class II: Slight limitation; comfortable at rest, ordinary activity causes symptoms
• Class III: Marked limitation; comfortable at rest, less than ordinary activity causes symptoms
• Class IV: Unable to carry out any activity without symptoms; symptoms at rest

AHA/ACC STAGES:
• Stage A: At risk, no structural disease or symptoms
• Stage B: Structural disease, no symptoms
• Stage C: Structural disease with current or prior symptoms
• Stage D: Refractory HF requiring advanced therapies

════════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC WORKUP FOR AHF
════════════════════════════════════════════════════════════════════════════════

IMMEDIATE (within 30-60 minutes):
• 12-lead ECG: Assess rhythm, ischemia, prior MI, LVH, conduction abnormalities
• Chest X-ray: Cardiomegaly, pulmonary congestion, pleural effusions, rule out pneumonia
• BNP or NT-proBNP: Aids diagnosis and prognosis
• Troponin: Rule out ACS (may be elevated from demand ischemia)
• Basic metabolic panel: Electrolytes, renal function, glucose
• Complete blood count: Anemia, infection

WITHIN 24-48 HOURS:
• Echocardiogram: ESSENTIAL - assess LVEF, wall motion, valves, pericardium, RV function
• Liver function tests
• Thyroid function (TSH)
• Lipid panel (if not recent)
• Iron studies (to evaluate for iron deficiency)

AS INDICATED:
• Coronary angiography: If ACS suspected or new HF with ischemic etiology
• Right heart catheterization: Hemodynamic assessment in cardiogenic shock, unclear volume status, pre-transplant evaluation
• Cardiac MRI: Etiology workup (myocarditis, infiltrative disease, viability)
• CT pulmonary angiography: If PE suspected`,

    differentialDiagnosis: [
      'COPD/Asthma exacerbation - Wheezing predominant, history of smoking/atopy, normal BNP, hyperinflation on CXR, responds to bronchodilators',
      'Pneumonia - Fever, productive cough, focal consolidation on CXR, elevated procalcitonin, unilateral crackles',
      'Pulmonary embolism - Sudden onset dyspnea, pleuritic chest pain, risk factors for DVT, elevated D-dimer, CT-PA diagnostic',
      'Acute coronary syndrome - Chest pain, ECG changes, elevated troponin; may coexist with or cause AHF',
      'Pericardial tamponade - Beck triad (hypotension, JVD, muffled heart sounds), pulsus paradoxus, electrical alternans on ECG',
      'Acute respiratory distress syndrome (ARDS) - Known precipitant, bilateral infiltrates, normal cardiac filling pressures, low BNP',
      'Severe anemia - Pallor, high-output failure, normal BNP may be normal, check hemoglobin',
      'Nephrotic syndrome - Edema with hypoalbuminemia, proteinuria, normal cardiac function',
      'Chronic kidney disease with volume overload - Uremic symptoms, elevated BUN/creatinine out of proportion to HF',
      'Cirrhosis with ascites - Stigmata of liver disease, hepatomegaly, spider angiomata, ascites without pulmonary edema',
      'Venous insufficiency - Bilateral leg edema without pulmonary congestion, varicosities, skin changes, normal JVP',
      'Constrictive pericarditis - JVD, Kussmaul sign, pericardial knock, calcification on imaging'
    ],

    investigations: [
      {
        name: '12-Lead ECG',
        indication: '🔴 IMMEDIATE - All patients; assess rhythm (AF), ischemia (ST changes), prior MI (Q waves), LVH, conduction delays',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray (PA and lateral)',
        indication: '🔴 IMMEDIATE - Cardiomegaly, pulmonary vascular congestion, Kerley B lines, pleural effusions, rule out pneumonia',
        availability: 'BASIC'
      },
      {
        name: 'BNP or NT-proBNP',
        indication: '🔴 Diagnostic and prognostic; BNP >100 pg/mL or NT-proBNP >300 pg/mL supports HF diagnosis; use age-adjusted cutoffs for NT-proBNP',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Troponin (High-sensitivity preferred)',
        indication: '🔴 Rule out ACS; elevated troponin in AHF indicates worse prognosis even without ACS',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Electrolytes, Creatinine, BUN, Glucose)',
        indication: '🔴 IMMEDIATE - Assess renal function (cardiorenal syndrome), electrolytes (hyponatremia, hypokalemia from diuretics)',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Assess for anemia (may precipitate or worsen HF), infection (leukocytosis)',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 IMMEDIATE - Continuous monitoring; guides oxygen therapy; target SpO₂ 92-96%',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 Severe respiratory distress, suspected respiratory failure, cardiogenic shock; assess oxygenation, ventilation, acid-base status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Transthoracic Echocardiogram (TTE)',
        indication: '🔴 ESSENTIAL within 24-48h (urgent if cardiogenic shock); assess LVEF, wall motion, valves, pericardium, RV function, estimate filling pressures',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Liver Function Tests (AST, ALT, Bilirubin, Albumin)',
        indication: 'Hepatic congestion (elevated AST/ALT, bilirubin); hypoalbuminemia indicates poor prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH)',
        indication: 'Hypo- or hyperthyroidism can cause or exacerbate HF; check in all new-onset HF',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: '🔴 Suspected cardiogenic shock; elevated lactate indicates tissue hypoperfusion and worse prognosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urinalysis',
        indication: 'Assess for proteinuria (nephrotic syndrome), signs of infection',
        availability: 'BASIC'
      },
      {
        name: 'Coronary Angiography',
        indication: '🔴 Urgent if ACS suspected; consider in new-onset HF to assess for ischemic etiology',
        availability: 'ADVANCED'
      },
      {
        name: 'Right Heart Catheterization (Swan-Ganz)',
        indication: 'Cardiogenic shock unresponsive to initial therapy; unclear volume status; pre-transplant evaluation; guides therapy with hemodynamic parameters',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Pulmonary Angiography',
        indication: 'Suspected pulmonary embolism; sudden unexplained dyspnea with clear lungs',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Cardiac MRI',
        indication: 'Etiology workup: myocarditis (edema, late gadolinium enhancement), infiltrative disease (amyloidosis, sarcoidosis), viability assessment',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS PERFUSION AND CONGESTION: Classify patient (Warm-Wet, Cold-Wet, Warm-Dry, Cold-Dry) to guide therapy',
        '⚡ OXYGEN: Only if SpO₂ <90%; target 92-96%; avoid hyperoxia; use nasal cannula or face mask initially',
        '⚡ NON-INVASIVE VENTILATION (NIV): For persistent respiratory distress despite initial therapy; CPAP 5-10 cm H₂O or BiPAP; contraindicated in cardiogenic shock, obtunded patient',
        '⚡ IV ACCESS: Establish large-bore IV access; central line if vasopressors/inotropes anticipated',
        '⚡ IV LOOP DIURETIC (if congested): Furosemide 40-80 mg IV (or 1-2x home oral dose); if no response in 2h, double dose; target UOP >100-150 mL/h initially',
        '⚡ VASODILATORS (if hypertensive, SBP >140): IV nitroglycerin 10-20 mcg/min, titrate to SBP and symptoms; reduces preload rapidly',
        '⚡ IDENTIFY AND TREAT PRECIPITANT: ACS → cath lab; Arrhythmia → rate/rhythm control; Infection → antibiotics; Hypertensive crisis → IV antihypertensives',
        '⚡ CONTINUOUS MONITORING: Telemetry, pulse oximetry, frequent vital signs; Foley catheter to monitor urine output',
        '⚡ RESTRICT SODIUM AND FLUIDS: Na <2g/day; fluids 1.5-2 L/day in hyponatremic patients',
        '⚡ HOLD NEPHROTOXIC DRUGS: Stop NSAIDs, adjust ACEi/ARB if significant AKI, hold metformin if lactate elevated'
      ],
      pharmacological: [
        {
          drug: 'Furosemide (IV)',
          indication: '🔴 FIRST-LINE for congestion; loop diuretic of choice in AHF',
          dose: '40-80 mg IV bolus initially (or 1-2x home oral dose); if inadequate response in 2h, double dose; max single dose 200 mg; can give as continuous infusion 5-40 mg/hour',
          route: 'IV (preferred for reliability)',
          duration: 'Until decongestion achieved, then transition to oral; typical 3-7 days IV',
          contraindications: ['Severe hypovolemia', 'Anuria unresponsive to diuretics', 'Hepatic encephalopathy (relative)'],
          sideEffects: ['Hypokalemia', 'Hypomagnesemia', 'Hyponatremia', 'Metabolic alkalosis', 'Ototoxicity (high doses)', 'Hyperuricemia', 'Worsening renal function'],
          alternatives: ['Bumetanide 1-2 mg IV (1 mg = 40 mg furosemide)', 'Torsemide 10-20 mg IV']
        },
        {
          drug: 'Nitroglycerin (IV)',
          indication: '🔴 Hypertensive AHF (SBP >140), flash pulmonary edema; reduces preload and afterload; also for ischemia/ACS',
          dose: 'Start 10-20 mcg/min IV infusion; titrate by 10-20 mcg/min every 5-10 min; typical range 50-200 mcg/min; max 400 mcg/min',
          route: 'IV continuous infusion',
          duration: '24-48 hours typically; transition to oral nitrates or discontinue as BP stabilizes',
          contraindications: ['Hypotension (SBP <90)', 'Severe aortic stenosis', 'Right ventricular infarction', 'Use of PDE5 inhibitors (sildenafil, etc.) within 24-48h'],
          sideEffects: ['Hypotension', 'Headache', 'Tachyphylaxis with continuous use', 'Methemoglobinemia (rare)'],
          alternatives: ['Nitroprusside (if severe hypertension)', 'Isosorbide dinitrate SL/IV']
        },
        {
          drug: 'Nitroprusside (IV)',
          indication: 'Severe hypertensive AHF, hypertensive crisis with HF; potent balanced vasodilator',
          dose: 'Start 0.25-0.5 mcg/kg/min IV; titrate by 0.25 mcg/kg/min every 5 min; usual range 0.5-3 mcg/kg/min; max 10 mcg/kg/min',
          route: 'IV continuous infusion (protect from light)',
          duration: 'Short-term use only (24-48h) due to cyanide toxicity risk; wean as oral agents titrated',
          contraindications: ['Hypotension', 'Severe renal impairment (cyanide accumulation)', 'Severe hepatic impairment (thiocyanate accumulation)', 'Inadequate cerebral perfusion'],
          sideEffects: ['Hypotension', 'Cyanide toxicity (metabolic acidosis, altered mental status)', 'Thiocyanate toxicity (prolonged use)', 'Rebound hypertension on discontinuation'],
          alternatives: ['Nitroglycerin IV', 'Clevidipine', 'Nicardipine']
        },
        {
          drug: 'Dobutamine',
          indication: '🔴 Cardiogenic shock, Cold-Wet profile; beta-1 agonist providing inotropy with some vasodilation',
          dose: 'Start 2-5 mcg/kg/min IV; titrate by 2.5 mcg/kg/min every 10-30 min; usual range 5-15 mcg/kg/min; max 20 mcg/kg/min',
          route: 'IV continuous infusion (central line preferred)',
          duration: 'Shortest duration possible; wean as hemodynamics improve; bridge to oral therapy or advanced support',
          contraindications: ['Hypertrophic obstructive cardiomyopathy', 'Mechanical obstruction to filling (tamponade, constrictive pericarditis)', 'Uncorrected hypovolemia'],
          sideEffects: ['Tachycardia', 'Arrhythmias (SVT, VT)', 'Hypotension (at high doses due to beta-2 effect)', 'Increased myocardial oxygen demand', 'Tolerance with prolonged use'],
          alternatives: ['Milrinone (if adequate BP)', 'Dopamine (if hypotensive)', 'Levosimendan (where available)']
        },
        {
          drug: 'Norepinephrine',
          indication: '🔴 Cardiogenic shock with severe hypotension (SBP <70-80) despite inotropes; first-line vasopressor for AHF',
          dose: 'Start 0.1-0.2 mcg/kg/min IV; titrate to MAP >65 mmHg; usual range 0.1-0.5 mcg/kg/min; may need higher in refractory shock',
          route: 'IV continuous infusion (central line strongly preferred)',
          duration: 'Wean as perfusion improves; transition to inotropes alone if possible',
          contraindications: ['Hypovolemia (correct first)', 'Mesenteric ischemia (relative)'],
          sideEffects: ['Peripheral vasoconstriction', 'Tissue necrosis if extravasation', 'Arrhythmias', 'Increased afterload (may worsen cardiac output if not combined with inotrope)'],
          alternatives: ['Epinephrine (if need combined inotrope/vasopressor)', 'Vasopressin (adjunct)']
        },
        {
          drug: 'Milrinone',
          indication: 'Cardiogenic shock when tachycardia limits dobutamine; pulmonary hypertension with RV failure; post-cardiac surgery',
          dose: 'Loading dose 50 mcg/kg over 10 min (often omitted due to hypotension); maintenance 0.375-0.75 mcg/kg/min',
          route: 'IV continuous infusion',
          duration: 'Short-term use; associated with worse outcomes in non-ischemic HF with prolonged use',
          contraindications: ['Severe hypotension (SBP <80)', 'Severe aortic or pulmonic stenosis'],
          sideEffects: ['Hypotension (more than dobutamine)', 'Arrhythmias', 'Thrombocytopenia', 'Headache'],
          alternatives: ['Dobutamine', 'Levosimendan']
        },
        {
          drug: 'Morphine',
          indication: 'Severe dyspnea with anxiety; reduces preload and respiratory drive; use with caution',
          dose: '2-4 mg IV slowly; may repeat every 5-15 min as needed; max 10-15 mg',
          route: 'IV',
          duration: 'Single or few doses; not for routine use',
          contraindications: ['Respiratory depression', 'Hypotension', 'Altered mental status', 'Acute asthma'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Nausea/vomiting', 'Bradycardia'],
          alternatives: ['Low-dose benzodiazepine for anxiety', 'NIV for respiratory distress (preferred)']
        },
        {
          drug: 'Spironolactone/Eplerenone',
          indication: 'Add to loop diuretic for diuretic resistance (sequential nephron blockade); long-term for HFrEF after stabilization',
          dose: 'Spironolactone 12.5-25 mg daily; Eplerenone 25 mg daily; increase as tolerated; monitor potassium',
          route: 'Oral',
          duration: 'Long-term for HFrEF; short-term (3-5 days) for diuretic resistance',
          contraindications: ['K+ >5.0 mEq/L', 'Severe renal impairment (eGFR <30)', 'Addison disease'],
          sideEffects: ['Hyperkalemia', 'Gynecomastia (spironolactone)', 'Renal impairment'],
          alternatives: ['Metolazone for diuretic resistance', 'Thiazide diuretic for sequential blockade']
        },
        {
          drug: 'Metolazone',
          indication: 'Diuretic resistance; add to loop diuretic for synergistic effect (sequential nephron blockade)',
          dose: '2.5-5 mg PO once daily; give 30-60 min before loop diuretic; max 10 mg daily',
          route: 'Oral',
          duration: 'Short-term (2-3 days) to overcome resistance; monitor closely for over-diuresis',
          contraindications: ['Anuria', 'Severe hypovolemia', 'Severe electrolyte depletion'],
          sideEffects: ['Severe hypokalemia', 'Hyponatremia', 'Hypomagnesemia', 'Volume depletion', 'Hyperuricemia'],
          alternatives: ['Chlorothiazide IV 500 mg', 'Acetazolamide 250-500 mg']
        },
        {
          drug: 'Carvedilol (or other beta-blocker)',
          indication: 'Continue at reduced dose if already on beta-blocker; initiate once stabilized for HFrEF; DO NOT initiate in acute decompensation',
          dose: 'If on beta-blocker: reduce dose by 50% or hold if severely decompensated; restart at low dose once euvolemic',
          route: 'Oral',
          duration: 'Long-term for chronic HF management',
          contraindications: ['Cardiogenic shock', 'Severe bradycardia', '2nd/3rd degree AV block', 'Decompensated HF (do not initiate)', 'Severe reactive airway disease'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue', 'Bronchospasm', 'Worsening HF (if initiated during decompensation)'],
          alternatives: ['Metoprolol succinate', 'Bisoprolol']
        },
        {
          drug: 'Lisinopril/Enalapril (ACEi) or Valsartan/Losartan (ARB)',
          indication: 'Continue if already on (may need dose reduction if AKI); initiate once stabilized for HFrEF',
          dose: 'Hold or reduce if creatinine rises >30% from baseline or K+ >5.5; restart at low dose once stable',
          route: 'Oral',
          duration: 'Long-term for chronic HF management',
          contraindications: ['Bilateral renal artery stenosis', 'History of angioedema', 'Pregnancy', 'Symptomatic hypotension', 'K+ >5.5'],
          sideEffects: ['Hypotension', 'Hyperkalemia', 'Cough (ACEi)', 'Angioedema', 'AKI'],
          alternatives: ['ARB if ACEi intolerant', 'Sacubitril/valsartan (ARNI) once stable']
        },
        {
          drug: 'Sacubitril/Valsartan (ARNI)',
          indication: 'HFrEF with LVEF ≤40%; superior to ACEi; initiate once stabilized (not in acute phase)',
          dose: 'Start 24/26 mg or 49/51 mg BD depending on prior ACEi dose; target 97/103 mg BD',
          route: 'Oral',
          duration: 'Long-term; do not initiate until 36h after last ACEi dose',
          contraindications: ['Concurrent ACEi use', 'History of angioedema with ACEi/ARB', 'Pregnancy', 'Severe hepatic impairment'],
          sideEffects: ['Hypotension', 'Hyperkalemia', 'Renal impairment', 'Angioedema'],
          alternatives: ['ACEi or ARB if ARNI not available or not tolerated']
        }
      ],
      nonPharmacological: [
        'NON-INVASIVE VENTILATION (NIV): CPAP 5-10 cm H₂O or BiPAP (IPAP 10-15, EPAP 5-8) for pulmonary edema with respiratory distress; reduces preload, improves oxygenation, decreases work of breathing',
        'FLUID AND SODIUM RESTRICTION: Sodium <2 g/day; fluid restriction 1.5-2 L/day especially if hyponatremic (Na <130)',
        'DAILY WEIGHTS: Essential for monitoring fluid balance; target 0.5-1 kg weight loss per day during active diuresis',
        'FOLEY CATHETER: Insert to accurately monitor urine output; target >0.5 mL/kg/hr; useful to guide diuretic titration',
        'UPRIGHT POSITIONING: Semi-Fowler or high-Fowler position improves respiratory mechanics and reduces preload',
        'DVT PROPHYLAXIS: Pharmacological prophylaxis (enoxaparin 40 mg SC daily or UFH 5000 units SC TID) unless bleeding risk; pneumatic compression devices',
        'TELEMETRY MONITORING: Continuous cardiac monitoring for arrhythmias; especially important given AF and VT risk',
        'OXYGEN THERAPY: Supplemental O₂ only if SpO₂ <90%; target 92-96%; avoid hyperoxia',
        'EARLY MOBILIZATION: Once stabilized, encourage sitting and ambulation to prevent deconditioning and DVT',
        'CARDIAC REHABILITATION: Referral for outpatient cardiac rehab upon discharge improves functional capacity and outcomes'
      ],
      surgical: [
        'INTRA-AORTIC BALLOON PUMP (IABP): Mechanical circulatory support for cardiogenic shock; augments diastolic coronary perfusion and reduces afterload; bridge to definitive therapy',
        'PERCUTANEOUS VENTRICULAR ASSIST DEVICE (Impella, TandemHeart): More robust circulatory support than IABP; for refractory cardiogenic shock; bridge to recovery, decision, or transplant',
        'EXTRACORPOREAL MEMBRANE OXYGENATION (ECMO): VA-ECMO for refractory cardiogenic shock; provides full cardiopulmonary support; bridge to recovery, LVAD, or transplant',
        'LEFT VENTRICULAR ASSIST DEVICE (LVAD): Durable mechanical support for advanced HFrEF; bridge to transplant or destination therapy',
        'HEART TRANSPLANTATION: Definitive treatment for end-stage heart failure; limited by donor availability; requires comprehensive pre-transplant evaluation',
        'CORONARY REVASCULARIZATION (PCI or CABG): For ACS as precipitant of AHF; consider revascularization for ischemic cardiomyopathy with viable myocardium',
        'VALVE SURGERY OR TRANSCATHETER INTERVENTION: For valvular etiology (acute severe MR, AR, AS); timing depends on hemodynamic stability',
        'CARDIAC RESYNCHRONIZATION THERAPY (CRT): For chronic HFrEF with LBBB and QRS ≥150 ms; not indicated in acute setting but may be considered before discharge',
        'IMPLANTABLE CARDIOVERTER-DEFIBRILLATOR (ICD): For secondary prevention if VT/VF; primary prevention in HFrEF ≤35% after 3 months of optimal therapy; evaluate before discharge',
        'ULTRAFILTRATION: For diuretic-resistant volume overload; mechanical fluid removal; limited evidence for routine use'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE CARDIOLOGY/ICU: Cardiogenic shock; need for inotropes or vasopressors; respiratory failure requiring intubation; acute severe valvular dysfunction',
        '🔴 URGENT CARDIOLOGY CONSULTATION: New-onset heart failure for etiology workup; suspected ACS as precipitant; hemodynamic instability; need for advanced therapies',
        '🔴 INTERVENTIONAL CARDIOLOGY: ACS requiring catheterization; need for mechanical circulatory support',
        '🟠 ELECTROPHYSIOLOGY: Malignant arrhythmias (VT/VF); consideration for ICD or CRT; AF requiring ablation',
        '🟠 HEART FAILURE SPECIALIST: Refractory HF despite optimal therapy; consideration for advanced therapies (LVAD, transplant); frequent hospitalizations',
        '🟠 CARDIAC SURGERY: Valvular intervention; CABG for ischemic cardiomyopathy; LVAD implantation',
        '🟠 PALLIATIVE CARE: End-stage heart failure with poor prognosis; goals of care discussions; symptom management'
      ],
      monitoring: [
        'VITAL SIGNS: Continuous telemetry, pulse oximetry; BP, HR, RR every 1-4 hours depending on severity',
        'URINE OUTPUT: Hourly initially with Foley; target >0.5 mL/kg/hr; guides diuretic titration',
        'DAILY WEIGHTS: Same scale, same time; target 0.5-1 kg/day loss during active diuresis',
        'INTAKE/OUTPUT: Strict fluid balance; guides diuretic therapy and fluid restriction',
        'ELECTROLYTES: Daily potassium, sodium, magnesium, creatinine during active diuresis; replete as needed',
        'RENAL FUNCTION: Monitor creatinine daily; tolerate up to 30% rise if achieving decongestion; worsening renal function may indicate over-diuresis or cardiorenal syndrome',
        'BNP/NT-proBNP: Repeat before discharge; decrease indicates successful decongestion; failure to decrease predicts early readmission',
        'CLINICAL ASSESSMENT: Daily evaluation of congestion (JVP, edema, crackles) and perfusion (mental status, extremities, urine output)',
        'SYMPTOM ASSESSMENT: Dyspnea, orthopnea, exercise tolerance; should improve with treatment'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
BEFORE DISCHARGE
════════════════════════════════════════════════════════════════════════════════

• Ensure patient is hemodynamically stable on oral medications for ≥24 hours
• Optimize GDMT: Beta-blocker, ACEi/ARB/ARNI, MRA; initiate or uptitrate as tolerated
• Assess ejection fraction; if ≤35% and ischemic etiology, consider ICD evaluation
• Patient education: daily weights, low-sodium diet, fluid restriction if indicated, medication adherence, symptom recognition
• Transition IV diuretics to oral; establish dry weight
• Address precipitating factors
• Arrange early follow-up appointment (within 7 days)

════════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

• EARLY FOLLOW-UP: Within 7 days of discharge - most critical period for rehospitalization
• Assess volume status, symptoms, medication tolerance
• Check electrolytes, renal function, BNP within 1-2 weeks
• Titrate GDMT to target doses as tolerated
• Repeat echo in 3 months if new diagnosis or change in clinical status
• Re-evaluate EF at 3 months for ICD decision in HFrEF

════════════════════════════════════════════════════════════════════════════════
LONG-TERM MANAGEMENT
════════════════════════════════════════════════════════════════════════════════

• Quarterly follow-up minimum; more frequent if unstable
• Annual comprehensive assessment including echo
• Continue GDMT optimization
• Cardiac rehabilitation referral
• Screen for depression and cognitive impairment
• Advanced care planning discussion
• Palliative care involvement for advanced HF`
    },

    redFlags: [
      '🔴 CRITICAL: Cardiogenic shock (Cold-Wet or Cold-Dry profile) - SBP <90, signs of hypoperfusion (altered mental status, cold extremities, oliguria); requires inotropes ± vasopressors ± mechanical support',
      '🔴 CRITICAL: Respiratory failure requiring intubation - inability to maintain oxygenation/ventilation despite NIV; altered mental status',
      '🔴 CRITICAL: Acute coronary syndrome as precipitant - ST elevation or dynamic ST-T changes with chest pain; requires urgent catheterization',
      '🔴 CRITICAL: Malignant arrhythmias - sustained VT, VF, complete heart block; requires immediate treatment',
      '🔴 CRITICAL: Severe hyponatremia (Na <125) - indicates severe neurohormonal activation; associated with very poor prognosis',
      '🔴 CRITICAL: Acute severe valvular dysfunction - acute MR, AR with hemodynamic compromise; may require urgent surgery',
      '🟠 URGENT: Diuretic resistance - persistent congestion despite high-dose IV diuretics; consider adding thiazide/MRA, ultrafiltration, or inotropes',
      '🟠 URGENT: Worsening renal function - creatinine rise >0.5 mg/dL or >30% from baseline; may need to reduce diuretics or add inotropes',
      '🟠 URGENT: Troponin elevation - indicates myocardial injury; evaluate for ACS vs. demand ischemia',
      '🟠 URGENT: New atrial fibrillation with rapid ventricular response - may be precipitant; requires rate or rhythm control',
      '🟠 URGENT: Lactate elevation - indicates tissue hypoperfusion; suggests cardiogenic shock or sepsis',
      '🟠 URGENT: Hypotension with adequate volume - suggests pump failure; may need inotropic support'
    ],

    complications: [
      'CARDIOGENIC SHOCK: Most severe presentation; inadequate tissue perfusion; requires inotropes, vasopressors, mechanical support; mortality 40-60%',
      'ACUTE KIDNEY INJURY (Cardiorenal Syndrome): Worsening renal function due to low cardiac output or venous congestion; may require renal replacement therapy',
      'ARRHYTHMIAS: Atrial fibrillation (precipitant and consequence), ventricular tachycardia/fibrillation (sudden cardiac death risk)',
      'THROMBOEMBOLIC EVENTS: Intracardiac thrombus (especially with AF and low EF), DVT/PE due to immobility and venous stasis',
      'HEPATIC CONGESTION: Elevated liver enzymes, coagulopathy; "cardiac cirrhosis" in chronic RHF',
      'HYPONATREMIA: Dilutional from neurohormonal activation and impaired free water excretion; marker of poor prognosis',
      'ELECTROLYTE DISTURBANCES: Hypokalemia, hypomagnesemia from diuretics; hyperkalemia from MRA/ACEi/renal failure',
      'DIURETIC RESISTANCE: Inadequate response to loop diuretics; requires combination therapy or alternative strategies',
      'RESPIRATORY FAILURE: May require non-invasive or invasive mechanical ventilation',
      'PROTEIN-LOSING ENTEROPATHY: Severe right heart failure causes intestinal edema and protein loss',
      'CARDIAC CACHEXIA: Involuntary weight loss in chronic/end-stage HF; indicates poor prognosis',
      'DEPRESSION: Common comorbidity; associated with worse outcomes and adherence'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
MORTALITY RATES
════════════════════════════════════════════════════════════════════════════════

• In-hospital mortality: 4-10% overall; varies by presentation
  - Hypertensive AHF: 2-3%
  - Normotensive AHF: 5-7%
  - Hypotensive/cardiogenic shock: 20-40%

• 30-day mortality: 10-15%
• 90-day mortality: 15-20%
• 1-year mortality: 25-35%
• 5-year mortality: 50-75%

• 30-day rehospitalization: 20-25% (major quality metric)
• 1-year rehospitalization: 50%

════════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
════════════════════════════════════════════════════════════════════════════════

POOR PROGNOSTIC MARKERS:
• Low systolic BP at presentation (<100-120 mmHg)
• Elevated BNP/NT-proBNP (and failure to decrease with treatment)
• Elevated troponin
• Hyponatremia (Na <135, especially <130)
• Renal dysfunction (elevated creatinine, low eGFR)
• Elevated lactate
• Low ejection fraction (especially <25%)
• Reduced cardiac index (<2.2 L/min/m²)
• Need for inotropes or mechanical support
• Older age
• Comorbidities (diabetes, CKD, COPD)
• Previous HF hospitalizations
• Ischemic etiology
• Cardiac cachexia

FAVORABLE PROGNOSTIC MARKERS:
• Hypertensive presentation (responds well to vasodilators)
• Identifiable and reversible precipitant
• Good response to diuretics
• Preserved renal function
• Improvement in BNP with treatment
• Ability to tolerate GDMT

════════════════════════════════════════════════════════════════════════════════
OUTCOMES BY CLASSIFICATION
════════════════════════════════════════════════════════════════════════════════

BY EJECTION FRACTION:
• HFrEF: Worse short-term prognosis but more treatment options
• HFpEF: Better short-term but limited disease-modifying therapies
• HFmrEF: Intermediate; may benefit from HFrEF therapies

BY HEMODYNAMIC PROFILE (Nohria-Stevenson):
• Warm-Dry (A): Best prognosis
• Warm-Wet (B): Intermediate prognosis, responds to diuretics
• Cold-Wet (C): Poor prognosis, requires inotropes
• Cold-Dry (L): Worst prognosis, hypovolemic or severely decompensated`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION (Preventing Heart Failure)
════════════════════════════════════════════════════════════════════════════════

CARDIOVASCULAR RISK FACTOR MANAGEMENT:
• Blood pressure control: Target <130/80 mmHg; reduces HF incidence by 50%
• Diabetes management: SGLT2 inhibitors reduce HF hospitalization in diabetics
• Dyslipidemia treatment: Statins reduce ischemic events that lead to HFrEF
• Smoking cessation: Reduces CAD risk and direct myocardial toxicity
• Weight management: Obesity increases HF risk
• Alcohol moderation: Excessive alcohol causes alcoholic cardiomyopathy
• Regular exercise: Reduces HF risk

MANAGEMENT OF STAGE A (At Risk):
• Treat hypertension, diabetes, dyslipidemia
• ACEi/ARB for patients with CAD, diabetes, or hypertension at high risk
• Avoid cardiotoxic agents when possible

MANAGEMENT OF STAGE B (Structural Disease, No Symptoms):
• ACEi/ARB and beta-blocker for reduced EF
• Statins for CAD
• ICD for post-MI patients with EF ≤30%
• Revascularization for CAD as appropriate

════════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Decompensation)
════════════════════════════════════════════════════════════════════════════════

GUIDELINE-DIRECTED MEDICAL THERAPY (GDMT) FOR HFrEF:
• ACEi/ARB/ARNI: Reduce mortality and hospitalization
• Beta-blockers: Carvedilol, metoprolol succinate, or bisoprolol
• MRA: Spironolactone or eplerenone
• SGLT2 inhibitors: Dapagliflozin or empagliflozin - reduce mortality and hospitalization
• Hydralazine/isosorbide dinitrate: For African descent or ACEi/ARB intolerant

PATIENT EDUCATION AND SELF-CARE:
• Daily weights: Report gain >1 kg/day or 2 kg/week
• Low sodium diet: <2 g sodium per day
• Fluid restriction: 1.5-2 L/day if hyponatremic or refractory
• Medication adherence: Most common cause of decompensation is non-adherence
• Recognize early warning signs: Increased dyspnea, edema, weight gain, fatigue
• Avoid NSAIDs, excessive alcohol, cardiotoxic drugs

DEVICE THERAPY:
• ICD for primary prevention if EF ≤35% after 3 months optimal therapy
• CRT for LBBB with QRS ≥150 ms and EF ≤35%

════════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT CONSIDERATIONS
════════════════════════════════════════════════════════════════════════════════

• HYPERTENSION CONTROL is critical - most common HF etiology in Africa
• RHEUMATIC HEART DISEASE prevention: Penicillin prophylaxis for RF
• PERIPARTUM CARDIOMYOPATHY: Avoid traditional postpartum practices (heat exposure, high salt)
• HIV-ASSOCIATED CARDIOMYOPATHY: ART adherence, early diagnosis
• MEDICATION ACCESS: Ensure availability of affordable essential medications (furosemide, ACEi, beta-blockers)
• COMMUNITY EDUCATION: Symptom recognition, medication adherence, danger signs`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

════════════════════════════════════════════════════════════════════════════════
UNDERSTANDING YOUR CONDITION (Plain Language)
════════════════════════════════════════════════════════════════════════════════

You have heart failure, which means your heart is not pumping blood as well as it should. This causes fluid to build up in your body, making you feel short of breath and causing swelling in your legs and feet.

Heart failure is a serious condition, but with proper treatment and self-care, many people live well with heart failure for many years.

COMMON SYMPTOMS:
• Shortness of breath - especially when lying down or with activity
• Swelling in feet, ankles, legs, or belly
• Feeling very tired and weak
• Weight gain from fluid buildup
• Coughing, especially at night
• Needing to sleep propped up on pillows
• Waking up at night gasping for breath

════════════════════════════════════════════════════════════════════════════════
YOUR MEDICATIONS
════════════════════════════════════════════════════════════════════════════════

You will likely be prescribed several medications that work together to help your heart:

• WATER PILLS (DIURETICS) like furosemide: Help remove extra fluid from your body
• ACE INHIBITORS or ARBs (names ending in -pril or -sartan): Help your heart pump better and reduce strain
• BETA-BLOCKERS (names ending in -olol): Slow your heart rate and help it pump more efficiently
• ALDOSTERONE BLOCKERS (spironolactone): Help with fluid balance and protect your heart

IMPORTANT: Take all your medications exactly as prescribed. Do not stop any medication without talking to your doctor first, even if you feel better.

════════════════════════════════════════════════════════════════════════════════
DAILY SELF-CARE - VERY IMPORTANT
════════════════════════════════════════════════════════════════════════════════

1. WEIGH YOURSELF EVERY DAY:
   • Same time each morning, after using the toilet, before eating
   • Use the same scale
   • Write down your weight
   • Call your doctor if you gain more than 1-2 kg in a day or 2-3 kg in a week

2. LIMIT SALT (SODIUM):
   • No more than 2 grams (2000 mg) of sodium per day
   • Do not add salt to food at the table
   • Avoid processed foods, fast food, canned soups, and salty snacks
   • Read food labels carefully

3. LIMIT FLUIDS (if your doctor advises):
   • Usually 1.5-2 liters (6-8 cups) per day
   • This includes water, tea, coffee, soup, and ice

4. TAKE YOUR MEDICATIONS:
   • Every day, at the same time
   • Do not skip doses
   • Keep a list of all your medications

5. STAY ACTIVE (as tolerated):
   • Light walking and gentle activity are usually good
   • Rest when you feel tired
   • Ask your doctor about cardiac rehabilitation

════════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - WHEN TO CALL YOUR DOCTOR
════════════════════════════════════════════════════════════════════════════════

Call your doctor or nurse if you notice:
• Weight gain of more than 1-2 kg in one day
• Weight gain of more than 2-3 kg in one week
• Increased swelling in feet, ankles, legs, or belly
• More shortness of breath than usual
• Needing more pillows to sleep
• Waking up at night short of breath
• New or worse cough
• Feeling more tired than usual
• Loss of appetite or nausea
• Dizziness or lightheadedness

════════════════════════════════════════════════════════════════════════════════
WHEN TO GO TO THE EMERGENCY ROOM
════════════════════════════════════════════════════════════════════════════════

Go to the emergency room immediately if you have:
🔴 Severe shortness of breath that does not improve with rest
🔴 Chest pain or pressure
🔴 Fainting or near-fainting
🔴 Fast or irregular heartbeat that makes you feel very unwell
🔴 Coughing up pink, frothy mucus
🔴 Confusion or difficulty thinking clearly
🔴 Bluish color to lips or fingertips

════════════════════════════════════════════════════════════════════════════════
THINGS TO AVOID
════════════════════════════════════════════════════════════════════════════════

• Do NOT take NSAIDs (ibuprofen, naproxen, diclofenac) - these can make heart failure worse
• Avoid excessive alcohol
• Do not smoke
• Avoid very hot or very cold environments
• Do not add salt to your food

════════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR HEALTHCARE PROVIDER
════════════════════════════════════════════════════════════════════════════════

• What caused my heart failure?
• What is my heart's pumping strength (ejection fraction)?
• What are my target weights and when should I call?
• How much fluid should I drink each day?
• When should I take my water pill?
• What activities are safe for me?
• When is my follow-up appointment?`,

    references: [
      'McDonagh TA, et al. 2021 ESC Guidelines for the diagnosis and treatment of acute and chronic heart failure. Eur Heart J. 2021;42(36):3599-3726. doi:10.1093/eurheartj/ehab368',
      'Heidenreich PA, et al. 2022 AHA/ACC/HFSA Guideline for the Management of Heart Failure. Circulation. 2022;145(18):e895-e1032. doi:10.1161/CIR.0000000000001063',
      'Mebazaa A, et al. Recommendations on pre-hospital and early hospital management of acute heart failure: a consensus paper. Eur Heart J. 2015;36(30):1958-66.',
      'Ponikowski P, et al. 2016 ESC Guidelines for the diagnosis and treatment of acute and chronic heart failure. Eur Heart J. 2016;37(27):2129-2200.',
      'Nieminen MS, et al. EuroHeart Failure Survey II (EHFS II): a survey on hospitalized acute heart failure patients. Eur Heart J. 2006;27(22):2725-36.',
      'Sliwa K, et al. Readmission and death after an acute heart failure event: predictors and outcomes in sub-Saharan Africa: results from the THESUS-HF registry. Eur Heart J. 2013;34(40):3151-9.',
      'Dokainish H, et al. Heart Failure in Africa, Asia, the Middle East and South America: The INTER-CHF study. Int J Cardiol. 2016;204:133-41.',
      'Damasceno A, et al. The causes, treatment, and outcome of acute heart failure in 1006 Africans from 9 countries: Results of the sub-Saharan Africa survey of heart failure. Arch Intern Med. 2012;172(18):1386-94.',
      'Yancy CW, et al. 2017 ACC/AHA/HFSA Focused Update of the 2013 ACCF/AHA Guideline for the Management of Heart Failure. Circulation. 2017;136(6):e137-e161.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 13. STATUS EPILEPTICUS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '13',
    name: 'Status Epilepticus',
    slug: 'status-epilepticus',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'G41.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Status epilepticus (SE) is a neurological emergency defined as continuous seizure activity lasting more than 5 minutes OR two or more discrete seizures without full recovery of consciousness between them. This represents the most severe form of seizure disorder with significant morbidity and mortality if not treated promptly and aggressively.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• TIME IS BRAIN: Status epilepticus is defined as ≥5 minutes of continuous seizure activity (not 30 minutes as in older definitions). Treatment must begin immediately.

• TREATMENT PROTOCOL by time from seizure onset:
  - 0-5 min: Stabilize (ABCs), check glucose, give benzodiazepine
  - 5-20 min (Early SE): IV lorazepam 0.1 mg/kg OR diazepam 0.15-0.2 mg/kg OR IM midazolam 10 mg
  - 20-40 min (Established SE): IV fosphenytoin 20 mg PE/kg OR valproate 40 mg/kg OR levetiracetam 60 mg/kg
  - >40 min (Refractory SE): Anesthetic doses - propofol, midazolam, or pentobarbital with continuous EEG monitoring

• BENZODIAZEPINES are FIRST-LINE - Lorazepam IV preferred; Midazolam IM if no IV access (equally effective per RAMPART trial)

• ALWAYS CHECK GLUCOSE - Hypoglycemia is a reversible cause; give D50W if glucose <60 mg/dL; give thiamine first if alcoholism suspected

• CAUSES to identify and treat (mnemonic "VITAMINS"):
  V - Vascular (stroke, hemorrhage)
  I - Infection (meningitis, encephalitis, cerebral malaria)
  T - Trauma, Tumor
  A - Alcohol withdrawal, Anoxia
  M - Metabolic (glucose, sodium, calcium, uremia, hepatic)
  I - Idiopathic/Inadequate AED levels
  N - Non-convulsive SE (subtle signs - eye deviation, automatisms)
  S - Substances (cocaine, theophylline toxicity)

• PHENYTOIN/FOSPHENYTOIN requires cardiac monitoring - can cause hypotension and arrhythmias; give at ≤50 mg/min (phenytoin) or ≤150 mg PE/min (fosphenytoin)

• NON-CONVULSIVE STATUS EPILEPTICUS (NCSE) may present as altered mental status without obvious convulsions - requires EEG for diagnosis; common after convulsive SE

• REFRACTORY SE (fails 2 appropriate AEDs): Requires ICU, intubation, continuous EEG, and anesthetic infusions; mortality 20-40%

• MORTALITY increases with duration: <30 min: 3%; 30-60 min: 19%; >60 min: 32%. Brain damage begins within 30-60 minutes of continuous seizure activity

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

STATUS EPILEPTICUS represents a failure of the normal mechanisms that terminate seizures:

PHASE 1 (0-30 minutes) - COMPENSATION:
• Massive sympathetic activation: Tachycardia, hypertension, hyperglycemia
• Increased cerebral blood flow (up to 300% of normal)
• Preserved autoregulation initially
• Systemic: Hyperthermia, lactic acidosis, rhabdomyolysis begins

PHASE 2 (30-60 minutes) - DECOMPENSATION:
• Cerebral autoregulation fails
• Blood pressure drops → cerebral hypoperfusion
• Glucose utilization exceeds supply → neuronal energy failure
• Excitotoxicity: Excessive glutamate release → calcium influx → cell death
• GABA receptor internalization (explains benzodiazepine resistance)
• NMDA receptor upregulation (explains ongoing excitotoxicity)

PHASE 3 (>60 minutes) - NEURONAL INJURY:
• Selective neuronal necrosis (hippocampus especially vulnerable)
• Systemic complications: Hyperthermia, rhabdomyolysis, DIC, pulmonary edema
• Multi-organ dysfunction syndrome

SELF-SUSTAINING SEIZURES:
After approximately 30-45 minutes, seizures become self-sustaining due to:
• Decreased inhibitory neurotransmission (GABA receptor changes)
• Increased excitatory neurotransmission (glutamate, NMDA)
• Altered gene expression
• Inflammatory cascade activation

This explains why early treatment is critical and why benzodiazepines become less effective over time.`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: 10-41 per 100,000 person-years (higher in developing countries)
• Bimodal age distribution: Peaks in children <1 year and adults >60 years
• Accounts for 1-8% of all hospital epilepsy admissions
• 10-20% of patients with epilepsy will have at least one episode of SE
• Overall mortality: 10-30% (varies by etiology and duration)
• Mortality in refractory SE: 30-50%
• Survivors: 10-30% have significant neurological sequelae

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• Higher incidence in Africa: 40-80 per 100,000 (2-3x higher than developed countries)
• COMMON CAUSES in Africa differ from Western countries:
  - CNS infections: Cerebral malaria (leading cause in endemic areas), meningitis, encephalitis
  - Febrile seizures in children (often complicated by malaria)
  - HIV-related: Opportunistic infections (toxoplasmosis, cryptococcal meningitis, TB meningitis), HIV encephalopathy
  - AED non-compliance due to medication unavailability or cost
  - Alcohol withdrawal
  - Perinatal complications (birth asphyxia, neonatal infections)

• Higher mortality in Africa (30-50%) due to:
  - Late presentation
  - Limited access to IV benzodiazepines and second-line AEDs
  - Limited ICU capacity for refractory SE
  - Delayed recognition of non-convulsive SE
  - Underlying infectious etiology with high severity

• CEREBRAL MALARIA-related seizures:
  - SE occurs in 20-30% of cerebral malaria cases
  - Prolonged seizures associated with worse outcomes
  - Requires simultaneous antimalarial therapy

• MEDICATION AVAILABILITY challenges:
  - IV lorazepam often unavailable; diazepam is most common first-line agent
  - Phenobarbital often used as second-line due to cost/availability
  - Fosphenytoin rarely available; phenytoin requires careful monitoring
  - Levetiracetam increasingly available in urban centers

• PEDIATRIC SE is particularly common:
  - Febrile SE accounts for 25-50% of pediatric SE in Africa
  - Often associated with malaria, bacterial meningitis

═══════════════════════════════════════════════════════════════════════════════
RISK FACTORS
═══════════════════════════════════════════════════════════════════════════════

EPILEPSY-RELATED:
• Known epilepsy with AED non-compliance (most common cause in known epileptics)
• Subtherapeutic AED levels
• Epilepsy syndrome with high SE risk (Dravet syndrome, Lennox-Gastaut)
• Remote symptomatic epilepsy (prior brain injury)

ACUTE CAUSES:
• CNS infections (meningitis, encephalitis, cerebral malaria, brain abscess)
• Acute stroke (ischemic or hemorrhagic)
• Traumatic brain injury
• Hypoxic-ischemic encephalopathy
• Autoimmune encephalitis
• Posterior reversible encephalopathy syndrome (PRES)

METABOLIC:
• Hypoglycemia (most important to identify - rapidly reversible)
• Hyponatremia
• Hypocalcemia
• Uremia
• Hepatic encephalopathy

TOXIC/WITHDRAWAL:
• Alcohol withdrawal
• Benzodiazepine withdrawal
• Drug intoxication (cocaine, amphetamines, synthetic cannabinoids)
• Medication toxicity (theophylline, isoniazid, tramadol, antibiotics)`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
TYPES OF STATUS EPILEPTICUS
═══════════════════════════════════════════════════════════════════════════════

1. CONVULSIVE STATUS EPILEPTICUS (CSE) - Most Common and Dangerous:
   • Generalized tonic-clonic (GTC) activity
   • Bilateral convulsive movements
   • Loss of consciousness
   • Post-ictal confusion if seizures terminate

2. NON-CONVULSIVE STATUS EPILEPTICUS (NCSE):
   • Altered mental status WITHOUT obvious convulsions
   • May show subtle signs: eye deviation, nystagmus, facial twitching, automatisms
   • Can occur de novo or follow CSE ("subtle SE")
   • Requires EEG for diagnosis
   • Often missed - high index of suspicion needed

3. FOCAL STATUS EPILEPTICUS:
   • Epilepsia partialis continua: Continuous focal motor seizures (often arm/face)
   • Focal SE with impaired awareness
   • May secondarily generalize

4. ABSENCE STATUS EPILEPTICUS:
   • Prolonged absence seizures
   • Altered awareness without convulsions
   • EEG shows continuous 3 Hz spike-wave

═══════════════════════════════════════════════════════════════════════════════
CLINICAL FEATURES
═══════════════════════════════════════════════════════════════════════════════

DURING CONVULSIVE SE:
• Bilateral tonic-clonic movements
• Loss of consciousness
• Cyanosis (due to respiratory compromise)
• Tongue biting, oral trauma
• Incontinence (urinary, fecal)
• Autonomic features: Tachycardia, hypertension, hyperthermia, salivation, lacrimation, pupil dilation

SYSTEMIC COMPLICATIONS (develop over time):
• Hyperthermia (can exceed 40°C - neuronal injury risk)
• Respiratory failure (aspiration, pulmonary edema, exhaustion)
• Rhabdomyolysis (muscle breakdown from continuous contractions)
• Metabolic acidosis (lactic acidosis from anaerobic metabolism)
• Cardiac arrhythmias (from catecholamine surge)
• Disseminated intravascular coagulation (DIC)

AFTER PROLONGED SE:
• Post-ictal confusion (may last hours to days)
• Todd paralysis (focal weakness)
• Persistent encephalopathy (may indicate ongoing NCSE)
• Amnesia

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia (often >120 bpm)
• Hypertension initially (may drop with prolonged SE)
• Tachypnea or apnea
• Hyperthermia (temperature often >38.5°C)
• Oxygen desaturation

NEUROLOGICAL EXAMINATION (inter-ictal or NCSE):
• Altered level of consciousness (GCS often reduced)
• Eye deviation (may indicate seizure focus)
• Subtle motor signs: facial twitching, finger movements, nystagmus
• Pupil changes: May be dilated and sluggish
• Absent or asymmetric reflexes post-ictally
• Todd paralysis (focal weakness suggesting focal onset)

SIGNS SUGGESTING ETIOLOGY:
• Fever, meningismus → CNS infection
• Focal neurological deficits → Stroke, mass lesion
• Track marks → Drug overdose/withdrawal
• Jaundice, asterixis → Hepatic encephalopathy
• Uremic frost, pericardial rub → Uremia
• Splenomegaly, pallor → Malaria in endemic areas`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

STATUS EPILEPTICUS is diagnosed when:
• Continuous seizure activity lasts ≥5 minutes, OR
• Two or more seizures occur without full recovery of consciousness between them

OPERATIONAL DEFINITIONS (2015 ILAE):
• Time point t1 (5 min): When treatment should be initiated
• Time point t2 (30 min): When ongoing seizure activity may cause long-term consequences

TYPES BY SEMIOLOGY:
• Convulsive (with prominent motor symptoms)
• Non-convulsive (without prominent motor symptoms) - requires EEG

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

IMMEDIATE (while treating):
1. History (from witnesses): Duration, description, precipitants, prior seizures, medications, alcohol/drugs
2. Fingerstick glucose - TREAT IMMEDIATELY if hypoglycemic
3. Vital signs and neurological assessment
4. IV access and blood draw for labs

LABORATORY WORKUP:
• Point-of-care glucose (IMMEDIATE)
• Complete metabolic panel (sodium, calcium, magnesium, BUN/creatinine, glucose)
• Complete blood count
• Liver function tests
• Arterial blood gas (assess acidosis)
• AED levels (phenytoin, valproate, carbamazepine if on therapy)
• Lactate
• Toxicology screen (urine and/or serum)
• Blood cultures if febrile

ADDITIONAL TESTS based on clinical suspicion:
• Lumbar puncture (if infection suspected - after CT if focal signs)
• Malaria smear/RDT (in endemic areas)
• HIV test (consider in all SE in Africa)
• Ammonia (hepatic encephalopathy)
• Thyroid function tests
• Autoimmune encephalitis panel (anti-NMDA receptor, etc.)
• Drug levels (theophylline, lithium, digoxin)

═══════════════════════════════════════════════════════════════════════════════
IMAGING
═══════════════════════════════════════════════════════════════════════════════

CT HEAD (without contrast):
• Indications: New-onset SE, focal onset, trauma, HIV-positive, focal deficit, papilledema
• Findings: Hemorrhage, mass, stroke, cerebral edema, hydrocephalus
• Should NOT delay treatment for SE

MRI BRAIN (with contrast):
• More sensitive for encephalitis, subtle infarcts, tumors, mesial temporal sclerosis
• Post-SE changes: Hippocampal signal change, cortical edema (DWI restriction)
• Perform after stabilization if etiology unclear

═══════════════════════════════════════════════════════════════════════════════
ELECTROENCEPHALOGRAM (EEG)
═══════════════════════════════════════════════════════════════════════════════

INDICATIONS:
• Non-convulsive SE (altered mental status without convulsions)
• Persistent encephalopathy after convulsive SE controlled
• Monitoring during treatment of refractory SE
• Guiding anesthetic titration

FINDINGS IN SE:
• Continuous ictal discharges
• Periodic lateralized epileptiform discharges (PLEDs/LPDs)
• Generalized periodic discharges (GPDs)
• Electrographic seizures without clinical correlation

CONTINUOUS EEG (cEEG):
• Gold standard for monitoring refractory SE
• Required for anesthetic management
• Target: Burst suppression for 24-48 hours in refractory SE`,

    differentialDiagnosis: [
      'Psychogenic non-epileptic seizures (PNES) - Eyes often closed, asynchronous movements, no post-ictal confusion, preserved awareness despite bilateral movements, longer duration, can coexist with epilepsy',
      'Syncope with convulsive movements - Brief myoclonic jerks after loss of consciousness, rapid recovery, precipitating factors (standing, pain, micturition)',
      'Decerebrate/decorticate posturing - Fixed posture, not rhythmic, associated with severe brain injury',
      'Rigors - Shivering with infection, teeth chattering, preserved consciousness, responds to temperature normalization',
      'Movement disorders - Dystonic storm, chorea; sustained/continuous movements without altered consciousness',
      'Drug-induced dyskinesias - History of neuroleptic use, oculogyric crisis, tongue movements',
      'Encephalopathy with myoclonus - Uremic, hepatic, anoxic; stimulus-sensitive myoclonus, underlying metabolic cause',
      'Tetanus - Risus sardonicus, opisthotonus, board-like rigidity, clear sensorium, no post-ictal phase',
      'Strychnine poisoning - Similar to tetanus, preserved consciousness, extensor spasms, facial grimacing',
      'Hypocalcemia - Carpopedal spasm, Chvostek sign, Trousseau sign, prolonged QT interval',
      'Catatonia - Immobility, waxy flexibility, mutism; responds to benzodiazepines but different etiology'
    ],

    investigations: [
      {
        name: 'Point-of-Care Glucose',
        indication: '🔴 IMMEDIATE - Before anything else; hypoglycemia is rapidly reversible cause of seizures',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Glucose, Sodium, Calcium, Magnesium, BUN, Creatinine)',
        indication: '🔴 Identify metabolic causes: hyponatremia, hypocalcemia, hypoglycemia, uremia',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Infection (leukocytosis), anemia, thrombocytopenia (DIC, malaria)',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 Assess acidosis (lactic acidosis common in SE), oxygenation, ventilation status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Antiepileptic Drug Levels',
        indication: 'Phenytoin, valproate, carbamazepine, phenobarbital levels if patient on AEDs - subtherapeutic levels common cause',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lactate',
        indication: 'Elevated in prolonged SE; marker of severity; can guide response to treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Liver Function Tests',
        indication: 'Hepatic encephalopathy as cause; baseline before AED loading',
        availability: 'BASIC'
      },
      {
        name: 'Toxicology Screen (Urine/Serum)',
        indication: 'Suspected intoxication or withdrawal; cocaine, amphetamines, synthetic cannabinoids',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Cultures',
        indication: 'If febrile or infection suspected; obtain before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Smear / Rapid Diagnostic Test',
        indication: '🔴 ESSENTIAL in endemic areas; cerebral malaria is leading cause of SE in Africa',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🔴 Consider in all SE in Africa; HIV-related opportunistic infections and encephalopathy',
        availability: 'BASIC'
      },
      {
        name: 'CT Head (Non-Contrast)',
        indication: '🔴 New-onset SE, focal onset, trauma, immunocompromised, focal deficits; rule out hemorrhage, mass, hydrocephalus',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture',
        indication: '🔴 Suspected CNS infection (meningitis, encephalitis); after CT if focal signs or papilledema',
        availability: 'BASIC'
      },
      {
        name: 'EEG (Electroencephalogram)',
        indication: '🔴 Non-convulsive SE diagnosis; persistent encephalopathy; monitoring refractory SE treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Continuous EEG Monitoring',
        indication: 'Refractory SE requiring anesthetic infusions; guides titration to burst suppression',
        availability: 'ADVANCED'
      },
      {
        name: 'MRI Brain',
        indication: 'Unexplained SE after initial workup; suspected encephalitis, tumor, stroke; post-SE to assess injury',
        availability: 'ADVANCED'
      },
      {
        name: 'Ammonia Level',
        indication: 'Suspected hepatic encephalopathy; valproate-induced hyperammonemia',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ TIME ZERO: Note the time seizure started - critical for treatment decisions',
        '⚡ PROTECT AIRWAY: Position patient on side (recovery position), suction secretions, insert oral airway if possible (do not force), prepare for intubation',
        '⚡ OXYGEN: High-flow oxygen via non-rebreather mask; bag-valve-mask if apneic',
        '⚡ IV ACCESS: Establish two large-bore IV lines; if IV unsuccessful within 2 minutes, give IM midazolam',
        '⚡ CHECK GLUCOSE: Fingerstick immediately; give D50W 50 mL IV if glucose <60 mg/dL (give thiamine 100 mg IV first if alcoholism suspected)',
        '⚡ BENZODIAZEPINE (0-5 min): Lorazepam 0.1 mg/kg IV (max 4 mg) OR Diazepam 0.15-0.2 mg/kg IV (max 10 mg) OR Midazolam 10 mg IM if no IV access',
        '⚡ REPEAT BENZODIAZEPINE: If seizure continues after 5 min, repeat once',
        '⚡ SECOND-LINE AED (5-20 min): If seizure persists, give fosphenytoin 20 mg PE/kg IV OR phenytoin 20 mg/kg IV (max 50 mg/min with cardiac monitoring) OR valproate 40 mg/kg IV OR levetiracetam 60 mg/kg IV',
        '⚡ MONITOR: Continuous pulse oximetry, cardiac monitor, blood pressure; check temperature',
        '⚡ IDENTIFY AND TREAT CAUSE: Treat hypoglycemia, start antibiotics if meningitis suspected, antimalarials if cerebral malaria suspected'
      ],
      pharmacological: [
        {
          drug: 'Lorazepam (Ativan)',
          indication: '🔴 FIRST-LINE for status epilepticus - IV benzodiazepine of choice',
          dose: '0.1 mg/kg IV (max 4 mg per dose); may repeat once after 5 minutes if seizure persists',
          route: 'IV (preferred) or IM',
          duration: 'Single or two doses; onset 2-3 minutes IV; duration 12-24 hours',
          contraindications: ['Acute narrow-angle glaucoma', 'Severe respiratory depression (relative)', 'Known lorazepam allergy'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Sedation', 'Paradoxical agitation (rare)'],
          alternatives: ['Diazepam IV', 'Midazolam IM (if no IV access)']
        },
        {
          drug: 'Diazepam (Valium)',
          indication: '🔴 FIRST-LINE alternative if lorazepam unavailable; widely available in Africa',
          dose: '0.15-0.2 mg/kg IV (max 10 mg per dose); may repeat once after 5 minutes; Rectal: 0.2-0.5 mg/kg',
          route: 'IV (preferred), Rectal (PR), IM (poorly absorbed IM)',
          duration: 'Onset 1-3 minutes IV; shorter duration than lorazepam (15-30 min), higher recurrence rate',
          contraindications: ['Acute narrow-angle glaucoma', 'Severe respiratory insufficiency', 'Myasthenia gravis'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Sedation', 'Phlebitis (IV)'],
          alternatives: ['Lorazepam IV', 'Midazolam IM']
        },
        {
          drug: 'Midazolam (Versed)',
          indication: '🔴 FIRST-LINE if no IV access - IM midazolam as effective as IV lorazepam (RAMPART trial)',
          dose: 'IM: 10 mg (>40 kg) or 5 mg (13-40 kg); IV: 0.2 mg/kg (max 10 mg); Intranasal/Buccal: 0.2 mg/kg',
          route: 'IM (first-line if no IV), IV, Intranasal, Buccal',
          duration: 'Onset 3-5 min IM, 1-2 min IV; duration 1-2 hours; may need repeat dosing',
          contraindications: ['Severe respiratory insufficiency', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Sedation'],
          alternatives: ['Diazepam rectal if midazolam unavailable']
        },
        {
          drug: 'Fosphenytoin (Cerebyx)',
          indication: '🔴 SECOND-LINE after benzodiazepine failure; prodrug of phenytoin with better safety profile',
          dose: '20 mg PE/kg IV at max rate 150 mg PE/min; additional 5-10 mg PE/kg if needed',
          route: 'IV or IM (IM absorption reliable, unlike phenytoin)',
          duration: 'Loading dose; then maintenance 4-6 mg PE/kg/day divided q8-12h',
          contraindications: ['Sinus bradycardia', 'SA block', 'Second/third degree AV block', 'Adams-Stokes syndrome'],
          sideEffects: ['Hypotension (less than phenytoin)', 'Cardiac arrhythmias', 'Purple glove syndrome (rare)', 'Nystagmus', 'Ataxia'],
          alternatives: ['Phenytoin (if fosphenytoin unavailable)', 'Valproate', 'Levetiracetam']
        },
        {
          drug: 'Phenytoin (Dilantin)',
          indication: '🔴 SECOND-LINE if fosphenytoin unavailable; requires cardiac monitoring during infusion',
          dose: '20 mg/kg IV at max rate 50 mg/min (25 mg/min in elderly); additional 5-10 mg/kg if needed',
          route: 'IV only (must use NS, incompatible with dextrose); filter required',
          duration: 'Loading dose; then maintenance 4-6 mg/kg/day divided q8-12h; check level in 2 hours',
          contraindications: ['Sinus bradycardia', 'SA/AV block', 'Adams-Stokes syndrome', 'Hypersensitivity'],
          sideEffects: ['Hypotension (common, rate-related)', 'Cardiac arrhythmias', 'Purple glove syndrome', 'Soft tissue injury if extravasation'],
          alternatives: ['Fosphenytoin', 'Valproate', 'Phenobarbital']
        },
        {
          drug: 'Valproate Sodium (Depakene/Epilim)',
          indication: '🔴 SECOND-LINE alternative; safe in all seizure types; no cardiac monitoring needed',
          dose: '40 mg/kg IV over 10 minutes (max 3000 mg); can give additional 20 mg/kg if needed',
          route: 'IV',
          duration: 'Loading dose; then maintenance 30-60 mg/kg/day divided q6-8h',
          contraindications: ['Hepatic disease', 'Urea cycle disorders', 'Mitochondrial disease (Alpers syndrome)', 'Pregnancy (neural tube defects)', 'Pancreatitis history'],
          sideEffects: ['Hyperammonemia', 'Hepatotoxicity', 'Thrombocytopenia', 'Pancreatitis', 'Hypotension (less than phenytoin)'],
          alternatives: ['Phenytoin/Fosphenytoin', 'Levetiracetam', 'Phenobarbital']
        },
        {
          drug: 'Levetiracetam (Keppra)',
          indication: 'SECOND-LINE alternative; excellent safety profile; no drug interactions; no cardiac/hepatic monitoring needed',
          dose: '60 mg/kg IV over 15 minutes (max 4500 mg); some protocols use up to 60 mg/kg',
          route: 'IV',
          duration: 'Loading dose; then maintenance 1000-3000 mg/day divided q12h',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Somnolence', 'Behavioral changes (irritability, aggression)', 'Dizziness', 'Minimal drug interactions'],
          alternatives: ['Valproate', 'Phenytoin/Fosphenytoin', 'Phenobarbital']
        },
        {
          drug: 'Phenobarbital',
          indication: 'SECOND/THIRD-LINE; commonly used in Africa due to availability and cost; effective but causes significant sedation',
          dose: '20 mg/kg IV at max rate 60 mg/min; additional 5-10 mg/kg if needed (max 30 mg/kg)',
          route: 'IV or IM',
          duration: 'Loading dose; then maintenance 3-5 mg/kg/day; long half-life (70-140 hours)',
          contraindications: ['Severe respiratory disease', 'Porphyria', 'Severe hepatic impairment'],
          sideEffects: ['Respiratory depression (especially with benzodiazepines)', 'Sedation', 'Hypotension', 'Paradoxical excitation in children'],
          alternatives: ['Valproate', 'Levetiracetam', 'Fosphenytoin']
        },
        {
          drug: 'Propofol',
          indication: '🔴 REFRACTORY SE (>40 min) - requires intubation and ICU; rapid onset, short acting',
          dose: 'Bolus: 1-2 mg/kg IV; Infusion: 2-5 mg/kg/hour; titrate to EEG burst suppression',
          route: 'IV continuous infusion',
          duration: '24-48 hours of burst suppression, then slow wean over 24 hours',
          contraindications: ['Propofol infusion syndrome risk (>48h, >5 mg/kg/hr)', 'Egg/soy allergy', 'Hemodynamic instability'],
          sideEffects: ['Hypotension', 'Propofol infusion syndrome (rhabdomyolysis, metabolic acidosis, cardiac failure)', 'Respiratory depression', 'Hypertriglyceridemia'],
          alternatives: ['Midazolam infusion', 'Pentobarbital']
        },
        {
          drug: 'Midazolam Continuous Infusion',
          indication: '🔴 REFRACTORY SE - alternative to propofol; may have more tachyphylaxis',
          dose: 'Bolus: 0.2 mg/kg IV; Infusion: 0.1-2 mg/kg/hour; titrate to EEG burst suppression',
          route: 'IV continuous infusion',
          duration: '24-48 hours of burst suppression, then slow wean',
          contraindications: ['Severe respiratory insufficiency', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Tachyphylaxis (may need escalating doses)', 'Prolonged sedation'],
          alternatives: ['Propofol', 'Pentobarbital']
        },
        {
          drug: 'Pentobarbital (Nembutal)',
          indication: 'SUPER-REFRACTORY SE - when propofol and midazolam fail; profound sedation',
          dose: 'Bolus: 5-15 mg/kg IV; Infusion: 0.5-5 mg/kg/hour; titrate to burst suppression',
          route: 'IV continuous infusion',
          duration: '24-48+ hours; very prolonged wean required',
          contraindications: ['Porphyria', 'Severe cardiovascular disease'],
          sideEffects: ['Profound hypotension', 'Cardiac depression', 'Paralytic ileus', 'Immunosuppression', 'Prolonged coma'],
          alternatives: ['Ketamine infusion', 'Inhaled anesthetics']
        },
        {
          drug: 'Thiamine (Vitamin B1)',
          indication: 'Give BEFORE glucose in suspected alcoholism or malnutrition to prevent Wernicke encephalopathy',
          dose: '100-250 mg IV before or with dextrose',
          route: 'IV (preferred) or IM',
          duration: 'Single dose initially; continue 100 mg daily if thiamine deficiency suspected',
          contraindications: ['Known hypersensitivity (rare)'],
          sideEffects: ['Rare anaphylaxis with IV (give slowly)', 'Injection site irritation'],
          alternatives: ['None - essential in alcoholics']
        }
      ],
      nonPharmacological: [
        'AIRWAY PROTECTION: Position in lateral decubitus (recovery position); suction oropharynx; insert oral/nasal airway if tolerated; prepare for intubation if prolonged SE or respiratory failure',
        'INTUBATION: Indicated for respiratory failure, inability to protect airway, refractory SE requiring anesthetic infusions; use rapid sequence intubation with short-acting paralytics',
        'TEMPERATURE CONTROL: Aggressive cooling for hyperthermia (>40°C); cooling blankets, ice packs, cold IV fluids; avoid shivering',
        'SEIZURE PRECAUTIONS: Padded bed rails, remove hazardous objects, do NOT restrain or insert objects in mouth, document seizure characteristics',
        'CONTINUOUS MONITORING: Telemetry, pulse oximetry, ETCO2 if intubated, blood pressure; continuous EEG for refractory SE',
        'NEUROPROTECTION: Avoid hyperthermia, hypoglycemia, hypoxia; maintain normoglycemia and normotension',
        'ICU ADMISSION: Required for refractory SE, respiratory failure, hemodynamic instability, need for anesthetic infusions'
      ],
      surgical: [
        'EMERGENT NEUROSURGICAL INTERVENTION: May be required for underlying cause - hemorrhage evacuation, tumor resection, abscess drainage',
        'EPILEPSY SURGERY: For drug-resistant focal epilepsy causing recurrent SE; temporal lobectomy, lesionectomy, corpus callosotomy, VNS implantation',
        'VAGUS NERVE STIMULATOR (VNS): May help in refractory epilepsy prone to SE; magnet activation during seizure',
        'DECOMPRESSIVE CRANIECTOMY: Rarely needed for severe cerebral edema complicating SE'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: All refractory SE (failure of first and second-line agents); respiratory failure; hemodynamic instability; need for continuous EEG monitoring',
        '🔴 IMMEDIATE NEUROLOGY: All SE for evaluation and management; EEG interpretation; refractory SE management',
        '🔴 URGENT NEUROSURGERY: Structural lesion requiring intervention (hemorrhage, tumor, abscess); increased ICP',
        '🟠 INFECTIOUS DISEASE: Suspected CNS infection; HIV-positive patients; unusual infections',
        '🟠 EPILEPSY SPECIALIST: Recurrent SE; drug-resistant epilepsy; consideration of epilepsy surgery',
        '🟠 TOXICOLOGY: Suspected poisoning or drug overdose causing SE'
      ],
      monitoring: [
        'VITAL SIGNS: Continuous pulse oximetry and telemetry; blood pressure every 5-15 minutes during active treatment',
        'NEUROLOGICAL STATUS: Continuous observation for seizure activity; level of consciousness; pupil response; focal deficits',
        'CONTINUOUS EEG: Gold standard for monitoring refractory SE; detect non-convulsive SE; guide anesthetic titration to burst suppression',
        'GLUCOSE: Repeat every 1-2 hours initially; hypoglycemia common with prolonged SE',
        'ELECTROLYTES: Sodium, potassium, calcium, magnesium every 6-12 hours; correct abnormalities',
        'AED LEVELS: Check phenytoin level 2 hours after loading (target 15-20 mcg/mL); valproate level if used',
        'RENAL AND HEPATIC FUNCTION: Monitor for rhabdomyolysis (CK, myoglobin) and organ dysfunction',
        'TEMPERATURE: Frequent monitoring; treat hyperthermia aggressively'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
ACUTE PHASE FOLLOW-UP (During Hospitalization)
═══════════════════════════════════════════════════════════════════════════════

• Daily neurological assessment until return to baseline
• EEG to confirm seizure cessation and rule out NCSE
• MRI brain if etiology unclear or for post-SE injury assessment
• Transition from IV to oral AEDs once seizure-free 24-48 hours
• Optimize AED regimen for long-term management

═══════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

• Neurology follow-up within 1-2 weeks of discharge
• AED level monitoring at 2-4 weeks
• EEG as outpatient if not done during admission
• MRI if not completed during admission
• Driving restrictions counseling (varies by jurisdiction, typically 3-12 months)
• Assessment of functional recovery and need for rehabilitation

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM MANAGEMENT
═══════════════════════════════════════════════════════════════════════════════

• Regular neurology follow-up every 3-6 months
• AED adherence counseling - non-compliance is leading cause of recurrent SE
• AED level monitoring as indicated
• Seizure diary
• Discussion of epilepsy surgery if recurrent SE or drug-resistant epilepsy
• Screening for comorbidities: depression, anxiety, cognitive impairment
• Women of childbearing age: Contraception counseling, folic acid supplementation
• Lifestyle modifications: Sleep hygiene, alcohol avoidance, stress management`
    },

    redFlags: [
      '🔴 CRITICAL: Seizure duration >5 minutes - defines SE; requires immediate benzodiazepine',
      '🔴 CRITICAL: Refractory SE (seizures persist after benzodiazepine + second-line AED) - requires ICU, intubation, anesthetic infusions',
      '🔴 CRITICAL: Respiratory failure - apnea, oxygen desaturation <90%, cyanosis; prepare for intubation',
      '🔴 CRITICAL: Hyperthermia >40°C - neuronal injury threshold; aggressive cooling required',
      '🔴 CRITICAL: Hypoglycemia - immediately reversible cause; check and treat urgently',
      '🔴 CRITICAL: Signs of herniation - unilateral dilated pupil, decerebrate posturing; neurosurgical emergency',
      '🔴 CRITICAL: Persistent encephalopathy after convulsions stop - suspect non-convulsive SE; obtain EEG',
      '🟠 URGENT: New focal neurological deficit - suggests structural cause (stroke, tumor, abscess)',
      '🟠 URGENT: Fever with neck stiffness - meningitis/encephalitis; urgent LP after CT if no contraindication',
      '🟠 URGENT: Known HIV-positive - higher risk of CNS opportunistic infections',
      '🟠 URGENT: Recurrent seizures without recovery - continuing SE; escalate treatment',
      '🟠 URGENT: Severe metabolic acidosis (pH <7.0) - indicates prolonged SE, rhabdomyolysis; poor prognosis',
      '🟠 URGENT: Cardiac arrhythmias during phenytoin infusion - slow or stop infusion'
    ],

    complications: [
      'NEURONAL INJURY AND DEATH: Excitotoxic damage especially to hippocampus, cortex; occurs after 30-60 min of continuous seizure',
      'CEREBRAL EDEMA: May cause increased ICP; can progress to herniation',
      'ASPIRATION PNEUMONIA: From impaired airway protection during seizures; common cause of morbidity',
      'RHABDOMYOLYSIS: Muscle breakdown from continuous contractions; can cause AKI from myoglobin',
      'ACUTE KIDNEY INJURY: From rhabdomyolysis, hypotension, nephrotoxic medications',
      'HYPERTHERMIA: Temperature can exceed 40°C; itself causes neuronal injury',
      'METABOLIC ACIDOSIS: Lactic acidosis from anaerobic muscle metabolism; marker of severity',
      'CARDIAC ARRHYTHMIAS: From catecholamine surge, medications (phenytoin), electrolyte disturbances',
      'DISSEMINATED INTRAVASCULAR COAGULATION (DIC): In prolonged/severe SE',
      'PULMONARY EDEMA: Neurogenic pulmonary edema or aspiration',
      'COGNITIVE IMPAIRMENT: Memory deficits, executive dysfunction; may be permanent',
      'POST-SE EPILEPSY: SE can cause epilepsy in previously non-epileptic patients',
      'TODD PARALYSIS: Transient focal weakness post-ictally; usually resolves within 48 hours',
      'DEATH: Mortality 10-30% overall; up to 50% in refractory SE'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

• Overall mortality: 10-30% (varies by etiology, duration, age)
• Short-term mortality: 7-20% during hospitalization
• Mortality by duration:
  - <30 minutes: 3%
  - 30-60 minutes: 19%
  - >60 minutes: 32%
• Refractory SE mortality: 30-50%
• Super-refractory SE mortality: >50%

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Older age (>65 years)
• Prolonged duration (>60 minutes especially)
• Anoxic/hypoxic etiology
• Refractory or super-refractory SE
• Acute symptomatic cause (stroke, CNS infection, metabolic)
• Non-convulsive SE following convulsive SE
• Absent brainstem reflexes
• Myoclonic SE after cardiac arrest (near 100% mortality)
• Delay in treatment initiation
• Severe medical comorbidities
• Continuous EEG showing GPDs or PLEDs

FAVORABLE PROGNOSIS:
• Young age
• Known epilepsy with AED non-compliance (once levels restored)
• Febrile SE in children (usually excellent outcome)
• Alcohol withdrawal-related
• Rapidly controlled with first/second-line agents
• No underlying structural brain lesion

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

SURVIVORS:
• Functional recovery to baseline: 50-70%
• New neurological deficits: 10-30%
• Cognitive impairment (memory, attention, executive function): 20-40%
• Development of epilepsy after first SE: 20-40%
• Recurrent SE: 10-20% within 2 years
• Mortality within 10 years of SE: 30-50% (often related to underlying cause)

PEDIATRIC OUTCOMES:
• Generally better than adults
• Febrile SE: Excellent prognosis, <1% mortality
• Risk of subsequent epilepsy after febrile SE: 2-7%`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

FOR PATIENTS WITH EPILEPSY:
• AED adherence - most important preventable cause of SE
• Regular neurology follow-up
• AED level monitoring
• Avoid AED withdrawal without supervision
• Avoid seizure triggers: Sleep deprivation, alcohol, flickering lights (if photosensitive)
• Drug interaction awareness - many medications lower seizure threshold or reduce AED levels
• Rescue medication at home (rectal diazepam, intranasal/buccal midazolam) for patients with seizure clusters

FOR GENERAL POPULATION:
• Prevention of risk factors: Stroke prevention, infection prevention, injury prevention
• Avoid excessive alcohol and abrupt withdrawal
• Avoid pro-convulsant drugs in susceptible individuals
• Malaria prevention in endemic areas (bed nets, prophylaxis)
• HIV prevention and early treatment

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After SE Episode)
═══════════════════════════════════════════════════════════════════════════════

• AED optimization - ensure therapeutic levels
• Patient education on adherence
• Seizure action plan for caregivers
• Medical alert bracelet
• Rescue benzodiazepine (home diazepam rectal gel, midazolam nasal spray)
• Regular neurology follow-up
• Address underlying cause (if treatable)
• Epilepsy surgery evaluation if drug-resistant

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT
═══════════════════════════════════════════════════════════════════════════════

• MALARIA PREVENTION: ITN (insecticide-treated nets), indoor spraying, antimalarial prophylaxis in endemic areas
• CEREBRAL MALARIA: Early recognition and treatment with IV artesunate
• INFECTION PREVENTION: Vaccination (meningococcal, pneumococcal, Hib), early treatment of infections
• AED ACCESS: Ensure reliable supply of affordable AEDs; phenobarbital is WHO essential medicine
• COMMUNITY EDUCATION: Seizure first aid, when to seek care, importance of medication adherence
• HEALTHCARE WORKER TRAINING: Recognition and initial management of SE at primary care level`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING STATUS EPILEPTICUS (For Patients and Caregivers)
═══════════════════════════════════════════════════════════════════════════════

Status epilepticus is a medical emergency where a seizure lasts too long (more than 5 minutes) or seizures happen one after another without the person waking up in between. This is dangerous because the longer a seizure lasts, the harder it is to stop, and it can cause permanent brain damage.

═══════════════════════════════════════════════════════════════════════════════
FOR PATIENTS WITH EPILEPSY
═══════════════════════════════════════════════════════════════════════════════

MEDICATION ADHERENCE IS CRITICAL:
• Take your seizure medication EVERY DAY at the same time
• Never stop your medication suddenly - this can trigger status epilepticus
• If you cannot afford your medication, tell your doctor immediately
• Keep extra medication available; do not wait until you run out
• If you miss a dose, take it as soon as you remember (unless it is almost time for the next dose)

KNOW YOUR TRIGGERS:
• Lack of sleep
• Excessive alcohol
• Stress
• Flashing lights (for some people)
• Illness with fever
• Missing medications

CARRY MEDICAL IDENTIFICATION:
• Wear a medical alert bracelet or necklace
• Carry a card listing your medications and emergency contact

═══════════════════════════════════════════════════════════════════════════════
FIRST AID FOR SEIZURES (For Caregivers)
═══════════════════════════════════════════════════════════════════════════════

DURING A SEIZURE:
✓ Stay calm and note the time the seizure started
✓ Clear the area of dangerous objects
✓ Gently guide the person to the floor if standing
✓ Turn them on their side to help them breathe (recovery position)
✓ Put something soft under their head
✓ Loosen tight clothing around the neck

✗ DO NOT put anything in their mouth
✗ DO NOT hold them down or restrain them
✗ DO NOT give them anything to eat or drink

AFTER THE SEIZURE:
• Stay with them until they are fully awake
• Speak calmly and reassure them
• Help them to a safe, comfortable place to rest

═══════════════════════════════════════════════════════════════════════════════
WHEN TO CALL EMERGENCY SERVICES (CALL IMMEDIATELY)
═══════════════════════════════════════════════════════════════════════════════

🔴 The seizure lasts more than 5 MINUTES
🔴 The person does not wake up between seizures
🔴 This is their FIRST seizure ever
🔴 The person is injured during the seizure
🔴 The person has difficulty breathing
🔴 The seizure happens in water
🔴 The person is pregnant, has diabetes, or has heart disease
🔴 You are unsure whether it is a seizure

═══════════════════════════════════════════════════════════════════════════════
RESCUE MEDICATIONS
═══════════════════════════════════════════════════════════════════════════════

If your doctor has prescribed rescue medication (such as rectal diazepam or nasal/cheek midazolam):
• Keep it with you at all times
• Make sure caregivers know how to use it
• Use it if a seizure lasts more than 3-5 minutes
• STILL call emergency services after giving rescue medication

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• Do I need rescue medication at home?
• When should my caregivers use it?
• What are my specific seizure triggers?
• When can I drive again?
• Are there any activities I should avoid?
• Should I wear a medical alert bracelet?
• Who should I call if I have a seizure?`,

    references: [
      'Trinka E, et al. A definition and classification of status epilepticus - Report of the ILAE Task Force on Classification of Status Epilepticus. Epilepsia. 2015;56(10):1515-23. doi:10.1111/epi.13121',
      'Brophy GM, et al. Guidelines for the Evaluation and Management of Status Epilepticus. Neurocrit Care. 2012;17(1):3-23. doi:10.1007/s12028-012-9695-z',
      'Silbergleit R, et al. Intramuscular versus Intravenous Therapy for Prehospital Status Epilepticus (RAMPART). N Engl J Med. 2012;366(7):591-600. doi:10.1056/NEJMoa1107494',
      'Glauser T, et al. Evidence-Based Guideline: Treatment of Convulsive Status Epilepticus in Children and Adults: Report of the Guideline Committee of the American Epilepsy Society. Epilepsy Curr. 2016;16(1):48-61.',
      'Kapur J, et al. Randomized Trial of Three Anticonvulsant Medications for Status Epilepticus (ESETT). N Engl J Med. 2019;381(22):2103-2113. doi:10.1056/NEJMoa1905795',
      'Rossetti AO, Lowenstein DH. Management of refractory status epilepticus in adults: still more questions than answers. Lancet Neurol. 2011;10(10):922-30.',
      'Newton CR, Garcia HH. Epilepsy in poor regions of the world. Lancet. 2012;380(9848):1193-201. doi:10.1016/S0140-6736(12)61381-6',
      'Chin RF, et al. Treatment of community-onset, childhood convulsive status epilepticus: a prospective, population-based study. Lancet Neurol. 2008;7(8):696-703.',
      'Treiman DM, et al. A Comparison of Four Treatments for Generalized Convulsive Status Epilepticus. N Engl J Med. 1998;339(12):792-8.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 14. ACUTE PANCREATITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '14',
    name: 'Acute Pancreatitis',
    slug: 'acute-pancreatitis',
    specialty: 'Gastroenterology',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K85.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute pancreatitis is an inflammatory condition of the pancreas characterized by sudden onset of upper abdominal pain, elevated serum pancreatic enzymes (amylase and/or lipase), and characteristic imaging findings. It ranges from mild, self-limiting disease (80%) to severe necrotizing pancreatitis with multiorgan failure and mortality rates of 20-40%.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DIAGNOSIS requires 2 of 3 criteria: (1) Characteristic abdominal pain, (2) Serum lipase OR amylase ≥3x upper limit of normal, (3) Characteristic imaging findings on CT/MRI/ultrasound

• LIPASE is more sensitive and specific than amylase - lipase remains elevated longer (8-14 days vs 3-5 days for amylase)

• TWO MOST COMMON CAUSES account for 80%: Gallstones (40-70%) and Alcohol (25-35%) - mnemonic "GET SMASHED" for all causes

• EARLY AGGRESSIVE FLUID RESUSCITATION is the cornerstone of treatment: Lactated Ringer's preferred; 250-500 mL/hour initially; target urine output >0.5 mL/kg/hr

• EARLY ENTERAL NUTRITION (within 24-48 hours) improves outcomes - "if the gut works, use it"; TPN only if enteral not tolerated

• PREDICT SEVERITY EARLY: BISAP score (Bedside Index of Severity in Acute Pancreatitis) at admission; APACHE II at 24h; CRP >150 mg/L at 48h predicts severe disease

• ERCP indicated URGENTLY (<24h) only for cholangitis or persistent biliary obstruction with gallstone pancreatitis - NOT routinely for all gallstone pancreatitis

• ANTIBIOTICS NOT indicated prophylactically - even in severe/necrotizing pancreatitis; use only for documented infection (infected necrosis, cholangitis)

• CT SCAN not needed initially if diagnosis is clear - reserve for unclear diagnosis, clinical deterioration, or suspicion of complications (typically after 72-96 hours)

• CHOLECYSTECTOMY should be performed during same admission for mild gallstone pancreatitis to prevent recurrence (30% recurrence rate if delayed)

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

INITIATING EVENT:
• Premature activation of trypsinogen to trypsin within pancreatic acinar cells
• Normal: Trypsinogen activated to trypsin only in duodenum by enterokinase
• In pancreatitis: Intracellular activation triggers autodigestion cascade

MECHANISMS BY ETIOLOGY:
• GALLSTONES: Transient ampullary obstruction → Bile reflux into pancreatic duct → Acinar cell injury
• ALCOHOL: Direct toxic effect on acinar cells; Increased pancreatic secretion with sphincter spasm; Oxidative stress
• HYPERTRIGLYCERIDEMIA: Free fatty acids from lipase action on TGs cause direct acinar cell toxicity

INFLAMMATORY CASCADE:
1. Trypsin activation within acinar cells
2. Activation of other zymogens (elastase, phospholipase A2, kallikrein)
3. Autodigestion of pancreatic tissue
4. Release of pro-inflammatory cytokines (IL-1, IL-6, TNF-α)
5. Systemic inflammatory response syndrome (SIRS)
6. Third-spacing of fluid → Hypovolemia
7. Multi-organ dysfunction in severe cases

LOCAL COMPLICATIONS:
• Acute peripancreatic fluid collections (<4 weeks)
• Pancreatic pseudocyst (>4 weeks, walled-off)
• Acute necrotic collection (<4 weeks)
• Walled-off necrosis (>4 weeks)
• Infected necrosis (most feared complication)

SYSTEMIC COMPLICATIONS:
• SIRS and sepsis
• Acute respiratory distress syndrome (ARDS)
• Acute kidney injury
• Disseminated intravascular coagulation (DIC)
• Multiorgan failure`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: 13-45 per 100,000 person-years (increasing due to obesity and gallstones)
• Third most common GI cause for hospital admission in the United States
• Annual US healthcare costs exceed $2.5 billion
• Hospitalization rates have increased 20-30% over past two decades
• Male:Female ratio approximately 1:1 (alcohol more common in males, gallstones in females)
• Peak age: 30-60 years for alcohol-related; 50-70 years for gallstone-related

SEVERITY DISTRIBUTION:
• Mild (interstitial/edematous): 80% of cases; mortality <1%
• Moderately severe (transient organ failure or local complications): 10-15%; mortality 1-3%
• Severe (persistent organ failure >48 hours): 5-10%; mortality 20-40%

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• LIMITED epidemiological data from Africa, but incidence appears to be increasing with urbanization and lifestyle changes

• ETIOLOGICAL DIFFERENCES in Africa:
  - Gallstones: Becoming more common with Westernization of diet
  - Alcohol: Major cause, especially in Southern and West Africa
  - Traditional medicines and herbal preparations: Underrecognized cause
  - Infections: HIV-related (opportunistic infections, medications), Ascariasis (biliary obstruction)
  - Trauma: Higher proportion due to road traffic accidents
  - Hypertriglyceridemia: Increasing with metabolic syndrome

• HIV-RELATED PANCREATITIS is significant in sub-Saharan Africa:
  - Direct HIV effect
  - Opportunistic infections (CMV, MAC, Cryptosporidium, TB)
  - Antiretroviral medications (didanosine, stavudine - older NRTIs)
  - Pentamidine for PCP prophylaxis/treatment

• DIAGNOSTIC CHALLENGES:
  - Limited access to CT imaging for severity assessment
  - Lipase testing may not be available; amylase more widely used
  - Delayed presentation common
  - May be confused with other acute abdominal conditions

• MANAGEMENT CHALLENGES:
  - ICU beds limited for severe pancreatitis
  - ERCP availability limited to major centers
  - Interventional radiology for drainage not widely available
  - Nutritional support may be suboptimal

• OUTCOMES:
  - Higher mortality reported in some African studies (15-30%) likely due to late presentation and limited intensive care resources
  - Post-ERCP pancreatitis may be higher where less experienced endoscopists perform procedures

═══════════════════════════════════════════════════════════════════════════════
ETIOLOGY - "GET SMASHED" MNEMONIC
═══════════════════════════════════════════════════════════════════════════════

G - Gallstones (40-70%) - Most common cause worldwide
E - Ethanol/Alcohol (25-35%) - Usually requires 5+ years heavy drinking
T - Trauma (including ERCP - 3-5% post-ERCP pancreatitis)
S - Steroids
M - Mumps and other infections (CMV, Coxsackie, HIV)
A - Autoimmune pancreatitis (IgG4-related)
S - Scorpion stings (Tityus trinitatis in Trinidad)
H - Hyperlipidemia (TG >1000 mg/dL), Hypercalcemia, Hypothermia
E - ERCP (most common cause of iatrogenic pancreatitis)
D - Drugs (azathioprine, valproic acid, didanosine, sulfonamides, thiazides, estrogens, many others)

OTHER CAUSES:
• Anatomic: Pancreas divisum, annular pancreas, sphincter of Oddi dysfunction
• Genetic: PRSS1, SPINK1, CFTR mutations (recurrent pancreatitis)
• Idiopathic: 10-30% (microlithiasis often the culprit)`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

CARDINAL SYMPTOM - ABDOMINAL PAIN:
• Location: Epigastric (most common), may radiate to back (50%)
• Character: Constant, boring, severe pain
• Radiation: Classic "band-like" radiation to back
• Position: Worse supine, better leaning forward (knee-chest position)
• Onset: Usually rapid (within hours of trigger)
• Duration: Persistent, lasting days
• Severity: Often described as "worst pain of my life"

ASSOCIATED SYMPTOMS:
• Nausea and vomiting: Present in 80-90%; vomiting does NOT relieve pain
• Anorexia
• Abdominal distension
• Fever: Suggests severity or complication (infected necrosis)
• Dyspnea: May indicate pleural effusion or ARDS

SYMPTOMS SUGGESTING ETIOLOGY:
• History of gallstones or biliary colic → Gallstone pancreatitis
• Heavy alcohol use (typically >4-5 drinks/day for >5 years) → Alcoholic pancreatitis
• Recent procedure (ERCP, surgery) → Iatrogenic
• New medication → Drug-induced
• Recent fatty meal → Gallstone pancreatitis

SYMPTOMS OF SEVERE DISEASE:
• Hematemesis (rare - gastric varices, Mallory-Weiss)
• Confusion or altered mental status (hypoxia, SIRS)
• Oliguria/anuria (AKI, third-spacing)
• Respiratory distress (ARDS, pleural effusion)

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia (common - due to pain, hypovolemia, SIRS)
• Fever (suggests severe disease or infection)
• Hypotension (severe disease with third-spacing)
• Tachypnea (pain, pleural effusion, ARDS)
• Oxygen desaturation (ARDS, atelectasis)

ABDOMINAL EXAMINATION:
• Epigastric tenderness (universal finding)
• Guarding (voluntary or involuntary)
• Abdominal distension (ileus, ascites)
• Decreased or absent bowel sounds (paralytic ileus)
• Palpable mass (pseudocyst - usually late finding)

CLASSIC SIGNS OF SEVERE HEMORRHAGIC PANCREATITIS (rare, <3%):
• CULLEN SIGN: Periumbilical ecchymosis (bluish discoloration)
• GREY TURNER SIGN: Flank ecchymosis
• FOX SIGN: Ecchymosis of inguinal ligament
These indicate retroperitoneal hemorrhage and carry poor prognosis

SIGNS OF COMPLICATIONS:
• Jaundice (biliary obstruction, common bile duct compression by edema/pseudocyst)
• Pleural effusion (usually left-sided; may be bilateral)
• Subcutaneous fat necrosis (rare; tender nodules)
• Altered mental status (encephalopathy, hypoxia)

VOLUME STATUS ASSESSMENT:
• Skin turgor
• Mucous membranes
• Capillary refill
• Urine output (catheterize for accurate monitoring)`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA (Revised Atlanta Classification 2012)
═══════════════════════════════════════════════════════════════════════════════

DIAGNOSIS requires at least 2 of the following 3 criteria:

1. ABDOMINAL PAIN: Acute onset, severe, epigastric, often radiating to back

2. SERUM ENZYMES: Amylase AND/OR Lipase ≥3 times upper limit of normal
   • Lipase is PREFERRED (more sensitive and specific, stays elevated longer)
   • Amylase: Peaks at 24h, returns to normal in 3-5 days
   • Lipase: Peaks at 24-48h, returns to normal in 8-14 days
   • Magnitude of elevation does NOT correlate with severity

3. IMAGING: Characteristic findings on CT, MRI, or transabdominal ultrasound
   • Note: Imaging not required if criteria 1 and 2 are met

═══════════════════════════════════════════════════════════════════════════════
SEVERITY CLASSIFICATION (Revised Atlanta 2012)
═══════════════════════════════════════════════════════════════════════════════

MILD ACUTE PANCREATITIS:
• No organ failure
• No local or systemic complications
• Usually resolves within first week
• Mortality: <1%

MODERATELY SEVERE ACUTE PANCREATITIS:
• Transient organ failure (<48 hours) AND/OR
• Local or systemic complications without persistent organ failure
• Mortality: 1-3%

SEVERE ACUTE PANCREATITIS:
• Persistent organ failure (>48 hours)
• May be single or multiple organ failure
• Mortality: 20-40%

ORGAN FAILURE (Modified Marshall Score):
• Respiratory: PaO2/FiO2 ≤300
• Cardiovascular: SBP <90 despite fluid resuscitation OR need for vasopressors
• Renal: Creatinine ≥1.9 mg/dL (170 μmol/L)

═══════════════════════════════════════════════════════════════════════════════
SEVERITY PREDICTION SCORES
═══════════════════════════════════════════════════════════════════════════════

BISAP SCORE (Bedside Index of Severity in AP) - Use at admission:
• BUN >25 mg/dL (1 point)
• Impaired mental status (1 point)
• SIRS (≥2 criteria) (1 point)
• Age >60 years (1 point)
• Pleural effusion on imaging (1 point)
Score ≥3: Increased risk of mortality (5-10x higher)

RANSON CRITERIA (at admission and 48h):
At admission:
• Age >55 years
• WBC >16,000/mm³
• Glucose >200 mg/dL (>11 mmol/L)
• LDH >350 IU/L
• AST >250 IU/L

At 48 hours:
• Hematocrit drop >10%
• BUN increase >5 mg/dL
• Calcium <8 mg/dL (<2 mmol/L)
• PaO2 <60 mmHg
• Base deficit >4 mEq/L
• Fluid sequestration >6 L

Score interpretation: 0-2 = mild; 3-4 = moderate; ≥5 = severe

APACHE II: Score ≥8 predicts severe disease

CRP: >150 mg/L at 48 hours predicts severe pancreatitis

HEMATOCRIT: Admission Hct >44% or failure to decrease at 24h suggests severe disease (hemoconcentration from third-spacing)

═══════════════════════════════════════════════════════════════════════════════
IMAGING
═══════════════════════════════════════════════════════════════════════════════

TRANSABDOMINAL ULTRASOUND:
• FIRST-LINE imaging for ALL patients
• Primary goal: Identify gallstones as etiology
• Findings: Gallstones, dilated CBD (>6mm or >8mm post-cholecystectomy), peripancreatic fluid
• Limitations: Pancreas often obscured by bowel gas

CT SCAN WITH IV CONTRAST (Pancreatic Protocol):
• NOT needed initially if diagnosis clear
• INDICATIONS:
  - Diagnostic uncertainty
  - Clinical deterioration despite adequate treatment
  - No improvement after 48-72 hours
  - Suspicion of complications (necrosis, pseudocyst, abscess)
• TIMING: Best performed 72-96 hours after symptom onset to assess necrosis
• CT Severity Index (CTSI): Combines Balthazar grade + extent of necrosis

MRI/MRCP:
• Alternative to CT (no radiation, better soft tissue contrast)
• MRCP excellent for detecting choledocholithiasis
• Useful in pregnancy, contrast allergy, renal impairment

ENDOSCOPIC ULTRASOUND (EUS):
• Most sensitive for microlithiasis and small pancreatic tumors
• Can detect CBD stones missed by other modalities
• Available in specialized centers`,

    differentialDiagnosis: [
      'Acute cholecystitis - RUQ pain, Murphy sign positive, thickened gallbladder wall on US, normal or mildly elevated lipase',
      'Peptic ulcer disease/perforation - History of NSAIDs or H. pylori, free air on upright CXR or CT if perforated, may have GI bleeding',
      'Acute mesenteric ischemia - Atrial fibrillation, "pain out of proportion to exam," metabolic acidosis, elevated lactate',
      'Bowel obstruction - Colicky pain, distension, vomiting, obstipation, dilated loops on imaging',
      'Acute appendicitis - Periumbilical pain migrating to RLQ, McBurney point tenderness, may have elevated WBC',
      'Ruptured abdominal aortic aneurysm - Elderly patient, pulsatile mass, hypotension, back pain, requires emergent imaging',
      'Myocardial infarction (inferior) - Can present as epigastric pain; ECG changes, elevated troponin; lipase may also be mildly elevated',
      'Diabetic ketoacidosis - Can cause abdominal pain AND elevated amylase; check glucose, pH, ketones',
      'Acute hepatitis - RUQ pain, markedly elevated transaminases (>1000), jaundice, hepatomegaly',
      'Renal colic - Flank pain radiating to groin, hematuria, stone on CT',
      'Ectopic pregnancy - Reproductive-age female, positive pregnancy test, pelvic pain, adnexal mass',
      'Splenic rupture/infarction - LUQ pain, history of trauma or hematologic disorder, splenomegaly'
    ],

    investigations: [
      {
        name: 'Serum Lipase',
        indication: '🔴 DIAGNOSTIC - Preferred enzyme; ≥3x ULN confirms diagnosis; more specific than amylase',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Amylase',
        indication: '🔴 DIAGNOSTIC - ≥3x ULN supports diagnosis; less specific (also elevated in salivary conditions, renal failure, bowel obstruction)',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'WBC elevation suggests inflammation/infection; Hct >44% indicates hemoconcentration and severity',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (BUN, Creatinine, Electrolytes, Glucose)',
        indication: '🔴 BUN >25 is BISAP criterion; assess renal function (AKI common); glucose elevation (Ranson criterion); calcium monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (AST, ALT, Bilirubin, ALP)',
        indication: '🔴 ALT >150 U/L highly suggestive of gallstone etiology; elevated bilirubin/ALP suggests biliary obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Calcium (Serum)',
        indication: 'Hypocalcemia <8 mg/dL is Ranson criterion and marker of severity (calcium sequestered in fat saponification)',
        availability: 'BASIC'
      },
      {
        name: 'Lactate Dehydrogenase (LDH)',
        indication: 'LDH >350 U/L is Ranson criterion; marker of tissue damage',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🔴 CRP >150 mg/L at 48 hours predicts severe pancreatitis; useful for monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Triglycerides',
        indication: '🔴 Check in ALL patients; TG >1000 mg/dL indicates hypertriglyceridemia as cause',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Severe disease; assess oxygenation (PaO2 <60 is Ranson criterion) and base deficit',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lactate',
        indication: 'Elevated lactate suggests tissue hypoperfusion; monitor for shock and complications',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 FIRST-LINE IMAGING - Primary goal is to identify gallstones; assess CBD dilation; may show peripancreatic fluid',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'Assess for pleural effusion (BISAP criterion, usually left-sided), ARDS, rule out perforation (free air)',
        availability: 'BASIC'
      },
      {
        name: 'CT Abdomen with IV Contrast (Pancreatic Protocol)',
        indication: 'NOT routine; indicated for: diagnostic uncertainty, no improvement at 48-72h, suspicion of complications; BEST at 72-96 hours to assess necrosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRCP (MR Cholangiopancreatography)',
        indication: 'Suspected CBD stone not seen on US; alternative to diagnostic ERCP; preferred in pregnancy',
        availability: 'ADVANCED'
      },
      {
        name: 'Endoscopic Ultrasound (EUS)',
        indication: 'Suspected microlithiasis; idiopathic recurrent pancreatitis; pancreatic mass evaluation',
        availability: 'ADVANCED'
      },
      {
        name: 'Procalcitonin',
        indication: 'May help differentiate infected necrosis from sterile inflammation; elevated >3.8 ng/mL suggests infection',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ NPO (Nil per os): Bowel rest initially; reduces pancreatic stimulation',
        '⚡ AGGRESSIVE IV FLUID RESUSCITATION: Lactated Ringer preferred; 250-500 mL/hour for first 12-24 hours; goal-directed (target UOP >0.5 mL/kg/hr, HR <120, MAP >65)',
        '⚡ ANALGESIA: Opioids are appropriate - Morphine, hydromorphone, or fentanyl IV; no evidence that morphine worsens pancreatitis by sphincter spasm',
        '⚡ ANTIEMETICS: Ondansetron 4-8 mg IV q6-8h PRN for nausea/vomiting',
        '⚡ MONITOR CLOSELY: Vital signs q1-4h; urine output (Foley catheter); serial labs (BMP, CBC, LFTs) every 12-24h',
        '⚡ ASSESS SEVERITY: Calculate BISAP at admission; Ranson criteria at admission and 48h; CRP at 48h',
        '⚡ DETERMINE ETIOLOGY: Ultrasound for gallstones; history for alcohol; check triglycerides in all patients',
        '⚡ ICU ADMISSION: If organ failure, hemodynamic instability, severe disease markers, or need for close monitoring',
        '⚡ DVT PROPHYLAXIS: Low-molecular-weight heparin or unfractionated heparin unless contraindicated',
        '⚡ EARLY ENTERAL NUTRITION: Plan for feeding within 24-48 hours once nausea resolves and pain improves'
      ],
      pharmacological: [
        {
          drug: 'Lactated Ringer Solution',
          indication: '🔴 CORNERSTONE OF TREATMENT - Aggressive fluid resuscitation; LR preferred over NS (may reduce SIRS)',
          dose: '250-500 mL/hour for first 12-24 hours; then adjust to maintain UOP >0.5 mL/kg/hr; may require 3-5+ liters in first 24 hours',
          route: 'IV',
          duration: 'Aggressive resuscitation for 24-48 hours; then maintenance based on clinical response',
          contraindications: ['Caution in heart failure (risk of volume overload)', 'Hyperkalemia (LR contains potassium)'],
          sideEffects: ['Volume overload', 'Pulmonary edema if over-resuscitated', 'Dilutional coagulopathy'],
          alternatives: ['Normal saline (if LR unavailable, though LR preferred)']
        },
        {
          drug: 'Morphine',
          indication: 'Severe pain control - opioids are first-line for acute pancreatitis pain',
          dose: '2-4 mg IV every 2-4 hours PRN; or PCA (Patient-Controlled Analgesia)',
          route: 'IV',
          duration: 'Until pain controlled; transition to oral opioids as tolerating diet',
          contraindications: ['Respiratory depression', 'Severe hypotension', 'Paralytic ileus (use with caution)'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Constipation', 'Nausea', 'Pruritus'],
          alternatives: ['Hydromorphone 0.5-1 mg IV q2-4h', 'Fentanyl 25-50 mcg IV q1-2h']
        },
        {
          drug: 'Hydromorphone (Dilaudid)',
          indication: 'Alternative opioid for severe pain; useful if morphine intolerance',
          dose: '0.5-1 mg IV every 2-4 hours PRN; adjust for renal function',
          route: 'IV',
          duration: 'Until pain controlled',
          contraindications: ['Same as morphine'],
          sideEffects: ['Same as morphine; may have less histamine release'],
          alternatives: ['Morphine', 'Fentanyl']
        },
        {
          drug: 'Ondansetron (Zofran)',
          indication: 'Antiemetic for nausea and vomiting',
          dose: '4-8 mg IV every 6-8 hours PRN',
          route: 'IV or oral',
          duration: 'Until nausea resolves and tolerating oral intake',
          contraindications: ['Known hypersensitivity', 'Congenital long QT syndrome'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation (rare at usual doses)'],
          alternatives: ['Metoclopramide 10 mg IV q6-8h', 'Prochlorperazine 5-10 mg IV q6h']
        },
        {
          drug: 'Pantoprazole (or other PPI)',
          indication: 'Stress ulcer prophylaxis in severe pancreatitis; symptomatic relief',
          dose: '40 mg IV daily; or 40 mg PO daily when tolerating oral',
          route: 'IV initially, then oral',
          duration: 'Throughout hospitalization; may discontinue on discharge if no ongoing indication',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Generally well tolerated; rare: hypomagnesemia, C. diff risk with prolonged use'],
          alternatives: ['Omeprazole', 'Esomeprazole', 'H2 blockers (ranitidine, famotidine)']
        },
        {
          drug: 'Enoxaparin (Lovenox) - DVT Prophylaxis',
          indication: 'VTE prophylaxis in hospitalized patients with acute pancreatitis',
          dose: '40 mg SC once daily; reduce to 30 mg daily if CrCl <30 mL/min',
          route: 'Subcutaneous',
          duration: 'Throughout hospitalization until fully ambulatory',
          contraindications: ['Active bleeding', 'Severe thrombocytopenia', 'Heparin-induced thrombocytopenia'],
          sideEffects: ['Bleeding', 'Injection site reactions', 'Thrombocytopenia'],
          alternatives: ['Unfractionated heparin 5000 units SC q8-12h', 'Mechanical prophylaxis if anticoagulation contraindicated']
        },
        {
          drug: 'Imipenem-Cilastatin',
          indication: '🔴 ONLY for infected pancreatic necrosis (NOT prophylactic) - good pancreatic tissue penetration',
          dose: '500 mg IV every 6 hours; adjust for renal function',
          route: 'IV',
          duration: '2-4 weeks for infected necrosis; based on clinical response and source control',
          contraindications: ['Hypersensitivity to carbapenems or penicillins (cross-reactivity rare)', 'Seizure disorder (relative)'],
          sideEffects: ['Seizures (rare)', 'Nausea', 'Diarrhea', 'C. difficile infection'],
          alternatives: ['Meropenem 1g IV q8h', 'Fluoroquinolone + metronidazole']
        },
        {
          drug: 'Meropenem',
          indication: '🔴 ONLY for infected pancreatic necrosis - alternative to imipenem with lower seizure risk',
          dose: '1 g IV every 8 hours; adjust for renal function',
          route: 'IV',
          duration: '2-4 weeks for infected necrosis',
          contraindications: ['Carbapenem hypersensitivity'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache', 'Rash', 'C. difficile infection'],
          alternatives: ['Imipenem-cilastatin', 'Ciprofloxacin + metronidazole']
        },
        {
          drug: 'Insulin IV Infusion',
          indication: '🔴 Hypertriglyceridemia-induced pancreatitis with TG >1000 mg/dL - activates lipoprotein lipase',
          dose: 'Regular insulin 0.1-0.3 units/kg/hour IV with D5W; maintain glucose 150-200 mg/dL',
          route: 'IV continuous infusion',
          duration: 'Until triglycerides <500 mg/dL; may take 2-4 days',
          contraindications: ['Active hypoglycemia (monitor closely)'],
          sideEffects: ['Hypoglycemia (monitor glucose hourly)', 'Hypokalemia'],
          alternatives: ['Plasmapheresis (if available and severe)']
        },
        {
          drug: 'Gemfibrozil/Fenofibrate',
          indication: 'After acute phase of hypertriglyceridemia-induced pancreatitis to prevent recurrence',
          dose: 'Gemfibrozil 600 mg PO BID or Fenofibrate 145 mg PO daily',
          route: 'Oral',
          duration: 'Long-term for secondary prevention',
          contraindications: ['Severe renal impairment', 'Hepatic disease', 'Gallbladder disease'],
          sideEffects: ['Myopathy (especially with statins)', 'GI upset', 'Cholelithiasis'],
          alternatives: ['Omega-3 fatty acids (high dose)', 'Niacin']
        }
      ],
      nonPharmacological: [
        'EARLY ENTERAL NUTRITION: Initiate within 24-48 hours if tolerated; reduces infectious complications, mortality, and length of stay; nasogastric or nasojejunal feeding equally effective; start with low rate and advance as tolerated',
        'ORAL FEEDING: Start clear liquids when pain improving, nausea resolved; advance to low-fat solid diet as tolerated; do NOT wait for enzyme normalization',
        'TPN (Total Parenteral Nutrition): Reserved for patients unable to tolerate enteral nutrition after multiple attempts; associated with higher complication rates than EN',
        'ERCP: Indicated URGENTLY (<24h) for cholangitis or persistent biliary obstruction in gallstone pancreatitis; NOT indicated routinely for predicted mild gallstone pancreatitis without cholangitis',
        'RESPIRATORY SUPPORT: Supplemental oxygen to maintain SpO2 >92%; mechanical ventilation if ARDS develops',
        'ICU CARE: For severe pancreatitis with organ failure; close hemodynamic monitoring; may require vasopressors',
        'ALCOHOL CESSATION: Counseling and support for alcohol-related pancreatitis; reduces recurrence risk',
        'SMOKING CESSATION: Smoking associated with chronic pancreatitis progression; counsel all patients'
      ],
      surgical: [
        'CHOLECYSTECTOMY: Indicated for ALL gallstone pancreatitis; perform during SAME admission for mild pancreatitis (prevents 30% recurrence); delay 6 weeks for severe/necrotizing pancreatitis',
        'PERCUTANEOUS DRAINAGE: For infected walled-off necrosis or pseudocyst causing symptoms; CT or US-guided; step-up approach preferred over surgery',
        'ENDOSCOPIC NECROSECTOMY: Minimally invasive debridement through transgastric or transduodenal approach; preferred over open surgery when possible',
        'VIDEO-ASSISTED RETROPERITONEAL DEBRIDEMENT (VARD): Minimally invasive surgical approach for infected necrosis',
        'OPEN NECROSECTOMY: Reserved for failed minimally invasive approaches; high morbidity; typically delayed until 4+ weeks when necrosis has demarcated',
        'PSEUDOCYST DRAINAGE: For symptomatic pseudocysts; endoscopic (transgastric or transpapillary), percutaneous, or surgical cystogastrostomy'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: Persistent organ failure (>48h); hemodynamic instability requiring vasopressors; respiratory failure; BISAP ≥3 or Ranson ≥3',
        '🔴 URGENT GI/ERCP: Cholangitis (fever, jaundice, RUQ pain with biliary obstruction); persistent CBD stone with biliary dilatation',
        '🔴 SURGERY CONSULTATION: Gallstone pancreatitis (for cholecystectomy planning); suspected infected necrosis; abdominal compartment syndrome',
        '🟠 INTERVENTIONAL RADIOLOGY: Percutaneous drainage of infected collections when endoscopic approach not feasible',
        '🟠 NUTRITION/DIETITIAN: All patients for enteral nutrition optimization and discharge diet counseling',
        '🟠 ADDICTION MEDICINE: Alcohol-related pancreatitis for counseling and treatment programs',
        '🟠 HEPATOLOGY/TRANSPLANT: End-stage pancreatic insufficiency; recurrent severe pancreatitis'
      ],
      monitoring: [
        'VITAL SIGNS: Every 1-4 hours depending on severity; watch for tachycardia (hypovolemia), hypotension (severe disease), fever (infection)',
        'URINE OUTPUT: Foley catheter for accurate monitoring; target >0.5 mL/kg/hour; indicates adequacy of resuscitation',
        'FLUID BALANCE: Strict intake/output; daily weights; avoid under- and over-resuscitation',
        'LABORATORY MONITORING: BMP every 12-24h initially (watch for AKI, electrolyte derangements); CBC daily; LFTs if biliary etiology; CRP at 48h',
        'HEMATOCRIT: Failure of Hct to decrease suggests ongoing third-spacing and need for more aggressive fluids',
        'OXYGENATION: Continuous pulse oximetry; ABG if hypoxic; CXR for pleural effusion and ARDS',
        'CLINICAL ASSESSMENT: Serial abdominal exams; watch for increasing tenderness, distension (ileus, compartment syndrome)',
        'PAIN ASSESSMENT: Regular pain scoring; adequate analgesia improves outcomes',
        'NUTRITION TOLERANCE: Monitor for feeding intolerance (abdominal distension, vomiting, pain with feeds)'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
INPATIENT MILESTONES FOR DISCHARGE
═══════════════════════════════════════════════════════════════════════════════

• Pain controlled with oral analgesics
• Tolerating low-fat oral diet
• No evidence of complications (infected necrosis, pseudocyst)
• Electrolytes stable off IV fluids
• For gallstone pancreatitis: Cholecystectomy completed OR scheduled within 2 weeks

═══════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

• OUTPATIENT VISIT: Within 2-4 weeks with gastroenterology or PCP
• REPEAT IMAGING: If complications suspected; for severe pancreatitis, CT at 4-6 weeks to assess for pseudocyst/necrosis resolution
• CHOLECYSTECTOMY: If not performed during admission, schedule within 2-6 weeks for mild pancreatitis

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM FOLLOW-UP AND PREVENTION
═══════════════════════════════════════════════════════════════════════════════

ALCOHOL-RELATED:
• Absolute alcohol cessation
• Alcohol use disorder treatment referral
• Recurrence rate with continued drinking: 40-50%

GALLSTONE-RELATED:
• Cholecystectomy (definitive prevention)
• Ursodeoxycholic acid if surgery contraindicated

HYPERTRIGLYCERIDEMIA-RELATED:
• Fibrate therapy long-term
• Dietary modification (very low fat diet)
• Diabetes control
• Target TG <500 mg/dL

IDIOPATHIC/RECURRENT:
• EUS to evaluate for microlithiasis
• Consider genetic testing (PRSS1, SPINK1, CFTR)
• Sphincter of Oddi manometry in selected cases

MONITORING FOR CHRONIC PANCREATITIS:
• Screen for exocrine insufficiency (fecal elastase)
• Screen for endocrine insufficiency (glucose, HbA1c)
• Pancreatic enzyme replacement if indicated
• Nutritional support with fat-soluble vitamins (A, D, E, K)`
    },

    redFlags: [
      '🔴 CRITICAL: Persistent organ failure >48 hours - defines severe pancreatitis; mortality 20-40%',
      '🔴 CRITICAL: Hemodynamic instability despite aggressive fluid resuscitation - indicates severe disease, may need vasopressors',
      '🔴 CRITICAL: Respiratory failure (PaO2 <60, SpO2 <90%) - ARDS; may require intubation',
      '🔴 CRITICAL: Acute kidney injury (creatinine >1.9 mg/dL, oliguria) - organ failure criterion',
      '🔴 CRITICAL: Fever with increasing WBC after initial improvement - suspect infected necrosis',
      '🔴 CRITICAL: Abdominal compartment syndrome - distension, respiratory compromise, oliguria, bladder pressure >20 mmHg',
      '🔴 CRITICAL: Cullen or Grey Turner sign - retroperitoneal hemorrhage; severe necrotizing pancreatitis',
      '🟠 URGENT: BISAP score ≥3 or Ranson score ≥3 - predicts severe disease',
      '🟠 URGENT: Hematocrit >44% or failure to decrease at 24h - suggests hemoconcentration and inadequate resuscitation',
      '🟠 URGENT: CRP >150 mg/L at 48 hours - predicts severe pancreatitis',
      '🟠 URGENT: Cholangitis (Charcot triad: fever, jaundice, RUQ pain) - requires urgent ERCP',
      '🟠 URGENT: Hypocalcemia <7.5 mg/dL - marker of severity (calcium sequestration)',
      '🟠 URGENT: Altered mental status - may indicate severe SIRS, hypoxia, or encephalopathy'
    ],

    complications: [
      'ACUTE PERIPANCREATIC FLUID COLLECTION: <4 weeks, no defined wall; usually resolves spontaneously; seen in interstitial pancreatitis',
      'PANCREATIC PSEUDOCYST: >4 weeks, well-defined wall; may cause pain, obstruction, or infection; drain if symptomatic or >6 cm',
      'ACUTE NECROTIC COLLECTION: <4 weeks, necrosis without wall; in necrotizing pancreatitis; may become infected',
      'WALLED-OFF NECROSIS (WON): >4 weeks, necrosis with defined wall; requires drainage if infected or symptomatic',
      'INFECTED PANCREATIC NECROSIS: Most feared complication; 20-40% mortality; presents with fever, sepsis after initial improvement; requires antibiotics and drainage',
      'SPLENIC VEIN THROMBOSIS: Can cause left-sided portal hypertension and gastric varices; watch for GI bleeding',
      'PSEUDOANEURYSM: Splenic, gastroduodenal, or pancreaticoduodenal arteries; can cause life-threatening hemorrhage; requires angioembolization',
      'ARDS (Acute Respiratory Distress Syndrome): Due to systemic inflammation; requires mechanical ventilation',
      'ACUTE KIDNEY INJURY: From hypovolemia, third-spacing, SIRS; may require renal replacement therapy',
      'PANCREATIC DUCT DISRUPTION: Can cause ascites or pleural effusion; diagnosed by amylase in fluid',
      'GASTRIC OUTLET/DUODENAL OBSTRUCTION: From edema, pseudocyst, or WON compression',
      'ABDOMINAL COMPARTMENT SYNDROME: Massive fluid resuscitation and inflammation; may require decompressive laparotomy',
      'CHRONIC PANCREATITIS: After recurrent acute episodes; exocrine and endocrine insufficiency',
      'DIABETES MELLITUS: Endocrine insufficiency from pancreatic damage; may be transient or permanent',
      'EXOCRINE PANCREATIC INSUFFICIENCY: Fat malabsorption, steatorrhea; requires enzyme replacement'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY BY SEVERITY
═══════════════════════════════════════════════════════════════════════════════

• Mild acute pancreatitis: <1% mortality; most recover completely within 1 week
• Moderately severe: 1-3% mortality; may have prolonged course
• Severe (persistent organ failure): 20-40% mortality
• Infected pancreatic necrosis: 30-50% mortality (highest risk)
• Sterile necrosis: 10-15% mortality

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Older age (>55 years)
• Obesity (BMI >30) - increases local and systemic complications
• Organ failure (especially if persistent >48h)
• Pancreatic necrosis >50% of gland
• Infected necrosis
• Need for ICU admission
• High APACHE II score (≥8)
• BISAP score ≥3
• Ranson score ≥3
• CRP >150 mg/L at 48h
• Hemoconcentration (Hct >44%)
• Comorbidities (CKD, heart failure, COPD)

FAVORABLE PROGNOSIS:
• Young age
• Mild/interstitial pancreatitis (no necrosis)
• Early resolution of SIRS
• Quick response to fluid resuscitation
• Gallstone etiology (recurrence preventable with cholecystectomy)
• No organ failure

═══════════════════════════════════════════════════════════════════════════════
RECURRENCE
═══════════════════════════════════════════════════════════════════════════════

• Gallstone pancreatitis WITHOUT cholecystectomy: 30% recurrence within 3-6 months
• Gallstone pancreatitis WITH cholecystectomy: <5% recurrence
• Alcoholic pancreatitis with continued drinking: 40-50% recurrence
• Idiopathic pancreatitis: 20-30% recurrence (consider EUS for microlithiasis)

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

• Full recovery: Majority with mild pancreatitis recover without sequelae
• Chronic pancreatitis: Risk increases with recurrent episodes, especially alcoholic
• Diabetes: 20-30% after severe necrotizing pancreatitis
• Exocrine insufficiency: 20-30% after severe disease
• Quality of life: Generally good after mild episode; may be reduced after severe pancreatitis`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

LIFESTYLE MODIFICATIONS:
• Alcohol moderation or abstinence (most important for alcohol-related)
• Smoking cessation (smoking increases progression risk)
• Maintain healthy weight (obesity is risk factor)
• Low-fat diet if hyperlipidemia present
• Regular physical activity

MEDICAL INTERVENTIONS:
• Treat hypertriglyceridemia: Fibrates, statins, omega-3; target TG <500 mg/dL
• Treat hypercalcemia
• Avoid medications associated with pancreatitis when possible
• Careful ERCP patient selection; prophylactic rectal indomethacin reduces post-ERCP pancreatitis

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After First Episode)
═══════════════════════════════════════════════════════════════════════════════

GALLSTONE PANCREATITIS:
• Cholecystectomy during same admission (mild) or within 6 weeks (severe)
• Reduces recurrence from 30% to <5%
• ERCP with sphincterotomy if not surgical candidate

ALCOHOLIC PANCREATITIS:
• Absolute alcohol cessation is essential
• Addiction medicine referral
• Psychological support and counseling
• Recurrence rate drops significantly with abstinence

HYPERTRIGLYCERIDEMIC PANCREATITIS:
• Strict low-fat diet (<20 g fat/day during acute phase)
• Fibrate therapy long-term
• Treat underlying causes (diabetes control, medication review)
• Target TG <500 mg/dL (ideally <200 mg/dL)

IDIOPATHIC PANCREATITIS:
• EUS to evaluate for microlithiasis (empiric cholecystectomy may help)
• Consider sphincter of Oddi dysfunction workup
• Genetic testing for hereditary pancreatitis

═══════════════════════════════════════════════════════════════════════════════
POST-ERCP PANCREATITIS PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• Rectal indomethacin (100 mg) immediately before or after ERCP - standard of care
• Prophylactic pancreatic duct stent in high-risk patients
• Careful patient selection and experienced endoscopist
• Aggressive IV hydration peri-procedure`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING YOUR CONDITION (Plain Language)
═══════════════════════════════════════════════════════════════════════════════

You have acute pancreatitis, which is inflammation of the pancreas. The pancreas is an organ behind your stomach that makes digestive enzymes and hormones like insulin. When the pancreas becomes inflamed, the digestive enzymes can start digesting the pancreas itself, causing severe pain and potentially serious complications.

WHAT CAUSED THIS:
The most common causes are:
• Gallstones (small stones from the gallbladder that block the pancreatic duct)
• Alcohol (heavy drinking over years can irritate the pancreas)
• High triglycerides (a type of fat in your blood)

═══════════════════════════════════════════════════════════════════════════════
DURING YOUR HOSPITAL STAY
═══════════════════════════════════════════════════════════════════════════════

WHAT TO EXPECT:
• You will receive IV fluids - lots of them. This is very important for healing.
• You may not be able to eat for a day or two to let your pancreas rest.
• You will receive pain medication. Let your nurse know if pain is not controlled.
• We will monitor you closely with blood tests and possibly imaging.
• Most people with mild pancreatitis recover within 3-7 days.

WHEN YOU CAN EAT AGAIN:
• You will start with clear liquids when your pain improves and nausea is gone
• Then advance to a LOW-FAT diet as tolerated
• Eating does NOT need to wait for your blood tests to normalize

═══════════════════════════════════════════════════════════════════════════════
AFTER DISCHARGE - IMPORTANT INSTRUCTIONS
═══════════════════════════════════════════════════════════════════════════════

DIET:
• Follow a LOW-FAT diet for at least 2-4 weeks
• Avoid fried foods, fatty meats, whole milk, cheese, butter, cream
• Choose lean proteins (chicken, fish), vegetables, fruits, whole grains
• Eat smaller, more frequent meals

IF YOUR PANCREATITIS WAS CAUSED BY GALLSTONES:
• You will need surgery to remove your gallbladder (cholecystectomy)
• This prevents the pancreatitis from coming back (30% chance of recurrence without surgery)
• This may be done during this hospital stay or scheduled within a few weeks

IF YOUR PANCREATITIS WAS CAUSED BY ALCOHOL:
• You MUST stop drinking alcohol completely
• Even one drink can cause another attack
• Ask your doctor about programs to help you stop drinking
• Your pancreas cannot heal if you continue to drink

IF YOUR PANCREATITIS WAS CAUSED BY HIGH TRIGLYCERIDES:
• Take your prescribed medications (fibrates) regularly
• Follow a very low-fat diet
• Control your blood sugar if you have diabetes
• Avoid alcohol, which raises triglycerides

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - WHEN TO SEEK IMMEDIATE CARE
═══════════════════════════════════════════════════════════════════════════════

Return to the emergency room immediately if you experience:

🔴 Severe abdominal pain returning or worsening
🔴 Fever or chills
🔴 Vomiting and unable to keep fluids down
🔴 Yellowing of skin or eyes (jaundice)
🔴 Dark urine or light-colored stools
🔴 Difficulty breathing
🔴 Confusion or lightheadedness
🔴 Blood in vomit or stool

═══════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
═══════════════════════════════════════════════════════════════════════════════

• Keep your follow-up appointment (usually 2-4 weeks after discharge)
• Bring a list of your medications
• Keep a food diary to help identify triggers
• Get your scheduled cholecystectomy if recommended

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What caused my pancreatitis?
• Do I need surgery to remove my gallbladder?
• When can I eat normally again?
• Do I need to take any long-term medications?
• How can I prevent this from happening again?
• When should I schedule my follow-up appointment?`,

    references: [
      'Banks PA, et al. Classification of acute pancreatitis--2012: revision of the Atlanta classification and definitions by international consensus. Gut. 2013;62(1):102-11. doi:10.1136/gutjnl-2012-302779',
      'Crockett SD, et al. American Gastroenterological Association Institute Guideline on Initial Management of Acute Pancreatitis. Gastroenterology. 2018;154(4):1096-1101. doi:10.1053/j.gastro.2018.01.032',
      'Tenner S, et al. American College of Gastroenterology Guideline: Management of Acute Pancreatitis. Am J Gastroenterol. 2013;108(9):1400-15. doi:10.1038/ajg.2013.218',
      'Working Group IAP/APA Acute Pancreatitis Guidelines. IAP/APA evidence-based guidelines for the management of acute pancreatitis. Pancreatology. 2013;13(4 Suppl 2):e1-15.',
      'Forsmark CE, et al. Acute Pancreatitis. N Engl J Med. 2016;375(20):1972-1981. doi:10.1056/NEJMra1505202',
      'van Santvoort HC, et al. A Step-up Approach or Open Necrosectomy for Necrotizing Pancreatitis (PANTER trial). N Engl J Med. 2010;362(16):1491-502.',
      'da Costa DW, et al. Same-admission versus interval cholecystectomy for mild gallstone pancreatitis (PONCHO): a multicentre randomised controlled trial. Lancet. 2015;386(10000):1261-8.',
      'Elmunzer BJ, et al. A randomized trial of rectal indomethacin to prevent post-ERCP pancreatitis. N Engl J Med. 2012;366(15):1414-22.',
      'Petrov MS, et al. Systematic review and meta-analysis of enteral nutrition formulations in acute pancreatitis. Br J Surg. 2009;96(11):1243-52.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 15. PULMONARY EMBOLISM
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '15',
    name: 'Pulmonary Embolism',
    slug: 'pulmonary-embolism',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'I26.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Pulmonary embolism (PE) is an acute, potentially life-threatening condition caused by obstruction of the pulmonary arterial system by thrombus, most commonly originating from deep veins of the lower extremities (deep vein thrombosis, DVT). PE and DVT are collectively termed venous thromboembolism (VTE). PE ranges from small subsegmental emboli causing minimal symptoms to massive saddle emboli causing sudden death.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• CLINICAL PROBABILITY ASSESSMENT is essential before testing - Use Wells Score or Geneva Score to guide workup and interpretation of results

• D-DIMER has HIGH NEGATIVE PREDICTIVE VALUE in LOW probability patients - Normal D-dimer (<500 ng/mL or age-adjusted) EXCLUDES PE in low/intermediate probability patients; do NOT order D-dimer if high probability (go straight to imaging)

• CT PULMONARY ANGIOGRAPHY (CTPA) is the gold standard diagnostic test - Confirmatory in high probability patients and those with elevated D-dimer

• RISK STRATIFY ALL CONFIRMED PE using sPESI, PESI, or hemodynamic status:
  - Low risk: Outpatient treatment possible
  - Intermediate risk: Hospitalize, consider escalation
  - High risk (massive PE): ICU, consider thrombolysis

• ANTICOAGULATION is the cornerstone of treatment - Start immediately when PE confirmed or highly suspected (do not wait for imaging if high probability)

• DIRECT ORAL ANTICOAGULANTS (DOACs) are first-line for most patients - Rivaroxaban, Apixaban, Edoxaban, or Dabigatran; avoid in severe renal impairment, cancer-associated VTE (LMWH preferred), pregnancy

• THROMBOLYSIS indicated for MASSIVE PE (hemodynamic instability) - Alteplase 100 mg IV over 2 hours; consider for submassive PE with RV dysfunction and clinical deterioration

• DURATION OF ANTICOAGULATION:
  - Provoked PE (surgery, trauma, immobility): 3 months
  - Unprovoked PE: At least 3 months, consider indefinite
  - Cancer-associated: Indefinite while cancer active

• V/Q SCAN is alternative to CTPA when CTPA contraindicated (contrast allergy, severe renal impairment, pregnancy)

• MASSIVE PE = Hemodynamic instability (SBP <90 mmHg, requiring vasopressors, or cardiac arrest) - Mortality >50% without treatment

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

VIRCHOW'S TRIAD - Risk factors for thrombosis:
1. VENOUS STASIS: Immobility, paralysis, prolonged travel, heart failure
2. ENDOTHELIAL INJURY: Surgery, trauma, central venous catheters, previous VTE
3. HYPERCOAGULABILITY: Malignancy, pregnancy, oral contraceptives, thrombophilia

PATHOPHYSIOLOGIC CASCADE:
1. Thrombus (usually from DVT) embolizes to pulmonary circulation
2. Mechanical obstruction of pulmonary arteries
3. Increased pulmonary vascular resistance (PVR)
4. Right ventricular (RV) pressure overload
5. RV dilation and dysfunction
6. Decreased LV preload (interventricular septal shift)
7. Decreased cardiac output and systemic hypotension
8. In massive PE: Obstructive shock and death

ADDITIONAL MECHANISMS:
• Hypoxemia from V/Q mismatch (perfusion to non-ventilated areas)
• Release of vasoactive mediators (serotonin, thromboxane) causing vasoconstriction
• Reflex bronchoconstriction
• Pulmonary infarction (uncommon, 10% - usually peripheral emboli)

RIGHT VENTRICULAR FAILURE:
• RV is thin-walled, designed for low-pressure system
• Acute pressure overload leads to RV dilation
• RV dilation shifts septum leftward (D-sign on echo)
• Decreased LV filling → decreased cardiac output
• This is the primary cause of death in massive PE`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: 60-70 per 100,000 person-years (likely underestimated)
• Third most common cardiovascular cause of death (after MI and stroke)
• 10% of all hospital deaths may be related to PE (often undiagnosed)
• VTE affects approximately 1-2 per 1000 adults annually
• 30-day mortality: 10-30% depending on severity
• Recurrence rate: 30% over 10 years without anticoagulation

MORTALITY BY CATEGORY:
• Low-risk PE: <1% 30-day mortality
• Intermediate-risk (submassive): 3-15% mortality
• High-risk (massive): 25-65% mortality
• Cardiac arrest from PE: >90% mortality

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HISTORICALLY UNDERDIAGNOSED in Africa due to limited diagnostic resources (CTPA, V/Q scans)
• Autopsy studies suggest PE is more common than clinically diagnosed
• Increasing recognition with improved imaging availability in urban centers

UNIQUE RISK FACTORS in Africa:
• HIV/AIDS: Hypercoagulable state; increased VTE risk 2-10x; ART may also contribute
• Tuberculosis: Both the disease and prolonged immobility during treatment increase risk
• Peripartum cardiomyopathy: Heart failure increases VTE risk
• Sickle cell disease: Hypercoagulable state, especially during crisis
• Traditional bone-setting practices: Prolonged immobilization
• Post-surgical: May have longer immobility periods due to healthcare resource constraints
• Limited thromboprophylaxis use in hospitalized patients

DIAGNOSTIC CHALLENGES:
• Limited access to CTPA - may rely more on clinical assessment and D-dimer
• V/Q scans rarely available outside major centers
• Echocardiography more available than CT in some settings
• May present late due to healthcare access issues

TREATMENT CHALLENGES:
• DOAC availability and cost - may rely on warfarin or LMWH
• INR monitoring challenging in rural areas
• Limited access to thrombolysis for massive PE
• ICU capacity for high-risk patients
• IVC filters rarely available

DIFFERENTIAL CONSIDERATIONS:
• TB pericarditis/pleuritis may mimic PE
• Pneumonia (including PCP in HIV) can have similar presentation
• Peripartum cardiomyopathy in postpartum dyspnea

═══════════════════════════════════════════════════════════════════════════════
RISK FACTORS
═══════════════════════════════════════════════════════════════════════════════

STRONG RISK FACTORS (Odds Ratio >10):
• Hip or leg fracture
• Hip or knee replacement surgery
• Major general surgery
• Major trauma
• Spinal cord injury
• Prior VTE

MODERATE RISK FACTORS (OR 2-9):
• Arthroscopic knee surgery
• Central venous catheters
• Cancer/chemotherapy
• Heart failure
• Hormone replacement therapy / Oral contraceptives
• Pregnancy / Postpartum
• Stroke with paralysis
• Prolonged immobility (bed rest >3 days)
• Thrombophilia (Factor V Leiden, Prothrombin mutation, Protein C/S deficiency, Antithrombin deficiency, Antiphospholipid syndrome)

WEAK RISK FACTORS (OR <2):
• Bed rest >3 days
• Long-distance travel (>8 hours)
• Age >40 years
• Obesity
• Varicose veins
• Laparoscopic surgery`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

CLASSIC TRIAD (present in minority of patients):
• Dyspnea (sudden onset)
• Pleuritic chest pain
• Hemoptysis

COMMON SYMPTOMS:
• DYSPNEA (73%): Most common symptom; often sudden onset; may be only symptom
• PLEURITIC CHEST PAIN (66%): Sharp, worse with inspiration; suggests peripheral PE with pleural involvement
• COUGH (37%): Usually dry; may be productive
• HEMOPTYSIS (13%): Suggests pulmonary infarction; usually scant
• LEG PAIN/SWELLING (44%): Concurrent DVT symptoms

LESS COMMON SYMPTOMS:
• Substernal chest pain (may mimic ACS)
• Palpitations
• Anxiety, sense of impending doom
• Syncope or presyncope (suggests massive PE)
• Fever (low-grade)

MASSIVE PE SYMPTOMS:
• Severe dyspnea
• Syncope
• Altered mental status
• Chest pain
• Sense of impending doom

IMPORTANT: PE can be ASYMPTOMATIC or present with nonspecific symptoms. High index of suspicion required.

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachypnea (70%): Most common sign; RR >20
• Tachycardia (30%): HR >100; may be only abnormality
• Hypoxemia: SpO2 may be normal in small PE
• Fever (7%): Usually low-grade; <38.5°C
• Hypotension: SBP <90 indicates massive PE

CARDIOVASCULAR EXAMINATION:
• Elevated JVP (indicates RV failure)
• Loud P2 (pulmonary hypertension)
• Right ventricular heave
• Tricuspid regurgitation murmur
• Right-sided S3 gallop

RESPIRATORY EXAMINATION:
• Often NORMAL
• Crackles (uncommon)
• Decreased breath sounds (if effusion)
• Pleural friction rub (if infarction)
• Wheezing (uncommon)

LOWER EXTREMITY (concurrent DVT):
• Unilateral leg swelling
• Calf tenderness
• Palpable cord (thrombosed vein)
• Homan's sign (unreliable)

SIGNS OF MASSIVE PE:
• Hypotension (SBP <90 or drop >40 mmHg for >15 min)
• Signs of shock (cool, clammy, mottled skin)
• Altered mental status
• Cyanosis
• Distended neck veins

IMPORTANT: Physical examination may be COMPLETELY NORMAL in PE.`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

STEP 1: ASSESS CLINICAL PROBABILITY (Before any testing)

WELLS SCORE FOR PE:
• Clinical signs of DVT: +3.0
• PE most likely diagnosis: +3.0
• Heart rate >100: +1.5
• Immobilization >3 days or surgery in past 4 weeks: +1.5
• Previous DVT/PE: +1.5
• Hemoptysis: +1.0
• Active cancer (treatment within 6 months): +1.0

Interpretation:
• Low probability: ≤4 (or ≤1 in two-tier model)
• High probability: >4 (or >1 in two-tier model)

REVISED GENEVA SCORE:
• Age >65: +1
• Previous DVT/PE: +3
• Surgery or fracture within 1 month: +2
• Active malignancy: +2
• Unilateral lower limb pain: +3
• Hemoptysis: +2
• Heart rate 75-94: +3 / ≥95: +5
• Pain on lower limb palpation and unilateral edema: +4

Interpretation:
• Low: 0-3
• Intermediate: 4-10
• High: ≥11

STEP 2: TESTING STRATEGY BASED ON PROBABILITY

LOW/INTERMEDIATE PROBABILITY:
→ D-dimer first
  • If NEGATIVE: PE excluded (high NPV)
  • If POSITIVE: Proceed to CTPA

HIGH PROBABILITY:
→ Skip D-dimer, go directly to CTPA
  • D-dimer will likely be positive; doesn't change management

HEMODYNAMICALLY UNSTABLE:
→ Bedside echocardiography (if CTPA not immediately available)
→ If RV dysfunction present: Consider empiric thrombolysis
→ CTPA when patient stabilized

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC TESTS
═══════════════════════════════════════════════════════════════════════════════

D-DIMER:
• Highly sensitive but NOT specific
• Normal D-dimer (<500 ng/mL) EXCLUDES PE in low/intermediate probability
• Age-adjusted cutoff: Age × 10 ng/mL (for age >50)
• Elevated in: cancer, infection, inflammation, pregnancy, recent surgery, trauma, renal disease
• Do NOT order if high clinical probability

CT PULMONARY ANGIOGRAPHY (CTPA):
• GOLD STANDARD for PE diagnosis
• Sensitivity 83-100%, Specificity 89-97%
• Can detect alternative diagnoses
• Requires IV contrast (caution in renal impairment, contrast allergy)
• Radiation exposure concern in pregnancy (though generally acceptable)

V/Q SCAN (Ventilation/Perfusion):
• Alternative when CTPA contraindicated
• Useful in pregnancy (less fetal radiation), contrast allergy, renal failure
• Best when CXR is normal
• Results: Normal (excludes PE), High probability (confirms PE), Non-diagnostic (need further testing)
• PIOPED criteria for interpretation

ECHOCARDIOGRAPHY:
• NOT diagnostic for PE, but important for risk stratification
• Findings: RV dilation, RV hypokinesis, septal flattening (D-sign), McConnell's sign (RV free wall hypokinesis with apical sparing), tricuspid regurgitation, pulmonary hypertension
• Useful at bedside for unstable patients when CTPA not immediately available

LOWER EXTREMITY DOPPLER ULTRASOUND:
• If DVT confirmed, supports PE diagnosis
• May avoid need for CTPA if both clinical probability for PE and DVT confirmed
• Useful in pregnancy to avoid radiation

PULMONARY ANGIOGRAPHY:
• Historical gold standard; rarely used now
• Reserved for cases where CTPA inconclusive and clinical suspicion remains high
• Can also perform catheter-directed therapy

═══════════════════════════════════════════════════════════════════════════════
RISK STRATIFICATION OF CONFIRMED PE
═══════════════════════════════════════════════════════════════════════════════

sPESI SCORE (Simplified Pulmonary Embolism Severity Index):
• Age >80: +1
• History of cancer: +1
• History of chronic cardiopulmonary disease: +1
• Heart rate ≥110: +1
• SBP <100 mmHg: +1
• SpO2 <90%: +1

Score 0 = Low risk (1% 30-day mortality) → Consider outpatient treatment
Score ≥1 = Not low risk → Hospitalization recommended

HEMODYNAMIC CLASSIFICATION:
• HIGH RISK (Massive): SBP <90 mmHg or requiring vasopressors → ICU, consider thrombolysis
• INTERMEDIATE-HIGH RISK: RV dysfunction AND elevated troponin → Close monitoring, consider escalation
• INTERMEDIATE-LOW RISK: RV dysfunction OR elevated troponin → Hospitalization
• LOW RISK: None of the above → Outpatient treatment possible`,

    differentialDiagnosis: [
      'Acute coronary syndrome (ACS) - Chest pain, dyspnea, elevated troponin; ECG changes; distinguish by CTPA and coronary evaluation',
      'Pneumonia - Fever, cough, consolidation on CXR; may coexist with PE',
      'Heart failure exacerbation - Dyspnea, orthopnea, peripheral edema; BNP elevated; bilateral infiltrates on CXR',
      'COPD/Asthma exacerbation - History of obstructive lung disease; wheezing; response to bronchodilators',
      'Pneumothorax - Sudden dyspnea, pleuritic pain; absent breath sounds; CXR diagnostic',
      'Aortic dissection - Tearing chest pain radiating to back; BP differential; CT angiography shows dissection flap',
      'Pericarditis - Pleuritic chest pain worse supine, better leaning forward; friction rub; diffuse ST elevation',
      'Musculoskeletal chest pain - Reproducible with palpation; no dyspnea or vital sign abnormalities',
      'Anxiety/Panic attack - Hyperventilation, paresthesias; normal workup; diagnosis of exclusion',
      'Pleuritis - Pleuritic pain; may be viral or autoimmune; PE must be excluded first',
      'Pulmonary hypertension - Chronic dyspnea; RV dysfunction; CTPA negative for acute PE',
      'Lung cancer - Chronic symptoms; mass on imaging; hemoptysis may be prominent'
    ],

    investigations: [
      {
        name: 'D-Dimer (Quantitative)',
        indication: '🔴 First-line in LOW/INTERMEDIATE probability - Normal D-dimer (<500 ng/mL or age-adjusted) excludes PE; do NOT order if high probability',
        availability: 'BASIC'
      },
      {
        name: 'CT Pulmonary Angiography (CTPA)',
        indication: '🔴 GOLD STANDARD - Confirmatory test for PE; use in high probability or positive D-dimer; shows filling defects in pulmonary arteries',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Electrocardiogram (ECG)',
        indication: '🔴 All patients - Most common finding is sinus tachycardia; S1Q3T3 pattern (classic but uncommon); T-wave inversions V1-V4; RBBB; may exclude ACS',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'Often NORMAL in PE; may show Westermark sign (oligemia), Hampton hump (peripheral wedge opacity), Fleischner sign (enlarged PA); useful to exclude other causes',
        availability: 'BASIC'
      },
      {
        name: 'Troponin (I or T)',
        indication: '🔴 Risk stratification - Elevated troponin indicates RV strain and higher risk; part of intermediate-high risk classification',
        availability: 'BASIC'
      },
      {
        name: 'BNP or NT-proBNP',
        indication: 'Risk stratification - Elevated suggests RV dysfunction; prognostic value; helps distinguish from heart failure',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: 'Assess oxygenation and acid-base status; may show hypoxemia, hypocapnia, respiratory alkalosis; A-a gradient often elevated',
        availability: 'BASIC'
      },
      {
        name: 'Echocardiography (TTE)',
        indication: '🔴 Risk stratification - RV dilation, RV dysfunction, McConnell sign, D-sign, TR; essential for hemodynamically unstable patients at bedside',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lower Extremity Doppler Ultrasound',
        indication: 'Assess for concurrent DVT; positive DVT in symptomatic patient supports PE diagnosis; may avoid CTPA in pregnancy',
        availability: 'BASIC'
      },
      {
        name: 'V/Q Scan (Ventilation/Perfusion)',
        indication: 'Alternative to CTPA when contraindicated (pregnancy, contrast allergy, severe renal impairment); best when CXR normal',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Baseline; assess for anemia (if hemoptysis); thrombocytopenia (affects anticoagulation choice)',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (BMP)',
        indication: 'Renal function for contrast and anticoagulation dosing; electrolytes',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: 'Baseline before anticoagulation; identify underlying coagulopathy',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Elevated in massive PE with shock; marker of tissue hypoperfusion; prognostic value',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Thrombophilia Panel',
        indication: 'Consider in unprovoked PE, young patients, recurrent VTE, family history; includes Factor V Leiden, Prothrombin mutation, Protein C/S, Antithrombin, Antiphospholipid antibodies; test AFTER completing anticoagulation',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS HEMODYNAMIC STABILITY: SBP <90 mmHg or requiring vasopressors = MASSIVE PE requiring emergent treatment',
        '⚡ OXYGEN: Maintain SpO2 >90%; high-flow oxygen; intubation if severe respiratory failure (caution - may precipitate cardiovascular collapse)',
        '⚡ IV ACCESS: Two large-bore IVs; prepare for fluid resuscitation or thrombolysis',
        '⚡ ANTICOAGULATION (if PE confirmed or highly suspected): Start IMMEDIATELY - do not wait for imaging if high probability and no contraindications',
        '⚡ FLUID RESUSCITATION: Judicious IV fluids (250-500 mL boluses) for hypotension; avoid excessive fluids (can worsen RV failure)',
        '⚡ VASOPRESSORS: Norepinephrine first-line if hypotensive despite fluids; dobutamine may help RV function',
        '⚡ RISK STRATIFY: Calculate sPESI; obtain troponin and echocardiography for intermediate-high risk assessment',
        '⚡ CONSIDER THROMBOLYSIS: For massive PE (hemodynamic instability) or deteriorating intermediate-high risk PE',
        '⚡ MONITOR: Continuous telemetry, pulse oximetry; repeat vitals every 15-30 minutes in high-risk patients',
        '⚡ ICU ADMISSION: For massive PE, intermediate-high risk with deterioration, or need for thrombolysis'
      ],
      pharmacological: [
        {
          drug: 'Rivaroxaban (Xarelto)',
          indication: '🔴 FIRST-LINE for most PE - Direct Xa inhibitor; monotherapy without heparin lead-in',
          dose: '15 mg PO twice daily with food for 21 days, then 20 mg PO once daily; Reduce to 10 mg daily after 6 months for extended treatment',
          route: 'Oral',
          duration: 'Provoked PE: 3 months; Unprovoked: At least 3 months, consider indefinite',
          contraindications: ['CrCl <30 mL/min (relative)', 'Active bleeding', 'Severe hepatic impairment', 'Pregnancy', 'Mechanical heart valve', 'Antiphospholipid syndrome'],
          sideEffects: ['Bleeding', 'GI upset', 'Anemia'],
          alternatives: ['Apixaban', 'LMWH + Warfarin', 'LMWH alone (cancer)']
        },
        {
          drug: 'Apixaban (Eliquis)',
          indication: '🔴 FIRST-LINE alternative - Direct Xa inhibitor; monotherapy without heparin lead-in; may have lower bleeding risk',
          dose: '10 mg PO twice daily for 7 days, then 5 mg PO twice daily; Extended: 2.5 mg twice daily after 6 months',
          route: 'Oral',
          duration: 'Provoked PE: 3 months; Unprovoked: At least 3 months, consider indefinite',
          contraindications: ['Active bleeding', 'Severe hepatic impairment', 'Pregnancy', 'Mechanical heart valve'],
          sideEffects: ['Bleeding (lower GI bleeding than rivaroxaban)', 'Nausea', 'Anemia'],
          alternatives: ['Rivaroxaban', 'Edoxaban', 'LMWH + Warfarin']
        },
        {
          drug: 'Enoxaparin (Lovenox)',
          indication: '🔴 Parenteral anticoagulation - Used for initial treatment or as bridge to warfarin; preferred in pregnancy and cancer',
          dose: '1 mg/kg SC every 12 hours OR 1.5 mg/kg SC once daily; Reduce to 1 mg/kg once daily if CrCl <30 mL/min',
          route: 'Subcutaneous',
          duration: 'At least 5 days AND until INR ≥2 for 24 hours if bridging to warfarin; longer in cancer',
          contraindications: ['Active major bleeding', 'Heparin-induced thrombocytopenia', 'Severe thrombocytopenia'],
          sideEffects: ['Bleeding', 'Injection site hematoma', 'Thrombocytopenia (HIT rare)'],
          alternatives: ['Unfractionated heparin', 'Fondaparinux']
        },
        {
          drug: 'Unfractionated Heparin (UFH)',
          indication: '🔴 For massive PE, renal failure, or when rapid reversal may be needed (bleeding risk, surgery)',
          dose: 'Bolus: 80 units/kg IV, then 18 units/kg/hour infusion; Titrate to aPTT 1.5-2.5x control (60-80 seconds)',
          route: 'IV continuous infusion',
          duration: 'Until transitioned to oral anticoagulation or thrombolysis given',
          contraindications: ['Active bleeding', 'HIT', 'Severe uncontrolled hypertension'],
          sideEffects: ['Bleeding', 'HIT (1-3%)', 'Osteoporosis with prolonged use'],
          alternatives: ['LMWH', 'Fondaparinux']
        },
        {
          drug: 'Warfarin (Coumadin)',
          indication: 'Long-term anticoagulation - Less preferred than DOACs but still appropriate option',
          dose: 'Start 5 mg PO daily (lower in elderly, malnourished, liver disease); Adjust to INR 2-3; Overlap with heparin for at least 5 days AND INR ≥2 for 24 hours',
          route: 'Oral',
          duration: 'Provoked PE: 3 months; Unprovoked: At least 3 months, consider indefinite',
          contraindications: ['Pregnancy', 'Active bleeding', 'Non-adherent patients', 'Cannot monitor INR'],
          sideEffects: ['Bleeding', 'Warfarin-induced skin necrosis (rare)', 'Requires dietary consistency (vitamin K)'],
          alternatives: ['DOACs preferred in most patients']
        },
        {
          drug: 'Alteplase (tPA)',
          indication: '🔴 THROMBOLYSIS for MASSIVE PE - Hemodynamically unstable (SBP <90, shock, cardiac arrest)',
          dose: '100 mg IV over 2 hours; OR 50 mg bolus in cardiac arrest; Some protocols: 0.6 mg/kg over 15 min (max 50 mg)',
          route: 'IV',
          duration: 'Single administration; Follow with heparin infusion (no bolus)',
          contraindications: ['Absolute: Active bleeding, recent hemorrhagic stroke, intracranial neoplasm, recent intracranial surgery', 'Relative: Recent surgery, GI bleeding, uncontrolled HTN, pregnancy, recent trauma'],
          sideEffects: ['Major bleeding (20%)', 'Intracranial hemorrhage (2-3%)', 'Fatal bleeding (1-2%)'],
          alternatives: ['Catheter-directed thrombolysis', 'Surgical embolectomy', 'Half-dose alteplase (50 mg)']
        },
        {
          drug: 'Edoxaban (Savaysa)',
          indication: 'Alternative DOAC - Requires 5 days of parenteral anticoagulation before starting',
          dose: '60 mg PO once daily; Reduce to 30 mg if CrCl 15-50, body weight ≤60 kg, or using P-gp inhibitors',
          route: 'Oral',
          duration: 'Provoked PE: 3 months; Unprovoked: At least 3 months, consider indefinite',
          contraindications: ['CrCl >95 mL/min (inferior efficacy)', 'Active bleeding', 'Severe hepatic impairment'],
          sideEffects: ['Bleeding', 'Anemia', 'Rash'],
          alternatives: ['Rivaroxaban', 'Apixaban']
        },
        {
          drug: 'Dabigatran (Pradaxa)',
          indication: 'Alternative DOAC - Direct thrombin inhibitor; requires 5 days parenteral anticoagulation first',
          dose: '150 mg PO twice daily after 5 days of parenteral anticoagulation; 110 mg twice daily if age ≥80 or high bleeding risk',
          route: 'Oral',
          duration: 'Provoked PE: 3 months; Unprovoked: At least 3 months, consider indefinite',
          contraindications: ['CrCl <30 mL/min', 'Mechanical heart valve', 'Active bleeding'],
          sideEffects: ['Bleeding', 'Dyspepsia (common)', 'GI bleeding'],
          alternatives: ['Rivaroxaban', 'Apixaban']
        },
        {
          drug: 'Fondaparinux (Arixtra)',
          indication: 'Alternative to LMWH - Useful if history of HIT',
          dose: '<50 kg: 5 mg SC daily; 50-100 kg: 7.5 mg SC daily; >100 kg: 10 mg SC daily',
          route: 'Subcutaneous',
          duration: 'Bridge to oral anticoagulation',
          contraindications: ['CrCl <30 mL/min', 'Active bleeding', 'Bacterial endocarditis'],
          sideEffects: ['Bleeding', 'Anemia', 'Thrombocytopenia (rare, no HIT)'],
          alternatives: ['LMWH', 'UFH']
        },
        {
          drug: 'Norepinephrine',
          indication: '🔴 MASSIVE PE with hypotension - First-line vasopressor',
          dose: 'Start 0.1-0.5 mcg/kg/min; titrate to MAP ≥65 mmHg',
          route: 'IV continuous infusion (central line preferred)',
          duration: 'Until hemodynamically stable',
          contraindications: ['None absolute in shock state'],
          sideEffects: ['Arrhythmias', 'Peripheral ischemia', 'Hypertension'],
          alternatives: ['Vasopressin', 'Epinephrine']
        }
      ],
      nonPharmacological: [
        'OXYGEN THERAPY: Maintain SpO2 >90%; nasal cannula to high-flow; mechanical ventilation if needed (use low tidal volumes, avoid high PEEP which worsens RV function)',
        'IVC FILTER: Consider if anticoagulation absolutely contraindicated OR recurrent PE despite adequate anticoagulation; retrievable filters preferred; NOT routine',
        'CATHETER-DIRECTED THROMBOLYSIS (CDT): Alternative to systemic thrombolysis; lower dose tPA directly to clot; may reduce bleeding risk',
        'SURGICAL EMBOLECTOMY: For massive PE when thrombolysis contraindicated or failed; requires cardiopulmonary bypass; limited availability',
        'MECHANICAL THROMBECTOMY: Catheter-based clot aspiration or fragmentation; increasingly used',
        'ECMO (Extracorporeal Membrane Oxygenation): Salvage therapy for refractory cardiogenic shock; bridge to recovery or intervention',
        'GRADUATED COMPRESSION STOCKINGS: NOT proven to prevent post-thrombotic syndrome in recent trials; optional',
        'EARLY AMBULATION: Encouraged once anticoagulated; does not increase PE risk'
      ],
      surgical: [
        'SURGICAL PULMONARY EMBOLECTOMY: Emergency surgery for massive PE when thrombolysis contraindicated or failed; requires experienced center with cardiopulmonary bypass capability',
        'IVC FILTER PLACEMENT: Interventional radiology procedure; inferior vena cava filter to prevent further emboli; indicated when anticoagulation contraindicated',
        'CATHETER-BASED INTERVENTIONS: Catheter-directed thrombolysis, aspiration thrombectomy, rheolytic thrombectomy; performed by interventional radiology/cardiology',
        'CHRONIC THROMBOEMBOLIC PULMONARY HYPERTENSION (CTEPH) SURGERY: Pulmonary thromboendarterectomy for patients with persistent clot and pulmonary hypertension after PE'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: Massive PE (hemodynamic instability); need for thrombolysis; mechanical ventilation; vasopressor requirement',
        '🔴 IMMEDIATE PULMONOLOGY/CRITICAL CARE: Intermediate-high risk PE; respiratory failure; clinical deterioration',
        '🔴 CARDIOLOGY: RV dysfunction on echo; consideration for catheter-directed therapy; cardiogenic shock',
        '🔴 INTERVENTIONAL RADIOLOGY: Catheter-directed thrombolysis; IVC filter placement; mechanical thrombectomy',
        '🔴 CARDIOTHORACIC SURGERY: Surgical embolectomy consideration; ECMO',
        '🟠 HEMATOLOGY: Unprovoked PE for thrombophilia workup; recurrent VTE; complex anticoagulation issues; cancer-associated VTE',
        '🟠 OUTPATIENT PULMONOLOGY: Low-risk PE for outpatient management; follow-up imaging; CTEPH screening'
      ],
      monitoring: [
        'VITAL SIGNS: Continuous monitoring in high/intermediate-risk; every 4 hours in low-risk; watch for deterioration',
        'OXYGENATION: Continuous pulse oximetry; ABG if hypoxemic or deteriorating',
        'CARDIAC MONITORING: Telemetry for arrhythmias, especially in intermediate-high risk',
        'REPEAT ECHOCARDIOGRAPHY: If clinical deterioration to assess RV function',
        'ANTICOAGULATION MONITORING: aPTT for UFH (q6h until stable, then daily); Anti-Xa levels for LMWH if renal impairment or obesity; INR for warfarin',
        'BLEEDING ASSESSMENT: Daily CBC; monitor for signs of bleeding (hematuria, melena, hemoptysis, bruising)',
        'RENAL FUNCTION: Especially if on DOACs or contrast given',
        'PLATELET COUNT: Monitor for HIT if on heparin (day 4-14)',
        'CLINICAL RESPONSE: Resolution of dyspnea, tachycardia, oxygen requirement'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
HOSPITAL DISCHARGE CRITERIA
═══════════════════════════════════════════════════════════════════════════════

• Hemodynamically stable for ≥24 hours
• Oxygen saturation stable without supplemental O2 (or at baseline)
• Pain controlled with oral medications
• Able to tolerate oral anticoagulation
• Adequate home support and follow-up arranged
• Patient education on anticoagulation completed

LOW-RISK PE (sPESI = 0):
• Consider outpatient treatment with DOAC
• Ensure close follow-up within 1 week
• Clear return precautions provided

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

1-2 WEEKS POST-DISCHARGE:
• Assess for bleeding complications
• Confirm anticoagulation adherence
• Review symptoms (dyspnea should improve)
• INR check if on warfarin

3 MONTHS:
• Reassess need for continued anticoagulation
• Provoked PE with resolved risk factor: Consider stopping
• Unprovoked PE: Discuss indefinite anticoagulation (balance bleeding vs recurrence risk)
• Assess for persistent dyspnea (may indicate CTEPH)

LONG-TERM:
• Annual assessment of anticoagulation continuation
• CTEPH screening: Echo at 3-6 months if persistent dyspnea
• Cancer screening in unprovoked PE (age-appropriate + consider CT abdomen/pelvis)
• Thrombophilia testing if unprovoked and would change management

═══════════════════════════════════════════════════════════════════════════════
DURATION OF ANTICOAGULATION
═══════════════════════════════════════════════════════════════════════════════

PROVOKED PE (transient risk factor):
• Major surgery, trauma, prolonged immobility: 3 months
• Risk factor resolved: Can stop after 3 months

UNPROVOKED PE:
• Minimum 3 months
• Reassess at 3 months for indefinite anticoagulation
• Factors favoring indefinite: Male sex, elevated D-dimer off anticoagulation, residual DVT, high-risk thrombophilia, recurrent VTE
• Patient preference important (bleeding risk vs recurrence risk)

CANCER-ASSOCIATED VTE:
• Continue anticoagulation while cancer active
• LMWH or DOACs (edoxaban, rivaroxaban) preferred
• At least 6 months, often indefinite

RECURRENT UNPROVOKED VTE:
• Indefinite anticoagulation recommended`
    },

    redFlags: [
      '🔴 CRITICAL: Hypotension (SBP <90 mmHg) or need for vasopressors - MASSIVE PE requiring emergent thrombolysis consideration',
      '🔴 CRITICAL: Cardiac arrest - PE is common cause; consider empiric thrombolysis during CPR',
      '🔴 CRITICAL: Severe hypoxemia (SpO2 <90% despite high-flow O2) - Respiratory failure; may need intubation',
      '🔴 CRITICAL: RV dysfunction on echo with elevated troponin - INTERMEDIATE-HIGH risk; may deteriorate rapidly',
      '🔴 CRITICAL: Syncope at presentation - Suggests massive or near-massive PE',
      '🔴 CRITICAL: Rapidly progressive dyspnea and tachycardia - Clinical deterioration; reassess and consider escalation',
      '🟠 URGENT: New or worsening RV dysfunction - Consider thrombolysis or catheter-directed therapy',
      '🟠 URGENT: sPESI ≥1 or PESI class III-V - Not low risk; requires hospitalization and close monitoring',
      '🟠 URGENT: Elevated troponin - Indicates RV strain; intermediate risk at minimum',
      '🟠 URGENT: Large clot burden on CTPA (saddle embolus, bilateral main PA involvement) - Higher risk of deterioration',
      '🟠 URGENT: Concurrent DVT with high proximal extent - Risk of further embolization',
      '🟠 URGENT: Active bleeding on anticoagulation - May need reversal, IVC filter consideration'
    ],

    complications: [
      'DEATH: 10-30% overall; >50% in massive PE; usually from RV failure or recurrent PE',
      'RECURRENT VTE: 30% over 10 years without anticoagulation; 3% per year on anticoagulation',
      'CHRONIC THROMBOEMBOLIC PULMONARY HYPERTENSION (CTEPH): 2-4% of PE survivors; persistent dyspnea, RV failure; diagnosed by V/Q scan and right heart catheterization; may require pulmonary thromboendarterectomy',
      'POST-PULMONARY EMBOLISM SYNDROME: Persistent dyspnea, exercise intolerance without CTEPH; functional limitation',
      'ANTICOAGULATION-RELATED BLEEDING: Major bleeding 2-3% per year; intracranial hemorrhage most feared; GI bleeding common',
      'HEPARIN-INDUCED THROMBOCYTOPENIA (HIT): 1-3% on UFH; <1% on LMWH; paradoxically causes thrombosis',
      'POST-THROMBOTIC SYNDROME: From concurrent DVT; chronic leg swelling, pain, ulceration',
      'PULMONARY INFARCTION: 10% of PE; causes hemoptysis, pleuritic pain, pleural effusion; usually recovers',
      'RIGHT HEART FAILURE: Acute or chronic RV dysfunction; may require long-term management',
      'PSYCHOLOGICAL IMPACT: Anxiety, depression, fear of recurrence common'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

• Overall 30-day mortality: 10-30% (depends on severity)
• Low-risk PE (sPESI 0): <1% mortality
• Intermediate-low risk: 3-5% mortality
• Intermediate-high risk: 5-15% mortality
• High-risk (massive) PE: 25-65% mortality
• PE with cardiac arrest: >90% mortality without treatment

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Hemodynamic instability (strongest predictor)
• RV dysfunction on echo or CT
• Elevated troponin
• Elevated BNP/NT-proBNP
• High PESI or sPESI score
• Large clot burden
• Cancer
• Advanced age
• Chronic cardiopulmonary disease
• Delayed diagnosis and treatment

FAVORABLE PROGNOSIS:
• Low-risk by sPESI (score = 0)
• Normal RV function
• Normal biomarkers
• Provoked by transient risk factor
• Young age
• Early diagnosis and treatment

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

RECURRENCE:
• Without anticoagulation: 30% at 10 years
• On anticoagulation: 3% per year
• After stopping anticoagulation (unprovoked PE): 10% at 1 year, 30% at 5 years

CTEPH (Chronic Thromboembolic Pulmonary Hypertension):
• Occurs in 2-4% of PE survivors
• Presents with progressive dyspnea months to years after PE
• Screen with echo if persistent symptoms
• Treatable with pulmonary thromboendarterectomy

QUALITY OF LIFE:
• Most patients recover fully
• 20-50% have persistent dyspnea or exercise limitation
• Post-PE syndrome recognized entity
• Psychological impact (anxiety, fear of recurrence) common`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION (VTE Prophylaxis)
═══════════════════════════════════════════════════════════════════════════════

HOSPITALIZED MEDICAL PATIENTS:
• Assess VTE risk (Padua score, IMPROVE score)
• Pharmacologic prophylaxis if high risk and no contraindications:
  - Enoxaparin 40 mg SC daily OR
  - UFH 5000 units SC q8-12h OR
  - Fondaparinux 2.5 mg SC daily
• Mechanical prophylaxis if bleeding risk: Intermittent pneumatic compression (IPC)
• Duration: Throughout hospitalization; consider extended prophylaxis in high-risk

SURGICAL PATIENTS:
• Risk stratify using Caprini score
• Low risk: Early ambulation
• Moderate risk: LMWH, UFH, or IPC
• High risk: LMWH or UFH PLUS IPC
• Very high risk (orthopedic surgery): Extended prophylaxis 28-35 days

ORTHOPEDIC SURGERY:
• Hip/knee replacement: LMWH, fondaparinux, rivaroxaban, apixaban, or aspirin for 35 days (hip) or 14 days (knee)
• Hip fracture: LMWH or fondaparinux for 28-35 days

CANCER PATIENTS:
• Higher VTE risk; prophylaxis during hospitalization
• Consider extended prophylaxis during chemotherapy (LMWH or DOAC per risk assessment)
• Khorana score to stratify outpatient risk

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After VTE)
═══════════════════════════════════════════════════════════════════════════════

• Anticoagulation: Duration based on provoked vs unprovoked (see management)
• Risk factor modification: Weight loss, smoking cessation, avoid estrogen if possible
• Compression stockings: No longer routinely recommended for post-thrombotic syndrome prevention
• Activity: Encourage exercise once anticoagulated; no evidence of harm from early ambulation
• Travel precautions: Hydration, calf exercises, compression stockings for long flights; prophylactic LMWH for very high-risk patients

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT
═══════════════════════════════════════════════════════════════════════════════

• VTE prophylaxis underutilized in many African hospitals
• Mechanical prophylaxis (IPC) may not be available
• LMWH cost may be prohibitive; UFH more available
• Education needed on VTE risk in hospitalized patients
• HIV increases VTE risk - consider prophylaxis in hospitalized HIV patients
• Post-surgical immobility may be prolonged - extended prophylaxis important`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING PULMONARY EMBOLISM
═══════════════════════════════════════════════════════════════════════════════

You have been diagnosed with a pulmonary embolism (PE), which is a blood clot in your lungs. This clot usually travels from a vein in your legs (called deep vein thrombosis or DVT). The clot blocks blood flow in your lungs, which can be dangerous.

WHY THIS HAPPENED:
Blood clots form when blood flow slows down or when there is something that makes your blood more likely to clot. Risk factors include:
• Recent surgery or hospitalization
• Long periods of not moving (bed rest, long flights)
• Cancer
• Hormonal medications (birth control, hormone replacement)
• Inherited clotting disorders
• Sometimes no clear cause is found

═══════════════════════════════════════════════════════════════════════════════
YOUR TREATMENT: BLOOD THINNERS
═══════════════════════════════════════════════════════════════════════════════

You will take blood thinner medication (anticoagulant) to:
• Prevent your current clot from growing
• Prevent new clots from forming
• Allow your body to slowly dissolve the clot naturally

TYPES OF BLOOD THINNERS:
• Pills (rivaroxaban, apixaban, warfarin): Taken by mouth daily
• Injections (enoxaparin): Given under the skin

HOW LONG:
• At least 3 months
• Your doctor may recommend longer, sometimes lifelong, depending on your situation

IMPORTANT MEDICATION INSTRUCTIONS:
• Take your blood thinner EXACTLY as prescribed
• Do NOT skip doses - this increases clot risk
• Do NOT take double doses
• Keep all follow-up appointments

═══════════════════════════════════════════════════════════════════════════════
LIVING ON BLOOD THINNERS
═══════════════════════════════════════════════════════════════════════════════

BLEEDING RISK:
Blood thinners increase your risk of bleeding. To stay safe:
• Avoid contact sports and activities with high injury risk
• Use a soft toothbrush and electric razor
• Be careful with sharp objects
• Tell all doctors and dentists you are on blood thinners
• Wear a medical alert bracelet

THINGS TO AVOID:
• Excessive alcohol (increases bleeding risk)
• NSAIDs (ibuprofen, naproxen) unless approved by your doctor
• If on WARFARIN: Keep vitamin K intake consistent (green leafy vegetables)

MEDICATIONS TO AVOID (unless approved):
• Aspirin (unless specifically prescribed)
• Ibuprofen, naproxen, and other anti-inflammatory drugs
• Many herbal supplements (fish oil, ginkgo, garlic pills, vitamin E)

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK IMMEDIATE CARE
═══════════════════════════════════════════════════════════════════════════════

Call emergency services or go to the ER immediately if you experience:

🔴 SIGNS OF ANOTHER CLOT:
• New or worsening shortness of breath
• Chest pain
• Rapid heartbeat
• Coughing up blood
• New leg swelling, pain, or warmth

🔴 SIGNS OF SERIOUS BLEEDING:
• Blood in urine (pink or red) or stool (black or tarry)
• Coughing or vomiting blood
• Severe headache, confusion, or weakness (could be brain bleeding)
• Heavy menstrual bleeding
• Bleeding that does not stop with pressure

🔴 SIGNS OF STROKE (blood thinners increase risk):
• Sudden weakness or numbness on one side
• Sudden confusion or trouble speaking
• Sudden severe headache
• Sudden vision problems

═══════════════════════════════════════════════════════════════════════════════
PREVENTING FUTURE CLOTS
═══════════════════════════════════════════════════════════════════════════════

• Take your blood thinner as prescribed
• Stay active - walk regularly; avoid sitting or lying for long periods
• Stay hydrated, especially during travel
• On long flights: Walk the aisle, do calf exercises, wear compression stockings
• Maintain a healthy weight
• Do not smoke
• Discuss estrogen-containing medications with your doctor

═══════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
═══════════════════════════════════════════════════════════════════════════════

• Keep all appointments with your doctor
• If on warfarin: Attend all INR blood test appointments
• Report any new symptoms promptly
• At 3 months: Discuss duration of treatment with your doctor
• If you have persistent shortness of breath: Tell your doctor (may need further testing)

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• How long will I need to take blood thinners?
• What caused my blood clot?
• Do I need testing for clotting disorders?
• What activities should I avoid?
• When can I return to work/exercise?
• What should I do if I miss a dose?
• Who should I contact if I have problems?`,

    references: [
      'Konstantinides SV, et al. 2019 ESC Guidelines for the diagnosis and management of acute pulmonary embolism. Eur Heart J. 2020;41(4):543-603. doi:10.1093/eurheartj/ehz405',
      'Kearon C, et al. Antithrombotic Therapy for VTE Disease: CHEST Guideline and Expert Panel Report. Chest. 2016;149(2):315-352. doi:10.1016/j.chest.2015.11.026',
      'PIOPED Investigators. Value of the ventilation/perfusion scan in acute pulmonary embolism. JAMA. 1990;263(20):2753-9.',
      'Agnelli G, et al. Oral Apixaban for the Treatment of Acute Venous Thromboembolism (AMPLIFY). N Engl J Med. 2013;369(9):799-808. doi:10.1056/NEJMoa1302507',
      'EINSTEIN Investigators. Oral Rivaroxaban for Symptomatic Venous Thromboembolism. N Engl J Med. 2010;363(26):2499-510. doi:10.1056/NEJMoa1007903',
      'Meyer G, et al. Fibrinolysis for Patients with Intermediate-Risk Pulmonary Embolism (PEITHO). N Engl J Med. 2014;370(15):1402-11. doi:10.1056/NEJMoa1302097',
      'Aujesky D, et al. Outpatient versus inpatient treatment for patients with acute pulmonary embolism: an international, open-label, randomised, non-inferiority trial. Lancet. 2011;378(9785):41-8.',
      'Wells PS, et al. Derivation of a simple clinical model to categorize patients probability of pulmonary embolism. Thromb Haemost. 2000;83(3):416-20.',
      'Tapson VF. Acute Pulmonary Embolism. N Engl J Med. 2008;358(10):1037-52. doi:10.1056/NEJMra072753'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 16. ANAPHYLAXIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '16',
    name: 'Anaphylaxis',
    slug: 'anaphylaxis',
    specialty: 'Emergency Medicine',
    bodySystem: 'Cardiovascular',
    icdCode: 'T78.2',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Anaphylaxis is a severe, life-threatening, systemic hypersensitivity reaction characterized by rapid onset of airway, breathing, and/or circulatory compromise, usually associated with skin and mucosal changes. It is a medical emergency requiring immediate recognition and treatment with intramuscular epinephrine.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• EPINEPHRINE IM is the FIRST-LINE treatment - Give IMMEDIATELY upon recognition; delay increases mortality. There are NO absolute contraindications to epinephrine in anaphylaxis.

• EPINEPHRINE DOSE: 0.3-0.5 mg IM (0.01 mg/kg in children, max 0.5 mg) into ANTEROLATERAL THIGH; repeat every 5-15 minutes if no improvement

• DIAGNOSIS IS CLINICAL - Do NOT wait for laboratory confirmation; anaphylaxis is a clinical diagnosis based on rapid onset of symptoms involving skin/mucosal tissue PLUS respiratory compromise OR hypotension OR end-organ dysfunction

• REMOVE THE TRIGGER if identifiable (stop IV medication, remove stinger)

• POSITION: Lay patient flat with legs elevated UNLESS respiratory distress (then sit up) or pregnant (left lateral); do NOT have patient sit or stand (risk of sudden death)

• BIPHASIC REACTIONS occur in 5-20% of cases - Symptoms recur 1-72 hours (usually 8-10 hours) after initial reaction; observe patients for at least 4-6 hours (some say 24 hours for severe reactions)

• MOST COMMON TRIGGERS: Foods (peanuts, tree nuts, shellfish, milk, eggs), medications (antibiotics, NSAIDs), insect stings (Hymenoptera), latex

• H1 + H2 ANTIHISTAMINES are ADJUNCTIVE only - They do NOT treat airway obstruction or shock; give AFTER epinephrine

• CORTICOSTEROIDS do NOT treat acute anaphylaxis - May help prevent biphasic reactions (evidence weak); give after epinephrine

• IV FLUIDS for hypotension - Aggressive crystalloid resuscitation (1-2 L in adults, 20 mL/kg in children); anaphylaxis causes massive fluid shifts

• GLUCAGON for patients on beta-blockers - May not respond to epinephrine; glucagon 1-5 mg IV/IM

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

IMMUNOLOGIC (IgE-MEDIATED) ANAPHYLAXIS:
1. Sensitization: First exposure to allergen → IgE antibody production
2. Re-exposure: Allergen binds to IgE on mast cells and basophils
3. Degranulation: Massive release of preformed mediators (histamine, tryptase, heparin)
4. Synthesis of new mediators: Leukotrienes, prostaglandins, platelet-activating factor
5. Systemic effects: Vasodilation, increased vascular permeability, bronchoconstriction, mucus secretion

NON-IMMUNOLOGIC (ANAPHYLACTOID) REACTIONS:
• Direct mast cell activation without IgE (e.g., radiocontrast, opioids, vancomycin)
• Clinically indistinguishable from IgE-mediated anaphylaxis
• Treated the same way

KEY MEDIATORS AND EFFECTS:
• HISTAMINE: Vasodilation, increased permeability, bronchoconstriction, itching
• LEUKOTRIENES (C4, D4, E4): Potent bronchoconstrictors, mucus production
• PROSTAGLANDIN D2: Vasodilation, bronchoconstriction
• PLATELET-ACTIVATING FACTOR: Hypotension, increased permeability
• TRYPTASE: Released from mast cells; elevated levels confirm mast cell activation

CARDIOVASCULAR COLLAPSE:
• Profound vasodilation → Distributive shock
• Increased vascular permeability → Intravascular volume loss (up to 35% in 10 minutes)
• Direct cardiac depression (mediator effects)
• Coronary vasospasm (Kounis syndrome)

AIRWAY COMPROMISE:
• Upper airway: Laryngeal edema, tongue swelling
• Lower airway: Bronchospasm, mucus plugging
• Can occur independently or together`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Lifetime prevalence: 0.5-2% of general population
• Incidence increasing worldwide, especially in developed countries
• Hospitalization rates: 1-30 per 100,000 person-years
• Mortality: 0.5-1 per million population per year
• Case fatality rate: 0.5-1% of anaphylaxis episodes
• Death usually occurs within 30-60 minutes of onset

TRIGGERS BY SETTING:
• Community: Foods (most common in children), insect stings
• Hospital: Medications (antibiotics, NSAIDs, anesthetics), blood products, contrast media
• Perioperative: Neuromuscular blocking agents, antibiotics, latex

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• LIMITED epidemiological data from Africa
• Likely UNDERDIAGNOSED and UNDERREPORTED
• May be misdiagnosed as asthma, "fainting," or unknown cause of death

COMMON TRIGGERS IN AFRICA:
• INSECT STINGS: Bee stings (Apis mellifera), wasp stings very common cause
• MEDICATIONS: 
  - Antibiotics (penicillins, sulfonamides) - often given without allergy history
  - NSAIDs (widely available over-the-counter)
  - Traditional/herbal medicines (unknown allergens)
  - Antimalarials (rare but reported)
• FOODS:
  - Peanuts and groundnuts (staple in many African diets)
  - Fish and shellfish
  - Cassava (rare)
  - Local foods may vary by region
• SNAKE ANTIVENOM: Anaphylaxis to antivenom is significant concern in snakebite treatment
• LATEX: Healthcare workers, particularly with limited non-latex glove access

CHALLENGES IN AFRICA:
• EPINEPHRINE AVAILABILITY: Auto-injectors (EpiPen) expensive and often unavailable; ampules more accessible but require syringe/needle
• COLD CHAIN: Epinephrine requires proper storage (protected from light and heat)
• RECOGNITION: May be mistaken for other conditions
• TRAINING: Healthcare workers may not be trained in anaphylaxis management
• DELAYED PRESENTATION: Patients may present late due to distance from healthcare
• MONITORING: ICU capacity limited for severe cases
• ALLERGY TESTING: Limited access to allergists and testing (skin prick tests, specific IgE)

SPECIFIC CONSIDERATIONS:
• HIV patients may have altered immune responses
• High burden of asthma (anaphylaxis risk factor)
• Food allergy prevalence may differ from Western countries
• Traditional medicine use complicates allergen identification

═══════════════════════════════════════════════════════════════════════════════
RISK FACTORS FOR SEVERE ANAPHYLAXIS
═══════════════════════════════════════════════════════════════════════════════

• Previous severe anaphylaxis
• Asthma (especially poorly controlled)
• Cardiovascular disease
• Mast cell disorders (mastocytosis)
• Beta-blocker use (may be resistant to epinephrine)
• ACE inhibitor use (may worsen angioedema)
• Older age
• Delayed epinephrine administration
• Upright posture during reaction (risk of "empty ventricle syndrome")`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA (World Allergy Organization)
═══════════════════════════════════════════════════════════════════════════════

Anaphylaxis is HIGHLY LIKELY when ONE of the following criteria is met:

CRITERION 1:
Acute onset (minutes to hours) of illness with involvement of SKIN/MUCOSAL tissue (hives, pruritus, flushing, swollen lips/tongue/uvula)
AND AT LEAST ONE OF:
• Respiratory compromise (dyspnea, wheeze, stridor, hypoxemia)
• Hypotension or end-organ dysfunction (syncope, incontinence, hypotonia)

CRITERION 2:
TWO OR MORE of the following occurring rapidly after exposure to a LIKELY allergen:
• Skin/mucosal involvement
• Respiratory compromise
• Hypotension or end-organ dysfunction
• Persistent GI symptoms (crampy abdominal pain, vomiting)

CRITERION 3:
Hypotension after exposure to a KNOWN allergen for that patient:
• Adults: SBP <90 mmHg or >30% decrease from baseline
• Children: Age-specific low SBP or >30% decrease

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS BY ORGAN SYSTEM
═══════════════════════════════════════════════════════════════════════════════

SKIN AND MUCOSA (80-90% of cases):
• Urticaria (hives) - raised, itchy wheals
• Flushing (erythema)
• Pruritus (itching) - may be generalized
• Angioedema - swelling of lips, tongue, face, throat
• Periorbital edema
• Note: 10-20% of anaphylaxis has NO skin findings

RESPIRATORY (70%):
• Dyspnea, shortness of breath
• Wheeze
• Stridor (upper airway obstruction - ominous sign)
• Throat tightness, "lump in throat"
• Hoarseness, voice change
• Cough
• Nasal congestion, rhinorrhea, sneezing

CARDIOVASCULAR (45%):
• Dizziness, lightheadedness
• Syncope or presyncope
• Hypotension
• Tachycardia (may be bradycardia in severe cases)
• Chest pain (may indicate Kounis syndrome - allergic MI)
• Cardiac arrest

GASTROINTESTINAL (45%):
• Nausea and vomiting
• Abdominal cramping/pain
• Diarrhea
• Dysphagia

NEUROLOGICAL:
• Anxiety, sense of impending doom
• Confusion
• Altered level of consciousness
• Headache
• Seizures (rare, usually from hypoxia)

TIMING OF ONSET:
• Usually within MINUTES of exposure (5-30 minutes)
• Can be delayed up to several hours (especially food triggers)
• Faster onset generally indicates more severe reaction

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia (most common) - HR >100
• Hypotension - SBP <90 mmHg or >30% drop
• Tachypnea
• Hypoxemia (SpO2 <92%)
• Bradycardia (can occur in severe cases - poor prognosis)

SKIN EXAMINATION:
• Urticaria - raised, erythematous, pruritic wheals
• Angioedema - non-pitting edema of lips, eyelids, face, hands
• Flushing - diffuse erythema
• Pallor (with shock)
• Cyanosis (severe hypoxemia)

AIRWAY EXAMINATION:
• Stridor (inspiratory - upper airway obstruction)
• Tongue swelling
• Uvula edema
• Voice changes (hoarse, "hot potato" voice)
• Drooling (inability to swallow secretions)

RESPIRATORY EXAMINATION:
• Wheeze (bronchospasm)
• Decreased air entry
• Use of accessory muscles
• Tripod positioning

CARDIOVASCULAR:
• Weak/thready pulse
• Delayed capillary refill
• Cool, clammy extremities (shock)
• Altered mental status`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSIS IS CLINICAL
═══════════════════════════════════════════════════════════════════════════════

Anaphylaxis is a CLINICAL DIAGNOSIS based on:
• Rapid onset of symptoms (usually minutes)
• Multi-system involvement (especially skin + respiratory or cardiovascular)
• Exposure to potential allergen (may be unknown)

DO NOT delay treatment waiting for laboratory confirmation!

═══════════════════════════════════════════════════════════════════════════════
LABORATORY TESTING
═══════════════════════════════════════════════════════════════════════════════

SERUM TRYPTASE:
• Most useful laboratory test for confirming anaphylaxis
• Released from mast cells during degranulation
• Draw within 15 min to 3 hours of symptom onset (peaks at 1-2 hours)
• Elevated tryptase supports diagnosis (especially if baseline available)
• Normal tryptase does NOT exclude anaphylaxis (especially food-induced)
• Useful for: Confirming diagnosis, identifying mast cell disorders, medicolegal purposes

HISTAMINE:
• Peaks within 5-15 minutes, returns to baseline by 60 minutes
• Difficult to obtain in clinical practice due to timing
• Not routinely recommended

OTHER TESTS (supportive):
• ABG: May show hypoxemia, metabolic acidosis in severe cases
• ECG: Tachycardia, ST changes (Kounis syndrome), arrhythmias
• Lactate: Elevated in shock
• CBC: May show hemoconcentration (increased Hct) from plasma extravasation

═══════════════════════════════════════════════════════════════════════════════
POST-EPISODE WORKUP (Outpatient)
═══════════════════════════════════════════════════════════════════════════════

ALLERGY TESTING (4-6 weeks after episode):
• Skin prick testing: Gold standard for IgE-mediated allergies
• Specific IgE blood tests: When skin testing not possible
• Component-resolved diagnostics: Identify specific allergenic proteins

REFERRAL TO ALLERGIST:
• Identify causative allergen
• Confirm diagnosis
• Provide written emergency action plan
• Prescribe epinephrine auto-injector
• Consider allergen immunotherapy if appropriate

BASELINE TRYPTASE:
• To identify underlying mast cell disorder if elevated during episode`,

    differentialDiagnosis: [
      'Vasovagal syncope - Bradycardia (not tachycardia), pallor, diaphoresis; no urticaria or angioedema; rapid recovery with supine position',
      'Acute asthma exacerbation - Wheeze and dyspnea WITHOUT hypotension, urticaria, or angioedema; history of asthma',
      'Panic attack/Anxiety - Hyperventilation, paresthesias, sense of doom WITHOUT objective findings; no urticaria or true hypotension',
      'Acute urticaria (without anaphylaxis) - Hives only, no respiratory or cardiovascular compromise',
      'Angioedema (hereditary or ACE-inhibitor induced) - Swelling WITHOUT urticaria or hypotension; ACE-I angioedema does not respond to epinephrine',
      'Carcinoid syndrome - Flushing, diarrhea, wheezing; usually chronic; 5-HIAA elevated',
      'Mastocytosis/Mast cell activation syndrome - Recurrent anaphylaxis-like episodes; elevated baseline tryptase',
      'Scombroid fish poisoning - Histamine toxicity from spoiled fish; flushing, urticaria, headache; multiple people affected',
      'Septic shock - Fever, infection source, gradual onset; no urticaria',
      'Vocal cord dysfunction - Stridor and dyspnea; no urticaria or hypotension; diagnosis by laryngoscopy',
      'Foreign body aspiration - Acute onset dyspnea/stridor; history of choking; no urticaria',
      'Epiglottitis - Stridor, drooling, fever; no urticaria; thumbprint sign on lateral neck X-ray'
    ],

    investigations: [
      {
        name: 'Serum Tryptase',
        indication: 'Confirmatory test - Draw within 15 min to 3 hours of onset; elevated level supports mast cell activation; compare to baseline',
        availability: 'ADVANCED'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 IMMEDIATE - Assess oxygenation; hypoxemia indicates severe reaction requiring aggressive airway management',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: 'Rule out hypoglycemia as cause of altered mental status; also monitor in diabetics receiving epinephrine',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: 'Chest pain or cardiovascular symptoms - Rule out Kounis syndrome (allergic MI), arrhythmias; baseline before epinephrine in cardiac patients',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Severe respiratory distress or shock - Assess oxygenation, ventilation, acid-base status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: 'May show hemoconcentration (elevated Hct) from plasma extravasation; baseline for monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: 'Assess renal function, electrolytes; potassium may decrease with epinephrine',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If respiratory symptoms persist - Rule out pulmonary edema, pneumothorax, aspiration',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Shock assessment - Elevated lactate indicates tissue hypoperfusion',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Skin Prick Testing',
        indication: 'OUTPATIENT (4-6 weeks post-episode) - Identify causative allergen; performed by allergist',
        availability: 'ADVANCED'
      },
      {
        name: 'Specific IgE Blood Tests',
        indication: 'OUTPATIENT - Alternative to skin testing; identify specific allergen sensitivities',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CALL FOR HELP: Activate emergency response; anaphylaxis requires team approach',
        '⚡ REMOVE TRIGGER: Stop IV medication, remove stinger (scrape, do not squeeze), remove patient from allergen source',
        '⚡ POSITION PATIENT: Lay FLAT with legs elevated; if dyspneic, allow to sit up; if pregnant, left lateral; if vomiting, recovery position; do NOT stand or sit patient up suddenly',
        '⚡ EPINEPHRINE IM - GIVE IMMEDIATELY: 0.3-0.5 mg (0.3-0.5 mL of 1:1000) IM into ANTEROLATERAL THIGH; Pediatric: 0.01 mg/kg (max 0.5 mg)',
        '⚡ REPEAT EPINEPHRINE: Every 5-15 minutes if no improvement; most patients need only 1-2 doses',
        '⚡ HIGH-FLOW OXYGEN: 15 L/min via non-rebreather mask; maintain SpO2 >94%',
        '⚡ IV ACCESS: Two large-bore IVs; prepare for aggressive fluid resuscitation',
        '⚡ IV FLUIDS: Normal saline 1-2 L rapid bolus for hypotension (adults); 20 mL/kg bolus for children; repeat as needed',
        '⚡ MONITOR: Continuous pulse oximetry, cardiac monitor, BP every 2-5 minutes',
        '⚡ PREPARE FOR AIRWAY MANAGEMENT: Have intubation equipment ready; early intubation if progressive airway edema',
        '⚡ ADJUNCTIVE MEDICATIONS: Give AFTER epinephrine - Antihistamines (H1 + H2 blockers), corticosteroids, bronchodilators if wheezing'
      ],
      pharmacological: [
        {
          drug: 'Epinephrine (Adrenaline) IM',
          indication: '🔴 FIRST-LINE - Give IMMEDIATELY; there are NO absolute contraindications in anaphylaxis',
          dose: 'Adults: 0.3-0.5 mg (0.3-0.5 mL of 1:1000 = 1 mg/mL) IM; Children: 0.01 mg/kg (max 0.5 mg); Auto-injector: EpiPen 0.3 mg (adults) or 0.15 mg (children <30 kg)',
          route: 'IM into ANTEROLATERAL THIGH (vastus lateralis) - fastest absorption; can give through clothing',
          duration: 'Repeat every 5-15 minutes if symptoms persist; most need 1-2 doses; if >2 doses needed, start IV epinephrine',
          contraindications: ['NO ABSOLUTE CONTRAINDICATIONS in anaphylaxis - benefits always outweigh risks'],
          sideEffects: ['Tachycardia', 'Palpitations', 'Tremor', 'Anxiety', 'Headache', 'Hypertension (transient)'],
          alternatives: ['If IM not working, consider IV epinephrine']
        },
        {
          drug: 'Epinephrine IV Infusion',
          indication: '🔴 REFRACTORY ANAPHYLAXIS - When repeated IM doses ineffective; for severe/refractory hypotension',
          dose: 'Infusion: 1-10 mcg/min (0.1-1 mcg/kg/min in children); titrate to BP; Bolus (cardiac arrest): 1 mg IV (1:10,000) per ACLS protocol',
          route: 'IV continuous infusion via infusion pump; central line preferred but peripheral acceptable in emergency',
          duration: 'Continue until hemodynamically stable; may wean slowly',
          contraindications: ['Relative: Severe coronary disease, arrhythmias - but still give if anaphylaxis'],
          sideEffects: ['Arrhythmias', 'Myocardial ischemia', 'Severe hypertension if too rapid'],
          alternatives: ['Norepinephrine', 'Vasopressin']
        },
        {
          drug: 'Diphenhydramine (Benadryl) - H1 Antihistamine',
          indication: 'ADJUNCTIVE - Relieves urticaria and pruritus; does NOT treat airway obstruction or shock; give AFTER epinephrine',
          dose: 'Adults: 25-50 mg IV/IM/PO; Children: 1-1.25 mg/kg (max 50 mg)',
          route: 'IV (faster onset), IM, or PO',
          duration: 'May repeat every 6 hours for 24-48 hours',
          contraindications: ['Caution in elderly (anticholinergic effects)', 'Narrow-angle glaucoma'],
          sideEffects: ['Sedation', 'Dry mouth', 'Urinary retention', 'Confusion in elderly'],
          alternatives: ['Cetirizine 10 mg PO (less sedating)', 'Chlorpheniramine']
        },
        {
          drug: 'Ranitidine or Famotidine - H2 Antihistamine',
          indication: 'ADJUNCTIVE - Combined H1 + H2 blockade may be more effective than H1 alone; give AFTER epinephrine',
          dose: 'Ranitidine 50 mg IV OR Famotidine 20 mg IV; Children: Ranitidine 1 mg/kg (max 50 mg)',
          route: 'IV or PO',
          duration: 'Single dose; may continue for 24-48 hours',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Headache', 'Dizziness', 'Rare: Cardiac arrhythmias with rapid IV ranitidine'],
          alternatives: ['Famotidine preferred over ranitidine']
        },
        {
          drug: 'Methylprednisolone (or equivalent corticosteroid)',
          indication: 'ADJUNCTIVE - May help prevent biphasic reaction (evidence weak); does NOT treat acute symptoms; give AFTER epinephrine',
          dose: 'Methylprednisolone 125 mg IV OR Hydrocortisone 200 mg IV OR Dexamethasone 8-10 mg IV; Children: Methylprednisolone 1-2 mg/kg',
          route: 'IV (preferred in acute setting) or PO',
          duration: 'Usually single dose; may continue prednisone 40-60 mg daily for 3-5 days',
          contraindications: ['Active untreated infection (relative)', 'No contraindications in true anaphylaxis'],
          sideEffects: ['Hyperglycemia', 'Hypertension', 'Agitation'],
          alternatives: ['Prednisone 50 mg PO', 'Dexamethasone 8 mg PO']
        },
        {
          drug: 'Albuterol (Salbutamol) Nebulized',
          indication: 'ADJUNCTIVE - For bronchospasm/wheezing that persists after epinephrine',
          dose: 'Adults: 2.5-5 mg nebulized; Children: 2.5 mg nebulized; may repeat every 20 minutes',
          route: 'Nebulized or MDI with spacer (4-8 puffs)',
          duration: 'Continue until wheeze resolves',
          contraindications: ['None in emergency'],
          sideEffects: ['Tachycardia', 'Tremor', 'Hypokalemia'],
          alternatives: ['Ipratropium can be added for severe bronchospasm']
        },
        {
          drug: 'Glucagon',
          indication: '🔴 For patients on BETA-BLOCKERS who do not respond to epinephrine - bypasses beta-receptors',
          dose: '1-5 mg IV/IM; may repeat in 5 minutes; Infusion: 5-15 mcg/min',
          route: 'IV or IM',
          duration: 'Repeat as needed until response to epinephrine',
          contraindications: ['Pheochromocytoma', 'Insulinoma'],
          sideEffects: ['Nausea', 'Vomiting', 'Hyperglycemia'],
          alternatives: ['Vasopressin']
        },
        {
          drug: 'Normal Saline (0.9% NaCl)',
          indication: '🔴 Volume resuscitation for hypotension - Anaphylaxis causes massive fluid shifts',
          dose: 'Adults: 1-2 L rapid bolus; may need 5-10 L total; Children: 20 mL/kg bolus, repeat as needed',
          route: 'IV rapid infusion',
          duration: 'Continue until normotensive; monitor for fluid overload',
          contraindications: ['Heart failure (use cautiously, still give if hypotensive)'],
          sideEffects: ['Volume overload', 'Pulmonary edema'],
          alternatives: ['Lactated Ringer solution']
        },
        {
          drug: 'Norepinephrine',
          indication: 'Refractory hypotension despite epinephrine and fluids',
          dose: 'Start 0.1-0.5 mcg/kg/min; titrate to MAP ≥65 mmHg',
          route: 'IV continuous infusion',
          duration: 'Until hemodynamically stable',
          contraindications: ['None in shock state'],
          sideEffects: ['Arrhythmias', 'Peripheral ischemia'],
          alternatives: ['Vasopressin 0.01-0.04 units/min']
        },
        {
          drug: 'Vasopressin',
          indication: 'Adjunctive vasopressor for refractory hypotension; may be useful in beta-blocker patients',
          dose: '0.01-0.04 units/min IV infusion',
          route: 'IV continuous infusion',
          duration: 'Until hemodynamically stable',
          contraindications: ['Coronary artery disease (relative)'],
          sideEffects: ['Myocardial ischemia', 'Hyponatremia', 'Mesenteric ischemia'],
          alternatives: ['Norepinephrine']
        }
      ],
      nonPharmacological: [
        'POSITIONING: Supine with legs elevated (Trendelenburg) to improve venous return; allow sitting if respiratory distress; left lateral decubitus if pregnant; recovery position if vomiting/unconscious',
        'AIRWAY MANAGEMENT: Early intubation if laryngeal edema progressing (do not wait for complete obstruction); difficult airway anticipated - have surgical airway backup',
        'CRICOTHYROTOMY/TRACHEOSTOMY: If unable to intubate due to severe angioedema; life-saving in complete upper airway obstruction',
        'CARDIOPULMONARY RESUSCITATION: If cardiac arrest; prolonged CPR may be needed; give epinephrine per ACLS protocol',
        'REMOVE STINGER: Scrape off (do not squeeze) if bee sting - squeezing may inject more venom',
        'TOURNIQUET (NOT recommended): Outdated practice; do not apply tourniquet to delay allergen absorption',
        'OBSERVATION: Minimum 4-6 hours post-treatment; 24 hours for severe reactions or those at risk of biphasic reaction',
        'PATIENT EDUCATION: Allergen avoidance, epinephrine auto-injector use, when to seek emergency care'
      ],
      surgical: [
        'CRICOTHYROTOMY: Emergency surgical airway for "cannot intubate, cannot oxygenate" scenario due to severe upper airway angioedema',
        'TRACHEOSTOMY: May be required if prolonged airway compromise anticipated'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: Refractory hypotension; respiratory failure requiring intubation; cardiac arrest; need for vasopressor infusion',
        '🔴 IMMEDIATE ANESTHESIA/AIRWAY: Progressive upper airway edema not responding to epinephrine; anticipated difficult airway',
        '🟠 ALLERGIST/IMMUNOLOGIST (Outpatient): ALL patients with anaphylaxis should be referred for allergen identification, testing, education, and auto-injector prescription',
        '🟠 CARDIOLOGY: If Kounis syndrome suspected (allergic MI) or significant cardiac involvement',
        '🟠 OBSERVATION UNIT: Patients with resolved mild-moderate anaphylaxis for monitoring of biphasic reaction'
      ],
      monitoring: [
        'CONTINUOUS MONITORING: Pulse oximetry, cardiac rhythm, blood pressure every 2-5 minutes during acute phase',
        'AIRWAY ASSESSMENT: Repeated examination for signs of progressive airway edema; voice changes, stridor, drooling',
        'RESPONSE TO TREATMENT: Improvement in BP, HR, breathing, skin findings within 5-15 minutes of epinephrine',
        'FLUID BALANCE: Monitor for signs of fluid overload with aggressive resuscitation',
        'OBSERVATION PERIOD: Minimum 4-6 hours after last dose of epinephrine; 24 hours for severe reactions',
        'BIPHASIC REACTION WATCH: Recurrence of symptoms usually 8-10 hours after initial episode; can occur up to 72 hours'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
BEFORE DISCHARGE
═══════════════════════════════════════════════════════════════════════════════

• Observation period complete (minimum 4-6 hours; 24 hours for severe)
• Symptoms fully resolved
• Patient able to use epinephrine auto-injector
• Written anaphylaxis action plan provided
• Epinephrine auto-injector prescribed (2 devices)
• Follow-up with allergist arranged
• Patient understands when to return to ED

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE MEDICATIONS AND EDUCATION
═══════════════════════════════════════════════════════════════════════════════

PRESCRIBE:
• Epinephrine auto-injector (EpiPen, Auvi-Q, or generic) - prescribe 2
• Oral antihistamine (diphenhydramine or cetirizine) for 3-5 days
• Oral corticosteroid (prednisone 40-60 mg daily) for 3-5 days (optional, may help prevent biphasic)

EDUCATION:
• How to use epinephrine auto-injector (demonstrate and have patient demonstrate back)
• Carry 2 auto-injectors at all times
• Allergen avoidance strategies
• Read food labels carefully
• Inform restaurants, schools, workplaces
• Wear medical alert bracelet/necklace
• When to use epinephrine: ANY symptoms suggesting anaphylaxis - do not wait

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

ALLERGIST REFERRAL (within 4-6 weeks):
• Confirm diagnosis
• Identify causative allergen (skin testing, specific IgE)
• Develop comprehensive avoidance plan
• Consider allergen immunotherapy (venom immunotherapy highly effective for insect stings)
• Review and update emergency action plan
• Refill epinephrine prescription

PRIMARY CARE:
• Review episode and treatment
• Reinforce allergen avoidance
• Update medication list to reflect allergy
• Annual epinephrine auto-injector renewal
• Address psychological impact (anxiety common after anaphylaxis)`
    },

    redFlags: [
      '🔴 CRITICAL: Stridor or voice changes - Laryngeal edema; impending airway obstruction; prepare for difficult airway',
      '🔴 CRITICAL: Hypotension (SBP <90 or >30% drop) - Anaphylactic shock; aggressive epinephrine and fluids required',
      '🔴 CRITICAL: Respiratory failure (SpO2 <90% despite O2) - May need intubation; severe bronchospasm or airway edema',
      '🔴 CRITICAL: Altered mental status - Sign of cerebral hypoperfusion or hypoxia; severe anaphylaxis',
      '🔴 CRITICAL: No response to 2 doses of IM epinephrine - Start IV epinephrine infusion; consider other vasopressors',
      '🔴 CRITICAL: Cardiac arrest - Prolonged CPR often needed; high-dose epinephrine; consider ECMO if available',
      '🟠 URGENT: Rapid progression of symptoms - Indicates severe reaction; early aggressive treatment critical',
      '🟠 URGENT: History of severe/near-fatal anaphylaxis - Higher risk of severe reaction',
      '🟠 URGENT: Patient on beta-blockers - May be resistant to epinephrine; give glucagon',
      '🟠 URGENT: Poorly controlled asthma - Risk factor for fatal anaphylaxis; aggressive bronchodilator therapy',
      '🟠 URGENT: Biphasic reaction - Symptom recurrence after initial resolution; resume treatment'
    ],

    complications: [
      'DEATH: 0.5-1% of anaphylaxis cases; usually from airway obstruction or cardiovascular collapse; most deaths occur within 30-60 minutes',
      'ANOXIC BRAIN INJURY: From prolonged hypoxia or cardiac arrest; irreversible',
      'MYOCARDIAL INFARCTION (Kounis syndrome): Allergic acute coronary syndrome; mast cell mediators cause coronary vasospasm or plaque rupture',
      'CARDIAC ARRHYTHMIAS: From mediator effects, hypoxia, or epinephrine; monitor on telemetry',
      'ASPIRATION PNEUMONIA: From vomiting with impaired consciousness; position in recovery if vomiting',
      'BIPHASIC REACTION: Recurrence of symptoms 1-72 hours after initial reaction (usually 8-10 hours); occurs in 5-20%; requires re-treatment',
      'PROTRACTED ANAPHYLAXIS: Symptoms lasting hours to days despite treatment; requires ICU admission',
      'ACUTE RESPIRATORY DISTRESS SYNDROME (ARDS): Rare complication of severe anaphylaxis',
      'DISSEMINATED INTRAVASCULAR COAGULATION (DIC): Rare in severe prolonged anaphylaxis'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

• Overall case fatality rate: 0.5-1%
• Death usually occurs within 30-60 minutes of allergen exposure
• Causes of death:
  - Airway obstruction (asphyxiation): Most common in food-induced
  - Cardiovascular collapse: Most common in medication/venom-induced
  - Both mechanisms often contribute

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FACTORS ASSOCIATED WITH FATAL OUTCOME:
• Delayed epinephrine administration (most important modifiable factor)
• Adolescent/young adult age (risk-taking behavior, delayed treatment)
• History of asthma (especially poorly controlled)
• Previous severe anaphylaxis
• Peanut or tree nut allergy (higher fatality rates)
• Upright posture during reaction
• Beta-blocker use
• Absence of skin symptoms (may delay recognition)

FAVORABLE PROGNOSIS:
• Early epinephrine administration
• Mild-moderate severity
• Good response to initial treatment
• No underlying cardiopulmonary disease
• Identifiable and avoidable trigger

═══════════════════════════════════════════════════════════════════════════════
RECURRENCE
═══════════════════════════════════════════════════════════════════════════════

• Risk of recurrent anaphylaxis if allergen not identified or avoided
• Venom immunotherapy: >95% effective in preventing future insect sting anaphylaxis
• Food allergies: Strict avoidance required; oral immunotherapy emerging for some foods
• Drug allergies: Avoidance and documentation; desensitization possible for some medications

QUALITY OF LIFE:
• Anxiety and fear of recurrence common
• Impact on daily activities, social situations, travel
• Psychological support may be needed
• Education and preparedness reduce anxiety`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

MEDICATION ALLERGIES:
• Always ask about allergies before prescribing
• Document allergies prominently in medical records
• Use alternative medications in allergic patients
• Pre-medication protocols for necessary high-risk procedures (e.g., contrast media)

FOOD ALLERGIES:
• Early introduction of allergenic foods (peanuts) may prevent development of allergies (LEAP study)
• Breastfeeding recommendations remain unchanged

INSECT STINGS:
• Avoid areas with high insect activity
• Do not wear bright colors or fragrances outdoors
• Keep food covered at outdoor events
• Wear shoes outdoors

LATEX ALLERGY:
• Use non-latex gloves and equipment in sensitized patients
• Healthcare workers with latex allergy need workplace accommodations

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After Anaphylaxis)
═══════════════════════════════════════════════════════════════════════════════

ALLERGEN AVOIDANCE:
• Identify and strictly avoid triggering allergen
• Read food labels carefully (for food allergies)
• Inform restaurants, schools, workplaces
• Be aware of cross-contamination and hidden allergens
• Wear medical alert identification

EPINEPHRINE AUTO-INJECTOR:
• Prescribe at least 2 devices
• Carry at all times
• Replace before expiration
• Teach proper use to patient, family, caregivers, teachers
• Use immediately at first sign of anaphylaxis - do NOT wait to see if symptoms progress

ANAPHYLAXIS ACTION PLAN:
• Written instructions for recognizing and treating anaphylaxis
• Provided to patient, family, school, workplace
• Review and update annually

ALLERGEN IMMUNOTHERAPY:
• VENOM IMMUNOTHERAPY: Highly effective (>95%) for preventing insect sting anaphylaxis
• DRUG DESENSITIZATION: For necessary medications (e.g., penicillin in syphilis, aspirin in cardiac disease)
• FOOD ORAL IMMUNOTHERAPY: Emerging treatment for peanut and other food allergies

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT - PREVENTION CHALLENGES
═══════════════════════════════════════════════════════════════════════════════

• EPINEPHRINE ACCESS: Auto-injectors expensive; ampules more available but require training
• EDUCATION: Community and healthcare worker education on anaphylaxis recognition
• ALLERGY TESTING: Limited access; empiric avoidance of suspected allergens
• IMMUNOTHERAPY: Venom immunotherapy may not be available
• MEDICAL ALERT: Bracelets/necklaces may not be available; written card alternative
• TRADITIONAL MEDICINES: Unknown allergens; counsel patients on risks`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS ANAPHYLAXIS?
═══════════════════════════════════════════════════════════════════════════════

Anaphylaxis is a severe allergic reaction that affects your whole body. It happens when your immune system overreacts to something you are allergic to (like a food, medication, or insect sting). It can be life-threatening if not treated immediately.

═══════════════════════════════════════════════════════════════════════════════
RECOGNIZE THE SIGNS
═══════════════════════════════════════════════════════════════════════════════

Anaphylaxis can start within MINUTES of exposure. Signs include:

SKIN:
• Hives (itchy, raised bumps)
• Flushing (red skin)
• Swelling of lips, tongue, face, throat
• Itching all over

BREATHING:
• Shortness of breath
• Wheezing
• Tight throat, difficulty swallowing
• Hoarse voice
• Coughing

HEART AND CIRCULATION:
• Dizziness or fainting
• Rapid or weak pulse
• Pale or blue skin

STOMACH:
• Nausea or vomiting
• Stomach cramps
• Diarrhea

OTHER:
• Feeling of doom
• Confusion
• Loss of consciousness

⚠️ You do NOT need to have all symptoms. Even ONE OR TWO severe symptoms are enough to use epinephrine!

═══════════════════════════════════════════════════════════════════════════════
WHAT TO DO - EMERGENCY ACTION
═══════════════════════════════════════════════════════════════════════════════

1. USE YOUR EPINEPHRINE AUTO-INJECTOR IMMEDIATELY
   • Do NOT wait to see if symptoms get worse
   • Inject into OUTER THIGH (can go through clothing)
   • Hold for 10 seconds
   • Note the time

2. CALL FOR EMERGENCY HELP
   • Call emergency services immediately after using epinephrine
   • Even if you feel better, you need medical evaluation

3. LIE DOWN with legs raised (unless you are having trouble breathing - then sit up)

4. USE A SECOND EPINEPHRINE DOSE if symptoms do not improve in 5-15 minutes

5. Go to the EMERGENCY ROOM even if symptoms improve - reactions can come back

═══════════════════════════════════════════════════════════════════════════════
HOW TO USE YOUR EPINEPHRINE AUTO-INJECTOR
═══════════════════════════════════════════════════════════════════════════════

EPIPEN:
1. Remove from carrier tube
2. Pull off blue safety cap
3. Hold orange tip against OUTER THIGH
4. Press firmly until you hear a click
5. Hold for 10 seconds
6. Remove and massage injection site

• Can inject through clothing
• Practice with trainer device
• Check expiration date monthly
• Replace before it expires
• Keep at room temperature (not in hot car or refrigerator)

═══════════════════════════════════════════════════════════════════════════════
PREVENTING FUTURE REACTIONS
═══════════════════════════════════════════════════════════════════════════════

AVOID YOUR ALLERGEN:
• Know exactly what you are allergic to
• Read ALL food labels if food allergic
• Tell restaurants about your allergy
• Ask about ingredients before eating

ALWAYS BE PREPARED:
• Carry 2 epinephrine auto-injectors at ALL times
• Wear a medical alert bracelet or necklace
• Tell family, friends, teachers, coworkers about your allergy
• Teach them how to use your epinephrine

AT RESTAURANTS:
• Tell the server AND manager about your allergy
• Ask how food is prepared
• Avoid buffets (cross-contamination risk)
• When in doubt, don't eat it

TRAVELING:
• Carry epinephrine in your carry-on (not checked luggage)
• Bring doctor's letter explaining your medications
• Research emergency services at your destination
• Know how to say "I am allergic to ___" in local language

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT REMINDERS
═══════════════════════════════════════════════════════════════════════════════

✓ ALWAYS carry your epinephrine - it can save your life
✓ Use epinephrine FIRST - do not try antihistamines instead
✓ When in doubt, USE IT - epinephrine is safe and side effects are temporary
✓ ALWAYS go to the emergency room after using epinephrine
✓ See an allergist for testing and a complete management plan
✓ Replace your epinephrine before it expires
✓ Tell everyone who needs to know about your allergy

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS FOR YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What exactly am I allergic to?
• Can I be tested to confirm my allergy?
• Are there other things I might also be allergic to?
• How do I get epinephrine auto-injectors?
• Should I see an allergist?
• What if I accidentally eat/contact my allergen?
• Are there treatments that could reduce my allergy?`,

    references: [
      'Cardona V, et al. World Allergy Organization Anaphylaxis Guidance 2020. World Allergy Organ J. 2020;13(10):100472. doi:10.1016/j.waojou.2020.100472',
      'Simons FER, et al. World Allergy Organization Guidelines for the Assessment and Management of Anaphylaxis. World Allergy Organ J. 2011;4(2):13-37. doi:10.1097/WOX.0b013e318211496c',
      'Lieberman P, et al. Anaphylaxis—a practice parameter update 2015. Ann Allergy Asthma Immunol. 2015;115(5):341-84. doi:10.1016/j.anai.2015.07.019',
      'Muraro A, et al. EAACI guidelines: Anaphylaxis (2021 update). Allergy. 2022;77(2):357-377. doi:10.1111/all.15032',
      'Turner PJ, et al. Fatal Anaphylaxis: Mortality Rate and Risk Factors. J Allergy Clin Immunol Pract. 2017;5(5):1169-1178. doi:10.1016/j.jaip.2017.06.031',
      'Sampson HA, et al. Second symposium on the definition and management of anaphylaxis. J Allergy Clin Immunol. 2006;117(2):391-7.',
      'Shaker MS, et al. Anaphylaxis—a 2020 practice parameter update, systematic review, and Grading of Recommendations. J Allergy Clin Immunol. 2020;145(4):1082-1123.',
      'Pumphrey RS. Lessons for management of anaphylaxis from a study of fatal reactions. Clin Exp Allergy. 2000;30(8):1144-50.',
      'Du Toit G, et al. Randomized Trial of Peanut Consumption in Infants at Risk for Peanut Allergy (LEAP). N Engl J Med. 2015;372(9):803-13. doi:10.1056/NEJMoa1414850'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 17. ACUTE GI BLEEDING
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '17',
    name: 'Acute GI Bleeding',
    slug: 'acute-gi-bleeding',
    specialty: 'Gastroenterology',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K92.2',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute gastrointestinal (GI) bleeding is a common medical emergency characterized by blood loss from the gastrointestinal tract. It is classified as upper GI bleeding (UGIB) if the source is proximal to the ligament of Treitz, or lower GI bleeding (LGIB) if distal. UGIB accounts for 80% of cases and has higher mortality. Prompt resuscitation, risk stratification, and appropriate endoscopic intervention are essential.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• RESUSCITATION FIRST: ABCs, two large-bore IVs, crystalloid resuscitation; blood transfusion if hemodynamically unstable or Hgb <7-8 g/dL

• UPPER vs LOWER GI BLEEDING:
  - UGIB: Hematemesis (vomiting blood), coffee-ground emesis, melena (black tarry stool)
  - LGIB: Hematochezia (bright red blood per rectum), maroon stool
  - Note: Brisk UGIB can present as hematochezia

• MOST COMMON CAUSES:
  - UGIB: Peptic ulcer disease (35-50%), esophageal/gastric varices (10-20%), erosive disease, Mallory-Weiss tear
  - LGIB: Diverticulosis (30-40%), angiodysplasia, hemorrhoids, colorectal neoplasia

• RISK STRATIFY using validated scores:
  - Glasgow-Blatchford Score (GBS): Pre-endoscopy; GBS 0-1 may be safely discharged
  - Rockall Score: Post-endoscopy; predicts rebleeding and mortality
  - Oakland Score: For lower GI bleeding

• RESTRICTIVE TRANSFUSION STRATEGY: Target Hgb 7-8 g/dL in stable patients; threshold 8-9 g/dL in acute coronary syndrome or significant cardiovascular disease

• TIMING OF ENDOSCOPY:
  - UGIB: Within 24 hours for most; within 12 hours for high-risk features; immediate if variceal bleeding suspected
  - LGIB: Within 24 hours for most; urgent colonoscopy if hemodynamically stable

• PPI THERAPY for suspected UGIB: High-dose IV PPI (80 mg bolus then 8 mg/hr infusion) before endoscopy; reduces need for intervention but does NOT improve mortality pre-endoscopy

• VARICEAL BLEEDING: IV octreotide/terlipressin + antibiotics (ceftriaxone) + early endoscopic band ligation; balloon tamponade (Sengstaken-Blakemore) as bridge to definitive therapy

• HOLD ANTICOAGULANTS AND ANTIPLATELETS initially; reverse if life-threatening bleeding; resume based on indication and bleeding source control

• NG TUBE/LAVAGE: No longer routinely recommended; does not improve outcomes and can be uncomfortable; may consider if unsure about UGIB vs LGIB

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

UPPER GI BLEEDING SOURCES:

PEPTIC ULCER DISEASE (35-50%):
• H. pylori infection or NSAID use most common causes
• Ulcer erodes into artery (especially gastroduodenal artery in posterior duodenal ulcer)
• Forrest classification predicts rebleeding risk

ESOPHAGEAL/GASTRIC VARICES (10-20%):
• Portal hypertension from cirrhosis causes portosystemic shunting
• Thin-walled varices develop in esophagus and stomach
• Rupture causes massive, life-threatening hemorrhage
• High mortality (15-20% per episode)

EROSIVE DISEASE (Gastritis/Esophagitis):
• Stress ulcers in critically ill patients
• NSAID-induced erosions
• Alcohol-related gastritis
• Reflux esophagitis

MALLORY-WEISS TEAR:
• Mucosal tear at gastroesophageal junction
• Often preceded by vomiting/retching
• Usually self-limited

LOWER GI BLEEDING SOURCES:

DIVERTICULOSIS (30-40%):
• Arterial bleeding from vasa recta at diverticular dome
• Usually painless, large volume
• Often stops spontaneously; 25% recurrence

ANGIODYSPLASIA:
• Degenerative vascular ectasias
• Associated with aortic stenosis, renal failure
• Right colon most common location

HEMORRHOIDS:
• Most common cause of minor rectal bleeding
• Bright red blood on toilet paper or coating stool
• Usually not hemodynamically significant

COLORECTAL NEOPLASIA:
• Polyps and cancers can bleed
• Usually chronic, occult blood loss
• Acute significant bleeding less common

LOWER GI BLEEDING MECHANISMS:
• Arterial bleeding (diverticula, angiodysplasia)
• Mucosal inflammation (colitis, proctitis)
• Mechanical trauma (hemorrhoids)
• Neoplastic erosion`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

UPPER GI BLEEDING:
• Incidence: 50-150 per 100,000 person-years
• Accounts for 300,000+ hospitalizations annually in US
• Mortality: 2-10% overall; up to 20% in variceal bleeding
• Mortality has decreased with PPI therapy and endoscopic techniques

LOWER GI BLEEDING:
• Incidence: 20-30 per 100,000 person-years
• Increases with age (200-fold higher in 80s vs 20s)
• Mortality: 2-4% (lower than UGIB)
• 80% stops spontaneously

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• LIMITED population-based data from Africa
• Hospital-based studies suggest different etiological patterns

UGIB CAUSES IN AFRICA:
• PEPTIC ULCER DISEASE: Leading cause; high H. pylori prevalence (70-90% in some regions)
• VARICEAL BLEEDING: Significant due to:
  - Hepatitis B and C related cirrhosis (high prevalence)
  - Schistosomiasis-related portal hypertension (periportal fibrosis)
  - Alcohol-related liver disease
• EROSIVE GASTRITIS: NSAID use, alcohol, traditional medicines
• GASTRIC CANCER: May present with bleeding; higher incidence in some regions
• ESOPHAGEAL CANCER: High incidence in East Africa (esophageal cancer belt)

LGIB CAUSES IN AFRICA:
• HEMORRHOIDS: Very common
• INFECTIOUS COLITIS: Amoebic dysentery, shigellosis, typhoid
• COLORECTAL CANCER: Increasing with Westernization
• DIVERTICULOSIS: Less common than in Western populations but increasing

UNIQUE CHALLENGES:
• BLOOD BANK LIMITATIONS: Blood product availability may be limited
• ENDOSCOPY ACCESS: Limited to urban centers; may not have 24/7 availability
• ICU CAPACITY: Limited for massive bleeding requiring intensive monitoring
• H. PYLORI TREATMENT: Antimicrobial resistance patterns may differ
• DELAY IN PRESENTATION: Patients may present late due to distance and cost
• TRADITIONAL MEDICINE: May mask or delay diagnosis

SCHISTOSOMIASIS-RELATED PORTAL HYPERTENSION:
• S. mansoni and S. japonicum cause periportal fibrosis
• Leading cause of variceal bleeding in endemic areas (Egypt, East Africa)
• Liver function often preserved (unlike cirrhosis)
• Variceal bleeding may be first presentation

═══════════════════════════════════════════════════════════════════════════════
RISK FACTORS
═══════════════════════════════════════════════════════════════════════════════

UGIB RISK FACTORS:
• H. pylori infection
• NSAID use (especially with steroids or anticoagulants)
• Antiplatelet/anticoagulant therapy
• Prior peptic ulcer disease
• Cirrhosis/portal hypertension
• Chronic kidney disease
• Advanced age
• Critical illness (stress ulcers)
• Heavy alcohol use

LGIB RISK FACTORS:
• Advanced age (strongest risk factor)
• Diverticulosis
• Anticoagulant/antiplatelet use
• History of GI bleeding
• NSAIDs
• Chronic kidney disease`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS BY TYPE
═══════════════════════════════════════════════════════════════════════════════

UPPER GI BLEEDING:

• HEMATEMESIS: Vomiting fresh blood (bright red) or old blood ("coffee-ground" - digested blood)
  - Fresh blood suggests ongoing/recent brisk bleeding
  - Coffee-ground suggests slower bleeding with gastric acid contact

• MELENA: Black, tarry, foul-smelling stool
  - Indicates blood exposed to GI tract for >8 hours
  - As little as 50-100 mL blood can cause melena
  - Usually indicates source proximal to cecum

• HEMATOCHEZIA: Can occur with brisk UGIB (>1000 mL)
  - Associated with hemodynamic instability
  - Do not assume lower source if hematochezia with shock

LOWER GI BLEEDING:

• HEMATOCHEZIA: Bright red blood per rectum
  - Blood mixed with stool (colonic source)
  - Blood coating stool (rectal/anal source)
  - Blood on toilet paper only (hemorrhoids, fissure)

• MAROON STOOL: Mix of fresh and partially digested blood
  - Suggests right colon or small bowel source
  - Or brisk left-sided bleeding

ASSOCIATED SYMPTOMS:

• Abdominal pain: May suggest ulcer, ischemia, or inflammatory cause
• Painless bleeding: Typical of varices, diverticula, angiodysplasia
• Syncope/presyncope: Significant blood loss
• Dyspnea, fatigue, chest pain: Anemia symptoms
• Weight loss: Suggests malignancy
• Change in bowel habits: Colorectal neoplasia
• Prior vomiting/retching: Mallory-Weiss tear
• Dysphagia: Esophageal pathology

HEMODYNAMIC SYMPTOMS:
• Lightheadedness, dizziness
• Weakness, fatigue
• Confusion (in elderly)
• Syncope
• Chest pain, dyspnea (anemia + coronary disease)

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS - ASSESS SEVERITY:
• Tachycardia: Often earliest sign (HR >100)
• Hypotension: SBP <90 mmHg indicates significant blood loss (>30-40% blood volume)
• Orthostatic changes: SBP drop >20 mmHg or HR increase >20 bpm on standing suggests 15-20% volume loss
• Tachypnea: Compensation for decreased oxygen delivery
• Normal vital signs do NOT exclude significant bleeding (especially in young patients)

GENERAL APPEARANCE:
• Pallor (conjunctiva, palms, nail beds)
• Diaphoresis
• Altered mental status (hypoperfusion)
• Cool, clammy extremities (shock)

ABDOMINAL EXAMINATION:
• Often benign despite significant bleeding
• Tenderness: Suggests ulcer perforation, ischemia, or inflammatory process
• Hepatomegaly, splenomegaly: Suggests portal hypertension/cirrhosis
• Ascites: Portal hypertension
• Abdominal mass: Malignancy
• Surgical scars: Prior surgery may alter anatomy

STIGMATA OF CHRONIC LIVER DISEASE:
• Spider angiomata
• Palmar erythema
• Gynecomastia
• Testicular atrophy
• Caput medusae
• Asterixis (hepatic encephalopathy)
• Jaundice

RECTAL EXAMINATION:
• MANDATORY in all GI bleeding
• Assess stool color: Fresh blood, melena, maroon
• Hemorrhoids, masses
• Anal fissure

NASOGASTRIC ASPIRATE (if uncertain about source):
• Fresh blood: Active UGIB
• Coffee-grounds: Recent UGIB
• Clear/bilious: Does NOT exclude UGIB (intermittent bleeding, pyloric spasm)
• Note: NG tube no longer routinely recommended`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

1. ASSESS SEVERITY AND RESUSCITATE
2. DETERMINE UGIB vs LGIB
3. RISK STRATIFY
4. TARGETED INVESTIGATIONS
5. ENDOSCOPY FOR DIAGNOSIS AND TREATMENT

═══════════════════════════════════════════════════════════════════════════════
RISK STRATIFICATION SCORES
═══════════════════════════════════════════════════════════════════════════════

GLASGOW-BLATCHFORD SCORE (GBS) - Pre-Endoscopy UGIB:
• BUN (mg/dL): 18.2-22.4 (+2), 22.4-28 (+3), 28-70 (+4), ≥70 (+6)
• Hemoglobin (g/dL) - Men: 12-13 (+1), 10-12 (+3), <10 (+6)
• Hemoglobin (g/dL) - Women: 10-12 (+1), <10 (+6)
• SBP (mmHg): 100-109 (+1), 90-99 (+2), <90 (+3)
• Pulse ≥100: +1
• Melena: +1
• Syncope: +2
• Hepatic disease: +2
• Heart failure: +2

Interpretation:
• GBS 0: Very low risk; may be discharged with outpatient EGD
• GBS 1: Low risk; consider early discharge
• GBS ≥6: High risk; requires admission, urgent endoscopy

ROCKALL SCORE - Post-Endoscopy UGIB:
Pre-endoscopy:
• Age: <60 (0), 60-79 (+1), ≥80 (+2)
• Shock: None (0), Pulse >100 (+1), SBP <100 (+2)
• Comorbidity: None (0), Major (+2), Renal/liver failure or cancer (+3)

Post-endoscopy:
• Diagnosis: Mallory-Weiss (0), All other diagnoses (+1), GI malignancy (+2)
• Stigmata: None or dark spot (0), Blood/visible vessel/clot (+2)

Interpretation:
• Score ≤2: Low risk (rebleeding 5%, mortality 0.1%)
• Score 3-4: Intermediate risk
• Score ≥5: High risk (rebleeding 25%, mortality 11%)

OAKLAND SCORE - LGIB:
• Age, sex, prior LGIB, DRE findings, heart rate, SBP, hemoglobin
• Score ≤8: Safe for outpatient management
• Higher scores: Require hospitalization

FORREST CLASSIFICATION (Peptic Ulcer at Endoscopy):
• Ia: Spurting hemorrhage (55% rebleed risk)
• Ib: Oozing hemorrhage (55% rebleed risk)
• IIa: Non-bleeding visible vessel (43% rebleed risk)
• IIb: Adherent clot (22% rebleed risk)
• IIc: Flat pigmented spot (10% rebleed risk)
• III: Clean ulcer base (5% rebleed risk)

═══════════════════════════════════════════════════════════════════════════════
ENDOSCOPY
═══════════════════════════════════════════════════════════════════════════════

ESOPHAGOGASTRODUODENOSCOPY (EGD) - UGIB:
• Diagnostic and therapeutic
• Timing: Within 24 hours for most; within 12 hours if high-risk features
• Immediate (within 12h): Hemodynamic instability despite resuscitation, suspected variceal bleeding
• Therapeutic interventions: Epinephrine injection, thermal coagulation, clips, band ligation

COLONOSCOPY - LGIB:
• After bowel preparation when possible
• Timing: Within 24 hours for most hospitalized patients
• Therapeutic: Clips, thermal coagulation, epinephrine injection

VIDEO CAPSULE ENDOSCOPY:
• For obscure GI bleeding (negative EGD and colonoscopy)
• Identifies small bowel sources

CT ANGIOGRAPHY:
• Active bleeding not localized by endoscopy
• Detects bleeding rate ≥0.3-0.5 mL/min
• Can guide angiographic intervention

CONVENTIONAL ANGIOGRAPHY:
• Diagnostic and therapeutic
• Detects bleeding rate ≥0.5-1 mL/min
• Allows embolization of bleeding vessel

TAGGED RED BLOOD CELL SCAN:
• Detects bleeding rate ≥0.1-0.4 mL/min (more sensitive than CT)
• Helps localize intermittent bleeding
• Does not provide treatment`,

    differentialDiagnosis: [
      'Hemoptysis - Coughing blood from respiratory tract; frothy, bright red; associated with pulmonary symptoms; exclude before assuming GI source',
      'Epistaxis (swallowed blood) - Nosebleed swallowed and vomited or passed as melena; examine nares',
      'Non-GI source of anemia - Chronic blood loss from other sites; menorrhagia; hematuria',
      'Ingested substances mimicking blood - Beets, red food coloring, bismuth (black stool), iron supplements (black stool)',
      'Oropharyngeal bleeding - Dental bleeding, oral lesions swallowed',
      'Aortoenteric fistula - History of aortic graft; "herald bleed" followed by massive hemorrhage; high mortality',
      'Hemobilia - Bleeding from biliary tree into duodenum; history of liver biopsy, trauma, or hepatobiliary procedure',
      'Hemosuccus pancreaticus - Bleeding from pancreatic duct; associated with pancreatitis, pseudoaneurysm'
    ],

    investigations: [
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - Hemoglobin/hematocrit to assess severity; may be normal initially (takes 24-72h to equilibrate); platelet count for coagulopathy',
        availability: 'BASIC'
      },
      {
        name: 'Type and Screen / Crossmatch',
        indication: '🔴 IMMEDIATE - Prepare for transfusion; crossmatch 2-6 units PRBCs based on severity',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: '🔴 IMMEDIATE - Identify coagulopathy requiring correction; especially important if on anticoagulants or liver disease',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (BMP)',
        indication: '🔴 IMMEDIATE - BUN often elevated in UGIB (digested blood); BUN:Creatinine ratio >30 suggests UGIB; assess renal function',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: 'Assess for liver disease/cirrhosis as cause of variceal bleeding; albumin for synthetic function',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Marker of tissue hypoperfusion in severe bleeding/shock',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: 'Assess for myocardial ischemia from anemia; especially in elderly or cardiac history',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If aspiration risk; assess for free air if perforation suspected',
        availability: 'BASIC'
      },
      {
        name: 'Esophagogastroduodenoscopy (EGD)',
        indication: '🔴 UGIB - Diagnostic and therapeutic; within 24h for most, 12h for high-risk; identifies source in >90%',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Colonoscopy',
        indication: '🔴 LGIB - After bowel prep if stable; identifies source in 70-90%; therapeutic capability',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Angiography',
        indication: 'Localize active bleeding when endoscopy unsuccessful; detects ≥0.3-0.5 mL/min bleeding; guides intervention',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Tagged RBC Scan',
        indication: 'Intermittent bleeding not localized by other methods; sensitive for low-rate bleeding ≥0.1 mL/min',
        availability: 'ADVANCED'
      },
      {
        name: 'Angiography (Mesenteric)',
        indication: 'Massive bleeding not controlled endoscopically; diagnostic and therapeutic (embolization); detects ≥0.5-1 mL/min',
        availability: 'ADVANCED'
      },
      {
        name: 'H. pylori Testing',
        indication: 'All patients with peptic ulcer disease; stool antigen, urea breath test, or biopsy; test and treat',
        availability: 'BASIC'
      },
      {
        name: 'Video Capsule Endoscopy',
        indication: 'Obscure GI bleeding (negative EGD and colonoscopy); identifies small bowel sources',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Vital signs, mental status, evidence of shock; large-bore IV access x2',
        '⚡ RESUSCITATION: Crystalloid bolus (NS or LR) 500-1000 mL; target MAP >65, HR <100, UOP >0.5 mL/kg/hr',
        '⚡ BLOOD TRANSFUSION: Transfuse PRBCs if Hgb <7-8 g/dL or hemodynamically unstable; use restrictive strategy in stable patients',
        '⚡ CORRECT COAGULOPATHY: FFP if INR >1.5 with active bleeding; platelets if <50,000; vitamin K if warfarin; specific reversal agents as indicated',
        '⚡ NPO: Keep patient nil per os for potential endoscopy',
        '⚡ PPI THERAPY (UGIB): High-dose IV PPI - Pantoprazole 80 mg IV bolus, then 8 mg/hour infusion; or Omeprazole equivalent',
        '⚡ VARICEAL BLEEDING: If suspected, start octreotide 50 mcg bolus then 50 mcg/hr + IV antibiotics (ceftriaxone 1g) + urgent EGD',
        '⚡ AIRWAY PROTECTION: Consider intubation if massive hematemesis, altered mental status, or high aspiration risk',
        '⚡ HOLD ANTICOAGULANTS/ANTIPLATELETS: Stop warfarin, DOACs, heparin, aspirin, clopidogrel; reverse if life-threatening',
        '⚡ GI CONSULT: Urgent for endoscopy; surgical consult if perforation or refractory bleeding',
        '⚡ ICU ADMISSION: For hemodynamic instability, massive bleeding, need for airway protection, or high-risk features'
      ],
      pharmacological: [
        {
          drug: 'Pantoprazole (or equivalent PPI) IV',
          indication: '🔴 UGIB - Reduces rebleeding and need for intervention; start pre-endoscopy',
          dose: 'High-dose: 80 mg IV bolus, then 8 mg/hour continuous infusion for 72 hours post-endoscopy (for high-risk ulcer stigmata); OR intermittent 40 mg IV q12h',
          route: 'IV',
          duration: 'Continuous infusion x 72 hours post-endoscopy for high-risk ulcers; then transition to oral PPI',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Generally well-tolerated', 'Rare: C. difficile', 'Hypomagnesemia with prolonged use'],
          alternatives: ['Omeprazole 80 mg bolus then 8 mg/hr', 'Esomeprazole IV']
        },
        {
          drug: 'Octreotide',
          indication: '🔴 VARICEAL BLEEDING - Splanchnic vasoconstriction; reduces portal pressure; start immediately if varices suspected',
          dose: '50 mcg IV bolus, then 50 mcg/hour continuous infusion',
          route: 'IV',
          duration: '3-5 days (continue even after endoscopic treatment)',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Hyperglycemia', 'Bradycardia', 'Abdominal cramping', 'Diarrhea'],
          alternatives: ['Terlipressin 2 mg IV q4h (not available in all countries)']
        },
        {
          drug: 'Terlipressin',
          indication: '🔴 VARICEAL BLEEDING - Vasopressin analogue; reduces portal pressure; alternative to octreotide',
          dose: '2 mg IV every 4 hours for 48 hours, then 1 mg IV q4h for up to 5 days',
          route: 'IV',
          duration: 'Up to 5 days',
          contraindications: ['Pregnancy', 'Septic shock', 'Ischemic heart disease', 'Peripheral vascular disease', 'Severe hypertension'],
          sideEffects: ['Hyponatremia', 'Peripheral and cardiac ischemia', 'Arrhythmias', 'Abdominal cramping'],
          alternatives: ['Octreotide']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 VARICEAL BLEEDING with cirrhosis - Antibiotic prophylaxis reduces mortality and rebleeding',
          dose: '1 g IV once daily',
          route: 'IV',
          duration: '5-7 days',
          contraindications: ['Cephalosporin allergy', 'Do not mix with calcium-containing solutions'],
          sideEffects: ['Diarrhea', 'Rash', 'C. difficile infection'],
          alternatives: ['Norfloxacin 400 mg PO BID (if less severe)']
        },
        {
          drug: 'Packed Red Blood Cells (PRBCs)',
          indication: '🔴 TRANSFUSION - Hemoglobin <7-8 g/dL or hemodynamic instability despite fluids',
          dose: 'Restrictive: Target Hgb 7-8 g/dL; Liberal: Target 9-10 g/dL for ACS or significant cardiac disease; 1 unit raises Hgb by ~1 g/dL',
          route: 'IV',
          duration: 'Until Hgb target achieved and bleeding controlled',
          contraindications: ['None absolute in life-threatening bleeding'],
          sideEffects: ['Transfusion reactions', 'TACO (transfusion-associated circulatory overload)', 'TRALI'],
          alternatives: ['Whole blood if available in massive hemorrhage protocols']
        },
        {
          drug: 'Fresh Frozen Plasma (FFP)',
          indication: 'Coagulopathy with INR >1.5 and active bleeding; warfarin reversal if PCC not available',
          dose: '10-15 mL/kg IV; typically 2-4 units',
          route: 'IV',
          duration: 'Until INR <1.5 or bleeding controlled',
          contraindications: ['IgA deficiency (relative)'],
          sideEffects: ['Allergic reactions', 'TACO', 'TRALI'],
          alternatives: ['Prothrombin Complex Concentrate (PCC) for warfarin reversal (faster, less volume)']
        },
        {
          drug: 'Vitamin K (Phytonadione)',
          indication: 'Warfarin-associated coagulopathy; adjunct to FFP or PCC',
          dose: '10 mg IV slow infusion (over 20-30 min to avoid anaphylaxis)',
          route: 'IV',
          duration: 'Single dose; may repeat in 12 hours if INR still elevated',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Anaphylaxis with rapid IV administration', 'Delayed warfarin effect reversal'],
          alternatives: ['PO vitamin K 2.5-5 mg if less urgent']
        },
        {
          drug: 'Tranexamic Acid',
          indication: 'May be considered in upper GI bleeding (HALT-IT trial showed no mortality benefit but some use in massive hemorrhage)',
          dose: '1 g IV over 10 minutes, then 1 g IV over 8 hours (or 1 g IV q8h)',
          route: 'IV',
          duration: 'Up to 24 hours',
          contraindications: ['Active intravascular clotting', 'History of convulsions', 'Color vision defects'],
          sideEffects: ['Nausea', 'Diarrhea', 'Seizures (rare)', 'Thromboembolic events'],
          alternatives: ['Aminocaproic acid']
        },
        {
          drug: 'Lactulose',
          indication: 'Hepatic encephalopathy prevention/treatment in cirrhotic patients with GI bleeding',
          dose: '30-45 mL PO/NG q1-2h until bowel movement, then titrate to 2-3 soft stools/day',
          route: 'PO or via NG tube',
          duration: 'Throughout hospitalization and ongoing if cirrhosis',
          contraindications: ['Galactosemia'],
          sideEffects: ['Diarrhea', 'Abdominal cramping', 'Flatulence', 'Hypernatremia'],
          alternatives: ['Rifaximin 550 mg PO BID']
        },
        {
          drug: 'H. pylori Eradication Therapy',
          indication: 'All patients with H. pylori-positive peptic ulcer disease',
          dose: 'Triple therapy: PPI + Clarithromycin 500 mg BID + Amoxicillin 1 g BID (or Metronidazole 500 mg BID); Quadruple therapy: PPI + Bismuth + Metronidazole + Tetracycline',
          route: 'PO',
          duration: '14 days',
          contraindications: ['Component allergies; clarithromycin with certain medications'],
          sideEffects: ['GI upset', 'Taste disturbance', 'Diarrhea', 'C. difficile'],
          alternatives: ['Regimen based on local resistance patterns']
        }
      ],
      nonPharmacological: [
        'ENDOSCOPIC THERAPY (UGIB): Epinephrine injection + thermal coagulation or clips for high-risk ulcer stigmata (Forrest Ia, Ib, IIa); band ligation or sclerotherapy for varices',
        'ENDOSCOPIC THERAPY (LGIB): Clips, thermal coagulation, epinephrine injection for identified sources; band ligation for hemorrhoids',
        'BALLOON TAMPONADE (Sengstaken-Blakemore or Minnesota tube): Temporizing measure for uncontrolled variceal bleeding; bridge to TIPS or endoscopy; high complication rate',
        'TRANSJUGULAR INTRAHEPATIC PORTOSYSTEMIC SHUNT (TIPS): For refractory or recurrent variceal bleeding; reduces portal pressure; risk of encephalopathy',
        'ANGIOGRAPHIC EMBOLIZATION: For bleeding not controlled endoscopically; selective embolization of bleeding vessel',
        'SURGERY: Reserved for refractory bleeding not controlled by endoscopic or angiographic means; oversewing ulcer, gastrectomy, bowel resection',
        'BOWEL PREPARATION: For colonoscopy in stable LGIB; polyethylene glycol solution 4-6 L',
        'PROKINETICS: Erythromycin 250 mg IV 30-90 min before EGD may improve visualization by promoting gastric emptying'
      ],
      surgical: [
        'EMERGENCY SURGERY for UGIB: Indicated for uncontrolled bleeding despite endoscopic and medical therapy; hemodynamic instability requiring >6 units PRBCs in 24h; recurrent bleeding after 2 endoscopic attempts; perforation',
        'SURGICAL OPTIONS for ULCER: Oversewing of bleeding vessel, vagotomy with pyloroplasty, partial gastrectomy',
        'EMERGENCY SURGERY for LGIB: Subtotal colectomy if bleeding source not identified; segmental resection if localized',
        'TIPS (Transjugular Intrahepatic Portosystemic Shunt): Interventional radiology procedure for refractory variceal bleeding; creates portosystemic shunt',
        'SPLENECTOMY/DEVASCULARIZATION: For variceal bleeding when TIPS not available or contraindicated'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE GI/ENDOSCOPY: All significant GI bleeding for diagnostic and therapeutic endoscopy',
        '🔴 IMMEDIATE ICU: Hemodynamic instability; massive transfusion requirement; need for airway protection; high-risk features',
        '🔴 IMMEDIATE SURGERY: Uncontrolled bleeding despite endoscopic therapy; perforation; massive hemorrhage',
        '🔴 INTERVENTIONAL RADIOLOGY: Bleeding not controlled endoscopically; for angiography and embolization; TIPS for variceal bleeding',
        '🟠 HEPATOLOGY: Variceal bleeding for portal hypertension management; liver transplant evaluation',
        '🟠 ONCOLOGY: If malignancy identified as bleeding source'
      ],
      monitoring: [
        'VITAL SIGNS: Continuous in active bleeding; every 1-4 hours when stable; watch for tachycardia as early sign of rebleeding',
        'HEMOGLOBIN: Every 6-8 hours during active bleeding; daily when stable; remember initial Hgb may not reflect true blood loss',
        'URINE OUTPUT: Foley catheter; target >0.5 mL/kg/hr; reflects perfusion status',
        'COAGULATION: INR, aPTT if coagulopathy or on anticoagulants; platelet count',
        'STOOL OUTPUT: Color and frequency; melena or hematochezia suggests ongoing bleeding',
        'CLINICAL STATUS: Mental status, skin perfusion, signs of rebleeding (hematemesis, melena, tachycardia, hypotension)',
        'REBLEEDING WATCH: Highest risk in first 72 hours; watch for sudden tachycardia, hypotension, hematemesis/hematochezia'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
INPATIENT COURSE
═══════════════════════════════════════════════════════════════════════════════

DURATION OF HOSPITALIZATION:
• Low-risk (GBS 0-1): May discharge from ED with outpatient EGD within 24-48h
• Standard: 2-3 days after bleeding controlled and stable
• High-risk or complicated: Until stable; may require prolonged stay

REBLEEDING:
• Highest risk in first 72 hours post-endoscopy
• If rebleeding: Repeat endoscopy; consider surgery or IR if second attempt fails

DIET ADVANCEMENT:
• Clear liquids once stable, typically 24h post-endoscopy
• Advance as tolerated
• Avoid NSAIDs

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE CRITERIA
═══════════════════════════════════════════════════════════════════════════════

• Hemodynamically stable for ≥24 hours
• No evidence of rebleeding
• Tolerating oral diet
• Stable hemoglobin (not requiring transfusion)
• Outpatient follow-up arranged
• Medications optimized (PPI, H. pylori treatment if indicated)
• Anticoagulation plan established

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

1-2 WEEKS:
• Assess symptoms, hemoglobin
• H. pylori eradication confirmation (if treated)
• PPI adherence
• Address cardiovascular risk if anemia revealed CAD

6-8 WEEKS (Peptic Ulcer):
• Repeat EGD for gastric ulcers (rule out malignancy)
• Confirm H. pylori eradication (stool antigen or urea breath test)

LONG-TERM:
• PPI therapy: Duration depends on etiology and ongoing risk factors
• NSAID avoidance or PPI prophylaxis if NSAID necessary
• Aspirin: May resume with PPI after ulcer healed if cardiovascular indication
• Anticoagulation: Resume when bleeding source controlled; timing individualized
• Colonoscopy: If LGIB not fully evaluated; colorectal cancer screening

VARICEAL BLEEDING FOLLOW-UP:
• Secondary prophylaxis: Non-selective beta-blocker (propranolol, nadolol) + repeat band ligation until varices eradicated
• Surveillance EGD every 6-12 months
• Hepatology follow-up for cirrhosis management
• Liver transplant evaluation if appropriate`
    },

    redFlags: [
      '🔴 CRITICAL: Hemodynamic instability (SBP <90, HR >120) despite 2L crystalloid - Massive hemorrhage; activate massive transfusion protocol',
      '🔴 CRITICAL: Witnessed large-volume hematemesis - Active major UGIB; urgent endoscopy; prepare for airway protection',
      '🔴 CRITICAL: Hematochezia with hemodynamic instability - May be brisk UGIB; do not assume lower source',
      '🔴 CRITICAL: Signs of shock (altered mental status, cool extremities, oliguria) - Urgent resuscitation and blood transfusion',
      '🔴 CRITICAL: Suspected variceal bleeding (cirrhosis + hematemesis) - Start octreotide + antibiotics + urgent EGD',
      '🔴 CRITICAL: Rebleeding after endoscopic therapy - Repeat endoscopy; prepare for surgery or IR',
      '🔴 CRITICAL: Suspicion of aortoenteric fistula (aortic graft history + GI bleeding) - Surgical emergency; CT angiography',
      '🟠 URGENT: Initial hemoglobin <7 g/dL - Likely significant blood loss; transfuse',
      '🟠 URGENT: INR >2.5 or platelets <50,000 with active bleeding - Correct coagulopathy urgently',
      '🟠 URGENT: High-risk endoscopic stigmata (Forrest Ia, Ib, IIa) - Requires endoscopic therapy and high-dose PPI',
      '🟠 URGENT: Aspirin or anticoagulant use with significant bleeding - Hold and consider reversal',
      '🟠 URGENT: Signs of perforation (peritonitis, free air) - Surgical emergency'
    ],

    complications: [
      'DEATH: Mortality 2-10% for UGIB (up to 20% for variceal); 2-4% for LGIB; higher with delayed treatment, comorbidities, rebleeding',
      'REBLEEDING: Occurs in 10-20% of peptic ulcer bleeding; risk highest in first 72 hours; highest with Forrest Ia, Ib, IIa stigmata',
      'ASPIRATION PNEUMONIA: From vomiting blood; highest risk with altered mental status; consider intubation for airway protection',
      'MYOCARDIAL INFARCTION: Anemia-induced demand ischemia, especially in elderly and those with CAD',
      'ACUTE KIDNEY INJURY: From hypoperfusion/shock; prerenal azotemia',
      'TRANSFUSION-RELATED COMPLICATIONS: TACO, TRALI, transfusion reactions, infection',
      'HEPATIC ENCEPHALOPATHY: In cirrhotic patients; blood in GI tract is protein load; use lactulose',
      'PERFORATION: From ulcer or during endoscopic therapy',
      'MULTIORGAN FAILURE: In massive hemorrhage with prolonged shock',
      'STRICTURE: Late complication of severe esophagitis or caustic injury'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

UPPER GI BLEEDING:
• Overall mortality: 2-10%
• Variceal bleeding: 15-20% per episode
• Has improved with PPIs and endoscopic therapy

LOWER GI BLEEDING:
• Overall mortality: 2-4%
• Most stops spontaneously (80%)
• Lower mortality than UGIB

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Advanced age (>65 years)
• Hemodynamic instability at presentation
• Comorbidities (liver disease, renal failure, malignancy, heart failure)
• Inpatient bleeding (vs community-onset)
• High-risk endoscopic findings
• Rebleeding
• Need for surgery
• Coagulopathy
• Red blood in NG aspirate (vs coffee-grounds)

FAVORABLE PROGNOSIS:
• Young age
• Low Glasgow-Blatchford Score (0-1)
• Hemodynamically stable
• Low-risk endoscopic findings (Forrest IIc, III)
• No significant comorbidities

═══════════════════════════════════════════════════════════════════════════════
RECURRENCE
═══════════════════════════════════════════════════════════════════════════════

PEPTIC ULCER:
• With H. pylori eradication: <5% recurrence
• Without H. pylori eradication: 50-80% recurrence
• With ongoing NSAID use: High recurrence

VARICEAL BLEEDING:
• Without secondary prophylaxis: 60-70% rebleed within 1-2 years
• With beta-blockers + band ligation: 15-30% rebleed

DIVERTICULAR BLEEDING:
• 25% recurrence after first episode
• 50% recurrence after second episode`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

PEPTIC ULCER DISEASE:
• Test and treat H. pylori in high-risk patients
• Avoid NSAIDs when possible; use lowest effective dose for shortest duration
• PPI co-therapy with NSAIDs in high-risk patients (prior ulcer, age >65, concurrent aspirin/anticoagulant, steroids)
• PPI with aspirin if prior ulcer or high GI risk

STRESS ULCER PROPHYLAXIS:
• PPI or H2 blocker in ICU patients with risk factors (coagulopathy, mechanical ventilation >48h, prior GI bleed)

VARICEAL BLEEDING:
• Screen for varices in cirrhosis (EGD)
• Primary prophylaxis: Non-selective beta-blocker (propranolol, nadolol) OR band ligation for medium/large varices

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

PEPTIC ULCER:
• H. pylori eradication (confirm cure)
• Maintenance PPI if ongoing NSAID or aspirin use
• Avoid NSAIDs; use alternative analgesics
• Discontinue smoking and reduce alcohol

VARICEAL BLEEDING:
• Secondary prophylaxis: Non-selective beta-blocker + band ligation (more effective than either alone)
• TIPS if refractory to medical/endoscopic therapy
• Liver transplant evaluation

DIVERTICULAR BLEEDING:
• Aspirin/NSAID avoidance if possible
• No specific prevention; fiber diet may help

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT
═══════════════════════════════════════════════════════════════════════════════

• H. PYLORI ERADICATION: Crucial given high prevalence; use locally appropriate regimens based on resistance patterns
• NSAID EDUCATION: Counsel on risks; many NSAIDs available over-the-counter
• SCHISTOSOMIASIS TREATMENT: Praziquantel in endemic areas to prevent portal hypertension
• HEPATITIS B VACCINATION: Prevent HBV-related cirrhosis
• ALCOHOL REDUCTION: Address alcohol use disorder to prevent liver disease
• HELICOBACTER PYLORI: High prevalence; improved sanitation may reduce transmission`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT HAPPENED TO YOU
═══════════════════════════════════════════════════════════════════════════════

You have had bleeding in your digestive system (stomach, intestines, or bowel). This is called gastrointestinal (GI) bleeding. It can be serious and needs treatment to stop the bleeding and prevent it from happening again.

Common causes include:
• Ulcers (sores) in the stomach or upper intestine
• Swollen blood vessels (varices) from liver disease
• Inflammation of the stomach lining
• Abnormal blood vessels in the intestines
• Hemorrhoids or other problems in the lower bowel

═══════════════════════════════════════════════════════════════════════════════
DURING YOUR HOSPITAL STAY
═══════════════════════════════════════════════════════════════════════════════

• You may have received blood transfusions to replace lost blood
• An endoscopy (camera test) may have been done to find and treat the bleeding
• You received medications to help stop the bleeding and heal your digestive system
• You may have been kept without food to rest your stomach before and after the procedure

═══════════════════════════════════════════════════════════════════════════════
MEDICATIONS AFTER DISCHARGE
═══════════════════════════════════════════════════════════════════════════════

PROTON PUMP INHIBITOR (PPI) - Examples: Omeprazole, Pantoprazole, Esomeprazole:
• Take once daily (usually in the morning) on an empty stomach
• Reduces stomach acid to help ulcers heal
• Take for as long as prescribed (usually at least 4-8 weeks, sometimes longer)

H. PYLORI TREATMENT (if you tested positive):
• Multiple antibiotics for 10-14 days
• VERY IMPORTANT to complete the full course
• Return for a test to confirm the bacteria is gone

═══════════════════════════════════════════════════════════════════════════════
LIFESTYLE CHANGES
═══════════════════════════════════════════════════════════════════════════════

AVOID:
• NSAIDs (ibuprofen, aspirin, naproxen, diclofenac) unless specifically prescribed by your doctor - these can cause bleeding
• Excessive alcohol
• Smoking

IF YOU NEED PAIN RELIEF:
• Use paracetamol (acetaminophen) instead of NSAIDs
• If you must take aspirin for your heart, discuss with your doctor about protecting your stomach

DIET:
• No special diet is required once you are healed
• Eat regular, balanced meals
• Some people feel better avoiding spicy foods, but this is individual

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - RETURN IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Return to the hospital immediately if you experience:

🔴 Vomiting blood (bright red or looks like coffee grounds)
🔴 Black, tarry stools (melena)
🔴 Bright red blood in your stool
🔴 Feeling faint, dizzy, or lightheaded
🔴 Rapid heartbeat
🔴 Extreme weakness
🔴 Severe abdominal pain
🔴 Confusion

These could mean the bleeding has returned and you need urgent treatment.

═══════════════════════════════════════════════════════════════════════════════
FOLLOW-UP APPOINTMENTS
═══════════════════════════════════════════════════════════════════════════════

• Keep all follow-up appointments with your doctor
• If you had a stomach ulcer, you may need a repeat endoscopy to ensure it has healed
• If you were treated for H. pylori, you will need a test to confirm the bacteria is gone
• If you have liver disease, you will need regular follow-up with a specialist

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What caused my bleeding?
• Do I need to take medications long-term?
• What medications should I avoid?
• Do I need another endoscopy?
• Was I tested for H. pylori?
• When can I resume my normal activities?
• When can I restart my blood thinners (if applicable)?`,

    references: [
      'Laine L, et al. ACG Clinical Guideline: Upper Gastrointestinal and Ulcer Bleeding. Am J Gastroenterol. 2021;116(5):899-917. doi:10.14309/ajg.0000000000001245',
      'Strate LL, et al. ACG Clinical Guideline: Management of Patients With Acute Lower Gastrointestinal Bleeding. Am J Gastroenterol. 2016;111(4):459-74.',
      'de Franchis R, et al. Baveno VII - Renewing consensus in portal hypertension. J Hepatol. 2022;76(4):959-974. doi:10.1016/j.jhep.2021.12.022',
      'Villanueva C, et al. Transfusion Strategies for Acute Upper Gastrointestinal Bleeding (TRIGGER). N Engl J Med. 2013;368(1):11-21. doi:10.1056/NEJMoa1211801',
      'Barkun AN, et al. Management of Nonvariceal Upper Gastrointestinal Bleeding: Guideline Recommendations From the International Consensus Group. Ann Intern Med. 2019;171(11):805-822.',
      'Oakland K, et al. Diagnosis and management of acute lower gastrointestinal bleeding: guidelines from the British Society of Gastroenterology. Gut. 2019;68(5):776-789.',
      'HALT-IT Trial Collaborators. Effects of a high-dose 24-h infusion of tranexamic acid on death and thromboembolic events in patients with acute gastrointestinal bleeding (HALT-IT). Lancet. 2020;395(10241):1927-1936.',
      'Blatchford O, et al. A risk score to predict need for treatment for upper-gastrointestinal haemorrhage. Lancet. 2000;356(9238):1318-21.',
      'Garcia-Tsao G, et al. Prevention and management of gastroesophageal varices and variceal hemorrhage in cirrhosis. Hepatology. 2007;46(3):922-38.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 18. HYPEROSMOLAR HYPERGLYCEMIC STATE (HHS)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '18',
    name: 'Hyperosmolar Hyperglycemic State (HHS)',
    slug: 'hyperosmolar-hyperglycemic-state',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E11.00',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Hyperosmolar Hyperglycemic State (HHS) is a serious, life-threatening complication of diabetes mellitus characterized by severe hyperglycemia, hyperosmolality, and profound dehydration WITHOUT significant ketoacidosis. It primarily occurs in Type 2 diabetes, often in elderly patients, and carries a higher mortality rate than diabetic ketoacidosis (DKA). The hallmarks are extreme hyperglycemia (often >600 mg/dL), serum osmolality >320 mOsm/kg, and minimal or absent ketosis.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• HHS vs DKA - KEY DIFFERENCES:
  - HHS: Glucose often >600 mg/dL, osmolality >320, minimal ketones, pH >7.30, HCO3 >18
  - DKA: Glucose usually 250-800 mg/dL, ketones present, pH <7.30, HCO3 <18
  - Mixed DKA/HHS can occur (30% overlap)

• FLUID DEFICIT IS MASSIVE: Average 8-10 L deficit; aggressive but careful rehydration is cornerstone of treatment

• FLUID RESUSCITATION FIRST: Start with 0.9% NS 1-2 L in first hour; then adjust based on sodium and hemodynamic status

• INSULIN TIMING: Start insulin AFTER initial fluid bolus (1-2 hours); starting insulin too early without fluids can precipitate cardiovascular collapse

• INSULIN DOSE: Lower than DKA - 0.05-0.1 units/kg/hour; goal is gradual glucose reduction (50-75 mg/dL per hour)

• CORRECT SODIUM FOR GLUCOSE: Corrected Na = Measured Na + [1.6 × (Glucose - 100)/100]; if corrected Na >145, use 0.45% NS

• POTASSIUM REPLACEMENT IS CRITICAL: Check K+ before insulin; if K+ <3.3, hold insulin and replace K+ first; add K+ to fluids once K+ <5.3 and patient urinating

• PRECIPITATING FACTOR: Always search for underlying cause - infection (#1), MI, stroke, medication non-compliance, new diabetes diagnosis

• MORTALITY IS HIGH: 10-20% (higher than DKA); often due to underlying illness, advanced age, and complications

• CEREBRAL EDEMA: Less common than in DKA but can occur; avoid overly rapid correction of osmolality (max decrease 3 mOsm/kg/hour)

• THROMBOEMBOLISM RISK: HHS is hypercoagulable state; consider DVT prophylaxis; high risk for stroke, MI, DVT, PE

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

CLASSIC HHS:
• Plasma glucose >600 mg/dL (33.3 mmol/L)
• Effective serum osmolality >320 mOsm/kg
• Arterial pH >7.30
• Serum bicarbonate >18 mEq/L
• Small or absent ketonemia/ketonuria
• Altered mental status (ranges from confusion to coma)

SERUM OSMOLALITY CALCULATION:
Effective Osmolality = 2[Na+] + [Glucose]/18
(Do not include BUN as it crosses cell membranes)

Normal: 285-295 mOsm/kg
HHS: >320 mOsm/kg (often >340)

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

UNDERLYING MECHANISM:
1. Relative insulin deficiency (enough to prevent ketosis, not enough to prevent hyperglycemia)
2. Counter-regulatory hormone excess (glucagon, cortisol, catecholamines)
3. Severe hyperglycemia develops over days to weeks
4. Osmotic diuresis causes profound water and electrolyte loss
5. Decreased GFR from dehydration impairs glucose excretion
6. Hyperosmolality causes cellular dehydration, especially in brain

WHY NO KETOSIS:
• Some endogenous insulin production persists (Type 2 DM)
• Enough insulin to suppress lipolysis and ketogenesis
• NOT enough insulin to promote glucose uptake

DEHYDRATION CASCADE:
• Hyperglycemia → Osmotic diuresis → Profound water loss (8-10 L)
• Electrolyte losses: Na+ (500-700 mEq), K+ (300-500 mEq), Cl−, PO4, Mg
• Hypovolemia → Decreased renal perfusion → Decreased glucose excretion → Worsening hyperglycemia
• Viscous cycle leading to extreme hyperglycemia

NEUROLOGICAL EFFECTS:
• Hyperosmolality causes water shift out of brain cells
• Cellular dehydration leads to altered mental status
• Severity of mental status change correlates with osmolality
• Osmolality >340: Coma common`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Less common than DKA but increasing with Type 2 DM epidemic
• Incidence: 1-2% of diabetes-related hospital admissions
• Predominantly affects elderly patients with Type 2 DM
• Average age: 60-70 years (older than DKA patients)
• Male = Female
• Mortality: 10-20% (higher than DKA at 1-5%)
• Often the initial presentation of previously undiagnosed Type 2 DM

RISK GROUPS:
• Elderly with Type 2 DM
• Nursing home residents
• Patients with limited access to water (dementia, immobility)
• Undiagnosed Type 2 DM (presenting symptom in 30-40%)
• Patients on medications that impair glucose tolerance

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• TYPE 2 DIABETES EPIDEMIC in Africa: Rising prevalence (5-10% in urban areas) with increasing obesity and lifestyle changes

• HHS LIKELY UNDERDIAGNOSED:
  - May be misdiagnosed as stroke, sepsis, or other altered mental status causes
  - Limited access to blood glucose testing in some areas
  - Late presentation common

• UNIQUE FEATURES IN AFRICA:
  - Younger age of onset (Type 2 DM presents earlier in Africa)
  - Ketosis-prone Type 2 DM more common (may see overlap with DKA)
  - "Flatbush diabetes" phenotype
  - Presentation may be first diagnosis of diabetes

• PRECIPITATING FACTORS IN AFRICA:
  - INFECTIONS: More common trigger; includes malaria, TB, HIV-related infections, pneumonia
  - Traditional medicine use
  - Limited access to diabetes medications
  - Medication non-adherence due to cost
  - Undiagnosed diabetes with delayed presentation

• CHALLENGES:
  - ICU availability for monitoring
  - Laboratory access (serum osmolality may not be available - must calculate)
  - Insulin pump availability for infusions (may need to use bolus dosing)
  - Potassium monitoring frequency
  - Blood glucose monitoring supplies

• HIV AND HHS:
  - HIV patients at increased diabetes risk
  - Some antiretroviral medications (protease inhibitors) increase diabetes risk
  - Opportunistic infections as precipitants

═══════════════════════════════════════════════════════════════════════════════
PRECIPITATING FACTORS
═══════════════════════════════════════════════════════════════════════════════

INFECTION (Most common - 40-60%):
• Pneumonia
• Urinary tract infection
• Sepsis
• Skin/soft tissue infections
• In Africa: Malaria, TB, HIV-related infections

CARDIOVASCULAR:
• Myocardial infarction
• Stroke
• Pulmonary embolism

MEDICATIONS:
• Corticosteroids
• Thiazide diuretics
• Beta-blockers
• Atypical antipsychotics (olanzapine, clozapine)
• Phenytoin
• Calcium channel blockers

OTHER:
• Non-compliance with diabetes medications
• New diagnosis of diabetes
• Dehydration (limited water access, vomiting, diarrhea)
• Surgery/trauma
• Pancreatitis
• Substance abuse (alcohol, cocaine)
• Endocrine disorders (thyrotoxicosis, Cushing's)`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS - GRADUAL ONSET (Days to Weeks)
═══════════════════════════════════════════════════════════════════════════════

CLASSIC SYMPTOMS:
• Polyuria (osmotic diuresis)
• Polydipsia (thirst - may be impaired in elderly/demented)
• Weakness and fatigue
• Weight loss
• Nausea (less prominent than DKA)
• Blurred vision

NEUROLOGICAL SYMPTOMS (correlate with osmolality):
• Confusion, disorientation
• Lethargy, drowsiness
• Stupor
• Coma (osmolality usually >340 mOsm/kg)
• Focal neurological deficits (may mimic stroke)
• Seizures (10-15%)

SYMPTOMS OF UNDERLYING ILLNESS:
• Fever, cough (infection)
• Chest pain (MI)
• Dysuria, frequency (UTI)

IMPORTANT: Symptoms develop GRADUALLY over days to weeks (unlike DKA which develops in hours to days)

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Tachycardia (dehydration, underlying illness)
• Hypotension or orthostatic hypotension (hypovolemia)
• Tachypnea (compensation, underlying illness - NOT Kussmaul breathing)
• Fever (suggests infection) or hypothermia (severe sepsis)
• Normal or elevated temperature

DEHYDRATION (Profound):
• Dry mucous membranes
• Decreased skin turgor (less reliable in elderly)
• Sunken eyes
• Delayed capillary refill
• Weak, thready pulse
• Cool extremities (if shock)

NEUROLOGICAL EXAMINATION:
• Altered mental status (ranges from confusion to coma)
• Focal neurological deficits possible (hemiparesis, aphasia - may mimic stroke)
• Positive Babinski sign
• Seizures (focal or generalized)
• Hyporeflexia or areflexia
• Nystagmus

ABDOMINAL EXAMINATION:
• Usually BENIGN (unlike DKA which often has abdominal pain)
• Mild tenderness possible
• Decreased bowel sounds (ileus from hypokalemia)

KEY DIFFERENCES FROM DKA:
• NO Kussmaul breathing (deep, labored respirations)
• NO fruity/acetone breath odor
• More profound mental status changes
• Abdominal pain less common
• Slower onset`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

HHS DIAGNOSIS requires:
1. Plasma glucose >600 mg/dL (>33.3 mmol/L)
2. Effective serum osmolality >320 mOsm/kg
3. pH >7.30 (arterial or venous)
4. Serum bicarbonate >18 mEq/L
5. Absent or minimal ketones (trace ketonuria acceptable)
6. Altered sensorium or mental status

═══════════════════════════════════════════════════════════════════════════════
CALCULATIONS
═══════════════════════════════════════════════════════════════════════════════

EFFECTIVE SERUM OSMOLALITY:
= 2[Na+ (mEq/L)] + [Glucose (mg/dL)/18]
Normal: 285-295 mOsm/kg
HHS: >320 mOsm/kg

CORRECTED SODIUM (for hyperglycemia):
= Measured Na+ + [1.6 × (Glucose - 100)/100]
OR
= Measured Na+ + [2.4 × (Glucose - 100)/100] (alternative formula)

ANION GAP:
= Na+ - (Cl− + HCO3−)
Normal: 8-12 mEq/L
Mildly elevated in HHS is acceptable; significantly elevated suggests DKA or lactic acidosis

═══════════════════════════════════════════════════════════════════════════════
HHS vs DKA COMPARISON
═══════════════════════════════════════════════════════════════════════════════

                        HHS             DKA
Glucose (mg/dL)         >600            250-800
Osmolality (mOsm/kg)    >320            Variable
pH                      >7.30           <7.30
Bicarbonate (mEq/L)     >18             <18
Ketones                 Absent/trace    Moderate-large
Mental status           Often altered   Variable
Onset                   Days-weeks      Hours-days
Typical patient         Elderly T2DM    Young T1DM
Mortality               10-20%          1-5%

NOTE: 30% of patients have MIXED DKA/HHS - treat as DKA

═══════════════════════════════════════════════════════════════════════════════
WORKUP FOR PRECIPITATING CAUSE
═══════════════════════════════════════════════════════════════════════════════

ALWAYS search for underlying trigger:
• Chest X-ray (pneumonia, heart failure)
• Urinalysis and culture (UTI)
• Blood cultures (sepsis)
• ECG (MI, arrhythmia)
• Cardiac enzymes (troponin)
• Lipase (pancreatitis)
• CT head if focal neurological deficits (stroke vs HHS)
• Malaria smear (in endemic areas)
• COVID-19 testing (recent pandemic precipitant)`,

    differentialDiagnosis: [
      'Diabetic Ketoacidosis (DKA) - Ketones present, lower glucose usually, metabolic acidosis with low pH and HCO3; treat similarly but DKA has more ketosis',
      'Stroke (CVA) - Focal neurological deficits; HHS can mimic stroke; CT head to differentiate; check glucose in all altered mental status',
      'Sepsis/Septic shock - Fever, hypotension, elevated lactate; can coexist with and precipitate HHS',
      'Uremic encephalopathy - Elevated BUN/creatinine, uremic frost, asterixis; can coexist with HHS',
      'Hepatic encephalopathy - Liver disease history, asterixis, elevated ammonia; may have normal glucose',
      'Drug overdose/intoxication - Toxicology screen; history of substance use; specific toxidromes',
      'Alcohol intoxication/withdrawal - History of alcohol use; may coexist; check glucose and osmolality',
      'Hypoglycemia - Low glucose (<70 mg/dL); responds to glucose administration',
      'Severe hypernatremia - Check osmolality; may have similar presentation',
      'Non-ketotic hyperglycinemia - Rare metabolic disorder; usually in neonates',
      'Central nervous system infection (meningitis/encephalitis) - Fever, neck stiffness; lumbar puncture if suspected'
    ],

    investigations: [
      {
        name: 'Blood Glucose (Point-of-Care and Lab)',
        indication: '🔴 IMMEDIATE - Confirms hyperglycemia; POC glucose may read "HIGH" if >600; send lab glucose for accuracy',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (BMP)',
        indication: '🔴 IMMEDIATE - Sodium (calculate corrected), potassium (critical before insulin), BUN/Cr (renal function), bicarbonate',
        availability: 'BASIC'
      },
      {
        name: 'Serum Osmolality (Calculated or Measured)',
        indication: '🔴 DIAGNOSTIC - >320 mOsm/kg defines HHS; if lab measurement unavailable, CALCULATE: 2[Na] + [Glucose/18]',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial or Venous Blood Gas',
        indication: '🔴 IMMEDIATE - Assess pH (>7.30 in HHS), bicarbonate; venous pH acceptable for monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Serum Ketones (Beta-hydroxybutyrate)',
        indication: '🔴 DIAGNOSTIC - Should be absent or trace in HHS; elevated suggests DKA or mixed picture',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urinalysis with Ketones',
        indication: 'Check for ketonuria (trace acceptable in HHS); also assess for UTI as precipitant',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Elevated WBC may indicate infection (though can be elevated from stress alone); assess for hemoconcentration',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Elevated lactate suggests tissue hypoperfusion or sepsis; helps assess severity',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: '🔴 IMMEDIATE - Rule out MI as precipitant; monitor for hypo/hyperkalemia effects (peaked T waves, prolonged QT)',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'Screen for pneumonia (common precipitant) and pulmonary edema',
        availability: 'BASIC'
      },
      {
        name: 'Blood and Urine Cultures',
        indication: 'Infection is most common precipitant; obtain before antibiotics if possible',
        availability: 'BASIC'
      },
      {
        name: 'Troponin',
        indication: 'Rule out MI as precipitant; may be elevated from demand ischemia',
        availability: 'BASIC'
      },
      {
        name: 'Lipase',
        indication: 'If abdominal pain present; pancreatitis can precipitate or complicate HHS',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: 'Assess baseline glycemic control; helps determine if new diagnosis or poorly controlled known diabetes',
        availability: 'BASIC'
      },
      {
        name: 'CT Head',
        indication: 'If focal neurological deficits, seizures, or altered mental status not improving; differentiate stroke from HHS',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Magnesium and Phosphate',
        indication: 'Often depleted in HHS; replace as needed; low Mg can cause arrhythmias',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ABCs: Assess airway, breathing, circulation; intubate if severe altered mental status with airway compromise',
        '⚡ IV ACCESS: Two large-bore IVs; central line if shock or prolonged ICU stay anticipated',
        '⚡ INITIAL FLUID BOLUS: 0.9% Normal Saline 1-2 L in first hour (15-20 mL/kg); this is THE most critical intervention',
        '⚡ CHECK POTASSIUM before insulin: If K+ <3.3 mEq/L, hold insulin and replace K+ first',
        '⚡ CONTINUOUS MONITORING: Cardiac monitor, pulse oximetry; Foley catheter for urine output',
        '⚡ FREQUENT LABS: Glucose every 1-2 hours; BMP every 2-4 hours; adjust therapy based on results',
        '⚡ START INSULIN only after initial fluid resuscitation and K+ ≥3.3 mEq/L',
        '⚡ SEARCH FOR PRECIPITANT: Infection workup, ECG, chest X-ray; treat underlying cause',
        '⚡ VTE PROPHYLAXIS: Enoxaparin or UFH unless contraindicated; HHS is hypercoagulable state',
        '⚡ ICU ADMISSION: Most HHS patients require ICU for close monitoring and frequent interventions'
      ],
      pharmacological: [
        {
          drug: 'Normal Saline 0.9% (NS)',
          indication: '🔴 FIRST-LINE FLUID - Cornerstone of treatment; replaces massive fluid deficit',
          dose: 'Initial: 1-2 L (15-20 mL/kg) in first hour; Then: 250-500 mL/hour (4-14 mL/kg/hr) adjusted to hemodynamics and urine output',
          route: 'IV',
          duration: 'Until hemodynamically stable and able to take oral fluids; average deficit 8-10 L replaced over 24-48 hours',
          contraindications: ['Heart failure (use cautiously with close monitoring)', 'Severe hypernatremia after initial resuscitation'],
          sideEffects: ['Volume overload', 'Pulmonary edema', 'Hyperchloremic metabolic acidosis'],
          alternatives: ['Switch to 0.45% NS when corrected Na+ >145 mEq/L']
        },
        {
          drug: 'Half-Normal Saline 0.45% (1/2 NS)',
          indication: 'After initial NS resuscitation if corrected sodium is elevated (>145 mEq/L)',
          dose: '250-500 mL/hour (4-14 mL/kg/hr)',
          route: 'IV',
          duration: 'Until sodium normalizes and glucose <300 mg/dL',
          contraindications: ['Hypotension', 'Hyponatremia', 'Initial resuscitation phase'],
          sideEffects: ['May not provide adequate volume resuscitation if used too early'],
          alternatives: ['0.9% NS']
        },
        {
          drug: 'Dextrose 5% in 0.45% NS (D5 1/2 NS)',
          indication: '🔴 CRITICAL - Add dextrose when glucose reaches 250-300 mg/dL to prevent hypoglycemia while continuing insulin',
          dose: '150-250 mL/hour',
          route: 'IV',
          duration: 'Continue until eating and transitioned to subcutaneous insulin',
          contraindications: ['Glucose still >300 mg/dL'],
          sideEffects: ['Hyperglycemia if rate too fast'],
          alternatives: ['D5NS', 'D5W with separate electrolyte infusion']
        },
        {
          drug: 'Regular Insulin (IV Infusion)',
          indication: '🔴 AFTER fluid resuscitation and K+ ≥3.3 - Lowers glucose; lower dose than DKA',
          dose: 'Bolus: 0.1 units/kg IV (optional, some protocols omit); Infusion: 0.05-0.1 units/kg/hour; Goal: Decrease glucose by 50-75 mg/dL per hour',
          route: 'IV continuous infusion',
          duration: 'Continue until: Osmolality <315 mOsm/kg AND glucose <300 mg/dL AND patient alert; then transition to SQ insulin',
          contraindications: ['K+ <3.3 mEq/L (replace potassium first)'],
          sideEffects: ['Hypoglycemia', 'Hypokalemia', 'Cerebral edema if glucose drops too fast'],
          alternatives: ['IM or SQ regular insulin if IV access difficult (less preferred)']
        },
        {
          drug: 'Potassium Chloride (KCl)',
          indication: '🔴 CRITICAL - Replace potassium before or with insulin; prevents life-threatening hypokalemia',
          dose: 'K+ <3.3 mEq/L: Hold insulin, give 20-40 mEq/hr until K+ >3.3; K+ 3.3-5.3: Add 20-40 mEq to each liter of IV fluid; K+ >5.3: Do not add K+, recheck in 2 hours',
          route: 'IV (peripheral: max 10 mEq/hr per line; central: up to 20-40 mEq/hr with monitoring)',
          duration: 'Continue replacement until K+ stable and eating',
          contraindications: ['K+ >5.3 mEq/L', 'Oliguria/anuria (until urine output established)'],
          sideEffects: ['Hyperkalemia if over-replaced or renal impairment', 'Phlebitis with peripheral infusion'],
          alternatives: ['Potassium phosphate (if phosphate also low)']
        },
        {
          drug: 'Potassium Phosphate',
          indication: 'Severe hypophosphatemia (<1.0 mg/dL) or symptomatic; can be used for part of K+ replacement',
          dose: '20-30 mEq phosphate IV over 6-12 hours',
          route: 'IV',
          duration: 'Until phosphate >1.0-1.5 mg/dL',
          contraindications: ['Hyperphosphatemia', 'Hypocalcemia', 'Renal failure'],
          sideEffects: ['Hypocalcemia', 'Hyperphosphatemia', 'Metastatic calcification'],
          alternatives: ['Oral phosphate supplementation when eating']
        },
        {
          drug: 'Enoxaparin (DVT Prophylaxis)',
          indication: '🔴 VTE PROPHYLAXIS - HHS is hypercoagulable; high risk of DVT, PE, stroke',
          dose: '40 mg SC once daily; 30 mg SC daily if CrCl <30 mL/min',
          route: 'Subcutaneous',
          duration: 'Throughout hospitalization until ambulatory',
          contraindications: ['Active bleeding', 'Severe thrombocytopenia', 'Recent major bleeding'],
          sideEffects: ['Bleeding', 'Thrombocytopenia'],
          alternatives: ['Unfractionated heparin 5000 units SC q8-12h']
        },
        {
          drug: 'Magnesium Sulfate',
          indication: 'Hypomagnesemia (common in HHS); may cause arrhythmias if not corrected',
          dose: '2-4 g IV over 1-2 hours, then 1-2 g every 4-6 hours as needed',
          route: 'IV',
          duration: 'Until magnesium level normal and patient eating',
          contraindications: ['Hypermagnesemia', 'Severe renal impairment'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Respiratory depression'],
          alternatives: ['Oral magnesium when eating']
        },
        {
          drug: 'Ceftriaxone (or broad-spectrum antibiotic)',
          indication: 'If infection suspected as precipitant (most common trigger); empiric coverage until cultures return',
          dose: '1-2 g IV daily',
          route: 'IV',
          duration: 'Based on identified infection and culture results',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Allergic reaction', 'C. difficile', 'Biliary sludging'],
          alternatives: ['Based on suspected source and local antibiogram']
        },
        {
          drug: 'Subcutaneous Insulin (Transition)',
          indication: 'Transition from IV to SQ insulin when: glucose <300, osmolality <315, mental status improved, patient eating',
          dose: 'Calculate total daily dose; give 80% of IV insulin requirement as daily SQ dose; split into basal (50%) and bolus (50%) OR resume home regimen with adjustments',
          route: 'Subcutaneous',
          duration: 'Long-term diabetes management',
          contraindications: ['Still critically ill', 'NPO status', 'Unstable'],
          sideEffects: ['Hypoglycemia if not adjusted properly'],
          alternatives: ['Continue IV insulin if patient not ready for transition']
        }
      ],
      nonPharmacological: [
        'ICU ADMISSION: Required for most HHS patients; close monitoring of glucose, electrolytes, mental status',
        'FREQUENT MONITORING: Glucose every 1-2 hours; BMP every 2-4 hours initially; strict I/O',
        'FOLEY CATHETER: For accurate urine output monitoring; target UOP >0.5-1 mL/kg/hr',
        'CARDIAC MONITORING: Arrhythmias from electrolyte shifts (hypokalemia, hypomagnesemia)',
        'NG TUBE: If altered mental status with vomiting risk; decompress stomach',
        'AVOID RAPID CORRECTION: Target glucose decrease 50-75 mg/dL/hr; osmolality decrease max 3 mOsm/kg/hr; rapid correction risks cerebral edema',
        'TREAT PRECIPITATING CAUSE: Antibiotics for infection, aspirin/anticoagulation for MI/stroke, etc.',
        'REHABILITATION: Physical therapy once stable; may have prolonged recovery'
      ],
      surgical: [
        'SOURCE CONTROL: If precipitating cause requires surgical intervention (e.g., cholecystitis, abscess drainage)',
        'AMPUTATION: May be needed if underlying diabetic foot infection/gangrene precipitated HHS'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: All HHS patients until stable; severe altered mental status; hemodynamic instability; significant electrolyte abnormalities',
        '🔴 IMMEDIATE ENDOCRINOLOGY: Complex cases; new diabetes diagnosis; difficult glucose management',
        '🔴 NEPHROLOGY: Acute kidney injury requiring dialysis; severe electrolyte disturbances',
        '🔴 CARDIOLOGY: MI as precipitant or complication',
        '🔴 NEUROLOGY: Persistent focal deficits; stroke suspected',
        '🟠 INFECTIOUS DISEASE: Complex or severe infections as precipitant',
        '🟠 DIABETES EDUCATION: Before discharge; all patients need diabetes self-management education'
      ],
      monitoring: [
        'BLOOD GLUCOSE: Every 1-2 hours on IV insulin; adjust insulin rate based on glucose trends',
        'BASIC METABOLIC PANEL: Every 2-4 hours initially (sodium, potassium, bicarbonate, creatinine); then every 4-6 hours',
        'SERUM OSMOLALITY: Every 4-6 hours (calculated if measured not available); target decrease ≤3 mOsm/kg/hr',
        'POTASSIUM: Check before starting insulin and every 2-4 hours; most critical electrolyte',
        'INPUT/OUTPUT: Strict I/O; Foley catheter; target UOP >0.5-1 mL/kg/hr',
        'VITAL SIGNS: Every 1-2 hours initially; continuous cardiac monitoring',
        'MENTAL STATUS: Serial neurological exams; mental status should improve as osmolality normalizes',
        'ANION GAP: If elevated, follow until closed (may indicate lactic acidosis or mixed DKA)'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
TRANSITION FROM IV TO SUBCUTANEOUS INSULIN
═══════════════════════════════════════════════════════════════════════════════

Criteria for transition:
• Blood glucose <300 mg/dL
• Serum osmolality <315 mOsm/kg
• Patient alert and able to eat
• Anion gap closed (if was elevated)
• Hemodynamically stable

Transition process:
1. Calculate subcutaneous insulin dose (approximately 80% of IV requirement/24hr)
2. Give first dose of long-acting insulin 2-4 hours BEFORE stopping IV insulin
3. Overlap IV and SQ insulin for 2-4 hours to prevent rebound hyperglycemia
4. Monitor glucose closely during transition

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE CRITERIA
═══════════════════════════════════════════════════════════════════════════════

• Glucose controlled on oral medications and/or subcutaneous insulin
• Eating and drinking adequately
• Electrolytes normal
• Mental status at baseline
• Precipitating cause treated
• Able to perform self-care or adequate support at home
• Diabetes education completed (or scheduled)
• Follow-up arranged

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

1-2 WEEKS POST-DISCHARGE:
• Review glucose logs
• Adjust insulin/medications as needed
• Reinforce sick day management
• Address any complications

ONGOING:
• HbA1c every 3 months until at goal, then every 6 months
• Comprehensive diabetes care (eye exams, foot exams, nephropathy screening)
• Cardiovascular risk reduction
• Diabetes self-management education

═══════════════════════════════════════════════════════════════════════════════
SICK DAY RULES (Prevent Future HHS)
═══════════════════════════════════════════════════════════════════════════════

• NEVER stop insulin, even if not eating
• Check blood glucose more frequently (every 4 hours)
• Stay hydrated - drink plenty of sugar-free fluids
• If unable to eat, consume liquids with carbohydrates
• Contact healthcare provider if: glucose >300 mg/dL for >24 hours, unable to keep fluids down, fever, confusion
• Know when to go to emergency department`
    },

    redFlags: [
      '🔴 CRITICAL: Severe altered mental status or coma - Osmolality likely >340; ICU admission; may need airway protection',
      '🔴 CRITICAL: Hemodynamic instability (SBP <90, HR >120) - Severe dehydration or sepsis; aggressive fluid resuscitation',
      '🔴 CRITICAL: Potassium <3.3 mEq/L - HOLD INSULIN; replace potassium first to avoid life-threatening arrhythmias',
      '🔴 CRITICAL: Potassium >6.0 mEq/L - Cardiac monitor; may need emergent treatment (calcium, insulin/glucose, kayexalate)',
      '🔴 CRITICAL: Seizures - May be from hyperosmolality or rapid correction; avoid rapid osmolality changes',
      '🔴 CRITICAL: Glucose not decreasing with insulin - Reassess insulin infusion, check for insulin resistance, ensure adequate fluids',
      '🔴 CRITICAL: Signs of cerebral edema (worsening mental status despite treatment) - Rare but serious; slow correction',
      '🟠 URGENT: Focal neurological deficits - CT head to rule out stroke; HHS can mimic stroke',
      '🟠 URGENT: Fever - Suggests infection as precipitant; full septic workup and empiric antibiotics',
      '🟠 URGENT: Chest pain or ECG changes - MI can precipitate or complicate HHS',
      '🟠 URGENT: Anion gap >16 - Suggests lactic acidosis or mixed DKA/HHS; treat underlying cause',
      '🟠 URGENT: Acute kidney injury (Cr rising despite fluids) - May need nephrology consultation'
    ],

    complications: [
      'DEATH: 10-20% mortality (higher than DKA); usually from underlying illness, cardiovascular events, or complications',
      'CEREBRAL EDEMA: Rare in adults but can occur; from overly rapid correction of osmolality; presents as declining mental status despite treatment',
      'THROMBOEMBOLISM: HHS is hypercoagulable; DVT, PE, stroke, MI all increased; prophylaxis essential',
      'ACUTE KIDNEY INJURY: From profound dehydration; usually prerenal; improves with fluids',
      'RHABDOMYOLYSIS: From immobility and hyperosmolality; check CK if suspected',
      'CARDIAC ARRHYTHMIAS: From electrolyte shifts (hypokalemia, hypomagnesemia, hypophosphatemia)',
      'ASPIRATION PNEUMONIA: From altered mental status; consider NG tube and aspiration precautions',
      'HYPOGLYCEMIA: From overly aggressive insulin therapy; monitor closely; add dextrose when glucose <300',
      'HYPOKALEMIA: From insulin therapy shifting K+ intracellularly; can cause arrhythmias and respiratory failure',
      'HEART FAILURE: From aggressive fluid resuscitation in patients with underlying cardiac disease',
      'ADULT RESPIRATORY DISTRESS SYNDROME (ARDS): Rare but reported complication',
      'DISSEMINATED INTRAVASCULAR COAGULATION (DIC): In severe cases with sepsis'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

• Overall mortality: 10-20% (significantly higher than DKA at 1-5%)
• Mortality has decreased with better ICU care but remains substantial
• Most deaths due to underlying illness rather than HHS itself
• Elderly patients and those with multiple comorbidities have highest mortality

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Advanced age
• Severe comorbidities (heart failure, renal failure, malignancy)
• Severe underlying precipitant (MI, stroke, severe sepsis)
• Profound alteration in mental status/coma at presentation
• Very high osmolality (>380 mOsm/kg)
• Delayed presentation and treatment
• Nursing home resident
• Need for mechanical ventilation

FAVORABLE PROGNOSIS:
• Younger patient
• No significant comorbidities
• Easily treatable precipitant (simple infection)
• Early recognition and treatment
• Mild mental status changes

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

• Survivors generally recover full neurological function (unlike stroke)
• Underlying diabetes requires lifelong management
• Risk of recurrence if sick day management not followed
• May have prolonged hospital stay and rehabilitation needs
• Increased risk of future cardiovascular events`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

GOOD DIABETES CONTROL:
• Regular glucose monitoring
• Medication adherence
• Regular follow-up with healthcare provider
• HbA1c monitoring and optimization

HYDRATION:
• Adequate fluid intake, especially in elderly and those with limited mobility
• Extra fluids during illness, hot weather, or increased activity

EARLY DIABETES DIAGNOSIS:
• Screening for diabetes in at-risk populations
• Education on symptoms of hyperglycemia

═══════════════════════════════════════════════════════════════════════════════
SICK DAY MANAGEMENT (Key to Prevention)
═══════════════════════════════════════════════════════════════════════════════

EDUCATE ALL DIABETES PATIENTS:
1. NEVER stop insulin or diabetes medications when sick
2. Check blood glucose every 4 hours during illness
3. Drink plenty of sugar-free fluids to prevent dehydration
4. If unable to eat, take liquids with carbohydrates (juice, regular soda)
5. Check urine ketones if glucose >300 mg/dL
6. Contact healthcare provider early if:
   - Unable to keep fluids down
   - Glucose persistently >300 mg/dL
   - Signs of dehydration
   - Confusion or lethargy
7. Know when to go to emergency department

═══════════════════════════════════════════════════════════════════════════════
SPECIAL POPULATIONS
═══════════════════════════════════════════════════════════════════════════════

ELDERLY/NURSING HOME RESIDENTS:
• Monitor for early signs of dehydration
• Ensure adequate fluid intake
• Regular glucose monitoring
• Low threshold for medical evaluation during illness

CAREGIVERS:
• Education on recognizing early HHS symptoms
• Know when to seek medical care
• Understand sick day rules

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT
═══════════════════════════════════════════════════════════════════════════════

• MEDICATION ACCESS: Ensure patients can obtain diabetes medications consistently
• EDUCATION: Community health workers can teach sick day rules
• GLUCOSE MONITORING: If glucometers unavailable, teach recognition of hyperglycemia symptoms
• HYDRATION: Emphasize importance of fluid intake during illness
• TRADITIONAL MEDICINE: Counsel on interactions and need to continue diabetes medications`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT HAPPENED TO YOU
═══════════════════════════════════════════════════════════════════════════════

You developed a serious condition called Hyperosmolar Hyperglycemic State (HHS). This happens when blood sugar becomes extremely high (usually over 600 mg/dL) and the body becomes severely dehydrated. It is a dangerous complication of diabetes that can be life-threatening if not treated.

HHS usually develops over several days to weeks and is most common in people with Type 2 diabetes, especially older adults.

What caused it:
• Very high blood sugar for a prolonged period
• Severe dehydration (your body lost a lot of water)
• Often triggered by an illness (like an infection), not taking diabetes medications, or a new diagnosis of diabetes

═══════════════════════════════════════════════════════════════════════════════
TREATMENT YOU RECEIVED
═══════════════════════════════════════════════════════════════════════════════

• Lots of intravenous (IV) fluids to rehydrate your body
• Insulin through an IV to lower your blood sugar
• Electrolyte replacement (especially potassium)
• Treatment for any underlying illness that triggered this
• Close monitoring in the intensive care unit

═══════════════════════════════════════════════════════════════════════════════
PREVENTING THIS FROM HAPPENING AGAIN
═══════════════════════════════════════════════════════════════════════════════

TAKE YOUR DIABETES MEDICATIONS:
• Take all your diabetes medications as prescribed, every day
• Never stop your insulin or diabetes pills, even when you are sick
• If you cannot afford your medications, talk to your doctor about alternatives

MONITOR YOUR BLOOD SUGAR:
• Check your blood sugar regularly (as often as your doctor recommends)
• Keep a log of your results to share with your doctor
• Know your target blood sugar range

STAY HYDRATED:
• Drink plenty of water every day (at least 8 glasses)
• Drink even more when you are sick, in hot weather, or exercising
• Avoid sugary drinks

═══════════════════════════════════════════════════════════════════════════════
SICK DAY RULES - VERY IMPORTANT
═══════════════════════════════════════════════════════════════════════════════

When you are sick (fever, cold, flu, infection, vomiting, diarrhea):

1. NEVER stop taking your insulin or diabetes medications

2. Check your blood sugar MORE often - every 4 hours

3. Drink LOTS of fluids:
   - If you can eat: drink water, sugar-free drinks
   - If you cannot eat: drink fluids with some sugar (juice, regular soda) to prevent low blood sugar

4. Try to eat your normal diet if possible

5. Rest and take care of yourself

6. Call your doctor if:
   - Your blood sugar stays above 300 mg/dL for more than 24 hours
   - You cannot keep any fluids down (vomiting)
   - You have fever that does not improve
   - You feel very weak, confused, or dizzy
   - You have chest pain, trouble breathing, or severe abdominal pain

7. Go to the emergency room if you feel very unwell or have any of the warning signs below

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - GO TO THE HOSPITAL IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

🔴 Confusion, extreme drowsiness, or difficulty waking up
🔴 Blood sugar above 400 mg/dL that does not come down
🔴 Not urinating for more than 8 hours
🔴 Extreme thirst that you cannot quench
🔴 Severe weakness, dizziness, or fainting
🔴 Rapid heartbeat
🔴 Difficulty breathing
🔴 Seizures

═══════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
═══════════════════════════════════════════════════════════════════════════════

• Keep all appointments with your doctor
• Get your HbA1c checked every 3 months (this shows your average blood sugar)
• Have regular checkups for your eyes, kidneys, and feet
• Learn as much as you can about managing your diabetes
• Consider joining a diabetes support group

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What is my target blood sugar range?
• How often should I check my blood sugar?
• What medications do I need to take?
• What should I do if I cannot afford my medications?
• What are the signs that I should call you or go to the hospital?
• Is there a diabetes education program I can attend?
• Should I see a diabetes specialist (endocrinologist)?`,

    references: [
      'Kitabchi AE, et al. Hyperglycemic crises in adult patients with diabetes. Diabetes Care. 2009;32(7):1335-43. doi:10.2337/dc09-9032',
      'American Diabetes Association. Diabetes Care in the Hospital: Standards of Medical Care in Diabetes-2023. Diabetes Care. 2023;46(Suppl 1):S267-S278.',
      'Pasquel FJ, Umpierrez GE. Hyperosmolar hyperglycemic state: a historic review of the clinical presentation, diagnosis, and treatment. Diabetes Care. 2014;37(11):3124-31.',
      'Fadini GP, et al. Characteristics and outcomes of the hyperglycemic hyperosmolar non-ketotic syndrome in a cohort of 51 consecutive cases at a single center. Diabetes Res Clin Pract. 2011;94(2):172-9.',
      'Joint British Diabetes Societies Inpatient Care Group. The Management of the Hyperosmolar Hyperglycaemic State (HHS) in Adults. 2022.',
      'Umpierrez GE, et al. Hyperglycemic crises: diabetic ketoacidosis and hyperglycemic hyperosmolar state. Endocrinol Metab Clin North Am. 2020;49(4):653-672.',
      'Wachtel TJ, et al. Predisposing factors for the diabetic hyperosmolar state. Arch Intern Med. 1987;147(3):499-501.',
      'Scott AR. Hyperosmolar hyperglycaemic syndrome. Diabet Med. 2015;32(6):714-24.',
      'Dhatariya KK, Vellanki P. Treatment of Diabetic Ketoacidosis (DKA)/Hyperglycemic Hyperosmolar State (HHS): Novel Advances in the Management of Hyperglycemic Crises. Curr Diab Rep. 2017;17(8):66.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 19. ACUTE LIVER FAILURE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '19',
    name: 'Acute Liver Failure',
    slug: 'acute-liver-failure',
    specialty: 'Gastroenterology',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K72.0',
    resourceLevel: 'ADVANCED',
    isEmergency: true,

    overview: `Acute Liver Failure (ALF), also called fulminant hepatic failure, is a rare but devastating syndrome characterized by rapid deterioration of liver function in a patient WITHOUT pre-existing liver disease, leading to coagulopathy (INR ≥1.5) and hepatic encephalopathy within 26 weeks of symptom onset. It is a medical emergency with high mortality requiring early recognition, supportive care, and consideration of urgent liver transplantation.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DEFINITION: Severe acute liver injury with coagulopathy (INR ≥1.5) AND hepatic encephalopathy in a patient WITHOUT pre-existing cirrhosis, within 26 weeks of illness onset

• CLASSIFICATION BY ONSET:
  - Hyperacute: Encephalopathy within 7 days of jaundice (best prognosis)
  - Acute: Encephalopathy 8-28 days after jaundice
  - Subacute: Encephalopathy 29 days - 12 weeks after jaundice (worst prognosis)

• MOST COMMON CAUSES:
  - Western countries: Acetaminophen (paracetamol) toxicity, drug-induced liver injury
  - Developing countries: Viral hepatitis (HBV, HEV), herbal/traditional medicines

• N-ACETYLCYSTEINE (NAC): Give to ALL patients with acetaminophen toxicity AND consider in all ALF regardless of etiology (may improve outcomes)

• CEREBRAL EDEMA is the leading cause of death: Monitor for signs; head of bed elevation, avoid hyperthermia, consider hypertonic saline; mannitol if herniation

• COAGULOPATHY: Do NOT correct with FFP/platelets unless active bleeding or invasive procedure planned; INR is prognostic marker

• HYPOGLYCEMIA is common: Frequent glucose monitoring; maintain glucose >70 mg/dL with IV dextrose

• INFECTION: Common and often without typical signs (no fever); low threshold for cultures and empiric antibiotics

• KING'S COLLEGE CRITERIA: Use to assess need for liver transplantation; consult transplant center early

• LIVER TRANSPLANTATION is the only definitive treatment for many patients; early referral to transplant center is essential

• MULTIORGAN FAILURE: ALF affects brain, kidneys, cardiovascular, respiratory, immune system; requires ICU-level care

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

MASSIVE HEPATOCYTE DEATH:
• Toxins, viruses, or immune-mediated injury cause widespread hepatocyte necrosis
• Loss of >80-90% of liver function leads to clinical syndrome
• Remaining hepatocytes cannot perform synthetic, metabolic, or detoxification functions

CONSEQUENCES OF LIVER FAILURE:
1. COAGULOPATHY: Decreased synthesis of clotting factors (II, V, VII, IX, X) and anticoagulants (protein C, S, antithrombin)
2. ENCEPHALOPATHY: Accumulation of ammonia and other neurotoxins; astrocyte swelling
3. HYPOGLYCEMIA: Impaired gluconeogenesis and glycogenolysis
4. JAUNDICE: Impaired bilirubin conjugation and excretion
5. IMPAIRED DETOXIFICATION: Accumulation of drugs, toxins, ammonia
6. IMMUNE DYSFUNCTION: Impaired Kupffer cell function; increased infection risk

CEREBRAL EDEMA (Most dangerous complication):
• Hyperammonemia → Astrocyte glutamine accumulation → Osmotic stress → Swelling
• Leads to increased intracranial pressure (ICP) and cerebral herniation
• Grade III-IV encephalopathy carries highest risk

SYSTEMIC INFLAMMATORY RESPONSE:
• Release of pro-inflammatory cytokines
• Vasodilation and hypotension (similar to sepsis)
• Multi-organ dysfunction
• Susceptibility to infections`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: ~2,000 cases/year in USA; rare but highly lethal
• Overall mortality without transplant: 40-80%
• With transplant: 1-year survival 60-80%
• Spontaneous survival (without transplant): Depends on etiology - acetaminophen ~70%, other causes ~25%

CAUSES BY REGION:

WESTERN COUNTRIES:
• Acetaminophen toxicity: 40-50% (most common in UK, USA)
• Drug-induced liver injury (non-acetaminophen): 10-15%
• Viral hepatitis: 10-15% (mostly HBV)
• Autoimmune hepatitis: 5%
• Indeterminate/unknown: 15-20%

DEVELOPING COUNTRIES (including Africa):
• Viral hepatitis: 50-70% (HBV most common, HEV in pregnancy)
• Herbal/traditional medicines: 10-20%
• Drug-induced: Variable
• Acetaminophen: Less common

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HEPATITIS B: Leading cause of ALF in Africa
  - High HBV prevalence (8-15% in sub-Saharan Africa)
  - Acute HBV infection
  - Reactivation in chronic carriers (immunosuppression, chemotherapy)
  - HBV/HDV co-infection (more severe)

• HEPATITIS E: Important cause, especially in:
  - PREGNANT WOMEN: 20-25% mortality (third trimester worst)
  - Outbreaks in refugee camps and areas with poor sanitation
  - Fecal-oral transmission

• TRADITIONAL/HERBAL MEDICINES:
  - Widely used in Africa; often unregulated
  - Unknown hepatotoxins
  - Frequently used before seeking medical care
  - May delay presentation

• OTHER CAUSES:
  - Drug-induced (anti-TB medications: isoniazid, rifampicin, pyrazinamide)
  - Mushroom poisoning (Amanita species)
  - Yellow fever (endemic in Africa)
  - Sepsis and ischemic hepatitis
  - Malaria-associated liver dysfunction (usually not true ALF)

• CHALLENGES IN AFRICA:
  - Limited ICU capacity
  - No liver transplant programs in most countries
  - Delayed presentation (traditional medicine use)
  - Laboratory limitations (INR, ammonia may not be available)
  - NAC availability may be limited
  - Blood product shortages
  - Limited access to renal replacement therapy

• HIV CONSIDERATIONS:
  - HIV/HBV co-infection common
  - Antiretroviral hepatotoxicity (nevirapine, efavirenz)
  - Immune reconstitution hepatitis
  - Opportunistic infections affecting liver

═══════════════════════════════════════════════════════════════════════════════
ETIOLOGY CLASSIFICATION
═══════════════════════════════════════════════════════════════════════════════

DRUG/TOXIN-INDUCED:
• Acetaminophen (paracetamol) - dose-dependent toxicity
• Idiosyncratic drug reactions (antibiotics, anticonvulsants, NSAIDs, statins)
• Herbal/dietary supplements
• Mushroom poisoning (Amanita phalloides)
• Industrial toxins (carbon tetrachloride)
• Recreational drugs (ecstasy, cocaine)

VIRAL:
• Hepatitis A (rare, usually self-limited)
• Hepatitis B (acute or reactivation)
• Hepatitis E (especially in pregnancy)
• Hepatitis D (co-infection with HBV)
• Herpes simplex virus (immunocompromised, pregnancy)
• Cytomegalovirus, Epstein-Barr virus
• Yellow fever, Dengue

VASCULAR:
• Ischemic hepatitis ("shock liver")
• Budd-Chiari syndrome (hepatic vein thrombosis)
• Veno-occlusive disease
• Acute heart failure (cardiac cirrhosis)

METABOLIC:
• Wilson's disease
• Acute fatty liver of pregnancy (AFLP)
• HELLP syndrome
• Reye's syndrome

OTHER:
• Autoimmune hepatitis
• Malignant infiltration
• Heat stroke
• Indeterminate (no cause found: 15-20%)`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

INITIAL SYMPTOMS (non-specific):
• Fatigue, malaise
• Nausea, vomiting
• Abdominal pain (right upper quadrant)
• Anorexia
• Fever (may suggest viral or drug-induced)

PROGRESSIVE SYMPTOMS:
• Jaundice (yellowing of skin and eyes)
• Dark urine, pale stools
• Confusion, disorientation (encephalopathy)
• Drowsiness, lethargy
• Easy bruising or bleeding (coagulopathy)
• Ascites (fluid accumulation)

HEPATIC ENCEPHALOPATHY SYMPTOMS:
• Grade I: Altered sleep pattern, mild confusion, difficulty concentrating
• Grade II: Lethargy, moderate confusion, inappropriate behavior
• Grade III: Marked confusion, somnolence but arousable, incoherent speech
• Grade IV: Coma, unresponsive to verbal or painful stimuli

ASK ABOUT:
• Medication history (especially acetaminophen, including combination products)
• Herbal/traditional medicine use
• Alcohol use
• Illicit drug use
• Mushroom ingestion
• Pregnancy status
• Recent travel (viral hepatitis exposure)
• Family history of liver disease (Wilson's disease)
• Recent illness or infection

═══════════════════════════════════════════════════════════════════════════════
SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Hypotension (vasodilation, hypovolemia)
• Tachycardia
• Tachypnea (metabolic acidosis, respiratory alkalosis compensation)
• Fever (may indicate infection) or hypothermia

GENERAL:
• Jaundice (may be absent early, especially in hyperacute)
• Altered mental status
• Fetor hepaticus (sweet, musty breath odor)
• Asterixis (flapping tremor) - in early encephalopathy

ABDOMINAL EXAMINATION:
• Tender hepatomegaly (early) OR
• Small, shrunken liver (late - indicates massive necrosis)
• Ascites (fluid wave, shifting dullness)
• Splenomegaly (less common than in chronic liver disease)

SKIN:
• Jaundice
• Spider angiomata (less common than chronic liver disease)
• Bruising, petechiae (coagulopathy)
• Excoriations (pruritus)

NEUROLOGICAL (Grade encephalopathy):
• Grade I: Subtle mental status changes, asterixis may be present
• Grade II: Asterixis, disorientation, inappropriate behavior
• Grade III: Marked asterixis, somnolence, hyperreflexia
• Grade IV: Coma, decerebrate posturing, absent reflexes

SIGNS OF CEREBRAL EDEMA (Emergency):
• Severe agitation or combativeness
• Hypertension with bradycardia (Cushing's reflex)
• Pupillary changes (dilated, unequal, or fixed)
• Decerebrate or decorticate posturing
• Seizures
• Respiratory irregularities`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

ACUTE LIVER FAILURE requires ALL of:
1. Evidence of severe acute liver injury (usually ALT/AST markedly elevated)
2. Coagulopathy (INR ≥1.5) not corrected by vitamin K
3. Hepatic encephalopathy (any grade)
4. Illness duration <26 weeks
5. No pre-existing cirrhosis or chronic liver disease

Note: If patient has encephalopathy with INR ≥1.5, this is ALF even if ALT/AST not dramatically elevated

═══════════════════════════════════════════════════════════════════════════════
SEVERITY CLASSIFICATION
═══════════════════════════════════════════════════════════════════════════════

BY TIME FROM JAUNDICE TO ENCEPHALOPATHY:
• HYPERACUTE: <7 days (typically acetaminophen, ischemic; best prognosis for spontaneous recovery but high risk of cerebral edema)
• ACUTE: 8-28 days (typically viral)
• SUBACUTE: 29 days - 12 weeks (typically drug-induced, indeterminate; worst prognosis)

HEPATIC ENCEPHALOPATHY GRADING (West Haven Criteria):
• Grade I: Trivial lack of awareness, shortened attention span, sleep disturbance
• Grade II: Lethargy, disorientation to time, inappropriate behavior, asterixis
• Grade III: Somnolent but arousable, marked confusion, incomprehensible speech
• Grade IV: Coma, unresponsive to pain

═══════════════════════════════════════════════════════════════════════════════
KING'S COLLEGE CRITERIA (Prognosis & Transplant Decision)
═══════════════════════════════════════════════════════════════════════════════

ACETAMINOPHEN-INDUCED ALF:
Transplant indicated if:
• Arterial pH <7.30 after resuscitation
OR ALL THREE:
• INR >6.5 (PT >100 seconds)
• Creatinine >3.4 mg/dL (>300 μmol/L)
• Grade III-IV encephalopathy

NON-ACETAMINOPHEN ALF:
Transplant indicated if:
• INR >6.5 (regardless of encephalopathy grade)
OR THREE of the following five criteria:
• Age <10 or >40 years
• Etiology: Non-A, non-B hepatitis, drug-induced, indeterminate
• Jaundice to encephalopathy >7 days
• INR >3.5
• Bilirubin >17.5 mg/dL (>300 μmol/L)

═══════════════════════════════════════════════════════════════════════════════
DETERMINING ETIOLOGY
═══════════════════════════════════════════════════════════════════════════════

THOROUGH HISTORY:
• All medications (including OTC, especially acetaminophen-containing products)
• Herbal supplements, traditional medicines
• Recreational drugs
• Alcohol intake
• Mushroom ingestion
• Recent travel, blood transfusions, sexual contacts
• Pregnancy status
• Family history (Wilson's disease)

LABORATORY WORKUP:
• Acetaminophen level (even if history negative)
• Viral hepatitis serologies (HAV IgM, HBsAg, HBV DNA, HBc IgM, HCV Ab, HEV IgM)
• Autoimmune markers (ANA, ASMA, IgG levels)
• Ceruloplasmin, 24-hour urine copper (Wilson's disease if <40 years)
• HIV testing
• HSV, CMV, EBV serologies (if immunocompromised or pregnant)
• Toxicology screen
• Pregnancy test in women of childbearing age`,

    differentialDiagnosis: [
      'Acute-on-chronic liver failure - Pre-existing cirrhosis with acute decompensation; look for stigmata of chronic liver disease',
      'Sepsis with liver dysfunction - Primary infection with secondary liver involvement; elevated lactate, infection source',
      'Ischemic hepatitis (shock liver) - Massive ALT/AST elevation (often >3000) with history of hypotension/shock; rapid improvement with resuscitation',
      'Alcoholic hepatitis - History of heavy alcohol use; AST:ALT ratio >2:1; usually no encephalopathy unless severe',
      'Severe acute viral hepatitis without ALF - Acute hepatitis without encephalopathy; may progress to ALF',
      'Malignant infiltration of liver - Diffuse tumor involvement; may have known malignancy; imaging shows infiltration',
      'Hemophagocytic lymphohistiocytosis (HLH) - Fever, cytopenias, hyperferritinemia, hepatosplenomegaly',
      'Acute Budd-Chiari syndrome - Hepatic vein thrombosis; ascites, hepatomegaly, abdominal pain; confirm with Doppler/CT',
      'HELLP syndrome - Pregnant patient; hemolysis, elevated liver enzymes, low platelets; hypertension, proteinuria',
      'Acute fatty liver of pregnancy - Third trimester; hypoglycemia, coagulopathy; requires urgent delivery',
      'Reye syndrome - Children; viral prodrome followed by ASA use; elevated ammonia, hypoglycemia',
      'Wilson disease (acute presentation) - Age <40; Kayser-Fleischer rings, low ceruloplasmin, hemolysis; Coombs-negative hemolytic anemia with ALF is highly suggestive'
    ],

    investigations: [
      {
        name: 'INR/PT',
        indication: '🔴 DIAGNOSTIC - INR ≥1.5 required for diagnosis; prognostic marker; do NOT correct with FFP unless bleeding',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (AST, ALT, Bilirubin, Albumin, ALP)',
        indication: '🔴 IMMEDIATE - AST/ALT often very high (>1000-10,000); bilirubin elevated; albumin may be low',
        availability: 'BASIC'
      },
      {
        name: 'Acetaminophen Level',
        indication: '🔴 IMMEDIATE - Check in ALL patients even if history negative; use Rumack-Matthew nomogram for toxicity',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Ammonia Level',
        indication: 'Elevated in hepatic encephalopathy; correlates with cerebral edema risk; arterial preferred but venous acceptable',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE and frequent - Hypoglycemia common and dangerous; check every 1-4 hours',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: '🔴 IMMEDIATE - Assess renal function (hepatorenal syndrome), electrolytes, acid-base status',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Assess acid-base status; respiratory alkalosis early; metabolic acidosis indicates poor prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Elevated lactate (especially >3.5) indicates poor prognosis in acetaminophen ALF; also suggests sepsis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Thrombocytopenia common; leukocytosis may indicate infection or be stress response',
        availability: 'BASIC'
      },
      {
        name: 'Viral Hepatitis Panel',
        indication: '🔴 ETIOLOGIC - HAV IgM, HBsAg, HBV DNA, anti-HBc IgM, anti-HCV, HEV IgM (especially if pregnant or from endemic area)',
        availability: 'BASIC'
      },
      {
        name: 'Autoimmune Markers (ANA, ASMA, IgG)',
        indication: 'Screen for autoimmune hepatitis; may require empiric corticosteroid treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Ceruloplasmin and Urine Copper',
        indication: 'Screen for Wilson disease in patients <40 years; low ceruloplasmin, elevated urine copper',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Factor V Level',
        indication: 'May be better prognostic marker than INR (not affected by vitamin K); <20% indicates poor prognosis',
        availability: 'ADVANCED'
      },
      {
        name: 'Blood and Urine Cultures',
        indication: 'Infection common; often without fever; obtain early and consider empiric antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal Ultrasound with Doppler',
        indication: 'Assess liver size, ascites, hepatic/portal vein patency (rule out Budd-Chiari)',
        availability: 'BASIC'
      },
      {
        name: 'CT Head (Non-contrast)',
        indication: 'If signs of cerebral edema (pupillary changes, posturing) or Grade III-IV encephalopathy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pregnancy Test',
        indication: 'All women of childbearing age - AFLP, HELLP, and HEV are pregnancy-related causes',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ICU ADMISSION: All patients with ALF require intensive care monitoring',
        '⚡ SECURE AIRWAY: Intubate if Grade III-IV encephalopathy or unable to protect airway',
        '⚡ IV ACCESS: Large-bore IVs; consider central venous access for monitoring and infusions',
        '⚡ N-ACETYLCYSTEINE (NAC): Start immediately for acetaminophen toxicity; consider for all ALF etiologies',
        '⚡ TREAT HYPOGLYCEMIA: IV dextrose (D10W infusion); monitor glucose every 1-4 hours',
        '⚡ AVOID SEDATION if possible: Makes encephalopathy assessment difficult; use short-acting agents if needed',
        '⚡ HEAD OF BED ELEVATION: 30 degrees to reduce intracranial pressure',
        '⚡ CONTACT TRANSPLANT CENTER EARLY: Even before King\'s criteria met; transfer may take time',
        '⚡ IDENTIFY AND TREAT PRECIPITANT: Acetaminophen antidote, antivirals, delivery if pregnancy-related',
        '⚡ INFECTION SURVEILLANCE: Low threshold for cultures and empiric antibiotics',
        '⚡ AVOID NEPHROTOXINS: No NSAIDs, aminoglycosides, contrast if possible',
        '⚡ PPI FOR STRESS ULCER PROPHYLAXIS: GI bleeding risk is high'
      ],
      pharmacological: [
        {
          drug: 'N-Acetylcysteine (NAC)',
          indication: '🔴 ESSENTIAL for acetaminophen toxicity; beneficial in non-acetaminophen ALF as well',
          dose: 'IV Protocol: Loading 150 mg/kg over 1 hour, then 50 mg/kg over 4 hours, then 100 mg/kg over 16 hours (total 300 mg/kg over 21 hours); Oral: 140 mg/kg loading, then 70 mg/kg every 4 hours x 17 doses',
          route: 'IV preferred (more reliable); PO if IV not available',
          duration: 'Continue until INR improving and acetaminophen undetectable; in non-acetaminophen ALF, continue until transplant or recovery',
          contraindications: ['Anaphylactoid reaction to NAC (rare; can slow infusion rate)'],
          sideEffects: ['Anaphylactoid reactions (flushing, pruritus, bronchospasm - usually with rapid infusion)', 'Nausea, vomiting'],
          alternatives: ['Oral NAC if IV unavailable']
        },
        {
          drug: 'Dextrose 10% (D10W) Infusion',
          indication: '🔴 PREVENT HYPOGLYCEMIA - Impaired gluconeogenesis makes hypoglycemia common and dangerous',
          dose: 'Continuous infusion to maintain glucose >70 mg/dL; typically 100-200 mL/hour; may need D20W or D50W boluses',
          route: 'IV continuous infusion',
          duration: 'Until liver function recovers and patient eating',
          contraindications: ['Hyperglycemia (adjust rate)'],
          sideEffects: ['Hyperglycemia', 'Volume overload'],
          alternatives: ['D20W through central line for higher concentration']
        },
        {
          drug: 'Lactulose',
          indication: 'Hepatic encephalopathy - reduces ammonia through gut clearance',
          dose: '30-45 mL PO/NG every 1-2 hours until bowel movement, then titrate to 2-4 soft stools/day; Enema: 300 mL in 700 mL water retained for 1 hour',
          route: 'PO, NG tube, or rectal',
          duration: 'Continue while encephalopathic',
          contraindications: ['Complete bowel obstruction'],
          sideEffects: ['Diarrhea', 'Dehydration', 'Electrolyte disturbances', 'Abdominal cramping'],
          alternatives: ['Rifaximin 550 mg BID (if available)']
        },
        {
          drug: 'Mannitol 20%',
          indication: '🔴 CEREBRAL EDEMA with signs of herniation - First-line osmotic therapy',
          dose: '0.5-1 g/kg IV bolus over 20 minutes; may repeat if serum osmolality <320 mOsm/kg',
          route: 'IV bolus',
          duration: 'As needed for intracranial hypertension episodes',
          contraindications: ['Anuria', 'Severe dehydration', 'Serum osmolality >320 mOsm/kg'],
          sideEffects: ['Volume overload', 'Rebound cerebral edema', 'Electrolyte disturbances', 'Acute kidney injury'],
          alternatives: ['Hypertonic saline 23.4%']
        },
        {
          drug: 'Hypertonic Saline 3% (or 23.4%)',
          indication: '🔴 CEREBRAL EDEMA - Maintain serum sodium 145-155 mEq/L for neuroprotection',
          dose: '3% NS infusion to target Na 145-155 mEq/L; 23.4% bolus: 30 mL over 10-20 minutes for acute herniation',
          route: 'IV (23.4% requires central line)',
          duration: 'Continuous in high-risk patients (Grade III-IV encephalopathy)',
          contraindications: ['Severe hypernatremia (Na >160)'],
          sideEffects: ['Hypernatremia', 'Volume overload', 'Central pontine myelinolysis (rare with gradual correction)'],
          alternatives: ['Mannitol']
        },
        {
          drug: 'Vitamin K (Phytonadione)',
          indication: 'Give to rule out vitamin K deficiency; will NOT correct coagulopathy of ALF',
          dose: '10 mg IV once',
          route: 'IV (slow infusion)',
          duration: 'Single dose; repeat if INR improves (suggests vitamin K deficiency, not ALF)',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Anaphylaxis (rare with slow IV infusion)'],
          alternatives: ['PO or SQ if IV not available']
        },
        {
          drug: 'Fresh Frozen Plasma (FFP)',
          indication: 'ONLY for active bleeding or before invasive procedures; do NOT give to correct INR alone (removes prognostic marker)',
          dose: '15-20 mL/kg',
          route: 'IV',
          duration: 'Until bleeding controlled or procedure completed',
          contraindications: ['No active bleeding and no procedure planned'],
          sideEffects: ['Volume overload', 'Transfusion reactions', 'TRALI'],
          alternatives: ['Prothrombin complex concentrate (PCC) for more rapid reversal with less volume']
        },
        {
          drug: 'Pantoprazole (or PPI)',
          indication: 'Stress ulcer prophylaxis - high risk of GI bleeding',
          dose: '40 mg IV once daily',
          route: 'IV',
          duration: 'Throughout ICU stay',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Generally well tolerated'],
          alternatives: ['H2 blockers (famotidine 20 mg IV BID)']
        },
        {
          drug: 'Broad-spectrum Antibiotics',
          indication: 'Empiric treatment if infection suspected; surveillance cultures often positive in ALF',
          dose: 'Piperacillin-tazobactam 4.5 g IV q6-8h OR ceftriaxone + metronidazole; adjust for local patterns',
          route: 'IV',
          duration: 'Based on culture results; discontinue if cultures negative and patient stable',
          contraindications: ['Known allergy'],
          sideEffects: ['C. difficile', 'Allergic reactions'],
          alternatives: ['Regimen based on suspected source and local antibiogram']
        },
        {
          drug: 'Propofol (for sedation if needed)',
          indication: 'Sedation for intubated patients or control of agitation; avoid long-acting sedatives',
          dose: 'Titrate to effect; typically 5-50 mcg/kg/min',
          route: 'IV continuous infusion',
          duration: 'While intubated and sedation needed',
          contraindications: ['Propofol infusion syndrome (prolonged use, high doses)'],
          sideEffects: ['Hypotension', 'Respiratory depression', 'Propofol infusion syndrome'],
          alternatives: ['Dexmedetomidine (less effect on encephalopathy assessment)']
        }
      ],
      nonPharmacological: [
        'ICU ADMISSION: Mandatory; close monitoring of mental status, glucose, hemodynamics',
        'AIRWAY MANAGEMENT: Early intubation for Grade III-IV encephalopathy; avoid hypoxia and hypercapnia',
        'INTRACRANIAL PRESSURE MONITORING: Consider in Grade III-IV encephalopathy awaiting transplant; controversial',
        'HEAD OF BED ELEVATION: 30 degrees; avoid neck flexion or compression',
        'TEMPERATURE CONTROL: Maintain normothermia or mild hypothermia (35-36°C); avoid hyperthermia (worsens cerebral edema)',
        'MINIMIZE STIMULATION: Quiet environment, minimize procedures, avoid agitation',
        'RENAL REPLACEMENT THERAPY: CRRT preferred for hepatorenal syndrome or volume overload; helps remove ammonia',
        'NUTRITION: Early enteral nutrition if possible; protein restriction NOT recommended (maintain 1.2-1.5 g/kg/day)',
        'LIVER SUPPORT DEVICES: Molecular Adsorbent Recirculating System (MARS), PROMETHEUS - limited availability, bridge to transplant',
        'PLASMA EXCHANGE: May be considered as bridge to transplant; removes toxins, replaces clotting factors'
      ],
      surgical: [
        'LIVER TRANSPLANTATION: Only definitive treatment for many patients; survival 60-80% at 1 year; must meet listing criteria (King\'s College or other)',
        'AUXILIARY LIVER TRANSPLANTATION: Partial liver graft to support patient while native liver regenerates; allows withdrawal of immunosuppression if native liver recovers',
        'LIVING DONOR LIVER TRANSPLANTATION: May be considered in urgent situations; complex ethical considerations'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE TRANSPLANT CENTER: ALL patients with ALF should be discussed with transplant center EARLY; do not wait for King\'s criteria',
        '🔴 IMMEDIATE ICU: All patients with ALF require ICU-level care',
        '🔴 IMMEDIATE HEPATOLOGY: For etiology workup, management guidance, and transplant evaluation',
        '🔴 NEPHROLOGY: If acute kidney injury requiring RRT; hepatorenal syndrome',
        '🔴 NEUROLOGY/NEUROSURGERY: If intracranial monitoring considered or signs of herniation',
        '🔴 POISON CONTROL: For acetaminophen or other toxin ingestion',
        '🔴 OBSTETRICS: If pregnancy-related cause (AFLP, HELLP) - urgent delivery often needed'
      ],
      monitoring: [
        'NEUROLOGICAL: Hourly assessment of mental status, pupillary response, Glasgow Coma Scale',
        'GLUCOSE: Every 1-4 hours; hypoglycemia is common and dangerous',
        'INR/COAGULATION: Every 6-12 hours; prognostic marker - do not treat to correct it',
        'BASIC METABOLIC PANEL: Every 6-12 hours; monitor renal function, electrolytes',
        'AMMONIA: Daily or as needed; correlates with cerebral edema risk',
        'LACTATE: Every 6-12 hours; persistently elevated indicates poor prognosis',
        'HEMODYNAMICS: Continuous; goal MAP >75 mmHg, CVP 8-12 mmHg',
        'TEMPERATURE: Continuous; maintain normothermia or mild hypothermia',
        'INFECTION SURVEILLANCE: Daily cultures if febrile or deteriorating; low threshold for empiric antibiotics',
        'LIVER ENZYMES: Daily; falling AST/ALT with rising INR may indicate massive necrosis (poor prognosis)'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
SPONTANEOUS RECOVERY
═══════════════════════════════════════════════════════════════════════════════

Patients who survive without transplant:
• Improvement usually begins within 5-10 days
• INR starts to fall, encephalopathy clears
• Full liver regeneration can occur
• May have complete recovery with no chronic liver disease

Monitoring after recovery:
• Weekly LFTs until normalized
• Screen for underlying liver disease
• Assess for biliary complications (strictures)

═══════════════════════════════════════════════════════════════════════════════
POST-TRANSPLANT CARE
═══════════════════════════════════════════════════════════════════════════════

• Lifelong immunosuppression
• Regular monitoring of graft function
• Screening for rejection, infection, malignancy
• Managed by transplant center

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM CONSIDERATIONS
═══════════════════════════════════════════════════════════════════════════════

For survivors without transplant:
• Avoid hepatotoxins (alcohol, unnecessary medications)
• Hepatitis A and B vaccination if not immune
• Monitor for chronic liver disease development
• Address underlying etiology (Wilson's treatment, autoimmune management)

For transplant recipients:
• Adherence to immunosuppression critical
• Regular follow-up with transplant team
• Screening for post-transplant complications
• Address recurrence risk if applicable (autoimmune, HBV)`
    },

    redFlags: [
      '🔴 CRITICAL: Grade III-IV hepatic encephalopathy - High risk of cerebral edema; consider intubation; ICP monitoring; transplant evaluation',
      '🔴 CRITICAL: Signs of cerebral herniation (pupil changes, posturing, Cushing reflex) - Emergent mannitol or hypertonic saline; neurosurgery consult',
      '🔴 CRITICAL: Arterial pH <7.30 (acetaminophen) - Meets King\'s criteria for transplant; urgent evaluation',
      '🔴 CRITICAL: INR >6.5 - Meets King\'s criteria for transplant; severe synthetic dysfunction',
      '🔴 CRITICAL: Hypoglycemia (<70 mg/dL) - Common and dangerous; immediate dextrose; frequent monitoring',
      '🔴 CRITICAL: Rising lactate (>3.5 mmol/L) - Indicates poor prognosis; often signals need for transplant',
      '🔴 CRITICAL: Multi-organ failure (renal, respiratory, cardiovascular) - Indicates severity; may affect transplant candidacy',
      '🟠 URGENT: Falling AST/ALT with rising INR - May indicate massive necrosis; paradoxically poor sign',
      '🟠 URGENT: Fever or sepsis - Common in ALF; low threshold for cultures and antibiotics',
      '🟠 URGENT: Acute kidney injury - Hepatorenal syndrome; may need RRT; complicates management',
      '🟠 URGENT: Seizures - May indicate cerebral edema; treat aggressively; worsens prognosis',
      '🟠 URGENT: Pregnancy with ALF - May be AFLP or HELLP; urgent delivery often needed'
    ],

    complications: [
      'CEREBRAL EDEMA AND INTRACRANIAL HYPERTENSION: Leading cause of death; more common in hyperacute and Grade III-IV encephalopathy; can cause herniation',
      'MULTI-ORGAN FAILURE: Cardiovascular (hypotension), respiratory (ARDS), renal (hepatorenal syndrome); indicates poor prognosis',
      'INFECTION/SEPSIS: Immune dysfunction makes infection common; often without typical signs; high index of suspicion needed',
      'HYPOGLYCEMIA: Impaired gluconeogenesis; can cause seizures, brain damage; requires frequent monitoring and IV dextrose',
      'COAGULOPATHY AND BLEEDING: Despite elevated INR, may also have increased clotting risk; GI bleeding, intracranial hemorrhage',
      'ACUTE KIDNEY INJURY/HEPATORENAL SYNDROME: 50-70% develop AKI; often requires RRT; indicator for transplant',
      'PANCREATITIS: Can occur as part of multi-organ failure',
      'RESPIRATORY FAILURE/ARDS: May require mechanical ventilation',
      'CARDIOVASCULAR COLLAPSE: Vasodilatory shock similar to sepsis; may require vasopressors',
      'ADRENAL INSUFFICIENCY: Relative adrenal insufficiency common; consider hydrocortisone if refractory hypotension'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

Overall mortality without transplant: 40-80% (varies by etiology and severity)

BY ETIOLOGY (survival without transplant):
• Acetaminophen: ~70% survival (best prognosis)
• Hepatitis A: ~60% survival
• Ischemic hepatitis: ~50% survival (if underlying cause addressed)
• Hepatitis B: ~30-40% survival
• Drug-induced (non-acetaminophen): ~25% survival
• Indeterminate: ~20% survival
• Wilson's disease: <5% survival without transplant (nearly always fatal)

WITH LIVER TRANSPLANTATION:
• 1-year survival: 60-80%
• 5-year survival: 70-75%

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSIS:
• Etiology: Non-acetaminophen drug-induced, Wilson's, indeterminate
• Grade III-IV encephalopathy
• INR >6.5 or Factor V <20%
• Age <10 or >40 years
• Jaundice to encephalopathy interval >7 days (subacute)
• Bilirubin >17.5 mg/dL
• Arterial lactate >3.5 mmol/L (acetaminophen)
• pH <7.30 (acetaminophen)
• Renal failure (creatinine >3.4 mg/dL)
• Cerebral edema

FAVORABLE PROGNOSIS:
• Acetaminophen etiology
• Hyperacute presentation (<7 days)
• Hepatitis A
• Younger age (but not <10)
• Improving INR and mental status
• Ischemic cause with resolution of underlying shock

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

Survivors without transplant:
• Most have complete liver regeneration
• Neurological recovery usually complete
• No increased risk of chronic liver disease (unless underlying cause persists)

Transplant recipients:
• Require lifelong immunosuppression
• Risk of rejection, infection, malignancy
• Good quality of life possible
• May have recurrence of underlying disease`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
ACETAMINOPHEN TOXICITY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• Maximum recommended dose: 4 g/day (lower in liver disease, alcohol use)
• Beware of combination products (many contain acetaminophen)
• Avoid alcohol when taking acetaminophen
• Educate patients on safe dosing
• Packaging limitations (blister packs reduce overdose severity)
• NAC available early for suspected overdose

═══════════════════════════════════════════════════════════════════════════════
VIRAL HEPATITIS PREVENTION
═══════════════════════════════════════════════════════════════════════════════

HEPATITIS A:
• Vaccination (especially travelers to endemic areas)
• Safe food and water practices

HEPATITIS B:
• Universal vaccination
• Screen pregnant women and treat if needed (prevent vertical transmission)
• Safe sex practices, avoid sharing needles
• Post-exposure prophylaxis

HEPATITIS E:
• Safe water and sanitation
• Avoid undercooked pork/game meat
• Vaccine available in some countries (not widely)
• Special caution in pregnancy

═══════════════════════════════════════════════════════════════════════════════
DRUG-INDUCED LIVER INJURY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• Monitor LFTs when starting hepatotoxic medications
• Educate patients on warning signs of liver injury
• Avoid unnecessary medications
• Caution with herbal/traditional medicines
• Stop suspect medication at first sign of liver injury

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT
═══════════════════════════════════════════════════════════════════════════════

• HBV VACCINATION: Expand coverage, especially birth dose
• CLEAN WATER: Prevent hepatitis E outbreaks
• EDUCATION: Dangers of traditional medicines, early health-seeking behavior
• HEALTHCARE INFRASTRUCTURE: Develop transplant programs, improve ICU capacity
• ACETAMINOPHEN AWARENESS: Educate on safe dosing, dangers of overdose`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS ACUTE LIVER FAILURE?
═══════════════════════════════════════════════════════════════════════════════

Acute liver failure is a rare but very serious condition where the liver suddenly stops working properly. Unlike chronic liver disease which develops over many years, acute liver failure happens quickly - over days to weeks.

The liver performs many vital functions including:
• Filtering toxins from the blood
• Making proteins that help blood clot
• Making substances that help digest food
• Storing energy
• Fighting infections

When the liver fails suddenly, all these functions are affected, which can be life-threatening.

═══════════════════════════════════════════════════════════════════════════════
CAUSES
═══════════════════════════════════════════════════════════════════════════════

The most common causes include:
• Acetaminophen (paracetamol) overdose - taking too much pain medication
• Viral infections (hepatitis A, B, or E)
• Reactions to medications or herbal supplements
• Autoimmune conditions
• Certain rare diseases

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK MEDICAL ATTENTION
═══════════════════════════════════════════════════════════════════════════════

If you or someone you know has taken too much acetaminophen or has liver disease and develops these symptoms, seek immediate medical attention:

🔴 Yellowing of the skin or eyes (jaundice)
🔴 Confusion or difficulty thinking clearly
🔴 Extreme tiredness or sleepiness
🔴 Nausea, vomiting, or loss of appetite
🔴 Pain in the upper right part of the abdomen
🔴 Easy bruising or bleeding
🔴 Swelling of the abdomen

═══════════════════════════════════════════════════════════════════════════════
ACETAMINOPHEN SAFETY
═══════════════════════════════════════════════════════════════════════════════

Acetaminophen (also called paracetamol or Tylenol) is a very common pain reliever found in many medications. It is safe when used correctly but can cause liver failure if too much is taken.

SAFE USE:
• Never take more than 4 grams (4000 mg) per day
• Check ALL your medications - many cold medicines, sleep aids, and prescription pain medications contain acetaminophen
• Do not drink alcohol while taking acetaminophen
• If you take acetaminophen regularly, ask your doctor about the right dose

IF YOU THINK YOU TOOK TOO MUCH:
• Go to the emergency room immediately
• Do not wait for symptoms to develop
• There is an antidote (NAC) that works best when given early
• Bring the medication bottle with you

═══════════════════════════════════════════════════════════════════════════════
PREVENTING LIVER DAMAGE
═══════════════════════════════════════════════════════════════════════════════

• Get vaccinated against hepatitis A and B
• Do not share needles or personal items that may have blood on them
• Practice safe sex
• Limit alcohol consumption
• Be cautious with herbal supplements and traditional medicines - they can damage the liver
• Only take medications as prescribed
• Tell your doctor about all medications you take

═══════════════════════════════════════════════════════════════════════════════
FOR FAMILY MEMBERS
═══════════════════════════════════════════════════════════════════════════════

If your loved one has acute liver failure:
• This is a very serious condition requiring intensive care
• The medical team is working to support the liver while it tries to recover
• In some cases, a liver transplant may be needed
• Stay informed and ask questions
• Take care of yourself too - this is stressful for families`,

    references: [
      'Lee WM, et al. Acute Liver Failure: Summary of a Workshop. Hepatology. 2008;47(4):1401-15.',
      'Stravitz RT, Lee WM. Acute Liver Failure. Lancet. 2019;394(10201):869-881. doi:10.1016/S0140-6736(19)31894-X',
      'European Association for the Study of the Liver. EASL Clinical Practical Guidelines on the management of acute (fulminant) liver failure. J Hepatol. 2017;66(5):1047-1081.',
      'Bernal W, et al. Acute Liver Failure. N Engl J Med. 2013;369(26):2525-34. doi:10.1056/NEJMra1208937',
      'O\'Grady JG, et al. Early indicators of prognosis in fulminant hepatic failure. Gastroenterology. 1989;97(2):439-45.',
      'Wendon J, et al. EASL Clinical Practical Guidelines: Liver Transplantation. J Hepatol. 2016;64(2):433-485.',
      'Polson J, Lee WM. AASLD Position Paper: The Management of Acute Liver Failure. Hepatology. 2005;41(5):1179-97.',
      'Bernal W, Wendon J. Acute Liver Failure. N Engl J Med. 2013;369:2525-2534.',
      'Lee WM. Acetaminophen (APAP) hepatotoxicity-Isn\'t it time for APAP to go away? J Hepatol. 2017;67(6):1324-1331.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 20. NEONATAL SEPSIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '20',
    name: 'Neonatal Sepsis',
    slug: 'neonatal-sepsis',
    specialty: 'Pediatrics',
    bodySystem: 'Systemic',
    icdCode: 'P36.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Neonatal sepsis is a systemic infection occurring in infants during the first 28 days of life. It is a leading cause of neonatal morbidity and mortality worldwide, particularly in low- and middle-income countries. Early recognition and prompt antibiotic treatment are essential, as neonates can deteriorate rapidly. Sepsis is classified as early-onset (EOS, ≤72 hours of life) or late-onset (LOS, >72 hours to 28 days), with different causative organisms and risk factors for each.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• EARLY-ONSET SEPSIS (EOS) ≤72 hours: Vertically transmitted from mother; Group B Streptococcus (GBS) and E. coli most common in developed countries; Klebsiella, E. coli, Staph aureus in Africa

• LATE-ONSET SEPSIS (LOS) >72 hours to 28 days: Nosocomial or community-acquired; Coagulase-negative Staph, S. aureus, Gram-negatives; associated with indwelling devices

• SIGNS ARE SUBTLE AND NONSPECIFIC: Temperature instability, poor feeding, lethargy, respiratory distress, apnea, irritability, hypotonia - have LOW threshold for evaluation

• "RULE OF 2s" FOR ANTIBIOTICS: Start antibiotics within 1 HOUR of suspicion; Ampicillin + Gentamicin for EOS; add Cefotaxime/Ceftriaxone if meningitis suspected or LOS

• BLOOD CULTURE BEFORE ANTIBIOTICS: But do NOT delay antibiotics waiting for culture results

• LUMBAR PUNCTURE: Perform in all neonates with suspected sepsis when stable enough; CSF culture essential; meningitis common in neonatal sepsis

• DURATION OF ANTIBIOTICS:
  - Culture-negative sepsis (suspected): 48-72 hours, then stop if cultures negative and baby well
  - Culture-positive bacteremia: 10-14 days
  - Meningitis: 14-21 days (GBS, Listeria) or 21 days (Gram-negative)

• SUPPORTIVE CARE IS CRITICAL: Respiratory support, fluid resuscitation, glucose management, thermoregulation

• MATERNAL RISK FACTORS (EOS): Prolonged rupture of membranes (>18 hours), maternal fever, chorioamnionitis, GBS colonization, prematurity

• MORTALITY REMAINS HIGH: 10-30% in developed countries; up to 50% in resource-limited settings

═══════════════════════════════════════════════════════════════════════════════
CLASSIFICATION
═══════════════════════════════════════════════════════════════════════════════

EARLY-ONSET SEPSIS (EOS):
• Onset: Birth to 72 hours (some definitions use 7 days)
• Transmission: Vertical (mother to baby during delivery)
• Organisms: GBS, E. coli, Listeria monocytogenes, other Gram-negatives
• Risk factors: Maternal chorioamnionitis, PROM, GBS colonization, prematurity

LATE-ONSET SEPSIS (LOS):
• Onset: >72 hours to 28 days of life
• Transmission: Nosocomial (hospital-acquired) or community-acquired
• Organisms: Coagulase-negative Staphylococci (CoNS), S. aureus, Gram-negatives, Candida
• Risk factors: Prematurity, central lines, mechanical ventilation, prolonged hospitalization

VERY LATE-ONSET SEPSIS:
• Onset: >28 days (in premature infants still hospitalized)
• Usually nosocomial; similar organisms to LOS

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

NEONATAL IMMUNE DEFICIENCY:
• Immature innate and adaptive immunity
• Decreased neutrophil function and reserves
• Low immunoglobulin levels (especially in preterm)
• Impaired complement function
• Limited T-cell responses

EARLY-ONSET SEPSIS MECHANISM:
1. Maternal colonization or infection (GBS, E. coli)
2. Ascending infection through ruptured membranes OR
3. Aspiration of infected amniotic fluid during delivery
4. Bacterial invasion of bloodstream
5. Systemic inflammatory response
6. Multi-organ dysfunction

LATE-ONSET SEPSIS MECHANISM:
1. Nosocomial: Bacteria colonize indwelling devices (central lines, ET tubes)
2. Community: Horizontal transmission from caregivers, environment
3. Bloodstream invasion
4. Often associated with procedures, devices

PROGRESSION TO SEPTIC SHOCK:
• Inadequate tissue perfusion
• Cardiovascular dysfunction
• Multi-organ failure
• High mortality if not rapidly treated`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Leading cause of neonatal mortality globally
• Estimated 3 million neonatal sepsis cases annually
• 400,000-700,000 neonatal deaths from sepsis each year
• 99% of deaths occur in low- and middle-income countries
• Case fatality rate: 10-30% (developed); 30-50% (developing countries)

INCIDENCE:
• EOS: 0.5-1 per 1,000 live births (developed countries); up to 38 per 1,000 in LMIC
• LOS: 2-5 per 1,000 live births; much higher in VLBW infants (20-30%)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• Neonatal sepsis is a LEADING CAUSE OF NEONATAL DEATH in Africa
• Estimated incidence: 5.5-38 per 1,000 live births
• Case fatality: 20-50% (significantly higher than developed countries)
• Accounts for 30-50% of neonatal deaths in many African hospitals

COMMON ORGANISMS IN AFRICA (differ from Western countries):
• GRAM-NEGATIVES predominate:
  - Klebsiella pneumoniae (often ESBL-producing)
  - Escherichia coli
  - Pseudomonas aeruginosa
  - Acinetobacter species
• GRAM-POSITIVES:
  - Staphylococcus aureus (including MRSA)
  - Group B Streptococcus (less common than in West)
  - Coagulase-negative Staphylococci
• OTHERS:
  - Salmonella species (more common in Africa)
  - Candida species (in prolonged NICU stays)

ANTIMICROBIAL RESISTANCE:
• Major challenge in Africa
• High rates of ESBL-producing Enterobacteriaceae
• Methicillin-resistant S. aureus (MRSA)
• Multidrug-resistant Gram-negatives
• First-line antibiotics often ineffective
• Limited access to second-line agents

UNIQUE CHALLENGES IN AFRICA:
• Home deliveries without skilled attendants
• Poor hygiene during delivery and cord care
• Delayed health-seeking behavior
• Limited laboratory capacity (blood cultures often unavailable)
• Overcrowded NICUs with infection control challenges
• Limited availability of antibiotics
• Shortage of trained neonatal nurses and doctors
• Lack of equipment (incubators, ventilators, IV pumps)

RISK FACTORS MORE COMMON IN AFRICA:
• Home delivery
• Traditional cord care practices (application of substances to umbilical stump)
• Prolonged/obstructed labor
• Maternal infections (including HIV, malaria)
• Low birth weight (high rates of prematurity)
• Birth asphyxia
• Poor nutrition

CORD CARE:
• Application of cow dung, ash, or other substances to umbilical cord stump
• Associated with tetanus and bacterial sepsis
• WHO recommends chlorhexidine application in high-mortality settings

═══════════════════════════════════════════════════════════════════════════════
RISK FACTORS
═══════════════════════════════════════════════════════════════════════════════

MATERNAL (EOS):
• Chorioamnionitis
• Prolonged rupture of membranes (>18 hours)
• GBS colonization without prophylaxis
• Maternal fever during labor
• Preterm delivery
• Urinary tract infection
• Multiple vaginal examinations
• Prolonged labor

NEONATAL:
• Prematurity (strongest risk factor)
• Low birth weight
• Male sex
• Low Apgar scores
• Invasive procedures
• Mechanical ventilation
• Central venous catheters
• Parenteral nutrition
• Prolonged hospitalization
• Multiple births
• Galactosemia (predisposes to E. coli sepsis)`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
KEY PRINCIPLE: SIGNS ARE SUBTLE AND NONSPECIFIC
═══════════════════════════════════════════════════════════════════════════════

The hallmark of neonatal sepsis is NONSPECIFIC presentation. Any change from baseline in a neonate should raise concern for sepsis.

The classic teaching: "A septic neonate just doesn't look right"

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS (Reported by Caregivers)
═══════════════════════════════════════════════════════════════════════════════

FEEDING:
• Poor feeding (most common early sign)
• Weak suck
• Feeding intolerance
• Vomiting

ACTIVITY:
• Lethargy, decreased activity
• Irritability
• Weak cry or high-pitched cry
• "Not acting right"

TEMPERATURE:
• Fever (uncommon in neonates)
• "Baby feels cold" (hypothermia more common, especially in preterm)

BREATHING:
• Breathing fast
• Difficulty breathing
• Episodes of not breathing (apnea)
• Grunting

OTHER:
• Abdominal distension
• Diarrhea
• Skin changes (rash, jaundice)

═══════════════════════════════════════════════════════════════════════════════
SIGNS (Physical Examination)
═══════════════════════════════════════════════════════════════════════════════

TEMPERATURE:
• Hypothermia (<36°C) - more common, especially in preterm
• Hyperthermia (>38°C) - more common in term infants
• Temperature instability

CARDIOVASCULAR:
• Tachycardia (HR >160-180)
• Bradycardia (HR <100) - ominous sign
• Poor perfusion (capillary refill >3 seconds)
• Hypotension (late sign)
• Mottled skin
• Cold extremities
• Weak pulses

RESPIRATORY:
• Tachypnea (RR >60)
• Grunting (sign of respiratory distress)
• Nasal flaring
• Retractions (subcostal, intercostal, suprasternal)
• Apnea (common in preterm; very concerning)
• Cyanosis

NEUROLOGICAL:
• Lethargy, hypotonia
• Irritability
• Bulging fontanelle (suggests meningitis)
• Seizures
• High-pitched cry
• Abnormal tone

GASTROINTESTINAL:
• Abdominal distension
• Feeding intolerance
• Vomiting
• Ileus
• Hepatomegaly (with congenital infection)

SKIN:
• Jaundice (especially with early onset)
• Pallor
• Petechiae, purpura (DIC)
• Sclerema (hardening of skin - ominous)
• Umbilical erythema, discharge (omphalitis)
• Pustules, bullae

METABOLIC:
• Hypoglycemia or hyperglycemia
• Metabolic acidosis

═══════════════════════════════════════════════════════════════════════════════
RED FLAGS - IMMEDIATE ACTION REQUIRED
═══════════════════════════════════════════════════════════════════════════════

• Apnea or severe respiratory distress
• Bradycardia
• Hypotension or shock
• Seizures
• Bulging fontanelle
• Sclerema
• Petechiae/purpura
• Severe hypothermia (<35°C)`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

KEY PRINCIPLE: Have a LOW threshold for sepsis workup in neonates. The cost of missing sepsis far outweighs the cost of overtreatment.

SEPSIS WORKUP ("Rule Out Sepsis" or "Septic Screen"):
1. Blood culture (BEFORE antibiotics)
2. Complete blood count with differential
3. C-reactive protein (CRP) or Procalcitonin
4. Lumbar puncture (when stable)
5. Urine culture (for LOS)
6. Chest X-ray (if respiratory symptoms)

═══════════════════════════════════════════════════════════════════════════════
CLINICAL CRITERIA
═══════════════════════════════════════════════════════════════════════════════

DEFINITE SEPSIS (Culture-proven):
• Positive blood, CSF, or urine culture with clinical signs

PROBABLE SEPSIS (Clinical):
• Clinical signs of sepsis PLUS
• Abnormal laboratory values (elevated CRP, abnormal WBC, I:T ratio >0.2)
• Negative or no cultures obtained

POSSIBLE SEPSIS:
• Risk factors for sepsis with some clinical signs
• Treated empirically pending workup

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

BLOOD CULTURE:
• Gold standard for diagnosis
• Draw before antibiotics if possible
• Minimum 1 mL blood (ideally 2 mL)
• Single culture usually adequate (two if catheter-related sepsis suspected)
• 24-48 hours for preliminary results

COMPLETE BLOOD COUNT:
• WBC <5,000 or >30,000 (age-specific values) - concerning
• Neutropenia (<1,500) - more specific for sepsis
• Bandemia (immature:total neutrophil ratio >0.2)
• Thrombocytopenia (<100,000) - late finding, poor prognosis

C-REACTIVE PROTEIN (CRP):
• Rises 6-8 hours after infection onset
• Peak at 24-48 hours
• Serial CRP more useful than single value
• Sensitivity improves with repeat testing at 24-48 hours
• Used to guide duration of antibiotics

PROCALCITONIN (PCT):
• Rises earlier than CRP (within 2-4 hours)
• More specific for bacterial infection
• Helpful in deciding to stop antibiotics
• May not be available in all settings

LUMBAR PUNCTURE:
• Essential in all neonates with suspected sepsis (when stable)
• Meningitis occurs in 10-15% of neonates with sepsis
• Blood cultures alone miss 15-30% of meningitis cases
• CSF: Cell count, glucose, protein, Gram stain, culture

CSF FINDINGS IN NEONATAL MENINGITIS:
• WBC >20-30 cells/mm³ (lower thresholds than older children)
• Protein >100-150 mg/dL
• Glucose <50% of serum glucose
• Gram stain positive in 60-80%

URINE CULTURE:
• For late-onset sepsis
• Catheterized or suprapubic specimen (NOT bag specimen)
• UTI more common in LOS

CHEST X-RAY:
• If respiratory symptoms present
• Rule out pneumonia

═══════════════════════════════════════════════════════════════════════════════
SEPSIS CALCULATORS
═══════════════════════════════════════════════════════════════════════════════

KAISER PERMANENTE EOS CALCULATOR:
• Uses maternal risk factors and clinical exam
• Provides probability of EOS
• Helps guide need for antibiotics and blood culture
• Available online: https://neonatalsepsiscalculator.kaiserpermanente.org`,

    differentialDiagnosis: [
      'Transient tachypnea of newborn (TTN) - Respiratory distress soon after birth, usually resolves within 24-72 hours; normal WBC/CRP',
      'Respiratory distress syndrome (RDS) - Preterm infant, progressive respiratory distress, CXR shows ground-glass appearance',
      'Meconium aspiration syndrome - History of meconium-stained fluid, respiratory distress, CXR shows patchy infiltrates',
      'Congenital pneumonia - May overlap with/be part of EOS; CXR infiltrates; treat as sepsis',
      'Congenital heart disease - Cyanosis, murmur, abnormal pulses; echo for diagnosis',
      'Inborn errors of metabolism - Lethargy, poor feeding, acidosis; specific metabolic workup',
      'Neonatal abstinence syndrome - Maternal drug use history; irritability, tremors, feeding difficulty',
      'Hypoglycemia - Lethargy, jitteriness, seizures; check glucose; responds to glucose',
      'Hypoxic-ischemic encephalopathy - History of perinatal asphyxia; neurological signs; may have concurrent sepsis',
      'Necrotizing enterocolitis (NEC) - Abdominal distension, bloody stools, feeding intolerance; may coexist with sepsis',
      'Congenital TORCH infections - Hepatosplenomegaly, jaundice, rash, microcephaly; specific serologies',
      'Neonatal herpes - Vesicular rash, seizures, hepatitis; maternal history; PCR/culture for HSV'
    ],

    investigations: [
      {
        name: 'Blood Culture',
        indication: '🔴 GOLD STANDARD - Draw BEFORE antibiotics; minimum 1 mL; do not delay antibiotics waiting for blood draw',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count with Differential',
        indication: '🔴 IMMEDIATE - WBC, ANC, Band count, I:T ratio, platelets; single value less reliable than serial CBCs',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🔴 Serial values most useful - At 0 and 24-48 hours; helps guide duration of antibiotics; sensitivity improves over time',
        availability: 'BASIC'
      },
      {
        name: 'Procalcitonin (PCT)',
        indication: 'Rises earlier than CRP; more specific for bacterial infection; helpful to stop antibiotics; may not be available everywhere',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture and CSF Analysis',
        indication: '🔴 ESSENTIAL - All neonates with suspected sepsis when stable; meningitis present in 10-15%; cell count, protein, glucose, Gram stain, culture',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE - Hypoglycemia and hyperglycemia common in sepsis; frequent monitoring required',
        availability: 'BASIC'
      },
      {
        name: 'Blood Gas (Arterial or Capillary)',
        indication: 'Assess acid-base status; metabolic acidosis common in sepsis; respiratory status',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes (Na, K, Ca)',
        indication: 'Electrolyte disturbances common; hyponatremia (SIADH), hypocalcemia',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT, aPTT)',
        indication: 'If bleeding, petechiae, or DIC suspected; DIC common in severe sepsis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urine Culture',
        indication: 'For LATE-ONSET sepsis; catheterized or suprapubic specimen; UTI common in LOS',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If respiratory symptoms; rule out pneumonia; may show cardiomegaly in septic shock',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal X-ray',
        indication: 'If abdominal distension or feeding intolerance; rule out NEC (pneumatosis intestinalis)',
        availability: 'BASIC'
      },
      {
        name: 'Bilirubin (Total and Direct)',
        indication: 'If jaundiced; sepsis can cause both direct and indirect hyperbilirubinemia',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: 'If hepatomegaly or suspected disseminated infection',
        availability: 'BASIC'
      },
      {
        name: 'Surface Cultures (Ear, Umbilicus, Throat)',
        indication: 'Limited value; may help identify colonizing organisms; not recommended routinely',
        availability: 'BASIC'
      },
      {
        name: 'Lactate',
        indication: 'Elevated in septic shock; marker of tissue hypoperfusion; prognostic',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ RECOGNIZE SICK NEONATE: Any change from baseline - decreased activity, poor feeding, temperature instability, respiratory distress',
        '⚡ STABILIZE: ABCs - Airway, Breathing, Circulation; oxygen, IV access, glucose check',
        '⚡ BLOOD CULTURE: Draw before antibiotics but DO NOT DELAY antibiotics waiting for culture',
        '⚡ ANTIBIOTICS WITHIN 1 HOUR: Ampicillin + Gentamicin for EOS; modify based on local patterns and severity',
        '⚡ LUMBAR PUNCTURE: When neonate stable; meningitis common; do not omit',
        '⚡ GLUCOSE MANAGEMENT: Check immediately; treat hypoglycemia with IV dextrose',
        '⚡ THERMOREGULATION: Keep warm; hypothermia worsens outcomes; incubator or skin-to-skin',
        '⚡ FLUID RESUSCITATION: NS 10-20 mL/kg bolus for poor perfusion; reassess after each bolus',
        '⚡ RESPIRATORY SUPPORT: Oxygen, CPAP, or mechanical ventilation as needed',
        '⚡ MONITOR CLOSELY: Vital signs every 1-4 hours; continuous monitoring if available'
      ],
      pharmacological: [
        {
          drug: 'Ampicillin',
          indication: '🔴 FIRST-LINE for EOS - Covers GBS, Listeria, Enterococcus, some Gram-negatives',
          dose: 'Sepsis: 50 mg/kg/dose IV; Meningitis: 100 mg/kg/dose IV; Frequency: q12h for ≤7 days age, q8h for >7 days age',
          route: 'IV',
          duration: 'Culture-negative: 48-72 hours then stop; Culture-positive: 10-14 days; Meningitis: 14-21 days',
          contraindications: ['Penicillin allergy (rare in neonates)'],
          sideEffects: ['Rash', 'Diarrhea', 'Candidiasis with prolonged use'],
          alternatives: ['Penicillin G for confirmed GBS']
        },
        {
          drug: 'Gentamicin',
          indication: '🔴 FIRST-LINE for EOS (with Ampicillin) - Covers Gram-negatives; synergistic with Ampicillin for GBS/Listeria',
          dose: '4-5 mg/kg/dose IV; Extended interval dosing: q24h for term, q36-48h for preterm (varies by gestational age)',
          route: 'IV (over 30 minutes)',
          duration: 'Usually 48-72 hours if cultures negative; continue with Ampicillin if Gram-negative bacteremia',
          contraindications: ['Renal impairment (adjust dose)'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity', 'Neuromuscular blockade'],
          alternatives: ['Amikacin (if resistant)']
        },
        {
          drug: 'Cefotaxime',
          indication: '🔴 Add for suspected MENINGITIS or severe sepsis; better CNS penetration than gentamicin',
          dose: '50 mg/kg/dose IV; q12h for ≤7 days age, q8h for >7 days age; Meningitis: may use 50 mg/kg q6-8h',
          route: 'IV',
          duration: 'Meningitis: 14-21 days (Gram-positive) or 21 days (Gram-negative)',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'C. difficile (rare in neonates)'],
          alternatives: ['Ceftriaxone (AVOID in jaundiced neonates - displaces bilirubin)']
        },
        {
          drug: 'Ceftriaxone',
          indication: 'Alternative to Cefotaxime for meningitis; convenient once-daily dosing',
          dose: '50 mg/kg/dose IV once daily; Meningitis: 100 mg/kg/day (may divide BID)',
          route: 'IV',
          duration: 'Based on organism and site',
          contraindications: ['⚠ AVOID in jaundiced neonates (displaces bilirubin)', 'Do not give with calcium-containing IV fluids'],
          sideEffects: ['Biliary sludging', 'Bilirubin displacement'],
          alternatives: ['Cefotaxime preferred in neonates']
        },
        {
          drug: 'Vancomycin',
          indication: '🔴 For LATE-ONSET sepsis (covers CoNS, MRSA); suspected catheter-related infection',
          dose: '15 mg/kg/dose IV; q12h for ≥35 weeks, q8-18h for preterm (varies by PMA and renal function)',
          route: 'IV (infuse over 60 minutes)',
          duration: 'Based on organism; usually 7-14 days; remove catheter if possible',
          contraindications: ['Vancomycin allergy (rare)'],
          sideEffects: ['Red man syndrome (slow infusion)', 'Nephrotoxicity', 'Ototoxicity'],
          alternatives: ['Linezolid for VRE']
        },
        {
          drug: 'Meropenem (or Imipenem)',
          indication: 'Severe sepsis, suspected ESBL organisms, failure of first-line antibiotics; common need in Africa',
          dose: 'Meropenem: 20 mg/kg/dose IV q8-12h; Meningitis: 40 mg/kg/dose q8h',
          route: 'IV',
          duration: 'Based on culture results; usually 10-14 days',
          contraindications: ['Carbapenem allergy'],
          sideEffects: ['Seizures (more with imipenem)', 'Diarrhea', 'Rash'],
          alternatives: ['Based on culture sensitivities']
        },
        {
          drug: 'Acyclovir',
          indication: '🔴 If HSV infection suspected - Vesicles, seizures, maternal history, liver dysfunction, severe presentation',
          dose: '20 mg/kg/dose IV every 8 hours',
          route: 'IV (infuse over 1 hour with adequate hydration)',
          duration: 'Disseminated/CNS: 21 days; Skin/eye/mouth: 14 days',
          contraindications: ['Renal impairment (adjust dose)'],
          sideEffects: ['Nephrotoxicity (ensure hydration)', 'Neutropenia'],
          alternatives: ['No alternative for neonatal HSV']
        },
        {
          drug: 'Fluconazole',
          indication: 'Candida sepsis (especially in VLBW infants with prolonged antibiotics, TPN, central lines)',
          dose: 'Loading: 25 mg/kg; Maintenance: 12 mg/kg/dose q24-72h (varies by age)',
          route: 'IV or PO',
          duration: 'Candidemia: at least 14 days after negative blood culture; Meningitis: longer',
          contraindications: ['Azole allergy'],
          sideEffects: ['Hepatotoxicity', 'Rash'],
          alternatives: ['Amphotericin B for resistant Candida']
        },
        {
          drug: 'Normal Saline (0.9% NS)',
          indication: '🔴 FLUID RESUSCITATION - For poor perfusion, hypotension',
          dose: '10-20 mL/kg bolus over 30-60 minutes; reassess after each bolus; may repeat up to 40-60 mL/kg total',
          route: 'IV',
          duration: 'Until perfusion improves',
          contraindications: ['Volume overload', 'Heart failure'],
          sideEffects: ['Volume overload', 'Hyperchloremic acidosis'],
          alternatives: ['Lactated Ringer']
        },
        {
          drug: 'Dopamine (or Epinephrine)',
          indication: 'Septic shock unresponsive to fluid resuscitation',
          dose: 'Dopamine: Start 5 mcg/kg/min, increase to 10-20 mcg/kg/min; Epinephrine: 0.05-0.3 mcg/kg/min',
          route: 'IV continuous infusion (central line preferred)',
          duration: 'Until shock resolved',
          contraindications: ['Uncorrected hypovolemia'],
          sideEffects: ['Tachycardia', 'Arrhythmias', 'Peripheral ischemia'],
          alternatives: ['Norepinephrine', 'Dobutamine for cardiac dysfunction']
        }
      ],
      nonPharmacological: [
        'THERMOREGULATION: Maintain temperature 36.5-37.5°C; incubator, radiant warmer, or Kangaroo Mother Care (KMC); hypothermia worsens outcomes',
        'RESPIRATORY SUPPORT: Oxygen for hypoxemia; CPAP for respiratory distress; mechanical ventilation for respiratory failure or apnea',
        'GLUCOSE MONITORING: Check every 4-6 hours; IV dextrose (D10W at 60-80 mL/kg/day) to maintain glucose >45-50 mg/dL',
        'NUTRITIONAL SUPPORT: Trophic feeds if tolerated (breast milk preferred); TPN if NPO prolonged; do not starve the septic neonate',
        'HAND HYGIENE: Strict hand hygiene before and after every patient contact; most important infection control measure',
        'INFECTION CONTROL: Cohorting of infected infants; clean equipment; limit visitors; avoid overcrowding',
        'REMOVE INVASIVE DEVICES: Remove central lines, urinary catheters, ET tubes as soon as possible',
        'SKIN CARE: Gentle handling; avoid unnecessary skin breaks; umbilical cord care (chlorhexidine in high-mortality settings)',
        'FAMILY-CENTERED CARE: Keep parents informed; encourage skin-to-skin care when stable; support breastfeeding'
      ],
      surgical: [
        'DRAINAGE OF ABSCESS: If localized abscess (skin, liver, brain) identified',
        'CENTRAL LINE REMOVAL: Remove infected central venous catheters',
        'SURGERY FOR NEC: If pneumoperitoneum (perforation) or failure of medical management',
        'VP SHUNT: May be needed for post-meningitis hydrocephalus'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE NICU/HIGHER LEVEL CARE: Septic shock, respiratory failure, severe prematurity, seizures, need for ventilation',
        '🔴 IMMEDIATE PEDIATRIC SURGERY: Suspected NEC with perforation, abscess requiring drainage',
        '🔴 PEDIATRIC INFECTIOUS DISEASE: Unusual organisms, multidrug-resistant infections, failure to respond to treatment',
        '🔴 PEDIATRIC NEUROLOGY: Seizures, suspected meningitis complications',
        '🟠 AUDIOLOGY: Follow-up hearing screen after meningitis or aminoglycoside exposure',
        '🟠 DEVELOPMENTAL FOLLOW-UP: All survivors of neonatal sepsis, especially with meningitis'
      ],
      monitoring: [
        'VITAL SIGNS: Every 1-4 hours (HR, RR, BP, Temperature, SpO2); continuous monitoring if available',
        'GLUCOSE: Every 4-6 hours; more frequently if hypo/hyperglycemia',
        'INPUT/OUTPUT: Strict measurement; urine output target >1 mL/kg/hr',
        'WEIGHT: Daily; assess fluid status',
        'CLINICAL STATUS: Feeding tolerance, activity, perfusion, mental status',
        'LABORATORY: CBC and CRP at 24-48 hours; repeat blood culture if persistent symptoms',
        'AMINOGLYCOSIDE LEVELS: Trough level before 3rd dose (gentamicin trough <2 mcg/mL)',
        'VANCOMYCIN LEVELS: Trough level before 4th dose if prolonged use expected (target 10-15 mcg/mL)'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
DURATION OF ANTIBIOTIC THERAPY
═══════════════════════════════════════════════════════════════════════════════

CULTURE-NEGATIVE "RULE OUT SEPSIS":
• If blood culture negative at 48-72 hours AND
• Baby clinically well AND
• CRP normal or normalizing
→ STOP antibiotics

CULTURE-POSITIVE BACTEREMIA (without meningitis):
• GBS: 10 days
• Gram-negative: 10-14 days
• S. aureus: 14 days (longer if complicated)

MENINGITIS:
• GBS: 14-21 days
• Listeria: 21 days
• Gram-negative: 21 days (minimum)
• Confirm CSF sterilization with repeat LP

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE CRITERIA
═══════════════════════════════════════════════════════════════════════════════

• Completing antibiotic course (may complete outpatient for stable infants)
• Feeding well (breast or bottle)
• Temperature stable in open cot
• No oxygen requirement
• Parents educated on feeding, danger signs, follow-up

═══════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

1-2 WEEKS:
• Clinical assessment
• Ensure completing antibiotics if outpatient treatment
• Assess feeding and growth

1-3 MONTHS:
• Developmental assessment
• Hearing screening (especially after meningitis or aminoglycosides)
• Growth monitoring

LONG-TERM:
• Neurodevelopmental follow-up for meningitis survivors
• Monitor for hearing loss, seizures, motor delays
• School readiness assessment

═══════════════════════════════════════════════════════════════════════════════
DANGER SIGNS (Teach Parents)
═══════════════════════════════════════════════════════════════════════════════

Return immediately if baby has:
• Fever or feels cold
• Not feeding well or refusing feeds
• Vomiting everything
• Breathing fast or with difficulty
• Less active than usual or difficult to wake
• Convulsions (fits)
• Yellow skin (jaundice) spreading or getting darker
• Umbilicus red, swollen, or with pus`
    },

    redFlags: [
      '🔴 CRITICAL: Apnea or severe respiratory distress - May need intubation; sign of severe sepsis or meningitis',
      '🔴 CRITICAL: Shock (poor perfusion, hypotension, tachycardia, weak pulses) - Immediate fluid resuscitation and vasopressors',
      '🔴 CRITICAL: Seizures - May indicate meningitis or severe metabolic disturbance; ABCs, glucose, anticonvulsants, LP',
      '🔴 CRITICAL: Bulging fontanelle - Suggests meningitis with increased ICP; LP essential when stable',
      '🔴 CRITICAL: Petechiae/purpura - Suggests DIC or severe sepsis; check coagulation, platelet count',
      '🔴 CRITICAL: Sclerema (hardening of skin) - Very poor prognostic sign; indicates severe sepsis',
      '🔴 CRITICAL: Severe hypothermia (<35°C) - Aggressive rewarming; associated with high mortality',
      '🟠 URGENT: No improvement after 48-72 hours of antibiotics - Reassess; consider resistant organism, wrong diagnosis, secondary focus',
      '🟠 URGENT: Abdominal distension with bloody stools - Suspect NEC; obtain abdominal X-ray; surgical consult',
      '🟠 URGENT: Vesicular rash - Consider HSV; start acyclovir immediately',
      '🟠 URGENT: Positive CSF Gram stain - Confirms meningitis; adjust antibiotics; ensure adequate CNS penetration',
      '🟠 URGENT: Multidrug-resistant organism isolated - Infectious disease consult; adjust antibiotics'
    ],

    complications: [
      'DEATH: Mortality 10-50% depending on setting, organism, and gestational age; higher in preterm and Gram-negative sepsis',
      'MENINGITIS: Occurs in 10-15% of septic neonates; higher morbidity; longer treatment duration',
      'SEPTIC SHOCK: Cardiovascular collapse requiring vasopressors; high mortality',
      'DISSEMINATED INTRAVASCULAR COAGULATION (DIC): Bleeding, petechiae, purpura; treat underlying sepsis',
      'NECROTIZING ENTEROCOLITIS (NEC): Bowel necrosis; may require surgery; can coexist with sepsis',
      'ACUTE KIDNEY INJURY: From hypoperfusion or aminoglycoside toxicity',
      'PERSISTENT PULMONARY HYPERTENSION (PPHN): May complicate severe sepsis',
      'HEARING LOSS: From meningitis or aminoglycoside toxicity; requires follow-up screening',
      'NEURODEVELOPMENTAL IMPAIRMENT: Cerebral palsy, cognitive delays, seizure disorders; especially after meningitis',
      'HYDROCEPHALUS: Post-meningitis complication; may require VP shunt',
      'PROLONGED HOSPITALIZATION: Weeks to months for severe sepsis; family stress; cost'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY
═══════════════════════════════════════════════════════════════════════════════

Overall neonatal sepsis mortality:
• Developed countries: 10-20%
• Developing countries (including Africa): 30-50%

BY ORGANISM:
• GBS: 5-10%
• E. coli: 20-30%
• Klebsiella/Gram-negatives: 30-50%
• Fungal: 30-40%

BY GESTATIONAL AGE:
• Term infants: Lower mortality
• Preterm: Higher mortality (especially <28 weeks)
• VLBW (<1500g): Highest mortality

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

SEPSIS WITHOUT MENINGITIS:
• Most survivors have normal development
• Some increased risk of developmental delays
• Need routine developmental surveillance

MENINGITIS SURVIVORS:
• 20-50% have long-term sequelae
• Hearing loss: 10-30%
• Cognitive impairment: 20-30%
• Motor deficits/cerebral palsy: 10-20%
• Seizure disorders: 5-15%
• Hydrocephalus: 5-10%

RISK FACTORS FOR POOR OUTCOME:
• Prematurity
• Gram-negative organisms
• Delayed treatment
• Shock at presentation
• Meningitis
• Seizures
• Prolonged mechanical ventilation
• Low resource setting

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC INDICATORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Term infant
• Early treatment (<1 hour)
• GBS organism
• Culture-negative (probable sepsis)
• Responsive to initial antibiotics
• No meningitis

UNFAVORABLE:
• Prematurity, especially <28 weeks
• Gram-negative organism
• Multidrug-resistant organism
• Shock at presentation
• Meningitis
• Seizures
• Sclerema
• DIC`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PREVENTION OF EARLY-ONSET SEPSIS
═══════════════════════════════════════════════════════════════════════════════

INTRAPARTUM ANTIBIOTIC PROPHYLAXIS (IAP) FOR GBS:
• Screen all pregnant women at 35-37 weeks (if available)
• Give IV antibiotics during labor to GBS-positive women
• Penicillin G (5 million units IV then 2.5-3 million q4h) preferred
• Alternative: Ampicillin 2g IV then 1g q4h
• Give ≥4 hours before delivery for adequate protection
• IAP has reduced EOS GBS by 80% in developed countries

INDICATIONS FOR IAP:
• GBS bacteriuria in current pregnancy
• Previous infant with GBS disease
• GBS colonization in current pregnancy
• Unknown GBS status with: preterm labor, PROM ≥18h, maternal fever ≥38°C

CLEAN DELIVERY PRACTICES:
• Skilled birth attendant
• Clean delivery surface
• Hand hygiene
• Clean cord cutting and tying
• Sterile cord clamp/tie

═══════════════════════════════════════════════════════════════════════════════
PREVENTION OF LATE-ONSET SEPSIS
═══════════════════════════════════════════════════════════════════════════════

HAND HYGIENE:
• MOST IMPORTANT intervention
• Before and after every patient contact
• Alcohol-based hand rub or soap and water

INFECTION CONTROL IN NICU:
• Cohorting of infected infants
• Minimize overcrowding
• Limit visitors
• Regular environmental cleaning
• Surveillance cultures

CARE BUNDLES:
• Central line bundles (hand hygiene, full barrier precautions, chlorhexidine, optimal site, daily review)
• Ventilator bundles (head elevation, oral care, sedation vacation)

BREAST MILK:
• Protective against sepsis and NEC
• Encourage exclusive breastfeeding
• Donor milk if mother's own milk unavailable

SKIN CARE:
• Minimize skin breaks
• Emollient use may reduce sepsis in preterm (e.g., sunflower oil)
• Careful IV insertion technique

EARLY REMOVAL OF DEVICES:
• Remove central lines, urinary catheters, ET tubes as soon as possible

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT - PREVENTION PRIORITIES
═══════════════════════════════════════════════════════════════════════════════

COMMUNITY LEVEL:
• Promote facility-based delivery with skilled attendants
• Train traditional birth attendants in clean delivery
• Chlorhexidine for cord care (WHO recommendation for high-mortality settings)
• Early recognition of danger signs
• Prompt health-seeking behavior

FACILITY LEVEL:
• Hand hygiene programs
• Clean delivery practices
• Basic newborn care (warmth, early breastfeeding)
• Infection prevention and control
• Avoid unnecessary antibiotics (prevents resistance)
• Adequate nurse-to-patient ratios

NATIONAL LEVEL:
• GBS screening programs (where feasible)
• Antimicrobial stewardship
• Surveillance for neonatal sepsis and antimicrobial resistance
• Training for healthcare workers`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS NEONATAL SEPSIS?
═══════════════════════════════════════════════════════════════════════════════

Neonatal sepsis is a serious infection in a newborn baby (first 28 days of life). The infection spreads through the baby's blood and can affect many organs. It is dangerous because babies have weak immune systems and can become very sick very quickly.

═══════════════════════════════════════════════════════════════════════════════
HOW DO BABIES GET INFECTED?
═══════════════════════════════════════════════════════════════════════════════

EARLY INFECTION (first 3 days):
• Infection passes from mother to baby during pregnancy or delivery
• Can happen if the water breaks too early
• Can happen if mother has a fever or infection during labor

LATE INFECTION (after 3 days):
• Baby catches infection from the environment
• Can happen in hospital (from equipment, other sick babies)
• Can happen at home (poor hygiene, unclean cord care)

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - BRING BABY TO HOSPITAL IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

🔴 FEVER or baby feels HOT
🔴 Baby feels COLD and cannot stay warm
🔴 NOT FEEDING well or refusing to feed
🔴 VOMITING everything
🔴 BREATHING FAST or with difficulty
🔴 Baby is LESS ACTIVE than usual or very sleepy
🔴 Baby is DIFFICULT TO WAKE UP
🔴 FITS (convulsions/seizures)
🔴 YELLOW SKIN that is spreading or getting darker
🔴 Umbilical cord is RED, SWOLLEN, or has PUS

If your baby has any of these signs, DO NOT WAIT - go to the hospital immediately!

═══════════════════════════════════════════════════════════════════════════════
TREATMENT
═══════════════════════════════════════════════════════════════════════════════

Your baby will receive:
• ANTIBIOTICS through an IV (injection into a vein) - these kill the bacteria
• FLUIDS to keep baby hydrated
• WARMTH to keep baby's temperature normal
• FEEDING support (breast milk is best; may be given through a tube if baby cannot suck)
• Close MONITORING of vital signs

Treatment usually takes 7-21 days depending on how serious the infection is.

═══════════════════════════════════════════════════════════════════════════════
PREVENTING INFECTION IN YOUR BABY
═══════════════════════════════════════════════════════════════════════════════

BEFORE BIRTH:
• Attend all antenatal clinic visits
• Deliver at a health facility with skilled attendants
• Tell the healthcare workers if you have fever or your water breaks early

AFTER BIRTH:
• WASH YOUR HANDS with soap and water before touching the baby
• Ask visitors to wash hands before touching the baby
• Keep the umbilical cord CLEAN and DRY - do not apply anything to it unless advised by healthcare worker
• BREASTFEED your baby exclusively (breast milk protects against infection)
• Keep baby WARM (skin-to-skin contact is excellent)
• Keep baby AWAY from sick people
• Complete all VACCINATIONS on schedule

═══════════════════════════════════════════════════════════════════════════════
CORD CARE
═══════════════════════════════════════════════════════════════════════════════

• Keep the cord stump CLEAN and DRY
• Do NOT cover it with bandages
• Do NOT apply ash, cow dung, oil, or other substances
• Fold the diaper below the cord stump
• The cord will fall off naturally in 1-3 weeks
• If the cord becomes red, swollen, smells bad, or has pus - go to the hospital

═══════════════════════════════════════════════════════════════════════════════
AFTER LEAVING HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

• Give all medicines as prescribed until finished
• Keep all follow-up appointments
• Continue breastfeeding
• Watch for danger signs (listed above)
• Your baby may need hearing tests later (some antibiotics and infections can affect hearing)

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK THE HEALTHCARE TEAM
═══════════════════════════════════════════════════════════════════════════════

• What type of infection does my baby have?
• How long will treatment take?
• Can I breastfeed my baby?
• Can I hold my baby?
• Will my baby have any long-term problems?
• When will my baby be able to go home?
• What follow-up care will my baby need?`,

    references: [
      'Shane AL, et al. Neonatal sepsis. Lancet. 2017;390(10104):1770-1780. doi:10.1016/S0140-6736(17)31002-4',
      'Puopolo KM, et al. Management of Neonates Born at ≥35 0/7 Weeks Gestation With Suspected or Proven Early-Onset Bacterial Sepsis. Pediatrics. 2018;142(6):e20182894.',
      'Fleischmann-Struzek C, et al. The global burden of paediatric and neonatal sepsis: a systematic review. Lancet Respir Med. 2018;6(3):223-230.',
      'Simonsen KA, et al. Early-Onset Neonatal Sepsis. Clin Microbiol Rev. 2014;27(1):21-47.',
      'Zaidi AK, et al. Hospital-acquired neonatal infections in developing countries. Lancet. 2005;365(9465):1175-88.',
      'Seale AC, et al. Neonatal severe bacterial infection impairment estimates in South Asia, sub-Saharan Africa, and Latin America for 2010. Pediatr Res. 2013;74 Suppl 1:73-85.',
      'World Health Organization. Pocket Book of Hospital Care for Children. 2nd ed. WHO; 2013.',
      'Verani JR, et al. Prevention of perinatal group B streptococcal disease--revised guidelines from CDC, 2010. MMWR Recomm Rep. 2010;59(RR-10):1-36.',
      'Obiero CW, et al. Aetiology of bacteraemia in febrile infants aged less than 60 days in sub-Saharan Africa: a systematic review. Lancet Glob Health. 2019;7(11):e1603-e1612.'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 21. THYROID STORM
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '21',
    name: 'Thyroid Storm',
    slug: 'thyroid-storm',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E05.5',
    resourceLevel: 'ADVANCED',
    isEmergency: true,

    overview: `Thyroid storm (thyrotoxic crisis) is a rare but life-threatening endocrine emergency characterized by severe manifestations of thyrotoxicosis with multi-organ decompensation. Mortality rates range from 10-30% even with aggressive treatment, making rapid recognition and intervention critical. The condition represents the extreme end of the hyperthyroid spectrum and requires immediate ICU-level care.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.


═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DIAGNOSIS IS CLINICAL: Do not wait for thyroid function tests to initiate treatment - use the Burch-Wartofsky Point Scale (BWPS) for clinical diagnosis
• BWPS ≥45 HIGHLY SUGGESTS thyroid storm; 25-44 suggests impending storm; <25 unlikely
• PRECIPITATING FACTORS: Infection (#1 trigger), surgery, trauma, DKA, iodinated contrast, medication non-compliance, radioactive iodine, childbirth
• TREATMENT ORDER: (1) Beta-blockers FIRST for symptom control, (2) Thionamides to block new hormone synthesis, (3) Iodine 1 HOUR AFTER thionamide, (4) Glucocorticoids
• PROPYLTHIOURACIL (PTU) preferred over methimazole in thyroid storm due to additional peripheral T4→T3 conversion blockade
• IODINE MUST BE GIVEN ≥1 HOUR AFTER THIONAMIDE to prevent iodine being used for new hormone synthesis (Wolff-Chaikoff effect)
• GLUCOCORTICOIDS are essential - they reduce T4→T3 conversion, treat relative adrenal insufficiency, and may have immunomodulatory effects
• FEVER IS CARDINAL: Temperature >40°C (104°F) with tachycardia disproportionate to fever suggests thyroid storm
• CARDIOVASCULAR COLLAPSE: High-output heart failure and atrial fibrillation are major causes of death
• ALTERED MENTAL STATUS: Ranges from agitation to psychosis to coma - indicates severe storm
• APATHETIC THYROID STORM: Elderly may present with minimal symptoms, bradycardia, or congestive heart failure rather than classic hyperadrenergic picture
• PROPRANOLOL preferred beta-blocker due to additional peripheral T4→T3 conversion blockade at high doses (>160mg/day)
• AVOID ASPIRIN: Displaces T4 from binding proteins, increasing free hormone levels
• PLASMAPHERESIS: Consider in refractory cases unresponsive to maximal medical therapy
• DEFINITIVE TREATMENT: Once stabilized, plan for thyroidectomy or radioactive iodine ablation

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

Thyroid storm represents severe thyroid hormone excess with catecholamine hypersensitivity:

HORMONE DYNAMICS:
• Thyroid hormones (T3, T4) increase β-adrenergic receptor density and sensitivity
• T3 is the active hormone; T4 is converted to T3 peripherally by deiodinases
• In thyroid storm, massive hormone release overwhelms normal regulatory mechanisms
• Free T4 and T3 may not be dramatically higher than uncomplicated thyrotoxicosis - it is the tissue response that differs

CARDIOVASCULAR EFFECTS:
• Increased cardiac output (can exceed 15-20 L/min)
• Decreased systemic vascular resistance
• Tachycardia, increased contractility, widened pulse pressure
• Atrial fibrillation in 10-35% (due to shortened atrial refractory period)
• High-output heart failure → eventual cardiovascular collapse

METABOLIC EFFECTS:
• Dramatically increased basal metabolic rate (can exceed 200% normal)
• Thermogenesis leads to hyperthermia
• Increased oxygen consumption and CO2 production
• Accelerated glucose turnover → hyperglycemia then hypoglycemia
• Negative nitrogen balance, muscle wasting

CNS EFFECTS:
• Direct neurotoxicity of thyroid hormones
• Cerebral hyperthermia
• Progression: anxiety → agitation → delirium → seizures → coma

ADRENAL AXIS:
• Accelerated cortisol metabolism creates relative adrenal insufficiency
• This is why glucocorticoids are essential in treatment`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: 0.2-0.4 per 100,000 per year in developed countries
• Represents <1-2% of all hospitalizations for thyrotoxicosis
• Mortality: 10-30% with treatment, >90% if untreated
• Female predominance: Female:Male ratio of 3-5:1 (reflects underlying Graves' disease epidemiology)
• Peak incidence: 40-60 years of age
• Graves' disease accounts for 60-80% of underlying causes
• Mortality improving with modern ICU care (from >50% historically to 10-20% currently)
• Survivors generally have good long-term outcomes if underlying thyroid disease controlled

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• PREVALENCE: Limited data, likely underdiagnosed due to healthcare access limitations
• IODINE DEFICIENCY PARADOX: Africa has high prevalence of iodine deficiency disorders, but iodine supplementation programs can trigger Jod-Basedow phenomenon (iodine-induced thyrotoxicosis) in patients with autonomous nodules
• UNDIAGNOSED GRAVES' DISEASE: Many patients present with thyroid storm as first manifestation of previously undiagnosed Graves' disease
• TOXIC MULTINODULAR GOITER: More common in iodine-deficient endemic goiter regions of Africa (Nigeria, Ethiopia, DRC, Tanzania)
• INFECTION AS TRIGGER: High burden of infectious diseases (malaria, typhoid, tuberculosis) serve as common precipitants
• SURGICAL PRECIPITANT: Thyroid storm may occur post-thyroidectomy in unprepared hyperthyroid patients - requires preoperative optimization
• HIV CONSIDERATIONS: Immune reconstitution inflammatory syndrome (IRIS) can trigger Graves' disease in HIV patients starting ART
• MEDICATION ACCESS: PTU and methimazole may have limited availability - ensure emergency stock
• ICU LIMITATIONS: Many facilities lack ICU capabilities required for severe thyroid storm management
• TRADITIONAL MEDICINE: Patients may use traditional remedies causing delayed presentation
• PREGNANCY: Thyroid storm in pregnancy carries high maternal and fetal mortality - requires specialized obstetric care
• DIAGNOSTIC CHALLENGES: TSH assays may not be readily available; clinical diagnosis using BWPS essential
• HEAT EXPOSURE: Hot climate may exacerbate hyperthermia and mask early fever as a presenting sign`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CARDINAL FEATURES (Classic Triad)
═══════════════════════════════════════════════════════════════════════════════

1. HYPERTHERMIA: Temperature >38.5°C (101.3°F), often >40°C (104°F)
2. TACHYCARDIA: Often >140 bpm, disproportionate to fever (expect 10 bpm rise per 1°C)
3. CNS DYSFUNCTION: Agitation, delirium, psychosis, seizures, or coma

═══════════════════════════════════════════════════════════════════════════════
SYSTEMIC MANIFESTATIONS
═══════════════════════════════════════════════════════════════════════════════

CARDIOVASCULAR:
• Severe tachycardia (often 140-200 bpm)
• Atrial fibrillation (10-35% of cases) - often with rapid ventricular response
• Wide pulse pressure (systolic hypertension with low diastolic)
• High-output heart failure with pulmonary edema
• Cardiovascular collapse in advanced cases
• Chest pain (due to increased myocardial oxygen demand)

NEUROLOGICAL:
• Severe anxiety, emotional lability
• Agitation, restlessness
• Tremor (fine, high frequency)
• Hyperreflexia
• Psychosis, hallucinations
• Seizures
• Coma (late finding, indicates severe storm)

GASTROINTESTINAL:
• Nausea, vomiting
• Diarrhea (can be profuse, leading to dehydration)
• Abdominal pain
• Hepatomegaly
• Jaundice (indicates hepatic dysfunction - poor prognostic sign)

THERMOREGULATORY:
• High fever (>40°C/104°F common)
• Profuse diaphoresis
• Heat intolerance
• Warm, moist skin
• Flushing

METABOLIC/GENERAL:
• Severe weight loss (chronic thyrotoxicosis)
• Extreme fatigue, weakness
• Dehydration
• Hyperglycemia (early) → hypoglycemia (late)

═══════════════════════════════════════════════════════════════════════════════
BURCH-WARTOFSKY POINT SCALE (BWPS)
═══════════════════════════════════════════════════════════════════════════════

THERMOREGULATORY DYSFUNCTION (Temperature):
• 37.2-37.7°C (99-99.9°F): 5 points
• 37.8-38.2°C (100-100.9°F): 10 points
• 38.3-38.8°C (101-101.9°F): 15 points
• 38.9-39.4°C (102-102.9°F): 20 points
• 39.5-39.9°C (103-103.9°F): 25 points
• ≥40°C (≥104°F): 30 points

CARDIOVASCULAR - Heart Rate:
• 100-109: 5 points
• 110-119: 10 points
• 120-129: 15 points
• 130-139: 20 points
• ≥140: 25 points

CARDIOVASCULAR - Atrial Fibrillation:
• Absent: 0 points
• Present: 10 points

CARDIOVASCULAR - Heart Failure:
• Absent: 0 points
• Mild (pedal edema): 5 points
• Moderate (bibasilar rales): 10 points
• Severe (pulmonary edema): 15 points

CNS DYSFUNCTION:
• Absent: 0 points
• Mild (agitation): 10 points
• Moderate (delirium, psychosis, extreme lethargy): 20 points
• Severe (seizure, coma): 30 points

GI-HEPATIC DYSFUNCTION:
• Absent: 0 points
• Moderate (diarrhea, nausea/vomiting, abdominal pain): 10 points
• Severe (unexplained jaundice): 20 points

PRECIPITATING EVENT:
• Absent: 0 points
• Present: 10 points

INTERPRETATION:
• ≥45 points: THYROID STORM - Highly suggestive
• 25-44 points: IMPENDING STORM - Supportive of diagnosis
• <25 points: STORM UNLIKELY

═══════════════════════════════════════════════════════════════════════════════
APATHETIC THYROID STORM (Elderly Presentation)
═══════════════════════════════════════════════════════════════════════════════

• May lack typical hyperadrenergic features
• Apathy, weakness, lethargy predominate
• May have bradycardia instead of tachycardia
• Weight loss, muscle wasting
• Congestive heart failure, atrial fibrillation
• Depression-like presentation
• Often misdiagnosed - HIGH INDEX OF SUSPICION NEEDED IN ELDERLY`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

🔴 THYROID STORM IS A CLINICAL DIAGNOSIS - DO NOT DELAY TREATMENT FOR LAB CONFIRMATION

STEP 1: CLINICAL RECOGNITION
• Apply Burch-Wartofsky Point Scale (BWPS)
• Score ≥45: Highly suggestive - initiate treatment immediately
• Look for: Fever + Tachycardia + CNS changes + Precipitating event

STEP 2: IDENTIFY PRECIPITATING CAUSE
• Infection (most common) - perform full septic workup
• Recent thyroid surgery or radioactive iodine
• Iodinated contrast exposure
• Medication non-compliance or withdrawal
• Diabetic ketoacidosis
• Trauma, surgery, childbirth
• Stroke, MI, pulmonary embolism

STEP 3: LABORATORY CONFIRMATION
• Thyroid function tests (TSH, Free T4, Free T3)
• Complete metabolic panel
• CBC with differential
• Liver function tests
• Coagulation studies
• Cardiac enzymes
• Blood cultures
• Urinalysis

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

THYROID FUNCTION:
• TSH: Suppressed (<0.01 mIU/L in primary hyperthyroidism)
• Free T4: Elevated (often >5 ng/dL / >64 pmol/L)
• Free T3: Elevated
• NOTE: Hormone levels may not be dramatically different from uncomplicated thyrotoxicosis

METABOLIC:
• Hyperglycemia (early) → Hypoglycemia (late)
• Hypercalcemia (mild, due to increased bone turnover)
• Hypokalemia (due to β-adrenergic-driven intracellular shift)
• Hypomagnesemia

HEPATIC:
• Elevated transaminases (AST, ALT)
• Elevated bilirubin (indicates hepatic dysfunction - poor prognosis)
• Elevated alkaline phosphatase (bone origin)

HEMATOLOGIC:
• Leukocytosis (may be present even without infection)
• Anemia (chronic thyrotoxicosis)
• Thrombocytopenia (in severe cases)

OTHER:
• Elevated cortisol (stress response) but functionally insufficient
• Low cholesterol (increased clearance)

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

CLINICAL DIAGNOSIS requires:
1. Evidence of thyrotoxicosis (biochemical or clinical)
2. Severe symptoms with multi-organ involvement
3. Burch-Wartofsky Score ≥45 OR
4. Clinical judgment suggesting life-threatening thyrotoxicosis

JAPANESE THYROID ASSOCIATION CRITERIA (Alternative):
Requires thyrotoxicosis PLUS:
• CNS manifestations (restlessness, delirium, psychosis, coma)
• Fever ≥38°C
• Tachycardia ≥130 bpm
• Heart failure symptoms
• GI symptoms (nausea, vomiting, diarrhea)`,

    differentialDiagnosis: [
      'Sepsis/Septic Shock - Fever, tachycardia, hypotension; distinguished by positive cultures, elevated procalcitonin, no thyroid history; can coexist with and precipitate thyroid storm',
      'Heat Stroke - Hyperthermia, altered mental status; distinguished by environmental exposure, anhidrosis, normal thyroid function',
      'Malignant Hyperthermia - Post-anesthetic hyperthermia, rigidity; distinguished by temporal relationship to anesthesia, elevated CK, dantrolene responsive',
      'Neuroleptic Malignant Syndrome - Fever, rigidity, altered consciousness; distinguished by recent neuroleptic use, lead-pipe rigidity, elevated CK',
      'Serotonin Syndrome - Hyperthermia, hyperreflexia, clonus; distinguished by recent serotonergic drug use, neuromuscular findings, no thyroid history',
      'Pheochromocytoma Crisis - Hypertension, tachycardia, diaphoresis; distinguished by severe paroxysmal hypertension, elevated catecholamines, normal thyroid function',
      'Sympathomimetic Toxicity (Cocaine, Amphetamines) - Agitation, tachycardia, hyperthermia; distinguished by drug history, urine toxicology, pupillary dilation',
      'Anticholinergic Toxicity - Hyperthermia, tachycardia, altered mental status; distinguished by dry skin, urinary retention, decreased bowel sounds',
      'Severe Delirium Tremens - Agitation, tachycardia, hyperthermia; distinguished by alcohol withdrawal history, visual hallucinations, tremor pattern',
      'Acute Mania - Agitation, insomnia, pressured speech; distinguished by psychiatric history, euthyroid state, lack of fever',
      'Meningitis/Encephalitis - Fever, altered mental status, headache; distinguished by meningeal signs, CSF findings, no thyroid history',
      'Status Epilepticus - Altered consciousness, fever; distinguished by witnessed seizure activity, EEG findings'
    ],

    investigations: [
      {
        name: 'Thyroid Function Tests (TSH, Free T4, Free T3)',
        indication: '🔴 IMMEDIATE - Confirms biochemical thyrotoxicosis; do NOT delay treatment pending results',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count with Differential',
        indication: '🔴 IMMEDIATE - Assesses for infection (leukocytosis), anemia; leukocytosis may occur in thyroid storm without infection',
        availability: 'BASIC'
      },
      {
        name: 'Comprehensive Metabolic Panel',
        indication: '🔴 IMMEDIATE - Evaluates glucose, electrolytes, renal function; expect hyperglycemia, hypokalemia, hypercalcemia',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (AST, ALT, Bilirubin, ALP)',
        indication: '🔴 IMMEDIATE - Hepatic dysfunction indicates severe storm and poor prognosis; jaundice is a red flag',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: '🔴 IMMEDIATE - Assesses coagulopathy; elevated in hepatic dysfunction and DIC',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures (2 sets)',
        indication: '🔴 IMMEDIATE - Infection is the most common precipitant; obtain before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis and Urine Culture',
        indication: '🔴 IMMEDIATE - Screens for urinary tract infection as precipitant',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 IMMEDIATE - Evaluates for pneumonia (precipitant) and pulmonary edema (heart failure)',
        availability: 'BASIC'
      },
      {
        name: 'Electrocardiogram (ECG)',
        indication: '🔴 IMMEDIATE - Assesses for sinus tachycardia, atrial fibrillation, signs of cardiac ischemia',
        availability: 'BASIC'
      },
      {
        name: 'Cardiac Enzymes (Troponin, BNP/NT-proBNP)',
        indication: '🟠 URGENT - Evaluates for demand ischemia and heart failure severity',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 IMMEDIATE - Assesses oxygenation, ventilation, acid-base status; may show respiratory alkalosis or metabolic acidosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Glucose (Point-of-Care)',
        indication: '🔴 IMMEDIATE - Serial monitoring; hyperglycemia early, hypoglycemia in late/severe cases',
        availability: 'BASIC'
      },
      {
        name: 'Serum Calcium and Phosphate',
        indication: '🟠 URGENT - Mild hypercalcemia common; severe hypercalcemia may contribute to symptoms',
        availability: 'BASIC'
      },
      {
        name: 'Serum Cortisol',
        indication: '🟠 URGENT - Baseline before glucocorticoid administration; assesses adrenal reserve',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'TSH Receptor Antibodies (TRAb)',
        indication: 'Elective - Confirms Graves disease as underlying etiology after stabilization',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Head',
        indication: '🟠 URGENT if CNS symptoms - Rules out stroke, hemorrhage as alternative diagnosis or precipitant',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture',
        indication: 'Consider if meningitis/encephalitis suspected - After CT head if indicated',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: '🟠 URGENT - Assesses cardiac function, pulmonary pressures in patients with heart failure',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ CALL FOR HELP - Notify endocrinology, ICU team; thyroid storm requires multidisciplinary care',
        '⚡ ICU ADMISSION - All patients with suspected thyroid storm require ICU-level monitoring',
        '⚡ AIRWAY/BREATHING - Supplemental oxygen, prepare for intubation if altered mental status or respiratory failure',
        '⚡ IV ACCESS - Establish 2 large-bore IVs; central line if hemodynamically unstable',
        '⚡ CONTINUOUS MONITORING - Cardiac telemetry, pulse oximetry, frequent vital signs (q15 min initially)',
        '⚡ AGGRESSIVE IV FLUIDS - 1-2L crystalloid bolus; ongoing 200-300 mL/hr; patients are often severely volume depleted',
        '⚡ CALCULATE BURCH-WARTOFSKY SCORE - Document severity for treatment decisions and monitoring',
        '⚡ COOLING MEASURES - Active cooling for hyperthermia; cooling blankets, ice packs to groin/axillae; target temperature <39°C',
        '⚡ AVOID ASPIRIN for fever - Use acetaminophen; aspirin displaces T4 from binding proteins',
        '⚡ IDENTIFY AND TREAT PRECIPITANT - Empiric antibiotics if infection suspected; treat DKA, etc.',
        '⚡ INITIATE DRUG THERAPY IN ORDER: (1) Beta-blocker, (2) Thionamide, (3) Iodine (1 hour later), (4) Glucocorticoid',
        '⚡ DVT PROPHYLAXIS - Mechanical and/or pharmacological; patients are hypercoagulable',
        '⚡ STRESS ULCER PROPHYLAXIS - H2 blocker or PPI',
        '⚡ FOLEY CATHETER - Strict intake/output monitoring',
        '⚡ NPO - Until mental status clears and patient stable'
      ],
      pharmacological: [
        {
          drug: 'Propranolol',
          indication: '🔴 FIRST-LINE BETA-BLOCKER - Controls adrenergic symptoms; also inhibits peripheral T4→T3 conversion at high doses',
          dose: 'IV: 0.5-1 mg slow IV push over 10 minutes, may repeat every 15 minutes; PO: 60-80 mg every 4-6 hours, may increase to 120-160 mg every 4-6 hours; maximum 480 mg/day',
          route: 'IV initially (rapid control), transition to PO when stable',
          duration: 'Continue until euthyroid state achieved; taper as thyroid function normalizes',
          contraindications: ['Severe bronchospasm/asthma (use esmolol or diltiazem instead)', 'Decompensated heart failure (use with extreme caution, may worsen)', 'Second/third-degree heart block', 'Severe hypotension', 'Cocaine intoxication (within 24 hours)'],
          sideEffects: ['Hypotension', 'Bradycardia', 'Bronchospasm', 'Heart failure exacerbation', 'Hypoglycemia masking', 'Fatigue'],
          alternatives: ['Esmolol (if IV needed with rapid offset)', 'Metoprolol (cardioselective)', 'Diltiazem (if beta-blocker contraindicated)']
        },
        {
          drug: 'Esmolol',
          indication: '🔴 ALTERNATIVE IV BETA-BLOCKER - Preferred when rapid titration and short half-life needed; useful in borderline heart failure',
          dose: 'Loading dose: 250-500 mcg/kg IV bolus; Maintenance: 50-100 mcg/kg/min IV infusion, titrate up to 200 mcg/kg/min to achieve heart rate control',
          route: 'IV infusion only',
          duration: 'Continue infusion until stabilized, then transition to oral propranolol',
          contraindications: ['Severe bronchospasm', 'Cardiogenic shock', 'Second/third-degree heart block', 'Severe bradycardia'],
          sideEffects: ['Hypotension', 'Bradycardia', 'Injection site reactions', 'Bronchospasm (less than propranolol)'],
          alternatives: ['Propranolol (longer acting)', 'Metoprolol', 'Diltiazem (if beta-blocker contraindicated)']
        },
        {
          drug: 'Propylthiouracil (PTU)',
          indication: '🔴 PREFERRED THIONAMIDE IN THYROID STORM - Blocks thyroid hormone synthesis AND peripheral T4→T3 conversion',
          dose: 'Loading dose: 500-1000 mg PO/NG; Maintenance: 200-250 mg PO every 4 hours (total 1000-1500 mg/day)',
          route: 'PO or via NG tube if unable to take orally; rectal administration (retention enema) as last resort',
          duration: 'Continue high-dose for 24-48 hours, then taper to 100-150 mg every 8 hours based on clinical response; long-term use limited by hepatotoxicity risk',
          contraindications: ['Previous severe PTU reaction', 'Severe hepatic impairment', 'Agranulocytosis from prior thionamide use'],
          sideEffects: ['Hepatotoxicity (potentially fatal - PTU-specific risk)', 'Agranulocytosis (0.3-0.5%)', 'ANCA-positive vasculitis', 'Rash', 'Arthralgia', 'GI upset'],
          alternatives: ['Methimazole (if PTU unavailable or after acute phase)', 'Lithium (rarely, if thionamides contraindicated)']
        },
        {
          drug: 'Methimazole',
          indication: '🟠 ALTERNATIVE THIONAMIDE - Use if PTU unavailable; less preferred acutely as lacks peripheral conversion blockade',
          dose: '20-30 mg PO/NG every 6-8 hours (total 60-120 mg/day in divided doses)',
          route: 'PO or via NG tube; rectal administration possible',
          duration: 'Continue until euthyroid; preferred for long-term maintenance after acute phase due to lower hepatotoxicity risk',
          contraindications: ['Previous severe methimazole reaction', 'Agranulocytosis from prior thionamide use', 'First trimester pregnancy (use PTU instead)'],
          sideEffects: ['Agranulocytosis (0.3-0.5%)', 'Rash', 'Arthralgia', 'GI upset', 'Cholestatic jaundice (rare)', 'Teratogenic (first trimester)'],
          alternatives: ['PTU (preferred in thyroid storm)', 'Lithium (if thionamides contraindicated)']
        },
        {
          drug: 'Lugol Solution (Strong Iodine Solution)',
          indication: '🔴 IODINE THERAPY - Blocks thyroid hormone release via Wolff-Chaikoff effect; MUST give ≥1 hour AFTER thionamide',
          dose: '5-10 drops (0.25-0.5 mL) PO/NG every 8 hours; each drop contains ~8 mg iodine',
          route: 'PO or via NG tube; dilute in juice or water to improve palatability',
          duration: '24-48 hours typically; discontinue once hormone synthesis fully blocked to prepare for definitive therapy',
          contraindications: ['Give ONLY after thionamide has been administered for ≥1 hour', 'Known iodine allergy', 'Contraindicated before radioactive iodine therapy'],
          sideEffects: ['Iodism (metallic taste, burning mouth, sore teeth)', 'Allergic reaction', 'Acneiform eruption', 'Sialadenitis'],
          alternatives: ['SSKI (saturated solution of potassium iodide)', 'Iodinated contrast (iopanoic acid if available)']
        },
        {
          drug: 'Potassium Iodide (SSKI)',
          indication: '🔴 ALTERNATIVE IODINE THERAPY - Same mechanism as Lugol; MUST give ≥1 hour AFTER thionamide',
          dose: '5 drops (approximately 250 mg) PO every 6-8 hours',
          route: 'PO or via NG tube; dilute in juice or water',
          duration: '24-48 hours; discontinue to allow radioactive iodine uptake if planned',
          contraindications: ['Give ONLY after thionamide for ≥1 hour', 'Iodine allergy', 'Dermatitis herpetiformis'],
          sideEffects: ['Iodism', 'Allergic reactions', 'GI upset', 'Hypersalivation'],
          alternatives: ['Lugol solution', 'Iodinated contrast agents']
        },
        {
          drug: 'Hydrocortisone',
          indication: '🔴 GLUCOCORTICOID - Addresses relative adrenal insufficiency, reduces T4→T3 conversion, immunomodulatory effects',
          dose: '100 mg IV every 8 hours (300 mg/day total); or initial 200 mg IV bolus then 100 mg every 8 hours',
          route: 'IV',
          duration: 'Continue for 24-48 hours, then taper over 3-5 days as patient stabilizes',
          contraindications: ['No absolute contraindications in thyroid storm', 'Use with caution in active untreated infection'],
          sideEffects: ['Hyperglycemia', 'Fluid retention', 'Hypokalemia', 'Immunosuppression', 'GI upset'],
          alternatives: ['Dexamethasone 2-4 mg IV every 6 hours (more potent T4→T3 conversion inhibition)', 'Methylprednisolone 20 mg IV every 6 hours']
        },
        {
          drug: 'Dexamethasone',
          indication: '🟠 ALTERNATIVE GLUCOCORTICOID - May be preferred due to more potent inhibition of T4→T3 conversion',
          dose: '2-4 mg IV every 6 hours',
          route: 'IV',
          duration: 'Continue for 24-48 hours, taper as patient stabilizes',
          contraindications: ['No absolute contraindications in thyroid storm'],
          sideEffects: ['Hyperglycemia', 'Hypokalemia', 'Immunosuppression', 'Insomnia', 'Psychiatric effects'],
          alternatives: ['Hydrocortisone (has mineralocorticoid activity)', 'Methylprednisolone']
        },
        {
          drug: 'Acetaminophen (Paracetamol)',
          indication: '🔴 ANTIPYRETIC - First-line for fever reduction; DO NOT USE ASPIRIN',
          dose: '650-1000 mg PO/IV every 4-6 hours; maximum 4 g/day; reduce dose in hepatic impairment',
          route: 'PO, IV, or rectal',
          duration: 'As needed for fever control',
          contraindications: ['Severe hepatic impairment (common in severe thyroid storm)', 'Known hypersensitivity'],
          sideEffects: ['Hepatotoxicity (dose-dependent)', 'Rare hypersensitivity reactions'],
          alternatives: ['External cooling measures', 'DO NOT USE ASPIRIN (increases free thyroid hormone)']
        },
        {
          drug: 'Cholestyramine',
          indication: '🟠 ADJUNCTIVE - Binds thyroid hormones in GI tract, interrupting enterohepatic circulation; reduces hormone levels',
          dose: '4 g PO every 6 hours',
          route: 'PO or via NG tube',
          duration: 'Continue until hormone levels begin to normalize',
          contraindications: ['Complete biliary obstruction', 'GI obstruction', 'Phenylketonuria (some formulations)'],
          sideEffects: ['Constipation', 'Bloating', 'Interference with absorption of other medications (give separately)'],
          alternatives: ['Colestipol']
        },
        {
          drug: 'Diltiazem',
          indication: '🟠 RATE CONTROL - Alternative when beta-blockers contraindicated (severe asthma/COPD)',
          dose: 'IV: 0.25 mg/kg bolus over 2 min, may repeat 0.35 mg/kg; Infusion: 5-15 mg/hr; PO: 60-90 mg every 6-8 hours',
          route: 'IV for acute rate control, transition to PO',
          duration: 'Continue until rate controlled, transition to oral and eventually discontinue as thyroid function normalizes',
          contraindications: ['Sick sinus syndrome', 'Second/third-degree AV block', 'Severe hypotension', 'Acute MI with pulmonary congestion', 'WPW with atrial fibrillation'],
          sideEffects: ['Hypotension', 'Bradycardia', 'Heart block', 'Peripheral edema', 'Constipation'],
          alternatives: ['Verapamil', 'Beta-blockers if not contraindicated']
        },
        {
          drug: 'Lithium Carbonate',
          indication: '🟠 RARELY USED - Blocks thyroid hormone release; reserved for patients with contraindications to thionamides AND iodine',
          dose: '300 mg PO every 8 hours; adjust to serum lithium level 0.6-1.0 mEq/L',
          route: 'PO',
          duration: 'Short-term only until alternative therapy available',
          contraindications: ['Severe renal impairment', 'Severe cardiovascular disease', 'Severe dehydration', 'Sodium depletion'],
          sideEffects: ['Narrow therapeutic index', 'Nephrogenic diabetes insipidus', 'Hypothyroidism (with chronic use)', 'Tremor', 'GI upset', 'Cardiac arrhythmias'],
          alternatives: ['Thionamides (preferred)', 'Iodine solutions']
        }
      ],
      nonPharmacological: [
        'ACTIVE COOLING: Cooling blankets, ice packs to axillae and groin, evaporative cooling (fans with misted water); avoid shivering (give sedation if needed as shivering increases heat production)',
        'FLUID RESUSCITATION: Aggressive IV crystalloids; patients may require 3-5 liters in first 24 hours due to insensible losses from fever, diaphoresis, vomiting, diarrhea',
        'NUTRITIONAL SUPPORT: High-calorie diet once able to eat; patients are hypercatabolic; may need 4000+ calories/day; supplement thiamine (B1) to prevent Wernicke encephalopathy',
        'ELECTROLYTE REPLACEMENT: Potassium, magnesium, phosphate as needed; hypokalemia is common',
        'DEXTROSE SUPPLEMENTATION: D5 or D10 in IV fluids; monitor for hypoglycemia especially in late/severe cases',
        'THIAMINE SUPPLEMENTATION: 100 mg IV before glucose administration; hypermetabolism depletes thiamine stores',
        'PLASMAPHERESIS/PLASMA EXCHANGE: Consider in refractory cases unresponsive to maximal medical therapy; rapidly removes circulating thyroid hormones',
        'TREAT UNDERLYING PRECIPITANT: Empiric antibiotics if infection suspected; treat DKA, MI, PE as indicated',
        'SEIZURE PRECAUTIONS: Padded side rails, suction available; benzodiazepines for seizure control',
        'DVT PROPHYLAXIS: Sequential compression devices; consider anticoagulation if no contraindications'
      ],
      surgical: [
        'EMERGENCY THYROIDECTOMY: Rarely indicated; reserved for patients failing maximal medical management, contraindications to all medications, or anatomical complications (airway compromise from goiter)',
        'SURGICAL TIMING: If surgery needed, optimize medically first if possible (beta-blockade, iodine, glucocorticoids) for 24-48 hours to reduce operative risk',
        'PREOPERATIVE PREPARATION: SSKI or Lugol solution for 10-14 days reduces vascularity (Plummer solution); beta-blockers for rate control; glucocorticoids for adrenal support',
        'DEFINITIVE SURGERY: Total thyroidectomy preferred once acute crisis resolved and patient stabilized; prevents recurrence',
        'RADIOACTIVE IODINE ABLATION: Alternative definitive therapy after acute phase; requires discontinuation of iodine therapy and thionamides; avoid in pregnancy'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ENDOCRINOLOGY CONSULTATION: All suspected thyroid storm cases',
        '🔴 IMMEDIATE ICU ADMISSION: All confirmed or suspected thyroid storm',
        '🔴 IMMEDIATE CARDIOLOGY: Atrial fibrillation with hemodynamic instability, heart failure, acute coronary syndrome',
        '🔴 IMMEDIATE SURGERY: Airway compromise from goiter, refractory to maximal medical therapy',
        '🟠 URGENT INFECTIOUS DISEASE: If infection suspected as precipitant with unusual organisms or severe sepsis',
        '🟠 URGENT NEPHROLOGY: If acute kidney injury, need for plasmapheresis',
        '🟠 URGENT HEMATOLOGY: If coagulopathy, DIC suspected',
        'OBSTETRICS: Thyroid storm in pregnancy (extremely high-risk situation)',
        'INTERVENTIONAL RADIOLOGY: If plasmapheresis needed and no vascular access'
      ],
      monitoring: [
        'CONTINUOUS CARDIAC MONITORING: Telemetry for arrhythmia detection, heart rate trending',
        'VITAL SIGNS: Every 15-30 minutes initially, then hourly once stabilizing',
        'TEMPERATURE: Continuous or every 1-2 hours; target <39°C',
        'MENTAL STATUS: Hourly GCS assessment; improvement indicates response to therapy',
        'BURCH-WARTOFSKY SCORE: Recalculate every 6-12 hours to assess treatment response',
        'FLUID BALANCE: Strict intake/output; daily weights',
        'BLOOD GLUCOSE: Every 2-4 hours; watch for hypoglycemia in late/severe cases',
        'ELECTROLYTES: Every 6-12 hours; potassium, magnesium frequently depleted',
        'THYROID FUNCTION: Daily T4/T3 initially; TSH will remain suppressed for weeks',
        'LIVER FUNCTION: Daily; monitor for hepatic dysfunction (poor prognostic sign) and PTU-induced hepatotoxicity',
        'RENAL FUNCTION: Daily; acute kidney injury common due to hypoperfusion',
        'CBC: Daily; watch for thionamide-induced agranulocytosis (fever, sore throat, mouth ulcers)',
        'RESPONSE TO THERAPY: Clinical improvement expected within 24-48 hours; heart rate should decrease first'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
INPATIENT PHASE (ICU)
═══════════════════════════════════════════════════════════════════════════════

• Continue ICU monitoring until hemodynamically stable for 24-48 hours
• Transition from IV to oral medications as able
• Continue high-dose thionamide until clinically improved and hormone levels declining
• Taper glucocorticoids over 3-5 days
• Identify and treat precipitating cause
• Endocrinology consultation for long-term management plan

═══════════════════════════════════════════════════════════════════════════════
STEP-DOWN/WARD PHASE
═══════════════════════════════════════════════════════════════════════════════

• Transition to oral propranolol; taper as heart rate normalizes
• Switch from PTU to methimazole for long-term therapy (lower hepatotoxicity)
• Titrate thionamide to maintain euthyroid state
• Monitor for thionamide side effects (rash, agranulocytosis, hepatotoxicity)
• Patient education on warning signs
• Plan for definitive therapy (RAI or surgery)

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

• WEEK 1-2: Endocrinology follow-up; repeat TFTs, LFTs, CBC
• WEEK 4: TFTs; adjust thionamide dose
• MONTHLY: TFTs until stable, then every 2-3 months
• CBC every 3 months (agranulocytosis risk highest in first 90 days)
• LFTs if on PTU (hepatotoxicity monitoring)
• Discuss definitive therapy: Radioactive iodine vs Total thyroidectomy

═══════════════════════════════════════════════════════════════════════════════
DEFINITIVE THERAPY PLANNING
═══════════════════════════════════════════════════════════════════════════════

RADIOACTIVE IODINE (RAI):
• Preferred for most Graves' disease patients after stabilization
• Wait until patient is euthyroid on thionamide
• Stop thionamide 3-5 days before RAI
• Restart thionamide 3-7 days after RAI if needed for symptom control
• Hypothyroidism expected outcome (start levothyroxine)
• Contraindicated in pregnancy, active ophthalmopathy

TOTAL THYROIDECTOMY:
• Preferred if: Large goiter, suspicious nodules, severe ophthalmopathy, patient preference
• Must be euthyroid preoperatively
• Lifelong levothyroxine replacement
• Risks: Hypocalcemia (hypoparathyroidism), recurrent laryngeal nerve injury

═══════════════════════════════════════════════════════════════════════════════
PATIENT COUNSELING
═══════════════════════════════════════════════════════════════════════════════

• Educate on warning signs of recurrence
• Importance of medication compliance
• Need for lifelong thyroid monitoring
• Report fever, sore throat, mouth ulcers immediately (agranulocytosis)
• Avoid iodine supplements, kelp, iodinated contrast unless directed
• Medical alert identification recommended`
    },

    redFlags: [
      '🔴 CRITICAL: Temperature >40°C (104°F) with altered mental status - indicates severe storm',
      '🔴 CRITICAL: Cardiovascular collapse with hypotension - impending death, maximally aggressive treatment',
      '🔴 CRITICAL: Seizures or coma - severe CNS involvement, immediate intubation consideration',
      '🔴 CRITICAL: Burch-Wartofsky Score ≥45 - confirms thyroid storm diagnosis',
      '🔴 CRITICAL: Jaundice with elevated bilirubin - hepatic failure, very poor prognosis',
      '🔴 CRITICAL: Pulmonary edema with respiratory failure - high-output heart failure',
      '🔴 CRITICAL: Atrial fibrillation with rapid ventricular response >150 bpm - urgent rate control',
      '🟠 URGENT: No clinical improvement within 24-48 hours of treatment initiation',
      '🟠 URGENT: Persistent fever despite treatment - reassess for infection, consider sepsis',
      '🟠 URGENT: Agranulocytosis symptoms (fever, sore throat, mouth ulcers) on thionamide - STOP drug, check CBC',
      '🟠 URGENT: Signs of adrenal crisis (refractory hypotension) - increase glucocorticoid dose',
      '🟠 URGENT: Worsening mental status despite treatment - consider additional diagnoses',
      '🟠 URGENT: Hypoglycemia - late/severe finding, indicates depleted glycogen stores'
    ],

    complications: [
      'CARDIOVASCULAR: High-output heart failure, pulmonary edema, atrial fibrillation with rapid ventricular response, cardiovascular collapse, arrhythmias',
      'NEUROLOGICAL: Seizures, coma, stroke (embolic from atrial fibrillation), cerebral edema',
      'HEPATIC: Hepatic failure, cholestatic jaundice (poor prognostic indicator)',
      'RENAL: Acute kidney injury secondary to hypoperfusion and dehydration',
      'METABOLIC: Severe hyperthermia, rhabdomyolysis, hypoglycemia, metabolic acidosis',
      'HEMATOLOGIC: Disseminated intravascular coagulation (DIC), thionamide-induced agranulocytosis',
      'INFECTIOUS: Secondary infections due to immunosuppression from glucocorticoids and critical illness',
      'RESPIRATORY: Acute respiratory failure, aspiration pneumonia (in altered mental status)',
      'MULTI-ORGAN DYSFUNCTION SYNDROME: Progressive failure of multiple organ systems',
      'DEATH: Mortality 10-30% with treatment, >90% if untreated; usually from cardiovascular collapse, arrhythmias, or hyperthermia',
      'TREATMENT-RELATED: PTU-induced hepatotoxicity, thionamide-induced agranulocytosis, steroid-related complications'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
MORTALITY AND SURVIVAL
═══════════════════════════════════════════════════════════════════════════════

• MORTALITY: 10-30% with aggressive treatment in modern ICU settings
• UNTREATED MORTALITY: >90%
• HISTORICAL MORTALITY: >50% before advent of beta-blockers and ICU care
• Most deaths occur within first 72 hours from cardiovascular collapse, arrhythmias, or refractory hyperthermia

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

POOR PROGNOSTIC INDICATORS:
• Advanced age (>60 years)
• Coma at presentation (GCS <8)
• Jaundice/hepatic dysfunction
• High Burch-Wartofsky score (>60)
• Cardiovascular collapse requiring vasopressors
• Multi-organ dysfunction
• Delayed presentation (>48 hours symptoms before treatment)
• Underlying cardiac disease
• Inadequate response to initial therapy

FAVORABLE PROGNOSTIC INDICATORS:
• Young age
• Early recognition and treatment
• Lower Burch-Wartofsky score (45-55)
• Rapid response to beta-blockers and thionamides
• No significant end-organ damage
• Identifiable and treatable precipitant

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

• Survivors generally have good long-term prognosis if underlying thyroid disease definitively treated
• Risk of recurrence if definitive therapy not performed
• May have residual cardiac dysfunction if significant heart failure during storm
• Full neurological recovery expected if no hypoxic injury
• Lifelong thyroid hormone monitoring required
• Psychological support may be needed after critical illness`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• EARLY DIAGNOSIS AND TREATMENT of hyperthyroidism prevents progression to storm
• Regular monitoring of thyroid function in known hyperthyroid patients
• Patient education on importance of medication compliance
• Screening for thyroid disease in high-risk populations

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Storm in Known Hyperthyroidism)
═══════════════════════════════════════════════════════════════════════════════

MEDICATION COMPLIANCE:
• Emphasize importance of taking thionamides as prescribed
• Discuss consequences of stopping medications abruptly
• Address barriers to adherence (cost, side effects, access)

SURGICAL/PROCEDURAL PREPARATION:
• NEVER operate on unprepared hyperthyroid patient
• Render patient euthyroid before any elective surgery
• Preoperative beta-blockers for all hyperthyroid patients undergoing surgery
• SSKI/Lugol for 10-14 days before thyroid surgery (reduces vascularity)
• If emergency surgery required: IV beta-blockers, steroids, and antithyroid drugs perioperatively

CONTRAST EXPOSURE:
• Avoid iodinated contrast in uncontrolled hyperthyroidism when possible
• If contrast essential: Pre-treat with thionamide and potassium perchlorate
• Monitor closely for 2-3 weeks after iodinated contrast in at-risk patients

INFECTION PREVENTION AND EARLY TREATMENT:
• Vaccinations as indicated
• Prompt treatment of infections
• Low threshold for antibiotics in hyperthyroid patients with fever

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT - PREVENTION STRATEGIES
═══════════════════════════════════════════════════════════════════════════════

• Improve access to thyroid function testing for early detection
• Ensure availability of PTU and methimazole at healthcare facilities
• Train healthcare workers to recognize hyperthyroidism symptoms
• Screen patients in iodine-supplementation programs for pre-existing autonomous nodules
• Educate patients on disease recognition and importance of follow-up
• Address traditional medicine use that may delay presentation
• Monitor HIV patients on ART for immune reconstitution thyroiditis`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING THYROID STORM
═══════════════════════════════════════════════════════════════════════════════

Thyroid storm is a life-threatening emergency that occurs when your thyroid gland releases too much hormone. Your thyroid controls your body's metabolism - how fast your heart beats, how warm you feel, and how much energy you have. In thyroid storm, everything goes into overdrive.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK EMERGENCY CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Call emergency services or go to hospital immediately if you experience:
• High fever (feeling very hot, temperature >38°C/100.4°F)
• Very fast heartbeat (>120 beats per minute) or pounding heart
• Confusion, agitation, or feeling extremely anxious
• Severe weakness or inability to walk
• Chest pain or difficulty breathing
• Severe diarrhea with dehydration
• Yellowing of skin or eyes (jaundice)
• Seizures or loss of consciousness

═══════════════════════════════════════════════════════════════════════════════
PREVENTING THYROID STORM
═══════════════════════════════════════════════════════════════════════════════

1. TAKE YOUR MEDICATIONS EXACTLY AS PRESCRIBED
   • Never stop your thyroid medications without consulting your doctor
   • Missing doses can trigger a storm
   • If you experience side effects, talk to your doctor before stopping

2. KEEP ALL FOLLOW-UP APPOINTMENTS
   • Regular blood tests monitor your thyroid levels
   • Medication doses may need adjustment

3. AVOID TRIGGERS
   • Treat infections promptly (don't ignore fever or sore throat)
   • Tell ALL doctors you have thyroid disease before any surgery or procedure
   • Avoid iodine supplements and kelp/seaweed products

4. KNOW YOUR LIMITS
   • Avoid extreme heat and strenuous exercise if not well-controlled
   • Stay well-hydrated

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MEDICATION WARNINGS
═══════════════════════════════════════════════════════════════════════════════

If taking antithyroid medications (PTU or methimazole), STOP the medication and see a doctor immediately if you develop:
• Fever with sore throat or mouth ulcers (could indicate low white blood cells)
• Yellowing of skin or eyes, dark urine (could indicate liver problems)
• Severe rash or joint pain

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM MANAGEMENT
═══════════════════════════════════════════════════════════════════════════════

• You will need lifelong thyroid monitoring
• Definitive treatment (surgery or radioactive iodine) is usually recommended to prevent recurrence
• Carry medical identification indicating you have thyroid disease
• Inform healthcare providers about your condition before any procedures

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What caused my thyroid storm?
• What is my long-term treatment plan?
• When should I have my thyroid levels rechecked?
• Should I have surgery or radioactive iodine treatment?
• What medications will I need long-term?
• Are there any activities I should avoid?`,

    references: [
      'Burch HB, Wartofsky L. Life-threatening thyrotoxicosis: Thyroid storm. Endocrinol Metab Clin North Am. 1993;22(2):263-277. [Original Burch-Wartofsky Point Scale]',
      'Ross DS, Burch HB, Cooper DS, et al. 2016 American Thyroid Association Guidelines for Diagnosis and Management of Hyperthyroidism and Other Causes of Thyrotoxicosis. Thyroid. 2016;26(10):1343-1421. doi:10.1089/thy.2016.0229',
      'Akamizu T, Satoh T, Isozaki O, et al. Diagnostic criteria, clinical features, and incidence of thyroid storm based on nationwide surveys. Thyroid. 2012;22(7):661-679. doi:10.1089/thy.2011.0334 [Japanese Thyroid Association Criteria]',
      'Chiha M, Samarasinghe S, Kabaker AS. Thyroid storm: An updated review. J Intensive Care Med. 2015;30(3):131-140. doi:10.1177/0885066613498053',
      'Swee DS, Chng CL, Lim A. Clinical characteristics and outcome of thyroid storm: A case series and review of neuropsychiatric derangements in thyrotoxicosis. Endocr Pract. 2015;21(2):182-189. doi:10.4158/EP14023.OR',
      'Angell TE, Lechner MG, Nguyen CT, et al. Clinical features and hospital outcomes in thyroid storm: A retrospective cohort study. J Clin Endocrinol Metab. 2015;100(2):451-459. doi:10.1210/jc.2014-2850',
      'World Health Organization. Iodine deficiency in Africa. WHO African Region, 2023. https://www.afro.who.int/health-topics/nutrition [African epidemiology context]',
      'Klubo-Gwiezdzinska J, Wartofsky L. Thyroid emergencies. Med Clin North Am. 2012;96(2):385-403. doi:10.1016/j.mcna.2012.01.015',
      'De Leo S, Lee SY, Braverman LE. Hyperthyroidism. Lancet. 2016;388(10047):906-918. doi:10.1016/S0140-6736(16)00278-6',
      'Idrose AM. Acute and emergency care for thyrotoxicosis and thyroid storm. Acute Med Surg. 2015;2(3):147-157. doi:10.1002/ams2.104'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 22. ADRENAL CRISIS (ACUTE ADRENAL INSUFFICIENCY)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '22',
    name: 'Adrenal Crisis',
    slug: 'adrenal-crisis',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E27.2',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Adrenal crisis (acute adrenal insufficiency, Addisonian crisis) is a life-threatening endocrine emergency resulting from acute cortisol deficiency. It occurs in patients with underlying adrenal insufficiency (primary or secondary) exposed to physiological stress, or from abrupt withdrawal of chronic glucocorticoid therapy. Without rapid recognition and treatment with parenteral glucocorticoids, mortality approaches 100%.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with an endocrinologist or appropriate specialist for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• TREAT FIRST, DIAGNOSE LATER: If adrenal crisis is suspected, give hydrocortisone IMMEDIATELY - do not wait for confirmatory tests
• HYDROCORTISONE 100 MG IV BOLUS is the cornerstone of treatment - this is the single most important intervention
• VOLUME RESUSCITATION: Patients are profoundly volume depleted; aggressive IV normal saline (0.9% NaCl) is essential
• HYPOGLYCEMIA: Check glucose immediately and correct with dextrose; hypoglycemia is common and dangerous
• MOST COMMON CAUSE WORLDWIDE: Abrupt withdrawal of chronic glucocorticoid therapy (iatrogenic)
• PRIMARY VS SECONDARY: Primary (Addison's) = adrenal gland failure; Secondary = pituitary/hypothalamic failure
• HYPERPIGMENTATION: Present in PRIMARY adrenal insufficiency only (due to elevated ACTH/MSH)
• HYPERKALEMIA + HYPONATREMIA: Classic electrolyte pattern in PRIMARY adrenal insufficiency (aldosterone deficiency)
• SECONDARY AI: Normal potassium (aldosterone preserved), but still has hyponatremia and hypoglycemia
• PRECIPITATING STRESS: Infection (#1), surgery, trauma, emotional stress, medication non-compliance
• STEROID-DEPENDENT PATIENTS: Anyone on >5 mg prednisone equivalent daily for >3 weeks may have HPA axis suppression
• STRESS DOSING: All known adrenal insufficient patients need increased glucocorticoids during illness/surgery
• RANDOM CORTISOL <3 µg/dL during stress is diagnostic; >18 µg/dL rules out adrenal insufficiency
• DEXAMETHASONE ALTERNATIVE: If diagnosis uncertain, give dexamethasone (does not interfere with cortisol assay) then do ACTH stimulation test
• MINERALOCORTICOID: Fludrocortisone needed for PRIMARY AI once acute phase resolved (not needed in secondary AI)
• LIFELONG THERAPY: Adrenal insufficiency requires lifelong glucocorticoid replacement and patient education

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

NORMAL CORTISOL FUNCTIONS:
• Maintains vascular tone and responsiveness to catecholamines
• Regulates glucose metabolism (gluconeogenesis, glycogenolysis)
• Modulates immune and inflammatory responses
• Essential for stress response adaptation
• Maintains fluid and electrolyte balance (permissive effect on free water excretion)

PRIMARY ADRENAL INSUFFICIENCY (Addison's Disease):
• Destruction of adrenal cortex → loss of cortisol AND aldosterone AND adrenal androgens
• Causes: Autoimmune (70-90% in developed countries), TB (most common in Africa), HIV/AIDS, fungal infections, hemorrhage, metastases
• Low cortisol → loss of negative feedback → elevated ACTH → hyperpigmentation (ACTH has MSH activity)
• Aldosterone deficiency → sodium wasting, potassium retention, volume depletion, metabolic acidosis

SECONDARY ADRENAL INSUFFICIENCY:
• Pituitary or hypothalamic disease → low ACTH → low cortisol
• Causes: Pituitary tumors, surgery, radiation, Sheehan syndrome, head trauma, chronic glucocorticoid use (most common)
• Aldosterone PRESERVED (regulated by renin-angiotensin, not ACTH) → normal potassium
• No hyperpigmentation (ACTH is low, not high)

TERTIARY ADRENAL INSUFFICIENCY:
• Chronic exogenous glucocorticoid use → hypothalamic-pituitary-adrenal (HPA) axis suppression
• Most common cause of adrenal insufficiency overall
• Can occur with any route: oral, inhaled, topical, intra-articular
• HPA axis may take 6-12 months to recover after glucocorticoid withdrawal

CRISIS PATHOPHYSIOLOGY:
• Acute cortisol deficiency during stress → inability to mount appropriate stress response
• Vasodilation + decreased vascular responsiveness to catecholamines → hypotension → shock
• Impaired gluconeogenesis → hypoglycemia
• Loss of mineralocorticoid (primary AI) → severe hyponatremia, hyperkalemia, volume depletion
• Without cortisol replacement → cardiovascular collapse → death`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Prevalence of primary adrenal insufficiency: 100-140 per million in developed countries
• Prevalence of secondary adrenal insufficiency: 150-280 per million (more common than primary)
• Adrenal crisis incidence: 6-8 episodes per 100 patient-years in known adrenal insufficiency
• Mortality from adrenal crisis: 0.5-2% per crisis episode with treatment; approaches 100% untreated
• Overall excess mortality in adrenal insufficiency: 2-fold increased vs general population
• Female:Male ratio: 2.5:1 for autoimmune Addison's; equal for other causes
• Peak age: 30-50 years for autoimmune; any age for other causes
• Most common precipitant: Gastrointestinal illness (vomiting/diarrhea preventing oral medication absorption)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• TUBERCULOSIS: Leading cause of primary adrenal insufficiency in Africa (vs autoimmune in developed countries)
• TB-RELATED ADDISON'S: Bilateral adrenal destruction by Mycobacterium tuberculosis; may present years after pulmonary TB
• HIV/AIDS: Major cause of adrenal insufficiency in Africa through multiple mechanisms:
  - Direct adrenal infection (CMV, MAC, cryptococcus, histoplasmosis)
  - HIV-associated adrenalitis
  - Drug-induced (ketoconazole, rifampicin interaction with steroids)
  - Immune reconstitution inflammatory syndrome (IRIS)
• HIV PREVALENCE: Up to 20% of advanced HIV patients have some degree of adrenal dysfunction
• FUNGAL INFECTIONS: Histoplasmosis, cryptococcosis causing adrenal destruction in immunocompromised
• DIAGNOSTIC CHALLENGES: Limited access to cortisol assays and ACTH stimulation testing in many settings
• MEDICATION ACCESS: Hydrocortisone and fludrocortisone may not be consistently available
• PRESENTATION: Often presents late due to healthcare access limitations; higher mortality
• INFECTION AS PRECIPITANT: High burden of infectious diseases (malaria, typhoid, bacterial infections) trigger crisis
• TRADITIONAL MEDICINE: May delay presentation or interfere with treatment
• STEROID MISUSE: Inappropriate glucocorticoid use (depot injections, unregulated medications) → subsequent withdrawal crisis
• CLIMATE: Hot climates may exacerbate salt wasting and volume depletion
• PATIENT EDUCATION: Limited awareness among patients about sick-day rules and emergency protocols
• MENINGOCOCCAL DISEASE: Waterhouse-Friderichsen syndrome (bilateral adrenal hemorrhage) occurs with meningococcemia`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CARDINAL FEATURES OF ADRENAL CRISIS
═══════════════════════════════════════════════════════════════════════════════

1. HYPOTENSION/SHOCK: Often refractory to fluids and vasopressors until glucocorticoids given
2. SEVERE WEAKNESS: Profound fatigue, lethargy, inability to stand
3. ABDOMINAL SYMPTOMS: Nausea, vomiting, abdominal pain (may mimic acute abdomen)
4. ALTERED MENTAL STATUS: Confusion, lethargy, obtundation, coma

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS BY SYSTEM
═══════════════════════════════════════════════════════════════════════════════

CARDIOVASCULAR:
• Hypotension (may be profound, systolic <90 mmHg)
• Orthostatic hypotension
• Tachycardia (compensatory)
• Shock refractory to fluids and vasopressors
• Cardiovascular collapse

GASTROINTESTINAL:
• Nausea and vomiting (very common, may prevent oral medication)
• Abdominal pain (can be severe, mimicking acute abdomen)
• Diarrhea or constipation
• Anorexia
• Weight loss (chronic insufficiency)

NEUROLOGICAL:
• Severe fatigue and weakness
• Lethargy, confusion
• Altered mental status
• Delirium
• Seizures (from hypoglycemia or hyponatremia)
• Coma

METABOLIC:
• Hypoglycemia (especially in children, secondary AI)
• Hyponatremia
• Hyperkalemia (PRIMARY AI only)
• Hypercalcemia (rare)
• Metabolic acidosis (PRIMARY AI)

MUSCULOSKELETAL:
• Profound muscle weakness
• Myalgias, arthralgias
• Muscle cramps (electrolyte disturbances)

PSYCHIATRIC:
• Depression
• Anxiety
• Psychosis (severe cases)

═══════════════════════════════════════════════════════════════════════════════
SIGNS ON EXAMINATION
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Hypotension (systolic <90 mmHg, often <80 mmHg in crisis)
• Tachycardia
• Fever (may be from underlying infection or adrenal insufficiency itself)
• Hypothermia (in severe cases)

SKIN (PRIMARY AI):
• Hyperpigmentation - generalized but especially in:
  - Sun-exposed areas
  - Skin creases (palmar creases, knuckles)
  - Pressure points (elbows, knees)
  - Scars
  - Buccal mucosa, gums
  - Nipples, areolae
  - Genital skin
• Vitiligo (if autoimmune etiology)
• Loss of axillary and pubic hair (women - androgen deficiency)

DEHYDRATION SIGNS:
• Dry mucous membranes
• Decreased skin turgor
• Sunken eyes
• Delayed capillary refill

OTHER:
• Decreased body hair in women
• Small heart on chest X-ray (chronic volume depletion)
• Abdominal tenderness (may mimic surgical abdomen)

═══════════════════════════════════════════════════════════════════════════════
CLINICAL CLUES BY ETIOLOGY
═══════════════════════════════════════════════════════════════════════════════

AUTOIMMUNE ADDISON'S:
• Hyperpigmentation
• Associated autoimmune conditions (thyroid disease, vitiligo, type 1 diabetes, pernicious anemia)
• Personal or family history of autoimmune disease

TUBERCULOUS ADDISON'S:
• History of TB or TB exposure
• May have active pulmonary TB
• Adrenal calcification on imaging

HIV/AIDS-RELATED:
• Known HIV or risk factors
• Opportunistic infection features
• Wasting syndrome

BILATERAL ADRENAL HEMORRHAGE (Waterhouse-Friderichsen):
• Sudden onset during severe sepsis (especially meningococcemia)
• Petechiae, purpura, DIC
• Very high mortality

SECONDARY AI:
• NO hyperpigmentation
• Normal potassium (aldosterone preserved)
• History of pituitary disease, surgery, radiation
• Features of other pituitary hormone deficiencies
• Visual field defects (if pituitary mass)

GLUCOCORTICOID WITHDRAWAL:
• History of chronic steroid use (any route)
• Recent tapering or abrupt discontinuation
• No hyperpigmentation`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

🔴 ADRENAL CRISIS IS A CLINICAL DIAGNOSIS - TREAT IMMEDIATELY, THEN CONFIRM

⚠️ IMPORTANT: While this guide provides diagnostic criteria, definitive diagnosis and management should be made by qualified physicians based on the complete clinical picture and available resources.

STEP 1: CLINICAL RECOGNITION
• High index of suspicion in: known adrenal insufficiency, chronic steroid users, unexplained shock
• Classic presentation: Hypotension + weakness + GI symptoms + altered mental status
• Look for hyperpigmentation (suggests primary AI)
• Review medication history for glucocorticoid use

STEP 2: IMMEDIATE TREATMENT (before labs return)
• If clinical suspicion high → Give hydrocortisone 100 mg IV immediately
• Draw blood for cortisol BEFORE giving steroids if possible (but don't delay treatment)

STEP 3: LABORATORY CONFIRMATION
• Random serum cortisol (ideally during stress/crisis)
• Basic metabolic panel (look for hyponatremia, hyperkalemia, hypoglycemia)
• ACTH level (to differentiate primary vs secondary)
• ACTH stimulation test (gold standard, but defer until stable)

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC CRITERIA
═══════════════════════════════════════════════════════════════════════════════

RANDOM CORTISOL INTERPRETATION (during acute illness/stress):
• <3 µg/dL (<83 nmol/L): DIAGNOSTIC of adrenal insufficiency
• 3-18 µg/dL (83-500 nmol/L): INDETERMINATE - need ACTH stimulation test
• >18 µg/dL (>500 nmol/L): EXCLUDES adrenal insufficiency

ACTH STIMULATION TEST (Standard 250 µg Cosyntropin Test):
• Give cosyntropin (synthetic ACTH) 250 µg IV/IM
• Measure cortisol at 0, 30, and 60 minutes
• NORMAL RESPONSE: Cortisol ≥18 µg/dL (≥500 nmol/L) at 30 or 60 min
• ABNORMAL: Peak cortisol <18 µg/dL confirms adrenal insufficiency

DISTINGUISHING PRIMARY VS SECONDARY AI:
• ACTH level: High in PRIMARY (>200 pg/mL), Low/Normal in SECONDARY
• Aldosterone: Low in PRIMARY, Normal in SECONDARY
• Potassium: High in PRIMARY, Normal in SECONDARY
• Hyperpigmentation: Present in PRIMARY, Absent in SECONDARY

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

ELECTROLYTES:
• Hyponatremia: Present in both primary and secondary AI (cortisol needed for free water excretion)
• Hyperkalemia: PRIMARY AI only (aldosterone deficiency)
• Hypercalcemia: Occasional (mechanism unclear)
• Metabolic acidosis: PRIMARY AI (aldosterone deficiency → renal H+ retention)

GLUCOSE:
• Hypoglycemia: Common, especially in secondary AI and children

RENAL:
• Elevated BUN and creatinine (prerenal azotemia from volume depletion)

HEMATOLOGY:
• Eosinophilia (loss of cortisol's suppressive effect)
• Lymphocytosis
• Anemia (normocytic, from chronic disease or androgen deficiency)

ENDOCRINE:
• Low cortisol (<3 µg/dL diagnostic during stress)
• ACTH: HIGH in primary AI, LOW in secondary AI
• Low aldosterone and elevated renin (PRIMARY AI)
• May have concurrent thyroid dysfunction (autoimmune)

═══════════════════════════════════════════════════════════════════════════════
IMAGING
═══════════════════════════════════════════════════════════════════════════════

CT ABDOMEN (adrenals):
• Enlarged adrenals: Hemorrhage, infection, infiltration, metastases
• Small/atrophic adrenals: Autoimmune, long-standing TB
• Calcified adrenals: Tuberculosis (classic finding), old hemorrhage, histoplasmosis
• Bilateral masses: Metastases, lymphoma, granulomatous disease

MRI PITUITARY:
• Indicated in secondary AI to evaluate pituitary pathology
• May show tumor, empty sella, hemorrhage (pituitary apoplexy)

CHEST X-RAY:
• May show small heart (chronic volume depletion)
• Evidence of TB if present`,

    differentialDiagnosis: [
      'Septic Shock - Hypotension, fever, altered mental status; distinguished by identified source of infection, positive cultures; CAN COEXIST with adrenal crisis (sepsis is a precipitant)',
      'Hypovolemic Shock - Hypotension from blood/fluid loss; distinguished by obvious source of loss, responds to volume alone without steroids',
      'Diabetic Ketoacidosis - Altered mental status, abdominal pain, dehydration; distinguished by hyperglycemia, ketones, high anion gap acidosis',
      'Hyperosmolar Hyperglycemic State - Altered mental status, dehydration; distinguished by severe hyperglycemia, hyperosmolality, minimal ketosis',
      'Myxedema Coma - Hypothermia, altered mental status, hypotension; distinguished by very low T4, elevated TSH, bradycardia (vs tachycardia in adrenal crisis)',
      'Acute Abdomen (surgical emergency) - Severe abdominal pain; distinguished by peritoneal signs, imaging findings; adrenal crisis can mimic acute abdomen',
      'Gastroenteritis - Nausea, vomiting, diarrhea; distinguished by lack of profound hypotension and hyperpigmentation; may precipitate crisis in AI patients',
      'Drug Overdose/Intoxication - Altered mental status, hypotension; distinguished by toxicology screen, specific toxidromes',
      'Hypoglycemia (other causes) - Altered mental status, diaphoresis; distinguished by response to glucose alone, identifiable cause (insulin excess)',
      'Severe Hyponatremia (other causes) - Altered mental status, seizures; distinguished by euvolemia or hypervolemia, normal cortisol',
      'Pheochromocytoma Crisis - Can cause hypotension after hypertensive phase; distinguished by paroxysmal hypertension, elevated catecholamines',
      'Anaphylaxis - Hypotension, altered mental status; distinguished by acute onset after allergen exposure, urticaria, angioedema, bronchospasm'
    ],

    investigations: [
      {
        name: 'Random Serum Cortisol',
        indication: '🔴 IMMEDIATE - Draw BEFORE giving steroids if possible; <3 µg/dL diagnostic, >18 µg/dL excludes AI',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Basic Metabolic Panel (Na, K, Glucose, BUN, Cr)',
        indication: '🔴 IMMEDIATE - Classic findings: hyponatremia, hyperkalemia (primary AI), hypoglycemia, prerenal azotemia',
        availability: 'BASIC'
      },
      {
        name: 'Point-of-Care Glucose',
        indication: '🔴 IMMEDIATE - Hypoglycemia common and dangerous; correct immediately if present',
        availability: 'BASIC'
      },
      {
        name: 'Plasma ACTH Level',
        indication: '🔴 IMMEDIATE - Draw at same time as cortisol to distinguish primary (high ACTH) from secondary (low ACTH); must process quickly (on ice)',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count with Differential',
        indication: '🔴 IMMEDIATE - May show eosinophilia, lymphocytosis (loss of cortisol suppression), anemia of chronic disease',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🟠 URGENT - Assess for metabolic acidosis (aldosterone deficiency in primary AI)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Calcium',
        indication: '🟠 URGENT - Mild hypercalcemia may be present; contributes to symptoms',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH, Free T4)',
        indication: '🟠 URGENT - Exclude concurrent hypothyroidism (autoimmune polyglandular syndrome); treat thyroid AFTER starting steroids',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Plasma Renin and Aldosterone',
        indication: 'Elective - High renin, low aldosterone confirms mineralocorticoid deficiency in primary AI',
        availability: 'ADVANCED'
      },
      {
        name: 'ACTH Stimulation Test (Cosyntropin Test)',
        indication: 'After stabilization - Gold standard for diagnosis; peak cortisol <18 µg/dL is abnormal',
        availability: 'ADVANCED'
      },
      {
        name: '21-Hydroxylase Antibodies',
        indication: 'Elective - Confirms autoimmune etiology of primary AI',
        availability: 'ADVANCED'
      },
      {
        name: 'Blood Cultures',
        indication: '🔴 IMMEDIATE - Infection is the most common precipitant; obtain before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis and Urine Culture',
        indication: '🔴 IMMEDIATE - Screen for UTI as precipitant',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 IMMEDIATE - Evaluate for pneumonia, TB, small heart size from chronic volume depletion',
        availability: 'BASIC'
      },
      {
        name: 'CT Abdomen (with adrenal focus)',
        indication: '🟠 URGENT - Evaluates adrenal size and morphology; calcification suggests TB, enlargement suggests hemorrhage/infection/tumor',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI Pituitary with Contrast',
        indication: 'Elective - Indicated in secondary AI to evaluate pituitary pathology (tumor, empty sella, apoplexy)',
        availability: 'ADVANCED'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT in African setting - HIV is major cause of adrenal insufficiency; test if status unknown',
        availability: 'BASIC'
      },
      {
        name: 'TB Workup (sputum AFB, GeneXpert, Quantiferon)',
        indication: '🟠 URGENT in endemic areas - TB is leading cause of Addison disease in Africa; evaluate all patients',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: '🔴 IMMEDIATE - Assess for hyperkalemia changes (peaked T waves, widened QRS) especially in primary AI',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ DO NOT DELAY TREATMENT FOR DIAGNOSTIC CONFIRMATION - Adrenal crisis is rapidly fatal without treatment',
        '⚡ HYDROCORTISONE 100 MG IV BOLUS IMMEDIATELY - This is the single most critical intervention',
        '⚡ ESTABLISH IV ACCESS - Large-bore IV, preferably two lines',
        '⚡ AGGRESSIVE IV FLUIDS - 1-2 liters 0.9% Normal Saline in first hour; continue 200-500 mL/hr based on response',
        '⚡ CHECK BLOOD GLUCOSE IMMEDIATELY - If hypoglycemic, give D50 50 mL IV or add dextrose to IV fluids',
        '⚡ CONTINUOUS MONITORING - Cardiac telemetry (hyperkalemia risk), BP, pulse oximetry',
        '⚡ DRAW BASELINE LABS - Cortisol, ACTH, electrolytes BEFORE steroids if possible (do not delay treatment)',
        '⚡ ECG FOR HYPERKALEMIA - Look for peaked T waves, widened QRS; treat hyperkalemia if present',
        '⚡ IDENTIFY AND TREAT PRECIPITANT - Empiric antibiotics if infection suspected',
        '⚡ AVOID MORPHINE, SEDATIVES - Reduced clearance in adrenal insufficiency',
        '⚡ AVOID RAPID SODIUM CORRECTION - Risk of osmotic demyelination; correct slowly if chronic hyponatremia',
        '⚡ ICU ADMISSION - All patients with adrenal crisis require close monitoring',
        '⚡ FOLEY CATHETER - Monitor urine output (target >0.5 mL/kg/hr)',
        '⚡ NPO - Until stable and mental status clears'
      ],
      pharmacological: [
        {
          drug: 'Hydrocortisone Sodium Succinate',
          indication: '🔴 FIRST-LINE GLUCOCORTICOID - Immediate life-saving treatment; provides both glucocorticoid and mineralocorticoid activity',
          dose: 'Initial: 100 mg IV bolus; Maintenance: 50-100 mg IV every 6-8 hours OR 200 mg/24h continuous infusion; Total: 200-300 mg in first 24 hours; Taper over 3-5 days to maintenance dose once stable',
          route: 'IV (preferred for rapid onset and reliable absorption in shock)',
          duration: 'High-dose for 24-72 hours, then taper to oral maintenance (typically hydrocortisone 15-25 mg/day in divided doses)',
          contraindications: ['No absolute contraindications in adrenal crisis', 'Systemic fungal infection (relative - treat both)'],
          sideEffects: ['Hyperglycemia', 'Fluid retention', 'Hypokalemia (with high doses)', 'Insomnia', 'Mood changes', 'GI upset'],
          alternatives: ['Dexamethasone (if hydrocortisone unavailable or diagnosis uncertain)', 'Methylprednisolone']
        },
        {
          drug: 'Dexamethasone',
          indication: '🟠 ALTERNATIVE - Use if hydrocortisone unavailable OR if need to perform ACTH stimulation test (dexamethasone does not interfere with cortisol assay)',
          dose: '4 mg IV initially, then 4 mg IV every 12 hours',
          route: 'IV',
          duration: 'Until ACTH stimulation test performed, then switch to hydrocortisone',
          contraindications: ['No absolute contraindications in adrenal crisis', 'Lacks mineralocorticoid activity - not ideal for primary AI'],
          sideEffects: ['Hyperglycemia', 'Insomnia', 'Mood changes', 'No mineralocorticoid activity'],
          alternatives: ['Hydrocortisone (preferred)', 'Methylprednisolone']
        },
        {
          drug: 'Methylprednisolone',
          indication: '🟠 ALTERNATIVE - If hydrocortisone unavailable; minimal mineralocorticoid activity',
          dose: '40-62.5 mg IV every 6 hours (equivalent to hydrocortisone 200-300 mg/day)',
          route: 'IV',
          duration: 'Until stable, then transition to hydrocortisone',
          contraindications: ['No absolute contraindications in adrenal crisis'],
          sideEffects: ['Hyperglycemia', 'Fluid retention (less than hydrocortisone)', 'Hypokalemia'],
          alternatives: ['Hydrocortisone (preferred)', 'Dexamethasone']
        },
        {
          drug: 'Normal Saline (0.9% NaCl)',
          indication: '🔴 CRITICAL - Aggressive volume resuscitation; patients are profoundly volume depleted',
          dose: '1-2 liters IV bolus in first hour; then 200-500 mL/hr adjusted to clinical response; may require 4-6 liters in first 24 hours',
          route: 'IV',
          duration: 'Continue until hemodynamically stable with adequate urine output',
          contraindications: ['Caution in heart failure, renal failure', 'Monitor for fluid overload'],
          sideEffects: ['Fluid overload', 'Hyperchloremic acidosis (with large volumes)'],
          alternatives: ['Lactated Ringer solution (avoid if hyperkalemic)', 'Add dextrose if hypoglycemic (D5NS)']
        },
        {
          drug: 'Dextrose 50% (D50)',
          indication: '🔴 FOR HYPOGLYCEMIA - Hypoglycemia common in adrenal crisis, especially secondary AI',
          dose: '25-50 mL IV push (12.5-25 g dextrose); may repeat as needed; consider D5 or D10 in maintenance fluids',
          route: 'IV push for acute hypoglycemia; infusion for maintenance',
          duration: 'Until glucose stable and patient eating; may need dextrose in IV fluids for 24-48 hours',
          contraindications: ['Hyperglycemia', 'Caution in thiamine deficiency (give thiamine first)'],
          sideEffects: ['Hyperglycemia', 'Phlebitis (concentrated solutions)', 'Wernicke encephalopathy (if thiamine deficient)'],
          alternatives: ['D10 infusion for maintenance', 'Oral glucose when able to eat']
        },
        {
          drug: 'Fludrocortisone',
          indication: '🟠 MINERALOCORTICOID - For PRIMARY adrenal insufficiency once acute phase resolved; NOT needed in secondary AI',
          dose: '0.05-0.2 mg PO once daily; start at 0.1 mg, titrate based on BP, sodium, potassium, and renin',
          route: 'PO only (no IV formulation)',
          duration: 'Lifelong in primary adrenal insufficiency',
          contraindications: ['Heart failure', 'Severe hypertension', 'Hypokalemia'],
          sideEffects: ['Hypertension', 'Hypokalemia', 'Edema', 'Headache', 'Heart failure exacerbation'],
          alternatives: ['Increase hydrocortisone dose (has some mineralocorticoid activity)', 'High salt diet as adjunct']
        },
        {
          drug: 'Calcium Gluconate 10%',
          indication: '🔴 FOR SEVERE HYPERKALEMIA - Cardiac membrane stabilization if ECG changes present (peaked T waves, widened QRS)',
          dose: '10-20 mL (1-2 g) IV over 2-3 minutes; may repeat in 5 minutes if ECG changes persist',
          route: 'IV slow push',
          duration: 'Immediate cardiac protection; effect lasts 30-60 minutes',
          contraindications: ['Digoxin toxicity (give more slowly)', 'Hypercalcemia'],
          sideEffects: ['Tissue necrosis if extravasation', 'Bradycardia (if given too fast)', 'Hypercalcemia'],
          alternatives: ['Calcium chloride (more concentrated, use central line)']
        },
        {
          drug: 'Insulin Regular + Dextrose',
          indication: '🔴 FOR SEVERE HYPERKALEMIA - Shifts potassium intracellularly',
          dose: '10 units regular insulin IV with 25-50 g dextrose (D50 50-100 mL)',
          route: 'IV',
          duration: 'Effect lasts 4-6 hours; may repeat as needed',
          contraindications: ['Hypoglycemia (give dextrose first)'],
          sideEffects: ['Hypoglycemia (monitor glucose closely)', 'Hypokalemia (with repeated doses)'],
          alternatives: ['Sodium bicarbonate', 'Beta-agonists (salbutamol nebulized)']
        },
        {
          drug: 'Sodium Polystyrene Sulfonate (Kayexalate)',
          indication: '🟠 FOR HYPERKALEMIA - Removes potassium from body; slower onset than other measures',
          dose: '15-30 g PO or 30-50 g rectally in sorbitol',
          route: 'PO or rectal',
          duration: 'Single dose or every 6 hours until potassium normalized',
          contraindications: ['Bowel obstruction', 'Post-operative patients (intestinal necrosis risk)'],
          sideEffects: ['Constipation/diarrhea', 'Intestinal necrosis (rare)', 'Hypokalemia', 'Sodium overload'],
          alternatives: ['Patiromer', 'Sodium zirconium cyclosilicate', 'Dialysis if refractory']
        },
        {
          drug: 'Thiamine (Vitamin B1)',
          indication: '🟠 GIVE BEFORE DEXTROSE if nutritional deficiency suspected - Prevents Wernicke encephalopathy',
          dose: '100-200 mg IV before or with glucose administration',
          route: 'IV',
          duration: 'Single dose sufficient for acute prevention; continue if deficiency suspected',
          contraindications: ['Known hypersensitivity (rare)'],
          sideEffects: ['Rare anaphylaxis with IV administration'],
          alternatives: ['Oral thiamine (if able to take PO)']
        },
        {
          drug: 'Broad-Spectrum Antibiotics',
          indication: '🔴 EMPIRIC - If infection suspected as precipitant; adrenal crisis impairs immune response',
          dose: 'Based on suspected source; e.g., ceftriaxone 2g IV + metronidazole 500mg IV for abdominal source',
          route: 'IV',
          duration: 'Until cultures return and source identified; de-escalate as appropriate',
          contraindications: ['Known severe allergy to specific antibiotic class'],
          sideEffects: ['Antibiotic-specific side effects', 'C. difficile infection', 'Resistance selection'],
          alternatives: ['Adjust based on local antibiogram and suspected source']
        },
        {
          drug: 'DHEA (Dehydroepiandrosterone)',
          indication: 'Elective - Adrenal androgen replacement; may improve well-being and libido in women with primary AI',
          dose: '25-50 mg PO once daily in the morning',
          route: 'PO',
          duration: 'Long-term; assess response at 3-6 months',
          contraindications: ['Hormone-sensitive cancers', 'Pregnancy'],
          sideEffects: ['Acne', 'Hirsutism', 'Oily skin', 'Mood changes'],
          alternatives: ['Not essential; quality of life improvement variable']
        }
      ],
      nonPharmacological: [
        'AGGRESSIVE FLUID RESUSCITATION: Normal saline is preferred; patients may require 4-6 liters in first 24 hours; monitor for fluid overload in elderly/cardiac patients',
        'IDENTIFY AND TREAT PRECIPITATING CAUSE: Infection most common - full septic workup; treat empirically if suspected',
        'TEMPERATURE MANAGEMENT: Fever may be from infection or adrenal insufficiency itself; external cooling if needed',
        'CORRECT HYPOGLYCEMIA: Dextrose-containing fluids after initial resuscitation; frequent glucose monitoring',
        'STRESS ULCER PROPHYLAXIS: H2 blocker or PPI given high-dose steroids',
        'DVT PROPHYLAXIS: Mechanical (SCDs) initially; pharmacological once stable',
        'NUTRITIONAL SUPPORT: NPO initially; advance diet as tolerated; may need high-calorie intake during recovery',
        'PATIENT EDUCATION: Critical for prevention of future crises - sick-day rules, emergency injection, medical alert identification',
        'FAMILY EDUCATION: Train family members in emergency hydrocortisone injection',
        'ELECTROLYTE MONITORING: Frequent sodium, potassium checks; avoid rapid sodium correction'
      ],
      surgical: [
        'SURGICAL INTERVENTION RARELY INDICATED for adrenal crisis itself',
        'TREAT SURGICAL PRECIPITANTS: If acute abdomen identified as cause (not consequence) of presentation',
        'SURGERY IN ADRENAL INSUFFICIENT PATIENTS: Requires stress-dose steroids perioperatively',
        'STRESS DOSING FOR SURGERY: Minor surgery: hydrocortisone 25-50 mg IV; Moderate: 50-75 mg IV then 50 mg q8h for 24-48h; Major: 100 mg IV then 50 mg q6-8h for 48-72h',
        'ADRENALECTOMY: May be indicated for bilateral adrenal masses or pheochromocytoma',
        'PITUITARY SURGERY: If secondary AI due to pituitary tumor, may require surgery once acute crisis resolved'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU ADMISSION: All patients with adrenal crisis',
        '🔴 IMMEDIATE ENDOCRINOLOGY: All cases of adrenal crisis for diagnosis confirmation and long-term management',
        '🟠 URGENT INFECTIOUS DISEASE: If TB, HIV, or fungal infection suspected as underlying cause',
        '🟠 URGENT NEPHROLOGY: If severe hyperkalemia refractory to medical management, need for dialysis',
        '🟠 URGENT NEUROSURGERY: If pituitary apoplexy suspected (sudden headache, visual loss, secondary AI)',
        '🟠 URGENT ONCOLOGY: If bilateral adrenal metastases identified',
        'GENETICS/IMMUNOLOGY: If autoimmune polyglandular syndrome suspected',
        'SURGERY: If surgical stress precipitated crisis or surgical intervention needed'
      ],
      monitoring: [
        'VITAL SIGNS: Every 15-30 minutes initially until hemodynamically stable, then hourly',
        'BLOOD PRESSURE: Target SBP >90 mmHg; should improve within hours of hydrocortisone',
        'BLOOD GLUCOSE: Every 1-2 hours initially; hypoglycemia common, especially in secondary AI',
        'ELECTROLYTES: Every 4-6 hours initially; monitor sodium correction rate (<8-10 mEq/L per 24 hours)',
        'POTASSIUM: Frequent monitoring if hyperkalemic; should improve with hydrocortisone and fluids',
        'URINE OUTPUT: Target >0.5 mL/kg/hr; reflects volume status and renal perfusion',
        'MENTAL STATUS: Should improve within hours of treatment; persistent confusion requires investigation',
        'ECG: Continuous telemetry for hyperkalemia; repeat ECG after potassium treatment',
        'FLUID BALANCE: Strict intake/output; daily weights',
        'CLINICAL RESPONSE: Improvement expected within 12-24 hours; hypotension should resolve within hours',
        'CORTISOL LEVELS: Not needed during acute treatment; check after recovery for baseline',
        'TEMPERATURE: Monitor for underlying infection; fever may persist briefly after steroids started'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
INPATIENT RECOVERY PHASE
═══════════════════════════════════════════════════════════════════════════════

• Continue IV hydrocortisone 50-100 mg every 6-8 hours for 24-48 hours
• Once stable and taking oral: Transition to oral hydrocortisone
• Typical oral regimen: Hydrocortisone 20 mg AM, 10 mg afternoon
• Add fludrocortisone 0.1 mg daily if PRIMARY adrenal insufficiency confirmed
• Complete workup for underlying etiology (autoimmune, TB, HIV, metastases)
• Endocrinology consultation for long-term management plan

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE PLANNING
═══════════════════════════════════════════════════════════════════════════════

ESSENTIAL BEFORE DISCHARGE:
• Patient and family education on sick-day rules
• Emergency hydrocortisone injection kit prescribed and technique demonstrated
• Medical alert identification ordered/obtained
• Written action plan for emergencies
• Follow-up appointment scheduled within 1-2 weeks
• Adequate medication supply

═══════════════════════════════════════════════════════════════════════════════
SICK-DAY RULES (Critical Patient Education)
═══════════════════════════════════════════════════════════════════════════════

MINOR ILLNESS: DOUBLE the oral hydrocortisone dose
MODERATE ILLNESS: TRIPLE the oral hydrocortisone dose
SEVERE ILLNESS/INABILITY TO TAKE ORAL: Self-inject hydrocortisone 100 mg IM, call emergency services
VOMITING OR DIARRHEA: If unable to keep oral medication down >4 hours: IM hydrocortisone

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP SCHEDULE
═══════════════════════════════════════════════════════════════════════════════

• WEEK 1-2: Endocrinology follow-up; confirm diagnosis; review sick-day rules
• MONTH 1: Assess maintenance doses; check electrolytes
• MONTH 3: Review symptoms, check for over/under-replacement
• EVERY 6 MONTHS: Once stable; routine monitoring
• ANNUAL: Comprehensive review; screen for associated conditions

⚠️ Individual follow-up schedules should be determined by the treating physician based on patient-specific factors.`
    },

    redFlags: [
      '🔴 CRITICAL: Refractory hypotension (SBP <90 mmHg) not responding to fluids - give hydrocortisone IMMEDIATELY',
      '🔴 CRITICAL: Altered mental status or coma - indicates severe crisis, airway protection may be needed',
      '🔴 CRITICAL: Severe hyperkalemia with ECG changes (peaked T waves, widened QRS) - cardiac arrest risk',
      '🔴 CRITICAL: Hypoglycemia with seizures or unresponsiveness - give IV dextrose immediately',
      '🔴 CRITICAL: Known adrenal insufficiency with fever and vomiting - cannot absorb oral steroids, needs IV',
      '🔴 CRITICAL: No improvement within 6-12 hours of starting treatment - reassess diagnosis and precipitant',
      '🟠 URGENT: Hyponatremia <120 mEq/L - risk of seizures; correct cautiously to avoid osmotic demyelination',
      '🟠 URGENT: History of chronic steroid use with acute illness - likely HPA axis suppression, needs stress dosing',
      '🟠 URGENT: Bilateral adrenal hemorrhage suspected (severe sepsis, meningococcemia, anticoagulation)',
      '🟠 URGENT: Concurrent severe infection without improvement on antibiotics',
      '🟠 URGENT: Signs of pituitary apoplexy (sudden headache, visual changes, ophthalmoplegia)',
      '🟠 URGENT: Pregnancy with adrenal crisis - high maternal and fetal mortality'
    ],

    complications: [
      'CARDIOVASCULAR: Refractory hypotensive shock, cardiovascular collapse, death (if untreated)',
      'METABOLIC: Severe hypoglycemia with seizures and brain damage, profound hyponatremia with cerebral edema',
      'CARDIAC: Hyperkalemia-induced arrhythmias, cardiac arrest (primary AI with severe hyperkalemia)',
      'NEUROLOGICAL: Seizures, coma, osmotic demyelination (if sodium corrected too rapidly)',
      'RENAL: Acute kidney injury from hypoperfusion, prerenal azotemia',
      'IATROGENIC: Osmotic demyelination syndrome from overly rapid sodium correction',
      'INFECTIOUS: Underlying infection may progress due to impaired immune response; sepsis',
      'DEATH: Mortality 0.5-2% per episode with treatment; approaches 100% if untreated',
      'RECURRENCE: Future crises if sick-day rules not followed, medication non-compliance',
      'CHRONIC COMPLICATIONS: If over-replaced - osteoporosis, weight gain, glucose intolerance'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
ACUTE PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

• MORTALITY WITH TREATMENT: 0.5-2% per crisis episode
• MORTALITY WITHOUT TREATMENT: Approaches 100%
• Most deaths occur from cardiovascular collapse before or shortly after diagnosis
• Clinical improvement typically begins within hours of hydrocortisone administration

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

• LIFE EXPECTANCY: Near-normal with proper replacement therapy and patient education
• EXCESS MORTALITY: 2-fold increased risk compared to general population
• Main causes of excess mortality: Adrenal crisis, cardiovascular disease, infections
• Crisis recurrence rate: 6-8 episodes per 100 patient-years
• Many crises are preventable with proper sick-day management

═══════════════════════════════════════════════════════════════════════════════
QUALITY OF LIFE
═══════════════════════════════════════════════════════════════════════════════

• Most patients report good quality of life with proper replacement
• Fatigue is the most common persistent symptom despite adequate replacement
• Regular endocrinology follow-up improves outcomes

⚠️ Prognosis varies based on individual factors. Discuss specific prognosis with your treating physician.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

PREVENTING ADRENAL INSUFFICIENCY:
• Judicious use of glucocorticoids - use lowest effective dose and duration
• Gradual tapering of chronic glucocorticoids (never stop abruptly after >3 weeks of use)
• Consider steroid-sparing agents where appropriate
• Proper treatment of TB to prevent adrenal destruction
• HIV treatment to prevent opportunistic infections affecting adrenals

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Crisis in Known AI)
═══════════════════════════════════════════════════════════════════════════════

PATIENT EDUCATION - THE CORNERSTONE:
• Teach sick-day rules to ALL patients with adrenal insufficiency
• Written action plan for illness and emergencies
• Ensure patients understand their condition is lifelong
• Regular reinforcement at each visit

EMERGENCY PREPAREDNESS:
• Prescribe emergency hydrocortisone injection kit
• Teach injection technique to patient and family members
• Medical alert bracelet/card - ESSENTIAL
• Steroid emergency card to carry at all times

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT - PREVENTION STRATEGIES
═══════════════════════════════════════════════════════════════════════════════

• Early TB diagnosis and treatment to prevent adrenal destruction
• HIV testing and ART access to reduce opportunistic infections
• Healthcare worker education on adrenal insufficiency recognition
• Ensure hydrocortisone and fludrocortisone availability at healthcare facilities
• Simple picture-based sick-day rule cards for low-literacy patients`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

Your doctor knows your individual medical history and can provide personalized advice that this general information cannot.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING ADRENAL INSUFFICIENCY
═══════════════════════════════════════════════════════════════════════════════

Your adrenal glands sit on top of your kidneys and make cortisol, a hormone essential for life. Cortisol helps your body respond to stress, maintain blood pressure, control blood sugar, and fight infections. When your adrenals cannot make enough cortisol, you need to take it as medicine every day.

WITHOUT CORTISOL REPLACEMENT, YOU CANNOT SURVIVE.

This is a lifelong condition. You will need to take medication every day for the rest of your life. The good news is that with proper treatment, you can live a normal, active life.

═══════════════════════════════════════════════════════════════════════════════
YOUR DAILY MEDICATION
═══════════════════════════════════════════════════════════════════════════════

HYDROCORTISONE (or prednisone):
• Take EVERY day, even when you feel well
• Take in divided doses as prescribed (usually morning and afternoon)
• NEVER stop taking it suddenly - this can be life-threatening
• If you run out, this is an EMERGENCY - contact your doctor immediately

⚠️ Your specific medication regimen should be determined by your doctor. Do not adjust doses without medical advice.

═══════════════════════════════════════════════════════════════════════════════
SICK-DAY RULES - DISCUSS WITH YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

When you are unwell, your body needs MORE cortisol. General guidance (confirm with YOUR doctor):

🟢 MINOR ILLNESS: Consider DOUBLING your usual dose
🟠 MODERATE ILLNESS: Consider TRIPLING your usual dose
🔴 SEVERE ILLNESS / VOMITING: INJECT emergency hydrocortisone and CALL FOR HELP

⚠️ WHEN IN DOUBT, ALWAYS SEEK MEDICAL ADVICE. It is better to be seen and sent home than to delay treatment.

═══════════════════════════════════════════════════════════════════════════════
EMERGENCY INJECTION
═══════════════════════════════════════════════════════════════════════════════

Your doctor should teach you how and when to use emergency hydrocortisone injection. Make sure:
• You have the injection kit at home
• You know how to use it
• Family members know how to give it to you
• You call emergency services AFTER injecting

═══════════════════════════════════════════════════════════════════════════════
MEDICAL ALERT IDENTIFICATION - ESSENTIAL
═══════════════════════════════════════════════════════════════════════════════

ALWAYS wear a medical alert bracelet or necklace. Carry a steroid emergency card. This can SAVE YOUR LIFE if you cannot speak for yourself.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - GET HELP IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Call emergency services or go to hospital if you have:
• Severe weakness, cannot stand up
• Severe dizziness or fainting
• Confusion
• Severe abdominal pain, nausea, or vomiting
• Fever with inability to take your medication

⚠️ DO NOT WAIT - adrenal crisis can be fatal within hours. When in doubt, seek emergency care.

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What caused my adrenal insufficiency?
• What are my exact medication doses and timing?
• How do I use my emergency injection?
• What specific sick-day rules should I follow?
• Who do I contact in an emergency?
• When is my next follow-up appointment?

Remember: Your healthcare team is your best resource. This information supports but does not replace their guidance.`,

    references: [
      'Bornstein SR, Allolio B, Arlt W, et al. Diagnosis and treatment of primary adrenal insufficiency: An Endocrine Society Clinical Practice Guideline. J Clin Endocrinol Metab. 2016;101(2):364-389. doi:10.1210/jc.2015-1710',
      'Husebye ES, Pearce SH, Krone NP, Kampe O. Adrenal insufficiency. Lancet. 2021;397(10274):613-629. doi:10.1016/S0140-6736(21)00136-7',
      'Rushworth RL, Torpy DJ, Falhammar H. Adrenal crisis. N Engl J Med. 2019;381(9):852-861. doi:10.1056/NEJMra1807486',
      'Puar TH, Stikkelbroeck NM, Smans LC, et al. Adrenal crisis: Still a deadly event in the 21st century. Am J Med. 2016;129(3):339.e1-339.e9. doi:10.1016/j.amjmed.2015.08.024',
      'Hahner S, Spinnler C, Grasemann C, et al. Epidemiology of adrenal crisis in chronic adrenal insufficiency. Eur J Endocrinol. 2010;162(3):597-602. doi:10.1530/EJE-09-0884',
      'World Health Organization. Global tuberculosis report 2023. Geneva: WHO; 2023.',
      'Bancos I, Hahner S, Tomlinson J, Arlt W. Diagnosis and management of adrenal insufficiency. Lancet Diabetes Endocrinol. 2015;3(3):216-226. doi:10.1016/S2213-8587(14)70142-1',
      'Charmandari E, Nicolaides NC, Chrousos GP. Adrenal insufficiency. Lancet. 2014;383(9935):2152-2167. doi:10.1016/S0140-6736(13)61684-0',
      'Ngaosuwan K, Johnston DG, Geer EB, et al. Mortality in patients with adrenal insufficiency. Endocrine. 2021;74(2):286-294. doi:10.1007/s12020-021-02755-9',
      'Johannsson G, Falorni A, Skrtic S, et al. Adrenal insufficiency: Review of clinical outcomes. Clin Endocrinol. 2015;82(1):2-11. doi:10.1111/cen.12603'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 23. ACUTE APPENDICITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '23',
    name: 'Acute Appendicitis',
    slug: 'acute-appendicitis',
    specialty: 'Emergency Medicine',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K35.80',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute appendicitis is the most common abdominal surgical emergency worldwide, characterized by inflammation of the vermiform appendix. It requires prompt diagnosis and treatment to prevent progression to perforation, abscess formation, and peritonitis. While appendectomy remains the gold standard treatment, antibiotic-first strategies are emerging as alternatives in selected uncomplicated cases.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• CLASSIC TRIAD: Periumbilical pain migrating to RLQ + anorexia + fever (present in only 50-60% of cases)
• MCBURNEY'S POINT: Located 1/3 of the distance from ASIS to umbilicus - maximal tenderness here suggests appendicitis
• ALVARADO SCORE (MANTRELS): Validated clinical scoring system - score ≥7 highly suggestive of appendicitis
• TIME IS CRITICAL: Risk of perforation increases significantly after 36-48 hours of symptom onset
• PERFORATION RATE: ~20% at presentation, increases to 70% by 48 hours if untreated
• CT SCAN: Gold standard imaging in adults with sensitivity >95% and specificity >95%
• ULTRASOUND FIRST: Preferred initial imaging in children, pregnant women, and resource-limited settings
• APPENDICEAL DIAMETER >6mm on imaging is suggestive; >10mm highly suggestive of appendicitis
• ANTIBIOTICS ALONE: May be appropriate for uncomplicated appendicitis in selected patients (controversial)
• LAPAROSCOPIC APPENDECTOMY: Preferred surgical approach when available - faster recovery, less wound infection
• NEGATIVE APPENDECTOMY RATE: Target <15% - balance between missing appendicitis and unnecessary surgery
• PEDIATRIC PRESENTATION: Often atypical; higher perforation rates due to delayed diagnosis
• ELDERLY PRESENTATION: Often atypical with minimal symptoms; higher morbidity and mortality
• PREGNANCY: Appendicitis is the most common non-obstetric surgical emergency; appendix displaced superiorly
• APPENDICEAL ABSCESS: May require interval appendectomy 6-8 weeks after drainage/antibiotics

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

INITIATING EVENT:
• Luminal obstruction is the primary cause in most cases (60-70%)
• Fecaliths (hardened stool) - most common cause in adults
• Lymphoid hyperplasia - common in children/young adults (often post-viral)
• Foreign bodies, parasites, tumors (carcinoid, adenocarcinoma) - less common

PROGRESSION OF DISEASE:
1. OBSTRUCTION PHASE:
   • Mucus accumulates, intraluminal pressure rises
   • Venous congestion and mucosal ischemia begin
   • Visceral afferent fibers stimulated → periumbilical pain (referred from T10)

2. INFLAMMATORY PHASE:
   • Bacterial overgrowth (E. coli, Bacteroides, Pseudomonas)
   • Transmural inflammation develops
   • Parietal peritoneum involvement → localized RLQ pain (somatic)
   • This explains the classic "migration" of pain

3. GANGRENOUS PHASE:
   • Arterial compromise leads to tissue necrosis
   • Wall weakening and impending perforation
   • Usually occurs 24-36 hours after onset

4. PERFORATED PHASE:
   • Wall rupture with spillage of infected contents
   • If walled off by omentum → periappendiceal abscess
   • If free perforation → generalized peritonitis

ANATOMICAL CONSIDERATIONS:
• Retrocecal appendix (65%): May have atypical presentation, flank/back pain
• Pelvic appendix (30%): May cause suprapubic pain, urinary symptoms, diarrhea
• Subhepatic appendix (rare): May mimic cholecystitis
• Left-sided appendicitis: Occurs in situs inversus`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Lifetime risk: 7-8% (slightly higher in males)
• Annual incidence: 90-100 per 100,000 population in Western countries
• Peak age: 10-30 years (median age ~22 years)
• Male:Female ratio: 1.4:1
• Most common abdominal surgical emergency worldwide
• ~300,000 appendectomies performed annually in the United States alone
• Mortality: <1% for uncomplicated; 5-15% for perforated (higher in elderly)
• Negative appendectomy rate: 10-20% historically (decreasing with better imaging)
• Perforation rate at presentation: 20-30% overall; higher in children (<5 years: 45-80%) and elderly (>50 years: 50-70%)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HISTORICALLY LOWER INCIDENCE: Traditionally reported as less common in Africa due to high-fiber diets
• RISING INCIDENCE: Urbanization and dietary changes leading to increasing appendicitis rates, especially in cities
• LATE PRESENTATION: Patients often present 3-7 days after symptom onset due to:
  - Limited healthcare access in rural areas
  - Traditional medicine consultation first
  - Financial barriers to seeking care
  - Lack of awareness of condition severity
• HIGH PERFORATION RATES: 40-70% perforation rate at presentation (vs 20-30% in developed countries)
• DIAGNOSTIC CHALLENGES:
  - Limited access to CT scanning in many facilities
  - Ultrasound availability variable
  - Reliance on clinical diagnosis and scoring systems
  - Differential diagnosis complicated by tropical infections (typhoid, amoebiasis)
• SURGICAL RESOURCES:
  - Limited operating theater availability
  - Laparoscopic surgery not widely available
  - Open appendectomy remains standard in most settings
  - Anesthesia and blood products may be limited
• CO-MORBIDITIES:
  - HIV/AIDS may alter presentation and increase complications
  - Malnutrition affects wound healing
  - Concurrent tropical infections common
• PEDIATRIC BURDEN: High perforation rates in children due to delayed presentation
• MORTALITY: Higher than developed countries (5-10% overall, up to 20% for complicated cases)
• DIFFERENTIAL DIAGNOSIS: Must consider typhoid fever (ileal perforation), intestinal TB, amoebic colitis
• PARASITIC CAUSES: Ascaris lumbricoides, Enterobius vermicularis can cause appendiceal obstruction`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CLASSIC PRESENTATION (50-60% of cases)
═══════════════════════════════════════════════════════════════════════════════

1. PERIUMBILICAL PAIN → MIGRATES TO RLQ (over 12-24 hours)
   • Initially vague, poorly localized periumbilical/epigastric pain (visceral)
   • Pain "migrates" to right lower quadrant as parietal peritoneum involved (somatic)
   • This sequence is highly specific for appendicitis

2. ANOREXIA (nearly universal - 95%)
   • If patient is hungry, consider alternative diagnosis

3. NAUSEA AND VOMITING (75%)
   • Usually follows onset of pain (pain first, then vomiting)
   • If vomiting precedes pain, consider gastroenteritis

4. LOW-GRADE FEVER (40%)
   • Temperature usually 37.5-38.5°C
   • High fever (>39°C) suggests perforation or alternative diagnosis

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

PAIN CHARACTERISTICS:
• Location: Initially periumbilical → migrates to RLQ
• Character: Initially dull, colicky → becomes sharp, constant
• Aggravating factors: Movement, coughing, deep breathing
• Timing: Usually evolves over 12-24 hours

GASTROINTESTINAL:
• Anorexia (almost universal)
• Nausea and vomiting (follows pain onset)
• Constipation (more common than diarrhea)
• Diarrhea (may occur with pelvic appendix irritating rectum)

URINARY (with pelvic appendix):
• Frequency
• Dysuria
• May cause sterile pyuria (false positive urinalysis)

SYSTEMIC:
• Low-grade fever
• Malaise
• Chills (suggests perforation)

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION SIGNS
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Low-grade fever (37.5-38.5°C)
• Tachycardia (mild)
• Blood pressure usually normal (hypotension suggests sepsis/perforation)

INSPECTION:
• Patient lies still (movement worsens pain)
• May lie with right hip flexed
• Flushed appearance with fever
• Foetor oris (bad breath)

ABDOMINAL EXAMINATION:
• McBurney's Point Tenderness: Maximum tenderness 1/3 of distance from ASIS to umbilicus
• Guarding: Voluntary → involuntary (indicates peritoneal irritation)
• Rebound Tenderness: Pain on release of pressure (peritoneal inflammation)
• Rigidity: Board-like rigidity suggests perforation with peritonitis

SPECIAL SIGNS:
• ROVSING'S SIGN: RLQ pain with palpation of LLQ (positive in 70%)
• PSOAS SIGN: Pain with passive right hip extension (retrocecal appendix)
• OBTURATOR SIGN: Pain with internal rotation of flexed right hip (pelvic appendix)
• DUNPHY'S SIGN: Increased pain with coughing
• HEEL DROP TEST: Pain in RLQ when patient drops from tiptoes to heels
• POINTING SIGN: Patient can point with one finger to site of maximal pain

DIGITAL RECTAL EXAMINATION:
• Right-sided tenderness suggests pelvic appendicitis
• Fullness may indicate pelvic abscess
• Not routinely required but helpful in atypical presentations

═══════════════════════════════════════════════════════════════════════════════
ATYPICAL PRESENTATIONS
═══════════════════════════════════════════════════════════════════════════════

RETROCECAL APPENDIX (65% of variants):
• Back or flank pain
• Minimal abdominal findings
• Psoas sign positive
• May mimic renal colic

PELVIC APPENDIX:
• Suprapubic pain
• Urinary symptoms (frequency, dysuria)
• Diarrhea, tenesmus
• Obturator sign positive
• May mimic UTI or PID

PREGNANCY:
• Appendix displaced superiorly by gravid uterus
• RUQ pain in third trimester
• Peritoneal signs may be masked
• Leukocytosis normal in pregnancy - less reliable

PEDIATRIC (<5 years):
• Cannot describe classic migration
• Irritability, lethargy
• Diarrhea more common
• Higher perforation rates (45-80%)

ELDERLY:
• Minimal symptoms
• Low-grade or absent fever
• Less marked leukocytosis
• High perforation rates (50-70%)
• Higher mortality (up to 5-10%)

IMMUNOCOMPROMISED (HIV/AIDS):
• Blunted inflammatory response
• Atypical presentations common
• Opportunistic infections may coexist
• Higher complication rates`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Clinical diagnosis should be made by a qualified physician. Scoring systems and imaging are aids to clinical judgment, not replacements for it.

STEP 1: CLINICAL ASSESSMENT
• History: Pain migration, anorexia, nausea/vomiting sequence
• Physical examination: McBurney's point tenderness, peritoneal signs
• Calculate Alvarado Score

STEP 2: LABORATORY TESTS
• CBC: WBC >10,000 with left shift
• CRP: Elevated (helpful in combination with WBC)
• Urinalysis: Rule out UTI (but sterile pyuria may occur)

STEP 3: IMAGING (based on clinical probability)
• Low probability: Observation or CT
• Moderate probability: CT (adults) or Ultrasound (children/pregnant)
• High probability: May proceed to surgery without imaging

═══════════════════════════════════════════════════════════════════════════════
ALVARADO SCORE (MANTRELS)
═══════════════════════════════════════════════════════════════════════════════

SYMPTOMS (3 points):
• Migration of pain to RLQ: 1 point
• Anorexia: 1 point
• Nausea/Vomiting: 1 point

SIGNS (3 points):
• Tenderness in RLQ: 2 points
• Rebound tenderness: 1 point

LABORATORY (2 points):
• Elevated temperature (>37.3°C): 1 point
• Leukocytosis (>10,000/μL): 2 points
• Shift to left (>75% neutrophils): 1 point

TOTAL: 10 points

INTERPRETATION:
• 0-4: Appendicitis unlikely - discharge with return precautions or observe
• 5-6: Possible appendicitis - imaging recommended (CT or US)
• 7-8: Probable appendicitis - surgical consultation, consider imaging
• 9-10: Highly probable - proceed to appendectomy (imaging optional)

═══════════════════════════════════════════════════════════════════════════════
APPENDICITIS INFLAMMATORY RESPONSE (AIR) SCORE
═══════════════════════════════════════════════════════════════════════════════

• Vomiting: 1 point
• Pain in RIF: 1 point
• Rebound tenderness: Light (1), Medium (2), Strong (3)
• Temperature ≥38.5°C: 1 point
• PMN leukocytes 70-84% (1), ≥85% (2)
• WBC 10-14.9 (1), ≥15 (2)
• CRP 10-49 (1), ≥50 (2)

INTERPRETATION:
• 0-4: Low probability - outpatient management
• 5-8: Intermediate - imaging, active observation
• 9-12: High probability - surgical exploration

═══════════════════════════════════════════════════════════════════════════════
IMAGING FINDINGS
═══════════════════════════════════════════════════════════════════════════════

CT ABDOMEN/PELVIS (Gold Standard in Adults):
• Sensitivity: 94-98%
• Specificity: 95-98%
• Findings:
  - Appendiceal diameter >6mm (>10mm highly suggestive)
  - Appendiceal wall thickening (>2mm)
  - Periappendiceal fat stranding
  - Appendicolith (30% of cases)
  - Periappendiceal fluid
  - Abscess or phlegmon (if complicated)

ULTRASOUND (First-line in Children, Pregnant, Resource-Limited):
• Sensitivity: 75-90% (operator dependent)
• Specificity: 85-95%
• Findings:
  - Non-compressible appendix >6mm diameter
  - Target sign (concentric rings on cross-section)
  - Appendicolith with shadowing
  - Periappendiceal fluid
  - Increased color Doppler flow
• Advantages: No radiation, portable, repeatable
• Limitations: Operator dependent, limited by body habitus, bowel gas

MRI (Second-line in Pregnancy if US inconclusive):
• Sensitivity: 90-100%
• Specificity: 94-98%
• No ionizing radiation - safe in pregnancy

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

COMPLETE BLOOD COUNT:
• WBC: >10,000/μL in 80-90% of cases
• Neutrophilia with left shift (>75% PMNs)
• Normal WBC does NOT exclude appendicitis (especially early or in elderly)

C-REACTIVE PROTEIN (CRP):
• Usually elevated (>10 mg/L)
• Higher levels (>50 mg/L) suggest complicated appendicitis
• Combined with WBC, improves diagnostic accuracy

URINALYSIS:
• Usually normal
• May show sterile pyuria (WBCs without bacteria) - appendix near ureter
• Significant bacteriuria suggests UTI as alternative diagnosis

PREGNANCY TEST:
• All women of reproductive age - rule out ectopic pregnancy`,

    differentialDiagnosis: [
      'Mesenteric Lymphadenitis - Common in children, often post-viral; RLQ pain but less localized tenderness; CT shows enlarged mesenteric lymph nodes without appendiceal changes',
      'Ovarian Cyst (Ruptured/Torsion) - Sudden onset pelvic pain, may have mid-cycle timing (mittelschmerz); pelvic ultrasound diagnostic; positive adnexal tenderness',
      'Ectopic Pregnancy - Reproductive age women; missed period, vaginal bleeding, positive pregnancy test; pelvic ultrasound shows adnexal mass, empty uterus',
      'Pelvic Inflammatory Disease - Sexually active women; bilateral lower abdominal pain, vaginal discharge, cervical motion tenderness; fever usually higher',
      'Urinary Tract Infection/Pyelonephritis - Dysuria, frequency, urgency, flank pain; urinalysis shows pyuria and bacteriuria; CVA tenderness',
      'Renal/Ureteric Colic - Colicky flank pain radiating to groin; hematuria; restlessness (vs lying still in appendicitis); CT shows stone',
      'Acute Cholecystitis - RUQ pain, positive Murphy sign; often post-fatty meal; ultrasound shows gallstones, wall thickening',
      'Gastroenteritis - Vomiting BEFORE pain (vs after in appendicitis); diarrhea prominent; diffuse abdominal pain; often sick contacts',
      'Crohn Disease - May mimic appendicitis with RLQ pain; diarrhea, weight loss history; CT shows terminal ileal thickening, skip lesions',
      'Meckel Diverticulitis - Similar presentation to appendicitis; located in ileum; CT may show inflamed diverticulum; often discovered at surgery',
      'Typhoid Fever (Africa) - Fever, relative bradycardia, rose spots; may present with RLQ pain or ileal perforation; blood/stool cultures positive',
      'Amoebic Colitis (Africa) - Bloody diarrhea, RLQ pain; stool microscopy shows trophozoites; may form amoebic abscess',
      'Intestinal Tuberculosis (Africa) - Chronic symptoms, weight loss, night sweats; may form RLQ mass; ascites common; biopsy shows granulomas',
      'Psoas Abscess - Limp, hip flexion; may have fever, back pain; CT shows psoas muscle collection; consider TB in Africa'
    ],

    investigations: [
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - WBC >10,000 with left shift supports diagnosis; normal WBC does not exclude appendicitis',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🔴 IMMEDIATE - Elevated in most cases; CRP >50 mg/L suggests complicated appendicitis',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🔴 IMMEDIATE - Rule out UTI; sterile pyuria may occur with pelvic appendicitis',
        availability: 'BASIC'
      },
      {
        name: 'Urine Pregnancy Test (β-hCG)',
        indication: '🔴 IMMEDIATE - All women of reproductive age; rule out ectopic pregnancy',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: '🔴 IMMEDIATE - Assess hydration, electrolytes; prepare for surgery; identify AKI',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 URGENT - If cholecystitis in differential; usually normal in appendicitis',
        availability: 'BASIC'
      },
      {
        name: 'Serum Lipase/Amylase',
        indication: '🟠 URGENT - If pancreatitis in differential; usually normal in appendicitis',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Screen',
        indication: '🔴 IMMEDIATE - Pre-operative preparation if surgery planned',
        availability: 'BASIC'
      },
      {
        name: 'CT Abdomen/Pelvis with IV Contrast',
        indication: '🔴 IMMEDIATE - Gold standard in adults; sensitivity >95%; shows appendiceal diameter, inflammation, complications',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 IMMEDIATE - First-line in children, pregnant women, resource-limited settings; non-compressible appendix >6mm diagnostic',
        availability: 'BASIC'
      },
      {
        name: 'MRI Abdomen',
        indication: '🟠 URGENT - Second-line in pregnancy if ultrasound inconclusive; no radiation exposure',
        availability: 'ADVANCED'
      },
      {
        name: 'Abdominal X-ray',
        indication: 'Limited utility - May show fecalith (5-10%), signs of perforation (free air), or bowel obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 URGENT - Pre-operative evaluation; rule out lower lobe pneumonia mimicking abdominal pain',
        availability: 'BASIC'
      },
      {
        name: 'Procalcitonin',
        indication: '🟠 URGENT if available - Elevated levels suggest complicated appendicitis or perforation',
        availability: 'ADVANCED'
      },
      {
        name: 'Blood Cultures',
        indication: '🟠 URGENT - If sepsis suspected (high fever, tachycardia, hypotension); obtain before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Stool Studies (Microscopy, Culture)',
        indication: '🟠 Consider in endemic areas - Rule out amoebic colitis, typhoid if clinical suspicion',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 Consider - May alter surgical planning; affects prognosis; opportunistic infections in differential',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ NPO (Nil Per Os) - Keep patient fasting in preparation for surgery',
        '⚡ IV ACCESS - Establish with large-bore cannula for fluid resuscitation and medications',
        '⚡ IV FLUID RESUSCITATION - Normal saline or Lactated Ringer; correct dehydration from vomiting/reduced intake',
        '⚡ ANALGESIA - IV opioids (morphine 0.1 mg/kg or fentanyl 1 mcg/kg); does NOT mask surgical findings',
        '⚡ ANTIEMETICS - Ondansetron 4-8 mg IV for nausea/vomiting',
        '⚡ CALCULATE ALVARADO SCORE - Document to guide management decisions',
        '⚡ SURGICAL CONSULTATION - All suspected appendicitis cases require surgical evaluation',
        '⚡ PREOPERATIVE ANTIBIOTICS - Give within 1 hour of skin incision; cover Gram-negatives and anaerobes',
        '⚡ LABORATORY TESTS - CBC, CRP, BMP, urinalysis, pregnancy test (women)',
        '⚡ IMAGING - CT (adults) or Ultrasound (children/pregnant) if diagnosis uncertain',
        '⚡ URINARY CATHETER - Consider for operative cases',
        '⚡ NASOGASTRIC TUBE - If significant vomiting or suspected bowel obstruction',
        '⚡ DVT PROPHYLAXIS - Mechanical (SCDs) pre-operatively; chemical post-operatively if indicated',
        '⚡ INFORMED CONSENT - Discuss appendectomy, risks, alternatives including antibiotic-only approach if appropriate'
      ],
      pharmacological: [
        {
          drug: 'Ceftriaxone + Metronidazole',
          indication: '🔴 FIRST-LINE PROPHYLAXIS/TREATMENT - Covers Gram-negatives and anaerobes; appropriate for most appendicitis cases',
          dose: 'Ceftriaxone 1-2 g IV once daily + Metronidazole 500 mg IV every 8 hours',
          route: 'IV',
          duration: 'Uncomplicated: Single pre-op dose (prophylaxis); Complicated/perforated: 3-5 days IV, may extend to 7-10 days total',
          contraindications: ['Cephalosporin allergy (use fluoroquinolone alternative)', 'Metronidazole: Disulfiram-like reaction with alcohol'],
          sideEffects: ['Diarrhea', 'C. difficile colitis', 'Allergic reactions', 'Metronidazole: Metallic taste, nausea, neuropathy with prolonged use'],
          alternatives: ['Piperacillin-tazobactam', 'Ampicillin-sulbactam', 'Ciprofloxacin + metronidazole if cephalosporin allergic']
        },
        {
          drug: 'Piperacillin-Tazobactam',
          indication: '🔴 ALTERNATIVE BROAD-SPECTRUM - Single agent covering Gram-negatives and anaerobes; good for complicated appendicitis',
          dose: '3.375 g IV every 6 hours (or 4.5 g every 8 hours); Pediatric: 80 mg/kg (piperacillin component) every 8 hours',
          route: 'IV',
          duration: 'Uncomplicated: Single pre-op dose; Complicated: 4-7 days',
          contraindications: ['Penicillin allergy', 'Severe renal impairment (dose adjust)'],
          sideEffects: ['Diarrhea', 'Nausea', 'Allergic reactions', 'Thrombocytopenia (rare)'],
          alternatives: ['Ceftriaxone + metronidazole', 'Meropenem (if ESBL suspected)']
        },
        {
          drug: 'Ciprofloxacin + Metronidazole',
          indication: '🟠 FOR PENICILLIN/CEPHALOSPORIN ALLERGY - Effective alternative combination',
          dose: 'Ciprofloxacin 400 mg IV every 12 hours + Metronidazole 500 mg IV every 8 hours',
          route: 'IV',
          duration: 'Uncomplicated: Single pre-op dose; Complicated: 4-7 days',
          contraindications: ['Fluoroquinolone hypersensitivity', 'Pregnancy', 'Children <18 years (relative - may use if no alternatives)', 'Prolonged QT interval'],
          sideEffects: ['Tendinopathy', 'QT prolongation', 'CNS effects', 'C. difficile colitis'],
          alternatives: ['Aztreonam + metronidazole', 'Meropenem']
        },
        {
          drug: 'Meropenem',
          indication: '🟠 RESERVED FOR SEVERE/COMPLICATED CASES - Broad-spectrum carbapenem; use if ESBL risk or failing other antibiotics',
          dose: '1 g IV every 8 hours; Pediatric: 20 mg/kg every 8 hours (max 1 g/dose)',
          route: 'IV',
          duration: '5-7 days for complicated appendicitis',
          contraindications: ['Carbapenem allergy', 'Caution in penicillin allergy (cross-reactivity ~1%)'],
          sideEffects: ['Diarrhea', 'Nausea', 'Seizures (rare, especially in renal impairment)', 'C. difficile colitis'],
          alternatives: ['Piperacillin-tazobactam', 'Imipenem-cilastatin']
        },
        {
          drug: 'Ertapenem',
          indication: '🟠 ONCE-DAILY CARBAPENEM - Convenient dosing for complicated appendicitis; no Pseudomonas coverage',
          dose: '1 g IV once daily; Pediatric (3 months-12 years): 15 mg/kg every 12 hours (max 1 g/day)',
          route: 'IV or IM',
          duration: '4-7 days',
          contraindications: ['Carbapenem allergy', 'Not for Pseudomonas infections'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache', 'Seizures (rare)'],
          alternatives: ['Meropenem (if Pseudomonas concern)', 'Piperacillin-tazobactam']
        },
        {
          drug: 'Morphine Sulfate',
          indication: '🔴 ANALGESIA - Effective pain control; does NOT mask surgical signs or delay diagnosis',
          dose: '0.05-0.1 mg/kg IV every 2-4 hours PRN; titrate to pain control',
          route: 'IV',
          duration: 'Until surgery and post-operative as needed',
          contraindications: ['Respiratory depression', 'Hypotension', 'Known allergy'],
          sideEffects: ['Nausea/vomiting', 'Constipation', 'Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Fentanyl (shorter acting)', 'Hydromorphone', 'Tramadol (less potent)']
        },
        {
          drug: 'Fentanyl',
          indication: '🔴 ANALGESIA - Short-acting; good for procedural pain and titration',
          dose: '0.5-1 mcg/kg IV every 1-2 hours PRN; or continuous infusion',
          route: 'IV',
          duration: 'Acute pain management',
          contraindications: ['Respiratory depression', 'Use with caution in opioid-naive patients'],
          sideEffects: ['Respiratory depression', 'Nausea', 'Sedation', 'Chest wall rigidity (with rapid IV push)'],
          alternatives: ['Morphine (longer acting)', 'Hydromorphone']
        },
        {
          drug: 'Ondansetron',
          indication: '🔴 ANTIEMETIC - First-line for nausea/vomiting',
          dose: 'Adults: 4-8 mg IV every 8 hours PRN; Pediatric: 0.1-0.15 mg/kg IV (max 4 mg)',
          route: 'IV or PO/ODT',
          duration: 'As needed for symptom control',
          contraindications: ['QT prolongation', 'Known hypersensitivity', 'Concomitant apomorphine'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation'],
          alternatives: ['Metoclopramide', 'Promethazine']
        },
        {
          drug: 'Ketorolac',
          indication: '🟠 NSAID ANALGESIA - Post-operative pain; opioid-sparing effect',
          dose: '15-30 mg IV every 6 hours; max 5 days use',
          route: 'IV or IM',
          duration: 'Maximum 5 days',
          contraindications: ['Renal impairment', 'Active GI bleeding', 'Coagulopathy', 'Post-CABG', 'Third trimester pregnancy'],
          sideEffects: ['GI bleeding/ulceration', 'Renal impairment', 'Platelet dysfunction'],
          alternatives: ['Paracetamol/Acetaminophen', 'Ibuprofen (oral)']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🔴 ADJUNCT ANALGESIA - Safe, effective; use in combination with opioids for multimodal analgesia',
          dose: 'Adults: 1 g IV/PO every 6 hours (max 4 g/day); Pediatric: 15 mg/kg every 6 hours',
          route: 'IV or PO',
          duration: 'As needed for pain control',
          contraindications: ['Severe hepatic impairment', 'Known hypersensitivity'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Rare allergic reactions'],
          alternatives: ['NSAIDs (if not contraindicated)']
        },
        {
          drug: 'Metoclopramide',
          indication: '🟠 ALTERNATIVE ANTIEMETIC/PROKINETIC - Promotes gastric emptying',
          dose: '10 mg IV every 8 hours',
          route: 'IV',
          duration: 'Short-term use only',
          contraindications: ['Bowel obstruction', 'GI perforation', 'Parkinson disease', 'Seizure disorder'],
          sideEffects: ['Extrapyramidal symptoms', 'Tardive dyskinesia (prolonged use)', 'Drowsiness'],
          alternatives: ['Ondansetron', 'Promethazine']
        },
        {
          drug: 'Enoxaparin',
          indication: '🟠 DVT PROPHYLAXIS - Post-operative; consider risk-benefit',
          dose: '40 mg SC once daily; start 12-24 hours post-operatively',
          route: 'Subcutaneous',
          duration: 'Until ambulatory; longer for high-risk patients',
          contraindications: ['Active bleeding', 'Thrombocytopenia', 'Severe renal impairment (CrCl <30 - reduce dose)'],
          sideEffects: ['Bleeding', 'Injection site hematoma', 'Thrombocytopenia'],
          alternatives: ['Heparin 5000 units SC every 8-12 hours', 'Mechanical prophylaxis only if bleeding risk high']
        }
      ],
      nonPharmacological: [
        'NPO STATUS: Essential for surgical preparation; clear liquids may be given in select cases pending surgery timing',
        'IV FLUID RESUSCITATION: Crystalloids (NS or LR) to correct dehydration; maintenance fluids at appropriate rate',
        'POSITIONING: Semi-Fowler position may improve comfort and help localize infection if perforated',
        'TEMPERATURE MANAGEMENT: Active cooling if febrile; warming if hypothermic (post-operative)',
        'NASOGASTRIC DECOMPRESSION: If ileus, obstruction, or significant distension',
        'URINARY CATHETER: For monitoring in operative cases; remove early post-operatively',
        'EARLY MOBILIZATION: Post-operative; reduces DVT risk, promotes recovery',
        'DIET ADVANCEMENT: Clear liquids when bowel function returns; advance as tolerated',
        'WOUND CARE: Keep surgical site clean and dry; monitor for infection signs',
        'PERCUTANEOUS DRAINAGE: For appendiceal abscess not amenable to immediate surgery; CT or ultrasound-guided'
      ],
      surgical: [
        'LAPAROSCOPIC APPENDECTOMY: Preferred approach when available; faster recovery, less wound infection, better cosmesis; 3-port technique standard',
        'OPEN APPENDECTOMY: McBurney (muscle-splitting) or Lanz (transverse) incision; still standard in resource-limited settings; necessary for some complicated cases',
        'CONVERSION: Laparoscopic to open if visualization poor, dense adhesions, or complications encountered',
        'APPENDICEAL STUMP: Secured with sutures, stapler, or endoloop; some surgeons bury stump, others do not',
        'PERITONEAL LAVAGE: Controversial; saline irrigation of peritoneal cavity in perforated appendicitis',
        'DRAIN PLACEMENT: Generally not required for uncomplicated cases; consider for established abscess cavity',
        'INTERVAL APPENDECTOMY: Performed 6-8 weeks after non-operative treatment of appendiceal mass/abscess; controversial - some advocate for conservative management without interval surgery',
        'NEGATIVE APPENDECTOMY: If appendix appears normal at surgery, should still be removed; look for alternative pathology (Meckel diverticulum, ovarian pathology)',
        'SPECIMEN HANDLING: All removed appendices should be sent for histopathology (rule out carcinoid, adenocarcinoma)',
        'ANTIBIOTICS-FIRST APPROACH: Selected uncomplicated appendicitis (non-perforated, no appendicolith) may be treated with IV antibiotics followed by oral; recurrence ~25-30%; requires informed patient selection'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE SURGICAL CONSULTATION: All suspected appendicitis cases',
        '🔴 IMMEDIATE ICU: Septic shock, multi-organ dysfunction',
        '🔴 IMMEDIATE SURGERY: Signs of perforation, generalized peritonitis, sepsis',
        '🟠 URGENT SURGERY: Confirmed appendicitis without complications',
        '🟠 URGENT INTERVENTIONAL RADIOLOGY: Appendiceal abscess requiring percutaneous drainage',
        'GASTROENTEROLOGY: If Crohn disease suspected as underlying pathology',
        'ONCOLOGY: If carcinoid or adenocarcinoma found on pathology',
        'OBSTETRICS: All pregnant patients with suspected appendicitis',
        'PEDIATRIC SURGERY: Pediatric cases, especially if complicated'
      ],
      monitoring: [
        'VITAL SIGNS: Every 4 hours initially; more frequent if unstable',
        'PAIN ASSESSMENT: Regular pain scoring; adequate analgesia is essential',
        'TEMPERATURE: Monitor for fever indicating ongoing infection or complication',
        'ABDOMINAL EXAMINATION: Serial exams to detect progression or improvement',
        'WHITE BLOOD CELL COUNT: May repeat if clinical concern for deterioration',
        'URINE OUTPUT: Target >0.5 mL/kg/hr; indicator of adequate resuscitation',
        'WOUND INSPECTION: Daily post-operative; monitor for infection',
        'DIET TOLERANCE: Monitor for return of bowel function (flatus, bowel sounds)',
        'AMBULATION: Track early mobilization progress',
        'DRAIN OUTPUT: If drain placed, monitor quantity and character'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
POST-OPERATIVE COURSE (UNCOMPLICATED)
═══════════════════════════════════════════════════════════════════════════════

DAY 0 (Surgery Day):
• Post-anesthesia care unit recovery
• Pain management with IV opioids/NSAIDs
• Clear liquid diet when alert (laparoscopic)
• Early ambulation encouraged

DAY 1:
• Advance diet as tolerated
• Convert to oral analgesics
• Discontinue IV fluids if tolerating oral intake
• Discharge criteria assessment (laparoscopic - often same day or next day)

DISCHARGE CRITERIA:
• Tolerating oral diet
• Pain controlled with oral medications
• Afebrile
• Ambulating independently
• Able to void
• Understands discharge instructions

═══════════════════════════════════════════════════════════════════════════════
POST-OPERATIVE COURSE (COMPLICATED/PERFORATED)
═══════════════════════════════════════════════════════════════════════════════

• Longer hospitalization (3-7 days typically)
• Continued IV antibiotics (total 5-7 days)
• NPO until bowel function returns
• Possible ileus requiring NG decompression
• Daily wound inspection
• May need imaging if poor clinical response
• Transition to oral antibiotics to complete course

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

WEEK 1-2:
• Wound check (may be telephone follow-up for laparoscopic)
• Review pathology results
• Address any concerns

WEEK 4-6:
• Final follow-up if uncomplicated
• Full return to normal activities
• Heavy lifting restriction lifted

INTERVAL APPENDECTOMY (if applicable):
• 6-8 weeks after initial non-operative treatment
• Repeat imaging to ensure resolution
• Elective appendectomy vs conservative management (discuss with patient)

═══════════════════════════════════════════════════════════════════════════════
DISCHARGE INSTRUCTIONS
═══════════════════════════════════════════════════════════════════════════════

WOUND CARE:
• Keep incisions clean and dry
• May shower 24-48 hours after surgery
• No swimming or bathing for 2 weeks
• Steri-strips will fall off on their own

ACTIVITY:
• Light activity immediately
• No heavy lifting (>10 lbs) for 2-4 weeks
• Return to work: 1-2 weeks (desk job), 4-6 weeks (manual labor)
• No driving while taking opioid pain medications

DIET:
• Resume normal diet as tolerated
• Increase fiber and fluids to prevent constipation

RED FLAGS - RETURN IMMEDIATELY:
• Fever >38.5°C (101.3°F)
• Worsening abdominal pain
• Redness, swelling, or drainage from incision
• Persistent nausea/vomiting
• Inability to tolerate liquids
• Signs of infection

⚠️ Always follow the specific instructions provided by your surgical team, as individual recommendations may vary.`
    },

    redFlags: [
      '🔴 CRITICAL: Signs of perforation - generalized peritonitis, rigid abdomen, sepsis - requires emergency surgery',
      '🔴 CRITICAL: Septic shock (hypotension, tachycardia, altered mental status, fever) - aggressive resuscitation and urgent surgery',
      '🔴 CRITICAL: Free air on imaging - indicates hollow viscus perforation, requires emergency laparotomy',
      '🔴 CRITICAL: Rapidly worsening abdominal pain with guarding - suggests progression to perforation',
      '🟠 URGENT: Appendiceal abscess or phlegmon on imaging - may require drainage before surgery',
      '🟠 URGENT: Elderly patient with minimal symptoms but positive imaging - high risk for complications',
      '🟠 URGENT: Pediatric patient <5 years - high perforation rate, expedite management',
      '🟠 URGENT: Pregnant patient with RLQ/RUQ pain - appendicitis is most common surgical emergency in pregnancy',
      '🟠 URGENT: Immunocompromised patient (HIV, transplant, chemotherapy) - atypical presentations, higher complications',
      '🟠 URGENT: Failure to improve after 24-48 hours of antibiotics - reassess, may need surgery',
      '🟠 URGENT: Rising WBC and CRP despite treatment - suggests ongoing infection or complication'
    ],

    complications: [
      'PERFORATION: Occurs in 20-30% at presentation; higher in delayed diagnosis, children, elderly; leads to peritonitis or abscess',
      'PERITONITIS: Localized or generalized; from perforation; requires urgent surgery and broad-spectrum antibiotics',
      'APPENDICEAL ABSCESS: Walled-off collection of pus; may be treated with antibiotics and percutaneous drainage initially',
      'PERIAPPENDICEAL PHLEGMON: Inflammatory mass without drainable collection; often treated non-operatively initially',
      'WOUND INFECTION: 3-5% in laparoscopic, 10-15% in open surgery; higher with perforation; presents days 3-7 post-op',
      'INTRA-ABDOMINAL ABSCESS: Post-operative complication; fever, pain, elevated WBC; requires drainage',
      'ILEUS/BOWEL OBSTRUCTION: Temporary (ileus) or from adhesions; may delay discharge or require intervention',
      'STUMP APPENDICITIS: Inflammation of residual appendiceal stump; rare; requires completion appendectomy',
      'INCISIONAL HERNIA: More common after open surgery; presents as bulge at incision site',
      'SMALL BOWEL OBSTRUCTION: From adhesions; can occur months to years after surgery',
      'FECAL FISTULA: Rare; from stump leak or cecal injury; may heal spontaneously or require surgery',
      'PORTAL VEIN THROMBOSIS/PYLEPHLEBITIS: Rare but serious; septic thrombophlebitis of portal vein; requires anticoagulation',
      'MORTALITY: <1% uncomplicated; 5-15% for perforated (higher in elderly and delayed presentation)'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
UNCOMPLICATED APPENDICITIS
═══════════════════════════════════════════════════════════════════════════════

• MORTALITY: <0.1%
• MORBIDITY: 5-10% (mostly wound infections)
• HOSPITAL STAY: 1-2 days (laparoscopic); 2-4 days (open)
• RETURN TO WORK: 1-2 weeks (sedentary); 4-6 weeks (manual labor)
• RECURRENCE: None after appendectomy; ~25-30% after antibiotic-only treatment
• LONG-TERM OUTCOMES: Excellent; full recovery expected

═══════════════════════════════════════════════════════════════════════════════
COMPLICATED/PERFORATED APPENDICITIS
═══════════════════════════════════════════════════════════════════════════════

• MORTALITY: 1-5% (higher in elderly: 5-15%)
• MORBIDITY: 20-30% (wound infection, abscess, ileus)
• HOSPITAL STAY: 5-10 days
• RETURN TO WORK: 4-8 weeks
• LONG-TERM OUTCOMES: Generally good; slightly higher adhesion-related complications

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Young age
• Early presentation (<24 hours)
• Uncomplicated disease
• Laparoscopic surgery
• No co-morbidities

UNFAVORABLE:
• Age >60 years
• Delayed presentation (>48 hours)
• Perforation at presentation
• Generalized peritonitis
• Immunocompromised state
• Significant co-morbidities (diabetes, cardiac disease)
• Resource-limited setting

⚠️ Individual prognosis depends on multiple factors. Discuss specific prognosis with the treating surgical team.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• HIGH-FIBER DIET: Traditional theory links fiber intake to reduced appendicitis risk
• Limited evidence for specific preventive measures
• No vaccines or medications prevent appendicitis

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Complications)
═══════════════════════════════════════════════════════════════════════════════

EARLY RECOGNITION:
• Public health education on symptoms of appendicitis
• Awareness that abdominal pain + fever + vomiting requires medical evaluation
• Discourage delays in seeking medical care

HEALTHCARE SYSTEM:
• Accessible emergency services
• Timely surgical consultation
• Appropriate imaging availability
• Trained surgical workforce

APPROPRIATE MANAGEMENT:
• Timely diagnosis and surgery
• Appropriate antibiotic prophylaxis
• Evidence-based surgical techniques

═══════════════════════════════════════════════════════════════════════════════
AFRICAN CONTEXT - PREVENTION STRATEGIES
═══════════════════════════════════════════════════════════════════════════════

• Community education on abdominal pain warning signs
• Reduce barriers to healthcare access (financial, geographic)
• Train healthcare workers to recognize appendicitis
• Strengthen referral systems from primary care to surgical facilities
• Ensure antibiotic and surgical supplies availability
• Develop clinical scoring systems adapted to resource-limited settings
• Promote ultrasound training for diagnosis where CT unavailable`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY. Abdominal pain with fever can be serious and requires prompt medical evaluation.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS APPENDICITIS?
═══════════════════════════════════════════════════════════════════════════════

Appendicitis is inflammation of the appendix, a small finger-shaped pouch attached to your large intestine in the lower right side of your abdomen. When the appendix becomes blocked and inflamed, it can become infected and may burst (rupture) if not treated promptly.

Appendicitis is the most common reason for emergency abdominal surgery.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK MEDICAL CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Classic symptoms (you may not have all of these):

1. PAIN that starts around the belly button and moves to the lower right abdomen
2. LOSS OF APPETITE (not feeling hungry)
3. NAUSEA and VOMITING (usually after pain begins)
4. LOW FEVER

Other symptoms may include:
• Pain that gets worse with movement, coughing, or pressing on the area
• Inability to pass gas
• Constipation or diarrhea
• Abdominal bloating

⚠️ DO NOT WAIT - appendicitis can get worse quickly. A ruptured appendix is life-threatening.

═══════════════════════════════════════════════════════════════════════════════
WHAT TO EXPECT AT THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

EVALUATION:
• The doctor will examine your abdomen
• Blood tests will be done
• You may have an ultrasound or CT scan
• You will not be allowed to eat or drink (in case surgery is needed)

TREATMENT:
• Most cases require surgery to remove the appendix (appendectomy)
• Surgery is usually done through small incisions (laparoscopic) or one larger incision
• You will receive antibiotics
• You will receive pain medication

═══════════════════════════════════════════════════════════════════════════════
AFTER SURGERY - RECOVERY
═══════════════════════════════════════════════════════════════════════════════

HOSPITAL STAY:
• Simple cases: 1-2 days
• Complicated cases (ruptured appendix): 5-7 days or longer

AT HOME:
• Rest for the first few days
• Take pain medication as prescribed
• Gradually increase activity
• Do not lift heavy objects for 2-4 weeks
• You may shower, but avoid soaking the incision

DIET:
• Start with clear fluids, then light foods
• Return to normal diet as tolerated
• Eat plenty of fiber and drink fluids to prevent constipation

═══════════════════════════════════════════════════════════════════════════════
WHEN TO SEEK IMMEDIATE CARE AFTER SURGERY
═══════════════════════════════════════════════════════════════════════════════

Return to hospital or call your surgeon immediately if you experience:

🔴 Fever above 38.5°C (101.3°F)
🔴 Worsening abdominal pain
🔴 Redness, swelling, or pus from your incision
🔴 Vomiting that does not stop
🔴 Unable to eat or drink
🔴 No bowel movements for 3 or more days
🔴 Blood in your stool

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What type of surgery will I have?
• How long will I stay in the hospital?
• When can I return to work or school?
• What activities should I avoid?
• When should I come back for a follow-up visit?
• What signs of complications should I watch for?

═══════════════════════════════════════════════════════════════════════════════
KEY POINTS TO REMEMBER
═══════════════════════════════════════════════════════════════════════════════

• Appendicitis is a medical emergency
• Early treatment prevents serious complications
• Do not take laxatives or apply heat to your abdomen if you suspect appendicitis
• Surgery is safe and most people recover completely
• Life without your appendix is completely normal

Your healthcare team is there to help you. Do not hesitate to ask questions or express concerns.`,

    references: [
      'Di Saverio S, Podda M, De Simone B, et al. Diagnosis and treatment of acute appendicitis: 2020 update of the WSES Jerusalem guidelines. World J Emerg Surg. 2020;15(1):27. doi:10.1186/s13017-020-00306-3',
      'Bhangu A, Søreide K, Di Saverio S, et al. Acute appendicitis: modern understanding of pathogenesis, diagnosis, and management. Lancet. 2015;386(10000):1278-1287. doi:10.1016/S0140-6736(15)00275-5',
      'Alvarado A. A practical score for the early diagnosis of acute appendicitis. Ann Emerg Med. 1986;15(5):557-564. doi:10.1016/S0196-0644(86)80993-3',
      'Flum DR. Acute Appendicitis - Appendectomy or the "Antibiotics First" Strategy. N Engl J Med. 2015;372(20):1937-1943. doi:10.1056/NEJMcp1215006',
      'Andersson RE. Meta-analysis of the clinical and laboratory diagnosis of appendicitis. Br J Surg. 2004;91(1):28-37. doi:10.1002/bjs.4464',
      'CODA Collaborative. A Randomized Trial Comparing Antibiotics with Appendectomy for Appendicitis. N Engl J Med. 2020;383(20):1907-1919. doi:10.1056/NEJMoa2014320',
      'Salminen P, Paajanen H, Rautio T, et al. Antibiotic Therapy vs Appendectomy for Treatment of Uncomplicated Acute Appendicitis: The APPAC Randomized Clinical Trial. JAMA. 2015;313(23):2340-2348. doi:10.1001/jama.2015.6154',
      'Ohene-Yeboah M, Togbe B. An audit of appendicitis and appendicectomy in Kumasi, Ghana. West Afr J Med. 2006;25(2):138-143. [African context]',
      'Kong VY, Bulajic B, Allorto NL, et al. Acute appendicitis in a developing country. World J Surg. 2012;36(9):2068-2073. doi:10.1007/s00268-012-1626-9 [African context]',
      'Markar SR, Penna M, Harris A. Laparoscopic approach to appendectomy reduces the incidence of short- and long-term post-operative bowel obstruction. J Gastrointest Surg. 2014;18(9):1683-1692. doi:10.1007/s11605-014-2567-z'
    ],
    lastUpdated: '2026-01-17',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 24. INTESTINAL OBSTRUCTION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '24',
    name: 'Intestinal Obstruction',
    slug: 'intestinal-obstruction',
    specialty: 'Emergency Medicine',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K56.6',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Intestinal obstruction is a mechanical or functional blockage of the intestinal lumen preventing normal transit of intestinal contents. It is a common surgical emergency that can rapidly progress to bowel ischemia, necrosis, perforation, and death if not promptly diagnosed and treated. Early recognition and appropriate management are critical for optimal outcomes.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• CLASSIC TETRAD: Colicky abdominal pain, vomiting, distension, absolute constipation (obstipation)
• SMALL BOWEL OBSTRUCTION (SBO): Adhesions (#1 cause in developed countries), hernias (#1 in Africa), tumors
• LARGE BOWEL OBSTRUCTION (LBO): Colorectal cancer (#1), volvulus, diverticular stricture
• STRANGULATION: The most feared complication - bowel ischemia leading to necrosis and perforation
• SIGNS OF STRANGULATION: Constant (non-colicky) pain, fever, tachycardia, peritonitis, elevated lactate
• "NEVER LET THE SUN SET ON A BOWEL OBSTRUCTION" - Delayed surgery increases mortality
• CLOSED-LOOP OBSTRUCTION: Surgical emergency - high risk of rapid ischemia and perforation
• SIGMOID VOLVULUS: Common in Africa; often amenable to endoscopic decompression initially
• CECAL VOLVULUS: Usually requires surgery; endoscopic decompression rarely successful
• X-RAY FINDINGS: Dilated loops, air-fluid levels, "string of beads" sign
• CT SCAN: Gold standard - shows transition point, cause, and complications
• NGT DECOMPRESSION: Essential for proximal SBO; reduces vomiting and aspiration risk
• WATER-SOLUBLE CONTRAST: Both diagnostic (predicts resolution) AND therapeutic for adhesive SBO
• SURGERY INDICATED: Complete obstruction, strangulation, closed-loop, failure of conservative management
• MORTALITY: 3-5% for simple obstruction; 20-40% for strangulated bowel

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

CLASSIFICATION:
1. BY MECHANISM:
   • Mechanical: Physical blockage of lumen (adhesions, tumor, hernia, volvulus)
   • Functional (Ileus): Impaired motility without mechanical obstruction

2. BY LOCATION:
   • Small bowel obstruction (SBO): More common; presents earlier with vomiting
   • Large bowel obstruction (LBO): Less common; presents with distension

3. BY DEGREE:
   • Complete: No passage of gas or stool
   • Partial: Some passage continues

4. BY BLOOD SUPPLY:
   • Simple: Blood supply intact
   • Strangulated: Compromised blood supply - SURGICAL EMERGENCY

SEQUENCE OF EVENTS IN MECHANICAL OBSTRUCTION:
1. Luminal obstruction → proximal bowel dilation
2. Accumulation of gas (swallowed air, bacterial fermentation) and fluid (GI secretions)
3. Increased intraluminal pressure → impaired venous return
4. Bowel wall edema → further fluid sequestration ("third spacing")
5. Bacterial overgrowth and translocation
6. If pressure exceeds arterial pressure → ischemia → necrosis → perforation

FLUID AND ELECTROLYTE LOSSES:
• GI tract secretes 6-8 L/day normally
• Obstruction causes massive third-spacing
• Vomiting leads to loss of H+, Cl-, K+ → hypochloremic hypokalemic metabolic alkalosis
• Dehydration, hypovolemia, shock can develop rapidly`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Accounts for 15-20% of surgical admissions for acute abdomen
• Small bowel obstruction: ~80% of all intestinal obstructions
• Large bowel obstruction: ~20% of all intestinal obstructions
• Most common cause of SBO worldwide: Adhesions (60-70% in developed countries)
• Most common cause of LBO: Colorectal carcinoma (60%)
• Overall mortality: 3-5% (simple); 20-40% (strangulated)
• Incidence increases with age
• Previous abdominal surgery is the strongest risk factor for adhesive SBO

CAUSES BY FREQUENCY:

SMALL BOWEL OBSTRUCTION (Developed Countries):
1. Adhesions: 60-70% (post-surgical)
2. Hernias: 10-15%
3. Crohn's disease: 5%
4. Malignancy: 5%
5. Other: Intussusception, volvulus, gallstone ileus, foreign body

LARGE BOWEL OBSTRUCTION (All Settings):
1. Colorectal carcinoma: 60%
2. Volvulus: 10-15% (higher in Africa/Asia)
3. Diverticular disease: 10%
4. Fecal impaction: 5%
5. Other: Hernias, inflammatory stricture

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HERNIAS: #1 cause of SBO in Africa (vs adhesions in developed countries)
  - Inguinal hernia most common
  - Often present late with strangulation
  - High mortality due to delayed presentation

• SIGMOID VOLVULUS: Disproportionately common in Africa
  - "Volvulus belt" - East Africa, Ethiopia, parts of West Africa
  - Younger age of onset (30-50 years vs 60-70 in West)
  - Related to high-fiber diet, megacolon, chronic constipation
  - Can often be managed with endoscopic decompression initially

• INTUSSUSCEPTION: More common cause of obstruction in children
  - Peak age: 3 months to 3 years
  - Classic triad: Colicky pain, "red currant jelly" stool, sausage-shaped mass

• ASCARIS LUMBRICOIDES: Parasitic cause of SBO
  - Bolus of worms can obstruct small bowel
  - Common in areas with poor sanitation
  - Treatment: Conservative management often successful; anthelmintics

• TUBERCULOSIS: Can cause strictures and obstruction
  - Usually ileocecal region
  - May present with subacute obstruction

• LATE PRESENTATION: Common due to:
  - Limited healthcare access
  - Financial barriers
  - Traditional medicine use first
  - Results in higher rates of strangulation and perforation

• DIAGNOSTIC CHALLENGES:
  - CT often unavailable
  - Reliance on clinical examination and plain X-rays
  - Ultrasound increasingly used for diagnosis

• SURGICAL CHALLENGES:
  - Limited operative resources
  - Blood products may be scarce
  - ICU care often limited
  - Higher mortality rates than developed countries`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CARDINAL SYMPTOMS - "CLASSIC TETRAD"
═══════════════════════════════════════════════════════════════════════════════

1. ABDOMINAL PAIN
   • Colicky (cramping, intermittent) - classic for simple obstruction
   • Correlates with peristaltic waves against obstruction
   • Constant, severe pain suggests strangulation
   • Location varies with site of obstruction

2. VOMITING
   • Earlier and more profuse in proximal obstruction
   • Initially gastric contents → bilious → feculent (late)
   • Feculent vomiting = distal SBO or prolonged obstruction
   • May be absent in distal LBO

3. ABDOMINAL DISTENSION
   • More pronounced in distal obstruction
   • Minimal in high/proximal SBO
   • Marked in LBO and distal SBO

4. CONSTIPATION/OBSTIPATION
   • Complete (absolute) = no gas or stool
   • Obstipation = failure to pass flatus AND stool
   • May have initial diarrhea (clearing of distal bowel)

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS BY LOCATION
═══════════════════════════════════════════════════════════════════════════════

PROXIMAL (HIGH) SMALL BOWEL:
• Early, profuse vomiting (bilious)
• Minimal distension
• Rapid dehydration and electrolyte imbalance
• Pain in upper/mid abdomen

DISTAL SMALL BOWEL:
• Delayed vomiting (may be feculent)
• Moderate distension
• More gradual dehydration
• Periumbilical/lower abdominal pain

LARGE BOWEL:
• Late or absent vomiting
• Marked distension
• Constipation predominant
• Lower abdominal/diffuse pain

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION FINDINGS
═══════════════════════════════════════════════════════════════════════════════

GENERAL APPEARANCE:
• Mild cases: Uncomfortable but stable
• Severe/strangulated: Ill-appearing, restless, diaphoretic

VITAL SIGNS:
• Tachycardia (dehydration, pain, sepsis)
• Hypotension (severe dehydration, sepsis)
• Fever (late sign; suggests strangulation, perforation)
• Tachypnea (pain, abdominal distension limiting diaphragm)

ABDOMINAL INSPECTION:
• Distension (generalized or localized)
• Visible peristalsis (ladder pattern - thin patients)
• Surgical scars (adhesions)
• Hernia sites (inguinal, femoral, umbilical, incisional)

ABDOMINAL AUSCULTATION:
• High-pitched, "tinkling" bowel sounds = mechanical obstruction
• Rushes and borborygmi (loud gurgles)
• Absent bowel sounds = late obstruction, ileus, or peritonitis

ABDOMINAL PALPATION:
• Tenderness - localized or diffuse
• Guarding - voluntary vs involuntary (peritonitis)
• Rebound tenderness - suggests peritoneal irritation
• Palpable mass - tumor, intussusception, closed-loop
• Hernia examination - ALWAYS examine all hernia orifices!

ABDOMINAL PERCUSSION:
• Tympany - dilated gas-filled bowel
• Dullness - fluid, mass, or ascites

DIGITAL RECTAL EXAMINATION:
• Empty rectum in complete obstruction
• Rectal mass (tumor)
• Blood (tumor, ischemia, intussusception)
• Fecal impaction

═══════════════════════════════════════════════════════════════════════════════
SIGNS OF STRANGULATION - SURGICAL EMERGENCY
═══════════════════════════════════════════════════════════════════════════════

🔴 Constant (non-colicky) severe pain
🔴 Fever > 38°C
🔴 Tachycardia > 100/min
🔴 Localized tenderness with guarding
🔴 Rebound tenderness / peritonitis
🔴 Absent bowel sounds (late)
🔴 Hemodynamic instability / shock
🔴 Elevated WBC, lactate, acidosis

⚠️ Clinical signs have LIMITED sensitivity for strangulation
⚠️ Absence of these signs does NOT exclude strangulation
⚠️ When in doubt, operate early`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

KEY DIAGNOSTIC QUESTIONS:
1. Is there obstruction? (vs ileus)
2. Where is the obstruction? (small vs large bowel)
3. What is the cause? (adhesions, hernia, tumor, volvulus)
4. Is it complete or partial?
5. Is there strangulation? (MOST IMPORTANT)

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

COMPLETE BLOOD COUNT:
• WBC: Elevated (leukocytosis) suggests inflammation/strangulation
• Hematocrit: May be elevated (hemoconcentration from dehydration)
• Normal WBC does NOT exclude strangulation

BASIC METABOLIC PANEL:
• Hypokalemia (vomiting)
• Hypochloremia (gastric losses)
• Metabolic alkalosis (early - from vomiting)
• Metabolic acidosis (late - from ischemia/sepsis)
• Elevated BUN/creatinine (prerenal azotemia from dehydration)
• Elevated glucose (stress response)

LACTATE:
• Elevated lactate suggests tissue ischemia
• >2 mmol/L concerning; >4 mmol/L strongly suggests strangulation
• Normal lactate does NOT exclude early strangulation

LIVER FUNCTION TESTS:
• Usually normal unless gallstone ileus or hepatic metastases

AMYLASE/LIPASE:
• May be mildly elevated in obstruction
• If very elevated, consider pancreatitis as cause

BLOOD GAS:
• Metabolic alkalosis (early) or acidosis (late strangulation)
• Helps assess severity

═══════════════════════════════════════════════════════════════════════════════
IMAGING FINDINGS
═══════════════════════════════════════════════════════════════════════════════

PLAIN ABDOMINAL X-RAY (Supine and Erect):
Sensitivity: 60-70% for SBO; 80% for LBO

SBO FINDINGS:
• Dilated small bowel loops (>3 cm diameter)
• Multiple air-fluid levels at different heights (erect film)
• "String of beads" sign - small bubbles of trapped gas
• "Step-ladder" pattern of dilated loops
• Paucity of colonic gas
• Valvulae conniventes visible (cross entire lumen)

LBO FINDINGS:
• Dilated colon (>6 cm; cecum >9 cm = impending perforation)
• Haustra visible (do not cross entire lumen)
• May have small bowel dilation if incompetent ileocecal valve
• "Coffee bean" sign in sigmoid volvulus
• "Bird's beak" sign at site of volvulus

CT ABDOMEN/PELVIS WITH IV CONTRAST (Gold Standard):
Sensitivity: 90-95% for obstruction and cause
Specificity: 90-96%

CT FINDINGS:
• Transition point - dilated proximal, decompressed distal bowel
• Cause identification (adhesions, hernia, tumor, volvulus)
• "Small bowel feces sign" - particulate matter in dilated SB
• Mesenteric haziness/stranding
• STRANGULATION SIGNS:
  - Bowel wall thickening
  - Decreased/absent wall enhancement
  - Mesenteric edema and haziness
  - Free fluid
  - Pneumatosis intestinalis (air in bowel wall)
  - Portal venous gas (LATE - very poor prognosis)
  - Closed-loop configuration

WATER-SOLUBLE CONTRAST STUDY (Gastrografin):
• Diagnostic AND therapeutic for adhesive SBO
• Contrast reaching colon within 24 hours = likely to resolve conservatively
• May speed resolution of partial adhesive SBO
• CONTRAINDICATED in complete obstruction or suspected perforation

ULTRASOUND:
• Useful when CT unavailable
• Can show dilated fluid-filled loops
• "Keyboard sign" - valvulae conniventes
• To-and-fro peristalsis
• Free fluid
• Can identify hernias

═══════════════════════════════════════════════════════════════════════════════
DIFFERENTIATION: SBO vs LBO
═══════════════════════════════════════════════════════════════════════════════

                        SBO                     LBO
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Vomiting              Early, profuse          Late or absent
Distension            Moderate                Marked
Pain                  Periumbilical           Lower abdominal
Constipation          Later                   Early, absolute
X-ray loops           Central, valvulae       Peripheral, haustra
Loop diameter         >3 cm                   >6 cm (colon)
Common cause          Adhesions, hernia       Cancer, volvulus`,

    differentialDiagnosis: [
      'Paralytic Ileus - Post-operative, medications (opioids), electrolyte disturbances, peritonitis; absent bowel sounds; no transition point on CT; treat underlying cause',
      'Acute Mesenteric Ischemia - Severe pain out of proportion to exam; atrial fibrillation risk factor; CT shows lack of bowel enhancement; high mortality',
      'Ogilvie Syndrome (Colonic Pseudo-obstruction) - Massive colonic dilation without mechanical cause; typically post-operative or critically ill; treat with neostigmine',
      'Acute Appendicitis - RLQ pain, anorexia, fever; may have ileus or localized obstruction; CT shows inflamed appendix',
      'Acute Pancreatitis - Epigastric pain radiating to back; elevated lipase; localized ileus common; CT shows pancreatic inflammation',
      'Acute Cholecystitis - RUQ pain, Murphy sign, fever; may have ileus; ultrasound shows gallbladder inflammation',
      'Gastroenteritis - Diarrhea, vomiting, diffuse cramping; no obstipation; usually self-limited',
      'Diverticulitis - LLQ pain, fever, altered bowel habits; CT shows colonic wall thickening, pericolic fat stranding',
      'Inflammatory Bowel Disease - Chronic symptoms, bloody diarrhea; may develop strictures causing obstruction',
      'Abdominal Aortic Aneurysm - Back/abdominal pain; pulsatile mass; CT shows aneurysm; may have ileus',
      'Incarcerated Hernia - Painful, irreducible groin/abdominal wall mass; examine all hernia orifices; emergency surgery if strangulated',
      'Intussusception (Pediatric) - Colicky pain, red currant jelly stool, sausage mass; ultrasound shows "target sign"; may reduce with air enema',
      'Gallstone Ileus - SBO in elderly with gallstones; air in biliary tree (pneumobilia); ectopic gallstone on imaging',
      'Fecal Impaction - Elderly, bedridden, chronic constipation; palpable on rectal exam; disimpaction usually curative'
    ],

    investigations: [
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - Leukocytosis suggests inflammation/strangulation; hemoconcentration indicates dehydration',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Electrolytes, BUN, Creatinine)',
        indication: '🔴 IMMEDIATE - Assess dehydration, electrolyte imbalances (hypokalemia, hypochloremia); guide resuscitation',
        availability: 'BASIC'
      },
      {
        name: 'Serum Lactate',
        indication: '🔴 IMMEDIATE - Elevated lactate (>2 mmol/L) suggests tissue ischemia/strangulation; serial monitoring useful',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 IMMEDIATE - Assess acid-base status; metabolic acidosis suggests ischemia; guides resuscitation',
        availability: 'BASIC'
      },
      {
        name: 'Serum Amylase/Lipase',
        indication: '🟠 URGENT - Rule out pancreatitis as cause of ileus; may be mildly elevated in obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 URGENT - Baseline; elevated if gallstone ileus or hepatic involvement',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🔴 IMMEDIATE - Pre-operative preparation if surgery anticipated',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🟠 URGENT - Rule out urinary cause of abdominal pain; assess hydration status',
        availability: 'BASIC'
      },
      {
        name: 'Plain Abdominal X-ray (Supine and Erect)',
        indication: '🔴 IMMEDIATE - First-line imaging; shows dilated loops, air-fluid levels, free air (perforation)',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray (Erect)',
        indication: '🔴 IMMEDIATE - Rule out free air under diaphragm (perforation); baseline pre-operative',
        availability: 'BASIC'
      },
      {
        name: 'CT Abdomen/Pelvis with IV Contrast',
        indication: '🔴 IMMEDIATE - Gold standard; identifies transition point, cause, and complications (strangulation)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🟠 URGENT - When CT unavailable; shows dilated loops, free fluid, hernias; good for intussusception',
        availability: 'BASIC'
      },
      {
        name: 'Water-Soluble Contrast Study (Gastrografin)',
        indication: '🟡 Consider - For adhesive SBO; diagnostic (predicts resolution) and therapeutic; do NOT use if perforation suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Sigmoidoscopy/Colonoscopy',
        indication: '🟡 Consider - For sigmoid volvulus (therapeutic decompression); to evaluate LBO cause; AVOID if perforation suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Procalcitonin',
        indication: '🟠 URGENT if available - May help identify strangulation/sepsis; elevated levels concerning',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Angiography',
        indication: '🟠 URGENT - If mesenteric ischemia suspected (pain out of proportion, AF history)',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ NPO (Nil Per Os) - Absolute bowel rest; no oral intake',
        '⚡ IV ACCESS - Large-bore (18G or larger) for aggressive fluid resuscitation',
        '⚡ AGGRESSIVE FLUID RESUSCITATION - Crystalloids (NS or LR); may need 4-6 L or more; target UOP >0.5 mL/kg/hr',
        '⚡ NASOGASTRIC TUBE - Decompression is ESSENTIAL; relieves vomiting, reduces aspiration risk, reduces distension',
        '⚡ URINARY CATHETER - Monitor urine output; guide resuscitation; assess for urinary retention',
        '⚡ ELECTROLYTE CORRECTION - Replace potassium, monitor and correct other electrolytes',
        '⚡ ANALGESIA - IV opioids for pain control; does NOT mask surgical findings',
        '⚡ ANTIEMETICS - Ondansetron for nausea',
        '⚡ SURGICAL CONSULTATION - Early surgical involvement for all cases of mechanical obstruction',
        '⚡ LABORATORY TESTS - CBC, BMP, lactate, type and screen',
        '⚡ IMAGING - Abdominal X-ray (stat); CT with contrast when stable',
        '⚡ ANTIBIOTICS - If strangulation suspected or surgery planned (cover gram-negatives and anaerobes)',
        '⚡ DVT PROPHYLAXIS - Mechanical initially; chemical when safe',
        '⚡ HERNIA EXAMINATION - Examine ALL hernia orifices (inguinal, femoral, umbilical, incisional)',
        '⚡ SERIAL EXAMINATIONS - Frequent reassessment for signs of strangulation'
      ],
      pharmacological: [
        {
          drug: 'Normal Saline (0.9% NaCl)',
          indication: '🔴 FIRST-LINE RESUSCITATION - Correct hypovolemia from third-spacing and vomiting',
          dose: '1-2 L bolus initially, then maintenance; may need 4-6 L or more in severe cases',
          route: 'IV',
          duration: 'Until euvolemic; guide by urine output, vital signs, lactate',
          contraindications: ['Fluid overload', 'Severe hypernatremia'],
          sideEffects: ['Hyperchloremic acidosis with large volumes', 'Fluid overload'],
          alternatives: ['Lactated Ringer (may be preferred to avoid hyperchloremic acidosis)']
        },
        {
          drug: 'Lactated Ringer Solution',
          indication: '🔴 ALTERNATIVE RESUSCITATION - Balanced crystalloid; may be preferred over NS for large-volume resuscitation',
          dose: '1-2 L bolus initially, then titrate to urine output and vital signs',
          route: 'IV',
          duration: 'Until euvolemic',
          contraindications: ['Severe liver failure', 'Hyperkalemia (contains K+)'],
          sideEffects: ['Fluid overload', 'Dilutional coagulopathy with massive resuscitation'],
          alternatives: ['Normal saline', 'Plasmalyte']
        },
        {
          drug: 'Potassium Chloride (KCl)',
          indication: '🔴 ELECTROLYTE REPLACEMENT - Correct hypokalemia from vomiting',
          dose: '10-20 mEq/hour IV (max 40 mEq/hour via central line); adjust based on serum K+',
          route: 'IV (never IV push)',
          duration: 'Until potassium normalized',
          contraindications: ['Hyperkalemia', 'Severe renal failure', 'Addison disease'],
          sideEffects: ['Hyperkalemia', 'Cardiac arrhythmias if infused too rapidly', 'Phlebitis'],
          alternatives: ['Oral potassium if able to take PO']
        },
        {
          drug: 'Morphine Sulfate',
          indication: '🔴 ANALGESIA - Pain control; does NOT mask surgical findings or delay diagnosis',
          dose: '2-4 mg IV every 2-4 hours PRN; titrate to effect',
          route: 'IV',
          duration: 'As needed for pain control',
          contraindications: ['Respiratory depression', 'Hypotension'],
          sideEffects: ['Nausea', 'Sedation', 'Respiratory depression', 'Constipation (less relevant in obstruction)'],
          alternatives: ['Fentanyl (shorter acting)', 'Hydromorphone']
        },
        {
          drug: 'Ondansetron',
          indication: '🔴 ANTIEMETIC - Control nausea/vomiting',
          dose: '4-8 mg IV every 6-8 hours',
          route: 'IV',
          duration: 'As needed',
          contraindications: ['QT prolongation', 'Hypersensitivity'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation'],
          alternatives: ['Metoclopramide (AVOID in complete obstruction)', 'Promethazine']
        },
        {
          drug: 'Ceftriaxone + Metronidazole',
          indication: '🔴 ANTIBIOTIC PROPHYLAXIS/TREATMENT - Cover gram-negatives and anaerobes; give if strangulation suspected or surgery planned',
          dose: 'Ceftriaxone 1-2 g IV daily + Metronidazole 500 mg IV every 8 hours',
          route: 'IV',
          duration: 'Prophylaxis: Single pre-op dose; Treatment: 5-7 days if strangulation/perforation',
          contraindications: ['Cephalosporin allergy', 'Metronidazole: Disulfiram reaction with alcohol'],
          sideEffects: ['Diarrhea', 'C. difficile', 'Allergic reaction'],
          alternatives: ['Piperacillin-tazobactam', 'Ampicillin-sulbactam', 'Ciprofloxacin + Metronidazole']
        },
        {
          drug: 'Piperacillin-Tazobactam',
          indication: '🔴 ALTERNATIVE BROAD-SPECTRUM - Single agent with gram-negative and anaerobic coverage',
          dose: '3.375-4.5 g IV every 6-8 hours',
          route: 'IV',
          duration: 'Based on clinical course',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Thrombocytopenia'],
          alternatives: ['Ceftriaxone + Metronidazole', 'Meropenem']
        },
        {
          drug: 'Gastrografin (Diatrizoate)',
          indication: '🟠 DIAGNOSTIC/THERAPEUTIC - Water-soluble contrast for adhesive SBO; predicts resolution; may speed resolution',
          dose: '100 mL via NGT; follow with abdominal X-ray at 8 and 24 hours',
          route: 'Via nasogastric tube',
          duration: 'Single administration',
          contraindications: ['Complete obstruction', 'Suspected perforation', 'Aspiration risk'],
          sideEffects: ['Aspiration pneumonitis if aspirated', 'Diarrhea (osmotic)'],
          alternatives: ['CT with oral contrast (less therapeutic effect)']
        },
        {
          drug: 'Neostigmine',
          indication: '🟠 FOR OGILVIE SYNDROME (Colonic pseudo-obstruction) - NOT for mechanical obstruction',
          dose: '2 mg IV over 3-5 minutes; may repeat once after 3 hours',
          route: 'IV (with cardiac monitoring)',
          duration: 'Single dose(s)',
          contraindications: ['Mechanical obstruction', 'Bradycardia', 'Recent MI', 'Asthma'],
          sideEffects: ['Bradycardia', 'Bronchospasm', 'Abdominal cramping', 'Hypotension'],
          alternatives: ['Colonoscopic decompression']
        },
        {
          drug: 'Enoxaparin',
          indication: '🟠 DVT PROPHYLAXIS - Start when bleeding risk acceptable',
          dose: '40 mg SC daily',
          route: 'Subcutaneous',
          duration: 'Until ambulatory',
          contraindications: ['Active bleeding', 'Severe thrombocytopenia', 'Recent surgery (relative)'],
          sideEffects: ['Bleeding', 'Hematoma at injection site'],
          alternatives: ['Heparin 5000 units SC q8-12h', 'Mechanical prophylaxis (SCDs)']
        },
        {
          drug: 'Albendazole',
          indication: '🟠 FOR ASCARIS OBSTRUCTION - If parasitic cause suspected/confirmed',
          dose: '400 mg PO once daily for 3 days',
          route: 'PO (after obstruction resolves)',
          duration: '3 days',
          contraindications: ['Pregnancy (first trimester)', 'Hypersensitivity'],
          sideEffects: ['Abdominal pain', 'Nausea', 'Elevated LFTs'],
          alternatives: ['Mebendazole', 'Pyrantel pamoate']
        }
      ],
      nonPharmacological: [
        'NPO STATUS: Absolute bowel rest until resolution or surgery',
        'NASOGASTRIC DECOMPRESSION: Low intermittent suction; essential for symptomatic relief and reducing aspiration risk',
        'IV FLUID RESUSCITATION: Aggressive replacement; patients may be severely depleted',
        'ELECTROLYTE MONITORING: Serial checks; replace as needed',
        'SERIAL ABDOMINAL EXAMS: Every 4-6 hours; assess for progression to strangulation',
        'STRICT INPUT/OUTPUT MONITORING: NGT output, urine output, IV input',
        'POSITION: Semi-recumbent (30-45°) to reduce aspiration risk',
        'HERNIA REDUCTION: If incarcerated hernia identified and viable (no strangulation), attempt gentle reduction',
        'RECTAL TUBE: May help decompress distal LBO',
        'ENDOSCOPIC DECOMPRESSION: For sigmoid volvulus - often successful; less effective for cecal volvulus'
      ],
      surgical: [
        'EMERGENCY SURGERY INDICATIONS: Strangulation, perforation, closed-loop obstruction, complete obstruction, failure of conservative management (48-72 hours)',
        'ADHESIOLYSIS: Lysis of adhesions for adhesive SBO; can be open or laparoscopic',
        'HERNIA REPAIR: Reduce and repair; may require bowel resection if strangulated',
        'BOWEL RESECTION: If ischemic/necrotic bowel; primary anastomosis vs stoma depends on contamination and patient stability',
        'HARTMANN PROCEDURE: For obstructing left colon cancer with perforation/instability; sigmoid resection with end colostomy',
        'COLONIC STENTING: For malignant LBO; bridge to surgery or palliation; avoid if perforation suspected',
        'SIGMOID VOLVULUS SURGERY: Sigmoid resection after failed/recurrent endoscopic decompression',
        'CECAL VOLVULUS SURGERY: Right hemicolectomy (endoscopic decompression usually fails)',
        'ENTEROTOMY: For gallstone ileus - remove impacted gallstone; cholecystectomy may be deferred',
        'INTUSSUSCEPTION REDUCTION: Manual reduction at surgery if air enema fails; resection if not reducible or necrotic',
        'STRICTUREPLASTY: For Crohn-related strictures to preserve bowel length',
        'SECOND-LOOK LAPAROTOMY: Planned re-exploration in 24-48 hours if bowel viability questionable'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE SURGICAL CONSULTATION: All cases of suspected mechanical intestinal obstruction',
        '🔴 EMERGENCY SURGERY: Signs of strangulation, perforation, closed-loop obstruction, hemodynamic instability',
        '🔴 URGENT SURGERY: Complete obstruction not responding to conservative management within 24-48 hours',
        '🟠 GASTROENTEROLOGY: For endoscopic decompression of sigmoid volvulus; colonoscopic stenting for malignant LBO',
        '🟠 INTERVENTIONAL RADIOLOGY: For percutaneous drainage if abscess; colonic stent placement',
        '🟠 ONCOLOGY: If malignancy identified as cause',
        '🟠 ICU ADMISSION: Hemodynamic instability, sepsis, post-operative critical care needs'
      ],
      monitoring: [
        'VITAL SIGNS: Every 2-4 hours; more frequent if unstable',
        'URINE OUTPUT: Hourly initially; target >0.5 mL/kg/hr',
        'ABDOMINAL EXAMINATION: Every 4-6 hours; assess for signs of strangulation',
        'NGT OUTPUT: Document volume and character every shift',
        'FLUID BALANCE: Strict I&O; daily weights if possible',
        'LABORATORY: Daily CBC, BMP, lactate until stable; more frequent if concerning',
        'IMAGING: Repeat X-ray if clinical deterioration; repeat CT if concern for strangulation',
        'PAIN ASSESSMENT: Regular assessment; increasing pain may indicate strangulation'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
CONSERVATIVE MANAGEMENT COURSE (Adhesive SBO)
═══════════════════════════════════════════════════════════════════════════════

INITIAL 24-48 HOURS:
• NPO, NGT decompression, IV fluids, electrolyte replacement
• Serial abdominal exams every 4-6 hours
• If Gastrografin used, X-ray at 8 and 24 hours
• Contrast reaching colon = likely to resolve

48-72 HOURS:
• If resolving: Clamp NGT, advance diet cautiously
• If not resolving: Surgery indicated

CRITERIA FOR RESOLUTION:
• Decreased NGT output
• Passage of flatus/stool
• Resolution of pain and distension
• Tolerating oral intake
• Improved X-ray findings

═══════════════════════════════════════════════════════════════════════════════
POST-OPERATIVE CARE
═══════════════════════════════════════════════════════════════════════════════

IMMEDIATE POST-OP:
• ICU if hemodynamically unstable or extensive surgery
• Continue NGT until return of bowel function
• IV fluids and electrolyte replacement
• Pain management (consider PCA, epidural)
• DVT prophylaxis

DIET ADVANCEMENT:
• Wait for return of bowel function (flatus, bowel sounds)
• Start with clear liquids
• Advance as tolerated
• May take 3-7 days after major surgery

WOUND CARE:
• Monitor for infection, dehiscence
• Stoma care teaching if applicable

HOSPITAL STAY:
• Simple adhesiolysis: 3-5 days
• Bowel resection: 5-10 days
• Complicated (perforation, sepsis): 2+ weeks

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

1-2 WEEKS POST-DISCHARGE:
• Wound check
• Ensure tolerating diet
• Stoma teaching reinforcement if applicable

4-6 WEEKS:
• Full recovery assessment
• Pathology results review (if resection)
• Discuss prevention of recurrence

LONG-TERM:
• Adhesive SBO: 10-30% recurrence risk; discuss warning signs
• Hernia repair: Monitor for recurrence
• Malignancy: Oncology follow-up, surveillance

⚠️ Individual follow-up schedules should be determined by the treating surgeon based on the specific case.`
    },

    redFlags: [
      '🔴 CRITICAL: Signs of strangulation - constant severe pain, fever, tachycardia, peritonitis, elevated lactate - EMERGENCY SURGERY',
      '🔴 CRITICAL: Perforation - free air on imaging, peritonitis, septic shock - EMERGENCY LAPAROTOMY',
      '🔴 CRITICAL: Closed-loop obstruction on CT - high risk of rapid ischemia - URGENT SURGERY',
      '🔴 CRITICAL: Hemodynamic instability despite resuscitation - suggests advanced strangulation/sepsis',
      '🔴 CRITICAL: Pneumatosis intestinalis or portal venous gas - indicates bowel necrosis - VERY POOR PROGNOSIS',
      '🟠 URGENT: Complete obstruction (no gas in colon/rectum) - less likely to resolve conservatively',
      '🟠 URGENT: Incarcerated/irreducible hernia - may progress to strangulation',
      '🟠 URGENT: Failure to improve with 48-72 hours of conservative management',
      '🟠 URGENT: Cecal diameter >12 cm on imaging - risk of perforation',
      '🟠 URGENT: Rising WBC and lactate despite treatment',
      '🟠 URGENT: New-onset fever or increasing pain during conservative management'
    ],

    complications: [
      'STRANGULATION: Compromised blood supply leading to ischemia - occurs in 10-15% of SBO; requires emergency surgery',
      'BOWEL NECROSIS: Progression of ischemia; requires resection of non-viable bowel',
      'PERFORATION: Full-thickness bowel wall necrosis with spillage; causes fecal peritonitis; high mortality',
      'PERITONITIS: Localized or generalized; from perforation or bacterial translocation',
      'SEPSIS/SEPTIC SHOCK: From bacterial translocation and/or perforation; high mortality',
      'ASPIRATION PNEUMONIA: From vomiting; prevented by early NGT decompression',
      'ELECTROLYTE IMBALANCES: Hypokalemia, hypochloremia, metabolic alkalosis/acidosis',
      'DEHYDRATION/HYPOVOLEMIC SHOCK: Massive third-spacing of fluids',
      'ACUTE KIDNEY INJURY: From hypovolemia; usually prerenal',
      'SHORT BOWEL SYNDROME: After extensive resection; malabsorption, dependence on TPN',
      'RECURRENCE: Adhesive SBO recurs in 10-30% of patients; higher with each episode',
      'POST-OPERATIVE COMPLICATIONS: Anastomotic leak, wound infection, ileus, DVT/PE',
      'ENTEROCUTANEOUS FISTULA: Abnormal connection between bowel and skin; may follow surgery or perforation'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

SIMPLE (NON-STRANGULATED) OBSTRUCTION:
• Mortality: 3-5%
• Most adhesive SBO resolves with conservative management (60-80%)
• Good long-term outcomes if treated promptly

STRANGULATED OBSTRUCTION:
• Mortality: 20-40% (higher with delayed diagnosis)
• Early surgery (<24 hours of strangulation) improves survival
• May require bowel resection

LARGE BOWEL OBSTRUCTION:
• Mortality depends on cause and presence of perforation
• Malignant LBO: Prognosis related to cancer stage
• Volvulus: Good if decompressed early; worse if necrotic

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Early presentation
• Partial obstruction
• No signs of strangulation
• Adhesive etiology (often resolves conservatively)
• Young, healthy patient
• Prompt surgical intervention when indicated

UNFAVORABLE:
• Delayed presentation (>24-48 hours)
• Complete obstruction
• Signs of strangulation
• Elderly patient or significant comorbidities
• Malignant cause
• Perforation
• Sepsis/shock at presentation

RECURRENCE:
• Adhesive SBO: 10-30% lifetime recurrence after first episode
• Risk increases with each episode
• Hernias: Recurrence depends on repair type and technique

⚠️ Individual prognosis depends on multiple factors. Discuss specific prognosis with the treating surgical team.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PREVENTION OF ADHESIVE OBSTRUCTION
═══════════════════════════════════════════════════════════════════════════════

SURGICAL TECHNIQUES:
• Laparoscopic surgery when feasible (fewer adhesions than open)
• Gentle tissue handling
• Meticulous hemostasis
• Minimize peritoneal trauma
• Avoid unnecessary peritoneal dissection
• Keep bowel moist during surgery

ADHESION BARRIERS:
• Hyaluronic acid/carboxymethylcellulose (Seprafilm)
• May reduce adhesion formation
• Evidence mixed; not universally adopted

EARLY MOBILIZATION:
• Post-operative ambulation may reduce ileus and adhesions

═══════════════════════════════════════════════════════════════════════════════
PREVENTION OF HERNIA-RELATED OBSTRUCTION
═══════════════════════════════════════════════════════════════════════════════

• ELECTIVE HERNIA REPAIR: Before incarceration occurs
• Patient education on hernia warning signs
• Avoid heavy lifting with known hernia
• Weight optimization before surgery

═══════════════════════════════════════════════════════════════════════════════
PREVENTION OF VOLVULUS
═══════════════════════════════════════════════════════════════════════════════

• High-fiber diet (controversial - may contribute in some populations)
• Adequate hydration
• Treat chronic constipation
• Sigmoid resection after recurrent volvulus

═══════════════════════════════════════════════════════════════════════════════
COLORECTAL CANCER SCREENING
═══════════════════════════════════════════════════════════════════════════════

• Colonoscopy starting at age 45-50 (or earlier if risk factors)
• Detects and removes precancerous polyps
• Identifies early cancers before obstruction develops

═══════════════════════════════════════════════════════════════════════════════
PATIENT EDUCATION
═══════════════════════════════════════════════════════════════════════════════

Patients with history of obstruction should:
• Know warning signs (pain, vomiting, distension, constipation)
• Seek early medical attention if symptoms develop
• Avoid known precipitants
• Maintain adequate hydration`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS INTESTINAL OBSTRUCTION?
═══════════════════════════════════════════════════════════════════════════════

Intestinal obstruction (bowel obstruction) occurs when something blocks your intestines, preventing food, fluids, and gas from passing through normally. This can cause severe pain, vomiting, and swelling of the abdomen. It is a serious condition that requires prompt medical attention.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK EMERGENCY CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Go to the hospital immediately if you experience:

🔴 Severe cramping abdominal pain that comes and goes
🔴 Vomiting that won't stop (especially if greenish or foul-smelling)
🔴 Swollen, bloated abdomen
🔴 Inability to pass gas or have a bowel movement
🔴 Severe constipation
🔴 A painful bulge in your groin or abdomen (may be a hernia)

DANGER SIGNS - These suggest the condition is getting worse:
🔴 Pain becomes constant and severe
🔴 Fever
🔴 Rapid heartbeat
🔴 Abdomen becomes very tender to touch

═══════════════════════════════════════════════════════════════════════════════
WHAT TO EXPECT AT THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

INITIAL TREATMENT:
• You will not be allowed to eat or drink
• A tube may be placed through your nose into your stomach to drain fluid and relieve pressure
• IV fluids will be given to prevent dehydration
• Blood tests and X-rays or CT scan will be done
• Pain medication will be provided

TREATMENT OPTIONS:
• Many blockages improve with rest and decompression
• Surgery may be needed if the blockage is complete or if blood supply to the bowel is affected

═══════════════════════════════════════════════════════════════════════════════
AFTER LEAVING THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

IF TREATED WITHOUT SURGERY:
• Follow your doctor's diet instructions carefully
• Start with clear liquids, then gradually advance your diet
• Chew food thoroughly
• Eat smaller, more frequent meals
• Stay hydrated

IF YOU HAD SURGERY:
• Follow all wound care instructions
• Do not lift heavy objects for 4-6 weeks
• Gradually increase activity as advised
• Attend all follow-up appointments

═══════════════════════════════════════════════════════════════════════════════
WHEN TO RETURN TO THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

Return immediately if you experience:
• Return of severe abdominal pain
• Vomiting
• Inability to eat or drink
• Fever
• Wound redness, swelling, or drainage (if you had surgery)
• Signs of dehydration (dizziness, dark urine, dry mouth)

═══════════════════════════════════════════════════════════════════════════════
PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• If you have a hernia, discuss repair with your doctor before it becomes an emergency
• Stay active and avoid prolonged bed rest after surgery
• Maintain good bowel habits with adequate fiber and fluids
• Do not ignore warning signs - early treatment is important
• If you've had one episode, know that there is a risk of recurrence

═══════════════════════════════════════════════════════════════════════════════
KEY POINTS TO REMEMBER
═══════════════════════════════════════════════════════════════════════════════

• Bowel obstruction is a medical emergency
• Early treatment leads to better outcomes
• Do not eat or drink if you suspect a blockage
• Always examine groin and abdomen for bulges (hernias)
• Surgery may be needed but many cases improve without operation

Your healthcare team is here to help you. Do not hesitate to ask questions or seek care when concerned.`,

    references: [
      'Maung AA, Johnson DC, Piper GL, et al. Evaluation and management of small-bowel obstruction: an Eastern Association for the Surgery of Trauma practice management guideline. J Trauma Acute Care Surg. 2012;73(5 Suppl 4):S362-S369.',
      'Ten Broek RPG, Krielen P, Di Saverio S, et al. Bologna guidelines for diagnosis and management of adhesive small bowel obstruction (ASBO): 2017 update of the evidence-based guidelines. World J Emerg Surg. 2018;13:24.',
      'Defined by Scott, et al. American College of Radiology ACR Appropriateness Criteria® Suspected Small-Bowel Obstruction. J Am Coll Radiol. 2019;16(5S):S170-S177.',
      'Bickell NA, Federman AD, Aufses AH. Influence of time on risk of bowel resection in complete small bowel obstruction. J Am Coll Surg. 2005;201(6):847-854.',
      'Catena F, De Simone B, Coccolini F, et al. Bowel obstruction: a narrative review for all physicians. World J Emerg Surg. 2019;14:20.',
      'Di Saverio S, Coccolini F, Galati M, et al. Bologna guidelines for diagnosis and management of adhesive small bowel obstruction (ASBO): 2013 update. World J Emerg Surg. 2013;8(1):42.',
      'Rami Reddy SR, Cappell MS. A Systematic Review of the Clinical Presentation, Diagnosis, and Treatment of Small Bowel Obstruction. Curr Gastroenterol Rep. 2017;19(6):28.',
      'Markogiannakis H, Messaris E, Dardamanis D, et al. Acute mechanical bowel obstruction: clinical presentation, etiology, management and outcome. World J Gastroenterol. 2007;13(3):432-437. [Includes developing country data]',
      'Shepherd JJ. Treatment of volvulus of sigmoid colon: a review of 425 cases. Br Med J. 1968;1(5592):280-283. [Classic African series]',
      'Chalya PL, Mabula JB, Koy M, et al. Sigmoid volvulus at a tertiary care hospital in Tanzania. Tanzania Journal of Health Research. 2015;17(2). [African context]'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 25. ECTOPIC PREGNANCY
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '25',
    name: 'Ectopic Pregnancy',
    slug: 'ectopic-pregnancy',
    specialty: 'Obstetrics',
    bodySystem: 'Endocrine',
    icdCode: 'O00.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Ectopic pregnancy occurs when a fertilized ovum implants outside the uterine cavity, most commonly (95-97%) in the fallopian tube. It is a life-threatening obstetric emergency and a leading cause of maternal mortality in the first trimester, particularly in Africa where delayed presentation and limited surgical resources contribute to high fatality rates.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• ANY woman of reproductive age with abdominal pain ± vaginal bleeding = RULE OUT ECTOPIC
• RUPTURED ECTOPIC: Acute abdomen, hemodynamic instability, shoulder tip pain - SURGICAL EMERGENCY
• β-hCG + Transvaginal Ultrasound = Diagnostic cornerstone
• "DISCRIMINATORY ZONE": β-hCG >1500-2000 mIU/mL should show intrauterine pregnancy on TVS
• If β-hCG above discriminatory zone + empty uterus = ECTOPIC until proven otherwise
• TRIAD: Amenorrhea + Abdominal pain + Vaginal bleeding (but many present atypically)
• 50% of ectopic pregnancies have NO identified risk factors
• RISK FACTORS: Previous ectopic, tubal surgery, PID, IVF, smoking, IUD
• METHOTREXATE: Medical management option for STABLE, UNRUPTURED ectopic
• METHOTREXATE CONTRAINDICATED: Ruptured, unstable, β-hCG >5000, fetal cardiac activity, non-compliance
• SURGICAL OPTIONS: Salpingectomy (tube removal) vs Salpingostomy (tube preservation)
• HETEROTOPIC PREGNANCY: Concurrent IUP + ectopic - rare naturally but 1-3% with IVF
• Rh-NEGATIVE WOMEN: Give Anti-D immunoglobulin after any intervention
• SERIAL β-hCG: Essential for monitoring; should decline by >15% at day 4-7 post-MTX
• MORTALITY: <0.5% in developed countries; 1-3% in sub-Saharan Africa due to late presentation

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

NORMAL FERTILIZATION AND IMPLANTATION:
• Fertilization occurs in ampulla of fallopian tube
• Embryo travels to uterus over 3-4 days
• Implantation in uterine endometrium ~6 days post-fertilization

ECTOPIC IMPLANTATION - CAUSES:
• Impaired tubal motility (ciliary dysfunction, muscular abnormalities)
• Tubal damage (PID, surgery, endometriosis)
• Anatomical abnormalities
• Altered hormonal environment

NATURAL HISTORY OF TUBAL ECTOPIC:
1. Implantation in tubal wall
2. Trophoblastic invasion into tubal muscularis
3. Progressive growth → tubal distension
4. Possible outcomes:
   - Tubal abortion (expulsion into peritoneal cavity)
   - Tubal rupture (erosion through tubal wall) - LIFE-THREATENING
   - Spontaneous resolution (rare)

CONSEQUENCES OF RUPTURE:
• Hemorrhage into peritoneal cavity
• Hemoperitoneum → hypovolemic shock
• Without intervention → death within hours`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• Incidence: 1-2% of all pregnancies
• Accounts for 3-4% of pregnancy-related deaths globally
• Leading cause of first-trimester maternal mortality
• 95-97% are tubal ectopics
• Incidence has increased due to:
  - Rising PID/STI rates
  - Increased assisted reproduction
  - Better diagnostic detection

SITE DISTRIBUTION:
• Ampullary (tubal): 70% - most common
• Isthmic (tubal): 12%
• Fimbrial (tubal): 11%
• Interstitial/Cornual: 2-4% (higher mortality)
• Ovarian: 1-3%
• Cervical: <1%
• Abdominal: 1%
• Cesarean scar: Increasing with rising CS rates

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HIGHER INCIDENCE: 2-4% of pregnancies in some African regions
• LEADING CAUSE OF MATERNAL DEATH: Up to 10% of maternal mortality
• MORTALITY: 1-3% case fatality (vs <0.5% in developed countries)

CONTRIBUTING FACTORS IN AFRICA:
• HIGH PID RATES: Untreated STIs (gonorrhea, chlamydia) cause tubal damage
• UNSAFE ABORTION: Complications leading to PID and tubal damage
• DELAYED PRESENTATION: >70% present with rupture in some series
• LIMITED DIAGNOSTIC RESOURCES: Ultrasound not always available
• SURGICAL DELAYS: Limited OR access, blood product shortages
• TRADITIONAL MEDICINE: Delays seeking modern care

HIGH-RISK POPULATIONS:
• Urban areas with high STI prevalence
• Women with history of unsafe abortion
• Women with limited healthcare access

PRESENTATION PATTERNS:
• Most present RUPTURED (60-90% in some African series)
• Often in hemorrhagic shock on arrival
• Higher need for emergency laparotomy vs laparoscopy
• Blood transfusion frequently required

HEALTHCARE CHALLENGES:
• Ultrasound availability: Limited in rural areas
• β-hCG testing: May not be readily available
• Methotrexate: Often unavailable or unaffordable
• Laparoscopy: Limited availability; most require laparotomy
• Blood products: Critical shortage in emergencies
• ICU care: Limited for post-operative complications`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CLASSIC TRIAD (Present in <50% of cases)
═══════════════════════════════════════════════════════════════════════════════

1. AMENORRHEA
   • Usually 6-8 weeks (range 4-12 weeks)
   • May report "abnormal last period" (implantation bleeding mistaken for menses)
   • 25% unaware they are pregnant

2. ABDOMINAL/PELVIC PAIN
   • Unilateral lower quadrant pain (most common symptom)
   • May be crampy or sharp
   • Progressively worsening
   • With rupture: Sudden severe pain, generalized abdominal pain

3. VAGINAL BLEEDING
   • Usually light, irregular
   • Often described as "spotting"
   • Rarely heavy (heavy bleeding suggests other pathology)
   • Passage of decidual cast may occur

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS BY PRESENTATION TYPE
═══════════════════════════════════════════════════════════════════════════════

UNRUPTURED ECTOPIC:
• Mild-moderate unilateral pelvic pain
• Light vaginal bleeding/spotting
• May be asymptomatic (detected incidentally)
• Hemodynamically stable
• Mild adnexal tenderness on exam

RUPTURED ECTOPIC - SURGICAL EMERGENCY:
• Sudden severe abdominal pain
• Generalized peritonitis
• SHOULDER TIP PAIN (referred from diaphragmatic irritation by blood)
• Syncope/pre-syncope
• Signs of hypovolemic shock:
  - Tachycardia
  - Hypotension
  - Pallor
  - Altered mental status
  - Cold, clammy extremities
• Abdominal distension (hemoperitoneum)

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
═══════════════════════════════════════════════════════════════════════════════

GENERAL APPEARANCE:
• Unruptured: May appear well
• Ruptured: Pale, distressed, diaphoretic, restless

VITAL SIGNS:
• Unruptured: Usually normal
• Ruptured: Tachycardia, hypotension, tachypnea
• CAUTION: Young women may maintain BP until late; tachycardia is earlier sign

ABDOMINAL EXAMINATION:
• Unruptured: Mild lower quadrant tenderness
• Ruptured:
  - Generalized tenderness
  - Guarding and rigidity
  - Rebound tenderness
  - Distension (hemoperitoneum)
  - Absent bowel sounds (ileus)
  - Cullen sign (periumbilical ecchymosis) - rare, late sign

PELVIC EXAMINATION:
• Cervical motion tenderness ("chandelier sign") - suggestive of hemoperitoneum
• Adnexal tenderness ± mass
• Uterus may be slightly enlarged (hormonal effect)
• Cul-de-sac fullness or mass (hemoperitoneum)
• Vaginal bleeding from os

═══════════════════════════════════════════════════════════════════════════════
ATYPICAL PRESENTATIONS - MAINTAIN HIGH SUSPICION
═══════════════════════════════════════════════════════════════════════════════

• Asymptomatic (incidental finding on ultrasound)
• GI symptoms: Nausea, vomiting, diarrhea (confused with gastroenteritis)
• Urinary symptoms: Dysuria, frequency (confused with UTI)
• Back pain only
• Rectal pressure/pain
• Shoulder pain without abdominal pain (referred pain)
• Presenting in cardiac arrest (massive hemorrhage)

⚠️ RULE: Any woman of reproductive age with abdominal pain, vaginal bleeding, 
   syncope, or shock should have pregnancy test performed.`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

STEP 1: CONFIRM PREGNANCY
• Urine pregnancy test (qualitative β-hCG)
• Serum β-hCG (quantitative) if urine test positive or high clinical suspicion

STEP 2: DETERMINE PREGNANCY LOCATION
• Transvaginal ultrasound (TVS) - Investigation of choice
• Correlate with β-hCG level

STEP 3: ASSESS STABILITY
• Vital signs
• Evidence of hemoperitoneum
• Need for emergency surgery vs medical management

═══════════════════════════════════════════════════════════════════════════════
β-hCG INTERPRETATION
═══════════════════════════════════════════════════════════════════════════════

DISCRIMINATORY ZONE:
• β-hCG level above which intrauterine pregnancy should be visible on TVS
• TVS: 1,500-2,000 mIU/mL
• TAS: 6,000-6,500 mIU/mL

INTERPRETATION:
• β-hCG ABOVE discriminatory zone + empty uterus = ECTOPIC until proven otherwise
• β-hCG BELOW discriminatory zone + empty uterus = Pregnancy of unknown location (PUL)
  → Repeat β-hCG in 48-72 hours

NORMAL β-hCG DOUBLING:
• Early viable IUP: β-hCG doubles every 48-72 hours
• Minimum expected rise: 53% in 48 hours (for β-hCG <1,500)
• Slower rise or plateau suggests abnormal pregnancy (ectopic or nonviable IUP)
• Decline suggests failing pregnancy

SERIAL β-hCG PATTERNS:
• Normal rise + IUP visible = Viable intrauterine pregnancy
• Normal rise + no IUP = Possible early IUP; repeat in 48-72h
• Slow rise + no IUP = Likely ectopic or failing IUP
• Decline + no IUP = Likely failing IUP or resolving ectopic
• Plateau = Highly suspicious for ectopic

═══════════════════════════════════════════════════════════════════════════════
TRANSVAGINAL ULTRASOUND FINDINGS
═══════════════════════════════════════════════════════════════════════════════

DEFINITIVE ECTOPIC (100% specific):
• Extrauterine gestational sac with yolk sac and/or embryo
• Extrauterine embryo with cardiac activity

HIGHLY SUSPICIOUS:
• Adnexal mass separate from ovary ("blob sign," "bagel sign")
• Complex adnexal mass with ring of fire (Doppler - trophoblastic flow)
• Empty uterus with β-hCG above discriminatory zone
• Free fluid in pelvis (especially echogenic - indicates blood)

LESS SPECIFIC:
• Pseudogestational sac (intrauterine fluid collection without double decidual sign)
• Thickened, heterogeneous endometrium
• Corpus luteum cyst (can mimic ectopic; usually intraovarian)

FINDINGS IN RUPTURED ECTOPIC:
• Significant free fluid in pelvis and abdomen
• Echogenic (complex) free fluid suggests hemoperitoneum
• May obscure visualization of adnexa

═══════════════════════════════════════════════════════════════════════════════
PREGNANCY OF UNKNOWN LOCATION (PUL)
═══════════════════════════════════════════════════════════════════════════════

DEFINITION:
• Positive pregnancy test
• No evidence of IUP or ectopic on TVS
• β-hCG below discriminatory zone

MANAGEMENT:
• Serial β-hCG every 48-72 hours
• Repeat TVS when β-hCG reaches discriminatory zone
• Most will declare as either IUP or ectopic
• Some resolve spontaneously (failed PUL)

⚠️ PUL requires close follow-up until location confirmed or pregnancy resolves`,

    differentialDiagnosis: [
      'Threatened/Incomplete/Complete Miscarriage - Intrauterine pregnancy on ultrasound; passage of products of conception; declining β-hCG with complete miscarriage',
      'Corpus Luteum Cyst (Hemorrhagic) - Within ovary on TVS; may cause pain if ruptured; IUP may be present; usually resolves',
      'Ovarian Torsion - Severe unilateral pain, nausea/vomiting; enlarged ovary with absent Doppler flow; may be pregnant or not',
      'Pelvic Inflammatory Disease (PID) - Bilateral adnexal tenderness, cervical motion tenderness; discharge, fever; negative pregnancy test',
      'Appendicitis - RLQ pain, anorexia, fever; may have leukocytosis; CT or US shows inflamed appendix; can coexist with pregnancy',
      'Urinary Tract Infection/Pyelonephritis - Dysuria, frequency, flank pain; pyuria on urinalysis; pregnancy test negative (unless concurrent)',
      'Ovarian Cyst (Rupture/Torsion) - Sharp unilateral pain; cyst visible on US; β-hCG negative',
      'Endometriosis/Endometrioma - Chronic pelvic pain, dysmenorrhea, dyspareunia; "chocolate cyst" on US; β-hCG negative',
      'Heterotopic Pregnancy - Concurrent IUP AND ectopic; rare naturally (1:30,000) but 1-3% with IVF; IUP does not exclude ectopic',
      'Gastroenteritis - Nausea, vomiting, diarrhea; abdominal cramping; usually afebrile; pregnancy test negative',
      'Degenerating Fibroid - Known fibroids; focal tenderness over fibroid; may cause pain in pregnancy',
      'Round Ligament Pain - Common in early pregnancy; sharp groin pain with movement; normal exam and US'
    ],

    investigations: [
      {
        name: 'Urine Pregnancy Test (Qualitative β-hCG)',
        indication: '🔴 IMMEDIATE - First step; highly sensitive; rapid bedside test; positive in virtually all ectopic pregnancies',
        availability: 'BASIC'
      },
      {
        name: 'Serum β-hCG (Quantitative)',
        indication: '🔴 IMMEDIATE - Confirms pregnancy; correlate with ultrasound findings; baseline for serial monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 IMMEDIATE - Hemoglobin/hematocrit for blood loss assessment; WBC (usually normal in ectopic); baseline pre-op',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🔴 IMMEDIATE - Essential; Rh status determines need for Anti-D; crossmatch for possible transfusion',
        availability: 'BASIC'
      },
      {
        name: 'Transvaginal Ultrasound (TVS)',
        indication: '🔴 IMMEDIATE - Gold standard imaging; identifies IUP or ectopic; assesses free fluid; guides management',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Transabdominal Ultrasound (TAS)',
        indication: '🟠 URGENT - When TVS unavailable; less sensitive; discriminatory zone higher (6,000+ mIU/mL)',
        availability: 'BASIC'
      },
      {
        name: 'Serial β-hCG (48-72 hour repeat)',
        indication: '🟠 URGENT - For pregnancy of unknown location; assess trend; <53% rise in 48h suggests abnormal pregnancy',
        availability: 'BASIC'
      },
      {
        name: 'Serum Progesterone',
        indication: '🟡 Adjunct - <5 ng/mL suggests nonviable pregnancy; >25 ng/mL suggests viable IUP; intermediate values not diagnostic',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Comprehensive Metabolic Panel',
        indication: '🟠 URGENT - Pre-operative assessment; baseline renal and liver function (relevant for methotrexate)',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, PTT)',
        indication: '🟠 URGENT - Pre-operative assessment; especially if significant hemorrhage',
        availability: 'BASIC'
      },
      {
        name: 'Culdocentesis (Aspiration of Cul-de-sac)',
        indication: '🟡 When ultrasound unavailable - Aspiration of non-clotting blood confirms hemoperitoneum; largely replaced by ultrasound',
        availability: 'BASIC'
      },
      {
        name: 'Diagnostic Laparoscopy',
        indication: '🟡 When diagnosis uncertain - Direct visualization; can be therapeutic; gold standard but invasive',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🟡 Pre-operative - Baseline if methotrexate planned (pulmonary toxicity risk); routine pre-op in some settings',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 Required before Methotrexate - Methotrexate contraindicated with hepatic dysfunction',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function Tests',
        indication: '🟠 Required before Methotrexate - Methotrexate contraindicated with renal dysfunction',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ABC ASSESSMENT - Airway, breathing, circulation; identify shock immediately',
        '⚡ IV ACCESS - Two large-bore (16-18G) IV lines; prepare for massive resuscitation if ruptured',
        '⚡ FLUID RESUSCITATION - Crystalloid bolus (1-2 L NS/LR) if hypotensive; reassess frequently',
        '⚡ PREGNANCY TEST - Urine β-hCG first; serum quantitative β-hCG to follow',
        '⚡ BLOOD TYPE AND CROSSMATCH - Prepare for possible transfusion; identify Rh status',
        '⚡ TRANSVAGINAL ULTRASOUND - As soon as possible to locate pregnancy',
        '⚡ SURGICAL CONSULTATION - Immediate if ruptured or unstable; urgent for all confirmed ectopics',
        '⚡ NPO STATUS - In case emergency surgery required',
        '⚡ ANALGESIA - IV opioids for pain control; do not delay for diagnosis',
        '⚡ BLOOD TRANSFUSION - If significant hemorrhage; do not wait for crossmatch if exsanguinating',
        '⚡ FOLEY CATHETER - Monitor urine output; empty bladder for ultrasound',
        '⚡ OXYGEN - If hypoxic or in shock',
        '⚡ NOTIFY OR - Alert operating room if surgery anticipated',
        '⚡ EMOTIONAL SUPPORT - Recognize pregnancy loss; involve support services'
      ],
      pharmacological: [
        {
          drug: 'Methotrexate (MTX)',
          indication: '🔴 MEDICAL MANAGEMENT - For stable, unruptured ectopic meeting criteria; first-line non-surgical option',
          dose: 'Single dose: 50 mg/m² IM; Multi-dose: 1 mg/kg IM on days 1, 3, 5, 7 alternating with leucovorin',
          route: 'IM (intramuscular)',
          duration: 'Single dose protocol most common; may require second dose if β-hCG decline <15% at day 4-7',
          contraindications: [
            'Hemodynamically unstable',
            'Ruptured ectopic',
            'β-hCG >5,000 mIU/mL (relative; some use higher thresholds)',
            'Fetal cardiac activity on ultrasound',
            'Ectopic size >4 cm (relative)',
            'Immunodeficiency',
            'Active pulmonary disease',
            'Peptic ulcer disease',
            'Hepatic dysfunction',
            'Renal dysfunction',
            'Breastfeeding',
            'Unable to comply with follow-up',
            'Heterotopic pregnancy'
          ],
          sideEffects: ['Abdominal pain (common; separation pain)', 'Nausea/vomiting', 'Stomatitis', 'Bone marrow suppression', 'Elevated LFTs', 'Photosensitivity', 'Pneumonitis (rare)'],
          alternatives: ['Surgical management (salpingectomy or salpingostomy)']
        },
        {
          drug: 'Leucovorin (Folinic Acid)',
          indication: '🟠 WITH MULTI-DOSE MTX - Rescue therapy to reduce methotrexate toxicity',
          dose: '0.1 mg/kg IM on days 2, 4, 6, 8 (day after each MTX dose)',
          route: 'IM',
          duration: 'Alternating with methotrexate doses',
          contraindications: ['Pernicious anemia (may mask B12 deficiency)'],
          sideEffects: ['Rare; generally well tolerated'],
          alternatives: ['Not needed with single-dose MTX protocol']
        },
        {
          drug: 'Anti-D Immunoglobulin (RhoGAM)',
          indication: '🔴 ALL Rh-NEGATIVE WOMEN - Prevents Rh sensitization; give within 72 hours of any intervention',
          dose: '300 mcg (1500 IU) IM for second/third trimester; 50-150 mcg may be sufficient for first trimester',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Rh-positive women', 'Known anti-D antibodies'],
          sideEffects: ['Injection site pain', 'Mild fever', 'Rare allergic reactions'],
          alternatives: ['None for Rh-negative women']
        },
        {
          drug: 'Normal Saline / Lactated Ringer',
          indication: '🔴 FLUID RESUSCITATION - For hypovolemia; initial resuscitation for ruptured ectopic',
          dose: '1-2 L bolus, then titrate to vital signs and urine output; may need 4-6 L or more',
          route: 'IV',
          duration: 'Until hemodynamically stable',
          contraindications: ['Fluid overload (rare concern in hemorrhage)'],
          sideEffects: ['Dilutional coagulopathy with massive resuscitation', 'Hypothermia if not warmed'],
          alternatives: ['Balanced crystalloids; colloids if available']
        },
        {
          drug: 'Packed Red Blood Cells',
          indication: '🔴 HEMORRHAGE/SHOCK - For significant blood loss; do not wait for crossmatch if exsanguinating',
          dose: 'Transfuse to maintain Hb >7-8 g/dL; massive transfusion protocol if severe hemorrhage',
          route: 'IV',
          duration: 'Until hemostasis achieved and Hb stable',
          contraindications: ['Jehovah Witness (respect patient autonomy after counseling)'],
          sideEffects: ['Transfusion reactions', 'Volume overload', 'Hypothermia', 'TRALI'],
          alternatives: ['Cell salvage if available; whole blood in resource-limited settings']
        },
        {
          drug: 'Morphine Sulfate',
          indication: '🔴 ANALGESIA - Pain control; should not be withheld while awaiting diagnosis',
          dose: '2-4 mg IV every 2-4 hours PRN; titrate to pain control',
          route: 'IV',
          duration: 'As needed',
          contraindications: ['Severe respiratory depression', 'Hypotension'],
          sideEffects: ['Nausea', 'Sedation', 'Respiratory depression', 'Hypotension'],
          alternatives: ['Fentanyl', 'Hydromorphone']
        },
        {
          drug: 'Ondansetron',
          indication: '🟠 ANTIEMETIC - For nausea; pre-medication with methotrexate',
          dose: '4-8 mg IV/PO every 8 hours PRN',
          route: 'IV or PO',
          duration: 'As needed',
          contraindications: ['QT prolongation', 'Hypersensitivity'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation'],
          alternatives: ['Metoclopramide', 'Promethazine']
        },
        {
          drug: 'Cefazolin',
          indication: '🟠 SURGICAL PROPHYLAXIS - Pre-operative antibiotic for surgical management',
          dose: '2 g IV within 60 minutes of incision',
          route: 'IV',
          duration: 'Single pre-operative dose',
          contraindications: ['Cephalosporin/penicillin allergy (use alternative)'],
          sideEffects: ['Allergic reaction', 'Diarrhea'],
          alternatives: ['Clindamycin + Gentamicin if penicillin allergy']
        },
        {
          drug: 'Oxytocin',
          indication: '🟡 NOT TYPICALLY INDICATED - Ectopic is not uterine pregnancy; no role for uterotonics',
          dose: 'N/A',
          route: 'N/A',
          duration: 'N/A',
          contraindications: ['Not indicated for ectopic pregnancy'],
          sideEffects: ['N/A'],
          alternatives: ['N/A']
        }
      ],
      nonPharmacological: [
        'NPO STATUS: Nothing by mouth in case emergency surgery required',
        'BED REST: Until stability confirmed and management plan established',
        'EMOTIONAL SUPPORT: Pregnancy loss counseling; involve social work/chaplaincy',
        'CONTRACEPTION COUNSELING: Advise avoiding pregnancy for 3 months post-MTX (teratogenic)',
        'FOLIC ACID: Avoid supplementation during and for 1 week after MTX (antagonizes effect)',
        'AVOID NSAIDs: May increase MTX toxicity',
        'AVOID ALCOHOL: Hepatotoxic; increased risk with MTX',
        'AVOID SUN EXPOSURE: Photosensitivity with MTX',
        'PELVIC REST: Avoid intercourse until ectopic resolved',
        'SERIAL MONITORING: Weekly β-hCG until undetectable (may take 4-8 weeks post-MTX)'
      ],
      surgical: [
        'LAPAROSCOPY - Preferred approach when available; faster recovery, less morbidity',
        'LAPAROTOMY - For unstable patients, ruptured ectopic with massive hemorrhage, when laparoscopy unavailable/failed',
        'SALPINGECTOMY - Removal of affected tube; preferred for: ruptured tube, severely damaged tube, no desire for future fertility, recurrent ectopic in same tube',
        'SALPINGOSTOMY - Incision in tube to remove ectopic, preserving tube; for: unruptured, desire future fertility, contralateral tube damaged/absent; requires serial β-hCG (5-20% persistent trophoblast)',
        'MILKING/EXPRESSION - Fimbrial ectopic may be expressed; higher persistent trophoblast rate; not generally recommended',
        'CORNUAL/INTERSTITIAL RESECTION - Cornual wedge resection; higher risk of hemorrhage; may require hysterectomy',
        'CERVICAL ECTOPIC - May attempt MTX, UAE, or curettage with balloon tamponade; hysterectomy may be required',
        'CESAREAN SCAR ECTOPIC - MTX, UAE, surgical excision; complex management',
        'DAMAGE CONTROL SURGERY - Massive hemorrhage: rapid salpingectomy, packing, resuscitate, second-look if needed'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE SURGICAL: Hemodynamically unstable, signs of rupture, peritonitis',
        '🔴 URGENT GYNECOLOGY: All confirmed ectopic pregnancies for management decision',
        '🟠 FERTILITY SPECIALIST: After ectopic resolved if future fertility desired; evaluate tubal status',
        '🟠 INTERVENTIONAL RADIOLOGY: For UAE in cervical or scar ectopic',
        '🟠 HIGH-RISK OBSTETRICS: Subsequent pregnancies after ectopic (increased recurrence risk)',
        '🟠 MENTAL HEALTH: Grief counseling for pregnancy loss',
        '🟠 BLOOD BANK: If massive transfusion anticipated'
      ],
      monitoring: [
        'VITAL SIGNS: Continuous if unstable; every 15-30 minutes initially, then every 1-4 hours',
        'URINE OUTPUT: Hourly via Foley; target >0.5 mL/kg/hr',
        'HEMOGLOBIN: Serial checks if bleeding; q4-6h initially',
        'β-hCG POST-MTX: Day 4 and Day 7 (expect <15% decline from Day 4 to Day 7)',
        'WEEKLY β-hCG: Until undetectable after MTX (may take 4-8 weeks)',
        'PAIN ASSESSMENT: Increasing pain may indicate rupture or treatment failure',
        'SYMPTOMS MONITORING: Teach patient warning signs for rupture',
        'LFTs/CBC: Weekly while on MTX; watch for bone marrow suppression'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
POST-METHOTREXATE FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

DAY 1: Methotrexate administered
• Baseline β-hCG, CBC, LFTs, Cr
• Educate on warning signs, activity restrictions, medication avoidances

DAY 4: β-hCG level
• May rise initially (trophoblast destruction releasing hCG) - expected

DAY 7: β-hCG level
• Should decline ≥15% from Day 4 level
• If <15% decline: Consider second dose MTX

WEEKLY THEREAFTER:
• β-hCG until undetectable (<5 mIU/mL)
• Average time to resolution: 4-6 weeks (range 2-8 weeks)

INDICATIONS FOR SECOND MTX DOSE:
• <15% decline in β-hCG between Day 4 and Day 7
• Plateau or rise in β-hCG on weekly monitoring

INDICATIONS FOR SURGERY AFTER MTX:
• Rupture (emergency surgery)
• Significantly rising β-hCG despite MTX
• Persistent ectopic after multiple MTX doses
• Patient preference

═══════════════════════════════════════════════════════════════════════════════
POST-SURGICAL FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

POST-SALPINGECTOMY:
• β-hCG at 1 week (should be undetectable or near-zero)
• Wound check at 1-2 weeks
• Follow-up at 4-6 weeks for recovery assessment

POST-SALPINGOSTOMY:
• Weekly β-hCG until undetectable (persistent trophoblast in 5-20%)
• If β-hCG plateaus or rises: MTX or repeat surgery

═══════════════════════════════════════════════════════════════════════════════
FUTURE FERTILITY COUNSELING
═══════════════════════════════════════════════════════════════════════════════

• Recurrence risk: 10-15% after one ectopic; 25-30% after two
• Intrauterine pregnancy rate: 50-80% after conservative management
• Early ultrasound in future pregnancies to confirm IUP
• Consider HSG or laparoscopy to assess tubal status if infertility
• IVF may be needed if bilateral tubal damage

CONTRACEPTION:
• Avoid pregnancy for 3 months post-MTX (teratogenic effect)
• Any reliable method acceptable
• IUD: NOT contraindicated after ectopic

═══════════════════════════════════════════════════════════════════════════════
PATIENT EDUCATION POINTS
═══════════════════════════════════════════════════════════════════════════════

• Explain diagnosis and treatment options
• Emphasize importance of follow-up β-hCG monitoring
• Teach warning signs: severe pain, dizziness, fainting, heavy bleeding
• Discuss future fertility and recurrence risk
• Provide grief/loss support resources
• Written discharge instructions with emergency contact numbers

⚠️ Individual follow-up schedules should be determined by the treating physician.`
    },

    redFlags: [
      '🔴 CRITICAL: Hemodynamic instability (hypotension, tachycardia) - indicates rupture with significant hemorrhage - EMERGENCY SURGERY',
      '🔴 CRITICAL: Severe sudden abdominal pain - suggests rupture - IMMEDIATE SURGICAL INTERVENTION',
      '🔴 CRITICAL: Shoulder tip pain - indicates diaphragmatic irritation from hemoperitoneum - RUPTURED ECTOPIC',
      '🔴 CRITICAL: Syncope or altered mental status - sign of severe hypovolemia - EMERGENCY',
      '🔴 CRITICAL: Rigid abdomen with peritonitis - intraperitoneal hemorrhage - EMERGENCY LAPAROTOMY',
      '🟠 URGENT: β-hCG above discriminatory zone with empty uterus - ectopic until proven otherwise',
      '🟠 URGENT: Increasing abdominal pain during MTX treatment - may indicate rupture or tubal abortion',
      '🟠 URGENT: Rising β-hCG after MTX treatment - treatment failure; consider repeat MTX or surgery',
      '🟠 URGENT: Fetal cardiac activity in extrauterine location - MTX contraindicated; surgery required',
      '🟠 URGENT: Large ectopic (>4 cm) - higher rupture risk; surgery may be preferred',
      '🟠 URGENT: Interstitial/cornual ectopic - high rupture risk and mortality; early intervention critical'
    ],

    complications: [
      'TUBAL RUPTURE: Most feared complication; can occur at any time; presents with acute abdomen and shock',
      'HEMORRHAGIC SHOCK: From ruptured ectopic; can be rapidly fatal; requires emergency surgery and transfusion',
      'DEATH: Leading cause of first-trimester maternal mortality; higher in Africa due to delayed presentation',
      'PERSISTENT TROPHOBLAST: 5-20% after salpingostomy; requires serial β-hCG and possible MTX/re-surgery',
      'RECURRENT ECTOPIC: 10-15% after first ectopic; higher with tubal damage, conservative management',
      'INFERTILITY: Due to tubal damage from ectopic or surgery; may require IVF',
      'MTX SIDE EFFECTS: Nausea, stomatitis, bone marrow suppression, hepatotoxicity, pneumonitis (rare)',
      'SURGICAL COMPLICATIONS: Bleeding, infection, anesthetic risks, injury to adjacent structures',
      'RH SENSITIZATION: If Anti-D not given to Rh-negative women; affects future pregnancies',
      'PSYCHOLOGICAL: Grief, depression, anxiety related to pregnancy loss; may require counseling'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

MORTALITY:
• Developed countries: <0.5% (due to early diagnosis and treatment)
• Sub-Saharan Africa: 1-3% (due to late presentation, limited resources)
• Ruptured with shock: Up to 10% mortality in low-resource settings

FERTILITY AFTER ECTOPIC:
• Intrauterine pregnancy rate: 50-80%
• Recurrent ectopic rate: 10-15%
• Infertility rate: 10-20%

TREATMENT SUCCESS RATES:

METHOTREXATE (Single Dose):
• Overall success: 85-90%
• Higher success with:
  - Lower β-hCG (<5000 mIU/mL)
  - Smaller ectopic size
  - No fetal cardiac activity
  - Declining β-hCG at presentation

SALPINGOSTOMY:
• Success rate: ~95%
• Persistent trophoblast: 5-20%
• Future IUP rate: 60-80%
• Future ectopic rate: 10-15%

SALPINGECTOMY:
• Success rate: ~99%
• No persistent trophoblast risk
• Future IUP rate: 50-60% (depends on contralateral tube)
• Future ectopic rate: 10%

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Early diagnosis (unruptured)
• Hemodynamic stability
• Lower β-hCG level
• Smaller ectopic size
• No fetal cardiac activity
• Normal contralateral tube
• Access to surgical care

UNFAVORABLE:
• Late presentation with rupture
• Hemodynamic instability
• High β-hCG level
• Large ectopic mass
• Fetal cardiac activity
• Bilateral tubal disease
• Limited healthcare resources

⚠️ Individual prognosis depends on presentation, treatment, and underlying fertility factors.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

REDUCE PID/STI RISK:
• Safe sex practices (condom use)
• STI screening and early treatment
• Partner notification and treatment
• Avoid multiple sexual partners
• HPV vaccination (reduces cervical procedures)

SMOKING CESSATION:
• Smoking increases ectopic risk 2-4 fold
• Affects tubal motility and ciliary function

AVOID TUBAL DAMAGE:
• Early treatment of PID
• Minimize pelvic surgeries when possible
• Skilled surgical technique if surgery needed

CONTRACEPTION COUNSELING:
• Highly effective contraception prevents all pregnancies, including ectopic
• If pregnancy not desired, consistent contraception is best prevention

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Early Detection)
═══════════════════════════════════════════════════════════════════════════════

HIGH-RISK WOMEN:
• Early pregnancy ultrasound to confirm IUP location
• Early β-hCG if pregnancy symptoms
• Low threshold for evaluation with pain or bleeding

WOMEN WITH PREVIOUS ECTOPIC:
• Early ultrasound in subsequent pregnancies
• β-hCG monitoring if uncertain location
• Education on warning signs

ASSISTED REPRODUCTION:
• Early ultrasound to confirm single IUP
• Awareness of heterotopic pregnancy risk
• Minimize number of embryos transferred

═══════════════════════════════════════════════════════════════════════════════
COMMUNITY/HEALTH SYSTEM PREVENTION
═══════════════════════════════════════════════════════════════════════════════

• Improve access to family planning services
• STI screening and treatment programs
• Reduce unsafe abortion through access to safe services
• Train healthcare workers in early recognition
• Ensure ultrasound availability at primary care level
• Establish referral pathways for emergency cases
• Public education on warning signs of ectopic pregnancy
• Blood bank availability for emergencies`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS AN ECTOPIC PREGNANCY?
═══════════════════════════════════════════════════════════════════════════════

An ectopic pregnancy happens when a fertilized egg implants and grows outside the uterus (womb), most commonly in the fallopian tube. Because the tube cannot stretch to accommodate a growing pregnancy, an ectopic pregnancy cannot continue normally and can be life-threatening if it ruptures.

This is NOT a viable pregnancy and cannot be saved or moved to the uterus. Treatment is necessary to protect your health and life.

═══════════════════════════════════════════════════════════════════════════════
EMERGENCY WARNING SIGNS - SEEK IMMEDIATE CARE
═══════════════════════════════════════════════════════════════════════════════

Call emergency services or go to the hospital IMMEDIATELY if you experience:

🔴 Sudden severe abdominal pain
🔴 Feeling dizzy, lightheaded, or faint
🔴 Pain in your shoulder tip (especially with abdominal pain)
🔴 Feeling like you might pass out
🔴 Very pale skin
🔴 Racing heartbeat
🔴 Heavy vaginal bleeding

These may be signs that the ectopic pregnancy has ruptured, which is a life-threatening emergency.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING YOUR TREATMENT
═══════════════════════════════════════════════════════════════════════════════

METHOTREXATE (Medicine Treatment):
If you received methotrexate:
• This medicine stops the pregnancy from growing
• You MUST return for follow-up blood tests as scheduled
• Some abdominal pain is normal (separation pain)
• Avoid pregnancy for 3 months after treatment
• Avoid alcohol, folic acid supplements, and anti-inflammatory medicines (ibuprofen)
• Avoid sun exposure (increased skin sensitivity)
• Treatment may take several weeks to complete

SURGERY:
If you had surgery:
• Rest at home for the first few days
• Gradually increase activity as tolerated
• Avoid heavy lifting for 4-6 weeks
• Attend all follow-up appointments
• Report any signs of wound infection

═══════════════════════════════════════════════════════════════════════════════
FOLLOW-UP IS ESSENTIAL
═══════════════════════════════════════════════════════════════════════════════

• You MUST attend all scheduled follow-up appointments
• Blood tests (hCG levels) will be checked regularly
• Treatment is not complete until hCG level reaches zero
• Missing follow-up appointments can be dangerous

═══════════════════════════════════════════════════════════════════════════════
EMOTIONAL SUPPORT
═══════════════════════════════════════════════════════════════════════════════

• An ectopic pregnancy is a pregnancy loss - it is normal to grieve
• You may experience sadness, anger, guilt, or anxiety
• Talk to your partner, family, friends, or a counselor
• Ask your healthcare provider about support groups
• Give yourself time to heal emotionally as well as physically

═══════════════════════════════════════════════════════════════════════════════
FUTURE PREGNANCIES
═══════════════════════════════════════════════════════════════════════════════

• You can try to become pregnant again after recovery
• Wait at least 3 months after methotrexate treatment
• Your chance of having a normal pregnancy in the future is good (50-80%)
• There is a small increased risk of another ectopic pregnancy (10-15%)
• Get early prenatal care in future pregnancies
• Tell your doctor about your history of ectopic pregnancy
• Early ultrasound will be done to confirm pregnancy location

═══════════════════════════════════════════════════════════════════════════════
REDUCING YOUR RISK
═══════════════════════════════════════════════════════════════════════════════

• Practice safe sex to prevent sexually transmitted infections
• Get tested and treated for STIs promptly
• Don't smoke (smoking increases ectopic risk)
• If you don't want to become pregnant, use reliable contraception

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What treatment is best for me?
• When should I return for follow-up?
• What warning signs should I watch for?
• When can I try to become pregnant again?
• Will this affect my ability to have children?
• Are there any support groups available?

Your healthcare team is here to help you through this difficult time.`,

    references: [
      'American College of Obstetricians and Gynecologists. ACOG Practice Bulletin No. 193: Tubal Ectopic Pregnancy. Obstet Gynecol. 2018;131(3):e91-e103.',
      'Barnhart KT. Clinical practice. Ectopic pregnancy. N Engl J Med. 2009;361(4):379-387.',
      'Bouyer J, Coste J, Fernandez H, et al. Sites of ectopic pregnancy: a 10 year population-based study of 1800 cases. Hum Reprod. 2002;17(12):3224-3230.',
      'Mol F, Mol BW, Ankum WM, et al. Current evidence on surgery, systemic methotrexate and expectant management in the treatment of tubal ectopic pregnancy: a systematic review and meta-analysis. Hum Reprod Update. 2008;14(4):309-319.',
      'Kirk E, Bottomley C, Bourne T. Diagnosing ectopic pregnancy and current concepts in the management of pregnancy of unknown location. Hum Reprod Update. 2014;20(2):250-261.',
      'Elson CJ, Salim R, Potdar N, et al. Diagnosis and management of ectopic pregnancy: Green-top Guideline No. 21. BJOG. 2016;123(13):e15-e55.',
      'Goyaux N, Leke R, Keita N, Thonneau P. Ectopic pregnancy in African developing countries. Acta Obstet Gynecol Scand. 2003;82(4):305-312. [African context]',
      'Adusei JA, Odame EA, Osei I. Ectopic pregnancy in a tertiary hospital in Ghana. Pan Afr Med J. 2022;42:176. [African data]',
      'Sivalingam VN, Duncan WC, Kirk E, et al. Diagnosis and management of ectopic pregnancy. J Fam Plann Reprod Health Care. 2011;37(4):231-240.',
      'Practice Committee of American Society for Reproductive Medicine. Medical treatment of ectopic pregnancy: a committee opinion. Fertil Steril. 2013;100(3):638-644.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 26. DIABETIC FOOT INFECTION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '26',
    name: 'Diabetic Foot Infection',
    slug: 'diabetic-foot-infection',
    specialty: 'Infectious Disease',
    bodySystem: 'Endocrine',
    icdCode: 'E11.621',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Diabetic foot infection (DFI) is a serious complication of diabetes mellitus characterized by infection of soft tissues and/or bone below the ankle in a person with diabetes. It is the leading cause of non-traumatic lower extremity amputation worldwide and represents a major cause of hospitalization, disability, and mortality in diabetic patients, particularly in Africa where late presentation is common.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DIABETIC FOOT ULCER (DFU) ≠ DIABETIC FOOT INFECTION - Not all ulcers are infected
• INFECTION DIAGNOSIS: Clinical, NOT based on culture; ≥2 signs of inflammation
• CLASSIC SIGNS MAY BE ABSENT: Neuropathy masks pain; PAD impairs inflammatory response
• PROBE-TO-BONE TEST: If positive, ~90% probability of osteomyelitis
• OSTEOMYELITIS: Present in 50-60% of severe DFI; requires prolonged antibiotics ± surgery
• ISCHEMIA ASSESSMENT: Mandatory - Check pulses, ABI; revascularization may be needed first
• GLYCEMIC CONTROL: Hyperglycemia impairs wound healing and immune function
• MULTIDISCIPLINARY APPROACH: Surgery, endocrinology, vascular, infectious disease, podiatry
• WOUND CULTURE: Obtain DEEP tissue culture (not superficial swab) before antibiotics
• EMPIRIC ANTIBIOTICS: Cover gram-positives (S. aureus); add gram-negatives/anaerobes for severe
• MRSA COVERAGE: Add if risk factors (prior MRSA, recent hospitalization, high local prevalence)
• SURGICAL DEBRIDEMENT: Essential for abscess, necrosis, extensive infection
• AMPUTATION: Life-saving when limb-threatening infection, uncontrollable sepsis, non-viable limb
• OFFLOADING: Critical for healing - total contact cast, removable walker, or therapeutic footwear
• PREVENTION: Daily foot inspection, proper footwear, regular podiatry, glycemic control

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY - THE "DIABETIC FOOT TRIAD"
═══════════════════════════════════════════════════════════════════════════════

1. PERIPHERAL NEUROPATHY (Sensory, Motor, Autonomic):
   • Sensory: Loss of protective sensation → unrecognized trauma
   • Motor: Intrinsic muscle weakness → foot deformity → abnormal pressure points
   • Autonomic: Decreased sweating → dry, cracked skin → entry points for bacteria

2. PERIPHERAL ARTERIAL DISEASE (PAD):
   • Macrovascular disease → reduced blood flow
   • Impaired delivery of oxygen, nutrients, antibiotics, immune cells
   • Poor wound healing
   • Increased amputation risk

3. IMMUNOPATHY:
   • Hyperglycemia impairs neutrophil function
   • Reduced chemotaxis, phagocytosis, and bacterial killing
   • Increased susceptibility to infection
   • Biofilm formation on chronic wounds

SEQUENCE OF EVENTS:
1. Minor trauma (often unnoticed due to neuropathy)
2. Skin breakdown → ulcer formation
3. Bacterial colonization → infection
4. Spread to deep tissues, bone
5. Systemic sepsis if untreated
6. Amputation or death`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• LIFETIME RISK: 15-25% of diabetics will develop a foot ulcer
• INFECTION RATE: 50-60% of diabetic foot ulcers become infected
• AMPUTATION: 85% of diabetes-related amputations preceded by foot ulcer
• GLOBAL AMPUTATION: One leg amputated every 30 seconds due to diabetes
• MORTALITY: 5-year mortality after major amputation: 50-70%
• RECURRENCE: 40% ulcer recurrence within 1 year; 65% within 5 years
• HOSPITALIZATION: DFI is the most common diabetes-related cause of hospitalization

MICROBIOLOGY:
• MILD INFECTION: Usually monomicrobial - S. aureus, Streptococcus
• MODERATE-SEVERE: Polymicrobial - gram-positives + gram-negatives + anaerobes
• MRSA: 10-30% of S. aureus isolates (higher in some regions)
• PSEUDOMONAS: Consider if macerated wounds, prior antibiotics, water exposure
• ANAEROBES: Present in 50%+ of deep/necrotic wounds

COMMON PATHOGENS:
• Staphylococcus aureus (most common overall)
• Beta-hemolytic streptococci (Group A, B)
• Enterococcus species
• Enterobacteriaceae (E. coli, Proteus, Klebsiella)
• Pseudomonas aeruginosa
• Anaerobes (Bacteroides, Peptostreptococcus, Clostridium)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• DIABETES EPIDEMIC: 24 million diabetics in Africa (projected 55 million by 2045)
• HIGH COMPLICATION RATES: Up to 15% of African diabetics develop foot ulcers
• LATE PRESENTATION: Most present with advanced disease (Wagner 3-5)
• AMPUTATION RATES: Higher than developed countries; often primary amputation
• MORTALITY: Perioperative mortality for amputation: 10-40% in some series

CONTRIBUTING FACTORS:
• Limited access to diabetes care and foot screening
• High rate of undiagnosed diabetes
• Barefoot walking → trauma, burns, puncture wounds
• Poor footwear (flip-flops, tight shoes)
• Limited podiatry services
• Financial barriers to care
• Use of traditional medicine/herbalists first
• Limited vascular surgery services
• Delayed presentation (often septic on arrival)
• High MRSA rates in some urban centers

UNIQUE PRESENTATIONS IN AFRICA:
• Burns from walking on hot surfaces (neuropathy)
• Jigger flea (Tunga penetrans) infestations → ulceration
• Tropical ulcers superimposed on diabetic ulcers
• Traditional medicine burns/incisions
• Snake and insect bites with delayed healing

HEALTHCARE CHALLENGES:
• Limited vascular assessment (Doppler, angiography often unavailable)
• Antibiotic availability and cost
• Limited OR access for timely debridement
• Prosthetics often unavailable or unaffordable
• Lack of multidisciplinary foot teams
• Limited rehabilitation services`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
CLINICAL FEATURES OF INFECTION
═══════════════════════════════════════════════════════════════════════════════

DIAGNOSTIC CRITERIA (IWGDF/IDSA):
Infection requires ≥2 of the following:
• Local swelling or induration
• Erythema >0.5 cm around ulcer
• Local tenderness or pain
• Local warmth
• Purulent discharge

⚠️ CAUTION: Classic inflammatory signs may be ABSENT or BLUNTED due to:
• Neuropathy (no pain)
• Peripheral arterial disease (reduced inflammatory response)
• Immunopathy

═══════════════════════════════════════════════════════════════════════════════
INFECTION SEVERITY CLASSIFICATION (IWGDF/IDSA)
═══════════════════════════════════════════════════════════════════════════════

UNINFECTED:
• Wound without purulence or inflammation

MILD INFECTION:
• ≥2 signs of inflammation
• Cellulitis/erythema ≤2 cm around ulcer
• Infection limited to skin/subcutaneous tissue
• No systemic illness

MODERATE INFECTION:
• Cellulitis extending >2 cm from ulcer margin, OR
• Involvement of structures deeper than skin (abscess, tendon, joint, bone)
• No systemic inflammatory response

SEVERE INFECTION:
• Any foot infection WITH systemic inflammatory response (≥2 SIRS criteria):
  - Temperature >38°C or <36°C
  - Heart rate >90/min
  - Respiratory rate >20/min or PaCO2 <32 mmHg
  - WBC >12,000 or <4,000 or >10% bands
• May have metabolic instability (hyperglycemia, acidosis, uremia)

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
═══════════════════════════════════════════════════════════════════════════════

WOUND ASSESSMENT:
• Location (plantar, dorsal, interdigital, heel)
• Size (length × width × depth)
• Base (granulation, slough, necrotic tissue, bone)
• Edges (undermining, rolled, callused)
• Drainage (serous, purulent, sanguinous)
• Odor (foul odor suggests anaerobes/necrosis)

SURROUNDING TISSUE:
• Erythema (measure extent from wound edge)
• Swelling/induration
• Warmth (compare to contralateral limb)
• Crepitus (gas in tissues - EMERGENCY)
• Fluctuance (abscess)
• Lymphangitis (streaking)

PROBE-TO-BONE TEST:
• Insert sterile metal probe into wound
• If bone is felt (hard, gritty surface) → high probability of osteomyelitis
• Sensitivity: 66%; Specificity: 85%
• Positive predictive value: ~90% in high-risk population

NEUROLOGICAL ASSESSMENT:
• 10g monofilament test (5 sites per foot)
• 128 Hz tuning fork (vibration sense)
• Ankle reflexes
• Loss of protective sensation = high ulcer risk

VASCULAR ASSESSMENT:
• Pedal pulses (dorsalis pedis, posterior tibial)
• Capillary refill time
• Skin color, temperature, hair loss
• Ankle-Brachial Index (ABI) if available
• Toe pressure/TcPO2 if available

ABI INTERPRETATION:
• >1.30: Non-compressible (calcified vessels) - unreliable
• 0.91-1.30: Normal
• 0.70-0.90: Mild PAD
• 0.40-0.69: Moderate PAD
• <0.40: Severe PAD (critical limb ischemia)

═══════════════════════════════════════════════════════════════════════════════
SIGNS OF LIMB-THREATENING INFECTION
═══════════════════════════════════════════════════════════════════════════════

🔴 Rapidly spreading cellulitis
🔴 Gas in tissues (crepitus, X-ray)
🔴 Necrotizing fasciitis features
🔴 Wet gangrene
🔴 Compartment syndrome
🔴 Sepsis/septic shock
🔴 Extensive tissue necrosis
🔴 Deep abscess`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

STEP 1: CONFIRM INFECTION (Clinical Diagnosis)
• ≥2 inflammatory signs (see clinical presentation)
• Wound colonization ≠ infection; do not treat colonization

STEP 2: ASSESS SEVERITY
• Classify as mild, moderate, or severe
• Determine if limb-threatening or life-threatening

STEP 3: EVALUATE FOR OSTEOMYELITIS
• Probe-to-bone test
• Imaging (X-ray, MRI)
• ESR, CRP

STEP 4: ASSESS VASCULAR STATUS
• Pulses, ABI
• Vascular consultation if PAD present

STEP 5: OBTAIN CULTURES
• Deep tissue samples preferred
• Before antibiotics if possible

STEP 6: ASSESS GLYCEMIC CONTROL
• Blood glucose, HbA1c

═══════════════════════════════════════════════════════════════════════════════
DIAGNOSING OSTEOMYELITIS
═══════════════════════════════════════════════════════════════════════════════

CLINICAL SUSPICION:
• Large ulcer (>2 cm²)
• Deep ulcer (probe-to-bone positive)
• Ulcer over bony prominence
• Ulcer duration >2 weeks
• ESR >70 mm/hr
• Visible bone in wound
• "Sausage toe" (diffusely swollen digit)

IMAGING:
PLAIN X-RAY (First-line):
• Bone erosion, periosteal reaction, cortical destruction
• Sensitivity: 55%; Specificity: 75%
• Changes lag 10-21 days behind infection
• May show gas in soft tissues

MRI (Gold Standard Imaging):
• Bone marrow edema (low T1, high T2/STIR)
• Sensitivity: 90%; Specificity: 80%
• Can show extent of infection
• Distinguishes from Charcot neuroarthropathy

BONE BIOPSY (Definitive Diagnosis):
• Histopathology + culture
• Gold standard for diagnosis and pathogen identification
• Consider when: diagnosis uncertain, prior treatment failure, unusual pathogens suspected

LABORATORY MARKERS:
• ESR: Elevated (>70 mm/hr highly suggestive)
• CRP: Elevated; useful for monitoring response
• WBC: May be normal in chronic osteomyelitis
• Procalcitonin: May help differentiate soft tissue vs bone infection

═══════════════════════════════════════════════════════════════════════════════
WOUND CULTURES
═══════════════════════════════════════════════════════════════════════════════

WHEN TO CULTURE:
• All moderate and severe infections
• Mild infection failing empiric therapy
• NOT routinely for mild infection responding to empirics

PROPER TECHNIQUE:
• Cleanse wound first (remove debris, slough)
• Avoid superficial swabs (colonizers only)
• PREFERRED: Curettage of ulcer base, deep tissue sample, or bone biopsy
• Aspiration of purulent collections

INTERPRETING RESULTS:
• Correlate with clinical presentation
• Distinguish colonization from infection
• Monomicrobial in mild; polymicrobial in severe
• Request sensitivities to guide targeted therapy`,

    differentialDiagnosis: [
      'Charcot Neuroarthropathy (Acute) - Hot, swollen foot; may mimic cellulitis; NO ulcer initially; X-ray shows bone/joint destruction; MRI differentiates',
      'Gout/Pseudogout - Acute monoarthritis; often 1st MTP; joint aspiration shows crystals; uric acid elevated in gout',
      'Cellulitis (Non-diabetic) - Spreading erythema without ulcer; may have portal of entry elsewhere; responds to antibiotics',
      'Deep Vein Thrombosis - Unilateral leg swelling, warmth; calf tenderness; Doppler ultrasound diagnostic',
      'Venous Stasis Ulcer - Medial malleolus location; edema, hemosiderin staining; shallow, irregular; less infection risk',
      'Arterial Ulcer - Distal/lateral location; punched-out, deep; very painful; absent pulses; ABI <0.5',
      'Necrotizing Fasciitis - Rapidly progressive; pain out of proportion; crepitus; systemic toxicity; surgical emergency',
      'Pyomyositis - Deep muscle abscess; may occur in diabetics; MRI shows intramuscular collection',
      'Osteomyelitis (Non-diabetic) - Hematogenous spread; different location; no neuropathy/PAD context',
      'Soft Tissue Gas Gangrene - Clostridial infection; rapid progression; crepitus; extreme toxicity; emergent debridement',
      'Squamous Cell Carcinoma - Chronic non-healing ulcer; raised edges; biopsy diagnostic (Marjolin ulcer)',
      'Vasculitis - Multiple ulcers; systemic symptoms; abnormal inflammatory markers; biopsy shows vessel inflammation'
    ],

    investigations: [
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - Leukocytosis suggests infection; left shift indicates severity; anemia common in chronic disease',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose (Random/Fasting)',
        indication: '🔴 IMMEDIATE - Assess glycemic control; hyperglycemia impairs healing and immune function',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: '🟠 URGENT - Long-term glycemic control; target <7% for healing; guides diabetes management',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel (Electrolytes, BUN, Creatinine)',
        indication: '🔴 IMMEDIATE - Assess renal function (affects antibiotic dosing); electrolyte imbalances; dehydration',
        availability: 'BASIC'
      },
      {
        name: 'Erythrocyte Sedimentation Rate (ESR)',
        indication: '🟠 URGENT - Elevated in infection; >70 mm/hr highly suggestive of osteomyelitis; monitor treatment response',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🟠 URGENT - Acute phase reactant; elevated in infection; more responsive than ESR for monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Procalcitonin',
        indication: '🟡 If available - May help differentiate soft tissue vs bone infection; elevated in severe/systemic infection',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Wound Culture (Deep Tissue)',
        indication: '🟠 URGENT - All moderate/severe infections; curettage or deep tissue preferred over superficial swab',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures',
        indication: '🔴 IMMEDIATE - For severe infection, sepsis, or systemic symptoms; before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Plain X-ray (Foot)',
        indication: '🔴 IMMEDIATE - First-line imaging; look for osteomyelitis, gas, foreign body; baseline for comparison',
        availability: 'BASIC'
      },
      {
        name: 'MRI Foot',
        indication: '🟠 URGENT - Gold standard for osteomyelitis; soft tissue extent; differentiates from Charcot',
        availability: 'ADVANCED'
      },
      {
        name: 'Ankle-Brachial Index (ABI)',
        indication: '🟠 URGENT - Assess peripheral arterial disease; <0.9 indicates PAD; guides vascular referral',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Doppler Ultrasound',
        indication: '🟠 URGENT - Non-invasive vascular assessment; when pulses not palpable',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Angiography',
        indication: '🟡 If revascularization considered - Defines arterial anatomy; planning for intervention',
        availability: 'ADVANCED'
      },
      {
        name: 'Bone Biopsy with Culture',
        indication: '🟡 Gold standard for osteomyelitis - When diagnosis uncertain or prior treatment failure',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lipid Profile',
        indication: '🟡 Routine - Cardiovascular risk assessment; part of comprehensive diabetes care',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🟡 Routine - Screen for diabetic nephropathy; UTI as occult infection source',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY - Classify as mild, moderate, or severe; identify limb-threatening features',
        '⚡ ABC IF SEPTIC - Airway, breathing, circulation; resuscitate if severe sepsis/shock',
        '⚡ IV ACCESS - For antibiotics and fluids if moderate-severe infection',
        '⚡ BLOOD GLUCOSE CONTROL - Check glucose; start insulin if significantly elevated',
        '⚡ OBTAIN CULTURES - Deep tissue sample before antibiotics (do not delay antibiotics for culture)',
        '⚡ START EMPIRIC ANTIBIOTICS - Based on severity and local resistance patterns',
        '⚡ WOUND ASSESSMENT - Document size, depth, tissue type, probe-to-bone',
        '⚡ VASCULAR ASSESSMENT - Check pulses, ABI; urgent vascular consult if critical ischemia',
        '⚡ X-RAY - Evaluate for osteomyelitis, gas, foreign body',
        '⚡ SURGICAL CONSULTATION - Urgent for abscess, necrosis, deep infection, gas in tissues',
        '⚡ OFFLOADING - Non-weight bearing for severe; appropriate device for others',
        '⚡ WOUND CARE - Initial debridement of necrotic tissue; appropriate dressings',
        '⚡ TETANUS STATUS - Update if needed',
        '⚡ VTE PROPHYLAXIS - If hospitalized and immobile',
        '⚡ DIABETOLOGY INVOLVEMENT - For glycemic optimization'
      ],
      pharmacological: [
        {
          drug: 'Amoxicillin-Clavulanate',
          indication: '🟢 MILD INFECTION (Outpatient) - Covers gram-positives and some gram-negatives/anaerobes',
          dose: '875/125 mg PO every 12 hours',
          route: 'Oral',
          duration: '1-2 weeks for soft tissue; 4-6 weeks if osteomyelitis',
          contraindications: ['Penicillin allergy', 'Severe hepatic impairment'],
          sideEffects: ['Diarrhea', 'Nausea', 'Rash', 'Hepatotoxicity (rare)'],
          alternatives: ['Clindamycin + Ciprofloxacin', 'TMP-SMX + Metronidazole']
        },
        {
          drug: 'Clindamycin',
          indication: '🟢 MILD INFECTION (Penicillin Allergy) - Good gram-positive and anaerobic coverage',
          dose: '300-450 mg PO every 6-8 hours',
          route: 'Oral',
          duration: '1-2 weeks for soft tissue',
          contraindications: ['History of C. difficile colitis', 'Hypersensitivity'],
          sideEffects: ['Diarrhea', 'C. difficile colitis', 'Rash', 'Metallic taste'],
          alternatives: ['Amoxicillin-clavulanate', 'TMP-SMX (if MRSA suspected)']
        },
        {
          drug: 'Trimethoprim-Sulfamethoxazole (TMP-SMX)',
          indication: '🟢 MILD INFECTION with MRSA risk - Excellent MRSA coverage; add metronidazole for anaerobes',
          dose: 'DS (160/800 mg) 1-2 tablets PO every 12 hours',
          route: 'Oral',
          duration: '1-2 weeks for soft tissue',
          contraindications: ['Sulfa allergy', 'Severe renal impairment', 'G6PD deficiency', 'Pregnancy (3rd trimester)'],
          sideEffects: ['Rash', 'GI upset', 'Hyperkalemia', 'Bone marrow suppression', 'Stevens-Johnson (rare)'],
          alternatives: ['Doxycycline', 'Linezolid']
        },
        {
          drug: 'Ampicillin-Sulbactam',
          indication: '🟠 MODERATE INFECTION (IV) - Broad coverage; gram-positives, gram-negatives, anaerobes',
          dose: '3 g (2g ampicillin/1g sulbactam) IV every 6 hours',
          route: 'IV',
          duration: '2-4 weeks for soft tissue; 4-6 weeks if osteomyelitis',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Injection site reaction', 'Seizures (high dose/renal impairment)'],
          alternatives: ['Piperacillin-tazobactam', 'Ertapenem', 'Ceftriaxone + Metronidazole']
        },
        {
          drug: 'Piperacillin-Tazobactam',
          indication: '🔴 SEVERE INFECTION - Broad-spectrum; Pseudomonas coverage; gram-positives, gram-negatives, anaerobes',
          dose: '4.5 g IV every 6-8 hours (adjust for renal function)',
          route: 'IV',
          duration: '2-4 weeks for soft tissue; 4-6 weeks if osteomyelitis',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Thrombocytopenia', 'Seizures', 'C. difficile'],
          alternatives: ['Meropenem', 'Imipenem-cilastatin', 'Cefepime + Metronidazole']
        },
        {
          drug: 'Vancomycin',
          indication: '🔴 ADD FOR MRSA COVERAGE - For severe infection or MRSA risk factors',
          dose: '15-20 mg/kg IV every 8-12 hours (target trough 15-20 mcg/mL for severe)',
          route: 'IV',
          duration: 'Based on infection severity and response',
          contraindications: ['Hypersensitivity (rare)'],
          sideEffects: ['Nephrotoxicity', 'Red man syndrome (infuse slowly)', 'Ototoxicity', 'Thrombocytopenia'],
          alternatives: ['Linezolid', 'Daptomycin', 'Telavancin']
        },
        {
          drug: 'Linezolid',
          indication: '🟠 MRSA ALTERNATIVE - Oral option; good bone penetration; for MRSA soft tissue or osteomyelitis',
          dose: '600 mg PO/IV every 12 hours',
          route: 'PO or IV',
          duration: 'Limit to 2-4 weeks if possible (toxicity with prolonged use)',
          contraindications: ['MAO inhibitors', 'Uncontrolled hypertension', 'Carcinoid syndrome'],
          sideEffects: ['Thrombocytopenia', 'Peripheral neuropathy', 'Optic neuritis', 'Serotonin syndrome', 'Lactic acidosis'],
          alternatives: ['Vancomycin', 'Daptomycin', 'TMP-SMX']
        },
        {
          drug: 'Ertapenem',
          indication: '🟠 MODERATE-SEVERE (Once Daily) - Broad-spectrum carbapenem; no Pseudomonas coverage',
          dose: '1 g IV once daily',
          route: 'IV',
          duration: '2-4 weeks for soft tissue',
          contraindications: ['Severe beta-lactam allergy', 'History of seizures with carbapenems'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache', 'Seizures (rare)', 'C. difficile'],
          alternatives: ['Meropenem (if Pseudomonas suspected)', 'Piperacillin-tazobactam']
        },
        {
          drug: 'Metronidazole',
          indication: '🟠 ANAEROBIC COVERAGE - Add to regimens lacking anaerobic coverage for deep/necrotic wounds',
          dose: '500 mg PO/IV every 8 hours',
          route: 'PO or IV',
          duration: 'Duration of main antibiotic course',
          contraindications: ['First trimester pregnancy', 'Disulfiram use', 'Alcohol consumption'],
          sideEffects: ['Metallic taste', 'Nausea', 'Peripheral neuropathy (prolonged use)', 'Disulfiram reaction with alcohol'],
          alternatives: ['Clindamycin (also covers anaerobes)']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟠 GRAM-NEGATIVE COVERAGE - Add if Pseudomonas suspected; often combined with gram-positive agent',
          dose: '500-750 mg PO every 12 hours OR 400 mg IV every 12 hours',
          route: 'PO or IV',
          duration: 'Based on infection course',
          contraindications: ['Tendon disorders', 'QT prolongation', 'Myasthenia gravis', 'Children (relative)'],
          sideEffects: ['Tendinopathy/tendon rupture', 'QT prolongation', 'C. difficile', 'Nausea', 'CNS effects'],
          alternatives: ['Levofloxacin', 'Ceftazidime', 'Aztreonam']
        },
        {
          drug: 'Insulin (Regular or Rapid-Acting)',
          indication: '🔴 GLYCEMIC CONTROL - Essential; hyperglycemia impairs healing and immune function',
          dose: 'Individualized; may need sliding scale or infusion for severe hyperglycemia',
          route: 'IV (if severe) or SC',
          duration: 'Throughout hospitalization; transition to outpatient regimen',
          contraindications: ['Hypoglycemia'],
          sideEffects: ['Hypoglycemia', 'Weight gain', 'Injection site reactions'],
          alternatives: ['Continue home oral agents if mild and eating (except metformin if renal impairment)']
        }
      ],
      nonPharmacological: [
        'WOUND DEBRIDEMENT: Sharp debridement of necrotic tissue, slough, callus; essential for healing and infection control',
        'WOUND DRESSINGS: Based on wound characteristics - moist wound healing principle; avoid dry dressings',
        'OFFLOADING: Critical for plantar ulcers; total contact cast (gold standard), removable cast walker, therapeutic footwear',
        'EDEMA MANAGEMENT: Elevation, compression (if no significant PAD)',
        'GLYCEMIC CONTROL: Target blood glucose 140-180 mg/dL inpatient; HbA1c <7% outpatient',
        'NUTRITION: Adequate protein and calories for healing; consider nutritional supplements; address deficiencies',
        'SMOKING CESSATION: Strongly encourage; impairs healing and increases amputation risk',
        'NEGATIVE PRESSURE WOUND THERAPY (NPWT/VAC): For large wounds post-debridement; promotes granulation',
        'HYPERBARIC OXYGEN THERAPY: Consider for refractory wounds (limited availability in Africa)',
        'BIOENGINEERED SKIN SUBSTITUTES: For non-healing wounds after adequate debridement (limited availability)'
      ],
      surgical: [
        'INCISION AND DRAINAGE: For abscess collections; send tissue for culture',
        'SURGICAL DEBRIDEMENT: Removal of all necrotic and infected tissue; may require multiple procedures',
        'BONE RESECTION: For osteomyelitis; remove infected bone to healthy bleeding margins',
        'RAY AMPUTATION: Removal of toe and part of metatarsal; preserves foot function',
        'TRANSMETATARSAL AMPUTATION (TMA): Amputation through metatarsals; maintains ambulation without prosthesis',
        'BELOW-KNEE AMPUTATION (BKA): When forefoot not salvageable; better for prosthetic fitting than AKA',
        'ABOVE-KNEE AMPUTATION (AKA): When BKA not possible (ischemia, infection extent); higher mobility loss',
        'REVASCULARIZATION: Angioplasty or bypass if significant PAD; consider before/with amputation',
        'SKIN GRAFTING: For large wounds after granulation tissue forms',
        'FLAP COVERAGE: For complex wounds with exposed bone/tendon',
        'ACHILLES TENDON LENGTHENING: For forefoot offloading in selected cases'
      ],
      referralCriteria: [
        '🔴 EMERGENCY SURGERY: Necrotizing fasciitis, gas gangrene, compartment syndrome, wet gangrene with sepsis',
        '🔴 URGENT SURGERY: Deep abscess, extensive necrosis, severe infection not responding to antibiotics',
        '🟠 VASCULAR SURGERY: Critical limb ischemia (ABI <0.4, rest pain, gangrene); for revascularization assessment',
        '🟠 ORTHOPEDIC SURGERY: Osteomyelitis requiring bone resection; Charcot neuroarthropathy',
        '🟠 PLASTIC SURGERY: Complex wound coverage; flap reconstruction',
        '🟠 INFECTIOUS DISEASE: Treatment failure; unusual pathogens; guidance on prolonged antibiotic therapy',
        '🟠 ENDOCRINOLOGY: Uncontrolled diabetes; comprehensive diabetes management',
        '🟠 PODIATRY: Offloading devices; preventive foot care; callus management',
        '🟠 WOUND CARE CENTER: Non-healing wounds; advanced wound therapies',
        '🟠 PROSTHETICS/REHABILITATION: Post-amputation fitting and mobility training'
      ],
      monitoring: [
        'WOUND ASSESSMENT: Daily inpatient; document size, depth, tissue type, drainage',
        'VITAL SIGNS: Every 4-8 hours; watch for sepsis deterioration',
        'BLOOD GLUCOSE: Multiple times daily; maintain 140-180 mg/dL inpatient',
        'CBC: Every 2-3 days initially; monitor WBC trend',
        'RENAL FUNCTION: Baseline and periodically; antibiotic dosing adjustments',
        'INFLAMMATORY MARKERS: ESR/CRP weekly to monitor response (especially osteomyelitis)',
        'ANTIBIOTIC LEVELS: Vancomycin trough monitoring',
        'WOUND CULTURES: Repeat if not responding or new signs of infection',
        'IMAGING: Repeat X-ray in 2-4 weeks if osteomyelitis; MRI if treatment failure',
        'VASCULAR STATUS: Reassess if wound not healing'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
INPATIENT MANAGEMENT
═══════════════════════════════════════════════════════════════════════════════

SEVERE INFECTION:
• IV antibiotics; daily wound assessment
• Surgical debridement as needed
• Glucose control (target 140-180 mg/dL)
• Transition to oral when:
  - Clinically improving
  - Afebrile for 48-72 hours
  - Tolerating oral intake
  - Adequate oral options available

MODERATE INFECTION:
• IV antibiotics initially; can often transition to oral in 2-5 days
• May be managed as outpatient if reliable follow-up

═══════════════════════════════════════════════════════════════════════════════
ANTIBIOTIC DURATION
═══════════════════════════════════════════════════════════════════════════════

SOFT TISSUE INFECTION (No Osteomyelitis):
• Mild: 1-2 weeks
• Moderate: 2-3 weeks
• Severe: 2-4 weeks

OSTEOMYELITIS:
• After complete resection of infected bone: 2-5 days
• After partial resection or no surgery: 4-6 weeks
• Some advocate longer courses (8-12 weeks) for residual infected bone

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

FIRST WEEK:
• Wound check every 2-3 days
• Ensure antibiotic compliance
• Monitor for worsening

WEEKS 2-4:
• Weekly wound assessment
• Assess healing trajectory
• Adjust offloading as needed

MONTHLY (Until Healed):
• Wound measurements
• Reassess glycemic control
• Address barriers to healing

AFTER HEALING:
• Lifelong preventive care
• Appropriate footwear always
• Regular foot examinations (every 1-3 months based on risk)
• Annual comprehensive foot exam
• Patient education reinforcement

═══════════════════════════════════════════════════════════════════════════════
MARKERS OF HEALING
═══════════════════════════════════════════════════════════════════════════════

• Decreasing wound size (measure weekly)
• Healthy granulation tissue
• Decreasing drainage
• Resolution of surrounding cellulitis
• Normalizing inflammatory markers
• Epithelialization from wound edges

IF NOT HEALING (>4 weeks without progress):
• Reassess for infection, osteomyelitis
• Evaluate vascular status
• Review offloading compliance
• Consider advanced therapies
• Biopsy to rule out malignancy

⚠️ Individual follow-up plans should be determined by the treating team.`
    },

    redFlags: [
      '🔴 CRITICAL: Gas in tissues (crepitus or X-ray) - Necrotizing infection/gas gangrene - EMERGENCY SURGERY',
      '🔴 CRITICAL: Rapidly spreading cellulitis - May indicate necrotizing fasciitis - URGENT SURGICAL CONSULT',
      '🔴 CRITICAL: Sepsis/septic shock (fever, hypotension, altered mental status) - Life-threatening - ICU care',
      '🔴 CRITICAL: Wet gangrene - Infected necrotic tissue - URGENT DEBRIDEMENT/AMPUTATION',
      '🔴 CRITICAL: Compartment syndrome (tense swelling, severe pain, pain with passive stretch) - EMERGENCY FASCIOTOMY',
      '🟠 URGENT: Large/deep abscess on imaging - Requires surgical drainage',
      '🟠 URGENT: Exposed bone or tendon - High risk for osteomyelitis; needs coverage',
      '🟠 URGENT: Critical limb ischemia (ABI <0.4, rest pain) - Revascularization needed for healing',
      '🟠 URGENT: Failure to improve after 48-72 hours of appropriate antibiotics',
      '🟠 URGENT: Rising WBC or inflammatory markers despite treatment',
      '🟠 URGENT: New areas of necrosis developing - Inadequate debridement or vascular compromise',
      '🟠 URGENT: Uncontrolled hyperglycemia (glucose >300 mg/dL) - Impairs healing and immunity'
    ],

    complications: [
      'OSTEOMYELITIS: Bone infection; present in 50-60% of severe DFI; requires prolonged antibiotics ± bone resection',
      'SEPSIS/SEPTIC SHOCK: Systemic infection; high mortality; requires ICU care',
      'NECROTIZING FASCIITIS: Rapidly progressive soft tissue necrosis; surgical emergency; high mortality',
      'GAS GANGRENE: Clostridial infection; crepitus, toxicity; requires emergent debridement',
      'AMPUTATION: Toe, ray, transmetatarsal, below-knee, or above-knee depending on extent',
      'CHARCOT NEUROARTHROPATHY: Bone/joint destruction; may be triggered by infection; requires offloading',
      'CHRONIC WOUND: Non-healing ulcer; significant morbidity; limb loss risk',
      'RECURRENT INFECTION: High recurrence rate; often same location',
      'CONTRALATERAL LIMB INVOLVEMENT: 50% develop ulcer in other foot within 5 years',
      'DEATH: Mortality up to 50-70% within 5 years of major amputation',
      'DISABILITY: Loss of mobility, independence, employment',
      'DEPRESSION: Common after amputation; requires screening and support',
      'ANTIBIOTIC-RELATED: C. difficile colitis, drug reactions, resistance development'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

HEALING RATES:
• Mild infection: >90% heal with appropriate treatment
• Moderate infection: 60-80% heal; 15-20% require amputation
• Severe infection: Variable; significant amputation and mortality risk

AMPUTATION RATES:
• Overall in DFI: 15-20%
• With osteomyelitis: 30-40%
• With critical limb ischemia: >50%

MORTALITY:
• In-hospital mortality for severe DFI: 5-10%
• 1-year mortality after major amputation: 40%
• 5-year mortality after major amputation: 50-70%

RECURRENCE:
• Ulcer recurrence: 40% at 1 year; 65% at 5 years
• Recurrent infection common if underlying issues not addressed

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Early presentation
• Mild infection
• No PAD or mild PAD
• Good glycemic control
• Compliance with offloading
• Access to multidisciplinary care
• No osteomyelitis
• Adequate nutrition

UNFAVORABLE:
• Late presentation
• Severe infection
• Critical limb ischemia
• Osteomyelitis
• Renal impairment/dialysis
• Poor glycemic control (HbA1c >9%)
• Large/deep ulcer
• Multiple comorbidities
• Limited healthcare access
• Poor social support/adherence

═══════════════════════════════════════════════════════════════════════════════
AFTER AMPUTATION
═══════════════════════════════════════════════════════════════════════════════

• Minor amputation (toe/ray): Usually maintains ambulation; 30% need higher amputation later
• Transmetatarsal: Can ambulate with modified shoe; 30-50% heal primarily
• Below-knee: Can use prosthesis; 60-70% achieve functional ambulation
• Above-knee: More difficult rehabilitation; <50% achieve community ambulation

⚠️ Prevention of contralateral limb loss is priority after any amputation.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

GLYCEMIC CONTROL:
• Target HbA1c <7% (individualized)
• Reduces neuropathy progression
• Improves immune function

FOOT CARE EDUCATION:
• Daily foot inspection (use mirror for soles)
• Wash feet daily; dry between toes
• Moisturize (not between toes)
• Never walk barefoot
• Check shoes for foreign objects before wearing
• Proper nail care (cut straight across)
• Avoid heat exposure (hot water, heating pads)
• Do not use sharp instruments on feet
• Report any injury or change immediately

APPROPRIATE FOOTWEAR:
• Properly fitted shoes (measure feet when buying)
• Avoid pointed toes, high heels, flip-flops
• Therapeutic footwear if neuropathy present
• Inspect shoes daily for wear and foreign objects
• Change socks daily; avoid seams that irritate

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Early Detection)
═══════════════════════════════════════════════════════════════════════════════

ROUTINE SCREENING:
• Annual comprehensive foot exam for all diabetics
• More frequent (every 1-3 months) if high risk

RISK STRATIFICATION:
• Low risk: Normal sensation, palpable pulses → Annual exam
• Moderate risk: Neuropathy OR PAD → Exam every 3-6 months
• High risk: Neuropathy + PAD, prior ulcer/amputation → Exam every 1-3 months

SCREENING COMPONENTS:
• Visual inspection (deformities, calluses, skin changes)
• Neurological (10g monofilament, 128 Hz tuning fork)
• Vascular (pulses, ABI if pulses diminished)
• Footwear assessment

═══════════════════════════════════════════════════════════════════════════════
TERTIARY PREVENTION (Prevent Recurrence)
═══════════════════════════════════════════════════════════════════════════════

AFTER ULCER HEALS:
• Lifelong vigilance
• Continued offloading with therapeutic footwear
• Regular podiatry care
• Aggressive callus management
• Continued glucose optimization
• Smoking cessation
• Address modifiable risk factors

SPECIAL POPULATIONS:
• Post-amputation: Protect remaining limb; contralateral foot at very high risk
• Dialysis patients: Highest risk; extra vigilance needed

═══════════════════════════════════════════════════════════════════════════════
COMMUNITY/HEALTH SYSTEM STRATEGIES
═══════════════════════════════════════════════════════════════════════════════

• Integrate foot care into routine diabetes clinics
• Train community health workers in foot screening
• Establish multidisciplinary diabetic foot teams
• Ensure availability of appropriate footwear
• Create referral pathways for high-risk feet
• Public education campaigns on foot care`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING DIABETIC FOOT INFECTION
═══════════════════════════════════════════════════════════════════════════════

If you have diabetes, you are at risk for foot problems. High blood sugar over time can damage the nerves in your feet (so you may not feel injuries) and reduce blood flow (so wounds heal slowly). Small cuts, blisters, or sores can become seriously infected.

Foot infections in diabetics can be very serious and may lead to:
• Hospitalization
• Surgery
• Amputation (loss of toe, foot, or leg)
• Life-threatening illness

The good news: Most diabetic foot problems CAN BE PREVENTED with proper care.

═══════════════════════════════════════════════════════════════════════════════
DANGER SIGNS - SEEK CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Go to the hospital or see your doctor RIGHT AWAY if you notice:

🔴 A sore, cut, or blister on your foot that is not healing
🔴 Redness spreading around a wound
🔴 Swelling of the foot
🔴 Warmth in the foot (feels hot)
🔴 Pus or bad-smelling drainage from a wound
🔴 Fever or chills
🔴 Black skin (gangrene) on any part of the foot
🔴 Pain in your foot (if you can feel it)
🔴 Feeling generally unwell with a foot wound

DO NOT wait to see if it gets better on its own. Early treatment saves limbs and lives.

═══════════════════════════════════════════════════════════════════════════════
DAILY FOOT CARE ROUTINE
═══════════════════════════════════════════════════════════════════════════════

INSPECT YOUR FEET EVERY DAY:
• Look at the top, bottom, sides, and between toes
• Use a mirror to see the bottom of your feet
• Look for cuts, blisters, redness, swelling, calluses, or color changes
• If you cannot see well, ask a family member to help

WASH YOUR FEET EVERY DAY:
• Use lukewarm water (test with elbow, not feet)
• Use mild soap
• Do NOT soak your feet (softens skin too much)
• Dry thoroughly, especially between toes

MOISTURIZE:
• Apply lotion to tops and bottoms of feet
• Do NOT put lotion between toes (causes fungal infections)

NAIL CARE:
• Cut toenails straight across
• File edges gently
• Do not cut too short
• If you cannot see well or reach, have someone else help or see a foot specialist

═══════════════════════════════════════════════════════════════════════════════
PROTECT YOUR FEET
═══════════════════════════════════════════════════════════════════════════════

NEVER WALK BAREFOOT:
• Always wear shoes or slippers, even at home
• Hot sand, pavement, and sharp objects can injure feet without you feeling it

CHOOSE PROPER FOOTWEAR:
• Shoes should fit well (not too tight or loose)
• Break in new shoes slowly
• Avoid pointed toes and high heels
• Wear clean, dry socks without tight elastic
• Check inside shoes for stones or rough areas before wearing

AVOID INJURY:
• Do not use heating pads or hot water bottles on feet
• Test bath water temperature with your hand or elbow
• Do not use sharp tools on your feet
• Do not use chemical corn/callus removers
• Do not try to remove calluses yourself

═══════════════════════════════════════════════════════════════════════════════
CONTROL YOUR DIABETES
═══════════════════════════════════════════════════════════════════════════════

• Take your diabetes medications as prescribed
• Check your blood sugar regularly
• Eat a healthy diet
• Exercise as recommended by your doctor
• Keep appointments with your healthcare team
• Stop smoking (smoking greatly increases foot problems)

═══════════════════════════════════════════════════════════════════════════════
IF YOU HAVE A FOOT WOUND
═══════════════════════════════════════════════════════════════════════════════

• See your doctor or nurse immediately
• Keep weight off the wound (use crutches or wheelchair if needed)
• Keep the wound clean and covered
• Take all antibiotics exactly as prescribed
• Do not miss follow-up appointments
• Check the wound daily for signs of worsening

═══════════════════════════════════════════════════════════════════════════════
REGULAR CHECK-UPS
═══════════════════════════════════════════════════════════════════════════════

• Have your feet examined at every diabetes visit
• Have a complete foot exam at least once a year
• See a foot specialist (podiatrist) if available
• Report any foot concerns immediately

Your feet are precious - take care of them every day!`,

    references: [
      'Lipsky BA, Berendt AR, Cornia PB, et al. 2012 Infectious Diseases Society of America clinical practice guideline for the diagnosis and treatment of diabetic foot infections. Clin Infect Dis. 2012;54(12):e132-e173.',
      'International Working Group on the Diabetic Foot. IWGDF Guidelines on the diagnosis and treatment of foot infection in persons with diabetes. 2023.',
      'Lipsky BA, Senneville É, Abbas ZG, et al. Guidelines on the diagnosis and treatment of foot infection in persons with diabetes (IWGDF 2019 update). Diabetes Metab Res Rev. 2020;36(S1):e3280.',
      'Lavery LA, Armstrong DG, Wunderlich RP, et al. Risk factors for foot infections in individuals with diabetes. Diabetes Care. 2006;29(6):1288-1293.',
      'Abbas ZG, Archibald LK. Epidemiology of the diabetic foot in Africa. Med Sci Monit. 2005;11(8):RA262-270. [African data]',
      'Ogbera AO, Chinenye S, Onyekwere A, Fasanmade O. Prognostic indices of diabetes mortality. Ethn Dis. 2007;17(4):721-725. [Nigerian data]',
      'Armstrong DG, Boulton AJM, Bus SA. Diabetic foot ulcers and their recurrence. N Engl J Med. 2017;376(24):2367-2375.',
      'Mills JL, Conte MS, Armstrong DG, et al. The Society for Vascular Surgery Lower Extremity Threatened Limb Classification System: risk stratification based on wound, ischemia, and foot infection (WIfI). J Vasc Surg. 2014;59(1):220-234.',
      'Hingorani A, LaMuraglia GM, Henke P, et al. The management of diabetic foot: A clinical practice guideline by the Society for Vascular Surgery. J Vasc Surg. 2016;63(2 Suppl):3S-21S.',
      'Game FL, Attinger C, Hartemann A, et al. IWGDF guidance on use of interventions to enhance the healing of chronic ulcers of the foot in diabetes. Diabetes Metab Res Rev. 2016;32(Suppl 1):75-83.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 27. ACUTE CHOLECYSTITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '27',
    name: 'Acute Cholecystitis',
    slug: 'acute-cholecystitis',
    specialty: 'Emergency Medicine',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K81.0',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Acute cholecystitis is inflammation of the gallbladder, most commonly caused by obstruction of the cystic duct by gallstones (calculous cholecystitis, 90-95%). It is one of the most common surgical emergencies worldwide. Early recognition and appropriate management, including timely cholecystectomy, are essential to prevent complications such as gangrene, perforation, and sepsis.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• MURPHY'S SIGN: Inspiratory arrest during RUQ palpation - classic but not pathognomonic
• GALLSTONES: Present in 90-95% of acute cholecystitis (calculous); 5-10% are acalculous
• ULTRASOUND: First-line imaging - gallstones + wall thickening (>3mm) + pericholecystic fluid + sonographic Murphy's
• EARLY CHOLECYSTECTOMY: Within 72 hours preferred; safe and reduces total hospital stay
• TOKYO GUIDELINES: Grade severity (I, II, III) to guide management approach
• ACALCULOUS CHOLECYSTITIS: Critically ill patients, ICU, trauma; high morbidity/mortality
• COMPLICATIONS: Gangrenous cholecystitis, perforation, cholecystoenteric fistula, gallstone ileus
• MIRIZZI SYNDROME: Stone in cystic duct compressing common hepatic duct → jaundice
• EMPHYSEMATOUS CHOLECYSTITIS: Gas in gallbladder wall; diabetics; emergency surgery
• ANTIBIOTICS: Cover enteric gram-negatives and anaerobes; not curative alone
• PERCUTANEOUS CHOLECYSTOSTOMY: Temporizing option for high-risk patients unfit for surgery
• BILIARY COLIC vs CHOLECYSTITIS: Colic = transient (<6h); Cholecystitis = persistent + inflammation
• CHOLEDOCHOLITHIASIS: CBD stones; suspect if elevated bilirubin/ALP, dilated CBD
• CHOLANGITIS: Fever + jaundice + RUQ pain (Charcot triad); ERCP urgently needed

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

CALCULOUS CHOLECYSTITIS (90-95%):
1. Gallstone impacts in cystic duct
2. Gallbladder distension → increased intraluminal pressure
3. Venous and lymphatic congestion → wall edema
4. Mucosal ischemia and inflammation
5. Bacterial invasion (secondary infection in 50-75%)
6. If untreated → gangrene → perforation → peritonitis

ACALCULOUS CHOLECYSTITIS (5-10%):
• Occurs WITHOUT gallstones
• Risk factors: Critical illness, major surgery, trauma, burns, TPN, prolonged fasting
• Pathogenesis: Bile stasis + gallbladder ischemia + direct mucosal injury
• Higher complication rate (gangrene, perforation)
• Higher mortality than calculous type

MICROBIOLOGY (when infected):
• Escherichia coli (most common, 40-60%)
• Klebsiella species (15-20%)
• Enterococcus species (10-15%)
• Enterobacter species
• Bacteroides fragilis and other anaerobes
• Clostridium species (emphysematous cholecystitis)`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• GALLSTONES: Affect 10-15% of adults in Western countries
• CHOLECYSTITIS: 1-3% of patients with gallstones develop acute cholecystitis per year
• CHOLECYSTECTOMY: One of the most common surgical procedures worldwide
• GENDER: Female:Male ratio 2-3:1 for gallstones
• AGE: Incidence increases with age; peak 40-60 years
• MORTALITY: <1% for uncomplicated; 5-10% for complicated; up to 30% for acalculous

RISK FACTORS FOR GALLSTONES ("5 F's" mnemonic):
• Female
• Forty (age >40)
• Fertile (multiparous)
• Fat (obesity, metabolic syndrome)
• Family history

ADDITIONAL RISK FACTORS:
• Rapid weight loss
• Pregnancy
• Oral contraceptives, hormone replacement
• Diabetes mellitus
• Cirrhosis
• Crohn disease (ileal disease)
• Hemolytic disorders (pigment stones)
• TPN (total parenteral nutrition)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HISTORICALLY LOWER PREVALENCE: Gallstones less common in sub-Saharan Africa than Western countries
• CHANGING EPIDEMIOLOGY: Increasing with urbanization, dietary changes, obesity epidemic
• PIGMENT STONES: More common in areas with hemolytic disorders (sickle cell disease, malaria)
• PRESENTATION: Often late presentation with complications
• DIAGNOSTIC CHALLENGES: Ultrasound availability varies; CT often limited
• SURGICAL ACCESS: Limited laparoscopic surgery in many areas; higher open cholecystectomy rates

UNIQUE CONSIDERATIONS IN AFRICA:
• SICKLE CELL DISEASE: High prevalence of pigment gallstones
• HIV/AIDS: Acalculous cholecystitis; CMV, Cryptosporidium cholangiopathy
• ASCARIS: Can cause biliary obstruction and cholangitis
• TYPHOID: May involve gallbladder (chronic carrier state)
• HEALTHCARE DELAYS: Often present with complications (gangrene, perforation)
• RESOURCE LIMITATIONS: May require conservative management when surgery unavailable

SURGICAL CHALLENGES:
• Limited laparoscopic equipment and expertise
• Anesthesia availability
• Blood product availability
• ICU access for complicated cases
• Higher conversion rates to open surgery`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

CHARACTERISTIC PAIN:
• Location: Right upper quadrant (RUQ) or epigastric
• Character: Constant, severe, deep
• Onset: Often after fatty meal; may wake from sleep
• Duration: >6 hours (unlike biliary colic which is <6 hours)
• Radiation: Right scapula, right shoulder (referred pain via phrenic nerve)

ASSOCIATED SYMPTOMS:
• Nausea and vomiting (common, 60-70%)
• Anorexia
• Low-grade fever (38-38.5°C typical)
• High fever suggests complicated disease or cholangitis

BILIARY COLIC vs ACUTE CHOLECYSTITIS:

                    BILIARY COLIC          ACUTE CHOLECYSTITIS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Pain Duration       <6 hours                >6 hours (often days)
Fever               Absent                  Present (low-grade)
Murphy Sign         Negative                Positive
WBC                 Normal                  Elevated
Ultrasound          Stones only             Stones + inflammation

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
═══════════════════════════════════════════════════════════════════════════════

GENERAL:
• Patient appears uncomfortable, may be febrile
• Prefers to lie still (peritoneal irritation)
• May be jaundiced (suggests choledocholithiasis or Mirizzi syndrome)

VITAL SIGNS:
• Fever (38-39°C; >39°C suggests complication or cholangitis)
• Tachycardia
• Hypotension suggests sepsis or perforation

ABDOMINAL EXAMINATION:

MURPHY'S SIGN (Classic):
• Technique: Palpate RUQ while patient inspires deeply
• Positive: Inspiratory arrest due to pain when inflamed gallbladder meets examiner's hand
• Sensitivity: 65%; Specificity: 87%
• Sonographic Murphy sign (during ultrasound) is more sensitive

OTHER FINDINGS:
• RUQ tenderness (nearly universal)
• Guarding (localized or generalized)
• Palpable gallbladder (15-20%; Courvoisier sign if painless)
• Rebound tenderness suggests peritoneal irritation
• Rigidity suggests perforation/peritonitis
• Hypoactive bowel sounds (ileus)

BOAS SIGN: Referred hyperesthesia below right scapula (rare, historical)

═══════════════════════════════════════════════════════════════════════════════
SIGNS OF COMPLICATED CHOLECYSTITIS
═══════════════════════════════════════════════════════════════════════════════

🔴 High fever (>39°C)
🔴 Rigors/chills
🔴 Marked leukocytosis (>18,000)
🔴 Palpable RUQ mass
🔴 Generalized peritonitis
🔴 Hemodynamic instability
🔴 Altered mental status
🔴 Jaundice (suggests CBD involvement)

These suggest: Gangrenous cholecystitis, perforation, emphysematous cholecystitis, or cholangitis`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

TOKYO GUIDELINES DIAGNOSTIC CRITERIA (TG18/TG13):

A. LOCAL SIGNS OF INFLAMMATION:
   • Murphy's sign
   • RUQ mass/pain/tenderness

B. SYSTEMIC SIGNS OF INFLAMMATION:
   • Fever (>38°C)
   • Elevated CRP (>1 mg/dL)
   • Elevated WBC (>10,000/μL)

C. IMAGING FINDINGS:
   • Characteristic ultrasound or other imaging findings of cholecystitis

DIAGNOSIS:
• SUSPECTED: One item from A + one item from B
• DEFINITE: One item from A + one item from B + C (imaging confirmation)

═══════════════════════════════════════════════════════════════════════════════
TOKYO GUIDELINES SEVERITY GRADING
═══════════════════════════════════════════════════════════════════════════════

GRADE I (MILD):
• Acute cholecystitis in healthy patient
• No organ dysfunction
• Mild inflammatory changes in gallbladder

GRADE II (MODERATE): Any of the following:
• WBC >18,000/μL
• Palpable tender RUQ mass
• Duration of symptoms >72 hours
• Marked local inflammation (gangrenous cholecystitis, pericholecystic abscess, hepatic abscess, biliary peritonitis, emphysematous cholecystitis)

GRADE III (SEVERE): Organ dysfunction in any of:
• Cardiovascular: Hypotension requiring vasopressors
• Neurological: Decreased consciousness
• Respiratory: PaO2/FiO2 ratio <300
• Renal: Oliguria, creatinine >2.0 mg/dL
• Hepatic: PT-INR >1.5
• Hematological: Platelets <100,000/μL

═══════════════════════════════════════════════════════════════════════════════
LABORATORY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

COMPLETE BLOOD COUNT:
• Leukocytosis (12,000-15,000/μL typical)
• Left shift (increased bands)
• WBC >18,000 suggests complicated disease

LIVER FUNCTION TESTS:
• Mild elevations common (due to local inflammation)
• AST/ALT: Mildly elevated (<3x ULN)
• Bilirubin: Usually normal or mildly elevated
• ALP: Usually normal or mildly elevated
• Significantly elevated LFTs suggest choledocholithiasis

AMYLASE/LIPASE:
• Mild elevation may occur
• Significant elevation suggests gallstone pancreatitis

OTHER:
• CRP: Elevated (useful for diagnosis and monitoring)
• Procalcitonin: Elevated in severe infection/sepsis
• Blood cultures: If septic or severe

═══════════════════════════════════════════════════════════════════════════════
IMAGING FINDINGS
═══════════════════════════════════════════════════════════════════════════════

ULTRASOUND (First-line - 90-95% sensitivity):
• Gallstones (echogenic foci with acoustic shadowing)
• Gallbladder wall thickening (>3 mm; normal is ≤3 mm)
• Pericholecystic fluid
• Sonographic Murphy sign (tenderness with transducer pressure)
• Gallbladder distension (>4 cm transverse, >10 cm longitudinal)
• Gallbladder sludge

CT ABDOMEN:
• Similar findings to ultrasound
• Better for detecting complications:
  - Wall defect (perforation)
  - Pericholecystic abscess
  - Gas in wall (emphysematous)
• Useful when ultrasound inconclusive or for surgical planning

HIDA SCAN (Hepatobiliary Iminodiacetic Acid):
• Radionuclide study
• Non-visualization of gallbladder at 4 hours = cystic duct obstruction
• Sensitivity: 95%; Specificity: 90%
• Useful when ultrasound equivocal and diagnosis uncertain
• Limited availability in many African settings

MRCP (Magnetic Resonance Cholangiopancreatography):
• Evaluates biliary tree
• Best for detecting choledocholithiasis
• Non-invasive alternative to ERCP for diagnosis

═══════════════════════════════════════════════════════════════════════════════
FINDINGS SUGGESTING CBD STONES (Choledocholithiasis)
═══════════════════════════════════════════════════════════════════════════════

• Elevated bilirubin (>1.5-2 mg/dL)
• Elevated ALP (>1.5x ULN)
• Elevated GGT
• Dilated CBD on ultrasound (>6 mm; >8 mm post-cholecystectomy or elderly)
• CBD stone visualized
• Clinical jaundice

If CBD stones suspected → MRCP or EUS to confirm; ERCP if high probability`,

    differentialDiagnosis: [
      'Biliary Colic - Self-limited pain <6 hours; no fever; no Murphy sign; normal WBC; no wall thickening on US',
      'Acute Pancreatitis - Epigastric pain radiating to back; elevated lipase (>3x ULN); may coexist with gallstones',
      'Peptic Ulcer Disease/Perforation - Epigastric pain; free air on imaging if perforated; history of NSAIDs or H. pylori',
      'Acute Appendicitis - RLQ pain; McBurney point tenderness; may have RUQ pain if retrocecal appendix',
      'Hepatitis (Acute Viral) - RUQ pain; marked transaminase elevation (>10x); viral markers positive',
      'Liver Abscess - Fever, RUQ pain; CT shows abscess; may be pyogenic or amoebic',
      'Fitz-Hugh-Curtis Syndrome - RUQ pain in young woman; "violin string" adhesions; associated with PID',
      'Right Lower Lobe Pneumonia - Cough, fever; referred RUQ pain; chest X-ray diagnostic',
      'Acute Hepatic Congestion - Right heart failure; hepatomegaly; JVD; leg edema',
      'Cholangitis - Charcot triad (fever, jaundice, RUQ pain); Reynolds pentad adds confusion + hypotension',
      'Choledocholithiasis - Jaundice, elevated LFTs; CBD stone on MRCP; may coexist with cholecystitis',
      'Gastroesophageal Reflux Disease - Burning epigastric/retrosternal pain; worse with meals; responds to PPI',
      'Myocardial Infarction (Inferior) - Epigastric pain; ECG changes; troponin elevated; consider in elderly'
    ],

    investigations: [
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - Leukocytosis (12-15k typical); WBC >18k suggests complication; left shift',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (LFTs)',
        indication: '🔴 IMMEDIATE - Mild elevation common; significant elevation suggests CBD stones',
        availability: 'BASIC'
      },
      {
        name: 'Serum Bilirubin (Total and Direct)',
        indication: '🔴 IMMEDIATE - Elevated suggests choledocholithiasis or Mirizzi syndrome',
        availability: 'BASIC'
      },
      {
        name: 'Serum Amylase and Lipase',
        indication: '🔴 IMMEDIATE - Rule out gallstone pancreatitis; lipase more specific',
        availability: 'BASIC'
      },
      {
        name: 'Basic Metabolic Panel',
        indication: '🔴 IMMEDIATE - Assess renal function, electrolytes; guide fluid resuscitation',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🟠 URGENT - Elevated in inflammation; useful for diagnosis and monitoring response',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, PTT)',
        indication: '🟠 URGENT - Pre-operative assessment; prolonged in severe liver disease or sepsis',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🟠 URGENT - Pre-operative preparation',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures',
        indication: '🟠 URGENT - If febrile, septic, or severe; before antibiotics if possible',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 IMMEDIATE - First-line imaging; gallstones, wall thickening >3mm, pericholecystic fluid, sonographic Murphy',
        availability: 'BASIC'
      },
      {
        name: 'CT Abdomen with Contrast',
        indication: '🟠 URGENT - If ultrasound inconclusive; better for complications (perforation, abscess, emphysematous)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRCP',
        indication: '🟡 If CBD stones suspected - Non-invasive evaluation of biliary tree; detects choledocholithiasis',
        availability: 'ADVANCED'
      },
      {
        name: 'HIDA Scan (Hepatobiliary Scintigraphy)',
        indication: '🟡 If diagnosis uncertain - Non-visualization of gallbladder confirms cystic duct obstruction',
        availability: 'ADVANCED'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 URGENT - Pre-operative; rule out right lower lobe pneumonia as cause of RUQ pain',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🟠 URGENT - Pre-operative; rule out inferior MI in elderly with epigastric pain',
        availability: 'BASIC'
      },
      {
        name: 'Pregnancy Test',
        indication: '🔴 IMMEDIATE - Women of childbearing age; affects imaging and surgical planning',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ NPO (Nil Per Os) - Bowel rest; reduces gallbladder stimulation',
        '⚡ IV ACCESS - For fluids, antibiotics, analgesia',
        '⚡ IV FLUID RESUSCITATION - Crystalloids (NS or LR) for dehydration and sepsis',
        '⚡ ANALGESIA - IV opioids (morphine, hydromorphone) or NSAIDs; do not withhold for diagnosis',
        '⚡ ANTIEMETICS - For nausea/vomiting (ondansetron, metoclopramide)',
        '⚡ ANTIBIOTICS - For moderate-severe cholecystitis; cover gram-negatives and anaerobes',
        '⚡ BLOOD TESTS - CBC, LFTs, amylase/lipase, BMP, coagulation',
        '⚡ ULTRASOUND - Confirm diagnosis; assess for complications',
        '⚡ SURGICAL CONSULTATION - Early involvement for cholecystectomy planning',
        '⚡ SEVERITY ASSESSMENT - Grade per Tokyo Guidelines to guide management',
        '⚡ ASSESS FOR CBD STONES - If LFTs elevated, obtain MRCP or plan for IOC/ERCP',
        '⚡ VTE PROPHYLAXIS - If hospitalized',
        '⚡ GLUCOSE CONTROL - If diabetic',
        '⚡ NASOGASTRIC TUBE - If severe vomiting or ileus'
      ],
      pharmacological: [
        {
          drug: 'Morphine Sulfate',
          indication: '🔴 ANALGESIA - First-line for severe pain; historical concern about sphincter of Oddi spasm is unfounded',
          dose: '2-4 mg IV every 3-4 hours PRN; titrate to pain control',
          route: 'IV',
          duration: 'Until pain controlled or surgery performed',
          contraindications: ['Respiratory depression', 'Hypotension', 'Known allergy'],
          sideEffects: ['Nausea/vomiting', 'Sedation', 'Constipation', 'Respiratory depression'],
          alternatives: ['Hydromorphone', 'Fentanyl', 'Ketorolac (if no contraindications)']
        },
        {
          drug: 'Ketorolac',
          indication: '🟠 ANALGESIA (Alternative/Adjunct) - NSAID; effective for biliary pain; opioid-sparing',
          dose: '15-30 mg IV/IM every 6 hours (max 5 days)',
          route: 'IV or IM',
          duration: 'Maximum 5 days',
          contraindications: ['Renal impairment', 'GI bleeding', 'Coagulopathy', 'Aspirin allergy', 'Elderly (relative)'],
          sideEffects: ['GI upset/bleeding', 'Renal impairment', 'Platelet dysfunction'],
          alternatives: ['Diclofenac', 'Morphine']
        },
        {
          drug: 'Ondansetron',
          indication: '🔴 ANTIEMETIC - For nausea and vomiting',
          dose: '4-8 mg IV every 8 hours PRN',
          route: 'IV',
          duration: 'As needed',
          contraindications: ['QT prolongation', 'Hypersensitivity'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation (rare)'],
          alternatives: ['Metoclopramide', 'Promethazine']
        },
        {
          drug: 'Ceftriaxone + Metronidazole',
          indication: '🔴 MILD-MODERATE CHOLECYSTITIS - Covers enteric gram-negatives and anaerobes',
          dose: 'Ceftriaxone 1-2 g IV daily + Metronidazole 500 mg IV every 8 hours',
          route: 'IV',
          duration: 'Until surgery; continue 24-48h post-op for uncomplicated; 5-7 days if complicated',
          contraindications: ['Cephalosporin allergy', 'Metronidazole: pregnancy (1st trimester), alcohol use'],
          sideEffects: ['Diarrhea', 'C. difficile', 'Rash', 'Metallic taste (metronidazole)'],
          alternatives: ['Ampicillin-sulbactam', 'Piperacillin-tazobactam']
        },
        {
          drug: 'Ampicillin-Sulbactam',
          indication: '🔴 MILD-MODERATE CHOLECYSTITIS - Single agent with gram-positive, gram-negative, anaerobic coverage',
          dose: '3 g IV every 6 hours',
          route: 'IV',
          duration: 'Until surgery; adjust duration based on findings',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'C. difficile', 'Injection site reaction'],
          alternatives: ['Ceftriaxone + Metronidazole', 'Ciprofloxacin + Metronidazole']
        },
        {
          drug: 'Piperacillin-Tazobactam',
          indication: '🔴 MODERATE-SEVERE CHOLECYSTITIS - Broad-spectrum; includes Pseudomonas coverage',
          dose: '3.375-4.5 g IV every 6-8 hours',
          route: 'IV',
          duration: 'Based on clinical response and surgical findings',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Thrombocytopenia', 'C. difficile'],
          alternatives: ['Meropenem', 'Imipenem-cilastatin']
        },
        {
          drug: 'Ciprofloxacin + Metronidazole',
          indication: '🟠 PENICILLIN-ALLERGIC PATIENTS - Alternative regimen for gram-negative and anaerobic coverage',
          dose: 'Ciprofloxacin 400 mg IV every 12 hours + Metronidazole 500 mg IV every 8 hours',
          route: 'IV',
          duration: 'Until surgery and clinical response',
          contraindications: ['Fluoroquinolone allergy', 'QT prolongation', 'Tendon disorders', 'Pregnancy'],
          sideEffects: ['Tendinopathy', 'QT prolongation', 'C. difficile', 'Nausea', 'CNS effects'],
          alternatives: ['Aztreonam + Vancomycin + Metronidazole']
        },
        {
          drug: 'Meropenem',
          indication: '🔴 SEVERE CHOLECYSTITIS or HEALTHCARE-ASSOCIATED - Carbapenem; very broad coverage',
          dose: '1 g IV every 8 hours',
          route: 'IV',
          duration: 'Based on clinical response; 5-7 days for complicated cases',
          contraindications: ['Carbapenem allergy', 'History of seizures with carbapenems'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache', 'C. difficile', 'Seizures (rare)'],
          alternatives: ['Imipenem-cilastatin', 'Piperacillin-tazobactam + Vancomycin']
        },
        {
          drug: 'Vancomycin',
          indication: '🟠 ADD IF MRSA SUSPECTED - Healthcare-associated infection, prior MRSA, severe sepsis',
          dose: '15-20 mg/kg IV every 8-12 hours (adjust for renal function; target trough 10-15 mcg/mL)',
          route: 'IV',
          duration: 'Based on cultures and clinical response',
          contraindications: ['Known allergy (rare)'],
          sideEffects: ['Nephrotoxicity', 'Red man syndrome', 'Ototoxicity'],
          alternatives: ['Linezolid', 'Daptomycin']
        },
        {
          drug: 'Ursodeoxycholic Acid (Ursodiol)',
          indication: '🟡 NOT FOR ACUTE CHOLECYSTITIS - Used for gallstone dissolution in select patients who cannot have surgery; not effective acutely',
          dose: '8-10 mg/kg/day PO in 2-3 divided doses',
          route: 'Oral',
          duration: 'Long-term (months to years) for stone dissolution',
          contraindications: ['Acute cholecystitis', 'Calcified stones', 'Bile duct obstruction', 'Large stones (>2cm)'],
          sideEffects: ['Diarrhea', 'Nausea', 'Elevated LFTs'],
          alternatives: ['Cholecystectomy (definitive)']
        }
      ],
      nonPharmacological: [
        'NPO STATUS: Bowel rest; advance diet after surgery when bowel function returns',
        'IV FLUID RESUSCITATION: Correct dehydration; maintain urine output',
        'NASOGASTRIC TUBE: If significant vomiting or ileus',
        'ELECTROLYTE MONITORING: Correct imbalances from vomiting/NPO status',
        'POSITION: Semi-upright position may be more comfortable',
        'ICE PACKS: May provide local comfort (limited evidence)'
      ],
      surgical: [
        'LAPAROSCOPIC CHOLECYSTECTOMY: Gold standard; preferred approach; early surgery (<72h) recommended',
        'OPEN CHOLECYSTECTOMY: When laparoscopy not possible or fails; severe inflammation; anatomical concerns',
        'SUBTOTAL CHOLECYSTECTOMY: When safe dissection of Calot triangle impossible; leave posterior wall on liver',
        'INTRAOPERATIVE CHOLANGIOGRAM (IOC): To assess CBD for stones; selective use based on risk factors',
        'PERCUTANEOUS CHOLECYSTOSTOMY: Temporizing measure for high-risk patients (Grade III) unfit for surgery; allows drainage',
        'ERCP: Pre-op or post-op if choledocholithiasis confirmed; sphincterotomy and stone extraction',
        'COMMON BILE DUCT EXPLORATION: If CBD stones found at surgery; laparoscopic or open'
      ],
      referralCriteria: [
        '🔴 EMERGENCY SURGERY: Perforated cholecystitis with peritonitis, emphysematous cholecystitis, gangrenous cholecystitis with sepsis',
        '🔴 URGENT SURGERY (<72h): All acute cholecystitis suitable for surgery (Grade I, II)',
        '🟠 INTENSIVE CARE: Grade III (severe) cholecystitis with organ dysfunction',
        '🟠 INTERVENTIONAL RADIOLOGY: For percutaneous cholecystostomy in high-risk patients',
        '🟠 GASTROENTEROLOGY: For ERCP if choledocholithiasis',
        '🟠 DELAYED SURGERY: Consider for Grade III after stabilization with cholecystostomy'
      ],
      monitoring: [
        'VITAL SIGNS: Every 4-6 hours; more frequent if unstable',
        'PAIN ASSESSMENT: Regular assessment; worsening pain may indicate complication',
        'ABDOMINAL EXAM: Serial exams; watch for peritonitis signs',
        'WBC/CRP: Repeat if not improving; should trend down with treatment',
        'URINE OUTPUT: Monitor hydration status',
        'POST-OPERATIVE: Pain, wound, drain output (if placed), diet tolerance'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
TIMING OF SURGERY - TOKYO GUIDELINES
═══════════════════════════════════════════════════════════════════════════════

GRADE I (MILD):
• Early laparoscopic cholecystectomy (within 72 hours)
• Can be performed at any qualified facility

GRADE II (MODERATE):
• Early laparoscopic cholecystectomy if experienced surgeon and adequate support
• If severe local inflammation, consider delayed surgery after cooling off
• Percutaneous cholecystostomy if surgery too risky

GRADE III (SEVERE):
• Urgent organ support in ICU
• Percutaneous cholecystostomy for source control
• Delayed cholecystectomy after stabilization (6-8 weeks)

═══════════════════════════════════════════════════════════════════════════════
EARLY vs DELAYED CHOLECYSTECTOMY
═══════════════════════════════════════════════════════════════════════════════

EARLY SURGERY (Within 72 hours):
• Shorter total hospital stay
• Lower morbidity and cost
• Similar conversion rates to open
• Recommended for Grade I and suitable Grade II

DELAYED SURGERY (6-8 weeks):
• After percutaneous drainage in high-risk patients
• Allows inflammation to settle
• May have more fibrosis/scarring

═══════════════════════════════════════════════════════════════════════════════
POST-OPERATIVE CARE
═══════════════════════════════════════════════════════════════════════════════

UNCOMPLICATED LAPAROSCOPIC CHOLECYSTECTOMY:
• Day surgery or 1 night stay typical
• Resume diet when awake and tolerating liquids
• Pain control with oral analgesia
• Remove port-site dressings in 24-48 hours
• Activity: Light activity immediately; avoid heavy lifting for 2 weeks
• Return to work: 1-2 weeks (desk job); 4-6 weeks (physical labor)

COMPLICATED CASES / OPEN SURGERY:
• Longer hospital stay
• May have drains in place
• Longer recovery (4-6 weeks)

═══════════════════════════════════════════════════════════════════════════════
OUTPATIENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

POST-DISCHARGE (1-2 weeks):
• Wound check
• Review pathology
• Assess diet tolerance
• Address any concerns

POST-CHOLECYSTECTOMY SYNDROME:
• Occurs in 5-40% of patients
• Persistent RUQ pain, dyspepsia, diarrhea
• Evaluate for retained CBD stone, sphincter of Oddi dysfunction, other causes
• Usually self-limited

DIETARY ADVICE:
• Low-fat diet initially (gallbladder no longer stores bile)
• Gradually reintroduce normal diet
• Some patients tolerate all foods; others have fat intolerance

⚠️ Individual follow-up plans should be determined by the treating surgeon.`
    },

    redFlags: [
      '🔴 CRITICAL: Signs of perforation (generalized peritonitis, free air, severe sepsis) - EMERGENCY SURGERY',
      '🔴 CRITICAL: Emphysematous cholecystitis (gas in gallbladder wall on imaging) - EMERGENCY SURGERY',
      '🔴 CRITICAL: Septic shock (hypotension, lactate elevation, organ dysfunction) - ICU + urgent source control',
      '🔴 CRITICAL: Gangrenous cholecystitis (failure to improve, high WBC, imaging findings) - URGENT SURGERY',
      '🟠 URGENT: Charcot triad (fever, jaundice, RUQ pain) - suggests cholangitis; may need ERCP',
      '🟠 URGENT: Reynolds pentad (Charcot triad + confusion + hypotension) - severe cholangitis',
      '🟠 URGENT: Failure to improve after 48-72 hours of appropriate treatment',
      '🟠 URGENT: Rising WBC, bilirubin, or inflammatory markers despite treatment',
      '🟠 URGENT: Palpable RUQ mass - may indicate empyema or pericholecystic abscess',
      '🟠 URGENT: New or worsening jaundice - suggests CBD involvement or Mirizzi syndrome'
    ],

    complications: [
      'GANGRENOUS CHOLECYSTITIS: Necrosis of gallbladder wall; higher perforation risk; requires urgent surgery',
      'PERFORATION: Free perforation (peritonitis) or contained (abscess); 10% of cases; high mortality',
      'PERICHOLECYSTIC ABSCESS: Contained perforation; may require drainage + antibiotics',
      'EMPYEMA: Pus-filled gallbladder; infected; high risk of perforation',
      'EMPHYSEMATOUS CHOLECYSTITIS: Gas-forming organisms (Clostridium); diabetics; high mortality',
      'CHOLECYSTOENTERIC FISTULA: Fistula between gallbladder and bowel; may lead to gallstone ileus',
      'GALLSTONE ILEUS: Large stone passes through fistula and obstructs bowel (usually terminal ileum)',
      'MIRIZZI SYNDROME: Stone in cystic duct compresses common hepatic duct → jaundice',
      'CHOLEDOCHOLITHIASIS: CBD stones; jaundice, cholangitis, pancreatitis',
      'CHOLANGITIS: Infection of biliary tree; Charcot triad; may be life-threatening',
      'BILIARY PANCREATITIS: Stone passes and impacts at ampulla → pancreatitis',
      'PORCELAIN GALLBLADDER: Calcified wall; associated with gallbladder cancer (controversial)',
      'SURGICAL COMPLICATIONS: Bile duct injury (0.3-0.5% for laparoscopic), bleeding, bile leak'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

UNCOMPLICATED ACUTE CHOLECYSTITIS:
• Mortality: <1% with timely surgery
• Excellent long-term outcomes after cholecystectomy
• Low recurrence of biliary symptoms

COMPLICATED CHOLECYSTITIS:
• Gangrenous: Mortality 5-10%
• Perforated: Mortality 10-30%
• Emphysematous: Mortality 15-25%

ACALCULOUS CHOLECYSTITIS:
• Mortality: 10-50% (often due to underlying critical illness)
• Higher complication rate than calculous type

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Young age
• Early presentation (<72 hours)
• Uncomplicated disease (Grade I)
• Laparoscopic surgery possible
• No comorbidities

UNFAVORABLE:
• Advanced age (>70)
• Delayed presentation
• Complicated disease (gangrene, perforation)
• Multiple comorbidities (diabetes, heart disease)
• Acalculous cholecystitis
• Grade III (severe) with organ dysfunction
• Need for open surgery

═══════════════════════════════════════════════════════════════════════════════
AFTER CHOLECYSTECTOMY
═══════════════════════════════════════════════════════════════════════════════

• Most patients do well with no long-term sequelae
• 5-40% experience post-cholecystectomy syndrome (usually mild, transient)
• Bile flows directly into duodenum (no storage)
• Some fat intolerance may occur initially
• No need for ongoing follow-up if uncomplicated recovery

⚠️ Individual prognosis depends on multiple factors. Discuss with treating team.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION (Preventing Gallstones)
═══════════════════════════════════════════════════════════════════════════════

LIFESTYLE MODIFICATIONS:
• Maintain healthy weight (obesity is major risk factor)
• Gradual weight loss if obese (avoid rapid weight loss - increases gallstone risk)
• Regular physical activity
• Balanced diet:
  - Increase fiber intake
  - Reduce saturated fats
  - Moderate coffee consumption may be protective
  - Nuts may be protective

MEDICAL PREVENTION (High-Risk Groups):
• Ursodiol during rapid weight loss (bariatric surgery, VLCD)
• Prevents pigment stones in hemolytic disorders (less effective)

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (Preventing Cholecystitis in Patients with Gallstones)
═══════════════════════════════════════════════════════════════════════════════

SYMPTOMATIC GALLSTONES:
• Elective cholecystectomy recommended after biliary colic
• Prevents progression to acute cholecystitis
• Risk of cholecystitis: 1-3% per year if untreated

ASYMPTOMATIC GALLSTONES:
• Generally observe (no routine cholecystectomy)
• Exceptions (consider cholecystectomy):
  - Porcelain gallbladder (cancer risk - controversial)
  - Gallbladder polyps >1 cm
  - Very large stones (>3 cm)
  - Patients undergoing bariatric surgery
  - Certain immunosuppressed patients

═══════════════════════════════════════════════════════════════════════════════
PREVENTING ACALCULOUS CHOLECYSTITIS (ICU Patients)
═══════════════════════════════════════════════════════════════════════════════

• Early enteral nutrition when possible (stimulates gallbladder contraction)
• Minimize prolonged fasting
• Maintain adequate perfusion
• Consider prophylactic cholecystectomy in very high-risk patients (rare)

═══════════════════════════════════════════════════════════════════════════════
PATIENT EDUCATION
═══════════════════════════════════════════════════════════════════════════════

Patients with known gallstones should:
• Know symptoms of biliary colic and cholecystitis
• Seek early medical attention if symptoms occur
• Understand that cholecystectomy is definitive treatment
• Discuss elective surgery after episode of biliary colic`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS ACUTE CHOLECYSTITIS?
═══════════════════════════════════════════════════════════════════════════════

Acute cholecystitis is inflammation of the gallbladder, usually caused by gallstones blocking the tube (cystic duct) that drains the gallbladder. When the gallbladder cannot empty properly, it becomes swollen, inflamed, and can become infected.

The gallbladder is a small organ under your liver that stores bile, a fluid that helps digest fats. When you eat fatty foods, the gallbladder squeezes bile into your intestine.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - SEEK CARE IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Go to the hospital if you experience:

🔴 Severe pain in the upper right part of your abdomen lasting more than a few hours
🔴 Pain that spreads to your right shoulder or back
🔴 Pain that gets worse when you breathe in deeply
🔴 Fever or chills
🔴 Nausea and vomiting
🔴 Yellowing of your skin or eyes (jaundice)
🔴 Abdominal pain so severe you cannot find a comfortable position

DO NOT wait to see if it improves - untreated cholecystitis can lead to serious complications.

═══════════════════════════════════════════════════════════════════════════════
WHAT TO EXPECT AT THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

TESTS YOU MAY HAVE:
• Blood tests (to check for infection and liver function)
• Ultrasound of your abdomen (to see gallstones and inflammation)
• CT scan (if ultrasound is not clear)

TREATMENT:
• You will not be allowed to eat or drink initially
• IV fluids will be given
• Pain medication and antibiotics
• Surgery to remove the gallbladder (cholecystectomy) is usually recommended

═══════════════════════════════════════════════════════════════════════════════
SURGERY - WHAT TO KNOW
═══════════════════════════════════════════════════════════════════════════════

LAPAROSCOPIC CHOLECYSTECTOMY:
• Most common approach
• Small incisions (keyhole surgery)
• Usually go home same day or next day
• Recovery: 1-2 weeks for most activities

OPEN SURGERY:
• Larger incision
• May be needed for complicated cases
• Longer recovery (4-6 weeks)

AFTER SURGERY:
• You can live normally without a gallbladder
• Bile flows directly from liver to intestine
• Most people have no long-term problems
• Some people experience loose stools initially, especially with fatty foods

═══════════════════════════════════════════════════════════════════════════════
AFTER LEAVING THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

CARE FOR YOUR INCISIONS:
• Keep wounds clean and dry
• Follow your surgeon's instructions
• Watch for signs of infection (redness, swelling, drainage, fever)

DIET:
• Start with light, low-fat foods
• Gradually return to normal diet
• Some people need to limit fatty foods long-term

ACTIVITY:
• Walk and move around gently
• Avoid heavy lifting for 2-4 weeks
• Return to work when comfortable (usually 1-2 weeks for desk work)

RETURN TO HOSPITAL IF:
• Fever develops
• Wound becomes red, swollen, or drains pus
• Severe abdominal pain returns
• Yellowing of skin or eyes
• Unable to eat or drink
• Persistent nausea and vomiting

═══════════════════════════════════════════════════════════════════════════════
PREVENTING GALLSTONES
═══════════════════════════════════════════════════════════════════════════════

While not all gallstones can be prevented, you can reduce your risk:

• Maintain a healthy weight
• Lose weight gradually (rapid weight loss increases risk)
• Eat a balanced diet with plenty of fiber
• Exercise regularly
• Avoid crash diets

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• Do I need surgery? How soon?
• What are the risks of surgery vs waiting?
• Will the surgery be laparoscopic?
• When can I return to my normal activities?
• What should I eat after surgery?
• What symptoms should I watch for after I go home?

Your healthcare team is here to help you. Do not hesitate to ask questions.`,

    references: [
      'Yokoe M, Hata J, Takada T, et al. Tokyo Guidelines 2018: diagnostic criteria and severity grading of acute cholecystitis. J Hepatobiliary Pancreat Sci. 2018;25(1):41-54.',
      'Okamoto K, Suzuki K, Takada T, et al. Tokyo Guidelines 2018: flowchart for the management of acute cholecystitis. J Hepatobiliary Pancreat Sci. 2018;25(1):55-72.',
      'Gomi H, Solomkin JS, Schlossberg D, et al. Tokyo Guidelines 2018: antimicrobial therapy for acute cholangitis and cholecystitis. J Hepatobiliary Pancreat Sci. 2018;25(1):3-16.',
      'Ansaloni L, Pisano M, Coccolini F, et al. 2016 WSES guidelines on acute calculous cholecystitis. World J Emerg Surg. 2016;11:25.',
      'Gurusamy KS, Davidson C, Gluud C, Davidson BR. Early versus delayed laparoscopic cholecystectomy for people with acute cholecystitis. Cochrane Database Syst Rev. 2013;(6):CD005440.',
      'Strasberg SM. Acute calculous cholecystitis. N Engl J Med. 2008;358(26):2804-2811.',
      'Borzellino G, Steccanella F, Mantovani W, et al. Predictive factors for the diagnosis of severe acute cholecystitis in an emergency setting. Surg Endosc. 2013;27(9):3388-3395.',
      'Loozen CS, van Santvoort HC, van Geloven AAW, et al. Perioperative and long-term outcomes of early versus delayed surgery for acute cholecystitis. Ann Surg. 2018;268(4):558-565.',
      'Afuwape OO, Akute OO. Pattern of presentation of acute cholecystitis in a Nigerian teaching hospital. Niger J Clin Pract. 2010;13(3):302-305. [African data]',
      'Arowolo OA, Agbakwuru EA, Adisa AO, et al. Evaluation of laparoscopic cholecystectomy in a Nigerian tertiary centre. West Afr J Med. 2011;30(4):280-284. [African data]'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 28. TUBERCULOSIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '28',
    name: 'Tuberculosis',
    slug: 'tuberculosis',
    specialty: 'Infectious Disease',
    bodySystem: 'Respiratory',
    icdCode: 'A15.0',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Tuberculosis (TB) is a chronic infectious disease caused by Mycobacterium tuberculosis, primarily affecting the lungs (pulmonary TB) but capable of involving any organ system (extrapulmonary TB). TB remains a leading cause of death from a single infectious agent globally and is the defining epidemic in Africa, particularly in the context of HIV co-infection. Early diagnosis, appropriate treatment, and public health measures are essential for individual cure and disease control.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• CHRONIC COUGH >2 WEEKS: Cardinal symptom; must consider TB in endemic areas
• SPUTUM SMEAR MICROSCOPY: First-line diagnostic; look for acid-fast bacilli (AFB)
• GeneXpert MTB/RIF: Rapid molecular test; detects TB AND rifampicin resistance simultaneously
• HIV TESTING: MANDATORY for all TB patients; TB-HIV co-infection is common and deadly
• STANDARD TREATMENT (Drug-Susceptible TB): 2HRZE/4HR (2 months intensive, 4 months continuation)
• DIRECTLY OBSERVED THERAPY (DOT): Essential for treatment adherence and preventing resistance
• DRUG-RESISTANT TB: MDR-TB (resistant to INH + RIF) requires specialized regimens (9-20 months)
• CONTACT TRACING: Screen all close contacts; provide preventive therapy to eligible contacts
• TB PREVENTIVE THERAPY (TPT): For latent TB infection, especially in PLHIV and child contacts
• EXTRAPULMONARY TB: 15-20% of cases; lymph nodes, pleura, spine, meninges, disseminated
• TB MENINGITIS: Most severe form; high mortality; requires prolonged treatment + steroids
• PARADOXICAL REACTION: Worsening symptoms after starting treatment (especially with ART); usually continue therapy
• INFECTION CONTROL: Respiratory isolation, N95 masks, ventilation, cough hygiene
• NOTIFICATION: TB is a notifiable disease; report all cases to public health authorities

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

TRANSMISSION:
• Airborne transmission via droplet nuclei (1-5 μm)
• Generated by coughing, sneezing, speaking, singing
• Inhaled droplets reach terminal alveoli
• Close, prolonged contact increases transmission risk
• Smear-positive pulmonary TB is most infectious

INFECTION STAGES:

1. PRIMARY INFECTION:
   • Bacilli reach alveoli → engulfed by alveolar macrophages
   • Bacteria multiply intracellularly
   • Spread to regional lymph nodes (hilar, mediastinal)
   • Ghon complex = primary lung lesion + lymph node involvement
   • Most infections are contained by cell-mediated immunity (2-8 weeks)

2. LATENT TB INFECTION (LTBI):
   • Contained infection; bacteria dormant in granulomas
   • Asymptomatic; not infectious
   • Positive TST/IGRA; normal CXR
   • 5-10% lifetime risk of reactivation (higher with HIV)

3. ACTIVE TB DISEASE:
   • Primary progressive: Fails to contain initial infection
   • Reactivation: Dormant bacteria reactivate (most adult TB)
   • Risk factors for progression: HIV, malnutrition, diabetes, immunosuppression, extremes of age

GRANULOMA FORMATION:
• Central caseous necrosis surrounded by epithelioid macrophages, Langhans giant cells, lymphocytes
• Fibrosis and calcification if contained
• Liquefaction and cavitation if progressive

EXTRAPULMONARY SPREAD:
• Hematogenous or lymphatic dissemination
• Can occur during primary or reactivation
• HIV greatly increases risk of disseminated and extrapulmonary TB`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• INCIDENCE: ~10.6 million new cases annually
• MORTALITY: ~1.3 million deaths annually (plus ~200,000 among HIV-positive)
• LEADING INFECTIOUS CAUSE OF DEATH: Surpassed HIV/AIDS
• LATENT TB: ~25% of world population infected
• DRUG-RESISTANT TB: ~500,000 new MDR/RR-TB cases annually
• TB-HIV CO-INFECTION: HIV is strongest risk factor for TB

HIGH-BURDEN COUNTRIES (WHO list):
• 30 countries account for 87% of global TB cases
• 8 countries account for 2/3 of cases: India, Indonesia, China, Philippines, Pakistan, Nigeria, Bangladesh, South Africa

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY - THE EPICENTER
═══════════════════════════════════════════════════════════════════════════════

• HIGHEST INCIDENCE RATES GLOBALLY: Sub-Saharan Africa leads worldwide
• TB-HIV EPICENTER: Africa has highest TB-HIV co-infection rates (>50% in some countries)
• TB IS LEADING CAUSE OF DEATH IN PLHIV: More than any other opportunistic infection

REGIONAL STATISTICS:
• South Africa: ~300,000 cases/year; highest MDR-TB burden
• Nigeria: ~220,000 cases/year; large absolute numbers
• DR Congo, Ethiopia, Kenya, Tanzania: High-burden countries
• Lesotho, Eswatini, South Africa: Highest incidence rates per capita

TB-HIV CO-INFECTION:
• 50-80% of TB patients are HIV-positive in high-burden African countries
• HIV increases TB risk 20-30 fold
• TB is #1 killer of PLHIV
• Co-infected patients have atypical presentations, higher mortality

DRUG-RESISTANT TB IN AFRICA:
• MDR-TB: 3-5% of new cases; up to 20% of previously treated
• XDR-TB: Emerging concern, especially South Africa
• Pre-XDR and XDR-TB associated with HIV

CHALLENGES IN AFRICA:
• Limited diagnostic capacity (microscopy only in many areas)
• GeneXpert rollout incomplete
• Drug stockouts
• High loss to follow-up
• Weak health systems
• Poverty and malnutrition
• Overcrowding (urban slums, prisons, mines)
• Stigma
• Mining industry (silicosis + TB)

SPECIAL POPULATIONS:
• MINERS: Silicosis increases TB risk 30-fold; Southern African mines heavily affected
• PRISONERS: Overcrowding, poor ventilation; high transmission
• REFUGEES/DISPLACED: Disrupted healthcare, crowded conditions
• HEALTHCARE WORKERS: Occupational risk; nosocomial transmission
• CHILDREN: Often underdiagnosed; high risk of severe forms`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
PULMONARY TUBERCULOSIS (80-85% of cases)
═══════════════════════════════════════════════════════════════════════════════

CLASSIC SYMPTOMS (May develop gradually over weeks-months):

RESPIRATORY:
• Chronic cough >2-3 weeks (most important symptom)
• Initially dry, becomes productive
• Hemoptysis (blood-streaked to massive)
• Chest pain (pleuritic if pleural involvement)
• Dyspnea (advanced disease)

CONSTITUTIONAL ("B SYMPTOMS"):
• Fever (often low-grade, evening rise)
• Night sweats (drenching, characteristic)
• Weight loss / failure to thrive
• Anorexia
• Fatigue, malaise

PHYSICAL EXAMINATION:
• May be normal in early disease
• Cachexia (wasting) in advanced disease
• Fever
• Pallor (anemia of chronic disease)
• Lymphadenopathy (cervical, supraclavicular)
• Chest findings:
  - Often minimal despite extensive radiographic disease
  - Crackles/rales over affected areas
  - Bronchial breathing over cavities
  - Signs of pleural effusion if present

═══════════════════════════════════════════════════════════════════════════════
TB IN HIV-POSITIVE PATIENTS - ATYPICAL PRESENTATIONS
═══════════════════════════════════════════════════════════════════════════════

⚠️ TB in HIV often presents ATYPICALLY - maintain high suspicion!

DIFFERENCES FROM HIV-NEGATIVE:
• More likely to have extrapulmonary or disseminated disease
• Less cavitation on CXR (impaired cell-mediated immunity)
• Atypical CXR patterns (lower lobe, miliary, adenopathy, normal CXR in 10-20%)
• More likely to be smear-negative
• Higher mortality
• Faster progression

CD4 COUNT AND PRESENTATION:
• CD4 >350: More typical presentation (cavities, upper lobe)
• CD4 <200: Atypical; adenopathy, miliary, disseminated, smear-negative
• Very low CD4: May have normal CXR with active TB

═══════════════════════════════════════════════════════════════════════════════
EXTRAPULMONARY TUBERCULOSIS (15-20% of cases; >50% in HIV+)
═══════════════════════════════════════════════════════════════════════════════

TB LYMPHADENITIS (Most common EPTB):
• Painless, slowly enlarging lymph nodes
• Cervical most common ("scrofula")
• May become matted, form abscesses, drain (sinus formation)
• Often unilateral

PLEURAL TB:
• Unilateral pleural effusion
• Pleuritic chest pain, cough, dyspnea
• Effusion is exudative, lymphocytic, low glucose, high ADA
• Paucibacillary; AFB smear often negative

TB MENINGITIS (Most severe form):
• Subacute onset (days to weeks)
• Headache, fever, altered mental status
• Neck stiffness, cranial nerve palsies (VI, III, IV)
• Hydrocephalus, stroke (basal meningitis)
• CSF: Lymphocytic, high protein, low glucose, elevated ADA
• HIGH MORTALITY (20-50%) even with treatment

MILIARY (DISSEMINATED) TB:
• Hematogenous spread throughout body
• "Millet seed" nodules on CXR (may be subtle)
• Fever, weight loss, hepatosplenomegaly
• May involve any organ; multiorgan failure
• Common in HIV and immunosuppressed

SPINAL TB (POTT DISEASE):
• Back pain, kyphosis ("gibbus" deformity)
• Thoracolumbar most common
• Paravertebral abscess (cold abscess)
• Neurological deficits if cord compression
• X-ray: Vertebral collapse, disc space narrowing

ABDOMINAL TB:
• Intestinal (ileocecal most common): pain, obstruction, mass
• Peritoneal: Ascites (lymphocytic, high protein, high ADA)
• Hepatosplenic: Hepatomegaly, elevated ALP

GENITOURINARY TB:
• Dysuria, frequency, hematuria
• Sterile pyuria (WBC but negative routine culture)
• Renal: Calcification, hydronephrosis
• Genital: Epididymitis, salpingitis, infertility

PERICARDIAL TB:
• Chest pain, dyspnea
• Pericardial effusion → tamponade
• Constrictive pericarditis (chronic)
• High mortality

CUTANEOUS TB:
• Lupus vulgaris (most common)
• Scrofuloderma (extension from lymph node)
• TB verrucosa cutis`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

STEP 1: CLINICAL SUSPICION
• Cough >2 weeks + constitutional symptoms in endemic area
• HIV-positive with any respiratory symptoms or unexplained fever
• Contact of known TB case
• Risk factors (diabetes, malnutrition, immunosuppression)

STEP 2: CHEST X-RAY
• First imaging modality
• Classic findings support diagnosis but not definitive

STEP 3: MICROBIOLOGICAL CONFIRMATION
• Sputum smear microscopy (3 specimens including early morning)
• GeneXpert MTB/RIF (rapid molecular test)
• Culture (gold standard but slow)

STEP 4: HIV TESTING
• All TB patients must be offered HIV testing

STEP 5: DRUG SUSCEPTIBILITY TESTING
• At minimum, rifampicin resistance (GeneXpert)
• Full DST for MDR-TB

═══════════════════════════════════════════════════════════════════════════════
MICROBIOLOGICAL TESTS
═══════════════════════════════════════════════════════════════════════════════

SPUTUM SMEAR MICROSCOPY (AFB Smear):
• Ziehl-Neelsen or auramine-rhodamine staining
• Detects acid-fast bacilli (AFB)
• Sensitivity: 50-80% (depends on bacterial load)
• Specificity: High in endemic areas
• Quick (same day) and inexpensive
• LIMITATION: Does not distinguish M. tuberculosis from NTM; does not detect resistance

GeneXpert MTB/RIF (Xpert):
• WHO-recommended rapid molecular test
• Detects M. tuberculosis DNA AND rifampicin resistance
• Sensitivity: ~85-90% (higher than smear)
• Specificity: ~99%
• Results in 2 hours
• Can use on sputum, CSF, other specimens
• Game-changer for TB diagnosis in Africa

TB CULTURE:
• Gold standard for definitive diagnosis
• Löwenstein-Jensen (LJ) medium: 4-8 weeks
• MGIT (liquid culture): 1-3 weeks
• Allows species identification and full DST
• Sensitivity: Nearly 100% (if viable organisms present)
• LIMITATION: Slow; requires lab infrastructure

DRUG SUSCEPTIBILITY TESTING (DST):
• Essential for guiding treatment
• Phenotypic (culture-based): 2-4 weeks additional
• Genotypic (molecular): Line probe assays (LPA), whole genome sequencing
• At minimum, test for rifampicin resistance

═══════════════════════════════════════════════════════════════════════════════
CHEST X-RAY FINDINGS
═══════════════════════════════════════════════════════════════════════════════

PRIMARY TB:
• Unilateral hilar/paratracheal lymphadenopathy
• Middle or lower lobe infiltrate
• Pleural effusion
• Ghon complex (calcified focus + calcified node)

REACTIVATION/POST-PRIMARY TB (Classic):
• Upper lobe infiltrates (apical and posterior segments)
• Cavitation (hallmark of reactivation)
• Fibrosis and volume loss
• Bronchogenic spread ("tree-in-bud" nodules)

MILIARY TB:
• Diffuse, uniformly distributed 1-3mm nodules
• "Millet seed" appearance
• May be subtle; HRCT more sensitive

HIV-ASSOCIATED TB:
• Lower lobe involvement more common
• Less cavitation
• Mediastinal/hilar adenopathy
• Miliary pattern
• Normal CXR in 10-20%!

═══════════════════════════════════════════════════════════════════════════════
TESTS FOR LATENT TB INFECTION (LTBI)
═══════════════════════════════════════════════════════════════════════════════

TUBERCULIN SKIN TEST (TST / Mantoux):
• Inject 5 TU PPD intradermally; read induration at 48-72 hours
• Positive cutoff varies by risk:
  - ≥5 mm: HIV+, contacts, immunosuppressed, fibrotic CXR
  - ≥10 mm: High-risk groups, healthcare workers
  - ≥15 mm: Low-risk individuals
• LIMITATIONS: False positive (BCG, NTM); false negative (anergy in HIV)

INTERFERON-GAMMA RELEASE ASSAYS (IGRAs):
• QuantiFERON-TB Gold, T-SPOT.TB
• Blood test; measures IFN-γ response to TB antigens
• Not affected by BCG vaccination
• Single visit
• LIMITATIONS: Expensive, requires lab infrastructure

⚠️ TST and IGRA detect INFECTION, not disease. Cannot distinguish LTBI from active TB.`,

    differentialDiagnosis: [
      'Community-Acquired Pneumonia - Acute onset; productive cough, fever; responds to standard antibiotics; CXR shows lobar consolidation',
      'Lung Cancer - Chronic cough, hemoptysis, weight loss; mass lesion on imaging; older age, smoking history; biopsy diagnostic',
      'HIV-related Pneumonia (PCP, bacterial) - In PLHIV; bilateral infiltrates (PCP); acute presentation; check CD4, sputum',
      'Nontuberculous Mycobacteria (NTM) - Similar presentation; culture distinguishes; consider if TB treatment fails',
      'Fungal Pneumonia (Histoplasmosis, Cryptococcosis) - Endemic areas; similar CXR; culture, antigen tests diagnostic',
      'Bronchiectasis - Chronic cough, recurrent infections; HRCT shows dilated airways; may coexist with TB',
      'Lung Abscess - Fever, productive cough, foul sputum; CXR shows cavity with air-fluid level; often polymicrobial',
      'Sarcoidosis - Bilateral hilar adenopathy; non-caseating granulomas on biopsy; elevated ACE; exclude TB first',
      'Silicosis - Mining history; upper lobe nodules, hilar adenopathy; increased TB risk (silico-tuberculosis)',
      'Lymphoma - Mediastinal adenopathy, B symptoms; biopsy diagnostic; can coexist with TB',
      'Mesothelioma - Asbestos exposure; unilateral pleural thickening/effusion; biopsy diagnostic',
      'Chronic Cough (Other Causes) - GERD, post-nasal drip, asthma, ACE inhibitors; no infiltrate on CXR; trial of therapy'
    ],

    investigations: [
      {
        name: 'Sputum AFB Smear (×3 specimens)',
        indication: '🔴 IMMEDIATE - First-line diagnosis; collect early morning specimens; same-day result',
        availability: 'BASIC'
      },
      {
        name: 'GeneXpert MTB/RIF',
        indication: '🔴 IMMEDIATE - Rapid molecular diagnosis; detects TB and rifampicin resistance in 2 hours; WHO-recommended',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray (PA and Lateral)',
        indication: '🔴 IMMEDIATE - Essential for all suspected pulmonary TB; assess extent and pattern',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🔴 IMMEDIATE - MANDATORY for all TB patients; determines prognosis and management',
        availability: 'BASIC'
      },
      {
        name: 'Sputum Culture for TB',
        indication: '🟠 URGENT - Gold standard; species confirmation and full DST; slow (weeks)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Drug Susceptibility Testing (DST)',
        indication: '🟠 URGENT - At minimum rifampicin (GeneXpert); full DST for confirmed MDR-TB',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Anemia of chronic disease common; baseline before treatment',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 URGENT - Baseline before hepatotoxic drugs (INH, RIF, PZA); monitor during treatment',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function Tests',
        indication: '🟠 URGENT - Baseline; important for drug dosing (streptomycin, ethambutol)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose / HbA1c',
        indication: '🟠 URGENT - Screen for diabetes; DM increases TB risk and affects outcomes',
        availability: 'BASIC'
      },
      {
        name: 'CD4 Count',
        indication: '🟠 URGENT - For HIV-positive patients; guides ART timing and OI prophylaxis',
        availability: 'BASIC'
      },
      {
        name: 'Tuberculin Skin Test (TST)',
        indication: '🟡 For LTBI screening - In contacts, PLHIV, pre-immunosuppression; not for active TB diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'IGRA (QuantiFERON/T-SPOT)',
        indication: '🟡 For LTBI screening - Alternative to TST; not affected by BCG; not for active TB diagnosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Chest',
        indication: '🟡 When CXR inconclusive - Better sensitivity for early, miliary, or atypical TB; detects cavities',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture (CSF Analysis)',
        indication: '🔴 IMMEDIATE if meningitis suspected - CSF: lymphocytic, low glucose, high protein, elevated ADA; GeneXpert on CSF',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid Analysis',
        indication: '🟠 URGENT for pleural TB - Exudate, lymphocytic, high ADA (>40 U/L); low yield on AFB smear',
        availability: 'BASIC'
      },
      {
        name: 'Tissue Biopsy (Lymph Node, Pleura, Other)',
        indication: '🟡 For extrapulmonary TB - Histology shows granulomas; send for AFB smear, culture, GeneXpert',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Adenosine Deaminase (ADA)',
        indication: '🟠 URGENT for serous TB - Elevated in TB pleural effusion (>40), peritoneal (>39), CSF (>9-10)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urine LAM (Lipoarabinomannan)',
        indication: '🟠 For HIV-positive with low CD4 - Point-of-care; diagnoses TB in advanced HIV; low sensitivity otherwise',
        availability: 'BASIC'
      },
      {
        name: 'Line Probe Assay (LPA)',
        indication: '🟠 For drug resistance - Detects INH and RIF resistance rapidly; first or second-line LPA',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ISOLATE PATIENT - Respiratory isolation for suspected/confirmed pulmonary TB until deemed non-infectious',
        '⚡ INFECTION CONTROL - N95 respirators for staff; surgical mask for patient; adequate ventilation',
        '⚡ COLLECT SPECIMENS - Sputum for AFB smear and GeneXpert before starting treatment if possible',
        '⚡ HIV TESTING - Offer to all TB patients; essential for management',
        '⚡ ASSESS FOR DANGER SIGNS - Respiratory distress, altered consciousness, unable to walk, very ill appearance',
        '⚡ NUTRITIONAL ASSESSMENT - TB is a wasting disease; nutritional support is essential',
        '⚡ CONTACT TRACING - Identify household and close contacts for screening',
        '⚡ NOTIFY PUBLIC HEALTH - TB is notifiable; register in national TB program',
        '⚡ BASELINE INVESTIGATIONS - CBC, LFTs, renal function, blood glucose, HIV test',
        '⚡ ASSESS FOR DRUG RESISTANCE - Prior TB treatment? Contact with MDR-TB? GeneXpert for RIF resistance'
      ],
      pharmacological: [
        {
          drug: 'STANDARD REGIMEN: 2HRZE/4HR (Drug-Susceptible TB)',
          indication: '🔴 FIRST-LINE - For new cases of drug-susceptible pulmonary TB',
          dose: 'INTENSIVE PHASE (2 months): Isoniazid (H) 5 mg/kg + Rifampicin (R) 10 mg/kg + Pyrazinamide (Z) 25 mg/kg + Ethambutol (E) 15 mg/kg DAILY. CONTINUATION PHASE (4 months): Isoniazid + Rifampicin DAILY',
          route: 'Oral (all drugs given together, preferably as fixed-dose combination)',
          duration: '6 months total (2 months intensive + 4 months continuation)',
          contraindications: ['Known drug allergy', 'Severe hepatic impairment (modify regimen)', 'Optic neuritis (avoid ethambutol)'],
          sideEffects: ['Hepatotoxicity (H, R, Z)', 'Peripheral neuropathy (H)', 'Optic neuritis (E)', 'Orange discoloration of fluids (R)', 'Hyperuricemia (Z)', 'Rash', 'GI upset'],
          alternatives: ['MDR-TB regimen if rifampicin resistance detected']
        },
        {
          drug: 'Isoniazid (INH, H)',
          indication: '🔴 CORE FIRST-LINE - Most potent early bactericidal activity',
          dose: 'Adults: 5 mg/kg/day (max 300 mg); Children: 10 mg/kg/day (range 7-15, max 300 mg)',
          route: 'Oral',
          duration: '6 months (entire treatment duration)',
          contraindications: ['Active hepatitis', 'Previous INH-induced hepatitis'],
          sideEffects: ['Hepatotoxicity (dose-related, age-related)', 'Peripheral neuropathy (prevent with pyridoxine)', 'Drug-induced lupus', 'Seizures (overdose)'],
          alternatives: ['Rifampicin-based regimen if INH resistance']
        },
        {
          drug: 'Rifampicin (RIF, R)',
          indication: '🔴 CORE FIRST-LINE - Sterilizing activity; kills dormant bacilli',
          dose: 'Adults: 10 mg/kg/day (450-600 mg); Children: 15 mg/kg/day (range 10-20, max 600 mg)',
          route: 'Oral (take on empty stomach)',
          duration: '6 months (entire treatment duration)',
          contraindications: ['Severe hepatic impairment', 'Concurrent use of certain antiretrovirals (adjust doses)', 'Jaundice'],
          sideEffects: ['Hepatotoxicity', 'Orange discoloration of urine, tears, sweat', 'GI upset', 'Flu-like syndrome (intermittent dosing)', 'Drug interactions (CYP450 inducer)'],
          alternatives: ['Rifabutin (with certain ART)', 'Rifapentine (specific regimens)']
        },
        {
          drug: 'Pyrazinamide (PZA, Z)',
          indication: '🔴 FIRST-LINE - Sterilizing activity in acidic environment; shortens treatment',
          dose: 'Adults: 25 mg/kg/day; Children: 35 mg/kg/day (range 30-40)',
          route: 'Oral',
          duration: '2 months (intensive phase only)',
          contraindications: ['Severe hepatic impairment', 'Acute gout'],
          sideEffects: ['Hepatotoxicity (most hepatotoxic first-line drug)', 'Hyperuricemia/gout', 'Arthralgias', 'GI upset', 'Rash'],
          alternatives: ['Extend continuation phase if PZA not tolerated']
        },
        {
          drug: 'Ethambutol (EMB, E)',
          indication: '🔴 FIRST-LINE - Prevents emergence of resistance; bacteriostatic',
          dose: 'Adults: 15 mg/kg/day (15-20); Children: 20 mg/kg/day (15-25). Avoid in children too young for visual testing unless necessary',
          route: 'Oral',
          duration: '2 months (intensive phase); can stop if drug-susceptible confirmed',
          contraindications: ['Optic neuritis', 'Inability to monitor vision', 'Severe renal impairment (dose adjust)'],
          sideEffects: ['Optic neuritis (dose-related, usually reversible)', 'Color vision changes (red-green)', 'Peripheral neuropathy', 'Rash'],
          alternatives: ['Streptomycin (if E not tolerated)']
        },
        {
          drug: 'Pyridoxine (Vitamin B6)',
          indication: '🟢 ADJUNCT - Prevents INH-induced peripheral neuropathy; given with INH',
          dose: '10-25 mg/day (up to 50 mg in high-risk patients)',
          route: 'Oral',
          duration: 'Throughout INH therapy',
          contraindications: ['Rare allergy'],
          sideEffects: ['Very well tolerated; neuropathy with very high doses'],
          alternatives: ['None needed if low neuropathy risk, but routinely recommended']
        },
        {
          drug: 'Dexamethasone / Prednisone',
          indication: '🔴 TB MENINGITIS - Reduces mortality and neurological sequelae; also used for pericardial TB',
          dose: 'TB Meningitis: Dexamethasone 0.4 mg/kg/day IV × 4 weeks, taper over 4 weeks; OR Prednisone 1-2 mg/kg/day',
          route: 'IV (dexamethasone initially) then oral',
          duration: '6-8 weeks with gradual taper',
          contraindications: ['Relative in uncontrolled DM, active other infection'],
          sideEffects: ['Hyperglycemia', 'Immunosuppression', 'GI bleeding', 'Mood changes', 'Cushingoid features'],
          alternatives: ['Prednisolone (equivalent dosing)']
        },
        {
          drug: 'MDR-TB Regimen (Bedaquiline-based)',
          indication: '🔴 DRUG-RESISTANT TB - For MDR-TB (resistant to INH + RIF); specialized regimen',
          dose: 'Individualized regimen; typically includes Bedaquiline + Linezolid + Fluoroquinolone (Levo/Moxi) + other agents. BPaLM regimen (Bedaquiline, Pretomanid, Linezolid ± Moxifloxacin) for 6 months in some settings',
          route: 'Oral (most drugs); injectable may be added',
          duration: '9-20 months depending on regimen',
          contraindications: ['Varies by drug; QT prolongation concerns with Bdq, Mfx'],
          sideEffects: ['QT prolongation (Bdq, Mfx)', 'Myelosuppression (Lzd)', 'Peripheral neuropathy (Lzd)', 'Optic neuritis (Lzd)', 'Hepatotoxicity', 'Tendinopathy (FQ)'],
          alternatives: ['Individualized based on DST results']
        },
        {
          drug: 'TB Preventive Therapy (TPT): Isoniazid Preventive Therapy (IPT)',
          indication: '🟢 LATENT TB - For PLHIV, child contacts <5 years, other high-risk groups with LTBI',
          dose: 'INH 5 mg/kg/day (max 300 mg) + Pyridoxine 25 mg/day × 6 months; OR 3HP (INH + Rifapentine weekly × 12 weeks)',
          route: 'Oral',
          duration: '6-9 months (INH); 12 weeks (3HP); 4 months (4R)',
          contraindications: ['Active TB (must be excluded)', 'Active hepatitis', 'INH allergy'],
          sideEffects: ['Hepatotoxicity', 'Peripheral neuropathy'],
          alternatives: ['3HP (INH+Rifapentine), 4R (Rifampicin 4 months), 3HR (INH+Rifampicin 3 months)']
        },
        {
          drug: 'Streptomycin',
          indication: '🟠 SECOND-LINE INJECTABLE - Reserved for retreatment or when first-line drugs contraindicated',
          dose: '15 mg/kg/day IM (max 1g); reduce in elderly and renal impairment',
          route: 'IM (or IV)',
          duration: '2 months (intensive phase) if used',
          contraindications: ['Renal impairment', 'Pregnancy (ototoxicity to fetus)', 'Previous streptomycin toxicity'],
          sideEffects: ['Ototoxicity (vestibular > cochlear)', 'Nephrotoxicity', 'Injection site pain'],
          alternatives: ['Amikacin', 'Capreomycin (second-line)']
        }
      ],
      nonPharmacological: [
        'DIRECTLY OBSERVED THERAPY (DOT): Healthcare worker or trained supporter observes medication ingestion; essential for adherence',
        'NUTRITIONAL SUPPORT: High-calorie, high-protein diet; address micronutrient deficiencies; therapeutic feeding if malnourished',
        'INFECTION CONTROL AT HOME: Ventilation, cough hygiene, separate sleeping initially, mask for patient',
        'CONTACT TRACING: Screen household and close contacts for symptoms; TST/IGRA for LTBI',
        'SMOKING CESSATION: Smoking worsens TB outcomes; strongly encourage quitting',
        'ALCOHOL CESSATION: Alcohol increases hepatotoxicity risk and non-adherence',
        'DIABETES CONTROL: Optimize glucose control; improves TB outcomes',
        'PATIENT EDUCATION: Explain treatment duration, importance of adherence, side effects to report',
        'PSYCHOSOCIAL SUPPORT: Address stigma; support for mental health; enablers (transport, food support)'
      ],
      surgical: [
        'SURGERY RARELY NEEDED for uncomplicated TB',
        'LUNG RESECTION: For localized MDR/XDR-TB not responding to medical therapy; destroyed lung; massive hemoptysis',
        'DRAINAGE OF ABSCESS: Large cold abscesses (spinal, psoas); may be aspirated or surgically drained',
        'SPINAL SURGERY: For Pott disease with cord compression, instability, or neurological deficit; debridement and stabilization',
        'PERICARDECTOMY: For constrictive pericarditis following TB pericarditis',
        'VP SHUNT: For TB meningitis with hydrocephalus',
        'LYMPH NODE EXCISION: Occasionally for diagnosis or if not responding to therapy'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE: TB meningitis, miliary TB, respiratory failure, severe illness - tertiary care',
        '🔴 IMMEDIATE: Suspected or confirmed MDR-TB - specialized MDR-TB treatment center',
        '🟠 URGENT: HIV co-infection requiring ART initiation - HIV clinic integration',
        '🟠 URGENT: Severe drug reactions (hepatotoxicity, severe rash, Stevens-Johnson) - hospitalization',
        '🟠 URGENT: Treatment failure (smear positive at 5 months or later) - investigate for resistance',
        '🟠 URGENT: Extrapulmonary TB at critical sites (spinal cord compression, pericardial) - specialist input',
        '🟡 ROUTINE: All cases should be managed within national TB program structures',
        '🟡 ROUTINE: Pediatric TB - may need specialist pediatric input'
      ],
      monitoring: [
        'SPUTUM SMEAR: At baseline, end of month 2, end of month 5 (or 6 if extended). Smear should convert negative by month 2',
        'CLINICAL ASSESSMENT: Monthly; weight, symptom review, adherence, side effects',
        'LIVER FUNCTION: Baseline; repeat if symptoms of hepatotoxicity (nausea, vomiting, jaundice, RUQ pain)',
        'VISUAL ACUITY: Baseline and monthly if on ethambutol (Snellen chart, color vision)',
        'HIV VIRAL LOAD AND CD4: Per HIV guidelines if co-infected',
        'CHEST X-RAY: Baseline; repeat at end of treatment to document resolution',
        'DRUG SUSCEPTIBILITY: If culture positive; if smear still positive at month 2',
        'RENAL FUNCTION: Baseline; monitor if on streptomycin or aminoglycosides',
        'BLOOD GLUCOSE: Baseline; monitor in diabetics'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
TREATMENT PHASES - DRUG-SUSCEPTIBLE TB
═══════════════════════════════════════════════════════════════════════════════

INTENSIVE PHASE (2 months):
• 4 drugs: Isoniazid + Rifampicin + Pyrazinamide + Ethambutol (HRZE)
• Daily dosing (DOT strongly recommended)
• Goal: Rapidly kill actively multiplying bacilli; render patient non-infectious
• Expected: Sputum smear conversion by end of month 2 in >85%

CONTINUATION PHASE (4 months):
• 2 drugs: Isoniazid + Rifampicin (HR)
• Daily dosing (some programs use thrice weekly)
• Goal: Eliminate persistent/dormant bacilli; prevent relapse

TOTAL DURATION: 6 months

═══════════════════════════════════════════════════════════════════════════════
EXTENDED TREATMENT DURATIONS
═══════════════════════════════════════════════════════════════════════════════

9 MONTHS TOTAL:
• TB meningitis (consider 12 months)
• Bone and joint TB
• Cavitary pulmonary TB with delayed sputum conversion

12 MONTHS OR LONGER:
• MDR-TB (9-20 months depending on regimen)
• XDR-TB

═══════════════════════════════════════════════════════════════════════════════
TB-HIV CO-INFECTION: ART TIMING
═══════════════════════════════════════════════════════════════════════════════

• START ART IN ALL TB-HIV PATIENTS regardless of CD4 count

TIMING:
• CD4 <50 or severe illness: Start ART within 2 weeks of TB treatment
• CD4 ≥50: Start ART within 8 weeks of TB treatment
• TB meningitis: Delay ART to 4-8 weeks (reduce IRIS risk)

DRUG INTERACTIONS:
• Rifampicin is potent CYP450 inducer
• Use Efavirenz or Dolutegravir (dose-adjusted) with rifampicin
• Avoid most PIs with rifampicin; can use rifabutin instead

IMMUNE RECONSTITUTION INFLAMMATORY SYNDROME (IRIS):
• Paradoxical worsening after starting ART
• Fever, lymphadenopathy, worsening infiltrates
• Usually self-limited; continue TB treatment and ART
• Steroids if severe

═══════════════════════════════════════════════════════════════════════════════
TREATMENT OUTCOMES (WHO DEFINITIONS)
═══════════════════════════════════════════════════════════════════════════════

• CURED: Smear-negative at completion and at least one prior occasion
• TREATMENT COMPLETED: Finished treatment without smear results
• TREATMENT FAILED: Smear-positive at month 5 or later
• DIED: Died during treatment
• LOST TO FOLLOW-UP: Interrupted treatment ≥2 consecutive months
• NOT EVALUATED: Transferred out; outcome unknown

═══════════════════════════════════════════════════════════════════════════════
POST-TREATMENT FOLLOW-UP
═══════════════════════════════════════════════════════════════════════════════

AFTER TREATMENT COMPLETION:
• Counsel on symptoms of relapse
• No routine follow-up sputum required
• Seek care if symptoms recur (cough, weight loss, fever)
• Relapse most common in first 2 years

RELAPSE MANAGEMENT:
• Obtain sputum for smear, culture, DST
• Do NOT restart treatment empirically without specimens
• Treat as retreatment case; rule out drug resistance

⚠️ Individual treatment plans should be guided by the national TB program and treating physician.`
    },

    redFlags: [
      '🔴 CRITICAL: TB meningitis (headache, altered consciousness, neck stiffness, cranial nerve palsies) - HIGH MORTALITY; start treatment immediately',
      '🔴 CRITICAL: Miliary/disseminated TB with respiratory failure - ICU admission; start treatment urgently',
      '🔴 CRITICAL: Massive hemoptysis (>500 mL/24h) - Life-threatening; airway management; may need embolization/surgery',
      '🔴 CRITICAL: Suspected MDR/XDR-TB (prior treatment failure, contact with DR-TB) - Obtain DST; specialized center',
      '🟠 URGENT: TB pericarditis with effusion/tamponade - Pericardiocentesis may be needed; start treatment + steroids',
      '🟠 URGENT: Spinal TB with neurological deficit - MRI; urgent surgical consultation; may need decompression',
      '🟠 URGENT: Drug-induced hepatotoxicity (ALT >5x ULN or jaundice) - Stop all hepatotoxic drugs; investigate',
      '🟠 URGENT: Severe drug hypersensitivity (Stevens-Johnson, DRESS) - Stop all drugs; supportive care',
      '🟠 URGENT: Smear positive at end of month 2 - Assess adherence; repeat smear; consider DST for resistance',
      '🟠 URGENT: Clinical deterioration on treatment - Consider IRIS (if on ART), drug resistance, other diagnosis',
      '🟠 URGENT: HIV-positive with very low CD4 (<50) - High mortality; expedite ART; screen for other OIs'
    ],

    complications: [
      'MILIARY TB: Disseminated disease; multiorgan involvement; high mortality',
      'TB MENINGITIS: Highest mortality form; hydrocephalus, stroke, cranial nerve palsies; neurological sequelae',
      'RESPIRATORY FAILURE: Extensive parenchymal disease; ARDS; may require ventilation',
      'MASSIVE HEMOPTYSIS: Erosion into pulmonary artery (Rasmussen aneurysm); life-threatening',
      'PNEUMOTHORAX: Rupture of cavity or subpleural bleb',
      'BRONCHIECTASIS: Post-TB lung damage; recurrent infections',
      'PULMONARY FIBROSIS: Extensive scarring; chronic respiratory impairment',
      'ASPERGILLOMA: Fungal ball in TB cavity; may cause hemoptysis',
      'CONSTRICTIVE PERICARDITIS: Following TB pericarditis; may need pericardectomy',
      'EMPYEMA NECESSITATIS: TB empyema tracking through chest wall',
      'SPINAL CORD COMPRESSION: From Pott disease; neurological emergency',
      'DRUG-INDUCED HEPATOTOXICITY: INH, RIF, PZA all hepatotoxic; may be severe',
      'PARADOXICAL REACTION/IRIS: Worsening on treatment, especially with ART',
      'MDR-TB DEVELOPMENT: From non-adherence or inadequate treatment; difficult to treat'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

DRUG-SUSCEPTIBLE TB:
• Cure rate: >85-90% with proper treatment
• Treatment success depends on adherence
• Mortality: <5% if properly treated (higher with HIV)

TB-HIV CO-INFECTION:
• Higher mortality than TB or HIV alone
• Improved dramatically with ART
• Mortality: 10-20% even with treatment (higher with low CD4)

DRUG-RESISTANT TB:
• MDR-TB: Treatment success 55-70% globally
• XDR-TB: Treatment success 30-50%
• Mortality higher; treatment longer and more toxic

EXTRAPULMONARY TB:
• Generally good prognosis with treatment
• TB meningitis: 20-50% mortality; 25% neurological sequelae
• Pericardial TB: 15-25% develop constriction

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Drug-susceptible TB
• Immunocompetent (HIV-negative or well-controlled HIV)
• Early diagnosis
• Good adherence to treatment
• Good nutritional status
• Limited disease extent
• Sputum conversion by month 2

UNFAVORABLE:
• Drug-resistant TB (MDR, XDR)
• HIV co-infection with low CD4
• Extensive or cavitary disease
• Delayed diagnosis
• Poor adherence
• Malnutrition
• Older age
• Diabetes
• Smear positive at month 2
• TB meningitis or miliary TB

═══════════════════════════════════════════════════════════════════════════════
RELAPSE
═══════════════════════════════════════════════════════════════════════════════

• Overall relapse rate: 3-5% after successful treatment
• Most relapses occur within 2 years
• Risk factors: Cavitary disease, extensive disease, HIV, poor adherence
• Must distinguish relapse (same strain) from reinfection (new strain)
• Obtain DST if relapse suspected

⚠️ Individual prognosis depends on multiple factors. Discuss with treating physician.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

BCG VACCINATION:
• Live attenuated vaccine from M. bovis
• Given at birth in high-burden countries
• Protects against severe childhood forms (meningitis, miliary)
• Limited protection against adult pulmonary TB
• Does not prevent infection, only severe disease in children
• Single dose; no booster recommended

INFECTION CONTROL:
• Early identification and treatment of cases (most important measure)
• Respiratory isolation of infectious patients
• Adequate ventilation (natural or mechanical)
• UV germicidal irradiation (healthcare facilities)
• N95 respirators for healthcare workers
• Surgical masks for infectious patients
• Cough hygiene education

REDUCE TRANSMISSION:
• Prompt diagnosis and treatment of active TB
• Contact tracing and screening
• Treatment of LTBI in high-risk contacts
• Improved housing and ventilation
• Reduce overcrowding

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION - TB PREVENTIVE THERAPY (TPT)
═══════════════════════════════════════════════════════════════════════════════

WHO-RECOMMENDED HIGH-PRIORITY GROUPS:
• PLHIV (all, regardless of LTBI test)
• Household contacts of TB patients (especially <5 years, PLHIV)
• Patients initiating anti-TNF therapy
• Patients on dialysis
• Patients preparing for organ transplant
• Patients with silicosis

OTHER CANDIDATES FOR TPT:
• Healthcare workers with new LTBI conversion
• Prisoners in high-burden settings
• Immigrants from high-burden countries
• Others with positive TST/IGRA and risk factors

TPT REGIMENS:
• 6H: Isoniazid daily × 6 months
• 9H: Isoniazid daily × 9 months
• 3HP: Isoniazid + Rifapentine weekly × 12 weeks (3 months)
• 4R: Rifampicin daily × 4 months
• 3HR: Isoniazid + Rifampicin daily × 3 months
• 1HP: Isoniazid + Rifapentine daily × 1 month (new option)

BEFORE STARTING TPT:
• MUST exclude active TB (symptoms, CXR)
• Screen for contraindications (hepatitis, drug interactions)
• Counsel on adherence and side effects

═══════════════════════════════════════════════════════════════════════════════
INFECTION CONTROL IN HEALTHCARE SETTINGS
═══════════════════════════════════════════════════════════════════════════════

ADMINISTRATIVE CONTROLS:
• Triage patients with cough
• Rapid diagnosis (GeneXpert)
• Separate waiting areas for cough patients
• Infection control training for staff
• Regular screening of healthcare workers

ENVIRONMENTAL CONTROLS:
• Maximize natural ventilation
• Mechanical ventilation (negative pressure rooms for high-risk procedures)
• UVGI (upper-room ultraviolet germicidal irradiation)
• Directional airflow

PERSONAL PROTECTIVE EQUIPMENT:
• N95 respirators (fit-tested) for healthcare workers
• Surgical masks for patients
• Fit-testing and training programs

═══════════════════════════════════════════════════════════════════════════════
END TB STRATEGY (WHO)
═══════════════════════════════════════════════════════════════════════════════

TARGETS BY 2035:
• 95% reduction in TB deaths (compared to 2015)
• 90% reduction in TB incidence
• Zero catastrophic costs for TB-affected families

PILLARS:
1. Integrated patient-centered care and prevention
2. Bold policies and supportive systems
3. Intensified research and innovation`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS TUBERCULOSIS?
═══════════════════════════════════════════════════════════════════════════════

Tuberculosis (TB) is an infection caused by bacteria called Mycobacterium tuberculosis. It usually affects the lungs but can affect other parts of the body.

TB spreads through the air when a person with TB in their lungs coughs, sneezes, or speaks. You cannot get TB from:
• Shaking hands
• Sharing food or drinks
• Touching bed linens or toilet seats
• Kissing

TB IS CURABLE with proper treatment.

═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS - WHEN TO SEEK CARE
═══════════════════════════════════════════════════════════════════════════════

See a healthcare provider if you have:

🔴 Cough lasting more than 2-3 weeks
🔴 Coughing up blood (even a small amount)
🔴 Unexplained weight loss
🔴 Night sweats (drenching, requiring clothes change)
🔴 Fever, especially in the evening
🔴 Loss of appetite
🔴 Feeling very tired
🔴 Chest pain

These symptoms can also be caused by other conditions, but TB should be checked.

═══════════════════════════════════════════════════════════════════════════════
YOUR TREATMENT
═══════════════════════════════════════════════════════════════════════════════

THE MOST IMPORTANT THING: Take ALL your medications, EVERY day, for the FULL course (usually 6 months). Stopping early or missing doses can:
• Make you sick again
• Make the TB harder to treat (drug resistance)
• Spread TB to your family and community

YOUR MEDICINES:
• You will take several medicines together
• Take them exactly as prescribed
• Take at the same time each day, preferably on an empty stomach
• Do NOT skip doses
• Do NOT stop early, even if you feel better

DIRECTLY OBSERVED THERAPY (DOT):
• A healthcare worker may watch you take your medicines
• This helps ensure you take them correctly
• This is normal and helps you get cured

═══════════════════════════════════════════════════════════════════════════════
POSSIBLE SIDE EFFECTS - WHAT TO REPORT
═══════════════════════════════════════════════════════════════════════════════

Tell your healthcare provider IMMEDIATELY if you experience:

🔴 Yellowing of skin or eyes (jaundice)
🔴 Dark urine
🔴 Nausea or vomiting that doesn't stop
🔴 Abdominal pain
🔴 Blurred vision or changes in color vision
🔴 Rash or itching
🔴 Tingling or numbness in hands or feet
🔴 Fever

NORMAL EFFECTS (not dangerous):
• Orange/red color of urine, tears, sweat (from rifampicin) - this is normal
• Mild stomach upset - try taking with a small snack

═══════════════════════════════════════════════════════════════════════════════
PROTECTING YOUR FAMILY AND COMMUNITY
═══════════════════════════════════════════════════════════════════════════════

WHILE YOU ARE INFECTIOUS (first 2-3 weeks of treatment):
• Cover your mouth when you cough or sneeze
• Open windows for fresh air in your home
• Sleep alone if possible
• Do not share utensils (this is more for comfort; TB doesn't spread this way)
• Wear a mask when around others, especially children and elderly

YOUR FAMILY AND CLOSE CONTACTS:
• They should be tested for TB
• Children under 5 and people with HIV are at highest risk
• They may be given preventive medicine

AFTER 2-3 WEEKS OF TREATMENT:
• You are usually no longer infectious if taking medicines correctly
• You can resume normal activities
• Complete your treatment to stay healthy

═══════════════════════════════════════════════════════════════════════════════
HIV AND TB
═══════════════════════════════════════════════════════════════════════════════

• TB and HIV often occur together
• All TB patients should be tested for HIV
• If you have both, both can be treated successfully
• Taking your HIV medicines (ART) and TB medicines is very important
• Tell your healthcare provider all the medicines you take

═══════════════════════════════════════════════════════════════════════════════
SUPPORT YOUR RECOVERY
═══════════════════════════════════════════════════════════════════════════════

• Eat well - nutritious food helps you heal
• Stop smoking and avoid alcohol
• Get adequate rest
• Keep all your clinic appointments
• Join a support group if available
• TB is not a reason for shame - many people get TB and are cured

═══════════════════════════════════════════════════════════════════════════════
REMEMBER
═══════════════════════════════════════════════════════════════════════════════

✅ TB is CURABLE
✅ Take ALL your medicines for the FULL course
✅ Never stop treatment early, even if you feel better
✅ Get your family tested
✅ You can live a normal life after treatment

Your healthcare team is here to support you. Ask questions if you don't understand something.`,

    references: [
      'World Health Organization. WHO consolidated guidelines on tuberculosis. Module 4: treatment - drug-susceptible tuberculosis treatment. Geneva: WHO; 2022.',
      'World Health Organization. WHO operational handbook on tuberculosis. Module 4: treatment - drug-susceptible tuberculosis treatment. Geneva: WHO; 2022.',
      'World Health Organization. WHO consolidated guidelines on tuberculosis. Module 4: treatment - drug-resistant tuberculosis treatment. Geneva: WHO; 2022.',
      'Nahid P, Dorman SE, Alipanah N, et al. Official American Thoracic Society/Centers for Disease Control and Prevention/Infectious Diseases Society of America Clinical Practice Guidelines: Treatment of Drug-Susceptible Tuberculosis. Clin Infect Dis. 2016;63(7):e147-e195.',
      'Getahun H, Matteelli A, Chaisson RE, Raviglione M. Latent Mycobacterium tuberculosis infection. N Engl J Med. 2015;372(22):2127-2135.',
      'Thwaites GE, van Toorn R, Schoeman J. Tuberculous meningitis: more questions, still too few answers. Lancet Neurol. 2013;12(10):999-1010.',
      'Lawn SD, Zumla AI. Tuberculosis. Lancet. 2011;378(9785):57-72.',
      'World Health Organization. Global tuberculosis report 2023. Geneva: WHO; 2023.',
      'Churchyard G, Kim P, Shah NS, et al. What We Know About Tuberculosis Transmission: An Overview. J Infect Dis. 2017;216(suppl_6):S629-S635.',
      'Harries AD, Kumar AMV, Satyanarayana S, et al. Tuberculosis in Africa - Battles won, but the war continues. S Afr Med J. 2020;110(11):1093-1097. [African context]'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 29. PRETERM LABOR
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '29',
    name: 'Preterm Labor',
    slug: 'preterm-labor',
    specialty: 'Obstetrics',
    bodySystem: 'Endocrine',
    icdCode: 'O60.0',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Preterm labor is defined as regular uterine contractions resulting in cervical change before 37 weeks of gestation. Preterm birth is the leading cause of neonatal mortality and morbidity worldwide and is particularly devastating in low-resource settings where neonatal intensive care is limited. Management focuses on delaying delivery to allow administration of antenatal corticosteroids for fetal lung maturity and magnesium sulfate for neuroprotection, while treating underlying causes and preparing for potential preterm delivery.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DEFINITION: Regular contractions + cervical change at 20+0 to 36+6 weeks
• VIABILITY: Generally 24 weeks in well-resourced settings; varies by context (may be 28+ weeks in limited-resource settings)
• ANTENATAL CORTICOSTEROIDS: Single course for 24-34 weeks; reduces RDS, IVH, NEC, death
• BETAMETHASONE: 12 mg IM × 2 doses, 24 hours apart (preferred) OR Dexamethasone 6 mg IM × 4 doses, 12 hours apart
• MAGNESIUM SULFATE: For neuroprotection if <32 weeks AND delivery expected within 24 hours
• TOCOLYTICS: Buy time for steroids/transfer (48 hours); NOT shown to improve neonatal outcomes long-term
• NIFEDIPINE: First-line tocolytic in most settings (oral, effective, safe)
• DO NOT TOCOLYSE: Chorioamnionitis, severe preeclampsia/eclampsia, placental abruption, fetal death, fetal distress, advanced labor (>4-5 cm)
• GBS PROPHYLAXIS: If GBS positive, unknown status, or risk factors - penicillin/ampicillin in labor
• CERVICAL LENGTH: <25 mm on transvaginal US increases preterm birth risk
• FETAL FIBRONECTIN (fFN): Negative fFN (intact membranes, <34 weeks, 1-3 cm) = >95% will NOT deliver in 7-14 days
• TRANSFER: If <32-34 weeks, transfer to facility with NICU if possible
• PROGESTERONE: For prevention in high-risk women, NOT for acute treatment of preterm labor

═══════════════════════════════════════════════════════════════════════════════
DEFINITIONS
═══════════════════════════════════════════════════════════════════════════════

PRETERM LABOR:
• Regular uterine contractions (≥4 in 20 minutes or ≥8 in 60 minutes)
• PLUS cervical change (dilation ≥1 cm and/or effacement ≥80%)
• OR cervix ≥3 cm dilated or ≥80% effaced regardless of contraction pattern
• At 20+0 to 36+6 weeks gestation

THREATENED PRETERM LABOR:
• Contractions WITHOUT significant cervical change
• May or may not progress to true preterm labor

PRETERM BIRTH CATEGORIES:
• Extremely preterm: <28 weeks
• Very preterm: 28-31+6 weeks
• Moderate preterm: 32-33+6 weeks
• Late preterm: 34-36+6 weeks

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

NORMAL LABOR INITIATION:
• Complex interplay of fetal, placental, and maternal factors
• Functional progesterone withdrawal
• Increased prostaglandin production
• Oxytocin receptor upregulation
• Cervical ripening (collagen remodeling)

PRETERM LABOR - COMMON PATHWAYS:
1. INFECTION/INFLAMMATION (25-40%):
   • Ascending infection from vagina/cervix (most common)
   • Chorioamnionitis (clinical or subclinical)
   • Systemic infection (UTI, pyelonephritis, pneumonia, malaria)
   • Periodontal disease
   • Inflammatory cytokines activate prostaglandin cascade

2. UTERINE OVERDISTENSION:
   • Multiple gestation
   • Polyhydramnios
   • Mechanical stretch activates contractile pathways

3. CERVICAL INSUFFICIENCY:
   • Structural weakness of cervix
   • Painless cervical dilation
   • Previous cervical surgery (cone biopsy, LEEP)
   • Congenital (DES exposure)

4. UTEROPLACENTAL ISCHEMIA/HEMORRHAGE:
   • Placental abruption
   • Preeclampsia/hypertensive disorders
   • Thrombophilias

5. MATERNAL STRESS:
   • Hypothalamic-pituitary-adrenal activation
   • Increased CRH and cortisol

6. IDIOPATHIC:
   • No identifiable cause in 40-50% of cases`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• INCIDENCE: ~15 million preterm births annually (11% of all births)
• LEADING CAUSE OF DEATH: In children <5 years; ~1 million deaths/year
• MORBIDITY: Major cause of long-term disability (cerebral palsy, developmental delay)
• TREND: Rates increasing in many countries
• DISPARITIES: Highest rates and mortality in low-resource settings

RISK FACTORS:
OBSTETRIC HISTORY:
• Previous preterm birth (strongest predictor; 15-30% recurrence)
• Previous second-trimester loss
• Previous cervical surgery (cone biopsy, LEEP)
• Short interpregnancy interval (<18 months)

CURRENT PREGNANCY:
• Multiple gestation (50% of twins deliver preterm)
• Polyhydramnios
• Uterine anomalies (bicornuate, septate)
• Short cervical length (<25 mm before 24 weeks)
• Placenta previa, placental abruption
• Preeclampsia

MATERNAL FACTORS:
• Extremes of age (<17 or >35 years)
• Low pre-pregnancy BMI
• African descent (2x risk in US data)
• Low socioeconomic status
• Smoking, substance use
• Periodontal disease
• Chronic stress, depression
• Intimate partner violence

INFECTIONS:
• Bacterial vaginosis
• Asymptomatic bacteriuria/UTI
• Sexually transmitted infections
• Chorioamnionitis
• Systemic infections (malaria, HIV)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HIGHEST RATES GLOBALLY: Sub-Saharan Africa has highest preterm birth rates (12-18%)
• HIGHEST MORTALITY: >90% of extremely preterm babies die (vs <10% in high-income countries)
• LEADING CAUSE OF NEONATAL DEATH: Preterm birth complications account for 35% of neonatal deaths

REGIONAL DATA:
• Malawi, Comoros, Congo, Zimbabwe: >16% preterm birth rate
• Nigeria: >12% preterm rate; largest absolute numbers
• South Africa: Better outcomes with more NICU capacity

CONTRIBUTING FACTORS IN AFRICA:
• HIGH INFECTION BURDEN:
  - Malaria in pregnancy (causes preterm birth)
  - HIV (higher preterm rates)
  - Bacterial vaginosis (high prevalence)
  - Sexually transmitted infections
  - Lack of treatment for asymptomatic bacteriuria

• MATERNAL FACTORS:
  - Malnutrition and anemia
  - Young maternal age (adolescent pregnancy)
  - Short interpregnancy intervals
  - Limited antenatal care
  - Heavy physical labor during pregnancy

• HEALTHCARE CHALLENGES:
  - Limited access to tocolytics
  - Corticosteroid availability and utilization gaps
  - Limited NICU capacity
  - Lack of CPAP and surfactant
  - Insufficient skilled providers
  - Delayed presentation
  - Transportation challenges

OUTCOMES:
• Viability threshold often 28 weeks (vs 24 weeks in high-resource settings)
• Survival at 28-32 weeks: 50-70% (vs >95% in high-income countries)
• High rates of disability among survivors
• Limited follow-up for developmental outcomes

OPPORTUNITIES FOR IMPACT:
• Antenatal corticosteroids are LOW-COST and HIGHLY EFFECTIVE
• Kangaroo mother care reduces mortality
• Basic respiratory support (CPAP) can save many lives
• Prevention strategies (progesterone, cerclage) underutilized`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
SYMPTOMS
═══════════════════════════════════════════════════════════════════════════════

UTERINE CONTRACTIONS:
• Regular, rhythmic contractions
• May be painful or painless ("tightening")
• Increasing in frequency and intensity
• ≥4 contractions in 20 minutes or ≥8 in 60 minutes

PELVIC PRESSURE:
• Sensation of heaviness or pressure in pelvis
• Feeling that baby is "pushing down"
• Rectal pressure

LOWER BACK PAIN:
• Constant or intermittent
• May radiate to abdomen
• Different from typical pregnancy back pain

VAGINAL DISCHARGE:
• Increase in vaginal discharge
• May be mucoid (mucus plug)
• Watery (concern for rupture of membranes)
• Bloody (bloody show)

OTHER:
• Abdominal cramping (menstrual-like)
• Intestinal cramping with or without diarrhea

⚠️ SYMPTOMS MAY BE SUBTLE: Many women do not recognize early preterm labor

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Temperature (fever suggests infection)
• Heart rate (tachycardia may indicate infection, dehydration, anxiety)
• Blood pressure (hypertension may indicate preeclampsia)

ABDOMINAL EXAMINATION:
• Fundal height (assess gestational age)
• Palpate contractions (frequency, duration, intensity)
• Uterine tenderness (suggests chorioamnionitis or abruption)
• Fetal lie and presentation

SPECULUM EXAMINATION (Before digital exam if PPROM suspected):
• Assess for pooling of amniotic fluid
• Cervical dilation visible
• Prolapsed cord or fetal parts
• Signs of infection (purulent discharge)
• Collect specimens (GBS, fFN, wet mount)

DIGITAL CERVICAL EXAMINATION:
⚠️ AVOID if rupture of membranes suspected (increases infection risk)
• Cervical dilation (cm)
• Effacement (%)
• Station (relationship of presenting part to ischial spines)
• Consistency (soft, firm)
• Position (posterior, mid, anterior)

BISHOP SCORE: Assesses cervical favorability
• Dilation: 0-3 cm (0-3 points)
• Effacement: 0-80%+ (0-3 points)
• Station: -3 to +2 (0-3 points)
• Consistency: Firm to soft (0-2 points)
• Position: Posterior to anterior (0-2 points)
• Score ≥8 favorable; higher scores = higher likelihood of progressing

═══════════════════════════════════════════════════════════════════════════════
CLINICAL DIAGNOSIS OF PRETERM LABOR
═══════════════════════════════════════════════════════════════════════════════

DIAGNOSIS REQUIRES:
1. Gestational age 20+0 to 36+6 weeks
2. Regular uterine contractions (documented by palpation or tocometry)
3. Cervical change:
   • Dilation ≥1 cm OR
   • Effacement ≥80% OR
   • Cervix ≥3 cm dilated regardless of change

ALTERNATIVELY (if cervical exam not definitive):
• Positive fetal fibronectin + short cervical length supports diagnosis
• Serial cervical exams showing change

DIFFERENTIAL: Braxton-Hicks vs True Preterm Labor:

                    BRAXTON-HICKS          TRUE PRETERM LABOR
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Regularity          Irregular              Regular
Intensity           Mild, unchanged        Increasing
Duration            Variable               30-70 seconds, consistent
Relief              Rest, hydration        Does not resolve
Cervical change     No                     Yes
fFN (if done)       Usually negative       May be positive`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

STEP 1: CONFIRM GESTATIONAL AGE
• Review dating ultrasound (most accurate if done <14 weeks)
• Last menstrual period
• Clinical estimate (fundal height) if no dating available

STEP 2: ASSESS SYMPTOMS AND CONTRACTIONS
• Contraction pattern (frequency, duration, intensity)
• Associated symptoms (ROM, bleeding, fetal movement)

STEP 3: SPECULUM EXAMINATION FIRST
• Rule out PPROM (pooling, ferning, nitrazine)
• Visual cervical assessment
• Collect fFN swab (before digital exam, if available)

STEP 4: CERVICAL ASSESSMENT
• Digital exam (if no PPROM) OR
• Transvaginal ultrasound for cervical length

STEP 5: EVALUATE FOR UNDERLYING CAUSES
• Infection workup (urinalysis, cultures)
• Consider chorioamnionitis
• Rule out abruption, preeclampsia

STEP 6: FETAL ASSESSMENT
• Fetal heart rate monitoring (NST or continuous)
• Ultrasound (presentation, AFI, estimated fetal weight)

═══════════════════════════════════════════════════════════════════════════════
CERVICAL LENGTH MEASUREMENT
═══════════════════════════════════════════════════════════════════════════════

TRANSVAGINAL ULTRASOUND (TVU):
• Gold standard for cervical assessment
• Measure from internal os to external os
• Empty bladder
• Avoid excess pressure

INTERPRETATION:
• ≥30 mm: Low risk of preterm delivery
• 25-29 mm: Intermediate risk
• 20-24 mm: Increased risk
• <20 mm: High risk
• <15 mm: Very high risk
• Funneling (dilation of internal os): Additional risk factor

CERVICAL LENGTH + fFN:
• Combination improves prediction
• Cervical length ≥30 mm AND negative fFN: <5% will deliver within 7 days

═══════════════════════════════════════════════════════════════════════════════
FETAL FIBRONECTIN (fFN)
═══════════════════════════════════════════════════════════════════════════════

WHAT IT IS:
• Glycoprotein at decidual-chorionic interface
• "Glue" between membranes and uterine wall
• Normally absent in cervicovaginal secretions 22-34 weeks
• Presence suggests disruption → increased preterm birth risk

COLLECTION:
• Swab posterior vaginal fornix
• BEFORE digital exam or lubricant
• Intact membranes
• 24+0 to 34+6 weeks
• Dilation <3 cm
• No intercourse, vaginal exam, or ultrasound in prior 24 hours

INTERPRETATION:
• NEGATIVE (<50 ng/mL): High negative predictive value (>95%)
  - Very unlikely to deliver in next 7-14 days
  - Can reassure and avoid unnecessary interventions

• POSITIVE (≥50 ng/mL): Moderate positive predictive value (~20-40%)
  - Increased risk but most will NOT deliver preterm
  - Warrants closer monitoring and preparation

CLINICAL UTILITY:
• Best used to RULE OUT imminent delivery
• Negative test can avoid unnecessary hospitalization, tocolysis, steroids
• Limited availability in many African settings

═══════════════════════════════════════════════════════════════════════════════
EVALUATION FOR UNDERLYING CAUSES
═══════════════════════════════════════════════════════════════════════════════

ALWAYS ASSESS FOR:
• Urinary tract infection (urinalysis, urine culture)
• Sexually transmitted infections
• Bacterial vaginosis (wet mount)
• Chorioamnionitis (fever, uterine tenderness, fetal tachycardia, maternal tachycardia, purulent discharge)
• Preterm premature rupture of membranes (PPROM)
• Placental abruption
• Preeclampsia
• Cervical insufficiency
• Multiple gestation
• Polyhydramnios`,

    differentialDiagnosis: [
      'Braxton-Hicks Contractions - Irregular, not increasing, resolve with rest/hydration, no cervical change',
      'Round Ligament Pain - Sharp, brief pains with movement; no contractions; normal cervix',
      'Urinary Tract Infection - Dysuria, frequency, suprapubic pain; may trigger contractions; treat infection',
      'Preterm Premature Rupture of Membranes (PPROM) - May present with contractions; confirm ROM first',
      'Placental Abruption - Painful bleeding, uterine tenderness, fetal distress; contractions may occur',
      'Chorioamnionitis - Fever, uterine tenderness, fetal/maternal tachycardia; requires delivery not tocolysis',
      'Appendicitis - RLQ pain, fever, anorexia; may trigger uterine irritability',
      'Gastroenteritis - Abdominal cramping, diarrhea; may mimic contractions',
      'Constipation - Cramping, straining; common in pregnancy; no cervical change',
      'Nephrolithiasis - Severe flank/abdominal pain; hematuria; may trigger contractions',
      'Cervical Insufficiency - Painless cervical dilation; minimal contractions initially',
      'Preeclampsia with Severe Features - May present with epigastric pain; check BP, labs'
    ],

    investigations: [
      {
        name: 'Cervical Examination (Digital)',
        indication: '🔴 IMMEDIATE - Assess dilation, effacement, station; document change over time',
        availability: 'BASIC'
      },
      {
        name: 'Transvaginal Ultrasound (Cervical Length)',
        indication: '🔴 IMMEDIATE - Objective cervical assessment; <25 mm increases risk; helps guide management',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'External Tocodynamometry',
        indication: '🔴 IMMEDIATE - Document contraction frequency and pattern; confirm regular contractions',
        availability: 'BASIC'
      },
      {
        name: 'Fetal Heart Rate Monitoring (NST/CTG)',
        indication: '🔴 IMMEDIATE - Assess fetal well-being; rule out distress; evaluate for tachycardia (infection)',
        availability: 'BASIC'
      },
      {
        name: 'Fetal Fibronectin (fFN)',
        indication: '🟠 URGENT - High NPV; negative test reassures; useful if diagnosis uncertain, 24-34 weeks, <3 cm dilated',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urinalysis and Urine Culture',
        indication: '🔴 IMMEDIATE - Rule out UTI as triggering cause; treat if positive',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Leukocytosis suggests infection; anemia common; baseline before delivery',
        availability: 'BASIC'
      },
      {
        name: 'Group B Streptococcus (GBS) Culture',
        indication: '🟠 URGENT - Rectovaginal swab; guides intrapartum antibiotic prophylaxis; treat if positive or unknown',
        availability: 'BASIC'
      },
      {
        name: 'Vaginal Wet Mount / Gram Stain',
        indication: '🟠 URGENT - Diagnose bacterial vaginosis, trichomonas, candidiasis; treat if present',
        availability: 'BASIC'
      },
      {
        name: 'Speculum Exam for PPROM',
        indication: '🔴 IMMEDIATE - Before digital exam; pooling, nitrazine, ferning; rule out ruptured membranes',
        availability: 'BASIC'
      },
      {
        name: 'Obstetric Ultrasound',
        indication: '🟠 URGENT - Confirm presentation, EFW, AFI, placental location; rule out abruption',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🟡 If infection suspected - Elevated in chorioamnionitis; nonspecific',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Antibody Screen',
        indication: '🟠 URGENT - Preparation for possible delivery; Rh status',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies',
        indication: '🟡 If abruption or preeclampsia suspected - PT, PTT, fibrinogen',
        availability: 'BASIC'
      },
      {
        name: 'Comprehensive Metabolic Panel',
        indication: '🟡 Baseline - Renal and liver function; especially if preeclampsia suspected',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT if status unknown - Guides intrapartum management and neonatal care',
        availability: 'BASIC'
      },
      {
        name: 'Malaria RDT/Smear',
        indication: '🟠 URGENT in endemic areas - Malaria causes preterm labor; treat if positive',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM GESTATIONAL AGE - Dating ultrasound, LMP; critical for management decisions',
        '⚡ CONFIRM DIAGNOSIS - Document contractions AND cervical change; rule out false labor',
        '⚡ RULE OUT CONTRAINDICATIONS TO TOCOLYSIS - Chorioamnionitis, severe preeclampsia, abruption, fetal distress, advanced labor',
        '⚡ LEFT LATERAL POSITION - Improves uteroplacental perfusion',
        '⚡ IV ACCESS - For medications and hydration',
        '⚡ IV FLUIDS - Bolus 500-1000 mL crystalloid (may reduce contractions if dehydrated)',
        '⚡ FETAL MONITORING - Continuous FHR monitoring; assess well-being',
        '⚡ SPECULUM EXAM - Before digital exam; rule out PPROM; collect GBS and fFN if available',
        '⚡ ANTENATAL CORTICOSTEROIDS - If 24-34 weeks, give immediately; do not delay for other interventions',
        '⚡ TOCOLYSIS - If no contraindications and steroids not yet complete; nifedipine first-line',
        '⚡ MAGNESIUM SULFATE - If <32 weeks and delivery anticipated within 24 hours (neuroprotection)',
        '⚡ GBS PROPHYLAXIS - Start antibiotics if GBS positive, unknown, or in active labor',
        '⚡ INFECTION WORKUP - Urinalysis, cultures, wet mount; treat underlying infections',
        '⚡ CONSIDER TRANSFER - If <32-34 weeks and higher-level NICU not available locally',
        '⚡ NOTIFY NEONATAL TEAM - Prepare for potential preterm delivery'
      ],
      pharmacological: [
        {
          drug: 'Betamethasone',
          indication: '🔴 ANTENATAL CORTICOSTEROID - First-line; reduces RDS, IVH, NEC, neonatal death by 30-50%',
          dose: '12 mg IM every 24 hours × 2 doses (total 24 mg)',
          route: 'IM (intramuscular)',
          duration: '2 doses; optimal benefit 48 hours to 7 days after first dose',
          contraindications: ['Systemic fungal infection', 'Relative: Active TB, viral hepatitis (still give if indicated)'],
          sideEffects: ['Transient maternal hyperglycemia', 'Transient decrease in fetal movement (normal)', 'Increased infection risk with multiple courses'],
          alternatives: ['Dexamethasone (if betamethasone unavailable)']
        },
        {
          drug: 'Dexamethasone',
          indication: '🔴 ANTENATAL CORTICOSTEROID - Alternative to betamethasone; equivalent efficacy',
          dose: '6 mg IM every 12 hours × 4 doses (total 24 mg)',
          route: 'IM',
          duration: '4 doses over 48 hours',
          contraindications: ['Same as betamethasone'],
          sideEffects: ['Same as betamethasone'],
          alternatives: ['Betamethasone (preferred if available)']
        },
        {
          drug: 'Nifedipine',
          indication: '🔴 FIRST-LINE TOCOLYTIC - Calcium channel blocker; oral, effective, well-tolerated',
          dose: 'Loading: 20-30 mg PO, then 10-20 mg PO every 4-6 hours (max 180 mg/day). OR 10 mg PO every 20 min × 3 doses, then 20 mg PO every 4-6 hours',
          route: 'Oral (do NOT use sublingual - unpredictable absorption and hypotension)',
          duration: '48 hours (time for steroids); maintenance tocolysis not recommended',
          contraindications: ['Hypotension', 'Cardiac disease', 'Concurrent magnesium sulfate (relative - use caution)', 'Maternal hypersensitivity'],
          sideEffects: ['Flushing', 'Headache', 'Dizziness', 'Hypotension', 'Tachycardia', 'Peripheral edema'],
          alternatives: ['Indomethacin', 'Atosiban (not widely available)']
        },
        {
          drug: 'Indomethacin',
          indication: '🟠 SECOND-LINE TOCOLYTIC - Prostaglandin synthetase inhibitor; very effective',
          dose: 'Loading: 50-100 mg PO/PR, then 25-50 mg PO every 6 hours',
          route: 'Oral or rectal',
          duration: 'Maximum 48-72 hours; avoid beyond 32 weeks (ductal closure risk)',
          contraindications: ['Gestational age >32 weeks', 'Oligohydramnios', 'Renal impairment', 'Bleeding disorders', 'NSAID allergy', 'Asthma (some)'],
          sideEffects: ['Fetal: Oligohydramnios (reversible), premature ductal closure, NEC (with prolonged use)', 'Maternal: GI upset, increased bleeding risk'],
          alternatives: ['Nifedipine', 'Atosiban']
        },
        {
          drug: 'Magnesium Sulfate (for Neuroprotection)',
          indication: '🔴 NEUROPROTECTION - If <32 weeks and delivery expected within 24 hours; reduces cerebral palsy risk',
          dose: 'Loading: 4 g IV over 20-30 minutes, then Maintenance: 1 g/hour IV until delivery or 24 hours',
          route: 'IV',
          duration: 'Until delivery or up to 24 hours; discontinue if delivery not imminent',
          contraindications: ['Myasthenia gravis', 'Severe renal impairment', 'Hypocalcemia'],
          sideEffects: ['Flushing', 'Nausea', 'Muscle weakness', 'Respiratory depression (toxicity)', 'Loss of deep tendon reflexes (toxicity)', 'Neonatal respiratory depression'],
          alternatives: ['No good alternative for neuroprotection']
        },
        {
          drug: 'Magnesium Sulfate (as Tocolytic)',
          indication: '🟡 TOCOLYTIC (Less favored) - Historically used; less effective than nifedipine; more side effects',
          dose: 'Loading: 4-6 g IV over 20 minutes, then Maintenance: 2-3 g/hour IV',
          route: 'IV',
          duration: '24-48 hours',
          contraindications: ['Myasthenia gravis', 'Renal impairment', 'Concurrent nifedipine (use caution)'],
          sideEffects: ['Flushing', 'Lethargy', 'Muscle weakness', 'Pulmonary edema', 'Respiratory depression'],
          alternatives: ['Nifedipine (preferred tocolytic)']
        },
        {
          drug: 'Penicillin G (GBS Prophylaxis)',
          indication: '🔴 GBS PROPHYLAXIS - First-line for GBS-positive or unknown status in labor',
          dose: 'Loading: 5 million units IV, then 2.5-3 million units IV every 4 hours',
          route: 'IV',
          duration: 'Until delivery',
          contraindications: ['Penicillin allergy (use alternative)'],
          sideEffects: ['Allergic reaction', 'Anaphylaxis (rare)'],
          alternatives: ['Ampicillin', 'Cefazolin (if mild penicillin allergy)', 'Clindamycin or Vancomycin (if severe allergy)']
        },
        {
          drug: 'Ampicillin (GBS Prophylaxis)',
          indication: '🔴 GBS PROPHYLAXIS - Alternative to penicillin; also used if PPROM',
          dose: 'Loading: 2 g IV, then 1 g IV every 4 hours',
          route: 'IV',
          duration: 'Until delivery',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Allergic reaction', 'Diarrhea', 'Rash'],
          alternatives: ['Penicillin G', 'Cefazolin', 'Clindamycin']
        },
        {
          drug: 'Terbutaline',
          indication: '🟡 ACUTE TOCOLYSIS (Short-term only) - Beta-agonist; for acute tocolysis or intrauterine resuscitation',
          dose: '0.25 mg SC; may repeat in 15-30 minutes × 1',
          route: 'Subcutaneous (NOT for continuous IV use)',
          duration: 'Single dose or maximum 2 doses; NOT for maintenance',
          contraindications: ['Cardiac disease', 'Poorly controlled diabetes', 'Hyperthyroidism', 'Prolonged use'],
          sideEffects: ['Tachycardia (maternal and fetal)', 'Palpitations', 'Tremor', 'Hyperglycemia', 'Hypokalemia', 'Pulmonary edema', 'Cardiac arrhythmias'],
          alternatives: ['Nifedipine (preferred for ongoing tocolysis)']
        },
        {
          drug: 'Progesterone (Vaginal)',
          indication: '🟢 PREVENTION (Not acute treatment) - For women with short cervix (<25 mm) at 16-24 weeks or prior spontaneous PTB',
          dose: '200 mg vaginal suppository daily OR 90 mg vaginal gel daily',
          route: 'Vaginal',
          duration: 'From 16-24 weeks until 36 weeks (prevention)',
          contraindications: ['Active thromboembolic disease', 'Hormone-sensitive cancer', 'Liver disease'],
          sideEffects: ['Vaginal discharge', 'Local irritation'],
          alternatives: ['17-hydroxyprogesterone caproate IM weekly (for prior PTB)']
        },
        {
          drug: 'Antibiotics for Chorioamnionitis',
          indication: '🔴 IF CHORIOAMNIONITIS - Ampicillin + Gentamicin; delivery indicated, NOT tocolysis',
          dose: 'Ampicillin 2 g IV every 6 hours + Gentamicin 5 mg/kg IV daily (or divided dosing)',
          route: 'IV',
          duration: 'Until delivery and post-partum',
          contraindications: ['Known severe allergy (use alternatives)'],
          sideEffects: ['Allergic reaction', 'Nephrotoxicity (gentamicin)', 'Ototoxicity'],
          alternatives: ['Cefoxitin or other broad-spectrum coverage if allergic']
        }
      ],
      nonPharmacological: [
        'BED REST: No evidence of benefit for routine bed rest; may cause harm (VTE risk); individualize',
        'LEFT LATERAL POSITION: Improves uteroplacental blood flow',
        'HYDRATION: IV fluid bolus may reduce contractions if dehydrated; avoid overhydration',
        'AVOID NIPPLE STIMULATION: Can trigger oxytocin release',
        'PSYCHOSOCIAL SUPPORT: Anxiety and stress management; explain situation to patient and family',
        'SMOKING CESSATION: If still smoking, encourage cessation',
        'CERVICAL CERCLAGE: NOT for acute preterm labor; used prophylactically for cervical insufficiency'
      ],
      surgical: [
        'EMERGENCY CERCLAGE: Controversial; may be considered in select cases of cervical dilation without labor (rescue cerclage)',
        'CESAREAN SECTION: For obstetric indications (malpresentation at early gestation, fetal distress, failed induction, etc.)',
        'NOTE: Cesarean purely for prematurity is NOT indicated; vaginal delivery appropriate for most preterm infants in cephalic presentation'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE: Transfer to facility with NICU if <32-34 weeks and local facility cannot manage preterm infant',
        '🔴 IMMEDIATE: Imminent delivery of extremely preterm infant (<28 weeks) - tertiary center if possible',
        '🟠 URGENT: Multiple gestation with preterm labor',
        '🟠 URGENT: Preterm labor with complications (preeclampsia, abruption, infection)',
        '🟠 URGENT: Tocolysis failure or advanced cervical dilation',
        '🟡 CONSIDER: Any preterm labor <34 weeks benefits from higher-level care if available',
        '🟡 IN-UTERO TRANSFER: Safest way to transfer preterm infant is before delivery if time allows'
      ],
      monitoring: [
        'CONTINUOUS FHR MONITORING: Throughout active preterm labor; assess for distress',
        'CONTRACTION MONITORING: Tocodynamometry; assess frequency and response to tocolysis',
        'CERVICAL EXAM: Serial exams to assess progression (every 2-4 hours if active)',
        'VITAL SIGNS: Every 1-4 hours; watch for infection, tocolytic side effects',
        'INTAKE/OUTPUT: Especially if on IV magnesium (fluid overload risk)',
        'MAGNESIUM LEVELS: If on magnesium for tocolysis (target 4-8 mg/dL); check reflexes',
        'BLOOD GLUCOSE: If on corticosteroids, especially if diabetic',
        'FETAL MOVEMENT: Patient should report decreased movement'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
MANAGEMENT BY GESTATIONAL AGE
═══════════════════════════════════════════════════════════════════════════════

<24 WEEKS (PERIVIABLE):
• Survival rare without advanced NICU
• Discuss goals of care with family
• Consider comfort care vs active intervention based on resources and family wishes
• Steroids may be offered from 22-23 weeks in select settings

24-33+6 WEEKS:
• Full intervention: Steroids + Tocolysis + MgSO4 (if <32 weeks) + GBS prophylaxis
• Transfer to facility with appropriate NICU level
• Goal: Delay delivery at least 48 hours for steroid benefit

34-36+6 WEEKS (LATE PRETERM):
• Antenatal steroids recommended (ACOG 2016: single course at 34-36+6 if at risk of delivery within 7 days and no prior course)
• Tocolysis less beneficial but may be used for 48 hours for steroids
• GBS prophylaxis if indicated
• Better prognosis but still morbidity risk

37+ WEEKS:
• Not preterm; allow labor to progress if spontaneous
• No tocolysis indicated

═══════════════════════════════════════════════════════════════════════════════
CORTICOSTEROID DOSING
═══════════════════════════════════════════════════════════════════════════════

OPTIMAL TIMING:
• Maximum benefit: 48 hours to 7 days after first dose
• Still beneficial if delivered <48 hours after starting
• Benefit wanes after 7-14 days

SINGLE COURSE (STANDARD):
• Betamethasone 12 mg IM × 2 doses (24 hours apart) OR
• Dexamethasone 6 mg IM × 4 doses (12 hours apart)

REPEAT/RESCUE COURSE:
• Consider single repeat course if:
  - >7-14 days since prior course
  - <34 weeks
  - Again at high risk of preterm delivery within 7 days
• Multiple repeat courses NOT recommended (fetal growth concerns)

═══════════════════════════════════════════════════════════════════════════════
TOCOLYSIS PRINCIPLES
═══════════════════════════════════════════════════════════════════════════════

GOALS OF TOCOLYSIS:
• Delay delivery 48 hours for corticosteroid effect
• Allow time for transfer to appropriate facility
• NOT proven to improve long-term neonatal outcomes

DURATION:
• 48 hours maximum typically
• Maintenance tocolysis NOT recommended

CONTRAINDICATIONS TO TOCOLYSIS:
• Intrauterine infection (chorioamnionitis)
• Significant vaginal bleeding (abruption)
• Severe preeclampsia/eclampsia
• Fetal demise
• Lethal fetal anomaly
• Fetal compromise requiring delivery
• Advanced labor (>4-5 cm; futile)
• Maternal contraindication to specific agent

═══════════════════════════════════════════════════════════════════════════════
AFTER STABILIZATION
═══════════════════════════════════════════════════════════════════════════════

IF LABOR ARRESTED:
• Discontinue tocolysis after 48 hours
• Continue hospitalization or close outpatient follow-up based on risk
• Activity restriction (individualized; strict bed rest not evidence-based)
• Pelvic rest (no intercourse)
• Recognize signs of recurrent labor
• Weekly or more frequent visits until 37 weeks

IF DELIVERY OCCURS:
• Ensure pediatric/neonatal team present
• Delayed cord clamping (30-60 seconds) if infant vigorous
• Immediate neonatal care based on gestational age and condition
• Kangaroo mother care as soon as stable
• Breastfeeding support

⚠️ Management should be individualized based on clinical circumstances and available resources.`
    },

    redFlags: [
      '🔴 CRITICAL: Chorioamnionitis (fever, uterine tenderness, fetal/maternal tachycardia, purulent discharge) - Delivery indicated, NOT tocolysis',
      '🔴 CRITICAL: Placental abruption (painful bleeding, rigid uterus, fetal distress) - No tocolysis; prepare for delivery',
      '🔴 CRITICAL: Severe preeclampsia/eclampsia - Delivery may be indicated; MgSO4 for seizure prophylaxis, not tocolysis',
      '🔴 CRITICAL: Non-reassuring fetal status (prolonged decelerations, absent variability) - Delivery may be needed',
      '🔴 CRITICAL: Cord prolapse - Emergency cesarean section',
      '🔴 CRITICAL: Advanced cervical dilation (>5 cm) - Tocolysis unlikely to succeed; prepare for imminent delivery',
      '🟠 URGENT: Magnesium toxicity (loss of DTRs, respiratory depression) - Stop infusion; calcium gluconate if severe',
      '🟠 URGENT: Rupture of membranes - Adjust management; latency antibiotics; no fFN or cervical length measurement',
      '🟠 URGENT: Preterm labor at extremely preterm gestation (<24-26 weeks) - Goals of care discussion needed',
      '🟠 URGENT: Multiple repeat tocolytic failures - Delivery likely; ensure steroids given; prepare neonatal team',
      '🟠 URGENT: Suspected cervical insufficiency (painless dilation) - Consider cerclage if <24 weeks and membranes intact'
    ],

    complications: [
      'PRETERM BIRTH: Despite management, many will deliver preterm; counsel on neonatal outcomes',
      'RESPIRATORY DISTRESS SYNDROME (RDS): Due to surfactant deficiency; steroids reduce risk',
      'INTRAVENTRICULAR HEMORRHAGE (IVH): Brain bleeding in preterm infants; steroids and MgSO4 reduce risk',
      'NECROTIZING ENTEROCOLITIS (NEC): Bowel necrosis; more common in preterm; steroids reduce risk',
      'NEONATAL SEPSIS: Increased risk with preterm birth, PPROM, GBS',
      'RETINOPATHY OF PREMATURITY (ROP): Eye disease from oxygen therapy; screening needed',
      'BRONCHOPULMONARY DYSPLASIA (BPD): Chronic lung disease of prematurity',
      'CEREBRAL PALSY: Long-term neurological disability; MgSO4 provides neuroprotection',
      'DEVELOPMENTAL DELAY: Cognitive and motor delays; require follow-up',
      'CHORIOAMNIONITIS: May develop during prolonged labor or with tocolysis delay',
      'PLACENTAL ABRUPTION: May complicate or precipitate preterm labor',
      'TOCOLYTIC SIDE EFFECTS: Pulmonary edema (especially with MgSO4 + beta-agonists), hypotension, arrhythmias',
      'POSTPARTUM HEMORRHAGE: Risk with any delivery; be prepared'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
NEONATAL SURVIVAL BY GESTATIONAL AGE
═══════════════════════════════════════════════════════════════════════════════

HIGH-RESOURCE SETTINGS (with NICU):
• 22 weeks: 5-20% survival
• 23 weeks: 20-35% survival
• 24 weeks: 40-70% survival
• 25 weeks: 50-80% survival
• 26 weeks: 80-90% survival
• 28 weeks: >90% survival
• 32 weeks: >95% survival
• 34+ weeks: >99% survival

LOW-RESOURCE SETTINGS (limited NICU):
• <28 weeks: Very low survival (<10-20% in many settings)
• 28-32 weeks: 50-70% survival with good basic care
• 32-34 weeks: >80-90% survival
• 34+ weeks: Similar to term

⚠️ Survival rates highly dependent on local resources, staffing, and equipment.

═══════════════════════════════════════════════════════════════════════════════
IMPACT OF INTERVENTIONS
═══════════════════════════════════════════════════════════════════════════════

ANTENATAL CORTICOSTEROIDS:
• Reduces RDS by 34%
• Reduces IVH by 46%
• Reduces NEC by 54%
• Reduces neonatal death by 31%
• Benefit even in low-resource settings

MAGNESIUM SULFATE FOR NEUROPROTECTION:
• Reduces cerebral palsy by ~30-40%
• NNT ~63 to prevent one case of CP

TOCOLYSIS:
• Delays delivery by 48 hours to 7 days
• No proven long-term neonatal benefit
• Value is in allowing time for steroids and transfer

KANGAROO MOTHER CARE:
• Reduces mortality by up to 40% in low-resource settings
• Reduces hypothermia, sepsis
• Improves breastfeeding

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

EARLY PRETERM (<32 weeks):
• Higher risk of cerebral palsy, cognitive delay, learning disabilities
• Chronic lung disease
• Visual and hearing impairment
• Behavioral problems
• School difficulties

LATE PRETERM (34-36 weeks):
• Generally good long-term outcomes
• Slight increase in respiratory, feeding problems initially
• May have subtle learning differences

═══════════════════════════════════════════════════════════════════════════════
RECURRENCE RISK
═══════════════════════════════════════════════════════════════════════════════

• After one preterm birth: 15-30% recurrence risk
• After two preterm births: 40-50% recurrence risk
• Offer progesterone prophylaxis in subsequent pregnancies
• Cervical length screening at 16-24 weeks

⚠️ Individual prognosis depends on gestational age, available resources, and associated conditions.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

PRE-PREGNANCY:
• Optimize maternal health (weight, chronic diseases)
• Folic acid supplementation
• Smoking cessation
• Substance abuse treatment
• Adequate interpregnancy interval (>18 months)
• Screen and treat for STIs
• Dental care (treat periodontal disease)

DURING PREGNANCY:
• Early and regular antenatal care
• Screen and treat infections (UTI, bacterial vaginosis, STIs)
• Screen and treat asymptomatic bacteriuria
• Malaria prevention in endemic areas (IPTp, ITNs)
• Screen for preeclampsia and manage hypertension
• Smoking cessation support
• Nutritional support
• Identify and manage multiple gestations

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (High-Risk Women)
═══════════════════════════════════════════════════════════════════════════════

PROGESTERONE SUPPLEMENTATION:
• Indicated for:
  - History of prior spontaneous preterm birth
  - Short cervix (<25 mm) detected at 16-24 weeks
• Options:
  - Vaginal progesterone 200 mg daily (short cervix)
  - 17-hydroxyprogesterone caproate (17-OHPC) 250 mg IM weekly (prior PTB)
• Start at 16-20 weeks; continue until 36 weeks

CERVICAL CERCLAGE:
• History-indicated cerclage (12-14 weeks):
  - ≥3 prior preterm births or second-trimester losses
  - Classic cervical insufficiency history
• Ultrasound-indicated cerclage:
  - Prior preterm birth + cervical length <25 mm at <24 weeks
• Consider rescue cerclage for dilated cervix without labor (controversial)

CERVICAL PESSARY:
• Alternative to cerclage in some settings
• Less invasive
• Evidence mixed

CERVICAL LENGTH SCREENING:
• Transvaginal ultrasound at 18-24 weeks in high-risk women
• If <25 mm → offer progesterone ± cerclage

═══════════════════════════════════════════════════════════════════════════════
PREVENTION IN AFRICAN SETTINGS - PRIORITIES
═══════════════════════════════════════════════════════════════════════════════

HIGH-IMPACT, LOW-COST INTERVENTIONS:
• Antenatal care attendance (at least 4 visits)
• Screen and treat infections (UTI, STIs, BV)
• Malaria prevention (IPTp with SP, insecticide-treated nets)
• Screen and treat HIV
• Identify preeclampsia early
• Ensure corticosteroids available and used
• Train providers on corticosteroid indications

POLICY AND SYSTEMS:
• Commodity supply for steroids, tocolytics, antibiotics
• Establish referral pathways for preterm labor
• Strengthen neonatal care (KMC, basic resuscitation)
• Birth spacing and family planning education`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS PRETERM LABOR?
═══════════════════════════════════════════════════════════════════════════════

Preterm labor is when your body starts getting ready to give birth too early - before 37 weeks of pregnancy. A normal pregnancy lasts about 40 weeks.

Babies born too early (premature babies) may have problems because their organs are not fully developed. The earlier a baby is born, the more problems they may have.

The good news is that there are medicines and treatments that can help if you go into labor early.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - CALL YOUR PROVIDER IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

Go to the hospital or call your healthcare provider RIGHT AWAY if you have:

🔴 Regular contractions or tightening of your belly (more than 4 in one hour)
🔴 Cramping that feels like period pain
🔴 Constant low backache (different from your usual pregnancy backache)
🔴 Pressure in your pelvis or vagina
🔴 Fluid leaking from your vagina (your water may have broken)
🔴 Bleeding from your vagina
🔴 Change in vaginal discharge (more, different color, or mucus-like)

DO NOT WAIT to see if these symptoms go away. Early treatment gives your baby the best chance.

═══════════════════════════════════════════════════════════════════════════════
WHAT HAPPENS AT THE HOSPITAL
═══════════════════════════════════════════════════════════════════════════════

YOUR DOCTOR WILL:
• Check how often you are having contractions
• Examine your cervix (the opening to your womb)
• Do an ultrasound to check your baby
• Test your urine and blood
• Check if your water has broken

IF YOU ARE IN PRETERM LABOR, YOU MAY RECEIVE:
• Steroid injections (VERY IMPORTANT): Help your baby's lungs develop faster
• Medicine to slow or stop contractions
• Antibiotics to prevent infection
• Medicine to protect your baby's brain (if very early)

═══════════════════════════════════════════════════════════════════════════════
WHY STEROIDS ARE IMPORTANT
═══════════════════════════════════════════════════════════════════════════════

If you are between 24 and 34 weeks pregnant and may deliver early, you will receive steroid injections (usually in your arm or hip).

These steroids:
• Help your baby's lungs mature faster
• Reduce the chance of breathing problems
• Reduce the chance of brain bleeding
• Are NOT the same as steroids used by athletes
• Are SAFE for you and your baby

The steroids work best if your baby is born 2-7 days after you get them, but they help even if delivery happens sooner.

═══════════════════════════════════════════════════════════════════════════════
REDUCING YOUR RISK
═══════════════════════════════════════════════════════════════════════════════

You can help reduce your chance of preterm labor:

✅ Go to all your prenatal appointments
✅ Tell your doctor if you had a premature baby before
✅ Avoid smoking, alcohol, and drugs
✅ Eat healthy foods
✅ Drink plenty of water
✅ Report any signs of infection (burning with urination, unusual discharge)
✅ Manage stress - ask for help when you need it
✅ Take medicines as prescribed
✅ If you have contractions, rest on your left side and drink fluids

═══════════════════════════════════════════════════════════════════════════════
IF YOUR BABY IS BORN EARLY
═══════════════════════════════════════════════════════════════════════════════

WHAT TO EXPECT:
• Your baby may need to stay in the hospital until they are ready to go home
• They may need help breathing
• They may need to be in a special incubator to stay warm
• You can still bond with your baby - skin-to-skin contact (kangaroo care) is very good for premature babies
• Breast milk is the best food for your premature baby

KANGAROO MOTHER CARE:
• Hold your baby skin-to-skin on your chest
• This helps your baby stay warm, breathe better, and grow
• It helps you bond and make more breast milk
• Ask your nurses about kangaroo care

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR HEALTHCARE PROVIDER
═══════════════════════════════════════════════════════════════════════════════

• Am I in preterm labor?
• How far along is my pregnancy?
• Will I receive steroid injections?
• Is my baby okay?
• What is being done to delay delivery?
• What should I expect if my baby is born early?
• What can I do to help?

Do not be afraid to ask questions. You have the right to understand what is happening.

Your healthcare team wants to help you have the healthiest pregnancy and baby possible.`,

    references: [
      'American College of Obstetricians and Gynecologists. Practice Bulletin No. 171: Management of Preterm Labor. Obstet Gynecol. 2016;128(4):e155-e164.',
      'World Health Organization. WHO recommendations on interventions to improve preterm birth outcomes. Geneva: WHO; 2015.',
      'Roberts D, Brown J, Medley N, Dalziel SR. Antenatal corticosteroids for accelerating fetal lung maturation for women at risk of preterm birth. Cochrane Database Syst Rev. 2017;3(3):CD004454.',
      'Doyle LW, Crowther CA, Middleton P, et al. Magnesium sulphate for women at risk of preterm birth for neuroprotection of the fetus. Cochrane Database Syst Rev. 2009;(1):CD004661.',
      'Conde-Agudelo A, Romero R, Nicolaides KH, et al. Vaginal progesterone vs cervical cerclage for the prevention of preterm birth in women with a sonographic short cervix, previous preterm birth, and singleton gestation: a systematic review and indirect comparison metaanalysis. Am J Obstet Gynecol. 2013;208(1):42.e1-42.e18.',
      'Flenady V, Wojcieszek AM, Papatsonis DN, et al. Calcium channel blockers for inhibiting preterm labour and birth. Cochrane Database Syst Rev. 2014;(6):CD002255.',
      'ACOG Committee Opinion No. 713: Antenatal Corticosteroid Therapy for Fetal Maturation. Obstet Gynecol. 2017;130(2):e102-e109.',
      'Blencowe H, Cousens S, Oestergaard MZ, et al. National, regional, and worldwide estimates of preterm birth rates in the year 2010 with time trends since 1990 for selected countries: a systematic analysis and implications. Lancet. 2012;379(9832):2162-2172.',
      'Lawn JE, Davidge R, Paul VK, et al. Born too soon: care for the preterm baby. Reprod Health. 2013;10 Suppl 1(Suppl 1):S5.',
      'Mwansa-Kambafwile J, Cousens S, Hansen T, et al. Antenatal steroids in preterm labour for the prevention of neonatal deaths due to complications of preterm birth. Int J Epidemiol. 2010;39 Suppl 1:i122-133. [LMIC evidence]'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 30. HYPERTENSIVE EMERGENCY
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '30',
    name: 'Hypertensive Emergency',
    slug: 'hypertensive-emergency',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I16.1',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Hypertensive emergency is defined as severe hypertension (typically systolic BP >180 mmHg and/or diastolic BP >120 mmHg) with evidence of acute target organ damage. This is a life-threatening condition requiring immediate blood pressure reduction with intravenous antihypertensive therapy in a monitored setting. It must be distinguished from hypertensive urgency (severe hypertension WITHOUT acute organ damage), which can be managed more gradually. The goal is controlled BP reduction to prevent further organ damage while avoiding hypotension-induced ischemia.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• EMERGENCY vs URGENCY: Emergency = acute organ damage; Urgency = severe BP without organ damage
• TARGET ORGAN DAMAGE: Brain (encephalopathy, stroke), Heart (ACS, acute HF, aortic dissection), Kidneys (AKI), Eyes (papilledema, hemorrhages), Eclampsia
• DO NOT DROP BP TOO FAST: Risk of watershed infarction (brain, heart, kidneys)
• GENERAL RULE: Lower MAP by 20-25% in first hour, then gradually to 160/100-110 over 2-6 hours
• EXCEPTIONS: Aortic dissection (rapid reduction to SBP <120 and HR <60), ischemic stroke (more cautious), thrombolysis candidates
• IV MEDICATIONS: Required for true hypertensive emergency; oral for urgency
• FIRST-LINE AGENTS: Labetalol, Nicardipine, Nitroprusside (depending on clinical scenario)
• AVOID: Sublingual nifedipine (unpredictable, precipitous drops), aggressive hydralazine without beta-blocker
• AORTIC DISSECTION: Beta-blocker FIRST (reduce shear stress), then vasodilator; target SBP <120
• PREGNANCY (Eclampsia/Severe Preeclampsia): Labetalol, Hydralazine, or Nicardipine; MgSO4 for seizure prophylaxis; AVOID ACEi/ARB, nitroprusside
• MONITOR CLOSELY: ICU or high-dependency setting; arterial line if available; frequent neuro checks
• LOOK FOR CAUSE: Non-adherence, renal disease, renovascular, pheochromocytoma, drugs (cocaine, amphetamines)

═══════════════════════════════════════════════════════════════════════════════
DEFINITIONS
═══════════════════════════════════════════════════════════════════════════════

HYPERTENSIVE EMERGENCY:
• Severe hypertension (usually SBP >180 and/or DBP >120 mmHg)
• WITH evidence of acute, ongoing target organ damage
• Requires immediate IV therapy in monitored setting

HYPERTENSIVE URGENCY:
• Severe hypertension (SBP >180 and/or DBP >120 mmHg)
• WITHOUT evidence of acute target organ damage
• Can be managed with oral agents; gradual reduction over 24-48 hours
• Often related to non-adherence

MALIGNANT HYPERTENSION:
• Historical term
• Severe hypertension with retinal hemorrhages, exudates, and/or papilledema
• Often associated with renal dysfunction
• Considered a hypertensive emergency

═══════════════════════════════════════════════════════════════════════════════
PATHOPHYSIOLOGY
═══════════════════════════════════════════════════════════════════════════════

MECHANISMS OF ACUTE ELEVATION:
1. Failure of normal autoregulation
2. Mechanical stress on vessel walls
3. Endothelial dysfunction and injury
4. Activation of renin-angiotensin-aldosterone system
5. Oxidative stress and inflammation
6. Fibrinoid necrosis of arterioles

AUTOREGULATION:
• Normally, cerebral and renal blood flow maintained constant over wide BP range
• Chronic hypertension shifts autoregulatory curve rightward
• In hypertensive emergency, BP exceeds autoregulatory capacity
• Sudden BP drops can cause ischemia (autoregulation set for higher pressures)

TARGET ORGAN EFFECTS:

BRAIN:
• Hypertensive encephalopathy (cerebral edema, PRES)
• Intracerebral hemorrhage
• Ischemic stroke (less common as primary manifestation)

HEART:
• Acute coronary syndrome (increased myocardial oxygen demand)
• Acute left ventricular failure / Pulmonary edema
• Aortic dissection (shear stress on aortic wall)

KIDNEYS:
• Acute kidney injury (arteriolar injury, ischemia)
• Microangiopathic hemolytic anemia (severe cases)

EYES:
• Retinal hemorrhages, exudates
• Papilledema (optic disc swelling)
• Vision changes

VASCULATURE:
• Aortic dissection
• Endothelial damage
• Microangiopathic hemolytic anemia`,

    epidemiology: `═══════════════════════════════════════════════════════════════════════════════
GLOBAL BURDEN
═══════════════════════════════════════════════════════════════════════════════

• HYPERTENSION PREVALENCE: ~1.3 billion adults globally; leading risk factor for death
• HYPERTENSIVE EMERGENCY: 1-2% of hypertensive patients will experience at some point
• ED VISITS: Accounts for ~25% of all medical emergencies in some series
• MORTALITY: Untreated malignant hypertension has >90% 1-year mortality
• OUTCOMES: With treatment, 5-year survival improved dramatically (previously <1%)

RISK FACTORS FOR HYPERTENSIVE EMERGENCY:
• Non-adherence to antihypertensive medications (most common precipitant)
• Inadequate or no treatment of hypertension
• Chronic kidney disease
• Renovascular disease
• Illicit drug use (cocaine, amphetamines, PCP)
• Pheochromocytoma
• Pregnancy (preeclampsia/eclampsia)
• Medication interactions (MAOIs + tyramine)
• Withdrawal of antihypertensives (especially clonidine, beta-blockers)
• Autonomic dysfunction

COMMON PRESENTATIONS:
• Hypertensive encephalopathy: 15-20%
• Acute heart failure: 25-30%
• Acute coronary syndrome: 10-25%
• Stroke (ischemic or hemorrhagic): 20-25%
• Aortic dissection: 2-5%
• Eclampsia: Variable (obstetric population)

═══════════════════════════════════════════════════════════════════════════════
AFRICAN EPIDEMIOLOGY
═══════════════════════════════════════════════════════════════════════════════

• HYPERTENSION EPIDEMIC: Highest prevalence globally (~30-40% in some countries)
• AWARENESS GAP: Only 25-40% of hypertensives are aware of their diagnosis
• TREATMENT GAP: <20% on treatment in many countries
• CONTROL GAP: <10% have controlled BP
• PRESENTATION: Often present late with severe hypertension and complications

REGIONAL DATA:
• Sub-Saharan Africa: Hypertension prevalence 20-50%
• Nigeria, Ghana, South Africa: High urban rates; rural catching up
• Stroke: Leading cause of cardiovascular death; often hemorrhagic (uncontrolled HTN)
• Heart failure: Hypertensive heart disease major contributor

CONTRIBUTING FACTORS IN AFRICA:
• Limited access to primary care
• Cost of medications and monitoring
• Non-adherence (cost, side effects, beliefs)
• Use of traditional/herbal remedies
• High salt diets (especially processed foods)
• Obesity epidemic
• Limited screening programs
• Healthcare provider shortages
• Drug availability and quality issues

CLINICAL IMPLICATIONS:
• Patients often present with first diagnosis at time of emergency
• Target organ damage already established at presentation
• Higher proportion of hemorrhagic strokes
• Higher rates of hypertensive heart failure
• Renal disease common at presentation

OPPORTUNITIES:
• Task-shifting for hypertension management
• Community-based screening programs
• Fixed-dose combinations improve adherence
• Essential medicines programs
• Mobile health (mHealth) for monitoring and reminders`,

    clinicalPresentation: `═══════════════════════════════════════════════════════════════════════════════
GENERAL PRESENTATION
═══════════════════════════════════════════════════════════════════════════════

VITAL SIGNS:
• Severely elevated BP: Usually SBP >180 and/or DBP >120 mmHg
• May have tachycardia or relative bradycardia
• Tachypnea if pulmonary edema or pain
• Fever possible if associated condition (stroke, infection)

COMMON SYMPTOMS:
• Headache (often severe, occipital)
• Visual disturbances (blurry vision, scotomata, vision loss)
• Nausea and vomiting
• Altered mental status (confusion, lethargy, agitation)
• Chest pain
• Dyspnea
• Focal neurological deficits
• Seizures

⚠️ BP LEVEL ALONE DOES NOT DEFINE EMERGENCY - Look for organ damage!

═══════════════════════════════════════════════════════════════════════════════
SPECIFIC HYPERTENSIVE EMERGENCIES
═══════════════════════════════════════════════════════════════════════════════

HYPERTENSIVE ENCEPHALOPATHY:
• Headache, nausea, vomiting
• Altered consciousness (confusion → stupor → coma)
• Visual disturbances
• Seizures
• Papilledema
• NO focal neurological deficits (distinguishes from stroke)
• PRES (Posterior Reversible Encephalopathy Syndrome) on imaging

INTRACEREBRAL HEMORRHAGE:
• Sudden onset severe headache
• Focal neurological deficits (hemiparesis, aphasia)
• Altered consciousness
• Nausea/vomiting
• CT shows hyperdense lesion (blood)

ISCHEMIC STROKE:
• Sudden onset focal deficits
• Face, arm, leg weakness (often unilateral)
• Speech difficulty
• BP often elevated as compensatory mechanism
• Caution with aggressive BP lowering (may worsen ischemia)

ACUTE AORTIC DISSECTION:
• Sudden, severe "tearing" or "ripping" chest/back pain
• May radiate to back, abdomen, or legs
• BP differential between arms (>20 mmHg)
• Pulse deficits
• New aortic regurgitation murmur
• May have neurological deficits, limb ischemia, renal failure
• CXR: Widened mediastinum

ACUTE CORONARY SYNDROME (ACS):
• Chest pain/pressure (may be atypical)
• Dyspnea
• Diaphoresis
• ECG changes (ST elevation/depression, T-wave changes)
• Elevated troponin

ACUTE LEFT VENTRICULAR FAILURE / PULMONARY EDEMA:
• Severe dyspnea
• Orthopnea
• Pink frothy sputum
• Crackles on lung exam
• S3 gallop
• Elevated JVP
• CXR: Pulmonary edema, cardiomegaly

ACUTE KIDNEY INJURY:
• Oliguria or anuria
• Elevated creatinine
• May have microscopic hematuria, proteinuria
• Often associated with other organ damage

ECLAMPSIA / SEVERE PREECLAMPSIA:
• Pregnancy >20 weeks or postpartum
• Severe hypertension (≥160/110)
• Seizures (eclampsia)
• Headache, visual changes, RUQ/epigastric pain
• Proteinuria
• HELLP syndrome features (hemolysis, elevated LFTs, low platelets)

PHEOCHROMOCYTOMA CRISIS:
• Paroxysmal hypertension (may be episodic)
• Classic triad: Headache, sweating, palpitations
• Pallor or flushing
• Anxiety, tremor
• Often precipitated by surgery, anesthesia, medications, stress

SYMPATHOMIMETIC/DRUG-INDUCED:
• Cocaine, amphetamines, PCP, MDMA
• Severe agitation
• Tachycardia
• Hyperthermia
• Mydriasis (dilated pupils)

═══════════════════════════════════════════════════════════════════════════════
PHYSICAL EXAMINATION PRIORITIES
═══════════════════════════════════════════════════════════════════════════════

• BP in both arms (dissection may cause differential)
• Fundoscopy (hemorrhages, exudates, papilledema)
• Neurological exam (mental status, focal deficits, reflexes)
• Cardiovascular (S3/S4, murmurs, JVD, peripheral pulses)
• Pulmonary (crackles, respiratory distress)
• Abdominal (bruits, pulsatile mass, tenderness)`,

    diagnosis: `═══════════════════════════════════════════════════════════════════════════════
DIAGNOSTIC APPROACH
═══════════════════════════════════════════════════════════════════════════════

⚠️ IMPORTANT: Diagnosis should be made by qualified physicians. These are aids to clinical judgment.

STEP 1: CONFIRM SEVERE HYPERTENSION
• Proper BP measurement technique
• Appropriate cuff size
• Patient rested, seated (or supine if unstable)
• Measure in both arms
• Repeat to confirm

STEP 2: ASSESS FOR TARGET ORGAN DAMAGE
This is what distinguishes EMERGENCY from URGENCY:

BRAIN:
• Mental status changes
• Focal neurological deficits
• Seizures
• Papilledema

HEART:
• Chest pain
• Dyspnea
• ECG changes
• Elevated troponin
• Signs of heart failure

AORTA:
• Chest/back pain
• BP differential
• Pulse deficits
• Widened mediastinum

KIDNEYS:
• Oliguria
• Rising creatinine
• Hematuria

EYES:
• Papilledema
• Retinal hemorrhages/exudates

PREGNANCY:
• Preeclampsia/eclampsia features

STEP 3: IDENTIFY UNDERLYING CAUSE
• Medication history (non-adherence?)
• Drug use (cocaine, amphetamines)
• Previous diagnosis of secondary hypertension
• Pregnancy status
• Symptoms suggesting pheochromocytoma

═══════════════════════════════════════════════════════════════════════════════
LABORATORY EVALUATION
═══════════════════════════════════════════════════════════════════════════════

IMMEDIATE (FOR ALL):
• Complete blood count (microangiopathic hemolysis? anemia?)
• Comprehensive metabolic panel (creatinine, electrolytes, glucose)
• Urinalysis (hematuria, proteinuria, casts)
• ECG
• Troponin (if chest pain or suspected ACS)
• BNP/NT-proBNP (if heart failure suspected)

BASED ON PRESENTATION:
• CT head: If neurological symptoms (rule out stroke, hemorrhage)
• CT angiography chest: If aortic dissection suspected
• Chest X-ray: Pulmonary edema, widened mediastinum
• Echocardiogram: LV function, wall motion, aortic root, pericardial effusion
• Peripheral blood smear: Schistocytes (microangiopathic hemolysis)
• Urine drug screen: If drug use suspected
• Plasma/urine metanephrines: If pheochromocytoma suspected (may defer to after stabilization)

═══════════════════════════════════════════════════════════════════════════════
FUNDOSCOPIC FINDINGS (KEITH-WAGENER-BARKER CLASSIFICATION)
═══════════════════════════════════════════════════════════════════════════════

GRADE I: Mild arteriolar narrowing
GRADE II: More marked narrowing + AV nicking
GRADE III: Hemorrhages and/or exudates (flame/dot-blot hemorrhages, cotton-wool spots, hard exudates)
GRADE IV: Papilledema (optic disc swelling) - indicates EMERGENCY

⚠️ Grade III-IV findings = hypertensive emergency regardless of exact BP reading`,

    differentialDiagnosis: [
      'Hypertensive Urgency - Severe BP WITHOUT organ damage; no immediate threat; gradual oral reduction appropriate',
      'White Coat Hypertension - Elevated BP in clinical setting only; no organ damage; ambulatory monitoring confirms',
      'Anxiety/Panic Attack - Elevated BP with anxiety, palpitations, hyperventilation; resolves with anxiolysis; no organ damage',
      'Pain-Related Hypertension - BP elevation secondary to pain; treat underlying pain; reassess BP',
      'Acute Stroke (without hypertensive emergency) - Focal deficits; BP elevation may be compensatory; do not aggressively lower initially',
      'Intracranial Hemorrhage - May present similarly; CT head essential; specific BP targets',
      'Pheochromocytoma - Paroxysmal hypertension; headache, sweating, palpitations; plasma metanephrines',
      'Thyrotoxicosis - Systolic hypertension, tachycardia, tremor, weight loss; check TSH',
      'Cocaine/Amphetamine Intoxication - Severe hypertension, agitation, tachycardia, mydriasis; urine drug screen',
      'Aortic Coarctation - BP differential upper vs lower extremities; younger patients; imaging confirms',
      'Renal Artery Stenosis - Flash pulmonary edema; resistant hypertension; renal bruit; imaging confirms',
      'Primary Hyperaldosteronism - Hypertension with hypokalemia; elevated aldosterone/renin ratio',
      'Cushings Syndrome - Hypertension with typical features; cortisol testing'
    ],

    investigations: [
      {
        name: 'Blood Pressure Measurement',
        indication: '🔴 IMMEDIATE - Both arms; proper technique; repeat to confirm; arterial line if available',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 IMMEDIATE - Check for microangiopathic hemolytic anemia (schistocytes); baseline',
        availability: 'BASIC'
      },
      {
        name: 'Comprehensive Metabolic Panel',
        indication: '🔴 IMMEDIATE - Creatinine (AKI), electrolytes, glucose; assess renal function',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🔴 IMMEDIATE - Hematuria, proteinuria, RBC casts suggest renal involvement',
        availability: 'BASIC'
      },
      {
        name: 'ECG (12-lead)',
        indication: '🔴 IMMEDIATE - Ischemia, LVH, arrhythmia; baseline and ongoing monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Troponin',
        indication: '🔴 IMMEDIATE - If chest pain or suspected ACS; serial measurements',
        availability: 'BASIC'
      },
      {
        name: 'BNP or NT-proBNP',
        indication: '🟠 URGENT - If heart failure suspected; elevated in acute HF',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 IMMEDIATE - Pulmonary edema, cardiomegaly, widened mediastinum (dissection)',
        availability: 'BASIC'
      },
      {
        name: 'CT Head (Non-contrast)',
        indication: '🔴 IMMEDIATE - If neurological symptoms; rule out hemorrhage before considering thrombolysis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Angiography (Chest/Abdomen)',
        indication: '🔴 IMMEDIATE - If aortic dissection suspected; defines extent and type',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: '🟠 URGENT - LV function, wall motion abnormalities, aortic root (dissection), pericardial effusion',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Fundoscopy',
        indication: '🔴 IMMEDIATE - Papilledema, hemorrhages, exudates indicate end-organ damage',
        availability: 'BASIC'
      },
      {
        name: 'Peripheral Blood Smear',
        indication: '🟠 URGENT - Schistocytes indicate microangiopathic hemolytic anemia',
        availability: 'BASIC'
      },
      {
        name: 'LDH, Haptoglobin, Reticulocyte Count',
        indication: '🟠 URGENT - If hemolysis suspected; elevated LDH, low haptoglobin',
        availability: 'BASIC'
      },
      {
        name: 'Urine Drug Screen',
        indication: '🟠 URGENT - If drug-induced crisis suspected (cocaine, amphetamines)',
        availability: 'BASIC'
      },
      {
        name: 'Plasma Metanephrines',
        indication: '🟡 If pheochromocytoma suspected - Can defer until after stabilization; elevated in pheochromocytoma',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Renal Ultrasound with Doppler',
        indication: '🟡 If renovascular disease suspected - Kidney size disparity, renal artery stenosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pregnancy Test',
        indication: '🔴 IMMEDIATE - All women of childbearing age; eclampsia/preeclampsia consideration',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ABCs - Ensure airway, breathing, circulation; supplemental oxygen if hypoxic',
        '⚡ IV ACCESS - Two large-bore IVs; prepare for IV antihypertensive therapy',
        '⚡ CONTINUOUS MONITORING - Cardiac monitor, pulse oximetry; ICU or high-dependency setting',
        '⚡ ARTERIAL LINE - If available; most accurate BP monitoring; facilitates titration',
        '⚡ CONFIRM EMERGENCY vs URGENCY - Assess for target organ damage before aggressive treatment',
        '⚡ IDENTIFY THE SPECIFIC EMERGENCY - Brain, heart, aorta, kidney, pregnancy; guides drug choice',
        '⚡ START IV ANTIHYPERTENSIVE - Drug selection based on clinical scenario',
        '⚡ BP REDUCTION GOAL: Lower MAP by 20-25% in first hour (exceptions: dissection, ischemic stroke)',
        '⚡ AVOID PRECIPITOUS BP DROP - May cause ischemia; controlled reduction essential',
        '⚡ FREQUENT REASSESSMENT - Neurological checks, symptoms, repeat BP every 5-15 minutes',
        '⚡ TREAT UNDERLYING CAUSE - Non-adherence, drug use, secondary causes',
        '⚡ FOLEY CATHETER - Monitor urine output; assess renal perfusion',
        '⚡ OBTAIN ESSENTIAL TESTS - ECG, labs, imaging as indicated by presentation',
        '⚡ SPECIALTY CONSULTATION - Cardiology, neurology, nephrology, surgery as needed'
      ],
      pharmacological: [
        {
          drug: 'Labetalol',
          indication: '🔴 FIRST-LINE for most hypertensive emergencies - Alpha and beta blocker; safe in pregnancy; good for most scenarios except acute HF, asthma, bradycardia',
          dose: 'IV Bolus: 10-20 mg over 2 min, then 20-80 mg every 10 min (max 300 mg total). IV Infusion: 0.5-2 mg/min. Onset 2-5 min; Duration 3-6 hours',
          route: 'IV',
          duration: 'Titrate to goal; transition to oral when stable',
          contraindications: ['Asthma/COPD (severe)', 'Bradycardia', 'Heart block (2nd/3rd degree)', 'Decompensated heart failure', 'Cocaine use (relative - may cause unopposed alpha)'],
          sideEffects: ['Bradycardia', 'Bronchospasm', 'Dizziness', 'Nausea', 'Scalp tingling', 'Heart block'],
          alternatives: ['Nicardipine', 'Esmolol (shorter acting)']
        },
        {
          drug: 'Nicardipine',
          indication: '🔴 FIRST-LINE alternative - Calcium channel blocker (dihydropyridine); good for most emergencies; titratable infusion',
          dose: 'IV Infusion: Start 5 mg/hour, increase by 2.5 mg/hour every 5-15 min (max 15 mg/hour). Onset 5-15 min; Duration 30-40 min after stopping',
          route: 'IV infusion only',
          duration: 'Titrate to goal; transition to oral when stable',
          contraindications: ['Severe aortic stenosis', 'Hypersensitivity'],
          sideEffects: ['Tachycardia', 'Headache', 'Flushing', 'Nausea', 'Phlebitis at infusion site'],
          alternatives: ['Labetalol', 'Clevidipine']
        },
        {
          drug: 'Sodium Nitroprusside',
          indication: '🔴 VERY POTENT - Arterial and venous dilator; rapid onset/offset; requires arterial line monitoring; useful for aortic dissection (with beta-blocker), severe HF',
          dose: 'IV Infusion: Start 0.25-0.5 mcg/kg/min, titrate up to 8-10 mcg/kg/min (max). Onset immediate; Duration 1-2 min after stopping',
          route: 'IV infusion (protect from light)',
          duration: 'Short-term use only (<48-72 hours); risk of cyanide toxicity with prolonged use',
          contraindications: ['Pregnancy (contraindicated)', 'Severe renal/hepatic impairment (cyanide accumulation)', 'Vitamin B12 deficiency', 'Inadequate cerebral circulation'],
          sideEffects: ['Cyanide toxicity (prolonged use, high doses)', 'Thiocyanate toxicity (renal impairment)', 'Hypotension', 'Nausea', 'Muscle twitching', 'Coronary steal'],
          alternatives: ['Nicardipine', 'Nitroglycerin', 'Labetalol']
        },
        {
          drug: 'Esmolol',
          indication: '🟠 SHORT-ACTING BETA-BLOCKER - Ideal for aortic dissection (control rate and BP); very titratable; short half-life',
          dose: 'Loading: 500 mcg/kg over 1 min, then Infusion: 50-200 mcg/kg/min (can increase every 5 min)',
          route: 'IV',
          duration: 'Half-life 9 minutes; effect gone within 20-30 min of stopping',
          contraindications: ['Asthma/COPD', 'Bradycardia', 'Heart block', 'Decompensated heart failure', 'Cocaine (relative)'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Bronchospasm', 'Heart block', 'Injection site reactions'],
          alternatives: ['Labetalol', 'Metoprolol IV']
        },
        {
          drug: 'Nitroglycerin',
          indication: '🔴 ACUTE CORONARY SYNDROME / PULMONARY EDEMA - Venodilator > arteriodilator; reduces preload; anti-ischemic',
          dose: 'IV Infusion: Start 5-10 mcg/min, increase by 5-10 mcg/min every 3-5 min (typical range 10-200 mcg/min)',
          route: 'IV infusion',
          duration: 'Titrate to effect; tolerance develops with prolonged use',
          contraindications: ['Severe hypotension', 'Right ventricular infarction', 'Use of PDE5 inhibitors (sildenafil, etc.)', 'Severe anemia', 'Increased ICP'],
          sideEffects: ['Headache (common)', 'Hypotension', 'Reflex tachycardia', 'Methemoglobinemia (rare, high doses)'],
          alternatives: ['Nitroprusside (more arteriolar)', 'Isosorbide dinitrate']
        },
        {
          drug: 'Hydralazine',
          indication: '🟠 PREGNANCY (Eclampsia/Preeclampsia) - Direct arteriolar vasodilator; long history of use in pregnancy',
          dose: 'IV: 5-10 mg every 20-30 min PRN (max 20 mg); IM: 10-20 mg. Onset 10-20 min; Duration 3-8 hours',
          route: 'IV or IM',
          duration: 'Prolonged effect; less titratable than labetalol or nicardipine',
          contraindications: ['Coronary artery disease (reflex tachycardia)', 'Aortic dissection', 'Mitral valve disease'],
          sideEffects: ['Reflex tachycardia', 'Headache', 'Flushing', 'Nausea', 'Lupus-like syndrome (chronic use)'],
          alternatives: ['Labetalol (preferred in pregnancy)', 'Nicardipine']
        },
        {
          drug: 'Phentolamine',
          indication: '🔴 PHEOCHROMOCYTOMA / CATECHOLAMINE EXCESS - Alpha-blocker; for pheochromocytoma crisis, cocaine, tyramine-MAOI interaction',
          dose: 'IV: 1-5 mg bolus, repeat every 5-15 min as needed; OR infusion 0.5-1 mg/min',
          route: 'IV',
          duration: 'Short-acting; 15-30 min duration',
          contraindications: ['Hypotension', 'Coronary artery disease (relative)'],
          sideEffects: ['Hypotension', 'Reflex tachycardia', 'Arrhythmias', 'GI upset'],
          alternatives: ['Nicardipine', 'Nitroprusside (add beta-blocker for tachycardia)']
        },
        {
          drug: 'Clevidipine',
          indication: '🟠 ALTERNATIVE CCB - Ultra-short-acting dihydropyridine CCB; very titratable; useful when available',
          dose: 'IV Infusion: Start 1-2 mg/hour, double every 90 seconds initially, then increase by smaller increments (max 32 mg/hour)',
          route: 'IV infusion (lipid emulsion)',
          duration: 'Half-life 1 minute; effect gone in 5-15 min',
          contraindications: ['Soy or egg allergy', 'Defective lipid metabolism', 'Severe aortic stenosis'],
          sideEffects: ['Headache', 'Nausea', 'Reflex tachycardia', 'Hypertriglyceridemia (prolonged use)'],
          alternatives: ['Nicardipine']
        },
        {
          drug: 'Fenoldopam',
          indication: '🟠 RENAL-PROTECTIVE OPTION - Dopamine-1 agonist; improves renal blood flow; useful in hypertensive emergency with AKI',
          dose: 'IV Infusion: Start 0.1 mcg/kg/min, titrate by 0.05-0.1 mcg/kg/min every 15 min (max 1.6 mcg/kg/min)',
          route: 'IV infusion',
          duration: 'Continuous infusion; effect wanes quickly when stopped',
          contraindications: ['Glaucoma (increases intraocular pressure)', 'Sulfite allergy'],
          sideEffects: ['Headache', 'Flushing', 'Tachycardia', 'Hypokalemia', 'Increased intraocular pressure'],
          alternatives: ['Nicardipine', 'Clevidipine']
        },
        {
          drug: 'Magnesium Sulfate',
          indication: '🔴 ECLAMPSIA - Seizure prophylaxis and treatment in preeclampsia/eclampsia; NOT a primary antihypertensive',
          dose: 'Loading: 4-6 g IV over 15-20 min, then Maintenance: 1-2 g/hour IV',
          route: 'IV',
          duration: 'Continue for 24-48 hours post-delivery or post-seizure',
          contraindications: ['Myasthenia gravis', 'Severe renal failure (dose adjust)'],
          sideEffects: ['Flushing', 'Respiratory depression (toxicity)', 'Loss of DTRs (toxicity)', 'Cardiac arrest (severe toxicity)'],
          alternatives: ['Diazepam or Phenytoin if MgSO4 unavailable (less preferred)']
        },
        {
          drug: 'Furosemide',
          indication: '🟠 ADJUNCT for Acute Pulmonary Edema - Diuretic; reduces preload; use with vasodilators',
          dose: '20-80 mg IV initially; may repeat or increase dose',
          route: 'IV',
          duration: 'As needed for diuresis',
          contraindications: ['Anuria', 'Severe hypovolemia', 'Hepatic coma'],
          sideEffects: ['Hypokalemia', 'Hypotension', 'Ototoxicity (high doses)', 'Dehydration'],
          alternatives: ['Bumetanide', 'Torsemide']
        }
      ],
      nonPharmacological: [
        'ICU/HIGH-DEPENDENCY ADMISSION: Continuous monitoring essential; arterial line if available',
        'POSITIONING: Semi-upright if pulmonary edema; supine if hypotensive',
        'OXYGEN: Supplement if hypoxic; high-flow or non-invasive ventilation for pulmonary edema',
        'PAIN CONTROL: Adequate analgesia (especially aortic dissection) helps reduce BP',
        'ANXIETY REDUCTION: Calm environment; benzodiazepines if agitated (also helps BP)',
        'AVOID EXACERBATING FACTORS: Stop NSAIDs, sympathomimetics, excessive salt/fluid if applicable',
        'PATIENT/FAMILY COMMUNICATION: Explain severity, treatment plan, prognosis'
      ],
      surgical: [
        'AORTIC DISSECTION - SURGICAL EMERGENCY for Type A (ascending aorta); urgent consultation',
        'AORTIC DISSECTION - MEDICAL MANAGEMENT for uncomplicated Type B (descending); surgery if complicated',
        'ENDOVASCULAR REPAIR (TEVAR) - For complicated Type B dissection; selected cases',
        'PHEOCHROMOCYTOMA RESECTION - After medical stabilization with alpha-blockade',
        'RENAL ARTERY REVASCULARIZATION - For renovascular hypertension; angioplasty or surgery',
        'CESAREAN SECTION - May be needed for refractory eclampsia'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE ICU: All hypertensive emergencies require intensive monitoring',
        '🔴 CARDIOTHORACIC SURGERY: Type A aortic dissection; complicated Type B',
        '🔴 NEUROSURGERY: Large intracerebral hemorrhage with mass effect; hydrocephalus',
        '🔴 INTERVENTIONAL CARDIOLOGY: STEMI complicating hypertensive emergency',
        '🟠 NEPHROLOGY: Acute kidney injury; possible need for dialysis',
        '🟠 OBSTETRICS: Eclampsia/severe preeclampsia for delivery planning',
        '🟠 ENDOCRINOLOGY: Suspected pheochromocytoma for further workup'
      ],
      monitoring: [
        'BLOOD PRESSURE: Every 5-15 min during IV therapy; arterial line preferred',
        'CONTINUOUS CARDIAC MONITORING: Watch for arrhythmias, ischemia',
        'NEUROLOGICAL CHECKS: Hourly or more frequent; watch for deterioration',
        'URINE OUTPUT: Hourly; aim >0.5 mL/kg/hr; reflects renal perfusion',
        'OXYGEN SATURATION: Continuous pulse oximetry',
        'SERIAL LABS: Repeat creatinine, troponin, CBC as indicated',
        'FLUID BALANCE: Avoid overload; careful in heart failure',
        'SIGNS OF OVER-TREATMENT: Hypotension, worsening mentation, ischemia - slow down!'
      ],
      followUp: `═══════════════════════════════════════════════════════════════════════════════
BP REDUCTION TARGETS BY SCENARIO
═══════════════════════════════════════════════════════════════════════════════

GENERAL HYPERTENSIVE EMERGENCY:
• First hour: Reduce MAP by 20-25% (NOT below 25%)
• Next 2-6 hours: Target ~160/100-110 mmHg
• Next 24-48 hours: Gradually normalize if tolerated

AORTIC DISSECTION:
• Immediate target: SBP <120 mmHg AND HR <60 bpm
• Beta-blocker FIRST (reduce dP/dt), then vasodilator
• Rapid control essential to prevent propagation

ISCHEMIC STROKE (Not receiving thrombolysis):
• BP often elevated acutely; generally do NOT lower aggressively
• Treat if SBP >220 or DBP >120 mmHg
• Target: 15% reduction in first 24 hours

ISCHEMIC STROKE (Receiving tPA):
• Target: <185/110 before tPA; <180/105 for 24 hours after

INTRACEREBRAL HEMORRHAGE:
• If SBP 150-220: Acute lowering to 140 mmHg is safe (AHA 2022)
• If SBP >220: Aggressive reduction with continuous monitoring

ECLAMPSIA/SEVERE PREECLAMPSIA:
• Target: SBP <160 and DBP <110 mmHg
• Use labetalol, hydralazine, or nicardipine
• MgSO4 for seizure prophylaxis (not BP control)
• Delivery is definitive treatment

ACS WITH HYPERTENSION:
• Target: Gradual reduction; avoid hypotension
• Nitroglycerin helpful (anti-ischemic + BP reduction)
• Beta-blockers if no contraindications

ACUTE PULMONARY EDEMA:
• Target: Aggressive reduction acceptable to relieve symptoms
• Vasodilators (nitroglycerin, nitroprusside) reduce preload and afterload
• Diuretics for volume overload

═══════════════════════════════════════════════════════════════════════════════
TRANSITION TO ORAL THERAPY
═══════════════════════════════════════════════════════════════════════════════

WHEN TO TRANSITION:
• BP at or near target for several hours
• Patient stable without organ damage progression
• Tolerating oral intake
• No ongoing precipitant (drug cleared, pain controlled)

ORAL AGENTS TO CONSIDER:
• Labetalol PO (if was on IV labetalol)
• Amlodipine (long-acting CCB)
• ACE inhibitors / ARBs (NOT in pregnancy, bilateral RAS)
• Thiazide or thiazide-like diuretics
• Beta-blockers

OVERLAP STRATEGY:
• Start oral agent and continue IV for 1-2 hours
• Observe for adequate BP control
• Wean IV once oral taking effect

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM MANAGEMENT
═══════════════════════════════════════════════════════════════════════════════

BEFORE DISCHARGE:
• Establish oral regimen with documented efficacy
• Ensure patient can afford and access medications
• Screen for secondary causes if indicated
• Address precipitants (non-adherence, drug use)

FOLLOW-UP:
• Close follow-up in 1-2 weeks initially
• Home BP monitoring if possible
• Evaluate for residual organ damage
• Reinforce adherence

PREVENTION OF RECURRENCE:
• Patient education on importance of adherence
• Simplify regimen (once-daily, fixed-dose combinations)
• Address barriers (cost, side effects, understanding)
• Treat underlying causes (CKD, renovascular, etc.)

⚠️ Management must be individualized based on the specific emergency and patient factors.`
    },

    redFlags: [
      '🔴 CRITICAL: Aortic dissection (tearing chest/back pain, BP differential, pulse deficit) - Surgical emergency for Type A',
      '🔴 CRITICAL: Acute pulmonary edema with respiratory failure - May need intubation; aggressive afterload reduction',
      '🔴 CRITICAL: Hypertensive encephalopathy (seizures, coma) - Immediate IV therapy; consider ICH',
      '🔴 CRITICAL: Intracerebral hemorrhage - Neurosurgical consultation; careful BP management',
      '🔴 CRITICAL: Eclampsia (seizures in pregnancy) - MgSO4, BP control, expedite delivery',
      '🔴 CRITICAL: STEMI with severe hypertension - Urgent revascularization; control BP carefully',
      '🟠 URGENT: Acute kidney injury with oliguria - Monitor closely; may need dialysis',
      '🟠 URGENT: Microangiopathic hemolytic anemia (schistocytes, rising LDH) - Indicates severe vascular damage',
      '🟠 URGENT: Overly rapid BP reduction (symptoms of hypoperfusion) - Slow down or stop infusion',
      '🟠 URGENT: Papilledema on fundoscopy - Confirms end-organ damage; aggressive treatment needed',
      '🟠 URGENT: Cocaine-induced hypertension - Avoid pure beta-blockers; use benzodiazepines, phentolamine, or nicardipine'
    ],

    complications: [
      'STROKE (Ischemic or Hemorrhagic): Major cause of morbidity/mortality; may be precipitated by over- or under-treatment',
      'MYOCARDIAL INFARCTION: Demand ischemia from elevated BP; or hypoperfusion from rapid BP drop',
      'ACUTE HEART FAILURE: Flash pulmonary edema; may be presenting feature or complication',
      'AORTIC DISSECTION: Can propagate if BP not controlled; high mortality',
      'ACUTE KIDNEY INJURY: May progress to chronic kidney disease; some need dialysis',
      'HYPERTENSIVE RETINOPATHY: Vision loss; usually improves with BP control',
      'POSTERIOR REVERSIBLE ENCEPHALOPATHY SYNDROME (PRES): Usually reversible with BP control',
      'DEATH: Without treatment, very high mortality; with treatment, improved but still significant',
      'TREATMENT-RELATED HYPOTENSION: May cause watershed infarcts (brain, heart, kidneys)',
      'RECURRENCE: High risk if underlying cause not addressed; non-adherence common'
    ],

    prognosis: `═══════════════════════════════════════════════════════════════════════════════
OVERALL PROGNOSIS
═══════════════════════════════════════════════════════════════════════════════

HISTORICAL (UNTREATED):
• Malignant hypertension: >90% mortality at 1 year
• Median survival: ~8.5 months without treatment

WITH MODERN TREATMENT:
• In-hospital mortality: 5-15% (varies by presentation)
• 1-year mortality: 20-40% (depends on comorbidities, organ damage)
• 5-year survival: 50-70%

PROGNOSIS BY PRESENTATION:
• Aortic dissection: Type A 30-day mortality 25-30% (surgical); Type B 10-15%
• Intracerebral hemorrhage: 30-day mortality 30-50%
• Hypertensive encephalopathy: Usually reversible with treatment
• Acute heart failure: Variable; depends on underlying cardiac function
• AKI: Many recover renal function; some progress to CKD/ESRD

═══════════════════════════════════════════════════════════════════════════════
PROGNOSTIC FACTORS
═══════════════════════════════════════════════════════════════════════════════

FAVORABLE:
• Early recognition and treatment
• Younger age
• No significant comorbidities
• Reversible precipitant (non-adherence, cocaine)
• Rapid response to therapy
• No stroke or ICH

UNFAVORABLE:
• Delayed presentation
• Advanced age
• Multiple comorbidities (CKD, DM, CAD)
• Significant end-organ damage at presentation
• Aortic dissection
• Intracerebral hemorrhage
• Recurrent episodes
• Poor medication adherence

═══════════════════════════════════════════════════════════════════════════════
LONG-TERM OUTCOMES
═══════════════════════════════════════════════════════════════════════════════

• Chronic kidney disease: 30-50% have residual renal impairment
• Recurrence: High risk without proper long-term management
• Cardiovascular events: Increased risk of MI, stroke, heart failure
• End-stage renal disease: Risk in those with severe AKI

⚠️ Prognosis significantly improved with early treatment and long-term BP control.`,

    prevention: `═══════════════════════════════════════════════════════════════════════════════
PRIMARY PREVENTION
═══════════════════════════════════════════════════════════════════════════════

POPULATION-LEVEL:
• Promote healthy lifestyle (diet, exercise, weight management)
• Reduce sodium intake (<2g/day sodium recommended)
• Limit alcohol consumption
• Smoking cessation programs
• Screen for hypertension routinely
• Ensure access to affordable antihypertensive medications

INDIVIDUAL-LEVEL:
• Regular BP monitoring
• Adherence to prescribed medications
• Lifestyle modifications (DASH diet, exercise, weight loss)
• Avoid NSAIDs, decongestants, excessive caffeine
• Avoid illicit drugs (cocaine, amphetamines)
• Manage comorbidities (diabetes, CKD)

═══════════════════════════════════════════════════════════════════════════════
SECONDARY PREVENTION (After Hypertensive Emergency)
═══════════════════════════════════════════════════════════════════════════════

BEFORE DISCHARGE:
• Establish effective oral regimen
• Patient education on:
  - Disease severity
  - Importance of adherence
  - Warning signs for return
  - Medication side effects
• Arrange follow-up within 1-2 weeks
• Social work/case management for barriers

MEDICATION STRATEGIES TO IMPROVE ADHERENCE:
• Once-daily dosing when possible
• Fixed-dose combinations (reduce pill burden)
• Generic/affordable options
• Pill boxes, reminders, mHealth apps
• Family involvement in care

ADDRESS BARRIERS:
• Cost (patient assistance programs, generics)
• Side effects (adjust medications)
• Health literacy (simple explanations, written materials)
• Cultural beliefs (engage with patient perspective)
• Access (community health workers, pharmacy-based care)

═══════════════════════════════════════════════════════════════════════════════
PREVENTION IN AFRICAN SETTINGS - PRIORITIES
═══════════════════════════════════════════════════════════════════════════════

HIGH-IMPACT, LOW-COST STRATEGIES:
• Task-shifting: Train nurses/CHWs to screen and manage hypertension
• Essential medicines: Ensure availability of thiazides, CCBs, ACEi
• Fixed-dose combinations: Improve adherence, reduce visits
• Community screening: Identify unknown hypertensives
• mHealth: SMS reminders for adherence
• Simple treatment algorithms: WHO HEARTS package

HEALTH SYSTEM STRENGTHENING:
• Reliable BP monitoring equipment
• Medication supply chain improvements
• Training on hypertension management
• Referral pathways for complicated cases
• Quality improvement initiatives

PUBLIC AWARENESS:
• Know your BP campaigns
• Education on lifestyle modifications
• Reduce stigma and fatalism about hypertension
• Involve traditional/community leaders`,

    patientEducation: `═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
WHAT IS A HYPERTENSIVE EMERGENCY?
═══════════════════════════════════════════════════════════════════════════════

A hypertensive emergency is when your blood pressure is dangerously high AND is causing damage to your body's organs, such as your brain, heart, kidneys, or eyes.

This is a medical emergency that can cause:
• Stroke
• Heart attack
• Heart failure
• Kidney damage
• Vision loss
• Death

You need immediate treatment in a hospital to lower your blood pressure safely and protect your organs.

═══════════════════════════════════════════════════════════════════════════════
WARNING SIGNS - GO TO THE HOSPITAL IMMEDIATELY
═══════════════════════════════════════════════════════════════════════════════

If you have high blood pressure AND any of these symptoms, call for help or go to the hospital:

🔴 Severe headache (worst of your life)
🔴 Confusion or difficulty thinking clearly
🔴 Vision changes (blurry vision, seeing spots, vision loss)
🔴 Chest pain or pressure
🔴 Difficulty breathing
🔴 Severe back pain (especially sudden, tearing pain)
🔴 Weakness or numbness on one side of your body
🔴 Difficulty speaking
🔴 Seizures
🔴 Nosebleeds that won't stop

DO NOT WAIT to see if these symptoms go away.

═══════════════════════════════════════════════════════════════════════════════
WHY DID THIS HAPPEN?
═══════════════════════════════════════════════════════════════════════════════

The most common reasons for a hypertensive emergency:

• NOT TAKING your blood pressure medications
• Running out of medications
• Not knowing you had high blood pressure
• Using drugs like cocaine or amphetamines
• Severe kidney disease
• Pregnancy complications
• Other medical conditions

═══════════════════════════════════════════════════════════════════════════════
YOUR TREATMENT
═══════════════════════════════════════════════════════════════════════════════

In the hospital, you will receive:
• Medicines through your IV to lower your blood pressure safely
• Close monitoring in an intensive care unit or special area
• Tests to check for damage to your brain, heart, kidneys, and eyes
• Treatment for any organ damage found

Your blood pressure will be lowered SLOWLY and CAREFULLY. Lowering it too fast can also cause problems.

═══════════════════════════════════════════════════════════════════════════════
PREVENTING THIS FROM HAPPENING AGAIN
═══════════════════════════════════════════════════════════════════════════════

The most important thing: TAKE YOUR BLOOD PRESSURE MEDICINES EVERY DAY!

✅ Take your medicines at the same time every day
✅ Do not stop taking your medicines even if you feel fine
✅ Do not run out - refill your prescription before it's empty
✅ Tell your doctor if you have side effects (they can adjust your medicines)
✅ Keep all your doctor's appointments
✅ Check your blood pressure regularly (at home if possible)

LIFESTYLE CHANGES THAT HELP:
✅ Reduce salt in your diet (avoid processed and fast foods)
✅ Eat more fruits, vegetables, and whole grains
✅ Maintain a healthy weight
✅ Exercise regularly (walking is a good start)
✅ Limit alcohol
✅ Do not smoke
✅ Manage stress
✅ Avoid cocaine and other drugs

═══════════════════════════════════════════════════════════════════════════════
KNOW YOUR NUMBERS
═══════════════════════════════════════════════════════════════════════════════

NORMAL BLOOD PRESSURE: Less than 120/80 mmHg
HIGH BLOOD PRESSURE: 130/80 mmHg or higher
SEVERE HIGH BLOOD PRESSURE: 180/120 mmHg or higher

Ask your doctor what your target blood pressure should be.

═══════════════════════════════════════════════════════════════════════════════
QUESTIONS TO ASK YOUR DOCTOR
═══════════════════════════════════════════════════════════════════════════════

• What caused my hypertensive emergency?
• Was there damage to my organs?
• What medications will I take when I go home?
• What is my target blood pressure?
• When should I follow up?
• What symptoms should bring me back to the hospital?
• Can I afford my medications? Are there assistance programs?

Your healthcare team wants to help you stay healthy. Don't be afraid to ask questions or tell them if you're having trouble taking your medicines.`,

    references: [
      'Whelton PK, Carey RM, Aronow WS, et al. 2017 ACC/AHA/AAPA/ABC/ACPM/AGS/APhA/ASH/ASPC/NMA/PCNA Guideline for the Prevention, Detection, Evaluation, and Management of High Blood Pressure in Adults. J Am Coll Cardiol. 2018;71(19):e127-e248.',
      'Unger T, Borghi C, Charchar F, et al. 2020 International Society of Hypertension Global Hypertension Practice Guidelines. Hypertension. 2020;75(6):1334-1357.',
      'van den Born BH, Lip GYH,"; A, et al. Hypertensive Emergencies. In: ESC CardioMed. 3rd ed. Oxford University Press; 2022.',
      'Peixoto AJ. Acute Severe Hypertension. N Engl J Med. 2019;381(19):1843-1852.',
      'Varon J, Marik PE. Clinical review: the management of hypertensive crises. Crit Care. 2003;7(5):374-384.',
      'Hiratzka LF, Bakris GL, Beckman JA, et al. 2010 ACCF/AHA/AATS/ACR/ASA/SCA/SCAI/SIR/STS/SVM guidelines for the diagnosis and management of patients with Thoracic Aortic Disease. Circulation. 2010;121(13):e266-e369.',
      'Powers WJ, Rabinstein AA, Ackerson T, et al. Guidelines for the Early Management of Patients With Acute Ischemic Stroke: 2019 Update. Stroke. 2019;50(12):e344-e418.',
      'Greenberg SM, Ziai WC, Cordonnier C, et al. 2022 Guideline for the Management of Patients With Spontaneous Intracerebral Hemorrhage. Stroke. 2022;53(7):e282-e361.',
      'American College of Obstetricians and Gynecologists. Gestational Hypertension and Preeclampsia: ACOG Practice Bulletin, Number 222. Obstet Gynecol. 2020;135(6):e237-e260.',
      'Kario K, Chia YC, Siddique S, et al. Seven-day home blood pressure monitoring in a hypertensive population in Asia - the MMM17 and 18 study in Africa and Asia. J Clin Hypertens. 2022;24(5):578-587. [African/Asian data]'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 31. ATRIAL FIBRILLATION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '31',
    name: 'Atrial Fibrillation',
    slug: 'atrial-fibrillation',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I48',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Atrial fibrillation (AF) is the most common sustained cardiac arrhythmia, characterized by rapid, irregular, and disorganized atrial electrical activity resulting in an irregularly irregular ventricular response. AF significantly increases the risk of stroke (5-fold), heart failure, and mortality. It affects 1-2% of the general population, with prevalence increasing with age.

In African populations, AF has unique epidemiological features including younger age of onset (mean age 50-60 years vs 70+ in Western populations), higher rates of rheumatic heart disease as an underlying cause, and significant underdiagnosis due to limited access to ECG monitoring. The management of AF in resource-limited settings requires adaptation of rate/rhythm control and anticoagulation strategies.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• AF increases stroke risk 5-fold - anticoagulation is the priority intervention
• CHA₂DS₂-VASc score guides anticoagulation: ≥2 in men, ≥3 in women = anticoagulate
• HAS-BLED score assesses bleeding risk but high score is NOT a contraindication to anticoagulation
• Rate control target: Resting HR <110 bpm (lenient) or <80 bpm (strict)
• Rhythm control preferred if: symptomatic, young, first episode, AF due to reversible cause
• NEW AF with hemodynamic instability → Synchronized cardioversion
• AF >48 hours or unknown duration → Anticoagulate 3 weeks before cardioversion OR TEE to exclude LAA thrombus
• In Africa, ALWAYS consider rheumatic heart disease as underlying cause
• Digoxin useful in resource-limited settings but narrow therapeutic window
• DOACs preferred over warfarin where available (no INR monitoring needed)`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Prevalence: 1-2% general population; 5-15% in those >80 years
• Incidence: 3-5 per 1,000 person-years
• Lifetime risk: 25% for those reaching age 40
• Male:Female ratio: 1.2:1
• Strong age association: Prevalence doubles with each decade after age 50
• Projected increase: 2-3 fold by 2050 due to aging populations

AFRICAN EPIDEMIOLOGY:
• Hospital-based prevalence: 0.7-2.0% in sub-Saharan Africa
• Younger age of onset: Mean age 50-60 years (vs 70+ in Western countries)
• Rheumatic heart disease: Leading cause in 20-40% of African AF patients (vs <5% globally)
• Hypertension: Most common overall risk factor (60-80% of AF patients)
• Significant underdiagnosis: Estimated 70-80% of AF cases undetected in rural areas
• Stroke: AF-related stroke mortality 2-3x higher in Africa due to limited anticoagulation access
• Heart failure: Common precipitant and consequence of AF in Africa

REGIONAL VARIATIONS IN AFRICA:
• West Africa: Higher rheumatic AF rates; Nigeria studies show 20-30% RHD-related AF
• East Africa: Kenya/Uganda data show hypertension as leading cause
• South Africa: More Western-type AF patterns in urban populations
• North Africa: Intermediate patterns; Egypt shows high hypertensive AF rates

RISK FACTORS:
Non-modifiable:
• Age (strongest risk factor)
• Male sex
• Family history/genetics
• White ethnicity (higher than African ancestry)

Modifiable:
• Hypertension (most common - 60-80%)
• Obesity
• Diabetes mellitus
• Obstructive sleep apnea
• Alcohol excess ("holiday heart")
• Hyperthyroidism
• Heart failure
• Valvular heart disease (especially mitral)
• Coronary artery disease
• Chronic kidney disease

African-Specific Factors:
• Rheumatic heart disease (major contributor)
• Uncontrolled hypertension (limited access to medications)
• Endomyocardial fibrosis (tropical regions)
• Peripartum cardiomyopathy
• HIV-related cardiomyopathy`,

    clinicalPresentation: `SYMPTOMS (Variable - may be asymptomatic in 25-40%):

Palpitations:
• Most common symptom (70-80%)
• Sensation of rapid, irregular heartbeat
• May describe as "heart racing," "fluttering," or "skipping beats"
• Often worse with exertion, caffeine, alcohol

Dyspnea:
• Exertional shortness of breath (50-60%)
• Due to loss of atrial contribution to cardiac output (15-25% reduction)
• May indicate heart failure with AF

Fatigue and Exercise Intolerance:
• Common (40-50%)
• May be the only symptom in elderly
• Often attributed to aging and missed

Dizziness/Lightheadedness:
• Due to irregular cardiac output
• Worse with rapid ventricular rates
• Syncope rare (suggests additional pathology)

Chest Discomfort:
• Non-specific chest pressure or discomfort
• May mimic angina
• Rule out ACS in high-risk patients

ASYMPTOMATIC AF:
• 25-40% of patients
• Often discovered incidentally on examination or ECG
• Higher in elderly and those with persistent AF
• Does NOT reduce stroke risk - still requires anticoagulation assessment

SIGNS:

Vital Signs:
• Irregularly irregular pulse (hallmark sign)
• Pulse deficit (apical rate > radial rate)
• Variable blood pressure
• Tachycardia (often 100-160 bpm if uncontrolled)

Cardiovascular Examination:
• Irregular rhythm on auscultation
• Variable intensity of S1 (due to varying diastolic filling)
• Signs of underlying heart disease (murmurs, S3, elevated JVP)
• Signs of heart failure if decompensated

CLINICAL CLASSIFICATION:

By Pattern:
• Paroxysmal: Self-terminating, usually <7 days (often <48 hours)
• Persistent: Sustained >7 days, or requiring cardioversion
• Long-standing persistent: Continuous AF >12 months
• Permanent: Accepted AF; rhythm control not pursued

By Symptoms (EHRA Score):
• EHRA I: No symptoms
• EHRA IIa: Mild symptoms; normal daily activity not affected
• EHRA IIb: Moderate symptoms; normal daily activity not affected but patient troubled
• EHRA III: Severe symptoms; normal daily activity affected
• EHRA IV: Disabling symptoms; normal daily activity discontinued

PRESENTATIONS REQUIRING URGENT MANAGEMENT:

🔴 AF with Rapid Ventricular Response (RVR):
• HR >150 bpm with symptoms
• Hemodynamic compromise

🔴 AF with Hemodynamic Instability:
• Hypotension (SBP <90 mmHg)
• Chest pain (ongoing ischemia)
• Pulmonary edema
• Altered mental status

🔴 AF as Presenting Feature of:
• Acute MI (especially RCA territory)
• Pulmonary embolism
• Sepsis
• Thyroid storm
• WPW syndrome with pre-excited AF (irregular wide-complex tachycardia)

🔴 New Stroke/TIA in Setting of AF`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Confirm AF Diagnosis
Step 2: Assess Hemodynamic Stability
Step 3: Identify Underlying Causes/Precipitants
Step 4: Assess Stroke Risk (CHA₂DS₂-VASc)
Step 5: Assess Bleeding Risk (HAS-BLED)
Step 6: Develop Management Strategy (Rate vs Rhythm, Anticoagulation)

ELECTROCARDIOGRAPHIC DIAGNOSIS:

12-Lead ECG Findings:
• Absent P waves (replaced by fibrillatory waves - "f waves")
• Irregularly irregular R-R intervals
• Fibrillatory baseline (best seen in V1)
• Narrow QRS (unless bundle branch block or pre-excitation)
• Variable ventricular rate (typically 100-160 bpm if untreated)

ECG Red Flags:
• Wide QRS with rapid irregular rate → Consider WPW with AF (avoid AV nodal blockers!)
• Regular R-R intervals → May be atrial flutter with fixed block, not AF
• Very slow regular rate → Complete heart block with AF (pacemaker needed)
• ST changes → Consider ischemia as cause or consequence

CHA₂DS₂-VASc SCORE (Stroke Risk Assessment):

| Risk Factor | Points |
|-------------|--------|
| Congestive heart failure | 1 |
| Hypertension | 1 |
| Age ≥75 years | 2 |
| Diabetes mellitus | 1 |
| Stroke/TIA/thromboembolism | 2 |
| Vascular disease (MI, PAD, aortic plaque) | 1 |
| Age 65-74 years | 1 |
| Sex category (female) | 1 |
| **Maximum Score** | **9** |

Interpretation:
• Score 0 (men) or 1 (women): Low risk - Consider no anticoagulation
• Score 1 (men): Low-moderate risk - Consider anticoagulation
• Score ≥2 (men) or ≥3 (women): Anticoagulation RECOMMENDED

Annual Stroke Risk by Score:
• 0: 0%
• 1: 1.3%
• 2: 2.2%
• 3: 3.2%
• 4: 4.0%
• 5: 6.7%
• 6+: 9.8%+

HAS-BLED SCORE (Bleeding Risk Assessment):

| Risk Factor | Points |
|-------------|--------|
| Hypertension (uncontrolled, SBP >160) | 1 |
| Abnormal renal/liver function (1 each) | 1 or 2 |
| Stroke history | 1 |
| Bleeding history or predisposition | 1 |
| Labile INR (if on warfarin) | 1 |
| Elderly (>65 years) | 1 |
| Drugs (antiplatelet/NSAIDs) or alcohol | 1 or 2 |
| **Maximum Score** | **9** |

Interpretation:
• Score ≥3: High bleeding risk - Address modifiable factors
• High HAS-BLED score is NOT a contraindication to anticoagulation
• Use to identify modifiable bleeding risks

ETIOLOGICAL WORKUP:

Laboratory Investigations:
• Thyroid function tests (TSH) - Essential in all new AF
• Complete blood count - Anemia, infection
• Electrolytes - Hypokalemia, hypomagnesemia
• Renal function - Affects drug choice and dosing
• Liver function - Affects drug choice
• Coagulation studies - Baseline before anticoagulation
• Fasting glucose/HbA1c - Diabetes screening
• BNP/NT-proBNP - Heart failure assessment

Echocardiography (Essential in All AF):
• Left atrial size (enlarged suggests longstanding AF)
• Left ventricular function (EF, diastolic function)
• Valvular disease (especially mitral stenosis)
• Left ventricular hypertrophy
• Wall motion abnormalities
• Right heart assessment
• Pericardial disease`,

    differentialDiagnosis: [
      'Atrial flutter - Regular atrial activity at 250-350 bpm with "sawtooth" flutter waves; may have regular ventricular response',
      'Multifocal atrial tachycardia (MAT) - ≥3 P wave morphologies; associated with COPD; irregularly irregular but has P waves',
      'Sinus rhythm with frequent APCs - Underlying sinus rhythm visible; APCs have P waves',
      'Sinus arrhythmia - Irregular rate varies with respiration; P waves present',
      'Wandering atrial pacemaker - ≥3 P wave morphologies; rate usually <100 bpm',
      'Atrial tachycardia with variable block - Regular atrial activity with variable AV conduction',
      'WPW with AF - Wide complex irregular tachycardia; delta waves when in sinus rhythm; AVOID AV nodal blockers',
      'Artifact - Movement, tremor, or electrical interference mimicking fibrillatory baseline',
      'Ventricular fibrillation - No organized QRS complexes; cardiac arrest',
      'Polymorphic VT/Torsades - Wide complex, varying QRS axis; usually symptomatic',
    ],

    investigations: [
      {
        name: '12-Lead Electrocardiogram (ECG)',
        indication: '🔴 IMMEDIATE - Essential for diagnosis; confirms AF, assesses rate, identifies pre-excitation or ischemia',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH)',
        indication: '🔴 IMMEDIATE - Essential in ALL new AF; hyperthyroidism is reversible cause',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Assess for anemia (high output cause), infection/sepsis precipitant',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Na, K, Mg)',
        indication: '🟠 URGENT - Hypokalemia/hypomagnesemia predispose to AF and affect management',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, eGFR)',
        indication: '🟠 URGENT - Essential for drug dosing (especially DOACs); CKD is risk factor',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟡 Routine - Affects anticoagulation choice; liver disease increases bleeding risk',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: '🟠 URGENT - Baseline before anticoagulation; detect pre-existing coagulopathy',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Glucose / HbA1c',
        indication: '🟡 Routine - Diabetes is CHA₂DS₂-VASc component and increases CV risk',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 URGENT - Assess cardiac size, pulmonary congestion, identify lung pathology',
        availability: 'BASIC'
      },
      {
        name: 'Transthoracic Echocardiogram (TTE)',
        indication: '🟠 URGENT - Essential in all AF; assess LA size, LV function, valvular disease',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'BNP / NT-proBNP',
        indication: '🟡 Routine - Assess heart failure; prognostic value',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Holter Monitor (24-48 hours)',
        indication: '🟡 Routine - Document paroxysmal AF; assess rate control; detect pauses',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Transesophageal Echocardiogram (TEE)',
        indication: '🟠 URGENT - Before cardioversion if AF >48h and not anticoagulated; detects LAA thrombus',
        availability: 'ADVANCED'
      },
      {
        name: 'Cardiac CT Angiography',
        indication: '🟡 Routine - Assess coronary disease if suspected; pre-ablation planning',
        availability: 'ADVANCED'
      },
      {
        name: 'Exercise Stress Test',
        indication: '🟡 Routine - Assess rate control during exercise; evaluate for ischemia',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Sleep Study (Polysomnography)',
        indication: '🟡 Routine - If obstructive sleep apnea suspected; common reversible factor',
        availability: 'ADVANCED'
      },
      {
        name: 'Electrophysiology Study',
        indication: '🟡 Routine - Pre-ablation assessment; evaluate conduction system',
        availability: 'ADVANCED'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS HEMODYNAMIC STABILITY - If unstable (hypotension, chest pain, pulmonary edema, altered mental status) → Immediate synchronized cardioversion',
        '⚡ Establish IV access and continuous cardiac monitoring',
        '⚡ 12-lead ECG - Confirm AF, assess ventricular rate, exclude pre-excitation (WPW)',
        '⚡ OXYGEN - If SpO2 <94% or respiratory distress',
        '⚡ Identify and treat precipitants - Sepsis, PE, MI, thyrotoxicosis, electrolyte abnormalities',
        '⚡ RATE CONTROL (if stable with RVR) - Target HR <110 bpm initially',
        '⚡ Calculate CHA₂DS₂-VASc and HAS-BLED scores',
        '⚡ Determine AF duration - If >48 hours or unknown, anticoagulate before cardioversion',
        '⚡ Anticoagulation decision - Start if CHA₂DS₂-VASc ≥2 (men) or ≥3 (women)',
        '⚡ Echocardiography - Arrange within 24-48 hours for all new AF',
        '⚡ Check thyroid function, electrolytes, renal function',
        '⚡ AVOID AV nodal blockers (beta-blockers, calcium blockers, digoxin) if WPW suspected',
      ],
      pharmacological: [
        {
          drug: 'Metoprolol',
          indication: '🔴 First-line Rate Control - Beta-blocker for AF with RVR; preferred if concurrent CAD, HFrEF, hypertension',
          dose: 'Acute: 2.5-5 mg IV over 2-5 min, may repeat q5min (max 15 mg). Maintenance: 25-200 mg PO twice daily',
          route: 'IV for acute; PO for maintenance',
          duration: 'Long-term for rate control',
          contraindications: ['Acute decompensated heart failure', 'Severe bradycardia/heart block', 'Severe asthma', 'Hypotension (SBP <90)', 'WPW with pre-excited AF'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue', 'Bronchospasm', 'Masking hypoglycemia'],
          alternatives: ['Bisoprolol 2.5-10mg daily', 'Atenolol 25-100mg daily', 'Carvedilol 3.125-25mg twice daily']
        },
        {
          drug: 'Diltiazem',
          indication: '🔴 Rate Control - Non-dihydropyridine CCB; preferred if beta-blocker contraindicated, COPD/asthma',
          dose: 'Acute: 0.25 mg/kg IV over 2 min (typically 15-20 mg), may repeat 0.35 mg/kg after 15 min. Infusion: 5-15 mg/hour. Maintenance: 120-360 mg PO daily (extended release)',
          route: 'IV for acute; PO for maintenance',
          duration: 'Long-term for rate control',
          contraindications: ['Heart failure with reduced EF', 'Severe bradycardia/heart block', 'Hypotension', 'WPW with pre-excited AF', 'Concurrent IV beta-blocker'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Peripheral edema', 'Constipation', 'Heart failure exacerbation'],
          alternatives: ['Verapamil 40-120mg PO three times daily or 240-480mg daily (extended release)']
        },
        {
          drug: 'Digoxin',
          indication: '🟠 Rate Control - Second-line; useful in heart failure, sedentary patients, resource-limited settings',
          dose: 'Loading: 0.25-0.5 mg IV, then 0.25 mg IV q6h (max 1-1.5 mg/24h). Maintenance: 0.125-0.25 mg PO daily (reduce in elderly, renal impairment)',
          route: 'IV for loading; PO for maintenance',
          duration: 'Long-term; monitor levels',
          contraindications: ['WPW with pre-excited AF', 'Hypertrophic cardiomyopathy with outflow obstruction', 'Severe renal impairment (adjust dose)', 'Hypokalemia (correct first)'],
          sideEffects: ['Digoxin toxicity (nausea, visual disturbance, arrhythmias)', 'Bradycardia', 'Heart block', 'Narrow therapeutic window'],
          alternatives: ['Beta-blocker or CCB preferred for rate control in most patients']
        },
        {
          drug: 'Amiodarone',
          indication: '🟠 Rhythm Control / Rate Control - For cardioversion, maintenance of sinus rhythm, or rate control when other agents fail',
          dose: 'IV loading: 150 mg over 10 min, then 1 mg/min for 6h, then 0.5 mg/min for 18h. PO loading: 200 mg TID for 1 week, then BID for 1 week, then 200 mg daily maintenance',
          route: 'IV or PO',
          duration: 'Long-term if for rhythm control; monitor thyroid, liver, lungs every 6 months',
          contraindications: ['Iodine allergy', 'Severe sinus node dysfunction', 'Second/third-degree heart block (without pacemaker)', 'Thyroid dysfunction (relative)', 'QTc >500ms'],
          sideEffects: ['Thyroid dysfunction (hypo/hyper)', 'Pulmonary fibrosis', 'Hepatotoxicity', 'Corneal deposits', 'Skin photosensitivity', 'Bradycardia', 'QT prolongation'],
          alternatives: ['Flecainide', 'Propafenone', 'Sotalol', 'Dronedarone (not in heart failure)']
        },
        {
          drug: 'Warfarin',
          indication: '🟠 Anticoagulation - For stroke prevention; preferred in valvular AF (rheumatic mitral stenosis, mechanical valves)',
          dose: 'Initial: 2-5 mg PO daily (lower in elderly, liver disease). Adjust to target INR 2.0-3.0 (2.5-3.5 for mechanical valves)',
          route: 'PO',
          duration: 'Lifelong in most cases; INR monitoring every 1-4 weeks when stable',
          contraindications: ['Active major bleeding', 'Severe uncontrolled hypertension', 'Recent major surgery/trauma', 'Pregnancy (teratogenic)', 'Poor compliance/monitoring access'],
          sideEffects: ['Bleeding (major 1-3%/year)', 'Skin necrosis (rare, with protein C deficiency)', 'Multiple drug-food interactions', 'Requires frequent monitoring'],
          alternatives: ['DOACs (rivaroxaban, apixaban, dabigatran, edoxaban) for non-valvular AF']
        },
        {
          drug: 'Rivaroxaban',
          indication: '🟠 Anticoagulation - DOAC for stroke prevention in non-valvular AF; no INR monitoring needed',
          dose: 'CrCl >50: 20 mg PO once daily with evening meal. CrCl 15-50: 15 mg PO once daily with evening meal. CrCl <15: Avoid',
          route: 'PO',
          duration: 'Lifelong in most cases',
          contraindications: ['Valvular AF (mechanical valve, moderate-severe mitral stenosis)', 'Severe renal impairment (CrCl <15)', 'Active bleeding', 'Severe liver disease', 'Pregnancy/breastfeeding'],
          sideEffects: ['Bleeding', 'GI upset', 'No routine reversal agent widely available (andexanet alfa expensive)', 'No monitoring parameter'],
          alternatives: ['Apixaban 5mg BID (preferred in elderly)', 'Dabigatran 150mg BID', 'Edoxaban 60mg daily']
        },
        {
          drug: 'Apixaban',
          indication: '🟠 Anticoagulation - DOAC; lowest bleeding risk among DOACs; preferred in elderly and renal impairment',
          dose: 'Standard: 5 mg PO twice daily. Reduced dose (2.5 mg BID) if ≥2 of: age ≥80, weight ≤60 kg, creatinine ≥133 μmol/L',
          route: 'PO',
          duration: 'Lifelong in most cases',
          contraindications: ['Valvular AF', 'Severe hepatic impairment', 'Active bleeding', 'Pregnancy'],
          sideEffects: ['Bleeding (lower rates than warfarin)', 'Nausea', 'Anemia'],
          alternatives: ['Rivaroxaban', 'Dabigatran', 'Edoxaban', 'Warfarin']
        },
        {
          drug: 'Heparin (Unfractionated)',
          indication: '🔴 Bridging Anticoagulation - For acute anticoagulation while initiating warfarin or peri-procedural management',
          dose: 'IV bolus: 60-80 units/kg (max 5000 units), then infusion 12-18 units/kg/hour. Target aPTT 1.5-2.5x control',
          route: 'IV',
          duration: 'Until therapeutic INR achieved (minimum 5 days overlap with warfarin) or procedure completed',
          contraindications: ['Active major bleeding', 'Heparin-induced thrombocytopenia history', 'Severe thrombocytopenia'],
          sideEffects: ['Bleeding', 'HIT (heparin-induced thrombocytopenia)', 'Osteoporosis (long-term)'],
          alternatives: ['Enoxaparin 1mg/kg SC q12h', 'DOACs (no bridging needed)']
        },
        {
          drug: 'Enoxaparin',
          indication: '🟠 Bridging Anticoagulation - LMWH for transition to warfarin or peri-procedural management',
          dose: 'Treatment dose: 1 mg/kg SC every 12 hours. Prophylactic: 40 mg SC daily. Adjust for renal function (CrCl <30: use UFH or reduce dose)',
          route: 'Subcutaneous',
          duration: 'Until INR therapeutic or procedure completed',
          contraindications: ['CrCl <15 mL/min (use UFH)', 'HIT history', 'Active bleeding'],
          sideEffects: ['Bleeding', 'Injection site hematoma', 'HIT (less common than UFH)', 'Osteoporosis'],
          alternatives: ['Unfractionated heparin', 'Fondaparinux']
        },
        {
          drug: 'Flecainide',
          indication: '🟡 Rhythm Control - "Pill-in-pocket" for cardioversion; maintenance of sinus rhythm in structurally normal hearts',
          dose: 'Cardioversion: 200-300 mg PO single dose. Maintenance: 50-150 mg PO twice daily',
          route: 'PO',
          duration: 'Long-term if for rhythm control maintenance',
          contraindications: ['Structural heart disease (CAD, heart failure, LVH)', 'Pre-existing conduction disease', 'Brugada syndrome'],
          sideEffects: ['Proarrhythmia (ventricular arrhythmias)', 'Heart failure', 'Dizziness', 'Visual disturbances'],
          alternatives: ['Propafenone', 'Amiodarone', 'Sotalol', 'Dronedarone']
        },
        {
          drug: 'Aspirin',
          indication: '🟡 LIMITED ROLE - Only for patients who refuse any anticoagulation; NOT recommended for stroke prevention in AF',
          dose: '75-100 mg PO daily',
          route: 'PO',
          duration: 'If used, long-term; but anticoagulation preferred',
          contraindications: ['Active peptic ulcer', 'Aspirin allergy', 'Bleeding disorders'],
          sideEffects: ['GI bleeding', 'Peptic ulcer', 'Aspirin-exacerbated respiratory disease'],
          alternatives: ['Proper anticoagulation is standard of care; aspirin is inferior']
        },
      ],
      nonPharmacological: [
        'Lifestyle Modification: Weight loss (10% reduction decreases AF burden), exercise, reduce alcohol, treat sleep apnea',
        'Electrical Cardioversion: Synchronized DC cardioversion (120-200J biphasic) for hemodynamically unstable AF or elective restoration of sinus rhythm',
        'Catheter Ablation: Pulmonary vein isolation; consider for symptomatic paroxysmal/persistent AF refractory to or intolerant of antiarrhythmic drugs; first-line in selected patients',
        'AV Node Ablation with Pacemaker: For rate control in refractory cases; creates complete heart block requiring permanent pacemaker',
        'Left Atrial Appendage Closure (LAAC): Device-based stroke prevention (Watchman, Amulet) for patients unable to take long-term anticoagulation',
        'Surgical Maze Procedure: Concomitant with cardiac surgery (valve surgery, CABG); creates linear scars to interrupt re-entry circuits',
        'Upstream Therapy: ACE inhibitors/ARBs, statins - may reduce AF incidence in high-risk patients (hypertension, heart failure)',
        'Rate vs Rhythm Control Decision: Individualized; rhythm control preferred for symptomatic patients, young patients, first episode, tachycardia-mediated cardiomyopathy',
      ],
      surgical: [
        'Surgical Maze Procedure (Cox-Maze IV): Gold standard surgical ablation; typically performed concomitantly with other cardiac surgery; creates transmural lesions using bipolar radiofrequency or cryoablation; success rate >90% for sinus rhythm restoration',
        'Left Atrial Appendage Ligation/Excision: Surgical removal or closure of LAA during cardiac surgery; reduces stroke risk; may allow cessation of anticoagulation in selected cases',
        'Minimally Invasive Surgical Ablation: Thoracoscopic approach for standalone AF ablation; bilateral pulmonary vein isolation with ganglionic plexus ablation',
        'Hybrid AF Ablation: Combined surgical (epicardial) and catheter (endocardial) approach for persistent/long-standing persistent AF',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Hemodynamically unstable AF (hypotension, pulmonary edema, ongoing ischemia, altered mental status) → Immediate cardioversion',
        '🔴 EMERGENCY - New stroke or TIA in setting of AF → Neurology and cardiology consultation',
        '🔴 EMERGENCY - Pre-excited AF (WPW) with rapid irregular wide-complex tachycardia → Avoid AV nodal blockers, consider cardioversion or procainamide',
        '🟠 URGENT - New AF for comprehensive evaluation, echocardiography, and management initiation',
        '🟠 URGENT - AF with heart failure for optimization of both conditions',
        '🟠 URGENT - Difficult rate control despite adequate doses of rate control agents',
        '🟡 ROUTINE - Consideration of rhythm control strategy or catheter ablation',
        '🟡 ROUTINE - Anticoagulation management challenges (bleeding events, high HAS-BLED)',
        '🟡 ROUTINE - Young patients (<65) with AF for comprehensive evaluation and consideration of ablation',
        '🟡 ROUTINE - Valvular AF (rheumatic mitral stenosis) for surgical evaluation',
      ],
      monitoring: [
        'Heart Rate: Target <110 bpm at rest (lenient) or <80 bpm (strict, if symptomatic); check rate control with Holter',
        'Rhythm: ECG at follow-up visits; Holter if symptomatic episodes or paroxysmal AF assessment',
        'INR (if on warfarin): Weekly initially, then every 1-4 weeks when stable; target 2.0-3.0 (2.5-3.5 for mechanical valves)',
        'Renal Function: Check creatinine/eGFR at baseline and annually (more frequently if on DOACs or impaired baseline)',
        'Thyroid Function: Annually, or more frequently if on amiodarone (every 6 months)',
        'Liver Function: Annually, or every 6 months if on amiodarone',
        'Symptoms: EHRA score at each visit; quality of life assessment',
        'Echocardiography: Repeat if clinical change (new heart failure symptoms, murmur) or annually if structural heart disease',
        'Bleeding Assessment: Review at each visit; address modifiable HAS-BLED factors',
        'Amiodarone Surveillance (if applicable): Thyroid, liver, pulmonary function tests every 6 months; annual chest X-ray; ophthalmology if visual symptoms',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

INITIAL MANAGEMENT:
• After new AF diagnosis: Review within 1-2 weeks
• After initiating rate control: Assess effectiveness within 2-4 weeks
• After starting anticoagulation: Review within 1-2 weeks (sooner if warfarin for INR check)
• After cardioversion: Follow-up within 1-2 weeks; continue anticoagulation ≥4 weeks (often indefinite based on CHA₂DS₂-VASc)

STABLE AF:
• Rate-controlled, anticoagulated: Every 3-6 months initially, then annually if stable
• On amiodarone: Every 6 months for toxicity monitoring
• Post-ablation: Follow-up at 3, 6, 12 months with rhythm monitoring

MONITORING AT EACH VISIT:
• Symptoms (palpitations, dyspnea, exercise tolerance)
• Pulse rate and rhythm
• Blood pressure
• Review medications and adherence
• INR if on warfarin
• Side effects (bleeding, drug-specific toxicities)
• Lifestyle factors (alcohol, weight, sleep apnea)
• Reassess CHA₂DS₂-VASc and HAS-BLED annually

LONG-TERM ANTICOAGULATION CONSIDERATIONS:
• CHA₂DS₂-VASc ≥2 (men) or ≥3 (women): Lifelong anticoagulation
• Score 1: Individual decision; generally favor anticoagulation
• Score 0: No anticoagulation needed
• Post-cardioversion/ablation: Continue ≥4 weeks; long-term based on stroke risk score (not AF status)
• Successful ablation does NOT eliminate need for anticoagulation if CHA₂DS₂-VASc indicates risk

SPECIAL POPULATIONS:
• Elderly (>75): Lower threshold for rate control; careful anticoagulation dosing; fall risk assessment
• Pregnancy: Rate control with beta-blockers; avoid warfarin (teratogenic) - use heparin; manage with MFM
• CKD: Adjust drug doses; warfarin accumulates; DOAC dose adjustments; increased bleeding and stroke risk
• Post-cardiac surgery: AF common (20-40%); usually self-limiting; rate control and short-term anticoagulation`
    },

    redFlags: [
      '🔴 CRITICAL: Hemodynamic instability (SBP <90, pulmonary edema, ongoing chest pain, altered mental status) → Immediate synchronized cardioversion',
      '🔴 CRITICAL: Pre-excited AF (WPW) - irregular wide-complex tachycardia → AVOID digoxin, beta-blockers, calcium channel blockers; consider procainamide or cardioversion',
      '🔴 CRITICAL: New stroke or TIA symptoms in AF patient → Emergent stroke evaluation; may need thrombolysis if within window',
      '🔴 CRITICAL: AF with very rapid rate (>150-180 bpm) and signs of ischemia → Urgent rate control; rule out MI',
      '🔴 CRITICAL: Massive bleeding on anticoagulation → Stop anticoagulant; reversal agents; supportive care',
      '🟠 URGENT: New AF in setting of acute MI, PE, or sepsis → Treat underlying condition; may resolve with treatment',
      '🟠 URGENT: AF with heart failure decompensation → Rate control critical; may need IV diuretics, inotropes',
      '🟠 URGENT: AF with thyrotoxicosis → Rate control difficult until euthyroid; beta-blocker first-line',
      '🟠 URGENT: Very slow ventricular rate (<40 bpm) in AF → Complete heart block; may need pacemaker',
      '🟠 URGENT: INR >5 or major bleeding on warfarin → Hold warfarin; vitamin K if severe; FFP/PCC if life-threatening',
      '🟠 URGENT: Subtherapeutic INR with recent stroke/TIA → High short-term stroke risk; optimize anticoagulation urgently',
    ],

    complications: [
      'Ischemic Stroke: 5-fold increased risk; cardioembolic strokes are larger and more disabling; anticoagulation reduces risk by 60-70%',
      'Systemic Embolism: Arterial thromboembolism to limbs, mesentery, kidneys; same mechanism as stroke',
      'Heart Failure: Both cause and consequence of AF; tachycardia-mediated cardiomyopathy may be reversible',
      'Tachycardia-Mediated Cardiomyopathy: Reduced EF due to persistent rapid rates; usually reversible with rate/rhythm control',
      'Cognitive Impairment/Dementia: Associated with AF independent of stroke; possible microembolism or hypoperfusion',
      'Reduced Quality of Life: Symptoms significantly impact daily activities and well-being',
      'Mortality: AF associated with 1.5-2 fold increased all-cause mortality',
      'Bleeding Complications: Anticoagulation-related; intracranial hemorrhage most feared (0.5-1%/year on warfarin)',
      'Drug Side Effects: Amiodarone toxicity (thyroid, lung, liver), digoxin toxicity, bradycardia from rate control agents',
      'Cardioversion Complications: Thromboembolism if not anticoagulated, skin burns, arrhythmias',
      'Catheter Ablation Complications: Pulmonary vein stenosis, cardiac perforation/tamponade, stroke, phrenic nerve injury, esophageal fistula (rare but fatal)',
      'Pacemaker Complications: Infection, lead displacement, pocket hematoma (post-AV node ablation)',
    ],

    prognosis: `OVERALL PROGNOSIS:

MORTALITY:
• AF associated with 1.5-2 fold increased mortality
• Mortality higher with:
  - Heart failure
  - Structural heart disease
  - Increasing CHA₂DS₂-VASc score
  - Poorly controlled ventricular rate
  - Non-anticoagulated patients

STROKE RISK:
• Without anticoagulation: 5% per year (varies by CHA₂DS₂-VASc)
• With anticoagulation: Risk reduced by 60-70%
• AF-related strokes are more severe, larger, and more often fatal

PROGRESSION:
• Paroxysmal → Persistent: ~5-10% per year
• "AF begets AF": Remodeling makes AF more sustained
• Early rhythm control may slow progression

AFRICAN CONTEXT PROGNOSIS:
• Worse outcomes due to:
  - Limited anticoagulation access and monitoring
  - Late presentation with complications
  - Higher rheumatic heart disease rates (difficult to manage)
  - Limited access to ablation and advanced therapies
• Stroke mortality 2-3x higher than Western populations
• Emphasis on prevention, early detection, and optimizing available therapies

FAVORABLE PROGNOSTIC FACTORS:
• Young age
• No structural heart disease
• Paroxysmal pattern
• Early rhythm control
• Good anticoagulation adherence
• Reversible cause (thyrotoxicosis, post-operative)

POOR PROGNOSTIC FACTORS:
• Heart failure with reduced EF
• Significant valvular disease
• Large left atrium
• Long-standing persistent AF
• Multiple comorbidities
• Poor anticoagulation adherence`,

    prevention: `PRIMARY PREVENTION (Preventing AF Development):

Risk Factor Modification:
• Hypertension control: Most important modifiable risk factor; target <130/80 mmHg
• Weight management: Obesity increases AF risk; 10% weight loss reduces AF burden
• Diabetes control: Good glycemic control may reduce AF incidence
• Alcohol moderation: Limit to ≤1 drink/day; avoid binge drinking
• Smoking cessation: Reduces cardiovascular risk
• Exercise: Regular moderate exercise; avoid excessive endurance exercise (paradoxically increases AF)
• Sleep apnea treatment: CPAP reduces AF burden and improves ablation outcomes

Upstream Therapy:
• ACE inhibitors/ARBs: May reduce new-onset AF in hypertension and heart failure
• Statins: Possible anti-inflammatory effect; benefit unclear
• Omega-3 fatty acids: Mixed evidence; may increase AF in some studies

SECONDARY PREVENTION (Preventing AF Complications):

Stroke Prevention:
• Anticoagulation based on CHA₂DS₂-VASc score
• DOACs preferred over warfarin for non-valvular AF (where available)
• Regular adherence monitoring
• Patient education on stroke warning signs (FAST)

Heart Failure Prevention:
• Rate control to prevent tachycardia-mediated cardiomyopathy
• Rhythm control in selected patients
• Treat underlying conditions

TERTIARY PREVENTION (African-Specific Strategies):

• Rheumatic Heart Disease Prevention:
  - Primary prevention of rheumatic fever (prompt treatment of streptococcal pharyngitis)
  - Secondary prophylaxis with penicillin
  - Early detection and management of rheumatic valvular disease

• Health System Strengthening:
  - Increase ECG availability at primary care level
  - Train healthcare workers in AF detection (pulse palpation)
  - Improve anticoagulation monitoring infrastructure
  - Develop INR point-of-care testing programs
  - Advocate for affordable DOAC access

• Community Education:
  - Awareness of irregular pulse as warning sign
  - Stroke symptoms (FAST)
  - Importance of medication adherence
  - Avoiding traditional remedies that may interact with anticoagulants`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING ATRIAL FIBRILLATION - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS ATRIAL FIBRILLATION (AF)?
Atrial fibrillation is an irregular heart rhythm where the upper chambers of your heart (atria) beat in a fast, disorganized way. Instead of beating regularly, the heart "quivers" and beats irregularly. This is the most common type of abnormal heart rhythm.

WHY IS AF IMPORTANT?
The main concern with AF is that it increases your risk of:
• STROKE: Blood can pool in the heart and form clots that travel to the brain
• HEART FAILURE: The heart may weaken if beating too fast for too long
• REDUCED QUALITY OF LIFE: Symptoms can affect daily activities

WHAT CAUSES AF?
Common causes include:
• High blood pressure (most common)
• Heart valve problems (especially rheumatic heart disease in Africa)
• Heart failure
• Overactive thyroid
• Excessive alcohol
• Obesity
• Sleep apnea
• Sometimes no obvious cause is found

WHAT ARE THE SYMPTOMS?
Many people feel:
• Heart racing, fluttering, or pounding (palpitations)
• Shortness of breath
• Tiredness, weakness
• Dizziness or lightheadedness
• Chest discomfort

⚠️ Some people have NO symptoms - AF is still dangerous even without symptoms!

TREATMENT - THE THREE PILLARS:

1. STROKE PREVENTION (Most Important!)
• Blood thinning medication (anticoagulation) prevents clots
• Your doctor will calculate your stroke risk to decide if you need this
• This is the most important treatment - it prevents stroke!

2. RATE CONTROL
• Medications to slow your heart rate to normal
• Target: Heart rate less than 100-110 beats per minute at rest

3. RHYTHM CONTROL (for some patients)
• Medications or procedures to restore normal rhythm
• Not everyone needs this - your doctor will advise

MEDICATIONS YOU MAY BE PRESCRIBED:

Blood Thinners (Anticoagulants):
• Warfarin: Requires regular blood tests (INR monitoring)
• Newer medications (DOACs - rivaroxaban, apixaban): No regular blood tests needed
• Take EXACTLY as prescribed - missing doses increases stroke risk

Rate Control Medications:
• Beta-blockers (metoprolol, bisoprolol)
• Calcium channel blockers (diltiazem, verapamil)
• Digoxin

IMPORTANT LIFESTYLE CHANGES:

• Limit alcohol: Can trigger AF episodes; avoid binge drinking
• Maintain healthy weight: Weight loss reduces AF symptoms
• Control blood pressure: Take your BP medications regularly
• Exercise moderately: Good for your heart, but avoid excessive exercise
• Treat sleep apnea: If you snore heavily, tell your doctor
• Reduce caffeine: May trigger symptoms in some people
• Stop smoking: Reduces overall heart risk

IF YOU ARE ON WARFARIN:
• Regular blood tests (INR) are essential - DO NOT skip appointments
• Target INR is usually 2.0-3.0 (your doctor will tell you)
• Many foods and medications interact with warfarin:
  - Maintain consistent vitamin K intake (green leafy vegetables)
  - Tell ALL doctors/dentists you are on warfarin
  - Avoid traditional remedies without checking with your doctor
• Watch for signs of bleeding

WHEN TO SEEK EMERGENCY CARE:
Go to the hospital IMMEDIATELY if you have:
• STROKE WARNING SIGNS (FAST):
  - Face drooping on one side
  - Arm weakness
  - Speech difficulty
  - Time to call for help!
• Chest pain or pressure
• Severe shortness of breath
• Fainting or near-fainting
• Heart racing very fast with dizziness
• Major bleeding (blood in stool, urine, vomiting blood, severe bruising)

MONITORING YOUR CONDITION:
• Check your pulse regularly - report if very fast, very slow, or irregular
• Keep all clinic appointments
• Take medications exactly as prescribed - do not stop without asking your doctor
• Report new symptoms or side effects promptly

REMEMBER:
• AF is manageable with proper treatment
• Stroke prevention is the top priority
• Take your medications as prescribed - adherence saves lives
• Lifestyle changes make a real difference
• Regular follow-up with your doctor is essential

Your doctor and healthcare team are your partners in managing this condition. Ask questions and share any concerns you have.`,

    references: [
      'Hindricks G, et al. 2020 ESC Guidelines for the diagnosis and management of atrial fibrillation. Eur Heart J. 2021;42:373-498. - European Society of Cardiology comprehensive guidelines',
      'January CT, et al. 2019 AHA/ACC/HRS Focused Update on Atrial Fibrillation. Circulation. 2019;140:e125-e151. - American guidelines',
      'Lip GYH, et al. Stroke prevention in atrial fibrillation: a systematic review. JAMA. 2012;307:2079-2085. - CHA₂DS₂-VASc validation',
      'Pisters R, et al. HAS-BLED: A novel practical tool to assess bleeding risk in anticoagulated patients with atrial fibrillation. Chest. 2010;138:1093-1100. - HAS-BLED score derivation',
      'Kirchhof P, et al. Early Rhythm-Control Therapy in Patients with Atrial Fibrillation (EAST-AFNET 4). N Engl J Med. 2020;383:1305-1316. - Landmark rhythm control trial',
      'Connolly SJ, et al. Dabigatran versus warfarin in patients with atrial fibrillation (RE-LY). N Engl J Med. 2009;361:1139-1151. - DOAC evidence',
      'Sliwa K, et al. Atrial fibrillation in Africa: current epidemiology, pathophysiology, and management approach. Circ Arrhythm Electrophysiol. 2022;15:e010897. - African-specific review',
      'Stambler BS, et al. Atrial fibrillation in Sub-Saharan Africa: epidemiology, unmet needs, and treatment options. Int J Cardiol. 2021;329:208-215. - African epidemiology',
      'Wyse DG, et al. AFFIRM Investigators. A comparison of rate control and rhythm control in patients with atrial fibrillation. N Engl J Med. 2002;347:1825-1833. - AFFIRM trial',
      'Calkins H, et al. 2017 HRS/EHRA/ECAS/APHRS/SOLAECE expert consensus statement on catheter and surgical ablation of atrial fibrillation. Europace. 2018;20:e1-e160. - Ablation guidelines',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 32. DEEP VEIN THROMBOSIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '32',
    name: 'Deep Vein Thrombosis',
    slug: 'deep-vein-thrombosis',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I82.40',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Deep vein thrombosis (DVT) is the formation of a blood clot (thrombus) in a deep vein, most commonly in the lower extremities (iliac, femoral, popliteal veins). DVT is part of the venous thromboembolism (VTE) spectrum, which includes pulmonary embolism (PE) - a potentially life-threatening complication when thrombus dislodges and travels to the pulmonary arteries.

DVT affects approximately 1-2 per 1,000 adults annually in developed countries, with significant morbidity from post-thrombotic syndrome and mortality from PE. In Africa, DVT is increasingly recognized with changing lifestyles, urbanization, increased surgical interventions, and HIV prevalence. However, significant underdiagnosis exists due to limited access to diagnostic imaging and D-dimer testing.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• DVT + PE = Venous Thromboembolism (VTE) - same disease spectrum
• Wells Score stratifies pre-test probability: Low (<2), Moderate (2-6), High (>6)
• Low Wells + Negative D-dimer = DVT ruled out (no imaging needed)
• Compression ultrasonography is first-line diagnostic imaging
• Proximal DVT (above knee) has higher PE risk - ALWAYS anticoagulate
• Distal DVT (below knee) - may observe with serial ultrasound or anticoagulate
• Anticoagulation duration: Provoked (3 months) vs Unprovoked (≥6 months to indefinite)
• DOACs are first-line for most DVT; LMWH preferred in cancer-associated thrombosis
• Post-thrombotic syndrome affects 20-50% - compression stockings controversial
• Catheter-directed thrombolysis for extensive iliofemoral DVT (phlegmasia)
• IVC filter only for contraindication to anticoagulation or failure despite adequate anticoagulation`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Annual incidence: 1-2 per 1,000 adults
• Increases exponentially with age: <1/1,000 in <50 years; 5-6/1,000 in >80 years
• Slight male predominance
• 30-50% of untreated proximal DVT progress to PE
• Recurrence rate: 5-10% per year; higher in unprovoked DVT
• Case fatality rate: 1-5% for DVT; 15-25% for PE

AFRICAN EPIDEMIOLOGY:
• Previously considered rare in Africa ("myth of rarity")
• Hospital-based studies show increasing recognition: 2-4% of medical admissions
• Risk factors increasingly prevalent: HIV, cancer, surgery, immobility, oral contraceptives
• HIV infection: 2-10 fold increased VTE risk (inflammation, protein S deficiency, ART effects)
• Significant underdiagnosis due to limited imaging access
• Many cases present late or as PE
• Post-thrombotic syndrome: Major long-term burden

VIRCHOW'S TRIAD (Risk Factors):

1. Stasis:
• Immobility (bed rest, long travel, paralysis)
• Surgery (especially orthopedic, pelvic)
• Heart failure
• Obesity

2. Endothelial Injury:
• Surgery, trauma
• Central venous catheters
• Previous DVT
• Vasculitis

3. Hypercoagulability:
• Acquired:
  - Cancer (especially pancreatic, lung, GI, brain)
  - Pregnancy/postpartum
  - Oral contraceptives/HRT
  - HIV infection
  - Nephrotic syndrome
  - Antiphospholipid syndrome
  - Myeloproliferative disorders
  - Inflammatory bowel disease
  - COVID-19 infection
  
• Inherited:
  - Factor V Leiden (most common)
  - Prothrombin G20210A mutation
  - Protein C deficiency
  - Protein S deficiency
  - Antithrombin deficiency
  - Hyperhomocysteinemia

AFRICAN-SPECIFIC RISK FACTORS:
• HIV/AIDS: Major driver of VTE in Africa; protein S deficiency common
• Tuberculosis: Hypercoagulable state
• Sickle cell disease: Increased thrombotic risk
• Limited thromboprophylaxis in surgical patients
• Traditional bone-setting: Prolonged immobilization
• Long-distance travel (bus, "economy class syndrome")
• Injectable contraceptives (widely used)`,

    clinicalPresentation: `SYMPTOMS:

Lower Extremity DVT (90%+ of cases):
• Unilateral leg swelling (most common symptom)
• Pain/tenderness along deep veins
• Warmth of affected limb
• Erythema or discoloration
• Prominent superficial veins
• May be asymptomatic (50% of cases)

Upper Extremity DVT (5-10%):
• Arm swelling
• Pain
• Often associated with central venous catheters or thoracic outlet obstruction (Paget-Schroetter syndrome)

SIGNS:

Inspection:
• Unilateral edema (compare circumference: >3 cm difference significant)
• Erythema or cyanotic discoloration
• Distended superficial veins
• Skin warmth

Palpation:
• Tenderness along deep vein course
• Palpable cord (thrombosed vein)
• Pitting edema
• Increased warmth

Classic Signs (Low sensitivity):
• Homan sign: Calf pain with passive dorsiflexion (unreliable, may dislodge clot)
• Pratt sign: Calf tenderness with compression
• Moses sign: Pain with calf compression against tibia

DVT ANATOMICAL CLASSIFICATION:

Proximal DVT (Higher PE risk - 50%):
• Iliofemoral veins
• Common femoral vein
• Superficial femoral vein (misnomer - it IS a deep vein)
• Popliteal vein

Distal DVT (Isolated calf vein):
• Posterior tibial veins
• Peroneal veins
• Anterior tibial veins
• Gastrocnemius and soleal veins
• 10-20% propagate proximally if untreated

SPECIAL PRESENTATIONS:

Phlegmasia Cerulea Dolens (Limb-Threatening):
• Massive iliofemoral DVT
• Severe pain, cyanosis, massive edema
• Arterial compromise (absent pulses)
• May progress to venous gangrene
• SURGICAL EMERGENCY - needs thrombolysis/thrombectomy

Phlegmasia Alba Dolens:
• "Milk leg" - painful white leg
• Iliofemoral DVT with arterial spasm
• Pale, cool limb with edema

May-Thurner Syndrome:
• Left iliac vein compression by right common iliac artery
• Predominantly affects young women
• Left > right iliofemoral DVT
• Consider in young patients with unprovoked left leg DVT

SYMPTOMS SUGGESTING CONCURRENT PE:
• Dyspnea (most common)
• Pleuritic chest pain
• Cough
• Hemoptysis
• Syncope
• Tachycardia, tachypnea
• Hypotension (massive PE)

⚠️ ALWAYS assess for PE symptoms in any DVT patient`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Clinical Probability Assessment (Wells Score)
Step 2: D-dimer Testing (if low/moderate probability)
Step 3: Compression Ultrasonography
Step 4: Additional Imaging if needed
Step 5: Assess for PE symptoms/signs
Step 6: Evaluate for underlying cause

WELLS SCORE FOR DVT:

| Clinical Feature | Points |
|------------------|--------|
| Active cancer (treatment within 6 months or palliative) | +1 |
| Paralysis, paresis, or recent cast immobilization of lower extremity | +1 |
| Bedridden >3 days or major surgery within 12 weeks | +1 |
| Localized tenderness along distribution of deep venous system | +1 |
| Entire leg swollen | +1 |
| Calf swelling >3 cm compared to asymptomatic leg (measured 10 cm below tibial tuberosity) | +1 |
| Pitting edema confined to symptomatic leg | +1 |
| Collateral superficial veins (non-varicose) | +1 |
| Previously documented DVT | +1 |
| Alternative diagnosis at least as likely as DVT | -2 |

Interpretation:
• Score ≤0: Low probability (DVT prevalence ~5%)
• Score 1-2: Moderate probability (DVT prevalence ~17%)
• Score ≥3: High probability (DVT prevalence ~53%)

DIAGNOSTIC ALGORITHM:

LOW PROBABILITY (Wells ≤0):
• D-dimer test
  - Negative → DVT ruled out (NPV >99%)
  - Positive → Compression ultrasound

MODERATE PROBABILITY (Wells 1-2):
• D-dimer test
  - Negative → DVT ruled out
  - Positive → Compression ultrasound

HIGH PROBABILITY (Wells ≥3):
• Proceed directly to compression ultrasound
• (D-dimer not helpful - high false positive rate)

D-DIMER TESTING:

Interpretation:
• High sensitivity (95-97%), low specificity (40-50%)
• Useful for RULING OUT DVT when negative
• Age-adjusted cutoff: Age × 10 μg/L (for patients >50 years)
  - Example: 70-year-old, cutoff = 700 μg/L instead of 500 μg/L

Causes of Elevated D-dimer (False Positives):
• Age >50
• Pregnancy
• Recent surgery/trauma
• Infection/sepsis
• Cancer
• Liver disease
• Renal disease
• Atrial fibrillation
• Heart failure
• Inflammatory conditions

IMAGING STUDIES:

Compression Ultrasonography (First-line):
• Sensitivity: 94-97% for proximal DVT
• Specificity: 94-98%
• Technique: Apply probe pressure; veins should fully compress
• Non-compressibility of vein = positive for DVT
• Augmentation: Absent flow augmentation with distal compression suggests obstruction
• Limitations: Operator-dependent; less sensitive for calf DVT (70%)

Whole-Leg Ultrasound:
• Includes calf veins
• Identifies isolated distal DVT
• May reduce need for serial imaging

Serial Ultrasound:
• Repeat in 5-7 days if initial negative but clinical suspicion persists
• To detect propagating distal DVT

CT Venography:
• Used in conjunction with CT pulmonary angiography
• Evaluates pelvic and abdominal veins
• Useful for iliofemoral and IVC thrombosis

MR Venography:
• No radiation
• Good for pelvic veins, pregnancy
• Expensive, limited availability

Contrast Venography (Rarely used):
• Historical gold standard
• Invasive, contrast load, risk of post-procedure DVT
• Reserved for equivocal cases or intervention planning

THROMBOPHILIA TESTING:

When to Test:
• Unprovoked DVT in young patients (<50)
• Recurrent unprovoked VTE
• VTE in unusual sites (cerebral, mesenteric, portal)
• Strong family history of VTE
• Warfarin-induced skin necrosis (protein C/S deficiency)

Tests:
• Factor V Leiden mutation
• Prothrombin G20210A mutation
• Antithrombin activity
• Protein C activity
• Protein S activity (free)
• Antiphospholipid antibodies (lupus anticoagulant, anticardiolipin, anti-β2 glycoprotein I)
• Homocysteine level

Timing:
• Many tests affected by acute thrombosis and anticoagulation
• Test 2-4 weeks after stopping anticoagulation, or
• Test before starting anticoagulation (antithrombin, protein C/S affected by heparin/warfarin)`,

    differentialDiagnosis: [
      'Cellulitis - Erythema, warmth, fever; may have skin break/entry point; bilateral leg swelling rare',
      'Superficial thrombophlebitis - Palpable tender cord along superficial vein; overlying erythema; less edema',
      'Ruptured Baker cyst - Posterior knee fullness, acute calf pain/swelling; ultrasound diagnostic',
      'Muscle strain/tear - History of exertion, localized tenderness, ecchymosis may be present',
      'Lymphedema - Chronic, non-pitting edema; skin changes (peau d orange); often bilateral',
      'Chronic venous insufficiency - Bilateral, worse with standing, varicose veins, skin changes (hemosiderin)',
      'Heart failure - Bilateral dependent edema, JVD, dyspnea; elevated BNP',
      'Nephrotic syndrome - Bilateral edema, periorbital edema, proteinuria, hypoalbuminemia',
      'Compartment syndrome - Severe pain out of proportion, tense compartment, pain with passive stretch',
      'Arterial insufficiency - Cool, pale limb; absent pulses; claudication; ulcers on pressure points',
      'May-Thurner syndrome - Young woman, left leg DVT, consider if unprovoked',
      'Hematoma - Trauma history, ecchymosis, may have anticoagulation history',
    ],

    investigations: [
      {
        name: 'Wells Score Calculation',
        indication: '🔴 IMMEDIATE - Calculate pre-test probability before any testing; guides diagnostic pathway',
        availability: 'BASIC'
      },
      {
        name: 'D-dimer (Quantitative)',
        indication: '🔴 IMMEDIATE - If low/moderate Wells score; negative D-dimer rules out DVT; use age-adjusted cutoff',
        availability: 'BASIC'
      },
      {
        name: 'Compression Ultrasonography',
        indication: '🔴 IMMEDIATE - First-line imaging; non-compressible vein confirms DVT; assess proximal vs distal',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Baseline before anticoagulation; assess for anemia, thrombocytopenia, thrombocytosis',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: '🟠 URGENT - Baseline before anticoagulation; identify pre-existing coagulopathy',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, eGFR)',
        indication: '🟠 URGENT - Essential for anticoagulant dosing (especially DOACs, LMWH)',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟡 Routine - Affects anticoagulant choice; liver disease increases bleeding risk',
        availability: 'BASIC'
      },
      {
        name: 'Pregnancy Test (β-hCG)',
        indication: '🔴 IMMEDIATE - All women of childbearing age; pregnancy affects anticoagulant choice and is VTE risk factor',
        availability: 'BASIC'
      },
      {
        name: 'Whole-Leg Ultrasound',
        indication: '🟠 URGENT - If calf DVT suspected; reduces need for serial imaging',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Pulmonary Angiography',
        indication: '🔴 IMMEDIATE - If PE symptoms present; can include CT venography for leg veins',
        availability: 'ADVANCED'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 URGENT - Baseline; exclude other causes of dyspnea if PE suspected',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🟠 URGENT - If PE suspected; may show sinus tachycardia, S1Q3T3, right heart strain',
        availability: 'BASIC'
      },
      {
        name: 'Echocardiogram',
        indication: '🟠 URGENT - If massive PE suspected; assess RV function, may see thrombus in transit',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Test',
        indication: '🟡 Routine - In African settings, HIV is major VTE risk factor; affects long-term management',
        availability: 'BASIC'
      },
      {
        name: 'Cancer Screening (Age-appropriate)',
        indication: '🟡 Routine - Unprovoked DVT warrants occult cancer evaluation; CT chest/abdomen/pelvis if no obvious cause',
        availability: 'ADVANCED'
      },
      {
        name: 'Thrombophilia Panel',
        indication: '🟡 Routine - Young patients with unprovoked DVT, recurrent VTE, strong family history; test after acute phase',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Venography / MR Venography',
        indication: '🟡 Routine - Iliofemoral or IVC thrombosis evaluation; planning for intervention',
        availability: 'ADVANCED'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS FOR PE - Check for dyspnea, chest pain, tachycardia, hypoxia; if present, evaluate urgently',
        '⚡ Calculate Wells Score for DVT pre-test probability',
        '⚡ D-dimer testing if low/moderate probability (skip if high probability)',
        '⚡ Compression ultrasound for diagnosis confirmation',
        '⚡ ANTICOAGULATION - Start immediately once DVT confirmed (or even before imaging if high clinical suspicion)',
        '⚡ Check renal function and platelet count before starting anticoagulation',
        '⚡ Pregnancy test in women of childbearing age',
        '⚡ Pain management - NSAIDs or paracetamol (avoid NSAIDs if on anticoagulation)',
        '⚡ Leg elevation for symptom relief',
        '⚡ Early mobilization - Bed rest NOT recommended unless massive iliofemoral DVT',
        '⚡ Compression therapy - Consider graduated compression stockings (controversial for PTS prevention)',
        '⚡ Assess bleeding risk before anticoagulation',
        '⚡ Identify and address provoking factors (discontinue OCPs, mobilize, etc.)',
      ],
      pharmacological: [
        {
          drug: 'Rivaroxaban',
          indication: '🔴 First-line Anticoagulation - DOAC for acute DVT treatment; single-drug approach (no heparin lead-in)',
          dose: '15 mg PO twice daily with food for 21 days, THEN 20 mg PO once daily with food. Reduce to 10 mg daily after 6 months for extended therapy',
          route: 'PO',
          duration: 'Provoked: 3 months. Unprovoked: ≥6 months to indefinite based on bleeding risk',
          contraindications: ['CrCl <30 mL/min', 'Active major bleeding', 'Severe liver disease', 'Pregnancy/breastfeeding', 'Concomitant strong CYP3A4/P-gp inhibitors'],
          sideEffects: ['Bleeding', 'GI upset', 'Anemia', 'No routine monitoring available'],
          alternatives: ['Apixaban', 'Edoxaban', 'Dabigatran', 'Warfarin with LMWH bridge']
        },
        {
          drug: 'Apixaban',
          indication: '🔴 First-line Anticoagulation - DOAC with lowest bleeding risk; good renal safety profile',
          dose: '10 mg PO twice daily for 7 days, THEN 5 mg PO twice daily. For extended therapy after 6 months: 2.5 mg twice daily',
          route: 'PO',
          duration: 'Provoked: 3 months. Unprovoked: ≥6 months to indefinite',
          contraindications: ['Active major bleeding', 'Severe liver disease', 'Pregnancy', 'Mechanical heart valve'],
          sideEffects: ['Bleeding (lower rates than other DOACs)', 'Nausea', 'Anemia'],
          alternatives: ['Rivaroxaban', 'Edoxaban', 'Dabigatran']
        },
        {
          drug: 'Enoxaparin',
          indication: '🔴 Acute Anticoagulation - LMWH; preferred in pregnancy, cancer-associated DVT, severe renal impairment bridge',
          dose: '1 mg/kg SC every 12 hours OR 1.5 mg/kg SC once daily. Cancer: 1 mg/kg SC every 12 hours preferred',
          route: 'Subcutaneous',
          duration: 'Bridge to warfarin (5+ days until INR therapeutic) OR long-term in cancer (3-6 months minimum)',
          contraindications: ['CrCl <30 mL/min (use UFH or reduce dose)', 'Active major bleeding', 'HIT history', 'Severe thrombocytopenia'],
          sideEffects: ['Bleeding', 'Injection site hematoma/pain', 'HIT (rare)', 'Osteoporosis (long-term)'],
          alternatives: ['Dalteparin', 'Tinzaparin', 'Fondaparinux', 'Unfractionated heparin']
        },
        {
          drug: 'Unfractionated Heparin',
          indication: '🔴 Acute Anticoagulation - For severe renal impairment, need for rapid reversal, or massive DVT with possible thrombolysis',
          dose: 'IV bolus: 80 units/kg, then infusion 18 units/kg/hour. Target aPTT 1.5-2.5x control (60-80 seconds)',
          route: 'IV',
          duration: 'Bridge to warfarin or until stable for DOAC transition',
          contraindications: ['Active major bleeding', 'HIT history', 'Severe uncontrolled hypertension'],
          sideEffects: ['Bleeding', 'HIT (more common than LMWH)', 'Osteoporosis', 'Requires monitoring'],
          alternatives: ['LMWH', 'Fondaparinux']
        },
        {
          drug: 'Warfarin',
          indication: '🟠 Long-term Anticoagulation - Alternative when DOACs contraindicated; requires LMWH/UFH bridge',
          dose: 'Start 2-5 mg PO daily (lower in elderly, liver disease). Adjust to target INR 2.0-3.0',
          route: 'PO',
          duration: 'Provoked: 3 months. Unprovoked: ≥6 months to indefinite',
          contraindications: ['Pregnancy (teratogenic)', 'Active major bleeding', 'Severe liver disease', 'Poor compliance/monitoring access'],
          sideEffects: ['Bleeding', 'Skin necrosis (protein C/S deficiency)', 'Requires INR monitoring', 'Multiple interactions'],
          alternatives: ['DOACs (rivaroxaban, apixaban, edoxaban, dabigatran)']
        },
        {
          drug: 'Edoxaban',
          indication: '🟠 Anticoagulation - DOAC; requires 5-10 days of parenteral anticoagulation first',
          dose: '60 mg PO once daily. Reduce to 30 mg daily if: CrCl 15-50, weight ≤60 kg, or P-gp inhibitor use',
          route: 'PO',
          duration: 'Provoked: 3 months. Unprovoked: ≥6 months to indefinite',
          contraindications: ['CrCl <15 mL/min', 'CrCl >95 mL/min (reduced efficacy)', 'Active bleeding', 'Severe liver disease'],
          sideEffects: ['Bleeding', 'Anemia', 'Rash', 'Abnormal LFTs'],
          alternatives: ['Rivaroxaban', 'Apixaban', 'Dabigatran']
        },
        {
          drug: 'Dabigatran',
          indication: '🟠 Anticoagulation - DOAC; requires 5-10 days of parenteral anticoagulation first; has reversal agent',
          dose: '150 mg PO twice daily. Reduce to 110 mg twice daily if: age >80, CrCl 30-50, high bleeding risk',
          route: 'PO',
          duration: 'Provoked: 3 months. Unprovoked: ≥6 months to indefinite',
          contraindications: ['CrCl <30 mL/min', 'Active bleeding', 'Mechanical heart valve', 'Severe liver disease'],
          sideEffects: ['Bleeding', 'Dyspepsia (most common)', 'GI bleeding higher than warfarin', 'Requires intact capsule'],
          alternatives: ['Rivaroxaban', 'Apixaban', 'Edoxaban']
        },
        {
          drug: 'Fondaparinux',
          indication: '🟠 Alternative Anticoagulation - Synthetic pentasaccharide; no HIT risk; useful if HIT history',
          dose: '<50 kg: 5 mg SC daily. 50-100 kg: 7.5 mg SC daily. >100 kg: 10 mg SC daily',
          route: 'Subcutaneous',
          duration: 'Bridge to warfarin or transition to oral anticoagulant',
          contraindications: ['CrCl <30 mL/min', 'Active major bleeding', 'Bacterial endocarditis', 'Body weight <50 kg (use lower dose)'],
          sideEffects: ['Bleeding', 'Anemia', 'Thrombocytopenia (not HIT)', 'Injection site reactions'],
          alternatives: ['LMWH', 'UFH']
        },
        {
          drug: 'Alteplase (tPA)',
          indication: '🔴 Thrombolysis - For massive iliofemoral DVT with limb threat (phlegmasia cerulea dolens)',
          dose: 'Catheter-directed: 0.5-1 mg/hour via catheter for 12-24 hours. Systemic (rarely used): 100 mg IV over 2 hours',
          route: 'IV (catheter-directed preferred)',
          duration: 'Single treatment course; continue anticoagulation after',
          contraindications: ['Active internal bleeding', 'Recent stroke (<3 months)', 'Intracranial pathology', 'Recent major surgery/trauma', 'Uncontrolled hypertension'],
          sideEffects: ['Major bleeding (10-20%)', 'Intracranial hemorrhage (1-2%)', 'Allergic reactions'],
          alternatives: ['Urokinase', 'Mechanical thrombectomy']
        },
        {
          drug: 'Aspirin (Extended Prophylaxis)',
          indication: '🟡 Extended VTE Prevention - After completing anticoagulation if not continuing indefinite anticoagulation',
          dose: '100 mg PO once daily',
          route: 'PO',
          duration: 'Long-term after stopping anticoagulation',
          contraindications: ['Active bleeding', 'Aspirin allergy', 'Concurrent anticoagulation (increased bleeding)'],
          sideEffects: ['GI bleeding', 'Bruising', 'Tinnitus (high doses)'],
          alternatives: ['Low-dose rivaroxaban 10 mg daily', 'Low-dose apixaban 2.5 mg twice daily']
        },
        {
          drug: 'Ibuprofen/NSAIDs',
          indication: '🟡 Pain Management - For DVT-associated pain and inflammation (use cautiously with anticoagulation)',
          dose: 'Ibuprofen: 400-600 mg PO every 6-8 hours as needed (max 2400 mg/day)',
          route: 'PO',
          duration: 'Short-term for symptom relief; avoid long-term with anticoagulation',
          contraindications: ['Active GI bleeding', 'Renal impairment', 'Concurrent anticoagulation (relative)', 'Heart failure'],
          sideEffects: ['GI upset/bleeding', 'Renal impairment', 'Increased bleeding with anticoagulants'],
          alternatives: ['Paracetamol (acetaminophen) preferred with anticoagulation']
        },
      ],
      nonPharmacological: [
        'Early Mobilization: Ambulation encouraged once anticoagulated; bed rest NOT routinely recommended',
        'Leg Elevation: Elevate affected limb above heart level for comfort and edema reduction',
        'Graduated Compression Stockings: 30-40 mmHg; controversial for PTS prevention (SOX trial negative); may help symptoms',
        'Hydration: Maintain adequate hydration, especially during long travel',
        'Inferior Vena Cava (IVC) Filter: Only for absolute contraindication to anticoagulation or recurrent PE despite adequate anticoagulation; retrievable filters preferred',
        'Catheter-Directed Thrombolysis: For acute iliofemoral DVT with severe symptoms, low bleeding risk, good functional status; reduces post-thrombotic syndrome',
        'Pharmacomechanical Thrombectomy: Combines catheter-directed thrombolysis with mechanical clot removal',
        'Surgical Thrombectomy: Rarely indicated; for phlegmasia with limb threat when thrombolysis fails or contraindicated',
        'Compression Therapy: Intermittent pneumatic compression for hospitalized patients; prophylaxis in those who cannot receive anticoagulation',
      ],
      surgical: [
        'Catheter-Directed Thrombolysis (CDT): Percutaneous catheter placement into thrombus with local thrombolytic infusion; reduces clot burden in iliofemoral DVT; may reduce post-thrombotic syndrome; requires ICU monitoring',
        'Pharmacomechanical Catheter-Directed Thrombolysis (PCDT): Combines CDT with mechanical thrombectomy devices (AngioJet, Trellis); faster thrombus removal; may reduce thrombolytic dose',
        'Percutaneous Mechanical Thrombectomy: Aspiration or fragmentation devices without thrombolytics; for patients with high bleeding risk',
        'Surgical Venous Thrombectomy: Open surgical removal of thrombus; rarely performed; reserved for phlegmasia cerulea dolens with impending venous gangrene when endovascular approaches fail',
        'IVC Filter Placement: Percutaneous insertion of filter in infrarenal IVC; traps emboli; retrievable filters should be removed when anticoagulation possible; permanent filters for permanent contraindication',
        'Venous Stenting: For May-Thurner syndrome or chronic iliofemoral venous obstruction; after thrombus clearance; improves patency and symptoms',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Phlegmasia cerulea dolens (cyanotic, severely swollen limb with arterial compromise) → Vascular surgery/IR for thrombolysis/thrombectomy',
        '🔴 EMERGENCY - Suspected massive PE with hemodynamic instability → ICU, consider systemic thrombolysis',
        '🔴 EMERGENCY - Active major bleeding on anticoagulation → Hematology, may need reversal agents',
        '🟠 URGENT - Extensive iliofemoral DVT in young, active patient → Consider catheter-directed thrombolysis',
        '🟠 URGENT - DVT with contraindication to anticoagulation → IVC filter consideration',
        '🟠 URGENT - Recurrent VTE despite adequate anticoagulation → Hematology evaluation, IVC filter',
        '🟠 URGENT - Suspected May-Thurner syndrome (young woman, left leg DVT) → Vascular for evaluation/stenting',
        '🟡 ROUTINE - Unprovoked DVT for thrombophilia workup and cancer screening → Hematology',
        '🟡 ROUTINE - Cancer-associated thrombosis for optimal anticoagulation strategy → Oncology/Hematology',
        '🟡 ROUTINE - Post-thrombotic syndrome management → Vascular medicine',
      ],
      monitoring: [
        'Clinical Response: Symptoms should improve within days; reassess if worsening',
        'Signs of PE: Monitor for dyspnea, chest pain, tachycardia, hypoxia throughout treatment',
        'Bleeding Assessment: Watch for signs of bleeding (bruising, hematuria, melena, gum bleeding)',
        'INR Monitoring (if on warfarin): Weekly until stable, then every 2-4 weeks; target 2.0-3.0',
        'Renal Function: Check at baseline and periodically for DOAC/LMWH dosing',
        'Platelet Count: Baseline and during heparin therapy (HIT surveillance)',
        'Hemoglobin: Check if bleeding suspected or before invasive procedures',
        'Repeat Ultrasound: At end of treatment to establish new baseline (for comparison if symptoms recur)',
        'Post-Thrombotic Syndrome Assessment: Villalta score at follow-up visits',
        'Extended Treatment Decision: Reassess at 3-6 months for continuation based on risk/benefit',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

INITIAL PHASE (First 3 months):
• 1-2 weeks after diagnosis: Assess symptom improvement, anticoagulation tolerance, bleeding
• 4-6 weeks: Review adherence, side effects, address questions
• 3 months: Major decision point - continue or stop anticoagulation

ANTICOAGULATION DURATION DECISIONS:

Provoked DVT (Transient Risk Factor):
• Surgery, trauma, immobilization, hospitalization
• Duration: 3 months
• Low recurrence risk after stopping (3-5% per year)

Unprovoked DVT (No Identifiable Trigger):
• Duration: Minimum 6 months; consider indefinite
• Higher recurrence risk (10% first year, 5% per year thereafter)
• Decision based on bleeding risk, patient preference, D-dimer after stopping

Cancer-Associated DVT:
• Duration: Minimum 6 months; continue while cancer active or on treatment
• LMWH or DOAC (edoxaban, rivaroxaban) preferred over warfarin
• Reassess every 6 months

EXTENDED ANTICOAGULATION:
• If continuing >3-6 months, consider reduced-dose regimens:
  - Rivaroxaban 10 mg daily
  - Apixaban 2.5 mg twice daily
• Annual reassessment of risk/benefit

MONITORING AT EACH VISIT:
• Symptoms (leg pain, swelling, dyspnea)
• Medication adherence
• Bleeding events (minor and major)
• INR if on warfarin
• Renal function (at least annually for DOACs)
• Quality of life
• Need for procedures (hold anticoagulation)

POST-THROMBOTIC SYNDROME ASSESSMENT:
• Villalta score at 3, 6, 12 months
• Symptoms: Pain, heaviness, cramps, itching, paresthesias
• Signs: Edema, skin induration, hyperpigmentation, venous ectasia, ulceration
• Management: Compression therapy, leg elevation, exercise, wound care if ulcer

D-DIMER FOR RECURRENCE RISK:
• Check 1 month after stopping anticoagulation
• Elevated D-dimer: Higher recurrence risk; consider resuming anticoagulation
• Normal D-dimer: Lower risk; may observe

THROMBOPHILIA TESTING:
• Test 2-4 weeks after stopping anticoagulation
• Consider if: <50 years, unprovoked, recurrent, family history, unusual site
• Results may influence duration but rarely change acute management

CANCER SCREENING:
• Unprovoked DVT: Age-appropriate cancer screening
• Limited CT (chest/abdomen/pelvis) if no obvious cause
• Follow-up abnormalities appropriately`
    },

    redFlags: [
      '🔴 CRITICAL: Phlegmasia cerulea dolens - Massive swelling, cyanosis, severe pain, absent pulses → Limb-threatening emergency, needs immediate thrombolysis/thrombectomy',
      '🔴 CRITICAL: Signs of PE - Sudden dyspnea, pleuritic chest pain, hemoptysis, syncope, hypotension → Evaluate urgently with CTPA',
      '🔴 CRITICAL: Bilateral DVT - Consider IVC thrombosis, pelvic pathology, or underlying malignancy',
      '🔴 CRITICAL: Major bleeding on anticoagulation - GI bleed, intracranial hemorrhage, hemodynamic instability → Stop anticoagulant, reversal agents, supportive care',
      '🟠 URGENT: Rapid symptom progression despite anticoagulation → Consider thrombus extension, inadequate anticoagulation, HIT',
      '🟠 URGENT: DVT in unusual site (upper extremity without catheter, cerebral, mesenteric) → Evaluate for underlying cause, malignancy, thrombophilia',
      '🟠 URGENT: Recurrent DVT on therapeutic anticoagulation → Evaluate compliance, consider IVC filter, hematology consultation',
      '🟠 URGENT: Young patient (<40) with unprovoked DVT → Thorough evaluation for occult cancer and thrombophilia',
      '🟠 URGENT: DVT in pregnancy → Requires LMWH management, close obstetric monitoring',
      '🟠 URGENT: Suspected HIT (platelet drop >50%, thrombosis on heparin) → Stop all heparin, start alternative anticoagulant',
      '🟡 WARNING: Extensive iliofemoral DVT with severe symptoms → Consider early CDT to prevent PTS',
    ],

    complications: [
      'Pulmonary Embolism: 30-50% of untreated proximal DVT; mortality 15-25% for PE; anticoagulation reduces to <5%',
      'Post-Thrombotic Syndrome (PTS): 20-50% within 2 years; chronic pain, swelling, skin changes, ulceration; Villalta score for assessment',
      'Recurrent VTE: 5-10% per year; higher in unprovoked DVT, cancer, thrombophilia; anticoagulation reduces risk',
      'Chronic Thromboembolic Pulmonary Hypertension (CTEPH): 2-4% after PE; dyspnea, exercise intolerance; requires specialist evaluation',
      'Venous Gangrene: Rare complication of phlegmasia; arterial insufficiency leads to tissue necrosis; may require amputation',
      'Anticoagulation-Related Bleeding: Major bleeding 1-3% per year on anticoagulation; intracranial hemorrhage most feared',
      'Heparin-Induced Thrombocytopenia (HIT): 1-5% on UFH, <1% on LMWH; paradoxical thrombosis; stop heparin, use alternative',
      'Warfarin Skin Necrosis: Rare; occurs in protein C/S deficiency; bridge with heparin when starting warfarin',
      'Chronic Venous Insufficiency: Long-term sequela; varicose veins, edema, skin changes',
      'Death: Primarily from PE; overall VTE mortality 10-15%; appropriate anticoagulation greatly reduces mortality',
    ],

    prognosis: `OVERALL PROGNOSIS:

SURVIVAL:
• With appropriate anticoagulation: Mortality 1-2%
• Untreated DVT with PE: Mortality 15-30%
• Cancer-associated DVT: Prognosis largely determined by underlying malignancy

RECURRENCE RATES:
• Provoked DVT (transient risk factor): 3-5% per year after stopping anticoagulation
• Unprovoked DVT: 10% first year, then 5% per year
• Cancer-associated: 15-20% per year even on anticoagulation
• Continuing anticoagulation reduces recurrence by 80-90%

POST-THROMBOTIC SYNDROME:
• Affects 20-50% of DVT patients within 2 years
• More common with: Proximal DVT, recurrent ipsilateral DVT, obesity, inadequate anticoagulation
• Ranges from mild (discomfort, swelling) to severe (ulceration)
• Compression stockings: Controversial for prevention; may help symptoms

PREDICTORS OF POOR OUTCOME:
• Iliofemoral DVT (vs calf DVT)
• Recurrent DVT
• Underlying malignancy
• Inadequate anticoagulation
• Persistent elevated D-dimer after treatment
• Residual thrombus on follow-up ultrasound
• Male sex
• Obesity
• Older age

FAVORABLE FACTORS:
• Distal (calf) DVT only
• Clear provoking factor
• Adequate anticoagulation
• Complete thrombus resolution
• Normal D-dimer after stopping anticoagulation
• Female sex
• Younger age

AFRICAN CONTEXT:
• Later presentation may lead to more extensive thrombosis
• Limited access to advanced interventions (CDT, filters)
• Challenges with long-term anticoagulation monitoring
• HIV and cancer as major drivers affecting prognosis
• Post-thrombotic syndrome: Significant burden in resource-limited settings`,

    prevention: `PRIMARY PREVENTION (Preventing First DVT):

HOSPITALIZED MEDICAL PATIENTS:
• Risk assessment (Padua score)
• Pharmacological prophylaxis (LMWH, UFH, fondaparinux) if high risk
• Mechanical prophylaxis (IPC, graduated compression stockings) if anticoagulation contraindicated
• Early mobilization

SURGICAL PATIENTS:
• Risk stratification (Caprini score)
• Low-risk: Early ambulation
• Moderate-risk: Pharmacological or mechanical prophylaxis
• High-risk (orthopedic, cancer surgery): Extended prophylaxis (up to 35 days)
• Neuraxial anesthesia considerations with anticoagulants

ORTHOPEDIC SURGERY (Highest Risk):
• Hip/knee replacement: Extended prophylaxis 10-35 days
• Options: LMWH, rivaroxaban, apixaban, dabigatran, aspirin (less effective)
• Hip fracture surgery: Prophylaxis until fully mobile

CANCER PATIENTS:
• High-risk outpatient cancer: Consider thromboprophylaxis (Khorana score ≥2)
• LMWH or rivaroxaban/apixaban for high-risk chemotherapy

LONG-DISTANCE TRAVEL:
• Frequent ambulation
• Calf exercises
• Adequate hydration
• Avoid alcohol, sedatives
• Compression stockings for high-risk individuals
• Consider single-dose LMWH for very high-risk travelers

PREGNANCY:
• Identify high-risk women early
• LMWH prophylaxis for: Previous VTE, high-risk thrombophilia, multiple risk factors
• Mechanical prophylaxis during delivery and postpartum

SECONDARY PREVENTION (Preventing Recurrence):

• Adequate duration of anticoagulation based on clinical scenario
• Extended anticoagulation for unprovoked DVT (assess annually)
• Address modifiable risk factors (obesity, immobility, smoking, OCPs)
• Aspirin or low-dose anticoagulation after stopping full anticoagulation
• Patient education on VTE symptoms and when to seek care

AFRICAN-SPECIFIC STRATEGIES:

• Increase awareness of DVT risk in hospitalized patients
• Implement thromboprophylaxis protocols in surgical units
• Identify and treat HIV (major risk factor)
• Educate about risks of prolonged immobility (traditional bone-setting)
• Improve access to D-dimer and ultrasound
• Train healthcare workers in VTE prevention`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING DEEP VEIN THROMBOSIS - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS DEEP VEIN THROMBOSIS (DVT)?
DVT is a blood clot that forms in a deep vein, usually in the leg. The clot blocks blood flow and causes swelling and pain. If the clot breaks loose, it can travel to the lungs and cause a life-threatening condition called pulmonary embolism (PE).

WHY IS DVT DANGEROUS?
• The clot can grow larger if not treated
• Part of the clot can break off and travel to your lungs (PE)
• PE can be life-threatening - it blocks blood flow to the lungs
• Long-term leg problems (swelling, pain, skin changes) can develop

WHAT CAUSES DVT?
Blood clots form when:
• Blood flow slows down (sitting for long periods, bed rest)
• Blood vessels are damaged (surgery, injury)
• Blood clots more easily than normal (inherited conditions, cancer, hormones)

RISK FACTORS:
• Recent surgery (especially hip, knee, pelvis)
• Long bed rest or immobility
• Long travel (plane, bus - "economy class syndrome")
• Cancer
• Pregnancy and postpartum period
• Birth control pills or hormone therapy
• Previous blood clot
• Family history of blood clots
• Obesity
• Smoking
• HIV infection

WHAT ARE THE SYMPTOMS OF DVT?
• Swelling in one leg (especially calf or thigh)
• Pain or tenderness (may feel like a cramp)
• Warmth in the affected area
• Red or discolored skin
• Veins that look larger than normal

⚠️ Some people have NO symptoms - DVT can still be dangerous!

SYMPTOMS OF PULMONARY EMBOLISM (LUNG CLOT) - EMERGENCY!
Seek IMMEDIATE medical attention if you have:
• Sudden shortness of breath
• Chest pain (worse when breathing deeply)
• Rapid heartbeat
• Coughing up blood
• Feeling faint or dizzy
• Excessive sweating

TREATMENT:

Blood Thinners (Anticoagulants):
• These medications prevent the clot from growing and new clots from forming
• Your body naturally dissolves the clot over time
• You will take blood thinners for at least 3 months, sometimes longer
• Types include: pills (rivaroxaban, apixaban, warfarin) or injections (enoxaparin)

TAKING YOUR MEDICATION:
• Take EXACTLY as prescribed - do not miss doses
• Missing doses puts you at risk for another clot
• Do not stop without talking to your doctor
• Keep all follow-up appointments
• Tell ALL your healthcare providers you are on blood thinners

IF YOU ARE ON WARFARIN:
• Regular blood tests (INR) are required
• Many foods and medications interact with warfarin
• Keep your diet consistent (especially green leafy vegetables)
• Avoid alcohol or drink very little

REDUCING BLEEDING RISK:
• Use a soft toothbrush
• Shave with an electric razor
• Be careful with sharp objects
• Wear shoes to prevent foot injuries
• Avoid contact sports
• Tell dentists and doctors before any procedures

LIFESTYLE RECOMMENDATIONS:
• Stay active - walking is encouraged
• Elevate your leg when sitting
• Wear compression stockings if recommended
• Stay hydrated
• Avoid sitting or standing for long periods
• During long travel: walk around, do calf exercises, stay hydrated

WHEN TO SEEK MEDICAL ATTENTION:
Call your doctor if:
• Leg swelling, pain, or redness gets worse
• You notice unusual bleeding or bruising
• Blood in urine, stool, or vomit
• Heavy menstrual bleeding
• Severe headache
• You fall or hit your head

GO TO THE EMERGENCY ROOM IMMEDIATELY if:
• Sudden difficulty breathing
• Chest pain
• Coughing up blood
• Fainting or severe dizziness
• Signs of stroke (face drooping, arm weakness, speech problems)

PREVENTING FUTURE CLOTS:
• Take all medications as prescribed
• Stay active - regular exercise helps
• Maintain a healthy weight
• Quit smoking
• Stay hydrated
• Move around during long trips
• Talk to your doctor before taking hormones

REMEMBER:
• DVT is treatable with proper medication
• Taking your blood thinner correctly is the most important thing you can do
• Watch for warning signs of PE - it is a medical emergency
• Keep all follow-up appointments
• Ask questions - your healthcare team is there to help`,

    references: [
      'Kearon C, et al. Antithrombotic therapy for VTE disease: CHEST guideline and expert panel report. Chest. 2021;160:e545-e608. - Primary treatment guidelines',
      'NICE. Venous thromboembolic diseases: diagnosis, management and thrombophilia testing. NICE guideline NG158. 2020. - UK guidelines',
      'Wells PS, et al. Evaluation of D-dimer in the diagnosis of suspected deep-vein thrombosis. N Engl J Med. 2003;349:1227-1235. - Wells score derivation',
      'Schulman S, et al. Dabigatran versus warfarin in the treatment of acute venous thromboembolism (RE-COVER). N Engl J Med. 2009;361:2342-2352. - DOAC evidence',
      'EINSTEIN Investigators. Oral rivaroxaban for symptomatic venous thromboembolism. N Engl J Med. 2010;363:2499-2510. - Rivaroxaban evidence',
      'Vedantham S, et al. Pharmacomechanical catheter-directed thrombolysis for deep-vein thrombosis (ATTRACT). N Engl J Med. 2017;377:2240-2252. - Thrombolysis trial',
      'Kahn SR, et al. Compression stockings to prevent post-thrombotic syndrome: a randomised placebo-controlled trial (SOX). Lancet. 2014;383:880-888. - PTS prevention',
      'Danwang C, et al. Epidemiology of venous thromboembolism in Africa: a systematic review. J Thromb Haemost. 2017;15:1770-1781. - African epidemiology',
      'Louw S, et al. Venous thromboembolism in HIV-infected patients receiving antiretroviral therapy: a systematic review. Clin Infect Dis. 2020;70:1642-1652. - HIV and VTE',
      'Stevens SM, et al. Antithrombotic therapy for VTE disease: Second update of the CHEST guideline. Chest. 2021;160:e545-e608. - Updated CHEST guidelines',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 33. RHEUMATIC HEART DISEASE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '33',
    name: 'Rheumatic Heart Disease',
    slug: 'rheumatic-heart-disease',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I09.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Rheumatic heart disease (RHD) is chronic cardiac damage caused by acute rheumatic fever (ARF), an autoimmune inflammatory response to Group A Streptococcus (GAS) pharyngitis. RHD primarily affects the heart valves, most commonly the mitral valve, leading to stenosis, regurgitation, or mixed lesions. It remains the leading cause of acquired heart disease in children and young adults in developing countries.

RHD is largely a disease of poverty and is virtually eliminated in developed countries but remains a major public health burden in Africa, affecting an estimated 5-7 per 1,000 school-aged children in endemic regions. Sub-Saharan Africa bears the highest global burden, with RHD accounting for 50-70% of cardiac surgery in many African centers. The disease is entirely preventable through primary and secondary prophylaxis of streptococcal pharyngitis.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• RHD is PREVENTABLE - Primary prevention (treating GAS pharyngitis) and secondary prophylaxis (penicillin) are key
• Mitral valve most commonly affected (75%); aortic valve second (25%); tricuspid rare
• Secondary prophylaxis with benzathine penicillin G every 3-4 weeks is LIFELONG for severe RHD
• Echocardiography is essential for diagnosis and severity assessment
• Atrial fibrillation is common complication - requires anticoagulation (warfarin, target INR 2-3)
• Infective endocarditis prophylaxis required for high-risk dental/surgical procedures
• Pregnancy in RHD is HIGH RISK - requires specialist management; mitral stenosis especially dangerous
• Balloon mitral valvuloplasty is first-line for symptomatic mitral stenosis with favorable anatomy
• Surgical valve repair/replacement for severe regurgitation or stenosis not amenable to percutaneous intervention
• Heart failure management: Diuretics, ACE inhibitors, beta-blockers (cautious in mitral stenosis)`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Global prevalence: 33-40 million affected worldwide
• Annual deaths: 300,000-500,000 globally
• 95% of burden in low and middle-income countries
• Peak age: 5-15 years for ARF; 25-45 years for RHD complications
• Female predominance: 2:1 for RHD (unclear etiology)
• Virtually eliminated in developed countries (<0.5 per 1,000)

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa has highest global burden
• Prevalence: 5-7 per 1,000 school-aged children (echocardiographic screening)
• Clinical prevalence underestimates true burden by 5-10 fold
• RHD accounts for 50-70% of cardiac surgery cases in African centers
• Mean age at cardiac surgery: 20-30 years
• Maternal mortality: RHD is leading cardiac cause of maternal death in Africa
• Case fatality: 2-4% per year; worse with limited surgical access

REGIONAL VARIATIONS:
• East Africa: High burden; Uganda, Kenya, Tanzania have active screening programs
• West Africa: Very high burden; Nigeria has millions affected
• Southern Africa: High rates despite middle-income status; South Africa has surgical programs
• North Africa: Lower rates but still significant; Egypt, Morocco endemic

RISK FACTORS FOR RHD:
• Poverty and overcrowding (GAS transmission)
• Poor access to healthcare (delayed treatment of pharyngitis)
• Limited antibiotic availability
• Lack of secondary prophylaxis programs
• Recurrent ARF episodes
• Genetic susceptibility (HLA associations)
• Malnutrition (impaired immunity)

ARF TO RHD PROGRESSION:
• First episode ARF: 30-50% develop carditis
• Without secondary prophylaxis: 50-70% recurrence of ARF
• Each recurrence increases RHD risk
• Established RHD: Progressive valve damage over years
• With secondary prophylaxis: <5% recurrence rate`,

    clinicalPresentation: `ACUTE RHEUMATIC FEVER (ARF):

Jones Criteria (2015 Revision) - Diagnosis requires:
• Evidence of preceding GAS infection PLUS
• 2 major criteria OR 1 major + 2 minor criteria

Major Criteria (High-risk populations like Africa):
• Carditis (clinical or subclinical/echo-detected)
• Polyarthritis OR monoarthritis OR polyarthralgia
• Chorea (Sydenham chorea)
• Erythema marginatum
• Subcutaneous nodules

Minor Criteria:
• Fever (≥38°C)
• Polyarthralgia (if arthritis not used as major)
• Elevated acute phase reactants (ESR ≥30, CRP ≥30)
• Prolonged PR interval (if carditis not major criterion)

CHRONIC RHEUMATIC HEART DISEASE:

Symptoms by Valve Lesion:

MITRAL STENOSIS (Most Common):
• Dyspnea on exertion (earliest symptom)
• Orthopnea, PND
• Fatigue
• Palpitations (atrial fibrillation)
• Hemoptysis (pulmonary venous hypertension)
• Hoarseness (Ortner syndrome - recurrent laryngeal nerve compression)
• Right heart failure symptoms (late): Edema, ascites, hepatomegaly

MITRAL REGURGITATION:
• May be asymptomatic for years
• Dyspnea on exertion
• Fatigue
• Palpitations
• Heart failure symptoms (late)

AORTIC REGURGITATION:
• Asymptomatic for years (wide pulse pressure may be noted)
• Dyspnea, orthopnea
• Angina (decreased coronary perfusion)
• Heart failure symptoms
• Palpitations (forceful heartbeat)

AORTIC STENOSIS (Rare in RHD):
• Exertional dyspnea
• Angina
• Syncope
• Heart failure (late)

PHYSICAL EXAMINATION FINDINGS:

General:
• Malar flush (mitral facies - in mitral stenosis)
• Cachexia (advanced disease)
• Elevated JVP (right heart failure)

Pulse:
• Atrial fibrillation: Irregularly irregular
• Mitral stenosis: Low-volume pulse
• Aortic regurgitation: Collapsing pulse, wide pulse pressure

Auscultation:

MITRAL STENOSIS:
• Loud S1 (if valve pliable)
• Opening snap (follows S2)
• Low-pitched diastolic rumble at apex (best heard with bell in left lateral position)
• Duration of murmur correlates with severity
• Presystolic accentuation (if in sinus rhythm)

MITRAL REGURGITATION:
• Soft S1
• Pansystolic murmur at apex, radiating to axilla
• S3 gallop (volume overload)
• Laterally displaced apex beat

AORTIC REGURGITATION:
• Early diastolic decrescendo murmur at left sternal border (best heard sitting forward, end-expiration)
• Austin Flint murmur (functional mitral stenosis)
• Wide pulse pressure, Corrigan pulse, de Musset sign, Quincke sign

MIXED/MULTIPLE VALVE DISEASE:
• Combination of findings
• Dominant lesion determines presentation

SIGNS OF COMPLICATIONS:
• Atrial fibrillation: Irregular pulse, variable S1
• Heart failure: Elevated JVP, pulmonary crackles, hepatomegaly, edema
• Pulmonary hypertension: Loud P2, RV heave, TR murmur
• Infective endocarditis: Fever, new/changing murmur, embolic phenomena`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Clinical Suspicion (Murmur, Heart Failure in Young Patient)
Step 2: Echocardiography (Gold Standard)
Step 3: Assess Severity and Complications
Step 4: Evaluate for Active ARF vs Chronic RHD
Step 5: Plan Management Strategy

ECHOCARDIOGRAPHIC CRITERIA FOR RHD (2012 World Heart Federation):

DEFINITE RHD:

Category A - Pathological MR and at least 2 morphological features:
• Anterior mitral valve leaflet (AMVL) thickening ≥3mm
• Chordal thickening
• Restricted leaflet motion
• Excessive leaflet tip motion

Category B - MS mean gradient ≥4 mmHg

Category C - Pathological AR and at least 2 morphological features:
• Irregular or focal thickening
• Coaptation defect
• Restricted leaflet motion
• Prolapse

Category D - Borderline disease of both aortic and mitral valves

BORDERLINE RHD:
• At least 2 morphological features of MV without pathological MR/MS
• Pathological MR without morphological features
• Pathological AR without morphological features

SEVERITY ASSESSMENT:

MITRAL STENOSIS:
| Severity | MVA (cm²) | Mean Gradient (mmHg) | PASP (mmHg) |
|----------|-----------|---------------------|-------------|
| Mild | >1.5 | <5 | <30 |
| Moderate | 1.0-1.5 | 5-10 | 30-50 |
| Severe | <1.0 | >10 | >50 |

MITRAL REGURGITATION:
| Parameter | Mild | Moderate | Severe |
|-----------|------|----------|--------|
| Regurgitant volume | <30 mL | 30-59 mL | ≥60 mL |
| Regurgitant fraction | <30% | 30-49% | ≥50% |
| EROA | <0.20 cm² | 0.20-0.39 cm² | ≥0.40 cm² |
| Vena contracta | <3 mm | 3-6.9 mm | ≥7 mm |

AORTIC REGURGITATION:
| Parameter | Mild | Moderate | Severe |
|-----------|------|----------|--------|
| Vena contracta | <3 mm | 3-6 mm | >6 mm |
| Regurgitant volume | <30 mL | 30-59 mL | ≥60 mL |
| PHT | >500 ms | 200-500 ms | <200 ms |

LABORATORY INVESTIGATIONS:

Evidence of Recent GAS Infection (for ARF):
• Elevated or rising ASO titer (>200 IU/mL adults, >400 IU/mL children)
• Elevated anti-DNase B
• Positive throat culture for GAS
• Positive rapid antigen detection test

Acute Phase Reactants:
• ESR: Elevated in ARF (often >50 mm/hr)
• CRP: Elevated (often >30 mg/L)
• WBC: May be elevated

Cardiac Biomarkers:
• BNP/NT-proBNP: Elevated in heart failure
• Troponin: May be elevated in active carditis

Other Tests:
• ECG: Prolonged PR interval, atrial fibrillation, LVH, RVH
• Chest X-ray: Cardiomegaly, LA enlargement, pulmonary congestion
• Cardiac catheterization: Pre-operative assessment, coronary evaluation

SCREENING STRATEGIES:

School-Based Echocardiographic Screening:
• Target: Endemic regions, high-risk populations
• Method: Portable echocardiography
• Yield: 5-10x higher detection than clinical screening alone
• Identifies subclinical RHD for secondary prophylaxis

Clinical Screening:
• Auscultation in school-based programs
• Lower sensitivity than echo but more feasible
• Refer murmurs for echocardiography`,

    differentialDiagnosis: [
      'Infective endocarditis - Fever, new/changing murmur, embolic phenomena; can complicate RHD; blood cultures, echo for vegetations',
      'Degenerative valve disease - Older patients, calcific changes; mitral annular calcification; aortic sclerosis/stenosis',
      'Congenital heart disease - Present from birth; bicuspid aortic valve, VSD, ASD; different echo features',
      'Mitral valve prolapse - Young women, mid-systolic click, late systolic MR; myxomatous changes on echo',
      'Dilated cardiomyopathy - Dilated LV with functional MR; no primary valve pathology; low EF',
      'Hypertrophic cardiomyopathy - LVH, dynamic outflow obstruction; SAM of mitral valve; family history',
      'Ischemic mitral regurgitation - Post-MI, papillary muscle dysfunction; wall motion abnormalities; CAD risk factors',
      'Carcinoid heart disease - Right-sided valve involvement; carcinoid syndrome; elevated 5-HIAA',
      'Systemic lupus erythematosus (Libman-Sacks) - Verrucous vegetations; SLE features; antiphospholipid antibodies',
      'Marfan syndrome - Aortic root dilation, AR; MVP; skeletal features; fibrillin-1 mutation',
      'Endomyocardial fibrosis - Restrictive cardiomyopathy; AV valve involvement; tropical regions; eosinophilia',
      'Drug-induced valvulopathy - Ergot derivatives, anorectic agents; restrictive valve lesions',
    ],

    investigations: [
      {
        name: 'Transthoracic Echocardiogram (TTE)',
        indication: '🔴 IMMEDIATE - Gold standard for RHD diagnosis; assess valve morphology, severity, LV/RV function, pulmonary pressures',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG (12-Lead)',
        indication: '🔴 IMMEDIATE - Assess rhythm (AF common), chamber enlargement, PR prolongation (ARF)',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 URGENT - Cardiac size, LA enlargement (double density, splayed carina), pulmonary congestion',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Anemia (chronic disease), leukocytosis (ARF, infection)',
        availability: 'BASIC'
      },
      {
        name: 'ESR and CRP',
        indication: '🟠 URGENT - Elevated in acute rheumatic fever; monitor disease activity',
        availability: 'BASIC'
      },
      {
        name: 'Anti-Streptolysin O (ASO) Titer',
        indication: '🟠 URGENT - Evidence of recent GAS infection; elevated in ARF',
        availability: 'BASIC'
      },
      {
        name: 'Anti-DNase B',
        indication: '🟡 Routine - Additional evidence of GAS infection; may be positive when ASO negative',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Throat Culture / Rapid Strep Test',
        indication: '🟠 URGENT - If pharyngitis present; positive in only 25% at time of ARF',
        availability: 'BASIC'
      },
      {
        name: 'BNP / NT-proBNP',
        indication: '🟠 URGENT - Heart failure assessment; prognostic value',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Renal Function (Creatinine, eGFR)',
        indication: '🟠 URGENT - Baseline; cardiorenal syndrome; drug dosing',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟡 Routine - Hepatic congestion in right heart failure',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR)',
        indication: '🟠 URGENT - If on warfarin or pre-procedure; target INR 2-3 for AF',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures (3 sets)',
        indication: '🔴 IMMEDIATE - If fever or suspected infective endocarditis; before antibiotics',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Transesophageal Echocardiogram (TEE)',
        indication: '🟠 URGENT - Suspected IE, pre-operative assessment, LA appendage thrombus before cardioversion',
        availability: 'ADVANCED'
      },
      {
        name: 'Cardiac Catheterization',
        indication: '🟡 Routine - Pre-operative; assess coronary arteries in patients >40 or with risk factors; hemodynamic assessment',
        availability: 'ADVANCED'
      },
      {
        name: 'Cardiac MRI',
        indication: '🟡 Routine - Quantify regurgitation, assess myocardial fibrosis, complex cases',
        availability: 'ADVANCED'
      },
      {
        name: 'HIV Test',
        indication: '🟡 Routine - In African settings; affects surgical planning and outcomes',
        availability: 'BASIC'
      },
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS - Echocardiography for valve assessment and severity grading',
        '⚡ Assess for active ARF vs chronic RHD - Check inflammatory markers, Jones criteria',
        '⚡ Evaluate functional status - NYHA class, exercise tolerance',
        '⚡ Screen for complications - Atrial fibrillation (ECG), heart failure, pulmonary hypertension',
        '⚡ Start SECONDARY PROPHYLAXIS immediately if not already on it - Benzathine penicillin G',
        '⚡ Heart failure management if present - Diuretics, fluid restriction, oxygen',
        '⚡ Rate/rhythm control for atrial fibrillation - Beta-blockers, digoxin, anticoagulation',
        '⚡ Anticoagulation if AF present - Warfarin with INR target 2-3',
        '⚡ Infective endocarditis prophylaxis education',
        '⚡ Pregnancy counseling for women of childbearing age - RHD pregnancy is HIGH RISK',
        '⚡ Surgical/interventional evaluation if symptomatic severe disease',
        '⚡ Nutritional assessment - Address malnutrition',
      ],
      pharmacological: [
        {
          drug: 'Benzathine Penicillin G (BPG)',
          indication: '🔴 Secondary Prophylaxis - Cornerstone of RHD prevention; prevents recurrent ARF and disease progression',
          dose: 'Adults and children >27 kg: 1.2 million units IM every 3-4 weeks. Children ≤27 kg: 600,000 units IM every 3-4 weeks',
          route: 'IM (deep gluteal injection)',
          duration: 'Lifelong for severe RHD. With carditis: Minimum 10 years or until age 40 (whichever longer). Without carditis: 5 years or until age 21',
          contraindications: ['Penicillin allergy (true anaphylaxis)', 'Previous severe reaction to BPG'],
          sideEffects: ['Injection site pain (most common)', 'Allergic reactions (rare)', 'Anaphylaxis (very rare - 1:100,000)', 'Procaine reaction'],
          alternatives: ['Penicillin V 250mg PO twice daily (less effective)', 'Erythromycin 250mg PO twice daily if penicillin allergic']
        },
        {
          drug: 'Penicillin V',
          indication: '🟠 Alternative Secondary Prophylaxis - Oral option when IM not feasible; less effective than BPG',
          dose: 'Adults and children: 250 mg PO twice daily',
          route: 'PO',
          duration: 'Same duration as BPG; STRICT adherence essential',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['GI upset', 'Diarrhea', 'Allergic reactions'],
          alternatives: ['Benzathine penicillin G IM (preferred)', 'Erythromycin if penicillin allergic']
        },
        {
          drug: 'Furosemide',
          indication: '🔴 Heart Failure Management - Loop diuretic for congestion; essential in symptomatic RHD',
          dose: 'Adults: 20-80 mg PO/IV daily, titrate to response. Children: 1-2 mg/kg/day. Max: 600 mg/day',
          route: 'PO or IV',
          duration: 'Long-term; adjust based on volume status',
          contraindications: ['Anuria', 'Severe hypovolemia', 'Severe electrolyte depletion'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Hypotension', 'Ototoxicity (high doses)', 'Hyperuricemia'],
          alternatives: ['Bumetanide', 'Torsemide', 'Hydrochlorothiazide (less potent)']
        },
        {
          drug: 'Spironolactone',
          indication: '🟠 Heart Failure Management - Potassium-sparing diuretic; mortality benefit in heart failure',
          dose: 'Adults: 12.5-25 mg PO daily, may increase to 50 mg daily. Children: 1-3 mg/kg/day divided twice daily',
          route: 'PO',
          duration: 'Long-term for heart failure',
          contraindications: ['Hyperkalemia (K >5.5)', 'Severe renal impairment', 'Addison disease'],
          sideEffects: ['Hyperkalemia', 'Gynecomastia', 'Breast tenderness', 'Menstrual irregularities'],
          alternatives: ['Eplerenone (fewer hormonal side effects)']
        },
        {
          drug: 'Digoxin',
          indication: '🟠 Rate Control / Heart Failure - Slows ventricular rate in AF; inotropic support in heart failure',
          dose: 'Adults: 0.125-0.25 mg PO daily (lower in elderly, renal impairment). Children: 8-12 mcg/kg/day loading, 3-5 mcg/kg/day maintenance',
          route: 'PO or IV',
          duration: 'Long-term; monitor levels periodically (target 0.5-1.0 ng/mL)',
          contraindications: ['WPW syndrome', 'Ventricular arrhythmias', 'AV block without pacemaker', 'Severe hypokalemia'],
          sideEffects: ['Digoxin toxicity (nausea, visual changes, arrhythmias)', 'Bradycardia', 'AV block'],
          alternatives: ['Beta-blockers', 'Non-DHP calcium channel blockers']
        },
        {
          drug: 'Metoprolol',
          indication: '🟠 Rate Control - Beta-blocker for AF rate control; use cautiously in mitral stenosis (prolonged diastole beneficial)',
          dose: 'Adults: 25-100 mg PO twice daily. Use extended release: 25-200 mg daily',
          route: 'PO',
          duration: 'Long-term for rate control',
          contraindications: ['Severe bradycardia', 'Decompensated heart failure', 'Severe asthma', 'Cardiogenic shock'],
          sideEffects: ['Bradycardia', 'Fatigue', 'Hypotension', 'Bronchospasm', 'Cold extremities'],
          alternatives: ['Bisoprolol', 'Carvedilol', 'Diltiazem']
        },
        {
          drug: 'Warfarin',
          indication: '🔴 Anticoagulation - Required for AF in RHD; mechanical valve; LA thrombus; high stroke risk',
          dose: 'Initial: 2-5 mg PO daily. Adjust to target INR 2.0-3.0 (2.5-3.5 for mechanical valves)',
          route: 'PO',
          duration: 'Lifelong for permanent AF, mechanical valve; reassess if sinus rhythm restored',
          contraindications: ['Active bleeding', 'Pregnancy (teratogenic)', 'Severe liver disease', 'Recent surgery', 'Uncontrolled hypertension'],
          sideEffects: ['Bleeding (major 1-3%/year)', 'Skin necrosis', 'Purple toe syndrome', 'Multiple drug/food interactions'],
          alternatives: ['DOACs NOT recommended for valvular AF (mitral stenosis) or mechanical valves']
        },
        {
          drug: 'Enalapril',
          indication: '🟠 Heart Failure / MR - ACE inhibitor; afterload reduction in MR; neurohormonal blockade in HF',
          dose: 'Adults: Start 2.5-5 mg PO twice daily, titrate to 10-20 mg twice daily. Children: 0.1-0.5 mg/kg/day',
          route: 'PO',
          duration: 'Long-term for heart failure or significant MR',
          contraindications: ['Pregnancy', 'Bilateral renal artery stenosis', 'Angioedema history', 'Hyperkalemia'],
          sideEffects: ['Cough (10-15%)', 'Hypotension', 'Hyperkalemia', 'Angioedema (rare)', 'Renal impairment'],
          alternatives: ['Lisinopril', 'Ramipril', 'ARBs (losartan, valsartan) if ACEi intolerant']
        },
        {
          drug: 'Amoxicillin',
          indication: '🔴 Primary Prevention - Treatment of GAS pharyngitis to prevent initial ARF episode',
          dose: 'Adults: 500 mg PO three times daily or 1g twice daily for 10 days. Children: 50 mg/kg/day (max 1g) divided twice daily for 10 days',
          route: 'PO',
          duration: '10 days (full course essential to eradicate GAS)',
          contraindications: ['Penicillin allergy', 'Infectious mononucleosis'],
          sideEffects: ['GI upset', 'Diarrhea', 'Rash', 'Allergic reactions'],
          alternatives: ['Penicillin V 250-500mg 2-3 times daily', 'Azithromycin if penicillin allergic']
        },
        {
          drug: 'Aspirin (ARF Treatment)',
          indication: '🔴 Acute Rheumatic Fever - Anti-inflammatory for arthritis and fever in ARF; NOT for carditis',
          dose: 'Adults: 4-8 g/day in divided doses. Children: 80-100 mg/kg/day in 4 divided doses (max 3g/day). Taper over 2-4 weeks',
          route: 'PO',
          duration: '2-6 weeks depending on response; taper to avoid rebound',
          contraindications: ['Active peptic ulcer', 'Bleeding disorders', 'Aspirin allergy', 'Children with viral illness (Reye syndrome risk)'],
          sideEffects: ['GI bleeding', 'Tinnitus', 'Salicylism (high doses)', 'Hepatotoxicity'],
          alternatives: ['Naproxen 10-20 mg/kg/day', 'Prednisone for severe carditis']
        },
        {
          drug: 'Prednisone (ARF with Severe Carditis)',
          indication: '🟠 Acute Rheumatic Fever - For moderate-severe carditis in ARF; may reduce inflammation but no proven prevention of RHD',
          dose: 'Adults: 1-2 mg/kg/day (max 80 mg/day). Children: 1-2 mg/kg/day (max 60 mg/day). Taper over 2-4 weeks',
          route: 'PO',
          duration: '2-4 weeks with gradual taper; overlap with aspirin during taper',
          contraindications: ['Active infection (untreated)', 'Psychosis', 'Uncontrolled diabetes'],
          sideEffects: ['Hyperglycemia', 'Hypertension', 'Weight gain', 'Mood changes', 'Immunosuppression', 'Osteoporosis'],
          alternatives: ['High-dose aspirin may be sufficient for mild-moderate carditis']
        },
        {
          drug: 'Amoxicillin (IE Prophylaxis)',
          indication: '🟡 Infective Endocarditis Prophylaxis - Single dose before high-risk dental procedures',
          dose: 'Adults: 2g PO 30-60 minutes before procedure. Children: 50 mg/kg PO (max 2g)',
          route: 'PO',
          duration: 'Single dose pre-procedure',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['GI upset', 'Allergic reactions (rare with single dose)'],
          alternatives: ['Clindamycin 600mg PO (adults) or 20 mg/kg (children) if penicillin allergic', 'Azithromycin 500mg']
        },
      ],
      nonPharmacological: [
        'Secondary Prophylaxis Adherence Program: Register-based tracking, reminder systems, community health worker support; critical for prevention of RHD progression',
        'Dietary Modification: Sodium restriction (<2g/day) for heart failure; adequate nutrition for growth in children',
        'Fluid Restriction: 1.5-2L/day if heart failure present',
        'Physical Activity: Individualized based on severity; avoid competitive sports in moderate-severe disease',
        'Smoking/Alcohol Cessation: Essential for cardiovascular health',
        'Weight Management: Avoid obesity; optimize nutrition in underweight',
        'Dental Hygiene: Prevent oral infections; regular dental care; IE prophylaxis awareness',
        'Pregnancy Planning: Pre-conception counseling essential; optimize cardiac status before conception; contraception advice',
        'Patient Education: Disease understanding, medication adherence, symptom recognition, when to seek care',
        'Family Screening: Echocardiographic screening of siblings and household contacts in endemic settings',
      ],
      surgical: [
        'Percutaneous Balloon Mitral Valvuloplasty (PBMV): First-line for symptomatic mitral stenosis with favorable anatomy (Wilkins score ≤8); low complication rate; success >90%; repeatable; pregnancy-safe',
        'Mitral Valve Repair: Preferred over replacement when feasible; preserves native valve; lower thromboembolism; no anticoagulation needed; better outcomes especially in young patients',
        'Mitral Valve Replacement: When repair not feasible; choice of mechanical (lifelong anticoagulation) vs bioprosthetic (limited durability); decision based on age, anticoagulation access, patient preference',
        'Aortic Valve Replacement: For severe symptomatic aortic stenosis or regurgitation; mechanical vs bioprosthetic valve selection',
        'Double Valve Replacement: Mitral and aortic valve surgery; higher risk; needed in combined severe disease',
        'Tricuspid Valve Repair/Annuloplasty: Usually performed concomitantly with left-sided valve surgery; repair preferred',
        'Maze Procedure: Surgical ablation for atrial fibrillation during valve surgery; may restore sinus rhythm',
        'Left Atrial Appendage Closure: During cardiac surgery to reduce stroke risk',
        'Closed Mitral Commissurotomy: Historical procedure; still performed where PBMV not available; reasonable outcomes',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Acute pulmonary edema / cardiogenic shock → ICU, urgent cardiology consultation',
        '🔴 EMERGENCY - Suspected infective endocarditis (fever, new murmur, embolic events) → Blood cultures, TEE, infectious disease consultation',
        '🔴 EMERGENCY - New stroke/TIA in RHD patient → Neurology, imaging, anticoagulation review',
        '🔴 EMERGENCY - Hemodynamic deterioration in pregnant RHD patient → Multidisciplinary team (cardiology, MFM, anesthesia)',
        '🟠 URGENT - Symptomatic severe valve disease (NYHA III-IV) → Cardiothoracic surgery evaluation',
        '🟠 URGENT - New-onset atrial fibrillation → Rate/rhythm control, anticoagulation initiation',
        '🟠 URGENT - Declining functional status or worsening symptoms → Reassess severity, intervention timing',
        '🟡 ROUTINE - Asymptomatic severe valve disease → Regular monitoring, discuss intervention timing',
        '🟡 ROUTINE - Moderate RHD for baseline evaluation and secondary prophylaxis optimization',
        '🟡 ROUTINE - Pre-pregnancy counseling in women with RHD → High-risk obstetrics',
        '🟡 ROUTINE - Consideration for PBMV in symptomatic mitral stenosis → Interventional cardiology',
      ],
      monitoring: [
        'Secondary Prophylaxis Adherence: Track BPG injections; address barriers to adherence; every 3-4 weeks',
        'Clinical Assessment: Symptoms (NYHA class), physical examination; every 3-6 months for stable RHD',
        'Echocardiography: Annual for mild-moderate RHD; every 6-12 months for severe RHD; earlier if symptoms change',
        'ECG: Assess rhythm; detect new AF; at each visit',
        'INR Monitoring (if on warfarin): Weekly initially, then every 2-4 weeks when stable; target 2.0-3.0',
        'Renal and Hepatic Function: Baseline and periodically; more frequent if heart failure or on multiple medications',
        'Inflammatory Markers (ESR, CRP): If suspected ARF recurrence or active disease',
        'BNP/NT-proBNP: Heart failure monitoring; prognostic value',
        'Nutritional Status: Especially in children; growth monitoring',
        'Dental Health: Regular dental check-ups; prevent oral infections',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

MILD RHD (Mild valve lesions, asymptomatic):
• Clinical review: Every 6-12 months
• Echocardiography: Annually
• Secondary prophylaxis: Every 3-4 weeks (BPG) or daily (oral)
• Focus: Adherence, symptom development, lifestyle

MODERATE RHD (Moderate valve lesions or mild symptoms):
• Clinical review: Every 3-6 months
• Echocardiography: Every 6-12 months
• Additional monitoring: ECG at each visit, BNP if available
• Focus: Symptom progression, AF detection, intervention timing

SEVERE RHD (Severe valve lesions or significant symptoms):
• Clinical review: Every 1-3 months
• Echocardiography: Every 6 months or if clinical change
• Consider intervention: Surgical or percutaneous evaluation
• Focus: Heart failure management, surgical timing, complications

POST-INTERVENTION (PBMV, Surgery):
• Early follow-up: 4-6 weeks post-procedure
• Echocardiography: 6 weeks, then 6-12 months
• Long-term: Annual or more frequent based on result
• Continue secondary prophylaxis
• Anticoagulation management (mechanical valves)

PREGNANCY IN RHD:
• Pre-conception: Optimize cardiac status, risk counseling
• During pregnancy: Monthly cardiology visits, more frequent in third trimester
• Delivery: Multidisciplinary planning
• Postpartum: Close monitoring for 6 weeks (highest risk period)

SECONDARY PROPHYLAXIS DURATION:

| Category | Duration |
|----------|----------|
| RHD with severe valvular disease | Lifelong |
| RHD with mild-moderate valve disease | 10 years or until age 40 (whichever longer) |
| ARF with carditis but no RHD | 10 years or until age 25 (whichever longer) |
| ARF without carditis | 5 years or until age 21 (whichever longer) |
| Recurrent ARF | Lifelong or as per highest category |

KEY MONITORING POINTS:
• Maintain high suspicion for IE (fever, new symptoms)
• Watch for AF development (palpitations, stroke symptoms)
• Assess functional status at each visit
• Address medication adherence consistently
• Screen family members in endemic settings
• Pre-pregnancy planning for all women of childbearing age`
    },

    redFlags: [
      '🔴 CRITICAL: Acute pulmonary edema - Severe dyspnea, orthopnea, pink frothy sputum → Emergency diuretics, oxygen, consider urgent intervention',
      '🔴 CRITICAL: Cardiogenic shock - Hypotension, poor perfusion, altered mental status → ICU, inotropes, urgent surgical evaluation',
      '🔴 CRITICAL: Suspected infective endocarditis - Fever + new/changing murmur + embolic phenomena → Blood cultures, TEE, urgent cardiology/ID consultation',
      '🔴 CRITICAL: Stroke/systemic embolism - Sudden neurological deficit or limb ischemia → Emergent stroke management, anticoagulation review',
      '🔴 CRITICAL: Hemodynamic instability in pregnancy - RHD + pregnancy + decompensation → Multidisciplinary emergency management',
      '🟠 URGENT: New-onset atrial fibrillation - Palpitations, irregular pulse → Anticoagulation, rate control, assess for cardioversion',
      '🟠 URGENT: Rapidly progressive symptoms - NYHA class worsening → Reassess severity, consider intervention timing',
      '🟠 URGENT: Severe mitral stenosis in pregnancy - Very high risk → Close monitoring, consider early delivery or intervention',
      '🟠 URGENT: Breakthrough ARF on secondary prophylaxis - Despite BPG → Assess adherence, consider shorter prophylaxis interval',
      '🟠 URGENT: Hemoptysis in mitral stenosis - Pulmonary venous hypertension, bronchial vein rupture → Urgent intervention evaluation',
      '🟡 WARNING: Non-adherence to secondary prophylaxis - Missed BPG doses → Address barriers, intensify support',
    ],

    complications: [
      'Heart Failure: Most common cause of death in RHD; both systolic and diastolic dysfunction; manage with diuretics, ACEi, beta-blockers',
      'Atrial Fibrillation: Occurs in 30-40% of RHD; increases stroke risk 5-fold; requires anticoagulation with warfarin',
      'Thromboembolism/Stroke: 5-10% per year without anticoagulation in AF; LA thrombus especially with MS; devastating in young patients',
      'Infective Endocarditis: 1-2% per year; damaged valves susceptible; high mortality; IE prophylaxis for high-risk procedures',
      'Pulmonary Hypertension: Develops with chronic LA pressure elevation; may become fixed; affects surgical outcomes',
      'Recurrent Acute Rheumatic Fever: Without secondary prophylaxis; each episode worsens valve damage',
      'Pregnancy Complications: Maternal mortality up to 1-5% in severe MS; fetal complications; requires specialized management',
      'Arrhythmias: AF most common; atrial flutter; ventricular arrhythmias in advanced disease',
      'Hemoptysis: From bronchial vein rupture in severe pulmonary venous hypertension; may be massive',
      'Hoarseness (Ortner Syndrome): Left recurrent laryngeal nerve compression by enlarged LA or pulmonary artery',
      'Post-Rheumatic Glomerulonephritis: Rare concurrent finding; immune complex deposition',
      'Valve Thrombosis: Mechanical prosthetic valve complication; requires anticoagulation optimization or intervention',
    ],

    prognosis: `OVERALL PROGNOSIS:

NATURAL HISTORY:
• Without intervention: 10-year survival 50-60% for symptomatic severe RHD
• With appropriate management: 10-year survival >80%
• Major determinants: Severity at diagnosis, access to surgery, adherence to secondary prophylaxis

MORTALITY:
• Global RHD mortality: 300,000-500,000 deaths per year
• Case fatality rate: 2-4% per year in endemic countries
• Heart failure: Leading cause of death
• Stroke: Second most common cause of death

PROGNOSIS BY LESION:

Mitral Stenosis:
• Asymptomatic: 10-year survival 80%
• NYHA II: 10-year survival 60%
• NYHA III-IV: 10-year survival <15% without intervention
• Post-PBMV: 10-year survival 80-90% (favorable anatomy)
• Post-MVR: 5-year survival 70-80%

Mitral Regurgitation:
• Asymptomatic severe MR: 10-year survival 75%
• Symptomatic: 5-year survival <50% without surgery
• Post-repair: 10-year survival 85%
• Post-replacement: 10-year survival 70%

SECONDARY PROPHYLAXIS IMPACT:
• With adherence: ARF recurrence <5%
• Without adherence: ARF recurrence 50-70%
• Each recurrence worsens prognosis

FAVORABLE PROGNOSTIC FACTORS:
• Early diagnosis and treatment
• Adherence to secondary prophylaxis
• Access to surgical intervention
• Mild-moderate disease at presentation
• Sinus rhythm maintained
• Good LV function

POOR PROGNOSTIC FACTORS:
• Severe disease at presentation
• Atrial fibrillation
• Pulmonary hypertension (PASP >50 mmHg)
• Low ejection fraction
• Recurrent ARF
• Limited access to surgery
• Poor prophylaxis adherence
• Pregnancy with severe RHD

AFRICAN CONTEXT:
• Higher mortality due to late presentation
• Limited surgical capacity (long waiting lists)
• Challenges with anticoagulation monitoring
• Secondary prophylaxis adherence barriers
• Maternal mortality: RHD is leading cardiac cause
• Young age at death: Mean 30-40 years`,

    prevention: `PRIMARY PREVENTION (Preventing ARF):

Treatment of GAS Pharyngitis:
• Goal: Eradicate GAS from pharynx before immune response leads to ARF
• Timing: Treatment within 9 days of pharyngitis onset prevents ARF
• First-line: Benzathine penicillin G 1.2 million units IM single dose (most effective)
• Alternative: Amoxicillin 50 mg/kg/day (max 1g) divided BID for 10 days
• Penicillin allergic: Azithromycin 12 mg/kg/day (max 500mg) for 5 days

Challenges in Africa:
• Limited access to healthcare
• Antibiotic availability and cost
• Differentiating viral from bacterial pharyngitis
• Lack of rapid strep testing

Strategies:
• School-based sore throat treatment programs
• Training community health workers
• Improving antibiotic access
• WHO Essential Medicines: Benzathine penicillin G

SECONDARY PREVENTION (Preventing Recurrent ARF):

Benzathine Penicillin G Prophylaxis:
• Most effective method (IM every 3-4 weeks)
• >90% reduction in ARF recurrence
• Prevents progression of RHD
• Cost-effective intervention

Duration Guidelines:
• Severe RHD: Lifelong
• Moderate RHD: 10 years or until age 40
• Mild/resolved RHD: 10 years or until age 25
• No carditis: 5 years or until age 21

Register-Based Programs:
• Essential for tracking and adherence
• Reminder systems (SMS, community health workers)
• Quality improvement initiatives
• Integration with primary healthcare

PRIMORDIAL PREVENTION:

Addressing Social Determinants:
• Reduce overcrowding
• Improve housing conditions
• Enhance hygiene and sanitation
• Strengthen health systems
• Economic development

TERTIARY PREVENTION (Preventing Complications):

• Early intervention (PBMV, surgery) before irreversible damage
• Anticoagulation for AF
• IE prophylaxis
• Heart failure management
• Pregnancy risk counseling
• Regular monitoring

GLOBAL AND AFRICAN INITIATIVES:

• WHO Resolution on RF/RHD (2018)
• African Union RHD Addis Ababa Communique
• National RHD control programs
• RHD registers
• Screening programs (school-based echo)
• Increasing surgical capacity
• Global partnerships (RHD Action, World Heart Federation)`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING RHEUMATIC HEART DISEASE - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS RHEUMATIC HEART DISEASE (RHD)?
Rheumatic heart disease is damage to the heart valves caused by rheumatic fever. Rheumatic fever happens when a throat infection caused by certain bacteria (called "strep throat") is not treated properly. Your body's immune system attacks the heart valves by mistake, causing them to become scarred and damaged.

WHY IS RHD SERIOUS?
• It can cause your heart valves to leak or become narrowed
• This makes your heart work harder and can lead to heart failure
• Blood clots can form in the heart and travel to the brain (stroke)
• RHD is the most common cause of heart disease in young people in Africa
• It is PREVENTABLE with proper treatment of sore throats

THE MOST IMPORTANT THING: PENICILLIN INJECTIONS

If you have RHD, you need penicillin injections every 3-4 weeks:
• This prevents new attacks of rheumatic fever
• It stops your heart valves from getting worse
• Missing injections can cause serious damage to your heart

⚠️ NEVER MISS YOUR PENICILLIN INJECTION - It protects your heart!

HOW LONG DO I NEED INJECTIONS?
• Severe heart valve damage: FOR LIFE
• Moderate damage: At least 10 years or until age 40
• Mild or no damage: 5-10 years
• Your doctor will tell you how long you need them

WHAT ARE THE SYMPTOMS OF RHD?
• Shortness of breath (especially with activity or lying flat)
• Feeling tired easily
• Swelling in legs or feet
• Fast or irregular heartbeat
• Chest discomfort
• Coughing

WHEN TO SEEK MEDICAL CARE:
See your doctor if you have:
• Worsening shortness of breath
• Swelling in your legs that is getting worse
• Feeling your heart racing or skipping beats
• Chest pain
• Coughing up blood
• Feeling dizzy or fainting

GO TO THE HOSPITAL IMMEDIATELY if:
• Severe difficulty breathing
• Chest pain
• Sudden weakness on one side of your body (stroke symptoms)
• High fever with shaking chills

IMPORTANT INFORMATION FOR WOMEN:

Pregnancy with RHD is HIGH RISK:
• Talk to your doctor BEFORE getting pregnant
• Your heart has to work much harder during pregnancy
• Some women with severe RHD should not get pregnant
• If you are pregnant and have RHD, you need special care

PREVENTING INFECTION IN YOUR HEART:

Your damaged heart valves are at risk for infection (endocarditis):
• Tell your dentist and all doctors that you have RHD
• You need antibiotics before dental procedures
• Keep your teeth and gums healthy
• See a dentist regularly
• Don't ignore any fever - get medical attention

TAKING YOUR MEDICATIONS:

• Take ALL medications exactly as prescribed
• If you are on warfarin (blood thinner):
  - Have regular blood tests (INR)
  - Watch for signs of bleeding
  - Avoid injury
  - Tell doctors before any procedures
• If you are on water pills (diuretics):
  - Weigh yourself daily
  - Limit salt in your food
  - Take potassium supplements if prescribed

LIFESTYLE RECOMMENDATIONS:

• Limit salt: Helps reduce fluid buildup
• Stay active: As advised by your doctor
• Maintain a healthy weight
• Don't smoke
• Limit alcohol
• Get enough rest
• Take your medications faithfully

PROTECT YOUR FAMILY:

• RHD can run in families - not because it is inherited, but because family members are exposed to the same bacteria
• If anyone in your family has a sore throat, they should be treated quickly with antibiotics
• Your siblings and children should be checked by a doctor

REMEMBER:
• RHD is a serious but manageable condition
• NEVER miss your penicillin injection
• Keep all your clinic appointments
• Take your medications exactly as prescribed
• Know the warning signs that need urgent attention
• Plan any pregnancy with your doctor's guidance
• Tell all healthcare providers that you have RHD

Your healthcare team is here to help you live well with RHD. Ask questions and share any concerns!`,

    references: [
      'Remenyi B, et al. World Heart Federation criteria for echocardiographic diagnosis of rheumatic heart disease. Nat Rev Cardiol. 2012;9:297-309. - Diagnostic criteria',
      'Gewitz MH, et al. Revision of the Jones Criteria for the Diagnosis of Acute Rheumatic Fever. Circulation. 2015;131:1806-1818. - Updated Jones criteria',
      'Watkins DA, et al. Global, Regional, and National Burden of Rheumatic Heart Disease, 1990-2015. N Engl J Med. 2017;377:713-722. - Global burden',
      'Zühlke L, et al. Characteristics, complications, and gaps in evidence-based interventions in rheumatic heart disease: the Global Rheumatic Heart Disease Registry (REMEDY). Eur Heart J. 2015;36:1115-1122. - REMEDY registry',
      'Carapetis JR, et al. Acute rheumatic fever and rheumatic heart disease. Nat Rev Dis Primers. 2016;2:15084. - Comprehensive review',
      'Nishimura RA, et al. 2017 AHA/ACC Focused Update on Valvular Heart Disease Guidelines. Circulation. 2017;135:e1159-e1195. - Valve management',
      'Sliwa K, et al. Rheumatic heart disease in Africa: burden and management. Cardiovasc J Afr. 2020;31:164-171. - African context',
      'Beaton A, et al. Echocardiographic screening for rheumatic heart disease in Ugandan schoolchildren. Circulation. 2012;125:3127-3132. - Screening evidence',
      'Mayosi BM, et al. The challenge of silent rheumatic heart disease. Lancet Glob Health. 2014;2:e677-e678. - Subclinical disease',
      'WHO. Rheumatic Fever and Rheumatic Heart Disease: Report of a WHO Expert Consultation. Geneva: WHO; 2004. - WHO guidelines',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 34. PERICARDITIS AND CARDIAC TAMPONADE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '34',
    name: 'Pericarditis and Cardiac Tamponade',
    slug: 'pericarditis-cardiac-tamponade',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I30.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Pericarditis is inflammation of the pericardium, the fibrous sac surrounding the heart. It can be acute (<4-6 weeks), incessant (>4-6 weeks but <3 months), recurrent (after symptom-free interval), or chronic (>3 months). Pericardial effusion is fluid accumulation in the pericardial space, which when large or rapidly accumulating can cause cardiac tamponade - a life-threatening emergency where fluid compresses the heart and impairs filling.

In developed countries, most acute pericarditis is idiopathic or viral. However, in Africa, tuberculosis (TB) is the leading cause of pericardial disease, accounting for 50-70% of cases in endemic regions. TB pericarditis has high mortality (17-40%) and can lead to constrictive pericarditis. HIV co-infection significantly increases TB pericarditis risk and affects outcomes.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• CARDIAC TAMPONADE IS A MEDICAL EMERGENCY - Beck's triad: Hypotension, elevated JVP, muffled heart sounds
• Pulsus paradoxus >10 mmHg suggests significant hemodynamic compromise
• Echocardiography is the key diagnostic tool - RA/RV diastolic collapse indicates tamponade physiology
• Pericardiocentesis is LIFE-SAVING in tamponade - do NOT delay for imaging if patient unstable
• In Africa, ALWAYS consider TB pericarditis - start empiric TB treatment if clinical suspicion high
• TB pericarditis: 4-drug anti-TB therapy; role of steroids debated (IMPI trial showed no mortality benefit)
• NSAIDs + Colchicine is first-line for acute idiopathic/viral pericarditis
• Avoid anticoagulation in acute pericarditis (risk of hemorrhagic effusion)
• High-risk features: Fever >38°C, subacute onset, large effusion, tamponade, failure of NSAID therapy
• Constrictive pericarditis: "Square root sign" on pressure tracing; pericardiectomy may be curative`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Acute pericarditis: 27.7 per 100,000 person-years in developed countries
• 5% of chest pain presentations to emergency departments
• Male predominance (2:1) for idiopathic/viral pericarditis
• Peak age: 20-50 years
• Recurrence rate: 15-30% after first episode; 50% after recurrence

ETIOLOGY - DEVELOPED COUNTRIES:
• Idiopathic (viral presumed): 80-90%
• Post-cardiac injury: 5-10% (post-MI, post-surgery, post-procedural)
• Autoimmune/inflammatory: 3-5%
• Neoplastic: 5-7%
• Bacterial (non-TB): <1%
• TB: <5%

AFRICAN EPIDEMIOLOGY:
• TB pericarditis: Leading cause (50-70% of pericardial disease)
• HIV dramatically increases TB pericarditis risk (up to 40-fold)
• TB pericarditis mortality: 17-40% (higher with HIV co-infection)
• Constrictive pericarditis: Often sequel of TB (30-50% of cases)
• Purulent bacterial pericarditis: More common than in developed countries
• HIV-associated pericardial effusion: Common, often TB-related

TB PERICARDITIS IN AFRICA:
• Incidence: 1-2% of TB cases develop pericarditis
• HIV-positive patients: TB pericarditis in 10-15% of hospitalized patients
• South Africa: Highest documented rates globally
• Mortality: 17-40% overall; >50% in some HIV cohorts
• Constrictive pericarditis: 30-50% of TB pericarditis survivors

RISK FACTORS FOR PERICARDITIS:
• Recent viral infection (idiopathic/viral)
• Post-MI (Dressler syndrome - 2-10 weeks post-MI)
• Post-cardiac surgery (post-pericardiotomy syndrome)
• Autoimmune disease (SLE, RA, scleroderma)
• Malignancy (lung, breast, lymphoma, leukemia)
• Chronic kidney disease (uremic pericarditis)
• TB exposure and HIV infection
• Chest radiation
• Trauma

RISK FACTORS FOR TB PERICARDITIS:
• HIV infection (most important)
• Active pulmonary TB
• TB endemic region
• Immunosuppression
• Malnutrition
• Close contact with TB`,

    clinicalPresentation: `ACUTE PERICARDITIS:

Classic Symptoms:
• Chest pain (85-90%):
  - Sharp, pleuritic (worse with inspiration)
  - Retrosternal, may radiate to trapezius ridge (pathognomonic)
  - Worse lying flat, relieved sitting forward
  - May mimic MI (but position-dependent)
• Dyspnea (especially with large effusion)
• Fever (common in infectious causes)
• Cough
• Fatigue, malaise

Physical Examination:
• Pericardial friction rub (pathognomonic):
  - Scratchy, high-pitched, best heard at left lower sternal border
  - Classically triphasic (atrial systole, ventricular systole, early diastole)
  - May be transient, positional (lean forward)
  - Absent if large effusion
• Tachycardia
• Low-grade fever
• Signs of underlying disease (TB, malignancy, autoimmune)

PERICARDIAL EFFUSION:

Symptoms (depend on rate of accumulation):
• May be asymptomatic (slow accumulation)
• Dyspnea, orthopnea
• Chest discomfort or fullness
• Cough, hiccups (phrenic nerve irritation)
• Nausea (diaphragmatic irritation)
• Dysphagia (esophageal compression)
• Hoarseness (recurrent laryngeal nerve)

Signs:
• Muffled/distant heart sounds
• Ewart sign: Dullness at left lung base (large effusion compressing lung)
• Decreased breath sounds at lung bases
• Absent or diminished apex beat

CARDIAC TAMPONADE (EMERGENCY):

Beck's Triad (Classic but often incomplete):
1. Hypotension
2. Elevated JVP (distended neck veins)
3. Muffled heart sounds

Additional Signs:
• Pulsus paradoxus: >10 mmHg drop in SBP during inspiration
  - Measure: SBP at first Korotkoff sound (expiration only) minus SBP at all phases
  - >10 mmHg significant; >20 mmHg severe
• Tachycardia (compensatory)
• Tachypnea
• Cool extremities, poor perfusion
• Narrowed pulse pressure
• Kussmaul sign: Paradoxical rise in JVP with inspiration (also in constrictive)
• Hepatomegaly, ascites (if subacute)
• Altered mental status (severe)

⚠️ TAMPONADE IS A CLINICAL DIAGNOSIS - Do NOT delay treatment for imaging in unstable patient!

TB PERICARDITIS - SPECIFIC FEATURES:

• Subacute onset (weeks to months)
• Constitutional symptoms: Fever, night sweats, weight loss
• Often large effusion at presentation
• High protein, lymphocytic effusion
• May have concurrent pulmonary TB (30-50%)
• HIV co-infection common
• High risk of progression to constriction

CONSTRICTIVE PERICARDITIS:

• Dyspnea on exertion
• Peripheral edema, ascites (out of proportion to dyspnea)
• Fatigue
• Hepatomegaly, hepatic dysfunction
• Kussmaul sign (JVP rises with inspiration)
• Pericardial knock (high-pitched early diastolic sound)
• Pulsus paradoxus usually absent or mild`,

    diagnosis: `DIAGNOSTIC CRITERIA FOR ACUTE PERICARDITIS:

Diagnosis requires ≥2 of 4 criteria:
1. Typical chest pain (sharp, pleuritic, positional)
2. Pericardial friction rub
3. New widespread ST elevation or PR depression on ECG
4. New or worsening pericardial effusion on echocardiography

ELECTROCARDIOGRAM (ECG) FINDINGS:

Stage 1 (Hours to days):
• Diffuse ST elevation (concave upward, "smiling")
• PR depression (most specific finding)
• ST elevation in all leads except aVR and V1
• PR elevation in aVR

Stage 2 (Days):
• ST segments normalize
• T waves flatten

Stage 3 (Days to weeks):
• Diffuse T wave inversions

Stage 4 (Weeks to months):
• ECG normalizes

Distinguishing from STEMI:
• Pericarditis: Diffuse ST elevation, PR depression, no reciprocal changes, ST/T ratio >0.25 in V6
• STEMI: Regional ST elevation, reciprocal changes, evolving Q waves

ECHOCARDIOGRAPHY:

Pericardial Effusion Grading:
• Trivial: <10 mm, seen only in systole
• Small: <10 mm in diastole
• Moderate: 10-20 mm
• Large: >20 mm

Tamponade Signs:
• RA collapse in late diastole (early sign, sensitive)
• RV collapse in early diastole (more specific)
• IVC plethora (dilated >2.1 cm, <50% inspiratory collapse)
• Respiratory variation in mitral/tricuspid inflow (>25% mitral, >40% tricuspid)
• "Swinging heart" in large effusion

LABORATORY INVESTIGATIONS:

Inflammatory Markers:
• ESR: Usually elevated
• CRP: Elevated (may guide treatment duration)
• WBC: May be elevated

Cardiac Biomarkers:
• Troponin: Elevated in 30-50% (myopericarditis); does not affect prognosis
• BNP: May be elevated with tamponade or heart failure

Pericardial Fluid Analysis (if pericardiocentesis performed):

| Parameter | Transudate | Exudate | TB Pericarditis |
|-----------|------------|---------|-----------------|
| Protein | <3 g/dL | >3 g/dL | Usually >5 g/dL |
| LDH | Low | High | Very high |
| Glucose | Normal | Low | Very low (<40) |
| Cell count | Low | High | High (lymphocytic) |
| ADA | <40 U/L | Variable | >40 U/L (>70 highly suggestive) |

TB Pericarditis Diagnosis:
• Adenosine deaminase (ADA) >40 U/L: Sensitivity 87-89%, Specificity 89-91%
• Pericardial fluid culture: Low yield (25-50%)
• PCR for M. tuberculosis: Sensitivity 60-80%
• Pericardial biopsy: Granulomas, AFB, culture
• Interferon-gamma: Promising but less available

IMAGING:

Chest X-ray:
• "Water bottle" heart (large effusion)
• Cardiomegaly
• Pulmonary findings (TB, malignancy)

CT Chest:
• Pericardial thickening (>4mm suggests inflammation)
• Pericardial calcification (constrictive pericarditis)
• Loculated effusion
• Underlying cause (TB, malignancy)

Cardiac MRI:
• Pericardial inflammation (late gadolinium enhancement)
• Distinguishes constrictive from restrictive cardiomyopathy
• Myocardial involvement

CARDIAC CATHETERIZATION:
• Tamponade: Equalization of diastolic pressures (RA = RV diastolic = PA diastolic = PCWP)
• Constrictive pericarditis: "Square root sign" or "dip and plateau" in ventricular pressure tracing`,

    differentialDiagnosis: [
      'Acute myocardial infarction - Regional ECG changes, troponin elevation, wall motion abnormalities; may have pericarditis as complication',
      'Myocarditis - Similar presentation; global LV dysfunction; often viral; may overlap (myopericarditis)',
      'Pulmonary embolism - Pleuritic chest pain, dyspnea; D-dimer, CT angiography; risk factors differ',
      'Aortic dissection - Tearing chest pain radiating to back; widened mediastinum; CT angiography diagnostic',
      'Pneumothorax - Sudden pleuritic chest pain, dyspnea; absent breath sounds; CXR diagnostic',
      'Pleuritis/Pleural effusion - Lateral chest pain, dyspnea; friction rub over lung fields; CXR, CT',
      'Costochondritis - Reproducible chest wall tenderness; no ECG changes; clinical diagnosis',
      'GERD/Esophageal spasm - Burning pain, positional; related to meals; trial of PPI',
      'Pneumonia - Fever, cough, consolidation on CXR; may cause parapneumonic pericarditis',
      'Malignant pericardial effusion - Known malignancy; hemorrhagic effusion; cytology positive',
      'Uremic pericarditis - ESRD patient; often hemorrhagic; responds to dialysis intensification',
      'Constrictive pericarditis - Chronic; elevated JVP, ascites; pericardial calcification; cardiac catheterization',
      'Restrictive cardiomyopathy - Mimics constriction; myocardial disease; MRI distinguishes',
    ],

    investigations: [
      {
        name: 'ECG (12-Lead)',
        indication: '🔴 IMMEDIATE - Diffuse ST elevation, PR depression; differentiate from STEMI; serial ECGs',
        availability: 'BASIC'
      },
      {
        name: 'Transthoracic Echocardiogram',
        indication: '🔴 IMMEDIATE - Detect effusion, assess size, tamponade physiology; guide pericardiocentesis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 IMMEDIATE - "Water bottle" heart; pulmonary pathology; cardiomegaly',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - Leukocytosis (infection); anemia (chronic disease, malignancy)',
        availability: 'BASIC'
      },
      {
        name: 'ESR and CRP',
        indication: '🟠 URGENT - Elevated in inflammation; CRP guides treatment duration; monitor response',
        availability: 'BASIC'
      },
      {
        name: 'Troponin',
        indication: '🔴 IMMEDIATE - Elevated in myopericarditis (30-50%); exclude MI',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, BUN)',
        indication: '🟠 URGENT - Uremic pericarditis; baseline before treatment',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests',
        indication: '🟡 Routine - Hypothyroid pericardial effusion',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT - In Africa, essential; HIV increases TB pericarditis risk dramatically',
        availability: 'BASIC'
      },
      {
        name: 'TB Workup (Sputum, Mantoux, Quantiferon)',
        indication: '🟠 URGENT - In endemic areas; always consider TB pericarditis',
        availability: 'BASIC'
      },
      {
        name: 'Pericardial Fluid Analysis',
        indication: '🔴 IMMEDIATE - If pericardiocentesis performed; cell count, protein, LDH, glucose, ADA, culture, cytology',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Adenosine Deaminase (ADA) in Pericardial Fluid',
        indication: '🔴 IMMEDIATE - TB pericarditis; >40 U/L highly suggestive; >70 U/L near-diagnostic',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pericardial Fluid AFB Smear and Culture',
        indication: '🟠 URGENT - TB confirmation; low sensitivity (25-50%) but specific',
        availability: 'BASIC'
      },
      {
        name: 'Pericardial Fluid TB PCR (GeneXpert)',
        indication: '🟠 URGENT - Rapid TB detection; sensitivity 60-80%',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Chest',
        indication: '🟠 URGENT - Pericardial thickening, calcification, underlying cause (TB, malignancy)',
        availability: 'ADVANCED'
      },
      {
        name: 'Cardiac MRI',
        indication: '🟡 Routine - Pericardial inflammation, myocardial involvement; constriction vs restriction',
        availability: 'ADVANCED'
      },
      {
        name: 'ANA, Anti-dsDNA, RF',
        indication: '🟡 Routine - If autoimmune cause suspected (SLE, RA)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pericardial Biopsy',
        indication: '🟡 Routine - Diagnostic uncertainty; suspected TB or malignancy; surgical or percutaneous',
        availability: 'ADVANCED'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS FOR TAMPONADE - Beck triad (hypotension, JVD, muffled sounds), pulsus paradoxus >10 mmHg',
        '⚡ If TAMPONADE: Immediate pericardiocentesis or surgical drainage - DO NOT DELAY',
        '⚡ IV access, cardiac monitoring, oxygen if hypoxic',
        '⚡ Avoid diuretics and vasodilators in tamponade (reduce preload worsens hemodynamics)',
        '⚡ IV fluids if hypotensive (temporizing measure while preparing drainage)',
        '⚡ Urgent echocardiography to confirm effusion and guide drainage',
        '⚡ 12-lead ECG - Differentiate from STEMI; look for diffuse ST elevation, PR depression',
        '⚡ In Africa: HIGH suspicion for TB - obtain ADA, start empiric TB treatment if clinical suspicion',
        '⚡ Assess for high-risk features: Fever >38°C, subacute onset, large effusion, tamponade, immunosuppression',
        '⚡ Discontinue anticoagulation if on it (risk of hemorrhagic effusion)',
        '⚡ Send pericardial fluid for analysis if drained (cell count, protein, LDH, glucose, ADA, culture, cytology)',
        '⚡ Pain relief - NSAIDs first-line for acute pericarditis',
      ],
      pharmacological: [
        {
          drug: 'Ibuprofen',
          indication: '🔴 First-line Treatment - NSAID for acute idiopathic/viral pericarditis; anti-inflammatory and analgesic',
          dose: '600 mg PO every 8 hours (range 1200-2400 mg/day). Taper over 2-4 weeks based on symptoms and CRP',
          route: 'PO',
          duration: '1-2 weeks at full dose, then taper over 2-4 weeks; total treatment 4-6 weeks',
          contraindications: ['Active peptic ulcer/GI bleeding', 'Severe renal impairment', 'Severe heart failure', 'Post-CABG (first 10-14 days)', 'Aspirin allergy'],
          sideEffects: ['GI upset, ulceration, bleeding', 'Renal impairment', 'Hypertension', 'Cardiovascular risk (long-term)'],
          alternatives: ['Aspirin 750-1000mg TID', 'Indomethacin 25-50mg TID', 'Naproxen 500mg BID']
        },
        {
          drug: 'Aspirin (High-dose)',
          indication: '🔴 First-line Alternative - Preferred in post-MI pericarditis (Dressler syndrome); anti-inflammatory',
          dose: '750-1000 mg PO every 8 hours (2.25-3g/day). Taper over 2-4 weeks',
          route: 'PO',
          duration: '1-2 weeks at full dose, then taper over 2-4 weeks',
          contraindications: ['Active peptic ulcer/GI bleeding', 'Aspirin allergy', 'Severe hepatic impairment', 'Children with viral illness (Reye syndrome)'],
          sideEffects: ['GI upset, ulceration', 'Tinnitus (high doses)', 'Bleeding', 'Hepatotoxicity'],
          alternatives: ['Ibuprofen if not post-MI', 'Indomethacin']
        },
        {
          drug: 'Colchicine',
          indication: '🔴 Adjunctive Therapy - Add to NSAID/aspirin; REDUCES RECURRENCE by 50%; improves remission rate',
          dose: 'Weight >70 kg: 0.5 mg PO twice daily. Weight ≤70 kg: 0.5 mg PO once daily. No loading dose',
          route: 'PO',
          duration: '3 months for acute pericarditis; 6 months for recurrent pericarditis',
          contraindications: ['Severe renal impairment (CrCl <30)', 'Severe hepatic impairment', 'Concurrent strong CYP3A4 inhibitors', 'Pregnancy'],
          sideEffects: ['Diarrhea (most common)', 'Nausea, vomiting', 'Abdominal pain', 'Myelosuppression (rare, dose-related)'],
          alternatives: ['If intolerant, use NSAID alone (higher recurrence risk)']
        },
        {
          drug: 'Prednisone',
          indication: '🟠 Second-line - Only if NSAID contraindicated, failed, or specific indications (autoimmune, uremic); INCREASES recurrence risk',
          dose: '0.25-0.5 mg/kg/day PO for 2-4 weeks, then SLOW taper over 2-3 months. Maintain colchicine during taper',
          route: 'PO',
          duration: 'Slow taper essential - rapid taper increases recurrence',
          contraindications: ['Active untreated infection (especially TB)', 'Uncontrolled diabetes', 'Severe osteoporosis'],
          sideEffects: ['Hyperglycemia', 'Weight gain', 'Immunosuppression', 'Osteoporosis', 'Adrenal suppression', 'Increased recurrence risk'],
          alternatives: ['Anakinra for refractory cases', 'Azathioprine for steroid-sparing']
        },
        {
          drug: 'Rifampicin (TB Pericarditis)',
          indication: '🔴 TB Treatment - Part of 4-drug intensive phase for TB pericarditis',
          dose: 'Adults: 10 mg/kg/day (max 600 mg) PO once daily. Children: 15 mg/kg/day',
          route: 'PO (empty stomach)',
          duration: 'Intensive phase: 2 months (with INH, PZA, EMB). Continuation: 4 months (with INH). Total: 6 months',
          contraindications: ['Severe hepatic disease', 'Hypersensitivity', 'Concurrent certain antiretrovirals (interactions)'],
          sideEffects: ['Hepatotoxicity', 'Orange discoloration of body fluids', 'Drug interactions', 'Flu-like syndrome'],
          alternatives: ['If intolerant, consult TB specialist for alternative regimen']
        },
        {
          drug: 'Isoniazid (TB Pericarditis)',
          indication: '🔴 TB Treatment - Part of 4-drug regimen; bactericidal against M. tuberculosis',
          dose: 'Adults: 5 mg/kg/day (max 300 mg) PO once daily. Children: 10-15 mg/kg/day. Add Pyridoxine 25-50 mg daily',
          route: 'PO',
          duration: '6 months total (2 months intensive + 4 months continuation)',
          contraindications: ['Acute hepatic disease', 'Previous INH hepatotoxicity'],
          sideEffects: ['Hepatotoxicity', 'Peripheral neuropathy (prevent with pyridoxine)', 'Hypersensitivity'],
          alternatives: ['Rifabutin-based regimen if INH intolerant']
        },
        {
          drug: 'Pyrazinamide (TB Pericarditis)',
          indication: '🔴 TB Treatment - Intensive phase only; sterilizing activity',
          dose: 'Adults: 25 mg/kg/day (max 2g) PO once daily. Weight-based: 40-55 kg: 1000 mg; 56-75 kg: 1500 mg; >75 kg: 2000 mg',
          route: 'PO',
          duration: 'Intensive phase only: 2 months',
          contraindications: ['Severe hepatic impairment', 'Acute gout', 'Porphyria'],
          sideEffects: ['Hepatotoxicity', 'Hyperuricemia/gout', 'Arthralgia', 'GI upset'],
          alternatives: ['Extended rifampicin + INH if PZA contraindicated']
        },
        {
          drug: 'Ethambutol (TB Pericarditis)',
          indication: '🔴 TB Treatment - Intensive phase; prevents resistance emergence',
          dose: 'Adults: 15-20 mg/kg/day (max 1.6g) PO once daily. Weight-based: 40-55 kg: 800 mg; 56-75 kg: 1200 mg; >75 kg: 1600 mg',
          route: 'PO',
          duration: 'Intensive phase: 2 months (may continue if resistance suspected)',
          contraindications: ['Optic neuritis', 'Inability to report visual symptoms (young children)'],
          sideEffects: ['Optic neuritis (dose-related, reversible)', 'Color blindness', 'Peripheral neuropathy'],
          alternatives: ['Streptomycin IM if ethambutol contraindicated']
        },
        {
          drug: 'Prednisone (TB Pericarditis - Controversial)',
          indication: '🟡 Adjunctive in TB Pericarditis - IMPI trial showed no mortality benefit; may reduce effusion reaccumulation and constriction',
          dose: '1 mg/kg/day (max 60 mg) for 4 weeks, then taper over 8 weeks. ONLY with concurrent anti-TB therapy',
          route: 'PO',
          duration: 'Total 6-8 weeks; taper gradually',
          contraindications: ['TB treatment not initiated', 'Uncontrolled HIV (may worsen)', 'Active other infections'],
          sideEffects: ['Immunosuppression', 'Hyperglycemia', 'Reactivation of other infections', 'HIV-related complications'],
          alternatives: ['Consider in HIV-negative patients with large effusion or constriction risk; avoid in HIV-positive']
        },
        {
          drug: 'Omeprazole/PPI',
          indication: '🟠 Gastroprotection - Concurrent with NSAID therapy to prevent GI ulceration',
          dose: '20-40 mg PO once daily',
          route: 'PO',
          duration: 'Duration of NSAID therapy',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Headache', 'GI upset', 'Long-term: hypomagnesemia, B12 deficiency, fracture risk'],
          alternatives: ['Pantoprazole', 'Esomeprazole', 'H2 blockers (less effective)']
        },
        {
          drug: 'Anakinra',
          indication: '🟡 Refractory/Recurrent Pericarditis - IL-1 receptor antagonist; for colchicine-resistant cases',
          dose: '100 mg SC daily for 3-6 months, then taper',
          route: 'Subcutaneous',
          duration: 'Months to years; taper cautiously',
          contraindications: ['Active infection', 'Neutropenia', 'Live vaccines'],
          sideEffects: ['Injection site reactions', 'Neutropenia', 'Increased infection risk', 'Expensive'],
          alternatives: ['Rilonacept (IL-1 trap)', 'Azathioprine for steroid-sparing']
        },
      ],
      nonPharmacological: [
        'Rest/Activity Restriction: Avoid strenuous activity until symptoms resolve and CRP normalizes; athletes: 3 months rest after acute episode',
        'Pericardiocentesis: LIFE-SAVING for tamponade; echo-guided preferred; drain >50 mL for analysis; leave catheter for ongoing drainage if needed',
        'Pericardial Window (Surgical): For recurrent effusion, loculated effusion, or need for biopsy; subxiphoid or thoracoscopic approach',
        'Pericardiectomy: For constrictive pericarditis; complete resection of pericardium; mortality 5-15%; best when done early before severe constriction',
        'Intrapericardial Therapy: Instillation of steroids or chemotherapy for malignant effusion; sclerosing agents (tetracycline) to prevent reaccumulation',
        'Dialysis Intensification: For uremic pericarditis; increase dialysis frequency; most cases resolve within 1-2 weeks',
        'Radiation: For malignant pericardial disease as part of oncologic management',
        'Nutritional Support: Especially in TB pericarditis; address malnutrition',
      ],
      surgical: [
        'Echo-Guided Pericardiocentesis: First-line drainage for tamponade and diagnostic/therapeutic in large effusion; subcostal or apical approach; >90% success rate; leave pigtail catheter for ongoing drainage',
        'Surgical Pericardial Window: Alternative to repeat pericardiocentesis; creates permanent drainage into pleural or peritoneal space; subxiphoid or thoracoscopic; useful for recurrent or loculated effusion, malignancy',
        'Pericardiectomy: Definitive treatment for constrictive pericarditis; extensive resection from phrenic to phrenic nerve; mortality 5-15%; hemodynamic improvement in 90%; best outcomes when done early',
        'Video-Assisted Thoracoscopic Surgery (VATS): For pericardial window, biopsy, or limited pericardiectomy; less invasive than sternotomy',
        'Emergency Thoracotomy/Sternotomy: For traumatic tamponade, post-cardiac surgery tamponade, or failed pericardiocentesis',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Cardiac tamponade → Immediate pericardiocentesis or surgical drainage; cardiothoracic surgery if pericardiocentesis fails',
        '🔴 EMERGENCY - Hemodynamic instability despite initial management → ICU, advanced hemodynamic support',
        '🔴 EMERGENCY - Suspected purulent pericarditis → Surgical drainage usually required; infectious disease consultation',
        '🟠 URGENT - Large pericardial effusion (>20 mm) without tamponade → Close monitoring; consider drainage',
        '🟠 URGENT - TB pericarditis → Start TB treatment; consider drainage if large effusion; monitor for constriction',
        '🟠 URGENT - Failure to respond to NSAID + colchicine after 1-2 weeks → Reassess diagnosis; consider steroids',
        '🟠 URGENT - Suspected malignant effusion → Oncology consultation; pericardial fluid cytology; staging',
        '🟡 ROUTINE - Recurrent pericarditis → Colchicine optimization; consider steroid-sparing agents',
        '🟡 ROUTINE - Suspected constrictive pericarditis → Cardiac MRI, catheterization; pericardiectomy evaluation',
        '🟡 ROUTINE - Myopericarditis (troponin elevation) → Follow LV function; avoid strenuous activity',
      ],
      monitoring: [
        'Symptoms: Daily assessment of chest pain, dyspnea, hemodynamic status during acute phase',
        'Vital Signs: BP (watch for pulsus paradoxus), heart rate, respiratory rate; continuous monitoring if effusion present',
        'CRP: Key marker; guide treatment duration; should normalize before stopping therapy',
        'Echocardiography: Repeat if symptoms worsen or new effusion suspected; follow effusion size; assess for constriction',
        'ECG: Serial ECGs to follow evolution; assess for arrhythmias',
        'Renal Function: Monitor during NSAID therapy; more frequent in CKD',
        'GI Symptoms: Watch for NSAID-related GI adverse effects',
        'TB Pericarditis: LFTs during anti-TB therapy; clinical response; repeat echo at 2, 4, 6 weeks',
        'Post-Drainage: Monitor for reaccumulation; drain output if catheter in place',
        'Activity Tolerance: Assess before returning to normal activity or sports',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

ACUTE PERICARDITIS (Uncomplicated):
• 1 week: Symptom assessment, CRP check
• 2-4 weeks: Clinical review, repeat CRP; begin taper if improved
• 6-8 weeks: Confirm resolution; discontinue medications if CRP normal and asymptomatic
• 3 months: Ensure no recurrence; clear for sports if asymptomatic

WITH PERICARDIAL EFFUSION:
• Weekly echocardiography until effusion stable or resolved
• More frequent if large effusion or any tamponade physiology
• After drainage: 24-48 hours, then weekly until resolved

TB PERICARDITIS:
• Baseline: Full TB workup, HIV test, LFTs, echo
• 2 weeks: Clinical response, LFTs, effusion reassessment
• Monthly during intensive phase: LFTs, clinical status, echo
• 2 months: End of intensive phase assessment; switch to continuation
• 6 months: End of treatment; assess for constrictive pericarditis
• 6-12 months post-treatment: Long-term follow-up for constriction (develops in 30-50%)

RECURRENT PERICARDITIS:
• Every 1-2 weeks during acute flare
• Monthly during colchicine therapy
• 6 months: Assess need for continued colchicine
• Consider steroid-sparing agents if >3 recurrences

CONSTRICTIVE PERICARDITIS:
• 3-6 months: Trial of anti-inflammatory therapy (especially TB-related)
• Surgical evaluation: If persistent constriction and functional limitation
• Post-pericardiectomy: 4-6 weeks, then 3-6 monthly for first year

MONITORING POINTS:
• CRP normalization is key before stopping treatment
• Athletes: 3 months activity restriction after acute episode
• Watch for constriction in TB (30-50% risk)
• Recurrence: Most common in first 18 months
• Chronic/recurrent: Consider referral to pericardial disease specialist`
    },

    redFlags: [
      '🔴 CRITICAL: Cardiac tamponade - Beck triad, pulsus paradoxus >10 mmHg, hemodynamic instability → IMMEDIATE pericardiocentesis',
      '🔴 CRITICAL: Rapidly enlarging effusion - Subacute tamponade developing → Urgent drainage',
      '🔴 CRITICAL: Purulent pericarditis suspected - High fever, severely ill, effusion → Surgical drainage, IV antibiotics',
      '🔴 CRITICAL: Post-cardiac surgery tamponade - Blood in pericardium → Emergency re-exploration',
      '🔴 CRITICAL: Failure to improve with drainage - Ongoing hypotension → Consider loculated effusion, need for surgical window',
      '🟠 URGENT: High-risk features at presentation - Fever >38°C, subacute onset, large effusion, immunosuppression, trauma → Admit, investigate etiology',
      '🟠 URGENT: Failure to respond to NSAIDs after 1 week - Symptoms persisting → Reassess diagnosis, consider TB/other etiology',
      '🟠 URGENT: Myopericarditis - Troponin elevation, wall motion abnormalities → Risk stratification, activity restriction',
      '🟠 URGENT: TB pericarditis with large effusion - High risk of tamponade and constriction → Consider early drainage',
      '🟠 URGENT: Malignant effusion - Known cancer with effusion → Oncology input, consider pericardial window',
      '🟡 WARNING: Signs of developing constriction - Increasing JVP, ascites, peripheral edema → Echo, CT, cardiology referral',
    ],

    complications: [
      'Cardiac Tamponade: Life-threatening compression of heart; occurs with rapid accumulation or large volume; mortality high if untreated',
      'Constrictive Pericarditis: Fibrosis and calcification restrict cardiac filling; 30-50% of TB pericarditis; requires pericardiectomy',
      'Recurrent Pericarditis: 15-30% after first episode; 50% after recurrence; often requires prolonged colchicine',
      'Effusive-Constrictive Pericarditis: Combination of effusion and constriction; elevated pressures persist after drainage',
      'Myopericarditis: Myocardial involvement; troponin elevation; may cause transient LV dysfunction; usually recovers',
      'Pericardial Calcification: Especially TB and purulent; may contribute to constriction',
      'Arrhythmias: Atrial fibrillation common with large effusion or constriction',
      'Hepatic Congestion: From right heart failure in constriction; "cardiac cirrhosis"',
      'Protein-Losing Enteropathy: Rare complication of severe constriction',
      'Procedure Complications: Pericardiocentesis risks (cardiac puncture, arrhythmia, pneumothorax); pericardiectomy mortality 5-15%',
    ],

    prognosis: `OVERALL PROGNOSIS:

ACUTE IDIOPATHIC/VIRAL PERICARDITIS:
• Excellent prognosis with treatment
• 70-90% resolve within 2-4 weeks
• Recurrence: 15-30% (reduced to 10-15% with colchicine)
• Tamponade: <5% in idiopathic
• Constriction: <1% in idiopathic

TB PERICARDITIS:
• Mortality: 17-40% overall
  - HIV-negative: 17-26%
  - HIV-positive: 34-40%
• Constrictive pericarditis: 30-50% of survivors
• Tamponade at presentation: 20-40%
• With treatment: Most effusions resolve in 6-8 weeks
• Steroids: IMPI trial showed no mortality benefit but may reduce constriction

PURULENT PERICARDITIS:
• High mortality: 20-40% even with treatment
• Requires surgical drainage
• Often in immunocompromised or post-thoracic surgery

MALIGNANT PERICARDIAL EFFUSION:
• Prognosis determined by underlying malignancy
• Median survival: 3-6 months
• Recurrence common without definitive intervention

CONSTRICTIVE PERICARDITIS:
• Without surgery: Progressive decline
• Pericardiectomy: 70-90% improvement
• Operative mortality: 5-15%
• Long-term survival post-pericardiectomy: 70-80% at 5 years

FACTORS AFFECTING PROGNOSIS:
• Etiology (TB and purulent worse than viral)
• HIV co-infection (worse outcomes)
• Timing of treatment
• Development of constriction
• Access to surgical intervention

AFRICAN CONTEXT:
• TB pericarditis: High mortality due to late presentation, HIV co-infection
• Limited surgical capacity for pericardiectomy
• Constrictive pericarditis: Major burden
• Early diagnosis and TB treatment improves outcomes`,

    prevention: `PRIMARY PREVENTION:

TB Pericarditis Prevention:
• TB prevention strategies (BCG vaccination, IPT in HIV)
• HIV prevention and treatment (ART reduces TB risk)
• Early detection and treatment of pulmonary TB
• Infection control measures
• Contact tracing

Preventing Recurrent Pericarditis:
• Complete initial treatment course (NSAID + colchicine)
• Do not stop medications abruptly
• Continue colchicine for full 3 months (acute) or 6 months (recurrent)
• Avoid excessive physical activity during acute phase

Post-MI Pericarditis (Dressler syndrome):
• Rare with modern early reperfusion
• No specific prevention

Post-Cardiac Surgery Pericarditis:
• Colchicine prophylaxis shown effective (COPPS trials)
• Dose: 0.5 mg BID starting on POD 3 for 1 month
• Reduces post-pericardiotomy syndrome by 50%

SECONDARY PREVENTION:

Preventing Constriction in TB Pericarditis:
• Prompt initiation of anti-TB therapy
• Complete 6-month treatment course
• Role of steroids uncertain (IMPI trial negative for mortality)
• Early drainage of large effusions may reduce constriction risk
• Close follow-up for developing constriction

Preventing Recurrence:
• Colchicine for 3-6 months minimum
• Avoid triggers (viral infections if recurrent)
• Gradual medication taper
• CRP normalization before stopping therapy

TERTIARY PREVENTION:

Preventing Complications of Constriction:
• Early pericardiectomy before severe symptoms
• Optimize nutrition and volume status
• Avoid factors worsening congestion

AFRICAN-SPECIFIC STRATEGIES:

• Strengthen TB control programs
• Improve HIV diagnosis and ART coverage
• Increase access to echocardiography
• Train healthcare workers in pericardiocentesis
• Build capacity for pericardiectomy
• Develop pericardial disease registries
• Implement screening in high-risk populations`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING PERICARDITIS - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS PERICARDITIS?
Pericarditis is inflammation (swelling and irritation) of the pericardium - the thin sac that surrounds your heart. This inflammation causes chest pain and sometimes fluid to build up around your heart (pericardial effusion).

WHAT CAUSES PERICARDITIS?
• Viral infections (most common in developed countries)
• Tuberculosis (TB) - very common cause in Africa
• Bacterial infections
• Autoimmune diseases (lupus, rheumatoid arthritis)
• Kidney failure
• Cancer
• After heart attack or heart surgery
• Sometimes the cause is unknown

WHY IS IT IMPORTANT?
• Most pericarditis gets better with treatment
• If too much fluid builds up, it can squeeze the heart (cardiac tamponade) - this is an EMERGENCY
• Some cases can lead to scarring that restricts the heart (constrictive pericarditis)

SYMPTOMS TO WATCH FOR:

Pericarditis symptoms:
• Sharp chest pain that:
  - Is worse when breathing deeply
  - Is worse when lying flat
  - Gets better when sitting up and leaning forward
  - May spread to the shoulder
• Fever
• Feeling tired
• Difficulty breathing

⚠️ EMERGENCY SYMPTOMS - GO TO HOSPITAL IMMEDIATELY:
• Severe difficulty breathing
• Fainting or near-fainting
• Swelling of legs and feet with breathlessness
• Feeling very unwell with low blood pressure

TREATMENT:

For most pericarditis:
• Anti-inflammatory medicines (like ibuprofen)
• Colchicine - an important medicine that prevents the condition from coming back
• Rest from strenuous activity

For TB pericarditis:
• You will need TB medicines for 6 months
• Take ALL your medicines exactly as prescribed
• Do not stop even if you feel better

If fluid has built up:
• You may need a procedure to drain the fluid (pericardiocentesis)
• A thin needle is inserted through the chest to remove fluid

TAKING YOUR MEDICATIONS:

• Take all medicines as prescribed
• Do not stop colchicine early - it prevents the condition from returning
• Take ibuprofen with food to protect your stomach
• If you have TB pericarditis, complete all 6 months of TB treatment

LIFESTYLE DURING RECOVERY:

• Rest from strenuous activity until your doctor says it is safe
• No sports or heavy exercise until fully recovered (usually 3 months)
• Eat a healthy diet
• Get adequate sleep
• Avoid smoking and excessive alcohol

FOLLOW-UP:

• Keep all your clinic appointments
• You will need repeat tests to make sure the condition is improving
• Blood tests (CRP) help your doctor know when you can stop treatment
• Ultrasound of the heart (echo) may be repeated

WHEN TO SEEK MEDICAL CARE:

See your doctor if:
• Chest pain is getting worse
• You develop new symptoms
• Fever returns
• You notice leg swelling
• You are having side effects from your medicines

PREVENTING RECURRENCE:

• Complete your treatment course fully
• Take colchicine for as long as prescribed (usually 3-6 months)
• Do not stop medications suddenly
• Return to activity gradually
• Tell your doctor if symptoms return

FOR TB PERICARDITIS PATIENTS:

• TB pericarditis is treatable but serious
• Take your TB medicines every day for the full 6 months
• Missing doses can cause treatment failure and drug resistance
• Your doctor will monitor for a complication called constrictive pericarditis
• You may need surgery if the sac around your heart becomes scarred and stiff
• If you are HIV-positive, take your HIV medicines as well

REMEMBER:
• Most pericarditis responds well to treatment
• Taking your medicines correctly is the most important thing you can do
• Watch for emergency symptoms - go to hospital immediately if they occur
• Keep all follow-up appointments
• Return to activity only when cleared by your doctor`,

    references: [
      'Adler Y, et al. 2015 ESC Guidelines for the diagnosis and management of pericardial diseases. Eur Heart J. 2015;36:2921-2964. - ESC guidelines',
      'Imazio M, et al. Colchicine for recurrent pericarditis (CORP-2). Ann Intern Med. 2014;160:73-80. - CORP-2 trial',
      'Imazio M, et al. A randomized trial of colchicine for acute pericarditis (COPE). N Engl J Med. 2013;369:1522-1528. - COPE trial',
      'Mayosi BM, et al. Prednisolone and Mycobacterium indicus pranii in tuberculous pericarditis (IMPI). N Engl J Med. 2014;371:1121-1130. - IMPI trial',
      'Reuter H, et al. Tuberculous pericardial effusion. Circulation. 2005;112:3608-3616. - TB pericarditis review',
      'Syed FF, Mayosi BM. A modern approach to tuberculous pericarditis. Prog Cardiovasc Dis. 2007;50:218-236. - TB pericarditis management',
      'Mayosi BM, Burgess LJ, Doubell AF. Tuberculous pericarditis. Circulation. 2005;112:3608-3616. - African perspective',
      'Spodick DH. Acute pericarditis: current concepts and practice. JAMA. 2003;289:1150-1153. - Classic review',
      'Klein AL, et al. American Society of Echocardiography clinical recommendations for multimodality cardiovascular imaging of patients with pericardial disease. J Am Soc Echocardiogr. 2013;26:965-1012. - Imaging guidelines',
      'Chiabrando JG, et al. Management of acute and recurrent pericarditis: JACC state-of-the-art review. J Am Coll Cardiol. 2020;75:76-92. - Contemporary management',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 35. PNEUMOTHORAX
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '35',
    name: 'Pneumothorax',
    slug: 'pneumothorax',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'J93.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Pneumothorax is the presence of air in the pleural space, causing partial or complete lung collapse. It can be spontaneous (primary or secondary), traumatic, or iatrogenic. Tension pneumothorax is a life-threatening emergency where air accumulates under pressure, causing mediastinal shift and cardiovascular collapse.

Primary spontaneous pneumothorax (PSP) typically occurs in tall, thin young men without underlying lung disease. Secondary spontaneous pneumothorax (SSP) occurs in patients with underlying lung pathology - in Africa, tuberculosis and HIV-related lung disease are major causes. Traumatic pneumothorax is common due to road traffic accidents, violence, and traditional practices.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• TENSION PNEUMOTHORAX IS A CLINICAL DIAGNOSIS - Do NOT delay treatment for imaging
• Tension pneumothorax signs: Hypotension, tracheal deviation, absent breath sounds, distended neck veins
• Needle decompression: 2nd intercostal space, midclavicular line (or 4th/5th ICS, anterior axillary line)
• Chest tube: 4th-5th intercostal space, anterior axillary line (safe triangle)
• Small PSP (<2 cm rim): May observe or aspirate; no chest tube needed
• SSP: Almost always requires chest tube (underlying lung disease impairs re-expansion)
• In Africa, ALWAYS consider TB as cause of secondary pneumothorax
• Recurrence: 30% after first PSP; consider pleurodesis/surgery after second episode
• Avoid flying and diving until complete resolution and specialist clearance
• High-flow oxygen accelerates reabsorption (4x faster than room air)`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Primary spontaneous pneumothorax (PSP): 7-18 per 100,000 males; 1-6 per 100,000 females annually
• Secondary spontaneous pneumothorax (SSP): 6.3 per 100,000 males; 2 per 100,000 females
• Male:Female ratio: 3-6:1 for PSP
• Peak age: PSP 15-34 years; SSP >55 years (COPD) or younger in Africa (TB)
• Traumatic pneumothorax: Common - up to 50% of chest trauma

AFRICAN EPIDEMIOLOGY:
• Higher incidence of SSP due to TB and HIV
• TB-related pneumothorax: Major cause (30-50% of SSP in endemic areas)
• HIV/AIDS: Increased risk of pneumothorax (Pneumocystis, TB, Kaposi sarcoma)
• Trauma: High burden from road traffic accidents, interpersonal violence
• Traditional practices: Chest trauma from bone-setters, traditional surgery
• Delayed presentation: Often present with larger pneumothorax
• Limited access to chest tubes and underwater seal drainage systems

CLASSIFICATION:

Primary Spontaneous (PSP):
• No clinically apparent lung disease
• Rupture of apical subpleural blebs
• Risk factors: Tall, thin body habitus; smoking; family history; Marfan syndrome

Secondary Spontaneous (SSP):
• Underlying lung disease present
• Causes:
  - COPD/Emphysema (most common in developed countries)
  - Tuberculosis (most common in Africa)
  - Pneumocystis jirovecii pneumonia (HIV)
  - Cystic fibrosis
  - Lung cancer
  - Interstitial lung disease
  - Necrotizing pneumonia
  - Lung abscess
  - Catamenial (menstruation-related, endometriosis)

Traumatic:
• Penetrating trauma (stab, gunshot)
• Blunt trauma (rib fractures, deceleration injury)
• Barotrauma (mechanical ventilation, diving)

Iatrogenic:
• Central line insertion (subclavian, IJ)
• Thoracentesis
• Lung biopsy
• Mechanical ventilation
• CPR

RISK FACTORS:
• Smoking (increases risk 20-fold in men, 10-fold in women)
• Tall, thin body habitus
• Male sex
• Age 15-34 for PSP
• Underlying lung disease
• Connective tissue disorders (Marfan, Ehlers-Danlos)
• Family history
• Previous pneumothorax (30% recurrence)
• HIV infection
• Mechanical ventilation (especially with high pressures)`,

    clinicalPresentation: `SYMPTOMS:

Typical Presentation:
• Sudden onset chest pain (>90%):
  - Sharp, pleuritic
  - Ipsilateral
  - May radiate to shoulder (referred from diaphragm)
• Dyspnea (80%):
  - Sudden onset
  - Severity depends on size and underlying lung function
  - May be minimal in small PSP
  - Severe in SSP even with small pneumothorax
• Cough (dry, non-productive)

Atypical Presentations:
• Asymptomatic (small pneumothorax, found incidentally)
• Gradual onset dyspnea (slowly accumulating)
• Referred shoulder pain only
• Anxiety, restlessness

TENSION PNEUMOTHORAX (EMERGENCY):

Symptoms:
• Severe dyspnea
• Chest pain
• Agitation, confusion
• Feeling of impending doom

Signs (develop rapidly):
• Hypotension / Cardiovascular collapse
• Tachycardia
• Tracheal deviation AWAY from affected side (late sign)
• Distended neck veins (JVP elevated)
• Absent breath sounds on affected side
• Hyperresonance to percussion
• Cyanosis
• Pulseless electrical activity (PEA) / Cardiac arrest

⚠️ TENSION PNEUMOTHORAX IS A CLINICAL DIAGNOSIS - TREAT IMMEDIATELY

PHYSICAL EXAMINATION:

Inspection:
• Reduced chest wall movement on affected side
• Tracheal deviation (tension pneumothorax - late sign)
• Cyanosis (severe cases)
• Use of accessory muscles
• Asymmetric chest expansion

Palpation:
• Decreased tactile fremitus on affected side
• Tracheal position
• Subcutaneous emphysema (if air tracking into tissues)

Percussion:
• Hyperresonance on affected side
• May be difficult to appreciate in noisy environments

Auscultation:
• Decreased or absent breath sounds on affected side
• May be normal in small pneumothorax

SIZE ESTIMATION (CXR):

BTS Method:
• Measure at level of hilum
• >2 cm between lung edge and chest wall = "Large"
• <2 cm = "Small"

Light Index (% collapse):
• % Pneumothorax = 100 - (diameter of collapsed lung³ / diameter of hemithorax³) × 100
• <15% = Small
• 15-60% = Moderate  
• >60% = Large

SPECIAL PRESENTATIONS:

Catamenial Pneumothorax:
• Women 30-40 years
• Recurrent, right-sided (>90%)
• Within 72 hours of menstruation
• Associated with thoracic endometriosis

Pneumothorax in Mechanically Ventilated Patients:
• High suspicion with sudden deterioration
• Increased airway pressures
• Hypoxia, hypotension
• May rapidly progress to tension

Bilateral Pneumothorax:
• Rare but life-threatening
• Often traumatic or iatrogenic
• Requires bilateral chest tubes`,

    diagnosis: `DIAGNOSTIC APPROACH:

TENSION PNEUMOTHORAX:
• CLINICAL DIAGNOSIS - Do NOT wait for imaging
• Immediate needle decompression followed by chest tube

NON-TENSION PNEUMOTHORAX:
• Clinical suspicion → Chest X-ray (confirmatory)
• If diagnosis uncertain → CT chest (more sensitive)
• Assess size and need for intervention

CHEST X-RAY FINDINGS:

Classic Signs:
• Visible visceral pleural line (white line with no lung markings beyond)
• Absence of lung markings peripheral to pleural line
• Deep sulcus sign (deep costophrenic angle on supine film)
• Hyperlucency on affected side

Tension Pneumothorax Signs:
• Mediastinal shift to contralateral side
• Flattening or inversion of ipsilateral hemidiaphragm
• Complete lung collapse
• Widened intercostal spaces

Tips:
• Inspiratory film preferred (expiratory increases sensitivity but less practical)
• Erect PA film if possible; supine films less sensitive
• Look carefully at apex for small pneumothorax
• Deep sulcus sign on supine film suggests pneumothorax

ULTRASOUND (Point-of-Care):

Findings of Pneumothorax:
• Absence of lung sliding (most sensitive - 95-100%)
• Absence of B-lines
• Absence of lung pulse
• Lung point (pathognomonic - junction of sliding and non-sliding pleura)
• A-lines only (non-specific)

Advantages:
• Rapid bedside assessment
• More sensitive than supine CXR
• No radiation
• Can be done during resuscitation

CT CHEST:

Indications:
• Equivocal CXR
• Quantify size accurately
• Identify underlying lung disease (bullae, TB, cancer)
• Pre-surgical planning
• Suspected occult pneumothorax in trauma

Findings:
• Air in pleural space (low attenuation)
• Can detect small pneumothorax (<10%) missed on CXR
• Identifies blebs, bullae, underlying pathology
• Loculated collections

DIFFERENTIATING PSP vs SSP:

| Feature | PSP | SSP |
|---------|-----|-----|
| Age | 15-34 years | Variable; older in COPD, younger in TB |
| Underlying disease | None | Present |
| Symptoms | Often minimal | Usually significant |
| Management | May observe small | Almost always drain |
| Prognosis | Generally good | Depends on underlying disease |

ASSESSMENT OF UNDERLYING CAUSE:

In African Settings - Always Consider:
• TB: Sputum for AFB, GeneXpert, chest CT
• HIV: Test all patients with SSP
• PCP: HIV-positive with bilateral infiltrates
• Lung cancer: Older patients, smoking history

Additional Tests:
• Sputum examination: AFB, culture, cytology
• HIV test: All patients with SSP
• CT chest: Define underlying pathology
• Alpha-1 antitrypsin: Young patients with emphysema, family history`,

    differentialDiagnosis: [
      'Pulmonary embolism - Pleuritic chest pain, dyspnea; risk factors; D-dimer, CTPA; different CXR findings',
      'Acute coronary syndrome - Chest pain, dyspnea; ECG changes, troponin elevation; different character of pain',
      'Pleuritis/Pleurisy - Pleuritic pain; friction rub; no air on imaging; often infectious',
      'Pneumonia - Fever, cough, consolidation on CXR; may coexist or cause pneumothorax',
      'Rib fracture - Localized pain, tenderness; trauma history; fracture on imaging',
      'Costochondritis - Reproducible chest wall tenderness; normal imaging',
      'Pericarditis - Positional pain, friction rub; ECG changes; different CXR',
      'Aortic dissection - Tearing pain radiating to back; widened mediastinum; CT angiography',
      'Esophageal rupture (Boerhaave) - Severe chest pain after vomiting; pneumomediastinum; may have hydropneumothorax',
      'Large bulla mimicking pneumothorax - CT differentiates; bulla has curved inner margin',
      'Diaphragmatic hernia - Bowel in thorax on imaging; trauma or congenital',
      'Skin fold artifact on CXR - Linear shadow; does not conform to pleural anatomy; no lung edge',
    ],

    investigations: [
      {
        name: 'Chest X-ray (PA Erect)',
        indication: '🔴 IMMEDIATE - First-line imaging; confirms diagnosis; assesses size; identifies tension signs. Skip if tension pneumothorax suspected clinically',
        availability: 'BASIC'
      },
      {
        name: 'Point-of-Care Ultrasound (POCUS)',
        indication: '🔴 IMMEDIATE - Bedside diagnosis; absence of lung sliding; more sensitive than supine CXR; ideal in trauma/resuscitation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 IMMEDIATE - Assess oxygenation; guide oxygen therapy; monitor response',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🟠 URGENT - If significant respiratory distress; assess oxygenation and ventilation; hypoxia, may have respiratory alkalosis initially',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Chest',
        indication: '🟠 URGENT - Equivocal CXR; quantify size; identify underlying disease (bullae, TB); pre-surgical planning',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 Routine - Baseline; assess for infection if secondary pneumothorax suspected',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function',
        indication: '🟡 Routine - Baseline before procedures; if contrast CT planned',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT - In African settings; HIV increases risk of TB, PCP, other causes of SSP',
        availability: 'BASIC'
      },
      {
        name: 'Sputum AFB Smear and Culture',
        indication: '🟠 URGENT - If TB suspected; essential in endemic areas; all SSP patients',
        availability: 'BASIC'
      },
      {
        name: 'GeneXpert MTB/RIF',
        indication: '🟠 URGENT - Rapid TB diagnosis; drug resistance detection; sputum or induced sputum',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: '🟠 URGENT - Exclude cardiac cause of chest pain; may show low voltage, axis deviation in large pneumothorax',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies',
        indication: '🟡 Routine - Before chest tube insertion if bleeding risk; anticoagulated patients',
        availability: 'BASIC'
      },
      {
        name: 'Alpha-1 Antitrypsin Level',
        indication: '🟡 Routine - Young patients with emphysema; family history; recurrent pneumothorax',
        availability: 'ADVANCED'
      },
    ],

    management: {
      immediate: [
        '⚡ TENSION PNEUMOTHORAX → Immediate needle decompression (do NOT wait for imaging)',
        '⚡ Needle decompression: 14-16G needle, 2nd ICS midclavicular line OR 4th-5th ICS anterior axillary line',
        '⚡ After needle decompression → Chest tube insertion',
        '⚡ High-flow oxygen (15 L/min via non-rebreather) - Accelerates reabsorption 4x faster than room air',
        '⚡ Establish IV access',
        '⚡ Continuous pulse oximetry',
        '⚡ Chest X-ray to confirm diagnosis and size (if not tension)',
        '⚡ Assess hemodynamic stability',
        '⚡ Determine type: PSP vs SSP vs Traumatic',
        '⚡ Size assessment: >2 cm = Large; <2 cm = Small (BTS criteria)',
        '⚡ Pain management - Analgesia before chest tube insertion',
        '⚡ Consider underlying cause - TB, HIV workup in endemic areas',
      ],
      pharmacological: [
        {
          drug: 'Oxygen (High-Flow)',
          indication: '🔴 First-line - ALL pneumothorax patients; accelerates reabsorption; treat hypoxia',
          dose: '10-15 L/min via non-rebreather mask. Target SpO2 >94% (88-92% if COPD)',
          route: 'Inhalation',
          duration: 'Until resolution or stabilization; continue during observation',
          contraindications: ['None in acute setting', 'Use caution in severe COPD (target 88-92%)'],
          sideEffects: ['Oxygen toxicity (prolonged high FiO2)', 'CO2 retention in COPD'],
          alternatives: ['Lower flow if mild hypoxia; maintain adequate oxygenation']
        },
        {
          drug: 'Morphine',
          indication: '🔴 Analgesia - Pain relief; especially before chest tube insertion; reduces respiratory splinting',
          dose: 'Adults: 2.5-5 mg IV every 5-10 min titrated to pain. Elderly: 1-2 mg increments',
          route: 'IV',
          duration: 'Acute phase; as needed for pain',
          contraindications: ['Respiratory depression (use cautiously)', 'Hypotension', 'Altered mental status'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Nausea/vomiting', 'Sedation'],
          alternatives: ['Fentanyl 25-50 mcg IV', 'Tramadol 50-100mg IV if morphine unavailable']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 Mild Analgesia - Adjunct or for mild pain; safe in most patients',
          dose: 'Adults: 1g PO/IV every 6 hours. Max 4g/day (2g/day if liver disease)',
          route: 'PO or IV',
          duration: 'As needed for pain',
          contraindications: ['Severe hepatic impairment', 'Hypersensitivity'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Rare allergic reactions'],
          alternatives: ['NSAIDs (if no contraindications)']
        },
        {
          drug: 'Lidocaine 1% (Local Anesthetic)',
          indication: '🔴 Local Anesthesia - Essential for chest tube insertion; infiltrate skin, subcutaneous tissue, periosteum, pleura',
          dose: 'Adults: 10-20 mL of 1% lidocaine (max 3 mg/kg without epinephrine, 7 mg/kg with epinephrine)',
          route: 'Local infiltration',
          duration: 'Single administration for procedure',
          contraindications: ['Allergy to amide local anesthetics', 'Injection into infected tissue'],
          sideEffects: ['Local: Hematoma, infection', 'Systemic toxicity (if inadvertent IV injection): Tinnitus, circumoral numbness, seizures, arrhythmias'],
          alternatives: ['Bupivacaine 0.25-0.5% (longer acting)']
        },
        {
          drug: 'Tetracycline/Doxycycline (Pleurodesis)',
          indication: '🟡 Chemical Pleurodesis - Prevention of recurrence; instilled via chest tube; causes pleural inflammation and adhesion',
          dose: 'Doxycycline: 500 mg in 50-100 mL saline instilled intrapleurally, clamp tube for 2 hours',
          route: 'Intrapleural',
          duration: 'Single or repeated instillation',
          contraindications: ['Active pleural infection', 'Allergy to tetracyclines', 'Unexpanded lung'],
          sideEffects: ['Significant pain (pre-medicate with opioids)', 'Fever', 'Rarely: ARDS'],
          alternatives: ['Talc pleurodesis (more effective)', 'Bleomycin', 'Surgical pleurodesis']
        },
        {
          drug: 'Talc Slurry (Pleurodesis)',
          indication: '🟡 Chemical Pleurodesis - Most effective agent; for recurrent pneumothorax; also used for malignant effusions',
          dose: '4-5 g sterile talc in 50-100 mL saline instilled intrapleurally',
          route: 'Intrapleural (via chest tube or thoracoscopy)',
          duration: 'Single administration',
          contraindications: ['Unexpanded lung', 'Active infection', 'Young patients (long-term concerns)'],
          sideEffects: ['Pain (significant)', 'Fever', 'Rarely: ARDS, respiratory failure'],
          alternatives: ['Doxycycline', 'Bleomycin', 'Surgical pleurodesis']
        },
        {
          drug: 'Prophylactic Antibiotics (Chest Tube)',
          indication: '🟡 Consider in Trauma - Controversial; may reduce empyema risk in traumatic pneumothorax',
          dose: 'Cefazolin 1g IV single dose or short course',
          route: 'IV',
          duration: 'Single dose or 24 hours (not routinely recommended for spontaneous)',
          contraindications: ['Allergy to cephalosporins/penicillins'],
          sideEffects: ['Allergic reactions', 'Diarrhea', 'Clostridioides difficile infection'],
          alternatives: ['Not routinely indicated for spontaneous pneumothorax']
        },
        {
          drug: 'TB Treatment (if TB-related)',
          indication: '🔴 If TB Suspected/Confirmed - Standard 4-drug regimen for TB-related pneumothorax',
          dose: 'RHZE: Rifampicin 10mg/kg + Isoniazid 5mg/kg + Pyrazinamide 25mg/kg + Ethambutol 15mg/kg daily',
          route: 'PO',
          duration: '6 months (2 months intensive + 4 months continuation)',
          contraindications: ['See individual drug contraindications'],
          sideEffects: ['Hepatotoxicity', 'Peripheral neuropathy', 'Visual disturbance', 'Hyperuricemia'],
          alternatives: ['Alternative regimens if drug-resistant TB']
        },
      ],
      nonPharmacological: [
        'Observation: Small PSP (<2 cm) in minimally symptomatic patient; admit, high-flow O2, serial CXR; 50% resolve spontaneously in 10 days',
        'Needle Aspiration: Small to moderate PSP; 16-18G needle in 2nd ICS MCL; aspirate air until resistance or 2.5L removed; success 50-70% for PSP',
        'Chest Tube (Tube Thoracostomy): Large pneumothorax, SSP, tension pneumothorax, failed aspiration, traumatic; 4th-5th ICS anterior axillary line (safe triangle)',
        'Underwater Seal Drainage: Connect chest tube to underwater seal; -10 to -20 cmH2O suction if not resolving; monitor for air leak',
        'Heimlich Valve: One-way valve for ambulatory management; allows outpatient treatment of selected PSP; patient can be mobile',
        'Digital Chest Drainage Systems: Electronic monitoring of air leak; objective assessment of resolution',
        'Video-Assisted Thoracoscopic Surgery (VATS): For persistent air leak, recurrent pneumothorax, bilateral disease; bullectomy ± pleurodesis',
        'Thoracotomy: If VATS not available or failed; open bullectomy and pleurodesis; higher morbidity than VATS',
        'Chemical Pleurodesis: Via chest tube (talc, doxycycline) or thoracoscopy; prevents recurrence',
        'Mechanical Pleurodesis: Abrasion of pleural surfaces during surgery; creates adhesions',
      ],
      surgical: [
        'Needle Thoracostomy (Emergency Decompression): 14-16G needle, 2nd ICS MCL or 4th-5th ICS AAL; converts tension to simple pneumothorax; bridge to chest tube',
        'Tube Thoracostomy (Chest Tube Insertion): Definitive drainage; 4th-5th ICS, anterior axillary line (safe triangle); 16-24 Fr for spontaneous, 28-32 Fr for trauma/hemothorax',
        'Video-Assisted Thoracoscopic Surgery (VATS): Gold standard for recurrence prevention; bullectomy + mechanical/chemical pleurodesis; 95% success rate; 2-5% recurrence',
        'Open Thoracotomy with Pleurectomy: If VATS unavailable or failed; apical bleb resection + parietal pleurectomy; <1% recurrence but higher morbidity',
        'Thoracoscopic Bullectomy: Stapled resection of blebs/bullae; combined with pleurodesis for best outcomes',
        'Pleural Abrasion/Pleurectomy: Creates adhesions between visceral and parietal pleura; prevents recurrence',
        'Lung Volume Reduction Surgery: For emphysematous bullous disease with recurrent pneumothorax',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Tension pneumothorax → Immediate needle decompression then chest tube; do NOT transfer before decompression',
        '🔴 EMERGENCY - Bilateral pneumothorax → Urgent bilateral chest tubes; ICU admission',
        '🔴 EMERGENCY - Pneumothorax with hemodynamic instability → Resuscitation and chest tube',
        '🟠 URGENT - Secondary spontaneous pneumothorax → Always requires chest tube; often needs admission',
        '🟠 URGENT - Persistent air leak >5-7 days → Thoracic surgery consultation for VATS',
        '🟠 URGENT - Failed needle aspiration in PSP → Chest tube insertion',
        '🟠 URGENT - Pneumothorax in mechanically ventilated patient → Chest tube; consider tension',
        '🟡 ROUTINE - Recurrent pneumothorax (second episode) → Thoracic surgery for definitive management (VATS)',
        '🟡 ROUTINE - First episode PSP for counseling → Smoking cessation, activity restrictions, recurrence risk',
        '🟡 ROUTINE - Underlying lung disease identified → Pulmonology follow-up; TB treatment if indicated',
      ],
      monitoring: [
        'Vital Signs: Continuous in acute phase; watch for tension pneumothorax (hypotension, tachycardia)',
        'Pulse Oximetry: Continuous until stable; target SpO2 >94% (88-92% in COPD)',
        'Chest Tube Output: Monitor air leak (bubbling); drainage volume; document resolution of air leak',
        'Air Leak Assessment: Observe underwater seal during cough; persistent bubbling = ongoing leak',
        'Serial Chest X-rays: Post-procedure; daily while chest tube in place; before and after tube removal',
        'Respiratory Status: Work of breathing, breath sounds, respiratory rate',
        'Pain Assessment: Adequate analgesia improves respiratory effort and outcome',
        'Chest Tube Patency: Ensure not kinked or blocked; check connections',
        'Suction Pressure: -10 to -20 cmH2O if applied; not always necessary',
        'Lung Re-expansion: Clinical and radiological assessment',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

AFTER CONSERVATIVE MANAGEMENT (Observation/Aspiration):
• 24-48 hours: Repeat CXR to confirm resolution
• 1-2 weeks: Clinical review, CXR
• 6 weeks: Final CXR to confirm complete resolution
• Before air travel: Confirm complete resolution (usually wait 1-2 weeks after resolution)

AFTER CHEST TUBE REMOVAL:
• 24 hours: CXR post-removal to confirm no reaccumulation
• 1 week: Clinical review
• 6 weeks: Follow-up CXR

AFTER SURGICAL INTERVENTION (VATS/Thoracotomy):
• 1-2 weeks: Wound check, CXR
• 6 weeks: Clinical review, CXR
• 3-6 months: Final assessment; can consider return to flying/diving

LONG-TERM FOLLOW-UP:
• Recurrence risk counseling: 30% after first PSP, 50% after second
• Smoking cessation: Essential - reduces recurrence risk
• Activity restrictions:
  - No flying until complete resolution (minimum 1 week; 6 weeks if VATS)
  - No scuba diving (relative contraindication; discuss with specialist)
  - Contact sports: Wait until cleared
• Occupational considerations: Pilots, divers need specialist clearance

INDICATIONS FOR SURGICAL REFERRAL:
• Second ipsilateral pneumothorax
• First contralateral pneumothorax
• Bilateral pneumothorax
• Persistent air leak >5-7 days
• Hemopneumothorax
• High-risk occupation (pilots, divers)
• Patient preference after first episode

TB-RELATED PNEUMOTHORAX:
• Complete 6-month TB treatment
• Regular sputum monitoring
• CXR at end of treatment
• Watch for recurrence or residual lung disease`
    },

    redFlags: [
      '🔴 CRITICAL: Tension pneumothorax - Hypotension, tracheal deviation, absent breath sounds, JVD → IMMEDIATE needle decompression',
      '🔴 CRITICAL: Bilateral pneumothorax - Severe respiratory distress → Bilateral chest tubes urgently',
      '🔴 CRITICAL: Pneumothorax with cardiac arrest - PEA, asystole → Needle decompression both sides during CPR',
      '🔴 CRITICAL: Massive hemopneumothorax - Blood + air in pleural space → Large bore chest tube, possible thoracotomy',
      '🔴 CRITICAL: Deterioration on mechanical ventilation - Sudden hypoxia, high airway pressures → Suspect pneumothorax',
      '🟠 URGENT: Persistent air leak >5-7 days - Ongoing bubbling despite chest tube → Surgical consultation',
      '🟠 URGENT: Re-expansion pulmonary edema - Cough, frothy sputum after drainage → Supportive care, slow drainage next time',
      '🟠 URGENT: Subcutaneous emphysema spreading - Indicates ongoing air leak → Ensure chest tube patent and adequate',
      '🟠 URGENT: Recurrent pneumothorax - Second episode → Definitive surgical management indicated',
      '🟠 URGENT: SSP with minimal reserve - Severe COPD, small pneumothorax but very symptomatic → Early chest tube',
      '🟡 WARNING: Failure of lung to re-expand - Despite drainage → Consider endobronchial obstruction, trapped lung',
    ],

    complications: [
      'Tension Pneumothorax: Progressive air accumulation under pressure; mediastinal shift; cardiovascular collapse; fatal if untreated',
      'Recurrence: 30% after first PSP; 50% after second; higher in smokers and tall thin patients',
      'Persistent Air Leak: >5-7 days; indicates bronchopleural fistula; may need surgery',
      'Re-expansion Pulmonary Edema: Rapid re-expansion of collapsed lung; cough, hypoxia, pulmonary infiltrates; usually mild, rarely fatal',
      'Hemopneumothorax: Blood and air in pleural space; may need surgical intervention if >1500 mL or ongoing bleeding',
      'Empyema: Infection of pleural space; rare complication of chest tube; requires drainage and antibiotics',
      'Subcutaneous Emphysema: Air tracking into soft tissues; usually benign; extensive emphysema suggests inadequate drainage',
      'Chest Tube Complications: Malposition, kinking, blockage, infection, organ injury during insertion',
      'Chronic Pneumothorax: Trapped lung; may need decortication',
      'Death: Rare in PSP (0.1%); higher in SSP (1-3%) and tension pneumothorax (if untreated)',
    ],

    prognosis: `OVERALL PROGNOSIS:

PRIMARY SPONTANEOUS PNEUMOTHORAX (PSP):
• Mortality: <0.1% (very rare)
• Resolution: 50% of small pneumothorax resolve spontaneously in 10 days
• Recurrence after first episode: 25-30%
• Recurrence after second episode: 50-60%
• After surgical intervention (VATS): 2-5% recurrence

SECONDARY SPONTANEOUS PNEUMOTHORAX (SSP):
• Mortality: 1-3% (higher due to underlying disease)
• Higher morbidity due to limited respiratory reserve
• Recurrence: Similar to PSP if underlying disease persists
• Prognosis largely determined by underlying condition
• TB-related: Good if TB treated successfully
• COPD-related: Guarded; reflects severity of underlying disease

TRAUMATIC PNEUMOTHORAX:
• Prognosis depends on associated injuries
• Isolated pneumothorax: Excellent with appropriate drainage
• Hemopneumothorax: May need surgery; good if bleeding controlled

RISK FACTORS FOR RECURRENCE:
• Smoking (continuing to smoke)
• Tall, thin body habitus
• Young age
• Radiographic evidence of bullae/blebs
• No pleurodesis performed
• COPD or other underlying lung disease
• Previous pneumothorax

REDUCING RECURRENCE:
• Smoking cessation: Most important modifiable factor
• Surgical intervention after second episode: Reduces recurrence to <5%
• Pleurodesis: Chemical or mechanical
• Avoid triggering activities (diving, flying in unpressurized aircraft)

LONG-TERM OUTCOMES:
• Most patients recover fully
• May have mild reduction in lung function
• Activity restrictions usually temporary
• Career implications for pilots, divers, athletes

AFRICAN CONTEXT:
• Higher mortality due to delayed presentation
• Limited surgical capacity for definitive management
• TB-related pneumothorax: Prognosis linked to TB treatment success
• Recurrence may be managed conservatively if surgery unavailable`,

    prevention: `PRIMARY PREVENTION:

Smoking Cessation:
• Most important preventable risk factor
• Smoking increases PSP risk 20-fold in men, 10-fold in women
• Risk decreases after cessation
• Counseling and support for all patients

Avoiding Precipitants:
• Avoid extreme altitude changes in at-risk individuals
• Cautious with activities involving pressure changes
• Awareness for tall, thin individuals with family history

SECONDARY PREVENTION (Preventing Recurrence):

After First Episode:
• Smoking cessation (essential)
• Patient education on symptoms
• When to seek medical attention
• Activity restrictions during recovery

Indications for Surgical Prevention:
• Second ipsilateral pneumothorax
• First contralateral pneumothorax (bilateral history)
• Bilateral simultaneous pneumothorax
• Persistent air leak >5-7 days
• Profession requiring zero recurrence risk (pilots, divers)
• Tension pneumothorax at presentation
• Patient preference (discuss risks/benefits)

Surgical Options:
• VATS bullectomy + pleurodesis (gold standard)
• Thoracotomy with pleurectomy (if VATS unavailable)
• Recurrence rate: <5% after surgery

Medical Pleurodesis:
• Via chest tube if surgery not possible
• Talc or doxycycline
• Less effective than surgical pleurodesis

PREVENTING IATROGENIC PNEUMOTHORAX:

Central Line Insertion:
• Ultrasound guidance (reduces risk significantly)
• Subclavian approach has highest pneumothorax risk
• Post-procedure CXR

Thoracentesis:
• Ultrasound guidance
• Avoid during active coughing
• Small-bore needles

Mechanical Ventilation:
• Lung protective ventilation (low tidal volumes)
• Avoid high PEEP when possible
• Monitor airway pressures

Lung Biopsy:
• CT guidance
• Experienced operator
• Appropriate patient selection

ACTIVITY RESTRICTIONS:

Air Travel:
• Avoid until complete resolution (minimum 1-2 weeks after resolution)
• 6 weeks after surgical intervention
• Commercial aircraft pressurized to ~8000 ft equivalent

Scuba Diving:
• Relative contraindication after pneumothorax
• May be considered after bilateral surgical pleurodesis
• Specialist assessment required

Contact Sports:
• Avoid during acute phase and recovery
• Return after complete resolution and clearance

AFRICAN-SPECIFIC PREVENTION:

• Strengthen TB control programs (reduce SSP from TB)
• Improve road safety (reduce traumatic pneumothorax)
• Train healthcare workers in chest tube insertion
• Ensure availability of chest tubes and underwater seal systems
• Build capacity for VATS in regional centers`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING PNEUMOTHORAX (COLLAPSED LUNG) - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS A PNEUMOTHORAX?
A pneumothorax (also called a "collapsed lung") happens when air leaks into the space between your lung and chest wall. This air pushes on the outside of your lung and causes it to collapse partially or completely.

WHAT CAUSES PNEUMOTHORAX?
• Spontaneous: Happens without obvious cause, often in tall, thin young men
• Injury: Trauma to the chest from accidents, falls, or violence
• Lung disease: Conditions like tuberculosis (TB), COPD, or lung infections can weaken the lung
• Medical procedures: Sometimes occurs as a complication of certain medical procedures

WHAT ARE THE SYMPTOMS?
• Sudden sharp chest pain on one side
• Pain worse when breathing deeply
• Shortness of breath
• Feeling like you cannot get enough air
• Rapid heartbeat
• Dry cough

⚠️ SEEK EMERGENCY CARE IMMEDIATELY IF:
• Severe difficulty breathing
• Blue lips or fingertips
• Feeling faint or dizzy
• Chest pain with rapid heartbeat and sweating
• These could be signs of a serious complication called "tension pneumothorax"

HOW IS IT TREATED?

Small pneumothorax:
• You may just need observation in the hospital
• Oxygen helps the air get absorbed faster
• Repeat X-rays to make sure it is improving

Larger pneumothorax:
• A chest tube (drain) may be needed
• The tube allows the air to escape so your lung can re-expand
• The tube is connected to a drainage system
• Once the air leak seals and your lung expands, the tube is removed

Surgery:
• May be needed if the pneumothorax keeps coming back
• Done through small incisions (keyhole surgery/VATS)
• Helps prevent future episodes

CARING FOR YOUR CHEST TUBE (if you have one):
• Keep the drainage unit below chest level
• Do not pull or kink the tubing
• You can move around and sit up
• Tell the nurse if you have increased pain or breathing problems
• Tell the nurse if the drainage system is not bubbling or draining

AFTER YOUR PNEUMOTHORAX:

Recovery:
• Take pain medicines as prescribed
• Deep breathing exercises help your lung recover
• Gradually increase activity as directed
• Most people recover completely

Things to AVOID:
• Smoking - This is the most important! Smoking greatly increases the chance of another pneumothorax
• Flying - Wait until your doctor confirms the pneumothorax has completely healed (usually at least 1-2 weeks after healing)
• Scuba diving - Generally not recommended after pneumothorax; discuss with your doctor
• Strenuous activity - Until cleared by your doctor
• Playing wind instruments - Check with your doctor

PREVENTING ANOTHER PNEUMOTHORAX:

STOP SMOKING - This is the single most important thing you can do!
• Continuing to smoke greatly increases your risk of another pneumothorax
• Your doctor can help you quit

Know the warning signs:
• If you experience sudden chest pain or shortness of breath, seek medical attention immediately
• Do not ignore symptoms hoping they will go away

WHEN TO CALL YOUR DOCTOR:
• Increasing pain or shortness of breath
• Fever
• Signs of infection at the tube site (if you have a drain)
• Any concerns about your recovery

WHEN TO GO TO THE EMERGENCY ROOM:
• Sudden severe chest pain
• Severe difficulty breathing
• Blue color around lips
• Feeling faint

RISK OF RECURRENCE:
• About 30% of people have another pneumothorax after the first one
• Risk increases if you continue smoking
• Surgery can reduce this risk to less than 5%
• If you have a second pneumothorax, surgery is usually recommended

IMPORTANT FOR TB PATIENTS:
If your pneumothorax was caused by tuberculosis:
• Take all your TB medicines exactly as prescribed
• Complete the full 6 months of treatment
• Missing doses can cause treatment failure and drug resistance
• Your doctor will monitor you for healing

REMEMBER:
• Most people recover completely from pneumothorax
• STOP SMOKING - this is crucial
• Know the warning signs and seek help quickly if symptoms return
• Keep all your follow-up appointments
• Follow activity restrictions until cleared by your doctor`,

    references: [
      'MacDuff A, et al. BTS Pleural Disease Guideline 2010: Management of spontaneous pneumothorax. Thorax. 2010;65(Suppl 2):ii18-ii31. - BTS guidelines',
      'Baumann MH, et al. Management of spontaneous pneumothorax: an American College of Chest Physicians Delphi consensus statement. Chest. 2001;119:590-602. - ACCP consensus',
      'Tschopp JM, et al. ERS task force statement: diagnosis and treatment of primary spontaneous pneumothorax. Eur Respir J. 2015;46:321-335. - ERS guidelines',
      'Roberts DJ, et al. Clinical presentation of patients with tension pneumothorax: a systematic review. Ann Surg. 2015;261:1068-1078. - Tension pneumothorax',
      'Hallifax RJ, et al. Pneumothorax. BMJ. 2017;359:j5054. - Comprehensive review',
      'Brown SGA, et al. Conservative versus interventional treatment for spontaneous pneumothorax. N Engl J Med. 2020;382:405-415. - PSP trial',
      'Maskell NA. Pneumothorax in Africa. Afr J Respir Med. 2012;8:5-8. - African perspective',
      'Tuberculosis-associated pneumothorax: Management in resource-limited settings. Int J Tuberc Lung Dis. 2019;23:867-874. - TB-related',
      'Onuki T, et al. Primary and secondary spontaneous pneumothorax: prevalence, clinical features, and in-hospital mortality. Can Respir J. 2017;2017:6014967. - Epidemiology',
      'Sawada S, et al. Catamenial pneumothorax: report of a case and review. Gen Thorac Cardiovasc Surg. 2017;65:108-111. - Special populations',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 36. PLEURAL EFFUSION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '36',
    name: 'Pleural Effusion',
    slug: 'pleural-effusion',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'J90',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Pleural effusion is the abnormal accumulation of fluid in the pleural space between the visceral and parietal pleura. It can be classified as transudative (due to systemic factors altering hydrostatic/oncotic pressure) or exudative (due to local inflammatory or malignant processes affecting the pleura). The distinction is made using Light's criteria and guides further investigation and management.

In developed countries, heart failure, malignancy, and pneumonia are the most common causes. However, in Africa, tuberculosis (TB) is the leading cause of pleural effusion, accounting for 30-80% of cases in endemic regions. HIV co-infection dramatically increases the incidence of TB pleural effusion. Other important causes in Africa include parapneumonic effusion, malignancy (especially Kaposi sarcoma in HIV), and heart failure.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• Light's Criteria: Exudate if ANY of: Pleural protein/serum protein >0.5, Pleural LDH/serum LDH >0.6, Pleural LDH >2/3 upper limit of serum normal
• In Africa, ALWAYS consider TB - Adenosine deaminase (ADA) >40 U/L strongly suggests TB effusion
• Thoracentesis is both diagnostic and therapeutic - Perform in all undiagnosed effusions unless obvious cause
• Parapneumonic effusion: Simple (responds to antibiotics) vs Complicated/Empyema (needs drainage)
• Empyema criteria: pH <7.2, glucose <40 mg/dL, positive Gram stain/culture, frank pus
• Malignant effusion: Cytology positive in 60% first tap, 75% second tap; consider thoracoscopy if negative
• Transudative effusions: Treat underlying cause (heart failure, cirrhosis, nephrotic syndrome)
• Large effusions causing respiratory compromise: Therapeutic thoracentesis (drain max 1-1.5 L at once)
• Indwelling pleural catheter or pleurodesis for recurrent malignant effusion
• Ultrasound guidance increases thoracentesis success and reduces complications`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 1.5 million new cases annually in the US alone
• Prevalence increases with age
• Male:Female ratio varies by etiology

CAUSES BY FREQUENCY (Developed Countries):
1. Heart failure (most common transudate)
2. Pneumonia/Parapneumonic (most common exudate)
3. Malignancy
4. Pulmonary embolism
5. Viral pleuritis
6. Coronary artery bypass surgery
7. Cirrhosis with ascites

AFRICAN EPIDEMIOLOGY:
• TB is the LEADING cause (30-80% of pleural effusions)
• HIV dramatically increases TB pleural effusion incidence
• TB pleural effusion often occurs early after primary infection
• Parapneumonic effusion/empyema: Common, often bacterial
• Malignancy: Increasing; Kaposi sarcoma significant in HIV
• Heart failure: Important cause, especially in urban areas with hypertension/RHD

CAUSES BY FREQUENCY (Africa):
1. Tuberculosis (30-80%)
2. Parapneumonic/Empyema (15-25%)
3. Malignancy (5-15%)
4. Heart failure (5-10%)
5. HIV-associated (Kaposi sarcoma, lymphoma)
6. Other (cirrhosis, renal failure, etc.)

TB PLEURAL EFFUSION:
• Represents 4-5% of all TB cases globally
• In Africa: Up to 30% of pulmonary TB has pleural involvement
• Peak age: Young adults (20-40 years)
• Usually unilateral
• HIV co-infection: More common, may be bilateral, atypical
• Paucibacillary: Smear and culture often negative
• ADA and lymphocyte predominance are key diagnostic markers

CLASSIFICATION:

TRANSUDATIVE (Systemic, non-inflammatory):
• Heart failure (left-sided or biventricular)
• Hepatic hydrothorax (cirrhosis)
• Nephrotic syndrome
• Hypoalbuminemia
• Peritoneal dialysis
• Constrictive pericarditis
• Myxedema
• Pulmonary embolism (can be either)

EXUDATIVE (Local, inflammatory/malignant):
• Infections: TB, bacterial pneumonia, viral, fungal, parasitic
• Malignancy: Lung cancer, breast cancer, lymphoma, mesothelioma
• Pulmonary embolism (20-30% are exudates)
• Autoimmune: Rheumatoid arthritis, SLE, drug-induced lupus
• Gastrointestinal: Pancreatitis, esophageal rupture, subphrenic abscess
• Post-surgical: CABG, thoracic surgery
• Other: Asbestos exposure, Dressler syndrome, chylothorax`,

    clinicalPresentation: `SYMPTOMS:

Common Symptoms:
• Dyspnea (most common symptom):
  - Progressive, worsening with exertion
  - Depends on effusion size and underlying lung function
  - May be orthopnea if large
• Pleuritic chest pain:
  - Sharp, localized
  - Worse with inspiration and coughing
  - Often absent in large effusions (pleural surfaces separated)
• Cough (usually dry)
• Reduced exercise tolerance

Symptoms of Underlying Cause:
• TB: Fever, night sweats, weight loss, cough (may have hemoptysis)
• Pneumonia: Fever, productive cough, rigors
• Heart failure: Orthopnea, PND, peripheral edema
• Malignancy: Weight loss, anorexia, known cancer history
• Pulmonary embolism: Sudden dyspnea, leg swelling, risk factors

PHYSICAL EXAMINATION:

General:
• Tachypnea
• Use of accessory muscles (large effusions)
• Fever (infectious/inflammatory causes)
• Cachexia (malignancy, TB)
• Lymphadenopathy (malignancy, TB)

Chest Examination:
• Inspection: Reduced chest expansion on affected side; may see fullness
• Palpation: Decreased tactile fremitus; tracheal deviation away (large effusion)
• Percussion: Stony dull (pathognomonic); dullness shifts with position change
• Auscultation:
  - Decreased or absent breath sounds over effusion
  - Bronchial breathing at upper border (compressed lung)
  - Decreased vocal resonance
  - Pleural friction rub (small effusions, pleuritis)
  - Egophony ("E to A" change) at upper border

Signs of Specific Causes:
• Heart failure: Elevated JVP, peripheral edema, S3 gallop, bilateral effusions
• Cirrhosis: Jaundice, spider angiomata, ascites, right-sided effusion common
• Rheumatoid arthritis: Joint deformities, nodules
• Malignancy: Clubbing, lymphadenopathy, hepatomegaly

EFFUSION SIZE ESTIMATION:

Small: <300 mL (may be asymptomatic; blunted costophrenic angle on CXR)
Moderate: 300-1000 mL (half hemithorax opacity)
Large: >1000 mL (complete hemithorax opacity; mediastinal shift)

SPECIAL PRESENTATIONS:

Empyema:
• Fever, rigors
• Chest wall tenderness
• Systemically unwell
• May point externally (empyema necessitatis)

Hepatic Hydrothorax:
• Usually right-sided (70%)
• May be without ascites
• Can be massive
• May cause hepato-hydrothorax syndrome (respiratory failure)

Chylothorax:
• Milky white fluid
• Post-surgical or malignancy
• High triglycerides (>110 mg/dL)`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Confirm presence of effusion (CXR, ultrasound)
Step 2: Diagnostic thoracentesis (unless obvious cause like CHF)
Step 3: Classify as transudate or exudate (Light's criteria)
Step 4: If exudate - determine specific cause
Step 5: Additional investigations based on clinical context

CHEST X-RAY FINDINGS:

PA Erect:
• Blunting of costophrenic angle (earliest sign; ~200 mL)
• Meniscus sign (curved upper border)
• Homogeneous opacity
• Mediastinal shift (large effusion)

Lateral Decubitus:
• Most sensitive for small effusions
• >10 mm layering suggests drainable effusion
• Helps identify loculation

Supine (ICU patients):
• Hazy opacity over hemithorax
• Loss of hemidiaphragm silhouette
• Blunting of costophrenic angle

ULTRASOUND:

Advantages:
• More sensitive than CXR (detects as little as 20 mL)
• Identifies loculations and septations
• Guides thoracentesis (reduces complications)
• Distinguishes effusion from consolidation

Findings:
• Anechoic (simple effusion) or echogenic (complex/exudative)
• Septations suggest complicated effusion
• Pleural thickening
• Underlying lung atelectasis

CT CHEST:

Indications:
• Suspected malignancy
• Loculated effusion
• Associated parenchymal disease
• Pre-procedural planning

Findings:
• Accurate quantification
• Pleural thickening (malignancy, TB)
• Nodularity (malignancy)
• Underlying lung pathology
• Mediastinal lymphadenopathy

THORACENTESIS AND FLUID ANALYSIS:

When to Perform:
• All undiagnosed pleural effusions
• May defer in obvious bilateral CHF if responding to diuretics
• New effusion in known CHF not responding to treatment
• Suspected infection (parapneumonic)

Light's Criteria (EXUDATE if ANY present):
1. Pleural protein / Serum protein > 0.5
2. Pleural LDH / Serum LDH > 0.6
3. Pleural LDH > 2/3 upper limit of serum normal

ROUTINE PLEURAL FLUID TESTS:

| Test | Interpretation |
|------|----------------|
| Appearance | Clear (transudate), Cloudy/turbid (exudate), Purulent (empyema), Bloody (malignancy, trauma, PE), Milky (chylothorax) |
| Protein | Exudate >3 g/dL |
| LDH | Exudate: elevated; very high in empyema |
| Glucose | Low in TB, RA, empyema, malignancy |
| pH | <7.2 suggests complicated parapneumonic/empyema |
| Cell count | Lymphocyte predominant: TB, malignancy. Neutrophil predominant: acute infection, PE |
| Gram stain/Culture | Identify organism in empyema |
| Cytology | Malignant cells (60-75% sensitivity) |
| ADA | >40 U/L: suggests TB (sensitivity 90%, specificity 89%) |

ADDITIONAL TESTS BASED ON CLINICAL CONTEXT:

TB Suspected:
• ADA >40 U/L (highly suggestive; >70 U/L near-diagnostic)
• Lymphocyte predominance (>50-80%)
• Low glucose (<60 mg/dL)
• High protein (>5 g/dL)
• Pleural biopsy: Granulomas (80% sensitivity)
• TB PCR (GeneXpert): Sensitivity 60-80%
• Culture: Low yield but specific

Malignancy Suspected:
• Cytology (repeat if first negative)
• Tumor markers (CEA, CA-125, CA 15-3)
• Thoracoscopy with pleural biopsy (gold standard)
• Flow cytometry if lymphoma suspected

Parapneumonic/Empyema:
• pH <7.2: Needs drainage
• Glucose <40 mg/dL: Needs drainage
• LDH >1000 IU/L: Suggests empyema
• Positive Gram stain or culture: Needs drainage

Rheumatoid Effusion:
• Very low glucose (<20 mg/dL)
• Low pH
• High LDH
• Low complement
• RF may be positive

Chylothorax:
• Milky appearance
• Triglycerides >110 mg/dL (diagnostic)
• Chylomicrons present`,

    differentialDiagnosis: [
      'Pneumonia with consolidation - Fever, cough, consolidation on imaging; may coexist with parapneumonic effusion',
      'Lung collapse/Atelectasis - Similar dullness; no fluid on ultrasound; bronchoscopy may show obstruction',
      'Elevated hemidiaphragm - Phrenic nerve palsy, subphrenic abscess; sniff test; different imaging appearance',
      'Pleural thickening - Chronic; no fluid on ultrasound; calcification suggests asbestos exposure or old TB',
      'Lung mass/Tumor - Mass effect; may cause effusion; CT defines anatomy',
      'Mesothelioma - Occupational history (asbestos); pleural thickening; diagnosis by biopsy',
      'Diaphragmatic hernia - Bowel in thorax; trauma history or congenital; CT diagnostic',
      'Chest wall mass - External to pleura; CT defines location',
      'Empyema necessitatis - Empyema draining through chest wall; tender swelling; imaging shows pleural collection with chest wall extension',
    ],

    investigations: [
      {
        name: 'Chest X-ray (PA and Lateral)',
        indication: '🔴 IMMEDIATE - First-line imaging; confirm effusion; assess size; identify associated pathology',
        availability: 'BASIC'
      },
      {
        name: 'Chest Ultrasound',
        indication: '🔴 IMMEDIATE - More sensitive than CXR; guide thoracentesis; identify loculations; distinguish from consolidation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Diagnostic Thoracentesis',
        indication: '🔴 IMMEDIATE - All undiagnosed effusions; obtain fluid for analysis; therapeutic if symptomatic',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid Protein and LDH',
        indication: '🔴 IMMEDIATE - Light criteria to classify transudate vs exudate; send with serum protein and LDH',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid Cell Count and Differential',
        indication: '🔴 IMMEDIATE - Lymphocyte predominant (TB, malignancy); Neutrophil predominant (acute bacterial, PE)',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid Glucose',
        indication: '🟠 URGENT - Low in empyema (<40), TB, RA, malignancy',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid pH',
        indication: '🟠 URGENT - pH <7.2 in parapneumonic effusion indicates need for drainage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pleural Fluid Gram Stain and Culture',
        indication: '🔴 IMMEDIATE - If infection suspected; identifies organism in empyema',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid Adenosine Deaminase (ADA)',
        indication: '🔴 IMMEDIATE - In Africa, essential test; ADA >40 U/L strongly suggests TB (>70 near-diagnostic)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pleural Fluid Cytology',
        indication: '🟠 URGENT - If malignancy suspected; 60% first tap, 75% second tap sensitivity',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pleural Fluid AFB Smear and Culture',
        indication: '🟠 URGENT - Low yield (10-20%) but specific for TB; send if TB suspected',
        availability: 'BASIC'
      },
      {
        name: 'Pleural Fluid TB PCR (GeneXpert)',
        indication: '🟠 URGENT - Rapid TB diagnosis; sensitivity 60-80% in pleural fluid',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Chest',
        indication: '🟠 URGENT - Suspected malignancy; loculated effusion; underlying lung pathology; pre-biopsy planning',
        availability: 'ADVANCED'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT - All patients with pleural effusion in endemic areas; HIV increases TB and KS risk',
        availability: 'BASIC'
      },
      {
        name: 'BNP/NT-proBNP',
        indication: '🟠 URGENT - If heart failure suspected; helps differentiate cardiac from non-cardiac cause',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Albumin',
        indication: '🟡 Routine - Hypoalbuminemia causes transudative effusion; liver disease, nephrotic syndrome',
        availability: 'BASIC'
      },
      {
        name: 'Thoracoscopy (Medical or VATS)',
        indication: '🟡 Routine - Undiagnosed exudative effusion after thoracentesis; suspected malignancy with negative cytology; pleural biopsy',
        availability: 'ADVANCED'
      },
      {
        name: 'Pleural Biopsy (Blind or Image-Guided)',
        indication: '🟡 Routine - TB or malignancy suspected with non-diagnostic thoracentesis; granulomas in TB (80% yield)',
        availability: 'INTERMEDIATE'
      },
    ],

    management: {
      immediate: [
        '⚡ Assess respiratory status - Large effusions may cause significant dyspnea; position patient sitting up',
        '⚡ Oxygen therapy if hypoxic (target SpO2 >94%; 88-92% if COPD)',
        '⚡ Chest X-ray to confirm and assess size',
        '⚡ Diagnostic thoracentesis for undiagnosed effusions',
        '⚡ Therapeutic thoracentesis if symptomatic (drain max 1-1.5 L at once to avoid re-expansion pulmonary edema)',
        '⚡ Send pleural fluid for: Protein, LDH, glucose, pH, cell count, Gram stain, culture, ADA, cytology',
        '⚡ Apply Light criteria to classify as transudate or exudate',
        '⚡ In Africa: HIGH suspicion for TB - obtain ADA on all exudative effusions',
        '⚡ If parapneumonic: Assess for empyema (pH <7.2, glucose <40, pus, positive culture)',
        '⚡ Treat underlying cause - Heart failure, infection, malignancy',
        '⚡ Urgent chest tube if empyema confirmed',
        '⚡ HIV test in endemic areas',
      ],
      pharmacological: [
        {
          drug: 'Furosemide',
          indication: '🔴 Transudative Effusion (Heart Failure) - Diuresis to reduce effusion; treat underlying CHF',
          dose: 'Adults: 40-80 mg IV initially; may repeat or infuse. Oral: 40-160 mg daily. Titrate to response',
          route: 'IV or PO',
          duration: 'Ongoing for heart failure management',
          contraindications: ['Anuria', 'Severe hypovolemia', 'Hepatic coma', 'Severe electrolyte depletion'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Hypotension', 'Ototoxicity', 'Hyperuricemia'],
          alternatives: ['Bumetanide', 'Torsemide', 'Add thiazide for diuretic resistance']
        },
        {
          drug: 'TB Treatment (RHZE)',
          indication: '🔴 TB Pleural Effusion - Standard 4-drug regimen; most effusions resolve without drainage',
          dose: 'Rifampicin 10mg/kg + Isoniazid 5mg/kg + Pyrazinamide 25mg/kg + Ethambutol 15mg/kg daily',
          route: 'PO',
          duration: '6 months (2 months intensive + 4 months continuation phase)',
          contraindications: ['See individual drug contraindications; adjust for hepatic impairment'],
          sideEffects: ['Hepatotoxicity', 'Peripheral neuropathy', 'Visual disturbance', 'Hyperuricemia'],
          alternatives: ['Drug-resistant TB regimens if indicated']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 Parapneumonic Effusion/Empyema - Empiric antibiotic for community-acquired; covers common organisms',
          dose: 'Adults: 1-2 g IV daily. Children: 50-75 mg/kg/day',
          route: 'IV',
          duration: '10-14 days for uncomplicated; 3-6 weeks for empyema',
          contraindications: ['Cephalosporin allergy', 'Neonates with hyperbilirubinemia'],
          sideEffects: ['Allergic reactions', 'Diarrhea', 'Biliary sludge', 'C. difficile infection'],
          alternatives: ['Amoxicillin-clavulanate', 'Ampicillin-sulbactam']
        },
        {
          drug: 'Metronidazole',
          indication: '🟠 Empyema - Add for anaerobic coverage; often polymicrobial including anaerobes',
          dose: 'Adults: 500 mg IV/PO every 8 hours. Children: 7.5 mg/kg every 8 hours',
          route: 'IV or PO',
          duration: 'Duration of antibiotic course (2-6 weeks for empyema)',
          contraindications: ['First trimester pregnancy', 'Disulfiram use', 'Alcohol consumption'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol', 'Peripheral neuropathy'],
          alternatives: ['Clindamycin for anaerobic coverage']
        },
        {
          drug: 'Vancomycin',
          indication: '🟠 Hospital-Acquired Empyema - MRSA coverage; hospital-acquired or recent healthcare exposure',
          dose: 'Adults: 15-20 mg/kg IV every 8-12 hours (adjust for renal function). Target trough 15-20 mg/L',
          route: 'IV',
          duration: 'Based on clinical response; typically 2-4 weeks',
          contraindications: ['Hypersensitivity to vancomycin'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity', 'Red man syndrome (infusion-related)', 'Thrombocytopenia'],
          alternatives: ['Linezolid', 'Daptomycin']
        },
        {
          drug: 'Prednisolone (TB Pleural Effusion)',
          indication: '🟡 Consider in TB Effusion - May accelerate fluid resolution and symptom improvement; ONLY with anti-TB therapy',
          dose: 'Adults: 0.75-1 mg/kg/day for 2-4 weeks, then taper over 4-8 weeks',
          route: 'PO',
          duration: '6-8 weeks total',
          contraindications: ['Not started on TB treatment', 'Uncontrolled HIV'],
          sideEffects: ['Immunosuppression', 'Hyperglycemia', 'Weight gain', 'Osteoporosis'],
          alternatives: ['TB treatment alone (effusion usually resolves); steroids optional and not mandatory']
        },
        {
          drug: 'Talc Slurry (Pleurodesis)',
          indication: '🟡 Recurrent Malignant Effusion - Chemical pleurodesis; prevents reaccumulation',
          dose: '4-5 g sterile talc in 50-100 mL saline instilled via chest tube',
          route: 'Intrapleural',
          duration: 'Single administration; may repeat if unsuccessful',
          contraindications: ['Trapped lung (lung will not re-expand)', 'Active infection', 'Very short life expectancy'],
          sideEffects: ['Significant pain (pre-medicate)', 'Fever', 'Rarely ARDS', 'Pleurodesis failure'],
          alternatives: ['Doxycycline pleurodesis', 'Indwelling pleural catheter', 'Bleomycin']
        },
        {
          drug: 'Alteplase (tPA) - Intrapleural',
          indication: '🟠 Complicated Parapneumonic/Empyema - Fibrinolytic for loculated effusion; breaks down septations',
          dose: '10 mg in 30 mL saline intrapleurally, dwell 1-2 hours, then drain. May combine with DNase',
          route: 'Intrapleural (via chest tube)',
          duration: 'Daily for 3 days typically (can be repeated)',
          contraindications: ['Active bleeding', 'Recent surgery', 'Bronchopleural fistula'],
          sideEffects: ['Bleeding', 'Fever', 'Allergic reactions', 'Chest pain'],
          alternatives: ['Urokinase', 'Streptokinase (less common)', 'VATS decortication']
        },
        {
          drug: 'DNase (Dornase alfa) - Intrapleural',
          indication: '🟠 Complicated Parapneumonic/Empyema - Combined with tPA; reduces viscosity of empyema',
          dose: '5 mg in 30 mL saline intrapleurally; often combined with tPA (MIST2 protocol)',
          route: 'Intrapleural',
          duration: 'Twice daily for 3 days with tPA',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Fever', 'Chest discomfort'],
          alternatives: ['tPA alone (less effective than combination)']
        },
        {
          drug: 'Albumin (for Hepatic Hydrothorax)',
          indication: '🟡 Hepatic Hydrothorax - Volume expansion with diuresis; improves oncotic pressure',
          dose: '25% albumin 50-100 mL IV with diuretics',
          route: 'IV',
          duration: 'As needed during diuresis',
          contraindications: ['Severe pulmonary edema'],
          sideEffects: ['Volume overload', 'Allergic reactions', 'Expensive'],
          alternatives: ['Diuretics alone if responds']
        },
      ],
      nonPharmacological: [
        'Observation: Small effusions (<10 mm on decubitus or asymptomatic transudates) may be observed while treating underlying cause',
        'Therapeutic Thoracentesis: Large symptomatic effusions; removes up to 1-1.5 L at a time; relief of dyspnea; can repeat as needed',
        'Chest Tube (Intercostal Drain): Empyema, complicated parapneumonic, hemothorax; continuous drainage until resolved',
        'Small-Bore Chest Tube (Pigtail Catheter): Less painful than large bore; effective for most non-loculated effusions; 10-14 Fr',
        'Indwelling Pleural Catheter (IPC): Recurrent malignant effusion; outpatient management; patient/family can drain at home',
        'Image-Guided Drainage: Ultrasound or CT-guided for loculated collections; improves success rate',
        'Intrapleural Fibrinolytics: tPA + DNase (MIST2 protocol) for loculated/organized empyema; reduces need for surgery',
        'Video-Assisted Thoracoscopic Surgery (VATS): Empyema drainage/decortication; diagnosis (pleural biopsy); pleurodesis',
        'Thoracotomy with Decortication: For organized empyema when VATS fails; removes fibrous peel from lung',
        'Pleuroperitoneal Shunt: Refractory effusion when pleurodesis fails; allows fluid to drain into abdomen',
        'Dietary: Low-sodium diet for CHF and cirrhosis-related effusions',
      ],
      surgical: [
        'Video-Assisted Thoracoscopic Surgery (VATS): Most versatile; drainage of empyema; decortication; pleural biopsy; talc pleurodesis; diagnosis of undiagnosed effusion',
        'VATS Decortication: Removal of fibrous peel in organized empyema; allows lung re-expansion; earlier intervention = better outcomes',
        'Open Thoracotomy with Decortication: Reserved for VATS failure or very organized empyema; higher morbidity but effective',
        'Pleurectomy: Removal of parietal pleura; for mesothelioma or recurrent pneumothorax; combined with pleurodesis',
        'Thoracoscopic Talc Poudrage: Insufflation of talc during thoracoscopy; most effective pleurodesis method (>90% success)',
        'Pleuroperitoneal Shunt: For refractory effusions; contraindicated if malignant ascites or infection',
        'Thoracic Duct Ligation: For chylothorax not responding to conservative management; VATS or open approach',
        'Indwelling Pleural Catheter Insertion: Tunneled catheter for long-term ambulatory drainage; malignant or refractory effusions',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Massive effusion with respiratory failure → Urgent therapeutic thoracentesis or chest tube',
        '🔴 EMERGENCY - Empyema (pus on aspiration) → Chest tube drainage; consider thoracic surgery if loculated',
        '🟠 URGENT - Complicated parapneumonic (pH <7.2, glucose <40) → Chest tube drainage; may need fibrinolytics',
        '🟠 URGENT - Suspected malignant effusion → Cytology, CT, thoracoscopy for diagnosis and staging',
        '🟠 URGENT - Hemothorax → Chest tube; thoracotomy if >1500 mL initial or ongoing bleeding',
        '🟠 URGENT - Undiagnosed exudative effusion despite thoracentesis → Thoracoscopy and pleural biopsy',
        '🟡 ROUTINE - Recurrent malignant effusion → Indwelling pleural catheter or pleurodesis',
        '🟡 ROUTINE - Hepatic hydrothorax refractory to diuretics → TIPS evaluation, transplant evaluation',
        '🟡 ROUTINE - Chylothorax → Dietary modification, TPN; surgery if persistent',
        '🟡 ROUTINE - TB pleural effusion → Complete TB treatment; usually resolves without drainage',
      ],
      monitoring: [
        'Respiratory Status: Dyspnea improvement after drainage; oxygen saturation',
        'Chest X-ray: Post-thoracentesis to assess fluid removal and exclude pneumothorax; serial imaging to monitor resolution',
        'Fluid Output: If chest tube in place - daily output; remove when <150-200 mL/day and lung expanded',
        'Vital Signs: Fever (infection), hypotension (large volume removal)',
        'Re-expansion Pulmonary Edema: Watch for cough, hypoxia after drainage; limit to 1-1.5 L per session',
        'Inflammatory Markers: CRP, WBC to monitor response to treatment (empyema, TB)',
        'Chest Tube: Patency, air leak, drain position',
        'Treatment Response: TB effusion should begin resolving in 2-4 weeks; empyema should improve with drainage and antibiotics',
        'Nutritional Status: Weight, albumin especially in TB and malignancy',
        'Pain Assessment: Adequate analgesia improves respiratory mechanics',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

TB PLEURAL EFFUSION:
• 2 weeks: Clinical response, repeat CXR; most effusions begin to decrease
• Monthly: During TB treatment; symptoms, CXR, treatment adherence
• 6 months: End of treatment; confirm resolution
• 12 months: Long-term follow-up; residual pleural thickening common but usually asymptomatic

PARAPNEUMONIC EFFUSION/EMPYEMA:
• Daily while admitted: Clinical status, drain output
• After drain removal: CXR at 24-48 hours
• 2-4 weeks: Clinical review, CXR to confirm resolution
• 6 weeks: Final assessment; CT if persistent abnormality

MALIGNANT EFFUSION:
• After intervention: Assess symptom relief, effusion recurrence
• Weekly-monthly: IPC drainage education and monitoring
• Ongoing: Palliative care integration; prognosis-based planning
• Median survival: 3-12 months depending on primary cancer

HEART FAILURE EFFUSION:
• Treat underlying CHF
• Follow up per heart failure guidelines
• Diuretic adjustment based on fluid status
• Repeat thoracentesis rarely needed if CHF well-controlled

CHYLOTHORAX:
• Dietary: Low-fat diet with MCT oil; if no improvement → TPN
• 2 weeks: Reassess; if persistent → consider thoracic duct embolization or ligation
• Surgery if >1 L/day for >2 weeks despite conservative treatment

HEPATIC HYDROTHORAX:
• Salt restriction, diuretics
• Avoid repeated thoracentesis (protein loss, infection risk)
• Consider TIPS for refractory cases
• Transplant evaluation

MONITORING FOR RECURRENCE:
• Malignant: High recurrence without pleurodesis; consider IPC or chemical pleurodesis
• TB: Rare recurrence with complete treatment
• Heart failure: Recurs if CHF not controlled
• Empyema: Watch for re-accumulation; may need decortication if trapped lung`
    },

    redFlags: [
      '🔴 CRITICAL: Massive effusion with respiratory failure - Complete hemithorax opacification, severe dyspnea, cyanosis → Urgent drainage',
      '🔴 CRITICAL: Empyema (frank pus on aspiration) - Requires chest tube drainage and IV antibiotics; may need surgery',
      '🔴 CRITICAL: Tension hydrothorax - Mediastinal shift, hypotension, tracheal deviation → Urgent decompression',
      '🔴 CRITICAL: Hemothorax - Blood on aspiration; >1500 mL initial or ongoing bleeding → Thoracic surgery consultation',
      '🟠 URGENT: Complicated parapneumonic - pH <7.2, glucose <40 mg/dL, loculations → Needs chest tube, possibly fibrinolytics',
      '🟠 URGENT: Rapidly reaccumulating effusion - May indicate malignancy, infection, or ongoing process → Investigate cause',
      '🟠 URGENT: Bilateral large effusions - Consider heart failure, malignancy, TB → Comprehensive evaluation',
      '🟠 URGENT: Effusion in immunocompromised patient - High risk of unusual infections → Broad evaluation including fungal',
      '🟠 URGENT: Undiagnosed exudative effusion - After thoracentesis non-diagnostic → Needs thoracoscopy/biopsy',
      '🟡 WARNING: Re-expansion pulmonary edema - After draining large effusion → Supportive care, limit future drainage volume',
      '🟡 WARNING: Trapped lung - Lung does not re-expand after drainage → May need decortication or IPC',
    ],

    complications: [
      'Empyema: Infected pleural space; 5-10% of parapneumonic effusions progress; requires drainage and prolonged antibiotics',
      'Trapped Lung: Visceral pleura encased by fibrous peel; lung cannot expand; may need decortication',
      'Fibrothorax: End-stage fibrosis; restricted lung expansion; may need decortication',
      'Pleural Thickening: Common after TB and empyema; usually asymptomatic; may cause restrictive deficit',
      'Re-expansion Pulmonary Edema: After rapid drainage of large effusion; 0.5-1% risk; limit drainage to 1-1.5 L',
      'Bronchopleural Fistula: Communication between bronchus and pleural space; persistent air leak; complex management',
      'Respiratory Failure: Large or bilateral effusions; especially in patients with underlying lung disease',
      'Malnutrition: Protein loss with repeated thoracentesis; especially hepatic hydrothorax',
      'Chronic Pain: Post-thoracotomy pain syndrome; more common with open surgery',
      'Procedure Complications: Pneumothorax, bleeding, infection from thoracentesis/chest tube',
    ],

    prognosis: `OVERALL PROGNOSIS:

Prognosis depends heavily on underlying cause:

TB PLEURAL EFFUSION:
• Excellent with treatment
• Most resolve within 6-12 weeks on TB treatment
• Rarely require drainage (unless loculated or causing significant symptoms)
• Pleural thickening common (up to 50%) but usually minimal functional impact
• Full recovery expected in most patients
• Without treatment: May progress to empyema or disseminated TB

PARAPNEUMONIC EFFUSION:
• Simple parapneumonic: Excellent with antibiotics alone
• Complicated/Empyema: 
  - Mortality 10-20% overall
  - Higher with delayed treatment, elderly, comorbidities
  - Early drainage improves outcomes
  - Most recover with appropriate treatment

MALIGNANT EFFUSION:
• Generally poor prognosis (reflects advanced malignancy)
• Median survival: 3-12 months (varies by cancer type)
• Lung cancer with effusion: Median 4-6 months
• Breast cancer with effusion: Better prognosis, median 7-15 months
• Mesothelioma: Median 8-14 months
• Pleurodesis provides symptom relief but doesn't affect survival

HEART FAILURE EFFUSION:
• Prognosis related to underlying cardiac disease
• Effusion usually resolves with CHF treatment
• Persistent effusion suggests refractory heart failure

HEPATIC HYDROTHORAX:
• Poor prognosis - reflects advanced liver disease
• Median survival 8-12 months without transplant
• TIPS may improve survival
• Liver transplant: Definitive treatment

CHYLOTHORAX:
• Prognosis depends on cause
• Traumatic/surgical: 50-70% resolve with conservative treatment
• Malignant: Poor, reflects advanced disease

AFRICAN CONTEXT:
• TB pleural effusion: Good outcomes if diagnosed and treated
• Delayed presentation worsens outcomes
• Limited surgical capacity affects empyema outcomes
• HIV co-infection increases complexity
• Malnutrition complicates recovery`,

    prevention: `PRIMARY PREVENTION:

TB Pleural Effusion:
• TB prevention strategies (BCG, IPT in HIV)
• Early detection and treatment of pulmonary TB
• HIV prevention and treatment (ART reduces TB risk)
• Infection control measures

Parapneumonic Effusion/Empyema:
• Early treatment of pneumonia
• Appropriate antibiotic selection
• Pneumococcal and influenza vaccination
• Smoking cessation

Heart Failure Effusion:
• Hypertension control
• Prevent coronary artery disease
• RHD secondary prophylaxis
• Adherence to CHF medications

SECONDARY PREVENTION:

Preventing Empyema Progression:
• Early recognition of complicated parapneumonic
• Timely drainage when indicated (pH <7.2)
• Adequate antibiotic duration
• Follow-up to ensure resolution

Preventing Recurrent Malignant Effusion:
• Pleurodesis after first drainage
• Indwelling pleural catheter
• Treatment of underlying malignancy

TERTIARY PREVENTION:

Preventing Complications:
• Limit drainage volume (1-1.5 L) to prevent re-expansion edema
• Ultrasound-guided thoracentesis to reduce complications
• Timely surgical intervention for empyema
• Adequate nutrition

AFRICAN-SPECIFIC PREVENTION:

• Strengthen TB control programs
• Improve access to diagnostic thoracentesis
• Train healthcare workers in pleural procedures
• Ensure availability of chest tubes and ADA testing
• Early HIV testing and ART initiation
• Improve road safety (reduce traumatic hemothorax)
• Build capacity for VATS in regional centers`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING PLEURAL EFFUSION - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS A PLEURAL EFFUSION?
A pleural effusion is a buildup of fluid between the layers of tissue (the pleura) that line the lungs and chest wall. Normally, there is only a small amount of fluid in this space that helps the lungs move smoothly during breathing. When too much fluid collects, it can make breathing difficult.

WHAT CAUSES PLEURAL EFFUSION?
Many conditions can cause fluid to build up around the lungs:
• Heart failure (the heart is not pumping well)
• Infections like tuberculosis (TB) or pneumonia
• Cancer (lung cancer, breast cancer, or cancer that has spread)
• Liver disease
• Kidney disease
• Blood clots in the lung
• Inflammation (lupus, rheumatoid arthritis)

In Africa, tuberculosis is one of the most common causes.

WHAT ARE THE SYMPTOMS?
• Shortness of breath (the main symptom)
• Sharp chest pain, especially when breathing deeply
• Dry cough
• Feeling tired
• Difficulty lying flat (need to sleep propped up)

Some people have no symptoms if the fluid buildup is small.

HOW IS IT DIAGNOSED?
• Chest X-ray: Shows fluid around the lung
• Ultrasound: Helps see exactly where the fluid is
• Thoracentesis: A needle is used to take a sample of the fluid for testing
  - This helps find the cause
  - It can also relieve symptoms by removing fluid
• Blood tests and other tests to find the underlying cause

HOW IS IT TREATED?

The treatment depends on the cause:

FOR TB PLEURAL EFFUSION:
• You will need TB medicines for 6 months
• The fluid usually goes away as the TB is treated
• Draining the fluid may or may not be needed
• Take all your medicines exactly as prescribed

FOR HEART FAILURE:
• Water pills (diuretics) to remove extra fluid from your body
• Low-salt diet
• Medicines for your heart
• The pleural fluid usually improves as heart failure is controlled

FOR INFECTION (PNEUMONIA):
• Antibiotics to treat the infection
• A tube may be needed to drain the fluid if it becomes infected (empyema)

FOR CANCER:
• Treatment of the underlying cancer
• Draining the fluid to relieve symptoms
• Procedures to prevent the fluid from coming back (pleurodesis)
• A permanent drain may be placed if fluid keeps coming back

IF YOU HAVE A CHEST TUBE (DRAIN):
• Keep the drainage container below your chest level
• Do not pull on the tube
• Tell the nurse if you have sudden pain or breathing problems
• You can sit up and move around carefully
• The tube will be removed when fluid drainage slows down

AFTER THORACENTESIS (FLUID REMOVAL):
• You may feel much better immediately as breathing becomes easier
• Some soreness at the needle site is normal
• Watch for signs of problems: Increased pain, fever, difficulty breathing, bleeding

⚠️ WHEN TO SEEK MEDICAL CARE:

See your doctor if:
• Breathing is getting worse
• You have fever or chills
• Chest pain is increasing
• You notice swelling in your legs or abdomen

Go to the hospital immediately if:
• Severe difficulty breathing
• Unable to catch your breath
• Blue color around lips or fingertips
• Chest pain with difficulty breathing

FOR TB PLEURAL EFFUSION PATIENTS:
• Take all your TB medicines every day for the full 6 months
• Do not stop taking medicines even if you feel better
• Missing doses can cause treatment failure
• Your doctor will monitor you with regular visits and X-rays
• The fluid usually starts to decrease within 2-4 weeks of treatment

LIFESTYLE RECOMMENDATIONS:
• If you have heart failure: Follow a low-salt diet
• If you smoke: Stop smoking
• Take all medicines as prescribed
• Keep all your follow-up appointments
• Stay as active as you can within your limits
• Get vaccinated (flu, pneumonia) as recommended

RECOVERY EXPECTATIONS:
• TB pleural effusion: Usually resolves completely with treatment (6 months)
• Heart failure: Improves with proper treatment of heart condition
• Infection: Usually resolves with antibiotics and drainage if needed
• Cancer: Treatment aims to control symptoms and prevent recurrence

REMEMBER:
• Pleural effusion is a symptom of an underlying condition
• Finding and treating the cause is the most important step
• Many causes are treatable with good outcomes
• Take all medicines as prescribed
• Keep all follow-up appointments
• Report any worsening symptoms promptly`,

    references: [
      'Light RW. Pleural Diseases. 6th ed. Philadelphia: Lippincott Williams & Wilkins; 2013. - Textbook reference',
      'Hooper C, et al. Investigation of a unilateral pleural effusion in adults: British Thoracic Society Pleural Disease Guideline 2010. Thorax. 2010;65(Suppl 2):ii4-ii17. - BTS guidelines',
      'Feller-Kopman D, Light R. Pleural Disease. N Engl J Med. 2018;378:740-751. - Comprehensive review',
      'Rahman NM, et al. Intrapleural use of tissue plasminogen activator and DNase in pleural infection (MIST2). N Engl J Med. 2011;365:518-526. - MIST2 trial',
      'Porcel JM, Light RW. Diagnostic approach to pleural effusion in adults. Am Fam Physician. 2006;73:1211-1220. - Diagnostic approach',
      'Valdés L, et al. Tuberculous pleurisy: a study of 254 patients. Arch Intern Med. 1998;158:2017-2021. - TB effusion',
      'Diacon AH, et al. Diagnostic tools in tuberculous pleurisy: a direct comparative study. Eur Respir J. 2003;22:589-591. - ADA in TB',
      'Shaw JA, et al. Pleural tuberculosis: A concise clinical review. Clin Respir J. 2018;12:1779-1786. - TB pleural effusion review',
      'Bedawi EO, et al. Pleural effusion: a comprehensive review. Ann Transl Med. 2021;9:661. - Contemporary review',
      'WHO. Systematic screening for active tuberculosis: principles and recommendations. Geneva: WHO; 2013. - TB screening',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 37. SICKLE CELL CRISIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '37',
    name: 'Sickle Cell Crisis',
    slug: 'sickle-cell-crisis',
    specialty: 'Hematology',
    bodySystem: 'Hematological',
    icdCode: 'D57.0',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Sickle cell disease (SCD) is an inherited hemoglobinopathy caused by a mutation in the β-globin gene, resulting in abnormal hemoglobin S (HbS). Under conditions of hypoxia, dehydration, acidosis, or infection, HbS polymerizes causing red blood cells to become rigid and sickle-shaped, leading to vaso-occlusion, hemolysis, and multi-organ damage. Sickle cell crisis refers to acute complications requiring urgent medical attention.

Africa bears approximately 75% of the global SCD burden, with an estimated 300,000 infants born with SCD annually, mostly in sub-Saharan Africa. Without early diagnosis and comprehensive care, up to 90% of affected children in Africa die before age 5. The sickle cell trait (HbAS) provides partial protection against severe malaria, explaining the high prevalence in malaria-endemic regions.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• PAIN CRISIS: Treat pain aggressively - Opioids are first-line; undertreatment is common
• ACUTE CHEST SYNDROME (ACS): Leading cause of death; fever + new pulmonary infiltrate + respiratory symptoms = ACS until proven otherwise
• ACS Management: Exchange transfusion + antibiotics + incentive spirometry + bronchodilators
• SPLENIC SEQUESTRATION: Acute splenic enlargement + falling Hb = EMERGENCY transfusion
• STROKE: 11% risk in children; transcranial Doppler screening; chronic transfusion program reduces risk by 90%
• INFECTION: Major cause of death in children; encapsulated organisms (Pneumococcus); penicillin prophylaxis essential
• HYDROXYUREA: Disease-modifying therapy; reduces crises by 50%; safe in children ≥9 months
• TRANSFUSION: Simple for Hb <5 g/dL; Exchange for ACS, stroke, multi-organ failure; Target HbS <30%
• APLASTIC CRISIS: Parvovirus B19; severe anemia with low reticulocytes; transfusion required
• Prevention: Hydration, avoid triggers (cold, hypoxia, dehydration), vaccinations, penicillin prophylaxis`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 300,000-400,000 infants born with SCD annually worldwide
• 20-25 million people living with SCD globally
• Highest prevalence in sub-Saharan Africa, Middle East, Mediterranean, India
• Life expectancy in high-income countries: 50-60 years
• Life expectancy in Africa without intervention: Often <20 years

AFRICAN EPIDEMIOLOGY:
• Africa: 75% of global SCD burden
• Nigeria: Largest absolute number (~150,000 births/year with SCD)
• Democratic Republic of Congo, Tanzania, Uganda: Very high burden
• Carrier rate (HbAS): 10-40% in many African populations
• Under-5 mortality: 50-90% without comprehensive care
• 50-80% of affected children die undiagnosed

SICKLE CELL DISTRIBUTION IN AFRICA:
• West Africa: Highest prevalence (Nigeria, Ghana, Senegal)
• Central Africa: Very high (DRC, Cameroon)
• East Africa: High (Tanzania, Uganda, Kenya)
• Southern Africa: Lower but significant
• "Malaria belt" corresponds to SCD distribution

GENETICS AND INHERITANCE:
• HbSS (Sickle cell anemia): Homozygous; most severe
• HbSC disease: Compound heterozygous; milder
• HbS/β-thalassemia: Variable severity
• HbAS (Sickle cell trait): Heterozygous carrier; usually asymptomatic

MALARIA PROTECTION:
• HbAS provides 90% protection against severe malaria
• Balanced polymorphism explains high prevalence in malaria-endemic areas
• HbSS patients at INCREASED risk of severe malaria

TYPES OF CRISIS:

1. Vaso-occlusive Crisis (VOC): Most common; painful
2. Acute Chest Syndrome (ACS): Pulmonary; life-threatening
3. Splenic Sequestration: Acute splenic enlargement; severe anemia
4. Aplastic Crisis: Parvovirus B19; reticulocytopenia
5. Hemolytic Crisis: Accelerated hemolysis; jaundice
6. Hepatic Sequestration: Rare; acute liver enlargement

TRIGGERS FOR CRISIS:
• Infection (most common trigger)
• Dehydration
• Cold exposure
• Hypoxia/High altitude
• Physical/emotional stress
• Menstruation
• Surgery/Anesthesia
• Alcohol
• Often no identifiable trigger`,

    clinicalPresentation: `VASO-OCCLUSIVE CRISIS (VOC) - MOST COMMON:

Pain Characteristics:
• Sudden onset, severe pain
• Common sites: Bones (long bones, spine, ribs), chest, abdomen, joints
• Bilateral and symmetric in children; more localized in adults
• Duration: Hours to days (typically 5-7 days)
• May have low-grade fever
• Dactylitis (hand-foot syndrome): First presentation in infants; swelling of hands/feet

Physical Findings:
• Tenderness over affected areas
• Swelling (dactylitis, joint effusion)
• May have fever
• Tachycardia
• Normal or mildly elevated Hb (no acute drop)

ACUTE CHEST SYNDROME (ACS) - LIFE-THREATENING:

Definition: New pulmonary infiltrate + ≥1 of:
• Fever (>38.5°C)
• Respiratory symptoms (cough, dyspnea, chest pain, hypoxia)
• Often develops 2-3 days into VOC admission

Symptoms:
• Chest pain (may be pleuritic)
• Dyspnea
• Cough (may be productive)
• Fever
• Hypoxia

Signs:
• Tachypnea
• Tachycardia
• Decreased oxygen saturation
• Decreased breath sounds
• Crackles
• Dullness to percussion

⚠️ ACS is the leading cause of death in adults with SCD

SPLENIC SEQUESTRATION - EMERGENCY:

Presentation:
• Acute splenic enlargement (>2 cm from baseline)
• Left upper quadrant pain
• Pallor, weakness
• Tachycardia
• Signs of hypovolemic shock
• Hb drop ≥2 g/dL from baseline (often <5 g/dL)

⚠️ Most common in children <5 years; can occur in adults with HbSC or HbS-βthal

APLASTIC CRISIS:

Cause: Parvovirus B19 infection
Presentation:
• Profound anemia (Hb often 2-4 g/dL)
• Fatigue, pallor, dyspnea
• Reticulocyte count very LOW (<1%)
• May have preceding viral symptoms
• Self-limiting (7-10 days) but may need transfusion

STROKE:

Prevalence: 11% in children with SCD
Presentation:
• Sudden weakness, hemiparesis
• Speech difficulties
• Facial droop
• Severe headache
• Altered consciousness
• Seizures

⚠️ Stroke is a medical emergency - requires immediate exchange transfusion

PRIAPISM:

Definition: Prolonged painful erection >4 hours (ischemic)
Classification:
• Stuttering: Recurrent short episodes (<3 hours)
• Major: Prolonged (>3 hours); requires urgent intervention

CHRONIC MANIFESTATIONS:

• Chronic hemolytic anemia (Hb typically 6-9 g/dL)
• Jaundice
• Leg ulcers (especially medial malleolus)
• Avascular necrosis (hip, shoulder)
• Chronic kidney disease
• Pulmonary hypertension
• Gallstones (pigment stones)
• Cardiomegaly
• Delayed puberty, growth retardation
• Cognitive impairment from silent strokes`,

    diagnosis: `DIAGNOSTIC APPROACH:

CONFIRMING SCD (if not previously diagnosed):

Hemoglobin Electrophoresis:
• Gold standard for diagnosis
• HbSS: Predominantly HbS (80-95%), HbF variable, absent HbA
• HbSC: HbS + HbC
• HbS-βthal: HbS + variable HbA

Sickle Solubility Test:
• Screening test; positive in HbS (trait or disease)
• Must confirm with electrophoresis
• False negatives in infants <6 months

Newborn Screening:
• Essential for early intervention
• Isoelectric focusing or HPLC
• Identifies affected infants and carriers

ASSESSING ACUTE CRISIS:

For VOC:
• CBC: Hb usually at baseline or slightly decreased; WBC may be elevated
• Reticulocyte count: Usually elevated (5-15%)
• Blood film: Sickle cells, target cells, Howell-Jolly bodies (asplenia)
• Infection workup: Blood cultures, urine culture, CXR

For ACS:
• Chest X-ray: New pulmonary infiltrate (may lag behind symptoms)
• Pulse oximetry/ABG: Hypoxemia
• CBC: May have falling Hb
• Blood cultures
• Sputum culture if productive cough

For Splenic Sequestration:
• CBC: Hb drop ≥2 g/dL (often <5 g/dL)
• Reticulocyte count: Elevated (unless concurrent aplastic crisis)
• Platelet count: May be decreased (sequestered)
• Ultrasound: Enlarged spleen

For Aplastic Crisis:
• CBC: Severe anemia
• Reticulocyte count: Very LOW (<1%) - KEY finding
• Parvovirus B19 serology: IgM positive
• Blood film: Absent reticulocytes

For Stroke:
• Urgent CT head: Rule out hemorrhage
• MRI/MRA: Define infarct territory; may be more sensitive
• Transcranial Doppler: Screen; elevated velocities predict stroke risk

ROUTINE MONITORING IN SCD:

| Test | Frequency | Purpose |
|------|-----------|---------|
| CBC, reticulocytes | Every visit | Baseline Hb, hemolysis |
| Comprehensive metabolic panel | Annually | Renal, liver function |
| Urinalysis | Annually | Proteinuria (nephropathy) |
| LDH, bilirubin | As needed | Hemolysis markers |
| Ferritin | If transfused | Iron overload |
| Transcranial Doppler | Annually 2-16 years | Stroke risk |
| Echocardiogram | As indicated | Pulmonary hypertension |
| Pulmonary function tests | As indicated | Pulmonary disease |

DIFFERENTIATING CRISIS TYPES:

| Feature | VOC | ACS | Splenic Seq | Aplastic |
|---------|-----|-----|-------------|----------|
| Pain | Severe, localized | Chest | LUQ | Minimal |
| Hb change | Minimal | May drop | Significant drop | Severe drop |
| Reticulocytes | Normal/high | Variable | High | Very LOW |
| Spleen | Not enlarged | Normal | ENLARGED | Normal |
| CXR | Normal | Infiltrate | Normal | Normal |`,

    differentialDiagnosis: [
      'Osteomyelitis - Localized bone pain, fever; may coexist with SCD; Salmonella and Staph aureus common; imaging and culture help differentiate',
      'Septic arthritis - Joint swelling, fever, refusal to move joint; arthrocentesis diagnostic',
      'Acute abdomen - Appendicitis, cholecystitis, bowel obstruction; SCD can mimic surgical abdomen; careful evaluation needed',
      'Pneumonia - Fever, cough, infiltrate; often triggers or accompanies ACS; treat both',
      'Pulmonary embolism - Chest pain, dyspnea; SCD increases PE risk; may be part of ACS spectrum',
      'Fat embolism - Post-bone infarction; causes ACS; part of SCD pathophysiology',
      'Meningitis - Fever, headache, altered mental status; SCD patients at high risk; LP if suspected',
      'Avascular necrosis - Chronic hip/shoulder pain; X-ray, MRI for diagnosis',
      'Pyelonephritis - Flank pain, fever, dysuria; urine analysis and culture',
      'Cholecystitis - RUQ pain, fever, Murphy sign; pigment gallstones common in SCD; ultrasound helpful',
      'Malaria - Fever, anemia, splenomegaly; endemic areas; always consider in African patients',
      'Hemolytic transfusion reaction - Post-transfusion; fever, dark urine, falling Hb; DAT may be negative in SCD',
    ],

    investigations: [
      {
        name: 'Complete Blood Count',
        indication: '🔴 IMMEDIATE - Baseline; assess severity of anemia; compare to patient baseline Hb',
        availability: 'BASIC'
      },
      {
        name: 'Reticulocyte Count',
        indication: '🔴 IMMEDIATE - KEY test; elevated in hemolysis, LOW in aplastic crisis',
        availability: 'BASIC'
      },
      {
        name: 'Blood Film',
        indication: '🟠 URGENT - Sickle cells, target cells; Howell-Jolly bodies indicate functional asplenia',
        availability: 'BASIC'
      },
      {
        name: 'Blood Group and Crossmatch',
        indication: '🔴 IMMEDIATE - If transfusion anticipated; extended phenotype if possible to reduce alloimmunization',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures',
        indication: '🔴 IMMEDIATE - If febrile; SCD patients at high risk for bacteremia; obtain before antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 IMMEDIATE - If respiratory symptoms, fever, or chest pain; diagnose ACS; baseline comparison',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry / ABG',
        indication: '🔴 IMMEDIATE - Assess oxygenation; hypoxemia triggers sickling and indicates ACS severity',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis and Urine Culture',
        indication: '🟠 URGENT - UTI is common trigger; painless hematuria common in SCD',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, BUN)',
        indication: '🟠 URGENT - Sickle nephropathy common; assess renal function; guide fluid management',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 URGENT - Baseline bilirubin elevated; hepatic sequestration; iron overload',
        availability: 'BASIC'
      },
      {
        name: 'LDH',
        indication: '🟡 Routine - Hemolysis marker; elevated in crisis; very high suggests hemolytic crisis',
        availability: 'BASIC'
      },
      {
        name: 'HbS Percentage (Pre-Transfusion)',
        indication: '🟠 URGENT - Before exchange transfusion; guide transfusion target (HbS <30%)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Parvovirus B19 Serology',
        indication: '🟠 URGENT - If aplastic crisis suspected (severe anemia + low reticulocytes)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Malaria Rapid Test / Blood Film',
        indication: '🔴 IMMEDIATE - In endemic areas; malaria can trigger crisis and cause severe disease in SCD',
        availability: 'BASIC'
      },
      {
        name: 'CT Head (Non-Contrast)',
        indication: '🔴 IMMEDIATE - If stroke suspected; rule out hemorrhage before exchange transfusion',
        availability: 'ADVANCED'
      },
      {
        name: 'Transcranial Doppler (TCD)',
        indication: '🟡 Routine - Annual screening in children 2-16 years; elevated velocity predicts stroke risk',
        availability: 'ADVANCED'
      },
      {
        name: 'Echocardiogram',
        indication: '🟡 Routine - Screen for pulmonary hypertension; TRV >2.5 m/s warrants further evaluation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Ferritin',
        indication: '🟡 Routine - Monitor iron overload in chronically transfused patients',
        availability: 'BASIC'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS CRISIS TYPE - VOC, ACS, Splenic sequestration, Aplastic crisis, Stroke',
        '⚡ IV ACCESS and FLUIDS - Normal saline or D5½NS at 1-1.5x maintenance; avoid over-hydration in ACS',
        '⚡ PAIN MANAGEMENT - Do NOT undertreat; opioids are first-line for severe pain',
        '⚡ OXYGEN - If SpO2 <95% or ACS suspected; maintain saturation >95%',
        '⚡ FEVER WORKUP - Blood cultures, urine culture, CXR; assume infection until proven otherwise',
        '⚡ Empiric ANTIBIOTICS if febrile - Cover encapsulated organisms; ceftriaxone first-line',
        '⚡ Check Hb and compare to BASELINE - Significant drop suggests sequestration or aplastic crisis',
        '⚡ Reticulocyte count - LOW suggests aplastic crisis (Parvovirus)',
        '⚡ TRANSFUSION if Hb <5 g/dL or symptomatic anemia or splenic sequestration',
        '⚡ EXCHANGE TRANSFUSION for ACS with hypoxemia, stroke, multi-organ failure',
        '⚡ Malaria test in endemic areas',
        '⚡ Incentive spirometry - Prevents ACS in chest/abdominal VOC',
        '⚡ Monitor closely - Patients can deteriorate rapidly; watch for ACS developing during VOC',
      ],
      pharmacological: [
        {
          drug: 'Morphine',
          indication: '🔴 First-line Analgesia - Severe vaso-occlusive crisis; do NOT undertreat pain',
          dose: 'Adults: 0.1-0.15 mg/kg IV every 2-4 hours OR PCA (1 mg bolus, 6-10 min lockout). Children: 0.1-0.2 mg/kg IV every 3-4 hours. Titrate to effect',
          route: 'IV (preferred for severe pain)',
          duration: 'Until pain controlled; taper as crisis resolves',
          contraindications: ['Respiratory depression (use caution, not absolute)', 'Allergy', 'Use with caution in ACS'],
          sideEffects: ['Respiratory depression (monitor closely)', 'Nausea/vomiting', 'Pruritus', 'Constipation', 'Sedation'],
          alternatives: ['Hydromorphone 0.015-0.02 mg/kg IV', 'Fentanyl 1-2 mcg/kg IV', 'Ketamine as adjunct']
        },
        {
          drug: 'Ketorolac',
          indication: '🟠 Adjunctive Analgesia - NSAID to reduce opioid requirement; NOT as monotherapy for severe pain',
          dose: 'Adults: 15-30 mg IV every 6 hours (max 5 days). Children >2 years: 0.5 mg/kg IV every 6 hours (max 30 mg/dose)',
          route: 'IV',
          duration: 'Maximum 5 days (risk of renal, GI toxicity)',
          contraindications: ['Renal impairment', 'GI bleeding', 'Dehydration', 'Concurrent anticoagulation'],
          sideEffects: ['Renal impairment (monitor)', 'GI bleeding', 'Platelet dysfunction'],
          alternatives: ['Ibuprofen PO if tolerating oral', 'Diclofenac']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 Adjunctive Analgesia - Opioid-sparing; safe as adjunct; fever reduction',
          dose: 'Adults: 1g IV/PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours',
          route: 'IV or PO',
          duration: 'Throughout crisis',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['NSAIDs as adjunct']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 Empiric Antibiotics - Febrile SCD patient; covers Pneumococcus, H. influenzae, Salmonella',
          dose: 'Adults: 2g IV daily. Children: 50-75 mg/kg IV daily (max 2g)',
          route: 'IV',
          duration: 'Until infection ruled out or completed treatment course',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Allergic reactions', 'Diarrhea', 'Biliary sludge'],
          alternatives: ['Ampicillin-sulbactam', 'Meropenem if severe sepsis']
        },
        {
          drug: 'Azithromycin',
          indication: '🟠 ACS Treatment - Atypical pathogen coverage (Mycoplasma, Chlamydia); add to beta-lactam',
          dose: 'Adults: 500 mg IV/PO on day 1, then 250 mg daily. Children: 10 mg/kg day 1 (max 500 mg), then 5 mg/kg daily',
          route: 'IV or PO',
          duration: '5 days for ACS',
          contraindications: ['QT prolongation', 'Macrolide allergy'],
          sideEffects: ['GI upset', 'QT prolongation', 'Hepatotoxicity'],
          alternatives: ['Fluoroquinolone in adults (respiratory)']
        },
        {
          drug: 'Penicillin V (Prophylaxis)',
          indication: '🔴 Infection Prophylaxis - ALL children with SCD from diagnosis to at least age 5; lifelong consideration',
          dose: 'Children <3 years: 125 mg PO twice daily. Children ≥3 years: 250 mg PO twice daily',
          route: 'PO',
          duration: 'Minimum until age 5; many continue lifelong',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Allergic reactions', 'Diarrhea'],
          alternatives: ['Erythromycin 10 mg/kg twice daily if penicillin allergic']
        },
        {
          drug: 'Hydroxyurea',
          indication: '🟠 Disease-Modifying Therapy - Reduces crises by 50%; increases HbF; safe in children ≥9 months',
          dose: 'Start 15-20 mg/kg/day PO; increase by 5 mg/kg every 8-12 weeks to max 35 mg/kg/day or max tolerated dose',
          route: 'PO',
          duration: 'Lifelong therapy; do not stop abruptly',
          contraindications: ['Pregnancy (teratogenic)', 'Severe myelosuppression', 'Breastfeeding'],
          sideEffects: ['Myelosuppression (dose-limiting; monitor CBC)', 'GI upset', 'Skin hyperpigmentation', 'Leg ulcers'],
          alternatives: ['L-glutamine (FDA approved but expensive)', 'Voxelotor', 'Crizanlizumab']
        },
        {
          drug: 'Folic Acid',
          indication: '🟡 Supportive - Increased demand due to chronic hemolysis; prevents megaloblastic anemia',
          dose: 'Adults: 1 mg PO daily. Children: 1 mg PO daily',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['None significant'],
          sideEffects: ['Generally well-tolerated'],
          alternatives: ['Diet rich in folate']
        },
        {
          drug: 'Deferoxamine / Deferasirox (Iron Chelation)',
          indication: '🟡 Iron Overload - For chronically transfused patients; ferritin >1000-1500 ng/mL',
          dose: 'Deferoxamine: 25-50 mg/kg/day SC infusion 8-12 hours, 5-7 days/week. Deferasirox: 20-40 mg/kg/day PO',
          route: 'SC (deferoxamine) or PO (deferasirox)',
          duration: 'Ongoing while transfusion-dependent; target ferritin <1000',
          contraindications: ['Severe renal impairment (deferasirox)', 'Allergy'],
          sideEffects: ['Deferoxamine: Injection site reactions, ototoxicity, retinal toxicity. Deferasirox: Renal impairment, GI upset, hepatotoxicity'],
          alternatives: ['Deferiprone (less commonly used in SCD)']
        },
        {
          drug: 'Pneumococcal Vaccine',
          indication: '🔴 Immunization - Essential; SCD patients at extremely high risk of pneumococcal sepsis',
          dose: 'PCV13: Standard schedule. PPSV23: At age 2 years, repeat at 5 years, then every 5 years',
          route: 'IM',
          duration: 'Per vaccination schedule',
          contraindications: ['Severe allergic reaction to previous dose'],
          sideEffects: ['Injection site reactions', 'Fever'],
          alternatives: ['None; vaccination essential']
        },
        {
          drug: 'Packed Red Blood Cells (Transfusion)',
          indication: '🔴 Acute Anemia - Hb <5 g/dL, symptomatic anemia, splenic sequestration, aplastic crisis, pre-operative',
          dose: 'Simple transfusion: 10-15 mL/kg (children) or 1-2 units (adults) to raise Hb by 1-2 g/dL. Avoid Hb >10-11 g/dL (hyperviscosity)',
          route: 'IV',
          duration: 'As needed for acute crises; chronic program for stroke prevention',
          contraindications: ['Hb >10 g/dL (relative - risk of hyperviscosity)'],
          sideEffects: ['Alloimmunization (30% of SCD patients)', 'Delayed hemolytic reaction', 'Iron overload', 'Transfusion reactions'],
          alternatives: ['Exchange transfusion when simple transfusion insufficient']
        },
        {
          drug: 'Exchange Transfusion',
          indication: '🔴 Severe Complications - ACS with hypoxemia, stroke, multi-organ failure, severe priapism; target HbS <30%',
          dose: 'Manual exchange: Remove 500 mL, replace with saline, then transfuse PRBCs. Automated: Apheresis preferred. Target: HbS <30%, Hb 10 g/dL',
          route: 'IV (manual or apheresis)',
          duration: 'Acute: Single or repeated as needed. Chronic: Monthly for stroke prevention',
          contraindications: ['Limited venous access (relative)', 'Volume intolerance'],
          sideEffects: ['Same as simple transfusion', 'Catheter complications', 'Citrate toxicity (apheresis)'],
          alternatives: ['Simple transfusion if exchange not available (less effective)']
        },
      ],
      nonPharmacological: [
        'IV Fluids: Normal saline or D5½NS at 1-1.5x maintenance; correct dehydration; avoid over-hydration in ACS',
        'Oxygen Therapy: If SpO2 <95%; maintain saturation >95%; hypoxia promotes sickling',
        'Incentive Spirometry: Every 2-4 hours while awake; prevents ACS in patients with chest/abdominal pain; 10 breaths every 2 hours',
        'Warmth: Keep patient warm; cold promotes sickling and vasoconstriction',
        'Rest and Comfort: Quiet environment; reduce stress',
        'Physical Therapy: Early mobilization when pain controlled; prevent venous stasis',
        'Heat Application: Warm compresses to painful areas (not cold)',
        'Psychological Support: Chronic disease burden; anxiety and depression common; counseling',
        'Patient Education: Trigger avoidance, hydration, when to seek care, medication adherence',
        'Newborn Screening Programs: Early diagnosis allows prophylaxis and comprehensive care',
        'Comprehensive Care Clinics: Multidisciplinary approach improves outcomes',
        'Genetic Counseling: For patients and family; reproductive options',
      ],
      surgical: [
        'Splenectomy: After splenic sequestration (usually after second episode in children); removes risk of recurrence; increases infection risk',
        'Cholecystectomy: For symptomatic gallstones (pigment stones common); often done laparoscopically; elective preferred over emergent',
        'Hip Replacement: For avascular necrosis with severe pain/disability; challenging due to anemia, infection risk',
        'Transcranial Doppler Screening: Not surgical but essential procedure for stroke prevention; annual in children 2-16',
        'Priapism Management: Aspiration and irrigation with phenylephrine; surgical shunt if refractory',
        'Leg Ulcer Debridement: Chronic leg ulcers may need debridement and skin grafting',
        'Bone Marrow Transplant (Hematopoietic Stem Cell Transplant): Curative; best results in children with matched sibling donor; 90% disease-free survival; limited availability in Africa',
        'Pre-Operative Transfusion: Simple transfusion to Hb 10 g/dL before major surgery; reduces perioperative complications',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Stroke symptoms → Immediate exchange transfusion; neurology consultation',
        '🔴 EMERGENCY - ACS with respiratory failure → ICU; exchange transfusion; pulmonology',
        '🔴 EMERGENCY - Splenic sequestration with shock → Urgent transfusion; pediatric emergency',
        '🔴 EMERGENCY - Priapism >4 hours → Urology emergency; aspiration and irrigation',
        '🔴 EMERGENCY - Sepsis → Aggressive antibiotics; ICU if unstable',
        '🟠 URGENT - ACS without respiratory failure → Close monitoring; simple transfusion; may need exchange',
        '🟠 URGENT - Pain crisis not responding to standard treatment → Consider other diagnoses; escalate analgesia',
        '🟠 URGENT - Aplastic crisis → Transfusion support; usually self-limiting',
        '🟡 ROUTINE - Recurrent crises → Hydroxyurea initiation; hematology follow-up',
        '🟡 ROUTINE - Elevated TCD velocity → Chronic transfusion program for stroke prevention',
        '🟡 ROUTINE - Chronic complications (AVN, nephropathy, leg ulcers) → Specialty referral',
        '🟡 ROUTINE - Consideration for bone marrow transplant → Pediatric hematology/transplant center',
      ],
      monitoring: [
        'Vital Signs: Continuous during acute crisis; temperature (infection), SpO2 (ACS), BP, HR',
        'Pain Assessment: Regular pain scores; adjust analgesia to achieve comfort',
        'Respiratory Status: Watch for ACS development; incentive spirometry compliance; oxygen saturation',
        'Hemoglobin: Daily during crisis; compare to baseline; watch for drops',
        'Reticulocyte Count: Helps differentiate crisis types; low in aplastic crisis',
        'Fluid Balance: Intake/output; avoid dehydration and over-hydration',
        'Mental Status: Watch for stroke symptoms; hypoxia; opioid toxicity',
        'Spleen Size: In children; daily palpation during febrile illness',
        'Post-Transfusion: Watch for reactions; check Hb; delayed hemolytic reactions days to weeks later',
        'Long-term: HbF level on hydroxyurea; organ function; TCD annually; iron overload if transfused',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

ACUTE CRISIS RECOVERY:
• 1-2 weeks post-discharge: Ensure crisis resolved; pain controlled; oral analgesia adequate
• Transition to outpatient pain management
• Reinforce trigger avoidance
• Ensure hydroxyurea restart if held

ROUTINE OUTPATIENT CARE:

Infants and Children:
• Monthly until age 1 year
• Every 2-3 months ages 1-5 years
• Every 3-4 months thereafter

Adults:
• Every 3-6 months for stable patients
• More frequent if complications or poorly controlled

AT EACH VISIT:
• Review crises since last visit
• Medication adherence (hydroxyurea, prophylaxis)
• Growth and development (children)
• Vaccination status
• Complications screening
• CBC, reticulocytes

ANNUAL ASSESSMENTS:

Children 2-16 years:
• Transcranial Doppler (TCD)
• Elevated velocity (>200 cm/s): Start chronic transfusion
• Conditional (170-199): Increase monitoring frequency

All Ages:
• Renal function (creatinine, urinalysis for protein)
• Liver function
• Ferritin if transfused
• Ophthalmology exam (retinopathy)
• Echocardiogram (pulmonary hypertension screening)

CHRONIC TRANSFUSION PROGRAM (Stroke Prevention):
• Monthly simple transfusion or exchange
• Target HbS <30%
• Monitor ferritin; chelation when indicated
• Continue indefinitely or until transplant

HYDROXYUREA MONITORING:
• CBC every 4-8 weeks during dose escalation
• Once stable: Every 2-3 months
• Target ANC >2000, Platelets >80,000
• HbF level (goal increase from baseline)

TRANSITION TO ADULT CARE:
• Begin planning at age 12-14
• Formal transition 18-21 years
• Ensure adult hematology continuity`
    },

    redFlags: [
      '🔴 CRITICAL: Stroke symptoms - Sudden weakness, speech difficulty, altered consciousness → IMMEDIATE exchange transfusion',
      '🔴 CRITICAL: ACS with respiratory failure - Severe hypoxemia, respiratory distress → ICU, exchange transfusion, ventilatory support',
      '🔴 CRITICAL: Splenic sequestration with shock - Pallor, tachycardia, hypotension, enlarged spleen → IMMEDIATE transfusion',
      '🔴 CRITICAL: Sepsis/Meningitis - High fever, altered mental status, hemodynamic instability → Aggressive antibiotics, fluid resuscitation',
      '🔴 CRITICAL: Priapism >4 hours - Prolonged painful erection → Urological emergency; aspiration and irrigation',
      '🔴 CRITICAL: Multi-organ failure - Concurrent ACS, hepatic sequestration, renal failure → Exchange transfusion, ICU',
      '🟠 URGENT: Fever >38.5°C - Assume bacteremia; blood cultures and empiric antibiotics immediately',
      '🟠 URGENT: Chest symptoms during VOC - May be developing ACS; CXR, oxygen, escalate care',
      '🟠 URGENT: Aplastic crisis - Severe anemia (Hb <5), low reticulocytes → Transfusion support',
      '🟠 URGENT: Rapid Hb drop - Consider splenic sequestration, hemolytic crisis, bleeding',
      '🟡 WARNING: Pain not responding to standard treatment - Consider other diagnoses; adjust analgesia; watch for ACS',
      '🟡 WARNING: Elevated TCD velocity (>200 cm/s) - High stroke risk → Initiate chronic transfusion program',
    ],

    complications: [
      'Stroke: 11% risk in children without screening; chronic transfusion reduces risk 90%; leads to significant disability',
      'Acute Chest Syndrome: Leading cause of death in adults; 50% mortality if requiring mechanical ventilation',
      'Overwhelming Post-Splenectomy Infection (OPSI): Functional asplenia by age 5; Pneumococcus, H. influenzae, meningococcus; rapidly fatal',
      'Pulmonary Hypertension: 10-30% prevalence; associated with increased mortality; TRV >2.5 m/s on echo warrants evaluation',
      'Chronic Kidney Disease: Progressive; 5-20% progress to ESRD; ACE inhibitors may slow progression',
      'Avascular Necrosis: Hip and shoulder most common; pain, disability; may need joint replacement',
      'Cholelithiasis: Pigment gallstones in 30-70%; cholecystectomy if symptomatic',
      'Chronic Leg Ulcers: Painful; difficult to heal; common in HbSS; may need grafting',
      'Proliferative Retinopathy: More common in HbSC; can cause vision loss; laser treatment',
      'Priapism: 30-45% of males; can cause erectile dysfunction if prolonged',
      'Iron Overload: From chronic transfusion; cardiac and liver damage; chelation required',
      'Delayed Growth and Puberty: Common in children; usually catch up eventually',
      'Silent Cerebral Infarcts: 30% of children; cognitive impairment; MRI screening controversial',
      'Pregnancy Complications: Increased maternal and fetal risks; requires specialized care',
    ],

    prognosis: `OVERALL PROGNOSIS:

SURVIVAL:
• High-income countries with comprehensive care: Median survival 50-60 years
• Without comprehensive care (much of Africa): Median survival often <20 years
• Up to 90% mortality before age 5 in Africa without newborn screening and care
• Survival improving with hydroxyurea, transfusion programs, transplant

MORTALITY CAUSES:
• Children: Infection (especially pneumococcus), splenic sequestration
• Adults: Acute chest syndrome, pulmonary hypertension, renal failure, stroke

QUALITY OF LIFE:
• Chronic pain affects 50%+ of adults
• Frequent hospitalizations
• Cognitive impairment from silent strokes (30%)
• Psychosocial impact significant
• Depression and anxiety common

FACTORS IMPROVING PROGNOSIS:

Newborn Screening:
• Early diagnosis allows prophylaxis and parental education
• Reduces mortality by 70-90% in first 5 years

Penicillin Prophylaxis:
• 84% reduction in pneumococcal sepsis
• Should continue at least to age 5

Hydroxyurea:
• 50% reduction in painful crises
• 50% reduction in ACS
• Reduces mortality
• Safe and effective from age 9 months

Chronic Transfusion:
• 90% reduction in stroke risk with TCD screening and transfusion
• Improves survival in high-risk patients

Hematopoietic Stem Cell Transplant:
• Curative in 90% with matched sibling donor
• Increasing options with haploidentical and gene therapy

AFRICAN CONTEXT:

Challenges:
• Limited newborn screening programs
• Lack of access to hydroxyurea
• Blood supply limitations
• Few comprehensive care centers
• High infection burden (malaria, bacterial)

Improvements Needed:
• National newborn screening programs
• Access to affordable hydroxyurea
• Safe blood transfusion capacity
• Training healthcare workers
• Comprehensive care clinics
• Pneumococcal vaccination programs`,

    prevention: `PRIMARY PREVENTION:

Genetic Counseling:
• Screen at-risk populations (carrier testing)
• Counsel couples where both are carriers (25% risk of affected child)
• Reproductive options: PGD, prenatal diagnosis, donor gametes, adoption

Newborn Screening:
• Enables early intervention
• Recommended in all endemic areas
• Heel-prick blood at birth
• Methods: HPLC, isoelectric focusing, Hb electrophoresis

SECONDARY PREVENTION (Preventing Complications):

Infection Prevention:
• Penicillin prophylaxis from diagnosis to at least age 5 (lifelong considered)
• Pneumococcal vaccination (PCV13 + PPSV23)
• H. influenzae type b vaccine
• Meningococcal vaccine
• Annual influenza vaccine
• Malaria prophylaxis in endemic areas during travel

Stroke Prevention:
• TCD screening annually ages 2-16
• Chronic transfusion if TCD velocity >200 cm/s
• Hydroxyurea may be alternative in resource-limited settings

Disease Modification:
• Hydroxyurea for all eligible patients (ages 9 months and up)
• Start early for best outcomes
• Continue lifelong

CRISIS PREVENTION:

Trigger Avoidance:
• Stay well-hydrated (8-10 glasses water daily)
• Avoid temperature extremes
• Dress warmly in cold weather
• Avoid high altitude (>10,000 feet) without preparation
• Get adequate rest
• Manage stress

Activity:
• Regular moderate exercise encouraged
• Avoid overexertion
• Stay hydrated during exercise
• Listen to body signals

Avoid:
• Smoking and secondhand smoke
• Excessive alcohol
• Recreational drugs
• Extreme physical exertion without preparation

VACCINATIONS:

Essential Vaccines:
• Pneumococcal (PCV13 and PPSV23) - Most important
• H. influenzae type b (Hib)
• Meningococcal (ACWY and B)
• Annual influenza
• COVID-19
• Routine childhood vaccines on schedule

Vaccination Schedule Highlights:
• PCV13: Standard infant schedule
• PPSV23: At 2 years, repeat at 5 years, then every 5 years for life
• Meningococcal: Per national guidelines; boosters every 5 years

AFRICAN-SPECIFIC STRATEGIES:

• Establish national newborn screening programs
• Ensure affordable hydroxyurea access
• Integrate SCD care into primary healthcare
• Train healthcare workers in SCD management
• Build capacity for blood transfusion services
• Strengthen vaccination programs
• Community education and awareness
• Support patient organizations
• Research African-specific interventions`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING SICKLE CELL DISEASE - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS SICKLE CELL DISEASE?
Sickle cell disease is an inherited blood disorder where red blood cells become abnormally shaped like crescents or "sickles" instead of round discs. These sickle cells are stiff and sticky, causing them to get stuck in small blood vessels and block blood flow. This causes episodes of pain and can damage organs over time.

HOW DID I GET IT?
• Sickle cell disease is inherited from BOTH parents
• Each parent must carry the sickle cell gene (called "sickle cell trait")
• If both parents have the trait, there is a 25% chance with each pregnancy that the child will have sickle cell disease
• Having sickle cell trait (one copy of the gene) usually causes no symptoms

WHAT ARE THE SYMPTOMS?

Pain Crisis (Most Common):
• Sudden, severe pain in bones, joints, chest, or abdomen
• Can last hours to days
• May have low fever
• This is the most common reason for hospital visits

Other Symptoms:
• Fatigue and weakness (from anemia)
• Yellowing of eyes and skin (jaundice)
• Swelling of hands and feet (especially in babies)
• Frequent infections
• Delayed growth in children

⚠️ GO TO THE HOSPITAL IMMEDIATELY IF:
• Fever above 38.5°C (101.3°F) - ALWAYS taken seriously
• Difficulty breathing or chest pain
• Severe abdominal pain with swelling
• Severe headache, weakness, or difficulty speaking (stroke symptoms)
• Sudden paleness and weakness
• Very painful erection lasting more than 4 hours (priapism)

WHAT TRIGGERS A CRISIS?
• Infections (most common trigger)
• Dehydration (not drinking enough water)
• Cold temperatures
• High altitudes
• Stress
• Overexertion
• Sometimes no clear trigger

HOW TO PREVENT CRISES:

Stay Well Hydrated:
• Drink 8-10 glasses of water daily
• More during hot weather or exercise
• Carry water with you always

Avoid Temperature Extremes:
• Dress warmly in cold weather
• Avoid cold water swimming
• Stay cool in hot weather (but not with cold air conditioning)

Prevent Infections:
• Take penicillin as prescribed (prevents serious infections)
• Get ALL recommended vaccines
• Wash hands frequently
• Avoid sick people when possible

Other Tips:
• Get adequate rest
• Exercise regularly but don't overexert
• Don't smoke or drink too much alcohol
• Manage stress
• Avoid very high altitudes

IMPORTANT MEDICINES:

Penicillin (Infection Prevention):
• Take EVERY DAY as prescribed
• Protects against serious infections
• Children must take this - it saves lives!
• Do not stop without doctor's advice

Hydroxyurea:
• Very important medicine that reduces crises
• Reduces pain episodes by half
• Reduces chest problems
• Safe for children 9 months and older
• Take every day as prescribed
• Your doctor will monitor your blood counts

Folic Acid:
• Helps your body make new red blood cells
• Take daily as prescribed

VACCINATIONS - VERY IMPORTANT:
Your spleen doesn't work properly, so you are at very high risk of serious infections. Vaccinations are ESSENTIAL:
• Pneumococcal vaccines (prevents pneumonia and bloodstream infections)
• Meningococcal vaccine
• Flu vaccine every year
• All routine childhood vaccines
• COVID-19 vaccine

WHEN PAIN HAPPENS AT HOME:
• Drink plenty of fluids
• Take pain medicines as prescribed
• Use warm compresses (NOT cold)
• Rest comfortably
• Distraction techniques (music, TV, reading)
• Go to the hospital if pain is severe or not controlled

LIVING WITH SICKLE CELL DISEASE:

School and Work:
• Most people with SCD can attend school and work
• May need accommodations (bathroom breaks for hydration)
• Teachers/employers should know about the condition
• Avoid extreme temperatures and overexertion

Exercise:
• Regular moderate exercise is GOOD
• Stay well-hydrated before, during, and after
• Listen to your body
• Avoid extreme or competitive sports without preparation

Travel:
• Carry medications and plenty of water
• On planes: Move around, stay hydrated
• Have a plan for medical care at your destination
• Carry a medical summary letter

Pregnancy:
• Pregnancy is higher risk in SCD
• Plan pregnancy with your doctor
• Close monitoring throughout pregnancy
• Specialized care is needed

FOR PARENTS OF CHILDREN WITH SCD:
• Learn the warning signs that need emergency care
• Keep your child up to date on vaccinations
• Give penicillin every day as prescribed
• Keep regular clinic appointments
• Teach your child about their condition as they grow
• Inform school about your child's needs
• Connect with other families for support

LONG-TERM HEALTH:
• Regular check-ups are essential
• Some people develop complications affecting:
  - Eyes (get regular eye exams)
  - Kidneys
  - Heart
  - Hips and joints
  - Lungs
• Early detection allows early treatment
• Modern treatment allows most people to live full lives

REMEMBER:
• Sickle cell disease can be managed with proper care
• Take your medicines every day
• Stay hydrated and avoid triggers
• Know when to seek emergency care
• Keep all your clinic appointments
• You are not alone - support is available

SUPPORT AND RESOURCES:
• Sickle Cell Disease Association (in your country)
• Connect with other patients and families
• Ask your doctor about support groups
• Mental health support is important too`,

    references: [
      'Ware RE, et al. Sickle cell disease. Lancet. 2017;390:311-323. - Comprehensive review',
      'Piel FB, et al. Global epidemiology of sickle haemoglobin in neonates: a contemporary geostatistical model-based map and population estimates. Lancet. 2013;381:142-151. - Epidemiology',
      'National Heart, Lung, and Blood Institute. Evidence-Based Management of Sickle Cell Disease: Expert Panel Report, 2014. - NIH guidelines',
      'Howard J, et al. Guideline on the management of acute chest syndrome in sickle cell disease. Br J Haematol. 2015;169:492-505. - ACS management',
      'Adams RJ, et al. Prevention of a first stroke by transfusions in children with sickle cell anemia and abnormal results on transcranial Doppler ultrasonography (STOP). N Engl J Med. 1998;339:5-11. - STOP trial',
      'Charache S, et al. Effect of hydroxyurea on the frequency of painful crises in sickle cell anemia (MSH). N Engl J Med. 1995;332:1317-1322. - MSH trial',
      'Makani J, et al. Sickle cell disease in Africa: burden and research priorities. Ann Trop Med Parasitol. 2007;101:3-14. - African burden',
      'McGann PT, et al. Hydroxyurea therapy for sickle cell anemia. Expert Opin Drug Saf. 2017;16:1459-1466. - Hydroxyurea',
      'Gluckman E, et al. Sickle cell disease: an international survey of results of HLA-identical sibling hematopoietic stem cell transplantation. Blood. 2017;129:1548-1556. - Transplant outcomes',
      'Piel FB, et al. Sickle cell disease. N Engl J Med. 2017;376:1561-1573. - NEJM review',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 38. SEVERE ACUTE MALNUTRITION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '38',
    name: 'Severe Acute Malnutrition',
    slug: 'severe-acute-malnutrition',
    specialty: 'Pediatrics',
    bodySystem: 'Gastrointestinal',
    icdCode: 'E43',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Severe Acute Malnutrition (SAM) is defined as weight-for-height/length <-3 SD of WHO median, or MUAC <115 mm (children 6-59 months), or presence of bilateral pitting edema (nutritional edema). SAM represents the most dangerous form of malnutrition and is a medical emergency requiring immediate intervention.

SAM is a major cause of child mortality in Africa, contributing to approximately 45% of all deaths in children under 5 years. An estimated 13 million children under 5 suffer from SAM globally, with the majority in Sub-Saharan Africa and South Asia. Without treatment, case fatality rates are 30-50%, but with appropriate management, mortality can be reduced to <5%.

SAM manifests in two main clinical forms: Marasmus (severe wasting without edema) and Kwashiorkor (presence of bilateral pitting edema, often with relatively preserved weight). Mixed presentations (marasmic-kwashiorkor) also occur.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• SAM DIAGNOSTIC CRITERIA: WFH <-3 SD, OR MUAC <115 mm (6-59 months), OR bilateral pitting edema
• INPATIENT vs OUTPATIENT: Complicated SAM (medical complications or failed appetite test) = INPATIENT; Uncomplicated = Outpatient with RUTF
• F-75 (stabilization phase) then F-100 (rehabilitation) for inpatient; RUTF for outpatient
• AVOID rapid refeeding - Refeeding syndrome risk (hypophosphatemia, cardiac failure)
• HYPOTHERMIA and HYPOGLYCEMIA: Common killers; keep warm, feed frequently
• Routine antibiotics for ALL inpatients (Amoxicillin + Gentamicin or Ampicillin)
• Do NOT give iron in stabilization phase - wait until rehabilitation phase (2 weeks)
• Fluid restriction: Malnourished children cannot handle fluid overload; use ReSoMal for dehydration
• Appetite test with RUTF determines outpatient eligibility
• Monitor weight gain: Target >5 g/kg/day in rehabilitation phase`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 45-50 million children under 5 with acute malnutrition globally
• 13-14 million children with SAM
• SAM contributes to 1-2 million child deaths annually
• Highest burden: Sub-Saharan Africa, South Asia
• SAM underlying cause in 45% of all under-5 deaths

AFRICAN EPIDEMIOLOGY:
• 7-8 million children with SAM in Africa
• Highest prevalence: Sahel region, Horn of Africa, Southern Africa
• Countries with high burden: Nigeria, DRC, Ethiopia, Niger, Chad, South Sudan
• Seasonal variation: Peak during "hunger gap" before harvest
• Humanitarian emergencies greatly increase SAM burden

CASE FATALITY:
• Without treatment: 30-50%
• With appropriate treatment: <5-10% in inpatient, <3% in outpatient
• Highest mortality: First 48-72 hours of admission
• Death often from: Hypoglycemia, hypothermia, sepsis, electrolyte imbalance

RISK FACTORS:
• Food insecurity and poverty
• Inappropriate infant and young child feeding
• Inadequate breastfeeding
• Recurrent infections (diarrhea, pneumonia, malaria, HIV, TB)
• Low birth weight
• Large family size
• Poor water and sanitation
• Maternal education and health
• Conflict and displacement

CLINICAL FORMS:

MARASMUS:
• Severe wasting (visible severe wasting)
• No edema
• "Old man" face
• Loss of subcutaneous fat
• Muscle wasting
• Alert, hungry appearance

KWASHIORKOR:
• Bilateral pitting edema (hallmark)
• May appear relatively "well-nourished" due to edema
• Skin changes (dermatosis, desquamation)
• Hair changes (sparse, discolored, easy pluckability)
• Apathy, irritability
• Fatty liver (hepatomegaly)

MARASMIC-KWASHIORKOR:
• Features of both: severe wasting + bilateral edema
• Highest mortality risk`,

    clinicalPresentation: `DIAGNOSTIC CRITERIA FOR SAM:

Children 6-59 months (ANY ONE):
• Weight-for-Height (WFH) <-3 SD (Z-score) of WHO standards
• Mid-Upper Arm Circumference (MUAC) <115 mm
• Bilateral pitting edema (nutritional edema)

Infants <6 months:
• Weight-for-Length <-3 SD
• Visible severe wasting
• Bilateral pitting edema
• Too weak or ill to breastfeed

MARASMUS - Clinical Features:

Appearance:
• Severe wasting - "skin and bones"
• Visible ribs, spine, scapulae
• "Old man" or "monkey" face (loss of buccal fat pad)
• Loose, baggy skin (loss of subcutaneous fat)
• Very thin limbs
• Prominent joints
• Anxious, alert appearance (usually)
• Often hungry

Weight:
• Very low weight-for-height (<-3 SD)
• Low MUAC (<115 mm)
• No edema

KWASHIORKOR - Clinical Features:

Edema:
• Bilateral pitting edema - DEFINING feature
• Starts in feet, ascends to legs, hands, face
• Pitting: Press firmly for 3 seconds over dorsum of foot

Skin Changes (Dermatosis):
• "Flaky paint" dermatosis
• Hyperpigmented patches that desquamate
• "Crazy pavement" appearance
• May have ulceration
• Pellagra-like lesions

Hair Changes:
• Sparse, thin hair
• Discolored (reddish, blonde - "flag sign")
• Easily pluckable
• Straightened (loss of curl in African hair)

Other Features:
• Moon face (edema)
• Hepatomegaly (fatty liver)
• Apathy, irritability
• Anorexia (poor appetite - contrast with marasmus)
• May appear relatively preserved weight (masked by edema)

MARASMIC-KWASHIORKOR:
• Severe wasting PLUS bilateral pitting edema
• Worst prognosis
• Combined features of both conditions

CLINICAL ASSESSMENT:

Assess for Complications (INPATIENT indicators):
1. Anorexia (failed appetite test)
2. Medical complications:
   • Severe dehydration
   • Shock
   • Hypoglycemia
   • Hypothermia (<35°C)
   • Severe anemia (Hb <4 g/dL or 4-6 with respiratory distress)
   • Severe infection/sepsis
   • Severe skin lesions
   • Eye signs of vitamin A deficiency
3. Bilateral pitting edema grade +++
4. Age <6 months or weight <4 kg

APPETITE TEST:
• Essential for determining inpatient vs outpatient care
• Offer RUTF in calm, separate area
• Passed: Child eats sufficient RUTF (see tables in guidelines)
• Failed: Child cannot eat adequate amount → Requires inpatient care

EDEMA GRADING:
• + (mild): Both feet
• ++ (moderate): Both feet + lower legs OR hands
• +++ (severe): Generalized including face → Always inpatient`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Anthropometric Assessment
Step 2: Clinical Assessment (edema, complications)
Step 3: Appetite Test (if no medical complications)
Step 4: Classify: Complicated vs Uncomplicated SAM
Step 5: Determine care pathway (Inpatient vs Outpatient)

ANTHROPOMETRIC MEASUREMENTS:

Weight-for-Height/Length (WFH/WFL):
• Use WHO growth standards
• <-3 SD = SAM
• -3 to -2 SD = MAM (Moderate Acute Malnutrition)
• Use length if <87 cm, height if ≥87 cm

Mid-Upper Arm Circumference (MUAC):
• Measured at midpoint of left upper arm
• Children 6-59 months:
  - <115 mm (11.5 cm) = SAM
  - 115-124 mm = MAM
  - ≥125 mm = Normal
• Simple, requires only MUAC tape
• Can be used by community health workers

MUAC Colors (standard MUAC tape):
• Red: <115 mm (SAM)
• Yellow: 115-124 mm (MAM)
• Green: ≥125 mm (normal)

Bilateral Pitting Edema Assessment:
• Press firmly over dorsum of both feet for 3 seconds
• Positive: Impression remains after release
• Must be BILATERAL to be nutritional
• Grade: +, ++, +++

CLASSIFICATION:

| Indicator | SAM | MAM |
|-----------|-----|-----|
| WFH Z-score | <-3 SD | -3 to -2 SD |
| MUAC (6-59 mo) | <115 mm | 115-124 mm |
| Edema | Present (bilateral) | Absent |

APPETITE TEST PROTOCOL:

Purpose: Determine if child can be treated as outpatient

Procedure:
1. Explain to caregiver
2. Provide quiet, separate area
3. Caregiver offers RUTF (do not force feed)
4. Allow adequate time
5. Observe amount consumed

Passed Appetite Test (can treat outpatient):
• Consumes adequate amount based on weight
• Generally: At least one sachet (92g) of RUTF

Failed Appetite Test:
• Refuses or unable to eat
• Eats very little
• → Requires inpatient care

INVESTIGATIONS:

For ALL SAM (if available):
• Blood glucose (hypoglycemia common)
• Hemoglobin (anemia common)
• Malaria rapid test (in endemic areas)
• HIV test (with consent)

Additional for Inpatient/Complicated SAM:
• Full blood count
• Electrolytes (hypophosphatemia, hypokalemia, hypomagnesemia)
• Blood culture if sepsis suspected
• Stool microscopy
• Urinalysis
• Chest X-ray if respiratory symptoms
• TB screening (high prevalence in SAM)`,

    differentialDiagnosis: [
      'Chronic disease with wasting - HIV, TB, malignancy, chronic kidney disease; may coexist with SAM',
      'Edema from other causes - Nephrotic syndrome (proteinuria), heart failure, liver disease, allergic reactions; usually not bilateral pitting',
      'Marasmus vs Kwashiorkor vs Mixed - Different clinical presentations but same management principles',
      'HIV wasting syndrome - Common co-morbidity; test all SAM children; affects prognosis and management',
      'Congenital metabolic disorders - Rare; failure to thrive despite adequate feeding',
      'Cystic fibrosis - Failure to thrive, malabsorption, respiratory symptoms',
      'Celiac disease - Malabsorption, failure to thrive; rare in Africa',
      'Intestinal parasites - Contribute to malnutrition; treat empirically',
      'Pyloric stenosis - Vomiting, failure to thrive in infants',
      'Chronic diarrheal diseases - Persistent diarrhea contributes to malnutrition',
      'Neglect/Abuse - Consider if nutritional history inconsistent with clinical picture',
    ],

    investigations: [
      {
        name: 'MUAC Measurement',
        indication: '🔴 IMMEDIATE - Rapid assessment; <115 mm = SAM; can be done by any trained worker',
        availability: 'BASIC'
      },
      {
        name: 'Weight-for-Height Z-score',
        indication: '🔴 IMMEDIATE - Gold standard anthropometric diagnosis; <-3 SD = SAM',
        availability: 'BASIC'
      },
      {
        name: 'Bilateral Pitting Edema Assessment',
        indication: '🔴 IMMEDIATE - Press both feet dorsum 3 seconds; presence = SAM regardless of other measurements',
        availability: 'BASIC'
      },
      {
        name: 'Appetite Test',
        indication: '🔴 IMMEDIATE - Determines inpatient vs outpatient; offer RUTF, observe intake',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE - Hypoglycemia (<3 mmol/L or <54 mg/dL) common and dangerous; check on admission',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin / Hematocrit',
        indication: '🔴 IMMEDIATE - Anemia common; Hb <4 g/dL or <6 with distress = severe; guide transfusion',
        availability: 'BASIC'
      },
      {
        name: 'Temperature',
        indication: '🔴 IMMEDIATE - Hypothermia (<35°C) common and dangerous; keep warm; axillary or rectal',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Rapid Test',
        indication: '🔴 IMMEDIATE - In endemic areas; malaria common trigger/complication; treat if positive',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT - All SAM children; HIV common comorbidity; affects management and prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 URGENT - If available; assess anemia severity, infection (WBC)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Electrolytes',
        indication: '🟠 URGENT - If available; refeeding syndrome risk (hypophosphatemia, hypokalemia, hypomagnesemia)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Culture',
        indication: '🟠 URGENT - If sepsis suspected; SAM children often have occult infection',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Stool Microscopy',
        indication: '🟡 Routine - Parasites common; treat empirically with albendazole/mebendazole',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🟡 Routine - UTI screening; proteinuria if edema etiology unclear',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🟡 Routine - If respiratory symptoms; pneumonia common; TB screening',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'TB Screening (Mantoux, GeneXpert)',
        indication: '🟠 URGENT - High TB prevalence in SAM; low threshold to investigate',
        availability: 'INTERMEDIATE'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS and CLASSIFY - SAM (MUAC <115, WFH <-3 SD, or bilateral edema)',
        '⚡ CHECK for complications - Hypoglycemia, hypothermia, dehydration, shock, infection',
        '⚡ APPETITE TEST - Determines inpatient vs outpatient pathway',
        '⚡ HYPOGLYCEMIA: If glucose <3 mmol/L → Give 50 mL 10% dextrose (or breastfeed/F-75 if conscious)',
        '⚡ HYPOTHERMIA: If temp <35°C → Kangaroo care, warm room, warm blankets, warm fluids',
        '⚡ DEHYDRATION: Use ReSoMal (NOT standard ORS), 5-10 mL/kg/hour for first 2 hours',
        '⚡ Start ROUTINE ANTIBIOTICS for all inpatients (Amoxicillin + Gentamicin)',
        '⚡ Start F-75 therapeutic milk (stabilization phase) every 2-3 hours',
        '⚡ Do NOT give iron in first 2 weeks (stabilization phase)',
        '⚡ Keep warm - Malnourished children lose heat rapidly',
        '⚡ Handle gently - Avoid unnecessary procedures',
        '⚡ VITAMIN A: Single dose on Day 1',
        '⚡ Treat underlying conditions: Malaria, infections, TB if present',
      ],
      pharmacological: [
        {
          drug: 'Amoxicillin',
          indication: '🔴 Routine Antibiotics (Outpatient SAM) - All SAM children have presumed occult infection',
          dose: 'Children: 25-40 mg/kg PO twice daily for 7 days. Use syrup or dispersible tablets',
          route: 'PO',
          duration: '7 days',
          contraindications: ['Severe penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'GI upset'],
          alternatives: ['If severely ill: IV Ampicillin + Gentamicin']
        },
        {
          drug: 'Ampicillin IV',
          indication: '🔴 Routine Antibiotics (Inpatient SAM) - For complicated SAM requiring inpatient care',
          dose: 'Children: 50 mg/kg IV/IM every 6 hours for minimum 2 days, then oral Amoxicillin',
          route: 'IV or IM',
          duration: 'Until improved (minimum 2 days), then switch to oral for total 7-10 days',
          contraindications: ['Severe penicillin allergy'],
          sideEffects: ['Allergic reactions', 'Diarrhea', 'Injection site pain'],
          alternatives: ['Ceftriaxone if available and indicated']
        },
        {
          drug: 'Gentamicin',
          indication: '🔴 Routine Antibiotics (Inpatient SAM) - Gram-negative coverage; given with Ampicillin',
          dose: 'Children: 7.5 mg/kg IV/IM once daily',
          route: 'IV or IM',
          duration: 'Until improved (minimum 2 days)',
          contraindications: ['Known gentamicin allergy', 'Renal failure (adjust dose)'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity (monitor if prolonged use)'],
          alternatives: ['Ceftriaxone monotherapy if gentamicin unavailable']
        },
        {
          drug: 'Vitamin A',
          indication: '🔴 Single Dose Day 1 - Prevents blindness; all SAM children; repeat if eye signs',
          dose: '6-12 months: 100,000 IU. >12 months: 200,000 IU. If eye signs: Repeat Day 2 and Day 14',
          route: 'PO',
          duration: 'Single dose (repeat if vitamin A deficiency signs)',
          contraindications: ['Recent high-dose vitamin A supplementation (within 1 month)'],
          sideEffects: ['Transient headache', 'Nausea', 'Bulging fontanelle (rare)'],
          alternatives: ['None; essential supplementation']
        },
        {
          drug: 'Folic Acid',
          indication: '🟠 Supplementation - Required for recovery; deficiency common',
          dose: 'Children: 5 mg on Day 1, then 1 mg daily',
          route: 'PO',
          duration: 'Duration of treatment',
          contraindications: ['None significant'],
          sideEffects: ['Generally well-tolerated'],
          alternatives: ['Included in therapeutic foods']
        },
        {
          drug: 'Zinc',
          indication: '🟠 Supplementation - Essential for recovery, growth, and immunity',
          dose: 'Children: 10-20 mg elemental zinc daily',
          route: 'PO',
          duration: 'Duration of treatment (usually in therapeutic foods)',
          contraindications: ['None significant'],
          sideEffects: ['Nausea if high doses on empty stomach'],
          alternatives: ['Included in RUTF and F-75/F-100']
        },
        {
          drug: 'Ferrous Sulfate (Iron)',
          indication: '🟡 Iron Supplementation - ONLY in rehabilitation phase (after 2 weeks); NOT in stabilization',
          dose: '3 mg/kg elemental iron daily. Give with food',
          route: 'PO',
          duration: 'During rehabilitation phase until discharge/recovery',
          contraindications: ['Stabilization phase (first 2 weeks)', 'Active infection/sepsis', 'Severe anemia requiring transfusion'],
          sideEffects: ['GI upset', 'Constipation', 'Black stools', 'May feed bacteria in infection'],
          alternatives: ['Iron in therapeutic foods']
        },
        {
          drug: 'Albendazole/Mebendazole',
          indication: '🟡 Deworming - After stabilization phase (2nd week); high helminth burden in endemic areas',
          dose: 'Albendazole: >12 months: 400 mg single dose. Mebendazole: 500 mg single dose OR 100 mg twice daily for 3 days',
          route: 'PO',
          duration: 'Single dose deworming',
          contraindications: ['First week of treatment (too sick)', 'Children <12 months (albendazole)'],
          sideEffects: ['Abdominal discomfort', 'Worm expulsion', 'Rarely: Elevated LFTs'],
          alternatives: ['Either drug acceptable']
        },
        {
          drug: 'Artemether-Lumefantrine (or other ACT)',
          indication: '🔴 Malaria Treatment - If malaria positive; treat promptly; common comorbidity',
          dose: 'Weight-based dosing per national guidelines. Give with food (fatty food enhances absorption)',
          route: 'PO',
          duration: '3 days (standard ACT course)',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Nausea', 'Headache', 'Dizziness'],
          alternatives: ['Artesunate IV if severe malaria']
        },
        {
          drug: 'ReSoMal (Rehydration Solution for Malnutrition)',
          indication: '🔴 Dehydration Treatment - Special ORS for SAM; lower sodium, higher potassium',
          dose: '5-10 mL/kg/hour for first 2 hours, then 5-10 mL/kg/hour alternate with F-75. Max 70-100 mL/kg over 12 hours',
          route: 'PO or NG tube',
          duration: 'Until rehydrated (usually 4-10 hours)',
          contraindications: ['Shock (use IV fluids instead)', 'Profuse watery diarrhea (may need standard ORS)'],
          sideEffects: ['Fluid overload if given too fast'],
          alternatives: ['Modified ORS if ReSoMal unavailable (dilute standard ORS, add potassium and sugar)']
        },
        {
          drug: 'Glucose 10% IV',
          indication: '🔴 Hypoglycemia Treatment - If blood glucose <3 mmol/L (<54 mg/dL)',
          dose: '5 mL/kg of 10% glucose IV bolus. If no IV: Give 50 mL of 10% glucose or sugar water by NG tube',
          route: 'IV (preferred) or NG',
          duration: 'Until blood glucose stable; then feed frequently',
          contraindications: ['None in emergency'],
          sideEffects: ['Hyperglycemia if excessive', 'Fluid overload'],
          alternatives: ['Sugar-water solution orally/NG if conscious and no IV']
        },
        {
          drug: 'Packed Red Blood Cells (Transfusion)',
          indication: '🔴 Severe Anemia - Hb <4 g/dL OR Hb 4-6 g/dL with respiratory distress; transfuse slowly',
          dose: '10 mL/kg whole blood or 5-7 mL/kg packed cells over 3-4 hours. Do NOT exceed this',
          route: 'IV',
          duration: 'Single slow transfusion; reassess after',
          contraindications: ['Mild/moderate anemia (do not transfuse for Hb >6 without distress)'],
          sideEffects: ['Fluid overload', 'Transfusion reactions', 'Heart failure if given too fast/too much'],
          alternatives: ['Treat underlying cause; nutritional recovery improves anemia']
        },
      ],
      nonPharmacological: [
        'F-75 Therapeutic Milk (Stabilization Phase): 75 kcal/100mL; 130 mL/kg/day divided into 8-12 feeds (every 2-3 hours); low protein, moderate fat; prevents refeeding syndrome',
        'F-100 Therapeutic Milk (Rehabilitation Phase): 100 kcal/100mL; 150-220 mL/kg/day; higher protein for catch-up growth; start when appetite returns and edema reducing',
        'Ready-to-Use Therapeutic Food (RUTF): Outpatient treatment; energy-dense (500 kcal/92g sachet); 150-200 kcal/kg/day; no preparation needed; can be stored',
        'Feeding Frequency: Every 2-3 hours (8-12 feeds/day) including night feeds; smaller, frequent feeds reduce metabolic stress',
        'Thermal Care: Keep child warm; kangaroo care; warm room (25-30°C); warm blankets; skin-to-skin contact',
        'Gentle Handling: Minimize painful procedures; handle with care; these children are fragile',
        'Breastfeeding Support: Continue breastfeeding alongside therapeutic feeding; encourage exclusive breastfeeding in <6 months',
        'Stimulation and Play Therapy: Emotional and physical stimulation; play activities; developmental support; reduces long-term cognitive effects',
        'Caregiver Education: Teach feeding, hygiene, danger signs; prepare for discharge; follow-up adherence',
        'Sensory Stimulation: Age-appropriate toys, talking, singing; promotes neurodevelopment',
      ],
      surgical: [
        'Generally No Surgical Intervention: SAM is a medical condition managed with nutrition and supportive care',
        'Wound Care: If skin ulceration present; gentle cleaning, topical antiseptics, dressings; promote healing with nutrition',
        'NG Tube Placement: If child unable to eat orally; for F-75 administration; gentle placement',
        'IV Access: For fluids, glucose, medications in severely ill children; avoid if possible due to infection risk',
        'Emergency Procedures: May be needed for concurrent surgical conditions; high anesthetic risk in SAM',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Shock (cold hands, weak pulse, altered consciousness) → IV fluids, antibiotics, urgent stabilization',
        '🔴 EMERGENCY - Severe dehydration with SAM → Careful rehydration with ReSoMal; high mortality risk',
        '🔴 EMERGENCY - Hypoglycemia (glucose <3 mmol/L) → IV/NG glucose; frequent feeding',
        '🔴 EMERGENCY - Hypothermia (<35°C) → Urgent warming measures',
        '🔴 EMERGENCY - Severe anemia (Hb <4 or respiratory distress) → Slow blood transfusion',
        '🟠 URGENT - Failed appetite test → Inpatient care required; cannot manage as outpatient',
        '🟠 URGENT - Edema +++ (severe generalized edema) → Inpatient care required',
        '🟠 URGENT - Medical complications (severe infection, severe skin lesions, eye signs) → Inpatient care',
        '🟠 URGENT - Age <6 months or weight <4 kg → Inpatient with breastfeeding support',
        '🟡 ROUTINE - Uncomplicated SAM with passed appetite test → Outpatient Therapeutic Program (OTP) with RUTF',
        '🟡 ROUTINE - Not responding to outpatient treatment (weight loss or no gain for 3 weeks) → Reassess, consider inpatient',
      ],
      monitoring: [
        'Weight: Daily in stabilization phase; weight gain target in rehabilitation >5 g/kg/day; key indicator of recovery',
        'MUAC: Weekly in outpatient; improvement indicates recovery',
        'Edema: Daily assessment; should decrease progressively; persistence concerning',
        'Temperature: Every 3-4 hours initially; risk of hypothermia and fever',
        'Blood Glucose: On admission; if symptomatic; maintain frequent feeding to prevent hypoglycemia',
        'Feeding: Monitor intake at each feed; document amount consumed; adjust if not tolerating',
        'Stool Frequency and Consistency: Diarrhea common; watch for severe watery diarrhea',
        'Vital Signs: Respiratory rate, heart rate; watch for signs of infection, heart failure',
        'Mental Status: Alertness, interaction; improvement indicates recovery',
        'Skin Changes: Healing of dermatosis indicates recovery',
        'Hydration Status: Urine output, skin turgor (unreliable in SAM), clinical assessment',
        'Complications: Watch for refeeding syndrome, infection, heart failure',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

INPATIENT CARE PHASES:

Phase 1 - Stabilization (Days 1-7):
• Treat/prevent hypoglycemia, hypothermia, dehydration
• Treat infection (routine antibiotics)
• Correct electrolyte imbalances
• Start cautious feeding with F-75
• Micronutrient supplementation (Vitamin A, folic acid)
• NO iron supplementation yet

Transition Phase:
• Appetite returning
• Edema reducing
• Infections controlled
• Ready to transition to F-100 or RUTF

Phase 2 - Rehabilitation (Weeks 2-6):
• Rapid catch-up feeding (F-100 or RUTF)
• Target weight gain >5 g/kg/day
• Start iron supplementation
• Sensory stimulation and play
• Prepare for discharge

DISCHARGE CRITERIA:
• Weight-for-Height >-2 SD OR MUAC ≥125 mm on 2 consecutive measurements
• Edema resolved for at least 2 weeks
• Eating adequately
• All medical complications resolved
• Completed immunizations and vitamin A
• Caregiver understands home care and follow-up

OUTPATIENT THERAPEUTIC PROGRAM (OTP):

Weekly Visits:
• Weight and MUAC measurement
• Appetite assessment
• Clinical examination
• RUTF provision for next week
• Nutrition counseling

Duration: Until discharge criteria met (typically 6-8 weeks)

RUTF Dosing (Outpatient):
• 150-200 kcal/kg/day
• Approximately 2-3 sachets per day for average child
• Exact amount based on weight

POST-DISCHARGE FOLLOW-UP:
• 1 week after discharge
• Monthly for 3 months
• Weight and MUAC monitoring
• Nutrition counseling
• Supplementary feeding if needed
• Assess for relapse

REFERRAL BACK TO INPATIENT IF:
• Weight loss for 2 consecutive weeks
• No weight gain for 3 consecutive weeks
• Develops medical complications
• Edema increases
• Fails appetite test

LONG-TERM FOLLOW-UP:
• Growth monitoring
• Developmental assessment
• Catch-up immunizations
• Address underlying causes (poverty, food insecurity, HIV)
• Family support and counseling`
    },

    redFlags: [
      '🔴 CRITICAL: Shock - Cold hands, weak/absent pulse, altered consciousness → IV fluids (Ringers Lactate with 5% dextrose 15 mL/kg over 1 hour)',
      '🔴 CRITICAL: Hypoglycemia - Blood glucose <3 mmol/L, lethargy, seizures → IV 10% dextrose 5 mL/kg',
      '🔴 CRITICAL: Hypothermia - Temperature <35°C → Urgent warming; kangaroo care, warm blankets',
      '🔴 CRITICAL: Severe dehydration - Sunken eyes, skin pinch very slow → ReSoMal cautiously; avoid fluid overload',
      '🔴 CRITICAL: Severe anemia with respiratory distress - Hb <4 or <6 with distress → Slow transfusion 10 mL/kg over 3 hours',
      '🔴 CRITICAL: Sepsis/Severe infection - High fever, toxic appearance, altered mental status → IV antibiotics, supportive care',
      '🟠 URGENT: Eye signs of Vitamin A deficiency - Night blindness, Bitot spots, corneal xerosis → High-dose Vitamin A urgently',
      '🟠 URGENT: Severe skin lesions - Extensive raw skin, ulceration → Gentle care, topical antiseptics, systemic antibiotics',
      '🟠 URGENT: Persistent vomiting - Unable to tolerate oral feeds → NG tube, investigate cause',
      '🟠 URGENT: Worsening edema despite treatment - May indicate heart failure, infection, or refeeding issues → Review management',
      '🟡 WARNING: No weight gain after 3 weeks outpatient treatment → Reassess, consider inpatient referral',
      '🟡 WARNING: Heart failure signs - Tachycardia, tachypnea, hepatomegaly, jugular engorgement → Reduce fluids, sit upright, diuretics cautiously',
    ],

    complications: [
      'Hypoglycemia: Blood glucose <3 mmol/L; common and rapidly fatal; prevent with frequent feeding; treat with glucose',
      'Hypothermia: Temperature <35°C; due to loss of subcutaneous fat, low metabolic rate; keep warm',
      'Refeeding Syndrome: Rapid reintroduction of nutrition causes electrolyte shifts (hypophosphatemia, hypokalemia); prevent by slow feeding, use F-75 first',
      'Heart Failure: Due to electrolyte imbalance, anemia, fluid overload, infection; presents with tachycardia, respiratory distress; manage fluids carefully',
      'Infection/Sepsis: Occult infection common; impaired immunity; treat with routine antibiotics; high mortality if untreated',
      'Severe Anemia: Hb <6 g/dL common; transfuse only if Hb <4 or <6 with respiratory distress; slow transfusion',
      'Dehydration: Often overdiagnosed in SAM; use ReSoMal not standard ORS; avoid over-hydration',
      'Electrolyte Imbalances: Hypokalemia, hypomagnesemia, hypophosphatemia; addressed by therapeutic foods',
      'Vitamin A Deficiency: Night blindness, xerophthalmia, keratomalacia (corneal ulceration); can cause blindness; give Vitamin A',
      'Skin Ulceration and Infection: "Flaky paint" dermatosis can break down; wound care needed',
      'Developmental Delay: Cognitive and motor delays; stimulation therapy helps recovery',
      'Death: 30-50% without treatment; even 5-10% with treatment; highest risk in first 48-72 hours',
    ],

    prognosis: `OVERALL PROGNOSIS:

SURVIVAL:
• Without treatment: 30-50% mortality
• With appropriate treatment: <5% mortality in outpatient, <10% in inpatient
• Mortality highest in first 48-72 hours of admission
• Edematous SAM (kwashiorkor): Higher mortality than marasmus

RECOVERY:
• Most children recover fully with appropriate treatment
• Average treatment duration: 6-8 weeks
• Inpatient phase: 1-3 weeks
• Outpatient phase: 4-6 weeks

DISCHARGE CRITERIA:
• WFH >-2 SD OR MUAC ≥125 mm (sustained)
• No edema for 2 weeks
• Clinically well
• Eating adequately

FACTORS AFFECTING PROGNOSIS:

Better Prognosis:
• Uncomplicated SAM
• Early presentation
• Good appetite
• Younger age (better catch-up growth)
• No HIV infection
• No severe infection
• Supportive family

Worse Prognosis:
• Complicated SAM
• Delayed presentation
• Edema +++ (severe)
• HIV infection
• Severe infection/sepsis
• Persistent diarrhea
• Very young age (<6 months)
• Failed appetite test
• Underlying chronic disease
• Social factors (poverty, caregiver capacity)

LONG-TERM OUTCOMES:

Growth:
• Most achieve catch-up growth
• Some residual stunting possible
• Better outcomes with prolonged support

Neurodevelopment:
• Risk of cognitive impairment
• Stimulation during treatment helps
• Early intervention improves outcomes
• Some children have lasting effects

Immune Function:
• Increased susceptibility to infections for months
• Gradually normalizes with recovery

Relapse Risk:
• 10-20% relapse rate
• Address underlying causes
• Long-term follow-up important

AFRICAN CONTEXT:

• Higher mortality due to late presentation
• Limited inpatient capacity
• Community-based programs crucial
• HIV and TB co-morbidities affect outcomes
• Food insecurity contributes to relapse
• Seasonal peaks during hunger periods
• Investment in prevention essential`,

    prevention: `PRIMARY PREVENTION:

Optimal Infant and Young Child Feeding:
• Exclusive breastfeeding for first 6 months
• Continued breastfeeding until 2 years and beyond
• Appropriate complementary feeding from 6 months
• Dietary diversity

Maternal Nutrition:
• Good nutrition during pregnancy
• Iron and folic acid supplementation
• Micronutrient supplementation
• Adequate weight gain

Food Security:
• Household food security programs
• Agricultural support
• Social protection programs
• Cash transfers
• Food assistance when needed

Infection Prevention:
• Immunizations (including measles - major cause of malnutrition)
• Safe water and sanitation
• Hygiene promotion
• Prompt treatment of infections

SECONDARY PREVENTION (Early Detection):

Community-Based Screening:
• MUAC screening by community health workers
• Growth monitoring and promotion
• Early identification of at-risk children
• Referral of SAM cases

Growth Monitoring:
• Regular weighing at health facilities
• Plot on growth charts
• Identify faltering growth early
• Intervene before SAM develops

Supplementary Feeding Programs:
• For children with MAM (moderate acute malnutrition)
• Blanket feeding during emergencies
• Targeted supplementary feeding

TERTIARY PREVENTION (Preventing Complications and Relapse):

Appropriate Treatment:
• Follow WHO/national protocols
• Community-based management of acute malnutrition (CMAM)
• Quality inpatient care for complicated cases

Relapse Prevention:
• Address underlying causes
• Ensure food security
• Treat chronic conditions (HIV, TB)
• Long-term follow-up
• Family support

AFRICAN-SPECIFIC STRATEGIES:

• Integrate SAM management into primary healthcare
• Train community health workers in MUAC screening
• Ensure RUTF supply chains
• Support breastfeeding promotion
• Link to food security programs
• HIV testing and treatment
• TB screening and treatment
• School feeding programs
• Social protection for vulnerable families
• Emergency preparedness for hunger crises`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child's medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, take them to the doctor, hospital, or call emergency services IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING SEVERE ACUTE MALNUTRITION - INFORMATION FOR CAREGIVERS
═══════════════════════════════════════════════════════════════════════════════

WHAT IS SEVERE ACUTE MALNUTRITION (SAM)?
Severe acute malnutrition is a very serious condition where a child is extremely thin or has swelling of the body due to not getting enough food or the right kinds of food. It is a medical emergency that needs immediate treatment.

HOW DO I KNOW IF MY CHILD HAS SAM?
Signs include:
• Very thin - you can easily see the ribs and bones
• "Old man" face - loss of fat from the face
• Swelling of feet, legs, or face (in some cases)
• Hair changes - thin, discolored, easy to pull out
• Skin changes - dry, cracking, or peeling skin
• Weakness and tiredness
• Not eating well

WHY IS SAM DANGEROUS?
Without treatment, children with SAM can die. Their body cannot fight infections, keep warm, or work properly. With proper treatment, most children recover completely.

WHAT IS THE TREATMENT?

Special Therapeutic Foods:
• Your child will receive special foods designed to help them recover
• RUTF (Ready-to-Use Therapeutic Food) - a peanut-based paste in packets
• These foods have everything your child needs to recover

FOR RUTF TREATMENT AT HOME:
• Give the RUTF as instructed by the health worker
• Do NOT share RUTF with other family members - it is medicine for your sick child
• Give small amounts frequently throughout the day
• Continue breastfeeding if your child is breastfeeding
• Give clean water after RUTF
• Bring your child every week for check-up

⚠️ DANGER SIGNS - BRING YOUR CHILD TO THE HOSPITAL IMMEDIATELY IF:
• Not eating or refusing food
• High fever
• Very weak or unconscious
• Convulsions (fits)
• Vomiting everything
• Severe diarrhea (many watery stools)
• Difficult or fast breathing
• Very cold to touch
• Increased swelling

HOW TO FEED YOUR CHILD DURING RECOVERY:
• Feed frequently - at least 5-6 times per day
• Give small amounts each time
• Continue breastfeeding
• Be patient - it may take time for appetite to return
• Do not force feed - encourage gently
• Give RUTF first, then other foods

KEEPING YOUR CHILD WARM:
• Malnourished children get cold easily
• Keep your child clothed and covered
• Hold your child close to your body (skin-to-skin)
• Keep the room warm
• Do not bathe in cold water

HYGIENE:
• Wash hands before preparing food and feeding
• Keep food covered and clean
• Use clean water for drinking
• Keep your child clean

WHAT HAPPENS DURING TREATMENT?
• Week 1-2: Stabilization - treating immediate problems
• Week 2-6+: Recovery - gaining weight and strength
• Weekly visits to monitor progress
• Treatment continues until fully recovered

HOW LONG UNTIL MY CHILD RECOVERS?
• Most children recover in 6-8 weeks
• Some may take longer
• Keep coming for weekly visits until discharged
• Do not stop treatment early even if child looks better

AFTER RECOVERY:
• Continue feeding your child well
• Give diverse foods (grains, vegetables, fruits, proteins)
• Continue breastfeeding if under 2 years
• Bring for regular check-ups
• Complete all vaccinations

PREVENTING MALNUTRITION:
• Breastfeed exclusively for first 6 months
• After 6 months, continue breastfeeding AND give nutritious foods
• Give diverse foods - include proteins (eggs, beans, fish, meat), vegetables, fruits
• Feed frequently (young children have small stomachs)
• Prevent infections - wash hands, use clean water
• Complete all vaccinations
• Seek care early when child is sick

REMEMBER:
• SAM is treatable - most children recover fully
• Follow the health worker's instructions carefully
• Come to all scheduled appointments
• Do not share therapeutic food with other family members
• Bring child immediately if danger signs appear
• Continue feeding well after recovery to prevent relapse

Your child's health is in your hands. You can help your child recover and grow healthy!`,

    references: [
      'WHO. Guideline: Updates on the management of severe acute malnutrition in infants and children. Geneva: WHO; 2013. - WHO guidelines',
      'WHO. Pocket book of hospital care for children: Guidelines for the management of common childhood illnesses. 2nd ed. Geneva: WHO; 2013. - IMCI reference',
      'Collins S, et al. Management of severe acute malnutrition in children. Lancet. 2006;368:1992-2000. - Lancet review',
      'Black RE, et al. Maternal and child undernutrition and overweight in low-income and middle-income countries. Lancet. 2013;382:427-451. - Global burden',
      'UNICEF/WHO/World Bank. Levels and trends in child malnutrition. Joint child malnutrition estimates. 2021. - Global estimates',
      'Briend A, et al. Ready-to-use therapeutic food for treatment of marasmus. Lancet. 1999;353:1767-1768. - RUTF development',
      'Ciliberto MA, et al. Comparison of home-based therapy with ready-to-use therapeutic food with standard therapy in the treatment of malnourished Malawian children. Am J Clin Nutr. 2005;81:864-870. - RUTF evidence',
      'Kerac M, et al. Probiotics and prebiotics for severe acute malnutrition (PRONUT study). Lancet. 2009;374:136-144. - PRONUT study',
      'Trehan I, et al. Antibiotics as part of the management of severe acute malnutrition. N Engl J Med. 2013;368:425-435. - Antibiotics in SAM',
      'FANTA. Training guide for community-based management of acute malnutrition (CMAM). Washington: USAID; 2018. - Implementation guide',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 39. PEDIATRIC DEHYDRATION AND ACUTE DIARRHEA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '39',
    name: 'Pediatric Dehydration and Acute Diarrhea',
    slug: 'pediatric-dehydration-diarrhea',
    specialty: 'Pediatrics',
    bodySystem: 'Gastrointestinal',
    icdCode: 'A09',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Acute diarrhea is defined as the passage of three or more loose or watery stools per day (or more frequent passage than normal for the individual), lasting less than 14 days. Dehydration, the loss of water and electrolytes, is the most dangerous complication of diarrhea and the primary cause of death. Diarrheal diseases remain one of the leading causes of death in children under 5 years globally.

Diarrhea kills approximately 525,000 children under 5 years annually, with the majority of deaths occurring in Sub-Saharan Africa and South Asia. In Africa, rotavirus is the leading cause of severe diarrhea, followed by bacterial pathogens (Shigella, Salmonella, pathogenic E. coli) and parasites (Giardia, Cryptosporidium, Entamoeba). Most diarrheal deaths are preventable with oral rehydration therapy (ORT), zinc supplementation, and continued feeding.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• ASSESS DEHYDRATION: No dehydration, Some dehydration, or Severe dehydration (WHO classification)
• ORS is the CORNERSTONE of treatment - Prevents and treats dehydration; reduces mortality by up to 93%
• ZINC for 10-14 days - Reduces duration, severity, and future diarrhea episodes
• CONTINUE FEEDING - Do not starve the gut; continue breastfeeding; early refeeding improves outcomes
• Severe dehydration = MEDICAL EMERGENCY - IV Ringer's Lactate (or Normal Saline) 100 mL/kg
• Antibiotics ONLY for: Bloody diarrhea (dysentery), cholera, proven bacterial infection
• Do NOT use anti-diarrheal drugs (loperamide) in children - dangerous
• Low-osmolarity ORS (245 mOsm/L) is superior to standard ORS
• Rotavirus vaccine prevents most severe diarrhea cases
• Persistent diarrhea (>14 days) needs different approach - nutritional rehabilitation, investigate cause`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 1.7 billion episodes of childhood diarrhea annually
• 525,000 deaths per year in children under 5
• Second leading cause of death in children under 5 (after pneumonia)
• Accounts for 8% of all deaths in children under 5
• 88% of diarrheal deaths attributable to unsafe water, sanitation, hygiene

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa has highest diarrheal mortality rates
• Estimated 30-50% of under-5 deaths in some regions
• Average African child: 4-5 diarrhea episodes per year
• Highest burden: DRC, Nigeria, Ethiopia, Niger, Chad
• Seasonal peaks: Rainy season (bacterial), dry season (rotavirus)

ETIOLOGY:

Viral (Most Common - 70%):
• Rotavirus: Leading cause of severe dehydrating diarrhea; vaccine preventable
• Norovirus: Common, highly contagious
• Adenovirus: Enteric types 40, 41
• Astrovirus

Bacterial (15-20%):
• Shigella: Bloody diarrhea (dysentery); most common bacterial cause of bloody diarrhea
• Salmonella: Especially non-typhoidal Salmonella
• Campylobacter: Common worldwide
• Enterotoxigenic E. coli (ETEC): Traveler's diarrhea, endemic areas
• Enteropathogenic E. coli (EPEC): Infants
• Vibrio cholerae: Epidemic diarrhea; "rice water" stools

Parasitic (5-10%):
• Giardia lamblia: Common, persistent diarrhea
• Cryptosporidium: Especially in HIV; severe in immunocompromised
• Entamoeba histolytica: Bloody diarrhea (amoebic dysentery)

RISK FACTORS:
• Age <2 years (highest risk)
• Malnutrition (increases severity and mortality)
• Lack of exclusive breastfeeding
• Contaminated water and food
• Poor sanitation and hygiene
• Measles (recent or current)
• Immunodeficiency (HIV, malnutrition)
• Lack of rotavirus vaccination
• Crowding
• Low maternal education

CLASSIFICATION BY DURATION:
• Acute diarrhea: <14 days
• Persistent diarrhea: 14-30 days
• Chronic diarrhea: >30 days

CLASSIFICATION BY TYPE:
• Watery diarrhea: Most common; rotavirus, cholera, ETEC
• Bloody diarrhea (Dysentery): Shigella, Campylobacter, EIEC, Entamoeba
• Persistent diarrhea: Protracted episodes; nutritional impact`,

    clinicalPresentation: `SYMPTOMS OF DIARRHEA:

• Increased frequency of stools (≥3 per day)
• Loose or watery consistency
• May have mucus
• Blood in stool (dysentery)
• Abdominal cramps
• Nausea and vomiting
• Fever (suggests infection)
• Poor feeding
• Irritability or lethargy

CLINICAL FEATURES BY CAUSE:

Rotavirus:
• Profuse watery diarrhea
• Vomiting (often precedes diarrhea)
• Fever
• Rapid dehydration
• Winter/dry season peaks
• Age 6 months - 2 years most affected

Cholera:
• Profuse watery "rice water" stools
• No fever (afebrile)
• No abdominal pain
• Rapid, severe dehydration
• Vomiting
• Muscle cramps (electrolyte loss)
• Can lose 10-20 L/day in severe cases

Shigella (Bacillary Dysentery):
• Bloody, mucoid diarrhea
• High fever
• Abdominal cramps
• Tenesmus (painful straining)
• Small frequent stools
• May have seizures (in young children)

Amebic Dysentery:
• Bloody diarrhea
• Less fever than Shigella
• Gradual onset
• Abdominal tenderness
• May have liver abscess

DEHYDRATION ASSESSMENT (WHO Classification):

SEVERE DEHYDRATION (≥2 of these signs):
• Lethargic or unconscious
• Sunken eyes
• Unable to drink or drinks poorly
• Skin pinch goes back very slowly (>2 seconds)
→ Treat with Plan C (IV fluids)

SOME DEHYDRATION (≥2 of these signs):
• Restless, irritable
• Sunken eyes
• Drinks eagerly, thirsty
• Skin pinch goes back slowly (1-2 seconds)
→ Treat with Plan B (ORS in facility)

NO DEHYDRATION:
• Well, alert
• Eyes normal
• Drinks normally, not thirsty
• Skin pinch goes back immediately
→ Treat with Plan A (ORS at home)

ADDITIONAL SIGNS TO ASSESS:

General Condition:
• Alert → Restless/Irritable → Lethargic/Unconscious

Eyes:
• Normal → Sunken → Very sunken and dry

Tears:
• Present → Absent

Mouth and Tongue:
• Moist → Dry → Very dry

Thirst:
• Drinks normally → Thirsty, drinks eagerly → Unable to drink

Skin Pinch (Abdominal):
• Goes back immediately → Goes back slowly (<2 sec) → Goes back very slowly (>2 sec)

SIGNS OF SHOCK:
• Cold, clammy extremities
• Weak or absent pulse
• Capillary refill >3 seconds
• Low blood pressure
• Altered consciousness
→ EMERGENCY - Treat as severe dehydration with shock`,

    diagnosis: `DIAGNOSTIC APPROACH:

Step 1: Assess and classify dehydration (WHO criteria)
Step 2: Identify type of diarrhea (watery vs bloody)
Step 3: Look for associated conditions (malnutrition, other infections)
Step 4: Determine need for investigations
Step 5: Select treatment plan (A, B, or C)

CLINICAL DIAGNOSIS:
• Diarrhea is primarily a clinical diagnosis
• Most cases do not need laboratory investigations
• Focus on hydration assessment and treatment

INDICATIONS FOR LABORATORY TESTS:

Stool Examination:
• Bloody diarrhea (dysentery)
• Persistent diarrhea (>14 days)
• Suspected cholera outbreak
• Immunocompromised patient
• Treatment failure

Tests to Consider:
• Stool microscopy: WBC, RBC, parasites
• Stool culture: Bacterial pathogens
• Rotavirus antigen test
• Ova and parasites
• C. difficile if antibiotic-associated

STOOL EXAMINATION FINDINGS:

| Finding | Suggests |
|---------|----------|
| WBC/Pus cells | Invasive bacterial (Shigella, Campylobacter) |
| RBC | Dysentery (Shigella, Entamoeba, EIEC) |
| Trophozoites | Giardia, Entamoeba |
| Cysts | Giardia, Entamoeba |
| Oocysts | Cryptosporidium |
| "Rice water" appearance | Cholera |

WHEN TO SUSPECT CHOLERA:
• Age >2 years with acute watery diarrhea AND
• Severe dehydration OR
• Known cholera outbreak in area

BLOOD TESTS (if available and indicated):
• Electrolytes: Hyponatremia, hypokalemia common
• Blood glucose: Hypoglycemia in malnourished
• Creatinine: Renal function in severe dehydration
• Blood culture: If sepsis suspected

HIV TESTING:
• Consider in persistent diarrhea
• Cryptosporidium common in HIV
• Affects prognosis and management

DIFFERENTIAL DIAGNOSIS OF BLOODY DIARRHEA:

Infectious:
• Shigella (most common bacterial cause)
• Campylobacter
• Salmonella
• Enteroinvasive E. coli (EIEC)
• Entamoeba histolytica

Non-Infectious:
• Intussusception (currant jelly stool, colicky pain)
• Inflammatory bowel disease (chronic, older children)
• Meckel's diverticulum
• Cow's milk protein allergy (infants)`,

    differentialDiagnosis: [
      'Intussusception - Colicky abdominal pain, currant jelly stool, sausage-shaped mass; surgical emergency',
      'Appendicitis - Right lower quadrant pain, fever, vomiting; diarrhea may be present',
      'Malaria - Fever, may have vomiting and diarrhea; test in endemic areas',
      'Urinary tract infection - Fever, vomiting; may have diarrhea in infants; check urinalysis',
      'Pneumonia - Respiratory symptoms; abdominal pain and vomiting can occur; check respiratory rate',
      'Meningitis - Fever, lethargy, irritability; may have vomiting and diarrhea',
      'Sepsis - Toxic appearance, fever or hypothermia; diarrhea may be a manifestation',
      'Food poisoning - Acute onset after specific meal; vomiting prominent; often multiple people affected',
      'Antibiotic-associated diarrhea - History of recent antibiotic use; consider C. difficile',
      'Malabsorption syndromes - Chronic/persistent diarrhea; failure to thrive; investigate cause',
      'Inflammatory bowel disease - Chronic bloody diarrhea; weight loss; older children/adolescents',
      'Lactose intolerance - Watery diarrhea after milk; bloating; can be secondary to acute diarrhea',
      'Celiac disease - Chronic diarrhea, failure to thrive; after introduction of gluten',
    ],

    investigations: [
      {
        name: 'Clinical Dehydration Assessment',
        indication: '🔴 IMMEDIATE - Classify dehydration using WHO criteria; determines treatment plan (A, B, or C)',
        availability: 'BASIC'
      },
      {
        name: 'Stool Microscopy',
        indication: '🟠 URGENT - Bloody diarrhea (dysentery); persistent diarrhea; look for WBC, RBC, parasites',
        availability: 'BASIC'
      },
      {
        name: 'Stool Culture',
        indication: '🟡 Routine - Bloody diarrhea; treatment failure; suspected outbreak; identify bacterial pathogen',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Rotavirus Antigen Test',
        indication: '🟡 Routine - Severe watery diarrhea in young children; epidemiological surveillance',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Stool for Ova and Parasites',
        indication: '🟡 Routine - Persistent diarrhea; suspected parasitic infection; multiple specimens increase yield',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE - Malnourished children; altered consciousness; hypoglycemia common',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: '🟠 URGENT - Severe dehydration; altered mental status; prolonged diarrhea; guide IV fluid composition',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 Routine - Suspected bacterial infection; assess for anemia; leukocytosis suggests bacterial',
        availability: 'BASIC'
      },
      {
        name: 'Blood Urea/Creatinine',
        indication: '🟠 URGENT - Severe dehydration; assess renal function; pre-renal azotemia common',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Malaria Rapid Test',
        indication: '🔴 IMMEDIATE - Febrile child with diarrhea in endemic area; malaria can cause diarrhea',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 URGENT - Persistent/recurrent diarrhea; failure to thrive; suspected immunodeficiency',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🟡 Routine - Fever without clear source; specific gravity indicates hydration status',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal X-ray',
        indication: '🟠 URGENT - Suspected obstruction or intussusception; abdominal distension',
        availability: 'INTERMEDIATE'
      },
    ],

    management: {
      immediate: [
        '⚡ ASSESS DEHYDRATION - Classify as None, Some, or Severe using WHO criteria',
        '⚡ SEVERE DEHYDRATION: Start IV Ringer Lactate 100 mL/kg immediately (Plan C)',
        '⚡ SHOCK: IV bolus 20 mL/kg Ringer Lactate or Normal Saline over 15-20 minutes, repeat as needed',
        '⚡ Check blood glucose in malnourished or lethargic children',
        '⚡ START ORS as soon as child can drink (even with IV running)',
        '⚡ ZINC supplementation - Start immediately, continue for 10-14 days',
        '⚡ CONTINUE FEEDING - Do not withhold food; continue breastfeeding',
        '⚡ Assess for bloody diarrhea (dysentery) - May need antibiotics',
        '⚡ Assess for severe malnutrition - Different fluid management (use ReSoMal)',
        '⚡ Look for danger signs: Convulsions, very sleepy/unconscious, unable to drink',
        '⚡ Test for malaria in endemic areas if febrile',
        '⚡ Monitor: Urine output, level of consciousness, ability to drink, ongoing losses',
      ],
      pharmacological: [
        {
          drug: 'Oral Rehydration Solution (ORS)',
          indication: '🔴 First-line Treatment - ALL diarrhea with dehydration; low-osmolarity ORS (245 mOsm/L) preferred',
          dose: 'Plan A (No dehydration): 50-100 mL after each loose stool (<2y), 100-200 mL (≥2y). Plan B (Some dehydration): 75 mL/kg over 4 hours',
          route: 'PO (or NG tube if unable to drink)',
          duration: 'Until diarrhea stops and hydration restored',
          contraindications: ['Severe dehydration (start IV first)', 'Ileus', 'Shock'],
          sideEffects: ['Vomiting (give small frequent sips)', 'Hypernatremia (rare with low-osmolarity ORS)'],
          alternatives: ['Home fluids for mild cases if ORS unavailable; ReSoMal for malnourished children']
        },
        {
          drug: 'Zinc Sulfate',
          indication: '🔴 Essential Treatment - ALL children with diarrhea; reduces duration, severity, and future episodes',
          dose: '<6 months: 10 mg once daily. ≥6 months: 20 mg once daily. Give for 10-14 days',
          route: 'PO (dispersible tablet or syrup)',
          duration: '10-14 days (complete course even if diarrhea stops)',
          contraindications: ['None significant'],
          sideEffects: ['Vomiting if taken on empty stomach (give with food)', 'Metallic taste'],
          alternatives: ['Zinc gluconate or other zinc salts']
        },
        {
          drug: 'Ringer Lactate (IV Fluids)',
          indication: '🔴 Severe Dehydration - Plan C; first-line IV fluid; contains potassium and is buffered',
          dose: 'Infants <12 mo: 30 mL/kg in 1 hour, then 70 mL/kg in 5 hours. Children 1-5 y: 30 mL/kg in 30 min, then 70 mL/kg in 2.5 hours',
          route: 'IV',
          duration: 'Until able to take adequate ORS; typically 3-6 hours',
          contraindications: ['None in emergency dehydration'],
          sideEffects: ['Fluid overload (monitor carefully)', 'Electrolyte imbalance'],
          alternatives: ['Normal Saline if Ringer Lactate unavailable (add potassium when able)']
        },
        {
          drug: 'Normal Saline 0.9%',
          indication: '🔴 Alternative IV Fluid - If Ringer Lactate unavailable; lacks potassium and buffer',
          dose: 'Same as Ringer Lactate. Add KCl 20 mEq/L when patient is urinating',
          route: 'IV',
          duration: 'Until oral intake adequate',
          contraindications: ['None in emergency'],
          sideEffects: ['Hyperchloremic acidosis with large volumes', 'Hypokalemia (lacks K+)'],
          alternatives: ['Ringer Lactate preferred']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟠 Bloody Diarrhea (Dysentery) - First-line antibiotic for shigellosis; only for bloody diarrhea',
          dose: 'Children: 15 mg/kg twice daily (max 500 mg/dose) for 3 days',
          route: 'PO',
          duration: '3 days',
          contraindications: ['Known hypersensitivity', 'Relative: Children (theoretical cartilage risk, but WHO recommends for dysentery)'],
          sideEffects: ['GI upset', 'Headache', 'Theoretical tendon/cartilage effects (rare in short course)'],
          alternatives: ['Azithromycin', 'Ceftriaxone if severe', 'Pivmecillinam']
        },
        {
          drug: 'Azithromycin',
          indication: '🟠 Alternative for Dysentery - If ciprofloxacin unavailable or resistance suspected; also for cholera',
          dose: 'Dysentery: 10-12 mg/kg once daily for 3 days (max 500 mg/day). Cholera: 20 mg/kg single dose (max 1g)',
          route: 'PO',
          duration: '1-3 days depending on indication',
          contraindications: ['Macrolide allergy', 'QT prolongation'],
          sideEffects: ['GI upset', 'Abdominal pain', 'QT prolongation (rare)'],
          alternatives: ['Ciprofloxacin', 'Ceftriaxone']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 Severe Dysentery/Sepsis - For severe cases requiring IV antibiotics; suspected bacteremia',
          dose: 'Children: 50-75 mg/kg IV once daily (max 2 g)',
          route: 'IV or IM',
          duration: '3-5 days',
          contraindications: ['Cephalosporin allergy', 'Hyperbilirubinemia in neonates'],
          sideEffects: ['Allergic reactions', 'Biliary sludge', 'Diarrhea'],
          alternatives: ['Ampicillin + Gentamicin if ceftriaxone unavailable']
        },
        {
          drug: 'Metronidazole',
          indication: '🟠 Amebic Dysentery/Giardiasis - For confirmed or suspected Entamoeba or Giardia infection',
          dose: 'Amebiasis: 30-50 mg/kg/day divided TID for 7-10 days (max 750 mg/dose). Giardiasis: 15 mg/kg/day divided TID for 5-7 days',
          route: 'PO',
          duration: '5-10 days depending on indication',
          contraindications: ['First trimester pregnancy', 'Alcohol use'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol'],
          alternatives: ['Tinidazole (single dose option for Giardia)']
        },
        {
          drug: 'Doxycycline (Cholera)',
          indication: '🟠 Cholera - Reduces stool output and duration in confirmed/suspected cholera',
          dose: 'Children >8 years: 2-4 mg/kg single dose (max 300 mg). Alternative: Azithromycin for younger children',
          route: 'PO',
          duration: 'Single dose',
          contraindications: ['Children <8 years (tooth discoloration)', 'Pregnancy'],
          sideEffects: ['GI upset', 'Photosensitivity', 'Tooth discoloration in young children'],
          alternatives: ['Azithromycin (preferred for children <8)']
        },
        {
          drug: 'Ondansetron',
          indication: '🟡 Severe Vomiting - If vomiting prevents oral rehydration; NOT routine',
          dose: 'Children: 0.15 mg/kg IV/PO (max 4 mg) single dose',
          route: 'IV, IM, or PO (ODT)',
          duration: 'Single dose; may repeat once',
          contraindications: ['QT prolongation', 'Known hypersensitivity'],
          sideEffects: ['Headache', 'Constipation', 'QT prolongation (rare)'],
          alternatives: ['Domperidone (where available)']
        },
        {
          drug: 'Vitamin A',
          indication: '🟡 Measles-Associated Diarrhea - If measles within last 3 months; routine in persistent diarrhea',
          dose: '6-12 months: 100,000 IU. >12 months: 200,000 IU. Repeat Day 2 if eye signs',
          route: 'PO',
          duration: 'Single dose (repeat if deficiency signs)',
          contraindications: ['Recent high-dose vitamin A supplementation'],
          sideEffects: ['Transient headache', 'Nausea'],
          alternatives: ['None; essential if indicated']
        },
      ],
      nonPharmacological: [
        'Oral Rehydration Therapy (ORT): Cornerstone of treatment; teach caregivers to give ORS correctly; small frequent sips if vomiting',
        'Continued Feeding: Do NOT starve the gut; continue breastfeeding throughout; resume age-appropriate diet as soon as tolerated',
        'Breastfeeding: Continue and increase frequency during diarrhea; protective effect; provides fluid and nutrition',
        'Complementary Foods: Offer easily digestible foods; avoid high-fiber, fatty foods initially; increase frequency',
        'Nasogastric Rehydration: If child unable to drink but not in shock; NG ORS at 20 mL/kg/hour until hydrated',
        'Monitoring: Teach caregivers to monitor urine output, hydration signs, ongoing losses; when to return',
        'Hand Hygiene Education: Handwashing with soap prevents transmission; before food preparation, after toilet/diapers',
        'Safe Water: Advise on water treatment if needed; boiling, chlorination, filtration',
        'Food Safety: Proper food storage and preparation; avoid contaminated food',
        'Exclusive Breastfeeding Promotion: For infants <6 months; prevents diarrheal disease',
      ],
      surgical: [
        'Generally No Surgical Intervention: Diarrhea is managed medically',
        'Intussusception: If suspected (currant jelly stool, mass, bilious vomiting); air or contrast enema for reduction; surgery if fails',
        'Appendicitis: If differential diagnosis; surgical consultation',
        'Bowel Obstruction: If suspected; surgical evaluation',
        'Intraosseous Access: For fluid resuscitation if IV access impossible in severe dehydration with shock',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Severe dehydration with shock → IV fluids urgently; may need intensive care',
        '🔴 EMERGENCY - Unable to drink or retain fluids → IV or NG rehydration required',
        '🔴 EMERGENCY - Convulsions or altered consciousness → Assess for hypoglycemia, severe dehydration, meningitis',
        '🔴 EMERGENCY - Signs of surgical abdomen (intussusception, obstruction) → Urgent surgical referral',
        '🟠 URGENT - Severe malnutrition with diarrhea → Inpatient care; modified rehydration (ReSoMal)',
        '🟠 URGENT - Bloody diarrhea (dysentery) not responding to treatment → Reassess, consider resistant organism',
        '🟠 URGENT - Cholera or suspected cholera outbreak → Treatment center; infection control',
        '🟡 ROUTINE - Persistent diarrhea (>14 days) → Investigate cause; nutritional rehabilitation',
        '🟡 ROUTINE - Recurrent diarrhea → Investigate underlying cause; consider HIV testing',
        '🟡 ROUTINE - Failure to improve with standard treatment → Reassess diagnosis and management',
      ],
      monitoring: [
        'Hydration Status: Reassess every 1-2 hours during rehydration; use WHO criteria',
        'Urine Output: Key indicator of hydration; should urinate within 6-8 hours of starting treatment',
        'Ongoing Losses: Estimate stool and vomiting losses; replace with ORS',
        'Ability to Drink: Monitor oral intake; transition from IV to oral when able',
        'Weight: Daily weight helps assess hydration; 1 kg = approximately 1 L fluid',
        'Vital Signs: Heart rate, respiratory rate (tachycardia suggests ongoing dehydration)',
        'Mental Status: Alertness should improve with rehydration; lethargy is danger sign',
        'Stool Output: Frequency, volume, blood; should decrease with treatment',
        'Blood Glucose: In malnourished children; risk of hypoglycemia',
        'Electrolytes: If available; particularly in severe or prolonged cases',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

ACUTE DIARRHEA:

Plan A (Home Treatment):
• Return if: Not better in 3 days, develops danger signs, blood in stool, unable to drink
• Follow-up: As needed; routine well-child visit

Plan B (Facility ORS):
• Reassess after 4 hours of ORS treatment
• If improved: Send home with Plan A instructions, zinc, and ORS packets
• If same or worse: Continue Plan B or escalate to Plan C

Plan C (Severe Dehydration):
• Continuous monitoring until stable
• Reassess every 15-30 minutes during IV rehydration
• Transition to ORS when able to drink
• Observe for 6-24 hours after rehydration
• Discharge with Plan A instructions, zinc, and ORS

POST-DISCHARGE:
• Continue zinc for 10-14 days (complete course)
• Return if diarrhea worsens or danger signs develop
• Follow-up in 5-7 days if not resolved
• Well-child visit for growth monitoring

PERSISTENT DIARRHEA (>14 days):
• Needs careful evaluation
• Stool examination for parasites
• Nutritional assessment and rehabilitation
• Consider underlying conditions (HIV, malabsorption)
• Weekly follow-up until resolved

DYSENTERY:
• Reassess in 2-3 days
• If not improving: Stool culture, consider alternative antibiotic
• Complete antibiotic course

PREVENTION COUNSELING:
• Exclusive breastfeeding for 6 months
• Handwashing with soap
• Safe water and food
• Rotavirus vaccination
• Proper sanitation
• Vitamin A supplementation
• Early ORS use at home for future episodes`
    },

    redFlags: [
      '🔴 CRITICAL: Severe dehydration with shock - Cold extremities, weak/absent pulse, capillary refill >3 sec → IV bolus 20 mL/kg immediately',
      '🔴 CRITICAL: Unable to drink or unconscious - Cannot take ORS → IV or NG rehydration urgently',
      '🔴 CRITICAL: Convulsions - May indicate severe dehydration, hypoglycemia, electrolyte imbalance, or meningitis → Emergency management',
      '🔴 CRITICAL: Suspected intussusception - Bilious vomiting, currant jelly stool, intermittent severe colicky pain → Urgent surgical referral',
      '🔴 CRITICAL: Severe malnutrition with dehydration - Different fluid protocol needed (ReSoMal) → Inpatient care',
      '🟠 URGENT: Bloody diarrhea (dysentery) - Needs antibiotic treatment; assess severity',
      '🟠 URGENT: Profuse watery diarrhea (suspect cholera) - Rapid dehydration; may need treatment center; contact tracing',
      '🟠 URGENT: High fever with toxic appearance - May indicate bacteremia or sepsis → Blood culture, empiric antibiotics',
      '🟠 URGENT: Persistent vomiting preventing ORS - Consider antiemetic; may need NG or IV fluids',
      '🟠 URGENT: Abdominal distension - May indicate ileus, obstruction, or severe electrolyte imbalance',
      '🟡 WARNING: Persistent diarrhea >14 days - Needs investigation; nutritional rehabilitation; consider HIV',
      '🟡 WARNING: Not improving after 3 days - Reassess diagnosis and treatment; consider other causes',
    ],

    complications: [
      'Severe Dehydration: Life-threatening; leads to shock, organ failure, death; prevented by ORT',
      'Hypovolemic Shock: End-stage dehydration; cold extremities, weak pulse, altered consciousness; needs IV resuscitation',
      'Electrolyte Imbalances: Hyponatremia, hypokalemia, metabolic acidosis; corrected with proper ORS/IV fluids',
      'Hypoglycemia: Especially in malnourished children; can cause seizures, brain damage; check glucose, treat promptly',
      'Seizures: From severe dehydration, hypoglycemia, electrolyte imbalance, or high fever',
      'Acute Kidney Injury: From severe dehydration; usually reversible with rehydration',
      'Malnutrition: Recurrent diarrhea leads to malnutrition; malnutrition increases diarrhea severity (vicious cycle)',
      'Persistent Diarrhea: Failure to resolve; requires investigation and nutritional rehabilitation',
      'Secondary Lactose Intolerance: Temporary after severe diarrhea; usually resolves; lactose-free formula if severe',
      'Rectal Prolapse: With severe straining (especially Shigella); usually self-resolving',
      'Hemolytic Uremic Syndrome (HUS): Rare; associated with STEC (E. coli O157:H7); renal failure, anemia, thrombocytopenia',
      'Reactive Arthritis: Post-infectious; especially with Shigella, Salmonella, Campylobacter',
      'Death: 525,000 deaths annually in children; almost all preventable with ORT, zinc, continued feeding',
    ],

    prognosis: `OVERALL PROGNOSIS:

SURVIVAL:
• With appropriate treatment (ORS, zinc, continued feeding): >99% survival
• Without treatment: 30-50% mortality from severe dehydration
• ORS alone reduces mortality by up to 93%

RECOVERY TIME:
• Most acute diarrhea: 3-7 days
• Viral gastroenteritis: 3-5 days
• Bacterial dysentery: 5-7 days with treatment
• Cholera: 3-5 days with treatment

FACTORS AFFECTING PROGNOSIS:

Better Prognosis:
• Early ORS initiation
• Continued breastfeeding
• Good nutritional status
• Access to healthcare
• Rotavirus vaccination

Worse Prognosis:
• Severe malnutrition
• Delayed treatment
• Very young age (<6 months)
• HIV infection
• Persistent/chronic diarrhea
• Lack of access to ORS/clean water
• Associated measles

LONG-TERM OUTCOMES:

Growth:
• Recurrent diarrhea impairs growth
• Catch-up growth with nutrition
• Stunting if repeated episodes

Cognitive Development:
• Early childhood diarrhea associated with cognitive deficits
• Improved with nutrition and stimulation

Immunity:
• Repeated infections build some immunity
• Rotavirus vaccine highly effective

GLOBAL TRENDS:
• Diarrheal deaths have decreased 60% since 1990
• Due to ORS, zinc, rotavirus vaccine, improved WASH
• Still 525,000 deaths annually - almost all preventable

AFRICAN CONTEXT:
• Highest mortality rates globally
• Limited access to ORS in some areas
• Low rotavirus vaccine coverage
• Water and sanitation challenges
• Malnutrition increases severity
• Prevention is key investment`,

    prevention: `PRIMARY PREVENTION:

Rotavirus Vaccination:
• Most effective intervention for severe diarrhea
• WHO recommends for all infants
• Two doses (some vaccines three)
• Reduces severe rotavirus by 80-90%
• Safe and effective

Exclusive Breastfeeding:
• First 6 months of life
• Protective antibodies
• Prevents contaminated food/water exposure
• Continue to 2 years with complementary foods

Water, Sanitation, and Hygiene (WASH):
• Safe drinking water (treat if needed)
• Improved sanitation (latrines)
• Handwashing with soap (critical times)
• Safe food preparation

Handwashing:
• Before preparing food
• Before feeding child
• After using toilet
• After changing diapers
• After handling animals

Safe Food Practices:
• Cook food thoroughly
• Eat cooked food while hot
• Store food safely (refrigerate)
• Wash fruits and vegetables
• Avoid raw foods if uncertain

Vitamin A Supplementation:
• Every 6 months from 6 months to 5 years
• Reduces diarrhea severity and mortality

Measles Vaccination:
• Measles increases diarrhea risk and severity
• Complete vaccination schedule

SECONDARY PREVENTION:

Early ORS Use:
• Start ORS at home at onset of diarrhea
• Every household should have ORS
• Prevents progression to dehydration

Zinc Supplementation:
• Reduces duration and severity
• Reduces future episodes for 2-3 months
• Include in all diarrhea treatment

Continued Feeding:
• Do not starve the gut
• Maintains nutritional status
• Prevents malnutrition cycle

COMMUNITY STRATEGIES:

ORS Availability:
• Community-based distribution
• Pharmacies and shops
• Health facilities at all levels

Community Health Workers:
• Train in ORS preparation
• Danger sign recognition
• Referral for severe cases

Health Education:
• Diarrhea prevention and treatment
• When to seek care
• ORS preparation at home

AFRICAN-SPECIFIC STRATEGIES:

• Scale up rotavirus vaccine coverage
• Improve water and sanitation infrastructure
• Train community health workers
• Ensure ORS and zinc availability
• Integrate diarrhea management into primary care
• Community-based management programs
• Emergency preparedness for outbreaks
• Address malnutrition (increases diarrhea severity)`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child's medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, take them to the hospital IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING DIARRHEA IN CHILDREN - INFORMATION FOR CAREGIVERS
═══════════════════════════════════════════════════════════════════════════════

WHAT IS DIARRHEA?
Diarrhea is when your child passes 3 or more loose or watery stools in a day. It is very common in children, especially those under 5 years. Most diarrhea is caused by germs (viruses or bacteria) and will get better on its own. The danger is DEHYDRATION - losing too much water from the body.

WHY IS DIARRHEA DANGEROUS?
The main danger is dehydration. When children lose water through diarrhea and vomiting faster than they can replace it, they become dehydrated. Severe dehydration can cause death if not treated quickly.

SIGNS OF DEHYDRATION (Take your child for treatment):
• Very thirsty
• Dry mouth and tongue
• Sunken eyes
• Crying with few or no tears
• Skin that stays pinched when you pinch and release it
• Less urine than normal (fewer wet diapers)
• Restless or irritable

DANGER SIGNS (Go to hospital IMMEDIATELY):
• Unable to drink or breastfeed
• Very sleepy or difficult to wake
• Vomiting everything
• Blood in the stool
• Convulsions (fits)
• Very weak or floppy
• Sunken soft spot on baby's head

HOW TO TREAT DIARRHEA AT HOME:

1. GIVE ORS (Oral Rehydration Solution):
• ORS is the most important treatment
• Mix the packet with the correct amount of clean water
• Give small sips frequently (not large amounts at once)
• If child vomits, wait 10 minutes, then continue smaller sips
• Continue ORS until diarrhea stops

How much ORS to give after each loose stool:
• Less than 2 years: 50-100 mL (about half a cup)
• 2 years and older: 100-200 mL (about a cup)

2. GIVE ZINC:
• Give zinc tablet or syrup every day for 10-14 days
• This helps the diarrhea get better faster
• It also protects against future diarrhea
• Complete all 14 days even if diarrhea stops

3. CONTINUE FEEDING:
• Do NOT stop feeding your child
• Continue breastfeeding - more often than usual
• Give soft, easy-to-digest foods
• Feed small amounts more frequently
• Avoid sugary drinks, fruit juice, and fizzy drinks

4. KEEP CLEAN:
• Wash your hands with soap after changing diapers
• Wash your hands before preparing food
• Dispose of stools safely
• Keep food covered and clean

WHEN TO RETURN TO THE HEALTH FACILITY:
• Child is getting worse
• Not able to drink or breastfeed
• Becomes very sick or weak
• Develops blood in stool
• Diarrhea continues for more than 3 days without improvement

HOW TO PREPARE ORS:
1. Wash your hands with soap
2. Measure the correct amount of clean water
3. Pour the ORS packet into the water
4. Stir until completely dissolved
5. Give to child with a cup and spoon (not a bottle)
6. Throw away any leftover ORS after 24 hours

IF ORS IS NOT AVAILABLE, make a temporary solution:
• 1 liter of clean water
• 6 teaspoons of sugar
• Half a teaspoon of salt
• Mix well and give like ORS
(Get ORS as soon as possible - it works better)

PREVENTING DIARRHEA:

• Breastfeed your baby exclusively for 6 months
• Wash hands with soap at key times:
  - Before preparing food
  - Before feeding your child
  - After using the toilet
  - After changing diapers
• Use clean, safe water for drinking
• Keep food covered and stored safely
• Use a latrine or toilet (not open defecation)
• Get your child vaccinated (including rotavirus vaccine)
• Give vitamin A supplements every 6 months

REMEMBER:
• Diarrhea is common but can be dangerous
• ORS and ZINC are the main treatments
• Continue feeding - do NOT starve your child
• Know the danger signs and go to hospital immediately if they appear
• Most children recover fully with proper treatment
• Prevention is important - practice good hygiene

Your quick action can save your child's life!`,

    references: [
      'WHO/UNICEF. Clinical management of acute diarrhoea. WHO/UNICEF Joint Statement. Geneva: WHO; 2004. - Joint statement',
      'WHO. The treatment of diarrhoea: a manual for physicians and other senior health workers. 4th revision. Geneva: WHO; 2005. - WHO manual',
      'WHO. Pocket book of hospital care for children. 2nd ed. Geneva: WHO; 2013. - IMCI guidelines',
      'Victora CG, et al. Reducing deaths from diarrhoea through oral rehydration therapy. Bull World Health Organ. 2000;78:1246-1255. - ORT evidence',
      'Lazzerini M, Wanzira H. Oral zinc for treating diarrhoea in children. Cochrane Database Syst Rev. 2016;12:CD005436. - Zinc evidence',
      'Tate JE, et al. Global, regional, and national estimates of rotavirus mortality in children <5 years of age, 2000-2013. Clin Infect Dis. 2016;62:S96-S105. - Rotavirus burden',
      'Kotloff KL, et al. Burden and aetiology of diarrhoeal disease in infants and young children in developing countries (GEMS). Lancet. 2013;382:209-222. - GEMS study',
      'Liu L, et al. Global, regional, and national causes of child mortality in 2000-13. Lancet. 2015;385:430-440. - Child mortality',
      'Walker CLF, et al. Global burden of childhood pneumonia and diarrhoea. Lancet. 2013;381:1405-1416. - Global burden',
      'Munos MK, et al. The effect of oral rehydration solution and recommended home fluids on diarrhoea mortality. Int J Epidemiol. 2010;39:i75-i87. - ORS effectiveness',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 40. TETANUS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '40',
    name: 'Tetanus',
    slug: 'tetanus',
    specialty: 'Infectious Disease',
    bodySystem: 'Neurological',
    icdCode: 'A35',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Tetanus is an acute, often fatal disease caused by an exotoxin (tetanospasmin) produced by Clostridium tetani, an anaerobic, gram-positive, spore-forming bacterium. The spores are ubiquitous in soil and can contaminate wounds. The toxin blocks inhibitory neurotransmitters (GABA and glycine) in the central nervous system, causing unopposed muscle contraction, rigidity, and spasms.

Tetanus remains a significant public health problem in Africa due to inadequate vaccination coverage, unhygienic delivery practices (neonatal tetanus), and traditional wound care. While largely eliminated in developed countries through vaccination, Africa accounts for a substantial proportion of global tetanus deaths. Neonatal tetanus alone kills an estimated 30,000-60,000 newborns annually, mostly in Africa and South Asia.

The disease is entirely preventable through vaccination, yet case fatality rates remain 10-70% depending on access to intensive care. The three main clinical forms are generalized tetanus (most common), localized tetanus, and cephalic tetanus. Neonatal tetanus is a form of generalized tetanus occurring in newborns.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

═══════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
═══════════════════════════════════════════════════════════════════════════════

• TETANUS IS A CLINICAL DIAGNOSIS - Do not wait for laboratory confirmation
• TRISMUS (lockjaw) is often the first symptom - Inability to open mouth fully
• HUMAN TETANUS IMMUNOGLOBULIN (HTIG) - Give 3000-6000 IU IM as soon as possible; neutralizes circulating toxin
• WOUND CARE - Debride wound thoroughly; remove necrotic tissue and foreign bodies
• METRONIDAZOLE is first-line antibiotic (not penicillin - GABA antagonist may worsen spasms)
• BENZODIAZEPINES (Diazepam) for muscle spasms - May need high doses and continuous infusion
• AUTONOMIC DYSFUNCTION - Major cause of death; magnesium sulfate may help
• QUIET, DARK ENVIRONMENT - Minimize stimuli that trigger spasms
• AIRWAY MANAGEMENT - Spasms can cause airway obstruction; early intubation may be needed
• PREVENTION - Tetanus is COMPLETELY PREVENTABLE by vaccination
• Recovery is slow (weeks to months) - Toxin bound to neurons is not neutralized by antitoxin`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 38,000-73,000 deaths annually (WHO 2019)
• 95% of deaths occur in Africa and South-East Asia
• Neonatal tetanus: 30,000-60,000 deaths annually
• Case fatality rate: 10-70% (depends on access to care)
• Incubation period: 3-21 days (average 7-10 days)

AFRICAN EPIDEMIOLOGY:
• Africa accounts for approximately 50% of global tetanus deaths
• Neonatal tetanus remains endemic in many African countries
• Maternal and neonatal tetanus (MNT) elimination not achieved in many countries
• Major risk: Unhygienic delivery and cord care practices
• Adult tetanus: Often from agricultural injuries, traditional practices

NEONATAL TETANUS IN AFRICA:
• Leading cause of neonatal deaths in some regions
• Risk factors: Home delivery without skilled attendant, unvaccinated mother
• Contaminated cord care: Application of dung, ash, or other substances
• Case fatality: 70-100% without treatment; 10-50% with treatment

CLINICAL FORMS:

Generalized Tetanus (80%):
• Most common form
• Descending pattern: Jaw → neck → trunk → limbs
• Includes neonatal tetanus

Localized Tetanus (Rare):
• Rigidity of muscles near wound site
• May progress to generalized

Cephalic Tetanus (Rare):
• Following head/face wounds
• Cranial nerve involvement
• Often progresses to generalized

Neonatal Tetanus:
• Generalized tetanus in newborns
• Onset typically day 3-14 of life
• Inability to suckle is key presenting symptom

RISK FACTORS:
• Incomplete or no tetanus vaccination
• Wounds contaminated with soil, feces, or saliva
• Puncture wounds, crush injuries
• Burns, frostbite
• Injection drug use
• Umbilical stump infection (neonatal)
• Surgical wounds, circumcision
• Animal bites
• Unsterile delivery (maternal and neonatal)
• Chronic wounds, ulcers
• Dental infections
• Elderly (waning immunity)

INCUBATION AND SEVERITY:
• Shorter incubation = More severe disease
• <7 days incubation: Severe, high mortality
• Shorter period from first symptom to first spasm (onset time) = More severe`,

    clinicalPresentation: `GENERALIZED TETANUS:

Prodromal Symptoms:
• Restlessness, irritability
• Headache
• Low-grade fever
• Muscle stiffness

Classical Triad:
1. TRISMUS (Lockjaw): Masseter muscle spasm; inability to open mouth
2. RISUS SARDONICUS: Sustained spasm of facial muscles; sardonic smile
3. OPISTHOTONUS: Severe spasm causing arched back

Progression of Symptoms:
• Day 1-2: Trismus, facial stiffness
• Day 2-3: Neck stiffness, dysphagia, risus sardonicus
• Day 3-7: Generalized rigidity, opisthotonus, reflex spasms
• Week 2-3: Autonomic dysfunction (if survives)

Muscle Rigidity:
• Constant, board-like abdominal rigidity
• Neck stiffness
• Limb rigidity (arms flexed, legs extended)
• Opisthotonus (severe arching)

Reflex Spasms:
• Triggered by stimuli: Touch, noise, light, movement
• Sudden, violent, painful contractions
• Can cause fractures, tendon rupture
• Laryngospasm: LIFE-THREATENING - airway obstruction
• Respiratory muscle spasm: Hypoxia

Autonomic Dysfunction (Late):
• Tachycardia alternating with bradycardia
• Hypertension alternating with hypotension
• Hyperthermia
• Profuse sweating
• Cardiac arrhythmias
• Sudden cardiac death

NEONATAL TETANUS:

Timing: Usually day 3-14 of life (median day 7)

Presenting Symptoms:
• Inability to suckle (previously feeding normally) - KEY symptom
• Excessive crying
• Irritability
• Trismus - cannot open mouth

Progression:
• Generalized rigidity
• Opisthotonus
• Spasms triggered by touch, noise
• Respiratory distress
• Cyanosis during spasms

Physical Findings:
• Umbilical stump infection (often)
• Trismus
• Rigidity
• Abnormal posturing

CEPHALIC TETANUS:

• Following head/face wounds
• Trismus
• Cranial nerve palsies (especially VII - facial)
• Dysphagia
• May have localized rigidity
• High risk of progression to generalized

LOCALIZED TETANUS:

• Rigidity confined to muscles near wound
• Usually milder
• May progress to generalized
• Better prognosis if remains localized

ABLETT CLASSIFICATION OF SEVERITY:

Grade I (Mild):
• Trismus, general spasticity
• No respiratory compromise
• No spasms
• No dysphagia

Grade II (Moderate):
• Trismus, rigidity
• Mild-moderate spasms
• Mild dysphagia
• Mild respiratory impairment

Grade III (Severe):
• Severe trismus and rigidity
• Frequent, prolonged spasms
• Severe dysphagia
• Significant respiratory compromise
• Tachycardia >120 bpm

Grade IV (Very Severe):
• Grade III features PLUS
• Autonomic dysfunction
• Severe hypertension
• Severe tachycardia`,

    diagnosis: `DIAGNOSTIC APPROACH:

TETANUS IS A CLINICAL DIAGNOSIS
• No laboratory test confirms or excludes tetanus
• Do NOT delay treatment waiting for test results
• Diagnosis based on clinical presentation and history

CLINICAL DIAGNOSTIC CRITERIA:

Classic Presentation:
• History of wound (may be trivial or not recalled)
• Trismus (lockjaw) - difficulty opening mouth
• Muscle rigidity (neck, abdomen)
• Reflex spasms triggered by stimuli
• Clear sensorium (conscious and alert)

Spatula Test:
• Touch posterior pharyngeal wall with soft spatula
• Normal response: Gag reflex, expels spatula
• Tetanus: Reflex spasm of masseters, bites spatula
• Sensitivity: 94%; Specificity: 100%

NEONATAL TETANUS DIAGNOSIS (WHO Definition):
• Neonate with normal ability to suckle and cry during first 2 days of life
• THEN develops:
  - Inability to suckle (day 3-28), AND
  - Stiffness or spasms or both

LABORATORY TESTS:

Not Diagnostic but May Help:
• Wound culture: C. tetani isolated in only 30% of cases
• Blood tests: Usually normal; may have leukocytosis
• CPK: May be elevated from muscle damage
• Blood glucose: Monitor (hypoglycemia in neonates)

To Exclude Other Conditions:
• Lumbar puncture: CSF is NORMAL in tetanus (differentiates from meningitis)
• Calcium, magnesium: Rule out hypocalcemia, hypomagnesemia
• Blood film: Rule out malaria in endemic areas
• Toxicology screen: If strychnine poisoning suspected

IMAGING:
• Usually not helpful for diagnosis
• May show fractures from severe spasms
• Head CT: Normal (rules out other causes)

DIFFERENTIAL DIAGNOSIS KEY POINTS:

| Condition | Differentiating Features |
|-----------|-------------------------|
| Meningitis | Fever, altered consciousness, abnormal CSF |
| Strychnine poisoning | No trismus between spasms, rapid onset |
| Hypocalcemic tetany | Positive Chvostek/Trousseau, low calcium |
| Dystonic drug reaction | History of phenothiazines, responds to anticholinergics |
| Rabies | Animal bite, hydrophobia, encephalitis |
| Dental abscess | Local pain, swelling, no generalized rigidity |
| Epilepsy | Loss of consciousness, postictal state |`,

    differentialDiagnosis: [
      'Meningitis/Encephalitis - Fever, altered consciousness, positive meningeal signs, abnormal CSF; tetanus has clear sensorium and normal CSF',
      'Strychnine poisoning - Similar spasms but NO trismus between spasms; very rapid onset; facial muscles relax between spasms',
      'Hypocalcemic tetany - Carpopedal spasm, Chvostek sign, Trousseau sign; low serum calcium; no trismus',
      'Dystonic drug reaction - History of phenothiazine or metoclopramide; responds to anticholinergics (benztropine)',
      'Rabies - Animal bite history, hydrophobia, aerophobia, encephalitis, abnormal behavior; wound history',
      'Dental abscess/Peritonsillar abscess - Local pain and swelling; trismus from local inflammation; no generalized rigidity',
      'Temporomandibular joint dysfunction - Limited mouth opening; no other rigidity; chronic history',
      'Epilepsy/Seizures - Loss of consciousness during episode; postictal confusion; EEG abnormalities',
      'Oropharyngeal infection - Local findings; no generalized spasms',
      'Malignant hyperthermia - Associated with anesthesia; hyperthermia, rigidity, rhabdomyolysis',
      'Neuroleptic malignant syndrome - History of antipsychotics; fever, rigidity, altered mental status',
      'Sepsis in neonates - Fever or hypothermia, lethargy, poor feeding; may coexist with neonatal tetanus',
    ],

    investigations: [
      {
        name: 'Clinical Diagnosis (Spatula Test)',
        indication: '🔴 IMMEDIATE - Touch posterior pharynx with spatula; biting = positive; 94% sensitive, 100% specific',
        availability: 'BASIC'
      },
      {
        name: 'Wound Examination',
        indication: '🔴 IMMEDIATE - Identify portal of entry; assess for debridement needs; may not find wound in 20%',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 IMMEDIATE - Hypoglycemia common in neonatal tetanus; monitor regularly',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: '🟠 URGENT - Rule out hypocalcemia as cause of tetany; monitor during treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 Routine - May show leukocytosis; assess for concurrent infection',
        availability: 'BASIC'
      },
      {
        name: 'Creatine Kinase (CPK)',
        indication: '🟡 Routine - Elevated from muscle damage due to spasms; monitor for rhabdomyolysis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Renal Function',
        indication: '🟠 URGENT - Rhabdomyolysis can cause renal failure; monitor creatinine',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture',
        indication: '🟠 URGENT - If meningitis suspected; CSF is NORMAL in tetanus (helps differentiate)',
        availability: 'BASIC'
      },
      {
        name: 'Wound Culture',
        indication: '🟡 Routine - Low yield (30%); positive culture does not confirm tetanus; negative does not exclude',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Test',
        indication: '🔴 IMMEDIATE - In endemic areas; concurrent malaria common',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🟠 URGENT - Respiratory compromise; assess ventilation; hypoxia during spasms',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🟡 Routine - Aspiration pneumonia risk; respiratory complications',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🟠 URGENT - Autonomic dysfunction causes arrhythmias; monitor',
        availability: 'BASIC'
      },
    ],

    management: {
      immediate: [
        '⚡ RECOGNIZE TETANUS CLINICALLY - Trismus, rigidity, spasms; do NOT wait for lab confirmation',
        '⚡ QUIET, DARK ENVIRONMENT - Minimize stimuli that trigger spasms; single quiet room',
        '⚡ HUMAN TETANUS IMMUNOGLOBULIN (HTIG) - 3000-6000 IU IM single dose; give immediately; neutralizes circulating toxin',
        '⚡ WOUND CARE - Debride wound thoroughly after HTIG given; remove foreign bodies and necrotic tissue',
        '⚡ METRONIDAZOLE - Start immediately; kills C. tetani, reduces toxin production',
        '⚡ DIAZEPAM for spasms - IV or rectal; may need high doses and continuous infusion',
        '⚡ AIRWAY ASSESSMENT - Spasms can cause airway obstruction; prepare for intubation',
        '⚡ IV ACCESS - Secure IV line for medications; avoid IM injections (trigger spasms)',
        '⚡ TETANUS TOXOID - Give at different site from HTIG; patient needs full vaccination series',
        '⚡ NPO if dysphagia - Aspiration risk; consider NG tube for feeding',
        '⚡ Monitor for autonomic dysfunction - BP, HR; can be fatal',
        '⚡ Supportive care - Fluids, nutrition, prevent complications',
      ],
      pharmacological: [
        {
          drug: 'Human Tetanus Immunoglobulin (HTIG)',
          indication: '🔴 IMMEDIATE - Neutralizes circulating (unbound) toxin; GIVE AS SOON AS POSSIBLE',
          dose: 'Therapeutic: 3000-6000 IU IM single dose (some give 500-1000 IU intrathecally in severe cases - controversial). Give at site distant from wound',
          route: 'IM (multiple sites if large volume)',
          duration: 'Single dose',
          contraindications: ['None in treatment of tetanus (life-saving)'],
          sideEffects: ['Injection site pain', 'Rarely allergic reactions', 'Does NOT cause serum sickness (human product)'],
          alternatives: ['Equine tetanus antitoxin (TAT) 10,000-20,000 IU if HTIG unavailable; higher allergy risk, test dose required']
        },
        {
          drug: 'Equine Tetanus Antitoxin (TAT)',
          indication: '🔴 Alternative to HTIG - When HTIG not available; neutralizes toxin; higher reaction risk',
          dose: '10,000-20,000 IU IV/IM after test dose. Test dose: 0.1 mL SC, wait 30 min',
          route: 'IV (preferred) or IM',
          duration: 'Single dose',
          contraindications: ['Known horse serum allergy (relative - may give with premedication if life-threatening)'],
          sideEffects: ['Anaphylaxis risk (test dose first)', 'Serum sickness (delayed)', 'Injection site reactions'],
          alternatives: ['HTIG strongly preferred if available']
        },
        {
          drug: 'Metronidazole',
          indication: '🔴 First-line Antibiotic - Kills C. tetani; reduces toxin production; preferred over penicillin',
          dose: 'Adults: 500 mg IV every 6 hours. Children: 7.5 mg/kg IV every 6 hours (max 500 mg/dose). Neonates: 15 mg/kg loading, then 7.5 mg/kg every 12 hours',
          route: 'IV (PO when able to swallow)',
          duration: '7-10 days',
          contraindications: ['First trimester pregnancy (relative)', 'Known hypersensitivity'],
          sideEffects: ['Nausea', 'Metallic taste', 'Disulfiram-like reaction with alcohol', 'Peripheral neuropathy (prolonged use)'],
          alternatives: ['Penicillin G (less preferred - GABA antagonist may worsen spasms)']
        },
        {
          drug: 'Diazepam',
          indication: '🔴 First-line for Spasms - Controls muscle spasms and rigidity; GABA agonist',
          dose: 'Adults: 10-40 mg IV every 1-8 hours as needed, OR continuous infusion 5-15 mg/hour. Children: 0.1-0.3 mg/kg IV every 1-4 hours (max 10 mg/dose). Neonates: 0.1-0.3 mg/kg every 6 hours',
          route: 'IV (preferred), rectal, NG',
          duration: 'Throughout spasm phase (typically 2-3 weeks); taper gradually',
          contraindications: ['Severe respiratory depression without ventilator support', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension', 'Paradoxical agitation', 'Dependence with prolonged use'],
          alternatives: ['Midazolam infusion', 'Lorazepam']
        },
        {
          drug: 'Midazolam',
          indication: '🔴 Alternative/Adjunct for Spasms - Short-acting; good for continuous infusion',
          dose: 'Adults: Loading 0.1-0.2 mg/kg IV, then infusion 0.05-0.4 mg/kg/hour. Children: 0.1-0.2 mg/kg bolus, then 1-5 mcg/kg/min infusion',
          route: 'IV infusion',
          duration: 'Throughout spasm phase; taper gradually',
          contraindications: ['Severe respiratory depression without ventilator'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Tolerance develops'],
          alternatives: ['Diazepam', 'Propofol (if intubated)']
        },
        {
          drug: 'Magnesium Sulfate',
          indication: '🟠 Autonomic Dysfunction - Helps control sympathetic overactivity; adjunct to benzodiazepines',
          dose: 'Loading: 5 g (40 mg/kg) IV over 20 min. Maintenance: 2-3 g/hour (children: 30-50 mg/kg/hour) IV infusion. Target Mg level: 2-4 mmol/L',
          route: 'IV infusion',
          duration: 'During autonomic dysfunction phase',
          contraindications: ['Renal failure (accumulates)', 'Myasthenia gravis', 'Heart block'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Loss of deep tendon reflexes (toxicity sign)', 'Flushing'],
          alternatives: ['Labetalol for hypertension', 'Morphine for sympathetic overactivity']
        },
        {
          drug: 'Tetanus Toxoid (Td or Tdap)',
          indication: '🔴 Active Immunization - Tetanus does NOT confer immunity; patient needs full vaccination series',
          dose: 'Td 0.5 mL IM at separate site from HTIG. Complete series: 0, 1 month, 6-12 months (3 doses)',
          route: 'IM (deltoid)',
          duration: 'Give first dose immediately; complete 3-dose series; boosters every 10 years',
          contraindications: ['Severe allergic reaction to previous dose'],
          sideEffects: ['Injection site pain', 'Fever', 'Rare: Allergic reactions'],
          alternatives: ['TT (tetanus toxoid alone) if combination not available']
        },
        {
          drug: 'Vecuronium/Rocuronium (Neuromuscular Blockade)',
          indication: '🔴 Severe Spasms - For intubated patients when benzodiazepines inadequate; complete paralysis',
          dose: 'Vecuronium: 0.1 mg/kg IV bolus, then 0.05-0.1 mg/kg/hour infusion. Rocuronium: Similar dosing',
          route: 'IV infusion',
          duration: 'While mechanically ventilated; often 2-3 weeks',
          contraindications: ['Without mechanical ventilation and sedation'],
          sideEffects: ['Requires mechanical ventilation', 'Critical illness myopathy', 'Prolonged weakness'],
          alternatives: ['Pancuronium (longer acting)']
        },
        {
          drug: 'Morphine',
          indication: '🟠 Autonomic Dysfunction/Pain - Helps control sympathetic overactivity; sedation; analgesia',
          dose: 'Adults: 2-5 mg IV every 2-4 hours or infusion 1-5 mg/hour',
          route: 'IV',
          duration: 'During autonomic dysfunction phase',
          contraindications: ['Respiratory depression without ventilator'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Constipation', 'Dependence'],
          alternatives: ['Fentanyl infusion']
        },
        {
          drug: 'Labetalol',
          indication: '🟠 Autonomic Dysfunction - For hypertension and tachycardia; alpha and beta blockade',
          dose: 'Adults: 0.25-1 mg/min IV infusion, titrate to BP/HR',
          route: 'IV infusion',
          duration: 'During autonomic dysfunction phase',
          contraindications: ['Severe bradycardia', 'Heart block', 'Cardiogenic shock', 'Severe asthma'],
          sideEffects: ['Hypotension', 'Bradycardia', 'Bronchospasm'],
          alternatives: ['Esmolol', 'Magnesium sulfate']
        },
      ],
      nonPharmacological: [
        'Quiet, Dark Room: Minimize stimuli (light, noise, touch) that trigger spasms; single room; limit examinations',
        'Gentle Handling: All procedures should be gentle; avoid unnecessary interventions',
        'Wound Debridement: Thorough debridement after HTIG given; remove necrotic tissue; leave wound open',
        'Airway Management: Prepare for intubation; tracheostomy may be preferred (less stimulating, prolonged ventilation expected)',
        'Mechanical Ventilation: Often required for severe cases; protect airway; manage spasms',
        'Nutritional Support: Early enteral nutrition via NG tube; high caloric needs; parenteral if enteral not possible',
        'DVT Prophylaxis: Compression stockings; anticoagulation when safe (immobilization risk)',
        'Pressure Ulcer Prevention: Regular turning (gently); appropriate mattress',
        'Physiotherapy: After spasm phase; prevent contractures; rehabilitation',
        'Psychological Support: Patient is conscious and aware; explain condition; reassurance',
      ],
      surgical: [
        'Wound Debridement: Essential; remove necrotic tissue, foreign bodies; do AFTER giving HTIG; thorough irrigation',
        'Tracheostomy: Often preferred over prolonged endotracheal intubation; easier airway management; less stimulating; anticipate need early',
        'NG Tube Placement: For nutrition and medication administration when unable to swallow',
        'IV/Central Line: Secure vascular access; avoid repeated injections',
        'Umbilical Stump Care (Neonatal): Clean and debride infected stump; antiseptic application',
      ],
      referralCriteria: [
        '🔴 EMERGENCY - Respiratory distress/Laryngospasm → Immediate intubation; ICU transfer',
        '🔴 EMERGENCY - Severe generalized tetanus (Grade III-IV) → ICU admission; mechanical ventilation likely needed',
        '🔴 EMERGENCY - Autonomic dysfunction → ICU monitoring; high mortality phase',
        '🟠 URGENT - Moderate tetanus (Grade II) → Hospital admission; close monitoring; may need ICU',
        '🟠 URGENT - Neonatal tetanus → Specialized neonatal care if available',
        '🟠 URGENT - Inadequate resources for management → Transfer to higher center if safe to do so',
        '🟡 ROUTINE - Mild tetanus (Grade I) → Hospital admission; monitoring; may not need ICU',
        '🟡 ROUTINE - Post-recovery rehabilitation → Physiotherapy; complete vaccination',
      ],
      monitoring: [
        'Respiratory Status: Continuous; SpO2; respiratory rate; ability to protect airway; ABG if compromised',
        'Cardiovascular: Continuous ECG and BP monitoring; autonomic instability causes fluctuations',
        'Spasm Frequency and Severity: Document; guides medication titration',
        'Level of Consciousness: Should remain alert; sedation level if on medications',
        'Fluid Balance: Intake/output; hydration status; sweat losses can be significant',
        'Temperature: Fever from autonomic dysfunction vs infection',
        'Blood Glucose: Especially neonates; hypoglycemia risk',
        'Electrolytes: Magnesium if on MgSO4 infusion; renal function',
        'Medication Effects: Respiratory depression from benzodiazepines; adjust doses',
        'Complications: Aspiration pneumonia, DVT, pressure ulcers, nosocomial infections',
      ],
      followUp: `FOLLOW-UP SCHEDULE:

ACUTE PHASE MONITORING:
• Continuous monitoring during spasm phase (2-3 weeks)
• Daily assessment of spasm severity
• Gradual reduction of sedation as spasms decrease

RECOVERY PHASE (Weeks 3-6):
• Wean sedation and ventilatory support
• Begin enteral feeding if not already
• Start physiotherapy
• Assess for complications

REHABILITATION:
• Physiotherapy for residual rigidity/weakness
• May take weeks to months for full recovery
• Some patients have prolonged weakness

VACCINATION COMPLETION:
• First dose given during acute illness
• Second dose: 4-6 weeks after first
• Third dose: 6-12 months after second
• Boosters: Every 10 years lifelong

DISCHARGE CRITERIA:
• No spasms for 48-72 hours
• Off sedation and ventilatory support
• Able to swallow safely
• Wounds healed or healing
• Family education completed
• Vaccination plan in place

POST-DISCHARGE FOLLOW-UP:
• 1-2 weeks: Wound check, assess recovery
• 4-6 weeks: Second vaccine dose
• 6-12 months: Third vaccine dose
• Annually: Assess for late complications, booster reminders

COMPLICATIONS TO MONITOR:
• Aspiration pneumonia
• Respiratory failure
• Nosocomial infections
• Vertebral fractures (from spasms)
• Muscle contractures
• Deep vein thrombosis
• Pressure ulcers
• Critical illness polyneuropathy

PROGNOSIS INDICATORS:
• Severity grade at presentation
• Incubation period (<7 days = worse)
• Time from first symptom to first spasm (<48 hours = worse)
• Access to intensive care
• Autonomic dysfunction severity
• Extremes of age (neonates, elderly)`
    },

    redFlags: [
      '🔴 CRITICAL: Laryngospasm/Respiratory arrest - Spasm of airway muscles → Immediate intubation/ventilation',
      '🔴 CRITICAL: Autonomic storm - Severe BP/HR fluctuations, arrhythmias → ICU; magnesium; may be fatal',
      '🔴 CRITICAL: Status spasticus - Continuous spasms → Neuromuscular blockade and ventilation',
      '🔴 CRITICAL: Aspiration - During spasm or impaired swallowing → Airway protection, NPO, antibiotics if pneumonia',
      '🔴 CRITICAL: Hypoxia during spasms - SpO2 dropping with spasms → Supplemental O2; intubation if recurrent',
      '🟠 URGENT: Increasing spasm frequency - Disease progression → Increase sedation; consider intubation',
      '🟠 URGENT: Fever - May indicate infection or autonomic dysfunction → Evaluate for both',
      '🟠 URGENT: Unable to swallow - Aspiration risk → NG tube; consider intubation',
      '🟠 URGENT: Short incubation period (<7 days) - Predicts severe disease → Prepare for ICU care',
      '🟡 WARNING: Persistent rigidity after acute phase - May indicate incomplete recovery → Continued physiotherapy',
      '🟡 WARNING: Fractures from spasms - Vertebral most common → Imaging if suspected; supportive care',
    ],

    complications: [
      'Respiratory Failure: From spasms of respiratory muscles, laryngospasm, aspiration; leading cause of death',
      'Autonomic Dysfunction: BP and HR instability; hyperthermia; arrhythmias; sudden death; occurs week 2-3',
      'Aspiration Pneumonia: From impaired swallowing and spasms; common cause of death',
      'Laryngospasm: Sudden closure of vocal cords during spasm; can cause asphyxiation',
      'Fractures: Vertebral compression fractures and long bone fractures from violent spasms',
      'Rhabdomyolysis: From severe muscle contractions; can cause acute kidney injury',
      'Pulmonary Embolism: From immobilization and DVT',
      'Nosocomial Infections: Ventilator-associated pneumonia, catheter infections, wound infections',
      'Cardiac Arrhythmias: From autonomic dysfunction; can be fatal',
      'Hypoxic Brain Injury: From respiratory arrest or prolonged spasms',
      'Contractures: From prolonged rigidity without adequate physiotherapy',
      'Critical Illness Polyneuropathy: Prolonged weakness after severe illness/paralysis',
      'Death: Case fatality 10-70% depending on severity and access to care',
    ],

    prognosis: `OVERALL PROGNOSIS:

CASE FATALITY RATES:
• With intensive care: 10-20%
• Without intensive care: 40-70%
• Neonatal tetanus: 50-90% even with treatment
• Mild tetanus (Grade I): <10%
• Severe tetanus (Grade III-IV): 30-50%

PROGNOSTIC FACTORS:

Better Prognosis:
• Longer incubation period (>14 days)
• Longer onset time (>48 hours)
• Mild disease (Grade I-II)
• Access to intensive care
• Younger adults (not neonates)
• Localized tetanus
• Early treatment

Worse Prognosis:
• Short incubation (<7 days)
• Short onset time (<48 hours)
• Severe disease (Grade III-IV)
• Autonomic dysfunction
• Neonatal tetanus
• Elderly patients
• Limited medical resources
• Delayed treatment

RECOVERY:
• Spasms typically last 2-3 weeks
• Autonomic dysfunction peaks week 2-3
• Total recovery time: 4-6 weeks to several months
• Some patients have prolonged weakness
• Full recovery is possible with good care

RECURRENCE:
• Tetanus does NOT confer natural immunity
• Must complete vaccination series after recovery
• Risk of recurrence if not vaccinated

AFRICAN CONTEXT:

Challenges:
• Limited ICU capacity
• HTIG often unavailable (TAT used instead)
• Delayed presentation
• Neonatal tetanus still endemic
• Low vaccination coverage

Improvements Needed:
• Maternal vaccination programs
• Clean delivery practices
• Cord care education
• Vaccine coverage expansion
• Healthcare worker training
• HTIG availability`,

    prevention: `PRIMARY PREVENTION:

VACCINATION - Most Important Intervention:

Childhood Schedule (WHO):
• DTP at 6, 10, 14 weeks of age
• Boosters at 12-23 months and 4-7 years
• Adolescent booster (Td or Tdap)
• Adult boosters every 10 years

Maternal Vaccination:
• Pregnant women: At least 2 doses of tetanus toxoid
• Protects mother AND newborn (passive immunity)
• At least 2 weeks before delivery for protection

Catch-up Vaccination:
• Unvaccinated adults: 3 doses (0, 1 month, 6-12 months)
• Boosters every 10 years

Wound Prophylaxis:
| Wound Type | Vaccination History | Td/Tdap | HTIG |
|------------|---------------------|---------|------|
| Clean, minor | Unknown or <3 doses | Yes | No |
| Clean, minor | ≥3 doses, last >10 years | Yes | No |
| Clean, minor | ≥3 doses, last <10 years | No | No |
| Contaminated | Unknown or <3 doses | Yes | Yes |
| Contaminated | ≥3 doses, last >5 years | Yes | No |
| Contaminated | ≥3 doses, last <5 years | No | No |

NEONATAL TETANUS PREVENTION:

Maternal Vaccination:
• At least 2 doses TT before delivery
• 3 doses for long-term protection
• Provides passive immunity to newborn

Clean Delivery:
• Skilled birth attendant
• Clean delivery surface
• Clean hands
• Clean cord cutting and tying

Clean Cord Care:
• Nothing applied to cord (no dung, ash, etc.)
• Keep cord dry and clean
• Chlorhexidine application (reduces infections)

SECONDARY PREVENTION:

Wound Care:
• Thorough cleaning of all wounds
• Remove foreign bodies and necrotic tissue
• Tetanus prophylaxis based on wound type and vaccination status

Post-Exposure Prophylaxis:
• Td/Tdap vaccine
• HTIG for tetanus-prone wounds in unvaccinated/incompletely vaccinated

AFRICAN-SPECIFIC STRATEGIES:

• Strengthen routine immunization programs
• Focus on maternal vaccination (MNT elimination)
• Train traditional birth attendants in clean delivery
• Promote facility-based delivery
• Educate on cord care (no harmful substances)
• Improve wound care practices
• Ensure vaccine and HTIG supply chains
• Target high-risk populations
• Community education on tetanus prevention`,

    patientEducation: `
═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think you may have a medical emergency, go to the hospital IMMEDIATELY.

═══════════════════════════════════════════════════════════════════════════════
UNDERSTANDING TETANUS - INFORMATION FOR PATIENTS AND FAMILIES
═══════════════════════════════════════════════════════════════════════════════

WHAT IS TETANUS?
Tetanus (also called "lockjaw") is a serious disease caused by bacteria that enter the body through wounds. The bacteria produce a poison (toxin) that affects the nerves, causing severe muscle stiffness and spasms. Tetanus can be fatal but is COMPLETELY PREVENTABLE through vaccination.

HOW DO YOU GET TETANUS?
• The bacteria (Clostridium tetani) live in soil, dust, and animal feces
• They enter the body through wounds, cuts, or burns
• Any wound can become infected, including:
  - Deep puncture wounds (nails, thorns)
  - Dirty wounds
  - Burns
  - Animal bites
  - Wounds from unclean tools
• In newborns: Through the umbilical cord stump if contaminated

WHAT ARE THE SYMPTOMS?
Symptoms usually appear 7-10 days after infection (range 3-21 days):
• Jaw stiffness - difficulty opening the mouth (lockjaw)
• Neck stiffness
• Difficulty swallowing
• Stiffness of abdominal muscles
• Painful muscle spasms triggered by noise, touch, or light
• Fever and sweating
• Fast heartbeat

In Newborns (Neonatal Tetanus):
• Unable to breastfeed after being able to previously
• Excessive crying
• Stiffness and spasms
• Usually appears 3-14 days after birth

GO TO THE HOSPITAL IMMEDIATELY IF:
• You have a deep or dirty wound AND are not up-to-date with tetanus vaccination
• You develop jaw stiffness after any wound
• Your newborn baby cannot suckle or has stiffness

WHY IS TETANUS DANGEROUS?
• The muscle spasms can be severe enough to break bones
• Spasms can affect breathing muscles, causing suffocation
• Without treatment, tetanus can be fatal in 30-50% of cases
• Even with treatment, it requires weeks of intensive care

HOW IS TETANUS TREATED?
• Hospital admission (usually intensive care)
• Medicine to neutralize the toxin
• Medicine to kill the bacteria
• Medicine to control muscle spasms
• Wound cleaning
• Breathing support if needed
• Treatment takes several weeks

CAN TETANUS BE PREVENTED?
YES! Tetanus is completely preventable through vaccination.

Vaccination Schedule:
• Babies: Vaccines at 6, 10, and 14 weeks of age (usually combined with other vaccines)
• Boosters: Throughout childhood and adolescence
• Adults: Booster every 10 years
• Pregnant women: At least 2 doses during pregnancy to protect mother and baby

IMPORTANT: Having tetanus does NOT protect you from getting it again. You still need to complete your vaccinations after recovering from tetanus.

WOUND CARE - PREVENTING TETANUS:

After Any Wound:
1. Clean the wound thoroughly with soap and clean water
2. Remove any dirt, foreign objects, or dead tissue
3. Cover with a clean bandage
4. Seek medical care for:
   - Deep puncture wounds
   - Wounds contaminated with dirt or feces
   - Wounds from rusty objects
   - Wounds in unvaccinated persons

For Medical Care:
• Tell the healthcare provider about your wound
• Know your vaccination status
• You may need a tetanus vaccine booster and/or protective injection

FOR NEW MOTHERS - PREVENTING NEWBORN TETANUS:

Before and During Pregnancy:
• Get vaccinated against tetanus (at least 2 doses before delivery)
• Deliver at a health facility if possible
• If home delivery, use a trained birth attendant

Cord Care:
• Cut the umbilical cord with a CLEAN blade
• Tie the cord with CLEAN thread
• Do NOT apply anything to the cord stump (no dung, ash, herbs)
• Keep the cord clean and dry
• Seek help if the cord becomes red, swollen, or smelly

KEY MESSAGES:

1. VACCINATION IS THE BEST PROTECTION
   - Keep your vaccinations up-to-date
   - Make sure your children are vaccinated
   - Get boosters every 10 years

2. PROPER WOUND CARE
   - Clean all wounds thoroughly
   - Seek medical care for deep or dirty wounds
   - Ask about tetanus vaccine if wound is risky

3. CLEAN DELIVERY AND CORD CARE
   - Deliver at a health facility
   - Use clean practices for umbilical cord
   - Never apply harmful substances to the cord

4. SEEK CARE EARLY
   - If you develop jaw stiffness or muscle spasms after a wound
   - If your newborn cannot feed or is very stiff

REMEMBER:
• Tetanus is PREVENTABLE through vaccination
• Tetanus is SERIOUS but treatable if caught early
• Complete your vaccination series
• Practice proper wound care
• Ensure clean delivery for newborns`,

    references: [
      'WHO. Tetanus vaccines: WHO position paper. Wkly Epidemiol Rec. 2017;92:53-76. - WHO position paper',
      'Cook TM, et al. Tetanus: a review of the literature. Br J Anaesth. 2001;87:477-487. - Comprehensive review',
      'Thwaites CL, et al. Maternal and neonatal tetanus. Lancet. 2015;385:362-370. - MNT review',
      'Rodrigo C, et al. Pharmacological management of tetanus: an evidence-based review. Crit Care. 2014;18:217. - Treatment review',
      'Brauner JS, et al. Changes in severe accidental tetanus mortality in the ICU during two decades in Brazil. Intensive Care Med. 2002;28:930-935. - ICU outcomes',
      'Thwaites CL, et al. Magnesium sulphate for treatment of severe tetanus. Lancet. 2006;368:1436-1443. - Magnesium trial',
      'Ahmadsyah I, Salim A. Treatment of tetanus: an open study to compare the efficacy of procaine penicillin and metronidazole. Br Med J. 1985;291:648-650. - Antibiotic comparison',
      'WHO. Maternal and Neonatal Tetanus Elimination (MNTE). Geneva: WHO; 2018. - Elimination initiative',
      'Ogunrin OA. Tetanus - A review of current concepts in management. J Postgrad Med. 2009;55:78-80. - African perspective',
      'Trujillo MH, et al. Impact of intensive care management on the prognosis of tetanus. Chest. 1987;92:63-65. - ICU impact',
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 41. NEONATAL JAUNDICE (HYPERBILIRUBINEMIA)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '41',
    name: 'Neonatal Jaundice',
    slug: 'neonatal-jaundice',
    specialty: 'Pediatrics',
    bodySystem: 'Hematological',
    icdCode: 'P59.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Neonatal jaundice (hyperbilirubinemia) is the yellow discoloration of the skin and sclera caused by elevated serum bilirubin levels in newborns. It is one of the most common conditions requiring medical attention in the neonatal period, affecting approximately 60% of term and 80% of preterm infants in the first week of life.

While physiological jaundice is benign and self-limiting, pathological hyperbilirubinemia can lead to acute bilirubin encephalopathy (ABE) and kernicterus—permanent neurological damage characterized by choreoathetoid cerebral palsy, hearing loss, gaze abnormalities, and dental enamel dysplasia.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 JAUNDICE IN FIRST 24 HOURS IS ALWAYS PATHOLOGICAL - Requires urgent evaluation for hemolysis (ABO/Rh incompatibility, G6PD deficiency)

🔴 TOTAL SERUM BILIRUBIN (TSB) determines management - Use hour-specific nomograms (Bhutani) or AAP guidelines for treatment thresholds

🔴 EXCHANGE TRANSFUSION THRESHOLDS - Term infant: TSB >25 mg/dL (428 μmol/L) or signs of ABE; Lower thresholds for preterm/sick infants

🔴 SIGNS OF ACUTE BILIRUBIN ENCEPHALOPATHY (ABE): Lethargy, hypotonia, poor feeding, high-pitched cry, opisthotonus, fever, seizures - EMERGENCY

🔴 G6PD DEFICIENCY is common in Africa (10-25% males) - Major cause of severe neonatal jaundice and kernicterus; avoid oxidant triggers

🔴 PHOTOTHERAPY is first-line treatment - Intensive phototherapy (≥30 μW/cm²/nm) can reduce TSB by 30-40% in 24 hours

🔴 BREASTFEEDING JAUNDICE vs BREAST MILK JAUNDICE - Different entities; breastfeeding should continue in both

🔴 KERNICTERUS IS PREVENTABLE - Early identification, monitoring, and treatment prevent permanent brain damage`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 60% of term and 80% of preterm newborns develop visible jaundice
• Severe hyperbilirubinemia: 8-11% of newborns worldwide
• Kernicterus incidence: <1 per 100,000 in high-income countries; much higher in LMIC
• Leading cause of preventable neonatal brain injury globally
• Annual burden: ~114,000 deaths and 178,000 survivors with kernicterus-related disability

AFRICAN EPIDEMIOLOGY:
• Africa bears disproportionate burden of kernicterus globally
• G6PD deficiency prevalence: 10-25% of males in sub-Saharan Africa (major risk factor)
• ABO incompatibility: Common cause of pathological jaundice
• Limited access to TSB measurement—clinical assessment unreliable
• Phototherapy often unavailable or inadequate (wrong wavelength, insufficient irradiance)
• Exchange transfusion capacity limited to tertiary centers
• Traditional practices (e.g., placing baby in sunlight) ineffective and may cause harm
• Delayed presentation due to home births and lack of awareness
• Case fatality rate from severe jaundice: 10-30% in some African settings
• Nigeria, DRC, and Ethiopia report highest absolute numbers of kernicterus cases
• Studies from Nigeria: 8-35% of neonatal admissions due to jaundice; kernicterus in 15-30%

RISK FACTORS FOR SEVERE HYPERBILIRUBINEMIA:
• Jaundice in first 24 hours
• Blood group incompatibility (ABO, Rh)
• G6PD deficiency
• Prematurity (<37 weeks)
• Exclusive breastfeeding with poor intake
• Previous sibling with phototherapy
• Cephalohematoma or significant bruising
• East Asian ethnicity
• Sepsis
• Polycythemia`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLINICAL ASSESSMENT:
• Yellow discoloration of skin and sclera
• Progression: Face → Chest → Abdomen → Extremities → Palms/soles (Kramer zones)
• Kramer Zone 1 (face): ~5-6 mg/dL
• Kramer Zone 2 (upper trunk): ~9-11 mg/dL
• Kramer Zone 3 (lower trunk/thighs): ~12-14 mg/dL
• Kramer Zone 4 (arms/legs): ~15-17 mg/dL
• Kramer Zone 5 (palms/soles): >18 mg/dL
⚠️ Visual assessment is UNRELIABLE - Always confirm with TSB

PHYSIOLOGICAL JAUNDICE:
• Appears after 24 hours of age
• Peaks at day 3-5 in term, day 5-7 in preterm
• TSB <15 mg/dL in term infants
• Resolves by 2 weeks (term) or 3 weeks (preterm)
• Infant feeding well, gaining weight, otherwise well

PATHOLOGICAL JAUNDICE - RED FLAGS:
• Onset within first 24 hours of life
• Rapidly rising bilirubin (>0.2 mg/dL/hour or >5 mg/dL/day)
• TSB exceeding hour-specific phototherapy thresholds
• Jaundice persisting >2 weeks in term, >3 weeks in preterm
• Direct (conjugated) bilirubin >1.5 mg/dL or >20% of TSB
• Associated pallor (hemolysis), hepatosplenomegaly, dark urine, pale stools

ACUTE BILIRUBIN ENCEPHALOPATHY (ABE) - EMERGENCY:
Early Phase (reversible):
• Lethargy, hypotonia, poor suck
• High-pitched cry
• Slightly decreased activity

Intermediate Phase:
• Moderate stupor, irritability
• Hypertonia (especially extensors)
• Minimal feeding
• High-pitched cry, inconsolable

Advanced Phase (often irreversible):
• Deep stupor to coma
• No feeding
• Opisthotonus, retrocollis
• Shrill cry
• Seizures
• Apnea, respiratory failure
• Fever
• Death if untreated

KERNICTERUS (Chronic Bilirubin Encephalopathy):
• Choreoathetoid cerebral palsy
• Sensorineural hearing loss (auditory neuropathy)
• Upward gaze palsy
• Dental enamel dysplasia
• Intellectual disability (variable)`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: IDENTIFY JAUNDICE
• Visual inspection in good lighting (daylight preferred)
• Press on skin to blanch and observe underlying color
• Check sclera, gums, and blanched skin
• Document extent using Kramer zones

STEP 2: MEASURE BILIRUBIN
• Transcutaneous bilirubinometry (TcB): Screening tool; confirm high values with TSB
• Total Serum Bilirubin (TSB): Gold standard; includes direct and indirect fractions
• Always obtain TSB if:
  - TcB ≥ phototherapy threshold minus 3 mg/dL
  - Jaundice in first 24 hours
  - Clinical concern for pathological jaundice
  - Planning treatment

STEP 3: DETERMINE IF PHYSIOLOGICAL OR PATHOLOGICAL
Pathological if ANY of:
• Jaundice <24 hours of age
• TSB rising >0.2 mg/dL/hour (>5 mg/dL/day)
• TSB exceeds hour-specific threshold (Bhutani nomogram)
• Direct bilirubin >1.5 mg/dL or >20% of TSB
• Jaundice >2 weeks (term) or >3 weeks (preterm)
• Signs of illness (lethargy, poor feeding, fever, ABE)

STEP 4: IDENTIFY ETIOLOGY
Hemolytic causes:
• ABO incompatibility (Mother O, baby A or B)
• Rh incompatibility (Rh-negative mother, Rh-positive baby)
• G6PD deficiency
• Hereditary spherocytosis
• Other enzyme defects (pyruvate kinase)

Non-hemolytic causes:
• Physiological jaundice
• Breastfeeding jaundice (dehydration, poor intake)
• Breast milk jaundice (prolonged, benign)
• Cephalohematoma, bruising
• Polycythemia
• Sepsis
• Hypothyroidism (prolonged jaundice)
• Metabolic (Crigler-Najjar, Gilbert)

Conjugated (Direct) Hyperbilirubinemia (>1.5 mg/dL or >20%):
• Biliary atresia - SURGICAL EMERGENCY if >8 weeks
• Neonatal hepatitis
• Choledochal cyst
• Sepsis
• TORCH infections
• Metabolic (galactosemia, tyrosinemia, alpha-1 antitrypsin deficiency)
• TPN-associated cholestasis

STEP 5: PLOT ON NOMOGRAM
• Use Bhutani hour-specific nomogram for risk stratification
• Use AAP guidelines (2004/2022 update) for treatment thresholds
• Thresholds are LOWER for:
  - Preterm infants
  - Sick infants
  - Hemolysis
  - G6PD deficiency
  - Albumin <3 g/dL`,

    differentialDiagnosis: [
      'Physiological jaundice - Most common; onset >24h, peaks day 3-5, resolves by 2 weeks',
      'ABO incompatibility - Mother O, baby A/B; positive DAT; spherocytes on smear',
      'Rh isoimmunization - Rh-negative mother, Rh-positive baby; positive DAT; severe anemia',
      'G6PD deficiency - Common in Africa; triggers: infection, drugs, fava beans; bite cells on smear',
      'Breastfeeding jaundice - Poor intake, dehydration; days 3-5; improves with increased feeding',
      'Breast milk jaundice - Prolonged jaundice; starts week 1, peaks week 2; benign; resolves by 12 weeks',
      'Sepsis - Ill-appearing; lethargy, temperature instability, poor feeding; conjugated component',
      'Cephalohematoma/bruising - Extravascular hemolysis; history of difficult delivery',
      'Hereditary spherocytosis - Family history; spherocytes; negative DAT; increased MCHC',
      'Hypothyroidism - Prolonged jaundice, constipation, poor feeding, large fontanelle; check TSH',
      'Biliary atresia - Conjugated hyperbilirubinemia; pale stools, dark urine; URGENT surgical referral',
      'Crigler-Najjar syndrome - Severe unconjugated; Type 1 unresponsive to phenobarbital',
      'Polycythemia - Plethoric infant; Hct >65%; often IDM or SGA',
      'TORCH infections - Hepatosplenomegaly, rash, microcephaly; conjugated component'
    ],

    investigations: [
      {
        name: 'Total Serum Bilirubin (TSB)',
        indication: '🔴 ESSENTIAL - Gold standard; measure total and direct fractions; plot on hour-specific nomogram',
        availability: 'BASIC'
      },
      {
        name: 'Transcutaneous Bilirubinometry (TcB)',
        indication: 'Screening tool; non-invasive; confirm high values with TSB; less reliable at very high levels or after phototherapy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Direct (Conjugated) Bilirubin',
        indication: '🔴 ESSENTIAL if prolonged jaundice - Direct >1.5 mg/dL or >20% indicates cholestasis; evaluate for biliary atresia',
        availability: 'BASIC'
      },
      {
        name: 'Blood Group and Rh (Mother and Baby)',
        indication: '🔴 ESSENTIAL - Identify ABO or Rh incompatibility; O mother with A/B baby at risk',
        availability: 'BASIC'
      },
      {
        name: 'Direct Antiglobulin Test (DAT/Coombs)',
        indication: '🔴 ESSENTIAL - Positive indicates immune-mediated hemolysis (ABO, Rh incompatibility)',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count with Reticulocyte Count',
        indication: '🔴 ESSENTIAL - Anemia and reticulocytosis suggest hemolysis; check for infection',
        availability: 'BASIC'
      },
      {
        name: 'Peripheral Blood Smear',
        indication: 'Spherocytes (ABO incompatibility, spherocytosis), bite cells (G6PD), polychromasia (hemolysis)',
        availability: 'BASIC'
      },
      {
        name: 'G6PD Assay',
        indication: '🔴 HIGH PRIORITY in Africa - Qualitative screen or quantitative; may be falsely normal during acute hemolysis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Albumin',
        indication: 'Bilirubin binding capacity; albumin <3 g/dL increases risk of neurotoxicity; lower treatment thresholds',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: 'Rule out hypoglycemia; sick infants at risk',
        availability: 'BASIC'
      },
      {
        name: 'Sepsis Workup (Blood Culture, CRP, CBC)',
        indication: 'If sepsis suspected; ill-appearing, fever, lethargy, conjugated hyperbilirubinemia',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH, Free T4)',
        indication: 'Prolonged unconjugated jaundice >2-3 weeks; screen for hypothyroidism',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urine for Reducing Substances',
        indication: 'Prolonged conjugated jaundice; screen for galactosemia',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (ALT, AST, GGT)',
        indication: 'Conjugated hyperbilirubinemia; evaluate liver pathology',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: 'Conjugated hyperbilirubinemia; visualize gallbladder (biliary atresia - absent/small GB), choledochal cyst',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIDA Scan',
        indication: 'Suspected biliary atresia; no excretion into bowel suggests atresia',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Obtain TSB immediately; plot on hour-specific nomogram',
        '⚡ IDENTIFY HIGH-RISK INFANTS: Jaundice <24h, hemolysis, G6PD, preterm, sick, rising rapidly',
        '⚡ RECOGNIZE ABE: Lethargy, hypotonia, poor feeding, high-pitched cry, opisthotonus = EMERGENCY',
        '⚡ INITIATE PHOTOTHERAPY if TSB exceeds threshold; use intensive phototherapy if near exchange level',
        '⚡ PREPARE FOR EXCHANGE TRANSFUSION if TSB at exchange threshold or signs of ABE',
        '⚡ ENSURE ADEQUATE HYDRATION: Increase breastfeeding frequency; supplement if needed',
        '⚡ STOP ANY OXIDANT DRUGS if G6PD deficiency suspected',
        '⚡ TREAT UNDERLYING CAUSE: Sepsis, dehydration, etc.'
      ],
      pharmacological: [
        {
          drug: 'Phototherapy (Blue Light 460-490nm)',
          indication: '💡 FIRST-LINE TREATMENT - Converts bilirubin to water-soluble isomers excreted without conjugation',
          dose: 'Intensive: ≥30 μW/cm²/nm over maximum body surface area; Standard: 8-10 μW/cm²/nm',
          route: 'Transcutaneous (overhead, underneath, fiberoptic blanket)',
          duration: 'Continue until TSB 2-3 mg/dL below threshold; typically 24-48 hours',
          contraindications: ['Conjugated hyperbilirubinemia (causes bronze baby syndrome)', 'Porphyria'],
          sideEffects: ['Insensible water loss (increase feeds 10-20%)', 'Temperature instability', 'Rash', 'Bronze baby (if conjugated)', 'Loose green stools', 'Retinal damage (protect eyes)'],
          alternatives: ['Exchange transfusion if phototherapy fails or TSB critical']
        },
        {
          drug: 'Intravenous Immunoglobulin (IVIG)',
          indication: '🩸 ADJUNCT for isoimmune hemolysis - ABO or Rh incompatibility with rising TSB despite phototherapy',
          dose: '0.5-1 g/kg IV over 2 hours',
          route: 'IV',
          duration: 'Single dose; may repeat in 12 hours if needed',
          contraindications: ['IgA deficiency (anaphylaxis risk)', 'Known hypersensitivity'],
          sideEffects: ['Infusion reactions', 'Headache', 'Fever', 'Hemolysis (rare)', 'Renal dysfunction'],
          alternatives: ['Exchange transfusion']
        },
        {
          drug: 'Exchange Transfusion',
          indication: '🔴 CRITICAL - TSB at exchange threshold OR signs of acute bilirubin encephalopathy',
          dose: 'Double-volume exchange (160-180 mL/kg); removes ~85% of bilirubin',
          route: 'Umbilical venous catheter (preferred) or peripheral access',
          duration: '1-2 hours; may need repeat if TSB rebounds',
          contraindications: ['Hemodynamic instability (relative)', 'Severe coagulopathy (correct first)'],
          sideEffects: ['Electrolyte disturbances (hypocalcemia, hyperkalemia)', 'Thrombocytopenia', 'Infection', 'NEC', 'Air embolism', 'Cardiac arrhythmia', 'Death (1-3%)'],
          alternatives: ['Intensive phototherapy as bridge while preparing exchange']
        },
        {
          drug: 'Phenobarbital',
          indication: '📋 PREVENTIVE in selected cases - Induces hepatic conjugation; limited role in acute treatment',
          dose: '3-5 mg/kg/day divided every 12-24 hours',
          route: 'PO or IV',
          duration: '3-7 days; takes 3-4 days for effect',
          contraindications: ['Porphyria', 'Severe respiratory depression'],
          sideEffects: ['Sedation', 'Respiratory depression', 'Poor feeding'],
          alternatives: ['Phototherapy (faster onset)']
        },
        {
          drug: 'Albumin Infusion',
          indication: '🩸 ADJUNCT before exchange transfusion - Increases bilirubin binding; use if albumin <3 g/dL',
          dose: '1 g/kg IV over 2-4 hours',
          route: 'IV',
          duration: 'Single dose before exchange',
          contraindications: ['Fluid overload', 'Heart failure'],
          sideEffects: ['Volume overload', 'Allergic reactions'],
          alternatives: ['Proceed with exchange without albumin if unavailable']
        },
        {
          drug: 'Normal Saline (0.9% NaCl)',
          indication: '💧 HYDRATION - For dehydrated infants; supports bilirubin excretion',
          dose: '10-20 mL/kg bolus, then maintenance fluids',
          route: 'IV',
          duration: 'Until adequate oral intake',
          contraindications: ['Fluid overload'],
          sideEffects: ['Fluid overload', 'Electrolyte disturbance'],
          alternatives: ['10% Dextrose if hypoglycemic']
        },
        {
          drug: '10% Dextrose',
          indication: '💧 HYPOGLYCEMIA treatment and prevention - Sick neonates, poor feeding',
          dose: '2 mL/kg bolus for hypoglycemia; 60-80 mL/kg/day maintenance',
          route: 'IV',
          duration: 'Until feeding established and glucose stable',
          contraindications: ['Hyperglycemia'],
          sideEffects: ['Hyperglycemia', 'Osmotic diuresis'],
          alternatives: ['Oral feeds if tolerated']
        },
        {
          drug: 'Ampicillin',
          indication: '🦠 If SEPSIS suspected - Empiric coverage for early-onset neonatal sepsis',
          dose: '50 mg/kg/dose IV every 8-12 hours (based on age)',
          route: 'IV',
          duration: '48-72 hours pending cultures; 7-10 days if confirmed',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Rash', 'Diarrhea', 'Allergic reaction'],
          alternatives: ['Penicillin G']
        },
        {
          drug: 'Gentamicin',
          indication: '🦠 If SEPSIS suspected - Gram-negative coverage; use with ampicillin',
          dose: '4-5 mg/kg/dose IV every 24-48 hours (based on gestational/postnatal age)',
          route: 'IV',
          duration: '48-72 hours pending cultures; 7-10 days if confirmed',
          contraindications: ['Renal impairment (adjust dose)', 'Concurrent ototoxic drugs'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity'],
          alternatives: ['Amikacin']
        },
        {
          drug: 'Vitamin K1 (Phytomenadione)',
          indication: '🩸 ROUTINE at birth - Prevents vitamin K deficiency bleeding; ensure given',
          dose: '1 mg IM at birth (0.5 mg if <1500g)',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['None significant'],
          sideEffects: ['Injection site reaction', 'Rare anaphylaxis'],
          alternatives: ['Oral vitamin K (less effective)']
        }
      ],
      nonPharmacological: [
        'PHOTOTHERAPY OPTIMIZATION: Maximize skin exposure (diaper only); use reflective surfaces; ensure adequate irradiance (≥30 μW/cm²/nm for intensive); minimize interruptions',
        'EYE PROTECTION: Opaque eye shields during phototherapy; check positioning frequently; remove during feeding/mother interaction',
        'INCREASE FEEDING FREQUENCY: Breastfeed every 2-3 hours (8-12 times/day); promotes gut motility and bilirubin excretion; assess latch and milk transfer',
        'SUPPLEMENTATION if indicated: Expressed breast milk or formula if weight loss >10%, signs of dehydration, or inadequate output; use cup/syringe to avoid nipple confusion',
        'TEMPERATURE MONITORING: Servo-controlled warmer or incubator during phototherapy; prevent hypo/hyperthermia',
        'HYDRATION ASSESSMENT: Monitor daily weights, urine output (>6 wet diapers/day), urine specific gravity; increase feeds 10-20% during phototherapy',
        'SKIN-TO-SKIN CARE: When not under phototherapy; promotes breastfeeding, bonding, temperature regulation',
        'PARENT EDUCATION: Explain jaundice, treatment, warning signs; allay fears about phototherapy; emphasize importance of follow-up',
        'AVOID SUNLIGHT EXPOSURE: Ineffective (wrong wavelength spectrum), risk of sunburn, hyperthermia, and dehydration; NOT recommended',
        'AVOID TRADITIONAL REMEDIES: Many are ineffective or harmful; counsel against glucose water, herbal remedies, unproven medications'
      ],
      surgical: [
        'EXCHANGE TRANSFUSION: Procedure to remove bilirubin-laden blood and replace with donor blood; performed via umbilical venous catheter',
        'UMBILICAL VENOUS CATHETERIZATION: Central access for exchange transfusion; tip in IVC above diaphragm',
        'KASAI PORTOENTEROSTOMY: For biliary atresia; must be performed before 8 weeks for best outcomes; refer urgently if conjugated hyperbilirubinemia',
        'LIVER TRANSPLANTATION: For failed Kasai or end-stage liver disease from biliary atresia'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL/NICU: TSB at or approaching exchange transfusion threshold',
        '🔴 IMMEDIATE REFERRAL/NICU: Signs of acute bilirubin encephalopathy (lethargy, hypotonia, opisthotonus, seizures)',
        '🔴 IMMEDIATE REFERRAL/NICU: Rapidly rising bilirubin (>0.2 mg/dL/hour) despite phototherapy',
        '🔴 IMMEDIATE REFERRAL/NICU: Need for exchange transfusion (facility must have capability)',
        '🔴 IMMEDIATE SURGICAL REFERRAL: Conjugated hyperbilirubinemia with suspected biliary atresia (pale stools, dark urine)',
        '🟠 URGENT REFERRAL: Jaundice in first 24 hours requiring evaluation for hemolysis',
        '🟠 URGENT REFERRAL: Inadequate response to phototherapy after 24-48 hours',
        '🟠 URGENT REFERRAL: Preterm infant <35 weeks with significant jaundice',
        '🟠 URGENT REFERRAL: G6PD deficiency with severe jaundice',
        '🟡 ROUTINE REFERRAL: Prolonged jaundice (>2 weeks term, >3 weeks preterm) for evaluation',
        '🟡 ROUTINE REFERRAL: Audiology follow-up for all infants with severe hyperbilirubinemia (TSB >20 mg/dL)'
      ],
      monitoring: [
        'TSB MONITORING: Repeat every 4-6 hours if near threshold; every 6-12 hours during phototherapy; every 12-24 hours once below threshold',
        'REBOUND CHECK: Obtain TSB 12-24 hours after stopping phototherapy to check for rebound',
        'VITAL SIGNS: Temperature, heart rate, respiratory rate every 3-4 hours during phototherapy',
        'WEIGHT: Daily; >10% loss from birth weight indicates significant dehydration',
        'INTAKE/OUTPUT: Document feeds, wet diapers (target >6/day), stools; loose green stools expected with phototherapy',
        'HYDRATION STATUS: Skin turgor, fontanelle, mucous membranes; urine specific gravity',
        'NEUROLOGICAL STATUS: Alertness, tone, feeding ability, cry; watch for signs of ABE',
        'PHOTOTHERAPY EQUIPMENT: Verify light source functioning; measure irradiance; change bulbs as needed',
        'EYE PROTECTION: Check eye shields in place and not displaced',
        'HEMOGLOBIN: Repeat if hemolysis; may need transfusion for severe anemia'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

WITHIN 24-48 HOURS of discharge:
• Repeat TSB if discharged <48 hours of age
• Clinical assessment for jaundice, feeding, hydration
• Weight check

1-2 WEEKS:
• Resolution of jaundice in most cases
• Weight gain assessment
• Continued breastfeeding support
• If jaundice persists >2 weeks: evaluate for conjugated hyperbilirubinemia, hypothyroidism

HIGH-RISK FOLLOW-UP (TSB >20 mg/dL, ABE, Exchange Transfusion):
• Audiology screening (ABR/BERA) at 1-3 months - auditory neuropathy risk
• Developmental assessment at 3, 6, 12, 18, 24 months
• Neurological examination
• Early intervention referral if concerns

G6PD DEFICIENCY:
• Confirm diagnosis with quantitative assay after acute episode
• Parent education on trigger avoidance (fava beans, certain drugs, naphthalene)
• Provide list of medications to avoid
• Medical alert identification

RECURRENCE PREVENTION:
• If Rh incompatibility: Ensure mother receives anti-D prophylaxis for future pregnancies
• Genetic counseling if hereditary condition identified`
    },

    redFlags: [
      '🔴 CRITICAL: Jaundice appearing within first 24 hours of life - Always pathological; urgent evaluation for hemolysis',
      '🔴 CRITICAL: Signs of acute bilirubin encephalopathy - Lethargy, hypotonia, poor feeding, high-pitched cry, opisthotonus, seizures; EMERGENCY',
      '🔴 CRITICAL: TSB at or above exchange transfusion threshold - Immediate preparation for exchange',
      '🔴 CRITICAL: TSB rising >0.2 mg/dL/hour (>5 mg/dL/day) despite phototherapy - Consider IVIG, prepare for exchange',
      '🔴 CRITICAL: Severe anemia (Hb <10 g/dL) with jaundice - Active hemolysis; may need transfusion and exchange',
      '🔴 CRITICAL: Opisthotonus, retrocollis, fever with jaundice - Advanced ABE; irreversible damage occurring',
      '🟠 URGENT: Conjugated hyperbilirubinemia - Direct bilirubin >1.5 mg/dL; evaluate for biliary atresia urgently',
      '🟠 URGENT: Pale stools with dark urine - Biliary obstruction; surgical emergency if biliary atresia',
      '🟠 URGENT: Preterm infant (<35 weeks) with jaundice - Lower thresholds; higher risk of kernicterus',
      '🟠 URGENT: Known G6PD deficiency with acute jaundice - Risk of rapid hemolysis and severe hyperbilirubinemia',
      '🟠 URGENT: Poor feeding, excessive weight loss (>10%), dehydration - Contributes to jaundice; needs intervention',
      '🟡 CAUTION: Prolonged jaundice >2 weeks (term) or >3 weeks (preterm) - Needs investigation for underlying cause'
    ],

    complications: [
      'ACUTE BILIRUBIN ENCEPHALOPATHY (ABE): Acute neurological dysfunction from bilirubin toxicity; reversible if treated early',
      'KERNICTERUS (Chronic Bilirubin Encephalopathy): Permanent neurological sequelae - choreoathetoid cerebral palsy, hearing loss, gaze palsy, dental dysplasia',
      'SENSORINEURAL HEARING LOSS: Auditory neuropathy spectrum disorder; may occur even without classic kernicterus; screen with ABR',
      'DEVELOPMENTAL DELAY: Cognitive, motor, language delays; may be subtle',
      'BILIRUBIN-INDUCED NEUROLOGIC DYSFUNCTION (BIND): Spectrum of subtle neurological deficits',
      'DEATH: From severe ABE, multiorgan failure; case fatality 10-30% in severe cases in resource-limited settings',
      'EXCHANGE TRANSFUSION COMPLICATIONS: Electrolyte disturbances, thrombocytopenia, infection, NEC, arrhythmia, death (1-3%)',
      'PHOTOTHERAPY COMPLICATIONS: Dehydration, temperature instability, retinal damage (if eyes not protected), bronze baby syndrome (if conjugated)',
      'ANEMIA: From ongoing hemolysis; may require red cell transfusion',
      'HYDROPS FETALIS: Severe Rh disease; antenatal complications',
      'LONG-TERM SEQUELAE: Cerebral palsy, intellectual disability, special education needs, lifelong care requirements'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS BY SEVERITY
════════════════════════════════════════════════════════════════════════════════

PHYSIOLOGICAL JAUNDICE:
• Excellent prognosis
• Self-limiting, resolves by 2 weeks
• No long-term sequelae

PATHOLOGICAL JAUNDICE - TREATED EARLY:
• Good prognosis with timely phototherapy
• Most infants have complete recovery
• Low risk of sequelae if TSB stays below exchange threshold

SEVERE HYPERBILIRUBINEMIA (TSB >25 mg/dL):
• Risk of auditory neuropathy: 5-10%
• Risk of subtle neurological dysfunction: variable
• Better outcomes with intensive phototherapy and/or exchange

ACUTE BILIRUBIN ENCEPHALOPATHY:
• Early phase (lethargy, hypotonia): Potentially reversible with urgent exchange
• Intermediate/advanced phase: High risk of permanent damage
• Mortality: 10-30% in severe cases

KERNICTERUS:
• Permanent, irreversible
• Lifelong disability requiring ongoing care
• Choreoathetoid CP affects ~50% of survivors
• Hearing loss in 60-80%
• Normal cognition in ~30% (but motor and hearing impaired)

PROGNOSTIC FACTORS:
Better prognosis:
• Early detection and treatment
• TSB below exchange threshold
• No signs of ABE
• Full-term infant
• No hemolysis

Worse prognosis:
• Delayed presentation
• Signs of ABE at presentation
• TSB at or above exchange threshold
• Preterm infant
• Active hemolysis (G6PD, isoimmune)
• Limited access to exchange transfusion`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

1. ANTENATAL:
   • Blood grouping and Rh typing for all pregnant women
   • Antibody screening for Rh-negative women
   • Anti-D immunoglobulin at 28 weeks and within 72 hours of delivery for Rh-negative mothers
   • G6PD screening programs (where feasible)
   • Avoid oxidant drugs in G6PD-deficient mothers near term

2. INTRAPARTUM:
   • Minimize birth trauma (cephalohematoma, bruising)
   • Proper cord clamping technique
   • Ensure vitamin K administration at birth

3. EARLY POSTPARTUM:
   • Universal bilirubin screening before discharge (TSB or TcB)
   • Early and frequent breastfeeding (8-12 times/day)
   • Lactation support to ensure adequate intake
   • Delay discharge until adequate feeding established
   • Risk assessment using Bhutani nomogram

SECONDARY PREVENTION (Early Detection):

• Pre-discharge TSB/TcB measurement in all newborns
• Risk stratification using hour-specific nomogram
• Targeted follow-up for high-risk infants within 24-48 hours
• Parent education on jaundice warning signs
• Low threshold for TSB measurement if jaundice observed

TERTIARY PREVENTION (Preventing Complications):

• Prompt phototherapy when indicated
• Intensive phototherapy for rapidly rising TSB
• IVIG for isoimmune hemolysis
• Exchange transfusion when needed
• Early recognition and treatment of ABE
• Audiology screening for all infants with severe hyperbilirubinemia

HEALTH SYSTEM STRENGTHENING:

• Ensure availability of TSB measurement in all birthing facilities
• Provide functional phototherapy equipment with adequate irradiance
• Train staff on hour-specific thresholds and treatment protocols
• Establish referral pathways for exchange transfusion
• Community education on dangers of neonatal jaundice
• Discourage harmful traditional practices`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS NEONATAL JAUNDICE?
Jaundice is the yellow color of a baby's skin and eyes caused by a substance called bilirubin. It is very common in newborn babies. Most jaundice is harmless and goes away on its own. However, if bilirubin levels get too high, it can damage the brain permanently.

WHY DOES IT HAPPEN?
• All babies produce bilirubin when red blood cells break down
• Newborn babies cannot get rid of bilirubin as fast as older children and adults
• In some babies, bilirubin builds up faster than normal (blood group problems, infections, certain conditions)

WARNING SIGNS - SEEK MEDICAL CARE IMMEDIATELY IF:
• Baby becomes very yellow (especially palms and soles)
• Baby is difficult to wake up or very sleepy
• Baby is not feeding well or refuses to feed
• Baby has a high-pitched or strange cry
• Baby becomes stiff or arches the back
• Baby has a fever

WHAT IS PHOTOTHERAPY?
• Special blue lights that help break down bilirubin in the skin
• Baby will be undressed (except for diaper) under the lights
• Eyes will be covered to protect them
• Baby can come out for feeding - keep interruptions brief
• The light is not harmful - it is not UV light

HOW CAN YOU HELP?
• Feed your baby frequently (every 2-3 hours, at least 8-12 times per day)
• Breast milk is best - do not stop breastfeeding
• Watch for signs of good feeding: baby is satisfied, has wet diapers, is gaining weight
• Make sure baby has at least 6 wet diapers per day
• Bring baby for follow-up as advised

WHAT DOES NOT WORK:
• Putting baby in sunlight is NOT recommended - it can cause sunburn and overheating
• Glucose water or herbal medicines do not help and may be harmful
• Delaying medical care can cause permanent brain damage

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your baby may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• What is causing my baby's jaundice?
• What is my baby's bilirubin level?
• Does my baby need treatment?
• How long will treatment take?
• When should I bring my baby back for a check?
• What warning signs should I watch for at home?
• Is my baby at risk for hearing problems?`,

    references: [
      'American Academy of Pediatrics. Clinical Practice Guideline Revision: Management of Hyperbilirubinemia in the Newborn Infant 35 or More Weeks of Gestation. Pediatrics. 2022;150(3):e2022058859.',
      'Bhutani VK, Johnson L, Sivieri EM. Predictive ability of a predischarge hour-specific serum bilirubin for subsequent significant hyperbilirubinemia in healthy term and near-term newborns. Pediatrics. 1999;103(1):6-14.',
      'Olusanya BO, Kaplan M, Hansen TWR. Neonatal hyperbilirubinemia: a global perspective. Lancet Child Adolesc Health. 2018;2(8):610-620.',
      'Slusher TM, Zamora TG, Appiah D, et al. Burden of severe neonatal jaundice: a systematic review and meta-analysis. BMJ Paediatr Open. 2017;1(1):e000105.',
      'World Health Organization. WHO recommendations on newborn health: guidelines approved by the WHO Guidelines Review Committee. Geneva: WHO; 2017.',
      'Watchko JF, Tiribelli C. Bilirubin-induced neurologic damage--mechanisms and management approaches. N Engl J Med. 2013;369(21):2021-30.',
      'Kaplan M, Hammerman C. Glucose-6-phosphate dehydrogenase deficiency and severe neonatal hyperbilirubinemia: a complexity of interactions between genes and environment. Semin Fetal Neonatal Med. 2010;15(3):148-56.',
      'Maisels MJ, McDonagh AF. Phototherapy for neonatal jaundice. N Engl J Med. 2008;358(9):920-8.',
      'National Institute for Health and Care Excellence (NICE). Jaundice in newborn babies under 28 days. Clinical guideline CG98. 2010, updated 2016.',
      'Olusanya BO, Osibanjo FB, Mabogunje CA, Slusher TM, Olowe SA. The burden and management of neonatal jaundice in Nigeria: a scoping review of the literature. Niger J Clin Pract. 2016;19(1):1-17.',
      'Dennery PA, Seidman DS, Stevenson DK. Neonatal hyperbilirubinemia. N Engl J Med. 2001;344(8):581-90.',
      'Hansen TW. Prevention of neurodevelopmental sequelae of jaundice in the newborn. Dev Med Child Neurol. 2011;53 Suppl 4:24-8.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 42. PRETERM BIRTH COMPLICATIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '42',
    name: 'Preterm Birth Complications',
    slug: 'preterm-birth-complications',
    specialty: 'Pediatrics',
    bodySystem: 'Systemic',
    icdCode: 'P07.3',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Preterm birth is defined as birth before 37 completed weeks of gestation. It is the leading cause of neonatal mortality worldwide and the second leading cause of death in children under 5 years. Preterm infants face numerous complications due to organ immaturity, including respiratory distress syndrome (RDS), intraventricular hemorrhage (IVH), necrotizing enterocolitis (NEC), retinopathy of prematurity (ROP), bronchopulmonary dysplasia (BPD), and sepsis.

Classification by gestational age:
• Extremely preterm: <28 weeks
• Very preterm: 28-31+6 weeks
• Moderate preterm: 32-33+6 weeks
• Late preterm: 34-36+6 weeks

Survival and outcomes are directly related to gestational age, birth weight, access to neonatal intensive care, and quality of care. In resource-limited settings, mortality remains unacceptably high due to limited access to essential interventions such as antenatal corticosteroids, surfactant, CPAP, and kangaroo mother care.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 ANTENATAL CORTICOSTEROIDS - Single most important intervention; give to all women 24-34 weeks at risk of preterm delivery within 7 days; reduces RDS, IVH, NEC, death by 30-50%

🔴 RESPIRATORY DISTRESS SYNDROME (RDS) - Most common cause of death in preterm; surfactant deficiency; presents within hours of birth; treat with CPAP ± surfactant

🔴 THERMOREGULATION IS CRITICAL - Hypothermia dramatically increases mortality; use plastic wrap, warm resuscitation, incubator/radiant warmer, kangaroo mother care

🔴 KANGAROO MOTHER CARE (KMC) - Skin-to-skin contact reduces mortality by 40%; start as early as possible; equivalent or superior to incubator care

🔴 SURFACTANT saves lives - Give early for RDS; natural surfactants preferred; can be given via INSURE or LISA technique

🔴 AVOID HYPEROXIA - Target SpO2 90-95%; high oxygen causes ROP and BPD; use blended oxygen when available

🔴 FEEDING: BREAST MILK IS ESSENTIAL - Reduces NEC, sepsis, improves neurodevelopment; start trophic feeds early; donor milk if mother's milk unavailable

🔴 CAFFEINE for apnea of prematurity - Start early in infants <32 weeks; reduces BPD and improves neurodevelopment`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 15 million preterm births annually worldwide (11% of all births)
• 1 million deaths from preterm birth complications annually
• Leading cause of neonatal mortality (35% of all neonatal deaths)
• Second leading cause of under-5 mortality after pneumonia
• 75% of preterm deaths occur in infants born <32 weeks
• Survival at 22-24 weeks: 10-50% in high-income countries, near 0% in LMIC
• Survival at 28 weeks: 90% in HIC, 30-50% in LMIC
• Survival at 34 weeks: >98% in HIC, 85-95% in LMIC

AFRICAN EPIDEMIOLOGY:
• Africa has highest preterm birth rate globally (~12%)
• Over 4 million preterm births annually in sub-Saharan Africa
• Accounts for ~28% of global preterm deaths
• Case fatality rate for preterm: 10-30% overall; >80% for <28 weeks in many settings
• Limited NICU capacity: <10% of needed beds in most countries
• Antenatal corticosteroid coverage: <10% in many African countries
• Surfactant availability: Limited to tertiary centers; often unaffordable
• CPAP increasingly available but coverage remains low
• KMC implementation improving but inconsistent
• Leading causes of preterm birth: infection, malaria, pre-eclampsia, multiple pregnancy, cervical insufficiency
• Co-morbidities: HIV, anemia, malnutrition increase risk

MAJOR COMPLICATIONS BY FREQUENCY:
• Respiratory distress syndrome: 60-80% of <30 weeks
• Apnea of prematurity: 50-80% of <32 weeks
• Patent ductus arteriosus: 30-40% of <30 weeks
• Intraventricular hemorrhage: 20-30% of <32 weeks (severe in 10-15%)
• Necrotizing enterocolitis: 5-10% of VLBW infants
• Retinopathy of prematurity: 10-20% of <30 weeks (severe in 5%)
• Bronchopulmonary dysplasia: 20-40% of <28 weeks
• Late-onset sepsis: 20-30% of VLBW infants`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

IMMEDIATE ASSESSMENT AT BIRTH:
• Gestational age determination (Ballard score if uncertain)
• Birth weight: ELBW <1000g, VLBW <1500g, LBW <2500g
• APGAR score
• Need for resuscitation
• Temperature (hypothermia common)
• Respiratory effort

RESPIRATORY DISTRESS SYNDROME (RDS):
• Onset within 6 hours of birth (usually 2-4 hours)
• Tachypnea (RR >60/min)
• Grunting (to maintain FRC)
• Nasal flaring
• Intercostal/subcostal retractions
• Cyanosis (if severe)
• Diminished breath sounds
• Worsens over 24-48 hours without treatment
• CXR: Ground-glass appearance, air bronchograms, low lung volumes

APNEA OF PREMATURITY:
• Cessation of breathing >20 seconds OR
• Shorter pause with bradycardia (<100 bpm) or desaturation/cyanosis
• Central, obstructive, or mixed
• Onset typically day 2-7 of life
• More frequent in smaller/younger infants

INTRAVENTRICULAR HEMORRHAGE (IVH):
• Often asymptomatic (detected on routine ultrasound)
• May present with: sudden deterioration, apnea, seizures, bulging fontanelle, anemia, hypotension
• 90% occur within first 72 hours
• Grading: I (germinal matrix), II (into ventricle), III (ventricle dilated), IV (parenchymal involvement)

NECROTIZING ENTEROCOLITIS (NEC):
• Typically presents day 8-10 of life (later in ELBW)
• Abdominal distension
• Feeding intolerance (increased residuals, vomiting)
• Bloody stools
• Bilious aspirates
• Abdominal wall discoloration/erythema
• Lethargy, temperature instability
• Apnea, bradycardia
• X-ray: Pneumatosis intestinalis, portal venous gas, pneumoperitoneum (perforation)

PATENT DUCTUS ARTERIOSUS (PDA):
• Continuous or systolic murmur (machinery murmur)
• Bounding pulses
• Wide pulse pressure
• Active precordium
• Respiratory deterioration
• May be asymptomatic (diagnosed on echo)

HYPOTHERMIA:
• Cold stress: 36.0-36.4°C
• Moderate hypothermia: 32.0-35.9°C
• Severe hypothermia: <32°C
• Signs: lethargy, poor feeding, weak cry, cool extremities, mottling, apnea, bradycardia, hypoglycemia

HYPOGLYCEMIA:
• Often asymptomatic
• Jitteriness, tremors
• Lethargy, hypotonia
• Poor feeding
• Apnea
• Seizures (severe)

LATE-ONSET SEPSIS:
• Temperature instability (hypo or hyperthermia)
• Lethargy, poor feeding
• Apnea, respiratory distress
• Abdominal distension
• Glucose instability
• Jaundice`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: ESTABLISH GESTATIONAL AGE AND WEIGHT
• Best obstetric estimate (early ultrasound most accurate)
• New Ballard Score if uncertain
• Plot weight, length, head circumference on growth charts
• Classify: SGA, AGA, LGA

STEP 2: INITIAL STABILIZATION AND ASSESSMENT
• APGAR scores at 1 and 5 minutes
• Temperature (core)
• Blood glucose
• Respiratory status (Silverman-Andersen score)
• Need for respiratory support

STEP 3: EVALUATE FOR IMMEDIATE COMPLICATIONS

Respiratory Distress Syndrome:
• Clinical: grunting, flaring, retractions within 6 hours
• Chest X-ray: diffuse ground-glass, air bronchograms, low volumes
• Blood gas: hypoxemia, hypercapnia, acidosis

Apnea:
• Clinical observation ± cardiorespiratory monitoring
• Rule out underlying cause (sepsis, IVH, hypoglycemia, anemia, etc.)

Intraventricular Hemorrhage:
• Cranial ultrasound at day 3-7 and week 4-6 (earlier if symptomatic)
• MRI if abnormalities or term equivalent age

Necrotizing Enterocolitis:
• Clinical: abdominal distension, feeding intolerance, bloody stools
• Abdominal X-ray: pneumatosis, portal gas, pneumoperitoneum
• Labs: thrombocytopenia, metabolic acidosis, elevated CRP

Patent Ductus Arteriosus:
• Clinical: murmur, wide pulse pressure
• Echocardiogram: gold standard

STEP 4: ONGOING MONITORING
• Daily weights
• Head circumference weekly
• Blood glucose initially Q3-6h, then as needed
• Cranial ultrasound per protocol
• ROP screening starting at 31 weeks PMA or 4 weeks of age
• Hearing screening before discharge

STEP 5: ASSESS FOR LATE COMPLICATIONS
• Bronchopulmonary dysplasia: Oxygen requirement at 36 weeks PMA
• Retinopathy of prematurity: Dilated eye exams
• Post-hemorrhagic hydrocephalus: Serial head circumference, ultrasound
• Growth failure: Weekly weights and measurements`,

    differentialDiagnosis: [
      'Respiratory Distress Syndrome (RDS) - Surfactant deficiency; ground-glass CXR; responds to surfactant',
      'Transient Tachypnea of Newborn (TTN) - Usually term/late preterm; resolves 24-72h; wet lungs on CXR',
      'Pneumonia (congenital) - Risk factors: PROM, chorioamnionitis; may be indistinguishable from RDS',
      'Meconium Aspiration Syndrome - Usually term/post-term; meconium-stained fluid; patchy CXR',
      'Pneumothorax - Sudden deterioration; asymmetric breath sounds; confirm with X-ray/transillumination',
      'Congenital Heart Disease - Persistent cyanosis; murmur; poor response to oxygen; echo diagnostic',
      'Pulmonary Hypoplasia - Severe refractory hypoxia; associated with oligohydramnios, CDH',
      'Sepsis/Pneumonia - May mimic RDS; risk factors for infection; positive cultures',
      'Metabolic acidosis - May present with respiratory distress; check blood gas, lactate',
      'Anemia - Pallor; tachycardia; may worsen respiratory status; check Hgb/Hct',
      'Congenital Diaphragmatic Hernia - Scaphoid abdomen; bowel in chest on CXR',
      'Tracheoesophageal Fistula - Excessive secretions; aspiration; unable to pass NG tube',
      'Choanal Atresia - Cyclical cyanosis; inability to pass catheter through nares'
    ],

    investigations: [
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Check immediately at birth, then Q3-6h until stable; hypoglycemia common and dangerous',
        availability: 'BASIC'
      },
      {
        name: 'Temperature',
        indication: '🔴 ESSENTIAL - Core temperature; target 36.5-37.5°C; hypothermia increases mortality',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - Continuous monitoring; target SpO2 90-95%; avoid hyperoxia',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 ESSENTIAL if respiratory distress - RDS: ground-glass; TTN: wet lungs; pneumothorax; ETT position',
        availability: 'BASIC'
      },
      {
        name: 'Arterial or Capillary Blood Gas',
        indication: '🔴 For respiratory distress - Assess oxygenation, ventilation, acid-base; guide respiratory support',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Anemia, thrombocytopenia (sepsis, DIC, NEC); neutropenia/neutrophilia (sepsis)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: '🔴 Before antibiotics if sepsis suspected - Early and late-onset sepsis common in preterm',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: 'Serial CRP for sepsis evaluation; rises 12-24h after infection onset',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: 'Sodium, potassium, calcium; preterm prone to hyponatremia, hyperkalemia, hypocalcemia',
        availability: 'BASIC'
      },
      {
        name: 'Serum Bilirubin',
        indication: 'Jaundice common; lower thresholds for preterm; risk of kernicterus',
        availability: 'BASIC'
      },
      {
        name: 'Cranial Ultrasound',
        indication: '🔴 ROUTINE - Day 3-7 and week 4-6 for all <32 weeks; detect IVH, PVL; earlier if symptomatic',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: 'Suspected PDA; congenital heart disease; hemodynamic instability',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal X-ray',
        indication: 'Suspected NEC - pneumatosis, portal venous gas, pneumoperitoneum; bowel obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture',
        indication: 'If sepsis/meningitis suspected; delay if unstable; always before prolonged antibiotics',
        availability: 'BASIC'
      },
      {
        name: 'Retinopathy of Prematurity Screening',
        indication: '🔴 MANDATORY - All infants <32 weeks or <1500g; first exam at 31 weeks PMA or 4 weeks age',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Hearing Screening (OAE or ABR)',
        indication: 'Before discharge; high risk for sensorineural hearing loss',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ THERMOREGULATION: Warm delivery room (25-28°C); plastic wrap for <32 weeks; warm resuscitation surface; avoid heat loss',
        '⚡ DELAYED CORD CLAMPING: 30-60 seconds if baby vigorous; improves Hgb, reduces IVH',
        '⚡ ASSESS RESPIRATION: Stimulate gently; if not breathing, start PPV with room air (or 21-30% O2 for <28 weeks)',
        '⚡ RESPIRATORY SUPPORT: CPAP early for respiratory distress; intubate and surfactant if severe',
        '⚡ GLUCOSE MONITORING: Check within 1 hour; treat hypoglycemia (<45 mg/dL) immediately',
        '⚡ IV ACCESS: Umbilical catheter or peripheral IV for fluids and medications',
        '⚡ SEPSIS CONSIDERATION: Cultures and antibiotics if risk factors or clinical concern',
        '⚡ VITAMIN K: 0.5 mg IM for <1500g; 1 mg for larger infants',
        '⚡ KANGAROO MOTHER CARE: Start as soon as stable; skin-to-skin with mother'
      ],
      pharmacological: [
        {
          drug: 'Antenatal Corticosteroids (Dexamethasone or Betamethasone)',
          indication: '🔴 MOST IMPORTANT INTERVENTION - Give to mother 24-34 weeks at risk of delivery within 7 days',
          dose: 'Betamethasone 12 mg IM q24h x 2 doses OR Dexamethasone 6 mg IM q12h x 4 doses',
          route: 'IM to mother',
          duration: 'Complete course ideally; benefit even if incomplete',
          contraindications: ['Active chorioamnionitis (relative)', 'Mother with systemic fungal infection'],
          sideEffects: ['Transient maternal hyperglycemia', 'Maternal insomnia'],
          alternatives: ['Either steroid equivalent; dexamethasone more available']
        },
        {
          drug: 'Surfactant (Poractant alfa, Beractant, Calfactant)',
          indication: '🫁 RESPIRATORY DISTRESS SYNDROME - Early rescue for RDS; prophylactic for extreme preterms',
          dose: 'Poractant alfa: 200 mg/kg initial, 100 mg/kg repeat; Beractant: 100 mg/kg; via ETT',
          route: 'Intratracheal (via ETT or LISA catheter)',
          duration: 'May repeat Q6-12h up to 3-4 doses if ongoing RDS',
          contraindications: ['Congenital anomalies incompatible with life'],
          sideEffects: ['Transient desaturation during administration', 'Bradycardia', 'ETT obstruction', 'Pulmonary hemorrhage (rare)'],
          alternatives: ['Natural surfactants (bovine, porcine) preferred over synthetic']
        },
        {
          drug: 'Caffeine Citrate',
          indication: '☕ APNEA OF PREMATURITY - Start early in all <32 weeks; reduces BPD, improves outcomes',
          dose: 'Loading: 20 mg/kg IV/PO; Maintenance: 5-10 mg/kg/day',
          route: 'IV or PO',
          duration: 'Until 33-35 weeks PMA or apnea-free for 5-7 days off caffeine',
          contraindications: ['Supraventricular tachycardia', 'Seizure disorder (relative)'],
          sideEffects: ['Tachycardia', 'Jitteriness', 'Feeding intolerance', 'Diuresis'],
          alternatives: ['Aminophylline/theophylline (narrower therapeutic index)']
        },
        {
          drug: 'Ampicillin + Gentamicin',
          indication: '🦠 EMPIRIC ANTIBIOTICS for early-onset sepsis - Start if risk factors or clinical concern',
          dose: 'Ampicillin: 50 mg/kg/dose Q8-12h; Gentamicin: 4-5 mg/kg/dose Q24-48h (based on GA)',
          route: 'IV',
          duration: '48-72h pending cultures; 7-10 days if confirmed; 14-21 days for meningitis',
          contraindications: ['Known allergy'],
          sideEffects: ['Gentamicin: nephrotoxicity, ototoxicity'],
          alternatives: ['Cefotaxime (if meningitis suspected, avoid ceftriaxone in neonates)']
        },
        {
          drug: 'Vancomycin + Gentamicin (or Cefepime)',
          indication: '🦠 LATE-ONSET SEPSIS - Empiric coverage for CONS and gram-negatives',
          dose: 'Vancomycin: 10-15 mg/kg/dose Q8-12h (based on GA); Gentamicin as above',
          route: 'IV',
          duration: 'Duration based on culture and clinical response',
          contraindications: ['Known allergy', 'Adjust for renal function'],
          sideEffects: ['Vancomycin: nephrotoxicity, red man syndrome; Gentamicin: oto/nephrotoxicity'],
          alternatives: ['Cefepime, Meropenem for resistant organisms']
        },
        {
          drug: 'Ibuprofen or Indomethacin',
          indication: '❤️ PATENT DUCTUS ARTERIOSUS (Hemodynamically significant) - Medical closure',
          dose: 'Ibuprofen: 10 mg/kg then 5 mg/kg at 24h and 48h; Indomethacin: 0.2 mg/kg Q12h x 3',
          route: 'IV (PO for ibuprofen if tolerating feeds)',
          duration: '3 doses; may repeat course if PDA persists',
          contraindications: ['Active NEC', 'Significant IVH', 'Renal failure', 'Thrombocytopenia', 'Active bleeding'],
          sideEffects: ['Oliguria', 'Renal impairment', 'GI perforation', 'Platelet dysfunction'],
          alternatives: ['Paracetamol/Acetaminophen (emerging evidence); surgical ligation']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '❤️ ALTERNATIVE for PDA closure - Emerging evidence; fewer renal side effects',
          dose: '15 mg/kg/dose Q6h IV or PO',
          route: 'IV or PO',
          duration: '3-7 days',
          contraindications: ['Hepatic impairment', 'Known allergy'],
          sideEffects: ['Hepatotoxicity (rare at standard doses)', 'Transient LFT elevation'],
          alternatives: ['Ibuprofen, Indomethacin, Surgical ligation']
        },
        {
          drug: '10% Dextrose',
          indication: '💧 HYPOGLYCEMIA treatment and maintenance fluids',
          dose: 'Bolus: 2 mL/kg (200 mg/kg) for symptomatic hypoglycemia; Maintenance: 60-80 mL/kg/day Day 1, advance',
          route: 'IV',
          duration: 'Until stable oral/enteral feeds established',
          contraindications: ['Hyperglycemia'],
          sideEffects: ['Hyperglycemia', 'Osmotic diuresis'],
          alternatives: ['Higher concentrations via central line if needed']
        },
        {
          drug: 'Calcium Gluconate 10%',
          indication: '🦴 HYPOCALCEMIA - Common in preterm, especially sick infants',
          dose: 'Symptomatic: 100-200 mg/kg (1-2 mL/kg) IV slow push; Maintenance: 200-400 mg/kg/day',
          route: 'IV (slow push or infusion)',
          duration: 'Until calcium normalized and feeds advancing',
          contraindications: ['Hypercalcemia', 'Digoxin use (use caution)'],
          sideEffects: ['Bradycardia (if given too fast)', 'Tissue necrosis if extravasation'],
          alternatives: ['Calcium chloride (more potent but more caustic)']
        },
        {
          drug: 'Vitamin K1 (Phytomenadione)',
          indication: '🩸 ROUTINE - Prevents vitamin K deficiency bleeding',
          dose: '0.5 mg IM for <1500g; 1 mg IM for larger infants',
          route: 'IM',
          duration: 'Single dose at birth',
          contraindications: ['None significant'],
          sideEffects: ['Injection site reaction'],
          alternatives: ['Oral vitamin K (less effective, requires multiple doses)']
        },
        {
          drug: 'Erythropoietin (EPO) ± Iron',
          indication: '🩸 ANEMIA OF PREMATURITY - Reduces transfusion need',
          dose: 'EPO: 250-400 U/kg SC 3x/week; Iron: 2-6 mg/kg/day PO',
          route: 'EPO: SC; Iron: PO',
          duration: 'Until ~35-36 weeks PMA or Hgb stable',
          contraindications: ['Hypertension', 'Known thrombotic risk'],
          sideEffects: ['Neutropenia', 'Hypertension', 'ROP (controversial)'],
          alternatives: ['Transfusion for symptomatic anemia']
        },
        {
          drug: 'Multivitamins with Vitamin D',
          indication: '💊 NUTRITIONAL SUPPLEMENTATION - All preterm infants',
          dose: 'Vitamin D: 400-1000 IU/day; Multivitamin per formulation',
          route: 'PO',
          duration: 'Throughout hospitalization and after discharge',
          contraindications: ['Hypercalcemia'],
          sideEffects: ['Generally well tolerated'],
          alternatives: ['Individual vitamin supplementation']
        }
      ],
      nonPharmacological: [
        'KANGAROO MOTHER CARE (KMC): Continuous or prolonged skin-to-skin contact; reduces mortality 40%, sepsis, hypothermia; promotes breastfeeding and bonding; start early when stable',
        'THERMOREGULATION: Incubator or radiant warmer; target 36.5-37.5°C; plastic wrap at delivery; warm transport; humidity 60-80% for <28 weeks',
        'CONTINUOUS POSITIVE AIRWAY PRESSURE (CPAP): First-line for RDS; 5-8 cm H2O; reduces need for intubation; use bubble CPAP where available',
        'GENTLE VENTILATION: If intubated, use low tidal volumes (4-6 mL/kg), permissive hypercapnia, volume-targeted ventilation; minimize barotrauma',
        'OXYGEN TARGETING: SpO2 90-95%; avoid hyperoxia (ROP, BPD risk); use blended oxygen when available',
        'DEVELOPMENTAL CARE: Minimize handling, noise, light; clustered care; positioning; non-nutritive sucking; NIDCAP principles',
        'BREAST MILK FEEDING: Mother\'s own milk best; donor milk if unavailable; start trophic feeds day 1-2; advance slowly; fortify when volumes established',
        'MINIMAL ENTERAL NUTRITION: Trophic feeds 10-20 mL/kg/day starting day 1; promotes gut maturation; reduces TPN duration',
        'INFECTION PREVENTION: Hand hygiene; aseptic technique; minimize invasive procedures; early line removal; antibiotic stewardship',
        'FAMILY-CENTERED CARE: Parent involvement in care; education; psychological support; discharge planning'
      ],
      surgical: [
        'SURFACTANT ADMINISTRATION: Via ETT (INSURE technique: Intubate-Surfactant-Extubate) or LISA (Less Invasive Surfactant Administration via thin catheter)',
        'PDA LIGATION: If medical treatment fails or contraindicated; thoracotomy or thoracoscopic approach',
        'NEC SURGERY: Laparotomy for perforation or failure of medical management; bowel resection; primary peritoneal drainage as temporizing measure',
        'VP SHUNT: For post-hemorrhagic hydrocephalus; after temporary measures (serial LP, reservoir)',
        'ROP LASER/ANTI-VEGF: Laser photocoagulation or intravitreal bevacizumab for severe ROP',
        'INGUINAL HERNIA REPAIR: Common in preterm; usually before discharge or shortly after',
        'TRACHEOSTOMY: For prolonged ventilator dependence (rare)'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL TO NICU: All infants <32 weeks or <1500g; any preterm with respiratory distress',
        '🔴 IMMEDIATE REFERRAL: Suspected NEC (abdominal distension, bloody stools, pneumatosis)',
        '🔴 IMMEDIATE REFERRAL: Suspected sepsis with hemodynamic instability',
        '🔴 IMMEDIATE REFERRAL: Severe IVH (Grade III-IV) or post-hemorrhagic hydrocephalus',
        '🔴 IMMEDIATE REFERRAL: Failed CPAP requiring mechanical ventilation',
        '🔴 SURGICAL REFERRAL: NEC with perforation; PDA ligation; VP shunt',
        '🟠 URGENT REFERRAL: Hemodynamically significant PDA not responding to medical treatment',
        '🟠 URGENT REFERRAL: Severe ROP requiring treatment',
        '🟠 URGENT REFERRAL: BPD requiring home oxygen',
        '🟡 ROUTINE REFERRAL: Developmental follow-up for all <32 weeks or <1500g',
        '🟡 ROUTINE REFERRAL: Ophthalmology for ROP screening',
        '🟡 ROUTINE REFERRAL: Audiology for hearing assessment'
      ],
      monitoring: [
        'CONTINUOUS CARDIORESPIRATORY MONITORING: Heart rate, respiratory rate, SpO2; apnea detection',
        'TEMPERATURE: Q3-4h or continuous; maintain 36.5-37.5°C',
        'BLOOD GLUCOSE: Q3-6h initially; maintain >45 mg/dL',
        'BLOOD PRESSURE: Invasive or non-invasive; mean BP ≥ gestational age in weeks as rough guide',
        'WEIGHT: Daily; expect initial loss 10-15%, then gain 15-20 g/kg/day',
        'HEAD CIRCUMFERENCE: Weekly; rapid increase suggests hydrocephalus',
        'INTAKE/OUTPUT: Strict recording; urine output target 1-3 mL/kg/hour',
        'CRANIAL ULTRASOUND: Day 3-7, week 4-6, and term equivalent age for <32 weeks',
        'ROP SCREENING: Starting at 31 weeks PMA or 4 weeks of age; q1-2 weeks until retina mature',
        'LABS: Blood gas PRN; CBC, CRP for sepsis evaluation; electrolytes; bilirubin',
        'GROWTH: Track weight, length, head circumference on preterm growth charts',
        'NUTRITION: Track feeds, TPN, fortification, growth velocity'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
POST-DISCHARGE FOLLOW-UP
════════════════════════════════════════════════════════════════════════════════

DISCHARGE CRITERIA:
• Maintaining temperature in open crib
• Feeding well (breast or bottle) with appropriate weight gain
• No apnea/bradycardia for 5-8 days (off caffeine)
• Stable respiratory status (off oxygen or stable home O2)
• ROP resolved or stable
• Hearing screen completed
• Vaccines up to date
• Parents trained in care, CPR, and warning signs

IMMEDIATE POST-DISCHARGE (1-2 weeks):
• Weight and feeding assessment
• Medication review
• Parental support and questions

NEONATAL FOLLOW-UP CLINIC:
• 2 weeks, 1 month, 3 months, 6 months, 12 months, 18-24 months (corrected age)
• Growth monitoring (use corrected age until 2-3 years)
• Developmental screening (ASQ, Bayley)
• Neurological examination
• Immunizations (follow chronological age)

SUBSPECIALTY FOLLOW-UP:
• Ophthalmology: Until ROP resolved or retina mature
• Audiology: If failed screen or risk factors
• Pulmonology: If BPD/home oxygen
• Neurology: If IVH, PVL, seizures
• Early Intervention: Physical, occupational, speech therapy as needed

LONG-TERM OUTCOMES TO MONITOR:
• Cerebral palsy (5-10% of <32 weeks)
• Cognitive impairment (20-30%)
• Visual impairment (ROP-related)
• Hearing loss (5-10%)
• Behavioral issues (ADHD, autism spectrum)
• Chronic lung disease
• Growth and nutrition

PARENT EDUCATION:
• Safe sleep (back to sleep, no co-sleeping)
• RSV prophylaxis (palivizumab) if eligible
• Avoid smoke exposure
• Hand hygiene and infection prevention
• Signs of illness requiring medical attention
• Developmental milestones (use corrected age)`
    },

    redFlags: [
      '🔴 CRITICAL: Severe respiratory distress not responding to CPAP - May need intubation, surfactant, or have pneumothorax',
      '🔴 CRITICAL: Sudden deterioration with unilateral decreased breath sounds - Tension pneumothorax; needle decompression',
      '🔴 CRITICAL: Abdominal distension with bilious aspirates and bloody stools - NEC; NPO, antibiotics, surgery evaluation',
      '🔴 CRITICAL: Seizures - May indicate IVH, hypoglycemia, meningitis, hypocalcemia; urgent evaluation',
      '🔴 CRITICAL: Severe hypothermia (<32°C) - High mortality; aggressive rewarming; evaluate for sepsis',
      '🔴 CRITICAL: Persistent hypoglycemia despite IV dextrose - May need higher concentration, evaluate for metabolic/endocrine cause',
      '🔴 CRITICAL: Hemodynamic instability (hypotension, poor perfusion) - Sepsis, NEC, PDA, adrenal insufficiency',
      '🟠 URGENT: Increasing oxygen requirement - Worsening RDS, pneumonia, PDA, evolving BPD',
      '🟠 URGENT: Apnea not responding to caffeine - Evaluate for sepsis, anemia, IVH, NEC',
      '🟠 URGENT: Rapidly enlarging head circumference - Post-hemorrhagic hydrocephalus; needs imaging',
      '🟠 URGENT: Feeding intolerance with abdominal distension - Early NEC; hold feeds, evaluate',
      '🟡 CAUTION: Failure to gain weight despite adequate calories - Evaluate for underlying illness, inadequate intake, metabolic issue'
    ],

    complications: [
      'RESPIRATORY DISTRESS SYNDROME (RDS): Surfactant deficiency; hyaline membrane disease; treat with CPAP, surfactant',
      'BRONCHOPULMONARY DYSPLASIA (BPD): Chronic lung disease; oxygen dependency at 36 weeks PMA; long-term respiratory issues',
      'APNEA OF PREMATURITY: Central and/or obstructive; treat with caffeine, CPAP',
      'PNEUMOTHORAX: Complication of RDS or ventilation; may need chest tube',
      'INTRAVENTRICULAR HEMORRHAGE (IVH): Grades I-IV; severe IVH associated with poor neurodevelopmental outcomes',
      'PERIVENTRICULAR LEUKOMALACIA (PVL): White matter injury; associated with cerebral palsy',
      'POST-HEMORRHAGIC HYDROCEPHALUS: Complication of IVH; may need VP shunt',
      'NECROTIZING ENTEROCOLITIS (NEC): Bowel necrosis; medical or surgical management; high mortality',
      'PATENT DUCTUS ARTERIOSUS (PDA): May cause heart failure; treat medically or surgically',
      'RETINOPATHY OF PREMATURITY (ROP): Abnormal retinal vessel growth; can cause blindness; screen and treat',
      'LATE-ONSET SEPSIS: Common; CONS, gram-negatives, Candida; high mortality',
      'ANEMIA OF PREMATURITY: Multifactorial; may need transfusion or EPO',
      'METABOLIC BONE DISEASE (OSTEOPENIA): From inadequate calcium/phosphorus; fractures; supplement',
      'INGUINAL HERNIA: Common; surgical repair usually needed',
      'NEURODEVELOPMENTAL IMPAIRMENT: CP, cognitive delay, hearing loss, visual impairment',
      'DEATH: Mortality inversely related to gestational age; highest in extremely preterm'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
SURVIVAL BY GESTATIONAL AGE (WELL-RESOURCED NICU)
════════════════════════════════════════════════════════════════════════════════

22-23 weeks: 10-30% survival; high morbidity among survivors
24 weeks: 40-70% survival
25 weeks: 50-80% survival
26 weeks: 70-90% survival
27 weeks: 80-90% survival
28 weeks: 90-95% survival
30 weeks: >95% survival
32 weeks: >98% survival
34-36 weeks: >99% survival (similar to term)

SURVIVAL IN RESOURCE-LIMITED SETTINGS:
• Significantly lower across all gestational ages
• 28 weeks: 30-60% survival in many African NICUs
• 32 weeks: 70-90% survival
• Survival rates improving with increased availability of CPAP, KMC, antenatal steroids

NEURODEVELOPMENTAL OUTCOMES (SURVIVORS):
• <28 weeks: 25-50% with significant impairment
• 28-32 weeks: 10-20% with significant impairment
• 32-34 weeks: 5-10% with mild impairment
• Late preterm (34-36 weeks): Increased risk compared to term but generally good outcomes

LONG-TERM OUTCOMES:
• Cerebral palsy: 5-10% of <32 weeks
• Cognitive impairment: 20-30% of ELBW
• Hearing loss: 5-10%
• Visual impairment (ROP-related): 1-5%
• Behavioral/learning difficulties: 20-40%
• Chronic respiratory issues: 10-20%
• Most survivors lead productive lives

PROGNOSTIC FACTORS:
Better outcomes:
• Higher gestational age and birth weight
• Antenatal corticosteroids given
• Female sex
• Singleton
• Appropriate for gestational age
• No major complications (severe IVH, NEC, sepsis)
• Access to quality NICU care
• Breast milk feeding

Worse outcomes:
• Extreme prematurity (<26 weeks)
• No antenatal steroids
• Male sex
• Multiple gestation
• Small for gestational age
• Chorioamnionitis
• Severe complications
• Resource-limited setting`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION OF PRETERM BIRTH AND ITS COMPLICATIONS
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Prevent Preterm Birth):

1. PRECONCEPTION:
   • Optimize maternal health before pregnancy
   • Folic acid supplementation
   • Treat chronic conditions (diabetes, hypertension)
   • Smoking cessation
   • Achieve healthy BMI
   • Adequate interpregnancy interval (≥18 months)

2. ANTENATAL CARE:
   • Early and regular ANC visits
   • Screen and treat infections (UTI, STIs, BV, malaria)
   • Cervical length screening in high-risk women
   • Progesterone for short cervix or prior preterm birth
   • Cerclage for cervical insufficiency
   • Aspirin for pre-eclampsia prevention
   • Malaria prophylaxis in endemic areas
   • Nutritional support

3. REDUCE IATROGENIC PRETERM BIRTH:
   • Avoid non-medically indicated early delivery
   • Accurate dating by early ultrasound

SECONDARY PREVENTION (Improve Outcomes When Preterm Birth Imminent):

1. ANTENATAL CORTICOSTEROIDS:
   • Single most effective intervention
   • Give to all women 24-34 weeks at risk of delivery within 7 days
   • Reduces RDS by 34%, IVH by 46%, mortality by 31%
   • Benefit outweighs risk even with infection

2. MAGNESIUM SULFATE FOR NEUROPROTECTION:
   • Give to women <32 weeks for neuroprotection of fetus
   • Reduces cerebral palsy by 30-40%

3. TRANSFER TO APPROPRIATE FACILITY:
   • In-utero transfer if time allows
   • Better outcomes in hospitals with NICU capacity

4. DELAYED CORD CLAMPING:
   • 30-60 seconds if baby vigorous
   • Improves hemoglobin, reduces IVH and NEC

TERTIARY PREVENTION (Minimize Complications):

• Thermoregulation
• CPAP for respiratory support
• Early surfactant for RDS
• Caffeine for apnea
• Breast milk feeding
• Kangaroo Mother Care
• Infection prevention
• Developmental care
• ROP screening and treatment
• Comprehensive follow-up

HEALTH SYSTEM STRENGTHENING:
• Train staff in essential newborn care
• Ensure availability of antenatal steroids, CPAP, surfactant
• Implement KMC at all levels
• Establish regionalized perinatal care
• Improve referral systems
• Community education on danger signs`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT DOES PRETERM MEAN?
Your baby was born early, before 37 weeks of pregnancy. This means your baby's organs, especially the lungs, may not be fully developed. Your baby needs special care to grow and develop normally.

WHY WAS MY BABY BORN EARLY?
There are many reasons for preterm birth:
• Infection in the mother
• Problems with the placenta or uterus
• High blood pressure in pregnancy
• Multiple babies (twins, triplets)
• Sometimes the cause is unknown

WHAT PROBLEMS MIGHT MY BABY HAVE?
• Breathing difficulties (the lungs are not fully mature)
• Difficulty staying warm
• Difficulty feeding
• Risk of infections
• Risk of bleeding in the brain
• Risk of eye problems
• Slower growth and development

WHAT TREATMENTS WILL MY BABY RECEIVE?
• Incubator or warmer to stay warm
• Help with breathing (oxygen, CPAP, or a breathing machine)
• Feeding through a tube until able to suck
• Medicine if needed for various problems
• Monitoring with machines

KANGAROO MOTHER CARE (KMC):
• Holding your baby skin-to-skin on your chest
• One of the best things you can do for your baby
• Keeps baby warm, improves breathing, helps with feeding
• Reduces infections and helps brain development
• You can do this as soon as your baby is stable

BREASTFEEDING:
• Your breast milk is the best food for your baby
• Breast milk protects against infections and helps development
• Even if your baby cannot suck yet, you can express milk
• Every drop of breast milk helps

HOW YOU CAN HELP:
• Wash your hands before touching your baby
• Learn to recognize your baby's cues
• Talk and sing to your baby
• Do kangaroo care as much as possible
• Learn to feed your baby
• Ask questions - we are here to help

WHEN CAN MY BABY GO HOME?
Your baby can go home when:
• Maintaining temperature without an incubator
• Feeding well and gaining weight
• Breathing normally without apnea
• All screening tests completed
• You are confident in caring for your baby

WARNING SIGNS TO WATCH FOR AT HOME:
• Difficulty breathing (fast breathing, grunting, blue color)
• Not feeding well or vomiting
• Temperature too high or too low
• Excessive sleepiness or irritability
• Fewer wet diapers than usual

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your baby's medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your baby may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• How premature is my baby and what does this mean?
• What problems should we watch for?
• When can I hold my baby?
• How can I help with feeding?
• How long will my baby need to stay in hospital?
• What follow-up will my baby need?`,

    references: [
      'World Health Organization. WHO recommendations on interventions to improve preterm birth outcomes. Geneva: WHO; 2015.',
      'Blencowe H, Cousens S, Chou D, et al. Born Too Soon: The global epidemiology of 15 million preterm births. Reprod Health. 2013;10(Suppl 1):S2.',
      'Sweet DG, Carnielli V, Greisen G, et al. European Consensus Guidelines on the Management of Respiratory Distress Syndrome - 2022 Update. Neonatology. 2023;120(1):3-23.',
      'Roberts D, Brown J, Medley N, Dalziel SR. Antenatal corticosteroids for accelerating fetal lung maturation for women at risk of preterm birth. Cochrane Database Syst Rev. 2017;3(3):CD004454.',
      'Conde-Agudelo A, Díaz-Rossello JL. Kangaroo mother care to reduce morbidity and mortality in low birthweight infants. Cochrane Database Syst Rev. 2016;2016(8):CD002771.',
      'Patel RM, Kandefer S, Walsh MC, et al. Causes and timing of death in extremely premature infants from 2000 through 2011. N Engl J Med. 2015;372(4):331-40.',
      'Schmidt B, Roberts RS, Davis P, et al. Caffeine therapy for apnea of prematurity. N Engl J Med. 2006;354(20):2112-21.',
      'Stoll BJ, Hansen NI, Bell EF, et al. Trends in Care Practices, Morbidity, and Mortality of Extremely Preterm Neonates, 1993-2012. JAMA. 2015;314(10):1039-51.',
      'Lawn JE, Davidge R, Paul VK, et al. Born Too Soon: Care for the preterm baby. Reprod Health. 2013;10(Suppl 1):S5.',
      'American Academy of Pediatrics Committee on Fetus and Newborn. Respiratory support in preterm infants at birth. Pediatrics. 2014;133(1):171-4.',
      'Fierson WM; American Academy of Pediatrics Section on Ophthalmology. Screening examination of premature infants for retinopathy of prematurity. Pediatrics. 2018;142(6):e20183061.',
      'Santhakumaran S, Statnikov Y, Gray D, et al. Survival of very preterm infants admitted to neonatal care in England 2008-2014. Arch Dis Child Fetal Neonatal Ed. 2018;103(3):F208-F215.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 43. PEDIATRIC PNEUMONIA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '43',
    name: 'Pediatric Pneumonia',
    slug: 'pediatric-pneumonia',
    specialty: 'Pediatrics',
    bodySystem: 'Respiratory',
    icdCode: 'J18.9',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Pneumonia is an acute infection of the lung parenchyma causing inflammation and consolidation. It remains the single largest infectious cause of death in children worldwide, killing approximately 700,000 children under 5 years annually—more than AIDS, malaria, and measles combined. Most deaths occur in sub-Saharan Africa and South Asia.

The WHO Integrated Management of Childhood Illness (IMCI) approach classifies pneumonia by severity based on clinical signs, enabling appropriate treatment even without chest X-ray or laboratory confirmation. This syndromic approach has been instrumental in reducing pneumonia mortality globally.

Common bacterial pathogens include Streptococcus pneumoniae (pneumococcus), Haemophilus influenzae type b (Hib), and Staphylococcus aureus. Viral causes (RSV, influenza, parainfluenza, adenovirus) are common, especially in younger children. In Africa, consider tuberculosis, PCP (in HIV), and atypical organisms.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 DANGER SIGNS (VERY SEVERE PNEUMONIA): Unable to drink/breastfeed, persistent vomiting, convulsions, lethargic/unconscious, stridor at rest, severe malnutrition → ADMIT, IV antibiotics, oxygen

🔴 COUNT RESPIRATORY RATE ACCURATELY: Fast breathing is KEY diagnostic sign
   • <2 months: ≥60 breaths/min
   • 2-11 months: ≥50 breaths/min
   • 1-5 years: ≥40 breaths/min

🔴 CHEST INDRAWING = SEVERE PNEUMONIA → Requires hospital admission and parenteral antibiotics

🔴 OXYGEN: Give if SpO2 <90% (or cyanosis, severe respiratory distress); target SpO2 ≥90%

🔴 AMOXICILLIN is first-line for non-severe pneumonia; high-dose (80-90 mg/kg/day) recommended

🔴 THINK TB in children with: persistent cough >2 weeks, poor response to antibiotics, failure to thrive, TB contact, HIV

🔴 HIV: All children with severe/recurrent pneumonia should be tested; consider PCP if HIV-positive

🔴 VACCINES SAVE LIVES: PCV and Hib vaccines prevent the two most common bacterial causes`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 120 million episodes of pneumonia in children <5 years annually
• 14 million progress to severe pneumonia requiring hospitalization
• ~700,000 deaths annually in children <5 years (2019 estimates)
• Leading infectious cause of death in children worldwide
• 99% of deaths occur in low- and middle-income countries
• Mortality rate: 0.1-0.2% in high-income countries; 5-15% in LMIC
• Peak incidence: 6 months to 2 years of age
• Male predominance (M:F ~1.3:1)

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa has highest pneumonia mortality rate globally
• ~500,000 pneumonia deaths annually in African children <5 years
• Case fatality rate: 5-15% (up to 30% in severe cases)
• Incidence: 0.22-0.33 episodes per child-year
• Major pathogens: S. pneumoniae (30-50%), H. influenzae (10-20%), S. aureus (5-20%)
• Viral pathogens: RSV (up to 30%), rhinovirus, influenza, parainfluenza
• Co-infections common (viral-bacterial, TB-bacterial)
• HIV increases pneumonia risk 6-fold and case fatality 4-fold
• PCP is leading cause of pneumonia death in HIV-exposed/infected infants
• Malnutrition doubles pneumonia risk and mortality
• Indoor air pollution (biomass fuels) major risk factor
• PCV coverage increasing but remains suboptimal in many countries
• Hib vaccine has dramatically reduced H. influenzae type b disease

RISK FACTORS:
• Age <2 years (especially <6 months)
• Malnutrition (especially severe acute malnutrition)
• HIV infection
• Low birth weight/prematurity
• Non-exclusive breastfeeding
• Lack of immunization (PCV, Hib, measles)
• Indoor air pollution (cooking fires, tobacco smoke)
• Crowding
• Lack of zinc supplementation
• Recent measles infection
• Vitamin A deficiency
• Underlying chronic conditions (heart disease, asthma, sickle cell)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

WHO IMCI CLASSIFICATION (Children 2 months to 5 years):

NO PNEUMONIA (Cough or Cold):
• Cough without fast breathing
• No chest indrawing
• No danger signs
→ Supportive care, no antibiotics

PNEUMONIA (Non-Severe):
• Cough AND/OR difficulty breathing
• Fast breathing for age:
  - 2-11 months: ≥50 breaths/min
  - 12 months-5 years: ≥40 breaths/min
• NO chest indrawing
• NO danger signs
→ Oral amoxicillin at home, follow-up in 2-3 days

SEVERE PNEUMONIA:
• Cough AND/OR difficulty breathing
• PLUS any of:
  - Chest indrawing (lower chest wall moves IN on inspiration)
  - SpO2 <90% or central cyanosis
→ Admit, oxygen if needed, IV/IM antibiotics

VERY SEVERE PNEUMONIA (Danger Signs):
• Any of the following:
  - Unable to drink or breastfeed
  - Persistent vomiting (vomits everything)
  - Convulsions
  - Lethargy or unconscious
  - Stridor when calm
  - Severe malnutrition
→ Emergency admission, IV antibiotics, oxygen, supportive care

YOUNG INFANTS (<2 months):
• Fast breathing ≥60/min OR
• Severe chest indrawing OR
• Fever (≥38°C) or hypothermia (<35.5°C) OR
• Danger signs (not feeding, convulsions, drowsy)
→ Treat as VERY SEVERE DISEASE; admit for IV antibiotics

CLINICAL FEATURES BY PATHOGEN:

Bacterial (S. pneumoniae, H. influenzae):
• Acute onset
• High fever
• Productive cough
• Tachypnea, chest indrawing
• Crackles, bronchial breathing
• May have pleural effusion

Viral (RSV, Influenza):
• More gradual onset
• Rhinorrhea, wheezing common
• Lower grade fever
• Diffuse crackles/wheezes
• Often affects multiple family members

Staphylococcal (S. aureus):
• Rapid progression
• Very sick child
• Pneumatoceles, empyema common
• Skin infections may be present

Mycoplasma/Chlamydia (Atypical):
• School-age children
• Gradual onset, non-productive cough
• Wheeze common
• Headache, malaise
• May have extrapulmonary features

PCP (HIV-infected):
• HIV-exposed infant 2-6 months
• Tachypnea, hypoxia out of proportion to findings
• Minimal auscultatory findings
• CXR: bilateral interstitial infiltrates

Tuberculosis:
• Chronic cough >2 weeks
• Failure to thrive
• TB contact
• Persistent fever
• Poor response to standard antibiotics`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL ASSESSMENT (IMCI)
• Count respiratory rate for full 60 seconds (child calm, not crying)
• Look for chest indrawing
• Check for danger signs
• Assess hydration and nutritional status
• Check immunization status
• Ask about HIV status/exposure

STEP 2: CLASSIFY SEVERITY
• No pneumonia: Cough without fast breathing or chest indrawing
• Pneumonia: Fast breathing, no chest indrawing, no danger signs
• Severe pneumonia: Chest indrawing OR hypoxia
• Very severe pneumonia: Danger signs present

STEP 3: PHYSICAL EXAMINATION
• General: Alertness, hydration, nutrition, cyanosis
• Respiratory: Rate, effort, indrawing, nasal flaring, grunting, head bobbing
• Auscultation: Crackles, bronchial breathing, decreased breath sounds, wheeze
• Percussion: Dullness (consolidation, effusion)
• Other systems: Cardiac (murmur, gallop), abdominal (hepatomegaly)

STEP 4: INVESTIGATIONS (as available)

Essential (all severe pneumonia):
• Pulse oximetry: SpO2 <90% = hypoxemia, needs oxygen
• Blood glucose: Hypoglycemia common in severe cases

If available:
• Chest X-ray: Lobar consolidation, interstitial infiltrates, effusion, pneumatocele
• Complete blood count: Leukocytosis suggests bacterial; lymphocytosis suggests viral
• Blood culture: Low yield but may identify organism
• HIV test: All severe/recurrent pneumonia
• Malaria test: Febrile children in endemic areas

If TB suspected:
• Tuberculin skin test (TST) or IGRA
• Sputum/gastric aspirate for AFB and GeneXpert
• CXR: Hilar lymphadenopathy, miliary pattern

STEP 5: IDENTIFY ETIOLOGY
• Most cases treated empirically based on clinical presentation
• Viral more likely: gradual onset, wheeze, rhinorrhea, low-grade fever
• Bacterial more likely: acute onset, high fever, lobar consolidation
• Consider TB, PCP, atypical organisms in appropriate clinical context

DIAGNOSTIC CRITERIA (WHO):
• Cough or difficulty breathing PLUS fast breathing = Pneumonia
• Clinical diagnosis; CXR NOT required to initiate treatment
• CXR useful for: severe cases, complications (effusion, pneumothorax), poor response to treatment`,

    differentialDiagnosis: [
      'Bronchiolitis - Age <2 years; RSV common; wheeze predominant; hyperinflation on CXR; supportive care',
      'Asthma exacerbation - History of recurrent wheeze; responds to bronchodilators; bilateral wheeze',
      'Tuberculosis - Chronic cough >2 weeks; TB contact; failure to thrive; hilar lymphadenopathy',
      'Malaria - Endemic areas; fever; may coexist with pneumonia; RDT/smear positive',
      'Viral upper respiratory infection - No fast breathing or chest indrawing; rhinorrhea; mild illness',
      'Pertussis (whooping cough) - Paroxysmal cough; inspiratory whoop; post-tussive vomiting; apnea in infants',
      'Foreign body aspiration - Sudden onset; unilateral wheeze/decreased air entry; history of choking',
      'Heart failure - Hepatomegaly; cardiomegaly; murmur; may have respiratory distress',
      'Severe anemia - Pallor; tachycardia; tachypnea; no focal lung findings; check Hgb',
      'Sepsis - Very sick; fever; poor perfusion; may have respiratory distress without focal findings',
      'Pneumocystis jirovecii pneumonia (PCP) - HIV-infected/exposed infant; severe hypoxia; diffuse infiltrates',
      'Empyema/Parapneumonic effusion - Persistent fever despite antibiotics; dullness; decreased breath sounds',
      'Pleural effusion - Dullness to percussion; decreased breath sounds; may be TB-related',
      'Congenital heart disease - Cyanosis; murmur; hepatomegaly; cardiomegaly on CXR'
    ],

    investigations: [
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - SpO2 <90% defines hypoxemia; determines need for oxygen; monitor during treatment',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL for severe pneumonia - Hypoglycemia common and dangerous; treat if <45 mg/dL',
        availability: 'BASIC'
      },
      {
        name: 'Respiratory Rate Count',
        indication: '🔴 ESSENTIAL - Count for full 60 seconds when child calm; defines fast breathing by age',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'Severe pneumonia, suspected complications (effusion, pneumothorax), poor response to treatment, recurrent pneumonia',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Severe pneumonia - Leukocytosis suggests bacterial; anemia common; thrombocytosis or thrombocytopenia',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: 'Severe pneumonia - Obtain before antibiotics; positive in 10-20% of bacterial pneumonia',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🔴 ALL children with severe, recurrent, or treatment-failure pneumonia - Changes management',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Rapid Test / Blood Smear',
        indication: 'Febrile children in malaria-endemic areas - Malaria and pneumonia frequently coexist',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: 'Helps differentiate bacterial vs viral; monitor response to treatment',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin / Hematocrit',
        indication: 'Assess for anemia - Common comorbidity; severe anemia causes respiratory distress',
        availability: 'BASIC'
      },
      {
        name: 'Tuberculin Skin Test (TST/Mantoux)',
        indication: 'Suspected TB - Chronic cough, TB contact, failure to thrive, poor antibiotic response',
        availability: 'BASIC'
      },
      {
        name: 'GeneXpert MTB/RIF',
        indication: 'Suspected TB - Sputum or gastric aspirate; rapid detection of TB and rifampicin resistance',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Nasopharyngeal Aspirate/Swab',
        indication: 'Viral studies (RSV, influenza) if available; guides infection control; limited treatment impact',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pleural Fluid Analysis',
        indication: 'If effusion present - Cell count, Gram stain, culture, protein, LDH, pH, ADA (if TB suspected)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Severe respiratory distress - Assess oxygenation, ventilation, acid-base status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Procalcitonin',
        indication: 'Differentiate bacterial vs viral; guide antibiotic decision; monitor treatment response',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS DANGER SIGNS: Unable to drink, vomiting everything, convulsions, lethargy, stridor at rest',
        '⚡ COUNT RESPIRATORY RATE: Full 60 seconds, child calm; classify severity',
        '⚡ CHECK OXYGEN SATURATION: Give oxygen if SpO2 <90% or severe respiratory distress',
        '⚡ CHECK BLOOD GLUCOSE: Treat hypoglycemia immediately (<45 mg/dL)',
        '⚡ ASSESS HYDRATION: Give fluids if dehydrated but AVOID overhydration',
        '⚡ START ANTIBIOTICS: Immediately for severe pneumonia; within 1-2 hours for non-severe',
        '⚡ TEST FOR HIV: All severe, recurrent, or treatment-failure pneumonia',
        '⚡ TEST FOR MALARIA: Febrile children in endemic areas'
      ],
      pharmacological: [
        {
          drug: 'Amoxicillin (Oral)',
          indication: '💊 FIRST-LINE for NON-SEVERE pneumonia - Outpatient treatment; covers S. pneumoniae, H. influenzae',
          dose: '40-50 mg/kg/day divided Q8h (standard) OR 80-90 mg/kg/day divided Q12h (high-dose for resistant S. pneumoniae)',
          route: 'PO',
          duration: '5 days (WHO recommendation); extend to 7-10 days if slow response',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Allergic reaction'],
          alternatives: ['Amoxicillin-clavulanate if beta-lactamase concern']
        },
        {
          drug: 'Ampicillin + Gentamicin (IV/IM)',
          indication: '🔴 FIRST-LINE for SEVERE pneumonia - Hospital admission; covers common bacterial pathogens',
          dose: 'Ampicillin: 50 mg/kg/dose Q6h; Gentamicin: 7.5 mg/kg once daily',
          route: 'IV or IM',
          duration: 'Until clinical improvement (usually 3-5 days), then switch to oral amoxicillin to complete 7-10 days',
          contraindications: ['Penicillin allergy', 'Renal impairment (adjust gentamicin)'],
          sideEffects: ['Gentamicin: nephrotoxicity, ototoxicity; Ampicillin: rash, diarrhea'],
          alternatives: ['Benzylpenicillin + Gentamicin; Ceftriaxone if available']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 SEVERE pneumonia - Alternative first-line; broad spectrum; once-daily dosing',
          dose: '50-100 mg/kg/day IV/IM once daily (max 4g/day)',
          route: 'IV or IM',
          duration: '5-7 days; switch to oral when improved',
          contraindications: ['Cephalosporin allergy', 'Neonates with jaundice (bilirubin displacement)', 'Co-administration with calcium-containing IV fluids in neonates'],
          sideEffects: ['Diarrhea', 'Biliary sludging', 'Rash', 'C. difficile colitis'],
          alternatives: ['Ampicillin + Gentamicin; Cefotaxime in neonates']
        },
        {
          drug: 'Cloxacillin or Flucloxacillin',
          indication: '🦠 ADD for suspected Staphylococcal pneumonia - Pneumatoceles, empyema, rapid deterioration, skin infection',
          dose: 'Cloxacillin: 50 mg/kg/dose Q6h IV',
          route: 'IV',
          duration: '2-4 weeks for staphylococcal pneumonia; longer if empyema',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Rash', 'GI upset', 'Hepatotoxicity (rare)'],
          alternatives: ['Vancomycin if MRSA suspected']
        },
        {
          drug: 'Vancomycin',
          indication: '🦠 MRSA pneumonia or penicillin-allergic with severe pneumonia',
          dose: '15 mg/kg/dose IV Q6-8h (target trough 10-15 mcg/mL)',
          route: 'IV',
          duration: 'Typically 2-3 weeks for staphylococcal pneumonia',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Nephrotoxicity', 'Red man syndrome', 'Ototoxicity'],
          alternatives: ['Clindamycin; Linezolid']
        },
        {
          drug: 'Azithromycin or Erythromycin',
          indication: '🦠 ATYPICAL pneumonia (Mycoplasma, Chlamydia) - School-age children; persistent cough; poor response to beta-lactams',
          dose: 'Azithromycin: 10 mg/kg Day 1, then 5 mg/kg Days 2-5; Erythromycin: 10 mg/kg/dose Q6h x 10 days',
          route: 'PO (IV azithromycin if unable to take oral)',
          duration: 'Azithromycin: 5 days; Erythromycin: 10-14 days',
          contraindications: ['QT prolongation', 'Macrolide allergy'],
          sideEffects: ['GI upset', 'QT prolongation (rare)', 'Cholestatic jaundice (erythromycin)'],
          alternatives: ['Doxycycline (children ≥8 years)']
        },
        {
          drug: 'Cotrimoxazole (TMP-SMX)',
          indication: '🔴 PCP treatment (HIV-infected/exposed) - High-dose for treatment; also for PCP prophylaxis',
          dose: 'Treatment: TMP 15-20 mg/kg/day divided Q6-8h; Prophylaxis: TMP 5 mg/kg/day once daily',
          route: 'PO or IV',
          duration: 'PCP treatment: 21 days; Prophylaxis: until immune reconstitution',
          contraindications: ['Sulfa allergy', 'G6PD deficiency (relative)', 'Severe renal/hepatic impairment'],
          sideEffects: ['Rash', 'GI upset', 'Bone marrow suppression', 'Hyperkalemia', 'Stevens-Johnson syndrome (rare)'],
          alternatives: ['Pentamidine; Atovaquone for PCP']
        },
        {
          drug: 'Prednisolone / Dexamethasone',
          indication: '💨 ADJUNCT for severe PCP - Improves outcomes in moderate-severe PCP (PaO2 <70 mmHg)',
          dose: 'Prednisolone: 1-2 mg/kg/day divided BID x 5 days, then taper; OR Dexamethasone equivalent',
          route: 'PO or IV',
          duration: '21 days total with taper',
          contraindications: ['Active untreated infection (other than PCP)', 'Use with caution in TB co-infection'],
          sideEffects: ['Hyperglycemia', 'Immunosuppression', 'GI upset', 'Mood changes'],
          alternatives: ['Methylprednisolone IV']
        },
        {
          drug: 'Oxygen',
          indication: '🫁 HYPOXEMIA - SpO2 <90% or central cyanosis or severe respiratory distress',
          dose: 'Start 1-2 L/min by nasal prongs; titrate to maintain SpO2 ≥90%',
          route: 'Nasal prongs (preferred), face mask, or head box',
          duration: 'Until SpO2 maintained ≥90% in room air',
          contraindications: ['None in acute hypoxemia'],
          sideEffects: ['Nasal irritation/dryness', 'Oxygen toxicity (prolonged high FiO2 - rare in pneumonia)'],
          alternatives: ['CPAP/BiPAP if severe; mechanical ventilation if respiratory failure']
        },
        {
          drug: 'Salbutamol (Albuterol)',
          indication: '💨 WHEEZE associated with pneumonia - Bronchodilator for bronchospasm',
          dose: 'Nebulized: 2.5-5 mg Q4-6h PRN; MDI: 2-4 puffs Q4-6h PRN (via spacer)',
          route: 'Inhaled (nebulizer or MDI with spacer)',
          duration: 'Until wheeze resolved',
          contraindications: ['Use with caution in tachyarrhythmia'],
          sideEffects: ['Tachycardia', 'Tremor', 'Hypokalemia'],
          alternatives: ['Epinephrine nebulizer if bronchiolitis/croup overlap']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🌡️ FEVER and discomfort - Symptomatic relief; does not affect outcome',
          dose: '15 mg/kg/dose Q4-6h PRN (max 4 doses/day)',
          route: 'PO or PR',
          duration: 'As needed for fever/discomfort',
          contraindications: ['Hepatic impairment', 'Known allergy'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['Ibuprofen (avoid if dehydrated)']
        },
        {
          drug: 'Zinc Sulfate',
          indication: '🔬 ADJUNCT therapy - Reduces duration and severity; WHO recommendation',
          dose: '<6 months: 10 mg/day; ≥6 months: 20 mg/day',
          route: 'PO',
          duration: '10-14 days',
          contraindications: ['None significant'],
          sideEffects: ['Vomiting (if given on empty stomach)'],
          alternatives: ['None; important adjunct in LMIC']
        }
      ],
      nonPharmacological: [
        'OXYGEN THERAPY: Give if SpO2 <90%; use nasal prongs (low flow 0.5-2 L/min); humidified if available; wean as tolerated',
        'POSITIONING: Elevate head of bed; prone positioning may help; frequent position changes',
        'AIRWAY CLEARANCE: Gentle suctioning if secretions; avoid excessive suctioning; physiotherapy if needed',
        'NUTRITIONAL SUPPORT: Continue breastfeeding; small frequent feeds if dyspneic; NG feeding if unable to feed safely',
        'FLUID MANAGEMENT: Avoid fluid overload (increases respiratory distress); maintenance fluids; treat dehydration carefully',
        'FEVER MANAGEMENT: Tepid sponging; antipyretics; avoid over-bundling',
        'MONITORING: Respiratory rate, SpO2, work of breathing, level of consciousness; vital signs Q4h or more frequently',
        'INFECTION CONTROL: Hand hygiene; respiratory isolation for TB/measles; cohort patients with similar infections',
        'PARENT INVOLVEMENT: Educate on danger signs; involve in care; skin-to-skin for stable infants',
        'AVOID HARMFUL PRACTICES: No unnecessary IV fluids; no antitussives/expectorants; no routine steroids (except PCP)'
      ],
      surgical: [
        'CHEST TUBE DRAINAGE: For significant parapneumonic effusion or empyema; closed underwater seal drainage',
        'VIDEO-ASSISTED THORACOSCOPIC SURGERY (VATS): For loculated empyema; debridement and decortication',
        'OPEN THORACOTOMY: Rarely needed; for complex empyema, bronchopleural fistula, lung abscess',
        'THORACENTESIS: Diagnostic tap for pleural effusion; therapeutic drainage if large'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Any danger sign (unable to drink, convulsions, lethargy, stridor at rest)',
        '🔴 IMMEDIATE REFERRAL: SpO2 <90% and oxygen not available',
        '🔴 IMMEDIATE REFERRAL: Severe respiratory distress not improving with initial treatment',
        '🔴 IMMEDIATE REFERRAL: Suspected complications (empyema, pneumothorax, sepsis)',
        '🔴 IMMEDIATE REFERRAL: Young infant <2 months with any pneumonia',
        '🔴 IMMEDIATE REFERRAL: Severe malnutrition with pneumonia',
        '🟠 URGENT REFERRAL: No improvement after 48 hours of appropriate antibiotics',
        '🟠 URGENT REFERRAL: Worsening despite treatment',
        '🟠 URGENT REFERRAL: Suspected TB pneumonia',
        '🟠 URGENT REFERRAL: HIV-positive child with pneumonia (may need PCP treatment)',
        '🟠 URGENT REFERRAL: Recurrent pneumonia (≥2 episodes in 1 year)',
        '🟡 ROUTINE REFERRAL: HIV testing for all severe/recurrent pneumonia survivors'
      ],
      monitoring: [
        'RESPIRATORY RATE: Count Q4-6h; decreasing RR indicates improvement',
        'OXYGEN SATURATION: Continuous or Q4h; target SpO2 ≥90%; wean oxygen as able',
        'WORK OF BREATHING: Chest indrawing, nasal flaring, grunting should improve',
        'LEVEL OF CONSCIOUSNESS: Alert and interactive = improving; lethargy = concern',
        'FEEDING: Able to feed = good sign; poor feeding = ongoing severe illness or fatigue',
        'FEVER: Should decrease within 48-72 hours; persistent fever suggests complication or wrong treatment',
        'HYDRATION: Urine output, skin turgor; avoid dehydration and overhydration',
        'CLINICAL REASSESSMENT: Re-examine Q12-24h; look for complications',
        'CHEST X-RAY: Repeat only if not improving or suspected complication (not routine)',
        'LABS: Repeat CBC, CRP if poor response to assess for bacterial vs viral, complication'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

OUTPATIENT (NON-SEVERE) PNEUMONIA:
• Reassess in 2-3 days
• Expected improvement: less fever, less cough, better feeding, less fast breathing
• If worse or not improving: admit for parenteral antibiotics
• Complete antibiotic course (5 days)

AFTER HOSPITAL DISCHARGE:
• Follow-up within 1 week
• Ensure completed antibiotic course
• Counsel on danger signs for recurrence
• Verify vaccination status (PCV, Hib, measles, influenza)
• HIV test result follow-up if pending

RECURRENT PNEUMONIA (≥2 episodes in 1 year):
• Investigate for underlying cause:
  - HIV infection
  - TB
  - Malnutrition
  - Aspiration (GERD, neurological impairment)
  - Immunodeficiency
  - Congenital heart disease
  - Cystic fibrosis (rare in Africa)
  - Asthma
  - Foreign body

PREVENTION COUNSELING:
• Exclusive breastfeeding for 6 months
• Complete immunizations (PCV, Hib, measles, influenza)
• Adequate nutrition
• Reduce indoor air pollution (improved stoves, no smoking)
• Handwashing
• Prompt care-seeking for respiratory illness
• Zinc supplementation

LONG-TERM OUTCOMES:
• Most children recover completely
• Chronic lung disease uncommon with appropriate treatment
• Adenovirus pneumonia may cause bronchiectasis
• TB pneumonia requires prolonged treatment and follow-up`
    },

    redFlags: [
      '🔴 CRITICAL: Unable to drink or breastfeed - Danger sign; very severe disease; admit immediately',
      '🔴 CRITICAL: Convulsions - Danger sign; may indicate hypoxia, hypoglycemia, meningitis, cerebral malaria',
      '🔴 CRITICAL: Lethargy or unconscious - Danger sign; very severe disease; urgent intervention',
      '🔴 CRITICAL: Stridor when calm - Upper airway obstruction; danger sign',
      '🔴 CRITICAL: Severe malnutrition - High mortality risk; specialized nutritional management needed',
      '🔴 CRITICAL: Central cyanosis - Severe hypoxemia; immediate oxygen; may need advanced support',
      '🔴 CRITICAL: SpO2 <90% - Hypoxemia; give oxygen immediately; continuous monitoring',
      '🟠 URGENT: Chest indrawing - Severe pneumonia; requires admission and parenteral antibiotics',
      '🟠 URGENT: Grunting - Sign of severe respiratory distress; trying to maintain lung expansion',
      '🟠 URGENT: Head bobbing - Severe work of breathing in infants',
      '🟠 URGENT: No improvement after 48-72 hours - Consider treatment failure, complication, wrong diagnosis',
      '🟠 URGENT: Persistent high fever despite antibiotics - Possible empyema, abscess, drug-resistant organism, TB',
      '🟡 CAUTION: Very fast breathing (RR >70 in infant, >60 in child) - Indicates severe disease even without other findings'
    ],

    complications: [
      'PARAPNEUMONIC EFFUSION: Fluid in pleural space; may need drainage; common with bacterial pneumonia',
      'EMPYEMA: Pus in pleural space; requires chest tube drainage ± surgery; prolonged antibiotics',
      'LUNG ABSCESS: Localized necrosis; usually responds to prolonged antibiotics; may need drainage',
      'PNEUMATOCELES: Air-filled cavities; usually S. aureus; mostly resolve spontaneously',
      'PNEUMOTHORAX: Air in pleural space; may need chest tube; can be tension pneumothorax',
      'SEPSIS/SEPTIC SHOCK: Systemic infection; high mortality; requires intensive management',
      'RESPIRATORY FAILURE: May need CPAP or mechanical ventilation; high mortality in resource-limited settings',
      'ACUTE RESPIRATORY DISTRESS SYNDROME (ARDS): Severe lung injury; high mortality',
      'MENINGITIS: Especially with S. pneumoniae; lumbar puncture if suspected',
      'PERICARDITIS: Rare complication; may cause tamponade',
      'HEMOLYTIC UREMIC SYNDROME: Rare; associated with S. pneumoniae',
      'BRONCHIECTASIS: Chronic complication; especially after adenovirus, measles, TB',
      'POST-INFECTIOUS BRONCHIOLITIS OBLITERANS: Rare; after adenovirus; chronic lung disease',
      'DEATH: ~5-15% case fatality in severe pneumonia in LMIC; higher with malnutrition, HIV'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL SURVIVAL:
• Non-severe pneumonia (treated): >99% survival
• Severe pneumonia (hospitalized): 90-95% survival with appropriate care
• Very severe pneumonia: 70-90% survival depending on resources and comorbidities
• High-income countries: Case fatality <1%
• Low-income countries: Case fatality 5-15% overall; up to 30% in very severe

EXPECTED RECOVERY:
• Clinical improvement: Usually within 48-72 hours of appropriate antibiotics
• Fever resolution: Usually 2-4 days
• Respiratory rate normalization: Usually 3-5 days
• Full recovery: 1-2 weeks for most
• CXR resolution: May take 4-8 weeks (do not repeat routinely)

FACTORS ASSOCIATED WITH POOR PROGNOSIS:
• Age <2 months
• Severe malnutrition
• HIV infection
• Hypoxemia (SpO2 <90%)
• Danger signs present
• Delayed presentation
• Complications (empyema, sepsis)
• Drug-resistant pathogens
• Limited healthcare access

FACTORS ASSOCIATED WITH GOOD PROGNOSIS:
• Non-severe pneumonia
• Well-nourished child
• Prompt appropriate treatment
• No underlying conditions
• Good response to initial therapy

LONG-TERM OUTCOMES:
• Most children: Complete recovery with no sequelae
• Some may have: Transient airway hyperreactivity (wheeze with viral infections)
• Rarely: Bronchiectasis, chronic lung disease (after severe/recurrent episodes)
• Neurodevelopmental concerns if severe hypoxia or prolonged hospitalization`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

VACCINATION (Most Important):

1. PNEUMOCOCCAL CONJUGATE VACCINE (PCV):
   • Prevents S. pneumoniae (most common cause)
   • 3 doses: 6, 10, 14 weeks (WHO schedule) or 2+1 schedule
   • 30-50% reduction in pneumonia hospitalizations
   • Herd immunity benefits unvaccinated

2. HAEMOPHILUS INFLUENZAE TYPE B (Hib) VACCINE:
   • Prevents H. influenzae type b pneumonia and meningitis
   • 3 doses: 6, 10, 14 weeks
   • Has dramatically reduced Hib disease globally

3. MEASLES VACCINE:
   • Prevents measles pneumonia (severe, high mortality)
   • 2 doses: 9 months and 15-18 months
   • Measles increases susceptibility to other pneumonias

4. PERTUSSIS VACCINE (DTP):
   • Prevents pertussis pneumonia
   • Part of routine childhood immunization

5. INFLUENZA VACCINE:
   • Annual vaccination
   • Recommended for high-risk children (HIV, chronic illness)

NUTRITIONAL INTERVENTIONS:

• EXCLUSIVE BREASTFEEDING for 6 months: Reduces pneumonia risk by 15-23%
• Continued breastfeeding to 2 years with complementary feeding
• Adequate nutrition: Malnutrition doubles pneumonia risk
• Zinc supplementation: Reduces pneumonia incidence by 15-25%
• Vitamin A supplementation: Reduces severity and mortality

ENVIRONMENTAL INTERVENTIONS:

• REDUCE INDOOR AIR POLLUTION: Improved cookstoves, ventilation; reduces pneumonia by 30-50%
• No household smoking
• Reduce crowding
• Handwashing with soap

HIV PREVENTION AND TREATMENT:

• PMTCT: Prevent mother-to-child transmission
• Early ART: Reduces pneumonia risk in HIV-infected children
• Cotrimoxazole prophylaxis: Prevents PCP and bacterial infections

HEALTH SYSTEM:

• Early care-seeking for respiratory illness
• Training health workers in IMCI
• Ensuring antibiotic availability
• Oxygen availability at referral facilities
• Pulse oximetry for severity assessment

COMMUNITY INTERVENTIONS:

• Health education on pneumonia danger signs
• Community case management of pneumonia
• Improving care-seeking behavior
• Birth spacing and family planning`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS PNEUMONIA?
Pneumonia is an infection of the lungs that makes it hard for your child to breathe. It is a serious illness that can be dangerous if not treated properly, but most children recover well with the right treatment.

HOW DO I KNOW IF MY CHILD HAS PNEUMONIA?
Watch for these signs:
• Cough
• Fast breathing (count for a full minute)
• Difficult breathing (chest going in with each breath)
• Fever
• Not eating or drinking well
• Looking sick or more sleepy than usual

DANGER SIGNS - GO TO HOSPITAL IMMEDIATELY IF:
• Your child cannot drink or breastfeed
• Your child vomits everything
• Your child has had a convulsion (fit)
• Your child is very sleepy or difficult to wake
• Your child makes a noise (stridor) when breathing in
• Your child is severely malnourished

HOW IS PNEUMONIA TREATED?
• Antibiotics: These medicines kill the germs causing pneumonia. Give ALL the medicine even if your child feels better.
• Oxygen: Given at the hospital if your child has difficulty breathing
• Fluids and food: Keep breastfeeding or giving small frequent feeds
• Fever medicine: Paracetamol can help with fever and discomfort

CARING FOR YOUR CHILD AT HOME:
• Give the antibiotic exactly as prescribed - complete the full course
• Continue breastfeeding or give small, frequent feeds
• Give extra fluids
• Keep your child warm but not hot
• Watch for danger signs
• Return in 2-3 days or sooner if getting worse

WHEN TO COME BACK IMMEDIATELY:
• Child is getting worse
• Child cannot drink or breastfeed
• Child has fast or difficult breathing
• Child has fever that will not go away
• Child is very sleepy or hard to wake

HOW TO PREVENT PNEUMONIA:
• Vaccinate your child (pneumonia vaccines at 6, 10, 14 weeks)
• Breastfeed exclusively for 6 months
• Wash hands frequently with soap
• Keep your home smoke-free
• Use clean cooking stoves with good ventilation
• Give zinc supplements as advised
• Seek care early when your child is sick

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• How severe is my child pneumonia?
• Does my child need to stay in hospital?
• What antibiotic is being given and for how long?
• How will I know if my child is getting better or worse?
• When should I bring my child back?
• How can I prevent this from happening again?`,

    references: [
      'World Health Organization. Revised WHO classification and treatment of pneumonia in children at health facilities: evidence summaries. Geneva: WHO; 2014.',
      'World Health Organization. Pocket Book of Hospital Care for Children: Guidelines for the Management of Common Childhood Illnesses. 2nd ed. Geneva: WHO; 2013.',
      'McAllister DA, Liu L, Shi T, et al. Global, regional, and national estimates of pneumonia morbidity and mortality in children younger than 5 years between 2000 and 2015: a systematic analysis. Lancet Glob Health. 2019;7(1):e47-e57.',
      'Bradley JS, Byington CL, Shah SS, et al. The management of community-acquired pneumonia in infants and children older than 3 months of age: clinical practice guidelines by the PIDS and IDSA. Clin Infect Dis. 2011;53(7):e25-76.',
      'Ginsburg AS, Mvalo T, Nkwopara E, et al. Amoxicillin for 3 or 5 Days for Chest-Indrawing Pneumonia in Malawian Children. N Engl J Med. 2020;383(1):13-23.',
      'Nascimento-Carvalho CM. Community-acquired pneumonia among children: the latest evidence for an updated management. J Pediatr (Rio J). 2020;96 Suppl 1:29-38.',
      'Izadnegahdar R, Cohen AL, Klugman KP, Qazi SA. Childhood pneumonia in developing countries. Lancet Respir Med. 2013;1(7):574-84.',
      'UNICEF. Pneumonia: The forgotten killer of children. New York: UNICEF; 2006.',
      'Sonego M, Pellegrin MC, Becker G, Lazzerini M. Risk factors for mortality from acute lower respiratory infections (ALRI) in children under five years of age in low and middle-income countries: a systematic review and meta-analysis of observational studies. PLoS One. 2015;10(1):e0116380.',
      'Chisti MJ, Tebruegge M, La Vincente S, Graham SM, Duke T. Pneumonia in severely malnourished children in developing countries - mortality risk, aetiology and validity of WHO clinical signs: a systematic review. Trop Med Int Health. 2009;14(10):1173-89.',
      'Lanata CF, Rudan I, Boschi-Pinto C, et al. Methodological and quality issues in epidemiological studies of acute lower respiratory infections in children in developing countries. Int J Epidemiol. 2004;33(6):1362-72.',
      'Graham SM. HIV-related pulmonary disorders: practice issues. Ann Trop Paediatr. 2007;27(4):243-52.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 44. MEASLES
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '44',
    name: 'Measles',
    slug: 'measles',
    specialty: 'Pediatrics',
    bodySystem: 'Systemic',
    icdCode: 'B05.9',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Measles is a highly contagious viral disease caused by the measles virus (genus Morbillivirus, family Paramyxoviridae). It remains one of the leading causes of death among young children globally, despite the availability of a safe and effective vaccine. Before widespread vaccination, measles caused an estimated 2.6 million deaths annually. Even today, measles kills over 100,000 people each year, mostly children under 5 years in developing countries.

The virus spreads through respiratory droplets and is extraordinarily contagious—one infected person can infect 12-18 susceptible individuals (R0 = 12-18). The classic presentation includes fever, cough, coryza, conjunctivitis (the 3 Cs), followed by a characteristic maculopapular rash. Koplik spots (white spots on buccal mucosa) are pathognomonic but transient.

Complications include pneumonia (most common cause of death), encephalitis, severe diarrhea, blindness (from vitamin A deficiency and keratitis), and otitis media. Measles causes profound immunosuppression lasting months, increasing susceptibility to other infections.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 HIGHLY CONTAGIOUS: R0 = 12-18; airborne transmission; infectious from 4 days before to 4 days after rash; ISOLATE immediately

🔴 VITAMIN A for ALL CASES: Reduces mortality by 50%, prevents blindness; give 2 doses to ALL children with measles

🔴 COMPLICATIONS cause death: Pneumonia (most common cause of death), encephalitis, severe diarrhea, malnutrition

🔴 DANGER SIGNS: Inability to drink, convulsions, lethargy, stridor, severe pneumonia, severe dehydration, clouding of cornea

🔴 IMMUNOSUPPRESSION lasts 2-3 years: Increased susceptibility to other infections; immune amnesia

🔴 MALNUTRITION + MEASLES = HIGH MORTALITY: Treat malnutrition aggressively; continue feeding

🔴 VACCINE is the ONLY prevention: 2 doses achieve 97% protection; outbreak response immunization critical

🔴 NOTIFY PUBLIC HEALTH: Measles is a notifiable disease; contact tracing and vaccination of contacts essential`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Pre-vaccine era: 2.6 million deaths annually
• Current: ~140,000 deaths annually (2018), mostly children <5 years
• 95% of deaths occur in low-income countries with weak health systems
• Highly contagious: R0 = 12-18 (one of the most infectious diseases known)
• Incubation period: 10-14 days (range 7-21 days)
• Peak transmission: Late winter to early spring in temperate climates; year-round in tropics
• Herd immunity threshold: 92-95% (very high due to high R0)
• Case fatality rate: 0.1-0.2% in developed countries; 3-6% in developing countries; up to 25% in malnourished/displaced populations

AFRICAN EPIDEMIOLOGY:
• Africa accounts for >50% of global measles deaths
• Estimated 50,000-100,000 measles deaths annually in Africa
• Outbreaks common due to suboptimal vaccination coverage
• First-dose MCV coverage: 69% in Africa (2019) vs 85% globally
• Second-dose coverage even lower: <50% in many African countries
• Case fatality rate: 3-6% overall; 10-30% in malnourished children and during outbreaks
• Major outbreaks: DRC, Nigeria, Ethiopia, Madagascar recurrently affected
• Risk factors for severe disease: Malnutrition, vitamin A deficiency, HIV, young age, overcrowding
• Vitamin A deficiency highly prevalent - worsens measles outcomes
• Measles is a major contributor to child blindness in Africa
• Post-measles immune suppression contributes to high mortality from secondary infections

TRANSMISSION:
• Airborne via respiratory droplets and aerosolized particles
• Highly contagious: 90% of susceptible contacts become infected
• Infectious period: 4 days before to 4 days after rash onset
• Virus survives in air and on surfaces for up to 2 hours
• Crowded settings (camps, hospitals, schools) facilitate spread`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLINICAL PHASES:

1. INCUBATION PERIOD (10-14 days):
• Asymptomatic
• Virus replicating in respiratory tract and lymphoid tissue

2. PRODROMAL PHASE (2-4 days):
• High fever (often 40-41°C)
• The 3 Cs: Cough, Coryza (runny nose), Conjunctivitis (red, watery eyes)
• Malaise, anorexia
• KOPLIK SPOTS: Pathognomonic
  - Small white/bluish-white spots on erythematous base
  - Located on buccal mucosa opposite molars
  - Appear 2-3 days before rash, fade as rash appears
  - Present in 50-70% of cases if looked for early

3. EXANTHEM PHASE (Rash, 4-7 days):
• Maculopapular rash appears as fever peaks
• Starts behind ears and on forehead at hairline
• Spreads cephalocaudally: face → neck → trunk → extremities over 3-4 days
• Rash becomes confluent, especially on face
• Rash fades in order of appearance
• Desquamation and brownish discoloration follow
• Fever typically resolves 2-3 days after rash onset

4. RECOVERY PHASE:
• Rash fades over 5-7 days
• Cough may persist 1-2 weeks
• Profound immunosuppression persists for months

CLINICAL FEATURES BY SEVERITY:

UNCOMPLICATED MEASLES:
• Fever, cough, coryza, conjunctivitis
• Characteristic rash
• Oral intake maintained
• No danger signs

COMPLICATED MEASLES:
• Pneumonia (most common complication)
• Diarrhea (may be severe)
• Otitis media
• Laryngotracheobronchitis (croup)
• Stomatitis/oral ulcers (difficulty feeding)
• Corneal ulceration/keratitis
• Encephalitis

SEVERE MEASLES (Danger Signs):
• Unable to drink or breastfeed
• Vomits everything
• Convulsions
• Lethargic or unconscious
• Mouth ulcers preventing feeding
• Clouding of cornea
• Deep or extensive skin ulcers
• Severe pneumonia (chest indrawing, SpO2 <90%)
• Severe dehydration
• Stridor in calm child`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL DIAGNOSIS
WHO Clinical Case Definition:
• Fever AND
• Generalized maculopapular rash AND
• At least one of: Cough, Coryza, Conjunctivitis

Suspect measles in any child with:
• Fever and rash
• History of contact with measles case
• Recent travel to area with outbreak
• Unvaccinated or incompletely vaccinated

STEP 2: LOOK FOR PATHOGNOMONIC SIGNS
• KOPLIK SPOTS: Check buccal mucosa carefully
  - Best seen in good light
  - Opposite lower molars
  - White/blue-white spots on red base
  - Disappear shortly after rash appears

• CHARACTERISTIC RASH:
  - Starts at hairline/behind ears
  - Spreads downward over 3 days
  - Maculopapular, becomes confluent
  - Non-blanching in dark skin (may be harder to see)

STEP 3: ASSESS FOR COMPLICATIONS
• Eyes: Conjunctivitis, corneal clouding, ulceration
• Ears: Otitis media (ear pain, discharge)
• Mouth: Stomatitis, oral ulcers
• Respiratory: Pneumonia (fast breathing, chest indrawing)
• GI: Diarrhea, dehydration
• Neurological: Encephalitis (altered consciousness, seizures)
• Nutritional status: Malnutrition worsens prognosis

STEP 4: LABORATORY CONFIRMATION (if available)
• Measles-specific IgM antibody: Positive 3-4 days after rash onset; remains positive 1-2 months
• Measles RNA by RT-PCR: From throat swab, nasopharyngeal swab, or urine
• Serology may be negative in first 3 days of rash - repeat if initially negative and clinical suspicion high
• Four-fold rise in IgG titers (acute and convalescent sera)

Note: Laboratory confirmation important for surveillance but do NOT delay treatment waiting for results

STEP 5: NOTIFICATION
• Measles is a NOTIFIABLE DISEASE
• Report to public health authorities immediately
• Initiate contact tracing and outbreak response`,

    differentialDiagnosis: [
      'Rubella (German measles) - Milder illness; posterior auricular lymphadenopathy; rash less confluent; no Koplik spots',
      'Roseola (HHV-6) - Rash appears as fever resolves; younger children (6-24 months); no conjunctivitis',
      'Scarlet fever - Sandpaper rash; strawberry tongue; circumoral pallor; follows streptococcal pharyngitis',
      'Kawasaki disease - Prolonged fever >5 days; conjunctivitis (non-purulent); strawberry tongue; polymorphous rash; extremity changes',
      'Drug eruption - History of new medication; variable rash morphology; no prodrome',
      'Infectious mononucleosis - Pharyngitis; lymphadenopathy; splenomegaly; rash especially if given ampicillin',
      'Dengue fever - Endemic areas; retro-orbital pain; leukopenia, thrombocytopenia; rash at defervescence',
      'Enteroviral exanthem - Milder illness; summer/fall; hand-foot-mouth pattern with some viruses',
      'Meningococcemia - Petechial/purpuric rash; rapid deterioration; shock',
      'Rickettsial infections - Tick exposure; eschar; centripetal rash (starts on extremities)',
      'Secondary syphilis - Sexual history; diffuse rash including palms/soles; lymphadenopathy; mucous patches',
      'Parvovirus B19 (Fifth disease) - Slapped cheek appearance; lacy reticular rash on extremities',
      'Typhoid fever - Endemic areas; step-ladder fever; relative bradycardia; rose spots (sparse)'
    ],

    investigations: [
      {
        name: 'Clinical Diagnosis',
        indication: '🔴 ESSENTIAL - Fever + rash + one of 3 Cs (cough, coryza, conjunctivitis) is sufficient to diagnose and treat',
        availability: 'BASIC'
      },
      {
        name: 'Measles IgM Antibody (Serology)',
        indication: 'Laboratory confirmation - Positive from day 3-4 after rash; collect blood at first contact; important for surveillance',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Measles RT-PCR',
        indication: 'Gold standard for confirmation - Throat/nasopharyngeal swab or urine; detects virus in first week',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count',
        indication: 'Leukopenia common; lymphopenia; thrombocytopenia in severe cases; assess for secondary bacterial infection',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL if severe - Hypoglycemia common in malnourished children with severe illness',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL if respiratory symptoms - Detect hypoxemia; guide oxygen therapy',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If pneumonia suspected - Interstitial infiltrates; bacterial superinfection shows consolidation',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: 'If severe diarrhea or dehydration - Guide fluid and electrolyte replacement',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: 'If bacterial superinfection suspected - Sepsis, severe pneumonia, persistent fever',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture / CSF Analysis',
        indication: 'If encephalitis suspected - Altered consciousness, seizures; rule out bacterial meningitis',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: 'Hepatitis can occur; elevated transaminases; usually self-limiting',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Test',
        indication: 'All children with severe or complicated measles - HIV increases severity and complications',
        availability: 'BASIC'
      },
      {
        name: 'Ophthalmologic Examination',
        indication: '🔴 If eye involvement - Corneal clouding, ulceration; prevent blindness',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ISOLATE THE PATIENT: Airborne precautions; negative pressure room if available; cohort measles patients together',
        '⚡ VITAMIN A: Give immediately to ALL children with measles - do not wait for complications',
        '⚡ ASSESS FOR DANGER SIGNS: Unable to drink, convulsions, lethargy, cloudy cornea, severe pneumonia, severe dehydration',
        '⚡ CHECK OXYGEN SATURATION: Give oxygen if SpO2 <90% or severe respiratory distress',
        '⚡ CHECK BLOOD GLUCOSE: Treat hypoglycemia immediately',
        '⚡ ASSESS HYDRATION: Oral rehydration if able to drink; IV fluids if severe dehydration or unable to drink',
        '⚡ ASSESS NUTRITIONAL STATUS: Malnutrition greatly increases mortality; continue feeding',
        '⚡ NOTIFY PUBLIC HEALTH: Measles is notifiable; initiate contact tracing'
      ],
      pharmacological: [
        {
          drug: 'Vitamin A',
          indication: '🔴 ESSENTIAL for ALL measles cases - Reduces mortality by 50%; prevents blindness; give immediately',
          dose: 'Age-based dosing: <6 months: 50,000 IU; 6-11 months: 100,000 IU; ≥12 months: 200,000 IU. Give 2 doses (Day 1 and Day 2). Third dose at 2-4 weeks if eye signs or severe malnutrition.',
          route: 'PO',
          duration: '2 doses 24 hours apart; third dose if indicated',
          contraindications: ['None in measles - benefits far outweigh risks'],
          sideEffects: ['Transient headache, nausea (rare at these doses)', 'Bulging fontanelle in infants (transient)'],
          alternatives: ['None - vitamin A is essential']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🌡️ FEVER control - Symptomatic relief; high fever common in measles',
          dose: '15 mg/kg/dose every 4-6 hours as needed (max 4 doses/day)',
          route: 'PO or PR',
          duration: 'Until fever resolves',
          contraindications: ['Severe hepatic impairment', 'Known allergy'],
          sideEffects: ['Hepatotoxicity (overdose only)'],
          alternatives: ['Ibuprofen (avoid if dehydrated)']
        },
        {
          drug: 'Oral Rehydration Solution (ORS)',
          indication: '💧 DEHYDRATION from diarrhea or poor intake - First-line for mild-moderate dehydration',
          dose: 'Some dehydration: 75 mL/kg over 4 hours. Maintenance: Replace ongoing losses.',
          route: 'PO (or via NG tube)',
          duration: 'Until hydrated and maintaining oral intake',
          contraindications: ['Severe dehydration (needs IV)', 'Inability to drink'],
          sideEffects: ['Vomiting if given too fast'],
          alternatives: ['IV fluids if unable to tolerate oral']
        },
        {
          drug: 'Intravenous Fluids (Ringers Lactate or Normal Saline)',
          indication: '💧 SEVERE DEHYDRATION or unable to drink - Rapid rehydration',
          dose: 'Severe dehydration: 30 mL/kg in 30 min (repeat if needed), then 70 mL/kg over 2.5-5 hours',
          route: 'IV',
          duration: 'Until able to tolerate oral fluids',
          contraindications: ['Fluid overload', 'Pulmonary edema'],
          sideEffects: ['Fluid overload if excessive'],
          alternatives: ['Intraosseous if IV access not possible']
        },
        {
          drug: 'Amoxicillin',
          indication: '🦠 BACTERIAL SUPERINFECTION - Pneumonia, otitis media in non-severe cases',
          dose: '40-50 mg/kg/day divided every 8 hours',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Allergic reaction'],
          alternatives: ['Amoxicillin-clavulanate; Azithromycin if penicillin-allergic']
        },
        {
          drug: 'Ampicillin + Gentamicin',
          indication: '🔴 SEVERE PNEUMONIA or sepsis - Parenteral antibiotics for serious bacterial complications',
          dose: 'Ampicillin: 50 mg/kg/dose IV Q6h; Gentamicin: 7.5 mg/kg IV once daily',
          route: 'IV',
          duration: 'Until clinical improvement, then switch to oral to complete 7-10 days',
          contraindications: ['Penicillin allergy (ampicillin)', 'Renal impairment (adjust gentamicin)'],
          sideEffects: ['Gentamicin: nephrotoxicity, ototoxicity'],
          alternatives: ['Ceftriaxone']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 SEVERE PNEUMONIA or suspected meningitis - Broad-spectrum coverage',
          dose: '50-100 mg/kg/day IV once daily (max 4g/day); Meningitis: 100 mg/kg/day',
          route: 'IV or IM',
          duration: 'Pneumonia: 7-10 days; Meningitis: 10-14 days',
          contraindications: ['Cephalosporin allergy', 'Neonates with hyperbilirubinemia'],
          sideEffects: ['Diarrhea', 'Biliary sludging', 'C. difficile colitis'],
          alternatives: ['Ampicillin + Gentamicin']
        },
        {
          drug: 'Oxygen',
          indication: '🫁 HYPOXEMIA - SpO2 <90% or respiratory distress',
          dose: 'Start 1-2 L/min by nasal prongs; titrate to SpO2 ≥90%',
          route: 'Nasal prongs, face mask, or head box',
          duration: 'Until SpO2 maintained ≥90% in room air',
          contraindications: ['None in acute hypoxemia'],
          sideEffects: ['Nasal dryness'],
          alternatives: ['CPAP if severe; mechanical ventilation if respiratory failure']
        },
        {
          drug: 'Tetracycline Eye Ointment 1%',
          indication: '👁️ EYE INVOLVEMENT - Conjunctivitis, prevent secondary bacterial infection, corneal protection',
          dose: 'Apply to both eyes 3-4 times daily',
          route: 'Topical (ophthalmic)',
          duration: 'Until eye inflammation resolved (usually 5-7 days)',
          contraindications: ['Known allergy'],
          sideEffects: ['Transient stinging', 'Blurred vision'],
          alternatives: ['Chloramphenicol eye ointment; Gentamicin eye drops']
        },
        {
          drug: 'Gentian Violet or Nystatin',
          indication: '👄 ORAL THRUSH/STOMATITIS - Common secondary infection; painful, impairs feeding',
          dose: 'Gentian violet 0.5%: Apply to lesions BD; Nystatin: 100,000 units QID',
          route: 'Topical (oral)',
          duration: '7 days or until resolved',
          contraindications: ['None significant'],
          sideEffects: ['Gentian violet stains tissues purple'],
          alternatives: ['Oral fluconazole for severe cases']
        },
        {
          drug: 'Zinc Sulfate',
          indication: '🔬 DIARRHEA - Reduces duration and severity; WHO recommended',
          dose: '<6 months: 10 mg/day; ≥6 months: 20 mg/day',
          route: 'PO',
          duration: '10-14 days',
          contraindications: ['None significant'],
          sideEffects: ['Vomiting if given on empty stomach'],
          alternatives: ['None']
        },
        {
          drug: 'Phenobarbital or Diazepam',
          indication: '🧠 SEIZURES - Encephalitis complication; status epilepticus management',
          dose: 'Diazepam: 0.1-0.3 mg/kg IV (max 10 mg) or 0.5 mg/kg rectal; Phenobarbital: 15-20 mg/kg IV load',
          route: 'IV or rectal',
          duration: 'Acute management; maintenance if recurrent',
          contraindications: ['Respiratory depression (use with caution)'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Lorazepam; Phenytoin for refractory seizures']
        }
      ],
      nonPharmacological: [
        'ISOLATION: Airborne precautions; single room or cohort with other measles patients; limit visitors; staff should be immune',
        'NUTRITION: Continue breastfeeding; small frequent feeds; high-calorie, high-protein diet; do NOT stop feeding',
        'FEVER MANAGEMENT: Tepid sponging; light clothing; adequate fluids; avoid over-bundling',
        'EYE CARE: Clean eyes with clean water; avoid bright light exposure; vitamin A prevents corneal damage',
        'MOUTH CARE: Clean mouth with salt water; soft foods if stomatitis; treat oral thrush',
        'SKIN CARE: Keep clean and dry; no need for special skin treatment; rash resolves spontaneously',
        'SUPPORTIVE CARE: Rest; quiet environment; adequate hydration',
        'MONITORING: Watch for complications; reassess frequently; educate caregiver on danger signs',
        'INFECTION CONTROL: Handwashing; respiratory hygiene; proper waste disposal; clean environment'
      ],
      surgical: [
        'TRACHEOSTOMY: Rarely needed; for severe laryngotracheobronchitis with airway obstruction',
        'CORNEAL TRANSPLANT: For severe corneal scarring causing blindness (late complication)',
        'MYRINGOTOMY: For complicated otitis media with mastoiditis (rare)',
        'CHEST TUBE: For empyema complicating bacterial pneumonia'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Any danger sign (unable to drink, convulsions, lethargy, unconscious)',
        '🔴 IMMEDIATE REFERRAL: Severe pneumonia (chest indrawing, SpO2 <90%)',
        '🔴 IMMEDIATE REFERRAL: Severe dehydration not responding to ORS',
        '🔴 IMMEDIATE REFERRAL: Clouding of cornea or corneal ulceration',
        '🔴 IMMEDIATE REFERRAL: Suspected encephalitis (altered consciousness, seizures)',
        '🔴 IMMEDIATE REFERRAL: Severe malnutrition with measles',
        '🔴 IMMEDIATE REFERRAL: Stridor in a calm child (croup)',
        '🟠 URGENT REFERRAL: No improvement after 48 hours of treatment',
        '🟠 URGENT REFERRAL: Persistent high fever beyond day 4 of rash',
        '🟠 URGENT REFERRAL: Suspected complications not manageable locally',
        '🟡 ROUTINE REFERRAL: Ophthalmology for eye complications after acute phase',
        '🟡 ROUTINE REFERRAL: Follow-up for nutritional rehabilitation if malnourished'
      ],
      monitoring: [
        'VITAL SIGNS: Temperature, respiratory rate, heart rate, SpO2 every 4-6 hours',
        'HYDRATION: Urine output, skin turgor, mucous membranes; daily weights if possible',
        'RESPIRATORY STATUS: Work of breathing, chest indrawing, breath sounds; watch for pneumonia',
        'LEVEL OF CONSCIOUSNESS: Alert and interactive = good; lethargy = concern for encephalitis',
        'FEEDING: Oral intake; ability to breastfeed or drink; watch for stomatitis',
        'EYES: Daily examination; check for corneal clouding, discharge, photophobia',
        'EARS: Pain, discharge; check tympanic membranes if otoscope available',
        'RASH PROGRESSION: Should spread downward and then fade in same order; document',
        'COMPLICATIONS: Daily assessment for pneumonia, diarrhea, encephalitis, malnutrition',
        'DANGER SIGNS: Teach caregivers; reassess if any develop'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DURING ACUTE ILLNESS (if managed as outpatient):
• Return in 2 days for reassessment
• Return immediately if any danger sign develops
• Ensure second dose of vitamin A given on Day 2
• Confirm adequate hydration and nutrition

AFTER RECOVERY:
• Third dose of vitamin A at 2-4 weeks if eye involvement or severe malnutrition
• Nutritional assessment and rehabilitation if malnourished
• Catch-up immunizations (other vaccines, not measles if just had disease)
• Monitor for late complications

POST-MEASLES IMMUNOSUPPRESSION:
• Increased susceptibility to infections for 2-3 years
• Ensure complete vaccination
• Prompt treatment of any new infections
• Nutritional support
• Parents should seek care early for any illness

EYE COMPLICATIONS:
• Ophthalmology follow-up if corneal involvement
• Long-term monitoring for visual impairment
• Refer for rehabilitation if blindness

NEUROLOGICAL COMPLICATIONS:
• Encephalitis: Monitor for residual deficits
• Developmental assessment
• Rehabilitation services if needed
• Rare: Subacute sclerosing panencephalitis (SSPE) years later - fatal

PUBLIC HEALTH FOLLOW-UP:
• Contact tracing
• Vaccination of susceptible contacts
• Outbreak investigation and response
• Community awareness`
    },

    redFlags: [
      '🔴 CRITICAL: Unable to drink or breastfeed - Danger sign; admit immediately; IV fluids, NG feeding',
      '🔴 CRITICAL: Convulsions - May indicate encephalitis; urgent management; phenobarbital/diazepam',
      '🔴 CRITICAL: Lethargy or unconscious - Danger sign; rule out encephalitis, hypoglycemia, sepsis',
      '🔴 CRITICAL: Clouding of cornea - Risk of blindness; vitamin A, eye care, ophthalmology referral',
      '🔴 CRITICAL: Severe pneumonia - Most common cause of death; chest indrawing, SpO2 <90%; oxygen, antibiotics',
      '🔴 CRITICAL: Severe dehydration - Sunken eyes, skin pinch very slow; IV fluids urgently',
      '🔴 CRITICAL: Stridor when calm - Severe laryngotracheobronchitis; may need airway management',
      '🔴 CRITICAL: Severe malnutrition - High mortality; specialized nutritional care; F75/F100 protocols',
      '🟠 URGENT: Mouth ulcers preventing feeding - Treat thrush; consider NG feeding; nutritional support',
      '🟠 URGENT: Ear discharge - Otitis media; may need antibiotics; watch for mastoiditis',
      '🟠 URGENT: Persistent fever beyond day 4 of rash - Suspect secondary bacterial infection; reassess',
      '🟠 URGENT: Worsening after initial improvement - Suspect complication; reassess thoroughly',
      '🟡 CAUTION: High fever unresponsive to antipyretics - Close monitoring; assess for complications'
    ],

    complications: [
      'PNEUMONIA: Most common cause of death (up to 60% of deaths); viral or bacterial superinfection; requires antibiotics',
      'DIARRHEA: Common; may be severe causing dehydration and malnutrition; ORS, zinc, continue feeding',
      'OTITIS MEDIA: Common; ear pain, discharge; treat with antibiotics; may cause hearing loss',
      'LARYNGOTRACHEOBRONCHITIS (CROUP): Stridor, barking cough; may cause airway obstruction',
      'ENCEPHALITIS: 1 in 1000 cases; altered consciousness, seizures; 10-15% mortality; 25% permanent sequelae',
      'KERATITIS/CORNEAL ULCERATION: Especially with vitamin A deficiency; can cause blindness; eye care essential',
      'STOMATITIS/ORAL ULCERS: Painful; impairs feeding; treat thrush; nutritional support',
      'ACUTE MALNUTRITION: Measles precipitates or worsens malnutrition; aggressive nutritional rehabilitation',
      'IMMUNE SUPPRESSION: Profound; lasts 2-3 years; increased susceptibility to other infections (immune amnesia)',
      'HEMORRHAGIC MEASLES (Black measles): Rare; severe; bleeding into skin and mucous membranes; high mortality',
      'MYOCARDITIS: Rare; ECG changes; usually self-limiting',
      'HEPATITIS: Elevated transaminases; usually subclinical',
      'SUBACUTE SCLEROSING PANENCEPHALITIS (SSPE): Rare late complication (7-10 years later); progressive neurological deterioration; fatal',
      'DEATH: Case fatality 3-6% in developing countries; up to 25% in malnourished/displaced populations'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Uncomplicated measles: Excellent prognosis; full recovery in 2-3 weeks
• Case fatality rate varies widely by setting:
  - Developed countries: 0.1-0.2%
  - Developing countries: 3-6%
  - Malnourished children: 10-25%
  - Refugee/displaced populations: Up to 25-30%

FACTORS AFFECTING PROGNOSIS:

Better prognosis:
• Well-nourished child
• Uncomplicated disease
• Early vitamin A administration
• Prompt treatment of complications
• Good supportive care
• No underlying conditions

Worse prognosis:
• Malnutrition (especially severe acute malnutrition)
• Vitamin A deficiency
• Young age (<1 year)
• HIV infection or other immunodeficiency
• Delayed presentation
• Complications present (pneumonia, encephalitis)
• Limited access to healthcare
• Overcrowded conditions (higher viral load exposure)

COMPLICATION-SPECIFIC OUTCOMES:

Pneumonia:
• Most common cause of death
• Good response to antibiotics if bacterial
• Mortality 5-15% with treatment in LMIC

Encephalitis:
• Mortality 10-15%
• 25% have permanent neurological sequelae
• May include intellectual disability, motor deficits, seizure disorder

Eye complications:
• Leading cause of childhood blindness in Africa
• Prevented by vitamin A
• Corneal scarring may require transplantation

SSPE (Subacute Sclerosing Panencephalitis):
• Very rare (1 in 10,000-100,000 cases)
• Occurs 7-10 years after measles
• Progressive, always fatal
• Prevented by vaccination

LONG-TERM OUTCOMES:
• Most children recover completely
• Immune suppression persists for months to years
• Increased susceptibility to other infections
• Catch-up growth after nutritional rehabilitation
• Some may have chronic sequelae from complications`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

VACCINATION (Most Important):

MEASLES-CONTAINING VACCINE (MCV):
• Live attenuated vaccine
• Highly effective: 93% after 1 dose, 97% after 2 doses
• WHO recommendations:
  - First dose (MCV1): 9 months in high-risk areas; 12 months elsewhere
  - Second dose (MCV2): 15-18 months (or school entry)
• Often given as MMR (measles-mumps-rubella) or MR (measles-rubella)
• Duration of immunity: Likely lifelong after 2 doses

ROUTINE IMMUNIZATION GOALS:
• ≥95% coverage with 2 doses needed for herd immunity
• Reaching unreached children is critical
• Supplementary immunization activities (SIAs) to fill gaps

OUTBREAK RESPONSE IMMUNIZATION:
• Vaccinate all susceptible individuals 6 months to 40 years
• Infants vaccinated at 6-9 months need revaccination at 9-12 months
• Ring vaccination around cases

POST-EXPOSURE PROPHYLAXIS:

VACCINE (within 72 hours of exposure):
• Can prevent or modify disease
• Preferred for healthy contacts ≥6 months

IMMUNOGLOBULIN (within 6 days of exposure):
• For high-risk contacts who cannot receive vaccine:
  - Infants <6 months
  - Pregnant women
  - Immunocompromised individuals
• Dose: 0.25 mL/kg IM (0.5 mL/kg for immunocompromised)

INFECTION CONTROL:

• ISOLATION: Airborne precautions; 4 days after rash onset for healthy; duration of illness for immunocompromised
• HEALTHCARE WORKERS: Ensure immunity (2 doses of vaccine or documented disease)
• CONTACT TRACING: Identify and vaccinate susceptible contacts
• EXCLUDE from school/childcare for 4 days after rash onset

VITAMIN A SUPPLEMENTATION:
• Routine supplementation in vitamin A deficient populations
• Reduces measles mortality and complications
• Given during immunization contacts

PUBLIC HEALTH MEASURES:
• Strong routine immunization programs
• Surveillance and rapid outbreak response
• Health education and community mobilization
• Addressing vaccine hesitancy
• Strengthening health systems`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS MEASLES?
Measles is a very contagious disease caused by a virus. It causes fever, cough, red eyes, and a rash. Measles can be serious, especially in young children and those who are malnourished.

HOW IS MEASLES SPREAD?
• Through the air when an infected person coughs or sneezes
• The virus can stay in the air for up to 2 hours
• A person with measles can spread it from 4 days before to 4 days after the rash appears
• It spreads very easily - almost everyone who is not protected will get it if exposed

WHAT ARE THE SIGNS OF MEASLES?
• High fever (often very high)
• Cough, runny nose, red watery eyes
• Small white spots inside the mouth (before the rash)
• Red blotchy rash starting on the face, then spreading down the body

DANGER SIGNS - BRING YOUR CHILD TO HOSPITAL IMMEDIATELY IF:
• Cannot drink or breastfeed
• Vomits everything
• Has a fit (convulsion)
• Is very sleepy or difficult to wake
• Has fast or difficult breathing
• Has white or cloudy eyes
• Has severe mouth sores

HOW IS MEASLES TREATED?
• VITAMIN A: Very important medicine that helps your child fight measles. Given on 2 days.
• Fever medicine: Paracetamol to bring down fever
• Fluids: Keep your child well hydrated
• Continue feeding: Do not stop breastfeeding or eating
• Eye care: Keep eyes clean
• Watch for danger signs

CARING FOR YOUR CHILD AT HOME:
• Give vitamin A as prescribed (2 doses)
• Give plenty of fluids
• Continue breastfeeding or frequent small feeds
• Keep your child comfortable (not too hot or cold)
• Clean eyes with clean water
• Keep away from other children to prevent spread
• Return to clinic in 2 days or sooner if getting worse

HOW TO PREVENT MEASLES:
• VACCINATION is the best protection
• Make sure your child gets measles vaccine at 9 months and again at 15-18 months
• If there is a measles outbreak, vaccinate children even earlier (6 months)
• Keep your child away from people with measles

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• Has my child received vitamin A?
• Does my child need to stay in hospital?
• What danger signs should I watch for?
• How can I prevent my child from spreading measles to others?
• When can my child return to school or be around other children?
• Are my other children protected against measles?`,

    references: [
      'World Health Organization. Measles vaccines: WHO position paper - April 2017. Wkly Epidemiol Rec. 2017;92(17):205-227.',
      'World Health Organization. Pocket Book of Hospital Care for Children: Guidelines for the Management of Common Childhood Illnesses. 2nd ed. Geneva: WHO; 2013.',
      'Moss WJ. Measles. Lancet. 2017;390(10111):2490-2502.',
      'Perry RT, Halsey NA. The clinical significance of measles: a review. J Infect Dis. 2004;189 Suppl 1:S4-16.',
      'Wolfson LJ, Grais RF, Luquero FJ, et al. Estimates of measles case fatality ratios: a comprehensive review of community-based studies. Int J Epidemiol. 2009;38(1):192-205.',
      'Huiming Y, Chaomin W, Meng M. Vitamin A for treating measles in children. Cochrane Database Syst Rev. 2005;(4):CD001479.',
      'Sudfeld CR, Navar AM, Halsey NA. Effectiveness of measles vaccination and vitamin A treatment. Int J Epidemiol. 2010;39 Suppl 1:i48-55.',
      'Mina MJ, Metcalf CJ, de Swart RL, et al. Long-term measles-induced immunomodulation increases overall childhood infectious disease mortality. Science. 2015;348(6235):694-9.',
      'Patel MK, Goodson JL, Alexander JP, et al. Progress Toward Regional Measles Elimination - Worldwide, 2000-2019. MMWR Morb Mortal Wkly Rep. 2020;69(45):1700-1705.',
      'Dabbagh A, Laws RL, Steulet C, et al. Progress toward regional measles elimination - worldwide, 2000-2017. MMWR Morb Mortal Wkly Rep. 2018;67(47):1323-1329.',
      'Centers for Disease Control and Prevention. Manual for the Surveillance of Vaccine-Preventable Diseases. Chapter 7: Measles. Atlanta: CDC; 2019.',
      'Strebel PM, Orenstein WA. Measles. N Engl J Med. 2019;381(4):349-357.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 45. PERTUSSIS (WHOOPING COUGH)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '45',
    name: 'Pertussis (Whooping Cough)',
    slug: 'pertussis',
    specialty: 'Pediatrics',
    bodySystem: 'Respiratory',
    icdCode: 'A37.9',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Pertussis (whooping cough) is a highly contagious respiratory infection caused by the gram-negative bacterium Bordetella pertussis. Despite widespread vaccination, pertussis remains a significant cause of morbidity and mortality worldwide, particularly in young infants who are too young to be fully vaccinated. Globally, pertussis causes an estimated 24 million cases and 160,000 deaths annually, with most deaths occurring in infants under 6 months.

The disease is characterized by paroxysmal coughing spells followed by an inspiratory "whoop," post-tussive vomiting, and a prolonged course lasting weeks to months. In young infants, the classic whoop may be absent, and the presentation may include apnea, cyanosis, and respiratory failure. Pertussis is often called the "100-day cough" due to its prolonged duration.

B. pertussis produces toxins that damage respiratory epithelium and cause intense inflammation. The toxin also impairs immune cell function. Secondary complications include pneumonia (the most common cause of death), seizures, encephalopathy, and malnutrition from prolonged illness.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 YOUNG INFANTS ARE AT HIGHEST RISK: <6 months have highest mortality; may present with APNEA without classic whoop

🔴 APNEA/CYANOSIS IN INFANTS = ADMIT IMMEDIATELY: Life-threatening; need continuous monitoring; may need resuscitation

🔴 MACROLIDES ARE TREATMENT OF CHOICE: Azithromycin, Erythromycin, or Clarithromycin; treat early to reduce transmission

🔴 ANTIBIOTICS DO NOT SHORTEN ILLNESS if given after paroxysmal phase, but REDUCE TRANSMISSION

🔴 POST-EXPOSURE PROPHYLAXIS: Give to ALL close contacts, regardless of vaccination status

🔴 ISOLATION: Droplet precautions; exclude from school/work until 5 days of antibiotics completed OR 21 days from cough onset

🔴 MOST CONTAGIOUS during catarrhal phase (before diagnosis is usually made)

🔴 VACCINATION is KEY: Primary series + boosters; maternal vaccination protects newborns`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 24 million cases and 160,000 deaths annually worldwide
• 90% of deaths occur in low- and middle-income countries
• Peak mortality in infants <6 months (especially <2 months)
• Case fatality rate: <1% in developed countries; 1-4% in developing countries
• Highly contagious: R0 = 12-17; attack rate 80-90% in susceptible household contacts
• Cyclical epidemics every 3-5 years even in vaccinated populations
• Incubation period: 7-10 days (range 5-21 days)
• Communicable period: From catarrhal phase until 3 weeks after cough onset (or 5 days after starting antibiotics)

AFRICAN EPIDEMIOLOGY:
• Pertussis significantly underreported in Africa due to limited diagnostics
• Estimated incidence: 20-40 per 100,000 population
• Case fatality rate: 1-4% overall; up to 10% in young infants in some settings
• DTP3 coverage in Africa: ~75% (2019); varies widely by country (30-95%)
• Waning immunity contributes to adolescent and adult cases
• Adolescents and adults serve as reservoirs, infecting vulnerable infants
• Co-infections common (malaria, malnutrition, HIV increase severity)
• Malnutrition increases severity and mortality
• Limited access to intensive care for severe infant cases
• Outbreaks occur periodically, often linked to vaccination gaps

AGE DISTRIBUTION:
• <6 months: Highest incidence and mortality; too young for full protection
• 6-12 months: Still at elevated risk; partial immunity
• 1-4 years: Moderate risk; immunity from vaccination may be waning
• 5-10 years: Lower risk if vaccinated; may have atypical presentation
• Adolescents/Adults: Reservoir for transmission; prolonged cough illness; rarely severe

RISK FACTORS FOR SEVERE DISEASE:
• Age <6 months (especially <2 months)
• Unvaccinated or incompletely vaccinated
• Premature birth
• Malnutrition
• Underlying respiratory conditions
• Immunodeficiency (including HIV)
• Crowded living conditions`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLINICAL PHASES:

1. CATARRHAL PHASE (1-2 weeks):
• Resembles common cold
• Rhinorrhea (runny nose)
• Mild cough
• Low-grade fever or no fever
• Sneezing
• Malaise
• MOST CONTAGIOUS during this phase
• Often not diagnosed until paroxysmal phase

2. PAROXYSMAL PHASE (2-8 weeks or longer):
• Paroxysms (bursts) of rapid, severe coughing
• Multiple coughs in single expiration
• Inspiratory WHOOP (forced inspiration against closed glottis)
• Post-tussive vomiting
• Post-tussive exhaustion
• Facial redness/cyanosis during paroxysms
• Episodes may be triggered by feeding, crying, activity
• Between paroxysms, child may appear well
• May have 10-30 paroxysms per day
• Worse at night
• No fever (fever suggests secondary infection)

3. CONVALESCENT PHASE (weeks to months):
• Gradual decrease in frequency and severity of paroxysms
• Cough may persist for weeks to months
• Paroxysms may recur with subsequent respiratory infections
• Full recovery eventually

PRESENTATION BY AGE:

YOUNG INFANTS (<6 months):
• Classic whoop often ABSENT
• Apnea (breathing pauses) - may be only manifestation
• Cyanosis during paroxysms or with apnea
• Bradycardia
• Poor feeding
• May present with respiratory arrest
• Gasping, gagging, eye bulging during paroxysms
• Highest risk of complications and death

OLDER CHILDREN:
• More likely to have classic presentation
• Paroxysmal cough with whoop
• Post-tussive vomiting
• May have subconjunctival hemorrhages from coughing

ADOLESCENTS AND ADULTS:
• Often atypical presentation
• Prolonged cough without whoop (>2 weeks)
• May be misdiagnosed as bronchitis
• Important reservoirs for transmission to infants

PHYSICAL EXAMINATION:
• May be normal between paroxysms
• During paroxysm: facial plethora, cyanosis, bulging eyes
• Subconjunctival hemorrhages
• Petechiae (face, conjunctivae)
• Lungs often clear unless pneumonia present
• Signs of dehydration or malnutrition if prolonged illness`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
Suspect pertussis in:
• Any infant with apnea, cyanosis, or paroxysmal cough
• Child with paroxysmal cough, whoop, or post-tussive vomiting
• Prolonged cough illness (>2 weeks) with paroxysms
• Household contact of confirmed case
• Unvaccinated or incompletely vaccinated child with cough

WHO CLINICAL CASE DEFINITION:
• Cough lasting ≥2 weeks WITH at least one of:
  - Paroxysms of coughing
  - Inspiratory whoop
  - Post-tussive vomiting
  - Apnea (with or without cyanosis) in infants

STEP 2: ASSESS SEVERITY
Mild: Paroxysms but feeding well, no cyanosis/apnea
Moderate: Frequent paroxysms, some cyanosis, post-tussive vomiting
Severe: Apnea, cyanosis, respiratory distress, unable to feed, dehydration

STEP 3: LABORATORY CONFIRMATION (if available)

CULTURE (Gold Standard):
• Nasopharyngeal swab or aspirate
• Best in first 2 weeks of illness
• Sensitivity decreases after antibiotics started
• Takes 3-7 days for results
• Highly specific but less sensitive

PCR (Polymerase Chain Reaction):
• Nasopharyngeal swab
• More sensitive than culture
• Rapid results (hours to 1-2 days)
• Useful later in illness
• Preferred test where available

SEROLOGY:
• Single high titer or 4-fold rise in pertussis antibodies
• Useful in later stages when culture/PCR negative
• Not standardized; interpretation varies

DIRECT FLUORESCENT ANTIBODY (DFA):
• Rapid but low sensitivity and specificity
• Not recommended as sole diagnostic test

STEP 4: SUPPORTIVE INVESTIGATIONS

Complete Blood Count:
• Marked lymphocytosis (15,000-100,000/mcL) - characteristic
• White cell count may be very high in severe cases
• Lymphocyte predominance
• In young infants, extreme leukocytosis (>30,000) associated with poor prognosis

Chest X-ray:
• May be normal
• Perihilar infiltrates ("shaggy heart" sign)
• Atelectasis
• Pneumonia (consolidation if secondary bacterial infection)

STEP 5: IDENTIFY COMPLICATIONS
• Pneumonia (most common cause of death)
• Seizures
• Encephalopathy
• Dehydration/malnutrition
• Rib fractures (from severe coughing)
• Subconjunctival hemorrhages
• Rectal prolapse`,

    differentialDiagnosis: [
      'Viral upper respiratory infection - Shorter duration; no paroxysms or whoop; self-limiting',
      'Bronchiolitis - Age <2 years; wheeze predominant; RSV common; no whoop',
      'Pneumonia - Fever, focal findings; tachypnea; consolidation on CXR',
      'Asthma exacerbation - History of recurrent wheeze; responds to bronchodilators; bilateral wheeze',
      'Croup (laryngotracheobronchitis) - Barking cough; inspiratory stridor (not whoop); hoarse voice',
      'Foreign body aspiration - Sudden onset; history of choking; unilateral findings',
      'Tuberculosis - Chronic cough >2 weeks; TB contact; failure to thrive; no paroxysms',
      'Chlamydia trachomatis pneumonia - Infants 4-12 weeks; staccato cough; conjunctivitis history; afebrile',
      'Mycoplasma pneumonia - School-age children; gradual onset; headache; bilateral findings',
      'Parapertussis - Milder illness; caused by B. parapertussis; similar but less severe',
      'Adenovirus infection - Pharyngitis; conjunctivitis; longer illness; may have pertussis-like cough',
      'Cystic fibrosis - Chronic cough; failure to thrive; steatorrhea; recurrent respiratory infections',
      'Gastroesophageal reflux - Cough worse after feeds; vomiting; arching; may trigger cough paroxysms'
    ],

    investigations: [
      {
        name: 'Clinical Diagnosis',
        indication: '🔴 ESSENTIAL - Paroxysmal cough + whoop/post-tussive vomiting/apnea sufficient to diagnose clinically and treat',
        availability: 'BASIC'
      },
      {
        name: 'Nasopharyngeal Culture',
        indication: 'Gold standard for confirmation - Best in first 2 weeks; before antibiotics; takes 3-7 days',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pertussis PCR',
        indication: 'Preferred confirmatory test - More sensitive than culture; faster results; useful later in illness',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Marked lymphocytosis (often >15,000) is characteristic; extreme WBC (>30,000) in infants = poor prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If pneumonia suspected - Perihilar infiltrates; atelectasis; consolidation suggests secondary bacterial infection',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL for all admitted patients - Detect hypoxemia; monitor during paroxysms; guide oxygen therapy',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: 'Young infants and malnourished children - Hypoglycemia risk with poor feeding',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: 'If dehydration or prolonged vomiting - Guide fluid replacement',
        availability: 'BASIC'
      },
      {
        name: 'Pertussis Serology (IgG, IgA)',
        indication: 'Later in illness when culture/PCR negative - Single high titer or 4-fold rise; useful for epidemiology',
        availability: 'ADVANCED'
      },
      {
        name: 'Blood Culture',
        indication: 'If secondary bacterial infection suspected - Fever with pertussis suggests superinfection',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Severe respiratory distress or apnea - Assess oxygenation, ventilation, acid-base status',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS FOR APNEA/CYANOSIS: Young infants with apnea need immediate resuscitation readiness and ADMISSION',
        '⚡ PULSE OXIMETRY: Check SpO2; give oxygen if <90% or cyanosis during paroxysms',
        '⚡ ASSESS HYDRATION: Post-tussive vomiting causes dehydration; assess and correct',
        '⚡ CHECK BLOOD GLUCOSE: Especially in young infants and malnourished; treat hypoglycemia',
        '⚡ START MACROLIDE ANTIBIOTIC: Begin immediately if pertussis suspected; do not wait for confirmation',
        '⚡ ISOLATE PATIENT: Droplet precautions; prevent transmission to other vulnerable patients',
        '⚡ IDENTIFY CONTACTS: Household and close contacts need prophylaxis',
        '⚡ NOTIFY PUBLIC HEALTH: Pertussis is notifiable; initiate contact tracing'
      ],
      pharmacological: [
        {
          drug: 'Azithromycin',
          indication: '🔴 FIRST-LINE ANTIBIOTIC - Treatment and post-exposure prophylaxis; preferred for infants <1 month',
          dose: 'Infants <6 months: 10 mg/kg/day once daily x 5 days. Children ≥6 months: 10 mg/kg Day 1 (max 500mg), then 5 mg/kg Days 2-5 (max 250mg). Adults: 500mg Day 1, then 250mg Days 2-5.',
          route: 'PO',
          duration: '5 days',
          contraindications: ['Known macrolide allergy', 'QT prolongation (use with caution)'],
          sideEffects: ['GI upset (nausea, diarrhea, abdominal pain)', 'QT prolongation (rare)'],
          alternatives: ['Erythromycin', 'Clarithromycin', 'TMP-SMX if macrolide-allergic']
        },
        {
          drug: 'Erythromycin',
          indication: '🔴 ALTERNATIVE MACROLIDE - Effective but more GI side effects; avoid in infants <1 month (pyloric stenosis risk)',
          dose: 'Children: 40-50 mg/kg/day divided every 6 hours (max 2g/day). Adults: 500mg every 6 hours.',
          route: 'PO',
          duration: '14 days',
          contraindications: ['Infants <1 month (pyloric stenosis risk)', 'Macrolide allergy', 'Concurrent use of drugs prolonging QT'],
          sideEffects: ['GI upset (common)', 'Pyloric stenosis in young infants', 'QT prolongation', 'Hepatotoxicity (rare)'],
          alternatives: ['Azithromycin (preferred)', 'Clarithromycin']
        },
        {
          drug: 'Clarithromycin',
          indication: '🔴 ALTERNATIVE MACROLIDE - Similar efficacy to azithromycin; twice daily dosing',
          dose: 'Children: 15 mg/kg/day divided every 12 hours (max 1g/day). Adults: 500mg every 12 hours.',
          route: 'PO',
          duration: '7 days',
          contraindications: ['Macrolide allergy', 'Severe hepatic impairment', 'QT prolongation'],
          sideEffects: ['GI upset', 'Taste disturbance', 'QT prolongation'],
          alternatives: ['Azithromycin', 'Erythromycin']
        },
        {
          drug: 'Trimethoprim-Sulfamethoxazole (TMP-SMX)',
          indication: '🦠 MACROLIDE ALLERGY - Alternative for patients who cannot take macrolides',
          dose: 'Children: TMP 8 mg/kg/day divided every 12 hours. Adults: TMP 160mg/SMX 800mg every 12 hours.',
          route: 'PO',
          duration: '14 days',
          contraindications: ['Sulfa allergy', 'Infants <2 months', 'G6PD deficiency (relative)', 'Severe renal impairment'],
          sideEffects: ['Rash', 'GI upset', 'Bone marrow suppression', 'Stevens-Johnson syndrome (rare)'],
          alternatives: ['Macrolides if tolerated']
        },
        {
          drug: 'Oxygen',
          indication: '🫁 HYPOXEMIA - SpO2 <90% or cyanosis during paroxysms',
          dose: 'Start 1-2 L/min by nasal prongs; titrate to maintain SpO2 ≥92%',
          route: 'Nasal prongs, face mask, or head box',
          duration: 'Until oxygenation stable',
          contraindications: ['None in acute hypoxemia'],
          sideEffects: ['Nasal dryness'],
          alternatives: ['High-flow nasal cannula; CPAP if severe']
        },
        {
          drug: 'Intravenous Fluids',
          indication: '💧 DEHYDRATION from post-tussive vomiting or poor feeding',
          dose: 'Maintenance fluids; correct deficits based on dehydration assessment',
          route: 'IV',
          duration: 'Until able to maintain oral intake',
          contraindications: ['Fluid overload'],
          sideEffects: ['Fluid overload if excessive'],
          alternatives: ['ORS if able to tolerate oral; NG fluids']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🌡️ FEVER control - Fever with pertussis suggests secondary bacterial infection',
          dose: '15 mg/kg/dose every 4-6 hours as needed (max 4 doses/day)',
          route: 'PO or PR',
          duration: 'As needed',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['Ibuprofen']
        },
        {
          drug: 'Ampicillin + Gentamicin or Ceftriaxone',
          indication: '🦠 SECONDARY BACTERIAL PNEUMONIA - Fever, consolidation on CXR, worsening despite pertussis treatment',
          dose: 'Ampicillin 50 mg/kg Q6h + Gentamicin 7.5 mg/kg daily; OR Ceftriaxone 50-100 mg/kg/day',
          route: 'IV',
          duration: '7-10 days based on clinical response',
          contraindications: ['Known allergies'],
          sideEffects: ['Gentamicin: nephro/ototoxicity'],
          alternatives: ['Amoxicillin-clavulanate oral if improving']
        },
        {
          drug: 'Corticosteroids',
          indication: '⚠️ CONTROVERSIAL - Limited evidence; some use in severe cases to reduce paroxysms',
          dose: 'Dexamethasone 0.3 mg/kg/day or Prednisolone 1 mg/kg/day if used',
          route: 'PO or IV',
          duration: '3-5 days if used',
          contraindications: ['Active untreated infections', 'No clear evidence of benefit'],
          sideEffects: ['Hyperglycemia', 'Immunosuppression'],
          alternatives: ['Not routinely recommended']
        },
        {
          drug: 'Salbutamol (Albuterol)',
          indication: '💨 NOT ROUTINELY RECOMMENDED - No proven benefit; may try if significant bronchospasm',
          dose: 'Nebulized 2.5-5 mg or MDI 2-4 puffs if trialed',
          route: 'Inhaled',
          duration: 'Discontinue if no benefit',
          contraindications: ['Not indicated for pertussis specifically'],
          sideEffects: ['Tachycardia', 'Tremor'],
          alternatives: ['None proven effective for pertussis cough']
        }
      ],
      nonPharmacological: [
        'HOSPITAL ADMISSION: All infants <6 months; any patient with apnea, cyanosis, respiratory distress, or poor feeding',
        'CONTINUOUS MONITORING: Cardiorespiratory monitor for infants; pulse oximetry; apnea monitor',
        'GENTLE SUCTIONING: Clear secretions gently; avoid triggering paroxysms',
        'QUIET ENVIRONMENT: Minimize stimulation that may trigger paroxysms',
        'SMALL FREQUENT FEEDS: Feed after paroxysms when child is calm; may need NG feeding if unable to feed safely',
        'POSITIONING: Keep head elevated; position to prevent aspiration if vomiting',
        'AVOID TRIGGERS: Minimize coughing triggers (smoke, dust, cold air, activity)',
        'ISOLATION: Droplet precautions; separate from other patients, especially young infants',
        'RESUSCITATION EQUIPMENT AT BEDSIDE: For infants with apnea; suction, bag-mask, oxygen ready',
        'PARENTAL EDUCATION: Teach recognition of apnea and cyanosis; when to call for help',
        'NUTRITIONAL SUPPORT: High-calorie intake to compensate for vomiting and increased metabolic demands'
      ],
      surgical: [
        'TRACHEOSTOMY: Very rarely needed; for prolonged severe respiratory failure',
        'CHEST TUBE: If pneumothorax from severe coughing (rare)'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL/ADMISSION: Any infant <6 months with suspected pertussis',
        '🔴 IMMEDIATE REFERRAL: Apnea or cyanotic episodes',
        '🔴 IMMEDIATE REFERRAL: Respiratory distress or SpO2 <90%',
        '🔴 IMMEDIATE REFERRAL: Unable to feed or severe dehydration',
        '🔴 IMMEDIATE REFERRAL: Seizures or altered consciousness (encephalopathy)',
        '🔴 IMMEDIATE REFERRAL: Suspected pneumonia',
        '🔴 IMMEDIATE REFERRAL: Severe paroxysms causing exhaustion',
        '🟠 URGENT REFERRAL: Worsening despite treatment',
        '🟠 URGENT REFERRAL: Signs of secondary bacterial infection (fever)',
        '🟠 URGENT REFERRAL: Significant weight loss or malnutrition',
        '🟡 ROUTINE REFERRAL: Confirmation testing if diagnosis uncertain',
        '🟡 ROUTINE REFERRAL: Contact tracing assistance from public health'
      ],
      monitoring: [
        'CONTINUOUS CARDIORESPIRATORY MONITORING: For all infants; detect apnea and bradycardia',
        'PULSE OXIMETRY: Continuous or during paroxysms; maintain SpO2 ≥92%',
        'PAROXYSM LOG: Frequency, duration, severity; post-tussive vomiting; cyanosis',
        'RESPIRATORY STATUS: Rate, effort, breath sounds; watch for pneumonia',
        'HYDRATION: Intake/output; daily weights; signs of dehydration',
        'FEEDING: Ability to feed; vomiting frequency; caloric intake',
        'NEUROLOGICAL: Level of consciousness; seizure activity',
        'VITAL SIGNS: Temperature (fever suggests secondary infection), HR, RR',
        'WEIGHT: Daily if admitted; watch for malnutrition from prolonged illness',
        'LABORATORY: Repeat WBC if very high initially (decreasing = improving)'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DURING ACUTE ILLNESS:
• Daily assessment if outpatient (phone or visit)
• Return immediately for apnea, cyanosis, inability to feed, breathing difficulty
• Complete full course of antibiotics

AFTER ACUTE PHASE:
• Cough may persist for weeks to months (convalescent phase)
• Paroxysms may recur with subsequent respiratory infections
• Gradual improvement expected
• Follow-up at 1-2 weeks to assess recovery

NUTRITIONAL REHABILITATION:
• Catch-up feeding after acute illness
• High-calorie diet
• Monitor weight gain
• Address any malnutrition that developed

IMMUNIZATION:
• Natural infection provides incomplete and temporary immunity
• Continue or complete vaccination schedule
• Children who had pertussis should still receive remaining doses

CONTACT MANAGEMENT:
• All household contacts need prophylaxis (same antibiotics as treatment)
• Close contacts should complete vaccination if not up to date
• Monitor contacts for symptoms for 21 days

LONG-TERM OUTCOMES:
• Most children recover completely
• Rarely: persistent cough, bronchiectasis
• Neurological sequelae rare but possible after encephalopathy

PUBLIC HEALTH:
• Report all cases
• Support outbreak investigation
• Promote vaccination in community`
    },

    redFlags: [
      '🔴 CRITICAL: Apnea (breathing stops) - Life-threatening in infants; need continuous monitoring; resuscitation readiness',
      '🔴 CRITICAL: Cyanosis during or after paroxysms - Severe hypoxemia; oxygen; admission; ICU if available',
      '🔴 CRITICAL: Respiratory failure - May need CPAP or mechanical ventilation; transfer to higher level care',
      '🔴 CRITICAL: Seizures - May indicate encephalopathy or hypoxia; anticonvulsants; urgent evaluation',
      '🔴 CRITICAL: Extreme leukocytosis (WBC >100,000) in infant - Associated with pulmonary hypertension and death',
      '🔴 CRITICAL: Unable to feed/severe dehydration - IV fluids; NG feeding may be needed',
      '🟠 URGENT: Fever with pertussis - Suggests secondary bacterial pneumonia; add antibiotics for pneumonia',
      '🟠 URGENT: Increasing frequency/severity of paroxysms - May need admission; reassess treatment',
      '🟠 URGENT: Post-tussive vomiting with weight loss - Nutritional support; may need NG feeding',
      '🟠 URGENT: Subconjunctival hemorrhage - Indicates severe paroxysms; watch for other complications',
      '🟡 CAUTION: Paroxysms persisting beyond 6 weeks - Expected but monitor for complications; consider other diagnoses'
    ],

    complications: [
      'PNEUMONIA: Most common cause of death; may be primary pertussis or secondary bacterial; treat with antibiotics',
      'APNEA: Especially in young infants; may cause hypoxic brain injury; requires monitoring and support',
      'SEIZURES: From hypoxia or encephalopathy; may need anticonvulsants',
      'ENCEPHALOPATHY: Rare; altered consciousness; may have permanent neurological sequelae',
      'PULMONARY HYPERTENSION: In severe infant cases with leukocytosis; poor prognosis',
      'SECONDARY BACTERIAL INFECTION: Fever suggests superinfection; otitis media, pneumonia',
      'MALNUTRITION/WEIGHT LOSS: From prolonged illness, vomiting, poor feeding',
      'DEHYDRATION: From post-tussive vomiting and poor oral intake',
      'RIB FRACTURES: From severe coughing; especially in older children and adults',
      'SUBCONJUNCTIVAL HEMORRHAGE: From increased venous pressure during paroxysms',
      'EPISTAXIS: Nosebleeds from coughing',
      'RECTAL PROLAPSE: From severe coughing; especially in malnourished children',
      'HERNIAS: Umbilical or inguinal; from increased abdominal pressure',
      'DEATH: 0.5-1% overall; higher in young infants (1-4%); usually from pneumonia or respiratory failure'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Most children and adults recover completely
• Prolonged course (weeks to months) but eventual resolution
• Case fatality rate: <0.5% in developed countries; 1-4% in developing countries
• Highest mortality in infants <6 months, especially <2 months

FACTORS AFFECTING PROGNOSIS:

Better prognosis:
• Age >6 months
• Fully vaccinated (milder disease if breakthrough)
• Well-nourished
• Early antibiotic treatment
• No complications
• Access to supportive care

Worse prognosis:
• Age <6 months (especially <2 months)
• Unvaccinated
• Premature infant
• Malnourished
• Complications present (pneumonia, encephalopathy)
• Extreme leukocytosis (WBC >100,000)
• Pulmonary hypertension
• Limited access to intensive care

EXPECTED COURSE:
• Catarrhal phase: 1-2 weeks
• Paroxysmal phase: 2-8 weeks (may be longer)
• Convalescent phase: weeks to months
• Total illness duration: 6-10 weeks typically; may be longer

LONG-TERM OUTCOMES:
• Complete recovery in most cases
• Immunity after infection is not complete or lifelong
• Rare: chronic cough, bronchiectasis after severe cases
• Neurological sequelae possible after encephalopathy (rare)
• Risk of severe disease with subsequent pertussis infection is lower but not zero`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

VACCINATION (Most Important):

PRIMARY SERIES:
• DTP/DTaP vaccine at 6, 10, 14 weeks (WHO EPI schedule)
• Additional dose at 15-18 months (booster)
• School-age booster at 4-6 years
• Adolescent booster (Tdap) at 11-12 years

VACCINE TYPES:
• Whole-cell pertussis (wP): Used in most developing countries; more reactogenic but effective
• Acellular pertussis (aP): Used in developed countries; fewer side effects; may have faster waning

VACCINE EFFECTIVENESS:
• 3 doses: 80-85% effective against typical disease
• Protection wanes over time (3-5 years)
• Vaccinated individuals may still get milder disease
• Vaccination reduces severe disease and death

MATERNAL IMMUNIZATION:
• Tdap during each pregnancy (27-36 weeks)
• Antibodies transferred to infant provide protection until infant vaccination
• Most effective strategy to protect newborns

COCOONING STRATEGY:
• Vaccinate all close contacts of newborns
• Parents, siblings, grandparents, caregivers
• Healthcare workers

POST-EXPOSURE PROPHYLAXIS:

WHO SHOULD RECEIVE PEP:
• All household contacts regardless of vaccination status
• Close contacts (face-to-face exposure within 3 feet, direct contact with secretions)
• High-risk contacts: infants, pregnant women, immunocompromised
• Healthcare workers with unprotected exposure

PEP REGIMEN:
• Same antibiotics and doses as treatment
• Azithromycin (preferred), Erythromycin, Clarithromycin, or TMP-SMX
• Start within 21 days of exposure (most effective within 14 days)

INFECTION CONTROL:

ISOLATION PERIOD:
• Until 5 days of appropriate antibiotics completed OR
• 21 days from cough onset if not treated

DROPLET PRECAUTIONS:
• Surgical mask for patient
• Mask for healthcare workers within 3 feet
• Hand hygiene
• Private room or cohort with other pertussis patients

EXCLUSION FROM SCHOOL/WORK:
• Until 5 days of antibiotics completed
• Or 21 days from cough onset if untreated

PUBLIC HEALTH MEASURES:
• Case reporting (notifiable disease)
• Contact tracing
• Outbreak investigation
• Community vaccination campaigns
• Health education`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS WHOOPING COUGH (PERTUSSIS)?
Whooping cough is a serious lung infection that causes severe coughing fits. It is caused by bacteria and spreads easily from person to person through coughing and sneezing. It is especially dangerous for babies.

WHY IS IT CALLED WHOOPING COUGH?
The cough comes in fits (paroxysms). After coughing many times, the child gasps for air, making a "whoop" sound. Young babies may not make this sound but may stop breathing instead.

WARNING SIGNS - BRING YOUR CHILD TO HOSPITAL IMMEDIATELY IF:
• Your baby stops breathing (even for a short time)
• Your child turns blue around the lips during coughing
• Your child cannot breathe properly
• Your child cannot drink or breastfeed
• Your child has a fit (convulsion)
• Your child is very sleepy or difficult to wake
• Your child has fever (this may mean a lung infection)

HOW IS WHOOPING COUGH TREATED?
• ANTIBIOTICS: Medicine to kill the bacteria. This helps prevent spread to others. Give all the medicine even if cough continues.
• The cough will continue for weeks even with treatment - this is normal
• Antibiotics work best when given early

CARING FOR YOUR CHILD AT HOME:
• Give the antibiotic exactly as prescribed
• Keep your child calm and quiet - activity can trigger coughing fits
• Feed small amounts frequently, after coughing fits
• Keep your child away from smoke, dust, and cold air
• Stay with your child during coughing fits
• Watch for the danger signs listed above

IMPORTANT TIPS DURING COUGHING FITS:
• Stay calm
• Sit your child up and lean them slightly forward
• Let them cough - do not try to stop the cough
• Have a bowl ready in case they vomit
• Comfort your child after the fit passes
• If your baby stops breathing, gently stimulate them

HOW TO PREVENT SPREAD:
• Your child is contagious until completing 5 days of antibiotics
• Keep your child away from babies and unvaccinated children
• All family members should take preventive antibiotics
• Cover coughs and sneezes
• Wash hands frequently
• Make sure everyone in the family is vaccinated

HOW TO PREVENT WHOOPING COUGH:
• VACCINATION is the best protection
• Make sure your child gets all doses of DTP vaccine
• Pregnant women should get vaccinated to protect newborns
• Keep vaccines up to date

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• How severe is my child whooping cough?
• Does my child need to stay in hospital?
• How long will the cough last?
• Do other family members need preventive antibiotics?
• When can my child return to school or be around other children?
• Is my child vaccination up to date?`,

    references: [
      'World Health Organization. Pertussis vaccines: WHO position paper - August 2015. Wkly Epidemiol Rec. 2015;90(35):433-458.',
      'Centers for Disease Control and Prevention. Manual for the Surveillance of Vaccine-Preventable Diseases. Chapter 10: Pertussis. Atlanta: CDC; 2019.',
      'Yeung KHT, Duclos P, Nelson EAS, Hutubessy RCW. An update of the global burden of pertussis in children younger than 5 years: a modelling study. Lancet Infect Dis. 2017;17(9):974-980.',
      'Cherry JD. Pertussis: challenges today and for the future. PLoS Pathog. 2013;9(7):e1003418.',
      'Kilgore PE, Salim AM, Zervos MJ, Schmitt HJ. Pertussis: Microbiology, Disease, Treatment, and Prevention. Clin Microbiol Rev. 2016;29(3):449-486.',
      'Tiwari T, Murphy TV, Moran J; National Immunization Program, CDC. Recommended antimicrobial agents for the treatment and postexposure prophylaxis of pertussis: 2005 CDC Guidelines. MMWR Recomm Rep. 2005;54(RR-14):1-16.',
      'Wood N, McIntyre P. Pertussis: review of epidemiology, diagnosis, management and prevention. Paediatr Respir Rev. 2008;9(3):201-11.',
      'Hozbor D, Mooi F, Flores D, et al. Pertussis epidemiology in Argentina: trends over 2004-2007. J Infect. 2009;59(4):225-31.',
      'Crowcroft NS, Pebody RG. Recent developments in pertussis. Lancet. 2006;367(9526):1926-36.',
      'Mattoo S, Cherry JD. Molecular pathogenesis, epidemiology, and clinical manifestations of respiratory infections due to Bordetella pertussis and other Bordetella subspecies. Clin Microbiol Rev. 2005;18(2):326-82.',
      'American Academy of Pediatrics. Pertussis (Whooping Cough). In: Kimberlin DW, et al., eds. Red Book: 2021-2024 Report of the Committee on Infectious Diseases. 32nd ed. Itasca, IL: AAP; 2021.',
      'Paddock CD, Sanden GN, Cherry JD, et al. Pathology and pathogenesis of fatal Bordetella pertussis infection in infants. Clin Infect Dis. 2008;47(3):328-38.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 46. ACUTE BRONCHIOLITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '46',
    name: 'Acute Bronchiolitis',
    slug: 'acute-bronchiolitis',
    specialty: 'Pediatrics',
    bodySystem: 'Respiratory',
    icdCode: 'J21.9',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Acute bronchiolitis is a lower respiratory tract infection characterized by inflammation and obstruction of the small airways (bronchioles) in infants and young children. It is the most common cause of hospitalization in infants under 12 months of age. Respiratory Syncytial Virus (RSV) is responsible for 50-80% of cases, with other viruses including rhinovirus, parainfluenza, influenza, human metapneumovirus, and adenovirus.

The disease typically affects children under 2 years, with peak incidence at 2-6 months of age. Inflammation, edema, and mucus production in the small airways cause airway obstruction, air trapping, and ventilation-perfusion mismatch. The hallmark clinical features are wheezing, crackles, increased work of breathing, and hypoxemia.

Management is primarily supportive, as there is no specific antiviral treatment and most commonly used interventions (bronchodilators, corticosteroids, antibiotics) have NOT been shown to be beneficial. The key interventions are oxygen supplementation, hydration support, and careful monitoring.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 SUPPORTIVE CARE IS THE MAINSTAY: Oxygen if hypoxemic, fluids if dehydrated, minimize handling

🔴 BRONCHODILATORS ARE NOT RECOMMENDED: No proven benefit in bronchiolitis; may cause harm; do not use routinely

🔴 CORTICOSTEROIDS ARE NOT RECOMMENDED: No benefit proven in multiple trials; do not use

🔴 ANTIBIOTICS ARE NOT INDICATED: Bronchiolitis is viral; antibiotics do not help unless bacterial superinfection

🔴 OXYGEN if SpO2 <90-92%: Supplemental oxygen is the key therapeutic intervention; target SpO2 ≥90%

🔴 APNEA RISK in young infants: Infants <6 weeks, premature, or with history of apnea need close monitoring

🔴 FEEDING DIFFICULTIES common: Small frequent feeds; NG feeding if needed; IV fluids if unable to feed

🔴 HIGH-RISK GROUPS: Premature infants, CHD, chronic lung disease, immunodeficiency, age <12 weeks`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common lower respiratory infection in infants
• Leading cause of hospitalization in infants <12 months
• Estimated 34 million cases annually worldwide in children <5 years
• 3.4 million hospitalizations annually
• 66,000-199,000 deaths annually, 99% in developing countries
• Peak age: 2-6 months
• Rare after age 2 years
• Seasonal: Winter months in temperate climates; rainy season in tropics
• RSV causes 50-80% of cases
• Attack rate: Nearly all children infected with RSV by age 2 years
• Hospitalization rate: 1-3% of all infants; 25% of high-risk infants

AFRICAN EPIDEMIOLOGY:
• Significant burden across sub-Saharan Africa
• RSV is leading viral cause of severe acute lower respiratory infection
• Estimated 1.4 million RSV hospitalizations annually in Africa
• High mortality: 1-3% of hospitalized cases in resource-limited settings
• Peak incidence during rainy season in many regions
• Often overlaps with other respiratory infections (pneumonia, pertussis)
• Underdiagnosed due to limited viral testing
• Co-infections common (bacterial, HIV, TB)
• Malnutrition increases severity and mortality
• Limited access to oxygen therapy worsens outcomes
• HIV-exposed infants at higher risk of severe disease

RISK FACTORS FOR SEVERE DISEASE:
• Age <12 weeks (especially <6 weeks)
• Prematurity (gestational age <37 weeks)
• Congenital heart disease (especially with pulmonary hypertension)
• Chronic lung disease / Bronchopulmonary dysplasia
• Immunodeficiency (including HIV)
• Neuromuscular disease
• Down syndrome
• Malnutrition
• Exposure to tobacco smoke
• Crowded living conditions
• Lack of breastfeeding
• Low socioeconomic status

CAUSATIVE AGENTS:
• RSV: 50-80% of cases (RSV-A more severe than RSV-B)
• Rhinovirus: 10-25%
• Parainfluenza virus: 5-10%
• Human metapneumovirus: 5-10%
• Influenza virus: 5%
• Adenovirus: 5% (may cause more severe disease)
• Bocavirus, coronavirus: Less common
• Co-infections: 10-30% of cases`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

TYPICAL CLINICAL COURSE:

PRODROME (Days 1-3):
• Upper respiratory symptoms
• Rhinorrhea (runny nose)
• Mild cough
• Low-grade fever or no fever
• Decreased appetite
• Irritability

ACUTE PHASE (Days 3-7):
• Worsening cough
• Tachypnea (fast breathing)
• Wheezing (expiratory)
• Crackles/rales
• Increased work of breathing:
  - Nasal flaring
  - Intercostal retractions
  - Subcostal retractions
  - Use of accessory muscles
  - Head bobbing (infants)
  - Grunting (severe)
• Hypoxemia
• Feeding difficulty
• Irritability or lethargy

PEAK ILLNESS (Days 5-7):
• Maximum respiratory distress
• Highest risk of deterioration
• May require hospitalization

RECOVERY PHASE (Days 7-14+):
• Gradual improvement
• Cough may persist 2-4 weeks
• Full recovery in most cases

CLINICAL SEVERITY ASSESSMENT:

MILD:
• Normal feeding
• Mild tachypnea
• Mild or no retractions
• SpO2 >92% in room air
• Alert, interactive

MODERATE:
• Some feeding difficulty
• Tachypnea
• Moderate retractions
• SpO2 90-92% in room air
• Irritable but consolable

SEVERE:
• Unable to feed or very poor feeding
• Marked tachypnea (RR >70/min)
• Severe retractions, grunting, nasal flaring
• SpO2 <90% in room air
• Cyanosis
• Lethargy or exhaustion
• Apnea

PHYSICAL EXAMINATION FINDINGS:
• Tachypnea: RR >50/min (infants), >40/min (older children)
• Tachycardia
• Low-grade fever (may be absent)
• Nasal flaring, grunting
• Chest retractions (intercostal, subcostal, suprasternal)
• Hyperinflation of chest
• Prolonged expiratory phase
• Widespread wheeze (expiratory > inspiratory)
• Fine crackles/rales
• Decreased air entry if severe
• Hepatomegaly (from hyperinflation pushing diaphragm down)
• Dehydration signs if poor feeding`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL DIAGNOSIS
Bronchiolitis is a CLINICAL DIAGNOSIS based on:
• Age <2 years (typically <12 months)
• First episode of wheezing
• Viral prodrome (rhinorrhea, cough, low-grade fever)
• Signs of lower respiratory tract involvement:
  - Wheezing
  - Crackles
  - Increased work of breathing
  - Hypoxemia

STEP 2: ASSESS SEVERITY
• Respiratory rate and effort
• Oxygen saturation (pulse oximetry)
• Feeding ability
• Level of alertness
• Presence of apnea
• Risk factors for severe disease

STEP 3: DETERMINE NEED FOR ADMISSION
Admit if:
• SpO2 <92% in room air (or <90% in some guidelines)
• Moderate to severe respiratory distress
• Apnea
• Unable to feed adequately
• Age <6 weeks
• High-risk conditions present
• Unreliable follow-up or parental concern

STEP 4: INVESTIGATIONS

ROUTINE INVESTIGATIONS ARE NOT NEEDED IN TYPICAL BRONCHIOLITIS

Consider if diagnosis uncertain or complications suspected:

Pulse Oximetry:
• Essential for all patients with respiratory symptoms
• Determines need for oxygen
• Target SpO2 ≥90% (some guidelines ≥92%)

Chest X-ray:
• NOT routinely indicated
• Consider if: severe disease, diagnostic uncertainty, suspected complication
• Typical findings: hyperinflation, peribronchial thickening, atelectasis
• May lead to unnecessary antibiotic use

Viral Testing (RSV, PCR panel):
• Not required for management (does not change treatment)
• Useful for: infection control (cohorting), epidemiology, excluding influenza
• RSV rapid antigen test or PCR from nasopharyngeal sample

Blood Tests:
• NOT routinely indicated
• Consider CBC, CRP if bacterial infection suspected
• Blood gas if severe respiratory distress

STEP 5: DIFFERENTIATE FROM OTHER CONDITIONS
• First episode of wheeze = more likely bronchiolitis
• Recurrent wheeze = consider asthma (typically >12 months)
• High fever, focal findings = consider pneumonia
• Poor feeding from birth, murmur = consider heart disease`,

    differentialDiagnosis: [
      'Viral-induced wheeze/Asthma - Recurrent episodes; family history of atopy; responds to bronchodilators; age typically >12 months',
      'Pneumonia (bacterial) - Focal findings; higher fever; consolidation on CXR; responds to antibiotics',
      'Pertussis - Paroxysmal cough; inspiratory whoop; post-tussive vomiting; older or unvaccinated',
      'Aspiration pneumonia - History of choking, GERD, neurological impairment; recurrent',
      'Foreign body aspiration - Sudden onset; unilateral wheeze; history of choking; older infant/toddler',
      'Congestive heart failure - Hepatomegaly; cardiomegaly; murmur; poor weight gain; edema',
      'Congenital heart disease - Murmur; cyanosis; failure to thrive; cardiomegaly',
      'Cystic fibrosis - Recurrent respiratory infections; failure to thrive; steatorrhea; family history',
      'Vascular ring/airway anomaly - Stridor; recurrent symptoms; fixed airway obstruction',
      'Gastroesophageal reflux - Vomiting; cough worse after feeds; arching; chronic symptoms',
      'Laryngomalacia - Stridor (inspiratory); worse when feeding/crying; improves when prone',
      'Myocarditis - Tachycardia out of proportion; poor perfusion; cardiomegaly'
    ],

    investigations: [
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - Determines hypoxemia and need for oxygen; target SpO2 ≥90%',
        availability: 'BASIC'
      },
      {
        name: 'Clinical Assessment',
        indication: '🔴 ESSENTIAL - Respiratory rate, work of breathing, feeding ability, alertness; no labs needed for typical cases',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'NOT ROUTINE - Only if: diagnostic uncertainty, suspected complication, severe disease, no improvement',
        availability: 'BASIC'
      },
      {
        name: 'RSV Rapid Antigen Test',
        indication: 'For infection control (cohorting) and epidemiology; does NOT change treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Respiratory Viral PCR Panel',
        indication: 'If viral etiology needed (influenza to guide oseltamivir); research; not routine',
        availability: 'ADVANCED'
      },
      {
        name: 'Complete Blood Count',
        indication: 'NOT ROUTINE - Consider if bacterial superinfection suspected (high fever, focal findings)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: 'Young infants, poor feeding, malnourished - Risk of hypoglycemia',
        availability: 'BASIC'
      },
      {
        name: 'Blood Gas (Capillary or Arterial)',
        indication: 'Severe respiratory distress - Assess ventilation and acid-base status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Culture',
        indication: 'If sepsis or bacterial pneumonia suspected - High fever, toxic appearance',
        availability: 'BASIC'
      },
      {
        name: 'Nasopharyngeal Aspirate/Swab',
        indication: 'For viral testing if indicated; proper technique important for yield',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes',
        indication: 'If significant dehydration or on IV fluids - Guide fluid management',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein',
        indication: 'NOT ROUTINE - May help differentiate bacterial vs viral; elevated in severe viral illness too',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Respiratory rate, work of breathing, SpO2, feeding, alertness',
        '⚡ PULSE OXIMETRY: Check SpO2; give oxygen if <90% (some use <92%)',
        '⚡ MINIMIZE HANDLING: Avoid unnecessary interventions that cause distress',
        '⚡ ASSESS HYDRATION: Can child feed? Signs of dehydration?',
        '⚡ CHECK FOR APNEA: Young infants (<6 weeks), premature, history of apnea - need monitoring',
        '⚡ DECIDE DISPOSITION: Admit if severe, unable to feed, apnea, hypoxemic, high-risk, or unreliable follow-up'
      ],
      pharmacological: [
        {
          drug: 'Oxygen',
          indication: '🔴 KEY INTERVENTION - SpO2 <90% (some guidelines <92%); severe respiratory distress; cyanosis',
          dose: 'Start 0.5-2 L/min via nasal prongs; titrate to SpO2 ≥90% (or ≥92%)',
          route: 'Nasal prongs (preferred), face mask, head box',
          duration: 'Until SpO2 maintained ≥90% in room air with acceptable work of breathing',
          contraindications: ['None in hypoxemia'],
          sideEffects: ['Nasal irritation'],
          alternatives: ['High-flow nasal cannula (HFNC); CPAP if available and severe']
        },
        {
          drug: 'Normal Saline Drops (Nasal)',
          indication: '👃 NASAL CONGESTION - Helps clear secretions before feeds',
          dose: '1-2 drops per nostril before feeds',
          route: 'Intranasal',
          duration: 'As needed',
          contraindications: ['None'],
          sideEffects: ['None significant'],
          alternatives: ['Gentle bulb suction after saline']
        },
        {
          drug: 'Intravenous Fluids (Maintenance)',
          indication: '💧 UNABLE TO FEED or severe respiratory distress - Prevent dehydration',
          dose: 'Maintenance fluids (e.g., D5 0.45% saline); avoid overhydration',
          route: 'IV',
          duration: 'Until able to feed safely',
          contraindications: ['Fluid overload'],
          sideEffects: ['Hyponatremia if hypotonic fluids used excessively'],
          alternatives: ['NG feeding if not in severe distress']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🌡️ FEVER or discomfort - Symptomatic relief only',
          dose: '15 mg/kg/dose every 4-6 hours as needed',
          route: 'PO or PR',
          duration: 'As needed',
          contraindications: ['Hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['Ibuprofen (if >3 months and not dehydrated)']
        },
        {
          drug: 'Hypertonic Saline (3%) Nebulized',
          indication: '🌫️ CONSIDER in hospitalized patients - May reduce length of stay; evidence mixed',
          dose: '4 mL nebulized every 4-6 hours',
          route: 'Nebulized',
          duration: 'During hospitalization; discontinue if no benefit',
          contraindications: ['Outpatients (not recommended)', 'Bronchospasm with administration'],
          sideEffects: ['Bronchospasm (rare)', 'Coughing'],
          alternatives: ['Normal saline nebulization (no proven benefit over supportive care)']
        },
        {
          drug: 'Salbutamol (Albuterol) - TRIAL ONLY',
          indication: '⚠️ NOT ROUTINELY RECOMMENDED - May trial ONCE in older infants with strong family history of atopy',
          dose: 'Nebulized 2.5 mg or MDI 2-4 puffs via spacer',
          route: 'Inhaled',
          duration: 'Single trial; discontinue if no objective improvement',
          contraindications: ['Routine use in bronchiolitis not recommended'],
          sideEffects: ['Tachycardia', 'Tremor', 'Paradoxical worsening'],
          alternatives: ['None proven effective; supportive care preferred']
        },
        {
          drug: 'Epinephrine (Adrenaline) Nebulized',
          indication: '⚠️ NOT ROUTINELY RECOMMENDED - Some evidence in ED setting; not sustained benefit',
          dose: '0.5 mL/kg of 1:1000 (max 5 mL) nebulized',
          route: 'Nebulized',
          duration: 'Single dose; may repeat once if benefit seen',
          contraindications: ['Routine use not recommended'],
          sideEffects: ['Tachycardia', 'Pallor', 'Tremor'],
          alternatives: ['Supportive care']
        },
        {
          drug: 'Corticosteroids (Prednisolone, Dexamethasone)',
          indication: '❌ NOT RECOMMENDED - Multiple trials show NO benefit in bronchiolitis',
          dose: 'Not applicable - should not be used',
          route: 'N/A',
          duration: 'N/A',
          contraindications: ['Bronchiolitis (no indication)'],
          sideEffects: ['Not applicable'],
          alternatives: ['Supportive care only']
        },
        {
          drug: 'Antibiotics',
          indication: '❌ NOT INDICATED for bronchiolitis - Viral illness; antibiotics do not help',
          dose: 'Not applicable unless bacterial superinfection documented',
          route: 'N/A',
          duration: 'N/A',
          contraindications: ['Uncomplicated bronchiolitis'],
          sideEffects: ['Not applicable'],
          alternatives: ['Reserve antibiotics for documented bacterial infection']
        },
        {
          drug: 'Oseltamivir (Tamiflu)',
          indication: '🦠 INFLUENZA co-infection or primary cause - If influenza confirmed in high-risk patient',
          dose: '<1 year: 3 mg/kg/dose BID; 1-12 years: weight-based (see dosing chart)',
          route: 'PO',
          duration: '5 days',
          contraindications: ['Known allergy'],
          sideEffects: ['Nausea', 'Vomiting'],
          alternatives: ['Supportive care if influenza not confirmed']
        },
        {
          drug: 'Palivizumab (Synagis)',
          indication: '🛡️ PREVENTION only - For high-risk infants during RSV season (not treatment)',
          dose: '15 mg/kg IM monthly during RSV season',
          route: 'IM',
          duration: 'Monthly during RSV season (5 doses typically)',
          contraindications: ['Treatment of active infection', 'Known hypersensitivity'],
          sideEffects: ['Injection site reaction', 'Fever'],
          alternatives: ['No alternative for RSV prophylaxis currently']
        }
      ],
      nonPharmacological: [
        'OXYGEN THERAPY: Key intervention; nasal prongs preferred; target SpO2 ≥90%; wean gradually',
        'HIGH-FLOW NASAL CANNULA (HFNC): If available, for moderate-severe cases not responding to standard oxygen; flow 2 L/kg/min',
        'CPAP: For severe cases with impending respiratory failure; if available',
        'MINIMAL HANDLING: Reduce interventions that cause distress; cluster care activities',
        'POSITIONING: Elevate head of bed; position of comfort',
        'NASAL SUCTIONING: Gentle bulb suction BEFORE feeds if congested; avoid deep suctioning',
        'FEEDING SUPPORT: Small frequent feeds; feed after suctioning; NG tube if too tired to feed safely',
        'HYDRATION: Oral if able; NG if mild distress but unable to feed; IV if severe distress',
        'MONITORING: Continuous pulse oximetry in admitted patients; cardiorespiratory monitor if apnea risk',
        'INFECTION CONTROL: Contact precautions; hand hygiene; cohort RSV-positive patients; limit visitors',
        'PARENTAL PRESENCE: Reduces distress; teach parents safe suctioning and feeding techniques',
        'AVOID SMOKE EXPOSURE: No smoking near child; important for recovery and prevention'
      ],
      surgical: [
        'INTUBATION AND MECHANICAL VENTILATION: For respiratory failure; apnea unresponsive to CPAP; rare',
        'CHEST TUBE: Very rare; only if pneumothorax from air trapping or positive pressure ventilation'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Apnea',
        '🔴 IMMEDIATE REFERRAL: SpO2 <90% not responding to oxygen or oxygen not available',
        '🔴 IMMEDIATE REFERRAL: Severe respiratory distress (grunting, marked retractions, exhaustion)',
        '🔴 IMMEDIATE REFERRAL: Cyanosis',
        '🔴 IMMEDIATE REFERRAL: Unable to feed',
        '🔴 IMMEDIATE REFERRAL: Lethargy or decreased responsiveness',
        '🔴 IMMEDIATE REFERRAL: Age <6 weeks',
        '🟠 URGENT REFERRAL: High-risk patient (CHD, CLD, immunodeficiency, prematurity) with bronchiolitis',
        '🟠 URGENT REFERRAL: Worsening despite supportive care',
        '🟠 URGENT REFERRAL: Need for HFNC, CPAP, or mechanical ventilation',
        '🟡 ROUTINE REFERRAL: Recurrent wheeze - evaluate for asthma after recovery'
      ],
      monitoring: [
        'OXYGEN SATURATION: Continuous pulse oximetry if admitted; intermittent if improving',
        'RESPIRATORY RATE AND EFFORT: Document every 4 hours minimum; more frequently if unstable',
        'FEEDING: Amount tolerated; ability to coordinate suck-swallow-breathe',
        'HYDRATION: Wet diapers; skin turgor; fontanelle; daily weights',
        'APNEA: Cardiorespiratory monitor for infants <6 weeks, ex-premature, or history of apnea',
        'CLINICAL STATUS: Alertness; work of breathing; color; general appearance',
        'WEANING OXYGEN: Attempt to wean when stable; tolerate SpO2 ≥90% in room air before discharge',
        'DISCHARGE READINESS: Feeding adequately; SpO2 stable in room air; no significant distress; reliable follow-up'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DISCHARGE CRITERIA:
• SpO2 ≥90% (or ≥92%) in room air for at least 4-12 hours
• Respiratory rate acceptable for age
• Feeding adequately (taking sufficient fluids orally)
• No apnea for at least 24 hours
• Parents understand warning signs and when to return
• Reliable follow-up available

POST-DISCHARGE INSTRUCTIONS:
• Cough and mild symptoms may persist 2-4 weeks
• Small frequent feeds; may need to feed more often
• Use saline drops and gentle suction before feeds if congested
• Avoid smoke exposure
• Keep away from other young children if possible
• Hand hygiene to prevent spread

RETURN IMMEDIATELY IF:
• Breathing becomes fast or difficult
• Skin between ribs sucking in with breathing
• Lips or fingernails turn blue
• Refuses to feed or feeds very little
• Fewer wet diapers than usual
• Becomes very sleepy or difficult to wake
• Has pauses in breathing

FOLLOW-UP VISIT:
• 1-2 days after discharge for high-risk patients
• 3-5 days for routine cases
• Sooner if concerns

LONG-TERM CONSIDERATIONS:
• 30-50% may have recurrent wheezing in childhood
• RSV bronchiolitis associated with later asthma (association, not necessarily causation)
• Monitor for recurrent wheeze - if recurs, consider asthma diagnosis
• Catch-up vaccinations when recovered`
    },

    redFlags: [
      '🔴 CRITICAL: Apnea - Life-threatening; continuous monitoring; may need ventilatory support',
      '🔴 CRITICAL: Cyanosis or SpO2 <90% - Severe hypoxemia; oxygen; escalate care',
      '🔴 CRITICAL: Exhaustion/lethargy - Impending respiratory failure; may need CPAP/ventilation',
      '🔴 CRITICAL: Grunting - Severe distress; trying to maintain lung recruitment; needs escalation',
      '🔴 CRITICAL: Unable to feed - Risk of dehydration and hypoglycemia; needs admission',
      '🔴 CRITICAL: Age <6 weeks - Higher risk of apnea and severe disease',
      '🟠 URGENT: Moderate retractions with feeding difficulty - Needs admission and monitoring',
      '🟠 URGENT: SpO2 90-92% in room air - Borderline; needs close monitoring or supplemental oxygen',
      '🟠 URGENT: High-risk infant (premature, CHD, CLD) - Lower threshold for admission',
      '🟠 URGENT: Dehydration - Needs fluid support (IV or NG)',
      '🟡 CAUTION: Fever with bronchiolitis - Usually low-grade; high fever may suggest bacterial co-infection',
      '🟡 CAUTION: Worsening at day 3-5 - Peak of illness; reassess if not improving'
    ],

    complications: [
      'RESPIRATORY FAILURE: May require CPAP or mechanical ventilation; usually recovers',
      'APNEA: Especially in young infants, ex-premature; may require monitoring and stimulation',
      'DEHYDRATION: From poor feeding and increased losses; IV or NG fluids needed',
      'SECONDARY BACTERIAL INFECTION: Uncommon; consider if fever persists, new deterioration',
      'ATELECTASIS: From mucus plugging; usually resolves; may be seen on CXR',
      'PNEUMOTHORAX: Rare; from air trapping; may need chest tube if large',
      'ACUTE OTITIS MEDIA: Common co-infection; treat if present',
      'PROLONGED HOSPITALIZATION: Risk factors: young age, prematurity, comorbidities',
      'DEATH: Rare (<1% hospitalized); higher in LMIC and with comorbidities',
      'RECURRENT WHEEZING: 30-50% have recurrent wheezing episodes; may develop asthma',
      'POST-BRONCHIOLITIS COUGH: May persist for weeks; reassure parents'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Excellent prognosis for most infants
• Self-limiting illness; most recover within 2-3 weeks
• Mortality <1% in developed countries; 1-3% in resource-limited settings
• Mortality higher with comorbidities and lack of oxygen availability

EXPECTED CLINICAL COURSE:
• Prodrome: 2-3 days
• Peak illness: Days 3-5 (worst respiratory distress)
• Improvement begins: Days 5-7
• Recovery: 1-2 weeks for most; cough may persist 2-4 weeks
• Return to baseline: 2-4 weeks

DURATION OF HOSPITALIZATION:
• Median: 2-3 days
• High-risk patients: May be longer (5-7 days or more)
• Factors prolonging stay: Oxygen requirement, feeding difficulty, young age, comorbidities

FACTORS AFFECTING PROGNOSIS:

Better prognosis:
• Age >3 months
• Term infant
• No underlying conditions
• Mild disease at presentation
• Good access to supportive care
• Early improvement

Worse prognosis:
• Age <6 weeks
• Prematurity
• Congenital heart disease
• Chronic lung disease
• Immunodeficiency
• Severe disease at presentation
• Limited access to oxygen and supportive care

LONG-TERM OUTCOMES:
• 30-50% have recurrent wheezing in first 5 years
• Association with later asthma (causation vs association debated)
• RSV infection in infancy may predispose to reactive airway disease
• Most children have normal lung function as adults
• Severe cases (especially adenovirus) may have chronic sequelae`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PASSIVE IMMUNIZATION:

PALIVIZUMAB (Synagis):
• Monoclonal antibody against RSV F protein
• Monthly IM injections during RSV season
• Indicated for high-risk infants:
  - Premature infants (<29 weeks GA) in first RSV season
  - Chronic lung disease of prematurity with treatment in past 6 months
  - Hemodynamically significant congenital heart disease
• Reduces RSV hospitalization by 50%
• Expensive; limited availability in LMIC

NIRSEVIMAB (Beyfortus):
• Long-acting monoclonal antibody
• Single dose protects entire RSV season
• Approved for all infants in first RSV season
• Also for high-risk children in second season
• More feasible for wider use than palivizumab

RSV VACCINES (Recently Approved):
• Maternal RSV vaccination during pregnancy
• Protects infant through transferred antibodies
• May become key prevention strategy

GENERAL PREVENTION MEASURES:

HAND HYGIENE:
• Most important measure
• Wash hands before touching infant
• Use alcohol-based hand rub

AVOID SICK CONTACTS:
• Keep infant away from people with respiratory symptoms
• Limit visitors during RSV season

BREASTFEEDING:
• Protective against severe RSV disease
• Encourage exclusive breastfeeding for 6 months

AVOID SMOKE EXPOSURE:
• No smoking in household
• No exposure to secondhand smoke

INFECTION CONTROL IN HEALTHCARE:
• Contact precautions for bronchiolitis patients
• Hand hygiene before and after patient contact
• Cohort RSV-positive patients
• Clean and disinfect equipment

REDUCE CROWDING:
• Avoid crowded places during RSV season
• Limit daycare exposure for high-risk infants

INFLUENZA VACCINATION:
• Household contacts should receive influenza vaccine
• Reduces influenza-associated bronchiolitis`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS BRONCHIOLITIS?
Bronchiolitis is an infection of the small airways in the lungs. It is caused by a virus (most commonly RSV - Respiratory Syncytial Virus). It is very common in babies, especially those under 1 year old. Most babies recover completely, but some need to be in hospital.

WHAT ARE THE SYMPTOMS?
• Runny nose and mild cough at first
• After 2-3 days: faster breathing, wheezing, difficulty feeding
• The cough may sound wet or rattly
• Your baby may be irritable or more sleepy

WHEN TO SEEK MEDICAL CARE:
• Breathing is fast or difficult
• You see the skin pulling in between the ribs when breathing
• Your baby is feeding less than half the usual amount
• Your baby has fewer wet diapers than usual
• Your baby is very sleepy or hard to wake
• Your baby looks pale or has blue lips

DANGER SIGNS - GO TO HOSPITAL IMMEDIATELY:
• Your baby stops breathing (even for a few seconds)
• Your baby turns blue
• Your baby is struggling to breathe
• Your baby will not feed at all
• Your baby is limp or unresponsive

HOW IS BRONCHIOLITIS TREATED?
• There is NO medicine that cures bronchiolitis - it is caused by a virus and antibiotics do not help
• Treatment is SUPPORTIVE: helping your baby breathe and feed until they get better
• Some babies need oxygen
• Some babies need fluids through a tube if they cannot feed

CARING FOR YOUR BABY AT HOME:
• Feed small amounts more often
• Use saline (salt water) drops in the nose before feeds to clear congestion
• Gently suction the nose with a bulb syringe if very blocked
• Keep your baby upright or with head slightly elevated
• Make sure your baby stays hydrated
• Do not expose your baby to smoke

WHAT TO EXPECT:
• Your baby will likely get worse before getting better (peak is usually day 3-5)
• The cough and some breathing difficulty may last 2-3 weeks
• Most babies recover completely

HOW TO PREVENT BRONCHIOLITIS:
• Wash your hands before touching your baby
• Keep your baby away from people who are sick
• Breastfeed your baby if possible
• Do not smoke or let anyone smoke near your baby
• Avoid crowded places during winter/RSV season

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your baby medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your baby may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• How severe is my baby bronchiolitis?
• Does my baby need to stay in hospital?
• How will I know if my baby is getting worse?
• When should my baby start to get better?
• How can I help my baby breathe and feed more easily?`,

    references: [
      'Ralston SL, Lieberthal AS, Meissner HC, et al. Clinical Practice Guideline: The Diagnosis, Management, and Prevention of Bronchiolitis. Pediatrics. 2014;134(5):e1474-e1502.',
      'National Institute for Health and Care Excellence (NICE). Bronchiolitis in children: diagnosis and management. NICE guideline [NG9]. 2015 (Updated 2021).',
      'Florin TA, Plint AC, Zorc JJ. Viral bronchiolitis. Lancet. 2017;389(10065):211-224.',
      'Meissner HC. Viral Bronchiolitis in Children. N Engl J Med. 2016;374(1):62-72.',
      'Shi T, McAllister DA, OBrien KL, et al. Global, regional, and national disease burden estimates of acute lower respiratory infections due to respiratory syncytial virus in young children in 2015: a systematic review and modelling study. Lancet. 2017;390(10098):946-958.',
      'American Academy of Pediatrics Committee on Infectious Diseases and Bronchiolitis Guidelines Committee. Updated guidance for palivizumab prophylaxis among infants and young children at increased risk of hospitalization for respiratory syncytial virus infection. Pediatrics. 2014;134(2):e620-38.',
      'Schuh S, Freedman S, Coates A, et al. Effect of Oximetry on Hospitalization in Bronchiolitis: A Randomized Clinical Trial. JAMA. 2014;312(7):712-718.',
      'Gadomski AM, Scribani MB. Bronchodilators for bronchiolitis. Cochrane Database Syst Rev. 2014;(6):CD001266.',
      'Fernandes RM, Bialy LM, Vandermeer B, et al. Glucocorticoids for acute viral bronchiolitis in infants and young children. Cochrane Database Syst Rev. 2013;(6):CD004878.',
      'Zhang L, Mendoza-Sassi RA, Wainwright C, Klassen TP. Nebulised hypertonic saline solution for acute bronchiolitis in infants. Cochrane Database Syst Rev. 2017;12(12):CD006458.',
      'Franklin D, Babl FE, Schlapbach LJ, et al. A Randomized Trial of High-Flow Oxygen Therapy in Infants with Bronchiolitis. N Engl J Med. 2018;378(12):1121-1131.',
      'Hasegawa K, Tsugawa Y, Brown DF, et al. Trends in bronchiolitis hospitalizations in the United States, 2000-2009. Pediatrics. 2013;132(1):28-36.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 47. FEBRILE SEIZURES
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '47',
    name: 'Febrile Seizures',
    slug: 'febrile-seizures',
    specialty: 'Pediatrics',
    bodySystem: 'Neurological',
    icdCode: 'R56.0',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Febrile seizures are seizures that occur in children between 6 months and 5 years of age, triggered by fever, without evidence of intracranial infection or other defined cause. They are the most common type of seizure in childhood, affecting 2-5% of children in this age group. Febrile seizures are generally benign and do not cause brain damage or increase the risk of epilepsy significantly in most children.

Febrile seizures are classified as:
• SIMPLE febrile seizures (70-75%): Generalized tonic-clonic, lasting <15 minutes, occurring once in 24 hours, with complete recovery
• COMPLEX febrile seizures (25-30%): Focal features, duration >15 minutes, recurrence within 24 hours, or incomplete recovery

The pathophysiology involves the interaction between fever, a developing brain, and genetic susceptibility. The seizure threshold is lowered by fever in susceptible children. Most febrile seizures occur with temperatures >38.5°C, often during the rapid rise in temperature.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 RULE OUT MENINGITIS: In young children with fever and seizure, meningitis must be excluded; LP if any concern

🔴 SIMPLE FEBRILE SEIZURE = BENIGN: <15 min, generalized, once in 24h, full recovery; excellent prognosis

🔴 MOST STOP SPONTANEOUSLY: Do not need anticonvulsants; focus on airway, positioning, fever source

🔴 IDENTIFY AND TREAT THE FEVER SOURCE: Viral illness most common; look for bacterial infection

🔴 ANTIPYRETICS do NOT prevent febrile seizures: Treat fever for comfort but do not prevent recurrence

🔴 ROUTINE EEG AND NEUROIMAGING NOT INDICATED for simple febrile seizures

🔴 RISK OF EPILEPSY: Only slightly increased (2-4% vs 1% general population) after simple febrile seizure

🔴 RECURRENCE RISK: ~30% will have another febrile seizure; higher if young age, low-grade fever, family history`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common seizure type in childhood
• Affects 2-5% of children in Western populations
• Higher prevalence in Asian populations (6-9% in Japan)
• Peak age: 12-18 months
• Age range: 6 months to 5 years (by definition)
• Male:Female ratio ~1.4:1
• 70-75% are simple febrile seizures
• 25-30% are complex febrile seizures
• Strong genetic component: 25-40% have family history

AFRICAN EPIDEMIOLOGY:
• Limited epidemiological data from Africa
• Estimated prevalence similar or higher than Western populations
• Often associated with malaria (febrile illness)
• Higher risk of complex seizures with severe malaria (cerebral malaria)
• Meningitis more common - must be excluded in African setting
• Limited access to diagnostic workup (LP, imaging)
• May be under-reported due to cultural beliefs
• Traditional medicine often sought first
• Higher risk of complications in settings with limited healthcare access
• Important to distinguish from epilepsy (which has higher stigma)

RECURRENCE RISK:
• Overall: ~30% will have at least one recurrence
• Risk factors for recurrence:
  - Age <18 months at first seizure (50% recurrence)
  - Low-grade fever (<40°C) at first seizure
  - Short duration of fever before seizure (<1 hour)
  - Family history of febrile seizures
• 10% have 3 or more febrile seizures
• Recurrence usually within 1 year of first episode

RISK OF SUBSEQUENT EPILEPSY:
• Simple febrile seizure: 2-4% (vs 1% general population)
• Complex febrile seizure: 4-6%
• Febrile status epilepticus: Up to 10%
• Risk factors for epilepsy:
  - Complex febrile seizures
  - Neurodevelopmental abnormality
  - Family history of epilepsy
  - Multiple recurrent febrile seizures

GENETICS:
• Family history in 25-40% of cases
• Complex inheritance pattern
• Several susceptibility genes identified
• First-degree relative with febrile seizures increases risk 3-fold`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

TYPICAL PRESENTATION:
• Child 6 months to 5 years of age
• Fever (usually >38°C, often >39°C)
• Seizure occurs early in febrile illness (often within first 24 hours)
• May be the first sign of fever (seizure before fever is recognized)
• Underlying illness usually viral (URI, otitis media, roseola, gastroenteritis)

SIMPLE FEBRILE SEIZURE (70-75%):
• Generalized tonic-clonic (stiffening then jerking)
• Duration <15 minutes (most last 1-5 minutes)
• Single seizure in 24-hour period
• No focal features (affects whole body equally)
• Complete recovery (may be sleepy but arousable)
• Normal neurological examination after postictal period

COMPLEX FEBRILE SEIZURE (25-30%):
One or more of:
• Duration >15 minutes (febrile status epilepticus if >30 min)
• Focal features (one side of body, eye deviation, partial onset)
• Recurrence within 24 hours (>1 seizure per febrile episode)
• Incomplete recovery or prolonged postictal state
• Todd paralysis (transient weakness after focal seizure)

SEIZURE DESCRIPTION:
Tonic phase:
• Sudden loss of consciousness
• Generalized stiffening
• May cry out
• Eyes may roll back
• Cyanosis may occur (apnea during tonic phase)

Clonic phase:
• Rhythmic jerking of extremities
• Usually bilateral and symmetric
• Gradually slowing and stopping

Postictal phase:
• Limp, unresponsive initially
• Gradually rouses
• Sleepy, confused (normal)
• Should be arousable and improving over 15-30 minutes

PHYSICAL EXAMINATION:
• Vital signs: Fever (document temperature); otherwise usually normal
• General: Postictal drowsiness expected; should improve
• HEENT: Source of fever (otitis media, pharyngitis)
• Neck: Stiffness (meningismus) - suggests meningitis
• Neurological: Should be normal after postictal period; focal signs suggest complex
• Skin: Rash (viral exanthem, petechiae - think meningococcemia)
• Fontanelle: Bulging (if open) - suggests increased ICP`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: STABILIZE AND ASSESS
• ABC (airway, breathing, circulation)
• Stop seizure if ongoing (>5 minutes)
• Check glucose
• Assess level of consciousness and recovery

STEP 2: CLASSIFY THE SEIZURE
SIMPLE Febrile Seizure (ALL criteria must be met):
✓ Age 6 months to 5 years
✓ Fever present (≥38°C)
✓ Generalized seizure
✓ Duration <15 minutes
✓ Only one seizure in 24 hours
✓ No prior afebrile seizures
✓ No CNS infection or other cause
✓ Normal neurological exam after recovery

COMPLEX Febrile Seizure (ANY of the following):
• Duration >15 minutes
• Focal features
• More than one seizure in 24 hours
• Incomplete recovery or focal neurological findings

STEP 3: IDENTIFY FEVER SOURCE
• Most common: Viral illness (URI, gastroenteritis, roseola)
• Otitis media
• Urinary tract infection (especially in young children)
• Pneumonia
• MUST EXCLUDE: Meningitis, encephalitis

STEP 4: DECIDE ON INVESTIGATIONS

LUMBAR PUNCTURE:
Strong indication:
• Signs of meningitis (neck stiffness, bulging fontanelle, petechial rash)
• Child looks ill or toxic
• Prolonged postictal state (>30-60 minutes)
• Age <12 months (signs of meningitis may be subtle)
• Complex febrile seizure
• Prior antibiotic treatment (may mask meningitis)

Consider:
• Age 12-18 months with incomplete Hib/PCV vaccination
• Any clinical concern for CNS infection

Usually not needed:
• Simple febrile seizure in well-appearing child >18 months
• Fully vaccinated
• Rapid recovery
• Clear source of fever

BLOOD TESTS:
• Not routinely indicated for simple febrile seizure
• Consider: CBC, glucose, electrolytes if clinically indicated
• Blood culture if bacterial infection suspected
• Malaria test in endemic areas

EEG:
• NOT indicated for simple febrile seizure
• Does not predict recurrence or epilepsy
• Consider for complex febrile seizures or if epilepsy suspected

NEUROIMAGING (CT/MRI):
• NOT indicated for simple febrile seizure
• Consider for: Focal seizure, focal neurological findings, prolonged altered consciousness, suspected structural abnormality

STEP 5: OBSERVATION AND DISPOSITION
• Observe until fully recovered and diagnosis clear
• Most children with simple febrile seizure can be discharged after observation
• Admit if: Complex seizure, diagnostic uncertainty, unable to exclude meningitis, social concerns`,

    differentialDiagnosis: [
      'Meningitis/Encephalitis - Sick-appearing; neck stiffness; prolonged altered consciousness; LP diagnostic',
      'Cerebral malaria - Endemic area; P. falciparum; altered consciousness; may have seizures',
      'Epilepsy - Afebrile seizures; recurrent unprovoked seizures; may have triggers other than fever',
      'Febrile illness with rigors/chills - Shivering with fever; no loss of consciousness; no postictal state',
      'Breath-holding spell - Provoked by crying/upset; brief; color change before event; rapid recovery',
      'Syncope - Trigger (standing, pain); pallor; brief; rapid recovery; no postictal confusion',
      'Hypoglycemia - Low glucose; may have seizure; check glucose in all seizures',
      'Electrolyte disturbance - Hyponatremia, hypocalcemia; check electrolytes if suspected',
      'Intracranial mass/hemorrhage - Focal signs; persistent altered consciousness; requires imaging',
      'Shigellosis - Diarrhea; seizure may occur before diarrhea; endemic in some regions',
      'Toxic ingestion - History of ingestion; may have other signs of toxicity',
      'Trauma/Non-accidental injury - Signs of injury; retinal hemorrhages; unexplained symptoms'
    ],

    investigations: [
      {
        name: 'Clinical Assessment',
        indication: '🔴 ESSENTIAL - History of seizure, fever, underlying illness; neurological exam after recovery',
        availability: 'BASIC'
      },
      {
        name: 'Temperature',
        indication: '🔴 ESSENTIAL - Document fever; usually >38.5°C',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Rule out hypoglycemia as cause or complication of seizure',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture / CSF Analysis',
        indication: '🔴 If meningitis suspected - Age <12 months, ill-appearing, signs of meningitis, complex seizure, prolonged postictal',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Rapid Test / Blood Smear',
        indication: '🔴 ESSENTIAL in endemic areas - Febrile seizure may be presentation of malaria; exclude cerebral malaria',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: 'If bacterial infection suspected; not routine for simple febrile seizure',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: 'If bacteremia or sepsis suspected; not routine',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis / Urine Culture',
        indication: 'If UTI suspected as fever source; especially in young children with no clear focus',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: 'If dehydration, diarrhea, or electrolyte abnormality suspected; not routine',
        availability: 'BASIC'
      },
      {
        name: 'EEG',
        indication: 'NOT indicated for simple febrile seizure; consider for complex or if epilepsy suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Head',
        indication: 'NOT routine - Only if focal seizure, focal neuro findings, prolonged altered consciousness, suspected intracranial pathology',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI Brain',
        indication: 'NOT routine - Consider for complex febrile seizures if structural abnormality suspected; after acute phase',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ PROTECT THE CHILD: Place on side (recovery position); clear surroundings; do NOT restrain or put anything in mouth',
        '⚡ TIME THE SEIZURE: Note duration; if >5 minutes, prepare to treat as status epilepticus',
        '⚡ AIRWAY: Ensure airway clear; suction if needed; give oxygen if available',
        '⚡ CHECK GLUCOSE: Treat hypoglycemia if present',
        '⚡ IF SEIZURE ONGOING >5 MIN: Give benzodiazepine (see pharmacological management)',
        '⚡ AFTER SEIZURE STOPS: Assess level of consciousness; monitor recovery',
        '⚡ IDENTIFY FEVER SOURCE: Look for cause of fever; treat underlying infection',
        '⚡ RULE OUT MENINGITIS: If any concern, perform lumbar puncture'
      ],
      pharmacological: [
        {
          drug: 'Diazepam (Rectal)',
          indication: '🔴 SEIZURE >5 MINUTES - First-line for prolonged febrile seizure',
          dose: '0.5 mg/kg rectally (max 10 mg). Can use IV formulation rectally.',
          route: 'Rectal',
          duration: 'Single dose; repeat once after 10 minutes if seizure continues',
          contraindications: ['Respiratory depression (use with caution)'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Midazolam buccal/intranasal; Lorazepam IV']
        },
        {
          drug: 'Diazepam (IV)',
          indication: '🔴 SEIZURE >5 MINUTES with IV access - Rapid onset',
          dose: '0.2-0.3 mg/kg IV slow push (max 10 mg)',
          route: 'IV (slow push over 2-3 minutes)',
          duration: 'Single dose; repeat once after 10 minutes if needed',
          contraindications: ['Respiratory depression'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Lorazepam IV (0.1 mg/kg)']
        },
        {
          drug: 'Midazolam (Buccal/Intranasal)',
          indication: '🔴 SEIZURE >5 MINUTES - Alternative to rectal diazepam; no IV needed',
          dose: 'Buccal: 0.3-0.5 mg/kg (max 10 mg); Intranasal: 0.2 mg/kg (max 10 mg)',
          route: 'Buccal (between cheek and gum) or Intranasal',
          duration: 'Single dose; can repeat once',
          contraindications: ['Respiratory depression'],
          sideEffects: ['Respiratory depression', 'Sedation'],
          alternatives: ['Rectal diazepam; IV lorazepam']
        },
        {
          drug: 'Lorazepam (IV)',
          indication: '🔴 ALTERNATIVE for prolonged seizure - Longer duration of action than diazepam',
          dose: '0.1 mg/kg IV (max 4 mg)',
          route: 'IV',
          duration: 'Single dose; repeat once after 10 minutes if needed',
          contraindications: ['Respiratory depression'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Diazepam IV/rectal; Midazolam']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🌡️ FEVER REDUCTION - For comfort; does NOT prevent febrile seizure recurrence',
          dose: '15 mg/kg/dose every 4-6 hours as needed (max 4 doses/day)',
          route: 'PO or PR',
          duration: 'As needed for fever',
          contraindications: ['Hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['Ibuprofen (if >3 months)']
        },
        {
          drug: 'Ibuprofen',
          indication: '🌡️ FEVER REDUCTION - Alternative to paracetamol; does NOT prevent seizure recurrence',
          dose: '10 mg/kg/dose every 6-8 hours as needed',
          route: 'PO',
          duration: 'As needed for fever',
          contraindications: ['Age <3 months', 'Dehydration', 'Renal impairment', 'GI bleeding'],
          sideEffects: ['GI upset', 'Renal impairment if dehydrated'],
          alternatives: ['Paracetamol']
        },
        {
          drug: 'Phenobarbital',
          indication: '⚠️ STATUS EPILEPTICUS - Second-line if benzodiazepines fail; not for routine use',
          dose: '15-20 mg/kg IV loading dose (infuse over 15-20 minutes)',
          route: 'IV',
          duration: 'Single loading dose; maintenance rarely needed for febrile seizures',
          contraindications: ['Respiratory depression', 'Porphyria'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Phenytoin/Fosphenytoin; Levetiracetam']
        },
        {
          drug: 'Rectal Diazepam (Prophylactic - Home)',
          indication: '⚠️ PREVENTION of prolonged seizures - For parents of children with recurrent/prolonged febrile seizures',
          dose: '0.5 mg/kg rectally at onset of seizure; give if seizure >5 minutes',
          route: 'Rectal',
          duration: 'Single dose at seizure onset; can repeat once',
          contraindications: ['Respiratory depression'],
          sideEffects: ['Sedation', 'Respiratory depression'],
          alternatives: ['Buccal/intranasal midazolam if available']
        },
        {
          drug: 'Intermittent Diazepam (With Fever)',
          indication: '⚠️ NOT ROUTINELY RECOMMENDED - May consider in select high-risk cases; significant side effects',
          dose: '0.33 mg/kg PO every 8 hours during febrile illness (first 48 hours)',
          route: 'PO',
          duration: 'During febrile illness only',
          contraindications: ['Routine use not recommended'],
          sideEffects: ['Sedation', 'Ataxia', 'Irritability', 'May mask CNS infection'],
          alternatives: ['Observation; rescue medication for prolonged seizures']
        },
        {
          drug: 'Continuous Antiepileptic Prophylaxis',
          indication: '❌ NOT RECOMMENDED for simple febrile seizures - Side effects outweigh benefits',
          dose: 'Not applicable for simple febrile seizures',
          route: 'N/A',
          duration: 'N/A',
          contraindications: ['Simple febrile seizures'],
          sideEffects: ['Not applicable'],
          alternatives: ['Reassurance; rescue medication for prolonged seizures']
        },
        {
          drug: 'Antibiotics (for underlying infection)',
          indication: '🦠 TREAT FEVER SOURCE - Based on identified bacterial infection',
          dose: 'Based on specific infection (otitis media, UTI, pneumonia, meningitis)',
          route: 'PO or IV depending on severity',
          duration: 'Based on infection type',
          contraindications: ['Viral illness (no indication)'],
          sideEffects: ['Varies by antibiotic'],
          alternatives: ['Supportive care for viral illness']
        }
      ],
      nonPharmacological: [
        'DURING SEIZURE: Place child on side (recovery position); do NOT restrain; do NOT put anything in mouth; clear surroundings',
        'AFTER SEIZURE: Keep in recovery position until fully awake; monitor breathing and consciousness',
        'FEVER MANAGEMENT: Tepid sponging (lukewarm water, not cold); light clothing; ensure hydration; antipyretics for comfort',
        'HYDRATION: Encourage oral fluids once alert; important especially if vomiting or diarrhea',
        'IDENTIFY FEVER SOURCE: Thorough examination for infection focus',
        'OBSERVATION: Monitor for full recovery; watch for recurrence within 24 hours',
        'PARENT EDUCATION: Crucial part of management; reassurance about benign nature',
        'SEIZURE FIRST AID TRAINING: Teach parents what to do if seizure recurs',
        'HOME RESCUE MEDICATION: Provide rectal/buccal diazepam for parents of children with recurrent or prolonged seizures',
        'FOLLOW-UP PLAN: Ensure family knows when to seek care'
      ],
      surgical: [
        'No surgical intervention indicated for febrile seizures'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Seizure lasting >15 minutes (status epilepticus)',
        '🔴 IMMEDIATE REFERRAL: Suspected meningitis or encephalitis',
        '🔴 IMMEDIATE REFERRAL: Prolonged postictal state (>30-60 minutes)',
        '🔴 IMMEDIATE REFERRAL: Focal seizure or focal neurological findings',
        '🔴 IMMEDIATE REFERRAL: Multiple seizures within 24 hours',
        '🔴 IMMEDIATE REFERRAL: Developmental concerns or pre-existing neurological condition',
        '🟠 URGENT REFERRAL: Complex febrile seizure - needs evaluation',
        '🟠 URGENT REFERRAL: First seizure in child <6 months or >5 years (may not be febrile seizure)',
        '🟠 URGENT REFERRAL: Recurrent febrile seizures (for counseling and management plan)',
        '🟡 ROUTINE REFERRAL: Neurology if concerns about epilepsy or atypical features'
      ],
      monitoring: [
        'DURING SEIZURE: Time duration; observe for focal features; breathing',
        'AFTER SEIZURE: Level of consciousness; recovery pattern; vital signs; temperature',
        'POSTICTAL PERIOD: Should improve over 15-30 minutes; prolonged = concern',
        'NEUROLOGICAL EXAM: After recovery; should be normal in simple febrile seizure',
        'HYDRATION: Especially if vomiting or poor intake',
        'FEVER: Monitor temperature; identify source',
        'OBSERVATION PERIOD: 2-4 hours in ED for simple febrile seizure; longer for complex'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DISCHARGE CRITERIA (Simple Febrile Seizure):
• Fully recovered with normal neurological exam
• Fever source identified and appropriately treated
• Parents educated and comfortable with management
• Reliable follow-up available
• Home rescue medication provided if indicated

POST-DISCHARGE:
• Follow-up with primary care within 1 week
• Earlier if any concerns or recurrence
• Continue treating underlying illness
• Antipyretics for comfort (will not prevent seizures)

PARENT EDUCATION (Critical):
1. REASSURANCE:
   • Febrile seizures are common and usually harmless
   • Do NOT cause brain damage
   • Risk of epilepsy only slightly increased
   • Most children outgrow febrile seizures by age 5

2. WHAT TO DO IF SEIZURE RECURS:
   • Stay calm
   • Place child on side (recovery position)
   • Do NOT put anything in mouth
   • Do NOT restrain
   • Time the seizure
   • If >5 minutes, give rescue medication (if provided) and call for help
   • If <5 minutes and child recovers, see doctor same day

3. WHEN TO SEEK IMMEDIATE CARE:
   • Seizure lasts >5 minutes
   • Child does not wake up after seizure
   • Child looks very ill
   • Difficulty breathing after seizure
   • Another seizure occurs
   • Signs of meningitis (stiff neck, severe headache, rash)

4. FEVER MANAGEMENT:
   • Antipyretics provide comfort but do not prevent seizures
   • Keep child hydrated
   • Do not over-bundle

LONG-TERM FOLLOW-UP:
• Most do not need specialist follow-up after simple febrile seizure
• Refer to neurology if: complex febrile seizures, developmental concerns, family requests
• Update vaccinations (vaccines may cause fever but benefits outweigh risks)

RECURRENCE:
• ~30% will have another febrile seizure
• Not a reason for prophylactic medication in most cases
• Home rescue medication for selected children`
    },

    redFlags: [
      '🔴 CRITICAL: Seizure lasting >15 minutes - Status epilepticus; requires urgent treatment',
      '🔴 CRITICAL: Signs of meningitis - Neck stiffness, bulging fontanelle, petechial rash, toxic appearance',
      '🔴 CRITICAL: Prolonged postictal state (>30-60 min) - Suggests more serious underlying cause',
      '🔴 CRITICAL: Focal seizure or focal neurological findings - May indicate structural abnormality',
      '🔴 CRITICAL: Multiple seizures within 24 hours - Complex febrile seizure; needs evaluation',
      '🔴 CRITICAL: Age <6 months or >5 years - May not be febrile seizure; consider other diagnoses',
      '🔴 CRITICAL: Hypoglycemia - Check glucose in all seizures; treat if low',
      '🟠 URGENT: Very high fever (>40°C) with seizure - Higher risk of bacterial infection',
      '🟠 URGENT: Child with developmental delay or prior neurological condition - Higher risk of complications',
      '🟠 URGENT: No clear fever source - Need to investigate; consider occult bacteremia, UTI',
      '🟡 CAUTION: Prior antibiotic use - May mask signs of meningitis'
    ],

    complications: [
      'FEBRILE STATUS EPILEPTICUS: Seizure >30 minutes; requires aggressive treatment; may cause neurological injury',
      'RECURRENT FEBRILE SEIZURES: ~30% recurrence rate; usually benign; reassurance for parents',
      'INJURY DURING SEIZURE: Falls, tongue biting; usually minor; prevented by safe positioning',
      'ASPIRATION: Rare; prevented by recovery position; do not put anything in mouth',
      'RESPIRATORY COMPROMISE: During prolonged seizure; may need support',
      'SUBSEQUENT EPILEPSY: 2-4% after simple febrile seizure (only slightly above baseline)',
      'PARENTAL ANXIETY: Very common; education and reassurance essential',
      'MISSED MENINGITIS: If not properly evaluated; can be catastrophic'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

EXCELLENT PROGNOSIS FOR SIMPLE FEBRILE SEIZURES:
• No increased mortality
• No brain damage
• No effect on intellect or development
• Normal school performance
• Most children outgrow febrile seizures by age 5

RECURRENCE:
• Overall recurrence risk: ~30%
• Risk factors for recurrence:
  - Age <18 months at first seizure: 50% recurrence
  - Low-grade fever (<40°C) at first seizure
  - Short duration of fever before seizure
  - Family history of febrile seizures
• Most recurrences occur within 1 year
• Recurrence does not change good overall prognosis

RISK OF SUBSEQUENT EPILEPSY:
• General population: ~1%
• Simple febrile seizure: 2-4%
• Complex febrile seizure: 4-6%
• Febrile status epilepticus: Up to 10%
• Risk factors for epilepsy:
  - Complex febrile seizures
  - Pre-existing neurodevelopmental abnormality
  - Family history of epilepsy
  - Multiple recurrent febrile seizures

NEURODEVELOPMENTAL OUTCOMES:
• Multiple large studies show NO adverse effect on:
  - Intelligence
  - Academic performance
  - Behavior
  - Memory
  - Attention
• This holds true even after multiple febrile seizures

LONG-TERM:
• Most children: Complete resolution by age 5-6 years
• No ongoing treatment needed
• Normal life expectancy
• Reassurance to parents is key`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

ANTIPYRETICS DO NOT PREVENT FEBRILE SEIZURES:
• Multiple studies show NO reduction in seizure recurrence
• Ibuprofen and paracetamol do not prevent febrile seizures
• Use antipyretics for COMFORT, not prevention
• Do NOT alternate antipyretics

INTERMITTENT PROPHYLAXIS:
• Diazepam during febrile illness
• May reduce recurrence but significant side effects (sedation, ataxia)
• NOT routinely recommended
• Consider ONLY in highly selected cases (multiple prolonged seizures)

CONTINUOUS PROPHYLAXIS:
• Phenobarbital or valproate can reduce recurrence
• NOT recommended due to significant side effects
• Side effects (sedation, behavioral changes, hepatotoxicity) outweigh benefits
• Risk-benefit ratio unfavorable for benign condition

WHAT IS RECOMMENDED:

1. EDUCATION AND REASSURANCE:
   • Most important intervention
   • Explain benign nature
   • Teach seizure first aid
   • Explain recurrence risk and prognosis

2. HOME RESCUE MEDICATION:
   • Rectal diazepam or buccal midazolam
   • Provide to parents of children with:
     - Previous prolonged seizure (>5 minutes)
     - History of status epilepticus
     - Limited access to medical care
   • Teach proper administration

3. PROMPT TREATMENT OF FEVER SOURCE:
   • Identify and treat infections appropriately
   • Antibiotics for bacterial infections
   • Supportive care for viral infections

4. GENERAL MEASURES:
   • Maintain hydration during febrile illness
   • Do not over-bundle child
   • Light clothing
   • Comfortable room temperature

5. VACCINATIONS:
   • Continue routine vaccinations
   • Vaccines may cause fever but benefits far outweigh risks
   • Slight increased risk of febrile seizure after some vaccines (MMR, DTaP)
   • Still recommended - diseases prevented are far worse`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS A FEBRILE SEIZURE?
A febrile seizure is a fit (convulsion) that can happen when a child has a fever. It occurs in children between 6 months and 5 years of age. About 1 in 20-30 children will have a febrile seizure. It is scary to watch but usually is not harmful.

IS A FEBRILE SEIZURE DANGEROUS?
• NO - febrile seizures are generally NOT dangerous
• They do NOT cause brain damage
• They do NOT affect your child intelligence or development
• Most children outgrow them by age 5

WHAT DOES A FEBRILE SEIZURE LOOK LIKE?
• Child may become stiff and then start jerking
• Eyes may roll back
• Child may not respond to you
• May turn blue briefly
• Usually lasts 1-3 minutes
• After the seizure, child will be sleepy but should gradually wake up

WHAT TO DO IF YOUR CHILD HAS A SEIZURE:
1. STAY CALM - the seizure will usually stop on its own
2. Place your child on their side (recovery position)
3. Do NOT put anything in their mouth
4. Do NOT try to hold them down or stop the jerking
5. Move away any objects that could hurt them
6. Look at the clock - time how long the seizure lasts
7. Stay with your child

AFTER THE SEIZURE:
• Keep your child on their side
• They will be sleepy - this is normal
• Stay with them until they are fully awake
• Check their temperature
• Give fever medicine once they are awake

WHEN TO CALL FOR EMERGENCY HELP:
• Seizure lasts more than 5 minutes
• Your child has trouble breathing after the seizure
• Your child does not wake up within 15-20 minutes
• Another seizure happens
• Your child looks very ill

WHEN TO SEE A DOCTOR:
• This is your child first seizure - always get checked
• If you are worried for any reason
• If your child has a stiff neck or bad headache
• If there is a rash that does not fade when pressed

ABOUT FEVER MEDICINE:
• Paracetamol and ibuprofen help your child feel more comfortable
• They do NOT prevent febrile seizures
• Give them for comfort, not to prevent seizures

WILL IT HAPPEN AGAIN?
• About 1 in 3 children who have one febrile seizure will have another
• Even if it happens again, it is usually not harmful
• Most children stop having febrile seizures by age 5

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• Was this definitely a febrile seizure?
• What caused my child fever?
• Will my child have more seizures?
• What should I do if another seizure happens?
• Do I need any medicine to prevent seizures?
• When should my child be seen again?`,

    references: [
      'Subcommittee on Febrile Seizures; American Academy of Pediatrics. Neurodiagnostic evaluation of the child with a simple febrile seizure. Pediatrics. 2011;127(2):389-394.',
      'Subcommittee on Febrile Seizures. Febrile Seizures: Guideline for the Neurodiagnostic Evaluation of the Child With a Simple Febrile Seizure. Pediatrics. 2011;127(2):389-394.',
      'Steering Committee on Quality Improvement and Management, Subcommittee on Febrile Seizures. Febrile seizures: clinical practice guideline for the long-term management of the child with simple febrile seizures. Pediatrics. 2008;121(6):1281-1286.',
      'Offringa M, Newton R, Cozijnsen MA, Nevitt SJ. Prophylactic drug management for febrile seizures in children. Cochrane Database Syst Rev. 2017;2(2):CD003031.',
      'Leung AK, Hon KL, Leung TN. Febrile seizures: an overview. Drugs Context. 2018;7:212536.',
      'Patel N, Ram D, Swiderska N, Misra D, McGhee K, Sherlock C. Febrile seizures. BMJ. 2015;351:h4240.',
      'Whelan H, Harber M, Kelso A. Febrile seizures: clinical practice guideline. J Paediatr Child Health. 2017;53(10):953-959.',
      'Sadleir LG, Scheffer IE. Febrile seizures. BMJ. 2007;334(7588):307-311.',
      'National Institute for Health and Care Excellence (NICE). Fever in under 5s: assessment and initial management. NICE guideline [NG143]. 2019.',
      'Shinnar S, Glauser TA. Febrile seizures. J Child Neurol. 2002;17 Suppl 1:S44-52.',
      'Vestergaard M, Pedersen CB, Sidenius P, et al. The long-term risk of epilepsy after febrile seizures in susceptible subgroups. Am J Epidemiol. 2007;165(8):911-918.',
      'Berg AT. Febrile seizures and epilepsy: the contributions of epidemiology. Paediatr Perinat Epidemiol. 1992;6(2):145-152.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 48. RICKETS / VITAMIN D DEFICIENCY
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '48',
    name: 'Rickets / Vitamin D Deficiency',
    slug: 'rickets-vitamin-d-deficiency',
    specialty: 'Pediatrics',
    bodySystem: 'Musculoskeletal',
    icdCode: 'E55.0',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Rickets is a disease of growing bone characterized by defective mineralization of the growth plate and osteoid, resulting in soft, weak bones. The most common cause worldwide is vitamin D deficiency (nutritional rickets), though it can also result from calcium deficiency, phosphate deficiency, or inherited disorders.

Vitamin D is essential for calcium and phosphate homeostasis. It is obtained through skin synthesis (UV-B exposure) and dietary intake. Deficiency leads to inadequate calcium absorption, secondary hyperparathyroidism, phosphate wasting, and ultimately defective bone mineralization.

Clinical features include skeletal deformities (bowed legs, knock knees, rachitic rosary, craniotabes), growth retardation, delayed motor milestones, hypocalcemic symptoms (tetany, seizures), and dental abnormalities. Rickets only affects growing children; the adult equivalent is osteomalacia.

Despite abundant sunshine in Africa, rickets remains prevalent due to dark skin pigmentation (reduces UV synthesis), cultural practices (covering skin, keeping infants indoors), exclusive breastfeeding without supplementation, and dietary calcium deficiency.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 HYPOCALCEMIC SEIZURES/TETANY: Medical emergency; treat hypocalcemia urgently BEFORE giving vitamin D

🔴 CHECK CALCIUM before vitamin D loading: Giving vitamin D without correcting severe hypocalcemia can worsen symptoms

🔴 VITAMIN D + CALCIUM: Both are needed for treatment; vitamin D alone may not suffice if calcium intake is low

🔴 STOSS THERAPY: High-dose single or intermittent vitamin D is effective and improves compliance

🔴 DIAGNOSIS: Clinical features + low 25(OH)D + elevated ALP + characteristic X-ray changes

🔴 PREVENTION: Vitamin D supplementation for all breastfed infants (400 IU/day)

🔴 SUNLIGHT ALONE may not be sufficient in dark-skinned populations - supplementation needed

🔴 CALCIUM-DEFICIENCY RICKETS: Common in Africa; may have normal vitamin D; needs calcium supplementation`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Rickets is re-emerging globally, even in developed countries
• Estimated 1 billion people worldwide have vitamin D deficiency
• Highest prevalence in Middle East, South Asia, and Africa
• Risk factors: dark skin, limited sun exposure, exclusive breastfeeding, vegetarian diet
• Peak age: 3-18 months (rapid growth period)
• Second peak in adolescence (growth spurt)
• More common in winter months in temperate climates
• Immigrant populations in northern latitudes at high risk

AFRICAN EPIDEMIOLOGY:
• Paradoxically high prevalence despite abundant sunshine
• Prevalence estimates: 5-20% in some populations
• Nigeria: Rickets common, especially in northern regions
• Ethiopia, Kenya, South Africa: Significant burden reported
• Dark skin requires 5-10x more sun exposure to produce same vitamin D as light skin
• Cultural practices contribute: covering skin, keeping infants indoors
• Exclusive breastfeeding without vitamin D supplementation
• CALCIUM-DEFICIENCY RICKETS: Major cause in Africa
  - Low dietary calcium intake (low dairy consumption)
  - May have normal or near-normal vitamin D levels
  - Requires calcium supplementation, not just vitamin D
• Urban populations increasingly affected (less outdoor activity)
• Malnutrition frequently coexists

RISK FACTORS:
• Dark skin pigmentation
• Exclusive breastfeeding without supplementation
• Limited sun exposure (indoor lifestyle, covering skin)
• Maternal vitamin D deficiency
• Prematurity (inadequate stores)
• Malabsorption (celiac disease, cystic fibrosis)
• Chronic kidney disease
• Liver disease
• Anticonvulsant medications (phenytoin, phenobarbital)
• Low dietary calcium intake
• Vegetarian/vegan diet without supplementation
• Obesity (vitamin D sequestered in fat)
• Northern latitudes / winter season

CAUSES OF RICKETS:
1. Nutritional (most common):
   • Vitamin D deficiency
   • Calcium deficiency
   • Combined deficiency

2. Vitamin D-dependent rickets (genetic):
   • Type 1: 1-alpha-hydroxylase deficiency
   • Type 2: Vitamin D receptor defect

3. Vitamin D-resistant rickets:
   • X-linked hypophosphatemia (most common inherited)
   • Autosomal dominant/recessive hypophosphatemia

4. Secondary causes:
   • Chronic kidney disease
   • Malabsorption syndromes
   • Hepatobiliary disease
   • Medications`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

SKELETAL MANIFESTATIONS:

Skull:
• Craniotabes: Softening of skull bones; "ping-pong ball" sensation on palpation
• Frontal bossing: Prominent forehead
• Delayed fontanelle closure (anterior fontanelle normally closes by 18 months)
• Caput quadratum: Square-shaped head

Chest:
• Rachitic rosary: Beading at costochondral junctions (palpable nodules)
• Harrison groove: Horizontal depression along lower chest at diaphragm attachment
• Pigeon chest (pectus carinatum) or funnel chest (pectus excavatum)

Spine:
• Kyphosis (especially when sitting)
• Scoliosis
• Lordosis

Extremities:
• Widening of wrists and ankles (metaphyseal flaring)
• Bowing of legs (genu varum) - most common deformity
• Knock knees (genu valgum)
• Windswept deformity (varus on one side, valgus on other)
• Coxa vara (abnormal hip angle)
• Greenstick fractures

Pelvis:
• Pelvic deformities
• May cause obstructed labor in females later in life

GROWTH AND DEVELOPMENT:
• Short stature / growth retardation
• Delayed motor milestones (sitting, standing, walking)
• Delayed dentition
• Enamel hypoplasia
• Dental caries
• Muscle weakness (proximal myopathy)
• Hypotonia
• Waddling gait

HYPOCALCEMIC MANIFESTATIONS:
• Tetany: Carpopedal spasm, laryngospasm
• Seizures (especially in infants)
• Irritability
• Paresthesias
• Trousseau sign: Carpal spasm with BP cuff inflation
• Chvostek sign: Facial twitching when tapping facial nerve
• Stridor (laryngospasm)
• Apnea (infants)

OTHER FEATURES:
• Failure to thrive
• Delayed closure of fontanelle
• Hypotonia ("floppy baby")
• Pot belly (weak abdominal muscles)
• Frequent respiratory infections (rib cage deformity)
• Cardiac: Dilated cardiomyopathy (severe cases)

CLINICAL PRESENTATION BY AGE:

0-6 months:
• Craniotabes
• Hypocalcemic seizures
• Hypotonia
• Poor feeding

6-18 months:
• Widened wrists
• Rachitic rosary
• Delayed motor milestones
• Frontal bossing

Toddlers:
• Bowing of legs (becomes apparent with weight-bearing)
• Waddling gait
• Short stature
• Muscle weakness

Older children/Adolescents:
• Leg deformities (genu valgum more common)
• Bone pain
• Muscle weakness
• Growth spurt abnormalities`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
Suspect rickets if:
• Skeletal deformities (bowed legs, widened wrists, rachitic rosary)
• Growth retardation
• Delayed motor milestones
• Hypocalcemic symptoms (tetany, seizures)
• Risk factors present (dark skin, breastfed without supplementation, limited sun)

STEP 2: BIOCHEMICAL EVALUATION

Essential tests:
• Serum Calcium: Low or low-normal (may be maintained at expense of bone)
• Serum Phosphorus: Low (vitamin D deficiency) or very low (hypophosphatemic rickets)
• Alkaline Phosphatase (ALP): ELEVATED - most sensitive marker of active rickets
• 25-hydroxyvitamin D [25(OH)D]: LOW in vitamin D deficiency (<20 ng/mL deficient; <12 ng/mL severe)
• Parathyroid Hormone (PTH): ELEVATED (secondary hyperparathyroidism)

Additional tests if needed:
• 1,25-dihydroxyvitamin D: Usually normal or elevated in nutritional rickets; low in type 1 VDDR
• Renal function (creatinine, BUN): Rule out renal osteodystrophy
• Urinary calcium and phosphorus
• Tubular reabsorption of phosphate (TRP): Low in hypophosphatemic rickets

STEP 3: RADIOLOGICAL EVALUATION

X-ray of wrists and knees (most diagnostic):
• Cupping: Concave, irregular metaphysis
• Fraying: Indistinct, frayed metaphyseal margins
• Splaying: Widened metaphysis
• Widened growth plate
• Decreased bone density (osteopenia)
• Coarse trabecular pattern
• Bowing of long bones
• Greenstick fractures
• Looser zones (pseudofractures) in severe cases

Severity grading (radiological):
• Grade 1: Mild metaphyseal changes
• Grade 2: Moderate changes, some bowing
• Grade 3: Severe changes, significant deformities

STEP 4: DETERMINE ETIOLOGY

Vitamin D deficiency rickets:
• Low 25(OH)D
• Low calcium and phosphorus (or calcium low-normal)
• High ALP
• High PTH

Calcium-deficiency rickets:
• Normal or mildly low 25(OH)D
• Low calcium
• Low or normal phosphorus
• High ALP
• High PTH
• Common in Africa

Hypophosphatemic rickets:
• Very low phosphorus
• Normal calcium
• Normal or low 25(OH)D
• Normal or low PTH
• Low TRP (tubular reabsorption of phosphate)
• Family history often present

DIAGNOSTIC CRITERIA:
Clinical rickets = Clinical features + Radiological changes + Biochemical abnormalities`,

    differentialDiagnosis: [
      'Physiological bowing - Normal variant in toddlers; usually resolves by age 2-3; no biochemical abnormalities',
      'Blount disease (tibia vara) - Abnormal growth of medial proximal tibial physis; progressive; X-ray shows medial tibial beaking',
      'Skeletal dysplasias - Short stature; disproportionate; family history; normal biochemistry; genetic testing',
      'Hypophosphatemic rickets (X-linked) - Very low phosphate; normal calcium; family history; requires phosphate + calcitriol',
      'Vitamin D-dependent rickets type 1 - Low 1,25(OH)D; normal 25(OH)D; genetic; requires calcitriol',
      'Vitamin D-dependent rickets type 2 - Receptor defect; high 1,25(OH)D; alopecia often present',
      'Renal osteodystrophy - Chronic kidney disease; elevated creatinine; secondary hyperparathyroidism',
      'Hypophosphatasia - Low ALP (distinguishing feature); genetic; skeletal abnormalities',
      'Metaphyseal chondrodysplasia - Metaphyseal changes; normal biochemistry; genetic',
      'Congenital hypothyroidism - Delayed development; large fontanelle; constipation; low T4/high TSH',
      'Osteogenesis imperfecta - Multiple fractures; blue sclerae; family history; normal biochemistry',
      'Non-accidental injury - Multiple fractures; inconsistent history; other signs of abuse'
    ],

    investigations: [
      {
        name: 'Serum Calcium',
        indication: '🔴 ESSENTIAL - Low or low-normal; check BEFORE giving vitamin D; treat hypocalcemia first',
        availability: 'BASIC'
      },
      {
        name: 'Serum Phosphorus',
        indication: '🔴 ESSENTIAL - Low in vitamin D deficiency; very low in hypophosphatemic rickets',
        availability: 'BASIC'
      },
      {
        name: 'Alkaline Phosphatase (ALP)',
        indication: '🔴 ESSENTIAL - ELEVATED; most sensitive marker; falls with treatment (monitor response)',
        availability: 'BASIC'
      },
      {
        name: '25-hydroxyvitamin D [25(OH)D]',
        indication: '🔴 ESSENTIAL for diagnosis - <20 ng/mL deficient; <12 ng/mL severe deficiency',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Parathyroid Hormone (PTH)',
        indication: 'ELEVATED in nutritional rickets (secondary hyperparathyroidism); helps distinguish etiology',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'X-ray of Wrists and Knees',
        indication: '🔴 ESSENTIAL - Cupping, fraying, splaying of metaphyses; widened growth plate; osteopenia',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine / Renal Function',
        indication: 'Rule out chronic kidney disease as cause of rickets',
        availability: 'BASIC'
      },
      {
        name: 'Urinary Calcium/Creatinine Ratio',
        indication: 'Monitor for hypercalciuria during treatment; risk of nephrocalcinosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: '1,25-dihydroxyvitamin D',
        indication: 'If VDDR suspected - Low in type 1; high in type 2 and nutritional rickets',
        availability: 'ADVANCED'
      },
      {
        name: 'Tubular Reabsorption of Phosphate (TRP)',
        indication: 'If hypophosphatemic rickets suspected - Low TRP indicates renal phosphate wasting',
        availability: 'ADVANCED'
      },
      {
        name: 'Genetic Testing',
        indication: 'If inherited form suspected - X-linked hypophosphatemia, VDDR type 1 or 2',
        availability: 'ADVANCED'
      },
      {
        name: 'Celiac Serology / Malabsorption Workup',
        indication: 'If malabsorption suspected as underlying cause; consider in refractory cases',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ CHECK SERUM CALCIUM FIRST: Hypocalcemia must be corrected BEFORE or WITH vitamin D therapy',
        '⚡ IF HYPOCALCEMIC SEIZURES/TETANY: Treat as emergency; give IV calcium gluconate',
        '⚡ SECURE AIRWAY if laryngospasm: Be prepared for airway intervention',
        '⚡ CARDIAC MONITORING: Calcium affects heart; monitor during IV calcium administration',
        '⚡ DETERMINE ETIOLOGY: Vitamin D deficiency vs calcium deficiency vs other causes'
      ],
      pharmacological: [
        {
          drug: 'Calcium Gluconate 10% (IV)',
          indication: '🔴 EMERGENCY - Symptomatic hypocalcemia (seizures, tetany, laryngospasm)',
          dose: '1-2 mL/kg (100-200 mg/kg of calcium gluconate = 9-18 mg/kg elemental calcium) IV slowly over 10-20 minutes; dilute in D5W or NS',
          route: 'IV (slow infusion with cardiac monitoring)',
          duration: 'Until symptoms controlled; then oral calcium maintenance',
          contraindications: ['Hypercalcemia', 'Digitalis toxicity (caution)'],
          sideEffects: ['Bradycardia if given too fast', 'Tissue necrosis if extravasation', 'Hypercalcemia'],
          alternatives: ['Calcium chloride (more potent but more caustic)']
        },
        {
          drug: 'Oral Calcium Supplementation',
          indication: '🔴 ALL RICKETS CASES - Essential component of treatment; give WITH vitamin D',
          dose: '30-75 mg/kg/day of elemental calcium divided in 2-3 doses (range: 500-1500 mg/day elemental calcium). Calcium carbonate (40% elemental) or calcium citrate (21% elemental).',
          route: 'PO',
          duration: 'Throughout active treatment (minimum 3 months); longer if dietary intake inadequate',
          contraindications: ['Hypercalcemia', 'Hypercalciuria'],
          sideEffects: ['Constipation', 'GI upset', 'Hypercalcemia (overdose)', 'Nephrocalcinosis'],
          alternatives: ['Dietary calcium if sufficient intake achievable']
        },
        {
          drug: 'Vitamin D3 (Cholecalciferol) - Daily Therapy',
          indication: '🔴 VITAMIN D DEFICIENCY RICKETS - Standard treatment option',
          dose: 'Treatment: 2000-6000 IU/day depending on age and severity. Infants <12 months: 2000 IU/day; 1-12 years: 3000-6000 IU/day; >12 years: 6000 IU/day.',
          route: 'PO',
          duration: '8-12 weeks, then maintenance 400-1000 IU/day',
          contraindications: ['Hypercalcemia', 'Hypervitaminosis D'],
          sideEffects: ['Hypercalcemia', 'Hypercalciuria', 'Nephrocalcinosis (if prolonged excess)'],
          alternatives: ['Vitamin D2 (ergocalciferol); Stoss therapy']
        },
        {
          drug: 'Vitamin D3 (Cholecalciferol) - STOSS Therapy (High-Dose)',
          indication: '💊 STOSS THERAPY - Single high-dose treatment; improves compliance; effective',
          dose: 'Single dose of 100,000-600,000 IU (age-dependent). <3 months: 50,000 IU; 3-12 months: 100,000-150,000 IU; 1-12 years: 150,000-300,000 IU; >12 years: 300,000-600,000 IU. Can repeat after 3 months if needed.',
          route: 'PO (or IM if absorption concern)',
          duration: 'Single dose; follow up at 1 and 3 months',
          contraindications: ['Hypercalcemia', 'Granulomatous disease (sarcoidosis)'],
          sideEffects: ['Hypercalcemia (usually transient)', 'Hypercalciuria'],
          alternatives: ['Daily vitamin D therapy']
        },
        {
          drug: 'Vitamin D2 (Ergocalciferol)',
          indication: '💊 ALTERNATIVE to vitamin D3 - Less potent; requires higher doses',
          dose: 'Treatment: 50,000 IU weekly for 8-12 weeks, then maintenance. Stoss: 300,000-600,000 IU single dose.',
          route: 'PO',
          duration: '8-12 weeks treatment, then maintenance',
          contraindications: ['Hypercalcemia'],
          sideEffects: ['Hypercalcemia', 'Hypercalciuria'],
          alternatives: ['Vitamin D3 (preferred)']
        },
        {
          drug: 'Calcitriol (1,25-dihydroxyvitamin D)',
          indication: '🔬 VDDR Type 1 or Chronic Kidney Disease - Active form of vitamin D',
          dose: '0.25-2 mcg/day in divided doses; titrate based on calcium levels',
          route: 'PO',
          duration: 'Lifelong for genetic causes; ongoing for CKD',
          contraindications: ['Hypercalcemia', 'Hypervitaminosis D'],
          sideEffects: ['Hypercalcemia', 'Hypercalciuria', 'Nephrocalcinosis'],
          alternatives: ['Alfacalcidol']
        },
        {
          drug: 'Alfacalcidol (1-alpha-hydroxyvitamin D)',
          indication: '🔬 ALTERNATIVE active vitamin D - Requires hepatic conversion',
          dose: '0.05-0.1 mcg/kg/day; titrate to response',
          route: 'PO',
          duration: 'As for calcitriol',
          contraindications: ['Hypercalcemia'],
          sideEffects: ['Hypercalcemia', 'Hypercalciuria'],
          alternatives: ['Calcitriol']
        },
        {
          drug: 'Phosphate Supplementation',
          indication: '🔬 HYPOPHOSPHATEMIC RICKETS - Essential for X-linked and other phosphate-wasting disorders',
          dose: '20-40 mg/kg/day of elemental phosphorus divided in 4-6 doses',
          route: 'PO',
          duration: 'Ongoing; lifelong for genetic causes',
          contraindications: ['Hyperphosphatemia', 'Renal failure'],
          sideEffects: ['Diarrhea', 'Secondary hyperparathyroidism if not balanced with calcitriol'],
          alternatives: ['None for hypophosphatemic rickets']
        },
        {
          drug: 'Maintenance Vitamin D (Prevention)',
          indication: '🛡️ PREVENTION after treatment and for all at-risk infants',
          dose: 'Infants: 400 IU/day; Children 1-18 years: 600-1000 IU/day',
          route: 'PO',
          duration: 'Ongoing; at least until adequate sun exposure and dietary intake assured',
          contraindications: ['Hypervitaminosis D'],
          sideEffects: ['Rare at preventive doses'],
          alternatives: ['Adequate sun exposure and dietary sources']
        }
      ],
      nonPharmacological: [
        'SUN EXPOSURE: 10-30 minutes of midday sun to face, hands, arms 2-3 times weekly (varies with skin color, latitude, season); more for dark-skinned individuals',
        'DIETARY CALCIUM: Encourage calcium-rich foods - dairy products, fortified foods, green leafy vegetables, small fish with bones',
        'DIETARY VITAMIN D: Fatty fish, egg yolks, fortified milk and cereals; often insufficient alone',
        'BREASTFEEDING SUPPORT: Continue breastfeeding but ADD vitamin D supplementation (400 IU/day)',
        'AVOID EXCESSIVE COVERING: Cultural practices of keeping infants covered/indoors contribute to deficiency',
        'PHYSICAL THERAPY: May be needed for muscle weakness; exercises to strengthen muscles',
        'WEIGHT-BEARING ACTIVITY: Encourage as tolerated; helps bone mineralization',
        'NUTRITIONAL REHABILITATION: Address any coexisting malnutrition',
        'MONITOR GROWTH: Plot on growth charts; catch-up growth expected with treatment',
        'FAMILY SCREENING: Screen siblings and mother for vitamin D deficiency'
      ],
      surgical: [
        'CORRECTIVE OSTEOTOMY: For persistent severe deformities after medical treatment complete (usually wait until growth plate healed)',
        'GUIDED GROWTH (Hemiepiphysiodesis): Minimally invasive; for angular deformities in growing children',
        'TIMING: Surgery only after rickets healed (normalized ALP, healed X-rays); typically wait 6-12 months after treatment'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Hypocalcemic seizures or tetany - medical emergency',
        '🔴 IMMEDIATE REFERRAL: Severe hypocalcemia with cardiac symptoms',
        '🟠 URGENT REFERRAL: Suspected inherited/genetic rickets (not responding to vitamin D)',
        '🟠 URGENT REFERRAL: Renal osteodystrophy (chronic kidney disease)',
        '🟠 URGENT REFERRAL: Rickets with malabsorption syndrome',
        '🟡 ROUTINE REFERRAL: Pediatric endocrinology if atypical features or poor response to treatment',
        '🟡 ROUTINE REFERRAL: Orthopedics for significant skeletal deformities after medical treatment',
        '🟡 ROUTINE REFERRAL: Nephrology if hypercalciuria or nephrocalcinosis develops',
        '🟡 ROUTINE REFERRAL: Genetic counseling for inherited forms'
      ],
      monitoring: [
        'SERUM CALCIUM: Weekly initially if hypocalcemic; then monthly during treatment; watch for hypercalcemia',
        'SERUM PHOSPHORUS: Monthly during treatment',
        'ALKALINE PHOSPHATASE: Monthly; should normalize within 1-3 months; best marker of healing',
        '25(OH)D LEVEL: At 3 months to confirm adequacy; target >30 ng/mL',
        'PTH: Should normalize as vitamin D repleted; persistent elevation suggests inadequate treatment',
        'URINARY CALCIUM/CREATININE: Monitor for hypercalciuria; risk of nephrocalcinosis',
        'X-RAYS: Repeat at 6-12 weeks; should show healing of metaphyseal changes',
        'GROWTH: Monitor height/length; expect catch-up growth with treatment',
        'CLINICAL IMPROVEMENT: Muscle strength, motor milestones, deformity progression'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

TREATMENT PHASE (First 3 months):
• Week 1-2: Check calcium (especially if initially hypocalcemic)
• Monthly: Calcium, phosphorus, ALP
• 6-8 weeks: Repeat X-ray to assess healing
• 3 months: 25(OH)D level; should be >30 ng/mL

HEALING PHASE (3-12 months):
• Every 2-3 months: Clinical assessment, ALP
• 6 months: Repeat X-rays if deformities present
• Assess need for ongoing treatment vs maintenance

MAINTENANCE PHASE:
• Every 6-12 months: 25(OH)D, calcium, ALP
• Annual assessment of growth, skeletal status
• Lifelong monitoring for inherited forms

EXPECTED RESPONSE TO TREATMENT:
• Biochemical: Calcium normalizes in days-weeks; ALP starts falling in 2-4 weeks
• Radiological: Healing visible in 6-8 weeks; complete healing 3-6 months
• Clinical: Improved muscle tone and strength in weeks; skeletal deformities may take months-years
• Growth: Catch-up growth begins once rickets controlled

TREATMENT FAILURE - CONSIDER:
• Non-compliance (most common)
• Inadequate calcium intake
• Malabsorption
• Incorrect diagnosis (inherited rickets)
• Ongoing risk factors not addressed

ORTHOPEDIC FOLLOW-UP:
• Refer if significant deformities persist after biochemical healing
• Most mild-moderate deformities improve with growth
• Surgery rarely needed; only after medical treatment complete

PREVENTION OF RECURRENCE:
• Maintenance vitamin D (400-1000 IU/day)
• Adequate calcium intake
• Appropriate sun exposure
• Address modifiable risk factors
• Screen and supplement other family members`
    },

    redFlags: [
      '🔴 CRITICAL: Hypocalcemic seizures - Medical emergency; IV calcium urgently; airway protection',
      '🔴 CRITICAL: Tetany/carpopedal spasm - Symptomatic hypocalcemia; IV calcium needed',
      '🔴 CRITICAL: Laryngospasm/stridor - Airway emergency; IV calcium; prepare for intubation',
      '🔴 CRITICAL: Dilated cardiomyopathy - Severe vitamin D deficiency can cause; needs cardiac assessment',
      '🟠 URGENT: Severe hypocalcemia (<7 mg/dL) - Even if asymptomatic; needs treatment',
      '🟠 URGENT: Failure to respond to standard vitamin D treatment - Consider malabsorption or inherited rickets',
      '🟠 URGENT: Pathological fractures - May occur with minimal trauma',
      '🟡 CAUTION: Hypercalcemia during treatment - Reduce vitamin D dose; check urinary calcium',
      '🟡 CAUTION: Hypercalciuria - Risk of nephrocalcinosis; reduce doses; ensure adequate hydration'
    ],

    complications: [
      'HYPOCALCEMIC SEIZURES: Medical emergency; brain damage if prolonged; prevent with adequate calcium',
      'TETANY/LARYNGOSPASM: Can be life-threatening; airway emergency',
      'SKELETAL DEFORMITIES: Bowing, knock knees, rachitic rosary; may be permanent if untreated',
      'SHORT STATURE: Growth retardation; catch-up growth possible with treatment',
      'PATHOLOGICAL FRACTURES: Bones are weak; fractures with minimal trauma',
      'DELAYED MOTOR DEVELOPMENT: Hypotonia, muscle weakness; improves with treatment',
      'DENTAL ABNORMALITIES: Enamel hypoplasia, delayed eruption, caries',
      'DILATED CARDIOMYOPATHY: Rare; severe cases; usually reversible with treatment',
      'PELVIC DEFORMITIES: May cause obstructed labor in females later in life',
      'RESPIRATORY COMPLICATIONS: Rib cage deformity may impair breathing',
      'HYPERCALCEMIA (Treatment complication): From excessive vitamin D; monitor during treatment',
      'NEPHROCALCINOSIS (Treatment complication): From hypercalciuria; monitor urinary calcium'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

EXCELLENT PROGNOSIS WITH TREATMENT:
• Biochemical abnormalities normalize within weeks
• Radiological healing within 3-6 months
• Growth catch-up expected
• Muscle strength improves
• Most skeletal deformities improve or resolve

SKELETAL OUTCOMES:
• Mild deformities: Usually resolve completely with growth
• Moderate deformities: Significant improvement; may normalize
• Severe deformities: May require surgical correction after medical treatment

FACTORS AFFECTING PROGNOSIS:

Better prognosis:
• Early diagnosis and treatment
• Younger age at treatment
• Good compliance
• Adequate calcium intake
• Mild disease
• Nutritional cause (vs inherited)

Worse prognosis:
• Delayed diagnosis
• Severe deformities at presentation
• Poor compliance
• Inherited forms (require lifelong treatment)
• Underlying malabsorption
• Ongoing risk factors not addressed

LONG-TERM OUTCOMES:
• Nutritional rickets: Excellent if treated; no long-term sequelae expected
• Inherited rickets: Requires lifelong treatment; deformities may be progressive without treatment
• Untreated rickets: Progressive deformities, short stature, fractures, dental problems

DEVELOPMENTAL OUTCOMES:
• Motor delays usually catch up completely
• No effect on cognitive development (unless severe hypocalcemia with seizures)
• Height potential may be reduced if treatment significantly delayed`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

VITAMIN D SUPPLEMENTATION:

INFANTS:
• ALL breastfed infants: 400 IU/day from birth
• Formula-fed infants: If <1 liter/day of fortified formula, supplement
• Premature infants: Higher doses needed (400-800 IU/day)
• Continue until dietary intake adequate or sun exposure sufficient

CHILDREN AND ADOLESCENTS:
• 600-1000 IU/day if risk factors present
• Higher doses if dark skin, limited sun, or northern latitude

PREGNANT AND LACTATING WOMEN:
• 600-4000 IU/day depending on risk factors
• Maternal supplementation improves infant vitamin D status

HIGH-RISK GROUPS (need supplementation):
• Dark-skinned individuals
• Those with limited sun exposure
• Exclusively breastfed infants
• Vegetarians/vegans
• Obese individuals
• Malabsorptive conditions
• Those on anticonvulsants

ADEQUATE SUN EXPOSURE:
• 10-30 minutes of midday sun (10am-3pm)
• Face, arms, legs exposed
• 2-3 times per week
• More time needed for dark-skinned individuals
• Difficult to achieve in northern latitudes in winter
• Balance with skin cancer prevention

DIETARY CALCIUM:
• Infants: 200-260 mg/day
• Children 1-3 years: 700 mg/day
• Children 4-8 years: 1000 mg/day
• Children 9-18 years: 1300 mg/day
• Sources: Dairy, fortified foods, green vegetables, small fish with bones

FOOD FORTIFICATION:
• Fortified milk, cereals, margarine
• Public health programs in endemic areas

MATERNAL HEALTH:
• Screen and treat vitamin D deficiency in pregnancy
• Reduces risk of neonatal rickets

PUBLIC HEALTH STRATEGIES:
• Education about vitamin D importance
• Universal infant supplementation programs
• Food fortification policies
• Addressing cultural practices that limit sun exposure`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS RICKETS?
Rickets is a disease that makes bones soft and weak. It happens when children do not get enough vitamin D and calcium. Vitamin D helps the body use calcium to make strong bones.

WHY DID MY CHILD GET RICKETS?
Your child may not have gotten enough:
• Sunlight (our skin makes vitamin D from sunlight)
• Vitamin D in food or supplements
• Calcium in food

Dark-skinned children need more sunlight to make vitamin D. Babies who only drink breast milk without vitamin D drops are at risk because breast milk has very little vitamin D.

WHAT ARE THE SIGNS OF RICKETS?
• Bowed legs or knock knees
• Swollen wrists or ankles
• Bumps on the chest (rachitic rosary)
• Soft skull in babies
• Delayed walking or sitting
• Muscle weakness
• Poor growth
• In severe cases: muscle spasms or seizures (fits)

HOW IS RICKETS TREATED?
• VITAMIN D medicine (drops or tablets)
• CALCIUM supplements or calcium-rich foods
• Treatment usually works within weeks to months
• Your child will need blood tests to check progress

It is very important to give the medicine exactly as prescribed. Do not stop without asking your doctor.

HOW TO PREVENT RICKETS:

1. VITAMIN D DROPS:
   • All breastfed babies need vitamin D drops (400 IU per day) from birth
   • Continue until your child eats vitamin D-rich foods or gets enough sun

2. SAFE SUNLIGHT:
   • Let your child play outside in sunlight
   • 10-30 minutes a few times a week
   • Face, hands, and arms should be in the sun
   • More time is needed for children with dark skin

3. CALCIUM-RICH FOODS:
   • Milk, cheese, yogurt
   • Small fish eaten with bones
   • Green leafy vegetables
   • Fortified cereals and foods

FOLLOW-UP IS IMPORTANT:
• Keep all clinic appointments
• Your child needs blood tests to check if treatment is working
• X-rays may be needed to see if bones are healing

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• What caused my child rickets?
• How long will treatment take?
• Will my child bones become normal?
• Do my other children need to be checked?
• How much vitamin D and calcium should my child take?
• When should we come back for follow-up?`,

    references: [
      'Munns CF, Shaw N, Kiely M, et al. Global Consensus Recommendations on Prevention and Management of Nutritional Rickets. J Clin Endocrinol Metab. 2016;101(2):394-415.',
      'Thacher TD, Fischer PR, Tebben PJ, et al. Increasing incidence of nutritional rickets: a population-based study in Olmsted County, Minnesota. Mayo Clin Proc. 2013;88(2):176-83.',
      'Pettifor JM. Calcium and vitamin D metabolism in children in developing countries. Ann Nutr Metab. 2014;64 Suppl 2:15-22.',
      'Thacher TD, Fischer PR, Strand MA, Pettifor JM. Nutritional rickets around the world: causes and future directions. Ann Trop Paediatr. 2006;26(1):1-16.',
      'Holick MF. Vitamin D deficiency. N Engl J Med. 2007;357(3):266-81.',
      'Wagner CL, Greer FR; American Academy of Pediatrics Section on Breastfeeding; American Academy of Pediatrics Committee on Nutrition. Prevention of rickets and vitamin D deficiency in infants, children, and adolescents. Pediatrics. 2008;122(5):1142-52.',
      'Misra M, Pacaud D, Petryk A, et al. Vitamin D deficiency in children and its management: review of current knowledge and recommendations. Pediatrics. 2008;122(2):398-417.',
      'Elder CJ, Bishop NJ. Rickets. Lancet. 2014;383(9929):1665-76.',
      'Thacher TD, Pludowski P, Shaw NJ, et al. Nutritional rickets in immigrant and refugee children. Public Health Rev. 2016;37:3.',
      'Uday S, Hogler W. Nutritional Rickets and Osteomalacia in the Twenty-first Century: Revised Concepts, Public Health, and Prevention Strategies. Curr Osteoporos Rep. 2017;15(4):293-302.',
      'Carpenter TO, Shaw NJ, Portale AA, et al. Rickets. Nat Rev Dis Primers. 2017;3:17101.',
      'Prentice A. Nutritional rickets around the world. J Steroid Biochem Mol Biol. 2013;136:201-6.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 49. IRON DEFICIENCY ANEMIA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '49',
    name: 'Iron Deficiency Anemia',
    slug: 'iron-deficiency-anemia',
    specialty: 'Pediatrics',
    bodySystem: 'Hematological',
    icdCode: 'D50.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Iron deficiency anemia (IDA) is the most common nutritional deficiency worldwide and the leading cause of anemia globally. It occurs when iron stores are depleted, leading to insufficient iron for hemoglobin synthesis and red blood cell production. Iron is essential not only for oxygen transport but also for neurodevelopment, immune function, and energy metabolism.

In children, iron deficiency is particularly concerning because it can cause irreversible cognitive and developmental impairment, even before anemia develops. The brain is highly vulnerable to iron deficiency during periods of rapid growth (infancy, adolescence).

Iron deficiency progresses through stages: (1) Iron depletion (low ferritin, normal hemoglobin), (2) Iron-deficient erythropoiesis (low transferrin saturation), (3) Iron deficiency anemia (low hemoglobin). Treatment aims to replenish iron stores, correct anemia, and address the underlying cause.

In Africa, iron deficiency is extremely prevalent due to dietary factors, parasitic infections (hookworm, schistosomiasis), malaria, and chronic infections. The interaction between iron, infection, and malaria is complex and requires careful consideration when implementing supplementation programs.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 IDENTIFY AND TREAT THE CAUSE: Iron deficiency is a symptom, not a diagnosis; find the underlying cause

🔴 FERRITIN is the BEST single test for iron stores: Low ferritin = iron deficiency; but elevated in infection/inflammation

🔴 ORAL IRON is first-line treatment: Ferrous sulfate 3-6 mg/kg/day of elemental iron; given between meals

🔴 TREAT FOR 3 MONTHS AFTER Hb normalizes: To replenish iron stores; stopping early leads to recurrence

🔴 RETICULOCYTE RESPONSE in 3-7 days: Best early indicator of response; Hb rises 1-2 g/dL per week

🔴 DEVELOPMENTAL EFFECTS may be IRREVERSIBLE: Early treatment and prevention are critical

🔴 DEWORM in endemic areas: Hookworm is major cause of IDA in Africa; treat before/with iron

🔴 MALARIA INTERACTION: In malaria-endemic areas, iron supplementation may increase malaria risk; ensure malaria prevention`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common nutritional deficiency worldwide
• Affects ~2 billion people globally
• Leading cause of anemia (responsible for ~50% of all anemia)
• Prevalence highest in young children and women of reproductive age
• Preschool children: 47% anemic globally (half due to iron deficiency)
• 40-50% of children 6-59 months in developing countries are anemic
• Contributes to 115,000 maternal deaths annually

AFRICAN EPIDEMIOLOGY:
• Anemia prevalence in children: 60-70% in many African countries
• Iron deficiency accounts for ~50% of anemia (other causes: malaria, infections, hemoglobinopathies)
• WHO Africa region has highest anemia prevalence globally
• Prevalence varies: 40-90% depending on country and population
• Major contributing factors in Africa:
  - Dietary iron deficiency (low meat intake, plant-based diets)
  - Hookworm infection (major cause of blood loss)
  - Schistosomiasis (urinary and intestinal blood loss)
  - Malaria (hemolysis, inflammation, bone marrow suppression)
  - Chronic infections (HIV, TB)
  - Hemoglobinopathies (sickle cell, thalassemia)
• Urban-rural differences: Often worse in rural areas
• Seasonal variation: May worsen during rainy/malaria season

RISK FACTORS:
Dietary:
• Exclusive breastfeeding beyond 6 months without complementary feeding
• Cow milk before 12 months (low iron, causes GI blood loss)
• Low dietary iron intake
• Low intake of iron absorption enhancers (vitamin C)
• High intake of iron absorption inhibitors (phytates, tannins)
• Vegetarian/vegan diet without supplementation

Increased requirements:
• Rapid growth (infancy, adolescence)
• Prematurity / Low birth weight
• Pregnancy

Blood loss:
• Hookworm infection
• Schistosomiasis
• Malaria
• GI bleeding (peptic ulcer, IBD, Meckel diverticulum)
• Heavy menstruation (adolescent girls)
• Frequent blood sampling (hospitalized infants)

Malabsorption:
• Celiac disease
• Inflammatory bowel disease
• H. pylori infection
• Gastric surgery

Other:
• Poverty
• Food insecurity
• Chronic infections
• Sickle cell disease (increased requirements)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

GENERAL SYMPTOMS:
• Fatigue and weakness (most common)
• Decreased energy and exercise tolerance
• Irritability
• Poor appetite
• Dizziness
• Headache
• Cold intolerance

CLASSIC SIGNS:

Skin and mucous membranes:
• Pallor (best assessed at conjunctivae, nail beds, palms, oral mucosa)
• Pallor of palmar creases (if Hb <7 g/dL)
• Pale tongue

Cardiovascular:
• Tachycardia
• Flow murmur (systolic ejection murmur)
• Cardiac enlargement (severe cases)
• High-output cardiac failure (severe chronic anemia)

Nails:
• Koilonychia (spoon-shaped nails) - late sign
• Brittle nails

Mouth:
• Angular stomatitis (cracks at corners of mouth)
• Glossitis (smooth, sore tongue)
• Atrophic glossitis

Hair:
• Dry, brittle hair
• Hair loss

PICA:
• Craving and eating non-food substances
• Pagophagia (ice)
• Geophagia (dirt, clay)
• Amylophagia (starch)
• Important to ask about - often not volunteered

NEURODEVELOPMENTAL EFFECTS (Critical in children):
• Developmental delay
• Cognitive impairment
• Poor school performance
• Attention deficits
• Behavioral problems
• Decreased motor development
• Effects may be IRREVERSIBLE even after treatment

PRESENTATION BY SEVERITY:

Mild anemia (Hb 10-11 g/dL):
• May be asymptomatic
• Mild fatigue
• Subtle pallor

Moderate anemia (Hb 7-10 g/dL):
• Noticeable pallor
• Fatigue with activity
• Tachycardia
• Irritability

Severe anemia (Hb <7 g/dL):
• Marked pallor (including palmar creases)
• Fatigue at rest
• Tachycardia, tachypnea
• Flow murmur
• May have signs of heart failure
• Lethargy

ASSOCIATED CONDITIONS TO LOOK FOR:
• Failure to thrive
• Recurrent infections (iron needed for immune function)
• Signs of underlying cause (e.g., worm infestation, malaria)`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CONFIRM ANEMIA
WHO Hemoglobin cutoffs for anemia (sea level):
• Children 6-59 months: <11.0 g/dL
• Children 5-11 years: <11.5 g/dL
• Children 12-14 years: <12.0 g/dL
• Males ≥15 years: <13.0 g/dL
• Non-pregnant females ≥15 years: <12.0 g/dL
• Pregnant women: <11.0 g/dL

Severity classification:
• Mild: Hb 10-10.9 g/dL (children)
• Moderate: Hb 7-9.9 g/dL
• Severe: Hb <7 g/dL

STEP 2: CONFIRM IRON DEFICIENCY

Complete Blood Count (CBC):
• Low hemoglobin and hematocrit
• Low MCV (microcytic): <70-80 fL in children
• Low MCH (hypochromic): <27 pg
• High RDW (anisocytosis): >14-15%
• Low reticulocyte count (initially)
• Thrombocytosis common (reactive)

Iron studies:
• FERRITIN: Best single test; <12-15 mcg/L diagnostic; <30 mcg/L suggests deficiency
  Note: Acute phase reactant - may be falsely normal/elevated in infection
• Serum Iron: Low (<60 mcg/dL)
• TIBC (Total Iron Binding Capacity): Elevated (>400 mcg/dL)
• Transferrin Saturation: Low (<16%)
• Soluble Transferrin Receptor (sTfR): Elevated (useful if inflammation present)

STEP 3: IDENTIFY THE CAUSE

History:
• Dietary history (milk intake, meat, vegetables)
• Blood loss symptoms (GI, menstrual)
• Pica
• Growth and development
• Previous anemia or iron treatment
• Family history (thalassemia, sickle cell)

Examination:
• Signs of chronic disease
• Abdominal examination (hepatosplenomegaly)
• Rectal examination if GI bleeding suspected

Additional investigations based on clinical suspicion:
• Stool for occult blood: GI bleeding
• Stool for ova and parasites: Hookworm, other parasites
• Malaria test: Endemic areas
• Hemoglobin electrophoresis: If MCV does not correct with iron; rule out thalassemia
• Celiac serology: If malabsorption suspected
• H. pylori testing: Refractory cases
• Endoscopy: If GI source suspected and not identified

STEP 4: THERAPEUTIC TRIAL
• In resource-limited settings, a therapeutic trial of iron is reasonable
• Response confirms diagnosis:
  - Reticulocyte count increases in 3-7 days
  - Hemoglobin rises 1-2 g/dL in 2-4 weeks
• No response → reconsider diagnosis or look for compliance/absorption issues

DIAGNOSTIC CRITERIA FOR IRON DEFICIENCY ANEMIA:
• Anemia (Hb below age-appropriate cutoff) PLUS
• Microcytic, hypochromic RBCs (low MCV, MCH) PLUS
• Low ferritin (<15 mcg/L) OR low transferrin saturation (<16%) PLUS
• Response to iron therapy`,

    differentialDiagnosis: [
      'Thalassemia trait (alpha or beta) - Microcytic but MCV very low for degree of anemia; normal RDW; ferritin normal; Hb electrophoresis diagnostic',
      'Anemia of chronic disease/inflammation - Chronic illness; ferritin normal or elevated; low serum iron; low TIBC; elevated inflammatory markers',
      'Sickle cell disease - Hemolysis; positive sickle screen; Hb electrophoresis shows HbS',
      'Lead poisoning - Basophilic stippling; elevated lead level; consider if pica present',
      'Sideroblastic anemia - Ring sideroblasts on bone marrow; variable MCV; may be inherited or acquired',
      'Copper deficiency - Very rare; neutropenia; neurological symptoms',
      'Vitamin B12 or folate deficiency - Macrocytic (high MCV); neurological symptoms (B12); hypersegmented neutrophils',
      'Hemolytic anemia - Reticulocytosis; elevated bilirubin and LDH; low haptoglobin',
      'Malaria - Endemic areas; fever; splenomegaly; positive blood smear/RDT',
      'Aplastic anemia - Pancytopenia; low reticulocytes; bone marrow hypocellular',
      'Chronic kidney disease - History of renal disease; elevated creatinine; normocytic usually',
      'Hypothyroidism - Fatigue; constipation; dry skin; elevated TSH'
    ],

    investigations: [
      {
        name: 'Hemoglobin / Hematocrit',
        indication: '🔴 ESSENTIAL - Confirms anemia; determines severity; monitors treatment response',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count (CBC)',
        indication: '🔴 ESSENTIAL - MCV (microcytic), MCH (hypochromic), RDW (elevated), platelets (often elevated)',
        availability: 'BASIC'
      },
      {
        name: 'Peripheral Blood Smear',
        indication: 'Microcytic hypochromic RBCs; anisocytosis; pencil cells; target cells; rule out other causes',
        availability: 'BASIC'
      },
      {
        name: 'Serum Ferritin',
        indication: '🔴 BEST SINGLE TEST for iron stores - <15 mcg/L diagnostic; but elevated in inflammation',
        availability: 'BASIC'
      },
      {
        name: 'Serum Iron',
        indication: 'Low in IDA; affected by recent intake; diurnal variation; less useful alone',
        availability: 'BASIC'
      },
      {
        name: 'Total Iron Binding Capacity (TIBC)',
        indication: 'Elevated in IDA (>400 mcg/dL); reflects transferrin; low in chronic disease',
        availability: 'BASIC'
      },
      {
        name: 'Transferrin Saturation',
        indication: 'Low in IDA (<16%); calculated as (serum iron/TIBC) x 100',
        availability: 'BASIC'
      },
      {
        name: 'Reticulocyte Count',
        indication: 'Monitor response to treatment - increases in 3-7 days; peak at 7-10 days',
        availability: 'BASIC'
      },
      {
        name: 'Stool for Ova and Parasites',
        indication: '🔴 ESSENTIAL in endemic areas - Hookworm major cause of IDA in Africa',
        availability: 'BASIC'
      },
      {
        name: 'Stool Occult Blood',
        indication: 'If GI bleeding suspected; chronic occult blood loss common cause',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Test (RDT/Smear)',
        indication: '🔴 In endemic areas - Malaria causes anemia; must be treated; affects iron supplementation decisions',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin Electrophoresis',
        indication: 'If MCV does not correct with iron; rule out thalassemia trait; screen for hemoglobinopathies',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Soluble Transferrin Receptor (sTfR)',
        indication: 'Elevated in IDA; useful when ferritin unreliable (inflammation); not widely available',
        availability: 'ADVANCED'
      },
      {
        name: 'Celiac Serology (tTG-IgA)',
        indication: 'If malabsorption suspected; refractory IDA; GI symptoms',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Upper/Lower GI Endoscopy',
        indication: 'If GI blood loss suspected and source not identified; especially in older children/adults',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Hb <5 g/dL or symptomatic severe anemia may need transfusion',
        '⚡ IF SEVERE SYMPTOMATIC ANEMIA: Transfuse packed RBCs slowly (5 mL/kg over 3-4 hours)',
        '⚡ CHECK FOR UNDERLYING CAUSE: Parasites, malaria, bleeding source',
        '⚡ TREAT CONCURRENT INFECTIONS: Malaria, hookworm, other infections',
        '⚡ ASSESS NUTRITIONAL STATUS: Often coexists with other deficiencies'
      ],
      pharmacological: [
        {
          drug: 'Ferrous Sulfate (Oral Iron)',
          indication: '🔴 FIRST-LINE TREATMENT - Most common oral iron preparation',
          dose: '3-6 mg/kg/day of ELEMENTAL iron divided in 2-3 doses. Ferrous sulfate is 20% elemental iron (e.g., 300mg tablet = 60mg elemental iron). Infants: 3 mg/kg/day. Children: 3-6 mg/kg/day (max 150-200 mg elemental iron/day).',
          route: 'PO',
          duration: 'Continue for 3 MONTHS after hemoglobin normalizes to replenish stores',
          contraindications: ['Iron overload', 'Hemochromatosis', 'Repeated transfusions', 'Active GI bleeding (relative)'],
          sideEffects: ['GI upset (nausea, constipation, diarrhea)', 'Dark stools', 'Abdominal pain', 'Teeth staining (liquid forms)'],
          alternatives: ['Ferrous gluconate', 'Ferrous fumarate', 'Iron polymaltose complex']
        },
        {
          drug: 'Ferrous Gluconate',
          indication: '💊 ALTERNATIVE oral iron - May be better tolerated than sulfate',
          dose: '3-6 mg/kg/day elemental iron. Ferrous gluconate is 12% elemental iron.',
          route: 'PO',
          duration: '3 months after Hb normalizes',
          contraindications: ['Iron overload'],
          sideEffects: ['GI upset (may be less than sulfate)', 'Dark stools'],
          alternatives: ['Ferrous sulfate', 'Ferrous fumarate']
        },
        {
          drug: 'Ferrous Fumarate',
          indication: '💊 ALTERNATIVE oral iron - Higher elemental iron content',
          dose: '3-6 mg/kg/day elemental iron. Ferrous fumarate is 33% elemental iron.',
          route: 'PO',
          duration: '3 months after Hb normalizes',
          contraindications: ['Iron overload'],
          sideEffects: ['GI upset', 'Constipation', 'Dark stools'],
          alternatives: ['Ferrous sulfate', 'Ferrous gluconate']
        },
        {
          drug: 'Iron Polymaltose Complex',
          indication: '💊 ALTERNATIVE - Better tolerated; can be taken with food; may have lower absorption',
          dose: '3-6 mg/kg/day elemental iron',
          route: 'PO',
          duration: '3 months after Hb normalizes',
          contraindications: ['Iron overload'],
          sideEffects: ['Generally better tolerated', 'Less GI upset than ferrous salts'],
          alternatives: ['Ferrous sulfate (more evidence)']
        },
        {
          drug: 'Vitamin C (Ascorbic Acid)',
          indication: '🍊 ENHANCES IRON ABSORPTION - Give with oral iron; especially if poor response',
          dose: '25-50 mg with each iron dose (or glass of orange juice)',
          route: 'PO',
          duration: 'With iron treatment',
          contraindications: ['None significant'],
          sideEffects: ['GI upset at high doses'],
          alternatives: ['Citrus juice', 'Other vitamin C-rich foods']
        },
        {
          drug: 'Parenteral Iron (IV Iron Sucrose, Ferric Carboxymaltose)',
          indication: '💉 ORAL IRON FAILURE or unable to tolerate - Malabsorption, non-compliance, severe anemia needing rapid correction',
          dose: 'Iron sucrose: Total dose calculated by formula. Ferric carboxymaltose: 15 mg/kg (max 750 mg) per infusion.',
          route: 'IV infusion',
          duration: 'Single or multiple infusions to replete stores',
          contraindications: ['Anaphylaxis to iron', 'First trimester pregnancy', 'Active infection (relative)'],
          sideEffects: ['Hypotension', 'Headache', 'Nausea', 'Anaphylaxis (rare)', 'Injection site reaction'],
          alternatives: ['Iron dextran (higher anaphylaxis risk)', 'Repeat oral trial']
        },
        {
          drug: 'Blood Transfusion (Packed RBCs)',
          indication: '🔴 SEVERE SYMPTOMATIC ANEMIA - Hb <5 g/dL OR hemodynamic instability OR severe symptoms',
          dose: '5-10 mL/kg packed RBCs; transfuse SLOWLY (over 3-4 hours) to avoid fluid overload',
          route: 'IV',
          duration: 'Until hemodynamically stable; usually single transfusion then oral iron',
          contraindications: ['Must crossmatch', 'Avoid if stable'],
          sideEffects: ['Transfusion reactions', 'Fluid overload', 'Iron overload (multiple transfusions)'],
          alternatives: ['Avoid if possible; oral/IV iron if stable']
        },
        {
          drug: 'Albendazole or Mebendazole',
          indication: '🪱 DEWORMING - Essential in endemic areas; treat hookworm and other helminths',
          dose: 'Albendazole: 400 mg single dose (200 mg if 12-24 months). Mebendazole: 500 mg single dose or 100 mg BD x 3 days.',
          route: 'PO',
          duration: 'Single dose; repeat every 6-12 months in endemic areas',
          contraindications: ['First trimester pregnancy (albendazole)', 'Age <12 months (caution)'],
          sideEffects: ['GI upset', 'Headache', 'Dizziness'],
          alternatives: ['Pyrantel pamoate']
        },
        {
          drug: 'Folic Acid',
          indication: '💊 ADJUNCT - Often given with iron; addresses common co-deficiency',
          dose: '1-5 mg/day',
          route: 'PO',
          duration: 'With iron treatment; especially if concurrent folate deficiency',
          contraindications: ['Untreated B12 deficiency (can mask)'],
          sideEffects: ['Rare'],
          alternatives: ['Dietary folate']
        },
        {
          drug: 'Antimalarials',
          indication: '🦟 TREAT MALARIA if present - Malaria must be treated; contributes to anemia',
          dose: 'Per malaria treatment guidelines (e.g., Artemether-lumefantrine)',
          route: 'PO',
          duration: 'As per malaria guidelines',
          contraindications: ['See malaria guidelines'],
          sideEffects: ['See malaria guidelines'],
          alternatives: ['As per national guidelines']
        }
      ],
      nonPharmacological: [
        'DIETARY COUNSELING: Increase iron-rich foods (meat, fish, poultry, beans, fortified cereals, dark green vegetables)',
        'ENHANCE ABSORPTION: Take iron with vitamin C (citrus); avoid taking with milk, tea, coffee, calcium',
        'TIMING OF IRON: Give between meals if tolerated (1 hour before or 2 hours after meals); with food if GI upset',
        'AVOID INHIBITORS: Tea, coffee, milk, calcium supplements, antacids reduce iron absorption',
        'LIQUID IRON: Use straw or dropper to back of mouth to prevent teeth staining; rinse mouth after',
        'COOKING PRACTICES: Cook in iron pots; increases iron content of food',
        'EXCLUSIVE BREASTFEEDING: Until 6 months; then introduce iron-rich complementary foods',
        'AVOID COW MILK <12 MONTHS: Low iron content; causes occult GI blood loss',
        'LIMIT COW MILK >12 MONTHS: Maximum 500 mL/day; excessive milk displaces iron-rich foods',
        'FOOD FORTIFICATION: Use iron-fortified cereals, flour, and foods where available'
      ],
      surgical: [
        'Treatment of underlying bleeding source if identified (e.g., Meckel diverticulum, polyp, vascular malformation)',
        'Rarely needed for iron deficiency itself'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Severe anemia with hemodynamic instability or heart failure',
        '🔴 IMMEDIATE REFERRAL: Hb <5 g/dL (may need transfusion)',
        '🟠 URGENT REFERRAL: No response to oral iron after 4-6 weeks (check compliance first)',
        '🟠 URGENT REFERRAL: Suspected malignancy or GI bleeding source needing investigation',
        '🟠 URGENT REFERRAL: Suspected thalassemia or other hemoglobinopathy',
        '🟡 ROUTINE REFERRAL: Recurrent iron deficiency despite treatment',
        '🟡 ROUTINE REFERRAL: Suspected malabsorption (celiac disease)',
        '🟡 ROUTINE REFERRAL: Need for IV iron therapy',
        '🟡 ROUTINE REFERRAL: Developmental concerns related to chronic anemia'
      ],
      monitoring: [
        'RETICULOCYTE COUNT: Check at 1 week; should increase (peak 7-10 days); best early response indicator',
        'HEMOGLOBIN: Check at 2-4 weeks; expect rise of 1-2 g/dL; then monthly until normal',
        'FERRITIN: Check after completing treatment to confirm stores replenished (target >30 mcg/L)',
        'COMPLIANCE: Ask about side effects; adherence is major issue',
        'CLINICAL IMPROVEMENT: Energy, appetite, pallor should improve within 1-2 weeks',
        'GROWTH AND DEVELOPMENT: Monitor catch-up growth; developmental milestones',
        'UNDERLYING CAUSE: Ensure treated; repeat stool if worms suspected'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

TREATMENT PHASE:
• 1 week: Reticulocyte count (should be rising)
• 2-4 weeks: Hemoglobin (expect 1-2 g/dL rise); assess tolerability
• Monthly: Hemoglobin until normalized
• Continue iron for 3 MONTHS after Hb normalizes

AFTER TREATMENT:
• Check ferritin to confirm stores replenished (>30 mcg/L)
• Dietary counseling reinforcement
• Address ongoing risk factors

NO RESPONSE TO ORAL IRON (After 4-6 weeks):
1. Check compliance (most common reason)
2. Review timing and absorption factors
3. Consider ongoing blood loss
4. Rule out malabsorption (celiac disease, H. pylori)
5. Reconsider diagnosis (thalassemia?)
6. Consider parenteral iron

PREVENTION OF RECURRENCE:
• Dietary modification
• Treat underlying cause
• Routine deworming (every 6 months in endemic areas)
• Iron supplementation in high-risk groups

HIGH-RISK GROUPS FOR ONGOING SUPPLEMENTATION:
• Preterm infants (iron 2 mg/kg/day from 1 month to 12 months)
• Low birth weight infants
• Infants in endemic areas (WHO: universal supplementation)
• Adolescent girls (menstrual losses)
• Pregnant women

DEVELOPMENTAL FOLLOW-UP:
• Children with prolonged severe IDA need developmental assessment
• Early intervention if delays identified
• Long-term follow-up as some effects may be irreversible`
    },

    redFlags: [
      '🔴 CRITICAL: Hb <5 g/dL - Severe anemia; may need transfusion; refer urgently',
      '🔴 CRITICAL: Signs of heart failure - Tachycardia, hepatomegaly, edema, respiratory distress',
      '🔴 CRITICAL: Hemodynamic instability - Hypotension, poor perfusion; needs urgent intervention',
      '🔴 CRITICAL: Active significant bleeding - GI hemorrhage; needs urgent investigation and transfusion',
      '🟠 URGENT: No response to iron after 4-6 weeks - Reassess diagnosis; check compliance; investigate',
      '🟠 URGENT: Rapidly worsening anemia - Consider ongoing blood loss; hemolysis; bone marrow problem',
      '🟠 URGENT: Neurological symptoms - May indicate severe B12 deficiency (different treatment needed)',
      '🟡 CAUTION: Very low MCV (<60 fL) not responding to iron - Consider thalassemia trait',
      '🟡 CAUTION: Pica persisting after treatment - May indicate ongoing deficiency or behavioral issue'
    ],

    complications: [
      'DEVELOPMENTAL DELAY: Cognitive and motor impairment; may be irreversible if prolonged deficiency in early childhood',
      'POOR SCHOOL PERFORMANCE: Attention deficits; learning difficulties',
      'GROWTH FAILURE: Inadequate iron affects growth',
      'INCREASED INFECTION RISK: Iron needed for immune function',
      'CARDIAC COMPLICATIONS: High-output heart failure in severe chronic anemia',
      'PICA: Eating non-food substances; may cause lead poisoning, parasites, GI problems',
      'EXERCISE INTOLERANCE: Reduced oxygen-carrying capacity',
      'BEHAVIORAL PROBLEMS: Irritability, attention issues',
      'RESTLESS LEG SYNDROME: Associated with iron deficiency',
      'PREGNANCY COMPLICATIONS (adolescents): Low birth weight, preterm delivery, maternal mortality'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

EXCELLENT PROGNOSIS WITH TREATMENT:
• Anemia corrects within 2-3 months of appropriate treatment
• Symptoms improve within 1-2 weeks
• Iron stores replenished with full treatment course
• Underlying cause addressed prevents recurrence

RESPONSE TO TREATMENT:
• Day 3-7: Reticulocyte count increases
• Week 1-2: Improved energy, appetite
• Week 2-4: Hemoglobin rises 1-2 g/dL
• Month 2-3: Hemoglobin normalizes
• Month 3-6: Iron stores replenished (ferritin normalizes)

CONCERNING FOR DEVELOPMENTAL OUTCOMES:
• Iron deficiency in infancy (6-24 months) during critical brain development
• Effects on cognition, attention, and motor development
• Some deficits may persist even after treatment
• Emphasizes importance of PREVENTION

FACTORS AFFECTING PROGNOSIS:

Better outcomes:
• Early detection and treatment
• Good compliance
• Underlying cause treated
• Adequate dietary iron intake maintained
• No other nutrient deficiencies

Worse outcomes:
• Prolonged severe deficiency (especially in infancy)
• Poor compliance
• Ongoing blood loss not addressed
• Recurrent deficiency
• Coexisting malnutrition
• Limited access to healthcare

RECURRENCE:
• Common if underlying cause not addressed
• Dietary habits not modified
• Insufficient treatment duration
• Ongoing risk factors (worms, heavy menses)
• Prevention strategies essential`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

DIETARY PREVENTION:

IRON-RICH FOODS:
• Heme iron (best absorbed): Red meat, poultry, fish, organ meats (liver)
• Non-heme iron: Beans, lentils, fortified cereals, dark green leafy vegetables, dried fruits
• Heme iron absorption: 15-35%
• Non-heme iron absorption: 2-20% (enhanced by vitamin C)

ENHANCE ABSORPTION:
• Eat vitamin C-rich foods with iron (citrus, tomatoes, peppers)
• Include some meat/fish with plant-based iron meals
• Cook in cast iron pots

REDUCE INHIBITORS:
• Avoid tea, coffee with meals (tannins inhibit absorption)
• Separate calcium supplements from iron-rich meals
• Reduce phytates (soaking, fermenting grains and legumes)

INFANT FEEDING:
• Exclusive breastfeeding for 6 months
• Iron-rich complementary foods from 6 months (iron-fortified cereal, pureed meats)
• Avoid cow milk before 12 months
• Limit cow milk to <500 mL/day after 12 months

SUPPLEMENTATION PROGRAMS:

WHO RECOMMENDATIONS FOR CHILDREN IN ENDEMIC AREAS:
• Children 6-23 months: 10-12.5 mg elemental iron daily
• Children 24-59 months: 30 mg elemental iron daily
• Intermittent (weekly) supplementation where daily not feasible
• Give with food; combine with deworming

PRETERM/LOW BIRTH WEIGHT INFANTS:
• Iron supplementation: 2 mg/kg/day from age 1 month to 12 months
• Essential to prevent deficiency

ADOLESCENT GIRLS:
• Weekly iron-folic acid supplementation in endemic areas
• 60 mg elemental iron + 2.8 mg folic acid weekly

PREGNANT WOMEN:
• Daily iron-folic acid: 30-60 mg iron + 400 mcg folic acid
• Throughout pregnancy and 3 months postpartum

PUBLIC HEALTH MEASURES:

FOOD FORTIFICATION:
• Iron-fortified flour, cereals, complementary foods
• Cost-effective population-level intervention

DEWORMING PROGRAMS:
• Mass drug administration in endemic areas
• School-based deworming programs
• Reduces hookworm-related iron loss

MALARIA CONTROL:
• Insecticide-treated bed nets
• Intermittent preventive treatment
• Reduces malaria-related anemia

DELAYED CORD CLAMPING:
• Wait 1-3 minutes before clamping umbilical cord
• Increases infant iron stores by 50%
• WHO recommendation for all births

SANITATION AND HYGIENE:
• Reduces soil-transmitted helminth transmission
• Clean water and sanitation

SCREENING:
• Screen high-risk populations (infants, pregnant women, adolescents)
• Treat subclinical deficiency before anemia develops`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PARENT AND CAREGIVER EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS IRON DEFICIENCY ANEMIA?
Iron deficiency anemia means your child does not have enough iron in their body to make healthy red blood cells. Red blood cells carry oxygen throughout the body. Without enough iron, your child may feel tired and weak.

WHY IS IRON IMPORTANT FOR CHILDREN?
Iron is needed for:
• Healthy blood to carry oxygen
• Brain development and learning
• Energy and growth
• Fighting infections

Iron deficiency can cause problems with learning and development, especially in young children. This is why prevention and early treatment are so important.

WHAT ARE THE SIGNS OF IRON DEFICIENCY?
• Pale skin, lips, or nail beds
• Tiredness and weakness
• Poor appetite
• Irritability
• Wanting to eat dirt, ice, or other non-food items (pica)
• Slow growth or development

HOW IS IT TREATED?
• IRON MEDICINE: Usually given by mouth (liquid or tablets)
• TREAT THE CAUSE: Worms, infections, or poor diet
• IMPROVE DIET: Eat more iron-rich foods

GIVING IRON MEDICINE:
• Give between meals if possible (1 hour before or 2 hours after eating)
• If stomach upset occurs, give with a small amount of food
• Give with orange juice or vitamin C to help absorption
• Do NOT give with milk, tea, or coffee (blocks absorption)
• Use a straw for liquid iron to prevent staining teeth
• Stools may turn dark - this is normal and harmless
• Continue medicine for 3 MONTHS after blood tests are normal

IRON-RICH FOODS FOR YOUR CHILD:
• Meat, chicken, fish (best sources)
• Liver and organ meats
• Beans, lentils, peas
• Dark green leafy vegetables
• Iron-fortified cereals and bread
• Eggs

FOODS THAT HELP IRON ABSORPTION:
• Oranges, lemons, tomatoes, peppers (vitamin C)
• Meat eaten with plant foods

FOODS TO LIMIT (they block iron absorption):
• Too much milk (maximum 2 cups/day for children over 1 year)
• Tea and coffee

FOR BABIES:
• Breastfeed for at least 6 months
• Start iron-rich foods at 6 months (iron-fortified cereal, mashed meat)
• Do not give cow milk before 12 months
• Give vitamin D drops as recommended

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding your child medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

If you think your child may have a medical emergency, call your doctor, go to the emergency department, or call emergency services IMMEDIATELY.

QUESTIONS TO ASK YOUR DOCTOR:
• What caused my child iron deficiency?
• What dose of iron should I give and for how long?
• What foods should my child eat more of?
• When should I bring my child back for blood tests?
• Does my child need treatment for worms?
• Will there be any long-term effects on my child development?`,

    references: [
      'World Health Organization. Iron deficiency anaemia: assessment, prevention and control. A guide for programme managers. Geneva: WHO; 2001.',
      'Baker RD, Greer FR; Committee on Nutrition American Academy of Pediatrics. Diagnosis and prevention of iron deficiency and iron-deficiency anemia in infants and young children (0-3 years of age). Pediatrics. 2010;126(5):1040-50.',
      'Camaschella C. Iron-Deficiency Anemia. N Engl J Med. 2015;372(19):1832-43.',
      'Kassebaum NJ, Jasrasaria R, Naghavi M, et al. A systematic analysis of global anemia burden from 1990 to 2010. Blood. 2014;123(5):615-24.',
      'World Health Organization. Guideline: Daily iron supplementation in infants and children. Geneva: WHO; 2016.',
      'Pasricha SR, Drakesmith H, Black J, et al. Control of iron deficiency anemia in low- and middle-income countries. Blood. 2013;121(14):2607-17.',
      'Lozoff B. Iron deficiency and child development. Food Nutr Bull. 2007;28(4 Suppl):S560-71.',
      'Sazawal S, Black RE, Ramsan M, et al. Effects of routine prophylactic supplementation with iron and folic acid on admission to hospital and mortality in preschool children in a high malaria transmission setting: community-based, randomised, placebo-controlled trial. Lancet. 2006;367(9505):133-43.',
      'Georgieff MK. Iron deficiency in pregnancy. Am J Obstet Gynecol. 2020;223(4):516-524.',
      'Powers JM, Buchanan GR. Diagnosis and management of iron deficiency anemia: a clinical update. Hematol Oncol Clin North Am. 2014;28(4):729-45.',
      'World Health Organization. Haemoglobin concentrations for the diagnosis of anaemia and assessment of severity. Geneva: WHO; 2011.',
      'Petry N, Olofin I, Hurrell RF, et al. The Proportion of Anemia Associated with Iron Deficiency in Low, Medium, and High Human Development Index Countries: A Systematic Analysis of National Surveys. Nutrients. 2016;8(11):693.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 50. SNAKE ENVENOMATION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '50',
    name: 'Snake Envenomation',
    slug: 'snake-envenomation',
    specialty: 'Emergency Medicine',
    bodySystem: 'Systemic',
    icdCode: 'T63.0',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Snake envenomation is a neglected tropical disease that causes significant morbidity and mortality, particularly in rural Africa, Asia, and Latin America. Globally, an estimated 5.4 million snakebites occur annually, with 1.8-2.7 million envenomations resulting in 81,000-138,000 deaths and 400,000 amputations or permanent disabilities.

Sub-Saharan Africa bears a disproportionate burden, with an estimated 1 million bites, 500,000 envenomations, and 20,000-30,000 deaths annually. Most victims are agricultural workers, children, and those in rural areas with limited access to healthcare and antivenom.

Snake venoms are complex mixtures of proteins and enzymes that cause diverse clinical syndromes depending on the snake species. The main clinical syndromes are:
• CYTOTOXIC: Local tissue destruction (vipers, some cobras)
• HEMOTOXIC: Coagulopathy, bleeding (vipers, boomslang)
• NEUROTOXIC: Paralysis, respiratory failure (mambas, cobras, kraits)
• MYOTOXIC: Rhabdomyolysis (sea snakes, some vipers)

Treatment requires prompt recognition, supportive care, and appropriate antivenom when indicated. The syndromic approach is essential when snake identification is uncertain.

⚠️ MEDICAL DISCLAIMER: This information is intended for healthcare professionals as a clinical decision support tool. It does not replace clinical judgment. All treatment decisions should be made by qualified physicians based on individual patient assessment. Always consult with appropriate specialists for complex cases.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 ABC FIRST: Airway (neurotoxic paralysis), Breathing, Circulation; be ready to intubate

🔴 IMMOBILIZE THE LIMB: Reduce movement; DO NOT incise, suck, or apply tourniquet

🔴 ANTIVENOM is the ONLY specific treatment: Give early if systemic envenomation; do not wait for severe symptoms

🔴 SYNDROMIC APPROACH: Identify clinical syndrome when snake not identified

🔴 20-MINUTE WHOLE BLOOD CLOTTING TEST (20WBCT): Simple bedside test for coagulopathy; repeat every 6 hours

🔴 WATCH FOR ANAPHYLAXIS: Antivenom reactions common; have epinephrine ready; premedicate if indicated

🔴 NEUROTOXICITY: Progressive paralysis; ptosis is early sign; respiratory failure can develop rapidly

🔴 COMPARTMENT SYNDROME: Monitor for tense swelling; fasciotomy rarely needed; antivenom first`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 5.4 million snakebites annually worldwide
• 1.8-2.7 million envenomations
• 81,000-138,000 deaths annually
• 400,000 amputations and permanent disabilities
• Highest burden in South Asia, Southeast Asia, and sub-Saharan Africa
• Agricultural workers at highest risk
• Peak incidence during rainy season and harvest time
• Most bites occur on lower limbs (walking) and upper limbs (farming)
• 50-70% of bites do not result in significant envenomation (dry bites)

AFRICAN EPIDEMIOLOGY:
• Estimated 1 million bites annually in sub-Saharan Africa
• 500,000 envenomations
• 20,000-30,000 deaths annually
• Case fatality rate: 5-10% where antivenom unavailable
• Highest mortality in West and Central Africa
• Limited antivenom availability (only 2-5% of need met)
• Rural populations most affected
• Children often bitten while playing, farming, or sleeping on floor
• Many deaths unreported (victims die before reaching hospital)

MAJOR VENOMOUS SNAKES IN AFRICA:

VIPERS (Viperidae):
• Puff adder (Bitis arietans): Most bites in Africa; cytotoxic/hemotoxic
• Saw-scaled/Carpet viper (Echis species): Major cause of mortality; severe coagulopathy
• Gaboon viper (Bitis gabonica): Massive fangs; severe local effects
• Horned vipers (Cerastes species): Northern Africa

ELAPIDS (Elapidae):
• Cobras (Naja species): Neurotoxic and cytotoxic; spitting cobras cause eye injury
• Black mamba (Dendroaspis polylepis): Highly neurotoxic; rapid progression
• Green mambas (Dendroaspis species): Neurotoxic
• Egyptian cobra (Naja haje): Common in North/East Africa

COLUBRIDS:
• Boomslang (Dispholidus typus): Potent hemotoxin; severe coagulopathy; rear-fanged
• Twig snake (Thelotornis species): Hemotoxic; similar to boomslang

SEASONAL PATTERNS:
• Peak during rainy season (snakes more active)
• Agricultural season (farmers at risk)
• Flooding drives snakes to higher ground near habitations

RISK FACTORS:
• Agricultural work
• Rural residence
• Walking barefoot
• Sleeping on floor
• Children playing outdoors
• Handling snakes
• Poor lighting at night`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLINICAL SYNDROMES OF ENVENOMATION:

1. LOCAL ENVENOMATION:
• Fang marks (one or two puncture wounds)
• Pain at bite site (immediate or delayed)
• Swelling (progressive; may extend up limb)
• Bruising and ecchymosis
• Blistering
• Tissue necrosis (especially vipers, spitting cobras)
• Lymphadenopathy (regional lymph nodes tender and enlarged)

2. CYTOTOXIC SYNDROME (Vipers, some Cobras):
• Severe local swelling and pain
• Tissue necrosis and blistering
• Compartment syndrome (rare)
• Secondary infection and gangrene
• May require debridement or amputation
• Common with: Puff adder, Gaboon viper, spitting cobras

3. HEMOTOXIC/COAGULOPATHY SYNDROME (Vipers, Boomslang):
• Bleeding from bite site (persistent oozing)
• Bleeding gums (spontaneous or with brushing)
• Epistaxis
• Hematemesis, melena
• Hematuria
• Intracranial hemorrhage (most dangerous)
• Prolonged clotting time (20WBCT positive)
• Thrombocytopenia
• Common with: Saw-scaled viper (Echis), Boomslang, Puff adder

4. NEUROTOXIC SYNDROME (Cobras, Mambas):
• Early signs: Ptosis (drooping eyelids) - EARLIEST SIGN
• Diplopia (double vision)
• Ophthalmoplegia (paralysis of eye muscles)
• Difficulty swallowing (bulbar paralysis)
• Dysarthria (slurred speech)
• Progressive descending paralysis
• Respiratory muscle weakness leading to respiratory failure
• Onset: Minutes to hours depending on species
• Common with: Black mamba, Green mamba, Cobras

5. MYOTOXIC SYNDROME (Sea snakes, some Vipers):
• Muscle pain and tenderness
• Trismus (difficulty opening mouth)
• Myoglobinuria (dark urine)
• Elevated CK
• Acute kidney injury from myoglobin
• Rare in African snakes

6. SYSTEMIC EFFECTS:
• Nausea, vomiting
• Abdominal pain
• Diarrhea
• Hypotension, shock
• Headache
• Drowsiness
• Convulsions (rare)

COBRA VENOM OPHTHALMIA (Spitting Cobras):
• Venom spat into eyes
• Intense pain
• Conjunctival injection
• Corneal ulceration if not treated
• Potential blindness
• IRRIGATE IMMEDIATELY with copious water

TIMING OF SYMPTOMS:
• Local effects: Usually within 30-60 minutes
• Neurotoxicity: Minutes to hours (mamba: may be <1 hour)
• Coagulopathy: May take 6-12 hours to manifest
• Swelling progression: Peaks at 24-72 hours`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: HISTORY
• Circumstances of bite (time, location, activity)
• Snake description (color, size, head shape) - often unreliable
• Dead snake brought (HANDLE CAREFULLY - can still envenomate)
• Time since bite (critical for management)
• First aid measures already applied
• Symptoms since bite (progression)
• Past medical history (allergies, previous snakebite)

STEP 2: PHYSICAL EXAMINATION
• Vital signs: BP (hypotension), HR (tachycardia), RR (respiratory compromise)
• Bite site: Fang marks, swelling (measure and mark), bruising, necrosis
• Mark extent of swelling with time noted
• Neurological: Ptosis, eye movements, swallowing, speech, limb power, respiratory effort
• Bleeding: Gums, venipuncture sites, spontaneous bruising
• Lymph nodes: Regional lymphadenopathy

STEP 3: 20-MINUTE WHOLE BLOOD CLOTTING TEST (20WBCT)

THE MOST IMPORTANT BEDSIDE TEST:
• Place 2-3 mL venous blood in clean, dry glass tube
• Leave undisturbed for 20 minutes at room temperature
• Tilt tube gently
• CLOTTED = Normal (no coagulopathy)
• NOT CLOTTED (remains liquid) = Coagulopathy present
• Repeat every 6 hours for at least 24 hours
• Positive 20WBCT indicates severe envenomation requiring antivenom

STEP 4: IDENTIFY CLINICAL SYNDROME
• COAGULOPATHY: Positive 20WBCT, bleeding = Hemotoxic
• PARALYSIS: Ptosis, weakness = Neurotoxic
• LOCAL DESTRUCTION: Severe swelling, necrosis = Cytotoxic
• COMBINATION: Some snakes cause multiple syndromes

STEP 5: LABORATORY INVESTIGATIONS (if available)

Essential:
• Hemoglobin/Hematocrit: Baseline; detect bleeding
• Platelet count: Thrombocytopenia in severe envenomation
• PT/INR, aPTT: Coagulopathy
• Fibrinogen: Low in consumptive coagulopathy
• Serum creatinine/BUN: Acute kidney injury

If available:
• D-dimer: Elevated in DIC
• FDP (Fibrin Degradation Products): Elevated
• Creatine Kinase (CK): Myotoxicity
• Urinalysis: Myoglobinuria, hematuria
• Blood group and crossmatch: If transfusion anticipated
• Arterial blood gas: Respiratory failure

STEP 6: ASSESS SEVERITY

MILD ENVENOMATION:
• Local pain and minimal swelling at bite site
• No systemic symptoms
• Normal 20WBCT
• Stable vital signs

MODERATE ENVENOMATION:
• Progressive local swelling extending beyond bite site
• Mild systemic symptoms (nausea, headache)
• OR early coagulopathy/mild bleeding
• OR early neurotoxicity (mild ptosis)

SEVERE ENVENOMATION:
• Extensive swelling (involving entire limb or trunk)
• Severe systemic symptoms
• Significant coagulopathy (positive 20WBCT, spontaneous bleeding)
• Neurotoxicity with respiratory compromise
• Hypotension/shock
• Acute kidney injury`,

    differentialDiagnosis: [
      'Non-venomous snakebite - Fang marks but no envenomation; local pain only; no progression',
      'Dry bite - Venomous snake but no venom injected (30-50% of bites); observe for 24 hours',
      'Insect bite/sting - May cause local reaction; different pattern; no systemic envenomation',
      'Scorpion sting - Pain, local reaction; autonomic symptoms; different syndrome',
      'Spider bite - Rare serious envenomation in Africa; local necrosis possible',
      'Cellulitis - Spreading erythema; may follow minor injury; no fang marks; no coagulopathy',
      'Necrotizing fasciitis - Rapidly spreading; severe pain; systemic toxicity; needs surgery',
      'Thrombocytopenia from other causes - Malaria, dengue, ITP; no bite history',
      'Coagulopathy from other causes - Liver disease, DIC from sepsis; no bite history',
      'Guillain-Barre syndrome - Ascending paralysis; no bite; may follow infection',
      'Myasthenia gravis - Ptosis, weakness; chronic/fluctuating; no bite',
      'Trauma - Crush injury; compartment syndrome; no fang marks'
    ],

    investigations: [
      {
        name: '20-Minute Whole Blood Clotting Test (20WBCT)',
        indication: '🔴 ESSENTIAL BEDSIDE TEST - Positive (unclotted) indicates coagulopathy; repeat every 6 hours; simple, free, highly sensitive',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin/Hematocrit',
        indication: '🔴 ESSENTIAL - Baseline; falling Hb indicates bleeding; repeat if bleeding suspected',
        availability: 'BASIC'
      },
      {
        name: 'Platelet Count',
        indication: 'Thrombocytopenia common with viper envenomation; may contribute to bleeding',
        availability: 'BASIC'
      },
      {
        name: 'PT/INR and aPTT',
        indication: 'Coagulopathy assessment; often prolonged in hemotoxic envenomation',
        availability: 'BASIC'
      },
      {
        name: 'Fibrinogen Level',
        indication: 'Low in venom-induced consumptive coagulopathy (VICC); may be undetectable',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Creatinine/BUN',
        indication: '🔴 ESSENTIAL - Acute kidney injury common (hypotension, myoglobinuria, direct nephrotoxicity)',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: 'Hematuria (bleeding); myoglobinuria (dark urine, positive for blood without RBCs)',
        availability: 'BASIC'
      },
      {
        name: 'Creatine Kinase (CK)',
        indication: 'Elevated in myotoxicity; rhabdomyolysis; indicates muscle damage',
        availability: 'BASIC'
      },
      {
        name: 'Blood Group and Crossmatch',
        indication: 'If significant bleeding or transfusion anticipated',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes (Na, K)',
        indication: 'Hyperkalemia from rhabdomyolysis or renal failure; hyponatremia',
        availability: 'BASIC'
      },
      {
        name: 'D-dimer / FDP',
        indication: 'Elevated in DIC and venom-induced consumptive coagulopathy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial Blood Gas',
        indication: 'Respiratory failure; metabolic acidosis; assess ventilation in neurotoxic syndrome',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: 'Hyperkalemia (peaked T waves); arrhythmias; some venoms are cardiotoxic',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'If respiratory compromise; aspiration; pulmonary edema',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ AIRWAY: Assess and secure; be ready to intubate if neurotoxic paralysis progressing',
        '⚡ BREATHING: Monitor respiratory rate and effort; have bag-valve-mask ready',
        '⚡ CIRCULATION: IV access (2 large bore); monitor BP; fluid resuscitation if hypotensive',
        '⚡ IMMOBILIZE: Keep patient and bitten limb still; movement increases venom spread',
        '⚡ REMOVE CONSTRICTIONS: Remove rings, bracelets, tight clothing before swelling worsens',
        '⚡ DO NOT: Incise, suck, apply tourniquet, apply ice, give aspirin',
        '⚡ PERFORM 20WBCT: Immediate bedside test for coagulopathy; repeat every 6 hours',
        '⚡ PAIN RELIEF: Paracetamol; avoid NSAIDs (increase bleeding); morphine if severe',
        '⚡ MARK SWELLING: Draw line around swelling edge with time; monitor progression',
        '⚡ PREPARE FOR ANTIVENOM: If systemic envenomation present or anticipated'
      ],
      pharmacological: [
        {
          drug: 'Polyvalent Snake Antivenom',
          indication: '🔴 ONLY SPECIFIC TREATMENT - Give for systemic envenomation (coagulopathy, neurotoxicity, severe local effects)',
          dose: 'Dose varies by product and severity. Typical initial dose: 1-5 vials (consult product insert). Give IV diluted in normal saline over 30-60 min. May need to repeat if symptoms progress or coagulopathy persists.',
          route: 'IV infusion (preferred) or slow IV push in emergency',
          duration: 'Repeat as needed; monitor response; 20WBCT should normalize within 6 hours',
          contraindications: ['No absolute contraindications if life-threatening envenomation; weigh risk-benefit'],
          sideEffects: ['Early anaphylactic reaction (urticaria, bronchospasm, hypotension)', 'Pyrogenic reaction', 'Late serum sickness (7-14 days)'],
          alternatives: ['Monovalent antivenom if species known and available']
        },
        {
          drug: 'Epinephrine (Adrenaline)',
          indication: '🔴 ANTIVENOM REACTION - Anaphylaxis; also give prophylactically (subcutaneous) in high-risk patients',
          dose: 'Anaphylaxis: 0.01 mg/kg IM (1:1000) - max 0.5 mg adult, 0.3 mg child. Prophylactic: 0.25-0.5 mg SC in adults before antivenom.',
          route: 'IM for anaphylaxis; SC for prophylaxis',
          duration: 'May repeat every 5-15 minutes if anaphylaxis persists',
          contraindications: ['Use with caution in cardiac disease; but no absolute contraindication in anaphylaxis'],
          sideEffects: ['Tachycardia', 'Hypertension', 'Anxiety', 'Arrhythmias'],
          alternatives: ['None for anaphylaxis']
        },
        {
          drug: 'Hydrocortisone',
          indication: '💊 ANTIVENOM PREMEDICATION and treatment of reactions - May reduce late serum sickness',
          dose: '100-200 mg IV (adults); 2-4 mg/kg IV (children)',
          route: 'IV',
          duration: 'Before antivenom; repeat if reaction occurs',
          contraindications: ['None significant in acute setting'],
          sideEffects: ['Hyperglycemia', 'Fluid retention'],
          alternatives: ['Dexamethasone', 'Methylprednisolone']
        },
        {
          drug: 'Promethazine or Chlorpheniramine',
          indication: '💊 ANTIVENOM PREMEDICATION - Antihistamine; may reduce mild reactions',
          dose: 'Promethazine: 0.25-0.5 mg/kg IV/IM (max 25 mg); Chlorpheniramine: 10 mg IV adult',
          route: 'IV or IM',
          duration: 'Before antivenom',
          contraindications: ['Caution in children (promethazine respiratory depression)'],
          sideEffects: ['Sedation', 'Anticholinergic effects'],
          alternatives: ['Diphenhydramine']
        },
        {
          drug: 'Neostigmine + Atropine',
          indication: '🧠 NEUROTOXIC ENVENOMATION - Anticholinesterase; may reverse post-synaptic neurotoxicity (cobra); trial worthwhile',
          dose: 'Atropine 0.6 mg IV first, then Neostigmine 0.5-2.5 mg IV/IM. Repeat atropine as needed for muscarinic effects.',
          route: 'IV or IM',
          duration: 'Repeat every 30-60 min if beneficial; continue until antivenom works',
          contraindications: ['Pre-synaptic neurotoxicity (mamba) - will not respond; but trial reasonable'],
          sideEffects: ['Muscarinic effects (bradycardia, salivation) - blocked by atropine'],
          alternatives: ['Edrophonium (shorter acting; diagnostic trial)']
        },
        {
          drug: 'Tetanus Prophylaxis',
          indication: '🛡️ ALL SNAKEBITES - Tetanus-prone wound; give TT or Tdap based on immunization status',
          dose: 'Tetanus toxoid 0.5 mL IM; add TIG if incomplete/unknown immunization',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Severe allergic reaction to previous dose'],
          sideEffects: ['Injection site pain', 'Mild fever'],
          alternatives: ['None']
        },
        {
          drug: 'Antibiotics',
          indication: '🦠 NOT ROUTINE - Only if signs of secondary infection; snake mouths have bacteria but infection rate low with proper wound care',
          dose: 'Amoxicillin-clavulanate or Clindamycin + Ciprofloxacin if infected',
          route: 'PO or IV',
          duration: 'Based on infection severity',
          contraindications: ['Known allergies'],
          sideEffects: ['Varies by antibiotic'],
          alternatives: ['Based on local resistance patterns']
        },
        {
          drug: 'Paracetamol',
          indication: '💊 PAIN RELIEF - Safe analgesic; avoid NSAIDs (increase bleeding)',
          dose: '15 mg/kg/dose every 4-6 hours (max 4g/day adult)',
          route: 'PO or IV',
          duration: 'As needed',
          contraindications: ['Hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)'],
          alternatives: ['Morphine/opioids for severe pain']
        },
        {
          drug: 'Morphine',
          indication: '💊 SEVERE PAIN - Opioid analgesia; use if paracetamol inadequate',
          dose: '0.1 mg/kg IV/IM/SC; titrate to effect',
          route: 'IV, IM, or SC',
          duration: 'As needed; monitor respiratory status',
          contraindications: ['Respiratory depression', 'Use with caution if neurotoxic envenomation'],
          sideEffects: ['Respiratory depression', 'Nausea', 'Sedation'],
          alternatives: ['Tramadol; Fentanyl']
        },
        {
          drug: 'Fresh Frozen Plasma / Cryoprecipitate',
          indication: '🩸 SEVERE BLEEDING with coagulopathy - Replaces clotting factors; use WITH antivenom',
          dose: 'FFP: 10-15 mL/kg; Cryoprecipitate: 1 unit per 5 kg',
          route: 'IV',
          duration: 'Until bleeding controlled and antivenom effective',
          contraindications: ['Without antivenom - clotting factors will be consumed again'],
          sideEffects: ['Transfusion reactions', 'Volume overload'],
          alternatives: ['Whole blood if components not available']
        },
        {
          drug: 'Packed Red Blood Cells',
          indication: '🩸 SIGNIFICANT BLEEDING with anemia - Hb <7 g/dL or symptomatic anemia',
          dose: '10-15 mL/kg',
          route: 'IV',
          duration: 'Until Hb stabilized',
          contraindications: ['Must crossmatch'],
          sideEffects: ['Transfusion reactions'],
          alternatives: ['Whole blood']
        },
        {
          drug: 'Intravenous Fluids (Normal Saline/Ringer Lactate)',
          indication: '💧 HYPOTENSION and fluid resuscitation - Maintain perfusion',
          dose: '20 mL/kg bolus; repeat as needed; then maintenance',
          route: 'IV',
          duration: 'Until hemodynamically stable',
          contraindications: ['Fluid overload'],
          sideEffects: ['Fluid overload', 'Dilutional coagulopathy if excessive'],
          alternatives: ['Colloids if available']
        }
      ],
      nonPharmacological: [
        'FIRST AID (Pre-Hospital): Reassure patient; immobilize bitten limb with splint at or below heart level; remove jewelry; transport to hospital urgently',
        'DO NOT: Incise or suck wound; apply tourniquet (tissue damage); apply ice or heat; give aspirin or NSAIDs; give alcohol; handle snake (can still bite)',
        'PRESSURE IMMOBILIZATION BANDAGE: ONLY for neurotoxic snakes (mambas, cobras) where rapid transport available; wrap firmly from bite site toward heart; NOT for cytotoxic snakes (vipers) as may worsen local necrosis',
        'WOUND CARE: Clean gently; do not debride early; tetanus prophylaxis; monitor for infection',
        'SPLINTING: Immobilize affected limb to reduce venom spread and pain',
        'ELEVATION: After initial period, elevate limb to reduce swelling (controversial early on)',
        'EYE IRRIGATION (Spitting Cobra): Copious irrigation with water or saline immediately; at least 15-20 minutes; local anesthetic drops if available; ophthalmology referral',
        'RESPIRATORY SUPPORT: Manual ventilation (bag-valve-mask) if respiratory failure; mechanical ventilation; may need prolonged ventilation (days) for neurotoxic envenomation',
        'MONITORING: Continuous observation; mark swelling progression; serial 20WBCT; neurological checks',
        'PSYCHOLOGICAL SUPPORT: Snakebite is terrifying; reassurance important; anxiety worsens symptoms'
      ],
      surgical: [
        'FASCIOTOMY: Rarely needed; ONLY if true compartment syndrome with documented elevated compartment pressures (>40 mmHg); antivenom should be given first as most swelling responds',
        'DEBRIDEMENT: Delayed until tissue demarcation clear (5-7 days); early debridement may remove viable tissue',
        'SKIN GRAFTING: For extensive tissue loss after debridement',
        'AMPUTATION: Last resort for extensive gangrene; usually due to delayed treatment'
      ],
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Any systemic envenomation (coagulopathy, neurotoxicity)',
        '🔴 IMMEDIATE REFERRAL: Respiratory compromise or progressive paralysis',
        '🔴 IMMEDIATE REFERRAL: Hypotension or shock',
        '🔴 IMMEDIATE REFERRAL: Extensive swelling involving entire limb',
        '🔴 IMMEDIATE REFERRAL: Confirmed venomous snakebite where antivenom needed but not available',
        '🔴 IMMEDIATE REFERRAL: Significant bleeding',
        '🟠 URGENT REFERRAL: Eye exposure to venom (spitting cobra) - ophthalmology',
        '🟠 URGENT REFERRAL: Suspected compartment syndrome',
        '🟠 URGENT REFERRAL: Tissue necrosis requiring surgical management',
        '🟡 ROUTINE REFERRAL: Follow-up for wound healing, rehabilitation'
      ],
      monitoring: [
        '20WBCT: Every 6 hours until normal and stable for 24 hours (coagulopathy can recur)',
        'VITAL SIGNS: Every 15-30 minutes initially; then hourly when stable',
        'SWELLING: Mark and measure every 4-6 hours; document progression or resolution',
        'NEUROLOGICAL: Ptosis, eye movements, speech, swallowing, limb power, respiratory effort',
        'RESPIRATORY: Rate, effort, oxygen saturation; be ready to ventilate',
        'URINE OUTPUT: Monitor for AKI; watch for myoglobinuria (dark urine)',
        'BLEEDING: Check all sites; gums, venipuncture sites, urine, stool',
        'HEMOGLOBIN: If bleeding; repeat every 6-12 hours',
        'RENAL FUNCTION: Creatinine daily if AKI risk',
        'ANTIVENOM RESPONSE: Coagulopathy should improve within 6 hours; neurotoxicity slower'
      ],
      followUp: `════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

HOSPITAL DISCHARGE CRITERIA:
• No systemic envenomation OR resolved
• 20WBCT normal and stable for 24 hours
• Swelling stable or decreasing
• No respiratory compromise
• No ongoing bleeding
• Pain controlled
• Wound clean, no signs of infection
• Patient/family educated on warning signs

POST-DISCHARGE FOLLOW-UP:
• Review in 2-3 days for wound check
• Weekly until wound healed
• Monitor for late serum sickness (7-14 days post-antivenom)

SERUM SICKNESS:
• Occurs 7-14 days after antivenom
• Fever, urticaria, arthralgia, lymphadenopathy
• Treatment: Antihistamines, corticosteroids (prednisolone 0.5-1 mg/kg for 5-7 days)

WOUND COMPLICATIONS:
• Secondary infection: Antibiotics, wound care
• Tissue necrosis: Delayed debridement when demarcated
• Chronic ulceration: May require skin grafting
• Contractures: Physiotherapy

REHABILITATION:
• Physiotherapy for limb function
• Occupational therapy if hand affected
• Psychological support (PTSD common)
• Prosthetics if amputation

PREVENTION EDUCATION:
• Wear boots and long trousers in snake areas
• Use torch/light at night
• Clear bush around dwellings
• Do not handle snakes
• Sleep on raised bed, not floor
• Seek immediate medical care for any snakebite

LONG-TERM COMPLICATIONS:
• Chronic pain
• Reduced limb function
• Chronic kidney disease
• Post-traumatic stress disorder
• Socioeconomic impact (disability, lost work)`
    },

    redFlags: [
      '🔴 CRITICAL: Respiratory difficulty/paralysis - Impending respiratory failure; prepare to intubate/ventilate',
      '🔴 CRITICAL: Ptosis progressing to bulbar weakness - Neurotoxicity advancing; antivenom + airway protection',
      '🔴 CRITICAL: Positive 20WBCT (blood not clotting) - Coagulopathy present; give antivenom; watch for bleeding',
      '🔴 CRITICAL: Spontaneous bleeding (gums, venipuncture sites) - Severe coagulopathy; urgent antivenom + blood products',
      '🔴 CRITICAL: Hypotension/shock - Massive envenomation; aggressive fluids; antivenom; consider vasopressors',
      '🔴 CRITICAL: Dark urine (myoglobinuria) - Rhabdomyolysis; aggressive fluids; monitor for AKI',
      '🔴 CRITICAL: Rapidly spreading swelling - Severe local envenomation; antivenom; mark progression',
      '🟠 URGENT: Swelling crossing major joint - Significant envenomation; consider antivenom',
      '🟠 URGENT: Tense swelling with severe pain - Possible compartment syndrome; measure pressures; antivenom first',
      '🟠 URGENT: Declining urine output - AKI developing; fluids; may need dialysis',
      '🟡 CAUTION: Venom in eyes (spitting cobra) - Irrigate immediately; ophthalmology referral'
    ],

    complications: [
      'DEATH: From respiratory failure (neurotoxic), intracranial hemorrhage (hemotoxic), or shock',
      'RESPIRATORY FAILURE: Neurotoxic paralysis; may require ventilation for days to weeks',
      'INTRACRANIAL HEMORRHAGE: Most dangerous complication of coagulopathy; often fatal',
      'ACUTE KIDNEY INJURY: From hypotension, myoglobinuria, direct nephrotoxicity; may need dialysis',
      'COMPARTMENT SYNDROME: Rare true compartment syndrome; often over-diagnosed; antivenom first',
      'TISSUE NECROSIS: Local venom effects; may require debridement, grafting',
      'SECONDARY INFECTION: Wound infection; cellulitis; osteomyelitis (rare)',
      'AMPUTATION: Extensive necrosis; usually due to delayed treatment or inappropriate first aid',
      'CHRONIC PAIN: Persistent pain at bite site',
      'CONTRACTURES: From scarring and tissue loss',
      'SERUM SICKNESS: Delayed reaction to antivenom (7-14 days)',
      'POST-TRAUMATIC STRESS DISORDER: Psychological sequelae common'
    ],

    prognosis: `════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• With appropriate antivenom and supportive care: Mortality <1-5%
• Without antivenom (severe envenomation): Mortality 5-15% or higher
• Many bites are dry bites (no envenomation): 30-50% - excellent prognosis

FACTORS AFFECTING PROGNOSIS:

Better prognosis:
• Early presentation (within 2-6 hours)
• Prompt antivenom administration
• Access to intensive care (ventilation if needed)
• Smaller amount of venom injected
• Bite on extremity (vs trunk, face)
• Healthy adult patient

Worse prognosis:
• Delayed presentation (>6-12 hours)
• Antivenom not available
• Large venom dose (multiple bites, large snake)
• Bite on trunk, face, or directly into blood vessel
• Children (higher venom dose per kg)
• Elderly or comorbid patients
• Limited access to supportive care

PROGNOSIS BY SYNDROME:

Neurotoxic (Mamba, Cobra):
• With antivenom and ventilatory support: Generally good recovery
• Paralysis may take days to weeks to resolve
• Without ventilation: High mortality from respiratory failure

Hemotoxic (Viper, Boomslang):
• With antivenom: Good recovery; coagulopathy resolves
• Intracranial hemorrhage: Often fatal
• Without antivenom: Risk of fatal bleeding

Cytotoxic (Puff Adder):
• Rarely fatal with supportive care
• Significant morbidity: Tissue loss, disability
• Long-term: Chronic pain, reduced function possible

LONG-TERM OUTCOMES:
• Complete recovery in most treated cases
• Permanent disability in 10-20% (tissue loss, amputation, chronic pain)
• Psychological impact significant
• Socioeconomic consequences (lost livelihood, medical costs)`,

    prevention: `════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PERSONAL PROTECTIVE MEASURES:

FOOTWEAR AND CLOTHING:
• Wear boots or thick shoes (not sandals) in snake-prone areas
• Wear long trousers tucked into boots
• Wear gloves when moving wood, stones, or debris

NIGHT PRECAUTIONS:
• Use torch/flashlight when walking at night
• Snakes are more active in warm evenings
• Clear pathways of debris

SLEEPING:
• Sleep on raised bed, not on floor
• Use mosquito net tucked under mattress (also protects from snakes)
• Shake out bedding and shoes before use

AVOID SNAKE CONTACT:
• Never handle snakes (even dead ones can envenomate)
• Do not try to kill or capture snakes
• Walk away slowly if snake encountered

ENVIRONMENTAL MODIFICATIONS:

AROUND DWELLINGS:
• Clear bush and long grass around homes
• Remove rubbish piles, wood stacks, rock piles (snake hiding places)
• Fill holes in ground and walls
• Store food properly (attracts rodents which attract snakes)

AGRICULTURAL PRACTICES:
• Be cautious when harvesting, especially at night
• Clear area before working
• Wear protective clothing

COMMUNITY MEASURES:
• Education about snakebite prevention and first aid
• Training healthcare workers in snakebite management
• Ensuring antivenom availability
• Community awareness campaigns

HEALTH SYSTEM STRENGTHENING:

ANTIVENOM:
• Increase availability of quality-assured antivenom
• Stock antivenom at district/rural hospitals
• Train healthcare workers in administration
• Reduce cost barriers

HEALTHCARE ACCESS:
• Strengthen referral systems
• Emergency transport availability
• Intensive care capacity

SURVEILLANCE:
• Improve snakebite reporting
• Map high-risk areas
• Monitor antivenom efficacy

RESEARCH:
• Develop improved antivenoms
• New therapeutics
• Better prevention strategies`,

    patientEducation: `════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT TO DO IF BITTEN BY A SNAKE:

IMMEDIATELY:
• Stay calm - panic makes venom spread faster
• Get away from the snake - do not try to catch or kill it
• Call for help
• Remove rings, watches, tight clothing (swelling will come)
• Keep still - do not run; movement spreads venom
• Immobilize the bitten limb (use a splint or sling)
• Get to hospital as fast as possible

DO NOT:
• Do NOT cut the wound
• Do NOT try to suck out venom
• Do NOT apply a tourniquet
• Do NOT apply ice or heat
• Do NOT drink alcohol
• Do NOT take aspirin (increases bleeding)
• Do NOT apply traditional medicines to wound

AT THE HOSPITAL:
• Tell the doctor when and where you were bitten
• Describe the snake if you saw it (but do not bring it)
• Tell them what first aid was done
• Report any symptoms (pain, swelling, weakness, bleeding)

WHAT TREATMENT WILL I RECEIVE?
• You will be closely monitored
• Blood tests will check if venom is affecting your blood
• If needed, you will receive antivenom (the medicine that neutralizes snake venom)
• You may need other medicines for pain, swelling, or to prevent infection
• Some people need help breathing if the venom affects muscles

WHAT ARE WARNING SIGNS OF SERIOUS ENVENOMATION?
• Trouble breathing or swallowing
• Drooping eyelids, double vision
• Bleeding from gums, nose, or bite wound that does not stop
• Dark or red urine
• Severe swelling spreading up the limb
• Feeling very weak or confused

HOW TO PREVENT SNAKEBITES:
• Wear boots and long trousers in the bush or at night
• Use a torch when walking at night
• Sleep on a raised bed, not on the ground
• Clear long grass and rubbish from around your home
• Shake out shoes and bedding before use
• Never handle snakes - even dead ones can bite

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for EDUCATIONAL PURPOSES ONLY and is NOT a substitute for professional medical advice, diagnosis, or treatment.

ALWAYS seek the advice of your physician or other qualified health provider with any questions you may have regarding a snakebite or any medical condition. NEVER disregard professional medical advice or delay in seeking it because of something you have read in this material.

Snakebite is a medical emergency. If you or someone is bitten by a snake, seek immediate medical care.

QUESTIONS TO ASK YOUR DOCTOR:
• What type of snake do you think bit me?
• Do I need antivenom?
• How long will I need to stay in hospital?
• What warning signs should I watch for after going home?
• Will there be any long-term effects?
• How can I prevent this from happening again?`,

    references: [
      'World Health Organization. Guidelines for the management of snakebites. 2nd ed. New Delhi: WHO Regional Office for South-East Asia; 2016.',
      'Warrell DA. Snake bite. Lancet. 2010;375(9708):77-88.',
      'Gutierrez JM, Calvete JJ, Habib AG, et al. Snakebite envenoming. Nat Rev Dis Primers. 2017;3:17063.',
      'Chippaux JP. Snakebite envenomation turns again into a neglected tropical disease! J Venom Anim Toxins Incl Trop Dis. 2017;23:38.',
      'Harrison RA, Hargreaves A, Wagstaff SC, et al. Snake envenoming: a disease of poverty. PLoS Negl Trop Dis. 2009;3(12):e569.',
      'Habib AG, Kuznik A, Hamza M, et al. Snakebite is Under Appreciated: Appraisal of Burden from West Africa. PLoS Negl Trop Dis. 2015;9(9):e0004088.',
      'Theakston RD, Warrell DA. Crisis in snake antivenom supply for Africa. Lancet. 2000;356(9247):2104.',
      'Alirol E, Sharma SK, Bawaskar HS, et al. Snake bite in South Asia: a review. PLoS Negl Trop Dis. 2010;4(1):e603.',
      'Chippaux JP. Estimate of the burden of snakebites in sub-Saharan Africa: a meta-analytic approach. Toxicon. 2011;57(4):586-99.',
      'White J. Snake venoms and coagulopathy. Toxicon. 2005;45(8):951-67.',
      'Silva A, Maduwage K, Sedgwick M, et al. Neurotoxicity in snakebite-the limits of our knowledge. PLoS Negl Trop Dis. 2017;11(2):e0005302.',
      'Williams DJ, Faiz MA, Abela-Ridder B, et al. Strategy for a globally coordinated response to a priority neglected tropical disease: Snakebite envenoming. PLoS Negl Trop Dis. 2019;13(2):e0007059.'
    ],
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 51. TETANUS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '51',
    name: 'Tetanus - Emergency Management',
    slug: 'tetanus-emergency-management',
    specialty: 'Emergency Medicine',
    bodySystem: 'Neurological',
    icdCode: 'A35',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `
Tetanus is an acute, life-threatening neurological disease caused by the neurotoxin tetanospasmin produced by the anaerobic bacterium Clostridium tetani. The toxin blocks inhibitory neurotransmitters in the central nervous system, leading to unopposed muscle contraction, severe spasms, and autonomic instability. Tetanus remains a significant public health problem in Africa due to inadequate vaccination coverage, unsafe delivery practices, and limited access to wound care.

The disease manifests in four clinical forms: generalized tetanus (most common, 80%), localized tetanus, cephalic tetanus, and neonatal tetanus. Neonatal tetanus, caused by umbilical stump infection from unclean delivery practices, remains a major cause of neonatal mortality in sub-Saharan Africa despite global elimination efforts.

Tetanus has a case fatality rate of 20-70% even with treatment, and up to 100% without treatment. Early recognition, aggressive supportive care, neutralization of circulating toxin, and elimination of the bacterial source are critical for survival.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 TETANUS IS A CLINICAL DIAGNOSIS - Do not delay treatment waiting for laboratory confirmation

🔴 HUMAN TETANUS IMMUNOGLOBULIN (HTIG) must be given EARLY - It only neutralizes CIRCULATING toxin, not toxin already bound to neurons

🔴 TRISMUS (lockjaw) is often the FIRST symptom - Ask about difficulty opening mouth or swallowing

🔴 SPASMS CAN CAUSE RESPIRATORY ARREST - Airway management is the priority; have emergency airway equipment ready

🔴 AUTONOMIC DYSFUNCTION is a major cause of death - Monitor for hypertension, tachycardia, arrhythmias, hyperthermia

🔴 NEONATAL TETANUS presents 3-14 days after birth - Poor feeding, inability to suck, and rigidity are early signs

🔴 QUIET, DARK ENVIRONMENT reduces stimulus-induced spasms - Minimize handling, noise, and bright lights

🔴 TETANUS DOES NOT CONFER IMMUNITY - Survivors must complete vaccination series
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Estimated 38,000-73,000 deaths annually worldwide (WHO 2019)
• Over 80% of cases occur in Africa and Southeast Asia
• Case fatality rate: 20-70% with treatment; nearly 100% without treatment
• Neonatal tetanus accounts for 5-7% of neonatal deaths in endemic areas
• Incubation period: 3-21 days (average 7-10 days); shorter incubation indicates worse prognosis

AFRICAN EPIDEMIOLOGY:
• Africa bears the highest burden of tetanus globally
• Estimated 10,000-15,000 neonatal tetanus deaths annually in sub-Saharan Africa
• Nigeria, Democratic Republic of Congo, and Ethiopia have highest case numbers
• Maternal and neonatal tetanus elimination (MNTE) not yet achieved in several African countries
• Adult tetanus commonly follows agricultural injuries, traditional practices, and road traffic accidents
• Traditional wound treatments (cow dung, soil, herbs) increase tetanus risk
• Only 50-70% of pregnant women in Africa receive adequate tetanus toxoid vaccination

RISK FACTORS:
• Incomplete or absent tetanus vaccination
• Contaminated wounds (especially puncture wounds, crush injuries, burns)
• Umbilical stump infection in neonates (unclean delivery, traditional cord care)
• Injection drug use
• Chronic wounds and ulcers (diabetic foot, pressure ulcers)
• Traditional surgical procedures (circumcision, scarification, ear piercing)
• Agricultural injuries (farming, animal husbandry)
• Road traffic accidents
• Post-surgical and post-abortion infections
• Advanced age (waning immunity)
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

GENERALIZED TETANUS (Most Common - 80%):

Early Symptoms (1-7 days):
• Trismus (lockjaw) - difficulty opening mouth, often the FIRST symptom
• Facial stiffness and risus sardonicus (sardonic smile from facial muscle spasm)
• Dysphagia (difficulty swallowing)
• Neck stiffness and pain
• Restlessness and irritability

Progressive Symptoms:
• Opisthotonus - severe arching of back due to paraspinal muscle spasm
• Board-like abdominal rigidity
• Generalized muscle rigidity affecting all limbs
• Reflex spasms triggered by stimuli (noise, light, touch)
• Painful muscle contractions lasting seconds to minutes
• Respiratory compromise from chest wall and diaphragm rigidity

Autonomic Dysfunction (usually appears after first week):
• Labile hypertension alternating with hypotension
• Tachycardia or bradycardia
• Profuse sweating
• Hyperthermia
• Cardiac arrhythmias
• Urinary retention

NEONATAL TETANUS:
• Onset: 3-14 days after birth (average day 6-8)
• Inability to suck or breastfeed (often first sign)
• Excessive crying followed by inability to cry
• Generalized rigidity and stiffness
• Opisthotonus
• Spasms triggered by touch or noise
• Trismus
• Umbilical stump may show signs of infection

LOCALIZED TETANUS:
• Rigidity confined to muscles near wound site
• May progress to generalized tetanus
• Better prognosis than generalized form

CEPHALIC TETANUS:
• Follows head/facial wounds or otitis media
• Cranial nerve palsies (especially VII)
• Trismus
• Dysphagia
• High risk of progression to generalized tetanus

PHYSICAL EXAMINATION:
• Trismus - inability to open mouth >4 cm (spatula test positive)
• Risus sardonicus - sustained contraction of facial muscles
• Nuchal rigidity
• Board-like abdominal rigidity
• Opisthotonus on stimulation
• Hyperreflexia
• Normal sensory examination (distinguishes from other causes)
• Clear consciousness (unless hypoxic)
• Examine wound site if identifiable
• In neonates: examine umbilical stump
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL RECOGNITION
Tetanus is a CLINICAL DIAGNOSIS based on characteristic signs:
• Trismus with risus sardonicus
• Generalized muscle rigidity
• Reflex spasms
• History of wound or risk factor (may be absent in up to 30%)
• Clear consciousness

STEP 2: SPATULA TEST (Highly Specific)
• Touch posterior pharyngeal wall with spatula
• POSITIVE (Tetanus): Jaw spasm causing patient to bite the spatula
• NEGATIVE (Normal): Gag reflex attempts to expel the spatula
• Sensitivity: 94%, Specificity: 100%

STEP 3: EXCLUDE DIFFERENTIAL DIAGNOSES
• Strychnine poisoning - history of ingestion, similar presentation
• Drug-induced dystonia - recent medication exposure
• Hypocalcemic tetany - carpopedal spasm, Chvostek sign, Trousseau sign
• Meningitis/Encephalitis - altered consciousness, fever, CSF abnormalities
• Rabies - history of animal bite, hydrophobia, aerophobia

STEP 4: SEVERITY GRADING (Ablett Classification)
Grade I (Mild): Mild trismus, general spasticity, no respiratory compromise, no spasms, no dysphagia
Grade II (Moderate): Moderate trismus, rigidity, mild-moderate spasms, mild respiratory compromise, mild dysphagia
Grade III (Severe): Severe trismus, generalized spasticity, reflex prolonged spasms, respiratory compromise, severe dysphagia, tachycardia >120/min
Grade IV (Very Severe): Grade III plus violent autonomic disturbances, severe hypertension, hypotension

DIAGNOSTIC CRITERIA FOR NEONATAL TETANUS (WHO):
• Neonate with normal ability to suck and cry during first 2 days of life
• Onset of illness between days 3-28 of life
• Inability to suck followed by stiffness and/or convulsions

LABORATORY FINDINGS (Supportive, Not Diagnostic):
• No specific diagnostic test available
• Wound culture: C. tetani isolated in <30% of cases
• CSF: Normal (helps exclude meningitis)
• Creatine kinase: May be elevated from muscle damage
• Leukocytosis: May be present
• Electrolytes: Exclude hypocalcemia
    `,

    differentialDiagnosis: [
      'Strychnine poisoning - Similar spasms, history of ingestion, no trismus initially, rapid onset',
      'Drug-induced dystonia (metoclopramide, phenothiazines) - Recent drug exposure, responds to anticholinergics',
      'Hypocalcemic tetany - Carpopedal spasm, Chvostek and Trousseau signs, low serum calcium',
      'Meningitis/Encephalitis - Altered consciousness, fever, CSF abnormalities, no trismus',
      'Rabies - Animal bite history, hydrophobia, aerophobia, altered consciousness',
      'Dental abscess/Peritonsillar abscess - Local findings, trismus but no generalized rigidity',
      'Temporomandibular joint disorders - Trismus without rigidity or spasms',
      'Seizure disorders - Loss of consciousness during episodes, EEG abnormalities',
      'Psychogenic disorders - Variable presentation, no true rigidity, normal spatula test',
      'Subarachnoid hemorrhage - Sudden headache, altered consciousness, bloody CSF',
      'Oropharyngeal infections - Local signs, fever, no generalized involvement',
      'Neuroleptic malignant syndrome - Recent antipsychotic use, hyperthermia, lead-pipe rigidity',
      'Malignant hyperthermia - Anesthesia exposure, hyperthermia, rhabdomyolysis',
      'Status epilepticus - Altered consciousness, EEG findings, responds to anticonvulsants',
      'Cerebral malaria (in neonates) - Parasitemia, altered consciousness, no trismus'
    ],

    investigations: [
      {
        name: 'Spatula Test',
        indication: '🔴 ESSENTIAL - Bedside diagnostic test; positive result highly specific for tetanus',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Baseline assessment; leukocytosis may indicate secondary infection',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Na, K, Ca, Mg)',
        indication: '🔴 ESSENTIAL - Exclude hypocalcemia as cause of tetany; monitor during treatment',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Hypoglycemia common in neonatal tetanus; monitor frequently',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and Urea',
        indication: '🟠 IMPORTANT - Baseline renal function; rhabdomyolysis may cause acute kidney injury',
        availability: 'BASIC'
      },
      {
        name: 'Creatine Kinase (CK)',
        indication: '🟠 IMPORTANT - Elevated from muscle spasms; monitor for rhabdomyolysis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 ESSENTIAL in severe cases - Monitor for respiratory failure and acidosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lumbar Puncture/CSF Analysis',
        indication: '🟠 IMPORTANT if meningitis suspected - Normal CSF in tetanus; helps exclude meningitis',
        availability: 'BASIC'
      },
      {
        name: 'Wound Culture',
        indication: '🟡 SUPPORTIVE - C. tetani isolated in <30% of cases; do not delay treatment for results',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 IMPORTANT - Assess for aspiration pneumonia; baseline for respiratory monitoring',
        availability: 'BASIC'
      },
      {
        name: 'ECG/Cardiac Monitor',
        indication: '🔴 ESSENTIAL in moderate-severe cases - Monitor for autonomic dysfunction and arrhythmias',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - Continuous monitoring for respiratory compromise',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: '🟠 IMPORTANT if sepsis suspected - Exclude concurrent bacteremia',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Lactate',
        indication: '🟠 IMPORTANT in severe cases - Indicator of tissue hypoperfusion',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ AIRWAY: Assess airway patency; prepare for emergency intubation if severe trismus or spasms',
        '⚡ BREATHING: Administer oxygen; monitor SpO2 continuously; have bag-valve-mask ready',
        '⚡ CIRCULATION: Establish IV access; start IV fluids (Normal Saline or Ringer Lactate)',
        '⚡ ENVIRONMENT: Move patient to quiet, dark, isolated room to minimize stimuli',
        '⚡ NEUTRALIZE TOXIN: Administer Human Tetanus Immunoglobulin (HTIG) IMMEDIATELY - Do not delay',
        '⚡ CONTROL SPASMS: Give Diazepam IV/Rectal for immediate spasm control',
        '⚡ WOUND CARE: Identify and debride wound if present (after HTIG administration)',
        '⚡ ANTIBIOTICS: Start Metronidazole to eliminate C. tetani organisms',
        '⚡ VACCINATION: Administer Tetanus Toxoid (TT) at different site from HTIG',
        '⚡ MONITORING: Continuous cardiac monitoring; prepare for autonomic instability'
      ],
      
      pharmacological: [
        {
          drug: 'Human Tetanus Immunoglobulin (HTIG)',
          indication: '🔴 CRITICAL - Neutralizes circulating (unbound) tetanospasmin toxin',
          dose: 'Adults: 3,000-6,000 IU IM as single dose (WHO recommends 500 IU). Neonates: 500 IU IM. Inject portion around wound if identified. Give at different site from Tetanus Toxoid.',
          route: 'IM (can give part intrathecally if available: 250 IU)',
          duration: 'Single dose',
          contraindications: ['Previous severe reaction to human immunoglobulin (rare)'],
          sideEffects: ['Injection site pain', 'Low-grade fever', 'Rare allergic reactions'],
          alternatives: ['Equine Tetanus Antitoxin (TAT) 10,000-20,000 IU if HTIG unavailable - requires skin testing for hypersensitivity']
        },
        {
          drug: 'Tetanus Toxoid (TT)',
          indication: '🔴 ESSENTIAL - Active immunization; tetanus does not confer natural immunity',
          dose: 'Adults and Children >7 years: 0.5 mL IM. Give first dose on admission, then complete primary series (3 doses at 0, 4 weeks, 6-12 months) after recovery.',
          route: 'IM',
          duration: 'Complete 3-dose primary series',
          contraindications: ['Severe allergic reaction to previous dose (rare)'],
          sideEffects: ['Injection site pain', 'Low-grade fever', 'Malaise'],
          alternatives: ['Td (Tetanus-Diphtheria) or Tdap if available']
        },
        {
          drug: 'Metronidazole',
          indication: '🔴 ESSENTIAL - First-line antibiotic to eliminate C. tetani and stop toxin production',
          dose: 'Adults: 500 mg IV/PO every 8 hours OR 1g every 12 hours. Children: 7.5 mg/kg (max 500 mg) every 8 hours. Neonates: 15 mg/kg loading, then 7.5 mg/kg every 12 hours.',
          route: 'IV preferred initially, then PO',
          duration: '7-10 days',
          contraindications: ['First trimester pregnancy (relative)', 'Known hypersensitivity'],
          sideEffects: ['Nausea', 'Metallic taste', 'Peripheral neuropathy with prolonged use', 'Disulfiram-like reaction with alcohol'],
          alternatives: ['Penicillin G 2-4 million units IV every 4-6 hours (less preferred - may enhance spasms via GABA antagonism)']
        },
        {
          drug: 'Diazepam',
          indication: '🔴 ESSENTIAL - First-line for spasm control and sedation',
          dose: 'Adults: 5-10 mg IV every 4-8 hours, titrate to spasm control; may need up to 120 mg/day. Severe cases: continuous infusion 5-15 mg/hour. Children: 0.1-0.3 mg/kg IV every 4-6 hours. Neonates: 0.1-0.3 mg/kg IV every 6-8 hours.',
          route: 'IV (preferred), Rectal if no IV access',
          duration: '2-6 weeks depending on severity',
          contraindications: ['Severe respiratory depression without ventilatory support', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Sedation', 'Paradoxical agitation'],
          alternatives: ['Midazolam 0.1-0.3 mg/kg IV bolus then 0.05-0.4 mg/kg/hour infusion', 'Lorazepam 0.1 mg/kg IV every 6-8 hours']
        },
        {
          drug: 'Midazolam',
          indication: '🔴 ALTERNATIVE - Shorter-acting benzodiazepine; useful for continuous infusion',
          dose: 'Adults: 0.1-0.2 mg/kg IV bolus, then 0.05-0.4 mg/kg/hour continuous infusion. Children: 0.15 mg/kg bolus, then 0.06-0.12 mg/kg/hour. Titrate to spasm control.',
          route: 'IV continuous infusion',
          duration: '2-6 weeks',
          contraindications: ['Severe respiratory depression without ventilatory support'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Sedation'],
          alternatives: ['Diazepam', 'Lorazepam']
        },
        {
          drug: 'Magnesium Sulfate',
          indication: '🟠 IMPORTANT - Adjunct for spasm control and autonomic dysfunction; reduces catecholamine release',
          dose: 'Adults: Loading 5g IV over 20 minutes, then 2-3 g/hour continuous infusion. Target serum Mg: 2-4 mmol/L. Monitor patellar reflexes (absent if toxic) and respiratory rate.',
          route: 'IV continuous infusion',
          duration: 'Continue until spasms controlled',
          contraindications: ['Myasthenia gravis', 'Severe renal impairment', 'Heart block'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Loss of deep tendon reflexes', 'Flushing'],
          alternatives: ['Increase benzodiazepine dose']
        },
        {
          drug: 'Baclofen',
          indication: '🟡 ADJUNCT - GABA-B agonist for refractory spasms; intrathecal route preferred if available',
          dose: 'Oral: 5-10 mg TID, increase to max 80 mg/day. Intrathecal: 500-2000 mcg/day via pump if available.',
          route: 'PO or Intrathecal',
          duration: 'Until spasms controlled',
          contraindications: ['Do not stop abruptly - taper to avoid withdrawal'],
          sideEffects: ['Sedation', 'Weakness', 'Confusion', 'Withdrawal syndrome if stopped abruptly'],
          alternatives: ['Dantrolene']
        },
        {
          drug: 'Morphine',
          indication: '🟠 IMPORTANT - Analgesia and sedation; useful for autonomic dysfunction',
          dose: 'Adults: 2-5 mg IV every 2-4 hours as needed. Children: 0.05-0.1 mg/kg IV every 4 hours. Titrate to comfort.',
          route: 'IV',
          duration: 'As needed for pain control',
          contraindications: ['Respiratory depression without ventilatory support', 'Acute severe asthma'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Constipation', 'Nausea'],
          alternatives: ['Fentanyl 25-50 mcg IV bolus then 50-200 mcg/hour infusion']
        },
        {
          drug: 'Propofol',
          indication: '🟠 FOR ICU - Sedation for mechanically ventilated patients with refractory spasms',
          dose: 'Adults: 1-3 mg/kg/hour continuous infusion. Not recommended in children (risk of propofol infusion syndrome).',
          route: 'IV continuous infusion',
          duration: 'Duration of mechanical ventilation',
          contraindications: ['Egg or soy allergy', 'Children (propofol infusion syndrome)', 'Hemodynamic instability'],
          sideEffects: ['Hypotension', 'Hypertriglyceridemia', 'Propofol infusion syndrome (rare)'],
          alternatives: ['Midazolam infusion']
        },
        {
          drug: 'Vecuronium/Rocuronium',
          indication: '🔴 FOR ICU - Neuromuscular blockade for refractory spasms in ventilated patients',
          dose: 'Vecuronium: 0.08-0.1 mg/kg bolus then 0.05-0.1 mg/kg/hour infusion. Rocuronium: 0.6 mg/kg bolus then 0.3-0.6 mg/kg/hour. Requires mechanical ventilation and continuous sedation.',
          route: 'IV',
          duration: 'Until spasms controlled (may need 2-3 weeks)',
          contraindications: ['Without mechanical ventilation', 'Without adequate sedation/analgesia'],
          sideEffects: ['Prolonged paralysis', 'ICU-acquired weakness', 'Awareness (ensure adequate sedation)'],
          alternatives: ['Pancuronium (longer acting)']
        },
        {
          drug: 'Labetalol',
          indication: '🟠 IMPORTANT - Control of hypertension and tachycardia from autonomic dysfunction',
          dose: 'Adults: 10-20 mg IV bolus, repeat every 10 minutes; max 300 mg. Or infusion 0.5-2 mg/min.',
          route: 'IV',
          duration: 'As needed for autonomic instability',
          contraindications: ['Severe bradycardia', 'Heart block', 'Severe asthma', 'Decompensated heart failure'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Bronchospasm'],
          alternatives: ['Esmolol infusion 50-200 mcg/kg/min', 'Magnesium sulfate']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 IMPORTANT - Antipyretic for hyperthermia; avoid NSAIDs',
          dose: 'Adults: 1g IV/PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours. Neonates: 10 mg/kg every 6-8 hours.',
          route: 'IV or PO',
          duration: 'As needed',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity with overdose'],
          alternatives: ['Physical cooling measures']
        }
      ],
      
      nonPharmacological: [
        'QUIET, DARK ENVIRONMENT: Essential - reduce all external stimuli; dim lights, minimize noise, limit visitors, handle patient gently',
        'AIRWAY MANAGEMENT: Early elective intubation for Grade III-IV tetanus; tracheostomy if prolonged ventilation expected (>10 days)',
        'WOUND DEBRIDEMENT: After HTIG administration, thoroughly debride wound to remove necrotic tissue and bacterial spores; leave wound open',
        'NUTRITIONAL SUPPORT: Early enteral feeding via nasogastric tube; high-calorie, high-protein diet; 40-50 kcal/kg/day',
        'DVT PROPHYLAXIS: Mechanical (compression stockings, pneumatic devices) and pharmacological (LMWH) in immobilized adults',
        'PRESSURE ULCER PREVENTION: Regular repositioning (every 2 hours), pressure-relieving mattress, skin care',
        'PHYSIOTHERAPY: Passive range of motion exercises during recovery to prevent contractures',
        'TRACHEOSTOMY CARE: Meticulous care if tracheostomy performed; regular suctioning, stoma care',
        'TEMPERATURE CONTROL: Physical cooling measures for hyperthermia (cooling blankets, tepid sponging)',
        'FLUID AND ELECTROLYTE MANAGEMENT: Monitor intake/output; correct electrolyte abnormalities; watch for rhabdomyolysis',
        'STRESS ULCER PROPHYLAXIS: H2 blockers or PPI for critically ill patients',
        'PSYCHOLOGICAL SUPPORT: Patients are often conscious during spasms - provide reassurance; consider anxiolytics'
      ],
      
      surgical: [
        'WOUND DEBRIDEMENT: Surgical excision of necrotic tissue and removal of foreign material after HTIG administration',
        'TRACHEOSTOMY: For patients requiring prolonged mechanical ventilation (>10-14 days expected); reduces sedation needs',
        'GASTROSTOMY/JEJUNOSTOMY: For long-term enteral nutrition if prolonged course expected',
        'UMBILICAL STUMP EXCISION: In neonatal tetanus, excision of infected umbilical stump after HTIG administration'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL/ICU ADMISSION: Any patient with tetanus - requires intensive monitoring',
        '🔴 IMMEDIATE REFERRAL: Respiratory compromise - SpO2 <92%, respiratory distress, severe spasms affecting breathing',
        '🔴 IMMEDIATE REFERRAL: Grade III or IV tetanus - requires mechanical ventilation capability',
        '🔴 IMMEDIATE REFERRAL: Neonatal tetanus - high mortality, requires specialized care',
        '🔴 IMMEDIATE REFERRAL: Autonomic dysfunction - labile BP, severe tachycardia, arrhythmias',
        '🔴 IMMEDIATE REFERRAL: Need for neuromuscular blockade - requires ICU with ventilator',
        '🟠 URGENT REFERRAL: Inability to control spasms with available medications',
        '🟠 URGENT REFERRAL: Progressive symptoms despite treatment',
        '🟠 URGENT REFERRAL: Aspiration pneumonia requiring advanced respiratory support',
        '🟡 REFERRAL TO HIGHER CENTER: Need for tracheostomy if not available locally',
        '🟡 REFERRAL: Tetanus in pregnancy - multidisciplinary care required',
        '🟡 POST-RECOVERY REFERRAL: Vaccination clinic to complete immunization series'
      ],
      
      monitoring: [
        'CONTINUOUS: Cardiac monitoring for arrhythmias and autonomic dysfunction',
        'CONTINUOUS: Pulse oximetry for oxygen saturation',
        'HOURLY: Blood pressure (watch for labile BP in autonomic dysfunction)',
        'HOURLY: Respiratory rate and pattern',
        'HOURLY: Level of consciousness and spasm frequency/severity',
        'EVERY 2-4 HOURS: Temperature monitoring',
        'EVERY 4 HOURS: Blood glucose (especially neonates)',
        'DAILY: Urine output and fluid balance',
        'DAILY: Electrolytes (Na, K, Ca, Mg) - especially if on magnesium infusion',
        'DAILY: Renal function (creatinine) - watch for rhabdomyolysis-induced AKI',
        'AS NEEDED: Creatine kinase if rhabdomyolysis suspected',
        'AS NEEDED: Arterial blood gas if respiratory compromise',
        'WHEN ON MAGNESIUM: Patellar reflexes every 2-4 hours (absent = toxicity)'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

HOSPITAL DISCHARGE CRITERIA:
• No spasms for at least 48-72 hours off sedation
• Able to swallow safely without aspiration risk
• Adequate oral intake
• Mobile with physiotherapy support
• Completed or scheduled for vaccination series

VACCINATION SCHEDULE (Critical - No Natural Immunity):
• Dose 1: Given during hospitalization
• Dose 2: 4 weeks after first dose
• Dose 3: 6-12 months after second dose
• Booster: Every 10 years thereafter

SHORT-TERM FOLLOW-UP (2-4 weeks post-discharge):
• Assess wound healing
• Evaluate for post-tetanus syndrome (prolonged weakness, stiffness)
• Confirm second vaccine dose administered
• Screen for depression and PTSD (common after ICU stay)
• Physiotherapy progress if contractures present

MEDIUM-TERM FOLLOW-UP (3-6 months):
• Complete vaccination series (third dose)
• Assess functional recovery
• Evaluate for persistent neurological sequelae
• Psychological support if needed

LONG-TERM CONSIDERATIONS:
• Tetanus booster every 10 years for life
• Patient education about wound care and tetanus prevention
• Family vaccination status review
• Counsel on avoiding traditional wound treatments

REHABILITATION:
• Physiotherapy for persistent muscle weakness or contractures
• Occupational therapy for functional limitations
• Speech therapy if prolonged intubation affected swallowing
• Psychological support for ICU-related trauma
      `
    },

    redFlags: [
      '🔴 CRITICAL: Respiratory failure - SpO2 <90%, cyanosis, inability to speak, severe chest wall rigidity - IMMEDIATE INTUBATION',
      '🔴 CRITICAL: Laryngospasm or severe trismus preventing airway management - EMERGENCY SURGICAL AIRWAY',
      '🔴 CRITICAL: Status dystonicus (continuous severe spasms) - Risk of rhabdomyolysis and cardiac arrest',
      '🔴 CRITICAL: Autonomic storm - Severe hypertension >180/120, tachycardia >140, hyperthermia >40C',
      '🔴 CRITICAL: Cardiac arrhythmias - Bradycardia, tachyarrhythmias, asystole',
      '🔴 CRITICAL: Cardiac arrest - More common in severe autonomic dysfunction',
      '🟠 URGENT: Aspiration - Coughing during feeding, desaturation, new respiratory symptoms',
      '🟠 URGENT: Short incubation period (<7 days) or onset period (<48 hours) - Indicates severe disease',
      '🟠 URGENT: Increasing spasm frequency despite maximum benzodiazepines',
      '🟠 URGENT: Rhabdomyolysis - Dark urine, elevated CK, rising creatinine',
      '🟠 URGENT: Hyperthermia unresponsive to antipyretics',
      '🟡 CAUTION: Nosocomial infection signs - New fever, leukocytosis (pneumonia, UTI, line sepsis)',
      '🟡 CAUTION: Deep vein thrombosis - Leg swelling, pain in immobilized patient'
    ],

    complications: [
      'Respiratory failure - Most common cause of death; from spasms, aspiration, or pneumonia',
      'Aspiration pneumonia - Due to dysphagia and impaired airway reflexes',
      'Laryngospasm - Sudden airway obstruction from vocal cord spasm',
      'Rhabdomyolysis - From severe sustained muscle contractions; can cause acute kidney injury',
      'Autonomic dysfunction - Hypertension, hypotension, tachycardia, arrhythmias, sudden death',
      'Cardiac arrest - From autonomic instability or arrhythmias',
      'Vertebral fractures - From severe opisthotonus and muscle spasms',
      'Tendon ruptures and muscle tears - From forceful contractions',
      'Pulmonary embolism - From prolonged immobilization',
      'Pressure ulcers - From prolonged bed rest and rigidity',
      'Nosocomial infections - Ventilator-associated pneumonia, catheter-related infections',
      'Contractures - From prolonged muscle spasms; may require rehabilitation',
      'Post-tetanus syndrome - Prolonged myalgia, weakness, and stiffness during recovery',
      'Psychological sequelae - PTSD, anxiety, depression after ICU stay with awareness during paralysis'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Case fatality rate: 10-70% depending on severity and available care
• With modern ICU care: 10-20% mortality
• Without ICU care: 50-70% mortality
• Neonatal tetanus: 70-100% mortality in resource-limited settings; 10-30% with optimal care
• Recovery period: 2-6 weeks typically; may take months in severe cases
• Full recovery possible in most survivors with good care

PROGNOSTIC FACTORS - POOR OUTCOME:
• Extremes of age (neonates, elderly >60 years)
• Short incubation period (<7 days from injury to first symptom)
• Short onset period (<48 hours from first symptom to first spasm)
• High disease severity (Ablett Grade III-IV)
• Presence of autonomic dysfunction
• Delayed presentation and treatment
• Cephalic tetanus (high mortality)
• Injecting drug use as source
• Limited access to ICU and mechanical ventilation
• Comorbidities (diabetes, HIV, malnutrition)

PROGNOSTIC FACTORS - BETTER OUTCOME:
• Younger adults
• Longer incubation period (>10 days)
• Localized tetanus
• Early recognition and treatment
• Access to ICU care and mechanical ventilation
• Complete vaccination after recovery (prevents recurrence)

SCORING SYSTEMS:
Tetanus Severity Score (Dakar Score):
• Incubation period <7 days (1 point)
• Onset period <2 days (1 point)
• Entry: umbilicus, uterus, burn, surgical, IM injection (1 point)
• Spasms present (1 point)
• Fever >38.4C (1 point)
• Heart rate >120 adult / >150 neonate (1 point)
Score 0-1: Mild (mortality <10%)
Score 2-3: Moderate (mortality 10-20%)
Score 4: Severe (mortality 20-40%)
Score 5-6: Very severe (mortality >50%)

LONG-TERM OUTCOMES:
• Most survivors recover completely
• Some may have prolonged weakness, stiffness, or contractures
• No lasting immunity - re-infection possible if not vaccinated
• Psychological effects common (PTSD, anxiety from ICU experience)
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION - VACCINATION:

CHILDHOOD IMMUNIZATION (WHO EPI Schedule):
• DTP/Pentavalent at 6, 10, 14 weeks of age
• DTP booster at 12-23 months
• Td/Tdap booster at 4-7 years
• Td booster at 9-15 years
• Td booster every 10 years thereafter

MATERNAL TETANUS IMMUNIZATION (Critical for MNTE):
• At least 2 doses of TT/Td during pregnancy
• Protects mother and provides passive immunity to newborn
• Five-dose schedule for lifetime protection:
  - TT1: First contact or early pregnancy
  - TT2: 4 weeks after TT1
  - TT3: 6 months after TT2 or next pregnancy
  - TT4: 1 year after TT3 or next pregnancy
  - TT5: 1 year after TT4 or next pregnancy

WOUND MANAGEMENT AND PROPHYLAXIS:

Clean Minor Wounds:
• History of 3+ TT doses and <10 years since last: No prophylaxis needed
• History of 3+ TT doses and >10 years since last: Give TT booster
• Unknown or <3 TT doses: Give TT and complete series

Tetanus-Prone Wounds (contaminated, puncture, crush, burns, frostbite, >6 hours old):
• History of 3+ TT doses and <5 years since last: No prophylaxis needed
• History of 3+ TT doses and >5 years since last: Give TT booster
• Unknown or <3 TT doses: Give TT AND HTIG (at different sites)

CLEAN DELIVERY PRACTICES (Prevent Neonatal Tetanus):
• Skilled birth attendant at delivery
• Clean delivery kits (6 Cleans): Clean hands, clean delivery surface, clean cord cutting instrument, clean cord tie, clean cord care, clean wrapping
• Sterile cutting of umbilical cord
• Avoid applying traditional substances to cord stump (cow dung, ash, mud, herbal preparations)
• Chlorhexidine for umbilical cord care where home deliveries common (WHO recommendation)

PUBLIC HEALTH MEASURES:
• Strengthen routine immunization programs
• Maternal TT vaccination campaigns
• Train traditional birth attendants on clean delivery
• Community education on wound care and dangers of traditional wound treatments
• Surveillance for neonatal tetanus cases
• Achieve and maintain MNTE status

SECONDARY PREVENTION:
• Early wound cleaning with soap and water
• Appropriate tetanus prophylaxis for injuries
• Prompt healthcare-seeking for contaminated wounds
• Education about early symptoms of tetanus

HIGH-RISK POPULATIONS TO TARGET:
• Pregnant women
• Agricultural workers
• Elderly (waning immunity)
• Unvaccinated or under-vaccinated individuals
• Areas with poor access to clean delivery services
• Communities using traditional wound treatments
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS TETANUS?

Tetanus, also called "lockjaw," is a serious disease caused by bacteria found in soil, dust, and animal waste. When these bacteria enter the body through a wound, they produce a poison that causes severe muscle stiffness and spasms. The disease can be life-threatening but is completely preventable through vaccination.

HOW DO PEOPLE GET TETANUS?

The bacteria that cause tetanus can enter through:
• Cuts, puncture wounds (especially from nails, thorns, animal bites)
• Burns and crush injuries
• Wounds contaminated with soil, rust, or animal waste
• Traditional practices using unclean instruments (circumcision, ear piercing, scarification)
• In newborns: through the umbilical cord stump when cut with unclean instruments or when traditional substances are applied

WHAT ARE THE WARNING SIGNS?

Seek medical help IMMEDIATELY if you notice:
• Difficulty opening the mouth (lockjaw)
• Stiff neck or difficulty swallowing
• Stiff muscles in the face, giving a fixed "smile"
• Painful muscle spasms triggered by noise, touch, or movement
• Stiff back muscles causing arching of the body
• In newborns (3-28 days old): Unable to suck or breastfeed, stiffness, spasms

HOW IS TETANUS PREVENTED?

1. VACCINATION (Most Important):
   • Make sure you and your children receive all tetanus vaccines
   • Pregnant women should receive tetanus vaccination to protect themselves and their babies
   • Adults need a booster dose every 10 years
   
2. WOUND CARE:
   • Clean all wounds thoroughly with soap and clean water
   • Seek medical care for deep, dirty, or contaminated wounds
   • Never apply cow dung, soil, ash, or traditional medicines to wounds
   
3. SAFE DELIVERY:
   • Deliver babies with trained healthcare workers
   • Use clean instruments to cut the umbilical cord
   • Do not apply any substances to the umbilical cord stump
   • Keep the cord stump clean and dry

IMPORTANT FOR NEWBORN CARE:

• Do NOT apply cow dung, ash, mud, or any herbal mixture to the umbilical cord
• Keep the cord stump clean and dry
• If the cord becomes red, swollen, or has discharge, seek medical care immediately
• Watch for signs of tetanus: inability to suck, stiffness, spasms

WHAT TO EXPECT IF DIAGNOSED WITH TETANUS:

• Tetanus requires hospital treatment, often in intensive care
• Treatment includes medication to neutralize the toxin, antibiotics, and drugs to control spasms
• Recovery takes 2-6 weeks or longer
• Most people who receive proper treatment recover fully
• You must complete your vaccination after recovery (tetanus does not give you immunity)

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition.

If you suspect tetanus, DO NOT WAIT - seek emergency medical care immediately. Tetanus is a medical emergency.

QUESTIONS TO ASK YOUR DOCTOR:
• Am I up to date on my tetanus vaccination?
• Does my wound need tetanus prevention treatment?
• What signs should I watch for after a wound?
• Is my baby protected against tetanus?
• When do I or my children need the next tetanus shot?
    `,

    references: [
      'World Health Organization. Tetanus vaccines: WHO position paper - February 2017. Weekly Epidemiological Record. 2017;92(6):53-76.',
      'Thwaites CL, Beeching NJ, Newton CR. Maternal and neonatal tetanus. Lancet. 2015;385(9965):362-370.',
      'Rodrigo C, Fernando D, Rajapakse S. Pharmacological management of tetanus: an evidence-based review. Critical Care. 2014;18(2):217.',
      'Cook TM, Protheroe RT, Handel JM. Tetanus: a review of the literature. British Journal of Anaesthesia. 2001;87(3):477-487.',
      'Blencowe H, Lawn J, Vandelaer J, et al. Tetanus toxoid immunization to reduce mortality from neonatal tetanus. International Journal of Epidemiology. 2010;39(suppl 1):i102-i109.',
      'World Health Organization. Current recommendations for treatment of tetanus during humanitarian emergencies: WHO Technical Note. Geneva: WHO; 2010.',
      'Yen LM, Thwaites CL. Tetanus. Lancet. 2019;393(10181):1657-1668.',
      'Afshar M, Raju M, Ansell D, Bleck TP. Narrative review: tetanus - a health threat after natural disasters in developing countries. Annals of Internal Medicine. 2011;154(5):329-335.',
      'Gibson K, Uwineza JB, Kiviri W, Parlow J. Tetanus in developing countries: a case series and review. Canadian Journal of Anesthesia. 2009;56(4):307-315.',
      'Okoromah CAN, Lesi FEA. Diazepam for treating tetanus. Cochrane Database of Systematic Reviews. 2004;(1):CD003954.',
      'Brauner JS, Vieira SR, Bleck TP. Changes in severe accidental tetanus mortality in the ICU during two decades in Brazil. Intensive Care Medicine. 2002;28(7):930-935.',
      'World Health Organization. Maternal and Neonatal Tetanus Elimination (MNTE). WHO; 2023. Available at: https://www.who.int/initiatives/maternal-and-neonatal-tetanus-elimination'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 52. RABIES
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '52',
    name: 'Rabies',
    slug: 'rabies',
    specialty: 'Emergency Medicine',
    bodySystem: 'Neurological',
    icdCode: 'A82.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `
Rabies is an acute, fatal viral encephalitis caused by neurotropic viruses of the genus Lyssavirus, most commonly Rabies lyssavirus (RABV). The virus is transmitted through the saliva of infected mammals, primarily via bites, scratches, or licks on broken skin or mucous membranes. Once clinical symptoms appear, rabies is virtually 100% fatal, making it one of the deadliest infectious diseases known. However, it is also nearly 100% preventable through timely post-exposure prophylaxis (PEP).

In Africa, domestic dogs are responsible for up to 99% of human rabies cases. The disease disproportionately affects children under 15 years, who account for 40% of exposures. Africa bears approximately 36% of global rabies deaths, with an estimated 21,000 deaths annually, though this is likely underreported due to limited surveillance.

The key to preventing rabies deaths lies in immediate wound care, risk assessment, and timely administration of post-exposure prophylaxis (PEP) consisting of rabies vaccine with or without rabies immunoglobulin (RIG). Every animal bite or scratch must be taken seriously as a potential rabies exposure.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 RABIES IS 100% FATAL ONCE SYMPTOMS APPEAR - There is NO treatment for clinical rabies; prevention is the ONLY option

🔴 IMMEDIATE WOUND WASHING SAVES LIVES - Wash wound with soap and water for 15 MINUTES immediately after exposure; this alone reduces rabies risk by up to 90%

🔴 POST-EXPOSURE PROPHYLAXIS (PEP) IS 100% EFFECTIVE - If given correctly before symptom onset, PEP prevents rabies in virtually all cases

🔴 NEVER DELAY PEP - Start PEP immediately after exposure; do not wait for animal observation results or laboratory confirmation

🔴 RABIES IMMUNOGLOBULIN (RIG) is for Category III exposures - Infiltrate as much as anatomically possible into and around ALL wounds

🔴 ALL DOG BITES IN ENDEMIC AREAS ARE RABIES EXPOSURES - Treat as such until proven otherwise

🔴 CHILDREN ARE AT HIGHEST RISK - 40% of exposures occur in children under 15; they often do not report bites

🔴 HYDROPHOBIA AND AEROPHOBIA are pathognomonic - But their presence means death is imminent and inevitable
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Estimated 59,000 human deaths annually worldwide (WHO 2019)
• 95% of deaths occur in Africa and Asia
• Most underreported of all major infectious diseases (actual deaths may be 2-3x higher)
• 99% of human cases caused by domestic dog bites
• 40% of victims are children under 15 years
• Incubation period: Typically 1-3 months (range: 1 week to >1 year)
• Case fatality rate: 100% once clinical symptoms appear

AFRICAN EPIDEMIOLOGY:
• Africa accounts for approximately 36% of global rabies deaths
• Estimated 21,000 deaths annually in Africa (likely underreported)
• Highest burden in East and West Africa
• Dog-mediated rabies predominant (>99% of human cases)
• Countries with highest burden: Ethiopia, Democratic Republic of Congo, Tanzania, Nigeria, South Africa
• Rural populations most affected due to limited PEP access
• Only 10-20% of bite victims in Africa receive adequate PEP
• Economic burden: $8.6 billion annually (Africa and Asia combined)
• Wildlife reservoirs include jackals, mongooses, and bats (less common transmission)

RISK FACTORS FOR EXPOSURE:
• Living in rabies-endemic areas (most of Africa)
• Contact with stray or unvaccinated dogs
• Children (more likely to approach animals, less likely to report bites)
• Rural residence with limited healthcare access
• Occupational exposure (veterinarians, animal handlers, laboratory workers)
• Travelers to endemic areas
• Bat exposure (including in sleeping areas)

RISK FACTORS FOR SEVERE OUTCOME:
• Bites to head, face, neck, or hands (short neural pathway to CNS)
• Multiple bites or deep wounds
• Bites from wild carnivores or bats (higher viral load)
• Delayed or incomplete PEP
• Failure to administer RIG for Category III exposures
• Immunocompromised status
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

INCUBATION PERIOD:
• Typically 1-3 months (range: 5 days to several years)
• Shorter with bites to head/face/hands or extensive wounds
• Longer with bites to lower extremities
• Virus replicates at wound site, then travels via peripheral nerves to CNS

PRODROMAL PHASE (2-10 days):
• Pain, paresthesia, or pruritus at bite site (pathognomonic if present - 50-80%)
• Fever, malaise, headache
• Nausea, vomiting, anorexia
• Anxiety, agitation, irritability
• Sore throat, cough (may mimic respiratory infection)
• Often misdiagnosed as non-specific viral illness

ACUTE NEUROLOGICAL PHASE - TWO FORMS:

FURIOUS (Encephalitic) RABIES (80%):
• Hydrophobia - intense fear of swallowing water due to painful pharyngeal spasms
• Aerophobia - fear of air/drafts triggering pharyngeal spasms
• Pharyngeal spasms - painful, violent spasms of throat and diaphragm
• Hypersalivation - excessive drooling ("foaming at mouth")
• Intermittent agitation, aggression, confusion alternating with lucid periods
• Hallucinations and bizarre behavior
• Autonomic dysfunction - hyperthermia, hypersalivation, hyperhidrosis, pupil dilation
• Inspiratory spasms
• Seizures
• Priapism (males)
• Progression to paralysis, coma, and death

PARALYTIC (Dumb) RABIES (20%):
• More common after bat exposure
• Ascending flaccid paralysis (may mimic Guillain-Barre syndrome)
• Starts in bitten limb, spreads symmetrically
• Fever
• Minimal or absent hydrophobia/aerophobia
• Urinary incontinence
• Respiratory failure
• Slower progression but equally fatal
• Often misdiagnosed, leading to healthcare worker exposure

TERMINAL PHASE:
• Coma
• Multiple organ failure
• Respiratory failure (cause of death)
• Cardiac arrhythmias
• Death typically 2-7 days after symptom onset (furious) or up to 2 weeks (paralytic)

PHYSICAL EXAMINATION:
• Anxiety, agitation (may be calm between episodes)
• Hypersalivation and difficulty swallowing
• Hydrophobia test: Offer water - patient attempts to drink but develops pharyngeal spasms
• Aerophobia test: Blow air gently on face - triggers spasms
• Bite wound (may be healed or minimal)
• Fever
• Hyperreflexia or flaccid paralysis (depending on form)
• Autonomic signs: tachycardia, irregular breathing, hyperthermia, dilated pupils
• Priapism (males)
• Altered mental status progressing to coma
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: EXPOSURE HISTORY (Critical)
• Any animal bite, scratch, or lick on broken skin/mucosa?
• What animal? (dog, cat, bat, wild animal)
• Was the attack provoked or unprovoked? (unprovoked more concerning)
• Where did bite occur? (head/face = shorter incubation)
• When did exposure occur? (incubation period)
• What is the status of the animal? (available for observation? sick? died?)
• Was PEP given? Complete?
• Prior rabies vaccination status?

STEP 2: CLINICAL DIAGNOSIS
Suspect rabies if:
• Acute progressive encephalitis with history of animal exposure
• Hydrophobia and/or aerophobia (pathognomonic)
• Pain/paresthesia at healed bite site
• Acute flaccid paralysis with exposure history
• Unexplained encephalitis in endemic area

STEP 3: EXPOSURE CATEGORIZATION (WHO Classification)

CATEGORY I - No exposure (No PEP required):
• Touching or feeding animals
• Licks on intact skin
• Contact of intact skin with animal secretions

CATEGORY II - Minor exposure (PEP: Vaccine only):
• Nibbling of uncovered skin
• Minor scratches or abrasions without bleeding
• Licks on broken skin

CATEGORY III - Severe exposure (PEP: RIG + Vaccine):
• Single or multiple transdermal bites or scratches
• Contamination of mucous membrane with saliva (licks)
• Exposure to bats (any physical contact)
• Bites or scratches that penetrate skin

STEP 4: LABORATORY CONFIRMATION (NOT Required for PEP Decision)

Ante-mortem Tests:
• Nuchal skin biopsy - Direct fluorescent antibody (DFA) test for viral antigen
• Saliva - RT-PCR for rabies RNA
• Corneal impression smear - DFA (less sensitive)
• CSF - May show mild pleocytosis; antibodies (appear late)
• Serum - Rabies antibodies (appear late, not useful early)

Post-mortem Confirmation:
• Brain tissue DFA - Gold standard
• Brain tissue RT-PCR
• Brain histology - Negri bodies (pathognomonic but present in only 50-80%)

IMPORTANT NOTES:
• Do NOT delay PEP waiting for test results
• Negative tests do not rule out rabies (virus may not be detectable early)
• Laboratory diagnosis confirms cases for surveillance but does not change management
• Most African facilities lack rabies diagnostic capacity

ANIMAL ASSESSMENT:
• Healthy dog/cat in endemic area: 10-day observation period (if animal remains healthy, was not rabid)
• Sick, dead, or unavailable animal: Assume rabid, complete PEP
• Wild animal or bat: Assume rabid, complete PEP
• Animal brain testing if available
    `,

    differentialDiagnosis: [
      'Viral encephalitis (Herpes, arbovirus) - No hydrophobia/aerophobia, different CSF findings, no bite history',
      'Cerebral malaria - Parasitemia, no hydrophobia, responds to antimalarials',
      'Bacterial meningitis - CSF pleocytosis with bacteria, no hydrophobia, responds to antibiotics',
      'Tetanus - Trismus, opisthotonus, no hydrophobia, clear consciousness early',
      'Guillain-Barre syndrome - Ascending paralysis, albuminocytologic dissociation in CSF, no fever typically',
      'Poliomyelitis - Asymmetric flaccid paralysis, no sensory changes, outbreak context',
      'Delirium tremens - Alcohol withdrawal history, no hydrophobia, tremor predominant',
      'Psychiatric disorders - No fever, no progression to paralysis/coma, no autonomic dysfunction',
      'Drug intoxication/poisoning - Toxicology positive, no progressive course, responds to antidotes',
      'Status epilepticus - Seizures predominant, EEG abnormalities, responds to anticonvulsants',
      'Organophosphate poisoning - Cholinergic crisis, miosis, responds to atropine',
      'Stroke - Focal neurological deficits, CT/MRI findings, no prodrome',
      'Brain abscess - Focal signs, imaging findings, fever, responds to drainage/antibiotics',
      'Tuberculous meningitis - Subacute course, CSF findings, responds to TB treatment',
      'Conversion disorder - Variable symptoms, no objective findings, no progression'
    ],

    investigations: [
      {
        name: 'Clinical Assessment and Exposure History',
        indication: '🔴 ESSENTIAL - Detailed history is the most important diagnostic tool; categorize exposure (I, II, III)',
        availability: 'BASIC'
      },
      {
        name: 'Wound Examination',
        indication: '🔴 ESSENTIAL - Document location, depth, number of wounds; assess contamination',
        availability: 'BASIC'
      },
      {
        name: 'Hydrophobia and Aerophobia Testing',
        indication: '🔴 ESSENTIAL for symptomatic patients - Pathognomonic signs; offer water, blow air on face',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Baseline; may show leukocytosis',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: '🟠 IMPORTANT - SIADH may cause hyponatremia in encephalitis',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture / CSF Analysis',
        indication: '🟠 IMPORTANT if diagnosis uncertain - Mild lymphocytic pleocytosis, normal glucose; helps exclude bacterial meningitis',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🟠 IMPORTANT - Exclude hypoglycemia as cause of altered consciousness',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Rapid Test / Blood Smear',
        indication: '🔴 ESSENTIAL in endemic areas - Exclude cerebral malaria',
        availability: 'BASIC'
      },
      {
        name: 'Nuchal Skin Biopsy with DFA',
        indication: '🟡 CONFIRMATORY - Direct fluorescent antibody test on hair follicle nerves; best ante-mortem test',
        availability: 'ADVANCED'
      },
      {
        name: 'Saliva RT-PCR',
        indication: '🟡 CONFIRMATORY - Detects rabies RNA; intermittent shedding reduces sensitivity',
        availability: 'ADVANCED'
      },
      {
        name: 'Serum Rabies Antibodies',
        indication: '🟡 SUPPORTIVE - Appear late in illness; useful in unvaccinated patients',
        availability: 'ADVANCED'
      },
      {
        name: 'CSF Rabies Antibodies',
        indication: '🟡 CONFIRMATORY - More specific than serum; appear late',
        availability: 'ADVANCED'
      },
      {
        name: 'Brain CT/MRI',
        indication: '🟠 IMPORTANT if available - Exclude other causes; rabies may show brainstem/limbic involvement late',
        availability: 'ADVANCED'
      },
      {
        name: 'Animal Brain DFA (Post-mortem)',
        indication: '🔴 DEFINITIVE - If animal available; confirms rabies in animal to guide human PEP',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ WOUND WASHING: Start IMMEDIATELY - Wash ALL wounds thoroughly with soap and running water for MINIMUM 15 MINUTES; this is the single most effective measure',
        '⚡ WOUND DISINFECTION: Apply povidone-iodine (Betadine), 70% alcohol, or other virucidal agent after washing',
        '⚡ EXPOSURE CATEGORIZATION: Determine WHO Category (I, II, or III) to guide PEP decision',
        '⚡ RABIES VACCINE: Initiate immediately for Category II and III exposures - Do NOT delay',
        '⚡ RABIES IMMUNOGLOBULIN (RIG): Administer for Category III exposures - Infiltrate into and around wounds',
        '⚡ TETANUS PROPHYLAXIS: Assess tetanus vaccination status and provide prophylaxis as indicated',
        '⚡ WOUND CARE: Do NOT suture wounds primarily unless essential for hemostasis; delayed closure preferred',
        '⚡ ANTIBIOTICS: Give prophylactic antibiotics for contaminated bite wounds',
        '⚡ ANIMAL STATUS: Attempt to identify and observe/test animal if possible (do not delay PEP)',
        '⚡ If clinical rabies suspected: Isolate patient, implement strict infection control, provide supportive and palliative care'
      ],
      
      pharmacological: [
        {
          drug: 'Rabies Vaccine (Cell-Culture or Purified)',
          indication: '🔴 CRITICAL - Post-exposure prophylaxis for Category II and III exposures; 100% effective if given correctly before symptoms',
          dose: 'INTRAMUSCULAR (IM) Regimen: Day 0, 3, 7, 14, 28 (5-dose Essen) OR Day 0, 3, 7, 14 (4-dose Zagreb). INTRADERMAL (ID) Updated WHO-IPC Regimen (preferred for resource-limited settings): 0.1mL at 2 sites on days 0, 3, 7, 28. Previously vaccinated: 2 doses on days 0 and 3 only (IM or ID).',
          route: 'IM (deltoid in adults, anterolateral thigh in children) or ID (over deltoid)',
          duration: 'Complete full series',
          contraindications: ['None for post-exposure prophylaxis - Rabies is 100% fatal so PEP is always indicated regardless of allergies or pregnancy'],
          sideEffects: ['Injection site pain and redness (common)', 'Mild systemic reactions (headache, myalgia, nausea)', 'Rare allergic reactions'],
          alternatives: ['Different approved cell-culture vaccines (PVRV, PCECV, PDEV, HDCV) are interchangeable']
        },
        {
          drug: 'Human Rabies Immunoglobulin (HRIG)',
          indication: '🔴 CRITICAL for Category III - Provides immediate passive immunity; use for ALL Category III exposures in unvaccinated individuals',
          dose: '20 IU/kg body weight. Infiltrate as much as anatomically feasible INTO and AROUND all wounds. Any remaining volume: inject IM at site distant from vaccine.',
          route: 'Local infiltration into wounds (preferred) + IM for remainder',
          duration: 'Single dose on Day 0 only',
          contraindications: ['Previous severe reaction to human immunoglobulin (extremely rare)', 'Do NOT give to previously vaccinated individuals', 'Do NOT give after Day 7 of vaccine series (antibodies already developing)'],
          sideEffects: ['Local pain', 'Low-grade fever', 'Rare allergic reactions'],
          alternatives: ['Equine Rabies Immunoglobulin (ERIG) 40 IU/kg if HRIG unavailable - requires skin sensitivity testing first']
        },
        {
          drug: 'Equine Rabies Immunoglobulin (ERIG)',
          indication: '🟠 ALTERNATIVE to HRIG - When HRIG unavailable; more widely available and affordable in Africa',
          dose: '40 IU/kg body weight. Infiltrate into and around wounds; remainder IM. Skin test before administration (0.1mL of 1:10 dilution ID, read at 20 minutes).',
          route: 'Local infiltration + IM',
          duration: 'Single dose on Day 0 only',
          contraindications: ['Known horse serum allergy', 'Positive skin test (proceed with desensitization if RIG absolutely needed)'],
          sideEffects: ['Serum sickness (fever, rash, arthralgia - 1-7% at day 7-14)', 'Anaphylaxis (rare)', 'Local reactions'],
          alternatives: ['HRIG (preferred if available)']
        },
        {
          drug: 'Tetanus Toxoid',
          indication: '🔴 ESSENTIAL - Animal bites are tetanus-prone wounds',
          dose: 'See tetanus prophylaxis guidelines based on vaccination history and wound type. Give TT and consider HTIG for tetanus-prone wounds in unvaccinated.',
          route: 'IM',
          duration: 'Per tetanus guidelines',
          contraindications: ['Severe reaction to previous dose (very rare)'],
          sideEffects: ['Injection site reactions'],
          alternatives: ['Td (Tetanus-Diphtheria) vaccine']
        },
        {
          drug: 'Amoxicillin-Clavulanate',
          indication: '🟠 IMPORTANT - Prophylactic antibiotic for contaminated bite wounds; covers Pasteurella, streptococci, staphylococci, anaerobes',
          dose: 'Adults: 875/125mg PO twice daily. Children: 25-45 mg/kg/day (amoxicillin component) divided twice daily.',
          route: 'PO',
          duration: '3-5 days for prophylaxis; 5-7 days if established infection',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Nausea', 'Rash', 'Candidiasis'],
          alternatives: ['Doxycycline 100mg BD (adults)', 'Metronidazole + Fluoroquinolone', 'Clindamycin (if penicillin allergic)']
        },
        {
          drug: 'Povidone-Iodine Solution (10%)',
          indication: '🔴 ESSENTIAL - Virucidal agent for wound disinfection after thorough washing',
          dose: 'Apply liberally to wound after 15 minutes of soap and water washing',
          route: 'Topical',
          duration: 'Single application, may repeat',
          contraindications: ['Iodine allergy', 'Thyroid disorders (relative)'],
          sideEffects: ['Local irritation', 'Staining'],
          alternatives: ['70% Ethanol', '0.05% Aqueous iodine', 'Benzalkonium chloride']
        },
        {
          drug: 'Midazolam (For symptomatic rabies)',
          indication: '🟠 PALLIATIVE - Sedation for agitation, spasms, and anxiety in clinical rabies',
          dose: 'Adults: 1-5mg IV/IM as needed; continuous infusion 0.05-0.2 mg/kg/hour. Children: 0.1-0.2 mg/kg.',
          route: 'IV or IM',
          duration: 'Continuous until death',
          contraindications: ['None in palliative context'],
          sideEffects: ['Respiratory depression', 'Hypotension'],
          alternatives: ['Diazepam', 'Morphine']
        },
        {
          drug: 'Morphine (For symptomatic rabies)',
          indication: '🟠 PALLIATIVE - Pain control and sedation for clinical rabies; reduces suffering',
          dose: 'Adults: 2-10mg IV every 2-4 hours as needed, or continuous infusion. Titrate to comfort.',
          route: 'IV or IM',
          duration: 'Continuous until death',
          contraindications: ['None in palliative context'],
          sideEffects: ['Respiratory depression (not a concern in palliative setting)'],
          alternatives: ['Fentanyl', 'Other opioids']
        }
      ],
      
      nonPharmacological: [
        'IMMEDIATE WOUND WASHING: Most critical intervention - Vigorous washing with soap and running water for minimum 15 minutes mechanically removes virus; can reduce rabies risk by up to 90%',
        'WOUND MANAGEMENT: Do not suture bite wounds primarily unless hemostasis required; delayed primary closure after 3-5 days if needed; leave facial wounds for plastic closure',
        'INFECTION CONTROL (Clinical rabies): Standard and droplet precautions; eye protection and mask when near patient; avoid contact with saliva; handle tissues with gloves',
        'PATIENT ISOLATION (Clinical rabies): Private room; minimize healthcare worker exposure; no cure exists',
        'PALLIATIVE CARE (Clinical rabies): Once clinical rabies confirmed, focus on comfort; pain control, sedation, hydration; family support; allow natural death with dignity',
        'ANIMAL OBSERVATION: If dog/cat available and appears healthy, observe for 10 days; if animal develops signs of rabies or dies, confirm with brain testing',
        'COMMUNITY EDUCATION: Teach proper wound washing; when to seek care; importance of completing PEP',
        'AVOID TRADITIONAL TREATMENTS: Do not apply chili, herbs, ash, or other traditional remedies to bite wounds - these are ineffective and may delay proper care',
        'EXPOSURE COUNSELING: Explain rabies risk, PEP schedule, importance of completing all doses; reassure that PEP is 100% effective',
        'CONTACT TRACING: Identify others potentially exposed to same animal; ensure they receive PEP if indicated',
        'REPORTING: Report suspected rabies cases to public health authorities; part of disease surveillance'
      ],
      
      surgical: [
        'WOUND DEBRIDEMENT: Remove devitalized tissue and debris after thorough washing; aids viral clearance',
        'DELAYED PRIMARY CLOSURE: If wound requires closure, wait 3-5 days after thorough cleaning and PEP initiation',
        'HEMOSTATIC SUTURING: Minimal sutures only if active bleeding cannot be controlled otherwise; loose closure',
        'FACIAL WOUND MANAGEMENT: May require earlier closure for cosmetic reasons; ensure thorough wound infiltration with RIG first',
        'TRACHEOSTOMY (Clinical rabies): May be needed for airway protection but does not alter fatal outcome'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Category III exposure with no local RIG availability - Patient needs RIG urgently',
        '🔴 IMMEDIATE REFERRAL: Exposure to head, face, or neck - High risk, short incubation, needs immediate RIG + vaccine',
        '🔴 IMMEDIATE REFERRAL: Multiple severe bites - Higher viral inoculum, needs complete PEP with RIG',
        '🔴 IMMEDIATE REFERRAL: Bat exposure - Always Category III, needs RIG even if no visible wound',
        '🔴 IMMEDIATE REFERRAL: Signs of clinical rabies - For confirmation and palliative care; warn receiving facility',
        '🔴 IMMEDIATE REFERRAL: Immunocompromised patient with exposure - May need modified PEP schedule, antibody testing',
        '🟠 URGENT REFERRAL: Vaccine not available locally - Patient must not miss doses',
        '🟠 URGENT REFERRAL: Wound requiring surgical management - Complex wounds needing specialist care',
        '🟠 URGENT REFERRAL: Unknown or escaped animal in high-risk context - Needs risk assessment by expert',
        '🟡 ROUTINE REFERRAL: Adverse reaction to vaccine - For management and alternative vaccine consideration',
        '🟡 ROUTINE REFERRAL: Pregnancy with exposure - Needs counseling (PEP is safe and indicated in pregnancy)',
        '🟡 CONSULTATION: Previous rabies vaccination - Confirm appropriate reduced PEP schedule'
      ],
      
      monitoring: [
        'PEP COMPLETION: Track all doses; contact patients who miss appointments; PEP failure is always due to incomplete or incorrect PEP',
        'WOUND HEALING: Monitor for signs of infection at bite site; secondary bacterial infection is common',
        'ADVERSE REACTIONS: Monitor for vaccine reactions (local or systemic) and serum sickness with ERIG',
        'ANIMAL STATUS: If animal under observation, check status daily for 10 days; any illness or death prompts testing',
        'ANTIBODY RESPONSE (Immunocompromised): Check rabies antibodies 2 weeks after PEP completion; may need additional doses',
        'CLINICAL RABIES: If managing symptomatic case, monitor vital signs; focus is palliative comfort, not cure',
        'MENTAL HEALTH: Rabies exposure causes significant anxiety; provide reassurance about PEP effectiveness',
        'COMPLIANCE BARRIERS: Identify and address barriers to completing vaccine series (cost, distance, work)'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

POST-EXPOSURE PROPHYLAXIS SCHEDULE:

Standard IM Schedule (Essen):
• Day 0: Wound care + RIG (if Category III) + Vaccine dose 1
• Day 3: Vaccine dose 2
• Day 7: Vaccine dose 3
• Day 14: Vaccine dose 4
• Day 28: Vaccine dose 5

Alternative IM Schedule (Zagreb 2-1-1):
• Day 0: 2 doses (1 each deltoid) + RIG (if Category III)
• Day 7: 1 dose
• Day 21: 1 dose

Intradermal Schedule (WHO Updated IPC):
• Day 0: 0.1mL at 2 ID sites + RIG (if Category III)
• Day 3: 0.1mL at 2 ID sites
• Day 7: 0.1mL at 2 ID sites
• Day 28: 0.1mL at 2 ID sites

Previously Vaccinated Individuals:
• Day 0: Vaccine dose 1 (no RIG needed)
• Day 3: Vaccine dose 2
• No further doses needed

CRITICAL FOLLOW-UP POINTS:

Day 3 Visit:
• Administer second vaccine dose
• Assess wound healing
• Check for adverse reactions
• Reinforce importance of completing series

Day 7 Visit:
• Administer third vaccine dose
• If ERIG given, monitor for serum sickness (fever, rash, joint pain)
• Update on animal observation status if applicable

Day 14 Visit:
• Administer fourth vaccine dose
• Most patients can be reassured at this point

Day 28 Visit:
• Complete series with final dose
• Counsel on future exposures (will need reduced PEP)
• Document vaccination for future reference

MISSED DOSES:
• Continue schedule from where left off; do NOT restart
• Any deviation from schedule still provides substantial protection
• Rabies has long incubation; late PEP better than no PEP

LONG-TERM:
• No routine boosters needed for general public
• High-risk occupations: Check antibodies every 6-24 months; booster if titer falls
• Pre-exposure prophylaxis for high-risk individuals (3 doses: Day 0, 7, 21-28)

POST-CLINICAL RABIES:
• Extremely rare survivors (Milwaukee Protocol) have severe neurological sequelae
• Most patients die within 2 weeks of symptom onset
• Family support and bereavement counseling essential
      `
    },

    redFlags: [
      '🔴 CRITICAL: Hydrophobia or aerophobia - Pathognomonic for clinical rabies; death is inevitable; initiate palliative care',
      '🔴 CRITICAL: Encephalitis symptoms with bite history - Agitation, confusion, hallucinations suggest clinical rabies',
      '🔴 CRITICAL: Pain/paresthesia at healed bite site - Prodromal symptom; may indicate virus has reached CNS',
      '🔴 CRITICAL: Pharyngeal spasms preventing swallowing - Clinical rabies established',
      '🔴 CRITICAL: Paralytic symptoms with exposure history - Paralytic rabies often misdiagnosed; equally fatal',
      '🔴 CRITICAL: Category III exposure without RIG access - Must refer urgently; RIG is critical for severe exposures',
      '🟠 URGENT: Head, face, or neck bite - Very short incubation possible; immediate PEP with RIG essential',
      '🟠 URGENT: Multiple deep bites - High viral inoculum; aggressive PEP required',
      '🟠 URGENT: Bat exposure - Any contact (including in sleeping areas); always Category III',
      '🟠 URGENT: Bite from wild carnivore - Assume rabid; higher viral loads than dogs',
      '🟠 URGENT: Animal developed illness or died - Strongly suggests rabies; complete PEP regardless of current dose',
      '🟡 CAUTION: Missed PEP dose - Continue schedule; counsel on importance of completion',
      '🟡 CAUTION: Immunocompromised patient - May need antibody testing after PEP completion'
    ],

    complications: [
      'Death - 100% fatal once clinical symptoms appear; the only outcome of clinical rabies',
      'Encephalitis - Progressive brain inflammation leading to coma and death',
      'Respiratory failure - Terminal event in most cases; from brainstem involvement',
      'Cardiac arrhythmias - Autonomic dysfunction causes fatal arrhythmias',
      'Aspiration pneumonia - From dysphagia and inability to protect airway',
      'Seizures - Common in terminal phase',
      'Wound infection - Secondary bacterial infection of bite wounds',
      'Serum sickness - From equine RIG; fever, rash, arthralgia 7-14 days after administration',
      'Vaccine adverse reactions - Usually mild local reactions; rare systemic reactions',
      'Psychological trauma - Anxiety, PTSD from exposure; fear of developing rabies',
      'Healthcare worker exposure - From managing undiagnosed case; requires PEP',
      'PEP failure - Extremely rare if PEP given correctly; usually due to incomplete PEP, incorrect technique, or failure to give RIG'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

CLINICAL RABIES - UNIFORMLY FATAL:
• Case fatality rate: 100% once clinical symptoms appear
• Survival: Fewer than 20 documented survivors worldwide (with severe neurological sequelae)
• Time from symptom onset to death: 2-7 days (furious), up to 2 weeks (paralytic)
• No effective treatment exists once clinical disease established

POST-EXPOSURE PROPHYLAXIS - HIGHLY EFFECTIVE:
• PEP effectiveness: Virtually 100% if given correctly before symptom onset
• PEP failure: Almost always due to errors (delayed initiation, omitted RIG, incomplete series)
• Factors in PEP failure: Severe head wounds, massive exposure, immunocompromise

DETERMINANTS OF OUTCOME:

SURVIVAL (with proper PEP):
• Immediate wound washing (reduces risk by up to 90% alone)
• Prompt initiation of PEP (ideally within hours of exposure)
• Correct administration of RIG for Category III exposures
• Completion of full vaccine series
• Previously vaccinated individuals (already have memory immunity)

DEATH (clinical disease):
• Any delay allowing virus to reach CNS (no treatment can reverse this)
• Failure to seek care after exposure
• Lack of access to PEP
• Incomplete or incorrect PEP
• Failure to give RIG for Category III exposures

MILWAUKEE PROTOCOL:
• Experimental intensive care protocol with induced coma
• Has resulted in very few survivals, all with severe disabilities
• Not recommended by WHO due to poor outcomes and resource requirements
• Not applicable in resource-limited settings
• Should not provide false hope - rabies remains essentially 100% fatal

KEY MESSAGE:
The prognosis for rabies exposure is EXCELLENT with proper wound care and PEP.
The prognosis for clinical rabies is UNIFORMLY FATAL.
Prevention is the ONLY solution.
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION - ELIMINATING DOG-MEDIATED RABIES:

Mass Dog Vaccination:
• Vaccinate >70% of dog population to achieve herd immunity
• Annual vaccination campaigns in endemic areas
• Include stray and owned dogs
• This is the most cost-effective intervention to prevent human rabies

Responsible Dog Ownership:
• Register and vaccinate all pet dogs
• Prevent dogs from roaming freely
• Spay/neuter to control stray population
• Report suspected rabid animals

HUMAN PRE-EXPOSURE PROPHYLAXIS (PrEP):

Indications:
• Laboratory workers handling rabies virus
• Veterinarians and animal handlers
• Wildlife workers (bat biologists, etc.)
• Travelers to high-risk endemic areas
• Children in high-endemicity areas (controversial due to cost)

Schedule:
• Day 0, Day 7, Day 21-28 (3 doses)
• Boosters for occupational risk based on antibody testing

POST-EXPOSURE PROPHYLAXIS (PEP):

Immediate Wound Care - TEACH EVERYONE:
• Wash wound with soap and running water for 15 MINUTES
• Apply antiseptic (povidone-iodine, alcohol)
• Seek medical care immediately
• Do NOT apply chili, ash, herbs, or traditional treatments

Access to PEP:
• Stock rabies vaccine and RIG at health facilities in endemic areas
• Ensure affordable or free PEP (cost is major barrier)
• Intradermal vaccination reduces cost significantly
• Train healthcare workers on PEP protocols

PUBLIC EDUCATION:

Key Messages:
• All dog and cat bites may transmit rabies
• Wash wounds immediately with soap and water for 15 minutes
• Seek medical care after any animal bite
• Rabies is 100% preventable but 100% fatal once sick
• Complete all vaccine doses even if you feel fine
• Avoid stray dogs and wild animals
• Vaccinate your dogs annually

Target Audiences:
• Children (highest risk group)
• Parents and teachers
• Community health workers
• Traditional healers (to discourage harmful practices)

SURVEILLANCE:

• Report all human rabies cases and deaths
• Report suspected rabid animals
• Test animal brains when possible
• Map high-risk areas
• Track PEP utilization

GLOBAL GOALS:

Zero by 30:
• WHO/OIE/FAO goal to eliminate dog-mediated human rabies by 2030
• Requires: Mass dog vaccination, accessible PEP, awareness campaigns
• Several African countries actively working toward this goal
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS RABIES?

Rabies is a deadly disease caused by a virus found in the saliva of infected animals. The virus attacks the brain and is almost always fatal once symptoms appear. However, rabies can be completely prevented if you get treatment quickly after being bitten or scratched by an animal.

HOW DO PEOPLE GET RABIES?

• Bites from infected dogs (most common in Africa)
• Scratches from infected animals
• Licks from infected animals on broken skin or eyes/mouth
• Contact with bats (even without obvious bite)
Rabies is NOT spread by touching an animal, petting animals, or contact with blood, urine, or feces.

WHAT TO DO IMMEDIATELY AFTER AN ANIMAL BITE:

1. WASH THE WOUND RIGHT AWAY:
   • Use soap and running water
   • Wash for AT LEAST 15 MINUTES
   • Scrub the wound thoroughly
   • This simple action can prevent rabies!

2. APPLY ANTISEPTIC:
   • Use Betadine, alcohol, or any available antiseptic
   • Do NOT apply chili, herbs, ash, or traditional medicines

3. GO TO A HEALTH FACILITY IMMEDIATELY:
   • Even if the wound looks small
   • Even if you are not sure the animal was rabid
   • Even if several days have passed since the bite

4. GET THE RABIES VACCINE:
   • The doctor will give you injections to prevent rabies
   • You must complete ALL doses
   • The vaccine is safe for pregnant women and children

WHY YOU MUST COMPLETE ALL VACCINE DOSES:

• Rabies can hide in your body for weeks or months
• The full vaccine series protects you completely
• Missing doses may leave you unprotected
• Once rabies symptoms start, there is NO cure

DANGER SIGNS - SEEK HELP IMMEDIATELY IF:

After an animal bite, watch for these signs (may appear weeks to months later):
• Pain, tingling, or itching at the bite site
• Fever and feeling unwell
• Fear of water or difficulty swallowing
• Fear of air or drafts
• Confusion, aggression, or strange behavior
• Muscle spasms

THESE SIGNS MEAN RABIES HAS STARTED. THERE IS NO CURE ONCE SYMPTOMS BEGIN.

HOW TO PREVENT RABIES:

• Vaccinate your dogs every year
• Do not touch stray dogs or wild animals
• Teach children to avoid unfamiliar animals
• Report any animal acting strangely to authorities
• If bitten, wash immediately and seek care

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only and is not a substitute for professional medical advice. Always seek immediate medical care after any animal bite or scratch.

Remember: Rabies is 100% PREVENTABLE but 100% FATAL once symptoms appear. 
Wash the wound. Get the vaccine. Save your life.

QUESTIONS TO ASK YOUR DOCTOR:
• How many vaccine doses will I need and when?
• Do I need the rabies antibody injection (immunoglobulin)?
• What should I watch for at the wound site?
• What are the danger signs I should look for?
• Should my family members get vaccinated?
• Should I vaccinate my dog?
    `,

    references: [
      'World Health Organization. WHO Expert Consultation on Rabies: Third Report. WHO Technical Report Series No. 1012. Geneva: WHO; 2018.',
      'World Health Organization. Rabies vaccines: WHO position paper - April 2018. Weekly Epidemiological Record. 2018;93(16):201-220.',
      'Hampson K, Coudeville L, Lembo T, et al. Estimating the global burden of endemic canine rabies. PLoS Neglected Tropical Diseases. 2015;9(4):e0003709.',
      'Fooks AR, Cliquet F, Finke S, et al. Rabies. Nature Reviews Disease Primers. 2017;3:17091.',
      'Rupprecht CE, Briggs D, Brown CM, et al. Use of a reduced (4-dose) vaccine schedule for postexposure prophylaxis to prevent human rabies. MMWR Recommendations and Reports. 2010;59(RR-2):1-9.',
      'Warrell MJ, Warrell DA. Rabies: a practical approach to post-exposure treatment. Transactions of the Royal Society of Tropical Medicine and Hygiene. 2015;109(2):83-89.',
      'Tarantola A. Four thousand years of concepts relating to rabies in animals and humans. Clinical Microbiology and Infection. 2017;23(11):778-784.',
      'Cleaveland S, Thumbi SM, Sambo M, et al. Proof of concept of mass dog vaccination for the control and elimination of canine rabies. Revue Scientifique et Technique. 2018;37(2):559-568.',
      'World Health Organization. Zero by 30: The Global Strategic Plan to End Human Deaths from Dog-Mediated Rabies by 2030. Geneva: WHO; 2018.',
      'Hemachudha T, Ugolini G, Wacharapluesadee S, et al. Human rabies: neuropathogenesis, diagnosis, and management. Lancet Neurology. 2013;12(5):498-513.',
      'Nel LH. Discrepancies in data reporting for rabies, Africa. Emerging Infectious Diseases. 2013;19(4):529-533.',
      'Warrell MJ. Current rabies vaccines and prophylaxis schedules: preventing rabies before and after exposure. Travel Medicine and Infectious Disease. 2012;10(1):1-15.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 53. SCHISTOSOMIASIS (Bilharzia)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '53',
    name: 'Schistosomiasis',
    slug: 'schistosomiasis',
    specialty: 'Infectious Disease',
    bodySystem: 'Systemic',
    icdCode: 'B65.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
Schistosomiasis (bilharzia) is a chronic parasitic disease caused by blood flukes (trematodes) of the genus Schistosoma. It is the second most important parasitic disease globally after malaria in terms of public health impact. The disease is transmitted through contact with freshwater contaminated with cercariae (larval forms) released from infected freshwater snails, which serve as intermediate hosts.

Three main species cause human disease: Schistosoma haematobium (urogenital schistosomiasis), Schistosoma mansoni (intestinal/hepatic schistosomiasis), and Schistosoma japonicum (intestinal/hepatic, primarily in Asia). In Africa, S. haematobium and S. mansoni are the predominant species, with S. haematobium being most prevalent.

The pathology of schistosomiasis is primarily caused by the host immune response to eggs deposited in tissues, leading to granuloma formation, fibrosis, and organ damage. Chronic infection can result in severe morbidity including bladder cancer (S. haematobium), portal hypertension with hepatosplenomegaly (S. mansoni), and significant impacts on child growth, cognitive development, and productivity.

Africa bears over 90% of the global schistosomiasis burden, with an estimated 200+ million people infected and 700+ million at risk. Mass drug administration (MDA) with praziquantel is the cornerstone of control programs, alongside water, sanitation, and hygiene (WASH) interventions and snail control.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 HEMATURIA IN ENDEMIC AREAS = SCHISTOSOMIASIS UNTIL PROVEN OTHERWISE - Terminal hematuria (blood at end of urination) is classic for S. haematobium

🔴 PRAZIQUANTEL IS THE DRUG OF CHOICE - Single oral dose (40 mg/kg) is effective, safe, and can be given in MDA programs

🔴 FRESHWATER CONTACT IS THE RISK - Swimming, bathing, washing, fishing in slow-moving freshwater in endemic areas transmits infection

🔴 EGGS IN URINE OR STOOL CONFIRM DIAGNOSIS - Microscopy for eggs is the gold standard; collect urine between 10am-2pm for best yield

🔴 CHRONIC INFECTION CAUSES SERIOUS COMPLICATIONS - Bladder cancer (S. haematobium), portal hypertension (S. mansoni), growth stunting in children

🔴 TREATMENT DOES NOT PREVENT REINFECTION - Patients in endemic areas will be reinfected; repeated treatment and prevention are essential

🔴 MASS DRUG ADMINISTRATION (MDA) IS KEY STRATEGY - School-based and community-wide praziquantel programs reduce morbidity

🔴 SNAILS ARE THE INTERMEDIATE HOST - Freshwater snails (Bulinus for S. haematobium, Biomphalaria for S. mansoni) must be present for transmission
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• 200+ million people infected worldwide
• 700+ million people at risk in 78 endemic countries
• 90% of cases occur in sub-Saharan Africa
• 200,000+ deaths annually attributed to schistosomiasis
• Second most socioeconomically devastating parasitic disease after malaria
• Estimated 1.9 million DALYs (Disability-Adjusted Life Years) lost annually
• WHO classifies as a Neglected Tropical Disease (NTD)

AFRICAN EPIDEMIOLOGY:
• Over 90% of global burden concentrated in Africa
• S. haematobium: Most prevalent in Africa; endemic in 53 countries
• S. mansoni: Endemic in 54 countries; overlaps with S. haematobium in many areas
• Highest prevalence: Nigeria, Tanzania, Ghana, Mozambique, Democratic Republic of Congo, Madagascar
• School-age children (5-14 years) have highest infection intensity
• Prevalence can exceed 80% in highly endemic communities
• Often co-endemic with soil-transmitted helminths
• Transmission linked to water bodies: Lake Victoria, Lake Malawi, Nile River, irrigation schemes

SPECIES DISTRIBUTION IN AFRICA:
• S. haematobium: Throughout sub-Saharan Africa; causes urogenital disease
• S. mansoni: East and Central Africa, parts of West Africa; causes hepatointestinal disease
• S. intercalatum: Limited to Central and West Africa (rare)
• Mixed infections common in co-endemic areas

RISK FACTORS:
• Living in endemic areas near freshwater bodies
• Occupational exposure: Farmers (irrigation), fishermen, sand harvesters, car washers
• Recreational exposure: Swimming, bathing in infested waters
• Domestic activities: Washing clothes, fetching water from infested sources
• Age: School-age children (5-14 years) most heavily infected
• Gender: Males often more infected (more water contact in many societies)
• Lack of safe water and sanitation facilities
• Poverty and rural residence
• Presence of intermediate host snails
• Seasonal patterns: Often peaks in dry season when water contact concentrated
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

ACUTE SCHISTOSOMIASIS (Katayama Syndrome):
Occurs 2-8 weeks after primary infection (cercarial penetration):
• Fever (often high, intermittent)
• Malaise and fatigue
• Myalgia and arthralgia
• Dry cough, wheezing
• Abdominal pain
• Diarrhea (sometimes bloody)
• Urticaria and angioedema
• Hepatosplenomegaly
• Marked eosinophilia
• More common in travelers/non-immune individuals
• Can be severe and occasionally fatal

CERCARIAL DERMATITIS (Swimmers Itch):
• Pruritic papular rash at site of cercarial penetration
• Occurs within hours of water contact
• Self-limiting within days
• More pronounced with repeated exposures

CHRONIC UROGENITAL SCHISTOSOMIASIS (S. haematobium):

Early/Active Disease:
• Terminal hematuria (blood at end of urination) - CLASSIC SYMPTOM
• Dysuria (painful urination)
• Increased urinary frequency
• Suprapubic discomfort
• Proteinuria

Advanced Disease:
• Bladder wall thickening and calcification
• Hydroureter and hydronephrosis (obstructive uropathy)
• Secondary bacterial urinary tract infections
• Bladder stones
• Squamous cell carcinoma of the bladder (long-term complication)
• Chronic kidney disease

Female Genital Schistosomiasis (FGS):
• Affects 30-75% of women with S. haematobium in endemic areas
• Sandy patches on cervix and vagina
• Contact bleeding
• Abnormal vaginal discharge
• Dyspareunia (painful intercourse)
• Infertility
• Increased HIV acquisition risk (3-4 fold)
• Often misdiagnosed as STI

Male Genital Schistosomiasis:
• Hematospermia (blood in semen)
• Genital lesions
• Prostatitis
• Reduced fertility

CHRONIC INTESTINAL/HEPATIC SCHISTOSOMIASIS (S. mansoni):

Intestinal Disease:
• Chronic or intermittent diarrhea
• Bloody stools (occult or visible)
• Abdominal pain and cramping
• Tenesmus
• Intestinal polyps
• Colonic strictures (rare)

Hepatosplenic Disease (Advanced):
• Hepatomegaly - firm, non-tender, left lobe predominance
• Splenomegaly - often massive
• Portal hypertension (Symmer pipestem fibrosis)
• Esophageal and gastric varices
• Variceal bleeding (hematemesis, melena)
• Ascites (late finding)
• Growth retardation in children
• Liver function usually preserved until very late

PEDIATRIC MANIFESTATIONS:
• Growth retardation and stunting
• Anemia (from chronic blood loss and inflammation)
• Reduced school performance and cognitive impairment
• Exercise intolerance
• Protein-calorie malnutrition

PHYSICAL EXAMINATION:
• General: May appear well in early disease; wasting in advanced disease
• Abdomen: Hepatomegaly (left lobe), splenomegaly, ascites
• Genitourinary: Suprapubic tenderness; genital lesions in FGS
• Speculum exam in women: Sandy patches, contact bleeding
• Signs of portal hypertension: Caput medusae, splenomegaly, ascites
• Signs of anemia: Pallor, tachycardia
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
Consider schistosomiasis in:
• Anyone with freshwater exposure in endemic areas
• Terminal hematuria in endemic areas
• Unexplained eosinophilia
• Hepatosplenomegaly with preserved liver function
• Chronic diarrhea with bloody stools
• Female genital symptoms in endemic areas
• Travelers returning from endemic areas with fever/eosinophilia

STEP 2: PARASITOLOGICAL DIAGNOSIS (Gold Standard)

Urine Microscopy (S. haematobium):
• Collect urine between 10:00 AM and 2:00 PM (peak egg excretion)
• Exercise before collection increases yield
• Sedimentation or filtration technique
• Identify characteristic terminal-spined eggs
• Quantify: Light (<50 eggs/10mL), Heavy (>50 eggs/10mL)

Stool Microscopy (S. mansoni):
• Kato-Katz technique (WHO recommended for MDA programs)
• Identify lateral-spined eggs
• Multiple samples increase sensitivity (3 samples on different days)
• Quantify: Light (<100 EPG), Moderate (100-399 EPG), Heavy (>400 EPG)

STEP 3: ADDITIONAL DIAGNOSTIC METHODS

Point-of-Care Circulating Cathodic Antigen (POC-CCA):
• Urine dipstick test
• Detects S. mansoni antigen
• More sensitive than single Kato-Katz
• Useful for mapping and MDA monitoring
• Less reliable for S. haematobium

Serology:
• ELISA for anti-schistosome antibodies
• High sensitivity but cannot distinguish past from current infection
• Useful in travelers and low-intensity infections
• Not useful in endemic areas (high background seropositivity)

PCR:
• Highly sensitive and specific
• Can detect species and intensity
• Limited availability in endemic settings
• Useful for research and reference laboratories

Tissue Biopsy:
• Rectal snip (S. mansoni) - identifies eggs in rectal mucosa
• Bladder biopsy (S. haematobium)
• Cervical/vaginal biopsy (FGS)
• Reserved for cases with negative microscopy but high suspicion

STEP 4: ASSESSMENT OF COMPLICATIONS

Urogenital Schistosomiasis:
• Urinalysis: Hematuria (micro or macro), proteinuria
• Urine culture: Exclude secondary bacterial infection
• Ultrasound: Bladder wall thickening, calcification, hydronephrosis
• Cystoscopy: Sandy patches, ulcers, masses (if cancer suspected)
• IVP/CT Urogram: Ureteral involvement (if available)

Hepatosplenic Schistosomiasis:
• Liver function tests: Usually normal until late; elevated in decompensation
• Complete blood count: Anemia, thrombocytopenia (hypersplenism), eosinophilia
• Abdominal ultrasound: Periportal fibrosis (characteristic pattern), splenomegaly, ascites
• Upper GI endoscopy: Esophageal/gastric varices
• Doppler ultrasound: Portal vein flow, portal hypertension assessment

WHO ULTRASOUND CLASSIFICATION (Hepatosplenic Disease):
• Image patterns A-F based on periportal fibrosis severity
• Standardized for epidemiological surveys
• Correlates with disease severity and portal hypertension risk
    `,

    differentialDiagnosis: [
      'Urinary tract infection - Dysuria, frequency, but pyuria rather than isolated hematuria; responds to antibiotics',
      'Bladder cancer (non-schistosomal) - Hematuria, older age, smoking history; requires cystoscopy and biopsy',
      'Renal stones - Colicky pain, hematuria, stones on imaging; no eggs in urine',
      'Glomerulonephritis - Hematuria with RBC casts, proteinuria, hypertension, renal impairment',
      'Tuberculosis (genitourinary) - Sterile pyuria, calcification, AFB on special stains',
      'Sexually transmitted infections - Genital symptoms overlap with FGS; test for STIs',
      'Cervical cancer - Genital bleeding, cervical mass; requires biopsy',
      'Cirrhosis (other causes) - Hepatomegaly, portal hypertension; different history and liver function pattern',
      'Hepatitis B/C - Hepatomegaly, abnormal LFTs, positive serology',
      'Portal vein thrombosis - Portal hypertension without hepatic fibrosis; Doppler diagnosis',
      'Inflammatory bowel disease - Bloody diarrhea, but no eosinophilia; colonoscopy findings differ',
      'Amebic colitis - Bloody diarrhea; trophozoites on stool microscopy',
      'Colorectal cancer - Bloody stools, older age, mass on colonoscopy',
      'Tropical splenomegaly syndrome - Massive splenomegaly, high malaria antibodies, IgM elevation',
      'Visceral leishmaniasis - Hepatosplenomegaly, fever, pancytopenia; amastigotes on aspirate'
    ],

    investigations: [
      {
        name: 'Urine Microscopy (Sedimentation/Filtration)',
        indication: '🔴 ESSENTIAL for S. haematobium - Collect midday urine (10am-2pm); identify terminal-spined eggs; quantify intensity',
        availability: 'BASIC'
      },
      {
        name: 'Stool Microscopy (Kato-Katz)',
        indication: '🔴 ESSENTIAL for S. mansoni - WHO-recommended technique; identify lateral-spined eggs; quantify EPG; examine multiple samples',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis (Dipstick)',
        indication: '🔴 ESSENTIAL - Detects hematuria (even microscopic) and proteinuria; useful for screening in endemic areas',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Eosinophilia common (especially acute phase); anemia from chronic blood loss; thrombocytopenia if hypersplenism',
        availability: 'BASIC'
      },
      {
        name: 'POC-CCA Urine Test',
        indication: '🟠 IMPORTANT - Point-of-care test for S. mansoni; more sensitive than single stool exam; useful for MDA programs',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 ESSENTIAL for assessing complications - Bladder wall changes, hydronephrosis (S. haematobium); periportal fibrosis, splenomegaly (S. mansoni)',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT in hepatosplenic disease - Usually normal until late; albumin low in decompensation; helps exclude other liver diseases',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine',
        indication: '🟠 IMPORTANT in urogenital disease - Assess renal function; may be elevated with obstructive uropathy',
        availability: 'BASIC'
      },
      {
        name: 'Serology (ELISA)',
        indication: '🟡 USEFUL in travelers/non-endemic - High sensitivity; cannot distinguish active from past infection; not useful in endemic populations',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Upper GI Endoscopy',
        indication: '🟠 IMPORTANT if portal hypertension suspected - Identify esophageal/gastric varices; guide primary prophylaxis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Rectal Snip/Biopsy',
        indication: '🟡 USEFUL when stool negative - Demonstrates eggs in rectal mucosa; more sensitive than stool in light infections',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Cystoscopy',
        indication: '🟡 INDICATED if bladder cancer suspected - Sandy patches, ulcers, masses; biopsy suspicious lesions',
        availability: 'ADVANCED'
      },
      {
        name: 'Pelvic Examination/Colposcopy',
        indication: '🟠 IMPORTANT for FGS diagnosis - Sandy patches on cervix/vagina; contact bleeding; often missed',
        availability: 'BASIC'
      },
      {
        name: 'CT/MRI Abdomen',
        indication: '🟡 ADVANCED IMAGING - For complex cases; assess extent of fibrosis, exclude malignancy',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Urine or stool microscopy to identify eggs and species; assess infection intensity',
        '⚡ ASSESS FOR COMPLICATIONS: Ultrasound for bladder/kidney (S. haematobium) or liver/spleen (S. mansoni)',
        '⚡ TREAT WITH PRAZIQUANTEL: Initiate treatment once diagnosis confirmed; single dose usually sufficient',
        '⚡ TREAT ANEMIA: If hemoglobin low, provide iron supplementation and address nutritional deficiencies',
        '⚡ SCREEN HOUSEHOLD/COMMUNITY: Others with similar exposure likely infected; consider mass treatment',
        '⚡ ADDRESS ACUTE SYMPTOMS: Symptomatic treatment for pain, fever if present (especially Katayama syndrome)',
        '⚡ COUNSEL ON PREVENTION: Avoid freshwater contact in endemic areas; explain reinfection risk',
        '⚡ MANAGE COMPLICATIONS: Refer for specialist care if portal hypertension, varices, or suspected malignancy'
      ],
      
      pharmacological: [
        {
          drug: 'Praziquantel',
          indication: '🔴 ESSENTIAL - Drug of choice for all Schistosoma species; safe, effective, and affordable',
          dose: 'Standard: 40 mg/kg as single oral dose (can divide into 2 doses 4-6 hours apart to reduce side effects). S. japonicum/mekongi: 60 mg/kg divided. Mass Drug Administration: 40 mg/kg single dose. Administer with food to enhance absorption and reduce GI side effects.',
          route: 'PO',
          duration: 'Single dose for uncomplicated infection; may repeat in 2-4 weeks for heavy infections or if symptoms persist',
          contraindications: ['Ocular cysticercosis (theoretical concern - seizure risk)', 'First trimester pregnancy (defer treatment if possible, but treat if clinically indicated)'],
          sideEffects: ['Abdominal pain and cramping (common)', 'Nausea, vomiting', 'Headache', 'Dizziness, drowsiness', 'Urticaria', 'Fever (especially heavy infections - dying worms)', 'Usually mild and transient'],
          alternatives: ['Oxamniquine 15 mg/kg single dose (S. mansoni only, limited availability)', 'Metrifonate (S. haematobium only, largely unavailable)']
        },
        {
          drug: 'Corticosteroids (for Katayama Syndrome)',
          indication: '🟠 IMPORTANT - For severe acute schistosomiasis (Katayama fever) with marked symptoms',
          dose: 'Prednisolone 1-2 mg/kg/day (max 60 mg) for 5-7 days, then taper. Start before or with praziquantel to prevent worsening of symptoms.',
          route: 'PO',
          duration: '5-7 days with taper',
          contraindications: ['Active untreated infections', 'Uncontrolled diabetes'],
          sideEffects: ['Hyperglycemia', 'Insomnia', 'GI upset', 'Immunosuppression'],
          alternatives: ['Antihistamines for mild symptoms']
        },
        {
          drug: 'Iron and Folic Acid',
          indication: '🔴 ESSENTIAL for anemia - Schistosomiasis causes chronic iron deficiency anemia; treat concurrently',
          dose: 'Adults: Ferrous sulfate 200mg (65mg elemental iron) 2-3 times daily + Folic acid 5mg daily. Children: 3-6 mg/kg/day elemental iron. Continue for 3 months after hemoglobin normalizes.',
          route: 'PO',
          duration: '3-6 months',
          contraindications: ['Iron overload disorders'],
          sideEffects: ['Constipation', 'Nausea', 'Black stools', 'Abdominal discomfort'],
          alternatives: ['Parenteral iron if severe anemia or malabsorption']
        },
        {
          drug: 'Albendazole',
          indication: '🟠 IMPORTANT - Co-administer in MDA programs; treats concurrent soil-transmitted helminths common in same populations',
          dose: 'Adults and children >2 years: 400 mg single dose. Children 1-2 years: 200 mg single dose.',
          route: 'PO',
          duration: 'Single dose (with praziquantel in MDA)',
          contraindications: ['First trimester pregnancy'],
          sideEffects: ['Abdominal pain', 'Nausea', 'Headache', 'Elevated LFTs (rare)'],
          alternatives: ['Mebendazole 500 mg single dose']
        },
        {
          drug: 'Propranolol (Non-selective Beta-blocker)',
          indication: '🟠 IMPORTANT for portal hypertension - Primary prophylaxis against variceal bleeding in patients with varices',
          dose: 'Start 20 mg BD, titrate to reduce resting heart rate by 25% or to 55-60 bpm. Usual range: 40-160 mg BD.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Asthma', 'Severe bradycardia', 'Decompensated heart failure', 'Heart block'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue', 'Bronchospasm'],
          alternatives: ['Carvedilol', 'Nadolol', 'Endoscopic variceal ligation']
        },
        {
          drug: 'Metronidazole',
          indication: '🟡 IF SECONDARY INFECTION - For secondary bacterial infections or suspected amebiasis',
          dose: 'Adults: 400-500 mg TID for 7-10 days. Children: 7.5 mg/kg TID.',
          route: 'PO',
          duration: '7-10 days',
          contraindications: ['First trimester pregnancy', 'Alcohol use'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol'],
          alternatives: ['Tinidazole']
        },
        {
          drug: 'Antibiotics for UTI',
          indication: '🟡 IF SECONDARY UTI - S. haematobium predisposes to bacterial UTIs',
          dose: 'Based on local susceptibility patterns. Options: Ciprofloxacin, Nitrofurantoin, Trimethoprim-sulfamethoxazole.',
          route: 'PO',
          duration: '3-7 days depending on agent and severity',
          contraindications: ['Drug-specific'],
          sideEffects: ['Drug-specific'],
          alternatives: ['Culture-directed therapy']
        }
      ],
      
      nonPharmacological: [
        'HEALTH EDUCATION: Explain disease transmission through freshwater contact; importance of avoiding infested water bodies; completing treatment',
        'SAFE WATER ACCESS: Encourage use of safe water sources for drinking, bathing, washing; community water supply improvements',
        'SANITATION IMPROVEMENT: Proper latrine use to prevent egg contamination of water; WASH interventions critical',
        'SNAIL CONTROL: Environmental modification (draining, clearing vegetation); focal mollusciciding in high-transmission areas',
        'BEHAVIORAL CHANGE: Reduce water contact activities; use protective footwear; avoid peak transmission times',
        'COMMUNITY MDA PROGRAMS: School-based deworming; community-wide treatment in high-prevalence areas',
        'NUTRITIONAL SUPPORT: Adequate protein and calories; micronutrient supplementation for malnourished children',
        'MANAGEMENT OF FGS: Gynecological evaluation; treatment improves lesions; screen for STIs including HIV',
        'PORTAL HYPERTENSION MANAGEMENT: Beta-blockers for primary prophylaxis; endoscopic therapy for varices; avoid hepatotoxins',
        'FOLLOW-UP AFTER TREATMENT: Re-examine 4-6 weeks after treatment to confirm cure; repeat treatment if eggs persist',
        'SURVEILLANCE AND MAPPING: Identify high-risk communities; target interventions appropriately'
      ],
      
      surgical: [
        'ENDOSCOPIC VARICEAL LIGATION (EVL): Treatment of choice for acute variceal bleeding and secondary prophylaxis',
        'ENDOSCOPIC SCLEROTHERAPY: Alternative to banding for variceal management if EVL unavailable',
        'SPLENECTOMY: For massive splenomegaly with severe hypersplenism; increases infection risk',
        'PORTOSYSTEMIC SHUNT SURGERY: TIPS or surgical shunts for refractory variceal bleeding (if available)',
        'BLADDER SURGERY: Resection of bladder masses if cancer; ureteral reimplantation for strictures',
        'RECONSTRUCTIVE SURGERY: For severe ureteral strictures or bladder contracture'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Upper GI bleeding (hematemesis, melena) - Variceal bleeding emergency',
        '🔴 IMMEDIATE REFERRAL: Signs of liver decompensation - Ascites, hepatic encephalopathy, jaundice',
        '🔴 IMMEDIATE REFERRAL: Suspected bladder cancer - Hematuria with mass, weight loss',
        '🟠 URGENT REFERRAL: Portal hypertension with varices - Needs endoscopy and primary prophylaxis',
        '🟠 URGENT REFERRAL: Obstructive uropathy with renal impairment - Rising creatinine, hydronephrosis',
        '🟠 URGENT REFERRAL: Severe Katayama syndrome - High fever, respiratory symptoms, marked eosinophilia',
        '🟠 URGENT REFERRAL: Female genital schistosomiasis with severe symptoms - Needs gynecological evaluation',
        '🟡 ROUTINE REFERRAL: Chronic hepatosplenomegaly - For ultrasound staging and monitoring',
        '🟡 ROUTINE REFERRAL: Treatment failure after two courses - Persistent egg excretion despite treatment',
        '🟡 ROUTINE REFERRAL: Infertility in endemic area - FGS or male genital schistosomiasis evaluation',
        '🟡 REFERRAL FOR MDA PROGRAMS: Contact NTD program for community-wide treatment implementation'
      ],
      
      monitoring: [
        'POST-TREATMENT EVALUATION: Re-examine urine/stool 4-6 weeks after praziquantel; eggs should be absent or markedly reduced',
        'HEMOGLOBIN: Monitor anemia response to treatment and iron supplementation; recheck at 4-8 weeks',
        'RENAL FUNCTION: For S. haematobium with uropathy; monitor creatinine and repeat ultrasound',
        'LIVER ULTRASOUND: For hepatosplenic disease; assess fibrosis progression or regression annually',
        'VARICES SURVEILLANCE: Upper endoscopy every 2-3 years if portal hypertension; annually if varices present',
        'EOSINOPHIL COUNT: Should decrease after successful treatment; persistent elevation suggests ongoing infection',
        'GROWTH MONITORING: In children; assess for catch-up growth after treatment',
        'COMMUNITY PREVALENCE: Monitor sentinel sites to assess MDA program effectiveness',
        'RE-INFECTION SURVEILLANCE: In endemic areas, annual screening may be needed; re-treat as indicated'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

IMMEDIATE POST-TREATMENT (4-6 weeks):
• Repeat urine or stool microscopy to confirm cure
• Egg count should decrease by >90% or become negative
• If eggs persist at same intensity, consider treatment failure and repeat praziquantel
• Assess symptom improvement (hematuria, abdominal pain)

SHORT-TERM (3-6 months):
• Check hemoglobin - should improve with iron supplementation
• Repeat ultrasound if baseline abnormalities (bladder, liver)
• Reassess nutritional status in children
• Reinforce prevention messages

MEDIUM-TERM (6-12 months):
• In endemic areas, re-screen for reinfection
• Annual treatment may be needed in high-transmission settings
• Continue monitoring complications (portal hypertension, uropathy)
• Link to MDA programs if available

LONG-TERM (Annual or as indicated):
• Hepatosplenic disease: Annual ultrasound, endoscopy as indicated
• Urogenital disease: Annual urinalysis, ultrasound if previous pathology
• Growth monitoring in children until adulthood
• Cancer surveillance: S. haematobium - bladder cancer risk persists even after cure

MDA PROGRAM FOLLOW-UP:
• Community prevalence monitoring (sentinel sites)
• Treatment coverage assessment
• SAEs (Serious Adverse Events) surveillance
• Adjust program intensity based on prevalence:
  - High-risk (>50%): Annual treatment
  - Moderate (10-50%): Every 2 years
  - Low (<10%): Twice during primary school

CURE CRITERIA:
• No eggs detected on repeat examination
• Resolution of hematuria/symptoms
• Serological tests remain positive for years (not useful for cure)
• Note: Viable eggs may be excreted for weeks after treatment from dying worms

REINFECTION:
• Common in endemic areas with continued water contact
• Does not indicate treatment failure
• Requires ongoing prevention measures and periodic retreatment
• MDA programs aim to reduce morbidity, not necessarily achieve elimination
      `
    },

    redFlags: [
      '🔴 CRITICAL: Hematemesis or melena - Variceal bleeding from portal hypertension; life-threatening emergency',
      '🔴 CRITICAL: Hepatic encephalopathy - Confusion, asterixis in patient with hepatosplenomegaly; urgent management needed',
      '🔴 CRITICAL: Gross hematuria with clots - May indicate bladder mass/cancer or severe active infection',
      '🔴 CRITICAL: Anuria or severe oliguria - Complete urinary obstruction; needs urgent decompression',
      '🟠 URGENT: Severe Katayama syndrome - High fever, respiratory distress, severe eosinophilia; needs hospitalization',
      '🟠 URGENT: Rapidly enlarging spleen - Risk of splenic rupture; may indicate massive infection',
      '🟠 URGENT: Rising creatinine with hydronephrosis - Progressive obstructive uropathy needs intervention',
      '🟠 URGENT: New ascites - Indicates hepatic decompensation in chronic disease',
      '🟠 URGENT: Severe anemia (Hb <7 g/dL) - May need transfusion; investigate cause',
      '🟡 CAUTION: Persistent symptoms after two treatments - Consider treatment failure, reinfection, or alternative diagnosis',
      '🟡 CAUTION: Weight loss in chronic infection - Screen for malignancy, especially bladder cancer',
      '🟡 CAUTION: Recurrent UTIs in S. haematobium - May indicate structural damage requiring urological evaluation'
    ],

    complications: [
      'Bladder squamous cell carcinoma - S. haematobium increases risk 5-fold; occurs after years of chronic infection',
      'Portal hypertension - Symmer periportal fibrosis from S. mansoni; leads to varices and ascites',
      'Esophageal variceal bleeding - Life-threatening complication of portal hypertension',
      'Chronic kidney disease - From obstructive uropathy and repeated infections in S. haematobium',
      'Hydronephrosis and hydroureter - Ureteral obstruction from egg deposition and fibrosis',
      'Female genital schistosomiasis - Sandy patches, bleeding, infertility, increased HIV risk',
      'Male genital involvement - Hematospermia, prostatitis, reduced fertility',
      'Growth retardation and stunting - In children with chronic infection; partially reversible with treatment',
      'Anemia - Iron deficiency from chronic blood loss; contributes to fatigue and poor school performance',
      'Cognitive impairment - Associated with chronic infection and anemia in children',
      'Pulmonary hypertension - Rare; from egg embolization to lungs in hepatosplenic disease',
      'Spinal cord involvement (transverse myelitis) - Rare but serious; egg deposition in spinal cord',
      'Cor pulmonale - From chronic pulmonary schistosomiasis (rare)',
      'Secondary bacterial infections - UTIs in urogenital disease; SBP in hepatosplenic disease'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Treatment with praziquantel is highly effective (cure rate >85-90%)
• Early treatment prevents or reverses most pathology
• Advanced fibrosis (bladder, liver) may be irreversible
• Morbidity and mortality related to complications of chronic untreated disease
• Reinfection common in endemic areas; does not indicate treatment failure

PROGNOSIS BY DISEASE STAGE:

Acute Schistosomiasis (Katayama):
• Usually self-limiting with supportive care
• Responds well to praziquantel + corticosteroids
• Full recovery expected in most cases
• Rarely fatal in severe cases

Early Urogenital Disease:
• Excellent prognosis with treatment
• Hematuria resolves
• Bladder lesions regress
• Renal function preserved

Advanced Urogenital Disease:
• Bladder calcification and fibrosis may persist
• Obstructive uropathy may require intervention
• CKD may be irreversible
• Bladder cancer risk persists even after treatment

Intestinal Schistosomiasis:
• Good prognosis with treatment
• Symptoms resolve
• Minimal long-term sequelae if treated early

Hepatosplenic Schistosomiasis:
• Fibrosis may regress partially with treatment
• Portal hypertension may persist
• Variceal bleeding: 30-50% mortality if untreated
• With proper management: reasonable long-term survival
• Liver function usually preserved (unlike cirrhosis)

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis and treatment
• Younger age
• Lower infection intensity
• Access to follow-up care
• Avoidance of reinfection
• MDA program coverage

Worse Outcomes:
• Delayed treatment
• Heavy infection intensity
• Advanced fibrosis at presentation
• Continued high-intensity reinfection
• Complications (varices, cancer, CKD)
• Poor access to healthcare
• Co-infections (HIV, HBV, HCV)

MORTALITY:
• Direct mortality relatively low
• Deaths mainly from variceal bleeding, renal failure, and bladder cancer
• Estimated 200,000+ deaths annually attributed to schistosomiasis globally
• Significant contribution to DALYs through disability
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

WHO STRATEGIC APPROACH (Multi-pronged):

1. PREVENTIVE CHEMOTHERAPY (Mass Drug Administration):

School-Based Deworming:
• Target school-age children (5-14 years) - highest infection intensity
• Praziquantel 40 mg/kg single dose
• Combine with albendazole for STH
• Frequency based on prevalence:
  - High-risk (>50%): Annual treatment
  - Moderate (10-50%): Every 2 years
  - Low (<10%): Twice during primary school

Community-Wide Treatment:
• In high-risk communities where adults also heavily infected
• Include high-risk occupational groups (fishermen, farmers, car washers)
• Treat pregnant and lactating women (praziquantel is safe)

2. WATER, SANITATION, AND HYGIENE (WASH):

Safe Water Supply:
• Piped water or protected wells reduce need for contact with infested water
• Water storage for 48-72 hours kills cercariae
• Filtration or chlorination of water

Sanitation:
• Latrine construction and use prevents egg contamination of water
• Community-led total sanitation (CLTS) programs
• Proper sewage management

Hygiene Education:
• Reduce water contact behaviors
• Use of protective footwear and clothing
• Behavior change communication

3. SNAIL CONTROL:

Environmental Management:
• Drainage of snail habitats
• Clearing vegetation from water bodies
• Proper irrigation system design

Biological Control:
• Introduction of competitor snails or predators (limited success)
• Fish that eat snails

Chemical Control (Mollusciciding):
• Niclosamide or other molluscicides
• Focal application in high-transmission areas
• Environmental concerns limit widespread use

4. HEALTH EDUCATION:

Key Messages:
• How schistosomiasis is transmitted
• Signs and symptoms to recognize
• Importance of treatment
• Avoiding contact with infested freshwater
• Using latrines properly
• Seeking treatment for hematuria or bloody stools

Target Groups:
• School children
• Women (water collection, washing activities)
• High-risk occupations
• Community leaders

5. INDIVIDUAL PREVENTION (Travelers/Visitors):

• Avoid freshwater swimming, wading, bathing in endemic areas
• Vigorous towel drying after accidental exposure (limited value)
• No effective chemoprophylaxis available
• Seek evaluation if exposure in endemic area

INTEGRATED APPROACH:

• Combine all strategies for maximum impact
• MDA alone will not achieve elimination - needs WASH and snail control
• Community engagement essential for sustainability
• Strong surveillance to monitor progress
• WHO goal: Elimination as public health problem by 2030
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS SCHISTOSOMIASIS (BILHARZIA)?

Schistosomiasis, also called bilharzia, is a disease caused by tiny worms that live in your blood vessels. You get infected by swimming, bathing, or washing in freshwater (rivers, lakes, ponds) where the worms live. The disease is very common in Africa and can cause serious health problems if not treated.

HOW DO YOU GET SCHISTOSOMIASIS?

The lifecycle involves:
1. Infected people urinate or defecate in or near fresh water
2. Worm eggs hatch and infect certain types of snails
3. The snails release tiny worms (cercariae) into the water
4. These worms burrow through your skin when you enter the water
5. The worms grow inside your blood vessels and produce eggs
6. The eggs cause damage to your bladder, intestines, or liver

YOU CANNOT GET SCHISTOSOMIASIS FROM:
• Salt water (ocean, sea)
• Chlorinated swimming pools
• Drinking contaminated water (though you should not drink it)
• Person-to-person contact

WHAT ARE THE SYMPTOMS?

Blood in Urine (Urinary Schistosomiasis - S. haematobium):
• Blood at the end of urinating (especially in children)
• Painful or frequent urination
• Pain in the lower belly

Stomach Problems (Intestinal Schistosomiasis - S. mansoni):
• Blood in stool
• Stomach pain and cramping
• Diarrhea
• Swollen liver and spleen (big belly)

In Children:
• Poor growth
• Tiredness and weakness
• Difficulty concentrating in school

HOW IS SCHISTOSOMIASIS TREATED?

• Medicine called PRAZIQUANTEL cures the infection
• Usually just ONE DOSE is needed
• The medicine is safe for children and pregnant women
• Take the medicine with food
• You may have temporary stomach upset or headache (this is normal)
• Come back for a check-up to make sure you are cured

IMPORTANT: The medicine kills the worms but does not prevent you from getting infected again. You must continue to avoid infected water.

HOW TO PREVENT SCHISTOSOMIASIS:

1. AVOID INFESTED WATER:
   • Do not swim, bathe, or wash in rivers, lakes, or ponds in endemic areas
   • Use tap water or water from a well for washing and bathing
   
2. USE SAFE WATER:
   • Fetch water from safe sources (taps, protected wells)
   • If you must use river/lake water, store it for 3 days before use (worms die)
   • Boiling water kills the worms
   
3. USE LATRINES:
   • Always use a latrine or toilet
   • Never urinate or defecate in or near water
   • This prevents the eggs from reaching the water
   
4. PARTICIPATE IN DEWORMING PROGRAMS:
   • Take the medicine when health workers offer it
   • Make sure your children receive treatment at school
   
5. VACCINATE YOUR CHILDREN (for the future):
   • A vaccine is being developed but not yet available

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only and is not a substitute for professional medical advice, diagnosis, or treatment. If you have blood in your urine or stool, or if you live in an area with schistosomiasis, please see a healthcare provider.

Early treatment prevents serious complications. Do not ignore blood in your urine.

QUESTIONS TO ASK YOUR DOCTOR:
• Do I need to be tested for schistosomiasis?
• How many doses of medicine will I need?
• Should my family members be tested too?
• When should I come back for a follow-up test?
• What can I do to avoid getting infected again?
• Are there deworming programs in my community?
    `,

    references: [
      'World Health Organization. Schistosomiasis: WHO Fact Sheet. Geneva: WHO; 2023.',
      'World Health Organization. WHO guideline on control and elimination of human schistosomiasis. Geneva: WHO; 2022.',
      'Colley DG, Bustinduy AL, Secor WE, King CH. Human schistosomiasis. Lancet. 2014;383(9936):2253-2264.',
      'McManus DP, Dunne DW, Sacko M, et al. Schistosomiasis. Nature Reviews Disease Primers. 2018;4(1):13.',
      'King CH. Parasites and poverty: the case of schistosomiasis. Acta Tropica. 2010;113(2):95-104.',
      'Kjetland EF, Leutscher PDC, Ndhlovu PD. A review of female genital schistosomiasis. Trends in Parasitology. 2012;28(2):58-65.',
      'Gryseels B, Polman K, Clerinx J, Kestens L. Human schistosomiasis. Lancet. 2006;368(9541):1106-1118.',
      'Bustinduy AL, King CH. Schistosomiasis. In: Farrar J, et al., eds. Manson Tropical Diseases. 23rd ed. Elsevier; 2014:698-725.',
      'Ross AG, Bartley PB, Sleigh AC, et al. Schistosomiasis. New England Journal of Medicine. 2002;346(16):1212-1220.',
      'Vennervald BJ, Dunne DW. Morbidity in schistosomiasis: an update. Current Opinion in Infectious Diseases. 2004;17(5):439-447.',
      'World Health Organization. Preventive chemotherapy in human helminthiasis. Geneva: WHO; 2006.',
      'Hotez PJ, Kamath A. Neglected tropical diseases in sub-Saharan Africa: review of their prevalence, distribution, and disease burden. PLoS Neglected Tropical Diseases. 2009;3(8):e412.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 54. SOIL-TRANSMITTED HELMINTHIASIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '54',
    name: 'Soil-Transmitted Helminthiasis',
    slug: 'soil-transmitted-helminthiasis',
    specialty: 'Infectious Disease',
    bodySystem: 'Gastrointestinal',
    icdCode: 'B76.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
Soil-transmitted helminthiasis (STH) refers to a group of parasitic infections caused by nematodes (roundworms) that are transmitted through contact with contaminated soil. The four main species causing human disease are: Ascaris lumbricoides (roundworm), Trichuris trichiura (whipworm), Ancylostoma duodenale (Old World hookworm), and Necator americanus (New World hookworm). These are among the most common infections worldwide, affecting over 1.5 billion people, predominantly in tropical and subtropical regions with poor sanitation.

STH infections are classified as Neglected Tropical Diseases (NTDs) by WHO. While individual infections may cause minimal symptoms, the cumulative burden of chronic infection, especially in children, leads to significant morbidity including anemia, malnutrition, growth stunting, and cognitive impairment. Hookworm is the leading cause of anemia worldwide, and heavy Ascaris infections can cause life-threatening intestinal obstruction.

Africa bears a substantial portion of the global STH burden, with hundreds of millions at risk. The cornerstone of control is mass drug administration (MDA) with albendazole or mebendazole, particularly through school-based deworming programs, combined with improved water, sanitation, and hygiene (WASH). These infections are entirely preventable with adequate sanitation and treatable with safe, inexpensive, single-dose medications.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 HOOKWORM IS THE LEADING INFECTIOUS CAUSE OF ANEMIA - Iron deficiency anemia from chronic intestinal blood loss; treat hookworm AND the anemia

🔴 ASCARIS CAN CAUSE INTESTINAL OBSTRUCTION - Heavy worm burden, especially in children, can cause life-threatening bowel obstruction requiring surgery

🔴 ALBENDAZOLE 400mg SINGLE DOSE treats all three major STH - Safe, effective, and the drug of choice for MDA programs

🔴 STOOL MICROSCOPY CONFIRMS DIAGNOSIS - Kato-Katz technique identifies characteristic eggs; quantify to assess intensity

🔴 REINFECTION IS RAPID WITHOUT SANITATION - Treatment alone is not enough; WASH interventions are essential for sustained control

🔴 SCHOOL-AGE CHILDREN ARE PRIORITY TARGET - Highest intensity infections; school-based deworming is most cost-effective intervention

🔴 MIXED INFECTIONS ARE COMMON - Patients often have multiple STH species; single-dose albendazole covers all

🔴 CHRONIC INFECTION STUNTS GROWTH AND COGNITION - Even moderate infections affect child development; treat early and repeatedly
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• 1.5 billion people infected worldwide (approximately 24% of world population)
• Most prevalent in tropical and subtropical regions with poor sanitation
• Ascaris lumbricoides: 800+ million infected
• Trichuris trichiura: 450+ million infected
• Hookworm (Ancylostoma/Necator): 450+ million infected
• 200,000+ deaths annually attributed to STH
• 5+ million DALYs lost annually
• WHO classifies as Neglected Tropical Diseases (NTDs)

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa: 200+ million people infected
• Countries with highest burden: Nigeria, Democratic Republic of Congo, Ethiopia, Tanzania, Kenya
• Prevalence can exceed 50-80% in endemic communities
• Often co-endemic with schistosomiasis
• Hookworm predominant in many areas (warm, moist conditions)
• School-age children (5-14 years) bear heaviest burden
• Preschool-age children (1-4 years) increasingly recognized as at-risk group
• Pregnant women: hookworm contributes significantly to maternal anemia

SPECIES-SPECIFIC EPIDEMIOLOGY:

Ascaris lumbricoides (Roundworm):
• Largest human intestinal nematode (15-35 cm)
• Transmission: Ingestion of eggs from contaminated soil, food, water
• Peak intensity in children 5-10 years
• Eggs survive in soil for years

Trichuris trichiura (Whipworm):
• Transmission: Ingestion of eggs from contaminated soil
• Often co-infected with Ascaris (similar transmission)
• Peak intensity in older children/adolescents
• Causes rectal prolapse in heavy infections

Hookworm (Ancylostoma duodenale and Necator americanus):
• Transmission: Larvae penetrate skin from contaminated soil
• Walking barefoot in contaminated areas main risk
• Ancylostoma: Also transmitted orally; causes more blood loss
• Necator: More common in Africa
• Peak intensity in adults (occupational exposure)
• Major cause of anemia in pregnancy

RISK FACTORS:
• Living in endemic areas with poor sanitation
• Lack of access to improved sanitation (open defecation)
• Lack of access to safe water
• Walking barefoot (hookworm)
• Poor hand hygiene
• Eating unwashed vegetables/contaminated food (Ascaris, Trichuris)
• Agricultural work with soil contact
• Children (pica, playing in soil)
• Poverty and rural residence
• Warm, moist climate
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

GENERAL PRINCIPLES:
• Light infections: Often asymptomatic
• Heavy infections: Significant morbidity
• Chronic effects: Anemia, malnutrition, growth retardation, cognitive impairment
• Mixed infections common; symptoms may overlap

ASCARIASIS (Ascaris lumbricoides):

Intestinal Phase:
• Light infection: Usually asymptomatic
• Heavy infection: Vague abdominal discomfort, nausea
• Visible worms in stool (large, pink-white, 15-35 cm)
• Biliary ascariasis: RUQ pain, cholangitis, pancreatitis (worms in bile ducts)
• Intestinal obstruction: Abdominal distension, vomiting, obstipation (surgical emergency)
• Appendicitis from worm migration

Pulmonary Phase (Loeffler Syndrome):
• Occurs 1-2 weeks after egg ingestion (larval migration through lungs)
• Dry cough, wheeze
• Dyspnea
• Fever
• Urticaria
• Marked eosinophilia
• Transient pulmonary infiltrates on chest X-ray

TRICHURIASIS (Trichuris trichiura):

Light Infection:
• Usually asymptomatic

Moderate-Heavy Infection:
• Chronic diarrhea (may be bloody/mucoid)
• Abdominal pain and cramping
• Tenesmus
• Flatulence
• Nausea

Severe Infection (Trichuris Dysentery Syndrome):
• Chronic bloody diarrhea
• Rectal prolapse (classic sign in children with heavy infection)
• Iron deficiency anemia
• Finger clubbing
• Growth retardation
• Cognitive impairment

HOOKWORM DISEASE (Ancylostoma and Necator):

Cutaneous Phase:
• Ground itch - pruritic papular rash at site of larval penetration
• Usually feet (walking barefoot)
• Self-limiting within 1-2 weeks

Pulmonary Phase:
• Usually mild or asymptomatic
• Occasional cough, wheeze (less severe than Ascaris)

Intestinal Phase:
• Epigastric pain (may mimic peptic ulcer)
• Nausea, vomiting
• Diarrhea or constipation
• Pica (unusual cravings - eating soil, ice)

Chronic Hookworm Disease:
• Iron deficiency anemia - MOST IMPORTANT MANIFESTATION
  - Fatigue, weakness
  - Pallor
  - Dyspnea on exertion
  - Palpitations
  - Can be severe (Hb <5 g/dL)
• Protein-losing enteropathy
• Hypoalbuminemia and edema
• Growth retardation in children
• Cognitive impairment
• Adverse pregnancy outcomes (anemia, low birth weight, maternal mortality)

PHYSICAL EXAMINATION:
• General: May appear well or show signs of malnutrition, anemia
• Growth: Plot on growth chart - stunting, wasting in chronic infection
• Pallor: Conjunctival, palmar pallor (anemia, especially hookworm)
• Abdomen: 
  - May be distended
  - Ascaris obstruction: Distension, visible peristalsis, palpable worm mass
  - Diffuse tenderness
• Rectal exam: Trichuris - may feel worms; rectal prolapse
• Skin: Ground itch rash (hookworm)
• Extremities: Edema (hypoalbuminemia)
• Nails: Koilonychia (spoon nails) in chronic iron deficiency
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
Consider STH in:
• Anyone in endemic area, especially children
• Unexplained iron deficiency anemia (hookworm)
• Chronic GI symptoms in endemic area
• Growth retardation or failure to thrive
• Visible worms passed in stool (Ascaris)
• Rectal prolapse in child (Trichuris)
• Eosinophilia (especially during migration phase)

STEP 2: STOOL MICROSCOPY (Gold Standard)

Kato-Katz Technique (WHO Recommended):
• Semi-quantitative method
• Identifies and counts eggs
• Single sample detects moderate-heavy infections
• Multiple samples (3 on different days) increase sensitivity for light infections
• Used for prevalence surveys and MDA monitoring

Direct Wet Mount:
• Quick, simple method
• Lower sensitivity than Kato-Katz
• May miss light infections
• Can see eggs and sometimes larvae (hookworm)

Egg Identification:

Ascaris lumbricoides eggs:
• Oval/round, 45-75 micrometers
• Thick, mammillated outer shell
• Bile-stained (brown)
• Fertilized and unfertilized forms

Trichuris trichiura eggs:
• Barrel/lemon-shaped, 50-55 micrometers
• Bipolar mucous plugs (clear ends)
• Brown, smooth shell

Hookworm eggs (Ancylostoma/Necator similar):
• Oval, 60-75 micrometers
• Thin, colorless shell
• Contain developing morula or larvae
• Cannot differentiate species by egg alone

STEP 3: QUANTIFY INFECTION INTENSITY

WHO Intensity Classification (eggs per gram of feces - EPG):

Ascaris lumbricoides:
• Light: 1-4,999 EPG
• Moderate: 5,000-49,999 EPG
• Heavy: ≥50,000 EPG

Trichuris trichiura:
• Light: 1-999 EPG
• Moderate: 1,000-9,999 EPG
• Heavy: ≥10,000 EPG

Hookworm:
• Light: 1-1,999 EPG
• Moderate: 2,000-3,999 EPG
• Heavy: ≥4,000 EPG

STEP 4: ASSESS FOR COMPLICATIONS

Complete Blood Count:
• Hemoglobin: Assess anemia severity (hookworm)
• MCV, MCH: Microcytic, hypochromic (iron deficiency)
• Eosinophilia: Common, especially during migration

Iron Studies (if available):
• Serum ferritin: Low in iron deficiency
• Serum iron, TIBC

Serum Albumin:
• Low in protein-losing enteropathy (hookworm)

Chest X-ray:
• Transient infiltrates in Loeffler syndrome (Ascaris, hookworm migration)

Abdominal X-ray/Ultrasound:
• Ascaris obstruction: Dilated loops, air-fluid levels, worm mass
• Biliary ascariasis: Worms in bile ducts on ultrasound

SPECIAL CONSIDERATIONS:

Strongyloides stercoralis:
• Often grouped with STH; important to consider
• Larvae (not eggs) in stool
• Risk of hyperinfection in immunocompromised
• Requires different treatment (ivermectin)
    `,

    differentialDiagnosis: [
      'Iron deficiency anemia (nutritional) - Similar findings, but no eggs in stool; dietary history',
      'Other causes of anemia (malaria, sickle cell, thalassemia) - Blood smear, hemoglobin electrophoresis distinguish',
      'Peptic ulcer disease - Epigastric pain; endoscopy shows ulcer; H. pylori testing',
      'Inflammatory bowel disease - Bloody diarrhea, but no eggs; colonoscopy findings differ',
      'Amebic dysentery - Bloody diarrhea; trophozoites on microscopy',
      'Schistosomiasis - Bloody stool (S. mansoni); lateral-spined eggs distinct from STH eggs',
      'Intestinal obstruction (other causes) - No worm mass; different history',
      'Rectal prolapse (other causes) - No Trichuris eggs; consider other etiologies in low-endemic areas',
      'Allergic asthma - Wheeze, but chronic; no pulmonary infiltrates; no eosinophilia resolution',
      'Strongyloidiasis - Larvae not eggs in stool; may coexist; consider in immunocompromised',
      'Malabsorption syndromes - Weight loss, diarrhea; no eggs; other tests abnormal',
      'Protein-energy malnutrition - Similar growth failure; often coexists with STH',
      'Intestinal tuberculosis - Chronic symptoms; AFB testing; imaging findings',
      'Giardiasis - Diarrhea, malabsorption; cysts/trophozoites on microscopy',
      'Tropical sprue - Malabsorption, villous atrophy; responds to antibiotics/folate'
    ],

    investigations: [
      {
        name: 'Stool Microscopy (Kato-Katz Technique)',
        indication: '🔴 ESSENTIAL - Gold standard; identifies and quantifies eggs; examine multiple samples for best sensitivity',
        availability: 'BASIC'
      },
      {
        name: 'Direct Stool Wet Mount',
        indication: '🔴 ESSENTIAL - Quick screening; can visualize eggs; lower sensitivity than Kato-Katz',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Assess for anemia (especially hookworm); eosinophilia common; baseline for treatment',
        availability: 'BASIC'
      },
      {
        name: 'Peripheral Blood Smear',
        indication: '🟠 IMPORTANT - Microcytic hypochromic picture in iron deficiency; eosinophilia; exclude malaria',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin/Hematocrit',
        indication: '🔴 ESSENTIAL - Screen for anemia; particularly important in hookworm, pregnancy',
        availability: 'BASIC'
      },
      {
        name: 'Serum Ferritin',
        indication: '🟠 IMPORTANT if available - Confirms iron deficiency; guides iron supplementation duration',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Albumin',
        indication: '🟠 IMPORTANT in severe infection - Low in protein-losing enteropathy from heavy hookworm',
        availability: 'BASIC'
      },
      {
        name: 'Stool Occult Blood Test',
        indication: '🟡 SUPPORTIVE - Positive in hookworm (chronic blood loss)',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal X-ray',
        indication: '🔴 ESSENTIAL if obstruction suspected - Air-fluid levels, dilated loops; may show worm mass',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🟠 IMPORTANT for Ascaris complications - Biliary ascariasis (worms in bile ducts), worm bolus',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 IMPORTANT if pulmonary symptoms - Transient infiltrates in Loeffler syndrome',
        availability: 'BASIC'
      },
      {
        name: 'Stool PCR',
        indication: '🟡 RESEARCH/REFERENCE - Highly sensitive; species identification; not needed for routine care',
        availability: 'ADVANCED'
      },
      {
        name: 'Growth Assessment',
        indication: '🔴 ESSENTIAL in children - Height, weight, MUAC; plot on growth charts; assess stunting/wasting',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Stool microscopy to identify eggs and quantify intensity',
        '⚡ ASSESS FOR COMPLICATIONS: Check hemoglobin, look for signs of obstruction (Ascaris) or prolapse (Trichuris)',
        '⚡ TREAT WITH ANTHELMINTIC: Albendazole 400mg single dose (first-line) or Mebendazole 500mg single dose',
        '⚡ TREAT ANEMIA: Iron supplementation if anemic; transfusion if severe (Hb <5 g/dL with symptoms)',
        '⚡ MANAGE COMPLICATIONS: Ascaris obstruction may need conservative management first; surgery if fails',
        '⚡ NUTRITIONAL SUPPORT: Address malnutrition; high-protein, iron-rich diet; vitamin supplementation',
        '⚡ TREAT FAMILY/HOUSEHOLD: Screen and treat others in household; shared environment = shared infection',
        '⚡ COUNSEL ON PREVENTION: Sanitation, handwashing, footwear (hookworm), food safety'
      ],
      
      pharmacological: [
        {
          drug: 'Albendazole',
          indication: '🔴 FIRST-LINE - Drug of choice for all STH; used in MDA programs; broad spectrum',
          dose: 'Standard (all STH): 400 mg single oral dose. Children 12-24 months: 200 mg single dose. For heavy Trichuris or hookworm: 400 mg daily for 3 days improves cure rate. Take with fatty food to enhance absorption.',
          route: 'PO',
          duration: 'Single dose for MDA; 3 days for heavy/symptomatic infection',
          contraindications: ['First trimester pregnancy (theoretical teratogenicity - but WHO approves in 2nd/3rd trimester and MDA)', 'Hypersensitivity to benzimidazoles'],
          sideEffects: ['Generally well tolerated', 'Mild GI upset (nausea, abdominal pain)', 'Headache', 'Dizziness', 'Transient hair loss (rare)', 'Elevated LFTs (rare, with prolonged use)'],
          alternatives: ['Mebendazole 500 mg single dose', 'Pyrantel pamoate 10 mg/kg single dose (pregnancy-safe)']
        },
        {
          drug: 'Mebendazole',
          indication: '🔴 ALTERNATIVE FIRST-LINE - Equally effective; used in MDA programs; interchangeable with albendazole',
          dose: 'Single dose regimen: 500 mg single dose. Alternative: 100 mg twice daily for 3 days (higher efficacy for Trichuris and hookworm). For Ascaris: single dose highly effective.',
          route: 'PO',
          duration: 'Single dose or 3 days',
          contraindications: ['First trimester pregnancy', 'Hypersensitivity'],
          sideEffects: ['Abdominal pain', 'Diarrhea', 'Generally well tolerated'],
          alternatives: ['Albendazole', 'Pyrantel pamoate']
        },
        {
          drug: 'Pyrantel Pamoate',
          indication: '🟠 ALTERNATIVE - Safe in pregnancy (all trimesters); good for Ascaris and hookworm; less effective for Trichuris',
          dose: '10-11 mg/kg (max 1g) single dose. For hookworm: may repeat for 3 days. Poorly absorbed - acts locally in gut.',
          route: 'PO',
          duration: 'Single dose (Ascaris); 3 days (hookworm)',
          contraindications: ['Liver disease', 'Hypersensitivity'],
          sideEffects: ['GI upset', 'Headache', 'Dizziness', 'Rash'],
          alternatives: ['Albendazole (if not pregnant)']
        },
        {
          drug: 'Levamisole',
          indication: '🟡 ALTERNATIVE - Effective for Ascaris; less available; not first choice',
          dose: 'Adults: 150 mg single dose. Children: 2.5 mg/kg single dose.',
          route: 'PO',
          duration: 'Single dose',
          contraindications: ['Blood dyscrasias', 'Hypersensitivity'],
          sideEffects: ['Nausea', 'Abdominal pain', 'Rare agranulocytosis'],
          alternatives: ['Albendazole', 'Mebendazole']
        },
        {
          drug: 'Ivermectin',
          indication: '🟠 ADJUNCT - Not first-line for classic STH; excellent for Strongyloides (often co-endemic)',
          dose: '200 mcg/kg single dose. Repeat in 2 weeks for Strongyloides.',
          route: 'PO',
          duration: 'Single dose; repeat for Strongyloides',
          contraindications: ['Pregnancy', 'Breastfeeding (first week of life)', 'Children <15 kg or <5 years', 'Loa loa co-infection (risk of encephalopathy)'],
          sideEffects: ['Dizziness', 'Nausea', 'Pruritus', 'Mazzotti reaction (in filariasis)'],
          alternatives: ['Albendazole for Strongyloides (less effective)']
        },
        {
          drug: 'Ferrous Sulfate',
          indication: '🔴 ESSENTIAL for hookworm anemia - Treat iron deficiency; continue for 3 months after Hb normalizes',
          dose: 'Adults: 200 mg (65 mg elemental iron) 2-3 times daily. Children: 3-6 mg/kg/day elemental iron in 2-3 divided doses. Take between meals for better absorption (with vitamin C if tolerated).',
          route: 'PO',
          duration: '3-6 months (continue 3 months after Hb normalizes to replenish stores)',
          contraindications: ['Iron overload', 'Hemochromatosis', 'Repeated transfusions'],
          sideEffects: ['Nausea', 'Constipation', 'Black stools', 'Abdominal discomfort'],
          alternatives: ['Ferrous fumarate', 'Ferrous gluconate', 'Parenteral iron (severe cases)']
        },
        {
          drug: 'Folic Acid',
          indication: '🟠 IMPORTANT - Often co-deficient with iron; essential in pregnancy',
          dose: 'Adults: 5 mg daily. Children: 1-5 mg daily depending on age.',
          route: 'PO',
          duration: 'With iron supplementation; throughout pregnancy',
          contraindications: ['Untreated B12 deficiency (may mask neurological damage)'],
          sideEffects: ['Generally well tolerated'],
          alternatives: ['Dietary folate']
        },
        {
          drug: 'Piperazine',
          indication: '🟡 SPECIFIC USE - For Ascaris intestinal obstruction; paralyzes worms for expulsion',
          dose: '75 mg/kg (max 3.5 g) daily for 2 days. Given via NG tube if vomiting.',
          route: 'PO or via NG tube',
          duration: '2 days (for obstruction)',
          contraindications: ['Epilepsy', 'Renal impairment', 'Hypersensitivity'],
          sideEffects: ['Nausea', 'Vomiting', 'Abdominal cramps', 'Neurological effects (rare)'],
          alternatives: ['Conservative management; proceed to surgery if fails']
        },
        {
          drug: 'Hyoscine Butylbromide (Buscopan)',
          indication: '🟡 SUPPORTIVE - Antispasmodic for abdominal cramps and Ascaris obstruction management',
          dose: 'Adults: 10-20 mg IV/IM or 10 mg PO TID. Children: 0.3-0.6 mg/kg.',
          route: 'PO, IV, or IM',
          duration: 'As needed',
          contraindications: ['Glaucoma', 'Myasthenia gravis', 'Paralytic ileus'],
          sideEffects: ['Dry mouth', 'Tachycardia', 'Urinary retention'],
          alternatives: ['Other antispasmodics']
        }
      ],
      
      nonPharmacological: [
        'HEALTH EDUCATION: Explain transmission (fecal-oral for Ascaris/Trichuris; skin penetration for hookworm); emphasize prevention',
        'HANDWASHING: Wash hands with soap before eating, after defecation; critical for Ascaris/Trichuris prevention',
        'FOOTWEAR: Always wear shoes/sandals, especially in hookworm-endemic areas; prevents larval skin penetration',
        'SANITATION: Use latrines; avoid open defecation; proper disposal of human waste prevents soil contamination',
        'SAFE WATER AND FOOD: Wash vegetables thoroughly; drink safe water; cook food properly',
        'SCHOOL-BASED DEWORMING: Participate in MDA programs; ensure children receive treatment',
        'NUTRITIONAL REHABILITATION: High-protein, iron-rich diet; eggs, meat, beans, dark leafy vegetables; vitamin C enhances iron absorption',
        'ENVIRONMENTAL HYGIENE: Keep living areas clean; prevent soil contamination near homes',
        'ASCARIS OBSTRUCTION MANAGEMENT: NPO, NG decompression, IV fluids; piperazine or mineral oil; surgery if conservative fails',
        'RECTAL PROLAPSE MANAGEMENT (Trichuris): Manual reduction; treat underlying infection; refer if recurrent',
        'COMMUNITY ENGAGEMENT: Involve community leaders, teachers in health education and MDA programs'
      ],
      
      surgical: [
        'LAPAROTOMY FOR ASCARIS OBSTRUCTION: If conservative management fails (24-48 hours); manual extraction of worm bolus; resection if gangrene',
        'ENTEROTOMY: To remove impacted worm mass if milking fails',
        'APPENDECTOMY: If Ascaris causes appendicitis',
        'ERCP/SURGERY FOR BILIARY ASCARIASIS: Endoscopic extraction if available; surgical exploration if refractory',
        'RECTAL PROLAPSE REPAIR: If medical management and deworming fail; rare need for surgery'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Intestinal obstruction (Ascaris) - Abdominal distension, vomiting, absent bowel sounds; surgical emergency',
        '🔴 IMMEDIATE REFERRAL: Severe anemia (Hb <5 g/dL) with symptoms - May need transfusion',
        '🔴 IMMEDIATE REFERRAL: Biliary/pancreatic ascariasis - RUQ pain, jaundice, cholangitis',
        '🔴 IMMEDIATE REFERRAL: Rectal prolapse not reducing - Needs manual reduction and evaluation',
        '🟠 URGENT REFERRAL: Moderate-severe anemia in pregnancy - High-risk pregnancy management',
        '🟠 URGENT REFERRAL: Suspected perforation or peritonitis - Acute abdomen, fever, shock',
        '🟠 URGENT REFERRAL: Treatment failure after 2 courses - Persistent eggs, consider alternative diagnosis',
        '🟡 ROUTINE REFERRAL: Severe growth retardation - Nutritional assessment and rehabilitation',
        '🟡 ROUTINE REFERRAL: Chronic symptoms despite treatment - Investigate other causes',
        '🟡 CONSULTATION: Pregnancy with STH - Safe treatment options, anemia management'
      ],
      
      monitoring: [
        'POST-TREATMENT STOOL EXAM: Repeat microscopy 2-4 weeks after treatment; eggs should be absent or markedly reduced',
        'HEMOGLOBIN: Recheck 4-8 weeks after treatment and iron supplementation; should improve',
        'GROWTH MONITORING: Weight and height at each visit; plot on growth charts; watch for catch-up growth',
        'CURE RATE ASSESSMENT: Single-dose albendazole cure rates: Ascaris >95%, Hookworm 70-80%, Trichuris 40-60%; may need repeat dosing',
        'REINFECTION: In endemic areas, reinfection occurs rapidly; periodic retreatment needed',
        'MDA COVERAGE: Monitor program coverage (target >75%); assess impact on prevalence',
        'IRON STORES: If ferritin available, continue iron until stores replenished (ferritin >30)',
        'DEVELOPMENTAL ASSESSMENT: In children; cognitive and school performance may improve with treatment'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INDIVIDUAL PATIENT FOLLOW-UP:

Week 2-4 After Treatment:
• Repeat stool microscopy (if symptomatic or heavy infection)
• Expected: Marked reduction or absence of eggs
• If eggs persist at same intensity, repeat treatment (3-day course)
• Check hemoglobin if anemic at baseline

Month 1-2:
• Assess symptom improvement
• Recheck hemoglobin (should be improving with iron)
• Weigh children - watch for catch-up growth
• Reinforce prevention messages

Month 3-6:
• In endemic areas, re-screen for reinfection
• Continue iron supplementation if anemia not resolved
• Growth monitoring in children

Ongoing:
• Annual deworming in endemic areas (via MDA or individual)
• Continue iron supplementation as needed
• Nutritional support

CURE RATES AND RETREATMENT:

Single-Dose Efficacy:
• Ascaris: Albendazole/Mebendazole >95% cure rate
• Hookworm: Albendazole 70-80%, Mebendazole 60-70%
• Trichuris: Albendazole 40-60%, Mebendazole 40-60%

If Persistent Infection:
• Ascaris: Single dose highly effective; repeat if needed
• Hookworm: 3-day course of albendazole 400 mg daily
• Trichuris: 3-day course of albendazole 400 mg daily OR albendazole-ivermectin combination

MASS DRUG ADMINISTRATION (MDA) PROGRAMS:

WHO Recommendations Based on Prevalence:
• High risk (>50% prevalence): Treat twice yearly
• Moderate risk (20-50%): Treat once yearly
• Low risk (<20%): Treat once every 2-3 years or case-by-case

Target Groups:
• Preschool children (12-59 months): Community-based
• School-age children (5-14 years): School-based (most cost-effective)
• Women of reproductive age: Community-based
• Adults in high-risk occupations: Targeted treatment

Program Monitoring:
• Coverage surveys (target >75%)
• Prevalence and intensity surveys (sentinel sites)
• Assess impact over time

PREGNANCY MANAGEMENT:

• Hookworm major contributor to maternal anemia
• WHO recommends deworming in 2nd and 3rd trimester
• Albendazole or mebendazole can be used
• Pyrantel pamoate safe in all trimesters (if benzimidazole concern)
• Iron and folic acid supplementation essential
• Monitor hemoglobin throughout pregnancy
      `
    },

    redFlags: [
      '🔴 CRITICAL: Intestinal obstruction (Ascaris) - Bilious vomiting, abdominal distension, absent bowel sounds; needs immediate surgical evaluation',
      '🔴 CRITICAL: Worm migration to airway - Coughing up worms, respiratory distress; airway emergency',
      '🔴 CRITICAL: Severe anemia (Hb <5 g/dL) with cardiac symptoms - Dyspnea, chest pain, heart failure; needs transfusion',
      '🔴 CRITICAL: Biliary ascariasis with cholangitis - Fever, jaundice, RUQ pain (Charcot triad); needs urgent intervention',
      '🔴 CRITICAL: Perforation/Peritonitis - Severe abdominal pain, rigidity, shock; surgical emergency',
      '🟠 URGENT: Rectal prolapse - Visible rectum protruding; needs reduction and treatment',
      '🟠 URGENT: Significant GI bleeding - Bloody diarrhea, melena; assess hemodynamic status',
      '🟠 URGENT: Appendicitis - RLQ pain, fever, guarding; may be caused by Ascaris',
      '🟠 URGENT: Severe symptomatic anemia in pregnancy - Risk to mother and fetus',
      '🟡 CAUTION: Loeffler syndrome (pulmonary phase) - Wheeze, cough, infiltrates; usually self-limiting but monitor',
      '🟡 CAUTION: Treatment failure - Persistent eggs after two courses; investigate further',
      '🟡 CAUTION: Severe malnutrition - Complicates management; needs nutritional rehabilitation'
    ],

    complications: [
      'Intestinal obstruction (Ascaris) - Worm bolus blocks intestine; surgical emergency; high mortality if delayed',
      'Biliary ascariasis - Worms migrate to bile ducts; cholangitis, pancreatitis, liver abscess',
      'Appendicitis (Ascaris) - Worm blocks appendiceal lumen',
      'Intestinal perforation - Rare; leads to peritonitis',
      'Iron deficiency anemia (Hookworm) - Chronic blood loss; can be severe; major cause globally',
      'Protein-losing enteropathy (Hookworm) - Hypoalbuminemia, edema',
      'Rectal prolapse (Trichuris) - Heavy whipworm infection; children most affected',
      'Growth retardation and stunting - Chronic infection impairs growth; partially reversible',
      'Cognitive impairment - Affects school performance; linked to chronic infection and anemia',
      'Maternal anemia and adverse pregnancy outcomes - Low birth weight, preterm birth, maternal mortality',
      'Loeffler syndrome - Pulmonary infiltrates during larval migration; usually self-limiting',
      'Secondary bacterial infections - Intestinal mucosal damage predisposes to infection',
      'Nutritional deficiencies - Multiple micronutrient deficiencies; malabsorption'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Excellent prognosis with treatment
• Single-dose therapy highly effective for most infections
• Anemia and nutritional status improve with treatment and supplementation
• Growth catch-up possible in children if treated early
• Reinfection common without improved sanitation

PROGNOSIS BY SPECIES:

Ascariasis:
• Excellent response to treatment (>95% cure rate)
• Complications (obstruction, biliary disease) can be fatal if untreated
• Mortality from obstruction: 5-10% even with surgery
• Full recovery expected in uncomplicated cases

Trichuriasis:
• Lower cure rates with single-dose therapy (40-60%)
• 3-day course improves outcomes
• Rectal prolapse reversible with treatment
• Chronic infection effects on growth may be partially reversible

Hookworm:
• Good response to treatment (70-80% cure)
• Anemia recovery depends on iron supplementation compliance
• Severe anemia may take months to correct
• Protein-losing enteropathy resolves with treatment
• Pregnancy outcomes improve with treatment

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis and treatment
• Compliance with full treatment course
• Adequate iron/nutritional supplementation
• Access to improved sanitation (prevents reinfection)
• Regular deworming in endemic areas
• Treatment of entire household

Worse Outcomes:
• Heavy infection intensity
• Delayed treatment
• Complications at presentation (obstruction, severe anemia)
• Continued exposure and reinfection
• Underlying malnutrition
• Co-infections (malaria, HIV, TB)
• Lack of access to healthcare

LONG-TERM IMPACT:
• Repeated infection and treatment cycles common in endemic areas
• MDA programs reduce morbidity at population level
• Cognitive effects may be permanent if chronic childhood infection
• Economic impact: Reduced productivity, lost school days

MORTALITY:
• Direct mortality relatively low
• Deaths mainly from Ascaris obstruction and severe hookworm anemia
• Contributes to child mortality through malnutrition
• Global estimate: 200,000+ deaths annually attributed to STH
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

WHO INTEGRATED APPROACH:

1. PREVENTIVE CHEMOTHERAPY (Mass Drug Administration):

School-Based Deworming:
• Most cost-effective public health intervention
• Albendazole 400 mg or Mebendazole 500 mg single dose
• Combined with praziquantel where schistosomiasis co-endemic
• Frequency based on prevalence:
  - High risk (>50%): Twice yearly
  - Moderate risk (20-50%): Once yearly
  - Low risk (<20%): Case-by-case

Community-Based Deworming:
• Target preschool children (12-59 months)
• Women of reproductive age
• Can be integrated with child health days, immunization campaigns

2. WATER, SANITATION, AND HYGIENE (WASH):

Sanitation:
• Construction and use of latrines
• Eliminate open defecation
• Community-Led Total Sanitation (CLTS) programs
• Safe disposal of child feces

Safe Water:
• Protected water sources
• Household water treatment
• Reduces transmission of many infections

Hygiene:
• Handwashing with soap (after defecation, before eating)
• Handwashing facilities at schools and homes
• Hygiene education in schools

3. HEALTH EDUCATION:

Key Messages:
• How worms spread (feces in soil, walking barefoot)
• Importance of using latrines
• Handwashing technique and timing
• Wearing shoes (hookworm prevention)
• Washing vegetables before eating
• Taking deworming medicine when offered

Delivery Channels:
• Schools (teachers trained as health educators)
• Health facilities
• Community health workers
• Mass media campaigns

4. PERSONAL PROTECTIVE MEASURES:

Hookworm Prevention:
• Wear shoes/sandals at all times
• Avoid walking barefoot in potentially contaminated areas
• Particularly important for farmers, children

Ascaris/Trichuris Prevention:
• Handwashing before eating
• Wash raw vegetables thoroughly
• Avoid eating soil (pica - educate caregivers)
• Keep fingernails short (children)

5. ENVIRONMENTAL MEASURES:

• Safe disposal of human waste
• Prevent use of human feces as fertilizer (or treat first)
• Protect soil around homes from fecal contamination
• School sanitation infrastructure

SPECIAL POPULATIONS:

Pregnant Women:
• WHO recommends deworming in 2nd/3rd trimester in endemic areas
• Treat anemia aggressively
• Iron-folic acid supplementation

Preschool Children:
• Include in community-based deworming
• Half dose (200 mg) albendazole for 12-24 months
• Vitamin A supplementation often combined

MONITORING AND EVALUATION:

• Coverage surveys (target >75% of at-risk population)
• Prevalence surveys using sentinel sites
• Impact assessment (prevalence reduction, intensity reduction)
• WHO targets: Morbidity control, then elimination as public health problem
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT ARE INTESTINAL WORMS?

Intestinal worms (also called soil-transmitted helminths or STH) are parasites that live in your intestines. The three main types are:
• Roundworm (Ascaris) - Large worms up to 35 cm long
• Whipworm (Trichuris) - Causes bloody diarrhea
• Hookworm - Causes anemia (weak blood)

These worms are very common in areas without good toilets and clean water.

HOW DO YOU GET WORMS?

ROUNDWORM AND WHIPWORM:
• Worm eggs are in the feces of infected people
• When people defecate in the open, eggs get into the soil
• You swallow the eggs by eating with dirty hands or eating unwashed vegetables

HOOKWORM:
• Tiny worm larvae live in contaminated soil
• They burrow through the skin of your feet when you walk barefoot
• They travel to your intestines where they suck blood

WHAT ARE THE SYMPTOMS?

Many people have no symptoms, but worms can cause:
• Stomach pain
• Diarrhea (sometimes bloody)
• Weakness and tiredness (from anemia)
• Poor appetite
• Weight loss
• In children: poor growth, difficulty learning
• Sometimes you may see worms in your stool

DANGER SIGNS - SEEK HELP IMMEDIATELY:
• Vomiting and severe stomach pain that does not stop (worms blocking intestines)
• Seeing the rectum come out (prolapse)
• Severe weakness, fast heartbeat, difficulty breathing (severe anemia)
• Coughing up worms

HOW ARE WORMS TREATED?

• Medicine called ALBENDAZOLE or MEBENDAZOLE kills the worms
• Usually just ONE DOSE is needed
• The medicine is safe for children
• If you have anemia, you will also need IRON tablets
• Take iron tablets for several months until your blood is strong

HOW TO PREVENT WORMS:

1. USE A TOILET:
   • Always use a latrine or toilet
   • Never defecate in the open or near where children play
   • Dispose of child feces safely

2. WASH YOUR HANDS:
   • With soap and water
   • After using the toilet
   • Before eating or preparing food
   • After working in soil

3. WEAR SHOES:
   • Always wear shoes or sandals
   • This prevents hookworm from entering through your feet
   • Children should wear shoes when playing outside

4. WASH FOOD:
   • Wash all vegetables and fruits before eating
   • Cook food thoroughly

5. TAKE DEWORMING MEDICINE:
   • Participate in school deworming programs
   • Take medicine when health workers offer it
   • Even if you feel well, you may have worms

WHY IS IT IMPORTANT TO TREAT WORMS?

• Worms steal your nutrition
• Hookworm makes you weak from blood loss
• Children with worms do not grow well and have trouble learning
• Pregnant women with hookworm have weak blood which is dangerous
• Treatment is easy and safe!

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only and is not a substitute for professional medical advice. If you or your child has symptoms of worm infection, please see a healthcare provider.

Remember: Worms are easy to treat but will come back without good sanitation. Use latrines, wash hands, and wear shoes!

QUESTIONS TO ASK YOUR DOCTOR:
• Do I need a stool test for worms?
• Should my whole family be treated?
• How long should I take the iron tablets?
• When should I come back for follow-up?
• Is there a deworming program at my child's school?
• How can I prevent getting worms again?
    `,

    references: [
      'World Health Organization. Guideline: Preventive chemotherapy to control soil-transmitted helminth infections in at-risk population groups. Geneva: WHO; 2017.',
      'World Health Organization. Soil-transmitted helminth infections: WHO Fact Sheet. Geneva: WHO; 2023.',
      'Bethony J, Brooker S, Albonico M, et al. Soil-transmitted helminth infections: ascariasis, trichuriasis, and hookworm. Lancet. 2006;367(9521):1521-1532.',
      'Hotez PJ, Brooker S, Bethony JM, et al. Hookworm infection. New England Journal of Medicine. 2004;351(8):799-807.',
      'Keiser J, Utzinger J. Efficacy of current drugs against soil-transmitted helminth infections: systematic review and meta-analysis. JAMA. 2008;299(16):1937-1948.',
      'Brooker S, Clements AC, Bundy DA. Global epidemiology, ecology and control of soil-transmitted helminth infections. Advances in Parasitology. 2006;62:221-261.',
      'Taylor-Robinson DC, Maayan N, Soares-Weiser K, et al. Deworming drugs for soil-transmitted intestinal worms in children: effects on nutritional indicators, haemoglobin, and school performance. Cochrane Database of Systematic Reviews. 2015;(7):CD000371.',
      'Pullan RL, Smith JL, Jasrasaria R, Brooker SJ. Global numbers of infection and disease burden of soil transmitted helminth infections in 2010. Parasites and Vectors. 2014;7:37.',
      'World Health Organization. Helminth control in school-age children: A guide for managers of control programmes. 2nd ed. Geneva: WHO; 2011.',
      'Albonico M, Allen H, Chitsulo L, et al. Controlling soil-transmitted helminthiasis in pre-school-age children through preventive chemotherapy. PLoS Neglected Tropical Diseases. 2008;2(3):e126.',
      'Crompton DW, Nesheim MC. Nutritional impact of intestinal helminthiasis during the human life cycle. Annual Review of Nutrition. 2002;22:35-59.',
      'Hotez PJ, Kamath A. Neglected tropical diseases in sub-Saharan Africa: review of their prevalence, distribution, and disease burden. PLoS Neglected Tropical Diseases. 2009;3(8):e412.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 55. BURNS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '55',
    name: 'Burns',
    slug: 'burns',
    specialty: 'Emergency Medicine',
    bodySystem: 'Systemic',
    icdCode: 'T30.0',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `
Burns are tissue injuries caused by heat (thermal), chemicals, electricity, radiation, or friction. They are a significant cause of morbidity and mortality globally, with over 95% of burn deaths occurring in low- and middle-income countries. In Africa, burns represent a major public health problem, particularly affecting children under 5 years who account for over half of burn admissions.

Burn severity depends on the depth of injury (superficial, partial-thickness, full-thickness), the total body surface area (TBSA) affected, the location (face, hands, feet, genitalia, joints are high-risk), and patient factors (age extremes, comorbidities). Major burns trigger a systemic inflammatory response with massive fluid shifts, leading to burn shock if not adequately resuscitated.

Common causes in Africa include scalds from hot water and cooking, open flame burns from cooking fires and kerosene lamps/stoves, and electrical burns. Traditional cooking practices, use of kerosene for lighting and cooking, and crowded living conditions contribute to the high burden. Prevention is critical, as burns cause prolonged suffering, significant disability, disfigurement, and enormous economic impact.

Management priorities include stopping the burning process, early fluid resuscitation using the Parkland formula, appropriate wound care, pain management, prevention of infection, nutritional support, and rehabilitation. Early excision and grafting have revolutionized outcomes in well-resourced settings, but may be limited in resource-constrained environments.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 STOP THE BURNING PROCESS FIRST - Remove from source, remove clothing/jewelry, cool with clean tepid water (not ice) for 20 minutes

🔴 ASSESS AIRWAY EARLY - Facial burns, singed nasal hairs, hoarse voice, stridor suggest inhalation injury; intubate EARLY before edema worsens

🔴 USE PARKLAND FORMULA FOR FLUID RESUSCITATION - 4 mL x kg x %TBSA of Ringer Lactate; half in first 8 hours from TIME OF BURN

🔴 RULE OF 9s ESTIMATES TBSA - Adults: Head 9%, each arm 9%, each leg 18%, trunk front 18%, trunk back 18%, perineum 1%

🔴 CHILDREN HAVE DIFFERENT PROPORTIONS - Use Lund-Browder chart; child head is larger (18% in infant), legs smaller

🔴 FULL-THICKNESS CIRCUMFERENTIAL BURNS NEED ESCHAROTOMY - Limbs: compartment syndrome risk; Chest: restricts breathing

🔴 BURN WOUND INFECTION IS MAJOR KILLER - Pseudomonas, Staph aureus common; watch for sepsis; topical antimicrobials essential

🔴 PAIN CONTROL IS ESSENTIAL - Burns are extremely painful; IV opioids for major burns; do not undertreat
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• 11 million burns requiring medical attention annually worldwide
• 180,000 deaths annually from burns globally
• 95% of burn deaths occur in low- and middle-income countries
• Fourth most common type of trauma worldwide
• Burns are leading cause of DALYs lost from injury in LMICs
• Children under 5 years at highest risk
• Male predominance overall, but women at higher risk in many LMICs (cooking-related)

AFRICAN EPIDEMIOLOGY:
• Africa has among the highest burn incidence and mortality rates globally
• Estimated incidence: 100-200 per 100,000 population in many African countries
• Children under 5 years account for 50-60% of burn admissions
• Leading causes in Africa:
  - Scalds (hot water, cooking liquids): 50-70% of pediatric burns
  - Open flame (cooking fires, kerosene lamps/stoves): 20-40%
  - Electrical burns: 5-10%
  - Chemical burns: 2-5%
• Kerosene-related burns: Major problem due to widespread use for cooking/lighting
• Mortality rates: 5-15% in specialized units; higher in general hospitals
• Significant morbidity: Contractures, disfigurement, disability
• Limited burn care infrastructure in most African countries

RISK FACTORS:
• Age extremes (children <5 years, elderly >60 years)
• Poverty and overcrowded housing
• Open cooking fires at ground level
• Kerosene stoves and lamps
• Lack of adult supervision of children
• Epilepsy (falls into fires during seizures)
• Alcohol intoxication
• Mental health disorders
• Lack of smoke detectors and fire safety awareness
• Traditional practices (hot water treatment, branding)
• Occupational exposure (industrial, electrical workers)
• Domestic violence (intentional burns)

HIGH-RISK SITUATIONS IN AFRICAN CONTEXT:
• Cooking on open fires with children nearby
• Bathing children with hot water in basins
• Kerosene stove explosions
• Sleeping near cooking fires for warmth
• Electrical injuries from informal connections
• Acid attacks (intentional burns)
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

BURN DEPTH CLASSIFICATION:

SUPERFICIAL (First Degree) Burns:
• Involves epidermis only
• Appearance: Red, dry, no blisters
• Sensation: Painful to touch
• Capillary refill: Brisk
• Healing: 3-7 days without scarring
• Example: Sunburn

SUPERFICIAL PARTIAL-THICKNESS (Second Degree) Burns:
• Involves epidermis and superficial dermis
• Appearance: Red/pink, moist, blisters, weeping
• Sensation: Very painful (nerve endings intact)
• Capillary refill: Brisk
• Healing: 7-21 days, usually without significant scarring
• Hair follicles intact

DEEP PARTIAL-THICKNESS (Second Degree) Burns:
• Involves epidermis and deep dermis
• Appearance: Pale, mottled red/white, less moist
• Sensation: Decreased (nerve damage)
• Capillary refill: Sluggish
• Healing: 3-8 weeks, significant scarring likely
• May require grafting

FULL-THICKNESS (Third Degree) Burns:
• Involves entire dermis, into subcutaneous tissue
• Appearance: White, waxy, leathery, or charred; dry
• Sensation: Insensate (nerves destroyed)
• Capillary refill: Absent
• Healing: Cannot heal spontaneously except from edges; requires grafting
• Thrombosed vessels may be visible

FOURTH DEGREE Burns:
• Extends into muscle, tendon, bone
• Charred appearance
• May require amputation

INHALATION INJURY:

Suspect if:
• Burns in enclosed space
• Facial burns, singed nasal hairs, eyebrows
• Carbonaceous sputum
• Hoarseness, stridor
• Respiratory distress
• History of smoke/fume exposure

Signs:
• Facial edema
• Oropharyngeal edema/erythema
• Wheezing, stridor
• Cough with carbonaceous sputum
• Progressive respiratory failure

CHEMICAL BURNS:

• Acids: Coagulation necrosis; damage often limited by protein precipitation
• Alkalis: Liquefactive necrosis; penetrate deeper; more severe injury
• Continue to cause damage until removed
• Associated systemic toxicity possible (hydrofluoric acid - hypocalcemia)

ELECTRICAL BURNS:

Low Voltage (<1000V):
• Usually localized entry/exit wounds
• Cardiac arrhythmias possible
• Tetanic muscle contraction

High Voltage (>1000V):
• Severe, deep tissue injury
• Entry and exit wounds (iceberg effect - more damage internally)
• Muscle necrosis, compartment syndrome
• Rhabdomyolysis and acute kidney injury
• Cardiac arrhythmias, arrest
• Fractures and dislocations (from falls or muscle contraction)
• Cataracts (delayed)
• Spinal cord injury

SYSTEMIC RESPONSE TO MAJOR BURNS:

Burn Shock (First 24-48 hours):
• Massive capillary leak
• Intravascular fluid loss
• Hypovolemia, hypotension
• Tachycardia
• Decreased urine output
• Metabolic acidosis

Hypermetabolic Phase (Days to weeks):
• Massive increase in metabolic rate (up to 200% of normal)
• Protein catabolism
• Immunosuppression
• Risk of infection and sepsis

PHYSICAL EXAMINATION:
• Primary survey: Airway, Breathing, Circulation (ATLS principles)
• Burn-specific assessment:
  - Estimate %TBSA using Rule of 9s or Lund-Browder
  - Assess burn depth
  - Document location (face, hands, feet, genitalia, joints - critical areas)
  - Check for circumferential burns (escharotomy may be needed)
  - Examine for associated injuries (trauma, fractures)
• Signs of inhalation injury
• Signs of abuse (pattern burns, delay in seeking care)
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: PRIMARY SURVEY (ABCDE)

Airway:
• Is airway patent?
• Signs of inhalation injury? (facial burns, stridor, carbonaceous sputum)
• Intubate early if any concern - edema will worsen over hours

Breathing:
• Respiratory rate, oxygen saturation
• Circumferential chest burns restricting ventilation?
• Need for escharotomy?

Circulation:
• Heart rate, blood pressure
• IV access (through burned skin if necessary)
• Signs of shock?

Disability:
• Level of consciousness (carbon monoxide poisoning?)
• Pain assessment

Exposure:
• Remove all clothing and jewelry
• Prevent hypothermia (burns lose thermoregulation)

STEP 2: ESTIMATE TOTAL BODY SURFACE AREA (TBSA)

Rule of 9s (Adults):
• Head and neck: 9%
• Each upper limb: 9%
• Each lower limb: 18%
• Anterior trunk: 18%
• Posterior trunk: 18%
• Perineum: 1%
• Patient palm (including fingers): ~1% (useful for scattered burns)

Lund-Browder Chart (Children - more accurate):
• Accounts for different proportions in children
• Head: 18% (infant) decreasing with age
• Each leg: 14% (infant) increasing with age
• Must use age-appropriate chart

TBSA Calculation Notes:
• Only count partial-thickness and full-thickness burns
• Do not include superficial (first-degree) burns in TBSA
• Erythema alone is not counted

STEP 3: ASSESS BURN DEPTH

Visual/Tactile Assessment:
• Color: Red (superficial) → White/Waxy (full-thickness)
• Moisture: Moist/blistered (partial) → Dry/leathery (full)
• Sensation: Painful (partial) → Insensate (full)
• Capillary refill: Present (partial) → Absent (full)
• Hair: Pulls out easily in full-thickness

Note: Burn depth may evolve over 48-72 hours; reassess regularly

STEP 4: IDENTIFY MAJOR BURN CRITERIA

Major Burns (Require Referral/Burn Unit):
• >20% TBSA in adults
• >10% TBSA in children or elderly
• Full-thickness burns >5% TBSA
• Burns to face, hands, feet, genitalia, perineum, major joints
• Circumferential burns
• Inhalation injury
• Electrical or chemical burns
• Burns with associated trauma
• Burns in patients with comorbidities
• Suspected non-accidental injury

STEP 5: DOCUMENT THOROUGHLY

• Time of injury
• Mechanism (scald, flame, chemical, electrical)
• First aid given
• %TBSA and depth
• Location of burns (diagram)
• Associated injuries
• Tetanus status
• Pre-existing conditions
    `,

    differentialDiagnosis: [
      'Toxic epidermal necrolysis (TEN) - Drug history, mucosal involvement, Nikolsky sign positive, no heat exposure',
      'Stevens-Johnson syndrome - Drug exposure, mucosal involvement, target lesions',
      'Staphylococcal scalded skin syndrome - Children, preceding infection, superficial peeling, Nikolsky positive',
      'Chemical dermatitis/contact burns - History of chemical exposure, pattern matches contact',
      'Frostbite - Cold exposure history, similar appearance to burns, tissue freezing',
      'Necrotizing fasciitis - Rapidly progressive, severe pain, systemic toxicity, crepitus',
      'Radiation burns - Radiation exposure history, delayed presentation',
      'Phytophotodermatitis - Plant contact + sun exposure, bizarre patterns',
      'Pemphigus/Pemphigoid - Chronic blistering, mucosal involvement, no burn history',
      'Non-accidental injury - Pattern burns (cigarette, iron), delay in presentation, inconsistent history',
      'Self-inflicted burns - Pattern suggests intentional injury, psychiatric history',
      'Electrical mark without known exposure - Consider lightning strike',
      'Fixed drug eruption - Recurrent localized reaction, drug history'
    ],

    investigations: [
      {
        name: 'Clinical Assessment (TBSA, Depth)',
        indication: '🔴 ESSENTIAL - Rule of 9s or Lund-Browder chart; depth assessment; most important initial evaluation',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Baseline; hemoconcentration in early burns (fluid shift); anemia later; leukocytosis may indicate infection',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Na, K, Cl)',
        indication: '🔴 ESSENTIAL - Monitor during fluid resuscitation; hyperkalemia risk (tissue destruction); hyponatremia common',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and Urea',
        indication: '🔴 ESSENTIAL - Baseline renal function; monitor for acute kidney injury (especially electrical burns, rhabdomyolysis)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Stress hyperglycemia common; hypoglycemia in children; affects outcomes',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: '🔴 ESSENTIAL - Myoglobinuria (red/brown urine) in electrical burns or crush injury indicates rhabdomyolysis',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🔴 ESSENTIAL in major burns - Assess oxygenation, ventilation; metabolic acidosis indicates poor perfusion',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Carboxyhemoglobin Level',
        indication: '🔴 ESSENTIAL if smoke inhalation - CO poisoning; levels >10% significant; >25% severe',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Lactate',
        indication: '🟠 IMPORTANT - Marker of tissue perfusion; elevated in burn shock; guide resuscitation adequacy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Creatine Kinase (CK)',
        indication: '🔴 ESSENTIAL in electrical burns - Elevated in rhabdomyolysis; guides aggressive hydration',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Coagulation Studies (PT, aPTT)',
        indication: '🟠 IMPORTANT in major burns - Coagulopathy may develop; baseline before surgery',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🔴 ESSENTIAL for major burns - Prepare for possible transfusion; surgery may be needed',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 ESSENTIAL if inhalation injury suspected - Baseline; may be initially normal; ARDS develops later',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🔴 ESSENTIAL for electrical burns - Arrhythmias; cardiac monitoring for 24 hours',
        availability: 'BASIC'
      },
      {
        name: 'Wound Culture',
        indication: '🟠 IMPORTANT if infection suspected - Identify organisms; guide antibiotic therapy; routine surveillance controversial',
        availability: 'BASIC'
      },
      {
        name: 'Bronchoscopy',
        indication: '🟡 IF AVAILABLE - Direct visualization of airway in suspected inhalation injury',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ STOP THE BURNING PROCESS: Remove from heat source; remove burning clothing and jewelry; do NOT remove adherent material',
        '⚡ COOL THE BURN: Cool water (not ice) for 20 minutes; effective up to 3 hours post-burn; do not over-cool (hypothermia risk)',
        '⚡ AIRWAY: Assess for inhalation injury; if any concern, INTUBATE EARLY before edema worsens; 100% oxygen if smoke inhalation',
        '⚡ BREATHING: Assess chest wall compliance; escharotomy if circumferential chest burns restrict ventilation',
        '⚡ CIRCULATION: Two large-bore IV lines (through burned skin if necessary); start fluid resuscitation immediately for >15% TBSA',
        '⚡ FLUID RESUSCITATION (Parkland Formula): 4 mL x kg x %TBSA of Ringer Lactate; give half in first 8 hours FROM TIME OF BURN',
        '⚡ PAIN CONTROL: IV morphine for major burns (0.1 mg/kg); titrate to effect; burns are extremely painful',
        '⚡ PREVENT HYPOTHERMIA: Cover with clean dry sheets; warm environment; warmed IV fluids',
        '⚡ WOUND CARE: Cover with clean, dry dressing; cling film is ideal for transport; do NOT apply traditional remedies',
        '⚡ TETANUS PROPHYLAXIS: Update if needed'
      ],
      
      pharmacological: [
        {
          drug: 'Ringer Lactate (Lactated Ringer Solution)',
          indication: '🔴 CRITICAL - Fluid of choice for burn resuscitation; isotonic crystalloid replaces intravascular losses',
          dose: 'PARKLAND FORMULA: 4 mL x body weight (kg) x %TBSA burned. Give HALF in first 8 hours from time of burn (not arrival time). Give remaining half over next 16 hours. Titrate to urine output 0.5-1 mL/kg/hour (adults) or 1-1.5 mL/kg/hour (children).',
          route: 'IV',
          duration: 'First 24 hours; then adjust based on response',
          contraindications: ['None in burn resuscitation'],
          sideEffects: ['Over-resuscitation: Edema, compartment syndrome, ARDS', 'Under-resuscitation: Shock, organ failure'],
          alternatives: ['Normal Saline (less preferred - hyperchloremic acidosis)', 'Hartmann solution']
        },
        {
          drug: 'Morphine',
          indication: '🔴 ESSENTIAL - First-line opioid for burn pain; titrate to effect',
          dose: 'Adults: 0.1 mg/kg IV loading, then 0.05-0.1 mg/kg every 2-4 hours as needed. Children: 0.05-0.1 mg/kg IV every 4 hours. For dressing changes: Pre-medicate 15-30 minutes before.',
          route: 'IV (preferred in acute burns for reliable absorption)',
          duration: 'As needed; transition to oral when stable',
          contraindications: ['Respiratory depression without airway control', 'Hypotension (relative)'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Nausea', 'Constipation', 'Pruritus'],
          alternatives: ['Fentanyl 1-2 mcg/kg IV (shorter acting)', 'Ketamine (dissociative - useful for dressing changes)']
        },
        {
          drug: 'Ketamine',
          indication: '🟠 IMPORTANT - Excellent for procedural sedation (dressing changes, debridement); maintains hemodynamics and airway reflexes',
          dose: 'Procedural sedation: 1-2 mg/kg IV or 4-5 mg/kg IM. Analgesia: 0.2-0.5 mg/kg IV. Give with midazolam to prevent emergence phenomena.',
          route: 'IV or IM',
          duration: 'Single procedure doses',
          contraindications: ['Severe hypertension', 'Raised ICP (relative)', 'Psychosis'],
          sideEffects: ['Emergence reactions (hallucinations)', 'Hypersalivation', 'Nystagmus', 'Hypertension'],
          alternatives: ['Morphine + Midazolam', 'Propofol (in controlled setting)']
        },
        {
          drug: 'Midazolam',
          indication: '🟠 IMPORTANT - Anxiolysis and sedation; often combined with opioids or ketamine for procedures',
          dose: 'Adults: 0.05-0.1 mg/kg IV. Children: 0.05-0.1 mg/kg IV. Intranasal: 0.2-0.3 mg/kg (if no IV).',
          route: 'IV, IM, or Intranasal',
          duration: 'As needed for procedures',
          contraindications: ['Respiratory depression without airway control'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Paradoxical agitation in children'],
          alternatives: ['Diazepam', 'Lorazepam']
        },
        {
          drug: 'Silver Sulfadiazine 1% Cream',
          indication: '🔴 ESSENTIAL - Topical antimicrobial for burn wounds; broad-spectrum (Pseudomonas, Staph, some fungi)',
          dose: 'Apply 1-2 mm thickness to wound once or twice daily after cleaning. Cover with sterile dressing.',
          route: 'Topical',
          duration: 'Until wound healed or grafted',
          contraindications: ['Sulfonamide allergy', 'G6PD deficiency (relative)', 'Pregnancy near term', 'Infants <2 months (kernicterus risk)'],
          sideEffects: ['Transient leukopenia (monitor CBC)', 'Pseudoeschar formation', 'Delayed wound healing (controversial)', 'Rash'],
          alternatives: ['Mafenide acetate (penetrates eschar)', 'Silver dressings', 'Honey (evidence-based alternative)', 'Chlorhexidine']
        },
        {
          drug: 'Medical Grade Honey',
          indication: '🟠 ALTERNATIVE - Natural antimicrobial; promotes moist wound healing; available and affordable in Africa',
          dose: 'Apply to wound and cover with dressing. Change daily or when strike-through.',
          route: 'Topical',
          duration: 'Until healed',
          contraindications: ['None significant'],
          sideEffects: ['Transient stinging', 'Rare allergy'],
          alternatives: ['Silver sulfadiazine', 'Silver dressings']
        },
        {
          drug: 'Tetanus Toxoid',
          indication: '🔴 ESSENTIAL - Burns are tetanus-prone wounds',
          dose: 'Per tetanus prophylaxis guidelines based on vaccination history. Give TT +/- HTIG if indicated.',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Severe reaction to previous dose (rare)'],
          sideEffects: ['Injection site reactions'],
          alternatives: ['Td vaccine']
        },
        {
          drug: 'Ranitidine or Omeprazole',
          indication: '🟠 IMPORTANT - Stress ulcer prophylaxis (Curling ulcer) in major burns',
          dose: 'Ranitidine: 50 mg IV every 8 hours or 150 mg PO twice daily. Omeprazole: 40 mg IV/PO daily.',
          route: 'IV or PO',
          duration: 'Until tolerating full enteral feeds',
          contraindications: ['Drug-specific allergies'],
          sideEffects: ['Headache', 'GI upset'],
          alternatives: ['Sucralfate 1g QID via NG']
        },
        {
          drug: 'Ceftriaxone + Metronidazole (or alternative)',
          indication: '🟠 FOR ESTABLISHED INFECTION - Not prophylactic; only for clinical signs of wound infection or sepsis',
          dose: 'Ceftriaxone: 1-2g IV daily. Metronidazole: 500 mg IV every 8 hours. Adjust based on culture results.',
          route: 'IV',
          duration: '7-14 days depending on response',
          contraindications: ['Drug allergies'],
          sideEffects: ['Drug-specific'],
          alternatives: ['Piperacillin-tazobactam', 'Meropenem for resistant organisms', 'Add vancomycin if MRSA suspected']
        },
        {
          drug: 'Albumin 5%',
          indication: '🟡 ADJUNCT - After first 24 hours if hypoalbuminemic; colloid resuscitation controversial',
          dose: '0.5-1 mL/kg/%TBSA over 24 hours. Start after first 24 hours of crystalloid resuscitation.',
          route: 'IV',
          duration: 'As needed',
          contraindications: ['First 24 hours of resuscitation (controversial)', 'Pulmonary edema'],
          sideEffects: ['Fluid overload', 'Allergic reactions'],
          alternatives: ['Continue crystalloid resuscitation']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 ADJUNCT - Mild pain, antipyretic; opioid-sparing',
          dose: 'Adults: 1g IV/PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours.',
          route: 'IV or PO',
          duration: 'As needed',
          contraindications: ['Severe liver disease'],
          sideEffects: ['Hepatotoxicity with overdose'],
          alternatives: ['NSAIDs (avoid in early burns - renal risk)']
        }
      ],
      
      nonPharmacological: [
        'FIRST AID (Pre-hospital): Cool running water for 20 minutes (not ice); remove non-adherent clothing/jewelry; cover with clean cloth/cling film; keep warm; do NOT apply toothpaste, butter, eggs, or other traditional remedies',
        'AIRWAY MANAGEMENT: Early intubation for any sign of inhalation injury; 100% oxygen for CO poisoning',
        'ESCHAROTOMY: For circumferential full-thickness burns compromising circulation (limbs) or ventilation (chest); bedside procedure; cut through eschar down to fat along lateral aspects',
        'WOUND CLEANING: Gentle debridement of loose tissue; cleanse with saline or mild antiseptic; do not rupture intact blisters',
        'DRESSING PRINCIPLES: Non-adherent primary layer, absorbent secondary layer, secure tertiary layer; change daily or as needed',
        'ELEVATION: Elevate burned extremities above heart level to reduce edema',
        'SPLINTING: Position joints in functional position; prevent contractures (anti-claw position for hands)',
        'NUTRITION: Early enteral feeding (within 24 hours); high-calorie, high-protein diet (25-35 kcal/kg + 40 kcal/%TBSA); protein 1.5-2 g/kg/day; supplement vitamins and zinc',
        'PHYSIOTHERAPY: Early mobilization; range of motion exercises; prevent contractures; pressure garments for hypertrophic scars',
        'PSYCHOLOGICAL SUPPORT: Burns cause significant psychological trauma; support for patient and family; address body image concerns',
        'INFECTION CONTROL: Hand hygiene; protective isolation for major burns; minimize wound manipulation',
        'TEMPERATURE REGULATION: Burns lose thermoregulatory capacity; maintain warm environment; warm IV fluids'
      ],
      
      surgical: [
        'ESCHAROTOMY: Emergency procedure for circumferential burns; restores circulation to limbs, allows chest expansion; bedside, no anesthesia needed (full-thickness is insensate)',
        'FASCIOTOMY: For compartment syndrome (especially electrical burns); deeper than escharotomy into fascial compartments',
        'EARLY EXCISION AND GRAFTING: Gold standard in major burns; excise eschar and apply skin graft within 5-7 days; reduces mortality, infection, hospital stay; may be limited by resources',
        'SPLIT-THICKNESS SKIN GRAFT (STSG): Harvest from unburned donor site; meshing allows coverage of larger areas; heals spontaneously at donor site',
        'FULL-THICKNESS SKIN GRAFT (FTSG): For specialized areas (face, hands); better cosmetic result; requires direct closure of donor site',
        'TEMPORARY WOUND COVERAGE: Allografts (cadaver skin), xenografts (pig skin), synthetic dressings when autograft not available',
        'CONTRACTURE RELEASE: Late surgery for established contractures; Z-plasty, grafting, tissue expansion',
        'AMPUTATION: For non-viable extremities (especially electrical burns)',
        'RECONSTRUCTIVE SURGERY: Long-term reconstruction for disfigurement, functional impairment'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Major burns (>20% TBSA adult, >10% child/elderly) - Requires specialized burn care',
        '🔴 IMMEDIATE REFERRAL: Inhalation injury - Requires ICU, ventilatory support',
        '🔴 IMMEDIATE REFERRAL: Burns to face, hands, feet, genitalia, perineum, major joints - High functional importance',
        '🔴 IMMEDIATE REFERRAL: Circumferential burns - Risk of compartment syndrome, ventilatory compromise',
        '🔴 IMMEDIATE REFERRAL: Full-thickness burns >5% TBSA - Will require grafting',
        '🔴 IMMEDIATE REFERRAL: Electrical burns - Risk of deep tissue injury, cardiac complications, rhabdomyolysis',
        '🔴 IMMEDIATE REFERRAL: Chemical burns - May need specific antidotes, specialized care',
        '🔴 IMMEDIATE REFERRAL: Burns with associated trauma - Multidisciplinary management needed',
        '🟠 URGENT REFERRAL: Burns in patients with comorbidities (diabetes, immunosuppression)',
        '🟠 URGENT REFERRAL: Suspected non-accidental injury (abuse) - Child protection involvement',
        '🟡 ROUTINE REFERRAL: Burns not healing by 2-3 weeks - May need grafting',
        '🟡 ROUTINE REFERRAL: Hypertrophic scarring or contractures - Reconstructive surgery evaluation'
      ],
      
      monitoring: [
        'URINE OUTPUT: Most important marker of resuscitation adequacy; target 0.5-1 mL/kg/hour (adults), 1-1.5 mL/kg/hour (children); catheterize for major burns',
        'VITAL SIGNS: Hourly in acute phase; heart rate, blood pressure, respiratory rate, temperature, oxygen saturation',
        'FLUID BALANCE: Strict intake/output; adjust resuscitation fluids to urine output, not fixed formula',
        'WEIGHT: Daily weights; expect gain in first 48 hours (edema), then diuresis',
        'WOUND ASSESSMENT: Daily evaluation for depth progression, infection signs (erythema, purulent discharge, odor)',
        'LABORATORY: Daily electrolytes, renal function, CBC in major burns; watch for hypokalemia after diuresis, anemia',
        'NUTRITION: Caloric intake monitoring; weight trends; prealbumin if available',
        'PAIN SCORES: Regular assessment; adjust analgesia; ensure adequate pre-medication for procedures',
        'COMPARTMENT PRESSURES: If concern for compartment syndrome; escharotomy/fasciotomy if elevated',
        'CARDIAC MONITORING: Continuous for electrical burns (minimum 24 hours); watch for arrhythmias'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE PHASE (First 48-72 hours):
• Monitor fluid resuscitation closely
• Reassess burn depth (may evolve)
• Watch for wound infection
• Nutritional support initiated
• Begin physiotherapy/positioning

WOUND HEALING PHASE (Days to weeks):
• Daily wound care and dressing changes
• Assess for spontaneous healing vs need for grafting
• Superficial partial-thickness: Expect healing in 2-3 weeks
• Deep partial-thickness: May need grafting if not healing by 3 weeks
• Full-thickness: Will not heal; requires grafting

REHABILITATION PHASE (Weeks to months):
• Intensive physiotherapy
• Range of motion exercises
• Splinting and positioning
• Pressure garment therapy for hypertrophic scars (start when wounds closed)
• Occupational therapy for functional recovery
• Psychological support

LONG-TERM FOLLOW-UP:

3-6 Months:
• Assess scar maturation
• Pressure garments continue (typically 12-18 months)
• Functional assessment
• Screen for contractures
• Psychological adjustment

6-12 Months:
• Scars maturing (remain red/raised for 12-24 months)
• Contracture release if limiting function
• Consider reconstructive surgery planning

1-2 Years and Beyond:
• Scars mature and soften
• Reconstructive surgery as needed
• Long-term functional optimization
• Cancer surveillance in chronic wounds/unstable scars
• Lifelong sun protection for grafted/scarred areas

DISCHARGE CRITERIA:
• Wounds healed or stable graft take
• Pain controlled with oral medications
• Able to perform wound care or caregiver trained
• Nutritional status adequate
• Rehabilitation plan in place
• Follow-up appointments scheduled
• Psychosocial support arranged

SCAR MANAGEMENT:
• Pressure garments (23 hours/day for 12-18 months)
• Silicone gel sheets
• Massage
• Moisturization
• Sun protection
• Intralesional steroids for hypertrophic scars (if available)
      `
    },

    redFlags: [
      '🔴 CRITICAL: Airway compromise - Stridor, hoarseness, facial edema, carbonaceous sputum; intubate immediately',
      '🔴 CRITICAL: Circumferential chest burn with respiratory distress - Needs emergent escharotomy',
      '🔴 CRITICAL: Circumferential limb burn with absent pulses - Compartment syndrome; needs escharotomy/fasciotomy',
      '🔴 CRITICAL: Burn shock - Hypotension, tachycardia, oliguria; increase fluid resuscitation',
      '🔴 CRITICAL: Myoglobinuria (dark red/brown urine) - Rhabdomyolysis; aggressive fluids to prevent renal failure',
      '🔴 CRITICAL: Cardiac arrhythmias (electrical burns) - May need defibrillation or anti-arrhythmics',
      '🟠 URGENT: Signs of wound infection - Increasing pain, erythema, purulent discharge, fever; may be developing sepsis',
      '🟠 URGENT: Burn wound sepsis - Fever/hypothermia, tachycardia, altered mental status, wound deterioration',
      '🟠 URGENT: Inadequate urine output despite fluids - Reassess resuscitation; consider other causes',
      '🟠 URGENT: Burn depth progression - Partial-thickness converting to full-thickness; needs reassessment',
      '🟡 CAUTION: Over-resuscitation - Peripheral edema, pulmonary edema, abdominal compartment syndrome',
      '🟡 CAUTION: Non-accidental injury - Stocking/glove pattern, buttock burns, delay in seeking care',
      '🟡 CAUTION: Wound not healing by 3 weeks - Likely needs grafting'
    ],

    complications: [
      'Burn shock - Hypovolemia from massive fluid shifts; leading cause of early death',
      'Inhalation injury - Upper airway obstruction, chemical tracheobronchitis, ARDS; major mortality factor',
      'Burn wound infection/sepsis - Leading cause of late death; Pseudomonas, Staph aureus common',
      'Compartment syndrome - From circumferential burns or fluid resuscitation; needs escharotomy/fasciotomy',
      'Acute kidney injury - From hypovolemia, myoglobinuria (electrical burns), sepsis',
      'ARDS (Acute Respiratory Distress Syndrome) - From inhalation, sepsis, over-resuscitation',
      'Curling ulcer (stress ulcer) - GI bleeding from physiological stress; prevented with prophylaxis',
      'DVT/Pulmonary embolism - From immobility, hypercoagulable state',
      'Hypertrophic scarring - Raised, red scars; worse with delayed healing, infection, darker skin',
      'Contractures - From scar contraction across joints; limits function; preventable with physiotherapy',
      'Heterotopic ossification - Bone formation in soft tissues; limits joint mobility',
      'Post-traumatic stress disorder - Very common after burns; affects quality of life',
      'Depression and anxiety - Related to disfigurement, chronic pain, disability',
      'Marjolin ulcer - Squamous cell carcinoma developing in chronic burn scars (years later)'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Survival depends on: Age, %TBSA, inhalation injury, depth, comorbidities, access to care
• Modern burn care has dramatically improved survival
• Most patients with <40% TBSA survive with good care
• Long-term quality of life can be good with comprehensive rehabilitation

PROGNOSTIC INDICATORS:

Baux Score (Simple Mortality Estimate):
• Mortality % ≈ Age + %TBSA
• Example: 40-year-old with 30% TBSA burn = ~70% survival
• Add 17 if inhalation injury present

Modified Baux Score:
• Better predictive accuracy
• Age + %TBSA + (17 x inhalation injury present)
• LD50 (50% mortality): Score of approximately 100-110

Factors Associated with Higher Mortality:
• Age extremes (<2 years, >60 years)
• Large TBSA (>40%)
• Full-thickness burns
• Inhalation injury (doubles mortality)
• Delay in resuscitation
• Pre-existing comorbidities (diabetes, cardiac disease, immunosuppression)
• Limited access to specialized care
• Burn wound sepsis

Factors Associated with Better Outcome:
• Younger adults
• Partial-thickness burns
• Early cooling and first aid
• Prompt fluid resuscitation
• Access to burn center/ICU
• Early excision and grafting
• Aggressive rehabilitation

SURVIVAL EXPECTATIONS (Well-Resourced Setting):
• <20% TBSA: >95% survival
• 20-40% TBSA: 80-95% survival
• 40-60% TBSA: 50-80% survival
• 60-80% TBSA: 20-50% survival
• >80% TBSA: <20% survival
(These rates are worse in resource-limited settings)

LONG-TERM OUTCOMES:

Functional Recovery:
• Most survivors achieve functional independence
• Hand burns most limiting for function
• Rehabilitation is key

Cosmetic Outcome:
• Hypertrophic scarring common, especially in deeper burns
• Darker skin more prone to hypertrophic scarring
• Pressure therapy and time improve appearance

Psychological Recovery:
• PTSD affects 10-45% of survivors
• Depression and anxiety common
• Body image issues, especially with visible burns
• Strong social support improves outcomes

Quality of Life:
• Many survivors report good quality of life
• Ongoing pain in some patients
• Return to work/school usually possible with rehabilitation
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

HOME SAFETY - COOKING:

• Keep children away from cooking areas; use barriers/playpens
• Turn pot handles inward, away from edge of stove
• Use back burners when possible
• Supervise children at all times around hot liquids
• Do not carry hot liquids while holding a child
• Test bath water temperature before placing child in
• Store matches and lighters out of children reach
• Ensure stable base for cooking stoves

KEROSENE AND FUEL SAFETY (Critical in Africa):

• Use improved cookstoves where available
• Store kerosene away from cooking/sleeping areas
• Never refuel stoves while hot or lit
• Keep kerosene containers clearly labeled
• Use stable, well-designed kerosene stoves
• Transition to safer cooking fuels where possible (LPG, electricity)
• Never use kerosene to start fires

FIRE SAFETY:

• Install smoke detectors if available
• Have fire escape plan
• Keep fire extinguisher accessible
• Do not leave candles or fires unattended
• Store flammable materials safely
• Maintain electrical installations properly
• Avoid overloading electrical circuits

FIRST AID EDUCATION:

• Teach cool water for 20 minutes
• Do NOT apply traditional remedies (toothpaste, butter, eggs, plant leaves)
• Remove clothing and jewelry
• Cover with clean cloth
• Seek medical care promptly

ELECTRICAL SAFETY:

• Ensure proper electrical installations
• Avoid informal electrical connections
• Keep children away from electrical outlets
• Report damaged power lines
• Do not use electrical appliances near water

CHEMICAL SAFETY:

• Store chemicals in original labeled containers
• Keep chemicals locked away from children
• Wear protective equipment when handling chemicals
• Know first aid for chemical exposures

WORKPLACE SAFETY:

• Proper training for workers
• Personal protective equipment
• Fire safety protocols
• First aid facilities and training

COMMUNITY INTERVENTIONS:

• Public education campaigns
• Burn prevention in school curricula
• Improved housing design
• Access to safer cooking technologies
• Strengthening burn care services
• Burn survivor support groups

HIGH-RISK GROUP INTERVENTIONS:

Children:
• Adult supervision
• Physical barriers around cooking areas
• Child-resistant lighters
• Education appropriate for age

Epilepsy patients:
• Seizure control
• Avoid open fires
• Supervision during cooking

Elderly:
• Safe cooking practices
• Working smoke detectors
• Assistance with cooking if needed
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT ARE BURNS?

Burns are injuries to the skin and underlying tissues caused by heat (fire, hot liquids), chemicals, electricity, or radiation. Burns can be minor or life-threatening depending on how deep and how much of the body is affected.

WHAT TO DO IF SOMEONE GETS BURNED:

FIRST AID (Very Important!):
1. MOVE AWAY from the source of the burn (fire, hot liquid, electricity)
2. COOL the burn with clean, cool running water for 20 MINUTES
   - Do this as soon as possible (works best in first 3 hours)
   - Do NOT use ice or very cold water
3. REMOVE clothing and jewelry near the burn (do not pull off stuck clothing)
4. COVER the burn with a clean cloth or plastic wrap
5. KEEP THE PERSON WARM (cover unburned areas)
6. GO TO THE HOSPITAL - Especially for:
   - Burns larger than your palm
   - Burns on face, hands, feet, or private parts
   - Burns that are white or leathery
   - Difficulty breathing after fire/smoke exposure
   - Electrical or chemical burns

DO NOT:
❌ Put toothpaste, butter, oil, eggs, or herbs on burns - These do NOT help and may cause infection
❌ Put ice directly on burns - Can cause more damage
❌ Break blisters - This increases infection risk
❌ Remove clothing stuck to the burn
❌ Delay going to the hospital

HOW ARE BURNS TREATED?

Minor Burns:
• Kept clean and covered with special dressings
• Pain medicine
• Usually heal in 1-3 weeks

Severe Burns:
• Require hospital admission, sometimes for weeks
• IV fluids to replace what is lost through the burn
• Special dressings and wound care
• May need surgery (skin grafts)
• Rehabilitation and physiotherapy

WHAT TO EXPECT DURING RECOVERY:

• Wound care will be done regularly (can be painful - medicine will help)
• Physiotherapy is very important to prevent stiffness
• Scars are common but improve over 1-2 years
• Special pressure garments may be needed
• Emotional recovery takes time - this is normal

HOW TO PREVENT BURNS:

In the Kitchen:
• Keep children away from cooking areas
• Turn pot handles toward the back of the stove
• Test bath water before putting children in
• Never leave cooking unattended

Kerosene Safety:
• Never refuel a hot stove
• Store kerosene away from sleeping areas
• Use improved cookstoves if available

Fire Safety:
• Do not leave candles or fires burning unattended
• Have a plan to escape if fire starts
• Keep matches and lighters away from children

Electrical Safety:
• Do not touch damaged electrical wires
• Keep children away from electrical outlets

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only and is not a substitute for professional medical advice. Burns can be serious injuries requiring immediate medical attention.

Remember: Cool water for 20 minutes, then go to the hospital. Do NOT use traditional remedies.

QUESTIONS TO ASK YOUR DOCTOR:
• How severe is this burn?
• Will it need surgery or skin grafting?
• How should I care for the wound at home?
• What signs of infection should I watch for?
• Will there be permanent scarring?
• What exercises should be done to prevent stiffness?
• When can I/my child return to normal activities?
    `,

    references: [
      'World Health Organization. Burns: WHO Fact Sheet. Geneva: WHO; 2023.',
      'Peck MD. Epidemiology of burns throughout the world. Part I: Distribution and risk factors. Burns. 2011;37(7):1087-1100.',
      'Greenhalgh DG. Management of burns. New England Journal of Medicine. 2019;380(24):2349-2359.',
      'American Burn Association. Advanced Burn Life Support Course Provider Manual. Chicago: ABA; 2018.',
      'ISBI Practice Guidelines Committee. ISBI Practice Guidelines for Burn Care. Burns. 2016;42(5):953-1021.',
      'Jeschke MG, van Baar ME, Choudhry MA, et al. Burn injury. Nature Reviews Disease Primers. 2020;6(1):11.',
      'Herndon DN, ed. Total Burn Care. 5th ed. Edinburgh: Elsevier; 2018.',
      'Palmieri TL, Greenhalgh DG. Topical treatment of pediatric patients with burns: a practical guide. American Journal of Clinical Dermatology. 2002;3(8):529-534.',
      'Nussbaum SR, Carter MJ, Fife CE, et al. An economic evaluation of the impact, cost, and medicare policy implications of chronic nonhealing wounds. Value in Health. 2018;21(1):27-32.',
      'Norman G, Christie J, Liu Z, et al. Antiseptics for burns. Cochrane Database of Systematic Reviews. 2017;7(7):CD011821.',
      'Wasiak J, Cleland H, Campbell F, Spinks A. Dressings for superficial and partial thickness burns. Cochrane Database of Systematic Reviews. 2013;(3):CD002106.',
      'Ahuja RB, Bhattacharya S. Burns in the developing world and burn disasters. BMJ. 2004;329(7463):447-449.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 56. TRAUMATIC BRAIN INJURY (TBI)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '56',
    name: 'Traumatic Brain Injury',
    slug: 'traumatic-brain-injury',
    specialty: 'Emergency Medicine',
    bodySystem: 'Neurological',
    icdCode: 'S06.9',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `
Traumatic Brain Injury (TBI) is defined as an alteration in brain function, or other evidence of brain pathology, caused by an external force. It is a leading cause of death and disability worldwide, particularly affecting young adults and increasingly the elderly. TBI ranges from mild (concussion) to severe, with outcomes depending on the nature, location, and extent of injury, as well as the quality and timeliness of care.

The pathophysiology of TBI involves primary injury (direct mechanical damage at the time of impact) and secondary injury (delayed biochemical and physiological cascades including cerebral edema, ischemia, excitotoxicity, and inflammation). The goal of acute management is to prevent or minimize secondary injury by maintaining adequate cerebral perfusion and oxygenation, controlling intracranial pressure (ICP), and avoiding secondary insults.

In Africa, TBI is predominantly caused by road traffic accidents (RTAs), which account for 50-70% of cases, followed by falls and assaults. The burden is disproportionately high due to rapid motorization without commensurate road safety measures, inadequate pre-hospital care, limited access to neurosurgical services, and lack of rehabilitation facilities. Helmet use among motorcyclists (a major mode of transport) remains low in many regions.

The Glasgow Coma Scale (GCS) is the universal tool for assessing TBI severity: Mild (GCS 13-15), Moderate (GCS 9-12), and Severe (GCS 3-8). Even mild TBI can have significant long-term consequences, and all patients require appropriate evaluation and follow-up.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 ASSUME CERVICAL SPINE INJURY until cleared - Immobilize C-spine in all significant head trauma; do not flex or rotate neck

🔴 GCS IS THE KEY ASSESSMENT TOOL - Eye (4) + Verbal (5) + Motor (6) = 15; Severe TBI = GCS ≤8; document and trend GCS frequently

🔴 PREVENT SECONDARY BRAIN INJURY - Avoid hypoxia (SpO2 >94%), hypotension (SBP >90), hyperthermia, hypo/hyperglycemia, seizures

🔴 INTUBATE IF GCS ≤8 - Patient cannot protect airway; RSI with C-spine precautions; avoid hypoxia during intubation

🔴 PUPIL ASYMMETRY IS AN EMERGENCY - Unilateral dilated pupil suggests herniation; this is a neurosurgical emergency

🔴 CT HEAD IS THE IMAGING OF CHOICE - Identifies surgical lesions (epidural, subdural, contusions); do not delay for MRI

🔴 ELEVATE HEAD OF BED 30° - Simple intervention to reduce ICP; keep head midline to promote venous drainage

🔴 MANNITOL OR HYPERTONIC SALINE for raised ICP - Mannitol 0.5-1 g/kg IV bolus; 3% saline 250mL bolus; temporizing measures before surgery
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• 69 million people sustain TBI annually worldwide
• Leading cause of death in people under 45 years
• Major cause of disability-adjusted life years (DALYs) lost
• Bimodal age distribution: Young adults (15-30) and elderly (>65)
• Male:Female ratio approximately 2-3:1
• Leading causes globally: Falls (especially elderly), Road traffic accidents, Assaults, Sports injuries
• Mild TBI accounts for 80-90% of all TBI

AFRICAN EPIDEMIOLOGY:
• TBI is a leading cause of death and disability in Africa
• Estimated incidence: 150-300 per 100,000 population (likely underestimated)
• Road Traffic Accidents: 50-70% of TBI in most African countries
• Africa has highest road traffic fatality rate globally (26.6 per 100,000)
• Motorcycle accidents: Major contributor, especially in West Africa
• Helmet use among motorcyclists: Often <20% in many regions
• Assault and interpersonal violence: Second leading cause in many areas
• Falls: Increasing, especially in urban areas and among elderly
• Limited neurosurgical capacity: Many countries have <1 neurosurgeon per million
• Pre-hospital care often non-existent or rudimentary
• High mortality rates due to delayed or unavailable definitive care
• Significant economic burden on families and healthcare systems

HIGH-RISK POPULATIONS:
• Young males (15-35 years): RTAs, assaults
• Commercial motorcyclists (okada/boda-boda riders)
• Pedestrians (especially children)
• Alcohol-intoxicated individuals
• Elderly: Falls, often on anticoagulants
• Construction workers and manual laborers
• Athletes (contact sports)

RISK FACTORS:
• Not wearing seatbelts or helmets
• Alcohol and substance use
• Speeding and reckless driving
• Poor road infrastructure
• Lack of traffic law enforcement
• Previous TBI (increases risk of subsequent injury)
• Anticoagulant use (increases severity)
• Occupational hazards
• Domestic violence
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

TBI SEVERITY CLASSIFICATION (Glasgow Coma Scale):

MILD TBI (GCS 13-15):
• Loss of consciousness <30 minutes (or none)
• Post-traumatic amnesia <24 hours
• Normal or minimal neurological findings
• Symptoms: Headache, dizziness, nausea, confusion, difficulty concentrating
• Usually good prognosis, but post-concussion syndrome may occur

MODERATE TBI (GCS 9-12):
• Loss of consciousness 30 minutes to 24 hours
• Post-traumatic amnesia 1-7 days
• May have focal neurological deficits
• Requires hospitalization and CT imaging
• Variable outcomes; many achieve good recovery

SEVERE TBI (GCS 3-8):
• Loss of consciousness >24 hours
• Post-traumatic amnesia >7 days
• Often with focal deficits, abnormal posturing
• Requires ICU, often intubation, ICP monitoring
• High mortality and morbidity; survivors often have significant disability

GLASGOW COMA SCALE (GCS):

Eye Opening (E):
• 4 = Spontaneous
• 3 = To voice/command
• 2 = To pain
• 1 = None

Verbal Response (V):
• 5 = Oriented
• 4 = Confused conversation
• 3 = Inappropriate words
• 2 = Incomprehensible sounds
• 1 = None

Motor Response (M):
• 6 = Obeys commands
• 5 = Localizes pain
• 4 = Withdraws from pain
• 3 = Abnormal flexion (decorticate)
• 2 = Extension (decerebrate)
• 1 = None

Total GCS = E + V + M (Range 3-15)

TYPES OF TBI:

Concussion:
• Transient neurological dysfunction
• No macroscopic lesion on CT
• May have brief LOC, confusion, amnesia
• Usually full recovery, but can have persistent symptoms

Cerebral Contusion:
• Bruising of brain parenchyma
• Often in coup (impact site) and contrecoup (opposite side) locations
• Common in frontal and temporal lobes
• May evolve/expand over first 24-48 hours

Epidural Hematoma (EDH):
• Bleeding between skull and dura
• Usually from middle meningeal artery (temporal)
• Classic: Lucid interval then rapid deterioration
• Lens-shaped (biconvex) on CT
• Neurosurgical emergency - excellent outcome if evacuated early

Subdural Hematoma (SDH):
• Bleeding between dura and arachnoid
• From torn bridging veins
• Acute (<3 days), Subacute (3-21 days), Chronic (>21 days)
• Crescent-shaped on CT
• Acute SDH has high mortality; chronic SDH common in elderly

Subarachnoid Hemorrhage (Traumatic):
• Blood in subarachnoid space
• Common in TBI
• Can cause vasospasm and hydrocephalus

Diffuse Axonal Injury (DAI):
• Shearing injury to white matter
• From rotational acceleration/deceleration
• Often minimal CT findings; MRI shows punctate hemorrhages
• Poor prognosis if severe; prolonged coma

Skull Fractures:
• Linear (most common), Depressed, Basilar, Open
• Basilar skull fracture signs: Raccoon eyes, Battle sign, CSF rhinorrhea/otorrhea, hemotympanum
• Depressed fracture may need surgical elevation

SIGNS OF RAISED INTRACRANIAL PRESSURE (ICP):
• Decreasing level of consciousness
• Headache (may be severe, worse with coughing/straining)
• Vomiting (often projectile)
• Papilledema (may be absent acutely)
• Cushing triad (late): Hypertension, bradycardia, irregular respirations
• Pupillary changes: Initially ipsilateral dilation, then bilateral

HERNIATION SYNDROMES:

Uncal (Transtentorial) Herniation:
• Ipsilateral pupil dilation (CN III compression)
• Contralateral hemiparesis
• Decreasing consciousness
• May progress to bilateral fixed dilated pupils

Central Herniation:
• Bilateral small reactive pupils → bilateral fixed dilated
• Decorticate → Decerebrate posturing
• Respiratory changes

Tonsillar Herniation:
• Cerebellar tonsils through foramen magnum
• Rapidly fatal; cardiorespiratory arrest

PHYSICAL EXAMINATION:
• Primary survey: ABCDE with C-spine immobilization
• GCS: Document and trend
• Pupils: Size, reactivity, symmetry
• Motor exam: Lateralizing signs
• Cranial nerves
• Signs of skull fracture: Scalp lacerations, palpable step-off, basilar signs
• Fundoscopy: Papilledema (if time permits)
• Look for other injuries: Polytrauma is common
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: PRIMARY SURVEY AND STABILIZATION

ABCDE Approach:
• Airway: Secure with C-spine immobilization; intubate if GCS ≤8
• Breathing: Ensure oxygenation (SpO2 >94%); avoid hyperventilation
• Circulation: Treat hypotension aggressively (SBP >90 target)
• Disability: GCS, pupils, lateralizing signs
• Exposure: Look for other injuries; prevent hypothermia

STEP 2: FOCUSED HISTORY (AMPLE)

• Allergies
• Medications (especially anticoagulants, antiplatelets)
• Past medical history (bleeding disorders, previous TBI)
• Last meal
• Events: Mechanism, time of injury, loss of consciousness, seizures, amnesia

STEP 3: DETERMINE NEED FOR CT HEAD

Indications for CT Head (Canadian CT Head Rules and others):
• GCS <15 at 2 hours post-injury
• Suspected open or depressed skull fracture
• Any sign of basilar skull fracture
• Vomiting (≥2 episodes)
• Age ≥65 years
• Amnesia before impact ≥30 minutes
• Dangerous mechanism (pedestrian vs vehicle, ejection, fall >3 feet or >5 stairs)
• Anticoagulant use
• Focal neurological deficit
• Seizure post-injury

Note: In resource-limited settings, CT may not be available; clinical judgment and close observation become critical.

STEP 4: IMAGING

Non-Contrast CT Head (Gold Standard):
• Fast, widely available, identifies surgical lesions
• Obtain as soon as patient stabilized
• Look for: Hematomas (EDH, SDH, ICH), contusions, skull fractures, mass effect, midline shift, hydrocephalus

CT Findings:
• Epidural hematoma: Biconvex (lens-shaped), usually temporal, does not cross suture lines
• Subdural hematoma: Crescent-shaped, crosses suture lines, may be bilateral
• Contusion: Hyperdense areas in brain parenchyma, often frontal/temporal
• Diffuse axonal injury: Often CT-negative; petechial hemorrhages at gray-white junction
• Subarachnoid hemorrhage: Blood in sulci, cisterns
• Midline shift: >5mm concerning; indicates mass effect

CT Cervical Spine:
• Obtain in significant TBI; clinical clearance unreliable if GCS altered

MRI Brain:
• More sensitive for DAI, posterior fossa, small lesions
• Not for acute evaluation; too time-consuming
• Use for prognostication and if CT does not explain deficits

STEP 5: CLASSIFY SEVERITY AND IDENTIFY SURGICAL LESIONS

Surgical Lesions Requiring Urgent Evacuation:
• Epidural hematoma >30 mL or causing mass effect
• Acute subdural hematoma >10mm or midline shift >5mm
• Depressed skull fracture > depth of skull thickness
• Open/compound skull fracture
• Posterior fossa hematoma with mass effect or hydrocephalus

If No Neurosurgery Available:
• Stabilize and transfer urgently
• Consider burr holes for rapidly deteriorating patient with clear lateralizing signs (if trained and no other option)
    `,

    differentialDiagnosis: [
      'Stroke (ischemic or hemorrhagic) - May present similarly; history of trauma absent; CT distinguishes',
      'Intracranial infection (meningitis, encephalitis, abscess) - Fever, neck stiffness, no trauma history',
      'Metabolic encephalopathy (hypoglycemia, hepatic, uremic) - Blood glucose, metabolic panel; no trauma',
      'Alcohol or drug intoxication - Very common; may mask or mimic TBI; always consider both',
      'Postictal state - History of seizures; TBI can cause seizures; both may coexist',
      'Syncope - May cause fall and head injury; cardiac workup if unclear',
      'Hypoxic brain injury - Drowning, strangulation, cardiac arrest; history clarifies',
      'Non-convulsive status epilepticus - EEG needed; consider in unexplained altered consciousness',
      'Hypertensive encephalopathy - Severely elevated BP; posterior reversible encephalopathy on imaging',
      'CNS tumor with hemorrhage - CT/MRI shows tumor; may present with acute deterioration',
      'Spontaneous subarachnoid hemorrhage - Sudden severe headache; may have minor trauma from collapse',
      'Subdural hematoma (spontaneous or on anticoagulation) - May have minimal or no trauma history',
      'Psychogenic unresponsiveness - Exam inconsistencies; normal investigations',
      'Locked-in syndrome - Appears unresponsive but aware; vertical eye movements preserved',
      'Carbon monoxide poisoning - Exposure history; cherry red skin; carboxyhemoglobin levels'
    ],

    investigations: [
      {
        name: 'Glasgow Coma Scale (GCS)',
        indication: '🔴 ESSENTIAL - Immediate and serial assessment; documents severity and trends; correlates with outcome',
        availability: 'BASIC'
      },
      {
        name: 'Non-Contrast CT Head',
        indication: '🔴 ESSENTIAL - Gold standard for acute TBI; identifies surgical lesions; obtain ASAP after stabilization',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Cervical Spine',
        indication: '🔴 ESSENTIAL - Assume C-spine injury until cleared; clinical clearance unreliable if altered consciousness',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Hypoglycemia mimics and worsens TBI; hyperglycemia worsens outcomes; correct abnormalities',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Baseline hemoglobin; thrombocytopenia affects bleeding risk; leukocytosis may indicate stress/infection',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR, aPTT)',
        indication: '🔴 ESSENTIAL - Identify coagulopathy; critical if on anticoagulants; guides reversal',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes',
        indication: '🔴 ESSENTIAL - Sodium critical (hypo/hypernatremia affects ICP); monitor closely especially with mannitol',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, Urea)',
        indication: '🔴 ESSENTIAL - Baseline; affects drug dosing; mannitol contraindicated in renal failure',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🔴 ESSENTIAL - Prepare for surgery and transfusion if needed',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas',
        indication: '🟠 IMPORTANT - Guide ventilation; avoid hypoxia and hypocapnia; assess metabolic status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Alcohol Level',
        indication: '🟠 IMPORTANT - Alcohol very commonly involved; affects assessment and management',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Lactate',
        indication: '🟠 IMPORTANT - Marker of tissue hypoperfusion; elevated in shock states',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 IMPORTANT - Polytrauma common; aspiration risk; ETT position if intubated',
        availability: 'BASIC'
      },
      {
        name: 'MRI Brain',
        indication: '🟡 WHEN AVAILABLE - More sensitive for DAI, posterior fossa; not for acute evaluation; for prognostication',
        availability: 'ADVANCED'
      },
      {
        name: 'Intracranial Pressure Monitoring',
        indication: '🟡 SPECIALIZED - For severe TBI (GCS ≤8 with abnormal CT); guides ICP management',
        availability: 'ADVANCED'
      },
      {
        name: 'EEG',
        indication: '🟡 WHEN INDICATED - For suspected non-convulsive seizures; prognostication',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ C-SPINE IMMOBILIZATION: Assume cervical spine injury; manual in-line stabilization until cleared',
        '⚡ AIRWAY: Secure airway if GCS ≤8; RSI with C-spine precautions; avoid nasal route if basilar fracture suspected',
        '⚡ BREATHING: Target SpO2 >94%; avoid hypoxia at all costs; normal ventilation (avoid hyper/hypoventilation)',
        '⚡ CIRCULATION: Target SBP >90 mmHg (preferably >100); IV fluids (isotonic crystalloid); avoid hypotension',
        '⚡ DISABILITY: Document GCS, pupil size and reactivity, motor response; reassess frequently',
        '⚡ BLOOD GLUCOSE: Check and correct; target 100-180 mg/dL; avoid hypoglycemia',
        '⚡ CT HEAD: Obtain as soon as patient stabilized; identify surgical lesions',
        '⚡ REVERSE ANTICOAGULATION: If on warfarin, give Vitamin K + FFP or PCC; for DOACs, specific reversal agents if available',
        '⚡ ELEVATE HEAD 30°: Keep head midline; promotes venous drainage; reduces ICP',
        '⚡ TREAT RAISED ICP: If signs of herniation - Mannitol 0.5-1 g/kg IV OR Hypertonic saline; call neurosurgery',
        '⚡ NEUROSURGICAL CONSULTATION: For surgical lesions, severe TBI, deteriorating patient'
      ],
      
      pharmacological: [
        {
          drug: 'Mannitol 20%',
          indication: '🔴 CRITICAL - Osmotic diuretic for raised ICP; temporizing measure before surgery; reduces cerebral edema',
          dose: '0.5-1 g/kg IV bolus over 15-20 minutes. May repeat 0.25-0.5 g/kg every 6 hours if needed. Maximum daily dose: 2 g/kg. Hold if serum osmolality >320 mOsm/kg or renal failure.',
          route: 'IV',
          duration: 'Acute phase; usually 24-72 hours',
          contraindications: ['Renal failure', 'Severe dehydration', 'Active intracranial bleeding (relative - use if herniation)', 'Hyperosmolality'],
          sideEffects: ['Dehydration', 'Electrolyte imbalance (hypokalemia)', 'Rebound ICP elevation', 'Acute kidney injury', 'Hypotension'],
          alternatives: ['Hypertonic saline 3% or 23.4%']
        },
        {
          drug: 'Hypertonic Saline (3%)',
          indication: '🔴 CRITICAL - Alternative to mannitol for raised ICP; may be preferred if hypotensive',
          dose: '3% NaCl: 250 mL (2-5 mL/kg) IV bolus over 10-20 minutes. 23.4% NaCl: 30 mL via central line over 10-20 minutes (for imminent herniation). Target serum Na: 145-155 mEq/L.',
          route: 'IV (23.4% requires central line)',
          duration: 'Acute phase',
          contraindications: ['Hypernatremia (Na >160)', 'Volume overload'],
          sideEffects: ['Hypernatremia', 'Central pontine myelinolysis (with rapid correction of chronic hyponatremia)', 'Phlebitis', 'Volume overload'],
          alternatives: ['Mannitol']
        },
        {
          drug: 'Propofol',
          indication: '🟠 IMPORTANT - Sedation for intubated patients; reduces cerebral metabolic rate and ICP',
          dose: 'Infusion: 0.5-4 mg/kg/hour. Bolus: 0.5-1 mg/kg for RSI. Short-acting; allows neurological assessment when held.',
          route: 'IV',
          duration: 'While intubated and sedation required',
          contraindications: ['Propofol infusion syndrome (prolonged high doses)', 'Hypotension', 'Egg/soy allergy'],
          sideEffects: ['Hypotension', 'Respiratory depression', 'Propofol infusion syndrome (prolonged use)', 'Hypertriglyceridemia'],
          alternatives: ['Midazolam', 'Dexmedetomidine']
        },
        {
          drug: 'Fentanyl',
          indication: '🟠 IMPORTANT - Analgesia for intubated patients; minimal effect on ICP if BP maintained',
          dose: 'Bolus: 1-2 mcg/kg IV. Infusion: 0.5-2 mcg/kg/hour.',
          route: 'IV',
          duration: 'While pain/sedation required',
          contraindications: ['Respiratory depression without airway control'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Chest wall rigidity (with rapid high doses)', 'Constipation'],
          alternatives: ['Morphine (avoid in hemodynamic instability)']
        },
        {
          drug: 'Phenytoin / Fosphenytoin',
          indication: '🟠 IMPORTANT - Seizure prophylaxis in severe TBI (first 7 days); treatment of post-traumatic seizures',
          dose: 'Phenytoin: Loading dose 15-20 mg/kg IV at max 50 mg/min. Maintenance: 100 mg IV/PO every 8 hours. Fosphenytoin: 15-20 mg PE/kg IV at 100-150 mg PE/min. Monitor levels.',
          route: 'IV or PO',
          duration: '7 days prophylaxis; longer if seizures occur',
          contraindications: ['Heart block', 'Sinus bradycardia', 'Hypersensitivity'],
          sideEffects: ['Hypotension (IV too fast)', 'Arrhythmias', 'Gingival hyperplasia', 'Ataxia', 'Purple glove syndrome'],
          alternatives: ['Levetiracetam 500-1000 mg IV/PO BD (preferred by many; fewer interactions)']
        },
        {
          drug: 'Levetiracetam',
          indication: '🟠 ALTERNATIVE - Seizure prophylaxis; fewer drug interactions than phenytoin; no level monitoring needed',
          dose: 'Loading: 1000-2000 mg IV over 15 minutes. Maintenance: 500-1000 mg IV/PO every 12 hours.',
          route: 'IV or PO',
          duration: '7 days for prophylaxis',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Somnolence', 'Behavioral changes', 'Dizziness'],
          alternatives: ['Phenytoin']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🔴 ESSENTIAL - Treat fever aggressively; hyperthermia worsens outcomes',
          dose: 'Adults: 1g IV/PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours.',
          route: 'IV or PO/PR',
          duration: 'As needed',
          contraindications: ['Severe liver disease'],
          sideEffects: ['Hepatotoxicity with overdose'],
          alternatives: ['Active cooling for fever']
        },
        {
          drug: 'Vitamin K (Phytonadione)',
          indication: '🔴 ESSENTIAL if on Warfarin - Reversal of anticoagulation; give with FFP or PCC for rapid reversal',
          dose: '10 mg IV slow push over 10 minutes.',
          route: 'IV',
          duration: 'Single dose; repeat if needed',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Anaphylaxis (rare, especially with rapid IV)', 'Flushing'],
          alternatives: ['Give alongside FFP or PCC for rapid effect']
        },
        {
          drug: 'Fresh Frozen Plasma (FFP)',
          indication: '🟠 FOR COAGULOPATHY - Reversal of warfarin or coagulopathy; slower than PCC',
          dose: '10-15 mL/kg IV. Large volume required.',
          route: 'IV',
          duration: 'Single dose or as needed based on INR',
          contraindications: ['Volume overload (relative)', 'IgA deficiency (risk of anaphylaxis)'],
          sideEffects: ['Volume overload', 'TRALI', 'Transfusion reactions', 'Infection risk'],
          alternatives: ['Prothrombin Complex Concentrate (PCC) - faster, lower volume']
        },
        {
          drug: 'Prothrombin Complex Concentrate (PCC)',
          indication: '🟠 PREFERRED for warfarin reversal - Faster and lower volume than FFP',
          dose: '25-50 units/kg IV based on INR. Check local product for specific dosing.',
          route: 'IV',
          duration: 'Single dose',
          contraindications: ['DIC (relative)', 'Known hypersensitivity'],
          sideEffects: ['Thromboembolic events', 'DIC'],
          alternatives: ['FFP']
        },
        {
          drug: 'Tranexamic Acid (TXA)',
          indication: '🟡 CONSIDER in TBI - CRASH-3 trial showed benefit if given <3 hours in mild-moderate TBI with intracranial bleeding; no benefit if severe or >3 hours',
          dose: '1 g IV over 10 minutes within 3 hours of injury. Only for patients with GCS >8 and intracranial bleeding on CT.',
          route: 'IV',
          duration: 'Single dose',
          contraindications: ['Severe TBI (GCS ≤8)', '>3 hours from injury', 'Known hypersensitivity'],
          sideEffects: ['Seizures (rare)', 'Thromboembolic events', 'Nausea'],
          alternatives: ['None specific']
        }
      ],
      
      nonPharmacological: [
        'C-SPINE IMMOBILIZATION: Maintain until cleared clinically or by imaging; hard collar and blocks',
        'HEAD ELEVATION: 30° head of bed elevation; keep head midline to promote venous drainage',
        'AVOID HYPERVENTILATION: Target PaCO2 35-40 mmHg; avoid routine hyperventilation (causes vasoconstriction and ischemia)',
        'TARGETED TEMPERATURE MANAGEMENT: Avoid hyperthermia (treat fever aggressively); mild hypothermia not recommended routinely',
        'GLUCOSE CONTROL: Avoid hypoglycemia and marked hyperglycemia; target 100-180 mg/dL',
        'NORMOVOLEMIA: Avoid dehydration and fluid overload; isotonic crystalloids (NS or Ringer Lactate)',
        'BLOOD PRESSURE MANAGEMENT: Avoid hypotension (SBP >90, preferably >100); may need vasopressors',
        'VENOUS THROMBOEMBOLISM PROPHYLAXIS: Mechanical (SCDs); pharmacological (LMWH) when safe (usually 24-72 hours post-injury if no ongoing bleeding)',
        'STRESS ULCER PROPHYLAXIS: H2 blockers or PPIs for patients at risk',
        'NUTRITIONAL SUPPORT: Early enteral feeding within 24-72 hours if possible; high caloric needs',
        'NEUROLOGICAL MONITORING: Frequent GCS, pupil checks; ICP monitoring if available for severe TBI',
        'REHABILITATION: Early involvement of physio, OT, speech therapy; crucial for outcomes'
      ],
      
      surgical: [
        'CRANIOTOMY FOR EPIDURAL HEMATOMA: Urgent evacuation; excellent outcomes if done early before herniation',
        'CRANIOTOMY FOR ACUTE SUBDURAL HEMATOMA: If >10mm thickness or >5mm midline shift or GCS drop; high mortality despite surgery',
        'CRANIOTOMY FOR INTRACEREBRAL HEMATOMA: Selected cases with mass effect; controversial in many situations',
        'DECOMPRESSIVE CRANIECTOMY: Removal of bone flap to allow brain swelling; for refractory raised ICP; RESCUE-ICP trial showed survival benefit but more disability',
        'ELEVATION OF DEPRESSED SKULL FRACTURE: If depression greater than skull thickness or open fracture',
        'DEBRIDEMENT OF OPEN/PENETRATING INJURY: Remove debris, devitalized tissue; repair dural lacerations',
        'CSF DRAINAGE: External ventricular drain (EVD) for ICP monitoring and therapeutic drainage',
        'POSTERIOR FOSSA DECOMPRESSION: For posterior fossa hematomas with brainstem compression or hydrocephalus',
        'BURR HOLES: May be life-saving in deteriorating patient when craniotomy not immediately available; for epidural hematoma',
        'CRANIOPLASTY: Delayed procedure to replace bone flap after decompressive craniectomy'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: GCS ≤8 (Severe TBI) - Requires neurosurgical care, ICU',
        '🔴 IMMEDIATE REFERRAL: Surgical lesion on CT (EDH, large SDH, depressed fracture) - Urgent neurosurgery',
        '🔴 IMMEDIATE REFERRAL: Deteriorating GCS - Indicates evolving lesion or raised ICP',
        '🔴 IMMEDIATE REFERRAL: Unequal pupils or focal neurological signs - Possible herniation',
        '🔴 IMMEDIATE REFERRAL: Open or compound skull fracture - Neurosurgical repair needed',
        '🔴 IMMEDIATE REFERRAL: CSF leak (rhinorrhea/otorrhea) - Risk of meningitis; may need repair',
        '🟠 URGENT REFERRAL: Moderate TBI (GCS 9-12) - Requires CT, observation, may need ICU',
        '🟠 URGENT REFERRAL: Any intracranial hemorrhage on CT - Even if mild symptoms; may need observation',
        '🟠 URGENT REFERRAL: Anticoagulated patient with head injury - Higher risk of delayed hemorrhage',
        '🟠 URGENT REFERRAL: Skull fracture - Risk of underlying injury; needs specialist evaluation',
        '🟡 ROUTINE REFERRAL: Post-concussion syndrome - Persistent symptoms >4 weeks; rehabilitation input',
        '🟡 ROUTINE REFERRAL: Cognitive or behavioral changes after TBI - Neuropsychological assessment'
      ],
      
      monitoring: [
        'GLASGOW COMA SCALE: Hourly initially; more frequent if deteriorating; trend is critical',
        'PUPIL SIZE AND REACTIVITY: Assess with each GCS check; asymmetry requires immediate action',
        'VITAL SIGNS: Continuous monitoring; watch for Cushing triad (late sign of raised ICP)',
        'OXYGEN SATURATION: Continuous; maintain >94%; hypoxia worsens outcomes',
        'BLOOD PRESSURE: Continuous or frequent; avoid hypotension (SBP <90); consider MAP targets',
        'INTRACRANIAL PRESSURE (if monitoring): Target ICP <22 mmHg; CPP 60-70 mmHg',
        'CEREBRAL PERFUSION PRESSURE: CPP = MAP - ICP; target 60-70 mmHg',
        'BLOOD GLUCOSE: Every 4-6 hours; avoid hypo/hyperglycemia',
        'SERUM SODIUM: Daily or more often if using mannitol/hypertonic saline; critical in TBI',
        'SERUM OSMOLALITY: If using mannitol; hold if >320 mOsm/kg',
        'URINE OUTPUT: Hourly; monitor for diabetes insipidus (excessive dilute urine)',
        'TEMPERATURE: Continuous or every 4 hours; treat fever aggressively',
        'REPEAT CT HEAD: If clinical deterioration; at 6-24 hours for moderate-severe TBI; before stopping anticoagulation reversal'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE HOSPITALIZATION:

Severe TBI (GCS ≤8):
• ICU management until stable
• Serial neurological exams
• ICP management if monitoring in place
• Early rehabilitation consultation
• Address nutrition, DVT prophylaxis, complications
• Family communication and support

Moderate TBI (GCS 9-12):
• Hospital observation minimum 24-48 hours
• Serial neurological exams
• Repeat CT if any deterioration
• Begin rehabilitation planning

Mild TBI (GCS 13-15):
• If CT negative and improving, may discharge with reliable caregiver
• Written head injury instructions
• Return precautions clearly explained
• Follow-up appointment within 1-2 weeks

DISCHARGE CRITERIA (Mild TBI):
• GCS 15 and neurologically intact
• No significant findings on CT (or CT not indicated)
• Able to maintain oral intake
• Reliable caregiver available for observation
• Access to emergency care if deterioration
• Understands return precautions

HEAD INJURY DISCHARGE INSTRUCTIONS:
• Rest (cognitive and physical) for first 24-48 hours
• No driving, alcohol, contact sports, or demanding mental tasks
• Return immediately if: Worsening headache, repeated vomiting, confusion, seizure, weakness, vision changes, unequal pupils, clear fluid from nose/ear

SHORT-TERM FOLLOW-UP (2-4 weeks):
• Assess symptom resolution
• Screen for post-concussion syndrome
• Gradual return to activities
• Repeat imaging only if new symptoms

POST-CONCUSSION SYNDROME:
• Symptoms persisting >4 weeks after mild TBI
• Headache, dizziness, fatigue, irritability, concentration problems, sleep disturbance
• Management: Reassurance, symptom management, graded return to activity, neuropsychological support

LONG-TERM FOLLOW-UP (Moderate-Severe TBI):
• Rehabilitation programs (inpatient or outpatient)
• Neuropsychological assessment
• Management of physical, cognitive, and behavioral sequelae
• Treatment of post-traumatic epilepsy (if develops)
• Vocational rehabilitation
• Support groups and family support

RETURN TO PLAY (Sports Concussion):
• Symptom-free at rest before beginning return protocol
• Graduated return: Light aerobic → sport-specific → non-contact drills → full contact → return to competition
• Minimum 24-48 hours between steps
• Return to previous step if symptoms recur
• No same-day return to play after concussion
      `
    },

    redFlags: [
      '🔴 CRITICAL: GCS ≤8 or deteriorating GCS - Severe TBI; intubate; urgent CT; neurosurgery',
      '🔴 CRITICAL: Unilateral dilated pupil - Impending uncal herniation; mannitol/hypertonic saline; emergency surgery',
      '🔴 CRITICAL: Bilateral fixed dilated pupils - Late herniation or brain death; poor prognosis',
      '🔴 CRITICAL: Cushing triad (HTN, bradycardia, irregular respiration) - Late sign of raised ICP; imminent herniation',
      '🔴 CRITICAL: Decerebrate or decorticate posturing - Severe brainstem injury; very poor prognosis',
      '🔴 CRITICAL: Rapidly expanding hematoma on CT - Urgent surgical evacuation needed',
      '🟠 URGENT: Midline shift >5mm on CT - Significant mass effect; likely needs surgery',
      '🟠 URGENT: Lucid interval followed by deterioration - Classic for epidural hematoma; surgical emergency',
      '🟠 URGENT: New focal neurological deficit - Indicates evolving lesion; repeat CT',
      '🟠 URGENT: Seizure - Post-traumatic seizure; load with antiepileptic; repeat CT',
      '🟠 URGENT: CSF leak (rhinorrhea or otorrhea) - Basilar skull fracture; meningitis risk',
      '🟠 URGENT: Coagulopathy with intracranial hemorrhage - Urgent reversal needed',
      '🟡 CAUTION: Anticoagulated patient with head injury - May have delayed bleed; low threshold for CT and admission',
      '🟡 CAUTION: Persistent vomiting or severe headache - May indicate raised ICP; needs evaluation'
    ],

    complications: [
      'Cerebral herniation - Uncal, central, or tonsillar; leads to brainstem compression and death if not treated',
      'Post-traumatic seizures - Immediate (<24h), early (1-7 days), late (>7 days); late seizures = post-traumatic epilepsy',
      'Hydrocephalus - From obstruction, blood in ventricles, or impaired CSF absorption; may need shunt',
      'Cerebral vasospasm - After traumatic SAH; can cause delayed ischemia',
      'Infection (meningitis, abscess) - Especially with open fractures, CSF leak; requires antibiotics, possible surgery',
      'DVT and pulmonary embolism - From immobility; prophylaxis important',
      'Diabetes insipidus - From pituitary stalk injury; excessive dilute urine; treat with desmopressin',
      'SIADH - Causes hyponatremia; fluid restriction',
      'Cerebral salt wasting - Hyponatremia with volume depletion; saline replacement',
      'Post-concussion syndrome - Persistent symptoms after mild TBI; headache, dizziness, cognitive issues',
      'Chronic traumatic encephalopathy (CTE) - From repetitive head trauma; progressive neurodegeneration',
      'Cognitive impairment - Memory, attention, executive function deficits; may be permanent',
      'Behavioral and psychiatric changes - Depression, anxiety, aggression, personality change',
      'Motor deficits - Hemiparesis, coordination problems; depend on injury location',
      'Post-traumatic headache - Very common; can be chronic',
      'Hypopituitarism - From pituitary injury; may present months later'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

PROGNOSTIC FACTORS:

Strong Predictors of Poor Outcome:
• Low GCS (especially motor score)
• Fixed dilated pupils
• Advanced age
• Hypotension (SBP <90)
• Hypoxia
• CT findings: Compressed/absent basal cisterns, midline shift, subarachnoid blood

IMPACT Prognostic Model (Validated):
• Uses age, GCS motor score, pupil reactivity, CT characteristics
• Available as online calculator
• Predicts 6-month mortality and unfavorable outcome

PROGNOSIS BY SEVERITY:

Mild TBI (GCS 13-15):
• Mortality: <1%
• Most make full recovery within weeks to months
• 10-20% have persistent post-concussion symptoms
• Repeat mild TBI increases risk of prolonged recovery

Moderate TBI (GCS 9-12):
• Mortality: 5-15%
• Many achieve functional independence
• May have residual cognitive or physical deficits
• Return to work rate: 60-80%

Severe TBI (GCS 3-8):
• Mortality: 30-50%
• Of survivors, many have significant disability
• Vegetative state or severe disability in significant proportion
• Good recovery possible, especially in younger patients
• Recovery continues for months to years

OUTCOMES SCALE (Glasgow Outcome Scale - Extended):

1. Death
2. Vegetative state: Unaware of surroundings
3. Lower severe disability: Dependent for daily activities
4. Upper severe disability: Some independence at home
5. Lower moderate disability: Independent but cannot resume all activities
6. Upper moderate disability: Reduced capacity but can work
7. Lower good recovery: Minor symptoms not affecting daily life
8. Upper good recovery: Full recovery

FACTORS AFFECTING RECOVERY:
• Age: Younger patients have better plasticity and recovery
• Pre-injury function: Higher baseline = better outcomes
• Injury severity and type
• Quality of acute care and rehabilitation
• Social support
• Absence of secondary insults (hypotension, hypoxia)
• Access to rehabilitation services

TIMELINE OF RECOVERY:
• Most rapid recovery in first 6 months
• Continued improvement up to 2 years or longer
• Plateau varies by individual
• Late deterioration may indicate new pathology
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

ROAD TRAFFIC SAFETY:

Helmet Use:
• Motorcycle helmets reduce death by 42% and head injury by 69%
• Mandatory helmet laws with enforcement
• Bicycle helmet promotion
• Quality standards for helmets

Seatbelt Use:
• Reduces fatal injury by 45-50%
• Mandatory seatbelt laws
• Enforcement and public education
• Child restraint seats for young children

Speed Reduction:
• Speed limits appropriate to road conditions
• Traffic calming measures in urban areas
• Enforcement of speed limits

Alcohol and Drug Control:
• Legal limits for blood alcohol while driving
• Enforcement (checkpoints)
• Public education campaigns
• Restrictions on alcohol sales times

Road Infrastructure:
• Improved road design
• Pedestrian crossings and walkways
• Street lighting
• Separation of motorized and non-motorized traffic
• Guardrails and barriers

Vehicle Safety:
• Vehicle safety standards
• Regular vehicle inspections
• Airbags and safety features

FALL PREVENTION:

Elderly:
• Home hazard assessment and modification
• Remove loose rugs, improve lighting
• Handrails on stairs and in bathrooms
• Vision and hearing assessment
• Medication review (reduce sedatives)
• Exercise programs for strength and balance
• Hip protectors for high-risk individuals

Children:
• Playground safety standards
• Window guards in high-rise buildings
• Supervision on stairs and elevated surfaces
• Safety gates at stairs

Workplace:
• Fall protection equipment
• Safety training
• Proper scaffolding and ladders

SPORTS:

• Protective equipment (helmets, mouthguards)
• Rule changes to reduce head contact
• Education on concussion recognition
• Strict return-to-play protocols
• Baseline neuropsychological testing for high-risk sports

VIOLENCE PREVENTION:

• Community violence prevention programs
• Domestic violence intervention
• Alcohol and substance abuse programs
• Conflict resolution education

PRE-HOSPITAL CARE:

• Emergency medical services development
• First aid training for public
• Early immobilization and transport protocols

PUBLIC EDUCATION:

• Awareness campaigns on TBI causes and prevention
• First aid and recognition of head injury
• Importance of seeking medical care
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS A HEAD INJURY?

A head injury (also called traumatic brain injury or TBI) happens when a blow or jolt to the head causes damage to the brain. It can be mild (like a concussion) or severe. Even mild head injuries need to be taken seriously.

CAUSES:
• Road accidents (the most common cause in Africa)
• Falls
• Assaults
• Sports injuries

WARNING SIGNS - GO TO HOSPITAL IMMEDIATELY IF:
• Unconscious or cannot be woken up
• Confused or not making sense
• Severe or worsening headache
• Vomiting more than once
• Seizure (fits/convulsions)
• Weakness on one side of the body
• One pupil larger than the other
• Clear fluid from nose or ear
• Blood from ears
• Cannot recognize people or places
• Unusual behavior

HEAD INJURY CARE AT HOME:

If sent home after a mild head injury:
• REST for the first 24-48 hours (both physical and mental rest)
• Someone should check on you every few hours, including at night
• Take paracetamol for headache (NOT aspirin or ibuprofen in first 24 hours)
• Do NOT drive, drink alcohol, or take sleeping pills
• Do NOT play sports or do strenuous activity
• Use ice packs wrapped in cloth for swelling

RECOVERY FROM MILD HEAD INJURY:

Most people recover fully, but you may experience:
• Headaches
• Dizziness
• Difficulty concentrating
• Tiredness
• Irritability
• Sleep problems
• Memory problems

These symptoms usually improve over days to weeks. If they persist beyond a month, see your doctor.

RETURNING TO NORMAL ACTIVITIES:

• Return gradually - do not rush back to work or school
• Start with light activities and increase slowly
• Stop if symptoms return or worsen
• For sports - follow your doctor medical advice before returning

FOR CAREGIVERS OF SEVERE HEAD INJURY:

Recovery from severe head injury takes months to years. You may see:
• Physical problems (weakness, balance, coordination)
• Difficulty speaking or understanding
• Memory and thinking problems
• Changes in personality or behavior
• Emotional changes (anger, depression)

Tips for caregivers:
• Be patient - recovery is slow
• Establish routines
• Give simple, clear instructions
• Reduce distractions
• Allow rest periods
• Celebrate small improvements
• Take care of yourself too - seek support

PREVENTION:

• ALWAYS wear a helmet when riding a motorcycle or bicycle
• ALWAYS wear a seatbelt in a car
• Do not drink and drive
• Make your home safe (remove loose rugs, good lighting, handrails)
• Supervise children carefully
• Use appropriate safety equipment for sports

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Head injuries can be life-threatening. If you or someone has a head injury with any warning signs, seek emergency medical care immediately.

When in doubt, get checked out.

QUESTIONS TO ASK YOUR DOCTOR:
• How severe is this head injury?
• Do I/they need a CT scan?
• What symptoms should I watch for?
• When can I return to work/school/sports?
• Will there be any long-term effects?
• Do I need follow-up appointments?
• Should I see a specialist?
    `,

    references: [
      'Carney N, Totten AM, OReilly C, et al. Guidelines for the Management of Severe Traumatic Brain Injury, Fourth Edition. Neurosurgery. 2017;80(1):6-15.',
      'Maas AIR, Menon DK, Adelson PD, et al. Traumatic brain injury: integrated approaches to improve prevention, clinical care, and research. Lancet Neurology. 2017;16(12):987-1048.',
      'Dewan MC, Rattani A, Gupta S, et al. Estimating the global incidence of traumatic brain injury. Journal of Neurosurgery. 2019;130(4):1080-1097.',
      'CRASH-3 Trial Collaborators. Effects of tranexamic acid on death, disability, vascular occlusive events and other morbidities in patients with acute traumatic brain injury (CRASH-3). Lancet. 2019;394(10210):1713-1723.',
      'Steyerberg EW, Mushkudiani N, Perel P, et al. Predicting outcome after traumatic brain injury: development and international validation of prognostic scores based on admission characteristics. PLoS Medicine. 2008;5(8):e165.',
      'Brain Trauma Foundation. Guidelines for the Management of Severe TBI. 4th Edition. 2016.',
      'Hutchinson PJ, Kolias AG, Timofeev IS, et al. Trial of Decompressive Craniectomy for Traumatic Intracranial Hypertension. New England Journal of Medicine. 2016;375(12):1119-1130.',
      'McCrory P, Meeuwisse W, Dvorak J, et al. Consensus statement on concussion in sport. British Journal of Sports Medicine. 2017;51(11):838-847.',
      'World Health Organization. Global Status Report on Road Safety 2018. Geneva: WHO; 2018.',
      'Odero W, Garner P, Zwi A. Road traffic injuries in developing countries: a comprehensive review of epidemiological studies. Tropical Medicine and International Health. 1997;2(5):445-460.',
      'Teasdale G, Maas A, Lecky F, et al. The Glasgow Coma Scale at 40 years: standing the test of time. Lancet Neurology. 2014;13(8):844-854.',
      'Rosenfeld JV, Maas AI, Bragge P, et al. Early management of severe traumatic brain injury. Lancet. 2012;380(9847):1088-1098.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 57. FRACTURES
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '57',
    name: 'Fractures',
    slug: 'fractures',
    specialty: 'Emergency Medicine',
    bodySystem: 'Musculoskeletal',
    icdCode: 'S82.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
A fracture is a break in the continuity of bone, ranging from a small crack to complete disruption with multiple fragments. Fractures are extremely common injuries, occurring across all age groups and representing a significant portion of emergency department visits and hospital admissions worldwide. They result from trauma (the most common cause), pathological weakening of bone (tumors, osteoporosis, infection), or repetitive stress (stress fractures).

Fracture management principles include: accurate diagnosis with appropriate imaging, adequate pain control, reduction (restoring alignment) if displaced, immobilization to allow healing, and rehabilitation to restore function. The choice between conservative (casts, splints) and operative management depends on the fracture type, location, degree of displacement, patient factors, and available resources.

In Africa, fractures are predominantly caused by road traffic accidents (RTAs), which are responsible for 50-70% of orthopedic trauma. Falls, occupational injuries, and interpersonal violence are other major causes. The burden is compounded by limited access to orthopedic surgical services, imaging facilities, and rehabilitation. Many patients present late or with complications of traditional bone-setter treatment, which remains common in many communities.

Early recognition and proper initial management of fractures, including identification of life-threatening associated injuries and limb-threatening complications like compartment syndrome and open fractures, is critical for optimal outcomes.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 OPEN FRACTURES ARE SURGICAL EMERGENCIES - Bone exposed through skin; high infection risk; IV antibiotics, tetanus, and urgent surgery

🔴 COMPARTMENT SYNDROME IS LIMB-THREATENING - Pain out of proportion, pain with passive stretch, pressure; do NOT wait for pulselessness; urgent fasciotomy

🔴 NEUROVASCULAR STATUS MUST BE DOCUMENTED - Check and record pulses, sensation, motor function before AND after any manipulation

🔴 ADEQUATE ANALGESIA IS ESSENTIAL - Fractures are extremely painful; use appropriate analgesia; regional blocks are excellent

🔴 SPLINT BEFORE MOVING - Immobilize suspected fractures before transport to reduce pain, bleeding, and further injury

🔴 TWO VIEWS MINIMUM ON X-RAY - AP and lateral views; include joints above and below the injury

🔴 REDUCTION RESTORES ALIGNMENT - Closed reduction for most fractures; surgical (open) reduction for complex or failed closed reduction

🔴 CHILDREN'S FRACTURES REMODEL - Accept more angulation in children; growth plate injuries need special attention (Salter-Harris classification)
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Fractures are one of the most common injuries worldwide
• Estimated 178 million new fractures annually globally
• Incidence varies by age, gender, and region
• Bimodal distribution: Young adults (trauma) and elderly (osteoporotic fractures)
• Most common fractures: Distal radius, hip, ankle, proximal humerus, clavicle
• Male predominance in younger ages; female predominance in elderly (osteoporosis)

AFRICAN EPIDEMIOLOGY:
• Road Traffic Accidents: Leading cause (50-70% of fractures in many settings)
• Falls: Second most common cause; significant in elderly
• Assault and interpersonal violence: Important contributor
• Occupational injuries: Construction, mining, agriculture
• Sports injuries: Football (soccer) particularly common
• Traditional bone-setter complications: Many patients present after failed traditional treatment
• Delayed presentation: Common due to access, cost, traditional practices

FRACTURE PATTERNS IN AFRICA:
• Lower limb fractures predominate (femur, tibia common from RTAs)
• Motorcycle-related injuries: Major and increasing burden
• Open fractures: Higher proportion due to high-energy RTAs
• Polytrauma: Frequently multiple injuries
• Pediatric fractures: Significant burden; supracondylar humerus common

HEALTHCARE CHALLENGES:
• Limited orthopedic surgeons: Many African countries have <1 per 100,000 population
• Limited imaging: X-ray not always available; CT rarely available
• Limited operative capacity: Equipment, implants, theaters
• High cost of care: Out-of-pocket payments; patients may not afford surgery
• Rehabilitation services: Very limited; affects functional outcomes

AGE-SPECIFIC PATTERNS:

Children:
• Buckle/torus fractures (cortex compression)
• Greenstick fractures (incomplete)
• Physeal (growth plate) injuries
• Supracondylar humerus fractures (high risk of neurovascular injury)
• Forearm fractures

Adults:
• Trauma-related fractures
• Work-related injuries
• Sports injuries

Elderly:
• Osteoporotic fractures
• Hip fractures (high mortality)
• Vertebral compression fractures
• Distal radius fractures
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

CARDINAL FEATURES OF FRACTURE:
• Pain: At fracture site; worse with movement or palpation
• Swelling: From bleeding and soft tissue injury
• Deformity: Visible angulation, shortening, rotation
• Loss of function: Unable to use limb
• Crepitus: Grating sensation (do not elicit deliberately)
• Abnormal mobility: Movement where there should be none

FRACTURE CLASSIFICATION:

By Skin Integrity:
• CLOSED (Simple): Skin intact; no communication with outside
• OPEN (Compound): Bone communicates with external environment through wound
  - Gustilo-Anderson Classification:
    * Type I: Clean wound <1cm; minimal soft tissue damage
    * Type II: Wound 1-10cm; moderate soft tissue damage
    * Type IIIA: Wound >10cm; adequate soft tissue coverage
    * Type IIIB: Extensive soft tissue loss; requires flap coverage
    * Type IIIC: Arterial injury requiring repair

By Fracture Pattern:
• Transverse: Perpendicular to long axis
• Oblique: Diagonal to long axis
• Spiral: Twisting injury; rotational force
• Comminuted: Multiple fragments (>2)
• Segmental: Two separate fracture lines isolating a segment
• Impacted: Fragments driven into each other
• Avulsion: Fragment pulled off by tendon/ligament

By Displacement:
• Undisplaced: Fragments in anatomical alignment
• Displaced: Fragments out of alignment
  - Angulated: Fragments at an angle
  - Translated: Shifted sideways
  - Rotated: Twisted on axis
  - Shortened/Overriding: Overlapping with shortening
  - Distracted: Pulled apart

Special Fracture Types:
• Pathological: Through abnormal bone (tumor, infection, osteoporosis)
• Stress: From repetitive loading; no acute trauma
• Greenstick: Incomplete; cortex broken on one side (children)
• Buckle/Torus: Cortical compression (children)
• Physeal: Involving growth plate (Salter-Harris classification)

SALTER-HARRIS CLASSIFICATION (Pediatric Growth Plate Injuries):
• Type I: Through physis only (S = Straight across)
• Type II: Through physis and metaphysis (A = Above)
• Type III: Through physis and epiphysis (L = Lower)
• Type IV: Through metaphysis, physis, and epiphysis (T = Through all)
• Type V: Crush injury to physis (ER = Erasure/crush)
(Higher types = higher risk of growth disturbance)

SPECIFIC FRACTURE PRESENTATIONS:

Clavicle Fracture:
• Shoulder pain, swelling
• Visible/palpable deformity
• Arm held across body

Proximal Humerus:
• Shoulder pain, swelling
• Inability to move shoulder
• Ecchymosis to chest/arm

Supracondylar Humerus (Children):
• Elbow pain, swelling
• S-shaped deformity
• HIGH RISK of brachial artery and nerve injury

Distal Radius (Colles/Smith):
• Wrist pain after FOOSH (fall on outstretched hand)
• Dinner fork deformity (Colles - dorsal displacement)
• Garden spade deformity (Smith - volar displacement)

Scaphoid:
• Wrist pain after FOOSH
• Anatomical snuffbox tenderness
• X-ray may be initially negative; high index of suspicion

Hip Fracture:
• Inability to weight bear
• Shortened, externally rotated leg
• Groin/hip pain

Femoral Shaft:
• Severe thigh pain and swelling
• Visible deformity
• High blood loss (1-2+ liters)

Tibial Shaft:
• Leg pain, swelling, deformity
• Subcutaneous - high open fracture rate
• Compartment syndrome risk

Ankle Fractures:
• Ankle pain, swelling, deformity
• Inability to weight bear
• Classify by Weber (fibula level) or Lauge-Hansen

NEUROVASCULAR EXAMINATION (CRITICAL):
• Pulses: Distal to fracture; compare to other side
• Capillary refill: <2 seconds normal
• Sensation: Light touch in nerve distributions
• Motor: Active movement of distal muscles
• Document before and after any manipulation!
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: HISTORY
• Mechanism of injury (determines likely fracture pattern and associated injuries)
• Time of injury
• Symptoms: Pain, swelling, deformity, inability to use limb
• Previous injury or surgery to area
• Medical history: Osteoporosis, bone disease, anticoagulation
• Tetanus status (if open fracture)
• Last oral intake (if surgery anticipated)

STEP 2: PHYSICAL EXAMINATION

Systematic Assessment:
• Look: Swelling, deformity, wounds, bruising, skin condition
• Feel: Tenderness, crepitus (do not elicit deliberately), temperature
• Move: Active then passive range of motion (stop if painful)
• Neurovascular: Pulses, capillary refill, sensation, motor function
• Joints above and below: May have associated injuries

Red Flags to Identify:
• Open fracture (wound communicating with fracture)
• Neurovascular compromise (absent pulses, numbness, weakness)
• Compartment syndrome signs (pain out of proportion, pain with passive stretch)
• Signs of significant blood loss (tachycardia, hypotension)
• Associated injuries (polytrauma)

STEP 3: IMAGING

X-ray (Gold Standard for Fractures):
• Minimum TWO views at 90° (usually AP and Lateral)
• Include joint above and below the injury
• Comparison views of opposite side may help (especially children)

X-ray Interpretation:
• Trace cortex systematically
• Look at medulla/trabecular pattern
• Check joint alignment
• Soft tissue signs (fat pads, effusions)
• Look for subtle fractures (scaphoid, base of 5th metatarsal)

CLINICAL DECISION RULES:

Ottawa Ankle Rules (When to X-ray Ankle):
X-ray if:
• Bone tenderness at posterior edge or tip of lateral malleolus, OR
• Bone tenderness at posterior edge or tip of medial malleolus, OR
• Unable to weight bear (4 steps) immediately and in ED

Ottawa Foot Rules (When to X-ray Foot):
X-ray if:
• Bone tenderness at base of 5th metatarsal, OR
• Bone tenderness at navicular, OR
• Unable to weight bear (4 steps) immediately and in ED

Ottawa Knee Rules (When to X-ray Knee):
X-ray if:
• Age ≥55, OR
• Isolated patellar tenderness, OR
• Fibular head tenderness, OR
• Unable to flex knee to 90°, OR
• Unable to weight bear (4 steps) immediately and in ED

STEP 4: CLASSIFY THE FRACTURE

Describe systematically:
1. Open or Closed
2. Location (which bone; proximal/middle/distal; intra-articular?)
3. Pattern (transverse, oblique, spiral, comminuted)
4. Displacement (angulation, translation, rotation, shortening)
5. Associated injuries

STEP 5: ASSESS FOR COMPLICATIONS

Immediate:
• Neurovascular injury
• Open wound
• Compartment syndrome developing
• Associated injuries

Early:
• Infection (especially open fractures)
• Compartment syndrome
• Fat embolism (long bone fractures)

SPECIAL IMAGING:

CT Scan:
• Complex articular fractures (tibial plateau, acetabulum)
• Spine fractures
• When surgery planned for detailed assessment
• Often not available in resource-limited settings

MRI:
• Occult fractures (hip, scaphoid)
• Ligamentous injuries
• Stress fractures
• Usually not needed acutely
    `,

    differentialDiagnosis: [
      'Soft tissue contusion - Pain and swelling without fracture; X-ray negative; usually less severe pain',
      'Ligament sprain - Joint injury; stress testing positive; X-ray may show soft tissue swelling only',
      'Tendon rupture - Loss of function of specific tendon; may be palpable gap; X-ray negative',
      'Dislocation - Joint out of alignment; X-ray shows joint disruption; may coexist with fracture (fracture-dislocation)',
      'Muscle strain - Pain with muscle use and stretch; no bony tenderness; X-ray negative',
      'Joint effusion - Swelling within joint; may be traumatic or inflammatory; X-ray shows soft tissue swelling',
      'Septic arthritis - Hot, swollen, painful joint; fever; limited ROM; aspiration diagnostic',
      'Osteomyelitis - Bone pain, fever, elevated inflammatory markers; may be pathological fracture',
      'Bone tumor - Pain may precede fracture; pathological fracture through lesion',
      'Gout/Pseudogout - Acute joint inflammation; crystal analysis of aspirate diagnostic',
      'Stress reaction (without fracture) - Activity-related pain; normal X-ray; MRI shows bone edema',
      'Growing pains (children) - Night pain, bilateral, no objective findings; diagnosis of exclusion',
      'Non-accidental injury (children) - Multiple fractures of different ages, inconsistent history',
      'Referred pain - Pain from spine, hip, or viscera may present as limb pain',
      'Compartment syndrome (without fracture) - Can occur from crush injury or bleeding without fracture'
    ],

    investigations: [
      {
        name: 'Plain X-ray (2 views minimum)',
        indication: '🔴 ESSENTIAL - Gold standard for fracture diagnosis; AP and lateral; include joints above and below',
        availability: 'BASIC'
      },
      {
        name: 'Neurovascular Examination',
        indication: '🔴 ESSENTIAL - Document pulses, sensation, motor function; before and after manipulation; no imaging substitute',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Baseline hemoglobin especially for femur/pelvis fractures; significant blood loss expected',
        availability: 'BASIC'
      },
      {
        name: 'Blood Type and Crossmatch',
        indication: '🔴 ESSENTIAL for major fractures - Femur, pelvis, open fractures; prepare for transfusion if needed',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes and Renal Function',
        indication: '🟠 IMPORTANT - Baseline before surgery; affects anesthesia; NSAIDs contraindicated if renal impairment',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation Studies (PT/INR)',
        indication: '🟠 IMPORTANT - If on anticoagulation or if surgery planned',
        availability: 'BASIC'
      },
      {
        name: 'Wound Swab/Culture',
        indication: '🟠 IMPORTANT for open fractures - Identify organisms; guide antibiotic therapy if infection develops',
        availability: 'BASIC'
      },
      {
        name: 'CT Scan',
        indication: '🟡 WHEN AVAILABLE - Complex articular fractures, spine, pelvis; surgical planning; often not available in resource-limited settings',
        availability: 'ADVANCED'
      },
      {
        name: 'MRI',
        indication: '🟡 FOR SPECIFIC INDICATIONS - Occult fractures (hip, scaphoid), soft tissue injury, stress fractures',
        availability: 'ADVANCED'
      },
      {
        name: 'Doppler Ultrasound',
        indication: '🟡 IF VASCULAR INJURY SUSPECTED - Assess blood flow if pulses diminished after fracture',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Compartment Pressure Measurement',
        indication: '🔴 IF COMPARTMENT SYNDROME SUSPECTED - Pressure >30 mmHg or within 30 of diastolic is abnormal; clinical diagnosis usually sufficient',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Angiography',
        indication: '🟡 IF MAJOR VASCULAR INJURY - Usually CT angiography; for limb-threatening vascular injury',
        availability: 'ADVANCED'
      },
      {
        name: 'Bone Scan / Nuclear Medicine',
        indication: '🟡 RARELY NEEDED ACUTELY - Stress fractures, pathological fractures, occult fractures if MRI unavailable',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ PRIMARY SURVEY (ATLS): Address life-threatening injuries first; fractures rarely kill but associated injuries can',
        '⚡ ANALGESIA: Provide adequate pain relief early; IV opioids for severe fractures; consider nerve blocks',
        '⚡ SPLINT THE FRACTURE: Immobilize in position found; reduces pain, bleeding, and further injury; include joint above and below',
        '⚡ NEUROVASCULAR CHECK: Document pulses, sensation, motor function; compare to other side; repeat after manipulation',
        '⚡ ASSESS FOR OPEN FRACTURE: Any wound near fracture = open until proven otherwise; cover with sterile saline-soaked dressing',
        '⚡ ASSESS FOR COMPARTMENT SYNDROME: Pain out of proportion, pain with passive stretch, tense compartment; urgent fasciotomy if present',
        '⚡ X-RAY: Two views minimum; include joints above and below; identify fracture pattern',
        '⚡ REDUCTION IF NEEDED: Restore alignment; closed reduction for most; adequate analgesia/sedation essential',
        '⚡ IMMOBILIZE: Plaster cast, splint, or traction depending on fracture; maintain reduction',
        '⚡ OPEN FRACTURES: IV antibiotics within 1 hour, tetanus prophylaxis, urgent surgical debridement'
      ],
      
      pharmacological: [
        {
          drug: 'Morphine',
          indication: '🔴 ESSENTIAL - First-line opioid for moderate-severe fracture pain; titrate to effect',
          dose: 'Adults: 0.1 mg/kg IV, titrate every 5-10 minutes as needed. Children: 0.05-0.1 mg/kg IV. IM: 0.1-0.15 mg/kg if no IV access.',
          route: 'IV (preferred) or IM',
          duration: 'Acute phase; transition to oral when able',
          contraindications: ['Respiratory depression without monitoring', 'Hypotension (relative)', 'Head injury (relative)'],
          sideEffects: ['Respiratory depression', 'Nausea/vomiting', 'Sedation', 'Constipation', 'Hypotension'],
          alternatives: ['Fentanyl', 'Tramadol', 'Ketamine']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🔴 ESSENTIAL - Baseline analgesia; opioid-sparing; use in combination with opioids',
          dose: 'Adults: 1g IV/PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours (max 60 mg/kg/day).',
          route: 'IV or PO',
          duration: 'As needed',
          contraindications: ['Severe liver disease'],
          sideEffects: ['Hepatotoxicity with overdose'],
          alternatives: ['NSAIDs (if no contraindications)']
        },
        {
          drug: 'Ibuprofen / Diclofenac (NSAIDs)',
          indication: '🟠 IMPORTANT - Anti-inflammatory and analgesic; useful for musculoskeletal pain; some concern about bone healing',
          dose: 'Ibuprofen: 400-600 mg PO every 6-8 hours. Diclofenac: 50 mg PO/IM every 8 hours. Maximum duration for fractures: 2 weeks (controversial effect on healing).',
          route: 'PO or IM',
          duration: 'Short-term (concerns about bone healing with prolonged use)',
          contraindications: ['Renal impairment', 'GI bleeding/ulcer history', 'Aspirin allergy', 'Third trimester pregnancy', 'Heart failure'],
          sideEffects: ['GI upset', 'Renal impairment', 'Bleeding risk', 'Possibly delayed bone healing'],
          alternatives: ['Paracetamol + opioid']
        },
        {
          drug: 'Tramadol',
          indication: '🟠 ALTERNATIVE - Moderate pain; less respiratory depression than morphine; dual mechanism',
          dose: 'Adults: 50-100 mg PO/IV every 4-6 hours (max 400 mg/day). Reduce dose in elderly and renal impairment.',
          route: 'PO or IV',
          duration: 'As needed',
          contraindications: ['Seizure disorder', 'MAO inhibitor use', 'Serotonin syndrome risk'],
          sideEffects: ['Nausea', 'Dizziness', 'Seizures (rare)', 'Serotonin syndrome'],
          alternatives: ['Codeine', 'Morphine']
        },
        {
          drug: 'Ketamine',
          indication: '🟠 USEFUL - Procedural sedation for reduction; analgesic properties; maintains hemodynamics',
          dose: 'Procedural sedation: 1-2 mg/kg IV or 4-5 mg/kg IM. Analgesia (low dose): 0.1-0.3 mg/kg IV. Use with midazolam to reduce emergence reactions.',
          route: 'IV or IM',
          duration: 'Single dose for procedure',
          contraindications: ['Severe hypertension', 'Raised ICP (relative)', 'Psychosis'],
          sideEffects: ['Emergence reactions', 'Hypersalivation', 'Hypertension', 'Nystagmus'],
          alternatives: ['Propofol + fentanyl (if monitoring available)']
        },
        {
          drug: 'Midazolam',
          indication: '🟠 ADJUNCT - Anxiolysis and sedation for procedures; combine with ketamine or opioids',
          dose: 'Adults: 1-2 mg IV, titrate. Children: 0.05-0.1 mg/kg IV. Intranasal: 0.2-0.3 mg/kg.',
          route: 'IV or Intranasal',
          duration: 'Single dose for procedure',
          contraindications: ['Severe respiratory depression without airway control'],
          sideEffects: ['Respiratory depression', 'Hypotension', 'Paradoxical agitation'],
          alternatives: ['Diazepam']
        },
        {
          drug: 'Cefazolin',
          indication: '🔴 ESSENTIAL for open fractures - First-line antibiotic; Gram-positive coverage; give within 1 hour of injury',
          dose: 'Adults: 2g IV every 8 hours. Children: 25-50 mg/kg IV every 8 hours (max 2g/dose).',
          route: 'IV',
          duration: 'Gustilo I-II: 24-48 hours post-closure. Gustilo III: Until wound closed or converted to Gustilo II.',
          contraindications: ['Severe penicillin/cephalosporin allergy'],
          sideEffects: ['Allergic reactions', 'GI upset', 'C. diff colitis'],
          alternatives: ['Clindamycin 900 mg IV every 8 hours (if penicillin allergic)']
        },
        {
          drug: 'Gentamicin',
          indication: '🟠 ADD for Gustilo III open fractures - Gram-negative coverage for severe contamination',
          dose: 'Adults: 5 mg/kg IV once daily. Adjust for renal function. Monitor levels if prolonged use.',
          route: 'IV',
          duration: 'Until wound management complete',
          contraindications: ['Renal failure (relative)', 'Myasthenia gravis'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity', 'Neuromuscular blockade'],
          alternatives: ['Ciprofloxacin']
        },
        {
          drug: 'Metronidazole',
          indication: '🟠 ADD for farm/fecal contamination - Anaerobic coverage for contaminated wounds',
          dose: 'Adults: 500 mg IV every 8 hours. Children: 7.5 mg/kg IV every 8 hours.',
          route: 'IV',
          duration: 'With other antibiotics until wound managed',
          contraindications: ['First trimester pregnancy (relative)', 'Disulfiram-like reaction with alcohol'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram reaction'],
          alternatives: ['Clindamycin has anaerobic coverage']
        },
        {
          drug: 'Tetanus Toxoid',
          indication: '🔴 ESSENTIAL for open fractures and wounds - Update if needed per guidelines',
          dose: 'Td 0.5 mL IM. Add TIG (Tetanus Immune Globulin) 250-500 units IM if high-risk wound and incomplete/unknown vaccination.',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Severe reaction to previous dose'],
          sideEffects: ['Injection site reactions'],
          alternatives: ['TT alone if Td unavailable']
        },
        {
          drug: 'Enoxaparin (LMWH)',
          indication: '🟠 IMPORTANT - VTE prophylaxis for lower limb fractures, immobilized patients, post-operative',
          dose: 'Prophylaxis: 40 mg SC once daily. Start 12-24 hours post-surgery if hemostasis achieved.',
          route: 'Subcutaneous',
          duration: 'Until mobile or per protocol (often 2-6 weeks for lower limb)',
          contraindications: ['Active bleeding', 'Severe thrombocytopenia', 'Recent neurosurgery'],
          sideEffects: ['Bleeding', 'Thrombocytopenia (HIT)', 'Injection site reactions'],
          alternatives: ['Unfractionated heparin', 'Rivaroxaban (post-op)']
        },
        {
          drug: 'Calcium and Vitamin D',
          indication: '🟡 SUPPORTIVE - Especially for osteoporotic fractures; supports bone healing',
          dose: 'Calcium carbonate 500-600 mg PO twice daily. Vitamin D3 800-2000 IU daily.',
          route: 'PO',
          duration: 'Long-term for osteoporosis; during healing for others',
          contraindications: ['Hypercalcemia', 'Kidney stones'],
          sideEffects: ['Constipation', 'GI upset'],
          alternatives: ['Dietary sources']
        }
      ],
      
      nonPharmacological: [
        'SPLINTING: Immobilize fracture including joint above and below; use padded splints; check neurovascular status after application',
        'CLOSED REDUCTION: Restore alignment under analgesia/sedation; traction followed by manipulation; check X-ray and neurovascular status after',
        'PLASTER OF PARIS (POP) CAST: For stable fractures after reduction; padded; complete circumference only when swelling resolved; half-cast/backslab initially',
        'TRACTION: Skin traction for temporary immobilization; skeletal traction for femur fractures awaiting surgery; helps with pain and alignment',
        'ELEVATION: Reduce swelling; limb above heart level',
        'ICE: Apply ice packs (wrapped) for first 48 hours; 20 minutes on, 20 minutes off; reduces swelling and pain',
        'SLING/SUPPORT: For upper limb fractures; broad arm sling, collar and cuff, or shoulder immobilizer depending on fracture',
        'WEIGHT-BEARING STATUS: Define clearly; non-weight bearing, touch weight bearing, partial weight bearing, full weight bearing',
        'PHYSICAL THERAPY: Early mobilization of unaffected joints; prevent stiffness; strengthening after fracture union',
        'CAST CARE EDUCATION: Keep dry; do not insert objects; watch for signs of problems (increased pain, numbness, color change)',
        'NUTRITION: Adequate protein and calories for bone healing; especially important in elderly and malnourished',
        'SMOKING CESSATION: Smoking delays bone healing; counsel strongly'
      ],
      
      surgical: [
        'OPEN REDUCTION AND INTERNAL FIXATION (ORIF): Plates, screws, intramedullary nails; for unstable fractures, articular fractures, failed closed reduction',
        'EXTERNAL FIXATION: Pins through bone connected to external frame; for open fractures, unstable pelvic fractures, damage control',
        'INTRAMEDULLARY NAILING: Rod inside bone canal; for long bone shaft fractures (femur, tibia); allows early weight bearing',
        'PERCUTANEOUS PINNING: K-wires through skin; for distal radius, supracondylar humerus, some hand fractures',
        'DEBRIDEMENT: Thorough cleaning and removal of dead tissue; essential for open fractures; may need serial debridement',
        'FASCIOTOMY: Urgent decompression for compartment syndrome; full-length incisions to release all compartments',
        'AMPUTATION: For non-salvageable limbs; severe crush injury, unreconstructable vascular injury',
        'BONE GRAFTING: For non-union; autograft or allograft',
        'ARTHROPLASTY: For some articular fractures (neck of femur in elderly - hemiarthroplasty or total hip replacement)',
        'SOFT TISSUE COVERAGE: Flaps, skin grafts for open fractures with soft tissue loss'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Open fractures - Surgical emergency; antibiotics and urgent debridement',
        '🔴 IMMEDIATE REFERRAL: Compartment syndrome - Fasciotomy within 6 hours to prevent permanent damage',
        '🔴 IMMEDIATE REFERRAL: Neurovascular compromise - Urgent reduction; may need vascular surgery',
        '🔴 IMMEDIATE REFERRAL: Fracture-dislocation - Urgent reduction to restore blood flow',
        '🔴 IMMEDIATE REFERRAL: Femoral neck fracture - Surgery within 24-48 hours for best outcomes',
        '🔴 IMMEDIATE REFERRAL: Pelvic fracture with hemodynamic instability - Life-threatening bleeding',
        '🟠 URGENT REFERRAL: Intra-articular fractures - Need anatomical reduction to prevent arthritis',
        '🟠 URGENT REFERRAL: Displaced physeal (growth plate) fractures - Risk of growth disturbance',
        '🟠 URGENT REFERRAL: Unstable fractures requiring fixation - Within days for optimal results',
        '🟠 URGENT REFERRAL: Multiple fractures/polytrauma - Need comprehensive management',
        '🟡 ROUTINE REFERRAL: Fractures not healing (non-union, delayed union) - May need surgery',
        '🟡 ROUTINE REFERRAL: Malunion causing functional problems - May need corrective osteotomy'
      ],
      
      monitoring: [
        'NEUROVASCULAR STATUS: Check frequently (hourly initially); after any manipulation or cast application; document each time',
        'CAST CHECK: Ensure not too tight; patient able to move digits; no excessive pain, numbness, or color change',
        'SWELLING: Monitor; may need to split cast if increasing; elevation helps',
        'PAIN CONTROL: Assess regularly; severe increasing pain may indicate compartment syndrome or other complication',
        'FOLLOW-UP X-RAYS: Weekly initially for unstable fractures; at 1-2 weeks for most fractures; at anticipated union time',
        'RANGE OF MOTION: Monitor adjacent joints for stiffness; encourage movement of unaffected joints',
        'WEIGHT-BEARING: Progress as healing allows; guided by X-ray union and symptoms',
        'WOUND CARE: For open fractures and surgical wounds; watch for signs of infection',
        'VTE PROPHYLAXIS: Ensure appropriate prophylaxis; watch for signs of DVT/PE',
        'HEALING PROGRESS: Clinical (no tenderness, no pain with stress) and radiological (bridging callus, trabeculation across fracture)'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

CAST/SPLINT MANAGEMENT:

Initial Visit (1-2 weeks):
• Check neurovascular status
• Assess swelling (may need cast change)
• Check cast integrity
• X-ray to confirm maintained reduction
• Pain and symptom assessment

Subsequent Visits:
• Every 2-4 weeks depending on fracture
• Clinical and X-ray assessment of healing
• Cast changes as needed (swelling decreases, cast loosens)
• Progress weight-bearing as appropriate

HEALING TIMEFRAMES (Approximate):

Upper Limb:
• Clavicle: 6-8 weeks
• Proximal humerus: 6-8 weeks
• Humeral shaft: 8-12 weeks
• Distal radius: 6 weeks
• Scaphoid: 8-12 weeks (high non-union rate)
• Metacarpals/phalanges: 3-6 weeks

Lower Limb:
• Femoral neck: 12-16 weeks (often fixed surgically)
• Femoral shaft: 12-16 weeks
• Tibial shaft: 12-20 weeks
• Ankle: 6-8 weeks
• Metatarsals: 6-8 weeks

Factors Affecting Healing:
• Age (children heal faster)
• Blood supply (better supply = faster healing)
• Degree of displacement and soft tissue injury
• Infection
• Nutrition
• Smoking (delays healing significantly)
• Diabetes
• Medications (steroids, NSAIDs may delay)

CAST REMOVAL:
• Clinical union: No tenderness at fracture site, no pain with stress
• Radiological union: Bridging callus visible on 3 of 4 cortices
• Gradual return to activity after cast removal
• Physical therapy for stiffness and weakness

COMPLICATIONS TO WATCH FOR:

Early:
• Compartment syndrome (first 24-48 hours)
• Infection (especially open fractures)
• Fat embolism syndrome (long bone fractures, 24-72 hours)
• DVT/PE

Late:
• Malunion (healed in poor position)
• Delayed union (taking longer than expected)
• Non-union (failure to heal)
• Stiffness
• Complex regional pain syndrome
• Post-traumatic arthritis
• Avascular necrosis (femoral neck, scaphoid, talus)

REHABILITATION:

Goals:
• Restore range of motion
• Restore strength
• Return to function (work, sports, daily activities)

Principles:
• Early mobilization of unaffected joints
• Progressive loading
• Patient education and active participation
• May need formal physiotherapy
      `
    },

    redFlags: [
      '🔴 CRITICAL: Open fracture - Bone exposed; wound communicating with fracture; surgical emergency; antibiotics within 1 hour',
      '🔴 CRITICAL: Compartment syndrome - Pain out of proportion, pain with passive stretch, tense compartment; fasciotomy within 6 hours',
      '🔴 CRITICAL: Absent distal pulses - Vascular injury or kinking; urgent reduction; may need vascular surgery',
      '🔴 CRITICAL: Complete nerve deficit after fracture - May indicate nerve laceration or severe compression',
      '🔴 CRITICAL: Pelvic fracture with hypotension - Massive hemorrhage; resuscitation and pelvic binder urgently',
      '🔴 CRITICAL: Femoral shaft fracture - Significant blood loss (1-2L); monitor hemodynamics; high energy injury',
      '🟠 URGENT: Progressive neurological deficit - Worsening nerve function needs urgent attention',
      '🟠 URGENT: Fracture-dislocation - Joint dislocated with fracture; needs urgent reduction',
      '🟠 URGENT: Increasing pain in cast - May be compartment syndrome, infection, or pressure sore',
      '🟠 URGENT: Fat embolism syndrome - Petechial rash, respiratory distress, confusion 24-72h after long bone fracture',
      '🟡 CAUTION: Numbness or tingling in cast - May be too tight; needs assessment and possible loosening',
      '🟡 CAUTION: Cold or discolored digits - Vascular compromise; urgent cast check',
      '🟡 CAUTION: Non-accidental injury pattern - Multiple fractures, different ages, inconsistent history (especially children)'
    ],

    complications: [
      'Compartment syndrome - Pressure in closed muscle compartment causes ischemia; permanent damage if not treated within 6 hours',
      'Fat embolism syndrome - Fat from marrow enters bloodstream; causes petechiae, respiratory failure, confusion; 24-72h post-fracture',
      'Infection - Especially open fractures; osteomyelitis is serious complication; may lead to chronic infection or amputation',
      'Non-union - Failure to heal; motion at fracture site >6 months; may need surgery, bone grafting',
      'Delayed union - Taking longer than expected to heal; may progress to union with time or may not heal',
      'Malunion - Healed in poor position; may cause functional impairment; may need corrective surgery',
      'Avascular necrosis - Blood supply disruption causes bone death; femoral neck, scaphoid, talus at risk',
      'Post-traumatic arthritis - Especially intra-articular fractures; accelerated joint degeneration',
      'Stiffness - From immobilization; worse in elderly; physiotherapy important',
      'Complex regional pain syndrome (CRPS) - Chronic pain, swelling, changes in skin color/temperature; difficult to treat',
      'DVT and pulmonary embolism - From immobility and hypercoagulable state after injury; can be fatal',
      'Nerve injury - From fracture fragments or during reduction; may recover or be permanent',
      'Vascular injury - Especially supracondylar humerus, knee fractures; limb-threatening',
      'Growth disturbance - From physeal injuries in children; may cause limb length discrepancy or angular deformity',
      'Muscle wasting - From disuse during immobilization; rehabilitation helps recover'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PRINCIPLES:
• Most fractures heal well with appropriate treatment
• Prognosis depends on fracture type, location, treatment quality, and patient factors
• Children heal faster and remodel better than adults
• Open fractures have higher complication rates
• Intra-articular fractures risk post-traumatic arthritis

HEALING RATES:
• Overall union rate: >90% for most fractures
• Non-union rate varies by location: Scaphoid and tibia higher risk
• Healing time depends on age, blood supply, stability, and patient factors

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Young age
• Closed fractures
• Stable fracture patterns
• Good reduction and immobilization
• Good blood supply
• Early appropriate treatment
• Non-smoker
• Good nutrition

Worse Outcomes:
• Open fractures
• Comminuted fractures
• Intra-articular involvement
• Poor reduction
• Delayed treatment
• Infection
• Smoking (significantly increases non-union)
• Diabetes
• Peripheral vascular disease
• Malnutrition
• Traditional bone-setter complications

SPECIFIC FRACTURE OUTCOMES:

Hip Fractures (Elderly):
• 1-year mortality: 20-30%
• Many lose independence
• Early surgery improves outcomes
• Rehabilitation critical

Femoral Shaft:
• Generally good outcomes with surgical fixation
• Most return to previous function
• Malrotation can be problematic

Tibial Shaft:
• Higher complication rate (open fractures, compartment syndrome, non-union)
• Union rate lower than other long bones
• May have prolonged healing

Distal Radius:
• Generally good functional outcomes
• Some stiffness common
• Elderly may have acceptable function despite radiological malunion

Scaphoid:
• Non-union rate 5-10%
• AVN risk
• Early diagnosis improves outcomes

FUNCTIONAL OUTCOMES:
• Most patients return to previous level of function
• May have some residual stiffness or weakness
• Elderly may not regain full independence
• Return to sports: Variable; depends on fracture and sport
• Return to work: Depends on type of work and fracture

LONG-TERM CONSIDERATIONS:
• Post-traumatic arthritis may develop years later
• Some fractures increase risk of subsequent osteoporosis
• Hardware may need removal
• Chronic pain in minority of patients
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

ROAD TRAFFIC INJURY PREVENTION:

Motorcycle Safety:
• Helmet use (reduces death by 42%, head injury by 69%)
• Mandatory helmet laws with enforcement
• Protective clothing
• Training and licensing

Vehicle Safety:
• Seatbelt use (reduces fatal injury by 45-50%)
• Child car seats
• Airbags and vehicle safety standards
• Speed limits and enforcement

Road Design:
• Pedestrian walkways and crossings
• Traffic calming in residential areas
• Street lighting
• Guardrails and barriers

Behavior:
• No drink-driving (legal limits and enforcement)
• No phone use while driving
• Rest breaks for long journeys
• Obeying traffic rules

FALL PREVENTION:

Elderly:
• Home safety assessment
• Remove loose rugs and clutter
• Adequate lighting, especially at night
• Handrails on stairs and in bathroom
• Non-slip mats
• Regular vision and hearing checks
• Medication review (reduce sedatives)
• Exercise for strength and balance
• Hip protectors for high-risk individuals

Children:
• Window guards in high buildings
• Safety gates at stairs
• Supervision on climbing equipment
• Safe playground surfaces

Workplace:
• Fall protection equipment
• Proper scaffolding
• Safety training
• Appropriate footwear

OSTEOPOROSIS PREVENTION:

Lifestyle:
• Regular weight-bearing exercise
• Adequate calcium intake (1000-1200 mg/day)
• Adequate vitamin D (800-1000 IU/day)
• Smoking cessation
• Limit alcohol
• Maintain healthy body weight

Medical:
• Bone density screening for at-risk individuals
• Treat osteoporosis (bisphosphonates, etc.)
• Fall prevention in those with osteoporosis
• Fracture liaison services

SPORTS INJURY PREVENTION:
• Protective equipment (helmets, pads)
• Proper training and conditioning
• Warm-up and stretching
• Playing by the rules
• Appropriate surfaces and equipment
• Not playing through pain

OCCUPATIONAL SAFETY:
• Personal protective equipment
• Safety training
• Machine guards
• Proper lifting techniques
• Reporting and fixing hazards

VIOLENCE PREVENTION:
• Community programs
• Domestic violence intervention
• Conflict resolution education
• Alcohol and drug programs
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS A FRACTURE?

A fracture is a broken bone. It can be:
• A small crack
• A complete break
• Bone broken into many pieces

Bones have a natural ability to heal themselves, but they need to be kept still and in the right position while healing.

TREATMENT OPTIONS:

Cast or Splint:
• Keeps the bone still while it heals
• May be plaster or fiberglass
• Usually worn for several weeks
• Do NOT get wet (unless waterproof type)

Surgery:
• Sometimes needed to put bones back in place
• Metal plates, screws, or rods may be used
• These may stay in permanently or be removed later

CAST CARE:

DO:
• Keep the cast dry (use plastic bag when bathing)
• Elevate the limb to reduce swelling
• Wiggle your fingers or toes to keep blood flowing
• Use a hairdryer on cool setting if cast gets damp inside
• Report problems immediately

DO NOT:
• Get the cast wet
• Put anything inside the cast (for itching)
• Walk on a leg cast unless told it is safe
• Remove or damage the cast
• Ignore warning signs

WARNING SIGNS - SEE DOCTOR IMMEDIATELY:
• Increasing pain not helped by medicine
• Numbness or tingling in fingers or toes
• Fingers or toes turning blue, white, or very cold
• Severe swelling above or below the cast
• Burning sensation under the cast
• Foul smell from the cast
• Fever

HEALING PROCESS:
• Fractures take weeks to months to heal
• Children heal faster than adults
• Follow-up X-rays will check healing
• Do not rush return to activities

AFTER CAST REMOVAL:
• The limb may look different (smaller, skin changes)
• Stiffness is normal - it will improve
• Weakness is normal - exercise will help
• Gradual return to activities
• Follow physiotherapy advice

LIFESTYLE DURING RECOVERY:
• Eat a healthy diet with adequate protein and calcium
• Do not smoke (delays healing significantly)
• Take prescribed medications
• Attend all follow-up appointments
• Do prescribed exercises
• Ask for help with daily activities if needed

PREVENTING FUTURE FRACTURES:
• Wear seatbelts in vehicles
• Wear helmets on motorcycles and bicycles
• Make your home safe (remove trip hazards, good lighting)
• Exercise regularly for strength and balance
• Get enough calcium and vitamin D
• Do not smoke

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. If you have a fracture or suspected fracture, seek medical attention for proper diagnosis and treatment.

QUESTIONS TO ASK YOUR DOCTOR:
• What type of fracture do I have?
• Do I need surgery or a cast?
• How long will it take to heal?
• What activities should I avoid?
• When can I return to work/sports?
• What exercises should I do?
• When is my next appointment?
• What warning signs should I watch for?
    `,

    references: [
      'Court-Brown CM, Heckman JD, McQueen MM, et al., eds. Rockwood and Greens Fractures in Adults. 8th ed. Wolters Kluwer; 2015.',
      'Gosselin RA, Spiegel DA, Coughlin R, Zirkle LG. Injuries: the neglected burden in developing countries. Bulletin of the World Health Organization. 2009;87(4):246-246a.',
      'Hak DJ, Fitzpatrick D, Bishop JA, et al. Delayed union and nonunions: epidemiology, clinical issues, and financial aspects. Injury. 2014;45(Suppl 2):S3-S7.',
      'Gustilo RB, Mendoza RM, Williams DN. Problems in the management of type III (severe) open fractures: a new classification of type III open fractures. Journal of Trauma. 1984;24(8):742-746.',
      'Stiell IG, Wells GA, McDowell I, et al. Use of radiography in acute ankle injuries: physicians attitudes and practice. CMAJ. 1992;147(11):1671-1678.',
      'British Orthopaedic Association. BOAST - Open Fractures. 2017.',
      'National Institute for Health and Care Excellence. Fractures (non-complex): assessment and management. NICE guideline NG38. 2016.',
      'Mock C, Cherian MN. The global burden of musculoskeletal injuries: challenges and solutions. Clinical Orthopaedics and Related Research. 2008;466(10):2306-2316.',
      'Nwachukwu BU, Okwesili IC, Harris MB, Katz JN. Traditional bonesetters and contemporary orthopaedic fracture care in a developing nation: historical aspects, contemporary status and future directions. Open Orthopaedics Journal. 2011;5:20-26.',
      'Spiegel DA, Gosselin RA, Coughlin RR, et al. Topics in global public health. Clinical Orthopaedics and Related Research. 2008;466(10):2377-2384.',
      'World Health Organization. Global Status Report on Road Safety 2018. Geneva: WHO; 2018.',
      'McRae R, Esser M. Practical Fracture Treatment. 5th ed. Churchill Livingstone; 2008.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 58. CELLULITIS AND SKIN ABSCESS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '58',
    name: 'Cellulitis and Skin Abscess',
    slug: 'cellulitis-skin-abscess',
    specialty: 'Infectious Disease',
    bodySystem: 'Systemic',
    icdCode: 'L03.90',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
Cellulitis and skin abscesses are common bacterial infections of the skin and soft tissues. Cellulitis is an acute, spreading infection of the dermis and subcutaneous tissues, characterized by erythema, warmth, swelling, and pain. Skin abscesses (also called boils or furuncles when involving hair follicles) are localized collections of pus within the dermis or deeper tissues, presenting as painful, fluctuant nodules.

The most common causative organisms are Staphylococcus aureus (including methicillin-resistant S. aureus/MRSA in many regions) and beta-hemolytic streptococci (especially Group A Streptococcus). The distinction between cellulitis (which requires antibiotics) and abscess (which primarily requires drainage) is clinically important for management.

In Africa, skin and soft tissue infections are extremely common, driven by factors including poor sanitation, lack of clean water, minor trauma from bare feet and manual labor, insect bites, and limited access to early treatment. Tropical ulcers, a specific form of skin infection seen in tropical regions, present additional challenges. The increasing prevalence of MRSA and limited access to second-line antibiotics complicates management in many settings.

Risk factors include diabetes mellitus, lymphedema, obesity, venous insufficiency, previous cellulitis, skin barrier disruption (wounds, tinea pedis, eczema), and immunosuppression. Prompt recognition and appropriate treatment prevent progression to serious complications including necrotizing fasciitis and sepsis.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 ABSCESS NEEDS DRAINAGE - Antibiotics alone are insufficient; "if there is pus, let it out"; incision and drainage is the primary treatment

🔴 DISTINGUISH FROM NECROTIZING FASCIITIS - Pain out of proportion, rapid spread, crepitus, skin necrosis, systemic toxicity; surgical emergency

🔴 MARK THE BORDERS - Draw a line around erythema with time/date; allows objective assessment of progression or response to treatment

🔴 CONSIDER MRSA - Community-acquired MRSA is common; purulent infections especially; adjust antibiotics if not responding

🔴 ADDRESS PREDISPOSING FACTORS - Treat tinea pedis, manage diabetes, optimize lymphedema; prevents recurrence

🔴 SYSTEMIC SIGNS NEED IV ANTIBIOTICS - Fever, tachycardia, hypotension, rapidly spreading infection; consider admission

🔴 LOWER LIMB CELLULITIS - Elevate the leg; reduces edema and promotes healing; often missed intervention

🔴 DIFFERENTIATE FROM DVT - Unilateral leg swelling with cellulitis-like appearance may be DVT; assess carefully
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Skin and soft tissue infections (SSTIs) are one of the most common reasons for healthcare visits
• Annual incidence of cellulitis: 20-50 per 1,000 person-years
• Account for 2-3% of emergency department visits in developed countries
• Abscess incidence has increased with rise of community-acquired MRSA
• Recurrence rates: 20-30% for cellulitis within 3 years

AFRICAN EPIDEMIOLOGY:
• Very high burden of skin infections across all ages
• Often among top 10 reasons for outpatient visits
• Contributing factors:
  - Poor sanitation and limited clean water access
  - Overcrowded living conditions
  - Bare feet and manual labor causing skin breaks
  - Insect bites (mosquitoes, flies)
  - Delayed presentation due to access and cost
  - Traditional remedies often tried first
• Tropical ulcers: Specific entity in tropical Africa; chronic, painful leg ulcers
• Buruli ulcer (Mycobacterium ulcerans): Endemic in some West African regions

MICROBIOLOGY:

Cellulitis (Non-purulent):
• Beta-hemolytic streptococci (Group A, B, C, G): Most common
• Staphylococcus aureus: Less common in pure cellulitis
• Often polymicrobial in diabetics and immunocompromised

Abscess (Purulent Infections):
• Staphylococcus aureus: Dominant organism (80-90%)
• MRSA: 30-70% in many regions including parts of Africa
• Polymicrobial in perianal and diabetic foot infections

Special Situations:
• Water exposure: Aeromonas, Vibrio species
• Animal bites: Pasteurella, Capnocytophaga
• Human bites: Eikenella, anaerobes
• Diabetic foot: Polymicrobial including anaerobes and Gram-negatives
• Immunocompromised: Pseudomonas, fungi, atypical organisms

RISK FACTORS:

Local Factors:
• Previous cellulitis (strongest predictor of recurrence)
• Lymphedema or chronic edema
• Venous insufficiency
• Skin barrier disruption (wounds, tinea pedis, eczema, ulcers)
• Surgical incisions
• Injection drug use
• Insect bites
• Traumatic wounds

Systemic Factors:
• Diabetes mellitus
• Obesity
• Immunosuppression (HIV, steroids, chemotherapy)
• Malnutrition
• Chronic kidney disease
• Chronic liver disease
• Peripheral vascular disease
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

CELLULITIS:

Cardinal Features:
• Erythema: Redness that spreads; ill-defined borders
• Warmth: Area feels hot compared to surrounding skin
• Swelling (Edema): Affected area is swollen
• Pain/Tenderness: Painful to touch and often spontaneously

Additional Features:
• Fever and chills (systemic involvement)
• Malaise
• Regional lymphadenopathy
• Lymphangitis (red streaks toward lymph nodes)
• Skin may have peau d'orange appearance (edema around hair follicles)

Typical Presentation:
• Unilateral involvement
• Often lower extremity (leg most common site)
• Portal of entry may be visible (wound, tinea pedis, insect bite)
• Relatively slow spread (over days)

SKIN ABSCESS:

Cardinal Features:
• Fluctuant nodule: Soft, compressible, moves like a balloon of fluid
• Painful: Often very tender
• Erythema: Surrounding redness
• May have pointing: Central area of thinning skin, may rupture

Types of Abscesses:
• Furuncle (Boil): Infection of hair follicle; single, painful nodule
• Carbuncle: Coalescence of multiple furuncles; larger, deeper; multiple drainage points
• Perianal/Perirectal abscess: Near anus; severe pain; may have systemic symptoms
• Bartholin abscess: Vulvar; unilateral painful swelling at vaginal introitus
• Pilonidal abscess: Midline sacral/coccygeal area; recurrent

ERYSIPELAS (Specific Form of Cellulitis):

• Sharply demarcated, raised borders
• "Butterfly" distribution on face common
• Usually Group A Streptococcus
• More superficial (dermis) than typical cellulitis
• Often more acute onset with fever and chills

CLASSIFICATION BY SEVERITY:

Class 1 (Mild):
• No systemic toxicity
• No uncontrolled comorbidities
• Can be treated as outpatient

Class 2 (Moderate):
• Systemically unwell OR
• Has comorbidity (diabetes, obesity) affecting response

Class 3 (Severe):
• Significant systemic toxicity (fever, tachycardia)
• Unstable comorbidities
• Limb-threatening infection

Class 4 (Life-threatening):
• Sepsis or necrotizing infection
• Requires urgent surgery and ICU care

PHYSICAL EXAMINATION:

Inspection:
• Extent of erythema (mark borders)
• Skin integrity (wound, ulcer, tinea)
• Signs of abscess (fluctuance, pointing)
• Skin necrosis or bullae (concerning for necrotizing infection)
• Crepitus (gas in tissue - emergency)

Palpation:
• Warmth
• Tenderness
• Fluctuance (pus collection)
• Induration (hardened tissue)
• Crepitus (subcutaneous emphysema)
• Lymph nodes (regional lymphadenopathy)

Assess for:
• Portal of entry (tinea pedis, wound, insect bite)
• Signs of systemic infection (fever, tachycardia, hypotension)
• Comorbidities (check for diabetic foot, lymphedema)

AREAS REQUIRING SPECIAL ATTENTION:

Facial Cellulitis:
• Risk of cavernous sinus thrombosis
• May need IV antibiotics and hospitalization
• Periorbital vs orbital cellulitis distinction critical

Perineal/Perianal:
• Risk of Fournier gangrene (necrotizing fasciitis of perineum)
• High morbidity and mortality
• May spread rapidly

Hand Infections:
• Risk of tendon sheath infection (flexor tenosynovitis)
• Kanavel signs: Fusiform swelling, flexed posture, tenderness along sheath, pain with extension
• Surgical emergency
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL DIAGNOSIS

Cellulitis and abscess are primarily CLINICAL DIAGNOSES based on:
• History: Onset, progression, risk factors, portal of entry
• Examination: Erythema, warmth, swelling, tenderness, fluctuance

Key Questions:
• Is this cellulitis, abscess, or both?
• Is there purulence (suggesting Staph, including MRSA)?
• Is this necrotizing fasciitis (surgical emergency)?
• What is the severity (outpatient vs inpatient)?
• What are the predisposing factors?

STEP 2: DISTINGUISH PURULENT VS NON-PURULENT

PURULENT (Abscess with or without Cellulitis):
• Fluctuant collection of pus
• Drainage is the primary treatment
• Staphylococcus aureus (including MRSA) likely
• Antibiotics may be optional for small drained abscesses

NON-PURULENT (Cellulitis without Abscess):
• No drainable collection
• Streptococci more likely
• Antibiotics are essential

STEP 3: EXCLUDE NECROTIZING FASCIITIS

RED FLAGS for Necrotizing Fasciitis:
• Pain out of proportion to examination findings
• Rapid progression despite antibiotics
• Systemic toxicity (high fever, confusion, hypotension)
• Skin necrosis, bullae, or ecchymosis
• Crepitus (gas in tissues)
• Woody induration
• Anesthesia of affected area

If suspected: Urgent surgical consultation; do not delay for imaging

STEP 4: ASSESS SEVERITY

Indicators of Severe Infection:
• Fever >38°C or hypothermia
• Tachycardia >100 bpm
• Hypotension
• Altered mental status
• Extensive involvement (large area)
• Rapid progression
• Bullae, skin necrosis
• Severe pain
• Complicating comorbidities

STEP 5: IDENTIFY PREDISPOSING FACTORS

Check for:
• Diabetes mellitus (check blood glucose, HbA1c)
• Tinea pedis (examine interdigital spaces)
• Chronic edema/lymphedema
• Venous insufficiency
• Previous cellulitis
• Immunosuppression
• Portal of entry (wound, bite, ulcer)

DIFFERENTIAL DIAGNOSIS APPROACH:

Lower Limb Swelling and Redness:
• Cellulitis: Unilateral, warm, tender, may have fever
• DVT: Unilateral swelling, may have calf tenderness, less erythema
• Stasis dermatitis: Bilateral, chronic, pigmentation changes, varicose veins
• Contact dermatitis: Itchy, vesicles, corresponds to contact pattern
• Gout: Joint involvement, severe pain, may have tophi

WHEN TO IMAGE:

Plain X-ray:
• Suspected foreign body
• Suspected osteomyelitis (underlying bone infection)
• Gas in soft tissues (necrotizing infection)

Ultrasound:
• Distinguish cellulitis from abscess (if clinical doubt)
• Identify drainable fluid collections
• Guide drainage if needed

CT or MRI:
• Suspected necrotizing fasciitis (but do not delay surgery for imaging)
• Deep space infections
• Extent of infection in complex cases
    `,

    differentialDiagnosis: [
      'Deep vein thrombosis (DVT) - Unilateral swelling, may have calf pain, less erythema and warmth; Doppler ultrasound to diagnose',
      'Stasis dermatitis - Bilateral, chronic changes, venous insufficiency signs, itching, pigmentation; often misdiagnosed as cellulitis',
      'Contact dermatitis - Itchy, vesicles/bullae, pattern matches contact with allergen; history of exposure',
      'Necrotizing fasciitis - Pain out of proportion, rapid spread, skin necrosis, systemic toxicity; surgical emergency',
      'Erythema nodosum - Painful nodules usually on shins; bilateral; often systemic cause (sarcoid, IBD, infections)',
      'Gout/Gouty arthritis - Joint-centered inflammation, severe pain, may have tophi; can coexist with cellulitis',
      'Septic arthritis - Joint swelling, very painful, limited ROM, fever; joint aspirate diagnostic',
      'Insect bite reaction - History of bite, central punctum, rapid onset, very itchy; may be secondarily infected',
      'Lipodermatosclerosis - Chronic painful induration of lower leg, "inverted champagne bottle" appearance; often mistaken for cellulitis',
      'Ruptured Baker cyst - Sudden calf swelling and pain; history of knee arthritis; ultrasound shows ruptured cyst',
      'Pyoderma gangrenosum - Painful ulcer with violaceous undermined borders; often associated with IBD; diagnosis of exclusion',
      'Herpes zoster - Painful vesicles in dermatomal distribution; precedes rash with pain/tingling',
      'Fixed drug eruption - Well-demarcated plaque, history of recurrence at same site with drug exposure',
      'Hematoma - History of trauma, ecchymosis, fluctuant but not warm; ultrasound shows blood',
      'Superficial thrombophlebitis - Tender, palpable cord along superficial vein; linear erythema'
    ],

    investigations: [
      {
        name: 'Clinical Examination',
        indication: '🔴 ESSENTIAL - Cellulitis and abscess are clinical diagnoses; look for erythema, warmth, swelling, fluctuance',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Screen for diabetes; uncontrolled diabetes is major risk factor and affects healing',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Leukocytosis suggests infection; bandemia indicates severe infection; may be normal in early/mild cellulitis',
        availability: 'BASIC'
      },
      {
        name: 'Wound Swab for Culture',
        indication: '🟠 IF PURULENT - Swab pus from abscess; guides antibiotic therapy especially if not responding to empiric treatment',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures',
        indication: '🟠 IF SYSTEMICALLY UNWELL - Fever, rigors, signs of sepsis; positive in only 2-5% of cellulitis but important if positive',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine',
        indication: '🟠 IMPORTANT - Baseline renal function; affects antibiotic dosing; may indicate sepsis if elevated',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🟡 HELPFUL - Elevated in infection; can help monitor response to treatment; not specific',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: '🟠 IF DIABETIC OR SUSPECTED - Assess glucose control; guide long-term management',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Test',
        indication: '🟠 CONSIDER - Recurrent or unusual infections; offer testing per national guidelines',
        availability: 'BASIC'
      },
      {
        name: 'Plain X-ray',
        indication: '🟠 IF INDICATED - Suspected foreign body, gas in tissues (necrotizing infection), underlying osteomyelitis',
        availability: 'BASIC'
      },
      {
        name: 'Ultrasound',
        indication: '🟡 IF DOUBT ABOUT ABSCESS - Identifies drainable fluid collection; can guide aspiration/drainage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Doppler Ultrasound of Leg Veins',
        indication: '🟠 IF DVT SUSPECTED - Differentiate cellulitis from DVT; unilateral leg swelling without clear cellulitis features',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Scan',
        indication: '🟡 FOR COMPLEX CASES - Deep space infections, necrotizing fasciitis extent (do not delay surgery); not routine',
        availability: 'ADVANCED'
      },
      {
        name: 'Skin Biopsy',
        indication: '🟡 RARELY NEEDED - Atypical presentation, not responding to treatment, suspect alternative diagnosis',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Mild (outpatient), moderate (may need admission), severe (requires admission and IV antibiotics)',
        '⚡ IDENTIFY PURULENT VS NON-PURULENT: Abscess needs drainage; cellulitis needs antibiotics',
        '⚡ EXCLUDE NECROTIZING FASCIITIS: Pain out of proportion, rapid spread, necrosis, crepitus; urgent surgery if suspected',
        '⚡ MARK THE BORDERS: Draw line around erythema with date/time; allows objective monitoring of progression or response',
        '⚡ INCISION AND DRAINAGE FOR ABSCESS: Primary treatment for abscess; antibiotics alone are insufficient',
        '⚡ START ANTIBIOTICS: For cellulitis or abscess with surrounding cellulitis; tailor to purulent vs non-purulent',
        '⚡ ADDRESS PREDISPOSING FACTORS: Check for and treat tinea pedis; optimize diabetes control; manage edema',
        '⚡ ELEVATE THE LIMB: For lower limb cellulitis; reduces edema and promotes healing',
        '⚡ ANALGESIA: Adequate pain control; NSAIDs (if no contraindication) or paracetamol; opioids if severe',
        '⚡ TETANUS PROPHYLAXIS: Update if wound present and immunization not current'
      ],
      
      pharmacological: [
        {
          drug: 'Flucloxacillin',
          indication: '🔴 FIRST-LINE for non-purulent cellulitis - Covers streptococci and MSSA; narrow spectrum preferred',
          dose: 'Mild: 500 mg PO every 6 hours. Moderate-Severe: 1-2 g IV every 6 hours. Duration: 5-7 days (extend if slow response).',
          route: 'PO or IV',
          duration: '5-7 days minimum; may need 10-14 days for severe',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['GI upset', 'Rash', 'Hepatitis (rare)', 'Cholestatic jaundice'],
          alternatives: ['Cloxacillin', 'Cephalexin', 'Clindamycin (if penicillin allergic)']
        },
        {
          drug: 'Clindamycin',
          indication: '🟠 ALTERNATIVE or ADD for severe - Covers Staph (including some MRSA), Strep, anaerobes; use if penicillin allergic or MRSA suspected',
          dose: 'PO: 300-450 mg every 8 hours. IV: 600-900 mg every 8 hours.',
          route: 'PO or IV',
          duration: '5-10 days',
          contraindications: ['Previous C. diff colitis with clindamycin'],
          sideEffects: ['Diarrhea', 'C. difficile colitis (significant risk)', 'Rash', 'Metallic taste'],
          alternatives: ['Doxycycline', 'TMP-SMX (for MRSA coverage)']
        },
        {
          drug: 'Amoxicillin-Clavulanate (Co-Amoxiclav)',
          indication: '🟠 ALTERNATIVE - Broader spectrum; useful for bite wounds, diabetic infections, mixed infections',
          dose: 'PO: 625 mg every 8 hours OR 1g every 12 hours. IV: 1.2 g every 8 hours.',
          route: 'PO or IV',
          duration: '5-10 days',
          contraindications: ['Penicillin allergy', 'Previous cholestatic jaundice with this drug'],
          sideEffects: ['Diarrhea', 'Rash', 'Cholestatic hepatitis', 'Candidiasis'],
          alternatives: ['Clindamycin + Ciprofloxacin']
        },
        {
          drug: 'Trimethoprim-Sulfamethoxazole (TMP-SMX / Cotrimoxazole)',
          indication: '🟠 FOR MRSA COVERAGE - Excellent MRSA coverage; use for purulent infections or if MRSA suspected',
          dose: 'Adults: 160/800 mg (1 DS tablet) PO twice daily. Children: 4-6 mg/kg TMP component twice daily.',
          route: 'PO',
          duration: '5-10 days',
          contraindications: ['Sulfa allergy', 'Severe renal impairment', 'G6PD deficiency (relative)', 'Pregnancy (third trimester)'],
          sideEffects: ['Rash', 'GI upset', 'Hyperkalemia', 'Cytopenias', 'Stevens-Johnson syndrome (rare)'],
          alternatives: ['Doxycycline', 'Clindamycin']
        },
        {
          drug: 'Doxycycline',
          indication: '🟠 FOR MRSA COVERAGE - Alternative oral agent with MRSA activity',
          dose: 'Adults: 100 mg PO twice daily. Not for children <8 years or pregnant women.',
          route: 'PO',
          duration: '5-10 days',
          contraindications: ['Pregnancy', 'Children <8 years', 'Severe hepatic impairment'],
          sideEffects: ['Photosensitivity', 'GI upset', 'Esophageal irritation', 'Tooth discoloration in children'],
          alternatives: ['TMP-SMX', 'Clindamycin']
        },
        {
          drug: 'Cephalexin (Cefalexin)',
          indication: '🟠 ALTERNATIVE first-generation cephalosporin - Covers MSSA and streptococci; does not cover MRSA',
          dose: 'Adults: 500 mg PO every 6 hours. Children: 25-50 mg/kg/day divided every 6-8 hours.',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Severe penicillin allergy (cross-reactivity risk)'],
          sideEffects: ['GI upset', 'Rash', 'Allergic reactions'],
          alternatives: ['Flucloxacillin', 'Clindamycin']
        },
        {
          drug: 'Vancomycin',
          indication: '🔴 FOR SEVERE MRSA or penicillin allergy - IV only; for severe infections requiring MRSA coverage',
          dose: '15-20 mg/kg IV every 8-12 hours. Adjust for renal function. Monitor trough levels (target 10-20 mg/L).',
          route: 'IV',
          duration: '7-14 days depending on response',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Nephrotoxicity', 'Red man syndrome (infuse slowly)', 'Ototoxicity', 'Thrombocytopenia'],
          alternatives: ['Linezolid', 'Daptomycin']
        },
        {
          drug: 'Metronidazole',
          indication: '🟠 ADD for anaerobic coverage - Diabetic foot infections, perianal abscesses, bite wounds',
          dose: 'PO: 400-500 mg every 8 hours. IV: 500 mg every 8 hours.',
          route: 'PO or IV',
          duration: 'With other antibiotics; duration depends on infection',
          contraindications: ['First trimester pregnancy (relative)'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol', 'Peripheral neuropathy (prolonged use)'],
          alternatives: ['Clindamycin has anaerobic coverage']
        },
        {
          drug: 'Benzylpenicillin (Penicillin G)',
          indication: '🟠 FOR SEVERE STREPTOCOCCAL INFECTIONS - Severe non-purulent cellulitis, erysipelas; excellent strep coverage',
          dose: '1.2-2.4 g (2-4 million units) IV every 4-6 hours.',
          route: 'IV',
          duration: 'Until improving, then switch to oral',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Allergic reactions', 'GI upset', 'Electrolyte disturbances (potassium in high doses)'],
          alternatives: ['Clindamycin', 'Vancomycin (if allergic)']
        },
        {
          drug: 'Paracetamol',
          indication: '🔴 ESSENTIAL - Analgesia and antipyretic; first-line for pain',
          dose: 'Adults: 1g PO every 6 hours (max 4g/day). Children: 15 mg/kg every 6 hours.',
          route: 'PO',
          duration: 'As needed',
          contraindications: ['Severe liver disease'],
          sideEffects: ['Hepatotoxicity with overdose'],
          alternatives: ['NSAIDs if not contraindicated']
        },
        {
          drug: 'Ibuprofen',
          indication: '🟠 ADJUNCT - Anti-inflammatory and analgesic; note some concern about NSAID use in cellulitis (controversial)',
          dose: 'Adults: 400 mg PO every 6-8 hours. Use with caution; some data suggest NSAIDs may mask necrotizing fasciitis.',
          route: 'PO',
          duration: 'Short-term for pain',
          contraindications: ['Renal impairment', 'GI bleeding', 'Aspirin-sensitive asthma'],
          sideEffects: ['GI upset', 'Renal impairment', 'May mask signs of worsening infection (controversial)'],
          alternatives: ['Paracetamol alone', 'Opioids for severe pain']
        },
        {
          drug: 'Tetanus Toxoid',
          indication: '🟠 IF WOUND PRESENT - Update tetanus prophylaxis per guidelines',
          dose: 'Td 0.5 mL IM if needed.',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Severe reaction to previous dose'],
          sideEffects: ['Injection site reactions'],
          alternatives: ['TT if Td unavailable']
        }
      ],
      
      nonPharmacological: [
        'INCISION AND DRAINAGE (I&D): Primary treatment for abscess; make adequate incision over fluctuant area; break up loculations; irrigate; pack wound loosely if needed',
        'ELEVATION: Elevate affected limb above heart level; reduces edema and promotes healing; very important for lower limb cellulitis',
        'MARK BORDERS: Draw line around erythema with date and time; objective assessment of progression or response',
        'WARM COMPRESSES: May help localize/ripen an abscess before drainage; use with caution (do not apply to area of spreading cellulitis)',
        'IMMOBILIZATION: Rest the affected area; may use splint for hand infections',
        'WOUND CARE: Keep wounds clean; apply sterile dressing; change daily or when soiled',
        'ADDRESS PREDISPOSING FACTORS: Treat tinea pedis (topical antifungals); manage eczema; compression for lymphedema; optimize diabetes',
        'EDUCATION: Complete antibiotic course; return if worsening; address risk factors to prevent recurrence',
        'NUTRITION: Adequate protein for healing; especially important in malnourished patients',
        'COMPRESSION THERAPY: For chronic lymphedema after acute infection resolves; reduces recurrence risk'
      ],
      
      surgical: [
        'INCISION AND DRAINAGE (I&D): Standard procedure for abscesses; adequate anesthesia (local infiltration, nerve block, or procedural sedation); incision length = diameter of abscess; break up loculations; irrigate; pack if needed',
        'NEEDLE ASPIRATION: Alternative for small abscesses; may be insufficient; lower success rate than formal I&D; useful when I&D not immediately available',
        'WOUND PACKING: Loosely pack abscess cavity with gauze to prevent premature closure; remove packing at 24-48 hours; wound heals by secondary intention',
        'DEBRIDEMENT: For necrotizing fasciitis - aggressive surgical debridement of all necrotic tissue; often multiple operations needed; life-saving',
        'FASCIOTOMY: For necrotizing fasciitis involving fascial planes; extensive incisions to decompress and allow debridement',
        'SKIN GRAFTING: May be needed after extensive debridement for necrotizing fasciitis or large tissue defects',
        'DRAINAGE OF DEEP SPACE INFECTIONS: Require surgical expertise; hand infections (tenosynovitis), perirectal abscesses, deep neck infections'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Suspected necrotizing fasciitis - Surgical emergency; pain out of proportion, rapid spread, necrosis, systemic toxicity',
        '🔴 IMMEDIATE REFERRAL: Fournier gangrene - Necrotizing fasciitis of perineum; very high mortality; needs urgent surgery',
        '🔴 IMMEDIATE REFERRAL: Orbital cellulitis - Risk of vision loss and intracranial spread; needs IV antibiotics and possible surgery',
        '🔴 IMMEDIATE REFERRAL: Flexor tenosynovitis of hand - Kanavel signs; needs urgent surgical drainage',
        '🔴 IMMEDIATE REFERRAL: Sepsis - High fever, hypotension, altered consciousness; needs ICU care',
        '🟠 URGENT REFERRAL: Rapidly progressing cellulitis despite antibiotics - May need IV antibiotics or alternative diagnosis',
        '🟠 URGENT REFERRAL: Periorbital cellulitis in children - Risk of progression to orbital; needs close monitoring',
        '🟠 URGENT REFERRAL: Large or deep abscess - May need surgical drainage under anesthesia',
        '🟠 URGENT REFERRAL: Diabetic foot infection - Needs multidisciplinary management; may need surgery',
        '🟠 URGENT REFERRAL: Facial cellulitis - Risk of intracranial spread; may need IV antibiotics',
        '🟡 ROUTINE REFERRAL: Recurrent cellulitis - Investigation of predisposing factors; consider prophylactic antibiotics',
        '🟡 ROUTINE REFERRAL: Chronic lymphedema - Needs specialist management to reduce recurrence'
      ],
      
      monitoring: [
        'ERYTHEMA MARGINS: Check daily; mark new borders; should see improvement within 24-48 hours of starting antibiotics',
        'TEMPERATURE: Monitor for defervescence; persistent fever suggests inadequate treatment or complication',
        'PAIN: Should improve with treatment; worsening pain concerning for complication',
        'SYSTEMIC SYMPTOMS: Improving energy, appetite; worsening concerning',
        'WOUND/ABSCESS: If drained, check healing; packing removal; watch for re-accumulation',
        'WHITE BLOOD CELL COUNT: If initially elevated, should normalize with treatment; rising WBC concerning',
        'BLOOD GLUCOSE: In diabetics; ensure good control for healing',
        'ANTIBIOTIC RESPONSE: If no improvement in 48-72 hours, consider alternative diagnosis, resistant organism, or undrained collection',
        'PREDISPOSING FACTORS: Monitor and treat tinea pedis, edema management'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DURING TREATMENT:

Outpatient Cellulitis:
• Review at 48-72 hours to assess response
• Erythema should be reducing (check against marked borders)
• Systemic symptoms improving
• If not improving: Consider IV antibiotics, alternative diagnosis, resistant organism

After Abscess Drainage:
• Wound check at 24-48 hours
• Remove packing if placed
• Assess for re-accumulation or surrounding cellulitis
• May need repeat drainage if collection reforms

TREATMENT DURATION:
• Uncomplicated cellulitis: 5-7 days usually sufficient
• Extend to 10-14 days if:
  - Slow to respond
  - Severe infection
  - Immunocompromised patient
  - Residual signs at 5 days

AFTER RESOLUTION:

Address Predisposing Factors:
• Treat tinea pedis: Topical antifungal for 4 weeks (terbinafine, clotrimazole)
• Manage chronic edema: Compression stockings, elevation, skin care
• Optimize diabetes: Target HbA1c <7% (or individualized)
• Skin care: Moisturize dry skin; treat eczema
• Weight management if obese

PREVENTION OF RECURRENCE:

High-Risk Patients (≥2 episodes/year):
• Consider prophylactic antibiotics:
  - Penicillin V 250 mg PO twice daily, OR
  - Erythromycin 250 mg PO twice daily (if penicillin allergic)
  - Continue for 6-12 months; reassess
• Aggressive management of predisposing factors
• Patient education on early signs and prompt treatment

Lymphedema Management:
• Compression therapy (stockings, bandaging)
• Complete decongestive therapy if available
• Skin care to prevent entry points
• Prompt treatment of any skin breaks

RECURRENT CELLULITIS:

Investigation:
• Screen for diabetes
• Assess venous insufficiency and lymphedema
• HIV test if recurrent or unusual
• Consider immune deficiency if multiple episodes

Management:
• Treat acute episode
• Address all risk factors aggressively
• Consider antibiotic prophylaxis
• Refer to dermatology or infectious disease if complex

FOLLOW-UP SCHEDULE:
• Uncomplicated: Review at 48-72 hours, then at end of treatment
• Abscess: 24-48 hours post-drainage, then weekly until healed
• Recurrent: After resolution, follow-up to address risk factors and discuss prophylaxis
      `
    },

    redFlags: [
      '🔴 CRITICAL: Pain out of proportion to examination - Suggests necrotizing fasciitis; surgical emergency',
      '🔴 CRITICAL: Crepitus (gas in tissues) - Necrotizing infection; needs immediate surgery',
      '🔴 CRITICAL: Skin necrosis, bullae, or ecchymosis - Necrotizing fasciitis likely; urgent surgical referral',
      '🔴 CRITICAL: Rapid progression despite antibiotics - May be necrotizing infection; reassess urgently',
      '🔴 CRITICAL: Sepsis (fever, hypotension, tachycardia, confusion) - Needs aggressive resuscitation and IV antibiotics',
      '🔴 CRITICAL: Perineal/scrotal cellulitis - Risk of Fournier gangrene; very high mortality; surgical emergency',
      '🔴 CRITICAL: Orbital cellulitis (proptosis, ophthalmoplegia, vision loss) - Risk of blindness and intracranial spread',
      '🟠 URGENT: Facial cellulitis - Risk of cavernous sinus thrombosis; may need IV antibiotics',
      '🟠 URGENT: Kanavel signs in hand (flexor tenosynovitis) - Fusiform swelling, flexed posture, tenderness along sheath, pain with extension; surgical emergency',
      '🟠 URGENT: Not responding to antibiotics after 48-72 hours - Consider alternative diagnosis, resistant organism, undrained collection',
      '🟠 URGENT: Immunocompromised patient with cellulitis - May progress rapidly; lower threshold for admission',
      '🟡 CAUTION: Diabetic foot infection - High risk of osteomyelitis, amputation; needs multidisciplinary care',
      '🟡 CAUTION: Animal or human bite wound infection - Risk of unusual organisms; may need surgical washout'
    ],

    complications: [
      'Necrotizing fasciitis - Life-threatening deep infection requiring emergency surgery; high mortality if delayed',
      'Sepsis and septic shock - Systemic spread of infection; can be fatal; needs aggressive treatment',
      'Bacteremia - Bacteria in bloodstream; can lead to metastatic infection',
      'Abscess formation - May occur during cellulitis; needs drainage',
      'Osteomyelitis - Spread to underlying bone; especially diabetic foot; difficult to treat',
      'Septic arthritis - Spread to adjacent joint; needs drainage and IV antibiotics',
      'Lymphangitis - Spread along lymphatic channels; red streaks toward regional nodes',
      'Thrombophlebitis - Inflammation of veins; can lead to DVT',
      'Deep vein thrombosis - From immobility and inflammation; especially lower limb cellulitis',
      'Endocarditis - Rare complication from bacteremia; especially in those with abnormal valves',
      'Recurrent cellulitis - Damages lymphatics leading to chronic lymphedema and further recurrence',
      'Chronic lymphedema - Permanent swelling from lymphatic damage; predisposes to recurrent infection',
      'Post-inflammatory hyperpigmentation - Skin darkening after resolution; cosmetic concern',
      'Scarring - Especially after surgical drainage or necrotizing infection'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

CELLULITIS:

Uncomplicated Cellulitis:
• Excellent prognosis with appropriate treatment
• Most respond within 24-48 hours of antibiotics
• Complete resolution expected in 7-10 days
• Mortality very low (<1%) in uncomplicated cases

Factors Associated with Poor Outcome:
• Delayed treatment
• Inadequate antibiotic coverage
• Uncontrolled diabetes
• Immunosuppression
• Progression to necrotizing fasciitis
• Development of sepsis

SKIN ABSCESS:

• Excellent prognosis if adequately drained
• Most heal within 1-2 weeks after drainage
• Antibiotics often not needed for small drained abscesses in healthy patients
• Recurrence possible, especially if risk factors persist

NECROTIZING FASCIITIS:

• Mortality 20-40% even with treatment
• Higher mortality with delayed diagnosis and surgery
• Survivors often have significant morbidity (amputations, skin grafts, prolonged hospitalization)
• Early recognition and surgery critical for survival

RECURRENCE:

• Recurrence rate for cellulitis: 20-30% within 3 years
• Risk factors for recurrence:
  - Previous cellulitis (strongest predictor)
  - Lymphedema
  - Venous insufficiency
  - Obesity
  - Tinea pedis
  - Diabetes
• Prophylactic antibiotics reduce recurrence by ~50%

LONG-TERM OUTCOMES:

• Most patients recover fully without sequelae
• Chronic lymphedema may develop after multiple episodes
• Recurrent infections damage lymphatic system further
• Post-inflammatory hyperpigmentation usually fades with time
• Addressing predisposing factors improves long-term outcomes
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

SKIN CARE:

General Measures:
• Keep skin clean and moisturized
• Avoid skin dryness (use emollients)
• Protect skin from trauma (wear shoes, gloves when appropriate)
• Treat cuts and abrasions promptly (clean, antiseptic, cover)
• Do not scratch insect bites excessively

Specific Conditions:
• Tinea pedis: Treat with topical antifungals; keep feet dry; change socks regularly
• Eczema: Moisturize regularly; treat flares; avoid scratching
• Lymphedema: Compression therapy; skin care; avoid injury; treat infections promptly
• Leg ulcers: Proper wound care; compression for venous ulcers; diabetic foot care

HYGIENE:

• Regular bathing/washing
• Clean water if available
• Hand hygiene to prevent spread of bacteria
• Clean clothes
• Clean bedding
• Avoid sharing personal items (towels, razors)

DIABETES MANAGEMENT:

• Good glycemic control (HbA1c <7% or individualized target)
• Daily foot inspection
• Proper footwear (no bare feet)
• Regular podiatry care if available
• Prompt treatment of any wounds

WOUND CARE:

• Clean wounds with clean water and soap
• Apply antiseptic if available
• Cover with clean dressing
• Watch for signs of infection
• Seek medical care if infection develops

FOR PATIENTS WITH RECURRENT CELLULITIS:

Prophylactic Antibiotics:
• Consider if ≥2 episodes per year
• Penicillin V 250 mg PO twice daily, OR
• Erythromycin 250 mg twice daily (if penicillin allergic)
• Continue 6-12 months; reassess risk factors

Risk Factor Modification:
• Treat tinea pedis aggressively (oral antifungals if topical fails)
• Compression for lymphedema/venous insufficiency
• Weight loss if obese
• Optimize diabetes control
• Leg elevation

COMMUNITY MEASURES:

• Access to clean water
• Improved sanitation
• Health education on wound care
• Early treatment of infections
• Footwear promotion
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS CELLULITIS?

Cellulitis is an infection of the skin caused by bacteria. The affected skin becomes:
• Red
• Warm
• Swollen
• Painful

Bacteria usually enter through a break in the skin such as a cut, scratch, insect bite, or athlete's foot.

WHAT IS A SKIN ABSCESS (BOIL)?

An abscess is a collection of pus under the skin. It appears as:
• A painful lump
• May have a "head" or point
• Feels soft and movable
• Surrounded by redness

TREATMENT:

For Cellulitis:
• Take ALL your antibiotic tablets as prescribed
• Do not stop early even if you feel better
• Elevate the affected area (especially leg)
• Rest the affected area
• Take painkillers if needed

For Abscess:
• The abscess needs to be DRAINED (cut open to let pus out)
• Antibiotics alone may not work without drainage
• The wound may need to be packed with gauze
• Return for wound check as advised

WARNING SIGNS - RETURN IMMEDIATELY IF:
• Infection is spreading (getting bigger despite treatment)
• Red line traveling up the limb
• High fever or feeling very unwell
• Increasing pain, especially if severe
• Blackening of the skin
• Feeling confused or very drowsy

AFTER YOUR INFECTION HEALS:

To Prevent Future Infections:
• Treat athlete's foot (fungal infection between toes)
• Keep your skin moisturized (dry skin cracks)
• Clean any cuts or wounds quickly
• Protect your skin from injury
• Wear shoes to protect your feet
• Control your blood sugar if diabetic
• Elevate swollen legs when resting

If You Get Repeated Infections:
• See your doctor - you may need preventive antibiotics
• You may need tests for diabetes or other conditions

SKIN CARE TIPS:

Daily Care:
• Wash affected area gently
• Pat dry (do not rub)
• Apply moisturizer to prevent dryness
• Check skin daily for any breaks or early signs of infection

If You Have a Wound:
• Wash with clean water and soap
• Apply antiseptic if available
• Cover with clean bandage
• Change dressing daily
• Watch for signs of infection

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Skin infections can become serious if not treated properly.

Seek medical care if you develop a skin infection, especially if:
• You have diabetes
• You have a weak immune system
• The infection is on your face
• The infection is spreading quickly
• You feel very unwell

QUESTIONS TO ASK YOUR DOCTOR:
• Is this cellulitis or an abscess?
• Do I need antibiotics or drainage?
• How should I take care of the wound?
• What signs should I watch for?
• Do I need to come back for a check?
• How can I prevent this from happening again?
    `,

    references: [
      'Stevens DL, Bisno AL, Chambers HF, et al. Practice guidelines for the diagnosis and management of skin and soft tissue infections: 2014 update by the IDSA. Clinical Infectious Diseases. 2014;59(2):e10-e52.',
      'Raff AB, Kroshinsky D. Cellulitis: a review. JAMA. 2016;316(3):325-337.',
      'Liu C, Bayer A, Cosgrove SE, et al. Clinical practice guidelines by the IDSA for the treatment of methicillin-resistant Staphylococcus aureus infections in adults and children. Clinical Infectious Diseases. 2011;52(3):e18-e55.',
      'Dalal A, Eskin-Schwartz M, Mimouni D, et al. Interventions for the prevention of recurrent erysipelas and cellulitis. Cochrane Database of Systematic Reviews. 2017;6(6):CD009758.',
      'Thomas KS, Crook AM, Nunn AJ, et al. Penicillin to prevent recurrent leg cellulitis. New England Journal of Medicine. 2013;368(18):1695-1703.',
      'Quirke M, Ayoub F, McCabe A, et al. Risk factors for nonpurulent leg cellulitis: a systematic review and meta-analysis. British Journal of Dermatology. 2017;177(2):382-394.',
      'Ki V, Rotstein C. Bacterial skin and soft tissue infections in adults: a review of their epidemiology, pathogenesis, diagnosis, treatment and site of care. Canadian Journal of Infectious Diseases and Medical Microbiology. 2008;19(2):173-184.',
      'Moet GJ, Jones RN, Biedenbach DJ, et al. Contemporary causes of skin and soft tissue infections in North America, Latin America, and Europe: report from the SENTRY Antimicrobial Surveillance Program. Diagnostic Microbiology and Infectious Disease. 2007;57(1):7-13.',
      'Talan DA, Mower WR, Krishnadasan A, et al. Trimethoprim-sulfamethoxazole versus placebo for uncomplicated skin abscess. New England Journal of Medicine. 2016;374(9):823-832.',
      'Sartelli M, Guirao X, Hardcastle TC, et al. 2018 WSES/SIS-E consensus conference: recommendations for the management of skin and soft-tissue infections. World Journal of Emergency Surgery. 2018;13:58.',
      'Baddour LM. Cellulitis and erysipelas. UpToDate. 2023.',
      'World Health Organization. Pocket book of hospital care for children. 2nd ed. Geneva: WHO; 2013.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 59. ECZEMA / ATOPIC DERMATITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '59',
    name: 'Eczema / Atopic Dermatitis',
    slug: 'eczema-atopic-dermatitis',
    specialty: 'Dermatology',
    bodySystem: 'Systemic',
    icdCode: 'L20.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
Atopic dermatitis (AD), commonly called eczema, is a chronic, relapsing inflammatory skin condition characterized by intensely itchy, dry skin with a typical distribution that varies with age. It is the most common inflammatory skin disease, affecting up to 20% of children and 3% of adults worldwide, with onset usually in early childhood.

Atopic dermatitis is part of the "atopic triad" or "atopic march," often associated with asthma and allergic rhinitis. The pathophysiology involves genetic predisposition (including filaggrin gene mutations affecting skin barrier function), immune dysregulation (Th2-skewed inflammation), and environmental factors (irritants, allergens, microbes). The resulting skin barrier dysfunction leads to increased transepidermal water loss, susceptibility to irritants and allergens, and microbial colonization.

In Africa, atopic dermatitis is increasingly recognized as a significant problem, though historically underdiagnosed. Prevalence is rising, particularly in urban areas, possibly due to the "hygiene hypothesis" and lifestyle changes. Management is complicated by limited access to emollients and topical corticosteroids, hot and humid climates that exacerbate symptoms, and the prominent post-inflammatory hyperpigmentation or hypopigmentation that occurs in darker skin tones.

The cornerstone of management is regular emollient use to restore the skin barrier, avoidance of triggers, and appropriate use of topical anti-inflammatory medications (corticosteroids or calcineurin inhibitors) during flares. Patient and family education is essential for successful long-term management of this chronic condition.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 EMOLLIENTS ARE THE FOUNDATION - Apply liberally and frequently (at least twice daily); use even when skin is clear; prevents flares

🔴 ITCH IS THE HALLMARK - "The itch that rashes"; intense pruritus leads to scratching and the itch-scratch cycle

🔴 TOPICAL STEROIDS ARE SAFE WHEN USED CORRECTLY - Use appropriate potency for site; treat flares adequately; do not underdose due to steroid phobia

🔴 RECOGNIZE SECONDARY INFECTION - Impetiginized eczema (crusting, weeping) needs antibiotics; Staph aureus colonizes 90% of AD skin

🔴 ECZEMA HERPETICUM IS AN EMERGENCY - Widespread herpes infection in eczema; punched-out erosions, fever, systemic illness; needs urgent antiviral

🔴 DISTRIBUTION VARIES BY AGE - Infants: face and extensor surfaces; Children: flexures; Adults: hands and flexures

🔴 AVOID KNOWN TRIGGERS - Soaps, detergents, wool, heat, sweating, stress, certain foods (in proven cases only)

🔴 POST-INFLAMMATORY PIGMENT CHANGES ARE COMMON - Especially in dark skin; reassure that these usually improve with time and eczema control
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Most common inflammatory skin disease worldwide
• Affects 15-20% of children and 1-3% of adults
• Prevalence has increased 2-3 fold over past 50 years
• Higher in developed countries; increasing in developing countries
• 60% develop symptoms in first year of life
• 90% develop symptoms by age 5 years
• Many improve with age; some persist into adulthood
• Approximately 50% of childhood AD resolves by adolescence

AFRICAN EPIDEMIOLOGY:
• Prevalence varies widely: 5-20% depending on region and methodology
• Higher in urban than rural areas
• Increasing prevalence, especially in rapidly urbanizing populations
• Often underdiagnosed due to:
  - Presentation differs in dark skin (less visible erythema)
  - Limited access to dermatological expertise
  - Focus on infectious diseases in healthcare systems
• Challenges in African settings:
  - Hot, humid climate exacerbates symptoms
  - Limited availability of emollients and topical steroids
  - Cost of treatments
  - Post-inflammatory hyper/hypopigmentation prominent

THE ATOPIC TRIAD:
• Atopic dermatitis
• Allergic rhinitis
• Asthma
• 50-80% of children with AD develop asthma or allergic rhinitis later
• Family history of atopy in 70% of patients

GENETICS AND RISK FACTORS:

Strong Risk Factors:
• Family history of atopy (strongest predictor)
• Filaggrin gene mutations (present in 20-50% of moderate-severe AD)
• African ancestry associated with more severe disease in some studies

Associated Factors:
• Urban living
• Higher socioeconomic status (in developed countries)
• Small family size
• Reduced microbial exposure (hygiene hypothesis)
• Cesarean delivery
• Antibiotic use in infancy (controversial)
• Climate (worse in low humidity, temperature extremes)

Protective Factors (Possible):
• Early diverse diet introduction
• Pet exposure (controversial)
• Farm living
• Breastfeeding (controversial/modest effect)
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

CARDINAL FEATURES:
• Pruritus (itch): Essential for diagnosis; often severe; worse at night
• Xerosis (dry skin): Generalized dryness
• Eczematous morphology: Erythema, papules, vesicles (acute); lichenification (chronic)
• Typical distribution by age (see below)
• Chronic or relapsing course

AGE-SPECIFIC PRESENTATIONS:

INFANTILE (Birth to 2 years):
• Face (cheeks) and scalp
• Extensor surfaces (outer arms and legs)
• Trunk may be involved
• Diaper area typically spared
• Often weepy, crusted lesions
• May have "cradle cap" appearance on scalp

CHILDHOOD (2-12 years):
• Flexural surfaces (antecubital fossae, popliteal fossae, wrists, ankles)
• Neck creases
• May have perioral and periorbital involvement
• Lesions become drier, lichenified (thickened skin with accentuated lines)
• Post-inflammatory pigment changes prominent in dark skin

ADOLESCENT/ADULT:
• Flexures remain common
• Hand eczema prominent
• Face and neck (head and neck dermatitis)
• May have generalized involvement
• Chronic lichenified plaques
• Prurigo nodularis (intensely itchy nodules) in chronic cases

ASSOCIATED FEATURES:

Classic Signs:
• Dennie-Morgan folds: Extra crease under lower eyelid
• Facial pallor: Pale face with infraorbital darkening
• Hyperlinear palms: Increased palmar creases
• Keratosis pilaris: "Chicken skin" on upper arms, thighs
• Pityriasis alba: Hypopigmented patches on face (especially dark skin)
• White dermographism: White line when skin stroked (instead of red)

IN SKIN OF COLOR (Important for African Patients):

• Erythema less visible: May appear gray, purple, or brown rather than red
• Follicular prominence: Papules centered on hair follicles
• Lichenification prominent: Thickened, "woody" feel to chronic lesions
• Post-inflammatory hyperpigmentation: Very common; may be more distressing than eczema itself
• Post-inflammatory hypopigmentation: Also common; can mimic vitiligo
• Pityriasis alba: Very visible as hypopigmented patches on face

SEVERITY ASSESSMENT:

Mild:
• Dry skin, infrequent itching
• Little impact on daily activities/sleep
• Limited areas affected

Moderate:
• Dry skin, frequent itching
• Some impact on daily activities/sleep
• More widespread involvement
• Frequent flares

Severe:
• Widespread dry skin, constant itching
• Significant impact on sleep and daily activities
• Extensive areas affected
• Bleeding, oozing, cracking
• Lichenification
• Pigment changes

PHYSICAL EXAMINATION:

Assess:
• Distribution and extent (use body surface area estimation)
• Morphology: Acute (erythema, vesicles, weeping) vs Chronic (lichenification, scaling)
• Signs of secondary infection (crusting, pustules, increased weeping)
• Stigmata of atopy (Dennie-Morgan folds, hyperlinear palms)
• Impact of pigmentary changes
• Signs of eczema herpeticum (punched-out erosions, vesicles on eczematous skin)
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL DIAGNOSIS

Atopic dermatitis is a CLINICAL DIAGNOSIS. No specific laboratory test exists.

UK Working Party Diagnostic Criteria (Validated, Practical):

Must have:
• Itchy skin condition (or parental report of scratching/rubbing in child)

Plus THREE or more of:
• History of involvement of skin creases (flexures, neck, around eyes in children <4 years)
• History of asthma or hay fever (or atopic disease in first-degree relative if child <4 years)
• History of general dry skin in past year
• Visible flexural eczema (or eczema of cheeks/forehead/outer limbs in child <4 years)
• Onset before age 2 years (if child ≥4 years)

STEP 2: ASSESS SEVERITY

Formal Scoring (If Needed):
• SCORAD: Extent, intensity, subjective symptoms (0-103)
• EASI: Extent and intensity of 4 regions (0-72)
• IGA: Investigator Global Assessment (0-4)

Practical Severity Assessment:
• Clear: No signs
• Mild: Dry skin, occasional itch
• Moderate: Frequent itch, moderate erythema/excoriation, some sleep disturbance
• Severe: Widespread, constant itch, extensive excoriation, significant sleep/activity impairment

STEP 3: IDENTIFY TRIGGERS AND COMPLICATIONS

Common Triggers:
• Irritants: Soaps, detergents, woolen clothing, synthetic fabrics
• Allergens: Dust mites, pet dander, pollens, certain foods
• Climate: Heat, cold, low humidity, sweating
• Infection: Staphylococcus aureus colonization
• Stress and emotions
• Hormonal changes (in women)

Complications to Look For:
• Secondary bacterial infection (impetiginized eczema)
• Eczema herpeticum (herpes simplex infection)
• Eye complications (cataracts, keratoconus - rare)
• Growth delay from severe disease or overuse of potent topical steroids
• Psychological impact

STEP 4: EXCLUDE DIFFERENTIAL DIAGNOSES

Consider alternative diagnoses if:
• Onset in adulthood without prior history
• Atypical distribution
• Not responding to appropriate treatment
• Systemic symptoms
• Signs of other specific conditions

WHEN TO INVESTIGATE:

Most cases need NO investigations. Consider:

Skin Swab:
• If secondary infection suspected
• Recurrent infections
• Not responding to treatment

Allergy Testing (Skin Prick or Specific IgE):
• Only if specific allergic trigger suspected (e.g., particular food worsens symptoms)
• Not routine; positive results do not always correlate with clinical relevance
• Negative tests more useful (to exclude allergy)

Skin Biopsy:
• Rarely needed
• If diagnosis uncertain and other conditions (e.g., cutaneous T-cell lymphoma) considered

HIV Test:
• If recurrent infections, severe disease, or other risk factors
    `,

    differentialDiagnosis: [
      'Seborrheic dermatitis - Greasy scales, scalp and face, less itchy, nasolabial folds involved; may coexist with AD',
      'Contact dermatitis (allergic or irritant) - Pattern matches exposure; may be superimposed on AD; patch testing if suspected',
      'Psoriasis - Well-demarcated plaques with silvery scale; extensor surfaces; nail changes; family history',
      'Scabies - Intense itch especially at night; burrows; web spaces and wrists; close contacts affected; may trigger or mimic eczema',
      'Tinea corporis - Annular, scaly, advancing border with central clearing; KOH positive; usually asymmetric',
      'Nummular eczema - Coin-shaped plaques; may be form of AD or distinct; less flexural',
      'Pityriasis rosea - Herald patch, Christmas tree distribution; resolves in 6-8 weeks; less itchy',
      'Lichen simplex chronicus - Localized lichenified plaque from chronic rubbing; often nape, ankles, genitals',
      'Impetigo - Honey-crusted lesions; Staph/Strep infection; may complicate AD (impetiginized eczema)',
      'Cutaneous T-cell lymphoma (mycosis fungoides) - Adults; persistent patches/plaques not responding to treatment; needs biopsy',
      'Ichthyosis vulgaris - Fish-like scales; often associated with AD; genetic',
      'Zinc deficiency (acrodermatitis enteropathica) - Periorificial and acral distribution; diarrhea; failure to thrive',
      'Langerhans cell histiocytosis - Infants; seborrheic-like rash not responding; systemic involvement',
      'Wiskott-Aldrich syndrome - Boys; severe eczema, thrombocytopenia, immunodeficiency',
      'Hyper-IgE syndrome - Severe eczema, recurrent skin and lung infections, very high IgE'
    ],

    investigations: [
      {
        name: 'Clinical Diagnosis',
        indication: '🔴 ESSENTIAL - AD is a clinical diagnosis based on UK Working Party or Hanifin-Rajka criteria; history and examination',
        availability: 'BASIC'
      },
      {
        name: 'Skin Swab for Culture',
        indication: '🟠 IF SECONDARY INFECTION SUSPECTED - Yellow crusting, weeping, pustules; guides antibiotic choice',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🟡 IF USING SYSTEMIC STEROIDS - Monitor for hyperglycemia; also if recurrent infections suggest diabetes',
        availability: 'BASIC'
      },
      {
        name: 'Serum IgE',
        indication: '🟡 NOT ROUTINE - May be elevated in AD but not diagnostic; useful if immunodeficiency suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Specific IgE / Skin Prick Testing',
        indication: '🟡 IF SPECIFIC ALLERGY SUSPECTED - Food allergy, aeroallergen; not routine; interpret cautiously',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Patch Testing',
        indication: '🟡 IF CONTACT ALLERGY SUSPECTED - Persistent eczema in specific pattern; occupational exposure; not improving',
        availability: 'ADVANCED'
      },
      {
        name: 'Viral Swab / PCR',
        indication: '🟠 IF ECZEMA HERPETICUM SUSPECTED - Punched-out erosions, vesicles on eczema; confirms HSV',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Skin Biopsy',
        indication: '🟡 IF DIAGNOSIS UNCERTAIN - Atypical presentation; not responding to treatment; exclude other conditions',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Test',
        indication: '🟠 CONSIDER IN APPROPRIATE SETTINGS - Severe or recurrent infections; adult-onset severe eczema',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 IF SYSTEMIC DISEASE SUSPECTED - Eosinophilia may be present but not diagnostic',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Mild, moderate, severe; guides intensity of treatment',
        '⚡ CHECK FOR SECONDARY INFECTION: Crusting, weeping, pustules need antibiotics before or with anti-inflammatory treatment',
        '⚡ EXCLUDE ECZEMA HERPETICUM: Punched-out erosions, vesicles, fever; needs urgent antiviral treatment',
        '⚡ START EMOLLIENTS IMMEDIATELY: Foundation of treatment; apply liberally and frequently; use even when skin is clear',
        '⚡ INITIATE TOPICAL CORTICOSTEROID FOR FLARE: Choose appropriate potency for site and severity; treat adequately to gain control',
        '⚡ PROVIDE WRITTEN ECZEMA ACTION PLAN: Clear instructions for daily care and flare management',
        '⚡ IDENTIFY AND ADDRESS TRIGGERS: Irritants, allergens, heat, stress',
        '⚡ EDUCATE ON BATHING PRACTICES: Short lukewarm baths; avoid soap; apply emollient immediately after'
      ],
      
      pharmacological: [
        {
          drug: 'Emollients (Moisturizers)',
          indication: '🔴 ESSENTIAL - Foundation of AD management; restores skin barrier; prevents flares; use even when skin is clear',
          dose: 'Apply liberally at least TWICE daily to entire skin surface. Apply MORE frequently to affected areas (up to 4-6 times daily). Apply within 3 minutes of bathing (to damp skin). Use 250-500g per week for child; 500g-1kg per week for adult if severe.',
          route: 'Topical',
          duration: 'Ongoing; lifelong for most patients',
          contraindications: ['Allergy to specific ingredient (rare)'],
          sideEffects: ['Stinging on broken skin (avoid those with urea)', 'Folliculitis (rare with ointments)'],
          alternatives: ['Multiple options: Petroleum jelly (cheap, effective), aqueous cream, emulsifying ointment, proprietary emollients']
        },
        {
          drug: 'Hydrocortisone 1% (Mild Potency Steroid)',
          indication: '🔴 FIRST-LINE for mild AD and sensitive areas - Face, neck, flexures, genitals, infants; safe for long-term use on appropriate sites',
          dose: 'Apply thin layer to affected areas once or twice daily. Fingertip unit (FTU): 0.5g covers area of 2 adult palms.',
          route: 'Topical',
          duration: 'Until flare controlled (usually 7-14 days); can use intermittently',
          contraindications: ['Untreated bacterial, viral, or fungal infection'],
          sideEffects: ['Skin thinning (minimal with mild potency)', 'Telangiectasia (rare)', 'Contact allergy (rare)'],
          alternatives: ['Tacrolimus/Pimecrolimus for steroid-sparing on face']
        },
        {
          drug: 'Betamethasone Valerate 0.1% / Clobetasone 0.05% (Moderate Potency Steroid)',
          indication: '🟠 FOR MODERATE AD on body - Trunk and limbs (not face or flexures); moderate flares',
          dose: 'Apply thin layer once or twice daily. Use fingertip units for dosing. Reduce frequency or potency once controlled.',
          route: 'Topical',
          duration: 'Usually 7-14 days for flare; step down to mild steroid for maintenance',
          contraindications: ['Face, genitals (use milder potency)', 'Untreated infection'],
          sideEffects: ['Skin thinning with prolonged use', 'Striae', 'Telangiectasia', 'Hypopigmentation'],
          alternatives: ['Step up to potent if not responding; step down to mild for maintenance']
        },
        {
          drug: 'Betamethasone Dipropionate 0.05% / Mometasone (Potent Steroid)',
          indication: '🟠 FOR SEVERE FLARES on body - Thick lichenified plaques; trunk and limbs; NOT face or flexures',
          dose: 'Apply thin layer once daily (or twice daily for severe). Short courses only (usually 7-14 days max). Step down to moderate/mild once controlled.',
          route: 'Topical',
          duration: 'Short courses; usually 1-2 weeks maximum',
          contraindications: ['Face', 'Flexures', 'Children (use with caution)', 'Large areas', 'Prolonged use'],
          sideEffects: ['Skin atrophy', 'Striae', 'Telangiectasia', 'Systemic absorption with extensive use', 'Hypopigmentation'],
          alternatives: ['Step up to very potent for resistant areas; always step down']
        },
        {
          drug: 'Clobetasol Propionate 0.05% (Very Potent Steroid)',
          indication: '🟡 FOR SEVERE/RESISTANT PLAQUES - Lichenified areas, palms/soles; specialist use; short courses only',
          dose: 'Apply once daily. Maximum 2 weeks continuous use. Maximum 50g per week. Step down to lower potency once controlled.',
          route: 'Topical',
          duration: 'Maximum 2 weeks; avoid prolonged use',
          contraindications: ['Face', 'Flexures', 'Children (avoid)', 'Large body surface areas'],
          sideEffects: ['Significant skin atrophy risk', 'Striae', 'Systemic absorption', 'Adrenal suppression with overuse'],
          alternatives: ['Consider systemic therapy if very potent steroids needed frequently']
        },
        {
          drug: 'Tacrolimus 0.03% / 0.1% Ointment',
          indication: '🟡 STEROID-SPARING - Face, neck, flexures; where topical steroids cause problems; moderate-severe AD',
          dose: '0.03% for children 2-15 years; 0.1% for adults and children >15 years. Apply twice daily until clear, then can use twice weekly for maintenance.',
          route: 'Topical',
          duration: 'Can be used long-term; twice weekly maintenance for relapsing disease',
          contraindications: ['Children <2 years', 'Active infection'],
          sideEffects: ['Burning/stinging on application (usually transient, first week)', 'Flushing with alcohol', 'Theoretical malignancy concern (FDA black box - not substantiated)'],
          alternatives: ['Pimecrolimus cream 1% (less potent, less burning)']
        },
        {
          drug: 'Chlorpheniramine / Hydroxyzine',
          indication: '🟡 ADJUNCT for night-time itch - Sedating antihistamines may help sleep; do not reduce itch in AD specifically',
          dose: 'Chlorpheniramine: Adults 4 mg at bedtime; Children 2-6 years: 1 mg; 6-12 years: 2 mg. Hydroxyzine: Adults 25-50 mg at bedtime; Children: 1-2 mg/kg/day.',
          route: 'PO',
          duration: 'As needed for sleep; not for continuous use',
          contraindications: ['Acute asthma (relative)'],
          sideEffects: ['Sedation (desired for sleep)', 'Dry mouth', 'Urinary retention'],
          alternatives: ['Non-sedating antihistamines do NOT help eczema itch but may help if concurrent allergic rhinitis']
        },
        {
          drug: 'Flucloxacillin / Cephalexin',
          indication: '🔴 FOR IMPETIGINIZED ECZEMA - Secondary bacterial infection; crusting, pustules, weeping',
          dose: 'Flucloxacillin: Adults 500 mg QID; Children: 12.5-25 mg/kg/day divided QID. Cephalexin: 25-50 mg/kg/day divided QID.',
          route: 'PO',
          duration: '7 days usually sufficient',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['GI upset', 'Rash', 'Allergic reactions'],
          alternatives: ['Erythromycin if penicillin allergic', 'Topical mupirocin for localized infection']
        },
        {
          drug: 'Mupirocin 2% Ointment',
          indication: '🟠 FOR LOCALIZED BACTERIAL INFECTION - Small areas of impetiginized eczema; S. aureus decolonization',
          dose: 'Apply to affected areas three times daily for 5-7 days. For nasal decolonization: Apply to anterior nares TID for 5 days.',
          route: 'Topical',
          duration: '5-7 days; avoid prolonged use (resistance)',
          contraindications: ['Large open wounds (systemic absorption of base)'],
          sideEffects: ['Burning', 'Stinging', 'Resistance with overuse'],
          alternatives: ['Fusidic acid (not available everywhere)']
        },
        {
          drug: 'Acyclovir',
          indication: '🔴 ESSENTIAL FOR ECZEMA HERPETICUM - Emergency; widespread herpes simplex infection in eczematous skin',
          dose: 'Adults: 400-800 mg PO five times daily OR 5-10 mg/kg IV every 8 hours (severe). Children: 20 mg/kg/dose PO five times daily (max 800 mg) OR 10-20 mg/kg IV every 8 hours.',
          route: 'PO or IV',
          duration: '5-10 days; IV for severe, switch to oral when improving',
          contraindications: ['Renal impairment (adjust dose)'],
          sideEffects: ['Nausea', 'Headache', 'Renal impairment (IV - hydrate adequately)'],
          alternatives: ['Valacyclovir (better oral bioavailability)']
        },
        {
          drug: 'Prednisolone (Oral)',
          indication: '🟡 FOR SEVERE FLARES - Short course for acute severe exacerbation; avoid frequent/prolonged use',
          dose: 'Adults: 0.5-1 mg/kg/day (max 40-60 mg/day) for 5-7 days, then taper. Children: 1-2 mg/kg/day for 5-7 days.',
          route: 'PO',
          duration: 'Short course only (5-14 days); rebound common so optimize topical therapy before stopping',
          contraindications: ['Active infection (relative)', 'Systemic fungal infection'],
          sideEffects: ['Mood changes', 'Increased appetite', 'Rebound flare on stopping', 'Adrenal suppression with prolonged use', 'Growth suppression in children'],
          alternatives: ['Optimize topical therapy; consider specialist referral for steroid-sparing systemic agents']
        }
      ],
      
      nonPharmacological: [
        'EMOLLIENT THERAPY: Apply liberally at least twice daily; more frequently to active areas; use within 3 minutes of bathing; ointments more effective than creams; lotions least effective',
        'BATHING PRACTICES: Daily lukewarm baths/showers (not hot); limit to 5-10 minutes; use soap substitute (emollient wash); apply emollient to damp skin immediately after',
        'SOAP SUBSTITUTES: Avoid all soaps and bubble baths; use emollients as soap substitutes or gentle cleansers',
        'WET WRAP THERAPY: For severe flares; apply emollient (and sometimes diluted steroid) then wet bandage layer, dry layer over; wear overnight; very effective for acute flares',
        'TRIGGER AVOIDANCE: Identify and avoid individual triggers; common: soaps, detergents, woolen/synthetic clothing, heat, sweating, dust mites, pet dander',
        'CLOTHING: Cotton or soft fabrics; avoid wool and rough synthetics; wash new clothes before wearing; use non-biological washing powder; rinse well',
        'ENVIRONMENTAL: Moderate temperature; avoid overheating; humidifier in dry climates; mattress and pillow covers for dust mite allergy',
        'NAIL CARE: Keep nails short and smooth to minimize damage from scratching; cotton mittens at night for children',
        'DIETARY MODIFICATION: Only if proven food allergy (rare in AD); do not restrict diet without evidence; proper allergy testing first',
        'PSYCHOLOGICAL SUPPORT: Recognize impact on quality of life; address sleep disturbance, behavioral issues; support groups'
      ],
      
      surgical: [
        'NOT APPLICABLE: Atopic dermatitis does not require surgical treatment',
        'PATCH TESTING: Dermatology procedure to identify contact allergies if suspected superimposed allergic contact dermatitis'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Eczema herpeticum - Punched-out erosions, vesicles, fever; needs urgent IV acyclovir',
        '🔴 IMMEDIATE REFERRAL: Erythroderma (>90% skin involved) - May need admission for supportive care',
        '🟠 URGENT REFERRAL: Severe eczema not responding to appropriate treatment - May need systemic therapy',
        '🟠 URGENT REFERRAL: Severe sleep disturbance or psychological impact - Quality of life significantly affected',
        '🟠 URGENT REFERRAL: Recurrent severe infections - May need investigation for immunodeficiency',
        '🟡 ROUTINE REFERRAL: Diagnostic uncertainty - Atypical features, consider differential diagnoses',
        '🟡 ROUTINE REFERRAL: Need for patch testing - Suspected contact allergy component',
        '🟡 ROUTINE REFERRAL: Failure of appropriate topical therapy - May need specialist topical regimens or systemics',
        '🟡 ROUTINE REFERRAL: Significant facial involvement not responding - May benefit from tacrolimus/specialist input',
        '🟡 ROUTINE REFERRAL: Suspected food allergy - For proper allergy testing before dietary modification'
      ],
      
      monitoring: [
        'SEVERITY ASSESSMENT: Use simple scoring (clear, mild, moderate, severe) or formal tool (SCORAD, EASI) at each visit',
        'TREATMENT RESPONSE: Expect improvement within 2-4 weeks of appropriate treatment; if not, reassess diagnosis and management',
        'EMOLLIENT USE: Check compliance and technique; most patients underuse emollients',
        'STEROID USE: Monitor for side effects (skin thinning, striae); ensure appropriate potency for site; avoid steroid phobia leading to undertreatment',
        'GROWTH IN CHILDREN: Monitor height if using moderate-potent steroids extensively or oral steroids',
        'INFECTION: Watch for signs of secondary bacterial or viral infection at each visit',
        'QUALITY OF LIFE: Assess sleep, daily activities, psychological impact; use QoL tools if available (CDLQI, DLQI)',
        'TRIGGER IDENTIFICATION: Ongoing assessment for triggers; dietary, environmental, occupational'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL MANAGEMENT:

First Visit:
• Confirm diagnosis
• Assess severity
• Educate on nature of condition (chronic, relapsing)
• Start emollients (demonstrate application)
• Start appropriate topical steroid for flare
• Provide written action plan
• Identify obvious triggers

Follow-up at 2-4 Weeks:
• Assess response to treatment
• Review emollient and steroid technique
• Adjust treatment if needed
• Reinforce education
• Address any concerns/questions

ONGOING MANAGEMENT:

Well-Controlled Eczema:
• Follow-up every 3-6 months
• Continue emollients daily
• Have topical steroids available for flares
• Written flare management plan
• Monitor for complications

Poorly Controlled Eczema:
• More frequent follow-up (every 2-4 weeks)
• Step up treatment
• Consider triggers
• Consider specialist referral
• Address compliance issues

MAINTENANCE STRATEGIES:

Proactive Approach:
• Continue emollients daily even when clear
• For relapsing eczema: Intermittent topical steroid (e.g., twice weekly) OR tacrolimus twice weekly to previously affected areas
• Reduces flare frequency

FLARE MANAGEMENT:

• Increase emollient frequency
• Start topical steroid appropriate for severity
• Apply steroid once or twice daily until clear (usually 7-14 days)
• Then step down potency or frequency
• Check for infection

NATURAL HISTORY:

In Children:
• 60% clear by adolescence
• 20% have persistent disease into adulthood
• 20% have recurrence in adulthood after childhood clearance

Predictors of Persistence:
• Early onset
• Severe childhood disease
• Associated asthma/allergic rhinitis
• Family history of atopy
• Filaggrin mutations

TRANSITION TO ADULTHOOD:

• Educate on chronic nature
• Transfer to adult services with written summary
• Ensure understanding of self-management
• Address occupational implications (avoid wet work, irritants)

PATIENT EDUCATION POINTS:

• Eczema is a chronic condition; treatment controls but does not cure
• Emollients are the foundation; use even when skin is clear
• Topical steroids are safe when used correctly
• Report signs of infection promptly
• Pigment changes will improve with eczema control
• Avoid unproven remedies
      `
    },

    redFlags: [
      '🔴 CRITICAL: Eczema herpeticum - Punched-out erosions, clustered vesicles, fever, malaise; HSV infection; needs urgent IV acyclovir',
      '🔴 CRITICAL: Erythroderma (>90% BSA) - Risk of hypothermia, fluid loss, cardiac failure; may need admission',
      '🔴 CRITICAL: Kaposi varicelliform eruption - Widespread vesicles; may be HSV or vaccinia; systemic illness',
      '🟠 URGENT: Severe secondary bacterial infection - Extensive crusting, cellulitis, systemic symptoms; may need IV antibiotics',
      '🟠 URGENT: Severe eczema not responding to appropriate treatment - Reassess diagnosis; consider specialist referral',
      '🟠 URGENT: Significant growth faltering in child - May indicate severe disease or steroid overuse',
      '🟡 CAUTION: Periorbital eczema with eye symptoms - Cataracts, keratoconus, blepharitis; ophthalmology referral',
      '🟡 CAUTION: Recurrent skin infections - Consider immunodeficiency (HIV, Hyper-IgE syndrome)',
      '🟡 CAUTION: Atypical distribution or morphology - Consider differential diagnoses',
      '🟡 CAUTION: Signs of topical steroid overuse - Skin thinning, striae, telangiectasia; reduce potency, consider alternatives'
    ],

    complications: [
      'Secondary bacterial infection - S. aureus colonizes 90% of AD skin; can cause impetiginization; worsens flares',
      'Eczema herpeticum - Disseminated HSV infection; punched-out erosions; systemic illness; can be life-threatening',
      'Molluscum contagiosum - Poxvirus; more extensive in AD; umbilicated papules',
      'Post-inflammatory hyperpigmentation - Very common in dark skin; persists after eczema clears; distressing',
      'Post-inflammatory hypopigmentation - Can mimic vitiligo; usually temporary',
      'Lichenification - Chronic thickening from scratching; may be difficult to reverse',
      'Sleep disturbance - Major impact on quality of life; affects whole family',
      'Psychological impact - Depression, anxiety, social isolation, bullying; affects self-esteem',
      'Growth impairment - From severe disease or topical/systemic steroid overuse',
      'Skin atrophy from topical steroids - Thinning, striae, telangiectasia; from inappropriate use',
      'Allergic contact dermatitis - May develop to emollients, topical medications',
      'Eye complications - Anterior subcapsular cataracts, keratoconus; rare; from rubbing, steroids',
      'Irritant hand eczema - Occupational implications; avoid wet work'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

NATURAL HISTORY:

Childhood-Onset AD:
• 60% clear by adolescence
• 20% have persistent disease into adulthood
• 20% experience clearance then recurrence later

Adult-Onset AD:
• Generally more persistent
• May be occupational (irritant or allergic contact dermatitis superimposed)

PROGNOSTIC FACTORS:

Better Prognosis (More Likely to Remit):
• Later onset (after age 2)
• Milder disease
• No associated asthma/allergic rhinitis
• No family history
• No filaggrin mutations

Worse Prognosis (More Likely to Persist):
• Early onset (first year of life)
• Severe disease
• Associated asthma, allergic rhinitis, food allergy
• Strong family history
• Filaggrin gene mutations
• Urban environment
• African ancestry (in some studies)

QUALITY OF LIFE:

• Significant impact on patient and family
• Sleep disturbance very common
• Affects school, work, social activities
• Psychological burden (depression, anxiety)
• Economic burden (treatments, time off work)
• Impact often underestimated by healthcare providers

ATOPIC MARCH:

• 50-80% of children with AD develop asthma and/or allergic rhinitis
• Typically: AD first → Food allergy → Asthma → Allergic rhinitis
• Early effective treatment of AD may (theoretically) reduce risk, but not proven

LONG-TERM OUTCOMES:

• Most patients achieve good control with appropriate management
• Quality of life can be restored with education and treatment
• Post-inflammatory pigment changes improve over months to years
• Occupational implications need consideration (avoid irritant exposure)
• Even those who "outgrow" AD may have:
  - Dry, sensitive skin
  - Irritant hand eczema
  - Increased risk of contact dermatitis
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing AD Development):

Evidence-Based:
• Daily emollient application from birth in high-risk infants (family history of atopy)
• May reduce AD development by up to 50% (some studies)
• No proven dietary interventions during pregnancy or lactation

Uncertain/Insufficient Evidence:
• Breastfeeding: Modest benefit possible but evidence mixed
• Probiotics: Some positive studies but inconsistent; not routinely recommended
• Early allergen introduction: May help prevent food allergy; unclear effect on AD
• Avoiding pets: Not recommended; some evidence early exposure may be protective

SECONDARY PREVENTION (Preventing Flares in Established AD):

Emollient Maintenance:
• Continue daily emollients even when skin is clear
• Restores skin barrier
• Reduces flare frequency and severity

Proactive Therapy:
• For patients with frequent relapses
• Apply low-potency steroid OR tacrolimus twice weekly to previously affected areas
• Reduces relapse frequency

Trigger Avoidance:
• Irritants: Avoid soaps, detergents, bubble baths; use emollient washes
• Clothing: Cotton or silk; avoid wool and rough synthetics
• Temperature: Avoid extremes; keep cool; avoid sweating
• Allergens: If specific allergy proven (dust mites, pets)
• Stress: Recognize and manage stress

Infection Prevention:
• Good skin care to maintain barrier
• Prompt treatment of flares (reduces S. aureus burden)
• Treat bacterial infections promptly
• Antiseptic bath additives (dilute bleach baths) may help some patients

PREVENTING COMPLICATIONS:

• Treat flares early and adequately
• Recognize and treat infections promptly
• Use appropriate steroid potency to avoid undertreatment and overtreatment
• Keep nails short to minimize excoriation
• Vaccinate normally (avoid live vaccines during severe flare or on immunosuppressants)

LIFESTYLE ADVICE:

• Keep home clean but not sterile
• Moderate temperature in home
• Regular emollient routine
• Have flare treatment readily available
• Know when to seek medical help
• Support groups and patient education resources
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS ECZEMA (ATOPIC DERMATITIS)?

Eczema is a common skin condition that makes your skin:
• Dry
• Itchy
• Red or darker than usual
• Sometimes cracked, weepy, or thickened

It usually comes and goes (flares and remissions). It is NOT contagious - you cannot catch it from someone else.

WHY DOES IT HAPPEN?

• The skin barrier is not working properly
• This lets moisture out and irritants in
• The immune system overreacts
• It often runs in families (along with asthma and hay fever)

TREATMENT - THE BASICS:

1. MOISTURIZERS (Emollients) - THE MOST IMPORTANT TREATMENT
• Use every day, even when skin looks normal
• Apply generously at least twice a day (more when flaring)
• Apply after bathing to damp skin
• Use them like you would use water if you were thirsty

2. STEROID CREAMS (for flares)
• Use when the skin is red, itchy, or flaring
• Apply thin layer to affected areas only
• Use the right strength for the right place
• Face and flexures (folds): Mild (e.g., hydrocortisone)
• Body: Moderate or potent as prescribed
• Are SAFE when used as directed - do not be afraid to use them properly

3. AVOID TRIGGERS
• Soaps and bubble baths (use moisturizer to wash instead)
• Wool and synthetic fabrics (cotton is best)
• Getting too hot or sweating
• Things you know make it worse

HOW TO BATHE:
• Use lukewarm water (not hot)
• Short baths or showers (5-10 minutes)
• Use moisturizer or soap substitute instead of soap
• Pat dry gently (do not rub)
• Apply moisturizer immediately while skin is still damp

SIGNS OF INFECTION (See Doctor):
• Yellow crusting or oozing
• Increased redness spreading
• Pain or tenderness
• Fever or feeling unwell
• Tiny blisters or raw areas appearing suddenly (could be herpes - urgent!)

SKIN COLOR CHANGES:
• Dark patches or light patches often develop
• This is NORMAL with eczema
• They will improve as the eczema is controlled
• It takes time (months) for the color to return to normal
• Do NOT use strong steroid creams to try to fix the color - this will not help

FOR PARENTS OF CHILDREN WITH ECZEMA:
• Cut nails short to reduce scratching damage
• Cotton mittens at night for babies
• Keep bedroom cool
• Be patient - it takes time
• It is not your fault

COMMON MYTHS:

❌ "Steroids are dangerous" - When used correctly, they are safe and essential
❌ "Eczema is caused by poor hygiene" - Not true; too much washing makes it worse
❌ "You need to find and eliminate foods" - Food allergy is rare; do not restrict diet without proper testing
❌ "It will be cured" - It can be controlled, not cured; but many children grow out of it

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Eczema management should be guided by healthcare professionals.

QUESTIONS TO ASK YOUR DOCTOR:
• What strength steroid should I use and where?
• How much moisturizer should I be using?
• How do I know if it is infected?
• What are my (or my child's) triggers?
• Will the skin color go back to normal?
• When should I come back?
    `,

    references: [
      'Weidinger S, Novak N. Atopic dermatitis. Lancet. 2016;387(10023):1109-1122.',
      'Eichenfield LF, Tom WL, Chamlin SL, et al. Guidelines of care for the management of atopic dermatitis. Journal of the American Academy of Dermatology. 2014;70(2):338-351.',
      'National Institute for Health and Care Excellence. Atopic eczema in under 12s: diagnosis and management. NICE guideline CG57. 2007 (updated 2021).',
      'Silverberg JI. Public health burden and epidemiology of atopic dermatitis. Dermatologic Clinics. 2017;35(3):283-289.',
      'Odhiambo JA, Williams HC, Clayton TO, et al. Global variations in prevalence of eczema symptoms in children from ISAAC Phase Three. Journal of Allergy and Clinical Immunology. 2009;124(6):1251-1258.',
      'Williams HC, Burney PG, Pembroke AC, Hay RJ. The UK Working Party diagnostic criteria for atopic dermatitis. British Journal of Dermatology. 1994;131(3):383-396.',
      'Hoare C, Li Wan Po A, Williams H. Systematic review of treatments for atopic eczema. Health Technology Assessment. 2000;4(37):1-191.',
      'Simpson EL, Chalmers JR, Hanifin JM, et al. Emollient enhancement of the skin barrier from birth offers effective atopic dermatitis prevention. Journal of Allergy and Clinical Immunology. 2014;134(4):818-823.',
      'Wollenberg A, Barbarot S, Bieber T, et al. Consensus-based European guidelines for treatment of atopic eczema (atopic dermatitis) in adults and children: part I. Journal of the European Academy of Dermatology and Venereology. 2018;32(5):657-682.',
      'Arkwright PD, Motala C, Subramanian H, et al. Management of difficult-to-treat atopic dermatitis. Journal of Allergy and Clinical Immunology: In Practice. 2013;1(2):142-151.',
      'Ben-Gashir MA, Seed PT, Hay RJ. Quality of life and disease severity are correlated in children with atopic dermatitis. British Journal of Dermatology. 2004;150(2):284-290.',
      'Leung DY, Guttman-Yassky E. Deciphering the complexities of atopic dermatitis: shifting paradigms in treatment approaches. Journal of Allergy and Clinical Immunology. 2014;134(4):769-779.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 60. FUNGAL SKIN INFECTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '60',
    name: 'Fungal Skin Infections',
    slug: 'fungal-skin-infections',
    specialty: 'Dermatology',
    bodySystem: 'Systemic',
    icdCode: 'B35.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `
Fungal skin infections (dermatomycoses) are extremely common conditions affecting the skin, hair, and nails, caused primarily by dermatophytes (tinea/ringworm), yeasts (Candida, Malassezia), and occasionally molds. These superficial mycoses represent some of the most prevalent infectious diseases worldwide, affecting an estimated 20-25% of the global population.

Dermatophyte infections (tinea) are caused by fungi that utilize keratin as a nutrient source, affecting the stratum corneum, hair, and nails. The three genera responsible are Trichophyton, Microsporum, and Epidermophyton. Clinical presentation is named by body site: tinea capitis (scalp), tinea corporis (body), tinea cruris (groin), tinea pedis (feet), tinea manuum (hands), tinea unguium/onychomycosis (nails), and tinea faciei (face).

Candida infections (candidiasis) typically affect moist areas and mucous membranes, causing conditions such as oral thrush, vulvovaginal candidiasis, intertrigo, and paronychia. Malassezia yeasts cause pityriasis versicolor (tinea versicolor), a very common condition in tropical climates characterized by hypo- or hyperpigmented patches.

In Africa, fungal skin infections are extremely common due to hot, humid climates that favor fungal growth, overcrowding, shared bathing facilities, and limited access to antifungal treatments. Tinea capitis is particularly prevalent in children, and pityriasis versicolor is one of the most common dermatological conditions across the continent.

Diagnosis is usually clinical but can be confirmed with KOH microscopy. Treatment depends on the site and extent of infection, ranging from topical antifungals for limited disease to systemic therapy for extensive, hair, or nail involvement.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 TINEA CAPITIS REQUIRES ORAL ANTIFUNGALS - Topical treatment alone does NOT work for scalp ringworm; griseofulvin or terbinafine are first-line

🔴 KOH PREPARATION IS THE KEY DIAGNOSTIC TEST - Simple, cheap, available; dissolves keratin to reveal fungal hyphae and spores

🔴 NOT ALL ROUND LESIONS ARE RINGWORM - Consider psoriasis, nummular eczema, pityriasis rosea, granuloma annulare; KOH helps differentiate

🔴 TINEA INCOGNITO - Fungal infection treated with steroids; atypical appearance; suspect when rash worsens with steroid cream

🔴 TREAT THE PORTAL OF ENTRY - Tinea pedis is the source of tinea cruris in most cases; treat feet to prevent recurrence

🔴 PITYRIASIS VERSICOLOR PIGMENT CHANGES PERSIST - Hypo/hyperpigmentation may take months to resolve after successful treatment; reassure patient

🔴 ONYCHOMYCOSIS NEEDS PROLONGED ORAL THERAPY - Nail infections require 6-12 weeks of systemic antifungals; topical alone rarely works

🔴 CONSIDER HIV IN EXTENSIVE OR REFRACTORY INFECTIONS - Widespread, severe, or treatment-resistant fungal infections may indicate immunosuppression
    `,

    epidemiology: `
GLOBAL EPIDEMIOLOGY:
• Superficial fungal infections affect 20-25% of world population
• One of the most common infections worldwide
• Prevalence increasing globally
• Affects all ages, races, and socioeconomic groups
• Dermatophytes cause majority of skin fungal infections
• Onychomycosis affects 10-12% of general population, up to 50% of elderly

AFRICAN EPIDEMIOLOGY:
• Very high burden across the continent
• Tinea capitis: Endemic; affects 10-30% of children in many African countries
• Pityriasis versicolor: Extremely common (up to 40% in some tropical populations)
• Tinea corporis and tinea cruris: Very common in adults

Contributing Factors in Africa:
• Hot, humid climate (optimal for fungal growth)
• Overcrowding and shared facilities
• Limited access to antifungal treatments
• Poverty and poor hygiene conditions
• Traditional practices (head shaving with shared instruments)
• HIV/AIDS epidemic (increases susceptibility and severity)
• Limited diagnostic facilities

TYPES BY PREVALENCE (Africa):

Children:
• Tinea capitis (most common; major public health issue)
• Tinea corporis

Adults:
• Pityriasis versicolor (extremely common)
• Tinea pedis
• Tinea cruris
• Candidal intertrigo
• Onychomycosis

CAUSATIVE ORGANISMS:

Dermatophytes:
• Trichophyton species (most common): T. rubrum, T. mentagrophytes, T. tonsurans, T. violaceum
• Microsporum species: M. canis, M. audouinii
• Epidermophyton floccosum

Yeasts:
• Candida species: C. albicans (most common), C. glabrata, C. tropicalis
• Malassezia species: M. furfur, M. globosa (cause pityriasis versicolor)

RISK FACTORS:

General:
• Hot, humid environment
• Sweating
• Occlusive clothing and footwear
• Obesity (skin fold moisture)
• Diabetes mellitus
• Immunosuppression (HIV, steroids, chemotherapy)
• Peripheral vascular disease
• Close contact with infected persons or animals
• Shared facilities (showers, swimming pools)

Site-Specific:
• Tinea pedis: Communal showers, occlusive footwear, athletes
• Tinea capitis: Children, crowded schools, shared combs/hats
• Tinea cruris: Athletes, obesity, tight clothing
• Candidiasis: Antibiotics, diabetes, immunosuppression, moisture
    `,

    clinicalPresentation: `
SYMPTOMS AND SIGNS:

DERMATOPHYTE INFECTIONS (TINEA):

TINEA CORPORIS (Body Ringworm):
• Annular (ring-shaped) plaques with raised, scaly, erythematous border
• Central clearing gives "ring" appearance
• Expanding outward
• Pruritic (itchy)
• May have multiple lesions
• Can occur anywhere on body (excluding scalp, groin, feet, nails)

TINEA CAPITIS (Scalp Ringworm):
• Most common in children 3-14 years
• Scaling and hair loss (may be patchy or diffuse)
• Black dot appearance (broken hair shafts)
• Gray patch (T. violaceum common in Africa)
• Kerion: Boggy, inflammatory mass with pustules; painful; may cause permanent scarring alopecia
• Favus: Yellow cup-shaped crusts (scutula); rare; T. schoenleinii
• Posterior cervical lymphadenopathy
• May be minimally symptomatic in carriers

TINEA CRURIS (Jock Itch):
• Erythematous, scaly plaques in groin
• Advancing, raised border
• Central clearing
• Extends to inner thighs, may involve buttocks
• Scrotum usually SPARED (unlike candida)
• Very pruritic
• Often associated with tinea pedis (same organism)

TINEA PEDIS (Athlete's Foot):
• Three patterns:
  1. Interdigital: Most common; maceration, scaling, fissuring between toes (especially 4th-5th web space)
  2. Moccasin: Diffuse scaling of soles and sides of feet; "dry type"
  3. Vesiculobullous: Vesicles and bullae on soles and instep; inflammatory
• Pruritic; may be malodorous
• Often bilateral
• Portal of entry for cellulitis

TINEA MANUUM (Hand Ringworm):
• Often unilateral ("two feet-one hand syndrome")
• Diffuse scaling of palm
• May have vesicles
• Associated with tinea pedis
• Occupational exposure possible

TINEA UNGUIUM / ONYCHOMYCOSIS (Nail Fungal Infection):
• Distal-lateral subungual: Most common; starts at nail tip; yellow-brown discoloration, thickening, subungual debris
• Proximal subungual: Starts at nail fold; seen in HIV
• Superficial white: White patches on nail surface
• Total dystrophic: Entire nail destroyed
• Usually painless unless secondary infection
• Fingernails or toenails; toenails more common

TINEA FACIEI (Face):
• Less annular due to hair growth
• Erythematous, scaly patches
• May be misdiagnosed as eczema, lupus, or seborrheic dermatitis
• Often treated incorrectly with steroids (tinea incognito)

TINEA BARBAE (Beard Area):
• Men; beard and mustache area
• Inflammatory nodules, pustules
• May resemble bacterial folliculitis

YEAST INFECTIONS:

PITYRIASIS VERSICOLOR (Tinea Versicolor):
• Malassezia yeast infection
• Very common in tropical climates
• Multiple round or oval macules
• Hypopigmented (more visible in dark skin) or hyperpigmented or pink/tan
• Fine scale (visible on scraping)
• Upper trunk, shoulders, upper arms; may extend to neck, face
• Usually asymptomatic or mildly pruritic
• Recurrent; especially in humid weather
• NOT contagious (normal skin flora)

CUTANEOUS CANDIDIASIS:

Intertrigo:
• Affects skin folds (axillae, groin, inframammary, intergluteal)
• Erythematous, moist, macerated
• Satellite pustules/papules (characteristic)
• Painful, burning
• Risk factors: Obesity, diabetes, moisture

Oral Candidiasis (Thrush):
• White plaques on oral mucosa that can be scraped off
• Erythematous base underneath
• May be painful, affect eating
• Angular cheilitis: Cracking at mouth corners
• Common in: Infants, denture wearers, immunocompromised, antibiotic use

Vulvovaginal Candidiasis:
• Thick, white "cottage cheese" discharge
• Vulvar itching, burning
• Erythema and edema
• Dysuria, dyspareunia
• Common; affects 75% of women at some point

Candidal Paronychia:
• Chronic infection of nail fold
• Swelling, erythema of proximal/lateral nail folds
• Painful
• Associated with wet work (dishwashers, cleaners)

SPECIAL PRESENTATIONS:

Tinea Incognito:
• Fungal infection modified by steroid treatment
• Atypical appearance; less scaly, less defined border
• May be extensive
• Suspect when "eczema" worsens with steroids

Majocchi Granuloma:
• Dermatophyte infection of hair follicles
• Papules, nodules, pustules
• Usually on legs; associated with shaving
• Requires oral antifungal treatment

ID Reaction (Dermatophytid):
• Allergic reaction to dermatophyte infection elsewhere
• Vesicular eruption on hands (dyshidrotic pattern)
• Occurs with inflammatory tinea (e.g., kerion)
• Not infected; treat the primary fungal infection
    `,

    diagnosis: `
DIAGNOSTIC APPROACH:

STEP 1: CLINICAL RECOGNITION

Pattern Recognition:
• Annular, scaly plaques with central clearing → Tinea corporis
• Scalp scaling with alopecia in child → Tinea capitis
• Scaly, macerated toe webs → Tinea pedis
• Groin rash sparing scrotum → Tinea cruris
• Hypo/hyperpigmented macules on trunk → Pityriasis versicolor
• Satellite pustules in skin folds → Candidiasis

STEP 2: KOH PREPARATION (Gold Standard for Diagnosis)

Technique:
1. Collect specimen from ACTIVE BORDER (not center) of lesion
2. For tinea capitis: Pluck broken hairs, scrape scalp
3. For nails: Scrape subungual debris, nail clippings
4. For pityriasis versicolor: Scrape the fine scale
5. Place specimen on glass slide
6. Add 1-2 drops of 10-20% KOH solution
7. Gently heat (optional; speeds clearing)
8. Wait 5-20 minutes (or longer for nails)
9. Examine under microscope (10x and 40x)

Findings:
• Dermatophytes: Septate, branching hyphae
• Pityriasis versicolor: "Spaghetti and meatballs" (short hyphae and round spores)
• Candida: Pseudohyphae and budding yeast cells

STEP 3: ADDITIONAL TESTS (When Needed)

Wood Lamp Examination:
• Useful for tinea capitis
• Microsporum species: Bright green fluorescence
• T. schoenleinii (favus): Dull green
• Trichophyton species: Usually NO fluorescence
• Pityriasis versicolor: Yellow-green fluorescence
• Erythrasma (bacterial): Coral-red fluorescence (differential)

Fungal Culture:
• Gold standard for species identification
• Takes 2-4 weeks (dermatophytes grow slowly)
• Needed when:
  - KOH negative but clinical suspicion high
  - Treatment failure
  - Need species identification (e.g., scalp infection source tracing)
  - Medicolegal documentation
• Sabouraud dextrose agar commonly used

Dermatophyte Test Medium (DTM):
• Color change to red indicates dermatophyte
• Simple, useful for basic facilities

Histopathology/PAS Stain:
• Rarely needed
• For difficult cases
• Biopsy with PAS (Periodic Acid-Schiff) stain shows fungal elements

STEP 4: DIFFERENTIAL DIAGNOSIS CONSIDERATION

When in Doubt:
• If KOH negative, reconsider diagnosis
• Psoriasis, eczema, and other conditions may mimic tinea
• Tinea incognito has atypical appearance
• If not responding to appropriate antifungal, reconsider

WHEN TO INVESTIGATE FURTHER:

• Recurrent or refractory infections → Consider diabetes, HIV
• Extensive or severe infections → Check for immunosuppression
• Multiple family members affected → Screen contacts
• Unusual presentation → Culture for species identification
    `,

    differentialDiagnosis: [
      'Psoriasis - Well-demarcated plaques with silvery scale; may affect same sites; extensor surfaces; nail pitting; KOH negative',
      'Nummular eczema - Coin-shaped eczematous plaques; very pruritic; often bilateral; lacks central clearing; KOH negative',
      'Pityriasis rosea - Herald patch then Christmas tree pattern on trunk; collarette scale; resolves in 6-8 weeks; KOH negative',
      'Contact dermatitis - Pattern matches exposure; may be vesicular; pruritic; history of exposure; KOH negative',
      'Seborrheic dermatitis - Scalp, face, chest; greasy yellow scale; nasolabial folds; KOH negative (but may coexist)',
      'Erythrasma - Bacterial (Corynebacterium); groin and axillae; coral-red fluorescence under Wood lamp; KOH negative',
      'Intertrigo (non-fungal) - Skin fold inflammation from friction and moisture; no satellite lesions; may be secondarily infected',
      'Granuloma annulare - Annular but no scale; smooth surface; dermis involved; KOH negative',
      'Subacute cutaneous lupus - Annular lesions but photodistributed; systemic symptoms; ANA positive',
      'Alopecia areata - Scalp hair loss but no scaling, no broken hairs; exclamation mark hairs; smooth patches',
      'Impetigo - Honey-crusted lesions; Staph/Strep infection; culture positive for bacteria',
      'Vitiligo - Depigmentation but no scale; symmetric; Wood lamp shows bright white; no response to antifungals',
      'Post-inflammatory hypopigmentation - History of preceding rash; no scale on scraping; KOH negative',
      'Secondary syphilis - May have scaly lesions; palms and soles involved; serology positive',
      'Lichen planus - Polygonal, purple, pruritic papules; Wickham striae; KOH negative'
    ],

    investigations: [
      {
        name: 'KOH Preparation (Potassium Hydroxide Mount)',
        indication: '🔴 ESSENTIAL - Gold standard for diagnosis; cheap, quick, available; confirms fungal elements (hyphae, spores)',
        availability: 'BASIC'
      },
      {
        name: 'Wood Lamp Examination',
        indication: '🟠 USEFUL - Detects fluorescence; Microsporum (green), pityriasis versicolor (yellow), erythrasma (coral-red)',
        availability: 'BASIC'
      },
      {
        name: 'Fungal Culture',
        indication: '🟠 WHEN NEEDED - Species identification; treatment failure; epidemiological tracing; takes 2-4 weeks',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Dermatophyte Test Medium (DTM)',
        indication: '🟡 ALTERNATIVE - Simple culture; color change to red indicates dermatophyte; no species identification',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose / HbA1c',
        indication: '🟠 IF RECURRENT OR EXTENSIVE - Screen for diabetes; predisposes to fungal infections',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 IF EXTENSIVE, REFRACTORY, OR UNUSUAL - Immunosuppression increases susceptibility and severity',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 BEFORE SYSTEMIC ANTIFUNGALS - Baseline before terbinafine, itraconazole, ketoconazole; monitor if prolonged use',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 BEFORE PROLONGED THERAPY - Baseline before griseofulvin (rare blood dyscrasias); terbinafine',
        availability: 'BASIC'
      },
      {
        name: 'Skin Biopsy with PAS Stain',
        indication: '🟡 RARELY NEEDED - Difficult cases; KOH-negative but high suspicion; shows fungal elements in tissue',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Nail Clipping Histology',
        indication: '🟡 FOR ONYCHOMYCOSIS - If KOH/culture negative but clinical suspicion; PAS stain confirms fungi',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: KOH preparation to visualize hyphae/spores before starting treatment',
        '⚡ DETERMINE EXTENT: Limited (topical may suffice) vs extensive (may need oral therapy)',
        '⚡ IDENTIFY SITE: Hair and nail involvement REQUIRES systemic treatment; skin may be topical or systemic',
        '⚡ CHECK FOR PREDISPOSING FACTORS: Diabetes, HIV, other immunosuppression; treat underlying condition',
        '⚡ EDUCATE ON TRANSMISSION: Personal items not shared; treat contacts if tinea capitis; good hygiene',
        '⚡ START APPROPRIATE ANTIFUNGAL: Topical for limited skin disease; oral for scalp, nails, extensive, or treatment failure',
        '⚡ ADDRESS SECONDARY BACTERIAL INFECTION: If present, treat with antibiotics before or with antifungal',
        '⚡ ADVISE ON PREVENTION: Keep areas dry; loose clothing; treat tinea pedis to prevent tinea cruris'
      ],
      
      pharmacological: [
        {
          drug: 'Clotrimazole 1% Cream',
          indication: '🔴 FIRST-LINE TOPICAL - Limited tinea corporis, tinea cruris, tinea pedis, candidiasis; cheap, widely available',
          dose: 'Apply thin layer to affected area and 2cm beyond, twice daily. Continue for 1-2 weeks after clinical clearance.',
          route: 'Topical',
          duration: 'Tinea corporis/cruris: 2-4 weeks. Tinea pedis: 4 weeks. Candidiasis: 2-4 weeks.',
          contraindications: ['Hypersensitivity to imidazoles'],
          sideEffects: ['Local irritation', 'Burning', 'Erythema (rare)'],
          alternatives: ['Miconazole', 'Ketoconazole cream', 'Terbinafine cream']
        },
        {
          drug: 'Miconazole 2% Cream',
          indication: '🔴 FIRST-LINE TOPICAL - Similar to clotrimazole; also effective for candidiasis; available over-the-counter',
          dose: 'Apply twice daily to affected area. Continue 1-2 weeks beyond clinical clearance.',
          route: 'Topical',
          duration: '2-4 weeks depending on site',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Local irritation', 'Contact dermatitis (rare)'],
          alternatives: ['Clotrimazole', 'Ketoconazole', 'Terbinafine cream']
        },
        {
          drug: 'Terbinafine 1% Cream',
          indication: '🟠 TOPICAL ALTERNATIVE - Fungicidal (vs fungistatic azoles); may be more effective; shorter treatment duration',
          dose: 'Apply once or twice daily. Tinea corporis/cruris: 1-2 weeks. Tinea pedis: 2-4 weeks.',
          route: 'Topical',
          duration: 'Shorter duration than azoles due to fungicidal action',
          contraindications: ['Hypersensitivity to allylamines'],
          sideEffects: ['Local irritation', 'Dryness', 'Rash'],
          alternatives: ['Clotrimazole', 'Miconazole']
        },
        {
          drug: 'Ketoconazole 2% Cream/Shampoo',
          indication: '🟠 FOR PITYRIASIS VERSICOLOR and seborrheic dermatitis - Also useful for other superficial fungal infections',
          dose: 'Cream: Apply once or twice daily. Shampoo: For pityriasis versicolor - apply to affected areas, leave 5-10 minutes, rinse; repeat daily for 5 days or weekly for prevention.',
          route: 'Topical',
          duration: 'Cream: 2-4 weeks. Shampoo: 5 days treatment; weekly for maintenance',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Local irritation', 'Dryness'],
          alternatives: ['Selenium sulfide shampoo for pityriasis versicolor']
        },
        {
          drug: 'Selenium Sulfide 2.5% Shampoo/Lotion',
          indication: '🟠 FOR PITYRIASIS VERSICOLOR - Cheap, effective; apply as lotion, wash off after 10-15 minutes',
          dose: 'Apply to affected areas, leave for 10-15 minutes, then wash off. Daily for 1-2 weeks, then weekly for prevention.',
          route: 'Topical',
          duration: '1-2 weeks; then monthly maintenance in recurrent cases',
          contraindications: ['Open wounds (irritating)'],
          sideEffects: ['Skin irritation', 'Discoloration of hair/skin (temporary)', 'Dryness'],
          alternatives: ['Ketoconazole shampoo']
        },
        {
          drug: 'Nystatin Cream/Ointment',
          indication: '🟠 FOR CANDIDIASIS ONLY - Does not work on dermatophytes; good for intertrigo, oral candidiasis',
          dose: 'Apply to affected areas 2-4 times daily. Oral suspension: 100,000 units/mL; swish and swallow QID.',
          route: 'Topical or oral suspension',
          duration: '1-2 weeks for skin; oral until 48 hours after symptoms resolve',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Local irritation (rare)', 'GI upset (oral)'],
          alternatives: ['Clotrimazole for candidiasis']
        },
        {
          drug: 'Whitfield Ointment (Benzoic Acid + Salicylic Acid)',
          indication: '🟡 TRADITIONAL OPTION - Cheap, available; keratolytic and antifungal; useful where other treatments unavailable',
          dose: 'Apply twice daily to affected areas.',
          route: 'Topical',
          duration: '2-4 weeks',
          contraindications: ['Large areas (salicylate absorption)', 'Children (salicylate toxicity risk)'],
          sideEffects: ['Local irritation', 'Dryness', 'Salicylate toxicity if extensive use'],
          alternatives: ['Modern azole antifungals preferred if available']
        },
        {
          drug: 'Griseofulvin',
          indication: '🔴 FIRST-LINE ORAL for TINEA CAPITIS - Requires oral therapy; fungistatic; take with fatty meal for absorption',
          dose: 'Microsize: Adults 500-1000 mg/day; Children 10-20 mg/kg/day (max 1g). Ultramicrosize: Adults 375-750 mg/day; Children 5-10 mg/kg/day. Take with fatty food.',
          route: 'PO',
          duration: 'Tinea capitis: 6-8 weeks (or 2 weeks beyond clinical cure). Tinea corporis: 2-4 weeks.',
          contraindications: ['Porphyria', 'Severe liver disease', 'Pregnancy (teratogenic)', 'Lupus'],
          sideEffects: ['Headache (common)', 'GI upset', 'Photosensitivity', 'Rash', 'Hepatotoxicity (rare)', 'Blood dyscrasias (rare)'],
          alternatives: ['Terbinafine (may be more effective for Trichophyton)']
        },
        {
          drug: 'Terbinafine (Oral)',
          indication: '🔴 FIRST-LINE for ONYCHOMYCOSIS; ALTERNATIVE for TINEA CAPITIS - Fungicidal; shorter courses than griseofulvin',
          dose: 'Adults: 250 mg once daily. Children: <20 kg: 62.5 mg/day; 20-40 kg: 125 mg/day; >40 kg: 250 mg/day.',
          route: 'PO',
          duration: 'Tinea capitis: 2-4 weeks (Trichophyton) or 4-8 weeks (Microsporum). Onychomycosis: Fingernails 6 weeks; Toenails 12 weeks.',
          contraindications: ['Chronic or active liver disease', 'Hypersensitivity'],
          sideEffects: ['GI upset', 'Taste disturbance (may persist)', 'Headache', 'Rash', 'Hepatotoxicity (rare but serious)', 'Blood dyscrasias (rare)'],
          alternatives: ['Griseofulvin for tinea capitis', 'Itraconazole for onychomycosis']
        },
        {
          drug: 'Itraconazole',
          indication: '🟠 ALTERNATIVE ORAL - Broad spectrum; good for Candida; option for onychomycosis (pulse dosing)',
          dose: 'Continuous: 200 mg daily. Pulse (onychomycosis): 200 mg twice daily for 1 week per month; repeat for 2 months (fingernails) or 3 months (toenails).',
          route: 'PO',
          duration: 'Varies by indication; onychomycosis may need 3-4 months',
          contraindications: ['Heart failure', 'Pregnancy', 'Significant liver disease', 'Drug interactions (CYP3A4)'],
          sideEffects: ['GI upset', 'Headache', 'Rash', 'Hepatotoxicity', 'Heart failure (rare)', 'Multiple drug interactions'],
          alternatives: ['Terbinafine', 'Fluconazole']
        },
        {
          drug: 'Fluconazole',
          indication: '🟠 FOR CANDIDIASIS; ALTERNATIVE for dermatophytes - Excellent for Candida; once-weekly dosing option',
          dose: 'Vulvovaginal candidiasis: 150 mg single dose. Oropharyngeal candidiasis: 100-200 mg daily for 7-14 days. Tinea/Onychomycosis: 150-300 mg once weekly.',
          route: 'PO',
          duration: 'Single dose for uncomplicated VVC; weeks to months for tinea/onychomycosis',
          contraindications: ['Severe liver disease', 'Pregnancy (high doses)', 'Drug interactions'],
          sideEffects: ['GI upset', 'Headache', 'Rash', 'Hepatotoxicity', 'QT prolongation'],
          alternatives: ['Itraconazole', 'Terbinafine']
        },
        {
          drug: 'Ketoconazole (Oral)',
          indication: '🟡 LAST RESORT - Significant hepatotoxicity risk; largely replaced by safer alternatives; still used in some resource-limited settings',
          dose: 'Adults: 200-400 mg once daily. Not recommended in children.',
          route: 'PO',
          duration: 'As short as possible; usually 2-4 weeks',
          contraindications: ['Liver disease', 'Pregnancy', 'Adrenal insufficiency', 'Concurrent hepatotoxic drugs'],
          sideEffects: ['HEPATOTOXICITY (serious - monitor LFTs)', 'Adrenal suppression', 'GI upset', 'Gynecomastia', 'Drug interactions'],
          alternatives: ['Terbinafine', 'Itraconazole', 'Fluconazole (all safer options)']
        }
      ],
      
      nonPharmacological: [
        'KEEP AFFECTED AREAS DRY: Dry thoroughly after bathing; use absorbent powder; avoid occlusive footwear',
        'WEAR LOOSE, BREATHABLE CLOTHING: Cotton undergarments; avoid tight jeans, synthetic materials that trap moisture',
        'AVOID SHARING PERSONAL ITEMS: Towels, combs, hats, shoes can transmit infection (especially tinea capitis)',
        'TREAT ALL AFFECTED CONTACTS: Screen and treat family members with tinea capitis to prevent reinfection',
        'ANTIFUNGAL SHAMPOO FOR HOUSEHOLD: In tinea capitis, all family members can use ketoconazole/selenium sulfide shampoo to reduce carriage',
        'PROPER FOOT HYGIENE: For tinea pedis - dry between toes, change socks daily, alternate shoes, use antifungal powder',
        'WEIGHT LOSS AND GLUCOSE CONTROL: For recurrent candidal intertrigo in obese/diabetic patients',
        'IMPROVE VENTILATION: Air conditioning or fans in hot climates; reduce humidity where possible',
        'DISINFECT CONTAMINATED ITEMS: Bleach soak for combs, hairbrushes; wash towels, sheets in hot water',
        'LIFESTYLE MODIFICATIONS: Reduce sweating during exercise; shower promptly after; change out of wet clothes'
      ],
      
      surgical: [
        'NAIL AVULSION: For severe onychomycosis; chemical (40% urea paste) or surgical removal; used with oral antifungals',
        'INCISION AND DRAINAGE: For kerion (inflammatory scalp ringworm) with secondary bacterial abscess; combine with oral antifungals',
        'DEBRIDEMENT: Of thickened, dystrophic nails before/during antifungal treatment; improves penetration and outcomes'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Suspected invasive fungal infection - Immunocompromised patient with extensive or deep infection',
        '🟠 URGENT REFERRAL: Kerion not responding to treatment - May need surgical drainage; risk of scarring alopecia',
        '🟠 URGENT REFERRAL: Suspicion of immunodeficiency - Extensive, refractory, or unusual fungal infections; needs HIV testing',
        '🟡 ROUTINE REFERRAL: Treatment-resistant infection - Not responding to appropriate therapy; needs culture and sensitivity',
        '🟡 ROUTINE REFERRAL: Diagnostic uncertainty - KOH negative but clinical suspicion; atypical appearance; needs dermatology input',
        '🟡 ROUTINE REFERRAL: Extensive onychomycosis - May need prolonged oral therapy with monitoring',
        '🟡 ROUTINE REFERRAL: Recurrent infections despite treatment - May need investigation for underlying cause'
      ],
      
      monitoring: [
        'CLINICAL RESPONSE: Expect improvement within 2-4 weeks of appropriate treatment; complete clearance may take longer',
        'KOH MICROSCOPY: Can repeat at end of treatment to confirm mycological cure (especially for tinea capitis)',
        'LIVER FUNCTION TESTS: Baseline and periodic monitoring for oral terbinafine, itraconazole, ketoconazole if prolonged use',
        'COMPLETE BLOOD COUNT: Baseline for griseofulvin (rare blood dyscrasias); not routinely needed',
        'SIDE EFFECT MONITORING: Taste disturbance (terbinafine), GI upset, headache; serious reactions rare',
        'TINEA CAPITIS: Treat until clinical cure AND microscopy/culture negative; usually 6-8 weeks',
        'ONYCHOMYCOSIS: Nails grow slowly; full cure takes 6-12 months after completing treatment; be patient',
        'RECURRENCE: Monitor for recurrence; address predisposing factors; may need maintenance therapy'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

GENERAL PRINCIPLES:

Response Assessment:
• Topical treatment: Review at 2-4 weeks
• Oral treatment: Review at 2-4 weeks initially
• Tinea capitis: Review at 4 weeks; continue until mycologically cured
• Onychomycosis: Review at end of treatment course; final assessment at 6-12 months (nail growth)

SITE-SPECIFIC FOLLOW-UP:

Tinea Corporis/Cruris:
• Usually responds within 2-4 weeks
• If not responding: Confirm diagnosis (repeat KOH), check compliance, consider oral therapy
• Prevent recurrence by treating tinea pedis

Tinea Pedis:
• May need longer treatment (4-6 weeks)
• High recurrence rate
• Advise ongoing prevention: dry feet, antifungal powder, proper footwear

Tinea Capitis:
• MUST treat until mycologically cured (negative microscopy/culture)
• Usually 6-8 weeks minimum
• Repeat KOH/culture at end of clinical treatment
• Screen and treat household contacts
• Watch for kerion development

Onychomycosis:
• Complete treatment course even if no visible improvement
• Nail takes 6-12 months to grow out fully
• Assess at 3-6 months after completing treatment
• Cure rates: 70-80% with terbinafine; lower with others
• Relapse and reinfection common; advise prevention

Pityriasis Versicolor:
• Often clears quickly but PIGMENT CHANGES PERSIST
• Educate that hypo/hyperpigmentation may take months to resolve
• High recurrence rate in tropical climates
• Maintenance therapy: Monthly ketoconazole shampoo or selenium sulfide

TREATMENT FAILURE:

If Not Responding:
1. Confirm diagnosis: Repeat KOH, consider culture
2. Check compliance: Ask about application technique, duration
3. Consider resistant organism: Culture may identify species
4. Look for predisposing factors: Diabetes, HIV, steroid use
5. Consider alternative diagnosis: Psoriasis, eczema, etc.
6. Step up therapy: Topical to oral; longer duration

PREVENTION OF RECURRENCE:

General Measures:
• Keep areas dry
• Loose, breathable clothing
• Treat other affected sites (especially tinea pedis)
• Good hygiene
• Treat close contacts (tinea capitis)

Maintenance Therapy (for Frequent Recurrence):
• Tinea pedis: Weekly topical antifungal
• Pityriasis versicolor: Monthly antifungal shampoo
• Tinea cruris: Antifungal powder to groin daily
      `
    },

    redFlags: [
      '🔴 CRITICAL: Deep or invasive fungal infection in immunocompromised - May spread systemically; needs urgent specialist care',
      '🔴 CRITICAL: Kerion with systemic symptoms - Painful boggy mass with fever; may need drainage and IV antibiotics if secondary infection',
      '🟠 URGENT: Widespread or extensive fungal infection - Consider underlying immunodeficiency; test for HIV and diabetes',
      '🟠 URGENT: Infection not responding to appropriate treatment - May need culture, alternative therapy, or reassessment of diagnosis',
      '🟠 URGENT: Proximal white subungual onychomycosis - Associated with HIV; screen for immunodeficiency',
      '🟡 CAUTION: Tinea incognito (steroid-modified fungal infection) - May be extensive and atypical; stop steroids, start antifungals',
      '🟡 CAUTION: Recurrent or chronic fungal infections - Screen for diabetes and HIV; assess for other predisposing factors',
      '🟡 CAUTION: Majocchi granuloma - Deep follicular infection; requires oral antifungal therapy',
      '🟡 CAUTION: Signs of secondary bacterial infection - Increased pain, purulence, spreading erythema; add antibiotics'
    ],

    complications: [
      'Secondary bacterial infection - Especially in tinea pedis; portal of entry for cellulitis; may need antibiotics',
      'Kerion - Severe inflammatory tinea capitis; can cause permanent scarring alopecia if not treated promptly',
      'Scarring alopecia - From severe or untreated tinea capitis; permanent hair loss',
      'ID reaction (dermatophytid) - Allergic vesicular reaction distant from fungal infection; treat primary infection',
      'Nail dystrophy - Permanent nail damage from chronic onychomycosis',
      'Spread to other body sites - Autoinoculation; one site infects another',
      'Transmission to contacts - Especially tinea capitis in families and schools',
      'Treatment failure and resistance - May occur with inadequate treatment; rare true resistance',
      'Drug-induced hepatotoxicity - From oral ketoconazole (highest risk), itraconazole, terbinafine; monitor LFTs',
      'Psychosocial impact - Disfigurement, embarrassment, social stigma especially with visible lesions'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Most superficial fungal infections have excellent prognosis with appropriate treatment
• Cure rates high for dermatophyte infections of skin
• Hair and nail infections more challenging; require longer treatment

BY CONDITION:

Tinea Corporis/Cruris:
• Cure rate >90% with appropriate topical treatment
• Higher cure rates with oral therapy
• Recurrence possible if predisposing factors not addressed

Tinea Pedis:
• Responds well to treatment
• High recurrence rate (up to 70% within months)
• Requires ongoing preventive measures
• Complete cure difficult; "control" rather than "cure" mindset

Tinea Capitis:
• Excellent prognosis if treated appropriately with oral antifungals
• Cure rates >90% with griseofulvin or terbinafine
• Kerion may cause permanent scarring alopecia
• Untreated may spread and cause epidemics

Onychomycosis:
• Most challenging superficial fungal infection
• Cure rates: Terbinafine 70-80%; Itraconazole 60-70%
• Relapse rate 10-25%
• Reinfection common
• Complete cure may not be achievable in all patients
• May need to accept improvement rather than cure in elderly

Pityriasis Versicolor:
• Responds well to treatment
• PIGMENT CHANGES TAKE MONTHS TO RESOLVE (not treatment failure)
• Very high recurrence rate in tropical climates (up to 80%)
• May need maintenance therapy long-term
• Not curable; controllable

Candidiasis:
• Responds well to treatment
• Oral candidiasis: Excellent response; recurrence indicates immunodeficiency
• Vulvovaginal candidiasis: Most respond to single-dose treatment; recurrent VVC in 5-8%
• Intertrigo: Responds well; recurrence if predisposing factors persist

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early treatment
• Correct diagnosis (KOH-confirmed)
• Appropriate therapy (oral when needed)
• Good compliance
• Addressing predisposing factors
• Intact immune system

Worse Outcomes:
• Delayed treatment
• Incorrect diagnosis
• Inadequate therapy
• Poor compliance
• Persistent predisposing factors (diabetes, obesity)
• Immunosuppression (HIV)
• Nail involvement
    `,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

GENERAL MEASURES:

Personal Hygiene:
• Keep skin clean and dry
• Dry thoroughly after bathing, especially between toes and in skin folds
• Change underwear and socks daily
• Do not share personal items (towels, combs, razors, shoes)

Clothing and Footwear:
• Wear loose-fitting, breathable clothing
• Cotton undergarments and socks
• Avoid prolonged wearing of wet or sweaty clothing
• Change out of wet swimwear promptly
• Wear sandals or open footwear when possible
• Use moisture-wicking socks for athletics

Environmental:
• Wear sandals in communal showers, locker rooms, swimming pools
• Keep living areas clean and well-ventilated
• Reduce humidity where possible

SITE-SPECIFIC PREVENTION:

Tinea Pedis (Athlete's Foot):
• Dry between toes thoroughly
• Use antifungal powder on feet and in shoes
• Alternate shoes (allow to dry completely)
• Wear breathable footwear
• Treat existing infection promptly to prevent spread

Tinea Cruris (Jock Itch):
• Treat tinea pedis first (source in most cases)
• Use separate towel for feet and groin
• Wear loose cotton underwear
• Antifungal powder to groin
• Dry groin area before putting on underwear

Tinea Capitis (Scalp Ringworm):
• Do not share combs, brushes, hats, pillows
• Treat all affected household members
• Screen school contacts during outbreaks
• Clean/disinfect barber tools between customers
• Antifungal shampoo for household members during treatment

Pityriasis Versicolor:
• Monthly antifungal shampoo (ketoconazole or selenium sulfide) for prevention
• Keep skin cool and dry
• Cannot be completely prevented in susceptible individuals in tropical climates

Candidiasis:
• Avoid prolonged antibiotic use when possible
• Good glucose control in diabetics
• Keep skin folds dry
• Lose weight if obese
• Avoid douching (disrupts vaginal flora)

Onychomycosis:
• Treat tinea pedis promptly
• Trim nails properly
• Avoid nail trauma
• Do not share nail clippers
• Disinfect pedicure tools

PUBLIC HEALTH MEASURES (Tinea Capitis):

Schools:
• Screen children during outbreaks
• Treat affected children
• Educate on not sharing personal items
• Do not exclude from school once on treatment

Communities:
• Health education on transmission and prevention
• Improve access to antifungal treatments
• Train traditional barbers on hygiene
• Screen and treat in endemic areas
    `,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT ARE FUNGAL SKIN INFECTIONS?

Fungal infections of the skin, hair, and nails are very common conditions caused by tiny organisms called fungi. They are often called "ringworm" because of the ring shape they can make on the skin, but there are no actual worms involved.

TYPES YOU MAY HAVE:

• Ringworm (body): Round, red, scaly patches with a clear center
• Scalp ringworm: Scaly patches on the scalp with hair loss (common in children)
• Jock itch: Itchy red rash in the groin area
• Athlete's foot: Itchy, cracked skin between the toes
• Nail fungus: Thick, discolored, crumbly nails
• Pityriasis versicolor: Light or dark patches on the body (usually back, chest, shoulders)

HOW DID I GET THIS?

Fungal infections spread through:
• Direct contact with infected people
• Sharing personal items (combs, towels, shoes)
• Walking barefoot in wet areas (showers, pools)
• Contact with infected animals (ringworm)
• The fungus that causes pityriasis versicolor normally lives on everyone's skin; it just overgrows in some people

TREATMENT:

Creams/Shampoos (Topical):
• Apply to the affected area AND a bit beyond
• Continue for 1-2 weeks after it looks clear
• Common treatments: Clotrimazole, Miconazole, Ketoconazole

Tablets (Oral):
• Needed for scalp ringworm, nail infections, or extensive skin infections
• Take for the full course even if it looks better
• Take griseofulvin with a fatty meal for better absorption
• Common treatments: Griseofulvin, Terbinafine

IMPORTANT POINTS:

For Scalp Ringworm:
• Creams DO NOT work for scalp infections - tablets are essential
• Treatment usually takes 6-8 weeks
• All family members should use antifungal shampoo
• Do not share combs, hats, or pillows

For Nail Infections:
• Treatment takes many months (nails grow slowly)
• Complete the full course even if the nail still looks abnormal
• It may take 6-12 months to see the final result

For Pityriasis Versicolor:
• The COLOR CHANGE can take MONTHS to fade after successful treatment
• This is normal and does not mean treatment failed
• The infection often comes back, especially in hot weather
• Monthly use of antifungal shampoo can help prevent recurrence

PREVENTING SPREAD AND RECURRENCE:

• Keep your skin clean and dry
• Dry thoroughly between toes and in skin folds
• Wear loose, cotton clothing
• Do not share towels, combs, or shoes
• Treat athlete's foot to prevent jock itch
• Change socks daily
• Wear sandals in public showers and swimming pools

WHEN TO SEE A DOCTOR AGAIN:

• Not improving after 2-4 weeks of treatment
• Getting worse despite treatment
• Spreading to new areas
• Developing pus or increased pain (bacterial infection)
• Recurrent infections

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Please consult a healthcare professional for proper diagnosis and treatment.

QUESTIONS TO ASK YOUR DOCTOR:
• Do I need cream or tablets?
• How long should I continue treatment?
• Should my family members be treated?
• How can I prevent this from coming back?
• When will the color of my skin return to normal? (for pityriasis versicolor)
    `,

    references: [
      'Hay RJ, Johns NE, Williams HC, et al. The global burden of skin disease in 2010: an analysis of the prevalence and impact of skin conditions. Journal of Investigative Dermatology. 2014;134(6):1527-1534.',
      'Ameen M. Epidemiology of superficial fungal infections. Clinics in Dermatology. 2010;28(2):197-201.',
      'Elewski BE, Hay RJ. Update on the management of onychomycosis: highlights of the third annual international summit on cutaneous antifungal therapy. Clinical Infectious Diseases. 1996;23(2):305-313.',
      'Fuller LC, Child FJ, Midgley G, Higgins EM. Diagnosis and management of scalp ringworm. BMJ. 2003;326(7388):539-541.',
      'Gupta AK, Foley KA, Versteeg SG. New antifungal agents and new formulations against dermatophytes. Mycopathologia. 2017;182(1-2):127-141.',
      'Havlickova B, Czaika VA, Friedrich M. Epidemiological trends in skin mycoses worldwide. Mycoses. 2008;51(Suppl 4):2-15.',
      'Karimkhani C, Dellavalle RP, Coffeng LE, et al. Global skin disease morbidity and mortality: an update from the Global Burden of Disease Study 2013. JAMA Dermatology. 2017;153(5):406-412.',
      'Nenoff P, Kruger C, Ginter-Hanselmayer G, Tietz HJ. Mycology - an update. Part 1: Dermatomycoses: causative agents, epidemiology and pathogenesis. Journal of the German Society of Dermatology. 2014;12(3):188-210.',
      'Pappas PG, Kauffman CA, Andes DR, et al. Clinical Practice Guideline for the Management of Candidiasis: 2016 Update by the Infectious Diseases Society of America. Clinical Infectious Diseases. 2016;62(4):e1-e50.',
      'Seebacher C, Bouchara JP, Mignon B. Updates on the epidemiology of dermatophyte infections. Mycopathologia. 2008;166(5-6):335-352.',
      'Sigurgeirsson B, Baran R. The prevalence of onychomycosis in the global population: a literature study. Journal of the European Academy of Dermatology and Venereology. 2014;28(11):1480-1491.',
      'World Health Organization. Epidemiology and management of common skin diseases in children in developing countries. WHO/FCH/CAH/05.12. Geneva: WHO; 2005.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 61. URINARY TRACT INFECTION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '61',
    name: 'Urinary Tract Infection',
    slug: 'urinary-tract-infection',
    specialty: 'Infectious Disease',
    bodySystem: 'Renal',
    icdCode: 'N39.0',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Urinary tract infection (UTI) is one of the most common bacterial infections worldwide, affecting an estimated 150 million people annually. UTIs encompass infections of any part of the urinary system, including the kidneys (pyelonephritis), ureters, bladder (cystitis), and urethra (urethritis). Women are disproportionately affected, with approximately 50-60% experiencing at least one UTI in their lifetime, while recurrent UTIs affect 20-30% of women.

The pathophysiology involves ascending infection from the periurethral area, with Escherichia coli responsible for 75-95% of uncomplicated cases. Other common pathogens include Klebsiella pneumoniae, Proteus mirabilis, Enterococcus faecalis, and Staphylococcus saprophyticus (particularly in young sexually active women). Complicated UTIs may involve a broader spectrum of organisms including Pseudomonas aeruginosa and resistant gram-negative bacteria.

In African settings, UTIs represent a significant healthcare burden with unique challenges including limited access to diagnostic facilities, high rates of antimicrobial resistance due to antibiotic misuse, increased prevalence of complicated UTIs from untreated conditions, and association with endemic diseases such as schistosomiasis and HIV. Resource limitations often necessitate empiric treatment, making knowledge of local resistance patterns crucial. The intersection of UTI with pregnancy is particularly important given high maternal mortality rates in the region.

Early recognition and appropriate treatment prevent progression to pyelonephritis, urosepsis, and renal scarring. In pregnancy, untreated bacteriuria significantly increases risks of preterm birth and low birth weight. Antimicrobial stewardship is essential to preserve antibiotic efficacy in resource-limited settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Always obtain urine culture before antibiotics in complicated UTI, pregnancy, treatment failure, or recurrent infections

🔴 CRITICAL POINT 2 - Pyelonephritis requires 7-14 days of treatment; inadequate duration leads to relapse and resistance

🔴 CRITICAL POINT 3 - All pregnant women require screening and treatment for asymptomatic bacteriuria to prevent preterm birth

🔴 CRITICAL POINT 4 - Fever with UTI symptoms suggests pyelonephritis or urosepsis - assess for sepsis criteria immediately

🟠 CRITICAL POINT 5 - Nitrofurantoin is first-line for uncomplicated cystitis but contraindicated in pyelonephritis (poor tissue penetration)

🟠 CRITICAL POINT 6 - Consider schistosomiasis in endemic areas presenting with dysuria and hematuria

🟠 CRITICAL POINT 7 - Catheter-associated UTI requires catheter removal or exchange before antibiotic therapy

🟡 CRITICAL POINT 8 - Fluoroquinolone resistance exceeds 50% in many African regions - reserve for pyelonephritis when susceptibility confirmed

🟡 CRITICAL POINT 9 - Recurrent UTI (≥3/year) warrants investigation for structural abnormalities and consideration of prophylaxis`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 150-250 million UTI cases annually worldwide
• Second most common infection after respiratory tract infections
• Women: 50-60% lifetime risk; 20-30% recurrence rate
• Men: 12% lifetime risk, increasing with age due to prostatic disease
• Accounts for 1-3% of all primary care consultations
• Healthcare-associated UTIs: 40% of all nosocomial infections
• Catheter-associated UTI: 70-80% of hospital-acquired UTIs

AFRICAN EPIDEMIOLOGY:
• Prevalence varies by region: 15-45% in various studies
• Higher rates in pregnant women: 10-20% (vs 2-7% globally)
• Nigeria: UTI prevalence 14-35% in different populations
• East Africa: High rates of ESBL-producing organisms (30-50%)
• South Africa: Increasing fluoroquinolone resistance (>40%)
• HIV-positive patients: 2-3x higher UTI rates
• Schistosomiasis co-infection common in endemic regions
• Limited surveillance leads to underreporting

RISK FACTORS:
• Female sex (short urethra, proximity to anus)
• Sexual activity (honeymoon cystitis)
• Pregnancy (ureteral dilation, urinary stasis)
• Menopause (estrogen deficiency, vaginal atrophy)
• Diabetes mellitus (glucosuria, immune dysfunction)
• Urinary catheterization
• Structural abnormalities (vesicoureteral reflux, obstruction)
• Prior UTI history
• Immunosuppression (HIV, organ transplant)
• Incomplete bladder emptying
• Poor hygiene and sanitation
• Lack of access to clean water`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

UNCOMPLICATED CYSTITIS (Lower UTI):
• Dysuria (painful urination) - most common symptom
• Urinary frequency and urgency
• Suprapubic pain or discomfort
• Gross hematuria (30% of cases)
• Cloudy or malodorous urine
• Usually afebrile
• No flank pain or systemic symptoms

ACUTE PYELONEPHRITIS (Upper UTI):
• Fever (>38°C) - hallmark feature
• Flank pain and costovertebral angle tenderness
• Nausea and vomiting
• Rigors and chills
• May have lower UTI symptoms
• Systemic illness appearance
• Tachycardia, hypotension in severe cases

COMPLICATED UTI:
• Symptoms of underlying condition
• May have atypical or minimal symptoms
• Higher risk of treatment failure
• Associated with structural/functional abnormalities
• Includes catheter-associated, pregnancy, male UTI

SPECIAL POPULATIONS:

Elderly:
• Altered mental status may be only symptom
• Absence of fever common
• Non-specific symptoms: weakness, falls, confusion
• Asymptomatic bacteriuria common (do not treat unless symptomatic)

Children:
• Infants: Fever, irritability, poor feeding, failure to thrive
• Toddlers: Abdominal pain, new-onset incontinence
• Older children: Similar to adult symptoms

Pregnant Women:
• May be asymptomatic (asymptomatic bacteriuria)
• Higher risk of pyelonephritis
• Preterm labor association

PHYSICAL EXAMINATION:
• Vital signs: Fever, tachycardia, hypotension suggest complicated infection
• Abdominal exam: Suprapubic tenderness in cystitis
• Costovertebral angle tenderness: Suggests pyelonephritis
• Pelvic exam in women: Rule out vaginitis, STI
• Prostate exam in men: Assess for prostatitis
• General: Assess hydration status, signs of sepsis`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL ASSESSMENT
• History: Symptoms, duration, prior UTIs, risk factors
• Physical examination: Vitals, abdominal, flank, pelvic/prostate exam
• Classify as uncomplicated vs complicated UTI
• Assess for sepsis criteria if febrile

STEP 2: URINALYSIS (ESSENTIAL)
• Dipstick analysis available at BASIC level
• Leukocyte esterase: Sensitivity 75-96% for pyuria
• Nitrites: Specific (>90%) but less sensitive (35-85%)
• Both negative: UTI unlikely (NPV >95%)
• Blood: May be present, not specific
• Microscopy if available: WBC >10/HPF suggests infection

STEP 3: URINE CULTURE (WHEN INDICATED)
• Indications: Complicated UTI, pregnancy, treatment failure, recurrence, male UTI
• Clean-catch midstream specimen
• Catheter specimen if unable to void
• Significant bacteriuria: ≥10⁵ CFU/mL (symptomatic: ≥10³ CFU/mL may be significant)
• Identifies organism and susceptibility

STEP 4: ADDITIONAL INVESTIGATIONS (SELECTED CASES)
• Blood cultures: If sepsis suspected, pyelonephritis
• Renal function tests: Complicated UTI, pyelonephritis
• Imaging: Suspected obstruction, abscess, treatment failure
• STI screening: Sexually active with urethritis symptoms

DIAGNOSTIC CRITERIA:

Uncomplicated Cystitis:
• Symptoms + positive urinalysis sufficient
• Culture not routinely required in women

Pyelonephritis:
• Fever + flank pain + positive urine
• Always obtain culture before treatment

Asymptomatic Bacteriuria:
• ≥10⁵ CFU/mL in 2 consecutive specimens (women)
• ≥10⁵ CFU/mL in single specimen (men)
• Only treat in pregnancy or before urologic procedures

CLASSIFICATION:

Uncomplicated UTI:
• Healthy, non-pregnant women
• No structural or functional abnormalities
• No instrumentation

Complicated UTI:
• Male sex
• Pregnancy
• Diabetes mellitus
• Immunosuppression
• Urinary catheter
• Structural abnormality
• Recent instrumentation
• Hospital-acquired
• Recent antibiotic use
• Treatment failure`,

    differentialDiagnosis: [
      'Vulvovaginitis - Vaginal discharge, pruritus, external dysuria; wet mount shows organisms; no pyuria',
      'Sexually transmitted infections (Chlamydia, Gonorrhea) - Urethral discharge, sexual history; NAAT testing positive',
      'Urethritis - Similar symptoms but often with discharge; pyuria without bacteriuria on culture',
      'Interstitial cystitis/Painful bladder syndrome - Chronic symptoms, negative cultures, bladder wall findings on cystoscopy',
      'Overactive bladder - Frequency and urgency without dysuria; negative urinalysis',
      'Prostatitis - Male with perineal pain, obstructive symptoms; tender prostate on exam',
      'Pelvic inflammatory disease - Lower abdominal pain, cervical motion tenderness, vaginal discharge',
      'Nephrolithiasis - Colicky flank pain, hematuria; stone visible on imaging',
      'Acute appendicitis - Right lower quadrant pain, may have pyuria if appendix near ureter',
      'Schistosomiasis (urinary) - Endemic area, terminal hematuria; eggs in urine',
      'Renal tuberculosis - Chronic symptoms, sterile pyuria; AFB in urine',
      'Bladder cancer - Older patients, painless hematuria; cystoscopy diagnostic',
      'Radiation cystitis - History of pelvic radiation; cystoscopy shows changes',
      'Chemical cystitis - Exposure to irritants; negative cultures',
      'Atrophic vaginitis - Postmenopausal, vaginal dryness; responds to estrogen'
    ],

    investigations: [
      {
        name: 'Urine Dipstick Analysis',
        indication: '🔴 ESSENTIAL - First-line screening for all suspected UTIs; leukocyte esterase and nitrites',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis with Microscopy',
        indication: '🔴 ESSENTIAL - Confirms pyuria (WBC >10/HPF), bacteriuria, hematuria; superior to dipstick alone',
        availability: 'BASIC'
      },
      {
        name: 'Urine Culture and Sensitivity',
        indication: '🔴 ESSENTIAL - Required for complicated UTI, pregnancy, recurrence, treatment failure; guides antibiotic choice',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Assess for leukocytosis in pyelonephritis; anemia in chronic infection',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and Urea',
        indication: '🟠 IMPORTANT - Assess renal function in pyelonephritis, complicated UTI, or obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose / HbA1c',
        indication: '🟠 IMPORTANT - Screen for diabetes in recurrent UTI or complicated presentations',
        availability: 'BASIC'
      },
      {
        name: 'Blood Cultures (2 sets)',
        indication: '🟠 IMPORTANT - Obtain before antibiotics in suspected urosepsis or pyelonephritis with sepsis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'C-Reactive Protein',
        indication: '🟡 WHEN AVAILABLE - Elevated in pyelonephritis; helps distinguish upper from lower UTI',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Procalcitonin',
        indication: '🟡 WHEN AVAILABLE - Higher specificity for bacterial infection; helps guide antibiotic duration',
        availability: 'ADVANCED'
      },
      {
        name: 'Renal Ultrasound',
        indication: '🟠 IMPORTANT - Suspected obstruction, abscess, treatment failure, recurrent UTI; first-line imaging',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Abdomen/Pelvis with Contrast',
        indication: '🟡 WHEN AVAILABLE - Complicated pyelonephritis, suspected abscess, stone evaluation',
        availability: 'ADVANCED'
      },
      {
        name: 'HIV Test',
        indication: '🟠 IMPORTANT - Screen in recurrent or atypical UTI; HIV increases UTI risk',
        availability: 'BASIC'
      },
      {
        name: 'Pregnancy Test (β-hCG)',
        indication: '🔴 ESSENTIAL - All women of reproductive age; changes management significantly',
        availability: 'BASIC'
      },
      {
        name: 'STI Screening (NAAT for Chlamydia/Gonorrhea)',
        indication: '🟠 IMPORTANT - Sexually active patients with urethritis symptoms or purulent discharge',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urine for Schistosoma Eggs',
        indication: '🟠 IMPORTANT - Endemic areas with hematuria; terminal urine specimen midday',
        availability: 'BASIC'
      },
      {
        name: 'Post-Void Residual (Bladder Scan)',
        indication: '🟡 WHEN AVAILABLE - Recurrent UTI, suspected incomplete emptying, neurogenic bladder',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Determine if uncomplicated cystitis, pyelonephritis, or urosepsis',
        '⚡ VITAL SIGNS: Check temperature, heart rate, blood pressure, respiratory rate',
        '⚡ SEPSIS SCREEN: If fever >38°C with UTI symptoms, evaluate for sepsis criteria (qSOFA)',
        '⚡ OBTAIN SAMPLES: Collect urine for analysis and culture BEFORE starting antibiotics in complicated cases',
        '⚡ PREGNANCY TEST: All women of reproductive age before prescribing antibiotics',
        '⚡ HYDRATION: Encourage oral fluids (2-3 L/day) if tolerating; IV fluids if vomiting or septic',
        '⚡ ANALGESIA: Paracetamol for fever and pain; consider phenazopyridine for dysuria if available',
        '⚡ CATHETER ASSESSMENT: If catheterized, consider removal or exchange before treating CAUTI',
        '⚡ START EMPIRIC ANTIBIOTICS: Based on local resistance patterns and UTI classification',
        '⚡ DISPOSITION: Uncomplicated cystitis - outpatient; Pyelonephritis without sepsis - consider outpatient; Sepsis/vomiting - admit'
      ],
      
      pharmacological: [
        {
          drug: 'Nitrofurantoin',
          indication: '🔴 FIRST-LINE - Uncomplicated cystitis; excellent urinary concentration; low resistance rates',
          dose: 'Adults: 100mg PO twice daily for 5 days (macrocrystalline) OR 50-100mg PO four times daily for 7 days. NOT for pyelonephritis (poor tissue penetration). Avoid if CrCl <30 mL/min.',
          route: 'PO',
          duration: '5-7 days for cystitis',
          contraindications: ['CrCl <30 mL/min', 'G6PD deficiency', 'First trimester pregnancy (relative)', 'Term pregnancy (38+ weeks)'],
          sideEffects: ['GI upset', 'Headache', 'Pulmonary toxicity (long-term)', 'Peripheral neuropathy'],
          alternatives: ['Fosfomycin', 'Trimethoprim-sulfamethoxazole']
        },
        {
          drug: 'Trimethoprim-Sulfamethoxazole (TMP-SMX)',
          indication: '🔴 FIRST-LINE - Uncomplicated cystitis if local resistance <20%; also for pyelonephritis',
          dose: 'Cystitis: 160/800mg (DS) PO twice daily for 3 days. Pyelonephritis: Same dose for 7-14 days. Pediatric: 4-6mg TMP/kg/dose twice daily.',
          route: 'PO',
          duration: '3 days for cystitis; 7-14 days for pyelonephritis',
          contraindications: ['Sulfa allergy', 'G6PD deficiency', 'First trimester pregnancy', 'Severe renal impairment'],
          sideEffects: ['Rash', 'GI upset', 'Hyperkalemia', 'Stevens-Johnson syndrome (rare)', 'Bone marrow suppression'],
          alternatives: ['Nitrofurantoin', 'Ciprofloxacin']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟠 SECOND-LINE - Reserve for pyelonephritis or confirmed susceptibility; high resistance in many African settings',
          dose: 'Cystitis: 250-500mg PO twice daily for 3 days. Pyelonephritis: 500-750mg PO twice daily for 7 days. Severe/IV: 400mg IV every 12 hours.',
          route: 'PO / IV',
          duration: '3 days (cystitis); 7-14 days (pyelonephritis)',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Children <18 years (relative)', 'QT prolongation', 'Tendon disorders'],
          sideEffects: ['GI upset', 'Tendinopathy/rupture', 'QT prolongation', 'CNS effects', 'Photosensitivity'],
          alternatives: ['Levofloxacin', 'Ceftriaxone']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 FIRST-LINE - Severe pyelonephritis, urosepsis, or when oral therapy not feasible',
          dose: 'Adults: 1-2g IV/IM once daily. Children: 50-75mg/kg/day (max 2g). Transition to oral when clinically improved.',
          route: 'IV / IM',
          duration: '10-14 days total (can step down to oral)',
          contraindications: ['Cephalosporin allergy', 'Severe penicillin allergy (cross-reactivity)'],
          sideEffects: ['Diarrhea', 'Rash', 'Biliary sludge', 'C. difficile colitis'],
          alternatives: ['Gentamicin + Ampicillin', 'Piperacillin-tazobactam']
        },
        {
          drug: 'Gentamicin',
          indication: '🟠 ALTERNATIVE - Severe UTI/pyelonephritis; often combined with ampicillin for broader coverage',
          dose: 'Adults: 5-7mg/kg IV once daily OR 1.5mg/kg every 8 hours. Adjust for renal function. Monitor levels if available.',
          route: 'IV / IM',
          duration: '7-14 days (often transition to oral after improvement)',
          contraindications: ['Myasthenia gravis', 'Pre-existing hearing loss', 'Severe renal impairment (use with caution)'],
          sideEffects: ['Nephrotoxicity', 'Ototoxicity', 'Neuromuscular blockade'],
          alternatives: ['Amikacin', 'Ceftriaxone']
        },
        {
          drug: 'Amoxicillin-Clavulanate',
          indication: '🟠 ALTERNATIVE - Complicated cystitis, pregnancy (second-line); covers broader spectrum',
          dose: 'Adults: 500/125mg PO three times daily OR 875/125mg PO twice daily. Children: 25-45mg/kg/day in divided doses.',
          route: 'PO',
          duration: '5-7 days (cystitis); 10-14 days (pyelonephritis)',
          contraindications: ['Penicillin allergy', 'History of cholestatic jaundice with amoxicillin-clavulanate'],
          sideEffects: ['Diarrhea', 'Rash', 'Hepatotoxicity', 'Candidiasis'],
          alternatives: ['Cefuroxime', 'Ceftriaxone']
        },
        {
          drug: 'Fosfomycin',
          indication: '🟠 ALTERNATIVE - Single-dose therapy for uncomplicated cystitis; useful for ESBL producers',
          dose: 'Adults: 3g PO single dose (dissolve in water). May repeat in 48-72 hours if needed. Not for pyelonephritis.',
          route: 'PO',
          duration: 'Single dose',
          contraindications: ['Severe renal impairment (CrCl <10)'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache', 'Vaginitis'],
          alternatives: ['Nitrofurantoin', 'TMP-SMX']
        },
        {
          drug: 'Cefixime',
          indication: '🟠 ALTERNATIVE - Oral option for pyelonephritis step-down or complicated cystitis',
          dose: 'Adults: 400mg PO once daily OR 200mg twice daily. Children: 8mg/kg/day once daily or divided.',
          route: 'PO',
          duration: '7-14 days',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Nausea', 'Headache'],
          alternatives: ['Ciprofloxacin', 'Amoxicillin-clavulanate']
        },
        {
          drug: 'Cephalexin',
          indication: '🟠 SECOND-LINE - Pregnancy-safe option for cystitis; less effective against gram-negatives',
          dose: 'Adults: 500mg PO twice to four times daily. Children: 25-50mg/kg/day in divided doses.',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Cephalosporin/severe penicillin allergy'],
          sideEffects: ['GI upset', 'Rash', 'Vaginitis'],
          alternatives: ['Amoxicillin-clavulanate', 'Nitrofurantoin']
        },
        {
          drug: 'Phenazopyridine',
          indication: '🟡 ADJUNCT - Urinary analgesic for symptomatic relief of dysuria',
          dose: 'Adults: 200mg PO three times daily. Maximum 2 days with antibiotics.',
          route: 'PO',
          duration: '1-2 days only',
          contraindications: ['Renal insufficiency', 'G6PD deficiency', 'Hepatic disease'],
          sideEffects: ['Orange discoloration of urine', 'Headache', 'GI upset'],
          alternatives: ['NSAIDs', 'Paracetamol']
        }
      ],
      
      nonPharmacological: [
        'HYDRATION: Encourage 2-3 liters of fluid daily to promote urinary flushing',
        'VOID FREQUENTLY: Urinate every 2-3 hours; complete bladder emptying important',
        'POST-COITAL VOIDING: Urinate within 30 minutes after sexual intercourse',
        'HYGIENE: Front-to-back wiping; avoid irritating products in genital area',
        'CRANBERRY PRODUCTS: May help prevent recurrence; limited evidence for treatment',
        'AVOID BLADDER IRRITANTS: Limit caffeine, alcohol, spicy foods during acute infection',
        'WARM COMPRESS: Apply to suprapubic area for comfort',
        'APPROPRIATE CATHETER CARE: If indwelling catheter, maintain closed system; daily meatal care',
        'REMOVE/REPLACE CATHETER: Essential step in managing catheter-associated UTI',
        'ESTROGEN THERAPY: Consider topical estrogen for postmenopausal women with recurrent UTI'
      ],
      
      surgical: [
        'PERCUTANEOUS NEPHROSTOMY: For obstructed infected kidney (pyonephrosis) - urgent drainage required',
        'URETERAL STENT PLACEMENT: Relief of obstruction with infected urine proximal to blockage',
        'ABSCESS DRAINAGE: Perinephric or renal abscess >3-5cm may require percutaneous or surgical drainage',
        'STONE REMOVAL: Infected stones require removal; antibiotics alone will not clear infection',
        'UROLOGIC RECONSTRUCTION: For structural abnormalities causing recurrent complicated UTI',
        'CATHETER REMOVAL: Essential first step in catheter-associated UTI management'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Urosepsis with hemodynamic instability - ICU admission required',
        '🔴 IMMEDIATE REFERRAL: Obstructed infected kidney (pyonephrosis) - urgent decompression needed',
        '🔴 IMMEDIATE REFERRAL: Suspected renal or perinephric abscess - imaging and possible drainage',
        '🟠 URGENT REFERRAL: Pyelonephritis not responding to 48-72 hours of appropriate antibiotics',
        '🟠 URGENT REFERRAL: UTI in pregnancy with persistent symptoms or concern for pyelonephritis',
        '🟠 URGENT REFERRAL: Male UTI with suspected prostatitis or prostatic abscess',
        '🟠 URGENT REFERRAL: Suspected urinary obstruction (stone, tumor, stricture)',
        '🟡 ROUTINE REFERRAL: Recurrent UTI (≥3 episodes/year) for urologic evaluation',
        '🟡 ROUTINE REFERRAL: Persistent hematuria after infection treated - rule out malignancy',
        '🟡 ROUTINE REFERRAL: Recurrent UTI in children - evaluate for vesicoureteral reflux',
        '🟡 ROUTINE REFERRAL: Complex neurogenic bladder with frequent infections'
      ],
      
      monitoring: [
        'CLINICAL RESPONSE: Symptom improvement expected within 24-48 hours of appropriate therapy',
        'TEMPERATURE: Should defervesce within 48-72 hours in pyelonephritis; persistent fever suggests complication',
        'REPEAT URINALYSIS: Not routinely needed if symptoms resolve; obtain if treatment failure',
        'TEST OF CURE CULTURE: Indicated in pregnancy (1-2 weeks after treatment) and recurrent UTI',
        'RENAL FUNCTION: Monitor in pyelonephritis, especially with aminoglycoside use',
        'FLUID BALANCE: Ensure adequate hydration; monitor intake/output in hospitalized patients',
        'ANTIBIOTIC LEVELS: Aminoglycoside levels if used >48 hours (trough <2 mg/L for gentamicin)',
        'IMAGING FOLLOW-UP: Repeat ultrasound if initial imaging showed abnormality',
        'PREGNANCY MONITORING: Monthly cultures after UTI in pregnancy until delivery',
        'LONG-TERM: Evaluate for prophylaxis in frequently recurring infections'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE PHASE FOLLOW-UP:
• Uncomplicated cystitis: Follow-up only if symptoms persist >48-72 hours
• Pyelonephritis (outpatient): Review at 48-72 hours to assess clinical response
• Hospitalized patients: Daily assessment until afebrile and clinically stable
• Treatment failure: Re-evaluate, obtain culture if not done, consider imaging

RESPONSE ASSESSMENT:
• Symptoms should improve within 24-48 hours of appropriate antibiotics
• Fever should resolve within 48-72 hours in pyelonephritis
• Persistent or worsening symptoms: Consider resistant organism, abscess, obstruction

TEST OF CURE:
• Routine test of cure NOT recommended for uncomplicated UTI
• Required in pregnancy: Urine culture 1-2 weeks after completing treatment
• Consider in recurrent UTI to document clearance

PREGNANCY FOLLOW-UP:
• Monthly urine cultures after any UTI in pregnancy
• Screen for asymptomatic bacteriuria at first prenatal visit
• Treat all bacteriuria in pregnancy (even asymptomatic)

RECURRENT UTI (≥3 episodes/year):
• Urologic evaluation to exclude structural abnormality
• Consider prophylaxis options:
  - Post-coital prophylaxis: TMP-SMX or nitrofurantoin single dose
  - Continuous prophylaxis: Low-dose nightly for 6-12 months
  - Self-start therapy: Patient-initiated treatment at symptom onset

LONG-TERM MANAGEMENT:
• Address modifiable risk factors
• Postmenopausal women: Consider topical estrogen
• Behavioral modifications: Hydration, voiding habits
• Evaluate for diabetes, immunosuppression in recurrent cases

TREATMENT DURATION:
• Uncomplicated cystitis: 3-5 days (nitrofurantoin 5 days, TMP-SMX 3 days)
• Pyelonephritis: 7-14 days (7 days may be sufficient if rapid response)
• Complicated UTI: 10-14 days
• Catheter-associated UTI: 7 days (women), 7-14 days (men)
• Relapse vs reinfection: Relapse (same organism <2 weeks) needs longer therapy`
    },

    redFlags: [
      '🔴 CRITICAL: Fever >39°C with rigors and hypotension - indicates urosepsis requiring immediate resuscitation',
      '🔴 CRITICAL: Costovertebral angle tenderness with systemic symptoms - pyelonephritis, risk of sepsis',
      '🔴 CRITICAL: Anuria or severe oliguria with UTI - suggests obstructed infected system (emergency)',
      '🔴 CRITICAL: Altered mental status in elderly with UTI - may indicate urosepsis',
      '🔴 CRITICAL: Diabetes with UTI and abdominal pain - consider emphysematous pyelonephritis/cystitis',
      '🟠 URGENT: Persistent fever >48-72 hours despite appropriate antibiotics - abscess, obstruction, or resistant organism',
      '🟠 URGENT: Gross hematuria with clots - may cause obstruction; evaluate for underlying pathology',
      '🟠 URGENT: UTI symptoms in pregnancy with fever or flank pain - high risk for pyelonephritis and preterm labor',
      '🟠 URGENT: Immunocompromised patient with UTI and any systemic symptoms',
      '🟠 URGENT: Recent urologic procedure with new UTI symptoms - risk of complicated infection',
      '🟡 CAUTION: Recurrent UTI with hematuria persisting after treatment - evaluate for bladder malignancy',
      '🟡 CAUTION: Male with UTI - always considered complicated; evaluate for underlying cause',
      '🟡 CAUTION: New urinary retention with infection - requires catheterization and investigation',
      '🟡 CAUTION: History of urinary tract abnormality or stones with UTI'
    ],

    complications: [
      'Pyelonephritis - Ascending infection to kidneys; fever, flank pain; requires prolonged antibiotics',
      'Urosepsis - Systemic infection from urinary source; can progress to septic shock; high mortality',
      'Renal abscess - Localized collection within kidney parenchyma; may need drainage',
      'Perinephric abscess - Collection around kidney; associated with obstruction or diabetes',
      'Emphysematous pyelonephritis - Gas-forming infection; associated with diabetes; often requires nephrectomy',
      'Emphysematous cystitis - Gas in bladder wall; more common in diabetics; usually responds to antibiotics',
      'Papillary necrosis - Ischemic necrosis of renal papillae; associated with diabetes, sickle cell, NSAID use',
      'Chronic pyelonephritis - Repeated infections causing renal scarring and CKD',
      'Xanthogranulomatous pyelonephritis - Chronic destructive process; often with obstructing stone; requires nephrectomy',
      'Preterm labor/delivery - UTI in pregnancy significantly increases preterm birth risk',
      'Recurrent infection - 20-30% of women have recurrence; may need prophylaxis',
      'Antibiotic resistance - Inappropriate treatment drives resistance; complicates future therapy',
      'Fournier gangrene - Necrotizing fasciitis of perineum; rare but devastating complication',
      'Chronic prostatitis - In men with recurrent UTI; difficult to eradicate',
      'Renal function decline - Repeated infections, especially with obstruction, can lead to CKD'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Uncomplicated cystitis: Excellent; >90% cure with appropriate antibiotics
• Pyelonephritis (uncomplicated): Good; most respond to 7-14 days therapy
• Complicated UTI: Variable depending on underlying condition
• Urosepsis: Mortality 20-40% despite treatment; depends on early recognition

PROGNOSTIC FACTORS:

Better Outcomes:
• Young, healthy patient
• Uncomplicated infection
• Early presentation and treatment
• Susceptible organism
• No structural abnormalities
• Good antibiotic penetration

Worse Outcomes:
• Elderly or immunocompromised
• Diabetes mellitus
• Obstructing stone or mass
• Resistant organism (ESBL, MDR)
• Delayed treatment
• Presence of abscess or emphysematous changes
• Chronic kidney disease
• Indwelling catheter

RECURRENCE RATES:
• First UTI: 20-30% will have recurrence within 6 months
• Recurrent UTI: Higher risk with each episode
• Post-menopausal: Up to 50% recurrence rate without prevention

LONG-TERM OUTCOMES:
• Most patients have no long-term sequelae from single UTI episode
• Recurrent pyelonephritis can cause permanent renal damage
• Children with VUR and recurrent UTI at highest risk for renal scarring
• Appropriate treatment and prevention strategies improve outcomes

MORTALITY:
• Uncomplicated UTI: Near zero
• Pyelonephritis: <1% in otherwise healthy patients
• Urosepsis: 20-40% mortality
• Emphysematous pyelonephritis: 20-70% mortality (higher with bilateral or medical management alone)`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

Behavioral Measures:
• Adequate hydration (2-3 liters daily)
• Regular voiding; avoid holding urine
• Post-coital voiding within 30 minutes
• Front-to-back wiping technique
• Avoid irritating feminine products
• Wear breathable cotton underwear

Specific Populations:
• Postmenopausal women: Topical vaginal estrogen reduces recurrence
• Pregnant women: Screen and treat asymptomatic bacteriuria
• Catheterized patients: Use only when necessary; remove promptly
• Diabetics: Optimize glycemic control

Catheter Care:
• Strict aseptic insertion technique
• Closed drainage system
• Daily meatal cleaning (no benefit proven but commonly done)
• Avoid routine catheter changes
• Remove as soon as clinically possible
• Consider alternatives (condom catheter, intermittent catheterization)

SECONDARY PREVENTION (Recurrent UTI ≥3/year):

Antibiotic Prophylaxis Options:
• Post-coital: TMP-SMX 80/400mg or nitrofurantoin 50-100mg single dose after intercourse
• Continuous: Nitrofurantoin 50-100mg nightly OR TMP-SMX 40/200mg nightly for 6-12 months
• Self-treatment: Patient-initiated 3-day course at symptom onset (selected patients)

Non-Antibiotic Prevention:
• Cranberry products (juice or capsules): May reduce recurrence; evidence mixed
• D-mannose: Binds E. coli fimbriae; some evidence for prevention
• Probiotics (Lactobacillus): May help restore vaginal flora
• Methenamine hippurate: Urinary antiseptic; avoid in renal impairment
• Immunostimulants (OM-89): E. coli extract vaccine; reduces recurrence

TERTIARY PREVENTION:
• Identify and correct structural abnormalities
• Manage underlying conditions (diabetes, neurogenic bladder)
• Regular monitoring in high-risk groups
• Patient education on early symptom recognition`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS A URINARY TRACT INFECTION?
A urinary tract infection (UTI) happens when germs (usually bacteria) get into your urinary system - the bladder, kidneys, or the tubes that connect them. UTIs are very common, especially in women. The infection can be in the bladder (causing pain when you urinate) or can spread to the kidneys (causing fever and back pain).

CAUSES:
• Bacteria from the bowel area entering the urinary opening
• Sexual activity can push bacteria into the bladder
• Holding urine for too long
• Not drinking enough water
• Catheters (tubes) in the bladder
• Diabetes or other conditions that weaken immunity
• Menopause (in women)
• Enlarged prostate (in men)

WARNING SIGNS - SEEK HELP IMMEDIATELY IF:
• High fever (above 38°C/100.4°F) with back pain or chills
• Blood in your urine
• Unable to urinate or very little urine
• Vomiting and cannot keep fluids down
• Severe pain in your back or side
• Confusion or extreme weakness (especially in elderly)
• Symptoms getting worse after 2 days of treatment
• You are pregnant with any UTI symptoms

TREATMENT:
• Antibiotics kill the bacteria causing the infection
• COMPLETE ALL YOUR ANTIBIOTICS even if you feel better
• Stopping early can make the infection come back stronger
• Drink plenty of water (at least 8 glasses a day)
• You should start feeling better within 1-2 days
• Pain medicine can help with discomfort

SELF-CARE AT HOME:
• Drink 2-3 liters of water daily
• Urinate frequently - do not hold your urine
• Use a warm water bottle on your belly for pain
• Avoid alcohol, caffeine, and spicy foods during infection
• Wipe from front to back after using the toilet
• Urinate soon after sexual intercourse

PREVENTION:
• Drink plenty of fluids every day
• Do not hold your urine for long periods
• Urinate after sexual intercourse
• Wipe from front to back
• Wear cotton underwear
• Avoid strong soaps or sprays near the genital area
• If you get frequent infections, talk to your doctor about prevention options

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. UTIs can become serious if not treated properly. Always consult a healthcare professional for proper diagnosis and treatment. Complete your full course of antibiotics as prescribed.

QUESTIONS TO ASK YOUR DOCTOR:
• What type of UTI do I have?
• How long should I take the antibiotics?
• What should I do if my symptoms do not improve?
• How can I prevent future infections?
• Should I be tested for other conditions?`,

    references: [
      'Gupta K, Hooton TM, et al. International Clinical Practice Guidelines for the Treatment of Acute Uncomplicated Cystitis and Pyelonephritis in Women. IDSA Guidelines. Clin Infect Dis. 2011;52(5):e103-e120.',
      'Flores-Mireles AL, Walker JN, Caparon M, Hultgren SJ. Urinary tract infections: epidemiology, mechanisms of infection and treatment options. Nat Rev Microbiol. 2015;13(5):269-284.',
      'World Health Organization. Antimicrobial Resistance: Global Report on Surveillance. WHO. 2024.',
      'Hooton TM. Uncomplicated Urinary Tract Infection. N Engl J Med. 2012;366(11):1028-1037.',
      'Foxman B. Urinary tract infection syndromes: occurrence, recurrence, bacteriology, risk factors, and disease burden. Infect Dis Clin North Am. 2014;28(1):1-13.',
      'Smaill FM, Vazquez JC. Antibiotics for asymptomatic bacteriuria in pregnancy. Cochrane Database Syst Rev. 2019;11:CD000490.',
      'Bonkat G, Bartoletti R, et al. EAU Guidelines on Urological Infections. European Association of Urology. 2024.',
      'Tandogdu Z, Wagenlehner FME. Global epidemiology of urinary tract infections. Curr Opin Infect Dis. 2016;29(1):73-79.',
      'Najar MS, Saldanha CL, Banday KA. Approach to urinary tract infections. Indian J Nephrol. 2009;19(4):129-139.',
      'Irenge LM, Kabego L, et al. Antimicrobial resistance of bacteria isolated from patients with urinary tract infections in a hospital in Bukavu, Democratic Republic of the Congo. J Antimicrob Chemother. 2014;69(2):484-488.',
      'Sabir S, Anjum AA, et al. Gram-negative bacilli causing urinary tract infections: A multicenter study in Africa. BMC Infect Dis. 2017;17:404.',
      'National Institute for Health and Care Excellence (NICE). Urinary tract infection (lower): antimicrobial prescribing. NICE guideline [NG109]. 2018 (updated 2023).',
      'Nicolle LE, Gupta K, et al. Clinical Practice Guideline for the Management of Asymptomatic Bacteriuria: 2019 Update by IDSA. Clin Infect Dis. 2019;68(10):e83-e110.',
      'Stapleton AE. Urinary tract infection pathogenesis: host factors. Infect Dis Clin North Am. 2014;28(1):149-159.',
      'Johnson JR, Russo TA. Acute Pyelonephritis in Adults. N Engl J Med. 2018;378(1):48-59.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 62. SEXUALLY TRANSMITTED INFECTIONS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '62',
    name: 'Sexually Transmitted Infections',
    slug: 'sexually-transmitted-infections',
    specialty: 'Infectious Disease',
    bodySystem: 'Reproductive',
    icdCode: 'A64',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Sexually transmitted infections (STIs) are infections spread primarily through sexual contact, representing a major global public health challenge with over 1 million new infections acquired daily worldwide. The WHO estimates 374 million new infections annually of four curable STIs: chlamydia (129 million), gonorrhea (82 million), syphilis (7.1 million), and trichomoniasis (156 million). Additionally, over 500 million people have genital herpes (HSV-2), and 300 million women have HPV infection at any given time.

The pathophysiology varies by organism: bacterial STIs (gonorrhea, chlamydia, syphilis) involve mucosal colonization and tissue invasion; viral STIs (HIV, HSV, HPV, Hepatitis B) establish latent or chronic infections; and parasitic STIs (trichomoniasis) cause inflammatory responses. Many STIs share the characteristic of causing asymptomatic infection, facilitating ongoing transmission. Co-infection is common and synergistic - genital ulcer diseases increase HIV transmission 2-5 fold, while HIV increases susceptibility to other STIs.

Sub-Saharan Africa bears the highest global STI burden, driven by multiple factors: high HIV prevalence creating immunocompromised populations, limited access to diagnostic testing and treatment, cultural barriers to sexual health services, gender inequality affecting womens healthcare-seeking, and inadequate partner notification systems. The syndromic management approach, while practical for resource-limited settings, may miss asymptomatic infections and contribute to antimicrobial resistance, particularly in gonorrhea where resistance to multiple drug classes is emerging across the continent.

Effective STI management requires a comprehensive approach: accurate diagnosis, appropriate treatment, partner notification, risk reduction counseling, and integration with HIV services. Prevention through condom promotion, HPV and Hepatitis B vaccination, and pre-exposure prophylaxis for HIV are essential components of STI control strategies.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - ALWAYS test for HIV when diagnosing any STI - presence of one STI indicates risk behavior and HIV testing is essential

🔴 CRITICAL POINT 2 - Treat BOTH partners simultaneously to prevent reinfection (ping-pong effect); provide expedited partner therapy when possible

🔴 CRITICAL POINT 3 - Syphilis in pregnancy is a medical emergency - untreated maternal syphilis causes stillbirth, neonatal death, or congenital syphilis in up to 80% of cases

🔴 CRITICAL POINT 4 - Genital ulcers increase HIV transmission 2-5 fold - prioritize ulcer treatment and HIV testing

🟠 CRITICAL POINT 5 - Gonorrhea requires DUAL THERAPY (ceftriaxone + azithromycin) due to widespread resistance; never use fluoroquinolones empirically

🟠 CRITICAL POINT 6 - Most chlamydia and gonorrhea infections in women are asymptomatic - screen high-risk populations proactively

🟠 CRITICAL POINT 7 - Syndromic management has limitations; use etiologic diagnosis when available for better outcomes and resistance surveillance

🟡 CRITICAL POINT 8 - All pregnant women should be screened for syphilis, HIV, and Hepatitis B at first antenatal visit

🟡 CRITICAL POINT 9 - Counsel on abstinence during treatment and until partners are treated to prevent reinfection and transmission`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 1 million new STI acquisitions daily worldwide
• 374 million new curable STI cases annually (WHO 2020 estimates)
• Chlamydia: 129 million new cases/year - most common bacterial STI
• Gonorrhea: 82 million new cases/year - rising antimicrobial resistance
• Syphilis: 7.1 million new cases/year - resurgence in many regions
• Trichomoniasis: 156 million new cases/year - most common curable STI
• Genital HSV-2: 491 million prevalent cases; 13 million new cases/year
• HPV: 300 million women infected at any time; causes 99% of cervical cancers
• Hepatitis B: 296 million chronic carriers; 1.5 million new cases/year

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa: Highest global STI burden
• Syphilis seroprevalence in antenatal women: 2-17% across different countries
• Gonorrhea: High rates of antimicrobial resistance (ceftriaxone resistance emerging)
• Chlamydia prevalence: 3-16% in various African populations
• Congenital syphilis: 350,000+ cases/year in Africa (preventable)
• HIV co-infection: Dramatically increases STI susceptibility and transmission
• Limited surveillance data due to syndromic management approach
• Mother-to-child transmission remains significant concern
• Nigeria: Estimated 1.2 million syphilis cases annually
• South Africa: High STI prevalence despite robust HIV programs

RISK FACTORS:
• Multiple sexual partners
• Unprotected sexual intercourse
• Age 15-24 years (highest rates)
• Previous STI history
• Commercial sex work
• Men who have sex with men (MSM)
• Substance abuse (especially alcohol)
• HIV infection (bidirectional relationship)
• Low educational attainment
• Inconsistent condom use
• Transactional sex
• Mobile populations and migrant workers`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

URETHRAL DISCHARGE SYNDROME (Men):
• Purulent/mucopurulent urethral discharge
• Dysuria (painful urination)
• Urethral pruritus or tingling
• Causes: Gonorrhea (thick, yellow), Chlamydia (clear, mucoid), Trichomonas, Mycoplasma

VAGINAL DISCHARGE SYNDROME:
• Abnormal vaginal discharge (color, consistency, odor)
• Vulvar itching or irritation
• Dyspareunia (painful intercourse)
• Causes: Trichomoniasis (frothy, yellow-green), Bacterial vaginosis (fishy odor), Candidiasis (curdy white), Chlamydia, Gonorrhea (cervicitis)

GENITAL ULCER SYNDROME:
• Painless ulcer(s): Syphilis (chancre - clean, indurated base), Lymphogranuloma venereum (LGV)
• Painful ulcer(s): Herpes (multiple, vesicular then ulcerative), Chancroid (ragged, purulent base)
• Inguinal lymphadenopathy
• Donovanosis: Beefy red granulomatous lesions (rare)

LOWER ABDOMINAL PAIN SYNDROME (Women):
• Bilateral lower abdominal pain
• Abnormal vaginal discharge
• Fever in severe cases
• Dyspareunia
• Suggests Pelvic Inflammatory Disease (PID)

INGUINAL BUBO SYNDROME:
• Painful inguinal lymph node swelling
• May suppurate and drain
• Causes: LGV, Chancroid, (occasionally primary syphilis, herpes)

SPECIFIC STI PRESENTATIONS:

Syphilis Stages:
• Primary: Painless chancre at inoculation site (3-90 days post-exposure)
• Secondary: Rash (palms/soles), mucous patches, condylomata lata, lymphadenopathy (4-10 weeks after chancre)
• Latent: Asymptomatic with positive serology
• Tertiary: Gummas, cardiovascular syphilis, neurosyphilis (years to decades later)

Genital Herpes (HSV):
• Primary: Painful grouped vesicles → ulcers, fever, inguinal adenopathy
• Recurrent: Milder, prodrome (tingling), localized lesions

Genital Warts (HPV):
• Cauliflower-like growths (condylomata acuminata)
• May be flat, papular, or keratotic
• Usually painless unless traumatized

PHYSICAL EXAMINATION:
• External genital inspection: ulcers, warts, discharge, erythema
• Speculum exam (women): cervical discharge, friability, ulcers
• Bimanual exam (women): cervical motion tenderness, adnexal tenderness
• Urethral milking (men): express discharge if not apparent
• Inguinal lymph node palpation
• Oropharyngeal examination if oral sex exposure
• Rectal examination if anal intercourse history
• Skin examination: rashes, palmar/plantar lesions`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL ASSESSMENT
• Sexual history: Partners, practices, protection, previous STIs
• Symptom history: Discharge, ulcers, pain, systemic symptoms
• Risk assessment: High-risk behaviors, partner symptoms
• Physical examination: Complete genital and relevant systems

STEP 2: SYNDROMIC DIAGNOSIS (RESOURCE-LIMITED SETTINGS)
• Based on presenting syndrome (discharge, ulcer, pain)
• Treat most likely causes without laboratory confirmation
• WHO syndromic management flowcharts available
• Appropriate when laboratory unavailable; limitations acknowledged

STEP 3: ETIOLOGIC DIAGNOSIS (WHEN AVAILABLE)
• Microscopy: Gram stain, wet mount, dark-field
• Rapid tests: Syphilis, HIV, Trichomonas
• Culture: Gonorrhea (with susceptibility testing)
• NAAT/PCR: Chlamydia, Gonorrhea, HSV, HPV (most sensitive)
• Serology: Syphilis (RPR/VDRL + TPHA/FTA-ABS), HIV, Hepatitis B

STEP 4: ALWAYS TEST FOR HIV
• Offer HIV testing to every STI patient
• Pre-test counseling and consent
• Post-test counseling regardless of result
• Link to care if positive

SYNDROMIC MANAGEMENT ALGORITHMS:

Urethral Discharge (Men):
• Treat for BOTH gonorrhea AND chlamydia
• Single-dose ceftriaxone + azithromycin or doxycycline

Vaginal Discharge:
• If cervicitis or high-risk: Treat for gonorrhea + chlamydia + trichomoniasis/BV
• If vaginitis only: Treat for trichomoniasis and/or candidiasis

Genital Ulcer:
• If vesicles or recurrent: Treat for herpes
• If non-vesicular: Treat for syphilis + chancroid (+ herpes if endemic)

Lower Abdominal Pain (PID):
• Treat for gonorrhea + chlamydia + anaerobes
• Outpatient or inpatient based on severity criteria

LABORATORY TESTS:

For Gonorrhea:
• Gram stain: Intracellular gram-negative diplococci (sensitivity ~95% in symptomatic men, ~50% in women)
• Culture: Gold standard for susceptibility; requires special media
• NAAT: Most sensitive; urine or swab specimens

For Chlamydia:
• NAAT: Test of choice; urine or swab
• Cannot be diagnosed by microscopy or routine culture

For Syphilis:
• Non-treponemal: RPR or VDRL (screening, quantitative for monitoring)
• Treponemal: TPHA, FTA-ABS, rapid treponemal tests (confirmatory)
• Both needed for diagnosis; treponemal tests remain positive for life

For Trichomoniasis:
• Wet mount: Motile trichomonads (sensitivity 50-70%)
• Rapid antigen test or NAAT: More sensitive

For Herpes:
• Clinical diagnosis often sufficient for typical presentations
• PCR of lesion: Most sensitive for confirmation
• Serology: Type-specific IgG for HSV-2`,

    differentialDiagnosis: [
      'Urinary tract infection - Dysuria without discharge; pyuria on urinalysis; responds to UTI antibiotics',
      'Candidal vulvovaginitis - Thick white curdy discharge, pruritus; KOH prep shows pseudohyphae; not sexually transmitted',
      'Bacterial vaginosis - Thin gray discharge, fishy odor; clue cells on wet mount; not truly an STI',
      'Contact dermatitis - Genital irritation from soaps, latex; no discharge; history of irritant exposure',
      'Fixed drug eruption - Recurrent ulcer at same site; medication history; heals with hyperpigmentation',
      'Behcet disease - Recurrent oral and genital ulcers; uveitis; systemic inflammatory condition',
      'Lichen planus - Violaceous papules, Wickham striae; may affect oral mucosa; chronic course',
      'Psoriasis - Well-demarcated erythematous plaques; may lack typical scale in genital area',
      'Trauma/abrasion - History of mechanical injury; linear pattern; heals without treatment',
      'Molluscum contagiosum - Umbilicated papules; not ulcerative; viral infection (poxvirus)',
      'Scabies - Intense pruritus worse at night; burrows; may have genital lesions',
      'Epididymitis - Testicular/scrotal pain and swelling; may be STI-related (chlamydia, gonorrhea) or non-STI',
      'Prostatitis - Perineal pain, urinary symptoms; tender prostate; may be chronic',
      'Cervical ectropion - Cervical discharge; normal finding especially in young women; no treatment needed',
      'Bartholin gland cyst/abscess - Unilateral labial swelling; may become infected; not always STI-related'
    ],

    investigations: [
      {
        name: 'HIV Rapid Test',
        indication: '🔴 ESSENTIAL - Offer to ALL STI patients; presence of STI indicates risk behavior for HIV',
        availability: 'BASIC'
      },
      {
        name: 'Syphilis Rapid Test (Treponemal)',
        indication: '🔴 ESSENTIAL - Screen all STI patients; all pregnant women at first antenatal visit',
        availability: 'BASIC'
      },
      {
        name: 'RPR or VDRL (Non-Treponemal)',
        indication: '🔴 ESSENTIAL - Confirms active syphilis; quantitative titer for treatment monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Gram Stain of Discharge',
        indication: '🔴 ESSENTIAL - Urethral/cervical specimens; identifies gonococci and provides rapid diagnosis in men',
        availability: 'BASIC'
      },
      {
        name: 'Wet Mount Microscopy',
        indication: '🟠 IMPORTANT - Vaginal discharge for trichomonads, clue cells (BV), yeast; immediate bedside test',
        availability: 'BASIC'
      },
      {
        name: 'KOH Preparation',
        indication: '🟠 IMPORTANT - Vaginal discharge; whiff test for BV; visualizes Candida pseudohyphae',
        availability: 'BASIC'
      },
      {
        name: 'Gonorrhea Culture',
        indication: '🟠 IMPORTANT - Gold standard; essential for antimicrobial susceptibility testing; requires Thayer-Martin media',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'NAAT for Chlamydia/Gonorrhea',
        indication: '🟠 IMPORTANT - Most sensitive test; can use urine specimen; ideal for asymptomatic screening',
        availability: 'ADVANCED'
      },
      {
        name: 'Hepatitis B Surface Antigen (HBsAg)',
        indication: '🟠 IMPORTANT - Screen all STI patients; all pregnant women; sexual transmission route',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis C Antibody',
        indication: '🟡 WHEN AVAILABLE - Screen high-risk patients (HIV+, PWID, MSM); sexual transmission less common but occurs',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pregnancy Test',
        indication: '🔴 ESSENTIAL - All women of reproductive age before treatment; affects drug selection',
        availability: 'BASIC'
      },
      {
        name: 'Herpes PCR (Lesion Swab)',
        indication: '🟡 WHEN AVAILABLE - Confirms HSV diagnosis; types HSV-1 vs HSV-2; most sensitive test for ulcers',
        availability: 'ADVANCED'
      },
      {
        name: 'HSV Type-Specific Serology',
        indication: '🟡 WHEN AVAILABLE - Identifies HSV-2 infection; useful when no active lesions; screening partners',
        availability: 'ADVANCED'
      },
      {
        name: 'Dark-Field Microscopy',
        indication: '🟡 WHEN AVAILABLE - Identifies T. pallidum spirochetes from chancre; immediate diagnosis of primary syphilis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HPV DNA Testing',
        indication: '🟡 WHEN AVAILABLE - Cervical cancer screening; identifies high-risk HPV types',
        availability: 'ADVANCED'
      },
      {
        name: 'CSF Analysis (Lumbar Puncture)',
        indication: '🟠 IMPORTANT - Suspected neurosyphilis; HIV+ with syphilis; neurological or ophthalmologic symptoms',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM SYNDROME: Classify as discharge, ulcer, lower abdominal pain, or bubo syndrome',
        '⚡ HIV TEST: Offer and perform HIV testing for ALL STI patients with counseling',
        '⚡ SYPHILIS SCREEN: Test all STI patients for syphilis; urgent in pregnancy',
        '⚡ PREGNANCY TEST: All women of reproductive age before prescribing treatment',
        '⚡ COLLECT SPECIMENS: Before treatment if etiologic diagnosis available (swabs, blood)',
        '⚡ START TREATMENT: Initiate appropriate syndromic or etiologic therapy immediately',
        '⚡ PARTNER NOTIFICATION: Counsel on importance; provide partner treatment or notification',
        '⚡ RISK REDUCTION: Counsel on condom use, reducing partners, abstinence during treatment',
        '⚡ HEPATITIS B: Screen and vaccinate susceptible individuals',
        '⚡ FOLLOW-UP: Schedule return visit for test of cure or response assessment'
      ],
      
      pharmacological: [
        {
          drug: 'Ceftriaxone',
          indication: '🔴 FIRST-LINE - Gonorrhea (urethritis, cervicitis, proctitis, pharyngitis); ALWAYS use with azithromycin for dual therapy',
          dose: 'Uncomplicated gonorrhea: 500mg IM single dose (or 250mg if 500mg unavailable). Disseminated: 1g IV/IM daily. MUST be combined with azithromycin.',
          route: 'IM / IV',
          duration: 'Single dose for uncomplicated; 7 days for disseminated',
          contraindications: ['Cephalosporin allergy', 'Severe penicillin allergy'],
          sideEffects: ['Injection site pain', 'Diarrhea', 'Rash'],
          alternatives: ['Gentamicin 240mg IM + Azithromycin 2g PO (if ceftriaxone unavailable)']
        },
        {
          drug: 'Azithromycin',
          indication: '🔴 FIRST-LINE - Chlamydia; Part of gonorrhea dual therapy; Chancroid; Single-dose advantage',
          dose: 'Chlamydia: 1g PO single dose. Gonorrhea dual therapy: 1g PO with ceftriaxone. Chancroid: 1g PO single dose. Granuloma inguinale: 1g weekly for 3+ weeks.',
          route: 'PO',
          duration: 'Single dose for most indications',
          contraindications: ['Macrolide allergy', 'QT prolongation (caution)'],
          sideEffects: ['GI upset', 'Nausea', 'Abdominal pain', 'QT prolongation'],
          alternatives: ['Doxycycline 100mg BD x 7 days']
        },
        {
          drug: 'Doxycycline',
          indication: '🔴 FIRST-LINE - Chlamydia (alternative to azithromycin); Syphilis (penicillin-allergic); LGV; PID component',
          dose: 'Chlamydia/NGU: 100mg PO twice daily for 7 days. LGV: 100mg PO twice daily for 21 days. Syphilis (if penicillin-allergic): 100mg PO twice daily for 14-28 days.',
          route: 'PO',
          duration: '7-28 days depending on indication',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Children <8 years'],
          sideEffects: ['Photosensitivity', 'GI upset', 'Esophagitis', 'Vaginal candidiasis'],
          alternatives: ['Azithromycin', 'Erythromycin (pregnancy)']
        },
        {
          drug: 'Benzathine Penicillin G',
          indication: '🔴 FIRST-LINE - Syphilis (all stages); Drug of choice especially in pregnancy; Single injection for primary/secondary',
          dose: 'Primary/Secondary/Early latent: 2.4 million units IM single dose. Late latent/Unknown duration: 2.4 million units IM weekly x 3 doses. Neurosyphilis: Aqueous penicillin G 3-4 million units IV q4h x 10-14 days.',
          route: 'IM',
          duration: 'Single dose to 3 weekly doses; IV for neurosyphilis',
          contraindications: ['Penicillin allergy (use doxycycline alternative, but NOT in pregnancy)'],
          sideEffects: ['Injection site pain', 'Jarisch-Herxheimer reaction', 'Anaphylaxis (rare)'],
          alternatives: ['Doxycycline (if penicillin-allergic and NOT pregnant)']
        },
        {
          drug: 'Metronidazole',
          indication: '🔴 FIRST-LINE - Trichomoniasis; Bacterial vaginosis; PID anaerobic coverage',
          dose: 'Trichomoniasis: 2g PO single dose OR 500mg PO twice daily for 7 days. BV: 500mg PO twice daily for 7 days OR 0.75% gel intravaginally. PID: 500mg PO/IV twice daily.',
          route: 'PO / IV / Topical',
          duration: 'Single dose or 7 days',
          contraindications: ['First trimester pregnancy (relative)', 'Alcohol consumption during treatment'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol', 'Neuropathy (prolonged use)'],
          alternatives: ['Tinidazole 2g single dose']
        },
        {
          drug: 'Acyclovir',
          indication: '🔴 FIRST-LINE - Genital herpes (HSV); First episode and recurrent; Suppressive therapy',
          dose: 'First episode: 400mg PO three times daily for 7-10 days OR 200mg PO five times daily. Recurrent: 400mg PO three times daily for 5 days OR 800mg PO twice daily for 5 days. Suppression: 400mg PO twice daily.',
          route: 'PO / IV (severe)',
          duration: '5-10 days for episodes; long-term for suppression',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Nausea', 'Headache', 'Renal impairment (IV, ensure hydration)'],
          alternatives: ['Valacyclovir', 'Famciclovir']
        },
        {
          drug: 'Valacyclovir',
          indication: '🟠 ALTERNATIVE - Genital herpes; Better bioavailability than acyclovir; Less frequent dosing',
          dose: 'First episode: 1g PO twice daily for 7-10 days. Recurrent: 500mg PO twice daily for 3 days OR 1g daily for 5 days. Suppression: 500mg-1g PO once daily.',
          route: 'PO',
          duration: '3-10 days for episodes; long-term for suppression',
          contraindications: ['Hypersensitivity to acyclovir/valacyclovir'],
          sideEffects: ['Headache', 'Nausea', 'TTP/HUS in immunocompromised (high doses)'],
          alternatives: ['Acyclovir', 'Famciclovir']
        },
        {
          drug: 'Erythromycin',
          indication: '🟠 ALTERNATIVE - Chlamydia in pregnancy; Syphilis in pregnancy (when penicillin desensitization not possible)',
          dose: 'Chlamydia (pregnancy): 500mg PO four times daily for 7 days. Note: Erythromycin does NOT reliably treat fetal syphilis - penicillin strongly preferred.',
          route: 'PO',
          duration: '7 days',
          contraindications: ['Macrolide allergy', 'QT prolongation'],
          sideEffects: ['GI upset', 'Hepatotoxicity', 'QT prolongation'],
          alternatives: ['Amoxicillin (for chlamydia in pregnancy)']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟡 LIMITED USE - Chancroid (alternative); NOT for gonorrhea due to high resistance; Granuloma inguinale',
          dose: 'Chancroid: 500mg PO twice daily for 3 days. Granuloma inguinale: 750mg PO twice daily for 3+ weeks.',
          route: 'PO',
          duration: '3 days to 3+ weeks',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Children'],
          sideEffects: ['Tendinopathy', 'GI upset', 'CNS effects'],
          alternatives: ['Azithromycin', 'Ceftriaxone']
        },
        {
          drug: 'Podophyllotoxin (0.5% Solution/Gel)',
          indication: '🟠 FIRST-LINE - Genital warts (external); Patient-applied treatment',
          dose: 'Apply twice daily for 3 consecutive days, then 4 days off. Repeat cycle up to 4 times. Maximum 0.5mL per day.',
          route: 'Topical',
          duration: 'Up to 4 treatment cycles',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Internal/cervical warts'],
          sideEffects: ['Local irritation', 'Pain', 'Erosion'],
          alternatives: ['Imiquimod', 'Cryotherapy', 'TCA']
        },
        {
          drug: 'Imiquimod (5% Cream)',
          indication: '🟠 ALTERNATIVE - Genital warts; Immune modulator; Patient-applied',
          dose: 'Apply at bedtime 3 times weekly (e.g., Mon, Wed, Fri). Wash off after 6-10 hours. Continue up to 16 weeks.',
          route: 'Topical',
          duration: 'Up to 16 weeks',
          contraindications: ['Pregnancy (limited data)', 'Internal warts'],
          sideEffects: ['Local erythema', 'Erosion', 'Flu-like symptoms'],
          alternatives: ['Podophyllotoxin', 'Cryotherapy']
        }
      ],
      
      nonPharmacological: [
        'PARTNER NOTIFICATION: Essential to break transmission chain; provide treatment for partners',
        'EXPEDITED PARTNER THERAPY (EPT): Provide prescription/medication for partners without examination where legal/available',
        'CONDOM PROMOTION: Consistent and correct use reduces transmission; provide free condoms',
        'ABSTINENCE DURING TREATMENT: Advise no sexual contact until treatment complete and symptoms resolved (minimum 7 days)',
        'RISK REDUCTION COUNSELING: Discuss number of partners, casual sex, alcohol/drug use, transactional sex',
        'CRYOTHERAPY: For genital warts - liquid nitrogen applied by healthcare provider; effective but requires equipment',
        'TRICHLOROACETIC ACID (TCA): For warts - 80-90% solution applied by provider; chemical destruction',
        'HEPATITIS B VACCINATION: Offer to all STI patients who are not immune; 3-dose series',
        'HPV VACCINATION: Offer to eligible age groups (ideally before sexual debut; up to age 26-45 in some guidelines)',
        'HIV PrEP DISCUSSION: Offer to high-risk individuals; daily TDF/FTC highly effective',
        'CIRCUMCISION COUNSELING: Voluntary male circumcision reduces HIV and some STI acquisition (where culturally appropriate)'
      ],
      
      surgical: [
        'CRYOTHERAPY: Liquid nitrogen destruction of genital warts; multiple sessions often needed',
        'ELECTROCAUTERY: For genital warts; requires local anesthesia; effective for larger warts',
        'SURGICAL EXCISION: Large warts or those unresponsive to other treatments',
        'LASER ABLATION: CO2 laser for extensive warts; requires specialized equipment',
        'LOOP ELECTROSURGICAL EXCISION (LEEP): For cervical dysplasia from HPV',
        'INCISION AND DRAINAGE: For fluctuant buboes (LGV, chancroid) - aspirate through healthy skin',
        'BARTHOLIN GLAND ABSCESS DRAINAGE: If secondary infection; may need Word catheter'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Pregnant woman with positive syphilis - urgent treatment to prevent congenital syphilis',
        '🔴 IMMEDIATE REFERRAL: Suspected neurosyphilis (neurological symptoms, uveitis) - CSF examination and IV penicillin needed',
        '🔴 IMMEDIATE REFERRAL: Severe PID with peritonitis, tubo-ovarian abscess, or sepsis - inpatient IV antibiotics',
        '🔴 IMMEDIATE REFERRAL: Disseminated gonococcal infection (arthritis, dermatitis, tenosynovitis)',
        '🟠 URGENT REFERRAL: Treatment failure or suspected antimicrobial resistance - culture and susceptibility testing',
        '🟠 URGENT REFERRAL: Recurrent genital ulcers - may need HSV suppression or evaluation for other causes',
        '🟠 URGENT REFERRAL: Newly diagnosed HIV - linkage to HIV care and treatment',
        '🟠 URGENT REFERRAL: Hepatitis B positive - evaluation for chronic infection and treatment eligibility',
        '🟡 ROUTINE REFERRAL: Genital warts not responding to topical therapy - specialist ablative treatment',
        '🟡 ROUTINE REFERRAL: Abnormal cervical cytology (HPV-related) - colposcopy',
        '🟡 ROUTINE REFERRAL: Recurrent STIs despite treatment - assess for reinfection vs treatment failure vs underlying condition',
        '🟡 ROUTINE REFERRAL: Complicated STI in HIV-positive patient - may need modified treatment'
      ],
      
      monitoring: [
        'SYPHILIS: RPR/VDRL titers at 3, 6, 12 months; expect 4-fold decline by 6 months post-treatment; retest if titers rise',
        'HIV LINKAGE: Confirm newly diagnosed patients are linked to HIV care; verify ART initiation',
        'TEST OF CURE: Gonorrhea - not routine unless symptoms persist; recommended for pharyngeal gonorrhea',
        'TEST OF CURE: Chlamydia - test at 3 months to detect reinfection (common); not immediately post-treatment',
        'PREGNANCY: Repeat syphilis testing in third trimester and at delivery in high-prevalence settings',
        'REINFECTION: High rates of repeat infection; rescreening at 3 months recommended for gonorrhea/chlamydia',
        'PARTNER TREATMENT: Verify partners were treated to prevent reinfection',
        'HEPATITIS B: Follow-up serology to confirm response to vaccination (anti-HBs)',
        'HERPES SUPPRESSION: Reassess need for suppressive therapy annually; discuss with patients',
        'TREATMENT RESPONSE: Symptoms should improve within days; persistent symptoms warrant reassessment'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

GONORRHEA AND CHLAMYDIA:
• Test of cure not routinely needed if symptoms resolve
• Retest at 3 months to detect reinfection (high rates)
• Pharyngeal gonorrhea: Test of cure at 14 days recommended
• Treatment failure: Culture for susceptibility testing

SYPHILIS:
• Primary/Secondary: RPR/VDRL at 3, 6, and 12 months
• Expected response: 4-fold decline in titer by 6 months
• Latent: Titers at 6, 12, and 24 months
• Treatment failure: Titer increases 4-fold or fails to decline
• HIV-positive: More frequent monitoring; may need CSF exam
• Pregnancy: Monthly titers until delivery

HERPES (HSV):
• First episode: Follow-up not routinely needed if symptoms resolve
• Recurrent: Discuss suppressive therapy if frequent (≥6/year)
• Suppressive therapy: Annual reassessment of need

GENITAL WARTS (HPV):
• Treatment completed: Follow-up at 3 months for recurrence
• Most clear within 2 years; may recur
• Cervical cytology per screening guidelines

HIV-POSITIVE PATIENTS:
• Link to care within 7 days of diagnosis
• Baseline CD4 and viral load
• ART initiation (ideally same day or within 7 days)
• Repeat STI screening every 3-6 months if ongoing risk

PARTNER FOLLOW-UP:
• Verify partners received treatment
• If not treated, patient remains at risk for reinfection

PREVENTION FOLLOW-UP:
• Reassess risk behaviors
• Reinforce condom use
• Complete Hepatitis B vaccination series
• Consider PrEP for high-risk HIV-negative individuals

PREGNANCY:
• Repeat syphilis at 28-32 weeks and delivery
• HIV retest in third trimester if high-risk
• Ensure adequate treatment documented for delivery planning`
    },

    redFlags: [
      '🔴 CRITICAL: Positive syphilis in pregnancy - EMERGENCY; untreated causes stillbirth, congenital syphilis in up to 80%',
      '🔴 CRITICAL: Headache, vision changes, or neurological symptoms with syphilis - suspect neurosyphilis; needs CSF examination',
      '🔴 CRITICAL: Severe abdominal pain with fever and cervical motion tenderness - severe PID; risk of tubo-ovarian abscess',
      '🔴 CRITICAL: Joint swelling, pustular rash, tenosynovitis - disseminated gonococcal infection; requires IV antibiotics',
      '🔴 CRITICAL: Extensive painful genital ulceration in immunocompromised - may be severe HSV needing IV acyclovir',
      '🟠 URGENT: Fluctuant inguinal bubo - requires aspiration to prevent spontaneous rupture and fistula formation',
      '🟠 URGENT: New HIV diagnosis - urgent linkage to HIV care for ART initiation',
      '🟠 URGENT: Urinary retention with genital herpes - sacral radiculopathy; may need catheterization',
      '🟠 URGENT: Treatment failure with gonococcal infection - culture needed; possible antimicrobial resistance',
      '🟠 URGENT: Jarisch-Herxheimer reaction after syphilis treatment - fever, rigors within 24 hours; supportive care needed',
      '🟡 CAUTION: Secondary syphilis rash on palms and soles - highly infectious; immediate treatment needed',
      '🟡 CAUTION: Anal STI symptoms in MSM - screen for gonorrhea, chlamydia, syphilis at all exposure sites',
      '🟡 CAUTION: Recurrent genital ulcers despite treatment - confirm diagnosis; consider HSV suppression',
      '🟡 CAUTION: Young adolescent with STI - assess for sexual abuse; mandatory reporting may apply'
    ],

    complications: [
      'Pelvic Inflammatory Disease (PID) - Ascending infection from chlamydia/gonorrhea; causes chronic pelvic pain, infertility, ectopic pregnancy',
      'Tubo-ovarian abscess - Severe PID complication; requires IV antibiotics and possibly drainage',
      'Infertility - Tubal damage from untreated chlamydia/gonorrhea; affects 10-20% after single PID episode',
      'Ectopic pregnancy - 6-10 fold increased risk after PID',
      'Chronic pelvic pain - Affects 20-30% of women after PID',
      'Congenital syphilis - Stillbirth, neonatal death, deformities; entirely preventable with prenatal treatment',
      'Neurosyphilis - CNS invasion; meningitis, stroke, dementia, tabes dorsalis; can occur at any stage',
      'Cardiovascular syphilis - Aortitis, aortic aneurysm; occurs in tertiary syphilis',
      'Neonatal herpes - HSV transmission during delivery; high mortality; requires C-section if active lesions at delivery',
      'Cervical cancer - High-risk HPV causes virtually all cervical cancers; preventable with vaccination and screening',
      'Disseminated gonococcal infection - Arthritis, dermatitis, tenosynovitis; rarely endocarditis, meningitis',
      'Epididymo-orchitis - Complication of urethritis; testicular pain and swelling; may affect fertility',
      'Reactive arthritis - Post-chlamydial or post-gonococcal; triad of arthritis, urethritis, conjunctivitis',
      'HIV acquisition - Genital ulcers increase HIV risk 2-5 fold; bidirectional relationship with STIs',
      'Antimicrobial resistance - Untreated or inadequately treated infections drive resistance; major threat for gonorrhea'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Bacterial STIs (gonorrhea, chlamydia, syphilis): Curable with appropriate antibiotics
• Viral STIs (HSV, HPV, HIV, Hepatitis B): Not curable but manageable
• Early treatment prevents complications and transmission
• Untreated infections lead to significant morbidity

SPECIFIC PROGNOSIS:

Gonorrhea:
• >95% cure with dual therapy (ceftriaxone + azithromycin)
• Rising resistance threatens treatment options
• Complications preventable with early treatment

Chlamydia:
• >95% cure with azithromycin or doxycycline
• Reinfection common (10-20%) - retest at 3 months
• PID risk reduced with early treatment

Syphilis:
• Primary/Secondary: Excellent response to single dose penicillin
• Latent: Good response; may need multiple doses
• Tertiary/Neurosyphilis: Treatment halts progression but damage may be irreversible
• Congenital: Preventable; treatment in pregnancy highly effective

Herpes (HSV):
• Incurable; lifelong latent infection
• First episode most severe; recurrences typically milder
• Suppressive therapy reduces transmission and recurrences
• Antiviral treatment does not eliminate virus

HPV:
• Most infections clear spontaneously within 2 years
• Persistent high-risk HPV may cause cancer (cervical, anal, oropharyngeal)
• Genital warts: May recur; most eventually clear
• Vaccination highly effective for prevention

PROGNOSTIC FACTORS:

Better Outcomes:
• Early diagnosis and treatment
• Partner treatment (prevents reinfection)
• Completion of full antibiotic course
• Behavioral change (condom use, fewer partners)
• HIV-negative status
• Access to follow-up care

Worse Outcomes:
• Delayed treatment
• Antimicrobial resistance
• HIV co-infection
• Recurrent infections
• Pregnancy (higher stakes for complications)
• Lack of partner treatment`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

Behavioral Interventions:
• Consistent and correct condom use (male and female condoms)
• Reducing number of sexual partners
• Mutual monogamy with uninfected partner
• Abstinence (100% effective if adhered to)
• Avoiding sex while symptomatic or during treatment
• Avoiding sex under influence of alcohol/drugs

Vaccination:
• Hepatitis B: Universal vaccination; 3-dose series; highly effective
• HPV: Ideally before sexual debut; approved ages vary (9-45 years); prevents cervical cancer and genital warts
• Hepatitis A: Recommended for MSM; food/water-borne but sexual transmission occurs

Biomedical Interventions:
• HIV PrEP: Daily TDF/FTC for high-risk HIV-negative individuals
• Male circumcision: Reduces HIV acquisition by 60%; modest effect on some other STIs
• Microbicides: Dapivirine ring approved for HIV prevention in women

Structural Interventions:
• Education and awareness programs
• Access to sexual health services
• Stigma reduction
• Legal protections for key populations

SECONDARY PREVENTION:
• Screening high-risk populations (pregnant women, MSM, sex workers, youth)
• Routine STI testing integrated with HIV services
• Partner notification and treatment
• Expedited partner therapy where available

TERTIARY PREVENTION:
• Effective treatment to prevent complications
• Prevention of mother-to-child transmission (HIV, syphilis, Hepatitis B, herpes)
• Cervical cancer screening for HPV-related dysplasia
• Regular monitoring of chronic viral infections

SPECIAL POPULATIONS:

Pregnant Women:
• Universal screening: HIV, syphilis, Hepatitis B at first antenatal visit
• Syphilis: Repeat in third trimester and at delivery in high-prevalence areas
• Treat ALL positive results promptly

Men Who Have Sex with Men:
• At least annual screening for HIV, syphilis, gonorrhea, chlamydia
• More frequent (every 3-6 months) if multiple partners
• Screen at all exposure sites (urethral, rectal, pharyngeal)
• Hepatitis A and B vaccination
• PrEP for HIV-negative at high risk

Sex Workers:
• Regular STI screening (monthly to quarterly)
• 100% condom policies
• Access to non-judgmental services
• PrEP consideration`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT ARE SEXUALLY TRANSMITTED INFECTIONS?
STIs are infections passed from one person to another through sexual contact (vaginal, anal, or oral sex). Some can also spread through blood or from mother to baby during pregnancy or childbirth. Many STIs can be cured with medicine, but some (like HIV and herpes) cannot be cured - only controlled. The good news is that ALL STIs can be prevented.

COMMON STIs:
• Gonorrhea and Chlamydia: Cause discharge and burning; can damage reproductive organs if untreated
• Syphilis: Starts as a painless sore; can become very serious if not treated
• Herpes: Causes painful blisters that come and go; cannot be cured but can be controlled
• Genital Warts (HPV): Causes growths; some types cause cancer; vaccine available
• HIV: Attacks the immune system; no cure but treatment allows normal life
• Trichomoniasis: Causes discharge and itching; easily cured

WARNING SIGNS - SEE A HEALTH WORKER IF:
• Unusual discharge from penis, vagina, or rectum
• Burning or pain when urinating
• Sores, bumps, or blisters on genitals, rectum, or mouth
• Itching or irritation in genital area
• Pain during sex
• Lower abdominal pain (women)
• Swollen glands in the groin
• Rash on body, especially palms and soles
• Your partner tells you they have an STI

IMPORTANT - YOU MAY HAVE NO SYMPTOMS!
Many people with STIs have no symptoms at all but can still:
• Pass the infection to partners
• Develop serious complications later
• Pass infection to babies during pregnancy

GET TESTED if you have had unprotected sex or a new partner!

TREATMENT:
• TAKE ALL YOUR MEDICINE exactly as prescribed
• Do not share your medicine with your partner - they need their own prescription
• Do not have sex until treatment is finished AND your partner is treated
• Return for follow-up as instructed
• Get tested for HIV - it is recommended for everyone with an STI

YOUR PARTNER MUST BE TREATED:
• If you are treated but your partner is not, you will get infected again
• Inform your partner(s) so they can be tested and treated
• Your health worker can help notify partners confidentially

PREVENTION:
• Use condoms correctly every time you have sex
• Limit your number of sexual partners
• Get tested regularly if you are sexually active
• Get vaccinated for Hepatitis B and HPV
• Talk with your partner about STIs and testing
• Avoid sex when you or your partner has symptoms

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. STIs are treatable, but some can cause serious problems if not treated. Do not be embarrassed - health workers are here to help, not judge. Early treatment protects you and your partners.

QUESTIONS TO ASK YOUR HEALTH WORKER:
• What STI do I have?
• What medicine do I need and for how long?
• Does my partner need to be treated?
• When can I have sex again?
• Should I be tested for HIV?
• How can I prevent getting this again?`,

    references: [
      'World Health Organization. Global Health Sector Strategies on HIV, Viral Hepatitis and STIs 2022-2030. WHO. 2022.',
      'World Health Organization. Guidelines for the Management of Symptomatic Sexually Transmitted Infections. WHO. 2021.',
      'Workowski KA, Bachmann LH, et al. Sexually Transmitted Infections Treatment Guidelines, 2021. MMWR Recomm Rep. 2021;70(4):1-187.',
      'Rowley J, Vander Hoorn S, et al. Chlamydia, gonorrhoea, trichomoniasis and syphilis: global prevalence and incidence estimates, 2016. Bull World Health Organ. 2019;97(8):548-562P.',
      'Unemo M, Seifert HS, et al. Gonorrhoea. Nat Rev Dis Primers. 2019;5(1):79.',
      'Korenromp EL, Rowley J, et al. Global burden of maternal and congenital syphilis and associated adverse birth outcomes. PLoS One. 2019;14(7):e0211720.',
      'James C, Harfouche M, et al. Herpes simplex virus: global infection prevalence and incidence estimates, 2016. Bull World Health Organ. 2020;98(5):315-329.',
      'Bruni L, Albero G, et al. Human Papillomavirus and Related Diseases Report. ICO/IARC HPV Information Centre. 2023.',
      'Wi T, Lahra MM, et al. Antimicrobial resistance in Neisseria gonorrhoeae: Global surveillance and a call for international collaborative action. PLoS Med. 2017;14(7):e1002344.',
      'Kularatne RS, Niit R, et al. Adult gonorrhoea, chlamydia and syphilis prevalence, incidence, treatment and syndromic case reporting in South Africa. BMC Infect Dis. 2018;18:175.',
      'Chico RM, Mayaud P, et al. Prevalence of malaria and sexually transmitted and reproductive tract infections in pregnancy in sub-Saharan Africa: a systematic review. JAMA. 2012;307(19):2079-2086.',
      'Gottlieb SL, Low N, et al. Screening and treatment to prevent sequelae in women with Chlamydia trachomatis genital infection. Lancet Infect Dis. 2010;10(8):544-556.',
      'Hook EW 3rd. Syphilis. Lancet. 2017;389(10078):1550-1557.',
      'Tuddenham S, Hamill MM, Ghanem KG. Diagnosis and Treatment of Sexually Transmitted Infections: A Review. JAMA. 2022;327(2):161-172.',
      'National Department of Health, South Africa. Sexually Transmitted Infections Management Guidelines 2018. Adapted WHO Guidelines for Africa. 2018.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 63. PELVIC INFLAMMATORY DISEASE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '63',
    name: 'Pelvic Inflammatory Disease',
    slug: 'pelvic-inflammatory-disease',
    specialty: 'Obstetrics',
    bodySystem: 'Reproductive',
    icdCode: 'N73.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Pelvic inflammatory disease (PID) is an infection of the upper female genital tract involving the uterus (endometritis), fallopian tubes (salpingitis), ovaries (oophoritis), and surrounding pelvic structures. It represents one of the most serious complications of sexually transmitted infections, affecting an estimated 4.4% of reproductive-age women globally. PID is the leading preventable cause of infertility worldwide and a major contributor to ectopic pregnancy and chronic pelvic pain.

The pathophysiology involves ascending infection from the lower genital tract, with Chlamydia trachomatis and Neisseria gonorrhoeae responsible for 50-65% of cases. However, PID is typically polymicrobial, also involving anaerobes (Bacteroides, Peptostreptococcus), facultative gram-negative bacteria (E. coli, Haemophilus influenzae), Mycoplasma genitalium, and vaginal flora. The infection causes inflammation, edema, and tubal damage that can lead to scarring, adhesions, and permanent structural changes even after successful antimicrobial treatment.

In sub-Saharan Africa, PID prevalence is substantially higher than in developed regions due to high STI rates, limited access to early treatment, and barriers to healthcare. Studies report PID prevalence of 5-15% among women attending gynecology clinics in various African countries. The condition disproportionately affects young women (15-25 years) during peak reproductive years, and tubal factor infertility accounts for 40-80% of female infertility in Africa compared to 10-30% in developed countries. HIV co-infection complicates PID management and may increase severity.

Early recognition and prompt treatment are essential to prevent long-term sequelae. Even subclinical PID can cause tubal damage, emphasizing the importance of screening and treating STIs before complications develop. The diagnosis is clinical, and a low threshold for empiric treatment is recommended given the serious consequences of missed diagnosis.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Maintain LOW threshold for diagnosis and treatment - consequences of untreated PID (infertility, ectopic pregnancy) are severe and irreversible

🔴 CRITICAL POINT 2 - ALWAYS rule out ectopic pregnancy in any woman with pelvic pain - obtain pregnancy test before treating as PID

🔴 CRITICAL POINT 3 - Treatment must cover Chlamydia, Gonorrhea, AND anaerobes - monotherapy is inadequate

🔴 CRITICAL POINT 4 - Hospitalize for: surgical emergency not excluded, tubo-ovarian abscess, pregnancy, severe illness, unable to tolerate oral therapy, or no response to outpatient treatment

🟠 CRITICAL POINT 5 - Sexual partners from past 60 days MUST be treated empirically regardless of their symptoms or test results

🟠 CRITICAL POINT 6 - Remove IUD only if no clinical improvement after 48-72 hours of antibiotics; do not delay treatment to remove IUD

🟠 CRITICAL POINT 7 - HIV-positive women with PID may have more severe disease but respond to same antibiotic regimens

🟡 CRITICAL POINT 8 - Test ALL PID patients for HIV, syphilis, and hepatitis B - presence of PID indicates high-risk sexual behavior

🟡 CRITICAL POINT 9 - Each episode of PID doubles the risk of infertility - emphasize prevention and partner treatment`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Affects approximately 4.4% of reproductive-age women worldwide
• 1-2 million new cases annually in the United States alone
• Peak incidence: Ages 15-25 years
• Leading cause of preventable infertility globally
• Accounts for 30% of all infertility cases in developed countries
• 15-20% of women with PID develop tubo-ovarian abscess
• Ectopic pregnancy risk increases 6-10 fold after PID
• Recurrent PID in 20-25% of women after initial episode

AFRICAN EPIDEMIOLOGY:
• Prevalence 5-15% in gynecology clinic populations
• Higher rates due to high STI prevalence and limited access to care
• Tubal factor infertility: 40-80% of female infertility (vs 10-30% in developed countries)
• Nigeria: Studies report 10-20% PID prevalence in some populations
• South Africa: High burden associated with HIV epidemic
• East Africa: Often presents late with complications
• Subclinical PID likely very common but underdiagnosed
• Post-abortal and post-partum PID significant in settings with unsafe procedures
• HIV co-infection: May have higher complication rates

RISK FACTORS:
• Sexually transmitted infections (chlamydia, gonorrhea) - PRIMARY RISK
• Multiple sexual partners
• New sexual partner in past 30 days
• Age 15-25 years (cervical ectopy, risk behaviors)
• Previous PID episode (20-25% recurrence)
• Bacterial vaginosis
• Intrauterine device (IUD) - primarily first 3 weeks after insertion
• Uterine instrumentation (D&C, hysteroscopy, endometrial biopsy)
• Unsafe abortion or post-partum period
• Vaginal douching
• Lower socioeconomic status
• HIV infection (increased susceptibility)
• Lack of barrier contraception
• Early sexual debut`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CARDINAL FEATURES:
• Bilateral lower abdominal/pelvic pain - most common symptom (>90%)
• Abnormal vaginal discharge - mucopurulent cervical discharge
• Adnexal tenderness on bimanual examination
• Cervical motion tenderness (chandelier sign)
• Uterine tenderness

ADDITIONAL SYMPTOMS:
• Abnormal uterine bleeding (intermenstrual, postcoital)
• Dyspareunia (pain during intercourse)
• Fever (present in <50%; suggests more severe disease)
• Nausea and vomiting (suggests severe PID or abscess)
• Right upper quadrant pain (Fitz-Hugh-Curtis syndrome - perihepatitis)
• Urinary symptoms (if concurrent UTI)
• Recent onset symptoms (typically <2 weeks)

CLINICAL SPECTRUM:

Mild/Subclinical PID:
• Minimal symptoms; may be diagnosed incidentally
• Lower abdominal discomfort
• Subtle cervical discharge
• Still causes tubal damage despite minimal symptoms

Moderate PID:
• Obvious pelvic pain limiting activity
• Cervical motion and adnexal tenderness
• Abnormal discharge
• Low-grade fever
• Elevated inflammatory markers

Severe PID:
• High fever (>38.3°C)
• Significant abdominal tenderness with guarding
• Pelvic mass suggesting tubo-ovarian abscess (TOA)
• Nausea, vomiting, unable to tolerate oral intake
• Signs of sepsis

PHYSICAL EXAMINATION:

Abdominal Examination:
• Bilateral lower abdominal tenderness
• Guarding and rebound in severe cases
• Right upper quadrant tenderness (perihepatitis)
• Palpable mass (tubo-ovarian abscess - advanced disease)

Speculum Examination:
• Mucopurulent cervical discharge (yellow/green)
• Cervical friability (bleeds easily when touched)
• Vaginal discharge abnormalities

Bimanual Examination:
• Cervical motion tenderness - highly sensitive for PID
• Uterine tenderness
• Adnexal tenderness (unilateral or bilateral)
• Adnexal mass or fullness (suggests TOA)

FITZ-HUGH-CURTIS SYNDROME:
• Perihepatitis associated with PID
• Right upper quadrant pain and tenderness
• May mimic cholecystitis or hepatitis
• Violin string adhesions between liver capsule and peritoneum
• Occurs in 10-20% of PID cases
• More common with chlamydial infection`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL ASSESSMENT
• History: Pelvic pain, discharge, bleeding, sexual history, contraception
• Risk assessment: Age, partners, previous STIs, recent procedures
• Physical examination: Abdominal, speculum, bimanual

STEP 2: MINIMUM CRITERIA FOR EMPIRIC TREATMENT (CDC)
One or more of the following in a sexually active woman with pelvic pain AND no other identifiable cause:
• Cervical motion tenderness, OR
• Uterine tenderness, OR
• Adnexal tenderness

STEP 3: ADDITIONAL CRITERIA (Increase Diagnostic Specificity)
• Oral temperature >38.3°C (101°F)
• Abnormal cervical mucopurulent discharge or cervical friability
• Abundant WBCs on saline wet mount of vaginal fluid
• Elevated ESR or CRP
• Laboratory confirmation of cervical N. gonorrhoeae or C. trachomatis

STEP 4: EXCLUDE SURGICAL EMERGENCIES
• Ectopic pregnancy (pregnancy test MANDATORY)
• Appendicitis
• Ovarian torsion
• Ruptured ovarian cyst

STEP 5: ASSESS FOR COMPLICATIONS
• Tubo-ovarian abscess (pelvic mass, severe illness)
• Perihepatitis (RUQ pain)
• Sepsis

DIAGNOSTIC CRITERIA:

Definitive Diagnosis (rarely needed clinically):
• Laparoscopy showing tubal inflammation
• Endometrial biopsy showing endometritis
• Imaging showing thickened, fluid-filled tubes with or without free pelvic fluid or tubo-ovarian complex

Presumptive Diagnosis (sufficient for treatment):
• Pelvic pain + cervical motion tenderness or uterine/adnexal tenderness
• No other identifiable cause
• Low threshold due to serious sequelae of untreated disease

CLASSIFICATION BY SEVERITY:

Mild PID:
• Low-grade symptoms
• Temperature <38.3°C
• WBC <10,000
• No peritoneal signs
• Outpatient treatment appropriate

Moderate PID:
• More pronounced symptoms
• May have low-grade fever
• Elevated inflammatory markers
• Ambulatory treatment possible if able to tolerate oral medications

Severe PID:
• Temperature ≥38.3°C
• WBC ≥10,000
• Peritoneal signs
• Tubo-ovarian abscess
• Unable to tolerate oral medications
• Requires hospitalization`,

    differentialDiagnosis: [
      'Ectopic pregnancy - MUST EXCLUDE; positive pregnancy test, adnexal mass, hemoperitoneum on ultrasound',
      'Acute appendicitis - RLQ pain, anorexia, migration of pain, may have peritoneal signs; imaging helpful',
      'Ovarian torsion - Sudden severe unilateral pain, nausea/vomiting, adnexal mass with absent Doppler flow',
      'Ruptured ovarian cyst - Sudden pain, may be mid-cycle, free fluid on ultrasound, resolves with conservative management',
      'Endometriosis - Chronic cyclic pelvic pain, dysmenorrhea, dyspareunia; laparoscopy for diagnosis',
      'Urinary tract infection - Dysuria, frequency, urgency; pyuria on urinalysis; may coexist with PID',
      'Nephrolithiasis - Colicky flank pain radiating to groin, hematuria, stone on imaging',
      'Irritable bowel syndrome - Chronic symptoms, altered bowel habits, no fever or inflammatory markers',
      'Inflammatory bowel disease - Chronic diarrhea, bloody stool, systemic symptoms; endoscopy diagnostic',
      'Diverticulitis - LLQ pain (typically), fever, elevated WBC; CT shows colonic inflammation',
      'Ovarian cyst (unruptured) - May cause pain; ultrasound shows simple cyst; no fever or discharge',
      'Uterine fibroids (degenerating) - Pelvic pain, heavy periods, enlarged uterus; ultrasound diagnostic',
      'Primary dysmenorrhea - Crampy pain with menses only, no fever, no discharge',
      'Mittelschmerz - Mid-cycle ovulatory pain, brief duration, no fever or discharge',
      'Pelvic adhesions - Chronic pain, history of prior surgery or infection; laparoscopy for diagnosis'
    ],

    investigations: [
      {
        name: 'Pregnancy Test (Urine or Serum β-hCG)',
        indication: '🔴 ESSENTIAL - MUST exclude ectopic pregnancy before treating as PID; mandatory in all reproductive-age women',
        availability: 'BASIC'
      },
      {
        name: 'Vaginal Wet Mount Microscopy',
        indication: '🔴 ESSENTIAL - Abundant WBCs supports PID diagnosis; also identifies trichomonas, bacterial vaginosis, candida',
        availability: 'BASIC'
      },
      {
        name: 'Gram Stain of Cervical Discharge',
        indication: '🔴 ESSENTIAL - Intracellular gram-negative diplococci suggest gonorrhea; WBCs indicate cervicitis',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Leukocytosis supports infection; helps assess severity; may be normal in mild PID',
        availability: 'BASIC'
      },
      {
        name: 'Erythrocyte Sedimentation Rate (ESR)',
        indication: '🟠 IMPORTANT - Elevated in PID; supports diagnosis when elevated; non-specific',
        availability: 'BASIC'
      },
      {
        name: 'C-Reactive Protein (CRP)',
        indication: '🟠 IMPORTANT - More specific than ESR; elevated values support PID diagnosis and indicate severity',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'NAAT for Chlamydia and Gonorrhea',
        indication: '🟠 IMPORTANT - Identifies causative organisms; most sensitive test; guides partner management',
        availability: 'ADVANCED'
      },
      {
        name: 'Gonorrhea Culture',
        indication: '🟠 IMPORTANT - Allows antimicrobial susceptibility testing; important given resistance concerns',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Rapid Test',
        indication: '🔴 ESSENTIAL - Test ALL PID patients; high-risk population; HIV affects prognosis and follow-up',
        availability: 'BASIC'
      },
      {
        name: 'Syphilis Serology (RPR/VDRL)',
        indication: '🟠 IMPORTANT - Screen all PID patients; presence of one STI indicates risk for others',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Surface Antigen',
        indication: '🟠 IMPORTANT - Screen and vaccinate if negative; sexual transmission route',
        availability: 'BASIC'
      },
      {
        name: 'Pelvic Ultrasound (Transvaginal preferred)',
        indication: '🟠 IMPORTANT - Identifies tubo-ovarian abscess, free fluid, tubal thickening; excludes ectopic pregnancy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Abdomen/Pelvis',
        indication: '🟡 WHEN AVAILABLE - Complex cases; suspected abscess; excludes appendicitis when diagnosis uncertain',
        availability: 'ADVANCED'
      },
      {
        name: 'MRI Pelvis',
        indication: '🟡 WHEN AVAILABLE - Best soft tissue detail; characterizes adnexal pathology; radiation-free',
        availability: 'ADVANCED'
      },
      {
        name: 'Urinalysis',
        indication: '🟠 IMPORTANT - Excludes UTI as cause of symptoms; may coexist with PID',
        availability: 'BASIC'
      },
      {
        name: 'Laparoscopy',
        indication: '🟡 WHEN AVAILABLE - Gold standard for diagnosis; rarely needed; reserved for diagnostic uncertainty or treatment (drainage)',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ PREGNANCY TEST: Obtain IMMEDIATELY - must exclude ectopic pregnancy before treating as PID',
        '⚡ ASSESS SEVERITY: Determine if outpatient or inpatient management appropriate',
        '⚡ VITAL SIGNS: Temperature, heart rate, blood pressure; assess for sepsis',
        '⚡ PELVIC EXAMINATION: Confirm cervical motion tenderness, adnexal tenderness',
        '⚡ COLLECT SPECIMENS: Endocervical swabs for gonorrhea/chlamydia BEFORE antibiotics',
        '⚡ START EMPIRIC ANTIBIOTICS: Do not delay treatment for test results',
        '⚡ HIV AND SYPHILIS TESTING: Offer to all patients',
        '⚡ ANALGESIA: NSAIDs or paracetamol for pain relief',
        '⚡ PARTNER NOTIFICATION: Counsel on need for partner treatment',
        '⚡ ABSTINENCE: Advise no intercourse until treatment complete and partner(s) treated'
      ],
      
      pharmacological: [
        {
          drug: 'Ceftriaxone + Doxycycline + Metronidazole (OUTPATIENT REGIMEN 1)',
          indication: '🔴 FIRST-LINE - Standard outpatient regimen; covers gonorrhea, chlamydia, and anaerobes',
          dose: 'Ceftriaxone 500mg IM single dose PLUS Doxycycline 100mg PO twice daily for 14 days PLUS Metronidazole 500mg PO twice daily for 14 days',
          route: 'IM + PO',
          duration: '14 days total',
          contraindications: ['Cephalosporin allergy', 'Pregnancy (doxycycline contraindicated)', 'Metronidazole: first trimester (relative)'],
          sideEffects: ['GI upset', 'Photosensitivity (doxycycline)', 'Metallic taste (metronidazole)'],
          alternatives: ['Cefoxitin-based regimen if ceftriaxone unavailable']
        },
        {
          drug: 'Cefoxitin + Probenecid + Doxycycline (OUTPATIENT REGIMEN 2)',
          indication: '🟠 ALTERNATIVE - When ceftriaxone unavailable; good anaerobic coverage',
          dose: 'Cefoxitin 2g IM single dose WITH Probenecid 1g PO (given together) PLUS Doxycycline 100mg PO twice daily for 14 days (with or without Metronidazole)',
          route: 'IM + PO',
          duration: '14 days',
          contraindications: ['Cephalosporin allergy', 'Pregnancy', 'Severe renal impairment (probenecid)'],
          sideEffects: ['Injection site pain', 'GI upset', 'Photosensitivity'],
          alternatives: ['Ceftriaxone-based regimen']
        },
        {
          drug: 'Ceftriaxone + Metronidazole + Azithromycin (PREGNANCY REGIMEN)',
          indication: '🔴 FIRST-LINE IN PREGNANCY - When PID occurs in pregnancy; azithromycin replaces doxycycline',
          dose: 'Ceftriaxone 500mg IM single dose (or 1g IV daily if admitted) PLUS Metronidazole 500mg PO/IV twice daily for 14 days PLUS Azithromycin 1g PO single dose (for chlamydia coverage)',
          route: 'IM/IV + PO',
          duration: '14 days',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['GI upset', 'Metallic taste'],
          alternatives: ['Consult specialist for alternative regimens']
        },
        {
          drug: 'Ceftriaxone + Doxycycline + Metronidazole IV (INPATIENT REGIMEN 1)',
          indication: '🔴 FIRST-LINE INPATIENT - Severe PID, tubo-ovarian abscess, unable to tolerate oral',
          dose: 'Ceftriaxone 1g IV every 24 hours PLUS Doxycycline 100mg IV/PO every 12 hours PLUS Metronidazole 500mg IV every 8 hours. Continue IV until 24-48 hours afebrile, then switch to oral.',
          route: 'IV',
          duration: 'IV until improved, then oral to complete 14 days',
          contraindications: ['Cephalosporin allergy', 'Pregnancy (doxycycline)'],
          sideEffects: ['Phlebitis', 'Diarrhea', 'GI upset'],
          alternatives: ['Clindamycin + Gentamicin regimen']
        },
        {
          drug: 'Clindamycin + Gentamicin (INPATIENT REGIMEN 2)',
          indication: '🟠 ALTERNATIVE INPATIENT - Excellent anaerobic coverage; useful if cephalosporin allergy',
          dose: 'Clindamycin 900mg IV every 8 hours PLUS Gentamicin 2mg/kg IV loading, then 1.5mg/kg every 8 hours (or 5mg/kg once daily). Continue until 24-48 hours afebrile, then Doxycycline 100mg PO twice daily OR Clindamycin 450mg PO four times daily to complete 14 days.',
          route: 'IV',
          duration: 'IV until improved, then oral to complete 14 days',
          contraindications: ['Clindamycin allergy', 'Myasthenia gravis (gentamicin)', 'Renal impairment (adjust gentamicin)'],
          sideEffects: ['C. difficile colitis', 'Nephrotoxicity', 'Ototoxicity'],
          alternatives: ['Ampicillin-sulbactam + Doxycycline']
        },
        {
          drug: 'Ampicillin-Sulbactam + Doxycycline (INPATIENT REGIMEN 3)',
          indication: '🟠 ALTERNATIVE INPATIENT - Broad spectrum; covers most PID pathogens',
          dose: 'Ampicillin-Sulbactam 3g IV every 6 hours PLUS Doxycycline 100mg IV/PO every 12 hours. Continue until 24-48 hours afebrile, then oral to complete 14 days.',
          route: 'IV',
          duration: 'IV until improved, then oral to complete 14 days',
          contraindications: ['Penicillin allergy', 'Pregnancy (doxycycline)'],
          sideEffects: ['Rash', 'Diarrhea', 'Phlebitis'],
          alternatives: ['Clindamycin + Gentamicin']
        },
        {
          drug: 'Azithromycin (Chlamydia Coverage)',
          indication: '🟠 COMPONENT - Alternative to doxycycline for chlamydia coverage; useful in pregnancy or poor compliance',
          dose: '1g PO single dose (may need to repeat weekly if prolonged treatment). Less data supporting use in PID than doxycycline.',
          route: 'PO',
          duration: 'Single dose or weekly for duration',
          contraindications: ['Macrolide allergy', 'QT prolongation'],
          sideEffects: ['GI upset', 'Nausea', 'QT prolongation'],
          alternatives: ['Doxycycline']
        },
        {
          drug: 'Metronidazole',
          indication: '🔴 ESSENTIAL COMPONENT - Anaerobic coverage; included in most PID regimens',
          dose: '500mg PO/IV twice daily for 14 days. Avoid alcohol during treatment and for 48 hours after completion.',
          route: 'PO / IV',
          duration: '14 days',
          contraindications: ['First trimester pregnancy (relative)', 'Alcohol use'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol'],
          alternatives: ['Clindamycin (provides anaerobic coverage)']
        },
        {
          drug: 'Ibuprofen or Naproxen',
          indication: '🟠 ADJUNCT - Pain management and anti-inflammatory effect',
          dose: 'Ibuprofen 400-600mg PO every 6-8 hours OR Naproxen 500mg PO twice daily. Take with food.',
          route: 'PO',
          duration: 'As needed during acute phase',
          contraindications: ['Peptic ulcer disease', 'Renal impairment', 'Third trimester pregnancy'],
          sideEffects: ['GI upset', 'Renal effects', 'Bleeding risk'],
          alternatives: ['Paracetamol']
        }
      ],
      
      nonPharmacological: [
        'BED REST: Advise rest during acute phase; semi-Fowler position may improve comfort and promote drainage',
        'HYDRATION: Encourage adequate oral fluid intake; IV fluids if unable to tolerate oral',
        'ABSTINENCE: No sexual intercourse until treatment complete, symptoms resolved, AND partner(s) treated',
        'PARTNER TREATMENT: Treat ALL sexual partners from past 60 days empirically for gonorrhea and chlamydia',
        'EXPEDITED PARTNER THERAPY: Provide partner medications without examination where legal and feasible',
        'IUD MANAGEMENT: Can remain in place during treatment; remove only if no improvement after 48-72 hours',
        'HEAT APPLICATION: Warm compresses to lower abdomen may provide comfort',
        'AVOID DOUCHING: Vaginal douching associated with increased PID risk; advise against',
        'NUTRITION: Adequate nutrition supports immune function and recovery',
        'CONDOM COUNSELING: Consistent condom use prevents recurrence; emphasize importance'
      ],
      
      surgical: [
        'LAPAROSCOPY: Diagnostic and therapeutic; drainage of abscesses, lysis of adhesions; rarely needed with antibiotic era',
        'ULTRASOUND-GUIDED DRAINAGE: Preferred for accessible tubo-ovarian abscesses >3cm not responding to antibiotics',
        'CT-GUIDED DRAINAGE: Alternative approach for abscess drainage when ultrasound not optimal',
        'LAPAROTOMY: Reserved for ruptured TOA, failed drainage, or hemodynamic instability',
        'SALPINGECTOMY: May be necessary for destroyed tube, chronic hydrosalpinx, or refractory infection',
        'HYSTERECTOMY WITH BILATERAL SALPINGO-OOPHORECTOMY: Rarely needed; severe refractory disease or concurrent pathology'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Surgical emergency not excluded (ectopic pregnancy, appendicitis, ovarian torsion)',
        '🔴 IMMEDIATE REFERRAL: Tubo-ovarian abscess with sepsis or peritonitis - may need surgical intervention',
        '🔴 IMMEDIATE REFERRAL: Pregnant woman with PID - high-risk pregnancy, needs specialist care',
        '🔴 IMMEDIATE REFERRAL: Hemodynamic instability or signs of septic shock',
        '🟠 URGENT REFERRAL: Tubo-ovarian abscess >3cm - may need drainage if no response to antibiotics',
        '🟠 URGENT REFERRAL: No clinical improvement after 72 hours of appropriate antibiotic therapy',
        '🟠 URGENT REFERRAL: Severe PID with high fever, peritoneal signs, or inability to tolerate oral medications',
        '🟠 URGENT REFERRAL: HIV-positive with severe PID - may have higher complication rates',
        '🟡 ROUTINE REFERRAL: Recurrent PID - evaluation for underlying cause, tubal assessment',
        '🟡 ROUTINE REFERRAL: Infertility concerns after PID - hysterosalpingography or laparoscopy',
        '🟡 ROUTINE REFERRAL: Chronic pelvic pain after PID - may need specialist evaluation',
        '🟡 ROUTINE REFERRAL: Adolescent with PID - multidisciplinary approach for prevention'
      ],
      
      monitoring: [
        'CLINICAL RESPONSE: Expect substantial improvement within 72 hours; if not improving, reassess diagnosis and treatment',
        'TEMPERATURE: Should decrease within 48-72 hours; persistent fever suggests abscess or resistant organism',
        'PAIN ASSESSMENT: Pain should improve daily; increasing pain suggests complication',
        'REPEAT EXAMINATION: Consider repeat pelvic exam at 48-72 hours if hospitalized to assess response',
        'COMPLETE TREATMENT: Ensure full 14-day course completed even if symptoms resolve earlier',
        'TEST OF CURE: NAAT at 3 months for chlamydia (high reinfection rate); not immediately post-treatment',
        'PARTNER TREATMENT VERIFICATION: Confirm partners received treatment before resuming sexual activity',
        'HIV FOLLOW-UP: If newly diagnosed, link to HIV care',
        'CONTRACEPTION COUNSELING: Discuss barrier methods to prevent recurrence',
        'FERTILITY ASSESSMENT: Consider referral if pregnancy not achieved after 12 months of trying post-PID'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE PHASE FOLLOW-UP:
• Outpatient: Re-evaluate in 48-72 hours to confirm clinical improvement
• If no improvement: Consider hospitalization, imaging for TOA, alternative diagnosis
• If improving: Continue treatment course, schedule follow-up at treatment completion

INPATIENT TO OUTPATIENT TRANSITION:
• Transition to oral antibiotics after 24-48 hours afebrile
• Ensure patient can tolerate oral medications
• Complete 14-day total antibiotic course
• Outpatient follow-up within 1 week of discharge

POST-TREATMENT FOLLOW-UP:
• Evaluate at end of treatment course
• Confirm symptom resolution
• Verify partner treatment completed
• Discuss prevention strategies

SCREENING FOR REINFECTION:
• Test for chlamydia and gonorrhea at 3 months post-treatment
• High reinfection rates (10-20%) warrant routine rescreening
• Do not test immediately after treatment (may detect non-viable organisms)

LONG-TERM CONSIDERATIONS:

Infertility:
• Risk increases with each PID episode
• 1 episode: 8-15% infertility risk
• 2 episodes: 20-35% infertility risk
• 3+ episodes: 40-75% infertility risk
• Refer if not pregnant after 12 months of unprotected intercourse

Ectopic Pregnancy:
• 6-10 fold increased risk after PID
• Early prenatal care for all pregnancies
• Early ultrasound to confirm intrauterine pregnancy

Chronic Pelvic Pain:
• Affects 20-30% of women after PID
• May need multidisciplinary management
• Consider adhesions, persistent infection, or other causes

PREVENTION OF RECURRENCE:
• Consistent condom use
• Mutual monogamy or reduced partners
• Prompt treatment of any STI symptoms
• Regular STI screening if high-risk
• Partner notification for ALL future STIs
• Consider routine chlamydia screening annually if sexually active <25 years`
    },

    redFlags: [
      '🔴 CRITICAL: Positive pregnancy test with pelvic pain - MUST EXCLUDE ECTOPIC PREGNANCY before treating as PID',
      '🔴 CRITICAL: Hemodynamic instability (tachycardia, hypotension) - indicates sepsis; requires immediate resuscitation',
      '🔴 CRITICAL: Peritonitis (rigid abdomen, rebound tenderness) - may indicate ruptured abscess or other surgical emergency',
      '🔴 CRITICAL: Pelvic mass with fever and severe pain - tubo-ovarian abscess requiring hospitalization and possible drainage',
      '🔴 CRITICAL: Unilateral severe pain with adnexal mass - consider ovarian torsion requiring urgent surgery',
      '🟠 URGENT: No improvement after 72 hours of appropriate antibiotics - consider abscess, resistant organisms, wrong diagnosis',
      '🟠 URGENT: Temperature ≥38.3°C with severe illness - indicates need for hospitalization and IV antibiotics',
      '🟠 URGENT: Unable to tolerate oral medications due to nausea/vomiting - requires IV therapy',
      '🟠 URGENT: Adolescent with PID - higher complication rates; consider admission and evaluate for compliance',
      '🟡 CAUTION: IUD in place with PID - do not remove immediately; only remove if no improvement after 48-72 hours',
      '🟡 CAUTION: HIV-positive patient with PID - same treatment but may have more severe disease',
      '🟡 CAUTION: Recurrent PID - evaluate for persistent infection, reinfection, or underlying anatomic abnormality',
      '🟡 CAUTION: Right upper quadrant pain with PID - suggests perihepatitis (Fitz-Hugh-Curtis syndrome)'
    ],

    complications: [
      'Tubo-ovarian abscess (TOA) - Occurs in 15-20%; may require drainage; can rupture causing life-threatening sepsis',
      'Infertility - Leading preventable cause; tubal damage from scarring and adhesions; risk increases with each episode',
      'Ectopic pregnancy - 6-10 fold increased risk; tubal damage impairs normal egg transport',
      'Chronic pelvic pain - Affects 20-30% of PID patients; from adhesions, persistent inflammation, or nerve damage',
      'Recurrent PID - 20-25% recurrence rate; cumulative tubal damage with each episode',
      'Fitz-Hugh-Curtis syndrome - Perihepatitis causing RUQ pain; violin string adhesions; more common with chlamydia',
      'Peritonitis - From ruptured TOA or severe ascending infection; surgical emergency',
      'Sepsis and septic shock - Rare but life-threatening complication of severe PID or ruptured abscess',
      'Tubal occlusion - Blockage from scarring; prevents natural conception; may need IVF',
      'Hydrosalpinx - Fluid-filled blocked tube; reduces IVF success; may need salpingectomy before IVF',
      'Pyosalpinx - Pus-filled fallopian tube; severe form of salpingitis',
      'Adhesive disease - Pelvic adhesions causing chronic pain, bowel obstruction, infertility',
      'Dyspareunia - Persistent pain during intercourse; significant impact on quality of life',
      'Ovarian abscess - Rare; infection involves ovarian tissue directly',
      'Mortality - Rare with modern treatment but possible with ruptured TOA and sepsis'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Clinical cure achieved in >90% with appropriate antibiotic treatment
• Long-term sequelae occur even with successful treatment due to tubal damage
• Earlier treatment associated with better reproductive outcomes
• Subclinical PID still causes tubal damage

INFERTILITY RISK:
• After 1 episode: 8-15%
• After 2 episodes: 20-35%
• After 3 or more episodes: 40-75%
• Severe PID or TOA: Higher infertility rates
• Chlamydia may cause more silent damage than gonorrhea

ECTOPIC PREGNANCY RISK:
• 6-10 fold increased risk after PID
• Cumulative risk increases with recurrent PID
• All pregnancies after PID should have early ultrasound

CHRONIC PELVIC PAIN:
• Occurs in 20-30% of women after PID
• More likely with recurrent episodes
• May persist despite adequate treatment

PROGNOSTIC FACTORS:

Better Outcomes:
• Early diagnosis and treatment
• Mild disease at presentation
• Younger age at first episode
• No prior PID history
• Chlamydia vs gonorrhea (controversial)
• Complete antibiotic course
• Partner treatment completed

Worse Outcomes:
• Delayed treatment (>3 days from symptom onset)
• Severe PID or TOA at presentation
• Recurrent episodes
• HIV co-infection
• IUD-associated PID (may be more severe)
• Gonococcal PID (more acute inflammation)
• Incomplete treatment

TUBO-OVARIAN ABSCESS:
• Medical treatment successful in 70-75%
• Drainage needed in 25-30% not responding to antibiotics
• Ruptured TOA: Surgical emergency with significant mortality risk
• Fertility significantly compromised after TOA

MORTALITY:
• Very low with modern treatment (<1%)
• Higher risk with ruptured TOA, sepsis, delayed treatment`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing STI acquisition):
• Consistent and correct condom use - most effective behavioral intervention
• Reducing number of sexual partners
• Mutual monogamy with uninfected partner
• Abstinence (100% effective if maintained)
• HPV vaccination (prevents genital warts and cervical cancer, not bacterial STIs)
• Hepatitis B vaccination
• Male circumcision (reduces HIV and some STI acquisition)

SECONDARY PREVENTION (Preventing PID in those with STIs):
• STI screening and treatment
  - Annual chlamydia screening for sexually active women <25 years
  - Screening for higher-risk women at any age
  - Prompt treatment of positive results
• Partner notification and treatment
  - Treat all partners from past 60 days
  - Expedited partner therapy where available
• Avoiding douching (disrupts vaginal flora, increases ascending infection risk)
• Prompt evaluation of discharge or pelvic pain symptoms

TERTIARY PREVENTION (Preventing complications in those with PID):
• Early recognition and treatment of PID
• Low threshold for diagnosis - treat suspected cases
• Complete antibiotic courses
• Partner treatment to prevent reinfection
• Follow-up to confirm treatment response
• Screening at 3 months for reinfection

IUD-RELATED PREVENTION:
• Screen for STIs before IUD insertion
• Treat any STIs before insertion
• Risk of PID highest in first 20 days after insertion
• IUD does not need removal if PID develops (unless no improvement with treatment)

SPECIAL POPULATIONS:

Adolescents:
• Higher PID rates due to cervical ectopy and risk behaviors
• Annual chlamydia screening recommended
• Confidential services important
• Education on prevention

HIV-positive women:
• More susceptible to STIs and PID
• Regular STI screening
• Prompt treatment of any symptoms

Sex workers:
• Regular STI screening (monthly to quarterly)
• 100% condom policies
• Access to non-judgmental healthcare

PUBLIC HEALTH MEASURES:
• STI surveillance and partner notification programs
• Public education and awareness
• Access to affordable STI testing and treatment
• Reducing stigma associated with STI care`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS PELVIC INFLAMMATORY DISEASE?
Pelvic inflammatory disease (PID) is an infection of a womans reproductive organs - the uterus (womb), fallopian tubes, and ovaries. It usually happens when sexually transmitted infections like chlamydia or gonorrhea spread upward from the vagina. PID is serious because it can damage the tubes and cause problems with fertility (having babies) even if you feel better after treatment.

CAUSES:
• Sexually transmitted infections (STIs) - mainly chlamydia and gonorrhea
• Bacteria can spread from the vagina into the uterus and tubes
• Risk increases with multiple partners or unprotected sex
• Can sometimes occur after procedures like IUD insertion or abortion

WARNING SIGNS - SEE A HEALTH WORKER IMMEDIATELY IF:
• Pain in your lower belly (on both sides)
• Unusual discharge from your vagina
• Pain during sex
• Bleeding between periods or after sex
• Fever with belly pain
• Feeling very sick with nausea and vomiting
• Pain getting worse despite taking medicine

TREATMENT:
• You will need antibiotics - TAKE ALL YOUR MEDICINE even if you feel better
• Treatment usually lasts 14 days
• You may need to go to hospital if you are very sick
• Rest and take pain medicine as advised
• Do NOT have sex until treatment is finished AND your partner is treated

YOUR PARTNER MUST BE TREATED:
• This is VERY important - if your partner is not treated, you will get infected again
• All partners from the last 2 months should be treated
• Partners need treatment even if they have no symptoms
• Your health worker can help notify partners confidentially

WHY IS PID SERIOUS?
Even though antibiotics can cure the infection, PID can still cause lasting damage:
• Difficulty getting pregnant (the tubes can be scarred)
• Higher chance of pregnancy in the tube (ectopic pregnancy) - this is dangerous
• Long-term pelvic pain
• Each time you get PID, the risk of these problems increases

PREVENTION:
• Use condoms every time you have sex
• Limit your number of sexual partners
• Get tested for STIs regularly if you are sexually active
• If you or your partner has symptoms, get treated right away
• Do not douche (wash inside the vagina)

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. PID is a serious infection that can affect your ability to have children. Complete your full course of treatment and make sure your partner is treated too. If you want to have children in the future, protecting yourself from PID is very important.

QUESTIONS TO ASK YOUR HEALTH WORKER:
• How serious is my PID?
• Do I need to stay in hospital?
• How long do I need to take the medicine?
• When can I have sex again?
• Will this affect my ability to have children?
• Should I get tested for HIV?`,

    references: [
      'Workowski KA, Bachmann LH, et al. Sexually Transmitted Infections Treatment Guidelines, 2021. MMWR Recomm Rep. 2021;70(4):1-187.',
      'Ross JDC, Cronje HS, et al. European guideline for the management of pelvic inflammatory disease. Int J STD AIDS. 2018;29(2):108-114.',
      'Ness RB, Soper DE, et al. Effectiveness of Inpatient and Outpatient Treatment Strategies for Women with Pelvic Inflammatory Disease. Am J Obstet Gynecol. 2002;186(5):929-937.',
      'Haggerty CL, Ness RB. Epidemiology, pathogenesis and treatment of pelvic inflammatory disease. Expert Rev Anti Infect Ther. 2006;4(2):235-247.',
      'Brunham RC, Gottlieb SL, Paavonen J. Pelvic inflammatory disease. N Engl J Med. 2015;372(21):2039-2048.',
      'Mitchell C, Prabhu M. Pelvic inflammatory disease: current concepts in pathogenesis, diagnosis and treatment. Infect Dis Clin North Am. 2013;27(4):793-809.',
      'Simms I, Stephenson JM. Pelvic inflammatory disease epidemiology: what do we know and what do we need to know? Sex Transm Infect. 2000;76(2):80-87.',
      'World Health Organization. Guidelines for the Management of Symptomatic Sexually Transmitted Infections. WHO. 2021.',
      'Adekunle AO, Ladipo OA. Reproductive tract infections in Africa: challenges and prospects. Afr J Reprod Health. 2001;5(1):7-12.',
      'Moodley P, Wilkinson D, et al. High HIV incidence during pregnancy: compelling reason for repeat HIV testing. AIDS. 2004;18(10):1553-1555.',
      'Trent M, Bass D, et al. Recurrent PID, subsequent STI, and reproductive health outcomes: findings from the PID Evaluation and Clinical Health (PEACH) Study. Sex Transm Dis. 2011;38(9):879-881.',
      'Wiesenfeld HC, Sweet RL, et al. Comparison of acute and subclinical pelvic inflammatory disease. Sex Transm Dis. 2005;32(7):400-405.',
      'Eschenbach DA. Acute pelvic inflammatory disease. Glob Libr Womens Med. 2008; DOI 10.3843/GLOWM.10029.',
      'Landers DV, Sweet RL. Tubo-ovarian abscess: contemporary approach to management. Rev Infect Dis. 1983;5(5):876-884.',
      'National Institute for Health and Care Excellence (NICE). Pelvic inflammatory disease. NICE guideline [NG138]. 2019 (updated 2022).'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 64. BENIGN PROSTATIC HYPERPLASIA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '64',
    name: 'Benign Prostatic Hyperplasia',
    slug: 'benign-prostatic-hyperplasia',
    specialty: 'Nephrology',
    bodySystem: 'Renal',
    icdCode: 'N40',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Benign prostatic hyperplasia (BPH) is a non-malignant enlargement of the prostate gland that occurs almost universally in aging men. Histologically, BPH affects over 50% of men by age 60 and up to 90% by age 85. While not all men with histological BPH develop symptoms, approximately 50% will have moderate to severe lower urinary tract symptoms (LUTS) requiring treatment. BPH is the most common benign tumor in men and the leading cause of bladder outlet obstruction.

The pathophysiology involves both static and dynamic components. The static component results from physical enlargement of the prostate compressing the prostatic urethra, while the dynamic component arises from increased smooth muscle tone in the prostate stroma and bladder neck mediated by alpha-1 adrenergic receptors. The condition develops in the transition zone of the prostate surrounding the urethra and requires both androgens (particularly dihydrotestosterone) and aging for development. This understanding forms the basis for medical therapy targeting either the hormonal pathway (5-alpha reductase inhibitors) or smooth muscle relaxation (alpha-blockers).

In African settings, BPH presents unique challenges including late presentation due to limited awareness and healthcare access, cultural reluctance to discuss urinary symptoms, high rates of acute urinary retention as the presenting complaint, and limited availability of urologic surgical services. Studies from Nigeria and other African countries report BPH prevalence of 25-45% in men over 50, with many presenting at advanced stages. The condition significantly impacts quality of life, affecting sleep, social activities, and daily function. Complications include acute urinary retention, recurrent urinary tract infections, bladder stones, chronic kidney disease from obstructive uropathy, and rarely, gross hematuria.

Management ranges from watchful waiting for mild symptoms to medical therapy and surgical intervention for more severe disease. Early recognition and appropriate treatment prevent complications and preserve renal function. While BPH itself is benign, it is essential to differentiate it from prostate cancer, which may coexist.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - ALWAYS perform digital rectal exam (DRE) to assess prostate size, consistency (nodules suggest cancer), and anal tone

🔴 CRITICAL POINT 2 - Acute urinary retention is a medical emergency - catheterize immediately; do NOT delay for imaging or other tests

🔴 CRITICAL POINT 3 - Check renal function (creatinine) in all BPH patients - chronic obstruction causes silent renal damage

🔴 CRITICAL POINT 4 - PSA testing requires informed discussion; elevated PSA may indicate BPH, cancer, or prostatitis - not diagnostic alone

🟠 CRITICAL POINT 5 - Alpha-blockers provide rapid symptom relief (days to weeks); 5-alpha reductase inhibitors take 3-6 months for effect

🟠 CRITICAL POINT 6 - Warn patients about alpha-blocker side effects: postural hypotension (first-dose effect), retrograde ejaculation

🟠 CRITICAL POINT 7 - Large prostates (>40g) benefit from combination therapy (alpha-blocker + 5-ARI); combination prevents progression

🟡 CRITICAL POINT 8 - Red flags requiring urgent urology referral: hematuria, recurrent UTI, bladder stones, renal insufficiency, retention

🟡 CRITICAL POINT 9 - Prostate size does not correlate with symptom severity - treat symptoms, not the size of the gland`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Histological BPH: 50% of men by age 60; 90% by age 85
• Symptomatic BPH: Affects ~50% of men with histological BPH
• Moderate-severe LUTS: 25-30% of men over 50 years
• Leading cause of LUTS in older men
• Most common benign tumor in men
• Prevalence increases linearly with age
• Lifetime risk of surgery: 20-30% (declining with medical therapy)
• Significant economic burden: Medications, procedures, complications

AFRICAN EPIDEMIOLOGY:
• Prevalence studies: 25-45% in men over 50 years
• Nigeria: 25-40% prevalence in various studies
• Ghana: 35-45% prevalence in men >50 years
• South Africa: Similar rates to Western populations
• Late presentation common due to limited healthcare access
• Higher rates of acute urinary retention as presenting symptom
• Limited access to urologic surgery in rural areas
• Traditional medicine often tried first, delaying presentation
• Prostate cancer may be underdiagnosed (masquerading as BPH)

RISK FACTORS:
• Age - strongest risk factor; rare before 40, increases linearly after
• Family history - 4-fold increased risk with first-degree relative
• Genetics - certain polymorphisms associated with risk
• Obesity - metabolic syndrome associated with BPH/LUTS
• Diabetes mellitus - increases risk and symptom severity
• Physical inactivity - sedentary lifestyle increases risk
• Diet - high fat, red meat may increase risk
• Heart disease - shared risk factors with BPH
• Testosterone levels - required for development but levels do not predict severity
• Race - African descent may have slightly higher prevalence

PROTECTIVE FACTORS:
• Physical activity
• Moderate alcohol consumption (controversial)
• Certain dietary patterns (vegetables, low fat)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

LOWER URINARY TRACT SYMPTOMS (LUTS):

Storage Symptoms (Irritative):
• Urinary frequency - voiding >8 times per day
• Nocturia - waking ≥2 times at night to void (very bothersome)
• Urgency - sudden compelling desire to urinate
• Urge incontinence - leakage associated with urgency

Voiding Symptoms (Obstructive):
• Weak urinary stream - reduced force and caliber
• Hesitancy - difficulty initiating urination
• Intermittency - stop-start stream
• Straining - need to push to void
• Prolonged voiding time
• Terminal dribbling - slow urine loss at end of voiding
• Incomplete emptying - sensation of residual urine

Post-Micturition Symptoms:
• Post-void dribbling
• Sensation of incomplete emptying

SYMPTOM ASSESSMENT - IPSS (International Prostate Symptom Score):
• Standardized 7-question assessment of LUTS
• Score 0-7: Mild symptoms
• Score 8-19: Moderate symptoms
• Score 20-35: Severe symptoms
• Includes quality of life question
• Useful for monitoring treatment response

PHYSICAL EXAMINATION:

Digital Rectal Examination (DRE):
• Essential component of evaluation
• Assess prostate size (normal ~20g, walnut-sized)
• Assess consistency - should be smooth, rubbery
• Check for nodules or induration (suggests cancer)
• Assess median sulcus (obliterated in large BPH)
• Check anal sphincter tone
• Note: DRE underestimates prostate size; ultrasound more accurate

Abdominal Examination:
• Palpable bladder indicates significant retention (>300-500mL)
• Suprapubic tenderness may indicate infection or acute retention
• Assess for flank tenderness (hydronephrosis)

General Examination:
• Assess overall fitness for potential surgery
• Check for lower limb edema (may indicate renal impairment)
• Neurological assessment if neurogenic bladder suspected

CLINICAL PROGRESSION:
• Often gradual symptom progression over years
• May have acute-on-chronic worsening (e.g., with infection, medications)
• Natural history variable - some improve, most progress, some stable
• Acute urinary retention can occur suddenly in previously stable patients`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL ASSESSMENT
• Detailed LUTS history using IPSS questionnaire
• Duration and progression of symptoms
• Impact on quality of life
• Medical history (diabetes, neurological conditions)
• Medication review (anticholinergics, sympathomimetics can worsen)
• Sexual function assessment

STEP 2: PHYSICAL EXAMINATION
• Abdominal examination for palpable bladder
• Digital rectal examination (DRE) - mandatory
• Focused neurological exam if indicated
• External genitalia examination

STEP 3: BASIC INVESTIGATIONS
• Urinalysis - exclude infection, hematuria
• Serum creatinine - assess renal function
• PSA - after informed discussion (optional but recommended)
• Blood glucose - screen for diabetes

STEP 4: ADDITIONAL INVESTIGATIONS (Selected Cases)
• Post-void residual (PVR) measurement
• Uroflowmetry
• Renal/bladder ultrasound
• Transrectal ultrasound (TRUS) - accurate prostate sizing

DIAGNOSTIC CRITERIA:
BPH is a clinical diagnosis based on:
• LUTS consistent with bladder outlet obstruction
• Enlarged prostate on DRE (though size varies)
• Exclusion of other causes (infection, cancer, neurogenic bladder)

INTERNATIONAL PROSTATE SYMPTOM SCORE (IPSS):
Questions assess (over past month):
1. Incomplete emptying
2. Frequency
3. Intermittency
4. Urgency
5. Weak stream
6. Straining
7. Nocturia
Each scored 0-5 (0=never, 5=almost always)
Plus quality of life question (0-6)

IPSS INTERPRETATION:
• 0-7: Mild - often watchful waiting appropriate
• 8-19: Moderate - consider medical therapy
• 20-35: Severe - medical or surgical therapy indicated

PROSTATE SIZE GRADING (DRE/Ultrasound):
• Normal: <20-25g (Grade I)
• Mild enlargement: 25-40g (Grade II)
• Moderate enlargement: 40-60g (Grade III)
• Severe enlargement: >60g (Grade IV)
Note: Prostate size does NOT correlate with symptom severity

PSA CONSIDERATIONS:
• Elevated in BPH, prostatitis, cancer, after DRE/instrumentation
• Higher PSA with larger prostates (0.1-0.3 ng/mL per gram of tissue)
• PSA >4 ng/mL warrants further evaluation (age-adjusted cutoffs exist)
• PSA velocity >0.75 ng/mL/year concerning for cancer
• Free PSA ratio <25% more suggestive of cancer
• Discuss benefits and limitations with patient before testing`,

    differentialDiagnosis: [
      'Prostate cancer - May coexist with BPH; hard nodule on DRE, elevated/rising PSA, abnormal free/total ratio; biopsy diagnostic',
      'Urinary tract infection - Acute symptoms, dysuria, pyuria; may precipitate retention in BPH; treat infection first',
      'Prostatitis (acute/chronic) - Tender prostate, fever (acute), perineal pain; may overlap with BPH',
      'Urethral stricture - History of STI, trauma, instrumentation; urethrography or cystoscopy diagnostic',
      'Neurogenic bladder - Diabetes, stroke, spinal cord disease, Parkinsons; urodynamics helpful',
      'Overactive bladder (OAB) - Predominantly storage symptoms without obstruction; urodynamics distinguishes',
      'Bladder cancer - Hematuria, irritative symptoms; cystoscopy and cytology diagnostic',
      'Bladder stones - May result from BPH; pain, hematuria, UTI; imaging diagnostic',
      'Detrusor underactivity - Weak contraction rather than obstruction; urodynamics required',
      'Medication-induced LUTS - Diuretics, anticholinergics, sympathomimetics; review medications',
      'Diabetes mellitus - Polyuria from glucosuria; check blood glucose',
      'Diabetes insipidus - Massive polyuria; water deprivation test diagnostic',
      'Nocturnal polyuria - Nocturia from excess nighttime urine production; voiding diary helpful',
      'Sleep apnea - Associated with nocturia; sleep study if suspected',
      'Chronic kidney disease - Polyuria in early CKD; check creatinine'
    ],

    investigations: [
      {
        name: 'Urinalysis (Dipstick and Microscopy)',
        indication: '🔴 ESSENTIAL - Exclude UTI, hematuria, glucosuria; baseline in all patients with LUTS',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine',
        indication: '🔴 ESSENTIAL - Assess renal function; chronic obstruction can cause silent CKD',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Screen for diabetes which causes polyuria and worsens BPH symptoms',
        availability: 'BASIC'
      },
      {
        name: 'Prostate Specific Antigen (PSA)',
        indication: '🟠 IMPORTANT - After informed consent; helps assess prostate size, cancer risk; guides treatment decisions',
        availability: 'BASIC'
      },
      {
        name: 'IPSS Questionnaire',
        indication: '🔴 ESSENTIAL - Quantifies symptom severity; guides treatment selection; monitors response',
        availability: 'BASIC'
      },
      {
        name: 'Post-Void Residual (PVR) - Bladder Scan or Catheter',
        indication: '🟠 IMPORTANT - Assess bladder emptying; PVR >100-200mL suggests significant obstruction',
        availability: 'BASIC'
      },
      {
        name: 'Renal and Bladder Ultrasound',
        indication: '🟠 IMPORTANT - Evaluate hydronephrosis, bladder wall thickness, stones, prostate size estimation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Transrectal Ultrasound (TRUS)',
        indication: '🟡 WHEN AVAILABLE - Accurate prostate volume measurement; guides surgical planning',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Uroflowmetry',
        indication: '🟡 WHEN AVAILABLE - Measures flow rate; Qmax <10 mL/s suggests obstruction; monitors treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Voiding Diary (Frequency-Volume Chart)',
        indication: '🟠 IMPORTANT - Documents pattern of voiding; identifies nocturnal polyuria; free and informative',
        availability: 'BASIC'
      },
      {
        name: 'Urodynamic Studies',
        indication: '🟡 WHEN AVAILABLE - Distinguishes obstruction from detrusor dysfunction; before surgery if diagnosis uncertain',
        availability: 'ADVANCED'
      },
      {
        name: 'Cystoscopy',
        indication: '🟡 WHEN AVAILABLE - Evaluates urethral stricture, bladder pathology; pre-operative assessment',
        availability: 'ADVANCED'
      },
      {
        name: 'Full Blood Count',
        indication: '🟠 IMPORTANT - Assess for anemia (chronic disease, hematuria); pre-operative baseline',
        availability: 'BASIC'
      },
      {
        name: 'CT Urogram',
        indication: '🟡 WHEN AVAILABLE - Evaluate hematuria, suspected upper tract pathology, complex cases',
        availability: 'ADVANCED'
      },
      {
        name: 'Prostate Biopsy (TRUS-guided)',
        indication: '🟡 WHEN INDICATED - Elevated PSA, abnormal DRE, PSA velocity concerning; rule out cancer',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ACUTE URINARY RETENTION: If unable to void with full bladder - CATHETERIZE IMMEDIATELY (do not delay)',
        '⚡ ASSESS SEVERITY: Use IPSS to quantify symptoms; determines treatment approach',
        '⚡ VITAL SIGNS: Check for signs of sepsis if concurrent UTI suspected',
        '⚡ DIGITAL RECTAL EXAM: Assess prostate size, consistency, exclude obvious malignancy',
        '⚡ URINALYSIS: Exclude infection (treat before addressing BPH if present)',
        '⚡ RENAL FUNCTION: Check creatinine - if elevated, indicates chronic obstruction',
        '⚡ MEDICATION REVIEW: Identify and stop/modify drugs worsening symptoms (anticholinergics, decongestants)',
        '⚡ HYDRATION ADVICE: Adequate fluids but avoid excessive intake; limit evening fluids for nocturia',
        '⚡ REASSURANCE: BPH is benign; explain condition and treatment options',
        '⚡ PSA DISCUSSION: Discuss benefits and limitations; obtain if appropriate after counseling'
      ],
      
      pharmacological: [
        {
          drug: 'Tamsulosin',
          indication: '🔴 FIRST-LINE - Alpha-1A selective blocker; rapid symptom relief; less hypotension than older agents',
          dose: 'Adults: 0.4mg PO once daily, 30 minutes after the same meal each day. May increase to 0.8mg if needed after 2-4 weeks.',
          route: 'PO',
          duration: 'Long-term; continue while symptomatic',
          contraindications: ['Hypersensitivity', 'Concurrent use with strong CYP3A4 inhibitors', 'Planned cataract surgery (stop 1-2 weeks before)'],
          sideEffects: ['Abnormal ejaculation (retrograde)', 'Dizziness', 'Rhinitis', 'Intraoperative floppy iris syndrome (IFIS)'],
          alternatives: ['Alfuzosin', 'Silodosin', 'Terazosin']
        },
        {
          drug: 'Alfuzosin',
          indication: '🔴 FIRST-LINE - Alpha-1 blocker; once-daily dosing; lower ejaculatory dysfunction than tamsulosin',
          dose: 'Adults: 10mg PO once daily after the same meal each day (extended-release formulation).',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hypotension', 'Severe hepatic impairment', 'QT prolongation', 'Strong CYP3A4 inhibitors'],
          sideEffects: ['Dizziness', 'Headache', 'Postural hypotension', 'Fatigue'],
          alternatives: ['Tamsulosin', 'Silodosin']
        },
        {
          drug: 'Terazosin',
          indication: '🟠 ALTERNATIVE - Non-selective alpha-blocker; also treats hypertension; requires dose titration',
          dose: 'Adults: Start 1mg PO at bedtime, titrate over 4-6 weeks to 2-5mg at bedtime. Maximum 20mg/day. Give first dose at bedtime to minimize first-dose hypotension.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity', 'Symptomatic hypotension'],
          sideEffects: ['Postural hypotension (especially first dose)', 'Dizziness', 'Fatigue', 'Nasal congestion'],
          alternatives: ['Doxazosin', 'Tamsulosin']
        },
        {
          drug: 'Doxazosin',
          indication: '🟠 ALTERNATIVE - Non-selective alpha-blocker; also treats hypertension; once-daily dosing',
          dose: 'Adults: Start 1mg PO once daily, titrate to 2-4mg daily. Maximum 8mg/day. Extended-release: 4-8mg daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity', 'Symptomatic hypotension'],
          sideEffects: ['Postural hypotension', 'Dizziness', 'Fatigue', 'Edema'],
          alternatives: ['Terazosin', 'Tamsulosin']
        },
        {
          drug: 'Finasteride',
          indication: '🔴 FIRST-LINE - 5-Alpha reductase inhibitor; reduces prostate size; best for large prostates (>40g)',
          dose: 'Adults: 5mg PO once daily. Takes 3-6 months for symptomatic improvement. Reduces prostate volume by 20-30%.',
          route: 'PO',
          duration: 'Long-term; must continue for benefit to persist',
          contraindications: ['Women (teratogenic - do not handle crushed tablets)', 'Children', 'Hypersensitivity'],
          sideEffects: ['Decreased libido', 'Erectile dysfunction', 'Reduced ejaculate volume', 'Gynecomastia (rare)'],
          alternatives: ['Dutasteride']
        },
        {
          drug: 'Dutasteride',
          indication: '🟠 ALTERNATIVE - Dual 5-ARI (inhibits both type 1 and 2); similar efficacy to finasteride',
          dose: 'Adults: 0.5mg PO once daily. Takes 3-6 months for effect. Longer half-life than finasteride.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Women (teratogenic)', 'Children', 'Severe hepatic impairment'],
          sideEffects: ['Decreased libido', 'Erectile dysfunction', 'Reduced ejaculate volume', 'Breast tenderness'],
          alternatives: ['Finasteride']
        },
        {
          drug: 'Tamsulosin + Dutasteride (Combination Therapy)',
          indication: '🟠 PREFERRED FOR LARGE PROSTATES - Combined alpha-blocker + 5-ARI; superior to monotherapy for progression',
          dose: 'Fixed combination: Tamsulosin 0.4mg + Dutasteride 0.5mg PO once daily. Or use individual agents together.',
          route: 'PO',
          duration: 'Long-term; Alpha-blocker may be stopped after 6-9 months if stable',
          contraindications: ['As per individual agents'],
          sideEffects: ['Combined side effects of both agents'],
          alternatives: ['Finasteride + any alpha-blocker']
        },
        {
          drug: 'Tadalafil',
          indication: '🟡 ALTERNATIVE - PDE5 inhibitor; treats both BPH/LUTS AND erectile dysfunction',
          dose: 'Adults: 5mg PO once daily (same time each day). Do not use with nitrates. May be added to alpha-blocker.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Concurrent nitrates', 'Recent stroke/MI', 'Severe hepatic/renal impairment', 'Hypotension'],
          sideEffects: ['Headache', 'Dyspepsia', 'Back pain', 'Flushing', 'Nasal congestion'],
          alternatives: ['Alpha-blocker monotherapy']
        },
        {
          drug: 'Tolterodine or Solifenacin (Add-on)',
          indication: '🟡 ADD-ON THERAPY - Antimuscarinic for persistent storage symptoms after alpha-blocker',
          dose: 'Tolterodine: 2mg PO twice daily or 4mg extended-release once daily. Solifenacin: 5-10mg PO once daily.',
          route: 'PO',
          duration: 'As needed for storage symptoms',
          contraindications: ['Urinary retention', 'PVR >200mL', 'Narrow-angle glaucoma', 'Severe hepatic impairment'],
          sideEffects: ['Dry mouth', 'Constipation', 'Blurred vision', 'Cognitive effects in elderly'],
          alternatives: ['Mirabegron (beta-3 agonist)']
        }
      ],
      
      nonPharmacological: [
        'WATCHFUL WAITING: Appropriate for mild symptoms (IPSS ≤7) with minimal bother; annual reassessment',
        'FLUID MANAGEMENT: Adequate hydration but avoid excessive intake; limit fluids 2-3 hours before bed for nocturia',
        'CAFFEINE AND ALCOHOL REDUCTION: Both are bladder irritants and diuretics; limit intake especially in evening',
        'DOUBLE VOIDING: Void, wait a moment, then try again to improve bladder emptying',
        'TIMED VOIDING: Scheduled voiding every 3-4 hours rather than waiting for urgency',
        'BLADDER TRAINING: Gradually increase intervals between voiding; helps with storage symptoms',
        'AVOID CONSTIPATION: Straining and full rectum worsen urinary symptoms; adequate fiber and fluids',
        'MEDICATION REVIEW: Avoid/minimize anticholinergics, antihistamines, decongestants, opioids',
        'PHYSICAL ACTIVITY: Regular exercise associated with lower LUTS severity',
        'WEIGHT MANAGEMENT: Obesity associated with worse symptoms; weight loss may help',
        'COLD AVOIDANCE: Exposure to cold can worsen symptoms; keep warm',
        'PELVIC FLOOR EXERCISES: May help with post-void dribbling and urgency'
      ],
      
      surgical: [
        'TRANSURETHRAL RESECTION OF PROSTATE (TURP): Gold standard surgical treatment; 30-60g prostates; 85-90% symptom improvement',
        'HOLMIUM LASER ENUCLEATION (HoLEP): Size-independent; excellent for large prostates; shorter catheter time than TURP',
        'PHOTOSELECTIVE VAPORIZATION (PVP/GreenLight): Laser ablation; good for patients on anticoagulation; less bleeding',
        'TRANSURETHRAL INCISION OF PROSTATE (TUIP): For small prostates (<30g); lower retrograde ejaculation risk than TURP',
        'OPEN PROSTATECTOMY: For very large prostates (>80-100g); suprapubic or retropubic approach; most tissue removed',
        'PROSTATIC URETHRAL LIFT (UroLift): Minimally invasive; mechanical opening of urethra; preserves ejaculation',
        'WATER VAPOR THERMAL THERAPY (Rezum): Outpatient procedure; steam injection causes necrosis; preserves sexual function',
        'PROSTATIC ARTERY EMBOLIZATION (PAE): Interventional radiology procedure; reduces prostate blood supply',
        'TEMPORARY PROSTATIC STENT: Bridge to surgery in unfit patients; not long-term solution',
        'SUPRAPUBIC CATHETER: If urethral catheter not possible; for long-term drainage in unfit surgical candidates'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Acute urinary retention not relieved by catheterization or unable to catheterize',
        '🔴 IMMEDIATE REFERRAL: Bilateral hydronephrosis with renal impairment - obstructive uropathy emergency',
        '🔴 IMMEDIATE REFERRAL: Suspected prostate cancer (hard nodule on DRE, significantly elevated PSA)',
        '🟠 URGENT REFERRAL: Recurrent urinary retention after catheter removal',
        '🟠 URGENT REFERRAL: Recurrent urinary tract infections despite treatment',
        '🟠 URGENT REFERRAL: Gross hematuria requiring evaluation',
        '🟠 URGENT REFERRAL: Bladder stones confirmed on imaging',
        '🟠 URGENT REFERRAL: Renal insufficiency attributed to BPH (obstructive uropathy)',
        '🟡 ROUTINE REFERRAL: Failed medical therapy - symptoms uncontrolled after adequate trial',
        '🟡 ROUTINE REFERRAL: Patient preference for surgical intervention',
        '🟡 ROUTINE REFERRAL: Large prostate (>60-80g) likely to require surgery',
        '🟡 ROUTINE REFERRAL: Elevated PSA requiring further evaluation',
        '🟡 ROUTINE REFERRAL: Moderate-severe symptoms (IPSS >19) for surgical evaluation'
      ],
      
      monitoring: [
        'IPSS SCORE: Repeat at 4-6 weeks after starting therapy, then 3-6 monthly; expect 3-5 point improvement',
        'SYMPTOM DIARY: Voiding frequency, nocturia episodes; useful for assessing treatment response',
        'RENAL FUNCTION: Check creatinine annually; more frequently if elevated at baseline',
        'PSA: Annual monitoring if on 5-ARI (expect 50% reduction); rising PSA concerning even on therapy',
        'POST-VOID RESIDUAL: Periodically, especially if worsening symptoms; PVR >200-300mL may need intervention',
        'UROFLOWMETRY: If available, Qmax monitoring; improvement of 2-3 mL/s clinically significant',
        'PROSTATE SIZE: Ultrasound if considering surgery; 5-ARI reduces size by 20-30% over months',
        'MEDICATION ADHERENCE: Review compliance; many patients discontinue treatment',
        'SIDE EFFECTS: Assess for postural hypotension, sexual dysfunction; may need medication adjustment',
        'WATCHFUL WAITING: Annual reassessment; ~30% progress to needing treatment; ~15% improve spontaneously'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

WATCHFUL WAITING:
• Annual assessment with IPSS
• Repeat creatinine and urinalysis annually
• DRE annually (or as appropriate for age/PSA screening)
• Intervention if symptoms progress or complications develop

MEDICAL THERAPY INITIATION:
• Alpha-blocker: Review at 2-4 weeks for efficacy and side effects
• 5-ARI: Review at 3-6 months (slower onset of action)
• Combination therapy: As per individual components
• Continue if benefiting and tolerated

ONGOING MEDICAL THERAPY:
• IPSS every 6-12 months
• Annual renal function and PSA
• Assess for medication side effects
• Consider step-down if stable for years

ALPHA-BLOCKER MONITORING:
• First-dose hypotension: Advise on first few doses
• Check blood pressure if symptomatic
• Assess ejaculatory function if concerned
• Warn about IFIS if cataract surgery planned

5-ARI MONITORING:
• PSA decreases ~50% after 6-12 months of therapy
• Any PSA rise requires investigation even if in normal range
• Assess sexual function side effects
• Explain need for long-term therapy for continued benefit

POST-SURGICAL FOLLOW-UP:
• TURP/surgical: Follow-up at 4-6 weeks, 3 months, then annually
• Assess voiding symptoms, IPSS
• Check for complications: UTI, stricture, incontinence
• PSA monitoring continues as age-appropriate

MONITORING FOR COMPLICATIONS:

Acute Urinary Retention:
• ~1-2% annual risk on watchful waiting
• Higher risk with larger prostates, severe symptoms, low Qmax

Chronic Retention/Renal Impairment:
• Annual creatinine
• Ultrasound if creatinine rises

Disease Progression:
• ~30% progress over 4 years without treatment
• Combination therapy reduces progression by ~66% vs placebo`
    },

    redFlags: [
      '🔴 CRITICAL: Complete inability to void (acute urinary retention) - CATHETERIZE IMMEDIATELY; do not delay',
      '🔴 CRITICAL: Hard, irregular prostate nodule on DRE - strongly suggests prostate cancer; urgent urology referral',
      '🔴 CRITICAL: Bilateral hydronephrosis with elevated creatinine - obstructive uropathy; urgent decompression needed',
      '🔴 CRITICAL: Urosepsis (fever, LUTS, hypotension) - requires emergency management; may need catheter drainage',
      '🟠 URGENT: PSA significantly elevated or rising - requires further evaluation to exclude malignancy',
      '🟠 URGENT: Gross hematuria - may indicate bladder or prostate pathology; needs evaluation',
      '🟠 URGENT: Recurrent urinary tract infections (>2/year) - indicates incomplete emptying or other pathology',
      '🟠 URGENT: Bladder stones on imaging - requires removal; indicates chronic obstruction',
      '🟠 URGENT: High post-void residual (>300mL) with symptoms - increased retention risk; may need intervention',
      '🟡 CAUTION: New-onset urinary incontinence - evaluate for overflow incontinence from retention',
      '🟡 CAUTION: Neurological symptoms with LUTS - consider neurogenic bladder, spinal pathology',
      '🟡 CAUTION: Progressive symptoms despite medical therapy - may need surgical intervention',
      '🟡 CAUTION: Weight loss, bone pain with LUTS in elderly - evaluate for metastatic prostate cancer'
    ],

    complications: [
      'Acute urinary retention (AUR) - Sudden inability to void; very distressing; requires catheterization; 1-2% annual risk',
      'Chronic urinary retention - Insidious incomplete emptying; may present with overflow incontinence',
      'Urinary tract infection - Stagnant urine promotes bacterial growth; recurrent UTIs common',
      'Bladder stones - Form in residual urine; cause pain, hematuria, recurrent infections',
      'Bladder decompensation - Chronic obstruction leads to detrusor failure; may not recover after relief',
      'Hydronephrosis - Back-pressure on kidneys from chronic obstruction; often bilateral',
      'Chronic kidney disease - From obstructive uropathy; may be silent until advanced',
      'Gross hematuria - From enlarged prostatic veins or associated pathology',
      'Bladder diverticula - Outpouchings from high-pressure voiding; harbor infection and stones',
      'Detrusor overactivity - Bladder responds to obstruction with unstable contractions; causes urgency',
      'Post-void dribbling - Urine trapped in bulbar urethra dribbles after voiding',
      'Erectile dysfunction - Both BPH and its treatments can affect sexual function',
      'Retrograde ejaculation - After TURP or from alpha-blockers; semen enters bladder',
      'Treatment side effects - Hypotension (alpha-blockers), sexual dysfunction (5-ARIs)',
      'Undiagnosed prostate cancer - May coexist with BPH; importance of DRE and PSA'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

NATURAL HISTORY WITHOUT TREATMENT:
• Progressive symptoms in ~30% over 4 years
• Stable symptoms in ~50%
• Spontaneous improvement in ~15-20%
• Acute urinary retention: 1-2% annual risk
• Higher progression risk: Larger prostate, higher PSA, severe symptoms, low Qmax

WITH MEDICAL THERAPY:

Alpha-Blockers:
• 4-6 point IPSS improvement (30-40% reduction)
• Qmax improvement of 2-3 mL/s
• Response within days to weeks
• No effect on disease progression or prostate size
• Long-term efficacy maintained

5-Alpha Reductase Inhibitors:
• 3-4 point IPSS improvement (takes 3-6 months)
• 20-30% reduction in prostate volume
• Reduces AUR risk by 57%
• Reduces need for surgery by 55%
• Most effective in larger prostates (>40g)

Combination Therapy:
• Superior to either monotherapy
• 66% reduction in progression vs placebo
• Recommended for larger prostates or high progression risk

WITH SURGICAL TREATMENT:

TURP:
• 85-90% symptom improvement
• Qmax improvement of 10-15 mL/s
• Durable results (10-15 years)
• Retreatment rate: 1-2% per year
• Complications: Retrograde ejaculation (65-75%), ED (5-10%), TUR syndrome (rare)

Other Procedures:
• HoLEP, PVP, open prostatectomy: Similar efficacy
• Minimally invasive: Lower efficacy but fewer side effects

QUALITY OF LIFE:
• Significant impact from untreated moderate-severe LUTS
• Treatment improves QoL in most patients
• Nocturia particularly bothersome; improves with treatment

MORTALITY:
• BPH itself not associated with increased mortality
• Complications (renal failure, urosepsis) can be serious if untreated
• Early treatment prevents most serious complications`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
BPH develops in most aging men; true prevention not possible, but risk modification may help:

Lifestyle Factors:
• Regular physical activity - associated with lower LUTS severity
• Healthy weight maintenance - obesity linked to worse symptoms
• Balanced diet - low in saturated fat, high in vegetables
• Moderate alcohol consumption (conflicting evidence)
• Avoid smoking - may be associated with worse symptoms

SECONDARY PREVENTION (Early Detection):
• Men over 50: Discuss prostate health at routine visits
• Symptom awareness: Educate about LUTS and when to seek help
• Regular health checks including DRE (as part of cancer screening discussion)
• Earlier intervention prevents complications

TERTIARY PREVENTION (Preventing Complications):

Medical Therapy Benefits:
• 5-ARIs reduce acute urinary retention risk by 57%
• 5-ARIs reduce need for surgery by 55%
• Combination therapy most effective for progression prevention

Avoiding Complications:
• Avoid medications that worsen symptoms (anticholinergics, decongestants)
• Treat UTIs promptly
• Monitor renal function
• Avoid prolonged holding of urine
• Limit excessive fluid intake
• Limit evening fluids for nocturia

Preventing Treatment Side Effects:
• First-dose alpha-blocker: Take at bedtime, rise slowly
• 5-ARI: Inform about sexual side effects; counsel partners about pregnancy
• Pre-cataract surgery: Inform ophthalmologist about alpha-blocker use (IFIS risk)

SCREENING CONSIDERATIONS:
• No formal BPH screening program exists
• Opportunistic assessment in men with symptoms
• PSA screening controversial; shared decision-making recommended
• DRE as part of comprehensive evaluation

PUBLIC HEALTH:
• Education about LUTS and available treatments
• Reduce stigma around urinary symptoms
• Improve access to urological services
• Train primary care providers in BPH management`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS BENIGN PROSTATIC HYPERPLASIA (BPH)?
BPH is a non-cancerous (benign) enlargement of the prostate gland that happens as men get older. The prostate is a walnut-sized gland that surrounds the urine tube (urethra) below the bladder. As it grows bigger, it can squeeze the urethra and cause problems with urination. This is very common - it affects most men over age 60 - and can be effectively treated.

COMMON SYMPTOMS:
• Difficulty starting to urinate
• Weak urine stream
• Stopping and starting during urination
• Feeling like your bladder is not completely empty
• Needing to urinate frequently, especially at night
• Sudden urgent need to urinate
• Dribbling after urination

IMPORTANT - BPH IS NOT CANCER:
• BPH is a benign (non-cancerous) condition
• Having BPH does not increase your risk of prostate cancer
• However, BPH and cancer can exist together
• Regular check-ups help monitor your prostate health

WHEN TO SEEK HELP URGENTLY:
• You cannot urinate at all (complete blockage)
• Blood in your urine
• Fever with urinary symptoms
• Severe pain
• Unable to control your bladder

TREATMENT OPTIONS:
• Watchful waiting: For mild symptoms; regular monitoring
• Medications: Pills that relax the prostate or shrink it over time
• Surgery: For severe symptoms or complications; very effective

ABOUT YOUR MEDICATIONS:

Alpha-blockers (Tamsulosin, Alfuzosin, etc.):
• Relax the prostate and bladder neck muscles
• Work quickly (days to weeks)
• May cause dizziness - get up slowly from sitting/lying
• May affect ejaculation (semen goes into bladder instead of out)
• Tell your eye doctor if you plan cataract surgery

5-Alpha Reductase Inhibitors (Finasteride, Dutasteride):
• Shrink the prostate over time
• Take 3-6 months to work fully
• May affect sexual function
• Women who are or may become pregnant should not handle these tablets

SELF-CARE TIPS:
• Urinate when you first feel the need - do not hold it too long
• Double void: After urinating, wait a moment and try again
• Limit fluids in the evening to reduce nighttime trips to the toilet
• Reduce caffeine and alcohol - they irritate the bladder
• Avoid cold exposure - can worsen symptoms
• Stay active - exercise helps
• Review your other medications with your doctor

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. BPH is a treatable condition, and most men get significant relief with proper treatment. Do not suffer in silence - talk to your doctor about your symptoms.

QUESTIONS TO ASK YOUR DOCTOR:
• How enlarged is my prostate?
• Do I need any tests for prostate cancer?
• What treatment do you recommend for my symptoms?
• What are the side effects of the medication?
• Will I need surgery eventually?
• How often should I follow up?`,

    references: [
      'McVary KT, Roehrborn CG, et al. American Urological Association Guideline: Management of Benign Prostatic Hyperplasia (BPH). AUA. 2021 (amended 2023).',
      'Gravas S, Cornu JN, et al. EAU Guidelines on Management of Non-Neurogenic Male Lower Urinary Tract Symptoms (LUTS). European Association of Urology. 2024.',
      'Parsons JK. Benign Prostatic Hyperplasia and Male Lower Urinary Tract Symptoms: Epidemiology and Risk Factors. Curr Bladder Dysfunct Rep. 2010;5(4):212-218.',
      'Roehrborn CG. Benign prostatic hyperplasia: an overview. Rev Urol. 2005;7(Suppl 9):S3-S14.',
      'Oelke M, Bachmann A, et al. EAU guidelines on the treatment and follow-up of non-neurogenic male lower urinary tract symptoms including benign prostatic obstruction. Eur Urol. 2013;64(1):118-140.',
      'McConnell JD, Roehrborn CG, et al. The long-term effect of doxazosin, finasteride, and combination therapy on the clinical progression of benign prostatic hyperplasia. N Engl J Med. 2003;349(25):2387-2398.',
      'Roehrborn CG, Siami P, et al. The effects of dutasteride, tamsulosin and combination therapy on lower urinary tract symptoms in men with benign prostatic hyperplasia and prostatic enlargement: 2-year results from the CombAT study. J Urol. 2008;179(2):616-621.',
      'Wei JT, Calhoun E, Jacobsen SJ. Urologic diseases in America project: benign prostatic hyperplasia. J Urol. 2005;173(4):1256-1261.',
      'Ajayi A, Ogunleye A, et al. Benign prostatic hyperplasia in Nigeria: A review of current situation. Niger J Med. 2019;28(3):225-232.',
      'Akpo EE, Akpo MO. Benign prostatic hyperplasia: a review of operative techniques. Afr J Urol. 2011;17(1):1-7.',
      'Rikili A, Memon MS, Kazmi SK. Correlation between IPSS, prostate volume and uroflowmetry in patients with LUTS. Pak J Surg. 2018;34(1):46-50.',
      'Barry MJ, Fowler FJ Jr, et al. The American Urological Association symptom index for benign prostatic hyperplasia. J Urol. 1992;148(5):1549-1557.',
      'Roehrborn CG. Pathology of benign prostatic hyperplasia. Int J Impot Res. 2008;20(Suppl 3):S11-S18.',
      'Issa MM, Regan TS. Medical therapy for benign prostatic hyperplasia--present and future impact. Am J Manag Care. 2007;13(Suppl 1):S4-S9.',
      'National Institute for Health and Care Excellence (NICE). Lower urinary tract symptoms in men: management. NICE guideline [CG97]. 2010 (updated 2015).'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 65. HYPERTENSION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '65',
    name: 'Hypertension',
    slug: 'hypertension',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I10',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Hypertension (high blood pressure) is the leading modifiable risk factor for cardiovascular disease and premature death worldwide, affecting over 1.28 billion adults globally. Defined as sustained systolic blood pressure ≥140 mmHg and/or diastolic blood pressure ≥90 mmHg, hypertension is responsible for approximately 10.4 million deaths annually, primarily through its contribution to stroke, ischemic heart disease, heart failure, and chronic kidney disease.

The pathophysiology of hypertension is multifactorial, involving complex interactions between genetic predisposition, environmental factors, and regulatory mechanisms. Essential (primary) hypertension, accounting for 90-95% of cases, results from interplay between increased cardiac output, elevated peripheral vascular resistance, and dysregulation of the renin-angiotensin-aldosterone system (RAAS), sympathetic nervous system, and endothelial function. Secondary hypertension (5-10%) has identifiable causes including renal parenchymal disease, renovascular disease, primary aldosteronism, pheochromocytoma, and obstructive sleep apnea.

Sub-Saharan Africa faces an escalating hypertension epidemic, with prevalence rates of 30-45% in many countries, driven by rapid urbanization, dietary transitions (increased salt, decreased potassium), obesity, physical inactivity, and population aging. Critically, awareness, treatment, and control rates remain dismally low - studies suggest only 30-40% are aware, 20-30% are treated, and merely 5-10% achieve control. This "rule of halves" results in high rates of hypertensive complications including hemorrhagic stroke (more common in Africa than ischemic stroke), hypertensive heart disease, and hypertensive nephropathy. African populations also demonstrate higher salt sensitivity and greater efficacy of calcium channel blockers and thiazide diuretics compared to RAAS inhibitors.

Effective management requires accurate diagnosis (multiple readings on separate occasions), cardiovascular risk assessment, target organ damage evaluation, and individualized therapy combining lifestyle modification with pharmacotherapy. Treatment targets are typically <140/90 mmHg for most adults, with lower targets (<130/80 mmHg) for high-risk patients where achievable without adverse effects.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Diagnose hypertension based on ≥2 elevated readings on ≥2 separate occasions; single elevated reading is insufficient

🔴 CRITICAL POINT 2 - Always assess for target organ damage: heart (LVH, heart failure), brain (stroke, TIA), kidneys (proteinuria, elevated creatinine), eyes (retinopathy), vessels (PAD)

🔴 CRITICAL POINT 3 - Hypertensive emergency (BP >180/120 with acute organ damage) requires immediate IV therapy and ICU admission; do NOT drop BP too rapidly

🔴 CRITICAL POINT 4 - In African patients, calcium channel blockers or thiazide diuretics are preferred first-line over ACE inhibitors/ARBs (better efficacy due to low-renin phenotype)

🟠 CRITICAL POINT 5 - ACE inhibitors/ARBs are preferred if diabetes, proteinuria, heart failure, or post-MI regardless of ethnicity

🟠 CRITICAL POINT 6 - Most patients require 2+ antihypertensive medications to achieve control; start combination therapy if BP ≥160/100 or >20/10 above target

🟠 CRITICAL POINT 7 - Check potassium and creatinine before and 1-2 weeks after starting ACE inhibitor/ARB or diuretic

🟡 CRITICAL POINT 8 - Lifestyle modification is essential for ALL patients: DASH diet, sodium restriction (<2g/day), weight loss, exercise, alcohol moderation, smoking cessation

🟡 CRITICAL POINT 9 - Screen for secondary causes if age <30, resistant hypertension, sudden onset, or hypokalemia`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 1.28 billion adults with hypertension worldwide (2019)
• Leading modifiable risk factor for death globally
• Responsible for 10.4 million deaths annually
• Prevalence: 30-45% of adults; increases with age
• 46% unaware of their diagnosis
• Only 21% achieve blood pressure control globally
• Accounts for 54% of strokes and 47% of ischemic heart disease
• Healthcare costs exceed $370 billion annually worldwide

AFRICAN EPIDEMIOLOGY:
• Prevalence: 30-45% across sub-Saharan Africa (among highest globally)
• Nigeria: 30-40% prevalence; <10% control rates
• South Africa: 35-45% prevalence
• Ghana, Kenya, Tanzania: Similar high prevalence rates
• Awareness: Only 30-40% know they have hypertension
• Treatment: 20-30% of those with hypertension are on medication
• Control: Merely 5-10% achieve target BP (<140/90)
• Hemorrhagic stroke more common than in Western populations
• Hypertensive heart disease: Leading cause of heart failure in Africa
• Higher salt sensitivity in African populations
• Rapid urbanization driving increasing prevalence
• Young-onset severe hypertension more common

RISK FACTORS:
• Non-modifiable: Age, family history, male sex, African ancestry, genetics
• Modifiable: High sodium intake, low potassium intake, obesity, physical inactivity
• Dietary factors: Low fruit/vegetable intake, high processed food consumption
• Excess alcohol consumption
• Tobacco use
• Chronic stress
• Low socioeconomic status
• Obstructive sleep apnea
• Chronic kidney disease

CONSEQUENCES OF UNCONTROLLED HYPERTENSION:
• Stroke: 54% attributable to hypertension
• Ischemic heart disease: 47% attributable
• Heart failure: Major cause of hypertensive heart disease
• Chronic kidney disease: Progressive nephrosclerosis
• Peripheral arterial disease
• Aortic aneurysm and dissection
• Retinopathy and vision loss
• Vascular dementia`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ESSENTIAL HYPERTENSION (Usually Asymptomatic):
• Most patients have NO symptoms - the "silent killer"
• Discovered incidentally during routine examination
• May have non-specific symptoms: Headache (especially occipital, morning)
• Dizziness, lightheadedness
• Visual disturbances
• Epistaxis (nosebleeds) - not reliable indicator
• Fatigue

SYMPTOMS OF TARGET ORGAN DAMAGE:
• Heart: Dyspnea, orthopnea, chest pain, palpitations (LVH, heart failure)
• Brain: Headache, confusion, visual changes, focal deficits (stroke, encephalopathy)
• Kidneys: Nocturia, foamy urine, edema (nephropathy)
• Eyes: Blurred vision, visual field defects (retinopathy)
• Peripheral vessels: Claudication, cold extremities (PAD)

SYMPTOMS SUGGESTING SECONDARY HYPERTENSION:
• Episodic headache, sweating, palpitations, pallor (pheochromocytoma)
• Muscle weakness, cramps, polyuria (primary aldosteronism - hypokalemia)
• Snoring, daytime somnolence, witnessed apneas (OSA)
• Abdominal bruit (renovascular disease)
• Weight gain, striae, moon facies (Cushing syndrome)

PHYSICAL EXAMINATION:

Blood Pressure Measurement:
• Use appropriate cuff size (bladder 80% of arm circumference)
• Patient seated quietly for 5 minutes, back supported, feet flat
• Arm at heart level, supported
• Measure in BOTH arms initially (>10-15 mmHg difference suggests vascular disease)
• Average of ≥2 readings on ≥2 occasions for diagnosis
• Check for orthostatic hypotension (especially in elderly, diabetics)

Cardiovascular Examination:
• Sustained apical impulse (LVH)
• S4 gallop (stiff LV from hypertrophy)
• S3 gallop (heart failure)
• Murmurs (aortic regurgitation from aortic root dilation)
• Elevated JVP (right heart failure)
• Peripheral edema

Other Examination:
• Fundoscopy: AV nicking, copper/silver wiring, hemorrhages, exudates, papilledema
• Carotid bruits (carotid stenosis)
• Abdominal bruits (renal artery stenosis)
• Enlarged kidneys (polycystic kidney disease)
• Femoral pulse delay (coarctation of aorta)
• Diminished peripheral pulses (PAD)

BLOOD PRESSURE CLASSIFICATION (mmHg):

Normal: <120/<80
Elevated: 120-129/<80
Stage 1 Hypertension: 130-139 OR 80-89
Stage 2 Hypertension: ≥140 OR ≥90
Hypertensive Crisis: >180/>120

WHO/ISH Classification:
• Grade 1: 140-159 / 90-99
• Grade 2: 160-179 / 100-109
• Grade 3: ≥180 / ≥110`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: ACCURATE BLOOD PRESSURE MEASUREMENT
• Proper technique essential (see clinical presentation)
• Office BP: Average of ≥2 readings
• Confirm on ≥2 separate visits before diagnosing
• Consider out-of-office confirmation: ABPM or home BP monitoring

STEP 2: CARDIOVASCULAR RISK ASSESSMENT
• Calculate 10-year CV risk (WHO/ISH charts, Framingham, SCORE)
• Consider: Age, sex, smoking, diabetes, cholesterol, family history
• Risk stratification guides treatment intensity

STEP 3: ASSESS FOR TARGET ORGAN DAMAGE
• Heart: ECG, echocardiogram (LVH, systolic/diastolic dysfunction)
• Kidneys: Creatinine, eGFR, urinalysis, urine albumin-creatinine ratio
• Eyes: Fundoscopy
• Vessels: ABI if peripheral pulses abnormal

STEP 4: SCREEN FOR SECONDARY CAUSES (If Indicated)
• Age of onset <30 or >55 years
• Sudden onset or worsening
• Resistant hypertension (uncontrolled on 3+ drugs including diuretic)
• Severe or accelerated hypertension
• Unprovoked hypokalemia
• Abdominal bruit
• Labile BP with symptoms (pheochromocytoma)

DIAGNOSTIC CRITERIA:

Office Blood Pressure:
• ≥140/90 mmHg on ≥2 occasions for most guidelines
• ≥130/80 mmHg (ACC/AHA 2017 for stage 1)

Ambulatory BP Monitoring (ABPM):
• 24-hour average ≥130/80 mmHg
• Daytime average ≥135/85 mmHg
• Nighttime average ≥120/70 mmHg

Home BP Monitoring:
• Average ≥135/85 mmHg

WHITE COAT HYPERTENSION:
• Elevated office BP but normal out-of-office BP
• Affects 15-30% of those with elevated office readings
• Lower CV risk but still higher than normotensives
• Confirm with ABPM or home monitoring

MASKED HYPERTENSION:
• Normal office BP but elevated out-of-office BP
• Higher CV risk than white coat hypertension
• Consider in patients with target organ damage but normal office BP

SECONDARY HYPERTENSION WORKUP:

If suspected, consider:
• Renal parenchymal disease: Urinalysis, creatinine, renal ultrasound
• Renovascular disease: Renal artery Doppler, CT/MR angiography
• Primary aldosteronism: Aldosterone-renin ratio (most common secondary cause)
• Pheochromocytoma: 24-hour urine metanephrines or plasma metanephrines
• Cushing syndrome: Overnight dexamethasone suppression test, 24-hour urine cortisol
• Thyroid disease: TSH
• Obstructive sleep apnea: Sleep study
• Coarctation: CT/MR aorta if arm-leg BP gradient`,

    differentialDiagnosis: [
      'White coat hypertension - Elevated office BP with normal ambulatory/home readings; lower CV risk; confirm with ABPM',
      'Secondary hypertension - Identifiable cause present; suspect if young, resistant, sudden onset, or hypokalemia',
      'Renal parenchymal disease - Elevated creatinine, abnormal urinalysis, small kidneys on ultrasound',
      'Renovascular hypertension - Abdominal bruit, worsening renal function with ACEi/ARB, flash pulmonary edema',
      'Primary aldosteronism - Hypokalemia (may be normokalemic), elevated aldosterone-renin ratio; 5-10% of hypertension',
      'Pheochromocytoma - Episodic symptoms (headache, sweating, palpitations); elevated metanephrines',
      'Cushing syndrome - Central obesity, striae, moon facies, glucose intolerance; elevated cortisol',
      'Thyroid disorders - Hyperthyroidism (systolic HTN), hypothyroidism (diastolic HTN); check TSH',
      'Coarctation of aorta - Young patient, upper extremity hypertension, weak femoral pulses, rib notching on CXR',
      'Obstructive sleep apnea - Snoring, witnessed apneas, daytime somnolence; resistant hypertension common',
      'Drug-induced hypertension - NSAIDs, decongestants, oral contraceptives, steroids, EPO, cyclosporine',
      'Anxiety/panic disorder - Episodic BP elevation with symptoms; normal between episodes',
      'Pain - Acute pain elevates BP; control pain before diagnosing hypertension',
      'Alcohol withdrawal - Acute elevation; history of heavy alcohol use',
      'Preeclampsia - Pregnancy with hypertension and proteinuria after 20 weeks; obstetric emergency'
    ],

    investigations: [
      {
        name: 'Blood Pressure Measurement (Office)',
        indication: '🔴 ESSENTIAL - Accurate measurement with proper technique; average of ≥2 readings on ≥2 occasions',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis (Dipstick)',
        indication: '🔴 ESSENTIAL - Screen for proteinuria (target organ damage), hematuria (renal disease), glucose (diabetes)',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and eGFR',
        indication: '🔴 ESSENTIAL - Assess renal function; identify CKD; baseline before starting ACEi/ARB/diuretic',
        availability: 'BASIC'
      },
      {
        name: 'Serum Potassium',
        indication: '🔴 ESSENTIAL - Baseline before diuretics/ACEi/ARB; hypokalemia suggests aldosteronism',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Blood Glucose or HbA1c',
        indication: '🔴 ESSENTIAL - Screen for diabetes; major CV risk factor; affects drug selection',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Lipid Profile',
        indication: '🔴 ESSENTIAL - Total cholesterol, LDL, HDL, triglycerides; complete CV risk assessment',
        availability: 'BASIC'
      },
      {
        name: 'Electrocardiogram (ECG)',
        indication: '🔴 ESSENTIAL - Detect LVH (voltage criteria, strain pattern), arrhythmias, prior MI',
        availability: 'BASIC'
      },
      {
        name: 'Urine Albumin-Creatinine Ratio (uACR)',
        indication: '🟠 IMPORTANT - Detect early nephropathy; indicates target organ damage and guides ACEi/ARB use',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: '🟠 IMPORTANT - Assess LVH (more sensitive than ECG), systolic/diastolic function, valvular disease',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Fundoscopy',
        indication: '🟠 IMPORTANT - Assess hypertensive retinopathy; Keith-Wagener-Barker grading; essential in severe/accelerated HTN',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🟠 IMPORTANT - Baseline; polycythemia can cause HTN; anemia in CKD',
        availability: 'BASIC'
      },
      {
        name: 'Ambulatory BP Monitoring (ABPM)',
        indication: '🟡 WHEN AVAILABLE - Gold standard for diagnosis; detects white coat and masked HTN; assesses nocturnal dipping',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Home BP Monitoring',
        indication: '🟠 IMPORTANT - Alternative to ABPM; patient-measured readings; improves adherence and control',
        availability: 'BASIC'
      },
      {
        name: 'Renal Ultrasound',
        indication: '🟡 WHEN INDICATED - Elevated creatinine, suspected renal disease; assess kidney size, obstruction',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Aldosterone-Renin Ratio',
        indication: '🟡 WHEN INDICATED - Screen for primary aldosteronism if resistant HTN, hypokalemia, adrenal incidentaloma',
        availability: 'ADVANCED'
      },
      {
        name: 'Plasma/Urine Metanephrines',
        indication: '🟡 WHEN INDICATED - Screen for pheochromocytoma if episodic symptoms, resistant HTN, adrenal mass',
        availability: 'ADVANCED'
      },
      {
        name: 'TSH',
        indication: '🟡 WHEN INDICATED - Screen for thyroid dysfunction if clinical suspicion',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Ensure BP measured correctly; ≥2 elevated readings on ≥2 occasions required',
        '⚡ ASSESS SEVERITY: Stage 1 (130-139/80-89), Stage 2 (≥140/≥90), Crisis (>180/120)',
        '⚡ EVALUATE FOR EMERGENCY: If BP >180/120, check for acute target organ damage (chest pain, dyspnea, neuro symptoms, AKI)',
        '⚡ CARDIOVASCULAR RISK: Calculate 10-year CV risk to guide treatment intensity',
        '⚡ TARGET ORGAN DAMAGE: Check ECG, creatinine, urinalysis, fundoscopy',
        '⚡ BASELINE LABS: Potassium, creatinine, glucose, lipids before starting therapy',
        '⚡ IDENTIFY SECONDARY CAUSES: Screen if age <30, resistant, sudden onset, or hypokalemia',
        '⚡ LIFESTYLE COUNSELING: Begin immediately regardless of whether starting medications',
        '⚡ MEDICATION INITIATION: Based on BP level, CV risk, and comorbidities',
        '⚡ PATIENT EDUCATION: Explain lifelong nature, importance of adherence, home monitoring'
      ],
      
      pharmacological: [
        {
          drug: 'Amlodipine',
          indication: '🔴 FIRST-LINE - Calcium channel blocker (CCB); preferred in African patients; excellent for isolated systolic HTN',
          dose: 'Adults: Start 5mg PO once daily. May increase to 10mg once daily after 1-2 weeks if needed. Elderly: Start 2.5mg.',
          route: 'PO',
          duration: 'Long-term; continue indefinitely',
          contraindications: ['Severe aortic stenosis', 'Cardiogenic shock', 'Unstable angina'],
          sideEffects: ['Peripheral edema (dose-dependent)', 'Flushing', 'Headache', 'Dizziness', 'Gingival hyperplasia'],
          alternatives: ['Nifedipine XL', 'Felodipine', 'Lercanidipine']
        },
        {
          drug: 'Hydrochlorothiazide',
          indication: '🔴 FIRST-LINE - Thiazide diuretic; preferred in African patients; low cost; excellent efficacy',
          dose: 'Adults: 12.5-25mg PO once daily in the morning. Maximum 50mg daily (higher doses increase side effects without much additional BP lowering).',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Anuria', 'Severe renal impairment (eGFR <30)', 'Hypersensitivity to sulfonamides', 'Refractory hypokalemia'],
          sideEffects: ['Hypokalemia', 'Hyperuricemia/gout', 'Hyperglycemia', 'Hyponatremia', 'Hypercalcemia'],
          alternatives: ['Chlorthalidone (more potent)', 'Indapamide']
        },
        {
          drug: 'Chlorthalidone',
          indication: '🟠 ALTERNATIVE - Thiazide-like diuretic; more potent than HCTZ; longer duration; better outcome data',
          dose: 'Adults: 12.5-25mg PO once daily. Maximum 50mg daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Anuria', 'Severe renal impairment', 'Hypersensitivity'],
          sideEffects: ['Hypokalemia (more than HCTZ)', 'Hyperuricemia', 'Hyponatremia', 'Glucose intolerance'],
          alternatives: ['Hydrochlorothiazide', 'Indapamide']
        },
        {
          drug: 'Lisinopril',
          indication: '🔴 FIRST-LINE - ACE inhibitor; preferred if diabetes, proteinuria, heart failure, post-MI; less effective as monotherapy in Africans',
          dose: 'Adults: Start 5-10mg PO once daily. Titrate to 20-40mg once daily. Heart failure: Start 2.5-5mg. Renal impairment: Start lower, adjust by eGFR.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Pregnancy', 'History of angioedema with ACEi', 'Bilateral renal artery stenosis', 'Hyperkalemia (K+ >5.5)'],
          sideEffects: ['Dry cough (10-15%)', 'Hyperkalemia', 'Angioedema (higher risk in Africans)', 'Acute kidney injury', 'Teratogenicity'],
          alternatives: ['Enalapril', 'Ramipril', 'Perindopril']
        },
        {
          drug: 'Enalapril',
          indication: '🟠 ALTERNATIVE - ACE inhibitor; twice-daily dosing; well-studied in heart failure',
          dose: 'Adults: Start 5mg PO once or twice daily. Titrate to 10-20mg twice daily. Heart failure: Start 2.5mg twice daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Pregnancy', 'Angioedema history', 'Bilateral RAS', 'Hyperkalemia'],
          sideEffects: ['Dry cough', 'Hyperkalemia', 'Angioedema', 'Hypotension', 'Renal impairment'],
          alternatives: ['Lisinopril', 'Ramipril']
        },
        {
          drug: 'Losartan',
          indication: '🟠 FIRST-LINE - ARB; no cough (alternative if ACEi cough); similar indications to ACEi; uricosuric effect',
          dose: 'Adults: Start 50mg PO once daily. May increase to 100mg once daily. Hepatic impairment: Start 25mg.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Pregnancy', 'Bilateral RAS', 'Hyperkalemia', 'Concurrent use with ACEi (generally)'],
          sideEffects: ['Hyperkalemia', 'Angioedema (less than ACEi but still possible)', 'Hypotension', 'Renal impairment'],
          alternatives: ['Valsartan', 'Irbesartan', 'Telmisartan']
        },
        {
          drug: 'Atenolol',
          indication: '🟡 SECOND-LINE - Beta-blocker; for hypertension WITH angina, post-MI, heart failure, or arrhythmia; less preferred as monotherapy',
          dose: 'Adults: 25-50mg PO once daily. May increase to 100mg once daily. Renal impairment: Dose reduction required.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Severe bradycardia', 'Heart block (2nd/3rd degree)', 'Decompensated heart failure', 'Severe asthma', 'Pheochromocytoma (without alpha blockade)'],
          sideEffects: ['Bradycardia', 'Fatigue', 'Cold extremities', 'Bronchospasm', 'Depression', 'Erectile dysfunction', 'Masking hypoglycemia'],
          alternatives: ['Bisoprolol', 'Metoprolol', 'Carvedilol']
        },
        {
          drug: 'Bisoprolol',
          indication: '🟡 SECOND-LINE - Cardioselective beta-blocker; preferred for heart failure; less bronchospasm than atenolol',
          dose: 'Adults: Start 2.5-5mg PO once daily. Titrate to 10mg once daily. Heart failure: Start 1.25mg, uptitrate slowly.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Severe bradycardia', 'Heart block', 'Decompensated heart failure', 'Severe asthma'],
          sideEffects: ['Bradycardia', 'Fatigue', 'Dizziness', 'Cold extremities'],
          alternatives: ['Carvedilol', 'Metoprolol succinate', 'Nebivolol']
        },
        {
          drug: 'Spironolactone',
          indication: '🟠 ADD-ON - Mineralocorticoid receptor antagonist; for resistant hypertension (4th-line); heart failure; primary aldosteronism',
          dose: 'Adults: 25mg PO once daily. May increase to 50mg once daily for resistant HTN. Monitor potassium closely.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hyperkalemia (K+ >5.5)', 'Severe renal impairment (eGFR <30)', 'Addison disease', 'Concurrent K+ supplements with ACEi/ARB'],
          sideEffects: ['Hyperkalemia', 'Gynecomastia (men)', 'Breast tenderness', 'Menstrual irregularities', 'GI upset'],
          alternatives: ['Eplerenone (less gynecomastia)']
        },
        {
          drug: 'Amlodipine + Lisinopril (Fixed-Dose Combination)',
          indication: '🟠 PREFERRED COMBINATION - CCB + ACEi; synergistic; reduces CCB-related edema; excellent for most patients',
          dose: 'Available combinations: 5/5, 5/10, 10/10, 10/20 mg. Start based on current therapy or 5/5 if initiating combination.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['As per individual components'],
          sideEffects: ['Combined side effects of both agents'],
          alternatives: ['Amlodipine + Losartan', 'Amlodipine + Valsartan']
        },
        {
          drug: 'Amlodipine + Hydrochlorothiazide or HCTZ + Lisinopril',
          indication: '🟠 ALTERNATIVE COMBINATIONS - Single-pill combinations improve adherence; various combinations available',
          dose: 'Dose based on specific combination product. Start based on prior monotherapy response.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['As per individual components'],
          sideEffects: ['As per individual components'],
          alternatives: ['Other fixed-dose combinations']
        }
      ],
      
      nonPharmacological: [
        'DIETARY SALT RESTRICTION: Target <5g salt (<2g sodium) per day; reduces BP by 4-5/2-3 mmHg; even greater effect in Africans',
        'DASH DIET: Dietary Approaches to Stop Hypertension; rich in fruits, vegetables, whole grains, low-fat dairy; reduces BP by 8-14 mmHg',
        'WEIGHT LOSS: Target BMI <25 kg/m²; each 1 kg weight loss reduces BP by ~1 mmHg',
        'PHYSICAL ACTIVITY: 150 minutes moderate or 75 minutes vigorous aerobic exercise per week; reduces BP by 5-8 mmHg',
        'ALCOHOL MODERATION: Limit to ≤2 drinks/day (men), ≤1 drink/day (women); reduces BP by 4 mmHg',
        'SMOKING CESSATION: Does not directly lower BP but dramatically reduces CV risk; essential component',
        'POTASSIUM INTAKE: Increase dietary potassium (fruits, vegetables) unless CKD with hyperkalemia risk',
        'STRESS MANAGEMENT: Relaxation techniques, meditation; modest BP-lowering effect',
        'REDUCE CAFFEINE: Moderate intake acceptable; avoid excessive consumption',
        'HOME BP MONITORING: Empowers patients; improves adherence; provides more readings for clinical decisions'
      ],
      
      surgical: [
        'RENAL DENERVATION: Catheter-based ablation of renal sympathetic nerves; for resistant hypertension; emerging therapy',
        'BARORECEPTOR ACTIVATION THERAPY: Implantable device stimulating carotid baroreceptors; for resistant HTN',
        'SURGICAL TREATMENT OF SECONDARY CAUSES: Adrenalectomy (aldosteronoma, pheochromocytoma), renal artery revascularization',
        'BARIATRIC SURGERY: For morbid obesity with hypertension; significant BP reduction with weight loss'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Hypertensive emergency (BP >180/120 with acute organ damage) - needs immediate IV therapy',
        '🔴 IMMEDIATE REFERRAL: Suspected aortic dissection (severe chest/back pain, BP differential between arms)',
        '🔴 IMMEDIATE REFERRAL: Hypertensive encephalopathy (severe headache, confusion, visual changes, seizures)',
        '🔴 IMMEDIATE REFERRAL: Acute pulmonary edema with severe hypertension',
        '🟠 URGENT REFERRAL: Suspected secondary hypertension requiring specialist workup',
        '🟠 URGENT REFERRAL: Malignant/accelerated hypertension (severe BP with grade 3-4 retinopathy)',
        '🟠 URGENT REFERRAL: Hypertension in pregnancy (preeclampsia evaluation)',
        '🟠 URGENT REFERRAL: Significant renal impairment discovered (eGFR <30 or rapidly declining)',
        '🟡 ROUTINE REFERRAL: Resistant hypertension (uncontrolled on 3 drugs including diuretic at optimal doses)',
        '🟡 ROUTINE REFERRAL: Young-onset hypertension (<30 years) for secondary cause workup',
        '🟡 ROUTINE REFERRAL: Complex comorbidities requiring specialist input',
        '🟡 ROUTINE REFERRAL: Intolerance to multiple antihypertensive classes'
      ],
      
      monitoring: [
        'BP MEASUREMENT: Office BP every 2-4 weeks when titrating, then every 3-6 months when stable',
        'HOME BP: Encourage regular home monitoring; average of morning and evening readings',
        'ELECTROLYTES: Check potassium, sodium, creatinine 1-2 weeks after starting/changing ACEi, ARB, or diuretic',
        'RENAL FUNCTION: Creatinine and eGFR at baseline, 1-2 weeks after ACEi/ARB, then annually if stable',
        'LIPIDS AND GLUCOSE: Annual fasting lipid profile and glucose/HbA1c',
        'ECG: Baseline; repeat if symptoms or every 2-3 years to assess LVH progression',
        'URINALYSIS/uACR: Annual screening for proteinuria',
        'MEDICATION ADHERENCE: Assess at every visit; major cause of poor control',
        'SIDE EFFECTS: Inquire about cough (ACEi), edema (CCB), fatigue (beta-blocker), erectile dysfunction',
        'TARGET ORGAN DAMAGE: Annual assessment; echocardiogram if LVH on ECG or heart failure symptoms'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (Achieving Control):
• Follow-up every 2-4 weeks until BP at target
• Assess response to therapy (expect full effect by 4-6 weeks)
• Titrate medications or add second agent if not at target
• Check electrolytes and creatinine 1-2 weeks after starting/changing ACEi/ARB/diuretic
• Reinforce lifestyle modifications at every visit

MAINTENANCE PHASE (Stable Control):
• Follow-up every 3-6 months once BP stable at target
• Annual comprehensive review: Labs, ECG, medication review
• Continue lifestyle counseling
• Assess for new symptoms suggesting target organ damage

BP TARGETS:

General Population:
• <140/90 mmHg (most guidelines)
• <130/80 mmHg (ACC/AHA for high CV risk; if achievable without adverse effects)

Special Populations:
• Diabetes: <130/80 mmHg
• CKD with proteinuria: <130/80 mmHg
• Elderly (>65 years): <140/90 mmHg; consider <130 systolic if tolerated
• Post-stroke: <130/80 mmHg (after acute phase)
• Pregnancy: Varies by condition; consult obstetric guidelines

ASSESSING CONTROL:
• Controlled: BP at target on most measurements
• Uncontrolled: BP persistently above target despite treatment
• Resistant: BP uncontrolled on ≥3 drugs (including diuretic) at optimal doses

TREATMENT INTENSIFICATION:
If not at target after 4-6 weeks on current therapy:
1. Optimize current medication dose
2. Add second agent from different class
3. Consider fixed-dose combination for adherence
4. Add third agent if needed

Common Effective Combinations:
• CCB + ACEi/ARB (preferred)
• CCB + Thiazide
• ACEi/ARB + Thiazide
• Triple: CCB + ACEi/ARB + Thiazide

RESISTANT HYPERTENSION:
• Confirm adherence (pill counts, pharmacy records)
• Confirm proper BP measurement technique
• Exclude white coat effect (ABPM/home BP)
• Screen for secondary causes
• Add spironolactone (most effective 4th agent)
• Consider specialist referral`
    },

    redFlags: [
      '🔴 CRITICAL: BP >180/120 with chest pain, dyspnea, or neurological symptoms - HYPERTENSIVE EMERGENCY; requires immediate IV therapy and ICU',
      '🔴 CRITICAL: Severe headache with confusion, visual changes, or seizures - suspect hypertensive encephalopathy',
      '🔴 CRITICAL: Severe chest/back pain with BP differential between arms - suspect aortic dissection',
      '🔴 CRITICAL: Acute pulmonary edema with severe hypertension - needs urgent BP reduction and diuresis',
      '🔴 CRITICAL: Papilledema on fundoscopy - indicates malignant/accelerated hypertension; urgent treatment needed',
      '🟠 URGENT: New focal neurological deficit with elevated BP - acute stroke; specific BP management needed',
      '🟠 URGENT: Acute kidney injury with severe hypertension - hypertensive nephropathy or renal artery stenosis',
      '🟠 URGENT: Hypertension in pregnancy with proteinuria, headache, or visual changes - preeclampsia evaluation',
      '🟠 URGENT: Epistaxis not stopping with pressure and severe hypertension',
      '🟡 CAUTION: Resistant hypertension (uncontrolled on 3+ drugs) - evaluate for secondary causes, adherence',
      '🟡 CAUTION: Young patient (<30) with hypertension - higher likelihood of secondary cause',
      '🟡 CAUTION: Unprovoked hypokalemia with hypertension - screen for primary aldosteronism',
      '🟡 CAUTION: Significant creatinine rise (>30%) after starting ACEi/ARB - evaluate for bilateral RAS'
    ],

    complications: [
      'Stroke (ischemic and hemorrhagic) - Most devastating complication; 54% of strokes attributable to HTN; hemorrhagic more common in Africa',
      'Coronary artery disease - Accelerated atherosclerosis; angina, myocardial infarction',
      'Heart failure - Both HFrEF and HFpEF; hypertensive heart disease major cause in Africa',
      'Left ventricular hypertrophy - Compensatory response; increases arrhythmia risk and oxygen demand',
      'Atrial fibrillation - Increased risk with LVH; stroke risk from AF',
      'Chronic kidney disease - Nephrosclerosis from chronic hypertension; may require dialysis',
      'Hypertensive retinopathy - AV nicking to hemorrhages/exudates to papilledema; can cause blindness',
      'Peripheral arterial disease - Claudication; increases CV mortality',
      'Aortic aneurysm and dissection - Life-threatening; requires immediate surgery for dissection',
      'Vascular dementia - Chronic cerebral hypoperfusion; cognitive decline',
      'Hypertensive encephalopathy - Cerebral edema from acute severe HTN; reversible if treated promptly',
      'Malignant/accelerated hypertension - Severe BP with grade 3-4 retinopathy and organ damage',
      'Erectile dysfunction - From vascular damage and medication effects',
      'Sudden cardiac death - From arrhythmias, particularly with LVH',
      'Preeclampsia/eclampsia - In pregnancy; maternal and fetal risks'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Untreated hypertension significantly increases CV morbidity and mortality
• Treated and controlled hypertension approaches risk of normotensive individuals
• Each 10 mmHg reduction in SBP reduces stroke by 27%, heart failure by 28%, major CV events by 20%
• Benefits of treatment seen regardless of baseline BP level

WITH TREATMENT:
• BP control achievable in 60-70% with combination therapy
• Risk reduction proportional to BP lowering achieved
• Earlier treatment initiation associated with better outcomes
• Treatment benefits persist into old age

FACTORS AFFECTING PROGNOSIS:

Better Prognosis:
• Early detection and treatment
• Good BP control achieved
• No or minimal target organ damage at diagnosis
• Adherence to medications and lifestyle changes
• Lower overall cardiovascular risk
• Absence of other risk factors (diabetes, smoking, dyslipidemia)

Worse Prognosis:
• Long duration of uncontrolled hypertension
• Presence of target organ damage (LVH, CKD, retinopathy)
• High cardiovascular risk (diabetes, prior CV events)
• Resistant hypertension
• Poor adherence
• Continued smoking, unhealthy diet

CARDIOVASCULAR RISK REDUCTION:
• Stroke risk reduced by 35-40% with treatment
• Coronary heart disease reduced by 20-25%
• Heart failure reduced by 50%
• All-cause mortality reduced

LIFE EXPECTANCY:
• Untreated hypertension reduces life expectancy by 5-10 years
• Well-controlled hypertension: Near-normal life expectancy
• Each 20/10 mmHg increase in BP doubles CV mortality

AFRICAN POPULATIONS:
• Higher stroke mortality than other populations
• Hemorrhagic stroke more common
• Earlier onset of complications
• Treatment equally effective when achieved
• Greater emphasis on CCB and thiazide diuretics may improve outcomes`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing Hypertension):

Dietary Interventions:
• DASH diet: Fruits, vegetables, whole grains, low-fat dairy, lean protein
• Sodium restriction: <5g salt (<2g sodium) per day
• Increased potassium intake: Fruits, vegetables, legumes (unless CKD)
• Limit processed and fast foods (high sodium content)
• Moderate alcohol: ≤2 drinks/day (men), ≤1 drink/day (women)

Physical Activity:
• 150 minutes moderate-intensity OR 75 minutes vigorous aerobic activity per week
• Additional benefit from resistance training
• Reduce sedentary time

Weight Management:
• Maintain BMI 18.5-24.9 kg/m²
• Even modest weight loss (5-10%) reduces BP

Smoking Cessation:
• Does not directly lower BP but essential for CV risk reduction
• Synergistic effect with hypertension on CV risk

SECONDARY PREVENTION (Preventing Complications):

Blood Pressure Control:
• Achieve and maintain BP target
• Combination of lifestyle and pharmacotherapy
• Regular monitoring and medication adjustment

Comprehensive CV Risk Management:
• Lipid management (statins for high CV risk)
• Glycemic control in diabetes
• Antiplatelet therapy when indicated (secondary prevention)
• Treatment of comorbidities

Adherence Support:
• Simplify regimens (fixed-dose combinations, once-daily dosing)
• Patient education
• Home BP monitoring
• Regular follow-up

SCREENING RECOMMENDATIONS:

Adults:
• BP measurement at every healthcare encounter
• At least annually if normal BP
• More frequently if elevated or high-normal

Children and Adolescents:
• Annual BP measurement starting at age 3
• Earlier if risk factors present

POPULATION-LEVEL INTERVENTIONS:
• Reduce sodium in processed foods (food industry regulation)
• Promote physical activity in communities
• Improve access to fruits and vegetables
• Public education campaigns
• Tobacco control policies
• Improve access to healthcare and medications
• Train healthcare workers in hypertension management`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS HIGH BLOOD PRESSURE (HYPERTENSION)?
Blood pressure is the force of blood pushing against your artery walls. When this pressure is too high, it is called hypertension. High blood pressure is called the "silent killer" because it usually has no symptoms but can damage your heart, brain, kidneys, and eyes over time. The good news is that high blood pressure can be controlled with lifestyle changes and medications.

UNDERSTANDING YOUR NUMBERS:
• Normal: Less than 120/80 mmHg
• Elevated: 120-129 / less than 80
• High Blood Pressure Stage 1: 130-139 / 80-89
• High Blood Pressure Stage 2: 140 or higher / 90 or higher
• Crisis (Seek Emergency Care): Higher than 180/120

The top number (systolic) is the pressure when your heart beats. The bottom number (diastolic) is the pressure when your heart rests between beats.

WHY IS HIGH BLOOD PRESSURE DANGEROUS?
If not controlled, high blood pressure can cause:
• Stroke (brain damage)
• Heart attack and heart failure
• Kidney damage and kidney failure
• Vision problems and blindness
• Memory problems

WHEN TO SEEK HELP IMMEDIATELY:
• Very high BP (above 180/120) with symptoms
• Severe headache
• Chest pain or difficulty breathing
• Weakness or numbness on one side of your body
• Trouble speaking or understanding speech
• Vision changes
• Severe nosebleed that won't stop

LIFESTYLE CHANGES THAT LOWER BLOOD PRESSURE:

Eat Healthy:
• Reduce salt (avoid adding salt to food, avoid processed foods)
• Eat more fruits, vegetables, and whole grains
• Choose lean meats and low-fat dairy
• Limit fried and fatty foods

Be Active:
• Walk, bike, swim, or exercise for at least 30 minutes most days
• Even short walks help - any activity is better than none
• Take stairs instead of elevators

Maintain Healthy Weight:
• Losing even a small amount of weight helps lower BP

Limit Alcohol:
• No more than 2 drinks per day for men
• No more than 1 drink per day for women

Quit Smoking:
• Smoking damages your blood vessels
• Quitting reduces your heart disease risk

Manage Stress:
• Find healthy ways to cope with stress
• Get enough sleep (7-8 hours per night)

ABOUT YOUR MEDICATIONS:
• Take your medications EVERY DAY, even if you feel fine
• Do not stop medications without talking to your doctor
• If you have side effects, talk to your doctor - different medications can be tried
• Missing doses can cause your blood pressure to go up
• Keep a regular schedule (same time each day)
• Use a pillbox if it helps you remember

HOME BLOOD PRESSURE MONITORING:
• Check your BP at home regularly
• Sit quietly for 5 minutes before measuring
• Keep your feet flat on the floor, arm supported at heart level
• Take 2 readings and record the average
• Bring your log to doctor visits

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. High blood pressure is a lifelong condition that requires ongoing management. With proper treatment and lifestyle changes, you can control your blood pressure and prevent complications. Work closely with your healthcare provider.

QUESTIONS TO ASK YOUR DOCTOR:
• What is my blood pressure reading?
• What is my target blood pressure?
• How often should I check my blood pressure?
• What are the side effects of my medication?
• Can I ever stop taking medication?
• What lifestyle changes should I focus on?`,

    references: [
      'World Health Organization. Guideline for the pharmacological treatment of hypertension in adults. WHO. 2021.',
      'Williams B, Mancia G, et al. 2018 ESC/ESH Guidelines for the management of arterial hypertension. Eur Heart J. 2018;39(33):3021-3104.',
      'Whelton PK, Carey RM, et al. 2017 ACC/AHA Guideline for the Prevention, Detection, Evaluation, and Management of High Blood Pressure in Adults. J Am Coll Cardiol. 2018;71(19):e127-e248.',
      'Unger T, Borghi C, et al. 2020 International Society of Hypertension Global Hypertension Practice Guidelines. Hypertension. 2020;75(6):1334-1357.',
      'NCD Risk Factor Collaboration. Worldwide trends in hypertension prevalence and progress in treatment and control from 1990 to 2019. Lancet. 2021;398(10304):957-980.',
      'Dzudie A, Rayner B, et al. Roadmap to achieve 25% hypertension control in Africa by 2025. Glob Heart. 2018;13(1):45-59.',
      'Ataklte F, Erqou S, et al. Burden of undiagnosed hypertension in sub-Saharan Africa: A systematic review and meta-analysis. Hypertension. 2015;65(2):291-298.',
      'Brewster LM, Seedat YK. Why do hypertensive patients of African ancestry respond better to calcium blockers and diuretics than to ACE inhibitors and beta-adrenergic blockers? A systematic review. BMC Med. 2013;11:141.',
      'Ojji DB, Mayosi B, et al. Comparison of Dual Therapies for Lowering Blood Pressure in Black Africans. N Engl J Med. 2019;380(25):2429-2439.',
      'Noubiap JJ, Bigna JJ, et al. The burden of hypertension in Africa: A systematic review and meta-analysis. Int J Hypertens. 2019;2019:8675783.',
      'Appel LJ, Brands MW, et al. Dietary approaches to prevent and treat hypertension: a scientific statement from the American Heart Association. Hypertension. 2006;47(2):296-308.',
      'James PA, Oparil S, et al. 2014 Evidence-Based Guideline for the Management of High Blood Pressure in Adults (JNC 8). JAMA. 2014;311(5):507-520.',
      'SPRINT Research Group. A Randomized Trial of Intensive versus Standard Blood-Pressure Control. N Engl J Med. 2015;373(22):2103-2116.',
      'Seedat YK, Rayner BL, Veriava Y. South African hypertension practice guideline 2014. Cardiovasc J Afr. 2014;25(6):288-294.',
      'Carey RM, Calhoun DA, et al. Resistant Hypertension: Detection, Evaluation, and Management. Hypertension. 2018;72(5):e53-e90.'
    ],
    
    lastUpdated: '2026-01-18',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 66. TYPE 2 DIABETES MELLITUS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '66',
    name: 'Type 2 Diabetes Mellitus',
    slug: 'type-2-diabetes-mellitus',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E11',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Type 2 diabetes mellitus (T2DM) is a chronic metabolic disorder characterized by hyperglycemia resulting from insulin resistance and progressive beta-cell dysfunction. Affecting over 537 million adults globally (10.5% of adult population), T2DM is the most common form of diabetes, accounting for 90-95% of all cases. The disease is associated with significant morbidity from microvascular complications (retinopathy, nephropathy, neuropathy) and macrovascular disease (cardiovascular disease, stroke, peripheral arterial disease), as well as reduced life expectancy.

The pathophysiology involves a complex interplay between genetic susceptibility and environmental factors. Insulin resistance in muscle, liver, and adipose tissue leads to compensatory hyperinsulinemia. When beta-cells can no longer maintain this compensation, hyperglycemia develops. This is accompanied by other defects including increased hepatic glucose production, incretin deficiency, increased glucagon secretion, and renal glucose reabsorption. The "ominous octet" of pathophysiological defects provides multiple therapeutic targets.

Sub-Saharan Africa is experiencing a diabetes epidemic, with prevalence projected to increase by 134% by 2045 - the highest regional increase globally. Current estimates suggest 24 million adults have diabetes in Africa, but up to 60% are undiagnosed. The region faces unique challenges: late presentation with complications already established, limited access to medications (particularly insulin), lack of glucose monitoring equipment, high cost of care relative to income, and competing health priorities (infectious diseases). Diabetic foot disease is particularly devastating, with amputation rates among the highest globally due to delayed presentation and limited wound care resources.

Management requires a comprehensive approach: lifestyle modification (diet and exercise), glycemic control with oral agents and/or insulin, cardiovascular risk reduction (blood pressure, lipids, antiplatelet therapy), and regular screening for complications. Patient education and self-management support are essential. Treatment must be individualized based on patient characteristics, comorbidities, and available resources.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Screen ALL diabetics for complications at diagnosis and annually: eyes (retinopathy), feet (neuropathy/PAD), kidneys (uACR, eGFR), cardiovascular disease

🔴 CRITICAL POINT 2 - Foot examination at EVERY visit - diabetic foot disease is the leading cause of non-traumatic amputation; most are preventable

🔴 CRITICAL POINT 3 - Start metformin at diagnosis (unless contraindicated) PLUS lifestyle modification; do not delay pharmacotherapy

🔴 CRITICAL POINT 4 - If HbA1c ≥10% or symptomatic hyperglycemia, consider starting insulin early; do not delay insulin when needed

🟠 CRITICAL POINT 5 - Cardiovascular disease is the leading cause of death in T2DM - treat hypertension (<130/80) and dyslipidemia (statin for most patients)

🟠 CRITICAL POINT 6 - ACE inhibitor or ARB for ALL patients with albuminuria (uACR ≥30 mg/g) regardless of blood pressure

🟠 CRITICAL POINT 7 - SGLT2 inhibitors and GLP-1 agonists reduce cardiovascular and renal events - prefer these if available and affordable for patients with CVD or CKD

🟡 CRITICAL POINT 8 - Individualize HbA1c targets: <7% for most, <6.5% if achievable without hypoglycemia, <8% for elderly/frail/limited life expectancy

🟡 CRITICAL POINT 9 - Educate patients on hypoglycemia recognition and treatment, especially if on sulfonylureas or insulin`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 537 million adults with diabetes worldwide (2021); projected 783 million by 2045
• Type 2 diabetes: 90-95% of all diabetes cases
• Global prevalence: 10.5% of adults (20-79 years)
• 6.7 million deaths attributed to diabetes annually
• 541 million adults with impaired glucose tolerance (prediabetes)
• 50% of people with diabetes are undiagnosed globally
• Total health expenditure: $966 billion USD annually
• Leading cause of blindness, kidney failure, non-traumatic amputation

AFRICAN EPIDEMIOLOGY:
• 24 million adults with diabetes in Africa (2021)
• Prevalence: 4.5% (likely underestimated due to underdiagnosis)
• Projected 134% increase by 2045 - highest growth rate globally
• 60% of people with diabetes in Africa are undiagnosed
• Nigeria: 3-5 million people with diabetes; many undiagnosed
• South Africa: 12-14% adult prevalence in some studies
• Urban prevalence 2-3 times higher than rural
• Rapidly increasing due to urbanization, dietary changes, obesity
• High rates of complications at diagnosis (late presentation)
• Diabetic foot: 15-25% of diabetics will develop foot ulcer; high amputation rates

RISK FACTORS:
• Non-modifiable: Age >45, family history, ethnicity (higher in certain groups), history of gestational diabetes
• Modifiable: Overweight/obesity, physical inactivity, unhealthy diet, smoking
• Metabolic: Prediabetes (IFG, IGT), metabolic syndrome, PCOS
• Hypertension, dyslipidemia, cardiovascular disease
• Low birth weight (fetal origins hypothesis)
• Medications: Corticosteroids, thiazides (high dose), atypical antipsychotics

PREDIABETES:
• Impaired fasting glucose (IFG): Fasting glucose 100-125 mg/dL (5.6-6.9 mmol/L)
• Impaired glucose tolerance (IGT): 2-hour glucose 140-199 mg/dL (7.8-11.0 mmol/L)
• HbA1c 5.7-6.4% (39-47 mmol/mol)
• Annual progression to diabetes: 5-10%
• Lifestyle intervention can prevent or delay progression by 58%`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLASSIC HYPERGLYCEMIC SYMPTOMS:
• Polyuria (frequent urination) - osmotic diuresis from glucosuria
• Polydipsia (excessive thirst) - compensation for fluid loss
• Polyphagia (increased hunger) - cellular glucose deprivation
• Unexplained weight loss - protein catabolism, fluid loss
• Fatigue and weakness
• Blurred vision - lens swelling from hyperglycemia

OFTEN ASYMPTOMATIC:
• Many patients diagnosed incidentally on routine screening
• May be asymptomatic for years while complications develop
• Up to 50% have complications at diagnosis

SYMPTOMS OF COMPLICATIONS:
• Retinopathy: Blurred vision, floaters, visual field defects, blindness
• Nephropathy: Often asymptomatic; edema, foamy urine (late); uremia symptoms (very late)
• Neuropathy: Numbness, tingling, burning in feet (stocking-glove), pain worse at night
• Cardiovascular: Chest pain, dyspnea, claudication, erectile dysfunction
• Foot disease: Ulcers, infections, Charcot foot, gangrene

SKIN MANIFESTATIONS:
• Acanthosis nigricans (velvety darkening of skin folds) - insulin resistance marker
• Skin tags
• Recurrent skin infections (bacterial, fungal)
• Diabetic dermopathy (shin spots)
• Necrobiosis lipoidica diabeticorum (rare)

PHYSICAL EXAMINATION:

General:
• BMI and waist circumference (central obesity)
• Blood pressure (hypertension common)
• Signs of insulin resistance: Acanthosis nigricans

Cardiovascular:
• Heart sounds, murmurs
• Peripheral pulses (dorsalis pedis, posterior tibial)
• Carotid bruits
• Signs of heart failure

Feet (ESSENTIAL at every visit):
• Inspection: Ulcers, calluses, deformities, skin changes, nail abnormalities
• Pulses: Dorsalis pedis, posterior tibial (absent suggests PAD)
• Sensation: 10g monofilament (5 sites), 128Hz tuning fork (vibration)
• Reflexes: Ankle reflex (often reduced)
• Skin: Dry, cracked, fissured skin; temperature (cool = PAD)

Eyes:
• Visual acuity
• Fundoscopy: Microaneurysms, hemorrhages, exudates, neovascularization

Neurological:
• Peripheral neuropathy assessment
• Autonomic neuropathy signs (resting tachycardia, orthostatic hypotension)`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CONFIRM DIAGNOSIS
• Requires TWO abnormal tests on separate days (if asymptomatic)
• Single test sufficient if symptomatic with classic hyperglycemia

DIAGNOSTIC CRITERIA (Any ONE of the following):

Fasting Plasma Glucose (FPG):
• ≥126 mg/dL (7.0 mmol/L) on two occasions
• Fasting = no caloric intake for ≥8 hours

Random Plasma Glucose:
• ≥200 mg/dL (11.1 mmol/L) WITH classic symptoms (polyuria, polydipsia, weight loss)
• Single value sufficient if symptomatic

HbA1c:
• ≥6.5% (48 mmol/mol) on two occasions
• Use NGSP-certified method

Oral Glucose Tolerance Test (OGTT):
• 2-hour glucose ≥200 mg/dL (11.1 mmol/L) after 75g glucose load

PREDIABETES CRITERIA:
• FPG 100-125 mg/dL (5.6-6.9 mmol/L) - Impaired Fasting Glucose
• 2-hour OGTT 140-199 mg/dL (7.8-11.0 mmol/L) - Impaired Glucose Tolerance
• HbA1c 5.7-6.4% (39-47 mmol/mol)

STEP 2: CLASSIFY DIABETES TYPE
• Type 2: Most common; insulin resistance + progressive beta-cell failure
• Type 1: Autoimmune beta-cell destruction; usually young, lean, ketosis-prone
• Other: LADA, MODY, gestational, secondary (pancreatitis, steroids)

Features favoring Type 2:
• Age >30 at onset (though increasingly diagnosed in youth)
• Overweight or obese
• Family history of T2DM
• Features of insulin resistance (acanthosis nigricans)
• No ketosis at presentation
• Gradual onset

STEP 3: ASSESS FOR COMPLICATIONS
At diagnosis and annually:
• Eyes: Dilated fundoscopy or retinal photography
• Kidneys: Serum creatinine, eGFR, urine albumin-creatinine ratio (uACR)
• Feet: Comprehensive foot exam
• Cardiovascular: BP, lipids, symptoms of CVD
• Neuropathy: Monofilament, vibration sense

STEP 4: CARDIOVASCULAR RISK ASSESSMENT
• Calculate 10-year CV risk
• Assess: BP, lipids, smoking, family history
• ECG (baseline)

STEP 5: ESTABLISH TREATMENT GOALS
• Individualize HbA1c target
• BP target: <130/80 mmHg
• LDL target: Based on CV risk (statin for most)`,

    differentialDiagnosis: [
      'Type 1 diabetes mellitus - Younger age, lean, rapid onset, ketosis-prone, positive autoantibodies (GAD, IA-2, ZnT8)',
      'Latent autoimmune diabetes of adults (LADA) - Adult onset but autoimmune; initially responds to oral agents then needs insulin',
      'Maturity-onset diabetes of the young (MODY) - Young onset, autosomal dominant family history, specific genetic mutations',
      'Secondary diabetes - Pancreatic disease, Cushing syndrome, acromegaly, hemochromatosis, medications (steroids)',
      'Gestational diabetes - Diabetes diagnosed during pregnancy; usually resolves postpartum but high T2DM risk',
      'Stress hyperglycemia - Acute illness causes transient hyperglycemia; repeat testing when recovered',
      'Chronic kidney disease - Can cause impaired glucose metabolism; also affects HbA1c interpretation',
      'Hemoglobinopathies - Affect HbA1c accuracy (sickle cell, thalassemia); use fructosamine or glucose-based criteria',
      'Prediabetes - IFG, IGT, or HbA1c 5.7-6.4%; high risk for progression; intensive lifestyle intervention indicated',
      'Drug-induced hyperglycemia - Corticosteroids, thiazides (high dose), atypical antipsychotics, HIV medications',
      'Endocrine disorders - Hyperthyroidism, pheochromocytoma, glucagonoma can cause hyperglycemia',
      'Pancreatic diabetes - Chronic pancreatitis, pancreatic cancer, cystic fibrosis; often brittle, insulin-requiring',
      'Cushing syndrome - Central obesity, striae, moon facies, hypertension; elevated cortisol',
      'Acromegaly - Enlarged features, headache, visual field defects; elevated IGF-1',
      'Polycystic ovary syndrome - Insulin resistance, oligomenorrhea, hyperandrogenism; high T2DM risk'
    ],

    investigations: [
      {
        name: 'Fasting Plasma Glucose',
        indication: '🔴 ESSENTIAL - Diagnostic test; ≥126 mg/dL (7.0 mmol/L) on two occasions confirms diabetes',
        availability: 'BASIC'
      },
      {
        name: 'Random Plasma Glucose',
        indication: '🔴 ESSENTIAL - With symptoms; ≥200 mg/dL (11.1 mmol/L) with classic symptoms is diagnostic',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c (Glycated Hemoglobin)',
        indication: '🔴 ESSENTIAL - Diagnostic (≥6.5%) and monitoring (every 3-6 months); reflects 2-3 month average glucose',
        availability: 'BASIC'
      },
      {
        name: 'Urine Dipstick (Glucose, Ketones, Protein)',
        indication: '🔴 ESSENTIAL - Screening; glucose suggests hyperglycemia; ketones suggest insulin deficiency; protein suggests nephropathy',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and eGFR',
        indication: '🔴 ESSENTIAL - Assess renal function; screen for diabetic nephropathy; guide medication dosing',
        availability: 'BASIC'
      },
      {
        name: 'Urine Albumin-Creatinine Ratio (uACR)',
        indication: '🔴 ESSENTIAL - Screen for diabetic nephropathy; ≥30 mg/g indicates albuminuria; repeat annually',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Fasting Lipid Profile',
        indication: '🔴 ESSENTIAL - Total cholesterol, LDL, HDL, triglycerides; CV risk assessment; guide statin therapy',
        availability: 'BASIC'
      },
      {
        name: 'Blood Pressure',
        indication: '🔴 ESSENTIAL - At every visit; hypertension common; target <130/80 mmHg',
        availability: 'BASIC'
      },
      {
        name: 'Electrocardiogram (ECG)',
        indication: '🟠 IMPORTANT - Baseline; detect silent MI; pre-exercise evaluation if indicated',
        availability: 'BASIC'
      },
      {
        name: 'Dilated Eye Examination / Retinal Photography',
        indication: '🔴 ESSENTIAL - At diagnosis and annually; detect diabetic retinopathy; refer if abnormal',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Foot Examination (Monofilament, Pulses)',
        indication: '🔴 ESSENTIAL - At EVERY visit; 10g monofilament, pedal pulses; identify high-risk feet',
        availability: 'BASIC'
      },
      {
        name: 'Oral Glucose Tolerance Test (OGTT)',
        indication: '🟡 WHEN NEEDED - If FPG borderline or suspected gestational diabetes; 75g glucose load',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🟠 IMPORTANT - Baseline; anemia affects HbA1c; check for infection',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT - Baseline before metformin; NAFLD common in T2DM; monitor if on certain medications',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function (TSH)',
        indication: '🟡 WHEN INDICATED - Screen if symptoms; thyroid disease more common in diabetes',
        availability: 'BASIC'
      },
      {
        name: 'C-Peptide',
        indication: '🟡 WHEN INDICATED - Assess endogenous insulin production; distinguish T1DM from T2DM if uncertain',
        availability: 'ADVANCED'
      },
      {
        name: 'GAD Antibodies',
        indication: '🟡 WHEN INDICATED - Suspect Type 1 or LADA; young, lean, rapid progression to insulin',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Two abnormal tests required if asymptomatic; single test if symptomatic',
        '⚡ ASSESS SEVERITY: Check for DKA/HHS if severe hyperglycemia (glucose >300), ketones, dehydration',
        '⚡ BASELINE ASSESSMENT: HbA1c, renal function, lipids, BP, weight, foot exam, eye exam',
        '⚡ CARDIOVASCULAR RISK: Calculate CV risk; most T2DM patients are high CV risk',
        '⚡ SCREEN FOR COMPLICATIONS: Eyes, kidneys (uACR), feet, cardiovascular - many have complications at diagnosis',
        '⚡ START METFORMIN: Unless contraindicated; begin at diagnosis along with lifestyle modification',
        '⚡ LIFESTYLE COUNSELING: Diet (reduce carbohydrates, avoid sugar-sweetened beverages), exercise, weight loss',
        '⚡ PATIENT EDUCATION: Explain disease, importance of adherence, self-monitoring, hypoglycemia recognition',
        '⚡ SET TARGETS: Individualize HbA1c target (usually <7%); BP <130/80; LDL based on risk',
        '⚡ VACCINATION: Ensure pneumococcal and annual influenza vaccination'
      ],
      
      pharmacological: [
        {
          drug: 'Metformin',
          indication: '🔴 FIRST-LINE - Start at diagnosis for ALL patients unless contraindicated; reduces hepatic glucose production',
          dose: 'Adults: Start 500mg PO once or twice daily with meals. Increase gradually over weeks to 1000mg twice daily (max 2550mg/day). Extended-release: Better GI tolerability.',
          route: 'PO',
          duration: 'Long-term; continue unless contraindicated',
          contraindications: ['eGFR <30 (use caution 30-45)', 'Acute illness with risk of lactic acidosis', 'Severe hepatic impairment', 'Contrast procedures (hold 48h)', 'Active alcoholism'],
          sideEffects: ['GI upset (nausea, diarrhea) - usually improves', 'B12 deficiency (check periodically)', 'Lactic acidosis (rare, in contraindicated patients)'],
          alternatives: ['If intolerant: Sulfonylurea, DPP-4 inhibitor, or SGLT2 inhibitor']
        },
        {
          drug: 'Glibenclamide (Glyburide)',
          indication: '🟠 SECOND-LINE - Sulfonylurea; stimulates insulin secretion; low cost; widely available in Africa',
          dose: 'Adults: Start 2.5-5mg PO once daily with breakfast. Increase gradually. Maximum 20mg/day (in 1-2 doses). Elderly: Start lower (1.25-2.5mg).',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes', 'DKA', 'Severe renal impairment (prefer shorter-acting SU)', 'Severe hepatic impairment', 'Sulfonamide allergy'],
          sideEffects: ['Hypoglycemia (significant risk, especially in elderly/renal impairment)', 'Weight gain', 'Rash'],
          alternatives: ['Gliclazide (less hypoglycemia)', 'Glimepiride']
        },
        {
          drug: 'Gliclazide',
          indication: '🟠 SECOND-LINE - Sulfonylurea; lower hypoglycemia risk than glibenclamide; preferred in elderly',
          dose: 'Immediate-release: Start 40-80mg PO once daily. Maximum 320mg/day. Modified-release: Start 30mg once daily, max 120mg.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes', 'DKA', 'Severe renal/hepatic impairment'],
          sideEffects: ['Hypoglycemia (less than glibenclamide)', 'Weight gain', 'GI upset'],
          alternatives: ['Glibenclamide', 'Glimepiride']
        },
        {
          drug: 'Glimepiride',
          indication: '🟠 SECOND-LINE - Sulfonylurea; once-daily dosing; intermediate hypoglycemia risk',
          dose: 'Adults: Start 1-2mg PO once daily with breakfast. Increase by 1-2mg every 1-2 weeks. Maximum 8mg/day.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes', 'DKA', 'Severe renal/hepatic impairment'],
          sideEffects: ['Hypoglycemia', 'Weight gain', 'GI upset'],
          alternatives: ['Gliclazide', 'Glibenclamide']
        },
        {
          drug: 'Empagliflozin',
          indication: '🟠 PREFERRED ADD-ON - SGLT2 inhibitor; CV and renal protection; weight loss; use if CVD or CKD present',
          dose: 'Adults: 10mg PO once daily in the morning. May increase to 25mg daily. Avoid if eGFR <30 (reduced efficacy, not safety).',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes (risk of DKA)', 'Severe renal impairment (eGFR <30)', 'Recurrent UTI or genital infections'],
          sideEffects: ['Genital mycotic infections', 'UTI', 'Volume depletion', 'Euglycemic DKA (rare)', 'Fournier gangrene (very rare)'],
          alternatives: ['Dapagliflozin', 'Canagliflozin']
        },
        {
          drug: 'Dapagliflozin',
          indication: '🟠 PREFERRED ADD-ON - SGLT2 inhibitor; CV benefit, heart failure reduction, renal protection',
          dose: 'Adults: 10mg PO once daily in the morning. No dose adjustment needed for renal impairment down to eGFR 25.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes', 'Severe renal impairment', 'Recurrent genital infections'],
          sideEffects: ['Genital mycotic infections', 'UTI', 'Hypotension/dehydration', 'Euglycemic DKA'],
          alternatives: ['Empagliflozin', 'Canagliflozin']
        },
        {
          drug: 'Sitagliptin',
          indication: '🟠 ADD-ON - DPP-4 inhibitor; weight neutral; low hypoglycemia risk; can use in renal impairment with dose adjustment',
          dose: 'Adults: 100mg PO once daily. Renal adjustment: eGFR 30-45: 50mg daily; eGFR <30: 25mg daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['History of pancreatitis', 'Hypersensitivity'],
          sideEffects: ['Nasopharyngitis', 'Headache', 'Pancreatitis (rare)', 'Joint pain'],
          alternatives: ['Linagliptin (no renal adjustment)', 'Vildagliptin']
        },
        {
          drug: 'Linagliptin',
          indication: '🟠 ADD-ON - DPP-4 inhibitor; no dose adjustment in renal impairment; hepatic elimination',
          dose: 'Adults: 5mg PO once daily. No adjustment needed for renal or hepatic impairment.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['History of pancreatitis', 'Hypersensitivity'],
          sideEffects: ['Nasopharyngitis', 'Hypoglycemia (if with SU/insulin)', 'Pancreatitis (rare)'],
          alternatives: ['Sitagliptin', 'Vildagliptin']
        },
        {
          drug: 'Liraglutide',
          indication: '🟠 PREFERRED ADD-ON - GLP-1 receptor agonist; CV benefit proven; weight loss; requires injection',
          dose: 'Adults: Start 0.6mg SC once daily for 1 week, then 1.2mg daily. May increase to 1.8mg daily for additional glycemic control.',
          route: 'SC injection',
          duration: 'Long-term',
          contraindications: ['Personal/family history of medullary thyroid carcinoma', 'MEN2 syndrome', 'History of pancreatitis'],
          sideEffects: ['Nausea, vomiting (dose-dependent, usually transient)', 'Diarrhea', 'Pancreatitis (rare)', 'Injection site reactions'],
          alternatives: ['Semaglutide (once weekly)', 'Dulaglutide']
        },
        {
          drug: 'Semaglutide (Subcutaneous)',
          indication: '🟠 PREFERRED ADD-ON - GLP-1 RA; once-weekly injection; significant CV benefit and weight loss',
          dose: 'Adults: Start 0.25mg SC once weekly for 4 weeks, then 0.5mg weekly. May increase to 1mg weekly after 4+ weeks.',
          route: 'SC injection (weekly)',
          duration: 'Long-term',
          contraindications: ['Personal/family history of MTC', 'MEN2', 'Pancreatitis history'],
          sideEffects: ['Nausea, vomiting', 'Diarrhea', 'Constipation', 'Pancreatitis (rare)', 'Retinopathy worsening (if rapid glucose improvement)'],
          alternatives: ['Liraglutide', 'Dulaglutide']
        },
        {
          drug: 'Pioglitazone',
          indication: '🟡 THIRD-LINE - Thiazolidinedione; improves insulin sensitivity; use with caution due to side effects',
          dose: 'Adults: Start 15-30mg PO once daily. Maximum 45mg/day. Takes 8-12 weeks for full effect.',
          route: 'PO',
          duration: 'Long-term if tolerated',
          contraindications: ['Heart failure (NYHA III-IV)', 'Active bladder cancer', 'Hepatic impairment'],
          sideEffects: ['Weight gain', 'Edema', 'Heart failure exacerbation', 'Fracture risk (women)', 'Bladder cancer (controversial)'],
          alternatives: ['Other oral agents']
        },
        {
          drug: 'Insulin NPH (Isophane)',
          indication: '🔴 WHEN NEEDED - Intermediate-acting basal insulin; start if HbA1c ≥10%, symptomatic, or oral agents inadequate',
          dose: 'Adults: Start 10 units or 0.1-0.2 units/kg SC at bedtime. Titrate by 2-4 units every 3-7 days to fasting glucose target (80-130 mg/dL).',
          route: 'SC injection',
          duration: 'Long-term once initiated',
          contraindications: ['Hypoglycemia unawareness (use with caution)'],
          sideEffects: ['Hypoglycemia', 'Weight gain', 'Injection site lipohypertrophy', 'Allergic reactions (rare)'],
          alternatives: ['Insulin glargine', 'Insulin detemir']
        },
        {
          drug: 'Insulin Glargine',
          indication: '🟠 PREFERRED BASAL - Long-acting basal insulin; once-daily, peakless; lower nocturnal hypoglycemia than NPH',
          dose: 'Adults: Start 10 units or 0.1-0.2 units/kg SC once daily (same time each day). Titrate by 2-4 units every 3-7 days.',
          route: 'SC injection',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Hypoglycemia (less than NPH)', 'Weight gain', 'Lipohypertrophy'],
          alternatives: ['Insulin detemir', 'Insulin degludec', 'NPH insulin']
        },
        {
          drug: 'Soluble (Regular) Insulin',
          indication: '🟠 PRANDIAL INSULIN - Short-acting; for mealtime coverage when basal insulin insufficient',
          dose: 'Adults: Start 4 units or 10% of basal dose before largest meal. Adjust based on post-meal glucose. Give 30 min before meals.',
          route: 'SC injection',
          duration: 'With meals',
          contraindications: ['Hypoglycemia'],
          sideEffects: ['Hypoglycemia', 'Weight gain'],
          alternatives: ['Rapid-acting insulin analogues (aspart, lispro)']
        }
      ],
      
      nonPharmacological: [
        'MEDICAL NUTRITION THERAPY: Individualized meal plan; reduce refined carbohydrates and sugars; emphasize vegetables, whole grains, lean protein',
        'CARBOHYDRATE MANAGEMENT: Consistent carbohydrate intake; avoid sugar-sweetened beverages; moderate portions',
        'WEIGHT LOSS: Target 5-10% initial weight loss for overweight/obese patients; significant glycemic improvement',
        'PHYSICAL ACTIVITY: 150 minutes/week moderate aerobic activity; include resistance training 2-3 times/week',
        'SMOKING CESSATION: Essential for cardiovascular risk reduction; provide support and pharmacotherapy',
        'ALCOHOL MODERATION: Limit to 1 drink/day (women), 2 drinks/day (men); avoid on empty stomach (hypoglycemia risk)',
        'DIABETES SELF-MANAGEMENT EDUCATION: Structured program covering all aspects of diabetes care; essential for outcomes',
        'SELF-MONITORING OF BLOOD GLUCOSE: Frequency based on regimen; essential for insulin users; guides adjustments',
        'FOOT CARE EDUCATION: Daily inspection, proper footwear, avoid barefoot walking, report any injury immediately',
        'STRESS MANAGEMENT: Stress affects glucose control; relaxation techniques, adequate sleep'
      ],
      
      surgical: [
        'BARIATRIC/METABOLIC SURGERY: Consider for BMI ≥40, or BMI 35-39.9 with difficult-to-control diabetes; can induce remission',
        'AMPUTATION: For non-salvageable diabetic foot disease; prevent progression of infection/gangrene',
        'DEBRIDEMENT: Surgical cleaning of diabetic foot ulcers; essential for wound healing',
        'REVASCULARIZATION: Angioplasty or bypass for peripheral arterial disease with critical limb ischemia',
        'VITRECTOMY: For non-clearing vitreous hemorrhage or tractional retinal detachment from diabetic retinopathy',
        'CATARACT SURGERY: Cataracts more common and occur earlier in diabetes',
        'RENAL TRANSPLANT: For end-stage diabetic nephropathy; combined kidney-pancreas transplant in selected cases'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: DKA or HHS - emergency management required',
        '🔴 IMMEDIATE REFERRAL: Severe hypoglycemia with altered consciousness',
        '🔴 IMMEDIATE REFERRAL: Diabetic foot infection with systemic signs or deep tissue involvement',
        '🔴 IMMEDIATE REFERRAL: Acute vision loss (possible vitreous hemorrhage or retinal detachment)',
        '🟠 URGENT REFERRAL: Diabetic foot ulcer - needs multidisciplinary foot care',
        '🟠 URGENT REFERRAL: Proliferative diabetic retinopathy or macular edema - needs laser/injection',
        '🟠 URGENT REFERRAL: Rapidly declining renal function or eGFR <30 - nephrologist',
        '🟠 URGENT REFERRAL: Suspected Type 1 diabetes or LADA - endocrinologist for confirmation',
        '🟡 ROUTINE REFERRAL: Newly diagnosed for diabetes education program',
        '🟡 ROUTINE REFERRAL: Not achieving glycemic targets despite multiple oral agents - insulin initiation support',
        '🟡 ROUTINE REFERRAL: Annual dilated eye examination - ophthalmologist/optometrist',
        '🟡 ROUTINE REFERRAL: Complex insulin regimens or insulin pump consideration - endocrinologist',
        '🟡 ROUTINE REFERRAL: Preconception counseling for women with diabetes planning pregnancy'
      ],
      
      monitoring: [
        'HbA1c: Every 3 months if not at target; every 6 months if stable at target',
        'FASTING/RANDOM GLUCOSE: As needed; more frequent if adjusting therapy',
        'SELF-MONITORING OF BLOOD GLUCOSE: Per regimen; essential for insulin users; before meals, bedtime, if symptomatic',
        'WEIGHT: At every visit; track trends',
        'BLOOD PRESSURE: At every visit; target <130/80 mmHg',
        'LIPIDS: Annually; more frequently if adjusting lipid therapy',
        'RENAL FUNCTION: Creatinine, eGFR, and uACR annually; more frequently if abnormal',
        'EYE EXAMINATION: Dilated exam annually; more frequently if retinopathy present',
        'FOOT EXAMINATION: At EVERY visit; comprehensive annual exam including monofilament',
        'MEDICATION REVIEW: Assess adherence, side effects, need for intensification at each visit',
        'VACCINATION STATUS: Annual influenza; pneumococcal per guidelines; hepatitis B if not immune'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (First 3-6 months):
• More frequent visits (every 2-4 weeks) while titrating therapy
• Diabetes self-management education
• HbA1c at 3 months to assess response
• Reinforce lifestyle modifications
• Address barriers to adherence

STABLE PHASE:
• Visits every 3-6 months
• HbA1c every 3-6 months
• Annual comprehensive review

ANNUAL COMPREHENSIVE REVIEW:
• HbA1c (if not done in past 3 months)
• Lipid profile
• Renal function (creatinine, eGFR, uACR)
• Dilated eye examination
• Comprehensive foot examination
• Blood pressure review
• Medication review and adjustment
• Weight and BMI
• Vaccination update
• Diabetes distress/depression screening

GLYCEMIC TARGETS:

HbA1c:
• Most adults: <7% (53 mmol/mol)
• More stringent (<6.5%): If achievable without hypoglycemia, short duration, long life expectancy
• Less stringent (<8%): History of severe hypoglycemia, limited life expectancy, advanced complications, frail elderly

Pre-meal Glucose: 80-130 mg/dL (4.4-7.2 mmol/L)
Post-meal Glucose (1-2h): <180 mg/dL (<10.0 mmol/L)

TREATMENT INTENSIFICATION:

If not at HbA1c target after 3 months:
1. Optimize current therapy (dose, adherence)
2. Add second oral agent
3. Consider GLP-1 RA or SGLT2i (especially if CVD, HF, or CKD)
4. Add basal insulin if still not at target
5. Intensify insulin regimen if needed (add prandial)

TIME TO INSULIN:
• Consider early if: HbA1c ≥10%, symptomatic hyperglycemia, catabolic features
• Do not delay insulin when oral agents failing
• Starting insulin does not mean failure - it is progression of disease

MANAGING COMPLICATIONS:
• Retinopathy: Tight glycemic and BP control; laser/anti-VEGF for severe
• Nephropathy: ACEi/ARB regardless of BP if albuminuria; SGLT2i for renal protection
• Neuropathy: Optimize glucose; pain management (pregabalin, duloxetine, gabapentin)
• Foot disease: Regular podiatry, protective footwear, wound care, revascularization if needed
• CVD: High-intensity statin, aspirin if established CVD, BP control, SGLT2i/GLP-1 RA`
    },

    redFlags: [
      '🔴 CRITICAL: Diabetic ketoacidosis (hyperglycemia, ketones, acidosis, altered consciousness) - EMERGENCY; IV fluids, insulin, K+ monitoring',
      '🔴 CRITICAL: Hyperosmolar hyperglycemic state (severe hyperglycemia >600, altered consciousness, minimal ketones) - EMERGENCY; aggressive rehydration',
      '🔴 CRITICAL: Severe hypoglycemia with unconsciousness or seizure - IV dextrose or IM glucagon; identify cause',
      '🔴 CRITICAL: Diabetic foot infection with systemic signs (fever, cellulitis, deep tissue) - hospitalization, IV antibiotics, surgical evaluation',
      '🔴 CRITICAL: Sudden vision loss in diabetic - possible vitreous hemorrhage or retinal detachment; urgent ophthalmology',
      '🟠 URGENT: New foot ulcer or worsening existing ulcer - immediate wound care evaluation',
      '🟠 URGENT: Rapid decline in renal function (>30% eGFR drop) - evaluate for acute causes, adjust medications',
      '🟠 URGENT: Recurrent severe hypoglycemia - adjust regimen, evaluate for hypoglycemia unawareness',
      '🟠 URGENT: Persistent HbA1c >10% despite treatment - reassess diagnosis, adherence, consider insulin',
      '🟡 CAUTION: New neuropathic symptoms (pain, numbness, weakness) - assess, optimize glucose, consider pain management',
      '🟡 CAUTION: Erectile dysfunction - may be first sign of autonomic neuropathy and CVD; evaluate',
      '🟡 CAUTION: Weight loss in established T2DM - consider progression, reassess diagnosis (LADA?), malignancy',
      '🟡 CAUTION: Repeated UTIs or genital infections - optimize glucose, consider SGLT2i discontinuation if on'
    ],

    complications: [
      'Diabetic retinopathy - Leading cause of blindness in working-age adults; non-proliferative to proliferative; requires laser/injection',
      'Diabetic nephropathy - Leading cause of end-stage renal disease; progresses from microalbuminuria to macroalbuminuria to ESRD',
      'Diabetic neuropathy - Peripheral (symmetric polyneuropathy), autonomic (gastroparesis, orthostatic hypotension, ED), mononeuropathies',
      'Diabetic foot disease - Ulcers, infections, Charcot foot, gangrene; leading cause of non-traumatic amputation',
      'Cardiovascular disease - 2-4 fold increased risk; coronary artery disease, heart failure, stroke',
      'Peripheral arterial disease - Claudication, rest pain, critical limb ischemia; often combined with neuropathy',
      'Diabetic ketoacidosis (DKA) - Usually T1DM but can occur in T2DM; hyperglycemia, ketosis, acidosis; life-threatening',
      'Hyperosmolar hyperglycemic state (HHS) - More common in T2DM; severe hyperglycemia (>600), hyperosmolarity, altered consciousness',
      'Hypoglycemia - From medications (sulfonylureas, insulin); can be severe and life-threatening',
      'Infections - Increased susceptibility; skin, urinary, respiratory infections more common and severe',
      'Non-alcoholic fatty liver disease (NAFLD) - Very common in T2DM; can progress to cirrhosis',
      'Depression - 2-3 fold increased risk; bidirectional relationship with diabetes',
      'Cognitive decline and dementia - Increased risk of vascular dementia and Alzheimer disease',
      'Periodontal disease - More severe gum disease; bidirectional relationship with glycemic control',
      'Sexual dysfunction - Erectile dysfunction (50% of men); female sexual dysfunction common'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Chronic, progressive disease with no cure (remission possible with weight loss/surgery)
• Life expectancy reduced by 6-8 years on average
• Quality of life significantly impacted by complications
• Outcomes highly dependent on glycemic control and CV risk management

WITH GOOD MANAGEMENT:
• Near-normal life expectancy achievable
• Complications can be prevented or significantly delayed
• Each 1% reduction in HbA1c reduces microvascular complications by 37%
• CV risk reduction with statins, BP control, and newer agents

MICROVASCULAR COMPLICATIONS:
• Retinopathy: Affects 35% after 20 years; blindness preventable with screening and treatment
• Nephropathy: 20-40% develop; progression to ESRD can be slowed with ACEi/ARB and SGLT2i
• Neuropathy: Affects 50% over lifetime; contributes to foot disease

MACROVASCULAR COMPLICATIONS:
• CVD remains leading cause of death (50-80% of deaths)
• SGLT2 inhibitors and GLP-1 agonists have changed prognosis for high-risk patients
• Aggressive CV risk factor management essential

REMISSION:
• Possible with significant weight loss (>15kg) especially early in disease
• Bariatric surgery can induce remission in 40-90% (depending on procedure and criteria)
• Does not mean "cured" - lifelong monitoring required

FACTORS AFFECTING PROGNOSIS:

Better Prognosis:
• Early diagnosis and treatment
• Good glycemic control (HbA1c at target)
• No or minimal complications at diagnosis
• CV risk factors well-controlled
• Adherent to medications and lifestyle
• Access to comprehensive diabetes care
• Self-management skills

Worse Prognosis:
• Late diagnosis with established complications
• Poor glycemic control
• Multiple CV risk factors poorly controlled
• Recurrent hypoglycemia
• Poor adherence
• Limited healthcare access
• Low socioeconomic status
• Depression and diabetes distress`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing T2DM):

Lifestyle Intervention (58% risk reduction in prediabetes):
• Weight loss: Target 7% of body weight
• Physical activity: 150 minutes/week moderate intensity
• Healthy diet: Reduce calories, saturated fat, sugar
• Intensive lifestyle programs most effective

Screening for Prediabetes:
• Screen adults with BMI ≥25 (≥23 in Asians) with risk factors
• All adults ≥45 years
• Every 3 years if normal; annually if prediabetes

Metformin for Prevention:
• Consider in high-risk prediabetes (BMI ≥35, age <60, prior GDM)
• 31% risk reduction in Diabetes Prevention Program

SECONDARY PREVENTION (Preventing Complications):

Glycemic Control:
• Achieve and maintain HbA1c target
• Intensive control early reduces long-term complications (legacy effect)

Blood Pressure Control:
• Target <130/80 mmHg
• ACEi or ARB preferred especially if albuminuria

Lipid Management:
• Statin therapy for most patients with diabetes
• Moderate or high-intensity based on CV risk

Aspirin:
• For secondary prevention (established CVD)
• Consider for primary prevention if high CV risk and low bleeding risk

Albuminuria Management:
• ACEi or ARB for all with uACR ≥30 mg/g
• SGLT2 inhibitors provide additional renal protection

Regular Screening:
• Annual eye exam
• Annual renal function and uACR
• Foot examination at every visit

TERTIARY PREVENTION (Preventing Progression):
• Multidisciplinary foot care for ulcers
• Timely laser treatment for retinopathy
• Renal replacement therapy when indicated
• Cardiac rehabilitation after CV events

POPULATION-LEVEL PREVENTION:
• Policies to promote healthy food environments
• Urban planning to encourage physical activity
• Sugar-sweetened beverage taxes
• Food labeling regulations
• Workplace wellness programs
• School-based prevention programs`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS TYPE 2 DIABETES?
Diabetes is a condition where your body cannot properly use sugar (glucose) from food for energy. In Type 2 diabetes, your body does not respond well to insulin (a hormone that moves sugar into your cells), and over time, your body makes less insulin. This causes sugar to build up in your blood, which can damage your heart, eyes, kidneys, nerves, and feet if not controlled.

THE GOOD NEWS:
• Diabetes can be controlled with proper treatment
• Healthy eating and exercise are powerful medicines
• Taking your medications as prescribed prevents complications
• Many people with diabetes live long, healthy lives

ABCs OF DIABETES MANAGEMENT:
• A = A1C (HbA1c): Goal usually below 7%
• B = Blood Pressure: Goal below 130/80
• C = Cholesterol: LDL (bad cholesterol) should be low
• S = Smoking: Stop smoking

WARNING SIGNS OF HIGH BLOOD SUGAR:
• Very thirsty
• Urinating frequently
• Blurred vision
• Feeling tired
• Losing weight without trying
• Slow-healing wounds

WARNING SIGNS OF LOW BLOOD SUGAR (Hypoglycemia):
• Shaking or trembling
• Sweating
• Fast heartbeat
• Feeling hungry
• Dizziness or lightheadedness
• Confusion
• Irritability

IF YOU HAVE LOW BLOOD SUGAR:
• Eat or drink 15-20 grams of fast-acting sugar immediately:
  - 4 glucose tablets
  - Half cup (120mL) fruit juice or regular soda
  - 1 tablespoon sugar or honey
• Wait 15 minutes and check again
• If still low, repeat treatment
• Eat a snack if your next meal is more than an hour away

HEALTHY EATING:
• Eat regular meals - do not skip meals
• Fill half your plate with vegetables
• Choose whole grains instead of refined grains
• Limit sugary drinks - choose water instead
• Limit fried and fatty foods
• Watch portion sizes
• Reduce salt intake

PHYSICAL ACTIVITY:
• Aim for 30 minutes most days (can be 10-minute intervals)
• Walking is excellent - start slowly and build up
• Include activities you enjoy
• Check your feet after exercise

FOOT CARE (VERY IMPORTANT):
• Check your feet EVERY DAY for cuts, blisters, redness, swelling
• Wash feet daily with warm (not hot) water
• Dry carefully, especially between toes
• Moisturize dry skin (not between toes)
• Never walk barefoot
• Wear comfortable, well-fitting shoes
• Cut nails straight across
• Report ANY foot problem to your doctor immediately

WHEN TO SEEK MEDICAL HELP IMMEDIATELY:
• Blood sugar very high (>300 mg/dL) with nausea/vomiting
• Blood sugar very low that does not respond to treatment
• Chest pain or difficulty breathing
• Signs of infection (fever, wound with pus)
• Any foot wound or ulcer
• Sudden vision changes
• Confusion or unusual behavior

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Diabetes is a serious condition that requires ongoing medical care. Work closely with your healthcare team to manage your diabetes effectively.

QUESTIONS TO ASK YOUR DOCTOR:
• What is my HbA1c and what should it be?
• What is my blood pressure and cholesterol?
• When should I check my blood sugar?
• What are the side effects of my medications?
• When should I have my eyes and feet examined?
• What should I do if my blood sugar is too high or too low?`,

    references: [
      'American Diabetes Association. Standards of Care in Diabetes - 2024. Diabetes Care. 2024;47(Suppl 1):S1-S321.',
      'International Diabetes Federation. IDF Diabetes Atlas, 10th Edition. Brussels, Belgium: IDF; 2021.',
      'World Health Organization. Classification of diabetes mellitus. WHO. 2019.',
      'Davies MJ, Aroda VR, et al. Management of Hyperglycemia in Type 2 Diabetes, 2022. A Consensus Report by ADA and EASD. Diabetes Care. 2022;45(11):2753-2786.',
      'Cosentino F, Grant PJ, et al. 2019 ESC Guidelines on diabetes, pre-diabetes, and cardiovascular diseases. Eur Heart J. 2020;41(2):255-323.',
      'SEMDSA Type 2 Diabetes Guidelines Expert Committee. The 2017 SEMDSA Guideline for the Management of Type 2 Diabetes. JEMDSA. 2017;22(1):S1-S196.',
      'Mbanya JC, Motala AA, et al. Diabetes in sub-Saharan Africa. Lancet. 2010;375(9733):2254-2266.',
      'Atun R, Davies JI, et al. Diabetes in sub-Saharan Africa: from clinical care to health policy. Lancet Diabetes Endocrinol. 2017;5(8):622-667.',
      'Diabetes Prevention Program Research Group. Reduction in the incidence of type 2 diabetes with lifestyle intervention or metformin. N Engl J Med. 2002;346(6):393-403.',
      'UKPDS Group. Intensive blood-glucose control with sulphonylureas or insulin compared with conventional treatment and risk of complications in patients with type 2 diabetes (UKPDS 33). Lancet. 1998;352(9131):837-853.',
      'Zinman B, Wanner C, et al. Empagliflozin, Cardiovascular Outcomes, and Mortality in Type 2 Diabetes (EMPA-REG OUTCOME). N Engl J Med. 2015;373(22):2117-2128.',
      'Marso SP, Daniels GH, et al. Liraglutide and Cardiovascular Outcomes in Type 2 Diabetes (LEADER). N Engl J Med. 2016;375(4):311-322.',
      'Perkovic V, Jardine MJ, et al. Canagliflozin and Renal Outcomes in Type 2 Diabetes and Nephropathy (CREDENCE). N Engl J Med. 2019;380(24):2295-2306.',
      'Abbas ZG, Archibald LK. Challenges for management of the diabetic foot in Africa: doing more with less. Int Wound J. 2007;4(4):305-313.',
      'Ogbera AO, Ekpebegh C. Diabetes mellitus in Nigeria: The past, present and future. World J Diabetes. 2014;5(6):905-911.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 67. ASTHMA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '67',
    name: 'Asthma',
    slug: 'asthma',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'J45',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Asthma is a chronic inflammatory disease of the airways characterized by variable airflow obstruction, bronchial hyperresponsiveness, and underlying inflammation. Affecting approximately 339 million people worldwide, asthma is one of the most common chronic diseases globally. The condition manifests as recurrent episodes of wheezing, breathlessness, chest tightness, and coughing, particularly at night or early morning, which are usually reversible either spontaneously or with treatment.

The pathophysiology involves complex interactions between genetic predisposition and environmental exposures. Airway inflammation is driven by Th2 lymphocytes, eosinophils, mast cells, and their mediators, leading to bronchial hyperreactivity, mucus hypersecretion, and airway remodeling. Triggers include allergens (dust mites, pollen, animal dander, molds), respiratory infections, exercise, cold air, air pollution, occupational exposures, and medications (NSAIDs, beta-blockers). Chronic inflammation, if untreated, leads to irreversible structural changes including subepithelial fibrosis, smooth muscle hypertrophy, and mucous gland hyperplasia.

In Africa, asthma prevalence is rising rapidly, particularly in urban areas, driven by urbanization, Westernization of lifestyle, and environmental factors. Studies suggest 10-15% prevalence in urban African children. The condition is frequently underdiagnosed and undertreated due to limited awareness, competing health priorities (infectious diseases), and poor access to essential medications. The Global Asthma Report highlights that many African countries have inadequate access to inhaled corticosteroids (ICS), the cornerstone of asthma treatment, and spacer devices. Asthma mortality remains unacceptably high in resource-limited settings, largely from preventable causes including delayed presentation, lack of written action plans, and overreliance on short-acting beta-agonists (SABA) without anti-inflammatory therapy.

Management follows a stepwise approach based on symptom control and risk factors, with inhaled corticosteroids as the foundation of controller therapy. The GINA 2023 guidelines emphasize that SABA-only treatment is no longer recommended; all patients should receive ICS-containing therapy to reduce exacerbations and mortality. Patient education, written action plans, and regular follow-up are essential for optimal outcomes.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - SABA-only treatment is NO LONGER RECOMMENDED; ALL patients with asthma should receive ICS-containing therapy (GINA 2023)

🔴 CRITICAL POINT 2 - Acute severe asthma is a medical emergency; give oxygen, repeated SABA, systemic corticosteroids, and consider ipratropium

🔴 CRITICAL POINT 3 - Overuse of SABA (>3 canisters/year) indicates poor control and increased exacerbation/death risk - step up controller therapy

🔴 CRITICAL POINT 4 - Every patient needs a written asthma action plan explaining daily treatment and how to recognize/manage worsening

🟠 CRITICAL POINT 5 - Inhaler technique must be checked at EVERY visit - poor technique is the most common cause of treatment failure

🟠 CRITICAL POINT 6 - Use a spacer with pMDI (pressurized metered-dose inhaler) - dramatically improves drug delivery and reduces side effects

🟠 CRITICAL POINT 7 - Assess BOTH symptom control AND future risk (exacerbations, lung function decline, side effects) at every visit

🟡 CRITICAL POINT 8 - Identify and address triggers: allergens, smoke exposure (including cooking fires), occupational exposures, medications

🟡 CRITICAL POINT 9 - Consider stepping down treatment after 3 months of good control; never stop ICS completely in diagnosed asthma`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 339 million people affected worldwide
• Prevalence: 1-18% depending on country (higher in developed nations)
• 461,000 deaths annually (most preventable)
• Affects all ages; often begins in childhood
• 80% of asthma deaths occur in low- and lower-middle-income countries
• Leading chronic disease in children
• Significant economic burden: Direct costs (medications, hospitalizations) + indirect costs (missed work/school)
• Rising prevalence globally, especially in developing countries

AFRICAN EPIDEMIOLOGY:
• Prevalence: 5-15% in urban areas; lower in rural (but increasing)
• Urban children: 10-15% prevalence in many African cities
• South Africa: Among highest prevalence in Africa (8-10%)
• Nigeria: Estimated 10-15 million asthmatics
• East Africa: Rising prevalence with urbanization
• Underdiagnosis: Many asthmatics undiagnosed or misdiagnosed as recurrent chest infections
• Limited access to ICS in many countries
• High mortality from lack of treatment and delayed presentation
• Biomass fuel exposure significant contributor to respiratory symptoms
• HIV/TB co-morbidity complicates diagnosis and management

RISK FACTORS:

Host Factors:
• Genetic predisposition (family history of asthma/atopy)
• Atopy (allergic rhinitis, eczema, food allergy)
• Obesity
• Sex (boys > girls in childhood; women > men in adulthood)
• Bronchial hyperresponsiveness

Environmental Factors:
• Allergen exposure (dust mites, cockroach, molds, pollen, animal dander)
• Respiratory infections (especially viral in childhood)
• Tobacco smoke exposure (active and passive)
• Biomass fuel smoke (cooking fires - major African factor)
• Air pollution (outdoor and indoor)
• Occupational exposures (chemicals, dust, fumes)
• Medications (NSAIDs, beta-blockers, aspirin)
• Diet and microbiome factors

TRIGGERS:
• Respiratory tract infections (most common)
• Allergens (variable by region)
• Exercise (especially cold, dry air)
• Weather changes
• Strong emotions/stress
• Air pollution
• Occupational exposures
• Drugs (aspirin, NSAIDs, beta-blockers)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CHARACTERISTIC SYMPTOMS:
• Wheeze - musical, high-pitched sound, especially on expiration; most characteristic
• Shortness of breath - episodic, variable
• Chest tightness - often described as band-like
• Cough - often worse at night and early morning; may be only symptom ("cough-variant asthma")

SYMPTOM PATTERNS:
• Variable - symptoms come and go
• Worse at night and early morning (diurnal variation)
• Triggered by specific exposures (allergens, exercise, cold air, infections)
• Reversible - improve spontaneously or with bronchodilator
• Seasonal variation in some patients (allergic asthma)

SYMPTOM COMBINATIONS:
• Multiple symptoms together more likely asthma than single symptom
• Symptoms in absence of respiratory infection
• Symptoms recurring over time
• Personal/family history of atopy increases likelihood

PHYSICAL EXAMINATION:

Between Attacks (May Be Normal):
• Often completely normal
• May have allergic features: allergic shiners, nasal polyps, eczema
• Examine for comorbidities

During Acute Exacerbation:
• Tachypnea (increased respiratory rate)
• Use of accessory muscles
• Intercostal/subcostal retractions
• Prolonged expiratory phase
• Wheeze (may be absent in severe obstruction - "silent chest")
• Tachycardia
• Inability to speak in full sentences
• Agitation or drowsiness (severe)
• Cyanosis (severe, life-threatening)
• Pulsus paradoxus (>25 mmHg suggests severe attack)

ASTHMA SEVERITY CLASSIFICATION (Pre-treatment):

Intermittent:
• Symptoms <2 days/week
• Nighttime awakenings <2/month
• SABA use <2 days/week
• No interference with normal activity
• FEV1 normal between exacerbations

Mild Persistent:
• Symptoms >2 days/week but not daily
• Nighttime awakenings 3-4/month
• SABA use >2 days/week
• Minor limitation of activity

Moderate Persistent:
• Daily symptoms
• Nighttime awakenings >1/week
• Daily SABA use
• Some limitation of activity
• FEV1 60-80% predicted

Severe Persistent:
• Symptoms throughout the day
• Nighttime awakenings nightly
• SABA use several times/day
• Extremely limited activity
• FEV1 <60% predicted`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL HISTORY
• Pattern of respiratory symptoms (wheeze, cough, dyspnea, chest tightness)
• Variability and triggers
• Personal/family history of atopy
• Symptom timing (nocturnal, seasonal, exposure-related)
• Response to previous treatment

STEP 2: PHYSICAL EXAMINATION
• May be normal between attacks
• Look for wheeze, prolonged expiration, signs of atopy
• Assess severity during exacerbation

STEP 3: CONFIRM VARIABLE AIRFLOW LIMITATION
• Spirometry with bronchodilator reversibility (gold standard)
• Peak expiratory flow (PEF) variability
• Positive bronchial provocation test (if available)

DIAGNOSTIC CRITERIA:

Clinical Criteria:
• History of variable respiratory symptoms
• AND evidence of variable expiratory airflow limitation

Spirometric Criteria (Adults):
• FEV1/FVC ratio reduced (below lower limit of normal; <0.75-0.80)
• Post-bronchodilator increase in FEV1 >12% AND >200mL (confirms reversibility)

Peak Flow Criteria:
• Diurnal PEF variability >10% (average of twice daily readings over 2 weeks)
• Significant improvement with bronchodilator or anti-inflammatory treatment

Bronchial Provocation (If Available):
• Positive methacholine or histamine challenge (PC20 <8 mg/mL)
• Positive exercise challenge (>10% fall in FEV1)

IN RESOURCE-LIMITED SETTINGS:
If spirometry unavailable, diagnosis can be made clinically:
• Characteristic symptom pattern
• Response to bronchodilator trial (clinical improvement)
• Exclusion of alternative diagnoses
• Document baseline PEF if available

ASTHMA-COPD OVERLAP:
• Features of both conditions
• Age usually >40
• History of childhood asthma plus smoking/biomass exposure
• Treat as asthma (ICS essential) with COPD features
• Never give SABA/LABA alone without ICS

CONTROL ASSESSMENT (GINA):

Well Controlled (all of):
• Daytime symptoms ≤2/week
• No nighttime symptoms
• Reliever needed ≤2/week
• No activity limitation

Partly Controlled (1-2 of):
• Daytime symptoms >2/week
• Any nighttime symptoms
• Reliever needed >2/week
• Any activity limitation

Uncontrolled (3-4 of above, or any exacerbation)`,

    differentialDiagnosis: [
      'COPD - Older age, smoking/biomass exposure, persistent airflow limitation, less reversibility; may overlap with asthma',
      'Bronchiectasis - Chronic productive cough, recurrent infections, clubbing; CT shows dilated bronchi',
      'Heart failure - Orthopnea, PND, peripheral edema, raised JVP; BNP elevated, CXR shows cardiomegaly',
      'Vocal cord dysfunction - Inspiratory stridor, symptoms localized to throat, normal spirometry between attacks',
      'Foreign body aspiration - Sudden onset, unilateral wheeze, history of choking episode; common in children',
      'Gastroesophageal reflux disease (GERD) - Heartburn, regurgitation; may trigger or mimic asthma',
      'Allergic rhinitis with post-nasal drip - Chronic cough, nasal symptoms, throat clearing',
      'Chronic sinusitis - Facial pain, nasal discharge, post-nasal drip; may coexist with asthma',
      'Tuberculosis - Endemic in Africa; chronic cough, weight loss, night sweats, hemoptysis; CXR changes, sputum AFB',
      'Pulmonary embolism - Acute dyspnea, pleuritic chest pain, risk factors; D-dimer, CTPA',
      'Hyperventilation/panic disorder - Paresthesias, dizziness, anxiety; normal examination and spirometry',
      'ACE inhibitor-induced cough - Dry cough, temporally related to ACE inhibitor initiation',
      'Eosinophilic bronchitis - Chronic cough, sputum eosinophilia, no airflow obstruction',
      'Interstitial lung disease - Progressive dyspnea, dry cough, crackles; restrictive pattern on spirometry',
      'Lung cancer - Older patient, smoking history, hemoptysis, weight loss; CXR/CT abnormal'
    ],

    investigations: [
      {
        name: 'Peak Expiratory Flow (PEF)',
        indication: '🔴 ESSENTIAL - Assess airflow; monitor variability (>10% diurnal variation supports diagnosis); track control',
        availability: 'BASIC'
      },
      {
        name: 'Spirometry (Pre and Post Bronchodilator)',
        indication: '🔴 ESSENTIAL - Gold standard; FEV1/FVC ratio confirms obstruction; >12% and >200mL improvement confirms reversibility',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - SpO2 in acute exacerbation; <92% indicates severe attack requiring oxygen',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-Ray',
        indication: '🟠 IMPORTANT - Not required for diagnosis; useful to exclude other conditions; hyperinflation in acute severe asthma',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🟠 IMPORTANT - Eosinophilia supports allergic asthma; assess for infection in exacerbation',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: '🟠 IMPORTANT - In severe exacerbation; hypoxia, initially hypocapnia then hypercapnia (ominous sign)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Allergy Testing (Skin Prick or Specific IgE)',
        indication: '🟡 WHEN AVAILABLE - Identify allergic triggers; guide allergen avoidance; consider immunotherapy',
        availability: 'ADVANCED'
      },
      {
        name: 'Fractional Exhaled Nitric Oxide (FeNO)',
        indication: '🟡 WHEN AVAILABLE - Elevated in eosinophilic/allergic asthma; supports diagnosis; guides ICS therapy',
        availability: 'ADVANCED'
      },
      {
        name: 'Bronchial Provocation Test (Methacholine)',
        indication: '🟡 WHEN AVAILABLE - If diagnosis uncertain with normal spirometry; positive test confirms hyperresponsiveness',
        availability: 'ADVANCED'
      },
      {
        name: 'Sputum Examination (AFB, Culture)',
        indication: '🟠 IMPORTANT - Exclude tuberculosis in endemic areas; eosinophils support asthma diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'CT Chest (High Resolution)',
        indication: '🟡 WHEN INDICATED - Atypical presentation, suspected bronchiectasis, or poor treatment response',
        availability: 'ADVANCED'
      },
      {
        name: 'Serum Total IgE',
        indication: '🟡 WHEN AVAILABLE - Elevated in allergic asthma; consider for anti-IgE therapy eligibility',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Determine if acute severe/life-threatening (cannot speak sentences, silent chest, cyanosis, exhaustion)',
        '⚡ OXYGEN: Give supplemental oxygen to maintain SpO2 94-98% (88-92% if COPD risk)',
        '⚡ SABA: Salbutamol 4-10 puffs via spacer OR nebulized 2.5-5mg; repeat every 20 min in severe attack',
        '⚡ SYSTEMIC CORTICOSTEROIDS: Give early in all moderate-severe attacks; prednisolone 40-50mg PO or hydrocortisone 100mg IV',
        '⚡ IPRATROPIUM: Add nebulized ipratropium 0.5mg to salbutamol for severe exacerbations',
        '⚡ MONITOR: Continuous SpO2, respiratory rate, heart rate, PEF (if able); watch for deterioration',
        '⚡ ESCALATE IF NEEDED: IV magnesium sulfate 2g over 20 min if poor response; consider ICU if life-threatening',
        '⚡ IDENTIFY TRIGGER: Infection, allergen exposure, medication, non-adherence?',
        '⚡ CHECK INHALER TECHNIQUE: Poor technique is common cause of poor control',
        '⚡ REVIEW CONTROLLER THERAPY: Ensure adequate ICS; step up if recurrent exacerbations'
      ],
      
      pharmacological: [
        {
          drug: 'Salbutamol (Albuterol) pMDI',
          indication: '🔴 RELIEVER - Short-acting beta-agonist (SABA); for acute symptom relief; should be used with ICS',
          dose: 'Acute: 4-10 puffs via spacer, repeat every 20 min if severe. Maintenance: As-needed (but prefer ICS-formoterol). 100mcg per puff.',
          route: 'Inhaled (pMDI with spacer preferred)',
          duration: 'As-needed for symptoms',
          contraindications: ['Use with caution in cardiac arrhythmias', 'Hyperthyroidism'],
          sideEffects: ['Tremor', 'Tachycardia', 'Palpitations', 'Headache', 'Hypokalemia (high doses)'],
          alternatives: ['Terbutaline', 'Levalbuterol']
        },
        {
          drug: 'Salbutamol Nebulized',
          indication: '🔴 ACUTE SEVERE - For moderate-severe exacerbations; continuous or intermittent nebulization',
          dose: 'Adults: 2.5-5mg nebulized every 20 minutes for 3 doses, then every 1-4 hours as needed. Severe: Continuous nebulization 10-15mg/hour.',
          route: 'Nebulized',
          duration: 'During acute exacerbation',
          contraindications: ['Cardiac arrhythmias (relative)'],
          sideEffects: ['Tremor', 'Tachycardia', 'Hypokalemia'],
          alternatives: ['Terbutaline nebulized']
        },
        {
          drug: 'Beclomethasone pMDI (Low-Dose ICS)',
          indication: '🔴 FIRST-LINE CONTROLLER - Inhaled corticosteroid; foundation of asthma therapy; anti-inflammatory',
          dose: 'Adults: Low dose 100-200mcg twice daily. Medium dose 200-400mcg twice daily. Use with spacer. Rinse mouth after use.',
          route: 'Inhaled (pMDI with spacer)',
          duration: 'Long-term daily; do not stop abruptly',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Oral candidiasis (thrush)', 'Dysphonia (hoarse voice)', 'Pharyngitis', 'Adrenal suppression (high doses)'],
          alternatives: ['Budesonide', 'Fluticasone', 'Ciclesonide']
        },
        {
          drug: 'Budesonide pMDI or DPI',
          indication: '🔴 FIRST-LINE CONTROLLER - ICS; available as DPI (Turbuhaler) or pMDI; pregnancy category B',
          dose: 'Adults: Low 200-400mcg/day. Medium 400-800mcg/day. High >800mcg/day. Once or twice daily dosing.',
          route: 'Inhaled',
          duration: 'Long-term daily',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Oral candidiasis', 'Dysphonia', 'Cough', 'Systemic effects at high doses'],
          alternatives: ['Beclomethasone', 'Fluticasone']
        },
        {
          drug: 'Fluticasone pMDI or DPI',
          indication: '🔴 FIRST-LINE CONTROLLER - Potent ICS; twice daily dosing',
          dose: 'Adults: Low 100-250mcg/day. Medium 250-500mcg/day. High >500mcg/day. Usually twice daily.',
          route: 'Inhaled',
          duration: 'Long-term daily',
          contraindications: ['Hypersensitivity', 'Primary treatment of acute asthma'],
          sideEffects: ['Oral candidiasis', 'Dysphonia', 'Adrenal suppression (high doses)'],
          alternatives: ['Budesonide', 'Beclomethasone']
        },
        {
          drug: 'Budesonide-Formoterol (ICS-LABA Combination)',
          indication: '🔴 PREFERRED - GINA preferred; can be used as both maintenance AND reliever (MART); Step 3-5',
          dose: 'Maintenance: 1-2 puffs (100/6 or 200/6) once or twice daily. Reliever: Additional 1 puff as needed. Maximum 12 puffs/day.',
          route: 'Inhaled',
          duration: 'Long-term with as-needed use',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Oral candidiasis', 'Tremor', 'Headache', 'Palpitations'],
          alternatives: ['Fluticasone-Salmeterol', 'Beclomethasone-Formoterol']
        },
        {
          drug: 'Fluticasone-Salmeterol (ICS-LABA Combination)',
          indication: '🟠 ALTERNATIVE ICS-LABA - Fixed combination; maintenance only (not for reliever); Step 3-5',
          dose: 'Adults: 100/50, 250/50, or 500/50 mcg one puff twice daily. Choose strength based on severity.',
          route: 'Inhaled (DPI or pMDI)',
          duration: 'Long-term; twice daily',
          contraindications: ['Hypersensitivity', 'Not for acute relief'],
          sideEffects: ['Oral candidiasis', 'Tremor', 'Hoarse voice', 'Headache'],
          alternatives: ['Budesonide-Formoterol']
        },
        {
          drug: 'Ipratropium Bromide (Nebulized)',
          indication: '🟠 ACUTE EXACERBATION - Short-acting muscarinic antagonist (SAMA); add to SABA in severe attacks',
          dose: 'Adults: 0.5mg (500mcg) nebulized with salbutamol every 20 minutes for 3 doses in severe exacerbation.',
          route: 'Nebulized',
          duration: 'During acute severe exacerbation',
          contraindications: ['Hypersensitivity to atropine or derivatives'],
          sideEffects: ['Dry mouth', 'Urinary retention', 'Glaucoma exacerbation'],
          alternatives: ['Ipratropium pMDI']
        },
        {
          drug: 'Prednisolone (Oral)',
          indication: '🔴 EXACERBATION - Systemic corticosteroid; essential for all moderate-severe exacerbations',
          dose: 'Adults: 40-50mg PO once daily for 5-7 days. No taper needed for short courses. Take in the morning.',
          route: 'PO',
          duration: '5-7 days for exacerbation',
          contraindications: ['Active untreated infection (relative)', 'Systemic fungal infection'],
          sideEffects: ['Hyperglycemia', 'Insomnia', 'Mood changes', 'Increased appetite', 'GI upset'],
          alternatives: ['Dexamethasone', 'Methylprednisolone']
        },
        {
          drug: 'Hydrocortisone IV',
          indication: '🔴 SEVERE EXACERBATION - When unable to take oral; for hospitalized severe asthma',
          dose: 'Adults: 100mg IV every 6-8 hours. Switch to oral prednisolone when able to take orally.',
          route: 'IV',
          duration: 'Until able to take oral',
          contraindications: ['Systemic fungal infection'],
          sideEffects: ['Hyperglycemia', 'Hypertension', 'Fluid retention'],
          alternatives: ['Methylprednisolone IV']
        },
        {
          drug: 'Montelukast',
          indication: '🟡 ADD-ON - Leukotriene receptor antagonist; add-on to ICS; useful in exercise-induced and allergic asthma',
          dose: 'Adults: 10mg PO once daily in the evening. Children 6-14 years: 5mg chewable once daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'Abdominal pain', 'Neuropsychiatric effects (rare - monitor)', 'Sleep disturbances'],
          alternatives: ['Zafirlukast']
        },
        {
          drug: 'Magnesium Sulfate IV',
          indication: '🟠 LIFE-THREATENING ASTHMA - Adjunct for severe exacerbation not responding to initial treatment',
          dose: 'Adults: 1.2-2g IV over 20 minutes as single dose. May repeat once if needed.',
          route: 'IV',
          duration: 'Single dose in acute severe asthma',
          contraindications: ['Myasthenia gravis', 'Heart block'],
          sideEffects: ['Flushing', 'Hypotension', 'Weakness', 'Respiratory depression (toxicity)'],
          alternatives: ['Consider ICU and mechanical ventilation if no response']
        },
        {
          drug: 'Tiotropium (Add-on)',
          indication: '🟡 STEP 4-5 ADD-ON - Long-acting muscarinic antagonist (LAMA); add to ICS-LABA if still uncontrolled',
          dose: 'Adults: 2.5mcg (2 puffs) via Respimat once daily OR 18mcg DPI once daily.',
          route: 'Inhaled',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity to atropine derivatives'],
          sideEffects: ['Dry mouth', 'Constipation', 'Urinary retention', 'Glaucoma (rare)'],
          alternatives: ['Glycopyrronium']
        }
      ],
      
      nonPharmacological: [
        'ALLERGEN AVOIDANCE: Identify and reduce exposure to triggers; dust mite covers, remove carpets, avoid pets if allergic',
        'SMOKING CESSATION: Essential; both active smoking and secondhand smoke worsen asthma; support cessation',
        'BIOMASS FUEL REDUCTION: Major African factor; improved stoves, better ventilation, alternative fuels where possible',
        'INDOOR AIR QUALITY: Reduce dust, mold; improve ventilation; avoid strong chemicals and sprays',
        'WEIGHT MANAGEMENT: Obesity worsens asthma; weight loss improves control',
        'EXERCISE: Regular physical activity is beneficial; pretreat with SABA or ICS-formoterol if exercise-induced symptoms',
        'BREATHING EXERCISES: May reduce symptoms and improve quality of life; Buteyko, yoga breathing',
        'INFLUENZA VACCINATION: Annual vaccination recommended; reduces exacerbations',
        'WRITTEN ASTHMA ACTION PLAN: Essential for all patients; instructions for daily management and worsening',
        'SELF-MONITORING: Regular PEF monitoring for moderate-severe asthma; recognize early worsening',
        'ASTHMA EDUCATION: Understanding disease, medications, triggers, inhaler technique'
      ],
      
      surgical: [
        'BRONCHIAL THERMOPLASTY: For severe refractory asthma; reduces smooth muscle via radiofrequency ablation',
        'MECHANICAL VENTILATION: For life-threatening asthma with respiratory failure; avoid if possible'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Life-threatening asthma (silent chest, cyanosis, exhaustion, altered consciousness, SpO2 <92%)',
        '🔴 IMMEDIATE REFERRAL: Acute severe asthma not responding to initial treatment within 15-30 minutes',
        '🔴 IMMEDIATE REFERRAL: Near-fatal asthma (previous ICU admission, intubation)',
        '🟠 URGENT REFERRAL: Frequent severe exacerbations despite Step 4 treatment',
        '🟠 URGENT REFERRAL: Suspected occupational asthma - needs specialist evaluation',
        '🟠 URGENT REFERRAL: Asthma in pregnancy with poor control - obstetric and respiratory input',
        '🟡 ROUTINE REFERRAL: Diagnosis uncertain after initial workup',
        '🟡 ROUTINE REFERRAL: Poor control despite good adherence to Step 4 therapy',
        '🟡 ROUTINE REFERRAL: Severe persistent asthma requiring Step 5 therapies',
        '🟡 ROUTINE REFERRAL: Consider for allergy testing and immunotherapy evaluation',
        '🟡 ROUTINE REFERRAL: Significant side effects from treatment'
      ],
      
      monitoring: [
        'SYMPTOM CONTROL: Assess using GINA control questions at every visit (symptoms, night waking, reliever use, activity limitation)',
        'PEF MONITORING: Twice daily in moderate-severe asthma; useful for action plan; normal PEF with symptoms suggests good control achievable',
        'SPIROMETRY: At diagnosis, after starting treatment (3-6 months), then periodically; track FEV1 over time',
        'EXACERBATION FREQUENCY: Track number, severity, triggers; recurrent exacerbations need step up',
        'MEDICATION ADHERENCE: Assess at every visit; ask about barriers; check inhaler technique',
        'INHALER TECHNIQUE: Demonstrate and check at EVERY visit; most common cause of treatment failure',
        'SABA USE: Track reliever use; >3 canisters/year indicates poor control and increased risk',
        'SIDE EFFECTS: Ask about oral candidiasis (rinse mouth), voice changes, tremor',
        'COMORBIDITIES: Assess rhinitis, GERD, obesity, anxiety/depression - treat to improve asthma control',
        'GROWTH IN CHILDREN: Monitor height; ICS may slow growth by 1cm in first year but final adult height unaffected'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

AFTER EXACERBATION:
• Review within 1-2 weeks of any exacerbation
• Assess for ongoing symptoms
• Identify trigger and address
• Ensure completing oral corticosteroid course if prescribed
• Step up controller therapy for 2-4 weeks
• Review and provide written action plan
• Check inhaler technique

STABLE ASTHMA:
• Review every 1-3 months until control achieved
• Review every 3-6 months once stable
• More frequent if recent exacerbation or step change

AT EACH VISIT:
1. Assess symptom control (GINA questions)
2. Assess risk factors (exacerbation history, FEV1)
3. Check inhaler technique
4. Check adherence
5. Review written action plan
6. Consider step up or step down

GINA STEPWISE TREATMENT:

Step 1 (Mild Intermittent):
• Preferred: As-needed low-dose ICS-formoterol
• Alternative: As-needed SABA + take ICS whenever SABA used

Step 2 (Mild Persistent):
• Preferred: Low-dose ICS + as-needed SABA OR as-needed ICS-formoterol
• Alternative: LTRA (less effective)

Step 3 (Moderate):
• Preferred: Low-dose ICS-LABA (maintenance + reliever if formoterol)
• Alternative: Medium-dose ICS or low-dose ICS + LTRA

Step 4 (Moderate-Severe):
• Preferred: Medium-dose ICS-LABA
• Add-on: Tiotropium, LTRA

Step 5 (Severe):
• High-dose ICS-LABA
• Add-on: Tiotropium, anti-IgE (omalizumab), anti-IL5, anti-IL4R
• Low-dose oral corticosteroids as last resort

STEPPING DOWN:
• Consider after 3 months of good control
• Never stop ICS completely in confirmed asthma
• Reduce ICS dose by 25-50%
• Good times: Low-risk season, no respiratory infections
• Maintain follow-up after step-down

STEPPING UP:
• If uncontrolled despite good technique and adherence
• Step up for 2-3 months, then reassess
• Consider short-term oral corticosteroid burst for loss of control`
    },

    redFlags: [
      '🔴 CRITICAL: Silent chest (no wheeze despite respiratory distress) - indicates severe bronchospasm; life-threatening',
      '🔴 CRITICAL: Cyanosis or SpO2 <92% on air - indicates severe hypoxia; needs immediate oxygen and intensive treatment',
      '🔴 CRITICAL: Exhaustion, altered consciousness, or inability to speak - imminent respiratory failure; prepare for intubation',
      '🔴 CRITICAL: Bradycardia or hypotension in acute asthma - pre-arrest; immediate senior help and resuscitation',
      '🔴 CRITICAL: Rising PaCO2 (>45 mmHg) or normal PaCO2 in severe attack - respiratory muscle fatigue; ICU',
      '🟠 URGENT: Peak flow <50% predicted or personal best - severe exacerbation; intensive bronchodilator therapy',
      '🟠 URGENT: Failure to respond to initial bronchodilator therapy - escalate treatment; consider IV magnesium',
      '🟠 URGENT: Previous near-fatal asthma (ICU admission, intubation) - higher risk; lower threshold for escalation',
      '🟠 URGENT: Using >1 SABA canister per month - very poor control; high risk of exacerbation and death',
      '🟡 CAUTION: Recurrent exacerbations despite controller therapy - reassess diagnosis, adherence, technique, triggers',
      '🟡 CAUTION: Night waking >1/week - indicates uncontrolled asthma; step up therapy',
      '🟡 CAUTION: Need for oral corticosteroids >2 times/year - frequent exacerbations; optimize control',
      '🟡 CAUTION: Fixed airflow obstruction (FEV1 persistently low) - suggests airway remodeling; maximize ICS'
    ],

    complications: [
      'Acute severe asthma (status asthmaticus) - Life-threatening exacerbation; requires hospitalization and intensive treatment',
      'Respiratory failure - From severe bronchospasm and exhaustion; may require mechanical ventilation',
      'Pneumothorax - From air trapping and high intrathoracic pressure; sudden deterioration in acute asthma',
      'Pneumomediastinum - Air in mediastinum from alveolar rupture; subcutaneous emphysema may be present',
      'Death - 461,000 deaths annually; most preventable with adequate controller therapy',
      'Airway remodeling - From chronic untreated inflammation; leads to fixed airflow obstruction',
      'Chronic cough - Significantly impacts quality of life',
      'Reduced lung function - Progressive decline if poorly controlled; accelerated lung aging',
      'Oral candidiasis - From inhaled corticosteroids; prevent by rinsing mouth',
      'Dysphonia (hoarse voice) - ICS side effect; can use spacer, rinse mouth, or change device',
      'Growth suppression in children - High-dose ICS may slow growth by ~1cm in first year; benefits outweigh risks',
      'Osteoporosis - With chronic high-dose ICS or frequent oral corticosteroids',
      'Adrenal suppression - With high-dose ICS or prolonged oral corticosteroid use',
      'Sleep disturbance - From nocturnal symptoms and SABA use',
      'Anxiety and depression - From chronic disease and fear of attacks; may worsen asthma'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• Asthma is generally controllable with appropriate therapy
• Most patients can achieve good control with ICS-based treatment
• Normal life expectancy achievable with optimal management
• Some patients may have partial or complete remission

WITH ADEQUATE TREATMENT:
• 80-90% of patients can achieve good symptom control
• Exacerbations can be significantly reduced
• Lung function can be preserved
• Normal activity levels including exercise achievable

CHILDHOOD ASTHMA:
• 30-50% of children "outgrow" asthma by adulthood
• More likely to remit: Mild asthma, non-atopic, male
• May recur later in life, especially with smoking/exposures
• Early ICS treatment may improve long-term outcomes

FACTORS AFFECTING PROGNOSIS:

Better Prognosis:
• Mild intermittent asthma
• Good adherence to ICS
• Correct inhaler technique
• Trigger avoidance achieved
• Non-smoker
• Normal lung function maintained
• No/few exacerbations
• Written action plan in use

Worse Prognosis:
• Severe persistent asthma
• Poor adherence to controller therapy
• Reliance on SABA without ICS
• Continued smoking or biomass exposure
• Frequent exacerbations
• Hospitalization or ICU admission for asthma
• Fixed airflow obstruction
• Comorbidities (obesity, rhinosinusitis, GERD)

MORTALITY:
• 461,000 deaths annually worldwide
• 80% of deaths in low/middle-income countries
• Most deaths preventable with:
  - Access to ICS
  - Written action plans
  - Prompt treatment of exacerbations
  - Avoidance of SABA-only treatment

AIRWAY REMODELING:
• Occurs with chronic untreated inflammation
• Leads to fixed (irreversible) airflow obstruction
• Early ICS treatment may prevent or slow remodeling
• Importance of treating even mild asthma with ICS`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing Asthma Development):

Evidence Limited But Consider:
• Breastfeeding - May have protective effect
• Avoid prenatal tobacco smoke exposure
• Avoid environmental tobacco smoke exposure in infancy
• Healthy diet during pregnancy and infancy
• Microbiome-friendly practices (avoid unnecessary antibiotics)
• Mediterranean diet may be protective
• No evidence for allergen avoidance preventing asthma development

SECONDARY PREVENTION (Preventing Symptoms and Exacerbations):

Pharmacological:
• Regular ICS - reduces symptoms, exacerbations, and mortality
• NEVER rely on SABA-only treatment
• Adherence to controller therapy is key
• Written asthma action plan

Trigger Avoidance:
• Allergen reduction (dust mites, pets, mold) - evidence mixed but reasonable
• Tobacco smoke avoidance - essential
• Biomass fuel smoke reduction
• Occupational exposure avoidance
• Avoid known triggers

Vaccinations:
• Annual influenza vaccination - reduces exacerbations
• Pneumococcal vaccination - per guidelines
• COVID-19 vaccination - reduces respiratory complications

Comorbidity Management:
• Treat allergic rhinitis - reduces asthma symptoms
• Treat GERD if symptomatic
• Weight management for obese patients

TERTIARY PREVENTION (Preventing Progression and Complications):

Optimized Treatment:
• Regular ICS to prevent airway remodeling
• Step up treatment if uncontrolled
• Specialist referral for severe asthma
• Consider biologic therapy for severe asthma

Self-Management:
• Asthma education programs
• Regular PEF monitoring
• Recognizing early warning signs
• Acting on written action plan

Environmental Control:
• Reduce indoor allergen exposure
• Improve indoor air quality
• Workplace modifications if occupational asthma

PUBLIC HEALTH MEASURES:
• Improve access to essential medications (ICS)
• Training healthcare workers in asthma management
• Public education campaigns
• Reduce air pollution
• Tobacco control policies
• Improve indoor cooking conditions`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS ASTHMA?
Asthma is a long-term condition affecting your breathing tubes (airways). In asthma, the airways are sensitive and easily become swollen and narrow when exposed to certain triggers. This causes wheezing, coughing, chest tightness, and shortness of breath. With proper treatment, most people with asthma can live normal, active lives.

UNDERSTANDING YOUR MEDICINES:

Controller (Preventer) - USE EVERY DAY:
• Usually a brown, orange, or purple inhaler
• Contains steroid medicine that reduces swelling in your airways
• MUST be used every day, even when you feel well
• Takes time to work (days to weeks for full effect)
• Prevents asthma attacks
• Rinse your mouth after use to prevent thrush

Reliever (Rescue) - USE WHEN NEEDED:
• Usually a blue inhaler (salbutamol)
• Opens your airways quickly when you have symptoms
• Works within minutes
• Should NOT be used more than 2-3 times per week if asthma is well controlled
• Using it too often means your asthma is not controlled - see your doctor

HOW TO USE YOUR INHALER:
1. Remove the cap and shake the inhaler
2. Attach to spacer (tube device) - ALWAYS use a spacer
3. Breathe out fully
4. Put the spacer mouthpiece in your mouth
5. Press the inhaler once
6. Breathe in slowly and deeply
7. Hold your breath for 10 seconds
8. If another puff needed, wait 30-60 seconds and repeat
9. Rinse your mouth with water after using controller inhaler

COMMON ASTHMA TRIGGERS:
• Dust and dust mites
• Mold and dampness
• Animal dander (pets)
• Pollen
• Smoke (tobacco, cooking fires, burning materials)
• Cold air
• Exercise
• Respiratory infections (colds, flu)
• Strong smells (perfumes, cleaning products)
• Some medications (aspirin, ibuprofen, beta-blockers)

WARNING SIGNS OF WORSENING ASTHMA:
• Needing reliever inhaler more often
• Waking at night with cough or wheeze
• Symptoms interfering with daily activities
• Peak flow readings dropping

WHEN TO SEEK EMERGENCY HELP:
• Severe shortness of breath
• Cannot speak in full sentences
• Blue lips or fingernails
• Reliever inhaler not helping
• Confusion or drowsiness
• Peak flow less than 50% of your best

YOUR ASTHMA ACTION PLAN:
Your doctor should give you a written plan that tells you:
• What medicine to take every day
• What to do when symptoms worsen
• When to increase treatment
• When to seek emergency help
Keep your plan where you can see it and follow it!

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is for educational purposes only. Asthma is a serious condition that requires regular treatment and follow-up. Always use your controller inhaler as prescribed, and seek help promptly for severe symptoms.

QUESTIONS TO ASK YOUR DOCTOR:
• Is my asthma well controlled?
• Am I using my inhalers correctly? (ask for demonstration)
• Do I need a written asthma action plan?
• What are my triggers and how can I avoid them?
• When should I seek emergency help?
• Do I need to increase my treatment?`,

    references: [
      'Global Initiative for Asthma (GINA). Global Strategy for Asthma Management and Prevention. 2023 Update. www.ginasthma.org',
      'British Thoracic Society/Scottish Intercollegiate Guidelines Network. British guideline on the management of asthma. BTS/SIGN. 2019 (updated 2023).',
      'National Asthma Education and Prevention Program. Expert Panel Report 3: Guidelines for the Diagnosis and Management of Asthma. NIH Publication 07-4051. 2007 (updated 2020).',
      'The Global Asthma Report 2022. Auckland, New Zealand: Global Asthma Network; 2022.',
      'Ait-Khaled N, Enarson DA, et al. Management of asthma in developing countries. Tuberculosis and respiratory diseases. Volume 1. IUATLD; 2004.',
      'Masoli M, Fabian D, et al. The global burden of asthma: executive summary of the GINA Dissemination Committee Report. Allergy. 2004;59(5):469-478.',
      'Adeloye D, Chan KY, et al. Global and regional estimates of COPD prevalence: Systematic review and meta-analysis. J Glob Health. 2015;5(2):020415.',
      'Ozoh OB, Aderibigbe SA, et al. The prevalence of asthma and allergic rhinitis in Nigeria: A nationwide survey among children, adolescents and adults. PLoS One. 2019;14(9):e0222281.',
      'Braman SS. The global burden of asthma. Chest. 2006;130(1 Suppl):4S-12S.',
      'Reddel HK, Bacharier LB, et al. Global Initiative for Asthma Strategy 2021: Executive Summary and Rationale for Key Changes. Am J Respir Crit Care Med. 2022;205(1):17-35.',
      'Rabe KF, Adachi M, et al. Worldwide severity and control of asthma in children and adults: The global Asthma Insights and Reality surveys. J Allergy Clin Immunol. 2004;114(1):40-47.',
      'Pavord ID, Beasley R, et al. After asthma: redefining airways diseases. Lancet. 2018;391(10118):350-400.',
      'Bateman ED, Hurd SS, et al. Global strategy for asthma management and prevention. Eur Respir J. 2008;31(1):143-178.',
      'Pearce N, Ait-Khaled N, et al. Worldwide trends in the prevalence of asthma symptoms: phase III of the International Study of Asthma and Allergies in Childhood (ISAAC). Thorax. 2007;62(9):758-766.',
      'Zar HJ, Levin ME. Challenges in treating pediatric asthma in developing countries. Paediatr Drugs. 2012;14(6):353-359.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 68. PEPTIC ULCER DISEASE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '68',
    name: 'Peptic Ulcer Disease',
    slug: 'peptic-ulcer-disease',
    specialty: 'Gastroenterology',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K27',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Peptic ulcer disease (PUD) refers to mucosal defects in the stomach (gastric ulcer) or duodenum (duodenal ulcer) that extend through the muscularis mucosae. Affecting approximately 5-10% of the population at some point in their lives, PUD remains a significant cause of morbidity worldwide. The two major etiological factors are Helicobacter pylori infection and nonsteroidal anti-inflammatory drug (NSAID) use, together accounting for over 90% of cases.

The pathophysiology involves an imbalance between protective factors (mucus-bicarbonate barrier, mucosal blood flow, prostaglandins, epithelial renewal) and aggressive factors (acid, pepsin, H. pylori, NSAIDs, bile). H. pylori infection causes chronic active gastritis that disrupts mucosal defenses, while NSAIDs inhibit cyclooxygenase (COX), reducing protective prostaglandin synthesis.

In Africa, H. pylori prevalence is among the highest globally (60-90% in many populations), acquired predominantly in childhood through fecal-oral transmission. Despite this high prevalence, peptic ulcer disease rates are paradoxically lower than in some developed countries - the "African enigma." However, PUD complications (bleeding, perforation) remain significant causes of morbidity and mortality, particularly where endoscopy services are limited.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - H. pylori and NSAIDs cause >90% of peptic ulcers - ALWAYS test for H. pylori and ask about NSAID use

🔴 CRITICAL POINT 2 - Alarm features (bleeding, weight loss, dysphagia, anemia, age >55 with new symptoms) require URGENT endoscopy

🔴 CRITICAL POINT 3 - Upper GI bleeding (hematemesis, melena) is a medical emergency - resuscitate, IV PPI, urgent endoscopy

🔴 CRITICAL POINT 4 - Perforated ulcer presents with sudden severe epigastric pain and peritonitis - surgical emergency

🟠 CRITICAL POINT 5 - Eradicate H. pylori with triple or quadruple therapy; confirm eradication 4+ weeks after treatment

🟠 CRITICAL POINT 6 - PPIs are more effective than H2 blockers for ulcer healing; standard duration is 4-8 weeks

🟠 CRITICAL POINT 7 - If patient MUST continue NSAIDs, co-prescribe PPI for gastroprotection

🟡 CRITICAL POINT 8 - Duodenal ulcer pain often relieved by food; gastric ulcer pain often worsened by food

🟡 CRITICAL POINT 9 - Smoking impairs ulcer healing and increases recurrence - smoking cessation is essential`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Lifetime prevalence: 5-10% of population
• Duodenal ulcers: 4 times more common than gastric ulcers
• Peak age: Duodenal ulcer 30-50 years; Gastric ulcer 50-70 years
• Complications (bleeding, perforation): 1-2% per year
• Mortality from complications: 5-10% for bleeding; 10-40% for perforation

AFRICAN EPIDEMIOLOGY:
• H. pylori prevalence: 60-90% (among highest globally)
• "African enigma": High H. pylori but relatively lower ulcer rates
• PUD complications remain significant cause of surgical admissions
• Limited endoscopy services affect diagnosis
• NSAID use increasing (often OTC)

RISK FACTORS:
• H. pylori infection (most important globally)
• NSAID use (including low-dose aspirin)
• Age >60 years
• Previous peptic ulcer history
• Concurrent anticoagulant or corticosteroid use
• Smoking
• Zollinger-Ellison syndrome (rare)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

DUODENAL ULCER:
• Epigastric pain (burning, gnawing)
• Pain 2-3 hours after meals
• Pain often relieved by food or antacids
• Nocturnal pain (1-3 AM)
• Periodicity: Symptom-free intervals

GASTRIC ULCER:
• Epigastric pain often worsened by food
• Nausea, vomiting more common
• Anorexia and weight loss

PHYSICAL EXAMINATION:
• Often normal in uncomplicated PUD
• Epigastric tenderness
• Signs of complications if present

ALARM FEATURES (Red Flags):
• Age >55 years with new onset dyspepsia
• Unintentional weight loss
• Progressive dysphagia
• GI bleeding (hematemesis, melena)
• Iron deficiency anemia
• Persistent vomiting
• Palpable mass`,

    diagnosis: `DIAGNOSTIC APPROACH:

H. PYLORI TESTING:

Non-Invasive:
• Urea breath test (UBT): Sensitivity/specificity >95%
• Stool antigen test: Sensitivity/specificity >90%
• Serology: Indicates exposure only, not active infection

Invasive (Endoscopy):
• Rapid urease test (CLO test)
• Histology
• Culture

IMPORTANT: Stop PPIs 2 weeks and antibiotics 4 weeks before H. pylori testing

ENDOSCOPY INDICATIONS:
• Alarm features present
• Treatment failure
• Gastric ulcer (ALL require biopsy)
• Age >55 with new dyspepsia

ALVARADO SCORE for gastric ulcers:
• ALL gastric ulcers require biopsy to exclude malignancy
• Follow-up endoscopy at 8 weeks to confirm healing`,

    differentialDiagnosis: [
      'Functional dyspepsia - Most common; no ulcer on endoscopy',
      'Gastroesophageal reflux disease (GERD) - Heartburn, regurgitation',
      'Gastric cancer - Weight loss, anorexia; biopsy essential',
      'Gastritis - Mucosal inflammation without ulceration',
      'Pancreatitis - Pain radiating to back; elevated amylase/lipase',
      'Biliary colic/cholecystitis - RUQ pain; ultrasound diagnostic',
      'Acute coronary syndrome - Epigastric pain may be atypical angina',
      'Zollinger-Ellison syndrome - Multiple/refractory ulcers'
    ],

    investigations: [
      {
        name: 'H. pylori Stool Antigen Test',
        indication: '🔴 ESSENTIAL - Non-invasive; good for diagnosis and eradication confirmation',
        availability: 'BASIC'
      },
      {
        name: 'Urea Breath Test (UBT)',
        indication: '🟠 PREFERRED - Most accurate non-invasive test; gold standard for eradication',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Upper GI Endoscopy (OGD)',
        indication: '🔴 ESSENTIAL IF ALARM FEATURES - Gold standard; visualizes ulcer; allows biopsy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Full Blood Count',
        indication: '🔴 ESSENTIAL - Detect anemia from chronic blood loss',
        availability: 'BASIC'
      },
      {
        name: 'Erect Chest X-Ray',
        indication: '🔴 ESSENTIAL IF PERFORATION SUSPECTED - Free air under diaphragm',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Serum Gastrin',
        indication: '🟡 WHEN INDICATED - Suspect Zollinger-Ellison if multiple/refractory ulcers',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS FOR COMPLICATIONS: Bleeding, perforation, obstruction',
        '⚡ IF BLEEDING: ABC resuscitation, crossmatch blood, IV PPI, urgent endoscopy',
        '⚡ IF PERFORATION: NPO, IV fluids, NG tube, IV antibiotics, urgent surgery',
        '⚡ STOP NSAIDs: Discontinue all NSAIDs if possible',
        '⚡ TEST FOR H. PYLORI: Stool antigen or serology',
        '⚡ START PPI: Begin acid suppression immediately',
        '⚡ SMOKING CESSATION: Essential for healing'
      ],
      
      pharmacological: [
        {
          drug: 'Omeprazole',
          indication: '🔴 FIRST-LINE - Proton pump inhibitor; promotes ulcer healing',
          dose: 'Ulcer healing: 20mg PO once daily for 4-8 weeks. Bleeding: 80mg IV bolus then 8mg/hour infusion.',
          route: 'PO / IV',
          duration: '4 weeks (duodenal); 8 weeks (gastric)',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'Diarrhea', 'B12 deficiency (long-term)'],
          alternatives: ['Esomeprazole', 'Pantoprazole', 'Lansoprazole']
        },
        {
          drug: 'H. pylori Triple Therapy (PPI + Clarithromycin + Amoxicillin)',
          indication: '🔴 FIRST-LINE ERADICATION - Standard triple therapy for 14 days',
          dose: 'Omeprazole 20mg BD + Clarithromycin 500mg BD + Amoxicillin 1g BD for 14 days',
          route: 'PO',
          duration: '14 days',
          contraindications: ['Penicillin allergy', 'Macrolide allergy'],
          sideEffects: ['Diarrhea', 'Metallic taste', 'Nausea'],
          alternatives: ['Quadruple therapy', 'Metronidazole-based regimen']
        },
        {
          drug: 'Bismuth Quadruple Therapy',
          indication: '🟠 FOR HIGH RESISTANCE AREAS - If clarithromycin resistance >15%',
          dose: 'PPI BD + Bismuth 525mg QDS + Metronidazole 500mg TDS + Tetracycline 500mg QDS for 14 days',
          route: 'PO',
          duration: '14 days',
          contraindications: ['Pregnancy (tetracycline)', 'Renal impairment (bismuth)'],
          sideEffects: ['Black stool', 'Nausea'],
          alternatives: ['Levofloxacin-based regimen']
        },
        {
          drug: 'Ranitidine or Famotidine',
          indication: '🟡 SECOND-LINE - H2 blocker if PPI unavailable',
          dose: 'Ranitidine 150mg BD or Famotidine 20mg BD',
          route: 'PO',
          duration: '4-8 weeks',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'Dizziness'],
          alternatives: ['PPI (preferred)']
        }
      ],
      
      nonPharmacological: [
        'NSAID CESSATION: Stop all NSAIDs; use paracetamol instead',
        'SMOKING CESSATION: Essential for healing and preventing recurrence',
        'ALCOHOL MODERATION: Avoid during healing',
        'REGULAR MEALS: Avoid prolonged fasting',
        'STRESS MANAGEMENT: May exacerbate symptoms'
      ],
      
      surgical: [
        'ENDOSCOPIC THERAPY FOR BLEEDING: Injection, thermal, or mechanical hemostasis',
        'OVERSEWING OF BLEEDING ULCER: If endoscopy fails',
        'GRAHAM PATCH REPAIR: For perforated ulcer',
        'GASTRECTOMY: For non-healing gastric ulcer or malignancy'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Upper GI bleeding - emergency endoscopy',
        '🔴 IMMEDIATE: Suspected perforation - emergency surgery',
        '🟠 URGENT: Alarm features - endoscopy to exclude malignancy',
        '🟡 ROUTINE: H. pylori eradication failure after two regimens'
      ],
      
      monitoring: [
        'SYMPTOM RESPONSE: Improvement expected within 1-2 weeks',
        'H. PYLORI ERADICATION: Confirm with UBT or stool antigen ≥4 weeks after treatment',
        'GASTRIC ULCER: Repeat endoscopy at 8 weeks to confirm healing and rebiopsy'
      ],
      
      followUp: `
UNCOMPLICATED DUODENAL ULCER:
• 4 weeks PPI, eradicate H. pylori
• Confirm eradication; no routine repeat endoscopy if asymptomatic

GASTRIC ULCER:
• 8 weeks PPI
• MANDATORY repeat endoscopy to confirm healing and rebiopsy

H. PYLORI ERADICATION:
• Test ≥4 weeks after completing treatment
• Stop PPI ≥2 weeks before testing`
    },

    redFlags: [
      '🔴 CRITICAL: Hematemesis or coffee-ground vomiting - acute upper GI bleed',
      '🔴 CRITICAL: Melena with hemodynamic instability - significant bleeding',
      '🔴 CRITICAL: Sudden severe epigastric pain with peritonitis - perforation',
      '🟠 URGENT: Unintentional weight loss - exclude malignancy',
      '🟠 URGENT: Progressive dysphagia - urgent endoscopy',
      '🟠 URGENT: Gastric ulcer not healing at 8 weeks - rebiopsy'
    ],

    complications: [
      'Upper GI bleeding - 15-20% of ulcers; mortality 5-10%',
      'Perforation - Surgical emergency; mortality 10-40%',
      'Gastric outlet obstruction - Rare with modern treatment',
      'Gastric cancer - Risk with H. pylori; all gastric ulcers biopsied'
    ],

    prognosis: `
• Excellent with appropriate treatment
• >95% healing with 4-8 weeks PPI
• H. pylori eradication prevents 80-90% of recurrences
• Without eradication: 60-80% recur within 1 year`,

    prevention: `
• H. pylori eradication
• Avoid unnecessary NSAIDs
• Co-prescribe PPI with essential NSAIDs in high-risk patients
• Smoking cessation`,

    patientEducation: `
WHAT IS A PEPTIC ULCER?
A sore in the stomach or small intestine lining, usually caused by H. pylori bacteria or painkillers (NSAIDs).

DANGER SIGNS - SEEK EMERGENCY HELP:
• Vomiting blood
• Black tarry stools
• Sudden severe abdominal pain

KEY POINTS:
• Complete ALL antibiotic treatment for H. pylori
• Avoid NSAIDs (ibuprofen, aspirin, diclofenac)
• Stop smoking
• Take PPI as prescribed`,

    references: [
      'Malfertheiner P, et al. Management of H. pylori infection: Maastricht VI/Florence consensus. Gut. 2022.',
      'Chey WD, et al. ACG Clinical Guideline: Treatment of H. pylori Infection. Am J Gastroenterol. 2017.',
      'Lanas A, Chan FKL. Peptic ulcer disease. Lancet. 2017.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 69. CHRONIC KIDNEY DISEASE
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '69',
    name: 'Chronic Kidney Disease',
    slug: 'chronic-kidney-disease',
    specialty: 'Nephrology',
    bodySystem: 'Renal',
    icdCode: 'N18',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Chronic kidney disease (CKD) is defined as abnormalities of kidney structure or function present for more than 3 months, with implications for health. It is classified based on cause, glomerular filtration rate (GFR) category (G1-G5), and albuminuria category (A1-A3). CKD affects approximately 10-15% of the global population and is a major risk factor for cardiovascular disease and end-stage renal disease (ESRD) requiring dialysis or transplantation.

The two leading causes of CKD globally are diabetes mellitus and hypertension, together accounting for 60-70% of cases. Other important causes include glomerulonephritis, polycystic kidney disease, obstructive uropathy, and chronic tubulointerstitial nephritis. CKD is often asymptomatic until advanced stages, making screening of high-risk populations essential. The condition progresses through stages based on GFR, with Stage 5 (GFR <15 mL/min/1.73m²) representing kidney failure requiring renal replacement therapy.

In Africa, CKD represents a devastating and often neglected epidemic. Prevalence estimates range from 10-20% in community studies, with hypertension being the leading cause (30-45%), followed by chronic glomerulonephritis (20-30%) and diabetes (15-25%). Unique African factors include HIV-associated nephropathy (HIVAN), sickle cell nephropathy, herbal nephrotoxicity from traditional medicines, and high rates of undiagnosed/untreated hypertension. The burden is compounded by late presentation (most patients present in Stage 4-5), limited access to dialysis (available to <5% of those who need it), and virtually no access to transplantation in most countries. Consequently, CKD mortality in Africa far exceeds that in developed regions.

Management focuses on slowing progression through blood pressure control (ACE inhibitors/ARBs are renoprotective), glycemic control in diabetes, addressing cardiovascular risk factors, and managing complications (anemia, mineral bone disease, acidosis, hyperkalemia). SGLT2 inhibitors have emerged as renoprotective agents. Preparation for renal replacement therapy should begin early, though access remains severely limited in most African settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - ACE inhibitors or ARBs are FIRST-LINE for CKD with proteinuria (uACR ≥30 mg/g) - reduce proteinuria and slow progression

🔴 CRITICAL POINT 2 - Check creatinine and potassium 1-2 weeks after starting or increasing ACEi/ARB; up to 30% rise in creatinine is acceptable

🔴 CRITICAL POINT 3 - SGLT2 inhibitors (dapagliflozin, empagliflozin) provide additional renoprotection - use if eGFR ≥20-25 and tolerated

🔴 CRITICAL POINT 4 - Avoid nephrotoxins: NSAIDs, aminoglycosides, IV contrast (if needed, hydrate well), herbal medicines

🟠 CRITICAL POINT 5 - Blood pressure target <130/80 mmHg for CKD with albuminuria; avoid excessive BP lowering in elderly/frail

🟠 CRITICAL POINT 6 - Screen for and treat CKD complications: anemia (check Hb), mineral bone disease (Ca, PO4, PTH), acidosis (HCO3), hyperkalemia

🟠 CRITICAL POINT 7 - Adjust medication doses for renal function - many drugs need dose reduction or avoidance in CKD

🟡 CRITICAL POINT 8 - Refer to nephrology if eGFR <30, rapidly declining GFR (>5 mL/min/year), heavy proteinuria, or refractory complications

🟡 CRITICAL POINT 9 - CKD patients have VERY HIGH cardiovascular risk - treat aggressively with statins, BP control, smoking cessation`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Global prevalence: 10-15% of adult population (~850 million people)
• Leading cause of death: Now among top 10 globally
• End-stage renal disease (ESRD): 2-3 million people on dialysis worldwide
• Major risk factor for cardiovascular disease (10-20x increased CV mortality)
• Leading causes: Diabetes (30-50%), Hypertension (25-30%), Glomerulonephritis (10-15%)
• Increasing prevalence due to aging population, diabetes, hypertension epidemics
• 80% of CKD patients are in low/middle-income countries

AFRICAN EPIDEMIOLOGY:
• Prevalence: 10-20% in community studies (likely underestimated)
• Leading cause: Hypertension (30-45%) - often undiagnosed and untreated
• Chronic glomerulonephritis: 20-30% (higher than developed countries)
• Diabetes: 15-25% (rising rapidly)
• HIV-associated nephropathy (HIVAN): Significant in high HIV prevalence areas
• Sickle cell nephropathy: Important in West/Central Africa
• Herbal/traditional medicine nephrotoxicity: Underrecognized cause
• Late presentation: 60-80% present in Stage 4-5 CKD
• Dialysis access: <5% of those needing RRT receive it
• Transplantation: Virtually unavailable in most countries
• Mortality: Very high due to limited RRT access

RISK FACTORS:

Non-Modifiable:
• Age >60 years
• Family history of CKD
• African ancestry (APOL1 gene variants increase risk)
• Male sex (slightly higher risk)
• Low birth weight

Modifiable:
• Diabetes mellitus
• Hypertension
• Obesity
• Smoking
• Nephrotoxic medications (NSAIDs, aminoglycosides)
• Recurrent UTIs/kidney stones
• Herbal medicine use

CAUSES BY CATEGORY:
• Diabetic nephropathy: Most common cause globally
• Hypertensive nephrosclerosis: Leading cause in Africa
• Glomerulonephritis: Primary and secondary
• Polycystic kidney disease: Autosomal dominant most common
• Obstructive uropathy: BPH, stones, strictures
• Chronic tubulointerstitial nephritis: Analgesics, infections, toxins
• HIV-associated nephropathy (HIVAN)
• Sickle cell nephropathy`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

EARLY CKD (Stages 1-3):
• Often ASYMPTOMATIC
• May have symptoms of underlying cause (e.g., diabetic symptoms)
• Nocturia (loss of concentrating ability)
• Mild fatigue
• Foamy urine (if significant proteinuria)

MODERATE-ADVANCED CKD (Stages 3b-4):
• Fatigue and weakness
• Decreased appetite
• Nausea
• Cognitive impairment (difficulty concentrating)
• Peripheral edema
• Pruritus (itching)
• Restless legs
• Sleep disturbance
• Muscle cramps

SEVERE CKD/UREMIA (Stage 5):
• Severe fatigue
• Anorexia, nausea, vomiting
• Metallic taste, uremic fetor (breath)
• Hiccups
• Significant edema
• Dyspnea (fluid overload, anemia, acidosis)
• Pericarditis (uremic)
• Encephalopathy (confusion, seizures, coma)
• Bleeding tendency
• Severe pruritus
• Skin changes (uremic frost - very late)

PHYSICAL EXAMINATION:

General:
• Pallor (anemia)
• Sallow complexion (uremic)
• Excoriations (scratching from pruritus)

Cardiovascular:
• Hypertension (very common)
• Signs of fluid overload (raised JVP, edema, crackles)
• Pericardial friction rub (uremic pericarditis)
• Cardiomegaly

Respiratory:
• Crackles (pulmonary edema)
• Pleural effusion

Neurological:
• Peripheral neuropathy (stocking-glove sensory loss)
• Asterixis (metabolic flap) - in uremia
• Altered mental status (uremic encephalopathy)

Renal:
• May palpate enlarged kidneys (polycystic kidney disease)
• Usually kidneys not palpable

Skin:
• Pallor
• Excoriations
• Easy bruising
• Uremic frost (very advanced - rare now)`,

    diagnosis: `DIAGNOSTIC APPROACH:

CKD DEFINITION (KDIGO):
Abnormalities of kidney structure or function present for >3 months:
• GFR <60 mL/min/1.73m² OR
• Markers of kidney damage (one or more):
  - Albuminuria (uACR ≥30 mg/g)
  - Urine sediment abnormalities
  - Electrolyte abnormalities due to tubular disorders
  - Abnormalities detected by histology
  - Structural abnormalities detected by imaging
  - History of kidney transplantation

GFR CATEGORIES (G1-G5):
• G1: ≥90 (Normal or high) - with kidney damage markers
• G2: 60-89 (Mildly decreased) - with kidney damage markers
• G3a: 45-59 (Mildly to moderately decreased)
• G3b: 30-44 (Moderately to severely decreased)
• G4: 15-29 (Severely decreased)
• G5: <15 (Kidney failure)

ALBUMINURIA CATEGORIES (A1-A3):
• A1: <30 mg/g (Normal to mildly increased)
• A2: 30-300 mg/g (Moderately increased - "microalbuminuria")
• A3: >300 mg/g (Severely increased - "macroalbuminuria")

CALCULATING eGFR:
Use CKD-EPI equation (preferred) or MDRD
Required: Serum creatinine, age, sex, (race - controversial, being removed)
Online calculators widely available

STEP 1: CONFIRM CHRONICITY
• Repeat tests at 3 months if first abnormal result
• Review previous results if available
• Small kidneys on ultrasound suggests chronicity

STEP 2: IDENTIFY CAUSE
• History: Diabetes, hypertension, medications, family history
• Urinalysis: Proteinuria, hematuria, casts
• Imaging: Ultrasound (size, obstruction, cysts)
• Consider biopsy if cause unclear and would change management

STEP 3: ASSESS SEVERITY AND COMPLICATIONS
• GFR category and albuminuria category
• Anemia, electrolytes, mineral bone disease
• Cardiovascular assessment

DIAGNOSTIC FEATURES BY CAUSE:

Diabetic Nephropathy:
• Long-standing diabetes (usually >10 years for T1DM)
• Progressive albuminuria (microalbuminuria → macroalbuminuria)
• Diabetic retinopathy often present
• No active urine sediment
• Kidneys initially enlarged, then normal/small

Hypertensive Nephrosclerosis:
• Long-standing hypertension
• Mild-moderate proteinuria (<1g/day usually)
• Hypertensive retinopathy
• Small kidneys, increased echogenicity

Glomerulonephritis:
• Active urine sediment (RBC casts, dysmorphic RBCs)
• Significant proteinuria (often nephrotic range)
• May have systemic features
• Biopsy usually needed for specific diagnosis

Polycystic Kidney Disease:
• Family history (autosomal dominant)
• Multiple bilateral cysts on imaging
• Enlarged kidneys
• Liver cysts common`,

    differentialDiagnosis: [
      'Acute kidney injury (AKI) - Sudden onset, often reversible, recent precipitant; differentiate from CKD',
      'Acute-on-chronic kidney disease - AKI superimposed on CKD; identify and treat acute component',
      'Prerenal azotemia - Volume depletion, heart failure; reversible with fluid/treatment',
      'Postrenal obstruction - Bilateral hydronephrosis; relieve obstruction',
      'Diabetic nephropathy - Progressive albuminuria, retinopathy present, long DM duration',
      'Hypertensive nephrosclerosis - Long HTN history, mild proteinuria, small kidneys',
      'Chronic glomerulonephritis - Active sediment, significant proteinuria; biopsy for diagnosis',
      'IgA nephropathy - Episodic gross hematuria, persistent microscopic hematuria',
      'Focal segmental glomerulosclerosis (FSGS) - Nephrotic syndrome, especially in Africans (APOL1)',
      'Membranous nephropathy - Nephrotic syndrome; may be secondary to malignancy, infections',
      'Lupus nephritis - SLE features, positive serology, active sediment',
      'Polycystic kidney disease - Multiple bilateral cysts, family history',
      'HIV-associated nephropathy (HIVAN) - HIV positive, rapid progression, large echogenic kidneys',
      'Sickle cell nephropathy - Sickle cell disease, concentrating defect, may have proteinuria',
      'Analgesic nephropathy - History of chronic analgesic use, papillary necrosis',
      'Obstructive uropathy - Hydronephrosis, identify and relieve obstruction'
    ],

    investigations: [
      {
        name: 'Serum Creatinine and eGFR',
        indication: '🔴 ESSENTIAL - Calculate eGFR to stage CKD; use CKD-EPI equation; repeat to confirm chronicity',
        availability: 'BASIC'
      },
      {
        name: 'Urine Albumin-Creatinine Ratio (uACR)',
        indication: '🔴 ESSENTIAL - Quantify albuminuria; ≥30 mg/g indicates kidney damage; prognostic value',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Urinalysis (Dipstick and Microscopy)',
        indication: '🔴 ESSENTIAL - Proteinuria, hematuria, casts; active sediment suggests glomerulonephritis',
        availability: 'BASIC'
      },
      {
        name: 'Renal Ultrasound',
        indication: '🔴 ESSENTIAL - Kidney size (small = chronic), obstruction, cysts, structural abnormalities',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🔴 ESSENTIAL - Anemia common in CKD (normocytic, normochromic); assess severity',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Na, K, Cl, HCO3)',
        indication: '🔴 ESSENTIAL - Hyperkalemia and metabolic acidosis common in advanced CKD',
        availability: 'BASIC'
      },
      {
        name: 'Serum Calcium and Phosphate',
        indication: '🔴 ESSENTIAL - CKD-mineral bone disease; hypocalcemia, hyperphosphatemia in advanced CKD',
        availability: 'BASIC'
      },
      {
        name: 'Serum Albumin',
        indication: '🟠 IMPORTANT - Assess nutritional status and nephrotic syndrome',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Lipid Profile',
        indication: '🟠 IMPORTANT - Cardiovascular risk assessment; dyslipidemia common in CKD',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Glucose / HbA1c',
        indication: '🟠 IMPORTANT - Screen for diabetes as cause; monitor control if diabetic (HbA1c may be inaccurate in CKD)',
        availability: 'BASIC'
      },
      {
        name: 'Parathyroid Hormone (PTH)',
        indication: '🟠 IMPORTANT - Secondary hyperparathyroidism; check if eGFR <45',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Iron Studies (Ferritin, TSAT)',
        indication: '🟠 IMPORTANT - Iron deficiency common; needed before ESA therapy; target ferritin >100, TSAT >20%',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Hepatitis B and C Serology',
        indication: '🟠 IMPORTANT - Screen before immunosuppression or dialysis; HBV/HCV can cause glomerulonephritis',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🟠 IMPORTANT - HIV-associated nephropathy; universal screening recommended',
        availability: 'BASIC'
      },
      {
        name: 'Kidney Biopsy',
        indication: '🟡 WHEN INDICATED - Unexplained CKD, significant proteinuria, active sediment; to guide specific treatment',
        availability: 'ADVANCED'
      },
      {
        name: 'ANA, ANCA, Anti-GBM, Complement',
        indication: '🟡 WHEN INDICATED - Suspected autoimmune/vasculitic cause; guided by clinical features',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ IDENTIFY CAUSE: Diabetes, hypertension, glomerulonephritis, obstruction, other',
        '⚡ STOP NEPHROTOXINS: Discontinue NSAIDs, aminoglycosides; review all medications',
        '⚡ ASSESS VOLUME STATUS: Treat dehydration or fluid overload appropriately',
        '⚡ BLOOD PRESSURE CONTROL: Target <130/80 if albuminuria; start ACEi/ARB if proteinuric',
        '⚡ GLYCEMIC CONTROL: If diabetic, optimize (target HbA1c individualized, usually <7-8%)',
        '⚡ CHECK FOR COMPLICATIONS: Anemia, hyperkalemia, acidosis, mineral bone disease',
        '⚡ DOSE ADJUST MEDICATIONS: Review all drugs for renal dosing requirements',
        '⚡ CARDIOVASCULAR RISK: Start statin (most CKD patients); aspirin if indicated',
        '⚡ LIFESTYLE ADVICE: Smoking cessation, salt restriction, weight management',
        '⚡ PATIENT EDUCATION: Explain CKD, importance of BP/glucose control, avoid nephrotoxins'
      ],
      
      pharmacological: [
        {
          drug: 'Enalapril (ACE Inhibitor)',
          indication: '🔴 FIRST-LINE - Renoprotective if albuminuria (uACR ≥30); reduces proteinuria; slows progression',
          dose: 'Start 2.5-5mg PO once daily. Titrate to 10-20mg daily as tolerated. Check K+ and Cr at 1-2 weeks.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Bilateral renal artery stenosis', 'Pregnancy', 'K+ >5.5', 'Previous angioedema with ACEi'],
          sideEffects: ['Cough (10-15%)', 'Hyperkalemia', 'Acute creatinine rise (acceptable up to 30%)', 'Angioedema (rare)'],
          alternatives: ['Lisinopril', 'Ramipril', 'ARB if ACEi intolerant (cough)']
        },
        {
          drug: 'Losartan (ARB)',
          indication: '🔴 FIRST-LINE ALTERNATIVE - If ACEi intolerant (cough); similar renoprotective effect',
          dose: 'Start 25-50mg PO once daily. Titrate to 100mg daily. Check K+ and Cr at 1-2 weeks.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Bilateral RAS', 'Pregnancy', 'Hyperkalemia', 'Previous angioedema'],
          sideEffects: ['Hyperkalemia', 'Acute creatinine rise', 'Angioedema (rare, less than ACEi)'],
          alternatives: ['Valsartan', 'Irbesartan', 'Telmisartan']
        },
        {
          drug: 'Dapagliflozin (SGLT2 Inhibitor)',
          indication: '🔴 ADD TO ACEi/ARB - Proven renoprotection in CKD; reduces progression to ESRD; CV benefit',
          dose: '10mg PO once daily. Can initiate if eGFR ≥20-25 (DAPA-CKD trial). Continue even if eGFR falls.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Type 1 diabetes (DKA risk)', 'Recurrent genital infections'],
          sideEffects: ['Genital mycotic infections', 'Volume depletion', 'Euglycemic DKA (rare)', 'Initial eGFR dip (expected, reversible)'],
          alternatives: ['Empagliflozin', 'Canagliflozin']
        },
        {
          drug: 'Amlodipine',
          indication: '🟠 ADD-ON ANTIHYPERTENSIVE - Calcium channel blocker; effective in CKD; no dose adjustment needed',
          dose: '5-10mg PO once daily.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Severe aortic stenosis', 'Cardiogenic shock'],
          sideEffects: ['Peripheral edema', 'Headache', 'Flushing'],
          alternatives: ['Nifedipine', 'Diltiazem', 'Verapamil']
        },
        {
          drug: 'Furosemide',
          indication: '🟠 FOR FLUID OVERLOAD - Loop diuretic; effective even in advanced CKD; higher doses needed as GFR declines',
          dose: 'Start 20-40mg PO once daily. May need 80-160mg or more in advanced CKD. IV if severe edema.',
          route: 'PO / IV',
          duration: 'As needed for volume control',
          contraindications: ['Severe hypovolemia', 'Anuria (relative)'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Dehydration', 'Ototoxicity (high IV doses)'],
          alternatives: ['Bumetanide', 'Torsemide']
        },
        {
          drug: 'Sodium Bicarbonate',
          indication: '🟠 FOR METABOLIC ACIDOSIS - If serum HCO3 <22 mmol/L; may slow CKD progression',
          dose: '500-1000mg (6-12 mmol) PO two to three times daily. Target HCO3 ≥22 mmol/L.',
          route: 'PO',
          duration: 'Long-term if acidosis persists',
          contraindications: ['Severe fluid overload', 'Severe hypertension'],
          sideEffects: ['Sodium/fluid retention', 'Bloating', 'Hypokalemia'],
          alternatives: ['Sodium citrate']
        },
        {
          drug: 'Calcium Carbonate',
          indication: '🟠 PHOSPHATE BINDER - For hyperphosphatemia in CKD Stage 4-5; also provides calcium',
          dose: '500-1500mg PO with meals (binds dietary phosphate). Adjust to achieve phosphate target.',
          route: 'PO',
          duration: 'Long-term with meals',
          contraindications: ['Hypercalcemia'],
          sideEffects: ['Hypercalcemia', 'Constipation', 'Vascular calcification risk if overused'],
          alternatives: ['Calcium acetate', 'Sevelamer (non-calcium)', 'Lanthanum']
        },
        {
          drug: 'Erythropoiesis-Stimulating Agent (Epoetin/Darbepoetin)',
          indication: '🟠 FOR ANEMIA - If Hb <10 g/dL and iron replete; target Hb 10-11.5 g/dL; do not normalize',
          dose: 'Epoetin: 50-100 units/kg SC three times weekly. Darbepoetin: 0.45 mcg/kg SC weekly. Titrate to Hb target.',
          route: 'SC',
          duration: 'Long-term',
          contraindications: ['Uncontrolled hypertension', 'Pure red cell aplasia history'],
          sideEffects: ['Hypertension', 'Thrombosis', 'Pure red cell aplasia (rare)'],
          alternatives: ['Blood transfusion (avoid if possible - sensitization)']
        },
        {
          drug: 'Ferrous Sulfate',
          indication: '🔴 FOR IRON DEFICIENCY - Treat before/with ESA; target ferritin >100, TSAT >20%',
          dose: '325mg (65mg elemental iron) PO two to three times daily. Take on empty stomach if tolerated.',
          route: 'PO',
          duration: 'Until iron replete; maintenance as needed',
          contraindications: ['Iron overload'],
          sideEffects: ['GI upset', 'Constipation', 'Black stools'],
          alternatives: ['IV iron (iron sucrose, ferric carboxymaltose) if oral not tolerated/absorbed']
        },
        {
          drug: 'Atorvastatin',
          indication: '🟠 CARDIOVASCULAR PROTECTION - Statin for most CKD patients (high CV risk); no dose adjustment',
          dose: '10-20mg PO once daily (moderate intensity). Higher doses if tolerated.',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Active liver disease', 'Pregnancy'],
          sideEffects: ['Myalgia', 'Elevated LFTs', 'Rhabdomyolysis (rare)'],
          alternatives: ['Rosuvastatin (renally eliminated - use lower dose)', 'Simvastatin']
        },
        {
          drug: 'Calcitriol (Active Vitamin D)',
          indication: '🟡 FOR SECONDARY HYPERPARATHYROIDISM - If PTH elevated despite phosphate control; use with caution',
          dose: '0.25-0.5 mcg PO daily or every other day. Monitor calcium closely.',
          route: 'PO',
          duration: 'Long-term; titrate to PTH',
          contraindications: ['Hypercalcemia', 'Hyperphosphatemia'],
          sideEffects: ['Hypercalcemia', 'Hyperphosphatemia', 'Soft tissue calcification'],
          alternatives: ['Alfacalcidol', 'Paricalcitol']
        }
      ],
      
      nonPharmacological: [
        'DIETARY SODIUM RESTRICTION: <2g/day (or <5g salt); reduces BP and edema; very important',
        'PROTEIN INTAKE: Moderate restriction (0.8g/kg/day) may slow progression; avoid severe restriction (malnutrition)',
        'POTASSIUM RESTRICTION: If hyperkalemia; avoid high-K foods (bananas, oranges, potatoes, tomatoes)',
        'PHOSPHATE RESTRICTION: Limit dairy, processed foods, colas in advanced CKD',
        'FLUID RESTRICTION: If oliguric or fluid overloaded; typically 1-1.5L/day in advanced CKD',
        'SMOKING CESSATION: Smoking accelerates CKD progression and increases CV risk',
        'WEIGHT MANAGEMENT: Obesity worsens CKD; target healthy BMI',
        'EXERCISE: Regular moderate exercise; improves CV fitness and quality of life',
        'AVOID NEPHROTOXINS: No NSAIDs, minimize contrast exposure, avoid herbal medicines',
        'SICK DAY RULES: Hold ACEi/ARB, diuretics, metformin, SGLT2i during acute illness with dehydration'
      ],
      
      surgical: [
        'ARTERIOVENOUS FISTULA CREATION: For hemodialysis access; create when eGFR <20-25 (6+ months before anticipated dialysis)',
        'PERITONEAL DIALYSIS CATHETER: Tenckhoff catheter insertion for PD; 2+ weeks before use',
        'KIDNEY TRANSPLANTATION: Best outcome for ESRD; limited availability in Africa',
        'RELIEF OF OBSTRUCTION: Nephrostomy or ureteric stent for obstructive uropathy',
        'PARATHYROIDECTOMY: For refractory tertiary hyperparathyroidism',
        'NEPHRECTOMY: For infected non-functioning kidney or large ADPKD kidneys pre-transplant'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Rapidly progressive renal failure (creatinine doubling in <3 months)',
        '🔴 IMMEDIATE REFERRAL: Suspected acute glomerulonephritis (active sediment, systemic features)',
        '🔴 IMMEDIATE REFERRAL: Severe hyperkalemia (K+ >6.5) or uremic emergency (pericarditis, encephalopathy)',
        '🟠 URGENT REFERRAL: eGFR <30 mL/min/1.73m² - prepare for RRT, manage complications',
        '🟠 URGENT REFERRAL: Heavy proteinuria (uACR >300 or PCR >100) - needs nephrology workup',
        '🟠 URGENT REFERRAL: GFR decline >5 mL/min/year (rapid progression)',
        '🟠 URGENT REFERRAL: Refractory hypertension despite 3+ agents including diuretic',
        '🟡 ROUTINE REFERRAL: CKD Stage 4 (eGFR 15-29) for RRT planning',
        '🟡 ROUTINE REFERRAL: Persistent hematuria with proteinuria (consider biopsy)',
        '🟡 ROUTINE REFERRAL: CKD complications difficult to manage (anemia, MBD, acidosis)'
      ],
      
      monitoring: [
        'eGFR AND CREATININE: Every 3-6 months (G3); every 1-3 months (G4-5)',
        'URINE ALBUMIN-CREATININE RATIO: Annually if A1; more frequently if A2-A3',
        'BLOOD PRESSURE: At every visit; home BP monitoring encouraged',
        'ELECTROLYTES (K+, HCO3): Every 3-6 months (G3); every 1-3 months (G4-5)',
        'HEMOGLOBIN: Every 6-12 months (G3); every 3 months (G4-5)',
        'CALCIUM, PHOSPHATE: Every 6-12 months (G3); every 3-6 months (G4-5)',
        'PTH: Every 6-12 months if eGFR <45',
        'LIPID PROFILE: Annually',
        'POTASSIUM AFTER ACEi/ARB CHANGE: Check 1-2 weeks after starting or dose change',
        'WEIGHT AND VOLUME STATUS: At every visit'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

FREQUENCY BY CKD STAGE:

Stage G1-G2 (eGFR ≥60 with kidney damage):
• Annual review if stable
• Monitor BP, uACR, eGFR annually
• Focus on managing underlying cause

Stage G3a (eGFR 45-59):
• Review every 6-12 months
• Monitor Hb, electrolytes, Ca/PO4 annually
• Cardiovascular risk management

Stage G3b (eGFR 30-44):
• Review every 3-6 months
• Monitor Hb, electrolytes, Ca/PO4 every 6 months
• PTH annually
• Consider nephrology referral

Stage G4 (eGFR 15-29):
• Review every 1-3 months
• Monitor Hb, electrolytes monthly
• Ca/PO4, PTH every 3-6 months
• Nephrology co-management
• Begin RRT education and planning
• AV fistula creation when eGFR <20-25

Stage G5 (eGFR <15):
• Review every 1-2 months
• Monitor Hb, electrolytes, Ca/PO4 monthly
• Prepare for dialysis initiation
• Consider conservative management in selected patients

KEY FOLLOW-UP ACTIONS:

At Every Visit:
• Blood pressure (target <130/80 if proteinuria)
• Weight and volume status
• Medication adherence
• Review for complications
• Reinforce lifestyle advice

Medication Review:
• Dose adjust for eGFR (renally cleared drugs)
• Avoid nephrotoxins
• Sick day rules education

RRT PREPARATION (when eGFR approaching 15-20):
• Patient education on dialysis modalities (HD vs PD)
• AV fistula creation (6+ months before anticipated need)
• Hepatitis B vaccination (higher dose, check response)
• Discuss transplant options if appropriate
• Advance care planning

CONSERVATIVE MANAGEMENT:
• Option for elderly, frail, or those declining dialysis
• Focus on symptom control and quality of life
• Palliative care involvement
• Documented goals of care`
    },

    redFlags: [
      '🔴 CRITICAL: Severe hyperkalemia (K+ >6.5 mmol/L with ECG changes) - emergency treatment required',
      '🔴 CRITICAL: Uremic pericarditis (chest pain, friction rub) - needs urgent dialysis',
      '🔴 CRITICAL: Uremic encephalopathy (confusion, seizures) - needs urgent dialysis',
      '🔴 CRITICAL: Severe pulmonary edema not responding to diuretics - needs urgent dialysis',
      '🔴 CRITICAL: Rapidly progressive glomerulonephritis (creatinine doubling in days-weeks)',
      '🟠 URGENT: eGFR decline >5 mL/min/year - investigate cause, optimize treatment',
      '🟠 URGENT: New-onset heavy proteinuria (nephrotic range) - needs investigation',
      '🟠 URGENT: Severe uncontrolled hypertension (>180/120) with CKD',
      '🟠 URGENT: Severe anemia (Hb <7 g/dL) - may need transfusion',
      '🟡 CAUTION: Persistent hyperkalemia despite dietary modification - review medications',
      '🟡 CAUTION: Creatinine rise >30% after starting ACEi/ARB - reassess (consider RAS)',
      '🟡 CAUTION: Refractory acidosis (HCO3 <18 despite supplementation)',
      '🟡 CAUTION: Secondary hyperparathyroidism with hypercalcemia (tertiary HPTH)'
    ],

    complications: [
      'Cardiovascular disease - Leading cause of death in CKD; 10-20x increased risk',
      'Anemia of CKD - From reduced erythropoietin; causes fatigue, reduced QoL',
      'CKD-Mineral Bone Disease - Hyperphosphatemia, hypocalcemia, secondary HPTH, renal osteodystrophy',
      'Hyperkalemia - Life-threatening arrhythmias; common with ACEi/ARB, dietary indiscretion',
      'Metabolic acidosis - Contributes to muscle wasting, bone disease; accelerates CKD progression',
      'Fluid overload - Edema, hypertension, pulmonary edema',
      'Uremic syndrome - Nausea, vomiting, encephalopathy, pericarditis, bleeding',
      'Malnutrition - Protein-energy wasting; poor outcomes',
      'Infection - Increased susceptibility; major cause of morbidity',
      'Peripheral neuropathy - Sensory > motor; improves with dialysis',
      'Restless legs syndrome - Improves with dialysis, dopamine agonists',
      'Sexual dysfunction - Erectile dysfunction, reduced libido',
      'Depression - Common; screening recommended',
      'Progression to ESRD - Requiring dialysis or transplant',
      'Death - High mortality, especially with cardiovascular complications'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL PROGNOSIS:
• CKD is usually progressive but rate varies widely
• Cardiovascular death more common than progression to ESRD
• Early detection and treatment significantly slows progression
• Prognosis depends on stage, albuminuria, cause, and comorbidities

PROGRESSION RATES:
• Average GFR decline: 1-2 mL/min/year (highly variable)
• Faster progression: Heavy proteinuria, uncontrolled BP, diabetes
• Slower progression: ACEi/ARB use, SGLT2i, BP control, lifestyle

RISK OF PROGRESSION TO ESRD:
• Stage G3a with A1: Low risk
• Stage G3b with A2: Moderate risk
• Stage G4 with A3: Very high risk
• Higher albuminuria = faster progression

MORTALITY:
• CKD significantly increases CV and all-cause mortality
• Stage G3: 1.5-2x increased mortality
• Stage G4: 2-3x increased mortality
• Stage G5 (dialysis): 15-20% annual mortality

IN AFRICAN CONTEXT:
• Prognosis often poor due to late presentation
• Limited RRT access means ESRD is often fatal
• Mortality rates higher than developed countries
• Prevention and early detection crucial

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early detection and treatment
• Good BP control (<130/80)
• ACEi/ARB use with proteinuria
• SGLT2 inhibitor use
• Glycemic control in diabetes
• Smoking cessation
• Low proteinuria

Worse Outcomes:
• Late presentation (Stage 4-5)
• Heavy proteinuria
• Uncontrolled hypertension
• Uncontrolled diabetes
• Cardiovascular disease
• Continued smoking
• Nephrotoxin exposure
• Limited healthcare access`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing CKD):

Population Level:
• Screen and treat hypertension
• Screen and treat diabetes
• Reduce obesity prevalence
• Tobacco control
• Clean water and sanitation (reduce GN from infections)
• Regulate herbal/traditional medicine safety

Individual Level:
• Healthy diet (low salt, balanced)
• Regular physical activity
• Maintain healthy weight
• Avoid smoking
• Limit alcohol
• Avoid nephrotoxins (NSAIDs, herbal medicines)
• Adequate hydration

SECONDARY PREVENTION (Preventing Progression):

Screening High-Risk Groups:
• Diabetes - annual uACR and eGFR
• Hypertension - annual eGFR
• Family history of CKD
• Cardiovascular disease
• HIV positive
• Previous AKI

Slowing Progression:
• ACEi/ARB for all with albuminuria
• SGLT2 inhibitor if eGFR ≥20-25
• Blood pressure <130/80
• Glycemic control (HbA1c <7-8%)
• Avoid nephrotoxins
• Treat complications (anemia, MBD, acidosis)

TERTIARY PREVENTION (ESRD Preparation):

Dialysis Access:
• Create AV fistula early (eGFR <20-25)
• Hepatitis B vaccination

Modality Choice:
• Education on HD vs PD
• Home dialysis where feasible

Transplantation:
• Pre-emptive transplant is best outcome
• Living donor evaluation
• Deceased donor waiting list

Conservative Care:
• Option for those declining dialysis
• Focus on symptom management and quality of life`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS CHRONIC KIDNEY DISEASE (CKD)?
Your kidneys filter waste and extra fluid from your blood. Chronic kidney disease means your kidneys are damaged and don't work as well as they should. CKD usually develops slowly over many years and may not cause symptoms until it is advanced. The two most common causes are high blood pressure and diabetes.

WHY IS CKD SERIOUS?
• CKD increases your risk of heart attack and stroke
• It can progress to kidney failure requiring dialysis
• Many problems can be prevented with early treatment

WHAT CAN YOU DO?

Blood Pressure:
• Take your BP medications EVERY DAY as prescribed
• Target is usually below 130/80
• Check your BP regularly at home or clinic

Diabetes (if you have it):
• Control your blood sugar
• Take medications as prescribed
• Regular monitoring

Medications to Protect Your Kidneys:
• ACE inhibitors or ARBs protect your kidneys
• SGLT2 inhibitors (if prescribed) also protect kidneys
• Take them even if you feel fine

AVOID THESE - THEY CAN HARM YOUR KIDNEYS:
• Painkillers like ibuprofen, diclofenac (NSAIDs)
• Herbal or traditional medicines (many are kidney toxic)
• Contrast dye for scans (tell doctors you have CKD)
• Dehydration (drink enough fluids unless restricted)
• Smoking (damages blood vessels including in kidneys)

DIET CHANGES:
• Reduce salt - avoid adding salt, processed foods
• Moderate protein intake
• If potassium is high: limit bananas, oranges, potatoes, tomatoes
• Avoid colas and processed foods (high phosphate)

SICK DAY RULES:
When you have vomiting, diarrhea, or fever:
• STOP: ACE inhibitors, ARBs, diuretics (water tablets), SGLT2 inhibitors, metformin
• Drink fluids if you can
• See a doctor if you cannot keep fluids down
• RESTART medications when eating and drinking normally

WHEN TO SEEK MEDICAL HELP:
• Swelling that is getting worse
• Difficulty breathing
• Chest pain
• Confusion or drowsiness
• Unable to pass urine
• Severe nausea or vomiting

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

CKD is a serious condition but can be managed. Take your medications as prescribed, attend all appointments, and follow lifestyle advice. Early treatment slows progression and prevents complications.

QUESTIONS TO ASK YOUR DOCTOR:
• What stage is my kidney disease?
• What caused my kidney disease?
• What can I do to slow it down?
• Do I need to see a kidney specialist?
• Should I be preparing for dialysis?`,

    references: [
      'Kidney Disease: Improving Global Outcomes (KDIGO) CKD Work Group. KDIGO 2024 Clinical Practice Guideline for the Evaluation and Management of Chronic Kidney Disease. Kidney Int. 2024;105(4S):S117-S314.',
      'Jager KJ, Kovesdy C, et al. A single number for advocacy and communication—worldwide more than 850 million individuals have kidney diseases. Kidney Int. 2019;96(5):1048-1050.',
      'GBD Chronic Kidney Disease Collaboration. Global, regional, and national burden of chronic kidney disease, 1990-2017. Lancet. 2020;395(10225):709-733.',
      'Stanifer JW, Jing B, et al. The epidemiology of chronic kidney disease in sub-Saharan Africa: a systematic review and meta-analysis. Lancet Glob Health. 2014;2(3):e174-e181.',
      'Adeyi OA, Akinwumi A, et al. Chronic kidney disease in low- and middle-income countries. Trans R Soc Trop Med Hyg. 2018;112(6):267-271.',
      'Heerspink HJL, Stefánsson BV, et al. Dapagliflozin in Patients with Chronic Kidney Disease (DAPA-CKD). N Engl J Med. 2020;383(15):1436-1446.',
      'EMPA-KIDNEY Collaborative Group. Empagliflozin in Patients with Chronic Kidney Disease. N Engl J Med. 2023;388(2):117-127.',
      'Brenner BM, Cooper ME, et al. Effects of losartan on renal and cardiovascular outcomes in patients with type 2 diabetes and nephropathy (RENAAL). N Engl J Med. 2001;345(12):861-869.',
      'Lewis EJ, Hunsicker LG, et al. Renoprotective effect of the angiotensin-receptor antagonist irbesartan in patients with nephropathy due to type 2 diabetes (IDNT). N Engl J Med. 2001;345(12):851-860.',
      'Naicker S. End-stage renal disease in sub-Saharan Africa. Kidney Int Suppl. 2013;3(2):161-163.',
      'Arogundade FA, Barsoum RS. CKD prevention in Sub-Saharan Africa: a call for governmental, nongovernmental, and community support. Am J Kidney Dis. 2008;51(3):515-523.',
      'Osafo C, Raji YR, et al. Human immunodeficiency virus-associated nephropathy in Africa: a systematic review. Kidney Int. 2023;103(2):303-314.',
      'Kidney Disease: Improving Global Outcomes (KDIGO) Anemia Work Group. KDIGO Clinical Practice Guideline for Anemia in Chronic Kidney Disease. Kidney Int Suppl. 2012;2(4):279-335.',
      'Kidney Disease: Improving Global Outcomes (KDIGO) CKD-MBD Work Group. KDIGO 2017 Clinical Practice Guideline Update for the Diagnosis, Evaluation, Prevention, and Treatment of CKD-MBD. Kidney Int Suppl. 2017;7(1):1-59.',
      'SPRINT Research Group. A Randomized Trial of Intensive versus Standard Blood-Pressure Control. N Engl J Med. 2015;373(22):2103-2116.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 70. HIV/AIDS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '70',
    name: 'HIV/AIDS',
    slug: 'hiv-aids',
    specialty: 'Infectious Disease',
    bodySystem: 'Systemic',
    icdCode: 'B20',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Human Immunodeficiency Virus (HIV) infection is a chronic viral infection that attacks the immune system, specifically CD4+ T lymphocytes. Without treatment, HIV infection progresses to Acquired Immunodeficiency Syndrome (AIDS), characterized by severe immunosuppression and susceptibility to opportunistic infections and malignancies. Globally, approximately 39 million people are living with HIV, with sub-Saharan Africa bearing the greatest burden (over 25 million people, representing two-thirds of the global total).

HIV is transmitted through sexual contact (most common), parenteral exposure (blood products, needle sharing, needlestick injuries), and vertical transmission (mother-to-child during pregnancy, delivery, or breastfeeding). The virus targets CD4+ cells, leading to progressive immune dysfunction. The natural history progresses through acute HIV infection (acute retroviral syndrome), clinical latency (asymptomatic period of variable duration), and AIDS (CD4 <200 cells/μL or AIDS-defining illness).

Sub-Saharan Africa remains the epicenter of the HIV epidemic. Eastern and Southern Africa account for over 50% of all people living with HIV globally. Key populations at higher risk include sex workers, men who have sex with men (MSM), people who inject drugs, and transgender individuals. The region has made remarkable progress in scaling up antiretroviral therapy (ART), with over 75% of people living with HIV now on treatment in many countries. UNAIDS 90-90-90 targets (90% diagnosed, 90% on ART, 90% virally suppressed) have driven this progress, with new 95-95-95 targets for 2025.

Management centers on lifelong antiretroviral therapy (ART), which suppresses viral replication, preserves immune function, prevents transmission (U=U: Undetectable = Untransmittable), and dramatically improves survival. WHO recommends "Treat All" - initiating ART regardless of CD4 count. Modern first-line regimens are highly effective, well-tolerated, and increasingly available as single-tablet regimens. Prevention strategies include condom use, voluntary medical male circumcision, pre-exposure prophylaxis (PrEP), and prevention of mother-to-child transmission (PMTCT).

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Start ART in ALL patients with HIV regardless of CD4 count ("Treat All" strategy) - earlier treatment improves outcomes

🔴 CRITICAL POINT 2 - Screen ALL HIV patients for tuberculosis at every visit - TB is the leading cause of death in HIV; start TB preventive therapy if eligible

🔴 CRITICAL POINT 3 - Check creatinine before starting TDF-containing regimens - avoid TDF if CrCl <50 mL/min; use TAF or alternative

🔴 CRITICAL POINT 4 - Cotrimoxazole prophylaxis for ALL patients with CD4 <350 (or WHO stage 3/4) - prevents PCP, toxoplasmosis, bacterial infections

🟠 CRITICAL POINT 5 - Monitor viral load at 6 months, then annually if suppressed - viral suppression (<1000 copies/mL) is the goal

🟠 CRITICAL POINT 6 - Adherence is critical - >95% adherence needed for viral suppression; counsel extensively; use fixed-dose combinations

🟠 CRITICAL POINT 7 - Be alert for Immune Reconstitution Inflammatory Syndrome (IRIS) in first 3 months of ART, especially with low CD4

🟡 CRITICAL POINT 8 - Screen for hepatitis B before starting ART - TDF/TAF+3TC/FTC have anti-HBV activity; stopping may cause HBV flare

🟡 CRITICAL POINT 9 - Cervical cancer screening for all HIV-positive women - annual screening recommended due to increased HPV-related cancer risk`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• 39 million people living with HIV globally (2022)
• 1.3 million new HIV infections annually
• 630,000 AIDS-related deaths annually
• 29.8 million people on antiretroviral therapy
• 86% of people living with HIV know their status
• HIV prevalence: 0.7% global adult prevalence
• Declining incidence but still significant new infections

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa: 25.6 million people living with HIV (65% of global total)
• Eastern and Southern Africa: Highest burden globally
• South Africa: 7.8 million (largest epidemic)
• Nigeria: 1.9 million (second largest in Africa)
• Mozambique, Tanzania, Uganda, Zimbabwe: >1 million each
• Adult prevalence: 6.1% in Eastern/Southern Africa; 1.2% in West/Central Africa
• Women disproportionately affected (63% of new infections in sub-Saharan Africa)
• Adolescent girls and young women: 4,000 new infections weekly in sub-Saharan Africa
• Key populations: Higher prevalence in sex workers, MSM, PWID
• Mother-to-child transmission: Significant progress but still 130,000 new pediatric infections/year

TRANSMISSION:
• Sexual (vaginal, anal): Most common globally and in Africa
• Parenteral: Needlestick, blood transfusion, injection drug use
• Vertical: Mother-to-child (pregnancy, delivery, breastfeeding)
• Risk per exposure: Receptive anal (1.4%), Needle sharing (0.6%), Receptive vaginal (0.08%)

NATURAL HISTORY (Untreated):
• Acute HIV infection: 2-4 weeks post-exposure
• Clinical latency: Median 8-10 years (highly variable)
• AIDS: CD4 <200 cells/μL or AIDS-defining illness
• Survival after AIDS: Median 1-2 years without ART`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ACUTE HIV INFECTION (Acute Retroviral Syndrome):
• Occurs 2-4 weeks after exposure in 50-90% of patients
• Often misdiagnosed as viral illness or mononucleosis
• Fever (most common - 80-90%)
• Fatigue and malaise
• Pharyngitis
• Lymphadenopathy (generalized)
• Rash (maculopapular, trunk and face)
• Myalgia, arthralgia
• Headache
• Oral/genital ulcers
• Nausea, vomiting, diarrhea

CLINICAL LATENCY (Asymptomatic Stage):
• Few or no symptoms
• May have persistent generalized lymphadenopathy
• Duration: Highly variable (median 8-10 years untreated)
• Gradual decline in CD4 count

WHO CLINICAL STAGING:

Stage 1 (Asymptomatic):
• Asymptomatic
• Persistent generalized lymphadenopathy

Stage 2 (Mild):
• Moderate unexplained weight loss (<10%)
• Recurrent respiratory infections
• Herpes zoster
• Angular cheilitis, oral ulcerations
• Seborrheic dermatitis
• Fungal nail infections

Stage 3 (Advanced):
• Severe weight loss (>10%)
• Chronic diarrhea >1 month
• Persistent fever >1 month
• Oral candidiasis
• Oral hairy leukoplakia
• Pulmonary TB
• Severe bacterial infections

Stage 4 (Severe/AIDS):
• HIV wasting syndrome
• Pneumocystis jirovecii pneumonia (PCP)
• Recurrent severe bacterial pneumonia
• Chronic herpes simplex >1 month
• Esophageal candidiasis
• Extrapulmonary TB
• Kaposi sarcoma
• Cryptococcal meningitis
• Toxoplasmosis
• CMV disease
• HIV encephalopathy
• Non-Hodgkin lymphoma
• Invasive cervical carcinoma`,

    diagnosis: `DIAGNOSTIC APPROACH:

HIV TESTING ALGORITHM:

Screening Test:
• Rapid HIV antibody test (most common in Africa)
• 4th generation Ag/Ab test (if available) - detects earlier

Confirmatory Test:
• Different rapid test on same sample
• OR ELISA on new sample
• OR viral load/DNA PCR

WHO Testing Strategy:
• Reactive Test 1 + Reactive Test 2 (different assay) = HIV positive
• Reactive Test 1 + Non-reactive Test 2 = Retest with Test 3
• Non-reactive Test 1 = HIV negative (if no recent exposure)

Window Period:
• Antibody tests: 3-12 weeks post-exposure
• 4th generation Ag/Ab: 2-4 weeks (detects p24 antigen)
• NAT/PCR: 10-14 days

Special Situations:
• Infants: Use HIV DNA PCR (maternal antibodies persist to 18 months)
• Acute HIV: May be antibody negative; use RNA or p24 antigen

BASELINE ASSESSMENT:

Clinical:
• WHO clinical staging
• Symptom screen for TB (cough, fever, weight loss, night sweats)
• Pregnancy test (women of childbearing age)

Laboratory (Baseline):
• CD4 count (determines OI prophylaxis need)
• Viral load (baseline; may be deferred if not available)
• Full blood count
• Renal function (creatinine) - before TDF
• Liver function tests
• Hepatitis B surface antigen
• Hepatitis C antibody (if risk factors)
• Syphilis serology (RPR/VDRL)
• Pregnancy test

MONITORING ON ART:
• Viral load: 6 months after ART initiation, then annually if suppressed
• CD4 count: Less frequent if virally suppressed
• Clinical assessment: Every 3-6 months
• Renal function: 6-monthly if on TDF`,

    differentialDiagnosis: [
      'Acute viral syndrome (EBV, CMV, influenza) - Monospot, viral serology; HIV test at presentation and 4-6 weeks',
      'Tuberculosis - Can occur with or without HIV; TB testing essential; HIV test all TB patients',
      'Syphilis - May coexist; ulcerative STIs increase HIV risk; RPR/VDRL and HIV testing',
      'Lymphoma - May be HIV-associated or unrelated; HIV test, staging workup',
      'Other immunodeficiency - Primary immunodeficiency, hematological malignancy; HIV test',
      'Drug reaction - Rash, fever; may mimic acute HIV; temporal relationship to drugs',
      'Autoimmune disease - SLE, RA; may cause lymphadenopathy; autoantibodies, HIV test',
      'Hepatitis B or C - May coexist with HIV; hepatitis serology, HIV test',
      'Malignancy - Various cancers can cause constitutional symptoms',
      'Parasitic infections - Malaria, leishmaniasis may cause fever, lymphadenopathy',
      'Endocarditis - Fever, weight loss; echocardiography, blood cultures',
      'Chronic fatigue syndrome - Diagnosis of exclusion; HIV test to exclude'
    ],

    investigations: [
      {
        name: 'HIV Rapid Antibody Test',
        indication: '🔴 ESSENTIAL - First-line screening; result in 15-20 minutes; confirm positive with second test',
        availability: 'BASIC'
      },
      {
        name: 'HIV ELISA (Enzyme Immunoassay)',
        indication: '🟠 CONFIRMATORY - Laboratory-based; 4th generation detects p24 antigen and antibodies',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Viral Load (RNA PCR)',
        indication: '🔴 ESSENTIAL FOR MONITORING - Baseline, 6 months, then annually; target <1000 copies/mL',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CD4 Count',
        indication: '🔴 ESSENTIAL - Baseline to guide OI prophylaxis; determines AIDS status',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV DNA PCR (Infant Diagnosis)',
        indication: '🔴 ESSENTIAL FOR INFANTS - Early infant diagnosis at 6 weeks; maternal antibodies persist to 18 months',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Creatinine and eGFR',
        indication: '🔴 ESSENTIAL - Before TDF; avoid if CrCl <50; monitor 6-monthly on TDF-containing regimens',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Surface Antigen (HBsAg)',
        indication: '🔴 ESSENTIAL - Before ART; TDF/TAF+3TC/FTC treat HBV; stopping may cause HBV flare',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🔴 ESSENTIAL - Baseline; anemia, neutropenia common; monitor on AZT',
        availability: 'BASIC'
      },
      {
        name: 'Cryptococcal Antigen (CrAg)',
        indication: '🔴 ESSENTIAL IF CD4 <100 - Screen before ART; if positive, rule out meningitis',
        availability: 'BASIC'
      },
      {
        name: 'TB Symptom Screen',
        indication: '🔴 ESSENTIAL - At EVERY visit; any symptom = investigate for TB',
        availability: 'BASIC'
      },
      {
        name: 'GeneXpert MTB/RIF',
        indication: '🟠 IMPORTANT - If TB suspected; detects TB and rifampicin resistance',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Syphilis Serology (RPR/VDRL)',
        indication: '🟠 IMPORTANT - Screen all HIV patients; treat if positive',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT - Baseline if hepatitis risk; monitor on NVP and hepatotoxic drugs',
        availability: 'BASIC'
      },
      {
        name: 'Pregnancy Test',
        indication: '🔴 ESSENTIAL - All women of childbearing age before ART',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM HIV STATUS: Two positive rapid tests (different assays) or confirmatory test',
        '⚡ ASSESS CLINICAL STAGE: WHO staging determines prophylaxis needs',
        '⚡ SCREEN FOR TB: Symptom screen at every visit; investigate if any positive symptom',
        '⚡ SCREEN FOR CRYPTOCOCCUS: CrAg if CD4 <100; LP if symptomatic or CrAg positive',
        '⚡ BASELINE LABS: CD4, creatinine (before TDF), HBsAg, FBC; viral load if available',
        '⚡ START COTRIMOXAZOLE: If CD4 <350 or WHO stage 3/4',
        '⚡ SAME-DAY ART: Recommended if no TB symptoms and no contraindications',
        '⚡ ADHERENCE COUNSELING: Critical before and during ART; >95% adherence needed',
        '⚡ PARTNER NOTIFICATION: Encourage disclosure and partner testing',
        '⚡ LINK TO SUPPORT SERVICES: Peer support, psychosocial support'
      ],
      
      pharmacological: [
        {
          drug: 'TLD (Tenofovir DF + Lamivudine + Dolutegravir)',
          indication: '🔴 FIRST-LINE ART - WHO-recommended preferred regimen; once daily; high barrier to resistance',
          dose: 'Fixed-dose combination: TDF 300mg + 3TC 300mg + DTG 50mg. One tablet once daily.',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['CrCl <50 mL/min (avoid TDF)', 'Concurrent rifampicin (increase DTG to twice daily)'],
          sideEffects: ['GI upset (transient)', 'Insomnia, headache (DTG)', 'Weight gain', 'Renal impairment (TDF)'],
          alternatives: ['TAF-based regimen if renal impairment', 'EFV-based if DTG contraindicated']
        },
        {
          drug: 'TLE (Tenofovir DF + Lamivudine + Efavirenz)',
          indication: '🟠 ALTERNATIVE FIRST-LINE - Use if DTG not available or contraindicated',
          dose: 'TDF 300mg + 3TC 300mg + EFV 600mg. One tablet once daily at bedtime.',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['Psychiatric history (relative)', 'First trimester pregnancy (historical concern)'],
          sideEffects: ['CNS effects (vivid dreams, dizziness)', 'Rash', 'Hepatotoxicity (rare)'],
          alternatives: ['TLD (preferred)']
        },
        {
          drug: 'Atazanavir/ritonavir (ATV/r)',
          indication: '🟠 SECOND-LINE PI - Boosted PI for treatment failure; once daily',
          dose: 'ATV 300mg + RTV 100mg PO once daily with food.',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['Concurrent rifampicin', 'Severe hepatic impairment'],
          sideEffects: ['Jaundice (hyperbilirubinemia)', 'GI upset', 'Nephrolithiasis'],
          alternatives: ['Lopinavir/ritonavir', 'Darunavir/ritonavir']
        },
        {
          drug: 'Lopinavir/ritonavir (LPV/r)',
          indication: '🟠 SECOND-LINE PI - Heat-stable formulation available; useful for children',
          dose: 'LPV 400mg/RTV 100mg PO twice daily with food.',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Diarrhea (common)', 'Nausea', 'Lipid abnormalities'],
          alternatives: ['Atazanavir/r', 'Darunavir/r']
        },
        {
          drug: 'Zidovudine (AZT)',
          indication: '🟡 ALTERNATIVE NRTI - Use if TDF contraindicated; also for PMTCT',
          dose: '300mg PO twice daily.',
          route: 'PO',
          duration: 'Lifelong as part of ART',
          contraindications: ['Severe anemia (Hb <7)', 'Neutropenia (<750)'],
          sideEffects: ['Anemia, neutropenia (monitor FBC)', 'Nausea', 'Lipoatrophy (long-term)'],
          alternatives: ['TDF', 'TAF', 'Abacavir']
        },
        {
          drug: 'Cotrimoxazole (TMP-SMX) Prophylaxis',
          indication: '🔴 ESSENTIAL OI PROPHYLAXIS - For all with CD4 <350 or WHO stage 3/4',
          dose: 'Adults: 960mg (DS tablet) PO once daily.',
          route: 'PO',
          duration: 'Continue until CD4 >350 for 6+ months on ART',
          contraindications: ['Severe sulfonamide allergy'],
          sideEffects: ['Rash (may be severe)', 'GI upset', 'Hepatotoxicity', 'Bone marrow suppression'],
          alternatives: ['Dapsone if sulfa allergy']
        },
        {
          drug: 'Fluconazole (Cryptococcal Prevention)',
          indication: '🔴 ESSENTIAL IF CrAg POSITIVE - Pre-emptive therapy if CrAg+ without meningitis',
          dose: 'CrAg+ no meningitis: 800mg daily x2 weeks, then 400mg daily x8 weeks, then 200mg daily.',
          route: 'PO',
          duration: 'Secondary prophylaxis until immune reconstitution',
          contraindications: ['Concurrent QT-prolonging drugs'],
          sideEffects: ['Hepatotoxicity', 'QT prolongation', 'GI upset'],
          alternatives: ['Amphotericin + flucytosine for meningitis']
        },
        {
          drug: 'Isoniazid Preventive Therapy (IPT)',
          indication: '🟠 TB PREVENTION - For all HIV patients without active TB',
          dose: '300mg PO once daily (with pyridoxine 25mg) for 6-36 months.',
          route: 'PO',
          duration: '6 months minimum; 36 months in high TB burden settings',
          contraindications: ['Active TB', 'Active hepatitis', 'INH allergy'],
          sideEffects: ['Hepatotoxicity', 'Peripheral neuropathy (give pyridoxine)'],
          alternatives: ['3HP (rifapentine + isoniazid weekly)']
        },
        {
          drug: 'PrEP (TDF/FTC or TDF/3TC)',
          indication: '🟠 HIV PREVENTION - For HIV-negative individuals at high risk',
          dose: 'TDF 300mg + FTC 200mg (or 3TC 300mg) PO once daily.',
          route: 'PO',
          duration: 'While at risk; with regular HIV testing',
          contraindications: ['HIV-positive status', 'CrCl <60 mL/min'],
          sideEffects: ['GI upset (transient)', 'Renal impairment (monitor)'],
          alternatives: ['CAB-LA (injectable) where available']
        },
        {
          drug: 'Post-Exposure Prophylaxis (PEP)',
          indication: '🔴 AFTER HIV EXPOSURE - Start within 72 hours of exposure',
          dose: 'TDF/3TC + DTG (or LPV/r) for 28 days.',
          route: 'PO',
          duration: '28 days',
          contraindications: ['Already HIV-positive'],
          sideEffects: ['GI upset', 'Headache'],
          alternatives: ['TDF/FTC + DTG']
        }
      ],
      
      nonPharmacological: [
        'ADHERENCE SUPPORT: Peer supporters, treatment buddies, reminder systems, pill boxes',
        'DISCLOSURE COUNSELING: Support gradual disclosure to family; partner notification',
        'NUTRITIONAL SUPPORT: Adequate nutrition essential; therapeutic feeding if malnourished',
        'POSITIVE LIVING: Support groups, mental health services',
        'HARM REDUCTION: For PWID - needle exchange, opioid substitution therapy',
        'SEXUAL AND REPRODUCTIVE HEALTH: Condom provision, family planning',
        'MENTAL HEALTH: Depression screening; high prevalence; affects adherence',
        'HOME-BASED CARE: For advanced disease; palliative care when appropriate',
        'INCOME GENERATION: Economic empowerment reduces vulnerability'
      ],
      
      surgical: [
        'VOLUNTARY MEDICAL MALE CIRCUMCISION: 60% reduction in HIV transmission; for HIV-negative men',
        'KAPOSI SARCOMA: May require chemotherapy for severe cases',
        'CERVICAL CANCER TREATMENT: Higher rates in HIV; regular screening and treatment',
        'ABSCESS DRAINAGE: For skin/soft tissue infections'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Suspected cryptococcal meningitis (headache, confusion, CD4 <100)',
        '🔴 IMMEDIATE REFERRAL: Severe OI requiring inpatient management (PCP, TB meningitis)',
        '🔴 IMMEDIATE REFERRAL: Severe IRIS with clinical deterioration',
        '🔴 IMMEDIATE REFERRAL: Severe drug reaction (SJS/TEN)',
        '🟠 URGENT REFERRAL: Virological failure (VL >1000 despite adherence support)',
        '🟠 URGENT REFERRAL: Complex drug interactions or comorbidities',
        '🟠 URGENT REFERRAL: Kaposi sarcoma or AIDS-related malignancy',
        '🟡 ROUTINE REFERRAL: Hepatitis B/C coinfection for specialized management',
        '🟡 ROUTINE REFERRAL: Pregnancy - link to PMTCT services'
      ],
      
      monitoring: [
        'VIRAL LOAD: At 6 months, then annually if suppressed; target <1000 copies/mL',
        'CD4 COUNT: Baseline and periodically; can stop if VL suppressed and stable',
        'TB SYMPTOM SCREEN: At every visit - cough, fever, weight loss, night sweats',
        'RENAL FUNCTION: Baseline and 6-monthly if on TDF',
        'ADHERENCE ASSESSMENT: Every visit; pill counts, self-report',
        'CERVICAL CANCER SCREENING: Annual VIA or cytology for HIV-positive women',
        'WEIGHT: Monitor at every visit; weight loss may indicate OI or failure'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (First 6 Months):
• Month 1: Adherence check, side effects, TB screen
• Month 2: Adherence, side effects, early IRIS detection
• Month 3: Clinical assessment, adherence, renal function if on TDF
• Month 6: VIRAL LOAD (most important), CD4, clinical assessment

STABLE PHASE (After 6 Months with VL <1000):
• Every 3-6 months: Clinical visit, adherence, TB screen
• Annually: Viral load, renal function (if on TDF), cervical screening
• Differentiated service delivery: Multi-month dispensing for stable patients

TREATMENT FAILURE MANAGEMENT:

Virological Failure: VL >1000 copies/mL on two occasions

Step 1: Assess Adherence
• Intensive adherence counseling
• Identify and address barriers
• Repeat VL after 3 months

Step 2: If VL Still >1000
• Consider resistance (if testing available)
• Switch to second-line regimen

Second-Line Regimens:
• If failing TLD: Switch to AZT + 3TC + ATV/r or LPV/r
• If failing TLE: Switch to AZT + 3TC + ATV/r or LPV/r

SPECIAL POPULATIONS:

Pregnancy:
• TLD now recommended
• Start ART immediately regardless of CD4
• Infant prophylaxis based on maternal viral load

TB Coinfection:
• Start TB treatment first
• Start ART within 2-8 weeks
• Increase DTG to twice daily with rifampicin`
    },

    redFlags: [
      '🔴 CRITICAL: Cryptococcal meningitis (headache, confusion, neck stiffness, CD4 <100) - LP urgently',
      '🔴 CRITICAL: PCP symptoms (progressive dyspnea, dry cough, hypoxia) - start treatment empirically',
      '🔴 CRITICAL: Severe IRIS (paradoxical worsening after ART) - may need steroids, continue ART',
      '🔴 CRITICAL: Severe drug reaction (SJS/TEN, hepatitis) - stop offending drug',
      '🔴 CRITICAL: Severe anemia (Hb <7) on AZT - stop AZT, transfuse, switch regimen',
      '🟠 URGENT: New neurological symptoms - CNS OI or malignancy; imaging needed',
      '🟠 URGENT: Persistent fever without source - investigate for TB, MAC, other OIs',
      '🟠 URGENT: Rapid weight loss - investigate for TB, malignancy',
      '🟠 URGENT: Viral load >1000 despite adherence - resistance likely',
      '🟡 CAUTION: Chronic diarrhea - Cryptosporidium, MAC, other OIs',
      '🟡 CAUTION: Visual symptoms with low CD4 - CMV retinitis; urgent ophthalmology',
      '🟡 CAUTION: Severe oral thrush on ART - may indicate treatment failure'
    ],

    complications: [
      'Opportunistic infections - PCP, cryptococcosis, toxoplasmosis, TB, CMV, MAC',
      'AIDS-related malignancies - Kaposi sarcoma, non-Hodgkin lymphoma, cervical cancer',
      'Immune reconstitution inflammatory syndrome (IRIS) - Paradoxical worsening after ART',
      'ART toxicity - Renal (TDF), CNS (EFV), metabolic (PIs), bone marrow (AZT)',
      'Cardiovascular disease - Increased risk from HIV and some ARVs',
      'Chronic kidney disease - HIV-associated nephropathy, TDF toxicity',
      'Hepatotoxicity - Drug-induced, viral hepatitis coinfection',
      'Metabolic syndrome - Lipodystrophy, dyslipidemia, insulin resistance',
      'Neurocognitive disorders - HIV-associated dementia',
      'Treatment failure - Virological, immunological, clinical',
      'Drug resistance - From poor adherence or transmitted resistance',
      'Depression - High prevalence; affects adherence',
      'Mother-to-child transmission - Without PMTCT: 25-40%; with PMTCT: <2%'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

WITH EFFECTIVE ART:
• Near-normal life expectancy if started early
• Viral suppression achieved in >90% with modern regimens
• CD4 count recovers
• Risk of OIs dramatically reduced
• U=U: Undetectable = Untransmittable

WITHOUT ART:
• Median survival after AIDS: 1-2 years
• Progressive immune decline
• Death from OIs or malignancy

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis and treatment
• High baseline CD4 at ART initiation
• Excellent adherence (>95%)
• No severe comorbidities
• Good social support

Worse Outcomes:
• Late diagnosis (CD4 <200 or AIDS)
• Poor adherence
• Drug-resistant virus
• Hepatitis B/C coinfection
• Substance use
• Limited healthcare access`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

BEHAVIORAL PREVENTION:
• Condom use (male and female)
• Reduction of sexual partners
• Delay of sexual debut

BIOMEDICAL PREVENTION:

Pre-Exposure Prophylaxis (PrEP):
• Daily TDF/FTC for HIV-negative at risk
• >90% effective if taken consistently
• For: Serodiscordant couples, sex workers, MSM

Post-Exposure Prophylaxis (PEP):
• 28 days of ART after exposure
• Start within 72 hours
• Regimen: TDF/3TC + DTG

Treatment as Prevention (TasP):
• U=U: Zero transmission risk when undetectable
• Most effective prevention strategy

Voluntary Medical Male Circumcision (VMMC):
• 60% reduction in female-to-male transmission
• One-time intervention with lifelong benefit

Prevention of Mother-to-Child Transmission (PMTCT):
• Universal ART for pregnant women
• Infant prophylaxis
• Reduces transmission to <2%

Harm Reduction (PWID):
• Needle and syringe programs
• Opioid substitution therapy`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS HIV?
HIV is a virus that attacks your immune system. Without treatment, it leads to AIDS. The good news is that with treatment, people with HIV can live long, healthy lives and cannot pass the virus to their partners (U=U).

HOW IS HIV SPREAD?
• Unprotected sex
• Sharing needles
• Mother to baby (without treatment)

HIV IS NOT SPREAD BY:
• Hugging, shaking hands
• Sharing food or utensils
• Mosquito bites
• Toilets or swimming pools

TAKING YOUR MEDICATIONS:
• Take your pills EVERY DAY at the same time
• Do not skip doses - even one missed dose matters
• Use reminders: phone alarms, pill boxes
• Keep enough pills before supply runs out

WHY ADHERENCE MATTERS:
• >95% adherence needed (miss less than 1-2 doses/month)
• Poor adherence leads to treatment failure
• The virus can become resistant

LIVING WELL WITH HIV:
• Eat a balanced diet
• Exercise regularly
• Get enough sleep
• Avoid smoking and excessive alcohol
• Attend all clinic appointments
• Get your viral load checked regularly

PREVENTING TRANSMISSION:
• Take your medications - undetectable = untransmittable
• Use condoms for extra protection
• Your partner may benefit from PrEP

WHEN TO SEEK HELP:
• Fever that does not go away
• Severe headache or confusion
• Difficulty breathing
• Significant weight loss
• Any new concerning symptoms

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

HIV is manageable with proper treatment. Take your medications as prescribed and attend all appointments. With ART, you can live a long, healthy life.`,

    references: [
      'World Health Organization. Consolidated guidelines on HIV prevention, testing, treatment, service delivery and monitoring. WHO; 2021.',
      'UNAIDS. Global AIDS Update 2023: The Path that Ends AIDS. Geneva: UNAIDS; 2023.',
      'Panel on Antiretroviral Guidelines for Adults and Adolescents. Guidelines for the Use of Antiretroviral Agents in Adults and Adolescents with HIV. DHHS; 2023.',
      'Cohen MS, et al. Antiretroviral Therapy for the Prevention of HIV-1 Transmission. N Engl J Med. 2016;375(9):830-839.',
      'Rodger AJ, et al. Risk of HIV transmission through condomless sex (PARTNER). Lancet. 2019;393:2428-2438.',
      'INSIGHT START Study Group. Initiation of Antiretroviral Therapy in Early Asymptomatic HIV Infection. N Engl J Med. 2015;373(9):795-807.',
      'Baeten JM, et al. Antiretroviral prophylaxis for HIV prevention (Partners PrEP). N Engl J Med. 2012;367(5):399-410.',
      'Gray RH, et al. Male circumcision for HIV prevention (Rakai trial). Lancet. 2007;369:657-666.',
      'Siedner MJ, et al. Efficacy of dolutegravir-based ART in South Africa (ADVANCE). Lancet HIV. 2020;7(7):e460-e467.',
      'Ford N, et al. Benefits and risks of rapid initiation of antiretroviral therapy. AIDS. 2018;32(1):17-23.',
      'Lawn SD, et al. Early mortality among adults accessing ART in sub-Saharan Africa. AIDS. 2008;22(15):1897-1908.',
      'WHO. Updated recommendations on HIV prevention, infant diagnosis, antiretroviral initiation. WHO; 2021.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 71. CHOLERA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '71',
    name: 'Cholera',
    slug: 'cholera',
    specialty: 'Infectious Disease',
    bodySystem: 'Gastrointestinal',
    icdCode: 'A00',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Cholera is an acute diarrheal infection caused by ingestion of food or water contaminated with the bacterium Vibrio cholerae. It causes profuse watery diarrhea ("rice-water" stools) that can rapidly lead to severe dehydration and death within hours if untreated. Cholera remains a global threat, with an estimated 1.3-4 million cases and 21,000-143,000 deaths annually. The disease is a key indicator of inequity and lack of social development, as it primarily affects populations without access to safe water and adequate sanitation.

Vibrio cholerae produces cholera toxin, which activates adenylate cyclase in intestinal epithelial cells, leading to massive secretion of chloride, bicarbonate, and water into the intestinal lumen. This results in the characteristic voluminous watery diarrhea - patients can lose 10-20 liters of fluid per day. The stool is isotonic with plasma but has higher bicarbonate and potassium concentrations, leading to metabolic acidosis and hypokalemia. Death results from hypovolemic shock and severe electrolyte disturbances.

Africa bears a disproportionate burden of cholera. The continent accounts for approximately 40% of global cases, with major outbreaks occurring in sub-Saharan Africa, particularly in areas affected by conflict, displacement, and inadequate water/sanitation infrastructure. Countries with recent large outbreaks include Democratic Republic of Congo, Nigeria, Mozambique, Somalia, Ethiopia, and Zimbabwe. Cholera outbreaks often follow natural disasters, conflict, and population displacement. Case fatality rates in Africa often exceed 2-3% (compared to <1% with proper treatment), reflecting delayed access to care.

The cornerstone of cholera treatment is rapid rehydration - oral rehydration solution (ORS) for mild-moderate cases and intravenous fluids (Ringer's lactate) for severe dehydration. With prompt, appropriate rehydration, mortality can be reduced to <1%. Antibiotics shorten duration and reduce transmission but are secondary to rehydration. Zinc supplementation reduces duration and severity in children. Prevention requires access to safe water, sanitation, and hygiene (WASH), along with oral cholera vaccines in endemic/outbreak settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - REHYDRATION IS THE TREATMENT - Patients die from dehydration, not infection; start fluids IMMEDIATELY

🔴 CRITICAL POINT 2 - Severe dehydration: IV Ringer's Lactate 100mL/kg in first 3 hours (adults) or 6 hours (children); then reassess

🔴 CRITICAL POINT 3 - ORS can treat 80% of patients - Use for mild-moderate dehydration; continue even if patient is vomiting

🔴 CRITICAL POINT 4 - Replace ongoing losses: Measure stool output and replace mL for mL with ORS or IV fluids

🟠 CRITICAL POINT 5 - Antibiotics are ADJUNCT, not primary treatment - Shorten illness and reduce transmission; give after rehydration started

🟠 CRITICAL POINT 6 - Doxycycline single dose (300mg adults) is first-line antibiotic; azithromycin for pregnant women and children

🟠 CRITICAL POINT 7 - Zinc (20mg/day for children) reduces duration and severity; give for 10-14 days

🟡 CRITICAL POINT 8 - NOTIFY PUBLIC HEALTH IMMEDIATELY - Cholera is a notifiable disease; outbreak response requires rapid action

🟡 CRITICAL POINT 9 - Infection control: Isolate patients, safe disposal of excreta, chlorinate water, promote handwashing`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 1.3-4 million cases annually worldwide
• 21,000-143,000 deaths per year
• Endemic in approximately 50 countries
• 7 pandemics since 1817; current (7th) began 1961
• V. cholerae O1 El Tor biotype predominates
• V. cholerae O139 emerged in 1992 (Asia)
• Case fatality rate: <1% with adequate treatment; can exceed 50% untreated
• Major risk: Lack of safe water and sanitation

AFRICAN EPIDEMIOLOGY:
• ~40% of global cholera cases occur in Africa
• Sub-Saharan Africa most affected region
• Major outbreaks in DRC, Nigeria, Mozambique, Somalia, Ethiopia, Zimbabwe, Malawi
• Case fatality rates often 2-5% (reflects delayed care access)
• Outbreaks associated with: Rainy season, flooding, conflict, displacement, refugee camps
• Endemic areas: Lake regions, coastal areas, urban slums
• 2023-2024: Major resurgence across multiple African countries
• Children <5 years: Higher attack rates in endemic areas
• Limited access to oral cholera vaccine (OCV)

TRANSMISSION:
• Fecal-oral route
• Contaminated water (primary)
• Contaminated food (especially raw/undercooked seafood)
• Person-to-person (less common)
• Incubation period: 12 hours to 5 days (usually 2-3 days)
• Infectious dose: High (10^8 organisms needed; lower with achlorhydria or PPI use)

RISK FACTORS:
• Lack of access to safe drinking water
• Inadequate sanitation
• Poor hygiene practices
• Displacement and overcrowding
• Natural disasters (floods, earthquakes)
• Conflict and humanitarian emergencies
• Low gastric acidity (PPIs, H2 blockers, malnutrition)
• Blood group O (higher susceptibility and severity)
• Malnutrition
• Living in cholera-endemic area

SEASONALITY:
• Peaks during rainy season (contamination of water sources)
• May also peak during dry season (concentration of pathogens in diminished water sources)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLINICAL SPECTRUM:
• Asymptomatic infection: 75% of infected persons
• Mild illness: Indistinguishable from other diarrhea
• Moderate illness: Significant diarrhea with dehydration
• Severe ("cholera gravis"): Massive fluid loss, shock - 5-10% of symptomatic cases

CLASSIC PRESENTATION (Severe Cholera):

Onset:
• Abrupt onset of painless, watery diarrhea
• Often no prodrome
• Vomiting follows (effortless, not preceded by nausea)

Diarrhea Characteristics:
• "Rice-water" stools: Gray, cloudy, watery, with fishy odor
• Massive volumes: 500-1000 mL/hour possible
• 10-20 liters/day in severe cases
• No blood, no mucus, no tenesmus (differentiates from dysentery)

Dehydration Signs:
• Intense thirst
• Muscle cramps (from electrolyte loss)
• Weakness
• Sunken eyes
• Dry mouth and tongue
• Decreased skin turgor ("skin pinch" goes back slowly)
• Absent tears
• Rapid, weak pulse
• Low blood pressure
• Decreased or absent urine output
• Altered mental status (in severe cases)

Other Features:
• Low-grade or no fever (unlike other bacterial diarrheas)
• Abdominal discomfort (not severe pain)
• Borborygmi (loud bowel sounds)

COMPLICATIONS IF UNTREATED:
• Hypovolemic shock
• Severe metabolic acidosis
• Severe hypokalemia (muscle weakness, cardiac arrhythmias)
• Hypoglycemia (especially children)
• Acute kidney injury (prerenal)
• Seizures (especially children with hypoglycemia/electrolyte disturbance)
• Death (within hours if untreated)

PHYSICAL EXAMINATION:

Assess Dehydration (WHO Classification):

No Dehydration:
• Alert, drinks normally
• Eyes normal
• Skin pinch goes back immediately

Some Dehydration:
• Restless, irritable
• Drinks eagerly, thirsty
• Sunken eyes
• Skin pinch goes back slowly (<2 seconds)

Severe Dehydration:
• Lethargic or unconscious
• Unable to drink or drinks poorly
• Very sunken eyes
• Skin pinch goes back very slowly (>2 seconds)
• Absent radial pulse (shock)

Vital Signs:
• Tachycardia (compensatory)
• Hypotension (late sign in adults)
• Tachypnea (Kussmaul breathing if acidosis)
• Normal or low temperature`,

    diagnosis: `DIAGNOSTIC APPROACH:

CLINICAL DIAGNOSIS (Primary Method in Outbreaks):
Suspect cholera if:
• Acute watery diarrhea with or without vomiting
• Severe dehydration or shock
• AND patient is from cholera-endemic area OR
• There is known cholera outbreak in area OR
• Recent travel to affected area

WHO Case Definition:
• Endemic area: Severe dehydration or death from acute watery diarrhea in patient ≥5 years
• Non-endemic: Acute watery diarrhea with isolation of V. cholerae O1 or O139

LABORATORY CONFIRMATION:

Stool Culture:
• Gold standard for confirmation
• Collect stool sample BEFORE antibiotics if possible
• Transport media: Cary-Blair or alkaline peptone water
• TCBS agar: Yellow colonies (sucrose fermenter)
• Serotyping: O1 (Ogawa, Inaba) or O139

Rapid Diagnostic Tests (RDTs):
• Crystal VC, Cholkit: Detect V. cholerae O1 and O139
• Results in 15-20 minutes
• Useful in outbreak settings
• Sensitivity/specificity vary; confirm with culture

Dark-Field Microscopy:
• Direct stool examination
• Characteristic "shooting star" motility
• Immobilized by O1 antisera

PCR:
• More sensitive than culture
• Detects cholera toxin genes (ctxA, ctxB)
• Not widely available in resource-limited settings

LABORATORY FINDINGS:

Blood Tests:
• Hemoconcentration (high Hb, Hct due to dehydration)
• Elevated BUN/creatinine (prerenal azotemia)
• Metabolic acidosis (low bicarbonate)
• Hypokalemia (often severe)
• Hyponatremia or normal sodium
• Hypoglycemia (especially children)

Stool Analysis:
• Watery, no blood, no WBCs
• Positive culture for V. cholerae
• Positive RDT

DIFFERENTIAL DIAGNOSIS CONSIDERATIONS:
• If bloody diarrhea: Consider dysentery (Shigella, EIEC, Entamoeba)
• If high fever: Consider typhoid, invasive bacterial diarrhea
• If >5 days: Consider parasitic causes
• In infants: Consider rotavirus, other viral causes`,

    differentialDiagnosis: [
      'Acute gastroenteritis (viral) - Usually less severe; vomiting more prominent; self-limited',
      'Enterotoxigenic E. coli (ETEC) - Traveler diarrhea; similar mechanism; cannot distinguish clinically',
      'Shigella dysenteriae - Bloody/mucoid diarrhea, fever, tenesmus; WBCs in stool',
      'Rotavirus (children) - Common in children <5; usually less severe dehydration',
      'Norovirus - Vomiting prominent; outbreaks in institutions; usually self-limited',
      'Salmonella enteritis - Fever, bloody diarrhea possible; usually less severe dehydration',
      'Vibrio parahaemolyticus - Similar but associated with seafood; usually less severe',
      'Clostridium difficile - Antibiotic history; pseudomembranes on colonoscopy',
      'Cryptosporidiosis - Prolonged watery diarrhea; immunocompromised patients',
      'Giardiasis - Chronic diarrhea; less acute presentation',
      'Amoebic dysentery - Bloody diarrhea with mucus; liver abscess possible',
      'Malaria - Consider in endemic areas; can present with diarrhea; check malaria RDT'
    ],

    investigations: [
      {
        name: 'Clinical Assessment of Dehydration',
        indication: '🔴 ESSENTIAL - WHO dehydration classification guides treatment; assess immediately on arrival',
        availability: 'BASIC'
      },
      {
        name: 'Stool Culture',
        indication: '🟠 IMPORTANT - Confirms diagnosis; collect before antibiotics; TCBS agar; guides outbreak response',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Rapid Diagnostic Test (Crystal VC)',
        indication: '🟠 IMPORTANT - Quick confirmation in outbreak settings; result in 15-20 minutes',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Na, K, Cl, HCO3)',
        indication: '🟠 IMPORTANT - Hypokalemia, acidosis common; guides replacement if severe; not essential for initial treatment',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL FOR CHILDREN - Hypoglycemia common and dangerous; check in all children with severe dehydration',
        availability: 'BASIC'
      },
      {
        name: 'Blood Urea and Creatinine',
        indication: '🟡 IF AVAILABLE - Assess prerenal AKI; usually normalizes with rehydration',
        availability: 'BASIC'
      },
      {
        name: 'Full Blood Count',
        indication: '🟡 IF AVAILABLE - Hemoconcentration (elevated Hb/Hct); returns to normal with rehydration',
        availability: 'BASIC'
      },
      {
        name: 'Stool Microscopy',
        indication: '🟡 IF AVAILABLE - No RBCs or WBCs (differentiates from dysentery); dark-field shows motile vibrios',
        availability: 'BASIC'
      },
      {
        name: 'Malaria RDT',
        indication: '🟠 IMPORTANT IN ENDEMIC AREAS - Malaria can present with diarrhea; exclude co-infection',
        availability: 'BASIC'
      },
      {
        name: 'ABG/VBG',
        indication: '🟡 IF SEVERE - Confirms metabolic acidosis; guides bicarbonate therapy in severe cases',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS DEHYDRATION: Use WHO classification - None, Some, Severe; determines treatment plan',
        '⚡ WEIGH PATIENT: Essential for calculating fluid requirements; if scale unavailable, use age-based estimates',
        '⚡ START REHYDRATION IMMEDIATELY: Do not wait for laboratory confirmation',
        '⚡ SEVERE DEHYDRATION: IV Ringer Lactate 100mL/kg - Adults: 30mL/kg in first 30 min, then 70mL/kg in 2.5 hours',
        '⚡ SOME DEHYDRATION: ORS 75mL/kg over 4 hours (Plan B)',
        '⚡ CHECK BLOOD GLUCOSE: Especially in children; treat hypoglycemia urgently',
        '⚡ ISOLATE PATIENT: Infection control; separate cholera ward if possible',
        '⚡ START ANTIBIOTICS: After rehydration begun; reduces duration and transmission',
        '⚡ ZINC SUPPLEMENTATION: Children - 20mg/day (10mg if <6 months) for 10-14 days',
        '⚡ NOTIFY PUBLIC HEALTH: Cholera is notifiable; triggers outbreak response'
      ],
      
      pharmacological: [
        {
          drug: 'Oral Rehydration Solution (ORS)',
          indication: '🔴 PRIMARY TREATMENT - For all patients; can treat 80% of cases; continue even if vomiting',
          dose: 'Some dehydration (Plan B): 75mL/kg over 4 hours. Maintenance: Replace ongoing losses mL for mL plus 200-300mL after each loose stool.',
          route: 'PO (or NG tube if unable to drink)',
          duration: 'Until diarrhea stops and hydration maintained',
          contraindications: ['Shock (give IV first)', 'Unable to drink and NG tube not possible', 'Severe vomiting not responding to small frequent sips'],
          sideEffects: ['Vomiting (give small amounts frequently)', 'Abdominal distension'],
          alternatives: ['Home fluids (rice water, soup) if ORS unavailable; IV fluids if unable to take oral']
        },
        {
          drug: 'Ringer Lactate (Ringer Lactate Solution)',
          indication: '🔴 SEVERE DEHYDRATION - First-line IV fluid; corrects volume and acidosis; preferred over normal saline',
          dose: 'Severe dehydration (Plan C): 100mL/kg total. Adults: 30mL/kg in first 30 min, then 70mL/kg in 2.5 hours. Children <1yr: 30mL/kg in 1 hour, then 70mL/kg in 5 hours.',
          route: 'IV (or intraosseous in children if IV difficult)',
          duration: 'Until severe dehydration corrected; then switch to ORS',
          contraindications: ['None in emergency resuscitation'],
          sideEffects: ['Fluid overload if excessive'],
          alternatives: ['Normal saline (0.9% NaCl) if Ringer not available - but need to correct acidosis separately']
        },
        {
          drug: 'Doxycycline',
          indication: '🔴 FIRST-LINE ANTIBIOTIC (Adults) - Shortens illness, reduces stool output, decreases transmission',
          dose: 'Adults: 300mg single dose (or 100mg twice daily for 3 days). Not for children <8 years or pregnant women.',
          route: 'PO',
          duration: 'Single dose preferred; 3-day course alternative',
          contraindications: ['Pregnancy', 'Children <8 years (dental staining)', 'Allergy'],
          sideEffects: ['Nausea', 'Photosensitivity', 'Esophagitis (take with water)'],
          alternatives: ['Azithromycin', 'Ciprofloxacin']
        },
        {
          drug: 'Azithromycin',
          indication: '🔴 FIRST-LINE FOR CHILDREN AND PREGNANT WOMEN - Safe in pregnancy; effective single dose',
          dose: 'Adults: 1g single dose. Children: 20mg/kg single dose (max 1g).',
          route: 'PO',
          duration: 'Single dose',
          contraindications: ['Azithromycin allergy', 'Severe hepatic impairment'],
          sideEffects: ['GI upset', 'QT prolongation (rare)'],
          alternatives: ['Erythromycin (children)', 'Ciprofloxacin (adults, if azithromycin resistant)']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟠 ALTERNATIVE ANTIBIOTIC - If doxycycline/azithromycin resistant or unavailable',
          dose: 'Adults: 1g single dose (or 500mg twice daily for 3 days). Not routinely for children.',
          route: 'PO',
          duration: 'Single dose or 3 days',
          contraindications: ['Children (relative - joint toxicity)', 'Pregnancy', 'QT prolongation'],
          sideEffects: ['GI upset', 'Tendon damage (rare)', 'QT prolongation'],
          alternatives: ['Doxycycline', 'Azithromycin']
        },
        {
          drug: 'Erythromycin',
          indication: '🟡 ALTERNATIVE FOR CHILDREN - If azithromycin unavailable',
          dose: 'Children: 12.5mg/kg 4 times daily for 3 days (max 500mg per dose).',
          route: 'PO',
          duration: '3 days',
          contraindications: ['Macrolide allergy'],
          sideEffects: ['GI upset', 'QT prolongation'],
          alternatives: ['Azithromycin (preferred)']
        },
        {
          drug: 'Zinc Sulfate',
          indication: '🔴 ESSENTIAL FOR CHILDREN - Reduces duration and severity of diarrhea; reduces future diarrhea episodes',
          dose: 'Children ≥6 months: 20mg once daily for 10-14 days. Children <6 months: 10mg once daily for 10-14 days.',
          route: 'PO',
          duration: '10-14 days (complete course even after diarrhea stops)',
          contraindications: ['None'],
          sideEffects: ['Vomiting if taken on empty stomach (give with food)'],
          alternatives: ['Zinc gluconate at equivalent elemental zinc dose']
        },
        {
          drug: 'Potassium Chloride',
          indication: '🟠 IF HYPOKALEMIA - ORS contains potassium; additional may be needed if severe hypokalemia',
          dose: 'Adults: 20-40 mEq PO or IV (diluted) as guided by serum levels. Max IV rate: 10-20 mEq/hour.',
          route: 'PO (preferred) or IV (diluted, slowly)',
          duration: 'Until serum K+ normalized',
          contraindications: ['Hyperkalemia', 'Severe renal failure'],
          sideEffects: ['GI upset (oral)', 'Cardiac arrhythmias if IV too fast'],
          alternatives: ['Potassium-rich foods (bananas) once tolerating oral']
        },
        {
          drug: 'Sodium Bicarbonate',
          indication: '🟡 IF SEVERE ACIDOSIS - Usually corrects with Ringer Lactate; only if pH <7.1 or HCO3 <10',
          dose: 'Calculate deficit. Typically 1-2 mEq/kg IV slowly. Recheck blood gas.',
          route: 'IV',
          duration: 'As needed for severe acidosis',
          contraindications: ['Adequate response to RL'],
          sideEffects: ['Paradoxical CNS acidosis', 'Hypokalemia', 'Fluid overload'],
          alternatives: ['Adequate Ringer Lactate usually sufficient']
        },
        {
          drug: 'Dextrose 10%',
          indication: '🔴 HYPOGLYCEMIA (especially children) - Blood glucose <3 mmol/L or <54 mg/dL',
          dose: 'Children: 5mL/kg D10% IV bolus. Adults: 50mL D50% IV (or 100mL D25%). Recheck glucose.',
          route: 'IV',
          duration: 'Until glucose normalized; then ensure feeding',
          contraindications: ['Hyperglycemia'],
          sideEffects: ['Hyperglycemia if excessive'],
          alternatives: ['Sugar water or ORS with added sugar if IV unavailable and patient can swallow']
        }
      ],
      
      nonPharmacological: [
        'INFECTION CONTROL: Isolate cholera patients; dedicated toilet/latrine; safe disposal of excreta',
        'HAND HYGIENE: Strict handwashing with soap for all contacts; alcohol-based hand rub',
        'SAFE WATER: Chlorinate water (household chlorine or tablets); boil water if chlorine unavailable',
        'FOOD SAFETY: Cook food thoroughly; avoid raw foods; safe food handling',
        'BODY DISPOSAL: Safe burial practices; disinfect body and any contaminated materials',
        'CHOLERA COTS: Use for severe cases to measure stool output accurately; hole with bucket beneath',
        'BREASTFEEDING: Continue breastfeeding for infants; breast milk is safe and protective',
        'ORAL CHOLERA VACCINE: 2-dose killed whole-cell vaccine; for outbreak response and endemic areas',
        'WASH INTERVENTIONS: Improve water, sanitation, hygiene at community level',
        'HEALTH EDUCATION: Community awareness on prevention, recognition, and when to seek care'
      ],
      
      surgical: [
        'NO SURGICAL INTERVENTION for cholera itself',
        'INTRAOSSEOUS ACCESS: In children with difficult IV access; for rapid fluid resuscitation',
        'CENTRAL VENOUS ACCESS: Rarely needed; if peripheral IV difficult in severely volume-depleted adults'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Shock not responding to initial fluid bolus - may need advanced care',
        '🔴 IMMEDIATE REFERRAL: Unable to establish IV access - intraosseous or central access needed',
        '🔴 IMMEDIATE REFERRAL: Seizures (hypoglycemia, severe electrolyte disturbance)',
        '🔴 IMMEDIATE REFERRAL: Severe comorbidity (malnutrition, HIV, renal failure)',
        '🟠 URGENT REFERRAL: Pregnant woman with cholera - risk of fetal loss with severe dehydration',
        '🟠 URGENT REFERRAL: Infant <6 months - higher risk, needs close monitoring',
        '🟡 ROUTINE: Contact local/national public health for outbreak notification',
        '🟡 ROUTINE: Establish cholera treatment center if outbreak confirmed'
      ],
      
      monitoring: [
        'CLINICAL STATUS: Level of consciousness, thirst, vital signs - every 15-30 min in severe cases',
        'URINE OUTPUT: Aim for >0.5mL/kg/hour; indicates adequate rehydration',
        'STOOL OUTPUT: Measure in cholera bucket; replace mL for mL',
        'WEIGHT: Daily or twice daily in severe cases; guides fluid therapy',
        'HYDRATION STATUS: Reassess WHO dehydration classification frequently',
        'BLOOD GLUCOSE: Check in children, especially if drowsy or seizing',
        'ELECTROLYTES: If severe or not improving; check K+ especially',
        'INTAKE/OUTPUT CHART: Meticulous documentation essential'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DURING ACUTE ILLNESS:

Severe Dehydration:
• Reassess every 15-30 minutes during IV rehydration
• Check hydration status, vital signs, urine output
• Transition to ORS when able to drink and dehydration improving
• May need 24-48 hours of intensive monitoring

Some Dehydration:
• Reassess after 4 hours of ORS therapy
• If improved: Continue maintenance with ORS at home (Plan A)
• If same or worse: Repeat Plan B or move to Plan C

DISCHARGE CRITERIA:
• Taking ORS and/or food orally
• Urine output adequate
• No signs of dehydration
• Diarrhea decreasing
• Family able to continue ORS at home

DISCHARGE INSTRUCTIONS:
• Continue ORS for ongoing losses
• Complete zinc course (10-14 days for children)
• Safe water and food practices
• When to return: Unable to drink, blood in stool, not improving in 2 days

HOUSEHOLD CONTACTS:
• Chemoprophylaxis NOT routinely recommended
• Emphasis on hand hygiene, safe water, sanitation
• Watch for symptoms; seek care if diarrhea develops

POST-OUTBREAK:
• Immunity after infection: Several years for same serogroup
• Oral cholera vaccine for high-risk individuals if available
• Long-term WASH improvements essential to prevent recurrence

OUTBREAK RESPONSE CONSIDERATIONS:
• Establish cholera treatment centers/units
• Preposition supplies (ORS, RL, antibiotics)
• Community health worker training
• Active case finding
• WASH interventions
• Oral cholera vaccine campaign if appropriate
• Coordination with national/international partners`
    },

    redFlags: [
      '🔴 CRITICAL: Signs of shock (weak/absent pulse, cold extremities, altered consciousness) - aggressive IV resuscitation',
      '🔴 CRITICAL: Unable to drink or vomiting everything - IV fluids essential; consider NG tube for ORS',
      '🔴 CRITICAL: Seizures - check and correct hypoglycemia, electrolytes; may indicate severe metabolic disturbance',
      '🔴 CRITICAL: No urine output for >6 hours despite fluids - assess for AKI, volume status',
      '🔴 CRITICAL: Infant <6 months with severe dehydration - very high mortality; expert care if possible',
      '🟠 URGENT: Pregnant woman with cholera - risk of fetal loss; aggressive rehydration, safe antibiotics',
      '🟠 URGENT: Severely malnourished child - altered fluid/electrolyte handling; slower rehydration',
      '🟠 URGENT: Not improving after 4 hours of adequate ORS - reassess, consider IV fluids',
      '🟡 CAUTION: High stool output continuing >48 hours - reassess antibiotic choice, resistance',
      '🟡 CAUTION: Abdominal distension - may indicate ileus; slow oral intake, NG decompression if severe'
    ],

    complications: [
      'Hypovolemic shock - From massive fluid loss; leading cause of death; prevented with rehydration',
      'Acute kidney injury (prerenal) - From dehydration; usually reversible with fluids',
      'Severe metabolic acidosis - From bicarbonate loss in stool; treat with Ringer Lactate',
      'Severe hypokalemia - From potassium loss in stool; can cause muscle weakness, cardiac arrhythmias',
      'Hypoglycemia - Especially in children; can cause seizures, brain damage; check and correct',
      'Seizures - From hypoglycemia, hyponatremia, or severe metabolic disturbance',
      'Cardiac arrhythmias - From severe hypokalemia; can be fatal',
      'Aspiration pneumonia - From vomiting in altered mental state; position patient carefully',
      'Muscle cramps - From electrolyte disturbances; painful but not dangerous',
      'Post-infectious irritable bowel - Persistent GI symptoms after acute infection',
      'Reactive arthritis - Rare; joint pains weeks after infection',
      'Death - Preventable with rehydration; occurs within hours if untreated'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

WITH APPROPRIATE TREATMENT:
• Case fatality rate: <1% (can be <0.5% in well-equipped settings)
• Most patients recover completely within 3-5 days
• No long-term sequelae in most cases
• Excellent prognosis with prompt rehydration

WITHOUT TREATMENT:
• Case fatality rate: Up to 50-70%
• Death can occur within hours of symptom onset
• Death from hypovolemic shock

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early presentation and treatment
• Access to ORS and IV fluids
• Older children and adults
• Good nutritional status
• Appropriate antibiotic therapy

Worse Outcomes:
• Delayed presentation
• Severe dehydration at presentation
• Infants and young children
• Pregnant women
• Severe malnutrition
• Underlying illness (HIV, comorbidities)
• Limited access to healthcare
• Outbreak settings with overwhelmed facilities

RECOVERY:
• Dehydration corrects within hours with adequate fluids
• Diarrhea usually stops within 1-3 days with antibiotics
• Full recovery expected
• Post-infectious symptoms (bloating, loose stools) may persist for weeks in some patients

IMMUNITY:
• Infection provides immunity for several years
• Protection against same serogroup
• Reinfection possible with different serogroup
• Oral cholera vaccine provides 60-85% protection for 3-5 years`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

WATER, SANITATION, AND HYGIENE (WASH):

Safe Water:
• Piped treated water supply (long-term goal)
• Household water treatment: Chlorination, boiling, filtration, solar disinfection
• Protecting water sources from contamination
• Safe water storage in covered containers

Sanitation:
• Improved latrines and toilets
• Safe disposal of human waste
• Sewage treatment
• No open defecation

Hygiene:
• Handwashing with soap at critical times (after toilet, before eating/cooking)
• Safe food preparation and storage
• Avoid raw or undercooked seafood in endemic areas

ORAL CHOLERA VACCINE (OCV):

WHO-Prequalified Vaccines:
• Dukoral: 2 doses, 1-6 weeks apart; requires buffer solution; protection ~60-85%
• Shanchol/Euvichol: 2 doses, 2 weeks apart; no buffer needed; protection ~65%

Indications:
• Endemic areas (routine vaccination)
• Outbreak response (reactive vaccination)
• Humanitarian emergencies
• Travel to high-risk areas (adjunct to WASH)

Limitations:
• 2 doses needed for full protection
• Protection wanes after 3-5 years
• Does not prevent all cases (60-85% efficacy)
• Limited global supply

OUTBREAK RESPONSE:

Immediate:
• Early detection and laboratory confirmation
• Case management (rehydration)
• Active surveillance
• WASH interventions (chlorine, hygiene promotion)
• Oral cholera vaccine (reactive vaccination)
• Risk communication

Medium/Long-term:
• WASH infrastructure improvement
• Sustainable water and sanitation solutions
• Community engagement
• Health system strengthening

CHEMOPROPHYLAXIS:
• NOT routinely recommended for household contacts
• May be considered in high-risk closed settings (institutions)
• Emphasis on WASH measures instead

HEALTH EDUCATION:
• Recognize symptoms of cholera
• Importance of early treatment-seeking
• ORS preparation and administration
• Safe water and food practices
• Handwashing`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS CHOLERA?
Cholera is a serious diarrheal disease caused by drinking water or eating food contaminated with cholera bacteria. It causes severe watery diarrhea that can lead to dangerous dehydration within hours. With proper treatment (drinking fluids), almost everyone survives. Without treatment, cholera can kill within hours.

HOW DO YOU GET CHOLERA?
• Drinking contaminated water
• Eating food prepared with contaminated water
• Eating raw or undercooked seafood from contaminated water
• Poor hand hygiene after using the toilet

WARNING SIGNS - SEEK HELP IMMEDIATELY:
• Profuse watery diarrhea (like "rice water")
• Extreme thirst
• Sunken eyes
• Dry mouth and skin
• Weakness, feeling faint
• Muscle cramps
• Reduced or no urine

THE TREATMENT IS FLUIDS:
• Most important: Replace the fluids you are losing
• Oral Rehydration Solution (ORS) can save your life
• Drink ORS even if you are vomiting - take small sips frequently
• Very sick patients need IV fluids in hospital

HOW TO PREPARE ORS:
• 1 packet ORS in 1 liter of clean water
• Stir until dissolved
• Use within 24 hours
• If no ORS: Mix 6 level teaspoons sugar + 1/2 level teaspoon salt in 1 liter clean water

HOW MUCH TO DRINK:
• Drink after every loose stool
• Adults: At least 1 cup (200-250 mL) after each stool
• Children: At least half cup after each stool
• Drink as much as you can tolerate

CONTINUE EATING:
• Continue breastfeeding babies
• Resume normal food as soon as able to eat
• Give extra food during recovery

PREVENTING CHOLERA:

Water:
• Drink only safe water (treated, boiled, or chlorinated)
• Store water in clean, covered containers
• Do not drink from rivers, lakes, or wells unless treated

Food:
• Cook food thoroughly
• Eat food while still hot
• Avoid raw foods and street food during outbreaks
• Wash fruits and vegetables with safe water

Hygiene:
• WASH HANDS with soap:
  - After using toilet
  - After caring for sick person
  - Before preparing food
  - Before eating
• Use a toilet or latrine - do not defecate in open
• Clean and disinfect surfaces that may be contaminated

CARING FOR A SICK PERSON:
• Give ORS frequently
• Wash hands after any contact
• Disinfect any surfaces contaminated with stool
• Use separate toilet if possible
• Wash soiled clothing and bedding with soap and chlorine

WHEN TO RETURN TO HOSPITAL:
• Unable to drink or keep fluids down
• Becoming more weak or drowsy
• Blood in stool
• Not improving after 2 days
• Child: Very thirsty, not passing urine, very sleepy

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

Cholera is serious but TREATABLE. The most important treatment is fluids (ORS). Seek medical help immediately if you or a family member develops severe watery diarrhea. With prompt treatment, almost everyone recovers fully.`,

    references: [
      'World Health Organization. Cholera vaccines: WHO position paper – August 2017. Wkly Epidemiol Rec. 2017;92(34):477-498.',
      'World Health Organization. The Treatment of Diarrhoea: A Manual for Physicians and Other Senior Health Workers. 4th rev. WHO; 2005.',
      'World Health Organization. Managing a Cholera Epidemic: Chapter 4 - Case management. WHO; 2017.',
      'Harris JB, LaRocque RC, et al. Cholera. Lancet. 2012;379(9835):2466-2476.',
      'Sack DA, Sack RB, et al. Cholera. Lancet. 2004;363(9404):223-233.',
      'Ali M, Nelson AR, et al. Updated Global Burden of Cholera in Endemic Countries. PLoS Negl Trop Dis. 2015;9(6):e0003832.',
      'Global Task Force on Cholera Control. Ending Cholera: A Global Roadmap to 2030. WHO; 2017.',
      'Qadri F, Wierzba TF, et al. Efficacy of a Single-Dose Regimen of Inactivated Whole-Cell Oral Cholera Vaccine: Results from 2 Years of Follow-Up of a Randomised Trial. Lancet Infect Dis. 2018;18(6):666-674.',
      'Luquero FJ, Grout L, et al. Use of Vibrio cholerae Vaccine in an Outbreak in Guinea. N Engl J Med. 2014;370(22):2111-2120.',
      'World Health Organization. Weekly Epidemiological Record: Cholera, 2022. WER. 2023;98(38):431-452.',
      'Clemens JD, Nair GB, et al. Cholera. Lancet. 2017;390(10101):1539-1549.',
      'Reveiz L, Chapman E, et al. Chemoprophylaxis in Contacts of Patients with Cholera: Systematic Review and Meta-Analysis. PLoS One. 2011;6(11):e27060.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 72. TYPHOID FEVER
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '72',
    name: 'Typhoid Fever',
    slug: 'typhoid-fever',
    specialty: 'Infectious Disease',
    bodySystem: 'Gastrointestinal',
    icdCode: 'A01.0',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Typhoid fever is a life-threatening systemic infection caused by Salmonella enterica serovar Typhi (S. Typhi), transmitted through the fecal-oral route via contaminated water and food. It remains a major global health concern with an estimated 11-21 million cases and 128,000-161,000 deaths annually worldwide, with the highest burden in South Asia and sub-Saharan Africa.

The pathophysiology involves ingestion of S. Typhi, which survives gastric acid, penetrates the intestinal mucosa through M cells overlying Peyer's patches, and is phagocytosed by macrophages. The bacteria multiply within macrophages, spread to mesenteric lymph nodes, and enter the bloodstream causing primary bacteremia. Secondary bacteremia follows multiplication in the reticuloendothelial system (liver, spleen, bone marrow), leading to the characteristic clinical syndrome.

In Africa, typhoid fever poses a significant public health challenge due to inadequate water and sanitation infrastructure, urban overcrowding, and limited diagnostic capacity. Sub-Saharan Africa accounts for approximately 40% of global typhoid cases. The emergence of extensively drug-resistant (XDR) typhoid, particularly from South Asia, raises concerns for global spread. Many African countries lack systematic surveillance, suggesting significant underreporting of true disease burden.

Early diagnosis and appropriate antibiotic therapy are crucial for reducing morbidity and mortality. Untreated typhoid has a case fatality rate of 10-30%, which drops to <1% with appropriate treatment. Prevention through improved water, sanitation, and hygiene (WASH) and vaccination remains the cornerstone of typhoid control in endemic settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Blood culture is gold standard but often negative after day 7; bone marrow culture has highest sensitivity (>90%) throughout illness

🔴 CRITICAL POINT 2 - Empiric treatment should cover fluoroquinolone-resistant strains; azithromycin or ceftriaxone are preferred first-line in Africa

🔴 CRITICAL POINT 3 - Intestinal perforation and hemorrhage typically occur in week 2-3; maintain high index of suspicion for acute abdomen

🔴 CRITICAL POINT 4 - Relative bradycardia (pulse-temperature dissociation) is a classic but not universal sign; absence does not exclude diagnosis

🟠 CRITICAL POINT 5 - Widal test has poor sensitivity and specificity; should NOT be used as sole diagnostic criterion

🟠 CRITICAL POINT 6 - Dexamethasone reduces mortality in severe typhoid with altered consciousness (3mg/kg loading, then 1mg/kg q6h × 48hrs)

🟠 CRITICAL POINT 7 - Chronic carriers (1-4% of patients) excrete bacteria for >1 year and require prolonged antibiotic treatment

🟡 CRITICAL POINT 8 - Rose spots (faint salmon-colored macules on trunk) appear in week 2 but are difficult to see on dark skin

🟡 CRITICAL POINT 9 - Typhoid conjugate vaccines (TCV) provide longer protection than older polysaccharide vaccines and can be given from 6 months of age`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 11-21 million cases and 128,000-161,000 deaths annually worldwide
• Highest incidence in South Asia (particularly India, Pakistan, Bangladesh) and sub-Saharan Africa
• Children aged 5-15 years most commonly affected in endemic areas
• Incidence: 100-1000 cases per 100,000 population in high-endemic areas
• Case fatality rate: <1% with treatment, 10-30% without treatment

AFRICAN EPIDEMIOLOGY:
• Sub-Saharan Africa accounts for ~40% of global typhoid burden
• Highest incidence in East and West Africa, particularly urban slums
• Kenya, DRC, Nigeria, and Ghana report significant case numbers
• Estimated incidence in Africa: 50-700 per 100,000 population in endemic areas
• Under-reporting common due to limited diagnostic capacity and surveillance
• Urban areas disproportionately affected due to overcrowding and poor sanitation
• Seasonal peaks during rainy seasons when water contamination increases
• Co-infection with malaria common, complicating diagnosis
• Antimicrobial resistance increasing, with MDR strains prevalent

TRANSMISSION:
• Fecal-oral route via contaminated water and food
• Human-to-human transmission (no animal reservoir)
• Chronic carriers (gallbladder colonization) serve as reservoir
• Incubation period: 7-14 days (range 3-60 days)
• Risk factors: Poor sanitation, contaminated water, street food, recent travel

NATURAL HISTORY:
• Week 1: Gradual onset fever, malaise, headache, abdominal discomfort
• Week 2: High persistent fever, rose spots, hepatosplenomegaly, complications begin
• Week 3: Severe complications (perforation, hemorrhage, encephalopathy)
• Week 4: Gradual defervescence if surviving without treatment
• Untreated mortality: 10-30%; treated mortality: <1%`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CLASSIC PRESENTATION (Stepladder Fever Pattern):
• Gradual onset of fever increasing over 3-4 days (stepladder pattern)
• High sustained fever (39-40°C) by end of week 1
• Frontal headache, often severe
• Malaise, anorexia, myalgia
• Relative bradycardia (pulse-temperature dissociation)
• Abdominal discomfort, constipation (early) or diarrhea (later)
• Dry cough (30% of patients)
• Epistaxis

WEEK 2-3 FEATURES:
• Persistent high fever (40-41°C)
• Rose spots: Faint salmon-colored blanching macules on trunk (10-30%, hard to see on dark skin)
• Hepatosplenomegaly (30-70%)
• Abdominal distension and tenderness
• Coated tongue with clean edges ("typhoid tongue")
• Altered mental status, delirium ("typhoid state")
• Pea-soup diarrhea (classic but not universal)

PHYSICAL EXAMINATION:

GENERAL:
• Toxic appearance, apathetic facies
• Relative bradycardia (pulse <100 despite fever >39°C)
• Coated tongue with red, clean margins

ABDOMINAL:
• Soft hepatomegaly (50-70%)
• Splenomegaly (30-50%)
• Diffuse tenderness, especially RLQ (Peyer's patches)
• Distension with hypoactive bowel sounds

SKIN:
• Rose spots: 2-4mm blanching macules on chest/abdomen (appears week 2)
• Crops of 10-20 lesions lasting 3-5 days

NEUROLOGICAL:
• Apathy, confusion, delirium
• Meningismus (uncommon)
• Tremor of hands (subsultus tendinum)

CLASSIFICATION BY SEVERITY:

UNCOMPLICATED TYPHOID:
• Fever without focal signs
• Alert and oriented
• Tolerating oral intake
• No complications

SEVERE/COMPLICATED TYPHOID:
• Altered consciousness, delirium, coma
• GI bleeding or perforation
• Hepatitis (AST/ALT >3× ULN)
• Myocarditis, pneumonia
• Shock or DIC`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
• Gradual onset fever lasting >3 days in endemic area
• Headache, malaise, abdominal symptoms
• Travel to endemic area within 60 days
• Relative bradycardia, hepatosplenomegaly

STEP 2: LABORATORY CONFIRMATION
• Blood culture (gold standard): Best in week 1, sensitivity 40-80%
• Bone marrow culture: Highest sensitivity (>90%), positive even on antibiotics
• Stool culture: Positive in weeks 2-3, useful for carrier detection
• Urine culture: Lower yield, positive in weeks 2-3

STEP 3: SUPPORTIVE TESTING
• Complete blood count: Leukopenia, anemia, thrombocytopenia
• Liver function tests: Mild transaminitis (2-3× ULN)
• Inflammatory markers: Elevated CRP, ESR

DIAGNOSTIC CRITERIA:

CONFIRMED CASE:
• Compatible clinical syndrome PLUS
• Isolation of S. Typhi from blood, bone marrow, stool, or other sterile site

PROBABLE CASE:
• Compatible clinical syndrome PLUS
• Positive serological test (in absence of culture) PLUS
• Epidemiological link or residence in endemic area

LABORATORY FINDINGS:

HEMATOLOGY:
• WBC: Often normal or low (leukopenia in 20-30%)
• Relative lymphocytosis
• Anemia (normocytic)
• Thrombocytopenia (may indicate severe disease or DIC)
• Eosinopenia (characteristic)

BIOCHEMISTRY:
• AST/ALT: Mildly elevated (2-3× ULN)
• Hyponatremia common
• LDH may be elevated

SEROLOGY (Limited Value):
• Widal test: Sensitivity 47-77%, specificity 50-92%; DO NOT use alone
• Typhidot (IgM/IgG): Better than Widal but false positives common
• Tubex (O9 antigen): Rapid test, moderate sensitivity
• NOTE: Serology may be negative in early disease and in vaccinated individuals`,

    differentialDiagnosis: [
      'Malaria - Fever pattern differs (paroxysmal), blood smear positive, common co-infection',
      'Paratyphoid fever - Clinically indistinguishable, caused by S. Paratyphi A, B, or C',
      'Brucellosis - Undulant fever, arthralgia, exposure to animals/unpasteurized dairy',
      'Tuberculosis (miliary) - Weight loss, night sweats, longer duration, CXR findings',
      'Infective endocarditis - Cardiac murmur, embolic phenomena, positive blood cultures',
      'Viral hepatitis - Jaundice more prominent, transaminases typically higher',
      'Dengue fever - Rash, myalgia, thrombocytopenia, no hepatosplenomegaly',
      'Rickettsial infections - Eschar, rash, exposure history',
      'Leptospirosis - Conjunctival suffusion, jaundice, water exposure',
      'Amoebic liver abscess - RUQ pain, hepatomegaly, ultrasound findings',
      'Visceral leishmaniasis (Kala-azar) - Massive splenomegaly, pancytopenia, prolonged course',
      'HIV seroconversion - Lymphadenopathy, rash, pharyngitis, risk factors',
      'Bacterial sepsis - Rapid onset, hemodynamic instability, focus of infection',
      'Meningitis - Meningeal signs, CSF abnormalities, acute onset'
    ],

    investigations: [
      {
        name: 'Blood Culture (Aerobic)',
        indication: '🔴 ESSENTIAL - Gold standard for diagnosis; collect 10-15mL (adults) or 2-4mL (children) before antibiotics; sensitivity 40-80% in week 1',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Look for leukopenia, relative lymphocytosis, eosinopenia, thrombocytopenia, anemia',
        availability: 'BASIC'
      },
      {
        name: 'Malaria Rapid Test/Blood Smear',
        indication: '🔴 ESSENTIAL - Rule out malaria; co-infection common in endemic areas; fever workup standard',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🔴 ESSENTIAL - Expect mild elevation (2-3× ULN); marked elevation suggests hepatic complication or alternative diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and Electrolytes',
        indication: '🔴 ESSENTIAL - Assess renal function; hyponatremia common; guide fluid management',
        availability: 'BASIC'
      },
      {
        name: 'Widal Test',
        indication: '🟠 IMPORTANT - If culture unavailable; single test unreliable; 4-fold rise in paired samples more useful; high false positive/negative rates',
        availability: 'BASIC'
      },
      {
        name: 'Stool Culture',
        indication: '🟠 IMPORTANT - Positive in weeks 2-3; useful for carrier detection; lower sensitivity than blood culture for acute diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'Typhidot or Tubex Rapid Test',
        indication: '🟠 IMPORTANT - Point-of-care serology; better than Widal but variable sensitivity; useful when culture unavailable',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🟠 IMPORTANT - Assess hepatosplenomegaly; detect free fluid if perforation suspected; rule out liver abscess',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🟠 IMPORTANT - If respiratory symptoms present; rule out pneumonia; detect free air under diaphragm if perforation',
        availability: 'BASIC'
      },
      {
        name: 'Bone Marrow Culture',
        indication: '🟡 IF AVAILABLE - Highest sensitivity (>90%); positive even after antibiotic therapy; reserved for culture-negative suspected cases',
        availability: 'ADVANCED'
      },
      {
        name: 'PCR for S. Typhi',
        indication: '🟡 IF AVAILABLE - Rapid, highly specific; useful in early disease; not widely available in resource-limited settings',
        availability: 'ADVANCED'
      },
      {
        name: 'Blood Gas Analysis',
        indication: '🟡 IF AVAILABLE - In severe disease; assess metabolic acidosis, lactate in shock',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Coagulation Profile',
        indication: '🟡 IF AVAILABLE - If bleeding suspected or severe disease; detect DIC',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS: Vital signs, hydration status, level of consciousness, abdominal examination',
        '⚡ OBTAIN: Blood cultures (ideally 2 sets from different sites) BEFORE starting antibiotics',
        '⚡ ESTABLISH: IV access if unable to tolerate oral intake or signs of dehydration/severe disease',
        '⚡ INITIATE: Fluid resuscitation with normal saline if dehydrated (20mL/kg bolus, reassess)',
        '⚡ START: Empiric antibiotic therapy - do NOT wait for culture results in suspected typhoid',
        '⚡ MONITOR: Temperature chart (4-hourly), vital signs, urine output, mental status',
        '⚡ MALARIA: Test for malaria (co-infection common) and treat if positive',
        '⚡ NUTRITION: Maintain oral intake if tolerated; soft, easily digestible diet; avoid high-fiber foods',
        '⚡ FEVER: Paracetamol 1g q6h (adults) for symptomatic relief; avoid NSAIDs (bleeding risk)',
        '⚡ ISOLATE: Enteric precautions; hand hygiene; safe disposal of excreta',
        '⚡ ALERT: Watch for complications - GI bleeding (melena, hematemesis), perforation (acute abdomen), altered consciousness'
      ],
      
      pharmacological: [
        {
          drug: 'Azithromycin',
          indication: '🔴 FIRST-LINE for uncomplicated typhoid in Africa (covers fluoroquinolone-resistant strains)',
          dose: 'Adults: 1g PO on day 1, then 500mg PO daily for 6 days (total 7 days) OR 20mg/kg/day (max 1g) daily for 5-7 days. Children: 20mg/kg/day (max 500mg) PO daily for 7 days',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Known macrolide hypersensitivity', 'Severe hepatic impairment', 'QT prolongation'],
          sideEffects: ['Nausea', 'Diarrhea', 'Abdominal pain', 'QT prolongation (rare)'],
          alternatives: ['Ceftriaxone', 'Ciprofloxacin (if susceptible)']
        },
        {
          drug: 'Ceftriaxone',
          indication: '🔴 FIRST-LINE for severe typhoid, IV therapy required, or azithromycin intolerance',
          dose: 'Adults: 2g IV once daily. Children: 50-80mg/kg/day IV once daily (max 2g)',
          route: 'IV/IM',
          duration: '10-14 days',
          contraindications: ['Cephalosporin allergy', 'Neonates with hyperbilirubinemia (IV)', 'Calcium-containing IV solutions'],
          sideEffects: ['Diarrhea', 'Rash', 'Eosinophilia', 'Elevated LFTs', 'Biliary sludging'],
          alternatives: ['Azithromycin', 'Cefixime', 'Cefotaxime']
        },
        {
          drug: 'Cefixime',
          indication: '🔴 FIRST-LINE oral alternative when IV not required; good for step-down therapy',
          dose: 'Adults: 200mg PO twice daily OR 400mg once daily. Children: 15-20mg/kg/day divided q12h (max 400mg/day)',
          route: 'PO',
          duration: '10-14 days',
          contraindications: ['Cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Nausea', 'Abdominal pain', 'Headache'],
          alternatives: ['Azithromycin', 'Ceftriaxone']
        },
        {
          drug: 'Ciprofloxacin',
          indication: '🟠 SECOND-LINE - Only if confirmed fluoroquinolone-susceptible strain; resistance common',
          dose: 'Adults: 500mg PO twice daily OR 400mg IV twice daily. Children >18 years only (generally avoid in pediatrics)',
          route: 'PO/IV',
          duration: '7-10 days (susceptible strains); 10-14 days (nalidixic acid resistant)',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Children <18 years (relative)', 'Tendon disorders', 'Myasthenia gravis', 'Known FQ resistance'],
          sideEffects: ['Nausea', 'Diarrhea', 'Tendinopathy', 'QT prolongation', 'CNS effects'],
          alternatives: ['Azithromycin', 'Ceftriaxone']
        },
        {
          drug: 'Ampicillin',
          indication: '🟡 ALTERNATIVE - Only if confirmed susceptible; high resistance rates limit utility',
          dose: 'Adults: 1-2g IV q6h. Children: 200mg/kg/day IV divided q6h',
          route: 'IV',
          duration: '14 days',
          contraindications: ['Penicillin allergy', 'Known resistance'],
          sideEffects: ['Rash', 'Diarrhea', 'Allergic reactions'],
          alternatives: ['Ceftriaxone', 'Azithromycin']
        },
        {
          drug: 'Chloramphenicol',
          indication: '🟡 ALTERNATIVE - Historical first-line; use only if susceptible and other options unavailable',
          dose: 'Adults: 500mg PO/IV q6h. Children: 50-75mg/kg/day divided q6h (max 3g/day)',
          route: 'PO/IV',
          duration: '14-21 days',
          contraindications: ['Bone marrow suppression', 'Pregnancy (third trimester)', 'Neonates'],
          sideEffects: ['Bone marrow suppression', 'Aplastic anemia (rare)', 'Gray baby syndrome', 'Nausea'],
          alternatives: ['Azithromycin', 'Ceftriaxone']
        },
        {
          drug: 'Dexamethasone',
          indication: '🔴 CRITICAL - For severe typhoid with altered consciousness (GCS ≤13) or shock; reduces mortality by 50%',
          dose: 'Loading: 3mg/kg IV over 30 minutes. Maintenance: 1mg/kg IV q6h for 48 hours (8 doses total)',
          route: 'IV',
          duration: '48 hours',
          contraindications: ['GI bleeding (relative)', 'Perforation suspected'],
          sideEffects: ['Hyperglycemia', 'GI bleeding', 'Masking of perforation signs'],
          alternatives: ['Hydrocortisone if dexamethasone unavailable']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 SUPPORTIVE - Antipyretic; avoid NSAIDs due to GI bleeding risk and platelet effects',
          dose: 'Adults: 500mg-1g PO/IV q6h (max 4g/day). Children: 15mg/kg q6h (max 75mg/kg/day)',
          route: 'PO/IV',
          duration: 'As needed during febrile period',
          contraindications: ['Severe hepatic impairment', 'Allergy'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Rare allergic reactions'],
          alternatives: ['Physical cooling measures']
        },
        {
          drug: 'Oral Rehydration Salts (ORS)',
          indication: '🟠 SUPPORTIVE - Maintain hydration in patients tolerating oral intake',
          dose: 'Adults: Free intake as tolerated, aim 2-3L/day. Children: 10mL/kg after each loose stool',
          route: 'PO',
          duration: 'Until hydration maintained',
          contraindications: ['Ileus', 'Complete bowel obstruction', 'Severe vomiting'],
          sideEffects: ['Hypernatremia (if excessive without free water)'],
          alternatives: ['IV Normal saline if oral not tolerated']
        },
        {
          drug: 'Ciprofloxacin (Carrier Eradication)',
          indication: '🟡 CARRIER TREATMENT - For chronic carriers; prolonged course required',
          dose: 'Adults: 750mg PO twice daily for 28 days',
          route: 'PO',
          duration: '28 days',
          contraindications: ['Pregnancy', 'Pediatric patients', 'Known resistance'],
          sideEffects: ['See ciprofloxacin above'],
          alternatives: ['Amoxicillin 2g q8h + probenecid for 6 weeks', 'Norfloxacin', 'Cholecystectomy if gallstones present']
        }
      ],
      
      nonPharmacological: [
        'BED REST: Recommend rest during acute febrile phase; gradual mobilization as fever resolves',
        'DIET: Soft, easily digestible, low-fiber diet; avoid raw vegetables and unpasteurized dairy; small frequent meals',
        'HYDRATION: Encourage oral fluids (2-3L/day); ORS, clean water, soups; avoid caffeine and alcohol',
        'ENTERIC PRECAUTIONS: Strict hand hygiene; safe disposal of feces; do not prepare food for others during illness',
        'FEVER MANAGEMENT: Tepid sponging; light clothing; well-ventilated room; avoid active cooling in rigors',
        'WOUND CARE: If surgical intervention for perforation - standard surgical wound care protocols',
        'INFECTION CONTROL: Isolate in single room if possible; dedicated bathroom; healthcare workers use gloves and gowns',
        'CONTACT TRACING: Identify household contacts; screen for carriers; ensure safe water/sanitation',
        'CARRIER SCREENING: Stool cultures at 1, 2, and 3 months post-treatment to confirm clearance',
        'HEALTH EDUCATION: Explain transmission, importance of completing antibiotics, hygiene measures, vaccination',
        'SAFE WATER: Advise boiling or treating water; avoid ice and uncooked foods in endemic areas'
      ],
      
      surgical: [
        'INTESTINAL PERFORATION: Emergency laparotomy indicated; simple closure vs resection depending on findings; mortality 20-60% even with surgery',
        'PERITONEAL LAVAGE: Thorough washout with warm saline during laparotomy for perforation',
        'BOWEL RESECTION: May be required for multiple perforations, extensive necrosis, or non-viable bowel',
        'CHOLECYSTECTOMY: For chronic carriers with gallstones who fail antibiotic therapy; removes nidus of infection',
        'DRAINAGE: If intra-abdominal abscess develops post-perforation',
        'HEMORRHAGE MANAGEMENT: Usually managed conservatively; surgery only for massive uncontrolled bleeding'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Signs of intestinal perforation - sudden severe abdominal pain, rigidity, absent bowel sounds, shock',
        '🔴 IMMEDIATE REFERRAL: Massive GI hemorrhage - hematemesis, large volume melena, hemodynamic instability',
        '🔴 IMMEDIATE REFERRAL: Severe altered consciousness (GCS <10) or coma (typhoid encephalopathy)',
        '🔴 IMMEDIATE REFERRAL: Septic shock - hypotension refractory to fluid resuscitation, requiring vasopressors',
        '🟠 URGENT REFERRAL: Moderate GI bleeding (stable but melena or significant blood loss)',
        '🟠 URGENT REFERRAL: Treatment failure - persistent fever >7 days on appropriate antibiotics',
        '🟠 URGENT REFERRAL: Suspected MDR/XDR typhoid - poor response to first-line agents',
        '🟠 URGENT REFERRAL: Myocarditis - new cardiac symptoms, arrhythmia, heart failure',
        '🟡 ROUTINE REFERRAL: Chronic carrier state for specialist management and possible cholecystectomy',
        '🟡 ROUTINE REFERRAL: Relapse after completing treatment course'
      ],
      
      monitoring: [
        'TEMPERATURE: 4-hourly chart; expect defervescence by day 4-5 of effective treatment',
        'VITAL SIGNS: Blood pressure, pulse, respiratory rate q4-6h; watch for shock or relative bradycardia',
        'ABDOMINAL EXAM: Daily examination; increasing tenderness, rigidity, or guarding suggests perforation',
        'FLUID BALANCE: Input/output chart; aim for urine output >0.5mL/kg/hour',
        'MENTAL STATUS: Daily GCS assessment; any deterioration requires urgent evaluation',
        'HEMOGLOBIN: Check every 2-3 days in severe disease; more frequently if bleeding suspected',
        'RESPONSE TO TREATMENT: Fever should decrease within 3-5 days; persistent fever suggests resistance or complication',
        'STOOL APPEARANCE: Monitor for blood; cessation of diarrhea expected with treatment'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (First 2 Weeks):
• Daily review during inpatient treatment until afebrile for 48-72 hours
• Transition from IV to oral antibiotics when tolerating oral intake and afebrile
• Discharge when afebrile ≥48 hours, tolerating oral diet, no complications
• Ensure completion of full antibiotic course (typically 7-14 days total)

EARLY RECOVERY (Weeks 2-4):
• Review at 1 week post-discharge
• Assess for relapse (fever returns in 5-10% of cases, usually weeks 2-3)
• If relapse: Repeat blood cultures, extend treatment to 14 days or change antibiotic
• Advise gradual return to normal activities; full recovery takes 3-4 weeks

CARRIER SCREENING:
• Stool culture at 1 month post-treatment
• Repeat at 2 and 3 months if initial positive or high-risk (food handlers, healthcare workers)
• If all 3 negative: Not a carrier
• If any positive: Chronic carrier - refer for prolonged treatment (4-6 weeks FQ) ± cholecystectomy

LONG-TERM (3+ Months):
• Vaccination counseling (if not previously vaccinated): TCV or Vi polysaccharide
• Health education: Safe water, sanitation, food hygiene
• Return if fever recurs (may be relapse or reinfection)

SPECIAL POPULATIONS:

Pregnancy:
• Azithromycin is preferred (Category B)
• Ceftriaxone acceptable
• Avoid fluoroquinolones
• Monitor for preterm labor, fetal complications
• Close obstetric follow-up

Children:
• Azithromycin or ceftriaxone first-line
• Avoid fluoroquinolones if possible (under 18)
• Ensure hydration maintenance
• Weight-based dosing critical

Immunocompromised:
• Higher risk of complications and relapse
• Prolonged treatment may be required (14-21 days)
• Lower threshold for admission and IV therapy`
    },

    redFlags: [
      '🔴 CRITICAL: Acute severe abdominal pain with rigidity - suggests intestinal perforation, requires emergency surgery',
      '🔴 CRITICAL: Frank blood per rectum or massive melena - GI hemorrhage, needs urgent resuscitation and possible surgery',
      '🔴 CRITICAL: Altered consciousness or coma (GCS <10) - typhoid encephalopathy, requires dexamethasone and ICU care',
      '🔴 CRITICAL: Hypotension (SBP <90) unresponsive to fluids - septic shock, needs vasopressors and ICU',
      '🔴 CRITICAL: Absent bowel sounds with distension - paralytic ileus or perforation, NPO and surgical consult',
      '🟠 URGENT: Persistent fever (>7 days) despite appropriate antibiotics - consider resistance, abscess, or alternative diagnosis',
      '🟠 URGENT: New onset hematemesis or melena - GI bleeding, assess hemodynamic stability, consider endoscopy',
      '🟠 URGENT: Increasing abdominal tenderness and distension - impending perforation, close monitoring needed',
      '🟠 URGENT: New cardiac symptoms (chest pain, dyspnea, palpitations) - possible myocarditis, ECG and echocardiogram',
      '🟠 URGENT: Jaundice or marked LFT elevation (>5× ULN) - hepatic typhoid or alternative diagnosis',
      '🟡 CAUTION: Relapse of fever after initial improvement - occurs in 5-10%, may need extended or alternative treatment',
      '🟡 CAUTION: Prolonged fever beyond day 5 of treatment - reassess antibiotic choice and compliance'
    ],

    complications: [
      'Intestinal perforation (1-3%) - Most serious complication; typically ileum; week 2-3; high mortality',
      'Gastrointestinal hemorrhage (2-8%) - Usually from ileal ulcers; can be massive and life-threatening',
      'Typhoid encephalopathy (5-10%) - Altered consciousness, delirium, coma; mortality 50% without steroids',
      'Septic shock - Multi-organ dysfunction; requires ICU care; poor prognosis',
      'Myocarditis - ECG changes, arrhythmias, heart failure; usually resolves with treatment',
      'Hepatitis - Elevated transaminases; usually mild and self-limited',
      'Cholecystitis - Acalculous; may contribute to carrier state; can be complicated',
      'Pneumonia - Secondary bacterial infection; requires additional antibiotics',
      'Osteomyelitis - Rare; may occur in sickle cell patients; prolonged antibiotic course',
      'Relapse (5-10%) - Recurrence of symptoms 2-3 weeks after treatment; usually milder',
      'Chronic carrier state (1-4%) - Persistent shedding >1 year; usually gallbladder colonization',
      'Disseminated intravascular coagulation (DIC) - In severe sepsis; poor prognosis',
      'Hemolytic anemia - Rare; in G6PD deficiency patients, drug-induced or autoimmune',
      'Neuropsychiatric sequelae - Rare persistent cognitive or behavioral changes'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

WITH APPROPRIATE TREATMENT:
• Case fatality rate: <1% with effective antibiotics
• Most patients defervesce within 3-5 days of starting appropriate treatment
• Full recovery typically within 2-4 weeks
• Relapse rate: 5-10% (usually milder than initial episode)
• Chronic carrier rate: 1-4% (higher in women, elderly, those with gallstones)

WITHOUT TREATMENT:
• Case fatality rate: 10-30%
• Prolonged illness over 4-6 weeks
• Higher complication rate (perforation up to 10%, bleeding up to 20%)
• Spontaneous resolution possible but prolonged and high risk

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis and treatment initiation
• Susceptible organism (no drug resistance)
• Young age (children generally do well)
• Good baseline nutrition and health
• Access to supportive care and monitoring
• Absence of complications at presentation

Worse Outcomes:
• Delayed presentation or treatment
• Drug-resistant strains (MDR, XDR)
• Complicated disease (perforation, encephalopathy, shock)
• Extremes of age (elderly, infants)
• Malnutrition or immunocompromise (HIV, diabetes)
• Limited healthcare resources
• Pregnancy (increased risk of complications)

COMPLICATION-SPECIFIC MORTALITY:
• Intestinal perforation: 20-60% (even with surgery)
• Typhoid encephalopathy: 50% (without steroids); 10-20% (with dexamethasone)
• Septic shock: 30-60%
• Uncomplicated typhoid with treatment: <1%`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

WATER, SANITATION AND HYGIENE (WASH):
• Safe drinking water: Treated, boiled, or bottled water in endemic areas
• Sanitation: Proper sewage disposal; prevent contamination of water sources
• Hand hygiene: Wash hands with soap after toilet and before food preparation/eating
• Food safety: Cook food thoroughly; avoid raw vegetables unless properly washed; avoid street food in endemic areas

VACCINATION:
• Typhoid Conjugate Vaccine (TCV) - PREFERRED
  - Typbar-TCV (Vi-TT conjugate)
  - Age: ≥6 months
  - Dose: Single 0.5mL IM injection
  - Protection: ~85% efficacy; lasts 3-5+ years
  - WHO prequalified and recommended for endemic areas

• Vi Polysaccharide Vaccine (ViPS)
  - Typhim Vi, Typherix
  - Age: ≥2 years
  - Dose: Single 0.5mL IM injection
  - Protection: 55-75% efficacy; lasts 2-3 years
  - Revaccination every 2-3 years if ongoing risk

• Live Oral Vaccine (Ty21a)
  - Vivotif
  - Age: ≥6 years
  - Dose: 1 capsule on days 1, 3, 5, 7 (4 doses)
  - Protection: 50-80% efficacy; lasts 5 years
  - Avoid with antibiotics and immunocompromise

SECONDARY PREVENTION:
• Early case detection and treatment
• Contact tracing and screening
• Carrier identification through stool cultures
• Food handler screening in endemic areas
• Outbreak investigation and source control

TERTIARY PREVENTION:
• Complete treatment to prevent relapse and carrier state
• Carrier treatment (prolonged antibiotics ± cholecystectomy)
• Follow-up stool cultures to confirm clearance
• Health education for carriers on preventing transmission

PUBLIC HEALTH MEASURES:
• Surveillance and reporting of cases
• Outbreak response protocols
• Improved water infrastructure investment
• Community health education programs
• Integration of typhoid vaccination into routine immunization programs (WHO recommendation)`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS TYPHOID FEVER?
Typhoid fever is a serious bacterial infection that spreads through contaminated water or food. The germs multiply in your blood and intestines, causing high fever that lasts many days if not treated. With proper antibiotic treatment, most people recover fully within 2-4 weeks.

SYMPTOMS TO WATCH FOR:
• High fever that increases over several days and stays high
• Headache and body weakness
• Stomach pain, constipation or diarrhea
• Loss of appetite
• A flat, pinkish rash on the body (not always visible on dark skin)

DANGER SIGNS - SEEK IMMEDIATE HELP IF:
• Severe stomach pain that comes suddenly
• Blood in stool or black, tarry stools
• Vomiting blood
• Confusion, difficulty staying awake, or unusual behavior
• Fainting or dizziness
• Fever does not improve after 5 days of taking medicines

YOUR TREATMENT:
• Take ALL your antibiotic medicines exactly as prescribed, even after you feel better
• Stopping early can cause the infection to come back and become harder to treat
• Most people need 7-14 days of antibiotics
• You may feel weak for several weeks after treatment - this is normal

HOW TO PREVENT SPREADING TO OTHERS:
• Wash hands thoroughly with soap after using the toilet
• Wash hands before preparing or eating food
• Do not prepare food for others while sick and for 1 week after recovery
• Use a separate toilet if possible, or clean the toilet after each use
• Do not share towels, utensils, or drinking cups

LIFESTYLE AND RECOVERY:
• Rest in bed until fever goes away
• Drink plenty of clean, safe fluids (boiled water, ORS, soups)
• Eat small, simple meals - soft foods like rice, porridge, bananas
• Avoid raw vegetables, unpasteurized milk, and street food
• No alcohol until fully recovered
• Gradually return to normal activities as you feel stronger

AFTER RECOVERY:
• You may need stool tests at 1, 2, and 3 months to make sure you are not carrying the germs
• Ask your doctor about typhoid vaccine to prevent future infection
• Continue to practice good hand hygiene and safe food/water habits

PROTECTING YOUR FAMILY:
• Everyone should wash hands frequently with soap
• Ensure drinking water is boiled or from a safe source
• Family members can eat from the same pot but should use clean utensils
• Family members should get vaccinated, especially children

═══════════════════════════════════════════════════════════════════════════════
⚠️ IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for education only and does not replace advice from your doctor or healthcare provider. Every person's situation is different, and your treatment should be guided by a qualified healthcare professional who knows your medical history. If you have questions about your treatment or if your symptoms worsen, contact your healthcare provider immediately.`,

    references: [
      'WHO. Typhoid vaccines: WHO position paper, March 2018. Wkly Epidemiol Rec. 2018;93:153-172.',
      'Crump JA, Sjolund-Karlsson M, Gordon MA, Parry CM. Epidemiology, Clinical Presentation, Laboratory Diagnosis, Antimicrobial Resistance, and Antimicrobial Management of Invasive Salmonella Infections. Clin Microbiol Rev. 2015;28(4):901-937.',
      'Bhutta ZA. Current concepts in the diagnosis and treatment of typhoid fever. BMJ. 2006;333(7558):78-82.',
      'Parry CM, Hien TT, Dougan G, et al. Typhoid fever. N Engl J Med. 2002;347(22):1770-1782.',
      'Mogasale V, Maskery B, Ochiai RL, et al. Burden of typhoid fever in low-income and middle-income countries: a systematic, literature-based update with risk-factor adjustment. Lancet Glob Health. 2014;2(10):e570-e580.',
      'Antillón M, Warren JL, Crawford FW, et al. The burden of typhoid fever in low- and middle-income countries: A meta-regression approach. PLoS Negl Trop Dis. 2017;11(2):e0005376.',
      'Stanaway JD, Reiner RC, Blacker BF, et al. The global burden of typhoid and paratyphoid fevers: a systematic analysis for the Global Burden of Disease Study 2017. Lancet Infect Dis. 2019;19(4):369-381.',
      'Andrews JR, Qamar FN, Charles RC, Ryan ET. Extensively Drug-Resistant Typhoid - Are Conjugate Vaccines Arriving Just in Time? N Engl J Med. 2018;379(16):1493-1495.',
      'Klemm EJ, Shakoor S, Page AJ, et al. Emergence of an Extensively Drug-Resistant Salmonella enterica Serovar Typhi Clone Harboring a Promiscuous Plasmid Encoding Resistance to Fluoroquinolones and Third-Generation Cephalosporins. mBio. 2018;9(1):e00105-18.',
      'Hoffman SL, Punjabi NH, Kumala S, et al. Reduction of mortality in chloramphenicol-treated severe typhoid fever by high-dose dexamethasone. N Engl J Med. 1984;310(2):82-88.',
      'Bhan MK, Bahl R, Bhatnagar S. Typhoid and paratyphoid fever. Lancet. 2005;366(9487):749-762.',
      'Levine MM, Simon R. The Gathering Storm: Is Untreatable Typhoid Fever on the Way? mBio. 2018;9(2):e00482-18.',
      'Wain J, Hendriksen RS, Mikoleit ML, Keddy KH, Ochiai RL. Typhoid fever. Lancet. 2015;385(9973):1136-1145.',
      'WHO. Guidelines on the quality, safety and efficacy of typhoid conjugate vaccines. WHO Technical Report Series. 2020;No. 1030.',
      'MSF Clinical Guidelines. Typhoid fever. Médecins Sans Frontières. 2021.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 73. HEPATITIS B
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '73',
    name: 'Hepatitis B',
    slug: 'hepatitis-b',
    specialty: 'Infectious Disease',
    bodySystem: 'Gastrointestinal',
    icdCode: 'B16',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Hepatitis B is a potentially life-threatening liver infection caused by the hepatitis B virus (HBV). It is a major global health problem with approximately 296 million people living with chronic HBV infection worldwide and an estimated 820,000 deaths annually, primarily from cirrhosis and hepatocellular carcinoma (HCC). HBV is 50-100 times more infectious than HIV and can survive outside the body for at least 7 days.

The pathophysiology of HBV involves infection of hepatocytes where the virus establishes covalently closed circular DNA (cccDNA) in the nucleus, serving as a template for viral replication. Liver damage results primarily from the host immune response to infected hepatocytes rather than direct viral cytopathicity. Chronic infection occurs when the immune system fails to clear the virus, leading to ongoing inflammation, fibrosis, and eventual cirrhosis.

Africa bears a disproportionate burden of hepatitis B, with the WHO African Region having the second-highest prevalence globally. Sub-Saharan Africa is classified as highly endemic, with chronic HBV infection rates of 6-8% in the general population and exceeding 10% in some countries. Horizontal transmission in early childhood (rather than perinatal) is a major route in Africa. Limited access to screening, vaccination, and antiviral therapy contributes to high morbidity and mortality, with HBV-related HCC being one of the most common cancers in African men.

The cornerstone of HBV control is prevention through vaccination, which is 95% effective in preventing infection and its chronic consequences. For those with chronic infection, long-term antiviral therapy can suppress viral replication, reduce liver inflammation, and prevent progression to cirrhosis and HCC.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - HBsAg positive for >6 months defines chronic hepatitis B; acute infection clears in >95% of immunocompetent adults but <5% of infants

🔴 CRITICAL POINT 2 - ALL pregnant women must be screened for HBsAg; infants born to HBsAg+ mothers need birth-dose vaccine + HBIG within 12 hours

🔴 CRITICAL POINT 3 - Tenofovir (TDF) is preferred first-line therapy in Africa due to high barrier to resistance and inclusion in HIV ART regimens

🔴 CRITICAL POINT 4 - HCC surveillance (ultrasound ± AFP every 6 months) is mandatory for all cirrhotics and high-risk chronic HBV patients

🟠 CRITICAL POINT 5 - Treatment does NOT eradicate HBV; cccDNA persists in hepatocytes; lifelong monitoring required even if HBsAg loss achieved

🟠 CRITICAL POINT 6 - Reactivation can occur with immunosuppression (chemotherapy, steroids, biologics); screen all patients before such therapy

🟠 CRITICAL POINT 7 - HBV/HIV coinfection requires TDF-based ART regardless of CD4 count; never use lamivudine monotherapy (resistance)

🟡 CRITICAL POINT 8 - Isolated anti-HBc positive may indicate occult HBV infection, resolved infection, or false positive; interpret in context

🟡 CRITICAL POINT 9 - Spontaneous HBsAg seroconversion occurs in 0.5-1% per year; functional cure but HCC risk persists in cirrhotics`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 296 million people living with chronic HBV infection (2019)
• 1.5 million new infections annually despite effective vaccine
• 820,000 deaths per year, primarily from cirrhosis (30%) and HCC (45%)
• Prevalence varies: <2% (low), 2-7% (intermediate), ≥8% (high endemicity)
• Highest prevalence in Western Pacific and African regions
• HBV is responsible for 50-80% of HCC cases worldwide

AFRICAN EPIDEMIOLOGY:
• WHO African Region: 6.1% prevalence (60 million chronic carriers)
• Highest prevalence countries: Nigeria (8.1%), Cameroon (11.2%), Burkina Faso (10%), South Sudan (22.4%)
• Horizontal transmission in early childhood predominates (unlike Asia where perinatal dominates)
• HBV-related HCC is one of the most common cancers in African men
• Only 2-5% of chronically infected individuals in Africa are diagnosed
• <2% of eligible patients receive antiviral treatment
• Mother-to-child transmission prevention programs limited; birth-dose vaccine coverage <10% in many countries
• High coinfection rates with HIV (15-25% of HIV+ individuals are HBsAg+)
• HDV (hepatitis D) coinfection present in 5-20% of HBV carriers in some regions

TRANSMISSION ROUTES:
• Perinatal (mother-to-child): Major route in high-endemic areas
• Horizontal (early childhood): Household contact, shared items, wounds - major in Africa
• Sexual transmission: Unprotected sex with infected partner
• Parenteral: Unsafe injections, blood transfusion, needle sharing, occupational exposure
• NOT transmitted by: Food, water, breastfeeding (if infant vaccinated), casual contact

NATURAL HISTORY:
• Acute infection: 70% subclinical, 30% symptomatic, <1% fulminant
• Chronicity risk by age at infection: Infants <1yr: 90%; Children 1-5yr: 30-50%; Adults: 5-10%
• Chronic infection phases: Immune tolerant → Immune active → Inactive carrier → Reactivation
• Cirrhosis development: 15-40% of untreated chronic HBV over 20-30 years
• HCC risk: 2-5% per year in cirrhotics; 0.2-0.6% per year in non-cirrhotics`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ACUTE HEPATITIS B:
• Incubation period: 30-180 days (average 75 days)
• Prodromal phase (1-2 weeks): Fatigue, malaise, anorexia, nausea, RUQ discomfort
• Icteric phase: Jaundice, dark urine, pale stools, hepatomegaly
• Recovery phase: Symptoms resolve over weeks; fatigue may persist months
• Note: 70% of acute infections are asymptomatic/subclinical

CHRONIC HEPATITIS B:
• Often asymptomatic for years to decades
• Fatigue, malaise (non-specific)
• RUQ discomfort or fullness
• Symptoms of cirrhosis when advanced (see below)
• Extrahepatic manifestations (uncommon): Polyarteritis nodosa, glomerulonephritis, serum sickness-like syndrome

CIRRHOSIS FEATURES:
• Jaundice (progressive)
• Ascites, peripheral edema
• Spider angiomata, palmar erythema
• Gynecomastia, testicular atrophy
• Hepatic encephalopathy (confusion, asterixis)
• Splenomegaly, caput medusae

PHYSICAL EXAMINATION:

ACUTE HEPATITIS:
• Jaundice (scleral icterus, skin)
• Tender hepatomegaly
• May have low-grade fever
• Skin excoriations from pruritus
• No signs of chronic liver disease

CHRONIC HEPATITIS/CIRRHOSIS:
• Hepatomegaly (early) or small, shrunken liver (late cirrhosis)
• Splenomegaly
• Signs of portal hypertension (ascites, caput medusae, hemorrhoids)
• Stigmata of chronic liver disease (spider nevi, palmar erythema, leukonychia)
• Asterixis (hepatic encephalopathy)
• Muscle wasting

PHASES OF CHRONIC HBV INFECTION:

1. IMMUNE TOLERANT (HBeAg-positive chronic infection):
• HBsAg+, HBeAg+, high HBV DNA (>10^7 IU/mL)
• Normal or minimally elevated ALT
• Minimal liver inflammation/fibrosis
• Common in perinatally infected; can last decades

2. IMMUNE ACTIVE (HBeAg-positive chronic hepatitis):
• HBsAg+, HBeAg+, high HBV DNA (>20,000 IU/mL)
• Elevated ALT (often >2x ULN)
• Active liver inflammation; fibrosis progressing
• Treatment indicated

3. INACTIVE CARRIER (HBeAg-negative chronic infection):
• HBsAg+, HBeAg-, anti-HBe+
• Low or undetectable HBV DNA (<2,000 IU/mL)
• Normal ALT, minimal fibrosis
• Good prognosis; monitor for reactivation

4. HBeAg-NEGATIVE CHRONIC HEPATITIS (Reactivation):
• HBsAg+, HBeAg-, anti-HBe+
• Fluctuating HBV DNA (>2,000 IU/mL)
• Elevated ALT (intermittent or persistent)
• Precore/core promoter mutants; more aggressive
• Treatment indicated`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: SEROLOGICAL SCREENING
• HBsAg: Marker of current infection (acute or chronic)
• If HBsAg positive → proceed to full hepatitis B panel

STEP 2: CONFIRM AND CHARACTERIZE INFECTION
• HBsAg, Anti-HBs, Anti-HBc (total and IgM), HBeAg, Anti-HBe
• Interpret pattern to determine infection status (see below)

STEP 3: ASSESS DISEASE ACTIVITY (if chronic HBV confirmed)
• HBV DNA viral load (quantitative PCR)
• ALT, AST, GGT, ALP, bilirubin, albumin, INR
• Assess fibrosis stage

STEP 4: SCREEN FOR COINFECTIONS AND COMPLICATIONS
• HIV, HCV, HDV antibody testing
• AFP and ultrasound for HCC screening

SEROLOGICAL PATTERNS:

ACUTE HEPATITIS B:
• HBsAg+, Anti-HBc IgM+, HBeAg usually +, Anti-HBs-

CHRONIC HEPATITIS B:
• HBsAg+ (>6 months), Anti-HBc IgG+, Anti-HBc IgM-, Anti-HBs-
• HBeAg status and HBV DNA define phase

RESOLVED INFECTION:
• HBsAg-, Anti-HBs+, Anti-HBc+

VACCINATED (IMMUNE):
• HBsAg-, Anti-HBs+ (>10 IU/L), Anti-HBc-

ISOLATED ANTI-HBc:
• HBsAg-, Anti-HBs-, Anti-HBc+ 
• Possible: Resolved infection (window), occult HBV, false positive

DIAGNOSTIC CRITERIA FOR CHRONIC HBV:
• HBsAg positive on two occasions at least 6 months apart OR
• HBsAg positive with Anti-HBc IgG+ and Anti-HBc IgM-

LABORATORY FINDINGS:

LIVER BIOCHEMISTRY:
• ALT: Variable; may be normal or elevated (>2x ULN in immune active phase)
• AST: Typically lower than ALT unless cirrhosis
• ALP/GGT: Mildly elevated or normal
• Bilirubin: Elevated in acute hepatitis, advanced cirrhosis, or flares
• Albumin: Low in cirrhosis
• INR: Prolonged in cirrhosis or acute liver failure

HBV DNA VIRAL LOAD:
• Undetectable to >10^9 IU/mL depending on phase
• Treatment threshold generally ≥2,000 IU/mL (with elevated ALT) or ≥20,000 IU/mL
• Goal of treatment: Sustained suppression (<20 IU/mL or undetectable)

FIBROSIS ASSESSMENT:
• APRI score: (AST/ULN) x 100 / Platelet count; >2.0 suggests cirrhosis
• FIB-4: Age x AST / (Platelet x sqrt ALT); >3.25 suggests advanced fibrosis
• Transient elastography (FibroScan): >11 kPa suggests cirrhosis
• Liver biopsy: Gold standard but invasive; rarely needed`,

    differentialDiagnosis: [
      'Hepatitis A - Fecal-oral transmission, anti-HAV IgM positive, no chronicity',
      'Hepatitis C - Parenteral/sexual transmission, anti-HCV and HCV RNA positive, different serologic profile',
      'Hepatitis D - Requires HBV coinfection, anti-HDV positive, more severe disease',
      'Hepatitis E - Fecal-oral, anti-HEV IgM positive, fulminant in pregnancy',
      'Alcoholic liver disease - History of alcohol use, AST:ALT ratio >2:1, elevated GGT',
      'Non-alcoholic fatty liver disease (NAFLD) - Metabolic syndrome, steatosis on imaging, hepatitis serologies negative',
      'Drug-induced liver injury - Temporal association with medication, negative infectious workup',
      'Autoimmune hepatitis - ANA, anti-smooth muscle antibodies positive, elevated IgG, interface hepatitis on biopsy',
      'Wilson disease - Young patient, low ceruloplasmin, KF rings, 24hr urinary copper elevated',
      'Hemochromatosis - Elevated ferritin and transferrin saturation, HFE gene mutations',
      'Primary biliary cholangitis - Anti-mitochondrial antibodies, cholestatic pattern, middle-aged women',
      'EBV/CMV hepatitis - Heterophile antibodies or specific IgM, atypical lymphocytosis',
      'Leptospirosis - Occupational exposure, conjunctival suffusion, renal involvement',
      'Yellow fever - Endemic areas, vaccination history, hemorrhagic features'
    ],

    investigations: [
      {
        name: 'Hepatitis B Surface Antigen (HBsAg)',
        indication: '🔴 ESSENTIAL - Primary screening test; positive indicates current infection (acute or chronic)',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Core Antibody (Anti-HBc Total)',
        indication: '🔴 ESSENTIAL - Marker of current or past infection; helps interpret HBsAg result',
        availability: 'BASIC'
      },
      {
        name: 'Anti-HBc IgM',
        indication: '🔴 ESSENTIAL - Distinguishes acute from chronic infection; positive in acute hepatitis B',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Surface Antibody (Anti-HBs)',
        indication: '🔴 ESSENTIAL - Marker of immunity (vaccination or resolved infection); >10 IU/L is protective',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests (ALT, AST, ALP, GGT, Bilirubin, Albumin)',
        indication: '🔴 ESSENTIAL - Assess disease activity and liver synthetic function; ALT key for treatment decisions',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count with Platelets',
        indication: '🔴 ESSENTIAL - Thrombocytopenia suggests portal hypertension/cirrhosis; anemia in advanced disease',
        availability: 'BASIC'
      },
      {
        name: 'INR/Prothrombin Time',
        indication: '🔴 ESSENTIAL - Marker of hepatic synthetic function; prolonged in cirrhosis and acute liver failure',
        availability: 'BASIC'
      },
      {
        name: 'HBeAg and Anti-HBe',
        indication: '🟠 IMPORTANT - Determines phase of chronic infection; HBeAg+ indicates high replication; guides treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HBV DNA Quantitative PCR',
        indication: '🟠 IMPORTANT - Viral load; essential for treatment decisions (threshold ≥2,000-20,000 IU/mL) and monitoring response',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HIV Antibody Test',
        indication: '🔴 ESSENTIAL - High coinfection rates in Africa; impacts treatment choice (TDF-based ART if coinfected)',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis C Antibody (Anti-HCV)',
        indication: '🟠 IMPORTANT - Screen for coinfection; dual infection worsens prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis D Antibody (Anti-HDV)',
        indication: '🟠 IMPORTANT - Screen all HBsAg+ patients in endemic areas; HDV causes more severe disease',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Alpha-Fetoprotein (AFP)',
        indication: '🟠 IMPORTANT - HCC screening; check every 6 months in cirrhotics and high-risk patients; >400 ng/mL highly suspicious',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 ESSENTIAL - Assess liver echotexture, portal hypertension signs, screen for HCC (every 6 months in high-risk)',
        availability: 'BASIC'
      },
      {
        name: 'Transient Elastography (FibroScan)',
        indication: '🟡 IF AVAILABLE - Non-invasive fibrosis assessment; >11 kPa suggests cirrhosis; guides treatment decisions',
        availability: 'ADVANCED'
      },
      {
        name: 'Serum Creatinine',
        indication: '🟠 IMPORTANT - Baseline renal function before TDF; monitor during treatment',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM: Verify HBsAg positive; order full hepatitis B panel to characterize infection',
        '⚡ ASSESS: Determine if acute (<6 months HBsAg+, IgM+) or chronic (>6 months, IgM-)',
        '⚡ EVALUATE: Check ALT, HBV DNA, HBeAg status to determine disease phase',
        '⚡ STAGE: Assess for cirrhosis using clinical signs, platelets, APRI/FIB-4 scores, ultrasound',
        '⚡ SCREEN: Test for HIV, HCV, and HDV coinfection (all HBsAg+ patients)',
        '⚡ VACCINATE: Offer hepatitis A vaccine if not immune (anti-HAV negative)',
        '⚡ COUNSEL: Avoid alcohol, hepatotoxic drugs; discuss transmission prevention',
        '⚡ CONTACTS: Screen and vaccinate household and sexual contacts',
        '⚡ PREGNANCY: If HBsAg+ pregnant woman - plan for birth-dose vaccine plus HBIG for infant',
        '⚡ HCC SURVEILLANCE: Initiate ultrasound plus AFP every 6 months if cirrhosis or high risk',
        '⚡ REFER: If meeting treatment criteria or cirrhosis present, refer for antiviral therapy'
      ],
      
      pharmacological: [
        {
          drug: 'Tenofovir Disoproxil Fumarate (TDF)',
          indication: '🔴 FIRST-LINE - Preferred antiviral in Africa; high barrier to resistance; safe in pregnancy; included in HIV ART',
          dose: 'Adults: 300mg PO once daily. Adolescents ≥35kg: 300mg once daily. Children ≥2yr: 8mg/kg/day (max 300mg). Renal dosing: CrCl 30-49: 300mg q48h; CrCl 10-29: 300mg q72-96h',
          route: 'PO',
          duration: 'Long-term/indefinite for most chronic HBV; minimum until treatment endpoints met',
          contraindications: ['CrCl <10 mL/min (use TAF instead)', 'Concurrent nephrotoxic drugs (use with caution)'],
          sideEffects: ['Nephrotoxicity (rare)', 'Fanconi syndrome', 'Bone mineral density loss', 'Nausea', 'GI upset'],
          alternatives: ['Entecavir', 'Tenofovir alafenamide (TAF)']
        },
        {
          drug: 'Entecavir (ETV)',
          indication: '🔴 FIRST-LINE ALTERNATIVE - High potency, high barrier to resistance; preferred if renal concerns; NOT for HIV coinfection unless fully suppressive ART',
          dose: 'Adults (treatment-naive): 0.5mg PO once daily. Adults (lamivudine-experienced): 1mg once daily. Children ≥2yr: Weight-based dosing (0.015mg/kg up to 0.5mg). Renal dosing required if CrCl <50',
          route: 'PO',
          duration: 'Long-term/indefinite',
          contraindications: ['HIV coinfection without suppressive ART (risk of HIV resistance)', 'Lamivudine-resistant HBV (use 1mg dose)'],
          sideEffects: ['Headache', 'Fatigue', 'Dizziness', 'Nausea', 'Elevated ALT (rare)', 'Lactic acidosis (rare)'],
          alternatives: ['Tenofovir (TDF or TAF)']
        },
        {
          drug: 'Tenofovir Alafenamide (TAF)',
          indication: '🟠 ALTERNATIVE - Improved renal and bone safety vs TDF; use if renal impairment or osteoporosis; expensive',
          dose: 'Adults: 25mg PO once daily. Not recommended if CrCl <15 without dialysis',
          route: 'PO',
          duration: 'Long-term/indefinite',
          contraindications: ['Severe renal impairment without dialysis', 'Pregnancy (limited data, TDF preferred)'],
          sideEffects: ['Headache', 'Abdominal pain', 'Fatigue', 'Cough', 'Nausea'],
          alternatives: ['TDF', 'Entecavir']
        },
        {
          drug: 'Lamivudine (3TC)',
          indication: '🟡 ALTERNATIVE - Low cost, widely available; HIGH resistance rate (70% at 5 years); use only if TDF/ETV unavailable',
          dose: 'Adults: 100mg PO once daily (for HBV). HBV/HIV coinfection: 150mg PO twice daily OR 300mg once daily (as part of ART). Renal dosing required if CrCl <50',
          route: 'PO',
          duration: 'Long-term; monitor for resistance',
          contraindications: ['As monotherapy in HIV coinfection'],
          sideEffects: ['Headache', 'Nausea', 'Fatigue', 'Pancreatitis (rare)', 'Lactic acidosis (rare)'],
          alternatives: ['Tenofovir', 'Entecavir']
        },
        {
          drug: 'Pegylated Interferon alfa-2a (PEG-IFN)',
          indication: '🟡 ALTERNATIVE - Finite duration (48 weeks); higher HBeAg/HBsAg seroconversion rates; poorly tolerated; contraindicated in cirrhosis',
          dose: 'Adults: 180mcg SC once weekly for 48 weeks',
          route: 'SC',
          duration: '48 weeks',
          contraindications: ['Decompensated cirrhosis', 'Autoimmune disease', 'Severe depression', 'Pregnancy', 'Uncontrolled seizures', 'Cytopenia'],
          sideEffects: ['Flu-like symptoms', 'Fatigue', 'Depression', 'Cytopenia', 'Thyroid dysfunction', 'Alopecia'],
          alternatives: ['Oral nucleos(t)ide analogues (TDF, ETV)']
        },
        {
          drug: 'Hepatitis B Immune Globulin (HBIG)',
          indication: '🔴 PREVENTION - Post-exposure prophylaxis; infants born to HBsAg+ mothers; post-liver transplant',
          dose: 'Neonates: 0.5mL (100-200 IU) IM within 12 hours of birth. Adults (post-exposure): 0.06mL/kg IM as soon as possible',
          route: 'IM',
          duration: 'Single dose (repeat doses post-transplant)',
          contraindications: ['IgA deficiency (anaphylaxis risk)'],
          sideEffects: ['Injection site pain', 'Headache', 'Fever', 'Anaphylaxis (rare)'],
          alternatives: ['If unavailable, ensure immediate vaccination']
        },
        {
          drug: 'Hepatitis B Vaccine',
          indication: '🔴 PREVENTION - All infants (birth dose critical), unvaccinated adults, high-risk groups, contacts of HBsAg+ individuals',
          dose: 'Infants: 0.5mL (10mcg) IM at 0, 1-2, 6 months. Adults: 1.0mL (20mcg) IM at 0, 1, 6 months. Dialysis/immunocompromised: 40mcg x 4 doses. Check Anti-HBs 1-2 months after series',
          route: 'IM (deltoid in adults, anterolateral thigh in infants)',
          duration: '3-dose series; boosters if anti-HBs <10 IU/L in high-risk',
          contraindications: ['Severe allergic reaction to previous dose or vaccine component'],
          sideEffects: ['Injection site soreness', 'Fever', 'Fatigue', 'Headache'],
          alternatives: ['Combined hepatitis A+B vaccine (Twinrix) for adults']
        },
        {
          drug: 'Lactulose',
          indication: '🟠 SUPPORTIVE - For hepatic encephalopathy in cirrhotic patients',
          dose: 'Adults: 15-30mL PO q8-12h, titrate to 2-3 soft bowel movements daily. Acute encephalopathy: 30-45mL PO q1-2h until bowel movement',
          route: 'PO/NG/Rectal',
          duration: 'Ongoing for encephalopathy prevention',
          contraindications: ['Galactosemia'],
          sideEffects: ['Bloating', 'Flatulence', 'Diarrhea', 'Abdominal cramps'],
          alternatives: ['Rifaximin (add to lactulose for recurrent encephalopathy)']
        },
        {
          drug: 'Spironolactone + Furosemide',
          indication: '🟠 SUPPORTIVE - For ascites management in cirrhotic patients',
          dose: 'Start: Spironolactone 100mg + Furosemide 40mg PO daily (100:40 ratio). Max: Spironolactone 400mg + Furosemide 160mg daily. Target: 0.5kg/day weight loss (no edema) or 1kg/day (with edema)',
          route: 'PO',
          duration: 'Ongoing for ascites control',
          contraindications: ['Severe hyponatremia (<125)', 'Acute kidney injury', 'Severe encephalopathy'],
          sideEffects: ['Gynecomastia (spironolactone)', 'Hyperkalemia', 'Hypokalemia', 'Renal impairment'],
          alternatives: ['Tolvaptan for refractory cases; therapeutic paracentesis']
        },
        {
          drug: 'Propranolol',
          indication: '🟠 SUPPORTIVE - Primary prophylaxis of variceal bleeding in cirrhotics with varices',
          dose: 'Adults: Start 20mg PO twice daily. Target: Resting heart rate 55-60 bpm or 25% reduction',
          route: 'PO',
          duration: 'Indefinite for variceal prophylaxis',
          contraindications: ['Severe asthma', 'Heart block', 'Severe bradycardia', 'Decompensated heart failure'],
          sideEffects: ['Bradycardia', 'Fatigue', 'Dizziness', 'Bronchospasm'],
          alternatives: ['Carvedilol', 'Nadolol', 'Endoscopic band ligation']
        }
      ],
      
      nonPharmacological: [
        'ALCOHOL CESSATION: Absolute avoidance of alcohol; accelerates liver fibrosis and HCC risk',
        'HEPATOTOXIC DRUG AVOIDANCE: Avoid or minimize paracetamol (limit to <2g/day), NSAIDs, herbal remedies',
        'WEIGHT MANAGEMENT: Maintain healthy weight; obesity worsens liver disease (NAFLD overlap)',
        'NUTRITION: Balanced diet; protein restriction only in acute encephalopathy; avoid raw shellfish (Vibrio risk)',
        'VACCINATION: Hepatitis A vaccine if non-immune; pneumococcal, influenza vaccines recommended',
        'INFECTION CONTROL: Cover wounds, do not share razors/toothbrushes, safe sex practices, disclose status to partners',
        'CONTACT SCREENING: Screen and vaccinate all household members and sexual partners',
        'REGULAR MONITORING: Lifelong follow-up even on treatment; HCC surveillance in high-risk',
        'SUPPORT GROUPS: Connect patients with peer support; address stigma',
        'AVOID TRADITIONAL REMEDIES: Many herbal products are hepatotoxic; counsel on risks',
        'PREGNANCY PLANNING: Women should discuss pregnancy plans; TDF safe in pregnancy; breastfeeding permitted if infant vaccinated'
      ],
      
      surgical: [
        'LIVER TRANSPLANTATION: For decompensated cirrhosis (MELD ≥15) or HCC within Milan criteria; HBIG + antivirals to prevent recurrence',
        'HCC TREATMENT: Resection if single lesion in non-cirrhotic or compensated cirrhosis; RFA/TACE for unresectable',
        'TACE (Transarterial Chemoembolization): For intermediate-stage HCC; palliative',
        'RFA (Radiofrequency Ablation): For small HCC (<3cm, ≤3 lesions); curative intent',
        'TIPS (Transjugular Intrahepatic Portosystemic Shunt): For refractory ascites or recurrent variceal bleeding',
        'PARACENTESIS: Large-volume for symptomatic tense ascites; replace albumin (6-8g per liter removed >5L)',
        'ENDOSCOPIC BAND LIGATION: For esophageal varices - treatment and secondary prophylaxis'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Acute liver failure - encephalopathy, coagulopathy (INR >1.5), jaundice; needs transplant evaluation',
        '🔴 IMMEDIATE REFERRAL: Variceal hemorrhage - hematemesis or melena with known/suspected cirrhosis',
        '🔴 IMMEDIATE REFERRAL: Suspected HCC - new hepatic lesion, rising AFP, weight loss in chronic HBV patient',
        '🟠 URGENT REFERRAL: Decompensated cirrhosis - new ascites, encephalopathy, jaundice; needs specialist management',
        '🟠 URGENT REFERRAL: Severe acute hepatitis B - deep jaundice, coagulopathy, declining synthetic function',
        '🟠 URGENT REFERRAL: Treatment failure - persistent viremia despite adherence to first-line antiviral',
        '🟠 URGENT REFERRAL: HBV/HIV coinfection - for coordinated ART management',
        '🟡 ROUTINE REFERRAL: Chronic hepatitis B meeting treatment criteria - for antiviral initiation',
        '🟡 ROUTINE REFERRAL: HDV coinfection - limited treatment options, specialist management',
        '🟡 ROUTINE REFERRAL: Pregnancy in HBsAg+ woman - for PMTCT planning and antiviral consideration in third trimester'
      ],
      
      monitoring: [
        'ALT: Every 3-6 months in untreated; every 3 months initially on treatment, then every 6-12 months when stable',
        'HBV DNA: Every 3-6 months on treatment until undetectable; then every 6-12 months; annually if untreated inactive carrier',
        'HBeAg/Anti-HBe: Every 6-12 months if HBeAg+; check for seroconversion',
        'HBsAg: Annually; quantitative HBsAg if available to predict HBsAg loss',
        'CREATININE: Baseline and every 3-6 months on TDF; monitor for nephrotoxicity',
        'AFP + ULTRASOUND: Every 6 months for HCC surveillance in cirrhotics and high-risk non-cirrhotics',
        'FIBROSIS ASSESSMENT: APRI/FIB-4 annually; FibroScan if available; repeat to assess regression on treatment',
        'ADHERENCE: Assess at each visit; viral breakthrough often due to non-adherence'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE HEPATITIS B:
• Monitor weekly until improving, then monthly until HBsAg negative
• Repeat HBsAg at 6 months to confirm clearance or diagnose chronicity
• If HBsAg persists >6 months → chronic hepatitis B; manage accordingly
• No antiviral needed for most acute cases (>95% clear spontaneously in adults)
• Exceptions: Severe acute hepatitis, immunocompromised, or protracted course

CHRONIC HEPATITIS B - NOT ON TREATMENT:
• ALT every 3-6 months
• HBV DNA every 6-12 months
• HBeAg annually
• AFP and ultrasound every 6 months if cirrhotic; annually if non-cirrhotic
• Reassess treatment eligibility at each visit

CHRONIC HEPATITIS B - ON TREATMENT:
• ALT and HBV DNA at 3 months, then every 3-6 months
• HBeAg every 6-12 months (if initially HBeAg+)
• Creatinine every 3-6 months (on TDF)
• AFP and ultrasound every 6 months (continue regardless of treatment response)
• Assess fibrosis annually to document regression

TREATMENT ENDPOINTS AND STOPPING RULES:
• Ideal endpoint: HBsAg loss with anti-HBs seroconversion (functional cure; <10%/year)
• Satisfactory endpoint (HBeAg+ patients): HBeAg seroconversion + undetectable HBV DNA maintained ≥12 months → can consider stopping with close monitoring
• HBeAg-negative patients: Generally indefinite treatment; stopping associated with high relapse
• Cirrhotics: NEVER stop treatment (risk of severe flare and decompensation)

SPECIAL POPULATIONS:

Pregnancy:
• Screen all pregnant women at first antenatal visit
• If HBsAg+: Check HBV DNA in second trimester
• If HBV DNA >200,000 IU/mL: Start TDF at 28-32 weeks to reduce MTCT
• Infant: Birth-dose HBV vaccine + HBIG within 12 hours; complete vaccine series
• Breastfeeding: Safe if infant vaccinated

HBV/HIV Coinfection:
• Start TDF-based ART regardless of CD4 count
• Never use lamivudine alone (rapid HBV resistance)
• Monitor for immune reconstitution hepatitis flare after ART initiation

Post-Immunosuppression:
• Screen all patients for HBsAg and anti-HBc before chemotherapy, biologics, or transplant
• HBsAg+: Antiviral prophylaxis throughout and 6-12 months after stopping immunosuppression
• Isolated anti-HBc+: Monitor closely; prophylaxis if high-risk (rituximab, stem cell transplant)`
    },

    redFlags: [
      '🔴 CRITICAL: Hepatic encephalopathy (confusion, asterixis, somnolence) - decompensation or acute liver failure; urgent management',
      '🔴 CRITICAL: INR >1.5 with jaundice in acute hepatitis - impending acute liver failure; transfer for transplant evaluation',
      '🔴 CRITICAL: Variceal hemorrhage (hematemesis, melena with cirrhosis) - life-threatening; resuscitate and urgent endoscopy',
      '🔴 CRITICAL: New hepatic mass or rapidly rising AFP - suspect HCC; urgent imaging and specialist referral',
      '🔴 CRITICAL: Rapid clinical deterioration in chronic HBV - may indicate HCC, severe flare, or superinfection (HDV, HAV)',
      '🟠 URGENT: New or increasing ascites - decompensation; assess for SBP, renal function, HCC',
      '🟠 URGENT: Severe ALT flare (>10x ULN) - may be seroconversion, reactivation, or superinfection; close monitoring',
      '🟠 URGENT: Persistent viremia on treatment - assess adherence, consider resistance; may need therapy change',
      '🟠 URGENT: Jaundice in previously stable chronic HBV patient - evaluate for flare, HCC, or biliary obstruction',
      '🟡 CAUTION: Isolated anti-HBc in patient starting immunosuppression - risk of reactivation; consider prophylaxis',
      '🟡 CAUTION: Pregnancy in woman on entecavir - switch to TDF (safer in pregnancy)',
      '🟡 CAUTION: Rising creatinine on TDF - possible nephrotoxicity; consider switch to TAF or entecavir'
    ],

    complications: [
      'Cirrhosis - Develops in 15-40% of untreated chronic HBV over 20-30 years; irreversible but progression can halt with treatment',
      'Hepatocellular carcinoma (HCC) - 2-5% annual risk in cirrhotics; can occur without cirrhosis; major cause of death',
      'Acute liver failure - Rare in acute HBV (<1%) but high mortality; more common with HDV coinfection',
      'Portal hypertension - Consequence of cirrhosis; leads to varices, ascites, splenomegaly',
      'Esophageal/gastric varices - Risk of life-threatening hemorrhage; require prophylaxis and surveillance',
      'Ascites - Sign of decompensation; risk of SBP; impacts prognosis',
      'Spontaneous bacterial peritonitis (SBP) - Infection of ascitic fluid; high mortality if untreated',
      'Hepatic encephalopathy - Confusion to coma; precipitated by GI bleed, infection, constipation',
      'Hepatorenal syndrome - Functional renal failure in advanced cirrhosis; poor prognosis',
      'HBV reactivation - Flare during immunosuppression; can be severe or fatal; preventable with prophylaxis',
      'HDV superinfection - Accelerates progression to cirrhosis and HCC; limited treatment options',
      'Extrahepatic manifestations - Polyarteritis nodosa, membranous glomerulonephritis, cryoglobulinemia (uncommon)',
      'Vertical transmission - MTCT leads to chronic infection in 90% of infected infants; preventable with vaccine + HBIG'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

ACUTE HEPATITIS B:
• >95% of immunocompetent adults recover fully and develop immunity
• <1% develop fulminant hepatic failure (higher if HDV coinfection)
• Fulminant hepatitis mortality: 80% without transplant

CHRONIC HEPATITIS B (NATURAL HISTORY WITHOUT TREATMENT):
• 15-40% progress to cirrhosis over 20-30 years
• Annual HCC incidence: 0.2-0.6% (non-cirrhotic), 2-5% (cirrhotic)
• 5-year survival in compensated cirrhosis: 80-85%
• 5-year survival in decompensated cirrhosis: 14-35%

WITH ANTIVIRAL TREATMENT:
• Viral suppression achieved in >95% with TDF/ETV
• Fibrosis regression documented in 50-70% after 5 years of treatment
• HCC risk reduced by 50-70% (but not eliminated, especially in cirrhotics)
• HBsAg loss (functional cure): <10% over 5-10 years of nucleos(t)ide therapy
• Survival approaches general population in non-cirrhotic patients achieving sustained viral suppression

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis before cirrhosis
• Sustained viral suppression with treatment
• HBeAg seroconversion
• HBsAg loss (functional cure)
• No HDV/HCV/HIV coinfection
• Absence of alcohol use
• Younger age at treatment initiation

Worse Outcomes:
• Cirrhosis at diagnosis
• HCC development
• HDV coinfection (more rapid progression)
• HIV coinfection (if untreated)
• Continued alcohol use
• Poor treatment adherence
• Male sex (higher HCC risk)
• Family history of HCC
• High HBV DNA levels off treatment`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

VACCINATION (MOST EFFECTIVE MEASURE):
• Universal infant vaccination: Birth dose + 2-3 additional doses
• Birth dose within 24 hours: Prevents perinatal transmission; critical in endemic areas
• Catch-up vaccination: All unvaccinated children and high-risk adults
• High-risk groups: Healthcare workers, dialysis patients, PWID, MSM, sex workers, household contacts of HBsAg+ individuals
• Post-vaccination testing: Check anti-HBs 1-2 months after series in high-risk; >10 IU/L is protective

SAFE PRACTICES:
• Safe injection practices in healthcare settings
• Blood supply screening (mandatory)
• Harm reduction for PWID: Needle exchange, opioid substitution therapy
• Safe sex practices: Condoms reduce transmission
• Avoid sharing personal items: Razors, toothbrushes, nail clippers

SECONDARY PREVENTION:

SCREENING PROGRAMS:
• All pregnant women (antenatal screening)
• Blood donors
• High-risk populations (PWID, MSM, healthcare workers, dialysis patients)
• HIV-positive individuals
• Household contacts of HBsAg+ individuals
• Patients before immunosuppressive therapy

PREVENTION OF MOTHER-TO-CHILD TRANSMISSION (PMTCT):
• Screen all pregnant women for HBsAg
• HBsAg+ mothers: Check HBV DNA; consider TDF if >200,000 IU/mL in third trimester
• Infant: Birth-dose vaccine + HBIG within 12 hours; complete vaccine series
• With proper PMTCT, transmission reduced to <2%

POST-EXPOSURE PROPHYLAXIS (PEP):
• HBIG + HBV vaccine series for:
  - Infants born to HBsAg+ mothers
  - Unvaccinated individuals with percutaneous/mucosal exposure to HBsAg+ blood
  - Sexual contacts of acute HBV cases
• HBIG most effective within 24 hours; vaccine series for long-term protection

TERTIARY PREVENTION:
• Antiviral treatment to prevent disease progression
• HCC surveillance in all at-risk patients
• Avoid hepatotoxins (alcohol, certain medications)
• Vaccinate against hepatitis A
• Screen and vaccinate contacts

PUBLIC HEALTH MEASURES:
• National immunization programs with birth-dose
• Blood safety programs
• Surveillance and reporting
• Health education to reduce stigma
• Integration with HIV programs (shared transmission routes)`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS HEPATITIS B?
Hepatitis B is a viral infection that affects your liver. The virus can stay in your body for a short time (acute infection) or for life (chronic infection). Many people with chronic hepatitis B feel healthy for years, but the virus can slowly damage your liver over time if not treated. With proper care and treatment, most people with hepatitis B can live long, healthy lives.

HOW IS HEPATITIS B SPREAD?
• From mother to baby during birth
• Through contact with infected blood (sharing needles, razors, toothbrushes)
• Through unprotected sex with an infected person
• Through unsafe medical procedures or needle pricks

Hepatitis B is NOT spread by:
• Sharing food or water
• Hugging, kissing, or shaking hands
• Breastfeeding (if baby is vaccinated)
• Coughing or sneezing

HOW DO I PROTECT OTHERS?
• Tell your family members and sexual partners so they can get tested and vaccinated
• Do not donate blood, organs, or semen
• Cover all cuts and open sores
• Do not share razors, toothbrushes, nail clippers, or needles
• Use condoms during sex
• Clean up any blood spills with bleach

DO I NEED TREATMENT?
Not everyone with hepatitis B needs medicine. Your doctor will do blood tests to check:
• How much virus is in your blood
• If your liver is inflamed or damaged
Based on these results, your doctor will decide if you need treatment.

IF YOU ARE ON TREATMENT:
• Take your medicine every day at the same time - do not miss doses
• Stopping medicine without doctor advice can cause serious liver flares
• You will need regular blood tests to check how treatment is working
• Treatment may be for many years or lifelong

LIFESTYLE RECOMMENDATIONS:
• DO NOT drink alcohol - it damages your liver faster
• Eat a healthy balanced diet with fruits, vegetables, and protein
• Maintain a healthy weight
• Avoid traditional herbal remedies - many can harm your liver
• Tell all your doctors that you have hepatitis B before taking any new medicine
• Get vaccinated against hepatitis A
• Get regular check-ups as advised by your doctor

LIVER CANCER SCREENING:
• Hepatitis B increases risk of liver cancer
• You need ultrasound and blood tests (AFP) every 6 months if you have cirrhosis or other risk factors
• Early detection of liver cancer improves outcomes

IF YOU ARE PREGNANT:
• Your baby can be protected from hepatitis B
• Baby must receive hepatitis B vaccine and HBIG within 12 hours of birth
• Complete all follow-up vaccines for your baby
• You can breastfeed if your baby receives the vaccines
• Tell your doctor if you are taking hepatitis B medicine

WHEN TO SEEK MEDICAL HELP:
• Yellow eyes or skin (jaundice)
• Dark urine or pale stools
• Severe tiredness or weakness
• Swelling of belly or legs
• Confusion or difficulty thinking clearly
• Vomiting blood or black stools

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MEDICAL DISCLAIMER
═══════════════════════════════════════════════════════════════════════════════

This information is provided for education only and does not replace advice from your doctor or healthcare provider. Hepatitis B is a serious condition that requires ongoing medical care. Every person situation is different. Follow your doctor instructions about testing, treatment, and follow-up visits. If you have questions or concerns, please speak with your healthcare provider.`,

    references: [
      'WHO. Hepatitis B vaccines: WHO position paper, July 2017. Wkly Epidemiol Rec. 2017;92:369-392.',
      'WHO. Guidelines for the prevention, care and treatment of persons with chronic hepatitis B infection. World Health Organization; 2015.',
      'Terrault NA, Lok ASF, McMahon BJ, et al. Update on prevention, diagnosis, and treatment of chronic hepatitis B: AASLD 2018 hepatitis B guidance. Hepatology. 2018;67(4):1560-1599.',
      'European Association for the Study of the Liver. EASL 2017 Clinical Practice Guidelines on the management of hepatitis B virus infection. J Hepatol. 2017;67(2):370-398.',
      'Razavi-Shearer D, Gamkrelidze I, Nguyen MH, et al. Global prevalence, treatment, and prevention of hepatitis B virus infection in 2016: a modelling study. Lancet Gastroenterol Hepatol. 2018;3(6):383-403.',
      'Spearman CW, Afihene M, Ally R, et al. Hepatitis B in sub-Saharan Africa: strategies to achieve the 2030 elimination targets. Lancet Gastroenterol Hepatol. 2017;2(12):900-909.',
      'Polaris Observatory Collaborators. Global prevalence, treatment, and prevention of hepatitis B virus infection in 2016: a modelling study. Lancet Gastroenterol Hepatol. 2018;3(6):383-403.',
      'Lemoine M, Eholie S, Lacombe K. Reducing the neglected burden of viral hepatitis in Africa: strategies for a global approach. J Hepatol. 2015;62(2):469-476.',
      'Shimakawa Y, Lemoine M, Njai HF, et al. Natural history of chronic HBV infection in West Africa: a longitudinal population-based study from The Gambia. Gut. 2016;65(12):2007-2016.',
      'Stanaway JD, Flaxman AD, Naghavi M, et al. The global burden of viral hepatitis from 1990 to 2013: findings from the Global Burden of Disease Study 2013. Lancet. 2016;388(10049):1081-1088.',
      'Ott JJ, Stevens GA, Groeger J, Wiersma ST. Global epidemiology of hepatitis B virus infection: new estimates of age-specific HBsAg seroprevalence and endemicity. Vaccine. 2012;30(12):2212-2219.',
      'Sarin SK, Kumar M, Lau GK, et al. Asian-Pacific clinical practice guidelines on the management of hepatitis B: a 2015 update. Hepatol Int. 2016;10(1):1-98.',
      'Lampertico P, Agarwal K, Berg T, et al. EASL 2017 Clinical Practice Guidelines on the management of hepatitis B virus infection. J Hepatol. 2017;67(2):370-398.',
      'Revill PA, Chisari FV, Block JM, et al. A global scientific strategy to cure hepatitis B. Lancet Gastroenterol Hepatol. 2019;4(7):545-558.',
      'Nelson NP, Easterbrook PJ, McMahon BJ. Epidemiology of Hepatitis B Virus Infection and Impact of Vaccination on Disease. Clin Liver Dis. 2016;20(4):607-628.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 74. HEPATITIS C
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '74',
    name: 'Hepatitis C',
    slug: 'hepatitis-c',
    specialty: 'Infectious Disease',
    bodySystem: 'Gastrointestinal',
    icdCode: 'B17.1',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Hepatitis C is a blood-borne viral infection caused by the hepatitis C virus (HCV) that primarily affects the liver. Unlike hepatitis B, the majority of acute HCV infections (55-85%) progress to chronic infection. Globally, an estimated 58 million people have chronic HCV infection, with approximately 1.5 million new infections and 290,000 deaths annually, primarily from cirrhosis and hepatocellular carcinoma (HCC).

The pathophysiology involves HCV entering hepatocytes via specific receptors (CD81, SR-B1, claudin-1, occludin). The virus replicates in the cytoplasm using an RNA-dependent RNA polymerase, which lacks proofreading ability, leading to high genetic variability and multiple genotypes (1-8). Liver damage results from both direct viral cytopathicity and immune-mediated injury. Chronic inflammation leads to progressive fibrosis and eventually cirrhosis in 15-30% of patients over 20-30 years.

Africa has an estimated 10 million people living with chronic HCV, with highest prevalence in Egypt (historically up to 15% due to parenteral antischistosomal therapy campaigns), Cameroon, Gabon, and Central African Republic. Genotype 4 predominates in Egypt and Central Africa, while genotype 1 is common in Southern Africa. Major transmission routes include unsafe healthcare practices, injection drug use, and inadequate blood screening. The high cost of diagnostics and treatment has historically limited access, though generic DAAs are increasingly available.

The revolutionary development of direct-acting antivirals (DAAs) has transformed HCV from a difficult-to-treat chronic disease to a curable infection. Modern pangenotypic DAA regimens achieve sustained virologic response (SVR) rates exceeding 95% with 8-12 weeks of oral therapy, representing a functional cure.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - HCV is CURABLE: DAAs achieve >95% SVR (cure) rates with 8-12 weeks of oral therapy; treat ALL patients with chronic HCV

🔴 CRITICAL POINT 2 - Anti-HCV positive requires HCV RNA confirmation; anti-HCV remains positive lifelong even after cure (indicates exposure, not active infection)

🔴 CRITICAL POINT 3 - Pangenotypic regimens (sofosbuvir/velpatasvir) simplify treatment by eliminating need for genotyping in resource-limited settings

🔴 CRITICAL POINT 4 - Assess for cirrhosis BEFORE treatment; cirrhotics need longer treatment (12 weeks) and continued HCC surveillance even after SVR

🟠 CRITICAL POINT 5 - Check for HBV coinfection before starting DAAs; risk of HBV reactivation during/after HCV treatment

🟠 CRITICAL POINT 6 - Drug interactions are critical: Check ALL medications before starting DAAs, especially PPIs, statins, anticonvulsants, and HIV drugs

🟠 CRITICAL POINT 7 - SVR12 (undetectable HCV RNA 12 weeks post-treatment) equals cure; no further HCV monitoring needed unless reinfection risk

🟡 CRITICAL POINT 8 - Unlike HBV, there is NO vaccine for HCV; prevention relies on harm reduction and safe practices

🟡 CRITICAL POINT 9 - Reinfection can occur after cure in high-risk individuals; counsel on risk reduction and consider periodic testing`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 58 million people with chronic HCV infection worldwide (2019)
• 1.5 million new infections annually
• 290,000 deaths per year (primarily cirrhosis and HCC)
• Global prevalence approximately 0.7% (ranges from 0.5-2.3% by region)
• Six major genotypes (GT1-6) with geographic variation
• GT1 most common globally (46%), GT3 second (22%), GT4 in Africa/Middle East (13%)

AFRICAN EPIDEMIOLOGY:
• Estimated 10 million people with chronic HCV in Africa
• Highest prevalence: Egypt (historically 15%, now declining with treatment programs), Cameroon (6%), Gabon (6%), Central African Republic, Burundi
• West Africa: Prevalence 2-5%; GT1 and GT2 predominant
• Central Africa: Prevalence 3-6%; GT4 common
• East Africa: Prevalence 1-3%; GT1 and GT4
• Southern Africa: Prevalence <2%; GT1 and GT5
• Egypt: Massive historical iatrogenic transmission; now has worlds largest HCV treatment program
• Major risk factors in Africa: Unsafe injections, inadequate blood screening, traditional practices (scarification, circumcision), PWID

TRANSMISSION ROUTES:
• Parenteral (most efficient): Blood transfusion, unsafe injections, needle sharing (PWID), needlestick injuries
• Sexual transmission: Low risk in monogamous heterosexual couples; higher in MSM, especially with HIV coinfection
• Vertical (mother-to-child): 4-8% if HCV RNA positive; higher if HIV coinfected (up to 20%)
• Healthcare-associated: Contaminated equipment, dialysis, unsafe procedures
• NOT transmitted by: Breastfeeding (unless cracked/bleeding nipples), casual contact, sharing food/water

NATURAL HISTORY:
• Acute infection: 15-45% spontaneous clearance within 6 months; rest become chronic
• Chronic infection: 15-30% develop cirrhosis over 20-30 years
• Cirrhosis: 1-5% annual risk of HCC; 3-6% annual risk of decompensation
• Cofactors accelerating progression: Alcohol, HBV coinfection, HIV coinfection, NAFLD, male sex, older age at infection`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ACUTE HEPATITIS C (Often Asymptomatic):
• Incubation period: 2-12 weeks (average 6-7 weeks)
• 70-80% of acute infections are asymptomatic
• When symptomatic: Fatigue, malaise, anorexia, nausea, RUQ discomfort
• Jaundice occurs in only 20-30% of symptomatic cases
• Fulminant hepatitis is rare (<1%)

CHRONIC HEPATITIS C:
• Majority (70-80%) are asymptomatic for years to decades
• Fatigue is most common symptom (50-70%)
• RUQ discomfort, anorexia, nausea (non-specific)
• Often diagnosed incidentally or at advanced disease stage
• Extrahepatic manifestations (15-40% of patients)

EXTRAHEPATIC MANIFESTATIONS:
• Mixed cryoglobulinemia (most common): Purpura, arthralgia, glomerulonephritis
• Autoimmune: Sjogren syndrome, thyroid disorders, lichen planus
• Hematologic: B-cell lymphoma, thrombocytopenia
• Metabolic: Insulin resistance, type 2 diabetes
• Dermatologic: Porphyria cutanea tarda, pruritus
• Neurologic: Peripheral neuropathy, cognitive impairment (brain fog)
• Renal: Membranoproliferative glomerulonephritis

CIRRHOSIS (15-30% of chronic HCV over 20-30 years):
• Compensated: Often asymptomatic; hepatomegaly, splenomegaly
• Decompensated: Jaundice, ascites, variceal bleeding, encephalopathy, coagulopathy

PHYSICAL EXAMINATION:

ACUTE/EARLY CHRONIC:
• Often normal examination
• May have mild hepatomegaly
• Jaundice (uncommon)

ADVANCED CHRONIC/CIRRHOSIS:
• Hepatomegaly (early) or small liver (late cirrhosis)
• Splenomegaly
• Spider angiomata, palmar erythema
• Jaundice, scleral icterus
• Ascites, peripheral edema
• Asterixis (hepatic encephalopathy)
• Muscle wasting
• Gynecomastia (males)
• Signs of extrahepatic manifestations (purpura, arthritis)

FIBROSIS STAGING (METAVIR):
• F0: No fibrosis
• F1: Portal fibrosis without septa
• F2: Portal fibrosis with few septa
• F3: Numerous septa without cirrhosis (bridging fibrosis)
• F4: Cirrhosis`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: SCREENING (Anti-HCV Antibody)
• Indicates exposure to HCV (current or past infection)
• Remains positive lifelong, even after cure
• If positive → confirm with HCV RNA

STEP 2: CONFIRMATION (HCV RNA PCR)
• Detectable HCV RNA = active infection (acute or chronic)
• Undetectable HCV RNA with positive anti-HCV = cleared infection (spontaneous or treatment-induced)
• Quantitative viral load not required for treatment decision with pangenotypic DAAs

STEP 3: ASSESS DISEASE STAGE
• Determine presence/absence of cirrhosis
• Methods: APRI, FIB-4, FibroScan, or clinical assessment
• Impacts treatment duration and post-cure monitoring

STEP 4: PRE-TREATMENT EVALUATION
• HBV status (HBsAg, anti-HBc) - risk of reactivation
• HIV status
• Genotype (if non-pangenotypic treatment planned)
• Renal function, CBC, LFTs
• Drug interaction check

DIAGNOSTIC CRITERIA:

ACUTE HEPATITIS C:
• Anti-HCV seroconversion (negative to positive) OR
• HCV RNA positive with documented negative within prior 6 months OR
• HCV RNA positive with ALT >10x ULN and no other cause

CHRONIC HEPATITIS C:
• HCV RNA positive for >6 months OR
• HCV RNA positive with anti-HCV positive and no evidence of acute infection

RESOLVED INFECTION:
• Anti-HCV positive, HCV RNA undetectable (spontaneous or treatment-induced)

LABORATORY FINDINGS:

LIVER BIOCHEMISTRY:
• ALT: May be normal or mildly elevated (often <3x ULN); normal ALT does not exclude significant fibrosis
• AST: Usually lower than ALT unless cirrhosis (AST/ALT ratio >1 suggests cirrhosis)
• ALP/GGT: Usually normal or mildly elevated
• Bilirubin: Normal unless cirrhosis or acute hepatitis
• Albumin: Low in cirrhosis
• INR: Prolonged in cirrhosis

HCV VIRAL MARKERS:
• Anti-HCV: Screening test; positive indicates exposure
• HCV RNA (qualitative or quantitative): Confirms active infection
• HCV genotype: GT1-6; determines treatment regimen if not using pangenotypic

FIBROSIS ASSESSMENT:
• APRI: (AST/ULN) x 100 / Platelet count; <0.5 excludes cirrhosis; >2.0 suggests cirrhosis
• FIB-4: Age x AST / (Platelet x sqrt ALT); <1.45 excludes advanced fibrosis; >3.25 suggests cirrhosis
• FibroScan: <9.5 kPa excludes cirrhosis; >12.5 kPa suggests cirrhosis
• Liver biopsy: Gold standard but rarely needed with non-invasive methods`,

    differentialDiagnosis: [
      'Hepatitis B - Different serology (HBsAg+), may coexist; check for coinfection',
      'Hepatitis A - Acute presentation, anti-HAV IgM positive, self-limited',
      'Hepatitis E - Fecal-oral transmission, anti-HEV IgM positive, severe in pregnancy',
      'Alcoholic liver disease - History of heavy alcohol use, AST:ALT >2:1, elevated GGT',
      'Non-alcoholic fatty liver disease (NAFLD) - Metabolic syndrome, steatosis, may coexist with HCV',
      'Drug-induced liver injury - Temporal association with medication, resolves with withdrawal',
      'Autoimmune hepatitis - ANA/ASMA positive, high IgG, interface hepatitis; may overlap with HCV',
      'Primary biliary cholangitis - AMA positive, cholestatic pattern, middle-aged women',
      'Hemochromatosis - Elevated ferritin and transferrin saturation, HFE mutations',
      'Wilson disease - Young patient, low ceruloplasmin, KF rings, neuropsychiatric symptoms',
      'HIV infection - Risk factors overlap; always test for HIV in HCV patients',
      'EBV/CMV hepatitis - Acute presentation, heterophile antibodies or specific serology',
      'Schistosomiasis (hepatosplenic) - Endemic areas, periportal fibrosis pattern, eosinophilia'
    ],

    investigations: [
      {
        name: 'Anti-HCV Antibody (EIA/CLIA)',
        indication: '🔴 ESSENTIAL - Screening test; positive indicates HCV exposure; must confirm with HCV RNA',
        availability: 'BASIC'
      },
      {
        name: 'HCV RNA PCR (Qualitative or Quantitative)',
        indication: '🔴 ESSENTIAL - Confirms active infection; required before treatment and to assess SVR; detectable = active infection',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Liver Function Tests (ALT, AST, ALP, GGT, Bilirubin, Albumin)',
        indication: '🔴 ESSENTIAL - Assess liver inflammation and synthetic function; note ALT may be normal despite significant disease',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count with Platelets',
        indication: '🔴 ESSENTIAL - Thrombocytopenia suggests cirrhosis/portal hypertension; anemia and leukopenia in advanced disease',
        availability: 'BASIC'
      },
      {
        name: 'INR/Prothrombin Time',
        indication: '🔴 ESSENTIAL - Hepatic synthetic function; prolonged in cirrhosis',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Serology (HBsAg, Anti-HBc)',
        indication: '🔴 ESSENTIAL - Screen for coinfection; risk of HBV reactivation with DAA treatment',
        availability: 'BASIC'
      },
      {
        name: 'HIV Antibody Test',
        indication: '🔴 ESSENTIAL - High coinfection rates; impacts treatment and monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Serum Creatinine and eGFR',
        indication: '🔴 ESSENTIAL - Baseline renal function; some DAAs require dose adjustment or are contraindicated in renal impairment',
        availability: 'BASIC'
      },
      {
        name: 'HCV Genotype',
        indication: '🟠 IMPORTANT - Determines treatment regimen if using genotype-specific DAAs; NOT required if using pangenotypic regimens',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'APRI/FIB-4 Score (Calculated)',
        indication: '🔴 ESSENTIAL - Non-invasive fibrosis assessment; use existing labs (AST, ALT, platelets, age) to stage fibrosis',
        availability: 'BASIC'
      },
      {
        name: 'Abdominal Ultrasound',
        indication: '🔴 ESSENTIAL - Assess for cirrhosis signs, portal hypertension, HCC screening; baseline and ongoing surveillance if cirrhotic',
        availability: 'BASIC'
      },
      {
        name: 'Transient Elastography (FibroScan)',
        indication: '🟡 IF AVAILABLE - Non-invasive fibrosis staging; >12.5 kPa suggests cirrhosis; helps determine treatment duration',
        availability: 'ADVANCED'
      },
      {
        name: 'Alpha-Fetoprotein (AFP)',
        indication: '🟠 IMPORTANT - HCC screening in cirrhotics; continue every 6 months even after SVR in patients with cirrhosis',
        availability: 'BASIC'
      },
      {
        name: 'HCV Resistance Testing',
        indication: '🟡 IF AVAILABLE - Consider in treatment-experienced patients or specific genotypes (GT3 with cirrhosis); guides retreatment',
        availability: 'ADVANCED'
      },
      {
        name: 'Pregnancy Test',
        indication: '🟠 IMPORTANT - Required before treatment in women of childbearing potential; some DAAs lack safety data in pregnancy',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM: Verify active infection with HCV RNA PCR (anti-HCV alone is insufficient)',
        '⚡ ASSESS FIBROSIS: Calculate APRI or FIB-4; determine if cirrhosis present (impacts treatment duration)',
        '⚡ SCREEN COINFECTIONS: Test for HBV (HBsAg, anti-HBc) and HIV; document results before starting DAAs',
        '⚡ BASELINE LABS: CBC, LFTs, creatinine, INR; document hepatic and renal function',
        '⚡ MEDICATION REVIEW: Check ALL current medications for drug interactions with planned DAA regimen',
        '⚡ ALCOHOL COUNSELING: Advise abstinence or reduction; alcohol accelerates fibrosis progression',
        '⚡ VACCINATE: Offer hepatitis A and B vaccines if not immune',
        '⚡ CONTRACEPTION: Counsel women of childbearing age; ensure contraception during treatment if needed',
        '⚡ EDUCATE: Explain that HCV is curable; discuss treatment duration, adherence, and follow-up',
        '⚡ HCC SURVEILLANCE: If cirrhotic, initiate ultrasound plus AFP every 6 months (continue lifelong)'
      ],
      
      pharmacological: [
        {
          drug: 'Sofosbuvir/Velpatasvir (SOF/VEL)',
          indication: '🔴 FIRST-LINE PANGENOTYPIC - Treats all genotypes (GT1-6); preferred in resource-limited settings; eliminates need for genotyping',
          dose: 'Adults: One tablet (400mg SOF/100mg VEL) PO once daily. Take with or without food. No dose adjustment for mild-moderate renal impairment',
          route: 'PO',
          duration: '12 weeks (all patients including cirrhotics); 8 weeks may be considered in non-cirrhotic GT1-6 treatment-naive',
          contraindications: ['Severe renal impairment (eGFR <30) - limited data', 'Concurrent use of strong P-gp inducers (rifampicin, carbamazepine, phenytoin)', 'Amiodarone (risk of severe bradycardia)'],
          sideEffects: ['Headache', 'Fatigue', 'Nausea', 'Insomnia', 'Generally very well tolerated'],
          alternatives: ['Sofosbuvir/daclatasvir', 'Glecaprevir/pibrentasvir']
        },
        {
          drug: 'Sofosbuvir/Daclatasvir (SOF/DCV)',
          indication: '🔴 FIRST-LINE ALTERNATIVE - Pangenotypic; widely available as generics in Africa; WHO prequalified',
          dose: 'Adults: Sofosbuvir 400mg + Daclatasvir 60mg PO once daily. DCV 30mg if with strong CYP3A4 inhibitors; DCV 90mg if with moderate CYP3A4 inducers',
          route: 'PO',
          duration: '12 weeks for most patients. GT3 with cirrhosis: 24 weeks or add ribavirin. Decompensated cirrhosis: 12 weeks + ribavirin',
          contraindications: ['Strong P-gp inducers', 'Amiodarone', 'Severe renal impairment (limited data for sofosbuvir)'],
          sideEffects: ['Headache', 'Fatigue', 'Nausea', 'Well tolerated'],
          alternatives: ['Sofosbuvir/velpatasvir', 'Glecaprevir/pibrentasvir']
        },
        {
          drug: 'Glecaprevir/Pibrentasvir (GLE/PIB)',
          indication: '🟠 ALTERNATIVE PANGENOTYPIC - 8-week treatment for treatment-naive non-cirrhotics; safe in severe renal impairment',
          dose: 'Adults: Three tablets (300mg GLE/120mg PIB total) PO once daily with food',
          route: 'PO',
          duration: '8 weeks (treatment-naive, non-cirrhotic, all genotypes). 12 weeks (compensated cirrhosis). 16 weeks (GT3 prior treatment failure)',
          contraindications: ['Decompensated cirrhosis (Child-Pugh B/C)', 'Prior NS3/4A or NS5A failure', 'Strong P-gp/CYP3A inducers'],
          sideEffects: ['Headache', 'Fatigue', 'Nausea', 'Pruritus', 'Well tolerated'],
          alternatives: ['Sofosbuvir/velpatasvir']
        },
        {
          drug: 'Sofosbuvir/Velpatasvir/Voxilaprevir (SOF/VEL/VOX)',
          indication: '🟡 SALVAGE THERAPY - For DAA treatment failures; triple-class combination',
          dose: 'Adults: One tablet (400mg SOF/100mg VEL/100mg VOX) PO once daily with food',
          route: 'PO',
          duration: '12 weeks',
          contraindications: ['Decompensated cirrhosis', 'Severe renal impairment', 'Strong P-gp/CYP3A inducers'],
          sideEffects: ['Headache', 'Fatigue', 'Diarrhea', 'Nausea'],
          alternatives: ['Glecaprevir/pibrentasvir + sofosbuvir + ribavirin (expert guidance)']
        },
        {
          drug: 'Ribavirin',
          indication: '🟠 ADJUNCT - Added to DAAs for decompensated cirrhosis, GT3 with cirrhosis, or difficult-to-treat cases',
          dose: 'Weight-based: <75kg: 1000mg/day divided BID; ≥75kg: 1200mg/day divided BID. Reduce dose for renal impairment and anemia',
          route: 'PO',
          duration: '12-24 weeks (with DAA regimen)',
          contraindications: ['Pregnancy (teratogenic - Category X)', 'Severe renal impairment', 'Severe anemia', 'Hemoglobinopathies', 'Unstable cardiac disease'],
          sideEffects: ['Hemolytic anemia (dose-limiting)', 'Fatigue', 'Rash', 'Pruritus', 'Cough', 'Teratogenicity'],
          alternatives: ['Extended DAA duration without ribavirin if contraindicated']
        },
        {
          drug: 'Hepatitis A Vaccine',
          indication: '🟠 PREVENTION - All HCV patients without immunity; prevents additional hepatic insult',
          dose: 'Adults: 1.0mL IM at 0 and 6-12 months (2-dose series)',
          route: 'IM',
          duration: '2-dose series',
          contraindications: ['Severe allergic reaction to previous dose'],
          sideEffects: ['Injection site soreness', 'Headache', 'Fatigue'],
          alternatives: ['Combined HAV/HBV vaccine (Twinrix) if also HBV non-immune']
        },
        {
          drug: 'Hepatitis B Vaccine',
          indication: '🟠 PREVENTION - All HCV patients without HBV immunity; prevents coinfection',
          dose: 'Adults: 1.0mL (20mcg) IM at 0, 1, 6 months. Check anti-HBs 1-2 months after series',
          route: 'IM',
          duration: '3-dose series',
          contraindications: ['Severe allergic reaction to previous dose or component'],
          sideEffects: ['Injection site soreness', 'Fever', 'Fatigue'],
          alternatives: ['Combined HAV/HBV vaccine (Twinrix)']
        },
        {
          drug: 'Lactulose',
          indication: '🟠 SUPPORTIVE - For hepatic encephalopathy in decompensated cirrhosis',
          dose: 'Adults: 15-30mL PO q8-12h, titrate to 2-3 soft bowel movements daily',
          route: 'PO',
          duration: 'Ongoing for encephalopathy management',
          contraindications: ['Galactosemia'],
          sideEffects: ['Bloating', 'Flatulence', 'Diarrhea', 'Abdominal cramps'],
          alternatives: ['Rifaximin (adjunct for recurrent encephalopathy)']
        },
        {
          drug: 'Propranolol',
          indication: '🟠 SUPPORTIVE - Variceal bleeding prophylaxis in cirrhotics with varices',
          dose: 'Adults: Start 20mg PO twice daily. Target heart rate 55-60 bpm',
          route: 'PO',
          duration: 'Indefinite',
          contraindications: ['Severe asthma', 'Heart block', 'Severe bradycardia'],
          sideEffects: ['Bradycardia', 'Fatigue', 'Dizziness', 'Bronchospasm'],
          alternatives: ['Carvedilol', 'Endoscopic band ligation']
        }
      ],
      
      nonPharmacological: [
        'ALCOHOL CESSATION: Strongly advise abstinence; alcohol accelerates fibrosis and reduces treatment response',
        'HARM REDUCTION: For PWID - needle/syringe programs, opioid substitution therapy; reduces reinfection risk',
        'WEIGHT MANAGEMENT: Address obesity and metabolic syndrome; reduces steatosis and fibrosis progression',
        'HEPATOTOXIC DRUG AVOIDANCE: Minimize paracetamol (<2g/day), avoid unnecessary medications and herbal remedies',
        'ADHERENCE SUPPORT: Emphasize importance of completing full course; missed doses reduce SVR rates',
        'SAFE PRACTICES: Avoid sharing personal items (razors, toothbrushes); cover wounds; safe sex practices',
        'CONTACT NOTIFICATION: Offer testing to sexual partners and household contacts (low but present risk)',
        'NUTRITION: Balanced diet; coffee consumption (3+ cups/day) associated with reduced fibrosis progression',
        'MENTAL HEALTH: Screen for depression and anxiety; common in chronic HCV; address before and during treatment',
        'SUPPORT GROUPS: Connect patients with peer support; address stigma and discrimination'
      ],
      
      surgical: [
        'LIVER TRANSPLANTATION: For decompensated cirrhosis (MELD ≥15) or HCC within criteria; HCV can be treated before, during, or after transplant',
        'HCC TREATMENT: Resection, RFA, TACE, or transplant depending on tumor stage and liver function; cure HCV first if possible',
        'TIPS: For refractory ascites or recurrent variceal bleeding in cirrhotics',
        'ENDOSCOPIC BAND LIGATION: Treatment and secondary prophylaxis of esophageal varices',
        'PARACENTESIS: Large-volume for symptomatic tense ascites'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Decompensated cirrhosis (ascites, encephalopathy, variceal bleeding) - needs specialist management and transplant evaluation',
        '🔴 IMMEDIATE REFERRAL: Suspected HCC - new hepatic lesion, rising AFP; urgent imaging and oncology consultation',
        '🔴 IMMEDIATE REFERRAL: Acute liver failure - rare in HCV but requires transplant center evaluation',
        '🟠 URGENT REFERRAL: HCV/HIV coinfection - needs coordinated management; drug interactions with ART',
        '🟠 URGENT REFERRAL: HCV/HBV coinfection - risk of HBV reactivation; may need concurrent HBV treatment',
        '🟠 URGENT REFERRAL: DAA treatment failure - needs resistance testing and salvage regimen planning',
        '🟠 URGENT REFERRAL: Complex drug interactions - polypharmacy patients needing expert review',
        '🟡 ROUTINE REFERRAL: Compensated cirrhosis - for treatment initiation and HCC surveillance planning',
        '🟡 ROUTINE REFERRAL: Extrahepatic manifestations (cryoglobulinemia, glomerulonephritis) - may need additional management',
        '🟡 ROUTINE REFERRAL: Pregnancy in HCV-positive woman - for monitoring and postnatal infant testing'
      ],
      
      monitoring: [
        'ON-TREATMENT: HCV RNA at week 4 (optional for adherence) and end of treatment (confirm undetectable)',
        'SVR12: HCV RNA 12 weeks after completing treatment - undetectable = CURE',
        'LFTs: At baseline, week 4, end of treatment; confirm normalization',
        'CBC: If on ribavirin - weekly for first month, then monthly (monitor hemoglobin)',
        'HBV MONITORING: If HBsAg+ or isolated anti-HBc+, monitor for reactivation during and after treatment',
        'HCC SURVEILLANCE: Ultrasound plus AFP every 6 months - CONTINUE LIFELONG if cirrhosis (even after SVR)',
        'POST-SVR (Non-cirrhotic): No further HCV monitoring unless reinfection risk; annual LFTs optional',
        'POST-SVR (Cirrhotic): Continue HCC surveillance; monitor for decompensation; annual endoscopy if varices',
        'REINFECTION SCREENING: Annual HCV RNA in high-risk individuals (PWID, high-risk sexual behavior)'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

DURING TREATMENT (8-12 weeks):
• Week 4: Optional HCV RNA (confirms adherence/response); assess side effects; check LFTs
• End of Treatment: HCV RNA (should be undetectable); LFTs; assess tolerability
• Adherence counseling at each contact; address any barriers

SVR12 ASSESSMENT (12 Weeks Post-Treatment):
• HCV RNA - undetectable = SVR = CURE
• If detectable: Assess for relapse vs. reinfection; refer for salvage therapy
• Celebrate success; reinforce harm reduction if ongoing risk factors

POST-SVR FOLLOW-UP:

Non-Cirrhotic Patients (F0-F3):
• No further HCV-specific monitoring required if SVR achieved
• Annual LFTs optional for general health
• Counsel on reinfection risk if ongoing risk factors
• If reinfection risk: Annual HCV RNA screening

Cirrhotic Patients (F4) - LIFELONG MONITORING REQUIRED:
• HCC surveillance continues indefinitely: Ultrasound plus AFP every 6 months
• SVR reduces but does not eliminate HCC risk (1-2% annual risk persists)
• Annual endoscopy if varices present
• Monitor for decompensation: Ascites, encephalopathy, jaundice
• Annual liver function tests
• Counsel on alcohol avoidance and hepatotoxin minimization

SPECIAL POPULATIONS:

HCV/HIV Coinfection:
• Check drug interactions between DAAs and ART before treatment
• May need ART modification during DAA treatment
• SVR rates similar to monoinfection with modern DAAs
• Continue HIV care after HCV cure

HCV/HBV Coinfection:
• Risk of HBV reactivation during/after DAA therapy
• If HBsAg+: Consider concurrent HBV treatment or close monitoring
• If isolated anti-HBc+: Monitor HBV DNA and ALT during treatment
• Continue HBV management after HCV cure

Pregnancy:
• DAAs not routinely recommended during pregnancy (limited safety data)
• Treat before conception if possible, or defer to postpartum
• Vertical transmission risk 4-8%; higher if high HCV RNA
• Test infant: HCV RNA at 2-6 months or anti-HCV at 18 months (maternal antibodies clear by then)

Renal Impairment:
• eGFR >30: Most DAAs can be used
• eGFR <30: Glecaprevir/pibrentasvir preferred (no dose adjustment)
• Sofosbuvir-containing regimens: Limited data in severe renal impairment; use with caution
• Dialysis: Glecaprevir/pibrentasvir is treatment of choice

Decompensated Cirrhosis:
• Treat to prevent further deterioration and improve transplant-free survival
• Sofosbuvir/velpatasvir plus ribavirin for 12 weeks (or SOF/DCV + RBV)
• Avoid protease inhibitors (glecaprevir, voxilaprevir)
• Close monitoring required; refer to specialist/transplant center`
    },

    redFlags: [
      '🔴 CRITICAL: Signs of hepatic decompensation (new ascites, encephalopathy, jaundice) - urgent specialist referral',
      '🔴 CRITICAL: Variceal hemorrhage (hematemesis, melena in cirrhotic) - resuscitate, urgent endoscopy, ICU',
      '🔴 CRITICAL: New hepatic lesion or rising AFP - suspect HCC; urgent imaging and referral',
      '🔴 CRITICAL: Severe bradycardia or syncope on DAAs - may indicate amiodarone interaction; stop and evaluate',
      '🔴 CRITICAL: HBV reactivation (rising HBV DNA, ALT flare) during HCV treatment - urgent HBV management',
      '🟠 URGENT: Significant anemia on ribavirin (Hb <10 g/dL) - reduce dose or discontinue ribavirin',
      '🟠 URGENT: Detectable HCV RNA at end of treatment or SVR12 - treatment failure; refer for salvage therapy',
      '🟠 URGENT: Worsening renal function on treatment - reassess regimen, check drug interactions',
      '🟠 URGENT: Severe depression or suicidal ideation - common in HCV; urgent psychiatric evaluation',
      '🟡 CAUTION: Drug interaction identified - may need regimen modification or alternative DAA',
      '🟡 CAUTION: Poor adherence suspected - intensify counseling, address barriers, consider DOT',
      '🟡 CAUTION: Ongoing high-risk behavior - reinforce harm reduction, plan for reinfection monitoring'
    ],

    complications: [
      'Cirrhosis (15-30% over 20-30 years) - Progressive fibrosis leading to liver dysfunction; SVR halts/reverses progression',
      'Hepatocellular carcinoma (HCC) - 1-5% annual risk in cirrhotics; persists at lower rate even after SVR',
      'Portal hypertension - Leads to varices, ascites, splenomegaly; develops with cirrhosis',
      'Esophageal/gastric varices - Risk of life-threatening hemorrhage; require surveillance and prophylaxis',
      'Ascites - Indicates decompensation; risk of SBP; poor prognosis',
      'Hepatic encephalopathy - Confusion to coma; precipitated by GI bleed, infection, electrolyte imbalance',
      'Hepatorenal syndrome - Functional renal failure in advanced cirrhosis; high mortality',
      'Mixed cryoglobulinemia - Vasculitis, purpura, arthralgia, glomerulonephritis; often improves with SVR',
      'Glomerulonephritis - Membranoproliferative; may require immunosuppression in addition to HCV treatment',
      'B-cell non-Hodgkin lymphoma - Increased risk with chronic HCV; may regress with SVR',
      'Type 2 diabetes - HCV increases insulin resistance; glycemic control may improve after SVR',
      'Porphyria cutanea tarda - Photosensitive blistering; improves with HCV cure and phlebotomy',
      'Reinfection - Can occur after SVR in high-risk individuals; not immunity'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

WITH DAA TREATMENT (CURRENT ERA):
• SVR (cure) rates: >95% overall with pangenotypic DAAs
• SVR rates by population: Non-cirrhotic >97%; Compensated cirrhosis >95%; Decompensated cirrhosis 85-90%
• SVR is considered a virologic cure; HCV replication permanently stops
• SVR reduces all-cause mortality by 50-75%
• SVR reduces HCC risk by 70-80% (but risk persists in cirrhotics)
• Liver fibrosis regresses in majority after SVR (even F4 can improve)
• Extrahepatic manifestations often improve or resolve after SVR

WITHOUT TREATMENT (NATURAL HISTORY):
• 15-30% develop cirrhosis over 20-30 years
• Cirrhosis: 1-5% annual risk of HCC; 3-6% annual risk of decompensation
• 5-year survival: Compensated cirrhosis 90%; Decompensated 50%
• HCV is leading cause of liver transplantation globally

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Achievement of SVR (most important factor)
• Treatment before cirrhosis develops
• Absence of significant fibrosis at treatment
• No HBV or HIV coinfection
• Alcohol abstinence
• Younger age
• Female sex (slower fibrosis progression)

Worse Outcomes:
• Established cirrhosis at treatment (HCC risk persists)
• Decompensated cirrhosis (limited reversibility)
• HCC at diagnosis
• HIV coinfection (if poorly controlled)
• Continued heavy alcohol use
• Older age at infection
• Male sex
• Metabolic syndrome/NAFLD coexistence

POST-SVR OUTCOMES:
• Non-cirrhotics: Essentially normal life expectancy
• Compensated cirrhotics: Improved but continued HCC surveillance needed
• Decompensated cirrhotics: Variable; some improve, others need transplant
• Fibrosis regression continues for years after SVR`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (No Vaccine Available):

HARM REDUCTION FOR PWID:
• Needle and syringe programs (NSP) - clean equipment access
• Opioid substitution therapy (OST) - methadone, buprenorphine
• Supervised injection facilities where available
• Education on safe injection practices
• Combination with HCV treatment (treatment as prevention)

BLOOD SAFETY:
• Universal screening of blood donations (mandatory)
• NAT testing where available (reduces window period)
• Appropriate donor deferral policies

HEALTHCARE SAFETY:
• Injection safety - single-use devices, safe disposal
• Infection control in all healthcare settings
• Safe surgical and dental practices
• Dialysis unit precautions
• Standard precautions for all patient contact

SAFE PRACTICES:
• Avoid sharing personal items that may have blood (razors, toothbrushes, nail clippers)
• Safe tattooing and piercing (single-use or sterilized equipment)
• Safe traditional practices (scarification, circumcision)
• Condom use (low but present sexual transmission risk)

SECONDARY PREVENTION:

SCREENING PROGRAMS:
• One-time screening for all adults (universal screening approach)
• Risk-based screening: PWID, blood transfusion recipients (before screening era), HIV+, incarcerated, healthcare workers with needlestick
• Antenatal screening (for PMTCT planning)
• Regular screening for ongoing risk (annual for active PWID)

TREATMENT AS PREVENTION:
• Curing HCV eliminates transmission from that individual
• PWID treatment reduces community viral pool
• Micro-elimination strategies targeting high-prevalence groups

TERTIARY PREVENTION:

POST-SVR:
• Harm reduction to prevent reinfection
• HCC surveillance in cirrhotics (lifelong)
• Hepatitis A and B vaccination
• Alcohol avoidance
• Management of metabolic comorbidities

PUBLIC HEALTH STRATEGIES:
• WHO goal: 90% diagnosed, 80% treated by 2030
• National hepatitis elimination programs
• Generic DAA access and affordability
• Simplified treatment algorithms (test and treat)
• Integration with HIV and harm reduction services`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS HEPATITIS C?
Hepatitis C is a viral infection that affects your liver. The good news is that hepatitis C can now be CURED with medicines taken for just 8-12 weeks. Most people feel fine during treatment and have no serious side effects.

THE MOST IMPORTANT MESSAGE: HEPATITIS C IS CURABLE!
• Modern medicines cure more than 95 out of 100 people
• Treatment is simple - usually one or two pills taken once daily
• Treatment is usually 12 weeks (sometimes 8 weeks)
• Once cured, the virus is gone from your body

HOW IS HEPATITIS C SPREAD?
• Through contact with infected blood (sharing needles, razors, or toothbrushes)
• Through unsterile medical procedures or equipment
• From mother to baby during birth (uncommon)
• Through unprotected sex (low risk but possible)

Hepatitis C is NOT spread by:
• Hugging, kissing, or shaking hands
• Sharing food, water, or eating utensils
• Coughing or sneezing
• Breastfeeding (unless nipples are cracked and bleeding)

HOW DO I PROTECT OTHERS?
• Do not share needles, razors, toothbrushes, or nail clippers
• Cover any cuts or wounds
• Do not donate blood until you are cured
• Tell healthcare workers about your hepatitis C
• Your sexual partners should be tested

YOUR TREATMENT:
• Take your medicine every day at the same time
• Complete the full course - do not stop early even if you feel well
• Tell your doctor about ALL other medicines you take (very important!)
• You can eat normal food during treatment
• Most people have few or no side effects

WHAT TO AVOID DURING TREATMENT:
• Alcohol - can harm your liver and reduce treatment success
• Certain medications - always check with your doctor first
• Pregnancy - some medicines may harm an unborn baby; use contraception
• St Johns Wort and some herbal remedies - can interfere with treatment

AFTER YOUR TREATMENT:
• You will have a blood test 12 weeks after finishing treatment
• If the virus is not detected, you are CURED
• Cure means the virus is gone permanently
• You can get infected again if you are exposed to the virus again

IF YOU HAVE LIVER SCARRING (CIRRHOSIS):
• Even after cure, you need regular check-ups
• Ultrasound scan every 6 months to check for liver cancer
• Avoid all alcohol permanently
• Your liver can still improve after the virus is gone

WHEN TO CONTACT YOUR DOCTOR:
• Yellowing of eyes or skin
• Severe tiredness that does not improve
• Dark urine or pale stools
• Swelling of legs or belly
• Confusion or drowsiness
• Blood in vomit or black stools

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Hepatitis C is no longer a lifelong disease. With modern treatment, you can be cured and live a normal, healthy life. Take your medicines as prescribed, attend all your appointments, and you have an excellent chance of being free of this virus forever.`,

    references: [
      'WHO. Guidelines for the care and treatment of persons diagnosed with chronic hepatitis C virus infection. World Health Organization; 2018.',
      'EASL. EASL recommendations on treatment of hepatitis C: Final update of the series. J Hepatol. 2020;73(5):1170-1218.',
      'AASLD-IDSA. HCV Guidance: Recommendations for Testing, Managing, and Treating Hepatitis C. Available at: www.hcvguidelines.org. Updated 2023.',
      'Polaris Observatory HCV Collaborators. Global prevalence and genotype distribution of hepatitis C virus infection in 2015: a modelling study. Lancet Gastroenterol Hepatol. 2017;2(3):161-176.',
      'Spearman CW, Dusheiko GM, Hellard M, Sonderup M. Hepatitis C. Lancet. 2019;394(10207):1451-1466.',
      'Blach S, Terrault NA, Grebely J, et al. Global change in hepatitis C virus prevalence and cascade of care between 2015 and 2020: a modelling study. Lancet Gastroenterol Hepatol. 2022;7(5):396-415.',
      'Feld JJ, Jacobson IM, Hezode C, et al. Sofosbuvir and Velpatasvir for HCV Genotype 1, 2, 4, 5, and 6 Infection. N Engl J Med. 2015;373(27):2599-2607.',
      'Foster GR, Afdhal N, Roberts SK, et al. Sofosbuvir and Velpatasvir for HCV Genotype 2 and 3 Infection. N Engl J Med. 2015;373(27):2608-2617.',
      'Zeuzem S, Foster GR, Wang S, et al. Glecaprevir-Pibrentasvir for 8 or 12 Weeks in HCV Genotype 1 or 3 Infection. N Engl J Med. 2018;378(4):354-369.',
      'Buti M, Riveiro-Barciela M, Esteban R. Long-term safety and efficacy of nucleo(s)tide analogue therapy in hepatitis B. Liver Int. 2018;38(Suppl 1):84-89.',
      'Ioannou GN, Green PK, Berry K. HCV eradication induced by direct-acting antiviral agents reduces the risk of hepatocellular carcinoma. J Hepatol. 2018;68(1):25-32.',
      'van der Meer AJ, Veldt BJ, Feld JJ, et al. Association between sustained virological response and all-cause mortality among patients with chronic hepatitis C and advanced hepatic fibrosis. JAMA. 2012;308(24):2584-2593.',
      'Manns MP, Buti M, Gane E, et al. Hepatitis C virus infection. Nat Rev Dis Primers. 2017;3:17006.',
      'Pawlotsky JM, Negro F, Aghemo A, et al. EASL recommendations on treatment of hepatitis C: Final update of the series. J Hepatol. 2020;73(5):1170-1218.',
      'Sonderup MW, Afihene M, Ally R, et al. Hepatitis C in sub-Saharan Africa: the current status and recommendations for achieving elimination by 2030. Lancet Gastroenterol Hepatol. 2017;2(12):910-919.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 75. COPD (CHRONIC OBSTRUCTIVE PULMONARY DISEASE)
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '75',
    name: 'COPD (Chronic Obstructive Pulmonary Disease)',
    slug: 'copd',
    specialty: 'Pulmonology',
    bodySystem: 'Respiratory',
    icdCode: 'J44',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Chronic Obstructive Pulmonary Disease (COPD) is a common, preventable, and treatable disease characterized by persistent respiratory symptoms and airflow limitation due to airway and/or alveolar abnormalities, usually caused by significant exposure to noxious particles or gases. COPD is the third leading cause of death worldwide, responsible for over 3 million deaths annually, with more than 90% occurring in low- and middle-income countries.

The pathophysiology involves chronic inflammation of the airways, lung parenchyma, and pulmonary vasculature in response to inhaled irritants. This leads to two main phenotypes often coexisting: chronic bronchitis (mucus hypersecretion, airway inflammation) and emphysema (alveolar destruction, loss of elastic recoil). The resulting airflow limitation is progressive and not fully reversible, distinguishing COPD from asthma.

In Africa, COPD represents a significant but underrecognized health burden. While tobacco smoking remains important, biomass fuel exposure from indoor cooking affects over 80% of rural African households and is a major COPD risk factor, particularly affecting women. Occupational exposures (mining, agriculture), outdoor air pollution, tuberculosis sequelae, and childhood respiratory infections also contribute significantly. Limited access to spirometry leads to underdiagnosis, while limited access to inhaled medications affects treatment outcomes.

Management focuses on reducing symptoms, preventing exacerbations, slowing disease progression, and improving quality of life. Smoking cessation is the single most effective intervention. Pharmacotherapy centers on bronchodilators (SABA, LABA, SAMA, LAMA) and, in selected patients, inhaled corticosteroids. Pulmonary rehabilitation and appropriate vaccination are essential components of comprehensive care.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Spirometry is REQUIRED for diagnosis: Post-bronchodilator FEV1/FVC < 0.70 confirms persistent airflow limitation

🔴 CRITICAL POINT 2 - Smoking cessation is the ONLY intervention proven to slow FEV1 decline; offer at every visit regardless of disease stage

🔴 CRITICAL POINT 3 - In Africa, consider biomass fuel exposure as major risk factor, especially in non-smoking women with COPD symptoms

🔴 CRITICAL POINT 4 - Acute exacerbations require bronchodilators, systemic corticosteroids (5 days), and antibiotics if purulent sputum or severe

🟠 CRITICAL POINT 5 - GOLD ABCD assessment guides treatment: based on symptoms (CAT or mMRC) and exacerbation history, NOT spirometry severity alone

🟠 CRITICAL POINT 6 - Long-acting bronchodilators (LAMA or LABA) are foundation of maintenance therapy; ICS added only for specific indications

🟠 CRITICAL POINT 7 - ICS increases pneumonia risk in COPD; reserve for patients with eosinophils >300, asthma-COPD overlap, or frequent exacerbations despite LABA+LAMA

🟡 CRITICAL POINT 8 - Differentiate COPD from asthma: COPD has incomplete reversibility, older onset, smoking/exposure history; may coexist (ACO)

🟡 CRITICAL POINT 9 - Screen for and treat comorbidities: cardiovascular disease, osteoporosis, depression, lung cancer, malnutrition`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Estimated 384 million people affected worldwide (global prevalence ~10-12% in adults >40 years)
• Third leading cause of death globally: >3 million deaths annually
• 90% of COPD deaths occur in low- and middle-income countries
• Prevalence increasing due to aging populations and continued exposure to risk factors
• Significant underdiagnosis: Up to 70% of cases undiagnosed globally
• Major cause of disability and healthcare utilization

AFRICAN EPIDEMIOLOGY:
• Prevalence estimates: 4-25% depending on region and diagnostic criteria
• Significant underdiagnosis due to limited spirometry access
• Tobacco smoking: Increasing prevalence, especially in urban areas and among men
• Biomass fuel exposure: Affects >80% of rural households; major risk factor for women
• Post-TB COPD: Significant contributor; TB causes airflow limitation in 30-60% of survivors
• Occupational exposures: Mining (silica, coal), agriculture (organic dusts), informal sector
• Urban air pollution: Increasing concern in rapidly urbanizing African cities
• HIV-associated COPD: Emerging recognition of HIV as independent risk factor
• Limited access to diagnosis (spirometry) and treatment (inhalers)

RISK FACTORS:
• Tobacco smoking (most important globally): Risk proportional to pack-years
• Biomass fuel smoke: Indoor cooking with wood, charcoal, dung, crop residues
• Occupational dusts and chemicals: Mining, farming, manufacturing
• Outdoor air pollution: Traffic, industrial emissions
• Alpha-1 antitrypsin deficiency: Rare but important in early-onset COPD
• Childhood factors: Low birth weight, respiratory infections, malnutrition
• Previous TB: Causes persistent airflow obstruction
• Asthma: Chronic asthma can lead to fixed airflow limitation
• HIV infection: Independent risk factor, accelerated lung aging

NATURAL HISTORY:
• Progressive decline in lung function (FEV1 decline 30-60 mL/year vs 20-30 mL/year in non-smokers)
• Punctuated by acute exacerbations that accelerate decline
• Increasing symptoms, disability, and exacerbations over time
• Development of complications: Respiratory failure, pulmonary hypertension, cor pulmonale
• Median survival varies by stage: GOLD 1-2 near normal; GOLD 4 approximately 5 years`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CARDINAL SYMPTOMS:
• Dyspnea: Progressive, persistent, worse with exertion; often described as increased effort to breathe, heaviness, air hunger
• Chronic cough: May be intermittent, may be unproductive; often first symptom but patients may not recognize it
• Sputum production: Any pattern of chronic sputum production may indicate COPD
• Wheeze and chest tightness: Variable, may worsen at night or with exertion

SYMPTOM PROGRESSION:
• Early: Dyspnea only with significant exertion; morning cough, minimal sputum
• Moderate: Dyspnea with daily activities; regular cough and sputum; occasional exacerbations
• Severe: Dyspnea at rest or minimal exertion; frequent exacerbations; limited ADLs
• Very Severe: Dyspnea at rest; respiratory failure; severely impaired quality of life

PHYSICAL EXAMINATION:

INSPECTION:
• Barrel chest (increased AP diameter) - suggests hyperinflation
• Use of accessory muscles (sternocleidomastoid, scalenes)
• Pursed-lip breathing - auto-PEEP to prevent airway collapse
• Cyanosis (central) - indicates hypoxemia
• Cachexia/muscle wasting - common in advanced disease
• Tripod positioning - leaning forward with arms braced

PALPATION:
• Reduced chest expansion
• Reduced tactile fremitus (emphysema)

PERCUSSION:
• Hyperresonance (emphysema)
• Low diaphragms, reduced diaphragmatic excursion

AUSCULTATION:
• Decreased breath sounds (emphysema)
• Prolonged expiratory phase
• Wheezes (may be absent in severe disease - silent chest)
• Coarse crackles (chronic bronchitis, infection)

SIGNS OF ADVANCED DISEASE:
• Peripheral edema (cor pulmonale)
• Elevated JVP
• Hepatomegaly (right heart failure)
• Asterixis (CO2 retention)
• Morning headache (hypercapnia)

COPD PHENOTYPES:

CHRONIC BRONCHITIS (Blue Bloater):
• Productive cough >3 months/year for 2 consecutive years
• Cyanosis, peripheral edema
• Hypoxemia, hypercapnia

EMPHYSEMA (Pink Puffer):
• Dyspnea predominant
• Thin, barrel chest, pursed-lip breathing
• Relatively preserved oxygenation until late
• Weight loss, muscle wasting

ASTHMA-COPD OVERLAP (ACO):
• Features of both conditions
• Greater reversibility (>12% and 200mL)
• Eosinophilia, atopy history
• Better response to ICS`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
• Age >40 years with dyspnea, chronic cough, or sputum production
• History of exposure: Tobacco, biomass fuel, occupational dusts
• Previous TB or childhood respiratory illness

STEP 2: SPIROMETRY (REQUIRED FOR DIAGNOSIS)
• Post-bronchodilator FEV1/FVC < 0.70 confirms airflow limitation
• Administer SABA (salbutamol 400mcg) 15-20 minutes before repeat spirometry
• Persistent ratio <0.70 after bronchodilator = COPD

STEP 3: ASSESS SEVERITY AND IMPACT
• Spirometric severity (GOLD 1-4)
• Symptom assessment (CAT or mMRC)
• Exacerbation history
• GOLD ABCD classification for treatment decisions

STEP 4: DIFFERENTIAL DIAGNOSIS AND COMORBIDITIES
• Rule out asthma, heart failure, TB, bronchiectasis
• Screen for comorbidities

DIAGNOSTIC CRITERIA (GOLD):
• Symptoms: Dyspnea, chronic cough, sputum production
• Risk factor exposure: Tobacco, biomass, occupational, pollution
• Spirometry: Post-bronchodilator FEV1/FVC < 0.70

SPIROMETRIC CLASSIFICATION (GOLD 1-4):
Based on POST-BRONCHODILATOR FEV1 (% predicted):
• GOLD 1 (Mild): FEV1 ≥ 80% predicted
• GOLD 2 (Moderate): 50% ≤ FEV1 < 80% predicted
• GOLD 3 (Severe): 30% ≤ FEV1 < 50% predicted
• GOLD 4 (Very Severe): FEV1 < 30% predicted

GOLD ABCD ASSESSMENT (Treatment Decisions):
Based on symptoms and exacerbations:
• Group A: Low symptoms (mMRC 0-1 or CAT <10), 0-1 exacerbations (not hospitalized)
• Group B: High symptoms (mMRC ≥2 or CAT ≥10), 0-1 exacerbations (not hospitalized)
• Group E (Exacerbator): ≥2 moderate exacerbations OR ≥1 hospitalization, regardless of symptoms

SYMPTOM SCORES:
• mMRC (Modified Medical Research Council) Dyspnea Scale: 0-4
  - 0: Breathless only with strenuous exercise
  - 1: Breathless when hurrying or walking up slight hill
  - 2: Walks slower than people of same age due to breathlessness
  - 3: Stops for breath after walking 100 meters or few minutes
  - 4: Too breathless to leave house or breathless when dressing

• CAT (COPD Assessment Test): 0-40 score; ≥10 indicates high symptom burden

LABORATORY FINDINGS:

SPIROMETRY:
• Reduced FEV1/FVC ratio (<0.70 post-bronchodilator)
• Reduced FEV1 (determines GOLD stage)
• Minimal bronchodilator reversibility (<12% and <200mL increase in FEV1)

ARTERIAL BLOOD GAS (Advanced Disease):
• Hypoxemia (PaO2 <60 mmHg indicates respiratory failure)
• Hypercapnia (PaCO2 >45 mmHg in advanced disease)
• Respiratory acidosis in acute exacerbations

OTHER:
• CBC: Polycythemia (chronic hypoxemia); anemia of chronic disease
• Alpha-1 antitrypsin level: Consider in early-onset or non-smoker COPD
• BNP/NT-proBNP: Elevated if cor pulmonale or concurrent heart failure`,

    differentialDiagnosis: [
      'Asthma - Earlier onset, variable symptoms, atopy, significant reversibility (>12% and >200mL), may coexist with COPD',
      'Heart failure - Orthopnea, PND, peripheral edema, elevated BNP, cardiomegaly, pulmonary edema on CXR',
      'Bronchiectasis - Large volume purulent sputum, recurrent infections, clubbing, CT shows airway dilation',
      'Tuberculosis (active or sequelae) - Endemic areas, weight loss, night sweats, hemoptysis, CXR/sputum findings',
      'Lung cancer - Hemoptysis, weight loss, new symptoms in smoker, mass on imaging',
      'Interstitial lung disease - Restrictive pattern on spirometry, fine crackles, CT findings',
      'Obliterative bronchiolitis - Younger patients, post-infection or transplant, CT shows mosaic pattern',
      'Diffuse panbronchiolitis - Asian populations, chronic sinusitis, centrilobular nodules on CT',
      'Tracheal stenosis - Fixed obstruction, stridor, flat inspiratory/expiratory flow-volume loop',
      'Chronic pulmonary thromboembolism - Dyspnea disproportionate to spirometry, CT pulmonary angiography',
      'Anemia - Dyspnea without airflow limitation, low hemoglobin',
      'Neuromuscular disease - Weakness, reduced respiratory muscle strength, restrictive pattern',
      'Obesity - Dyspnea, restrictive pattern, BMI >30'
    ],

    investigations: [
      {
        name: 'Spirometry (Pre and Post-Bronchodilator)',
        indication: '🔴 ESSENTIAL - REQUIRED for diagnosis; post-bronchodilator FEV1/FVC <0.70 confirms COPD; determines GOLD stage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 ESSENTIAL - Exclude other diagnoses (TB, lung cancer, heart failure); may show hyperinflation, flattened diaphragms, bullae',
        availability: 'BASIC'
      },
      {
        name: 'Pulse Oximetry',
        indication: '🔴 ESSENTIAL - Screen for hypoxemia; SpO2 <92% indicates need for ABG; guides oxygen therapy',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Detect polycythemia (chronic hypoxemia), anemia, eosinophilia (consider ACO if >300 cells/mcL)',
        availability: 'BASIC'
      },
      {
        name: 'Sputum Examination (Microscopy, Culture, GeneXpert)',
        indication: '🔴 ESSENTIAL - Rule out TB in endemic areas; guide antibiotic therapy in exacerbations',
        availability: 'BASIC'
      },
      {
        name: 'Arterial Blood Gas (ABG)',
        indication: '🟠 IMPORTANT - In severe COPD or exacerbations; assess oxygenation, ventilation, acid-base status; guide oxygen therapy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: '🟠 IMPORTANT - Detect cor pulmonale (P pulmonale, right axis deviation, RVH), arrhythmias, ischemic heart disease',
        availability: 'BASIC'
      },
      {
        name: 'Blood Eosinophil Count',
        indication: '🟠 IMPORTANT - Guides ICS use; eosinophils >300 cells/mcL predicts ICS benefit; lower counts suggest limited ICS benefit',
        availability: 'BASIC'
      },
      {
        name: 'BNP or NT-proBNP',
        indication: '🟠 IMPORTANT - Differentiate cardiac from pulmonary dyspnea; elevated in cor pulmonale and heart failure',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Alpha-1 Antitrypsin Level',
        indication: '🟡 IF AVAILABLE - Consider in early-onset COPD (<45 years), non-smokers, family history, or lower lobe emphysema',
        availability: 'ADVANCED'
      },
      {
        name: 'CT Chest (HRCT)',
        indication: '🟡 IF AVAILABLE - Characterize emphysema, bronchiectasis, lung cancer screening in high-risk; not routine for diagnosis',
        availability: 'ADVANCED'
      },
      {
        name: 'Echocardiography',
        indication: '🟡 IF AVAILABLE - Assess pulmonary hypertension, right ventricular function, exclude left heart disease',
        availability: 'INTERMEDIATE'
      },
      {
        name: '6-Minute Walk Test',
        indication: '🟡 IF AVAILABLE - Assess functional capacity, prognosis, response to treatment; desaturation indicates severity',
        availability: 'BASIC'
      },
      {
        name: 'Peak Expiratory Flow (PEF)',
        indication: '🟡 ALTERNATIVE - If spirometry unavailable; less specific but low PEF with symptoms suggests airflow limitation',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Ensure spirometry has been performed; post-BD FEV1/FVC <0.70 required',
        '⚡ ASSESS SEVERITY: Determine GOLD stage (1-4) from FEV1 and GOLD group (A, B, E) from symptoms/exacerbations',
        '⚡ SMOKING CESSATION: Ask about tobacco and biomass exposure; strongly advise cessation at EVERY visit',
        '⚡ VACCINATIONS: Ensure influenza (annual), pneumococcal, COVID-19 vaccines are up to date',
        '⚡ INHALER TECHNIQUE: Assess and teach proper technique; poor technique is most common cause of treatment failure',
        '⚡ BRONCHODILATOR: Initiate SABA as reliever; start maintenance therapy based on GOLD group',
        '⚡ EXACERBATION PLAN: Provide written action plan for recognizing and managing exacerbations',
        '⚡ COMORBIDITIES: Screen for and address cardiovascular disease, depression, osteoporosis, malnutrition',
        '⚡ OXYGEN ASSESSMENT: Check SpO2; if <92% at rest, consider ABG and long-term oxygen therapy evaluation',
        '⚡ EDUCATION: Explain disease, treatment goals, importance of adherence, when to seek help'
      ],
      
      pharmacological: [
        {
          drug: 'Salbutamol (Albuterol) - SABA',
          indication: '🔴 FIRST-LINE RELIEVER - As-needed for all COPD patients; rapid relief of breathlessness',
          dose: 'MDI: 100-200mcg (1-2 puffs) PRN, max 8 puffs/day. Nebulizer: 2.5-5mg PRN. For exacerbations: 4-8 puffs q20min x3 or continuous nebulization',
          route: 'Inhaled (MDI with spacer preferred; nebulizer for severe)',
          duration: 'Ongoing PRN; overuse (>2 canisters/month) indicates poor control',
          contraindications: ['Hypersensitivity to salbutamol'],
          sideEffects: ['Tremor', 'Tachycardia', 'Palpitations', 'Hypokalemia (high doses)', 'Headache'],
          alternatives: ['Terbutaline', 'Ipratropium (SAMA)']
        },
        {
          drug: 'Ipratropium Bromide - SAMA',
          indication: '🟠 ALTERNATIVE RELIEVER - Can be used alone or with SABA; particularly useful in exacerbations',
          dose: 'MDI: 40-80mcg (2-4 puffs) q6h PRN. Nebulizer: 500mcg q6-8h. Can combine with SABA',
          route: 'Inhaled',
          duration: 'Ongoing PRN',
          contraindications: ['Hypersensitivity to atropine-like drugs', 'Narrow-angle glaucoma (nebulized - protect eyes)'],
          sideEffects: ['Dry mouth', 'Urinary retention', 'Constipation', 'Blurred vision (if in eyes)'],
          alternatives: ['Salbutamol (SABA)']
        },
        {
          drug: 'Tiotropium - LAMA',
          indication: '🔴 FIRST-LINE MAINTENANCE - Preferred initial maintenance for GOLD Group B and E; once-daily dosing',
          dose: 'HandiHaler: 18mcg (1 capsule) inhaled once daily. Respimat: 5mcg (2 puffs) once daily',
          route: 'Inhaled',
          duration: 'Long-term maintenance',
          contraindications: ['Hypersensitivity to tiotropium or atropine derivatives'],
          sideEffects: ['Dry mouth', 'Constipation', 'Urinary retention', 'Blurred vision', 'Rarely: paradoxical bronchospasm'],
          alternatives: ['Glycopyrronium', 'Umeclidinium', 'Aclidinium']
        },
        {
          drug: 'Formoterol - LABA',
          indication: '🔴 FIRST-LINE MAINTENANCE - Alternative to LAMA; can combine with LAMA for additional benefit',
          dose: 'DPI: 12mcg twice daily. Can increase to 24mcg twice daily. Max: 48mcg/day',
          route: 'Inhaled',
          duration: 'Long-term maintenance',
          contraindications: ['Hypersensitivity', 'Use without ICS in asthma (but OK in COPD)'],
          sideEffects: ['Tremor', 'Palpitations', 'Headache', 'Muscle cramps', 'Hypokalemia'],
          alternatives: ['Salmeterol', 'Indacaterol', 'Olodaterol']
        },
        {
          drug: 'Salmeterol - LABA',
          indication: '🔴 FIRST-LINE MAINTENANCE - Alternative LABA; twice-daily dosing',
          dose: 'MDI/DPI: 50mcg twice daily',
          route: 'Inhaled',
          duration: 'Long-term maintenance',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Tremor', 'Headache', 'Palpitations', 'Muscle cramps'],
          alternatives: ['Formoterol', 'Indacaterol']
        },
        {
          drug: 'LABA + LAMA Combination (e.g., Tiotropium/Olodaterol, Umeclidinium/Vilanterol)',
          indication: '🔴 PREFERRED FOR GROUP E - Dual bronchodilation for patients with exacerbations or persistent symptoms on monotherapy',
          dose: 'Per specific product: Usually one inhalation once daily. Examples: Tiotropium 5mcg/Olodaterol 5mcg; Umeclidinium 62.5mcg/Vilanterol 25mcg',
          route: 'Inhaled',
          duration: 'Long-term maintenance',
          contraindications: ['Hypersensitivity to components'],
          sideEffects: ['Combined LAMA and LABA side effects', 'Generally well tolerated'],
          alternatives: ['Separate LAMA + LABA inhalers']
        },
        {
          drug: 'Budesonide/Formoterol - ICS/LABA',
          indication: '🟠 STEP-UP THERAPY - Add ICS if eosinophils >300, history of asthma, or frequent exacerbations despite LABA+LAMA',
          dose: 'DPI: 200/6mcg or 400/12mcg, 1-2 inhalations twice daily. Adjust based on control',
          route: 'Inhaled',
          duration: 'Long-term; reassess ICS need periodically',
          contraindications: ['Active TB', 'Untreated fungal infections'],
          sideEffects: ['Oral candidiasis (rinse mouth)', 'Hoarseness', 'Increased pneumonia risk', 'Skin bruising', 'Adrenal suppression (high dose)'],
          alternatives: ['Fluticasone/Salmeterol', 'Beclomethasone/Formoterol']
        },
        {
          drug: 'Triple Therapy (ICS/LABA/LAMA)',
          indication: '🟠 FOR PERSISTENT EXACERBATORS - Patients with exacerbations despite LABA+LAMA and eosinophils ≥100',
          dose: 'Single inhaler options (e.g., Fluticasone furoate/Umeclidinium/Vilanterol): One inhalation once daily',
          route: 'Inhaled',
          duration: 'Long-term; monitor for ICS side effects',
          contraindications: ['Hypersensitivity', 'Active TB'],
          sideEffects: ['Pneumonia risk (ICS component)', 'Oral candidiasis', 'Combined bronchodilator effects'],
          alternatives: ['Separate ICS/LABA + LAMA inhalers']
        },
        {
          drug: 'Prednisolone (Oral Corticosteroid)',
          indication: '🔴 FOR EXACERBATIONS - Short course for acute exacerbations; NOT for maintenance',
          dose: 'Adults: 40mg PO once daily for 5 days. No taper needed for short course',
          route: 'PO',
          duration: '5 days (do not exceed 7 days)',
          contraindications: ['Active untreated infection', 'Recent GI bleeding (relative)'],
          sideEffects: ['Hyperglycemia', 'Insomnia', 'Mood changes', 'GI upset', 'Immunosuppression'],
          alternatives: ['Methylprednisolone IV if unable to take oral', 'Dexamethasone']
        },
        {
          drug: 'Amoxicillin or Amoxicillin-Clavulanate',
          indication: '🟠 FOR EXACERBATIONS - If purulent sputum (Anthonisen type 1) or severe exacerbation requiring hospitalization',
          dose: 'Amoxicillin: 500mg-1g PO three times daily. Amox-Clav: 625mg PO three times daily or 1g twice daily',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Diarrhea', 'Nausea', 'Rash', 'Allergic reactions'],
          alternatives: ['Doxycycline', 'Azithromycin', 'Respiratory fluoroquinolone (if indicated)']
        },
        {
          drug: 'Azithromycin (Prophylactic)',
          indication: '🟡 EXACERBATION PREVENTION - For frequent exacerbators despite optimal inhaler therapy; long-term low-dose',
          dose: '250mg PO once daily OR 500mg PO three times weekly',
          route: 'PO',
          duration: 'Long-term (1 year then reassess); check ECG (QT), hearing',
          contraindications: ['QT prolongation', 'Hearing impairment', 'Liver disease'],
          sideEffects: ['GI upset', 'Hearing loss', 'QT prolongation', 'Antibiotic resistance'],
          alternatives: ['Roflumilast (if chronic bronchitis phenotype)']
        },
        {
          drug: 'Theophylline',
          indication: '🟡 THIRD-LINE - When inhaled bronchodilators unavailable or as add-on; narrow therapeutic index',
          dose: 'Sustained-release: 200-400mg PO twice daily. Target serum level: 8-12 mcg/mL (check levels)',
          route: 'PO',
          duration: 'Long-term if beneficial',
          contraindications: ['Uncontrolled arrhythmias', 'Active peptic ulcer', 'Seizure disorder (relative)'],
          sideEffects: ['Nausea', 'Tremor', 'Tachycardia', 'Arrhythmias', 'Seizures (toxicity)', 'Insomnia'],
          alternatives: ['Optimize inhaled therapy first']
        },
        {
          drug: 'Supplemental Oxygen',
          indication: '🔴 FOR HYPOXEMIA - Long-term oxygen therapy (LTOT) if PaO2 ≤55 mmHg or SpO2 ≤88%; improves survival',
          dose: 'Titrate to SpO2 88-92% (avoid over-oxygenation in CO2 retainers). Usually 1-3 L/min via nasal cannula',
          route: 'Nasal cannula or mask',
          duration: 'At least 15 hours/day for survival benefit; ideally continuous',
          contraindications: ['Active smoking (fire hazard)'],
          sideEffects: ['Nasal dryness', 'Skin irritation', 'CO2 retention risk if too high'],
          alternatives: ['None for qualifying patients']
        }
      ],
      
      nonPharmacological: [
        'SMOKING CESSATION: Most important intervention; offer counseling, NRT, and/or pharmacotherapy (varenicline, bupropion) at every visit',
        'BIOMASS EXPOSURE REDUCTION: Improved cook stoves, kitchen ventilation, cleaner fuels; critical in Africa',
        'PULMONARY REHABILITATION: 6-12 week program of exercise training and education; improves symptoms, exercise capacity, QoL',
        'VACCINATION: Annual influenza vaccine; pneumococcal vaccine (PCV13, PPSV23); COVID-19 vaccine; pertussis',
        'NUTRITION: Address malnutrition (common in severe COPD); high-protein diet; oral supplements if BMI <20',
        'PHYSICAL ACTIVITY: Encourage regular exercise within tolerance; improves function and reduces hospitalization',
        'BREATHING TECHNIQUES: Pursed-lip breathing, diaphragmatic breathing; reduce dyspnea',
        'ENERGY CONSERVATION: Teach pacing and planning activities; occupational therapy input',
        'SELF-MANAGEMENT: Written action plan for exacerbations; regular review of inhaler technique',
        'MENTAL HEALTH: Screen for anxiety and depression (common); treat appropriately',
        'OCCUPATIONAL ASSESSMENT: Minimize ongoing exposures; consider disability evaluation if severe'
      ],
      
      surgical: [
        'LUNG VOLUME REDUCTION SURGERY (LVRS): For selected patients with upper-lobe predominant emphysema and low exercise capacity; improves symptoms and survival in appropriate candidates',
        'BULLECTOMY: For large bullae (>1/3 hemithorax) compressing adjacent lung; improves dyspnea',
        'LUNG TRANSPLANTATION: For very severe COPD (BODE index 7-10) refractory to maximal medical therapy; improves quality of life; limited availability in Africa',
        'BRONCHOSCOPIC LUNG VOLUME REDUCTION: Endobronchial valves for heterogeneous emphysema with intact fissures; less invasive than LVRS'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Severe acute exacerbation with respiratory failure (SpO2 <88%, altered consciousness, hemodynamic instability)',
        '🔴 IMMEDIATE REFERRAL: Pneumothorax in COPD patient - requires chest drain and may need pleurodesis',
        '🔴 IMMEDIATE REFERRAL: Massive hemoptysis - exclude lung cancer, TB, bronchiectasis',
        '🟠 URGENT REFERRAL: Frequent exacerbations (≥2/year) despite optimal therapy - specialist review, consider prophylaxis',
        '🟠 URGENT REFERRAL: Diagnostic uncertainty - spirometry unavailable, atypical features, possible ACO',
        '🟠 URGENT REFERRAL: Suspected lung cancer - new mass, significant hemoptysis, unexplained weight loss',
        '🟠 URGENT REFERRAL: Cor pulmonale/pulmonary hypertension - right heart failure signs, unexplained edema',
        '🟡 ROUTINE REFERRAL: Severe COPD (GOLD 3-4) for comprehensive management and rehabilitation',
        '🟡 ROUTINE REFERRAL: Assessment for long-term oxygen therapy - needs ABG, specialist prescription',
        '🟡 ROUTINE REFERRAL: Young patient (<45 years) or non-smoker with COPD - alpha-1 antitrypsin testing',
        '🟡 ROUTINE REFERRAL: Consideration for lung volume reduction or transplant in appropriate candidates'
      ],
      
      monitoring: [
        'SPIROMETRY: Annual FEV1 to track disease progression; more often if rapid decline suspected',
        'SYMPTOMS: Regular assessment with CAT or mMRC score; document changes',
        'EXACERBATIONS: Track frequency, severity, hospitalizations; adjust therapy accordingly',
        'OXYGEN SATURATION: Check at each visit; ABG if SpO2 <92% or declining',
        'INHALER TECHNIQUE: Assess and correct at every visit; most common cause of treatment failure',
        'SMOKING STATUS: Document at every visit; offer cessation support',
        'WEIGHT/BMI: Monitor for malnutrition (BMI <20) or obesity (complicates management)',
        'COMORBIDITIES: Annual screening for CVD, osteoporosis, depression, lung cancer (if eligible)',
        'MEDICATION ADHERENCE: Assess fill rates, inhaler counters, and self-report',
        'VACCINATION STATUS: Ensure annual influenza and recommended pneumococcal vaccines'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

STABLE COPD:

Mild-Moderate (GOLD 1-2, Group A-B):
• Review every 3-6 months initially, then 6-12 months if stable
• At each visit: Symptoms, exacerbations, smoking status, inhaler technique
• Annual spirometry to track FEV1
• Vaccination status review

Severe-Very Severe (GOLD 3-4, Group E):
• Review every 1-3 months
• More frequent assessment of symptoms, oxygenation, exacerbations
• Monitor for complications: Cor pulmonale, respiratory failure
• Consider palliative care discussion for very severe disease

AFTER EXACERBATION:
• Follow-up within 1-2 weeks of hospital discharge
• Review what led to exacerbation; optimize preventive therapy
• Ensure completion of any antibiotic/steroid course
• Check inhaler technique and supply
• Arrange pulmonary rehabilitation if not already enrolled
• Consider stepping up maintenance therapy if frequent exacerbations

TREATMENT ESCALATION PATHWAY:
1. Start: SABA PRN for all
2. Group A: SABA PRN may be sufficient; can add LAMA or LABA
3. Group B: Start LAMA or LABA; if inadequate, dual LABA+LAMA
4. Group E: Start LABA+LAMA; if exacerbations continue and eosinophils ≥300, add ICS (triple therapy)
5. If still exacerbating: Consider roflumilast (chronic bronchitis) or azithromycin prophylaxis

TREATMENT DE-ESCALATION:
• Consider ICS withdrawal if: No exacerbations for 1 year, no asthma features, eosinophils <300
• Taper gradually while monitoring for exacerbations
• Never stop long-acting bronchodilators abruptly

SPECIAL POPULATIONS:

ACO (Asthma-COPD Overlap):
• ICS-containing regimen essential (unlike pure COPD)
• Manage as asthma that also has COPD features
• Avoid LABA monotherapy

Alpha-1 Antitrypsin Deficiency:
• Standard COPD therapy applies
• Augmentation therapy (IV alpha-1 antitrypsin) in selected patients
• Family screening recommended

End-Stage COPD:
• Discuss prognosis and goals of care
• Consider palliative care/hospice referral
• Optimize symptom management (opioids can reduce dyspnea)
• Advance care planning and documentation
• Transplant evaluation if appropriate and available`
    },

    redFlags: [
      '🔴 CRITICAL: Severe respiratory distress (RR >30, accessory muscle use, inability to speak) - impending respiratory failure, prepare for NIV/intubation',
      '🔴 CRITICAL: Altered consciousness or confusion - hypercapnic encephalopathy or hypoxemia; ABG, oxygen with caution, consider NIV',
      '🔴 CRITICAL: SpO2 <88% on room air - severe hypoxemia; controlled oxygen, ABG, monitor closely for CO2 retention',
      '🔴 CRITICAL: Hemodynamic instability in exacerbation - sepsis, tension pneumothorax, massive PE; urgent assessment',
      '🔴 CRITICAL: New onset hemoptysis - exclude lung cancer, active TB, PE; urgent imaging and specialist referral',
      '🟠 URGENT: Exacerbation not responding to treatment within 48-72 hours - reassess diagnosis, consider hospital admission',
      '🟠 URGENT: Progressive peripheral edema - cor pulmonale developing; assess oxygenation, consider diuretics and specialist input',
      '🟠 URGENT: Significant weight loss (>5% in 6 months) - exclude lung cancer, TB; nutritional assessment',
      '🟠 URGENT: FEV1 declining faster than expected (>60 mL/year) - reassess treatment, adherence, ongoing exposures',
      '🟡 CAUTION: Poor inhaler technique despite education - consider device change, supervised dosing, or nebulizer',
      '🟡 CAUTION: Frequent SABA use (>2 canisters/month) - inadequate maintenance therapy; step up treatment',
      '🟡 CAUTION: Depression or anxiety symptoms - common in COPD; screen and treat; affects outcomes'
    ],

    complications: [
      'Acute exacerbation - Worsening of symptoms requiring additional therapy; accelerates lung function decline',
      'Respiratory failure (Type 1 and 2) - Hypoxemia with or without hypercapnia; may require oxygen, NIV, or intubation',
      'Cor pulmonale (Right heart failure) - Due to pulmonary hypertension; presents with edema, elevated JVP',
      'Pulmonary hypertension - Consequence of chronic hypoxemia and vascular remodeling; poor prognosis',
      'Pneumothorax - Higher risk due to bullous disease; may be tension pneumothorax',
      'Pneumonia - Increased risk, especially with ICS use; high morbidity in COPD patients',
      'Lung cancer - Strongly associated with smoking; screen high-risk patients with low-dose CT',
      'Malnutrition and cachexia - Increased metabolic demands, reduced intake; poor prognosis indicator',
      'Osteoporosis - Due to systemic inflammation, steroid use, inactivity; increased fracture risk',
      'Depression and anxiety - Highly prevalent; often underdiagnosed; worsens outcomes',
      'Cardiovascular disease - Shared risk factors; leading cause of death in mild-moderate COPD',
      'Polycythemia - Secondary to chronic hypoxemia; increases thromboembolic risk',
      'Sleep disorders - Sleep apnea overlap, nocturnal desaturation; affects quality of life'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL PROGNOSIS:
• COPD is a progressive disease; rate of decline varies widely
• Leading cause of mortality in severe stages
• Quality of life often significantly impaired

SURVIVAL BY STAGE:
• GOLD 1-2: Near-normal life expectancy with optimal management
• GOLD 3: Reduced life expectancy; median survival approximately 5-8 years
• GOLD 4: Poor prognosis; median survival approximately 2-5 years

BODE INDEX (Prognostic Tool):
• B: BMI (<21 = 1 point)
• O: Airflow Obstruction (FEV1 % predicted)
• D: Dyspnea (mMRC scale)
• E: Exercise capacity (6-minute walk distance)
• Score 0-10; higher scores = worse prognosis
• BODE 7-10: 4-year mortality approximately 80%

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Smoking cessation (most important modifiable factor)
• Higher baseline FEV1
• Fewer exacerbations
• Good exercise capacity
• Higher BMI (within normal range)
• Absence of hypercapnia
• Good response to bronchodilators
• Adherence to treatment

Worse Outcomes:
• Continued smoking
• Severe airflow limitation (low FEV1)
• Frequent exacerbations (≥2/year)
• Hypoxemia and hypercapnia
• Cor pulmonale
• Low BMI (<21) and muscle wasting
• Multiple comorbidities
• Poor functional status
• Depression

IMPACT OF INTERVENTIONS:
• Smoking cessation: Only intervention proven to slow FEV1 decline
• Long-term oxygen therapy: Improves survival in hypoxemic patients
• LVRS: Improves survival in selected patients (upper-lobe emphysema)
• Pulmonary rehabilitation: Improves quality of life, reduces hospitalizations
• Pharmacotherapy: Improves symptoms and reduces exacerbations; mortality benefit less clear

CAUSE OF DEATH:
• Respiratory failure: 30-50% (more common in severe COPD)
• Cardiovascular disease: 25-30% (especially in mild-moderate COPD)
• Lung cancer: 10-20%
• Other causes: Variable`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing COPD Development):

TOBACCO CONTROL:
• Population-level policies: Taxation, advertising bans, smoke-free legislation
• Individual counseling: Brief advice at every healthcare contact
• Pharmacotherapy for cessation: NRT, varenicline, bupropion
• Never too late: Quitting at any age slows decline

REDUCING BIOMASS FUEL EXPOSURE:
• Improved cook stoves: More efficient, reduced emissions
• Kitchen ventilation: Windows, chimneys, separate cooking areas
• Cleaner fuels: LPG, electricity, solar where accessible
• Behavior change: Keep children away from cooking areas
• Critical intervention in Africa where 80% of rural households affected

OCCUPATIONAL HEALTH:
• Workplace exposure monitoring and limits
• Personal protective equipment (masks, respirators)
• Job modification for symptomatic workers
• Pre-employment and periodic screening spirometry

AIR QUALITY:
• Reducing outdoor pollution: Vehicle emissions, industry
• Indoor air quality: Avoid burning waste, adequate ventilation
• Public health advisories during high pollution days

REDUCING CHILDHOOD RISK FACTORS:
• Preventing low birth weight and prematurity
• Reducing childhood respiratory infections (vaccination, nutrition)
• Avoiding tobacco smoke exposure in children

SECONDARY PREVENTION (Early Detection):

• Spirometry screening in high-risk individuals (smokers >40, occupational exposure)
• Early diagnosis allows earlier intervention
• Case-finding in primary care

TERTIARY PREVENTION (Preventing Complications):

• Optimal pharmacotherapy to reduce exacerbations
• Vaccination: Influenza, pneumococcal, COVID-19
• Pulmonary rehabilitation
• Smoking cessation (still beneficial even in severe disease)
• Long-term oxygen for qualifying patients
• Monitoring and treating comorbidities

PUBLIC HEALTH PRIORITIES FOR AFRICA:
• Tobacco control legislation and enforcement
• Clean cooking initiatives (major focus needed)
• Integration of COPD into primary care
• Access to spirometry for diagnosis
• Affordable access to essential inhaled medications
• Training healthcare workers in COPD management`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS COPD?
COPD (Chronic Obstructive Pulmonary Disease) is a lung condition that makes it hard to breathe. The airways in your lungs become damaged and narrowed over time, usually from breathing in harmful substances like tobacco smoke or smoke from cooking fires. COPD cannot be cured, but with proper treatment you can breathe better, do more activities, and have fewer flare-ups.

WHAT CAUSES COPD?
• Cigarette smoking (most common cause worldwide)
• Smoke from cooking fires (very common in Africa, especially for women)
• Dust and chemicals at work
• Air pollution
• Sometimes it runs in families

THE MOST IMPORTANT THING YOU CAN DO:
If you smoke - STOP SMOKING. This is the single best thing you can do for your lungs. It is never too late to quit. Ask your doctor for help with quitting.

If you cook with wood, charcoal, or other fuels:
• Cook in a well-ventilated area or outside if possible
• Use an improved cookstove if available
• Keep children away from cooking smoke
• Open windows when cooking

USING YOUR INHALERS:
Your inhaler medicine only works if you use it correctly.
• Take your medicines every day as prescribed, even when you feel well
• Learn the correct technique for your inhaler - ask your healthcare provider to show you
• Always use a spacer with your puffer (MDI) inhaler if you have one
• Rinse your mouth after using steroid inhalers to prevent thrush

RECOGNIZING A FLARE-UP (EXACERBATION):
A flare-up is when your COPD suddenly gets worse. Signs include:
• More breathlessness than usual
• More coughing
• More phlegm (mucus), especially if it becomes yellow or green
• Feeling more tired than usual
• Needing your reliever inhaler more often

WHAT TO DO IF YOU HAVE A FLARE-UP:
• Use your reliever inhaler (usually blue) more often
• Follow your written action plan if you have one
• Take your emergency medicines (steroids, antibiotics) if your doctor has given them to you
• Seek medical help if not improving in 48 hours or getting worse

SEEK HELP IMMEDIATELY IF:
• Severe breathlessness - cannot speak in sentences
• Lips or fingers turning blue
• Confusion or drowsiness
• Very rapid breathing
• Chest pain

STAYING HEALTHY WITH COPD:
• Take all your medicines as prescribed
• Get your flu vaccine every year
• Get pneumonia vaccine as recommended
• Stay physically active - walking is excellent
• Eat well - you need energy to breathe
• Avoid people with colds and flu
• Wash hands frequently to prevent infections

IF YOU NEED OXYGEN AT HOME:
• Use oxygen for at least 15 hours every day, including while sleeping
• NEVER smoke or allow smoking near oxygen (fire risk)
• Keep the equipment clean and well-maintained
• Have a backup plan if power cuts occur

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MESSAGE
═══════════════════════════════════════════════════════════════════════════════

COPD is a lifelong condition, but with proper care you can live well with it. The key is: stop smoking, take your inhalers correctly every day, stay active, and get help early if you have a flare-up. Your healthcare team is here to support you.`,

    references: [
      'Global Initiative for Chronic Obstructive Lung Disease (GOLD). Global Strategy for the Diagnosis, Management, and Prevention of COPD. 2024 Report. Available at: www.goldcopd.org',
      'WHO. Chronic obstructive pulmonary disease (COPD). Fact sheet. World Health Organization. 2023.',
      'Adeloye D, Chua S, Lee C, et al. Global and regional estimates of COPD prevalence: Systematic review and meta-analysis. J Glob Health. 2015;5(2):020415.',
      'Salvi SS, Barnes PJ. Chronic obstructive pulmonary disease in non-smokers. Lancet. 2009;374(9691):733-743.',
      'Gordon SB, Bruce NG, Grigg J, et al. Respiratory risks from household air pollution in low and middle income countries. Lancet Respir Med. 2014;2(10):823-860.',
      'Mortimer K, Ndamala CB, Nauber AC, et al. A cleaner burning biomass-fuelled cookstove intervention to prevent pneumonia in children under 5 years old in rural Malawi (the Cooking and Pneumonia Study): a cluster randomised controlled trial. Lancet. 2017;389(10065):167-175.',
      'van Gemert F, van der Molen T, Jones R, et al. The impact of asthma and COPD in sub-Saharan Africa. Prim Care Respir J. 2011;20(3):240-248.',
      'Rabe KF, Hurd S, Anzueto A, et al. Global strategy for the diagnosis, management, and prevention of chronic obstructive pulmonary disease: GOLD executive summary. Am J Respir Crit Care Med. 2007;176(6):532-555.',
      'Vogelmeier CF, Criner GJ, Martinez FJ, et al. Global Strategy for the Diagnosis, Management, and Prevention of Chronic Obstructive Lung Disease 2017 Report. GOLD Executive Summary. Am J Respir Crit Care Med. 2017;195(5):557-582.',
      'Wedzicha JA, Calverley PMA, Albert RK, et al. Prevention of COPD exacerbations: a European Respiratory Society/American Thoracic Society guideline. Eur Respir J. 2017;50(3):1602265.',
      'McCarthy B, Casey D, Devane D, et al. Pulmonary rehabilitation for chronic obstructive pulmonary disease. Cochrane Database Syst Rev. 2015;(2):CD003793.',
      'Nocturnal Oxygen Therapy Trial Group. Continuous or nocturnal oxygen therapy in hypoxemic chronic obstructive lung disease: a clinical trial. Ann Intern Med. 1980;93(3):391-398.',
      'Anthonisen NR, Connett JE, Murray RP. Smoking and lung function of Lung Health Study participants after 11 years. Am J Respir Crit Care Med. 2002;166(5):675-679.',
      'Siddharthan T, Gupte A, Barnes PJ. Chronic Obstructive Pulmonary Disease Endotypes in Low- and Middle-Income Country Settings: Precision Medicine for All. Am J Respir Crit Care Med. 2020;202(2):171-172.',
      'Allwood BW, Gillespie R, Galperin-Aizenberg M, et al. Mechanism of airflow obstruction in tuberculosis-associated obstructive pulmonary disease. Am J Respir Crit Care Med. 2014;190(10):1184-1186.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 76. EPILEPSY - CHRONIC MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '76',
    name: 'Epilepsy - Chronic Management',
    slug: 'epilepsy-chronic-management',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'G40',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Epilepsy is a chronic neurological disorder characterized by recurrent, unprovoked seizures due to abnormal excessive or synchronous neuronal activity in the brain. It affects approximately 50 million people worldwide, making it one of the most common neurological conditions globally. Epilepsy is defined by any of the following: (1) at least two unprovoked seizures occurring more than 24 hours apart, (2) one unprovoked seizure with a probability of further seizures similar to the general recurrence risk after two unprovoked seizures (≥60%), or (3) diagnosis of an epilepsy syndrome.

The pathophysiology involves an imbalance between neuronal excitation and inhibition, leading to hypersynchronous electrical discharges. This can result from structural abnormalities (cortical malformations, tumors, stroke, trauma), genetic factors, metabolic derangements, infectious causes, or immune-mediated mechanisms. In many cases, the etiology remains unknown (idiopathic/genetic epilepsy).

Africa bears a disproportionate burden of epilepsy, with prevalence rates of 4-15 per 1,000 population (higher than the global average of 6-8 per 1,000). The treatment gap in Africa is staggering: up to 80% of people with epilepsy in sub-Saharan Africa receive no treatment, compared to <10% in high-income countries. Contributing factors include limited access to healthcare, shortage of neurologists and EEG facilities, cost and availability of antiseizure medications (ASMs), and significant stigma and misconceptions about epilepsy.

Chronic management focuses on achieving seizure freedom (or optimal seizure control) while minimizing medication side effects and addressing psychosocial aspects. With appropriate treatment, 70% of people with epilepsy can become seizure-free. The WHO Essential Medicines List includes phenobarbital, carbamazepine, phenytoin, and valproic acid, which are effective and affordable options for resource-limited settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Seizure TYPE determines drug choice: Focal seizures → carbamazepine, lamotrigine; Generalized → valproate, lamotrigine; Absence → ethosuximide, valproate

🔴 CRITICAL POINT 2 - AVOID valproate in women of childbearing potential (teratogenic, neurodevelopmental effects); use effective contraception if no alternative

🔴 CRITICAL POINT 3 - Start LOW, go SLOW: Begin with low dose of single ASM, titrate gradually to minimize side effects and find minimum effective dose

🔴 CRITICAL POINT 4 - Never stop ASMs abruptly - risk of status epilepticus; taper gradually even if changing medications

🟠 CRITICAL POINT 5 - Phenobarbital is effective, affordable, and widely available; acceptable first-line in resource-limited settings despite sedation

🟠 CRITICAL POINT 6 - Drug interactions are common: Enzyme-inducing ASMs (carbamazepine, phenytoin, phenobarbital) reduce efficacy of many drugs including hormonal contraceptives

🟠 CRITICAL POINT 7 - Treat the underlying cause when identified: neurocysticercosis, brain tumors, metabolic disorders, HIV-related

🟡 CRITICAL POINT 8 - Most patients (60-70%) achieve seizure control with first appropriate ASM; try adequate dose before adding second drug

🟡 CRITICAL POINT 9 - Address stigma and psychosocial issues; epilepsy is NOT supernatural or contagious; education reduces discrimination`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Approximately 50 million people with epilepsy worldwide
• Active epilepsy prevalence: 6-8 per 1,000 population
• Incidence: 50-70 per 100,000 per year (higher in LMICs)
• Bimodal age distribution: Peak in childhood and elderly
• 80% of people with epilepsy live in low- and middle-income countries
• Global treatment gap: 75% in LMICs vs <10% in high-income countries

AFRICAN EPIDEMIOLOGY:
• Prevalence: 4-15 per 1,000 (higher than global average)
• Estimated 10+ million people with epilepsy in Africa
• Treatment gap: 60-90% (among highest globally)
• Limited access: <1 neurologist per 1 million population in most African countries
• High stigma: Affects marriage, employment, education, social participation
• Traditional healers often first point of contact

ETIOLOGY IN AFRICA:
• Perinatal causes: Birth asphyxia, neonatal infections (30-40% in some regions)
• CNS infections: Neurocysticercosis, meningitis, encephalitis, cerebral malaria, HIV
• Head trauma: Road traffic accidents, falls, violence
• Stroke: Increasingly common with epidemiologic transition
• Genetic/idiopathic: Significant proportion, especially childhood-onset
• Onchocerciasis-associated epilepsy: Emerging recognition in endemic areas (nodding syndrome)
• Cortical malformations: Underdiagnosed due to limited neuroimaging

NEUROCYSTICERCOSIS:
• Major cause of epilepsy in endemic areas (pork-raising communities)
• Responsible for up to 30% of epilepsy cases in some African regions
• Preventable with improved sanitation and pork inspection
• Treatable with albendazole/praziquantel plus corticosteroids

MORTALITY:
• 2-3 times higher mortality than general population
• SUDEP (Sudden Unexpected Death in Epilepsy): 1-2 per 1,000 patient-years
• Status epilepticus, accidents, drowning, suicide
• Higher mortality in untreated/poorly controlled epilepsy`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

SEIZURE CLASSIFICATION (ILAE 2017):

FOCAL ONSET SEIZURES (start in one area):
• Aware (simple partial) - consciousness preserved
  - Motor: Clonic movements of one limb/face, head turning
  - Sensory: Tingling, visual/auditory phenomena, vertigo
  - Autonomic: Palpitations, sweating, piloerection, GI sensations
  - Psychic: Deja vu, fear, depersonalization
• Impaired awareness (complex partial) - consciousness affected
  - Often temporal lobe origin
  - Aura followed by staring, unresponsiveness
  - Automatisms: Lip-smacking, picking, fumbling
  - Post-ictal confusion common
• Focal to bilateral tonic-clonic (secondary generalization)

GENERALIZED ONSET SEIZURES (both hemispheres from start):
• Tonic-clonic (grand mal):
  - Sudden loss of consciousness, cry
  - Tonic phase: Stiffening (10-20 seconds)
  - Clonic phase: Rhythmic jerking (30-60 seconds)
  - Post-ictal: Confusion, drowsiness, headache, muscle pain
• Absence (petit mal):
  - Brief (5-30 seconds) loss of awareness
  - Staring, eye blinking, lip smacking
  - Abrupt onset and offset, no post-ictal confusion
  - Multiple daily episodes common
• Myoclonic: Brief, shock-like jerks; often morning
• Atonic (drop attacks): Sudden loss of muscle tone, falls
• Tonic: Sustained stiffening
• Clonic: Rhythmic jerking without tonic phase

UNKNOWN ONSET:
• Insufficient information to classify
• Can be tonic-clonic, other motor, or non-motor

EPILEPSY SYNDROMES (Selected):

CHILDHOOD:
• Childhood absence epilepsy: Age 4-10, multiple daily absences, good prognosis
• Juvenile myoclonic epilepsy: Adolescent onset, morning myoclonus, GTC seizures
• Benign rolandic epilepsy: Age 3-13, focal motor/sensory, centrotemporal spikes, remits
• Lennox-Gastaut syndrome: Multiple seizure types, cognitive impairment, difficult to treat

ADULT:
• Temporal lobe epilepsy: Most common focal epilepsy; auras, automatisms, memory issues
• Frontal lobe epilepsy: Brief, nocturnal, hyperkinetic seizures; often misdiagnosed

HISTORY TAKING FOR SEIZURES:
1. Detailed seizure description (patient AND witness)
2. Prodrome/aura
3. Seizure semiology (what happened during)
4. Post-ictal state and duration
5. Frequency, duration, timing (day/night, triggers)
6. Age of onset
7. Risk factors: Birth history, febrile seizures, head injury, CNS infections, family history
8. Current medications and adherence
9. Social impact: Driving, employment, relationships`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CONFIRM SEIZURE DIAGNOSIS
• Detailed history from patient and witness is essential
• Rule out non-epileptic events (syncope, psychogenic, cardiac, metabolic)
• Consider provoked seizures (alcohol withdrawal, hypoglycemia, medications)

STEP 2: CLASSIFY SEIZURE TYPE
• Focal vs generalized onset
• Motor vs non-motor features
• Awareness preserved or impaired

STEP 3: IDENTIFY EPILEPSY SYNDROME (if possible)
• Age of onset, seizure types, EEG patterns, prognosis

STEP 4: DETERMINE ETIOLOGY
• Structural, genetic, infectious, metabolic, immune, unknown
• Neuroimaging and relevant investigations

DIAGNOSTIC CRITERIA FOR EPILEPSY:
• ≥2 unprovoked seizures >24 hours apart, OR
• 1 unprovoked seizure + ≥60% recurrence risk (e.g., structural lesion, epileptiform EEG), OR
• Diagnosis of an epilepsy syndrome

DIFFERENTIATING FROM MIMICS:

SYNCOPE vs SEIZURE:
• Syncope: Prodrome (lightheaded, vision changes), pallor, brief if any convulsive movements, rapid recovery
• Seizure: May have aura, cyanosis, prolonged convulsion, post-ictal confusion, tongue biting, incontinence

PSYCHOGENIC NON-EPILEPTIC SEIZURES (PNES):
• Prolonged, waxing/waning, eyes closed (often), pelvic thrusting, crying
• Normal EEG during event; may coexist with epilepsy
• History of trauma/psychiatric comorbidity common

INVESTIGATIONS:

ESSENTIAL:
• Detailed history and witness account (most important)
• Physical and neurological examination
• Blood glucose (exclude hypoglycemia)
• Basic metabolic panel (sodium, calcium, renal function)

STRONGLY RECOMMENDED:
• EEG: Supports diagnosis, helps classify seizure type/syndrome
  - Interictal: Epileptiform discharges (spikes, sharp waves)
  - May be normal in up to 50% after first seizure
  - Sleep-deprived or prolonged EEG increases yield
• Neuroimaging (MRI preferred, CT if unavailable)
  - Identifies structural causes
  - MRI superior for subtle lesions
  - Indicated for: Focal seizures, focal neurologic signs, infants, elderly onset

ADDITIONAL (Based on Clinical Suspicion):
• Lumbar puncture: If CNS infection suspected
• HIV testing: HIV-related opportunistic infections, cryptococcosis
• Neurocysticercosis serology: Endemic areas, ring-enhancing lesions
• Toxicology screen: Suspected substance use
• ECG: Rule out cardiac causes (long QT, arrhythmias)
• Prolactin level: Elevated post-ictally in GTC (supports diagnosis)`,

    differentialDiagnosis: [
      'Syncope (vasovagal, cardiac, orthostatic) - Prodrome, pallor, brief convulsive movements, rapid recovery',
      'Psychogenic non-epileptic seizures (PNES) - Variable semiology, eyes often closed, prolonged, waxing/waning',
      'Transient ischemic attack (TIA) - Focal neurologic deficit, typically negative symptoms (weakness, numbness)',
      'Migraine with aura - Gradual onset visual/sensory symptoms, headache follows',
      'Hypoglycemia - Sweating, tremor, confusion, responds to glucose',
      'Cardiac arrhythmias - Palpitations, sudden LOC, may have convulsive syncope',
      'Sleep disorders (narcolepsy, parasomnias) - Sleep-related, characteristic features',
      'Movement disorders (dystonia, tics, tremor) - No LOC, characteristic patterns',
      'Panic attacks - Anxiety, hyperventilation, no LOC or post-ictal state',
      'Transient global amnesia - Sudden memory loss, repetitive questioning, no LOC',
      'Hyperventilation syndrome - Tingling, lightheadedness, anxiety, responds to rebreathing',
      'Drug intoxication or withdrawal - History of substance use, characteristic syndromes',
      'Metabolic encephalopathy - Underlying metabolic derangement, altered consciousness'
    ],

    investigations: [
      {
        name: 'Detailed History and Witness Account',
        indication: '🔴 ESSENTIAL - Most important diagnostic tool; obtain description from patient AND observer; video if available',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Exclude hypoglycemia as cause of seizure or altered consciousness',
        availability: 'BASIC'
      },
      {
        name: 'Serum Electrolytes (Sodium, Potassium, Calcium, Magnesium)',
        indication: '🔴 ESSENTIAL - Exclude metabolic causes; hyponatremia and hypocalcemia can cause seizures',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Baseline before starting ASMs; infection workup',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function Tests (Urea, Creatinine)',
        indication: '🔴 ESSENTIAL - Baseline; uremia can cause seizures; affects ASM dosing',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🔴 ESSENTIAL - Baseline before hepatically metabolized ASMs; hepatic encephalopathy',
        availability: 'BASIC'
      },
      {
        name: 'Electroencephalogram (EEG)',
        indication: '🟠 IMPORTANT - Supports diagnosis, classifies seizure type/syndrome; may be normal interictally; sleep-deprived increases yield',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT Head',
        indication: '🟠 IMPORTANT - If MRI unavailable; detects major structural lesions, calcifications (NCC), hemorrhage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI Brain',
        indication: '🟡 IF AVAILABLE - Preferred imaging; detects subtle lesions (hippocampal sclerosis, cortical malformations, small tumors)',
        availability: 'ADVANCED'
      },
      {
        name: 'HIV Test',
        indication: '🟠 IMPORTANT - HIV-related opportunistic infections (toxoplasmosis, cryptococcosis, PML) can cause seizures',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar Puncture and CSF Analysis',
        indication: '🟠 IMPORTANT - If CNS infection suspected (meningitis, encephalitis); new-onset seizures with fever',
        availability: 'BASIC'
      },
      {
        name: 'Neurocysticercosis Serology/Stool for Ova',
        indication: '🟠 IMPORTANT - Endemic areas; ring-enhancing lesions on imaging; serodiagnosis supports NCC',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: '🟠 IMPORTANT - Exclude cardiac causes (long QT, arrhythmias); baseline before QT-prolonging ASMs',
        availability: 'BASIC'
      },
      {
        name: 'Antiseizure Medication Levels',
        indication: '🟡 IF AVAILABLE - For phenytoin (narrow therapeutic index), suspected toxicity, adherence concerns, breakthrough seizures',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pregnancy Test',
        indication: '🔴 ESSENTIAL - Women of childbearing age before starting ASMs; valproate contraindicated in pregnancy',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Ensure events are truly epileptic seizures; exclude mimics and provoked seizures',
        '⚡ CLASSIFY: Determine seizure type (focal vs generalized) - guides drug selection',
        '⚡ INVESTIGATE: Baseline labs, consider EEG and neuroimaging based on clinical picture',
        '⚡ IDENTIFY CAUSE: Treat underlying etiology if found (NCC, tumor, infection, metabolic)',
        '⚡ WOMEN OF CHILDBEARING AGE: Discuss teratogenicity; avoid valproate; ensure contraception if sexually active',
        '⚡ SELECT ASM: Choose based on seizure type, patient factors, availability, and cost',
        '⚡ START LOW, GO SLOW: Initiate at low dose of monotherapy; titrate gradually',
        '⚡ EDUCATE: Explain diagnosis, treatment, adherence, safety precautions, and stigma issues',
        '⚡ SAFETY COUNSELING: Driving restrictions, swimming supervision, heights, fire/cooking, machinery',
        '⚡ FOLLOW-UP PLAN: Schedule review in 2-4 weeks; assess efficacy, tolerability, adherence'
      ],
      
      pharmacological: [
        {
          drug: 'Carbamazepine',
          indication: '🔴 FIRST-LINE for FOCAL SEIZURES - Effective, affordable, widely available; WHO Essential Medicine',
          dose: 'Adults: Start 100-200mg BD, increase by 100-200mg weekly. Target: 400-600mg BD (800-1200mg/day). Max: 1600-2000mg/day. Children: 5-10mg/kg/day in 2-3 doses, increase gradually',
          route: 'PO',
          duration: 'Long-term; consider withdrawal after 2+ years seizure-free',
          contraindications: ['AV block', 'History of bone marrow suppression', 'Porphyria', 'MAOIs within 14 days', 'HLA-B*1502 positive (Asian ancestry - Stevens-Johnson risk)'],
          sideEffects: ['Drowsiness', 'Dizziness', 'Ataxia', 'Diplopia', 'Nausea', 'Hyponatremia', 'Rash (including severe SJS/TEN)', 'Enzyme induction (drug interactions)'],
          alternatives: ['Phenytoin', 'Lamotrigine', 'Levetiracetam', 'Oxcarbazepine']
        },
        {
          drug: 'Sodium Valproate',
          indication: '🔴 FIRST-LINE for GENERALIZED SEIZURES - Broad spectrum; effective for GTC, absence, myoclonic; WHO Essential Medicine',
          dose: 'Adults: Start 300-500mg/day in 1-2 doses, increase by 200-500mg every 3-7 days. Target: 1000-2000mg/day. Max: 2500-3000mg/day. Children: 10-15mg/kg/day, increase to 25-30mg/kg/day',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Pregnancy (AVOID - teratogenic)', 'Women of childbearing potential (unless no alternative and on contraception)', 'Liver disease', 'Urea cycle disorders', 'Mitochondrial disease (POLG mutations)'],
          sideEffects: ['Weight gain', 'Tremor', 'Hair loss', 'Nausea', 'Hepatotoxicity (rare, monitor LFTs)', 'Thrombocytopenia', 'Teratogenicity (neural tube defects, neurodevelopmental effects)', 'Pancreatitis (rare)'],
          alternatives: ['Lamotrigine', 'Levetiracetam', 'Topiramate']
        },
        {
          drug: 'Phenobarbital',
          indication: '🔴 FIRST-LINE in RESOURCE-LIMITED SETTINGS - Very affordable, once daily, effective; WHO Essential Medicine',
          dose: 'Adults: 60-180mg/day once daily (usually at bedtime). Start 60mg, increase by 30mg every 2 weeks. Children: 3-5mg/kg/day once daily',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Porphyria', 'Severe respiratory depression', 'Severe hepatic impairment'],
          sideEffects: ['Sedation (often improves with time)', 'Cognitive impairment', 'Behavioral changes (children)', 'Depression', 'Vitamin D deficiency', 'Enzyme induction', 'Dependence'],
          alternatives: ['Carbamazepine', 'Phenytoin', 'Valproate']
        },
        {
          drug: 'Phenytoin',
          indication: '🟠 ALTERNATIVE for FOCAL and GTC - Effective but narrow therapeutic index; requires monitoring',
          dose: 'Adults: 200-400mg/day in 1-2 doses. Start 200-300mg/day. Max: 500-600mg/day. Therapeutic level: 10-20 mcg/mL. Children: 5-8mg/kg/day in 2 doses',
          route: 'PO',
          duration: 'Long-term; monitor levels',
          contraindications: ['Sinus bradycardia', 'SA block', 'Second/third degree AV block', 'Porphyria'],
          sideEffects: ['Gingival hyperplasia', 'Hirsutism', 'Coarsening of facial features', 'Ataxia', 'Nystagmus', 'Peripheral neuropathy', 'Osteomalacia', 'Folate deficiency', 'Teratogenic'],
          alternatives: ['Carbamazepine', 'Lamotrigine', 'Levetiracetam']
        },
        {
          drug: 'Lamotrigine',
          indication: '🟠 FIRST-LINE for FOCAL and GENERALIZED - Broad spectrum; safer in pregnancy; preferred for women of childbearing age',
          dose: 'Adults (monotherapy): Start 25mg daily x 2 weeks, then 50mg daily x 2 weeks, then increase by 50mg every 1-2 weeks. Target: 100-200mg BD. With valproate: Half the dose, slower titration. With enzyme inducers: Double the dose',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Previous severe rash with lamotrigine'],
          sideEffects: ['Rash (including SJS/TEN - slow titration reduces risk)', 'Headache', 'Dizziness', 'Diplopia', 'Insomnia', 'Nausea'],
          alternatives: ['Levetiracetam', 'Carbamazepine', 'Valproate']
        },
        {
          drug: 'Levetiracetam',
          indication: '🟠 FIRST-LINE ALTERNATIVE - Broad spectrum; minimal interactions; renally excreted; increasingly available generically',
          dose: 'Adults: Start 250-500mg BD, increase by 500mg every 2 weeks. Target: 1000-1500mg BD. Max: 3000mg/day. Children: 10mg/kg BD, increase to 30mg/kg BD',
          route: 'PO (IV available)',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Behavioral changes (irritability, aggression, depression)', 'Somnolence', 'Dizziness', 'Generally well tolerated'],
          alternatives: ['Lamotrigine', 'Valproate', 'Carbamazepine']
        },
        {
          drug: 'Ethosuximide',
          indication: '🔴 FIRST-LINE for ABSENCE SEIZURES - Drug of choice for childhood absence epilepsy',
          dose: 'Adults and children >6 years: Start 250mg BD, increase by 250mg every 4-7 days. Target: 750-1500mg/day in 2 doses. Max: 2000mg/day',
          route: 'PO',
          duration: 'Long-term; may attempt withdrawal if seizure-free in adolescence',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['GI upset (nausea, vomiting)', 'Drowsiness', 'Headache', 'Hiccups', 'Blood dyscrasias (rare)', 'Lupus-like syndrome (rare)'],
          alternatives: ['Valproate (also effective for absence)']
        },
        {
          drug: 'Clobazam',
          indication: '🟠 ADJUNCTIVE THERAPY - Add-on for refractory focal or generalized seizures; useful for catamenial epilepsy',
          dose: 'Adults: Start 5-10mg at night, increase to 10-20mg/day in 1-2 doses. Max: 40mg/day. Children: 0.25-0.5mg/kg/day',
          route: 'PO',
          duration: 'Long-term; tolerance may develop',
          contraindications: ['Severe respiratory depression', 'Myasthenia gravis', 'Sleep apnea'],
          sideEffects: ['Sedation', 'Dizziness', 'Ataxia', 'Tolerance', 'Withdrawal seizures if stopped abruptly'],
          alternatives: ['Clonazepam', 'Other add-on ASMs']
        },
        {
          drug: 'Topiramate',
          indication: '🟡 SECOND-LINE - Broad spectrum; useful adjunct; causes weight loss',
          dose: 'Adults: Start 25mg at night, increase by 25-50mg weekly. Target: 100-200mg BD. Max: 400mg/day',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Hypersensitivity', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Cognitive slowing (word-finding difficulty)', 'Paresthesias', 'Weight loss', 'Kidney stones', 'Metabolic acidosis', 'Teratogenic (oral clefts)'],
          alternatives: ['Levetiracetam', 'Lamotrigine', 'Zonisamide']
        },
        {
          drug: 'Albendazole (for Neurocysticercosis)',
          indication: '🟠 ETIOLOGIC TREATMENT - For viable parenchymal neurocysticercosis with multiple cysts',
          dose: 'Adults: 15mg/kg/day (max 800mg) in 2 divided doses with fatty food. Duration: 8-30 days depending on cyst burden',
          route: 'PO',
          duration: 'Course varies; often with corticosteroids',
          contraindications: ['Pregnancy', 'Subarachnoid or intraventricular cysts (risk of hydrocephalus)'],
          sideEffects: ['Headache (may worsen initially)', 'Seizures (peri-treatment increase)', 'Elevated LFTs', 'Alopecia'],
          alternatives: ['Praziquantel 50mg/kg/day in 3 doses for 15 days']
        },
        {
          drug: 'Dexamethasone/Prednisolone (for NCC)',
          indication: '🟠 ADJUNCT for NCC - Reduces inflammation and edema during antiparasitic treatment',
          dose: 'Dexamethasone: 0.1mg/kg/day or Prednisolone: 1mg/kg/day. Start before or with antiparasitic; taper over 2-4 weeks',
          route: 'PO',
          duration: 'During and shortly after antiparasitic treatment',
          contraindications: ['Active infection (other than NCC)'],
          sideEffects: ['Hyperglycemia', 'Immunosuppression', 'GI upset', 'Mood changes'],
          alternatives: ['May use higher doses if significant edema']
        }
      ],
      
      nonPharmacological: [
        'LIFESTYLE MODIFICATIONS: Regular sleep (sleep deprivation is a trigger), avoid excessive alcohol, stress management',
        'SEIZURE DIARY: Track seizures, potential triggers, medication times; aids management decisions',
        'SAFETY PRECAUTIONS: No unsupervised swimming, shower instead of bath, caution with heights, fire/cooking safety, avoid dangerous machinery',
        'DRIVING: Counsel on local driving regulations; typically requires seizure-free period (varies by region)',
        'KETOGENIC DIET: Considered for drug-resistant epilepsy, especially in children; requires supervision',
        'EDUCATION AND COUNSELING: Explain epilepsy, treatment, prognosis; address misconceptions and stigma',
        'PSYCHOSOCIAL SUPPORT: Address depression, anxiety (common comorbidities); support groups; vocational rehabilitation',
        'ADHERENCE STRATEGIES: Pill boxes, alarms, simple regimens, education on importance of compliance',
        'SCHOOL/WORK SUPPORT: Inform teachers/employers as appropriate; ensure safety measures; combat discrimination',
        'FIRST AID EDUCATION: Teach family/caregivers seizure first aid; do NOT put anything in mouth'
      ],
      
      surgical: [
        'RESECTIVE SURGERY: For drug-resistant focal epilepsy with identifiable lesion; anterior temporal lobectomy most common (60-80% seizure-free)',
        'LESIONECTOMY: Removal of epileptogenic lesion (tumor, cavernoma, FCD); guided by neuroimaging and EEG',
        'CORPUS CALLOSOTOMY: For drop attacks in generalized epilepsy; reduces severity rather than eliminates seizures',
        'VAGUS NERVE STIMULATION (VNS): Palliative for drug-resistant epilepsy; reduces seizure frequency by 30-50%',
        'DEEP BRAIN STIMULATION: Emerging option for drug-resistant epilepsy; anterior thalamic nucleus',
        'RESPONSIVE NEUROSTIMULATION: Closed-loop system that detects and responds to seizure activity'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE REFERRAL: Status epilepticus or prolonged seizure >5 minutes - emergency management',
        '🔴 IMMEDIATE REFERRAL: New neurological deficit post-seizure - exclude stroke, mass lesion',
        '🔴 IMMEDIATE REFERRAL: Suspected brain tumor or space-occupying lesion - urgent neurosurgical evaluation',
        '🟠 URGENT REFERRAL: Drug-resistant epilepsy (failed 2 appropriate ASMs at adequate doses) - specialist evaluation, consider surgery',
        '🟠 URGENT REFERRAL: Diagnostic uncertainty - unclear if epilepsy, need for EEG/advanced testing',
        '🟠 URGENT REFERRAL: First seizure in pregnancy - management complexities, fetal monitoring',
        '🟠 URGENT REFERRAL: Severe medication side effects - Steven-Johnson syndrome, hepatotoxicity',
        '🟡 ROUTINE REFERRAL: New diagnosis of epilepsy - confirm diagnosis, classify, investigate',
        '🟡 ROUTINE REFERRAL: Pre-conception counseling for women with epilepsy - optimize ASM, folic acid',
        '🟡 ROUTINE REFERRAL: Consideration for surgery - focal epilepsy not responding to medications',
        '🟡 ROUTINE REFERRAL: Psychiatric comorbidity (depression, anxiety) - common and undertreated'
      ],
      
      monitoring: [
        'SEIZURE FREQUENCY: Document in seizure diary; assess at each visit; goal is seizure freedom or maximum reduction',
        'MEDICATION ADHERENCE: Ask about missed doses; check refill patterns; common cause of breakthrough seizures',
        'SIDE EFFECTS: Assess cognitive function, mood, weight, specific drug effects at each visit',
        'DRUG LEVELS: Phenytoin (narrow index) - monitor levels; others if toxicity, non-adherence, or pregnancy suspected',
        'BLOOD TESTS: FBC, LFTs at baseline and periodically (6-12 monthly) for carbamazepine, valproate, phenytoin',
        'BONE HEALTH: Consider vitamin D supplementation and DEXA if long-term enzyme-inducing ASMs',
        'PREGNANCY MONITORING: More frequent visits, drug levels, detailed ultrasound, AFP if on valproate',
        'QUALITY OF LIFE: Assess impact on work, relationships, driving, mood; address psychosocial issues'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

NEWLY DIAGNOSED:
• Review in 2-4 weeks after starting treatment
• Assess tolerance, side effects, adherence
• Check if seizures continuing; adjust dose if needed
• Review diagnosis if events continue despite treatment

STABLE ON TREATMENT:
• Review every 3-6 months initially
• Can extend to 6-12 monthly if seizure-free and stable
• At each visit: Seizure frequency, adherence, side effects, psychosocial issues

TREATMENT ADJUSTMENT:
• If seizures continue: Check adherence first, then increase dose to maximum tolerated
• If first drug fails at adequate dose: Consider alternative monotherapy OR add second drug
• If second drug fails: Refer for specialist evaluation; consider drug-resistant epilepsy

DRUG-RESISTANT EPILEPSY:
• Defined as failure of 2 appropriately chosen and used ASMs
• Comprehensive evaluation: MRI, video-EEG monitoring
• Consider surgery if focal and resectable
• Palliative options: VNS, dietary therapy, additional ASMs

WITHDRAWAL OF ASMs:
• Consider after 2+ years seizure-free (longer for some syndromes)
• Factors favoring withdrawal: Normal EEG, no underlying lesion, single seizure type, childhood-onset
• Taper slowly over 3-6 months; never stop abruptly
• Recurrence risk: 20-40% within 2 years of withdrawal

SPECIAL POPULATIONS:

WOMEN OF CHILDBEARING AGE:
• Avoid valproate if possible (teratogenic, neurodevelopmental)
• Use effective contraception (note: enzyme-inducing ASMs reduce efficacy of hormonal contraceptives)
• Pre-conception counseling: Optimize ASM, high-dose folic acid (5mg/day)
• Pregnancy: Monitor closely, adjust doses as needed, detailed ultrasound

PREGNANCY:
• Continue ASM - uncontrolled seizures are more harmful than most ASMs
• Monotherapy at lowest effective dose
• Monthly visits; drug levels (free levels for highly protein-bound drugs)
• Detailed anomaly ultrasound at 18-20 weeks
• Vitamin K 10mg daily in last month if on enzyme-inducers (prevents neonatal hemorrhage)

ELDERLY:
• Start at lower doses; slower titration
• Consider drug interactions (polypharmacy common)
• Levetiracetam and lamotrigine often preferred (fewer interactions)
• Monitor for cognitive effects

COMORBID CONDITIONS:
• Depression/anxiety: Common; screen and treat; levetiracetam may worsen mood
• Migraine: Topiramate or valproate may help both
• Renal impairment: Adjust doses of renally excreted ASMs (levetiracetam, gabapentin)
• Hepatic impairment: Caution with hepatically metabolized ASMs; avoid valproate`
    },

    redFlags: [
      '🔴 CRITICAL: Status epilepticus (seizure >5 min or recurrent without recovery) - medical emergency; benzodiazepines, airway management',
      '🔴 CRITICAL: New focal neurological deficit - exclude stroke, tumor, or other structural cause; urgent imaging',
      '🔴 CRITICAL: Seizure in pregnancy - risk to mother and fetus; manage as per protocol; fetal monitoring',
      '🔴 CRITICAL: First seizure with fever and neck stiffness - meningitis until proven otherwise; LP and antibiotics',
      '🔴 CRITICAL: Severe rash on ASM (blistering, mucosal involvement) - possible SJS/TEN; stop drug immediately, hospitalize',
      '🟠 URGENT: Increasing seizure frequency despite compliance - reassess diagnosis, consider drug resistance',
      '🟠 URGENT: Signs of drug toxicity (severe ataxia, confusion, nystagmus) - check levels, reduce dose',
      '🟠 URGENT: Suicidal ideation or severe depression - common in epilepsy; urgent psychiatric input',
      '🟠 URGENT: Seizure with prolonged post-ictal paralysis (Todd paresis >24 hrs) - exclude stroke or structural lesion',
      '🟡 CAUTION: Non-adherence identified - address barriers, simplify regimen, educate on risks',
      '🟡 CAUTION: Woman with epilepsy wishing to conceive - optimize ASM, folic acid, counseling before pregnancy',
      '🟡 CAUTION: Drowsiness or cognitive decline on phenobarbital - consider alternative if significantly impacted'
    ],

    complications: [
      'Status epilepticus - Prolonged or repetitive seizures; medical emergency; 10-20% mortality',
      'SUDEP (Sudden Unexpected Death in Epilepsy) - Risk 1:1000/year; poorly controlled GTC seizures highest risk',
      'Injuries - Falls, burns, drowning, motor vehicle accidents during seizures',
      'Cognitive impairment - May be due to underlying cause, frequent seizures, or ASM side effects',
      'Psychiatric comorbidities - Depression (20-30%), anxiety (20-25%); suicide risk elevated',
      'Social consequences - Stigma, discrimination, unemployment, driving restrictions, relationship difficulties',
      'ASM side effects - Sedation, cognitive effects, teratogenicity, metabolic effects, skin reactions',
      'Drug-resistant epilepsy - 30% of patients; failure of 2+ appropriate ASMs; may need surgery',
      'Bone disease - Osteoporosis/osteomalacia with long-term enzyme-inducing ASMs',
      'Todd paralysis - Transient post-ictal focal weakness; resolves within 48 hours'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• 60-70% of patients achieve seizure freedom with first appropriate ASM
• Additional 10-15% controlled with second drug or combination
• 30% have drug-resistant epilepsy (fail 2+ appropriate ASMs)
• Of drug-resistant patients, 50-60% may benefit from surgery if focal

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Idiopathic/genetic epilepsy
• Normal neurological examination
• Normal MRI and EEG
• Good response to first ASM
• Infrequent seizures before treatment
• Single seizure type
• Childhood-onset benign syndromes

Worse Outcomes:
• Structural brain lesion
• Abnormal neurological examination
• Multiple seizure types
• Early-onset symptomatic epilepsy
• Failure of multiple ASMs
• Status epilepticus history
• Lennox-Gastaut and similar syndromes

SYNDROME-SPECIFIC PROGNOSIS:
• Childhood absence epilepsy: Excellent; 70-80% remit in adolescence
• Juvenile myoclonic epilepsy: Seizure control usually good but lifelong treatment needed; relapse if ASMs stopped
• Benign rolandic epilepsy: Remits by adolescence; excellent prognosis
• Temporal lobe epilepsy: Often drug-resistant; surgery can be curative (60-80% seizure-free)
• Lennox-Gastaut syndrome: Poor; multiple seizure types, intellectual disability, drug-resistant

MORTALITY:
• 2-3 times higher than general population
• Causes: SUDEP, status epilepticus, accidents, suicide, underlying disease
• SUDEP risk factors: Frequent GTC seizures, nocturnal seizures, non-adherence
• Good seizure control reduces mortality risk

ASM WITHDRAWAL:
• After 2+ years seizure-free, withdrawal can be considered
• Overall recurrence risk: 20-40% within 2 years
• Lower risk: No underlying lesion, normal EEG, longer seizure-free period
• Many adults choose to continue treatment to avoid recurrence risk`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing Epilepsy):

PERINATAL CARE:
• Skilled birth attendance - prevents birth asphyxia (major cause in Africa)
• Neonatal resuscitation programs
• Prevention and treatment of neonatal infections

PREVENTION OF CNS INFECTIONS:
• Vaccination: Measles, meningococcal, pneumococcal, Hib, Japanese encephalitis (endemic areas)
• Malaria prevention and prompt treatment - cerebral malaria causes epilepsy
• Neurocysticercosis prevention: Sanitation, safe water, proper pork cooking, treatment of taeniasis

HEAD INJURY PREVENTION:
• Road safety: Seatbelts, helmets, speed limits
• Fall prevention in elderly and children
• Violence prevention programs

STROKE PREVENTION:
• Hypertension control
• Diabetes management
• Smoking cessation
• Post-stroke seizure risk - consider short-term prophylaxis in high-risk

SECONDARY PREVENTION (Preventing Seizure Recurrence):

MEDICATION ADHERENCE:
• Education on importance of daily medication
• Simple regimens (once or twice daily)
• Addressing barriers: Cost, access, side effects, beliefs

AVOIDING TRIGGERS:
• Sleep deprivation - ensure adequate sleep
• Alcohol - avoid excess; withdrawal can trigger seizures
• Drug interactions - review all medications
• Photosensitive epilepsy - avoid flashing lights, use blue filter

LIFESTYLE:
• Stress management
• Regular meals (avoid hypoglycemia)
• Avoid recreational drugs

TERTIARY PREVENTION (Preventing Complications):

• Optimize seizure control to reduce SUDEP and injury risk
• Safety counseling: Swimming, bathing, heights, cooking, machinery
• Psychiatric screening and treatment
• Bone health protection with vitamin D if on enzyme-inducers
• Pre-conception planning for women

PUBLIC HEALTH PRIORITIES FOR AFRICA:
• Reduce treatment gap through improved access to ASMs
• Training of primary care providers in epilepsy management
• Combat stigma through education and awareness campaigns
• Improve birth and perinatal care
• NCC control programs in endemic areas
• Strengthen referral pathways for drug-resistant epilepsy`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS EPILEPSY?
Epilepsy is a brain condition that causes repeated seizures. During a seizure, there is a sudden burst of electrical activity in the brain that temporarily affects how it works. Epilepsy is NOT a mental illness, NOT contagious, and NOT a curse or spiritual problem. It is a medical condition that can be treated with medicine.

THE GOOD NEWS:
• Most people with epilepsy can control their seizures with medication
• 7 out of 10 people become seizure-free with the right medicine
• People with epilepsy can live normal, full lives - work, marry, have children

TAKING YOUR MEDICINE:
• Take your medicine EVERY DAY at the same time
• NEVER stop suddenly - this can cause dangerous seizures
• If you miss a dose, take it as soon as you remember (unless almost time for next dose)
• Keep enough medicine at home - do not let it run out
• Tell your doctor about any side effects, but do not stop without asking first

COMMON SIDE EFFECTS (often improve with time):
• Drowsiness or tiredness (especially at first)
• Dizziness
• Weight changes
• Tell your doctor if side effects are troubling you - dosage or medicine can often be adjusted

WHAT CAN TRIGGER SEIZURES?
• Missing your medicine (most common!)
• Not enough sleep
• Too much alcohol
• Stress
• Flashing lights (in some people)
• Illness with fever

SAFETY PRECAUTIONS:
• Swimming: Never swim alone; always have someone watching who knows you have epilepsy
• Bathing: Showers are safer than baths; do not lock the bathroom door
• Cooking: Use back burners; turn pot handles inward; microwave is safer
• Heights: Avoid climbing ladders or being on rooftops/balconies alone
• Driving: Follow local rules - most require being seizure-free for a certain period

WHAT TO DO IF SOMEONE HAS A SEIZURE:
✓ Stay calm
✓ Protect from injury - move dangerous objects away
✓ Turn on their side (recovery position) once jerking stops
✓ Stay with them until fully awake
✓ Time the seizure

✗ DO NOT put anything in the mouth - they will NOT swallow their tongue
✗ DO NOT hold them down
✗ DO NOT give water or medicine during the seizure

WHEN TO GET EMERGENCY HELP:
• Seizure lasts more than 5 minutes
• Another seizure starts before they wake up
• They do not wake up after 10 minutes
• They are injured
• First ever seizure
• Seizure in water
• Seizure during pregnancy

FOR WOMEN:
• Tell your doctor if you might become pregnant
• Some medicines can harm an unborn baby
• Safe medicines are available for pregnancy
• Take folic acid if planning pregnancy
• Breastfeeding is usually safe - ask your doctor

LIVING WELL WITH EPILEPSY:
• Get regular sleep - go to bed and wake up at the same time
• Eat regular meals
• Limit alcohol
• Manage stress
• Keep a seizure diary
• Attend regular check-ups
• Join a support group if available

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Epilepsy is a treatable medical condition. With the right medicine taken regularly, most people with epilepsy can live normal, healthy lives. You should not be ashamed of having epilepsy - it is no different from other medical conditions like diabetes or high blood pressure. Take your medicine, keep safe, and live your life fully.`,

    references: [
      'Fisher RS, Acevedo C, Arzimanoglou A, et al. ILAE official report: a practical clinical definition of epilepsy. Epilepsia. 2014;55(4):475-482.',
      'Scheffer IE, Berkovic S, Capovilla G, et al. ILAE classification of the epilepsies: Position paper of the ILAE Commission for Classification and Terminology. Epilepsia. 2017;58(4):512-521.',
      'WHO. Epilepsy: a public health imperative. World Health Organization. 2019.',
      'Ngugi AK, Bottomley C, Kleinschmidt I, et al. Estimation of the burden of active and life-time epilepsy: a meta-analytic approach. Epilepsia. 2010;51(5):883-890.',
      'Mbuba CK, Ngugi AK, Newton CR, Carter JA. The epilepsy treatment gap in developing countries: a systematic review of the magnitude, causes, and intervention strategies. Epilepsia. 2008;49(9):1491-1503.',
      'Kwan P, Arzimanoglou A, Berg AT, et al. Definition of drug resistant epilepsy: consensus proposal by the ad hoc Task Force of the ILAE Commission on Therapeutic Strategies. Epilepsia. 2010;51(6):1069-1077.',
      'Glauser T, Ben-Menachem E, Bourgeois B, et al. Updated ILAE evidence review of antiepileptic drug efficacy and effectiveness as initial monotherapy for epileptic seizures and syndromes. Epilepsia. 2013;54(3):551-563.',
      'Marson AG, Al-Kharusi AM, Alwaidh M, et al. The SANAD study of effectiveness of carbamazepine, gabapentin, lamotrigine, oxcarbazepine, or topiramate for treatment of partial epilepsy: an unblinded randomised controlled trial. Lancet. 2007;369(9566):1000-1015.',
      'Tomson T, Battino D, Bonizzoni E, et al. Comparative risk of major congenital malformations with eight different antiepileptic drugs: a prospective cohort study of the EURAP registry. Lancet Neurol. 2018;17(6):530-538.',
      'Garcia HH, Nash TE, Del Brutto OH. Clinical symptoms, diagnosis, and treatment of neurocysticercosis. Lancet Neurol. 2014;13(12):1202-1215.',
      'Wilmshurst JM, Kakooza-Mwesige A, Newton CR. The challenges of managing children with epilepsy in Africa. Semin Pediatr Neurol. 2014;21(1):36-41.',
      'Winkler AS, Kerschbaumsteiner K, Stelzhammer B, et al. Prevalence, incidence, and clinical characteristics of epilepsy - a community-based door-to-door study in northern Tanzania. Epilepsia. 2009;50(10):2310-2313.',
      'Birbeck GL, Meyer AC, Ogunniyi A. Nervous system disorders across the life course in resource-limited settings. Nature. 2015;527(7578):S167-S171.',
      'Keezer MR, Sisodiya SM, Sander JW. Comorbidities of epilepsy: current concepts and future perspectives. Lancet Neurol. 2016;15(1):106-115.',
      'Devinsky O, Hesdorffer DC, Thurman DJ, et al. Sudden unexpected death in epilepsy: epidemiology, mechanisms, and prevention. Lancet Neurol. 2016;15(10):1075-1088.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 77. DEPRESSION
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '77',
    name: 'Depression',
    slug: 'depression',
    specialty: 'Psychiatry',
    bodySystem: 'Neurological',
    icdCode: 'F32',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Depression (Major Depressive Disorder) is a common and serious mental health condition characterized by persistent low mood, loss of interest or pleasure, and a range of emotional, cognitive, and physical symptoms that significantly impair daily functioning. It is the leading cause of disability worldwide, affecting more than 280 million people globally. Depression is not a sign of weakness or a character flaw - it is a medical condition that responds to treatment.

The pathophysiology of depression involves complex interactions between genetic vulnerability, neurobiological factors (monoamine neurotransmitter dysfunction, HPA axis dysregulation, neuroinflammation, reduced neuroplasticity), and psychosocial stressors. The monoamine hypothesis suggests deficiency in serotonin, norepinephrine, and dopamine, which forms the basis for antidepressant pharmacotherapy. However, the neurobiology is far more complex, involving alterations in brain circuits, neurogenesis, and inflammatory processes.

Depression in Africa represents a significant but underrecognized health burden. Prevalence estimates range from 3-15% depending on the population studied, with higher rates in conflict zones, among people living with HIV, and in populations affected by poverty and displacement. The treatment gap is enormous: up to 90% of people with depression in sub-Saharan Africa receive no treatment. Barriers include severe shortage of mental health professionals (<0.5 psychiatrists per 100,000 in most African countries), limited access to medications, high levels of stigma, and frequent presentation with somatic symptoms rather than psychological complaints.

With appropriate treatment, most people with depression can recover. First-line treatments include antidepressant medications and structured psychological therapies. Fluoxetine (an SSRI) and amitriptyline (a TCA) are on the WHO Essential Medicines List and are effective, affordable options for resource-limited settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - ALWAYS assess suicide risk: Ask directly about suicidal thoughts, plans, and intent; asking does NOT increase risk

🔴 CRITICAL POINT 2 - SSRIs (fluoxetine, sertraline) are first-line treatment: Effective, safe, well-tolerated; fluoxetine is on WHO Essential List

🔴 CRITICAL POINT 3 - Antidepressants take 2-4 weeks to show effect; continue for at least 6-9 months after remission to prevent relapse

🔴 CRITICAL POINT 4 - Screen for bipolar disorder before starting antidepressants: Ask about previous manic/hypomanic episodes; antidepressants can trigger mania

🟠 CRITICAL POINT 5 - Depression often presents with SOMATIC symptoms in Africa: Headache, body pains, fatigue, sleep problems may be the chief complaint

🟠 CRITICAL POINT 6 - Depression is highly comorbid with chronic diseases: HIV, diabetes, heart disease, epilepsy, TB; screen and treat both conditions

🟠 CRITICAL POINT 7 - Psychological therapy (CBT, IPT, PST) is effective and can be delivered by trained non-specialists in primary care

🟡 CRITICAL POINT 8 - Amitriptyline is affordable and available; effective but more side effects and dangerous in overdose compared to SSRIs

🟡 CRITICAL POINT 9 - Address stigma: Depression is a medical illness, not weakness or spiritual problem; education reduces discrimination`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Over 280 million people affected worldwide (3.8% of population)
• Leading cause of disability globally (WHO)
• Lifetime prevalence: 15-20% (higher in women)
• Women affected 2x more than men
• Peak onset: Late adolescence to mid-20s; second peak in older age
• 700,000+ suicides annually worldwide; depression is major risk factor
• Enormous economic burden: Lost productivity, healthcare costs

AFRICAN EPIDEMIOLOGY:
• Prevalence estimates: 3-15% (varies by country and population)
• Treatment gap: 75-90% receive no treatment (among highest globally)
• Mental health workforce: <0.5 psychiatrists per 100,000 in most countries (vs 15+ in high-income countries)
• Higher rates in: Conflict-affected populations, refugees/IDPs, people living with HIV, urban poor
• HIV-associated depression: 20-40% of PLHIV have comorbid depression
• Perinatal depression: 15-25% of pregnant/postpartum women in Africa
• Often presents with somatic complaints (headache, pain, fatigue) rather than psychological symptoms
• Significant stigma and misconceptions; often attributed to spiritual causes
• Traditional healers frequently first point of contact

RISK FACTORS:
• Female sex (2:1 female to male ratio)
• Previous depressive episode (strongest predictor of recurrence)
• Family history of depression or suicide
• Childhood adversity (abuse, neglect, loss)
• Stressful life events (bereavement, divorce, job loss, trauma)
• Chronic medical illness (HIV, cancer, diabetes, stroke, chronic pain)
• Substance use disorders (alcohol, drugs)
• Social isolation and lack of support
• Poverty and unemployment
• Postpartum period
• Personality factors (neuroticism)

COMORBIDITY:
• Anxiety disorders: 50-60% comorbidity
• Substance use disorders: 30-40%
• Chronic medical conditions: Depression worsens outcomes
• Other psychiatric disorders: PTSD, eating disorders, personality disorders`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CORE SYMPTOMS (at least one required for diagnosis):
• Depressed mood: Persistent sadness, emptiness, hopelessness, tearfulness
  - May be described as heaviness, darkness, or pain in the heart
  - In some cultures, expressed as somatic complaints
• Anhedonia: Loss of interest or pleasure in activities previously enjoyed
  - No longer enjoys hobbies, socializing, sex, food
  - Everything feels like a chore

ADDITIONAL SYMPTOMS:
• Sleep disturbance:
  - Insomnia (difficulty falling/staying asleep, early morning awakening) - most common
  - Hypersomnia (excessive sleep) - more common in atypical depression
• Appetite/weight changes:
  - Decreased appetite and weight loss (typical)
  - Increased appetite and weight gain (atypical)
• Fatigue and loss of energy: Exhaustion, everything requires effort
• Psychomotor changes:
  - Retardation: Slowed speech, movement, thinking
  - Agitation: Restlessness, inability to sit still, hand-wringing
• Concentration difficulties: Trouble focusing, making decisions, memory complaints
• Worthlessness and guilt: Excessive, inappropriate guilt; feeling like a burden
• Suicidal ideation: Thoughts of death, dying, suicide; plans or attempts

SOMATIC PRESENTATIONS (Common in Africa):
• Headache (often described as heaviness or pressure)
• Generalized body pain
• Abdominal discomfort, nausea
• Chest pain or tightness
• Dizziness
• Fatigue without medical explanation
• Multiple unexplained physical complaints

SEVERITY CLASSIFICATION:

MILD DEPRESSION:
• 5 symptoms (minimum for diagnosis)
• Mild functional impairment
• Can usually continue work/activities with effort

MODERATE DEPRESSION:
• More than minimum symptoms
• Moderate functional impairment
• Difficulty maintaining work/relationships

SEVERE DEPRESSION:
• Most symptoms present and intense
• Marked functional impairment
• May have psychotic features (delusions, hallucinations)
• High suicide risk

DEPRESSION WITH PSYCHOTIC FEATURES:
• Mood-congruent delusions: Worthlessness, guilt, disease, poverty
• Mood-congruent hallucinations: Voices criticizing, commanding suicide
• Requires antipsychotic in addition to antidepressant

MELANCHOLIC FEATURES:
• Profound anhedonia (nothing brings pleasure)
• Lack of mood reactivity (cannot be cheered up)
• Worse in morning, early morning awakening
• Psychomotor changes, weight loss, excessive guilt

ATYPICAL FEATURES:
• Mood reactivity (can be cheered up temporarily)
• Increased sleep and appetite
• Leaden paralysis (heavy feeling in limbs)
• Rejection sensitivity

PERIPARTUM DEPRESSION:
• Onset during pregnancy or within 4 weeks postpartum
• Differentiate from baby blues (transient, mild, first 2 weeks)
• Screen all pregnant and postpartum women`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: SCREENING
• Use validated screening tool (PHQ-9, PHQ-2)
• PHQ-2: Two questions on depressed mood and anhedonia
• If positive (score ≥3), proceed to full assessment

STEP 2: DIAGNOSTIC INTERVIEW
• Assess all DSM-5/ICD-11 criteria
• Duration (≥2 weeks)
• Functional impairment
• Exclude other causes

STEP 3: ASSESS SEVERITY AND SAFETY
• Determine mild/moderate/severe
• Always assess suicide risk
• Assess for psychotic features

STEP 4: RULE OUT DIFFERENTIAL DIAGNOSES
• Bipolar disorder (crucial - ask about manic episodes)
• Medical conditions causing depression
• Substance-induced mood disorder
• Bereavement vs major depression

DIAGNOSTIC CRITERIA (DSM-5 - Major Depressive Episode):
Five or more symptoms during same 2-week period, with at least one being (1) or (2):
1. Depressed mood most of the day, nearly every day
2. Markedly diminished interest or pleasure (anhedonia)
3. Significant weight loss/gain or appetite change
4. Insomnia or hypersomnia nearly every day
5. Psychomotor agitation or retardation
6. Fatigue or loss of energy
7. Feelings of worthlessness or excessive guilt
8. Diminished concentration or indecisiveness
9. Recurrent thoughts of death, suicidal ideation, plan, or attempt

Plus:
• Symptoms cause clinically significant distress or impairment
• Episode not attributable to substance or medical condition
• Not better explained by other mental disorder
• No history of manic/hypomanic episode (otherwise: bipolar)

PHQ-9 SCORING:
• 0-4: Minimal/no depression
• 5-9: Mild depression
• 10-14: Moderate depression
• 15-19: Moderately severe depression
• 20-27: Severe depression

SUICIDE RISK ASSESSMENT (Essential at every visit):
Ask directly:
• Are you having thoughts of harming yourself or ending your life?
• Have you thought about how you would do it? (Plan)
• Do you have access to means? (Firearms, medications, etc.)
• Have you ever attempted suicide before?
• What stops you from acting on these thoughts? (Protective factors)

HIGH RISK INDICATORS:
• Specific plan with lethal means available
• Previous suicide attempt (strongest predictor)
• Hopelessness, no reasons for living
• Current substance intoxication
• Recent major loss or humiliation
• Social isolation
• Psychotic symptoms (command hallucinations)

INVESTIGATIONS TO EXCLUDE MEDICAL CAUSES:

ESSENTIAL:
• Thyroid function (TSH) - hypothyroidism mimics depression
• Complete blood count - anemia causes fatigue
• Blood glucose - diabetes; hypo/hyperglycemia affects mood
• HIV test - depression is common in HIV; may be first presentation

CONSIDER BASED ON CLINICAL PICTURE:
• Vitamin B12, folate - deficiency causes depression-like symptoms
• Liver function tests - hepatic encephalopathy, alcohol use
• Renal function - uremia affects cognition and mood
• Calcium - hypercalcemia can cause mood changes
• Syphilis serology (RPR/VDRL) - neurosyphilis
• Cortisol - Cushing disease (if clinical suspicion)
• Medication review - many drugs cause depression
• Urine drug screen - substance use`,

    differentialDiagnosis: [
      'Bipolar disorder - History of manic/hypomanic episodes; crucial to exclude before antidepressants',
      'Adjustment disorder - Symptoms follow identifiable stressor, less severe, resolves within 6 months',
      'Persistent depressive disorder (dysthymia) - Chronic (2+ years) milder depression',
      'Bereavement/grief - Following loss; differentiate from major depression if severe/prolonged',
      'Anxiety disorders - May coexist; primary anxiety with secondary depressive symptoms',
      'PTSD - Trauma history, re-experiencing, avoidance, hyperarousal symptoms',
      'Hypothyroidism - Fatigue, weight gain, cold intolerance; check TSH',
      'Anemia - Fatigue, weakness; check hemoglobin',
      'HIV/AIDS - Depression common; may be presenting complaint; test all',
      'Chronic fatigue syndrome - Fatigue predominant; less mood disturbance',
      'Substance use disorder - Alcohol, cannabis, opioids can cause depression; may coexist',
      'Dementia - Cognitive symptoms prominent; may have apathy mimicking depression',
      'Parkinsons disease - Psychomotor slowing, masked facies; depression is common comorbidity',
      'Medication-induced - Beta-blockers, steroids, interferon, some anticonvulsants, isotretinoin',
      'Schizoaffective disorder - Psychotic symptoms outside of mood episodes',
      'Personality disorders - Chronic mood instability; different pattern from discrete episodes'
    ],

    investigations: [
      {
        name: 'PHQ-9 (Patient Health Questionnaire-9)',
        indication: '🔴 ESSENTIAL - Validated screening and severity tool; 9 questions; score guides treatment decisions',
        availability: 'BASIC'
      },
      {
        name: 'Detailed Psychiatric History and Mental State Exam',
        indication: '🔴 ESSENTIAL - Clinical interview remains cornerstone of diagnosis; assess all symptom domains',
        availability: 'BASIC'
      },
      {
        name: 'Suicide Risk Assessment',
        indication: '🔴 ESSENTIAL - Must be performed at every visit; ask directly about thoughts, plans, intent, means',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH)',
        indication: '🔴 ESSENTIAL - Hypothyroidism mimics depression; should be checked in all new presentations',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Exclude anemia as cause of fatigue and low energy',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose (Fasting or Random)',
        indication: '🔴 ESSENTIAL - Diabetes is comorbid; hypoglycemia can cause mood symptoms',
        availability: 'BASIC'
      },
      {
        name: 'HIV Test',
        indication: '🔴 ESSENTIAL - High comorbidity; depression may be first presentation; universal testing recommended',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function Tests',
        indication: '🟠 IMPORTANT - Baseline before medications; uremia affects cognition',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT - Baseline before medications; assess for alcohol-related liver disease',
        availability: 'BASIC'
      },
      {
        name: 'Vitamin B12 Level',
        indication: '🟡 IF AVAILABLE - Deficiency causes neuropsychiatric symptoms; consider in elderly, vegetarians',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Syphilis Serology (RPR/VDRL)',
        indication: '🟡 IF INDICATED - Neurosyphilis can present with psychiatric symptoms',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🟠 IMPORTANT - Baseline before TCAs (cardiotoxic); check QTc if using QT-prolonging medications',
        availability: 'BASIC'
      },
      {
        name: 'Urine Drug Screen',
        indication: '🟡 IF INDICATED - Substance use comorbidity; may explain or worsen symptoms',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT/MRI Brain',
        indication: '🟡 IF INDICATED - Late-onset depression, focal neurological signs, atypical presentation; exclude structural lesion',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ SAFETY FIRST: Assess suicide risk at every visit; create safety plan if any risk identified',
        '⚡ RULE OUT BIPOLAR: Ask about previous manic/hypomanic episodes before starting antidepressant',
        '⚡ EXCLUDE MEDICAL CAUSES: Check TSH, CBC, glucose, HIV; treat underlying conditions',
        '⚡ SEVERITY ASSESSMENT: Use PHQ-9 to guide treatment intensity; mild vs moderate-severe',
        '⚡ PSYCHOEDUCATION: Explain depression is a medical illness, treatable; address stigma',
        '⚡ TREATMENT SELECTION: Mild - consider psychological therapy first; Moderate-severe - medication +/- therapy',
        '⚡ START ANTIDEPRESSANT: If indicated, begin SSRI (fluoxetine or sertraline); start low, go slow',
        '⚡ SET EXPECTATIONS: Explain 2-4 weeks to see improvement; importance of adherence',
        '⚡ FOLLOW-UP PLAN: Schedule review in 1-2 weeks initially; more frequent if suicide risk',
        '⚡ SUPPORT SYSTEM: Engage family/caregivers with patient permission; social support aids recovery'
      ],
      
      pharmacological: [
        {
          drug: 'Fluoxetine',
          indication: '🔴 FIRST-LINE SSRI - Effective, safe, once-daily, affordable; WHO Essential Medicine; long half-life (good for adherence)',
          dose: 'Adults: Start 20mg PO once daily in the morning. May increase to 40-60mg after 4-6 weeks if inadequate response. Max: 80mg/day. Elderly: Start 10mg',
          route: 'PO',
          duration: 'Continue for 6-9 months after remission (first episode); 2+ years if recurrent; lifelong if multiple recurrences',
          contraindications: ['MAOIs (within 14 days)', 'Pimozide', 'Thioridazine', 'Caution in severe hepatic impairment'],
          sideEffects: ['Nausea (common early)', 'Headache', 'Insomnia or drowsiness', 'Sexual dysfunction', 'Anxiety/agitation (early)', 'Weight changes', 'Serotonin syndrome (with other serotonergic drugs)'],
          alternatives: ['Sertraline', 'Escitalopram', 'Paroxetine']
        },
        {
          drug: 'Sertraline',
          indication: '🔴 FIRST-LINE SSRI - Effective, well-tolerated; preferred in cardiovascular disease and breastfeeding; less drug interactions than fluoxetine',
          dose: 'Adults: Start 50mg PO once daily. May increase by 50mg at weekly intervals. Target: 50-200mg/day. Max: 200mg/day',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['MAOIs (within 14 days)', 'Pimozide', 'Disulfiram (liquid contains alcohol)'],
          sideEffects: ['Nausea', 'Diarrhea', 'Insomnia', 'Sexual dysfunction', 'Headache', 'Dizziness'],
          alternatives: ['Fluoxetine', 'Escitalopram']
        },
        {
          drug: 'Amitriptyline',
          indication: '🟠 ALTERNATIVE (TCA) - Effective, very affordable, widely available; useful when SSRIs unavailable; also helps pain and insomnia',
          dose: 'Adults: Start 25mg PO at bedtime. Increase by 25mg every 3-7 days. Target: 75-150mg/day. Max: 300mg/day. Elderly: Start 10mg, max 75-100mg',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['Recent MI', 'Arrhythmias', 'Heart block', 'MAOIs', 'Acute narrow-angle glaucoma', 'Urinary retention'],
          sideEffects: ['Sedation', 'Dry mouth', 'Constipation', 'Urinary retention', 'Blurred vision', 'Weight gain', 'Orthostatic hypotension', 'DANGEROUS IN OVERDOSE (cardiac)'],
          alternatives: ['Imipramine', 'Clomipramine', 'SSRIs preferred if available']
        },
        {
          drug: 'Escitalopram',
          indication: '🟠 FIRST-LINE SSRI - Most selective SSRI; fewer drug interactions; well-tolerated',
          dose: 'Adults: Start 10mg PO once daily. May increase to 20mg after 1 week. Max: 20mg/day. Elderly: Max 10mg',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['MAOIs', 'QT prolongation', 'Concomitant QT-prolonging drugs'],
          sideEffects: ['Nausea', 'Headache', 'Insomnia', 'Sexual dysfunction', 'QT prolongation (dose-dependent)'],
          alternatives: ['Fluoxetine', 'Sertraline']
        },
        {
          drug: 'Mirtazapine',
          indication: '🟠 ALTERNATIVE - Useful when insomnia, poor appetite prominent; less sexual dysfunction; sedating',
          dose: 'Adults: Start 15mg PO at bedtime. May increase every 1-2 weeks. Target: 30-45mg/day. Note: Higher doses less sedating',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['MAOIs'],
          sideEffects: ['Sedation', 'Increased appetite and weight gain', 'Dry mouth', 'Constipation', 'Rarely agranulocytosis'],
          alternatives: ['Trazodone', 'Amitriptyline']
        },
        {
          drug: 'Venlafaxine',
          indication: '🟡 SECOND-LINE (SNRI) - When SSRI fails; also helps anxiety and pain; requires dose titration',
          dose: 'Extended-release: Start 75mg PO once daily with food. Increase by 75mg every 4+ days. Target: 150-225mg/day. Max: 375mg/day',
          route: 'PO',
          duration: 'Long-term; taper slowly when stopping (discontinuation syndrome)',
          contraindications: ['MAOIs', 'Uncontrolled hypertension'],
          sideEffects: ['Nausea', 'Headache', 'Insomnia', 'Sexual dysfunction', 'Hypertension (dose-dependent)', 'Discontinuation syndrome if stopped abruptly'],
          alternatives: ['Duloxetine', 'Mirtazapine augmentation']
        },
        {
          drug: 'Risperidone (for Psychotic Depression)',
          indication: '🟠 ADJUNCT - Add to antidepressant for depression with psychotic features',
          dose: 'Adults: Start 0.5-1mg PO daily. Increase gradually. Target: 1-4mg/day for psychotic depression',
          route: 'PO',
          duration: 'Until psychotic symptoms resolve, then taper gradually',
          contraindications: ['Hypersensitivity', 'QT prolongation'],
          sideEffects: ['Sedation', 'Weight gain', 'Metabolic effects', 'EPS', 'Prolactin elevation', 'Orthostatic hypotension'],
          alternatives: ['Olanzapine', 'Quetiapine', 'Haloperidol']
        },
        {
          drug: 'Lithium (Augmentation)',
          indication: '🟡 AUGMENTATION - For treatment-resistant depression; add to antidepressant',
          dose: 'Adults: Start 300mg PO twice daily. Adjust based on levels. Target level: 0.4-0.8 mEq/L for augmentation',
          route: 'PO',
          duration: 'Long-term if effective; monitor levels',
          contraindications: ['Severe renal impairment', 'Severe cardiovascular disease', 'Dehydration', 'Addison disease'],
          sideEffects: ['Tremor', 'Polyuria', 'Polydipsia', 'Weight gain', 'Hypothyroidism', 'Renal impairment (long-term)', 'Toxicity (narrow therapeutic index)'],
          alternatives: ['Thyroid hormone augmentation', 'Atypical antipsychotic augmentation']
        },
        {
          drug: 'Diazepam (Short-term)',
          indication: '🟡 ADJUNCT - Short-term only for severe anxiety or agitation while waiting for antidepressant to work',
          dose: 'Adults: 2-5mg PO two to three times daily. Use lowest effective dose. Duration: Maximum 2-4 weeks',
          route: 'PO',
          duration: 'Short-term ONLY (2-4 weeks); avoid long-term use (dependence)',
          contraindications: ['Severe respiratory depression', 'Sleep apnea', 'Myasthenia gravis', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Sedation', 'Cognitive impairment', 'Dependence', 'Withdrawal seizures if stopped abruptly'],
          alternatives: ['Hydroxyzine', 'Low-dose mirtazapine or trazodone for sleep']
        }
      ],
      
      nonPharmacological: [
        'PSYCHOEDUCATION: Explain depression as medical illness; discuss causes, treatment, prognosis; reduce stigma',
        'COGNITIVE BEHAVIORAL THERAPY (CBT): Structured therapy addressing negative thought patterns and behaviors; effective for mild-moderate depression',
        'INTERPERSONAL THERAPY (IPT): Focuses on relationships and social functioning; effective for depression',
        'PROBLEM-SOLVING THERAPY (PST): Brief, structured approach suitable for primary care; teaches coping skills',
        'BEHAVIORAL ACTIVATION: Increasing engagement in pleasant and meaningful activities; simple but effective',
        'SUPPORTIVE COUNSELING: Active listening, validation, problem-solving; can be done by trained non-specialists',
        'EXERCISE: Regular physical activity (30 min, 3-5x/week) has antidepressant effects; encourage walking, sports',
        'SLEEP HYGIENE: Regular sleep schedule, avoid screens before bed, limit caffeine and alcohol',
        'SOCIAL SUPPORT: Encourage maintaining relationships; involvement of family/friends in care',
        'STRESS REDUCTION: Relaxation techniques, mindfulness, time management',
        'SUBSTANCE AVOIDANCE: Reduce or stop alcohol and cannabis (worsen depression)',
        'RELIGIOUS/SPIRITUAL SUPPORT: May be helpful for some patients; respect cultural beliefs while ensuring medical treatment'
      ],
      
      surgical: [
        'ELECTROCONVULSIVE THERAPY (ECT): Highly effective for severe, treatment-resistant, or psychotic depression; safe with modern techniques; limited availability in Africa',
        'TRANSCRANIAL MAGNETIC STIMULATION (TMS): Non-invasive brain stimulation; for treatment-resistant depression; limited availability',
        'VAGUS NERVE STIMULATION: For chronic treatment-resistant depression; rarely available in Africa'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE/EMERGENCY: Active suicidal ideation with plan and intent - crisis intervention, possible hospitalization',
        '🔴 IMMEDIATE: Psychotic features (delusions, hallucinations) - needs antipsychotic and specialist care',
        '🔴 IMMEDIATE: Severe depression with inability to care for self - may need hospitalization',
        '🔴 IMMEDIATE: Depression in pregnancy with suicidal ideation - urgent psychiatry and obstetric coordination',
        '🟠 URGENT: Suspected bipolar disorder - requires mood stabilizer, not just antidepressant',
        '🟠 URGENT: Treatment-resistant depression (failed 2+ adequate antidepressant trials) - specialist review',
        '🟠 URGENT: Severe side effects or complications of treatment',
        '🟠 URGENT: Comorbid severe substance use disorder - integrated treatment needed',
        '🟡 ROUTINE: Diagnostic uncertainty - is it depression or another condition?',
        '🟡 ROUTINE: Request for psychotherapy - if available and patient prefers',
        '🟡 ROUTINE: Complex comorbidities requiring specialist coordination',
        '🟡 ROUTINE: Consideration for ECT in severe refractory cases'
      ],
      
      monitoring: [
        'PHQ-9 SCORE: At baseline and every visit; track improvement; score <5 indicates remission',
        'SUICIDE RISK: Assess at EVERY visit, especially first weeks of treatment (risk may increase as energy returns)',
        'SIDE EFFECTS: Especially early in treatment; adjust if intolerable',
        'ADHERENCE: Ask about missed doses; common reason for treatment failure',
        'RESPONSE TIMELINE: Expect some improvement by 2-4 weeks; full effect by 6-8 weeks',
        'FUNCTIONAL IMPROVEMENT: Work, relationships, self-care; aim for full recovery not just symptom reduction',
        'WEIGHT: Monitor especially with mirtazapine, TCAs, some antipsychotics',
        'BLOOD PRESSURE: If on venlafaxine or duloxetine (SNRIs)',
        'ECG: If on TCAs or QT-prolonging medications',
        'LITHIUM LEVEL, RENAL, THYROID: If on lithium (every 3-6 months when stable)'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (First 2-4 weeks):
• Follow-up at 1-2 weeks after starting treatment
• Assess: Suicide risk, side effects, early response, adherence
• Adjust: Manage side effects; reinforce adherence
• Note: Risk may paradoxically increase early (energy returns before mood improves)

ACUTE TREATMENT (Weeks 4-12):
• Follow-up every 2-4 weeks
• Assess response using PHQ-9
• If partial response at 4 weeks: Increase dose to maximum tolerated
• If no response at 4-6 weeks: Consider switching to different antidepressant
• If response: Continue current regimen

TREATMENT RESPONSE DEFINITIONS:
• Response: ≥50% reduction in PHQ-9 score
• Remission: PHQ-9 <5; minimal/no symptoms; full function restored
• Non-response: <25% improvement despite adequate trial

CONTINUATION PHASE (6-9 months after remission):
• Maintain same dose that achieved remission
• Follow-up monthly initially, then every 2-3 months
• Goal: Prevent relapse (return of symptoms during same episode)
• Continue antidepressant for at least 6-9 months after remission

MAINTENANCE PHASE (Long-term):
Consider indefinite treatment if:
• 3 or more depressive episodes
• 2 episodes with: Rapid recurrence, severe episodes, family history, residual symptoms
• Chronic depression (>2 years)
• Follow-up every 3-6 months when stable

DISCONTINUING ANTIDEPRESSANTS:
• Only after sustained remission (6-9 months minimum)
• Discuss with patient: Recurrence risk vs benefits of stopping
• Taper gradually over 4+ weeks (faster taper = discontinuation symptoms)
• Monitor closely for recurrence after stopping
• Educate about early warning signs and when to return

IF TREATMENT FAILS:

Step 1 - Reassess:
• Correct diagnosis? (Consider bipolar, medical cause, substance use)
• Adequate dose and duration? (6+ weeks at therapeutic dose)
• Good adherence?
• Persistent psychosocial stressors?

Step 2 - Optimize:
• Increase to maximum tolerated dose
• Ensure adequate trial duration (8-12 weeks)

Step 3 - Switch or Augment:
• Switch to different class (SSRI → SNRI, or TCA)
• Augment with: Mirtazapine, lithium, atypical antipsychotic, or thyroid hormone

Step 4 - Refer:
• Treatment-resistant depression (failed 2+ adequate trials)
• Consider ECT if available and severe

SPECIAL POPULATIONS:

Pregnancy and Breastfeeding:
• Depression during pregnancy has risks for mother and baby
• Untreated depression: Preterm birth, low birth weight, developmental effects
• SSRIs (sertraline, fluoxetine) generally preferred if medication needed
• Discuss risks and benefits; shared decision-making
• Monitor closely; dosage may need increase in third trimester
• Breastfeeding: Sertraline and paroxetine have lowest milk levels

Elderly:
• Start with lower doses (start low, go slow)
• SSRIs preferred over TCAs (safer)
• Watch for drug interactions (polypharmacy)
• Monitor for hyponatremia (SIADH)
• Consider vascular depression in late-onset cases

HIV-Associated Depression:
• Very common (20-40% of PLHIV)
• Screen all HIV patients for depression
• SSRIs generally safe with ART (check interactions)
• Treating depression improves ART adherence
• Rule out CNS opportunistic infections

Comorbid Medical Conditions:
• Depression worsens outcomes in diabetes, heart disease, cancer
• Treating depression improves medical outcomes
• Choose antidepressant based on side effect profile and interactions`
    },

    redFlags: [
      '🔴 CRITICAL: Active suicidal ideation with plan and means - emergency; ensure safety, remove means, consider hospitalization',
      '🔴 CRITICAL: Suicide attempt - emergency assessment; medical stabilization if needed; psychiatric admission',
      '🔴 CRITICAL: Psychotic symptoms (delusions of guilt, nihilism; command hallucinations) - severe depression; needs antipsychotic',
      '🔴 CRITICAL: Severe depression with inability to eat, drink, or care for self - may need hospitalization, ECT',
      '🔴 CRITICAL: Depression in pregnancy with suicidal ideation - urgent coordinated obstetric and psychiatric care',
      '🟠 URGENT: Suspected bipolar disorder (history of mania) - stop antidepressant monotherapy; start mood stabilizer',
      '🟠 URGENT: Serotonin syndrome (agitation, hyperthermia, hyperreflexia, clonus) - stop serotonergic drugs; supportive care',
      '🟠 URGENT: Worsening depression or new suicidality after starting treatment - increase monitoring; reassess',
      '🟠 URGENT: Severe side effects (severe rash, hepatotoxicity signs, cardiac symptoms on TCA)',
      '🟡 CAUTION: No improvement after 4-6 weeks of adequate treatment - reassess diagnosis, adherence; consider change',
      '🟡 CAUTION: Concurrent substance use - complicates treatment; address both issues',
      '🟡 CAUTION: Significant weight gain or metabolic effects - may affect adherence and health'
    ],

    complications: [
      'Suicide - Leading complication; risk assessment essential at every visit',
      'Self-harm - May occur without suicidal intent; indicates severity',
      'Psychotic features - Delusions, hallucinations; indicates severe depression',
      'Treatment-resistant depression - 30% do not respond to first two treatments',
      'Chronic depression - Symptoms persist >2 years; worse prognosis',
      'Recurrence - 50% after first episode; 70% after second; 90% after third',
      'Functional impairment - Work disability, relationship breakdown, financial problems',
      'Comorbid anxiety disorders - Very common; may need combined treatment',
      'Substance use disorders - Self-medication with alcohol, drugs; worsens depression',
      'Medical complications - Depression worsens outcomes in diabetes, heart disease, HIV',
      'Medication side effects - Sexual dysfunction, weight gain, discontinuation syndrome',
      'Serotonin syndrome - With drug combinations; medical emergency'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Most patients respond to treatment: 60-70% respond to first antidepressant
• Remission: 30-40% achieve remission with first treatment; higher with optimization
• Chronic course: 15-20% have chronic symptoms despite treatment
• Depression is highly treatable, but undertreated especially in Africa

EPISODE DURATION:
• Untreated: Average 6-13 months
• With treatment: Average 3 months to remission
• Continuation treatment prevents relapse

RECURRENCE:
• After first episode: 50% will have another
• After second episode: 70% will have another
• After third episode: 90% will have another
• Recurrence risk highest in first 6 months after recovery

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early treatment initiation
• Acute onset (vs gradual)
• Shorter episode duration
• Full remission achieved
• Good social support
• No comorbid conditions
• Adherence to treatment
• Psychological therapy combined with medication

Worse Outcomes:
• Delayed or no treatment
• Severe episodes
• Psychotic features
• Comorbid anxiety or substance use
• Chronic medical illness
• Childhood trauma history
• Poor social support
• Multiple recurrences
• Partial response/residual symptoms

LONG-TERM OUTCOMES:
• Full recovery with no recurrence: 20-30%
• Recurrent episodes with full inter-episode recovery: 40-50%
• Chronic course with persistent symptoms: 15-20%
• Incomplete recovery with residual symptoms: 20-30%

MORTALITY:
• Suicide: 2-6% lifetime risk in severe depression
• All-cause mortality increased (cardiovascular, etc.)
• Treatment reduces suicide risk significantly

FUNCTIONAL RECOVERY:
• Many patients achieve symptom remission but have residual functional impairment
• Full functional recovery should be treatment goal
• Psychotherapy may improve functional outcomes`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
• Public education: Awareness of depression as medical illness; reduce stigma
• Addressing social determinants: Poverty reduction, education, employment
• Early childhood interventions: Preventing abuse, neglect, trauma
• School-based programs: Building resilience, coping skills, emotional literacy
• Workplace mental health programs: Stress management, supportive environments
• Training of community health workers to identify early signs

SECONDARY PREVENTION (Early Detection):
• Screening in high-risk groups: Pregnant women, PLHIV, chronic disease, recent loss
• Integration of mental health into primary care
• Training primary care providers to recognize depression
• Use of screening tools (PHQ-2, PHQ-9) in routine care
• Prompt treatment initiation when detected

TERTIARY PREVENTION (Preventing Recurrence and Complications):
• Continuation treatment: 6-9 months minimum after remission
• Maintenance treatment: For recurrent depression
• Psychotherapy: Reduces recurrence risk, especially after medication stopped
• Self-monitoring: Recognize early warning signs
• Lifestyle factors: Regular exercise, good sleep, stress management, avoid substance use
• Support networks: Family involvement, support groups
• Regular follow-up: Even when well, periodic check-ins

PREVENTING SUICIDE:
• Universal screening for depression and suicidal ideation
• Means restriction: Safe storage of medications, firearms
• Crisis services: Hotlines, emergency mental health services
• Training of gatekeepers: Teachers, community leaders, healthcare workers
• Media guidelines: Responsible reporting of suicide
• Follow-up after attempts: Reduces repeat attempts

PUBLIC HEALTH PRIORITIES FOR AFRICA:
• Integration of mental health into primary care (WHO mhGAP)
• Task-shifting to non-specialist health workers
• Ensuring access to essential psychotropic medications
• Community mental health programs
• Anti-stigma campaigns
• Building mental health workforce capacity
• Research to understand African depression presentations and effective interventions`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS DEPRESSION?
Depression is a medical illness that affects how you feel, think, and function. It is NOT a sign of weakness, laziness, or a spiritual problem. Just like diabetes or hypertension, depression is a health condition that needs treatment. Depression affects chemicals in your brain and can happen to anyone.

SYMPTOMS OF DEPRESSION:
• Feeling sad, empty, or hopeless most of the time
• No longer enjoying things you used to enjoy
• Feeling very tired with no energy
• Trouble sleeping or sleeping too much
• Eating too much or too little
• Difficulty concentrating or making decisions
• Feeling worthless or guilty
• Aches and pains without clear cause
• Thoughts of death or suicide

THE GOOD NEWS:
Depression is treatable! With proper care:
• Most people feel better within a few weeks to months
• Treatment helps 7-8 out of 10 people
• You can return to your normal life

ABOUT YOUR MEDICINE:
• Take your medicine EVERY DAY, even when you start feeling better
• It takes 2-4 weeks to start working - do not give up too soon
• Do not stop suddenly - this can cause problems; always talk to your doctor first
• Continue for at least 6-9 months after you feel better to prevent return
• Side effects are often mild and improve with time - tell your doctor about them

COMMON SIDE EFFECTS (usually improve):
• Nausea or stomach upset (take with food)
• Headache
• Trouble sleeping or feeling sleepy (timing of dose can help)
• Changes in appetite
• Talk to your doctor if side effects bother you - adjustments can be made

HELPING YOURSELF:
• Keep taking your medicine as prescribed
• Try to stay active - even a short walk helps
• Maintain regular sleep schedule
• Eat regular, balanced meals
• Avoid alcohol - it makes depression worse
• Stay connected with family and friends
• Do activities you used to enjoy, even if you do not feel like it
• Be patient with yourself - recovery takes time

FOR FAMILY MEMBERS:
• Depression is not the persons fault - they cannot just snap out of it
• Be patient, listen without judging
• Encourage treatment and help with appointments
• Help with daily tasks if needed
• Watch for warning signs of suicide
• Take care of your own health too

WARNING SIGNS - SEEK HELP IMMEDIATELY IF:
• Talking about wanting to die or kill oneself
• Looking for ways to harm oneself (collecting pills, weapons)
• Talking about being a burden or having no reason to live
• Giving away possessions
• Saying goodbye to people
• Severe withdrawal or isolation

IF IN CRISIS:
• Do not leave the person alone
• Remove any harmful items (medications, sharp objects)
• Take them to the nearest health facility or emergency room
• Stay calm and listen

IMPORTANT TRUTHS ABOUT DEPRESSION:
✓ Depression is a real medical illness, not weakness
✓ It is not your fault
✓ Treatment works for most people
✓ You can recover and live a full life
✓ Asking for help is a sign of strength, not weakness

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MESSAGE
═══════════════════════════════════════════════════════════════════════════════

You are not alone. Millions of people have depression and recover with treatment. There is hope. Please take your medicine, attend your appointments, and reach out for help when you need it. Recovery is possible, and you deserve to feel better.`,

    references: [
      'American Psychiatric Association. Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition (DSM-5). 2013.',
      'WHO. Depression and Other Common Mental Disorders: Global Health Estimates. World Health Organization. 2017.',
      'WHO. mhGAP Intervention Guide for Mental, Neurological and Substance Use Disorders in Non-Specialized Health Settings. Version 2.0. 2016.',
      'NICE. Depression in adults: treatment and management. National Institute for Health and Care Excellence. NG222. 2022.',
      'Cipriani A, Furukawa TA, Salanti G, et al. Comparative efficacy and acceptability of 21 antidepressant drugs for the acute treatment of adults with major depressive disorder: a systematic review and network meta-analysis. Lancet. 2018;391(10128):1357-1366.',
      'Cuijpers P, Quero S, Noma H, et al. Psychotherapies for depression: a network meta-analysis covering efficacy, acceptability and long-term outcomes of all main treatment types. World Psychiatry. 2021;20(2):283-293.',
      'Patel V, Saxena S, Lund C, et al. The Lancet Commission on global mental health and sustainable development. Lancet. 2018;392(10157):1553-1598.',
      'Lund C, Brooke-Sumner C, Baingana F, et al. Social determinants of mental disorders and the Sustainable Development Goals: a systematic review of reviews. Lancet Psychiatry. 2018;5(4):357-369.',
      'Sweetland AC, Belkin GS, Verdeli H. Measuring depression and anxiety in sub-Saharan Africa. Depress Anxiety. 2014;31(3):223-232.',
      'Chibanda D, Weiss HA, Verhey R, et al. Effect of a Primary Care-Based Psychological Intervention on Symptoms of Common Mental Disorders in Zimbabwe: A Randomized Clinical Trial (Friendship Bench). JAMA. 2016;316(24):2618-2626.',
      'Seedat S, Stein DJ, Herman A, et al. Twelve-month treatment of psychiatric disorders in the South African Stress and Health Study (World Mental Health Survey Initiative). Soc Psychiatry Psychiatr Epidemiol. 2008;43(11):889-897.',
      'Gelenberg AJ, Freeman MP, Markowitz JC, et al. Practice Guideline for the Treatment of Patients with Major Depressive Disorder. 3rd ed. American Psychiatric Association. 2010.',
      'Malhi GS, Bell E, Singh AB, et al. The 2020 Royal Australian and New Zealand College of Psychiatrists clinical practice guidelines for mood disorders: Major depression summary. Bipolar Disord. 2020;22(8):788-804.',
      'Rush AJ, Trivedi MH, Wisniewski SR, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.',
      'Otte C, Gold SM, Penninx BW, et al. Major depressive disorder. Nat Rev Dis Primers. 2016;2:16065.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 78. ANXIETY DISORDERS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '78',
    name: 'Anxiety Disorders',
    slug: 'anxiety-disorders',
    specialty: 'Psychiatry',
    bodySystem: 'Neurological',
    icdCode: 'F41',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Anxiety disorders are a group of mental health conditions characterized by excessive fear, worry, and related behavioral disturbances that cause significant distress and impairment in daily functioning. They are the most common mental health disorders worldwide, affecting approximately 301 million people globally. Unlike normal anxiety (which is adaptive and time-limited), anxiety disorders involve disproportionate, persistent, and often irrational fear or worry that interferes with work, relationships, and quality of life.

The main types of anxiety disorders include Generalized Anxiety Disorder (GAD), Panic Disorder, Social Anxiety Disorder (Social Phobia), Specific Phobias, and Agoraphobia. While each has distinct features, they share common elements of excessive anxiety and avoidance behaviors. Separation Anxiety Disorder and Selective Mutism are also classified as anxiety disorders but are more common in children.

The pathophysiology involves dysregulation of the fear circuitry, including the amygdala, prefrontal cortex, and hippocampus. Neurotransmitter imbalances (serotonin, GABA, norepinephrine), genetic vulnerability, and learned behaviors all contribute. The HPA axis may be hyperactive, and there is often autonomic nervous system hyperarousal.

In Africa, anxiety disorders are common but significantly underrecognized and undertreated. Prevalence estimates range from 5-15% depending on the population, with higher rates among women, people living with HIV, refugees, and those in conflict-affected areas. Anxiety often presents with somatic symptoms (palpitations, headaches, dizziness, GI complaints) rather than psychological complaints, leading to multiple medical consultations and delayed diagnosis. The treatment gap is substantial, with limited access to mental health services and medications.

Treatment is effective for most anxiety disorders. First-line options include SSRIs and psychological therapies (particularly CBT). Benzodiazepines should be used sparingly and only short-term due to dependence risk. Many patients can be effectively managed in primary care settings.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - SSRIs are FIRST-LINE for anxiety disorders: Fluoxetine, sertraline, escitalopram; same medications as for depression; start low, go slow

🔴 CRITICAL POINT 2 - AVOID long-term benzodiazepines: High dependence risk; use only for short-term crisis management (2-4 weeks maximum)

🔴 CRITICAL POINT 3 - CBT is highly effective for all anxiety disorders: Exposure therapy is key component; can be delivered by trained non-specialists

🔴 CRITICAL POINT 4 - Rule out medical causes: Hyperthyroidism, cardiac arrhythmias, pheochromocytoma, substance use can all cause anxiety symptoms

🟠 CRITICAL POINT 5 - Anxiety often presents with SOMATIC symptoms in Africa: Palpitations, chest pain, dizziness, headache, GI symptoms may be chief complaint

🟠 CRITICAL POINT 6 - High comorbidity with depression: Screen for both; 50-60% overlap; may need to treat both conditions

🟠 CRITICAL POINT 7 - SSRIs may initially WORSEN anxiety: Start at low dose, warn patient; improvement takes 2-4 weeks

🟡 CRITICAL POINT 8 - Panic attacks are not dangerous: Educate patients that panic cannot cause heart attack, stroke, or death; reduces fear of attacks

🟡 CRITICAL POINT 9 - Avoidance maintains anxiety: Gradual exposure to feared situations is key to recovery; avoidance worsens condition`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common mental disorders worldwide: 301 million affected (4% of population)
• Lifetime prevalence: 15-30% (varies by disorder and population)
• Women affected 2x more than men across all anxiety disorders
• Onset typically in childhood/adolescence (median age 11 years) but can occur at any age
• Chronic course if untreated; waxing and waning symptoms
• Significant economic burden: Healthcare costs, lost productivity

PREVALENCE BY DISORDER:
• Specific Phobias: 7-9% lifetime
• Social Anxiety Disorder: 5-12% lifetime
• Generalized Anxiety Disorder: 3-6% lifetime
• Panic Disorder: 2-5% lifetime
• Agoraphobia: 1-2% lifetime

AFRICAN EPIDEMIOLOGY:
• Prevalence estimates: 5-15% (likely underestimated due to underrecognition)
• Treatment gap: 75-90% receive no treatment
• Limited mental health workforce (<0.5 psychiatrists per 100,000)
• Higher rates in: Women, PLHIV, refugees/IDPs, conflict-affected populations, urban poor
• Often presents with somatic complaints: Palpitations, headaches, dizziness, GI symptoms
• Frequent medical consultations before psychiatric diagnosis
• Stigma leads to delayed help-seeking
• Traditional/spiritual explanations common

RISK FACTORS:
• Female sex (2:1 ratio)
• Family history of anxiety disorders
• Childhood adversity (abuse, neglect, parental loss)
• Temperament (behavioral inhibition, neuroticism)
• Stressful life events
• Chronic medical illness
• Substance use (caffeine, alcohol, cannabis)
• Trauma exposure
• Low socioeconomic status

COMORBIDITY:
• Depression: 50-60% comorbidity (most common)
• Other anxiety disorders: High overlap
• Substance use disorders: 20-30%
• Medical conditions: Cardiovascular disease, respiratory disease, chronic pain
• Suicide risk: Elevated, especially with comorbid depression`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

GENERALIZED ANXIETY DISORDER (GAD):
Core features:
• Excessive, uncontrollable worry about multiple areas (work, health, family, finances)
• Occurring more days than not for at least 6 months
• Difficulty controlling the worry

Associated symptoms (3+ required):
• Restlessness or feeling on edge
• Easy fatigability
• Difficulty concentrating, mind going blank
• Irritability
• Muscle tension
• Sleep disturbance (difficulty falling/staying asleep, restless sleep)

PANIC DISORDER:
Recurrent, unexpected panic attacks with:
• Palpitations, pounding heart
• Sweating
• Trembling or shaking
• Shortness of breath, feeling of choking
• Chest pain or discomfort
• Nausea or abdominal distress
• Dizziness, lightheadedness, faintness
• Chills or hot flushes
• Paresthesias (numbness, tingling)
• Derealization or depersonalization
• Fear of losing control or going crazy
• Fear of dying

Plus at least 1 month of:
• Persistent concern about additional attacks
• Worry about implications (heart attack, losing control)
• Significant behavioral change (avoidance)

SOCIAL ANXIETY DISORDER (Social Phobia):
• Marked fear of social situations where exposed to scrutiny
• Fear of acting in way that will be negatively evaluated
• Social situations almost always provoke fear
• Avoidance or endured with intense anxiety
• Fear is out of proportion to actual threat
• Duration at least 6 months

Common feared situations:
• Public speaking
• Meeting new people
• Eating/drinking in public
• Being observed while working
• Initiating conversations
• Asserting oneself

SPECIFIC PHOBIAS:
• Marked fear about specific object or situation
• Phobic stimulus almost always provokes immediate fear
• Actively avoided or endured with intense anxiety
• Fear is out of proportion
• Duration at least 6 months

Common types:
• Animal (spiders, snakes, dogs)
• Natural environment (heights, storms, water)
• Blood-injection-injury
• Situational (flying, elevators, enclosed spaces)

AGORAPHOBIA:
Marked fear about 2+ of:
• Public transportation
• Open spaces
• Enclosed spaces
• Standing in line or being in a crowd
• Being outside of home alone

Fear due to thought that escape might be difficult or help unavailable if panic-like symptoms occur

PHYSICAL SYMPTOMS OF ANXIETY (Autonomic Arousal):
• Cardiovascular: Palpitations, tachycardia, chest tightness
• Respiratory: Shortness of breath, hyperventilation
• GI: Nausea, diarrhea, abdominal pain, dry mouth
• Neurological: Dizziness, lightheadedness, tremor, paresthesias
• Muscular: Tension, aches, restlessness
• Other: Sweating, urinary frequency, fatigue

SOMATIC PRESENTATIONS (Common in Africa):
Patients may present with:
• Headache (tension-type)
• Chest pain and palpitations
• Dizziness
• Fatigue
• Abdominal complaints
• Multiple unexplained physical symptoms
Often seek repeated medical consultations before anxiety recognized`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: SCREENING
• Use validated screening tools: GAD-7, PHQ-4
• GAD-7: 7 questions; score ≥10 indicates moderate-severe anxiety
• PHQ-4: 4 questions screening both anxiety and depression

STEP 2: CLINICAL INTERVIEW
• Characterize anxiety symptoms
• Determine type of anxiety disorder
• Assess duration and functional impairment
• Screen for comorbidities (depression, substance use)

STEP 3: RULE OUT MEDICAL CAUSES
• Thyroid disease (hyperthyroidism)
• Cardiac conditions (arrhythmias, MVP)
• Respiratory conditions (asthma, COPD)
• Substance use/withdrawal
• Medications causing anxiety

STEP 4: ASSESS SEVERITY AND SAFETY
• Functional impairment
• Suicidal ideation (anxiety + depression increases risk)
• Severity guides treatment intensity

DIAGNOSTIC CRITERIA SUMMARIES:

GENERALIZED ANXIETY DISORDER (GAD) - DSM-5:
A. Excessive worry about multiple events/activities, more days than not, for ≥6 months
B. Difficulty controlling worry
C. 3+ of: Restlessness, fatigue, concentration difficulty, irritability, muscle tension, sleep disturbance
D. Significant distress or impairment
E. Not due to substances or medical condition
F. Not better explained by another mental disorder

PANIC DISORDER:
A. Recurrent unexpected panic attacks (abrupt surge of fear with 4+ physical/cognitive symptoms, peaking within minutes)
B. ≥1 attack followed by ≥1 month of:
   - Persistent concern about additional attacks, OR
   - Significant maladaptive change in behavior
C. Not due to substances or medical condition
D. Not better explained by another mental disorder

SOCIAL ANXIETY DISORDER:
A. Marked fear of social situations with possible scrutiny
B. Fear of negative evaluation
C. Social situations almost always provoke fear
D. Avoided or endured with intense anxiety
E. Out of proportion to actual threat
F. ≥6 months duration
G. Significant distress or impairment

GAD-7 SCORING:
• 0-4: Minimal anxiety
• 5-9: Mild anxiety
• 10-14: Moderate anxiety
• 15-21: Severe anxiety

INVESTIGATIONS TO EXCLUDE MEDICAL CAUSES:

ESSENTIAL:
• Thyroid function (TSH) - hyperthyroidism mimics anxiety
• Complete blood count - anemia causes fatigue and palpitations
• Blood glucose - hypoglycemia can cause anxiety symptoms
• ECG - arrhythmias, particularly if palpitations prominent

CONSIDER BASED ON PRESENTATION:
• Cardiac workup if chest pain, palpitations (echo, Holter)
• Pulmonary function if respiratory symptoms
• Urine drug screen if substance use suspected
• Caffeine intake assessment
• Medication review (stimulants, steroids, bronchodilators, thyroid hormones)
• 24-hour urinary catecholamines if pheochromocytoma suspected (rare)`,

    differentialDiagnosis: [
      'Hyperthyroidism - Weight loss, heat intolerance, tremor, tachycardia; check TSH',
      'Cardiac arrhythmias - Palpitations, ECG abnormalities; Holter monitoring',
      'Pheochromocytoma - Episodic hypertension, headache, sweating; urinary catecholamines',
      'Hypoglycemia - Sweating, tremor, confusion; resolves with glucose',
      'Substance intoxication/withdrawal - Caffeine, stimulants, alcohol, benzodiazepine withdrawal',
      'Medication-induced - Bronchodilators, steroids, thyroid hormones, decongestants',
      'Depression - May have anxious features; screen for core depressive symptoms',
      'PTSD - Trauma history, re-experiencing, avoidance, hyperarousal',
      'OCD - Obsessions (intrusive thoughts) and compulsions (repetitive behaviors)',
      'Bipolar disorder - Anxiety during mood episodes; assess for mania history',
      'Psychotic disorders - Anxiety may be prominent; assess for delusions/hallucinations',
      'COPD/Asthma - Respiratory symptoms; pulmonary function testing',
      'Vestibular disorders - Dizziness, vertigo; ENT evaluation',
      'Anemia - Fatigue, palpitations; check hemoglobin',
      'Chronic pain syndromes - Pain with anxiety component',
      'Adjustment disorder with anxiety - Identifiable stressor, time-limited'
    ],

    investigations: [
      {
        name: 'GAD-7 (Generalized Anxiety Disorder 7-item)',
        indication: '🔴 ESSENTIAL - Validated screening and severity tool for anxiety; score ≥10 indicates clinically significant anxiety',
        availability: 'BASIC'
      },
      {
        name: 'PHQ-9 (Patient Health Questionnaire-9)',
        indication: '🔴 ESSENTIAL - Screen for comorbid depression; very common (50-60% comorbidity)',
        availability: 'BASIC'
      },
      {
        name: 'Detailed Psychiatric History and Mental State Exam',
        indication: '🔴 ESSENTIAL - Clinical interview to characterize anxiety, determine type, assess severity',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Function Tests (TSH)',
        indication: '🔴 ESSENTIAL - Hyperthyroidism mimics anxiety; check in all new presentations',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Exclude anemia (causes fatigue, palpitations)',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose',
        indication: '🔴 ESSENTIAL - Hypoglycemia causes anxiety-like symptoms',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: '🔴 ESSENTIAL - Exclude cardiac arrhythmias, especially if palpitations or chest pain prominent',
        availability: 'BASIC'
      },
      {
        name: 'Urea and Electrolytes',
        indication: '🟠 IMPORTANT - Baseline before medications; electrolyte imbalances can cause symptoms',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT - Baseline; hepatic dysfunction affects medication metabolism',
        availability: 'BASIC'
      },
      {
        name: 'Urine Drug Screen',
        indication: '🟡 IF INDICATED - Stimulants, cannabis can cause anxiety; alcohol/benzodiazepine withdrawal',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: '🟡 IF INDICATED - Persistent palpitations, murmur, or structural heart disease suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Holter Monitor',
        indication: '🟡 IF INDICATED - Capture intermittent arrhythmias if palpitations prominent',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Pulmonary Function Tests',
        indication: '🟡 IF INDICATED - If respiratory symptoms prominent; exclude asthma, COPD',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ VALIDATE: Acknowledge symptoms are real and distressing; anxiety is treatable',
        '⚡ EXCLUDE MEDICAL CAUSES: Check TSH, CBC, glucose, ECG if not recently done',
        '⚡ SCREEN FOR DEPRESSION: Use PHQ-9; high comorbidity affects treatment',
        '⚡ ASSESS SEVERITY: Use GAD-7; mild vs moderate-severe guides treatment',
        '⚡ SAFETY ASSESSMENT: Suicidal ideation risk increases with comorbid depression',
        '⚡ PSYCHOEDUCATION: Explain nature of anxiety, fight-or-flight response, treatment options',
        '⚡ BREATHING TECHNIQUES: Teach slow, diaphragmatic breathing for immediate symptom relief',
        '⚡ TREATMENT SELECTION: Mild - psychotherapy/self-help; Moderate-severe - medication plus therapy',
        '⚡ START SSRI: If medication indicated, start low (e.g., sertraline 25mg, fluoxetine 10mg)',
        '⚡ FOLLOW-UP: Schedule review in 1-2 weeks to assess tolerance and early response'
      ],
      
      pharmacological: [
        {
          drug: 'Sertraline',
          indication: '🔴 FIRST-LINE SSRI - Effective for all anxiety disorders; fewer drug interactions; well-tolerated',
          dose: 'Adults: Start 25mg PO once daily for 1 week, then increase to 50mg daily. May increase by 50mg every 4 weeks. Target: 50-200mg/day',
          route: 'PO',
          duration: 'Long-term: Continue 6-12 months after remission; longer if recurrent',
          contraindications: ['MAOIs (within 14 days)', 'Pimozide'],
          sideEffects: ['Nausea (common early)', 'Diarrhea', 'Insomnia', 'Sexual dysfunction', 'Initial anxiety increase', 'Headache'],
          alternatives: ['Fluoxetine', 'Escitalopram', 'Paroxetine']
        },
        {
          drug: 'Fluoxetine',
          indication: '🔴 FIRST-LINE SSRI - WHO Essential Medicine; effective, affordable; long half-life (aids adherence)',
          dose: 'Adults: Start 10mg PO once daily for anxiety (lower than depression due to initial activation). Increase to 20mg after 1-2 weeks. Target: 20-60mg/day',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['MAOIs', 'Pimozide', 'Thioridazine'],
          sideEffects: ['Nausea', 'Headache', 'Insomnia', 'Anxiety/activation (early)', 'Sexual dysfunction', 'Weight changes'],
          alternatives: ['Sertraline', 'Escitalopram']
        },
        {
          drug: 'Escitalopram',
          indication: '🔴 FIRST-LINE SSRI - Most selective SSRI; fewer drug interactions; good tolerability',
          dose: 'Adults: Start 5mg PO once daily for 1 week, then 10mg daily. May increase to 20mg after 4+ weeks. Max: 20mg/day',
          route: 'PO',
          duration: 'Long-term as above',
          contraindications: ['MAOIs', 'QT prolongation', 'Concomitant QT-prolonging drugs'],
          sideEffects: ['Nausea', 'Insomnia', 'Sexual dysfunction', 'QT prolongation (dose-dependent)'],
          alternatives: ['Sertraline', 'Fluoxetine']
        },
        {
          drug: 'Paroxetine',
          indication: '🟠 ALTERNATIVE SSRI - Effective for all anxiety disorders; more sedating; significant discontinuation syndrome',
          dose: 'Adults: Start 10mg PO once daily in morning. Increase by 10mg weekly. Target: 20-50mg/day for anxiety disorders',
          route: 'PO',
          duration: 'Long-term; taper VERY slowly when discontinuing',
          contraindications: ['MAOIs', 'Pimozide', 'Thioridazine', 'Pregnancy (avoid if possible)'],
          sideEffects: ['Sedation', 'Weight gain', 'Sexual dysfunction', 'Discontinuation syndrome (severe)', 'Anticholinergic effects'],
          alternatives: ['Sertraline', 'Escitalopram']
        },
        {
          drug: 'Venlafaxine XR',
          indication: '🟠 SECOND-LINE (SNRI) - When SSRIs fail; effective for GAD and social anxiety; monitor BP',
          dose: 'Adults: Start 37.5-75mg PO once daily. Increase by 75mg every 4+ days. Target: 150-225mg/day. Max: 225mg/day for anxiety',
          route: 'PO',
          duration: 'Long-term; taper slowly (discontinuation syndrome)',
          contraindications: ['MAOIs', 'Uncontrolled hypertension'],
          sideEffects: ['Nausea', 'Headache', 'Insomnia', 'Hypertension (dose-dependent)', 'Sexual dysfunction', 'Severe discontinuation syndrome'],
          alternatives: ['Duloxetine', 'Mirtazapine']
        },
        {
          drug: 'Pregabalin',
          indication: '🟠 ALTERNATIVE for GAD - Rapid onset; useful when SSRI not tolerated; controlled substance in some jurisdictions',
          dose: 'Adults: Start 75mg PO twice daily OR 50mg three times daily. Increase after 1 week. Target: 150-600mg/day in 2-3 divided doses',
          route: 'PO',
          duration: 'Long-term if effective',
          contraindications: ['Hypersensitivity', 'Caution in renal impairment (dose adjust)'],
          sideEffects: ['Dizziness', 'Somnolence', 'Weight gain', 'Peripheral edema', 'Blurred vision', 'Dependence potential'],
          alternatives: ['Gabapentin (less evidence)', 'Buspirone']
        },
        {
          drug: 'Buspirone',
          indication: '🟠 ALTERNATIVE for GAD - Non-sedating, no dependence; takes 2-4 weeks for effect; less effective than SSRIs',
          dose: 'Adults: Start 5mg PO three times daily. Increase by 5mg every 2-3 days. Target: 15-30mg/day in divided doses. Max: 60mg/day',
          route: 'PO',
          duration: 'Long-term if effective',
          contraindications: ['MAOIs', 'Severe hepatic/renal impairment'],
          sideEffects: ['Dizziness', 'Nausea', 'Headache', 'Nervousness', 'Generally well tolerated'],
          alternatives: ['SSRIs (more effective)', 'Pregabalin']
        },
        {
          drug: 'Propranolol',
          indication: '🟠 ADJUNCT for PERFORMANCE ANXIETY - Beta-blocker; blocks physical symptoms; use PRN before specific situations',
          dose: 'Adults: 10-40mg PO 30-60 minutes before anxiety-provoking situation. May repeat if needed. Not for regular use',
          route: 'PO',
          duration: 'PRN for specific situations (public speaking, performances)',
          contraindications: ['Asthma', 'Heart block', 'Severe bradycardia', 'Hypotension', 'Decompensated heart failure'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue', 'Cold extremities', 'Bronchospasm'],
          alternatives: ['Atenolol']
        },
        {
          drug: 'Diazepam',
          indication: '🟡 SHORT-TERM ONLY - For acute/severe anxiety or while waiting for SSRI to work; MAX 2-4 weeks',
          dose: 'Adults: 2-5mg PO two to three times daily. Use lowest effective dose. Maximum duration: 2-4 weeks',
          route: 'PO',
          duration: 'SHORT-TERM ONLY (2-4 weeks maximum); avoid long-term use',
          contraindications: ['Respiratory depression', 'Sleep apnea', 'Severe hepatic impairment', 'Myasthenia gravis'],
          sideEffects: ['Sedation', 'Cognitive impairment', 'DEPENDENCE (develops within weeks)', 'Withdrawal seizures', 'Falls (elderly)'],
          alternatives: ['Lorazepam (shorter-acting)', 'Clonazepam (longer-acting)']
        },
        {
          drug: 'Lorazepam',
          indication: '🟡 SHORT-TERM/ACUTE - For severe acute anxiety, panic attacks; shorter-acting; IM/IV available',
          dose: 'Adults: 0.5-1mg PO two to three times daily PRN. For acute panic: 1-2mg PO/SL. Max: 4mg/day. SHORT-TERM ONLY',
          route: 'PO/SL/IM/IV',
          duration: 'SHORT-TERM ONLY; avoid long-term',
          contraindications: ['Same as diazepam'],
          sideEffects: ['Same as diazepam; high dependence potential'],
          alternatives: ['Diazepam', 'Clonazepam']
        },
        {
          drug: 'Hydroxyzine',
          indication: '🟡 ALTERNATIVE - Antihistamine with anxiolytic properties; no dependence; useful if benzodiazepines to be avoided',
          dose: 'Adults: 25-50mg PO three to four times daily PRN. Max: 100mg per dose, 400mg/day',
          route: 'PO',
          duration: 'Short or medium-term; less effective than other options',
          contraindications: ['QT prolongation', 'Concurrent QT-prolonging drugs', 'Pregnancy (first trimester)'],
          sideEffects: ['Sedation', 'Dry mouth', 'Dizziness', 'QT prolongation'],
          alternatives: ['Buspirone', 'Low-dose TCA']
        },
        {
          drug: 'Amitriptyline (Low Dose)',
          indication: '🟡 ALTERNATIVE - TCA at low doses; useful if insomnia prominent; very affordable',
          dose: 'Adults: Start 10-25mg PO at bedtime. May increase gradually. Target for anxiety: 25-75mg at bedtime',
          route: 'PO',
          duration: 'Long-term if effective',
          contraindications: ['Cardiac conduction abnormalities', 'Recent MI', 'Glaucoma', 'Urinary retention'],
          sideEffects: ['Sedation', 'Dry mouth', 'Constipation', 'Weight gain', 'Orthostatic hypotension', 'Dangerous in overdose'],
          alternatives: ['Mirtazapine (if insomnia)', 'SSRIs preferred']
        }
      ],
      
      nonPharmacological: [
        'COGNITIVE BEHAVIORAL THERAPY (CBT): First-line psychological treatment; addresses cognitive distortions and avoidance; highly effective',
        'EXPOSURE THERAPY: Gradual, systematic exposure to feared situations; essential for phobias, panic, social anxiety',
        'RELAXATION TECHNIQUES: Progressive muscle relaxation, deep breathing exercises; reduce physical symptoms',
        'MINDFULNESS-BASED INTERVENTIONS: Mindfulness meditation; helps with worry and rumination in GAD',
        'PSYCHOEDUCATION: Understanding anxiety (fight-or-flight), recognizing triggers, breaking avoidance cycle',
        'GRADED EXPOSURE HIERARCHY: List feared situations from least to most anxiety-provoking; tackle systematically',
        'BEHAVIORAL EXPERIMENTS: Test anxious predictions against reality',
        'LIFESTYLE MODIFICATIONS: Regular exercise (30 min, 5x/week); limit caffeine and alcohol; good sleep hygiene',
        'STRESS MANAGEMENT: Time management, problem-solving, assertiveness training',
        'SUPPORT GROUPS: Peer support; reduces isolation; shared coping strategies',
        'SELF-HELP RESOURCES: Books, apps, online programs based on CBT principles',
        'BREATHING RETRAINING: For panic and hyperventilation; slow, diaphragmatic breathing'
      ],
      
      surgical: [
        'No surgical treatments for anxiety disorders',
        'Deep brain stimulation and other neurosurgical approaches are experimental for severe refractory cases'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Severe anxiety with suicidal ideation - crisis intervention; consider inpatient care',
        '🔴 IMMEDIATE: Psychotic symptoms in context of anxiety - unusual; needs specialist evaluation',
        '🔴 IMMEDIATE: Severe panic disorder with inability to function - may need intensive treatment',
        '🟠 URGENT: Suspected bipolar disorder - anxiety during mood episodes; needs mood stabilizer',
        '🟠 URGENT: Treatment-resistant anxiety (failed 2+ adequate SSRI trials) - specialist review',
        '🟠 URGENT: Severe OCD features - may need specialist CBT (ERP) and higher SSRI doses',
        '🟠 URGENT: Comorbid severe substance use disorder - integrated treatment needed',
        '🟡 ROUTINE: Request for psychological therapy - CBT referral if available',
        '🟡 ROUTINE: Diagnostic uncertainty - differentiate from other conditions',
        '🟡 ROUTINE: Benzodiazepine dependence - specialist support for tapering',
        '🟡 ROUTINE: Complex PTSD or trauma history - may need trauma-focused therapy'
      ],
      
      monitoring: [
        'GAD-7 SCORE: At baseline and every visit; track improvement; score <5 indicates remission',
        'PHQ-9: Monitor for comorbid depression; may emerge during treatment',
        'SIDE EFFECTS: Especially first 2 weeks on SSRI (nausea, activation, sleep changes)',
        'SUICIDE RISK: Assess at each visit, especially if depression present',
        'RESPONSE TIMELINE: Some improvement expected by 4 weeks; full effect 8-12 weeks',
        'FUNCTIONAL IMPROVEMENT: Work, social activities, relationships; aim for full recovery',
        'AVOIDANCE BEHAVIORS: Are they reducing? Essential for recovery',
        'BLOOD PRESSURE: If on venlafaxine (SNRIs can increase BP)',
        'BENZODIAZEPINE USE: If prescribed, monitor duration strictly; plan taper from start',
        'CAFFEINE AND ALCOHOL: Both worsen anxiety; monitor and advise reduction'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL PHASE (First 4 weeks):
• Follow-up at 1-2 weeks after starting SSRI
• Assess: Side effects (especially initial worsening), adherence, early response
• Reassure: SSRIs may initially increase anxiety; this improves
• Adjust: Manage side effects; maintain dose if tolerable

ACUTE TREATMENT (Weeks 4-12):
• Follow-up every 2-4 weeks
• Assess response using GAD-7
• If partial response at 6 weeks: Increase to maximum tolerated dose
• If no response at 8 weeks: Consider switch or augmentation
• Encourage gradual exposure to avoided situations

CONTINUATION (6-12 months after remission):
• Maintain dose that achieved remission
• Follow-up every 1-3 months
• Goal: Prevent relapse
• Continue psychological strategies learned

MAINTENANCE (Long-term if needed):
Consider indefinite treatment if:
• Multiple relapses
• Severe episodes
• Residual symptoms
• Patient preference after informed discussion
• Follow-up every 3-6 months when stable

DISCONTINUING MEDICATION:
• Only after sustained remission (6-12 months)
• Taper SLOWLY over 2-4 months (longer for paroxetine, venlafaxine)
• Monitor for discontinuation symptoms (dizziness, flu-like, rebound anxiety)
• Monitor for recurrence; have plan for rapid re-start if needed
• Continue psychological strategies to prevent relapse

IF TREATMENT FAILS:

Step 1 - Reassess:
• Correct diagnosis? (Medical cause? Different anxiety disorder? Depression?)
• Adequate dose and duration? (8-12 weeks at therapeutic dose)
• Adherence?
• Ongoing stressors or substance use?
• Avoidance behaviors continuing?

Step 2 - Optimize:
• Increase to maximum tolerated dose
• Add psychological therapy if not already included
• Address avoidance with exposure

Step 3 - Switch or Augment:
• Switch to different SSRI or to SNRI
• Augment with: Buspirone, pregabalin, or low-dose antipsychotic (specialist)

Step 4 - Refer:
• Treatment-resistant cases
• Complex comorbidities
• Consider intensive therapy programs

DISORDER-SPECIFIC NOTES:

Panic Disorder:
• SSRIs highly effective; full dose often needed
• Educate: Panic attacks are not dangerous; cannot cause death
• Interoceptive exposure: Practice inducing and tolerating panic sensations
• Avoid benzodiazepine PRN use (reinforces safety-seeking)

Social Anxiety Disorder:
• SSRIs first-line; may need higher doses
• CBT with exposure to social situations essential
• Propranolol useful for performance-only anxiety
• Long-term treatment often needed (high relapse rate)

Specific Phobias:
• Exposure therapy is treatment of choice (not medication)
• Systematic desensitization with hierarchy of feared stimuli
• Single-session exposure can be effective for some phobias
• Medications play minimal role

GAD:
• SSRIs/SNRIs first-line; pregabalin alternative
• CBT addresses worry, intolerance of uncertainty
• Often chronic; may need long-term treatment
• Address perfectionism and need for control`
    },

    redFlags: [
      '🔴 CRITICAL: Suicidal ideation - anxiety plus depression increases risk; urgent safety assessment',
      '🔴 CRITICAL: Severe panic with chest pain - rule out cardiac cause; may need ER evaluation',
      '🔴 CRITICAL: Psychotic symptoms - unusual in pure anxiety; consider other diagnoses',
      '🔴 CRITICAL: Inability to function (cannot work, care for self, leave home) - severe case needing intensive intervention',
      '🟠 URGENT: Suspected hyperthyroidism (weight loss, tremor, tachycardia, heat intolerance) - check TFTs',
      '🟠 URGENT: Cardiac symptoms not improving with anxiety treatment - further cardiac workup',
      '🟠 URGENT: Benzodiazepine dependence developing - refer for specialist tapering support',
      '🟠 URGENT: Worsening depression during anxiety treatment - adjust management',
      '🟠 URGENT: Manic/hypomanic symptoms - stop antidepressant; consider bipolar disorder',
      '🟡 CAUTION: Initial SSRI activation (worse anxiety, agitation, insomnia) - usually transient; reassure and manage',
      '🟡 CAUTION: No improvement after 8+ weeks adequate treatment - reassess and consider change',
      '🟡 CAUTION: High caffeine intake - significant contributor; advise reduction',
      '🟡 CAUTION: Alcohol use to self-medicate - worsens anxiety; address substance use'
    ],

    complications: [
      'Depression - Very common comorbidity (50-60%); may develop during course',
      'Substance use disorders - Self-medication with alcohol, benzodiazepines, cannabis',
      'Benzodiazepine dependence - If prescribed long-term; difficult to discontinue',
      'Functional impairment - Inability to work, social isolation, relationship breakdown',
      'Avoidance behaviors - Reinforce anxiety; lead to restricted life',
      'Agoraphobia - May develop secondary to panic disorder',
      'Suicide - Risk elevated, especially with comorbid depression',
      'Physical health effects - Cardiovascular disease association; muscle tension, GI problems',
      'Economic impact - Lost productivity, frequent medical consultations',
      'Chronic course - Without treatment, anxiety disorders tend to persist',
      'Treatment resistance - 20-30% do not respond adequately to first-line treatments',
      'SSRI discontinuation syndrome - If medication stopped abruptly'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTCOMES:
• Anxiety disorders are HIGHLY TREATABLE
• 50-60% respond well to first-line treatment (SSRI + CBT)
• With adequate treatment, many achieve remission
• Without treatment, tend to be chronic with waxing/waning course

TREATMENT RESPONSE:
• SSRIs: 50-70% response rate
• CBT: 50-80% response rate
• Combined treatment: May be more effective than either alone
• Response typically seen within 4-8 weeks
• Full benefit may take 12+ weeks

DISORDER-SPECIFIC OUTCOMES:

Panic Disorder:
• Good response to treatment (70-90% improve)
• High relapse rate if medication stopped (30-50%)
• CBT reduces relapse risk
• Some develop agoraphobia

Social Anxiety Disorder:
• Responds well to treatment
• Often chronic; high relapse after stopping medication
• Early onset may predict poorer outcome
• CBT provides more durable response

GAD:
• Chronic, waxing and waning course
• 50% maintain improvement long-term
• Often requires long-term treatment
• Comorbidities common

Specific Phobias:
• Excellent response to exposure therapy
• Often cured with systematic desensitization
• Rapid response possible (even single session)

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early treatment
• Less severe symptoms at baseline
• No comorbid depression
• No substance use
• Good social support
• Treatment adherence
• Completion of CBT/exposure
• Confronting (not avoiding) fears

Worse Outcomes:
• Delayed treatment
• Severe symptoms
• Comorbid depression or substance use
• Early onset
• Poor social support
• Personality disorders
• Continued avoidance behaviors
• Multiple anxiety disorders

LONG-TERM OUTLOOK:
• With treatment, most people improve significantly
• Some need long-term medication
• CBT skills provide lasting benefit
• Lifestyle changes help maintain gains
• Recurrence possible but manageable with early intervention`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
• Building resilience in children: Coping skills, emotional regulation
• Reducing childhood adversity: Preventing abuse, neglect
• School-based anxiety prevention programs
• Parenting programs: Reducing overprotection, modeling coping
• Reducing excessive caffeine consumption
• Promoting healthy lifestyle: Exercise, sleep, stress management

SECONDARY PREVENTION (Early Intervention):
• Screening in primary care: GAD-7, PHQ-4
• Training primary care providers to recognize anxiety
• Early treatment of subclinical anxiety
• Addressing anxiety in medical settings (e.g., post-MI, cancer)
• Brief interventions for at-risk individuals

TERTIARY PREVENTION (Preventing Complications/Relapse):
• Continuation treatment after remission (6-12 months)
• CBT to provide lasting skills
• Relapse prevention planning
• Avoid benzodiazepine dependence (short-term use only)
• Monitor for comorbid depression
• Address substance use
• Regular follow-up

PREVENTING AVOIDANCE:
• Educate that avoidance worsens anxiety
• Encourage gradual exposure to feared situations
• Behavioral experiments to test fears
• Build tolerance for uncertainty

LIFESTYLE FACTORS:
• Regular physical exercise (30 min, 5x/week)
• Limit caffeine (coffee, tea, energy drinks)
• Limit alcohol
• Good sleep hygiene
• Stress management techniques
• Social connection

PUBLIC HEALTH PRIORITIES FOR AFRICA:
• Integration of mental health into primary care
• Training non-specialist health workers (mhGAP)
• Ensuring access to SSRIs (essential medicines)
• Reducing stigma around mental health
• Community-based mental health programs
• Research on anxiety presentations in African populations`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS ANXIETY?
Anxiety is your body's natural alarm system. It is the fight-or-flight response that helps you deal with danger. However, when this alarm system becomes too sensitive, it can cause problems. Anxiety disorders happen when you feel anxious too often, too strongly, or in situations that are not actually dangerous. This is a medical condition that can be treated.

TYPES OF ANXIETY:
• Generalized Anxiety: Constant worry about many things (health, work, family, money)
• Panic Attacks: Sudden episodes of intense fear with physical symptoms (racing heart, sweating, difficulty breathing)
• Social Anxiety: Extreme fear of social situations and being judged by others
• Phobias: Strong fear of specific things (heights, animals, flying)

WHAT HAPPENS IN YOUR BODY:
When you feel anxious, your body releases stress hormones that cause:
• Fast heartbeat and palpitations
• Sweating
• Trembling or shaking
• Shortness of breath
• Dizziness
• Nausea or stomach upset
• Muscle tension
• Feeling like something terrible will happen

IMPORTANT: These physical symptoms are uncomfortable but NOT dangerous. A panic attack cannot cause a heart attack, stroke, or death. Understanding this helps reduce the fear of anxiety itself.

THE GOOD NEWS:
• Anxiety disorders are very treatable
• 6-7 out of 10 people improve significantly with treatment
• You can learn to manage anxiety and live a normal life

TREATMENT OPTIONS:
1. Talking Therapy (CBT): Learning to change unhelpful thoughts and gradually face fears
2. Medication: SSRIs (like fluoxetine or sertraline) help rebalance brain chemicals
3. Self-help: Relaxation techniques, exercise, lifestyle changes

ABOUT YOUR MEDICINE (if prescribed SSRI):
• Start with a low dose - may be increased gradually
• Takes 2-4 weeks to start working - be patient
• May feel slightly worse before feeling better - this is temporary
• Take every day, even when feeling better
• Do not stop suddenly - always talk to your doctor first
• Side effects (nausea, headache) usually improve with time

THE IMPORTANCE OF FACING FEARS:
• Avoiding things that make you anxious feels better in the short term
• BUT avoidance makes anxiety WORSE in the long term
• Gradual exposure to feared situations is KEY to recovery
• Each time you face a fear, it becomes less scary

SELF-HELP STRATEGIES:
• Slow Breathing: Breathe in for 4 counts, out for 6 counts; slows the anxiety response
• Challenge Anxious Thoughts: Ask yourself - Is this thought realistic? What is the evidence?
• Regular Exercise: 30 minutes, 5 times a week reduces anxiety significantly
• Limit Caffeine: Coffee, tea, energy drinks, cola can worsen anxiety
• Limit Alcohol: May feel calming but makes anxiety worse overall
• Good Sleep: Regular sleep schedule; avoid screens before bed
• Stay Connected: Talk to family and friends; do not isolate

WHAT TO DO DURING A PANIC ATTACK:
1. Remember: This is a panic attack. It is not dangerous. It will pass.
2. Stay where you are if safe to do so (leaving reinforces fear)
3. Slow your breathing: In for 4, out for 6
4. Focus on the present: Notice 5 things you can see, 4 you can touch, 3 you can hear
5. Ride it out: Panic peaks in about 10 minutes then subsides
6. Afterward: Carry on with your day; do not let panic control you

FOR FAMILY MEMBERS:
• Anxiety is not weakness or something the person can just stop
• Be patient and supportive
• Encourage treatment
• Gently encourage facing fears (do not help with avoidance)
• Learn about anxiety to understand what they experience
• Take care of your own wellbeing too

WHEN TO SEEK HELP:
• Anxiety is affecting work, relationships, or daily activities
• You are avoiding many situations due to anxiety
• You are having frequent panic attacks
• You are using alcohol or drugs to cope
• You are having thoughts of harming yourself
• Symptoms are not improving despite trying self-help

═══════════════════════════════════════════════════════════════════════════════
IMPORTANT MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Anxiety is one of the most treatable mental health conditions. With the right help, you can overcome your fears and get back to living fully. Treatment works, recovery is possible, and you do not have to suffer alone. Take the first step - stick with treatment, face your fears gradually, and you will see improvement.`,

    references: [
      'American Psychiatric Association. Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition (DSM-5). 2013.',
      'WHO. Depression and Other Common Mental Disorders: Global Health Estimates. World Health Organization. 2017.',
      'WHO. mhGAP Intervention Guide for Mental, Neurological and Substance Use Disorders in Non-Specialized Health Settings. Version 2.0. 2016.',
      'NICE. Generalised anxiety disorder and panic disorder in adults: management. Clinical Guideline CG113. Updated 2019.',
      'NICE. Social anxiety disorder: recognition, assessment and treatment. Clinical Guideline CG159. 2013.',
      'Bandelow B, Michaelis S, Wedekind D. Treatment of anxiety disorders. Dialogues Clin Neurosci. 2017;19(2):93-107.',
      'Craske MG, Stein MB. Anxiety. Lancet. 2016;388(10063):3048-3059.',
      'Kessler RC, Petukhova M, Sampson NA, et al. Twelve-month and lifetime prevalence and lifetime morbid risk of anxiety and mood disorders in the United States. Int J Methods Psychiatr Res. 2012;21(3):169-184.',
      'Hofmann SG, Smits JA. Cognitive-behavioral therapy for adult anxiety disorders: a meta-analysis of randomized placebo-controlled trials. J Clin Psychiatry. 2008;69(4):621-632.',
      'Baldwin DS, Anderson IM, Nutt DJ, et al. Evidence-based pharmacological treatment of anxiety disorders, post-traumatic stress disorder and obsessive-compulsive disorder: a revision of the 2005 guidelines from the British Association for Psychopharmacology. J Psychopharmacol. 2014;28(5):403-439.',
      'Stein MB, Sareen J. Clinical Practice. Generalized Anxiety Disorder. N Engl J Med. 2015;373(21):2059-2068.',
      'Seedat S, Scott KM, Angermeyer MC, et al. Cross-national associations between gender and mental disorders in the World Health Organization World Mental Health Surveys. Arch Gen Psychiatry. 2009;66(7):785-795.',
      'Patel V, Chisholm D, Parikh R, et al. Addressing the burden of mental, neurological, and substance use disorders: key messages from Disease Control Priorities, 3rd edition. Lancet. 2016;387(10028):1672-1685.',
      'Cuijpers P, Sijbrandij M, Koole S, et al. Psychological treatment of generalized anxiety disorder: a meta-analysis. Clin Psychol Rev. 2014;34(2):130-140.',
      'Spitzer RL, Kroenke K, Williams JB, Lowe B. A brief measure for assessing generalized anxiety disorder: the GAD-7. Arch Intern Med. 2006;166(10):1092-1097.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 79. CIRRHOSIS OF THE LIVER
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '79',
    name: 'Cirrhosis of the Liver',
    slug: 'cirrhosis-liver',
    specialty: 'Gastroenterology',
    bodySystem: 'Gastrointestinal',
    icdCode: 'K74',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Cirrhosis is the end-stage of chronic liver disease characterized by diffuse fibrosis, regenerative nodules, and architectural distortion of the liver. It results from sustained hepatic injury from various causes and represents a major global health burden, causing over 1 million deaths annually. Cirrhosis is potentially reversible in early stages if the underlying cause is treated, but advanced cirrhosis with complications often requires liver transplantation.

The pathophysiology involves repeated cycles of hepatocyte injury and regeneration leading to progressive fibrosis. Activated hepatic stellate cells produce excessive collagen and extracellular matrix, disrupting normal liver architecture, increasing intrahepatic resistance, and causing portal hypertension - the main driver of complications. As functional hepatocyte mass decreases, synthetic function fails (coagulopathy, hypoalbuminemia) and metabolic capacity is impaired (hyperbilirubinemia, encephalopathy).

Cirrhosis is classified as compensated (no major complications) or decompensated (presence of ascites, variceal bleeding, hepatic encephalopathy, or jaundice). Decompensation marks a critical transition with significantly worse prognosis.

In Africa, cirrhosis is primarily driven by viral hepatitis (HBV accounts for 60-70% of cases in sub-Saharan Africa), with alcohol-related liver disease increasing in urban areas. HBV-related hepatocellular carcinoma (HCC) often presents at advanced stages due to limited surveillance. Access to specialists, endoscopy, and liver transplantation is severely limited across the continent.

Management focuses on treating the underlying cause (viral suppression, alcohol cessation), preventing and managing complications (ascites, varices, encephalopathy, HCC), and assessing for transplantation when indicated.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Treat the UNDERLYING CAUSE: Viral suppression (HBV/HCV), alcohol cessation can halt or reverse early cirrhosis

🔴 CRITICAL POINT 2 - HCC surveillance is MANDATORY: Ultrasound plus AFP every 6 months for ALL cirrhotic patients

🔴 CRITICAL POINT 3 - Screen ALL cirrhotics for varices: EGD at diagnosis; beta-blockers or band ligation for medium/large varices

🔴 CRITICAL POINT 4 - Spontaneous bacterial peritonitis (SBP): Suspect in any cirrhotic with ascites who deteriorates; diagnostic paracentesis essential

🟠 CRITICAL POINT 5 - Ascites management: Sodium restriction (2g/day) plus spironolactone:furosemide 100:40 ratio

🟠 CRITICAL POINT 6 - Hepatic encephalopathy: Lactulose titrated to 2-3 soft stools daily; rifaximin for recurrent episodes

🟠 CRITICAL POINT 7 - Child-Pugh and MELD scores: Guide prognosis and transplant timing; MELD ≥15 consider transplant evaluation

🟡 CRITICAL POINT 8 - Avoid nephrotoxins: NSAIDs, aminoglycosides; hepatorenal syndrome is often fatal

🟡 CRITICAL POINT 9 - Nutrition is critical: High protein (1.2-1.5 g/kg/day); avoid prolonged fasting; late evening snack`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Cirrhosis causes over 1 million deaths annually (2% of global deaths)
• 11th leading cause of death globally
• Major causes: Hepatitis C (25-30%), Hepatitis B (20-30%), Alcohol (20-25%), NAFLD (increasing)
• HCC develops in 1-5% of cirrhotics annually

AFRICAN EPIDEMIOLOGY:
• Hepatitis B is leading cause of cirrhosis in sub-Saharan Africa (60-70%)
• HBV prevalence 6-8% (hyperendemic); vertical and horizontal transmission
• Hepatitis C significant in North Africa (Egypt historical epidemic)
• Alcohol-related cirrhosis increasing in urban areas
• Schistosomiasis causes periportal fibrosis in endemic areas
• NAFLD/NASH increasing with urbanization
• HCC is one of the most common cancers in African men
• Limited access to liver specialists, endoscopy, transplantation
• High mortality due to delayed diagnosis

NATURAL HISTORY:
• Compensated cirrhosis: Median survival 12+ years
• Decompensated cirrhosis: Median survival 2 years
• Annual risk of decompensation: 5-7%
• Annual risk of HCC: 1-5%`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

COMPENSATED CIRRHOSIS (may be asymptomatic):
• Often discovered incidentally
• Fatigue, weakness
• Anorexia, weight loss
• Hepatomegaly (early) or small liver (late)
• Splenomegaly
• Spider angiomata, palmar erythema

DECOMPENSATED CIRRHOSIS:
• Ascites (most common decompensation)
• Peripheral edema
• Jaundice
• Hepatic encephalopathy (confusion, asterixis)
• Variceal hemorrhage (hematemesis, melena)
• Spontaneous bacterial peritonitis
• Hepatorenal syndrome

PHYSICAL EXAMINATION:
• Jaundice (scleral icterus)
• Spider angiomata (upper body)
• Palmar erythema
• Dupuytren contracture
• Gynecomastia, testicular atrophy (men)
• Ascites (shifting dullness, fluid wave)
• Caput medusae
• Asterixis (flapping tremor)
• Fetor hepaticus

CHILD-PUGH CLASSIFICATION:
Parameters (1-3 points each):
• Bilirubin: <2 / 2-3 / >3 mg/dL
• Albumin: >3.5 / 2.8-3.5 / <2.8 g/dL
• INR: <1.7 / 1.7-2.3 / >2.3
• Ascites: None / Mild / Moderate-Severe
• Encephalopathy: None / Grade 1-2 / Grade 3-4

• Class A (5-6): Compensated, 1-year survival 100%
• Class B (7-9): Significant impairment, 1-year survival 80%
• Class C (10-15): Decompensated, 1-year survival 45%

MELD SCORE:
• Based on bilirubin, INR, creatinine
• MELD <10: 2% 3-month mortality
• MELD 20-29: 20% mortality
• MELD ≥30: 50% mortality
• MELD ≥15: Consider transplant evaluation`,

    diagnosis: `DIAGNOSTIC APPROACH:

STEP 1: CLINICAL SUSPICION
• Risk factors: HBV/HCV, alcohol, metabolic syndrome
• Signs of chronic liver disease
• Abnormal liver tests

STEP 2: CONFIRM CIRRHOSIS
• Imaging: Nodular liver, portal hypertension signs
• FibroScan >12.5 kPa suggests cirrhosis
• APRI >2 or FIB-4 >3.25 suggests cirrhosis
• Liver biopsy (rarely needed)

STEP 3: DETERMINE ETIOLOGY
• HBsAg, anti-HCV, HCV RNA
• Alcohol history
• Metabolic/autoimmune workup if indicated

STEP 4: ASSESS SEVERITY
• Child-Pugh and MELD scores
• Screen for varices (EGD)
• Screen for HCC (US + AFP)

LABORATORY FINDINGS:
• AST/ALT: May be normal or mildly elevated; AST > ALT in alcohol
• Bilirubin: Elevated in decompensated
• Albumin: Low (decreased synthesis)
• INR: Prolonged
• Platelets: Low (hypersplenism) - often first sign
• Sodium: Low (dilutional)

SERUM-ASCITES ALBUMIN GRADIENT (SAAG):
• SAAG ≥1.1 g/dL = Portal hypertension (cirrhosis)
• SAAG <1.1 g/dL = Non-portal hypertensive cause

SBP DIAGNOSIS:
• Ascitic fluid PMN >250 cells/mm3 = SBP
• Culture often negative`,

    differentialDiagnosis: [
      'Non-cirrhotic portal hypertension - Portal vein thrombosis, schistosomiasis; preserved liver function',
      'Cardiac cirrhosis - Right heart failure; elevated JVP; responds to diuresis',
      'Budd-Chiari syndrome - Hepatic vein thrombosis; Doppler ultrasound diagnostic',
      'Acute liver failure - Rapid onset without prior liver disease',
      'Hepatocellular carcinoma - May coexist; mass on imaging, elevated AFP',
      'Alcoholic hepatitis - Acute presentation; very high AST; may have underlying cirrhosis',
      'Metastatic liver disease - Multiple lesions; search for primary',
      'Infiltrative disease - Amyloidosis, lymphoma; biopsy diagnostic'
    ],

    investigations: [
      {
        name: 'Liver Function Tests (AST, ALT, ALP, GGT, Bilirubin, Albumin)',
        indication: '🔴 ESSENTIAL - Assess hepatocyte injury and synthetic function; albumin and bilirubin prognostic',
        availability: 'BASIC'
      },
      {
        name: 'INR/Prothrombin Time',
        indication: '🔴 ESSENTIAL - Synthetic function; component of Child-Pugh and MELD scores',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count with Platelets',
        indication: '🔴 ESSENTIAL - Thrombocytopenia often first sign of portal hypertension',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function and Electrolytes',
        indication: '🔴 ESSENTIAL - Creatinine for MELD; detect hepatorenal syndrome; monitor diuretics',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B Serology (HBsAg, HBeAg, HBV DNA)',
        indication: '🔴 ESSENTIAL - Most common cause in Africa; determines need for antiviral therapy',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis C Serology and HCV RNA',
        indication: '🔴 ESSENTIAL - Curable cause; treat to prevent progression',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Abdominal Ultrasound with Doppler',
        indication: '🔴 ESSENTIAL - Liver morphology, portal vein patency, ascites, HCC screening',
        availability: 'BASIC'
      },
      {
        name: 'Alpha-Fetoprotein (AFP)',
        indication: '🔴 ESSENTIAL - HCC screening every 6 months; >400 ng/mL highly suspicious',
        availability: 'BASIC'
      },
      {
        name: 'Upper GI Endoscopy (EGD)',
        indication: '🔴 ESSENTIAL - Screen for varices at diagnosis; determines prophylaxis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Diagnostic Paracentesis',
        indication: '🔴 ESSENTIAL - New ascites or suspected SBP; cell count, albumin (SAAG), culture',
        availability: 'BASIC'
      },
      {
        name: 'APRI/FIB-4 Score (Calculated)',
        indication: '🔴 ESSENTIAL - Non-invasive fibrosis assessment using routine labs',
        availability: 'BASIC'
      },
      {
        name: 'FibroScan (Transient Elastography)',
        indication: '🟡 IF AVAILABLE - Non-invasive fibrosis staging; >12.5 kPa suggests cirrhosis',
        availability: 'ADVANCED'
      },
      {
        name: 'CT/MRI Abdomen',
        indication: '🟠 IMPORTANT - HCC characterization; arterial enhancement with venous washout',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '⚡ IDENTIFY CAUSE: Viral serologies, alcohol history; treat underlying etiology',
        '⚡ ASSESS SEVERITY: Calculate Child-Pugh and MELD scores',
        '⚡ SCREEN FOR VARICES: All new cirrhosis diagnoses need EGD',
        '⚡ HCC SURVEILLANCE: Ultrasound plus AFP every 6 months - lifelong',
        '⚡ VACCINATIONS: Hepatitis A and B (if not immune), pneumococcal, influenza',
        '⚡ ALCOHOL CESSATION: Absolute abstinence required',
        '⚡ PARACENTESIS: If new ascites - diagnostic tap to rule out SBP',
        '⚡ MEDICATION REVIEW: Stop NSAIDs, reduce hepatically metabolized drugs',
        '⚡ NUTRITION: High protein diet (1.2-1.5g/kg/day); avoid fasting',
        '⚡ EDUCATION: Explain disease, warning signs, medication adherence'
      ],
      
      pharmacological: [
        {
          drug: 'Tenofovir or Entecavir (for HBV cirrhosis)',
          indication: '🔴 ESSENTIAL - All HBV cirrhotics need indefinite antiviral therapy',
          dose: 'TDF 300mg PO daily OR Entecavir 0.5-1mg PO daily. Adjust for renal function',
          route: 'PO',
          duration: 'INDEFINITE - Never stop in HBV cirrhosis',
          contraindications: ['Adjust dose for renal impairment'],
          sideEffects: ['TDF: Nephrotoxicity, bone effects', 'Generally well tolerated'],
          alternatives: ['TAF if renal concerns']
        },
        {
          drug: 'Sofosbuvir/Velpatasvir (for HCV cirrhosis)',
          indication: '🔴 ESSENTIAL - Curative treatment for HCV; >95% SVR even in cirrhosis',
          dose: 'SOF/VEL 400/100mg one tablet PO daily x 12 weeks. Decompensated: Add ribavirin',
          route: 'PO',
          duration: '12 weeks',
          contraindications: ['Severe renal impairment for sofosbuvir'],
          sideEffects: ['Headache', 'Fatigue', 'Generally well tolerated'],
          alternatives: ['Sofosbuvir/daclatasvir']
        },
        {
          drug: 'Spironolactone',
          indication: '🔴 FIRST-LINE for ASCITES - Aldosterone antagonist; cornerstone of diuretic therapy',
          dose: 'Start 100mg PO daily. Increase by 100mg every 3-5 days. Max: 400mg/day. Maintain 100:40 ratio with furosemide',
          route: 'PO',
          duration: 'Ongoing while ascites present',
          contraindications: ['Hyperkalemia >5.5', 'Severe renal impairment'],
          sideEffects: ['Hyperkalemia', 'Gynecomastia', 'Breast tenderness'],
          alternatives: ['Amiloride if gynecomastia intolerable']
        },
        {
          drug: 'Furosemide',
          indication: '🔴 ADJUNCT for ASCITES - Add to spironolactone; maintain 100:40 ratio',
          dose: 'Start 40mg PO daily with spironolactone 100mg. Max: 160mg/day',
          route: 'PO',
          duration: 'Ongoing with spironolactone',
          contraindications: ['Severe hyponatremia <125', 'Hepatic encephalopathy (hold)'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Prerenal azotemia'],
          alternatives: ['Torsemide']
        },
        {
          drug: 'Propranolol',
          indication: '🔴 PRIMARY PROPHYLAXIS for VARICES - For medium/large varices; reduces portal pressure',
          dose: 'Start 20mg PO BD. Titrate to HR 55-60 bpm. Usual: 40-80mg BD. Max: 320mg/day',
          route: 'PO',
          duration: 'Indefinite for variceal prophylaxis',
          contraindications: ['Refractory ascites', 'SBP', 'Severe bradycardia', 'Asthma'],
          sideEffects: ['Bradycardia', 'Hypotension', 'Fatigue'],
          alternatives: ['Carvedilol 6.25-12.5mg BD', 'Nadolol']
        },
        {
          drug: 'Lactulose',
          indication: '🔴 FIRST-LINE for HEPATIC ENCEPHALOPATHY - Reduces ammonia; titrate to bowel movements',
          dose: 'Treatment: 30-45mL PO q1-2h until bowel movement, then titrate to 2-3 soft stools/day. Maintenance: 15-30mL 2-3 times daily',
          route: 'PO or rectal enema',
          duration: 'Ongoing for secondary prophylaxis',
          contraindications: ['Galactosemia', 'Bowel obstruction'],
          sideEffects: ['Bloating', 'Flatulence', 'Diarrhea if excessive'],
          alternatives: ['Lactulose enema if unable to take orally']
        },
        {
          drug: 'Rifaximin',
          indication: '🟠 ADD-ON for RECURRENT HE - Add to lactulose after second episode',
          dose: '550mg PO twice daily',
          route: 'PO',
          duration: 'Long-term prophylaxis',
          contraindications: ['Hypersensitivity to rifamycins'],
          sideEffects: ['Generally well tolerated', 'Nausea'],
          alternatives: ['Oral neomycin (less preferred)']
        },
        {
          drug: 'Ceftriaxone (for SBP)',
          indication: '🔴 FIRST-LINE for SBP - Empiric treatment; covers common pathogens',
          dose: '2g IV once daily for 5 days',
          route: 'IV',
          duration: '5 days',
          contraindications: ['Ceftriaxone hypersensitivity'],
          sideEffects: ['Diarrhea', 'Rash'],
          alternatives: ['Ciprofloxacin if not on quinolone prophylaxis']
        },
        {
          drug: 'Albumin',
          indication: '🔴 ESSENTIAL for SBP and LVP - Prevents hepatorenal syndrome with SBP; volume expansion with LVP >5L',
          dose: 'SBP: 1.5g/kg day 1, 1g/kg day 3. LVP: 6-8g per liter removed if >5L drained',
          route: 'IV',
          duration: 'Per indication',
          contraindications: ['Volume overload'],
          sideEffects: ['Fluid overload', 'Allergic reactions (rare)'],
          alternatives: ['Limit drainage to <5L if albumin unavailable']
        },
        {
          drug: 'Norfloxacin (SBP Prophylaxis)',
          indication: '🟠 SECONDARY PROPHYLAXIS - After SBP episode; primary if ascitic protein <1.5 with risk factors',
          dose: '400mg PO once daily',
          route: 'PO',
          duration: 'Indefinite or until ascites resolves',
          contraindications: ['QT prolongation'],
          sideEffects: ['GI upset', 'Tendinitis'],
          alternatives: ['Ciprofloxacin 500mg daily', 'TMP-SMX']
        }
      ],
      
      nonPharmacological: [
        'ALCOHOL ABSTINENCE: Absolute requirement regardless of cirrhosis etiology',
        'SODIUM RESTRICTION: 2g (88mmol) per day for ascites management',
        'NUTRITION: High protein 1.2-1.5g/kg/day; frequent small meals; late evening snack',
        'AVOID FASTING: Eat every 3-4 hours to prevent catabolism',
        'FLUID RESTRICTION: Only if Na+ <125; restrict to 1-1.5L/day',
        'WEIGHT MONITORING: Daily weights; target 0.5kg/day loss (no edema) or 1kg/day (with edema)',
        'AVOID HEPATOTOXINS: No NSAIDs, limit paracetamol <2g/day, avoid herbal supplements',
        'VACCINATION: HAV, HBV, pneumococcal, influenza',
        'INFECTION PREVENTION: Good hygiene; avoid raw seafood'
      ],
      
      surgical: [
        'LIVER TRANSPLANTATION: Definitive treatment for decompensated cirrhosis; MELD ≥15 or complications',
        'TIPS (Transjugular Intrahepatic Portosystemic Shunt): For refractory ascites or recurrent variceal bleeding',
        'LARGE VOLUME PARACENTESIS: For tense ascites; give albumin 6-8g/L if >5L removed',
        'ENDOSCOPIC BAND LIGATION: Primary prophylaxis alternative; treatment of acute variceal bleed',
        'HCC TREATMENT: Resection, ablation, TACE, or transplant depending on stage'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Acute variceal hemorrhage - resuscitate, PPI, octreotide, urgent endoscopy',
        '🔴 IMMEDIATE: Grade 3-4 hepatic encephalopathy - airway protection, ICU if available',
        '🔴 IMMEDIATE: Suspected SBP - paracentesis, empiric antibiotics',
        '🔴 IMMEDIATE: Hepatorenal syndrome - stop diuretics, albumin, specialist input',
        '🟠 URGENT: New decompensation - comprehensive evaluation',
        '🟠 URGENT: MELD ≥15 - transplant evaluation if available',
        '🟠 URGENT: Suspected HCC - CT/MRI, oncology input',
        '🟡 ROUTINE: New diagnosis - EGD, baseline evaluation',
        '🟡 ROUTINE: Refractory ascites - consider TIPS'
      ],
      
      monitoring: [
        'LFTs, ALBUMIN, INR: Every 3-6 months; more frequent if decompensated',
        'RENAL FUNCTION: Monitor closely on diuretics; watch for hepatorenal syndrome',
        'HCC SURVEILLANCE: Ultrasound + AFP every 6 months - LIFELONG - do not miss',
        'CHILD-PUGH AND MELD: Calculate at each visit; track trends',
        'WEIGHT: Daily for ascites patients; adjust diuretics accordingly',
        'ENCEPHALOPATHY: Assess at each visit; adjust lactulose to 2-3 soft stools/day',
        'VARICES: Repeat EGD per protocol',
        'NUTRITION: Monitor weight, muscle mass, dietary intake'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

COMPENSATED CIRRHOSIS (Child-Pugh A):
• Follow-up every 3-6 months
• HCC surveillance: US + AFP every 6 months (MANDATORY)
• Variceal screening: EGD at diagnosis
  - No varices: Repeat in 2-3 years
  - Small varices: Repeat annually or start beta-blocker
  - Medium/large: Beta-blocker OR band ligation

DECOMPENSATED CIRRHOSIS (Child-Pugh B/C):
• Follow-up every 1-3 months
• HCC surveillance continues every 6 months
• Transplant evaluation if available
• Manage complications aggressively

ASCITES MANAGEMENT:
• Sodium restriction 2g/day
• Diuretics: Spironolactone 100mg + furosemide 40mg; adjust in 100:40 ratio
• Target weight loss: 0.5 kg/day (no edema) to 1 kg/day (with edema)
• Diagnostic paracentesis if clinical deterioration (rule out SBP)

VARICEAL BLEEDING PREVENTION:
• Primary prophylaxis: Beta-blocker OR EVL for medium/large varices
• Secondary prophylaxis: Beta-blocker PLUS EVL

HEPATIC ENCEPHALOPATHY:
• Identify precipitant (GI bleed, infection, constipation)
• Lactulose to 2-3 soft stools daily
• Rifaximin for recurrent episodes
• Maintain protein intake (do NOT restrict)

SBP MANAGEMENT:
• Diagnosis: Ascitic fluid PMN >250/mm3
• Treatment: Ceftriaxone + albumin
• Secondary prophylaxis: Norfloxacin indefinitely

TRANSPLANT CONSIDERATIONS:
• Evaluate when: MELD ≥15, recurrent decompensation, HCC within criteria
• Limited availability in Africa
• Focus on optimal medical management`
    },

    redFlags: [
      '🔴 CRITICAL: Hematemesis or melena - variceal bleeding; resuscitate, urgent endoscopy',
      '🔴 CRITICAL: Grade 3-4 encephalopathy - airway protection, identify precipitant, ICU',
      '🔴 CRITICAL: Fever with ascites - SBP; immediate paracentesis and antibiotics',
      '🔴 CRITICAL: Rising creatinine with ascites - hepatorenal syndrome; stop diuretics, albumin',
      '🔴 CRITICAL: Severe hyponatremia <120 - careful correction, transplant evaluation',
      '🟠 URGENT: New jaundice in stable cirrhotic - exclude HCC, biliary obstruction',
      '🟠 URGENT: Refractory ascites - consider TIPS evaluation',
      '🟠 URGENT: Rising AFP or new liver lesion - urgent HCC workup',
      '🟡 CAUTION: Worsening encephalopathy - search for precipitant',
      '🟡 CAUTION: Progressive muscle wasting - optimize nutrition'
    ],

    complications: [
      'Portal hypertension - Drives most complications',
      'Ascites - Most common decompensation; risk of SBP',
      'Spontaneous bacterial peritonitis (SBP) - 20-40% mortality',
      'Esophageal/gastric varices - Bleeding is life-threatening (20% mortality)',
      'Hepatic encephalopathy - Confusion to coma; usually reversible',
      'Hepatorenal syndrome - Functional renal failure; very high mortality',
      'Hepatocellular carcinoma (HCC) - 1-5% annual incidence',
      'Hepatopulmonary syndrome - Hypoxemia from intrapulmonary shunting',
      'Coagulopathy - Both bleeding and thrombotic risk',
      'Malnutrition and sarcopenia - Worsens prognosis',
      'Dilutional hyponatremia - Marker of advanced disease'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

SURVIVAL BY STAGE:
• Compensated cirrhosis: Median survival 12+ years
• Decompensated: Median survival 2 years without transplant

CHILD-PUGH SURVIVAL:
• Class A: 1-year 100%, 2-year 85%
• Class B: 1-year 80%, 2-year 60%
• Class C: 1-year 45%, 2-year 35%

IMPACT OF TREATMENT:
• HBV suppression: Reduces decompensation, HCC risk, fibrosis may regress
• HCV cure (SVR): Fibrosis may regress, HCC risk reduced but not eliminated
• Alcohol abstinence: Significant survival improvement
• Liver transplantation: 5-year survival 70-80%

FACTORS AFFECTING PROGNOSIS:
Better: Treating cause, compensated at diagnosis, lower MELD, no HCC
Worse: Decompensation, high MELD, HCC, continued alcohol, sarcopenia`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
• Hepatitis B vaccination (including birth dose)
• Safe injection practices, blood screening (HCV)
• Alcohol moderation/avoidance
• Weight management for NAFLD prevention

SECONDARY PREVENTION:
• Treat HBV/HCV before cirrhosis develops
• Alcohol cessation in early liver disease

TERTIARY PREVENTION:
• HCC surveillance every 6 months
• Variceal screening and prophylaxis
• SBP prophylaxis when indicated
• Nutrition optimization

PUBLIC HEALTH PRIORITIES FOR AFRICA:
• Scale up HBV vaccination (birth dose critical)
• Improve HBV and HCV testing and treatment access
• Reduce alcohol-related harm
• Train healthcare workers in cirrhosis management
• Improve endoscopy access`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS CIRRHOSIS?
Cirrhosis is scarring of the liver from long-term damage. The liver is vital for processing nutrients, making proteins, and removing toxins. When scarred, it cannot work properly.

COMMON CAUSES IN AFRICA:
• Hepatitis B virus (most common)
• Hepatitis C virus
• Heavy alcohol use
• Fatty liver disease

THE GOOD NEWS:
• If caught early, progression can be stopped
• Treating the cause (virus, alcohol) is very important
• Many complications can be prevented or managed

IMPORTANT LIFESTYLE CHANGES:

ABSOLUTELY NO ALCOHOL - even small amounts can worsen damage

LOW-SALT DIET (if you have fluid):
• No added salt; avoid processed foods
• Less than 2 grams (half teaspoon) per day

GOOD NUTRITION:
• Eat plenty of protein (eggs, fish, chicken, beans)
• Do NOT skip meals; have a snack before bed
• Avoid raw shellfish

TAKE YOUR MEDICINES AS PRESCRIBED:
• Diuretics: Help remove fluid
• Lactulose: Prevents confusion; aim for 2-3 soft bowel movements daily
• Antiviral medicines: Essential if you have hepatitis

MEDICINES TO AVOID:
• Ibuprofen, diclofenac (can cause kidney problems)
• Limit paracetamol to less than 2g per day
• Avoid herbal remedies

WARNING SIGNS - SEEK HELP IMMEDIATELY:
• Vomiting blood or black stools
• Increasing confusion or drowsiness
• Fever with belly swelling
• Very little urine

REGULAR CHECK-UPS:
• Ultrasound and blood test every 6 months (for liver cancer)
• Camera test (endoscopy) to check for vein swelling
• Keep all appointments

Cirrhosis is serious but manageable with proper care. Take your medicines, avoid alcohol, follow your diet, and attend all appointments.`,

    references: [
      'European Association for the Study of the Liver. EASL Clinical Practice Guidelines for the management of patients with decompensated cirrhosis. J Hepatol. 2018;69(2):406-460.',
      'Garcia-Tsao G, Abraldes JG, Berzigotti A, Bosch J. Portal hypertensive bleeding in cirrhosis: Risk stratification, diagnosis, and management: 2016 practice guidance by AASLD. Hepatology. 2017;65(1):310-335.',
      'Vilstrup H, Amodio P, Bajaj J, et al. Hepatic encephalopathy in chronic liver disease: 2014 Practice Guideline by AASLD and EASL. Hepatology. 2014;60(2):715-735.',
      'Runyon BA, AASLD. Management of adult patients with ascites due to cirrhosis: Update 2012. Hepatology. 2013;57(4):1651-1653.',
      'Marrero JA, Kulik LM, Sirlin CB, et al. Diagnosis, Staging, and Management of Hepatocellular Carcinoma: 2018 Practice Guidance by AASLD. Hepatology. 2018;68(2):723-750.',
      'WHO. Guidelines for the prevention, care and treatment of persons with chronic hepatitis B infection. 2015.',
      'Tsochatzis EA, Bosch J, Burroughs AK. Liver cirrhosis. Lancet. 2014;383(9930):1749-1761.',
      'Spearman CW, Afihene M, Ally R, et al. Hepatitis B in sub-Saharan Africa: strategies to achieve the 2030 elimination targets. Lancet Gastroenterol Hepatol. 2017;2(12):900-909.',
      'Gines P, Krag A, Abraldes JG, et al. Liver cirrhosis. Lancet. 2021;398(10308):1359-1376.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 80. GOUT
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '80',
    name: 'Gout',
    slug: 'gout',
    specialty: 'Internal Medicine',
    bodySystem: 'Musculoskeletal',
    icdCode: 'M10',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Gout is an inflammatory arthritis caused by deposition of monosodium urate (MSU) crystals in joints and soft tissues, resulting from chronic hyperuricemia. It is the most common form of inflammatory arthritis in men and older women, affecting approximately 1-4% of adults in developed countries. Gout causes recurrent attacks of acute, intensely painful monoarthritis, most classically affecting the first metatarsophalangeal (MTP) joint (podagra).

The pathophysiology begins with sustained hyperuricemia (serum uric acid >6.8 mg/dL or 0.40 mmol/L, the saturation point for urate in physiological conditions). Over years, urate crystals deposit in joints and soft tissues. Acute gout flares occur when crystals trigger an intense inflammatory response mediated by the NLRP3 inflammasome, IL-1β release, and neutrophil infiltration. The resulting inflammation causes the characteristic sudden, severe pain, swelling, warmth, and erythema.

Gout progresses through stages: asymptomatic hyperuricemia → acute intermittent gout → intercritical periods → chronic tophaceous gout. Without treatment, flares become more frequent, polyarticular, and may lead to joint damage and disability from tophi (urate crystal deposits).

In Africa, gout prevalence is rising, particularly in urban areas, associated with dietary changes (increased meat and alcohol consumption), obesity, and metabolic syndrome. Prevalence estimates range from 0.5-3% depending on the population. Access to urate-lowering therapy (ULT) and optimal management varies significantly. Many patients receive suboptimal care, with undertreatment of acute flares and failure to initiate or titrate ULT appropriately.

Management involves two distinct phases: (1) treating acute flares with anti-inflammatory therapy (NSAIDs, colchicine, or corticosteroids), and (2) long-term urate-lowering therapy to prevent recurrent flares, tophi, and joint damage. The target serum urate is <6 mg/dL (<0.36 mmol/L), or <5 mg/dL in severe gout.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Acute flare treatment: NSAIDs, colchicine, or corticosteroids; start within 24 hours; do NOT start or stop ULT during acute flare

🔴 CRITICAL POINT 2 - Target serum urate <6 mg/dL (<0.36 mmol/L): Reduces flare frequency and tophi; some guidelines recommend <5 mg/dL in severe gout

🔴 CRITICAL POINT 3 - When starting ULT, provide flare prophylaxis: Low-dose colchicine or NSAID for 3-6 months; flares common when initiating ULT

🔴 CRITICAL POINT 4 - Allopurinol start LOW, go SLOW: Begin 100mg/day (50mg if renal impairment), increase by 100mg every 2-4 weeks to target urate

🟠 CRITICAL POINT 5 - Do NOT stop allopurinol during acute flare: Continue ULT; stopping may worsen or prolong the flare

🟠 CRITICAL POINT 6 - Colchicine dosing has changed: Low-dose is as effective and much safer; 1.2mg then 0.6mg 1 hour later (not repeated high doses)

🟠 CRITICAL POINT 7 - Gout is associated with metabolic syndrome: Screen for and manage hypertension, diabetes, dyslipidemia, obesity, CKD

🟡 CRITICAL POINT 8 - Crystal confirmation is gold standard: Aspirate joint if possible; negatively birefringent needle-shaped crystals under polarized microscopy

🟡 CRITICAL POINT 9 - Asymptomatic hyperuricemia usually does NOT need treatment: Treat gout, not just high uric acid numbers`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common inflammatory arthritis: Affects 1-4% of adults
• Prevalence increasing globally over past decades
• Male predominance (3-4:1); female risk increases post-menopause
• Peak incidence: Men 40-50 years; women >60 years
• Strong association with metabolic syndrome, CKD, cardiovascular disease
• Increasing with obesity epidemic and dietary changes

RISK FACTORS:
• Hyperuricemia (serum urate >6.8 mg/dL is necessary but not sufficient)
• Male sex
• Increasing age
• Obesity and metabolic syndrome
• Diet: Red meat, organ meats, shellfish, high-fructose corn syrup
• Alcohol: Beer and spirits (wine has less risk)
• Medications: Thiazide and loop diuretics, low-dose aspirin, cyclosporine
• Chronic kidney disease (reduced urate excretion)
• Family history (genetic variants in urate transporters)
• Lead exposure (saturnine gout)

AFRICAN EPIDEMIOLOGY:
• Prevalence estimates: 0.5-3% (increasing with urbanization)
• Historically considered rare in Africa, now increasing
• Rising with dietary westernization (meat, processed foods, sugary drinks)
• Alcohol consumption patterns contribute
• Association with hypertension (common in Africa)
• Often underdiagnosed or misdiagnosed
• Limited access to serum urate testing and optimal treatment
• Higher rates in urban vs rural populations
• Men predominantly affected

URIC ACID SOURCES:
• Endogenous purine metabolism (two-thirds)
• Dietary purines (one-third)

URIC ACID ELIMINATION:
• Kidney (70%): Filtered, reabsorbed, secreted
• Gut (30%): Bacterial uricolysis

PROGRESSION:
• Asymptomatic hyperuricemia: Years to decades
• First gout attack: ~80% are monoarticular
• Intercritical periods: Asymptomatic between attacks
• Recurrence: >60% have second attack within 1 year if untreated
• Chronic tophaceous gout: After years of inadequate treatment`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ACUTE GOUT FLARE:

Classic Presentation:
• Sudden onset, often nocturnal
• Severe pain - "worst pain imaginable"
• Peak intensity within 12-24 hours
• Single joint affected (monoarthritis) in 80-90% of first attacks
• First MTP joint (podagra) most common (50% of first attacks)
• Other common sites: Ankle, knee, wrist, fingers, olecranon bursa

Joint Findings:
• Exquisite tenderness (even bedsheet causes pain)
• Swelling
• Warmth
• Erythema (can be so red it mimics cellulitis)
• Limited range of motion

Systemic Features:
• Low-grade fever possible
• Malaise
• Self-limited: Resolves in 7-14 days even without treatment

Triggers for Acute Attacks:
• Dietary excess (purine-rich food, alcohol)
• Dehydration
• Acute illness or surgery
• Trauma to joint
• Starting or stopping urate-lowering therapy
• Diuretic initiation

INTERCRITICAL GOUT:
• Asymptomatic period between flares
• Duration shortens over time if untreated
• Urate crystals still present in joints
• Imaging may show crystal deposits

CHRONIC TOPHACEOUS GOUT:

Tophi:
• Nodular deposits of urate crystite in soft tissues
• Chalky white material if ulcerated
• Common locations: Helix of ear, olecranon bursa, Achilles tendon, finger pads, toes
• Can cause joint destruction and deformity
• May ulcerate and drain

Chronic Arthritis:
• Polyarticular involvement
• Joint damage and erosions
• Reduced function and disability
• Can mimic rheumatoid arthritis

ATYPICAL PRESENTATIONS:
• Polyarticular gout (more common in elderly, women)
• Less dramatic inflammation (especially in elderly)
• Upper extremity involvement (especially if diuretic-induced)
• Bursitis (olecranon, prepatellar)
• Tenosynovitis
• Spinal gout (rare)

STAGES OF GOUT:
1. Asymptomatic hyperuricemia: Elevated urate, no symptoms
2. Acute gouty arthritis: Painful flares with symptom-free intervals
3. Intercritical gout: Asymptomatic between attacks
4. Chronic tophaceous gout: Tophi, chronic arthritis, joint damage`,

    diagnosis: `DIAGNOSTIC APPROACH:

GOLD STANDARD:
• Identification of monosodium urate (MSU) crystals in synovial fluid or tophus
• Needle-shaped, negatively birefringent crystals under polarized microscopy
• Yellow when parallel to compensator axis

CLINICAL DIAGNOSIS:
When joint aspiration not possible, clinical criteria can be used:
• Typical podagra presentation
• Response to colchicine within 24-48 hours
• More than one attack of acute arthritis
• Unilateral first MTP joint involvement
• Redness over joint
• Hyperuricemia (supportive but not diagnostic)

ACR/EULAR 2015 GOUT CLASSIFICATION CRITERIA:
Entry criterion: At least one episode of swelling, pain, or tenderness in joint or bursa

Score ≥8 points classifies as gout:
• MSU crystals in symptomatic joint/bursa or tophus: Sufficient alone
• Clinical: Pattern, characteristics, time course, tophi
• Laboratory: Serum urate, MSU-negative synovial fluid
• Imaging: Double contour sign, erosions, tophi on imaging

SERUM URATE:
• Hyperuricemia defined: >6.8 mg/dL (>0.40 mmol/L)
• Often NORMAL or LOW during acute attack (cytokine effect)
• Check 2+ weeks after flare resolves for accurate baseline
• Neither sensitive nor specific for diagnosis
• Used for monitoring treatment, not diagnosis

JOINT ASPIRATION (Arthrocentesis):
• Gold standard when feasible
• Fluid analysis: WBC count (usually 10,000-70,000/mm3, neutrophil predominant)
• Crystal examination under polarized microscopy
• Also rules out septic arthritis

IMAGING:

PLAIN X-RAY:
• May be normal early in disease
• Soft tissue swelling during acute attack
• "Punched-out" erosions with sclerotic margins (chronic gout)
• Overhanging edges (pathognomonic)
• Joint space preserved until late
• Tophi may be visible

ULTRASOUND:
• Double contour sign: Hyperechoic line over articular cartilage (urate deposits)
• Tophaceous deposits
• Useful for diagnosis and monitoring
• Increasingly available

DUAL-ENERGY CT (DECT):
• Highly specific for urate deposits
• Can detect tophi not clinically apparent
• Limited availability and cost

DIFFERENTIAL FEATURES:

GOUT vs SEPTIC ARTHRITIS:
• Both can have fever, hot red joint
• Gout: History of prior attacks, rapid onset, podagra, hyperuricemia
• Septic: More systemic toxicity, higher WBC, positive culture
• When in doubt, ASPIRATE - can have both simultaneously

GOUT vs PSEUDOGOUT (CPPD):
• Pseudogout: Calcium pyrophosphate crystals (positively birefringent, rhomboid)
• Pseudogout: More often knee, wrist; elderly; chondrocalcinosis on X-ray
• Gout: First MTP, negatively birefringent needle crystals`,

    differentialDiagnosis: [
      'Septic arthritis - Fever, more systemic; joint aspiration with culture essential; can coexist with gout',
      'Pseudogout (CPPD) - Calcium pyrophosphate crystals; positively birefringent, rhomboid; elderly, knee/wrist',
      'Cellulitis - Skin infection; may mimic periarticular gout; no joint effusion; spreading erythema',
      'Reactive arthritis - Post-infectious; often oligoarticular; conjunctivitis, urethritis may be present',
      'Rheumatoid arthritis - Polyarticular, symmetric; morning stiffness >1 hour; RF/anti-CCP positive',
      'Psoriatic arthritis - Psoriasis present; dactylitis, enthesitis; nail changes',
      'Osteoarthritis - Chronic; less inflammatory; Heberden/Bouchard nodes; X-ray changes',
      'Trauma/fracture - History of injury; X-ray diagnostic',
      'Bursitis (non-gouty) - Olecranon, prepatellar; may be traumatic or infectious',
      'Palindromic rheumatism - Recurrent self-limited monoarthritis; may precede RA',
      'Sarcoid arthropathy - Systemic features; hilar lymphadenopathy; elevated ACE',
      'Hemochromatosis arthropathy - Second and third MCP joints; iron studies abnormal'
    ],

    investigations: [
      {
        name: 'Joint Aspiration with Synovial Fluid Analysis',
        indication: '🔴 GOLD STANDARD - Crystal identification under polarized microscopy; also excludes septic arthritis',
        availability: 'BASIC'
      },
      {
        name: 'Synovial Fluid Crystal Examination (Polarized Microscopy)',
        indication: '🔴 DIAGNOSTIC - MSU crystals: Needle-shaped, negatively birefringent; definitive diagnosis',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Uric Acid',
        indication: '🔴 ESSENTIAL - For monitoring treatment; check AFTER flare resolves (2+ weeks); target <6 mg/dL',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟠 IMPORTANT - Leukocytosis during acute flare; exclude infection; baseline before treatment',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, eGFR)',
        indication: '🔴 ESSENTIAL - Gout associated with CKD; adjust allopurinol dose; avoid NSAIDs if impaired',
        availability: 'BASIC'
      },
      {
        name: 'Fasting Glucose / HbA1c',
        indication: '🟠 IMPORTANT - Screen for diabetes (metabolic syndrome association)',
        availability: 'BASIC'
      },
      {
        name: 'Lipid Profile',
        indication: '🟠 IMPORTANT - Screen for dyslipidemia (metabolic syndrome association)',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🟠 IMPORTANT - Baseline before ULT; alcohol-related liver disease',
        availability: 'BASIC'
      },
      {
        name: 'Plain X-ray of Affected Joint',
        indication: '🟠 IMPORTANT - May be normal early; erosions with overhanging edges in chronic gout; exclude other pathology',
        availability: 'BASIC'
      },
      {
        name: 'Musculoskeletal Ultrasound',
        indication: '🟡 IF AVAILABLE - Double contour sign (urate on cartilage); tophi detection; increasingly used',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Synovial Fluid Culture',
        indication: '🔴 ESSENTIAL if infection suspected - Gout and septic arthritis can coexist; always consider in monoarthritis',
        availability: 'BASIC'
      },
      {
        name: '24-hour Urine Uric Acid',
        indication: '🟡 IF INDICATED - Distinguish overproducers (>800mg/day) from underexcretors; guides choice of ULT',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'HLA-B*5801 Testing',
        indication: '🟡 IF AVAILABLE - Consider before allopurinol in high-risk populations (Han Chinese, Thai, Korean); severe hypersensitivity risk',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Joint aspiration if possible; clinical criteria if not; consider septic arthritis',
        '⚡ TREAT ACUTE FLARE PROMPTLY: Start anti-inflammatory within 24 hours; earlier is better',
        '⚡ CHOOSE ANTI-INFLAMMATORY: NSAIDs (if no contraindications), colchicine, or corticosteroids',
        '⚡ DO NOT START ULT DURING FLARE: Wait until flare resolved (2+ weeks); may worsen flare',
        '⚡ IF ALREADY ON ULT, CONTINUE: Do not stop allopurinol during acute flare',
        '⚡ REST AND ICE: Elevate affected joint; apply ice packs',
        '⚡ ASSESS COMORBIDITIES: Check renal function, cardiovascular risk factors, medications',
        '⚡ REVIEW MEDICATIONS: Identify drugs that raise urate (thiazides, loop diuretics, low-dose aspirin)',
        '⚡ PATIENT EDUCATION: Explain disease, dietary factors, importance of long-term treatment',
        '⚡ PLAN LONG-TERM: Schedule follow-up to discuss ULT after flare resolves'
      ],
      
      pharmacological: [
        {
          drug: 'Naproxen (NSAID)',
          indication: '🔴 FIRST-LINE for ACUTE FLARE - Effective, affordable; start at full dose; continue until flare resolved',
          dose: 'Adults: 500mg PO immediately, then 250mg every 8 hours OR 500mg twice daily. Continue for 5-7 days or until flare resolved',
          route: 'PO',
          duration: '5-7 days (until flare resolved)',
          contraindications: ['Active peptic ulcer', 'GI bleeding', 'Severe renal impairment (CKD stage 4-5)', 'Heart failure', 'Aspirin-sensitive asthma', 'Anticoagulant therapy'],
          sideEffects: ['GI upset', 'GI bleeding', 'Renal impairment', 'Fluid retention', 'Hypertension'],
          alternatives: ['Indomethacin 50mg TDS', 'Diclofenac 50mg TDS', 'Etoricoxib 120mg daily']
        },
        {
          drug: 'Indomethacin (NSAID)',
          indication: '🔴 FIRST-LINE for ACUTE FLARE - Traditionally used for gout; very effective; more GI side effects',
          dose: 'Adults: 50mg PO three times daily for 2-3 days, then reduce to 25mg TDS until resolved',
          route: 'PO',
          duration: '5-7 days',
          contraindications: ['Same as naproxen; also avoid in depression, epilepsy, parkinsonism'],
          sideEffects: ['GI upset (higher than other NSAIDs)', 'Headache', 'Dizziness', 'CNS effects'],
          alternatives: ['Naproxen', 'Other NSAIDs']
        },
        {
          drug: 'Colchicine (Acute Flare)',
          indication: '🔴 FIRST-LINE for ACUTE FLARE - Especially if NSAIDs contraindicated; LOW-DOSE regimen is standard',
          dose: 'LOW-DOSE (preferred): 1mg (or 1.2mg) initially, then 0.5mg (or 0.6mg) 1 hour later. Max 1.5mg (or 1.8mg) in first 24 hours. Can continue 0.5mg twice daily until resolved. HIGH-DOSE regimen is obsolete.',
          route: 'PO',
          duration: 'Until flare resolved (usually 3-7 days)',
          contraindications: ['Severe renal impairment (reduce dose)', 'Severe hepatic impairment', 'Concurrent strong CYP3A4 or P-gp inhibitors'],
          sideEffects: ['Diarrhea (dose-limiting)', 'Nausea', 'Vomiting', 'Bone marrow suppression (rare at low dose)', 'Myopathy', 'Neuropathy'],
          alternatives: ['NSAIDs', 'Corticosteroids']
        },
        {
          drug: 'Prednisolone (Corticosteroid)',
          indication: '🔴 FIRST-LINE if NSAIDs/colchicine contraindicated - Effective; useful in CKD, polyarticular gout',
          dose: 'Adults: 30-40mg PO once daily for 5 days, then stop (or rapid taper). OR 30mg for 3 days, 20mg for 3 days, 10mg for 3 days',
          route: 'PO',
          duration: '5-10 days total',
          contraindications: ['Active infection (relative)', 'Uncontrolled diabetes (caution)', 'Psychosis'],
          sideEffects: ['Hyperglycemia', 'Fluid retention', 'Insomnia', 'Mood changes', 'GI upset'],
          alternatives: ['Intra-articular injection', 'IM triamcinolone']
        },
        {
          drug: 'Intra-articular Corticosteroid (Triamcinolone)',
          indication: '🟠 ALTERNATIVE - For monoarticular gout; rapid relief; avoids systemic effects',
          dose: 'Large joint (knee): 40mg. Medium joint (ankle, wrist): 20-30mg. Small joint (MTP): 10mg. Single injection',
          route: 'Intra-articular',
          duration: 'Single injection; may repeat if needed',
          contraindications: ['Septic arthritis (must be excluded)', 'Overlying skin infection', 'Prosthetic joint (caution)'],
          sideEffects: ['Post-injection flare', 'Infection (rare)', 'Local skin changes'],
          alternatives: ['Oral prednisolone', 'IM methylprednisolone']
        },
        {
          drug: 'Allopurinol',
          indication: '🔴 FIRST-LINE ULT - Xanthine oxidase inhibitor; reduces urate production; start AFTER flare resolved',
          dose: 'Start 100mg PO daily (50mg if CKD stage 3-4). Increase by 100mg every 2-4 weeks to reach target urate <6 mg/dL. Max: 800mg/day (300mg if CKD)',
          route: 'PO',
          duration: 'LIFELONG once started',
          contraindications: ['Previous severe allopurinol hypersensitivity', 'HLA-B*5801 positive (relative)'],
          sideEffects: ['Rash', 'GI upset', 'Allopurinol hypersensitivity syndrome (rare but serious: fever, rash, eosinophilia, hepatitis, renal failure)', 'Gout flare (when initiating)'],
          alternatives: ['Febuxostat']
        },
        {
          drug: 'Febuxostat',
          indication: '🟠 ALTERNATIVE ULT - Non-purine xanthine oxidase inhibitor; use if allopurinol intolerant/contraindicated',
          dose: 'Adults: Start 40mg PO once daily. May increase to 80mg if target urate not achieved after 2-4 weeks',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['Azathioprine or mercaptopurine use', 'Possible increased CV risk (use with caution in CVD)'],
          sideEffects: ['Gout flares (when initiating)', 'Liver function abnormalities', 'Nausea', 'Rash', 'Cardiovascular events (controversial)'],
          alternatives: ['Allopurinol', 'Probenecid']
        },
        {
          drug: 'Probenecid',
          indication: '🟡 SECOND-LINE ULT - Uricosuric; increases renal urate excretion; requires good renal function',
          dose: 'Adults: Start 250mg PO twice daily for 1 week, then 500mg twice daily. May increase to 1g twice daily. Ensure adequate hydration',
          route: 'PO',
          duration: 'Lifelong',
          contraindications: ['CKD (eGFR <50)', 'History of nephrolithiasis', 'Uric acid overproduction (>800mg/24h urine)'],
          sideEffects: ['GI upset', 'Nephrolithiasis', 'Rash', 'Gout flares'],
          alternatives: ['Allopurinol', 'Febuxostat']
        },
        {
          drug: 'Colchicine (Flare Prophylaxis)',
          indication: '🔴 ESSENTIAL when starting ULT - Prevents flares during ULT initiation; continue 3-6 months',
          dose: 'Adults: 0.5mg PO once or twice daily. Reduce dose in renal impairment. Continue for 3-6 months after reaching target urate',
          route: 'PO',
          duration: '3-6 months minimum after achieving target serum urate',
          contraindications: ['Severe renal impairment (reduce dose)', 'Strong CYP3A4 inhibitors'],
          sideEffects: ['Diarrhea', 'Nausea', 'Generally well tolerated at prophylactic doses'],
          alternatives: ['Low-dose NSAID (if colchicine contraindicated)', 'Low-dose prednisolone']
        },
        {
          drug: 'Losartan',
          indication: '🟡 ADJUNCT - Antihypertensive with mild uricosuric effect; consider in gout patients with hypertension',
          dose: 'Adults: 50-100mg PO once daily (for hypertension)',
          route: 'PO',
          duration: 'Long-term for hypertension',
          contraindications: ['Pregnancy', 'Bilateral renal artery stenosis', 'Hyperkalemia'],
          sideEffects: ['Dizziness', 'Hyperkalemia', 'Hypotension', 'Cough (less than ACE inhibitors)'],
          alternatives: ['Other ARBs do not have same uricosuric effect']
        },
        {
          drug: 'Fenofibrate',
          indication: '🟡 ADJUNCT - Lipid-lowering with uricosuric effect; consider in gout patients with dyslipidemia',
          dose: 'Adults: 145mg PO once daily (for dyslipidemia)',
          route: 'PO',
          duration: 'Long-term for lipid management',
          contraindications: ['Severe renal impairment', 'Hepatic impairment', 'Gallbladder disease'],
          sideEffects: ['GI upset', 'Myopathy (especially with statins)', 'Elevated LFTs'],
          alternatives: ['Statins (no uricosuric effect but indicated for CVD risk)']
        }
      ],
      
      nonPharmacological: [
        'DIETARY MODIFICATION: Reduce high-purine foods (red meat, organ meats, shellfish, sardines, anchovies)',
        'LIMIT ALCOHOL: Especially beer and spirits; wine has less risk; complete avoidance if frequent flares',
        'LIMIT FRUCTOSE: Reduce sugary drinks, fruit juices, high-fructose corn syrup',
        'HYDRATION: Adequate fluid intake (2-3L/day) to promote urate excretion and prevent stones',
        'WEIGHT LOSS: Gradual weight reduction if overweight; reduces urate and improves metabolic syndrome',
        'DAIRY: Low-fat dairy may help reduce urate; consider increasing intake',
        'CHERRIES: Some evidence for reducing flares; 10-12 cherries daily or cherry extract',
        'VITAMIN C: 500mg/day may modestly reduce urate (weak evidence)',
        'REST AND ICE: During acute flare; elevate affected joint; apply ice for 20 minutes several times daily',
        'CARDIOVASCULAR RISK: Address all modifiable risk factors (smoking, BP, lipids, diabetes)',
        'MEDICATION REVIEW: Work with other physicians to modify drugs that raise urate if possible (thiazides, loop diuretics)'
      ],
      
      surgical: [
        'Surgical debulking of large tophi: For mechanical impairment, ulceration, infection, or cosmesis; rarely needed with optimal medical management',
        'Joint replacement: For severe joint destruction from chronic tophaceous gout',
        'Drainage of infected tophus: If secondary bacterial infection develops',
        'Carpal tunnel release: If tophi cause nerve compression'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Suspected septic arthritis - Hot, swollen joint with fever; cannot exclude infection clinically; aspirate urgently',
        '🟠 URGENT: Diagnostic uncertainty - Atypical presentation; need for joint aspiration; crystal confirmation',
        '🟠 URGENT: Severe, polyarticular flare - Multiple joints affected; systemic features; may need hospitalization',
        '🟠 URGENT: Allopurinol hypersensitivity reaction - Fever, rash, eosinophilia; stop drug; urgent evaluation',
        '🟡 ROUTINE: Treatment failure - Not reaching urate target despite maximum allopurinol; consider febuxostat or combination',
        '🟡 ROUTINE: Recurrent flares despite ULT - May need dose optimization or additional therapy',
        '🟡 ROUTINE: Severe tophaceous gout - Large or problematic tophi; may need specialist management',
        '🟡 ROUTINE: Significant renal impairment - Drug dosing challenges; may benefit from specialist input',
        '🟡 ROUTINE: Multiple comorbidities - Complex patients with diabetes, CVD, CKD; multidisciplinary approach'
      ],
      
      monitoring: [
        'SERUM URATE: Check 2-4 weeks after dose change; target <6 mg/dL (<0.36 mmol/L); check every 6-12 months once stable',
        'FLARE FREQUENCY: Track number and severity of flares; should decrease with effective ULT',
        'TOPHUS SIZE: Monitor if present; should shrink/resolve with sustained target urate',
        'RENAL FUNCTION: Check before starting ULT; monitor periodically (gout associated with CKD)',
        'LIVER FUNCTION: Baseline and periodically with allopurinol or febuxostat',
        'BLOOD PRESSURE: Screen and manage; common comorbidity',
        'GLUCOSE/HbA1c: Screen for diabetes; metabolic syndrome association',
        'LIPIDS: Check and treat dyslipidemia; cardiovascular risk reduction',
        'ADVERSE EFFECTS: Monitor for allopurinol hypersensitivity (especially first 3 months), colchicine toxicity',
        'ADHERENCE: Discuss importance of lifelong ULT; common reason for treatment failure'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE FLARE:
• Review in 24-48 hours if not improving
• Ensure adequate anti-inflammatory dosing
• Exclude septic arthritis if worsening
• Plan for ULT discussion once flare resolved

INITIATING URATE-LOWERING THERAPY:

When to Start ULT:
• After first flare if: Tophi, CKD stage 3+, urate >9 mg/dL, urolithiasis
• After second flare in most patients
• Some guidelines: Consider after any confirmed gout attack

ULT Titration Phase:
• Start allopurinol 100mg daily (50mg if CKD)
• Check serum urate every 2-4 weeks
• Increase by 100mg increments until target <6 mg/dL reached
• Provide flare prophylaxis (colchicine 0.5mg daily or BD) for 3-6 months
• Educate that flares may occur initially (not a sign of treatment failure)

STABLE ON ULT:
• Review every 6-12 months
• Check serum urate (confirm at target)
• Ask about flares (should be rare once urate controlled)
• Monitor tophi (should be shrinking)
• Screen/manage comorbidities

TREATMENT TARGETS:
• Serum urate <6 mg/dL (<0.36 mmol/L) for all gout patients
• Serum urate <5 mg/dL (<0.30 mmol/L) for severe/tophaceous gout
• Resolution of tophi
• No or minimal flares

DURATION OF THERAPY:
• ULT is typically LIFELONG once started
• Discontinuation leads to recurrence in most patients
• In rare cases, may consider stopping after years with:
  - No flares for 5+ years
  - Urate consistently at target
  - No tophi
  - Patient prefers trial off medication

MANAGING COMMON SCENARIOS:

Flare While on ULT:
• Continue ULT (do NOT stop)
• Treat flare with NSAID, colchicine, or prednisolone
• Consider if urate at target; may need dose increase
• Extend prophylaxis duration

Not Reaching Urate Target:
• Ensure adherence
• Maximize allopurinol dose (can go to 800mg if tolerated and renal function allows)
• If still not at target: Switch to or add febuxostat, consider uricosuric
• Dietary review

Allopurinol Hypersensitivity Syndrome:
• Presents 2-6 weeks after starting
• Fever, rash, eosinophilia, hepatitis, renal failure
• Potentially fatal
• STOP allopurinol permanently
• Supportive care; may need steroids
• Never rechallenge with allopurinol
• Use febuxostat (some cross-reactivity but usually tolerated)

PATIENT EDUCATION POINTS:
• Gout is a lifelong condition but very controllable
• ULT dramatically reduces flares and prevents joint damage
• Initial flares when starting ULT are expected and not a reason to stop
• Dietary changes help but rarely sufficient alone
• Adherence is critical - stopping medication leads to return of symptoms
• Address comorbidities (they share risk factors and worsen outcomes)`
    },

    redFlags: [
      '🔴 CRITICAL: Hot, swollen joint with high fever and systemic illness - Septic arthritis until proven otherwise; aspirate immediately',
      '🔴 CRITICAL: Allopurinol hypersensitivity syndrome (fever, rash, eosinophilia, organ involvement) - Stop drug; potentially fatal; urgent care',
      '🔴 CRITICAL: Polyarticular gout with systemic features in immunocompromised patient - May have disseminated infection; thorough evaluation',
      '🟠 URGENT: Joint aspiration shows both crystals AND bacteria - Gout and septic arthritis can coexist; treat both',
      '🟠 URGENT: New-onset gout in young patient without risk factors - Consider secondary causes (myeloproliferative disorder, lead exposure)',
      '🟠 URGENT: Severe kidney stones in gout patient - May need nephrology input; affects ULT choice',
      '🟡 CAUTION: Gout flare not responding to treatment after 3-5 days - Reassess diagnosis; consider aspiration',
      '🟡 CAUTION: Colchicine toxicity (severe diarrhea, myopathy, cytopenias) - Reduce or stop; check renal function and drug interactions',
      '🟡 CAUTION: Ulcerated or infected tophus - May need drainage, antibiotics, surgical debridement'
    ],

    complications: [
      'Chronic tophaceous gout - Urate deposits causing deformity, joint destruction, disability',
      'Erosive arthropathy - Joint damage visible on X-ray; irreversible',
      'Uric acid nephrolithiasis - Kidney stones; present in 10-25% of gout patients',
      'Chronic kidney disease - Both a cause and consequence of gout',
      'Cardiovascular disease - Gout associated with increased MI, stroke, heart failure risk',
      'Metabolic syndrome - Obesity, diabetes, hypertension, dyslipidemia often coexist',
      'Allopurinol hypersensitivity syndrome - Rare but potentially fatal; fever, rash, organ failure',
      'Medication side effects - GI bleeding from NSAIDs, diarrhea from colchicine',
      'Functional impairment - Difficulty walking, working, performing daily activities',
      'Quality of life impact - Pain, disability, depression, work loss',
      'Secondary infection of tophi - Requires antibiotics and possibly drainage'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

OVERALL OUTLOOK:
• Gout is highly treatable with excellent outcomes if managed properly
• With effective ULT, most patients become flare-free
• Tophi can completely resolve with sustained urate lowering
• Joint damage can be prevented if treated before chronic changes

NATURAL HISTORY (UNTREATED):
• First attack resolves in 7-14 days even without treatment
• >60% have second attack within 1 year
• Flares become more frequent over time
• Intercritical periods shorten
• Eventual progression to chronic tophaceous gout
• Joint destruction and disability

OUTCOMES WITH TREATMENT:
• 90% achieve target urate with appropriate ULT
• Flare frequency decreases dramatically (often to zero)
• Tophi resolve (may take months to years)
• Quality of life improves significantly
• Joint damage prevented or halted

FACTORS AFFECTING PROGNOSIS:

Better Outcomes:
• Early diagnosis and treatment
• Good adherence to ULT
• Reaching and maintaining urate target <6 mg/dL
• Lifestyle modifications
• Management of comorbidities
• Absence of tophi at diagnosis

Worse Outcomes:
• Delayed treatment
• Poor adherence
• Tophaceous gout at presentation
• Renal impairment
• Multiple comorbidities
• Continued high alcohol or purine intake
• Unable to tolerate ULT

MORTALITY:
• Gout itself rarely directly fatal
• Associated with increased cardiovascular mortality (shared risk factors)
• Gout patients have 25-45% increased mortality risk vs general population
• Optimal treatment of gout and comorbidities may reduce this risk
• Allopurinol hypersensitivity syndrome: 20-25% mortality if severe`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing First Gout Attack):
• Maintain healthy weight
• Limit purine-rich foods (red meat, organ meats, shellfish)
• Limit alcohol, especially beer
• Avoid high-fructose beverages
• Stay well hydrated
• Consider avoiding or minimizing medications that raise urate (thiazides, loop diuretics) when alternatives exist
• Manage metabolic syndrome components

SECONDARY PREVENTION (Preventing Recurrent Flares):

Urate-Lowering Therapy:
• First-line approach for most patients after diagnosis
• Target serum urate <6 mg/dL
• Allopurinol first-line; start low, go slow
• Lifelong therapy for most

Flare Prophylaxis When Starting ULT:
• Low-dose colchicine (0.5mg once or twice daily) OR
• Low-dose NSAID for 3-6 months
• Prevents flares during urate-lowering phase

Lifestyle Modifications:
• Dietary changes (as above)
• Weight loss
• Limit alcohol
• Adequate hydration

TERTIARY PREVENTION (Preventing Complications):
• Achieve and maintain target urate
• Monitor and treat tophi
• Screen for and manage CKD
• Address cardiovascular risk factors
• Regular monitoring

PREVENTING ALLOPURINOL HYPERSENSITIVITY:
• Start with low dose (100mg, or 50mg if CKD)
• Titrate slowly
• Consider HLA-B*5801 testing in high-risk populations (Han Chinese, Thai, Korean)
• Educate patients on early warning signs (rash, fever)

SPECIAL POPULATIONS:
• Asymptomatic hyperuricemia: Usually does NOT need treatment; lifestyle measures only
• Post-transplant: Consider interaction with immunosuppressants
• Chronic kidney disease: Dose adjustment; may benefit from early ULT

PUBLIC HEALTH CONSIDERATIONS FOR AFRICA:
• Education on gout as a medical condition (not just dietary)
• Awareness of rising prevalence with urbanization
• Ensuring access to affordable ULT (allopurinol is low-cost)
• Training healthcare workers in gout diagnosis and management
• Integration with cardiovascular disease and diabetes programs`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS GOUT?
Gout is a type of arthritis caused by too much uric acid in your blood. Uric acid is a normal waste product, but when levels are too high, it forms crystals that deposit in your joints. These crystals cause sudden, severe attacks of pain, swelling, and redness - usually in the big toe, but can affect any joint.

WHY DID I GET GOUT?
• Your body either makes too much uric acid or cannot get rid of it fast enough
• It runs in families
• Risk factors include: Being overweight, eating lots of red meat or shellfish, drinking alcohol (especially beer), some medications, kidney problems

THE GOOD NEWS:
• Gout is very treatable
• With proper medication, you can prevent future attacks
• Damage to your joints can be prevented
• You can lead a normal, active life

THERE ARE TWO PARTS TO TREATMENT:

1. TREATING ATTACKS:
• Take anti-inflammatory medicine as soon as an attack starts
• The sooner you treat it, the faster it gets better
• Rest the joint, apply ice, elevate if possible
• An attack will go away in 1-2 weeks even without treatment, but medication makes it much faster

2. PREVENTING FUTURE ATTACKS (Long-term Medication):
• A daily medicine called allopurinol lowers uric acid
• This is the most important part of treatment
• It prevents attacks and protects your joints
• You need to take it every day for life (like blood pressure medication)
• When you first start, you might still get attacks - this is NORMAL and does not mean the medicine is not working

IMPORTANT ABOUT YOUR MEDICINE:
• Start allopurinol at a low dose; your doctor will increase it gradually
• Your doctor will give you extra medicine for the first few months to prevent attacks while starting
• NEVER stop allopurinol during an attack - this can make things worse
• Take it every day, even when you feel fine

FOODS TO LIMIT:
• Red meat (beef, lamb, pork)
• Organ meats (liver, kidney)
• Shellfish (shrimp, crab, lobster, mussels)
• Beer (worst for gout) and spirits
• Sugary drinks and foods with high-fructose corn syrup

FOODS THAT MAY HELP:
• Low-fat dairy products (milk, yogurt)
• Vegetables (all are fine, including previously restricted ones like spinach)
• Cherries (some evidence they reduce attacks)
• Coffee (may lower uric acid)
• Plenty of water (at least 8 glasses daily)

LIFESTYLE TIPS:
• Lose weight gradually if overweight (crash dieting can trigger attacks)
• Stay well hydrated
• Limit alcohol, especially beer
• Exercise regularly
• Do not take aspirin or ibuprofen regularly without asking your doctor

WARNING - WHEN TO SEEK IMMEDIATE HELP:
• Fever with a hot, swollen joint (could be infection)
• Attack not improving after 3 days of treatment
• Skin rash, especially if you recently started allopurinol
• Multiple joints affected at once

REGULAR CHECK-UPS:
• Blood tests to check your uric acid level (target is below 6)
• Check kidney function and other health markers
• Discuss any attacks or side effects

COMMON QUESTIONS:

Q: Is gout my fault? Did I cause it by eating wrong?
A: No. While diet plays a role, gout is mainly determined by your genetics and how your body handles uric acid. Anyone can get gout.

Q: Can I stop medication once I feel better?
A: No. Gout is a lifelong condition. Stopping medication leads to uric acid building up again and attacks returning. Think of it like blood pressure medication.

Q: Why did I get an attack after starting allopurinol?
A: This is normal and expected. When uric acid levels change rapidly, it can trigger attacks. Your doctor will give you preventive medicine for the first few months. This does NOT mean the allopurinol is not working.

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Gout is a painful condition, but it is completely controllable. Take your daily medication as prescribed, make healthy lifestyle choices, and keep your regular appointments. With proper treatment, you can live without gout attacks and protect your joints for the future.`,

    references: [
      'FitzGerald JD, Dalbeth N, Mikuls T, et al. 2020 American College of Rheumatology Guideline for the Management of Gout. Arthritis Care Res (Hoboken). 2020;72(6):744-760.',
      'Richette P, Doherty M, Pascual E, et al. 2016 updated EULAR evidence-based recommendations for the management of gout. Ann Rheum Dis. 2017;76(1):29-42.',
      'Neogi T, Jansen TL, Dalbeth N, et al. 2015 Gout classification criteria: an American College of Rheumatology/European League Against Rheumatism collaborative initiative. Ann Rheum Dis. 2015;74(10):1789-1798.',
      'Dalbeth N, Merriman TR, Stamp LK. Gout. Lancet. 2016;388(10055):2039-2052.',
      'Khanna D, Fitzgerald JD, Khanna PP, et al. 2012 American College of Rheumatology guidelines for management of gout. Part 1: systematic nonpharmacologic and pharmacologic therapeutic approaches to hyperuricemia. Arthritis Care Res (Hoboken). 2012;64(10):1431-1446.',
      'Khanna D, Khanna PP, Fitzgerald JD, et al. 2012 American College of Rheumatology guidelines for management of gout. Part 2: therapy and antiinflammatory prophylaxis of acute gouty arthritis. Arthritis Care Res (Hoboken). 2012;64(10):1447-1461.',
      'Stamp LK, Chapman PT, Barclay ML, et al. A randomised controlled trial of the efficacy and safety of allopurinol dose escalation to achieve target serum urate in people with gout. Ann Rheum Dis. 2017;76(9):1522-1528.',
      'Terkeltaub RA, Furst DE, Bennett K, et al. High versus low dosing of oral colchicine for early acute gout flare: Twenty-four-hour outcome of the first multicenter, randomized, double-blind, placebo-controlled, parallel-group, dose-comparison colchicine study. Arthritis Rheum. 2010;62(4):1060-1068.',
      'Stamp LK, Taylor WJ, Jones PB, et al. Starting dose is a risk factor for allopurinol hypersensitivity syndrome: a proposed safe starting dose of allopurinol. Arthritis Rheum. 2012;64(8):2529-2536.',
      'Kuo CF, Grainge MJ, Mallen C, et al. Rising burden of gout in the UK but continuing suboptimal management: a nationwide population study. Ann Rheum Dis. 2015;74(4):661-667.',
      'Singh JA, Gaffo A. Gout epidemiology and comorbidities. Semin Arthritis Rheum. 2020;50(3S):S11-S16.',
      'Choi HK, Atkinson K, Karlson EW, et al. Purine-rich foods, dairy and protein intake, and the risk of gout in men. N Engl J Med. 2004;350(11):1093-1103.',
      'Choi HK, Curhan G. Beer, liquor, and wine consumption and serum uric acid level: the Third National Health and Nutrition Examination Survey. Arthritis Rheum. 2004;51(6):1023-1029.',
      'Beyl RN, Hughes L, Morgan S. Update on Importance of Diet in Gout. Am J Med. 2016;129(11):1153-1158.',
      'Keenan RT, Pillinger MH. Febuxostat: a new agent for lowering serum urate. Drugs Today (Barc). 2009;45(4):247-260.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 81. OSTEOARTHRITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '81',
    name: 'Osteoarthritis',
    slug: 'osteoarthritis',
    specialty: 'Internal Medicine',
    bodySystem: 'Musculoskeletal',
    icdCode: 'M15-M19',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Osteoarthritis (OA) is the most common form of arthritis and the leading cause of chronic disability in older adults. It is a degenerative joint disease characterized by progressive loss of articular cartilage, subchondral bone changes, osteophyte formation, and synovial inflammation. OA affects over 500 million people globally and its prevalence is increasing with aging populations and rising obesity rates.

The pathophysiology involves a complex interplay of mechanical, biological, and biochemical factors. While traditionally viewed as "wear and tear," OA is now understood as an active disease process involving the entire joint. Cartilage degradation results from an imbalance between anabolic and catabolic processes, with increased activity of matrix metalloproteinases (MMPs) and inflammatory cytokines. Subchondral bone remodeling, synovitis, and changes in periarticular structures (ligaments, muscles) all contribute to symptoms and progression.

The most commonly affected joints are the knees, hips, hands (DIPs, PIPs, first CMC), and spine. Risk factors include age, obesity, joint injury, repetitive occupational stress, genetic predisposition, and female sex. OA causes pain, stiffness, reduced function, and impaired quality of life. In advanced cases, it leads to significant disability.

In Africa, OA prevalence is lower than in Western populations but is increasing with urbanization, aging populations, and rising obesity. Knee OA is the most common form. Access to joint replacement surgery is extremely limited, making conservative management crucial. Many patients present late with advanced disease and significant disability.

Management is multimodal, combining patient education, exercise, weight management, pharmacotherapy (primarily analgesics), and surgical intervention for refractory cases. No disease-modifying therapies are currently available.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Exercise is FIRST-LINE treatment: Land-based or aquatic exercise improves pain and function; recommend to ALL patients

🔴 CRITICAL POINT 2 - Weight loss for overweight patients: Even 5-10% weight loss significantly reduces knee pain and improves function

🔴 CRITICAL POINT 3 - Paracetamol is NOT first-line: NSAIDs (topical first, then oral) are more effective; paracetamol has limited efficacy

🔴 CRITICAL POINT 4 - OA is a CLINICAL diagnosis: X-rays often do not correlate with symptoms; treat the patient, not the X-ray

🟠 CRITICAL POINT 5 - Topical NSAIDs first for knee and hand OA: Effective with fewer systemic side effects; use before oral NSAIDs

🟠 CRITICAL POINT 6 - Oral NSAIDs: Use lowest effective dose for shortest duration; consider GI and CV risks

🟠 CRITICAL POINT 7 - Intra-articular corticosteroids: Provide short-term relief (weeks to months); limit frequency due to potential cartilage effects

🟡 CRITICAL POINT 8 - Glucosamine/chondroitin: Evidence is weak; may provide modest benefit in some patients; not recommended by most guidelines

🟡 CRITICAL POINT 9 - Joint replacement: Highly effective for end-stage OA when conservative management fails; limited access in Africa`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Most common joint disease: Affects over 500 million people worldwide
• Prevalence increasing with aging and obesity
• Leading cause of disability in older adults
• Affects 10-15% of adults over 60 years
• Knee OA most common, followed by hip and hand
• Women more affected than men, especially after menopause
• Significant economic burden (healthcare costs, lost productivity)

RISK FACTORS:

NON-MODIFIABLE:
• Age (strongest risk factor; prevalence increases sharply after 50)
• Female sex (1.5-2x higher risk, especially postmenopausal)
• Genetics (heritability 40-65% for hip and hand OA)
• Race/ethnicity (variations in prevalence and affected joints)

MODIFIABLE:
• Obesity (major risk factor for knee OA; BMI >30 doubles risk)
• Joint injury (ACL tear, meniscal injury, fractures)
• Occupational factors (repetitive kneeling, squatting, heavy lifting)
• Sports injuries (high-impact, pivoting sports)
• Muscle weakness (especially quadriceps for knee OA)
• Joint malalignment (varus/valgus deformity)

AFRICAN EPIDEMIOLOGY:
• Prevalence estimates: 5-15% of adults (lower than Western countries)
• Prevalence increasing with urbanization and lifestyle changes
• Knee OA most common form
• Hip OA less common than in Western populations
• Rising obesity rates contributing to increasing burden
• Limited access to joint replacement surgery
• Many patients present with advanced disease
• Traditional healers often first point of contact
• Limited data on disease burden due to lack of epidemiological studies

JOINT-SPECIFIC PATTERNS:
• Knee: Most common; medial compartment predominantly affected
• Hip: Second most common large joint; more common in Western populations
• Hand: DIPs (Heberden nodes), PIPs (Bouchard nodes), first CMC (base of thumb)
• Spine: Cervical and lumbar most affected; can cause radiculopathy
• Foot/ankle: First MTP common
• Shoulder: Less common as primary OA`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

CARDINAL SYMPTOMS:

PAIN:
• Activity-related pain (worse with use, better with rest)
• Initially intermittent, becomes constant in advanced disease
• May have "start-up" pain (stiffness after rest that improves with movement)
• Night pain suggests more severe disease
• Location correlates with affected joint

STIFFNESS:
• Morning stiffness <30 minutes (vs >1 hour in inflammatory arthritis)
• Gelling phenomenon (stiffness after periods of inactivity)
• Improves with movement

FUNCTIONAL LIMITATION:
• Difficulty with activities of daily living
• Knee OA: Difficulty climbing stairs, rising from chair, walking
• Hip OA: Difficulty putting on shoes/socks, getting in/out of car
• Hand OA: Difficulty gripping, opening jars, fine motor tasks

PHYSICAL EXAMINATION:

INSPECTION:
• Joint swelling (bony enlargement, sometimes effusion)
• Heberden nodes (DIP joints) - bony swelling at DIP
• Bouchard nodes (PIP joints) - bony swelling at PIP
• Squaring of thumb base (first CMC OA)
• Muscle wasting (quadriceps in knee OA)
• Joint deformity (varus/valgus, flexion contracture)
• Gait abnormality (antalgic gait, Trendelenburg)

PALPATION:
• Bony tenderness at joint margins
• Crepitus (grating sensation with movement)
• Cool joint (vs warm in inflammatory arthritis)
• Joint line tenderness
• Osteophytes palpable
• Effusion (bulge sign, patellar tap in knee)

RANGE OF MOTION:
• Reduced range of motion
• Pain at end range
• Flexion contracture (inability to fully extend)

JOINT-SPECIFIC FINDINGS:

KNEE OA:
• Varus (bow-legged) or valgus (knock-kneed) deformity
• Crepitus with movement
• Effusion (cool, non-inflammatory)
• Quadriceps wasting
• Patellofemoral grinding
• Tender joint line

HIP OA:
• Groin pain (referred to anterior thigh/knee)
• Limited internal rotation (earliest and most sensitive)
• Limited flexion
• Trendelenburg gait (abductor weakness)
• Leg length discrepancy

HAND OA:
• Heberden nodes (DIP)
• Bouchard nodes (PIP)
• First CMC squaring, tenderness, crepitus
• Grip weakness
• Usually spares MCPs (MCP involvement suggests RA)

SPINE OA:
• Localized back or neck pain
• Stiffness
• May have radicular symptoms if nerve compression
• Reduced range of motion`,

    diagnosis: `DIAGNOSTIC APPROACH:

OA IS PRIMARILY A CLINICAL DIAGNOSIS

ACR CLINICAL CRITERIA FOR KNEE OA:
Knee pain plus at least 3 of:
• Age >50 years
• Morning stiffness <30 minutes
• Crepitus on active motion
• Bony tenderness
• Bony enlargement
• No palpable warmth

Sensitivity 95%, Specificity 69%

ACR CLINICAL CRITERIA FOR HIP OA:
Hip pain plus at least 2 of:
• ESR <20 mm/hr
• Femoral and/or acetabular osteophytes on X-ray
• Joint space narrowing on X-ray

Or hip pain plus:
• Hip internal rotation <15 degrees
• Morning stiffness ≤60 minutes
• Age >50 years
• Pain on internal rotation

ACR CLINICAL CRITERIA FOR HAND OA:
Hand pain, aching, or stiffness plus at least 3 of:
• Hard tissue enlargement of 2+ of 10 selected joints
• Hard tissue enlargement of 2+ DIP joints
• Fewer than 3 swollen MCP joints
• Deformity of at least 1 of 10 selected joints

IMAGING:

PLAIN X-RAY:
Classic features (often do NOT correlate with symptoms):
• Joint space narrowing (cartilage loss)
• Osteophytes (bone spurs)
• Subchondral sclerosis (increased bone density)
• Subchondral cysts

Kellgren-Lawrence Grading:
• Grade 0: No features
• Grade 1: Doubtful narrowing, possible osteophytes
• Grade 2: Definite osteophytes, possible narrowing
• Grade 3: Moderate osteophytes, definite narrowing, some sclerosis
• Grade 4: Large osteophytes, marked narrowing, severe sclerosis, deformity

IMPORTANT: X-ray severity often does NOT correlate with symptoms. Many patients with severe X-ray changes have minimal symptoms, and vice versa. Treat the patient, not the X-ray.

MRI:
• Not routinely needed for diagnosis
• Shows cartilage damage, bone marrow lesions, meniscal tears, synovitis
• Useful if atypical presentation or considering surgery

ULTRASOUND:
• Can detect effusion, osteophytes, synovitis
• Increasingly used but not essential for diagnosis

LABORATORY TESTS:
• NOT needed for typical OA diagnosis
• Normal inflammatory markers (ESR, CRP)
• No specific blood tests for OA
• Use to exclude other conditions (RF, anti-CCP for RA; uric acid for gout)

SYNOVIAL FLUID (if aspirated):
• Non-inflammatory: WBC <2000/mm3, predominantly mononuclear
• Clear, viscous fluid
• No crystals (rules out gout, pseudogout)`,

    differentialDiagnosis: [
      'Rheumatoid arthritis - Symmetric polyarthritis, morning stiffness >1 hour, MCPs/wrists affected, RF/anti-CCP positive',
      'Gout - Acute, severe, monoarticular attacks; MSU crystals; elevated uric acid',
      'Pseudogout (CPPD) - Acute attacks; chondrocalcinosis on X-ray; CPP crystals',
      'Psoriatic arthritis - Psoriasis, nail changes, dactylitis, enthesitis; DIP involvement',
      'Septic arthritis - Hot, swollen joint; fever; very painful; elevated WBC/CRP; positive culture',
      'Bursitis - Localized tenderness over bursa; no joint line tenderness',
      'Tendinopathy - Pain with specific movements; tenderness over tendon',
      'Meniscal tear - Mechanical symptoms (locking, catching); joint line tenderness; positive McMurray',
      'Ligament injury - History of trauma; instability; positive stress tests',
      'Referred pain - Hip pathology can cause knee pain; spine pathology can cause hip/leg pain',
      'Avascular necrosis - Risk factors (steroids, alcohol, SLE); MRI diagnostic',
      'Inflammatory OA (erosive OA) - Hand OA with inflammatory flares; erosions on X-ray'
    ],

    investigations: [
      {
        name: 'Clinical Examination',
        indication: '🔴 ESSENTIAL - OA is a clinical diagnosis; history and exam usually sufficient',
        availability: 'BASIC'
      },
      {
        name: 'Plain X-ray of Affected Joint',
        indication: '🟠 IMPORTANT - Confirms diagnosis; shows joint space narrowing, osteophytes, sclerosis; severity often does NOT correlate with symptoms',
        availability: 'BASIC'
      },
      {
        name: 'Weight-bearing X-rays for Knee',
        indication: '🟠 IMPORTANT - More accurate for joint space narrowing; AP weight-bearing, lateral, skyline views',
        availability: 'BASIC'
      },
      {
        name: 'ESR/CRP',
        indication: '🟡 IF INDICATED - Should be normal in OA; elevated suggests inflammatory arthritis or infection',
        availability: 'BASIC'
      },
      {
        name: 'Rheumatoid Factor, Anti-CCP',
        indication: '🟡 IF INDICATED - To exclude RA if clinical doubt; negative in OA',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum Uric Acid',
        indication: '🟡 IF INDICATED - To exclude gout if acute presentation',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 IF INDICATED - Usually normal; baseline before NSAIDs',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function',
        indication: '🟠 IMPORTANT - Check before starting NSAIDs; monitor if on long-term NSAIDs',
        availability: 'BASIC'
      },
      {
        name: 'Joint Aspiration with Synovial Fluid Analysis',
        indication: '🟡 IF INDICATED - If effusion present and diagnosis uncertain; rules out gout, infection',
        availability: 'BASIC'
      },
      {
        name: 'MRI',
        indication: '🟡 IF INDICATED - Atypical presentation; pre-surgical planning; suspected AVN or meniscal tear',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Clinical assessment; X-ray if needed; exclude inflammatory arthritis',
        '⚡ PATIENT EDUCATION: Explain OA is manageable; not inevitable deterioration; importance of self-management',
        '⚡ EXERCISE PRESCRIPTION: Land-based or aquatic exercise for ALL patients - this is first-line treatment',
        '⚡ WEIGHT ASSESSMENT: Calculate BMI; discuss weight loss if overweight (target 5-10% loss)',
        '⚡ PAIN MANAGEMENT: Start with topical NSAIDs for knee/hand; progress to oral if needed',
        '⚡ ASSESS FUNCTION: Impact on daily activities, work, quality of life',
        '⚡ IDENTIFY MODIFIABLE FACTORS: Occupational issues, footwear, walking aids',
        '⚡ COMORBIDITY SCREEN: Cardiovascular, GI, renal risk before NSAIDs',
        '⚡ SET EXPECTATIONS: OA is chronic; goal is symptom control and function, not cure',
        '⚡ PROVIDE RESOURCES: Information leaflets, physiotherapy referral if available'
      ],
      
      pharmacological: [
        {
          drug: 'Topical Diclofenac (Gel/Cream)',
          indication: '🔴 FIRST-LINE for KNEE and HAND OA - Effective with minimal systemic absorption; use before oral NSAIDs',
          dose: 'Apply 4g (approximately 4cm) to affected joint 4 times daily. Max: 16g/day per joint. Rub in gently',
          route: 'Topical',
          duration: 'Ongoing as needed; reassess periodically',
          contraindications: ['Aspirin-sensitive asthma', 'Open wounds or infected skin', 'Use with oral NSAIDs (caution)'],
          sideEffects: ['Local skin reactions (rash, itching)', 'Minimal systemic absorption', 'Photosensitivity'],
          alternatives: ['Topical ibuprofen', 'Topical ketoprofen']
        },
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🟠 ADJUNCT - Limited efficacy in OA; may be used in combination or if NSAIDs contraindicated; NOT first-line',
          dose: 'Adults: 500mg-1g PO every 4-6 hours as needed. Max: 4g/day (3g/day in elderly or liver disease)',
          route: 'PO',
          duration: 'As needed; avoid long-term high doses',
          contraindications: ['Severe hepatic impairment', 'Alcohol excess'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Generally well tolerated at recommended doses'],
          alternatives: ['Topical or oral NSAIDs (more effective)']
        },
        {
          drug: 'Ibuprofen',
          indication: '🔴 FIRST-LINE ORAL NSAID - When topical insufficient; use lowest effective dose for shortest duration',
          dose: 'Adults: 200-400mg PO three times daily with food. Max: 1200mg/day OTC; 2400mg/day prescription',
          route: 'PO',
          duration: 'Shortest duration possible; reassess regularly',
          contraindications: ['Active peptic ulcer/GI bleeding', 'Severe heart failure', 'Severe renal impairment', 'Third trimester pregnancy', 'Aspirin-sensitive asthma'],
          sideEffects: ['GI upset, ulceration, bleeding', 'Renal impairment', 'Cardiovascular events (long-term)', 'Hypertension'],
          alternatives: ['Naproxen', 'Diclofenac', 'Celecoxib']
        },
        {
          drug: 'Naproxen',
          indication: '🔴 FIRST-LINE ORAL NSAID - Possibly lower CV risk than other NSAIDs; good for sustained effect',
          dose: 'Adults: 250-500mg PO twice daily with food. Max: 1250mg/day initially, then 1000mg/day',
          route: 'PO',
          duration: 'Shortest duration possible',
          contraindications: ['Same as ibuprofen'],
          sideEffects: ['Same as ibuprofen; possibly lower CV risk'],
          alternatives: ['Ibuprofen', 'Diclofenac']
        },
        {
          drug: 'Diclofenac (Oral)',
          indication: '🟠 ALTERNATIVE ORAL NSAID - Effective but higher CV risk; use with caution',
          dose: 'Adults: 50mg PO 2-3 times daily OR 75mg twice daily with food. Max: 150mg/day',
          route: 'PO',
          duration: 'Shortest duration possible; avoid in CVD',
          contraindications: ['Same as ibuprofen; avoid in established cardiovascular disease'],
          sideEffects: ['Same as ibuprofen; higher CV risk'],
          alternatives: ['Naproxen (lower CV risk)', 'Ibuprofen']
        },
        {
          drug: 'Celecoxib',
          indication: '🟠 COX-2 SELECTIVE - Lower GI risk than traditional NSAIDs; use if high GI risk',
          dose: 'Adults: 100-200mg PO once or twice daily. Max: 400mg/day',
          route: 'PO',
          duration: 'Shortest duration possible',
          contraindications: ['Sulfonamide allergy', 'Established CVD', 'Same general NSAID contraindications'],
          sideEffects: ['Lower GI toxicity', 'Same CV risk as traditional NSAIDs', 'Renal effects'],
          alternatives: ['Traditional NSAID plus PPI']
        },
        {
          drug: 'PPI (Omeprazole) with NSAIDs',
          indication: '🟠 GASTROPROTECTION - For patients on oral NSAIDs with GI risk factors',
          dose: 'Adults: Omeprazole 20mg PO once daily while on NSAID',
          route: 'PO',
          duration: 'While on NSAID therapy',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Generally well tolerated', 'Long-term: B12 deficiency, hypomagnesemia, bone loss'],
          alternatives: ['Misoprostol', 'H2 blocker (less effective)']
        },
        {
          drug: 'Tramadol',
          indication: '🟡 SECOND-LINE - Weak opioid; use if NSAIDs contraindicated or insufficient; limited evidence in OA',
          dose: 'Adults: Start 50mg PO 1-2 times daily. Increase gradually. Max: 400mg/day. Use immediate or extended release',
          route: 'PO',
          duration: 'Short-term; avoid long-term use if possible',
          contraindications: ['Seizure disorder', 'MAO inhibitors', 'Uncontrolled epilepsy', 'Severe respiratory depression'],
          sideEffects: ['Nausea', 'Dizziness', 'Constipation', 'Dependence risk', 'Seizures', 'Serotonin syndrome'],
          alternatives: ['Codeine (similar limitations)']
        },
        {
          drug: 'Duloxetine',
          indication: '🟡 ADJUNCT - SNRI; may help chronic OA pain, especially with central sensitization; consider if depression coexists',
          dose: 'Adults: Start 30mg PO once daily for 1-2 weeks, then 60mg once daily',
          route: 'PO',
          duration: 'Long-term if effective',
          contraindications: ['MAO inhibitors', 'Uncontrolled narrow-angle glaucoma', 'Severe renal impairment'],
          sideEffects: ['Nausea', 'Dry mouth', 'Constipation', 'Dizziness', 'Fatigue', 'Withdrawal if stopped abruptly'],
          alternatives: ['Amitriptyline (low dose)']
        },
        {
          drug: 'Intra-articular Corticosteroid (Triamcinolone/Methylprednisolone)',
          indication: '🟠 ADJUNCT - Short-term relief for flares; knee and hip OA; limit frequency',
          dose: 'Knee: Triamcinolone 40mg OR Methylprednisolone 40-80mg. Hip: Similar. Small joints: 10-20mg. No more than 3-4 injections per joint per year',
          route: 'Intra-articular injection',
          duration: 'Effect lasts weeks to months; do not repeat more than every 3 months',
          contraindications: ['Local or systemic infection', 'Joint prosthesis', 'Coagulopathy (relative)'],
          sideEffects: ['Post-injection flare', 'Infection (rare)', 'Skin atrophy', 'Cartilage damage with repeated injections', 'Transient hyperglycemia'],
          alternatives: ['Intra-articular hyaluronic acid (weak evidence)']
        },
        {
          drug: 'Capsaicin Cream',
          indication: '🟡 ADJUNCT - Topical for knee OA; depletes substance P; requires regular application',
          dose: 'Apply 0.025-0.075% cream to affected area 3-4 times daily. Takes 2-4 weeks for full effect',
          route: 'Topical',
          duration: 'Ongoing; must apply regularly',
          contraindications: ['Broken skin', 'Near eyes/mucous membranes'],
          sideEffects: ['Burning sensation (decreases with use)', 'Skin irritation'],
          alternatives: ['Topical NSAIDs']
        }
      ],
      
      nonPharmacological: [
        'EXERCISE (FIRST-LINE): Land-based exercise (walking, strengthening, tai chi) or aquatic exercise; 30 min most days; supervised initially if possible',
        'WEIGHT LOSS: Target 5-10% weight loss if overweight; major impact on knee OA symptoms; combines diet and exercise',
        'PHYSIOTHERAPY: Strengthening exercises (quadriceps for knee, hip abductors for hip), range of motion, balance training',
        'WALKING AIDS: Cane (use in hand opposite to affected leg), walker for stability if needed',
        'KNEE BRACES: Unloader braces for unicompartmental knee OA; patellar taping for patellofemoral OA',
        'APPROPRIATE FOOTWEAR: Shock-absorbing shoes; avoid high heels; consider lateral wedge insoles for medial knee OA',
        'HEAT/COLD: Heat for stiffness (morning); ice for acute pain/swelling',
        'ASSISTIVE DEVICES: Jar openers, raised toilet seats, grab rails, long-handled tools',
        'ACTIVITY MODIFICATION: Avoid prolonged standing, kneeling, squatting; pace activities; rest periods',
        'PATIENT EDUCATION: Self-management programs; understanding OA; staying active despite pain',
        'OCCUPATIONAL THERAPY: Joint protection techniques; adaptive equipment; workplace modification',
        'ACUPUNCTURE: May provide modest benefit; patient preference'
      ],
      
      surgical: [
        'TOTAL KNEE REPLACEMENT (TKR): Highly effective for end-stage knee OA; >90% satisfaction; last 15-20+ years',
        'TOTAL HIP REPLACEMENT (THR): Highly effective for end-stage hip OA; excellent outcomes; last 15-25+ years',
        'PARTIAL KNEE REPLACEMENT (UKR): For unicompartmental disease; faster recovery; may need revision',
        'ARTHROSCOPY: Limited role in OA; NOT recommended for routine debridement (no benefit over placebo)',
        'OSTEOTOMY: Realignment procedure for younger patients with malalignment; delays need for replacement',
        'JOINT FUSION (Arthrodesis): Pain relief but loss of motion; rarely used for knee/hip; option for some joints',
        'TRAPEZIECTOMY: For severe thumb base (first CMC) OA; good outcomes'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Suspected septic arthritis - Hot, swollen joint with fever; aspirate urgently',
        '🔴 IMMEDIATE: Acute locked knee - Mechanical block; may need urgent arthroscopy',
        '🟠 URGENT: Rapidly progressive symptoms or joint destruction',
        '🟠 URGENT: Suspected inflammatory arthritis (prolonged morning stiffness, multiple swollen joints)',
        '🟡 ROUTINE: Symptoms not adequately controlled with conservative management',
        '🟡 ROUTINE: Significant functional limitation affecting daily activities and quality of life',
        '🟡 ROUTINE: Considering joint replacement - End-stage OA failing conservative measures',
        '🟡 ROUTINE: Need for supervised physiotherapy or exercise program',
        '🟡 ROUTINE: Diagnostic uncertainty - Atypical features, young patient',
        '🟡 ROUTINE: Occupational assessment needed - Work modifications'
      ],
      
      monitoring: [
        'SYMPTOMS: Pain levels, stiffness, functional ability at each visit',
        'FUNCTION: Ask about specific activities (walking distance, stairs, ADLs)',
        'MEDICATION EFFICACY: Is current regimen controlling symptoms adequately?',
        'MEDICATION SAFETY: GI symptoms, BP, renal function if on NSAIDs',
        'WEIGHT: Track if weight loss recommended; encourage progress',
        'EXERCISE ADHERENCE: Reinforce importance; troubleshoot barriers',
        'JOINT STATUS: Progression of deformity, range of motion, effusion',
        'QUALITY OF LIFE: Sleep, mood, social function',
        'REASSESS PERIODICALLY: X-rays not needed routinely unless significant change or pre-surgical'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

INITIAL MANAGEMENT:
• Patient education - crucial for engagement
• Start exercise program (most important intervention)
• Weight loss plan if overweight
• Topical NSAID for knee/hand OA
• Follow-up in 4-8 weeks to assess response

REASSESSMENT VISIT:
• Review symptom control and function
• Check exercise adherence; reinforce benefits
• Progress weight loss efforts
• If topical NSAID inadequate, consider oral NSAID (lowest dose, shortest duration)
• Check for NSAID contraindications and need for gastroprotection
• Discuss intra-articular injection if significant symptoms

ONGOING MANAGEMENT:
• Regular follow-up every 3-6 months initially, then annually if stable
• Reinforce exercise and weight management at every visit
• Minimize long-term NSAID use where possible
• Monitor for disease progression
• Adjust treatment based on symptoms and function

WHEN TO CONSIDER SURGERY:
• Symptoms significantly affecting quality of life despite optimal conservative management
• X-ray evidence of significant joint damage (though correlate with symptoms)
• Night pain, rest pain (indicates more severe disease)
• Patient willing and fit for surgery
• Realistic expectations

PRE-SURGICAL OPTIMIZATION:
• Weight optimization (BMI <40 preferred, <35 better outcomes)
• Optimize comorbidities (diabetes, hypertension, cardiovascular disease)
• Stop smoking
• Address depression and anxiety
• Pre-operative physiotherapy may help

SPECIAL CONSIDERATIONS FOR AFRICA:
• Limited access to joint replacement - maximize conservative management
• Ensure exercise advice is culturally appropriate and accessible
• Consider affordability of medications
• Traditional healers may be involved - coordinate care if possible
• Community-based exercise programs where available

MEDICATIONS TO AVOID OR USE CAUTIOUSLY:
• Long-term high-dose NSAIDs: Cardiovascular and GI risks
• Opioids: Limited evidence; dependence risk; avoid long-term
• Glucosamine/chondroitin: Weak evidence; expensive; not recommended by most guidelines
• Intra-articular hyaluronic acid: Weak evidence; expensive; not routinely recommended

POOR PROGNOSTIC FACTORS:
• Obesity
• Malalignment
• Widespread OA (multiple joints)
• Comorbid depression
• Low quadriceps strength
• Severe baseline symptoms and disability
• Bone marrow lesions on MRI`
    },

    redFlags: [
      '🔴 CRITICAL: Hot, swollen joint with fever - Septic arthritis until proven otherwise; aspirate urgently',
      '🔴 CRITICAL: Acute severe pain with joint instability - Possible fracture or ligament rupture',
      '🔴 CRITICAL: Rapidly progressive joint destruction - Consider malignancy, infection, inflammatory arthritis',
      '🟠 URGENT: Locked knee (cannot extend) - Mechanical block from loose body or meniscal tear',
      '🟠 URGENT: Symptoms suggestive of inflammatory arthritis - Morning stiffness >1 hour, multiple swollen joints, systemic symptoms',
      '🟠 URGENT: Signs of nerve compression - Weakness, numbness (spine OA with radiculopathy)',
      '🟡 CAUTION: Night pain and rest pain - May indicate more severe disease or other pathology',
      '🟡 CAUTION: Young patient (<40) with OA - Consider secondary causes (injury, AVN, inflammatory arthritis)',
      '🟡 CAUTION: GI symptoms on NSAIDs - May indicate ulceration; review need and add gastroprotection'
    ],

    complications: [
      'Chronic pain and disability - Progressive functional impairment',
      'Joint deformity - Varus/valgus, flexion contracture',
      'Muscle weakness and atrophy - Especially quadriceps in knee OA',
      'Falls risk - Due to pain, instability, muscle weakness',
      'Sleep disturbance - From night pain',
      'Depression and anxiety - Common comorbidity; affects outcomes',
      'Reduced physical activity - Leading to weight gain, cardiovascular deconditioning',
      'NSAID complications - GI bleeding, renal impairment, cardiovascular events',
      'Opioid dependence - If prescribed for chronic use',
      'Work disability - Inability to perform occupational duties',
      'Reduced quality of life - Impact on daily activities, social participation'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

NATURAL HISTORY:
• OA is generally progressive but rate varies widely
• Some patients remain stable for years
• Others progress to severe disease requiring joint replacement
• Spontaneous improvement can occur (especially hand OA)

FACTORS INFLUENCING PROGRESSION:

WORSE PROGNOSIS:
• Obesity (especially for knee OA)
• Joint malalignment
• Previous joint injury
• Widespread OA (multiple joints)
• Presence of inflammation (effusion, synovitis)
• Bone marrow lesions on MRI
• Genetic predisposition
• Comorbid depression
• Low muscle strength

BETTER PROGNOSIS:
• Normal weight
• Good muscle strength
• Single joint involvement
• Early stage at diagnosis
• Good response to conservative measures
• Active lifestyle with regular exercise

OUTCOMES WITH TREATMENT:

CONSERVATIVE MANAGEMENT:
• Exercise: Reduces pain 20-30%, improves function
• Weight loss: 10% loss reduces knee pain by 50%
• Topical NSAIDs: Modest pain reduction, good safety
• Oral NSAIDs: Moderate pain reduction, short-term use preferred

JOINT REPLACEMENT:
• Highly effective for end-stage disease
• 85-95% of patients report significant improvement
• >90% of knee and hip replacements last 15-20 years
• Allows return to most activities (avoid high-impact)

OVERALL OUTLOOK:
• OA is a chronic condition but symptoms can be well managed
• Most patients maintain reasonable function with conservative treatment
• Joint replacement is an excellent option for end-stage disease
• Quality of life can be maintained with appropriate management`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing OA):
• Maintain healthy weight (most important modifiable factor)
• Regular physical activity (strengthens muscles, maintains joint health)
• Prevent joint injuries (proper technique in sports, workplace safety)
• Avoid repetitive joint stress (occupational modification)
• Treat joint injuries properly (ACL reconstruction, meniscal repair when indicated)

SECONDARY PREVENTION (Early OA):
• Weight loss if overweight
• Muscle strengthening (quadriceps for knee, hip abductors for hip)
• Low-impact exercise (walking, swimming, cycling)
• Activity modification to reduce joint stress
• Address malalignment (orthotic devices, bracing)

TERTIARY PREVENTION (Established OA):
• Optimize conservative management
• Prevent falls (strength, balance training, walking aids)
• Manage comorbidities (cardiovascular, metabolic)
• Preserve joint function as long as possible
• Timely surgical intervention when indicated

SPECIFIC RECOMMENDATIONS:

WEIGHT MANAGEMENT:
• Maintain BMI <25 if possible
• Even modest weight loss (5-10%) has significant benefits
• Combine diet and exercise

EXERCISE:
• Regular physical activity throughout life
• Mix of aerobic and strengthening exercises
• Low-impact activities preferred
• Aim for 150 minutes moderate activity per week

JOINT PROTECTION:
• Proper body mechanics (lifting, bending)
• Avoid prolonged kneeling, squatting
• Use appropriate footwear
• Use assistive devices when needed

OCCUPATIONAL HEALTH:
• Minimize repetitive joint stress
• Ergonomic workplace setup
• Regular breaks from static positions
• Job modification if needed`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS OSTEOARTHRITIS?
Osteoarthritis (OA) is the most common type of arthritis. It happens when the protective cartilage that cushions the ends of your bones wears down over time. It most commonly affects the knees, hips, hands, and spine.

IS IT JUST "WEAR AND TEAR"?
Not exactly. While OA does involve cartilage breakdown, it is an active disease process. The whole joint is affected, including the bone underneath, the lining of the joint, and the surrounding muscles and ligaments.

WILL IT KEEP GETTING WORSE?
Not necessarily! Many people with OA remain stable for years. With proper management, you can control your symptoms and maintain a good quality of life.

THE BEST TREATMENTS - AND THEY ARE FREE!

EXERCISE IS THE BEST MEDICINE:
• Regular exercise REDUCES pain and IMPROVES function
• Aim for 30 minutes most days
• Good options: Walking, swimming, cycling, tai chi
• Strengthening exercises are especially important
• It is safe to exercise with OA - movement is good for your joints
• Start slowly and build up gradually

WEIGHT MANAGEMENT:
• If you are overweight, losing weight is very helpful
• Even losing 5-10% of your weight can significantly reduce knee pain
• Every kilogram of weight you lose takes 4kg of pressure off your knees

MEDICATIONS:

TOPICAL CREAMS (First Choice for Knee/Hand):
• Apply directly to the painful joint
• Work locally with fewer side effects than pills
• Use 3-4 times daily

ORAL PAIN MEDICATIONS:
• Take the lowest dose that controls your pain
• Use for the shortest time possible
• Take with food to protect your stomach
• Tell your doctor if you have heart, kidney, or stomach problems

PARACETAMOL:
• Less effective than anti-inflammatory drugs
• Can be used if you cannot take other medications

OTHER HELPFUL STRATEGIES:
• Heat for stiffness (warm shower in the morning)
• Ice for swelling (20 minutes at a time)
• Walking stick (use in the hand opposite to the painful leg)
• Comfortable, supportive shoes
• Pace your activities - alternate activity with rest
• Assistive devices (jar openers, grab rails)

WHAT ABOUT SURGERY?
• Joint replacement is very successful for severe OA
• It is usually considered when other treatments no longer help
• Most people experience great improvement
• Replacements typically last 15-20+ years

THINGS TO AVOID:
• Long periods of inactivity (keep moving!)
• Excessive high-impact activities (running, jumping)
• Crash diets (gradual weight loss is better)
• Prolonged kneeling or squatting
• Expecting a pill to fix everything (exercise and weight loss are more important)

WARNING SIGNS - SEE YOUR DOCTOR:
• Sudden increase in pain or swelling
• Hot, red joint (could be infection)
• Fever with joint pain
• Joint giving way or locking
• Unable to bear weight

REMEMBER:
• OA is very common and very manageable
• Exercise and weight loss are the most effective treatments
• You can live well with OA
• Stay active - it helps your joints!

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Osteoarthritis is a chronic condition, but it does not mean a life of pain and disability. The keys to living well with OA are: stay active with regular exercise, maintain a healthy weight, and use medications wisely when needed. With these steps, most people with OA lead full, active lives.`,

    references: [
      'Kolasinski SL, Neogi T, Hochberg MC, et al. 2019 American College of Rheumatology/Arthritis Foundation Guideline for the Management of Osteoarthritis of the Hand, Hip, and Knee. Arthritis Care Res (Hoboken). 2020;72(2):149-162.',
      'National Institute for Health and Care Excellence (NICE). Osteoarthritis: care and management. Clinical guideline [CG177]. 2014 (updated 2020).',
      'Bannuru RR, Osani MC, Vaysbrot EE, et al. OARSI guidelines for the non-surgical management of knee, hip, and polyarticular osteoarthritis. Osteoarthritis Cartilage. 2019;27(11):1578-1589.',
      'Hunter DJ, Bierma-Zeinstra S. Osteoarthritis. Lancet. 2019;393(10182):1745-1759.',
      'Safiri S, Kolahi AA, Smith E, et al. Global, regional and national burden of osteoarthritis 1990-2017: a systematic analysis of the Global Burden of Disease Study 2017. Ann Rheum Dis. 2020;79(6):819-828.',
      'Felson DT. Clinical practice. Osteoarthritis of the knee. N Engl J Med. 2006;354(8):841-848.',
      'Zhang W, Doherty M, Peat G, et al. EULAR evidence-based recommendations for the diagnosis of knee osteoarthritis. Ann Rheum Dis. 2010;69(3):483-489.',
      'Jevsevar DS. Treatment of osteoarthritis of the knee: evidence-based guideline, 2nd edition. J Am Acad Orthop Surg. 2013;21(9):571-576.',
      'Bruyère O, Honvo G, Veronese N, et al. An updated algorithm recommendation for the management of knee osteoarthritis from the European Society for Clinical and Economic Aspects of Osteoporosis, Osteoarthritis and Musculoskeletal Diseases (ESCEO). Semin Arthritis Rheum. 2019;49(3):337-350.',
      'Hochberg MC, Altman RD, April KT, et al. American College of Rheumatology 2012 recommendations for the use of nonpharmacologic and pharmacologic therapies in osteoarthritis of the hand, hip, and knee. Arthritis Care Res (Hoboken). 2012;64(4):465-474.',
      'Messier SP, Mihalko SL, Legault C, et al. Effects of intensive diet and exercise on knee joint loads, inflammation, and clinical outcomes among overweight and obese adults with knee osteoarthritis: the IDEA randomized clinical trial. JAMA. 2013;310(12):1263-1273.',
      'Fransen M, McConnell S, Harmer AR, et al. Exercise for osteoarthritis of the knee: a Cochrane systematic review. Br J Sports Med. 2015;49(24):1554-1557.',
      'da Costa BR, Reichenbach S, Keller N, et al. Effectiveness of non-steroidal anti-inflammatory drugs for the treatment of pain in knee and hip osteoarthritis: a network meta-analysis. Lancet. 2017;390(10090):e21-e33.',
      'Arden NK, Perry TA, Bannuru RR, et al. Non-surgical management of knee osteoarthritis: comparison of ESCEO and OARSI 2019 guidelines. Nat Rev Rheumatol. 2021;17(1):59-66.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 82. RHEUMATOID ARTHRITIS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '82',
    name: 'Rheumatoid Arthritis',
    slug: 'rheumatoid-arthritis',
    specialty: 'Internal Medicine',
    bodySystem: 'Musculoskeletal',
    icdCode: 'M05-M06',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Rheumatoid arthritis (RA) is a chronic, systemic autoimmune disease characterized by symmetric inflammatory polyarthritis that primarily affects the small joints of the hands and feet. If untreated, it leads to progressive joint destruction, deformity, and disability. RA affects approximately 0.5-1% of the global population, with women affected 2-3 times more often than men.

The pathophysiology involves autoimmune-mediated inflammation of the synovium (synovitis). Activated T cells, B cells, macrophages, and fibroblasts infiltrate the synovium, producing inflammatory cytokines (TNF-α, IL-1, IL-6) and autoantibodies (RF, anti-CCP). The inflamed synovium forms pannus tissue that erodes cartilage and bone. Without treatment, joint destruction begins within months of disease onset.

RA is a clinical diagnosis supported by serological markers (RF, anti-CCP) and inflammatory markers (ESR, CRP). The 2010 ACR/EULAR classification criteria help identify patients early. Early diagnosis and treatment are critical - the first 3-6 months represent a "window of opportunity" where aggressive treatment can prevent irreversible damage.

In Africa, RA prevalence is lower than in Western populations (0.1-0.5%) but the disease tends to present later and with more severe manifestations due to delayed diagnosis and limited access to disease-modifying therapy. Methotrexate, the cornerstone of treatment, is affordable and available in most settings, but biologic agents are largely inaccessible.

Management follows a "treat-to-target" strategy aiming for remission or low disease activity. Methotrexate is first-line DMARD, with other conventional DMARDs (sulfasalazine, leflunomide, hydroxychloroquine) and biologics used as needed. Early, aggressive treatment dramatically improves outcomes.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - EARLY TREATMENT IS CRITICAL: Start DMARD within 3 months of symptom onset; delays lead to irreversible joint damage

🔴 CRITICAL POINT 2 - Methotrexate is FIRST-LINE DMARD: Start early, optimize dose (up to 25mg/week); add folic acid to reduce toxicity

🔴 CRITICAL POINT 3 - Treat-to-Target: Aim for REMISSION or low disease activity; adjust treatment every 3 months until target achieved

🔴 CRITICAL POINT 4 - NSAIDs and steroids are BRIDGING therapy only: They do NOT prevent joint damage; DMARDs are essential

🟠 CRITICAL POINT 5 - Morning stiffness >1 hour suggests inflammatory arthritis: Key distinguishing feature from osteoarthritis

🟠 CRITICAL POINT 6 - Anti-CCP is highly specific (>95%): More specific than RF; positive anti-CCP predicts erosive disease

🟠 CRITICAL POINT 7 - Monitor for extra-articular manifestations: Lung disease, cardiovascular disease, eye involvement

🟡 CRITICAL POINT 8 - Screen before methotrexate: Hepatitis B/C, chest X-ray, LFTs, CBC, renal function

🟡 CRITICAL POINT 9 - Increased cardiovascular risk: RA is an independent CV risk factor; manage aggressively`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Prevalence: 0.5-1% of adults worldwide
• Incidence: 20-50 per 100,000 per year
• Female:Male ratio: 2-3:1
• Peak onset: 40-60 years (can occur at any age)
• More common in developed countries and urban areas
• Prevalence increasing globally

RISK FACTORS:
• Genetic: HLA-DR4, HLA-DR1 (shared epitope); family history
• Female sex and hormonal factors
• Smoking (strongest modifiable risk factor; also worsens disease)
• Obesity
• Silica exposure, textile dust
• Periodontal disease (P. gingivalis)
• Gut microbiome alterations

AFRICAN EPIDEMIOLOGY:
• Prevalence: 0.1-0.5% (lower than Western populations)
• Urban areas have higher prevalence than rural
• Often presents later with more severe disease
• Seropositive disease (RF+, anti-CCP+) less common in some populations
• Limited access to rheumatologists and DMARDs
• Methotrexate usually available and affordable
• Biologics largely inaccessible due to cost
• Traditional medicine often used before presentation
• Delayed diagnosis common

DISEASE COURSE:
• Without treatment: Progressive joint destruction, deformity, disability
• With treatment: Remission achievable in 30-50%; low disease activity in many more
• Mortality increased (mainly cardiovascular disease)
• Life expectancy reduced by 3-10 years if poorly controlled

PROGNOSIS FACTORS (Poor):
• High disease activity at presentation
• RF and anti-CCP positive (especially high titers)
• Early erosions on X-ray
• Elevated ESR/CRP
• Smoking
• Extra-articular manifestations
• Delayed treatment initiation`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ARTICULAR MANIFESTATIONS:

JOINT PATTERN:
• Symmetric polyarthritis (bilateral, same joints)
• Small joints: MCPs, PIPs, wrists, MTPs (classic)
• Large joints can be affected: Shoulders, elbows, knees, ankles
• DIP joints typically SPARED (unlike OA and psoriatic arthritis)
• Cervical spine can be involved (atlantoaxial subluxation)

JOINT SYMPTOMS:
• Pain and tenderness
• Swelling (soft, boggy synovial thickening)
• Warmth
• Morning stiffness >1 HOUR (key feature; improves with activity)
• Stiffness after inactivity (gelling)
• Reduced range of motion
• Grip weakness

EARLY DISEASE:
• Fatigue (often prominent, precedes joint symptoms)
• Symmetric joint pain and swelling
• Morning stiffness >1 hour
• Gradual onset over weeks to months
• May have prodrome of fatigue, malaise, weight loss

ESTABLISHED/LATE DISEASE:
• Joint deformities:
  - Ulnar deviation of fingers at MCPs
  - Swan neck deformity (PIP hyperextension, DIP flexion)
  - Boutonniere deformity (PIP flexion, DIP hyperextension)
  - Z-thumb deformity
  - Cock-up toes
• Subluxation
• Joint instability
• Muscle wasting
• Rheumatoid nodules (20-30% of seropositive patients)

EXTRA-ARTICULAR MANIFESTATIONS (occur in 40% of patients):

CONSTITUTIONAL:
• Fatigue (very common)
• Low-grade fever
• Weight loss
• Malaise

SKIN:
• Rheumatoid nodules (elbows, fingers, Achilles; seropositive disease)
• Vasculitic lesions
• Palmar erythema

PULMONARY (common cause of morbidity):
• Interstitial lung disease (ILD) - most serious
• Pleural effusions
• Rheumatoid nodules in lung
• Bronchiectasis
• Drug-induced lung disease (methotrexate)

CARDIOVASCULAR:
• Accelerated atherosclerosis (major cause of death)
• Pericarditis
• Myocarditis (rare)
• Vasculitis

OCULAR:
• Keratoconjunctivitis sicca (dry eyes; secondary Sjogren)
• Episcleritis
• Scleritis (serious; can perforate)

NEUROLOGICAL:
• Cervical myelopathy (atlantoaxial subluxation)
• Peripheral neuropathy
• Carpal tunnel syndrome
• Mononeuritis multiplex (vasculitis)

HEMATOLOGICAL:
• Anemia of chronic disease (most common)
• Felty syndrome (RA + splenomegaly + neutropenia)
• Large granular lymphocyte syndrome

RENAL:
• Amyloidosis (long-standing uncontrolled disease)
• Drug-induced (NSAIDs, gold, penicillamine)`,

    diagnosis: `DIAGNOSTIC APPROACH:

RA IS A CLINICAL DIAGNOSIS supported by laboratory and imaging findings.

2010 ACR/EULAR CLASSIFICATION CRITERIA:
Apply to patients with at least 1 joint with definite clinical synovitis not explained by another disease.

JOINT INVOLVEMENT (0-5 points):
• 1 large joint: 0
• 2-10 large joints: 1
• 1-3 small joints: 2
• 4-10 small joints: 3
• >10 joints (at least 1 small): 5

SEROLOGY (0-3 points):
• Negative RF AND negative anti-CCP: 0
• Low positive RF OR low positive anti-CCP: 2
• High positive RF OR high positive anti-CCP: 3

ACUTE PHASE REACTANTS (0-1 point):
• Normal CRP AND normal ESR: 0
• Abnormal CRP OR abnormal ESR: 1

DURATION (0-1 point):
• <6 weeks: 0
• ≥6 weeks: 1

SCORE ≥6/10 = Classified as RA

LABORATORY FINDINGS:

AUTOANTIBODIES:
• Rheumatoid Factor (RF):
  - Positive in 70-80% of RA patients
  - Sensitivity ~70%, Specificity ~80%
  - Can be positive in other conditions (infections, other autoimmune diseases, elderly)
  
• Anti-CCP (Anti-Citrullinated Protein Antibodies):
  - Positive in 60-70% of RA patients
  - Sensitivity ~70%, Specificity >95% (highly specific)
  - Predicts erosive disease
  - May be positive years before symptom onset

INFLAMMATORY MARKERS:
• ESR: Usually elevated (often >30 mm/hr)
• CRP: Usually elevated
• Used for monitoring disease activity

OTHER LABS:
• CBC: Anemia of chronic disease, thrombocytosis; neutropenia in Felty
• LFTs: Baseline before methotrexate
• Renal function: Baseline, monitor on NSAIDs
• Hepatitis B/C serology: Before immunosuppression

IMAGING:

PLAIN X-RAY (Hands and Feet):
• Early: Soft tissue swelling, periarticular osteopenia, joint space preserved
• Later: Joint space narrowing, erosions (marginal), subluxation
• Erosions indicate need for aggressive treatment

ULTRASOUND:
• More sensitive than clinical exam for synovitis
• Detects erosions earlier than X-ray
• Power Doppler shows active inflammation
• Increasingly used for diagnosis and monitoring

MRI:
• Most sensitive for early erosions and synovitis
• Bone marrow edema predicts erosions
• Not routinely needed; useful in diagnostic uncertainty

DISEASE ACTIVITY ASSESSMENT:

DAS28 (Disease Activity Score):
• Based on: 28 joint count (tender + swollen), ESR or CRP, patient global assessment
• Remission: <2.6
• Low disease activity: 2.6-3.2
• Moderate: 3.2-5.1
• High: >5.1

Other measures: CDAI, SDAI, ACR response criteria`,

    differentialDiagnosis: [
      'Psoriatic arthritis - Psoriasis, nail changes, DIP involvement, dactylitis, enthesitis; can be RF negative',
      'Systemic lupus erythematosus - Malar rash, photosensitivity, serositis, renal involvement; ANA positive; non-erosive',
      'Viral arthritis - Acute onset, self-limited; hepatitis B/C, parvovirus B19, chikungunya; recent viral illness',
      'Reactive arthritis - Preceding GI or GU infection; asymmetric oligoarthritis; conjunctivitis, urethritis',
      'Gout/Pseudogout - Acute monoarticular attacks; crystals on aspiration; uric acid elevated (gout)',
      'Osteoarthritis - DIP involvement, Heberden nodes; morning stiffness <30 min; non-inflammatory',
      'Polymyalgia rheumatica - Age >50; shoulder/hip girdle stiffness and pain; elevated ESR; no synovitis',
      'Fibromyalgia - Widespread pain; tender points; no joint swelling; normal labs',
      'Sarcoidosis - Bilateral hilar lymphadenopathy; erythema nodosum; elevated ACE',
      'Hemochromatosis arthropathy - Second/third MCPs; hook-like osteophytes; elevated ferritin/transferrin saturation',
      'Adult-onset Still disease - Quotidian fever, salmon-colored rash, elevated ferritin, negative RF/ANA',
      'Septic arthritis - Acute, monoarticular, systemic toxicity; positive synovial fluid culture'
    ],

    investigations: [
      {
        name: 'Rheumatoid Factor (RF)',
        indication: '🔴 ESSENTIAL - Positive in 70-80%; supports diagnosis; high titers predict worse prognosis',
        availability: 'BASIC'
      },
      {
        name: 'Anti-CCP Antibodies',
        indication: '🔴 ESSENTIAL - Highly specific (>95%); predicts erosive disease; may be positive early',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ESR (Erythrocyte Sedimentation Rate)',
        indication: '🔴 ESSENTIAL - Usually elevated; used for diagnosis and monitoring disease activity',
        availability: 'BASIC'
      },
      {
        name: 'CRP (C-Reactive Protein)',
        indication: '🔴 ESSENTIAL - Elevated in active disease; more responsive to changes than ESR',
        availability: 'BASIC'
      },
      {
        name: 'Complete Blood Count',
        indication: '🔴 ESSENTIAL - Anemia of chronic disease common; baseline before DMARDs; thrombocytosis',
        availability: 'BASIC'
      },
      {
        name: 'Liver Function Tests',
        indication: '🔴 ESSENTIAL - Baseline before methotrexate; monitor during treatment',
        availability: 'BASIC'
      },
      {
        name: 'Renal Function (Creatinine, eGFR)',
        indication: '🔴 ESSENTIAL - Baseline; monitor on NSAIDs; adjust DMARD doses',
        availability: 'BASIC'
      },
      {
        name: 'X-ray Hands and Feet',
        indication: '🔴 ESSENTIAL - Baseline for erosions; periarticular osteopenia early; erosions indicate aggressive disease',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: '🔴 ESSENTIAL - Baseline before methotrexate; screen for ILD, TB, nodules',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B and C Serology',
        indication: '🔴 ESSENTIAL - Before immunosuppressive therapy; reactivation risk',
        availability: 'BASIC'
      },
      {
        name: 'ANA (Antinuclear Antibody)',
        indication: '🟠 IMPORTANT - To help exclude SLE if clinical features suggest; low-titer ANA can occur in RA',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Uric Acid',
        indication: '🟡 IF INDICATED - To exclude gout if acute monoarthritis',
        availability: 'BASIC'
      },
      {
        name: 'Synovial Fluid Analysis',
        indication: '🟡 IF INDICATED - Inflammatory fluid (WBC 2000-50000); rules out septic arthritis, crystals',
        availability: 'BASIC'
      },
      {
        name: 'Musculoskeletal Ultrasound',
        indication: '🟡 IF AVAILABLE - More sensitive for synovitis than exam; detects early erosions; monitors treatment',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI (Hands/Wrists)',
        indication: '🟡 IF AVAILABLE - Most sensitive for early erosions and synovitis; bone marrow edema',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM DIAGNOSIS: Clinical assessment, RF, anti-CCP, ESR/CRP, X-ray hands and feet',
        '⚡ START DMARD EARLY: Methotrexate within 3 months of symptom onset; do not delay',
        '⚡ BASELINE INVESTIGATIONS: CBC, LFTs, renal function, hepatitis B/C, chest X-ray before MTX',
        '⚡ BRIDGING THERAPY: Short course prednisolone or IM steroid while waiting for DMARD effect',
        '⚡ PAIN MANAGEMENT: NSAIDs for symptom relief (not disease-modifying)',
        '⚡ PATIENT EDUCATION: Explain chronic nature, importance of adherence, treatment goals',
        '⚡ FOLIC ACID: Start with methotrexate (5mg weekly or 1mg daily) to reduce side effects',
        '⚡ ASSESS DISEASE ACTIVITY: Calculate DAS28 or use clinical assessment; document baseline',
        '⚡ SCREEN FOR COMORBIDITIES: Cardiovascular risk, infections, depression',
        '⚡ VACCINATIONS: Update before starting immunosuppression; avoid live vaccines on DMARDs'
      ],
      
      pharmacological: [
        {
          drug: 'Methotrexate',
          indication: '🔴 FIRST-LINE DMARD - Cornerstone of RA treatment; start early and optimize dose',
          dose: 'Start 7.5-15mg PO/SC once weekly. Increase by 2.5-5mg every 2-4 weeks to 20-25mg weekly. Take on same day each week. Add folic acid 5mg weekly (day after MTX) or 1mg daily (except MTX day)',
          route: 'PO or SC (SC if inadequate response or GI intolerance to oral)',
          duration: 'Long-term; continue if effective and tolerated',
          contraindications: ['Pregnancy/breastfeeding', 'Severe hepatic disease', 'Significant renal impairment', 'Active infection', 'Bone marrow suppression', 'Immunodeficiency'],
          sideEffects: ['Nausea', 'Oral ulcers', 'Hepatotoxicity', 'Bone marrow suppression', 'Pneumonitis', 'Teratogenicity'],
          alternatives: ['Leflunomide', 'Sulfasalazine']
        },
        {
          drug: 'Folic Acid',
          indication: '🔴 ESSENTIAL with METHOTREXATE - Reduces MTX side effects (nausea, oral ulcers, hepatotoxicity)',
          dose: '5mg PO once weekly (day after MTX) OR 1mg PO daily (except day of MTX)',
          route: 'PO',
          duration: 'Continue while on methotrexate',
          contraindications: ['Untreated B12 deficiency (may mask)'],
          sideEffects: ['Generally well tolerated'],
          alternatives: ['Folinic acid (leucovorin) if severe MTX toxicity']
        },
        {
          drug: 'Sulfasalazine',
          indication: '🟠 ALTERNATIVE or COMBINATION DMARD - Can be used alone or with MTX; useful if MTX contraindicated',
          dose: 'Start 500mg PO daily. Increase by 500mg weekly to 1g twice daily (max 3g/day in divided doses)',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Sulfonamide/salicylate allergy', 'G6PD deficiency', 'Porphyria'],
          sideEffects: ['GI upset', 'Rash', 'Headache', 'Hepatotoxicity', 'Bone marrow suppression', 'Male infertility (reversible)'],
          alternatives: ['Methotrexate', 'Leflunomide']
        },
        {
          drug: 'Hydroxychloroquine',
          indication: '🟠 ADJUNCT DMARD - Mild disease or combination therapy; least potent DMARD; good safety profile',
          dose: 'Adults: 200-400mg PO once daily (5mg/kg/day max). Usually 200mg twice daily',
          route: 'PO',
          duration: 'Long-term; requires eye monitoring',
          contraindications: ['Retinopathy', 'Maculopathy', 'Long QT syndrome'],
          sideEffects: ['GI upset', 'Skin rash', 'Retinal toxicity (rare, monitor)', 'QT prolongation'],
          alternatives: ['Chloroquine (higher retinal toxicity)']
        },
        {
          drug: 'Leflunomide',
          indication: '🟠 ALTERNATIVE to METHOTREXATE - Similar efficacy; use if MTX contraindicated or failed',
          dose: 'Loading dose (optional): 100mg PO daily for 3 days. Maintenance: 20mg PO once daily (10mg if not tolerated)',
          route: 'PO',
          duration: 'Long-term',
          contraindications: ['Pregnancy/breastfeeding (long washout needed)', 'Severe hepatic impairment', 'Severe immunodeficiency'],
          sideEffects: ['Diarrhea', 'Hepatotoxicity', 'Hypertension', 'Alopecia', 'Teratogenicity', 'Peripheral neuropathy'],
          alternatives: ['Methotrexate', 'Sulfasalazine']
        },
        {
          drug: 'Prednisolone',
          indication: '🔴 BRIDGING THERAPY - Short-term while waiting for DMARD effect; also for flares',
          dose: 'Bridging: 10-15mg PO daily, taper over 2-3 months. Flare: Higher dose short course. Long-term: Avoid if possible; if needed, ≤7.5mg daily',
          route: 'PO',
          duration: 'Short-term bridging (weeks to months); minimize long-term use',
          contraindications: ['Active untreated infection', 'Avoid long-term if possible'],
          sideEffects: ['Weight gain', 'Hyperglycemia', 'Osteoporosis', 'Cataracts', 'Infection risk', 'Adrenal suppression'],
          alternatives: ['IM triamcinolone (single injection for bridging)']
        },
        {
          drug: 'Intramuscular Triamcinolone',
          indication: '🟠 BRIDGING OPTION - Single injection while waiting for DMARD effect; useful if oral steroids problematic',
          dose: 'Adults: Triamcinolone acetonide 40-80mg IM once. May repeat after 4-6 weeks if needed',
          route: 'IM',
          duration: 'Single injection; effect lasts weeks',
          contraindications: ['Active infection', 'Anticoagulation (caution)'],
          sideEffects: ['Local injection site reactions', 'Systemic steroid effects (transient)'],
          alternatives: ['Oral prednisolone taper']
        },
        {
          drug: 'Intra-articular Corticosteroid',
          indication: '🟠 ADJUNCT - For flare in individual joint; while awaiting systemic treatment effect',
          dose: 'Knee: 40mg triamcinolone. Medium joint: 20-30mg. Small joint: 10mg. Limit to 3-4 injections per joint per year',
          route: 'Intra-articular',
          duration: 'Effect lasts weeks to months',
          contraindications: ['Local infection', 'Prosthetic joint', 'Coagulopathy'],
          sideEffects: ['Post-injection flare', 'Infection', 'Soft tissue atrophy'],
          alternatives: ['Systemic steroids for polyarticular flare']
        },
        {
          drug: 'Naproxen',
          indication: '🟠 SYMPTOMATIC RELIEF - Does NOT modify disease; use for pain/inflammation while DMARD takes effect',
          dose: 'Adults: 250-500mg PO twice daily with food. Max: 1250mg/day initially, then 1000mg/day',
          route: 'PO',
          duration: 'As needed; try to minimize once DMARD effective',
          contraindications: ['Active peptic ulcer', 'Severe renal impairment', 'Heart failure', 'Third trimester pregnancy'],
          sideEffects: ['GI upset/bleeding', 'Renal impairment', 'Cardiovascular events'],
          alternatives: ['Ibuprofen', 'Celecoxib (if high GI risk)']
        },
        {
          drug: 'Triple Therapy (MTX + SSZ + HCQ)',
          indication: '🟠 COMBINATION cDMARD - If inadequate response to MTX alone; alternative to biologics',
          dose: 'Methotrexate (optimized) + Sulfasalazine 1g BD + Hydroxychloroquine 200mg BD',
          route: 'PO',
          duration: 'Long-term if effective',
          contraindications: ['Individual drug contraindications'],
          sideEffects: ['Combined side effects of individual drugs'],
          alternatives: ['Add biologic (if available)']
        },
        {
          drug: 'Biologic DMARDs (TNF inhibitors, etc.)',
          indication: '🟡 IF AVAILABLE - For inadequate response to cDMARDs; highly effective but expensive',
          dose: 'Examples: Etanercept 50mg SC weekly; Adalimumab 40mg SC every 2 weeks; Tocilizumab 8mg/kg IV monthly',
          route: 'SC or IV depending on agent',
          duration: 'Long-term',
          contraindications: ['Active serious infection', 'TB (screen and treat before starting)', 'Heart failure (TNF inhibitors)', 'Demyelinating disease'],
          sideEffects: ['Serious infections', 'Injection/infusion reactions', 'Reactivation of TB/hepatitis B'],
          alternatives: ['Different biologic class', 'JAK inhibitors (tofacitinib)']
        }
      ],
      
      nonPharmacological: [
        'PATIENT EDUCATION: Explain disease, importance of early treatment, DMARD adherence, treatment targets',
        'EXERCISE: Regular exercise improves function and cardiovascular health; low-impact, strengthening, aerobic',
        'PHYSIOTHERAPY: Range of motion exercises, strengthening, joint protection techniques',
        'OCCUPATIONAL THERAPY: Splints (resting splints for flares, working splints), assistive devices, joint protection',
        'SMOKING CESSATION: Smoking worsens RA and reduces treatment response; essential to quit',
        'WEIGHT MANAGEMENT: Obesity worsens outcomes and reduces drug efficacy',
        'CARDIOVASCULAR RISK MANAGEMENT: RA is independent CV risk factor; optimize BP, lipids, diabetes',
        'VACCINATION: Influenza annually, pneumococcal; complete before starting DMARDs; avoid live vaccines on immunosuppression',
        'PSYCHOLOGICAL SUPPORT: Depression and anxiety common; significant impact on quality of life',
        'FOOT CARE: Podiatry referral for foot involvement; appropriate footwear',
        'REST DURING FLARES: Short-term rest; avoid prolonged immobility'
      ],
      
      surgical: [
        'SYNOVECTOMY: For persistent single joint synovitis despite medical therapy',
        'TENDON REPAIR: For ruptured tendons (extensor tendons common)',
        'JOINT REPLACEMENT: For end-stage joint destruction; hip and knee most common; excellent outcomes',
        'ARTHRODESIS (Fusion): For unstable painful joints; wrist, ankle, cervical spine',
        'CERVICAL SPINE SURGERY: For atlantoaxial subluxation with cord compression (neurological emergency)',
        'CARPAL TUNNEL RELEASE: For median nerve compression'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Suspected septic arthritis - Hot, swollen joint with fever; aspirate urgently',
        '🔴 IMMEDIATE: Cervical myelopathy symptoms - Neck pain with limb weakness/sensory changes; atlantoaxial subluxation',
        '🔴 IMMEDIATE: Scleritis - Red, painful eye; can perforate; urgent ophthalmology',
        '🟠 URGENT: New diagnosis of RA - Early rheumatology referral for DMARD initiation (within 6 weeks ideal)',
        '🟠 URGENT: Inadequate response to methotrexate - Need for combination therapy or biologics',
        '🟠 URGENT: Significant extra-articular manifestations - ILD, vasculitis, Felty syndrome',
        '🟠 URGENT: Suspected drug toxicity - Methotrexate pneumonitis, severe hepatotoxicity',
        '🟡 ROUTINE: Disease activity not at target - Optimization of treatment',
        '🟡 ROUTINE: Joint damage requiring surgery - Orthopedic evaluation',
        '🟡 ROUTINE: Annual review for stable patients - Disease activity, complications, comorbidities'
      ],
      
      monitoring: [
        'DISEASE ACTIVITY: DAS28 or clinical assessment every 1-3 months until target achieved; then every 3-6 months',
        'CBC: Baseline, then every 2-4 weeks for first 3 months on MTX, then every 2-3 months',
        'LFTs: Baseline, then every 2-4 weeks for first 3 months on MTX, then every 2-3 months',
        'RENAL FUNCTION: Baseline, then every 3-6 months; more often if on NSAIDs',
        'ESR/CRP: Each visit to assess disease activity',
        'X-RAYS HANDS/FEET: Baseline, then every 1-2 years to assess for progression',
        'CHEST X-RAY: Baseline; repeat if respiratory symptoms (MTX pneumonitis)',
        'EYE EXAMINATION: Annual if on hydroxychloroquine (after 5 years or earlier if high dose/renal impairment)',
        'CARDIOVASCULAR RISK: Annual assessment; lipids, BP, glucose',
        'BONE DENSITY: If on long-term steroids; screen for osteoporosis',
        'VACCINATION STATUS: Ensure up to date; annual influenza'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

TREAT-TO-TARGET STRATEGY:

TARGET: Remission (DAS28 <2.6) or Low Disease Activity (DAS28 2.6-3.2)

TIMELINE:
• Assess disease activity every 1-3 months
• If not at target, adjust treatment
• Once at target for 6+ months, may cautiously reduce treatment

TREATMENT ESCALATION:
1. Start MTX, optimize to 25mg/week
2. If inadequate response: Add sulfasalazine and/or hydroxychloroquine (triple therapy)
3. If still inadequate: Add biologic DMARD (if available)

EARLY DISEASE (<6 months symptoms):
• Urgent rheumatology referral
• Start methotrexate immediately
• Bridge with steroids while waiting for MTX effect
• Frequent monitoring (monthly) initially
• Goal: Achieve remission while in "window of opportunity"

ESTABLISHED DISEASE:
• Continue treat-to-target approach
• Review every 3-6 months if stable at target
• Annual X-rays to assess for progression
• Manage comorbidities

MONITORING ON METHOTREXATE:
• Weeks 0-12: CBC, LFTs every 2-4 weeks
• After 12 weeks if stable: Every 2-3 months
• Report symptoms: Mouth ulcers, nausea, infection, cough/dyspnea
• Annual chest X-ray if symptoms or high risk

WHEN TO CONSIDER REDUCING TREATMENT:
• Sustained remission for 6-12 months
• Reduce slowly (one drug at a time)
• Continue monitoring for flare
• Many patients require long-term DMARD therapy

PREGNANCY PLANNING:
• Discuss BEFORE conception
• Stop methotrexate 3 months before (men and women)
• Stop leflunomide (requires washout procedure)
• Hydroxychloroquine and sulfasalazine can continue
• Low-dose prednisolone if needed
• Plan with rheumatologist

MANAGING FLARES:
• Intra-articular steroid for single joint
• Short course oral prednisolone for polyarticular flare
• Ensure DMARD optimized
• Identify triggers (infection, non-adherence)

SPECIAL CONSIDERATIONS FOR AFRICA:
• Methotrexate is widely available and affordable
• Biologics generally not accessible
• Maximize conventional DMARD combinations
• Screen for TB before starting DMARDs
• Monitor for infections (higher risk in some regions)
• Traditional medicine use - educate on importance of DMARDs
• Community-based monitoring may be needed`
    },

    redFlags: [
      '🔴 CRITICAL: Hot swollen joint with fever - Septic arthritis; aspirate immediately; can coexist with RA',
      '🔴 CRITICAL: Neck pain with limb weakness/numbness - Cervical myelopathy from atlantoaxial subluxation; urgent imaging',
      '🔴 CRITICAL: Red, painful eye with vision changes - Scleritis; can perforate; urgent ophthalmology',
      '🔴 CRITICAL: Acute dyspnea on methotrexate - Possible MTX pneumonitis; stop drug, urgent evaluation',
      '🟠 URGENT: New dry cough and dyspnea - Interstitial lung disease; needs HRCT',
      '🟠 URGENT: Persistent fevers - Consider infection (immunosuppressed) or vasculitis',
      '🟠 URGENT: Leg ulcers or digital infarcts - Vasculitis; needs urgent assessment',
      '🟠 URGENT: Significant cytopenias on DMARD - Review drug, consider bone marrow suppression',
      '🟡 CAUTION: Rising LFTs on methotrexate - May need dose reduction; exclude other causes',
      '🟡 CAUTION: New sensory symptoms in hands/feet - Peripheral neuropathy; may be drug-related or vasculitic',
      '🟡 CAUTION: Persistent disease activity despite treatment - Needs treatment escalation; reassess diagnosis'
    ],

    complications: [
      'Joint destruction and deformity - Progressive if untreated; may require surgery',
      'Atlantoaxial subluxation - Potentially life-threatening; can cause cord compression',
      'Interstitial lung disease - Common cause of morbidity; can be disease or drug-related',
      'Cardiovascular disease - Accelerated atherosclerosis; major cause of mortality',
      'Osteoporosis - From disease activity and corticosteroid use',
      'Infections - Increased risk due to disease and immunosuppressive treatment',
      'Secondary Sjogren syndrome - Dry eyes, dry mouth',
      'Rheumatoid nodules - Occur in seropositive disease; generally benign',
      'Vasculitis - Can cause skin ulcers, neuropathy, organ damage',
      'Felty syndrome - RA + splenomegaly + neutropenia; increased infection risk',
      'Amyloidosis - Long-standing uncontrolled disease; renal involvement',
      'Drug toxicity - Methotrexate hepatotoxicity/pneumonitis, NSAID gastropathy'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

NATURAL HISTORY (UNTREATED):
• Progressive joint destruction
• Significant disability within 10 years in most
• Reduced life expectancy by 3-10 years
• Extra-articular complications increase morbidity

WITH MODERN TREATMENT:
• Remission achievable in 30-50% with early aggressive therapy
• Low disease activity in additional 30-40%
• Joint damage largely preventable if treated early
• Near-normal function and quality of life possible
• Mortality gap with general population narrowing

FACTORS AFFECTING PROGNOSIS:

POOR PROGNOSTIC FACTORS:
• High RF and/or anti-CCP titers
• Early erosions on X-ray
• High disease activity at presentation
• Many swollen joints
• Elevated ESR/CRP
• Extra-articular disease
• Smoking
• Delay in starting DMARDs
• Poor response to initial treatment

BETTER PROGNOSTIC FACTORS:
• Seronegative disease
• Early diagnosis and treatment
• Rapid response to DMARDs
• Achievement of remission
• Non-smoker
• Good functional status at diagnosis

MORTALITY:
• Increased compared to general population (SMR 1.5-2.0)
• Main causes: Cardiovascular disease, infections, malignancy, lung disease
• Mortality gap improving with better treatment
• CV risk equivalent to diabetes - manage aggressively

KEY MESSAGE:
Early, aggressive treatment dramatically improves outcomes. The goal is remission, and many patients can achieve this with modern therapy.`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION (Preventing RA):
• SMOKING CESSATION: Strongest modifiable risk factor; increases risk 2-4 fold
• Periodontal health: Treat gum disease (P. gingivalis linked to RA)
• Healthy weight: Obesity increases risk
• Limited evidence for other interventions

SECONDARY PREVENTION (Early Detection):
• Awareness of early symptoms: Symmetric joint swelling, morning stiffness >1 hour
• Early referral: Suspected RA to rheumatologist within 6 weeks
• Screening relatives: Family history increases risk
• Anti-CCP positive individuals: Higher risk; close monitoring if symptoms develop

TERTIARY PREVENTION (Preventing Complications):

JOINT DAMAGE PREVENTION:
• Early DMARD therapy (within 3 months)
• Treat-to-target approach
• Regular monitoring and treatment adjustment

CARDIOVASCULAR RISK REDUCTION:
• RA is independent CV risk factor
• Aggressive management of traditional risk factors
• Control disease activity (reduces CV risk)
• Smoking cessation
• Statin therapy as indicated

OSTEOPOROSIS PREVENTION:
• Minimize corticosteroid use
• Calcium and vitamin D supplementation
• Bisphosphonates if on long-term steroids or high risk

INFECTION PREVENTION:
• Vaccinations (before starting DMARDs if possible)
• Annual influenza vaccine
• Pneumococcal vaccine
• Shingrix (inactivated zoster vaccine)
• Avoid live vaccines on immunosuppression
• Screen for TB before biologics
• Hepatitis B screening and vaccination

DRUG TOXICITY PREVENTION:
• Folic acid with methotrexate
• Regular monitoring (blood tests)
• Patient education on warning signs
• Gastroprotection with NSAIDs if risk factors`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS RHEUMATOID ARTHRITIS?
Rheumatoid arthritis (RA) is a condition where your immune system attacks your own joints, causing inflammation, pain, and swelling. It usually affects the same joints on both sides of the body (like both hands or both knees). If not treated, it can damage your joints permanently.

THE GOOD NEWS:
• RA is very treatable
• Modern medicines can stop the disease from damaging your joints
• Many people with RA achieve remission (no active disease)
• You can live a full, active life with proper treatment

UNDERSTANDING YOUR TREATMENT:

DISEASE-MODIFYING DRUGS (DMARDs):
These medicines actually stop the disease from progressing:
• METHOTREXATE is the most important medicine - it works slowly but effectively
• It takes 2-3 months to feel the full effect
• Take it EVERY WEEK on the SAME DAY
• Take FOLIC ACID as prescribed (prevents side effects)
• These medicines are much more important than painkillers

PAINKILLERS AND ANTI-INFLAMMATORIES:
• These help with pain but do NOT stop joint damage
• Use them as needed, but DMARDs are the priority

STEROIDS (Prednisolone):
• Work quickly but have many side effects with long-term use
• Usually used short-term while waiting for DMARDs to work
• Do not stop suddenly if youve been on them for more than a few weeks

IMPORTANT ABOUT YOUR MEDICINES:
• Take your medicines exactly as prescribed
• Do not stop medicines without discussing with your doctor
• Tell your doctor about ALL other medicines you take
• Report side effects but do not stop DMARDs without medical advice

IMPORTANT SIDE EFFECTS TO REPORT:
• Mouth ulcers, nausea, or vomiting on methotrexate
• New cough or shortness of breath
• Signs of infection (fever, feeling unwell)
• Unusual bruising or bleeding
• Yellow skin or eyes

LIFESTYLE RECOMMENDATIONS:

EXERCISE IS IMPORTANT:
• Regular gentle exercise helps your joints and overall health
• Good options: Walking, swimming, cycling, tai chi
• Strengthening exercises help protect joints
• Rest during flares, but stay active overall

STOP SMOKING:
• Smoking makes RA worse and reduces how well medicines work
• Quitting is one of the best things you can do for your RA

PROTECT YOUR HEART:
• RA increases heart disease risk
• Check and control blood pressure, cholesterol, blood sugar
• Stay active, maintain healthy weight
• Take prescribed heart medicines

REGULAR MONITORING:
• Blood tests are essential to check your medicines are safe
• Never miss monitoring appointments
• X-rays periodically check for joint damage

WARNING SIGNS - SEEK URGENT HELP:
• High fever with joint swelling (could be infected joint)
• Neck pain with arm/leg weakness or numbness
• Red, painful eye with vision changes
• Severe shortness of breath
• Signs of serious infection

PREGNANCY AND RA:
• Discuss with your doctor BEFORE trying to conceive
• Some medicines must be stopped months before pregnancy
• Pregnancy can be safe with proper planning

VACCINATIONS:
• Get flu vaccine every year
• Pneumonia vaccine as recommended
• Avoid live vaccines while on treatment
• Complete vaccinations before starting treatment if possible

REMEMBER:
• RA is a lifelong condition but very controllable
• Early treatment prevents joint damage
• Take your medicines regularly - they protect your joints
• Stay active and maintain a healthy lifestyle
• Keep all your appointments and monitoring tests

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

The most important thing in RA is starting treatment early and taking your disease-modifying medicines regularly. These medicines protect your joints from permanent damage. With proper treatment, most people with RA can live normal, active lives.`,

    references: [
      'Smolen JS, Landewé RBM, Bijlsma JWJ, et al. EULAR recommendations for the management of rheumatoid arthritis with synthetic and biological disease-modifying antirheumatic drugs: 2019 update. Ann Rheum Dis. 2020;79(6):685-699.',
      'Fraenkel L, Bathon JM, England BR, et al. 2021 American College of Rheumatology Guideline for the Treatment of Rheumatoid Arthritis. Arthritis Care Res (Hoboken). 2021;73(7):924-939.',
      'Aletaha D, Neogi T, Silman AJ, et al. 2010 Rheumatoid arthritis classification criteria: an American College of Rheumatology/European League Against Rheumatism collaborative initiative. Arthritis Rheum. 2010;62(9):2569-2581.',
      'Smolen JS, Aletaha D, McInnes IB. Rheumatoid arthritis. Lancet. 2016;388(10055):2023-2038.',
      'McInnes IB, Schett G. The pathogenesis of rheumatoid arthritis. N Engl J Med. 2011;365(23):2205-2219.',
      'Singh JA, Saag KG, Bridges SL Jr, et al. 2015 American College of Rheumatology Guideline for the Treatment of Rheumatoid Arthritis. Arthritis Care Res (Hoboken). 2016;68(1):1-25.',
      'Combe B, Landewe R, Daien CI, et al. 2016 update of the EULAR recommendations for the management of early arthritis. Ann Rheum Dis. 2017;76(6):948-959.',
      'Deane KD, Demoruelle MK, Kelmenson LB, et al. Genetic and environmental risk factors for rheumatoid arthritis. Best Pract Res Clin Rheumatol. 2017;31(1):3-18.',
      'Agca R, Heslinga SC, Rollefstad S, et al. EULAR recommendations for cardiovascular disease risk management in patients with rheumatoid arthritis and other forms of inflammatory joint disorders: 2015/2016 update. Ann Rheum Dis. 2017;76(1):17-28.',
      'Furst DE, Pangan AL, Engel-Gonzalez P. Methotrexate in rheumatoid arthritis. Arthritis Res Ther. 2006;8(3):215.',
      'Mody GM, Cardiel MH. Challenges in the management of rheumatoid arthritis in developing countries. Best Pract Res Clin Rheumatol. 2008;22(4):621-641.',
      'Solomon DH, Karlson EW, Rimm EB, et al. Cardiovascular morbidity and mortality in women diagnosed with rheumatoid arthritis. Circulation. 2003;107(9):1303-1307.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 83. GLAUCOMA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '83',
    name: 'Glaucoma',
    slug: 'glaucoma',
    specialty: 'Ophthalmology',
    bodySystem: 'Neurological',
    icdCode: 'H40',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `Glaucoma is a group of progressive optic neuropathies characterized by degeneration of retinal ganglion cells and their axons, resulting in a distinctive pattern of optic nerve damage (cupping) and corresponding visual field loss. It is the leading cause of irreversible blindness worldwide, affecting over 80 million people globally, with numbers projected to increase significantly with aging populations.

The pathophysiology involves progressive loss of retinal ganglion cells and damage to the optic nerve head. While elevated intraocular pressure (IOP) is the most important modifiable risk factor, glaucoma can occur at any IOP level (normal tension glaucoma), and not everyone with elevated IOP develops glaucoma (ocular hypertension). The damage results from a combination of mechanical compression and vascular insufficiency at the optic nerve head.

There are two main types: Primary Open-Angle Glaucoma (POAG), which is chronic, progressive, and usually asymptomatic until advanced; and Angle-Closure Glaucoma, which can present acutely (emergency) or chronically. Secondary glaucomas result from other ocular or systemic conditions.

In Africa, glaucoma is a major public health problem. African populations have among the highest prevalence of POAG globally (4-8% in those over 40), with earlier onset and more aggressive disease compared to European populations. The condition often presents late due to its asymptomatic nature, limited access to eye care, and lack of screening programs. Many patients are blind or severely visually impaired at diagnosis. Access to medications and surgery varies significantly across the continent.

Management focuses on lowering IOP to prevent further optic nerve damage. Treatment options include topical medications (prostaglandin analogues, beta-blockers, alpha-agonists, carbonic anhydrase inhibitors), laser procedures, and surgery. The target IOP is individualized based on disease severity and rate of progression.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Acute angle-closure is an EMERGENCY: Severe eye pain, halos, nausea, mid-dilated fixed pupil; requires immediate IOP lowering

🔴 CRITICAL POINT 2 - POAG is ASYMPTOMATIC until advanced: Central vision preserved until late; peripheral vision lost first; screen high-risk populations

🔴 CRITICAL POINT 3 - African ancestry is a major risk factor: 4-8x higher POAG risk; earlier onset; more aggressive; screen from age 35-40

🔴 CRITICAL POINT 4 - Vision loss is IRREVERSIBLE: Treatment prevents further damage but cannot restore lost vision; early detection critical

🟠 CRITICAL POINT 5 - IOP is not diagnostic: Normal IOP does not exclude glaucoma (normal tension glaucoma); high IOP alone is not glaucoma (ocular hypertension)

🟠 CRITICAL POINT 6 - Cup-to-disc ratio >0.5 or asymmetry >0.2 is suspicious: Refer for comprehensive evaluation

🟠 CRITICAL POINT 7 - Prostaglandin analogues are FIRST-LINE: Most effective, once daily dosing, good tolerability; e.g., latanoprost

🟡 CRITICAL POINT 8 - Topical beta-blockers: Avoid in asthma, COPD, bradycardia, heart block

🟡 CRITICAL POINT 9 - Adherence is critical: Lifelong treatment; poor adherence is major cause of progression`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Affects over 80 million people worldwide (2020)
• Leading cause of IRREVERSIBLE blindness globally
• Projected to affect 112 million by 2040
• POAG accounts for 70-90% of cases globally
• Angle-closure glaucoma more common in Asia
• 50% of glaucoma cases undiagnosed in developed countries; higher in developing countries

TYPES OF GLAUCOMA:

PRIMARY OPEN-ANGLE GLAUCOMA (POAG):
• Most common type (especially in Africa)
• Chronic, progressive, bilateral (often asymmetric)
• Open drainage angle on gonioscopy
• Usually asymptomatic until advanced

PRIMARY ANGLE-CLOSURE GLAUCOMA:
• Acute: Medical emergency (sudden IOP rise)
• Chronic: Gradual angle closure
• More common in Asia, hyperopes, women
• Anatomically predisposed (shallow anterior chamber)

SECONDARY GLAUCOMAS:
• Pseudoexfoliation glaucoma (common cause of secondary OAG)
• Pigmentary glaucoma
• Steroid-induced glaucoma
• Traumatic glaucoma
• Neovascular glaucoma (diabetes, CRVO)
• Uveitic glaucoma

RISK FACTORS FOR POAG:
• African ancestry (4-8x higher risk)
• Age (risk increases significantly after 40)
• Family history (first-degree relative increases risk 4-10x)
• Elevated IOP (major modifiable risk factor)
• Myopia (nearsightedness)
• Thin central cornea
• Diabetes mellitus
• Systemic hypertension (controversial)
• Obstructive sleep apnea

AFRICAN EPIDEMIOLOGY:
• POAG prevalence: 4-8% in those over 40 (among highest globally)
• Disease onset 10-15 years earlier than Europeans
• More aggressive disease course
• Higher proportion present with advanced disease/blindness
• Limited access to eye care and screening
• Fewer than 1 ophthalmologist per million population in many countries
• Medications often expensive or unavailable
• Surgery may be first-line due to access/adherence issues
• Cultural barriers to seeking eye care
• Pseudoexfoliation syndrome increasingly recognized

BLINDNESS BURDEN:
• Glaucoma causes 8-12% of global blindness
• In Africa, up to 20-30% of blindness may be glaucoma-related
• 50-90% of African glaucoma patients undiagnosed
• Many present already blind in one or both eyes`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

PRIMARY OPEN-ANGLE GLAUCOMA (POAG):

EARLY DISEASE:
• Usually ASYMPTOMATIC (the "silent thief of sight")
• No pain, no redness
• Central vision preserved initially
• May not notice peripheral vision loss

MODERATE DISEASE:
• Gradual peripheral visual field loss
• Difficulty with night driving
• Bumping into objects on sides
• Often still asymptomatic

ADVANCED DISEASE:
• Tunnel vision (only central island remains)
• Significant functional impairment
• May affect central vision
• At high risk of blindness

EXAMINATION FINDINGS:
• IOP: May be elevated (>21 mmHg) or normal
• Optic disc: Cupping (increased C/D ratio), thinning of neuroretinal rim
• ISNT rule violation (normal rim: Inferior > Superior > Nasal > Temporal)
• Disc hemorrhage (indicates progression)
• Nerve fiber layer defects
• Visual field: Arcuate scotomas, nasal step, paracentral defects

ACUTE ANGLE-CLOSURE GLAUCOMA:

SYMPTOMS (Sudden onset):
• Severe eye pain
• Headache (can be frontal or unilateral)
• Nausea and vomiting (can mimic acute abdomen or migraine)
• Seeing halos around lights
• Blurred vision
• Red eye

EXAMINATION FINDINGS:
• IOP: Very high (often 40-80 mmHg)
• Corneal edema (hazy, steamy cornea)
• Mid-dilated pupil, fixed, oval
• Shallow anterior chamber
• Conjunctival injection (ciliary flush)
• Closed angle on gonioscopy
• Reduced visual acuity

CHRONIC ANGLE-CLOSURE:
• Often asymptomatic
• Progressive angle closure
• May have intermittent symptoms (evening headaches, halos)
• IOP may be normal or elevated

SECONDARY GLAUCOMAS:

Pseudoexfoliation Glaucoma:
• White flaky material on lens capsule and pupil margin
• Often higher IOP and more fluctuation
• More aggressive than POAG
• Common in Africa

Neovascular Glaucoma:
• Secondary to diabetes, CRVO
• New vessels on iris (rubeosis)
• Often painful
• Very difficult to treat

Steroid-Induced:
• History of topical, periocular, or systemic steroids
• IOP usually normalizes after stopping steroids

OPTIC DISC CHANGES IN GLAUCOMA:
• Increased cup-to-disc ratio (>0.5 suspicious; >0.7 likely pathological)
• Vertical elongation of cup
• Asymmetry between eyes (>0.2 difference suspicious)
• Notching of neuroretinal rim (focal loss)
• Disc hemorrhage (splinter hemorrhage at disc margin)
• Pallor of neuroretinal rim
• Exposed lamina cribrosa (laminar dots)
• Bayoneting of vessels (double angulation)
• Peripapillary atrophy`,

    diagnosis: `DIAGNOSTIC APPROACH:

GLAUCOMA DIAGNOSIS REQUIRES:
1. Characteristic optic nerve damage (cupping)
2. Corresponding visual field defects
3. Open or closed angle on gonioscopy
4. Exclusion of secondary causes

CORE DIAGNOSTIC TESTS:

INTRAOCULAR PRESSURE (IOP):
• Goldmann applanation tonometry (gold standard)
• Normal: 10-21 mmHg (population average 15-16 mmHg)
• Single reading is inadequate (diurnal variation)
• High IOP alone is NOT diagnostic of glaucoma
• Normal IOP does NOT exclude glaucoma

GONIOSCOPY:
• Essential to classify glaucoma type
• Examines drainage angle
• Open angle: Structures visible to scleral spur or beyond
• Closed angle: Structures not visible
• Identifies secondary causes (neovascularization, pigment, pseudoexfoliation)

OPTIC DISC EXAMINATION:
• Dilated fundus examination essential
• Cup-to-disc ratio (C/D): Vertical and horizontal
• Neuroretinal rim: ISNT rule (I>S>N>T)
• Notching, hemorrhages
• Asymmetry between eyes
• Document with photographs or drawings

VISUAL FIELD TESTING (Perimetry):
• Standard Automated Perimetry (SAP) - gold standard
• Typical defects: Arcuate scotomas, nasal step, paracentral scotomas
• Correlate with optic disc findings
• Used for diagnosis and monitoring progression

ADDITIONAL TESTS:

PACHYMETRY (Central Corneal Thickness):
• Thin CCT (<520 μm) is risk factor for progression
• Affects IOP measurement accuracy
• Thinner cornea may underestimate true IOP

OPTICAL COHERENCE TOMOGRAPHY (OCT):
• Measures retinal nerve fiber layer (RNFL) thickness
• Detects damage before visual field loss
• Useful for early detection and monitoring
• Increasingly available in urban centers

DISC PHOTOGRAPHY:
• Baseline documentation
• Comparison over time
• Stereo photography if available

DIAGNOSTIC CRITERIA:

POAG DIAGNOSIS:
• Open angle on gonioscopy
• Glaucomatous optic neuropathy (cupping, rim loss)
• Corresponding visual field defects
• Absence of secondary causes

ANGLE-CLOSURE GLAUCOMA:
• Closed or narrow angle on gonioscopy
• Evidence of angle closure (PAS, elevated IOP)
• With or without optic neuropathy

OCULAR HYPERTENSION (Not Glaucoma):
• IOP >21 mmHg
• Normal optic disc
• Normal visual fields
• Open angles
• Requires monitoring; may need treatment based on risk

SUSPECT GLAUCOMA:
• Suspicious disc appearance
• Normal visual fields (or equivocal)
• Requires close monitoring

WHEN TO REFER FOR GLAUCOMA EVALUATION:
• Elevated IOP (>21 mmHg)
• C/D ratio >0.5 or asymmetry >0.2
• Suspicious optic disc appearance
• Visual field defect
• Family history with symptoms
• Acute angle-closure symptoms`,

    differentialDiagnosis: [
      'Ocular hypertension - Elevated IOP without optic nerve damage or field loss; monitor or treat based on risk',
      'Physiological cupping - Large cups bilaterally symmetric; normal rim; normal fields; no progression',
      'Optic disc drusen - Buried drusen can mimic cupping; B-scan ultrasound or OCT confirms',
      'Non-glaucomatous optic neuropathy - Arteritic/non-arteritic AION, optic neuritis; different disc appearance and field pattern',
      'Compressive optic neuropathy - Tumor, aneurysm; may have proptosis, motility issues; MRI indicated',
      'Optic disc coloboma - Congenital; inferior disc defect; nonprogressive',
      'Myopic disc - Tilted, pale discs in high myopia; differentiate from glaucoma',
      'Acute angle-closure vs acute iritis - Both painful red eye; iritis has small pupil, cells/flare; AC has fixed mid-dilated pupil, high IOP',
      'Acute angle-closure vs migraine - Both have headache, nausea; check IOP and pupil in any severe headache with visual symptoms',
      'Secondary glaucoma causes - Steroid use, trauma, uveitis, lens-related, neovascular'
    ],

    investigations: [
      {
        name: 'Intraocular Pressure (Tonometry)',
        indication: '🔴 ESSENTIAL - Goldmann applanation gold standard; elevated IOP is major risk factor; single reading insufficient',
        availability: 'BASIC'
      },
      {
        name: 'Dilated Fundus Examination (Optic Disc)',
        indication: '🔴 ESSENTIAL - Assess cup-to-disc ratio, rim, hemorrhages; document with photo or drawing',
        availability: 'BASIC'
      },
      {
        name: 'Gonioscopy',
        indication: '🔴 ESSENTIAL - Classifies glaucoma type (open vs closed angle); identifies secondary causes',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Visual Field Testing (Perimetry)',
        indication: '🔴 ESSENTIAL - Documents functional damage; monitors progression; standard automated perimetry preferred',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Central Corneal Thickness (Pachymetry)',
        indication: '🟠 IMPORTANT - Thin cornea is risk factor; affects IOP interpretation',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Optical Coherence Tomography (OCT)',
        indication: '🟠 IMPORTANT - RNFL thickness; detects early damage; monitors progression; increasingly essential',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Optic Disc Photography',
        indication: '🟠 IMPORTANT - Baseline documentation; comparison for progression',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Slit Lamp Examination',
        indication: '🔴 ESSENTIAL - Anterior segment evaluation; pseudoexfoliation, pigment dispersion, lens status, anterior chamber depth',
        availability: 'BASIC'
      },
      {
        name: 'Visual Acuity',
        indication: '🔴 ESSENTIAL - Baseline and monitoring; usually preserved until advanced glaucoma',
        availability: 'BASIC'
      },
      {
        name: 'Blood Glucose/HbA1c',
        indication: '🟡 IF INDICATED - Screen for diabetes; risk factor; causes neovascular glaucoma',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ACUTE ANGLE-CLOSURE IS EMERGENCY: Immediate IOP lowering required; see emergency protocol below',
        '⚡ CONFIRM DIAGNOSIS: IOP, gonioscopy, optic disc assessment, visual fields',
        '⚡ DETERMINE GLAUCOMA TYPE: Open angle vs angle closure; primary vs secondary',
        '⚡ ASSESS SEVERITY: Degree of disc damage and visual field loss',
        '⚡ SET TARGET IOP: Individualized based on severity; generally 25-50% reduction from baseline',
        '⚡ INITIATE TREATMENT: First-line is usually prostaglandin analogue for POAG',
        '⚡ PATIENT EDUCATION: Lifelong treatment, importance of adherence, irreversible nature of damage',
        '⚡ BASELINE DOCUMENTATION: IOP, disc photos, visual fields, OCT if available',
        '⚡ ASSESS OTHER EYE: Glaucoma often bilateral; examine fellow eye',
        '⚡ IDENTIFY CONTRIBUTING FACTORS: Steroid use, trauma history, systemic conditions'
      ],
      
      pharmacological: [
        {
          drug: 'Latanoprost 0.005% (Prostaglandin Analogue)',
          indication: '🔴 FIRST-LINE for POAG - Most effective IOP lowering (25-35%); once daily dosing; well tolerated',
          dose: 'One drop in affected eye(s) once daily in the EVENING (most effective timing)',
          route: 'Topical ophthalmic',
          duration: 'Lifelong in most cases',
          contraindications: ['Active intraocular inflammation', 'Known hypersensitivity'],
          sideEffects: ['Iris color change (darkening)', 'Eyelash growth', 'Periorbital skin darkening', 'Conjunctival hyperemia', 'Rare: Cystoid macular edema, uveitis'],
          alternatives: ['Travoprost', 'Bimatoprost', 'Tafluprost']
        },
        {
          drug: 'Timolol 0.5% (Beta-Blocker)',
          indication: '🔴 FIRST-LINE ALTERNATIVE or ADJUNCT - Effective (20-25% IOP reduction); twice daily; inexpensive; widely available',
          dose: 'One drop in affected eye(s) twice daily (morning and evening). Gel formulation once daily',
          route: 'Topical ophthalmic',
          duration: 'Lifelong',
          contraindications: ['Asthma', 'COPD', 'Bradycardia', 'Heart block (2nd/3rd degree)', 'Decompensated heart failure'],
          sideEffects: ['Bradycardia', 'Bronchospasm', 'Hypotension', 'Fatigue', 'Depression', 'Decreased libido', 'Masking of hypoglycemia'],
          alternatives: ['Betaxolol (cardioselective, less pulmonary effect)']
        },
        {
          drug: 'Brimonidine 0.2% (Alpha-2 Agonist)',
          indication: '🟠 SECOND-LINE or ADJUNCT - 20-25% IOP reduction; neuroprotective properties suggested',
          dose: 'One drop in affected eye(s) twice or three times daily',
          route: 'Topical ophthalmic',
          duration: 'Long-term',
          contraindications: ['MAO inhibitor use', 'Infants/young children (CNS depression)', 'Severe cardiovascular disease'],
          sideEffects: ['Allergic conjunctivitis (common - up to 25%)', 'Dry mouth', 'Fatigue', 'Drowsiness'],
          alternatives: ['Apraclonidine (more allergy)']
        },
        {
          drug: 'Dorzolamide 2% (Topical Carbonic Anhydrase Inhibitor)',
          indication: '🟠 SECOND-LINE or ADJUNCT - 15-20% IOP reduction; useful add-on therapy',
          dose: 'One drop in affected eye(s) three times daily (or twice daily as adjunct)',
          route: 'Topical ophthalmic',
          duration: 'Long-term',
          contraindications: ['Sulfonamide allergy', 'Severe renal impairment'],
          sideEffects: ['Stinging/burning on instillation', 'Bitter taste', 'Superficial punctate keratitis', 'Rarely systemic CAI effects'],
          alternatives: ['Brinzolamide (less stinging)']
        },
        {
          drug: 'Fixed Combination: Dorzolamide/Timolol',
          indication: '🟠 COMBINATION THERAPY - When monotherapy insufficient; improves adherence (fewer drops)',
          dose: 'One drop in affected eye(s) twice daily',
          route: 'Topical ophthalmic',
          duration: 'Long-term',
          contraindications: ['Contraindications of both components'],
          sideEffects: ['Combined side effects of both drugs'],
          alternatives: ['Brimonidine/Timolol', 'Latanoprost/Timolol']
        },
        {
          drug: 'Fixed Combination: Latanoprost/Timolol',
          indication: '🟠 COMBINATION THERAPY - Prostaglandin + beta-blocker; maximal medical therapy component',
          dose: 'One drop in affected eye(s) once daily in the morning',
          route: 'Topical ophthalmic',
          duration: 'Long-term',
          contraindications: ['Contraindications of both components'],
          sideEffects: ['Combined side effects'],
          alternatives: ['Separate medications if combination not available']
        },
        {
          drug: 'Acetazolamide (Systemic CAI)',
          indication: '🔴 ACUTE ANGLE-CLOSURE / EMERGENCY - Rapid IOP reduction; short-term use',
          dose: 'Acute: 500mg IV/PO stat, then 250mg PO every 6 hours. Max: 1g/day. Short-term only',
          route: 'PO/IV',
          duration: 'Short-term (days); not for chronic use due to side effects',
          contraindications: ['Sulfonamide allergy', 'Severe renal/hepatic disease', 'Hypokalemia', 'Adrenal insufficiency'],
          sideEffects: ['Paresthesias', 'Metallic taste', 'GI upset', 'Hypokalemia', 'Metabolic acidosis', 'Kidney stones', 'Aplastic anemia (rare)'],
          alternatives: ['Mannitol IV for very high IOP']
        },
        {
          drug: 'Mannitol 20% IV',
          indication: '🔴 ACUTE ANGLE-CLOSURE / EMERGENCY - For very high IOP not responding to other measures',
          dose: '1-2 g/kg IV over 30-60 minutes. Usually 250-500mL of 20% solution',
          route: 'IV',
          duration: 'Single dose; can repeat once if needed',
          contraindications: ['Anuria', 'Severe dehydration', 'Pulmonary edema', 'Active intracranial bleeding'],
          sideEffects: ['Fluid overload', 'Electrolyte imbalance', 'Headache', 'Nausea'],
          alternatives: ['Glycerol PO']
        },
        {
          drug: 'Pilocarpine 2-4% (Miotic)',
          indication: '🔴 ACUTE ANGLE-CLOSURE - Opens drainage angle by pulling iris away; use AFTER IOP lowering',
          dose: 'Acute: One drop every 15-30 minutes for 1-2 hours. Maintenance: One drop 3-4 times daily',
          route: 'Topical ophthalmic',
          duration: 'Acute setting; rarely used long-term now',
          contraindications: ['Neovascular glaucoma', 'Uveitis', 'Posterior synechiae'],
          sideEffects: ['Miosis (dim vision)', 'Brow ache', 'Accommodative spasm', 'Retinal detachment risk (rare)'],
          alternatives: ['Laser peripheral iridotomy is definitive treatment for angle closure']
        }
      ],
      
      nonPharmacological: [
        'ADHERENCE SUPPORT: Education on lifelong treatment; simplify regimen; once-daily dosing preferred',
        'PROPER DROP TECHNIQUE: Wash hands; tilt head back; pull lower lid; one drop in pocket; close eyes 1-2 min; punctal occlusion',
        'AVOID DROP WASTAGE: One drop is sufficient; wait 5 minutes between different drops',
        'MEDICATION STORAGE: Some drops need refrigeration; check package insert',
        'REGULAR FOLLOW-UP: Essential for monitoring progression and adjusting treatment',
        'LIFESTYLE: Regular exercise may lower IOP slightly; avoid head-down positions in angle closure',
        'AVOID MEDICATIONS THAT RAISE IOP: Systemic/topical steroids (use with caution; monitor IOP)',
        'FAMILY SCREENING: First-degree relatives should be screened regularly',
        'VISION REHABILITATION: For advanced disease; low vision aids; mobility training',
        'DRIVING ASSESSMENT: Visual field requirements for driving; may need to stop driving'
      ],
      
      surgical: [
        'LASER PERIPHERAL IRIDOTOMY (LPI): DEFINITIVE for angle-closure; creates hole in iris to relieve pupillary block; can be prophylactic in fellow eye',
        'SELECTIVE LASER TRABECULOPLASTY (SLT): First-line alternative to drops; can be repeated; 20-30% IOP reduction; good option for adherence issues',
        'ARGON LASER TRABECULOPLASTY (ALT): Older laser technique; limited repeatability',
        'TRABECULECTOMY: Gold standard filtering surgery; creates new drainage pathway; 60-80% success; complications include bleb leak, infection, hypotony',
        'GLAUCOMA DRAINAGE DEVICES: Ahmed, Baerveldt valves; for failed trabeculectomy or high-risk cases',
        'MINIMALLY INVASIVE GLAUCOMA SURGERY (MIGS): iStent, Hydrus, XEN gel stent; lower risk than trabeculectomy; moderate IOP lowering',
        'CYCLOPHOTOCOAGULATION: Destroys ciliary body; for refractory glaucoma; may need repeat; can cause phthisis',
        'LENS EXTRACTION: In angle closure with cataract; opens angle; may be definitive treatment'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Acute angle-closure glaucoma - Severe eye pain, halos, fixed mid-dilated pupil, high IOP; EMERGENCY',
        '🔴 IMMEDIATE: IOP >40 mmHg with symptoms - Requires urgent IOP lowering',
        '🔴 IMMEDIATE: Neovascular glaucoma - Rubeosis, high IOP; often needs urgent surgery',
        '🟠 URGENT: Newly diagnosed glaucoma - For comprehensive assessment and treatment initiation',
        '🟠 URGENT: Glaucoma not controlled on maximum medical therapy - Consider laser or surgery',
        '🟠 URGENT: Rapidly progressing glaucoma despite treatment - Needs escalation',
        '🟠 URGENT: Disc hemorrhage - Sign of progression; reassess treatment',
        '🟡 ROUTINE: Elevated IOP with normal disc/fields - Ocular hypertension; assess risk and monitor',
        '🟡 ROUTINE: Suspicious optic disc (C/D >0.5, asymmetry) - Comprehensive glaucoma evaluation',
        '🟡 ROUTINE: Family history of glaucoma with symptoms or signs - Screening examination',
        '🟡 ROUTINE: Established glaucoma for regular monitoring - Per local protocols'
      ],
      
      monitoring: [
        'IOP: Every visit; target individualized (usually 25-50% reduction); at consistent time of day',
        'OPTIC DISC: Document cup-to-disc ratio; photographs; compare over time for progression',
        'VISUAL FIELDS: Every 6-12 months for stable disease; more frequently if progressing or new diagnosis',
        'OCT RNFL: Every 6-12 months if available; detects progression before visual field changes',
        'GONIOSCOPY: Periodically; especially if angle closure risk or on miotics',
        'VISUAL ACUITY: Each visit; significant change may indicate advanced disease or other pathology',
        'ADHERENCE ASSESSMENT: Ask about drop use; check refill history; bottle counting',
        'MEDICATION TOLERANCE: Side effects; allergy; consider alternatives if problems',
        'SYSTEMIC EFFECTS: Check pulse if on beta-blockers; ask about respiratory symptoms',
        'FELLOW EYE: Glaucoma is usually bilateral; monitor both eyes'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE ANGLE-CLOSURE - EMERGENCY PROTOCOL:

IMMEDIATE TREATMENT (within minutes):
1. Acetazolamide 500mg IV/PO stat
2. Timolol 0.5% one drop
3. Brimonidine 0.2% one drop (or apraclonidine)
4. Prednisolone acetate 1% one drop (reduces inflammation)
5. Pilocarpine 2% - only AFTER IOP starts to fall (fixed pupil won't respond initially)
6. If IOP >50-60 or not responding: Mannitol 1-2g/kg IV over 30-60 minutes
7. Position: Supine (may help open angle)
8. Analgesia and antiemetic as needed

DEFINITIVE TREATMENT:
• Laser peripheral iridotomy (LPI) once cornea clears and inflammation controlled
• LPI to fellow eye prophylactically (high risk of attack)

CHRONIC OPEN-ANGLE GLAUCOMA MANAGEMENT:

SETTING TARGET IOP:
• Mild damage: 25% reduction from baseline
• Moderate damage: 30-40% reduction
• Severe damage: 40-50% reduction
• Lower targets if progression despite treatment

TREATMENT ALGORITHM:
Step 1: Prostaglandin analogue (latanoprost) once daily
        OR Beta-blocker (timolol) twice daily if prostaglandin contraindicated
        OR SLT (laser) as primary treatment

Step 2: Add second medication (e.g., add timolol to prostaglandin)
        OR Use fixed combination

Step 3: Add third medication (e.g., brimonidine or dorzolamide)
        Consider laser (SLT) if not done

Step 4: Maximum medical therapy with 3-4 medications
        Consider surgery if target IOP not achieved

Step 5: Trabeculectomy or glaucoma drainage device
        MIGS in selected cases

FOLLOW-UP SCHEDULE:

NEWLY DIAGNOSED:
• 1-2 weeks: Check IOP response to treatment
• 1 month: Reassess; adjust treatment if needed
• 3 months: Visual fields, OCT if available
• Then every 3-6 months until stable

STABLE GLAUCOMA:
• Every 6-12 months: IOP, disc assessment
• Annual: Visual fields
• Annual: OCT if available

PROGRESSING GLAUCOMA:
• More frequent monitoring (every 3-4 months)
• Escalate treatment
• Consider surgery earlier

ADVANCED GLAUCOMA:
• Very frequent monitoring
• Low threshold for surgery
• Aggressive IOP lowering
• Visual rehabilitation

SPECIAL SITUATIONS:

OCULAR HYPERTENSION (high IOP, no damage):
• Assess risk: IOP level, CCT, disc appearance, family history
• Low risk: Monitor every 6-12 months
• High risk: Consider treatment or close monitoring (every 4-6 months)

NORMAL TENSION GLAUCOMA:
• Still treat by lowering IOP (30% reduction target)
• May need lower target pressures
• Consider systemic factors (hypotension, vasospasm)

MEDICATION ADHERENCE CHALLENGES:
• Simplify regimen (once-daily drops, combinations)
• SLT as alternative to or in addition to drops
• Fixed combinations reduce drop burden
• Family involvement in administration
• Consider surgery if adherence is major issue

AFRICAN-SPECIFIC CONSIDERATIONS:
• Screen earlier (age 35-40) due to higher risk
• Family screening important
• Surgery may be first-line in some settings (adherence, access, cost)
• Trabeculectomy with antimetabolites often needed
• Consider SLT where available - good option for resource-limited settings`
    },

    redFlags: [
      '🔴 CRITICAL: Severe eye pain with halos, nausea, fixed mid-dilated pupil - Acute angle closure; EMERGENCY',
      '🔴 CRITICAL: IOP >40 mmHg with symptoms - Requires immediate IOP lowering',
      '🔴 CRITICAL: Sudden vision loss in known glaucoma patient - May indicate advanced damage or other pathology',
      '🔴 CRITICAL: New rubeosis iridis (iris neovascularization) - Neovascular glaucoma; very high risk',
      '🟠 URGENT: Disc hemorrhage - Sign of progression; indicates need for treatment escalation',
      '🟠 URGENT: Visual field progression despite treatment - Reassess target IOP; escalate therapy',
      '🟠 URGENT: IOP consistently above target - Need additional treatment',
      '🟠 URGENT: New glaucoma in young patient - Consider secondary causes; more aggressive disease',
      '🟡 CAUTION: Significant IOP asymmetry between eyes - May indicate secondary cause',
      '🟡 CAUTION: Patient non-adherent to treatment - Address barriers; consider alternatives',
      '🟡 CAUTION: New respiratory or cardiac symptoms on beta-blockers - Switch medication class'
    ],

    complications: [
      'Vision loss and blindness - Irreversible; goal is prevention through treatment',
      'Falls and injuries - From peripheral vision loss; especially in elderly',
      'Loss of driving ability - Visual field requirements not met',
      'Medication side effects - Topical and systemic; see individual drugs',
      'Trabeculectomy complications - Bleb leak, infection (blebitis, endophthalmitis), hypotony, cataract',
      'Malignant glaucoma - Rare; aqueous misdirection after surgery; difficult to treat',
      'Tube erosion or exposure - With glaucoma drainage devices',
      'Depression and anxiety - Due to chronic disease and vision loss',
      'Reduced quality of life - Functional impairment from vision loss',
      'Socioeconomic impact - Work disability, dependence on others'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

GENERAL OUTLOOK:
• Glaucoma is treatable but not curable
• Vision loss is IRREVERSIBLE - treatment prevents further damage
• Most patients maintain useful vision with proper treatment
• Early detection and treatment are key

POAG PROGNOSIS:
• Slowly progressive over years to decades
• With treatment: Most maintain functional vision for life
• Without treatment: Progressive to blindness over 15-20 years
• Advanced disease at diagnosis has worse outcomes

FACTORS AFFECTING PROGNOSIS:

WORSE PROGNOSIS:
• African ancestry (more aggressive disease)
• Young age at diagnosis
• High baseline IOP
• Advanced damage at presentation
• Thin central cornea
• Disc hemorrhages
• Family history of blindness from glaucoma
• Poor adherence to treatment
• Pseudoexfoliation

BETTER PROGNOSIS:
• Early detection and treatment
• Good IOP control
• Adherence to treatment
• Mild damage at diagnosis
• Good response to medical therapy

PROGRESSION RATES:
• With treatment: ~5% progress to blindness over 15 years
• Without treatment: Much higher progression rates
• Visual field loss: Average 0.5-1 dB/year if progressing (faster in some)

AFRICAN POPULATIONS:
• More aggressive disease course
• Higher risk of blindness
• Often present late with advanced disease
• Earlier onset
• May need more aggressive treatment

ACUTE ANGLE-CLOSURE:
• Good prognosis if treated promptly
• LPI is usually curative (prevents further attacks)
• Some residual optic nerve damage may occur from acute episode
• Fellow eye at high risk - needs prophylactic treatment

KEY MESSAGE:
With early detection and proper treatment, most glaucoma patients maintain useful vision throughout their lives. The challenge is identifying patients early before significant damage occurs.`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
• Currently no proven primary prevention
• Healthy lifestyle may have modest benefit
• Avoid prolonged steroid use when possible

SECONDARY PREVENTION (Early Detection):

SCREENING HIGH-RISK GROUPS:
• African ancestry: Screen from age 35-40; every 2-3 years
• First-degree relative with glaucoma: Screen from age 40; every 1-2 years
• Diabetes: Include glaucoma check in annual eye exam
• High myopia: Regular comprehensive eye exams
• Previous eye injury: Long-term monitoring
• Steroid users: Regular IOP checks

SCREENING COMPONENTS:
• IOP measurement
• Optic disc examination
• Visual field testing if suspicious
• OCT if available

ANGLE-CLOSURE PREVENTION:
• Laser peripheral iridotomy in anatomically at-risk eyes
• Prophylactic LPI in fellow eye after acute attack
• Early cataract surgery in selected cases

TERTIARY PREVENTION (Preventing Blindness):
• Early, aggressive treatment
• Achieve and maintain target IOP
• Regular monitoring for progression
• Treatment escalation when needed
• Good adherence

PUBLIC HEALTH STRATEGIES FOR AFRICA:
• Community screening programs
• Training of primary care workers in basic eye examination
• Awareness campaigns (glaucoma often unknown in communities)
• Family-based screening approach
• Affordable medications
• Task-shifting for glaucoma care
• Integration with diabetic eye screening

FAMILY SCREENING:
• All first-degree relatives of glaucoma patients should be screened
• 10x higher risk in family members
• Screen earlier than general population
• Repeat regularly (every 1-2 years)`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS GLAUCOMA?
Glaucoma is a condition that damages the optic nerve - the connection between your eye and brain. This damage is usually caused by high pressure inside the eye. Without treatment, glaucoma leads to permanent vision loss, starting with your side (peripheral) vision.

WHY IS EARLY DETECTION IMPORTANT?
• Glaucoma has NO symptoms until very advanced
• You will not feel the pressure
• You will not notice vision loss until it is severe
• Vision lost to glaucoma CANNOT be restored
• Treatment can only prevent FURTHER damage

THE GOOD NEWS:
• Glaucoma is treatable
• If found early and treated properly, most people keep useful vision
• Treatment is usually eye drops - simple to use daily

HOW TO USE YOUR EYE DROPS:

PROPER TECHNIQUE:
1. Wash your hands
2. Tilt your head back or lie down
3. Pull down your lower eyelid to make a pocket
4. Look up and squeeze ONE drop into the pocket
5. Close your eye gently (do not squeeze)
6. Press gently on the inner corner of your eye for 1-2 minutes (prevents medicine going into your body)
7. If using more than one type of drop, wait 5 minutes between different drops

IMPORTANT TIPS:
• Use drops at the same time every day
• Set a reminder on your phone or link to daily routine (meals, bedtime)
• Evening is best for prostaglandin drops (like latanoprost)
• Do not miss doses - even one missed dose can cause pressure to rise
• Never run out of medicine - get refills before you run out
• Store drops as directed (some need refrigeration)

SIDE EFFECTS TO WATCH FOR:
• Latanoprost: May darken eye color and increase eyelash growth (normal)
• Timolol: May cause slow heartbeat, breathing problems (tell doctor if you have asthma or heart problems)
• Brimonidine: May cause red, itchy eyes (allergic reaction - tell doctor)

THIS IS LIFELONG TREATMENT:
• You will need treatment for the rest of your life
• Even when you feel fine, the pressure can damage your nerve
• Stopping treatment will cause the pressure to rise again
• Never stop without talking to your doctor

WHY FOLLOW-UP IS ESSENTIAL:
• To check if treatment is working
• To check for progression
• To adjust treatment if needed
• Attend ALL appointments

FAMILY MEMBERS:
• Glaucoma runs in families
• If you have glaucoma, tell your close relatives
• Parents, siblings, and children should be checked regularly
• Early detection in family members can prevent blindness

EMERGENCY - SEEK IMMEDIATE HELP IF:
• Severe eye pain with headache
• Sudden blurred vision
• Seeing halos (colored rings around lights)
• Nausea and vomiting with eye symptoms
• These could be acute glaucoma - a MEDICAL EMERGENCY

LIVING WITH GLAUCOMA:
• Take your drops every day without fail
• Keep all your appointments
• Tell your doctor about all other medicines you take
• Good lighting helps when you have reduced vision
• Be careful with stairs and curbs (side vision affected)
• Inform DMV/licensing authority if required in your country

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Glaucoma is a serious condition, but with daily eye drops and regular check-ups, you can prevent further vision loss and keep your sight. The most important things are: use your drops every day, never miss appointments, and tell your family to get checked. This is a lifelong commitment that protects your vision.`,

    references: [
      'European Glaucoma Society. Terminology and Guidelines for Glaucoma. 5th Edition. 2020.',
      'American Academy of Ophthalmology. Primary Open-Angle Glaucoma Preferred Practice Pattern. 2020.',
      'American Academy of Ophthalmology. Primary Angle Closure Disease Preferred Practice Pattern. 2020.',
      'Weinreb RN, Aung T, Medeiros FA. The pathophysiology and treatment of glaucoma: a review. JAMA. 2014;311(18):1901-1911.',
      'Tham YC, Li X, Wong TY, et al. Global prevalence of glaucoma and projections of glaucoma burden through 2040: a systematic review and meta-analysis. Ophthalmology. 2014;121(11):2081-2090.',
      'Kyari F, Abdull MM, Bastawrous A, et al. Epidemiology of glaucoma in sub-saharan Africa: prevalence, incidence and risk factors. Middle East Afr J Ophthalmol. 2013;20(2):111-125.',
      'Budenz DL, Barton K, Whiteside-de Vos J, et al. Prevalence of glaucoma in an urban West African population: the Tema Eye Survey. JAMA Ophthalmol. 2013;131(5):651-658.',
      'Kass MA, Heuer DK, Higginbotham EJ, et al. The Ocular Hypertension Treatment Study: a randomized trial determines that topical ocular hypotensive medication delays or prevents the onset of primary open-angle glaucoma. Arch Ophthalmol. 2002;120(6):701-713.',
      'Heijl A, Leske MC, Bengtsson B, et al. Reduction of intraocular pressure and glaucoma progression: results from the Early Manifest Glaucoma Trial. Arch Ophthalmol. 2002;120(10):1268-1279.',
      'Garway-Heath DF, Crabb DP, Bunce C, et al. Latanoprost for open-angle glaucoma (UKGTS): a randomised, multicentre, placebo-controlled trial. Lancet. 2015;385(9975):1295-1304.',
      'Gazzard G, Konstantakopoulou E, Garway-Heath D, et al. Selective laser trabeculoplasty versus eye drops for first-line treatment of ocular hypertension and glaucoma (LiGHT): a multicentre randomised controlled trial. Lancet. 2019;393(10180):1505-1516.',
      'Sihota R, Angmo D, Ramaswamy D, Dada T. Simplifying "target" intraocular pressure for different stages of primary open-angle glaucoma and primary angle-closure glaucoma. Indian J Ophthalmol. 2018;66(4):495-505.',
      'Quigley HA. Glaucoma. Lancet. 2011;377(9774):1367-1377.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 84. CATARACTS
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '84',
    name: 'Cataracts',
    slug: 'cataracts',
    specialty: 'Ophthalmology',
    bodySystem: 'Neurological',
    icdCode: 'H25-H26',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: false,

    overview: `A cataract is an opacification of the crystalline lens of the eye that causes progressive, painless visual impairment. Cataracts are the leading cause of blindness worldwide, responsible for approximately 50% of global blindness (over 20 million people), and are a major cause of visual impairment affecting over 90 million people. The vast majority of this burden is in low- and middle-income countries where access to surgery is limited.

The crystalline lens is normally transparent, allowing light to pass through and focus on the retina. With age, the lens proteins (crystallins) undergo oxidative damage and aggregate, causing opacity. The lens also becomes less flexible with age, contributing to presbyopia. Cataracts develop gradually over months to years, and progression rate varies considerably between individuals.

The primary types of age-related cataract are nuclear sclerotic (central hardening and yellowing), cortical (spoke-like opacities from periphery), and posterior subcapsular (opacity at back of lens, common with steroids and diabetes). Other causes include congenital cataracts, trauma, metabolic conditions (diabetes), inflammation (uveitis), and medications (corticosteroids).

In Africa, cataracts are responsible for approximately 50% of blindness. The cataract surgical rate (CSR) - surgeries per million population per year - remains low in many African countries (often <500) compared to the target of 2000-3000 needed to address the backlog. Limited surgical capacity, lack of ophthalmologists, cost, and access barriers contribute to this gap. Manual Small Incision Cataract Surgery (MSICS) is commonly used in Africa due to lower cost and equipment requirements compared to phacoemulsification.

Surgery is the only effective treatment for cataracts. Modern cataract surgery is highly successful, with over 95% of patients achieving significant visual improvement. The decision to operate is based on the impact on the patients quality of life and ability to function, not just visual acuity measurement.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Surgery is the ONLY effective treatment: No medications or drops can reverse or prevent cataracts

🔴 CRITICAL POINT 2 - Cataract causes PAINLESS gradual vision loss: Sudden painful vision loss suggests other pathology

🔴 CRITICAL POINT 3 - Surgical decision based on FUNCTIONAL impairment: Operate when cataract affects patients daily activities, not based on arbitrary VA cutoff

🔴 CRITICAL POINT 4 - Rule out other causes of vision loss: Glaucoma, diabetic retinopathy, macular degeneration may coexist or be primary cause

🟠 CRITICAL POINT 5 - Dense cataracts can cause phacomorphic glaucoma: Swollen lens blocks angle; urgent surgery needed

🟠 CRITICAL POINT 6 - MSICS is excellent for dense cataracts: Widely used in Africa; comparable outcomes to phacoemulsification; lower cost

🟠 CRITICAL POINT 7 - Posterior subcapsular cataracts cause more symptoms: Glare, near vision problems; often in younger patients (steroids, diabetes)

🟡 CRITICAL POINT 8 - Red reflex screening in newborns: Essential to detect congenital cataracts; surgery within weeks for best outcomes

🟡 CRITICAL POINT 9 - Second eye surgery: Usually done once first eye stable; bilateral blindness has much greater functional impact`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• Leading cause of blindness worldwide (~50% of global blindness)
• Leading cause of moderate-severe visual impairment
• Over 20 million people blind from cataracts
• Over 90 million with visual impairment from cataracts
• Prevalence increases sharply with age: <5% at 50-54; >50% at 75-79
• Bilateral in most cases (though often asymmetric)
• Affects all populations; burden highest in LMIC

TYPES OF CATARACTS:

AGE-RELATED (Most common - 90%):
• Nuclear sclerotic: Central hardening, yellowing; slow progression; "second sight" (temporary near vision improvement from increased lens power)
• Cortical: Spoke-like opacities from periphery; glare symptoms
• Posterior subcapsular (PSC): Opacity at back of lens; rapid progression; glare, reading difficulty; associated with steroids, diabetes

OTHER TYPES:
• Congenital: Present at birth; hereditary or intrauterine infection (rubella)
• Traumatic: Blunt or penetrating injury; may develop years later
• Secondary: Diabetes, uveitis, radiation, electrical injury
• Drug-induced: Corticosteroids (especially PSC), phenothiazines, amiodarone
• Metabolic: Galactosemia, hypocalcemia, Wilson disease

RISK FACTORS:
• Age (strongest risk factor)
• UV light exposure
• Smoking (2-3x increased risk)
• Diabetes mellitus
• Corticosteroid use
• Alcohol excess
• Myopia (nuclear cataract)
• Previous eye surgery or trauma
• Uveitis
• Family history

AFRICAN EPIDEMIOLOGY:
• Cataracts cause ~50% of blindness in Africa
• Estimated 5-9 million people blind from cataracts in Africa
• Low Cataract Surgical Rate (CSR): Often <500/million/year
• Target CSR: 2000-3000/million/year to address backlog
• Major barriers: Cost, access, awareness, fear, limited surgical capacity
• Fewer than 1 ophthalmologist per million in many countries
• MSICS commonly performed (lower cost, suitable equipment)
• Outreach surgical camps increase access
• Cataract blindness has severe socioeconomic impact
• Pediatric cataracts often present late with poor outcomes

SURGICAL OUTCOMES:
• >95% achieve significant visual improvement with modern surgery
• Success rates in well-run African programs comparable to developed countries
• Complications: Posterior capsule opacity (most common), endophthalmitis (rare), retinal detachment (rare)`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

SYMPTOMS:

VISUAL SYMPTOMS:
• Gradual, progressive, PAINLESS blurred vision
• Often described as "foggy," "hazy," or "like looking through a dirty window"
• Difficulty with faces, reading, watching TV
• Colors appear faded or yellowed (especially nuclear cataract)
• Difficulty in low light / night vision
• Glare and halos around lights (especially cortical and PSC)
• Frequent spectacle prescription changes
• "Second sight" - temporary improvement in near vision (nuclear cataract increases lens power)
• Monocular diplopia (double vision in one eye)

PATTERN BY CATARACT TYPE:

Nuclear Sclerotic:
• Slow progression over years
• Distance vision affected more than near initially
• Colors appear yellow/brown
• May have temporary improvement in reading ("second sight")

Cortical:
• Glare, especially with oncoming headlights
• May be asymptomatic if opacities not in visual axis

Posterior Subcapsular:
• Rapid progression (months)
• Significant glare symptoms
• Reading and near work difficulty (visual axis affected early)
• Young patients, steroid users, diabetics

EXAMINATION FINDINGS:

VISUAL ACUITY:
• Reduced (variable depending on cataract density)
• May improve with pinhole if mild
• Dense cataract: May only perceive light direction or hand movements

RED REFLEX:
• Normally bright orange-red
• Cataracts cause: Dull, dark, or absent red reflex
• Useful screening test
• Direct ophthalmoscope at arms length

SLIT LAMP EXAMINATION:
• Nuclear sclerotic: Central yellow-brown opacity; hardening
• Cortical: Spoke-like (cuneiform) opacities from periphery
• PSC: Central posterior granular plaque; best seen on retroillumination

FUNDUS EXAMINATION:
• May be difficult to visualize if dense cataract
• Important to examine before surgery (rule out other pathology)
• B-scan ultrasound if fundus not visible

IMPORTANT: RULE OUT OTHER PATHOLOGY:
• Check for: Glaucoma (disc, IOP), diabetic retinopathy, macular degeneration
• A cataract may coexist with other conditions
• Cataract surgery will not improve vision from other diseases

GRADING CATARACTS:

LOCS III (Lens Opacities Classification System):
• Grades nuclear color, nuclear opalescence, cortical, PSC
• Useful for research and documentation

PRACTICAL GRADING:
• Mild: Good fundus view; VA often 6/12 or better
• Moderate: Partial fundus view; VA 6/18-6/60
• Severe/Dense: No fundus view; VA <6/60 to light perception
• Mature: Completely opaque; white cataract
• Hypermature: Lens shrinkage; may leak proteins (phacolytic glaucoma)

SPECIAL PRESENTATIONS:

PHACOMORPHIC GLAUCOMA:
• Swollen (intumescent) cataract
• Shallow anterior chamber
• Elevated IOP
• Painful, red eye
• Requires urgent surgery

PHACOLYTIC GLAUCOMA:
• Hypermature cataract leaking proteins
• Inflammatory response
• Elevated IOP
• Requires urgent surgery

LENS SUBLUXATION/DISLOCATION:
• Trauma or Marfan syndrome, homocystinuria
• Iridodonesis (tremulous iris)
• May see lens edge in pupil`,

    diagnosis: `DIAGNOSTIC APPROACH:

CATARACT DIAGNOSIS IS CLINICAL

STEP 1: HISTORY
• Gradual painless vision loss
• Risk factors: Age, diabetes, steroids, trauma, UV exposure
• Functional impact: Driving, reading, work, daily activities
• Red flags for other pathology: Pain, redness, sudden loss, floaters, flashes

STEP 2: VISUAL ACUITY
• Distance vision (Snellen chart)
• Near vision
• With and without glasses (best corrected)
• Pinhole acuity (improves with refractive error, not dense cataract)

STEP 3: PUPIL EXAMINATION
• Check for RAPD (relative afferent pupillary defect) - indicates optic nerve or retinal pathology beyond cataract
• Should be absent in pure cataract

STEP 4: RED REFLEX
• Direct ophthalmoscope from arms length
• Cataract appears as dark opacity against red background
• Absent reflex = dense cataract

STEP 5: SLIT LAMP EXAMINATION
• Type and location of opacity
• Grade density
• Check for other pathology (uveitis, pseudoexfoliation, phacodonesis)
• Examine cornea (for surgical planning)

STEP 6: DILATED FUNDUS EXAMINATION
• Essential before surgery
• Rule out: Diabetic retinopathy, macular degeneration, glaucoma
• If view obscured by cataract, perform B-scan ultrasound

STEP 7: INTRAOCULAR PRESSURE
• Rule out glaucoma (common comorbidity in elderly)
• Elevated in phacomorphic/phacolytic glaucoma

PRE-OPERATIVE ASSESSMENT:

BIOMETRY:
• Measures axial length and keratometry
• Calculates IOL power
• Optical biometry (IOLMaster, Lenstar) preferred
• A-scan ultrasound if dense cataract prevents optical measurement

CORNEAL ASSESSMENT:
• Endothelial cell count (if available)
• Corneal topography for astigmatism (premium IOLs)

MACULAR ASSESSMENT:
• OCT if available (detect macular pathology)
• Potential acuity testing (PAM) if dense cataract

SYSTEMIC ASSESSMENT:
• Diabetes control (HbA1c)
• Blood pressure
• Anticoagulation status
• Fitness for local anesthesia

CRITERIA FOR SURGERY:

NO UNIVERSAL VA CUTOFF - Functional impairment determines need

Consider surgery when:
• Cataract significantly affects patients quality of life
• Patient unable to perform desired activities (driving, reading, work)
• Patient understands risks and benefits and wishes surgery
• No contraindications to surgery

Earlier surgery may be indicated for:
• Phacomorphic or phacolytic glaucoma (urgent)
• Need to visualize/treat retina (e.g., diabetic retinopathy)
• Anisometropia causing problems
• Patient choice with informed consent`,

    differentialDiagnosis: [
      'Refractive error - Corrects with glasses or pinhole; clear lens on exam',
      'Age-related macular degeneration - Central vision loss; drusen, pigment changes on fundoscopy; metamorphopsia',
      'Diabetic retinopathy - May coexist; examine fundus; macular edema affects central vision',
      'Glaucoma - May coexist; optic disc cupping, visual field loss; cataract does not cause field defects',
      'Corneal opacity - Visible on direct inspection; slit lamp confirms; history of trauma, infection, dystrophy',
      'Vitreous hemorrhage - Sudden onset; diabetes, trauma; no red reflex; B-scan shows vitreous opacity',
      'Retinal detachment - Sudden onset; floaters, flashes, curtain/shadow; B-scan if fundus not visible',
      'Optic neuropathy - RAPD present; disc pallor; visual field defects; cataract does not cause RAPD',
      'Posterior capsule opacity - After previous cataract surgery; YAG laser treatment',
      'Amblyopia - Longstanding; history of childhood strabismus or deprivation; surgery will not improve',
      'Asteroid hyalosis - Vitreous opacities; usually does not affect vision significantly'
    ],

    investigations: [
      {
        name: 'Visual Acuity (Distance and Near)',
        indication: '🔴 ESSENTIAL - Document baseline and best corrected acuity; functional assessment',
        availability: 'BASIC'
      },
      {
        name: 'Red Reflex Examination',
        indication: '🔴 ESSENTIAL - Quick screening; cataract appears as dark opacity; absent in dense cataract',
        availability: 'BASIC'
      },
      {
        name: 'Slit Lamp Examination',
        indication: '🔴 ESSENTIAL - Characterize cataract type and density; examine anterior segment',
        availability: 'BASIC'
      },
      {
        name: 'Dilated Fundus Examination',
        indication: '🔴 ESSENTIAL - Rule out concurrent pathology; pre-operative assessment',
        availability: 'BASIC'
      },
      {
        name: 'Intraocular Pressure',
        indication: '🔴 ESSENTIAL - Rule out glaucoma; detect phacomorphic glaucoma',
        availability: 'BASIC'
      },
      {
        name: 'Biometry (A-scan or Optical)',
        indication: '🔴 ESSENTIAL PRE-OP - Calculate IOL power for surgery; optical biometry preferred',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'B-scan Ultrasound',
        indication: '🟠 IMPORTANT - When fundus not visible; rule out retinal detachment, tumor, vitreous hemorrhage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood Glucose / HbA1c',
        indication: '🟠 IMPORTANT - Screen for diabetes; optimize control before surgery',
        availability: 'BASIC'
      },
      {
        name: 'OCT Macula',
        indication: '🟡 IF AVAILABLE - Detect macular pathology; predict visual outcome',
        availability: 'ADVANCED'
      },
      {
        name: 'Corneal Topography',
        indication: '🟡 IF AVAILABLE - Pre-surgical planning; astigmatism assessment for toric IOL',
        availability: 'ADVANCED'
      },
      {
        name: 'Specular Microscopy',
        indication: '🟡 IF AVAILABLE - Corneal endothelial cell count; assess surgical risk',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        '⚡ CONFIRM CATARACT IS CAUSE OF VISION LOSS: Rule out glaucoma, macular disease, diabetic retinopathy',
        '⚡ ASSESS FUNCTIONAL IMPACT: How does vision loss affect daily activities?',
        '⚡ SURGICAL CANDIDACY: Patient wishes surgery, understands risks/benefits, medically fit',
        '⚡ TREAT URGENT COMPLICATIONS: Phacomorphic/phacolytic glaucoma requires urgent surgery',
        '⚡ OPTIMIZE COMORBIDITIES: Diabetes control, blood pressure, anticoagulation management',
        '⚡ BIOMETRY: Measure for IOL power calculation',
        '⚡ FUNDUS ASSESSMENT: Examine or B-scan; predict visual outcome',
        '⚡ INFORMED CONSENT: Discuss procedure, IOL options, risks, expected outcomes',
        '⚡ PRE-OPERATIVE DROPS: Topical antibiotics and/or NSAIDs as per protocol',
        '⚡ CONGENITAL CATARACT: Urgent referral - surgery within weeks for best outcomes'
      ],
      
      pharmacological: [
        {
          drug: 'NO EFFECTIVE MEDICAL TREATMENT FOR CATARACT',
          indication: '⚠️ IMPORTANT - Surgery is the ONLY treatment; no drops can prevent, reverse, or slow cataracts',
          dose: 'N/A - Do not prescribe "cataract drops" - they do not work',
          route: 'N/A',
          duration: 'N/A',
          contraindications: [],
          sideEffects: [],
          alternatives: ['Surgery is the only treatment']
        },
        {
          drug: 'Tropicamide 1% (Pre-operative)',
          indication: '🔴 PRE-OPERATIVE - Pupil dilation for surgery',
          dose: 'One drop every 10-15 minutes x 3-4 starting 30-60 minutes before surgery',
          route: 'Topical ophthalmic',
          duration: 'Pre-operative only',
          contraindications: ['Narrow angles (risk of angle closure)'],
          sideEffects: ['Photophobia', 'Blurred vision', 'Rare systemic effects'],
          alternatives: ['Phenylephrine 2.5%', 'Cyclopentolate']
        },
        {
          drug: 'Phenylephrine 2.5% (Pre-operative)',
          indication: '🔴 PRE-OPERATIVE - Pupil dilation for surgery (with tropicamide)',
          dose: 'One drop every 10-15 minutes x 3-4',
          route: 'Topical ophthalmic',
          duration: 'Pre-operative only',
          contraindications: ['Severe hypertension', 'Cardiac disease (caution)'],
          sideEffects: ['Hypertension', 'Tachycardia', 'Rebound miosis'],
          alternatives: ['Tropicamide alone if cardiovascular concerns']
        },
        {
          drug: 'Topical Antibiotic (Pre- and Post-operative)',
          indication: '🔴 PERI-OPERATIVE - Reduce endophthalmitis risk; various protocols',
          dose: 'Moxifloxacin 0.5% or Ofloxacin 0.3%: Pre-op day before or day of surgery; Post-op QID for 1-4 weeks',
          route: 'Topical ophthalmic',
          duration: '1-4 weeks post-operatively',
          contraindications: ['Known allergy to fluoroquinolones'],
          sideEffects: ['Local irritation', 'Allergic reaction (rare)'],
          alternatives: ['Chloramphenicol', 'Tobramycin']
        },
        {
          drug: 'Topical Corticosteroid (Post-operative)',
          indication: '🔴 POST-OPERATIVE - Control inflammation',
          dose: 'Prednisolone acetate 1% or Dexamethasone 0.1%: QID for 1 week, then taper over 3-4 weeks',
          route: 'Topical ophthalmic',
          duration: '4-6 weeks post-operatively',
          contraindications: ['Active ocular infection'],
          sideEffects: ['Elevated IOP (monitor)', 'Delayed wound healing'],
          alternatives: ['Topical NSAID (less potent anti-inflammatory)']
        },
        {
          drug: 'Topical NSAID (Post-operative)',
          indication: '🟠 POST-OPERATIVE - Reduce inflammation and CME risk; may use with or instead of steroid',
          dose: 'Ketorolac 0.5% or Nepafenac 0.1%: TID-QID for 4-6 weeks',
          route: 'Topical ophthalmic',
          duration: '4-6 weeks post-operatively',
          contraindications: ['NSAID allergy', 'Corneal epithelial defects'],
          sideEffects: ['Stinging', 'Corneal melting (rare with prolonged use)'],
          alternatives: ['Steroid alone']
        },
        {
          drug: 'Intracameral Cefuroxime',
          indication: '🔴 INTRA-OPERATIVE - Reduces endophthalmitis risk by 5-fold; standard of care in many centers',
          dose: '1mg in 0.1mL injected into anterior chamber at end of surgery',
          route: 'Intracameral',
          duration: 'Single intra-operative dose',
          contraindications: ['Cephalosporin/penicillin allergy'],
          sideEffects: ['Generally well tolerated'],
          alternatives: ['Intracameral moxifloxacin', 'Intracameral vancomycin']
        },
        {
          drug: 'Acetazolamide (Post-operative IOP spike)',
          indication: '🟠 POST-OPERATIVE - For elevated IOP after surgery',
          dose: '250mg PO single dose or BD for 1-2 days',
          route: 'PO',
          duration: 'Short-term (1-2 days)',
          contraindications: ['Sulfonamide allergy', 'Severe renal disease'],
          sideEffects: ['Paresthesias', 'GI upset', 'Electrolyte disturbance'],
          alternatives: ['Topical IOP-lowering drops']
        }
      ],
      
      nonPharmacological: [
        'PATIENT EDUCATION: Explain that surgery is the only treatment; drops cannot fix cataracts',
        'SPECTACLE UPDATE: May provide temporary improvement in mild cataract; not a substitute for surgery',
        'LIFESTYLE MODIFICATIONS: Good lighting, magnifying aids, contrast enhancement as temporizing measures',
        'UV PROTECTION: Sunglasses may slow progression (limited evidence); general eye health',
        'SMOKING CESSATION: Reduces cataract risk and progression; general health benefit',
        'DIABETES CONTROL: Good glycemic control may slow cataract progression',
        'DRIVING ASSESSMENT: Advise on safety; check legal requirements for vision standards',
        'POST-OPERATIVE CARE: Avoid rubbing eye, heavy lifting, swimming for specified period; use shield at night'
      ],
      
      surgical: [
        'PHACOEMULSIFICATION: Gold standard in developed settings; ultrasound breaks up lens; small incision; rapid recovery; requires expensive equipment',
        'MANUAL SMALL INCISION CATARACT SURGERY (MSICS): Excellent outcomes; larger incision than phaco but still sutureless; lower cost; suitable for dense cataracts; widely used in Africa',
        'EXTRACAPSULAR CATARACT EXTRACTION (ECCE): Larger incision with sutures; rarely used now except very dense cataracts or no phaco/MSICS available',
        'INTRACAPSULAR CATARACT EXTRACTION (ICCE): Entire lens and capsule removed; obsolete except for subluxated lens',
        'INTRAOCULAR LENS (IOL) IMPLANTATION: Standard of care; placed in capsular bag; monofocal most common; multifocal/toric for selected patients',
        'FEMTOSECOND LASER-ASSISTED: Uses laser for some steps; potential precision advantages; high cost; not proven superior',
        'YAG LASER CAPSULOTOMY: For posterior capsule opacity (PCO) after cataract surgery; quick outpatient procedure'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Phacomorphic glaucoma - Painful red eye with intumescent lens; urgent surgery needed',
        '🔴 IMMEDIATE: Phacolytic glaucoma - Hypermature cataract with inflammation; urgent surgery',
        '🔴 IMMEDIATE: Lens dislocation into anterior chamber - Causes corneal damage; urgent removal',
        '🔴 IMMEDIATE: Congenital cataract in infant - Surgery within weeks for best visual development',
        '🟠 URGENT: Mature cataract - At risk of complications; cannot examine fundus',
        '🟠 URGENT: Cataract preventing essential retinal treatment (e.g., diabetic retinopathy)',
        '🟡 ROUTINE: Cataract causing functional visual impairment - Patient wishes surgery',
        '🟡 ROUTINE: Cataract surgery for second eye - Once first eye stable',
        '🟡 ROUTINE: Asymptomatic cataract - No treatment needed; monitor'
      ],
      
      monitoring: [
        'VISUAL ACUITY: Document at each visit; assess functional impact',
        'CATARACT PROGRESSION: Compare density over time; rate of progression varies',
        'INTRAOCULAR PRESSURE: Especially important as cataracts progress (phacomorphic risk)',
        'FUNDUS EXAMINATION: Monitor concurrent conditions (diabetes, macular degeneration)',
        'POST-OPERATIVE: Day 1, Week 1, Month 1, then as needed; check VA, IOP, inflammation, wound',
        'POSTERIOR CAPSULE OPACITY: May develop months to years after surgery; YAG laser treatment'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

PRE-OPERATIVE WORKUP:
• Comprehensive eye examination
• Biometry for IOL calculation
• B-scan if fundus not visible
• Optimize diabetes, hypertension
• Review anticoagulation (usually continue low-dose aspirin; discuss warfarin)
• Informed consent
• Pre-operative drops as per protocol

SURGICAL TECHNIQUES COMPARISON:

PHACOEMULSIFICATION:
• Small incision (2.2-2.8mm)
• Ultrasound emulsification of lens
• Rapid visual recovery
• Expensive equipment needed
• Preferred for softer cataracts

MSICS:
• Larger incision (5-7mm) but sutureless
• Manual nucleus removal
• Excellent for dense cataracts
• Lower cost, minimal equipment
• Commonly used in Africa
• Outcomes comparable to phaco

POST-OPERATIVE CARE:

DAY 1:
• Check visual acuity (may still be reduced due to inflammation, medication effect)
• Examine wound for integrity
• Check IOP (post-op spike common)
• Assess anterior chamber (cells, flare)
• Confirm drops regimen

WEEK 1:
• Visual acuity improving
• Check for infection (endophthalmitis rare but devastating)
• IOP check
• Continue drops

MONTH 1:
• Visual acuity should be near final
• Refraction for spectacles
• Taper steroid drops
• Check for CME if vision unexpectedly poor

LONG-TERM:
• Routine eye exam annually
• Monitor for posterior capsule opacity (PCO)
• Second eye surgery when appropriate

POST-OPERATIVE COMPLICATIONS:

EARLY (Days to Weeks):
• Endophthalmitis: Rare (1:1000-3000); devastating; pain, vision loss, hypopyon; urgent vitrectomy and intravitreal antibiotics
• Elevated IOP: Usually transient; treat if significant
• Wound leak: May need resuturing
• Corneal edema: Usually resolves
• Hyphema: Usually absorbs
• Iris prolapse: May need repair

LATE (Weeks to Months):
• Posterior capsule opacity (PCO): Most common (up to 30%); YAG laser capsulotomy is definitive
• Cystoid macular edema (CME): Treat with topical NSAIDs and steroids
• Retinal detachment: Rare; higher risk with myopia
• Dropped nucleus: Requires vitrectomy
• IOL dislocation: May need repositioning or exchange

EXPECTED OUTCOMES:
• >95% achieve significant visual improvement
• Most patients see 6/12 or better
• Visual recovery usually rapid (days to weeks)
• Glasses usually needed for reading (with monofocal IOL)

SECOND EYE SURGERY:
• Usually performed once first eye stable (2-4 weeks to months)
• Bilateral surgery same day increasingly accepted in some settings
• Consider if significant anisometropia or bilateral visual impairment

POSTERIOR CAPSULE OPACITY (PCO):
• Most common long-term complication
• Vision becomes cloudy again months to years after surgery
• Treatment: YAG laser capsulotomy (quick, effective, outpatient)
• Not a failure of surgery - expected in a proportion of patients

CONGENITAL CATARACT MANAGEMENT:
• Urgent referral and surgery (within weeks of diagnosis)
• Delay causes irreversible amblyopia
• May need contact lens or IOL for optical correction
• Intensive amblyopia treatment (patching)
• Often need multiple procedures over childhood
• Visual outcomes often limited despite surgery if diagnosed late`
    },

    redFlags: [
      '🔴 CRITICAL: Painful red eye with dense cataract - Phacomorphic or phacolytic glaucoma; urgent surgery needed',
      '🔴 CRITICAL: White reflex in infant (leukocoria) - Congenital cataract or retinoblastoma; urgent evaluation',
      '🔴 CRITICAL: Post-operative pain, vision loss, hypopyon - Endophthalmitis; emergency treatment required',
      '🔴 CRITICAL: Sudden vision loss (vs gradual) - Not typical of cataract; consider retinal detachment, hemorrhage',
      '🟠 URGENT: Mature/dense white cataract - Cannot assess fundus; at risk of complications',
      '🟠 URGENT: Post-operative signs of infection - Increasing pain, redness, discharge; may indicate endophthalmitis',
      '🟡 CAUTION: Cataract with coexisting pathology - Glaucoma, diabetic retinopathy; cataract surgery alone may not improve vision',
      '🟡 CAUTION: Cataract in young patient without clear cause - Consider systemic associations (diabetes, steroids, trauma)',
      '🟡 CAUTION: Rapidly progressive cataract - Consider posterior subcapsular type; rule out ongoing steroid use, uncontrolled diabetes'
    ],

    complications: [
      'Phacomorphic glaucoma - Swollen lens causes angle closure; acute painful elevation of IOP; requires urgent surgery',
      'Phacolytic glaucoma - Lens proteins leak through hypermature lens; inflammatory glaucoma; requires surgery',
      'Lens-induced uveitis - Inflammation from lens proteins; may need anti-inflammatory treatment and surgery',
      'Surgical complications - Endophthalmitis, posterior capsule rupture, dropped nucleus, corneal edema',
      'Posterior capsule opacity - Common after surgery (20-30%); treated with YAG laser',
      'Cystoid macular edema - Can reduce final visual acuity; treated with topical NSAIDs/steroids',
      'Retinal detachment - Rare post-operative complication; higher risk in myopes',
      'Chronic inflammation - Can occur post-operatively; needs ongoing treatment'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

NATURAL HISTORY (UNTREATED):
• Progressive worsening of vision over months to years
• Eventually mature/hypermature cataract
• Risk of complications (phacomorphic/phacolytic glaucoma)
• Ultimately blindness if untreated

PROGNOSIS WITH SURGERY:

EXCELLENT OUTCOMES:
• >95% achieve significant visual improvement
• >90% achieve 6/12 (driving standard) or better
• Visual recovery usually rapid (days to weeks)
• High patient satisfaction

FACTORS AFFECTING OUTCOME:

FAVORABLE:
• Healthy eye with no other pathology
• Straightforward surgery
• Good compliance with post-operative care
• No complications

UNFAVORABLE (May still benefit from surgery):
• Coexisting eye disease (glaucoma, macular degeneration, diabetic retinopathy)
• Dense/mature cataract (higher surgical risk)
• Previous eye surgery or trauma
• Complicated surgery (posterior capsule rupture, vitreous loss)
• Amblyopia
• Very high or low IOL power needed

VISUAL OUTCOMES IN AFRICAN SETTINGS:
• Good outcomes achievable with well-trained surgeons
• MSICS outcomes comparable to phacoemulsification
• Barriers: late presentation, limited post-operative care, complications
• Outreach programs have demonstrated excellent results

KEY MESSAGE:
Cataract surgery is one of the most successful operations in medicine. With modern techniques, the vast majority of patients experience significant improvement in vision and quality of life.`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:
• Limited ability to prevent age-related cataracts
• UV protection (sunglasses) - may slow progression
• Smoking cessation - reduces risk
• Diabetes control - may slow progression
• Avoid unnecessary corticosteroid use
• Eye protection - prevent traumatic cataract
• Rubella vaccination - prevent congenital cataracts

SECONDARY PREVENTION (Early Detection):
• Regular eye examinations, especially after age 50
• Screen diabetic patients
• Red reflex screening in newborns (congenital cataract)
• Early referral when cataracts affect function

TERTIARY PREVENTION (Preventing Complications):
• Timely surgery before complications develop
• Good surgical technique
• Appropriate post-operative care
• YAG laser for posterior capsule opacity

PUBLIC HEALTH STRATEGIES FOR AFRICA:

INCREASE CATARACT SURGICAL RATE:
• Train more ophthalmic surgeons
• Task-shifting: Cataract surgeons, ophthalmic clinical officers
• Outreach surgical programs
• Reduce cost barriers
• Community awareness campaigns

IMPROVE OUTCOMES:
• High-quality training
• Standard operating procedures
• Adequate supplies and equipment
• Post-operative care systems
• Audit and feedback

CONGENITAL CATARACT:
• Red reflex screening of all newborns
• Early referral system
• Pediatric ophthalmology services
• Amblyopia treatment support`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

WHAT IS A CATARACT?
A cataract is when the lens inside your eye becomes cloudy. The lens is normally clear like glass, allowing light to pass through to the back of your eye. When it becomes cloudy, your vision becomes blurry, like looking through a foggy window.

IS IT SERIOUS?
Cataracts are very common, especially as we get older. Almost everyone develops some degree of cataract if they live long enough. The good news is that cataracts are treatable with a simple operation that has excellent results.

WHAT ARE THE SYMPTOMS?
• Gradual blurring of vision (not sudden)
• Colors may look faded or yellow
• Difficulty seeing at night or in dim light
• Glare from lights (headlights, sunlight)
• Needing brighter light to read
• Frequent changes in glasses prescription
• Cataracts are NOT painful - if you have pain, there may be another problem

CAN MEDICINE OR DROPS FIX A CATARACT?
No. There are no drops, tablets, or exercises that can prevent, reverse, or cure a cataract. Surgery is the only effective treatment. Do not waste money on "cataract drops" - they do not work.

WHEN DO I NEED SURGERY?
You need surgery when the cataract affects your daily life:
• You cannot see well enough to do your work
• You cannot read, watch TV, or recognize faces
• You cannot drive safely (if applicable)
• You feel unsafe moving around

The decision is based on how the cataract affects YOU, not just the number on the eye test.

WHAT IS CATARACT SURGERY?
• A short operation (usually 15-30 minutes)
• Usually done under local anesthesia (you are awake but feel no pain)
• The cloudy lens is removed and replaced with a clear artificial lens (implant)
• You go home the same day
• Vision usually improves within days

IS SURGERY SAFE?
Cataract surgery is one of the safest and most successful operations:
• Over 95% of people see much better after surgery
• Serious complications are rare
• Like any operation, there are some risks (your doctor will explain these)

AFTER SURGERY:
• Use eye drops as prescribed (very important)
• Do not rub or press on your eye
• Wear the eye shield at night as directed
• Avoid heavy lifting, bending, and swimming for a few weeks
• Attend all follow-up appointments

YOU WILL PROBABLY STILL NEED GLASSES:
• Most people need reading glasses after surgery
• Some people need glasses for distance too
• This is normal - the operation is still successful

WARNING SIGNS AFTER SURGERY - SEEK HELP IMMEDIATELY:
• Increasing pain in the eye
• Getting worse instead of better vision
• Increasing redness
• Pus or discharge from the eye
These could be signs of infection, which is rare but serious.

WHAT ABOUT MY OTHER EYE?
• Cataracts usually develop in both eyes
• We typically operate on one eye first
• Once that eye is healed, we can discuss operating on the second eye

FOR FAMILY MEMBERS:
• Encourage your relative to have surgery when recommended
• Help them use their drops correctly after surgery
• Bring them to follow-up appointments
• Watch for warning signs of complications

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Cataracts cause blurry vision but are easily fixed with a safe, quick operation. Do not let fear or false beliefs about surgery cause you to go blind from a treatable condition. Cataract surgery can restore your sight and change your life.`,

    references: [
      'American Academy of Ophthalmology. Cataract in the Adult Eye Preferred Practice Pattern. 2021.',
      'National Institute for Health and Care Excellence (NICE). Cataracts in adults: management. NICE guideline [NG77]. 2017.',
      'World Health Organization. World report on vision. 2019.',
      'Bourne RRA, Flaxman SR, Braithwaite T, et al. Magnitude, temporal trends, and projections of the global prevalence of blindness and distance and near vision impairment: a systematic review and meta-analysis. Lancet Glob Health. 2017;5(9):e888-e897.',
      'Asbell PA, Dualan I, Mindel J, et al. Age-related cataract. Lancet. 2005;365(9459):599-609.',
      'Liu YC, Wilkins M, Kim T, et al. Cataracts. Lancet. 2017;390(10094):600-612.',
      'Allen D, Vasavada A. Cataract and surgery for cataract. BMJ. 2006;333(7559):128-132.',
      'Venkatesh R, Chang DF, Muralikrishnan R, et al. Manual small incision cataract surgery: a review. Asia Pac J Ophthalmol (Phila). 2012;1(2):113-119.',
      'Gogate P, Optom JJB, Deshpande M, Naidoo K. Meta-analysis to compare the safety and efficacy of manual small incision cataract surgery and phacoemulsification. Middle East Afr J Ophthalmol. 2015;22(3):362-369.',
      'ESCRS Endophthalmitis Study Group. Prophylaxis of postoperative endophthalmitis following cataract surgery: results of the ESCRS multicenter study and identification of risk factors. J Cataract Refract Surg. 2007;33(6):978-988.',
      'Lindfield R, Vishwanath K, Ngounou F, Khanna RC. The challenges in improving outcome of cataract surgery in low and middle income countries. Indian J Ophthalmol. 2012;60(5):464-469.',
      'Lewallen S, Courtright P. Blindness in Africa: present situation and future needs. Br J Ophthalmol. 2001;85(8):897-903.',
      'Yorston D. High-volume surgery in developing countries. Eye (Lond). 2005;19(10):1083-1089.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // ═══════════════════════════════════════════════════════════════════════════════
  // 85. OTITIS MEDIA
  // ═══════════════════════════════════════════════════════════════════════════════
  {
    id: '85',
    name: 'Otitis Media',
    slug: 'otitis-media',
    specialty: 'ENT',
    bodySystem: 'Neurological',
    icdCode: 'H66',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Otitis media (OM) is inflammation of the middle ear, most commonly caused by bacterial or viral infection. It is one of the most common childhood infections and a leading reason for pediatric healthcare visits and antibiotic prescriptions worldwide. Acute otitis media (AOM) is characterized by rapid onset of signs and symptoms of middle ear inflammation, often following an upper respiratory tract infection.

The pathophysiology involves dysfunction of the Eustachian tube, which normally ventilates the middle ear and drains secretions to the nasopharynx. In children, the Eustachian tube is shorter, more horizontal, and more compliant, predisposing to dysfunction. Upper respiratory infections cause mucosal edema and obstruction, leading to negative middle ear pressure, effusion, and secondary bacterial infection in many cases.

The main types are: Acute Otitis Media (AOM) - acute infection with middle ear effusion and signs of acute illness; Otitis Media with Effusion (OME) - fluid in middle ear without signs of acute infection (also called "glue ear"); and Chronic Suppurative Otitis Media (CSOM) - chronic infection with persistent tympanic membrane perforation and discharge.

The most common bacterial pathogens are Streptococcus pneumoniae, Haemophilus influenzae (non-typeable), and Moraxella catarrhalis. Viral pathogens include respiratory syncytial virus (RSV), rhinovirus, influenza, and adenovirus.

In Africa, otitis media is highly prevalent due to overcrowding, malnutrition, high rates of upper respiratory infections, and limited access to healthcare. CSOM is a major cause of preventable hearing loss in children, which impacts language development, education, and economic potential. The WHO estimates that CSOM affects 65-330 million people globally, with 60% of cases resulting in hearing impairment.

Management of AOM includes pain control for all patients and antibiotics for selected cases. Many cases resolve spontaneously, but antibiotics are indicated for severe cases, young children, and bilateral disease. Prevention through pneumococcal and Hib vaccination has significantly reduced disease burden where implemented.

⚠️ MEDICAL DISCLAIMER: This clinical information is intended for healthcare professionals practicing in resource-limited settings. Treatment decisions must be individualized based on patient presentation, available resources, and local guidelines. This content does not replace clinical judgment or local protocols.

════════════════════════════════════════════════════════════════════════════════
CRITICAL POINTS - MEMORIZE THESE
════════════════════════════════════════════════════════════════════════════════

🔴 CRITICAL POINT 1 - Pain management is ESSENTIAL for ALL AOM: Treat pain regardless of whether antibiotics are given

🔴 CRITICAL POINT 2 - Not all AOM needs antibiotics: Observation option for mild-moderate AOM in children >2 years; reduces antibiotic resistance

🔴 CRITICAL POINT 3 - Amoxicillin is FIRST-LINE antibiotic: High-dose (80-90 mg/kg/day) for AOM; covers S. pneumoniae

🔴 CRITICAL POINT 4 - CSOM requires DRY EAR PRECAUTIONS and topical treatment: Aural toilet plus topical antibiotics; systemic antibiotics alone are insufficient

🟠 CRITICAL POINT 5 - Mastoiditis is a serious complication: Fever, post-auricular swelling/tenderness, protruding ear; requires IV antibiotics and often surgery

🟠 CRITICAL POINT 6 - Recurrent AOM may need ENT referral: Consider tympanostomy tubes if 3+ episodes in 6 months or 4+ in 12 months

🟠 CRITICAL POINT 7 - OME (glue ear) usually resolves spontaneously: Watch and wait for 3 months before intervention; affects hearing temporarily

🟡 CRITICAL POINT 8 - Hearing loss from CSOM is PREVENTABLE: Early treatment and dry ear precautions; major impact on child development

🟡 CRITICAL POINT 9 - Pneumococcal and Hib vaccines reduce AOM incidence: Ensure vaccination coverage`,

    epidemiology: `GLOBAL EPIDEMIOLOGY:
• One of most common childhood infections worldwide
• 80% of children have at least one episode of AOM by age 3
• Peak incidence: 6-18 months of age
• 700 million cases of AOM annually worldwide
• Leading cause of pediatric antibiotic prescriptions
• CSOM affects 65-330 million people globally
• Hearing loss affects ~60% of CSOM cases

TYPES OF OTITIS MEDIA:

ACUTE OTITIS MEDIA (AOM):
• Rapid onset (<48 hours)
• Middle ear effusion present
• Signs of middle ear inflammation (bulging, erythema)
• Signs/symptoms of acute illness (pain, fever, irritability)

OTITIS MEDIA WITH EFFUSION (OME):
• Fluid in middle ear WITHOUT signs of acute infection
• No fever, no acute pain
• May follow AOM or occur independently
• Also called "glue ear" or "serous otitis media"
• Causes conductive hearing loss

CHRONIC SUPPURATIVE OTITIS MEDIA (CSOM):
• Chronic infection >2-6 weeks
• Persistent tympanic membrane perforation
• Chronic or recurrent ear discharge
• Often painless
• Major cause of hearing loss

RISK FACTORS:
• Age 6-24 months (peak incidence)
• Daycare attendance
• Lack of breastfeeding
• Pacifier use
• Exposure to tobacco smoke
• Craniofacial abnormalities (cleft palate, Down syndrome)
• Family history
• Upper respiratory infections
• Allergic rhinitis
• Low socioeconomic status
• Overcrowding
• Malnutrition
• Lack of vaccination

COMMON PATHOGENS:

BACTERIAL:
• Streptococcus pneumoniae (25-50%)
• Haemophilus influenzae - non-typeable (15-30%)
• Moraxella catarrhalis (3-20%)
• Group A Streptococcus (occasional)
• Staphylococcus aureus (occasional, more common in CSOM)
• Pseudomonas aeruginosa (CSOM, especially with water exposure)

VIRAL (often co-infection):
• Respiratory syncytial virus (RSV)
• Rhinovirus
• Influenza
• Adenovirus
• Parainfluenza

AFRICAN EPIDEMIOLOGY:
• High prevalence of otitis media
• CSOM prevalence: 0.4-4.2% (among highest globally)
• Major cause of preventable hearing impairment in children
• Contributing factors: Overcrowding, malnutrition, high URI rates, limited healthcare access
• Lower vaccination rates for pneumococcal and Hib in some regions
• Late presentation common
• Limited access to ENT specialists and audiology
• Hearing loss impacts language, education, economic potential
• Cultural factors may delay presentation
• Traditional remedies sometimes used`,

    clinicalPresentation: `SYMPTOMS AND SIGNS:

ACUTE OTITIS MEDIA (AOM):

SYMPTOMS:
• Ear pain (otalgia) - often severe, acute onset
• In infants: Irritability, crying, ear pulling/tugging, poor feeding
• Fever (variable; may be absent)
• Hearing loss (temporary)
• Often preceded by upper respiratory symptoms (runny nose, cough)
• Disturbed sleep
• Balance problems (less common)

IF TYMPANIC MEMBRANE PERFORATES:
• Sudden relief of pain
• Purulent (pus) discharge from ear
• Often spontaneous healing

EXAMINATION - OTOSCOPY (KEY TO DIAGNOSIS):

NORMAL TYMPANIC MEMBRANE:
• Pearly gray, translucent
• Visible landmarks (light reflex, malleus)
• Mobile with pneumatic otoscopy

AOM FINDINGS:
• Bulging tympanic membrane (most specific sign)
• Erythema (redness) - but can occur with crying
• Cloudiness/opacity
• Reduced or absent mobility (pneumatic otoscopy)
• Purulent effusion visible behind TM
• May see perforation with discharge

OTITIS MEDIA WITH EFFUSION (OME):

SYMPTOMS:
• Often asymptomatic
• Hearing difficulty ("not responding," "turning up TV")
• Ear fullness or "popping"
• Balance problems (occasional)
• NO fever, NO acute pain
• May follow AOM

EXAMINATION:
• Retracted or neutral tympanic membrane
• Amber or blue discoloration
• Air-fluid level or bubbles visible behind TM
• Reduced mobility
• NO bulging, NO acute inflammation

CHRONIC SUPPURATIVE OTITIS MEDIA (CSOM):

SYMPTOMS:
• Chronic or recurrent ear discharge (>2-6 weeks)
• Hearing loss (conductive)
• Usually painless (pain suggests complication)
• Foul-smelling discharge (especially with cholesteatoma)

EXAMINATION:
• Tympanic membrane perforation (central or marginal)
• Purulent or mucoid discharge in canal
• Middle ear mucosa may be visible
• Polyps may be present
• Cholesteatoma (white mass, foul smell) - serious complication

COMPLICATIONS - EXAMINE FOR:

MASTOIDITIS:
• Post-auricular swelling, erythema, tenderness
• Ear pushed forward and outward
• Fever
• Ear pain
• Medical/surgical emergency

INTRACRANIAL COMPLICATIONS (Rare but serious):
• Meningitis
• Brain abscess
• Lateral sinus thrombosis
• Persistent severe headache, altered mental status, focal signs

FACIAL NERVE PALSY:
• Facial weakness on affected side
• Indicates spread of infection`,

    diagnosis: `DIAGNOSTIC APPROACH:

AOM IS A CLINICAL DIAGNOSIS based on:
1. Acute onset of symptoms (<48 hours)
2. Presence of middle ear effusion
3. Signs of middle ear inflammation

DIAGNOSTIC CRITERIA (AAP Guidelines):

DEFINITE AOM - Requires ALL of:
• Moderate to severe bulging of TM, OR
• New onset otorrhea not due to otitis externa, OR
• Mild bulging of TM WITH recent onset ear pain or intense TM erythema

OTOSCOPY IS ESSENTIAL:
• Remove cerumen for adequate visualization
• Assess TM position (bulging, retracted, neutral)
• Assess TM color (erythema, opacity, amber)
• Assess TM mobility (pneumatic otoscopy if available)
• Look for perforation, discharge, cholesteatoma

PNEUMATIC OTOSCOPY:
• Attaches bulb to otoscope
• Positive pressure: TM should move inward
• Negative pressure: TM should move outward
• Effusion causes reduced or absent mobility
• Increases diagnostic accuracy

TYMPANOMETRY (If Available):
• Objective measure of TM compliance
• Type A: Normal (peaked curve)
• Type B: Flat curve - effusion present
• Type C: Negative peak - Eustachian tube dysfunction
• Useful for OME diagnosis and monitoring

DISTINGUISHING AOM FROM OME:

AOM:
• Acute symptoms (<48 hours)
• Bulging TM
• Signs of acute inflammation
• Fever, pain, irritability

OME:
• No acute symptoms
• Retracted or neutral TM
• No acute inflammation
• Hearing loss, fullness

CSOM DIAGNOSIS:
• Chronic discharge >2-6 weeks
• Visible TM perforation
• Rule out cholesteatoma (marginal perforation, white mass)
• May need CT if complications suspected

WHEN TO IMAGE:

CT TEMPORAL BONE:
• Suspected mastoiditis
• Suspected intracranial complication
• Cholesteatoma evaluation
• Chronic ear disease not responding to treatment
• Pre-operative planning

MRI:
• Intracranial complications
• Cholesteatoma (complements CT)

AUDIOMETRY:
• OME lasting >3 months (assess hearing)
• CSOM (assess hearing loss)
• Any concern about hearing
• Before and after intervention`,

    differentialDiagnosis: [
      'Otitis externa - Ear canal pain (worse with tragus pressure/pinna pull); canal edema and discharge; TM usually normal',
      'Referred otalgia - Pain from teeth, TMJ, pharynx, cervical spine; normal ear examination',
      'Foreign body in ear - History of insertion; visible on otoscopy; may cause secondary infection',
      'Eustachian tube dysfunction - Ear fullness, popping; retracted TM; often with URI or allergies',
      'Tympanic membrane perforation (traumatic) - History of trauma, barotrauma, or blast; visible perforation',
      'Cholesteatoma - White mass behind TM; marginal perforation; foul discharge; hearing loss; requires surgery',
      'Mastoiditis - Post-auricular swelling and tenderness; ear displaced; complication of AOM; urgent',
      'Bullous myringitis - Blisters on TM; very painful; often viral or Mycoplasma',
      'Herpes zoster oticus (Ramsay Hunt) - Vesicles in ear canal; facial palsy; severe pain; treat with antivirals',
      'Cerumen impaction - Ear fullness, hearing loss; visible cerumen; no inflammation',
      'Temporomandibular joint (TMJ) dysfunction - Pain with jaw movement; tender TMJ; normal ear exam'
    ],

    investigations: [
      {
        name: 'Otoscopy',
        indication: '🔴 ESSENTIAL - Core diagnostic tool; assess TM position, color, mobility, perforation',
        availability: 'BASIC'
      },
      {
        name: 'Pneumatic Otoscopy',
        indication: '🔴 ESSENTIAL - Assess TM mobility; increases diagnostic accuracy for effusion',
        availability: 'BASIC'
      },
      {
        name: 'Tympanometry',
        indication: '🟠 IMPORTANT - Objective effusion detection; useful for OME monitoring; Type B indicates effusion',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Audiometry',
        indication: '🟠 IMPORTANT - Assess hearing loss in OME >3 months, CSOM, or any hearing concern',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Culture of Ear Discharge',
        indication: '🟡 IF INDICATED - CSOM not responding to treatment; recurrent AOM; immunocompromised',
        availability: 'BASIC'
      },
      {
        name: 'CT Temporal Bone',
        indication: '🟠 IMPORTANT - Suspected mastoiditis, intracranial complications, cholesteatoma',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Complete Blood Count',
        indication: '🟡 IF INDICATED - Suspected serious infection/complication; usually not needed for uncomplicated AOM',
        availability: 'BASIC'
      },
      {
        name: 'Blood Culture',
        indication: '🟡 IF INDICATED - Toxic appearance, suspected sepsis, mastoiditis',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '⚡ ASSESS SEVERITY: Mild-moderate vs severe AOM; unilateral vs bilateral',
        '⚡ PAIN MANAGEMENT FIRST: Essential for ALL patients with AOM; do not delay for antibiotics',
        '⚡ DECIDE: Immediate antibiotics OR observation (watchful waiting)',
        '⚡ REMOVE CERUMEN: If blocking view of tympanic membrane',
        '⚡ EXAMINE BOTH EARS: AOM is often bilateral',
        '⚡ CHECK FOR COMPLICATIONS: Mastoiditis, facial palsy, meningeal signs',
        '⚡ ASSESS HEARING: Gross hearing check; formal if concern or persistent OME',
        '⚡ EDUCATE PARENTS: Expected course, pain management, when to return',
        '⚡ ENSURE FOLLOW-UP: Recheck in 48-72 hours if observing; sooner if worsening',
        '⚡ VACCINATION STATUS: Ensure up-to-date pneumococcal and Hib vaccines'
      ],
      
      pharmacological: [
        {
          drug: 'Paracetamol (Acetaminophen)',
          indication: '🔴 ESSENTIAL - Pain relief for ALL AOM patients; fever reduction',
          dose: 'Children: 15 mg/kg PO every 4-6 hours as needed. Max: 75 mg/kg/day or 4g/day. Adults: 500mg-1g every 4-6 hours. Max: 4g/day',
          route: 'PO or rectal',
          duration: 'As needed for pain/fever (usually 2-3 days)',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Generally very safe at correct doses'],
          alternatives: ['Ibuprofen']
        },
        {
          drug: 'Ibuprofen',
          indication: '🔴 ESSENTIAL - Pain relief; anti-inflammatory; alternative or adjunct to paracetamol',
          dose: 'Children >6 months: 5-10 mg/kg PO every 6-8 hours. Max: 40 mg/kg/day. Adults: 200-400mg every 6-8 hours. Max: 1200mg/day (OTC)',
          route: 'PO',
          duration: 'As needed for pain (usually 2-3 days)',
          contraindications: ['Age <6 months', 'Renal impairment', 'GI bleeding', 'Aspirin-sensitive asthma'],
          sideEffects: ['GI upset', 'Renal impairment (rare at short-term use)'],
          alternatives: ['Paracetamol']
        },
        {
          drug: 'Amoxicillin (High-Dose)',
          indication: '🔴 FIRST-LINE ANTIBIOTIC for AOM - When antibiotics indicated; high dose for S. pneumoniae coverage',
          dose: 'Children: 80-90 mg/kg/day PO divided into 2 doses (or 3 doses). Max: 3g/day. Adults: 500mg-1g PO three times daily',
          route: 'PO',
          duration: '5 days if ≥2 years with mild-moderate; 10 days if <2 years or severe',
          contraindications: ['Penicillin allergy', 'History of amoxicillin-associated severe reaction'],
          sideEffects: ['Diarrhea', 'Rash', 'Nausea', 'Allergic reactions'],
          alternatives: ['Amoxicillin-clavulanate if recent antibiotics or treatment failure']
        },
        {
          drug: 'Amoxicillin-Clavulanate (Augmentin)',
          indication: '🔴 SECOND-LINE - For treatment failure, recent antibiotics (<30 days), or severe AOM',
          dose: 'Children: 90 mg/kg/day amoxicillin component PO divided twice daily. Use high-dose formulation. Adults: 875/125mg PO twice daily or 500/125mg three times daily',
          route: 'PO',
          duration: '10 days',
          contraindications: ['Penicillin allergy', 'Previous cholestatic jaundice with amoxicillin-clavulanate'],
          sideEffects: ['Diarrhea (more than amoxicillin)', 'Rash', 'GI upset'],
          alternatives: ['Ceftriaxone IM if vomiting or non-adherence']
        },
        {
          drug: 'Ceftriaxone IM',
          indication: '🟠 ALTERNATIVE - For vomiting, non-adherence, or treatment failure with oral antibiotics',
          dose: 'Children: 50 mg/kg IM (max 1g) once daily for 1-3 days. Adults: 1-2g IM once daily for 1-3 days',
          route: 'IM',
          duration: '1 day for initial treatment; 3 days for treatment failure',
          contraindications: ['Cephalosporin allergy', 'Severe penicillin allergy (cross-reactivity low but exists)'],
          sideEffects: ['Injection site pain', 'Diarrhea', 'Rash'],
          alternatives: ['Azithromycin if severe penicillin allergy']
        },
        {
          drug: 'Azithromycin',
          indication: '🟡 PENICILLIN ALLERGY ALTERNATIVE - For patients with penicillin allergy (less effective coverage)',
          dose: 'Children: 10 mg/kg PO on day 1 (max 500mg), then 5 mg/kg on days 2-5. OR 30 mg/kg single dose. Adults: 500mg day 1, then 250mg days 2-5',
          route: 'PO',
          duration: '5 days (or single dose)',
          contraindications: ['Macrolide allergy', 'QT prolongation'],
          sideEffects: ['GI upset', 'QT prolongation', 'Hepatotoxicity (rare)'],
          alternatives: ['Clarithromycin', 'Trimethoprim-sulfamethoxazole']
        },
        {
          drug: 'Ciprofloxacin Ear Drops (for CSOM)',
          indication: '🔴 FIRST-LINE for CSOM - Topical fluoroquinolone for chronic discharge; safe with perforation',
          dose: 'Ciprofloxacin 0.3% ear drops: 3-4 drops into affected ear twice daily',
          route: 'Topical otic',
          duration: '7-14 days; may need longer for CSOM',
          contraindications: ['Fluoroquinolone allergy'],
          sideEffects: ['Local irritation (rare)', 'No ototoxicity concerns'],
          alternatives: ['Ofloxacin ear drops']
        },
        {
          drug: 'Ofloxacin Ear Drops (for CSOM)',
          indication: '🔴 FIRST-LINE for CSOM - Alternative topical fluoroquinolone; safe with perforation',
          dose: 'Ofloxacin 0.3% ear drops: Children ≥1 year: 5 drops twice daily. Adults: 10 drops twice daily',
          route: 'Topical otic',
          duration: '7-14 days',
          contraindications: ['Fluoroquinolone allergy'],
          sideEffects: ['Local irritation (rare)'],
          alternatives: ['Ciprofloxacin ear drops']
        },
        {
          drug: 'Topical Antiseptics (for CSOM)',
          indication: '🟠 ALTERNATIVE for CSOM - When fluoroquinolone drops unavailable; use with aural toilet',
          dose: 'Dilute acetic acid 2% (vinegar solution): 3-5 drops twice daily after cleaning',
          route: 'Topical otic',
          duration: 'Until discharge resolves; long-term may be needed',
          contraindications: ['Severe canal inflammation'],
          sideEffects: ['Stinging', 'Local irritation'],
          alternatives: ['Boric acid powder', 'Aluminum acetate']
        }
      ],
      
      nonPharmacological: [
        'AURAL TOILET (Essential for CSOM): Gently clean ear discharge with cotton wick, dry mopping; before applying topical medications',
        'DRY EAR PRECAUTIONS (CSOM): No water in ear; cotton ball with petroleum jelly during bathing; no swimming',
        'WARM COMPRESS: May provide comfort for ear pain',
        'ELEVATE HEAD: Slightly elevate head during sleep to promote drainage',
        'BREASTFEEDING: Protective against AOM; encourage for infants',
        'AVOID PASSIVE SMOKE: Increases AOM risk; counsel family on smoke-free environment',
        'AVOID BOTTLE PROPPING: Do not feed infant lying flat with propped bottle',
        'PNEUMOCOCCAL AND HIB VACCINATION: Reduces AOM incidence; ensure up-to-date',
        'INFLUENZA VACCINATION: Annual vaccination reduces AOM during flu season',
        'TREAT ALLERGIC RHINITIS: If present; may reduce Eustachian tube dysfunction'
      ],
      
      surgical: [
        'MYRINGOTOMY: Incision in TM to drain effusion; immediate relief; rarely needed for uncomplicated AOM',
        'TYMPANOSTOMY TUBES (Grommets): For recurrent AOM (≥3 in 6 months or ≥4 in 12 months) or persistent OME >3 months with hearing loss; ventilate middle ear',
        'ADENOIDECTOMY: May be added to tubes for recurrent AOM, especially if nasal obstruction; reduces recurrence',
        'MASTOIDECTOMY: For acute mastoiditis not responding to antibiotics, or chronic mastoiditis; drainage and removal of infected bone',
        'TYMPANOPLASTY: Repair of TM perforation; for CSOM with persistent perforation after infection controlled',
        'CHOLESTEATOMA EXCISION: Mastoidectomy with removal of cholesteatoma; prevents complications'
      ],
      
      referralCriteria: [
        '🔴 IMMEDIATE: Signs of mastoiditis - Post-auricular swelling, tenderness, protruding ear; needs IV antibiotics and possible surgery',
        '🔴 IMMEDIATE: Signs of intracranial complication - Severe headache, altered mental status, neck stiffness, focal neurological signs',
        '🔴 IMMEDIATE: Facial nerve palsy - Indicates spread of infection; urgent ENT and possibly neurology',
        '🟠 URGENT: AOM not responding to second-line antibiotics - Consider resistant organisms, complications',
        '🟠 URGENT: Suspected cholesteatoma - Marginal perforation, white debris, foul discharge',
        '🟡 ROUTINE: Recurrent AOM (≥3 episodes in 6 months or ≥4 in 12 months) - Consider tympanostomy tubes',
        '🟡 ROUTINE: OME persisting >3 months with hearing loss - Consider tubes if affecting development',
        '🟡 ROUTINE: CSOM not responding to topical treatment - May need systemic treatment or surgery',
        '🟡 ROUTINE: TM perforation not healing after 3 months - Consider tympanoplasty',
        '🟡 ROUTINE: Hearing loss concern - Audiology and ENT evaluation'
      ],
      
      monitoring: [
        'AOM (Observation): Recheck in 48-72 hours if no antibiotics given; ensure pain controlled; start antibiotics if worsening',
        'AOM (On Antibiotics): Should improve within 48-72 hours; if not improving, reassess and consider second-line treatment',
        'POST-AOM: Recheck at 2-3 months if effusion persisted; many resolve spontaneously',
        'OME: Recheck every 3 months; hearing assessment if persisting >3 months; most resolve by 3 months',
        'CSOM: Regular aural toilet and topical treatment; monitor for healing; hearing assessment',
        'TYMPANOSTOMY TUBES: Check every 6 months; tubes typically extrude in 6-18 months; watch for otorrhea',
        'HEARING: Assess in persistent OME, CSOM, recurrent AOM, or any developmental concern'
      ],
      
      followUp: `
════════════════════════════════════════════════════════════════════════════════
FOLLOW-UP CARE
════════════════════════════════════════════════════════════════════════════════

ACUTE OTITIS MEDIA MANAGEMENT:

OBSERVATION (Watchful Waiting) - Appropriate if ALL of:
• Age ≥2 years
• Unilateral OR mild-moderate symptoms
• Non-severe illness (no high fever, not toxic)
• Reliable follow-up available
• Pain controlled

If observing:
• Ensure adequate analgesia (paracetamol/ibuprofen)
• Safety net prescription for antibiotics if worsens
• Recheck in 48-72 hours
• Start antibiotics if no improvement or worsening

IMMEDIATE ANTIBIOTICS - Indicated if ANY of:
• Age <2 years (especially <6 months)
• Bilateral AOM
• Severe symptoms (high fever ≥39°C, severe pain, toxic appearance)
• Otorrhea (TM perforation with discharge)
• Immunocompromised child
• No reliable follow-up

ANTIBIOTIC CHOICE:
First-line: Amoxicillin 80-90 mg/kg/day divided BD or TDS

Use Amoxicillin-Clavulanate if:
• Received antibiotics in past 30 days
• Concurrent conjunctivitis (suggests H. influenzae)
• Treatment failure with amoxicillin

Duration:
• ≥2 years with mild-moderate: 5 days
• <2 years OR severe: 10 days

TREATMENT FAILURE (No improvement after 48-72 hours):
• Reassess diagnosis (is it really AOM?)
• Switch to amoxicillin-clavulanate if on amoxicillin
• If on amoxicillin-clavulanate: Ceftriaxone IM x 3 days
• Consider tympanocentesis for culture (rarely available)
• Look for complications

OTITIS MEDIA WITH EFFUSION (OME):

• Usually follows AOM or occurs with Eustachian tube dysfunction
• Most cases resolve spontaneously within 3 months
• NO antibiotics (not effective)
• NO antihistamines or decongestants (not effective)
• Monitor hearing

If OME persists >3 months:
• Audiometry to assess hearing
• Consider tympanostomy tubes if:
  - Bilateral OME with hearing loss ≥25-30 dB
  - Speech/language delay concern
  - At-risk children (developmental delays, cleft palate)

CHRONIC SUPPURATIVE OTITIS MEDIA (CSOM):

TREATMENT PRINCIPLES:
1. AURAL TOILET (Most important):
   - Clean ear of discharge with dry cotton wick
   - Dry mopping - gently absorb discharge
   - Do before applying topical medications
   - May need suction cleaning (if available)

2. DRY EAR PRECAUTIONS:
   - No water in the ear
   - Cotton ball with petroleum jelly during bathing
   - No swimming
   - Prevent reinfection

3. TOPICAL ANTIBIOTICS:
   - Ciprofloxacin or Ofloxacin ear drops (safe with perforation)
   - Apply after aural toilet
   - 7-14 days; may need longer
   - Systemic antibiotics alone are NOT sufficient

4. IF NOT RESPONDING:
   - Culture discharge
   - Consider systemic antibiotics in addition to topical
   - ENT referral
   - Exclude cholesteatoma

RECURRENT AOM (≥3 in 6 months or ≥4 in 12 months):
• Consider tympanostomy tubes
• Ensure vaccinations up-to-date
• Address risk factors (daycare, smoke exposure, breastfeeding)
• Prophylactic antibiotics NOT recommended (resistance concern)

PREVENTION:
• Breastfeeding (protective)
• Avoid supine bottle feeding
• Smoke-free environment
• Pneumococcal conjugate vaccine (PCV)
• Hib vaccine
• Influenza vaccine
• Treat allergic rhinitis if present`
    },

    redFlags: [
      '🔴 CRITICAL: Post-auricular swelling, tenderness, protruding ear - Mastoiditis; urgent IV antibiotics and ENT referral',
      '🔴 CRITICAL: Signs of meningitis - Fever, neck stiffness, altered consciousness; may complicate OM; lumbar puncture and IV antibiotics',
      '🔴 CRITICAL: Facial nerve palsy - Facial weakness with ear infection; indicates serious spread; urgent ENT',
      '🔴 CRITICAL: Severe headache with altered mental status - Possible intracranial complication (abscess, sinus thrombosis)',
      '🟠 URGENT: High fever with toxic appearance - May indicate bacteremia or serious complication',
      '🟠 URGENT: AOM not responding to second-line antibiotics - Consider resistant organisms, complication, alternative diagnosis',
      '🟠 URGENT: Marginal TM perforation or white debris - Possible cholesteatoma; needs ENT evaluation',
      '🟠 URGENT: Vertigo with ear infection - May indicate labyrinthitis; needs assessment',
      '🟡 CAUTION: Persistent OME >3 months - Check hearing; may affect speech/language development',
      '🟡 CAUTION: Recurrent AOM affecting quality of life - Consider ENT referral for tubes'
    ],

    complications: [
      'Mastoiditis - Infection spreads to mastoid bone; post-auricular swelling; requires IV antibiotics, often surgery',
      'Tympanic membrane perforation - Usually heals spontaneously; persistent perforation may need tympanoplasty',
      'Hearing loss - Conductive from effusion/perforation; usually temporary in AOM; permanent in CSOM',
      'Cholesteatoma - Abnormal skin growth in middle ear; destroys bone; requires surgery',
      'Labyrinthitis - Spread to inner ear; vertigo, sensorineural hearing loss',
      'Facial nerve palsy - Spread to facial nerve; usually recovers with treatment',
      'Meningitis - Rare but serious; spread to meninges',
      'Brain abscess - Rare; spread through temporal bone or vasculature',
      'Lateral sinus thrombosis - Rare; spread to sigmoid sinus; presents with headache, fever',
      'Speech and language delay - Due to chronic hearing loss from OME or CSOM'
    ],

    prognosis: `
════════════════════════════════════════════════════════════════════════════════
PROGNOSIS
════════════════════════════════════════════════════════════════════════════════

ACUTE OTITIS MEDIA:
• Excellent prognosis
• Most cases resolve within 7-14 days
• 60-80% resolve spontaneously without antibiotics
• Pain usually improves within 24-48 hours with or without antibiotics
• Antibiotics shorten duration by approximately 1 day
• TM perforation from AOM usually heals spontaneously
• Recurrence is common (30-40% have repeat episode within 6 months)

OTITIS MEDIA WITH EFFUSION:
• Most cases resolve spontaneously
• 50% resolve by 1 month after AOM
• 90% resolve by 3 months
• Hearing returns to normal once effusion clears
• Persistent OME may impact language development

CHRONIC SUPPURATIVE OTITIS MEDIA:
• Variable prognosis depending on treatment
• With proper treatment (aural toilet + topical antibiotics): Good outcomes
• Without treatment: Progressive hearing loss, complications
• Hearing loss is often permanent if CSOM prolonged
• Cholesteatoma requires surgical management

COMPLICATIONS:
• Mastoiditis: Good prognosis with early treatment; may need surgery
• Intracranial complications: Serious but rare; improved with modern antibiotics
• Hearing loss: Permanent if untreated CSOM or repeated infections

IMPACT ON CHILDREN:
• Temporary hearing loss may affect language development
• Recurrent infections cause significant burden on families
• CSOM-related hearing loss has major educational and economic impact

FACTORS AFFECTING PROGNOSIS:
• Age at first episode (younger = more recurrence)
• Access to healthcare
• Vaccination status
• Environmental factors (smoke exposure, daycare)
• Underlying conditions (cleft palate, immunodeficiency)`,

    prevention: `
════════════════════════════════════════════════════════════════════════════════
PREVENTION STRATEGIES
════════════════════════════════════════════════════════════════════════════════

PRIMARY PREVENTION:

VACCINATION:
• Pneumococcal conjugate vaccine (PCV13/PCV15) - Reduces AOM by 6-7%
• Haemophilus influenzae type b (Hib) vaccine
• Influenza vaccine - Annual; reduces AOM during flu season

BREASTFEEDING:
• Exclusive breastfeeding for first 6 months
• Continue breastfeeding to 12 months or beyond
• Reduces AOM risk by 50% in first year

AVOID SMOKE EXPOSURE:
• No smoking in household
• Reduces AOM risk by 30-40%
• Major modifiable risk factor

FEEDING PRACTICES:
• Do not prop bottle while infant lying flat
• Hold infant semi-upright during feeding
• Avoid prolonged pacifier use after 6 months

REDUCE DAYCARE EXPOSURE:
• Smaller daycare groups if possible
• Home care reduces AOM risk
• Not always practical/possible

HAND HYGIENE:
• Reduces transmission of respiratory viruses
• Teach children proper handwashing

SECONDARY PREVENTION:

PROMPT TREATMENT:
• Treat AOM appropriately to prevent complications
• Complete antibiotic course if prescribed

PREVENT RECURRENCE:
• Address modifiable risk factors
• Tympanostomy tubes if recurrent AOM criteria met
• Adenoidectomy may help in selected cases

CSOM PREVENTION:
• Treat AOM effectively
• Dry ear precautions with TM perforation
• Early treatment of ear discharge

TERTIARY PREVENTION:

PREVENT COMPLICATIONS:
• Recognize warning signs (mastoiditis, etc.)
• Early referral for concerning features
• Monitor hearing in chronic ear disease

HEARING PRESERVATION:
• Early treatment of CSOM
• Dry ear precautions
• Surgery (tympanoplasty) when appropriate

PUBLIC HEALTH STRATEGIES FOR AFRICA:
• Strengthen vaccination programs (PCV, Hib)
• Promote breastfeeding
• Reduce household smoke exposure
• Train health workers in ear care
• Establish ear care services at primary level
• Community education on ear health
• Early identification and treatment of CSOM
• Hearing screening programs`,

    patientEducation: `
════════════════════════════════════════════════════════════════════════════════
PATIENT AND FAMILY EDUCATION
════════════════════════════════════════════════════════════════════════════════

FOR PARENTS OF CHILDREN WITH EAR INFECTION (AOM):

WHAT IS AN EAR INFECTION?
An ear infection happens when germs get into the space behind the eardrum and cause swelling and fluid buildup. It is very common in young children because the tube that drains the ear is short and easily blocked.

WHAT ARE THE SYMPTOMS?
• Ear pain (young children may pull at their ears, cry, be irritable)
• Fever
• Trouble sleeping
• Not hearing well
• Not wanting to eat
• Sometimes pus draining from the ear

DOES MY CHILD NEED ANTIBIOTICS?
Not always! Many ear infections get better on their own. Your doctor may recommend:
• Pain medicine only and watch for 2-3 days ("watchful waiting")
• Antibiotics right away if your child is very young, very sick, or has infection in both ears

If given a "wait and see" prescription - only use it if your child is not better in 2-3 days or gets worse.

PAIN RELIEF IS MOST IMPORTANT:
• Give paracetamol or ibuprofen as directed
• Do not wait for antibiotics to control pain
• A warm cloth on the ear may help

IF ANTIBIOTICS ARE PRESCRIBED:
• Give ALL the medicine even if your child feels better
• Do not skip doses
• Complete the full course (usually 5-10 days)

WHEN TO COME BACK OR SEEK URGENT CARE:
• Your child is getting worse instead of better
• High fever does not respond to medicine
• Swelling or redness behind the ear
• Your child seems very sick, weak, or confused
• Stiff neck
• Weakness of the face

═══════════════════════════════════════════════════════════════════════════════

FOR PATIENTS WITH CHRONIC EAR DISCHARGE (CSOM):

WHAT IS HAPPENING?
You have a hole in your eardrum with ongoing infection. This causes discharge from your ear and hearing loss. It will not go away without proper treatment.

TREATMENT HAS THREE PARTS:

1. KEEP THE EAR CLEAN (Most Important):
• Gently clean discharge from your ear with clean, dry cotton
• Do this 1-2 times daily, or whenever there is discharge
• Be gentle - do not push anything deep into the ear

2. KEEP THE EAR DRY:
• NO water should enter the ear
• Use a cotton ball with petroleum jelly (Vaseline) during bathing
• No swimming until the infection is controlled and the hole has healed
• Avoid getting soap or shampoo in the ear

3. USE EAR DROPS:
• Apply drops AFTER cleaning the ear
• Lie on your side with the affected ear up
• Put in the correct number of drops
• Stay lying down for a few minutes to let drops work
• Continue for the full course (usually 1-2 weeks or longer)

WHY KEEPING IT DRY IS SO IMPORTANT:
Water carries germs and keeps the infection going. Even clean-looking water can make the infection worse. Protecting your ear from water is as important as using the ear drops.

WHEN TO COME BACK:
• Discharge is not improving after 2 weeks of treatment
• You develop pain (infection may be spreading)
• Swelling behind the ear
• Dizziness
• Weakness of the face

HEARING LOSS:
Your hearing may be reduced because of the infection and the hole in your eardrum. With proper treatment, some hearing can be restored. If the hole does not heal on its own, an operation can repair it.

═══════════════════════════════════════════════════════════════════════════════

PREVENTION OF EAR INFECTIONS:

• Breastfeed babies if possible
• Keep vaccinations up to date
• Do not smoke around children (very important!)
• Hold babies upright during feeding, not flat
• Wash hands frequently to prevent colds
• Avoid putting objects in ears

═══════════════════════════════════════════════════════════════════════════════
KEY MESSAGE
═══════════════════════════════════════════════════════════════════════════════

Most ear infections in children are not serious and get better with pain medicine and time. However, follow your doctors advice about antibiotics. Chronic ear discharge needs special care - keep the ear clean and dry. Protecting hearing is important for children's development.`,

    references: [
      'Lieberthal AS, Carroll AE, Chonmaitree T, et al. The diagnosis and management of acute otitis media. Pediatrics. 2013;131(3):e964-e999.',
      'Rosenfeld RM, Shin JJ, Schwartz SR, et al. Clinical Practice Guideline: Otitis Media with Effusion (Update). Otolaryngol Head Neck Surg. 2016;154(1 Suppl):S1-S41.',
      'World Health Organization. Chronic suppurative otitis media: Burden of illness and management options. 2004.',
      'Monasta L, Ronfani L, Marchetti F, et al. Burden of disease caused by otitis media: systematic review and global estimates. PLoS One. 2012;7(4):e36226.',
      'Schilder AG, Chonmaitree T, Cripps AW, et al. Otitis media. Nat Rev Dis Primers. 2016;2:16063.',
      'Venekamp RP, Sanders SL, Steyerberg EW, et al. Antibiotics for acute otitis media in children. Cochrane Database Syst Rev. 2015;(6):CD000219.',
      'Rovers MM, Glasziou P, Appelman CL, et al. Antibiotics for acute otitis media: a meta-analysis with individual patient data. Lancet. 2006;368(9545):1429-1435.',
      'Acuin J. Chronic suppurative otitis media. BMJ Clin Evid. 2007;2007:0507.',
      'Macfadyen CA, Acuin JM, Gamble C. Systemic antibiotics versus topical treatments for chronically discharging ears with underlying eardrum perforations. Cochrane Database Syst Rev. 2006;(1):CD005608.',
      'Verhoeff M, van der Veen EL, Rovers MM, et al. Chronic suppurative otitis media: a review. Int J Pediatr Otorhinolaryngol. 2006;70(1):1-12.',
      'Berman S. Otitis media in developing countries. Pediatrics. 1995;96(1 Pt 1):126-131.',
      'Mulwafu W, Kuper H, Ensink RJ. Prevalence and causes of hearing impairment in Africa. Trop Med Int Health. 2016;21(2):158-165.',
      'National Institute for Health and Care Excellence (NICE). Otitis media (acute): antimicrobial prescribing. NICE guideline [NG91]. 2018.'
    ],
    
    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
  // Conditions 4-100 would follow the same detailed structure


  // ═══════════════════════════════════════════════════════════════════════════
  // 86. ALLERGIC RHINITIS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '86',
    name: 'Allergic Rhinitis',
    slug: 'allergic-rhinitis',
    specialty: 'ENT',
    bodySystem: 'Respiratory',
    icdCode: 'J30',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Allergic rhinitis is an IgE-mediated inflammatory disorder of the nasal mucosa characterized by nasal congestion, rhinorrhea, sneezing, and nasal itching following allergen exposure. It affects 10-40% of the global population and significantly impacts quality of life, sleep, cognitive function, and work/school productivity.

In Africa, allergic rhinitis is increasingly recognized as a major health burden, with prevalence rates of 7-27% across different regions. The condition is often underdiagnosed and undertreated due to limited awareness, restricted access to allergy testing, and misconceptions that symptoms represent "just a cold." The unified airway concept recognizes that allergic rhinitis and asthma are manifestations of the same underlying disease process, with up to 40% of rhinitis patients having concurrent asthma.

Key African considerations include high exposure to house dust mites (thriving in humid environments), mold spores, and specific regional pollens. Urban air pollution in rapidly growing African cities exacerbates symptoms.

🔴 KEY CLINICAL POINTS:
• Intranasal corticosteroids are FIRST-LINE (more effective than antihistamines)
• Second-generation antihistamines preferred (less sedating)
• Decongestant nasal sprays: MAXIMUM 5-7 days (rhinitis medicamentosa risk)
• 40% of patients have concurrent asthma - always screen
• ARIA Classification: Intermittent vs Persistent; Mild vs Moderate-Severe`,

    epidemiology: `GLOBAL BURDEN: Affects 400+ million people worldwide. Prevalence 10-40% globally, increasing in developing nations.

AFRICAN EPIDEMIOLOGY:
• Prevalence: 7.2% (Ethiopia) to 27.3% (South Africa)
• Nigeria: 12-20% in school children
• Kenya: 18% in Nairobi schoolchildren
• Urban > rural (pollution, lifestyle changes)

RISK FACTORS:
• Family history of atopy (strongest predictor)
• Urban dwelling
• Indoor allergens (dust mites thrive in humid African climates)
• Exposure to biomass fuel smoke

COMMON ALLERGENS IN AFRICA:
• House dust mites - most common
• Mold spores (Aspergillus, Alternaria)
• Cockroach allergens (urban Africa)
• Grass pollens (varies by region)
• Pet dander (increasingly common)`,

    clinicalPresentation: `CARDINAL SYMPTOMS (ARIA criteria):
1. Nasal congestion/obstruction
2. Rhinorrhea (clear, watery discharge)
3. Sneezing (often paroxysmal)
4. Nasal itching
• Symptoms typically bilateral and recurrent

ASSOCIATED SYMPTOMS:
• Allergic conjunctivitis (50-70%): itching, redness, watering
• Post-nasal drip with throat clearing
• Ear fullness (Eustachian tube dysfunction)
• Hyposmia (reduced smell)
• Sleep disturbance, fatigue
• "Allergic fog" - impaired concentration

ARIA CLASSIFICATION:
By Duration:
• Intermittent: <4 days/week OR <4 weeks
• Persistent: ≥4 days/week AND ≥4 weeks

By Severity:
• Mild: Normal sleep and daily activities
• Moderate-Severe: Impaired sleep OR daily activities

EXAMINATION FINDINGS:
• Pale, boggy nasal mucosa
• Clear watery secretions
• Swollen inferior turbinates
• Allergic shiners (dark circles)
• Dennie-Morgan lines
• Nasal crease ("allergic salute")
• Cobblestoning of posterior pharynx`,

    diagnosis: `CLINICAL DIAGNOSIS (Primary approach):
Allergic rhinitis is primarily a clinical diagnosis based on:
• Characteristic symptom pattern
• Temporal relationship to allergen exposure
• Associated atopic conditions
• Response to empiric treatment

DIAGNOSTIC CRITERIA (ARIA):
Two or more symptoms for >1 hour on most days:
1. Watery rhinorrhea
2. Sneezing (especially paroxysmal)
3. Nasal obstruction
4. Nasal itching
PLUS allergic etiology suggested by history

ALLERGY TESTING (when available):
• Skin Prick Testing (SPT) - Gold standard, limited in Africa
• Serum Specific IgE - Alternative when SPT unavailable
• Nasal smear for eosinophils - Simple, supports diagnosis

RESOURCE-LIMITED APPROACH:
1. Detailed clinical history (most important)
2. Physical examination
3. Trial of treatment (response supports diagnosis)
4. Refer for testing if uncertain or refractory`,

    differentialDiagnosis: [
      'Viral URI (common cold) - Shorter duration (7-10 days), fever, sore throat',
      'Non-allergic rhinitis (vasomotor) - No allergic trigger, irritant-induced',
      'Chronic rhinosinusitis - Facial pain, purulent discharge, >12 weeks',
      'Nasal polyposis - Progressive obstruction, anosmia',
      'Rhinitis medicamentosa - Decongestant overuse >5-7 days',
      'Infectious rhinitis - Purulent discharge, unilateral, fever',
      'Foreign body (children) - Unilateral, foul discharge',
      'CSF rhinorrhea - Clear unilateral discharge, post-trauma',
      'Drug-induced rhinitis - ACE inhibitors, NSAIDs, beta-blockers'
    ],

    investigations: [
      {
        name: 'Clinical history and examination',
        indication: 'All patients - cornerstone of diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'Anterior rhinoscopy',
        indication: 'Assess mucosa, rule out polyps, septal deviation',
        availability: 'BASIC'
      },
      {
        name: 'Nasal smear for eosinophils',
        indication: 'Supports allergic etiology if >10% eosinophils',
        availability: 'BASIC'
      },
      {
        name: 'Complete blood count',
        indication: 'May show peripheral eosinophilia',
        availability: 'BASIC'
      },
      {
        name: 'Skin prick testing (SPT)',
        indication: 'Identify specific allergens, immunotherapy consideration',
        availability: 'ADVANCED'
      },
      {
        name: 'Serum specific IgE',
        indication: 'When SPT unavailable or patient on antihistamines',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Nasal endoscopy',
        indication: 'Detailed examination, identify polyps',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT sinuses',
        indication: 'If sinusitis suspected or surgical planning',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Confirm diagnosis based on clinical history',
        'Classify: severity (mild vs moderate-severe) and pattern (intermittent vs persistent)',
        'Assess impact on quality of life and sleep',
        'Screen for associated asthma, conjunctivitis, eczema',
        'Identify triggers through detailed history',
        'Initiate allergen avoidance measures',
        'Begin pharmacotherapy based on ARIA classification'
      ],

      pharmacological: [
        {
          drug: 'Intranasal corticosteroids (Fluticasone, Beclomethasone, Mometasone)',
          indication: 'FIRST-LINE for moderate-severe allergic rhinitis - most effective treatment',
          dose: 'Fluticasone 50-100mcg/nostril daily; Beclomethasone 50-100mcg/nostril BD; Mometasone 100mcg/nostril daily',
          route: 'Intranasal',
          duration: 'Regular use; continuous for persistent symptoms',
          contraindications: ['Untreated nasal infections', 'Recent nasal surgery', 'Nasal ulcers'],
          sideEffects: ['Nasal dryness', 'Epistaxis (direct spray away from septum)', 'Throat irritation'],
          alternatives: ['Different intranasal corticosteroid', 'Oral antihistamine if steroids not tolerated']
        },
        {
          drug: 'Second-generation oral antihistamines (Cetirizine, Loratadine, Fexofenadine)',
          indication: 'First-line for mild symptoms; adjunct to intranasal steroids; effective for sneezing, itching, rhinorrhea',
          dose: 'Cetirizine 10mg OD; Loratadine 10mg OD; Fexofenadine 120-180mg OD',
          route: 'Oral',
          duration: 'PRN for intermittent; regular for persistent',
          contraindications: ['Hypersensitivity', 'Severe renal impairment (dose adjust)'],
          sideEffects: ['Minimal sedation', 'Dry mouth', 'Headache'],
          alternatives: ['First-generation antihistamines if cost barrier (more sedating)']
        },
        {
          drug: 'Intranasal antihistamines (Azelastine, Olopatadine)',
          indication: 'Alternative to oral antihistamines; rapid onset',
          dose: 'Azelastine 1-2 sprays/nostril BD; Olopatadine 2 sprays/nostril BD',
          route: 'Intranasal',
          duration: 'PRN or regular use',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Bitter taste (common)', 'Nasal irritation', 'Drowsiness'],
          alternatives: ['Oral antihistamines']
        },
        {
          drug: 'Intranasal decongestants (Oxymetazoline, Xylometazoline)',
          indication: 'Short-term relief of severe congestion - MAXIMUM 5-7 DAYS',
          dose: 'Oxymetazoline 2-3 sprays/nostril BD; Xylometazoline 2-3 sprays/nostril TDS',
          route: 'Intranasal',
          duration: 'MAXIMUM 5-7 days (rhinitis medicamentosa risk)',
          contraindications: ['Hypertension', 'Coronary artery disease', 'Glaucoma', 'MAOIs'],
          sideEffects: ['Rebound congestion with overuse', 'Nasal dryness', 'Tachycardia'],
          alternatives: ['Intranasal corticosteroids (slower onset but no rebound)']
        },
        {
          drug: 'Leukotriene receptor antagonists (Montelukast)',
          indication: 'Add-on therapy; particularly useful if concurrent asthma',
          dose: 'Adults: 10mg OD; Children 6-14y: 5mg OD; Children 2-5y: 4mg OD',
          route: 'Oral',
          duration: 'Regular use for persistent symptoms',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'GI upset', 'Neuropsychiatric events (monitor mood)'],
          alternatives: ['Intranasal corticosteroids']
        },
        {
          drug: 'Nasal saline irrigation',
          indication: 'Adjunct therapy - clears allergens, reduces medication need',
          dose: 'Isotonic or hypertonic saline, 1-2 times daily',
          route: 'Intranasal',
          duration: 'Ongoing as needed',
          contraindications: ['None significant'],
          sideEffects: ['Nasal irritation', 'Ear discomfort if technique poor'],
          alternatives: ['Saline sprays if irrigation not tolerated']
        }
      ],

      nonPharmacological: [
        'ALLERGEN AVOIDANCE - Cornerstone of management',
        'House dust mite reduction: allergen-proof mattress/pillow covers, wash bedding weekly >60°C, reduce humidity <50%, remove bedroom carpets',
        'Mold reduction: fix water leaks, improve ventilation, remove visible mold',
        'Pollen avoidance: keep windows closed on high pollen days, shower after outdoor exposure',
        'Cockroach control: eliminate food sources, seal cracks, bait traps',
        'Pet allergens: keep pets out of bedrooms, HEPA air purifiers',
        'Smoking cessation and avoidance of secondhand smoke',
        'Proper intranasal spray technique education: shake, tilt head forward, direct spray away from septum'
      ],

      surgical: [
        'Inferior turbinate reduction - for severe refractory nasal obstruction',
        'Septoplasty - for significant septal deviation',
        'FESS - for concurrent chronic rhinosinusitis or polyps'
      ],

      referralCriteria: [
        'Uncertain diagnosis requiring allergy testing',
        'Moderate-severe symptoms uncontrolled after 2-4 weeks appropriate therapy',
        'Consideration for allergen immunotherapy',
        'Suspected structural abnormality (polyps, septal deviation)',
        'Concurrent difficult-to-control asthma',
        'Occupational rhinitis',
        'Significant quality of life impact despite treatment'
      ],

      monitoring: [
        'Symptom assessment at each visit',
        'Quality of life and sleep impact',
        'Medication adherence and spray technique',
        'Side effects (epistaxis with intranasal steroids)',
        'Concurrent asthma control',
        'Development of complications (sinusitis, polyps)'
      ],

      followUp: `INITIAL: Mild - 2-4 weeks; Moderate-severe - 1-2 weeks then 2-4 weeks.
ONGOING: Well-controlled - every 3-6 months; Annual review of diagnosis and treatment.
STEP-DOWN: Consider after 3 months good control. STEP-UP: Check diagnosis, adherence, technique, then add/increase medications.`
    },

    redFlags: [
      '🔴 CRITICAL: Unilateral symptoms (obstruction, discharge, bleeding) - Exclude neoplasm, foreign body, CSF leak',
      '🔴 CRITICAL: Blood-stained discharge without trauma - Exclude malignancy',
      '🔴 CRITICAL: Stridor, severe dyspnea, anaphylaxis features - Manage as anaphylaxis',
      '🟠 URGENT: Periorbital swelling with fever - Orbital cellulitis from sinusitis',
      '🟠 URGENT: Severe headache with nasal symptoms - Complicated sinusitis',
      '🟠 URGENT: New anosmia without congestion - Investigate other causes',
      '🟡 WARNING: Symptoms only at work - Occupational rhinitis assessment needed',
      '🟡 WARNING: Crusting with foul odor - Atrophic rhinitis or infection',
      '🟡 WARNING: Decongestant use >7 days - Rhinitis medicamentosa risk'
    ],

    complications: [
      'Acute and chronic sinusitis',
      'Asthma exacerbation (unified airway)',
      'Otitis media with effusion',
      'Nasal polyps',
      'Sleep disturbance and OSA',
      'Dental malocclusion in children (chronic mouth breathing)',
      'Impaired quality of life, productivity',
      'Rhinitis medicamentosa (decongestant overuse)'
    ],

    prognosis: `NATURAL HISTORY: Chronic condition with variable course. Childhood-onset may improve in 20-30% by adulthood. Adult-onset tends to persist. 10-40% develop asthma.

WITH TREATMENT: Most achieve good symptom control. QoL significantly improves. Intranasal steroids reduce symptoms 30-50%. Immunotherapy provides disease-modifying effects.

BETTER OUTCOMES: Mild disease, good adherence, successful allergen avoidance, single allergen sensitivity.
POORER OUTCOMES: Polysensitization, concurrent asthma, occupational exposures.`,

    prevention: `PRIMARY: Limited evidence. Breastfeeding recommended. Avoid maternal smoking during pregnancy.

SECONDARY (Symptom reduction):
• Allergen avoidance appropriate to specific sensitivities
• Early effective treatment may prevent asthma progression
• Immunotherapy provides disease-modifying effects

ENVIRONMENTAL CONTROL:
• Dust mites: Encasings, hot wash bedding, reduce humidity
• Pollens: Monitor counts, keep windows closed, shower after exposure
• Molds: Control humidity, fix leaks, ventilate
• Pets: Keep out of bedroom, HEPA purifiers`,

    patientEducation: `UNDERSTANDING ALLERGIC RHINITIS:
Your nose becomes inflamed when exposed to substances you're allergic to. This is a real medical condition affecting millions - not something to "just ignore."

COMMON TRIGGERS IN AFRICA:
• House dust mites (in bedding, carpets)
• Mold (damp areas)
• Pollen from grasses
• Pet dander
• Cockroach droppings

USING NASAL SPRAY CORRECTLY:
1. Blow nose gently first
2. Shake bottle
3. Tilt head FORWARD (not back)
4. Point spray toward outer wall of nose
5. Breathe in gently
6. Don't sniff hard

IMPORTANT MEDICATION FACTS:
• Steroid nasal sprays are SAFE long-term - different from harmful steroids
• Use steroid sprays DAILY for best effect, not just when bad
• Decongestant sprays (Otrivin) - MAXIMUM 5-7 days or they make things worse
• Newer antihistamines cause less drowsiness

WHEN TO SEEK HELP:
• Symptoms not improving with treatment
• One-sided symptoms only
• Blood in discharge
• Fever with worsening symptoms
• Severe headache or facial pain

ASTHMA CONNECTION:
If you have allergic rhinitis, you may also have asthma. Tell your doctor about any wheezing, chest tightness, or breathlessness.`,

    references: [
      'ARIA Guidelines 2023 Update',
      'WHO Essential Medicines List 2023',
      'BSACI Guidelines on Allergic Rhinitis',
      'EAACI Guidelines 2020',
      'ISAAC Phase Three Study - African Data',
      'South African Allergic Rhinitis Working Group 2012'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 87. URTICARIA (HIVES)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '87',
    name: 'Urticaria (Hives)',
    slug: 'urticaria',
    specialty: 'Dermatology',
    bodySystem: 'Dermatological',
    icdCode: 'L50',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Urticaria (hives) is a common skin condition characterized by intensely pruritic, transient, raised wheals (welts) that can appear anywhere on the body. Individual wheals typically resolve within 24 hours without leaving marks, but new ones may continuously appear. It affects 15-25% of people at some point in their lives.

The condition is classified as ACUTE (<6 weeks) or CHRONIC (≥6 weeks). Acute urticaria is often triggered by infections, medications, foods, or insect stings. Chronic urticaria is frequently idiopathic (chronic spontaneous urticaria) with no identifiable external trigger.

🔴 CRITICAL CLINICAL POINTS:
• Second-generation H1 antihistamines are FIRST-LINE treatment
• Up-dosing antihistamines (up to 4x standard dose) BEFORE adding other agents
• Always assess for ANGIOEDEMA - especially lips, tongue, throat
• Rule out ANAPHYLAXIS if systemic symptoms present
• Chronic urticaria: Often no identifiable cause (50-80% idiopathic)
• Omalizumab for refractory chronic spontaneous urticaria (limited in Africa)

AFRICAN CONTEXT:
• Infections are a common trigger (parasitic, viral, bacterial)
• Drug reactions common (including traditional medicines)
• Insect bites/stings prevalent
• Limited access to specialist care and newer biologics
• Chronic urticaria significantly impacts quality of life`,

    epidemiology: `GLOBAL BURDEN:
• Lifetime prevalence: 15-25% (acute urticaria)
• Point prevalence of chronic urticaria: 0.5-5%
• Affects all ages, peak in 20-40 years
• Female predominance in chronic urticaria (2:1)
• Acute urticaria more common in children and young adults

AFRICAN EPIDEMIOLOGY:
• Limited epidemiological data from Africa
• Higher rates of infection-related acute urticaria
• Parasitic infections as triggers (schistosomiasis, helminth infections)
• Drug-induced urticaria common (antimalarials, antibiotics, NSAIDs)
• Insect-related urticaria (bee stings, mosquito bites, bed bugs)

COMMON TRIGGERS:
Acute Urticaria:
• Infections (viral URIs most common, bacterial, parasitic)
• Medications (antibiotics, NSAIDs, opioids, ACE inhibitors)
• Foods (shellfish, nuts, eggs, milk - usually within 2 hours)
• Insect stings/bites
• Contact allergens (latex, plants)
• Physical triggers (pressure, cold, heat, sun)

Chronic Urticaria:
• Idiopathic/spontaneous (50-80%)
• Autoimmune (anti-FcεRI or anti-IgE antibodies)
• Physical urticarias (dermographism, cold, cholinergic)
• Chronic infections (H. pylori, parasites - controversial)
• Rarely: underlying systemic disease`,

    clinicalPresentation: `SYMPTOMS:
• Intensely pruritic (itchy) raised wheals
• Wheals vary in size from millimeters to large plaques
• Individual wheals resolve within 24 hours (usually 1-4 hours)
• New wheals may appear as old ones resolve
• Burning or stinging sensation (less common than itch)

CHARACTERISTICS OF WHEALS:
• Raised, erythematous with central pallor
• Well-defined borders
• Blanch with pressure
• Any body area affected
• Leave NO residual marks after resolution

ANGIOEDEMA (occurs in 40-50% of urticaria):
• Deeper swelling of dermis/subcutaneous tissue
• Commonly affects face, lips, tongue, hands, feet, genitals
• May affect throat/larynx → airway compromise
• Less pruritic, more painful/burning
• Resolves more slowly (24-72 hours)

CLASSIFICATION BY DURATION:
• Acute urticaria: <6 weeks duration
• Chronic urticaria: ≥6 weeks duration
  - Chronic spontaneous urticaria (CSU): No external trigger
  - Chronic inducible urticaria: Specific trigger (physical)

PHYSICAL URTICARIAS:
• Dermographism (most common): Wheals at sites of scratching/pressure
• Cold urticaria: Exposure to cold air, water, objects
• Cholinergic urticaria: Heat, exercise, emotional stress (small wheals)
• Delayed pressure urticaria: Prolonged pressure (hours later)
• Solar urticaria: Sun exposure
• Aquagenic urticaria: Contact with water (rare)`,

    diagnosis: `CLINICAL DIAGNOSIS:
Urticaria is primarily a clinical diagnosis based on:
• Characteristic appearance of wheals
• Transient nature (<24 hours per individual wheal)
• Intense pruritus
• History of triggers and timing

KEY HISTORY QUESTIONS:
1. Duration: Acute vs chronic (>6 weeks)?
2. Timing: When do wheals appear? How long do they last?
3. Triggers: Foods, medications, infections, physical factors?
4. Associated symptoms: Angioedema? Systemic symptoms?
5. Medications: Recent changes? NSAIDs? ACE inhibitors?
6. Impact: Sleep, work, quality of life?
7. Previous treatments: What has been tried?

PHYSICAL EXAMINATION:
• Examine skin for active wheals - document appearance
• Check for angioedema (face, lips, tongue)
• Dermographism test: Stroke skin firmly with tongue depressor
• Assess for underlying conditions

WHEN TO INVESTIGATE:
• Chronic urticaria (>6 weeks)
• Suspected underlying cause
• Individual wheals lasting >24 hours (consider urticarial vasculitis)
• Systemic symptoms
• Not responding to standard treatment

INVESTIGATIONS (Chronic Urticaria):
• FBC with differential - eosinophilia, basopenia
• ESR/CRP - exclude inflammatory conditions
• Thyroid function tests - autoimmune thyroiditis common
• Consider: LFTs, hepatitis serology, stool for parasites (Africa)

DIFFERENTIAL DIAGNOSIS:
Wheals lasting >24 hours with bruising suggest urticarial vasculitis (needs biopsy)`,

    differentialDiagnosis: [
      'Urticarial vasculitis - Wheals last >24 hours, painful rather than itchy, leave bruising/pigmentation, may have systemic symptoms',
      'Erythema multiforme - Target lesions, fixed (not transient), often follows infections or drug exposure',
      'Bullous pemphigoid (urticarial phase) - May start with urticarial plaques before blisters, older patients',
      'Insect bite reactions - Papular urticaria, persistent, grouped, exposed areas',
      'Contact dermatitis - Localized to contact area, may have vesicles, slower to resolve',
      'Drug eruption - May be urticarial but often morbilliform, timing related to drug exposure',
      'Mastocytosis - Urticaria pigmentosa, Darier sign positive (whealing on rubbing lesions)',
      'Angioedema without urticaria - Consider hereditary angioedema (C1-INH deficiency) or ACE inhibitor-induced',
      'Anaphylaxis - Urticaria with hypotension, respiratory symptoms, GI symptoms - EMERGENCY'
    ],

    investigations: [
      {
        name: 'Clinical history and examination',
        indication: 'All patients - diagnosis is primarily clinical',
        availability: 'BASIC'
      },
      {
        name: 'Dermographism test',
        indication: 'Suspected physical urticaria - stroke skin firmly',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count with differential',
        indication: 'Chronic urticaria - check for eosinophilia, infection',
        availability: 'BASIC'
      },
      {
        name: 'ESR / CRP',
        indication: 'Chronic urticaria - screen for inflammatory conditions',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid function tests',
        indication: 'Chronic urticaria - autoimmune thyroiditis association',
        availability: 'BASIC'
      },
      {
        name: 'Liver function tests',
        indication: 'Chronic urticaria - hepatitis association',
        availability: 'BASIC'
      },
      {
        name: 'Stool microscopy for ova and parasites',
        indication: 'Chronic urticaria in Africa - parasitic trigger',
        availability: 'BASIC'
      },
      {
        name: 'Hepatitis B and C serology',
        indication: 'Chronic urticaria - viral hepatitis association',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Autologous serum skin test (ASST)',
        indication: 'Suspected autoimmune urticaria - limited availability',
        availability: 'ADVANCED'
      },
      {
        name: 'Skin biopsy',
        indication: 'Suspected urticarial vasculitis - wheals >24 hours, bruising',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Specific IgE / skin prick tests',
        indication: 'Suspected allergic trigger in acute urticaria',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        'Assess for anaphylaxis - if present, treat as emergency (IM adrenaline)',
        'Assess airway - angioedema of lips/tongue/throat requires urgent management',
        'Identify and remove trigger if known (stop suspected medication, avoid food)',
        'Classify: acute vs chronic; spontaneous vs inducible',
        'Assess severity and impact on quality of life',
        'Start first-line treatment: second-generation H1 antihistamine'
      ],

      pharmacological: [
        {
          drug: 'Second-generation H1 antihistamines (Cetirizine, Loratadine, Fexofenadine, Desloratadine, Bilastine)',
          indication: 'FIRST-LINE for all urticaria - effective and well-tolerated',
          dose: 'Standard dose initially: Cetirizine 10mg OD, Loratadine 10mg OD, Fexofenadine 180mg OD. UP-DOSE up to 4x if needed',
          route: 'Oral',
          duration: 'Acute: until resolved + 3-5 days; Chronic: continuous daily dosing',
          contraindications: ['Hypersensitivity', 'Severe renal impairment (dose adjust)'],
          sideEffects: ['Drowsiness (less than first-gen)', 'Dry mouth', 'Headache'],
          alternatives: ['Different second-generation antihistamine', 'First-generation if cost barrier']
        },
        {
          drug: 'Up-dosed second-generation antihistamines',
          indication: 'When standard dose inadequate - GUIDELINE RECOMMENDED before adding other agents',
          dose: 'Increase up to 4x standard dose: e.g., Cetirizine 10mg QID (40mg/day), Fexofenadine 180mg BD-QID',
          route: 'Oral',
          duration: 'Continuous for chronic urticaria',
          contraindications: ['Severe renal/hepatic impairment at high doses'],
          sideEffects: ['Increased sedation at higher doses', 'Dry mouth'],
          alternatives: ['Add H2 blocker', 'Add montelukast']
        },
        {
          drug: 'First-generation H1 antihistamines (Chlorpheniramine, Diphenhydramine, Promethazine)',
          indication: 'Second-line if second-generation unavailable; useful at night for sedation',
          dose: 'Chlorpheniramine 4mg TDS-QID; Diphenhydramine 25-50mg TDS-QID; Promethazine 25mg BD-TDS',
          route: 'Oral',
          duration: 'Short-term preferred due to side effects',
          contraindications: ['Glaucoma', 'Urinary retention', 'Elderly (falls risk)'],
          sideEffects: ['Sedation (significant)', 'Anticholinergic effects', 'Cognitive impairment'],
          alternatives: ['Second-generation antihistamines (preferred)']
        },
        {
          drug: 'H2 receptor antagonists (Ranitidine, Famotidine)',
          indication: 'Add-on to H1 antihistamines if inadequate response',
          dose: 'Ranitidine 150mg BD; Famotidine 20-40mg BD',
          route: 'Oral',
          duration: 'Trial for 2-4 weeks to assess benefit',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'GI upset', 'Rare: hepatitis'],
          alternatives: ['Montelukast as add-on instead']
        },
        {
          drug: 'Leukotriene receptor antagonists (Montelukast)',
          indication: 'Add-on therapy when antihistamines inadequate; may help in aspirin-sensitive urticaria',
          dose: 'Montelukast 10mg OD (adults)',
          route: 'Oral',
          duration: 'Trial for 4 weeks; continue if beneficial',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Headache', 'GI upset', 'Neuropsychiatric effects (rare)'],
          alternatives: ['H2 blocker as add-on']
        },
        {
          drug: 'Oral corticosteroids (Prednisolone)',
          indication: 'SHORT COURSE ONLY for severe acute urticaria or acute exacerbation; NOT for long-term chronic urticaria',
          dose: 'Prednisolone 20-40mg daily for 3-7 days maximum',
          route: 'Oral',
          duration: 'MAXIMUM 7-10 days; avoid repeated courses',
          contraindications: ['Active infection', 'Uncontrolled diabetes'],
          sideEffects: ['Hyperglycemia', 'Mood changes', 'Insomnia', 'Long-term: adrenal suppression, osteoporosis'],
          alternatives: ['Optimized antihistamine therapy']
        },
        {
          drug: 'Omalizumab (anti-IgE)',
          indication: 'Chronic spontaneous urticaria refractory to up-dosed antihistamines - THIRD-LINE',
          dose: '300mg subcutaneous every 4 weeks',
          route: 'Subcutaneous injection',
          duration: 'Ongoing; reassess every 6-12 months',
          contraindications: ['Hypersensitivity'],
          sideEffects: ['Injection site reactions', 'Headache', 'Rare: anaphylaxis'],
          alternatives: ['Ciclosporin (specialist only)']
        },
        {
          drug: 'IM Adrenaline (Epinephrine)',
          indication: 'ANAPHYLAXIS with urticaria - EMERGENCY',
          dose: 'Adrenaline 0.5mg (0.5ml of 1:1000) IM; Repeat every 5-15 minutes if needed',
          route: 'Intramuscular (anterolateral thigh)',
          duration: 'Emergency use only',
          contraindications: ['None in anaphylaxis - life-saving'],
          sideEffects: ['Tachycardia', 'Tremor', 'Anxiety'],
          alternatives: ['None for anaphylaxis']
        }
      ],

      nonPharmacological: [
        'Identify and avoid triggers (keep symptom diary)',
        'Avoid known aggravating factors: NSAIDs, aspirin, alcohol, stress, overheating',
        'Cool compresses for symptomatic relief',
        'Loose, cotton clothing to reduce irritation',
        'Avoid hot baths/showers (can worsen symptoms)',
        'Moisturizers for dry skin (can reduce itch threshold)',
        'Stress management - stress can exacerbate urticaria',
        'Patient education: reassurance that most acute urticaria is self-limiting',
        'Symptom diary: document wheals, timing, potential triggers',
        'Medic alert bracelet if severe allergic triggers identified'
      ],

      surgical: [
        'No surgical treatment for urticaria',
        'Surgery only if underlying cause identified that requires it (rare)'
      ],

      referralCriteria: [
        'Chronic urticaria (>6 weeks) not responding to standard antihistamines',
        'Need for up-dosing beyond 2x standard dose',
        'Consideration for omalizumab or other biologics',
        'Suspected urticarial vasculitis (wheals >24 hours, bruising)',
        'Recurrent angioedema, especially without urticaria (exclude hereditary angioedema)',
        'Significant impact on quality of life despite treatment',
        'Diagnostic uncertainty'
      ],

      monitoring: [
        'Symptom control - frequency, severity, duration of wheals',
        'Quality of life assessment (sleep, work, daily activities)',
        'Side effects of antihistamines (especially sedation)',
        'Response to treatment escalation',
        'For chronic urticaria: Urticaria Activity Score (UAS7) if available',
        'Watch for development of angioedema'
      ],

      followUp: `ACUTE URTICARIA:
• Often self-limiting; follow up only if persisting >6 weeks or severe
• Review if trigger not identified and symptoms recur

CHRONIC URTICARIA:
• Initial: Review in 2-4 weeks to assess antihistamine response
• If controlled: Review every 1-3 months, attempt dose reduction every 3-6 months
• If uncontrolled: Review in 2-4 weeks after each escalation step
• Long-term: Annual review; many patients remit over 1-5 years`
    },

    redFlags: [
      '🔴 CRITICAL: Anaphylaxis (urticaria + hypotension, respiratory distress, GI symptoms) - IM ADRENALINE immediately',
      '🔴 CRITICAL: Angioedema of tongue, throat, larynx - Airway emergency, prepare for intubation',
      '🔴 CRITICAL: Rapidly spreading urticaria with systemic symptoms - Treat as anaphylaxis',
      '🟠 URGENT: Angioedema of lips/face without airway compromise - Close monitoring, may need steroids',
      '🟠 URGENT: Urticaria with fever and joint pain - Consider serum sickness, urticarial vasculitis',
      '🟠 URGENT: Wheals lasting >24 hours with bruising - Suspect urticarial vasculitis, needs biopsy',
      '🟡 WARNING: New medication started recently - Consider drug-induced urticaria',
      '🟡 WARNING: Recurrent angioedema without urticaria - Screen for hereditary angioedema',
      '🟡 WARNING: Not responding to up-dosed antihistamines - Needs specialist referral'
    ],

    complications: [
      'Anaphylaxis (if allergic trigger) - life-threatening',
      'Airway compromise from angioedema - emergency',
      'Significant quality of life impairment',
      'Sleep disturbance leading to fatigue, poor concentration',
      'Anxiety and depression (chronic urticaria)',
      'Work/school impairment',
      'Side effects from chronic antihistamine use',
      'Steroid side effects if used repeatedly (avoid in chronic urticaria)'
    ],

    prognosis: `ACUTE URTICARIA:
• Excellent prognosis - most cases resolve within days to weeks
• Recurrence possible if trigger re-exposure

CHRONIC SPONTANEOUS URTICARIA:
• Variable course - average duration 2-5 years
• Spontaneous remission occurs in most:
  - 50% remit within 1 year
  - 80% remit within 5 years
  - 20% may have symptoms >5 years
• Better prognosis: Mild symptoms, no angioedema, no autoimmune markers
• Worse prognosis: Severe symptoms, angioedema, positive ASST, autoimmune thyroiditis

WITH TREATMENT:
• Most patients achieve good control with antihistamines
• Up-dosing effective in additional 40-50%
• Omalizumab effective in 70-80% of refractory cases`,

    prevention: `PRIMARY PREVENTION:
• Avoid known triggers (medications, foods, physical factors)
• Careful drug history before prescribing (especially NSAIDs, antibiotics)
• Gradual introduction of new foods in patients with food allergies

SECONDARY PREVENTION (Reducing recurrence):
• Maintain symptom diary to identify triggers
• Avoid aggravating factors (aspirin, NSAIDs unless essential)
• Stress management
• For physical urticarias: avoid specific trigger (cold, heat, pressure)
• Continuous antihistamine therapy during flare-prone periods
• Treat underlying conditions (thyroid disease, infections)

PATIENT ADVICE:
• Carry emergency medication if severe allergic triggers (antihistamines, adrenaline autoinjector if prescribed)
• Wear medical alert identification if anaphylaxis history
• Inform healthcare providers of triggers before procedures`,

    patientEducation: `UNDERSTANDING URTICARIA (HIVES):
Urticaria causes itchy, raised bumps (wheals) on your skin that come and go. Individual bumps usually disappear within a few hours, but new ones may appear. It is NOT contagious.

ACUTE VS CHRONIC:
• Acute: Lasts less than 6 weeks - often caused by infections, medications, or foods
• Chronic: Lasts more than 6 weeks - often no specific cause is found

WHEN TO SEEK EMERGENCY HELP:
🚨 GO TO HOSPITAL IMMEDIATELY IF:
• Swelling of lips, tongue, or throat
• Difficulty breathing or swallowing
• Feeling faint or dizzy
• Severe symptoms spreading rapidly

COMMON TRIGGERS TO AVOID:
• Medications that made it worse (especially aspirin, ibuprofen)
• Foods that triggered reactions
• Extreme temperatures
• Tight clothing or pressure on skin
• Stress

TAKING YOUR ANTIHISTAMINES:
• Take daily as prescribed, not just when symptoms appear
• Newer antihistamines (cetirizine, loratadine) cause less drowsiness
• Your doctor may increase the dose - this is safe and often more effective
• Don't stop suddenly if taking for chronic urticaria

HELPFUL TIPS:
• Keep a diary of when wheals appear to identify triggers
• Use cool compresses for itch relief
• Avoid hot showers/baths
• Wear loose, cotton clothing
• Manage stress - it can make symptoms worse

WHAT TO EXPECT:
• Acute urticaria usually resolves within days to weeks
• Chronic urticaria can last months to years BUT most people improve over time
• Treatment controls symptoms - keep taking medication as prescribed
• Follow up with your doctor to adjust treatment as needed`,

    references: [
      'EAACI/GA²LEN/EDF/WAO Urticaria Guidelines 2022',
      'British Association of Dermatologists Guidelines 2021',
      'UpToDate: Chronic spontaneous urticaria: Clinical manifestations, diagnosis, pathogenesis, and natural history',
      'UpToDate: Chronic spontaneous urticaria: Treatment',
      'WHO Essential Medicines List 2023',
      'Zuberbier T, et al. The international EAACI/GA²LEN/EuroGuiDerm/APAAACI guideline for the definition, classification, diagnosis, and management of urticaria. Allergy. 2022'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 88. CONTACT DERMATITIS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '88',
    name: 'Contact Dermatitis',
    slug: 'contact-dermatitis',
    specialty: 'Dermatology',
    bodySystem: 'Dermatological',
    icdCode: 'L23-L25',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Contact dermatitis is an inflammatory skin reaction caused by direct contact with an external substance. It is one of the most common occupational skin diseases and a frequent cause of hand eczema. The condition is classified into two main types:

1. IRRITANT CONTACT DERMATITIS (ICD) - 80% of cases
   • Direct chemical/physical damage to skin
   • No prior sensitization required
   • Can affect anyone with sufficient exposure
   • Common irritants: soaps, detergents, solvents, water, friction

2. ALLERGIC CONTACT DERMATITIS (ACD) - 20% of cases
   • Type IV delayed hypersensitivity reaction
   • Requires prior sensitization (days to years)
   • Only affects sensitized individuals
   • Common allergens: nickel, fragrances, preservatives, rubber

🔴 KEY CLINICAL POINTS:
• IDENTIFY AND REMOVE THE OFFENDING AGENT - Most important step
• Topical corticosteroids for inflammation
• Emollients for skin barrier repair
• Patch testing for allergic type (limited availability in Africa)
• Occupational causes are common - take detailed work history

AFRICAN CONTEXT:
• Traditional remedies and cosmetics are common causes
• Occupational exposures: agriculture, mining, construction, healthcare
• Hair care products (relaxers, dyes) significant cause
• Plant-related dermatitis (e.g., Rhus species, cashew nut shell)
• Limited access to patch testing for diagnosis`,

    epidemiology: `GLOBAL BURDEN:
• Lifetime prevalence: 15-20%
• Accounts for 90-95% of occupational skin diseases
• Hand dermatitis affects 2-10% of population
• Women more commonly affected (cosmetics, cleaning, healthcare)
• Irritant CD more common than allergic CD (80% vs 20%)

AFRICAN EPIDEMIOLOGY:
• Limited epidemiological data
• Occupational CD common in:
  - Agricultural workers (pesticides, plants, fertilizers)
  - Healthcare workers (gloves, hand hygiene products)
  - Construction workers (cement - chromate allergy)
  - Hairdressers (dyes, relaxers, chemicals)
  - Domestic workers (cleaning products)
• Traditional medicine/cosmetic-related CD significant
• Hair product-related dermatitis particularly common

COMMON CAUSES BY REGION/OCCUPATION:

Irritant Contact Dermatitis:
• Water/wet work (most common occupational irritant)
• Soaps and detergents
• Solvents and chemicals
• Cement (also allergic - chromate)
• Friction and microtrauma
• Plants (mechanical irritation)

Allergic Contact Dermatitis:
• Nickel (jewelry, belt buckles, phones) - most common allergen globally
• Fragrances (cosmetics, toiletries)
• Preservatives (methylisothiazolinone, formaldehyde releasers)
• Hair dye (paraphenylenediamine - PPD)
• Rubber chemicals (gloves, shoes)
• Topical medications (neomycin, bacitracin, lanolin)
• Plants (poison ivy not in Africa, but Rhus species present)
• Cement (chromate)

AFRICAN-SPECIFIC CAUSES:
• Traditional cosmetics and skin lightening creams
• Hair relaxers and dyes (PPD)
• Shea butter preparations (may contain additives)
• Traditional remedies applied topically
• Cashew nut shell oil
• Mango (cross-reacts with poison ivy family)
• Latex (rubber tappers)`,

    clinicalPresentation: `ACUTE CONTACT DERMATITIS:
• Erythema (redness)
• Edema (swelling)
• Vesicles and bullae (blisters)
• Weeping and oozing
• Intense pruritus (itch)
• Sharp demarcation at site of contact

CHRONIC CONTACT DERMATITIS:
• Lichenification (thickened skin with accentuated markings)
• Scaling and dryness
• Fissuring (cracks)
• Hyperpigmentation or hypopigmentation
• Less distinct borders
• May spread beyond contact area

DISTRIBUTION PATTERNS (Diagnostic Clues):
• Hands: Occupational, wet work, cleaning products, gloves
• Face: Cosmetics, skincare products, airborne allergens
• Eyelids: Nail polish (transferred), cosmetics, airborne
• Neck: Jewelry (nickel), fragrances, necklaces
• Wrists: Watch straps, bracelets
• Feet: Shoe components (rubber, chromate, dyes)
• Trunk: Clothing dyes, fabric finishes, belt buckles
• Perioral: Toothpaste, lip products, foods

DISTINGUISHING IRRITANT VS ALLERGIC:

Irritant Contact Dermatitis:
• Burning, stinging more than itching
• Occurs at site of contact only
• Develops within minutes to hours
• Dose-dependent (stronger irritant = worse reaction)
• Sharp borders matching contact area
• May occur on first exposure

Allergic Contact Dermatitis:
• Intensely pruritic (itchy)
• May spread beyond contact area
• Delayed onset (24-72 hours after exposure)
• Small amount can trigger reaction
• May have satellite lesions
• Requires prior sensitization (days to years)

SPECIAL PRESENTATIONS:
• Airborne contact dermatitis: Face, neck, V of chest, exposed arms
• Photocontact dermatitis: Sun-exposed areas after topical agent
• Systemic contact dermatitis: Widespread after systemic exposure to allergen`,

    diagnosis: `CLINICAL DIAGNOSIS:
Contact dermatitis is primarily diagnosed clinically based on:
• Characteristic morphology and distribution
• Temporal relationship to exposure
• Improvement with avoidance
• Detailed exposure history

KEY HISTORY QUESTIONS:
1. When and where did the rash start?
2. What substances contact the affected area?
3. Occupation: What do you work with? Protective equipment?
4. Hobbies: Gardening, crafts, sports?
5. Cosmetics and personal care products?
6. New products introduced recently?
7. Does it improve on weekends/holidays? (occupational)
8. Any topical medications or traditional remedies used?

PHYSICAL EXAMINATION:
• Document distribution pattern precisely
• Note morphology: acute (vesicles, weeping) vs chronic (lichenification)
• Check for sharp borders (suggests contact pattern)
• Examine hands, face, and flexures carefully
• Look for secondary infection

PATCH TESTING (Allergic Contact Dermatitis):
• Gold standard for identifying contact allergens
• Apply standardized allergens under occlusion for 48 hours
• Read at 48 hours and 72-96 hours
• Positive: Erythema, induration, vesicles at test site
• LIMITED AVAILABILITY in most African settings
• Refer to dermatologist if available

WHEN TO SUSPECT ALLERGIC VS IRRITANT:
Allergic more likely if:
• Intense pruritus
• Spread beyond contact area
• Recurs with minimal exposure
• Occupational pattern (better on holiday)
• Distribution suggests specific allergen (e.g., nickel pattern)

Irritant more likely if:
• Burning/stinging predominant
• Strictly confined to contact area
• Dose-dependent response
• Wet work history
• Multiple people affected (occupational outbreak)`,

    differentialDiagnosis: [
      'Atopic dermatitis - Flexural distribution, personal/family history of atopy, starts in childhood, chronic relapsing course',
      'Psoriasis - Well-defined plaques with silvery scale, nail changes, may have joint symptoms',
      'Nummular eczema - Coin-shaped plaques, not following contact pattern, often on legs',
      'Seborrheic dermatitis - Scalp, face (nasolabial folds, eyebrows), greasy scales',
      'Tinea (fungal infection) - Annular lesions with active scaly border, KOH positive',
      'Scabies - Burrows, web spaces involved, nocturnal itch, household contacts affected',
      'Stasis dermatitis - Lower legs, associated with venous insufficiency, edema',
      'Drug eruption - Widespread, timing related to drug, may have systemic symptoms',
      'Photodermatitis - Strictly sun-exposed areas, spares shaded sites',
      'Id reaction (autoeczematization) - Widespread secondary to fungal infection elsewhere'
    ],

    investigations: [
      {
        name: 'Detailed clinical history and examination',
        indication: 'All patients - cornerstone of diagnosis',
        availability: 'BASIC'
      },
      {
        name: 'Patch testing',
        indication: 'Suspected allergic contact dermatitis, chronic/recurrent cases, occupational dermatitis',
        availability: 'ADVANCED'
      },
      {
        name: 'Skin biopsy',
        indication: 'Diagnostic uncertainty, exclude other conditions (psoriasis, mycosis fungoides)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'KOH preparation',
        indication: 'Exclude tinea if annular or scaly lesions',
        availability: 'BASIC'
      },
      {
        name: 'Bacterial swab',
        indication: 'Suspected secondary infection (crusting, pustules)',
        availability: 'BASIC'
      },
      {
        name: 'Prick testing',
        indication: 'Suspected contact urticaria (immediate reaction)',
        availability: 'ADVANCED'
      },
      {
        name: 'Repeated Open Application Test (ROAT)',
        indication: 'Confirm relevance of suspected allergen when patch testing unavailable',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        'IDENTIFY AND REMOVE THE CAUSATIVE AGENT - Most critical step',
        'Take detailed history of exposures (occupation, hobbies, cosmetics, medications)',
        'Assess severity: Mild (localized) vs Moderate-Severe (widespread, affecting function)',
        'Check for secondary infection',
        'Provide symptomatic relief with emollients and topical steroids',
        'Advise on protective measures'
      ],

      pharmacological: [
        {
          drug: 'Topical corticosteroids',
          indication: 'First-line anti-inflammatory treatment for active dermatitis',
          dose: 'Mild (face, flexures): Hydrocortisone 1% BD. Moderate (body): Betamethasone 0.1% BD. Severe: Clobetasol 0.05% BD',
          route: 'Topical',
          duration: 'Acute: 1-2 weeks then taper. Avoid potent steroids >2 weeks continuously',
          contraindications: ['Infected skin (treat infection first)', 'Rosacea', 'Perioral dermatitis'],
          sideEffects: ['Skin atrophy', 'Striae', 'Telangiectasia', 'Steroid acne', 'Tachyphylaxis'],
          alternatives: ['Topical calcineurin inhibitors for sensitive areas']
        },
        {
          drug: 'Emollients (moisturizers)',
          indication: 'Essential for all patients - repairs skin barrier, reduces irritation',
          dose: 'Apply liberally and frequently, at least 3-4 times daily, more if needed',
          route: 'Topical',
          duration: 'Ongoing - continue even when dermatitis cleared',
          contraindications: ['Known allergy to ingredients (e.g., lanolin)'],
          sideEffects: ['Stinging on broken skin (may need different product)', 'Folliculitis (rare)'],
          alternatives: ['Different emollient formulation (cream, ointment, lotion)']
        },
        {
          drug: 'Topical calcineurin inhibitors (Tacrolimus, Pimecrolimus)',
          indication: 'Face, flexures, eyelids - steroid-sparing; maintenance therapy',
          dose: 'Tacrolimus 0.1% (adults) or 0.03% (children/face) BD; Pimecrolimus 1% BD',
          route: 'Topical',
          duration: 'Can use longer-term than potent steroids',
          contraindications: ['Active skin infection', 'Immunocompromised patients'],
          sideEffects: ['Burning/stinging initially', 'Flushing with alcohol'],
          alternatives: ['Low-potency topical corticosteroids']
        },
        {
          drug: 'Oral antihistamines',
          indication: 'Symptomatic relief of pruritus, especially at night',
          dose: 'Cetirizine 10mg OD; Chlorpheniramine 4mg TDS (sedating - useful at night)',
          route: 'Oral',
          duration: 'As needed for symptom control',
          contraindications: ['Caution in elderly with sedating antihistamines'],
          sideEffects: ['Drowsiness (first-generation)', 'Dry mouth'],
          alternatives: ['Non-sedating: loratadine, fexofenadine']
        },
        {
          drug: 'Oral corticosteroids',
          indication: 'Severe acute contact dermatitis (widespread, disabling); NOT for chronic use',
          dose: 'Prednisolone 0.5-1mg/kg/day (30-60mg) for 5-14 days, taper over 2-3 weeks',
          route: 'Oral',
          duration: 'Short course only; taper to prevent rebound',
          contraindications: ['Active infection', 'Uncontrolled diabetes', 'Peptic ulcer'],
          sideEffects: ['Hyperglycemia', 'Mood changes', 'Insomnia', 'GI upset'],
          alternatives: ['Potent topical steroids with wet wraps']
        },
        {
          drug: 'Topical antibiotics',
          indication: 'Secondary bacterial infection (crusting, pustules, golden crust)',
          dose: 'Fusidic acid 2% BD or Mupirocin 2% TDS',
          route: 'Topical',
          duration: '5-7 days',
          contraindications: ['Known allergy'],
          sideEffects: ['Contact sensitization (especially neomycin - avoid)'],
          alternatives: ['Oral antibiotics if widespread infection']
        },
        {
          drug: 'Oral antibiotics',
          indication: 'Widespread secondary infection, cellulitis',
          dose: 'Flucloxacillin 500mg QID or Erythromycin 500mg QID (if penicillin allergic)',
          route: 'Oral',
          duration: '7 days',
          contraindications: ['Allergy to antibiotic class'],
          sideEffects: ['GI upset', 'Allergic reaction'],
          alternatives: ['Clindamycin', 'Co-amoxiclav']
        },
        {
          drug: 'Barrier creams',
          indication: 'Prevention in occupational dermatitis; protect against irritants',
          dose: 'Apply before exposure to irritants',
          route: 'Topical',
          duration: 'Ongoing with occupational exposure',
          contraindications: ['None significant'],
          sideEffects: ['May interfere with glove adhesion'],
          alternatives: ['Appropriate gloves']
        }
      ],

      nonPharmacological: [
        'ALLERGEN/IRRITANT AVOIDANCE - Identify and avoid the cause (most important)',
        'Protective gloves: Vinyl or nitrile (not latex if latex allergy); cotton liners for sweating',
        'Occupational modifications: Change work practices, substitute less harmful substances',
        'Hand care: Wash with lukewarm water, mild soap-free cleansers, pat dry, apply emollient',
        'Avoid over-washing: Wet work damages skin barrier',
        'Remove rings when wet work (trap irritants)',
        'Emollient use: Frequent, generous application; after hand washing; at bedtime',
        'Wet wraps: For severe dermatitis - apply steroid, cover with damp then dry bandage',
        'Substitute products: Fragrance-free, hypoallergenic alternatives',
        'Education: Explain condition, importance of avoidance, proper skin care',
        'Occupational health: Workplace assessment, protective equipment, job modification'
      ],

      surgical: [
        'No surgical treatment for contact dermatitis'
      ],

      referralCriteria: [
        'Diagnostic uncertainty - need for patch testing',
        'Suspected occupational contact dermatitis (may need specialist report)',
        'Not responding to standard treatment after 2-4 weeks',
        'Severe or widespread disease affecting function',
        'Recurrent disease despite allergen avoidance',
        'Need for systemic immunosuppression',
        'Patch testing requested to identify allergen'
      ],

      monitoring: [
        'Response to treatment - reassess in 1-2 weeks',
        'Compliance with avoidance measures',
        'Side effects of topical steroids (especially if potent or prolonged)',
        'Development of secondary infection',
        'Impact on work and quality of life',
        'For occupational: Documentation for workplace'
      ],

      followUp: `ACUTE CONTACT DERMATITIS:
• Review in 1-2 weeks to assess response
• If improving: Continue emollients, taper steroids
• If not improving: Reassess diagnosis, check compliance, consider referral

CHRONIC CONTACT DERMATITIS:
• Regular follow-up every 2-4 weeks until controlled
• Occupational cases: May need ongoing monitoring
• Long-term: Periodic review, reinforce avoidance, check for steroid side effects

OCCUPATIONAL CONTACT DERMATITIS:
• May require medical report for employer
• Liaison with occupational health
• Consider job modification or retraining if severe`
    },

    redFlags: [
      '🔴 CRITICAL: Widespread severe blistering with systemic symptoms - Consider severe drug reaction (SJS/TEN)',
      '🔴 CRITICAL: Airway involvement (inhaled allergen) with respiratory distress - Emergency',
      '🟠 URGENT: Facial/eyelid swelling affecting vision - Needs prompt treatment',
      '🟠 URGENT: Secondary infection with fever, lymphadenopathy, spreading erythema - Cellulitis',
      '🟠 URGENT: Severe hand dermatitis affecting daily function/work - Urgent dermatology referral',
      '🟡 WARNING: Not responding to appropriate treatment - Reassess diagnosis',
      '🟡 WARNING: Chronic use of potent topical steroids - Monitor for side effects',
      '🟡 WARNING: Occupational pattern (better on holidays) - Needs workplace assessment'
    ],

    complications: [
      'Secondary bacterial infection (Staphylococcus, Streptococcus)',
      'Post-inflammatory hyperpigmentation (common in darker skin)',
      'Post-inflammatory hypopigmentation',
      'Lichenification (chronic rubbing/scratching)',
      'Skin atrophy from topical steroid overuse',
      'Occupational disability and job loss',
      'Psychological impact: anxiety, depression, reduced quality of life',
      'Spread of allergen sensitization (new allergies develop)',
      'Id reaction (autoeczematization) - generalized spread'
    ],

    prognosis: `PROGNOSIS:
• Excellent if causative agent identified and avoided
• Irritant CD: Usually clears within 1-3 weeks of avoiding irritant
• Allergic CD: May take 2-4 weeks to clear after allergen removal
• Chronic exposure leads to chronic dermatitis

FACTORS AFFECTING OUTCOME:
Good prognosis:
• Cause identified and avoidable
• Acute presentation
• Non-occupational cause
• Good compliance with avoidance and treatment

Poor prognosis:
• Unable to identify cause
• Unavoidable exposure (occupational, ubiquitous allergen)
• Chronic dermatitis with lichenification
• Multiple allergen sensitivities
• Atopic background (impaired skin barrier)

OCCUPATIONAL CONTACT DERMATITIS:
• Up to 25% may need to change occupation
• Early intervention improves outcome
• Persistent symptoms common if exposure continues`,

    prevention: `PRIMARY PREVENTION:
• Skin protection in high-risk occupations (gloves, barrier creams)
• Avoid known irritants and sensitizers
• Use protective equipment correctly
• Skin care: Regular emollients to maintain barrier
• Avoid over-washing hands

SECONDARY PREVENTION (After diagnosis):
• Strict allergen/irritant avoidance
• Read product labels for identified allergens
• Substitute products with hypoallergenic alternatives
• Inform healthcare providers of allergies (medical alert)
• Occupational: Workplace modifications, alternative duties

WORKPLACE MEASURES:
• Pre-employment screening for high-risk occupations
• Education on skin hazards and protection
• Provide appropriate protective equipment
• Access to emollients and mild cleansers
• Regular skin checks
• Early reporting of skin problems

PATIENT ADVICE - ALLERGEN AVOIDANCE:
If patch testing identifies allergen:
• Obtain list of products containing the allergen
• Check labels of all personal care products
• Inform hairdressers, beauticians of allergies
• Medical alert jewelry for severe reactions
• Cross-reactions: Be aware of related substances`,

    patientEducation: `UNDERSTANDING CONTACT DERMATITIS:
Contact dermatitis is a skin reaction caused by something that touches your skin. There are two types:
• Irritant: Direct damage from harsh substances (soaps, chemicals) - can happen to anyone
• Allergic: Your immune system reacts to a specific substance - only in sensitized people

THE MOST IMPORTANT TREATMENT:
Finding and avoiding what causes your rash is the KEY to getting better. All the creams in the world won't help if you keep exposing your skin to the cause.

COMMON CAUSES:
• Cleaning products and detergents
• Soaps and hand sanitizers
• Jewelry (especially nickel)
• Cosmetics and fragrances
• Hair dyes and relaxers
• Rubber gloves
• Plants
• Work-related chemicals

CARING FOR YOUR SKIN:
• Use lukewarm (not hot) water for washing
• Use gentle, fragrance-free soap or soap substitute
• Pat dry, don't rub
• Apply moisturizer immediately after washing and frequently throughout day
• Apply steroid cream only to affected areas as directed
• Wear cotton-lined gloves for wet work

USING YOUR STEROID CREAM:
• Apply thin layer to affected areas only
• Use for prescribed duration (usually 1-2 weeks at a time)
• Don't use strong steroids on face or skin folds
• Moisturize regularly between steroid applications
• See your doctor if not improving after 2 weeks

PROTECTING YOUR HANDS:
• Wear appropriate gloves for work (but not for too long - sweating irritates)
• Use cotton glove liners if hands sweat
• Remove rings when doing wet work
• Apply barrier cream before work if recommended
• Moisturize hands frequently

WHEN TO SEEK HELP:
• Rash spreading or getting worse
• Signs of infection (increased pain, pus, spreading redness, fever)
• Not improving despite treatment
• Affecting your sleep or daily activities
• Work-related rash that keeps coming back

IF YOU KNOW YOUR ALLERGEN:
• Read labels on all products you use
• Inform hairdressers, nail technicians of your allergies
• Tell doctors and nurses before any procedure
• Consider medical alert bracelet if severe`,

    references: [
      'British Association of Dermatologists Guidelines on Contact Dermatitis 2021',
      'European Society of Contact Dermatitis Guidelines',
      'American Contact Dermatitis Society Guidelines',
      'NICE CKS: Contact Dermatitis',
      'UpToDate: Contact dermatitis in adults',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 89. SCABIES
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '89',
    name: 'Scabies',
    slug: 'scabies',
    specialty: 'Dermatology',
    bodySystem: 'Dermatological',
    icdCode: 'B86',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Scabies is a highly contagious parasitic skin infestation caused by the mite Sarcoptes scabiei var. hominis. The female mite burrows into the stratum corneum, laying eggs and producing an intensely pruritic allergic reaction. It is a major public health problem in resource-limited settings.

Scabies affects an estimated 200-300 million people globally, with highest prevalence in tropical regions, overcrowded conditions, and areas with limited water access. It is classified as a Neglected Tropical Disease (NTD) by WHO.

🔴 KEY CLINICAL POINTS:
• INTENSE NOCTURNAL PRURITUS is the hallmark symptom
• Look for BURROWS in finger web spaces - pathognomonic
• TREAT ALL CLOSE CONTACTS simultaneously (even if asymptomatic)
• WASH/TREAT ALL BEDDING AND CLOTHING on day of treatment
• Permethrin 5% cream is first-line topical treatment
• Oral Ivermectin highly effective (single dose, may repeat in 1-2 weeks)
• Post-treatment itch may persist 2-4 weeks (does not indicate treatment failure)

AFRICAN CONTEXT:
• Highly prevalent in many African communities
• Overcrowding and limited water access increase transmission
• Secondary bacterial infection (impetigo) very common
• Ivermectin increasingly available and preferred in mass treatment
• Crusted (Norwegian) scabies seen in HIV/immunocompromised
• Part of WHO NTD elimination programs`,

    epidemiology: `GLOBAL BURDEN:
• 200-300 million cases annually worldwide
• Prevalence: 0.2-71% depending on region
• WHO Neglected Tropical Disease
• Higher in tropical/subtropical regions
• Affects all socioeconomic groups but worse in poverty

AFRICAN EPIDEMIOLOGY:
• Prevalence varies: 2-30% in community surveys
• Higher in children (peak 5-14 years)
• Seasonal variation in some regions
• Associated with overcrowding, water scarcity
• School outbreaks common
• Often endemic in refugee camps, orphanages

TRANSMISSION:
• Direct prolonged skin-to-skin contact (>15-20 minutes)
• Sexual contact (common route in adults)
• Sharing of bedding/clothing (less common, except crusted scabies)
• NOT transmitted by brief casual contact (handshake)
• Mites survive off host: 24-36 hours (up to 7 days in humid conditions)

RISK FACTORS:
• Overcrowded living conditions
• Poverty and limited water access
• Institutions (schools, orphanages, prisons, care homes)
• Sexual contact with infected person
• Immunocompromised (HIV, malnutrition) - risk of crusted scabies
• Healthcare workers caring for crusted scabies patients

VARIANTS:
• Classical scabies: Typical presentation, 10-15 mites
• Crusted (Norwegian) scabies: Hyperinfestation (millions of mites), immunocompromised, highly contagious
• Nodular scabies: Persistent nodules, especially genitalia
• Scabies incognito: Modified by steroids, atypical presentation`,

    clinicalPresentation: `SYMPTOMS:
• INTENSE PRURITUS - characteristically worse at night and after hot bath
• Pruritus develops 3-6 weeks after first infestation (sensitization period)
• Re-infestation: Symptoms within 1-3 days
• Family members/close contacts often affected

CLASSIC DISTRIBUTION (Adults):
• Finger web spaces - MOST COMMON, look for burrows
• Wrists (flexor surfaces)
• Elbows
• Axillae (anterior folds)
• Periumbilical area
• Waistband area (belt line)
• Buttocks
• Genitalia (men) - penile papules/nodules characteristic
• Breasts (women) - areolae
• Usually SPARES head and neck in adults

DISTRIBUTION IN INFANTS/YOUNG CHILDREN:
• May involve HEAD, FACE, NECK (unlike adults)
• Palms and soles commonly affected
• More widespread distribution
• Vesicles and pustules common

SKIN FINDINGS:
• BURROWS: Thin, wavy, grayish-white lines 2-10mm (pathognomonic)
• Papules: Small, erythematous
• Vesicles: Especially on hands in children
• Nodules: Scabetic nodules, persist for weeks/months (genitalia, axillae)
• Excoriations: From scratching
• Secondary changes: Eczematization, lichenification

CRUSTED (NORWEGIAN) SCABIES:
• Thick, hyperkeratotic crusts (psoriasis-like)
• May be widespread or localized (hands, feet, scalp)
• Minimal or NO pruritus (immune dysfunction)
• HIGHLY CONTAGIOUS - millions of mites (vs 10-15 in classic)
• Seen in: HIV/AIDS, elderly, immunosuppressed, neurological impairment
• Requires prolonged/repeated treatment

SECONDARY BACTERIAL INFECTION:
• Very common complication in Africa
• Staphylococcus aureus, Group A Streptococcus
• Impetigo, cellulitis, abscesses
• Can lead to post-streptococcal glomerulonephritis (PSGN)
• Treat infection alongside scabies`,

    diagnosis: `CLINICAL DIAGNOSIS:
Scabies is primarily a clinical diagnosis based on:
• Characteristic intense nocturnal pruritus
• Typical distribution
• Identification of burrows
• Affected household contacts

DIAGNOSTIC CRITERIA (Clinical):
HIGHLY SUGGESTIVE if:
1. Intense nocturnal pruritus AND
2. Typical distribution (web spaces, wrists, genitalia) AND
3. Household contacts with similar symptoms

CONFIRMATION METHODS:
1. Burrow identification: Look with magnifying glass in web spaces
2. Dermoscopy: "Jet with contrail" sign (mite head + burrow)
3. Skin scraping: Scrape burrow with scalpel, examine under microscope for mites, eggs, or fecal pellets (scybala)

PRACTICAL APPROACH IN RESOURCE-LIMITED SETTINGS:
• Clinical diagnosis usually sufficient
• High index of suspicion in endemic areas
• Treat empirically if clinical picture consistent
• Response to treatment confirms diagnosis

INK BURROW TEST (if microscopy unavailable):
1. Apply ink (fountain pen) to suspected burrow
2. Wipe off with alcohol swab
3. Ink retained in burrow appears as dark line

WHEN TO SUSPECT CRUSTED SCABIES:
• Thick hyperkeratotic scales
• Widespread "psoriasis-like" rash
• Known immunocompromised patient
• Minimal pruritus despite extensive skin changes
• Contacts developing symptoms rapidly

DIFFERENTIAL DIAGNOSIS CONSIDERATIONS:
• If not responding to treatment: Reconsider diagnosis
• If contacts unaffected: Less likely scabies
• If no itch: Consider other diagnoses (except crusted scabies)`,

    differentialDiagnosis: [
      'Atopic dermatitis - Flexural distribution, chronic relapsing, personal/family atopy history',
      'Contact dermatitis - Distribution matches contactant, may have identifiable cause',
      'Insect bites - Grouped, exposed areas, papular urticaria, no burrows',
      'Papular urticaria - Clusters of itchy papules, usually children, exposed areas',
      'Pediculosis (lice) - Scalp/pubic involvement, visible nits on hair shafts',
      'Dermatitis herpetiformis - Extremely itchy, grouped vesicles, elbows/knees, celiac association',
      'Drug eruption - Timing with medication, widespread, may have systemic symptoms',
      'Psoriasis - Silvery scale, well-demarcated plaques, nail changes (vs crusted scabies)',
      'Seborrheic dermatitis - Scalp, face, greasy scales',
      'Lichen planus - Purple polygonal papules, Wickham striae, oral involvement'
    ],

    investigations: [
      {
        name: 'Clinical examination with good lighting',
        indication: 'All patients - look for burrows in typical locations',
        availability: 'BASIC'
      },
      {
        name: 'Dermoscopy',
        indication: 'Visualize mite (delta wing jet sign) and burrows',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Skin scraping microscopy',
        indication: 'Confirm diagnosis - identify mites, eggs, or fecal pellets',
        availability: 'BASIC'
      },
      {
        name: 'Ink burrow test',
        indication: 'Highlight burrows when difficult to visualize',
        availability: 'BASIC'
      },
      {
        name: 'HIV test',
        indication: 'Crusted scabies or severe/recurrent infection',
        availability: 'BASIC'
      },
      {
        name: 'FBC',
        indication: 'May show eosinophilia; check if secondary infection',
        availability: 'BASIC'
      },
      {
        name: 'Wound swab',
        indication: 'Secondary bacterial infection for culture and sensitivity',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: 'Monitor for post-streptococcal glomerulonephritis if streptococcal infection',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        'Confirm clinical diagnosis (burrows, distribution, contacts)',
        'Assess for secondary bacterial infection - treat if present',
        'Identify ALL close contacts for simultaneous treatment',
        'Check for crusted scabies (immunocompromised patients)',
        'Educate on proper application of topical treatment',
        'Arrange environmental decontamination measures'
      ],

      pharmacological: [
        {
          drug: 'Permethrin 5% cream',
          indication: 'First-line topical treatment for classical scabies',
          dose: 'Apply to entire body from neck down (include head in infants). Leave on 8-14 hours (overnight), then wash off. Repeat in 1 week.',
          route: 'Topical',
          duration: 'Two applications, 1 week apart',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Mild skin irritation', 'Temporary burning/stinging'],
          alternatives: ['Ivermectin oral', 'Benzyl benzoate']
        },
        {
          drug: 'Ivermectin (oral)',
          indication: 'Highly effective for classical scabies; first-line for crusted scabies; mass treatment programs',
          dose: '200 mcg/kg single dose. Repeat in 1-2 weeks. Crusted scabies: Multiple doses needed (see specialist)',
          route: 'Oral',
          duration: 'Two doses, 1-2 weeks apart',
          contraindications: ['Pregnancy', 'Breastfeeding', 'Children <15kg or <5 years (some guidelines allow)'],
          sideEffects: ['Headache', 'Dizziness', 'Nausea', 'Mazzotti reaction if Loa loa co-infection'],
          alternatives: ['Permethrin topical']
        },
        {
          drug: 'Benzyl benzoate 25% lotion',
          indication: 'Alternative topical treatment where permethrin unavailable',
          dose: 'Apply to entire body from neck down. Leave on 24 hours, repeat application, wash off after another 24 hours. Use 12.5% for children.',
          route: 'Topical',
          duration: 'Two consecutive applications over 48 hours',
          contraindications: ['Hypersensitivity', 'Inflamed/broken skin (causes severe irritation)'],
          sideEffects: ['Skin irritation (common)', 'Burning sensation', 'Contact dermatitis'],
          alternatives: ['Permethrin', 'Ivermectin']
        },
        {
          drug: 'Sulfur 5-10% in petrolatum',
          indication: 'Safe in pregnancy, breastfeeding, infants <2 months',
          dose: 'Apply to entire body for 3 consecutive nights, wash off 24 hours after last application',
          route: 'Topical',
          duration: '3 consecutive nights',
          contraindications: ['Sulfur allergy'],
          sideEffects: ['Unpleasant smell', 'Skin irritation', 'Stains clothing'],
          alternatives: ['Permethrin (if not pregnant/infant)']
        },
        {
          drug: 'Crotamiton 10% cream/lotion',
          indication: 'Alternative; also has anti-pruritic properties',
          dose: 'Apply daily for 3-5 consecutive days',
          route: 'Topical',
          duration: '3-5 days',
          contraindications: ['Hypersensitivity', 'Inflamed skin'],
          sideEffects: ['Skin irritation'],
          alternatives: ['Permethrin', 'Ivermectin']
        },
        {
          drug: 'Antihistamines (for pruritus)',
          indication: 'Symptomatic relief of itch (does not treat scabies)',
          dose: 'Chlorpheniramine 4mg TDS (sedating) or Cetirizine 10mg OD',
          route: 'Oral',
          duration: 'Until itch resolves (may be 2-4 weeks after treatment)',
          contraindications: ['Sedating types: caution in elderly'],
          sideEffects: ['Drowsiness (first-generation)', 'Dry mouth'],
          alternatives: ['Calamine lotion topically']
        },
        {
          drug: 'Antibiotics (for secondary infection)',
          indication: 'Secondary bacterial infection (impetigo, cellulitis)',
          dose: 'Flucloxacillin 500mg QID or Erythromycin 500mg QID x 7 days',
          route: 'Oral',
          duration: '7 days',
          contraindications: ['Allergy to antibiotic'],
          sideEffects: ['GI upset', 'Allergic reaction'],
          alternatives: ['Topical fusidic acid for localized impetigo']
        },
        {
          drug: 'Topical corticosteroids',
          indication: 'Post-scabies eczema/persistent dermatitis AFTER completing scabicidal treatment',
          dose: 'Hydrocortisone 1% or Betamethasone 0.1% BD for 1-2 weeks',
          route: 'Topical',
          duration: '1-2 weeks after scabies treatment completed',
          contraindications: ['Active infection', 'Do not use BEFORE scabies treatment'],
          sideEffects: ['Skin atrophy if prolonged'],
          alternatives: ['Emollients alone for mild cases']
        }
      ],

      nonPharmacological: [
        'TREAT ALL CLOSE CONTACTS SIMULTANEOUSLY - Even if asymptomatic (incubation period)',
        'Apply topical treatment to ENTIRE body from neck down (include scalp, face, ears in infants/elderly/crusted)',
        'Pay special attention to: web spaces, under nails, wrists, genitalia, under breasts',
        'Cut fingernails short and apply treatment under nails',
        'ENVIRONMENTAL MEASURES on day of treatment:',
        '  - Wash all bedding, towels, clothing in hot water (>50°C) and dry on high heat',
        '  - Items that cannot be washed: seal in plastic bag for 72 hours (mites die)',
        '  - Vacuum mattresses, furniture, carpets',
        'POST-TREATMENT ITCH: May persist 2-4 weeks due to dead mite antigens - does NOT indicate treatment failure',
        'Avoid close physical contact until treatment completed',
        'Re-examine in 2-4 weeks to confirm cure',
        'In institutions/outbreaks: Mass treatment may be needed'
      ],

      surgical: [
        'No surgical treatment for scabies',
        'Nodular scabies lesions may persist for months but resolve spontaneously'
      ],

      referralCriteria: [
        'Crusted (Norwegian) scabies - requires specialist management and infection control',
        'Treatment failure after two correctly applied courses',
        'Diagnostic uncertainty',
        'Severe secondary bacterial infection (cellulitis, abscess)',
        'Immunocompromised patient with atypical presentation',
        'Institutional outbreak requiring coordination'
      ],

      monitoring: [
        'Review in 2-4 weeks to assess treatment response',
        'Check all treated contacts for symptom resolution',
        'Monitor for secondary infection',
        'Reassess if itch persists beyond 4 weeks post-treatment',
        'Crusted scabies: Close monitoring, repeated treatments often needed'
      ],

      followUp: `CLASSICAL SCABIES:
• Review at 2-4 weeks to confirm cure
• Itch may persist 2-4 weeks post-treatment - reassure patient
• If itch persists >4 weeks or new burrows appear: Retreat and re-examine contacts

CRUSTED SCABIES:
• Requires repeated treatments over weeks
• Follow-up every 1-2 weeks during treatment
• Skin scrapings to confirm clearance

TREATMENT FAILURE CHECKLIST:
1. Was treatment applied correctly (entire body, sufficient duration)?
2. Were ALL contacts treated simultaneously?
3. Was environmental decontamination done?
4. Re-infestation from untreated contact?
5. Consider ivermectin if topical treatment failed`
    },

    redFlags: [
      '🔴 CRITICAL: Crusted scabies with sepsis - Overwhelming bacterial infection risk',
      '🟠 URGENT: Crusted (Norwegian) scabies - Highly contagious, needs isolation and aggressive treatment',
      '🟠 URGENT: Spreading cellulitis with fever - Needs systemic antibiotics, possible admission',
      '🟠 URGENT: Scabies in immunocompromised patient - Higher risk of crusted scabies and complications',
      '🟡 WARNING: Treatment failure after two courses - Reassess diagnosis, compliance, contacts',
      '🟡 WARNING: Persistent nodules - Scabetic nodules can persist months, reassure but monitor',
      '🟡 WARNING: Institutional outbreak - Needs coordinated mass treatment',
      '🟡 WARNING: Post-streptococcal complications - Monitor for glomerulonephritis if streptococcal infection'
    ],

    complications: [
      'Secondary bacterial infection - Impetigo (very common in Africa)',
      'Cellulitis and soft tissue infection',
      'Abscess formation',
      'Post-streptococcal glomerulonephritis (PSGN) - From Group A Strep',
      'Septicemia - Especially in crusted scabies',
      'Scabetic nodules - Persistent itchy nodules (genitalia, axillae)',
      'Post-scabies eczema/dermatitis',
      'Psychological impact - Stigma, embarrassment',
      'Sleep disturbance from nocturnal itch'
    ],

    prognosis: `CLASSICAL SCABIES:
• Excellent prognosis with proper treatment
• Cure rate >95% with permethrin or ivermectin
• Itch improves within days but may persist 2-4 weeks
• Re-infestation common if contacts not treated

CRUSTED SCABIES:
• More difficult to treat, requires repeated courses
• High risk of secondary infection and sepsis
• May need prolonged treatment over weeks
• Prognosis depends on underlying condition

FACTORS AFFECTING OUTCOME:
Good prognosis:
• Proper treatment application
• All contacts treated simultaneously
• Environmental decontamination done
• No immunocompromise

Poor prognosis:
• Untreated contacts (re-infestation cycle)
• Crusted scabies
• Immunocompromised
• Secondary bacterial infection complications`,

    prevention: `PRIMARY PREVENTION:
• Avoid prolonged skin-to-skin contact with infected individuals
• Do not share bedding, clothing, towels with infected persons
• Early identification and treatment of cases
• Education in endemic communities

SECONDARY PREVENTION:
• Treat ALL close contacts simultaneously (household, sexual partners)
• Environmental decontamination (bedding, clothing)
• Screen close contacts in institutions

MASS DRUG ADMINISTRATION (MDA):
• WHO strategy for endemic communities
• Ivermectin-based MDA effective in reducing prevalence
• Part of NTD control programs

INSTITUTIONAL SETTINGS:
• Screen all new admissions in high-risk settings
• Isolate crusted scabies cases (highly contagious)
• Treat all residents and staff if outbreak
• Regular surveillance

INFECTION CONTROL - CRUSTED SCABIES:
• Contact isolation until treated
• Healthcare workers: Gowns and gloves
• Environmental cleaning of room
• Treat all contacts including healthcare workers`,

    patientEducation: `UNDERSTANDING SCABIES:
Scabies is caused by tiny mites that burrow into your skin and cause intense itching. It spreads through close skin-to-skin contact and is very common - it is NOT a sign of being dirty.

HOW TO USE YOUR TREATMENT (PERMETHRIN CREAM):
1. Bathe or shower and dry completely
2. Apply cream to ENTIRE body from neck down (include between fingers, under nails, soles of feet, genitals)
3. For children under 2 and elderly: Also apply to scalp, face, and ears
4. Leave on for 8-14 hours (apply at bedtime, wash off in morning)
5. Put on clean clothes after washing off
6. REPEAT the treatment in 1 week

VERY IMPORTANT - TREAT EVERYONE:
• ALL household members and close contacts must be treated at THE SAME TIME
• Even people without symptoms need treatment (they may be incubating)
• If you don't treat everyone, you will catch it again!

CLEANING YOUR HOME (On treatment day):
• Wash all bedsheets, pillowcases, towels, and worn clothes in HOT water
• Dry on high heat if possible
• Items you can't wash: Put in a sealed plastic bag for 3 days
• Vacuum mattresses and furniture

AFTER TREATMENT:
• Itching may continue for 2-4 weeks even after successful treatment
• This is from your body reacting to dead mites - it does NOT mean treatment failed
• Take antihistamine tablets to help with itch
• Use moisturizing cream for dry skin
• DO NOT use steroid cream unless your doctor prescribes it

WHEN TO RETURN TO CLINIC:
• Itch getting worse after 2 weeks (not just continuing)
• New bumps appearing after treatment
• Signs of infection: spreading redness, pus, fever
• Not better after 4 weeks

CAN I GO TO WORK/SCHOOL?
• You can return after completing treatment (after washing off the cream)
• Avoid close physical contact until treated
• Inform school/workplace so contacts can be treated`,

    references: [
      'WHO Guidelines on Scabies - Neglected Tropical Diseases',
      'CDC Guidelines: Scabies',
      'British Association of Dermatologists Guidelines on Scabies 2016',
      'Engelman D, et al. The 2020 IACS Consensus Criteria for Scabies',
      'Romani L, et al. Prevalence of scabies and impetigo worldwide: a systematic review. Lancet Infect Dis. 2015',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 90. CELLULITIS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '90',
    name: 'Cellulitis',
    slug: 'cellulitis',
    specialty: 'Dermatology',
    bodySystem: 'Dermatological',
    icdCode: 'L03',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Cellulitis is an acute, spreading bacterial infection of the skin and subcutaneous tissue, characterized by erythema, warmth, swelling, and tenderness. It is one of the most common skin infections, accounting for significant morbidity and healthcare utilization.

The most common causative organisms are:
• Streptococcus pyogenes (Group A Strep) - Most common
• Staphylococcus aureus (including MRSA in some regions)
• Less commonly: other streptococci, gram-negative organisms

🔴 KEY CLINICAL POINTS:
• UNILATERAL presentation - Bilateral "cellulitis" is usually NOT cellulitis (think stasis dermatitis, contact dermatitis)
• Mark the border with pen to monitor progression/response
• Lower limbs most commonly affected (especially with predisposing factors)
• Look for ENTRY POINT: tinea pedis, wound, ulcer, insect bite
• Treat underlying predisposing conditions (tinea pedis, edema, lymphedema)
• Distinguish from NECROTIZING FASCIITIS - surgical emergency!

AFRICAN CONTEXT:
• Common presentation in tropical climates
• Often secondary to insect bites, wounds, skin infections
• Diabetic foot infections common in uncontrolled diabetes
• Limited access to IV antibiotics may delay treatment
• MRSA prevalence varies by region
• Traditional remedies may delay presentation`,

    epidemiology: `GLOBAL BURDEN:
• Very common - one of most frequent skin infections
• Incidence: 200 per 100,000 person-years
• Accounts for 3-10% of hospital admissions for infection
• Recurrence rate: 8-20% annually
• Lower limb cellulitis: 70-80% of cases

AFRICAN EPIDEMIOLOGY:
• High burden in tropical/subtropical regions
• Often associated with:
  - Insect bites and stings
  - Traumatic wounds
  - Diabetic foot infections
  - Lymphatic filariasis (leading to lymphedema)
  - Scabies and skin infections
• Delayed presentation due to traditional remedies
• Higher rates of complications due to late treatment

RISK FACTORS:
Local factors:
• Skin breach: Wounds, ulcers, tinea pedis, dermatitis
• Edema: Venous insufficiency, lymphedema, heart failure
• Previous cellulitis (damages lymphatics, increases recurrence risk)
• Obesity
• Peripheral vascular disease

Systemic factors:
• Diabetes mellitus (increased severity, polymicrobial)
• Immunocompromise (HIV, chemotherapy, steroids)
• Chronic kidney disease
• Malnutrition
• Alcoholism

CAUSATIVE ORGANISMS:
• Streptococcus pyogenes (GAS) - Most common, classic cellulitis
• Staphylococcus aureus - Especially with abscess, wound
• MRSA - Variable prevalence, consider if not responding
• Gram-negatives - Diabetic foot, immunocompromised, water exposure
• Pasteurella - Animal bites
• Vibrio - Saltwater exposure
• Aeromonas - Freshwater exposure`,

    clinicalPresentation: `CLINICAL FEATURES:
• Erythema - spreading, poorly demarcated borders
• Warmth - affected area feels hot
• Swelling/Edema
• Tenderness/Pain
• Usually UNILATERAL
• May have associated lymphangitis (red streaking)
• Regional lymphadenopathy

SYSTEMIC SYMPTOMS:
• Fever, chills, rigors
• Malaise
• Tachycardia
• In severe cases: Hypotension, altered consciousness

DISTRIBUTION:
• Lower limbs - Most common (70-80%)
• Upper limbs - Often after hand injury
• Face - Periorbital, buccal (dental source)
• Trunk - Less common
• Perineum - Fournier's gangrene (surgical emergency!)

CLASSIFICATION BY SEVERITY:
CLASS I - Mild:
• No systemic toxicity
• No uncontrolled comorbidities
• Can be treated as outpatient with oral antibiotics

CLASS II - Moderate:
• Systemically unwell OR
• Has comorbidities that may complicate (diabetes, PVD, obesity)
• May need short IV therapy then oral

CLASS III - Severe:
• Significant systemic toxicity (fever >38°C, HR >100, hypotension)
• Rapid progression
• Concern for deeper infection
• Requires hospitalization and IV antibiotics

CLASS IV - Severe with sepsis/necrotizing fasciitis:
• Sepsis or septic shock
• Suspected necrotizing fasciitis
• Requires emergency management, surgical evaluation

SPECIAL PRESENTATIONS:
Erysipelas:
• More superficial (upper dermis)
• Sharply demarcated, raised border
• "Orange peel" (peau d'orange) appearance
• Face commonly affected
• Almost always streptococcal

Periorbital/Preseptal Cellulitis:
• Infection anterior to orbital septum
• Eyelid swelling, erythema
• Normal eye movements and vision
• Distinguish from ORBITAL cellulitis (emergency!)

Orbital Cellulitis:
• Infection posterior to orbital septum
• Proptosis, limited/painful eye movements
• Visual changes, diplopia
• EMERGENCY - risk of blindness, intracranial spread`,

    diagnosis: `CLINICAL DIAGNOSIS:
Cellulitis is primarily a clinical diagnosis based on:
• Characteristic appearance (erythema, warmth, swelling, tenderness)
• Unilateral involvement
• Acute onset
• Identification of entry point/predisposing factors

KEY HISTORY:
1. Duration and progression of symptoms
2. Systemic symptoms (fever, chills, malaise)
3. Entry point (wound, bite, tinea, ulcer)
4. Predisposing factors (diabetes, edema, previous cellulitis)
5. Previous episodes and treatments
6. Animal/water exposure
7. Immunocompromise

PHYSICAL EXAMINATION:
• Mark the border of erythema with pen - allows monitoring
• Assess for fluctuance (abscess)
• Check for crepitus (gas - suggests necrotizing infection!)
• Examine for entry point (especially interdigital tinea pedis)
• Assess peripheral pulses (PVD)
• Check for lymphangitis and lymphadenopathy
• Assess severity (vital signs, mental status)

INVESTIGATIONS:
• Often NOT needed for uncomplicated cellulitis
• Blood cultures: Positive in only 2-5%, but useful in severe/sepsis
• Blood tests: FBC, CRP, U&E - for severity assessment
• Imaging: If abscess suspected (ultrasound) or deeper infection

WHEN TO INVESTIGATE MORE:
• Severe or rapidly progressive infection
• Systemically unwell/sepsis
• Not responding to antibiotics
• Suspected abscess
• Concern for necrotizing fasciitis
• Immunocompromised patient
• Periorbital infection (CT if orbital involvement suspected)

NECROTIZING FASCIITIS - MUST NOT MISS:
Suspect if:
• Pain out of proportion to appearance
• Rapid progression despite antibiotics
• Systemic toxicity (shock)
• Skin necrosis or bullae
• Crepitus (gas in tissues)
• Wooden-hard induration
→ SURGICAL EMERGENCY - immediate surgical exploration`,

    differentialDiagnosis: [
      'Stasis dermatitis - BILATERAL, chronic, associated with venous insufficiency, varicose veins, hemosiderin staining',
      'Deep vein thrombosis (DVT) - Unilateral leg swelling, less erythema, calf tenderness, risk factors',
      'Contact dermatitis - History of exposure, may be bilateral, vesicles/blisters',
      'Necrotizing fasciitis - Pain out of proportion, rapid spread, systemic toxicity, crepitus - EMERGENCY',
      'Abscess - Fluctuant collection, may coexist with cellulitis',
      'Septic arthritis - Joint involvement, severe pain on movement, effusion',
      'Gout/Pseudogout - Joint-centered, acute onset, history of gout',
      'Erythema nodosum - Tender nodules, usually bilateral shins, systemic association',
      'Lipodermatosclerosis - Chronic, "inverted champagne bottle" leg, fibrosis',
      'Erysipeloid - Occupational (fish/meat handlers), well-demarcated purple lesion on hand'
    ],

    investigations: [
      {
        name: 'Clinical assessment and marking borders',
        indication: 'All patients - clinical diagnosis, monitor progression',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Moderate-severe cellulitis - leukocytosis, left shift',
        availability: 'BASIC'
      },
      {
        name: 'CRP / ESR',
        indication: 'Assess severity and monitor response',
        availability: 'BASIC'
      },
      {
        name: 'Blood glucose / HbA1c',
        indication: 'Screen for diabetes; monitor in known diabetics',
        availability: 'BASIC'
      },
      {
        name: 'Urea and electrolytes',
        indication: 'Severe cellulitis, assess renal function, sepsis workup',
        availability: 'BASIC'
      },
      {
        name: 'Blood cultures',
        indication: 'Severe cellulitis, sepsis, immunocompromised (positive in only 2-5%)',
        availability: 'BASIC'
      },
      {
        name: 'Wound swab',
        indication: 'Open wound, abscess drainage, not responding to treatment',
        availability: 'BASIC'
      },
      {
        name: 'Ultrasound',
        indication: 'Suspected abscess - identify collection for drainage',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'X-ray of affected area',
        indication: 'Suspected osteomyelitis, foreign body, gas in tissues',
        availability: 'BASIC'
      },
      {
        name: 'CT scan',
        indication: 'Suspected necrotizing fasciitis, orbital cellulitis, deep space infection',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Doppler ultrasound',
        indication: 'Exclude DVT if diagnostic uncertainty',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Assess severity: Vital signs, extent of infection, systemic symptoms',
        'Mark borders of erythema with pen to monitor progression',
        'Identify and treat entry point (tinea pedis, wound)',
        'Assess for abscess requiring drainage',
        'EXCLUDE NECROTIZING FASCIITIS - if suspected, urgent surgical referral',
        'Determine if outpatient (mild) or inpatient (moderate-severe) treatment',
        'Elevate affected limb',
        'Analgesia as needed'
      ],

      pharmacological: [
        {
          drug: 'Flucloxacillin',
          indication: 'First-line for non-purulent cellulitis (streptococcal/staphylococcal coverage)',
          dose: 'Mild: 500mg QID oral. Moderate-Severe: 1-2g QID IV',
          route: 'Oral or IV depending on severity',
          duration: '5-7 days (may extend to 14 days if slow response)',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['GI upset', 'Rash', 'Hepatitis (rare)'],
          alternatives: ['Clindamycin', 'Erythromycin', 'Cephalosporins']
        },
        {
          drug: 'Amoxicillin-Clavulanate (Co-amoxiclav)',
          indication: 'Alternative first-line; broader coverage including some gram-negatives',
          dose: 'Mild: 625mg TDS oral. Moderate-Severe: 1.2g TDS IV',
          route: 'Oral or IV',
          duration: '5-7 days',
          contraindications: ['Penicillin allergy', 'Previous co-amoxiclav jaundice'],
          sideEffects: ['Diarrhea', 'Nausea', 'Rash', 'Cholestatic jaundice'],
          alternatives: ['Clindamycin plus ciprofloxacin']
        },
        {
          drug: 'Clindamycin',
          indication: 'Penicillin allergy; suspected MRSA; necrotizing fasciitis (toxin inhibition)',
          dose: '300-450mg QID oral or 600-900mg TDS IV',
          route: 'Oral or IV',
          duration: '5-7 days',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Diarrhea', 'C. difficile colitis (monitor for diarrhea)', 'Rash'],
          alternatives: ['Vancomycin (for MRSA)', 'Doxycycline']
        },
        {
          drug: 'Erythromycin',
          indication: 'Penicillin allergy (non-severe); limited resource setting',
          dose: '500mg QID oral',
          route: 'Oral',
          duration: '5-7 days',
          contraindications: ['Known hypersensitivity', 'QT prolongation'],
          sideEffects: ['GI upset (common)', 'QT prolongation'],
          alternatives: ['Azithromycin', 'Clindamycin']
        },
        {
          drug: 'Ceftriaxone',
          indication: 'Severe cellulitis requiring IV therapy; once-daily dosing advantage',
          dose: '1-2g daily IV',
          route: 'IV',
          duration: 'Until improvement, then switch to oral',
          contraindications: ['Severe penicillin/cephalosporin allergy'],
          sideEffects: ['Diarrhea', 'Rash', 'Biliary sludging'],
          alternatives: ['Co-amoxiclav IV', 'Flucloxacillin IV']
        },
        {
          drug: 'Vancomycin',
          indication: 'Confirmed or suspected MRSA; severe penicillin allergy with severe infection',
          dose: '15-20mg/kg IV every 8-12 hours (adjust for renal function)',
          route: 'IV',
          duration: 'Until clinical improvement and culture results',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Red man syndrome (infuse slowly)', 'Nephrotoxicity', 'Ototoxicity'],
          alternatives: ['Linezolid', 'Daptomycin']
        },
        {
          drug: 'Doxycycline',
          indication: 'Penicillin allergy; MRSA coverage (community-acquired)',
          dose: '100mg BD oral',
          route: 'Oral',
          duration: '5-7 days',
          contraindications: ['Pregnancy', 'Children <8 years', 'Severe hepatic impairment'],
          sideEffects: ['Photosensitivity', 'GI upset', 'Esophageal ulceration'],
          alternatives: ['Trimethoprim-sulfamethoxazole']
        },
        {
          drug: 'Metronidazole',
          indication: 'Add for anaerobic coverage: Diabetic foot, perineal infection, bite wounds',
          dose: '400-500mg TDS oral or IV',
          route: 'Oral or IV',
          duration: '7-14 days (depending on infection)',
          contraindications: ['First trimester pregnancy (relative)'],
          sideEffects: ['Metallic taste', 'Nausea', 'Disulfiram-like reaction with alcohol'],
          alternatives: ['Clindamycin (also covers anaerobes)']
        },
        {
          drug: 'Benzylpenicillin (Penicillin G)',
          indication: 'Erysipelas, confirmed streptococcal cellulitis',
          dose: '1.2-2.4g QID IV (or 600mg-1.2g QID IV)',
          route: 'IV',
          duration: 'Until improvement then switch to oral penicillin V or amoxicillin',
          contraindications: ['Penicillin allergy'],
          sideEffects: ['Allergic reaction', 'Diarrhea'],
          alternatives: ['Ceftriaxone', 'Clindamycin']
        },
        {
          drug: 'Analgesia (Paracetamol, NSAIDs)',
          indication: 'Pain relief',
          dose: 'Paracetamol 1g QID; Ibuprofen 400mg TDS (if not contraindicated)',
          route: 'Oral',
          duration: 'As needed',
          contraindications: ['NSAIDs: Renal impairment, peptic ulcer, aspirin-sensitive asthma'],
          sideEffects: ['Paracetamol: Hepatotoxicity (overdose). NSAIDs: GI, renal'],
          alternatives: ['Opioids for severe pain']
        }
      ],

      nonPharmacological: [
        'ELEVATE affected limb - reduces edema, improves drainage',
        'Rest - avoid weight-bearing on affected limb if lower leg',
        'Mark erythema borders to monitor progression or response',
        'Treat underlying conditions: Tinea pedis (antifungals), edema (compression when infection controlled)',
        'Wound care: Clean and dress any wounds appropriately',
        'Incision and drainage if abscess present',
        'Compression therapy AFTER acute infection resolved (for edema)',
        'Skin care: Moisturize dry/cracked skin to prevent recurrence',
        'Weight management if obesity contributing',
        'Diabetic foot care education'
      ],

      surgical: [
        'Incision and drainage of abscess if present',
        'URGENT surgical exploration if necrotizing fasciitis suspected',
        'Debridement of necrotic tissue',
        'Fasciotomy if compartment syndrome'
      ],

      referralCriteria: [
        'Suspected necrotizing fasciitis - EMERGENCY surgical referral',
        'Orbital cellulitis - Urgent ophthalmology/ENT',
        'Not responding to appropriate oral antibiotics after 48-72 hours',
        'Severe/rapidly progressive infection',
        'Significant comorbidities (uncontrolled diabetes, immunocompromise)',
        'Facial cellulitis with risk of intracranial spread',
        'Recurrent cellulitis (>2 episodes) - investigate underlying cause',
        'Need for IV antibiotics if outpatient IV not available'
      ],

      monitoring: [
        'Re-mark borders daily to assess progression/response',
        'Monitor temperature and systemic symptoms',
        'Review in 48-72 hours if outpatient - should see improvement',
        'Daily review if inpatient',
        'Monitor for abscess development',
        'Check renal function if on nephrotoxic antibiotics (vancomycin, aminoglycosides)',
        'Watch for signs of necrotizing fasciitis'
      ],

      followUp: `OUTPATIENT MANAGEMENT:
• Review in 48-72 hours - should see improvement
• If improving: Complete antibiotic course
• If not improving: Consider IV therapy, alternative antibiotics, exclude abscess/necrotizing fasciitis
• After resolution: Address predisposing factors (tinea, edema)

INPATIENT MANAGEMENT:
• Daily review until improving
• Switch to oral antibiotics when: Afebrile 24-48 hours, improving clinically, tolerating oral intake
• Total course: Usually 5-7 days (may extend to 14 days if severe)

RECURRENT CELLULITIS (≥2 episodes per year):
• Investigate and treat predisposing factors aggressively
• Consider prophylactic antibiotics: Penicillin V 250mg BD or Erythromycin 250mg BD
• Optimize edema management
• Lifelong skin care`
    },

    redFlags: [
      '🔴 CRITICAL: Pain out of proportion to clinical findings - NECROTIZING FASCIITIS until proven otherwise',
      '🔴 CRITICAL: Crepitus (gas in tissues) - Necrotizing fasciitis/gas gangrene - EMERGENCY surgery',
      '🔴 CRITICAL: Rapid progression despite antibiotics - Suspect necrotizing infection',
      '🔴 CRITICAL: Septic shock (hypotension, altered consciousness) - Resuscitate, IV antibiotics, ICU',
      '🔴 CRITICAL: Orbital cellulitis (proptosis, limited eye movement, visual changes) - Risk of blindness',
      '🟠 URGENT: Extensive involvement or bullae formation - May need IV antibiotics, surgical assessment',
      '🟠 URGENT: Facial cellulitis near eye or nose - Risk of orbital/intracranial spread',
      '🟠 URGENT: Diabetic foot infection - High risk of osteomyelitis, may need surgical debridement',
      '🟡 WARNING: Not improving after 48-72 hours oral antibiotics - Escalate treatment',
      '🟡 WARNING: Bilateral lower limb redness - Unlikely cellulitis, consider stasis dermatitis, contact dermatitis'
    ],

    complications: [
      'Abscess formation - requires drainage',
      'Necrotizing fasciitis - life-threatening',
      'Sepsis and septic shock',
      'Bacteremia',
      'Osteomyelitis (underlying bone infection)',
      'Septic arthritis',
      'Thrombophlebitis / DVT',
      'Post-streptococcal glomerulonephritis',
      'Lymphatic damage leading to recurrent cellulitis',
      'Chronic edema / lymphedema',
      'Skin necrosis'
    ],

    prognosis: `UNCOMPLICATED CELLULITIS:
• Excellent prognosis with appropriate treatment
• Most respond within 24-48 hours of antibiotics
• Complete resolution typically within 5-10 days
• Erythema may persist after other symptoms resolve

RECURRENCE:
• 8-20% experience recurrence within 1-3 years
• Risk factors for recurrence: Previous cellulitis, lymphedema, obesity, tinea pedis
• Each episode damages lymphatics, increasing future risk

COMPLICATED CELLULITIS:
• Necrotizing fasciitis: 20-40% mortality even with treatment
• Diabetic foot infections: Higher risk of amputation
• Sepsis: Mortality depends on severity and time to treatment

FACTORS AFFECTING OUTCOME:
Good prognosis:
• Early presentation and treatment
• Mild disease, no comorbidities
• Response to oral antibiotics
• Treating predisposing factors

Poor prognosis:
• Delayed treatment
• Necrotizing fasciitis
• Immunocompromise
• Severe comorbidities (uncontrolled diabetes)
• Recurrent episodes`,

    prevention: `PRIMARY PREVENTION:
• Treat skin conditions that provide entry points (tinea pedis, eczema)
• Proper wound care - clean, dress, monitor wounds
• Skin moisturization to prevent cracks
• Protective footwear
• Insect bite prevention
• Good glycemic control in diabetics

SECONDARY PREVENTION (After first episode):
• Aggressive treatment of predisposing factors
• Treat tinea pedis with antifungals
• Manage edema (compression when appropriate)
• Weight management
• Skin care regimen

PROPHYLACTIC ANTIBIOTICS (Recurrent cellulitis ≥2 episodes/year):
• Penicillin V 250mg BD long-term OR
• Erythromycin 250mg BD (if penicillin allergic)
• Reduces recurrence by 50-70%
• Continue indefinitely or until predisposing factors controlled

PATIENT EDUCATION:
• Recognize early signs of cellulitis
• Seek early medical attention
• Complete full antibiotic course
• Care for skin between episodes
• Elevate legs if prone to swelling`,

    patientEducation: `UNDERSTANDING CELLULITIS:
Cellulitis is a bacterial infection of the skin that causes redness, warmth, swelling, and pain. It usually enters through a break in the skin like a cut, insect bite, or cracked skin (especially between toes).

TAKING YOUR ANTIBIOTICS:
• Take ALL the antibiotics as prescribed, even if you feel better
• Don't skip doses - take them at regular intervals
• Complete the full course (usually 5-7 days)
• If you miss a dose, take it as soon as you remember

WHILE YOU'RE RECOVERING:
• ELEVATE the affected limb - this helps reduce swelling
• Rest and avoid putting weight on affected leg
• Take paracetamol for pain and fever
• Keep the area clean and dry
• Don't scratch even if it itches

WHAT TO EXPECT:
• You should start to feel better within 2-3 days
• The redness may spread slightly in the first 24 hours, but fever should improve
• The redness may be the last thing to fully clear
• Mark the edge of the redness with pen - it should stop spreading with treatment

RETURN IMMEDIATELY IF:
🚨 The redness is spreading rapidly
🚨 Pain is getting much worse or "out of proportion"
🚨 You feel very unwell, confused, or have high fever
🚨 The skin turns dark/black or develops blisters
🚨 You notice crackling under the skin

PREVENTING FUTURE EPISODES:
• Check your feet daily, especially between toes
• Treat athlete's foot (tinea) promptly with antifungal cream
• Moisturize dry, cracked skin daily
• Keep wounds clean and covered
• Don't walk barefoot
• If your legs tend to swell, wear compression stockings (when not infected)
• If diabetic: Good foot care and blood sugar control

IF YOU GET REPEATED CELLULITIS:
Tell your doctor - you may need long-term low-dose antibiotics to prevent further episodes.`,

    references: [
      'CREST Guidelines: Management of Cellulitis in Adults',
      'Stevens DL, et al. IDSA Guidelines for Skin and Soft Tissue Infections 2014',
      'NICE Clinical Knowledge Summaries: Cellulitis - Acute',
      'British Lymphology Society: Consensus on Cellulitis Management',
      'Phoenix G, et al. Diagnosis and management of cellulitis. BMJ 2012',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 91. MIGRAINE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '91',
    name: 'Migraine',
    slug: 'migraine',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'G43',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Migraine is a common primary headache disorder characterized by recurrent episodes of moderate-to-severe, usually unilateral, pulsating headache lasting 4-72 hours, often accompanied by nausea, vomiting, photophobia, and phonophobia. It is a leading cause of disability worldwide, particularly affecting people during their most productive years.

Migraine is classified as:
• Migraine WITHOUT aura (75-80% of migraineurs)
• Migraine WITH aura (20-25%) - Visual, sensory, or speech disturbances preceding headache
• Chronic migraine (≥15 headache days/month for >3 months, with ≥8 migraine days)

🔴 KEY CLINICAL POINTS:
• Diagnosis is CLINICAL - based on ICHD-3 criteria
• EXCLUDE secondary causes with red flags (see below)
• Acute treatment: Simple analgesics first, triptans for moderate-severe
• Treat early in attack for best efficacy
• Prophylaxis if ≥4 attacks/month or significant disability
• Avoid medication overuse headache (analgesics >15 days/month, triptans >10 days)

AFRICAN CONTEXT:
• Often underdiagnosed and undertreated
• May be attributed to "stress" or "spiritual causes"
• Limited access to triptans in many settings
• Simple analgesics and metoclopramide widely available
• Need to exclude malaria and meningitis in acute presentation with fever`,

    epidemiology: `GLOBAL BURDEN:
• Affects ~15% of global population
• 3rd most prevalent disorder worldwide
• Leading cause of disability in under-50s
• Peak prevalence: 25-55 years (productive years)
• Female:Male ratio 3:1 (after puberty)
• WHO ranks as 2nd highest cause of years lived with disability

AFRICAN EPIDEMIOLOGY:
• Prevalence: 5-20% (varies by study and region)
• Often underreported and undertreated
• Similar female predominance
• May present later due to healthcare access issues
• Frequently misdiagnosed as "malaria headache" or tension headache

TYPES:
• Episodic migraine: <15 headache days/month
• Chronic migraine: ≥15 headache days/month for >3 months
• 2-3% of episodic migraine transforms to chronic yearly

TRIGGERS (Common in Africa):
• Stress and anxiety (most common)
• Sleep disturbance (deprivation or excess)
• Hormonal changes (menstruation, contraceptives)
• Fasting/skipping meals
• Dehydration
• Bright sunlight
• Strong odors
• Alcohol (especially red wine)
• Certain foods (variable - cheese, chocolate, MSG)
• Weather changes
• Physical exertion

COMORBIDITIES:
• Depression and anxiety (2-4x increased risk)
• Stroke (migraine with aura increases risk)
• Epilepsy
• Cardiovascular disease
• Sleep disorders`,

    clinicalPresentation: `MIGRAINE WITHOUT AURA (ICHD-3 Criteria):
A. At least 5 attacks fulfilling criteria B-D
B. Headache lasting 4-72 hours (untreated)
C. At least 2 of:
   • Unilateral location
   • Pulsating/throbbing quality
   • Moderate or severe intensity
   • Aggravation by routine physical activity
D. During headache, at least 1 of:
   • Nausea and/or vomiting
   • Photophobia AND phonophobia

MIGRAINE WITH AURA:
• Aura: Fully reversible visual, sensory, or speech symptoms
• Develops gradually over ≥5 minutes
• Lasts 5-60 minutes
• Usually followed by headache within 60 minutes
• Visual aura most common: Scotoma, fortification spectra, flashing lights

TYPICAL MIGRAINE PHASES:
1. PRODROME (hours to days before):
   • Mood changes, irritability, depression
   • Food cravings
   • Fatigue, yawning
   • Neck stiffness
   • Fluid retention

2. AURA (if present, 5-60 minutes):
   • Visual: Scotoma, zigzag lines, flashing lights
   • Sensory: Tingling, numbness (face, arm)
   • Speech: Dysphasia

3. HEADACHE (4-72 hours):
   • Usually unilateral (may switch sides)
   • Pulsating/throbbing
   • Moderate to severe intensity
   • Worse with movement
   • Associated: Nausea, vomiting, photophobia, phonophobia

4. POSTDROME (hours to days after):
   • Fatigue, "washed out"
   • Cognitive difficulties
   • Mood changes

CHRONIC MIGRAINE:
• ≥15 headache days/month for >3 months
• At least 8 days have migraine features
• Often associated with medication overuse

MENSTRUAL MIGRAINE:
• Attacks occur ±2 days of menstruation
• Often more severe, longer, less responsive to treatment`,

    diagnosis: `CLINICAL DIAGNOSIS:
Migraine is a CLINICAL diagnosis based on:
• Characteristic headache features (ICHD-3 criteria)
• Normal neurological examination
• Exclusion of secondary causes

DIAGNOSTIC APPROACH:
1. Detailed headache history (use headache diary)
2. Identify red flags for secondary headache
3. Neurological examination (should be normal)
4. Investigations ONLY if red flags present

KEY HISTORY QUESTIONS:
• Age at onset and duration of attacks
• Frequency, duration, severity
• Location and character of pain
• Associated symptoms (nausea, photophobia, phonophobia)
• Presence of aura
• Triggers
• Impact on daily life
• Previous treatments and response
• Family history (70% have affected first-degree relative)
• Medication use (assess for overuse)

HEADACHE DIARY:
Essential for diagnosis and management - document:
• Date and time of attacks
• Duration
• Severity (0-10 scale)
• Associated symptoms
• Triggers
• Medications taken and response
• Menstrual cycle (women)

RED FLAGS (Require investigation):
🔴 SNOOP Mnemonic:
S - Systemic symptoms (fever, weight loss, cancer, HIV)
N - Neurological symptoms/signs (focal deficits, papilledema)
O - Onset sudden ("thunderclap" - worst headache of life)
O - Older age (new headache >50 years)
P - Pattern change (different from usual headache)

Additional red flags:
• Progressive headache
• Headache worse with cough, strain, position change
• Seizures
• Personality change
• Morning headache with vomiting

WHEN TO IMAGE:
• New neurological signs
• Thunderclap headache
• New headache pattern
• Headache with fever and neck stiffness
• Papilledema
• Immunocompromised patient
• Not meeting typical migraine criteria`,

    differentialDiagnosis: [
      'Tension-type headache - Bilateral, pressing/tightening, mild-moderate, no nausea/vomiting, no significant photophobia/phonophobia',
      'Cluster headache - Unilateral severe periorbital pain, autonomic features (tearing, rhinorrhea), shorter attacks (15-180 min), male predominance',
      'Medication overuse headache - Daily or near-daily headache, history of analgesic/triptan overuse (>10-15 days/month)',
      'Secondary headache: Intracranial lesion - Progressive, neurological signs, papilledema, worse in morning',
      'Secondary headache: Subarachnoid hemorrhage - Thunderclap onset, meningism, may have focal signs',
      'Secondary headache: Meningitis - Fever, neck stiffness, photophobia, altered consciousness',
      'Secondary headache: Giant cell arteritis - Age >50, scalp tenderness, jaw claudication, visual symptoms, elevated ESR',
      'Secondary headache: Idiopathic intracranial hypertension - Overweight young woman, papilledema, worse with cough/strain',
      'Sinusitis - Facial pain over sinuses, nasal symptoms, may have fever',
      'Cervicogenic headache - Associated with neck pain/stiffness, reduced neck ROM'
    ],

    investigations: [
      {
        name: 'Clinical history and examination',
        indication: 'All patients - diagnosis is clinical',
        availability: 'BASIC'
      },
      {
        name: 'Headache diary',
        indication: 'All patients - essential for diagnosis and monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Blood pressure measurement',
        indication: 'All patients - exclude hypertension',
        availability: 'BASIC'
      },
      {
        name: 'Fundoscopy',
        indication: 'Check for papilledema if suspecting raised ICP',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'If systemic symptoms, infection suspected',
        availability: 'BASIC'
      },
      {
        name: 'ESR / CRP',
        indication: 'Age >50 - exclude giant cell arteritis',
        availability: 'BASIC'
      },
      {
        name: 'CT head',
        indication: 'Red flags present; exclude intracranial pathology',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI brain',
        indication: 'Atypical features, focal signs, to exclude structural cause',
        availability: 'ADVANCED'
      },
      {
        name: 'Lumbar puncture',
        indication: 'Thunderclap headache (after CT), suspected meningitis, IIH',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Confirm diagnosis meets ICHD-3 criteria',
        'Exclude secondary causes (check for red flags)',
        'Assess attack frequency and disability',
        'Identify triggers through headache diary',
        'Assess current medication use (check for overuse)',
        'Educate patient about condition and treatment goals',
        'Develop acute treatment plan',
        'Assess need for prophylaxis'
      ],

      pharmacological: [
        {
          drug: 'Simple analgesics (Paracetamol, Aspirin, Ibuprofen)',
          indication: 'First-line for mild-moderate attacks; take early in attack',
          dose: 'Paracetamol 1g; Aspirin 900-1000mg; Ibuprofen 400-600mg. Can combine paracetamol + NSAID',
          route: 'Oral',
          duration: 'Single dose at onset; may repeat once. Limit to <15 days/month',
          contraindications: ['NSAIDs: Peptic ulcer, aspirin-sensitive asthma, renal impairment, pregnancy (3rd trimester)'],
          sideEffects: ['GI upset', 'GI bleeding (NSAIDs)', 'Renal impairment (NSAIDs)'],
          alternatives: ['Add antiemetic if nausea', 'Triptans if inadequate']
        },
        {
          drug: 'Metoclopramide or Domperidone (antiemetics)',
          indication: 'Add to analgesics for nausea/vomiting; improves absorption',
          dose: 'Metoclopramide 10mg; Domperidone 10-20mg. Take 15-20 min before analgesic',
          route: 'Oral (or IM/suppository if vomiting)',
          duration: 'Single dose with acute treatment',
          contraindications: ['Metoclopramide: Parkinson disease, GI obstruction'],
          sideEffects: ['Drowsiness', 'Extrapyramidal effects (metoclopramide)'],
          alternatives: ['Prochlorperazine', 'Ondansetron']
        },
        {
          drug: 'Triptans (Sumatriptan, Rizatriptan, Zolmitriptan)',
          indication: 'Moderate-severe migraine; when simple analgesics fail',
          dose: 'Sumatriptan 50-100mg PO (or 6mg SC, 20mg nasal). Rizatriptan 10mg. May repeat once after 2 hours if partial response',
          route: 'Oral, nasal, subcutaneous (depends on agent)',
          duration: 'Acute attack only. Limit to <10 days/month',
          contraindications: ['Ischemic heart disease', 'Uncontrolled hypertension', 'Previous stroke/TIA', 'Peripheral vascular disease', 'Pregnancy', 'Hemiplegic/basilar migraine'],
          sideEffects: ['Tingling', 'Flushing', 'Chest tightness (usually benign)', 'Drowsiness'],
          alternatives: ['Different triptan (try another if one fails)', 'Ergotamine']
        },
        {
          drug: 'Ergotamine',
          indication: 'Alternative to triptans (limited availability); long attacks',
          dose: 'Ergotamine 1-2mg at onset, may repeat 1mg after 30 min (max 4mg/attack, 6mg/week)',
          route: 'Oral or suppository',
          duration: 'Limit frequency to prevent dependence. Max 2 days/week',
          contraindications: ['Pregnancy', 'Cardiovascular disease', 'Peripheral vascular disease', 'Uncontrolled hypertension', 'Liver/kidney disease'],
          sideEffects: ['Nausea', 'Vomiting', 'Vasoconstriction', 'Ergotism with overuse'],
          alternatives: ['Triptans (preferred)']
        },
        {
          drug: 'PROPHYLAXIS: Propranolol',
          indication: 'First-line prophylaxis; ≥4 attacks/month or significant disability',
          dose: 'Start 40mg BD, increase gradually to 80-160mg/day in divided doses',
          route: 'Oral',
          duration: 'Minimum 3 months trial; continue 6-12 months if effective, then attempt taper',
          contraindications: ['Asthma', 'Heart block', 'Decompensated heart failure', 'Bradycardia'],
          sideEffects: ['Fatigue', 'Cold extremities', 'Sleep disturbance', 'Depression', 'Bradycardia'],
          alternatives: ['Amitriptyline', 'Topiramate', 'Sodium valproate']
        },
        {
          drug: 'PROPHYLAXIS: Amitriptyline',
          indication: 'Prophylaxis; especially if comorbid tension headache, depression, insomnia',
          dose: 'Start 10-25mg at night, increase gradually to 50-100mg',
          route: 'Oral',
          duration: 'Minimum 3 months trial',
          contraindications: ['Recent MI', 'Arrhythmias', 'Severe liver disease', 'Urinary retention'],
          sideEffects: ['Drowsiness', 'Dry mouth', 'Constipation', 'Weight gain', 'Urinary retention'],
          alternatives: ['Nortriptyline (less sedating)', 'Propranolol']
        },
        {
          drug: 'PROPHYLAXIS: Topiramate',
          indication: 'Prophylaxis; especially if overweight (causes weight loss)',
          dose: 'Start 25mg at night, increase by 25mg weekly to 50-100mg BD',
          route: 'Oral',
          duration: 'Minimum 3 months trial',
          contraindications: ['Pregnancy (teratogenic)', 'Kidney stones', 'Glaucoma'],
          sideEffects: ['Cognitive slowing ("dopamax")', 'Paresthesias', 'Weight loss', 'Kidney stones'],
          alternatives: ['Propranolol', 'Amitriptyline']
        },
        {
          drug: 'PROPHYLAXIS: Sodium Valproate',
          indication: 'Prophylaxis; especially if comorbid epilepsy',
          dose: 'Start 200mg BD, increase to 400-600mg BD',
          route: 'Oral',
          duration: 'Minimum 3 months trial',
          contraindications: ['Pregnancy (HIGHLY teratogenic - avoid in women of childbearing potential)', 'Liver disease', 'Porphyria'],
          sideEffects: ['Weight gain', 'Tremor', 'Hair loss', 'Hepatotoxicity', 'Teratogenicity'],
          alternatives: ['Propranolol', 'Topiramate']
        }
      ],

      nonPharmacological: [
        'TRIGGER IDENTIFICATION AND AVOIDANCE - Use headache diary',
        'Regular sleep schedule - Consistent bedtime and wake time',
        'Regular meals - Do not skip meals, stay hydrated',
        'Stress management - Relaxation techniques, CBT if needed',
        'Regular aerobic exercise - 30 minutes, 3-5 times/week',
        'Limit caffeine intake (but avoid sudden withdrawal)',
        'Avoid known dietary triggers (individualized)',
        'Dark, quiet room during acute attack',
        'Cold compress to forehead or neck',
        'Biofeedback and relaxation training (where available)',
        'Acupuncture (some evidence of benefit)',
        'Address medication overuse if present'
      ],

      surgical: [
        'No routine surgical treatment for migraine',
        'Nerve blocks/Botox for chronic migraine (specialist)'
      ],

      referralCriteria: [
        'Diagnostic uncertainty - atypical features, red flags',
        'Suspected secondary headache',
        'Not responding to appropriate treatment after 3 months',
        'Chronic migraine (≥15 days/month)',
        'Medication overuse headache',
        'Multiple prophylaxis failures',
        'Severe disability despite treatment',
        'Hemiplegic or basilar migraine',
        'Consideration for botulinum toxin or CGRP inhibitors'
      ],

      monitoring: [
        'Headache diary - Frequency, severity, medication use',
        'Review response to treatment in 4-8 weeks',
        'Monitor for medication overuse (analgesics >15 days, triptans >10 days/month)',
        'Assess side effects of prophylaxis',
        'Prophylaxis efficacy: Aim for 50% reduction in attack frequency',
        'After 6-12 months effective prophylaxis, consider gradual withdrawal',
        'Monitor blood pressure with beta-blockers',
        'LFTs if on valproate'
      ],

      followUp: `ACUTE MIGRAINE:
• Review in 4-8 weeks to assess treatment efficacy
• Adjust acute treatment if needed

PROPHYLAXIS:
• Review in 4-6 weeks after starting to assess tolerance
• Allow 2-3 months for full effect before judging efficacy
• If effective: Continue 6-12 months, then consider gradual taper
• If ineffective after adequate trial: Try alternative prophylactic

CHRONIC MIGRAINE:
• Closer follow-up every 4-6 weeks
• Address medication overuse
• Consider specialist referral

GOALS OF TREATMENT:
• Reduce attack frequency by ≥50%
• Reduce attack severity and disability
• Improve quality of life
• Avoid medication overuse`
    },

    redFlags: [
      '🔴 CRITICAL: Thunderclap headache (severe within seconds) - SAH until proven otherwise, CT + LP',
      '🔴 CRITICAL: Headache with fever, neck stiffness, altered consciousness - Meningitis/encephalitis',
      '🔴 CRITICAL: Headache with focal neurological signs - Space-occupying lesion, stroke',
      '🔴 CRITICAL: Headache with papilledema - Raised intracranial pressure',
      '🟠 URGENT: New headache in patient >50 years - Giant cell arteritis, malignancy',
      '🟠 URGENT: Progressive headache worsening over weeks - Exclude space-occupying lesion',
      '🟠 URGENT: Headache worse with cough/strain/position - Raised ICP, Chiari malformation',
      '🟡 WARNING: Change in established headache pattern - Reassess diagnosis',
      '🟡 WARNING: Headache with personality change - Frontal lesion',
      '🟡 WARNING: Medication overuse (analgesics >15 days, triptans >10 days/month) - Risk of MOH'
    ],

    complications: [
      'Medication overuse headache - From chronic use of acute treatments',
      'Chronic migraine - Transformation from episodic',
      'Status migrainosus - Debilitating attack lasting >72 hours',
      'Migrainous infarction - Stroke during migraine with aura (rare)',
      'Persistent aura without infarction',
      'Depression and anxiety (comorbid)',
      'Reduced quality of life and productivity',
      'Relationship and social difficulties',
      'Medication side effects (chronic prophylaxis)'
    ],

    prognosis: `EPISODIC MIGRAINE:
• Variable course - May improve, worsen, or remain stable
• 50-60% of patients respond well to treatment
• Many patients improve after age 50-60
• Women may improve after menopause

CHRONIC MIGRAINE:
• More difficult to treat
• 26-70% can revert to episodic with proper treatment
• Medication overuse headache complicates many cases

FACTORS AFFECTING PROGNOSIS:
Better prognosis:
• Early effective treatment
• Trigger avoidance
• No medication overuse
• No psychiatric comorbidity
• Good response to prophylaxis

Worse prognosis:
• Chronic migraine
• Medication overuse
• Comorbid depression/anxiety
• Multiple failed treatments
• Severe attacks with high disability

MIGRAINE WITH AURA:
• Slightly increased stroke risk (especially with smoking, estrogen use)
• Counsel to avoid smoking, combined oral contraceptives`,

    prevention: `TRIGGER AVOIDANCE:
• Identify individual triggers with headache diary
• Maintain regular sleep schedule
• Regular meals, adequate hydration
• Stress management
• Limit alcohol, caffeine
• Avoid known food triggers (individual)

LIFESTYLE MODIFICATIONS:
• Regular aerobic exercise
• Weight management if overweight
• Good sleep hygiene
• Stress reduction techniques

MEDICATION PROPHYLAXIS (Indications):
• ≥4 attacks per month
• Attacks significantly impairing daily function
• Acute treatments ineffective or contraindicated
• Risk of medication overuse
• Patient preference

SPECIAL SITUATIONS:
Menstrual migraine:
• Mini-prophylaxis: NSAID or triptan starting 2 days before expected menses
• Continuous combined OCP or estrogen supplementation perimenstrually

Migraine with aura:
• Avoid combined oral contraceptives (increased stroke risk)
• Smoking cessation essential`,

    patientEducation: `UNDERSTANDING MIGRAINE:
Migraine is a real medical condition involving the brain and blood vessels. It is NOT "just a headache" or caused by stress alone. It tends to run in families and is very common - affecting about 1 in 7 people.

KEEPING A HEADACHE DIARY:
This is one of the most important things you can do. Record:
• Date and time of each headache
• How long it lasted
• How severe (0-10)
• What symptoms you had (nausea, light sensitivity)
• What might have triggered it
• What medication you took and did it help

COMMON TRIGGERS TO WATCH FOR:
• Skipping meals or fasting
• Dehydration
• Too little OR too much sleep
• Stress (or let-down after stress)
• Bright lights or strong smells
• Weather changes
• Alcohol
• Hormonal changes (periods)

TREATING AN ATTACK:
• Take your medication EARLY - At the first sign of migraine
• Rest in a dark, quiet room
• Apply cold compress to forehead
• Stay hydrated
• Sleep if you can

IMPORTANT MEDICATION RULES:
⚠️ Do not take painkillers more than 15 days per month
⚠️ Do not take triptans more than 10 days per month
→ Overuse makes headaches WORSE and more frequent

WHEN TO SEEK URGENT HELP:
🚨 The worst headache of your life (sudden, severe)
🚨 Headache with fever, stiff neck, confusion
🚨 Headache with weakness, numbness, vision loss
🚨 Headache that keeps getting worse over days/weeks
🚨 First severe headache if you're over 50

IF YOU HAVE MIGRAINE WITH AURA:
• You should NOT take combined contraceptive pills
• It is especially important NOT to smoke
• Tell your doctor if your aura changes or lasts longer than usual

PREVENTION IS KEY:
• Regular sleep, meals, and exercise
• Manage stress
• Avoid your personal triggers
• Take preventive medication regularly if prescribed`,

    references: [
      'International Headache Society ICHD-3 Criteria 2018',
      'NICE Guidelines: Headaches in over 12s - Diagnosis and management 2021',
      'American Headache Society Consensus Statement on Acute Migraine Treatment 2021',
      'WHO Essential Medicines List 2023',
      'British Association for the Study of Headache Guidelines',
      'European Headache Federation Guidelines on Migraine'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 92. TENSION HEADACHE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '92',
    name: 'Tension-Type Headache',
    slug: 'tension-headache',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'G44.2',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Tension-type headache (TTH) is the most common primary headache disorder, characterized by bilateral, pressing or tightening (non-pulsating) pain of mild-to-moderate intensity. Unlike migraine, TTH is NOT aggravated by routine physical activity and is NOT associated with nausea, vomiting, or significant photophobia/phonophobia.

TTH is classified by frequency:
• INFREQUENT EPISODIC: <1 day/month (<12 days/year)
• FREQUENT EPISODIC: 1-14 days/month for >3 months (12-180 days/year)
• CHRONIC: ≥15 days/month for >3 months (>180 days/year)

🔴 KEY CLINICAL POINTS:
• MOST COMMON headache type - affects up to 80% of population
• Diagnosis is CLINICAL - based on ICHD-3 criteria
• BILATERAL, pressing/tightening, mild-moderate, NO nausea/vomiting
• Simple analgesics effective for episodic TTH
• AVOID medication overuse (>15 days/month) - causes chronic daily headache
• Chronic TTH: Prophylaxis with amitriptyline + non-pharmacological measures
• Always exclude secondary causes with red flags

AFRICAN CONTEXT:
• Very common but often self-treated
• Stress, poor sleep, and workload are major triggers
• Limited access to preventive medications
• Important to differentiate from malaria-associated headache
• Simple analgesics widely available and effective`,

    epidemiology: `GLOBAL BURDEN:
• Most prevalent primary headache disorder
• Lifetime prevalence: 30-78% of population
• Point prevalence: 38%
• Female:Male ratio 1.4:1 (less pronounced than migraine)
• Peak prevalence: 30-39 years

CLASSIFICATION BY FREQUENCY:
• Infrequent episodic TTH: 60-80% of TTH sufferers
• Frequent episodic TTH: 20-30%
• Chronic TTH: 2-3% (but causes significant disability)

AFRICAN EPIDEMIOLOGY:
• Similar prevalence to global estimates
• Often underreported (not seen as "medical problem")
• Self-medication with OTC analgesics common
• May be attributed to "stress" or "thinking too much"
• Important to exclude secondary causes common in Africa

RISK FACTORS / TRIGGERS:
• Stress and anxiety (most common trigger)
• Poor posture (especially desk work, phone use)
• Muscle tension (neck, shoulders)
• Eye strain (uncorrected refractive errors)
• Sleep disturbance (too little or too much)
• Dehydration
• Caffeine withdrawal
• Jaw clenching/TMJ dysfunction
• Depression

COMORBIDITIES:
• Depression and anxiety
• Other chronic pain conditions
• Sleep disorders
• Migraine (may coexist)`,

    clinicalPresentation: `ICHD-3 DIAGNOSTIC CRITERIA - TENSION-TYPE HEADACHE:

A. At least 10 episodes occurring on <15 days/month (episodic) or ≥15 days/month (chronic)
B. Headache lasting 30 minutes to 7 days
C. At least 2 of the following:
   • BILATERAL location
   • Pressing or tightening (non-pulsating) quality
   • Mild or moderate intensity
   • NOT aggravated by routine physical activity
D. BOTH of the following:
   • NO nausea or vomiting
   • No more than ONE of photophobia OR phonophobia

TYPICAL DESCRIPTION:
"Band-like" or "vice-like" pressure around head
"Tight cap" sensation
"Weight on top of head"

CHARACTERISTICS:
• Location: Bilateral (forehead, temples, back of head, or whole head)
• Quality: Pressing, tightening, squeezing (NOT pulsating)
• Intensity: Mild to moderate (can work through it)
• Duration: 30 minutes to 7 days (often end of day)
• Activity: NOT worsened by walking, climbing stairs
• Associated features: May have mild light OR sound sensitivity (not both)
• Pericranial tenderness: Often present on palpation

DISTINGUISHING TTH FROM MIGRAINE:
Tension-Type Headache:
• Bilateral
• Pressing/tightening
• Mild-moderate
• Not worse with activity
• No nausea/vomiting
• ≤1 of photophobia/phonophobia

Migraine:
• Usually unilateral
• Pulsating/throbbing
• Moderate-severe
• Worse with activity
• Nausea ± vomiting
• Both photophobia AND phonophobia

CHRONIC TENSION-TYPE HEADACHE:
• ≥15 days/month for >3 months
• May evolve from episodic TTH
• Often associated with medication overuse
• Significant impact on quality of life
• May have mild nausea (distinguishes from episodic)
• Depression and anxiety common comorbidities`,

    diagnosis: `CLINICAL DIAGNOSIS:
Tension-type headache is a CLINICAL diagnosis based on:
• Characteristic features (bilateral, pressing, mild-moderate, no nausea)
• Normal neurological examination
• Absence of red flags

DIAGNOSTIC APPROACH:
1. Detailed headache history
2. Screen for red flags (secondary headache)
3. Neurological examination (should be normal)
4. No routine investigations needed if typical presentation

KEY HISTORY QUESTIONS:
• Location: Where exactly is the pain?
• Quality: What does it feel like? (pressing, throbbing?)
• Intensity: Can you work through it? (mild-moderate)
• Duration: How long do attacks last?
• Frequency: How many days per month?
• Activity: Does walking/stairs make it worse?
• Associated symptoms: Nausea? Light/sound sensitivity?
• Triggers: Stress, sleep, posture?
• Medication use: What do you take? How often?

HEADACHE DIARY:
Recommend to track:
• Frequency and duration
• Triggers
• Medication use and response
• Impact on function

PERICRANIAL MUSCLE EXAMINATION:
• Palpate: Frontalis, temporalis, masseter, pterygoid, SCM, trapezius
• Tenderness often present in TTH
• May help distinguish from other headaches

RED FLAGS - EXCLUDE SECONDARY HEADACHE:
Same as migraine - use SNOOP mnemonic:
S - Systemic symptoms (fever, weight loss)
N - Neurological symptoms/signs
O - Onset sudden (thunderclap)
O - Older age of onset (>50 years)
P - Pattern change

Additional red flags:
• Positional headache
• Worse with cough/strain
• Progressive worsening
• Papilledema

WHEN TO INVESTIGATE:
• Red flags present
• Atypical features
• Not responding to treatment
• Neurological signs on examination`,

    differentialDiagnosis: [
      'Migraine without aura - Unilateral, pulsating, moderate-severe, nausea/vomiting, photophobia AND phonophobia, worse with activity',
      'Cervicogenic headache - Associated with neck pain/stiffness, reduced ROM, pain from neck to head, often unilateral',
      'Medication overuse headache - Daily/near-daily headache, history of frequent analgesic use (>15 days/month)',
      'Chronic daily headache - Multiple causes including transformed migraine, chronic TTH, medication overuse',
      'Idiopathic intracranial hypertension - Worse with cough/strain, papilledema, visual symptoms, overweight young women',
      'Giant cell arteritis - Age >50, scalp tenderness, jaw claudication, elevated ESR, visual symptoms',
      'Brain tumor - Progressive headache, focal signs, papilledema, morning headache with vomiting',
      'Sinusitis - Facial pain over sinuses, nasal symptoms, may have fever',
      'Temporomandibular disorder - Jaw pain, clicking, pain with chewing, facial pain',
      'Refractive error - Headache with visual tasks, relieved by rest, needs eye examination'
    ],

    investigations: [
      {
        name: 'Clinical history and neurological examination',
        indication: 'All patients - diagnosis is clinical',
        availability: 'BASIC'
      },
      {
        name: 'Headache diary',
        indication: 'All patients - track frequency, triggers, medication use',
        availability: 'BASIC'
      },
      {
        name: 'Blood pressure measurement',
        indication: 'Exclude hypertension as cause',
        availability: 'BASIC'
      },
      {
        name: 'Visual acuity / Eye examination',
        indication: 'If headache associated with visual tasks - exclude refractive error',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'If systemic symptoms or anemia suspected',
        availability: 'BASIC'
      },
      {
        name: 'ESR / CRP',
        indication: 'Age >50 years to exclude giant cell arteritis',
        availability: 'BASIC'
      },
      {
        name: 'CT / MRI brain',
        indication: 'ONLY if red flags present - not routine for typical TTH',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Confirm diagnosis meets ICHD-3 criteria',
        'Exclude secondary causes (red flags)',
        'Determine frequency: Episodic vs Chronic',
        'Assess medication use (check for overuse)',
        'Identify triggers and contributing factors',
        'Educate patient about condition',
        'Determine treatment approach based on frequency'
      ],

      pharmacological: [
        {
          drug: 'Simple analgesics (Paracetamol)',
          indication: 'First-line for episodic TTH - effective and well-tolerated',
          dose: 'Paracetamol 1000mg (two 500mg tablets)',
          route: 'Oral',
          duration: 'Single dose for acute attack. LIMIT to <15 days/month',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity (overdose)', 'Generally very well tolerated'],
          alternatives: ['NSAIDs']
        },
        {
          drug: 'NSAIDs (Ibuprofen, Aspirin, Naproxen)',
          indication: 'Alternative first-line for episodic TTH',
          dose: 'Ibuprofen 400mg; Aspirin 500-1000mg; Naproxen 250-500mg',
          route: 'Oral',
          duration: 'Single dose for acute attack. LIMIT to <15 days/month',
          contraindications: ['Peptic ulcer', 'Renal impairment', 'Aspirin-sensitive asthma', 'Third trimester pregnancy'],
          sideEffects: ['GI upset', 'GI bleeding', 'Renal impairment'],
          alternatives: ['Paracetamol']
        },
        {
          drug: 'Combination analgesics (Paracetamol + Caffeine)',
          indication: 'May be more effective than simple analgesics alone',
          dose: 'Paracetamol 1000mg + Caffeine 130mg',
          route: 'Oral',
          duration: 'Acute attacks. Limit frequency to avoid MOH and caffeine dependence',
          contraindications: ['Same as paracetamol', 'Anxiety disorders (caffeine)'],
          sideEffects: ['Caffeine: Insomnia, anxiety, palpitations'],
          alternatives: ['Simple analgesics without caffeine']
        },
        {
          drug: 'PROPHYLAXIS: Amitriptyline',
          indication: 'First-line prophylaxis for CHRONIC TTH (≥15 days/month)',
          dose: 'Start 10-25mg at night; increase gradually every 2 weeks to 50-75mg (max 150mg)',
          route: 'Oral',
          duration: 'Minimum 3-6 months; continue if effective',
          contraindications: ['Recent MI', 'Arrhythmias', 'Urinary retention', 'Glaucoma'],
          sideEffects: ['Drowsiness', 'Dry mouth', 'Constipation', 'Weight gain', 'Urinary retention'],
          alternatives: ['Nortriptyline (less sedating)', 'Mirtazapine']
        },
        {
          drug: 'PROPHYLAXIS: Nortriptyline',
          indication: 'Alternative to amitriptyline if sedation problematic',
          dose: 'Start 10-25mg at night; increase to 50-75mg',
          route: 'Oral',
          duration: 'Minimum 3-6 months',
          contraindications: ['Same as amitriptyline'],
          sideEffects: ['Less sedating than amitriptyline', 'Same anticholinergic effects'],
          alternatives: ['Amitriptyline', 'Mirtazapine']
        },
        {
          drug: 'PROPHYLAXIS: Mirtazapine',
          indication: 'Alternative prophylaxis; good if comorbid depression/insomnia',
          dose: 'Start 15mg at night; may increase to 30-45mg',
          route: 'Oral',
          duration: 'Minimum 3-6 months',
          contraindications: ['MAOIs'],
          sideEffects: ['Sedation', 'Weight gain', 'Increased appetite'],
          alternatives: ['Amitriptyline']
        }
      ],

      nonPharmacological: [
        'STRESS MANAGEMENT - Often the most important intervention',
        'Relaxation techniques: Progressive muscle relaxation, deep breathing',
        'Regular exercise - 30 minutes aerobic exercise, 3-5 times/week',
        'Sleep hygiene - Regular sleep schedule, 7-8 hours/night',
        'Posture correction - Especially for desk workers, phone users',
        'Regular breaks from screen work (20-20-20 rule)',
        'Correct refractive errors (eye examination)',
        'Physical therapy for neck/shoulder tension',
        'Massage therapy',
        'Biofeedback (where available)',
        'Cognitive behavioral therapy for chronic TTH',
        'Acupuncture (some evidence of benefit)',
        'Avoid excessive caffeine and ensure adequate hydration',
        'Address underlying depression/anxiety if present'
      ],

      surgical: [
        'No surgical treatment for tension-type headache'
      ],

      referralCriteria: [
        'Diagnostic uncertainty - atypical features or red flags',
        'Chronic TTH not responding to first-line treatment',
        'Suspected medication overuse headache',
        'Significant psychiatric comorbidity requiring specialist input',
        'Need for specialized interventions (biofeedback, CBT)',
        'Multiple treatment failures'
      ],

      monitoring: [
        'Headache diary - Frequency, triggers, medication use',
        'Review response to treatment in 4-8 weeks',
        'Monitor for medication overuse (>15 days/month)',
        'Assess impact on quality of life',
        'Screen for depression/anxiety',
        'If on amitriptyline: Monitor for side effects, consider ECG if cardiac history'
      ],

      followUp: `EPISODIC TTH:
• Usually managed in primary care
• Review if not responding to simple analgesics
• Monitor for progression to chronic TTH
• Watch for medication overuse

CHRONIC TTH:
• Review 4-6 weeks after starting prophylaxis
• Allow 2-3 months for full prophylactic effect
• If effective: Continue 6-12 months, then gradual taper
• If not effective: Try alternative prophylaxis

TREATMENT GOALS:
• Reduce headache frequency by ≥50%
• Reduce disability
• Prevent medication overuse
• Improve quality of life`
    },

    redFlags: [
      '🔴 CRITICAL: Thunderclap headache (worst headache, sudden onset) - SAH, urgent CT/LP',
      '🔴 CRITICAL: Headache with fever, neck stiffness, altered consciousness - Meningitis',
      '🔴 CRITICAL: Headache with focal neurological signs - Space-occupying lesion, stroke',
      '🔴 CRITICAL: Headache with papilledema - Raised intracranial pressure',
      '🟠 URGENT: New headache pattern in patient >50 years - GCA, malignancy',
      '🟠 URGENT: Progressive headache worsening over weeks - Exclude mass lesion',
      '🟠 URGENT: Positional headache (worse lying/standing) - CSF pressure disorder',
      '🟡 WARNING: Daily headache with analgesic use >15 days/month - Medication overuse headache',
      '🟡 WARNING: Headache with depression/anxiety - Address mental health',
      '🟡 WARNING: Change in established headache pattern - Reassess diagnosis'
    ],

    complications: [
      'Medication overuse headache - From chronic analgesic use (>15 days/month)',
      'Progression to chronic TTH',
      'Depression and anxiety (comorbid or consequence)',
      'Reduced quality of life',
      'Impaired work/school performance',
      'Sleep disturbance',
      'NSAID complications: GI bleeding, renal impairment (if overused)'
    ],

    prognosis: `EPISODIC TTH:
• Excellent prognosis
• Many patients improve with lifestyle modification
• Simple analgesics effective
• Does not typically progress

CHRONIC TTH:
• More challenging to treat
• Often associated with medication overuse
• Comorbid depression/anxiety common
• With proper treatment, many can revert to episodic

FACTORS AFFECTING OUTCOME:
Better prognosis:
• Episodic rather than chronic
• No medication overuse
• Identifiable triggers that can be modified
• Good response to treatment
• No psychiatric comorbidity

Worse prognosis:
• Chronic daily headache
• Medication overuse
• Comorbid depression/anxiety
• Poor stress management
• Multiple failed treatments`,

    prevention: `LIFESTYLE MODIFICATION (Most important):
• Stress management and relaxation techniques
• Regular exercise (30 min, 3-5x/week)
• Consistent sleep schedule
• Ergonomic workplace setup
• Regular breaks from screen work
• Adequate hydration
• Limit caffeine intake
• Address underlying anxiety/depression

AVOIDING MEDICATION OVERUSE:
• Do NOT use analgesics >15 days per month
• Track medication use in headache diary
• If needing frequent analgesics → consider prophylaxis

PROPHYLAXIS INDICATIONS:
• Chronic TTH (≥15 days/month)
• Frequent episodic TTH with significant disability
• Risk of medication overuse
• Not responding to acute treatment alone

WORKPLACE CONSIDERATIONS:
• Ergonomic assessment
• Regular breaks (every 30-60 minutes)
• Screen positioning and lighting
• Stress reduction strategies`,

    patientEducation: `UNDERSTANDING TENSION-TYPE HEADACHE:
Tension-type headache is the most common type of headache. It feels like a tight band around your head or pressure on top of your head. Unlike migraine, you usually don't have nausea or vomiting, and you can usually continue your daily activities.

TYPICAL FEATURES:
• Pain on both sides of your head
• Pressing or squeezing feeling (not throbbing)
• Mild to moderate pain (you can usually still function)
• Not made worse by walking or climbing stairs
• No nausea or vomiting

COMMON TRIGGERS:
• Stress and worry (most common)
• Poor sleep (too little or too much)
• Skipping meals or dehydration
• Poor posture (desk work, phone use)
• Eye strain (may need glasses check)
• Muscle tension in neck and shoulders

TREATING AN ATTACK:
• Take paracetamol (1000mg) or ibuprofen (400mg) early
• Rest in a quiet place if possible
• Apply gentle massage to neck and shoulders
• Use hot or cold compress on neck/forehead
• Stay hydrated

⚠️ IMPORTANT - MEDICATION LIMIT:
Do NOT take painkillers more than 15 days per month
Taking too many painkillers can actually CAUSE more headaches (medication overuse headache)

PREVENTING HEADACHES:
• Manage stress - learn relaxation techniques
• Exercise regularly - walking, swimming, cycling
• Keep a regular sleep schedule
• Don't skip meals, drink enough water
• Take regular breaks from screens (every 30-60 minutes)
• Check your posture - especially when working
• Get your eyes tested if headaches relate to reading/screens

WHEN TO SEE A DOCTOR:
• Headaches becoming more frequent
• Needing painkillers more than 2 days per week
• Headaches stopping you from working or daily activities
• New or different type of headache

WHEN TO SEEK URGENT HELP:
🚨 Sudden severe headache (worst of your life)
🚨 Headache with fever, stiff neck, confusion
🚨 Headache with weakness, numbness, vision problems
🚨 Headache getting progressively worse over days/weeks

LIFESTYLE IS KEY:
For most people with tension-type headache, improving stress management, sleep, exercise, and posture makes the biggest difference - even more than medication.`,

    references: [
      'International Headache Society ICHD-3 Criteria 2018',
      'NICE Guidelines: Headaches in over 12s 2021',
      'European Federation of Neurological Societies Guidelines on TTH',
      'WHO Essential Medicines List 2023',
      'British Association for the Study of Headache Guidelines',
      'Bendtsen L, et al. EFNS guideline on the treatment of tension-type headache. Eur J Neurol 2010'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 93. VERTIGO / DIZZINESS
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '93',
    name: 'Vertigo and Dizziness',
    slug: 'vertigo',
    specialty: 'ENT',
    bodySystem: 'Neurological',
    icdCode: 'R42',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Dizziness is one of the most common presenting complaints in medicine, affecting up to 30% of the population at some point. It encompasses several distinct sensations that must be differentiated for proper management:

• VERTIGO: Illusion of movement (spinning/tilting) - vestibular origin
• PRESYNCOPE: Feeling of impending faint - cardiovascular origin
• DISEQUILIBRIUM: Unsteadiness/imbalance - multifactorial
• Non-specific lightheadedness - Often anxiety, hyperventilation

🔴 KEY CLINICAL POINTS:
• BPPV (Benign Paroxysmal Positional Vertigo) is the MOST COMMON cause of vertigo
• DISTINGUISH PERIPHERAL vs CENTRAL vertigo - Central is dangerous!
• DIX-HALLPIKE TEST: Diagnostic for posterior canal BPPV
• EPLEY MANEUVER: Highly effective treatment for BPPV (80-90% cure rate)
• HINTS exam: Better than MRI for differentiating stroke from vestibular neuritis
• Central causes require urgent imaging and neurology referral

PERIPHERAL vs CENTRAL VERTIGO:
PERIPHERAL (Inner ear):
• Sudden onset, severe, episodic
• Horizontal/torsional nystagmus (suppressed by fixation)
• Hearing loss/tinnitus may be present
• No other neurological signs
• Examples: BPPV, vestibular neuritis, Meniere's disease

CENTRAL (Brainstem/cerebellum):
• Gradual or sudden onset
• Vertical or direction-changing nystagmus (not suppressed by fixation)
• Associated neurological signs (diplopia, dysarthria, dysphagia, weakness)
• Severe imbalance (cannot walk)
• Examples: Stroke, MS, tumor

AFRICAN CONTEXT:
• BPPV often unrecognized - Epley maneuver curative but underutilized
• Vestibular sedatives often over-prescribed
• Important to exclude central causes and cardiovascular disease
• Malaria can cause dizziness - consider in endemic areas
• Limited access to specialized vestibular testing`,

    epidemiology: `GLOBAL BURDEN:
• Dizziness: Lifetime prevalence 20-30%
• Vertigo: 7% of population annually
• Accounts for 3-5% of emergency department visits
• More common with increasing age
• Female predominance (1.5-2:1)

CAUSES BY FREQUENCY:
1. BPPV - Most common (17-42% of vertigo)
2. Vestibular migraine (7-15%)
3. Vestibular neuritis/labyrinthitis (3-10%)
4. Meniere's disease (2-10%)
5. Central causes (5-10%)
6. Psychogenic (10-15%)

AFRICAN EPIDEMIOLOGY:
• Similar prevalence to global data
• BPPV often misdiagnosed as "BP problems" or malaria
• Stroke risk factors (hypertension, diabetes) common
• Infectious causes: Malaria, TB meningitis, HIV-related
• Limited access to specialized diagnosis
• Traditional remedies may delay presentation

RISK FACTORS:
• Increasing age
• Female sex
• Previous vestibular disorder
• Head trauma
• Migraine history
• Cardiovascular risk factors (for central causes)
• Anxiety/depression (psychogenic dizziness)
• Ototoxic medications (aminoglycosides, quinine)`,

    clinicalPresentation: `FIRST: CHARACTERIZE THE DIZZINESS

Ask: "What exactly do you mean by dizzy?"

1. VERTIGO (Room spinning or tilting):
   • Suggests vestibular pathology
   • Peripheral vs central differentiation crucial

2. PRESYNCOPE (Feeling about to faint):
   • Suggests cardiovascular cause
   • Orthostatic hypotension, arrhythmia, vasovagal

3. DISEQUILIBRIUM (Unsteady, off-balance):
   • Often multifactorial in elderly
   • Peripheral neuropathy, visual impairment, deconditioning

4. NON-SPECIFIC (Floating, lightheaded):
   • Often anxiety, hyperventilation, medications

TIMING AND TRIGGERS - Key Diagnostic Features:

BPPV (Benign Paroxysmal Positional Vertigo):
• Brief episodes (<1 minute)
• Triggered by position change (turning in bed, looking up, bending)
• No hearing loss
• Positive Dix-Hallpike test

VESTIBULAR NEURITIS/LABYRINTHITIS:
• Sudden onset severe vertigo
• Lasts days (continuous initially, then improves)
• Often post-viral
• Labyrinthitis: + hearing loss; Neuritis: hearing preserved
• Positive head impulse test

MENIERE'S DISEASE:
• Episodes lasting 20 minutes to hours
• Associated: Fluctuating hearing loss, tinnitus, aural fullness
• "Triad": Vertigo + hearing loss + tinnitus
• Spontaneous, not positional

VESTIBULAR MIGRAINE:
• Episodes minutes to 72 hours
• Associated with migraine headache (or history of migraine)
• Photophobia, phonophobia
• May not have headache with every episode

CENTRAL VERTIGO (Stroke, tumor, MS):
• May be sudden or gradual
• Often with neurological symptoms
• Severe imbalance (cannot walk unaided)
• Vertical/direction-changing nystagmus

ASSOCIATED SYMPTOMS:
• Hearing loss: Meniere's, labyrinthitis, acoustic neuroma
• Tinnitus: Meniere's, labyrinthitis
• Headache: Vestibular migraine, central cause
• Neurological symptoms: CENTRAL - urgent evaluation
• Nausea/vomiting: Common with all vestibular vertigo`,

    diagnosis: `CLINICAL DIAGNOSIS:
Vertigo diagnosis is primarily clinical based on:
• Characterization of dizziness type
• Timing and triggers
• Associated symptoms
• Bedside examination tests

KEY HISTORY QUESTIONS:
1. What do you mean by "dizzy"? (Spinning? Faint? Unsteady?)
2. How long do episodes last? (Seconds? Minutes? Hours? Days?)
3. What triggers it? (Position change? Spontaneous? Standing?)
4. Associated symptoms? (Hearing loss? Tinnitus? Headache? Neurological?)
5. Is this the first episode or recurrent?
6. Any recent illness? (Viral URI - vestibular neuritis)
7. Medications? (Ototoxic drugs, BP medications)
8. Cardiovascular risk factors?

BEDSIDE EXAMINATION:

1. DIX-HALLPIKE TEST (Diagnostic for posterior canal BPPV):
   • Patient sitting, head turned 45° to one side
   • Rapidly lie patient back with head hanging 20° below horizontal
   • Watch eyes for 30 seconds
   • Positive: Upbeat-torsional nystagmus after brief latency (2-20 sec), fatigues <1 min
   • Repeat on other side

2. HEAD IMPULSE TEST (Assesses VOR):
   • Patient fixates on examiner's nose
   • Rapidly turn head 10-20° to one side
   • Normal: Eyes stay on target
   • Abnormal (peripheral): Corrective saccade (eyes "catch up")

3. HINTS EXAM (For acute vestibular syndrome - better than MRI!):
   • Head Impulse test
   • Nystagmus type
   • Test of Skew
   • Peripheral pattern: Abnormal HI, horizontal nystagmus, no skew
   • Central pattern: Normal HI, vertical/direction-changing nystagmus, skew present → STROKE

4. ROMBERG TEST:
   • Standing with feet together, eyes closed
   • Positive: Falls with eyes closed (proprioceptive or vestibular)

5. OTOSCOPY:
   • Exclude ear pathology (infection, cholesteatoma, perforation)

6. HEARING ASSESSMENT:
   • Whispered voice test or tuning fork tests (Rinne, Weber)

WHEN TO IMAGE:
• Red flags for central cause
• Atypical presentation
• Neurological signs
• Not responding to treatment
• First episode in elderly with vascular risk factors`,

    differentialDiagnosis: [
      'BPPV - Brief (<1 min), positional, positive Dix-Hallpike, no hearing loss',
      'Vestibular neuritis - Sudden onset, continuous days, post-viral, positive head impulse, no hearing loss',
      'Labyrinthitis - Same as neuritis but WITH hearing loss',
      'Meniere disease - Episodes 20 min-hours, hearing loss + tinnitus + aural fullness',
      'Vestibular migraine - Minutes to hours, migraine history/features',
      'Posterior circulation stroke - Sudden onset, neurological signs, abnormal HINTS (normal head impulse, vertical nystagmus, skew)',
      'Acoustic neuroma - Progressive hearing loss, tinnitus, imbalance',
      'Orthostatic hypotension - Presyncope on standing, BP drop >20/10 mmHg',
      'Cardiac arrhythmia - Presyncope, palpitations, may have syncope',
      'Anxiety/Panic disorder - Lightheadedness, hyperventilation, situational'
    ],

    investigations: [
      {
        name: 'Dix-Hallpike test',
        indication: 'All patients with positional vertigo - diagnostic for BPPV',
        availability: 'BASIC'
      },
      {
        name: 'Head impulse test',
        indication: 'Acute vestibular syndrome - distinguish peripheral from central',
        availability: 'BASIC'
      },
      {
        name: 'Blood pressure (lying and standing)',
        indication: 'Presyncope - check for orthostatic hypotension',
        availability: 'BASIC'
      },
      {
        name: 'Blood glucose',
        indication: 'Exclude hypoglycemia as cause',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Exclude anemia',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: 'Presyncope, cardiac risk factors - exclude arrhythmia',
        availability: 'BASIC'
      },
      {
        name: 'Hearing test (Audiometry)',
        indication: 'Vertigo with hearing symptoms - Meniere, labyrinthitis, acoustic neuroma',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT head',
        indication: 'Acute presentation with central features if MRI unavailable',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI brain (with IAMs)',
        indication: 'Suspected central cause, acoustic neuroma, not responding to treatment',
        availability: 'ADVANCED'
      },
      {
        name: 'Videonystagmography (VNG)',
        indication: 'Specialized vestibular testing, complex cases',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        'Characterize type of dizziness (vertigo vs presyncope vs disequilibrium)',
        'Assess for red flags suggesting central cause',
        'Perform Dix-Hallpike test if positional symptoms',
        'Check vital signs including orthostatic BP',
        'Perform HINTS exam if acute vestibular syndrome',
        'If central cause suspected → urgent imaging and neurology referral',
        'For BPPV: Perform Epley maneuver'
      ],

      pharmacological: [
        {
          drug: 'Vestibular sedatives (Prochlorperazine, Promethazine, Cinnarizine)',
          indication: 'SHORT-TERM symptomatic relief of acute vertigo and nausea (max 3 days)',
          dose: 'Prochlorperazine 5-10mg TDS; Promethazine 25mg TDS; Cinnarizine 15-30mg TDS',
          route: 'Oral, IM, or suppository (if vomiting)',
          duration: 'MAXIMUM 3 days - prolonged use delays vestibular compensation',
          contraindications: ['Parkinson disease', 'Caution in elderly'],
          sideEffects: ['Drowsiness', 'Extrapyramidal effects', 'Dry mouth'],
          alternatives: ['Ondansetron for nausea without sedation']
        },
        {
          drug: 'Betahistine',
          indication: 'Meniere disease - May reduce frequency of attacks',
          dose: '16mg TDS (may increase to 24-48mg TDS)',
          route: 'Oral',
          duration: 'Long-term for Meniere disease',
          contraindications: ['Pheochromocytoma', 'Active peptic ulcer'],
          sideEffects: ['GI upset', 'Headache'],
          alternatives: ['Diuretics (low-salt diet)']
        },
        {
          drug: 'Corticosteroids (for vestibular neuritis)',
          indication: 'May improve recovery in vestibular neuritis if given early',
          dose: 'Prednisolone 50-60mg daily for 5-7 days then taper',
          route: 'Oral',
          duration: '10-14 days total with taper',
          contraindications: ['Active infection', 'Uncontrolled diabetes'],
          sideEffects: ['Hyperglycemia', 'Mood changes', 'Insomnia'],
          alternatives: ['Supportive care alone']
        },
        {
          drug: 'Diuretics (for Meniere disease)',
          indication: 'Reduce endolymphatic pressure in Meniere disease',
          dose: 'Hydrochlorothiazide 25-50mg daily or Acetazolamide 250mg BD',
          route: 'Oral',
          duration: 'Long-term for prevention',
          contraindications: ['Electrolyte imbalances', 'Renal impairment'],
          sideEffects: ['Hypokalemia', 'Dehydration', 'Paresthesias (acetazolamide)'],
          alternatives: ['Salt restriction alone']
        },
        {
          drug: 'Migraine prophylaxis (for vestibular migraine)',
          indication: 'Frequent vestibular migraine episodes',
          dose: 'Propranolol 40-160mg/day; Amitriptyline 10-50mg nocte; Topiramate 50-100mg/day',
          route: 'Oral',
          duration: '3-6 months minimum trial',
          contraindications: ['As per individual drug'],
          sideEffects: ['As per individual drug'],
          alternatives: ['Different prophylactic agent']
        }
      ],

      nonPharmacological: [
        'EPLEY MANEUVER - First-line for posterior canal BPPV (80-90% effective):',
        '  1. Start with positive Dix-Hallpike position (affected ear down)',
        '  2. Turn head 90° to opposite side (affected ear up)',
        '  3. Roll onto side, nose pointing down',
        '  4. Sit up slowly from side-lying position',
        '  5. Hold each position until nystagmus stops + 30 seconds',
        '  6. May repeat 2-3 times',
        'VESTIBULAR REHABILITATION - Essential for chronic/persistent symptoms',
        'Balance exercises and gaze stabilization',
        'Avoid vestibular suppressants beyond 3 days (delay compensation)',
        'Meniere disease: Low-salt diet (<2g/day), caffeine restriction, stress management',
        'Fall prevention in elderly with chronic dizziness',
        'Anxiety management if contributing',
        'Teach home Epley maneuver for recurrent BPPV'
      ],

      surgical: [
        'Rarely needed for peripheral vestibular disorders',
        'Meniere disease: Endolymphatic sac surgery, vestibular neurectomy, or labyrinthectomy for intractable cases',
        'Acoustic neuroma: Surgery or radiosurgery depending on size/symptoms'
      ],

      referralCriteria: [
        'Suspected central cause - URGENT neurology/stroke referral',
        'Sudden hearing loss with vertigo - URGENT ENT referral',
        'BPPV not responding to Epley maneuver after 3 attempts',
        'Recurrent or persistent vertigo not responding to treatment',
        'Suspected Meniere disease for diagnosis confirmation',
        'Suspected acoustic neuroma (progressive hearing loss + tinnitus)',
        'Need for specialized vestibular testing',
        'Complex or atypical presentations'
      ],

      monitoring: [
        'BPPV: Check resolution after Epley, may need repeat',
        'Vestibular neuritis: Monitor for recovery over weeks',
        'Meniere disease: Track attack frequency, hearing levels',
        'Vestibular migraine: Headache diary, attack frequency',
        'Monitor for medication side effects',
        'Fall risk assessment in elderly'
      ],

      followUp: `BPPV:
• Review 1-2 weeks after Epley maneuver
• If symptoms persist: Repeat Epley or consider horizontal canal BPPV
• Teach self-treatment Epley for recurrences

VESTIBULAR NEURITIS:
• Acute symptoms improve over days to weeks
• Complete recovery may take months
• Vestibular rehabilitation speeds recovery
• Persistent symptoms: Refer for vestibular rehabilitation

MENIERE DISEASE:
• Regular monitoring every 3-6 months
• Track attack frequency and hearing
• Audiometry periodically
• Refer if attacks frequent despite treatment

VESTIBULAR MIGRAINE:
• Monitor as per migraine management
• Prophylaxis if frequent episodes`
    },

    redFlags: [
      '🔴 CRITICAL: Acute vertigo + neurological signs (diplopia, dysarthria, dysphagia, weakness) - STROKE',
      '🔴 CRITICAL: Normal head impulse test + vertical nystagmus + skew deviation - CENTRAL cause (HINTS)',
      '🔴 CRITICAL: Sudden severe headache with vertigo - Cerebellar hemorrhage/stroke',
      '🔴 CRITICAL: Decreased consciousness with vertigo - Central cause',
      '🟠 URGENT: Sudden hearing loss with vertigo - Labyrinthitis or acoustic emergency - ENT referral',
      '🟠 URGENT: Unable to walk (severe ataxia) - Central cause or severe peripheral',
      '🟠 URGENT: First episode in elderly with cardiovascular risk factors - Consider stroke',
      '🟡 WARNING: Persistent unilateral hearing loss + tinnitus - Exclude acoustic neuroma',
      '🟡 WARNING: Vertigo with recurrent falls - Fall risk assessment needed',
      '🟡 WARNING: Presyncope with exertion or palpitations - Cardiac evaluation needed'
    ],

    complications: [
      'Falls and fall-related injuries (fractures, head injury)',
      'Chronic imbalance and reduced mobility',
      'Anxiety and depression (common with chronic dizziness)',
      'Reduced quality of life',
      'Social isolation and functional impairment',
      'Medication side effects (vestibular suppressants)',
      'Delayed vestibular compensation (from excessive medication use)',
      'Meniere: Progressive hearing loss'
    ],

    prognosis: `BPPV:
• Excellent prognosis
• 80-90% cure rate with single Epley maneuver
• May recur (15-20% within 1 year)
• Self-treatment effective for recurrences

VESTIBULAR NEURITIS:
• Gradual recovery over weeks to months
• Most recover fully with vestibular compensation
• 10-15% develop BPPV afterwards
• Recurrence uncommon

MENIERE DISEASE:
• Chronic condition with unpredictable attacks
• Hearing loss tends to progress over years
• Vertigo attacks may "burn out" over time
• 5-15% have bilateral involvement

VESTIBULAR MIGRAINE:
• Chronic relapsing condition
• Responds to migraine management
• Prognosis similar to migraine

CENTRAL CAUSES:
• Depends on underlying cause
• Stroke prognosis depends on severity and treatment timing`,

    prevention: `BPPV:
• Avoid prolonged head-down positions
• Sleep with head slightly elevated
• Vitamin D supplementation may reduce recurrence
• Learn home Epley maneuver for early self-treatment

MENIERE DISEASE:
• Low-salt diet (<2g/day)
• Avoid caffeine and alcohol
• Stress management
• Regular sleep patterns
• Take betahistine as prescribed

VESTIBULAR MIGRAINE:
• Same prevention as migraine
• Identify and avoid triggers
• Consider prophylaxis if frequent

GENERAL:
• Fall prevention measures in elderly
• Avoid ototoxic medications when possible
• Manage cardiovascular risk factors (stroke prevention)
• Treat ear infections promptly`,

    patientEducation: `UNDERSTANDING YOUR DIZZINESS:
Dizziness can mean different things. True vertigo is when you or the room feels like it's spinning. The most common cause is a condition called BPPV, where tiny crystals in your inner ear become dislodged. The good news is this can usually be cured with a simple repositioning maneuver.

WHAT IS BPPV?
• Stands for Benign Paroxysmal Positional Vertigo
• Caused by crystals in the inner ear moving where they shouldn't be
• Triggered by head movements (turning in bed, looking up, bending over)
• Episodes are brief (usually less than 1 minute)
• Can be cured with the Epley maneuver (your doctor can show you)

IF YOU HAVE BPPV:
• The Epley maneuver works 80-90% of the time
• You may feel slightly off-balance for a few days after treatment
• Sleep propped up for a night or two after the maneuver
• Avoid lying flat for 24-48 hours if possible
• It can come back - learn to do the maneuver at home

DURING AN EPISODE OF VERTIGO:
• Sit or lie down immediately
• Keep your head still
• Focus on a stationary object
• Don't close your eyes (makes it worse)
• Move slowly when it settles

MEDICATIONS:
• Vestibular suppressants (like prochlorperazine) help with nausea
• DO NOT take these for more than 3 days - they slow your recovery
• Your brain needs to "recalibrate" - medications prevent this

WHEN TO SEEK URGENT HELP:
🚨 Vertigo with any of these needs IMMEDIATE attention:
• Weakness or numbness on one side
• Difficulty speaking or understanding speech
• Double vision or vision loss
• Severe headache
• Cannot walk at all
• Sudden hearing loss

VESTIBULAR REHABILITATION:
If your dizziness is lasting, exercises can help your brain compensate:
• Your doctor or physiotherapist can teach you specific exercises
• These involve controlled head and eye movements
• Do them regularly as prescribed
• It may feel worse initially but leads to better recovery

FOR MENIERE'S DISEASE:
• Reduce salt in your diet (less than 2g per day)
• Avoid caffeine and alcohol
• Manage stress
• Take your medications regularly
• Keep a diary of attacks to identify triggers`,

    references: [
      'Bhattacharyya N, et al. AAO-HNS Clinical Practice Guideline: BPPV 2017',
      'Strupp M, et al. Vestibular neuritis. NEJM 2024',
      'Lempert T, et al. Vestibular migraine: Diagnostic criteria. J Vestib Res 2012',
      'Kattah JC, et al. HINTS to diagnose stroke in acute vestibular syndrome. Stroke 2009',
      'NICE Clinical Knowledge Summaries: Vertigo',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 94. INSOMNIA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '94',
    name: 'Insomnia',
    slug: 'insomnia',
    specialty: 'Psychiatry',
    bodySystem: 'Neurological',
    icdCode: 'G47.0',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Insomnia is the most common sleep disorder, characterized by difficulty initiating sleep, maintaining sleep, or early morning awakening, despite adequate opportunity for sleep, resulting in daytime impairment. It affects approximately 10-30% of the adult population.

CLASSIFICATION:
• ACUTE (Short-term) insomnia: <3 months duration, often triggered by stress
• CHRONIC insomnia: ≥3 nights/week for ≥3 months
• Comorbid insomnia: Associated with medical/psychiatric conditions

🔴 KEY CLINICAL POINTS:
• CBT-I (Cognitive Behavioral Therapy for Insomnia) is FIRST-LINE treatment
• CBT-I is MORE effective than medications long-term
• Hypnotics should be SHORT-TERM only (2-4 weeks maximum)
• Always identify and treat underlying causes
• Sleep hygiene alone is usually insufficient for chronic insomnia
• Avoid benzodiazepines long-term - dependence, tolerance, falls risk

COMMON CAUSES:
• Primary insomnia (no identifiable cause)
• Psychiatric: Depression, anxiety, PTSD (very common)
• Medical: Pain, COPD, heart failure, GERD, nocturia, menopause
• Medications: Steroids, stimulants, beta-blockers, SSRIs
• Substances: Caffeine, alcohol, nicotine
• Sleep disorders: Sleep apnea, restless legs syndrome
• Circadian rhythm disorders: Shift work, jet lag

AFRICAN CONTEXT:
• Often undertreated or self-medicated
• Depression and anxiety frequently comorbid but underdiagnosed
• Limited access to CBT-I specialists
• Benzodiazepines often overused
• Environmental factors: Heat, noise, overcrowding
• Malaria and other infections can disrupt sleep
• Economic stress is a major contributor`,

    epidemiology: `GLOBAL BURDEN:
• Insomnia symptoms: 30-35% of adults
• Insomnia disorder (with daytime impairment): 10-15%
• Chronic insomnia: 6-10%
• Female:Male ratio ~1.5:1
• Increases with age
• Associated with significant healthcare costs and lost productivity

AFRICAN EPIDEMIOLOGY:
• Similar prevalence estimates (10-30%)
• Often underreported and undertreated
• High rates of comorbid depression and anxiety
• Environmental factors significant:
  - Heat and humidity affecting sleep
  - Noise pollution in urban areas
  - Overcrowded living conditions
  - Economic stress and worry
• Malaria and other infections disrupt sleep
• HIV/AIDS associated with sleep disturbance

RISK FACTORS:
• Female sex
• Older age
• Psychiatric disorders (depression, anxiety, PTSD)
• Medical conditions (chronic pain, respiratory disease)
• Shift work
• Low socioeconomic status
• Life stressors
• Family history of insomnia
• Personality traits (tendency to worry, perfectionism)

CONSEQUENCES OF UNTREATED INSOMNIA:
• Impaired daytime functioning and concentration
• Increased accident risk
• Depression and anxiety (bidirectional relationship)
• Cardiovascular disease
• Metabolic disorders
• Reduced quality of life
• Economic burden (absenteeism, reduced productivity)`,

    clinicalPresentation: `DIAGNOSTIC CRITERIA (ICSD-3):
A. Difficulty initiating sleep, maintaining sleep, or waking earlier than desired
B. Sleep difficulty occurs despite adequate opportunity and circumstances for sleep
C. Daytime consequences (at least one):
   • Fatigue or malaise
   • Attention, concentration, or memory impairment
   • Social, occupational, or academic dysfunction
   • Mood disturbance or irritability
   • Daytime sleepiness
   • Reduced motivation, energy, or initiative
   • Proneness to errors or accidents
   • Concerns about or dissatisfaction with sleep

TYPES OF SLEEP DIFFICULTY:
1. SLEEP-ONSET INSOMNIA (Difficulty falling asleep):
   • Lying awake >30 minutes before falling asleep
   • Often associated with anxiety, rumination
   • "Racing mind" at bedtime

2. SLEEP-MAINTENANCE INSOMNIA (Difficulty staying asleep):
   • Waking during the night
   • Difficulty returning to sleep (>30 min awake)
   • May be associated with depression, sleep apnea, pain

3. EARLY MORNING AWAKENING:
   • Waking earlier than desired (>30 min early)
   • Unable to return to sleep
   • Classic feature of depression

ASSOCIATED FEATURES:
• Spending excessive time in bed
• Irregular sleep schedule
• Daytime napping (can perpetuate insomnia)
• Pre-sleep arousal (worry about sleep)
• Conditioned arousal (bed associated with wakefulness)
• Compensatory behaviors (caffeine, alcohol)

ASSESS FOR COMORBIDITIES:
• Depression: Low mood, anhedonia, hopelessness, appetite/weight changes
• Anxiety: Worry, tension, panic symptoms
• Sleep apnea: Snoring, witnessed apneas, daytime sleepiness, obesity
• Restless legs syndrome: Urge to move legs, worse at rest/evening
• Pain: Chronic pain conditions
• Substance use: Caffeine, alcohol, recreational drugs`,

    diagnosis: `CLINICAL DIAGNOSIS:
Insomnia is primarily a clinical diagnosis based on:
• Sleep complaint with daytime impairment
• Adequate opportunity for sleep
• Duration and frequency criteria
• Exclusion or identification of comorbid conditions

KEY HISTORY COMPONENTS:

SLEEP HISTORY:
• Bedtime and wake time (weekdays and weekends)
• Time to fall asleep (sleep onset latency)
• Number and duration of nighttime awakenings
• Final wake time vs desired wake time
• Total sleep time
• Sleep quality (subjective)
• Daytime functioning and symptoms

24-HOUR HISTORY:
• Daytime napping
• Caffeine intake (amount, timing)
• Alcohol use (often used as sleep aid but disrupts sleep)
• Nicotine use
• Exercise (type, timing)
• Screen use before bed
• Bedroom environment (temperature, noise, light, bed partner)

MEDICAL HISTORY:
• Chronic conditions affecting sleep
• Medications (review all - many affect sleep)
• Psychiatric history (depression, anxiety, trauma)
• Previous sleep treatments and response

SLEEP DIARY:
• Gold standard for assessment
• 2 weeks minimum
• Record: Bedtime, wake time, sleep latency, awakenings, sleep quality, daytime symptoms
• Helps identify patterns and treatment response

SCREENING QUESTIONS FOR COMORBIDITIES:
• Depression: PHQ-2/PHQ-9
• Anxiety: GAD-2/GAD-7
• Sleep apnea: STOP-BANG questionnaire
• Restless legs: Do your legs bother you at night?

PHYSICAL EXAMINATION:
• Often normal
• Assess: BMI (sleep apnea risk), blood pressure, thyroid
• Look for signs of depression, substance use`,

    differentialDiagnosis: [
      'Short sleep (insufficient sleep syndrome) - Inadequate opportunity for sleep rather than inability',
      'Depression with insomnia - Screen for mood symptoms, anhedonia, early morning waking classic',
      'Anxiety disorders - Difficulty falling asleep, racing thoughts, may have daytime anxiety',
      'Obstructive sleep apnea - Snoring, witnessed apneas, daytime sleepiness, obesity, can cause maintenance insomnia',
      'Restless legs syndrome - Urge to move legs, worse at rest and evening, may delay sleep onset',
      'Circadian rhythm disorders - Delayed sleep phase (night owls), advanced sleep phase (elderly), shift work',
      'Substance-induced insomnia - Caffeine, stimulants, alcohol withdrawal, medications',
      'Chronic pain disorders - Pain interfering with sleep onset or maintenance',
      'Hyperthyroidism - Difficulty sleeping, anxiety, weight loss, tremor',
      'Periodic limb movement disorder - Repetitive leg movements during sleep, may cause awakenings'
    ],

    investigations: [
      {
        name: 'Sleep diary (2 weeks)',
        indication: 'All patients - Gold standard for assessment',
        availability: 'BASIC'
      },
      {
        name: 'Sleep questionnaires (ISI, PSQI)',
        indication: 'Quantify severity and monitor treatment response',
        availability: 'BASIC'
      },
      {
        name: 'Depression screening (PHQ-9)',
        indication: 'All patients - Depression commonly comorbid',
        availability: 'BASIC'
      },
      {
        name: 'Anxiety screening (GAD-7)',
        indication: 'All patients - Anxiety commonly comorbid',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid function tests',
        indication: 'If clinical suspicion of thyroid disorder',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'If restless legs suspected (check iron status)',
        availability: 'BASIC'
      },
      {
        name: 'Ferritin level',
        indication: 'Restless legs syndrome - Often low',
        availability: 'BASIC'
      },
      {
        name: 'Polysomnography (sleep study)',
        indication: 'Suspected sleep apnea, PLMD, or atypical presentation - NOT routine for insomnia',
        availability: 'ADVANCED'
      },
      {
        name: 'Actigraphy',
        indication: 'Objective measure of sleep-wake patterns if diary unreliable',
        availability: 'ADVANCED'
      }
    ],

    management: {
      immediate: [
        'Take detailed sleep history and 24-hour history',
        'Have patient complete sleep diary for 2 weeks',
        'Screen for depression, anxiety, and other sleep disorders',
        'Review medications for sleep-disrupting drugs',
        'Identify and address underlying medical conditions',
        'Educate patient about sleep hygiene (but this alone is usually insufficient)',
        'Discuss treatment options: CBT-I vs medications'
      ],

      pharmacological: [
        {
          drug: 'Short-acting benzodiazepines (Temazepam, Triazolam)',
          indication: 'Short-term use ONLY for acute/transient insomnia (max 2-4 weeks)',
          dose: 'Temazepam 10-20mg; Triazolam 0.125-0.25mg at bedtime',
          route: 'Oral',
          duration: 'Maximum 2-4 weeks - AVOID long-term use',
          contraindications: ['Sleep apnea', 'Respiratory insufficiency', 'Myasthenia gravis', 'Severe hepatic impairment', 'Pregnancy'],
          sideEffects: ['Daytime sedation', 'Cognitive impairment', 'Falls (especially elderly)', 'Dependence', 'Tolerance', 'Rebound insomnia on withdrawal'],
          alternatives: ['Z-drugs', 'Low-dose sedating antidepressants']
        },
        {
          drug: 'Z-drugs (Zolpidem, Zopiclone, Eszopiclone)',
          indication: 'Short-term treatment of insomnia; similar efficacy to benzodiazepines',
          dose: 'Zolpidem 5-10mg; Zopiclone 3.75-7.5mg; Eszopiclone 1-3mg at bedtime',
          route: 'Oral',
          duration: 'Short-term (2-4 weeks); intermittent use may be considered',
          contraindications: ['Sleep apnea', 'Respiratory insufficiency', 'Severe hepatic impairment'],
          sideEffects: ['Daytime sedation', 'Metallic taste (zopiclone)', 'Complex sleep behaviors (rare)', 'Dependence (less than benzodiazepines but still occurs)'],
          alternatives: ['Low-dose sedating antidepressants', 'Melatonin']
        },
        {
          drug: 'Melatonin',
          indication: 'Sleep-onset insomnia; circadian rhythm disorders; elderly (preferred over hypnotics)',
          dose: 'Immediate-release: 0.5-5mg 30-60 min before bed. Prolonged-release: 2mg',
          route: 'Oral',
          duration: 'Can be used longer-term; limited evidence for efficacy >4 weeks',
          contraindications: ['Autoimmune conditions (theoretical)'],
          sideEffects: ['Generally well tolerated', 'Headache', 'Dizziness', 'Daytime drowsiness'],
          alternatives: ['Sleep hygiene and CBT-I']
        },
        {
          drug: 'Low-dose Amitriptyline',
          indication: 'Chronic insomnia; especially with comorbid depression, pain, or headaches',
          dose: '10-50mg at bedtime (lower than antidepressant dose)',
          route: 'Oral',
          duration: 'Can be used longer-term; reassess periodically',
          contraindications: ['Recent MI', 'Arrhythmias', 'Urinary retention', 'Glaucoma'],
          sideEffects: ['Morning sedation', 'Dry mouth', 'Constipation', 'Weight gain', 'Urinary retention'],
          alternatives: ['Trazodone', 'Mirtazapine']
        },
        {
          drug: 'Trazodone',
          indication: 'Chronic insomnia; less anticholinergic than amitriptyline; comorbid depression',
          dose: '25-100mg at bedtime',
          route: 'Oral',
          duration: 'Can be used longer-term',
          contraindications: ['Recent MI', 'Priapism history'],
          sideEffects: ['Morning sedation', 'Orthostatic hypotension', 'Priapism (rare)'],
          alternatives: ['Amitriptyline', 'Mirtazapine']
        },
        {
          drug: 'Mirtazapine',
          indication: 'Insomnia with comorbid depression; especially if underweight/poor appetite',
          dose: '7.5-15mg at bedtime (lower doses more sedating)',
          route: 'Oral',
          duration: 'Can be used longer-term',
          contraindications: ['MAOIs'],
          sideEffects: ['Sedation', 'Weight gain', 'Increased appetite'],
          alternatives: ['Trazodone', 'Amitriptyline']
        },
        {
          drug: 'Antihistamines (Diphenhydramine, Promethazine)',
          indication: 'OTC option; limited evidence; tolerance develops quickly',
          dose: 'Diphenhydramine 25-50mg at bedtime',
          route: 'Oral',
          duration: 'Short-term only; tolerance develops within days',
          contraindications: ['Glaucoma', 'Urinary retention', 'Caution in elderly'],
          sideEffects: ['Anticholinergic effects', 'Morning sedation', 'Cognitive impairment', 'Tolerance'],
          alternatives: ['Melatonin', 'CBT-I']
        }
      ],

      nonPharmacological: [
        'CBT-I (Cognitive Behavioral Therapy for Insomnia) - FIRST-LINE treatment:',
        '  1. SLEEP RESTRICTION: Limit time in bed to actual sleep time, gradually increase',
        '  2. STIMULUS CONTROL: Bed only for sleep/sex; leave bed if awake >15-20 min',
        '  3. COGNITIVE THERAPY: Address dysfunctional beliefs about sleep',
        '  4. RELAXATION TECHNIQUES: Progressive muscle relaxation, deep breathing',
        '  5. SLEEP HYGIENE EDUCATION: Part of CBT-I but insufficient alone',
        '',
        'SLEEP HYGIENE MEASURES (Adjunctive, not standalone treatment):',
        '• Fixed wake time every day (including weekends) - most important',
        '• Avoid napping (or limit to <30 min before 3pm)',
        '• Avoid caffeine after noon',
        '• Avoid alcohol before bed (disrupts sleep architecture)',
        '• Regular exercise (but not within 4 hours of bedtime)',
        '• Comfortable sleep environment (cool, dark, quiet)',
        '• Avoid screens 1 hour before bed (blue light suppresses melatonin)',
        '• Wind-down routine before bed',
        '• Do not watch the clock',
        '',
        'STIMULUS CONTROL INSTRUCTIONS:',
        '• Go to bed only when sleepy',
        '• Use bed only for sleep and sex (no TV, phone, reading, worrying)',
        '• If unable to sleep after 15-20 minutes, get up and do quiet activity',
        '• Return to bed only when sleepy',
        '• Maintain fixed wake time regardless of sleep amount'
      ],

      surgical: [
        'No surgical treatment for primary insomnia',
        'If sleep apnea identified: May need CPAP or surgical options'
      ],

      referralCriteria: [
        'Suspected sleep apnea (for sleep study)',
        'Complex psychiatric comorbidity requiring specialist management',
        'Not responding to CBT-I and appropriate pharmacotherapy',
        'Need for CBT-I delivery (if available)',
        'Suspected other sleep disorders (narcolepsy, parasomnia)',
        'Long-term benzodiazepine dependence requiring supervised withdrawal'
      ],

      monitoring: [
        'Sleep diary: Track progress throughout treatment',
        'Sleep questionnaire scores (ISI, PSQI) at baseline and follow-up',
        'Monitor for medication side effects',
        'Assess for developing dependence if using hypnotics',
        'Screen for persistent depression/anxiety',
        'Review after 2-4 weeks of medication treatment'
      ],

      followUp: `ACUTE INSOMNIA:
• Often self-limiting
• If medication used, review in 2-4 weeks to taper

CHRONIC INSOMNIA:
• Review 2-4 weeks after initiating CBT-I or medication
• CBT-I: Full effect may take 4-8 weeks
• Medications: Plan for tapering after acute phase

MEDICATION TAPERING:
• Gradual reduction over weeks (not abrupt)
• Combine with CBT-I principles
• Expect some temporary worsening

LONG-TERM MANAGEMENT:
• Maintain good sleep habits
• Address relapses early
• Periodic reassessment`
    },

    redFlags: [
      '🔴 CRITICAL: Suicidal ideation with sleep disturbance - Urgent psychiatric assessment',
      '🟠 URGENT: Severe depression with insomnia - Mental health referral',
      '🟠 URGENT: Signs of sleep apnea (snoring, apneas, severe daytime sleepiness) - Sleep study',
      '🟡 WARNING: Long-term benzodiazepine use - Plan for gradual withdrawal',
      '🟡 WARNING: Insomnia with substance abuse - Address substance use',
      '🟡 WARNING: Parasomnias (sleepwalking, violent behavior in sleep) - Specialist referral',
      '🟡 WARNING: Excessive daytime sleepiness despite adequate sleep opportunity - Exclude other sleep disorders'
    ],

    complications: [
      'Depression and anxiety (bidirectional relationship)',
      'Impaired cognitive function and concentration',
      'Increased accident risk (driving, occupational)',
      'Cardiovascular disease',
      'Metabolic disorders (obesity, diabetes)',
      'Reduced immune function',
      'Hypnotic dependence and tolerance',
      'Rebound insomnia on medication withdrawal',
      'Falls and fractures (especially elderly on sedatives)',
      'Reduced quality of life and work performance'
    ],

    prognosis: `ACUTE INSOMNIA:
• Often resolves when precipitant addressed
• Good prognosis with appropriate management
• Risk of becoming chronic if poor sleep habits develop

CHRONIC INSOMNIA:
• CBT-I effective in 70-80% of patients
• Effects of CBT-I are sustained long-term (unlike medications)
• Medications provide short-term relief but do not address underlying causes
• Relapse possible; may need booster CBT-I sessions

FACTORS AFFECTING OUTCOME:
Better prognosis:
• Identifiable and treatable precipitant
• Engagement with CBT-I
• No comorbid psychiatric disorder
• No medication dependence

Worse prognosis:
• Chronic untreated insomnia
• Comorbid depression/anxiety (untreated)
• Long-term hypnotic use
• Persistent perpetuating factors`,

    prevention: `PRIMARY PREVENTION:
• Maintain regular sleep-wake schedule
• Good sleep hygiene practices
• Manage stress effectively
• Limit caffeine and alcohol
• Regular exercise
• Address medical conditions that affect sleep
• Avoid long-term hypnotic use

PREVENTING ACUTE → CHRONIC:
• Treat acute insomnia early
• Avoid prolonged use of hypnotics
• Address precipitating factors
• Maintain good sleep habits even after recovery

RELAPSE PREVENTION:
• Continue CBT-I principles long-term
• Early intervention if sleep difficulty recurs
• Maintain regular sleep schedule
• Manage stress proactively`,

    patientEducation: `UNDERSTANDING INSOMNIA:
Insomnia is very common and treatable. It's not just about not sleeping enough - it's about sleep difficulties that affect how you function during the day. The good news is that the most effective treatment doesn't involve medications.

WHAT CAUSES INSOMNIA?
Insomnia usually starts with a trigger (stress, illness, life change) but continues because of changes in behavior and thinking about sleep. Worrying about sleep and spending too long in bed often make it worse.

THE BEST TREATMENT - CBT-I:
Cognitive Behavioral Therapy for Insomnia (CBT-I) works better than sleeping pills in the long run. It includes:
• Changing behaviors that interfere with sleep
• Changing thoughts and worries about sleep
• Relaxation techniques

KEY SLEEP RULES:

1. WAKE UP AT THE SAME TIME EVERY DAY
   • This is the single most important rule
   • Yes, even on weekends
   • Even if you slept poorly

2. GO TO BED ONLY WHEN SLEEPY
   • Not just tired - actually sleepy (eyes heavy, yawning)
   • Don't go to bed just because it's "bedtime"

3. USE YOUR BED ONLY FOR SLEEP (AND SEX)
   • No TV, phone, tablet, or laptop in bed
   • No reading, eating, or working in bed
   • Train your brain: Bed = Sleep

4. IF YOU CAN'T SLEEP, GET UP
   • If you've been awake 15-20 minutes, get out of bed
   • Do something quiet and relaxing in dim light
   • Return to bed only when sleepy again

5. LIMIT TIME IN BED
   • Spending too much time in bed makes sleep worse
   • Better to feel sleepy and sleep well than lie awake for hours

ABOUT SLEEPING PILLS:
• They work short-term but don't fix the problem
• You can become dependent on them
• Side effects include daytime drowsiness, falls, memory problems
• Should be used for short periods only (2-4 weeks)
• Stopping suddenly can cause worse sleep temporarily

SLEEP HYGIENE TIPS:
• Avoid caffeine after noon (coffee, tea, cola, energy drinks)
• Avoid alcohol before bed (it disrupts sleep later in the night)
• Exercise regularly, but not close to bedtime
• Make your bedroom cool, dark, and quiet
• Put screens away 1 hour before bed
• Have a relaxing wind-down routine

WHAT TO EXPECT:
• Sleep restriction may feel hard at first - you'll be sleepier
• It gets better! Your sleep will become more efficient
• Improvements usually seen within 2-4 weeks
• The benefits last longer than with sleeping pills

WHEN TO GET HELP:
• Sleep problems lasting more than a few weeks
• Significant impact on your daily life
• Snoring with pauses in breathing (may be sleep apnea)
• Feeling very low or anxious along with poor sleep`,

    references: [
      'American Academy of Sleep Medicine Clinical Practice Guideline for Insomnia 2021',
      'European Sleep Research Society Guidelines on Insomnia',
      'NICE Guidelines: Insomnia',
      'Qaseem A, et al. ACP Clinical Guidelines: Management of Chronic Insomnia',
      'Sateia MJ, et al. AASM Clinical Practice Guideline on Pharmacologic Treatment of Chronic Insomnia',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 97. EPILEPSY / SEIZURES
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '97',
    name: 'Epilepsy and Seizure Disorders',
    slug: 'epilepsy',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'G40',
    resourceLevel: 'BASIC',
    isEmergency: true,

    overview: `Epilepsy is a chronic neurological disorder characterized by recurrent unprovoked seizures. A seizure is a transient occurrence of signs/symptoms due to abnormal excessive or synchronous neuronal activity in the brain. Epilepsy affects approximately 50 million people worldwide, with 80% living in low- and middle-income countries.

DEFINITIONS:
• SEIZURE: Single event of abnormal brain electrical activity
• EPILEPSY: ≥2 unprovoked seizures >24 hours apart, OR 1 seizure with high recurrence risk, OR epilepsy syndrome diagnosis
• STATUS EPILEPTICUS: Seizure lasting >5 minutes OR ≥2 seizures without recovery - MEDICAL EMERGENCY

🔴 KEY CLINICAL POINTS:
• STATUS EPILEPTICUS is a medical emergency - Treat immediately
• First-line AEDs: Sodium valproate (generalized), Carbamazepine (focal)
• Valproate is HIGHLY TERATOGENIC - Avoid in women of childbearing potential
• Monotherapy preferred - Start low, go slow
• 70% of patients can be seizure-free with appropriate treatment
• Driving and safety counseling essential
• Sudden unexpected death in epilepsy (SUDEP) - Discuss risk

AFRICAN CONTEXT:
• Higher prevalence (7-14 per 1000) vs developed countries (4-7 per 1000)
• "Treatment gap" - 75-90% untreated in some regions
• Common causes: Perinatal injury, CNS infections (meningitis, cerebral malaria, neurocysticercosis)
• Significant stigma - May be attributed to spiritual causes
• Limited access to neurologists and EEG
• Essential AEDs (phenobarbital, carbamazepine, valproate, phenytoin) should be available
• Phenobarbital: Effective, affordable, but sedating`,

    epidemiology: `GLOBAL BURDEN:
• ~50 million people with epilepsy worldwide
• 80% live in low- and middle-income countries
• Incidence: 50-70 per 100,000/year (higher in LMIC)
• Prevalence: 4-10 per 1,000
• Bimodal age distribution: Children and elderly

AFRICAN EPIDEMIOLOGY:
• Prevalence: 7-14 per 1,000 (higher than global average)
• Incidence: Up to 190 per 100,000/year in some regions
• "Treatment gap": 75-90% of people with epilepsy untreated
• Common etiologies in Africa:
  - Perinatal brain injury
  - CNS infections: Meningitis, cerebral malaria, neurocysticercosis
  - Traumatic brain injury
  - Stroke
  - HIV-related opportunistic infections
• Significant stigma and discrimination
• Traditional/spiritual explanations often sought

ETIOLOGY BY AGE:
Neonates/Infants:
• Perinatal hypoxia-ischemia
• Congenital malformations
• Metabolic disorders
• Infections (meningitis, encephalitis)

Children:
• Febrile seizures (may precede epilepsy)
• Genetic/idiopathic epilepsies
• CNS infections
• Traumatic brain injury

Adults:
• Traumatic brain injury
• CNS infections
• Stroke
• Brain tumors
• Alcohol withdrawal

Elderly:
• Cerebrovascular disease (most common)
• Neurodegenerative diseases
• Brain tumors
• Metabolic disorders`,

    clinicalPresentation: `SEIZURE CLASSIFICATION (ILAE 2017):

FOCAL (PARTIAL) SEIZURES - Start in one brain area:
1. Focal Aware (Simple partial) - Consciousness preserved
   • Motor: Jerking of one limb/face
   • Sensory: Tingling, visual/auditory phenomena
   • Autonomic: Sweating, palpitations, epigastric rising
   • Psychological: Déjà vu, fear, hallucinations

2. Focal Impaired Awareness (Complex partial) - Consciousness impaired
   • Often temporal lobe origin
   • Aura may precede
   • Automatisms: Lip smacking, chewing, fumbling
   • Post-ictal confusion

3. Focal to Bilateral Tonic-Clonic (Secondary generalization)
   • Starts focal, then spreads to generalized convulsion

GENERALIZED SEIZURES - Both hemispheres from onset:
1. Tonic-Clonic (Grand mal)
   • Sudden loss of consciousness
   • Tonic phase: Stiffening, cry, fall (10-20 seconds)
   • Clonic phase: Rhythmic jerking (30-60 seconds)
   • Post-ictal: Confusion, fatigue, headache, sleep
   • May have tongue bite, incontinence

2. Absence (Petit mal)
   • Brief staring spells (5-30 seconds)
   • Abrupt onset and offset
   • No post-ictal confusion
   • May have subtle automatisms
   • Common in children

3. Myoclonic
   • Brief, shock-like jerks
   • Often on awakening
   • Consciousness usually preserved

4. Tonic
   • Sudden stiffening without clonic phase

5. Atonic (Drop attacks)
   • Sudden loss of muscle tone
   • Falls, head drops
   • High injury risk

IMPORTANT FEATURES TO DOCUMENT:
• Witness account (patient often amnestic)
• Prodrome or aura
• Onset: Focal signs suggest focal onset
• Movements: Tonic, clonic, automatisms
• Duration
• Post-ictal state and duration
• Tongue bite (lateral = more specific for epileptic seizure)
• Incontinence
• Injury during episode`,

    diagnosis: `CLINICAL DIAGNOSIS:
Epilepsy diagnosis is primarily clinical, based on:
• Detailed history (witness account essential)
• Seizure semiology
• EEG findings (supportive, not required)
• Neuroimaging to identify etiology

KEY HISTORY:
From patient:
• Aura or warning symptoms
• Memory of event
• Post-ictal symptoms
• Triggers (sleep deprivation, alcohol, flashing lights)
• Previous episodes
• Birth and developmental history
• Head injury, meningitis, febrile seizures

From witness (CRITICAL):
• What were they doing before?
• How did it start? (focal onset vs generalized)
• What movements occurred?
• Eyes open or closed? Eye deviation?
• Duration of each phase
• Responsiveness during event
• How did it end?
• Post-ictal confusion/weakness?

EXAMINATION:
• Often normal between seizures
• May show: Todd's paresis (post-ictal focal weakness - suggests focal seizure)
• Look for: Tongue bite, injuries from fall
• Neurological exam for focal signs
• Skin exam (neurocutaneous syndromes)
• Cardiovascular exam (syncope mimic)

DIFFERENTIAL DIAGNOSIS KEY FEATURES:
Syncope:
• Trigger (standing, pain, emotion)
• Prodrome (lightheadedness, nausea, vision dimming)
• Brief duration (<1 minute)
• Rapid recovery
• Pallor during event
• May have brief convulsive movements

Epileptic seizure:
• Often no trigger
• Aura if present is stereotyped
• Longer duration
• Post-ictal confusion (prolonged)
• Cyanosis during event
• Lateral tongue bite
• Prolonged automatisms

INVESTIGATIONS APPROACH:
• First seizure: Investigate to find cause
• Recurrent seizures: Confirm epilepsy, classify, find cause`,

    differentialDiagnosis: [
      'Syncope - Trigger (standing, pain), prodrome, rapid recovery, brief convulsive movements possible',
      'Psychogenic non-epileptic seizures (PNES) - Variable presentation, prolonged, eyes closed, no post-ictal confusion, often psychiatric comorbidity',
      'Transient ischemic attack (TIA) - Negative symptoms (weakness, numbness), not rhythmic movements',
      'Hypoglycemia - Confusion, sweating, tremor, known diabetes, responds to glucose',
      'Cardiac arrhythmia - Palpitations, sudden collapse, rapid recovery',
      'Migraine with aura - Visual/sensory symptoms spread slowly, followed by headache',
      'Panic attacks - Fear, palpitations, hyperventilation, no loss of consciousness',
      'Sleep disorders (narcolepsy, parasomnias) - Sleep-related, different semiology',
      'Movement disorders (dystonia, tics) - No loss of awareness, different pattern',
      'Metabolic encephalopathy - Prolonged confusion, underlying metabolic cause'
    ],

    investigations: [
      {
        name: 'Blood glucose',
        indication: 'All patients - Exclude hypoglycemia as cause',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Baseline; infection workup; AED monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes (Na, K, Ca, Mg)',
        indication: 'All patients - Exclude metabolic cause',
        availability: 'BASIC'
      },
      {
        name: 'Renal and liver function',
        indication: 'Baseline before AEDs; monitoring',
        availability: 'BASIC'
      },
      {
        name: 'Malaria test',
        indication: 'Endemic areas - Cerebral malaria can cause seizures',
        availability: 'BASIC'
      },
      {
        name: 'HIV test',
        indication: 'Consider - CNS opportunistic infections can cause seizures',
        availability: 'BASIC'
      },
      {
        name: 'Lumbar puncture',
        indication: 'If meningitis/encephalitis suspected (fever, neck stiffness)',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: 'Differentiate from cardiac syncope; baseline before some AEDs',
        availability: 'BASIC'
      },
      {
        name: 'EEG (Electroencephalogram)',
        indication: 'Supports diagnosis, helps classify epilepsy syndrome - May be normal between seizures',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'CT head',
        indication: 'First seizure in adults; focal seizures; status epilepticus; trauma',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI brain',
        indication: 'Gold standard for structural cause; focal epilepsy; drug-resistant epilepsy',
        availability: 'ADVANCED'
      },
      {
        name: 'AED drug levels',
        indication: 'Poor control, suspected toxicity, adherence concerns, drug interactions',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '🚨 STATUS EPILEPTICUS - EMERGENCY PROTOCOL:',
        '  • Protect airway, give oxygen, IV access',
        '  • Check glucose - Give dextrose if low',
        '  • First-line: Benzodiazepine (Lorazepam 4mg IV or Diazepam 10mg IV/PR)',
        '  • If continues >5 min: Repeat benzodiazepine once',
        '  • If continues: Phenytoin 20mg/kg IV OR Levetiracetam 60mg/kg IV OR Valproate 40mg/kg IV',
        '  • If refractory: ICU, anesthetic agents',
        '',
        'DURING ACUTE SEIZURE (Non-status):',
        '  • Protect from injury (move objects away)',
        '  • Position on side (recovery position) after convulsion stops',
        '  • Do NOT restrain or put anything in mouth',
        '  • Time the seizure',
        '  • Most seizures stop within 2-3 minutes',
        '',
        'AFTER SEIZURE:',
        '  • Maintain airway, recovery position',
        '  • Monitor until fully conscious',
        '  • Check glucose and vitals',
        '  • Assess for injuries'
      ],

      pharmacological: [
        {
          drug: 'Sodium Valproate',
          indication: 'First-line for GENERALIZED epilepsy (including absence, myoclonic). Broad spectrum.',
          dose: 'Start 300mg BD (or 10-15mg/kg/day); increase by 200mg every 3 days; usual 1-2g/day',
          route: 'Oral (IV for status)',
          duration: 'Long-term; minimum 2 years seizure-free before considering withdrawal',
          contraindications: ['⚠️ WOMEN OF CHILDBEARING POTENTIAL (highly teratogenic)', 'Liver disease', 'Mitochondrial disorders', 'Porphyria'],
          sideEffects: ['Weight gain', 'Tremor', 'Hair loss', 'Hepatotoxicity', 'Pancreatitis', 'Teratogenicity (neural tube defects)'],
          alternatives: ['Levetiracetam (safer in women)', 'Lamotrigine']
        },
        {
          drug: 'Carbamazepine',
          indication: 'First-line for FOCAL epilepsy',
          dose: 'Start 100-200mg BD; increase gradually every 1-2 weeks; usual 400-1200mg/day',
          route: 'Oral',
          duration: 'Long-term',
          contraindications: ['AV block', 'History of bone marrow suppression', 'Porphyria', 'Concurrent MAOIs'],
          sideEffects: ['Drowsiness', 'Dizziness', 'Diplopia', 'Rash (can be severe - SJS)', 'Hyponatremia', 'Enzyme induction (drug interactions)'],
          alternatives: ['Lamotrigine', 'Levetiracetam', 'Oxcarbazepine']
        },
        {
          drug: 'Levetiracetam',
          indication: 'Broad spectrum; good tolerability; SAFE IN PREGNANCY; few drug interactions',
          dose: 'Start 250-500mg BD; increase every 2 weeks; usual 1000-3000mg/day',
          route: 'Oral (IV available)',
          duration: 'Long-term',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Behavioral changes (irritability, aggression)', 'Drowsiness', 'Dizziness', 'Generally well tolerated'],
          alternatives: ['Lamotrigine', 'Valproate (not in pregnancy)']
        },
        {
          drug: 'Lamotrigine',
          indication: 'Broad spectrum; good for focal and generalized; SAFER IN PREGNANCY than valproate',
          dose: 'Start 25mg OD; increase very slowly (risk of rash); usual 100-400mg/day',
          route: 'Oral',
          duration: 'Long-term',
          contraindications: ['Known hypersensitivity'],
          sideEffects: ['Rash (can be severe SJS - slow titration reduces risk)', 'Headache', 'Dizziness', 'Diplopia'],
          alternatives: ['Levetiracetam', 'Carbamazepine (focal)']
        },
        {
          drug: 'Phenytoin',
          indication: 'Focal and generalized tonic-clonic; IV for status epilepticus',
          dose: 'Oral: 200-400mg/day (narrow therapeutic index - monitor levels). Status: 20mg/kg IV load',
          route: 'Oral or IV',
          duration: 'Long-term',
          contraindications: ['Sinus bradycardia', 'Heart block', 'Porphyria'],
          sideEffects: ['Gum hyperplasia', 'Hirsutism', 'Coarsening of facial features', 'Ataxia', 'Nystagmus', 'Osteoporosis', 'Enzyme induction'],
          alternatives: ['Carbamazepine', 'Lamotrigine']
        },
        {
          drug: 'Phenobarbital',
          indication: 'Affordable option; widely available in Africa; all seizure types',
          dose: 'Start 60mg at night; usual 60-180mg/day',
          route: 'Oral (IM/IV for status)',
          duration: 'Long-term',
          contraindications: ['Porphyria', 'Severe respiratory depression'],
          sideEffects: ['Sedation (common)', 'Cognitive impairment', 'Behavioral changes', 'Dependence', 'Enzyme induction'],
          alternatives: ['Carbamazepine', 'Valproate (if affordable)']
        },
        {
          drug: 'Diazepam / Lorazepam',
          indication: 'ACUTE SEIZURE / STATUS EPILEPTICUS - First-line emergency treatment',
          dose: 'Diazepam 10mg IV (or 10-20mg PR). Lorazepam 4mg IV. May repeat once.',
          route: 'IV (preferred) or PR/IM',
          duration: 'Acute use only - NOT for maintenance',
          contraindications: ['Respiratory depression', 'Acute narrow-angle glaucoma'],
          sideEffects: ['Respiratory depression', 'Sedation', 'Hypotension'],
          alternatives: ['Midazolam buccal/IM']
        },
        {
          drug: 'Clobazam',
          indication: 'Adjunctive therapy; intermittent use (catamenial epilepsy, cluster seizures)',
          dose: '10-20mg/day, usually at night',
          route: 'Oral',
          duration: 'Adjunctive; tolerance can develop',
          contraindications: ['Myasthenia gravis', 'Severe respiratory insufficiency'],
          sideEffects: ['Sedation', 'Tolerance', 'Dependence'],
          alternatives: ['Increase primary AED']
        }
      ],

      nonPharmacological: [
        'SAFETY COUNSELING (Essential):',
        '  • Driving regulations: Usually cannot drive until seizure-free period (varies by jurisdiction)',
        '  • Water safety: No unsupervised swimming/bathing; showers preferred',
        '  • Heights and machinery: Avoid unprotected heights, dangerous machinery',
        '  • Fire safety: Guards on fires/heaters',
        '  • Inform family/colleagues about first aid',
        '',
        'LIFESTYLE MODIFICATIONS:',
        '  • Avoid sleep deprivation (major trigger)',
        '  • Limit alcohol (lowers seizure threshold)',
        '  • Medication adherence - Take AEDs regularly, do not miss doses',
        '  • Avoid known individual triggers',
        '  • Photosensitive epilepsy: Avoid flashing lights, use screen filters',
        '',
        'WOMEN OF CHILDBEARING POTENTIAL:',
        '  • Preconception counseling essential',
        '  • Folic acid 5mg daily (reduces neural tube defect risk)',
        '  • AVOID valproate if possible (teratogenic)',
        '  • Levetiracetam and lamotrigine preferred in pregnancy',
        '  • Plan pregnancy - Optimize AED before conception',
        '',
        'SURGICAL OPTIONS (Refractory epilepsy):',
        '  • Resective surgery for focal epilepsy with identifiable lesion',
        '  • Vagus nerve stimulation',
        '  • Refer to epilepsy surgery center if drug-resistant',
        '',
        'KETOGENIC DIET:',
        '  • High-fat, low-carbohydrate diet',
        '  • Effective in some drug-resistant epilepsies, especially in children'
      ],

      surgical: [
        'Epilepsy surgery for drug-resistant focal epilepsy',
        'Vagus nerve stimulator implantation',
        'Corpus callosotomy for drop attacks',
        'Hemispherectomy for hemispheric syndromes'
      ],

      referralCriteria: [
        'First seizure - For diagnosis confirmation and investigation',
        'Diagnostic uncertainty (epilepsy vs syncope vs PNES)',
        'Drug-resistant epilepsy (failed 2 appropriate AEDs)',
        'Consideration for epilepsy surgery',
        'Women planning pregnancy - AED optimization',
        'Status epilepticus not responding to first-line treatment',
        'Suspected underlying structural lesion',
        'Epilepsy in children - Pediatric neurology'
      ],

      monitoring: [
        'Seizure frequency (patient diary)',
        'Medication adherence',
        'AED side effects',
        'Drug levels: Phenytoin (narrow therapeutic index), carbamazepine, phenobarbital, valproate',
        'FBC: Carbamazepine (blood dyscrasias), valproate',
        'LFTs: Valproate, carbamazepine',
        'Sodium: Carbamazepine, oxcarbazepine (hyponatremia)',
        'Bone health: Long-term enzyme-inducing AEDs (osteoporosis)',
        'Review every 6-12 months when stable'
      ],

      followUp: `AFTER STARTING AED:
• Review in 2-4 weeks to assess tolerance
• Review in 3 months to assess efficacy
• Aim for seizure freedom with minimal side effects

DOSE ADJUSTMENTS:
• If seizures continue on adequate dose: Increase to maximum tolerated
• If still uncontrolled on max dose of first AED: Add or switch to second AED
• Monotherapy preferred when possible

DRUG-RESISTANT EPILEPSY:
• Defined as: Failed 2 appropriate AEDs at adequate doses
• Refer to specialist/epilepsy center
• Consider surgery evaluation if focal epilepsy

STOPPING AEDS:
• Consider after 2+ years seizure-free
• Higher recurrence risk: Focal epilepsy, abnormal EEG, known structural cause
• Gradual taper over 2-6 months
• Advise not to drive during withdrawal and for period after
• 30-40% will have recurrence after withdrawal`
    },

    redFlags: [
      '🔴 CRITICAL: STATUS EPILEPTICUS (seizure >5 min or repeated without recovery) - EMERGENCY',
      '🔴 CRITICAL: First seizure with fever and neck stiffness - Meningitis/encephalitis',
      '🔴 CRITICAL: Seizure with severe headache - Consider SAH or mass lesion',
      '🔴 CRITICAL: Post-ictal focal weakness persisting >24 hours - Investigate for stroke/lesion',
      '🔴 CRITICAL: Seizure in pregnancy - Eclampsia until proven otherwise',
      '🟠 URGENT: First seizure in adult - Needs investigation (tumor, infection, stroke)',
      '🟠 URGENT: Change in seizure pattern in known epilepsy - May indicate new pathology',
      '🟠 URGENT: Signs of AED toxicity (ataxia, nystagmus, severe drowsiness)',
      '🟡 WARNING: Frequent seizures despite treatment - Reassess diagnosis and treatment',
      '🟡 WARNING: Woman with epilepsy planning pregnancy - Preconception counseling urgent'
    ],

    complications: [
      'Status epilepticus - Medical emergency, can cause brain damage, death',
      'Sudden Unexpected Death in Epilepsy (SUDEP) - Higher risk with uncontrolled seizures',
      'Physical injury during seizures (falls, burns, drowning)',
      'Aspiration pneumonia',
      'Cognitive impairment (seizures and some AEDs)',
      'Psychiatric comorbidity (depression, anxiety common)',
      'Social stigma and discrimination',
      'Medication side effects',
      'Teratogenicity (valproate especially)',
      'Driving and employment restrictions',
      'Osteoporosis (enzyme-inducing AEDs)'
    ],

    prognosis: `WITH TREATMENT:
• 70% of patients can become seizure-free with appropriate AEDs
• 50% achieve remission with first AED
• Another 13% with second AED
• Drug-resistant epilepsy: ~30% (failed 2+ AEDs)
• Epilepsy surgery can cure 60-70% of suitable focal epilepsy

SUDEP (Sudden Unexpected Death in Epilepsy):
• Risk: ~1 per 1000 patient-years
• Higher risk with uncontrolled generalized tonic-clonic seizures
• Seizure control reduces risk

FACTORS AFFECTING PROGNOSIS:
Better prognosis:
• Idiopathic/genetic epilepsy
• Normal neurological exam and imaging
• Good response to first AED
• Seizure-free for >2 years

Worse prognosis:
• Symptomatic epilepsy (structural cause)
• Abnormal neurological exam
• Drug-resistant epilepsy
• Frequent generalized tonic-clonic seizures`,

    prevention: `PRIMARY PREVENTION:
• Improve perinatal care (reduce birth asphyxia)
• Prevent head injuries (helmets, seatbelts)
• Prevent and treat CNS infections (vaccination, early treatment)
• Cerebral malaria prevention and treatment
• Neurocysticercosis prevention (food hygiene)
• Stroke prevention (control hypertension, diabetes)

SECONDARY PREVENTION (Prevent seizures):
• AED adherence - Most important factor
• Avoid triggers: Sleep deprivation, alcohol, missed medications
• Photosensitive epilepsy: Avoid triggers, sunglasses
• Regular follow-up

PREVENT COMPLICATIONS:
• Seizure control reduces SUDEP risk
• Safety modifications at home
• Supervision for high-risk activities
• Bone health monitoring on long-term AEDs`,

    patientEducation: `UNDERSTANDING EPILEPSY:
Epilepsy is a medical condition where the brain has a tendency to have seizures. Seizures happen when there is sudden abnormal electrical activity in the brain. Epilepsy is very common and can be controlled with medication in most people.

Epilepsy is:
• A medical condition, NOT a mental illness
• NOT contagious
• NOT caused by evil spirits or curses
• TREATABLE - Most people can lead normal lives

YOUR MEDICATION:
• Take your medication EVERY DAY at the same times
• Do NOT stop or change dose without consulting your doctor
• Missing doses is the most common cause of breakthrough seizures
• Carry spare medication when traveling
• Get refills BEFORE you run out

IF YOU MISS A DOSE:
• Take it as soon as you remember
• If close to next dose, skip the missed one (don't double up)
• Tell your doctor if you frequently forget

COMMON TRIGGERS TO AVOID:
• Sleep deprivation - Get regular, adequate sleep
• Alcohol - Can trigger seizures and interacts with medication
• Missing medication doses
• Stress (though this is harder to avoid)
• Flashing lights (if photosensitive)

SAFETY MEASURES:
🚿 Bathing/Swimming:
• Showers are safer than baths
• Leave bathroom door unlocked
• Never swim alone - always with someone who knows about your epilepsy

🔥 Kitchen/Fire safety:
• Use back burners on stove
• Use microwave when possible
• Have guards on fires and heaters

🚗 Driving:
• You cannot drive until seizure-free for a specified period (check local regulations)
• Tell your doctor if you drive - they can advise

⚠️ Heights and machinery:
• Avoid unprotected heights
• Be cautious with dangerous machinery

WHAT TO TELL FAMILY AND FRIENDS - First Aid for Seizures:
✓ Stay calm
✓ Time the seizure
✓ Protect from injury (move dangerous objects away)
✓ Put something soft under head
✓ Turn on side after jerking stops (recovery position)
✓ Stay with person until fully recovered
✓ Reassure them as they wake up

✗ Do NOT restrain the person
✗ Do NOT put anything in their mouth
✗ Do NOT give water or medication until fully awake

📞 Call ambulance if:
• Seizure lasts more than 5 minutes
• Repeated seizures without waking
• Person is injured or doesn't breathe
• First seizure
• Seizure in water
• You're unsure or worried

FOR WOMEN:
• Tell your doctor if you're planning pregnancy - BEFORE getting pregnant
• Some epilepsy medications can harm the baby
• Take folic acid 5mg daily if you might become pregnant
• Contraception: Some medications reduce pill effectiveness - discuss with doctor
• It IS possible to have healthy pregnancies with epilepsy - just plan ahead

LIVING WITH EPILEPSY:
• You can live a full, active life
• Tell your employer only if relevant to safety
• Educate friends and family about first aid
• Join a support group if available
• Never be ashamed - epilepsy is just a medical condition`,

    references: [
      'ILAE Classification of Seizures and Epilepsies 2017',
      'NICE Guidelines: Epilepsies - diagnosis and management 2022',
      'WHO Essential Medicines List 2023 - Antiepileptic drugs',
      'Trinka E, et al. ILAE Definition of Status Epilepticus 2015',
      'Scottish Intercollegiate Guidelines Network: Diagnosis and management of epilepsy',
      'Epilepsy Foundation Guidelines for Treatment'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 98. STROKE / CEREBROVASCULAR ACCIDENT (CVA)
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '98',
    name: 'Stroke (Cerebrovascular Accident)',
    slug: 'stroke',
    specialty: 'Neurology',
    bodySystem: 'Neurological',
    icdCode: 'I63',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Stroke is a medical emergency caused by disruption of blood supply to the brain, resulting in rapid loss of brain function. It is the second leading cause of death globally and a major cause of long-term disability. "Time is Brain" - Every minute of untreated stroke, ~1.9 million neurons are lost.

TYPES OF STROKE:
• ISCHEMIC STROKE (85%): Blockage of blood vessel by clot
  - Thrombotic: Clot forms in brain artery (often atherosclerosis)
  - Embolic: Clot travels from elsewhere (heart, carotid)
• HEMORRHAGIC STROKE (15%): Bleeding into brain tissue
  - Intracerebral hemorrhage (ICH): Bleeding within brain
  - Subarachnoid hemorrhage (SAH): Bleeding around brain

🔴 KEY CLINICAL POINTS:
• STROKE IS A MEDICAL EMERGENCY - "Time is Brain"
• Use FAST: Face drooping, Arm weakness, Speech difficulty, Time to call emergency
• CT HEAD URGENTLY - Differentiate ischemic vs hemorrhagic (determines treatment)
• THROMBOLYSIS (tPA): Within 4.5 hours of symptom onset for eligible ischemic stroke
• THROMBECTOMY: Within 6-24 hours for large vessel occlusion
• DO NOT give thrombolysis for hemorrhagic stroke (will worsen bleeding!)
• Secondary prevention is critical to prevent recurrence

AFRICAN CONTEXT:
• Rising burden due to epidemiological transition
• Hypertension is the leading modifiable risk factor (often uncontrolled)
• Younger age of onset compared to developed countries
• Limited access to CT, thrombolysis, and stroke units
• Late presentation common (reduces thrombolysis eligibility)
• Higher proportion of hemorrhagic stroke vs developed countries
• Rheumatic heart disease remains a cause of embolic stroke`,

    epidemiology: `GLOBAL BURDEN:
• Second leading cause of death worldwide (11% of deaths)
• Leading cause of acquired disability in adults
• 15 million strokes per year globally
• 5 million deaths, 5 million left permanently disabled
• Lifetime risk: 1 in 4 adults

STROKE TYPES:
• Ischemic: 85%
• Hemorrhagic: 15% (ICH 10%, SAH 5%)

AFRICAN EPIDEMIOLOGY:
• Stroke incidence increasing due to epidemiological transition
• Age-standardized mortality higher than developed regions
• Mean age of stroke: 10-15 years younger than in Western populations
• Higher proportion of hemorrhagic stroke (up to 30-40% in some studies)
• Hypertension prevalence: 30-45% (often undiagnosed/uncontrolled)
• Limited stroke services: Only 1-2% receive thrombolysis
• Case fatality: 30-40% at 30 days (higher than developed countries)

RISK FACTORS:
Non-modifiable:
• Age (risk doubles each decade after 55)
• Male sex (slightly higher risk)
• Family history/genetics
• Prior stroke or TIA

Modifiable (Most important in Africa):
• HYPERTENSION - Single most important risk factor (contributes to 50%+ of strokes)
• Diabetes mellitus
• Atrial fibrillation
• Smoking
• Dyslipidemia
• Obesity
• Physical inactivity
• Excessive alcohol
• Carotid stenosis
• Sickle cell disease (common in Africa)
• Rheumatic heart disease
• HIV infection (increases stroke risk)`,

    clinicalPresentation: `RECOGNITION - FAST MNEMONIC:
F - FACE: Facial drooping (ask to smile)
A - ARM: Arm weakness (ask to raise both arms)
S - SPEECH: Speech difficulty (slurred, word-finding problems)
T - TIME: Time to call emergency services immediately

TYPICAL STROKE SYNDROMES:

ANTERIOR CIRCULATION (Carotid territory):
Middle Cerebral Artery (Most common):
• Contralateral hemiparesis (face and arm > leg)
• Contralateral hemisensory loss
• Aphasia if dominant hemisphere (usually left)
• Hemineglect if non-dominant hemisphere
• Homonymous hemianopia
• Gaze deviation toward side of lesion

Anterior Cerebral Artery:
• Contralateral leg weakness > arm/face
• Personality changes (frontal)
• Urinary incontinence

POSTERIOR CIRCULATION (Vertebrobasilar):
• Vertigo, ataxia, nystagmus
• Diplopia, visual field defects
• Dysarthria, dysphagia
• Crossed signs (ipsilateral cranial nerve, contralateral limb weakness)
• Decreased consciousness (basilar artery)

LACUNAR STROKES (Small vessel disease):
• Pure motor hemiparesis
• Pure sensory stroke
• Ataxic hemiparesis
• Dysarthria-clumsy hand syndrome
• No cortical signs (no aphasia, neglect, visual field defect)

HEMORRHAGIC STROKE FEATURES:
• May have more severe headache at onset
• Rapid deterioration of consciousness
• Vomiting
• Very high blood pressure
• Cannot reliably distinguish from ischemic clinically - NEED CT

SUBARACHNOID HEMORRHAGE:
• "Thunderclap" headache - Worst headache of life, sudden onset
• Neck stiffness (meningism)
• Photophobia
• May have brief loss of consciousness
• Often younger patients

TIA (Transient Ischemic Attack):
• Stroke symptoms resolving within 24 hours (usually <1 hour)
• HIGH RISK of subsequent stroke (10-15% within 90 days)
• Urgent investigation and secondary prevention needed`,

    diagnosis: `DIAGNOSIS IS A MEDICAL EMERGENCY:
• Clinical recognition of stroke symptoms
• Urgent CT head to differentiate ischemic vs hemorrhagic
• Time of symptom onset is critical (thrombolysis window)

IMMEDIATE ASSESSMENT:
1. ABCs - Airway, Breathing, Circulation
2. Time of symptom onset (or when last seen well)
3. Focused history: Symptoms, risk factors, medications (anticoagulants!)
4. Neurological examination - Document deficits
5. Blood glucose (hypoglycemia can mimic stroke)
6. NIHSS score (National Institutes of Health Stroke Scale)

KEY HISTORY:
• Exact time symptoms started (or last seen normal)
• Nature and progression of symptoms
• Previous stroke or TIA
• Risk factors: Hypertension, diabetes, AF, smoking
• Current medications (anticoagulants, antiplatelets)
• Recent surgery or trauma (contraindication to thrombolysis)
• Seizure at onset (may be Todd's paresis, not stroke)

EXAMINATION:
• Vital signs: BP, HR, rhythm, O2 saturation
• Cardiovascular: Irregular pulse (AF), murmurs, carotid bruits
• Neurological: GCS, cranial nerves, motor, sensory, speech, coordination
• NIHSS score if trained

NIHSS (National Institutes of Health Stroke Scale):
• Standardized assessment of stroke severity
• Scores 0-42 (higher = more severe)
• 0-4: Minor stroke
• 5-15: Moderate stroke
• 16-20: Moderate-severe stroke
• >20: Severe stroke

CT HEAD FINDINGS:
Ischemic stroke:
• May be NORMAL in first 6-12 hours
• Early signs: Loss of grey-white differentiation, insular ribbon sign, hyperdense artery
• Later: Hypodense area corresponding to vascular territory

Hemorrhagic stroke:
• Hyperdense (bright) area = acute blood
• Mass effect, midline shift in large hemorrhages

CT ANGIOGRAPHY (CTA):
• Identify large vessel occlusion (for thrombectomy consideration)
• Assess carotid and intracranial vessels`,

    differentialDiagnosis: [
      'Hypoglycemia - Check glucose immediately; rapid recovery with glucose',
      'Seizure with Todd paralysis - Post-ictal weakness; history of seizure; resolves',
      'Migraine with aura - Gradual onset, spreading symptoms, history of migraine, headache follows',
      'Intracranial mass/tumor - More gradual onset, may have papilledema',
      'Subdural hematoma - History of trauma (may be minor), gradual or fluctuating',
      'Brain abscess - Fever, risk factors (endocarditis, immunocompromise)',
      'Encephalitis - Fever, altered consciousness, may have seizures',
      'Hypertensive encephalopathy - Severely elevated BP, confusion, may have seizures',
      'Wernicke encephalopathy - Confusion, ataxia, ophthalmoplegia; alcohol history',
      'Functional/Conversion disorder - Inconsistent exam, psychiatric history'
    ],

    investigations: [
      {
        name: 'CT Head (Non-contrast)',
        indication: 'IMMEDIATE - All suspected stroke; differentiate ischemic vs hemorrhagic',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood glucose',
        indication: 'IMMEDIATE - Hypoglycemia can mimic stroke',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Baseline; check platelets before thrombolysis',
        availability: 'BASIC'
      },
      {
        name: 'Coagulation studies (PT/INR, APTT)',
        indication: 'Before thrombolysis; patients on anticoagulants',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes, renal function',
        indication: 'Baseline; exclude metabolic causes',
        availability: 'BASIC'
      },
      {
        name: 'ECG',
        indication: 'All patients - Detect atrial fibrillation, MI',
        availability: 'BASIC'
      },
      {
        name: 'Troponin',
        indication: 'Concurrent MI; neurogenic cardiac injury',
        availability: 'BASIC'
      },
      {
        name: 'CT Angiography (CTA)',
        indication: 'Identify large vessel occlusion for thrombectomy',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'MRI Brain',
        indication: 'Better sensitivity for early/small infarcts; posterior fossa strokes',
        availability: 'ADVANCED'
      },
      {
        name: 'Carotid Doppler ultrasound',
        indication: 'Carotid stenosis assessment after anterior circulation stroke',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Echocardiogram',
        indication: 'Identify cardiac source of embolism (AF, valvular disease, PFO)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Lipid profile',
        indication: 'Risk factor assessment; guide statin therapy',
        availability: 'BASIC'
      },
      {
        name: 'HbA1c',
        indication: 'Screen for diabetes',
        availability: 'BASIC'
      },
      {
        name: 'Sickle cell screen',
        indication: 'Young patients in endemic areas; stroke in children',
        availability: 'BASIC'
      }
    ],

    management: {
      immediate: [
        '🚨 STROKE IS A MEDICAL EMERGENCY - Act immediately',
        'ABCs: Secure airway, breathing, circulation',
        'Establish IV access, continuous monitoring',
        'Check blood glucose - Treat hypoglycemia immediately',
        'Urgent CT head - MUST differentiate ischemic vs hemorrhagic',
        'Document time of symptom onset (or last known well)',
        'NPO (nil by mouth) until swallow assessment',
        'Head of bed 0-30 degrees initially',
        '',
        '🔴 ISCHEMIC STROKE - THROMBOLYSIS ASSESSMENT:',
        'If within 4.5 hours of symptom onset and no contraindications:',
        '  • IV Alteplase (tPA) 0.9mg/kg (max 90mg)',
        '  • 10% as bolus, 90% over 60 minutes',
        '  • Strict BP control (<185/110 before and during)',
        '  • Monitor for hemorrhagic transformation',
        '',
        '🔴 LARGE VESSEL OCCLUSION - THROMBECTOMY:',
        'If large vessel occlusion on CTA, within 6-24 hours:',
        '  • Transfer to thrombectomy-capable center',
        '',
        '🔴 HEMORRHAGIC STROKE:',
        '  • DO NOT give thrombolysis',
        '  • Urgent BP control (target <140 systolic)',
        '  • Reverse anticoagulation if applicable',
        '  • Neurosurgical consultation for large hemorrhages'
      ],

      pharmacological: [
        {
          drug: 'Alteplase (tPA) - Thrombolysis',
          indication: 'ISCHEMIC stroke within 4.5 hours, no contraindications',
          dose: '0.9mg/kg IV (maximum 90mg); 10% bolus, 90% infusion over 60 minutes',
          route: 'IV',
          duration: 'Single treatment',
          contraindications: ['Hemorrhagic stroke', 'Recent major surgery/trauma', 'Active bleeding', 'INR >1.7', 'Platelets <100,000', 'BP >185/110 despite treatment', 'Previous ICH', 'Stroke/head trauma in last 3 months'],
          sideEffects: ['Intracranial hemorrhage (6%)', 'Systemic bleeding', 'Angioedema'],
          alternatives: ['Tenecteplase (emerging alternative)', 'Mechanical thrombectomy']
        },
        {
          drug: 'Aspirin',
          indication: 'All ischemic strokes (after hemorrhage excluded); start within 24-48 hours',
          dose: '300mg loading dose, then 75-100mg daily long-term',
          route: 'Oral (or rectal if swallowing unsafe)',
          duration: 'Long-term secondary prevention',
          contraindications: ['Active bleeding', 'Aspirin allergy', 'Within 24 hours of thrombolysis'],
          sideEffects: ['GI bleeding', 'Bruising'],
          alternatives: ['Clopidogrel if aspirin-intolerant']
        },
        {
          drug: 'Clopidogrel',
          indication: 'Secondary prevention; dual antiplatelet with aspirin for 21 days after minor stroke/TIA',
          dose: '300mg loading, then 75mg daily',
          route: 'Oral',
          duration: 'Long-term (or 21 days if dual with aspirin)',
          contraindications: ['Active bleeding'],
          sideEffects: ['Bleeding', 'Rash', 'GI upset'],
          alternatives: ['Aspirin alone', 'Dipyridamole + aspirin']
        },
        {
          drug: 'Anticoagulation (Warfarin, DOACs)',
          indication: 'Cardioembolic stroke (AF, mechanical valve); NOT in acute phase',
          dose: 'Warfarin: Titrate to INR 2-3. DOACs: Apixaban 5mg BD, Rivaroxaban 20mg OD, Dabigatran 150mg BD',
          route: 'Oral',
          duration: 'Long-term (lifelong for AF)',
          contraindications: ['Active bleeding', 'Recent hemorrhagic stroke', 'Severe thrombocytopenia'],
          sideEffects: ['Bleeding', 'Warfarin: Requires INR monitoring'],
          alternatives: ['Left atrial appendage closure if anticoagulation contraindicated']
        },
        {
          drug: 'Antihypertensive therapy - Acute phase',
          indication: 'BP control in acute stroke (targets vary by stroke type and thrombolysis)',
          dose: 'IV Labetalol 10-20mg boluses; Nicardipine infusion; Hydralazine',
          route: 'IV in acute phase',
          duration: 'Until stable; transition to oral',
          contraindications: ['Labetalol: Asthma, heart block'],
          sideEffects: ['Hypotension (avoid aggressive lowering in ischemic stroke)'],
          alternatives: ['Various IV antihypertensives']
        },
        {
          drug: 'Statin (Atorvastatin)',
          indication: 'All ischemic stroke/TIA - High intensity statin',
          dose: 'Atorvastatin 40-80mg daily',
          route: 'Oral',
          duration: 'Long-term',
          contraindications: ['Active liver disease', 'Pregnancy'],
          sideEffects: ['Myalgia', 'Elevated LFTs', 'Myopathy (rare)'],
          alternatives: ['Rosuvastatin']
        },
        {
          drug: 'Antihypertensive therapy - Secondary prevention',
          indication: 'All stroke patients with hypertension (most important secondary prevention)',
          dose: 'Target BP <130/80 (after acute phase); ACE inhibitor + thiazide often used',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['As per specific agent'],
          sideEffects: ['As per specific agent'],
          alternatives: ['Multiple classes available']
        },
        {
          drug: 'Enoxaparin / Heparin (VTE prophylaxis)',
          indication: 'DVT prophylaxis in immobile stroke patients',
          dose: 'Enoxaparin 40mg SC daily',
          route: 'Subcutaneous',
          duration: 'Until mobile',
          contraindications: ['Hemorrhagic stroke (use compression stockings initially)', 'Active bleeding'],
          sideEffects: ['Bleeding', 'Heparin-induced thrombocytopenia'],
          alternatives: ['Intermittent pneumatic compression']
        }
      ],

      nonPharmacological: [
        'STROKE UNIT CARE (Proven mortality benefit):',
        '  • Specialized multidisciplinary team',
        '  • Continuous monitoring',
        '  • Early mobilization',
        '  • Structured rehabilitation',
        '',
        'SWALLOW ASSESSMENT:',
        '  • Before any oral intake',
        '  • Formal swallow assessment by trained staff',
        '  • NGT if unsafe swallow',
        '',
        'EARLY REHABILITATION:',
        '  • Physiotherapy - Mobility, strength, balance',
        '  • Occupational therapy - ADLs, upper limb function',
        '  • Speech and language therapy - Communication, swallowing',
        '  • Start within 24-48 hours if medically stable',
        '',
        'POSITIONING AND PRESSURE CARE:',
        '  • Regular repositioning',
        '  • Pressure-relieving mattress',
        '  • Assess skin integrity',
        '',
        'DVT PROPHYLAXIS:',
        '  • Early mobilization',
        '  • Compression stockings (if not contraindicated)',
        '  • LMWH for ischemic stroke (delayed in hemorrhagic)',
        '',
        'NUTRITION:',
        '  • Nutritional assessment',
        '  • NGT feeding if prolonged dysphagia',
        '  • PEG if dysphagia persists >2-4 weeks',
        '',
        'BLADDER AND BOWEL CARE:',
        '  • Avoid indwelling catheter if possible',
        '  • Bowel regimen to prevent constipation'
      ],

      surgical: [
        'Mechanical thrombectomy for large vessel occlusion (within 6-24 hours)',
        'Decompressive craniectomy for malignant MCA infarction with brain swelling',
        'Carotid endarterectomy or stenting for significant carotid stenosis',
        'Surgical evacuation of cerebellar hemorrhage with brainstem compression',
        'Aneurysm clipping or coiling for SAH'
      ],

      referralCriteria: [
        'ALL suspected strokes - Urgent hospital admission',
        'Within thrombolysis window - Urgent to stroke-capable facility',
        'Large vessel occlusion - Transfer to thrombectomy center',
        'Hemorrhagic stroke - Neurosurgical consultation',
        'Carotid stenosis >50% symptomatic - Vascular surgery',
        'Young stroke (<45 years) - Specialist investigation',
        'Rehabilitation needs - Stroke rehabilitation unit'
      ],

      monitoring: [
        'Neurological observations (GCS, pupils, limb power) - Hourly initially',
        'Vital signs - Continuous monitoring initially',
        'Blood glucose - Maintain 4-10 mmol/L',
        'Oxygen saturation - Maintain >94%',
        'Post-thrombolysis: Strict neuro obs, BP monitoring, watch for hemorrhage',
        'Daily swallow assessment until safe',
        'DVT risk assessment',
        'Nutrition and hydration status'
      ],

      followUp: `ACUTE PHASE (Hospital):
• Daily review by stroke team
• Rehabilitation assessments
• Secondary prevention initiated before discharge
• Patient and family education

DISCHARGE PLANNING:
• Comprehensive discharge summary
• Medication reconciliation
• Rehabilitation plan (inpatient, outpatient, or home-based)
• Follow-up appointments
• Driving advice
• Return to work considerations

OUTPATIENT FOLLOW-UP:
• 4-6 weeks post-discharge: Review recovery, medications, risk factors
• Ongoing rehabilitation as needed
• Regular BP monitoring
• Annual review: Risk factor control, medication adherence

SECONDARY PREVENTION TARGETS:
• BP <130/80 mmHg
• LDL <1.8 mmol/L (or >50% reduction)
• HbA1c <7% if diabetic
• Smoking cessation
• Regular physical activity
• Healthy diet (Mediterranean or similar)`
    },

    redFlags: [
      '🔴 CRITICAL: Any stroke symptoms - TIME IS BRAIN, immediate emergency response',
      '🔴 CRITICAL: Deteriorating consciousness - May indicate hemorrhage or brain swelling',
      '🔴 CRITICAL: "Thunderclap" headache - Subarachnoid hemorrhage until proven otherwise',
      '🔴 CRITICAL: Signs of raised ICP (decreasing GCS, vomiting, pupil changes) - Urgent neurosurgery',
      '🔴 CRITICAL: Post-thrombolysis: New headache, neurological deterioration - Suspect hemorrhage',
      '🟠 URGENT: TIA (resolved symptoms) - High stroke risk, urgent investigation within 24 hours',
      '🟠 URGENT: New atrial fibrillation - Cardioembolic source, needs anticoagulation',
      '🟠 URGENT: Severe carotid stenosis - May need urgent intervention',
      '🟡 WARNING: Persistent dysphagia - Aspiration risk, may need NGT/PEG',
      '🟡 WARNING: Depression post-stroke - Very common, screen and treat'
    ],

    complications: [
      'Brain edema and herniation (especially large MCA strokes)',
      'Hemorrhagic transformation of ischemic stroke',
      'Recurrent stroke (highest risk early)',
      'Aspiration pneumonia (due to dysphagia)',
      'Deep vein thrombosis and pulmonary embolism',
      'Pressure ulcers',
      'Urinary tract infections',
      'Falls and fractures',
      'Spasticity and contractures',
      'Post-stroke depression (30-50%)',
      'Post-stroke epilepsy (5-10%)',
      'Cognitive impairment and vascular dementia',
      'Shoulder subluxation and pain',
      'Central post-stroke pain'
    ],

    prognosis: `MORTALITY:
• 30-day mortality: 10-15% for ischemic, 30-50% for hemorrhagic
• 1-year mortality: 20-30%
• Higher mortality in Africa due to late presentation and limited resources

DISABILITY:
• 25-30% remain significantly disabled
• 40% have moderate impairment
• 25-30% recover well

FACTORS AFFECTING OUTCOME:
Better prognosis:
• Rapid treatment (thrombolysis/thrombectomy)
• Stroke unit care
• Smaller infarct/hemorrhage volume
• Younger age
• Less severe initial deficit (lower NIHSS)
• Early rehabilitation
• Good secondary prevention

Worse prognosis:
• Large infarct with mass effect
• Hemorrhagic stroke (vs ischemic)
• Delayed presentation
• Older age
• Severe initial deficit
• Poor secondary prevention adherence

RECURRENCE:
• 10-15% risk of recurrent stroke in first year
• Risk reduced by 80% with optimal secondary prevention`,

    prevention: `PRIMARY PREVENTION (Most important):
• Blood pressure control - Single most important intervention
  - Target <130/80 mmHg
  - Population-wide salt reduction
• Smoking cessation
• Diabetes control
• Lipid management
• Atrial fibrillation: Anticoagulation (CHA2DS2-VASc guided)
• Healthy diet (Mediterranean, DASH)
• Regular physical activity
• Moderate alcohol consumption
• Obesity management

SECONDARY PREVENTION (After stroke/TIA):
Essential interventions:
• Antiplatelet therapy (aspirin ± clopidogrel) for ischemic stroke
• Anticoagulation for cardioembolic stroke (AF)
• High-intensity statin
• Blood pressure control
• Diabetes control
• Lifestyle modification
• Carotid intervention if significant stenosis

Targets:
• BP <130/80 mmHg
• LDL <1.8 mmol/L
• HbA1c <7%
• Complete smoking cessation

AFRICAN CONTEXT - Key Priorities:
• Hypertension awareness, treatment, and control
• Access to basic secondary prevention medications
• Community education on stroke warning signs (FAST)
• Reducing delays to hospital presentation`,

    patientEducation: `RECOGNIZING STROKE - FAST:
Learn the signs of stroke and act FAST - Every minute matters!

F - FACE: Ask the person to smile. Does one side droop?
A - ARMS: Ask them to raise both arms. Does one drift down?
S - SPEECH: Ask them to repeat a simple phrase. Is it slurred or strange?
T - TIME: If you see ANY of these signs, call emergency services IMMEDIATELY

🚨 STROKE IS A MEDICAL EMERGENCY 🚨
The faster treatment is received, the more brain can be saved.
Do NOT wait to see if symptoms improve - call for help immediately.

Other warning signs:
• Sudden numbness or weakness (especially one-sided)
• Sudden confusion or trouble understanding
• Sudden trouble seeing
• Sudden trouble walking, dizziness, loss of balance
• Sudden severe headache with no known cause

WHAT IS A STROKE?
A stroke happens when blood flow to part of the brain is blocked (most common) or when a blood vessel in the brain bursts. Without blood, brain cells start to die within minutes.

UNDERSTANDING YOUR STROKE:
Your doctor will explain what type of stroke you had:
• Ischemic stroke: A clot blocked a blood vessel (85% of strokes)
• Hemorrhagic stroke: A blood vessel burst causing bleeding (15% of strokes)

WHY PREVENTION MATTERS:
After a stroke, you are at higher risk of having another one. BUT - taking your medications and making lifestyle changes can reduce this risk by up to 80%!

YOUR MEDICATIONS - Take them every day:
• Blood thinners (aspirin, clopidogrel, or anticoagulants): Prevent clots
• Blood pressure medication: Control BP to protect your brain
• Statin: Lowers cholesterol and stabilizes blood vessel walls
• Other medications as prescribed by your doctor

⚠️ Never stop your medications without talking to your doctor

LIFESTYLE CHANGES THAT HELP:
Blood Pressure Control:
• Take BP medication as prescribed
• Reduce salt in your diet
• Check your BP regularly

Healthy Eating:
• More fruits, vegetables, whole grains
• Less salt, saturated fat, processed foods
• Eat fish twice a week if possible

Physical Activity:
• Aim for 30 minutes of activity most days
• Start slowly and increase gradually
• Even walking helps!

Stop Smoking:
• Smoking doubles your stroke risk
• Quitting at any age helps

Limit Alcohol:
• Men: Maximum 2 drinks per day
• Women: Maximum 1 drink per day

REHABILITATION:
Recovery continues for months after a stroke. Your rehabilitation team may include:
• Physiotherapist - Movement and strength
• Occupational therapist - Daily activities
• Speech therapist - Speaking and swallowing
• Psychologist - Emotional support

Be patient with yourself - Recovery takes time.

DRIVING:
• You must not drive for a period after stroke (check local regulations)
• Tell your insurance company
• Your doctor will advise when you can drive again

EMOTIONAL CHANGES:
Depression is very common after stroke (1 in 3 people). Signs include:
• Feeling sad, hopeless, or empty
• Loss of interest in activities
• Sleep problems
• Difficulty concentrating

Tell your doctor if you experience these - Treatment helps.

WHEN TO SEEK URGENT HELP:
🚨 ANY new stroke symptoms (FAST)
🚨 Severe headache (especially if different from usual)
🚨 Difficulty breathing
🚨 Chest pain
🚨 Falls or injuries

Remember: You CAN reduce your risk of another stroke. Take your medications, make healthy choices, and attend your follow-up appointments.`,

    references: [
      'American Heart Association/American Stroke Association Guidelines for Stroke Management 2019',
      'NICE Guidelines: Stroke and transient ischaemic attack 2022',
      'European Stroke Organisation Guidelines',
      'WHO HEARTS Technical Package for Cardiovascular Disease Management',
      'Powers WJ, et al. AHA/ASA Guidelines for Early Management of Acute Ischemic Stroke 2019',
      'WHO Essential Medicines List 2023'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 98. UNCOMPLICATED MALARIA
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '98',
    name: 'Uncomplicated Malaria',
    slug: 'uncomplicated-malaria',
    specialty: 'Infectious Disease',
    bodySystem: 'Infectious',
    icdCode: 'B50',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Uncomplicated malaria is a symptomatic malaria infection without signs of severity or vital organ dysfunction. It is the most common presentation of malaria and accounts for the vast majority of malaria cases in endemic areas. Prompt diagnosis and treatment prevents progression to severe malaria.

DEFINITION:
Uncomplicated malaria = Symptomatic malaria (fever + parasitemia) WITHOUT features of severe malaria

MALARIA SPECIES:
• Plasmodium falciparum - Most dangerous, causes severe malaria, dominant in Africa
• Plasmodium vivax - Can cause relapses (hypnozoites), common in Asia/Americas
• Plasmodium ovale - Can cause relapses, found in West Africa
• Plasmodium malariae - Causes chronic infection, found worldwide
• Plasmodium knowlesi - Zoonotic, found in Southeast Asia

🔴 KEY CLINICAL POINTS:
• TEST BEFORE TREAT - Parasitological confirmation required (RDT or microscopy)
• ACT (Artemisinin-based Combination Therapy) is first-line for P. falciparum
• Complete the full course (3 days) even if feeling better
• Reassess for SEVERE MALARIA signs at every visit - Can progress rapidly
• Primaquine needed for P. vivax/ovale to prevent relapses (check G6PD first)
• Pregnant women: Quinine + Clindamycin (1st trimester) or ACT (2nd/3rd trimester)
• Children under 5 and pregnant women are highest risk for severe disease

DISTINGUISHING UNCOMPLICATED FROM SEVERE MALARIA:
Uncomplicated = Can take oral medications, no danger signs
Severe = Any of: Impaired consciousness, prostration, multiple convulsions, respiratory distress, circulatory collapse, jaundice, significant bleeding, severe anemia (Hb <5), hypoglycemia, acidosis, renal impairment, hyperparasitemia

AFRICAN CONTEXT:
• Leading cause of outpatient visits and hospital admissions
• 95% of global malaria deaths occur in Africa
• P. falciparum accounts for >99% of cases in sub-Saharan Africa
• Seasonal transmission in Sahel; year-round in equatorial regions
• Partial immunity develops in endemic areas (semi-immune adults)
• RDTs have revolutionized diagnosis at community level
• Integrated Community Case Management (iCCM) enables community treatment`,

    epidemiology: `GLOBAL BURDEN (WHO 2023):
• 249 million cases globally (2022)
• 608,000 deaths (2022)
• 94% of cases and 95% of deaths in Africa
• 80% of deaths are children under 5 years

AFRICAN EPIDEMIOLOGY:
• Highest burden region globally
• P. falciparum predominates (>99%)
• Transmission intensity varies:
  - High/stable transmission: Equatorial Africa (year-round)
  - Seasonal transmission: Sahel region (rainy season)
  - Low transmission: Highland and urban areas
• Endemic countries: Nigeria, DRC, Uganda, Mozambique, Niger account for >50% of global cases

POPULATIONS AT HIGHEST RISK:
• Children under 5 years (highest mortality)
• Pregnant women (increased severity, adverse pregnancy outcomes)
• Non-immune travelers
• People living with HIV
• Migrants and mobile populations

TRANSMISSION:
• Vector: Female Anopheles mosquito (bites dusk to dawn)
• Peak biting: Late evening to early morning
• Transmission season: Related to rainfall and temperature
• Incubation period: 7-30 days (usually 10-15 days for P. falciparum)

IMMUNITY:
• Partial immunity develops after repeated exposure
• Semi-immune adults may have parasitemia with minimal symptoms
• Immunity wanes without continued exposure
• Children in endemic areas are most vulnerable (no immunity)`,

    clinicalPresentation: `CLASSIC TRIAD (Often absent in endemic areas):
• Fever (may be periodic or continuous)
• Chills and rigors
• Sweating

TYPICAL SYMPTOMS:
• Fever - Most consistent symptom (may be >40°C)
• Headache - Very common
• Body aches and myalgia
• Fatigue and malaise
• Anorexia, nausea, vomiting
• Abdominal pain (especially children)
• Diarrhea (can mimic gastroenteritis)

PHYSICAL EXAMINATION:
• Fever (may be absent between paroxysms)
• Tachycardia
• Pallor (anemia)
• Mild splenomegaly (may be absent in first infection)
• Mild hepatomegaly
• Jaundice (mild, in some cases)

PRESENTATION IN ENDEMIC AREAS:
• May be less dramatic due to partial immunity
• Fever may be low-grade
• Non-specific symptoms predominate
• High index of suspicion needed
• Many conditions mimic malaria

FEVER PATTERNS (Classic but often not seen):
• P. falciparum: Irregular, quotidian
• P. vivax/ovale: Tertian (48-hour cycle)
• P. malariae: Quartan (72-hour cycle)

PRESENTATION IN CHILDREN:
• Fever (may present with febrile seizures)
• Irritability, poor feeding
• Vomiting, diarrhea
• Cough (can mimic respiratory infection)
• Lethargy
• Pallor
• Hepatosplenomegaly

PRESENTATION IN PREGNANCY:
• May have minimal symptoms despite high parasitemia
• Higher risk of anemia
• Placental malaria affects fetal growth
• Risk of miscarriage, stillbirth, low birth weight`,

    diagnosis: `PARASITOLOGICAL CONFIRMATION IS MANDATORY:
WHO recommends: Test before treat (no presumptive treatment)
Only treat confirmed cases (except in special circumstances)

DIAGNOSTIC METHODS:

1. RAPID DIAGNOSTIC TEST (RDT):
   • Detects malaria antigens in blood
   • HRP2-based: Detects P. falciparum
   • pLDH/Aldolase-based: Detects all species
   • Results in 15-20 minutes
   • Sensitivity ~95% for P. falciparum
   • Advantages: No equipment, minimal training, field-deployable
   • Limitations: Cannot quantify parasitemia, HRP2 persists after treatment
   • FALSE NEGATIVES: Low parasitemia, HRP2 gene deletions

2. MICROSCOPY (Gold standard):
   • Thick film: Screening (more sensitive)
   • Thin film: Species identification and parasite count
   • Can quantify parasitemia (parasites/μL)
   • Requires trained microscopist and equipment
   • Sensitivity depends on expertise

WHEN TO TEST:
• Any febrile illness in endemic area
• Fever within 3 months of travel to endemic area
• Unexplained anemia in endemic areas
• Any illness in endemic area where malaria is possible

INTERPRETING RESULTS:
• Positive RDT/microscopy + symptoms = Treat for malaria
• Negative RDT/microscopy: Consider repeating in 12-24 hours if strong suspicion
• Positive test in asymptomatic person in endemic area: May be incidental parasitemia

ASSESSING FOR SEVERITY - CHECK AT EVERY VISIT:
• Level of consciousness (can the patient sit/stand?)
• Ability to eat/drink and take oral medications
• Respiratory rate (tachypnea suggests acidosis)
• Urine output
• Pallor (severe anemia)
• Jaundice
• Bleeding

LABORATORY FINDINGS (if available):
• Anemia (normocytic)
• Thrombocytopenia (common, not a severity criterion)
• Mild elevation of bilirubin and transaminases
• Hypoglycemia (check in all severe cases and children)`,

    differentialDiagnosis: [
      'Typhoid fever - Gradual onset, relative bradycardia, rose spots, constipation > diarrhea',
      'Viral infections (Dengue, Chikungunya) - Rash, severe myalgia, leukopenia',
      'Bacterial sepsis - May coexist, look for focus of infection',
      'Urinary tract infection - Dysuria, frequency, positive urinalysis',
      'Pneumonia - Cough, tachypnea, chest signs',
      'Meningitis - Neck stiffness, photophobia, altered consciousness',
      'Acute HIV seroconversion - Rash, lymphadenopathy, pharyngitis',
      'Viral hepatitis - Jaundice, transaminitis, hepatomegaly',
      'Influenza - Respiratory symptoms, myalgia, seasonal',
      'Gastroenteritis - Vomiting and diarrhea predominate'
    ],

    investigations: [
      {
        name: 'Malaria RDT',
        indication: 'All suspected malaria - First-line diagnostic test',
        availability: 'BASIC'
      },
      {
        name: 'Blood smear microscopy (thick and thin film)',
        indication: 'Gold standard - Species identification, parasite quantification',
        availability: 'BASIC'
      },
      {
        name: 'Hemoglobin / Hematocrit',
        indication: 'Assess for anemia - Important for severity assessment',
        availability: 'BASIC'
      },
      {
        name: 'Blood glucose',
        indication: 'All children and severe cases - Hypoglycemia common',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Assess anemia, thrombocytopenia; baseline',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: 'Dark urine may indicate hemolysis; exclude UTI',
        availability: 'BASIC'
      },
      {
        name: 'Renal function (Creatinine)',
        indication: 'If severe malaria suspected - Assess for AKI',
        availability: 'BASIC'
      },
      {
        name: 'Liver function tests',
        indication: 'If jaundiced or severe malaria suspected',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'G6PD testing',
        indication: 'Before primaquine for P. vivax/ovale (prevents hemolysis)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Blood culture',
        indication: 'If bacterial co-infection suspected',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Confirm diagnosis with RDT or microscopy (Test before treat)',
        'Assess for signs of SEVERE MALARIA - If present, treat as severe',
        'Check ability to tolerate oral medications',
        'Check blood glucose (especially children)',
        'Assess hydration status',
        'Calculate weight-based dosing for ACT',
        'Provide first dose under observation if possible',
        'Advise to return immediately if condition worsens'
      ],

      pharmacological: [
        {
          drug: 'Artemether-Lumefantrine (AL / Coartem)',
          indication: 'First-line ACT for uncomplicated P. falciparum malaria',
          dose: 'Weight-based dosing over 3 days (6 doses at 0, 8, 24, 36, 48, 60 hours): 5-14kg: 1 tab per dose; 15-24kg: 2 tabs; 25-34kg: 3 tabs; ≥35kg: 4 tabs',
          route: 'Oral (take with fatty food to improve absorption)',
          duration: '3 days (6 doses total)',
          contraindications: ['First trimester pregnancy (use quinine)', 'Severe malaria (use IV artesunate)'],
          sideEffects: ['Nausea', 'Headache', 'Dizziness', 'QT prolongation (rare)'],
          alternatives: ['Artesunate-Amodiaquine', 'Dihydroartemisinin-Piperaquine']
        },
        {
          drug: 'Artesunate-Amodiaquine (ASAQ)',
          indication: 'Alternative first-line ACT for uncomplicated P. falciparum',
          dose: 'Fixed-dose combination by weight: Available as co-formulated tablets. 3-day course once daily',
          route: 'Oral',
          duration: '3 days',
          contraindications: ['First trimester pregnancy', 'Severe hepatic impairment'],
          sideEffects: ['Nausea', 'Vomiting', 'Abdominal pain', 'Neutropenia (rare)'],
          alternatives: ['Artemether-Lumefantrine', 'Dihydroartemisinin-Piperaquine']
        },
        {
          drug: 'Dihydroartemisinin-Piperaquine (DHA-PQ)',
          indication: 'Alternative first-line ACT; longer post-treatment prophylaxis',
          dose: 'Weight-based: 2-4 mg/kg DHA + 16-32 mg/kg PQ daily for 3 days',
          route: 'Oral (take on empty stomach)',
          duration: '3 days',
          contraindications: ['Cardiac conduction disorders', 'QT prolongation', 'First trimester pregnancy'],
          sideEffects: ['QT prolongation', 'Nausea', 'Vomiting'],
          alternatives: ['Artemether-Lumefantrine']
        },
        {
          drug: 'Quinine + Clindamycin',
          indication: 'First trimester pregnancy (ACTs contraindicated)',
          dose: 'Quinine 10 mg/kg TDS for 7 days + Clindamycin 10 mg/kg BD for 7 days',
          route: 'Oral',
          duration: '7 days',
          contraindications: ['Quinine hypersensitivity', 'Myasthenia gravis'],
          sideEffects: ['Cinchonism (tinnitus, headache, nausea)', 'Hypoglycemia', 'QT prolongation'],
          alternatives: ['Quinine monotherapy for 7 days if clindamycin unavailable']
        },
        {
          drug: 'Primaquine (for P. vivax/ovale)',
          indication: 'Radical cure - Eliminates liver hypnozoites to prevent relapse',
          dose: '0.25-0.5 mg/kg daily for 14 days (after ACT treatment)',
          route: 'Oral',
          duration: '14 days',
          contraindications: ['G6PD deficiency (causes hemolysis)', 'Pregnancy', 'Breastfeeding infant with unknown G6PD'],
          sideEffects: ['Hemolysis in G6PD deficiency', 'GI upset', 'Methemoglobinemia'],
          alternatives: ['Weekly primaquine 0.75 mg/kg for 8 weeks in mild G6PD deficiency (with monitoring)']
        },
        {
          drug: 'Paracetamol',
          indication: 'Symptomatic relief of fever and pain',
          dose: 'Adults: 500-1000mg every 4-6 hours (max 4g/day); Children: 10-15mg/kg every 4-6 hours',
          route: 'Oral',
          duration: 'As needed for symptoms',
          contraindications: ['Severe hepatic impairment'],
          sideEffects: ['Hepatotoxicity in overdose'],
          alternatives: ['Ibuprofen (avoid in dehydration)']
        }
      ],

      nonPharmacological: [
        'SUPPORTIVE CARE:',
        '  • Encourage oral fluids to maintain hydration',
        '  • Light, easily digestible diet',
        '  • Rest during acute illness',
        '  • Tepid sponging for high fever',
        '',
        'PATIENT EDUCATION:',
        '  • Complete full 3-day course even if feeling better',
        '  • Take AL with fatty food (milk, groundnuts) for absorption',
        '  • Return IMMEDIATELY if: Unable to eat/drink, vomiting everything, very weak, confused, breathing fast, convulsions',
        '  • Prevention: Sleep under ITN, use repellents',
        '',
        'DIRECTLY OBSERVED THERAPY:',
        '  • First dose observed at health facility when possible',
        '  • Ensures correct administration and monitors for vomiting',
        '',
        'MALARIA PREVENTION (Counsel all patients):',
        '  • Insecticide-treated bed nets (ITNs) - Sleep under every night',
        '  • Indoor residual spraying (IRS) where available',
        '  • Seasonal malaria chemoprevention (SMC) for children in Sahel',
        '  • Intermittent preventive treatment in pregnancy (IPTp) with SP',
        '  • Eliminate standing water around home'
      ],

      surgical: [
        'No surgical intervention for uncomplicated malaria'
      ],

      referralCriteria: [
        'Any sign of SEVERE MALARIA - Immediate referral',
        'Unable to tolerate oral medication (persistent vomiting)',
        'Deterioration despite treatment',
        'Pregnant woman with malaria (higher risk)',
        'Infant under 2 months',
        'Significant comorbidities (HIV, malnutrition, sickle cell)',
        'Treatment failure (persistent symptoms after 3 days, parasitemia after 7 days)',
        'Uncertainty about diagnosis'
      ],

      monitoring: [
        'Clinical response: Fever should resolve within 48-72 hours',
        'Ability to eat and drink',
        'Watch for danger signs at each contact',
        'Parasite clearance: Microscopy at day 3 if available (should be negative or near-negative)',
        'Day 7 and Day 28 follow-up for treatment efficacy monitoring (research/surveillance settings)',
        'Hemoglobin check if anemic at presentation'
      ],

      followUp: `ROUTINE FOLLOW-UP:
• Day 3: Assess clinical response, complete treatment course
• If fever persists at day 3: Re-examine, repeat test, consider treatment failure or co-infection
• If symptoms resolve: No routine follow-up needed

TREATMENT FAILURE:
• Early treatment failure: Danger signs by day 3, parasitemia day 2 > day 0, parasitemia day 3 ≥25% of day 0
• Late treatment failure: Return of symptoms and parasitemia after initial clearance (day 4-28)
• Management: Treat with second-line ACT or quinine + clindamycin

SPECIAL SITUATIONS:
• P. vivax/ovale: Follow up to ensure completion of primaquine 14-day course
• Pregnancy: Close monitoring throughout pregnancy; IPTp after treatment
• Severe anemia: Recheck hemoglobin at 2-4 weeks`
    },

    redFlags: [
      '🔴 CRITICAL: Impaired consciousness or coma - SEVERE MALARIA, needs IV artesunate',
      '🔴 CRITICAL: Respiratory distress (acidotic breathing) - SEVERE MALARIA, urgent referral',
      '🔴 CRITICAL: Multiple convulsions - SEVERE MALARIA, urgent management',
      '🔴 CRITICAL: Prostration (unable to sit/stand/walk) - SEVERE MALARIA',
      '🔴 CRITICAL: Shock (cold extremities, weak pulse) - SEVERE MALARIA',
      '🟠 URGENT: Severe anemia (Hb <5 g/dL or severe pallor) - May need transfusion',
      '🟠 URGENT: Hypoglycemia (blood glucose <3 mmol/L) - Give glucose immediately',
      '🟠 URGENT: Persistent vomiting - Cannot take oral ACT, needs parenteral treatment',
      '🟠 URGENT: Jaundice + fever - Possible severe malaria or other diagnosis',
      '🟡 WARNING: High parasitemia (>4% or >200,000/μL) - Risk of progression',
      '🟡 WARNING: Pregnant woman with malaria - Higher risk, close monitoring',
      '🟡 WARNING: Child with malaria + malnutrition - Higher risk of severe disease'
    ],

    complications: [
      'Progression to severe malaria (if untreated or treatment delayed)',
      'Severe malarial anemia',
      'Cerebral malaria',
      'Acute kidney injury',
      'Acute respiratory distress syndrome',
      'Hypoglycemia (especially children and pregnant women)',
      'Metabolic acidosis',
      'Splenic rupture (rare)',
      'Post-malaria neurological syndrome (after cerebral malaria)',
      'Blackwater fever (massive hemolysis)',
      'Pregnancy complications: Miscarriage, stillbirth, low birth weight, maternal anemia'
    ],

    prognosis: `UNCOMPLICATED MALARIA:
• Excellent prognosis with prompt, effective treatment
• Fever typically resolves within 24-48 hours
• Full recovery expected in 3-7 days
• Mortality <0.1% with appropriate treatment

RISK OF PROGRESSION:
• Untreated or delayed treatment: Can progress to severe malaria within hours
• Children under 5 and pregnant women: Highest risk of severe disease
• Non-immune individuals: More likely to have severe symptoms

TREATMENT FAILURE:
• ACT failure rate is generally <5% in most of Africa
• Artemisinin resistance emerging in parts of East Africa (Rwanda, Uganda)
• Treatment failure requires alternative antimalarial

RECURRENCE:
• New infection (reinfection) common in endemic areas
• Recrudescence (treatment failure) if incomplete treatment or resistance
• Relapse (P. vivax/ovale) if primaquine not given

IMMUNITY:
• Partial immunity develops after repeated infections
• Adults in endemic areas may tolerate parasitemia with minimal symptoms
• Immunity wanes if person leaves endemic area`,

    prevention: `VECTOR CONTROL (Most effective):
• Insecticide-Treated Nets (ITNs):
  - Sleep under ITN every night
  - Ensure net is intact, properly tucked
  - Treat or replace nets as recommended
  
• Indoor Residual Spraying (IRS):
  - Spray interior walls with insecticide
  - Effective for 3-6 months
  - Community-level intervention

• Environmental management:
  - Eliminate standing water (breeding sites)
  - Clear vegetation around homes

CHEMOPREVENTION:
• Intermittent Preventive Treatment in Pregnancy (IPTp):
  - Sulfadoxine-Pyrimethamine (SP) at each ANC visit from 2nd trimester
  - At least 3 doses during pregnancy
  
• Seasonal Malaria Chemoprevention (SMC):
  - Monthly SP + Amodiaquine during rainy season
  - Children 3-59 months in Sahel region
  
• Intermittent Preventive Treatment in Infants (IPTi):
  - SP given with vaccinations

PERSONAL PROTECTION:
• Use mosquito repellents (DEET, picaridin)
• Wear long sleeves and pants in evening
• Use screens on windows and doors
• Air conditioning reduces mosquito entry

MALARIA VACCINE:
• RTS,S/AS01 (Mosquirix) - First approved malaria vaccine
• Recommended by WHO for children in endemic areas
• R21/Matrix-M - Newer vaccine with higher efficacy
• Given as series of doses in early childhood

TRAVELERS:
• Chemoprophylaxis (Atovaquone-proguanil, Doxycycline, or Mefloquine)
• Start before travel, continue during and after
• Combined with personal protection measures`,

    patientEducation: `UNDERSTANDING MALARIA:
Malaria is caused by parasites transmitted through mosquito bites. It is very common in our region but can be treated effectively if diagnosed early. Without treatment, it can become very serious, especially in young children and pregnant women.

YOUR TREATMENT:
You have been prescribed ACT (Artemisinin-based Combination Therapy). This is the most effective treatment for malaria.

HOW TO TAKE YOUR MEDICINE:
• Take ALL tablets as prescribed for 3 FULL DAYS
• Do NOT stop early even if you feel better - This can cause the malaria to come back
• Take Coartem (AL) with food or milk - This helps your body absorb the medicine
• If you vomit within 30 minutes of taking the dose, take it again
• If you vomit between 30-60 minutes, take half the dose again

WHAT TO EXPECT:
• Your fever should start to improve within 1-2 days
• You should feel much better by day 3
• Complete rest and plenty of fluids will help you recover faster

⚠️ RETURN TO HOSPITAL IMMEDIATELY IF:
• You cannot keep the medicine down (vomiting everything)
• You become more weak or confused
• You have difficulty breathing or fast breathing
• You have a convulsion (fit)
• You become very pale
• Your child stops eating or drinking
• The fever does not improve after 2 days
• You feel worse instead of better

These could be signs of SEVERE MALARIA which needs hospital treatment.

PREVENTING MALARIA - PROTECT YOUR FAMILY:
🛏️ Sleep Under a Treated Mosquito Net Every Night
• Make sure the net has no holes
• Tuck it under the mattress properly
• Everyone should sleep under a net, especially children and pregnant women

🦟 Protect Yourself from Mosquito Bites:
• Mosquitoes that carry malaria bite between dusk and dawn
• Wear long sleeves and pants in the evening
• Use mosquito repellent on exposed skin
• Close windows and doors in the evening

🏠 Keep Your Environment Clean:
• Remove standing water around your home (old tires, containers, blocked drains)
• This is where mosquitoes breed

👩‍🍼 IF YOU ARE PREGNANT:
• Attend all your antenatal clinic appointments
• Take the preventive malaria medicine (SP/Fansidar) given at the clinic
• Sleep under a treated net every night
• Report any fever immediately

FOR YOUR CHILDREN:
• Young children are most at risk from malaria
• Ensure they sleep under a net every night
• If your child has fever, get them tested immediately - Do not delay
• Keep your child's vaccination card - Malaria vaccine is now available

REMEMBER:
• Malaria is preventable with bed nets and preventive medicine
• Malaria is curable with proper treatment
• Early treatment prevents severe malaria
• Complete your medication even if you feel better`,

    references: [
      'WHO Guidelines for Malaria 2023',
      'WHO World Malaria Report 2023',
      'Severe Malaria: Supplement to WHO Guidelines 2021',
      'National Malaria Control Programme Guidelines (Various African Countries)',
      'Malaria Consortium Technical Briefs',
      'Roll Back Malaria Partnership Guidelines'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 99. CHRONIC HEART FAILURE
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '99',
    name: 'Chronic Heart Failure',
    slug: 'chronic-heart-failure',
    specialty: 'Cardiology',
    bodySystem: 'Cardiovascular',
    icdCode: 'I50.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Chronic heart failure (CHF) is a clinical syndrome in which the heart is unable to pump sufficient blood to meet the body's metabolic demands, or can only do so at elevated filling pressures. It is a progressive condition requiring lifelong management with medications that improve symptoms AND prolong survival.

CLASSIFICATION BY EJECTION FRACTION:
• HFrEF (Heart Failure with Reduced EF): EF ≤40% - "Systolic heart failure"
• HFmrEF (Mildly Reduced EF): EF 41-49%
• HFpEF (Preserved EF): EF ≥50% - "Diastolic heart failure"

NYHA FUNCTIONAL CLASSIFICATION:
• Class I: No limitation of physical activity
• Class II: Slight limitation; comfortable at rest; ordinary activity causes symptoms
• Class III: Marked limitation; comfortable at rest; less than ordinary activity causes symptoms
• Class IV: Unable to carry out any physical activity without symptoms; symptoms at rest

🔴 KEY CLINICAL POINTS:
• FOUR PILLARS of HFrEF therapy (All reduce mortality):
  1. ACE inhibitor/ARB (or ARNI)
  2. Beta-blocker (Carvedilol, Bisoprolol, Metoprolol succinate)
  3. Mineralocorticoid receptor antagonist (Spironolactone)
  4. SGLT2 inhibitor (Empagliflozin, Dapagliflozin)
• Start low, go slow with beta-blockers (may initially worsen symptoms)
• Diuretics for SYMPTOM relief (do not improve mortality)
• Identify and treat underlying cause (hypertension, IHD, valvular disease)
• Salt and fluid restriction
• Daily weights to monitor fluid status

AFRICAN CONTEXT:
• Hypertension is the leading cause (vs IHD in Western countries)
• Rheumatic heart disease still significant, especially in young patients
• Peripartum cardiomyopathy - Higher incidence in Africa
• Endomyocardial fibrosis - Endemic in tropical Africa
• HIV-related cardiomyopathy
• Late presentation with advanced disease common
• Limited access to echocardiography and specialist care
• Essential medications increasingly available`,

    epidemiology: `GLOBAL BURDEN:
• ~64 million people affected worldwide
• Prevalence increases with age (10% in >70 years)
• Leading cause of hospitalization in elderly
• 5-year mortality: 50% (worse than many cancers)
• Healthcare costs substantial

AFRICAN EPIDEMIOLOGY:
• Prevalence: 1-4% of adult population
• Younger age of onset than developed countries
• Hospital admission rate increasing

CAUSES IN AFRICA (Different from developed countries):
1. HYPERTENSION - Leading cause (60-70% of cases)
   - Often undiagnosed and untreated
   - Leads to hypertensive heart disease

2. RHEUMATIC HEART DISEASE (10-20%)
   - Still prevalent, especially in rural areas
   - Affects young people
   - Mitral and aortic valve involvement

3. CARDIOMYOPATHIES (15-20%)
   - Dilated cardiomyopathy (idiopathic, HIV-related)
   - Peripartum cardiomyopathy (higher in Africa)
   - Endomyocardial fibrosis (tropical)

4. ISCHEMIC HEART DISEASE (5-10%)
   - Increasing with epidemiological transition
   - Still less common than in Western countries

5. OTHER CAUSES
   - HIV-related (direct and opportunistic)
   - Pericardial disease (TB pericarditis)
   - Cor pulmonale (from lung disease)

RISK FACTORS:
• Hypertension (most important modifiable risk factor)
• Coronary artery disease
• Diabetes mellitus
• Obesity
• Smoking
• Excessive alcohol
• Rheumatic fever history
• Family history of cardiomyopathy`,

    clinicalPresentation: `SYMPTOMS OF HEART FAILURE:

LEFT-SIDED FAILURE (Pulmonary congestion):
• Dyspnea (breathlessness) - Cardinal symptom
• Orthopnea (breathlessness lying flat) - Ask "How many pillows?"
• Paroxysmal nocturnal dyspnea (waking at night breathless)
• Fatigue and exercise intolerance
• Cough (may be dry or with pink frothy sputum)
• Wheeze ("cardiac asthma")

RIGHT-SIDED FAILURE (Systemic congestion):
• Peripheral edema (ankles, legs, sacrum if bedbound)
• Abdominal distension (ascites)
• Right upper quadrant pain (hepatic congestion)
• Anorexia, nausea
• Early satiety

LOW CARDIAC OUTPUT:
• Fatigue
• Weakness
• Dizziness
• Cold peripheries
• Confusion (in severe cases)

PHYSICAL EXAMINATION:
General:
• Tachypnea
• Cyanosis (peripheral or central in severe cases)
• Cachexia (cardiac cachexia in advanced disease)

Cardiovascular:
• Elevated JVP (>4cm above sternal angle)
• Displaced apex beat (cardiomegaly)
• S3 gallop (volume overload)
• S4 gallop (stiff ventricle)
• Murmurs (underlying valvular disease)
• Tachycardia

Respiratory:
• Bibasal crackles (pulmonary edema)
• Pleural effusion (dullness to percussion)
• Wheeze

Abdominal:
• Hepatomegaly (tender, pulsatile in TR)
• Ascites
• Hepatojugular reflux

Peripheral:
• Pitting edema (ankles, sacrum)
• Cold peripheries

TYPICAL SCENARIOS:
• Young patient with RHD murmur and heart failure
• Middle-aged hypertensive with breathlessness and leg swelling
• Postpartum woman with new heart failure (peripartum cardiomyopathy)
• HIV patient with cardiomyopathy`,

    diagnosis: `DIAGNOSTIC APPROACH:
1. Clinical assessment (symptoms and signs)
2. Confirm heart failure (BNP/echocardiography)
3. Determine ejection fraction (HFrEF vs HFpEF)
4. Identify underlying cause
5. Assess severity (NYHA class)
6. Identify precipitants of decompensation

CLINICAL DIAGNOSIS:
Framingham Criteria (2 major OR 1 major + 2 minor):
Major: PND, JVD, Crackles, S3 gallop, Cardiomegaly, Pulmonary edema on CXR
Minor: Ankle edema, Night cough, Dyspnea on exertion, Hepatomegaly, Pleural effusion, Tachycardia >120

KEY INVESTIGATIONS:

ECG:
• Rarely normal in heart failure
• May show: LVH, Q waves (prior MI), AF, LBBB
• Normal ECG has high negative predictive value

CHEST X-RAY:
• Cardiomegaly (CTR >50%)
• Pulmonary venous congestion (upper lobe diversion)
• Pulmonary edema (Kerley B lines, bat-wing opacities)
• Pleural effusions

ECHOCARDIOGRAPHY (Essential for diagnosis):
• Confirms diagnosis
• Measures ejection fraction (EF)
• Identifies structural abnormalities (valves, wall motion)
• Assesses diastolic function
• Guides treatment selection

BNP/NT-proBNP:
• Elevated in heart failure
• Helps diagnose and rule out heart failure
• BNP <100 pg/mL or NT-proBNP <300 pg/mL makes HF unlikely
• Useful for monitoring and prognosis
• Elevated in renal failure (adjust interpretation)

BASIC BLOODS:
• FBC: Anemia can worsen HF
• Renal function: Baseline before ACEi, monitor for worsening
• Electrolytes: Hyponatremia (poor prognosis), hypokalemia
• Liver function: Hepatic congestion
• Thyroid function: Thyroid disease can cause/worsen HF
• Glucose/HbA1c: Diabetes is common comorbidity
• Lipid profile: CV risk assessment`,

    differentialDiagnosis: [
      'COPD exacerbation - Smoking history, hyperinflation on CXR, obstructive spirometry',
      'Pneumonia - Fever, productive cough, consolidation on CXR',
      'Pulmonary embolism - Sudden onset, pleuritic pain, risk factors for VTE',
      'Asthma - Younger patient, wheeze, reversibility, no cardiac findings',
      'Nephrotic syndrome - Edema, heavy proteinuria, hypoalbuminemia',
      'Liver cirrhosis - Ascites, spider naevi, history of alcohol/hepatitis',
      'Anemia - Pallor, high-output symptoms, low hemoglobin',
      'Pericardial effusion/tamponade - Muffled heart sounds, pulsus paradoxus, raised JVP',
      'Constrictive pericarditis - Raised JVP, Kussmaul sign, pericardial calcification',
      'Obesity/deconditioning - No cardiac findings, normal BNP, normal echo'
    ],

    investigations: [
      {
        name: 'ECG',
        indication: 'All patients - Rarely normal in HF; identifies AF, LVH, ischemia',
        availability: 'BASIC'
      },
      {
        name: 'Chest X-ray',
        indication: 'All patients - Cardiomegaly, pulmonary congestion, alternative diagnoses',
        availability: 'BASIC'
      },
      {
        name: 'Echocardiography',
        indication: 'ESSENTIAL - Confirms diagnosis, measures EF, identifies cause',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'BNP or NT-proBNP',
        indication: 'Diagnosis when uncertain; prognostication; monitoring',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Full blood count',
        indication: 'All patients - Anemia worsens HF',
        availability: 'BASIC'
      },
      {
        name: 'Renal function (Creatinine, eGFR)',
        indication: 'All patients - Baseline, monitor with ACEi/diuretics',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes (Na, K)',
        indication: 'All patients - Hyponatremia (prognosis), monitor with diuretics',
        availability: 'BASIC'
      },
      {
        name: 'Liver function tests',
        indication: 'Hepatic congestion; baseline before statins',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid function tests',
        indication: 'All patients - Thyroid disease is treatable cause',
        availability: 'BASIC'
      },
      {
        name: 'Blood glucose / HbA1c',
        indication: 'Screen for diabetes - Common comorbidity',
        availability: 'BASIC'
      },
      {
        name: 'Lipid profile',
        indication: 'Cardiovascular risk assessment',
        availability: 'BASIC'
      },
      {
        name: 'HIV test',
        indication: 'Consider in endemic areas - HIV cardiomyopathy',
        availability: 'BASIC'
      },
      {
        name: 'Iron studies',
        indication: 'Iron deficiency common and treatable in HF',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Assess severity and stability (acute decompensation needs different management)',
        'Confirm diagnosis with echocardiography if not already done',
        'Identify and treat underlying cause and precipitants',
        'Assess volume status (congested vs euvolemic)',
        'Review current medications',
        'Start disease-modifying therapy for HFrEF (Four Pillars)',
        'Educate patient on fluid restriction, daily weights, medication adherence'
      ],

      pharmacological: [
        {
          drug: 'ACE Inhibitors (Enalapril, Lisinopril, Ramipril)',
          indication: 'ALL patients with HFrEF - First-line, mortality benefit',
          dose: 'Enalapril: Start 2.5mg BD, target 10-20mg BD. Lisinopril: Start 2.5-5mg OD, target 20-40mg OD. Ramipril: Start 1.25-2.5mg OD, target 10mg OD',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Pregnancy', 'Bilateral renal artery stenosis', 'Hyperkalemia (K+ >5.5)', 'Angioedema history'],
          sideEffects: ['Cough (10-15%)', 'Hyperkalemia', 'Renal impairment', 'Hypotension', 'Angioedema (rare)'],
          alternatives: ['ARB (Losartan, Valsartan) if ACEi-intolerant due to cough']
        },
        {
          drug: 'Beta-Blockers (Carvedilol, Bisoprolol, Metoprolol succinate)',
          indication: 'ALL patients with HFrEF - Mortality benefit; Start when euvolemic',
          dose: 'Carvedilol: Start 3.125mg BD, double every 2 weeks, target 25mg BD. Bisoprolol: Start 1.25mg OD, target 10mg OD. Metoprolol succinate: Start 12.5-25mg OD, target 200mg OD',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Asthma', 'Severe bradycardia', 'Heart block (without pacemaker)', 'Decompensated HF (wait until stable)'],
          sideEffects: ['Fatigue', 'Bradycardia', 'Hypotension', 'Cold extremities', 'Initial worsening of HF symptoms'],
          alternatives: ['Ivabradine if cannot tolerate beta-blockers and in sinus rhythm']
        },
        {
          drug: 'Spironolactone (MRA)',
          indication: 'HFrEF with NYHA II-IV despite ACEi and BB - Mortality benefit',
          dose: 'Start 25mg OD, target 25-50mg OD',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Hyperkalemia (K+ >5.0)', 'Severe renal impairment (eGFR <30)', 'Addison disease'],
          sideEffects: ['Hyperkalemia', 'Gynecomastia (men)', 'Breast tenderness', 'Renal impairment'],
          alternatives: ['Eplerenone (less gynecomastia)']
        },
        {
          drug: 'SGLT2 Inhibitors (Empagliflozin, Dapagliflozin)',
          indication: 'ALL patients with HFrEF (and HFpEF) - Mortality and hospitalization benefit',
          dose: 'Empagliflozin 10mg OD or Dapagliflozin 10mg OD',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Type 1 diabetes', 'eGFR <20 (reduced efficacy)', 'Recurrent DKA'],
          sideEffects: ['Genital mycotic infections', 'UTIs', 'Volume depletion', 'DKA (rare)'],
          alternatives: ['Prioritize other pillars if unavailable']
        },
        {
          drug: 'Loop Diuretics (Furosemide, Bumetanide)',
          indication: 'Symptomatic relief of congestion - Does NOT improve mortality',
          dose: 'Furosemide: Start 20-40mg OD, adjust to symptoms. Higher doses in renal impairment. Bumetanide: 0.5-2mg OD',
          route: 'Oral (IV for acute decompensation)',
          duration: 'Ongoing; titrate to lowest effective dose',
          contraindications: ['Hypovolemia', 'Anuria'],
          sideEffects: ['Hypokalemia', 'Hyponatremia', 'Hypomagnesemia', 'Dehydration', 'Gout', 'Ototoxicity'],
          alternatives: ['Thiazides for adjunctive use in diuretic resistance']
        },
        {
          drug: 'ARBs (Losartan, Valsartan, Candesartan)',
          indication: 'Alternative to ACEi if cough intolerant',
          dose: 'Losartan: Start 25-50mg OD, target 150mg OD. Valsartan: Start 40mg BD, target 160mg BD. Candesartan: Start 4-8mg OD, target 32mg OD',
          route: 'Oral',
          duration: 'Lifelong',
          contraindications: ['Same as ACEi except cough is not an issue'],
          sideEffects: ['Hyperkalemia', 'Renal impairment', 'Hypotension', 'Angioedema (rare)'],
          alternatives: ['Sacubitril/Valsartan (ARNI) if available']
        },
        {
          drug: 'Digoxin',
          indication: 'HFrEF with AF for rate control; Persistent symptoms despite optimal therapy',
          dose: '62.5-250 micrograms OD (lower in elderly, renal impairment)',
          route: 'Oral',
          duration: 'Ongoing; monitor levels',
          contraindications: ['Heart block', 'WPW syndrome', 'Hypertrophic cardiomyopathy'],
          sideEffects: ['Toxicity: Nausea, visual disturbances, arrhythmias', 'Narrow therapeutic index'],
          alternatives: ['Rate control with beta-blockers']
        },
        {
          drug: 'Hydralazine + Isosorbide dinitrate',
          indication: 'Alternative if ACEi/ARB contraindicated; Add-on in Black African patients',
          dose: 'Hydralazine 25mg TDS, target 75mg TDS + ISDN 20mg TDS, target 40mg TDS',
          route: 'Oral',
          duration: 'Ongoing',
          contraindications: ['Severe hypotension', 'Aortic stenosis'],
          sideEffects: ['Headache', 'Flushing', 'Hypotension', 'Lupus-like syndrome (hydralazine)'],
          alternatives: ['ACEi/ARB if tolerated']
        }
      ],

      nonPharmacological: [
        'LIFESTYLE MODIFICATIONS:',
        '  • Fluid restriction: 1.5-2L/day (stricter in severe HF)',
        '  • Salt restriction: <2g sodium/day (avoid adding salt, avoid processed foods)',
        '  • Daily weights: Weigh at same time each day; Report gain >2kg in 3 days',
        '  • Smoking cessation (absolute)',
        '  • Alcohol: Avoid or limit to 1-2 units/day; STOP if alcohol-induced cardiomyopathy',
        '  • Exercise: Regular moderate exercise improves symptoms and quality of life',
        '',
        'PATIENT EDUCATION (Critical):',
        '  • Understanding heart failure and its progressive nature',
        '  • Importance of medication adherence',
        '  • Recognizing warning signs of decompensation',
        '  • When to seek medical help',
        '  • Avoid NSAIDs (worsen HF and renal function)',
        '',
        'VACCINATIONS:',
        '  • Annual influenza vaccine',
        '  • Pneumococcal vaccine',
        '  • COVID-19 vaccine',
        '',
        'CARDIAC REHABILITATION:',
        '  • Exercise training program if available',
        '  • Improves exercise tolerance and quality of life',
        '',
        'DEVICE THERAPY (Advanced settings):',
        '  • ICD for primary/secondary prevention of sudden death',
        '  • CRT for HFrEF with wide QRS',
        '  • Heart transplant for end-stage HF'
      ],

      surgical: [
        'Valve repair/replacement for valvular heart disease',
        'Coronary revascularization (CABG/PCI) if ischemic etiology',
        'CRT device implantation (QRS >150ms with LBBB)',
        'ICD implantation for sudden death prevention',
        'LVAD as bridge to transplant or destination therapy',
        'Heart transplantation for end-stage heart failure'
      ],

      referralCriteria: [
        'New diagnosis of heart failure for echocardiography and specialist assessment',
        'HFrEF for optimization of medical therapy',
        'Frequent hospitalizations despite optimal medical therapy',
        'Consideration for device therapy (ICD/CRT)',
        'Valvular heart disease requiring intervention',
        'Young patient with heart failure (exclude genetic causes)',
        'Peripartum cardiomyopathy',
        'End-stage heart failure for transplant evaluation'
      ],

      monitoring: [
        'Symptoms and functional status (NYHA class) at each visit',
        'Weight and volume status',
        'Blood pressure and heart rate',
        'Renal function and electrolytes: 1-2 weeks after starting/changing ACEi/ARB/MRA, then periodically',
        'Potassium: Monitor with ACEi/ARB/MRA; aim 4.0-5.0 mEq/L',
        'BNP/NT-proBNP if available (trend more useful than single value)',
        'Medication adherence',
        'Quality of life'
      ],

      followUp: `STABLE HEART FAILURE:
• Review every 3-6 months
• Assess symptoms, weight, medication adherence
• Check renal function and electrolytes 1-2x per year
• Uptitrate medications toward target doses

AFTER HOSPITALIZATION:
• Review within 1-2 weeks
• Assess volume status
• Check renal function and electrolytes
• Reinforce education
• High-risk period for readmission

MEDICATION TITRATION:
• Uptitrate ACEi/ARB every 2-4 weeks to target dose
• Uptitrate beta-blockers every 2-4 weeks (go slowly)
• Check renal function/K+ after each dose increase of ACEi/ARB/MRA

WORSENING SYMPTOMS:
• Assess for precipitants (non-adherence, arrhythmia, infection, anemia)
• Optimize diuretic therapy
• Review need for hospitalization`
    },

    redFlags: [
      '🔴 CRITICAL: Acute pulmonary edema - Sit upright, oxygen, IV furosemide, urgent management',
      '🔴 CRITICAL: Cardiogenic shock (hypotension, poor perfusion) - ICU admission',
      '🔴 CRITICAL: New/poorly controlled AF with rapid ventricular response - Rate/rhythm control',
      '🔴 CRITICAL: Severe hyperkalemia (K+ >6.0) - Stop ACEi/ARB/MRA, urgent treatment',
      '🟠 URGENT: Worsening dyspnea at rest - May need IV diuretics/admission',
      '🟠 URGENT: Weight gain >2kg in 3 days despite adherence - Increasing diuretics',
      '🟠 URGENT: New syncope - Arrhythmia workup needed',
      '🟠 URGENT: Worsening renal function with ACEi/diuretics - Reassess fluid status and medications',
      '🟡 WARNING: Persistent symptoms despite optimal therapy - Specialist referral',
      '🟡 WARNING: Non-adherence to medications - Education and support'
    ],

    complications: [
      'Acute decompensation requiring hospitalization',
      'Arrhythmias (AF most common; ventricular arrhythmias cause sudden death)',
      'Sudden cardiac death',
      'Cardiorenal syndrome (progressive renal impairment)',
      'Hepatic congestion and cardiac cirrhosis',
      'Cachexia (muscle wasting in advanced HF)',
      'Depression and reduced quality of life',
      'Thromboembolism (stroke, DVT/PE)',
      'Medication side effects (renal impairment, hyperkalemia)',
      'Respiratory infections',
      'End-stage heart failure'
    ],

    prognosis: `OVERALL PROGNOSIS:
• 5-year mortality: ~50% (worse than many cancers)
• Prognosis improving with modern therapy
• HFrEF: Mortality reduced by 30-40% with guideline-directed medical therapy (GDMT)
• HFpEF: Prognosis similar, fewer evidence-based treatments

PROGNOSTIC FACTORS:
Poor prognosis:
• Low ejection fraction
• NYHA Class III-IV
• Elevated BNP/NT-proBNP
• Hyponatremia
• Renal dysfunction
• Recurrent hospitalizations
• Advanced age
• Multiple comorbidities
• Low blood pressure on ACEi
• Ischemic etiology

Better prognosis:
• Higher ejection fraction
• NYHA Class I-II
• Response to medical therapy
• Preserved renal function
• Reversible cause (e.g., tachycardia-mediated)
• Adherence to therapy

CAUSES OF DEATH:
• Progressive pump failure (~40%)
• Sudden cardiac death (~35%)
• Other cardiovascular causes
• Non-cardiovascular causes`,

    prevention: `PRIMARY PREVENTION:
• Treat hypertension (most important in Africa)
  - Target <130/80 mmHg
  - ACEi/ARB preferred in those with diabetes or CKD
• Prevent and treat coronary artery disease
  - Smoking cessation
  - Lipid management
  - Diabetes control
• Prevent rheumatic heart disease
  - Treat streptococcal pharyngitis
  - Secondary prophylaxis for those with RHD
• Limit alcohol consumption
• Maintain healthy weight
• Regular physical activity

SECONDARY PREVENTION (After HF diagnosis):
• Guideline-directed medical therapy (Four Pillars)
• Salt and fluid restriction
• Daily weights
• Medication adherence
• Vaccination (influenza, pneumococcal)
• Avoid NSAIDs
• Cardiac rehabilitation

PREVENT HOSPITALIZATIONS:
• Patient education on warning signs
• Early recognition and treatment of decompensation
• Telephone/remote monitoring where available
• Close follow-up after discharge`,

    patientEducation: `UNDERSTANDING HEART FAILURE:
Heart failure means your heart is not pumping blood as well as it should. It does NOT mean your heart has stopped working. With proper treatment and lifestyle changes, many people with heart failure live active lives.

YOUR MEDICATIONS ARE LIFE-SAVING:
You will likely be prescribed several medications. Each one has an important role:

💊 ACE inhibitors (e.g., Enalapril, Lisinopril):
• Helps your heart pump better
• Proven to help you live longer
• May cause dry cough in some people

💊 Beta-blockers (e.g., Carvedilol, Bisoprolol):
• Slows and protects your heart
• Proven to help you live longer
• May make you feel tired at first - This improves over time
• Never stop suddenly

💊 Spironolactone:
• Reduces fluid and protects your heart
• Proven to help you live longer

💊 Water tablets / Diuretics (e.g., Furosemide):
• Removes excess fluid
• Helps with breathlessness and swelling
• You may urinate more - Take in the morning

⚠️ Take ALL your medications every day, even when you feel well.
⚠️ Never stop medications without consulting your doctor.

DAILY WEIGHTS - Very Important:
📊 Weigh yourself every morning:
• Same time, same clothes, after urinating
• Write it down
• If you gain more than 2 kg in 3 days, contact your doctor

FLUID AND SALT RESTRICTION:
💧 Limit fluids to 1.5-2 liters per day (includes tea, soup, etc.)
🧂 Reduce salt intake:
• Don't add salt at the table
• Avoid processed and canned foods
• Avoid salty snacks and fast food
• Season with herbs and spices instead

LIFESTYLE CHANGES:
🚭 Stop smoking completely
🍺 Limit or avoid alcohol
🏃 Stay active - Gentle exercise helps (walking is good)
⚖️ Maintain healthy weight

WARNING SIGNS - When to Seek Help:
🚨 Contact your doctor if:
• Sudden weight gain (>2 kg in 3 days)
• Increased breathlessness, especially lying down
• Needing more pillows to sleep
• Waking up at night breathless
• Increased swelling in legs or abdomen
• Persistent cough or coughing blood-tinged phlegm
• Feeling dizzy or fainting
• Fast or irregular heartbeat

🚨 Go to hospital immediately if:
• Severe breathlessness at rest
• Chest pain
• Confusion
• Unable to lie down at all

MEDICATIONS TO AVOID:
⛔ Do NOT take without asking your doctor:
• Ibuprofen, Diclofenac, or other painkillers (NSAIDs)
• Fizzy antacids (high in sodium)
• Some herbal remedies

VACCINATIONS:
Get your annual flu vaccine and pneumonia vaccine - Infections can worsen heart failure.

LIVING WITH HEART FAILURE:
• You can still live a fulfilling life
• Take medications as prescribed
• Follow salt and fluid restrictions
• Stay active within your limits
• Weigh yourself daily
• Know the warning signs
• Keep all your appointments
• Don't hesitate to ask questions

Your healthcare team is here to help you manage your condition and maintain the best quality of life possible.`,

    references: [
      'ESC Guidelines for the diagnosis and treatment of acute and chronic heart failure 2021',
      'ACC/AHA Guidelines for the Management of Heart Failure 2022',
      'NICE Guidelines: Chronic Heart Failure 2018 (updated)',
      'Pan-African Society of Cardiology Position Statement on Heart Failure in Africa',
      'WHO Package of Essential NCD Interventions (PEN)',
      'Heart Failure Society of America Guidelines'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 100. OBSTRUCTED LABOR
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '100',
    name: 'Obstructed Labor',
    slug: 'obstructed-labor',
    specialty: 'Obstetrics',
    bodySystem: 'Reproductive',
    icdCode: 'O66',
    resourceLevel: 'INTERMEDIATE',
    isEmergency: true,

    overview: `Obstructed labor occurs when the fetus cannot progress through the birth canal despite adequate uterine contractions, due to mechanical obstruction. It is a life-threatening obstetric emergency and a leading cause of maternal and perinatal mortality in low-resource settings, yet is largely preventable with proper antenatal care and timely intervention.

DEFINITION:
• Failure of labor to progress despite adequate uterine contractions
• Mechanical barrier preventing vaginal delivery
• Diagnosed when cervix is fully dilated (second stage) or nearly so with no descent despite pushing

CAUSES (The 3 P's):
1. PASSENGER (Fetal factors):
   • Malpresentation (brow, face, transverse lie)
   • Malposition (persistent occipito-posterior)
   • Large baby (macrosomia)
   • Hydrocephalus
   • Fetal anomalies

2. PASSAGE (Maternal pelvis):
   • Contracted pelvis (malnutrition, rickets, polio, injury)
   • Pelvic tumors (fibroids, ovarian cysts)
   • Soft tissue obstruction (cervical fibroid, vaginal septum)

3. POWERS (Uterine contractions):
   • Note: True obstruction has ADEQUATE contractions
   • Inadequate contractions = Inefficient labor (different management)

🔴 KEY CLINICAL POINTS:
• OBSTETRIC EMERGENCY - Can cause maternal death, uterine rupture, fetal death
• TIME IS CRITICAL - Prolonged obstruction leads to ischemic injury
• Cesarean section or assisted vaginal delivery required
• DO NOT attempt vaginal delivery if signs of obstruction present
• Signs of obstruction: Bandl's ring, edematous cervix, caput, molding
• Complications: Uterine rupture, obstetric fistula, sepsis, PPH

AFRICAN CONTEXT:
• Leading cause of maternal mortality in many African countries
• 8% of maternal deaths globally due to obstructed labor
• Obstetric fistula affects millions of African women
• Contributing factors:
  - Lack of antenatal care
  - Early marriage and pregnancy (immature pelvis)
  - Malnutrition leading to contracted pelvis
  - Delayed referral and transport difficulties
  - Shortage of emergency obstetric care
  - Home deliveries with unskilled attendants`,

    epidemiology: `GLOBAL BURDEN:
• Obstructed labor complicates 3-6% of all deliveries
• Higher rates in low-resource settings (up to 8-10%)
• Causes 8% of maternal deaths globally (~40,000 deaths/year)
• Leading cause of obstetric fistula (2-3 million women affected globally)

AFRICAN EPIDEMIOLOGY:
• Incidence: 2-8% of deliveries depending on region
• Case fatality rate: 1-5% (vs <0.1% in developed countries)
• Primary cause of obstetric fistula
  - 30,000-130,000 new cases annually in Africa
  - 2+ million women living with untreated fistula
• Leading direct cause of maternal death in several countries
• Significant contributor to perinatal mortality (up to 90% fetal mortality if prolonged)

RISK FACTORS:
Maternal:
• Primigravida (first pregnancy)
• Short stature (<145cm)
• Age <18 years (immature pelvis)
• Previous difficult delivery
• Previous cesarean section
• History of pelvic injury or surgery
• Nutritional deficiencies (rickets, osteomalacia)
• Female genital mutilation

Fetal:
• Macrosomia (>4kg)
• Malpresentation/malposition
• Fetal anomalies

Healthcare factors:
• Lack of antenatal care
• Unskilled birth attendant
• Delayed referral
• Distance from emergency obstetric care
• Lack of partograph use`,

    clinicalPresentation: `SYMPTOMS:
• Prolonged painful labor (>12-24 hours)
• Strong, frequent contractions with no progress
• Unable to push baby out despite urge
• Exhaustion and dehydration
• May have urinary retention
• Fever if prolonged (developing sepsis)

SIGNS OF OBSTRUCTED LABOR:

MATERNAL SIGNS:
• Exhaustion, dehydration
• Tachycardia
• Fever (late sign - infection)
• Abdominal tenderness
• BANDL'S RING: Pathognomonic sign
  - Visible/palpable oblique ridge between lower and upper uterine segments
  - Indicates impending uterine rupture
• Edematous (swollen) cervix
• Vaginal bleeding (may indicate rupture)

ABDOMINAL EXAMINATION:
• Uterus tender
• Contractions may be strong and continuous
• Fetus difficult to palpate (may be in abnormal lie)
• Lower segment tender and thinned
• Bandl's ring visible/palpable

VAGINAL EXAMINATION:
• Cervix fully dilated (or rim of edematous cervix)
• CAPUT SUCCEDANEUM: Severe scalp swelling (edema) on presenting part
• MOLDING: Overlapping of fetal skull bones (graded 1-3+)
  - 1+: Bones touching
  - 2+: Bones overlapping but reducible
  - 3+: Bones overlapping and not reducible (severe obstruction)
• Head high despite full dilatation
• May feel large baby or abnormal presentation

FETAL SIGNS:
• Fetal heart rate abnormalities (late decelerations, bradycardia, absent)
• Meconium-stained liquor (fetal distress)
• Intrauterine fetal death (if prolonged)

SIGNS OF UTERINE RUPTURE (Complication):
• Sudden cessation of contractions
• Severe abdominal pain then relief
• Vaginal bleeding
• Shock (tachycardia, hypotension)
• Fetal parts easily palpable
• Absent fetal heart
• Hematuria

STAGES OF OBSTRUCTED LABOR:
1. Threatened obstruction: Poor progress with strong contractions
2. Established obstruction: Cervix fully dilated, Bandl's ring forming, 3+ molding
3. Neglected obstruction: Dehydration, infection, impending rupture`,

    diagnosis: `CLINICAL DIAGNOSIS:
Obstructed labor is a clinical diagnosis based on:
• Failure to progress despite adequate contractions
• Cervix fully or nearly fully dilated
• Head not descending
• Signs of obstruction (Bandl's ring, caput, molding)

PARTOGRAPH - Essential tool for early detection:
• Plot cervical dilatation and fetal descent
• Alert line: Expected 1cm/hour dilatation
• Action line: 4 hours to the right of alert line
• Crossing action line = Labor not progressing = ACTION NEEDED
• Early detection prevents obstructed labor

ASSESSMENT CRITERIA:

PROLONGED LABOR vs OBSTRUCTED LABOR:
• Prolonged labor: Slow progress, may respond to augmentation
• Obstructed labor: NO progress possible, mechanical barrier
• Critical distinction - Augmentation of obstructed labor causes rupture!

HISTORY:
• Duration of labor (>12 hours concerning, >24 hours serious)
• Duration of ruptured membranes
• Number of previous pregnancies and deliveries
• Previous cesarean sections
• Antenatal care and identified risk factors
• Time of last oral intake
• Fluid intake and urine output

EXAMINATION:
General: Dehydration, exhaustion, fever
Abdominal: Uterine tenderness, Bandl's ring, lie and presentation
Vaginal: Dilatation, position, station, caput, molding, pelvis assessment

ASSESSMENT OF PELVIS:
• Diagonal conjugate (should be >11.5cm)
• Pelvic shape and capacity
• Ischial spines (prominent = narrowed)
• Sacral curve
• Subpubic angle

FETAL ASSESSMENT:
• Fetal heart rate (CTG if available, Pinard or Doppler)
• Presentation and position
• Estimated fetal weight
• Liquor (meconium staining?)

WHEN TO DIAGNOSE OBSTRUCTION:
• Cervix fully dilated
• Adequate contractions (3 in 10 minutes, lasting 40+ seconds)
• No descent after 1 hour of active pushing
• Presence of Bandl's ring, severe caput, 3+ molding`,

    differentialDiagnosis: [
      'Inefficient uterine action - Poor contractions, may respond to augmentation (but NEVER augment if obstructed)',
      'Prolonged latent phase - Cervix <4cm, irregular contractions, not true obstruction',
      'Secondary arrest of labor - Progress stops but may be due to exhaustion, epidural, or obstruction',
      'Fetal distress without obstruction - May need intervention but not mechanical obstruction',
      'Placental abruption - Painful bleeding, tender uterus, fetal distress',
      'Uterine rupture - Sudden cessation of contractions, shock, fetal parts palpable'
    ],

    investigations: [
      {
        name: 'Partograph monitoring',
        indication: 'ALL laboring women - Essential for early detection of abnormal labor',
        availability: 'BASIC'
      },
      {
        name: 'Vaginal examination',
        indication: 'Assess dilatation, station, presentation, pelvis, caput, molding',
        availability: 'BASIC'
      },
      {
        name: 'Fetal heart rate monitoring',
        indication: 'All cases - Assess fetal wellbeing (Pinard, Doppler, or CTG)',
        availability: 'BASIC'
      },
      {
        name: 'Urinalysis',
        indication: 'Assess dehydration (ketones), infection',
        availability: 'BASIC'
      },
      {
        name: 'Full blood count',
        indication: 'Baseline before surgery; assess for anemia, infection',
        availability: 'BASIC'
      },
      {
        name: 'Blood group and cross-match',
        indication: 'Prepare blood for surgery - PPH risk high',
        availability: 'BASIC'
      },
      {
        name: 'Ultrasound',
        indication: 'Confirm fetal lie, presentation, viability if uncertain',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Serum electrolytes',
        indication: 'If prolonged labor with dehydration',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        '🚨 OBSTRUCTED LABOR IS AN EMERGENCY - Act immediately',
        'Call for help - Inform senior staff, prepare for theater',
        'Establish IV access - Large bore cannula',
        'Fluid resuscitation - IV crystalloids (Ringer lactate or Normal saline)',
        'Catheterize bladder - Relieve retention, monitor output',
        'Check hemoglobin and cross-match blood',
        'Give antibiotics (sepsis prevention/treatment)',
        'Assess fetal viability (fetal heart)',
        'DO NOT GIVE OXYTOCIN - Will cause uterine rupture',
        'Prepare for CESAREAN SECTION (most cases) or assisted vaginal delivery'
      ],

      pharmacological: [
        {
          drug: 'IV Fluids (Ringer Lactate or Normal Saline)',
          indication: 'Rehydration - ALL cases of obstructed labor are dehydrated',
          dose: '1-2 liters rapidly, then continue as needed',
          route: 'IV',
          duration: 'Until surgery and postoperatively',
          contraindications: ['None in this emergency'],
          sideEffects: ['Fluid overload if excessive'],
          alternatives: ['Any available crystalloid']
        },
        {
          drug: 'Ampicillin + Gentamicin + Metronidazole',
          indication: 'Prophylaxis and treatment of infection - Always give',
          dose: 'Ampicillin 2g IV q6h + Gentamicin 5mg/kg IV daily + Metronidazole 500mg IV q8h',
          route: 'IV',
          duration: 'Continue 24-48 hours post-delivery if uncomplicated; longer if sepsis',
          contraindications: ['Known allergy (use alternatives)'],
          sideEffects: ['GI upset', 'Allergic reaction'],
          alternatives: ['Ceftriaxone + Metronidazole if penicillin allergy']
        },
        {
          drug: 'Oxytocin (POSTPARTUM ONLY)',
          indication: 'AFTER delivery to prevent PPH - NEVER before delivery in obstruction',
          dose: '10 IU IM or 5 IU slow IV after delivery of baby',
          route: 'IM or IV',
          duration: 'Single dose; can continue infusion for PPH prophylaxis',
          contraindications: ['BEFORE delivery in obstructed labor - Causes rupture'],
          sideEffects: ['Hypotension if given rapid IV', 'Water retention'],
          alternatives: ['Ergometrine', 'Misoprostol']
        },
        {
          drug: 'Misoprostol',
          indication: 'PPH prophylaxis/treatment if oxytocin unavailable',
          dose: '600mcg sublingual or rectal immediately after delivery',
          route: 'Sublingual or rectal',
          duration: 'Single dose',
          contraindications: ['Before delivery'],
          sideEffects: ['Shivering', 'Fever', 'Diarrhea'],
          alternatives: ['Oxytocin (preferred)']
        },
        {
          drug: 'Tetanus prophylaxis',
          indication: 'If immunization status uncertain or incomplete',
          dose: 'Tetanus toxoid 0.5mL IM',
          route: 'IM',
          duration: 'Single dose',
          contraindications: ['Known severe allergy'],
          sideEffects: ['Local pain', 'Mild fever'],
          alternatives: ['None']
        }
      ],

      nonPharmacological: [
        'DEFINITIVE MANAGEMENT - Delivery is ESSENTIAL:',
        '',
        '1. CESAREAN SECTION (Most cases):',
        '   • Indicated when vaginal delivery is not possible',
        '   • Safest option for most obstructed labor',
        '   • Assess for uterine rupture at surgery',
        '   • May need hysterectomy if ruptured/necrotic uterus',
        '',
        '2. ASSISTED VAGINAL DELIVERY (Selected cases only):',
        '   • Only if head is engaged and low (at or below spines)',
        '   • Vacuum extraction preferred over forceps',
        '   • Must meet criteria: Full dilatation, head at +2 station or lower, adequate pelvis',
        '   • Abandon if not delivering within 2-3 pulls',
        '',
        '3. DESTRUCTIVE OPERATIONS (If fetus dead):',
        '   • Craniotomy, decapitation, evisceration',
        '   • ONLY performed on confirmed dead fetus',
        '   • Requires trained personnel',
        '   • May be life-saving when cesarean unavailable',
        '',
        'POST-DELIVERY CARE:',
        '   • Continue IV fluids',
        '   • Continue antibiotics',
        '   • Monitor for PPH',
        '   • Keep catheter for minimum 14 days if prolonged obstruction (prevents fistula)',
        '   • Monitor urine output',
        '   • Debrief and counseling',
        '',
        'PREVENTION OF FISTULA:',
        '   • Continuous bladder drainage for 10-14 days post-delivery',
        '   • Allows healing of ischemic tissue',
        '   • High fluid intake',
        '   • Monitor for urine leakage'
      ],

      surgical: [
        'Cesarean section - Definitive treatment for most cases',
        'Vacuum extraction - If criteria met for assisted vaginal delivery',
        'Forceps delivery - If criteria met (less preferred than vacuum)',
        'Symphysiotomy - Rarely used; can be life-saving where cesarean unavailable',
        'Craniotomy - Only for dead fetus when cesarean unavailable',
        'Hysterectomy - If uterus ruptured or necrotic',
        'Repair of uterine rupture if viable',
        'Fistula repair (later, if fistula develops)'
      ],

      referralCriteria: [
        'ALL cases of obstructed labor require referral to facility with surgical capacity',
        'If no surgical capacity: Stabilize and transfer URGENTLY',
        'Suspected uterine rupture - Immediate surgery needed',
        'Signs of sepsis - Need intensive management',
        'Postoperative complications',
        'Vesicovaginal or rectovaginal fistula - Specialized repair'
      ],

      monitoring: [
        'Continuous fetal heart monitoring until delivery',
        'Maternal vital signs every 15-30 minutes',
        'Urine output (catheterized)',
        'Watch for signs of uterine rupture',
        'Post-delivery: Monitor for PPH, sepsis',
        'Postoperative: Wound, urine output, infection signs'
      ],

      followUp: `IMMEDIATE POSTPARTUM:
• Monitor for PPH - Have uterotonics ready
• Continue antibiotics 24-48 hours minimum
• Keep bladder catheter for 10-14 days (fistula prevention)
• Monitor urine output and character
• Wound care if cesarean section

BEFORE DISCHARGE:
• Ensure passing urine normally after catheter removal
• Check for incontinence (urine or feces)
• Discuss contraception
• Counsel about future pregnancies
• Arrange follow-up

6 WEEKS POSTPARTUM:
• Full postnatal check
• Assess for urinary or fecal incontinence (fistula)
• Screen for depression/trauma
• Discuss future pregnancy planning
• Contraception

FUTURE PREGNANCIES:
• High-risk - Requires antenatal care
• Previous obstructed labor = Risk of recurrence
• May need elective cesarean section
• Early referral to facility with surgical capacity
• No home delivery

IF FISTULA DEVELOPS:
• Refer to fistula repair center
• Successful repair possible in most cases
• Social support needed`
    },

    redFlags: [
      '🔴 CRITICAL: Bandl\'s ring visible/palpable - IMPENDING UTERINE RUPTURE, immediate cesarean',
      '🔴 CRITICAL: Sudden cessation of contractions with shock - UTERINE RUPTURE, emergency laparotomy',
      '🔴 CRITICAL: Fetal heart absent - Fetal death; still need urgent delivery for maternal safety',
      '🔴 CRITICAL: Maternal shock (tachycardia, hypotension, confusion) - Ruptured uterus or sepsis',
      '🔴 CRITICAL: Fresh vaginal bleeding with obstructed labor - May indicate rupture',
      '🟠 URGENT: Fetal bradycardia or late decelerations - Fetal distress, expedite delivery',
      '🟠 URGENT: Maternal fever >38°C - Sepsis developing, antibiotics and delivery',
      '🟠 URGENT: Meconium-stained liquor - Fetal distress',
      '🟡 WARNING: 3+ molding with high head - Will not deliver vaginally, needs cesarean',
      '🟡 WARNING: Labor >24 hours with poor progress - High risk of complications'
    ],

    complications: [
      'Uterine rupture - Life-threatening maternal emergency',
      'Maternal death',
      'Fetal death / stillbirth',
      'Birth asphyxia and neonatal death',
      'Vesicovaginal fistula (VVF) - Continuous urinary incontinence',
      'Rectovaginal fistula (RVF) - Fecal incontinence',
      'Postpartum hemorrhage',
      'Puerperal sepsis',
      'Bladder and urethral injury',
      'Cervical and vaginal lacerations',
      'Uterine atony',
      'Hysterectomy (loss of fertility)',
      'Long-term: Pelvic organ prolapse',
      'Psychological trauma, PTSD, depression',
      'Social consequences (divorce, abandonment, stigma with fistula)'
    ],

    prognosis: `MATERNAL OUTCOMES:
• With timely cesarean section: Maternal mortality <1%
• Without intervention: Maternal mortality 5-15%
• Uterine rupture: Mortality 10-40%
• Surviving mothers may have lifelong disability (fistula)

FETAL OUTCOMES:
• Early intervention: Perinatal mortality 10-20%
• Prolonged obstruction: Perinatal mortality up to 90%
• Survivors may have birth asphyxia complications (cerebral palsy, developmental delay)

FISTULA OUTCOMES:
• Develops in 2-5% of obstructed labor survivors
• Without repair: Lifelong incontinence, social isolation
• With surgical repair: 80-95% success rate
• Multiple repairs may be needed

FUTURE PREGNANCY:
• Higher risk of recurrence
• Elective cesarean often recommended
• Good outcomes with proper care`,

    prevention: `PRIMARY PREVENTION:
• Universal access to antenatal care
  - Early detection of risk factors
  - Identification of malpresentation
  - Appropriate birth planning
• Delay first pregnancy until physical maturity (>18 years)
• Prevent child marriage
• Improve nutrition (prevent contracted pelvis)
• Skilled birth attendant at every delivery
• Partograph use in ALL labors
• Functional referral system

SECONDARY PREVENTION (Early detection):
• Use partograph for all laboring women
• Act when crossing action line
• Early referral for prolonged labor
• Never leave laboring woman alone
• Regular assessment of progress

TERTIARY PREVENTION (Limit complications):
• Timely cesarean section
• Appropriate surgical technique
• Prolonged catheterization to prevent fistula
• Early treatment of sepsis
• Fistula repair for affected women

HEALTH SYSTEM INTERVENTIONS:
• Emergency obstetric care (EmOC) availability
  - Basic EmOC: Antibiotics, oxytocics, anticonvulsants, manual removal of placenta, assisted vaginal delivery
  - Comprehensive EmOC: + Cesarean section, blood transfusion
• Transport systems for referral
• Trained personnel
• Essential supplies and equipment

COMMUNITY INTERVENTIONS:
• Birth preparedness (know danger signs, plan for emergency)
• Community transport schemes
• Women's education and empowerment
• End child marriage
• Improved nutrition for girls`,

    patientEducation: `FOR PREGNANT WOMEN - PREVENTING OBSTRUCTED LABOR:

ATTEND ANTENATAL CARE:
• Go for check-ups throughout your pregnancy
• The doctor/midwife can identify problems early
• Plan where you will deliver

KNOW THE DANGER SIGNS IN LABOR:
🚨 Go to the hospital IMMEDIATELY if:
• You have been in labor for more than 12 hours
• Your water breaks but baby doesn't come
• You feel like pushing but baby is not coming
• You have severe pain that doesn't stop between contractions
• You notice blood
• You feel very weak or dizzy
• You have fever during labor
• Baby's movements become less or stop

PLAN FOR YOUR DELIVERY:
• Decide where you will deliver (hospital/health center with trained staff)
• Know how you will get there quickly
• Save money for transport and emergencies
• Identify who will help care for your other children
• Have someone with you who can make decisions

FOR WOMEN IN LABOR:
• Do not stay at home if labor is long
• If traditional birth attendant cannot deliver baby, go to hospital
• It is better to go early than too late
• Obstructed labor can kill you or your baby

AFTER OBSTRUCTED LABOR:
• Keep the urinary catheter in as long as instructed (prevents fistula)
• Drink plenty of fluids
• Take all your medications
• Watch for signs of infection (fever, discharge)
• Come back if you notice urine or stool leaking

IF YOU DEVELOP A FISTULA:
• This can be repaired - Do not lose hope
• Seek care at a fistula treatment center
• Most women can be cured with surgery
• You are not alone - Many women have recovered

FOR FUTURE PREGNANCIES:
• You are at higher risk
• Attend antenatal care early
• Plan to deliver in a hospital
• You may need a cesarean section
• Do not deliver at home

FOR COMMUNITY HEALTH WORKERS:
• Encourage all pregnant women to attend ANC
• Ensure every labor is monitored with partograph
• Know when to refer (crossing action line, danger signs)
• Refer early - It is better to be safe
• Accompany women to hospital if needed
• Delays cost lives - Act quickly`,

    references: [
      'WHO Managing Complications in Pregnancy and Childbirth (MCPC) 2017',
      'WHO Recommendations for Augmentation of Labour 2014',
      'FIGO Guidelines on Prevention of Obstructed Labor',
      'Royal College of Obstetricians and Gynaecologists: Operative Vaginal Delivery',
      'Management of Obstructed Labour: A Practical Guide (EngenderHealth)',
      'WHO Essential Interventions for Reproductive Health'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 100. HYPOTHYROIDISM
  // ═══════════════════════════════════════════════════════════════════════════
  {
    id: '100',
    name: 'Hypothyroidism',
    slug: 'hypothyroidism',
    specialty: 'Endocrinology',
    bodySystem: 'Endocrine',
    icdCode: 'E03.9',
    resourceLevel: 'BASIC',
    isEmergency: false,

    overview: `Hypothyroidism is a condition characterized by insufficient production of thyroid hormones by the thyroid gland. It is one of the most common endocrine disorders worldwide, affecting multiple organ systems and causing a wide range of symptoms. When diagnosed, it is easily treatable with thyroid hormone replacement.

TYPES OF HYPOTHYROIDISM:
• PRIMARY Hypothyroidism (95%): Thyroid gland failure
  - Hashimoto's thyroiditis (autoimmune) - Most common cause globally
  - Iodine deficiency - Most common cause in endemic areas
  - Post-thyroidectomy or radioiodine treatment
  - Medications (amiodarone, lithium, antithyroid drugs)
  - Congenital hypothyroidism

• SECONDARY Hypothyroidism (5%): Pituitary failure (low TSH)
• TERTIARY Hypothyroidism: Hypothalamic failure (rare)

🔴 KEY CLINICAL POINTS:
• Often INSIDIOUS onset - Symptoms develop gradually over months/years
• FATIGUE, WEIGHT GAIN, COLD INTOLERANCE are classic symptoms
• TSH is the best screening test (elevated in primary hypothyroidism)
• LEVOTHYROXINE is the treatment - Simple, effective, lifelong
• Start low in elderly and cardiac patients (risk of angina/arrhythmia)
• Check TSH 6-8 weeks after dose change
• MYXEDEMA COMA is rare but life-threatening emergency
• Screen in pregnancy - Untreated hypothyroidism causes fetal harm

AFRICAN CONTEXT:
• Iodine deficiency remains a cause in some regions despite salt iodization
• Often underdiagnosed due to non-specific symptoms
• Hashimoto's thyroiditis prevalence increasing
• Levothyroxine is on WHO Essential Medicines List
• Congenital hypothyroidism screening limited in many countries
• Goiter still common in iodine-deficient areas`,

    epidemiology: `GLOBAL PREVALENCE:
• Overall prevalence: 1-2% overt, 4-10% subclinical
• More common in women (5-8x higher than men)
• Increases with age (up to 10% in elderly women)
• Congenital hypothyroidism: 1 in 3,000-4,000 births

AFRICAN EPIDEMIOLOGY:
• Variable prevalence depending on iodine status
• Iodine deficiency disorders still present in some regions
• Goiter prevalence: 5-30% in endemic areas
• Hashimoto's thyroiditis increasingly recognized
• Limited data due to underdiagnosis
• Congenital hypothyroidism often missed (limited screening)

CAUSES IN AFRICA:
1. Iodine deficiency (endemic goiter regions)
2. Hashimoto's thyroiditis (autoimmune)
3. Post-surgical (thyroidectomy for goiter)
4. Medications
5. Congenital

RISK FACTORS:
• Female sex
• Age >60 years
• Family history of thyroid disease
• Personal history of autoimmune disease (Type 1 DM, vitiligo, RA)
• Previous thyroid surgery or radioiodine
• Head/neck radiation
• Medications: Amiodarone, lithium, interferon
• Iodine deficiency or excess
• Pregnancy (postpartum thyroiditis)
• Down syndrome, Turner syndrome`,

    clinicalPresentation: `SYMPTOMS (Often non-specific):

METABOLIC:
• Fatigue and lethargy (most common)
• Weight gain (despite poor appetite)
• Cold intolerance
• Decreased sweating

NEUROLOGICAL:
• Mental slowness, poor concentration
• Depression
• Memory impairment
• Slow speech
• Delayed reflexes (hung-up reflexes)
• Carpal tunnel syndrome
• Hearing loss

CARDIOVASCULAR:
• Bradycardia
• Diastolic hypertension
• Pericardial effusion (severe cases)

GASTROINTESTINAL:
• Constipation
• Decreased appetite

MUSCULOSKELETAL:
• Muscle cramps and stiffness
• Myalgia
• Elevated CK

DERMATOLOGICAL:
• Dry, coarse skin
• Hair loss (especially outer third of eyebrows)
• Brittle nails
• Non-pitting edema (myxedema)
• Yellowish skin (carotenemia)

REPRODUCTIVE:
• Menorrhagia or oligomenorrhea
• Infertility
• Erectile dysfunction
• Decreased libido

PHYSICAL EXAMINATION:
• Goiter (may be present or absent)
• Bradycardia
• Hypothermia
• Delayed relaxation of deep tendon reflexes
• Dry, cool, rough skin
• Periorbital puffiness
• Non-pitting edema (myxedema)
• Thinning hair, loss of lateral eyebrows
• Hoarse voice
• Slow movements and speech

MYXEDEMA COMA (Emergency):
• Altered mental status / coma
• Hypothermia
• Bradycardia, hypotension
• Hypoventilation
• Hyponatremia
• Precipitants: Infection, cold exposure, sedatives, non-compliance`,

    diagnosis: `DIAGNOSTIC APPROACH:
1. Clinical suspicion based on symptoms
2. Confirm with thyroid function tests
3. Determine cause
4. Screen for complications

THYROID FUNCTION TESTS:

PRIMARY HYPOTHYROIDISM:
• TSH: ELEVATED (first test to order)
• Free T4: LOW
• Free T3: Low or normal (less useful)

SUBCLINICAL HYPOTHYROIDISM:
• TSH: Mildly elevated (4.5-10 mIU/L)
• Free T4: NORMAL

SECONDARY/TERTIARY HYPOTHYROIDISM:
• TSH: Low or inappropriately normal
• Free T4: LOW

INTERPRETATION:
| TSH | Free T4 | Diagnosis |
|-----|---------|-----------|
| High | Low | Primary hypothyroidism |
| High | Normal | Subclinical hypothyroidism |
| Low/Normal | Low | Central hypothyroidism |

ADDITIONAL TESTS:

Anti-TPO Antibodies:
• Positive in Hashimoto's thyroiditis (90%+)
• Confirms autoimmune cause
• Predicts progression of subclinical to overt hypothyroidism

Anti-Thyroglobulin Antibodies:
• Also positive in Hashimoto's (less specific)

Thyroid Ultrasound:
• Assess gland size and nodules
• Heterogeneous pattern in Hashimoto's

Lipid Profile:
• Hypercholesterolemia common in hypothyroidism

Full Blood Count:
• Macrocytic or normocytic anemia may be present

WHO TO SCREEN:
• Symptomatic patients
• Goiter
• Family history of thyroid disease
• Other autoimmune diseases
• Previous thyroid surgery/radioiodine
• Medications (amiodarone, lithium)
• Pregnancy (first trimester or preconception)
• Infertility
• Hyperlipidemia unresponsive to treatment
• Down syndrome (annually)`,

    differentialDiagnosis: [
      'Depression - Fatigue, low mood, weight changes; normal TSH',
      'Anemia - Fatigue, pallor; check FBC',
      'Chronic fatigue syndrome - Fatigue without objective findings',
      'Heart failure - Edema, fatigue, dyspnea; check BNP, echo',
      'Nephrotic syndrome - Edema, proteinuria, hypoalbuminemia',
      'Obstructive sleep apnea - Fatigue, obesity, snoring',
      'Medication side effects - Beta-blockers, sedatives',
      'Diabetes mellitus - Fatigue, weight changes; check glucose',
      'Adrenal insufficiency - Fatigue, weight loss, hypotension; check cortisol',
      'Normal aging - Some symptoms overlap; TSH distinguishes'
    ],

    investigations: [
      {
        name: 'TSH (Thyroid Stimulating Hormone)',
        indication: 'First-line test for ALL suspected thyroid dysfunction',
        availability: 'BASIC'
      },
      {
        name: 'Free T4 (Free Thyroxine)',
        indication: 'Confirm hypothyroidism if TSH elevated; assess severity',
        availability: 'BASIC'
      },
      {
        name: 'Anti-TPO Antibodies',
        indication: 'Determine autoimmune cause (Hashimoto thyroiditis)',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Anti-Thyroglobulin Antibodies',
        indication: 'Additional autoimmune marker if TPO negative',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'Full Blood Count',
        indication: 'Assess for anemia (common in hypothyroidism)',
        availability: 'BASIC'
      },
      {
        name: 'Lipid Profile',
        indication: 'Hypercholesterolemia common; assess CV risk',
        availability: 'BASIC'
      },
      {
        name: 'Electrolytes (especially sodium)',
        indication: 'Hyponatremia may occur; important in myxedema coma',
        availability: 'BASIC'
      },
      {
        name: 'Creatine Kinase (CK)',
        indication: 'Elevated in hypothyroid myopathy',
        availability: 'BASIC'
      },
      {
        name: 'Thyroid Ultrasound',
        indication: 'Assess goiter size, nodules; Hashimoto pattern',
        availability: 'INTERMEDIATE'
      },
      {
        name: 'ECG',
        indication: 'Bradycardia, low voltage, T wave changes',
        availability: 'BASIC'
      },
      {
        name: 'Free T3',
        indication: 'Usually not needed; may be normal in early hypothyroidism',
        availability: 'INTERMEDIATE'
      }
    ],

    management: {
      immediate: [
        'Confirm diagnosis with TSH and Free T4',
        'Assess severity and duration of symptoms',
        'Check for coronary artery disease risk (elderly, cardiac history)',
        'Start levothyroxine replacement',
        'In elderly/cardiac patients: Start with low dose',
        'For MYXEDEMA COMA: ICU admission, IV levothyroxine, hydrocortisone, supportive care'
      ],

      pharmacological: [
        {
          drug: 'Levothyroxine (L-T4)',
          indication: 'ALL patients with overt hypothyroidism - First-line treatment',
          dose: 'Full replacement: 1.6 mcg/kg/day (typically 50-200 mcg/day). Young healthy adults: Start at full dose. Elderly/cardiac disease: Start 12.5-25 mcg/day, increase by 12.5-25 mcg every 4-6 weeks',
          route: 'Oral, on empty stomach (30-60 min before breakfast)',
          duration: 'LIFELONG in most cases',
          contraindications: ['Untreated adrenal insufficiency (treat with hydrocortisone first)', 'Recent MI (start very low dose)'],
          sideEffects: ['Overdose symptoms: Palpitations, tremor, weight loss, anxiety, angina'],
          alternatives: ['Liothyronine (T3) - Rarely needed; combination therapy controversial']
        },
        {
          drug: 'Levothyroxine - Subclinical Hypothyroidism',
          indication: 'Consider if TSH >10 mIU/L OR TSH 4.5-10 with symptoms or pregnancy',
          dose: 'Lower doses: 25-50 mcg/day',
          route: 'Oral',
          duration: 'May be temporary; reassess after normalizing',
          contraindications: ['Same as above'],
          sideEffects: ['Same as above'],
          alternatives: ['Observation with repeat TSH in 2-3 months if TSH <10 and asymptomatic']
        },
        {
          drug: 'Levothyroxine - Pregnancy',
          indication: 'ALL pregnant women with overt or subclinical hypothyroidism',
          dose: 'Increase dose by 25-30% as soon as pregnancy confirmed. Target TSH <2.5 mIU/L in first trimester',
          route: 'Oral',
          duration: 'Throughout pregnancy; may need dose reduction postpartum',
          contraindications: ['None in pregnancy - Treatment essential'],
          sideEffects: ['Same as above'],
          alternatives: ['None - Levothyroxine is safe and essential in pregnancy']
        },
        {
          drug: 'IV Levothyroxine (Myxedema Coma)',
          indication: 'MYXEDEMA COMA - Medical emergency',
          dose: 'Loading dose: 200-400 mcg IV, then 50-100 mcg IV daily',
          route: 'IV (oral absorption unreliable in coma)',
          duration: 'Until patient can take oral; then switch',
          contraindications: ['Give hydrocortisone first (may have coexisting adrenal insufficiency)'],
          sideEffects: ['Arrhythmias', 'Myocardial ischemia'],
          alternatives: ['IV liothyronine may be added in severe cases']
        },
        {
          drug: 'Hydrocortisone (for Myxedema Coma)',
          indication: 'Myxedema coma - Given BEFORE or with levothyroxine',
          dose: '100 mg IV bolus, then 50-100 mg every 8 hours',
          route: 'IV',
          duration: 'Until adrenal insufficiency excluded or patient stable',
          contraindications: ['None in emergency'],
          sideEffects: ['Hyperglycemia'],
          alternatives: ['Dexamethasone if cortisol testing needed']
        }
      ],

      nonPharmacological: [
        'MEDICATION ADMINISTRATION:',
        '  • Take levothyroxine on EMPTY STOMACH',
        '  • 30-60 minutes before breakfast OR at bedtime (3+ hours after eating)',
        '  • Consistent timing important',
        '  • Avoid taking with calcium, iron, PPIs, antacids (reduce absorption)',
        '',
        'PATIENT EDUCATION:',
        '  • Lifelong treatment usually required',
        '  • Do NOT stop medication when feeling better',
        '  • Symptoms improve gradually over weeks to months',
        '  • Regular monitoring needed',
        '',
        'DIETARY CONSIDERATIONS:',
        '  • Ensure adequate iodine intake (iodized salt)',
        '  • Avoid excess iodine (kelp, seaweed supplements)',
        '  • Fiber, soy, and certain foods may affect absorption if taken together',
        '',
        'IODINE DEFICIENCY PREVENTION:',
        '  • Universal salt iodization programs',
        '  • Iodine supplementation in pregnancy (150 mcg/day)',
        '',
        'LIFESTYLE:',
        '  • Regular exercise helps with fatigue and weight',
        '  • Address cardiovascular risk factors'
      ],

      surgical: [
        'Surgery not indicated for hypothyroidism itself',
        'Thyroidectomy may be needed for large goiter causing compression',
        'Post-thyroidectomy patients require lifelong levothyroxine'
      ],

      referralCriteria: [
        'Myxedema coma - Emergency, ICU admission',
        'Pregnancy with hypothyroidism - Obstetric and endocrine co-management',
        'Secondary/central hypothyroidism - Needs pituitary evaluation',
        'Difficulty achieving euthyroid state despite adequate dosing',
        'Cardiac disease and hypothyroidism (careful dose titration)',
        'Children with hypothyroidism',
        'Suspected thyroid cancer',
        'Large goiter requiring surgical evaluation'
      ],

      monitoring: [
        'TSH: Check 6-8 weeks after starting or changing dose',
        'Once stable: Check TSH every 6-12 months',
        'Symptoms: Should improve within 2-4 weeks, full effect by 3-6 months',
        'Weight, heart rate, symptoms at each visit',
        'Pregnancy: Check TSH each trimester, more often if adjusting dose',
        'Lipid profile: Recheck after euthyroid achieved',
        'Do NOT check TSH too soon after dose change (takes 6 weeks to equilibrate)'
      ],

      followUp: `INITIAL PHASE:
• Check TSH 6-8 weeks after starting or each dose change
• Adjust dose by 12.5-25 mcg increments
• Continue until TSH in target range (0.5-4.0 mIU/L, or 0.5-2.5 in pregnancy)

MAINTENANCE PHASE:
• Once stable, check TSH every 6-12 months
• Annual clinical review
• Reinforce medication adherence
• Review for symptoms of over- or under-treatment

DOSE ADJUSTMENTS NEEDED:
• Pregnancy: Increase dose by 25-30% early in pregnancy
• Weight gain: May need dose increase
• Weight loss: May need dose decrease
• Starting estrogen therapy: May need dose increase
• Aging: Requirements may decrease in elderly
• Malabsorption: May need dose increase

WHEN TO RECHECK SOONER:
• Symptoms of hypo- or hyperthyroidism
• Significant weight change
• Starting medications that affect thyroid/absorption
• Pregnancy
• Poor adherence suspected`
    },

    redFlags: [
      '🔴 CRITICAL: Myxedema coma (altered consciousness, hypothermia, bradycardia) - ICU, IV levothyroxine + hydrocortisone',
      '🔴 CRITICAL: Severe hyponatremia with hypothyroidism - May indicate myxedema coma',
      '🔴 CRITICAL: Hypothyroid patient with chest pain - Caution with levothyroxine dosing, cardiac workup',
      '🟠 URGENT: Pregnant woman with untreated hypothyroidism - Start treatment immediately',
      '🟠 URGENT: Newborn with suspected congenital hypothyroidism - Urgent TSH, early treatment critical',
      '🟠 URGENT: Pericardial effusion with hypothyroidism - May cause tamponade in severe cases',
      '🟡 WARNING: Persistent symptoms despite normal TSH - Reassess diagnosis, adherence',
      '🟡 WARNING: TSH remains elevated despite adequate dose - Check adherence, absorption, timing'
    ],

    complications: [
      'Cardiovascular: Dyslipidemia, atherosclerosis, heart failure, pericardial effusion',
      'Myxedema coma (severe, life-threatening)',
      'Infertility and pregnancy complications',
      'Congenital hypothyroidism effects (intellectual disability if untreated in infants)',
      'Peripheral neuropathy',
      'Carpal tunnel syndrome',
      'Myopathy',
      'Hyponatremia',
      'Anemia',
      'Depression and cognitive impairment',
      'Hyperlipidemia and accelerated atherosclerosis',
      'Goiter (in iodine deficiency)'
    ],

    prognosis: `EXCELLENT PROGNOSIS WITH TREATMENT:
• Levothyroxine replacement is highly effective
• Most symptoms resolve within weeks to months
• Normal life expectancy with proper treatment
• Quality of life returns to normal

TIMELINE OF IMPROVEMENT:
• Energy: Improvement within 2-4 weeks
• Weight: Gradual improvement over months
• Skin and hair: May take 3-6 months
• Cholesterol: Improves after achieving euthyroid state

UNTREATED HYPOTHYROIDISM:
• Progressive symptoms
• Increased cardiovascular risk
• Myxedema coma (rare, 20-60% mortality)
• Pregnancy complications: Miscarriage, preterm birth, developmental issues

CONGENITAL HYPOTHYROIDISM:
• If treated within first 2 weeks of life: Normal development
• If untreated: Irreversible intellectual disability (cretinism)

SUBCLINICAL HYPOTHYROIDISM:
• 2-5% per year progress to overt hypothyroidism
• Higher progression if TPO antibodies positive
• Treatment decision individualized`,

    prevention: `PRIMARY PREVENTION:
• Universal salt iodization (most effective public health measure)
• Adequate iodine intake (150 mcg/day, 250 mcg in pregnancy)
• Avoid excess iodine (can also cause thyroid dysfunction)
• Newborn screening for congenital hypothyroidism

IODINE REQUIREMENTS:
• Adults: 150 mcg/day
• Pregnancy: 250 mcg/day
• Lactation: 250 mcg/day
• Children: 90-120 mcg/day

SECONDARY PREVENTION:
• Early detection through screening high-risk groups
• Regular monitoring of patients on medications affecting thyroid (amiodarone, lithium)
• Screening after head/neck radiation
• Screening in pregnancy

PREVENTING COMPLICATIONS:
• Adherence to levothyroxine therapy
• Regular TSH monitoring
• Prompt treatment of overt hypothyroidism
• Special attention in pregnancy

CONGENITAL HYPOTHYROIDISM SCREENING:
• Newborn screening programs (heel prick TSH)
• Early treatment prevents intellectual disability
• Advocacy for universal newborn screening in Africa`,

    patientEducation: `UNDERSTANDING HYPOTHYROIDISM:
Your thyroid gland (in your neck) makes hormones that control your body's metabolism - how fast you burn energy. When your thyroid doesn't make enough hormone, everything slows down. This is called hypothyroidism or "underactive thyroid."

COMMON SYMPTOMS:
• Feeling tired all the time
• Gaining weight even without eating more
• Feeling cold when others are comfortable
• Constipation
• Dry skin and hair
• Slow thinking or forgetfulness
• Depression

THE GOOD NEWS:
Hypothyroidism is easily treated! A simple tablet taken once a day replaces the hormone your thyroid isn't making.

YOUR MEDICATION - LEVOTHYROXINE:
💊 This is the same hormone your body should be making
💊 You will likely need to take it for the rest of your life
💊 It is very safe with almost no side effects when taken correctly

HOW TO TAKE LEVOTHYROXINE:
✅ Take it EVERY DAY at the same time
✅ Take on an EMPTY STOMACH
✅ Best time: First thing in the morning, 30-60 minutes before breakfast
✅ Swallow with water only

⚠️ IMPORTANT - Things that reduce absorption:
• Calcium tablets or antacids
• Iron supplements
• Some heart and stomach medications
Take these at least 4 hours apart from levothyroxine

DO NOT:
❌ Stop taking your medication when you feel better
❌ Change the dose yourself
❌ Take extra if you miss a dose (just take next dose as normal)
❌ Share your medication with others

WHAT TO EXPECT:
• You should start feeling better in 2-4 weeks
• Full improvement may take 3-6 months
• Your doctor will check your blood tests regularly
• Your dose may need adjustment until it's right for you

BLOOD TESTS:
• TSH blood test shows if your dose is correct
• Check TSH 6-8 weeks after starting or changing dose
• Once stable, check every 6-12 months

WHEN TO CONTACT YOUR DOCTOR:
📞 Call if you experience:
• Palpitations (heart racing)
• Excessive sweating
• Unexplained weight loss
• Tremor or anxiety
(These may mean your dose is too high)

📞 Also call if:
• Symptoms not improving after several weeks
• Symptoms coming back
• You become pregnant (dose often needs increasing)

PREGNANCY AND HYPOTHYROIDISM:
🤰 If you are pregnant or planning pregnancy:
• Tell your doctor immediately
• Your dose will likely need to increase
• Proper treatment is essential for your baby's brain development
• Take your medication every day without fail

LIVING WITH HYPOTHYROIDISM:
• This is a manageable condition
• Most people live completely normal lives
• Take your medication daily
• Attend your follow-up appointments
• Eat a balanced diet with iodized salt
• Exercise regularly - it helps with energy and weight

REMEMBER:
• Hypothyroidism is common and treatable
• One small tablet a day is all you need
• Don't stop your medication - it's for life
• Regular check-ups keep you healthy`,

    references: [
      'American Thyroid Association Guidelines for Hypothyroidism in Adults 2014',
      'European Thyroid Association Guidelines on Subclinical Hypothyroidism 2013',
      'NICE Clinical Knowledge Summary: Hypothyroidism',
      'WHO Guideline: Fortification of Food-Grade Salt with Iodine',
      'Endocrine Society Guidelines on Thyroid Dysfunction in Pregnancy 2017',
      'WHO Essential Medicines List 2023 (Levothyroxine included)'
    ],

    lastUpdated: '2026-01-19',
    evidenceGrade: 'A'
  },
];

// Helper functions
export const getConditionBySlug = (slug: string) => 
  conditions.find(c => c.slug === slug);

export const getConditionById = (id: string) => 
  conditions.find(c => c.id === id);

export const getEmergencyConditions = () => 
  conditions.filter(c => c.isEmergency);

export const getConditionsBySpecialty = (specialty: string) => 
  conditions.filter(c => c.specialty === specialty);

export const getConditionsByBodySystem = (system: string) =>
  conditions.filter(c => c.bodySystem === system);

export const searchConditions = (query: string) => {
  const q = query.toLowerCase();
  return conditions.filter(c => 
    c.name.toLowerCase().includes(q) ||
    c.overview.toLowerCase().includes(q) ||
    c.icdCode.toLowerCase().includes(q)
  );
};
